const STORAGE_KEY = "tms.masterPortal.localPreview.v1";
const WORKFLOW_API_BASE_KEY = "tms.masterPortal.workflowApiBase";
const WORKFLOW_API_TOKEN_KEY = "tms.masterPortal.workflowApiToken";
const TRACKER_API_MODE_KEY = "tms.masterPortal.trackerApiMode";
const TRACKER_API_BASE_KEY = "tms.masterPortal.trackerApiBase";
const TRACKER_API_TOKEN_KEY = "tms.masterPortal.trackerApiToken";
const REPORT_API_MODE_KEY = "tms.masterPortal.reportApiMode";
const REPORT_API_BASE_KEY = "tms.masterPortal.reportApiBase";
const REPORT_API_TOKEN_KEY = "tms.masterPortal.reportApiToken";
const PORTAL_AUTH_TOKEN_KEY = "tms.masterPortal.authToken";
const PORTAL_AUTH_SESSION_TOKEN_KEY = "tms.masterPortal.authToken.session";
const PORTAL_AUTH_USER_KEY = "tms.masterPortal.authUser";

// Screenshot images and intermediate text are memory-only, outside saved portal state.
let claimScreenshotGeneration = 0;
let claimScreenshotRuntimePromise = null;
let claimScreenshotActiveJob = null;
let claimScreenshotReviewContext = null;

let trackerPublicationEpoch = 0;
let trackerApiPageState = { key: "", status: "idle", response: null, error: "", staleResponses: 0, lastStaleAt: "" };
let trackerApiFacetState = { key: "", status: "idle", response: null, error: "", staleResponses: 0, lastStaleAt: "" };
let workbenchRenderProfileState = { current: null, last: null, lastSlicerFacetMs: 0 };
let deferredWorkbenchPanelRenderTimer = 0;
let reportApiState = { key: "", status: "idle", response: null, error: "" };
let reportQueueDrilldownState = { key: "", status: "idle", response: null, error: "", request: null };
let feeScheduleApiState = { key: "", status: "idle", rows: [], truncated: false, rowLimit: 0, error: "", savedAt: "" };
let feeScheduleGridFocusIntent = null;
let feeScheduleMissingApiState = { key: "", requestId: 0, status: "idle", rows: [], truncated: false, rowLimit: 0, complete: false, source: "", provenance: null, error: "", generatedAt: "" };
let feeScheduleMissingRequestSeq = 0;
const FEE_SCHEDULE_MISSING_PAGE_SIZE = 25;
// orgId -> friendly company label for the worklist, rebuilt on every render.
let feeScheduleMissingOrgLabels = new Map();
let feeScheduleMissingPage = 1;
let serverClaimDrawerRequestKey = "";
let serverClaimDrawerController = null;
let activeClaimDrawerContext = null;
let followUpEditorState = null;
// A REJECTED SAVE MUST NOT EAT WHAT THE BILLER TYPED.
//
// The follow-up editor requires a claim note, and the note is the expensive part
// - it is the sentence explaining the call that was just made. When the save was
// refused the modal kept the text only until it was closed, and the natural
// reaction to "could not be saved" is to close the box; the note went with it and
// the biller retyped it. Drafts are kept per (claim, service line) so a refusal,
// a close, and reopening the same line all land back on the same words. Cleared
// only when the write actually lands.
let followUpEditorDrafts = {};
let claimNoteMutationState = null;
let bulkFollowUpRetryState = null;
// PHASE-1 productivity telemetry: one focus-session record per open claim drawer.
let claimWorkTelemetry = { claimId: "", serviceId: "", queueKey: "", source: "", openedAt: 0 };
let reportRunTrailRequestKey = "";
let reportExportJobTrailRequestKey = "";
let exportGovernanceActivityTrailRequestKey = "";
let claimQueueWorkAuditTrailRequestKey = "";
let reportRunDetailRequestKey = "";
let exportJobDetailRequestKey = "";
let exportJobDownloadPreviewRequestKey = "";
let trackerWorkbookExportRequestSeq = 0;
let reportExportPackagePreviewRequestKey = "";
let securityReviewRequestKey = "";
let feedbackAdminRequestKey = "";
let feedbackTicketStatusRequestKey = "";
let feedbackSettingsSaveRequestKey = "";
let exportGovernanceActivityExportRequestKey = "";
let reportRunAuditExportRequestKey = "";
let queueWorkExportRequestKey = "";
let accessAdminPreviewRequestKey = "";
let liveQueryMapRequestKey = "";
let queryMapRulebookRequestKey = "";
let workflowApiHealthRequestKey = "";
// `inviteLink` holds the shareable setup link the server just minted. TRANSIENT
// ON PURPOSE: that URL carries a live token that sets the account's password, so
// it lives here and in the DOM and nowhere else - it is never assigned onto a
// row in `users`, so it can never ride persistCustomUsers into localStorage.
let userMgmtDirectoryState = { selectedIndex: 0, editing: false, editDraft: null, inviteLink: null, tab: "people" };
let staffTeamState = { status: "idle", teams: [], selectedId: "", editing: false, draft: null, error: "", pendingUserKey: "", archivingId: "", search: "" };
let portalUserDirectoryState = { status: "idle", requestKey: "", users: [], lanes: null, error: "" };
let portalUserDirectoryRequestPromise = null;
let teamStructureUiState = {
  view: "people",
  status: "idle",
  requestKey: "",
  orgId: "",
  version: 0,
  members: [],
  eligibleUsers: [],
  directoryLanes: null,
  updatedAtUtc: "",
  updatedByUserId: "",
  error: "",
  notice: "",
  saving: false,
  dirty: false,
  search: ""
};
let teamStructureRequestSeq = 0;
let userMgmtInviteLinkCopyTimer = 0;
let userActivityAdminState = { status: "idle", summaries: [], events: [], loadedAt: "", error: "", filters: { days: "30", feature: "All", deniedOnly: false } };
let userAddWizardInviteRequestKey = "";
let userAddWizardInviteRequestSeq = 0;
let feedbackTicketSubmitRequestSeq = 0;
let tmsBootRequestSeq = 0;
let feedbackAdminState = { status: "all", tickets: [], notificationEmail: "", loading: false, error: "" };

function workbenchProfileNow() {
  return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}

function beginWorkbenchRenderProfile(options = {}) {
  return {
    label: "renderClaimsTable",
    startedAt: new Date().toISOString(),
    selectionOnly: Boolean(options.selectionOnly),
    mode: "",
    serverBacked: false,
    queryFilterMs: 0,
    apiWaitMs: 0,
    slicerFacetMs: workbenchRenderProfileState.lastSlicerFacetMs || 0,
    financialCalcMs: 0,
    statusFormulaMs: 0,
    tableRenderMs: 0,
    totalMs: 0,
    rowsFiltered: 0,
    rowsRendered: 0,
    totalRows: 0,
    apiPageStatus: trackerApiPageState.status || "idle",
    apiFacetStatus: trackerApiFacetState.status || "idle",
    apiPageRequestMs: trackerApiPageState.lastRequestMs || 0,
    apiFacetRequestMs: trackerApiFacetState.lastRequestMs || 0,
    pendingApiPage: trackerApiPageState.status === "pending",
    pendingApiFacet: trackerApiFacetState.status === "pending",
    startedMs: workbenchProfileNow()
  };
}

function profileWorkbenchStep(profile, key, fn) {
  const start = workbenchProfileNow();
  try {
    return fn();
  } finally {
    profile[key] = moneyNumber((profile[key] || 0) + (workbenchProfileNow() - start));
  }
}

function finishWorkbenchRenderProfile(profile) {
  if (!profile) return;
  profile.totalMs = moneyNumber(workbenchProfileNow() - profile.startedMs);
  delete profile.startedMs;
  workbenchRenderProfileState.last = profile;
  workbenchRenderProfileState.current = null;
  if (typeof window !== "undefined") {
    window.__tmsLastWorkbenchRenderProfile = profile;
    window.__tmsGetWorkbenchRenderProfile = () => workbenchRenderProfileState.last;
    if (window.TMS_ENABLE_RENDER_PROFILE_LOG === true || /[?&]qa=render-profile\b/i.test(String(window.location?.search || ""))) {
      console.table([profile]);
    }
  }
}

function getLastWorkbenchRenderProfile() {
  return workbenchRenderProfileState.last;
}

// PER USER DIRECTION (multi-tenant): TMS bills for ~10 different groups
// and NONE can see each other's data. Some stateside billers see 3
// groups, some only 1. So scope lives on the USER, not the role.
//
// `clientNames` are the tenant/client scope shown to users. orgIds remain
// database metadata and an additional audit/persistence scope when present.
// Leaving both empty / omitted means "no tenant" (deny-by-default).
// SuperAdmin is the only role that bypasses tenant scope — they see every
// group (cross-tenant by design).
const users = [
  { name: "Platform Admin", role: "SuperAdmin" },
  // Senior biller — covers 3 groups
  { name: "Stateside Biller A", role: "StatesideBiller", clientNames: ["Demo Client A", "Demo Client B", "Demo Client C"], orgIds: [
    "90000001",
    "90000002",
    "90000003"
  ] },
  // Junior biller — covers 1 group only
  { name: "Stateside Biller B", role: "StatesideBiller", clientNames: ["Demo Client A"], orgIds: [
    "90000001"
  ] },
  // Offshore billers don't get org scope — they see only what's
  // explicitly routed to them. The route IS the scope.
  //
  // The offshore roster is NOT here, and no longer ships at all: it is real,
  // named staff that Super Admin adds / removes / deactivates from the UI as the
  // team grows and turns over (teams of 10–20 soon). They live in the persisted
  // "custom user" store the UI writes to (tms.portal.customUsers, loaded by
  // hydrateCustomUsers), so every offshore biller reaches this array by one path
  // and behaves identically everywhere — dropdown, scope, audit, and tests.
  // See the note below on why the five shipped defaults were deleted.
  // Client role preview users — each maps to ONE practice. Switching between
  // them shows the strict per-tenant isolation (none can see each
  // other's data).
  { name: "Demo Client A", role: "Client", clientNames: ["Demo Client A"], orgIds: ["90000001"] },
  { name: "Demo Client B", role: "Client", clientNames: ["Demo Client B"], orgIds: ["90000002"] },
  { name: "Demo Client C", role: "Client", clientNames: ["Demo Client C"], orgIds: ["90000003"] }
];

// THERE IS NO SHIPPED OFFSHORE ROSTER, AND THAT IS THE POINT (2026-08-05).
//
// This file used to declare DEFAULT_OFFSHORE_BILLERS — five real, named staff --
// and seed them into the in-memory user store so the "Assign to..." dropdown had
// suggestions on a fresh browser. staticwebapp.config.json routes /app.js with
// NO allowedRoles and a one-year immutable cache, and both live apps return it
// 200 ANONYMOUSLY: that list was a publicly enumerable staff roster, and the
// mailboxes follow from the names. Two of the five were among the twelve names
// f7cd8ce / c9dc41e deliberately moved OUT of this bundle and behind
// GET /api/user/note-authors, gated on data:internalNotes. A convenience default
// was re-publishing what an authenticated endpoint was built to protect.
//
// It is deleted rather than replaced, for two reasons.
//   * A FALLBACK THAT LEAKS IS WORSE THAN AN EMPTY STATE. The empty state here
//     is an "Assign to..." picker with no suggestions until an admin adds the
//     team in Manage Users — where the roster belongs, is editable, and is
//     already the only source (tms.portal.customUsers, loaded by
//     hydrateCustomUsers). Nobody's saved roster is affected; only the
//     never-edited first-run default is gone.
//   * THE NOTE-AUTHOR DIRECTORY CANNOT STAND IN FOR IT. That endpoint answers
//     "who wrote this note" (initials -> name, gated on data:internalNotes) and
//     carries no role, so it cannot say who may RECEIVE work (claim:assign).
//     It is also async, and this store is read synchronously at module load --
//     including by the server-side verifiers, where there is no fetch and no
//     localStorage. Hydrating the assignee picker from it would be both the
//     wrong list and a race.
//
// backend/verify_service_line_note_authorship.js enforces the absence: any
// directory name or mailbox appearing in app.js fails the build.

// Active user — what controls the orgIds scope at runtime. SuperAdmin
// can override via state.activeUserName; the current login defaults to
// the first user matching the active role.
// NOTE THE users[0] FALLBACK (portal-guardian, 2026-08-05). Now that no offshore
// roster ships, an OffshoreBiller session with nobody provisioned matches no user
// and falls back to users[0] — the Platform Admin record. That does NOT widen
// anything: permissions come from roleProfiles[state.role], currentAccessProfile
// hardcodes allOrgs:false / crossTenant:false for the role and derives orgIds from
// the record (undefined -> []), so canAccessClaim denies on an empty scope and the
// session sees zero claims; the getClaims cache key leads with state.role, so
// there is no bleed between the two. Executed, not assumed. What it DOES do is
// stamp "Platform Admin" on assignedBy in that state, and hand a SuperAdmin record
// to any future reader of currentUser().role — there are none today. Read the ROLE
// from state.role, never from this record.
function currentUser() {
  const role = canonicalRole(state?.role);
  if (state?.activeUserName) {
    const named = users.find((u) => u.name === state.activeUserName);
    if (named && canonicalRole(named.role) === role && normalizeUserStatus(named.status) !== "disabled") return named;
  }
  return users.find((u) => canonicalRole(u.role) === role && normalizeUserStatus(u.status) !== "disabled") || users[0];
}

const PERMISSIONS = {
  VIEW_WORKBENCH: "view:workbench",
  VIEW_ASSIGNMENTS: "view:assignments",
  VIEW_REVIEWS: "view:reviews",
  // FRONTEND-DEAD, BACKEND-LOAD-BEARING. No viewPermissions key references this
  // any more - the `client` view it gated was folded into `reports` on
  // 2026-08-11. DO NOT DELETE IT. backend/trackerApi.js's
  // requireClientPaymentsPermission accepts view:client as one of its candidate
  // grants, and backend/portalRoleCatalog.js still grants it to SuperAdmin and
  // StatesideBiller - which is how an internal biller opening the Payments /
  // Client Receivables / Client RCM reports inside Reports is authorised for
  // /api/client/reports/claims and /api/client/payments/checks. Removing it
  // here, or from the role catalog, 403s those three reports for internal staff.
  VIEW_CLIENT: "view:client",
  VIEW_REPORTS: "view:reports",
  VIEW_FEE_SCHEDULES: "view:feeSchedules",
  // A full Client reads its own company's rates (Nathan, 2026-10-13). Read only.
  VIEW_OWN_FEE_SCHEDULE: "feeSchedules:viewOwn",
  VIEW_FOLLOW_UP_RULES: "view:followUpRules",
  VIEW_QUERY_MAP: "view:queryMap",
  VIEW_SECURITY: "view:security",
  VIEW_AUDIT: "view:audit",
  VIEW_FINANCIALS: "data:financials",
  VIEW_PHI: "data:phi",
  VIEW_INTERNAL_NOTES: "data:internalNotes",
  SECURITY_AUDIT: "security:audit:view",
  MANAGE_USERS: "users:manage",
  MANAGE_GROUPS: "groups:manage",
  ORGANIZATIONS_ONBOARD: "organizations:onboard",
  ORGANIZATIONS_CREDENTIALS_ROTATE: "organizations:credentials:rotate",
  MANAGE_ACCESS_GRANTS: "access:grants:manage",
  MANAGE_PERMISSION_OVERRIDES: "access:overrides:manage",
  REPORT_EXPORT: "reports:export",
  EXPORT_WORKFLOW: "workflow:export",
  IMPORT_WORKFLOW: "workflow:import",
  ASSIGN_CLAIMS: "claim:assign",
  UPDATE_FOLLOW_UP: "claim:updateFollowUp",
  SUBMIT_REVIEW: "claim:submitReview",
  APPROVE_REVIEW: "claim:approveReview",
  MANAGE_WRITE_OFFS: "claim:manageWriteOffs",
  ADD_INTERNAL_NOTE: "claim:addInternalNote",
  VIEW_CLAIM_NOTES: "claim:notes:view",
  ADD_CLAIM_NOTE: "claim:notes:add",
  CORRECT_OWN_CLAIM_NOTE: "claim:notes:correctOwn",
  VOID_CLAIM_NOTE: "claim:notes:void",
  VIEW_CLAIM_NOTE_AUDIT: "claim:notes:audit:view",
  SEARCH_CLAIM_NOTES: "claim:notes:search",
  EXPORT_CLAIM_NOTES: "claim:notes:export",
  MANAGE_FEE_SCHEDULES: "setup:feeSchedules",
  MANAGE_FOLLOW_UP_RULES: "setup:followUpRules",
  MARK_STATEMENTS: "client:markStatements",
  STATEMENTS_VIEW: "statements:view",
  STATEMENTS_GENERATE: "statements:generate",
  STATEMENTS_RECONCILIATION_VIEW: "statements:reconciliation:view",
  // On-Hold Authorization Tracker (ON_HOLD_TRACKER_DESIGN.md §2). Dotted keys
  // deliberately match the backend strings in portalRoleCatalog.js /
  // workflowCommandHandlers.js so one grant works in the UI and the API.
  AUTHORIZATIONS_VIEW: "authorizations.view",
  AUTHORIZATIONS_MANAGE: "authorizations.manage",
  // THE SEND GRANT (owner directive D2). Held by TMS roles only: recording an
  // authorization is the client's act, sending the claims out of EZClaim is ours.
  // It already existed in portalRoleCatalog.js and had no counterpart here, which
  // left the whole send lane unreachable by any human — the same drift class
  // startLocalWorkflowApi.js records having shipped once already.
  AUTHORIZATIONS_SUBMIT: "authorizations.submit",
  // CREDENTIALING HOLD (CPH). The SIBLING lane, split exactly like the three
  // above and for the same reasons -- a claim parks because the therapist is
  // not credentialed with the payer, the CLIENT records that they now are, and
  // TMS bills it out. Dotted keys match portalRoleCatalog.js /
  // credentialingHoldPopulation.js so one grant works in the UI and the API.
  //
  // view = read the provider board; manage = record the decision + its
  // effective date (the client's own attributable assertion about their own
  // clinician); submit = move the bill-out task the release creates.
  CREDENTIALING_VIEW: "credentialing.view",
  CREDENTIALING_MANAGE: "credentialing.manage",
  CREDENTIALING_SUBMIT: "credentialing.submit",
  // Post-submission facility-information cases. View/respond are shared;
  // manage/resolve remain attributable TMS operations.
  CLIENT_ACTION_VIEW: "clientAction.view",
  CLIENT_ACTION_RESPOND: "clientAction.reply",
  CLIENT_ACTION_MANAGE: "clientAction.manage",
  CLIENT_ACTION_RESOLVE: "clientAction.resolve",
  // Internal factual denial histories. Supporting evidence uses its own
  // read-only grant; ordinary claim editing and patient lookup keep their scope.
  DENIAL_INTELLIGENCE_VIEW: "denial-intelligence:view",
  DENIAL_INTELLIGENCE_EVIDENCE_VIEW: "denial-intelligence:evidence:view",
  // Client Role Experience v2 (CLIENT_EXPERIENCE_V2_SPEC §1). These two READ
  // grants deliberately retire the 2026-06-21 statements-only lockdown, so a
  // practice owner can answer "how is my money doing / what's stuck on me"
  // instead of landing on a board and being told they cannot see it.
  // Scope is unchanged: ROLE_BASE_SCOPE.Client stays {} — per-user orgIds,
  // deny-by-default. Never claim-level data, rates, or biller workflow.
  CLIENT_HOME: "client:home",
  CLIENT_PAYMENTS: "client:payments",
  // The ClientBiller role's ONE extra grant over Client (2026-08-03). It opens
  // the All Data grid as a FROZEN 34-column read-only projection (the backend's
  // CLIENT_WORKBENCH_COLUMNS list) — never the claim drawer, never a workflow
  // command. Mirrors "client:workbench" in backend/portalRoleCatalog.js so one
  // grant works in the UI and in the API.
  CLIENT_WORKBENCH: "client:workbench"
};

const viewPermissions = {
  // Two grants open All Data and they mean different things: view:workbench is
  // the biller's full grid, client:workbench is the ClientBiller's frozen
  // read-only projection. Listing both here — rather than granting
  // view:workbench to ClientBiller — keeps every OTHER view:workbench-gated
  // control (claim drawer, workflow commands, internal exports) shut to an
  // external user. Same pattern as the client: key below.
  workbench: [PERMISSIONS.VIEW_WORKBENCH, PERMISSIONS.CLIENT_WORKBENCH],
  assignments: PERMISSIONS.VIEW_ASSIGNMENTS,
  reviews: PERMISSIONS.VIEW_REVIEWS,
  clientHome: PERMISSIONS.CLIENT_HOME,
  statements: PERMISSIONS.STATEMENTS_VIEW,
  authorizations: PERMISSIONS.AUTHORIZATIONS_VIEW,
  credentialing: PERMISSIONS.CREDENTIALING_VIEW,
  clientActionPending: PERMISSIONS.CLIENT_ACTION_VIEW,
  denialIntelligence: PERMISSIONS.DENIAL_INTELLIGENCE_VIEW,
  // ONE ANALYTICS SURFACE (owner, 2026-08-11): "we could really get rid of the
  // whole Payments & Exports... it's all just part of reporting, right?". The
  // `client` view key is gone and its three reports live inside Reports, so
  // BOTH grants open this view - internal staff via view:reports, an external
  // practice owner via client:payments. Same multi-grant pattern as `workbench`
  // above, and for the same reason: view:reports carries internal meaning
  // everywhere else (the report dataset lane, the export gate, the biller work
  // queue), so granting it to the Client role to fix a NAV problem would hand a
  // practice owner fifteen internal reports. What a client-only session
  // actually gets is decided ONCE, at the top of renderReports(), by asking
  // whether the session holds view:reports.
  reports: [PERMISSIONS.VIEW_REPORTS, PERMISSIONS.CLIENT_PAYMENTS],
  // Whole-book LITE export is its own Analytics surface. Gate on the same
  // export grants the old Reports-foot cockpit used, so a role that cannot
  // download the workbook never sees a dead studio. A full Client holds the
  // report-export grant but not the separate workflow-workbook grant, so it
  // does not satisfy this two-grant gate.
  trackerExport: [PERMISSIONS.REPORT_EXPORT, PERMISSIONS.EXPORT_WORKFLOW],
  feeSchedules: [PERMISSIONS.VIEW_FEE_SCHEDULES, PERMISSIONS.VIEW_OWN_FEE_SCHEDULE],
  // Missing Rates is the WORK QUEUE half of fee schedule setup, so it is
  // gated by the same grant rather than a new one: anyone who may read the
  // rate table may read the list of rates that are not there yet, and the
  // actions on it carry MANAGE_FEE_SCHEDULES exactly as the rate table does.
  // Minting a second permission would have created a role-matrix row that
  // means nothing on its own and one more place for the two to disagree.
  missingRates: PERMISSIONS.VIEW_FEE_SCHEDULES,
  followUpRules: PERMISSIONS.VIEW_FOLLOW_UP_RULES,
  queryMap: PERMISSIONS.VIEW_QUERY_MAP,
  feedback: PERMISSIONS.SECURITY_AUDIT,
  // Oversight: global admins only. The SuperAdmin-only audit grant keeps it out of
  // every other role's nav and access preview; viewAllPermissionRequirements adds
  // Financials, and canAccessView adds the all-org, cross-tenant requirement.
  companyOversight: PERMISSIONS.SECURITY_AUDIT,
  backupHistory: PERMISSIONS.SECURITY_AUDIT,
  // Training (2026-10-15) teaches the client pages, so any grant that opens one
  // of them opens the library. Each tour inside is still filtered to the pages
  // this session can open (trainingVideosForSession), so the page never
  // advertises a surface the role does not have.
  training: [PERMISSIONS.VIEW_WORKBENCH, PERMISSIONS.CLIENT_WORKBENCH, PERMISSIONS.AUTHORIZATIONS_VIEW, PERMISSIONS.CREDENTIALING_VIEW, PERMISSIONS.CLIENT_ACTION_VIEW, PERMISSIONS.VIEW_REPORTS, PERMISSIONS.CLIENT_PAYMENTS, PERMISSIONS.STATEMENTS_VIEW],
  audit: PERMISSIONS.VIEW_AUDIT
};

// Most permission arrays above are alternatives: either the internal grant or
// the client-safe grant opens the same shell. These two views are deliberately
// different. They expose broad financial/export data and therefore require
// every listed grant. Keep that distinction in one helper so live navigation,
// deep links, and access-directory previews cannot drift apart.
const viewAllPermissionRequirements = {
  trackerExport: [PERMISSIONS.REPORT_EXPORT, PERMISSIONS.EXPORT_WORKFLOW],
  companyOversight: [PERMISSIONS.SECURITY_AUDIT, PERMISSIONS.VIEW_FINANCIALS]
};

function viewPermissionGateSatisfied(view, permissionHeld) {
  const requiredAll = viewAllPermissionRequirements[view];
  if (requiredAll) return requiredAll.every((permission) => permissionHeld(permission));
  const permission = viewPermissions[view];
  const alternatives = Array.isArray(permission) ? permission : [permission];
  return alternatives.some((candidate) => Boolean(candidate) && permissionHeld(candidate));
}

// WHO EACH SURFACE IS FOR. Declared once, on the SURFACE, next to the
// permission that gates it -- never as a per-role nav list. A per-role list is
// what produced two nav entries both reading "Home": the same question answered
// in five places, four of which nobody updates.
//
// Nav visibility is therefore the permission model AND this classification.
// canAccessView answers "may this principal open it"; viewOfferedToRole answers
// "is it theirs"; only both together put an entry in the sidebar. The ROLE side
// of the second question is isExternalClientRole -- the same single authority
// the backend uses (portalRoleCatalog.js EXTERNAL_CLIENT_ROLES) -- so a sixth
// role is classified in one edit instead of fourteen.
const VIEW_AUDIENCE = {
  // ═══════════════════════════════════════════════════════════════════════════
  // CLIENT HOME IS RETIRED FOR EVERY ROLE (owner, 2026-08-21)
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // "I thought we removed the Home tab from everyone. Somehow it keeps on
  // appearing. I have no idea why. There's no user that should have the Home
  // tab."
  //
  // WHY IT KEPT APPEARING, since that is the actual question: the 2026-08-06
  // removal was INTERNAL-STAFF-ONLY BY DESIGN. "external-client" here meant
  // SuperAdmin/StatesideBiller/OffshoreBiller stopped being offered the surface
  // while Client and ClientBiller kept it as their landing view -- so the tab
  // reappeared the moment anyone looked at the portal as a client, including
  // through the role switcher, which is how it was seen again today. Nothing
  // regressed and nothing "came back"; the rule was narrower than remembered.
  //
  // "retired" is the whole answer: viewOfferedToRole returns false for every
  // role, internal and external, so no sidebar paints it, no landing resolves
  // to it, and a stale ?view=clientHome or persisted state.view normalizes away.
  //
  // WHERE THE ROLES GO INSTEAD. A practice owner still lands on Reports, which
  // is the money view Client Home was a smaller copy of. A Redacted Client -- no
  // reports grant -- lands on All Data, which is the same answer the owner gave
  // for internal staff in 2026-08-06: "it should just start with the all data".
  // firstAccessibleView owns that and verify_client_home_is_not_internal_nav.js
  // proves EVERY role still has a landing view that is in its own sidebar.
  //
  // THE client:home PERMISSION IS STILL DELIBERATELY NOT REVOKED, and this
  // decision does not change that. Nav visibility and the permission model are
  // different axes. SuperAdmin's frontend grant set IS `allPermissions` by
  // construction, so revoking one grant means inventing an exception list to
  // "SuperAdmin holds everything" -- a new drift surface, and a new way for the
  // frontend and portalRoleCatalog.js to disagree -- in order to fix a nav
  // problem. It would also make a ?view=clientHome deep link answer "you do not
  // have permission" to the one account that has every permission, and write
  // that false denial into the access-decision audit. The security review's
  // client-safe surface list therefore still names Client Home: a client
  // legitimately HOLDS that grant, and reporting otherwise would be the lie.
  //
  // THE CODE BEHIND IT IS KEPT, NOT DELETED. renderClientHome and #clientHomeView
  // stay put. Retiring a surface is one line here; deleting four hundred is a
  // different change with a different blast radius, and the owner asked for the
  // tab to be gone, not for the code to be ripped out.
  clientHome: "retired",
  // Denial history is internal TMS operating guidance. A permission override
  // may narrow staff access but can never offer this surface to a client role.
  denialIntelligence: "internal-staff",
  backupHistory: "internal-staff",
  companyOversight: "internal-staff"
};
const DEFAULT_VIEW_AUDIENCE = "any";

const navigationSections = [
  { key: "operations", views: ["companyOversight", "clientHome", "workbench", "assignments", "authorizations", "credentialing", "clientActionPending", "denialIntelligence"] },
  { key: "analytics", views: ["reports", "trackerExport", "statements"] },
  { key: "help", views: ["training"] },
  { key: "setup", views: ["feeSchedules", "missingRates"] },
  { key: "governance", views: [] },
  // Internal tooling sits in a muted final "System" section (2026-07-14):
  // same permissions as before, just less nav parity with daily surfaces.
  { key: "system", views: ["queryMap", "feedback", "backupHistory", "audit"] }
];

// SIDEBAR ENTRIES THAT ARE NOT VIEWS. The admin surfaces that open a MODAL
// instead of routing to a view panel, so they cannot live in navigationSections
// above - every entry there is a view key, and the section gate up there is
// "does this role hold a grant for any of these views".
//
// THEY HANG OFF THE SHARED USERS & ACCESS SECTION KEY RATHER THAN INVENTING ONE.
// The former read-only Access destination duplicated the authoritative roster
// already used by Manage Users, so the section now contains only operational
// user/group actions while backend authorization remains unchanged.
//
// The GROUP is still gated, never only its buttons: a labelled heading standing
// over an empty body is a visible ghost of an admin surface the session cannot
// open, and it names the surface just as loudly as the button would have. On a
// SHARED key that gate is a union (see applyNavVisibility) - the heading renders
// if the Access view OR this action is permitted - so the entry keeps carrying
// its own SuperAdmin-only gate and the heading is never the thing that leaks.
//
// S4's Manage Groups entry landed here, exactly as that slot predicted: one
// gate below, one more key in the actions list, one more button in index.html
// beside #manageUsersBtn. Manage Users is available to the scoped internal
// manager roles; Manage Groups remains exact-SuperAdmin-only.
const navActionGates = {
  manageUsers: () => isTrustedUserManagerSession(),
  manageGroups: () => isTrustedSuperAdminSession() && hasPermission(PERMISSIONS.ORGANIZATIONS_ONBOARD),
  emailNotifications: () => isTrustedSuperAdminSession(),
  // Server-decided: shown only after /api/client-api-access/status answers 200.
  apiAccess: () => clientApiAccessUiAvailable()
};

const navActionSections = [
  { key: "governance", actions: ["manageUsers", "manageGroups", "apiAccess"] },
  { key: "system", actions: ["emailNotifications"] }
];

const uiNavViews = new Set(["workbench", "reports"]);

const allPermissions = Object.values(PERMISSIONS);
const accessAdminPermissionOptions = Array.from(new Set([
  ...allPermissions,
  "claim:view",
  "reports:view"
])).sort();

const permissionGroups = [
  { key: "views", label: "Views", permissions: [PERMISSIONS.VIEW_WORKBENCH, PERMISSIONS.VIEW_ASSIGNMENTS, PERMISSIONS.VIEW_REVIEWS, PERMISSIONS.VIEW_CLIENT, PERMISSIONS.VIEW_REPORTS, PERMISSIONS.VIEW_FEE_SCHEDULES, PERMISSIONS.VIEW_FOLLOW_UP_RULES, PERMISSIONS.VIEW_QUERY_MAP, PERMISSIONS.VIEW_SECURITY, PERMISSIONS.VIEW_AUDIT] },
  { key: "data", label: "Sensitive Data", permissions: [PERMISSIONS.VIEW_FINANCIALS, PERMISSIONS.VIEW_PHI, PERMISSIONS.VIEW_INTERNAL_NOTES] },
  { key: "reports", label: "Reports / Exports", permissions: [PERMISSIONS.VIEW_REPORTS, PERMISSIONS.REPORT_EXPORT, PERMISSIONS.EXPORT_WORKFLOW, PERMISSIONS.IMPORT_WORKFLOW] },
  { key: "workflow", label: "Claim Workflow", permissions: [PERMISSIONS.ASSIGN_CLAIMS, PERMISSIONS.UPDATE_FOLLOW_UP, PERMISSIONS.SUBMIT_REVIEW, PERMISSIONS.APPROVE_REVIEW, PERMISSIONS.MANAGE_WRITE_OFFS, PERMISSIONS.ADD_INTERNAL_NOTE, PERMISSIONS.VIEW_CLAIM_NOTES, PERMISSIONS.ADD_CLAIM_NOTE, PERMISSIONS.CORRECT_OWN_CLAIM_NOTE, PERMISSIONS.VOID_CLAIM_NOTE, PERMISSIONS.VIEW_CLAIM_NOTE_AUDIT, PERMISSIONS.SEARCH_CLAIM_NOTES, PERMISSIONS.EXPORT_CLAIM_NOTES] },
  { key: "setup", label: "Setup", permissions: [PERMISSIONS.MANAGE_FEE_SCHEDULES, PERMISSIONS.MANAGE_FOLLOW_UP_RULES] },
  { key: "client", label: "Client Ops", permissions: [PERMISSIONS.MARK_STATEMENTS, PERMISSIONS.CLIENT_HOME, PERMISSIONS.CLIENT_PAYMENTS, PERMISSIONS.CLIENT_WORKBENCH] },
  { key: "statements", label: "Statements", permissions: [PERMISSIONS.STATEMENTS_VIEW, PERMISSIONS.STATEMENTS_GENERATE, PERMISSIONS.STATEMENTS_RECONCILIATION_VIEW] },
  { key: "authorizations", label: "Authorizations", permissions: [PERMISSIONS.AUTHORIZATIONS_VIEW, PERMISSIONS.AUTHORIZATIONS_MANAGE, PERMISSIONS.AUTHORIZATIONS_SUBMIT] },
  { key: "credentialing", label: "Credentialing", permissions: [PERMISSIONS.CREDENTIALING_VIEW, PERMISSIONS.CREDENTIALING_MANAGE, PERMISSIONS.CREDENTIALING_SUBMIT] },
  { key: "clientAction", label: "Client Action Pending", permissions: [PERMISSIONS.CLIENT_ACTION_VIEW, PERMISSIONS.CLIENT_ACTION_RESPOND, PERMISSIONS.CLIENT_ACTION_MANAGE, PERMISSIONS.CLIENT_ACTION_RESOLVE] },
  { key: "denialIntelligence", label: "Denial Intelligence", permissions: [PERMISSIONS.DENIAL_INTELLIGENCE_VIEW, PERMISSIONS.DENIAL_INTELLIGENCE_EVIDENCE_VIEW] },
  { key: "security", label: "Security", permissions: [PERMISSIONS.VIEW_SECURITY, PERMISSIONS.SECURITY_AUDIT] },
  { key: "accessAdmin", label: "Access Admin", permissions: [PERMISSIONS.MANAGE_ACCESS_GRANTS, PERMISSIONS.MANAGE_PERMISSION_OVERRIDES] },
  { key: "userGroupGovernance", label: "User and Group Governance", permissions: [PERMISSIONS.MANAGE_USERS, PERMISSIONS.MANAGE_GROUPS, PERMISSIONS.ORGANIZATIONS_ONBOARD, PERMISSIONS.ORGANIZATIONS_CREDENTIALS_ROTATE] }
];

// FIVE canonical roles. Legacy Admin and ClientBiller values normalize below.
const roleProfiles = {
  SuperAdmin: { label: "Super Admin", scope: { allOrgs: true, crossTenant: true }, permissions: allPermissions },
  StatesideBiller: {
    label: "Stateside Biller", scope: { crossTenant: false },
    permissions: allPermissions.filter((permission) => ![PERMISSIONS.MANAGE_GROUPS, PERMISSIONS.ORGANIZATIONS_ONBOARD, PERMISSIONS.ORGANIZATIONS_CREDENTIALS_ROTATE, PERMISSIONS.MANAGE_ACCESS_GRANTS, PERMISSIONS.MANAGE_PERMISSION_OVERRIDES,
      PERMISSIONS.MARK_STATEMENTS, PERMISSIONS.STATEMENTS_GENERATE].includes(permission))
  },
  OffshoreBiller: {
    label: "Offshore Biller", scope: { crossTenant: false },
    permissions: [PERMISSIONS.VIEW_WORKBENCH, PERMISSIONS.VIEW_ASSIGNMENTS, PERMISSIONS.VIEW_REVIEWS, PERMISSIONS.VIEW_FEE_SCHEDULES,
      PERMISSIONS.VIEW_PHI, PERMISSIONS.VIEW_FINANCIALS, PERMISSIONS.VIEW_INTERNAL_NOTES,
      PERMISSIONS.EXPORT_WORKFLOW, PERMISSIONS.IMPORT_WORKFLOW, PERMISSIONS.ASSIGN_CLAIMS,
      PERMISSIONS.UPDATE_FOLLOW_UP, PERMISSIONS.SUBMIT_REVIEW,
      PERMISSIONS.ADD_INTERNAL_NOTE, PERMISSIONS.VIEW_CLAIM_NOTES, PERMISSIONS.ADD_CLAIM_NOTE,
      PERMISSIONS.CORRECT_OWN_CLAIM_NOTE, PERMISSIONS.VIEW_CLAIM_NOTE_AUDIT,
      PERMISSIONS.SEARCH_CLAIM_NOTES, PERMISSIONS.EXPORT_CLAIM_NOTES,
      PERMISSIONS.AUTHORIZATIONS_VIEW, PERMISSIONS.AUTHORIZATIONS_SUBMIT,
      PERMISSIONS.CREDENTIALING_VIEW, PERMISSIONS.CREDENTIALING_SUBMIT, PERMISSIONS.CLIENT_ACTION_VIEW, PERMISSIONS.CLIENT_ACTION_RESPOND, PERMISSIONS.CLIENT_ACTION_MANAGE,
      PERMISSIONS.DENIAL_INTELLIGENCE_VIEW, PERMISSIONS.DENIAL_INTELLIGENCE_EVIDENCE_VIEW]
  },
  Client: {
    label: "Client", scope: {},
    permissions: [PERMISSIONS.VIEW_WORKBENCH, PERMISSIONS.VIEW_REPORTS, PERMISSIONS.VIEW_FINANCIALS, PERMISSIONS.REPORT_EXPORT, PERMISSIONS.VIEW_OWN_FEE_SCHEDULE,
      PERMISSIONS.VIEW_PHI, PERMISSIONS.CLIENT_HOME, PERMISSIONS.CLIENT_PAYMENTS, PERMISSIONS.CLIENT_WORKBENCH,
      PERMISSIONS.VIEW_CLAIM_NOTES, PERMISSIONS.SEARCH_CLAIM_NOTES, PERMISSIONS.EXPORT_CLAIM_NOTES,
      PERMISSIONS.STATEMENTS_VIEW, PERMISSIONS.STATEMENTS_GENERATE, PERMISSIONS.MARK_STATEMENTS,
      PERMISSIONS.AUTHORIZATIONS_VIEW, PERMISSIONS.AUTHORIZATIONS_MANAGE,
      PERMISSIONS.CREDENTIALING_VIEW, PERMISSIONS.CREDENTIALING_MANAGE,
      PERMISSIONS.CLIENT_ACTION_VIEW, PERMISSIONS.CLIENT_ACTION_RESPOND]
  },
  RedactedClient: {
    label: "Redacted Client", scope: {},
    permissions: [PERMISSIONS.VIEW_WORKBENCH, PERMISSIONS.VIEW_PHI, PERMISSIONS.CLIENT_HOME, PERMISSIONS.CLIENT_WORKBENCH,
      PERMISSIONS.VIEW_CLAIM_NOTES, PERMISSIONS.SEARCH_CLAIM_NOTES, PERMISSIONS.EXPORT_CLAIM_NOTES,
      PERMISSIONS.STATEMENTS_VIEW, PERMISSIONS.STATEMENTS_GENERATE, PERMISSIONS.MARK_STATEMENTS,
      PERMISSIONS.AUTHORIZATIONS_VIEW, PERMISSIONS.AUTHORIZATIONS_MANAGE,
      PERMISSIONS.CREDENTIALING_VIEW, PERMISSIONS.CREDENTIALING_MANAGE,
      PERMISSIONS.CLIENT_ACTION_VIEW, PERMISSIONS.CLIENT_ACTION_RESPOND]
  }
};

// WHICH ROLES REQUIRE A BOOK — mirrors ROLE_REQUIRES_BOOK in
// backend/portalRoleCatalog.js.
//
// An org list is a TENANT BOUNDARY: which practices exist for you. A BOOK is
// which of that work is yours. The backend re-derives this requirement from the
// ROLE on every request and answers a bookless principal with `1 = 0` plus a
// typed 403, so a bookless account is not a degraded account — it is a dead one,
// and creating one is a provisioning bug rather than a quiet day.
//
// Held as a SET and asked through a predicate, never spelled `role ===
// "OffshoreBiller"` at each call site. That literal is the same trap
// isExternalClientRole exists to close: on the day a second role needs a book,
// every hardcoded site still reads correct in isolation while collectively
// letting the new role through unbooked.
const ROLE_REQUIRES_BOOK = Object.freeze([]);

function roleRequiresBook(role) {
  return ROLE_REQUIRES_BOOK.includes(canonicalRole(role));
}

// Mirrors portalRoleCatalog.scopeHasBook: payerNames OR clientNames OR
// assignedOnly.
//
// orgIds deliberately does NOT count. Counting it would be the tempting bug --
// every offshore biller already has orgIds, so the UI would agree the account
// was fine while every request it made came back denied. The frontend's answer
// to "does this have a book?" has to be the backend's answer, or the gate is
// theatre.
function scopeHasBook(scope = {}) {
  // Laxer than the backend's stringList on ONE axis, deliberately: this also
  // reads the Edit rail's comma-separated string draft, where the backend only
  // accepts arrays. Safe because portalInvitePayloadFromDraft normalizes to an
  // array before anything is sent, so the API never sees the string form.
  return normalizeUserScopeList(scope.payerNames).length > 0
    || normalizeUserScopeList(scope.clientNames).length > 0
    // booleanFlag, not Boolean: the backend accepts only true/1/"1"/"true"/
    // "yes"/"on", so plain truthiness would read the string "false" as a book
    // and let the UI pass a draft the API refuses — the theatre this mirror
    // exists to prevent.
    || [true, 1, "1", "true", "yes", "on"].includes(typeof scope.assignedOnly === "string" ? scope.assignedOnly.trim().toLowerCase() : scope.assignedOnly);
}

// Backwards-compat aliases so any persisted role from the old set degrades
// gracefully on load (mapped to the closest canonical role). These deliberately
// still point at Client and NOT ClientBiller: a legacy alias must never
// silently upgrade an existing external login into the grid role.
const __legacyRoleAlias = {
  Admin: "StatesideBiller", TenantOwner: "StatesideBiller", Manager: "StatesideBiller",
  BillingManager: "StatesideBiller", Biller: "StatesideBiller", Offshore: "OffshoreBiller",
  ClientBiller: "RedactedClient", ReadOnly: "Client", Provider: "Client"
};

// FOLLOW-UP ACTION MATRIX — the biller-facing picker.
//
// AUTHORITATIVE 26-action CT-TRACKER matrix (CT_TRACKER_EXTRACTED_VALUES.md §1, extracted from the
// real `LITE Follow-Up Matrix` sheet of `TMS Claims Tracker Light.xlsm`). This is an EMBEDDED
// projection of the ONE source of truth, backend/followUpActionCatalog.js — app.js must run with no
// backend (demo/offline), so it cannot fetch it. Drift is impossible to ship:
// backend/verify_follow_up_action_catalog.js parses this exact literal and fails if it does not
// match DEFAULT_FOLLOW_UP_MATRIX action-for-action, offset-for-offset, flag-for-flag.
// EDIT THE CATALOG, THEN REGENERATE THIS BLOCK — never hand-edit one side.
//
// Before 2026-07-22 this was a 9-action scaffold that did not even contain "Insurance Follow-Up" --
// the most-used action and the one our own stale-insurance sweep auto-writes — so billers could see
// actions in the grid they could not set, filter to, or reproduce
// (CT_TRACKER_PARITY_AUDIT_2026-07-22 finding 3-A).
//
// Ordering: block 1 keeps the previous picker order, with Insurance Follow-Up promoted to first;
// block 2 appends the 16 restored actions grouped by family. `systemOnly` actions are auto-added
// only and are HIDDEN from every manual picker (CT: workflow_app_prototype.py:329-330) while
// remaining visible in filters/slicers so a biller can still find that work.
const actionRules = [
  { action: "Insurance Follow-Up", offset: 30, removeIfPaid: true },
  { action: "AUDIT", offset: null, removeIfPaid: true },
  { action: "Payment Processing", offset: 14, removeIfPaid: true },
  { action: "Escalate", offset: 5, removeIfPaid: false },
  { action: "Submit To Secondary", offset: 14, removeIfPaid: true },
  { action: "Primary Claim In Process", offset: 30, removeIfPaid: true },
  { action: "Appeal to Primary", offset: 7, removeIfPaid: false },
  { action: "Client Action Pending-Authorization", offset: 7, removeIfPaid: true },
  { action: "Claim Correction Required", offset: 5, removeIfPaid: false },
  { action: "Write-Off", offset: null, removeIfPaid: false },
  { action: "Resubmit To Primary", offset: 1, removeIfPaid: false },
  { action: "Resubmit to Secondary", offset: 1, removeIfPaid: false },
  { action: "Appeal to Secondary", offset: 7, removeIfPaid: false },
  { action: "Secondary Claim in Process", offset: 30, removeIfPaid: true },
  { action: "Claim Correction In Process", offset: 21, removeIfPaid: false },
  { action: "Escalation Follow Up", offset: 21, removeIfPaid: true },
  { action: "Client Action Pending", offset: 7, removeIfPaid: true },
  { action: "Client Action Pending-Coverage", offset: 7, removeIfPaid: true },
  { action: "Client Action Pending-Credentialing", offset: 7, removeIfPaid: true },
  { action: "Client Action Pending-COB", offset: 7, removeIfPaid: false },
  { action: "Client Action Pending-EVV", offset: 7, removeIfPaid: true },
  { action: "Internal Review Required", offset: 7, removeIfPaid: false },
  { action: "AR Project", offset: null, removeIfPaid: true },
  { action: "EPSDT", offset: null, removeIfPaid: true },
  { action: "Payment Sent To Patient", offset: null, removeIfPaid: true },
  { action: "Claim Status Checkup Required", offset: 3, removeIfPaid: true, systemOnly: true },
  // PORTAL-NATIVE (CT_TRACKER_EXTRACTED_VALUES.md §1b), and system-only for the same reason
  // the checkup is: the picker never offers it, so an action on the grid that reads
  // "Underpayment Review Required" can only have been written by the sweep, never by a
  // colleague. Mirrors backend/followUpActionCatalog.js — the verify compares them exactly.
  { action: "Underpayment Review Required", offset: 30, removeIfPaid: true, systemOnly: true },
  { action: "Missing Fee Schedule", offset: 1, removeIfPaid: false, systemOnly: true },
  // PORTAL-NATIVE (Nathan, 2026-10-20): the sweep moves a 14-day-old Submit To Secondary here.
  { action: "Submitted To Secondary", offset: 30, removeIfPaid: true, systemOnly: true }
];

// Legacy/imported action aliases — CT import_tenant_workflow_from_client_trackers.py:89-116.
// Keys are ALREADY normalized (uppercase, every "-" → " - ", whitespace collapsed). "" = CT drops it.
// Mirror of FOLLOW_UP_ACTION_ALIASES in backend/followUpActionCatalog.js (drift-guarded by the verify).
const FOLLOW_UP_ACTION_ALIASES = {
  "WRITE - OFF - DUPLICATE": "Write-Off",
  "WRITE - OFF - NO AUTHORIZATION": "Write-Off",
  "WRITE - OFF APPROVED": "Write-Off",
  "WRITE - OFF - PR BALANCE": "Write-Off",
  "CLIENT ACTION PENDING - COVERAGE - EVV": "Client Action Pending-EVV",
  "CLIENT ACTION PENDING - AUTHARAZATION": "Client Action Pending-Authorization",
  "CLIENT ACTION PENDING - EVV": "Client Action Pending-EVV",
  "CLIENT ACTION PENDING - COVERAGE": "Client Action Pending-Coverage",
  "CLIENT ACTION PENDING - COB": "Client Action Pending-COB",
  "DUE FROM PATIENT": "",
  "MISSING PATIENT MEDICAL RECORD FOR THIS SERVICE.": "",
  "TRICARE AUDIT CLAIM CORRECTION IN PROCESS - CREATED CHARGE AMOUNT": ""
};

function normalizeFollowUpActionKey(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/–|—/g, "-")
    .toUpperCase()
    .replace(/-/g, " - ")
    .replace(/\s+/g, " ")
    .trim();
}

// Resolve any raw/legacy/imported action string to its canonical matrix action name.
// "" = deliberately dropped by CT; null = genuinely not a matrix action (leave it alone).
function canonicalFollowUpAction(value, rules) {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const list = Array.isArray(rules) ? rules : (() => { try { return getActionRules(); } catch (_) { return actionRules; } })();
  if (list.some((rule) => rule.action === raw)) return raw;
  const key = normalizeFollowUpActionKey(raw);
  if (!key) return null;
  const hit = list.find((rule) => normalizeFollowUpActionKey(rule.action) === key);
  if (hit) return hit.action;
  if (Object.prototype.hasOwnProperty.call(FOLLOW_UP_ACTION_ALIASES, key)) {
    const alias = FOLLOW_UP_ACTION_ALIASES[key];
    if (alias === "") return "";
    return list.some((rule) => rule.action === alias) ? alias : null;
  }
  const writeOffKey = normalizeFollowUpActionKey("Write-Off");
  if (key === writeOffKey || key.indexOf(writeOffKey + " ") === 0) {
    return list.some((rule) => rule.action === "Write-Off") ? "Write-Off" : null;
  }
  const capKey = normalizeFollowUpActionKey("Client Action Pending");
  if (key === capKey || key.indexOf(capKey + " ") === 0) {
    return list.some((rule) => rule.action === "Client Action Pending") ? "Client Action Pending" : null;
  }
  return null;
}

// Employee-specific follow-up action access. The server is the enforcement
// authority; this projection keeps every manual picker honest and makes the
// administrator's saved intent legible. Missing entries in an explicit policy
// are denied, so a newly cataloged action appears in Manage Users but cannot
// silently become usable before an administrator reviews it.
const FOLLOW_UP_ACTION_POLICY_MODES = Object.freeze(["denied", "approval_required", "direct"]);
const FOLLOW_UP_ACTION_POLICY_LEGACY_BASELINE = Object.freeze([
  "Insurance Follow-Up", "AUDIT", "Payment Processing", "Escalate", "Submit To Secondary",
  "Primary Claim In Process", "Appeal to Primary", "Client Action Pending-Authorization",
  "Claim Correction Required", "Write-Off", "Resubmit To Primary", "Resubmit to Secondary",
  "Appeal to Secondary", "Secondary Claim in Process", "Claim Correction In Process",
  "Escalation Follow Up", "Client Action Pending", "Client Action Pending-Coverage",
  "Client Action Pending-Credentialing", "Client Action Pending-COB", "Client Action Pending-EVV",
  "Internal Review Required", "AR Project", "EPSDT", "Payment Sent To Patient"
]);
const FOLLOW_UP_ACTION_POLICY_LEGACY_SET = new Set(FOLLOW_UP_ACTION_POLICY_LEGACY_BASELINE);

// THE GRID ROW BUILD'S POLICY MEMO -----------------------------------------
//
// Measured on the QA harness, API lane, 500 rows x 48 columns: 3.2s of a
// 4.5s renderClaimsTable was spent inside normalizeFollowUpRule,
// normalizeFollowUpRules and followUpActionPolicyForRole. None of that answer
// is per-row - the follow-up rule matrix and the actor's action policy are
// identical for every row on the page - but the Follow up Action cell rebuilds
// its <select> per row, and each of its ~24 options calls
// followUpActionChoiceLabel(), which resolves the entire policy again. That is
// ~50 structuredClone()d copies of the matrix PER ROW, ~25,000 for one page.
//
// The memo's lifetime is ONE SYNCHRONOUS ROW BUILD and nothing else: it is
// opened immediately before the rows are built and closed in a finally. It
// cannot go stale, because state.followUpRules, the role and the authenticated
// scope are only ever written from event handlers and fetch callbacks, and none
// of those can run between the first and the last read inside one synchronous
// pass. Outside that window every reader computes exactly as it always did -
// which is why the policy verifier's push-a-rule-then-read-again case still
// sees the new rule.
let followUpPolicyBuildMemo = null;

function beginFollowUpPolicyBuildMemo() {
  const previous = followUpPolicyBuildMemo;
  followUpPolicyBuildMemo = {
    actionRules: null,
    manualRules: null,
    policy: null,
    selectable: null,
    modes: new Map(),
    optionsHtml: new Map(),
    rowMoneyCertainty: typeof WeakMap === "function" ? new WeakMap() : null
  };
  return previous;
}

function endFollowUpPolicyBuildMemo(previous) {
  followUpPolicyBuildMemo = previous || null;
}

function manualFollowUpActionRules() {
  const memo = followUpPolicyBuildMemo;
  if (memo && memo.manualRules) return memo.manualRules;
  const rules = getActionRules().filter((rule) => !rule.systemOnly);
  if (memo) memo.manualRules = rules;
  return rules;
}

function normalizeFollowUpActionPolicyMode(value) {
  const mode = String(value || "").trim().toLowerCase();
  return FOLLOW_UP_ACTION_POLICY_MODES.includes(mode) ? mode : "denied";
}

function followUpActionRoleDefaultMode(role, action) {
  const canonicalRoleName = canonicalRole(role);
  if (!FOLLOW_UP_ACTION_POLICY_LEGACY_SET.has(String(action || ""))) return "denied";
  if (canonicalRoleName === "OffshoreBiller") return action === "Write-Off" ? "denied" : "approval_required";
  if (canonicalRoleName === "SuperAdmin" || canonicalRoleName === "StatesideBiller") return "direct";
  return "denied";
}

function followUpActionPolicySource(value = {}) {
  const outer = value && typeof value === "object" ? value : {};
  const source = outer.followUpActionAccess || outer.followUpActionPolicy || outer;
  const actions = source && typeof source === "object" ? source.actions : null;
  const map = {};
  let explicit = false;
  if (Array.isArray(actions)) {
    explicit = true;
    actions.forEach((entry) => {
      const action = String(entry?.action || "").trim();
      if (!action) return;
      const projected = entry?.mode || (entry?.canApplyDirectly ? "direct" : (entry?.requiresApproval ? "approval_required" : "denied"));
      map[action] = normalizeFollowUpActionPolicyMode(projected);
    });
  } else if (actions && typeof actions === "object") {
    explicit = true;
    Object.entries(actions).forEach(([action, mode]) => { map[String(action)] = normalizeFollowUpActionPolicyMode(mode); });
  }
  return { explicit, map };
}

function followUpActionPolicyForRole(value = {}, role = "") {
  const source = followUpActionPolicySource(value);
  const actions = {};
  manualFollowUpActionRules().forEach((rule) => {
    actions[rule.action] = source.explicit
      ? normalizeFollowUpActionPolicyMode(source.map[rule.action])
      : followUpActionRoleDefaultMode(role, rule.action);
  });
  return { version: 1, actions };
}

function followUpActionPolicyPreset(role = "", preset = "role_default") {
  const actions = {};
  manualFollowUpActionRules().forEach((rule) => {
    let mode = followUpActionRoleDefaultMode(role, rule.action);
    if (preset === "restricted") mode = rule.action === "Write-Off" ? "denied" : "approval_required";
    if (preset === "supervised") mode = "approval_required";
    if (preset === "trusted") mode = "direct";
    if (preset === "hidden") mode = "denied";
    if (isExternalClientRole(role)) mode = "denied";
    actions[rule.action] = mode;
  });
  return { version: 1, actions };
}

function followUpActionPolicyCounts(policy = {}) {
  const counts = { denied: 0, approval_required: 0, direct: 0 };
  manualFollowUpActionRules().forEach((rule) => {
    const mode = normalizeFollowUpActionPolicyMode(policy?.actions?.[rule.action]);
    counts[mode] += 1;
  });
  return counts;
}

function sanitizeFollowUpActionAccessProjection(value) {
  const source = followUpActionPolicySource(value);
  if (!source.explicit) return null;
  const policy = followUpActionPolicyForRole(value, "Client");
  const counts = followUpActionPolicyCounts(policy);
  return {
    version: 1,
    digest: String(value?.digest || value?.followUpActionAccess?.digest || ""),
    counts,
    actions: manualFollowUpActionRules().map((rule) => {
      const mode = policy.actions[rule.action];
      return { action: rule.action, mode, visible: mode !== "denied", requiresApproval: mode === "approval_required", canApplyDirectly: mode === "direct" };
    })
  };
}

function currentFollowUpActionPolicy() {
  const memo = followUpPolicyBuildMemo;
  if (memo && memo.policy) return memo.policy;
  const policy = resolveCurrentFollowUpActionPolicy();
  if (memo) memo.policy = policy;
  return policy;
}

function resolveCurrentFollowUpActionPolicy() {
  const role = canonicalRole(state?.role);
  const auth = authenticatedScopeForRole(role);
  if (auth?.followUpActionAccess) return followUpActionPolicyForRole(auth.followUpActionAccess, role);
  const user = typeof currentUser === "function" ? currentUser() : null;
  return followUpActionPolicyForRole(user?.followUpActionAccess || user?.followUpActionPolicy || {}, role);
}

function currentFollowUpActionMode(action) {
  const memo = followUpPolicyBuildMemo;
  const memoKey = memo ? String(action == null ? "" : action) : null;
  if (memoKey !== null && memo.modes.has(memoKey)) return memo.modes.get(memoKey);
  const canonical = canonicalFollowUpAction(action, manualFollowUpActionRules());
  const mode = canonical ? normalizeFollowUpActionPolicyMode(currentFollowUpActionPolicy().actions[canonical]) : "";
  if (memoKey !== null) memo.modes.set(memoKey, mode);
  return mode;
}

function followUpActionIsRestrictedCurrent(action) {
  return Boolean(String(action || "").trim()) && currentFollowUpActionMode(action) === "denied";
}

function followUpActionChoiceMatches(key, label, search = "") {
  const text = String(label == null ? "" : label).trim().toLocaleLowerCase();
  const query = String(search == null ? "" : search).trim().toLocaleLowerCase();
  return !query || (key === "action" ? text.startsWith(query) : text.includes(query));
}

function compareFollowUpActionChoices(left, right) {
  return String(left).localeCompare(String(right), undefined, { sensitivity: "base", numeric: true });
}

const followUpActionSearchControls = new WeakMap();
const FOLLOW_UP_ACTION_SEARCH_SELECTORS = '#followUpEditorAction, #bulkAction, select[data-action-edit], select[data-drawer-action-edit], select[data-offshore-next-action], select[data-next-action], [data-cap-workflow] select[name="action"], #oversightDialog select[name="action"], #oversightDialog select[name="suggested"]';
let followUpActionSearchObserver = null;
let followUpActionChoiceSequence = 0;

function enhanceFollowUpActionSelect(select) {
  if (!select) return;
  const existing = followUpActionSearchControls.get(select);
  if (existing) { existing.refresh(); return; }
  // Read-mode grid cells need no extra controls until their pill is activated.
  const gridCell = select.closest(".followup-cell");
  if (gridCell && !gridCell.classList.contains("is-editing")) return;
  // Adding another input must not steal the native select's implicit label.
  const labels = Array.from(select.labels || []);
  if (!select.hasAttribute("aria-label")) {
    const name = labels.map((label) => {
      const copy = label.cloneNode(true);
      copy.querySelectorAll("select, input, textarea, button").forEach((control) => control.remove());
      return String(copy.textContent || "").trim();
    }).filter(Boolean).join(" ");
    select.setAttribute("aria-label", name || "Follow-up action");
  }
  if (labels.length && !select.id) select.id = "followUpActionChoice-" + (++followUpActionChoiceSequence);
  labels.forEach((label) => { if (!label.htmlFor) label.htmlFor = select.id; });
  const wrapper = document.createElement("div");
  wrapper.className = "followup-action-choice-search";
  const input = document.createElement("input");
  input.type = "search";
  input.autocomplete = "off";
  input.placeholder = "Type the start of an action";
  input.setAttribute("aria-label", "Search follow-up actions by name");
  input.dataset.followupActionSearch = "";
  const status = document.createElement("span");
  status.className = "followup-action-choice-status";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  const originalHidden = new WeakMap();
  const refresh = () => {
    input.disabled = select.disabled;
    const query = input.value.trim();
    let count = 0;
    Array.from(select.options).forEach((option) => {
      if (!originalHidden.has(option)) originalHidden.set(option, option.hidden);
      const action = Boolean(option.value && option.value !== "None");
      const matches = !query || (action && followUpActionChoiceMatches("action", option.value, query));
      option.hidden = originalHidden.get(option) || !matches;
      if (!option.hidden && !option.disabled && action) count += 1;
    });
    status.textContent = query ? count ? count + (count === 1 ? " matching action" : " matching actions") : "No matching actions" : "";
    status.hidden = !query;
  };
  const reset = () => { input.value = ""; refresh(); };
  input.addEventListener("input", refresh);
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); reset(); }
    if (event.key === "Enter" || event.key === "ArrowDown") {
      event.preventDefault();
      select.focus();
      if (typeof select.showPicker === "function") { try { select.showPicker(); } catch (_) {} }
    }
  });
  select.addEventListener("change", reset);
  wrapper.addEventListener("focusout", (event) => {
    if (wrapper.contains(event.relatedTarget)) return;
    const cell = select.closest(".followup-cell");
    if (cell) { cell.classList.remove("is-editing"); select.setAttribute("tabindex", "-1"); }
  });
  followUpActionSearchControls.set(select, { input, refresh });
  select.before(wrapper);
  wrapper.append(input, select, status);
  refresh();
}

function initializeFollowUpActionChoiceSearch() {
  if (followUpActionSearchObserver || typeof MutationObserver !== "function") return;
  document.querySelectorAll(FOLLOW_UP_ACTION_SEARCH_SELECTORS).forEach(enhanceFollowUpActionSelect);
  followUpActionSearchObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.target?.matches?.(FOLLOW_UP_ACTION_SEARCH_SELECTORS)) enhanceFollowUpActionSelect(record.target);
      Array.from(record.addedNodes || []).forEach((node) => {
        if (node.nodeType !== 1 || node.classList.contains("followup-action-choice-search")) return;
        if (node.matches(FOLLOW_UP_ACTION_SEARCH_SELECTORS)) enhanceFollowUpActionSelect(node);
        // Options and leaf controls cannot contain another action selector.
        if (/^(SELECT|OPTION|INPUT|TEXTAREA|BUTTON|IMG|SVG)$/.test(node.tagName)) return;
        node.querySelectorAll(FOLLOW_UP_ACTION_SEARCH_SELECTORS).forEach(enhanceFollowUpActionSelect);
      });
    });
  });
  followUpActionSearchObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["disabled"] });
}

function followUpActionChoiceLabel(action) {
  return currentFollowUpActionMode(action) === "approval_required" ? `${action} — Needs approval` : action;
}

function followUpActionOptionsHtml(currentAction = "", options = {}) {
  // Inside a row build the option list depends on nothing but the CURRENT
  // action, so one page of 500 rows builds at most a handful of distinct
  // <option> sets instead of 500 identical ones. Any caller that passes
  // label/clear overrides skips the memo and renders exactly as before.
  const memo = followUpPolicyBuildMemo;
  const memoKey = memo && options && Object.keys(options).length === 0
    ? String(currentAction || "").trim()
    : null;
  if (memoKey !== null && memo.optionsHtml.has(memoKey)) return memo.optionsHtml.get(memoKey);
  const rules = getSelectableActionRules();
  const current = String(currentAction || "").trim();
  const restricted = followUpActionIsRestrictedCurrent(current);
  const matched = rules.some((rule) => rule.action === current);
  const pinned = current && !matched
    ? `<option value="${escapeHtml(current)}" selected${restricted ? " disabled" : ""}>${escapeHtml(restricted ? `Restricted action — ${current}` : followUpOptionLabel(current))}</option>`
    : "";
  const clear = options.includeClear === false ? "" : `<option value="${escapeHtml(options.clearValue === undefined ? "" : options.clearValue)}" ${!current ? "selected" : ""}>${escapeHtml(options.clearLabel || (current ? "— Clear follow-up" : "—"))}</option>`;
  const html = clear + pinned + rules.map((rule) => `<option value="${escapeHtml(rule.action)}" ${rule.action === current ? "selected" : ""}>${escapeHtml(followUpActionChoiceLabel(rule.action))}</option>`).join("");
  if (memoKey !== null) memo.optionsHtml.set(memoKey, html);
  return html;
}

function followUpActionPolicyGroup(action = "") {
  const text = String(action);
  if (/Client Action|Credential|Coverage|COB|EVV/i.test(text)) return "Client requests";
  if (/Write-Off|EPSDT|AR Project|AUDIT|Internal Review|Escalat/i.test(text)) return "Review and exceptions";
  if (/Correction|Resubmit|Appeal/i.test(text)) return "Corrections and appeals";
  return "Payer follow-up";
}

function followUpActionPolicyGroupsHtml(policy = {}, search = "", idPrefix = "followUpPolicy") {
  const query = String(search || "").trim().toLowerCase();
  const groups = new Map();
  manualFollowUpActionRules().forEach((rule, index) => {
    if (query && !rule.action.toLowerCase().includes(query) && !followUpActionPolicyGroup(rule.action).toLowerCase().includes(query)) return;
    const group = followUpActionPolicyGroup(rule.action);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push({ rule, index });
  });
  if (!groups.size) return `<div class="followup-policy-empty">No action matches “${escapeHtml(search)}”.</div>`;
  const labels = { denied: "Hidden / no access", approval_required: "Needs approval", direct: "Direct" };
  return Array.from(groups.entries()).map(([group, rows]) => `
    <fieldset class="followup-policy-group">
      <legend>${escapeHtml(group)}</legend>
      ${rows.map(({ rule, index }) => {
        const mode = normalizeFollowUpActionPolicyMode(policy?.actions?.[rule.action]);
        const inputName = `${idPrefix}-${index}`;
        return `<div class="followup-policy-row">
          <div class="followup-policy-action"><strong>${escapeHtml(rule.action)}</strong><small>${Number.isFinite(rule.offset) ? `Default due in ${rule.offset} day${rule.offset === 1 ? "" : "s"}` : "No automatic due date"}</small></div>
          <div class="followup-policy-modes" role="radiogroup" aria-label="Access for ${escapeHtml(rule.action)}">
            ${FOLLOW_UP_ACTION_POLICY_MODES.map((choice) => `<label class="followup-policy-mode" data-mode="${choice}"><input type="radio" name="${escapeHtml(inputName)}" value="${choice}" data-followup-policy-mode data-followup-policy-action="${escapeHtml(rule.action)}"${mode === choice ? " checked" : ""}><span>${labels[choice]}</span></label>`).join("")}
          </div>
        </div>`;
      }).join("")}
    </fieldset>`).join("");
}

function renderFollowUpActionPolicyEditor(source = {}, role = "", options = {}) {
  if (isExternalClientRole(role)) return "";
  const policy = followUpActionPolicyForRole(source.followUpActionPolicy || source.followUpActionAccess || source, role);
  const counts = followUpActionPolicyCounts(policy);
  const idPrefix = options.idPrefix || "followUpPolicy";
  const search = String(source.followUpPolicySearch || "");
  return `<section class="followup-policy-editor${options.wide ? " followup-policy-editor--wide" : ""}" data-followup-policy-editor data-policy-prefix="${escapeHtml(idPrefix)}">
    <div class="followup-policy-head"><div><span>Follow-up actions</span><strong>Choose what this employee can do</strong></div><p>Hidden actions never appear in their work controls. Needs approval routes the saved work to a supervisor. Direct applies immediately. The server enforces every choice.</p></div>
    ${source.followUpPolicyNotice ? `<div class="followup-policy-notice" role="status">${escapeHtml(source.followUpPolicyNotice)}</div>` : ""}
    <div class="followup-policy-counts" aria-label="Follow-up action permission summary"><span data-tone="hidden">${counts.denied} hidden</span><span data-tone="approval">${counts.approval_required} need approval</span><span data-tone="direct">${counts.direct} direct</span></div>
    <div class="followup-policy-presets" aria-label="Permission presets">
      <button type="button" data-followup-policy-preset="restricted">Restricted new hire</button>
      <button type="button" data-followup-policy-preset="supervised">Supervised</button>
      <button type="button" data-followup-policy-preset="trusted">Trusted direct</button>
      <button type="button" data-followup-policy-preset="hidden">Hide all</button>
      <button type="button" data-followup-policy-preset="role_default">Reset to role default</button>
    </div>
    <label class="followup-policy-search"><span>Find an action</span><input type="search" value="${escapeHtml(search)}" placeholder="Search ${manualFollowUpActionRules().length} actions" data-followup-policy-search autocomplete="off"></label>
    <div class="followup-policy-groups" data-followup-policy-groups>${followUpActionPolicyGroupsHtml(policy, search, idPrefix)}</div>
    <p class="followup-policy-foot">New catalog actions start hidden until someone reviews them here.</p>
  </section>`;
}

function wireFollowUpActionPolicyEditor(host, source, role, rerender) {
  if (!host || !source) return;
  const ensurePolicy = () => {
    source.followUpActionPolicy = followUpActionPolicyForRole(source.followUpActionPolicy || source.followUpActionAccess || {}, role);
    return source.followUpActionPolicy;
  };
  host.querySelectorAll("[data-followup-policy-mode]").forEach((input) => input.addEventListener("change", () => {
    if (!input.checked) return;
    const policy = ensurePolicy();
    policy.actions[input.dataset.followupPolicyAction] = normalizeFollowUpActionPolicyMode(input.value);
    source.followUpPolicyNotice = "";
    rerender();
  }));
  host.querySelectorAll("[data-followup-policy-preset]").forEach((button) => button.addEventListener("click", () => {
    source.followUpActionPolicy = followUpActionPolicyPreset(role, button.dataset.followupPolicyPreset);
    source.followUpPolicyNotice = button.dataset.followupPolicyPreset === "role_default"
      ? "Reset to the safe default for this role. Review the actions below before saving."
      : "Preset applied to this draft. Nothing changes until you save.";
    rerender();
  }));
  const search = host.querySelector("[data-followup-policy-search]");
  search?.addEventListener("input", () => {
    source.followUpPolicySearch = search.value;
    const groups = host.querySelector("[data-followup-policy-groups]");
    if (groups) {
      groups.innerHTML = followUpActionPolicyGroupsHtml(ensurePolicy(), search.value, host.querySelector("[data-followup-policy-editor]")?.dataset.policyPrefix || "followUpPolicy");
      wireFollowUpActionPolicyEditor(groups, source, role, rerender);
    }
  });
}

// Per user direction: tenant/client labels group the workbench, but fee
// schedule rates themselves are org-ID scoped for writes and calculations.
const defaultFeeScheduleRows = [
  { tenantId: "RiverbendABA", orgId: "90000001", payer: "Aetna", state: "CT", service: "ABA", procedure: "97153", m1: "HN", m2: "", place: "11", rate: 72.25, startDate: "2025-01-01", endDate: "", network: "In Network", duplicateCheck: "Local Seed" },
  { tenantId: "RiverbendABA", orgId: "90000001", payer: "United", state: "CT", service: "ABA", procedure: "97155", m1: "HO", m2: "", place: "11", rate: 140, startDate: "2025-01-01", endDate: "", network: "In Network", duplicateCheck: "Local Seed" },
  { tenantId: "HarborPointABA", orgId: "90000004", payer: "Southland Health Plan", state: "GA", service: "ABA", procedure: "97153", m1: "HN", m2: "", place: "11", rate: 66.75, startDate: "2025-01-01", endDate: "", network: "In Network", duplicateCheck: "Local Seed" },
  { tenantId: "HarborPointABA", orgId: "90000004", payer: "Unity Health Plan", state: "GA", service: "ABA", procedure: "97155", m1: "HO", m2: "", place: "11", rate: 135, startDate: "2025-01-01", endDate: "", network: "OON", duplicateCheck: "Local Seed" },
  { tenantId: "CedarLaneABA", orgId: "90000005", payer: "Medicaid MD", state: "MD", service: "ABA", procedure: "97153", m1: "HN", m2: "GT", place: "02", rate: 62, startDate: "2025-01-01", endDate: "", network: "OON", duplicateCheck: "Local Seed" }
];

const emptySlicers = {
  renderingProvider: [],
  match: [],
  claimDue: [],
  followUpDue: [],
  primaryPayerClass: [],
  secondaryPayerClass: [],
  aging: [],
  action: [],
  primaryPayer: [],
  secondaryPayer: [],
  facility: [],
  procedure: [],
  claimStatus: [],
  serviceMonth: [],
  patClassification: [],
  secondaryCalc: [],
  claimClassification: [],
  network: []
};

let slicerDraftState = null;
// The DIRECTION is part of the same draft as the ticks: staging "exclude" and
// then pressing Cancel has to put the card back exactly as it was, and Apply has
// to move both halves at once or the grid would filter one way while the card
// shows the other.
let slicerDraftModeState = null;
let slicerDraftKey = "";
let slicerDraftReturnFocus = null;

// ---------------------------------------------------------------------------
// APPLIED UNLESS UNAPPLIED (owner, 2026-09-22).
// ---------------------------------------------------------------------------
//   "Across the entire portal, if I don't press Apply, it just doesn't apply.
//    Can the default be that it gets applied unless it's unapplied? Fewer
//    buttons to click."
//
// So a tick, an untick, Select all, Clear, the Include/Exclude switch and the
// Client Action Pending family button all commit BY THEMSELVES. The three
// things that made Apply worth keeping are kept instead of the button:
//
//   THE BURST IS ONE REQUEST. Ticking five boxes is one decision, not five, and
//   five queries against a 500-row page budget is how a filter rail becomes the
//   slowest thing on the screen. Every staged change restarts one timer, so a
//   burst issues exactly one query and at most one facet batch - asserted in
//   the browser proofs rather than described here.
//
//   THE CARD SAYS IT IS WORKING. "Applying..." appears the moment a change is
//   staged and stays until the commit has repainted, because a control that
//   silently does nothing for 400ms is the control the owner already reported
//   as broken once.
//
//   IT IS UNDOABLE. Escape and Cancel revert to the selection this card session
//   started from - not to "whatever auto-apply last wrote", which would make
//   Cancel a no-op - and one Undo restores the state from before the last
//   burst. Apply STAYS, as the immediate no-wait path: pressing it commits now
//   and closes the card, exactly as it always did.
//
// TYPING IS NOT TICKING. A number or date range is half-typed for most of the
// time it is being entered ("5", "50", "500"), so those boxes keep Apply and
// commit on blur or Enter - see wireColumnFilterRangeControls. The option
// SEARCH box never auto-applies either: it narrows which choices are on screen
// and changes no selection at all.
const FILTER_AUTO_APPLY_DELAY_MS = 400;
let filterCardAutoApplyTimer = null;
let filterCardAutoApplyKey = "";
// The selection this card session opened with. Cancel/Escape go back to THIS,
// which is what "revert to the last applied selection" has to mean once an
// auto-apply may have moved the applied state under the reader.
let slicerDraftBaseline = null;
// One level of undo, captured immediately before each auto-applied commit.
let filterCardUndoState = null;

function filterCardPanel(key) {
  return key ? document.querySelector(`[data-slicer-panel="${key}"]`) : null;
}

function markFilterCardApplying(key, applying) {
  const panel = filterCardPanel(key);
  if (!panel) return;
  panel.classList.toggle("is-applying", Boolean(applying));
}

function filterCardAutoApplyPending(key) {
  return Boolean(filterCardAutoApplyTimer) && filterCardAutoApplyKey === key;
}

function cancelFilterCardAutoApply() {
  if (filterCardAutoApplyTimer && typeof window !== "undefined") window.clearTimeout(filterCardAutoApplyTimer);
  filterCardAutoApplyTimer = null;
  const key = filterCardAutoApplyKey;
  filterCardAutoApplyKey = "";
  if (key) markFilterCardApplying(key, false);
}

// ONE TIMER, RESTARTED. Not one timer per change: a burst of ticks must settle
// into a single commit, and a commit that fired mid-burst would query for a
// selection the reader was still assembling.
function scheduleFilterCardAutoApply(key) {
  if (!key || slicerDraftKey !== key || typeof window === "undefined") return;
  cancelFilterCardAutoApply();
  if (!slicerDraftChanged(key)) return;
  filterCardAutoApplyKey = key;
  markFilterCardApplying(key, true);
  filterCardAutoApplyTimer = window.setTimeout(() => {
    filterCardAutoApplyTimer = null;
    filterCardAutoApplyKey = "";
    // STALE-SCOPE FENCED. The card may have been closed, cancelled, applied by
    // hand or repainted onto a different principal's options while this waited.
    if (slicerDraftKey !== key || !slicerDraftState) { markFilterCardApplying(key, false); return; }
    if (!slicerDraftChanged(key)) { markFilterCardApplying(key, false); refreshSlicerDraftControls(key); return; }
    applySlicerDraft(key, { auto: true });
  }, FILTER_AUTO_APPLY_DELAY_MS);
}

const emptyTrackerExportSlicers = {
  action: [],
  claimStatus: [],
  aging: [],
  match: [],
  followUpDue: [],
  patClassification: [],
  procedure: [],
  secondaryCalc: [],
  primaryPayerClass: [],
  primaryPayer: [],
  secondaryPayer: [],
  claimClassification: [],
  network: []
};

// ===========================================================================
// CT-TRACKER AR AGING BANDS — MIRROR of backend/ctTrackerAgingBuckets.js.
// app.js runs with no backend, so the bands are duplicated here; the copy is
// drift-guarded by backend/verify_ct_tracker_aging_buckets.js and must never be
// edited on its own. Parity audit 2026-07-22 finding 2-C: THREE aging schemes
// used to coexist in-tree, so the same claim landed in different buckets on the
// grid, the reports and the warehouse. This is now the only scheme in app.js.
// Inclusive upper bounds, exactly as build_lite_snapshot.py:890-902 reads them.
// ===========================================================================
const CT_AR_AGING_BUCKETS = [
  { key: "0-30", label: "0–30", min: 0, max: 30 },
  { key: "31-60", label: "31–60", min: 31, max: 60 },
  { key: "61-90", label: "61–90", min: 61, max: 90 },
  { key: "91-120", label: "91–120", min: 91, max: 120 },
  { key: "121+", label: "121+", min: 121, max: Infinity }
];
const CT_AR_AGING_BUCKET_KEYS = CT_AR_AGING_BUCKETS.map((bucket) => bucket.key);
// Legacy aging vocabularies still present in SERVED data. Band-ordinal aliases:
// they put a legacy label in the right BAND (so grouping / sorting / colour agree)
// without pretending to re-derive membership, which only aging DAYS can fix.
//   deploy/create_curated_tracker_views.sql (mirror view) -> 0-29/30-59/60-89/90-119/120+
//   scripts/export_synapse_snapshot.py (demo snapshot)    -> 0-30/30-60/61-90/91-120/120+
const LEGACY_AR_AGING_BUCKET_ALIASES = {
  "0-29": "0-30",
  "30-59": "31-60",
  "60-89": "61-90",
  "90-119": "91-120",
  "30-60": "31-60",
  "120+": "121+"
};
function normalizeAgingBucketLabel(label) {
  const text = String(label == null ? "" : label).trim();
  if (!text) return "";
  if (CT_AR_AGING_BUCKET_KEYS.includes(text)) return text;
  return LEGACY_AR_AGING_BUCKET_ALIASES[text] || text;
}
// Band ordinal, -1 for "(blank)" / unrecognized so unknowns never read as oldest.
function agingBucketOrdinal(label) {
  return CT_AR_AGING_BUCKET_KEYS.indexOf(normalizeAgingBucketLabel(label));
}
function claimAgingBand(claim) {
  return normalizeAgingBucketLabel(claim && claim.aging);
}
function isAgingBandAtLeast(claim, bucketKey) {
  const ordinal = agingBucketOrdinal(claim && claim.aging);
  const floor = CT_AR_AGING_BUCKET_KEYS.indexOf(bucketKey);
  return ordinal >= 0 && floor >= 0 && ordinal >= floor;
}
// ===========================================================================
// HOW MANY DAYS HAS THIS LINE BEEN AGING? — ONE READER FOR EVERY SURFACE.
// ===========================================================================
// Owner report 2026-09-17: "aging is showing blank right now" and "that should
// be a big decision". Two separate facts were behind it.
//
// 1. THE BLANK. The hosted drawer reads its claim through
//    serverDetailToDrawerClaim, which built its object field by field and
//    simply never copied `aging`. The API had always sent it
//    (trackerDetailResolver.js serializeClaimDetail), the grid had always
//    shown it, and the drawer rendered `claim.aging || "\u2014"` against an
//    undefined - an em dash that reads as "this claim has no age".
// 2. THE BAND IS NOT THE ANSWER. Even unblanked, the pill said "0-30". A
//    supervisor approving a write-off needs the DAY COUNT.
//
// THE ONE DEFINITION, and it is not a new one: bill-date aging days, i.e.
// days since the bill date on the Eastern business day, computed AT INGEST
// (trackerColumnSourceMap.js billDateAging; gold ships it as days-at-load).
// It is what the follow-up rulebook's stale + 30-day Claim Status Checkup
// rules already measure (followUpSweepPlanner.js rowToLine -> billAgeDays,
// consumed by followUpLifecycleEngine), and `aging_bucket` is DERIVED from
// it in SQL (goldTrackerCompatProjection.js arAgingBucketSqlCase), so the
// band and the days can never be two competing measurements - the band is a
// projection of the days. Nothing here recomputes anything from today's
// date: the snapshot's number is shown as the snapshot computed it, which is
// why the grid, the drawer and a review row all read the same figure.
//
// WHAT IT REFUSES TO DO:
//   * never fabricate 0. `bill_date_aging` arrives from mssql as a STRING
//     (SQL bigint), so Number() it - but absent stays absent, because a
//     stated "0 days" on an approve/reject screen is an assertion that the
//     claim was billed today.
//   * never print a bare dash. Unknown says "Aging unavailable", so a blank
//     can never again be mistaken for "nothing to see".
//   * never state a day count it does not have. With only a band label
//     (legacy/demo feeds) it says the BAND in days - "0\u201330 days" - rather
//     than printing the band floor as if it were measured.
function agingDaysNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(String(value).replace(/[$,]/g, "").trim());
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  // Math.ROUND and a REFUSED negative, both matching
  // backend/ctTrackerAgingBuckets.toAgingDays, which also returns null for a
  // negative before its Math.max ever runs (that clamp is unreachable). The two
  // readers therefore agree on every input, which is the point: a display reader
  // that rounded or floored differently from the bucketing authority would be a
  // second definition of aging waiting for its first non-integer input.
  return Math.round(numeric);
}
// Band for a measured day count - the app-side twin of arAgingBucket().
function agingBandForDays(days) {
  if (days === null || days === undefined) return null;
  return CT_AR_AGING_BUCKETS.find((bucket) => days >= bucket.min && days <= bucket.max)
    || CT_AR_AGING_BUCKETS[CT_AR_AGING_BUCKETS.length - 1];
}
// The same emphasis the drawer hero already used: oldest band red, the two
// bands before it amber, everything younger plain. No new thresholds.
function agingBandTone(bandKey) {
  if (bandKey === "121+") return "red";
  if (bandKey === "91-120" || bandKey === "61-90") return "amber";
  return "muted";
}
// `source` is any row carrying billDateAging and/or aging: a drawer claim, a
// grid row, an internal-review row, an approval proposal, a service line.
function agingDaysView(source) {
  const row = source && typeof source === "object" ? source : {};
  const measured = agingDaysNumber(firstPresent(row.billDateAging, row.bill_date_aging));
  if (measured !== null) {
    const band = agingBandForDays(measured);
    return {
      known: true,
      basis: "days",
      days: measured,
      band: band ? band.key : "",
      text: measured === 1 ? "1 day" : measured.toLocaleString() + " days",
      tone: agingBandTone(band ? band.key : ""),
      detail: "Days since the bill date" + (band ? " \u00b7 " + band.label + " band" : "")
    };
  }
  const bandKey = normalizeAgingBucketLabel(row.aging);
  const band = CT_AR_AGING_BUCKETS.find((bucket) => bucket.key === bandKey);
  if (band) {
    return {
      known: true,
      basis: "band",
      days: band.min,
      band: band.key,
      text: band.label + " days",
      tone: agingBandTone(band.key),
      detail: "Aging band only \u00b7 this feed did not supply a day count"
    };
  }
  return { known: false, basis: "", days: null, band: "", text: "Aging unavailable", tone: "muted", detail: "No bill date or aging band on this line" };
}
// The OLDEST line of a set, for a claim-grouped review row. A measured day
// count outranks a band estimate at equal days, and an unknown never wins -
// "we cannot measure this one" is not evidence that it is the oldest.
function oldestAgingDaysView(sources) {
  const views = (Array.isArray(sources) ? sources : []).map(agingDaysView).filter((view) => view.known);
  if (!views.length) return { ...agingDaysView(null), varies: false };
  const oldest = views.reduce((current, view) => (
    view.days > current.days || (view.days === current.days && view.basis === "days" && current.basis !== "days")
      ? view : current
  ));
  // `varies` is measured, not assumed: it is what licenses the "Oldest line"
  // label. Unknown lines are excluded above, so a group of one measured line
  // and one unmeasurable line does not count as varying - we would be naming a
  // difference we cannot see.
  return { ...oldest, varies: new Set(views.map((view) => view.days)).size > 1 };
}
// A claim group's chip. "Oldest line" is only said when the lines ACTUALLY
// disagree: bill-date aging is derived from ClaBillDate, a claim field, and a
// production measurement on 2026-09-17 found 0 of 89,591 (org, claim) pairs
// whose lines differ - so the label would have implied a variance that cannot
// occur and invited a supervisor to go looking for it. The oldest-line
// selection stays, because it is the safe answer if that ever changes; only
// the claim that it was NEEDED is withdrawn.
function agingDaysGroupChipHtml(rows) {
  const view = oldestAgingDaysView(rows);
  return agingDaysChipHtml(view, view.varies ? "Oldest line" : "Aging");
}
// One chip, one spelling, everywhere a review decision is made. The DAYS are
// in the text, never only in the colour.
function agingDaysChipHtml(view, label) {
  const resolved = view && typeof view === "object" && "text" in view ? view : agingDaysView(view);
  return '<span class="work-aging-chip" data-aging-basis="' + escapeHtml(resolved.basis || "none") + '" data-status-pill data-tone="'
    + escapeHtml(resolved.tone) + '" title="' + escapeHtml(resolved.detail) + '">'
    + (label ? '<small>' + escapeHtml(label) + '</small> ' : "")
    + escapeHtml(resolved.text) + '</span>';
}
// The approval badge: one claim's days and bucket, one color per bucket. Only
// the oldest bucket is flagged. A band-only feed already says the bucket in its
// text, so it is not repeated.
function agingBucketBadgeHtml(view) {
  const resolved = view && typeof view === "object" && "text" in view ? view : agingDaysView(view);
  if (!resolved.known) return '<span class="approval-age-badge approval-age-badge--none" title="' + escapeHtml(resolved.detail) + '">' + escapeHtml(resolved.text) + '</span>';
  const bucket = CT_AR_AGING_BUCKETS.find((entry) => entry.key === resolved.band);
  const flag = resolved.band === "121+"
    ? '<svg class="approval-age-flag" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 1.5v13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/><path d="M3.9 2.2h9.2l-2.3 3.2 2.3 3.2H3.9z" fill="currentColor"/></svg>'
    : "";
  return '<span class="approval-age-badge approval-age-badge--' + escapeHtml(resolved.band.replace("+", "plus")) + '" data-aging-bucket="' + escapeHtml(resolved.band) + '" title="' + escapeHtml(resolved.detail) + '">'
    + flag + '<span>' + escapeHtml(resolved.text) + '</span>'
    + (resolved.basis === "days" && bucket ? '<small>' + escapeHtml(bucket.label) + '</small>' : "") + '</span>';
}
function approvalClaimAgeView(claim) {
  return oldestAgingDaysView(claim.items.map((item) => item.row));
}
// Aging slicer chip order (oldest chip first). Chips carry the RAW server value so
// the value still round-trips to the aging_bucket equality filter; each legacy
// label is ranked next to the CT band it belongs to instead of falling to the
// unordered "other" tail.
const AGING_SLICER_ORDER = CT_AR_AGING_BUCKET_KEYS
  .map((key) => [key, ...Object.keys(LEGACY_AR_AGING_BUCKET_ALIASES).filter((legacy) => LEGACY_AR_AGING_BUCKET_ALIASES[legacy] === key)])
  .reverse()
  .reduce((flat, group) => flat.concat(group), []);

// ---------------------------------------------------------------------------
// PERSISTED AGING-SELECTION MIGRATION (WAREHOUSE_CUTOVER_PLAN.md phase 5, N1).
// MIRROR of migrateAgingSlicerValues in backend/ctTrackerAgingBuckets.js; the two
// copies are behaviour-compared by backend/verify_ct_tracker_aging_buckets.js.
//
// The feeds disagree on the aging VOCABULARY and TMS_TRACKER_SOURCE is process-wide:
// the mirror view serves 0-29/30-59/60-89/90-119/120+, the gold/warehouse projection
// serves the CT bands, the demo snapshot serves 0-30/30-60/61-90/91-120/120+. A slicer
// selection is matched by EXACT string equality everywhere (local matchesSlicers, the
// API's buildSlicerMatcher, the SQL IN list), so on flip day every selection persisted
// in mirror mode matches ZERO rows: an empty grid, no error, every user at once.
//
// The stored label is KEPT and its CT band key is ADDED alongside, never swapped — the
// same expand-never-narrow shape as migrateMatchSlicerValues. A selection of
// {"60-89","61-90"} matches the band under EITHER vocabulary, so the flip and the
// documented rollback (flip the env vars back) are both safe and no lane has to move in
// lockstep. Unrecognized text is left exactly as stored: it keeps working as it does
// today rather than being dropped. Idempotent: a second pass can only re-add values that
// are already present.
//
// Applied to EVERY persisted aging selection, because each one is matched by exact
// string equality and would otherwise go silently empty on flip day:
//   state.slicers.aging + savedOperationsViews[].state.slicers.aging  -> ensureSlicerState
//   state.columnFilters.aging + the saved-view leg                    -> migrateColumnFilters
//   state.clientPivot.slicers.aging                                   -> normalizeClientPivotState
//   state.clientReportSlicers.aging                                   -> normalizeClientReportSlicers
function migrateAgingSlicerValues(values = []) {
  const list = Array.isArray(values) ? values : [];
  const out = [];
  const seen = new Set();
  const push = (value) => {
    if (seen.has(value)) return;
    seen.add(value);
    out.push(value);
  };
  list.forEach((value) => {
    const stored = String(value == null ? "" : value);
    push(stored);
    const band = normalizeAgingBucketLabel(stored);
    if (band && band !== stored) push(band);
  });
  return out;
}

// Every value in `values` that resolves to the same CT band as `value` — i.e. the twin
// labels migrateAgingSlicerValues added. The UI treats a band as ONE unit: one chip, one
// summary entry, one click to clear.
function agingSlicerBandValues(values, value) {
  const band = normalizeAgingBucketLabel(value);
  if (!band) return [];
  return Array.from(values || []).filter((existing) => normalizeAgingBucketLabel(existing) === band);
}

// THE SINGLE-SELECT TWIN OF agingSlicerBandValues (WAREHOUSE_CUTOVER_PLAN.md phase 5, N1).
// A <select> holds ONE value, so it structurally cannot carry the stored label AND its CT band
// the way migrateAgingSlicerValues expands a multi-select selection. It has to pick the ONE
// spelling the option list in front of it actually offers, and that list is re-cut from whichever
// feed is serving (mirror / warehouse / demo snapshot). These are every label that means the same
// band: the stored spelling first, then the CT band key, then that band's legacy aliases.
function agingFilterBandCandidates(value) {
  const stored = String(value == null ? "" : value).trim();
  const band = normalizeAgingBucketLabel(stored);
  if (!band) return [];
  const legacy = Object.keys(LEGACY_AR_AGING_BUCKET_ALIASES).filter((label) => LEGACY_AR_AGING_BUCKET_ALIASES[label] === band);
  return Array.from(new Set([stored, band, ...legacy].filter(Boolean)));
}

// Resolve a persisted aging dropdown value onto the option this select actually carries.
// Assigning a <select> a value it has no option for silently yields "", and collectTrackerFilters
// reads `els.agingFilter?.value || "All"` — so the miss FAILS OPEN: a saved view titled for one
// band quietly shows EVERY band, which is a widening the reader is never told about (the mirror
// image of the silent-empty grid migrateAgingSlicerValues prevents on the multi-selects).
// Left EXACTLY as stored: "All", genuinely unknown text, and any value while the select is
// unpopulated — nothing that works today starts behaving differently.
function agingFilterValueForOptions(select, value) {
  const stored = String(value == null || value === "" ? "All" : value);
  if (!select || stored === "All") return stored;
  const options = Array.from(select.options || []).map((option) => option.value);
  if (!options.length || options.includes(stored)) return stored;
  return agingFilterBandCandidates(stored).find((candidate) => options.includes(candidate)) || stored;
}

// Chips for a slicer strip whose values come from the DATA rather than from selected
// state — the client pivot and the client report both build their chips by counting
// values across the claims they can see. A selection the feed does not serve gets no
// chip there, so it can never be cleared: an aging band expanded across both
// vocabularies (migrateAgingSlicerValues) would leave the twin as an INVISIBLE filter
// matching zero rows, in a client-facing view. Same rule the AR strip applies in
// sortedSlicerValuesFromCounts: the band is ONE chip — the label this feed actually
// serves — and any other selected value is appended so it stays visible and clearable.
// Values the feed does serve are never deduped: a chip carries its own count and its
// own exact-equality filter value.
function slicerChipValuesWithSelection(key, dataValues = [], selectedValues = []) {
  const chips = (Array.isArray(dataValues) ? dataValues : Array.from(dataValues || [])).slice();
  Array.from(selectedValues || []).forEach((value) => {
    if (chips.includes(value)) return;
    if (key === "aging" && agingSlicerBandValues(chips, value).length) return;
    chips.push(value);
  });
  return key === "action" ? followUpFilterValues(chips) : chips;
}

// Toggling one value of a persisted slicer selection. Deselecting an aging label clears
// the whole BAND, exactly as toggleSlicer does for the AR strip: while the mirror and
// the warehouse disagree on the vocabulary a band can be selected under two labels at
// once, and dropping only the clicked label would leave its twin selected with no chip
// to clear it. Selecting adds the clicked label alone — matching stays exact string
// equality everywhere, so the client and the server keep agreeing.
function toggleSlicerSelectionValue(key, selectedValues = [], value) {
  const selected = new Set(Array.from(selectedValues || []));
  if (selected.has(value)) {
    selected.delete(value);
    if (key === "aging") agingSlicerBandValues(selected, value).forEach((twin) => selected.delete(twin));
  } else {
    selected.add(value);
  }
  return Array.from(selected);
}

// Finalized Match vocabulary. The engine (backend/financialCalculationEngine.js:655-656
// and trackerFinalizedMatch below) emits No Primary Rate / No Secondary Rate as two
// separate buckets, because the fee-schedule fix differs: a missing PRIMARY rate blocks
// the whole line, a missing SECONDARY rate only blocks the crossover. Parity audit
// 2026-07-22 finding 2-A: the slicer used to collapse both into one "No Rate Found"
// chip that matched neither value, so the rate-cleanup queue could not be filtered.
const MATCH_SLICER_ORDER = [
  "Paid",
  "Not Paid",
  "Under Paid",
  "Over Paid",
  "Missing Fee Schedule",
  "No Primary Rate",
  "No Secondary Rate",
  "Pat Bal Over Expected",
  "Pat Paid Over Expected"
];
const NO_RATE_MATCH_VALUES = ["Missing Fee Schedule", "No Primary Rate", "No Secondary Rate"];
// True for either half of the split rate-cleanup bucket, plus the retired collapsed
// value so stale cached rows keep their rate-review treatment.
function isNoRateMatch(match) {
  const text = String(match == null ? "" : match).trim();
  return NO_RATE_MATCH_VALUES.includes(text) || matchValueIsLegacyCollapsedNoRate(text);
}
// The retired collapsed value itself is recognized by matchValueIsLegacyCollapsedNoRate,
// used only to migrate saved views / persisted slicer state written before the split.

// IN / OUT OF NETWORK — the browser half of backend/networkVocabulary.js.
//
// Three surfaces read it: the Reports slicer, the All Data slicer rail, and the
// workbook export studio. The SQL lanes implement the same rule twice more (a
// bound IN-list in reportDatasetSource, a CASE expression in
// trackerQueryExecutionPlan), both from that module's spelling families.
// verify_report_network_slicer.js pins this copy against them spelling by
// spelling, because the drift is silent: a lane that forgets "IN" drops the 1,672
// rows the fee schedule actually spells that way and still looks correct.
//
// Declared HERE, above slicerDefinitions, and not beside the Reports filter code
// where it started: a `const` referenced by a module-level definition below would
// be in the temporal dead zone and the app would not boot.
const NETWORK_BUCKET_OPTIONS = ["In Network", "OON", "Unknown"];

// "Unknown" is not a failure mode. It is the truthful answer for a line whose
// payer has no matching contract, for one whose payer carries BOTH an in- and an
// out-of-network contract with nothing on the claim to choose between them (the
// resolver narrows to network-neutral rows there rather than guessing), and for
// any spelling outside both families. Defining it as the COMPLEMENT is what makes
// the three buckets add up to the whole book instead of quietly hiding rows.
function networkBucket(value) {
  const text = String(value == null ? "" : value).trim().toLowerCase().replace(/\s+/g, " ");
  if (/^(in|inn|in network|in-network|in net)$/.test(text)) return "In Network";
  if (/^(out|oon|out of network|out-of-network|out net)$/.test(text)) return "OON";
  return "Unknown";
}

const slicerDefinitions = [
  { key: "renderingProvider", element: "providerSlicer", field: "renderingProvider", order: "alpha" },
  { key: "match", element: "matchSlicer", field: "match", order: MATCH_SLICER_ORDER, requiredValues: ["Paid", "Not Paid", "Under Paid"] },
  { key: "aging", element: "agingSlicer", field: "aging", order: AGING_SLICER_ORDER },
  { key: "action", element: "actionSlicer", field: "action", order: "actions", requiredValues: ["Blank"] },
  { key: "primaryPayer", element: "primarySlicer", field: "primaryPayer", order: "alpha" },
  { key: "secondaryPayer", element: "secondarySlicer", field: "secondaryPayer", order: "alpha" },
  { key: "facility", element: "facilitySlicer", field: "facility", order: "alpha" },
  { key: "procedure", element: "procedureSlicer", field: "procedure", serviceField: "procedure", order: "alpha" },
  { key: "claimStatus", element: "claimStatusSlicer", field: "claimStatus", order: "alpha" },
  // Service Month is a DERIVED per-service-line bucket (serviceMonth(dos) ->
  // "yyyy-MM"); the backend mirrors it with a computed CONVERT(char(7),
  // service_date, 126) facet/predicate so client + server agree. Newest month
  // first (alpha-desc on the sortable "yyyy-MM" string).
  { key: "serviceMonth", element: "serviceMonthSlicer", field: "serviceMonth", serviceField: "dos", order: "alpha-desc" },
  { key: "followUpDue", element: "dueSlicer", field: "followUpDue", computed: true, order: ["Due Now", "Future", "No Follow-Up Date"], requiredValues: ["Due Now", "Future", "No Follow-Up Date"] },
  { key: "primaryPayerClass", element: "primaryClassSlicer", field: "primaryPayerClass", computed: true, order: ["Medicaid", "Commercial"], requiredValues: ["Medicaid", "Commercial"] },
  { key: "secondaryPayerClass", element: "secondaryClassSlicer", field: "secondaryPayerClass", computed: true, order: ["Medicaid", "Commercial"], requiredValues: ["Medicaid", "Commercial"] },
  { key: "patClassification", element: "patClassSlicer", field: "patClassification", order: "alpha" },
  { key: "secondaryCalc", element: "secondaryCalcSlicer", field: "secondaryCalc", order: "alpha" },
  { key: "claimClassification", element: "claimClassSlicer", field: "claimClassification", order: "alpha" },
  // IN/OUT OF NETWORK. serviceField routes it through the per-line path, exactly
  // like Procedure and Service Month: the fee-schedule match that produces this
  // value runs per service line, so one claim can genuinely be both.
  //
  // requiredValues keeps all three chips on the rail even when the current page
  // has none of one — a biller needs to SEE that "Unknown" is a bucket that
  // exists before they can reason about why a line landed in it.
  { key: "network", element: "networkSlicer", field: "contractNetwork", serviceField: "contractNetwork", order: NETWORK_BUCKET_OPTIONS, requiredValues: NETWORK_BUCKET_OPTIONS }
];

const TRACKER_EXPORT_SLICER_DEFS = [
  { key: "action", label: "Follow-up Action", facet: "action", blank: true, searchable: true },
  { key: "claimStatus", label: "Claim Status", facet: "claimStatus", blank: true },
  { key: "aging", label: "Aging", facet: "aging", order: AGING_SLICER_ORDER },
  { key: "match", label: "Finalized Match", facet: "match", order: MATCH_SLICER_ORDER, requiredValues: ["Paid", "Not Paid", "Under Paid"] },
  { key: "followUpDue", label: "Follow-Up Due", facet: "followUpDue", requiredValues: ["Due Now", "Future", "No Follow-Up Date"] },
  { key: "patClassification", label: "Patient Classification", facet: "patClassification", blank: true },
  { key: "procedure", label: "Procedure", facet: "procedure", searchable: true },
  { key: "secondaryCalc", label: "Secondary Calc", facet: "secondaryCalc", blank: true },
  { key: "primaryPayerClass", label: "Medicaid / Commercial", facet: "primaryPayerClass", requiredValues: ["Medicaid", "Commercial"] },
  { key: "primaryPayer", label: "Payer", facet: "primaryPayer", searchable: true, blank: true },
  { key: "secondaryPayer", label: "Secondary Payer", facet: "secondaryPayer", searchable: true, blank: true },
  { key: "claimClassification", label: "Claim Classification", facet: "claimClassification", blank: true },
  // No `blank: true`: this facet has no blank case. Every row falls in one of the
  // three buckets because Unknown is the COMPLEMENT of the two real ones, so a
  // "Blank" chip beside "Unknown" would be an empty second name for the same set.
  { key: "network", label: "In / Out of Network", facet: "network", requiredValues: NETWORK_BUCKET_OPTIONS }
];

// Must match trackerWorkbookSynapseRowSource.DEFAULT_MAX_ROWS. The preview
// count is authoritative, so never let a scope that the API will reject look
// downloadable in the studio.
//
// DELIBERATELY NOT LOWERED to the builder's own refusal ceiling. The builder used
// to die of a V8 heap abort well below this, and the obvious reaction was to drop
// this number to match - but that shrinks what a biller can export, and the whole
// point of a tracker export is that the tracker is all there. The memory cliff was
// fixed at the root instead (the payload streams rather than serialising to a
// 442-million-character string), so the reach stays where it was and the server's
// 413 is a backstop for a scope that is genuinely too large, not the normal case.
// The size check now reports the server's buildable limit (rowLimit), and the
// studio gates on the lower of the two; this constant only covers an API build
// that does not report one.
const TRACKER_WORKBOOK_EXPORT_MAX_ROWS = 200000;

// Not a limit - a limit is TRACKER_WORKBOOK_EXPORT_MAX_ROWS above. This is the
// size past which "try again" stops being useful advice, taken from the measured
// build curve: 25,000 rows builds in ~21s, 60,000 in ~70s, 119,282 in ~157s. Above
// roughly 60,000 a failure is far more likely to be the range than the network,
// and repeating the identical request just spends the same minutes again.
const TRACKER_EXPORT_LARGE_RANGE_ROWS = 60000;

const emptyColumnFilters = {};

// INCLUDE / EXCLUDE ON A FILTER THAT ALREADY HAS A SELECTION (owner, 2026-09-22).
//
//   "I want to be able to do all follow-up actions, but select only specific
//    ones ... or take off multiple follow-up actions. I don't want to see any
//    that are just Client Action Pending."
//
// That is one filter with a direction, not two filters. So the direction rides
// BESIDE the same selection list every surface already reads - one scalar per
// filter key, in the same key space as state.slicers / state.columnFilters -
// rather than a second parallel selection nobody else would look at. Everything
// that counts, chips, clears, saves, restores or serializes a filter therefore
// keeps reading exactly ONE list and asks this map which way it points.
//
// Include is the default and is never stored: a missing mode is what every
// saved view, every ?queue= seed and every older localStorage blob already
// means, so nothing had to be migrated.
const emptyFilterModes = {};
const FILTER_MODE_EXCLUDE = "exclude";
const FILTER_MODE_INCLUDE = "include";

// The keys whose UI offers the switch. The MODEL below is generic - the server
// predicate, the chips, the request and the local matchers all read any key -
// so widening this set is the whole of adding the control to another filter.
// Today the owner asked for Follow-Up Action, and a filter review is running in
// another lane, so exactly that one is offered.
const FILTER_EXCLUSION_KEYS = new Set(["action"]);

// ---------------------------------------------------------------------------
// ONE CARD, TWO SURFACES (owner, 2026-09-22).
// ---------------------------------------------------------------------------
//   "Every report filter ... should work exactly like the All Data slicer
//    cards ... Reuse the All Data slicer card component and its filter model
//    rather than building a second widget, so the two surfaces cannot drift."
//
// The All Data rail and the Reports filter bar now render the SAME card and run
// the SAME draft state machine. What tells them apart is the CARD ID every one
// of those functions is already keyed by: a bare key ("action") is an All Data
// slicer, and a "report:" prefixed one ("report:action") is a Reports filter.
// That is the whole of the seam - the DOM query, the CSS, the search box, the
// Select all / Clear buttons, the include/exclude switch, the family toggle and
// the staged-count status line are untouched and shared, and a HOST descriptor
// (filterCardHost) is asked the three questions that actually differ: what this
// filter is called, what is currently applied, and where to commit.
const REPORT_FILTER_CARD_PREFIX = "report:";

function filterCardSurface(id) {
  return String(id == null ? "" : id).startsWith(REPORT_FILTER_CARD_PREFIX) ? "report" : "slicer";
}

function filterCardKey(id) {
  const text = String(id == null ? "" : id);
  return text.startsWith(REPORT_FILTER_CARD_PREFIX) ? text.slice(REPORT_FILTER_CARD_PREFIX.length) : text;
}

function filterCardId(surface, key) {
  return surface === "report" ? `${REPORT_FILTER_CARD_PREFIX}${key}` : String(key);
}

// WHICH FILTERS OFFER THE DIRECTION SWITCH.
//
// The MODEL has always been generic - the server predicate, the chips, the
// request and the local matchers read any key - so widening this is the whole
// of adding the control to another filter. On All Data the owner asked for
// Follow-Up Action and a filter review is running in another lane, so exactly
// that one is still offered there. On REPORTS he asked for the direction on
// every filter ("pick some values and hide others"), and the only report filter
// that does not get it is the company, which is single-select by scope contract
// (see reportFilterCardDefinitions).
function filterKeySupportsExclusion(id) {
  if (filterCardSurface(id) === "report") return reportFilterKeyIsMultiSelect(filterCardKey(id));
  return FILTER_EXCLUSION_KEYS.has(String(id));
}

// The three questions a card has to ask about the surface it is sitting on.
// Everything else about a card is the same on both, which is the point.
function filterCardHost(id) {
  if (filterCardSurface(id) === "report") {
    return {
      surface: "report",
      key: filterCardKey(id),
      label: (key) => reportFilterLabel(key),
      selections: (key) => reportFilterSelections(key),
      modes: () => reportFilterModeState(),
      commit: (key, values, exclude, options) => applyReportFilterSelection(key, values, exclude, options)
    };
  }
  return {
    surface: "slicer",
    key: String(id),
    label: (key) => slicerLabel(key),
    selections: (key) => {
      const applied = ensureSlicerState(state.slicers);
      return Array.isArray(applied[key]) ? applied[key] : [];
    },
    modes: () => slicerModeState(),
    commit: (key, values, exclude, options) => applyAppliedSlicerSelection(key, values, exclude, options)
  };
}

// A mode is kept only when it spells "exclude" AND its key actually carries a
// selection. A direction with nothing selected narrows nothing, and leaving it
// behind is how a cleared filter comes back looking active.
function ensureFilterModeState(modes, selections = {}) {
  if (!modes || typeof modes !== "object") return {};
  return Object.fromEntries(Object.entries(modes)
    .filter(([key, mode]) => String(mode == null ? "" : mode).trim().toLowerCase() === FILTER_MODE_EXCLUDE
      && Array.isArray(selections[key]) && selections[key].length)
    .map(([key]) => [String(key), FILTER_MODE_EXCLUDE]));
}

function filterModeFor(modes, key) {
  return modes && modes[key] === FILTER_MODE_EXCLUDE ? FILTER_MODE_EXCLUDE : FILTER_MODE_INCLUDE;
}

function filterModeExcludes(modes, key) {
  return filterModeFor(modes, key) === FILTER_MODE_EXCLUDE;
}

function slicerModeState() {
  return ensureFilterModeState(state?.slicerModes, ensureSlicerState(state?.slicers));
}

function columnFilterModeState() {
  return ensureFilterModeState(state?.columnFilterModes, ensureColumnFilterState(state?.columnFilters));
}

// ---------------------------------------------------------------------------
// THE COMPARISON HALF OF A COLUMN FILTER (owner, 2026-09-22).
// ---------------------------------------------------------------------------
//   "Everything has to be like Excel. There have to be drop-downs for all of
//    the columns."
//
// Excel's column menu over a number is TWO controls, not one: a value checklist
// AND a Number Filters section, because "greater than 500" is not a value
// anybody can tick and 4,000 distinct charge amounts are not a menu. The
// checklist half is the server's bounded facet lane; this is the other half.
//
// IT RIDES BESIDE THE SELECTION, exactly as the include/exclude MODE does and
// for the reason recorded on emptyFilterModes: one filter key, one key space.
// state.columnFilters holds what was ticked, state.columnFilterModes holds which
// way that points, state.columnFilterRanges holds the comparison. Everything
// that counts, chips, clears, saves, restores or serializes a column filter
// therefore reads one more scalar off the key it is already holding, rather
// than learning a second model with its own rail, its own badge and its own
// clear button to forget about.
//
// Absent is the default and is never stored, so every saved view, ?queue= seed
// and older localStorage blob means exactly what it meant before this existed.
const emptyColumnFilterRanges = {};

// MIRRORS backend/trackerQueryExecutionPlan.js columnFilterRangeFamily(), and
// verify_all_data_column_filter_controls.js asserts the two agree for EVERY grid
// column in both modes. A control the server would refuse is a control that
// leads only to an error, which is the defect SERVER_FILTERABLE_TRACKER_COLUMNS
// was written to remove.
const COLUMN_FILTER_NUMBER_COLUMNS = new Set([
  "charges", "adjs", "balance", "owed",
  "totalInsAmtPaid", "totalPatAmtPaid", "totalPatBal", "positiveTotalPatBal",
  "primaryRate", "primaryExpectedTotal", "secondaryRate", "secondaryAllowedTotal",
  "secondaryExpectedTotal", "staticTotalExpected", "finalExpectedTotalNumeric",
  "finalSecondaryExpectedNumeric", "secondaryExpectedChangeNumeric",
  "totalExpectedChangeNumeric", "writeOffAmount", "finalInsuranceExpected",
  "finalInsurancePaid", "finalInsuranceBalance", "prAdjustmentTotal",
  "primaryDisbursementTotal", "secondaryDisbursementTotal",
  "unmatchedDisbursementTotal", "totalExpected", "totalPaid",
  // A quantity, not a dollar. Same decimal(18,4) column shape, same comparison,
  // formatted without a currency symbol.
  "units",
  // Hold duration (backend/serviceLineHoldEpisodes.js).
  "daysOnHold"
]);
const COLUMN_FILTER_DATE_COLUMNS = new Set(["dos", "followUpDate", "onHoldSince", "holdReleasedOn"]);

// The operators each family offers, in the order Excel lists them. `blank` and
// `notBlank` are last because they take no operand.
const COLUMN_FILTER_NUMBER_OPERATORS = ["eq", "ne", "gt", "lt", "between", "blank", "notBlank"];
const COLUMN_FILTER_DATE_OPERATORS = ["before", "after", "between", "blank", "notBlank"];
const COLUMN_FILTER_RANGE_ARITY = { eq: 1, ne: 1, gt: 1, gte: 1, lt: 1, lte: 1, between: 2, before: 1, after: 1, blank: 0, notBlank: 0 };
// The words the MENU uses. The chip says it differently - see
// columnFilterRangeChipText - because a chip is a sentence about the whole grid
// and a menu label sits under a column heading that already names the column.
const COLUMN_FILTER_RANGE_OPERATOR_LABELS = {
  eq: "equals", ne: "does not equal", gt: "is greater than", lt: "is less than",
  gte: "is greater than or equal to", lte: "is less than or equal to",
  between: "is between", before: "is before", after: "is after",
  blank: "is blank", notBlank: "is not blank"
};
const COLUMN_FILTER_RANGE_CHIP_WORDS = {
  eq: "=", ne: "≠", gt: ">", lt: "<", gte: "≥", lte: "≤",
  between: "between", before: "before", after: "after",
  blank: "is blank", notBlank: "is not blank"
};

function columnFilterRangeFamily(key) {
  if (COLUMN_FILTER_DATE_COLUMNS.has(String(key))) return "date";
  if (COLUMN_FILTER_NUMBER_COLUMNS.has(String(key))) return "numeric";
  return "";
}

function columnFilterRangeOperators(key) {
  const family = columnFilterRangeFamily(key);
  if (family === "date") return COLUMN_FILTER_DATE_OPERATORS;
  if (family === "numeric") return COLUMN_FILTER_NUMBER_OPERATORS;
  return [];
}

// NORMALIZED OR DROPPED, never repaired into a different question - the same
// rule the server applies, so a range the grid shows a chip for is a range the
// server compiled. An operand that will not parse, a missing second bound on
// "between", an operator this column does not offer: each of them means no
// filter, and the chip rail then prints nothing rather than describing a
// narrowing that is not happening.
function normalizeColumnFilterRangeValue(key, raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const family = columnFilterRangeFamily(key);
  if (!family) return null;
  const op = String(raw.op == null ? "" : raw.op).trim();
  if (!columnFilterRangeOperators(key).includes(op)) return null;
  const arity = COLUMN_FILTER_RANGE_ARITY[op] || 0;
  if (!arity) return { op };
  const first = normalizeColumnFilterRangeOperand(family, raw.value);
  if (first == null) return null;
  if (arity === 1) return { op, value: first };
  const second = normalizeColumnFilterRangeOperand(family, raw.value2);
  if (second == null) return null;
  // Ordered, for the reason the server states: SQL answers a backwards BETWEEN
  // with zero rows and no complaint, so a biller who typed the bounds in the
  // order they thought of them would read a chip that disagrees with the grid.
  const ascending = family === "numeric" ? Number(first) <= Number(second) : String(first) <= String(second);
  return ascending ? { op, value: first, value2: second } : { op, value: second, value2: first };
}

function normalizeColumnFilterRangeOperand(family, value) {
  if (value == null || value === "") return null;
  if (family === "numeric") {
    // moneyNumber over the repo's own money reader, so "$1,500" and "(250)" -
    // the way the cells above the box are printed - both land.
    const parsed = typeof parseMoneyNumber === "function" ? parseMoneyNumber(value) : Number(value);
    return Number.isFinite(parsed) ? Number(parsed) : null;
  }
  return normalizeDateValue(value) || null;
}

function ensureColumnFilterRangeState(value) {
  if (!value || typeof value !== "object") return structuredClone(emptyColumnFilterRanges);
  const next = {};
  Object.entries(value).forEach(([key, raw]) => {
    const range = normalizeColumnFilterRangeValue(key, raw);
    if (range) next[String(key)] = range;
  });
  return next;
}

function columnFilterRangeState() {
  return ensureColumnFilterRangeState(state?.columnFilterRanges);
}

function columnFilterRangeFor(key) {
  return columnFilterRangeState()[String(key)] || null;
}

// The operand, printed the way its own column is printed. A money column reads
// $1,234.56 (two places, not the grid cell's whole dollars - a filter bound of
// "$1,234" over a cell that rounds would be a different number), a quantity
// reads as a plain number, a date reads the way every other date on the screen
// does.
function columnFilterRangeOperandText(key, value) {
  if (columnFilterRangeFamily(key) === "date") {
    const iso = normalizeDateValue(value);
    return iso ? formatDate(iso) : String(value == null ? "" : value);
  }
  if (currencyColumns.has(key)) return Formatters.moneyExact(value);
  return String(Number(value));
}

// AN HONEST CHIP. "Charges > 500" and "Srvc Date between 2026-01-01 and
// 2026-03-31" are what the reader asked for, in their own words; a chip reading
// "Charges: 1 value" over a comparison would describe a checklist that is not
// there.
function columnFilterRangeChipText(key, range) {
  const word = COLUMN_FILTER_RANGE_CHIP_WORDS[range.op] || range.op;
  if (range.op === "blank" || range.op === "notBlank") return `${columnLabel(key)} ${word}`;
  if (range.op === "between") {
    return `${columnLabel(key)} between ${columnFilterRangeOperandText(key, range.value)} and ${columnFilterRangeOperandText(key, range.value2)}`;
  }
  return `${columnLabel(key)} ${word} ${columnFilterRangeOperandText(key, range.value)}`;
}

// A RANGE ON A COLUMN THE TARGET MODE DOES NOT DRAW HAS NOWHERE TO LIVE, exactly
// as its selection does not - see columnFiltersForRowMode, which is the same
// rule over the same permitted set, so a Rows/Groups switch cannot keep half a
// filter and drop the other half.
function columnFilterRangesForRowMode(mode, ranges = state.columnFilterRanges) {
  const permitted = new Set(permittedColumnsForMode(mode).map((column) => column.key));
  const kept = structuredClone(emptyColumnFilterRanges);
  Object.entries(ensureColumnFilterRangeState(ranges)).forEach(([key, range]) => {
    if (permitted.has(key)) kept[key] = range;
  });
  return kept;
}

// THE CLIENT ACTION PENDING FAMILY IS DERIVED, NEVER LISTED.
//
// The catalog spells it "Client Action Pending", "Client Action Pending-EVV",
// "Client Action Pending-Credentialing" and so on, and migrated tracker data
// also carries legacy spacing and misspellings ("Client Action Pending -
// Autharazation" - see backend/followUpLifecycleEngine.js). A hard-coded list
// would quietly stop covering the variant nobody remembered, which is the exact
// defect matrixEntry() was fixed for. So membership is decided by PREFIX over
// the values the filter is actually offering right now, compared on a key that
// ignores case, punctuation and whitespace runs - and the word boundary is kept,
// so a genuinely different action that merely starts with the same letters is
// not swept in.
const CLIENT_ACTION_PENDING_FAMILY = "Client Action Pending";

function followUpFamilyKey(value) {
  return String(value == null ? "" : value).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function followUpActionInFamily(value, family = CLIENT_ACTION_PENDING_FAMILY) {
  const prefix = followUpFamilyKey(family);
  if (!prefix) return false;
  const text = followUpFamilyKey(value);
  return text === prefix || text.startsWith(prefix + " ");
}

// The live option list first (what the card is offering, including whatever
// legacy spellings the data carries), then the catalog this build knows about -
// so the one-click family option can never leave behind a variant the reader
// can see.
function followUpFamilyValues(offered = [], family = CLIENT_ACTION_PENDING_FAMILY) {
  const catalog = (() => {
    try { return (getActionRules() || []).map((rule) => rule.action); } catch (_) { return actionRules.map((rule) => rule.action); }
  })();
  return Array.from(new Set([...offered, ...catalog].map((value) => followUpFilterValue(value))))
    .filter((value) => value !== "Blank" && followUpActionInFamily(value, family));
}

// Operational queue labels. Per PORTAL_DIRECTIVES copy table: framing
// describes what TMS is actively DOING about a claim, not the situation
// alone. Keys stay stable (used everywhere for routing, matchesQueue(),
// saved views, backend follow-up summary, audits); only the user-visible
// labels and details are positive-framed.
const queueDefinitions = [
  { key: "workFirst", label: "High-Priority Work", detail: "Active follow-ups, denials, high-dollar, or first-touch AR" },
  { key: "denied", label: "Claims In Appeal", detail: "Denied, rejected, corrected, or appeal work" },
  { key: "unpaid", label: "Awaiting Payer Response", detail: "No insurance payment posted yet" },
  { key: "partial", label: "Underpayment Recovery", detail: "Paid below expected reimbursement" },
  { key: "payerFollowUp", label: "Payer Follow-Up", detail: "Open AR that needs payer response or portal/call follow-up" },
  { key: "waitingPrimary", label: "Waiting Primary", detail: "Primary payer response or first payment is still pending" },
  { key: "secondary", label: "Secondary", detail: "Secondary payer or submission work" },
  { key: "waitingSecondary", label: "Waiting Secondary", detail: "Secondary payer response or payment is still pending" },
  { key: "clientAction", label: "Client Action", detail: "Authorization, COB, documentation, or provider follow-up" },
  // Was "No Recent Touch" — directive flagged that as negligence framing.
  // Reframed to describe what TMS is going to do about it (first outreach).
  { key: "stale", label: "Ready For Outreach", detail: "Open AR queued for the next biller touch" },
  { key: "highDollar", label: "Premium AR Focus", detail: "Open balance at or above $1,000" },
  { key: "missingPayerResponse", label: "Awaiting Payer Response", detail: "No payer response/status evidence on an open balance" },
  { key: "missingInfo", label: "Awaiting Provider Info", detail: "Missing status, payer response, or payer translator validation" },
  // D1. Not a work queue — the opposite. This is where bad debt goes so it is
  // out of every collectable lane and still auditable. A biller should never
  // need to open it to do their day; a manager needs it to exist.
  { key: "writtenOff", label: "Written Off (bad debt)", detail: "No longer expected — excluded from AR, aging, and every work queue" }
];

// HOW THE REPORTING SOURCE SPELLS THESE QUEUES (2026-08-10).
//
// /api/tracker/facets groups by the raw [work_queue] column, so the queue facet
// comes back as 'payer-follow-up' / 'waiting-secondary' while every card above
// is keyed 'payerFollowUp' / 'waitingSecondary'. The lookup missed on every
// single card, so EVERY queue card rendered "0" and "$0.00 open" over 41,203
// real rows and $6.4M of open AR — a confident zero, which is worse than a
// blank. Same root cause as the drill-down returning an empty work list.
//
// Duplicated here on purpose: this file cannot require() the server module. It
// is the SAME vocabulary as backend/claimQueueSourceVocabulary.js, and
// backend/verify_claim_queue_source_vocabulary.js reads BOTH files and fails the
// build the moment they disagree — the pattern the payment-lane keys already use.
const QUEUE_SOURCE_VALUES = {
  payerFollowUp: ["payer-follow-up"],
  waitingSecondary: ["waiting-secondary"],
  denied: ["denied"]
};
const SERVER_QUEUE_CARD_KEYS = new Set(Object.keys(QUEUE_SOURCE_VALUES));
const SERVER_BILLER_FOCUS_KEYS = new Set(["dueNow", "denials", "payer", "secondary", "highBalance"]);

const MAX_RENDERED_TRACKER_ROWS = 50;
const TRACKER_PAGE_SIZE_OPTIONS = [50, 100, 150, 200, 250, 300, 350, 400, 450, 500];
// A CONTROL THAT DOES NOT DO WHAT IT SAYS IS WORSE THAN NO CONTROL (2026-08-11).
// This was MAX_RENDERED_TRACKER_ROWS, so the live API lane clamped every one of
// the nine larger choices back to 50: you picked 500, the box went back to
// reading 50, and the pager agreed with the box.
//
// The cap was a render guard, so it was retired on measurement, not on taste.
// Measured in a real browser at ~40 columns over a 2,536-row corpus (median of
// 3 page turns, scripts/_pagesize_perf_20260811.cjs):
//   50 rows  =  2,450 cells =  ~90ms,  0ms main thread blocked
//  100 rows  =  4,900 cells = ~186ms,  0ms main thread blocked
//  250 rows  = 12,250 cells = ~666ms,  0ms main thread blocked
//  500 rows  = 24,500 cells = ~802ms,  0ms main thread blocked
// Nothing freezes, and the server has allowed 2,500 all along
// (trackerQueryResolver MAX_PAGE_SIZE). Sub-second for a page the user
// deliberately asked to be ten times bigger is a trade he is entitled to make.
//
// The ceiling stays a real ceiling - a stale saved setting above the largest
// offered option still gets pulled back - it is just no longer BELOW the
// options the dropdown advertises.
//
// MAX_RENDERED_TRACKER_ROWS stays 50 deliberately: it is the DEFAULT page size
// and the fallback for an unparseable value. 090cfd4 tuned first-render speed
// down to 50 on purpose and that was never the broken part.
const TRACKER_API_PAGE_SIZE = Math.max(...TRACKER_PAGE_SIZE_OPTIONS);
const MAX_REPORT_QUEUE_DRILLDOWN_PAGE_NUMBER = 1000;
const fastGridExcludedApiColumns = new Set(["notes"]);
// Hidden calculation evidence for the live grid. These identifiers are not
// workbook columns, but payment attribution and fee matching read them after
// trackerApiRowToClaim() hydrates each page. Request them only when the current
// principal already holds the PHI permission; the server still independently
// intersects the request with its own allowed-column envelope.
const TRACKER_CALCULATION_EVIDENCE_COLUMNS = Object.freeze(["primaryExternalId", "secondaryExternalId"]);
// Hidden operational identity used by row actions and the claim drawer must
// survive every visible-column choice. The server still intersects these
// requested keys with the true principal's allowed columns. Claim-grouped rows
// deliberately omit service identity and remain read-only at service-line grain.
const TRACKER_OPERATIONAL_KEY_COLUMNS = Object.freeze(["orgId", "claimId", "serviceId"]);
const TRACKER_OON_RATE_TAG_COLUMNS = Object.freeze(["oonRateState", "oonLearnedRate", "oonRateChangePct", "oonPointCount"]);
function trackerOonRateTagAllowed() {
  return ["SuperAdmin", "StatesideBiller", "OffshoreBiller"].includes(canonicalRole(state.role));
}
function trackerApiResponseColumns(access = {}, mode = "service") {
  const rendered = (Array.isArray(access.allowedColumns) ? access.allowedColumns : [])
    .filter((column) => !fastGridExcludedApiColumns.has(column));
  const permittedKeys = new Set(permittedColumnsForMode(mode).map((column) => column.key));
  const operationalKeys = TRACKER_OPERATIONAL_KEY_COLUMNS.filter((column) => column === "orgId" || permittedKeys.has(column));
  const evidence = access.includePhi === true ? [...TRACKER_CALCULATION_EVIDENCE_COLUMNS] : [];
  if (access.includeFinancials === true || rendered.some((key) => ["primaryExpectedTotal", "totalExpected", "finalInsuranceExpected"].includes(key))) evidence.push("calculationGuards");
  // OON learned-rate tag under Primary Expected. Staff only: the server grants these
  // four to data:financials staff and refuses them to Client and RedactedClient.
  if (mode === "service" && access.includeFinancials === true && rendered.includes("primaryExpectedTotal") && trackerOonRateTagAllowed()) evidence.push(...TRACKER_OON_RATE_TAG_COLUMNS);
  // The Notes cell is drawn from the latest PORTAL note, which the server only
  // reads when serviceLineNotes is asked for. That column is hidden by default
  // behind Notes, so asking only for what is on screen left every Notes cell
  // empty (2026-10-16, GetSetABA claim 932).
  const noteInputs = rendered.includes("mergedNotes") && permittedKeys.has("serviceLineNotes") ? ["serviceLineNotes"] : [];
  // The hold cells say "or earlier" / "0+" only when the row carries
  // holdStartObserved (trackerHoldCellValue). It is not a column of its own, so
  // it rides along whenever a hold column is on screen; without it every
  // pre-tracking hold read as a confident start (production, 2026-10-21).
  const holdInputs = ["holdReason", "onHoldSince", "daysOnHold", "holdReleasedOn"].some((key) => rendered.includes(key)) ? ["holdStartObserved"] : [];
  return Array.from(new Set([...rendered, ...noteInputs, ...holdInputs, ...operationalKeys, ...evidence]))
    .filter((column) => mode !== "claim" || column !== "serviceId");
}

// Claim-grouped row mode — same legacy workbook tracker columns, just rolled up.
// All portal-invented columns (Priority / Lifecycle / Status Category /
// Work Reason / Recommended Next / Calc Guards / Payer Resp / Patient
// Resp / Underpayment / Overpayment / Payments / Assigned / Review) are
// removed per user direction.
const claimColumns = [
  { key: "claimId", label: "Claim ID" },
  { key: "action", label: "Follow-up Action" },
  { key: "followUpDate", label: "Follow-up Date" },
  { key: "claimStatus", label: "Claim Status" },
  { key: "renderingProvider", label: "Provider" },
  { key: "client", label: "Client" },
  { key: "facility", label: "Facility" },
  { key: "primaryPayer", label: "Primary Payer" },
  { key: "secondaryPayer", label: "Secondary Payer" },
  { key: "match", label: "Match" },
  { key: "aging", label: "Aging" },
  { key: "totalExpected", label: "Total Expected" },
  { key: "totalPaid", label: "Total Paid" },
  { key: "owed", label: "Ins Owed" }
];

// Per user direction the column order is legacy workbook-derived but with two
// operational tweaks: EHR Entry ID is the third column (owner, 2026-09-22 -
// Claim ID, Service ID, EHR Entry ID are the three identifiers a biller uses
// to find one line in three systems), and the biller's working columns
// (Follow up Action, Follow up Action Date, Claim Status) follow them.
// Everything past Claim Status continues in legacy workbook source order. No
// portal-invented columns exist here.
//
// Both 'action' and 'followUpDate' are always editable inline — see
// serviceCellHtml / claimCellHtml. The action select offers the
// standard follow-up rules; the date input is freely typeable to any
// custom timing (a biller who doesn't want to wait the rule's default
// 30 days can enter 3 days). Permission gate: UPDATE_FOLLOW_UP.
const serviceColumns = [
  { key: "claimId", label: "Claim ID" },
  { key: "serviceId", label: "Service ID" },
  // THIRD BY DEFAULT (owner, 2026-09-22). The three identifier columns a
  // biller uses to find the same line in three systems - the claim, the
  // service line and the CentralReach billing entry - now sit together at
  // the front instead of leaving the third one 40 columns to the right.
  // This is the DEFAULT only: a saved arrangement still applies over it
  // (applyUserColumnOrder), and a biller who never arranged theirs simply
  // gets the new order. The frozen leading run is unchanged - claimId and
  // serviceId are still the only FREEZABLE_COLUMN_KEYS, so the run still
  // stops at the third column rather than pinning a fourth.
  { key: "ehrBillingEntryId", label: "EHR Entry ID" },
  { key: "action", label: "Follow-up Action" },
  { key: "followUpDate", label: "Follow-up Date" },
  // NOTES (owner, Nathan, 2026-09-23): "one column is enough". The newer of the
  // two note columns below, labelled with where it came from; hover or expand a
  // cell to read both. Its header dropdown lists the note texts of BOTH sources
  // and a tick returns every line whose EZClaim note OR latest portal note
  // matches (backend/mergedNotesColumn.js). The two originals stay in the
  // Columns dialog under their own names, hidden by default - see
  // DEFAULT_HIDDEN_COLUMN_KEYS. Workbook and CSV exports keep their own columns.
  { key: "mergedNotes", label: "Notes" },
  { key: "serviceLineNotes", label: "Latest Claim Note" },
  // SERVICE LINE NOTES - the EZClaim SOURCE note (SrvCustomField1), which is
  // what Max's tracker's Service Line Notes dropdown lists and what our own
  // workbook export's Service Line Notes column has always carried.
  //
  // DELIBERATELY NOT THE SAME THING as the row above. "Latest Claim Note" is the
  // portal claim-note ledger's newest note for the line; this is the text a
  // biller typed into the EHR. Collapsing the two is a mistake this codebase has
  // already made once - trackerWorkbookExport's "TWO DIFFERENT NOTES" comment
  // records 22,705 source notes and 1,140 workflow notes disappearing from the
  // file when they were - so they sit side by side under their own names.
  { key: "sourceServiceLineNotes", label: "Service Line Notes" },
  { key: "claimStatus", label: "Claim Status" },
  { key: "claimClassification", label: "Claim Class" },
  { key: "facility", label: "Facility" },
  { key: "patient", label: "Name" },
  { key: "dos", label: "Srvc Date" },
  { key: "place", label: "Place" },
  { key: "procedure", label: "Procedure" },
  { key: "m1", label: "M1" },
  { key: "m2", label: "M2" },
  { key: "charges", label: "Charges" },
  { key: "units", label: "Units" },
  { key: "adjs", label: "Adjs" },
  { key: "totalInsAmtPaid", label: "Ins Paid" },
  { key: "totalPatAmtPaid", label: "Pat Paid" },
  // LEFT AFTER INSURANCE (owner, 2026-09-23). These two are the source
  // service line's total patient balance (gold [Total Pat Bal] / [Positive Total
  // Pat Bal]): charges minus insurance paid and posted adjustments, parked on the
  // patient side. That is NOT PR 1/2/3 patient responsibility, and "Pat Bal" read
  // as "what the patient owes", so the grid names them for what they are.
  //   * keys unchanged - saved views, sorts, filters and facets address keys;
  //   * exportLabel keeps the workbook/CSV header byte-identical (the export
  //     contract names the tracker's headers, not the grid's);
  //   * description is the header tooltip and its accessible description;
  //   * hidden from Client and RedactedClient - see internalOnlyServiceColumns.
  { key: "totalPatBal", label: "Left After Insurance", exportLabel: "Pat Bal", description: "Charges minus insurance paid and posted adjustments. Not what the patient owes; PR 1/2/3 patient responsibility is shown in Patient Responsibility." },
  { key: "positiveTotalPatBal", label: "Left After Insurance (+)", exportLabel: "Pat Bal (+)", description: "Charges minus insurance paid and posted adjustments. Not what the patient owes; PR 1/2/3 patient responsibility is shown in Patient Responsibility. Negative amounts show as $0, and Medicaid lines show $0." },
  { key: "balance", label: "Balance" },
  { key: "primaryPayer", label: "Primary Payer" },
  { key: "respPartyName", label: "Resp Party" },
  { key: "secondaryPayer", label: "Secondary Payer" },
  { key: "renderingProvider", label: "Provider" },
  { key: "originalBillDate", label: "Bill Date" },
  { key: "billDateAging", label: "Bill Aging" },
  // How long the line sat in On Hold (backend/serviceLineHoldEpisodes.js).
  { key: "holdReason", label: "Hold Reason" },
  { key: "onHoldSince", label: "On Hold Since" },
  { key: "daysOnHold", label: "Days On Hold" },
  { key: "holdReleasedOn", label: "Hold Released" },
  { key: "totalPaid", label: "Total Paid" },
  { key: "addCode", label: "AddCode" },
  { key: "network", label: "Network" },
  { key: "writeOffAmount", label: "Write-off" },
  { key: "bcba", label: "BCBA" },
  { key: "rbt", label: "RBT" },
  { key: "patClassification", label: "Pat Class" },
  { key: "primaryRate", label: "Primary_Rate" },
  { key: "secondaryCalc", label: "Secondary_Calc" },
  { key: "primaryExpectedTotal", label: "Primary Expected" },
  { key: "secondaryRate", label: "Secondary_Rate" },
  { key: "secondaryExpectedTotal", label: "Secondary Expected" },
  { key: "totalExpected", label: "Total Expected" },
  { key: "owed", label: "Ins Owed" },
  { key: "match", label: "Match" },
  { key: "time", label: "Time" },
  { key: "aging", label: "Aging" },
  { key: "weekEnding", label: "Week Ending" },
];

// Service columns an external client role (Client, RedactedClient) is never
// shown. permittedColumnsForMode() builds BOTH the grid header row and the
// Columns dialog from clientServiceColumns, so one list hides a column in both;
// a saved view naming one of these keys simply loses it for that role.
//
// Service Line Notes (key serviceLineNotes) are NOT here and must never be: that historical key now
// carries the shared latest-claim-note projection, visible to every role.
//
// Left After Insurance / (+) (owner, 2026-09-23): an internal working figure
// that reads as a patient bill, so it stays with internal roles. This hides the
// COLUMN; it does not change a money value, and the server still carries the
// figure in the client feed because the client Receivables report derives its
// left-after-insurance side from it.
const internalOnlyServiceColumns = new Set(["totalPatBal", "positiveTotalPatBal"]);

// One wording for every surface that shows the figure outside the grid.
const LEFT_AFTER_INSURANCE_POSITIVE_HELP = serviceColumns.find((column) => column.key === "positiveTotalPatBal").description;

// A header cell that names the left-after-insurance figure, with the tooltip on
// hover and the same text as its accessible description. helpId names a hidden
// node the caller renders ONCE per surface (leftAfterInsuranceHelpNodeHtml);
// aria-describedby still reads a hidden node's text.
function leftAfterInsuranceHeaderHtml(helpId, label = "Left After Insurance") {
  return `<th title="${escapeHtml(LEFT_AFTER_INSURANCE_POSITIVE_HELP)}" aria-describedby="${escapeHtml(helpId)}">${escapeHtml(label)}</th>`;
}

function leftAfterInsuranceHelpNodeHtml(helpId) {
  return `<span id="${escapeHtml(helpId)}" hidden>${escapeHtml(LEFT_AFTER_INSURANCE_POSITIVE_HELP)}</span>`;
}

const REDACTED_CLIENT_HIDDEN_COLUMN_KEYS = new Set([
  "charges", "adjs", "primaryRate", "secondaryRate", "secondaryCalc", "primaryExpectedTotal",
  "secondaryAllowedTotal", "secondaryExpectedTotal", "totalExpected", "finalInsuranceExpected",
  "totalInsAmtPaid", "totalPaid", "finalInsurancePaid", "balance", "finalInsuranceBalance", "owed",
  "primaryDisbursementTotal", "secondaryDisbursementTotal", "unmatchedDisbursementTotal",
  "payerResponsibility", "underpayment", "overpayment", "writeOffAmount", "applied1", "applied2", "applied3",
  "revenuePerHour", "profitPerHour"
]);

const financialColumns = new Set([
  "charges",
  "adjs",
  "totalInsAmtPaid",
  "totalPatAmtPaid",
  "totalPatBal",
  "truePatientResponsibility",
  "positiveTotalPatBal",
  "balance",
  "primaryRate",
  "primaryExpectedTotal",
  "secondaryRate",
  "secondaryExpectedTotal",
  "totalExpected",
  "totalPaid",
  "owed",
  "payerResponsibility",
  "patientResponsibility",
  "underpayment",
  "overpayment",
  "paymentStatus",
  "applied1",
  "applied2",
  "applied3",
  "writeOffAmount",
  "revenuePerHour",
  "profitPerHour"
]);

const phiColumns = new Set([
  "patient",
  "respPartyName",
  "serviceLineNotes",
  // Raw EHR biller text. The server gates it on data:phi AND data:internalNotes
  // and denies it to every client role (trackerQueryResolver.operationalColumns);
  // this list is the browser half of the same rule.
  "sourceServiceLineNotes",
  // Both of the above, merged - so it is PHI whenever either is.
  "mergedNotes",
  "notes"
]);

// FROZEN COLUMNS ARE FROZEN BY KEY, NEVER BY POSITION.
//
// styles.css used to freeze .claims-table th/td:nth-child(1..3) while this file
// emitted .sticky-col/.sticky-claim/.sticky-service classes that had NO CSS at
// all - so the classes were inert and the freeze was purely positional. Column
// ORDER here is user-configurable: setColumnSelection() and server-supplied
// saved column views both accept an arbitrary key order, so any view that put
// "patient" in one of the first three slots PINNED A PATIENT NAME ON SCREEN for
// the entire 9,200px horizontal scroll - including during a screen share or a
// demo. That is the PHI control described in DESIGN_DIRECTION_2026.md section
// 3.7 item 9 (checklist G5 / M7).
//
// FREEZABLE_COLUMN_KEYS is now the ONLY route to stickiness. It holds record
// IDENTIFIERS - the two keys a biller needs kept in view while scrolling - and
// nothing else. Adding a key here is a PHI decision, and it is guarded:
// backend/verify_frozen_columns_are_key_based.js fails the build if any entry is
// a PHI or financial column, and if any sticky class this file emits has no
// matching rule in styles.css (the silent no-op that let the bug hide).
const FREEZABLE_COLUMN_KEYS = ["claimId", "serviceId"];
const FREEZABLE_COLUMN_CLASSES = { claimId: "sticky-claim", serviceId: "sticky-service" };
// Must agree with the min-widths declared for these classes in styles.css.
const FREEZABLE_COLUMN_WIDTHS = { claimId: 128, serviceId: 128 };
// The row-selection checkbox column is not a data column and carries no value,
// so it is freezable by construction rather than by key.
const STICKY_SELECT_KEY = "__rowSelect";
const STICKY_SELECT_WIDTH = 38;

// ===========================================================================
// EHR ENTRY ID OPENS THE ENTRY IN CENTRALREACH (owner, 2026-09-22).
// ===========================================================================
//
// The value in this column is EZClaim's SrvCustomField2, which IS the
// CentralReach billing entry id (trackerApiCellRawValue records the 112,144
// of 112,149 match that settled that). A biller reads it here and retypes it
// into CentralReach's billing manager; the billing manager takes it as a
// FILTER, so the retyping is the only part that was ever necessary.
//
// THE URL SHAPE, and why the dates are what they are. The owner's pasted link
// carried a start/end week that did not contain the entry's 6/26/25 service
// date, and CentralReach still showed exactly that one entry under a
// "Billing Entry: Entry ID: 400679754" chip - billingEntryId is the effective
// filter and the window is something it sits inside. He then re-tested the
// same entry with the window bounded to the service date itself and got the
// same single entry. So we send the ROW'S OWN service date for both ends: the
// narrowest window that provably contains the entry, and the one a reader
// recognises when the page opens. A row with no DOS gets today for both.
//
// ONE CONSTANT. Changing the host or the route is this line and nothing else.
const CENTRAL_REACH_BILLING_MANAGER_URL = "https://members.centralreach.com/#billingmanager/billing/";

// NOTHING CLICKABLE FOR A VALUE WE CANNOT ADDRESS. The live lane leaves this
// column BLANK rather than guess, and the local sample book carries
// placeholders like EHR-CR-918-01. A link built on either would drop a biller
// into an empty billing manager, which reads as CentralReach being broken
// rather than as our value being absent. CentralReach billing entry ids are
// integers, so that is the whole test - and it is applied to the value, never
// to the lane, because both lanes can carry either kind.
function centralReachBillingEntryId(value) {
  const text = String(value == null ? "" : value).trim();
  return /^[0-9]+$/.test(text) ? text : "";
}

// "" when the id is blank or not an entry id. Callers render plain text then.
function centralReachBillingEntryUrl(entryId, dosValue) {
  const id = centralReachBillingEntryId(entryId);
  if (!id) return "";
  const day = normalizeDateValue(dosValue) || localDateString();
  return `${CENTRAL_REACH_BILLING_MANAGER_URL}?billingEntryId=${encodeURIComponent(id)}`
    + `&startdate=${encodeURIComponent(day)}&enddate=${encodeURIComponent(day)}`;
}

// THE CELL'S TEXT IS EXACTLY THE ID. The affordance is an inline SVG with no
// text node and aria-hidden, so the id is still the link's accessible name
// and still the only thing a Ctrl+C out of the sheet or a browser print
// carries - the string a biller pastes back into CentralReach. The workbook
// export is built by the server from the same value and never sees this HTML.
//
// target=_blank keeps the reader's place in a filtered 48-column sheet;
// rel="noopener noreferrer" is not optional with it. The anchor also keeps
// the cell out of the row's open-the-drawer click, which already ignores
// "button, a, input, ..." - so following the link never also opens a drawer.
function ehrBillingEntryLinkHtml(href, text) {
  return `<a class="ehr-entry-link" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" title="Open in CentralReach">`
    + `${escapeHtml(text)}`
    + `<svg class="ehr-entry-link__icon" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M4.75 1.75h5.5v5.5"/><path d="M10.25 1.75 5.5 6.5"/><path d="M8.75 7v3.25H1.5V3h3.25"/></svg></a>`;
}

// SEVERAL IDS IN ONE CELL. Owner, 2026-09-23, over a screenshot of the grid: a
// single service line can carry more than one CentralReach billing entry, and
// EZClaim hands the pair down in ONE field as "403889234;403889808". The
// single-value gate above then failed the WHOLE cell, so a biller lost both
// links in exactly the place that needs two clicks rather than one.
//
// DELIMITED CELLS ONLY. A semicolon or a comma is what makes a cell a list;
// inside such a cell surrounding whitespace is separator noise as well, so
// "403889234; 403889808" is the same two ids. A value with NO delimiter stays
// ONE value, so "4006 79754" - a mistyped id rather than two ids - still falls
// through to plain text instead of becoming two links into an empty billing
// manager, which is defect class 2 above and the whole reason for the gate.
//
// Order is the owner's, blanks drop, and a repeated id is linked once: two
// identical links side by side read as two entries that do not exist.
function ehrBillingEntryIdTokens(value) {
  const text = String(value == null ? "" : value).trim();
  if (!text) return [];
  if (!/[;,]/.test(text)) return [text];
  const seen = new Set();
  const tokens = [];
  text.split(/[;,\s]+/).forEach((piece) => {
    const token = piece.trim();
    if (!token || seen.has(token)) return;
    seen.add(token);
    tokens.push(token);
  });
  return tokens;
}

// THE SEPARATOR IS TEXT, not a border or a margin: it lives inside the cell's
// selection, so a copy out of the sheet still reads as the ids the way the
// field carried them, and a print shows the same string.
const EHR_BILLING_ENTRY_ID_SEPARATOR = "; ";

// "" when NOTHING in the cell is addressable - each caller then renders its own
// plain text exactly as it did before. Otherwise every id in the cell is its own
// link and every token that is not an id stays plain text in place, so a cell
// that is half good is not all or nothing.
function ehrBillingEntryCellHtml(value, dosValue) {
  const tokens = ehrBillingEntryIdTokens(value);
  if (!tokens.some((token) => centralReachBillingEntryId(token))) return "";
  return tokens
    .map((token) => {
      const href = centralReachBillingEntryUrl(token, dosValue);
      return href ? ehrBillingEntryLinkHtml(href, token) : escapeHtml(token);
    })
    .join(EHR_BILLING_ENTRY_ID_SEPARATOR);
}

const currencyColumns = new Set([
  "totalExpected", "totalPaid", "owed", "balance", "charges", "adjs",
  "totalInsAmtPaid", "totalPatAmtPaid", "totalPatBal", "truePatientResponsibility",
  "positiveTotalPatBal", "primaryRate", "primaryExpectedTotal",
  "secondaryRate", "secondaryExpectedTotal", "payerResponsibility",
  "patientResponsibility", "underpayment", "overpayment", "writeOffAmount",
  "applied1", "applied2", "applied3", "revenuePerHour", "profitPerHour",
  "secondaryAllowedTotal", "staticTotalExpected", "finalExpectedTotalNumeric", "finalInsuranceExpected", "finalInsuranceBalance"
]);

const numericColumns = new Set(["aging", "billDateAging", "daysOnHold", "units", "unitsConverted"]);

// MONEY CERTAINTY: "we know it is zero" is not the same as "we do not know".
//
// NORTH_STAR.md section 6 - never show a confident wrong number; a blank that
// says "unknown" beats a figure that is wrong, and a dead upstream feed reads as
// "no rate", never as "$0 owed". The DATA layer already got this right (commit
// c89a464: a warehouse rate of 0 now means ABSENT, not "$0 contracted"). The
// PRESENTATION layer still lied: an unpriceable line rendered $0 in every money
// column, with the "no rate" truth carried only by the adjacent Match pill.
//
// UNKNOWN_MONEY is an EXPLICIT sentinel, deliberately NOT null/undefined.
// money() has ~843 call sites and roughly 70% of them pass a bare property
// access; making a missing property mean "unknown" would turn hundreds of
// legitimately-empty aggregates into dashes, which is the same lie pointed the
// other way. Unknown has to be ASSERTED by a caller that actually knows.
const MONEY_UNKNOWN_TEXT = "—";
const UNKNOWN_MONEY = Object.freeze({ __unknownMoney: true });
function isUnknownMoney(value) {
  return value === UNKNOWN_MONEY || Boolean(value && typeof value === "object" && value.__unknownMoney === true);
}

// Which money columns lose their meaning when a service line has no rate on
// file. Charges, payments, adjustments, write-offs and patient balances are NOT
// in these sets on purpose: that is real money which exists independently of the
// rate, so a 0 there is a KNOWN zero and must keep rendering $0.
const PRIMARY_RATE_DERIVED_MONEY_COLUMNS = new Set(["primaryRate", "primaryExpectedTotal"]);
const SECONDARY_RATE_DERIVED_MONEY_COLUMNS = new Set(["secondaryRate", "secondaryExpectedTotal", "secondaryAllowedTotal"]);
// The totals fold BOTH rates in, so either gap makes them unknown.
const EXPECTED_DERIVED_MONEY_COLUMNS = new Set([
  "totalExpected", "owed", "balance", "underpayment", "overpayment", "payerResponsibility",
  "staticTotalExpected", "finalExpectedTotalNumeric", "finalInsuranceExpected", "finalInsuranceBalance"
]);

const NO_PRIMARY_RATE_REASON = "No primary rate on file for this service line, so the expected amount is not known. This is not $0 - add the rate in Fee Schedules.";
const NO_SECONDARY_RATE_REASON = "No secondary rate on file for this service line, so the expected amount is not known. This is not $0 - add the rate in Fee Schedules.";

// A CLAIM's total is unknown because SOME of its service lines are. Say how
// many: at the claim grain the bare service-line reason reads as a copy-paste,
// and "1 of 6 lines" is the difference between "this claim is unpriced" and
// "one line on this claim is unpriced". Empty for the service grain, which is
// already talking about exactly one line.
function rateMissingGrainNote(missingLines, lineCount) {
  const missing = Number(missingLines) || 0;
  const total = Number(lineCount) || 0;
  if (!missing || total < 2) return "";
  return ` ${missing} of this claim's ${total} service lines ${missing === 1 ? "has" : "have"} no rate on file.`;
}

// The plain-English reason this money cell is UNKNOWN, or "" when the figure is
// knowable - including when it is a genuine zero.
function unknownMoneyReason(key, certainty = {}) {
  if (!currencyColumns.has(key)) return "";
  const primaryRateMissing = Boolean(certainty && certainty.primaryRateMissing) && (key === "primaryRate" || !certainty.primaryRateEstimated);
  const secondaryRateMissing = Boolean(certainty && certainty.secondaryRateMissing) && (key === "secondaryRate" || !certainty.secondaryRateEstimated);
  if (primaryRateMissing && (PRIMARY_RATE_DERIVED_MONEY_COLUMNS.has(key) || EXPECTED_DERIVED_MONEY_COLUMNS.has(key))) return NO_PRIMARY_RATE_REASON + rateMissingGrainNote(certainty.primaryRateMissingLines, certainty.lineCount);
  if (secondaryRateMissing && (SECONDARY_RATE_DERIVED_MONEY_COLUMNS.has(key) || EXPECTED_DERIVED_MONEY_COLUMNS.has(key))) return NO_SECONDARY_RATE_REASON + rateMissingGrainNote(certainty.secondaryRateMissingLines, certainty.lineCount);
  return "";
}

// The money cell for an unknown value: an em-dash that SAYS why - on hover and
// to a screen reader. Never $0, and never blank (blank reads as "not loaded yet").
function moneyUnknownHtml(reason) {
  const text = String(reason || NO_PRIMARY_RATE_REASON);
  return `<span class="money-unknown" title="${escapeHtml(text)}">${MONEY_UNKNOWN_TEXT}<span class="visually-hidden">${escapeHtml(text)}</span></span>`;
}

// The live (API-backed) lane has no financial view per row - it carries the
// server's finalized Match instead, which is the same signal by another name.
function trackerRowMoneyCertainty(matchValue, guardValue = "") {
  const text = String(matchValue == null ? "" : matchValue).trim();
  return {
    // The retired collapsed "No Rate Found" value meant the PRIMARY gap.
    primaryRateMissing: /^(?:missing fee schedule|no primary rate)$/i.test(text) || matchValueIsLegacyCollapsedNoRate(text),
    secondaryRateMissing: /^no secondary rate$/i.test(text),
    ...trackerEstimateCertainty(guardValue)
  };
}

function trackerEstimateCertainty(guardValue) {
  const flags = (Array.isArray(guardValue) ? guardValue : String(guardValue || "").split(",")).map((flag) => String(flag).trim());
  const unsafe = flags.some((flag) => /unreadable|ambiguous|unavailable|unsupported/.test(flag));
  return {
    primaryRateEstimated: !unsafe && flags.includes("primary_rate_estimated"),
    secondaryRateEstimated: !unsafe && flags.includes("secondary_rate_estimated")
  };
}

function moneyIsEstimated(key, certainty = {}) {
  if (key === "primaryRate" || key === "secondaryRate" || unknownMoneyReason(key, certainty)) return false;
  return Boolean((certainty.primaryRateEstimated && (PRIMARY_RATE_DERIVED_MONEY_COLUMNS.has(key) || EXPECTED_DERIVED_MONEY_COLUMNS.has(key)))
    || (certainty.secondaryRateEstimated && (SECONDARY_RATE_DERIVED_MONEY_COLUMNS.has(key) || EXPECTED_DERIVED_MONEY_COLUMNS.has(key))));
}

// Per user direction: no AR Scan / Denial Triage / Payment Posting /
// Executive preset tabs. All Data is all data — that's it.
const workbenchPresets = {
  full: { label: "Accounts Receivable", hint: "Excel tracker parity — every column", service: null, claim: null }
};

const workbenchPresetOrder = ["full"];

// FRONTEND MIRROR of backend/trackerQueryResolver.js CLIENT_WORKBENCH_COLUMNS.
//
// The server projects an external client role holding client:workbench through
// that frozen list and IGNORES data:phi / data:financials while doing it. The
// grid used to pick its columns by those same two permissions — which a
// ClientBiller correctly does not hold — so without this mirror the client
// grid rendered with no patient and no money at all: every column the
// allow-list exists to serve, filtered out one layer above the data.
//
// Worse, the obvious support fix for that would have been to hand ClientBiller
// data:phi + data:financials, which the server would tolerate in silence while
// widening the report dataset, the claim drawer's money and every other
// permission-gated surface. So the fix is here, and
// backend/verify_client_workbench_columns.js pins this list against the backend
// export element by element.
const CLIENT_WORKBENCH_COLUMN_KEYS = Object.freeze([
  // ehrBillingEntryId is EZClaim's SrvCustomField2 - the client's own billing entry,
  // and how they find the line in their EHR. Mirrors the backend list; the verifier
  // pins the two element by element, so they move together or the guard fails.
  "claimId", "serviceId", "ehrBillingEntryId", "orgId", "client", "claimStatus", "facility", "patient",
  "renderingProvider", "dos", "procedure", "m1", "m2", "place", "units", "aging", "serviceLineNotes",
  "primaryPayer", "secondaryPayer", "respPartyName", "charges", "adjs",
  "primaryExpectedTotal", "secondaryAllowedTotal", "secondaryExpectedTotal",
  "totalExpected", "finalInsuranceExpected", "totalInsAmtPaid", "totalPaid",
  "finalInsurancePaid", "totalPatAmtPaid", "totalPatBal", "truePatientResponsibility",
  "balance", "finalInsuranceBalance", "owed",
  "paymentStatus", "primaryDisbursementTotal", "secondaryDisbursementTotal",
  "unmatchedDisbursementTotal", "paymentExternalPayerIds", "calculationGuards"
]);
const CLIENT_WORKBENCH_COLUMN_KEY_SET = new Set(CLIENT_WORKBENCH_COLUMN_KEYS);

// Full Client was widened by the owner-approved 2026-08-20 role correction to
// the full read-only All Data projection. This is the frontend mirror of
// backend/trackerQueryResolver.js FULL_CLIENT_WORKBENCH_COLUMNS; applying the
// older 40-column ClientBiller list here would now hide valid columns.
const FULL_CLIENT_WORKBENCH_COLUMN_KEYS = Object.freeze([
  "claimId", "serviceId", "ehrBillingEntryId", "priority", "lifecycleStage", "statusCategory", "queueReason",
  "recommendedNextAction", "calculationGuards", "orgId", "client", "claimStatus",
  "claimClassification", "patClassification", "authorizationNumber", "authorizationLane",
  "authorizationPayerSequence", "authorizationPayerInternalId", "authorizationPayerName",
  "authorizationPayerExternalId", "authorizationMarkerLane", "facility", "patient", "dos",
  "procedure", "m1", "m2", "place", "charges", "units", "adjs", "totalInsAmtPaid",
  "totalPatAmtPaid", "totalPatBal", "truePatientResponsibility", "positiveTotalPatBal",
  "balance", "primaryPayer", "primaryExternalId", "respPartyName", "secondaryPayer",
  "secondaryExternalId", "renderingProvider", "totalPaid", "primaryRate",
  "primaryExpectedTotal", "secondaryRate", "secondaryCalc", "secondaryAllowedTotal",
  "secondaryExpectedTotal", "staticTotalExpected", "finalExpectedTotalNumeric",
  "finalSecondaryExpectedNumeric", "secondaryExpectedChangeNumeric", "totalExpectedChangeNumeric",
  "finalInsuranceExpected", "finalInsurancePaid", "finalInsuranceBalance", "prAdjustmentTotal",
  "isSecondarySubmitted", "paymentExternalPayerIds", "primaryDisbursementTotal",
  "secondaryDisbursementTotal", "unmatchedDisbursementTotal", "totalExpected", "owed",
  "writeOffAmount", "match", "aging", "originalBillDate", "billDateAging", "claimDue",
  "serviceLineNotes", "action", "followUpDate", "pmtSource1", "applied1", "pmtSource2", "applied2",
  "pmtSource3", "applied3", "paymentStatus", "assignedTo", "assignmentStatus",
  "assignmentDue", "runId", "ingestedAtUtc"
]);
const FULL_CLIENT_WORKBENCH_COLUMN_KEY_SET = new Set(FULL_CLIENT_WORKBENCH_COLUMN_KEYS);

// WHICH OF THE 39 THE GRID ACTUALLY RENDERS — and why the rest never will.
//
// permittedColumnsForMode() intersects the allow-list with the frontend column
// DEFINITIONS, so an allow-listed key with no definition is simply dropped. That
// is correct: the allow-list is a FEED contract shared by three client surfaces
// (this grid, /api/client/reports/claims, /api/client/payments), not a grid
// contract. But it was also SILENT — the list named 40 columns, the grid could
// draw 28, and nothing recorded which 11 were never meant to be drawn or why.
// The next key added would join that set exactly as invisibly.
//
// So the 11 are declared here WITH their reason, and the verifier enforces an
// EXCLUSIVE rule: every allow-listed key is either grid-renderable or declared
// below — never both, never neither. A column that quietly stops rendering
// becomes a failed build instead of a support ticket nobody can reproduce.
//
// NONE of these is a missing grid column, and two are forbidden ones:
// PORTAL_DIRECTIVES.md lines 128/130 list orgId and truePatientResponsibility
// among the portal-invented columns removed for tracker parity, "do NOT re-add
// without user permission". The three finalInsurance* keys are already on the
// grid under their tracker-parity names, so adding them would be duplicate
// columns rather than new data.
const CLIENT_WORKBENCH_NON_GRID_KEYS = Object.freeze({
  orgId: "identity: the tenant join, read outside the column loop by renderTrackerApiRows. Grid column banned by PORTAL_DIRECTIVES.md, \"All Data columns — BLSM Excel tracker parity, strict\".",
  secondaryAllowedTotal: "feed: the client report lane recomputes secondary expected from it, because secondaryRate and secondaryCalc are redacted for clients.",
  finalInsuranceExpected: "feed: client report lane. Already on the grid as totalExpected — the same number under its tracker-parity name.",
  finalInsurancePaid: "feed: client report lane. Already on the grid as totalPaid / totalInsAmtPaid.",
  finalInsuranceBalance: "feed: client report lane. Already on the grid as owed / balance.",
  truePatientResponsibility: "no live lane: absent from API_TO_DB_COLUMNS, so it resolves to nothing on the lane clients actually run. Grid column banned by PORTAL_DIRECTIVES.md, \"All Data columns — BLSM Excel tracker parity, strict\"; its live-lane twin prAdjustmentTotal is deliberately not allow-listed.",
  paymentStatus: "feed: the Match signal on the client report lane and the row status on the client payments lane.",
  primaryDisbursementTotal: "feed: payment attribution split on the client payments lane.",
  secondaryDisbursementTotal: "feed: payment attribution split on the client payments lane.",
  unmatchedDisbursementTotal: "feed: payment attribution split; drives the Attribution review status.",
  paymentExternalPayerIds: "feed: the payment reference line on the client payments lane.",
  totalPatBal: "feed: the client Receivables report's left-after-insurance side. Grid column (Left After Insurance) is internal-only by owner decision 2026-09-23 - see internalOnlyServiceColumns."
});

const clientServiceColumns = serviceColumns.filter((column) => !internalOnlyServiceColumns.has(column.key));

const clientClaimColumns = claimColumns.filter((column) => !["assignedTo", "assignmentStatus"].includes(column.key));

const columnViewModes = ["service", "claim"];
const billerColumnViewKeys = {
  service: ["claimId", "serviceId", "action", "followUpDate", "claimStatus", "patient", "dos", "procedure", "primaryPayer", "secondaryPayer", "renderingProvider", "owed", "mergedNotes", "notes"],
  claim: ["claimId", "action", "followUpDate", "claimStatus", "renderingProvider", "client", "facility", "primaryPayer", "secondaryPayer", "aging", "owed"]
};
let columnViewUi = { open: false, search: "", saving: false, label: "", selectedSavedViewId: "" };
let columnViewApiState = { loaded: false, loading: false, status: "idle", error: "", loadedModes: {}, loadingModes: {} };

const DATA_URLS = [
  "./data/real_claims.local.json",
  "./data/sample_claims.json"
];

let sourceClaims = [];
let sourceFeeScheduleRows = [];
let databaseTenantInventory = [];
let sourceDataInfo = {
  url: "./data/sample_claims.json",
  label: "Information temporarily unavailable",
  detail: "Non-PHI local fallback data",
  generatedAt: "",
  generatedFrom: "",
  isLocalRealData: false,
  sourceStats: {},
  rowLimit: ""
};
let state;
let syncingGridScroll = false;
let derivedClaimCache = new WeakMap();
let derivedServiceCache = new WeakMap();
// Performance: getClaims() previously minted fresh merged objects on every
// call, defeating the per-claim financial WeakMap caches (keyed on object
// identity). We now memoize the merged array and only rebuild when either the
// underlying data changed (claimsDataVersion bumped at every mutation site) or
// the role/user/scope that permittedByRole filters on changed.
let claimsDataVersion = 0;
let getClaimsCache = { version: -1, scopeKey: null, claims: null };
let liveQueryMapState = { status: "idle", response: null, error: "" };
let queryMapRulebookState = { status: "idle", response: null, error: "" };
let queryMapActiveTab = "overview";
let queryMapSearchText = "";
let clientPaymentChecksApiState = { key: "", status: "idle", response: null, error: "" };
// D3 S4 - the portal's OWN client-group roster, read from portal.organizations.
// Distinct from clientPaymentChecksApiState.organizations above, which is a
// GROUP BY over claim data and therefore cannot contain a group that has no
// claims yet. Fetched only by the two SuperAdmin modals, so for every other
// session it stays empty and the pickers behave exactly as they did pre-S4.
let portalOrgDirectoryState = { status: "idle", orgs: [], error: "" };
let clientReportClaimsApiState = { key: "", status: "idle", rows: [], totalRows: 0, truncated: false, error: "", retried: false };
let clientReportAggregateApiState = { key: "", status: "idle", response: null, error: "" };
let clientReportAggregatePage = 1;
let clientReportAggregatePageIdentity = "";
let clientReportServerDrillState = { status: "idle", key: "", response: null, error: "", page: 1 };
let clientReportAggregateFilters = { groupBy: "payer", client: "All", payer: "All", fromDate: "", toDate: "", slicers: {} };
const clientPaymentCheckDetailCache = new Map();
let clientPaymentsDashState = { search: "", payer: "All", range: "all", sort: "date", dir: "desc", expanded: "", org: "", focusSearch: false, page: 1 };
let clientPaymentsLastModel = null;
let pendingPortalAuthUser = null;

const els = {};

// Boot the app once the DOM is ready. The script can now be loaded
// dynamically (cache-bust via timestamped URL injected after DOMContentLoaded),
// so we must check readyState — if the document already passed the
// 'loading' phase, DOMContentLoaded will NEVER fire again and the boot
// handler would silently never run, leaving the page stuck on the
// pre-render 'Local JSON Snapshot' placeholder.
const __tmsBoot = async () => {
  const bootRequestSeq = ++tmsBootRequestSeq;
  const bootStillCurrent = () => bootRequestSeq === tmsBootRequestSeq;
  const step = (n, fn) => { try { fn(); } catch (e) { console.error("[tms.boot]", n, e); throw e; } };
  step("bindElements", () => bindElements());
  const authenticated = await requirePortalLogin();
  if (!bootStillCurrent()) return;
  if (!authenticated) return;
  const sourceData = await loadSourceData();
  if (!bootStillCurrent()) return;
  sourceClaims = sourceData.claims;
  bumpClaimsDataVersion();
  sourceFeeScheduleRows = sourceData.feeSchedules;
  sourceDataInfo = sourceData.info;
  const tenantInventory = await loadDatabaseTenantInventory();
  if (!bootStillCurrent()) return;
  databaseTenantInventory = tenantInventory;
  step("hydrateCustomUsersForState", () => mergeCustomUsers(readCustomUsersFromStorage()));
  step("loadState", () => { state = loadState(); });
  step("applyPendingPortalUser", () => applyPendingPortalAuthUser());
  // Staff names for note authorship are NOT in this bundle (app.js is served
  // anonymously). Ask the authenticated, internal-notes-gated endpoint for them;
  // if it is not there yet the panel just shows initials.
  step("requestNoteAuthorDirectory", () => requestNoteAuthorDirectory());
  // Seed realistic Offshore routing on the demo dataset AFTER state has
  // loaded so we can write to both sourceClaims AND state.workflowByClaim.
  // Otherwise a stale workflowByClaim from localStorage overrides the
  // seed when getClaims() spreads workflow state over the claim.
  if (localFallbackSeedingEnabled()) {
    try { seedDemoOffshoreAssignments(); } catch (e) { console.error("[tms.boot] seedDemoOffshoreAssignments", e); }
    try { normalizeDemoNetworkField(); } catch (e) { console.error("[tms.boot] normalizeDemoNetworkField", e); }
  }
  step("applyUrlState", () => applyUrlState());
  step("applyBootDefaultView", () => applyBootDefaultView());
  step("normalizeResponsiveLayoutState", () => normalizeResponsiveLayoutState());
  step("seedControls", () => seedControls());
  step("bindEvents", () => bindEvents());
  step("followUpActionChoiceSearch", () => initializeFollowUpActionChoiceSearch());
  // Apply the stored theme before the first truthful shell paint. Previously
  // the shell was exposed in requirePortalLogin, then state/layout/theme were
  // applied several awaits later, producing the visible "old portal -> new
  // portal" swap reported by users.
  try { document.documentElement.dataset.theme = resolveTheme(getStoredTheme()); } catch (_) {}
  step("installInteractionFeedback", () => installInteractionFeedback());
  step("updateDataSourceBox", () => updateDataSourceBox());
  // A throw in render() used to abort boot after the shell was already visible,
  // which left All Data as an empty table with "Live unknown" and no retry.
  try {
    render();
  } catch (e) {
    console.error("[tms.boot] render", e);
  }
  try { kickTrackerApiAfterBoot(); } catch (e) { console.error("[tms.boot] tracker kick", e); }
  // Badges and nav counts that stood aside for the first paint run from here.
  try { scheduleFirstGridPaintRelease(); } catch (e) { console.error("[tms.boot] scheduleFirstGridPaintRelease", e); }
  try { refreshWorkflowApiHealth(); } catch (e) { console.error("[tms.boot] refreshWorkflowApiHealth", e); }
  try { if (workflowApiBase() && portalAuthToken()) flushWorkflowCommandQueue(); } catch (e) { console.error("[tms.boot] flushWorkflowCommandQueue", e); }
  try { CmdPalette.bind(); } catch (e) { console.error("[tms.boot] CmdPalette.bind", e); } // UI-100 #26
  try { installSheetKeyboardNav(); } catch (e) { console.error("[tms.boot] installSheetKeyboardNav", e); } // Excel-like row navigation
  try { applyThemeFromState(); } catch (e) { console.error("[tms.boot] applyThemeFromState", e); } // UI-100 #44
  try { updateDemoDataCallout(); } catch (e) { console.error("[tms.render] updateDemoDataCallout", e); } // UI-100 #81
  try { maybeRunFirstTour(); } catch (e) { console.error("[tms.boot] maybeRunFirstTour", e); } // UI-100 #94
  try { installFriendlyErrorHandler(); } catch (e) { console.error("[tms.boot] installFriendlyErrorHandler", e); } // UI-100 #89
  try { installReportsPrintScope(); } catch (e) { console.error("[tms.boot] installReportsPrintScope", e); } // scope Reports @media print to body.reports-printing
  // A workbook may still be building from before this reload; pick it back up
  // rather than making them resubmit and wait through the whole build again.
  try { bindTrackerExportJobChip(); } catch (e) { console.error("[tms.boot] bindTrackerExportJobChip", e); }
  try { resumeTrackerWorkbookExportJob(); } catch (e) { console.error("[tms.boot] resumeTrackerWorkbookExportJob", e); }
  portalAppBooted = true;
  // Authentication alone is not readiness. Reveal only after role gates,
  // saved layout, URL state, controls, and the first render are coherent.
  showPortalAppShell();
  // The earlier tracker kick runs before portalAppBooted is true. Start the
  // publication watcher only now, when its readiness guard can accept boot.
  try { startTrackerPublicationWatcher(); } catch (e) { console.error("[tms.boot] publication watcher", e); }
};

async function requirePortalLogin() {
  if (String(window.location.hash || "").startsWith("#reset-password=")) {
    const token = String(window.location.hash).slice("#reset-password=".length);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    return (await renderPortalAccountRecovery({ token })) === true;
  }
  // Completed invite verification has already established the session.
  // Continue this boot just as after an ordinary successful sign-in.
  if (await maybeRenderInviteOnboarding()) return true;
  if (!portalLoginRequired()) {
    hydratePortalAuthToken();
    return true;
  }
  hydratePortalAuthToken();
  if (portalAuthToken()) {
    if (!applyStoredPortalUserToState()) {
      // A bearer without its authenticated identity snapshot must never boot
      // the default SuperAdmin/workbench shell. A valid snapshot with state
      // not yet created is success — do not treat that as an orphan bearer.
      const signedIn = await offerPortalReauth();
      return signedIn === true;
    }
    return true;
  }
  const signedIn = await renderPortalLogin();
  return signedIn === true;
}

function portalLoginRequired() {
  if (!document.head || !document.body || typeof fetch !== "function") return false;
  if (window.TMS_REQUIRE_PORTAL_LOGIN === false) return false;
  return Boolean(window.TMS_REQUIRE_PORTAL_LOGIN || workflowApiBase());
}

function hydratePortalAuthToken() {
  const token = portalAuthToken();
  if (!token) return;
  window.TMS_WORKFLOW_API_TOKEN = window.TMS_WORKFLOW_API_TOKEN || token;
  window.TMS_TRACKER_API_TOKEN = window.TMS_TRACKER_API_TOKEN || token;
  window.TMS_REPORT_API_TOKEN = window.TMS_REPORT_API_TOKEN || token;
  migratePortalAuthTokenToSession(token);
}

function portalAuthToken() {
  return window.TMS_PORTAL_AUTH_TOKEN
    || safeSessionStorageGet(PORTAL_AUTH_SESSION_TOKEN_KEY)
    || safeLocalStorageGet(PORTAL_AUTH_TOKEN_KEY)
    || safeLocalStorageGet(WORKFLOW_API_TOKEN_KEY)
    || "";
}

// The read-only viewer account (backend/readOnlyViewerPolicy.js) keeps its
// token across browser restarts so its 30-day session is usable. Every other
// account stays tab-scoped.
function portalStoredUserIsReadOnlyViewer() {
  try {
    return JSON.parse(safeLocalStorageGet(PORTAL_AUTH_USER_KEY) || "{}").readOnlyViewer === true;
  } catch {
    return false;
  }
}

function migratePortalAuthTokenToSession(token) {
  if (!token) return;
  safeSessionStorageSet(PORTAL_AUTH_SESSION_TOKEN_KEY, token);
  if (portalStoredUserIsReadOnlyViewer()) {
    safeLocalStorageSet(PORTAL_AUTH_TOKEN_KEY, token);
    return;
  }
  [
    PORTAL_AUTH_TOKEN_KEY,
    WORKFLOW_API_TOKEN_KEY,
    TRACKER_API_TOKEN_KEY,
    REPORT_API_TOKEN_KEY
  ].forEach((key) => {
    if (safeLocalStorageGet(key) === token) safeLocalStorageRemove(key);
  });
}

// The signed-out chrome: who this is and what it is, above the card. The
// wordmark artwork is navy-on-transparent, so dark mode plates it in white
// (--login-plate) rather than inverting a logo we do not own the source of.
function portalLoginBrandHtml() {
  return `
    <section class="portal-login-hero" aria-labelledby="portalLoginHeroTitle">
      <div class="portal-login-ambient" aria-hidden="true"><span class="portal-login-orb portal-login-orb-a"></span><span class="portal-login-orb portal-login-orb-b"></span><span class="portal-login-orb portal-login-orb-c"></span><span class="portal-login-orbit"></span><span class="portal-login-dots"></span><span class="portal-login-sweep"></span></div>
      <div class="portal-login-hero-copy">
        <p class="portal-login-product"><span aria-hidden="true"></span> TMS VISTA · YOUR BUSINESS OVERVIEW</p>
        <h2 id="portalLoginHeroTitle">Your business.<br> One clear <em>overview.</em></h2>
        <p class="portal-login-hero-description">See your performance. Understand your progress.<br>Reports, claim status, and next steps. All in one place.</p>
      </div>
      <div class="portal-login-flow" aria-hidden="true">
        <div class="portal-login-report-illustration"><span class="portal-login-flow-icon"><svg viewBox="0 0 24 24"><path d="M4 4v16h17M8 15v-4M13 15V6M18 15v-7"/></svg></span><strong>Performance reports</strong><svg class="portal-login-report-bars" viewBox="0 0 300 90" fill="none"><path d="M0 89H300M0 45H300"/><rect x="14" y="49" width="24" height="40" rx="4"/><rect x="61" y="31" width="24" height="58" rx="4"/><rect x="108" y="39" width="24" height="50" rx="4"/><rect x="155" y="18" width="24" height="71" rx="4"/><rect x="202" y="29" width="24" height="60" rx="4"/><rect x="249" y="8" width="24" height="81" rx="4"/><path class="portal-login-trend" d="M26 52 L73 36 L120 42 L167 22 L214 31 L261 10" pathLength="1"/><circle class="portal-login-trend-dot" cx="261" cy="10" r="5"/></svg><div class="portal-login-report-capabilities"><span>Claim status</span><span>Next steps</span></div></div>
      </div>
    </section>
    <div class="portal-login-brand">
      <span class="portal-login-brand-mark"><img src="./assets/tms-logo.png" alt="Therapy Management Solutions" width="176" height="40"></span>
    </div>
  `;
}

function portalLoginDeveloperHtml() {
  return `<div class="portal-login-developer"><a href="https://www.nsdevelopment.dev/" target="_blank" rel="noopener noreferrer"><span>Designed &amp; developed by</span><img src="./assets/ns-development-logo-mark.png" alt="NS Development" width="549" height="185"></a></div>`;
}

// The page-level chrome VOB frames its sign-in with and we had none of: a
// real bottom band, which gives the viewport an edge instead of letting the
// card float in an open field, and which is exactly where VOB's own footer
// puts its build credit. Shared by both gates so the two cannot drift apart.
function portalLoginCreditHtml() {
  return `<footer class="portal-login-credit"><span class="portal-login-credit-name">© ${new Date().getFullYear()} Therapy Management Solutions</span></footer>`;
}

function portalLoginRevealButtonHtml() {
  return `<button type="button" class="portal-login-reveal" data-portal-login-reveal aria-pressed="false" aria-label="Show password" title="Show password">
      <svg class="portal-login-eye-on" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"></path><circle cx="12" cy="12" r="2.75"></circle></svg>
      <svg class="portal-login-eye-off" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 4l16 16"></path><path d="M9.6 5.8A9.9 9.9 0 0 1 12 5.5c6.4 0 10 6.5 10 6.5a17.6 17.6 0 0 1-3.4 4.2"></path><path d="M6.4 7.7A17.5 17.5 0 0 0 2 12s3.6 6.5 10 6.5a10 10 0 0 0 3.1-.5"></path><path d="M9.8 9.9a2.75 2.75 0 0 0 3.8 3.9"></path></svg>
    </button>`;
}

// Reveal is a convenience, never a requirement: if either node is missing the
// password field keeps working exactly as it always did.
function installPortalLoginReveal(root) {
  if (!root || typeof root.querySelector !== "function") return;
  const button = root.querySelector("[data-portal-login-reveal]");
  const input = root.querySelector("input[name=password]");
  if (!button || !input || typeof button.addEventListener !== "function") return;
  button.addEventListener("click", () => {
    const shown = input.type === "text";
    input.type = shown ? "password" : "text";
    button.setAttribute("aria-pressed", shown ? "false" : "true");
    button.setAttribute("aria-label", shown ? "Show password" : "Hide password");
    button.setAttribute("title", shown ? "Show password" : "Hide password");
    if (button.classList && typeof button.classList.toggle === "function") {
      button.classList.toggle("is-shown", !shown);
    }
    if (typeof input.focus === "function") input.focus();
  });
}

function renderPortalLogin() {
  return new Promise((resolve) => {
    const base = workflowApiBase();
    let loginRequestSeq = 0;
    let activeLoginRequest = null;
    let recoveryOpened = false;
    const loginTimeoutMs = 15000;
    const overlay = document.createElement("div");
    overlay.className = "portal-login-gate";
    // The brand row and the help line live OUTSIDE the card on purpose: every
    // later step (2FA code, one-time enrolment, invite onboarding, dead end)
    // replaces the card node wholesale, so anything that must survive the whole
    // flow has to be its sibling rather than its child.
    overlay.innerHTML = `
      <div class="portal-login-viewport">
        <div class="portal-login-stage">
        ${portalLoginBrandHtml()}
        <form class="portal-login-card" autocomplete="on">
          <div class="portal-login-head">
            <span class="portal-login-eyebrow">Secure sign-in</span>
            <h1>Sign in to your portal</h1>
            <p>Use your portal username and password to continue.</p>
          </div>
          <div class="portal-login-group">
            <label class="portal-login-label" for="portalLoginUsernameField">Email address</label>
            <input id="portalLoginUsernameField" name="username" type="text" inputmode="email" autocomplete="username" placeholder="you@yourpractice.com" required autofocus>
          </div>
          <div class="portal-login-group">
            <label class="portal-login-label" for="portalLoginPasswordField">Password</label>
            <span class="portal-login-field">
              <input id="portalLoginPasswordField" name="password" type="password" autocomplete="current-password" placeholder="Enter your password" required>
              ${portalLoginRevealButtonHtml()}
            </span>
          </div>
          <button type="submit">Sign in</button>
          <button type="button" class="portal-login-retry" data-portal-forgot-password>Forgot password?</button>
          <div class="portal-login-error" role="status" aria-live="polite"></div>
          <p class="portal-login-foot"><svg class="portal-login-shield" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"></path><path d="M9 12l2 2 4-4"></path></svg>Access your organization's secure portal.</p>
        </form>
        <p class="portal-login-help">Trouble signing in? Contact your portal administrator.</p>
        ${portalLoginDeveloperHtml()}
        </div>
      </div>
      ${portalLoginCreditHtml()}
    `;
    document.body.appendChild(overlay);
    installPortalLoginStyles();
    const form = overlay.querySelector("form");
    const error = overlay.querySelector(".portal-login-error");
    installPortalLoginReveal(overlay);
    const forgotPasswordButton = overlay.querySelector("[data-portal-forgot-password]");
    if (forgotPasswordButton) forgotPasswordButton.addEventListener("click", () => {
      recoveryOpened = true;
      loginRequestSeq += 1;
      activeLoginRequest = null;
      overlay.remove();
      renderPortalAccountRecovery().then(resolve);
    });
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (recoveryOpened || activeLoginRequest) return;
      error.textContent = "";
      error.className = "portal-login-error";
      const submitButton = form.querySelector("button[type=submit]");
      submitButton.disabled = true;
      submitButton.textContent = "Signing in...";
      const requestSeq = ++loginRequestSeq;
      const requestMarker = {};
      activeLoginRequest = requestMarker;
      form.dataset.loginRequestSeq = String(requestSeq);
      try {
        const formData = new FormData(form);
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), loginTimeoutMs);
        let response;
        try {
          response = await fetch(`${base}/api/auth/login`, {
            method: "POST",
            cache: "no-store",
            signal: controller.signal,
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              username: formData.get("username"),
              password: formData.get("password"),
              deviceToken: portalTrustedDeviceToken(String(formData.get("username") || ""))
            })
          });
        } catch (fetchError) {
          if (fetchError && fetchError.name === "AbortError") {
            const timedOut = new Error("Sign-in timed out. The service may be temporarily unavailable - try again shortly.");
            timedOut.loginOutage = true;
            throw timedOut;
          }
          // A DEAD API IS NOT A BAD PASSWORD, AND `Failed to fetch` IS NOT A
          // SENTENCE (2026-08-10). During a 9-minute API outage the only thing
          // any user saw under the Sign in button was the browser's raw fetch
          // string: no explanation of what had happened, no indication it was
          // temporary, and nothing to press. Mark the failure as an outage here
          // so the catch below can say so and offer a retry; a 401 keeps its own
          // copy, because "we are down" and "that password is wrong" are
          // different facts and must never be shown interchangeably.
          const outage = new Error(PORTAL_SIGN_IN_OUTAGE_MESSAGE);
          outage.loginOutage = true;
          outage.cause = fetchError;
          throw outage;
        } finally {
          window.clearTimeout(timeoutId);
        }
        const body = await response.json().catch(() => ({}));
        if (activeLoginRequest !== requestMarker || requestSeq !== loginRequestSeq || form.dataset.loginRequestSeq !== String(requestSeq)) return;
        if (response.ok && !body.token && body.mfa && body.mfa.required) {
          renderPortalMfaStep({ overlay, base, mfa: body.mfa, username: String(formData.get("username") || ""), resolve });
          return;
        }
        if (!response.ok || !body.token) {
          // THE SECOND HALF OF THE SAME DEFECT. When the API is down it answers
          // 502/503 with an HTML body, `response.json()` falls back to `{}`, and
          // this line then told the user their password was wrong - a worse lie
          // than `Failed to fetch`, because it is specific, plausible and sends
          // them to reset a credential that was never the problem. A 5xx is the
          // service's problem; 400/401 stay byte-identical.
          if (response.status >= 500) {
            const outage = new Error(PORTAL_SIGN_IN_OUTAGE_MESSAGE);
            outage.loginOutage = true;
            throw outage;
          }
          throw new Error(body.message || "Invalid email address or password.");
        }
        completePortalSignIn({ body, overlay, resolve, username: String(formData.get("username") || "") });
      } catch (err) {
        if (activeLoginRequest !== requestMarker || requestSeq !== loginRequestSeq || form.dataset.loginRequestSeq !== String(requestSeq)) return;
        if (err && err.loginOutage) {
          // A retry affordance, not a reload: the form still holds what they
          // typed, so pressing this re-submits the same credentials the moment
          // the service is back. Deliberately NOT the scope-denial panel - a 403
          // is a standing decision with nothing to retry, and NO_SCOPE_EMPTY
          // _STATE_SPEC forbids the word "Retry" there for exactly that reason.
          // These two states must stay tellable apart.
          error.className = "portal-login-error is-outage";
          error.innerHTML = `<span class="portal-login-error-text">${escapeHtml(err.message || PORTAL_SIGN_IN_OUTAGE_MESSAGE)}</span> <button type="button" class="portal-login-retry" data-portal-login-retry>Try again</button>`;
          const retryButton = error.querySelector("[data-portal-login-retry]");
          if (retryButton && typeof retryButton.addEventListener === "function") {
            retryButton.addEventListener("click", () => {
              error.textContent = "";
              error.className = "portal-login-error";
              if (typeof form.requestSubmit === "function") form.requestSubmit();
              else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
            });
          }
        } else {
          error.textContent = err.message || "Login failed.";
        }
      } finally {
        if (activeLoginRequest !== requestMarker || requestSeq !== loginRequestSeq || form.dataset.loginRequestSeq !== String(requestSeq)) return;
        submitButton.disabled = false;
        submitButton.textContent = "Sign in";
        activeLoginRequest = null;
      }
    });
  });
}

// Account recovery keeps its token only in this closure. It never reaches a
// query string, browser storage, logging or automatic sign-in.
function renderPortalAccountRecovery({ token = null } = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "portal-login-gate";
    overlay.innerHTML = `
      <div class="portal-login-viewport"><div class="portal-login-stage">
        ${portalLoginBrandHtml()}
        <form class="portal-login-card" autocomplete="on">
          <div class="portal-login-head"><span class="portal-login-eyebrow">Account recovery</span>
            <h1>${token === null ? "Forgot your password?" : "Reset your password"}</h1>
            <p data-recovery-description>${token === null ? "Enter the email address registered to your portal account." : "Checking your reset link…"}</p>
          </div>
          <div data-recovery-fields></div>
          <button type="submit" data-recovery-submit hidden>Send reset link</button>
          <div class="portal-login-error" role="status" aria-live="polite"></div>
          <button type="button" class="portal-login-retry" data-recovery-back>Back to sign in</button>
        </form>
        ${portalLoginDeveloperHtml()}
      </div></div>${portalLoginCreditHtml()}
    `;
    document.body.appendChild(overlay);
    installPortalLoginStyles();
    const form = overlay.querySelector("form");
    const fields = overlay.querySelector("[data-recovery-fields]");
    const submit = overlay.querySelector("[data-recovery-submit]");
    const error = overlay.querySelector(".portal-login-error");
    const description = overlay.querySelector("[data-recovery-description]");
    let active = true;
    let busy = false;
    let mode = token === null ? "request" : "preview";
    const showRequest = () => {
      mode = "request";
      token = null;
      fields.innerHTML = '<div class="portal-login-group"><label class="portal-login-label" for="portalRecoveryEmail">Account email</label><input id="portalRecoveryEmail" name="email" type="email" autocomplete="email" maxlength="254" required></div>';
      submit.hidden = false;
      submit.textContent = "Send reset link";
      fields.querySelector("input").focus();
    };
    const call = async (action, body) => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(`${workflowApiBase()}/api/auth/recovery/${action}`, { method: "POST", cache: "no-store", credentials: "omit", referrerPolicy: "no-referrer", signal: controller.signal, headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
        return { ok: response.ok, status: response.status, body: await response.json().catch(() => ({})) };
      } finally { window.clearTimeout(timer); }
    };
    overlay.querySelector("[data-recovery-back]").addEventListener("click", () => {
      if (busy) return;
      active = false;
      token = null;
      overlay.remove();
      renderPortalLogin().then(resolve);
    });
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!active || busy || !["request", "redeem"].includes(mode)) return;
      error.textContent = "";
      const values = new FormData(form);
      if (mode === "redeem" && values.get("password") !== values.get("confirmPassword")) { error.textContent = "The passwords do not match."; return; }
      busy = true;
      submit.disabled = true;
      const action = mode;
      submit.textContent = action === "request" ? "Sending…" : "Saving…";
      try {
        const result = await call(action, action === "request" ? { email: values.get("email") } : { token, password: values.get("password") });
        if (!active) return;
        if (!result.ok) {
          error.textContent = result.status >= 500 ? "Password recovery is temporarily unavailable. Try again shortly or contact your administrator." : (result.body.message || "This reset link is invalid or expired. Request a new link.");
          return;
        }
        if (action === "request") {
          description.textContent = "If this email belongs to an eligible account, we will email a password reset link. If no email arrives, contact your administrator.";
        } else {
          [PORTAL_AUTH_TOKEN_KEY, PORTAL_AUTH_SESSION_TOKEN_KEY, PORTAL_AUTH_USER_KEY, WORKFLOW_API_TOKEN_KEY, TRACKER_API_TOKEN_KEY, REPORT_API_TOKEN_KEY].forEach((key) => { safeLocalStorageRemove(key); safeSessionStorageRemove(key); });
          window.TMS_PORTAL_AUTH_TOKEN = "";
          window.TMS_WORKFLOW_API_TOKEN = "";
          window.TMS_TRACKER_API_TOKEN = "";
          window.TMS_REPORT_API_TOKEN = "";
          token = null;
          description.textContent = "Your password has been changed. Sign in with your new password.";
        }
        mode = "complete";
        fields.innerHTML = "";
        submit.hidden = true;
      } catch (_error) {
        if (active) error.textContent = "Password recovery could not connect. Try again shortly.";
      } finally {
        busy = false;
        submit.disabled = false;
        submit.textContent = action === "request" ? "Send reset link" : "Change password";
      }
    });
    if (token === null) { showRequest(); return; }
    call("preview", { token }).then((result) => {
      if (!active) return;
      if (!result.ok || !result.body.valid) {
        description.textContent = result.status >= 500 ? "Password recovery is temporarily unavailable. Try again shortly." : "This reset link is invalid or expired. Request a new link below.";
        showRequest();
        return;
      }
      mode = "redeem";
      description.textContent = "Choose a new password with at least 12 characters.";
      fields.innerHTML = '<div class="portal-login-group"><label class="portal-login-label" for="portalRecoveryPassword">New password</label><input id="portalRecoveryPassword" name="password" type="password" autocomplete="new-password" minlength="12" maxlength="256" required></div><div class="portal-login-group"><label class="portal-login-label" for="portalRecoveryConfirm">Confirm new password</label><input id="portalRecoveryConfirm" name="confirmPassword" type="password" autocomplete="new-password" minlength="12" maxlength="256" required></div>';
      submit.hidden = false;
      submit.textContent = "Change password";
      fields.querySelector("input").focus();
    }).catch(() => {
      if (!active) return;
      description.textContent = "Password recovery could not connect. Request a new link or try again shortly.";
      showRequest();
    });
  });
}

// ============================================================================
// 2FA + invite onboarding screens (slices 7-8). One card, obvious steps.
// All state lives in the overlay; tokens follow the existing storage flow.
// ============================================================================

const PORTAL_TRUSTED_DEVICES_KEY = "tms_portal_trusted_devices";

// The same login card node morphs between steps so the experience reads as
// one continuous flow: password -> code (or enrollment -> code) -> signed in.
function completePortalSignIn({ body = {}, overlay = null, resolve = () => {}, username = "" } = {}) {
  // The signed-in user is stored first so token persistence follows THIS
  // account, never whoever signed in on this browser before.
  safeLocalStorageSet(PORTAL_AUTH_USER_KEY, JSON.stringify(body.user || {}));
  safeSessionStorageSet(PORTAL_AUTH_SESSION_TOKEN_KEY, body.token);
  migratePortalAuthTokenToSession(body.token);
  if (body.trustedDevice && body.trustedDevice.deviceToken) {
    savePortalTrustedDevice(username || body.user?.username || "", body.trustedDevice);
  }
  applyAuthenticatedUserToState(body.user || {});
  hydratePortalAuthToken();
  if (overlay) overlay.remove();
  resolve(true);
  if (portalAppBooted) {
    showPortalAppShell();
    if (typeof render === "function") {
      try { render(); } catch (e) { console.error("[tms.boot] render after sign-in", e); }
    }
    try { kickTrackerApiAfterBoot(); } catch (e) { console.error("[tms.boot] tracker kick after signed-in render", e); }
    return;
  }
  // The in-flight __tmsBoot is awaiting requirePortalLogin and will continue
  // after resolve(true). A second boot here re-enters requirePortalLogin
  // before loadState(), which used to look like an orphan bearer and wipe
  // the just-minted session.
}

// BOOT WORK THAT MUST NOT COMPETE WITH THE FIRST GRID PAINT (2026-09-22).
//
// A cold All Data load at 500 rows/page measured 8,651 ms to first rows in
// production, and ~3,600 ms of that - 42% - was the page query and the facet
// batch queueing behind requests the boot issued for CHROME: the notification
// unread badge and the navigation pending counts
// (perf/ALL_DATA_LATENCY_2026-09-22.md). Neither is on screen before the rows
// are, and neither is wrong a moment later: they are badges.
//
// So they are registered once, by key, and run when the main thread is next
// idle - which on All Data is after the DOM build, and on a role home is
// immediately. requestIdleCallback carries its own 3 s deadline so a busy tab
// can never strand them, and the tracker page answer releases them early.
// Keyed, because the callers are RENDER paths: renderWorkflowNotificationChrome
// and applyPermissionChrome run on every render, and an unkeyed queue would
// grow one closure per paint.
let firstGridPaintReleased = false;
const afterFirstGridPaintQueue = [];
const bootDeferredRequestKeys = new Set();

function runAfterFirstGridPaint(work) {
  if (typeof work !== "function") return;
  if (firstGridPaintReleased) { work(); return; }
  afterFirstGridPaintQueue.push(work);
}

// Returns true when the caller should stand down because its work has been
// queued for after the first paint, false when it should proceed now.
function deferBootRequestUntilFirstGridPaint(key, run) {
  if (firstGridPaintReleased) return false;
  if (!bootDeferredRequestKeys.has(key)) {
    bootDeferredRequestKeys.add(key);
    runAfterFirstGridPaint(() => { bootDeferredRequestKeys.delete(key); run(); });
  }
  return true;
}

function releaseFirstGridPaintWork() {
  if (firstGridPaintReleased) return;
  firstGridPaintReleased = true;
  const queued = afterFirstGridPaintQueue.splice(0);
  queued.forEach((work) => {
    try { work(); } catch (error) { console.error("[tms.boot] deferred boot work", error); }
  });
}

function scheduleFirstGridPaintRelease() {
  if (typeof window === "undefined") return;
  // WHEN A PAGE QUERY IS ON THE WIRE, THE RELEASE IS ITS ANSWER.
  //
  // requestIdleCallback is not a proxy for "the rows are up": the main thread
  // IS idle while it waits on that network read, so the idle callback fired
  // half a second BEFORE the first row painted and put the badge reads back in
  // front of the query - measured, and the reason this is not an idle callback.
  // requestTrackerApiPage releases the queue when the read settles, whichever
  // way it settles; the timer below is only a floor so a lane that never
  // answers at all cannot strand the badges forever (the page read aborts
  // itself at 45 s).
  const waitingOnGrid = typeof trackerApiEnabled === "function" && trackerApiEnabled()
    && typeof trackerApiPageState !== "undefined" && trackerApiPageState.status === "pending";
  if (waitingOnGrid) { window.setTimeout(() => releaseFirstGridPaintWork(), 20000); return; }
  if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(() => releaseFirstGridPaintWork(), { timeout: 3000 });
  else window.setTimeout(() => releaseFirstGridPaintWork(), 1200);
}

function kickTrackerApiAfterBoot() {
  if (typeof trackerApiEnabled !== "function" || !trackerApiEnabled()) return;
  if (typeof resetTrackerApiAutoRetry === "function") resetTrackerApiAutoRetry();
  const query = typeof buildTrackerQueryRequest === "function"
    ? buildTrackerQueryRequest((typeof state !== "undefined" && state && state.rowMode) || "service")
    : null;
  // THE BOOT RENDER HAS USUALLY ALREADY ASKED FOR THIS EXACT PAGE.
  //
  // __tmsBoot calls render() and then this, and render() -> renderSlicers() ->
  // trackerApiPageForQuery() issues the page query for the same key one tick
  // earlier. Resetting trackerApiPageState here ERASED that in-flight request
  // from the states own bookkeeping, so the request below could not recognise
  // it and every boot sent the grid query TWICE with a byte-identical body -
  // two COUNT_BIG + page pairs and, at 500 rows/page, a wasted 723 KB
  // (perf/ALL_DATA_LATENCY_2026-09-22.md). The reset still happens for the
  // case it was written for - a post-sign-in kick landing on a stale or failed
  // lane - it just no longer throws away a live answer for the current key.
  const key = query && typeof trackerApiCacheKey === "function" ? trackerApiCacheKey(query) : "";
  const alreadyAsked = Boolean(key) && trackerApiPageState.key === key
    && (trackerApiPageState.status === "pending" || (trackerApiPageState.status === "ok" && trackerApiPageState.response));
  if (!alreadyAsked) {
    if (typeof trackerApiInitialState === "function") trackerApiPageState = trackerApiInitialState();
    if (query && typeof requestTrackerApiPage === "function") requestTrackerApiPage(query, key || undefined);
  }
  if (typeof startTrackerPublicationWatcher === "function") startTrackerPublicationWatcher();
}

// Trusted devices: per-username map in localStorage (NOT sessionStorage - it
// must survive browser restarts; that is the whole point of "30 days").
// Sign-out intentionally keeps this map: trusting a device is about the
// device, not the session.
function portalTrustedDeviceToken(username) {
  const key = String(username || "").trim().toLowerCase();
  if (!key) return "";
  try {
    const map = JSON.parse(safeLocalStorageGet(PORTAL_TRUSTED_DEVICES_KEY) || "{}");
    const entry = map[key];
    if (!entry || !entry.deviceToken) return "";
    if (new Date(entry.expiresAtUtc || 0).getTime() <= Date.now()) {
      delete map[key];
      safeLocalStorageSet(PORTAL_TRUSTED_DEVICES_KEY, JSON.stringify(map));
      return "";
    }
    return entry.deviceToken;
  } catch (_) {
    return "";
  }
}

function savePortalTrustedDevice(username, trustedDevice = {}) {
  const key = String(username || "").trim().toLowerCase();
  if (!key || !trustedDevice.deviceToken) return;
  try {
    const map = JSON.parse(safeLocalStorageGet(PORTAL_TRUSTED_DEVICES_KEY) || "{}");
    map[key] = {
      deviceToken: trustedDevice.deviceToken,
      expiresAtUtc: trustedDevice.expiresAtUtc || ""
    };
    safeLocalStorageSet(PORTAL_TRUSTED_DEVICES_KEY, JSON.stringify(map));
  } catch (_) {}
}

function portalDeviceLabel() {
  const ua = String(navigator.userAgent || "");
  const browser = /Edg\//.test(ua) ? "Edge"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) ? "Safari"
    : "Browser";
  const os = /Windows/.test(ua) ? "Windows"
    : /Mac OS X/.test(ua) ? "Mac"
    : /Android/.test(ua) ? "Android"
    : /iPhone|iPad/.test(ua) ? "iPhone/iPad"
    : /Linux/.test(ua) ? "Linux"
    : "device";
  return `${browser} on ${os}`;
}

// Shared OTP code step. Used by login 2FA, forced enrollment, and invite
// onboarding - same screen everywhere, nothing new to learn.
function renderPortalCodeStep({ card, base, challenge = {}, username = "", overlay, resolve, intro = "", onStartOver = null } = {}) {
  const masked = escapeHtml(challenge.destination || "your email");
  card.innerHTML = `
    <div class="portal-login-head">
      <span class="portal-login-eyebrow">Verify it's you</span>
      <h1>Check your email</h1>
      <p>${intro || `We sent a 6-digit code to <strong>${masked}</strong>.`}</p>
    </div>
    <label class="portal-otp-label">
      <span class="portal-login-label-text">Sign-in code</span>
      <input class="portal-otp-input" name="otpCode" type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]*" required autofocus>
    </label>
    <label class="portal-login-check">
      <input type="checkbox" name="rememberDevice">
      <span>Trust this device for 30 days</span>
    </label>
    <button type="submit">Continue</button>
    <div class="portal-login-error" role="status" aria-live="polite"></div>
    <p class="portal-login-foot">
      <button type="button" class="portal-login-resend" data-resend>Send a new code</button>
      <span class="portal-login-resend-wait" data-resend-wait></span>
    </p>
  `;
  const error = card.querySelector(".portal-login-error");
  const codeInput = card.querySelector(".portal-otp-input");
  const submitButton = card.querySelector("button[type=submit]");
  const resendButton = card.querySelector("[data-resend]");
  const resendWait = card.querySelector("[data-resend-wait]");
  let challengeId = challenge.challengeId || "";
  let resendAvailableAt = new Date(challenge.resendAvailableAtUtc || 0).getTime();
  let waitTimer = 0;
  let resendRequestSeq = 0;
  // Auto-submit, Enter and resend must share one request gate: each code is single-use.
  let requestInFlight = false;
  let stepComplete = false;

  const updateResendState = () => {
    window.clearTimeout(waitTimer);
    if (stepComplete) return;
    const remaining = Math.ceil((resendAvailableAt - Date.now()) / 1000);
    if (remaining > 0) {
      resendButton.disabled = true;
      resendWait.textContent = `(${remaining}s)`;
      waitTimer = window.setTimeout(updateResendState, 500);
    } else {
      resendButton.disabled = requestInFlight;
      resendWait.textContent = "";
    }
  };
  updateResendState();

  const setRequestInFlight = (busy) => {
    requestInFlight = busy;
    submitButton.disabled = busy;
    codeInput.readOnly = busy;
    updateResendState();
  };
  const recoverTerminalChallenge = (response, body) => {
    if (![404, 410, 423].includes(response.status) && !["challenge_not_pending", "account_changed", "account_not_issuable", "session_persistence_not_configured"].includes(body.error)) return false;
    stepComplete = true;
    window.clearTimeout(waitTimer);
    renderPortalDeadEnd({
      card,
      heading: "Start sign-in again",
      message: body.message || "That code can no longer be used.",
      onStartOver
    });
    return true;
  };

  const verifyCode = async () => {
    if (requestInFlight || stepComplete) return;
    const code = String(codeInput.value || "").replace(/\D/g, "");
    if (code.length !== 6) {
      error.textContent = "Enter the 6-digit code.";
      return;
    }
    setRequestInFlight(true);
    submitButton.textContent = "Checking...";
    error.textContent = "";
    const submittedChallengeId = challengeId;
    try {
      const response = await fetch(`${base}/api/auth/login/verify`, {
        method: "POST",
        cache: "no-store",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          challengeId,
          code,
          rememberDevice: card.querySelector("[name=rememberDevice]").checked === true,
          deviceLabel: portalDeviceLabel()
        })
      });
      const body = await response.json().catch(() => ({}));
      if (challengeId !== submittedChallengeId) return;
      if (response.ok && body.token) {
        stepComplete = true;
        window.clearTimeout(waitTimer);
        completePortalSignIn({ body, overlay, resolve, username });
        return;
      }
      error.textContent = body.message || "That code is not right. Try again.";
      if (recoverTerminalChallenge(response, body)) return;
      codeInput.value = "";
      codeInput.focus();
    } catch (_) {
      error.textContent = "Could not reach the portal. Check your connection and try again.";
    } finally {
      if (!stepComplete) {
        setRequestInFlight(false);
        submitButton.textContent = "Continue";
      }
    }
  };

  codeInput.addEventListener("input", () => {
    codeInput.value = codeInput.value.replace(/\D/g, "").slice(0, 6);
    if (codeInput.value.length === 6) verifyCode();
  });
  card.onsubmit = (event) => {
    event.preventDefault();
    verifyCode();
  };
  resendButton.addEventListener("click", async () => {
    if (requestInFlight || stepComplete || Date.now() < resendAvailableAt) return;
    setRequestInFlight(true);
    error.textContent = "";
    const requestSeq = ++resendRequestSeq;
    const requestChallengeId = challengeId;
    try {
      const response = await fetch(`${base}/api/auth/login/resend`, {
        method: "POST",
        cache: "no-store",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ challengeId })
      });
      const body = await response.json().catch(() => ({}));
      if (requestSeq !== resendRequestSeq || challengeId !== requestChallengeId) return;
      if (response.ok) {
        challengeId = body.challengeId || challengeId;
        codeInput.value = "";
        resendAvailableAt = new Date(body.resendAvailableAtUtc || Date.now() + 30000).getTime();
        showToast("New code sent.");
      } else {
        if (recoverTerminalChallenge(response, body)) return;
        error.textContent = body.message || "Could not send a new code right now.";
        if (body.retryAtUtc) resendAvailableAt = new Date(body.retryAtUtc).getTime();
      }
    } catch (_) {
      error.textContent = "Could not reach the portal.";
    } finally {
      if (!stepComplete) setRequestInFlight(false);
    }
  });
}

// 2FA branch from the password screen: either a code step (enrolled) or the
// one-time enrollment step (existing users right after the MFA flip).
function renderPortalMfaStep({ overlay, base, mfa = {}, username = "", resolve } = {}) {
  // Replace the card node so the password form's submit listener is gone;
  // the morphed steps drive submission through card.onsubmit instead.
  const oldCard = overlay.querySelector(".portal-login-card");
  const card = oldCard.cloneNode(false);
  oldCard.replaceWith(card);
  const startOver = () => window.location.reload();
  if (mfa.enrollmentRequired) {
    renderPortalEnrollmentStep({ card, base, enrollmentToken: mfa.enrollmentToken, username, overlay, resolve, onStartOver: startOver });
    return;
  }
  renderPortalCodeStep({ card, base, challenge: mfa, username, overlay, resolve, onStartOver: startOver });
}

function renderPortalEnrollmentStep({ card, base, enrollmentToken = "", username = "", overlay, resolve, onStartOver = null } = {}) {
  let enrollmentRequestSeq = 0;
  card.innerHTML = `
    <div class="portal-login-head">
      <span class="portal-login-eyebrow">One-time setup</span>
      <h1>Add your sign-in email</h1>
      <p>From now on, signing in takes your password plus a 6-digit code. Tell us where to send your codes.</p>
    </div>
    <label>
      <span class="portal-login-label-text">Email for sign-in codes</span>
      <input name="mfaEmail" type="email" autocomplete="email" placeholder="you@example.com" required autofocus>
    </label>
    <button type="submit">Send my code</button>
    <div class="portal-login-error" role="status" aria-live="polite"></div>
    <p class="portal-login-foot">You'll only do this once.</p>
  `;
  const error = card.querySelector(".portal-login-error");
  card.onsubmit = async (event) => {
    event.preventDefault();
    const submitButton = card.querySelector("button[type=submit]");
    submitButton.disabled = true;
    submitButton.textContent = "Sending...";
    error.textContent = "";
    const requestSeq = ++enrollmentRequestSeq;
    try {
      const response = await fetch(`${base}/api/auth/enrollment`, {
        method: "POST",
        cache: "no-store",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          enrollmentToken,
          email: card.querySelector("[name=mfaEmail]").value,
          phone: ""
        })
      });
      const body = await response.json().catch(() => ({}));
      if (requestSeq !== enrollmentRequestSeq) return;
      if (response.ok && body.challengeId) {
        renderPortalCodeStep({ card, base, challenge: body, username, overlay, resolve, onStartOver });
        return;
      }
      error.textContent = body.message || "Could not start setup. Try again.";
      if ([404, 410].includes(response.status) && onStartOver) {
        renderPortalDeadEnd({ card, heading: "Start sign-in again", message: body.message || "This setup session ended.", onStartOver });
      }
    } catch (_) {
      if (requestSeq !== enrollmentRequestSeq) return;
      error.textContent = "Could not reach the portal.";
    } finally {
      if (requestSeq !== enrollmentRequestSeq) return;
      submitButton.disabled = false;
      submitButton.textContent = "Send my code";
    }
  };
}

function renderPortalDeadEnd({ card, heading = "", message = "", onStartOver = null } = {}) {
  card.innerHTML = `
    <div class="portal-login-head">
      <span class="portal-login-eyebrow">Sign-in</span>
      <h1>${escapeHtml(heading || "This link is no longer valid")}</h1>
      <p>${escapeHtml(message || "Ask your administrator to send a new invite.")}</p>
    </div>
    ${onStartOver ? `<button type="button" class="portal-login-startover">Back to sign in</button>` : ""}
  `;
  const startOverButton = card.querySelector(".portal-login-startover");
  if (startOverButton && onStartOver) startOverButton.addEventListener("click", onStartOver);
}

// ?invite=TOKEN onboarding: one card, three obvious steps.
// 1. Create your password  2. Your mobile number  3. Enter the emailed code.
function currentInviteTokenFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get("invite") || "";
  } catch (_) {
    return "";
  }
}

async function maybeRenderInviteOnboarding() {
  const token = currentInviteTokenFromUrl();
  if (!token || !portalLoginRequired()) return false;
  const base = workflowApiBase();
  if (!base) return false;

  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "portal-login-gate portal-onboarding-gate";
    overlay.innerHTML = `
      <div class="portal-login-viewport">
        <div class="portal-login-stage">
        ${portalLoginBrandHtml()}
        <form class="portal-login-card" autocomplete="on">
          <div class="portal-login-head">
            <span class="portal-login-eyebrow">Account setup</span>
            <h1>Setting up your account…</h1>
            <p>One moment.</p>
          </div>
        </form>
        <p class="portal-login-help">Trouble signing in? Contact your portal administrator.</p>
        ${portalLoginDeveloperHtml()}
        </div>
      </div>
      ${portalLoginCreditHtml()}
    `;
    document.body.appendChild(overlay);
    installPortalLoginStyles();
    const card = overlay.querySelector(".portal-login-card");
    const stripInviteFromUrl = () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("invite");
        window.history.replaceState({}, "", url.toString());
      } catch (_) {}
    };
    const finishResolve = (value) => {
      stripInviteFromUrl();
      resolve(value);
    };

    (async () => {
      let preview = null;
      try {
        const response = await fetch(`${base}/api/users/invites/preview`, {
          method: "POST",
          cache: "no-store",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ token })
        });
        preview = response.ok ? await response.json().catch(() => null) : null;
      } catch (_) {
        preview = null;
      }
      if (currentInviteTokenFromUrl() !== token) {
        overlay.remove();
        resolve(false);
        return;
      }
      if (!preview || preview.status !== "pending_invite") {
        renderPortalDeadEnd({
          card,
          heading: "This invite link has expired",
          message: "Ask your administrator to send a new invite, then use the newest email.",
          onStartOver: () => {
            stripInviteFromUrl();
            window.location.reload();
          }
        });
        return;
      }
      renderInvitePasswordStep({ card, base, token, preview, overlay, resolve: finishResolve });
    })();
  });
}

function renderInvitePasswordStep({ card, base, token, preview = {}, overlay, resolve } = {}) {
  const scopeLine = (roleProfiles[preview.role] || {}).label || preview.role || "";
  card.innerHTML = `
    <div class="portal-login-head">
      <span class="portal-login-eyebrow">Welcome</span>
      <h1>Hi ${escapeHtml(preview.displayName || "there")}</h1>
      <p>You're joining as <strong>${escapeHtml(scopeLine || "a portal user")}</strong><br>Account email: ${escapeHtml(preview.maskedEmail || "")}</p>
    </div>
    <label>
      <span class="portal-login-label-text">Create your password</span>
      <input name="newPassword" type="password" autocomplete="new-password" minlength="12" placeholder="At least 12 characters" required autofocus>
    </label>
    <label>
      <span class="portal-login-label-text">Confirm password</span>
      <input name="confirmPassword" type="password" autocomplete="new-password" minlength="12" placeholder="Type it again" required>
    </label>
    <button type="submit">Continue</button>
    <div class="portal-login-error" role="status" aria-live="polite"></div>
    <p class="portal-login-foot">Next: we'll email you a 6-digit code to confirm it's you.</p>
  `;
  const error = card.querySelector(".portal-login-error");
  card.onsubmit = async (event) => {
    event.preventDefault();
    const password = card.querySelector("[name=newPassword]").value;
    const confirm = card.querySelector("[name=confirmPassword]").value;
    if (password.length < 12) {
      error.textContent = "Password must be at least 12 characters.";
      return;
    }
    if (password !== confirm) {
      error.textContent = "Passwords do not match.";
      return;
    }
    const submitButton = card.querySelector("button[type=submit]");
    submitButton.disabled = true;
    submitButton.textContent = "Creating your account...";
    error.textContent = "";
    try {
      if (currentInviteTokenFromUrl() && currentInviteTokenFromUrl() !== token) return;
      const response = await fetch(`${base}/api/users/invites/accept`, {
        method: "POST",
        cache: "no-store",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          token,
          password,
          phone: ""
        })
      });
      const body = await response.json().catch(() => ({}));
      if (currentInviteTokenFromUrl() && currentInviteTokenFromUrl() !== token) return;
      if (response.ok && body.nextStep === "verify-code" && body.challenge) {
        renderPortalCodeStep({
          card,
          base,
          challenge: body.challenge,
          username: body.invite?.email || "",
          overlay,
          resolve,
          intro: `Last step: we emailed a 6-digit code to <strong>${escapeHtml(body.challenge.destination || "your email")}</strong>.`,
          onStartOver: () => window.location.reload()
        });
        return;
      }
      error.textContent = body.message || "Could not finish setup. Try again.";
      if (body.reason === "invite_already_accepted" || body.reason === "invite_expired" || body.reason === "invite_revoked") {
        renderPortalDeadEnd({
          card,
          heading: "This invite link can't be used",
          message: body.message || "Ask your administrator to send a new invite.",
          onStartOver: () => window.location.reload()
        });
      }
    } catch (_) {
      error.textContent = "Could not reach the portal.";
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = "Continue";
    }
  };
}

function showPortalAppShell() {
  const body = document.body;
  if (!body?.classList?.contains?.("portal-boot-pending")) return;
  // A wheel or PageDown during startup must never decide where the completed
  // workspace opens. Normalize only this initial reveal; later reauth calls do
  // not carry the pending class and therefore preserve the user's position.
  try { window.scrollTo(0, 0); } catch (_) {}
  if (document.documentElement) document.documentElement.scrollTop = 0;
  body.scrollTop = 0;
  // Sheet mode is the only AR mode (2026-08-17), but its body class used to
  // arrive only when AR first rendered, so the top bar and page padding of
  // every other view shifted 7px the first time someone opened AR. Apply it
  // at the reveal so the shell is the same from the first paint.
  body.classList.add("workbench-sheet-mode");
  body.classList.remove("portal-boot-pending");
}

function applyAuthenticatedUserToState(user = {}) {
  cancelClaimScreenshot();
  // Fail closed: only payloads whose role resolves through the known role
  // map (direct roleProfiles hit or legacy alias) are applied. canonicalRole
  // now defaults unknowns to Client (least privilege), but a missing or
  // unrecognized role is still rejected outright — clear the stored payload
  // so a real login never lands on a role it did not authenticate as.
  const rawRole = typeof user.role === "string" ? user.role.trim() : "";
  const role = rawRole && roleProfiles[rawRole] ? rawRole : (__legacyRoleAlias[rawRole] || "");
  if (!role || !roleProfiles[role]) {
    safeLocalStorageRemove(PORTAL_AUTH_USER_KEY);
    safeSessionStorageRemove(PORTAL_AUTH_USER_KEY);
    if (state) {
      state.authUserScope = null;
      saveState();
    }
    return false;
  }
  // Boot order: requirePortalLogin runs before loadState(). A valid snapshot
  // with no state yet is not an orphan bearer — stash and let boot continue.
  if (!state) {
    pendingPortalAuthUser = user;
    return true;
  }
  // A fresh login is a new count lifecycle even when the same person signs back
  // in. Abort and generation-invalidate any response owned by the prior session.
  invalidateNavigationPendingCounts();
  state.role = role;
  // An authenticated portal entry starts on the role's canonical landing view,
  // even when localStorage remembers another surface from an earlier visit.
  // During an in-page reauthentication, retain the current permitted surface so
  // an expiring session does not interrupt active work. applyUrlState runs after
  // boot authentication and remains the final authority for explicit deep links.
  if (!portalAppBooted || !viewIsAvailable(state.view)) {
    state.view = firstAccessibleView();
    if (state.view === "reports" && isExternalClientRole(role)) reportArState.view = "ar";
  }
  __bootViewIsDefault = false;
  if (user.displayName || user.username) state.activeUserName = user.displayName || user.username;
  // The login payload is the authoritative scope for real logins; demo users
  // in the in-app store keep working when no authenticated scope is present.
  state.authUserScope = {
    role,
    orgIds: normalizeUserScopeList(user.orgIds),
    clientNames: normalizeUserScopeList(user.clientNames),
    payerNames: normalizeUserScopeList(user.payerNames),
    assignedOnly: user.assignedOnly === true,
    assignedName: String(user.assignedName || user.userId || "").trim(),
    followUpActionAccessConfigured: user.followUpActionAccessConfigured === true,
    followUpActions: normalizeUserScopeList(user.followUpActions),
    teamIds: normalizeUserScopeList(user.teamIds),
    // The SESSION channel of the scope denial (backend portalLoginService ->
    // principalScopeDenial). Field-by-field and string-coerced, never spread:
    // this payload is untrusted here in exactly the way the role is. Absent
    // (older API) -> null, and the UI simply never claims a denial.
    followUpPolicies: oversightPolicies(user.followUpPolicies),
    scopeDenial: sanitizeScopeDenialPayload(user.scopeDenial),
    followUpActionAccess: sanitizeFollowUpActionAccessProjection(user.followUpActionAccess)
  };
  saveState();
  return true;
}

// One shape, two entry points (fresh login and a persisted reload). Anything
// that is not an object with a code is nothing at all - a denial the UI cannot
// name is not a denial it may render.
function sanitizeScopeDenialPayload(value) {
  if (!value || typeof value !== "object") return null;
  const code = String(value.code || value.reason || "").trim();
  if (!code) return null;
  return {
    code,
    title: String(value.title || "").trim(),
    message: String(value.message || "").trim(),
    remedy: String(value.remedy || "").trim()
  };
}

function applyPendingPortalAuthUser() {
  if (!pendingPortalAuthUser) return;
  const user = pendingPortalAuthUser;
  pendingPortalAuthUser = null;
  applyAuthenticatedUserToState(user);
}

function applyStoredPortalUserToState() {
  const raw = safeLocalStorageGet(PORTAL_AUTH_USER_KEY);
  if (!raw) return false;
  try {
    return applyAuthenticatedUserToState(JSON.parse(raw)) === true;
  } catch (_) {
    safeLocalStorageRemove(PORTAL_AUTH_USER_KEY);
    safeSessionStorageRemove(PORTAL_AUTH_USER_KEY);
    return false;
  }
}

let portalAppBooted = false;
let portalReauthInFlight = null;

// Mid-session 401: keep the current page, put the login gate on top.
// After sign-in, completePortalSignIn must NOT call __tmsBoot again —
// that would double-bind every control. render() is enough.
function offerPortalReauth() {
  stopTrackerPublicationWatcher();
  if (typeof portalLoginRequired === "function" && !portalLoginRequired()) return;
  if (typeof document !== "undefined" && document.querySelector(".portal-login-gate")) return;
  if (portalReauthInFlight) return portalReauthInFlight;
  if (typeof state !== "undefined" && state && typeof saveState === "function") {
    try { saveState(); } catch (_e) { /* view stay is best-effort */ }
  }
  [
    PORTAL_AUTH_TOKEN_KEY,
    PORTAL_AUTH_SESSION_TOKEN_KEY,
    PORTAL_AUTH_USER_KEY,
    WORKFLOW_API_TOKEN_KEY,
    TRACKER_API_TOKEN_KEY,
    REPORT_API_TOKEN_KEY
  ].forEach((key) => {
    safeLocalStorageRemove(key);
    safeSessionStorageRemove(key);
  });
  window.TMS_PORTAL_AUTH_TOKEN = "";
  window.TMS_WORKFLOW_API_TOKEN = "";
  window.TMS_TRACKER_API_TOKEN = "";
  window.TMS_REPORT_API_TOKEN = "";
  portalReauthInFlight = Promise.resolve()
    .then(() => renderPortalLogin())
    .finally(() => { portalReauthInFlight = null; });
  return portalReauthInFlight;
}

function notePortalSessionExpired() {
  window.TMSBackupHistory?.setContext(null);
  offerPortalReauth();
}

function signOutPortalUser() {
  const bearer = workflowApiToken() || trackerApiToken();
  const logoutRequest = bearer
    ? fetch(trackerApiBase() + "/api/auth/logout", {
      method: "POST",
      cache: "no-store",
      headers: { authorization: `Bearer ${bearer}` }
    }).then((response) => response.status === 204).catch(() => false)
    : Promise.resolve(true);
  window.TMSBackupHistory?.setContext(null);
  stopReportReadSession();
  stopTrackerPublicationWatcher();
  cancelClaimScreenshot();
  tmsBootRequestSeq += 1;
  invalidateNavigationPendingCounts();
  // A facet refresh is intentionally deferred so quick filter changes collapse
  // into one request. Cancel that queued work before removing the bearer: the
  // callback must never wake during the short signed-out/reload boundary and
  // dispatch an unauthenticated tracker request.
  cancelQueuedTrackerApiFacetRequest();
  [
    PORTAL_AUTH_TOKEN_KEY,
    PORTAL_AUTH_SESSION_TOKEN_KEY,
    PORTAL_AUTH_USER_KEY,
    WORKFLOW_API_TOKEN_KEY,
    TRACKER_API_TOKEN_KEY,
    REPORT_API_TOKEN_KEY
  ].forEach((key) => {
    safeLocalStorageRemove(key);
    safeSessionStorageRemove(key);
  });
  window.TMS_PORTAL_AUTH_TOKEN = "";
  window.TMS_WORKFLOW_API_TOKEN = "";
  window.TMS_TRACKER_API_TOKEN = "";
  window.TMS_REPORT_API_TOKEN = "";
  if (state) {
    state.authUserScope = null;
    saveState();
  }
  showToast("Signing out...");
  return logoutRequest.then((confirmed) => {
    showToast(confirmed ? "Signed out." : "Signed out on this device. Server session revocation could not be confirmed; sign in again and retry.");
    window.setTimeout(() => {
      window.location.reload();
    }, confirmed ? 120 : 1800);
    return confirmed;
  });
}

// The honest sentence for "the portal is down right now". One constant, so the
// password lane, the timeout lane and the 5xx lane cannot drift into three
// different explanations of the same event.
const PORTAL_SIGN_IN_OUTAGE_MESSAGE = "We couldn't reach the portal service - it's temporarily unavailable. This isn't your password. Try again shortly.";

// LIVING WELCOME (October 2026). The entrance still settles once; afterwards the
// only movement is a gentle tilt that follows the visitor's own pointer, so the
// page never animates by itself and needs no pause control. One document-level
// listener serves every gate (sign-in, recovery, invite) and stands down under
// prefers-reduced-motion or on touch-only pointers.
function installPortalLoginPointerMotion() {
  if (typeof window === "undefined" || window.__tmsLoginPointerMotion) return;
  window.__tmsLoginPointerMotion = true;
  if (typeof document.addEventListener !== "function") return;
  const reduce = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
  let frame = 0;
  let pending = null;
  document.addEventListener("pointermove", (event) => {
    if (event.pointerType && event.pointerType !== "mouse") return;
    if (reduce && reduce.matches) return;
    pending = event;
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      const gate = document.querySelector(".portal-login-gate");
      const hero = gate && gate.querySelector(".portal-login-hero");
      if (!hero || !pending) return;
      const box = hero.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const clamp = (value) => Math.max(-1, Math.min(1, value));
      const x = clamp(((pending.clientX - box.left) / box.width) * 2 - 1);
      const y = clamp(((pending.clientY - box.top) / box.height) * 2 - 1);
      gate.style.setProperty("--login-px", x.toFixed(3));
      gate.style.setProperty("--login-py", y.toFixed(3));
    });
  }, { passive: true });
}

function installPortalLoginStyles() {
  installPortalLoginPointerMotion();
  // The gate paints BEFORE __tmsBoot ever reaches applyThemeFromState (that call
  // sits after requirePortalLogin resolves), so until now the first screen anyone
  // saw was hardcoded light and the theme preference visibly did nothing to it.
  // Set the attribute only - calling applyThemeFromState here would register a
  // SECOND once-listener on the shell's theme toggle and the two would cancel.
  try { document.documentElement.dataset.theme = resolveTheme(getStoredTheme()); } catch (_) {}
  if (document.getElementById("portalLoginGateStyles")) return;
  const style = document.createElement("style");
  style.id = "portalLoginGateStyles";
  style.textContent = `
    html:has(.portal-login-gate),
    body:has(.portal-login-gate) {
      height: 100%;
      overflow: hidden;
    }
    body:has(.portal-login-gate) .app-shell {
      visibility: hidden;
      pointer-events: none;
      user-select: none;
    }
    /* ====================================================================
       THE SIGN-IN GATE.

       Everything below resolves through the design tokens in styles.css so
       the screen flips with the theme instead of being a light-mode island
       of hardcoded hexes (it was 34 of them, and dark mode rendered
       byte-identical to light).

       The handful of --login-* properties are gate-local tokens with a dark
       counterpart declared in the same block (rule G5). They exist because
       the global palette genuinely has no answer for them:
         - the ambient page wash is translucent, and no token is,
         - --blue is the light-mode CTA but its dark value (#4f93e8) carries
           white text at 3.15:1, so the dark CTA needs its own deeper blue,
         - --line-strong is 1.48:1 on a white panel, which fails WCAG 1.4.11
           for a form-field boundary; the field border needs >= 3:1.
       ==================================================================== */
    .portal-login-gate {
      /* A GATE-LOCAL GROUND, deliberately NOT --surface-page. The shell's
         page token is #f5f7fa, which against a #ffffff card is a 1.07:1
         figure/ground - measured, the entire horizontal band flanking the
         card was still the untouched base colour, which is what read as
         vague. VOB gets away with the same token because a solid white
         header AND a solid white footer supply its definition; we only have
         a footer, so the ground itself has to do the work. #e9eff7 takes
         card/ground to ~1.16:1 - the same sign and size the dark block
         already relies on - and keeps --muted at 5.9:1, well clear of AA.
         Do not go past #e6edf6: that is where the 12px help line drops
         under 4.5:1 and this stops being free. */
      --login-ground: #e9eff7;
      /* The top aurora carries TWO stops. A single-stop radial falls off
         linearly, so past ~60% of its radius it is worth nothing however
         strong the core is - measured, the band directly flanking the card
         was still landing at 0.0 levels off the ground even after the core
         was raised. The -far stop is a plateau that carries the wash out to
         the edges without making the top of the screen any louder. */
      --login-glow-1: rgba(10, 99, 201, 0.17);
      --login-glow-1-far: rgba(10, 99, 201, 0.12);
      --login-glow-2: rgba(20, 163, 189, 0.20);
      /* Navy at 8% over near-white desaturates to a grey smudge (measured
         233,238,243 - no hue left). --brand-2 at 14% keeps the hue. */
      --login-glow-3: rgba(2, 80, 143, 0.14);
      /* The third aurora and the grid ink are new gate-local colours for the
         same reason as the first two: the ambient field is drawn in
         TRANSLUCENT ink over --surface-page and not one palette token is
         translucent. --login-credit-plate-pad exists because the dark-mode
         white plate that rescues the navy-on-transparent artwork needs a much
         tighter inset around a 24px footer logo than around the 176px
         wordmark --login-plate-pad is sized for. */
      --login-grid-line: rgba(15, 23, 42, 0.06);
      --login-ring: rgba(10, 99, 201, 0.26);
      --login-field-border: #7d8ba1;          /* 3.45:1 on --panel */
      --login-cta-bg: var(--blue);            /* white ink 5.77:1 */
      --login-cta-bg-hover: var(--blue-dark); /* white ink 7.98:1 */
      --login-cta-ink: #ffffff;
      --login-plate: transparent;
      --login-plate-pad: 0px;
      --login-credit-plate-pad: 0px;
      position: fixed;
      inset: 0;
      z-index: 99999;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      background-color: var(--login-ground);
      color: var(--ink);
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    :root[data-theme="dark"] .portal-login-gate {
      /* Dark already measured as a designed field, so the ground stays put
         and only the light half moves. Declared anyway: an undeclared
         counterpart falls back to nothing, not to the light value. */
      --login-ground: #03101f;
      --login-glow-1: rgba(79, 147, 232, 0.20);
      --login-glow-1-far: rgba(79, 147, 232, 0.12);
      --login-glow-2: rgba(47, 192, 216, 0.13);
      --login-glow-3: rgba(12, 52, 104, 0.60);
      --login-grid-line: rgba(148, 184, 232, 0.08);
      --login-ring: rgba(79, 147, 232, 0.42);
      --login-field-border: #647a9d;          /* 3.66:1 on --panel-muted */
      --login-cta-bg: #2a66c8;                /* white ink 5.48:1 */
      --login-cta-bg-hover: #356fd8;          /* white ink 4.75:1 */
      --login-plate: #ffffff;
      --login-plate-pad: 10px 14px;
      --login-credit-plate-pad: 2px 5px;
    }
    /* ====================================================================
       THE AMBIENT FIELD.

       The old page was one flat wash with two 0.10-0.13 alpha glows parked
       in corners 14% and 112% off-canvas, i.e. almost nothing reached a
       1380px viewport - which is why the screen read as vague rather than
       calm. Two layers replace it:

         ::before  three soft brand auroras, top-lit, so the field has a
                   direction and the card sits IN something.
         ::after   a 34px hairline grid, masked to a ring - it is erased
                   directly behind the card so it can never compete with it,
                   and fades again past ~985px from centre so a very wide
                   monitor gets a frame rather than a hard edge.

       The mask is sized in PIXELS, not percentages. A percentage mask is
       relative to the element, i.e. the viewport: at 1380px wide the old
       116% radius put the full-ink stop 896px from centre, 206px PAST the
       left edge, so the pattern was mathematically unreachable at mid
       height and showed up only in the corners - an accidental vignette
       running the wrong way, which reads as a smudge rather than as
       structure. Fixed radii make the quiet zone, the plateau and the frame
       fade land in the same place on every monitor.

       Both are position:fixed, not absolute: the gate is a scroll container
       (the enrolment card can outgrow a short window) and an absolute layer
       would scroll away and leave bare --surface-page under the fold. Fixed
       descendants are not clipped by an ancestor's overflow, and the gate has
       no transform, so their containing block is the viewport.
       ==================================================================== */
    .portal-login-gate::before,
    .portal-login-gate::after {
      content: "";
      position: fixed;
      inset: 0;
      z-index: 0;
      pointer-events: none;
    }
    .portal-login-gate::before {
      background-image:
        radial-gradient(1520px 1020px at 50% -6%, var(--login-glow-1) 0%, var(--login-glow-1-far) 40%, transparent 92%),
        radial-gradient(880px 620px at 98% 106%, var(--login-glow-2), transparent 62%),
        radial-gradient(900px 640px at -6% 88%, var(--login-glow-3), transparent 60%);
    }
    .portal-login-gate::after {
      background-image:
        linear-gradient(var(--login-grid-line) 1px, transparent 1px),
        linear-gradient(90deg, var(--login-grid-line) 1px, transparent 1px);
      background-size: 34px 34px, 34px 34px;
      -webkit-mask-image: radial-gradient(1200px 760px at 50% 40%, transparent 0%, transparent 26%, #000 52%, #000 82%, transparent 100%);
      mask-image: radial-gradient(1200px 760px at 50% 40%, transparent 0%, transparent 26%, #000 52%, #000 82%, transparent 100%);
    }
    /* The stage and the band ride above the ambient layers. The gate already
       makes its own stacking context (fixed + z-index), so z-index:1 here is
       scoped to it and cannot reach the app shell underneath. */
    .portal-login-viewport {
      position: relative;
      z-index: 1;
      flex: 1 0 auto;
      display: flex;
      justify-content: center;
      width: 100%;
      /* The heavier bottom pad is optical: with margin:auto doing the
         centring, it lifts the card off the geometric middle to where the eye
         expects it. It cannot break the tall card's scroll-to-top - that is a
         property of the auto margin, not of the padding. */
      padding: 40px 24px clamp(32px, 7vh, 88px);
    }
    /* margin:auto on the child (rather than align-items:center on the flex
       parent) is what lets a tall card - the invite flow's - still scroll to
       its top instead of being clipped at both ends. */
    .portal-login-stage {
      width: min(412px, 100%);
      margin: auto;
      display: grid;
      gap: var(--space-5);
      animation: portalLoginRise 320ms cubic-bezier(0.16, 0.84, 0.44, 1) both;
    }
    @keyframes portalLoginRise {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: none; }
    }
    .portal-login-brand {
      display: grid;
      justify-items: center;
      gap: 12px;
    }
    .portal-login-brand-mark {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: var(--login-plate-pad);
      border-radius: var(--radius-md);
      background: var(--login-plate);
    }
    .portal-login-brand-mark img {
      display: block;
      width: 176px;
      max-width: 58vw;
      height: auto;
    }
    .portal-login-brand-name {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 5px 13px;
      border: 1px solid var(--line);
      border-radius: var(--radius-full);
      background: var(--panel);
      box-shadow: var(--elev-1);
      color: var(--ink-soft);
      font-size: var(--fs-200);
      font-weight: var(--fw-normal);
      letter-spacing: 0.02em;
    }
    .portal-login-brand-name::before {
      content: "";
      width: 6px;
      height: 6px;
      border-radius: var(--radius-full);
      background: var(--accent);
    }
    .portal-login-card {
      position: relative;
      display: grid;
      gap: var(--space-4);
      width: 100%;
      padding: 28px;
      border: 1px solid var(--line);
      border-radius: var(--radius-lg);
      background-color: var(--panel);
      /* The brand hairline along the top edge. A BACKGROUND layer, not an
         absolutely-positioned bar: a bar squares off outside the rounded
         corner, and clipping it with overflow:hidden would also clip the
         focus ring of anything that sits near the card edge. */
      background-image: linear-gradient(90deg, var(--brand), var(--blue) 52%, var(--accent));
      background-repeat: no-repeat;
      background-size: 100% 3px;
      background-position: left top;
      color: var(--ink);
      box-shadow: var(--elev-3);
      font-family: inherit;
    }
    .portal-login-head {
      display: grid;
      gap: 6px;
    }
    .portal-login-eyebrow {
      color: var(--muted);
      font-size: var(--fs-100);
      font-weight: var(--fw-strong);
      letter-spacing: 0.09em;
      text-transform: uppercase;
    }
    .portal-login-card h1 {
      margin: 0;
      color: var(--ink);
      font-size: var(--fs-700);
      font-weight: var(--fw-strong);
      line-height: 1.25;
      letter-spacing: -0.02em;
    }
    .portal-login-card p {
      margin: 0;
      color: var(--muted);
      font-size: var(--fs-300);
      font-weight: var(--fw-prose);
      line-height: 1.55;
    }
    .portal-login-card label {
      display: grid;
      gap: 7px;
      color: var(--ink-soft);
      font-size: var(--fs-300);
      font-weight: var(--fw-normal);
    }
    /* THE SHOW/HIDE CONTROL CANNOT LIVE INSIDE THE <label>. A click anywhere in
       a label is forwarded to the labelled control, so the button was hit-tested
       away and the eye did nothing by mouse - while still working by keyboard,
       which is exactly the kind of half-working nobody reports. The two fields
       therefore use explicit for/id pairing and keep the button outside. */
    .portal-login-group {
      display: grid;
      gap: 7px;
    }
    .portal-login-card label.portal-login-label {
      display: block;
      gap: 0;
    }
    /* AN INPUT GROUP, NOT AN OVERLAY. The reveal control was first built as an
       absolutely-positioned button sitting ON the password field, and Chromium
       never routed a single mouse event to it: inside the span's inline
       formatting context the click hit-tested straight through to the wrapper,
       so the eye worked by keyboard and did nothing at all by mouse. The
       wrapper is now a flex row that owns the border and the focus ring, and
       the button is a real sibling of the input with its own box. */
    .portal-login-field {
      display: flex;
      align-items: center;
      min-height: 44px;
      border: 1px solid var(--login-field-border);
      border-radius: var(--radius-sm);
      background: var(--panel);
      transition: border-color 140ms ease, box-shadow 140ms ease;
    }
    :root[data-theme="dark"] .portal-login-field { background: var(--panel-muted); }
    .portal-login-field:hover { border-color: var(--ink-soft); }
    .portal-login-field:focus-within {
      outline: 2px solid transparent;
      outline-offset: 2px;
      border-color: var(--blue);
      box-shadow: 0 0 0 3px var(--login-ring);
    }
    .portal-login-card input {
      width: 100%;
      min-height: 44px;
      border: 1px solid var(--login-field-border);
      border-radius: var(--radius-sm);
      background: var(--panel);
      color: var(--ink);
      padding: 10px 12px;
      /* Longhands, not the font: inherit shorthand, which reset font-size AND
         font-weight at (0,1,1), which is how a 30px OTP field rendered at
         13px inside its own stylesheet. */
      font-family: inherit;
      font-size: var(--fs-300);
      font-weight: var(--fw-prose);
      transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;
    }
    :root[data-theme="dark"] .portal-login-card input {
      background: var(--panel-muted);
      border-color: var(--login-field-border);
    }
    .portal-login-card input::placeholder { color: var(--muted); opacity: 1; }
    .portal-login-card input:hover { border-color: var(--ink-soft); }
    .portal-login-card input:focus,
    .portal-login-card input:focus-visible {
      /* A transparent outline rather than none: it is invisible here but
         Windows high-contrast mode repaints it, so the ring below is never the
         only thing standing between a keyboard user and a lost caret. */
      outline: 2px solid transparent;
      outline-offset: 2px;
      border-color: var(--blue);
      box-shadow: 0 0 0 3px var(--login-ring);
    }
    .portal-login-card .portal-login-field input {
      flex: 1 1 auto;
      min-width: 0;
      border: 0;
      background: transparent;
      padding-right: 6px;
    }
    .portal-login-card .portal-login-field input:focus,
    .portal-login-card .portal-login-field input:focus-visible {
      border-color: transparent;
      box-shadow: none;
    }
    .portal-login-reveal {
      flex: none;
      margin-right: 5px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      min-height: 0;
      padding: 0;
      border: 0;
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--muted);
      cursor: pointer;
      transition: color 120ms ease, background-color 120ms ease;
    }
    .portal-login-reveal:hover { background: var(--soft); color: var(--ink); }
    .portal-login-reveal:focus-visible { outline: 2px solid var(--blue); outline-offset: 2px; }
    .portal-login-reveal svg {
      width: 18px;
      height: 18px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .portal-login-reveal .portal-login-eye-off { display: none; }
    .portal-login-reveal.is-shown .portal-login-eye-on { display: none; }
    .portal-login-reveal.is-shown .portal-login-eye-off { display: block; }
    .portal-login-card button[type="submit"] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 9px;
      width: 100%;
      min-height: 44px;
      margin-top: 2px;
      border: 1px solid var(--login-cta-bg);
      border-radius: var(--radius-sm);
      background: var(--login-cta-bg);
      color: var(--login-cta-ink);
      font-family: inherit;
      font-size: var(--fs-300);
      font-weight: var(--fw-strong);
      letter-spacing: 0.01em;
      cursor: pointer;
      transition: background-color 130ms ease, border-color 130ms ease, transform 90ms ease;
    }
    .portal-login-card button[type="submit"]:hover {
      background: var(--login-cta-bg-hover);
      border-color: var(--login-cta-bg-hover);
    }
    .portal-login-card button[type="submit"]:active { transform: translateY(1px); }
    .portal-login-card button[type="submit"]:focus-visible {
      outline: 2px solid var(--login-cta-bg);
      outline-offset: 2px;
    }
    .portal-login-card button[type="submit"]:disabled { opacity: 0.75; cursor: wait; transform: none; }
    /* The submit label is rewritten with textContent while in flight, which
       wipes any child node - so the spinner has to be a pseudo-element. */
    .portal-login-card button[type="submit"]:disabled::after {
      content: "";
      width: 15px;
      height: 15px;
      border: 2px solid currentColor;
      border-right-color: transparent;
      border-bottom-color: transparent;
      border-radius: var(--radius-full);
      animation: portalLoginSpin 700ms linear infinite;
    }
    @keyframes portalLoginSpin { to { transform: rotate(360deg); } }
    .portal-login-card .portal-login-startover {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 44px;
      border: 1px solid var(--login-field-border);
      border-radius: var(--radius-sm);
      background: var(--panel);
      color: var(--ink);
      font-family: inherit;
      font-size: var(--fs-300);
      font-weight: var(--fw-normal);
      cursor: pointer;
      transition: background-color 120ms ease, border-color 120ms ease;
    }
    .portal-login-card .portal-login-startover:hover { background: var(--panel-muted); border-color: var(--ink-soft); }
    .portal-login-card .portal-login-startover:focus-visible { outline: 2px solid var(--blue); outline-offset: 2px; }
    /* THE TWO FAILURE LANES, KEPT TELLABLE APART. Red is "that credential is
       wrong"; amber is "the service is down, this is not your fault". They were
       the same bare red sentence, and the outage's Try again was inheriting the
       primary-button rule - so an outage rendered as a SECOND blue CTA under
       the first, which reads as a step to take rather than a wait. */
    .portal-login-error {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: 9px 11px;
      padding: 11px 13px;
      border: 1px solid var(--danger-border);
      border-left: 3px solid var(--danger-ink);
      border-radius: var(--radius-sm);
      background: var(--danger-bg);
      color: var(--danger-ink);
      font-size: var(--fs-300);
      font-weight: var(--fw-normal);
      line-height: 1.5;
    }
    /* THE LIVE REGION STAYS IN THE ACCESSIBILITY TREE. display:none would take
       it out, and a region that enters the tree in the same frame as its text
       is announced unreliably - which for a screen-reader user is the ONLY
       feedback that the sign-in failed. It collapses to nothing instead, and
       the negative margin eats the grid gap a zero-height item would add so the
       idle card is pixel-identical to having no panel at all. */
    .portal-login-error:empty {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
    .portal-login-error:empty::before { display: none; }
    .portal-login-error::before {
      content: "";
      flex: none;
      width: 17px;
      height: 17px;
      margin-top: 1px;
      background-color: currentColor;
      -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'><circle cx='12' cy='12' r='9'/><path d='M12 7.5v5'/><path d='M12 16.4h.01'/></svg>") center / contain no-repeat;
      mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'><circle cx='12' cy='12' r='9'/><path d='M12 7.5v5'/><path d='M12 16.4h.01'/></svg>") center / contain no-repeat;
    }
    .portal-login-error.is-outage {
      border-color: var(--warn-border);
      border-left-color: var(--warn-ink);
      background: var(--warn-bg);
      color: var(--warn-ink);
    }
    .portal-login-error-text { flex: 1 1 220px; min-width: 0; }
    .portal-login-card .portal-login-retry {
      flex: none;
      margin-left: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 34px;
      padding: 0 14px;
      border: 1px solid var(--warn-border);
      border-radius: var(--radius-sm);
      background: var(--panel);
      color: var(--warn-ink);
      font-family: inherit;
      font-size: var(--fs-250);
      font-weight: var(--fw-normal);
      cursor: pointer;
      transition: background-color 120ms ease;
    }
    .portal-login-card .portal-login-retry:hover { background: var(--warn-bg); }
    .portal-login-card .portal-login-retry:focus-visible { outline: 2px solid var(--warn-ink); outline-offset: 2px; }
    .portal-login-foot {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: 8px;
      margin: 2px 0 0;
      padding-top: 15px;
      border-top: 1px solid var(--line);
      text-align: center;
      color: var(--muted);
      font-size: var(--fs-200);
      font-weight: var(--fw-prose);
    }
    .portal-login-shield {
      width: 15px;
      height: 15px;
      flex-shrink: 0;
      fill: none;
      stroke: var(--accent);
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .portal-login-help {
      margin: 0;
      text-align: center;
      color: var(--muted);
      font-size: var(--fs-200);
      font-weight: var(--fw-prose);
      line-height: 1.55;
    }
    /* THE HORIZON. VOB frames its sign-in with a solid header band above and
       a solid footer band below; ours had neither, and an unbounded field is
       most of why the page read as a void. This band gives the viewport a
       real bottom edge, and being OPAQUE (--panel) it also takes its own text
       off the gradient field, so the credit line's contrast is a fixed pair
       rather than something that moves with the aurora underneath it. */
    .portal-login-credit {
      position: relative;
      z-index: 1;
      flex: none;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px 20px;
      width: 100%;
      padding: 12px clamp(18px, 5vw, 40px);
      border-top: 1px solid var(--line);
      background: var(--panel);
      color: var(--muted);
      font-size: var(--fs-200);
      font-weight: var(--fw-prose);
    }
    .portal-login-credit-name { letter-spacing: 0.01em; }
    .portal-login-credit-link {
      display: inline-flex;
      align-items: center;
      gap: 9px;
      margin: -4px -8px -4px 0;
      padding: 4px 8px;
      border-radius: var(--radius-sm);
      color: var(--muted);
      text-decoration: none;
      transition: background-color 120ms ease, color 120ms ease;
    }
    .portal-login-credit-link:hover { background: var(--panel-muted); color: var(--ink-soft); }
    .portal-login-credit-link:focus-visible { outline: 2px solid var(--blue); outline-offset: 2px; }
    /* The NSD artwork is navy-on-transparent, the same problem the TMS
       wordmark above the card has, so dark mode plates it on white with the
       same --login-plate rather than filtering artwork we do not own the
       source of. */
    .portal-login-credit-logo {
      display: block;
      /* 20px, the height VOB's own footer gives this artwork. At 24 the
         mark's brighter cyan-blue starts reading as a SECOND blue in the
         bottom-right corner and competes with the CTA. Confident, not
         loud - and no opacity, because a faded partner logo is worse than
         a small one. */
      height: 20px;
      width: auto;
      padding: var(--login-credit-plate-pad);
      border-radius: var(--radius-sm);
      background: var(--login-plate);
    }
    .portal-otp-label { justify-items: center; text-align: center; }
    .portal-login-card .portal-otp-input {
      width: 100%;
      max-width: 258px;
      min-height: 58px;
      justify-self: center;
      text-align: center;
      font-size: var(--fs-800);
      font-weight: var(--fw-strong);
      /* text-indent cancels the trailing letter-space so six digits sit on the
         optical centre rather than 12px left of it. */
      letter-spacing: 12px;
      text-indent: 12px;
      color: var(--ink);
    }
    .portal-login-card label.portal-login-check {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 11px 13px;
      border: 1px solid var(--line);
      border-radius: var(--radius-sm);
      background: var(--panel-muted);
      color: var(--ink-soft);
      font-size: var(--fs-300);
      font-weight: var(--fw-normal);
      cursor: pointer;
    }
    .portal-login-card label.portal-login-check:hover { border-color: var(--login-field-border); }
    .portal-login-check input {
      min-height: 0;
      flex: none;
      width: 17px;
      height: 17px;
      accent-color: var(--blue);
    }
    .portal-login-card .portal-login-resend {
      min-height: 0;
      padding: 0;
      border: 0;
      background: none;
      color: var(--blue);
      font-family: inherit;
      font-size: var(--fs-200);
      font-weight: var(--fw-normal);
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .portal-login-card .portal-login-resend:hover { background: none; color: var(--blue-dark); }
    .portal-login-card .portal-login-resend:disabled { color: var(--muted); cursor: default; text-decoration: none; opacity: 1; }
    .portal-login-card .portal-login-resend:focus-visible { outline: 2px solid var(--blue); outline-offset: 3px; border-radius: var(--radius-sm); }
    .portal-login-resend-wait { color: var(--muted); }
    @media (max-width: 560px) {
      /* The card is full-bleed here, so the grid has nothing left to frame -
         it just becomes the loudest thing on the screen after the card, on
         the device least able to afford the extra compositing layer. */
      .portal-login-gate::after { display: none; }
      /* THE AURORA IS RE-SIZED, NOT RE-TINTED. Its radii are absolute pixels,
         chosen for a desktop viewport - on a 390px screen the entire page sits
         inside the core, the wash never falls off, and the help line under the
         card measured 4.41:1 against --muted, i.e. under AA. Shrinking the
         radii puts the falloff back on screen: the sky still covers the top
         through the card, and the ground is clean again by the time the help
         line starts. Same colours, same intent, correct scale. */
      .portal-login-gate::before {
        background-image:
          radial-gradient(700px 620px at 50% -10%, var(--login-glow-1) 0%, var(--login-glow-1-far) 45%, transparent 92%),
          radial-gradient(460px 420px at 100% 104%, var(--login-glow-2), transparent 62%),
          radial-gradient(420px 380px at -10% 86%, var(--login-glow-3), transparent 62%);
      }
      .portal-login-viewport { padding: 24px 16px clamp(24px, 5vh, 56px); }
      .portal-login-credit {
        flex-direction: column;
        justify-content: center;
        gap: 9px;
        padding: 12px 16px;
        text-align: center;
      }
      .portal-login-credit-link { margin: 0; }
      .portal-login-stage { gap: var(--space-4); }
      .portal-login-card { padding: 22px 18px; border-radius: var(--radius-md); }
      .portal-login-card h1 { font-size: var(--fs-600); }
      .portal-login-brand-mark img { width: 152px; }
      .portal-login-card .portal-otp-input { font-size: var(--fs-700); letter-spacing: 9px; text-indent: 9px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .portal-login-stage { animation: none; }
      .portal-login-card button[type="submit"]:disabled::after { animation: none; }
      .portal-login-gate *,
      .portal-login-gate *::before,
      .portal-login-gate *::after { transition-duration: 1ms !important; }
    }
    /* A quiet reporting-led welcome; decoration settles once, then remains still. */
    .portal-login-gate { --login-ground: #edf2f5; background: var(--login-ground); }
    :root[data-theme="dark"] .portal-login-gate { --login-ground: #061322; }
    .portal-login-gate::before { background: radial-gradient(ellipse at 12% 12%, rgba(18,164,177,.07), transparent 54%); }
    .portal-login-gate::after { display: none; }
    .portal-login-viewport { padding: 36px; }
    .portal-login-stage { width: min(1120px, 100%); grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 0; align-items: start; border: 1px solid var(--line); border-radius: 26px; background: var(--panel); box-shadow: 0 24px 75px rgba(9,32,57,.1); overflow: hidden; animation: none; }
    .portal-login-hero { position: relative; isolation: isolate; align-self: stretch; grid-column: 1; grid-row: 1 / 5; min-height: 660px; display: flex; flex-direction: column; padding: 58px 48px; background: #092737; color: #f5fafb; overflow: hidden; }
    .portal-login-ambient { position: absolute; inset: 0; overflow: hidden; z-index: -1; background: linear-gradient(150deg, #0b253e 0%, #0b3543 65%, #125354 100%); }
    .portal-login-orb { position: absolute; width: 430px; height: 430px; border-radius: 50%; filter: blur(60px); opacity: .23; animation: portalLoginAmbientDrift 4s ease-out 1 both; }
    .portal-login-orb-a { background: #0dabb8; left: 170px; top: 220px; }
    .portal-login-orb-b { width: 310px; height: 310px; background: #9bbd50; left: -170px; bottom: -180px; opacity: .17; }
    .portal-login-orbit { position: absolute; width: 800px; height: 800px; border: 1px solid rgba(124,216,218,.09); border-radius: 50%; left: -410px; top: 65px; }
    @keyframes portalLoginAmbientDrift { from { transform: translate3d(-18px,-12px,0) scale(.96); } to { transform: translate3d(0,0,0) scale(1); } }
    .portal-login-product { display: flex; align-items: center; gap: 10px; margin: 0 0 50px; color: #c3dadd; font-size: 10px; letter-spacing: .15em; font-weight: 600; }
    .portal-login-product > span { display: block; width: 20px; height: 3px; border-radius: 2px; background: #8cbc43; }
    .portal-login-hero h2 { margin: 0; color: #f6fbfb; font-size: clamp(38px,4.1vw,57px); line-height: 1.06; font-weight: 500; letter-spacing: -.055em; }
    .portal-login-hero h2 em { font-family: Georgia, "Times New Roman", serif; font-weight: 400; color: #a9ded8; letter-spacing: -.045em; }
    .portal-login-hero-description { margin: 26px 0 0; font-size: 15px; line-height: 1.8; color: #bed5db; font-weight: 400; }
    .portal-login-flow { margin-top: 44px; width: min(330px,100%); }
    .portal-login-report-illustration { display: grid; grid-template-columns: 24px 1fr; align-items: center; gap: 14px 12px; border: 1px solid rgba(177,227,223,.23); border-radius: 14px; padding: 22px; position: relative; isolation: isolate; transform: perspective(1000px) rotateX(8deg) rotateY(-12deg) rotateZ(-2deg); transform-style: preserve-3d; background: linear-gradient(135deg, rgba(73,130,142,.7), rgba(19,65,80,.94)); box-shadow: 12px 18px 0 -6px rgba(72,141,151,.18), 24px 36px 0 -12px rgba(72,141,151,.09), 22px 30px 48px rgba(0,10,24,.38), inset 0 1px 0 rgba(218,255,247,.23); }
    .portal-login-flow-icon { width: 24px; height: 24px; display: grid; place-items: center; color: #b8ddd6; }
    .portal-login-flow-icon svg { width: 22px; height: 22px; stroke: currentColor; stroke-width: 1.5; fill: none; stroke-linecap: round; stroke-linejoin: round; }
    .portal-login-report-illustration strong { color: #e2f1f1; font-size: 15px; font-weight: 500; }
    .portal-login-report-bars { grid-column: 1 / 3; width: 100%; height: 90px; margin-top: 8px; }
    .portal-login-report-bars path { stroke: rgba(177,227,223,.12); stroke-width: 1; }
    .portal-login-report-bars rect { fill: rgba(151,208,201,.65); filter: drop-shadow(4px 5px 0 rgba(6,38,52,.65)); }
    .portal-login-report-bars rect:last-child { fill: #a5c981; }
    .portal-login-report-capabilities { grid-column: 1 / 3; display: flex; gap: 18px; padding-top: 6px; color: #b9d4d8; font-size: 11px; }
    .portal-login-report-capabilities span + span { border-left: 1px solid rgba(177,227,223,.25); padding-left: 18px; }
    .portal-login-stage > .portal-login-brand { grid-column: 2; align-self: end; margin: 40px 46px 24px; justify-items: start; gap: 9px; }
    .portal-login-brand-mark img { width: 178px; }
    .portal-login-brand-name { padding: 0; border: 0; border-radius: 0; background: none; box-shadow: none; font-size: 18px; font-weight: 600; letter-spacing: -.025em; text-transform: none; }
    .portal-login-brand-name::before { display: none; }
    .portal-login-stage > .portal-login-card { grid-column: 2; width: calc(100% - 92px); margin: 0 46px; padding: 0; border: 0; border-radius: 0; box-shadow: none; background-image: none; }
    .portal-login-head-signin { text-align: left; }
    .portal-login-card h1 { font-size: 25px; letter-spacing: -.04em; font-weight: 600; }
    .portal-login-card .portal-login-head { gap: 9px; padding-bottom: 6px; }
    .portal-login-card input { min-height: 46px; border-radius: 8px; }
    :root[data-theme="dark"] .portal-login-card input { background-color: var(--panel-muted); color: var(--ink); -webkit-text-fill-color: var(--ink); }
    .portal-login-card button[type="submit"] { min-height: 46px; border-radius: 8px; }
    .portal-login-stage > .portal-login-help { grid-column: 2; margin: 22px 30px 14px; text-align: center; font-size: 11px; }
    .portal-login-developer { grid-column: 2; padding: 0 28px 28px; text-align: center; }
    .portal-login-developer a { display: inline-flex; align-items: center; justify-content: center; gap: 12px; min-height: 44px; color: var(--muted); font-size: 11px; text-decoration: none; }
    .portal-login-developer a:hover { color: var(--blue); }
    .portal-login-developer a:focus-visible { outline: 2px solid var(--blue); outline-offset: 4px; border-radius: 4px; }
    .portal-login-developer img { display: block; width: 88px; height: auto; background: var(--login-plate); padding: var(--login-credit-plate-pad); border-radius: 4px; }
    .portal-login-credit { justify-content: center; background: transparent; border: 0; padding: 10px 24px 18px; }
    @media (max-width: 1050px) and (min-width: 801px) {
      .portal-login-hero { padding: 44px 30px; }
      .portal-login-stage > .portal-login-brand { margin-left: 30px; margin-right: 30px; }
      .portal-login-stage > .portal-login-card { width: calc(100% - 60px); margin-left: 30px; margin-right: 30px; }
    }
    @media (max-width: 800px) {
      .portal-login-viewport { padding: 18px 16px; }
      .portal-login-stage { grid-template-columns: minmax(0,1fr); width: min(520px,100%); border-radius: 20px; }
      .portal-login-hero { grid-column: 1; grid-row: 1; min-height: 0; padding: 22px 26px; }
      .portal-login-product { display: none; }
      .portal-login-hero h2 { font-size: 32px; line-height: 1.15; }
      .portal-login-hero h2 br { display: initial; }
      .portal-login-hero-description { font-size: 12px; line-height: 1.6; margin-top: 10px; }
      .portal-login-flow { display: none; }
      .portal-login-orb-a { width: 250px; height: 250px; left: 170px; top: -100px; }
      .portal-login-orb-b { width: 220px; height: 220px; bottom: -180px; }
      .portal-login-stage > .portal-login-brand { grid-column: 1; margin: 24px 26px 20px; }
      .portal-login-brand-mark img { width: 152px; }
      .portal-login-stage > .portal-login-card { grid-column: 1; width: calc(100% - 52px); margin: 0 26px; gap: 14px; }
      .portal-login-stage > .portal-login-help { grid-column: 1; margin: 18px 24px 10px; line-height: 1.6; }
      .portal-login-developer { grid-column: 1; padding: 0 22px 18px; }
      .portal-login-card h1 { font-size: 24px; }
      .portal-login-card .portal-login-head { gap: 6px; padding-bottom: 2px; }
      .portal-login-card .portal-login-foot { padding-top: 12px; font-size: 11px; }
      .portal-login-credit { padding: 5px 16px 16px; }
    }
    @media (max-width: 400px) {
      .portal-login-viewport { padding: 12px; }
      .portal-login-hero { padding: 20px 22px; }
      .portal-login-hero h2 { font-size: 30px; }
      .portal-login-stage > .portal-login-brand { margin: 22px 22px 18px; }
      .portal-login-stage > .portal-login-card { width: calc(100% - 44px); margin: 0 22px; }
      .portal-login-card h1 { font-size: 23px; }
      .portal-login-developer a { gap: 9px; font-size: 10px; }
    }
    /* Secondary help stays available without competing with sign-in. */
    .portal-login-developer a { gap: 8px; min-height: 28px; font-size: 10px; }
    .portal-login-developer img { width: 56px; }
    .portal-login-card [data-portal-forgot-password],
    .portal-login-card [data-password-request] { justify-self: center; width: fit-content; min-height: 28px; margin: 0; padding: 4px 8px; border: 0; background: transparent; box-shadow: none; color: var(--muted); font-size: 12px; font-weight: 400; line-height: 1.5; }
    .portal-login-card [data-portal-forgot-password]:hover,
    .portal-login-card [data-password-request]:hover { background: transparent; color: var(--blue); text-decoration: underline; }
    .portal-login-card [data-portal-forgot-password]:focus-visible,
    .portal-login-card [data-password-request]:focus-visible { outline: 2px solid var(--blue); outline-offset: 3px; }
    /* Full-screen welcome, September 2026: the split continues to every edge. */
    .portal-login-viewport { padding: 0; }
    .portal-login-stage { width: 100%; margin: 0; border: 0; border-radius: 0; box-shadow: none; }
    .portal-login-brand-mark img { width: 216px; max-width: 100%; }
    .portal-login-stage > .portal-login-brand { gap: 0; }
    @media (min-width: 801px) {
      .portal-login-stage { min-height: calc(100svh - 42px); grid-template-rows: minmax(40px, 1fr) auto auto auto auto minmax(36px, 1fr); }
      .portal-login-hero { grid-row: 1 / 7; min-height: 680px; padding: clamp(48px, 7vh, 88px) clamp(40px, 6vw, 112px); justify-content: center; }
      .portal-login-product { margin-bottom: 42px; }
      .portal-login-hero h2 { font-size: clamp(42px, 3.6vw, 68px); }
      .portal-login-flow { width: min(360px, 100%); }
      .portal-login-stage > .portal-login-brand { grid-row: 2; width: min(440px, calc(100% - 80px)); margin: 0 auto 36px; }
      .portal-login-stage > .portal-login-card { grid-row: 3; width: min(440px, calc(100% - 80px)); margin: 0 auto; }
      .portal-login-stage > .portal-login-help { grid-row: 4; margin: 22px 30px 12px; }
      .portal-login-developer { grid-row: 5; padding-bottom: 0; }
      .portal-login-credit { min-height: 42px; padding: 10px 24px 14px calc(50% + 24px); background: linear-gradient(to right, #125354 50%, var(--panel) 50%); }
    }
    @media (max-width: 800px) {
      .portal-login-stage { width: 100%; border-radius: 0; }
      .portal-login-stage > .portal-login-brand { margin-top: 24px; margin-bottom: 24px; }
      .portal-login-brand-mark img { width: 184px; }
      .portal-login-gate, .portal-login-credit { background: var(--panel); }
    }
    @media (prefers-reduced-motion: reduce) { .portal-login-orb { animation: none !important; } }
    /* Living welcome, October 2026: a choreographed entrance that settles inside the
       existing four-second window, then pointer-follow depth. Nothing loops. */
    .portal-login-gate { --login-px: 0; --login-py: 0; --login-ease: cubic-bezier(.16,.84,.3,1); }
    .portal-login-orb-c { width: 260px; height: 260px; background: #3fb6c9; right: -90px; top: -70px; opacity: .14; }
    .portal-login-dots { position: absolute; inset: 0; background-image: radial-gradient(rgba(190,236,232,.16) 1px, transparent 1.4px); background-size: 26px 26px; -webkit-mask-image: radial-gradient(ellipse 70% 60% at 70% 40%, #000 0%, transparent 72%); mask-image: radial-gradient(ellipse 70% 60% at 70% 40%, #000 0%, transparent 72%); opacity: 0; animation: portalLoginFade 1.6s ease-out .2s 1 both; }
    .portal-login-sweep { position: absolute; top: -20%; bottom: -20%; left: -40%; width: 38%; background: linear-gradient(100deg, transparent, rgba(214,255,250,.08) 45%, rgba(214,255,250,.13) 50%, rgba(214,255,250,.08) 55%, transparent); transform: translateX(0) skewX(-12deg); opacity: 0; animation: portalLoginSweep 2.2s cubic-bezier(.45,.05,.3,1) .9s 1 both; }
    .portal-login-ambient { transform: translate3d(calc(var(--login-px) * -16px), calc(var(--login-py) * -12px), 0) scale(1.04); transition: transform 1.1s var(--login-ease); }
    .portal-login-product { animation: portalLoginUp .7s var(--login-ease) .15s 1 both; }
    .portal-login-hero h2 { animation: portalLoginFocus 1s var(--login-ease) .3s 1 both; }
    .portal-login-hero h2 em { background: linear-gradient(100deg, #a9ded8 0%, #a9ded8 38%, #f3fffb 50%, #a9ded8 62%, #a9ded8 100%); background-size: 260% 100%; background-position: 100% 0; -webkit-background-clip: text; background-clip: text; color: transparent; padding-right: .06em; animation: portalLoginShine 1.5s ease-in-out 1.15s 1 both; }
    .portal-login-hero-description { animation: portalLoginUp .8s var(--login-ease) .65s 1 both; }
    .portal-login-flow { perspective: 1100px; transform-style: preserve-3d; transform: translate3d(calc(var(--login-px) * 12px), calc(var(--login-py) * 9px), 0) rotateX(calc(var(--login-py) * -5deg)) rotateY(calc(var(--login-px) * 7deg)); transition: transform .9s var(--login-ease); }
    .portal-login-report-illustration { animation: portalLoginCardIn 1.15s var(--login-ease) .8s 1 both; transition: box-shadow .5s ease; }
    .portal-login-flow:hover .portal-login-report-illustration { box-shadow: 12px 18px 0 -6px rgba(72,141,151,.22), 24px 36px 0 -12px rgba(72,141,151,.12), 26px 36px 60px rgba(0,10,24,.46), 0 0 0 1px rgba(169,222,216,.35), inset 0 1px 0 rgba(218,255,247,.3); }
    .portal-login-report-bars rect { transform-box: fill-box; transform-origin: 50% 100%; animation: portalLoginBar .75s var(--login-ease) 1 both; }
    .portal-login-report-bars rect:nth-of-type(1) { animation-delay: 1.3s; }
    .portal-login-report-bars rect:nth-of-type(2) { animation-delay: 1.38s; }
    .portal-login-report-bars rect:nth-of-type(3) { animation-delay: 1.46s; }
    .portal-login-report-bars rect:nth-of-type(4) { animation-delay: 1.54s; }
    .portal-login-report-bars rect:nth-of-type(5) { animation-delay: 1.62s; }
    .portal-login-report-bars rect:nth-of-type(6) { animation-delay: 1.7s; }
    .portal-login-report-bars rect:last-of-type { fill: #a5c981; }
    .portal-login-report-bars .portal-login-trend { fill: none; stroke: #e9f8c9; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 1; stroke-dashoffset: 1; filter: drop-shadow(0 0 6px rgba(233,248,201,.55)); animation: portalLoginDraw 1.1s cubic-bezier(.6,.05,.25,1) 1.9s 1 both; }
    .portal-login-trend-dot { fill: #f4ffd9; stroke: rgba(165,201,129,.55); stroke-width: 6; transform-box: fill-box; transform-origin: center; animation: portalLoginPop .45s cubic-bezier(.3,1.6,.5,1) 2.95s 1 both; }
    .portal-login-report-capabilities { animation: portalLoginFade .7s ease-out 2.5s 1 both; }
    .portal-login-stage > .portal-login-brand { animation: portalLoginUp .7s var(--login-ease) .1s 1 both; }
    .portal-login-stage > .portal-login-card { animation: portalLoginUp .8s var(--login-ease) .25s 1 both; }
    .portal-login-stage > .portal-login-help,
    .portal-login-developer { animation: portalLoginFade .8s ease-out .6s 1 both; }
    .portal-login-card input { transition: border-color .2s ease, box-shadow .25s ease, background-color .2s ease; }
    .portal-login-card input:focus { border-color: #14a3bd; box-shadow: 0 0 0 4px rgba(20,163,189,.16), 0 6px 18px rgba(14,124,134,.10); }
    .portal-login-card button[type="submit"] { background-image: linear-gradient(100deg, transparent 35%, rgba(255,255,255,.3) 50%, transparent 65%); background-size: 260% 100%; background-repeat: no-repeat; background-position: 130% 0; transition: transform .2s var(--login-ease), box-shadow .25s ease, background-color .2s ease, background-position .75s var(--login-ease); }
    .portal-login-card button[type="submit"]:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(12,96,180,.28); }
    .portal-login-card button[type="submit"]:not(:disabled):hover { background-image: linear-gradient(100deg, transparent 35%, rgba(255,255,255,.3) 50%, transparent 65%); background-size: 260% 100%; background-repeat: no-repeat; background-position: -30% 0; }
    .portal-login-card button[type="submit"]:not(:disabled):active { transform: translateY(0); box-shadow: 0 4px 10px rgba(12,96,180,.22); }
    @keyframes portalLoginFade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes portalLoginUp { from { opacity: 0; transform: translate3d(0,18px,0); } to { opacity: 1; transform: none; } }
    @keyframes portalLoginFocus { from { opacity: 0; transform: translate3d(0,22px,0); filter: blur(10px); } to { opacity: 1; transform: none; filter: blur(0); } }
    @keyframes portalLoginShine { from { background-position: 100% 0; } to { background-position: 0% 0; } }
    @keyframes portalLoginSweep { 0% { opacity: 0; transform: translateX(0) skewX(-12deg); } 15% { opacity: 1; } 85% { opacity: 1; } 100% { opacity: 0; transform: translateX(420%) skewX(-12deg); } }
    @keyframes portalLoginCardIn { from { opacity: 0; transform: perspective(1000px) translate3d(0,46px,0) rotateX(26deg) rotateY(-4deg) rotateZ(-4deg); } to { opacity: 1; transform: perspective(1000px) rotateX(8deg) rotateY(-12deg) rotateZ(-2deg); } }
    @keyframes portalLoginBar { from { transform: scaleY(0); opacity: .2; } to { transform: scaleY(1); opacity: 1; } }
    @keyframes portalLoginDraw { to { stroke-dashoffset: 0; } }
    @keyframes portalLoginPop { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
    @media (prefers-reduced-motion: reduce) {
      .portal-login-dots, .portal-login-product, .portal-login-hero h2, .portal-login-hero h2 em, .portal-login-hero-description,
      .portal-login-report-illustration, .portal-login-report-bars rect, .portal-login-trend, .portal-login-trend-dot,
      .portal-login-report-capabilities, .portal-login-stage > .portal-login-brand, .portal-login-stage > .portal-login-card,
      .portal-login-stage > .portal-login-help, .portal-login-developer { animation: none !important; }
      .portal-login-sweep { display: none; }
      .portal-login-report-bars .portal-login-trend { stroke-dashoffset: 0; }
      .portal-login-hero h2 em { background-position: 0% 0; }
      .portal-login-ambient, .portal-login-flow { transform: none !important; transition: none !important; }
      .portal-login-card button[type="submit"] { background-image: none; }
    }
  `;
  document.head.appendChild(style);
}

// Run __tmsBoot when the DOM is ready — or immediately if we missed
// the DOMContentLoaded event (script was injected late by a cache-busting
// loader).
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", __tmsBoot);
} else {
  // Defer one tick so any other module-init code runs first.
  Promise.resolve().then(__tmsBoot);
}

// UI-100 #89: turn uncaught runtime errors into non-technical user feedback
// (without losing the stack trace from devtools).
function installFriendlyErrorHandler() {
  if (typeof window === "undefined" || window.__tmsFriendlyErrors) return;
  window.__tmsFriendlyErrors = true;
  const surface = (label) => {
    if (typeof showToast === "function") {
      showToast(`Something went wrong — ${label}. Your work is saved locally; try again.`);
    }
  };
  window.addEventListener("error", (e) => {
    // Filter out script-loading hiccups; they're noisy and usually transient.
    if (!e || !e.error) return;
    surface("we hit an unexpected snag");
    console.error("[tms.portal] uncaught:", e.error);
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (!e || !e.reason) return;
    surface("a background request failed");
    console.error("[tms.portal] unhandled rejection:", e.reason);
  });
}

// Scope the Reports @media print rules to body.reports-printing so the
// global "hide everything but #reportsView" block only fires while the
// Reports view is actually being printed (data-ar-print buttons OR Ctrl+P).
// Statements has its own explicit print flow (sets statements-printing);
// we deliberately do not touch it here. Registered once per window.
let reportPrintComplete = false;
function installReportsPrintScope() {
  if (typeof window === "undefined" || window.__tmsReportsPrintScope) return;
  window.__tmsReportsPrintScope = true;
  if (!window.addEventListener) return;
  window.addEventListener("beforeprint", function () {
    if (!document.body.classList.contains("statements-printing") && state && state.view === "reports") {
      document.body.classList.add("reports-printing");
      const preparedPage = trackerApiEnabled() ? preparedSnapshotReportState.response?.groupPage : null;
      reportPrintComplete = !preparedPage;
      renderReports();
      if (preparedPage && els.reportsBody) {
        const notice = document.createElement("p"); notice.id = "preparedReportPrintNotice"; notice.className = "panel";
        notice.textContent = "Printed report page " + Number(preparedPage.page || 1) + ". The table contains this page's groups; totals cover all " + Number(preparedPage.totalRows || 0).toLocaleString() + " groups. Use the complete export for all underlying rows.";
        els.reportsBody.prepend(notice);
      }
    }
  });
  window.addEventListener("afterprint", function () {
    document.body.classList.remove("reports-printing");
    document.getElementById("preparedReportPrintNotice")?.remove();
    if (!reportPrintComplete) return;
    reportPrintComplete = false;
    if (state && state.view === "reports") renderReports();
  });
}

function bindElements() {
  Object.assign(els, {
    roleSelect: document.getElementById("roleSelect"),
    userSelect: document.getElementById("userSelect"),
    userScopeHint: document.getElementById("userScopeHint"),
    viewTitle: document.getElementById("viewTitle"),
    viewSubtitle: document.getElementById("viewSubtitle"),
    navItems: Array.from(document.querySelectorAll(".nav-item")),
    navSections: Array.from(document.querySelectorAll(".nav-section")),
    viewPanels: Array.from(document.querySelectorAll(".view-panel")),
    metricPastDue: document.getElementById("metricPastDue"),
    metricReview: document.getElementById("metricReview"),
    metricOwed: document.getElementById("metricOwed"),
    metricAging: document.getElementById("metricAging"),
    rowModeSelect: document.getElementById("rowModeSelect"),
    claimIdSearchInput: document.getElementById("claimIdSearchInput"),
    claimIdSearchList: document.getElementById("claimIdSearchList"),
    claimIdFilter: document.getElementById("claimIdFilter"),
    claimScreenshotBtn: document.getElementById("claimScreenshotBtn"),
    claimScreenshotFileInput: document.getElementById("claimScreenshotFileInput"),
    claimScreenshotStatus: document.getElementById("claimScreenshotStatus"),
    claimScreenshotCancelBtn: document.getElementById("claimScreenshotCancelBtn"),
    claimScreenshotReview: document.getElementById("claimScreenshotReview"),
    claimScreenshotReviewInput: document.getElementById("claimScreenshotReviewInput"),
    claimScreenshotReviewCount: document.getElementById("claimScreenshotReviewCount"),
    claimScreenshotApplyBtn: document.getElementById("claimScreenshotApplyBtn"),
    claimIdSearchHint: document.getElementById("claimIdSearchHint"),
    clearClaimIdsBtn: document.getElementById("clearClaimIdsBtn"),
    serviceIdSearchInput: document.getElementById("serviceIdSearchInput"),
    searchInput: document.getElementById("searchInput"),
    noteSearchInput: document.getElementById("noteSearchInput"),
    clientScopeSwitcher: document.getElementById("clientScopeSwitcher"),
    clientScopeSelect: document.getElementById("clientScopeSelect"),
    clientScopeChip: document.getElementById("clientScopeChip"),
    clientScopeNotice: document.getElementById("clientScopeNotice"),
    tenantFilter: document.getElementById("tenantFilter"),
    providerFilter: document.getElementById("providerFilter"),
    primaryFilter: document.getElementById("primaryFilter"),
    secondaryFilter: document.getElementById("secondaryFilter"),
    actionFilter: document.getElementById("actionFilter"),
    matchFilter: document.getElementById("matchFilter"),
    claimStatusFilter: document.getElementById("claimStatusFilter"),
    lifecycleFilter: document.getElementById("lifecycleFilter"),
    agingFilter: document.getElementById("agingFilter"),
    ownerFilter: document.getElementById("ownerFilter"),
    minBalanceFilter: document.getElementById("minBalanceFilter"),
    queueCards: document.getElementById("queueCards"),
    clearQueueBtn: document.getElementById("clearQueueBtn"),
    arForecastPanel: document.getElementById("arForecastPanel"),
    claimSubmissionCommand: document.getElementById("claimSubmissionCommand"),
    providerCredentialingCommand: document.getElementById("providerCredentialingCommand"),
    prebillQualityPanel: document.getElementById("prebillQualityPanel"),
    denialAppealCommand: document.getElementById("denialAppealCommand"),
    operationsIntelligence: document.getElementById("operationsIntelligence"),
    paymentReconciliationQueue: document.getElementById("paymentReconciliationQueue"),
    creditBalanceCommand: document.getElementById("creditBalanceCommand"),
    patientResponsibilityCommand: document.getElementById("patientResponsibilityCommand"),
    payerContactCommand: document.getElementById("payerContactCommand"),
    savedOperationsViews: document.getElementById("savedOperationsViews"),
    billerTaskSummary: document.getElementById("billerTaskSummary"),
    prevWorkClaimBtn: document.getElementById("prevWorkClaimBtn"),
    nextWorkClaimBtn: document.getElementById("nextWorkClaimBtn"),
    openFocusedClaimBtn: document.getElementById("openFocusedClaimBtn"),
    focusedClaimText: document.getElementById("focusedClaimText"),
    selectWorkPageBtn: document.getElementById("selectWorkPageBtn"),
    selectTopWorkBtn: document.getElementById("selectTopWorkBtn"),
    clearSelectionBtn: document.getElementById("clearSelectionBtn"),
    providerSlicer: document.getElementById("providerSlicer"),
    matchSlicer: document.getElementById("matchSlicer"),
    dueSlicer: document.getElementById("dueSlicer"),
    primaryClassSlicer: document.getElementById("primaryClassSlicer"),
    secondaryClassSlicer: document.getElementById("secondaryClassSlicer"),
    agingSlicer: document.getElementById("agingSlicer"),
    actionSlicer: document.getElementById("actionSlicer"),
    primarySlicer: document.getElementById("primarySlicer"),
    secondarySlicer: document.getElementById("secondarySlicer"),
    facilitySlicer: document.getElementById("facilitySlicer"),
    procedureSlicer: document.getElementById("procedureSlicer"),
    claimStatusSlicer: document.getElementById("claimStatusSlicer"),
    serviceMonthSlicer: document.getElementById("serviceMonthSlicer"),
    patClassSlicer: document.getElementById("patClassSlicer"),
    secondaryCalcSlicer: document.getElementById("secondaryCalcSlicer"),
    claimClassSlicer: document.getElementById("claimClassSlicer"),
    networkSlicer: document.getElementById("networkSlicer"),
    clearSlicersBtn: document.getElementById("clearSlicersBtn"),
    activeSlicerText: document.getElementById("activeSlicerText"),
    sheetFormulaText: document.getElementById("sheetFormulaText"),
    sheetTotalsBar: document.getElementById("sheetTotalsBar"),
    allDataTotalsFooter: document.getElementById("allDataTotalsFooter"),
    dataSourceConfidence: document.getElementById("dataSourceConfidence"),
    workbenchFormulaAudit: document.getElementById("workbenchFormulaAudit"),
    rcmExceptionCommand: document.getElementById("rcmExceptionCommand"),
    eligibilityBenefitsCommand: document.getElementById("eligibilityBenefitsCommand"),
    rcmLifecycleCommand: document.getElementById("rcmLifecycleCommand"),
    authorizationUtilizationCommand: document.getElementById("authorizationUtilizationCommand"),
    timelyFilingCommand: document.getElementById("timelyFilingCommand"),
    workbenchStatus: document.getElementById("workbenchStatus"),
    bulkAction: document.getElementById("bulkAction"),
    bulkActionDate: document.getElementById("bulkActionDate"),
    bulkNote: document.getElementById("bulkNote"),
    bulkNoteScope: document.getElementById("bulkNoteScope"),
    bulkFollowUpBtn: document.getElementById("bulkFollowUpBtn"),
    bulkConfirmField: document.getElementById("bulkConfirmField"),
    bulkConfirmLabel: document.getElementById("bulkConfirmLabel"),
    bulkConfirmCount: document.getElementById("bulkConfirmCount"),
    bulkConfirmMessage: document.getElementById("bulkConfirmMessage"),
    selectionCount: document.getElementById("selectionCount"),
    selectAllVisible: document.getElementById("selectAllVisible"),
    workbenchHeader: document.getElementById("workbenchHeader"),
    claimRows: document.getElementById("claimRows"),
    trackerTableWrap: document.getElementById("trackerTableWrap"),
    topGridScroll: document.getElementById("topGridScroll"),
    topGridScrollInner: document.getElementById("topGridScrollInner"),
    prevTrackerPageBtn: document.getElementById("prevTrackerPageBtn"),
    nextTrackerPageBtn: document.getElementById("nextTrackerPageBtn"),
    trackerPageText: document.getElementById("trackerPageText"),
    trackerPageSizeSelect: document.getElementById("trackerPageSizeSelect"),
    trackerPageNumberInput: document.getElementById("trackerPageNumberInput"),
    columnViewButton: document.getElementById("columnViewButton"),
    columnFilterMenu: document.getElementById("columnFilterMenu"),
    feeScheduleColumnFilterMenu: document.getElementById("feeScheduleColumnFilterMenu"),
    offshoreQueue: document.getElementById("offshoreQueue"),
    reviewQueue: document.getElementById("reviewQueue"),
    clientSelect: document.getElementById("clientSelect"),
    clientStateFilter: document.getElementById("clientStateFilter"),
    clientInsuranceFilter: document.getElementById("clientInsuranceFilter"),
    clientMetrics: document.getElementById("clientMetrics"),
    clientFormulaReconciliation: document.getElementById("clientFormulaReconciliation"),
    clientAging: document.getElementById("clientAging"),
    clientMonthlySummary: document.getElementById("clientMonthlySummary"),
    clientCharts: document.getElementById("clientCharts"),
    clientRcmCockpit: document.getElementById("clientRcmCockpit"),
    clientEvidenceCenter: document.getElementById("clientEvidenceCenter"),
    clientClaimRows: document.getElementById("clientClaimRows"),
    statementPrintFilter: document.getElementById("statementPrintFilter"),
    statementRows: document.getElementById("statementRows"),
    clientActionRows: document.getElementById("clientActionRows"),
    secondaryPlaceholderRows: document.getElementById("secondaryPlaceholderRows"),
    portalHomeButton: document.getElementById("portalHomeButton"),
    reportTenantFilter: document.getElementById("reportTenantFilter"),
    reportPayerFilter: document.getElementById("reportPayerFilter"),
    reportProviderFilter: document.getElementById("reportProviderFilter"),
    reportLocationFilter: document.getElementById("reportLocationFilter"),
    reportAgingFilter: document.getElementById("reportAgingFilter"),
    reportStatusFilter: document.getElementById("reportStatusFilter"),
    reportMatchFilter: document.getElementById("reportMatchFilter"),
    reportFollowUpDueFilter: document.getElementById("reportFollowUpDueFilter"),
    reportActionFilter: document.getElementById("reportActionFilter"),
    reportServiceMonthFilter: document.getElementById("reportServiceMonthFilter"),
    reportProcedureFilter: document.getElementById("reportProcedureFilter"),
    reportNetworkFilter: document.getElementById("reportNetworkFilter"),
    reportFromDate: document.getElementById("reportFromDate"),
    reportFromDateLabel: document.getElementById("reportFromDateLabel"),
    reportToDate: document.getElementById("reportToDate"),
    reportToDateLabel: document.getElementById("reportToDateLabel"),
    clearReportFiltersBtn: document.getElementById("clearReportFiltersBtn"),
    reportFilterDisclosure: document.getElementById("reportFilterDisclosure"),
    reportFilterGrid: document.getElementById("reportFilterGrid"),
    reportFilterToggleBtn: document.getElementById("reportFilterToggleBtn"),
    reportFilterActiveCount: document.getElementById("reportFilterActiveCount"),
    reportFilterSummaryText: document.getElementById("reportFilterSummaryText"),
    reportsBody: document.getElementById("reportsBody"),
    reportSummary: document.getElementById("reportSummary"),
    reportAging: document.getElementById("reportAging"),
    reportRisks: document.getElementById("reportRisks"),
    reportCharts: document.getElementById("reportCharts"),
    reportChartsMeta: document.getElementById("reportChartsMeta"),
    previewReportPackageBtn: document.getElementById("previewReportPackageBtn"),
    trackerExportTenant: document.getElementById("trackerExportTenant"),
    trackerExportFromMonth: document.getElementById("trackerExportFromMonth"),
    trackerExportToMonth: document.getElementById("trackerExportToMonth"),
    previewTrackerWorkbookBtn: document.getElementById("previewTrackerWorkbookBtn"),
    downloadTrackerWorkbookBtn: document.getElementById("downloadTrackerWorkbookBtn"),
    trackerWorkbookExportStatus: document.getElementById("trackerWorkbookExportStatus"),
    reportFilterPanel: document.getElementById("reportFilterPanel"),
    clientReportsHost: document.getElementById("clientReportsHost"),
    clientTrackerExportTenant: document.getElementById("clientTrackerExportTenant"),
    clientTrackerExportFromMonth: document.getElementById("clientTrackerExportFromMonth"),
    clientTrackerExportToMonth: document.getElementById("clientTrackerExportToMonth"),
    clientPreviewTrackerWorkbookBtn: document.getElementById("clientPreviewTrackerWorkbookBtn"),
    clientDownloadTrackerWorkbookBtn: document.getElementById("clientDownloadTrackerWorkbookBtn"),
    openTrackerTeamSpaceBtn: document.getElementById("openTrackerTeamSpaceBtn"),
    clientTrackerWorkbookExportStatus: document.getElementById("clientTrackerWorkbookExportStatus"),
    trackerExportSlicers: document.getElementById("trackerExportSlicers"),
    trackerExportActiveChips: document.getElementById("trackerExportActiveChips"),
    trackerExportClearSlicersBtn: document.getElementById("trackerExportClearSlicersBtn"),
    reportExportPackagePreview: document.getElementById("reportExportPackagePreview"),
    reportProviderRows: document.getElementById("reportProviderRows"),
    reportUnderpaymentRows: document.getElementById("reportUnderpaymentRows"),
    reportDenialTrendRows: document.getElementById("reportDenialTrendRows"),
    reportTrendRows: document.getElementById("reportTrendRows"),
    reportCashFlowRows: document.getElementById("reportCashFlowRows"),
    reportPayerBehaviorRows: document.getElementById("reportPayerBehaviorRows"),
    reportPayerRows: document.getElementById("reportPayerRows"),
    reportTenantRows: document.getElementById("reportTenantRows"),
    reportRiskRows: document.getElementById("reportRiskRows"),
    reportDenialRows: document.getElementById("reportDenialRows"),
    reportPayerWorklistRows: document.getElementById("reportPayerWorklistRows"),
    reportStatusQueueRows: document.getElementById("reportStatusQueueRows"),
    reportQueueDrilldown: document.getElementById("reportQueueDrilldown"),
    reportWorkQueuePanel: document.getElementById("reportWorkQueuePanel"),
    reportWorkQueueLanes: document.getElementById("reportWorkQueueLanes"),
    reportWorkQueueQuickFilters: document.getElementById("reportWorkQueueQuickFilters"),
    reportOwnerRows: document.getElementById("reportOwnerRows"),
    reportSlaRows: document.getElementById("reportSlaRows"),
    sidebarToggleBtn: document.getElementById("sidebarToggleBtn"),
    sidebarCloseBtn: document.getElementById("sidebarCloseBtn"),
    feeScheduleRows: document.getElementById("feeScheduleRows"),
    feeScheduleStats: document.getElementById("feeScheduleStats"),
    feeScheduleOrgFilter: document.getElementById("feeScheduleOrgFilter"),
    feeScheduleOrgIdFilter: document.getElementById("feeScheduleOrgIdFilter"),
    feeSchedulePayerFilter: document.getElementById("feeSchedulePayerFilter"),
    feeScheduleStateFilter: document.getElementById("feeScheduleStateFilter"),
    feeScheduleProcedureFilter: document.getElementById("feeScheduleProcedureFilter"),
    feeScheduleNetworkFilter: document.getElementById("feeScheduleNetworkFilter"),
    feeScheduleActiveDateFilter: document.getElementById("feeScheduleActiveDateFilter"),
    feeScheduleSearchInput: document.getElementById("feeScheduleSearchInput"),
    feeScheduleShowArchived: document.getElementById("feeScheduleShowArchived"),
    feeScheduleFilterSummary: document.getElementById("feeScheduleFilterSummary"),
    feeScheduleCoveragePanel: document.getElementById("feeScheduleCoveragePanel"),
    feeScheduleMissingPanel: document.getElementById("feeScheduleMissingPanel"),
    missingRatesWorklistPane: document.getElementById("missingRatesWorklistPane"),
    missingRatesParentsPane: document.getElementById("missingRatesParentsPane"),
    missingRatesRefreshBtn: document.getElementById("missingRatesRefreshBtn"),
    openMissingRatesFromFeeSchedules: document.getElementById("openMissingRatesFromFeeSchedules"),
    feeParentMatchPanel: document.getElementById("feeParentMatchPanel"),
    feeParentMappingsPanel: document.getElementById("feeParentMappingsPanel"),
    clearFeeScheduleFiltersBtn: document.getElementById("clearFeeScheduleFiltersBtn"),
    exportFeeScheduleBtn: document.getElementById("exportFeeScheduleBtn"),
    downloadFeeScheduleTemplateBtn: document.getElementById("downloadFeeScheduleTemplateBtn"),
    importFeeScheduleCsvBtn: document.getElementById("importFeeScheduleCsvBtn"),
    bulkEditFeeScheduleBtn: document.getElementById("bulkEditFeeScheduleBtn"),
    feeScheduleBulkPanel: document.getElementById("feeScheduleBulkPanel"),
    feeScheduleBulkField: document.getElementById("feeScheduleBulkField"),
    feeScheduleBulkValueHost: document.getElementById("feeScheduleBulkValueHost"),
    feeScheduleBulkApplyBtn: document.getElementById("feeScheduleBulkApplyBtn"),
    feeScheduleBulkCancelBtn: document.getElementById("feeScheduleBulkCancelBtn"),
    feeScheduleBulkStatus: document.getElementById("feeScheduleBulkStatus"),
    feeScheduleCsvFile: document.getElementById("feeScheduleCsvFile"),
    payerRulesReadinessPanel: document.getElementById("payerRulesReadinessPanel"),
    contractVariancePanel: document.getElementById("contractVariancePanel"),
    followUpRuleRows: document.getElementById("followUpRuleRows"),
    followUpRuleStats: document.getElementById("followUpRuleStats"),
    followUpRulebookRoot: document.getElementById("followUpRulebookRoot"),
    followUpLocalEditor: document.getElementById("followUpLocalEditor"),
    queryMapRoot: document.getElementById("queryMapRoot"),
    addFeeRowBtn: document.getElementById("addFeeRowBtn"),
    feeRateModal: document.getElementById("feeRateModal"),
    feeRateModalCloseBtn: document.getElementById("feeRateModalCloseBtn"),
    feeRateForm: document.getElementById("feeRateForm"),
    addFollowUpRuleBtn: document.getElementById("addFollowUpRuleBtn"),
    securitySummary: document.getElementById("securitySummary"),
    deploymentReadinessPanel: document.getElementById("deploymentReadinessPanel"),
    tenantBoundaryReview: document.getElementById("tenantBoundaryReview"),
    accessOperatingModel: document.getElementById("accessOperatingModel"),
    securityOrgFilter: document.getElementById("securityOrgFilter"),
    securityDecisionFilter: document.getElementById("securityDecisionFilter"),
    securityPermissionFilter: document.getElementById("securityPermissionFilter"),
    securityFromDate: document.getElementById("securityFromDate"),
    securityToDate: document.getElementById("securityToDate"),
    authorizationFailureRows: document.getElementById("authorizationFailureRows"),
    accessDecisionRows: document.getElementById("accessDecisionRows"),
    permissionMatrixRows: document.getElementById("permissionMatrixRows"),
    permissionGroupRows: document.getElementById("permissionGroupRows"),
    accessTargetUser: document.getElementById("accessTargetUser"),
    accessTargetRole: document.getElementById("accessTargetRole"),
    accessPermissionKey: document.getElementById("accessPermissionKey"),
    accessScopeOrg: document.getElementById("accessScopeOrg"),
    accessScopeClient: document.getElementById("accessScopeClient"),
    accessAllowCrossTenant: document.getElementById("accessAllowCrossTenant"),
    accessGrantExpiry: document.getElementById("accessGrantExpiry"),
    accessReason: document.getElementById("accessReason"),
    accessApproverOne: document.getElementById("accessApproverOne"),
    accessApproverTwo: document.getElementById("accessApproverTwo"),
    accessUserOptions: document.getElementById("accessUserOptions"),
    accessPermissionOptions: document.getElementById("accessPermissionOptions"),
    previewGrantBtn: document.getElementById("previewGrantBtn"),
    previewOverrideBtn: document.getElementById("previewOverrideBtn"),
    accessAdminPreviewRows: document.getElementById("accessAdminPreviewRows"),
    workflowSyncStatus: document.getElementById("workflowSyncStatus"),
    reportRunTenantFilter: document.getElementById("reportRunTenantFilter"),
    reportRunOrgFilter: document.getElementById("reportRunOrgFilter"),
    reportRunFromDate: document.getElementById("reportRunFromDate"),
    reportRunToDate: document.getElementById("reportRunToDate"),
    reportRunExportOnly: document.getElementById("reportRunExportOnly"),
    reportRunDeniedOnly: document.getElementById("reportRunDeniedOnly"),
    reportRunRedactedOnly: document.getElementById("reportRunRedactedOnly"),
    exportJobKindFilter: document.getElementById("exportJobKindFilter"),
    previewReportRunExportBtn: document.getElementById("previewReportRunExportBtn"),
    previewQueueWorkExportBtn: document.getElementById("previewQueueWorkExportBtn"),
    previewActivityExportBtn: document.getElementById("previewActivityExportBtn"),
    reportRunExportPreview: document.getElementById("reportRunExportPreview"),
    queueWorkExportPreview: document.getElementById("queueWorkExportPreview"),
    exportGovernanceActivityExportPreview: document.getElementById("exportGovernanceActivityExportPreview"),
    exportGovernanceActivityTrail: document.getElementById("exportGovernanceActivityTrail"),
    reportExportJobTrail: document.getElementById("reportExportJobTrail"),
    claimQueueWorkAuditTrail: document.getElementById("claimQueueWorkAuditTrail"),
    reportRunTrail: document.getElementById("reportRunTrail"),
    workflowCommandTrail: document.getElementById("workflowCommandTrail"),
    auditTrail: document.getElementById("auditTrail"),
    trailPersonFilter: document.getElementById("trailPersonFilter"),
    trailOrgFilter: document.getElementById("trailOrgFilter"),
    trailPresetFilter: document.getElementById("trailPresetFilter"),
    trailFromDate: document.getElementById("trailFromDate"),
    trailToDate: document.getElementById("trailToDate"),
    trailActionFilter: document.getElementById("trailActionFilter"),
    trailSearchInput: document.getElementById("trailSearchInput"),
    trailChangesOnly: document.getElementById("trailChangesOnly"),
    trailBanner: document.getElementById("trailBanner"),
    trailCategoryChips: document.getElementById("trailCategoryChips"),
    trailCountLine: document.getElementById("trailCountLine"),
    trailPeople: document.getElementById("trailPeople"),
    trailOrgs: document.getElementById("trailOrgs"),
    trailEvents: document.getElementById("trailEvents"),
    refreshTrailBtn: document.getElementById("refreshTrailBtn"),
    exportTrailBtn: document.getElementById("exportTrailBtn"),
    openTicketBtn: document.getElementById("openTicketBtn"),
    clientFeedbackBtn: document.getElementById("clientFeedbackBtn"),
    feedbackModal: document.getElementById("feedbackModal"),
    feedbackModalCloseBtn: document.getElementById("feedbackModalCloseBtn"),
    feedbackTicketForm: document.getElementById("feedbackTicketForm"),
    feedbackTicketCategory: document.getElementById("feedbackTicketCategory"),
    feedbackTicketSubject: document.getElementById("feedbackTicketSubject"),
    feedbackTicketMessage: document.getElementById("feedbackTicketMessage"),
    feedbackTicketStatus: document.getElementById("feedbackTicketStatus"),
    feedbackTicketSubmitBtn: document.getElementById("feedbackTicketSubmitBtn"),
    refreshFeedbackBtn: document.getElementById("refreshFeedbackBtn"),
    feedbackNotificationEmailLabel: document.getElementById("feedbackNotificationEmailLabel"),
    feedbackNotificationEmail: document.getElementById("feedbackNotificationEmail"),
    saveFeedbackEmailBtn: document.getElementById("saveFeedbackEmailBtn"),
    feedbackAdminStatus: document.getElementById("feedbackAdminStatus"),
    feedbackAdminRows: document.getElementById("feedbackAdminRows"),
    workflowNotificationsBtn: document.getElementById("workflowNotificationsBtn"),
    workflowNotificationsBadge: document.getElementById("workflowNotificationsBadge"),
    workflowNotificationsModal: document.getElementById("workflowNotificationsModal"),
    workflowNotificationsCloseBtn: document.getElementById("workflowNotificationsCloseBtn"),
    workflowNotificationsOrg: document.getElementById("workflowNotificationsOrg"),
    workflowNotificationsMarkReadBtn: document.getElementById("workflowNotificationsMarkReadBtn"),
    workflowNotificationsStatus: document.getElementById("workflowNotificationsStatus"),
    workflowNotificationsList: document.getElementById("workflowNotificationsList"),
    workflowNotificationsCount: document.getElementById("workflowNotificationsCount"),
    workflowNotificationsLoadOlderBtn: document.getElementById("workflowNotificationsLoadOlderBtn"),
    claimDrawer: document.getElementById("claimDrawer"),
    drawerOverlay: document.getElementById("drawerOverlay"),
    authorizationsBody: document.getElementById("authorizationsBody"),
    credentialingBody: document.getElementById("credentialingBody"),
    authDrawer: document.getElementById("authDrawer"),
    authDrawerOverlay: document.getElementById("authDrawerOverlay"),
    authDrawerBody: document.getElementById("authDrawerBody"),
    drawerKicker: document.getElementById("drawerKicker"),
    drawerTitle: document.getElementById("drawerTitle"),
    drawerPatient: document.getElementById("drawerPatient"),
    drawerBody: document.getElementById("drawerBody"),
    followUpEditorModal: document.getElementById("followUpEditorModal"),
    followUpEditorForm: document.getElementById("followUpEditorForm"),
    followUpEditorAction: document.getElementById("followUpEditorAction"),
    followUpEditorDate: document.getElementById("followUpEditorDate"),
    followUpEditorNote: document.getElementById("followUpEditorNote"),
    followUpEditorNoteCount: document.getElementById("followUpEditorNoteCount"),
    followUpEditorContext: document.getElementById("followUpEditorContext"),
    followUpEditorStatus: document.getElementById("followUpEditorStatus"),
    followUpEditorSubmitBtn: document.getElementById("followUpEditorSubmitBtn"),
    followUpEditorCloseBtn: document.getElementById("followUpEditorCloseBtn"),
    claimNoteMutationModal: document.getElementById("claimNoteMutationModal"),
    claimNoteMutationForm: document.getElementById("claimNoteMutationForm"),
    claimNoteMutationTitle: document.getElementById("claimNoteMutationTitle"),
    claimNoteMutationContext: document.getElementById("claimNoteMutationContext"),
    claimNoteMutationTextLabel: document.getElementById("claimNoteMutationTextLabel"),
    claimNoteMutationText: document.getElementById("claimNoteMutationText"),
    claimNoteMutationReason: document.getElementById("claimNoteMutationReason"),
    claimNoteMutationStatus: document.getElementById("claimNoteMutationStatus"),
    claimNoteMutationSubmitBtn: document.getElementById("claimNoteMutationSubmitBtn"),
    claimNoteMutationCloseBtn: document.getElementById("claimNoteMutationCloseBtn"),
    closeDrawerBtn: document.getElementById("closeDrawerBtn"),
    resetDemoBtn: document.getElementById("resetDemoBtn"),
    importBtn: document.getElementById("importBtn"),
    importFile: document.getElementById("importFile"),
    exportBtn: document.getElementById("exportBtn"),
    logoutBtn: document.getElementById("logoutBtn"),
    dataSourceName: document.getElementById("dataSourceName"),
    dataSourceDetail: document.getElementById("dataSourceDetail"),
    toast: document.getElementById("toast")
  });
}

function seedControls() {
  // Seed filter options from scope-filtered claims so a scoped login never
  // sees other companies' org IDs, providers, or payers in dropdowns.
  const scopedClaims = getClaims();
  seedSelect(els.tenantFilter, allDataTenantClientValues());
  seedSelect(els.providerFilter, uniqueValues(scopedClaims, "renderingProvider"));
  seedSelect(els.primaryFilter, uniqueValues(scopedClaims, "primaryPayer"));
  seedSelect(els.secondaryFilter, uniqueValues(scopedClaims, "secondaryPayer"));
  seedSelect(els.claimStatusFilter, uniqueValues(scopedClaims, "claimStatus"));
  seedSelect(els.clientSelect, allDataTenantClientValues());
  seedSelect(els.clientStateFilter, clientStateValues(scopedClaims));
  seedSelect(els.clientInsuranceFilter, clientInsuranceValues(scopedClaims));
  seedSelect(els.reportRunTenantFilter, allDataTenantClientValues());
  seedSelect(els.reportRunOrgFilter, uniqueValues(scopedClaims, "orgId"));
  seedSelect(els.securityOrgFilter, uniqueValues(scopedClaims, "orgId"));
  seedTrackerExportControls();

  // Filter lists the WHOLE matrix (a biller must be able to filter to the system-added
  // "Claim Status Checkup Required" work); bulk-apply only offers what a human may set.
  replaceSelectOptions(els.actionFilter, getActionRules().map((rule) => rule.action));
  getSelectableActionRules().forEach((rule) => {
    if (els.bulkAction) {
      const bulkOption = document.createElement("option");
      bulkOption.value = rule.action;
      bulkOption.textContent = followUpActionChoiceLabel(rule.action);
      els.bulkAction.appendChild(bulkOption);
    }
  });

  if (els.bulkAction) {
    // The backend rejects empty actions, so clearing rides the explicit
    // "None" sentinel exposed here as a labelled Clear follow-up choice.
    const clearSeparator = document.createElement("option");
    clearSeparator.disabled = true;
    clearSeparator.textContent = "────────";
    els.bulkAction.appendChild(clearSeparator);
    const clearOption = document.createElement("option");
    clearOption.value = "None";
    clearOption.textContent = "— Clear follow-up";
    els.bulkAction.appendChild(clearOption);
  }

  if (els.ownerFilter) {
    ["Unassigned", ...users.map((user) => user.name)].forEach((owner) => {
      const option = document.createElement("option");
      option.value = owner;
      option.textContent = owner;
      els.ownerFilter.appendChild(option);
    });
  }
}

function seedSelect(select, values) {
  // Guard: some <select>s may have been removed from the HTML (e.g. when
  // the Client View was replaced with the legacy workbook pivot). seedSelect must
  // not crash if the element is missing — just skip.
  if (!select) return;
  values.forEach((value) => {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = value;
    select.appendChild(option);
  });
}

function seedTrackerExportControls() {
  trackerExportTenantControls().forEach((control) => seedSelect(control, allDataTenantClientValues()));
  // Empty dates mean the whole book. The old cockpit silently defaulted to
  // the last 90 days, which made "export the tracker" a surprise slice.
  syncTrackerExportControls(primaryTrackerExportControlSet());
}

function trackerExportTenantControls() {
  return [els.trackerExportTenant, els.clientTrackerExportTenant].filter(Boolean);
}

function trackerExportFromDateControls() {
  return [els.trackerExportFromMonth, els.clientTrackerExportFromMonth].filter(Boolean);
}

function trackerExportToDateControls() {
  return [els.trackerExportToMonth, els.clientTrackerExportToMonth].filter(Boolean);
}

function trackerExportControlSets() {
  return [
    {
      tenant: els.trackerExportTenant,
      fromDate: els.trackerExportFromMonth,
      toDate: els.trackerExportToMonth
    },
    {
      tenant: els.clientTrackerExportTenant,
      fromDate: els.clientTrackerExportFromMonth,
      toDate: els.clientTrackerExportToMonth
    }
  ].filter((set) => set.tenant || set.fromDate || set.toDate);
}

// ONE STUDIO IN THE DOM. The whole-book LITE export used to sit under every
// Reports tab (and before that, under two cockpits). It now lives only on
// Export Tracker. These helpers still accept the retired Reports-control ids
// so old tests can drive the same tenant/date fields.
function primaryTrackerExportControlSet() {
  return {
    tenant: els.trackerExportTenant || els.clientTrackerExportTenant,
    fromDate: els.trackerExportFromMonth || els.clientTrackerExportFromMonth,
    toDate: els.trackerExportToMonth || els.clientTrackerExportToMonth
  };
}

function syncTrackerExportControls(source = primaryTrackerExportControlSet()) {
  const tenant = source?.tenant?.value || "All";
  const fromDate = source?.fromDate?.value || "";
  const toDate = source?.toDate?.value || "";
  trackerExportControlSets().forEach((set) => {
    if (set.tenant && set.tenant.value !== tenant) set.tenant.value = tenant;
    if (set.fromDate && set.fromDate.value !== fromDate) set.fromDate.value = fromDate;
    if (set.toDate && set.toDate.value !== toDate) set.toDate.value = toDate;
  });
}

function bindEvents() {
  els.roleSelect.value = state.role;
  els.rowModeSelect.value = state.rowMode || "service";
  els.roleSelect.addEventListener("change", () => {
    cancelClaimScreenshot();
    closeTransientOverlays();
    state.role = els.roleSelect.value;
    // Reset active user when role changes — pick the first user of that role.
    const firstOfRole = users.find((u) => canonicalRole(u.role) === canonicalRole(state.role));
    state.activeUserName = firstOfRole?.name || null;
    state.view = firstAccessibleView();
    if (!hasPermission(PERMISSIONS.ASSIGN_CLAIMS)) state.selectedClaims = [];
    if (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) {
      state.rowMode = "service";
    }
    audit(`${state.role} role selected`, "Local User");
    saveState();
    populateUserSelect();
    render();
    updateActingAsBanner();
    updateUserScopeHint();
    updateDataSourceBox();
  });
  populateUserSelect();
  updateUserScopeHint();
  updateDataSourceBox();
  // Hydrate any user-added users from localStorage so they survive reload.
  hydrateCustomUsers();
  // Wire the "+ Manage users" modal — SuperAdmin can name new offshore
  // (or stateside) billers; names persist across reloads. Each new
  // name shows up in the bulk-assign 'Assign to' dropdown automatically.
  document.getElementById("manageUsersBtn")?.addEventListener("click", openUserMgmtModal);
  // Delegate tab clicks from the modal shell. The directory refresh replaces
  // parts of Manage Users while it is open; delegation keeps People / Teams
  // clickable even when a refreshed pane has replaced the original button.
  const userMgmtModal = document.getElementById("userMgmtModal");
  userMgmtModal?.addEventListener("click", (event) => {
    if (event.target === userMgmtModal) {
      closeUserMgmtModal();
      return;
    }
    const tab = event.target?.closest?.("[data-user-mgmt-tab]");
    if (!tab || !userMgmtModal.contains(tab)) return;
    setUserMgmtTab(tab.dataset.userMgmtTab);
  });
  // Listen at the document boundary because Teams and Reporting lines replace
  // their inner controls after a save. The previously focused button can be
  // detached during that render; Escape and the focus trap must still work.
  document.addEventListener("keydown", handleUserMgmtModalKeydown);
  // The API-backed grid replaces its rows after each server response. Keep the
  // claim-detail action on the stable tbody so a refreshed Open button never
  // loses its click handler.
  els.claimRows?.addEventListener("click", (event) => {
    const button = event.target?.closest?.("[data-open]");
    if (!button || !els.claimRows.contains(button)) return;
    openDrawer(button.dataset.open, {
      serviceId: button.dataset.serviceId || "",
      orgId: button.dataset.orgId || button.closest?.("[data-row-org-id]")?.dataset?.rowOrgId || ""
    });
  });
  // D3 S4 - Manage Groups (CLIENT groups). Same gate, same chrome, sibling modal.
  document.getElementById("manageGroupsBtn")?.addEventListener("click", openUserGroupMgmtModal);
  document.getElementById("emailNotificationsBtn")?.addEventListener("click", openEmailNotificationsModal);
  document.getElementById("clientApiAccessBtn")?.addEventListener("click", openClientApiAccessModal);
  document.getElementById("userGroupMgmtCloseBtn")?.addEventListener("click", closeUserGroupMgmtModal);
  document.getElementById("userGroupMgmtModal")?.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeUserGroupMgmtModal();
  });
  Array.from(document.querySelectorAll("[data-group-tab]")).forEach((tab) => {
    tab.addEventListener("click", () => userGroupMgmtSetTab(tab.dataset.groupTab));
  });
  document.getElementById("userGroupMgmtBody")?.addEventListener("click", handleUserGroupMgmtBodyClick);
  document.getElementById("userGroupMgmtBody")?.addEventListener("input", handleUserGroupMgmtBodyInput);
  document.getElementById("userGroupMgmtBody")?.addEventListener("keydown", handleUserGroupMgmtBodyKeydown);
  if (!window.__tmsClientReportHistoryBound) {
    window.__tmsClientReportHistoryBound = true;
    window.addEventListener("popstate", (event) => {
      // The Add User wizard owns Back while it is open: it steps back through its
      // own screens and then leaves to Manage Users. Repainting the portal
      // underneath a full-screen dialog is not navigation the user asked for.
      if (handleUserAddWizardPopstate(event)) return;
      denialIntelligenceHideVisibleHistory();
      cancelClaimScreenshot();
      applyUrlState({ seedFilters: false });
      render();
    });
  }
  document.getElementById("userMgmtCloseBtn")?.addEventListener("click", closeUserMgmtModal);
  document.querySelectorAll("[data-user-mgmt-view]").forEach((tab) => {
    tab.addEventListener("click", () => setUserMgmtView(tab.dataset.userMgmtView));
  });
  document.getElementById("userMgmtTeamView")?.addEventListener("change", handleTeamStructureChange);
  document.getElementById("userMgmtTeamView")?.addEventListener("input", handleTeamStructureSearchInput);
  document.getElementById("userMgmtTeamView")?.addEventListener("click", handleTeamStructureClick);
  ["userMgmtTeamsPane", "userMgmtTeamView"].forEach((id) => {
    document.getElementById(id)?.addEventListener("keydown", handleUserMgmtScrollablePaneKeydown);
  });
  document.getElementById("userMgmtSearch")?.addEventListener("input", handlePeopleDirectorySearch);
  document.getElementById("userMgmtExportBtn")?.addEventListener("click", exportUserMgmtDirectory);
  document.getElementById("userMgmtNewInviteBtn")?.addEventListener("click", openUserAddWizard);
  // Both exits, and Escape above, go through the SAME gate — so the discard
  // confirmation cannot be true of one of them and not the others.
  document.getElementById("userAddWizardCloseBtn")?.addEventListener("click", () => requestUserAddWizardExit());
  document.getElementById("userAddWizardCancelBtn")?.addEventListener("click", () => requestUserAddWizardExit());
  document.getElementById("userAddWizardBackBtn")?.addEventListener("click", userAddWizardBack);
  document.getElementById("userAddWizardNextBtn")?.addEventListener("click", advanceUserAddWizard);

  els.openTicketBtn?.addEventListener("click", openFeedbackModal);
  els.clientFeedbackBtn?.addEventListener("click", openFeedbackModal);
  els.feedbackModalCloseBtn?.addEventListener("click", closeFeedbackModal);
  Array.from(document.querySelectorAll("[data-feedback-close]")).forEach((button) => {
    button.addEventListener("click", closeFeedbackModal);
  });
  els.feedbackTicketForm?.addEventListener("submit", submitFeedbackTicket);
  els.refreshFeedbackBtn?.addEventListener("click", () => refreshFeedbackAdmin({ force: true }));
  els.saveFeedbackEmailBtn?.addEventListener("click", saveFeedbackNotificationEmail);
  els.feedbackAdminRows?.addEventListener("change", (event) => {
    const control = event.target?.closest?.("[data-feedback-ticket-status]");
    if (!control) return;
    updateFeedbackTicketStatus(control.dataset.feedbackTicketStatus, control.value);
  });
  Array.from(document.querySelectorAll("[data-feedback-status-filter]")).forEach((button) => {
    button.addEventListener("click", () => {
      feedbackAdminState.status = button.dataset.feedbackStatusFilter || "all";
      renderFeedbackStatusTabs();
      refreshFeedbackAdmin({ force: true });
    });
  });
  els.userSelect?.addEventListener("change", () => {
    cancelClaimScreenshot();
    state.activeUserName = els.userSelect.value || null;
    audit(`Switched active user to ${state.activeUserName}`, "Local User");
    saveState();
    render();
    updateUserScopeHint();
  });
  bindTopbarActionMenu();



  els.rowModeSelect.addEventListener("change", () => {
    state.rowMode = els.rowModeSelect.value;
    state.columnFilters = columnFiltersForRowMode(state.rowMode);
    state.columnFilterRanges = columnFilterRangesForRowMode(state.rowMode);
    resetTrackerPage();
    saveState();
    renderClaimsTable();
  });

  els.navItems.forEach((item) => {
    item.addEventListener("click", () => {
      // Action entries (Manage users) open a modal from their own listener and
      // have no view to route to; navigateToView(undefined) would deny-toast.
      if (item.dataset.navAction) return;
      navigateToView(item.dataset.view);
    });
  });
  els.portalHomeButton?.addEventListener("click", navigateToHome);

  // UI-100 #72: debounce broad search and min balance separately from exact
  // identifiers, which settle briefly or apply immediately on Enter.
  // A RENDER CAN BEAT THE DEBOUNCE (2026-10-14). The page query reads the box
  // directly, so any render in the 160 ms window (filter lists, timers) already
  // sent this exact query; resetting then aborted or wiped it and sent it again,
  // and under load the grid flashed back to "Loading rows" after the answer had
  // painted. Page 1 of the same query, answered or in flight, is left alone.
  const searchAlreadyCurrent = () => {
    if (typeof trackerApiEnabled !== "function" || !trackerApiEnabled() || Number(state.trackerPage || 1) !== 1) return false;
    const query = buildTrackerQueryRequest(state.rowMode || "service");
    return trackerApiPageState.key === trackerApiCacheKey(query) && ["ok", "pending"].includes(trackerApiPageState.status);
  };
  const debouncedRerender = (() => {
    let t = null;
    return () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { if (!searchAlreadyCurrent()) resetTrackerPage(); renderClaimsTable(); }, 160);
    };
  })();
  const immediateRerender = () => { resetTrackerPage(); renderClaimsTable(); };
  ["trackerServiceDateFrom", "trackerServiceDateTo"].forEach(id => {
    document.getElementById(id)?.addEventListener("change", () => {
      const range = trackerServiceDateRange();
      const message = range.invalid ? "Enter valid MM/DD/YYYY dates with From on or before To." : "";
      document.getElementById("trackerServiceDateError").textContent = message;
      ["trackerServiceDateFrom", "trackerServiceDateTo"].forEach(key => document.getElementById(key).setCustomValidity(message));
      if (range.invalid) return;
      immediateRerender();
    });
  });
  [els.searchInput, els.noteSearchInput, els.minBalanceFilter].forEach((control) => {
    if (!control) return;
    control.addEventListener("input", debouncedRerender);
  });
  // Exact identifiers remain equality-only on the server, but the controls
  // still need to behave like filters instead of hidden keyboard commands.
  // Wait for a short typing pause so a multi-digit ID launches one indexed
  // lookup; Enter applies immediately, and clearing the search box restores
  // the full result set immediately.
  let exactIdRerenderTimer = null;
  const scheduleExactIdRerender = () => {
    if (exactIdRerenderTimer) clearTimeout(exactIdRerenderTimer);
    exactIdRerenderTimer = setTimeout(() => {
      exactIdRerenderTimer = null;
      immediateRerender();
    }, 450);
  };
  const applyExactIdFilters = () => {
    if (exactIdRerenderTimer) clearTimeout(exactIdRerenderTimer);
    exactIdRerenderTimer = null;
    immediateRerender();
  };
  const onExactIdInput = (event) => {
    const control = event.target;
    if (control !== els.serviceIdSearchInput && !control.matches?.("[data-claim-id-search]")) return;
    cancelClaimScreenshot();
    if (control !== els.serviceIdSearchInput) syncClaimIdSearchInputs();
    if (event.isComposing) return;
    if (control.value.trim()) scheduleExactIdRerender();
    else applyExactIdFilters();
  };
  const onExactIdKeydown = (event) => {
    if (event.key !== "Enter" || event.isComposing) return;
    if (event.target !== els.serviceIdSearchInput && !event.target.matches?.("[data-claim-id-search]")) return;
    event.preventDefault();
    applyExactIdFilters();
  };
  // Delegation keeps appended inputs live without replacing the focused node.
  [els.claimIdSearchList || els.claimIdSearchInput, els.serviceIdSearchInput].forEach((control) => {
    if (!control) return;
    control.addEventListener("input", onExactIdInput);
    control.addEventListener("compositionend", onExactIdInput);
    control.addEventListener("keydown", onExactIdKeydown);
  });
  els.claimIdSearchList?.addEventListener("click", (event) => {
    const button = event.target.closest?.("[data-remove-claim-id]");
    if (!button) return;
    const inputs = claimIdSearchInputs();
    const index = inputs.indexOf(button.parentElement.querySelector("[data-claim-id-search]"));
    if (index < 0) return;
    setClaimIdSearchValue(inputs.filter((_, itemIndex) => itemIndex !== index).map((input) => input.value));
    const remaining = claimIdSearchInputs();
    remaining[Math.min(index, remaining.length - 1)]?.focus();
    applyExactIdFilters();
  });
  els.clearClaimIdsBtn?.addEventListener("click", () => {
    setClaimIdSearchValue("");
    els.claimIdSearchInput?.focus();
    applyExactIdFilters();
  });
  syncClaimIdSearchInputs();
  bindClaimScreenshotInputs(applyExactIdFilters);
  [els.tenantFilter, els.providerFilter, els.primaryFilter, els.secondaryFilter, els.actionFilter, els.matchFilter, els.claimStatusFilter, els.lifecycleFilter, els.agingFilter, els.ownerFilter].forEach((control) => {
    if (!control) return;
    control.addEventListener("input", immediateRerender);
  });
  // GLOBAL CLIENT SWITCHER. One change re-scopes every page and persists.
  els.clientScopeSelect?.addEventListener("change", () => {
    setActiveClientGroup(els.clientScopeSelect.value || "");
  });
  // UI-100 #36: live validation feedback for Min Balance.
  els.minBalanceFilter?.addEventListener("input", () => {
    const v = els.minBalanceFilter.value.trim();
    const hint = document.getElementById("minBalanceHint");
    if (!hint) return;
    if (v === "") {
      hint.textContent = "Hide claims with outstanding balance below this amount.";
      hint.classList.remove("is-error");
      els.minBalanceFilter.removeAttribute("aria-invalid");
      return;
    }
    const n = parseMoneyNumber(v);
    if (!Number.isFinite(n) || n < 0) {
      hint.textContent = "Enter a positive dollar amount, or leave blank for any.";
      hint.classList.add("is-error");
      els.minBalanceFilter.setAttribute("aria-invalid", "true");
    } else {
      hint.textContent = `Hiding claims with balance below ${money(n)}.`;
      hint.classList.remove("is-error");
      els.minBalanceFilter.removeAttribute("aria-invalid");
    }
  });

  // Compact All Data filter workspace. The utility row stays visible while
  // the full filter panel remembers the user's expanded/collapsed preference.
  document.getElementById("slicerToggleBtn")?.addEventListener("click", () => {
    const board = document.getElementById("slicerBoard");
    const btn = document.getElementById("slicerToggleBtn");
    if (!board || !btn) return;
    const isOpen = !board.hasAttribute("hidden");
    if (isOpen) {
      board.setAttribute("hidden", "");
      btn.setAttribute("aria-expanded", "false");
      btn.innerHTML = 'Show filters <span class="slicer-toggle-caret" aria-hidden="true">▾</span>';
    } else {
      board.removeAttribute("hidden");
      btn.setAttribute("aria-expanded", "true");
      btn.innerHTML = 'Hide filters <span class="slicer-toggle-caret" aria-hidden="true">▴</span>';
    }
    try { localStorage.setItem("tms.portal.slicersOpen", isOpen ? "0" : "1"); } catch (_) {}
  });
  // Restore preference, and auto-open when any slicer has a selection.
  (function restoreSlicerToggle() {
    const board = document.getElementById("slicerBoard");
    const btn = document.getElementById("slicerToggleBtn");
    if (!board || !btn) return;
    let stored = null;
    try { stored = localStorage.getItem("tms.portal.slicersOpen"); } catch (_) {}
    const anyActive = Object.values(state.slicers || {}).some((v) => Array.isArray(v) && v.length);
    const shouldOpen = stored !== "0" || anyActive;
    if (shouldOpen) {
      board.removeAttribute("hidden");
      btn.setAttribute("aria-expanded", "true");
      btn.innerHTML = 'Hide filters <span class="slicer-toggle-caret" aria-hidden="true">▴</span>';
    }
  })();
  applySlicerSizePreference();

  // One Advanced Filters control owns the two advanced slicer sections. The
  // six daily workflow filters remain visible before this is opened.
  document.getElementById("moreFiltersToggle")?.addEventListener("click", () => {
    state.slicerSize = state.slicerSize === "expanded" ? "compact" : "expanded";
    applySlicerSizePreference();
    // The initial All Data request hydrates only the six visible daily filters
    // (plus queue-card totals). Opening Advanced Filters changes the requested facet
    // key, so render once here to fetch the remaining panels on demand. This
    // avoids running the expensive 23-way aggregation before anyone can see it.
    renderSlicers();
  });

  els.clearQueueBtn?.addEventListener("click", () => setQueueFilter("all"));
  // UI-100 #39: grid-mode mirror in the titlebar drives the same rowModeSelect.
  document.querySelectorAll(".grid-mode-mirror-btn[data-grid-mode]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const mode = btn.dataset.gridMode;
      els.rowModeSelect.value = mode;
      state.rowMode = mode;
      state.columnFilters = columnFiltersForRowMode(mode);
      state.columnFilterRanges = columnFilterRangesForRowMode(mode);
      resetTrackerPage();
      saveState();
      renderClaimsTable();
      updateGridModeMirror();
    });
  });
  updateGridModeMirror();
  document.getElementById("resetWorkbenchBtn")?.addEventListener("click", resetWorkbenchToAllData);
  document.getElementById("columnViewButton")?.addEventListener("click", openColumnViewModal);
  const billerFocusHost = document.getElementById("billerFocusActions");
  billerFocusHost?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-quick-focus]");
    if (button && billerFocusHost.contains(button)) {
      applyBillerFocus(button.dataset.quickFocus);
    }
  });
  els.selectWorkPageBtn?.addEventListener("click", () => selectVisibleWorkClaims("page"));
  els.selectTopWorkBtn?.addEventListener("click", () => selectVisibleWorkClaims("top10"));
  els.prevWorkClaimBtn?.addEventListener("click", () => navigateWorkClaim("previous"));
  els.nextWorkClaimBtn?.addEventListener("click", () => navigateWorkClaim("next"));
  els.openFocusedClaimBtn?.addEventListener("click", () => openFocusedWorkClaim());
  els.clearSelectionBtn?.addEventListener("click", clearWorkbenchSelection);
  Array.from(document.querySelectorAll("[data-bulk-date-days]")).forEach((button) => {
    button.addEventListener("click", () => setBulkActionDatePreset(Number(button.dataset.bulkDateDays)));
  });
  els.prevTrackerPageBtn?.addEventListener("click", () => setTrackerPage((state.trackerPage || 1) - 1));
  els.nextTrackerPageBtn?.addEventListener("click", () => setTrackerPage((state.trackerPage || 1) + 1));
  els.trackerPageSizeSelect?.addEventListener("change", (event) => setTrackerPageSize(event.target?.value));
  els.trackerPageNumberInput?.addEventListener("change", (event) => setTrackerPage(event.target?.value));
  els.trackerPageNumberInput?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setTrackerPage(event.target?.value);
      event.target?.blur?.();
    }
  });
  els.clearSlicersBtn?.addEventListener("click", clearAllTrackerFilters);
  Array.from(document.querySelectorAll("[data-slicer-clear]")).forEach((button) => {
    button.addEventListener("click", () => clearSlicerDraft(button.dataset.slicerClear));
  });
  Array.from(document.querySelectorAll("[data-slicer-search]")).forEach((input) => {
    input.addEventListener("input", () => applySlicerOptionSearch(input.dataset.slicerSearch, input.value));
  });
  Array.from(document.querySelectorAll("#slicerBoard details.slicer-group")).forEach((panel) => {
    panel.addEventListener("toggle", () => {
      const key = panel.dataset.slicerPanel || "";
      if (!panel.open) {
        if (slicerDraftKey === key) discardSlicerDraft({ returnFocus: false });
        return;
      }
      Array.from(document.querySelectorAll("#slicerBoard details.slicer-group[open]")).forEach((otherPanel) => {
        if (otherPanel !== panel) otherPanel.open = false;
      });
      beginSlicerDraft(key, panel.querySelector("summary"));
      if (trackerApiEnabled()) requestPagedSlicerChoices(key, panel.querySelector("[data-slicer-search]")?.value || "");
      panel.querySelector("[data-slicer-search]")?.focus?.();
    });
  });
  document.addEventListener("keydown", handleWorkbenchKeyboardNavigation);
  [els.clientSelect, els.clientStateFilter, els.clientInsuranceFilter, els.statementPrintFilter].forEach((control) => {
    if (!control) return;
    control.addEventListener("input", renderClientView);
    control.addEventListener("change", renderClientView);
  });
  [els.reportTenantFilter, els.reportPayerFilter, els.reportProviderFilter, els.reportLocationFilter, els.reportAgingFilter, els.reportStatusFilter, els.reportMatchFilter, els.reportFollowUpDueFilter, els.reportActionFilter, els.reportServiceMonthFilter, els.reportProcedureFilter, els.reportNetworkFilter, els.reportFromDate, els.reportToDate].forEach((control) => {
    if (!control) return;
    const changed = () => {
      saveReportFilterSelections();
      renderReports();
    };
    control.addEventListener("input", changed);
    // "change" as well, because applyDatePreset() writes .value directly and
    // then dispatches only "change" - an "input"-only binding made every date
    // quick-preset a no-op on this surface.
    control.addEventListener("change", changed);
  });
  bindReportFilterDisclosure();
  bindReportRunFilter(els.reportRunTenantFilter, "tenantId");
  bindReportRunFilter(els.reportRunOrgFilter, "orgId");
  bindReportRunFilter(els.reportRunFromDate, "fromDate");
  bindReportRunFilter(els.reportRunToDate, "toDate");
  bindReportRunFilter(els.reportRunExportOnly, "exportOnly", "checked");
  bindReportRunFilter(els.reportRunDeniedOnly, "deniedOnly", "checked");
  bindReportRunFilter(els.reportRunRedactedOnly, "redactedOnly", "checked");
  bindReportRunFilter(els.exportJobKindFilter, "exportKind");
  els.previewReportRunExportBtn?.addEventListener("click", previewReportRunAuditExport);
  els.previewQueueWorkExportBtn?.addEventListener("click", previewQueueWorkExport);
  els.previewActivityExportBtn?.addEventListener("click", previewExportGovernanceActivityExport);
  els.previewReportPackageBtn?.addEventListener("click", previewReportExportPackage);
  els.previewTrackerWorkbookBtn?.addEventListener("click", previewTrackerWorkbookExport);
  els.downloadTrackerWorkbookBtn?.addEventListener("click", downloadTrackerWorkbookExport);
  els.clientPreviewTrackerWorkbookBtn?.addEventListener("click", previewTrackerWorkbookExport);
  els.clientDownloadTrackerWorkbookBtn?.addEventListener("click", downloadTrackerWorkbookExport);
  els.openTrackerTeamSpaceBtn?.addEventListener("click", openTrackerTeamSpace);
  els.trackerExportClearSlicersBtn?.addEventListener("click", clearAllTrackerExportSlicers);
  document.querySelectorAll("[data-export-date-preset]").forEach((button) => {
    button.addEventListener("click", () => applyTrackerExportDatePreset(button.getAttribute("data-export-date-preset") || ""));
  });
  [
    els.trackerExportTenant,
    els.trackerExportFromMonth,
    els.trackerExportToMonth,
    els.clientTrackerExportTenant,
    els.clientTrackerExportFromMonth,
    els.clientTrackerExportToMonth
  ].forEach((control) => {
    if (!control) return;
    const syncAndRender = () => {
      const source = trackerExportControlSets().find((set) => set.tenant === control || set.fromDate === control || set.toDate === control);
      syncTrackerExportControls(source);
      if (state.view === "trackerExport") renderTrackerExportStudio();
      else renderTrackerWorkbookExportStatus();
    };
    control.addEventListener("input", syncAndRender);
    control.addEventListener("change", syncAndRender);
  });
  bindSecurityFilter(els.securityOrgFilter, "orgId");
  bindSecurityFilter(els.securityDecisionFilter, "decision");
  bindSecurityFilter(els.securityPermissionFilter, "permission");
  bindSecurityFilter(els.securityFromDate, "fromDate");
  bindSecurityFilter(els.securityToDate, "toDate");
  els.previewGrantBtn?.addEventListener("click", () => previewAccessAdminCommand("grant"));
  els.previewOverrideBtn?.addEventListener("click", () => previewAccessAdminCommand("override"));
  els.clearReportFiltersBtn?.addEventListener("click", clearReportFilters);
  bindFeeScheduleFilter(els.feeScheduleOrgFilter, "tenantId");
  bindFeeScheduleFilter(els.feeScheduleOrgIdFilter, "orgId");
  bindFeeScheduleFilter(els.feeSchedulePayerFilter, "payer");
  bindFeeScheduleFilter(els.feeScheduleStateFilter, "state");
  bindFeeScheduleFilter(els.feeScheduleProcedureFilter, "procedure");
  bindFeeScheduleFilter(els.feeScheduleNetworkFilter, "network");
  bindFeeScheduleFilter(els.feeScheduleActiveDateFilter, "activeDate");
  bindFeeScheduleFilter(els.feeScheduleSearchInput, "search");
  if (els.feeScheduleShowArchived) {
    els.feeScheduleShowArchived.addEventListener("change", () => {
      state.feeScheduleFilters = { ...(state.feeScheduleFilters || {}), showArchived: els.feeScheduleShowArchived.checked };
      saveState();
      renderFeeSchedules();
    });
  }
  els.clearFeeScheduleFiltersBtn?.addEventListener("click", clearFeeScheduleFilters);
  els.exportFeeScheduleBtn?.addEventListener("click", exportFilteredFeeScheduleRows);
  els.downloadFeeScheduleTemplateBtn?.addEventListener("click", downloadFeeScheduleImportTemplate);
  els.importFeeScheduleCsvBtn?.addEventListener("click", () => els.feeScheduleCsvFile?.click());
  els.feeScheduleCsvFile?.addEventListener("change", importFeeScheduleCsv);
  els.bulkEditFeeScheduleBtn?.addEventListener("click", () => toggleFeeScheduleBulkPanel());
  els.feeScheduleBulkCancelBtn?.addEventListener("click", () => toggleFeeScheduleBulkPanel(false));
  els.feeScheduleBulkField?.addEventListener("change", renderFeeScheduleBulkValueControl);
  els.feeScheduleBulkApplyBtn?.addEventListener("click", applyFeeScheduleBulkEdit);

  // UI-100 #37: bind date-range quick presets (works for any pair w/ data-date-target-*).
  document.querySelectorAll(".date-preset[data-date-preset]").forEach((btn) => {
    btn.addEventListener("click", () => applyDatePreset(btn.dataset.datePreset, btn.dataset.dateTargetFrom, btn.dataset.dateTargetTo));
  });

  els.sidebarToggleBtn.addEventListener("click", toggleSidebar);
  installSidebarTogglePlacement();
  els.sidebarCloseBtn?.addEventListener("click", () => collapseSidebar({ restoreFocus: true }));
  installResponsiveSidebarGuard();

  els.bulkFollowUpBtn?.addEventListener("click", bulkFollowUpUpdate);
  els.followUpEditorForm?.addEventListener("submit", submitFollowUpEditor);
  els.followUpEditorCloseBtn?.addEventListener("click", closeFollowUpEditor);
  Array.from(document.querySelectorAll("[data-followup-editor-close]")).forEach((node) => node.addEventListener("click", closeFollowUpEditor));
  els.followUpEditorNote?.addEventListener("input", () => setText(els.followUpEditorNoteCount, String(els.followUpEditorNote.value.length)));
  els.followUpEditorAction?.addEventListener("change", () => {
    const action = getValue(els.followUpEditorAction);
    if (!action || action === "None") setValue(els.followUpEditorDate, "");
    else setValue(els.followUpEditorDate, defaultFollowUpDateForAction(action, getValue(els.followUpEditorDate)));
  });
  els.claimNoteMutationForm?.addEventListener("submit", submitClaimNoteMutation);
  els.claimNoteMutationCloseBtn?.addEventListener("click", closeClaimNoteMutationModal);
  Array.from(document.querySelectorAll("[data-claim-note-mutation-close]")).forEach((node) => node.addEventListener("click", closeClaimNoteMutationModal));
  // Type-to-confirm guard: validate live as the user types, and let Enter
  // commit the moment the number matches so the guard costs one field and no
  // extra clicks. The gate itself is re-checked inside bulkFollowUpUpdate.
  els.bulkConfirmCount?.addEventListener("input", () => updateBulkFollowUpGuard());
  els.bulkConfirmCount?.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (bulkFollowUpConfirmState().matched) bulkFollowUpUpdate();
    else updateBulkFollowUpGuard({ focus: true });
  });
  // Switching to/from the "None" clear sentinel moves the threshold, so the
  // guard has to repaint on action changes as well as on selection changes.
  els.bulkAction?.addEventListener("change", () => updateBulkFollowUpGuard());
  els.bulkNote?.addEventListener("input", () => updateBulkFollowUpGuard());
  // Auto-populate the bulk follow-up date when a bulk action is chosen.
  // Each action rule has an `offset` (days from today). Picking an action
  // pre-fills the date — user can override to any custom date afterwards.
  els.bulkAction?.addEventListener("change", () => {
    const chosen = getValue(els.bulkAction);
    const rule = followUpRuleForAction(chosen);
    if (rule && Number.isFinite(rule.offset) && els.bulkActionDate) {
      els.bulkActionDate.value = addDays(todayDateString(), rule.offset);
    } else if (chosen === "None" && els.bulkActionDate) {
      // Clear follow-up has no rule offset; blank any stale prefilled date.
      els.bulkActionDate.value = "";
    }
  });
  els.topGridScroll?.addEventListener("scroll", () => syncGridScroll(els.topGridScroll, els.trackerTableWrap));
  els.trackerTableWrap?.addEventListener("scroll", () => syncGridScroll(els.trackerTableWrap, els.topGridScroll));
  els.addFeeRowBtn?.addEventListener("click", openAddFeeRateModal);
  els.missingRatesRefreshBtn?.addEventListener("click", () => {
    requestFeeScheduleMissingApiRows({ force: true });
    requestFeeScheduleParentMappings({ force: true });
    renderMissingRates();
  });
  els.openMissingRatesFromFeeSchedules?.addEventListener("click", () => navigateToView("missingRates"));
  Array.from(document.querySelectorAll("[data-missing-rates-tab]")).forEach((button) => {
    button.addEventListener("click", () => {
      state.missingRatesTab = button.dataset.missingRatesTab === "parents" ? "parents" : "worklist";
      saveState();
      renderMissingRates();
    });
  });
  els.feeRateModalCloseBtn?.addEventListener("click", closeAddFeeRateModal);
  els.feeRateForm?.addEventListener("submit", submitAddFeeRate);
  els.feeRateForm?.addEventListener("input", onAddFeeRateFormInput);
  els.feeRateForm?.addEventListener("change", onAddFeeRateFormChange);
  Array.from(document.querySelectorAll("[data-fee-rate-close]")).forEach((node) => {
    node.addEventListener("click", closeAddFeeRateModal);
  });
  els.addFollowUpRuleBtn?.addEventListener("click", addFollowUpRule);
  els.closeDrawerBtn?.addEventListener("click", closeDrawer);
  els.drawerOverlay?.addEventListener("click", closeDrawer);
  els.resetDemoBtn?.addEventListener("click", resetDemo);
  els.importBtn?.addEventListener("click", () => els.importFile?.click());
  els.importFile?.addEventListener("change", importWorkflowState);
  els.exportBtn?.addEventListener("click", exportState);
  els.logoutBtn?.addEventListener("click", async () => { await signOutPortalUser(); });
  els.workflowNotificationsBtn?.addEventListener("click", openWorkflowNotifications);
  els.workflowNotificationsCloseBtn?.addEventListener("click", closeWorkflowNotifications);
  Array.from(document.querySelectorAll("[data-workflow-notifications-close]")).forEach((node) => node.addEventListener("click", closeWorkflowNotifications));
  els.workflowNotificationsOrg?.addEventListener("change", () => selectWorkflowNotificationOrg(els.workflowNotificationsOrg.value));
  els.workflowNotificationsMarkReadBtn?.addEventListener("click", markShownWorkflowNotificationsRead);
  els.workflowNotificationsLoadOlderBtn?.addEventListener("click", () => requestWorkflowNotifications({ append: true }));
  els.workflowNotificationsList?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-workflow-notification-open]");
    if (button) openWorkflowNotification(button.dataset.workflowNotificationOpen || "");
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && els.workflowNotificationsModal && !els.workflowNotificationsModal.classList.contains("hidden")) closeWorkflowNotifications();
  });
  document.addEventListener("click", (event) => {
    if (!els.columnFilterMenu || els.columnFilterMenu.hidden) return;
    if (event.target.closest(".column-filter-menu") || event.target.closest("[data-column-filter]")) return;
    closeColumnFilterMenu();
  });
  // Same rule for the Fee Schedules rate-table filter menu. It is a separate
  // node from the All Data one so the two popups can never fight over one host.
  document.addEventListener("click", (event) => {
    if (!els.feeScheduleColumnFilterMenu || els.feeScheduleColumnFilterMenu.hidden) return;
    if (event.target.closest(".fee-column-filter-menu") || event.target.closest("[data-fee-column-filter]")) return;
    closeFeeScheduleColumnFilterMenu();
  });
}

// UI-100 #59: surface the active role so the user can't forget what scope they're in.
function updateActingAsBanner() {
  const banner = document.getElementById("actingAsBanner");
  if (!banner) return;
  const role = canonicalRole(state.role);
  // Keep the default SuperAdmin view quiet. Other roles identify whether this
  // is the authenticated login or a different role selected for preview.
  if (role === "SuperAdmin") { banner.classList.add("hidden"); return; }
  const signedInRole = state.authUserScope?.role ? canonicalRole(state.authUserScope.role) : "";
  // Telling people the role they signed in with says nothing (Nathan,
  // 2026-10-13). The strip only earns its room while someone previews
  // another role, where "Acting as" prevents a wrong-scope mistake.
  if (signedInRole === role) { banner.classList.add("hidden"); return; }
  banner.classList.remove("hidden");
  const eyebrow = banner.querySelector(".acting-as-eyebrow");
  if (eyebrow) eyebrow.textContent = signedInRole === role ? "Signed in as" : "Acting as";
  const nameEl = document.getElementById("actingAsRoleName");
  const scopeEl = document.getElementById("actingAsScope");
  if (nameEl) nameEl.textContent = role;
  if (scopeEl) {
    const opt = els.roleSelect && els.roleSelect.querySelector(`option[value="${role}"]`);
    scopeEl.textContent = opt ? `· ${opt.textContent.replace(role, "").replace(/^[\s—–-]+/, "").trim() || opt.title || ""}` : "";
  }
}

function closeTopbarActionMenu() {
  const menu = document.getElementById("topbarActionMenu");
  const button = document.getElementById("topbarMoreActionsBtn");
  menu?.classList.remove("is-open");
  button?.setAttribute("aria-expanded", "false");
  document.body.classList.remove("topbar-menu-open");
}

function bindTopbarActionMenu() {
  const menu = document.getElementById("topbarActionMenu");
  const button = document.getElementById("topbarMoreActionsBtn");
  if (!menu || !button || menu.dataset.bound === "true") return;
  menu.dataset.bound = "true";
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    const open = menu.classList.toggle("is-open");
    button.setAttribute("aria-expanded", open ? "true" : "false");
    document.body.classList.toggle("topbar-menu-open", open);
  });
  document.addEventListener("click", (event) => {
    if (!menu.contains(event.target)) closeTopbarActionMenu();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeTopbarActionMenu();
  });
}

let interactionFeedbackInstalled = false;
let interactionProgressTimer = null;

function installInteractionFeedback() {
  if (interactionFeedbackInstalled || typeof document === "undefined") return;
  interactionFeedbackInstalled = true;
  ensureInteractionProgress();
  document.addEventListener("pointerdown", handleInteractionPointerDown, true);
  document.addEventListener("pointerup", clearPressedInteraction, true);
  document.addEventListener("pointercancel", clearPressedInteraction, true);
  document.addEventListener("click", handleInteractionClick, true);
  document.addEventListener("change", handleInteractionChange, true);
  document.addEventListener("input", handleInteractionInput, true);
}

function ensureInteractionProgress() {
  if (!document.body || document.getElementById("interactionProgress")) return;
  const bar = document.createElement("div");
  bar.id = "interactionProgress";
  bar.className = "interaction-progress";
  bar.setAttribute("aria-hidden", "true");
  document.body.appendChild(bar);
}

function actionFeedbackElement(target) {
  if (!target || typeof target.closest !== "function") return null;
  return target.closest([
    "button",
    "[role='button']",
    "a[href]",
    "input",
    "select",
    "textarea",
    "[data-open]",
    "[data-action]",
    "[data-view]",
    "[data-grid-mode]",
    "[data-pivot-view]",
    "[data-chart-view]",
    "[data-chart-table-copy]",
    "[data-column-filter]",
    "[data-slicer-clear]",
    "[data-quick-focus]",
    "[data-bulk-date-days]",
    "[data-date-preset]"
  ].join(","));
}

function interactionFeedbackAllowed(element) {
  if (!element || !element.classList) return false;
  if (element.disabled || element.getAttribute("aria-disabled") === "true") return false;
  if (element.closest("[hidden], .hidden, .column-filter-menu[hidden]")) return false;
  if (element.dataset?.noInteractionFeedback === "true") return false;
  const tag = (element.tagName || "").toLowerCase();
  if (tag === "input" && ["hidden", "file"].includes((element.type || "").toLowerCase())) return false;
  return true;
}

function handleInteractionPointerDown(event) {
  const element = actionFeedbackElement(event.target);
  if (!interactionFeedbackAllowed(element)) return;
  element.classList.add("is-pressing");
}

function clearPressedInteraction(event) {
  const element = actionFeedbackElement(event.target);
  if (!element || !element.classList) return;
  window.setTimeout(() => element.classList.remove("is-pressing"), 90);
}

function handleInteractionClick(event) {
  const element = actionFeedbackElement(event.target);
  if (!interactionFeedbackAllowed(element)) return;
  acknowledgeInteraction(element, { busy: shouldShowBusyFeedback(element) });
  if (isProgressWorthyInteraction(element)) pulseInteractionProgress(520);
}

function handleInteractionChange(event) {
  const element = actionFeedbackElement(event.target);
  if (!interactionFeedbackAllowed(element)) return;
  markFieldChanged(element);
  if (isProgressWorthyInteraction(element)) pulseInteractionProgress(420);
}

function handleInteractionInput(event) {
  const element = actionFeedbackElement(event.target);
  if (!interactionFeedbackAllowed(element)) return;
  const tag = (element.tagName || "").toLowerCase();
  const type = (element.type || "").toLowerCase();
  if (tag === "textarea" || tag === "select" || ["search", "text", "number", "date", "month"].includes(type)) {
    markFieldChanged(element);
  }
}

function acknowledgeInteraction(element, options = {}) {
  element.classList.remove("tms-action-ack");
  // Restart the CSS animation even when the same control is clicked quickly.
  void element.offsetWidth;
  element.classList.add("tms-action-ack");
  window.setTimeout(() => element.classList.remove("tms-action-ack", "is-pressing"), 720);
  if (options.busy && canShowBusyFeedback(element)) {
    element.classList.add("is-busy");
    element.setAttribute("aria-busy", "true");
    window.setTimeout(() => {
      element.classList.remove("is-busy");
      if (element.getAttribute("aria-busy") === "true") element.removeAttribute("aria-busy");
    }, 820);
  }
}

function markFieldChanged(element) {
  element.classList.remove("tms-field-updated");
  void element.offsetWidth;
  element.classList.add("tms-field-updated");
  window.clearTimeout(element.__tmsFieldUpdatedTimer);
  element.__tmsFieldUpdatedTimer = window.setTimeout(() => element.classList.remove("tms-field-updated"), 760);
}

function pulseInteractionProgress(duration = 520) {
  const bar = document.getElementById("interactionProgress");
  if (!bar) return;
  bar.classList.remove("is-active");
  void bar.offsetWidth;
  bar.classList.add("is-active");
  window.clearTimeout(interactionProgressTimer);
  interactionProgressTimer = window.setTimeout(() => bar.classList.remove("is-active"), duration);
}

function canShowBusyFeedback(element) {
  const tag = (element.tagName || "").toLowerCase();
  return tag === "button" || tag === "a" || element.getAttribute("role") === "button";
}

function shouldShowBusyFeedback(element) {
  if (!canShowBusyFeedback(element)) return false;
  const text = [
    element.textContent || "",
    element.getAttribute("aria-label") || "",
    element.getAttribute("title") || "",
    Object.values(element.dataset || {}).join(" ")
  ].join(" ").toLowerCase();
  return /\b(add|assign|approve|bounce|clear|copy|delete|download|export|import|load|preview|print|refresh|remove|reset|retry|run|save|send|submit|sync|update|upload|write-off)\b/.test(text);
}

function isProgressWorthyInteraction(element) {
  const tag = (element.tagName || "").toLowerCase();
  const type = (element.type || "").toLowerCase();
  if (tag === "select" || tag === "textarea") return true;
  if (tag === "button" || tag === "a" || element.getAttribute("role") === "button") return true;
  if (tag === "input") return ["checkbox", "radio", "date", "month", "number", "search"].includes(type);
  return Boolean(element.dataset && Object.keys(element.dataset).length);
}

// LOCAL DEMO freshness: slide sample payment dates forward so the newest
// payment always lands on today and Arriving Soon demonstrates honestly via a
// real hitsBankDate. Never applied to real data - loadSourceData gates on the
// sample fallback URL. DOS, follow-up dates, and aging strings stay untouched.
function shiftSampleClaimPaymentDates(claims, today = localDateString()) {
  if (!Array.isArray(claims)) return claims;
  const todayValue = dateOnlySortValue(today);
  if (!todayValue) return claims;
  let newestPayment = null;
  let newestValue = 0;
  let secondNewestPayment = null;
  let secondNewestValue = 0;
  claims.forEach((claim) => {
    const payments = Array.isArray(claim.payments) ? claim.payments : [];
    payments.forEach((payment) => {
      const value = dateOnlySortValue(normalizeDateValue(firstPresent(payment.date, payment.paymentDate, payment.postedDate)));
      if (value > newestValue) {
        secondNewestValue = newestValue;
        secondNewestPayment = newestPayment;
        newestValue = value;
        newestPayment = payment;
      } else if (value > secondNewestValue) {
        secondNewestValue = value;
        secondNewestPayment = payment;
      }
    });
  });
  if (!newestPayment) return claims;
  const offsetDays = Math.round((todayValue - newestValue) / 86400000);
  claims.forEach((claim) => {
    const payments = Array.isArray(claim.payments) ? claim.payments : [];
    payments.forEach((payment) => {
      ["date", "paymentDate", "postedDate"].forEach((field) => {
        if (!payment[field]) return;
        const shifted = addDays(normalizeDateValue(payment[field]), offsetDays);
        if (shifted) payment[field] = shifted;
      });
    });
  });
  // The arriving-soon demo rides the SECOND-newest payment: a hitsBankDate in
  // the future would pull the newest payment out of Landed Today.
  if (secondNewestPayment) secondNewestPayment.hitsBankDate = addDays(today, 2);
  return claims;
}

async function loadSourceData() {
  if (trackerApiEnabled()) {
    const base = trackerApiBase();
    return {
      claims: [],
      feeSchedules: [],
      info: {
        url: "",
        label: "Current information",
        detail: `live paging configured${base ? ` from ${base}` : ""}`,
        generatedAt: "",
        generatedFrom: base || "",
        isLocalRealData: true,
        sourceStats: {},
        rowLimit: ""
      }
    };
  }
  if (window.TMS_SANDBOX_ONBOARDING === true) {
    return { claims: [], feeSchedules: [], info: { url: "", label: "Sandbox workspace", detail: "Test source rows are available in company onboarding", generatedAt: "", generatedFrom: "", isLocalRealData: false, sourceStats: {}, rowLimit: "" } };
  }
  for (const url of DATA_URLS) {
    try {
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) continue;
      const payload = await response.json();
      const claims = normalizeSourceClaims(payload.claims);
      if (!claims.length) continue;
      // The flag, not the URL, is the real/demo discriminator: qa-serve aliases
      // the real-data URL onto the sample file for local proof runs.
      if (!booleanFlagValue(payload.isLocalRealData)) shiftSampleClaimPaymentDates(claims);
      return {
        claims,
        feeSchedules: normalizeSourceFeeSchedules(payload.feeSchedules),
        info: {
          url,
          label: booleanFlagValue(payload.isLocalRealData) ? "Current information" : "Information temporarily unavailable",
          generatedAt: payload.generatedAt || "",
          generatedFrom: payload.generatedFrom || "",
          isLocalRealData: booleanFlagValue(payload.isLocalRealData),
          sourceStats: payload.sourceStats || {},
          rowLimit: payload.rowLimit || "",
          detail: payload.generatedAt
            ? `Generated ${formatDateTime(payload.generatedAt)} · ${formatNounCount(claims.length, "claim")}`
            : `${formatNounCount(claims.length, "local fallback claim")}`
        }
      };
    } catch (error) {
      console.warn(`Could not load ${url}`, error);
    }
  }
  showToast("Could not load local claim data.");
  return { claims: [], feeSchedules: [], info: sourceDataInfo };
}

async function loadDatabaseTenantInventory() {
  if (typeof fetch !== "function") return [];
  if (isLocalStaticRuntime()) {
    const localInventoryUrls = ["./data/tenant_inventory.local.json", "./data/tenant_inventory.json"];
    for (const url of localInventoryUrls) {
      try {
        const response = await fetch(url, { cache: "no-store" });
        if (!response.ok) continue;
        const payload = await response.json();
        const inventory = normalizeDatabaseTenantInventory(payload);
        if (inventory.length) return inventory;
      } catch (error) {
        console.warn(`Could not load ${url}`, error);
      }
    }
    return [];
  }
  if (!trackerApiEnabled()) return [];
  const base = trackerApiBase();
  const token = trackerApiToken();
  const requestSnapshot = tenantInventoryAccessSnapshot(base, token);
  try {
    const response = await fetch(`${base}/api/tenants/inventory`, {
      cache: "no-store",
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {})
      }
    });
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      return [];
    }
    if (!response.ok) return [];
    const body = await response.json().catch(() => ({}));
    if (tenantInventoryAccessSnapshot() !== requestSnapshot) return [];
    return normalizeDatabaseTenantInventory(body);
  } catch (error) {
    console.warn("Could not load tenant inventory from API", error);
    return [];
  }
}

function tenantInventoryAccessSnapshot(base = trackerApiBase(), token = trackerApiToken()) {
  const authUser = pendingPortalAuthUser || state?.authUserScope || storedPortalAuthUserSnapshot();
  return JSON.stringify({
    base: String(base || ""),
    token: String(token || ""),
    role: canonicalRole(state?.role || authUser?.role || ""),
    activeUserName: String(state?.activeUserName || authUser?.displayName || authUser?.username || authUser?.email || ""),
    auth: {
      role: canonicalRole(authUser?.role || ""),
      orgIds: normalizeUserScopeList(authUser?.orgIds),
      clientNames: normalizeUserScopeList(authUser?.clientNames),
      assignedName: String(authUser?.assignedName || "").trim()
    }
  });
}

function storedPortalAuthUserSnapshot() {
  try {
    return JSON.parse(safeLocalStorageGet(PORTAL_AUTH_USER_KEY) || safeSessionStorageGet(PORTAL_AUTH_USER_KEY) || "{}");
  } catch (_) {
    return {};
  }
}

function isLocalStaticRuntime() {
  if (typeof window === "undefined" || !window.location) return false;
  const protocol = String(window.location.protocol || "").toLowerCase();
  const hostname = String(window.location.hostname || "").toLowerCase();
  return protocol === "file:" || hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

function normalizeDatabaseTenantInventory(payload = {}) {
  const rows = Array.isArray(payload.orgs)
    ? payload.orgs
    : Array.isArray(payload.tenants)
      ? payload.tenants
      : Array.isArray(payload)
        ? payload
        : [];
  const grouped = new Map();
  rows.forEach((row) => {
    if (!row || typeof row !== "object") return;
    const tenantName = String(firstPresent(
      row.tenantName,
      row.clientName,
      row.orgName,
      row.company,
      row.client,
      row.tenant
    ) || "").trim();
    const orgId = String(firstPresent(row.orgId, row.org_id, row.id) || "").trim();
    if (!tenantName) return;
    const existing = grouped.get(tenantName) || {
      value: tenantName,
      label: tenantName,
      tenantName,
      orgIds: new Set(),
      orgNames: new Set()
    };
    if (orgId) existing.orgIds.add(orgId);
    if (row.orgName) existing.orgNames.add(String(row.orgName).trim());
    grouped.set(tenantName, existing);
  });
  return Array.from(grouped.values())
    .map((entry) => ({
      ...entry,
      orgIds: Array.from(entry.orgIds).filter(Boolean).sort(compareText),
      orgNames: Array.from(entry.orgNames).filter(Boolean).sort(compareText)
    }))
    .sort((a, b) => compareText(a.label, b.label));
}

function normalizeSourceFeeSchedules(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    tenantId: row.tenantId || row.tenant_id || row.CompanyClient || row["Company / Client"] || row.companyClient || row.company || row.client || "",
    orgId: row.orgId || row.org_id || row.OrgId || "",
    orgName: row.orgName || row.org_name || row.OrgName || "",
    payorId: row.payorId || row.PayorId || row.payerId || row.PayerId || "",
    payer: row.payer || row.Payor || "",
    state: row.state || row.State || "",
    service: row.service || row.Service || "",
    procedure: row.procedure || row.ProcedureCode || "",
    m1: row.m1 || row.M1 || "",
    m2: row.m2 || row.M2 || "",
    place: row.place || row["Place of Service"] || "",
    rate: moneyNumber(row.rate ?? row["Agreed Rate"] ?? 0),
    startDate: row.startDate || row["Start Date"] || "",
    endDate: row.endDate || row["End Date"] || "",
    network: row.network || row["In/Out of Network"] || "",
    duplicateCheck: row.duplicateCheck || row["DUPLICATE CHECK"] || "Synapse"
  })).filter((row) => row.payer || row.procedure || row.rate);
}

function normalizeSourceClaims(rows) {
  if (!Array.isArray(rows)) return [];
  const seedDemoVelocity = !trackerApiEnabled();
  const VEL_DAY = 86400000;
  return rows
    .filter((claim) => claim && typeof claim === "object")
    .map((claim, index) => {
      const normalized = {
        ...claim,
        claimId: normalizeClaimId(claim.claimId || claim.id || ""),
        services: normalizeSourceServices(claim.services),
        payments: Array.isArray(claim.payments) ? claim.payments.filter((payment) => payment && typeof payment === "object") : [],
        notes: Array.isArray(claim.notes) ? claim.notes.filter((note) => note && typeof note === "object") : [],
        paymentAttribution: claim.paymentAttribution && typeof claim.paymentAttribution === "object" ? claim.paymentAttribution : {}
      };
      // Demo/local-fallback ONLY: synthesize recent assignment transition timestamps
      // so the velocity strip (cleared this week / avg turnaround) is demonstrable.
      // Real Azure/mirror data (trackerApiEnabled) is never synthesized.
      if (seedDemoVelocity && normalized.assignedTo) {
        const turnaround = 2 + (index % 5);
        if (normalized.assignmentStatus === "Approved" && !normalized.approvedAt) {
          const approvedDaysAgo = index % 6;
          normalized.approvedAt = new Date(Date.now() - approvedDaysAgo * VEL_DAY).toISOString();
          if (!normalized.assignedAt) normalized.assignedAt = new Date(Date.now() - (approvedDaysAgo + turnaround) * VEL_DAY).toISOString();
        } else if (!normalized.assignedAt) {
          normalized.assignedAt = new Date(Date.now() - (turnaround + 1) * VEL_DAY).toISOString();
        }
      }
      return normalized;
    })
    .filter((claim) => claim.claimId);
}

function normalizeSourceServices(services) {
  if (!Array.isArray(services)) return [];
  return services
    .filter((service) => service && typeof service === "object")
    .map((service) => ({
      ...service,
      adjustments: Array.isArray(service.adjustments)
        ? service.adjustments.filter((adjustment) => adjustment && typeof adjustment === "object")
        : []
    }));
}

// Internal data-source / scope diagnostics are for admin/dev roles only. End-user
// roles (offshore biller, client) must never see "Local JSON fallback" / "No tenant
// scope" chrome, which reads like an error to a first-week biller.
function showsInternalDiagnostics() {
  const role = canonicalRole(state?.role);
  return role === "SuperAdmin" || role === "StatesideBiller";
}

function updateDataSourceBox() {
  const displayInfo = currentDataSourceDisplayInfo();
  if (els.dataSourceName) els.dataSourceName.textContent = displayInfo.label;
  if (els.dataSourceDetail) els.dataSourceDetail.textContent = `${displayInfo.detail} - workflow state stored separately`;
  const sourceBox = els.dataSourceName ? els.dataSourceName.closest(".source-box") : null;
  if (sourceBox) sourceBox.style.display = showsInternalDiagnostics() ? "" : "none";
  updateLastSyncedIndicator();
}

function currentDataSourceDisplayInfo() {
  if (trackerApiEnabled()) {
    const base = trackerApiBase();
    const status = trackerApiPageState.status === "ok" ? "live rows loaded" : "live paging configured";
    return {
      label: "Current information",
      detail: `${status}${base ? ` from ${base}` : ""}`
    };
  }
  return {
    label: sourceDataInfo.label,
    detail: sourceDataInfo.detail
  };
}

// Header "Live" pill. The stamp is the warehouse book (prepared-page
// refreshedAtUtc), never the moment this tab opened. Inventing Date.now()
// here made a 90-minute-old T1a look like "just now".
function parseLiveDataStamp(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? new Date(parsed) : null;
}

// Boot can draw the pill before state/permissions exist; no surface then.
function liveStampReportSurface() {
  try { return typeof reportReadSurface === "function" ? String(reportReadSurface() || "") : ""; } catch (_) { return ""; }
}

function liveDataStamp() {
  const page = typeof trackerApiPageState !== "undefined" && trackerApiPageState
    ? (trackerApiPageState.response || trackerApiPageState.previousResponse)
    : null;
  const plan = page && page.queryPlanSummary;
  const stamp = parseLiveDataStamp(plan && (plan.refreshedAtUtc || plan.sourceIngestedAtUtc))
    || parseLiveDataStamp(sourceDataInfo && (sourceDataInfo.lastSyncedAt || sourceDataInfo.fetchedAt || sourceDataInfo.generatedAt));
  // Once a publication has been applied, every covered view re-read from a book
  // at least as new as the one /api/health reported; never claim newer than that.
  // Prepared Reports/Statements read a pinned session, not the tracker page.
  const reportSurface = liveStampReportSurface();
  const lease = reportSurface && typeof reportReadContext !== "undefined" && reportReadContext
    && reportReadContext.report === reportSurface ? reportReadContext.lease : null;
  if (reportSurface) return lease ? parseLiveDataStamp(lease.publishedAt || lease.sourceAsOf) : null;
  // All Data's own page is the truth there (and says Updating while it reloads).
  const onWorkbench = typeof state !== "undefined" && state && state.view === "workbench";
  const watch = typeof trackerPublicationWatch !== "undefined" ? trackerPublicationWatch : null;
  const applied = !onWorkbench && watch && watch.appliedAt && watch.observedAt > 1 ? new Date(watch.observedAt) : null;
  if (applied && (!stamp || applied.getTime() > stamp.getTime())) return applied;
  return stamp;
}

function liveDataStampAgeMinutes(stamp = liveDataStamp()) {
  if (!stamp) return null;
  return Math.max(0, Math.round((Date.now() - stamp.getTime()) / 60000));
}

function formatLiveDataStampAge(mins) {
  if (mins == null) return "";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
}

// The warehouse rebuilds hourly and a build takes about half an hour, so a
// current book is routinely ~60-75 min old. Past 90 min the open tab is
// behind: say "Loaded" instead of "Live" and offer an explicit Update click.
// Nothing refreshes on a timer (2026-09-07 button-click-only decision).
const LIVE_STAMP_BEHIND_MINUTES = 90;

function updateLastSyncedIndicator() {
  const wrap = document.getElementById("lastSyncedIndicator");
  const time = document.getElementById("lastSyncedTime");
  if (!wrap || !time) return;
  const stamp = liveDataStamp();
  const mins = liveDataStampAgeMinutes(stamp);
  const api = typeof trackerApiEnabled === "function" ? trackerApiEnabled() : false;
  const pageStatus = typeof trackerApiPageState !== "undefined" && trackerApiPageState
    ? String(trackerApiPageState.status || "")
    : "";
  let label;
  if (stamp) {
    label = formatLiveDataStampAge(mins);
    wrap.title = `Data as of ${stamp.toISOString().slice(0, 16).replace("T", " ")} UTC`;
  } else if (api && (pageStatus === "pending" || liveStampReportSurface())) {
    label = "checking";
    wrap.title = "Waiting for the live tracker page to name when this book was built.";
  } else {
    label = "unknown";
    wrap.title = "The last-updated time is not available yet.";
  }
  if (typeof trackerPublicationWatch !== "undefined"
    && (trackerPublicationWatch.pendingAt || trackerPublicationWatch.deferredViews.size)
    && trackerPublicationInteractionActive()) wrap.title += " · Automatic update waiting until you finish editing or close this detail.";
  const watch = typeof trackerPublicationWatch !== "undefined" ? trackerPublicationWatch : null;
  // A check in the last 10 min that found nothing newer than what is shown
  // means the server itself is behind: an Update click could not help.
  const serverBehind = Boolean(stamp && watch && watch.checkedAt && Date.now() - watch.checkedAt < 10 * 60000
    && watch.observedAt > 1 && stamp.getTime() >= watch.observedAt);
  const behind = api && mins != null && mins >= LIVE_STAMP_BEHIND_MINUTES && !serverBehind;
  const waiting = behind && Boolean(watch && watch.pendingAt)
    && typeof trackerPublicationInteractionActive === "function" && trackerPublicationInteractionActive();
  const updating = behind && !waiting && (pageStatus === "pending" || Boolean(watch && watch.pendingAt));
  if (serverBehind && mins >= LIVE_STAMP_BEHIND_MINUTES) wrap.title += " · This is the newest data available; the next hourly update has not finished yet.";
  // While waiting, the interaction sentence above already explains why.
  else if (behind && !waiting) wrap.title += updating ? " · Loading the newest data." : " · Click Update to load the newest data.";
  const updateBtn = document.getElementById("lastSyncedUpdate");
  if (updateBtn) {
    updateBtn.hidden = !behind;
    updateBtn.disabled = updating || waiting;
    updateBtn.textContent = waiting ? "Waiting…" : updating ? "Updating…" : "Update";
  }
  time.textContent = label;
  wrap.classList.toggle("is-stale", mins != null && mins >= LIVE_STAMP_BEHIND_MINUTES && mins < 1440);
  wrap.classList.toggle("is-offline", mins != null && mins >= 1440);
  wrap.classList.toggle("is-unknown", !stamp);
  const labelEl = typeof wrap.querySelector === "function"
    ? wrap.querySelector(".last-synced-label")
    : document.querySelector(".last-synced-label");
  if (labelEl) labelEl.textContent = api
    ? (behind ? "Loaded" : "Live")
    : sourceDataInfo && /demo|json|snapshot/i.test(sourceDataInfo.label || "") ? "Local" : "Synced";
}
// Tick the relative time every 30s. Guarded so the verifier (vm context) can load this file.
if (typeof setInterval === "function") {
  setInterval(() => { try { updateLastSyncedIndicator(); } catch (_) {} }, 30000);
}

function defaultWorkflowByClaim() {
  return Object.fromEntries(sourceClaims.map((claim) => [claim.claimId, pickWorkflowFields(claim)]));
}

function defaultColumnViewSelections() {
  return { service: [], claim: [] };
}

function defaultSavedColumnViews() {
  return { service: [], claim: [] };
}

function defaultActiveColumnViewIds() {
  return { service: "", claim: "" };
}

function normalizeColumnViewMode(mode) {
  return mode === "claim" ? "claim" : "service";
}

function normalizeColumnKeyList(value) {
  if (!Array.isArray(value)) return [];
  const keys = [];
  const seen = new Set();
  value.forEach((item) => {
    const key = String(item || "").trim();
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(key) || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  });
  return keys.slice(0, 120);
}

function normalizeColumnViewSelections(value = {}) {
  return {
    service: normalizeColumnKeyList(value.service),
    claim: normalizeColumnKeyList(value.claim)
  };
}

// A COLUMN ADDED AFTER A USER LAST TOUCHED THE COLUMN PICKER.
//
// With no saved selection the grid shows every permitted column, so a new one
// is visible to a fresh user immediately. A user who has EVER opened the picker
// has a frozen key list instead, and selectedColumnKeysForMode only filters that
// list down to what is permitted — it never adds. So a genuinely new column is
// invisible to exactly the people who use the grid most, forever, and nothing
// tells them it exists.
//
// This is the same one-time migration shape as slicerLayoutVersion: bump the
// version, append once, stamp. It is NOT "saved views always get every column" —
// a user who deliberately narrowed to eight columns keeps their eight plus the
// new one, and can remove it in the picker like any other. Named saved views are
// deliberately NOT touched: those are artifacts a person chose and named, and
// they can update one themselves.
//
// To add a column here later: append its key and bump COLUMN_VIEW_VERSION.
const COLUMN_VIEW_VERSION = 1;
const COLUMNS_INTRODUCED_BY_VERSION = Object.freeze({
  // In / Out of Network, 2026-08-21. The picker had listed it since the grid was
  // built, but it had no source until the T1a snapshot began deriving it from the
  // fee-schedule contract that priced each line, so no existing selection has it.
  1: Object.freeze(["network"])
});

function migrateColumnViewSelections(selections, storedVersion) {
  const from = Number.isFinite(Number(storedVersion)) ? Number(storedVersion) : 0;
  if (from >= COLUMN_VIEW_VERSION) return selections;
  const added = [];
  for (let version = from + 1; version <= COLUMN_VIEW_VERSION; version += 1) {
    added.push(...(COLUMNS_INTRODUCED_BY_VERSION[version] || []));
  }
  if (!added.length) return selections;
  return Object.fromEntries(Object.entries(selections).map(([mode, keys]) => {
    // An EMPTY list means "no explicit selection", which already renders every
    // permitted column. Appending here would turn that into an explicit
    // one-column selection and hide the other 46.
    if (!Array.isArray(keys) || !keys.length) return [mode, keys];
    const missing = added.filter((key) => !keys.includes(key));
    return [mode, missing.length ? [...keys, ...missing] : keys];
  }));
}

function normalizeActiveColumnViewIds(value = {}) {
  return {
    service: String(value.service || "").trim(),
    claim: String(value.claim || "").trim()
  };
}

function normalizeSavedColumnViews(value = {}) {
  const normalized = defaultSavedColumnViews();
  columnViewModes.forEach((mode) => {
    const rows = Array.isArray(value[mode]) ? value[mode] : [];
    normalized[mode] = rows.map((view) => normalizeSavedColumnView(view, mode)).filter(Boolean).slice(0, 50);
  });
  return normalized;
}

function normalizeSavedColumnView(view = {}, fallbackMode = "service") {
  const rowMode = normalizeColumnViewMode(view.rowMode || view.row_mode || fallbackMode);
  const label = String(view.label || "").trim().replace(/\s+/g, " ").slice(0, 80);
  const columnKeys = normalizeColumnKeyList(view.columnKeys || view.column_keys);
  if (!label || !columnKeys.length) return null;
  const viewId = String(view.viewId || view.view_id || `${rowMode}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`).trim().slice(0, 96);
  return {
    viewId,
    label,
    rowMode,
    columnKeys,
    isDefault: view.isDefault === true || view.is_default === true,
    createdAtUtc: String(view.createdAtUtc || view.created_at_utc || ""),
    updatedAtUtc: String(view.updatedAtUtc || view.updated_at_utc || "")
  };
}

function defaultState() {
  return {
    role: "SuperAdmin",
    activeUserName: "Platform Admin",
    view: "workbench",
    rowMode: "service",
    workbenchPreset: "full",
    workbenchSheetMode: true,
    sheetModeVersion: 4,
    slicerLayoutVersion: 2,
    slicerSize: "compact",
    queueFilter: "all",
    focusedWorkClaimId: "",
    trackerPage: 1,
    trackerPageSize: MAX_RENDERED_TRACKER_ROWS,
    tableSort: { column: null, dir: null }, // UI-100 #19
    rowDensity: "compact", // compact is the only density (2026-08-17)
    slicers: structuredClone(emptySlicers),
    columnFilters: structuredClone(emptyColumnFilters),
    slicerModes: structuredClone(emptyFilterModes),
    columnFilterModes: structuredClone(emptyFilterModes),
    columnFilterRanges: structuredClone(emptyColumnFilterRanges),
    // Filters the reader turned OFF in the filter panel, kept to turn back on.
    disabledFilters: {},
    columnViewVersion: COLUMN_VIEW_VERSION,
    columnViewSelections: defaultColumnViewSelections(),
    savedColumnViews: defaultSavedColumnViews(),
    activeColumnViewIds: defaultActiveColumnViewIds(),
    savedOperationsViews: defaultSavedOperationsViews(),
    feeScheduleRows: sourceFeeScheduleRows.length ? structuredClone(sourceFeeScheduleRows) : structuredClone(defaultFeeScheduleRows),
    followUpRules: structuredClone(actionRules),
    selectedClaims: [],
    selectedClaimTargets: {},
    selectedServiceTargets: {},
    sidebarCollapsed: false,
    sourceUrl: sourceDataInfo.url,
    statementPrints: {},
    clientPivot: defaultClientPivotState(),
    clientReportOpenKey: "",
    clientReportSlicers: {},
    clientReportExpanded: {},
    // The Reports filter bar, so a reload does not silently widen a narrowed
    // report back to the whole book (see restoreReportFilterSelections).
    reportFilters: {},
    workflowByClaim: defaultWorkflowByClaim(),
    followUpByService: {},
    workflowCommandQueue: [],
    queueWorkContextByClaim: {},
    workflowApiHealth: { status: "unchecked", checkedAt: "" },
    reportRunFilters: { tenantId: "All", orgId: "All", fromDate: "", toDate: "", exportOnly: false, deniedOnly: false, redactedOnly: false, exportKind: "All" },
    reportRunTrail: [],
    reportExportJobTrail: [],
    exportGovernanceActivityTrail: [],
    exportGovernanceActivityExportPreview: null,
    reportExportPackagePreview: null,
    trackerWorkbookExportPreview: null,
    trackerExportSlicers: structuredClone(emptyTrackerExportSlicers),
    selectedExportJobDownloadPreview: null,
    selectedExportJobDetail: null,
    claimQueueWorkAuditTrail: [],
    feedbackTickets: [],
    // Blank on purpose: /app.js is world-readable (see the roster note at the top
    // of this file) and this default shipped a real company mailbox. It is also
    // never read — the Feedback admin screen renders
    // feedbackAdminState.notificationEmail, which comes from GET /api/feedback
    // settings — so the only thing the literal did was publish an address.
    feedbackNotificationEmail: "",
    claimQueueWorkOutcomeSummary: null,
    queueWorkExportPreview: null,
    selectedReportRun: null,
    reportRunAuditExportPreview: null,
    // GLOBAL CLIENT SWITCHER (MASTER_BACKLOG.md UI-6). "" means All clients.
    // The whole inventory entry, not just a name: All Data / Reports / the
    // workbook export narrow by served client tenant NAME, while
    // Authorizations / Statements / Payments / Fee Schedules / Access narrow
    // by database org ID - and one tenant can own several
    // (TENANT_DROPDOWN_SCOPE.md:14). Re-validated against the live inventory
    // on every read; never trusted raw out of localStorage.
    activeClientGroup: { value: "", label: "", orgIds: [] },
    feeScheduleFilters: { tenantId: "All", orgId: "All", payer: "All", state: "All", procedure: "All", network: "All", activeDate: "", search: "" },
    // Per-column (Excel AutoFilter) narrowing on the rate table. Display only.
    feeScheduleColumnFilters: {},
    securityReviewFilters: { orgId: "All", decision: "All", permission: "", fromDate: "", toDate: "" },
    authorizationFailures: [],
    accessDecisions: [],
    accessAdminPreviews: [],
    audit: [
      { at: "2026-05-17 19:40", by: "System", text: "Local portal preview initialized from workbook discovery decisions." }
    ]
  };
}

// Fresh-session marker: true when the booting view came from defaultState()'s
// "workbench" placeholder rather than a persisted user choice or an explicit
// ?view= param. applyUrlState / applyAuthenticatedUserToState clear it when
// they set a view; applyBootDefaultView consumes it at boot so fresh sessions
// land on the role's canonical landing surface (HOME_VIEW_SPEC.md §1).
let __bootViewIsDefault = false;

function loadState() {
  const base = defaultState();
  const stored = safeLocalStorageGet(STORAGE_KEY);
  if (!stored) { __bootViewIsDefault = true; return base; }
  try {
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed.claims) && !parsed.workflowByClaim) {
      parsed.workflowByClaim = Object.fromEntries(parsed.claims.map((claim) => [claim.claimId, pickWorkflowFields(claim)]));
      delete parsed.claims;
    }
    if (parsed.sourceUrl !== sourceDataInfo.url) {
      __bootViewIsDefault = true;
      return {
        ...base,
        audit: [
          ...(Array.isArray(parsed.audit) ? parsed.audit.slice(-20) : []),
          { at: timestamp(), by: "System", text: `Information refreshed; filters and workflow state were reset.` }
        ]
      };
    }
    __bootViewIsDefault = !(typeof parsed.view === "string" && parsed.view);
    const normalizedRole = canonicalRole(parsed.role);
    const normalizedActiveUserName = users.some((user) => user.name === parsed.activeUserName && canonicalRole(user.role) === normalizedRole)
      ? parsed.activeUserName
      : (users.find((user) => canonicalRole(user.role) === normalizedRole)?.name || base.activeUserName);
    return {
      ...base,
      ...parsed,
      role: normalizedRole,
      activeUserName: normalizedActiveUserName,
      sourceUrl: sourceDataInfo.url,
      rowMode: columnViewModes.includes(parsed.rowMode) ? parsed.rowMode : base.rowMode,
      workbenchPreset: workbenchPresets[parsed.workbenchPreset] ? parsed.workbenchPreset : base.workbenchPreset,
      workbenchSheetMode: base.workbenchSheetMode,
      rowDensity: base.rowDensity,
      sheetModeVersion: base.sheetModeVersion,
      slicerLayoutVersion: base.slicerLayoutVersion,
      // Advanced Filters is a disclosure, not a saved workspace preference. Every
      // fresh load begins with the six daily filters; advanced filters appear
      // only after the person asks for them in this session.
      slicerSize: "compact",
      queueFilter: queueDefinitions.some((queue) => queue.key === parsed.queueFilter) ? parsed.queueFilter : "all",
      focusedWorkClaimId: typeof parsed.focusedWorkClaimId === "string" ? parsed.focusedWorkClaimId : "",
      trackerPage: Math.max(1, positiveNumber(parsed.trackerPage, 1)),
      trackerPageSize: trackerPageSizeValue(parsed.trackerPageSize),
      workflowByClaim: {
        ...base.workflowByClaim,
        ...migrateWorkflowByClaim(parsed.workflowByClaim || {})
      },
      followUpByService: normalizeFollowUpByService(parsed.followUpByService || {}),
      slicers: ensureSlicerState(parsed.slicers),
      columnFilters: migrateColumnFilters(ensureColumnFilterState(parsed.columnFilters)),
      // An older blob has neither key; both normalize to {} = include, which is
      // exactly what that blob meant.
      slicerModes: ensureFilterModeState(parsed.slicerModes, ensureSlicerState(parsed.slicers)),
      columnFilterModes: ensureFilterModeState(parsed.columnFilterModes, migrateColumnFilters(ensureColumnFilterState(parsed.columnFilters))),
      // RELOAD RESTORATION. An older blob has no such key and normalizes to {},
      // which is exactly what that blob meant; a stored range whose operator or
      // operand no longer parses is DROPPED here rather than restored as a
      // filter nobody can see in the chip rail.
      columnFilterRanges: ensureColumnFilterRangeState(parsed.columnFilterRanges),
      disabledFilters: ensureDisabledFilterState(parsed.disabledFilters),
      columnViewVersion: COLUMN_VIEW_VERSION,
      columnViewSelections: migrateColumnViewSelections(
        normalizeColumnViewSelections(parsed.columnViewSelections),
        parsed.columnViewVersion
      ),
      savedColumnViews: normalizeSavedColumnViews(parsed.savedColumnViews),
      activeColumnViewIds: normalizeActiveColumnViewIds(parsed.activeColumnViewIds),
      savedOperationsViews: normalizeSavedOperationsViews(parsed.savedOperationsViews),
      feeScheduleRows: feeScheduleRowsForState(parsed.feeScheduleRows),
      followUpRules: normalizeFollowUpRules(parsed.followUpRules),
      selectedClaims: Array.isArray(parsed.selectedClaims) ? parsed.selectedClaims.map(normalizeClaimId) : [],
      selectedClaimTargets: parsed.selectedClaimTargets && typeof parsed.selectedClaimTargets === "object" ? parsed.selectedClaimTargets : {},
      selectedServiceTargets: normalizeSelectedServiceTargets(parsed.selectedServiceTargets),
      sidebarCollapsed: booleanFlagValue(parsed.sidebarCollapsed),
      statementPrints: parsed.statementPrints && typeof parsed.statementPrints === "object" ? parsed.statementPrints : {},
      clientPivot: normalizeClientPivotState(parsed.clientPivot),
      clientReportOpenKey: CLIENT_REPORT_LIBRARY.some((report) => report.key === parsed.clientReportOpenKey) ? parsed.clientReportOpenKey : "",
      clientReportSlicers: normalizeClientReportSlicers(parsed.clientReportSlicers),
      clientReportExpanded: normalizeClientReportExpanded(parsed.clientReportExpanded),
      workflowCommandQueue: Array.isArray(parsed.workflowCommandQueue) ? parsed.workflowCommandQueue.slice(-500) : [],
      queueWorkContextByClaim: parsed.queueWorkContextByClaim && typeof parsed.queueWorkContextByClaim === "object" ? parsed.queueWorkContextByClaim : {},
      workflowApiHealth: parsed.workflowApiHealth && typeof parsed.workflowApiHealth === "object" ? parsed.workflowApiHealth : base.workflowApiHealth,
      reportRunFilters: {
        ...base.reportRunFilters,
        ...(parsed.reportRunFilters && typeof parsed.reportRunFilters === "object" ? parsed.reportRunFilters : {})
      },
      reportRunTrail: Array.isArray(parsed.reportRunTrail) ? parsed.reportRunTrail.slice(-100) : [],
      reportExportJobTrail: Array.isArray(parsed.reportExportJobTrail) ? parsed.reportExportJobTrail.slice(-100) : [],
      exportGovernanceActivityTrail: Array.isArray(parsed.exportGovernanceActivityTrail) ? parsed.exportGovernanceActivityTrail.slice(-150) : [],
      exportGovernanceActivityExportPreview: parsed.exportGovernanceActivityExportPreview && typeof parsed.exportGovernanceActivityExportPreview === "object" ? parsed.exportGovernanceActivityExportPreview : null,
      reportExportPackagePreview: parsed.reportExportPackagePreview && typeof parsed.reportExportPackagePreview === "object" ? parsed.reportExportPackagePreview : null,
      trackerWorkbookExportPreview: parsed.trackerWorkbookExportPreview && typeof parsed.trackerWorkbookExportPreview === "object" ? parsed.trackerWorkbookExportPreview : null,
      trackerExportSlicers: normalizeTrackerExportSlicers(parsed.trackerExportSlicers),
      selectedExportJobDownloadPreview: parsed.selectedExportJobDownloadPreview && typeof parsed.selectedExportJobDownloadPreview === "object" ? parsed.selectedExportJobDownloadPreview : null,
      selectedExportJobDetail: parsed.selectedExportJobDetail && typeof parsed.selectedExportJobDetail === "object" ? parsed.selectedExportJobDetail : null,
      claimQueueWorkAuditTrail: Array.isArray(parsed.claimQueueWorkAuditTrail) ? parsed.claimQueueWorkAuditTrail.slice(-100) : [],
      feedbackTickets: Array.isArray(parsed.feedbackTickets) ? parsed.feedbackTickets.slice(-200) : [],
      feedbackNotificationEmail: typeof parsed.feedbackNotificationEmail === "string" ? parsed.feedbackNotificationEmail : base.feedbackNotificationEmail,
      claimQueueWorkOutcomeSummary: parsed.claimQueueWorkOutcomeSummary && typeof parsed.claimQueueWorkOutcomeSummary === "object" ? parsed.claimQueueWorkOutcomeSummary : null,
      queueWorkExportPreview: parsed.queueWorkExportPreview && typeof parsed.queueWorkExportPreview === "object" ? parsed.queueWorkExportPreview : null,
      selectedReportRun: parsed.selectedReportRun && typeof parsed.selectedReportRun === "object" ? parsed.selectedReportRun : null,
      reportRunAuditExportPreview: parsed.reportRunAuditExportPreview && typeof parsed.reportRunAuditExportPreview === "object" ? parsed.reportRunAuditExportPreview : null,
      feeScheduleFilters: {
        ...base.feeScheduleFilters,
        ...(parsed.feeScheduleFilters && typeof parsed.feeScheduleFilters === "object" ? parsed.feeScheduleFilters : {}),
        tenantId: parsed.feeScheduleFilters?.tenantId || "All"
      },
      feeScheduleColumnFilters: ensureFeeScheduleColumnFilterState(parsed.feeScheduleColumnFilters),
      securityReviewFilters: {
        ...base.securityReviewFilters,
        ...(parsed.securityReviewFilters && typeof parsed.securityReviewFilters === "object" ? parsed.securityReviewFilters : {})
      },
      authorizationFailures: Array.isArray(parsed.authorizationFailures) ? parsed.authorizationFailures.slice(-100) : [],
      accessDecisions: Array.isArray(parsed.accessDecisions) ? parsed.accessDecisions.slice(-100) : [],
      accessAdminPreviews: Array.isArray(parsed.accessAdminPreviews) ? parsed.accessAdminPreviews.slice(-50) : [],
      authUserScope: sanitizeAuthUserScope(parsed.authUserScope),
      audit: Array.isArray(parsed.audit) ? parsed.audit.slice(-200) : base.audit
    };
  } catch {
    __bootViewIsDefault = true;
    return base;
  }
}

// Persisted authenticated scope is only trusted when its role is one of the
// canonical roleProfiles keys; anything else (tampered or legacy payloads)
// resets to null so the demo-store path applies instead of a stale scope.
function sanitizeAuthUserScope(scope) {
  if (!scope || typeof scope !== "object") return null;
  const role = typeof scope.role === "string" ? scope.role : "";
  if (!roleProfiles[role]) return null;
  return {
    role,
    orgIds: normalizeUserScopeList(scope.orgIds),
    clientNames: normalizeUserScopeList(scope.clientNames),
    payerNames: normalizeUserScopeList(scope.payerNames),
    assignedOnly: scope.assignedOnly === true,
    assignedName: String(scope.assignedName || "").trim(),
    followUpPolicies: oversightPolicies(scope.followUpPolicies),
    scopeDenial: sanitizeScopeDenialPayload(scope.scopeDenial),
    followUpActionAccess: sanitizeFollowUpActionAccessProjection(scope.followUpActionAccess)
  };
}

function applyUrlState({ seedFilters = true } = {}) {
  const params = new URLSearchParams(window.location.search);
  const requestedView = params.get("view");
  // "client" WAS the Payments & Exports view key until 2026-08-11. Bookmarks,
  // saved links, the ?view=client&clientReport=payments harness scripts and
  // Client Home's own deep links all still carry it, and the surface it named
  // did not disappear - it moved inside Reports. Aliasing it here lands those
  // links on the right screen; without the alias an unknown key is ignored two
  // lines down and the URL silently does nothing.
  const canonicalRequestedView = requestedView === "client" ? "reports" : requestedView;
  if (canonicalRequestedView && Object.prototype.hasOwnProperty.call(viewPermissions, canonicalRequestedView)) {
    state.view = canonicalRequestedView;
    __bootViewIsDefault = false;
  }
  if (state.view === "authorizations" && params.get("report") === "waiting") window.__authzWaitingReportAutoOpen = true;
  const requestedClientReport = params.get("clientReport") || "";
  if (state.view === "reports") {
    const openClientReportKey = CLIENT_REPORT_LIBRARY.some((report) => report.key === requestedClientReport)
      ? requestedClientReport
      : "";
    state.clientReportOpenKey = openClientReportKey;
    // An internal session picks these three from the report family switcher,
    // so a ?clientReport= link has to move THAT picker too, or the link opens
    // Reports on whatever report happened to be active last.
    const clientFamilyView = reportViewForClientReportKey(openClientReportKey);
    if (clientFamilyView) reportArState.view = clientFamilyView;
  }
  // ?queue= SEEDS A FILTER ON BOOT ONLY (2026-09-17, review).
  // clearTrackerFilterUrlState() rewrites the CURRENT history entry, so an older
  // entry can still carry the parameter. Re-running this from popstate walked a
  // cleared queue back on - Back then Forward ended with the queue active and an
  // address bar that did not name it, which is unarguably "it's still using the
  // filters". Navigation restores the VIEW; it does not re-apply filters.
  const requestedQueue = seedFilters ? params.get("queue") : "";
  if (requestedQueue && queueDefinitions.some((queue) => queue.key === requestedQueue)) {
    state.queueFilter = requestedQueue;
  }
}

function normalizeClaimId(claimId) {
  return String(claimId || "").replace(/^CLM-/i, "");
}

// Follow-up action/date attach to a single service line, not the whole claim.
// Key the per-line optimistic overlay (state.followUpByService) on the
// normalized claim id + the service line id so setting a follow-up on one
// row never fans onto sibling service lines of the same claim.
function followUpKey(claimId, serviceId) {
  return `${normalizeClaimId(claimId)}::${String(serviceId || "").trim()}`;
}

function serviceSelectionKey(orgId, claimId, serviceId) {
  return [String(orgId || "").trim(), normalizeClaimId(claimId), String(serviceId || "").trim()].join("\u001f");
}

function normalizeSelectedServiceTargets(value) {
  const targets = Array.isArray(value) ? value : (value && typeof value === "object" ? Object.values(value) : []);
  return Object.fromEntries(targets.map((target) => {
    const orgId = String(target?.orgId || "").trim();
    const claimId = normalizeClaimId(target?.claimId);
    const serviceId = String(target?.serviceId || "").trim();
    return [serviceSelectionKey(orgId, claimId, serviceId), { orgId, claimId, serviceId }];
  }).filter(([key, target]) => target.orgId && target.claimId && target.serviceId && key));
}

function feeScheduleRowsForState(storedRows) {
  if (sourceFeeScheduleRows.length && (!Array.isArray(storedRows) || isDemoFeeScheduleRows(storedRows))) {
    return normalizeFeeScheduleRows(sourceFeeScheduleRows);
  }
  return normalizeFeeScheduleRows(Array.isArray(storedRows) ? storedRows : defaultFeeScheduleRows);
}

function isDemoFeeScheduleRows(rows) {
  return Array.isArray(rows)
    && rows.length === defaultFeeScheduleRows.length
    && rows.every((row) => ["Demo", "Local Seed"].includes(row.duplicateCheck));
}

function normalizeFeeScheduleRows(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeFeeScheduleRow);
}

function normalizeFeeScheduleRow(row = {}) {
  const rate = normalizeOptionalNumber(row.rate);
  const rawOrgId = String(firstPresent(row.orgId, row.org_id, row.OrgId) || "").trim();
  const rawTenantId = String(firstPresent(
    row.tenantId,
    row.tenant,
    row.Tenant,
    row.TenantId,
    row.tenantName,
    row.tenant_name,
    row.tenant_id,
    row.companyClient,
    row.company_client,
    row.company,
    row.client
  ) || "").trim();
  const inventoryTenantId = tenantNameForOrgId(rawOrgId) || tenantNameForLegacyOrgId(rawOrgId);
  const tenantId = isLegacyPseudoOrgId(rawTenantId) ? inventoryTenantId : (rawTenantId || inventoryTenantId);
  const tenantOrgIds = orgIdsForTenantName(tenantId);
  // A legacy/pseudo org id ("undefined", "", "ORG-...") collapses to a real
  // tenant org id when one exists. An ORG-* id with no inventory mapping is
  // preserved as-is — demo claims carry the same ORG-* ids, and blanking the
  // row breaks bestFeeScheduleMatch's exact org pairing. Only blank / literal
  // "undefined" collapse to blank for the grid input.
  const preservedLegacyOrgId = /^ORG-/i.test(String(rawOrgId || "").trim()) ? String(rawOrgId).trim() : "";
  const orgId = (isLegacyPseudoOrgId(rawOrgId) && tenantOrgIds.length)
    ? tenantOrgIds[0]
    : (isLegacyPseudoOrgId(rawOrgId) ? preservedLegacyOrgId : rawOrgId);
  return {
    feeScheduleRateId: String(row.feeScheduleRateId || row.fee_schedule_rate_id || row.id || ""),
    tenantId,
    orgId,
    orgName: String(firstPresent(row.orgName, row.org_name, row.OrgName, row.companyName, row.company_name) ?? "").trim(),
    payorId: String(row.payorId || row.payerId || ""),
    externalPayerId: String(row.externalPayerId || row.external_payer_id || ""),
    payer: String(row.payer || row.payerName || ""),
    state: String(row.state || ""),
    service: String(row.service || ""),
    procedure: String(row.procedure || row.procedureCode || ""),
    m1: String(row.m1 || row.modifier1 || ""),
    m2: String(row.m2 || row.modifier2 || ""),
    place: String(row.place || row.placeOfService || ""),
    rate,
    startDate: dateInputValue(row.startDate || row.effectiveStartDate || ""),
    endDate: dateInputValue(row.endDate || row.effectiveEndDate || ""),
    network: String(row.network || ""),
    duplicateCheck: String(row.duplicateCheck || row.rateMatchMethod || ""),
    // Per-payer secondary expected rule ("Allowed Amount" / "PR Amount" / "").
    // Mirrors backend/feeScheduleRates.js#normalizeFeeRow.
    secondaryCalc: String(row.secondaryCalc || row.secondary_calc || row.Secondary_Calc || ""),
    approvalStatus: String(firstPresent(row.approvalStatus, row.approval_status, "draft")),
    status: String(row.status || "active"),
    sourceSystem: String(row.sourceSystem || "")
  };
}

function isLegacyPseudoOrgId(orgId = "") {
  const value = String(orgId || "").trim();
  return !value || /^ORG-/i.test(value) || value.toLowerCase() === "undefined";
}

function tenantNameForLegacyOrgId(orgId = "") {
  const compactOrg = compactContractText(orgId);
  if (!compactOrg) return "";
  const match = (Array.isArray(databaseTenantInventory) ? databaseTenantInventory : []).find((entry) => {
    const tenant = compactContractText(entry.tenantName || entry.label || entry.value || "");
    const orgName = compactContractText(entry.orgName || "");
    return (tenant && compactOrg.includes(tenant)) || (orgName && compactOrg.includes(orgName));
  });
  return match ? (match.tenantName || match.label || match.value || "") : "";
}

function normalizeOptionalNumber(value) {
  if (value === "" || value == null) return "";
  const number = parseMoneyNumber(value);
  return Number.isFinite(number) ? number : "";
}

function dateInputValue(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const date = new Date(text);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

// Per-service-line follow-up overlay: { [followUpKey]: { action, followUpDate } }.
function normalizeFollowUpByService(followUpByService) {
  const source = followUpByService && typeof followUpByService === "object" ? followUpByService : {};
  const clean = {};
  Object.entries(source).forEach(([key, entry]) => {
    if (!key || !entry || typeof entry !== "object") return;
    clean[String(key)] = {
      action: String(entry.action || ""),
      followUpDate: String(entry.followUpDate || "")
    };
  });
  return clean;
}

function migrateWorkflowByClaim(workflowByClaim) {
  return Object.fromEntries(Object.entries(workflowByClaim)
    .map(([claimId, workflow]) => [normalizeClaimId(claimId), sanitizeWorkflowFields(workflow)]));
}

function migrateWorkflowPatchByClaim(workflowByClaim) {
  return Object.fromEntries(Object.entries(workflowByClaim)
    .map(([claimId, workflow]) => [normalizeClaimId(claimId), sanitizeWorkflowPatch(workflow)]));
}

const WORKFLOW_ASSIGNMENT_STATUS_BY_KEY = new Map([
  ["unassigned", "Unassigned"],
  ["assigned", "Assigned"],
  ["routed to offshore review", "Routed to Offshore Review"],
  ["working", "Working"],
  ["in progress", "In Progress"],
  ["submitted for review", "Submitted For Review"],
  ["in review", "In Review"],
  ["approved", "Approved"],
  ["bounced back", "Bounced Back"]
]);

function workflowAssignmentStatus(value, fallback = "") {
  const key = String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
  return WORKFLOW_ASSIGNMENT_STATUS_BY_KEY.get(key) || fallback;
}

function sanitizeWorkflowFields(workflow) {
  const source = workflow && typeof workflow === "object" ? workflow : {};
  return {
    action: String(source.action || ""),
    followUpDate: String(source.followUpDate || ""),
    assignedTo: String(source.assignedTo || ""),
    assignedBy: String(source.assignedBy || ""),
    assignmentDue: String(source.assignmentDue || ""),
    assignmentStatus: workflowAssignmentStatus(source.assignmentStatus, "Unassigned"),
    assignmentInstruction: String(source.assignmentInstruction || ""),
    instructionBy: String(source.instructionBy || ""),
    instructionAt: String(source.instructionAt || ""),
    bounceReason: String(source.bounceReason || ""),
    bounceBy: String(source.bounceBy || ""),
    bounceAt: String(source.bounceAt || ""),
    reviewNote: String(source.reviewNote || ""),
    writeOffSnapshot: source.writeOffSnapshot && typeof source.writeOffSnapshot === "object" ? source.writeOffSnapshot : null,
    notes: Array.isArray(source.notes)
      ? source.notes.map((note) => ({
        at: String(note?.at || ""),
        by: String(note?.by || ""),
        text: String(note?.text || "")
      })).slice(-200)
      : []
  };
}

function sanitizeWorkflowPatch(patch) {
  const source = patch && typeof patch === "object" ? patch : {};
  const clean = {};
  if ("action" in source) clean.action = String(source.action || "");
  if ("followUpDate" in source) clean.followUpDate = String(source.followUpDate || "");
  if ("assignedTo" in source) clean.assignedTo = String(source.assignedTo || "");
  if ("assignedBy" in source) clean.assignedBy = String(source.assignedBy || "");
  if ("assignmentDue" in source) clean.assignmentDue = String(source.assignmentDue || "");
  if ("assignmentStatus" in source) {
    const status = workflowAssignmentStatus(source.assignmentStatus, "");
    if (status) clean.assignmentStatus = status;
  }
  if ("assignmentInstruction" in source) clean.assignmentInstruction = String(source.assignmentInstruction || "");
  if ("instructionBy" in source) clean.instructionBy = String(source.instructionBy || "");
  if ("instructionAt" in source) clean.instructionAt = String(source.instructionAt || "");
  if ("bounceReason" in source) clean.bounceReason = String(source.bounceReason || "");
  if ("bounceBy" in source) clean.bounceBy = String(source.bounceBy || "");
  if ("bounceAt" in source) clean.bounceAt = String(source.bounceAt || "");
  if ("reviewNote" in source) clean.reviewNote = String(source.reviewNote || "");
  if ("writeOffSnapshot" in source) clean.writeOffSnapshot = source.writeOffSnapshot && typeof source.writeOffSnapshot === "object" ? source.writeOffSnapshot : null;
  if ("notes" in source && Array.isArray(source.notes)) {
    clean.notes = source.notes.map((note) => ({
      at: String(note?.at || ""),
      by: String(note?.by || ""),
      text: String(note?.text || "")
    })).slice(-200);
  }
  return clean;
}

// OFFSHORE_HANDOFF_SPEC §4 — the ONE bounce-reason read helper, used everywhere
// the reason renders. New writes land in claim.bounceReason; legacy bounced
// claims stored the manager's reason in reviewNote, so fall back to it ONLY
// while the claim is still Bounced Back (any other status means reviewNote is
// offshore's own words).
function claimBounceReason(claim) {
  if (!claim || typeof claim !== "object") return "";
  return String(claim.bounceReason || "").trim()
    || (claim.assignmentStatus === "Bounced Back" ? String(claim.reviewNote || "").trim() : "");
}

function normalizeFollowUpRules(value) {
  const fallback = structuredClone(actionRules);
  if (!Array.isArray(value)) return fallback;
  const cleaned = value.map(normalizeFollowUpRule).filter(Boolean);
  if (!cleaned.length) return fallback;
  // BACKFILL: a browser that stored the old 9-action scaffold (or any list predating an
  // addition to the authoritative matrix) must not stay stuck on it — that was the whole
  // point of finding 3-A, and localStorage would silently re-create it. Any authoritative
  // action the persisted list is missing is appended in matrix order. The biller's own
  // customizations (edited offsets, custom actions) are preserved untouched.
  const present = new Set(cleaned.map((rule) => normalizeFollowUpActionKey(rule.action)));
  actionRules.forEach((rule) => {
    if (present.has(normalizeFollowUpActionKey(rule.action))) return;
    present.add(normalizeFollowUpActionKey(rule.action));
    cleaned.push(structuredClone(rule));
  });
  return cleaned;
}

function normalizeFollowUpRule(rule) {
  if (!rule || typeof rule !== "object") return null;
  const action = String(rule.action || "").trim();
  if (!action) return null;
  const offset = rule.offset === "" || rule.offset == null ? null : parseMoneyNumber(rule.offset);
  const clean = {
    action: action.slice(0, 80),
    offset: Number.isFinite(offset) ? offset : null,
    removeIfPaid: booleanFlagValue(rule.removeIfPaid)
  };
  // systemOnly must survive normalization (persisted state, imported setups) or a
  // system-only action such as "Claim Status Checkup Required" would leak back into the
  // manual pickers, which CT explicitly forbids (workflow_app_prototype.py:329-330).
  // A rule the biller renamed to a known system-only action inherits the flag too.
  if (rule.systemOnly === true || isSystemOnlyFollowUpAction(clean.action)) clean.systemOnly = true;
  return clean;
}

// The canonical system-only set, read straight off the authoritative embedded matrix.
function isSystemOnlyFollowUpAction(action) {
  const key = normalizeFollowUpActionKey(action);
  if (!key) return false;
  return actionRules.some((rule) => rule.systemOnly && normalizeFollowUpActionKey(rule.action) === key);
}

// Persisted COLUMN filters re-enter the app on two hydration paths and BOTH land here:
// the localStorage restore in loadState and every saved AR view (normalizeOperationsViewState).
// The Aging column filter is persisted and matched by exact equality exactly like the aging
// SLICER is — and on the live lane the selection is handed to SQL as an aging_bucket IN list --
// so it needs the same expand-never-swap migration (migrateAgingSlicerValues). Without it every
// stored aging column filter matches ZERO rows the moment the warehouse vocabulary replaces the
// mirror's: an empty grid, no error. The stored label is kept, its CT band joins it.
function migrateColumnFilters(filters) {
  const next = { ...filters };
  if (Array.isArray(next.claimId)) next.claimId = next.claimId.map(normalizeClaimId);
  if (Array.isArray(next.aging)) next.aging = migrateAgingSlicerValues(next.aging);
  return next;
}

function defaultClientPivotState() {
  return {
    view: "table",
    slicers: Object.fromEntries(CLIENT_PIVOT_SLICERS.map((slicer) => [slicer.key, []]))
  };
}

function normalizeClientPivotState(value) {
  const base = defaultClientPivotState();
  if (!value || typeof value !== "object") return base;
  const view = value.view === "chart" ? "chart" : "table";
  const storedSlicers = value.slicers && typeof value.slicers === "object" ? value.slicers : {};
  const slicers = Object.fromEntries(CLIENT_PIVOT_SLICERS.map((slicer) => {
    const selected = Array.isArray(storedSlicers[slicer.key])
      ? storedSlicers[slicer.key].filter((item) => item != null).map(String)
      : [];
    // Same persisted-aging migration as the AR slicers. A client's saved pivot is matched
    // by active.includes(v), so a selection stored under the mirror vocabulary would render
    // every metric as zero the moment the warehouse serves the CT bands — silently, in a
    // client-facing view. Expand rather than swap, so the documented rollback is safe too.
    return [slicer.key, slicer.key === "aging" ? migrateAgingSlicerValues(selected) : (slicer.key === "action" ? Array.from(new Set(selected.map(value => value === "(blank)" ? "Blank" : followUpFilterValue(value)))) : selected)];
  }));
  return { view, slicers };
}

function compactStateForPersistence(value) {
  const compact = {
    ...value,
    workflowCommandQueue: Array.isArray(value.workflowCommandQueue) ? value.workflowCommandQueue.slice(-100) : [],
    reportRunTrail: Array.isArray(value.reportRunTrail) ? value.reportRunTrail.slice(-50) : [],
    reportExportJobTrail: Array.isArray(value.reportExportJobTrail) ? value.reportExportJobTrail.slice(-50) : [],
    exportGovernanceActivityTrail: Array.isArray(value.exportGovernanceActivityTrail) ? value.exportGovernanceActivityTrail.slice(-75) : [],
    claimQueueWorkAuditTrail: Array.isArray(value.claimQueueWorkAuditTrail) ? value.claimQueueWorkAuditTrail.slice(-50) : [],
    authorizationFailures: Array.isArray(value.authorizationFailures) ? value.authorizationFailures.slice(-50) : [],
    accessDecisions: Array.isArray(value.accessDecisions) ? value.accessDecisions.slice(-50) : [],
    accessAdminPreviews: Array.isArray(value.accessAdminPreviews) ? value.accessAdminPreviews.slice(-25) : [],
    exportGovernanceActivityExportPreview: null,
    reportExportPackagePreview: null,
    trackerWorkbookExportPreview: null,
    selectedExportJobDownloadPreview: null,
    selectedExportJobDetail: null,
    queueWorkExportPreview: null,
    selectedReportRun: null,
    reportRunAuditExportPreview: null
  };
  const audit = Array.isArray(value.audit) ? value.audit.slice(-100) : [];
  compact.audit = [
    ...audit,
    { at: timestamp(), by: "System", text: "Local browser state was compacted after storage quota pressure; durable source data was not changed." }
  ];
  return compact;
}

function saveState() {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    try {
      state = compactStateForPersistence(state);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn("TMS portal local state compacted after storage write failed.", error);
      }
    } catch (compactError) {
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn("TMS portal local state could not be persisted.", compactError);
      }
    }
  }
}

// Performance: render() calls this on EVERY render, including cosmetic ones
// (tab switch, selection toggle) where no claim data changed. Blowing away the
// financial WeakMaps there throws away valid memoized claim/service views. The
// caches are keyed on the merged claim objects returned by getClaims(), which
// are now stable across renders (same references until claimsDataVersion bumps),
// so it is safe to keep them until the data version actually changes.
let lastDerivedResetVersion = -1;
function resetDerivedCaches() {
  if (claimsDataVersion === lastDerivedResetVersion) return;
  derivedClaimCache = new WeakMap();
  derivedServiceCache = new WeakMap();
  lastDerivedResetVersion = claimsDataVersion;
}

function claimCache(claim) {
  if (!claim || typeof claim !== "object") return {};
  let cache = derivedClaimCache.get(claim);
  if (!cache) {
    cache = {};
    derivedClaimCache.set(claim, cache);
  }
  return cache;
}

function serviceCache(service) {
  if (!service || typeof service !== "object") return {};
  let cache = derivedServiceCache.get(service);
  if (!cache) {
    cache = {};
    derivedServiceCache.set(service, cache);
  }
  return cache;
}

// User management: SuperAdmin can add/remove offshore (or stateside)
// biller names. Custom users persist in localStorage so the
// 'Assign to' dropdown stays populated across reloads.
const CUSTOM_USERS_KEY = "tms.portal.customUsers";

function hydrateCustomUsers() {
  try {
    mergeCustomUsers(readCustomUsersFromStorage());
    // Refresh the bulk-assign dropdown + active-user picker so newly
    // restored offshore billers appear immediately.
    populateUserSelect();
  } catch (_) {}
}

function readCustomUsersFromStorage() {
  try {
    const stored = JSON.parse(localStorage.getItem(CUSTOM_USERS_KEY) || "[]");
    return Array.isArray(stored) ? stored : [];
  } catch (_) {
    return [];
  }
}

function mergeCustomUsers(customUsers) {
  if (!Array.isArray(customUsers)) return;
  customUsers.forEach((u) => {
    if (!u || !u.name || !u.role) return;
    // Collision check mirrors the add/edit uniqueness rules: trimmed,
    // whitespace-collapsed, case-insensitive — "platform  admin " must not
    // slip past the built-in "Platform Admin".
    const incomingName = String(u.name).trim().replace(/\s+/g, " ");
    if (!incomingName || users.some((existing) => String(existing.name).trim().replace(/\s+/g, " ").toLowerCase() === incomingName.toLowerCase())) return;
    users.push({
      name: incomingName,
      email: String(u.email || ""),
      role: canonicalRole(u.role),
      custom: true,
      orgIds: normalizeUserScopeList(u.orgIds),
      clientNames: normalizeUserScopeList(u.clientNames),
      payerNames: normalizeUserScopeList(u.payerNames),
      status: normalizeUserStatus(u.status),
      // Survives the reload: "we disabled the portal account but their static
      // users-JSON login is still live" is exactly the fact an admin must not
      // lose track of, and it cannot be re-derived from the roster listing.
      staticLaneNotRevoked: Boolean(u.staticLaneNotRevoked),
      inviteId: String(u.inviteId || ""),
      inviteProvider: String(u.inviteProvider || ""),
      inviteMessageId: String(u.inviteMessageId || ""),
      invitedAt: String(u.invitedAt || "")
    });
  });
}

function persistCustomUsers() {
  try {
  const custom = users.filter((u) => u.custom && !u.directoryAuthoritative).map((u) => ({
      name: u.name,
      email: u.email || "",
      role: u.role,
      orgIds: normalizeUserScopeList(u.orgIds),
      clientNames: normalizeUserScopeList(u.clientNames),
      payerNames: normalizeUserScopeList(u.payerNames),
      status: normalizeUserStatus(u.status),
      staticLaneNotRevoked: Boolean(u.staticLaneNotRevoked),
      inviteId: String(u.inviteId || ""),
      inviteProvider: String(u.inviteProvider || ""),
      inviteMessageId: String(u.inviteMessageId || ""),
      invitedAt: String(u.invitedAt || "")
    }));
    localStorage.setItem(CUSTOM_USERS_KEY, JSON.stringify(custom));
    // Kept as a WRITE-ONLY breadcrumb. It existed so the deleted default-roster
    // seeder could not re-inject a name an admin had removed; nothing reads it
    // now that no roster ships (the stored roster is simply the only roster).
    // Left in place because browsers already carry it and a migration to remove
    // a key nobody reads buys nothing.
    localStorage.setItem("tms.portal.offshoreRosterTouched", "1");
  } catch (_) {}
  // Any user-directory write can change the active user's tenant scope, which
  // drives canAccessClaim. Invalidate the getClaims cache so a follow-on
  // "view as" never serves a stale wrong-tenant array.
  bumpClaimsDataVersion();
}

function normalizeUserScopeList(value) {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || "")
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeUserStatus(status) {
  return ["active", "pending_invite", "disabled"].includes(status) ? status : "pending_invite";
}

function userStatusLabel(status) {
  return {
    active: "Active",
    pending_invite: "Invite sent",
    disabled: "Disabled"
  }[normalizeUserStatus(status)] || "Invite sent";
}

// The roster of offshore billers who can currently receive claims. Deactivated
// (status "disabled") billers stay in the directory and keep their history, but
// drop out of the "Assign to..." dropdown so no new work routes to someone who
// has left or is on hold. Active and pending-invite billers both remain
// assignable.
function assignableOffshoreBillers() {
  return users.filter((u) =>
    canonicalRole(u.role) === "OffshoreBiller"
    && normalizeUserStatus(u.status) !== "disabled"
  );
}

// Claims currently routed to a given biller. Effective owner is the workflow
// override when present, otherwise the claim's own assignedTo — mirroring how
// the rest of the portal resolves ownership.
function claimsAssignedTo(name) {
  const targetOwner = String(name || "").trim();
  if (!targetOwner || !Array.isArray(sourceClaims)) return [];
  const wfMap = (state && state.workflowByClaim) || {};
  return sourceClaims.filter((claim) => {
    const wf = wfMap[claim.claimId];
    const owner = wf && wf.assignedTo !== undefined ? wf.assignedTo : claim.assignedTo;
    return String(owner || "").trim() === targetOwner;
  });
}

// Re-point a set of claims to another owner ("" returns them to the unassigned
// pool). Writes BOTH the source claim and the persistent workflowByClaim record
// so the change survives reload and shows in every view, then persists.
function reassignClaims(claims, toName, options = {}) {
  if (!Array.isArray(claims) || !claims.length) return;
  const owner = String(toName || "");
  const newInstruction = String(options.instruction || "").trim();
  const nowIso = new Date().toISOString();
  if (state && !state.workflowByClaim) state.workflowByClaim = {};
  claims.forEach((claim) => {
    const from = String(claim.assignedTo || "").trim() || "Unassigned";
    const to = owner || "Unassigned";
    // The assign API requires a due date: keep the caller's, else the claim's
    // existing one, else today — never enqueue a command the server must 400.
    const due = owner ? String(options.due || claim.assignmentDue || todayDateString()) : "";
    claim.assignedTo = owner;
    if (owner && newInstruction) { claim.assignmentInstruction = newInstruction; claim.instructionBy = activeInternalUser(); claim.instructionAt = nowIso; }
    if (state && state.workflowByClaim) {
      state.workflowByClaim[claim.claimId] = {
        ...(state.workflowByClaim[claim.claimId] || {}),
        assignedTo: owner,
        ...(owner
          ? {
            assignmentDue: due,
            // Assign owns the manager instruction (OFFSHORE_HANDOFF_SPEC §1);
            // it never touches the offshore-owned reviewNote.
            // A reassign with no instruction PRESERVES the existing one
            // (guardian N1: never silently wipe manager intent).
            assignmentInstruction: newInstruction || String(claim.assignmentInstruction || "").trim(),
            instructionBy: newInstruction ? activeInternalUser() : String(claim.instructionBy || ""),
            instructionAt: newInstruction ? nowIso : String(claim.instructionAt || ""),
            ...(from === "Unassigned" && !claim.assignedAt ? { assignedAt: nowIso } : {})
          }
          // Unassign clears every handoff field explicitly (backend parity):
          // owner, due, status, instruction, bounce reason, offshore note.
          : { assignedBy: "", assignmentDue: "", assignmentStatus: "Unassigned", assignmentInstruction: "", instructionBy: "", instructionAt: "", bounceReason: "", bounceBy: "", bounceAt: "", reviewNote: "" })
      };
    }
    // E2E finding 1 (CRITICAL): board/roster assign paths were localStorage-
    // only. Every reassign now enqueues the SAME workflow command the All Data
    // bulk path sends, so the server mirror follows the board.
    enqueueWorkflowCommand("assign", {
      claimId: claim.claimId,
      ...workflowTargetFieldsForClaim(claim.claimId),
      assignedTo: owner,
      assignmentDue: due,
      ...(owner ? { instruction: newInstruction || String(claim.assignmentInstruction || "").trim() } : { assignmentStatus: "Unassigned" })
    });
    if (from !== to) {
      const text = from === "Unassigned"
        ? `${claim.claimId}: assigned to ${to}`
        : (to === "Unassigned" ? `${claim.claimId}: unassigned from ${from}` : `${claim.claimId}: reassigned from ${from} to ${to}`);
      audit(text, activeInternalUser());
    }
  });
  bumpClaimsDataVersion();
  saveState();
}

function assignClaimFromBoard(claimId, owner, options = {}) {
  if (!hasPermission(PERMISSIONS.ASSIGN_CLAIMS)) return denyAction(PERMISSIONS.ASSIGN_CLAIMS, "assign claim");
  const name = String(owner || "").trim();
  if (!name) { showToast("Pick an owner to assign."); return; }
  const claim = getClaim(claimId);
  if (!claim) return;
  reassignClaims([claim], name, options);
  // reassignClaims sets assignedTo + the workflow mirror; stamp who assigned and
  // advance a freshly-routed claim out of the unassigned pool.
  const by = currentUser()?.name || "";
  claim.assignedBy = by;
  if (!claim.assignmentStatus || claim.assignmentStatus === "Unassigned") claim.assignmentStatus = "Assigned";
  if (state.workflowByClaim) {
    state.workflowByClaim[claim.claimId] = {
      ...(state.workflowByClaim[claim.claimId] || {}),
      assignedBy: by,
      assignmentStatus: claim.assignmentStatus
    };
  }
  saveState();
  renderAssignments();
  showToast(`Assigned ${claim.claimId} to ${name}`);
}

function bulkAssignFromBoard(claimIds, owner, options = {}) {
  if (!hasPermission(PERMISSIONS.ASSIGN_CLAIMS)) return denyAction(PERMISSIONS.ASSIGN_CLAIMS, "assign claims");
  const name = String(owner || "").trim();
  if (!name) { showToast("Pick an owner to assign."); return; }
  const ids = Array.isArray(claimIds) ? claimIds.map(String) : [];
  const claims = ids.map((id) => getClaim(id)).filter(Boolean);
  if (!claims.length) { showToast("Select at least one claim to assign."); return; }
  reassignClaims(claims, name, options);
  const by = currentUser()?.name || "";
  claims.forEach((claim) => {
    claim.assignedBy = by;
    if (!claim.assignmentStatus || claim.assignmentStatus === "Unassigned") claim.assignmentStatus = "Assigned";
    if (state.workflowByClaim) {
      state.workflowByClaim[claim.claimId] = {
        ...(state.workflowByClaim[claim.claimId] || {}),
        assignedBy: by,
        assignmentStatus: claim.assignmentStatus
      };
    }
  });
  assignmentIntakeSelection.clear();
  saveState();
  renderAssignments();
  showToast(`Assigned ${claims.length} ${claims.length === 1 ? "claim" : "claims"} to ${name}.`);
}

// ===========================================================================
// D3 S4 - Manage Groups. CLIENT groups (organizations), not permission groups:
// this product has no user-created permission bundles, and `permissionGroups`
// is a caption for the permission matrix, not an entity.
//
// portal.organizations has been WRITTEN since the first tenant-persistence
// release and READ by nothing, so every org name the portal shows is scraped
// out of claim data. That is the whole reported problem, stated exactly: a
// client group with no claims does not exist to this product, and there was
// nowhere to name one before its first claim landed. This is that surface.
//
// THERE IS NO ARCHIVE CONTROL, and its absence is the honest answer rather
// than an oversight: `organizations.status` is inert - nothing anywhere reads
// it - so an archive button would present itself as a revocation while
// revoking nothing at all. The manage tab says so in words.
// ===========================================================================
const userGroupMgmtState = {
  tab: "manage",
  query: "",
  renamingOrgId: "",
  busy: false,
  busyAction: "",
  notice: "",
  noticeTone: "info",
  onboardingStatus: "idle",
  onboardings: [],
  onboardingError: "",
  connectorAvailable: null,
  automaticIdAvailable: false,
  contractVersion: 1,
  reviewSelection: null,
  selectedOnboardingId: "",
  selectedOnboardingMode: "connect",
  draftOrgId: "",
  draftOrgName: "",
  commandKeys: Object.create(null),
  // Company deactivation (offboarding): preview -> reason -> confirm.
  deactivationAvailable: false,
  inactiveOrgs: [],
  showInactive: false,
  deactivatingOrgId: "",
  deactivationPreview: null,
  deactivationError: "",
  deactivationReason: "",
  reactivatingOrgId: "",
  reactivationReason: ""
};
let organizationOnboardingRefreshTimer = null;

function normalizePortalOrgRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      orgId: String((row && row.orgId) || "").trim(),
      orgName: String((row && row.orgName) || "").trim(),
      status: String((row && row.status) || "").trim(),
      source: String((row && row.source) || "").trim()
    }))
    .filter((row) => row.orgId);
}

// The server scope-filters this roster; the client never widens it. An empty
// list from a principal with no org scope is the CORRECT answer, not an error
// state - so nothing here treats "no groups" as a failure to retry.
function requestPortalOrgDirectory({ force = false, groupManagement = false } = {}) {
  // These are management-only routes, even when another workspace wants labels.
  // Client/Offshore views use their existing scoped claim/company label map.
  const canRead = () => groupManagement
    ? isTrustedSuperAdminSession() && hasPermission(PERMISSIONS.MANAGE_GROUPS)
    : isTrustedUserManagerSession() && hasPermission(PERMISSIONS.MANAGE_USERS);
  if (!canRead()) {
    // The two routes share a cache. A denied call to one must not clear a
    // directory the current session may still read through the other route.
    const retainsDirectoryAccess = (isTrustedUserManagerSession() && hasPermission(PERMISSIONS.MANAGE_USERS))
      || (isTrustedSuperAdminSession() && hasPermission(PERMISSIONS.MANAGE_GROUPS));
    if (!retainsDirectoryAccess) {
      portalOrgDirectoryReadSequence += 1;
      portalOrgDirectoryState = { key: "", status: "idle", orgs: [], error: "" };
    }
    return Promise.resolve();
  }
  if (!trackerApiEnabled() || typeof fetch !== "function") return Promise.resolve();
  const base = trackerApiBase();
  if (!base) return Promise.resolve();
  const readScope = additionalPortalDataReadScope();
  const key = JSON.stringify([readScope, groupManagement]);
  if (!force && portalOrgDirectoryState.key === key && ["pending", "ok", "error"].includes(portalOrgDirectoryState.status)) {
    // A read that has not landed yet is not a cache hit. Handing back an
    // already-resolved promise tells anyone who awaited this that the roster
    // is ready when it is still empty, and they conclude there is nothing
    // there. Settled statuses still resolve at once.
    return portalOrgDirectoryState.status === "pending" && portalOrgDirectoryInFlight
      ? portalOrgDirectoryInFlight
      : Promise.resolve();
  }
  const sequence = ++portalOrgDirectoryReadSequence;
  const current = () => canRead() && sequence === portalOrgDirectoryReadSequence && readScope === additionalPortalDataReadScope();
  const token = trackerApiToken();
  portalOrgDirectoryState = { key, status: "pending", orgs: portalOrgDirectoryState.key === key ? portalOrgDirectoryState.orgs : [], error: "" };
  const path = groupManagement ? "/api/orgs" : "/api/users/orgs";
  const request = fetch(additionalPortalDataReadUrl(`${base}${path}`), {
    cache: "no-store",
    headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) }
  })
    .then(async (response) => {
      if (!current()) return;
      const body = await response.json().catch(() => ({}));
      if (!current()) return;
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") {
          notePortalSessionExpired();
          portalOrgDirectoryState = { key,
            status: "error",
            orgs: portalOrgDirectoryState.orgs,
            error: "Your session expired. Sign in again — this page will still be here."
          };
        } else {
          portalOrgDirectoryState = { key, status: "error", orgs: [], error: body.message || body.error || `HTTP ${response.status}` };
        }
      } else {
        portalOrgDirectoryState = { key, status: "ok", orgs: normalizePortalOrgRows(body.orgs), error: "" };
      }
      renderUserGroupMgmtBody();
      const userMgmtModalEl = document.getElementById("userMgmtModal");
      if (userMgmtModalEl && !userMgmtModalEl.classList.contains("hidden")) renderUserMgmtList();
      // The Notifications company list is built from the same label map, so a
      // roster that lands while the bell is open has to repaint it too (it
      // no-ops while the panel is hidden).
      if (typeof renderWorkflowNotificationChrome === "function") renderWorkflowNotificationChrome();
      if (typeof renderWorkflowNotificationsModal === "function") renderWorkflowNotificationsModal();
      // Every surface that names an org from this directory has to repaint, not
      // just the screen that happened to ask for it. Client Action Pending renders
      // company names on its picker AND on every card.
      //
      // ON ITS OWN SCREEN ONLY (2026-09-22). #clientActionPendingBody exists in
      // the shell on every view, so this repainted a hidden panel during All
      // Data boot - and renderClientActionPending() ends by kicking
      // loadClientActionCases() whenever the workspace is idle. That is the
      // 927 KB GET /api/client-action the cold-load measurement caught racing the
      // grid query, plus the GET /api/client-action/:org/:case its auto-select
      // fires straight after (perf/ALL_DATA_LATENCY_2026-09-22.md). Nothing on
      // screen needed either: the sidebar badge reads its total from
      // /api/navigation/pending-counts (see updateClientActionBadge), and
      // renderActivePanel paints this panel - and asks for its rows - the moment
      // the view is opened. The other two surfaces below already gate the same way.
      if (state.view === "clientActionPending" && typeof renderClientActionPending === "function") renderClientActionPending();
      const userWizard = document.getElementById("userAddWizard");
      if (userWizard && !userWizard.classList.contains("hidden") && userAddWizardState) renderUserAddWizard();
      if (state.view === "audit" && typeof renderSupervisorTrail === "function") renderSupervisorTrail();
      if (state.view === "assignments" && typeof renderAssignments === "function") renderAssignments();
    })
    .catch((error) => {
      if (!current()) return;
      portalOrgDirectoryState = { key, status: "error", orgs: [], error: error.message };
      renderUserGroupMgmtBody();
      const userWizard = document.getElementById("userAddWizard");
      if (userWizard && !userWizard.classList.contains("hidden") && userAddWizardState) renderUserAddWizard();
    });
  const tracked = request.then(() => {
    if (portalOrgDirectoryInFlight === tracked) portalOrgDirectoryInFlight = null;
  });
  portalOrgDirectoryInFlight = tracked;
  return tracked;
}

async function postPortalOrgCommand(path, payload) {
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") return { ok: false, message: "The portal is temporarily unavailable, so nothing was saved." };
  const token = trackerApiToken();
  try {
    const response = await fetch(`${base}${path}`, {
      method: "POST",
      cache: "no-store",
      headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload)
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.error) return { ok: false, message: body.message || body.error || `HTTP ${response.status}` };
    return { ok: true, body };
  } catch (error) {
    return { ok: false, message: error.message };
  }
}

async function getPortalOrgJson(path) {
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") return { ok: false, message: "The portal is temporarily unavailable." };
  const token = trackerApiToken();
  try {
    const response = await fetch(`${base}${path}`, { cache: "no-store", headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) } });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.error) return { ok: false, message: body.message || body.error || `HTTP ${response.status}` };
    return { ok: true, body };
  } catch (error) {
    return { ok: false, message: error.message };
  }
}

// Inactive companies come from the Super Admin management list only; the shared
// directory cache (pickers, labels) keeps showing active companies.
async function loadUserGroupInactiveOrgs() {
  if (!isTrustedSuperAdminSession() || !hasPermission(PERMISSIONS.MANAGE_GROUPS)) return;
  const result = await getPortalOrgJson("/api/orgs/manage");
  if (!result.ok) return;
  userGroupMgmtState.deactivationAvailable = result.body.deactivationAvailable === true;
  userGroupMgmtState.inactiveOrgs = normalizePortalOrgRows(result.body.orgs).filter((org) => org.status === "inactive");
  if (userGroupMgmtState.tab === "manage") renderUserGroupMgmtBody();
}

function companyDeactivationPersonText(person) {
  if (person.action === "disable") return "Login turned off (this was their only company)";
  if (person.action === "review") return "Not changed: this is their only company. Give them another company in Manage Users";
  const others = Number(person.remainingCompanies) || 0;
  return `Company removed from their access (keeps ${formatCount(others)} other ${others === 1 ? "company" : "companies"})`;
}

function userGroupDeactivationPanelHtml(org) {
  const preview = userGroupMgmtState.deactivationPreview;
  const label = org.orgName || org.orgId;
  if (userGroupMgmtState.deactivationError) {
    return `<div class="user-mgmt-group-deactivate" role="region" aria-label="Deactivate ${escapeHtml(label)}">
      <p class="user-mgmt-group-notice user-mgmt-group-notice--error">${escapeHtml(userGroupMgmtState.deactivationError)}</p>
      <div class="user-mgmt-group-actions"><button class="ghost-button" type="button" data-group-deactivate-cancel="1">Close</button></div>
    </div>`;
  }
  if (!preview || preview.org?.orgId !== org.orgId) return `<div class="user-mgmt-group-deactivate"><p class="user-mgmt-group-field-hint">Checking who has access…</p></div>`;
  const people = preview.people.length
    ? `<ul class="user-mgmt-group-impact">${preview.people.map((person) => `<li><strong>${escapeHtml(person.userKey)}</strong> <span>${escapeHtml(person.role)}</span> — ${escapeHtml(companyDeactivationPersonText(person))}</li>`).join("")}</ul>`
    : `<p class="user-mgmt-group-field-hint">No portal login is limited to this company.</p>`;
  const teams = preview.teams.length
    ? `<ul class="user-mgmt-group-impact">${preview.teams.map((team) => `<li><strong>${escapeHtml(team.name || team.teamId)}</strong> — ${team.action === "archive" ? "Team archived (this was its only company)" : "Company removed from the team"}</li>`).join("")}</ul>`
    : `<p class="user-mgmt-group-field-hint">No staff team covers this company.</p>`;
  const collection = preview.collection ? `<p class="user-mgmt-group-field-hint">Automatic claim pulls for this company will be paused.</p>` : "";
  return `<div class="user-mgmt-group-deactivate" role="region" aria-label="Deactivate ${escapeHtml(label)}">
    <p class="user-mgmt-group-field-hint">${escapeHtml(label)} will be hidden from company pickers and work queues and the claim loader will stop pulling it. Its claim history stays in reports. Nothing is deleted, and you can reactivate it later.</p>
    <p class="user-mgmt-group-field-label">People</p>${people}
    <p class="user-mgmt-group-field-label">Staff teams</p>${teams}
    ${collection}
    <label class="user-mgmt-group-field-label" for="userGroupDeactivateReason">Reason</label>
    <input id="userGroupDeactivateReason" class="user-mgmt-group-input" type="text" maxlength="400" placeholder="For example: Company ended services" value="${escapeHtml(userGroupMgmtState.deactivationReason)}" data-group-deactivate-reason="1" autocomplete="off">
    <div class="user-mgmt-group-actions">
      <button class="primary-button is-danger" type="button" data-group-deactivate-confirm="${escapeHtml(org.orgId)}"${userGroupMgmtState.busy ? " disabled" : ""}>${userGroupMgmtState.busy ? "Deactivating…" : "Deactivate company"}</button>
      <button class="ghost-button" type="button" data-group-deactivate-cancel="1">Cancel</button>
    </div>
  </div>`;
}

async function startUserGroupDeactivation(orgId) {
  userGroupMgmtState.deactivatingOrgId = orgId;
  userGroupMgmtState.deactivationPreview = null;
  userGroupMgmtState.deactivationError = "";
  userGroupMgmtState.renamingOrgId = "";
  renderUserGroupMgmtList();
  const result = await getPortalOrgJson(`/api/orgs/deactivation-preview?orgId=${encodeURIComponent(orgId)}`);
  if (userGroupMgmtState.deactivatingOrgId !== orgId) return;
  if (!result.ok) userGroupMgmtState.deactivationError = result.message;
  else userGroupMgmtState.deactivationPreview = result.body;
  renderUserGroupMgmtList();
  document.getElementById("userGroupDeactivateReason")?.focus({ preventScroll: true });
}

async function submitUserGroupDeactivation(orgId) {
  const preview = userGroupMgmtState.deactivationPreview;
  if (userGroupMgmtState.busy || !preview || preview.org?.orgId !== orgId) return;
  const reason = String(userGroupMgmtState.deactivationReason || "").replace(/\s+/g, " ").trim();
  if (reason.length < 3) { showToast("Enter a reason for deactivating this company."); return; }
  userGroupMgmtState.busy = true;
  renderUserGroupMgmtList();
  const result = await postPortalOrgCommand("/api/orgs/deactivate", { orgId, reason, planDigest: preview.digest });
  userGroupMgmtState.busy = false;
  if (!result.ok) {
    // A changed plan comes back as a refusal; show the fresh list to confirm again.
    userGroupMgmtState.notice = result.message;
    userGroupMgmtState.noticeTone = "error";
    renderUserGroupMgmtBody();
    await startUserGroupDeactivation(orgId);
    return;
  }
  const receipt = result.body.receipt || {};
  const followUps = [...(receipt.people || []), ...(receipt.teams || [])].filter((row) => !row.ok || row.action === "review").length
    + (receipt.collection && !receipt.collection.ok ? 1 : 0);
  userGroupMgmtState.deactivatingOrgId = "";
  userGroupMgmtState.deactivationPreview = null;
  userGroupMgmtState.deactivationReason = "";
  userGroupMgmtNotice(`${result.body.orgName || orgId} is now inactive.${followUps ? ` ${formatCount(followUps)} ${followUps === 1 ? "item needs" : "items need"} follow-up in Manage Users or Teams.` : ""}`, followUps ? "error" : "info");
  await Promise.all([requestPortalOrgDirectory({ force: true, groupManagement: true }), loadUserGroupInactiveOrgs()]);
}

async function submitUserGroupReactivation(orgId) {
  if (userGroupMgmtState.busy) return;
  const reason = String(userGroupMgmtState.reactivationReason || "").replace(/\s+/g, " ").trim();
  if (reason.length < 3) { showToast("Enter a reason for reactivating this company."); return; }
  userGroupMgmtState.busy = true;
  renderUserGroupMgmtBody();
  const result = await postPortalOrgCommand("/api/orgs/reactivate", { orgId, reason });
  userGroupMgmtState.busy = false;
  if (!result.ok) { userGroupMgmtNotice(result.message, "error"); return; }
  userGroupMgmtState.reactivatingOrgId = "";
  userGroupMgmtState.reactivationReason = "";
  userGroupMgmtNotice(`${result.body.orgName || orgId} is active again. Give people access in Manage Users; access is not restored automatically.`, "info");
  await Promise.all([requestPortalOrgDirectory({ force: true, groupManagement: true }), loadUserGroupInactiveOrgs()]);
}

function userGroupInactiveHtml() {
  const rows = userGroupMgmtState.inactiveOrgs;
  if (!rows.length) return "";
  const toggle = `<button class="ghost-button" type="button" data-group-show-inactive="1" aria-expanded="${userGroupMgmtState.showInactive ? "true" : "false"}">${userGroupMgmtState.showInactive ? "Hide" : "Show"} inactive companies (${formatCount(rows.length)})</button>`;
  if (!userGroupMgmtState.showInactive) return `<div class="user-mgmt-group-inactive">${toggle}</div>`;
  return `<div class="user-mgmt-group-inactive">${toggle}<div class="user-mgmt-group-list">${rows.map((org) => {
    const label = org.orgName || org.orgId;
    const open = userGroupMgmtState.reactivatingOrgId === org.orgId;
    return `<div class="user-mgmt-group-row${open ? " is-renaming" : ""}" data-group-inactive-row="${escapeHtml(org.orgId)}">
      <div class="user-mgmt-group-identity"><span class="user-mgmt-group-name">${escapeHtml(label)} <span class="pill gray">Inactive</span></span>
        ${open ? `<label class="user-mgmt-group-field-label" for="userGroupReactivateReason">Reason</label>
        <input id="userGroupReactivateReason" class="user-mgmt-group-input" type="text" maxlength="400" value="${escapeHtml(userGroupMgmtState.reactivationReason)}" data-group-reactivate-reason="1" autocomplete="off">` : ""}
      </div>
      <div class="user-mgmt-group-actions">${open
        ? `<button class="primary-button" type="button" data-group-reactivate-confirm="${escapeHtml(org.orgId)}"${userGroupMgmtState.busy ? " disabled" : ""}>Reactivate</button><button class="ghost-button" type="button" data-group-reactivate-cancel="1">Cancel</button>`
        : `<button class="ghost-button" type="button" data-group-reactivate="${escapeHtml(org.orgId)}"${userGroupMgmtState.deactivationAvailable ? "" : " disabled"}>Reactivate</button>`}</div>
    </div>`;
  }).join("")}</div></div>`;
}

function normalizeCompanyOnboardingReview(review) {
  if (!review || typeof review.digest !== "string" || !/^[a-f0-9]{64}$/i.test(review.digest)) return null;
  const count = value => Number.isSafeInteger(value) && value >= 0 ? value : 0;
  return { digest: review.digest, companyName: String(review.companyName || ""), credentialVersion: count(review.credentialVersion),
    resourceCount: count(review.resourceCount), sourceRowCount: count(review.sourceRowCount), serviceRowCount: count(review.serviceRowCount),
    checks: (Array.isArray(review.checks) ? review.checks : []).map(item => ({ code: String(item?.code || ""), status: String(item?.status || "") })),
    setupWarnings: (Array.isArray(review.setupWarnings) ? review.setupWarnings : []).map(item => ({ code: String(item?.code || ""), count: count(item?.count) })) };
}

function normalizeOrganizationOnboardingRows(rows) {
  const statuses = new Set(["draft", "running", "waiting_upstream", "action_required", "ready_to_activate", "active", "cancelled"]);
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      onboardingId: String(row?.onboardingId || "").trim(),
      orgId: String(row?.orgId || "").trim(),
      orgName: String(row?.orgName || "").trim(),
      status: statuses.has(String(row?.status || "")) ? String(row.status) : "action_required",
      contractVersion: row?.contractVersion === 2 ? 2 : 1,
      connectionStatus: String(row?.connectionStatus || ""),
      financialReadiness: ["pending", "needs_setup", "ready"].includes(row?.financialReadiness) ? row.financialReadiness : "pending",
      paused: row?.paused === true,
      credentialRetryRequired: row?.credentialRetryRequired === true,
      hasAcceptedSource: row?.hasAcceptedSource === true,
      lastScheduledAtUtc: String(row?.lastScheduledAtUtc || ""),
      nextScheduledAtUtc: String(row?.nextScheduledAtUtc || ""),
      review: normalizeCompanyOnboardingReview(row?.review),
      lastCompletedStep: String(row?.lastCompletedStep || "draft").trim(),
      stateVersion: Number.isInteger(Number(row?.stateVersion)) ? Number(row.stateVersion) : 0,
      credentialVersion: Number.isInteger(Number(row?.credentialVersion)) ? Number(row.credentialVersion) : 0,
      sourceRowCount: Math.max(0, Number(row?.sourceRowCount) || 0),
      warehouseRowCount: Math.max(0, Number(row?.warehouseRowCount) || 0),
      snapshotRowCount: Math.max(0, Number(row?.snapshotRowCount) || 0),
      failedStep: String(row?.failedStep || "").trim(),
      failureCode: String(row?.failureCode || "").trim(),
      retryable: Boolean(row?.retryable),
      attemptCount: Math.max(0, Number(row?.attemptCount) || 0),
      createdAtUtc: String(row?.createdAtUtc || "").trim(),
      updatedAtUtc: String(row?.updatedAtUtc || "").trim(),
      activatedAtUtc: String(row?.activatedAtUtc || "").trim()
    }))
    .filter((row) => row.onboardingId && row.orgId)
    .sort((a, b) => String(b.updatedAtUtc || b.createdAtUtc).localeCompare(String(a.updatedAtUtc || a.createdAtUtc)));
}

function newOrganizationOnboardingCommandKey() {
  const cryptoObject = typeof crypto !== "undefined" ? crypto : null;
  if (cryptoObject?.randomUUID) return cryptoObject.randomUUID();
  if (cryptoObject?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoObject.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  }
  return `onboard-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function organizationOnboardingCommandKey(slot) {
  if (!userGroupMgmtState.commandKeys[slot]) userGroupMgmtState.commandKeys[slot] = newOrganizationOnboardingCommandKey();
  return userGroupMgmtState.commandKeys[slot];
}

function clearOrganizationOnboardingCommandKey(slot) {
  delete userGroupMgmtState.commandKeys[slot];
}

function organizationOnboardingErrorText(result) {
  const code = String(result?.code || result?.body?.error || "").toLowerCase();
  if (result?.status === 401) return "Your session expired. Sign in again — this setup is still saved.";
  if (result?.status === 403) return "Only a Super Admin with company setup access can do that.";
  if (result?.status === 503 || /unconfigured|upstream|connection_unavailable/.test(code)) {
    return "The company connection is unavailable right now. This setup is saved and can continue when the connection is ready.";
  }
  if (result?.status === 409 && /stale|version/.test(code)) return "This setup changed after you opened it. It has been refreshed; try again.";
  if (result?.status === 409 && /idempotency/.test(code)) return "That setup request no longer matches this company. Refresh and try again.";
  const message = String(result?.message || "").trim();
  if (message && !/\b(api|sql|database|backend|pipeline|warehouse|key vault|secret reference)\b/i.test(message)) return message;
  return "Setup could not continue. Nothing was lost; try again or check the saved setup below.";
}

async function organizationOnboardingRequest(method, path, { payload, idempotencyKey } = {}) {
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") return { ok: false, status: 503, code: "connection_unavailable" };
  const token = trackerApiToken();
  const headers = { ...(token ? { authorization: `Bearer ${token}` } : {}) };
  if (payload !== undefined) headers["content-type"] = "application/json";
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  try {
    const response = await fetch(`${base}${path}`, {
      method,
      cache: "no-store",
      headers,
      ...(payload !== undefined ? { body: JSON.stringify(payload) } : {})
    });
    const body = await response.json().catch(() => ({}));
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok || body.error) {
      return { ok: false, status: response.status, code: body.error || "", message: body.message || "", body };
    }
    return { ok: true, status: response.status, body };
  } catch (_) {
    return { ok: false, status: 503, code: "connection_unavailable" };
  }
}

function upsertOrganizationOnboarding(row) {
  const normalized = normalizeOrganizationOnboardingRows([row])[0];
  if (!normalized) return null;
  const next = userGroupMgmtState.onboardings.filter((item) => item.onboardingId !== normalized.onboardingId);
  next.push(normalized);
  userGroupMgmtState.onboardings = normalizeOrganizationOnboardingRows(next);
  return normalized;
}

async function requestOrganizationOnboardings({ force = false } = {}) {
  if (!isTrustedSuperAdminSession() || !hasPermission(PERMISSIONS.ORGANIZATIONS_ONBOARD)) return;
  if (!force && ["pending", "ok"].includes(userGroupMgmtState.onboardingStatus)) return;
  userGroupMgmtState.onboardingStatus = "pending";
  userGroupMgmtState.onboardingError = "";
  renderUserGroupMgmtBody();
  const result = await organizationOnboardingRequest("GET", "/api/organization-onboardings");
  if (!result.ok) {
    userGroupMgmtState.onboardingStatus = "error";
    userGroupMgmtState.onboardingError = organizationOnboardingErrorText(result);
    userGroupMgmtState.connectorAvailable = false;
    userGroupMgmtState.automaticIdAvailable = false;
  } else {
    userGroupMgmtState.onboardingStatus = "ok";
    userGroupMgmtState.connectorAvailable = result.body.connectorAvailable === true || result.body.capabilities?.connectorAvailable === true;
    userGroupMgmtState.automaticIdAvailable = result.body.automaticIdAvailable === true;
    userGroupMgmtState.contractVersion = result.body.contractVersion === 2 ? 2 : 1;
    userGroupMgmtState.onboardings = normalizeOrganizationOnboardingRows(result.body.onboardings);
  }
  renderUserGroupMgmtBody();
  scheduleOrganizationOnboardingRefresh();
}

function scheduleOrganizationOnboardingRefresh() {
  if (typeof window === "undefined" || typeof window.setTimeout !== "function") return;
  if (organizationOnboardingRefreshTimer) window.clearTimeout(organizationOnboardingRefreshTimer);
  organizationOnboardingRefreshTimer = null;
  const modal = document.getElementById("userGroupMgmtModal");
  const shouldPoll = modal && !modal.classList.contains("hidden")
    && userGroupMgmtState.onboardings.some((row) => row.status === "running" || (row.contractVersion === 2 && ["checking", "waiting_schedule"].includes(row.connectionStatus)));
  if (!shouldPoll) return;
  organizationOnboardingRefreshTimer = window.setTimeout(() => {
    organizationOnboardingRefreshTimer = null;
    userGroupMgmtState.onboardingStatus = "idle";
    requestOrganizationOnboardings({ force: true });
  }, 4000);
}

function openUserGroupMgmtModal() {
  if (!isTrustedSuperAdminSession() || !hasPermission(PERMISSIONS.ORGANIZATIONS_ONBOARD)) {
    showToast("Only a Super Admin with company setup access can manage companies.");
    return;
  }
  const modal = document.getElementById("userGroupMgmtModal");
  if (!modal) return;
  if (modal.parentElement !== document.body) document.body.appendChild(modal);
  modal.classList.remove("hidden");
  userGroupMgmtState.tab = "manage";
  userGroupMgmtState.query = "";
  userGroupMgmtState.renamingOrgId = "";
  userGroupMgmtState.deactivatingOrgId = "";
  userGroupMgmtState.deactivationPreview = null;
  userGroupMgmtState.reactivatingOrgId = "";
  loadUserGroupInactiveOrgs();
  if (userGroupMgmtState.contractVersion !== 2) {
    userGroupMgmtState.busy = false;
    userGroupMgmtState.busyAction = "";
  }
  userGroupMgmtState.selectedOnboardingId = "";
  userGroupMgmtState.selectedOnboardingMode = "connect";
  userGroupMgmtState.notice = "";
  userGroupMgmtState.noticeTone = "info";
  renderUserGroupMgmtBody();
  activateForegroundModal(modal, {
    initialFocus: "#userGroupMgmtCloseBtn",
    onEscape: closeUserGroupMgmtModal
  });
  requestPortalOrgDirectory({ force: true, groupManagement: true });
  requestOrganizationOnboardings({ force: true });
}

function closeUserGroupMgmtModal() {
  window.TmsSandboxOnboarding?.close();
  const connectionInput = document.getElementById("userGroupAddConnectionKey");
  if (connectionInput) connectionInput.value = "";
  const modal = document.getElementById("userGroupMgmtModal");
  modal?.classList.add("hidden");
  userGroupMgmtState.reviewSelection = null;
  deactivateForegroundModal(modal);
  if (organizationOnboardingRefreshTimer && typeof window !== "undefined") window.clearTimeout(organizationOnboardingRefreshTimer);
  organizationOnboardingRefreshTimer = null;
}

function userGroupMgmtSetTab(tab) {
  const next = tab === "onboard" ? "onboard" : "manage";
  if (userGroupMgmtState.tab === next) return;
  userGroupMgmtState.tab = next;
  userGroupMgmtState.renamingOrgId = "";
  userGroupMgmtState.notice = "";
  renderUserGroupMgmtBody();
  if (next === "onboard" && userGroupMgmtState.onboardingStatus === "idle") requestOrganizationOnboardings({ force: true });
}

function userGroupMgmtFilteredOrgs() {
  const query = String(userGroupMgmtState.query || "").trim().toLowerCase();
  const selected = activeClientGroupOrgIds();
  const orgs = portalOrgDirectoryState.orgs.filter(org => !activeClientGroupTenant() || selected.includes(String(org.orgId)));
  if (!query) return orgs;
  return orgs.filter((org) => `${org.orgName} ${org.orgId}`.toLowerCase().includes(query));
}

function userGroupMgmtNoticeHtml() {
  if (!userGroupMgmtState.notice) return "";
  const tone = userGroupMgmtState.noticeTone === "error" ? " user-mgmt-group-notice--error" : "";
  return `<p class="user-mgmt-group-notice${tone}" role="status">${escapeHtml(userGroupMgmtState.notice)}</p>`;
}

function userGroupMgmtRowHtml(org) {
  const renaming = userGroupMgmtState.renamingOrgId === org.orgId;
  const named = Boolean(org.orgName) && org.orgName !== org.orgId;
  const nameCell = named
    ? `<span class="user-mgmt-group-name">${escapeHtml(org.orgName)}</span>`
    : `<span class="user-mgmt-group-name user-mgmt-group-name--unnamed">Not named yet</span>`;
  if (renaming) {
    return `<div class="user-mgmt-group-row is-renaming" data-group-row="${escapeHtml(org.orgId)}">
      <div class="user-mgmt-group-identity">
        <label class="user-mgmt-group-field-label" for="userGroupRenameInput">New name for ${escapeHtml(named ? org.orgName : "this company")}</label>
        <input id="userGroupRenameInput" class="user-mgmt-group-input" type="text" maxlength="200" value="${escapeHtml(userGroupMgmtState.renameDraft ?? (named ? org.orgName : ""))}" data-group-rename-input="${escapeHtml(org.orgId)}" autocomplete="off">
      </div>
      <div class="user-mgmt-group-actions">
        <button class="primary-button" type="button" data-group-rename-save="${escapeHtml(org.orgId)}"${userGroupMgmtState.busy ? " disabled" : ""}>Save name</button>
        <button class="ghost-button" type="button" data-group-rename-cancel="1">Cancel</button>
      </div>
    </div>`;
  }
  const deactivating = userGroupMgmtState.deactivatingOrgId === org.orgId;
  return `<div class="user-mgmt-group-row${deactivating ? " is-renaming" : ""}" data-group-row="${escapeHtml(org.orgId)}">
    <div class="user-mgmt-group-identity">
      ${nameCell}
    </div>
    <div class="user-mgmt-group-actions">
      <button class="ghost-button" type="button" data-group-rename="${escapeHtml(org.orgId)}">Rename</button>
      ${userGroupMgmtState.deactivationAvailable && !deactivating ? `<button class="ghost-button" type="button" data-group-deactivate="${escapeHtml(org.orgId)}">Deactivate</button>` : ""}
    </div>
    ${deactivating ? userGroupDeactivationPanelHtml(org) : ""}
  </div>`;
}

const managementListPages = new Map();

function managementListPageWindow(key, rows, scope = "") {
  const previous = managementListPages.get(key);
  const size = 25;
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const page = Math.min(pages, Math.max(1, previous?.scope === scope ? previous.page : 1));
  managementListPages.set(key, { page, scope });
  const start = (page - 1) * size;
  return { key, page, pages, total: rows.length, first: rows.length ? start + 1 : 0, last: Math.min(start + size, rows.length), rows: rows.slice(start, start + size) };
}

function managementListPagerHtml(paging, label) {
  return '<nav class="management-list-pagination" aria-label="' + escapeHtml(label) + ' pages">'
    + '<span role="status" aria-live="polite">Showing ' + formatCount(paging.first) + '–' + formatCount(paging.last) + ' of ' + formatCount(paging.total) + '</span>'
    + '<span class="management-list-page-controls">'
    + [[1, 'First'], [paging.page - 1, 'Previous'], [paging.page + 1, 'Next'], [paging.pages, 'Last']].map(([page, text], index) => '<button class="ghost-button" type="button" data-management-page="' + page + '" data-management-list="' + paging.key + '" data-management-direction="' + text + '"' + ((index < 2 ? paging.page <= 1 : paging.page >= paging.pages) ? ' disabled' : '') + '>' + text + '</button>').join('')
    + '<span>Page ' + formatCount(paging.page) + ' of ' + formatCount(paging.pages) + '</span></span></nav>';
}

function bindManagementListPager(host, key, render, beforeRender) {
  host?.querySelectorAll('[data-management-list="' + key + '"]').forEach((button) => {
    button.addEventListener('click', () => {
      const entry = managementListPages.get(key);
      const page = Number(button.dataset.managementPage);
      if (!entry || !Number.isInteger(page) || page < 1) return;
      if (beforeRender) beforeRender();
      entry.page = page;
      const direction = button.dataset.managementDirection;
      render();
      const next = host.querySelector('[data-management-list="' + key + '"][data-management-direction="' + direction + '"]:not(:disabled)')
        || host.querySelector('[data-management-list="' + key + '"]:not(:disabled)');
      next?.focus?.({ preventScroll: true });
      const region = next?.closest?.('.staff-team-list, .user-mgmt-group-list, #userMgmtList, #userMgmtTeamView');
      if (region) region.scrollTop = 0;
    });
  });
}

function userGroupMgmtListHtml() {
  if (portalOrgDirectoryState.status === "pending" && !portalOrgDirectoryState.orgs.length) {
    return `<p class="user-mgmt-group-empty">Loading client groups…</p>`;
  }
  if (portalOrgDirectoryState.status === "error") {
    return `<p class="user-mgmt-group-empty">Client groups are unavailable — ${escapeHtml(portalOrgDirectoryState.error)}</p>`;
  }
  const orgs = userGroupMgmtFilteredOrgs();
  const page = managementListPageWindow("companies", orgs, String(userGroupMgmtState.query || ""));
  if (!orgs.length) {
    return managementListPagerHtml(page, "Companies") + (String(userGroupMgmtState.query || "").trim()
      ? `<p class="user-mgmt-group-empty">No client group matches that search.</p>`
      : `<p class="user-mgmt-group-empty">No client groups on file yet. Onboard the first company.</p>`);
  }
  return managementListPagerHtml(page, "Companies") + page.rows.map((org) => userGroupMgmtRowHtml(org)).join("");
}

function userGroupMgmtManageHtml() {
  const total = userGroupMgmtFilteredOrgs().length;
  return `${userGroupMgmtNoticeHtml()}
    <div class="user-mgmt-group-toolbar">
      <input id="userGroupMgmtSearch" type="search" placeholder="Search by name or org ID" autocomplete="off" aria-label="Search client groups">
      <span class="user-mgmt-group-count" aria-live="polite">${formatNounCount(total, "client group")}</span>
    </div>
    <div id="userGroupMgmtList" class="user-mgmt-group-list">${userGroupMgmtListHtml()}</div>
    ${userGroupInactiveHtml()}
    <p class="user-mgmt-group-footnote">${userGroupMgmtState.deactivationAvailable ? "Deactivate a company when it stops working with us. Its history stays in reports and nothing is deleted." : "Groups cannot be archived here. Revoke a person’s access in Manage Users when needed."}</p>`;
}

const ORGANIZATION_ONBOARDING_STEPS = [
  ["draft", "Company details saved"],
  ["credential_stored", "Connection key protected"],
  ["connection_verified", "Connection checked"],
  ["pipeline_registered", "Data collection prepared"],
  ["ingestion_succeeded", "First company pull complete"],
  ["warehouse_verified", "Company records prepared"],
  ["snapshot_verified", "Portal records prepared"],
  ["portal_verified", "Company visibility checked"],
  ["pipeline_enabled", "Ongoing collection enabled"],
  ["org_registered", "Company added"],
  ["activated", "Onboarding complete"]
];

function organizationOnboardingStatusMeta(row) {
  const status = row?.status || "action_required";
  if (status === "active") return { label: "Active", tone: "success", detail: "The company is ready. You can add its first user now." };
  if (status === "running") return { label: "Setting up", tone: "progress", detail: "Setup is moving through the remaining checks." };
  if (status === "waiting_upstream") return { label: "Connection unavailable", tone: "warning", detail: "The company connection is unavailable right now. This setup is saved and can continue when the connection is ready." };
  if (status === "action_required") return { label: "Needs attention", tone: "danger", detail: row.retryable ? "A check needs another try. The completed work is saved." : "A setup issue needs review before this company can continue." };
  if (status === "ready_to_activate") return { label: "Ready to finish", tone: "progress", detail: "All checks passed. Finish setup to make the company available to users." };
  if (status === "cancelled") return { label: "Cancelled", tone: "muted", detail: "This setup was cancelled. No user access was added." };
  return { label: "Connection key needed", tone: "muted", detail: "Enter the company’s EZClaim connection key to continue." };
}

function organizationOnboardingStepLabel(step) {
  return ORGANIZATION_ONBOARDING_STEPS.find(([key]) => key === step)?.[1] || "Company details saved";
}

function organizationOnboardingProgress(row) {
  if (row.status === "active") return 100;
  const index = ORGANIZATION_ONBOARDING_STEPS.findIndex(([key]) => key === row.lastCompletedStep);
  return Math.max(5, Math.round(((index + 1) / ORGANIZATION_ONBOARDING_STEPS.length) * 100));
}

function organizationOnboardingCountsHtml(row) {
  const values = [
    ["Imported", row.sourceRowCount],
    ["Prepared", row.warehouseRowCount],
    ["Visible", row.snapshotRowCount]
  ].filter(([, count]) => Number(count) > 0);
  if (!values.length) return "";
  return `<div class="org-onboarding-counts">${values.map(([label, count]) => `<span><strong>${Number(count).toLocaleString()}</strong>${label}</span>`).join("")}</div>`;
}

function organizationOnboardingActionsHtml(row) {
  const disabled = userGroupMgmtState.busy ? " disabled" : "";
  if (row.status === "active") {
    const rotate = hasPermission(PERMISSIONS.ORGANIZATIONS_CREDENTIALS_ROTATE)
      ? `<button class="ghost-button" type="button" data-onboarding-rotate="${escapeHtml(row.onboardingId)}"${disabled}>Replace connection key</button>`
      : "";
    return `<button class="primary-button" type="button" data-onboarding-add-user="${escapeHtml(row.orgId)}"${disabled}>Add first user</button>${rotate}`;
  }
  if (row.status === "cancelled") {
    return `<button class="primary-button" type="button" data-onboarding-reopen="${escapeHtml(row.onboardingId)}"${disabled}>Reopen setup</button>`;
  }
  const cancel = `<button class="ghost-button" type="button" data-onboarding-cancel="${escapeHtml(row.onboardingId)}"${disabled}>Cancel setup</button>`;
  if (row.lastCompletedStep === "draft") {
    return `<button class="primary-button" type="button" data-onboarding-connect="${escapeHtml(row.onboardingId)}"${disabled}>Enter connection key</button>${cancel}`;
  }
  const canContinue = row.status === "running" || row.status === "waiting_upstream" || row.status === "ready_to_activate" || row.retryable;
  if (!canContinue) return cancel;
  const label = row.status === "waiting_upstream" ? "Check again" : row.status === "ready_to_activate" ? "Finish setup" : row.status === "running" ? "Continue setup" : "Try again";
  return `<button class="primary-button" type="button" data-onboarding-retry="${escapeHtml(row.onboardingId)}"${disabled}>${label}</button>${cancel}`;
}

function companyOnboardingReviewHtml(row) {
  const selected = userGroupMgmtState.reviewSelection;
  if (!selected || selected.id !== row.onboardingId) return "";
  if (!row.review || row.connectionStatus !== "ready_to_review" || row.stateVersion !== selected.version || row.review.digest !== selected.digest) {
    return `<p role="status">This company changed. Open Review again to confirm the current checks.</p>`;
  }
  const labels = { scope_complete: "Source access", schema_valid: "Record formats", resource_manifest_valid: "Source collection", relationships_valid: "Record links" };
  const outcomes = { passed: "Passed", warning: "Needs review", pending: "Pending" };
  const checks = row.review.checks.map(check => `<li>${escapeHtml(labels[check.code] || "Additional source check")}: ${escapeHtml(outcomes[check.status] || "Needs review")}</li>`).join("");
  return `<section aria-label="Review company source" data-company-review-panel="${escapeHtml(row.onboardingId)}">
    <h5>Review ${escapeHtml(row.orgName)}</h5>
    <p>Confirm this token belongs to this company. EZClaim access checks do not identify the company for you.</p>
    <p>${row.review.resourceCount.toLocaleString()} source collections · ${row.review.sourceRowCount.toLocaleString()} source rows · ${row.review.serviceRowCount.toLocaleString()} service rows</p>
    <ul>${checks}</ul>
    <p>Approval adds this source to the normal import schedule. Billing setup and report readiness are checked separately.</p>
    <label><input type="checkbox" data-company-confirm="${escapeHtml(row.onboardingId)}"> I confirm this source belongs to ${escapeHtml(row.orgName)} and acknowledge the setup items shown here.</label>
    <div class="user-mgmt-group-actions"><button class="primary-button" type="button" data-company-command="approve" data-company-id="${escapeHtml(row.onboardingId)}"${userGroupMgmtState.busy ? " disabled" : ""}>Approve for scheduled import</button></div>
  </section>`;
}

function companyOnboardingCardHtml(row) {
  const statuses = {
    draft: ["Token needed", "Enter the company’s EZClaim token to start source checks."],
    checking: ["Checking source", "Source access and records are being checked. Approval will be required."],
    ready_to_review: ["Ready for review", "Review the source checks and confirm the company before scheduled imports begin."],
    waiting_schedule: ["Waiting for scheduled import", "Approved. The company will become connected after its scheduled import and portal checks pass."],
    active: ["Connected", "The company’s scheduled import and portal readback passed."],
    paused: ["Collection paused", "Future collection is paused. Existing reports and user access remain available."],
    needs_attention: ["Needs attention", "A source or import check needs attention. Previously accepted records are retained."]
  };
  const [label, detail] = row.status === "cancelled" ? ["Cancelled", "This setup is cancelled."] : statuses[row.connectionStatus] || ["Status pending", "Refresh to check company setup."];
  const billing = { ready: "Ready", needs_setup: "Needs setup", pending: "Pending checks" }[row.financialReadiness] || "Pending checks";
  const warningLabels = { fee_setup_pending: "Fee coverage needs review", payer_setup_pending: "Payer matching needs review", state_setup_pending: "State setup needs review", procedure_review_required: "Procedure codes need review" };
  const warnings = row.review?.setupWarnings || [];
  const warningHtml = warnings.length ? `<ul>${warnings.map(item => `<li>${escapeHtml(warningLabels[item.code] || "Additional billing setup needs review")}${item.count ? ` (${item.count.toLocaleString()})` : ""}</li>`).join("")}</ul>` : "";
  const disabled = userGroupMgmtState.busy ? " disabled" : "";
  const id = escapeHtml(row.onboardingId);
  let actions = "";
  if (row.status === "cancelled") actions = `<button class="ghost-button" type="button" data-onboarding-reopen="${id}"${disabled}>Reopen setup</button>`;
  else if (row.connectionStatus === "ready_to_review" && row.review) actions += `<button class="primary-button" type="button" data-company-review="${id}"${disabled}>Review company</button>`;
  else if (row.connectionStatus === "draft") actions += `<button class="primary-button" type="button" data-onboarding-connect="${id}"${disabled}>Enter connection key</button>`;
  else if (row.connectionStatus === "needs_attention" && row.retryable) actions += `<button class="ghost-button" type="button" data-onboarding-retry="${id}"${disabled}>Retry source checks</button>`;
  if (row.credentialRetryRequired && row.status !== "cancelled" && (!row.hasAcceptedSource || hasPermission(PERMISSIONS.ORGANIZATIONS_CREDENTIALS_ROTATE))) {
    actions += row.hasAcceptedSource ? `<button class="ghost-button" type="button" data-onboarding-rotate="${id}"${disabled}>Re-enter token</button>` : `<button class="ghost-button" type="button" data-onboarding-connect="${id}"${disabled}>Re-enter token</button>`;
  }
  if (row.status !== "cancelled" && ["active", "paused", "waiting_schedule"].includes(row.connectionStatus)) {
    const command = row.paused || row.connectionStatus === "paused" ? "resume" : "pause";
    actions += `<button class="ghost-button" type="button" data-company-command="${command}" data-company-id="${id}"${disabled}>${command === "pause" ? "Pause collection" : "Resume collection"}</button>`;
  }
  if (row.status === "active") actions += `<button class="ghost-button" type="button" data-onboarding-add-user="${escapeHtml(row.orgId)}"${disabled}>Add user</button>${hasPermission(PERMISSIONS.ORGANIZATIONS_CREDENTIALS_ROTATE) ? `<button class="ghost-button" type="button" data-onboarding-rotate="${id}"${disabled}>Replace connection key</button>` : ""}`;
  else if (row.status !== "cancelled") actions += `<button class="ghost-button" type="button" data-onboarding-cancel="${id}"${disabled}>Cancel setup</button>`;
  const setup = hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES) && row.financialReadiness !== "ready" ? `<div class="user-mgmt-group-actions"><button class="ghost-button" type="button" data-company-setup="missingRates">Review missing rates</button><button class="ghost-button" type="button" data-company-setup="feeSchedules">Open fee schedules</button></div>` : "";
  const date = value => { const parsed = new Date(value); return value && Number.isFinite(parsed.getTime()) ? parsed.toLocaleString() : ""; };
  return `<article class="org-onboarding-card" data-onboarding-id="${id}">
    <div class="org-onboarding-card-head"><div><strong>${escapeHtml(row.orgName)}</strong><span>Company ${escapeHtml(row.orgId)}</span></div><span class="org-onboarding-status">${escapeHtml(label)}</span></div>
    <p>${escapeHtml(detail)}</p><p>Billing readiness: <strong>${escapeHtml(billing)}</strong>${row.financialReadiness !== "ready" ? ". Connection does not mean financial reports are ready." : ""}</p>
    ${warningHtml}${setup}
    ${date(row.lastScheduledAtUtc) ? `<p>Last scheduled import: ${escapeHtml(date(row.lastScheduledAtUtc))}</p>` : ""}
    ${date(row.nextScheduledAtUtc) ? `<p>Next scheduled import: ${escapeHtml(date(row.nextScheduledAtUtc))}</p>` : ""}
    ${companyOnboardingReviewHtml(row)}
    <div class="user-mgmt-group-actions">${actions}</div>
    ${["active", "paused", "waiting_schedule"].includes(row.connectionStatus) ? `<p class="user-mgmt-group-footnote">Pausing collection does not revoke user access or remove accepted reports.</p>` : ""}
  </article>`;
}

async function runCompanyOnboardingCommand(onboardingId, action) {
  if (!isTrustedSuperAdminSession() || !hasPermission(PERMISSIONS.ORGANIZATIONS_ONBOARD) || userGroupMgmtState.busy || !["approve", "pause", "resume"].includes(action)) return;
  const row = userGroupMgmtState.onboardings.find(item => item.onboardingId === onboardingId);
  if (row?.contractVersion !== 2 || row.status === "cancelled") return;
  const payload = { expectedVersion: row.stateVersion };
  if (action === "approve") {
    const selected = userGroupMgmtState.reviewSelection;
    if (row.connectionStatus !== "ready_to_review" || !row.review || selected?.id !== row.onboardingId || selected.version !== row.stateVersion || selected.digest !== row.review.digest) {
      userGroupMgmtNotice("This review changed. Open Review again before approving.", "error"); return;
    }
    const checked = Array.from(document.querySelectorAll("[data-company-confirm]")).some(input => input.dataset.companyConfirm === row.onboardingId && input.checked);
    if (!checked) { userGroupMgmtNotice("Confirm the company source and acknowledge its setup items before approving.", "error"); return; }
    payload.reviewDigest = row.review.digest; payload.confirmCompany = true;
  } else if (!["active", "paused", "waiting_schedule"].includes(row.connectionStatus)) return;
  const slot = `${action}:${row.onboardingId}:${row.stateVersion}:${action === "approve" ? row.review.digest : ""}`;
  userGroupMgmtState.busy = true; userGroupMgmtState.busyAction = action;
  renderUserGroupMgmtBody();
  try {
    const result = await organizationOnboardingRequest("POST", `/api/organization-onboardings/${encodeURIComponent(row.onboardingId)}/${action}`, { payload, idempotencyKey: organizationOnboardingCommandKey(slot) });
    userGroupMgmtState.reviewSelection = null;
    if (!result.ok) {
      userGroupMgmtState.notice = organizationOnboardingErrorText(result); userGroupMgmtState.noticeTone = "error";
      await requestOrganizationOnboardings({ force: true }); return;
    }
    clearOrganizationOnboardingCommandKey(slot); upsertOrganizationOnboarding(result.body.onboarding);
    userGroupMgmtState.notice = action === "approve" ? "Approved for the normal import schedule. Billing readiness is tracked separately." : action === "pause" ? "Collection paused. User access and accepted reports remain available." : "Collection resumed for the normal schedule.";
    userGroupMgmtState.noticeTone = "info";
  } finally { userGroupMgmtState.busy = false; userGroupMgmtState.busyAction = ""; renderUserGroupMgmtBody(); }
}

function organizationOnboardingCardHtml(row) {
  if (row.contractVersion === 2) return companyOnboardingCardHtml(row);
  const meta = organizationOnboardingStatusMeta(row);
  const progress = organizationOnboardingProgress(row);
  return `<article class="org-onboarding-card" data-onboarding-id="${escapeHtml(row.onboardingId)}">
    <div class="org-onboarding-card-head">
      <div><strong>${escapeHtml(organizationOnboardingCompanyName(row))}</strong></div>
      <span class="org-onboarding-status org-onboarding-status--${meta.tone}">${escapeHtml(meta.label)}</span>
    </div>
    <div class="org-onboarding-progress" role="progressbar" aria-label="Company setup progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${progress}"><span style="width:${progress}%"></span></div>
    <p class="org-onboarding-step">${escapeHtml(organizationOnboardingStepLabel(row.lastCompletedStep))}</p>
    <p class="org-onboarding-detail">${escapeHtml(meta.detail)}</p>
    ${organizationOnboardingCountsHtml(row)}
    <div class="user-mgmt-group-actions">${organizationOnboardingActionsHtml(row)}</div>
  </article>`;
}

function organizationOnboardingListHtml() {
  if (userGroupMgmtState.onboardingStatus === "pending" && !userGroupMgmtState.onboardings.length) {
    return `<p class="user-mgmt-group-empty">Loading saved company setups…</p>`;
  }
  if (userGroupMgmtState.onboardingStatus === "error" && !userGroupMgmtState.onboardings.length) {
    return `<div class="org-onboarding-unavailable" role="status"><strong>Company setup is unavailable</strong><p>${escapeHtml(userGroupMgmtState.onboardingError)}</p><button class="ghost-button" type="button" data-onboarding-refresh="1">Check again</button></div>`;
  }
  if (!userGroupMgmtState.onboardings.length) return `<p class="user-mgmt-group-empty">No company setups yet.</p>`;
  return userGroupMgmtState.onboardings.map(organizationOnboardingCardHtml).join("");
}

function selectedOrganizationOnboarding() {
  return userGroupMgmtState.onboardings.find((row) => row.onboardingId === userGroupMgmtState.selectedOnboardingId) || null;
}

function userGroupMgmtOnboardHtml() {
  const selected = selectedOrganizationOnboarding();
  const rotating = (selected?.contractVersion === 2 ? selected.hasAcceptedSource : selected?.status === "active") && userGroupMgmtState.selectedOnboardingMode === "rotate";
  const orgId = selected?.orgId || userGroupMgmtState.draftOrgId;
  const orgName = selected?.orgName || userGroupMgmtState.draftOrgName;
  const buttonLabel = rotating ? "Replace connection key" : selected ? `Continue ${organizationOnboardingCompanyName(selected)}` : "Start company setup";
  const connectorAvailable = userGroupMgmtState.connectorAvailable === true;
  const canAcceptConnectionKey = connectorAvailable && (userGroupMgmtState.contractVersion !== 2 || selected || userGroupMgmtState.automaticIdAvailable === true) && (!rotating || hasPermission(PERMISSIONS.ORGANIZATIONS_CREDENTIALS_ROTATE));
  const connectorMessage = userGroupMgmtState.connectorAvailable === null
    ? "Checking whether company setup is ready. No key can be entered until this check finishes."
    : !connectorAvailable
      ? "The connection needed to protect and check a new key is not ready yet. You can review saved setups, but do not enter a key until this message is gone."
      : userGroupMgmtState.contractVersion === 2 && !selected && !userGroupMgmtState.automaticIdAvailable
        ? "Automatic company IDs are unavailable. Refresh to check again; no token can be entered yet."
        : "You do not have permission to replace this company’s connection key.";
  const formTitle = rotating ? "Replace the connection key" : selected ? "Add the connection key" : "Onboard a company";
  const formIntro = rotating
    ? "Use a new EZClaim connection key for future company pulls. Existing records stay in place."
    : userGroupMgmtState.contractVersion === 2 ? "Enter the company name and EZClaim token. Review the source checks before it joins the normal import schedule." : "Set up the company from its EZClaim connection through its first portal-ready records.";
  const keyLabel = rotating ? "New EZClaim connection key" : "EZClaim connection key";
  const keyHint = rotating
    ? "Replaces the current key for future pulls. It is cleared from this form as soon as the change starts and is never saved in this browser."
    : "Used once to connect this company. It is cleared from this form as soon as setup starts and is never saved in this browser.";
  return `${userGroupMgmtNoticeHtml()}
    <div class="org-onboarding-layout">
      <section class="org-onboarding-form-panel" aria-labelledby="orgOnboardingFormTitle">
        <div><h4 id="orgOnboardingFormTitle">${escapeHtml(formTitle)}</h4><p>${escapeHtml(formIntro)}</p></div>
        <div class="user-mgmt-group-form">
          ${!selected && (userGroupMgmtState.automaticIdAvailable === true || userGroupMgmtState.contractVersion === 2) ? `<p class="user-mgmt-group-field-hint">A permanent company ID will be assigned automatically.</p>` : `<label class="user-mgmt-group-field">
            <span class="user-mgmt-group-field-label">Organization ID</span>
            <input id="userGroupAddOrgId" class="user-mgmt-group-input" type="text" maxlength="80" autocomplete="off" placeholder="e.g. 40699714" value="${escapeHtml(orgId)}"${selected ? " disabled" : ""}>
            <span class="user-mgmt-group-field-hint">The permanent internal ID used for this company’s data and access.</span>
          </label>`}
          <label class="user-mgmt-group-field">
            <span class="user-mgmt-group-field-label">Company name</span>
            <input id="userGroupAddOrgName" class="user-mgmt-group-input" type="text" maxlength="200" autocomplete="off" placeholder="e.g. Bright Builders" value="${escapeHtml(orgName)}"${selected ? " disabled" : ""}>
            <span class="user-mgmt-group-field-hint">The name people will see throughout the portal.</span>
          </label>
          ${canAcceptConnectionKey ? `<label class="user-mgmt-group-field">
            <span class="user-mgmt-group-field-label">${escapeHtml(keyLabel)}</span>
            <input id="userGroupAddConnectionKey" class="user-mgmt-group-input" type="password" maxlength="4096" autocomplete="new-password" spellcheck="false" aria-describedby="userGroupAddConnectionKeyHint">
            <span id="userGroupAddConnectionKeyHint" class="user-mgmt-group-field-hint">${escapeHtml(keyHint)}</span>
          </label>
          <div class="user-mgmt-group-actions">
            <button class="primary-button" type="button" data-onboarding-submit="1"${userGroupMgmtState.busy ? " disabled" : ""}>${userGroupMgmtState.busyAction === "connect" ? "Starting setup…" : userGroupMgmtState.busyAction === "rotate" ? "Replacing key…" : escapeHtml(buttonLabel)}</button>
            ${selected ? `<button class="ghost-button" type="button" data-onboarding-new="1"${userGroupMgmtState.busy ? " disabled" : ""}>${rotating ? "Back" : "Start another"}</button>` : ""}
          </div>` : `<div class="org-onboarding-unavailable" role="status"><strong>${userGroupMgmtState.connectorAvailable === null ? "Checking company setup" : "Company connection unavailable"}</strong><p>${escapeHtml(connectorMessage)}</p></div>`}
        </div>
      </section>
      <section class="org-onboarding-history" aria-labelledby="orgOnboardingHistoryTitle">
        <div class="org-onboarding-history-head"><div><h4 id="orgOnboardingHistoryTitle">Saved setups</h4><p>Progress is kept here, so a company can be continued safely.</p></div><button class="ghost-button" type="button" data-onboarding-refresh="1"${userGroupMgmtState.busy ? " disabled" : ""}>Refresh</button></div>
        ${userGroupMgmtState.onboardingStatus === "error" && userGroupMgmtState.onboardings.length ? `<p class="user-mgmt-group-notice user-mgmt-group-notice--error" role="status">${escapeHtml(userGroupMgmtState.onboardingError)}</p>` : ""}
        <div class="org-onboarding-list">${organizationOnboardingListHtml()}</div>
      </section>
    </div>`;
}

function renderUserGroupMgmtList() {
  const list = document.getElementById("userGroupMgmtList");
  if (!list) return;
  list.innerHTML = userGroupMgmtListHtml();
  bindManagementListPager(list, "companies", renderUserGroupMgmtList);
  if (userGroupMgmtState.renamingOrgId) document.getElementById("userGroupRenameInput")?.focus({ preventScroll: true });
}

function renderUserGroupMgmtBody() {
  const host = document.getElementById("userGroupMgmtBody");
  if (!host) return;
  Array.from(document.querySelectorAll("[data-group-tab]")).forEach((tab) => {
    const selected = tab.dataset.groupTab === userGroupMgmtState.tab;
    tab.classList.toggle("is-active", selected);
    tab.setAttribute("aria-selected", selected ? "true" : "false");
  });
  const previousSandboxHost = document.getElementById("sandboxOnboardingPanel");
  host.innerHTML = userGroupMgmtState.tab === "onboard" ? userGroupMgmtOnboardHtml() : userGroupMgmtManageHtml();
  if (userGroupMgmtState.tab === "onboard" && window.TMS_SANDBOX_ONBOARDING === true) {
    const sandboxHost = previousSandboxHost || document.createElement("div");
    sandboxHost.id = "sandboxOnboardingPanel";
    host.prepend(sandboxHost);
    const sandboxOptions = {
      authorized: () => isTrustedSuperAdminSession() && hasPermission(PERMISSIONS.ORGANIZATIONS_ONBOARD),
      request: organizationOnboardingRequest,
      commandKey: newOrganizationOnboardingCommandKey
    };
    if (window.TmsSandboxOnboarding) {
      window.TmsSandboxOnboarding.mount(sandboxHost, sandboxOptions);
    } else if (sandboxOptions.authorized()) {
      const existingScript = document.getElementById("sandboxOnboardingModuleScript");
      sandboxHost.textContent = existingScript?.dataset.loadFailed === "true"
        ? "Isolated company setup is unavailable. Reload this page to retry."
        : "Loading isolated company setup…";
      if (!existingScript) {
        const script = document.createElement("script");
        script.id = "sandboxOnboardingModuleScript";
        script.src = "./sandboxOnboardingUi.js";
        script.onload = () => {
          const modal = document.getElementById("userGroupMgmtModal");
          if (modal && !modal.classList.contains("hidden") && userGroupMgmtState.tab === "onboard"
              && window.TMS_SANDBOX_ONBOARDING === true && sandboxOptions.authorized()) renderUserGroupMgmtBody();
        };
        script.onerror = () => {
          script.dataset.loadFailed = "true";
          const panel = document.getElementById("sandboxOnboardingPanel");
          if (panel) panel.textContent = "Isolated company setup is unavailable. Reload this page to retry.";
        };
        document.head.appendChild(script);
      }
    }
  } else { window.TmsSandboxOnboarding?.close(); }
  const search = document.getElementById("userGroupMgmtSearch");
  if (search) search.value = userGroupMgmtState.query;
  bindManagementListPager(document.getElementById("userGroupMgmtList"), "companies", renderUserGroupMgmtList);
  scheduleOrganizationOnboardingRefresh();
}

async function runOrganizationOnboarding(onboardingId) {
  if (!isTrustedSuperAdminSession() || !hasPermission(PERMISSIONS.ORGANIZATIONS_ONBOARD) || userGroupMgmtState.busy) return;
  userGroupMgmtState.busy = true;
  userGroupMgmtState.busyAction = "advance";
  renderUserGroupMgmtBody();
  try {
    for (let checkpoint = 0; checkpoint < ORGANIZATION_ONBOARDING_STEPS.length + 2; checkpoint += 1) {
      const row = userGroupMgmtState.onboardings.find((item) => item.onboardingId === onboardingId);
      if (!row || ["active", "cancelled"].includes(row.status)) break;
      if (row.contractVersion === 2 && (checkpoint > 0 || row.connectionStatus !== "needs_attention" || !row.retryable)) break;
      if (checkpoint > 0 && ["waiting_upstream", "action_required"].includes(row.status)) break;
      if (row.status === "draft") {
        userGroupMgmtState.selectedOnboardingId = row.onboardingId;
        userGroupMgmtState.tab = "onboard";
        break;
      }
      const slot = `advance:${row.onboardingId}:${row.stateVersion}`;
      const result = await organizationOnboardingRequest("POST", `/api/organization-onboardings/${encodeURIComponent(row.onboardingId)}/advance`, {
        payload: { expectedVersion: row.stateVersion },
        idempotencyKey: organizationOnboardingCommandKey(slot)
      });
      if (!result.ok) {
        userGroupMgmtState.notice = organizationOnboardingErrorText(result);
        userGroupMgmtState.noticeTone = "error";
        userGroupMgmtState.onboardingStatus = "idle";
        await requestOrganizationOnboardings({ force: true });
        break;
      }
      clearOrganizationOnboardingCommandKey(slot);
      const next = upsertOrganizationOnboarding(result.body.onboarding);
      renderUserGroupMgmtBody();
      if (!next || ["active", "cancelled", "waiting_upstream", "action_required"].includes(next.status)) break;
      await new Promise((resolve) => setTimeout(resolve, 350));
    }
    const completed = userGroupMgmtState.onboardings.find((row) => row.onboardingId === onboardingId);
    if (completed?.status === "active") {
      userGroupMgmtState.notice = `${organizationOnboardingCompanyName(completed)} is ready. Add its first user when you’re ready.`;
      userGroupMgmtState.noticeTone = "info";
      await requestPortalOrgDirectory({ force: true, groupManagement: true });
    }
  } finally {
    userGroupMgmtState.busy = false;
    userGroupMgmtState.busyAction = "";
    renderUserGroupMgmtBody();
  }
}

async function reopenOrganizationOnboarding(onboardingId) {
  if (!isTrustedSuperAdminSession() || !hasPermission(PERMISSIONS.ORGANIZATIONS_ONBOARD) || userGroupMgmtState.busy) return;
  const row = userGroupMgmtState.onboardings.find((item) => item.onboardingId === onboardingId);
  if (!row || row.status !== "cancelled") return;
  const slot = `reopen:${row.onboardingId}:${row.stateVersion}`;
  userGroupMgmtState.busy = true;
  userGroupMgmtState.busyAction = "reopen";
  renderUserGroupMgmtBody();
  try {
    const result = await organizationOnboardingRequest("POST", `/api/organization-onboardings/${encodeURIComponent(row.onboardingId)}/reopen`, {
      payload: { expectedVersion: row.stateVersion },
      idempotencyKey: organizationOnboardingCommandKey(slot)
    });
    if (!result.ok) {
      userGroupMgmtState.notice = organizationOnboardingErrorText(result);
      userGroupMgmtState.noticeTone = "error";
      userGroupMgmtState.onboardingStatus = "idle";
      await requestOrganizationOnboardings({ force: true });
      return;
    }
    clearOrganizationOnboardingCommandKey(slot);
    const reopened = upsertOrganizationOnboarding(result.body.onboarding);
    userGroupMgmtState.selectedOnboardingId = reopened?.onboardingId || row.onboardingId;
    userGroupMgmtState.selectedOnboardingMode = "connect";
    userGroupMgmtState.notice = `${organizationOnboardingCompanyName({ orgId: row.orgId, orgName: reopened?.orgName || row.orgName })} setup is open again. Enter its connection key to continue.`;
    userGroupMgmtState.noticeTone = "info";
  } finally {
    userGroupMgmtState.busy = false;
    userGroupMgmtState.busyAction = "";
    renderUserGroupMgmtBody();
    document.getElementById("userGroupAddConnectionKey")?.focus({ preventScroll: true });
  }
}

async function cancelOrganizationOnboarding(onboardingId) {
  if (!isTrustedSuperAdminSession() || !hasPermission(PERMISSIONS.ORGANIZATIONS_ONBOARD) || userGroupMgmtState.busy) return;
  const row = userGroupMgmtState.onboardings.find((item) => item.onboardingId === onboardingId);
  if (!row || ["active", "cancelled"].includes(row.status)) return;
  if (typeof window !== "undefined" && typeof window.confirm === "function" && !window.confirm(`Cancel setup for ${organizationOnboardingCompanyName(row)}? Completed setup progress will remain in its history.`)) return;
  const slot = `cancel:${row.onboardingId}:${row.stateVersion}`;
  userGroupMgmtState.busy = true;
  userGroupMgmtState.busyAction = "cancel";
  renderUserGroupMgmtBody();
  try {
    const result = await organizationOnboardingRequest("POST", `/api/organization-onboardings/${encodeURIComponent(row.onboardingId)}/cancel`, {
      payload: { expectedVersion: row.stateVersion },
      idempotencyKey: organizationOnboardingCommandKey(slot)
    });
    if (!result.ok) {
      userGroupMgmtState.notice = organizationOnboardingErrorText(result);
      userGroupMgmtState.noticeTone = "error";
      userGroupMgmtState.onboardingStatus = "idle";
      await requestOrganizationOnboardings({ force: true });
      return;
    }
    clearOrganizationOnboardingCommandKey(slot);
    const cancelled = upsertOrganizationOnboarding(result.body.onboarding);
    if (userGroupMgmtState.selectedOnboardingId === row.onboardingId) {
      userGroupMgmtState.selectedOnboardingId = "";
      userGroupMgmtState.selectedOnboardingMode = "connect";
    }
    userGroupMgmtState.notice = `${organizationOnboardingCompanyName({ orgId: row.orgId, orgName: cancelled?.orgName || row.orgName })} setup was cancelled. No active company was removed.`;
    userGroupMgmtState.noticeTone = "info";
  } finally {
    userGroupMgmtState.busy = false;
    userGroupMgmtState.busyAction = "";
    renderUserGroupMgmtBody();
  }
}

async function submitOrganizationOnboarding() {
  if (!isTrustedSuperAdminSession() || !hasPermission(PERMISSIONS.ORGANIZATIONS_ONBOARD) || userGroupMgmtState.busy) return;
  const selected = selectedOrganizationOnboarding();
  const rotating = (selected?.contractVersion === 2 ? selected.hasAcceptedSource : selected?.status === "active") && userGroupMgmtState.selectedOnboardingMode === "rotate";
  if (rotating && !hasPermission(PERMISSIONS.ORGANIZATIONS_CREDENTIALS_ROTATE)) return;
  if (userGroupMgmtState.connectorAvailable !== true) { userGroupMgmtNotice("The company connection is unavailable, so no key was accepted.", "error"); return; }
  const orgId = String(selected?.orgId || document.getElementById("userGroupAddOrgId")?.value || "").trim();
  const orgName = String(selected?.orgName || document.getElementById("userGroupAddOrgName")?.value || "").replace(/\s+/g, " ").trim();
  const connectionInput = document.getElementById("userGroupAddConnectionKey");
  let connectionKey = String(connectionInput?.value || "");
  const automaticId = !selected && userGroupMgmtState.automaticIdAvailable === true;
  if (!selected && userGroupMgmtState.contractVersion === 2 && !automaticId) { userGroupMgmtNotice("Automatic company IDs are unavailable. No key was accepted.", "error"); return; }
  if (!orgId && !automaticId) { userGroupMgmtNotice("Enter the organization ID for this company.", "error"); return; }
  if (!orgName) { userGroupMgmtNotice("Enter the company name.", "error"); return; }
  if (!connectionKey.trim()) { userGroupMgmtNotice(rotating ? "Enter the new EZClaim connection key." : "Enter the EZClaim connection key.", "error"); return; }

  // The key belongs only to this in-flight request. Clear the visible control
  // before the first await and never put it in Web Storage or page state.
  if (connectionInput) connectionInput.value = "";
  userGroupMgmtState.busy = true;
  userGroupMgmtState.busyAction = rotating ? "rotate" : "connect";
  userGroupMgmtState.notice = "";
  renderUserGroupMgmtBody();
  let onboarding = selected;
  try {
    if (rotating) {
      const rotateSlot = `rotate:${onboarding.onboardingId}:${onboarding.stateVersion}`;
      const rotated = await organizationOnboardingRequest("PUT", `/api/organization-onboardings/${encodeURIComponent(onboarding.onboardingId)}/credential/rotate`, {
        payload: { apiKey: connectionKey, expectedVersion: onboarding.stateVersion },
        idempotencyKey: organizationOnboardingCommandKey(rotateSlot)
      });
      connectionKey = "";
      if (!rotated.ok) {
        userGroupMgmtState.notice = organizationOnboardingErrorText(rotated);
        userGroupMgmtState.noticeTone = "error";
        userGroupMgmtState.onboardingStatus = "idle";
        await requestOrganizationOnboardings({ force: true });
        return;
      }
      clearOrganizationOnboardingCommandKey(rotateSlot);
      const updated = upsertOrganizationOnboarding(rotated.body.onboarding);
      userGroupMgmtState.selectedOnboardingId = "";
      userGroupMgmtState.selectedOnboardingMode = "connect";
      userGroupMgmtState.notice = updated?.contractVersion === 2 ? "The replacement token is being checked. Review it before future imports use it." : `${updated?.orgName || orgName} now uses the new connection key.`;
      userGroupMgmtState.noticeTone = "info";
      return;
    }
    if (!onboarding) {
      const createSlot = `create:${automaticId ? "auto" : orgId}:${orgName}`;
      const created = await organizationOnboardingRequest("POST", "/api/organization-onboardings", {
        payload: automaticId ? { orgName } : { orgId, orgName },
        idempotencyKey: organizationOnboardingCommandKey(createSlot)
      });
      if (!created.ok) {
        userGroupMgmtState.notice = organizationOnboardingErrorText(created);
        userGroupMgmtState.noticeTone = "error";
        return;
      }
      clearOrganizationOnboardingCommandKey(createSlot);
      onboarding = upsertOrganizationOnboarding(created.body.onboarding);
      userGroupMgmtState.selectedOnboardingId = onboarding?.onboardingId || "";
      userGroupMgmtState.selectedOnboardingMode = "connect";
    }
    if (!onboarding) {
      userGroupMgmtState.notice = "The company setup was saved, but its progress could not be loaded. Refresh and continue it below.";
      userGroupMgmtState.noticeTone = "error";
      return;
    }
    const credentialSlot = `credential:${onboarding.onboardingId}:${onboarding.stateVersion}`;
    const connected = await organizationOnboardingRequest("PUT", `/api/organization-onboardings/${encodeURIComponent(onboarding.onboardingId)}/credential`, {
      payload: { apiKey: connectionKey, expectedVersion: onboarding.stateVersion },
      idempotencyKey: organizationOnboardingCommandKey(credentialSlot)
    });
    connectionKey = "";
    if (!connected.ok) {
      userGroupMgmtState.notice = organizationOnboardingErrorText(connected);
      userGroupMgmtState.noticeTone = "error";
      userGroupMgmtState.onboardingStatus = "idle";
      await requestOrganizationOnboardings({ force: true });
      return;
    }
    clearOrganizationOnboardingCommandKey(credentialSlot);
    const connectedRow = upsertOrganizationOnboarding(connected.body.onboarding);
    userGroupMgmtState.selectedOnboardingId = "";
    userGroupMgmtState.selectedOnboardingMode = "connect";
    userGroupMgmtState.draftOrgId = "";
    userGroupMgmtState.draftOrgName = "";
    userGroupMgmtState.busy = false;
    userGroupMgmtState.busyAction = "";
    renderUserGroupMgmtBody();
    if (connectedRow?.contractVersion === 2) {
      userGroupMgmtState.notice = "Source checks have started. Review this company when the checks finish.";
    } else if (connectedRow) await runOrganizationOnboarding(connectedRow.onboardingId);
  } finally {
    connectionKey = "";
    userGroupMgmtState.busy = false;
    userGroupMgmtState.busyAction = "";
    renderUserGroupMgmtBody();
  }
}

async function submitUserGroupRename(orgId) {
  if (!isTrustedSuperAdminSession()) return;
  if (userGroupMgmtState.busy) return;
  const orgName = String(document.getElementById("userGroupRenameInput")?.value || "").replace(/\s+/g, " ").trim();
  if (!orgName) { userGroupMgmtNotice("Enter a name for this client group.", "error"); return; }
  userGroupMgmtState.busy = true;
  renderUserGroupMgmtList();
  const result = await postPortalOrgCommand("/api/orgs/rename", { orgId, orgName });
  userGroupMgmtState.busy = false;
  if (!result.ok) {
    userGroupMgmtNotice(result.message, "error");
    renderUserGroupMgmtBody();
    return;
  }
  userGroupMgmtState.renamingOrgId = "";
  userGroupMgmtNotice(`This company is now ${orgName} everywhere in the portal.`, "info");
  await requestPortalOrgDirectory({ force: true, groupManagement: true });
}

function userGroupMgmtNotice(message, tone) {
  userGroupMgmtState.notice = String(message || "");
  userGroupMgmtState.noticeTone = tone === "error" ? "error" : "info";
  renderUserGroupMgmtBody();
}

async function openUserAddWizardForOrganization(orgId) {
  // Captured BEFORE the groups modal closes: by the time the wizard opens, the
  // button that started this is hidden and document.activeElement is the body.
  const opener = document.activeElement;
  await requestPortalOrgDirectory({ force: true, groupManagement: true });
  closeUserGroupMgmtModal();
  openUserAddWizard({ returnFocus: opener });
  if (!userAddWizardState) return;
  // Opening the wizard starts its own read on the users route, which shares
  // this cache slot and replaces the groups roster with an empty pending one.
  // The picker is gated on that users roster - it is the set the invite gate
  // will accept - so the preset waits for it. Without this wait the lookup
  // finds nothing and this entry point quietly loses the company it exists
  // to pre-select.
  await requestPortalOrgDirectory();
  if (!userAddWizardState) return;
  const option = userAddWizardCompanyOptions().find((item) => normalizeUserScopeList(item.orgIds).includes(String(orgId)));
  if (!option) {
    // Returning here used to leave the admin on a blank "Who are you adding?"
    // with the groups screen gone and nothing said. Put them back where they
    // pressed the button and name the reason, because both reasons - not
    // active yet, or not named yet - are fixed on that screen.
    //
    // Read the reason NOW. Reopening the groups screen starts a read on the
    // other directory route, which shares this cache slot and would leave the
    // list looking like it had never loaded — so asking afterwards would blame
    // a roster that answered perfectly well and simply does not hold this org.
    const listState = userAddWizardCompanyListState();
    const reason = listState === "ready" || listState === "empty"
      ? "You cannot add a user to that company yet. It has to be active and have a name on file before anyone can be invited to it."
      : "The company list has not loaded, so there is nothing safe to add a user to yet. Try again in a moment.";
    closeUserAddWizard();
    openUserGroupMgmtModal();
    userGroupMgmtNotice(reason, "error");
    return;
  }
  userAddWizardState.type = "client";
  userAddWizardState.company = option.value;
  userAddWizardState.companyLabel = option.label;
  userAddWizardState.stepIndex = 2;
  // This company was chosen by the PORTAL, not by the admin. Re-baseline what
  // "entered" means, or Escape on the very first screen of this entry point
  // would ask them to discard something they never typed.
  userAddWizardOpenedSignature = userAddWizardInputSignature(userAddWizardState);
  renderUserAddWizard();
}

function handleUserGroupMgmtBodyClick(event) {
  const target = event.target;
  if (!target || typeof target.closest !== "function") return;
  const startRename = target.closest("[data-group-rename]");
  if (startRename) {
    userGroupMgmtState.renamingOrgId = startRename.dataset.groupRename;
    userGroupMgmtState.renameDraft = null;
    renderUserGroupMgmtList();
    return;
  }
  if (target.closest("[data-group-rename-cancel]")) {
    userGroupMgmtState.renamingOrgId = "";
    renderUserGroupMgmtList();
    return;
  }
  const saveRename = target.closest("[data-group-rename-save]");
  if (saveRename) { submitUserGroupRename(saveRename.dataset.groupRenameSave); return; }
  const deactivate = target.closest("[data-group-deactivate]");
  if (deactivate) { startUserGroupDeactivation(deactivate.dataset.groupDeactivate); return; }
  if (target.closest("[data-group-deactivate-cancel]")) {
    userGroupMgmtState.deactivatingOrgId = "";
    userGroupMgmtState.deactivationPreview = null;
    userGroupMgmtState.deactivationError = "";
    renderUserGroupMgmtList();
    return;
  }
  const confirmDeactivate = target.closest("[data-group-deactivate-confirm]");
  if (confirmDeactivate) { submitUserGroupDeactivation(confirmDeactivate.dataset.groupDeactivateConfirm); return; }
  if (target.closest("[data-group-show-inactive]")) { userGroupMgmtState.showInactive = !userGroupMgmtState.showInactive; renderUserGroupMgmtBody(); return; }
  const reactivate = target.closest("[data-group-reactivate]");
  if (reactivate) {
    userGroupMgmtState.reactivatingOrgId = reactivate.dataset.groupReactivate;
    userGroupMgmtState.reactivationReason = "";
    renderUserGroupMgmtBody();
    document.getElementById("userGroupReactivateReason")?.focus({ preventScroll: true });
    return;
  }
  if (target.closest("[data-group-reactivate-cancel]")) { userGroupMgmtState.reactivatingOrgId = ""; renderUserGroupMgmtBody(); return; }
  const confirmReactivate = target.closest("[data-group-reactivate-confirm]");
  if (confirmReactivate) { submitUserGroupReactivation(confirmReactivate.dataset.groupReactivateConfirm); return; }
  if (target.closest("[data-onboarding-submit]")) { submitOrganizationOnboarding(); return; }
  const connect = target.closest("[data-onboarding-connect]");
  if (connect) {
    userGroupMgmtState.selectedOnboardingId = connect.dataset.onboardingConnect;
    userGroupMgmtState.selectedOnboardingMode = "connect";
    userGroupMgmtState.tab = "onboard";
    userGroupMgmtState.notice = "";
    renderUserGroupMgmtBody();
    document.getElementById("userGroupAddConnectionKey")?.focus({ preventScroll: true });
    return;
  }
  const rotate = target.closest("[data-onboarding-rotate]");
  if (rotate && hasPermission(PERMISSIONS.ORGANIZATIONS_CREDENTIALS_ROTATE)) {
    userGroupMgmtState.selectedOnboardingId = rotate.dataset.onboardingRotate;
    userGroupMgmtState.selectedOnboardingMode = "rotate";
    userGroupMgmtState.tab = "onboard";
    userGroupMgmtState.notice = "";
    renderUserGroupMgmtBody();
    document.getElementById("userGroupAddConnectionKey")?.focus({ preventScroll: true });
    return;
  }
  const review = target.closest("[data-company-review]");
  if (review) {
    const row = userGroupMgmtState.onboardings.find(item => item.onboardingId === review.dataset.companyReview);
    if (!userGroupMgmtState.busy && row?.contractVersion === 2 && row.connectionStatus === "ready_to_review" && row.review) {
      userGroupMgmtState.reviewSelection = { id: row.onboardingId, version: row.stateVersion, digest: row.review.digest };
      renderUserGroupMgmtBody();
    }
    return;
  }
  const companyCommand = target.closest("[data-company-command]");
  if (companyCommand) { runCompanyOnboardingCommand(companyCommand.dataset.companyId, companyCommand.dataset.companyCommand); return; }
  const setup = target.closest("[data-company-setup]");
  if (setup && hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES)) { closeUserGroupMgmtModal(); navigateToView(setup.dataset.companySetup === "missingRates" ? "missingRates" : "feeSchedules"); return; }
  const retry = target.closest("[data-onboarding-retry]");
  if (retry) { runOrganizationOnboarding(retry.dataset.onboardingRetry); return; }
  const reopen = target.closest("[data-onboarding-reopen]");
  if (reopen) { reopenOrganizationOnboarding(reopen.dataset.onboardingReopen); return; }
  const cancel = target.closest("[data-onboarding-cancel]");
  if (cancel) { cancelOrganizationOnboarding(cancel.dataset.onboardingCancel); return; }
  const addUser = target.closest("[data-onboarding-add-user]");
  if (addUser) { openUserAddWizardForOrganization(addUser.dataset.onboardingAddUser); return; }
  if (target.closest("[data-onboarding-refresh]")) {
    userGroupMgmtState.onboardingStatus = "idle";
    requestOrganizationOnboardings({ force: true });
    return;
  }
  if (target.closest("[data-onboarding-new]")) {
    const connectionInput = document.getElementById("userGroupAddConnectionKey");
    if (connectionInput) connectionInput.value = "";
    userGroupMgmtState.selectedOnboardingId = "";
    userGroupMgmtState.selectedOnboardingMode = "connect";
    userGroupMgmtState.draftOrgId = "";
    userGroupMgmtState.draftOrgName = "";
    userGroupMgmtState.notice = "";
    renderUserGroupMgmtBody();
  }
}

function handleUserGroupMgmtBodyInput(event) {
  if (event.target?.matches?.("[data-group-rename-input]")) userGroupMgmtState.renameDraft = event.target.value;
  if (event.target?.matches?.("[data-group-deactivate-reason]")) userGroupMgmtState.deactivationReason = event.target.value;
  if (event.target?.matches?.("[data-group-reactivate-reason]")) userGroupMgmtState.reactivationReason = event.target.value;
  if (event.target?.id === "userGroupMgmtSearch") {
    userGroupMgmtState.query = String(event.target.value || "");
    renderUserGroupMgmtList();
    return;
  }
  if (event.target?.id === "userGroupAddOrgId") {
    userGroupMgmtState.draftOrgId = String(event.target.value || "");
    Object.keys(userGroupMgmtState.commandKeys).filter((key) => key.startsWith("create:")).forEach(clearOrganizationOnboardingCommandKey);
  }
  if (event.target?.id === "userGroupAddOrgName") {
    userGroupMgmtState.draftOrgName = String(event.target.value || "");
    Object.keys(userGroupMgmtState.commandKeys).filter((key) => key.startsWith("create:")).forEach(clearOrganizationOnboardingCommandKey);
  }
}

function handleUserGroupMgmtBodyKeydown(event) {
  if (event.key !== "Enter") return;
  const renameInput = event.target?.closest?.("[data-group-rename-input]");
  if (renameInput) {
    event.preventDefault();
    submitUserGroupRename(renameInput.dataset.groupRenameInput);
    return;
  }
  if (["userGroupAddOrgId", "userGroupAddOrgName", "userGroupAddConnectionKey"].includes(event.target?.id)) {
    event.preventDefault();
    submitOrganizationOnboarding();
  }
}


function peopleDirectoryState() {
  const scope=portalUserDirectoryAccessScopedRequestKey({kind:"people-pages"});
  let value=peopleDirectoryState.value;
  if (!value || value.scope!==scope) {
    value?.controller?.abort();
    users.splice(0);
    portalUserDirectoryState={status:"idle",requestKey:"",users:[],lanes:null,error:""};
    value=peopleDirectoryState.value={scope,page:1,search:"",pageInfo:null,pageKeys:[],selected:null};
    userMgmtDirectoryState.selectedPersonKey="";
    userMgmtDirectoryState.editing=false;
    userMgmtDirectoryState.editDraft=null;
  }
  return value;
}

function managementServerPage(key,rows,info={}) {
  const page=Number(info.page)||1,size=Number(info.pageSize)||50,total=Number(info.totalRows)||0,start=(page-1)*size;
  return {key,page,pages:Math.max(1,Math.ceil(total/size)),total,first:rows.length?start+1:0,last:rows.length?start+rows.length:0,rows};
}

function bindManagementServerPager(host,key,load) {
  host?.querySelectorAll('[data-management-list="'+key+'"]').forEach(button=>button.addEventListener("click",()=>load(Number(button.dataset.managementPage)||1)));
}

async function requestSelectedDirectoryPerson(user,{preserveDraft=false}={}) {
  if(!portalUserDirectoryHostedMode())return user;
  const view=peopleDirectoryState(),key=userMgmtPersonKey(user),userKey=staffTeamUserKey(user);
  const response=await fetch(`${portalUserDirectoryApiBase()}/api/users/directory?userKey=${encodeURIComponent(userKey)}`,{cache:"no-store",headers:{authorization:`Bearer ${workflowApiToken() || trackerApiToken()}`}});
  const body=await response.json().catch(()=>({}));
  if(peopleDirectoryState()!==view || userMgmtDirectoryState.selectedPersonKey!==key)return null;
  const row=body.users?.find(row=>String(row.userKey).toLowerCase()===userKey);
  if(response.status===403 || response.status===401 || response.ok && !row){
    view.selected=null;userMgmtDirectoryState.editing=false;userMgmtDirectoryState.editDraft=null;userMgmtDirectoryState.selectedPersonKey="";
    const index=users.findIndex(row=>userMgmtPersonKey(row)===key);if(index>=0)users.splice(index,1);
    renderUserMgmtList();return null;
  }
  if(!response.ok || !row)return null;
  const current=mapPortalUserDirectoryRow(row);
  if(!preserveDraft || !userMgmtDirectoryState.editing){view.selected=current;const index=users.findIndex(row=>userMgmtPersonKey(row)===key);if(index>=0)users[index]=current;}
  return current;
}

function handlePeopleDirectorySearch() {
  if(!portalUserDirectoryHostedMode()){renderUserMgmtList();return;}
  const view=peopleDirectoryState();clearTimeout(view.searchTimer);
  const search=userMgmtSearchQuery();
  view.searchTimer=setTimeout(()=>requestPortalUserDirectory({search,page:1,force:true}),180);
}

async function selectStaffTeam(teamId,{edit=false}={}) {
  const session=ensureStaffTeamSession();
  if(staffTeamState.editing && staffTeamState.selectedId!==teamId && !confirm("Discard the unsaved team changes?"))return;
  const sequence=(staffTeamState.detailSequence || 0)+1;staffTeamState.detailSequence=sequence;
  const response=await fetch(`${portalUserDirectoryApiBase()}/api/users/teams?teamId=${encodeURIComponent(teamId)}`,{cache:"no-store",headers:{authorization:`Bearer ${workflowApiToken() || trackerApiToken()}`}});
  const payload=await response.json().catch(()=>({}));
  if(session!==staffTeamSessionKey() || sequence!==staffTeamState.detailSequence || !isTrustedUserManagerSession())return;
  if(!response.ok || !payload.team){if([401,403,404].includes(response.status)){Object.assign(staffTeamState,{selectedTeam:null,selectedId:"",draft:null,editing:false});renderStaffTeams();}showToast("This team is no longer available. Refresh the team list.");return;}
  const team=payload.team;
  const memberKeys=[...new Set([...(team.memberUserKeys || []),team.leadUserKey].filter(Boolean))];
  if(memberKeys.length){
    const labelsResponse=await fetch(`${portalUserDirectoryApiBase()}/api/users/directory?userKeys=${encodeURIComponent(JSON.stringify(memberKeys))}`,{cache:"no-store",headers:{authorization:`Bearer ${workflowApiToken() || trackerApiToken()}`}});
    const labels=await labelsResponse.json().catch(()=>({}));
    if(session!==staffTeamSessionKey() || sequence!==staffTeamState.detailSequence || !isTrustedUserManagerSession())return;
    if([401,403].includes(labelsResponse.status)){Object.assign(staffTeamState,{selectedTeam:null,selectedId:"",draft:null,editing:false});renderStaffTeams();return;}
    if(labelsResponse.ok)team.people=(labels.users || []).map(mapPortalUserDirectoryRow);
  }
  const draft=blankStaffTeamDraft(team),pending=staffTeamState.pendingUserKey;
  if(pending && !draft.memberUserKeys.includes(pending))draft.memberUserKeys.push(pending);
  Object.assign(staffTeamState,{selectedId:teamId,selectedTeam:team,editing:Boolean(edit || pending),draft:edit || pending ? draft : null});
  if(edit || pending)await loadStaffTeamPeople({page:1,search:""});
  renderStaffTeams();
}

async function loadStaffTeamPeople({page=1,search=""}={}) {
  const session=ensureStaffTeamSession();
  staffTeamState.peopleController?.abort();const controller=new AbortController();staffTeamState.peopleController=controller;
  const query=new URLSearchParams({page:String(page),pageSize:"50",search,role:"internal"});
  const response=await fetch(`${portalUserDirectoryApiBase()}/api/users/directory?${query}`,{cache:"no-store",signal:controller.signal,headers:{authorization:`Bearer ${workflowApiToken() || trackerApiToken()}`}}).catch(error=>{if(error.name!=="AbortError")showToast("People could not load.");return null;});
  if(!response)return;
  const payload=await response.json().catch(()=>({}));
  if(session!==staffTeamSessionKey() || controller!==staffTeamState.peopleController || !isTrustedUserManagerSession())return;
  if(!response.ok){if([401,403].includes(response.status)){Object.assign(staffTeamState,{draft:null,editing:false,people:[],selectedTeam:null});}showToast("People could not load.");return;}
  Object.assign(staffTeamState,{people:(payload.users || []).map(mapPortalUserDirectoryRow),peoplePage:payload.page,peopleSearch:search});
  renderStaffTeams();
}

function portalUserDirectoryApiBase() {
  return workflowApiBase() || trackerApiBase();
}

function portalUserDirectoryHostedMode() {
  return Boolean(portalUserDirectoryApiBase() && typeof fetch === "function");
}

function portalUserDirectorySessionSnapshot() {
  const auth = (typeof state !== "undefined" && state && state.authUserScope) || storedPortalAuthUserSnapshot() || {};
  const role = canonicalRole(auth.role || "");
  const profile = roleProfiles[role] || { permissions: [] };
  return {
    token: String(workflowApiToken() || trackerApiToken() || ""),
    authority: auth,
    role,
    orgIds: normalizeUserScopeList(auth.orgIds),
    clientNames: normalizeUserScopeList(auth.clientNames),
    assignedOnly: Boolean(auth.assignedOnly),
    assignedName: String(auth.assignedName || "").trim(),
    canManageUsers: Array.isArray(profile.permissions) && profile.permissions.includes(PERMISSIONS.MANAGE_USERS)
  };
}

function portalUserDirectoryAccessScopedRequestKey(parts = {}) {
  return JSON.stringify({ ...parts, company: activeClientGroupTenant(), companyOrgIds: activeClientGroupOrgIds(), session: portalUserDirectorySessionSnapshot() });
}

function portalUserDirectoryExpectedRequestKey() {
  const base = portalUserDirectoryApiBase();
  // The directory is keyed to authenticated access, never the demo/user picker.
  // Hydrating it can replace a placeholder display name in that picker. Keeping
  // that presentation-only name in the key invalidates our own successful read.
  let readScope = additionalPortalDataReadScope();
  try {
    const scope = JSON.parse(readScope);
    if (scope && scope.access && typeof scope.access === "object") {
      const { user: _previewUser, ...access } = scope.access;
      readScope = JSON.stringify({ ...scope, access });
    }
  } catch (_error) { /* Preserve opaque scope keys without weakening them. */ }
  return base ? portalUserDirectoryAccessScopedRequestKey({ kind: "portal-user-directory", base, readScope, page: peopleDirectoryState().page, search: peopleDirectoryState().search }) : "";
}

function portalUserDirectoryIsCurrent() {
  const expected = portalUserDirectoryExpectedRequestKey();
  return Boolean(expected && portalUserDirectoryState.status === "ok" && portalUserDirectoryState.requestKey === expected);
}

// Governance surfaces must either show the server-owned directory or disclose
// that it is unavailable. The browser roster remains available only when the
// app has explicitly booted without an API (local/demo development).
function governanceUserRows() {
  if (!portalUserDirectoryHostedMode()) return users;
  return portalUserDirectoryIsCurrent() ? portalUserDirectoryState.users : [];
}

function sanitizePortalUserAuthLanePolicyDigest(value) {
  const digest = String(value || "").trim().toLowerCase();
  return /^sha256:[a-f0-9]{64}$/.test(digest) ? digest : "";
}

function sanitizePortalUserAuthLanePolicyCounts(value = {}) {
  const safeCount = (candidate) => {
    const count = Number(candidate);
    return Number.isFinite(count) && count >= 0 ? Math.trunc(count) : 0;
  };
  return {
    denied: safeCount(value?.denied),
    approval_required: safeCount(value?.approval_required),
    direct: safeCount(value?.direct)
  };
}

function mapPortalUserDirectoryRow(row = {}) {
  const setupStatus = normalizeUserSetupStatus(row.setupStatus);
  const setupState = String(setupStatus?.state || "").toLowerCase();
  const accountStatus = String(row.status || "").toLowerCase();
  const status = accountStatus === "disabled" || setupState === "disabled"
    ? "disabled"
    : (["invited", "draft", "invite_expired"].includes(setupState) ? "pending_invite" : (row.canSignIn ? "active" : (["revoked", "expired"].includes(accountStatus) ? "disabled" : (accountStatus || "pending_invite"))));
  return {
    name: String(row.displayName || row.userKey || "Unnamed account"),
    displayName: String(row.displayName || ""),
    userId: String(row.userKey || ""),
    username: String(row.userKey || ""),
    email: String(row.email || ""),
    role: canonicalRole(row.role),
    followUpPolicies: oversightPolicies(row.followUpPolicies),
    status,
    orgIds: normalizeUserScopeList(row.orgIds),
    clientNames: [],
    payerNames: normalizeUserScopeList(row.payerNames),
    allOrgs: Boolean(row.allOrgs),
    assignedOnly: Boolean(row.assignedOnly),
    assignedName: String(row.assignedName || row.userKey || ""),
    followUpActionAccessConfigured: row.followUpActionAccessConfigured === true,
    followUpActions: normalizeUserScopeList(row.followUpActions),
    teamIds: normalizeUserScopeList(row.teamIds),
    canSignIn: Boolean(row.canSignIn),
    cannotSignInReason: String(row.cannotSignInReason || ""),
    scopeConflict: Boolean(row.scopeConflict),
    followUpActionPolicyConflict: Boolean(row.followUpActionPolicyConflict),
    sources: Array.isArray(row.sources) ? row.sources.map(String) : [],
    authLanes: Array.isArray(row.authLanes) ? row.authLanes.map((lane) => ({
      source: String(lane?.source || ""),
      role: canonicalRole(lane?.role),
      status: String(lane?.status || "unknown"),
      orgIds: normalizeUserScopeList(lane?.orgIds),
      allOrgs: Boolean(lane?.allOrgs),
      assignedOnly: Boolean(lane?.assignedOnly),
      canSignIn: Boolean(lane?.canSignIn),
      followUpActionPolicyDigest: sanitizePortalUserAuthLanePolicyDigest(lane?.followUpActionPolicyDigest),
      followUpActionPolicyCounts: sanitizePortalUserAuthLanePolicyCounts(lane?.followUpActionPolicyCounts)
    })) : [],
    setupStatus,
    lastSignInAtUtc: String(row.lastSignInAtUtc || ""),
    profileVersion: String(row.profileVersion || ""),
    inviteId:String(row.inviteId || ""),invitedAt:String(row.invitedAt || ""),inviteExpiresAtUtc:String(row.inviteExpiresAtUtc || ""),
    inviteProvider:String(row.inviteProvider || ""),inviteMessageId:String(row.inviteMessageId || ""),inviteAccountCreated:row.inviteAccountCreated===true,inviteStatus:String(row.inviteStatus || ""),
    profileManaged: row.profileManaged === true,
    supervisionManaged: row.supervisionManaged === true,
    supervisionUnavailable: row.supervisionUnavailable === true,
    supervisionNeedsReview: row.supervisionNeedsReview === true,
    supervisionVersion: Number.isInteger(row.supervisionVersion) && row.supervisionVersion >= 0 ? row.supervisionVersion : null,
    supervisionBaselineDigest: /^[a-f0-9]{64}$/.test(String(row.supervisionBaselineDigest || "")) ? row.supervisionBaselineDigest : "",
    supervisionPolicies: oversightPolicies(row.supervisionPolicies),
    supervisionBaselinePolicies: oversightPolicies(row.supervisionBaselinePolicies),
    followUpActionAccess: sanitizeFollowUpActionAccessProjection(row.followUpActionAccess),
    staticLaneNotRevoked: accountStatus === "disabled" && Boolean(row.canSignIn)
      && Array.isArray(row.sources) && row.sources.includes("static-json"),
    // Existing invite/account-status actions key off `custom` and email. A SQL
    // account with an email remains mutable through those existing server
    // routes; static-secret accounts stay read-only because the API cannot
    // revoke that lane.
    custom: Boolean(String(row.email || "").trim()),
    directoryAuthoritative: true
  };
}

function portalUserAuthLaneLabel(source = "") {
  return source === "static-json" ? "Managed login" : (source === "sql-credential" ? "Portal account" : "Sign-in method");
}

function portalUserAuthLaneSummaries(user = {}) {
  return (Array.isArray(user.authLanes) ? user.authLanes : []).map((lane) => {
    const scope = lane.allOrgs ? "all companies" : (lane.orgIds?.length ? lane.orgIds.join(", ") : (lane.assignedOnly ? "assigned work only" : "no company scope"));
    const counts = sanitizePortalUserAuthLanePolicyCounts(lane.followUpActionPolicyCounts);
    const policySummary = sanitizePortalUserAuthLanePolicyDigest(lane.followUpActionPolicyDigest)
      ? `, follow-up actions: ${counts.direct} direct, ${counts.approval_required} approval required, ${counts.denied} denied`
      : "";
    return `${portalUserAuthLaneLabel(lane.source)}: ${roleProfiles[canonicalRole(lane.role)]?.label || canonicalRole(lane.role)}, ${lane.status || "unknown"}, ${scope}, ${lane.canSignIn ? "can sign in" : "cannot sign in"}${policySummary}`;
  });
}

function portalUserAuthDisagreement(user = {}) {
  return Boolean(user.scopeConflict || user.followUpActionPolicyConflict || (normalizeUserStatus(user.status) === "disabled" && user.canSignIn));
}

function portalUserAuthDisagreementMessage(user = {}) {
  const messages = [];
  if (user.scopeConflict) messages.push("The authentication lanes disagree on role or company scope.");
  if (user.followUpActionPolicyConflict) messages.push("The authentication lanes grant different follow-up action authority.");
  if (normalizeUserStatus(user.status) === "disabled" && user.canSignIn) messages.push("One lane is disabled, but another lane can still sign in.");
  return messages.join(" ");
}

function requestPortalUserDirectory({force=false,page,search}={}) {
  if (!portalUserDirectoryHostedMode()) {
    portalUserDirectoryState={status:"local",requestKey:"",users,lanes:null,error:""};
    return Promise.resolve(true);
  }
  const view=peopleDirectoryState();
  if (search!==undefined && String(search)!==view.search) {view.search=String(search);view.page=1;}
  if (page!==undefined) view.page=Math.max(1,Number(page)||1);
  const requestKey=portalUserDirectoryExpectedRequestKey(),sequence=++portalUserDirectoryReadSequence;
  if (!force && portalUserDirectoryState.requestKey===requestKey && portalUserDirectoryState.status==="ok") return Promise.resolve(true);
  view.controller?.abort();view.controller=new AbortController();
  const controller=view.controller,current=()=>sequence===portalUserDirectoryReadSequence && peopleDirectoryState()===view && portalUserDirectoryExpectedRequestKey()===requestKey;
  const previousUsers=portalUserDirectoryState.users || [],previousLanes=portalUserDirectoryState.lanes;
  const selected=users[userMgmtDirectoryState.selectedIndex];
  if (selected?.directoryAuthoritative) {view.selected=selected;userMgmtDirectoryState.selectedPersonKey=userMgmtPersonKey(selected);}
  portalUserDirectoryState={...portalUserDirectoryState,status:"pending",requestKey,users:previousUsers,lanes:previousLanes,error:""};
  const query=new URLSearchParams({page:String(view.page),pageSize:"50",search:view.search});
  if (activeClientGroupTenant()) query.set("companyOrgId", activeClientGroupServerOrgId());
  const timeout=setTimeout(()=>controller.abort(),15000);
  const promise=fetch(additionalPortalDataReadUrl(`${portalUserDirectoryApiBase()}/api/users/directory?${query}`),{
    method:"GET",cache:"no-store",headers:{accept:"application/json",authorization:`Bearer ${workflowApiToken() || trackerApiToken()}`},signal:controller.signal
  }).then(async response=>{
    const body=await response.json().catch(()=>({}));if(!current())return false;
    if (!response.ok || !Array.isArray(body.users)) {
      if(response.status===401){
        // An expired session is not a permission denial (2026-08-14 owner
        // decision): open login, say the session expired, and keep the roster.
        portalUserDirectoryState={...portalUserDirectoryState,status:"error",requestKey,users: previousUsers,lanes:previousLanes,error:"Your session expired. Sign in again — this page will still be here."};
        if(typeof notePortalSessionExpired==="function")notePortalSessionExpired();
      } else if(response.status===403){
        users.splice(0);view.selected=null;view.pageKeys=[];userMgmtDirectoryState.editing=false;userMgmtDirectoryState.editDraft=null;
        portalUserDirectoryState={status:"denied",requestKey,users:[],lanes:null,error:"The current session cannot access this directory."};
      } else portalUserDirectoryState={...portalUserDirectoryState,status:"error",error:String(body.message || "The directory could not load.")};
      return false;
    }
    const rows=body.users.map(mapPortalUserDirectoryRow);
    view.pageInfo=body.page || {page:view.page,pageSize:50,totalRows:body.count || rows.length};
    view.roleCounts=body.roleCounts || {};view.pageKeys=rows.map(userMgmtPersonKey);
    const selectedKey=userMgmtDirectoryState.selectedPersonKey;
    const onPage=rows.find(user=>userMgmtPersonKey(user)===selectedKey);
    if(onPage && !userMgmtDirectoryState.editing)view.selected=onPage;
    const retained=view.selected && !rows.some(user=>userMgmtPersonKey(user)===userMgmtPersonKey(view.selected)) ? [view.selected] : [];
    if(userMgmtDirectoryState.editing && view.selected && onPage)rows[rows.indexOf(onPage)]=view.selected;
    users.splice(0,users.length,...rows,...retained);
    const selectedIndex=users.findIndex(user=>userMgmtPersonKey(user)===selectedKey);
    userMgmtDirectoryState.selectedIndex=selectedIndex>=0?selectedIndex:0;
    portalUserDirectoryState={status:"ok",requestKey,users,lanes:body.lanes || null,page:view.pageInfo,roleCounts:view.roleCounts,error:""};
    populateUserSelect();
    if(view.selected && !onPage)requestSelectedDirectoryPerson(view.selected,{preserveDraft:true}).catch(()=>{});
    return true;
  }).catch(error=>{
    if(!current())return false;
    portalUserDirectoryState={...portalUserDirectoryState,status:"error",error:portalUserDirectoryFetchErrorMessage(error)};
    return false;
  }).finally(()=>{
    clearTimeout(timeout);
    if(current())renderUserMgmtList();
    if(portalUserDirectoryRequestPromise===promise)portalUserDirectoryRequestPromise=null;
  });
  portalUserDirectoryRequestPromise=promise;return promise;
}

function portalUserDirectoryUnavailableMessage() {
  if (portalUserDirectoryState.status === "pending" || portalUserDirectoryState.status === "idle") return "Loading the authoritative portal user directory…";
  if (portalUserDirectoryState.status === "denied") return "The authoritative user directory is unavailable because this session no longer has user-management permission.";
  return `The authoritative user directory is unavailable${portalUserDirectoryState.error ? `: ${portalUserDirectoryState.error}` : "."}`;
}

function teamStructureApiBase() {
  return (workflowApiBase() || trackerApiBase() || "").replace(/\/$/, "");
}

function teamStructureOrgOptions() {
  const auth = state?.authUserScope || {};
  const role = canonicalRole(auth.role || state?.role);
  const accessScope = role === "SuperAdmin"
    ? { allOrgs: true, orgIds: [] }
    : (auth.role ? auth : (currentAccessProfile().scope || {}));
  const allowed = new Set(normalizeUserScopeList(accessScope.orgIds));
  const allOrgs = accessScope.allOrgs === true || role === "SuperAdmin";
  const byOrg = new Map();
  userMgmtTenantClientDatabaseOptions().forEach((option) => {
    const orgId = normalizeUserScopeList(option.orgIds)[0] || "";
    // Reporting lines, My Work, and Notifications all consume this list. Show
    // the company people recognize; keep the database key only in the option
    // value used by API requests and tenant checks.
    if (orgId && (allOrgs || allowed.has(orgId))) byOrg.set(orgId, { orgId, label: option.clientName || option.label || orgId });
  });
  if (!allOrgs) {
    const displayLabels = userMgmtDisplayOrgLabelMap();
    allowed.forEach((orgId) => {
      if (!byOrg.has(orgId)) byOrg.set(orgId, { orgId, label: displayLabels.get(orgId) || orgId });
    });
  }
  const options = [...byOrg.values()];
  const unnamed = options
    .filter((option) => !String(option.label || "").trim() || String(option.label).trim() === option.orgId)
    .sort((left, right) => compareText(left.orgId, right.orgId));
  const unnamedLabels = new Map(unnamed.map((option, index) => [
    option.orgId,
    unnamed.length === 1 ? "Company name unavailable" : `Company name unavailable ${index + 1}`
  ]));
  // An authenticated scope can legitimately arrive before claims or the
  // company directory knows its display name. Never turn that data-quality gap
  // into a visible database key. Keep the exact key in `orgId`/option value and
  // use a truthful, numbered placeholder when several unnamed companies exist.
  const selectedCompanyOrgs = activeClientGroupOrgIds();
  return options
    .filter(option => !activeClientGroupTenant() || selectedCompanyOrgs.includes(option.orgId))
    .map((option) => ({ ...option, label: unnamedLabels.get(option.orgId) || option.label }))
    .sort((left, right) => compareText(left.label, right.label));
}

// Recipient-scoped workflow inbox. This is intentionally not Audit History:
// the bell contains concise routing prompts for the signed-in staff member,
// while the complete immutable event record stays on the audit surface.
let workflowNotificationState = emptyWorkflowNotificationState();
let workflowNotificationListRequestSequence = 0;
let workflowNotificationCountRequestSequence = 0;
let workflowNotificationCountTimer = null;
// Organizations the SERVER refused for this session. The badge list is built
// from the company label map, which outlives a scope change: a grant edited
// while the tab is open leaves an id in the list the principal may no longer
// read, and ONE such id refuses the whole count - correctly, because a partial
// total would read on the badge as "nothing is waiting". The old per-company
// fan-out only hid that by losing one request of twenty. Dropping the named id
// keeps the single-request budget and keeps the number honest about what it
// counted. Cleared with the session, because the scope is part of the key.
let workflowNotificationOutOfScopeOrgIds = new Set();

function emptyWorkflowNotificationState(sessionKey = "") {
  return {
    sessionKey,
    orgId: "",
    paging: { index: 0, cursors: [""], starts: [0] },
    rows: [],
    nextCursor: "",
    totalCount: 0,
    unreadCount: 0,
    aggregateUnreadCount: 0,
    status: "idle",
    countStatus: "idle",
    error: "",
    returnFocus: null
  };
}

function workflowNotificationSessionKey() {
  const auth = state?.authUserScope || {};
  return JSON.stringify({
    role: canonicalRole(auth.role || state?.role),
    user: String(auth.userId || auth.userKey || state?.activeUserName || ""),
    base: workflowNotificationApiBase(), company: activeClientGroupTenant(), companyOrgIds: activeClientGroupOrgIds()
  });
}

function workflowNotificationApiBase() {
  return ((typeof workflowApiBase === "function" && workflowApiBase()) || (typeof trackerApiBase === "function" && trackerApiBase()) || "").replace(/\/$/, "");
}

function workflowNotificationApiToken() {
  return (typeof portalAuthToken === "function" && portalAuthToken())
    || (typeof workflowApiToken === "function" && workflowApiToken())
    || (typeof trackerApiToken === "function" && trackerApiToken())
    || "";
}

function workflowNotificationsAvailable() {
  const role = canonicalRole(state?.authUserScope?.role || state?.role);
  return ["SuperAdmin", "StatesideBiller", "OffshoreBiller"].includes(role)
    && hasPermission(PERMISSIONS.VIEW_ASSIGNMENTS)
    && Boolean(workflowNotificationApiBase() && workflowNotificationApiToken())
    && teamStructureOrgOptions().length > 0;
}

function ensureWorkflowNotificationSession() {
  const key = workflowNotificationSessionKey();
  if (workflowNotificationState.sessionKey === key) return;
  workflowNotificationListRequestSequence += 1;
  workflowNotificationCountRequestSequence += 1;
  workflowNotificationOutOfScopeOrgIds = new Set();
  workflowNotificationState = emptyWorkflowNotificationState(key);
}

function renderWorkflowNotificationChrome() {
  if (!els.workflowNotificationsBtn) return;
  ensureWorkflowNotificationSession();
  // The bell is only offered when the company list is non-empty, and that
  // list is the org label map. Against a live API a user-management principal
  // should get the portal roster before the first paint decides, instead of
  // depending on whichever claim/fee rows happened to load first. The request
  // is a no-op unless the directory is still idle, and the callback repaints
  // this chrome. Principals without users:manage cannot read the directory
  // (server returns 403), so they keep the row-derived list.
  if (portalOrgDirectoryState.status === "idle" && typeof requestPortalOrgDirectory === "function"
    && ["SuperAdmin", "StatesideBiller"].includes(canonicalRole(state?.authUserScope?.role || state?.role))
    && hasPermission(PERMISSIONS.MANAGE_USERS)) {
    requestPortalOrgDirectory();
  }
  const available = workflowNotificationsAvailable();
  els.workflowNotificationsBtn.classList.toggle("hidden", !available);
  if (!available) {
    els.workflowNotificationsBadge?.classList.add("hidden");
    closeWorkflowNotifications({ restoreFocus: false });
    return;
  }
  const unread = Math.max(0, Number(workflowNotificationState.aggregateUnreadCount || 0));
  if (els.workflowNotificationsBadge) {
    els.workflowNotificationsBadge.textContent = unread > 99 ? "99+" : String(unread);
    els.workflowNotificationsBadge.classList.toggle("hidden", unread < 1 || workflowNotificationState.countStatus === "error");
  }
  els.workflowNotificationsBtn.setAttribute("aria-label", unread ? `Open work notifications, ${unread} unread` : "Open work notifications");
  // The badge is chrome: it may arrive after the rows do, and on a cold All
  // Data load it MUST, or it is one more request in front of the page query.
  if (workflowNotificationState.countStatus === "idle") {
    deferBootRequestUntilFirstGridPaint("workflowNotificationUnreadCount", () => {
      if (workflowNotificationState.countStatus === "idle") requestWorkflowNotificationUnreadCount();
    }) || requestWorkflowNotificationUnreadCount();
  }
  installWorkflowNotificationPolling();
}

function installWorkflowNotificationPolling() {
  if (workflowNotificationCountTimer || typeof window === "undefined") return;
  workflowNotificationCountTimer = window.setInterval(() => {
    if (!document.hidden && workflowNotificationsAvailable()) requestWorkflowNotificationUnreadCount({ force: true, minGapMs: WORKFLOW_NOTIFICATION_COUNT_MIN_GAP_MS });
  }, WORKFLOW_NOTIFICATION_COUNT_POLL_MS);
  // Coming back to the tab re-reads the badge, but not more often than the
  // poll would have: alt-tabbing is not a reason to re-ask six times a minute.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && workflowNotificationsAvailable()) requestWorkflowNotificationUnreadCount({ force: true, minGapMs: WORKFLOW_NOTIFICATION_COUNT_MIN_GAP_MS });
  });
}

async function workflowNotificationFetch(path, init = {}, isCurrent = () => true) {
  const base = workflowNotificationApiBase();
  const token = workflowNotificationApiToken();
  if (!base || !token) throw new Error("Notifications are unavailable for this session.");
  const response = await fetch(`${base}${path}`, {
    cache: "no-store",
    ...init,
    headers: {
      accept: "application/json",
      ...(init.body ? { "content-type": "application/json" } : {}),
      authorization: `Bearer ${token}`,
      ...(init.headers || {})
    }
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401 && isCurrent() && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    // The refusal travels with its own error: a caller that can narrow the
    // request (the multi-organization count) needs the reason and the org id
    // the server named, not just a sentence to show.
    const refusal = new Error(String(body.message || body.error || "Notifications are temporarily unavailable."));
    refusal.status = response.status;
    refusal.reason = String(body.reason || "");
    refusal.orgId = String(body.orgId || "");
    throw refusal;
  }
  return body;
}

// ONE REQUEST FOR THE WHOLE BADGE, NOT ONE PER COMPANY (2026-09-22).
//
// This counted every authenticated organization by asking for each one
// separately - options.map(...) inside a Promise.all - so the badge cost one
// GET per company. On the production census that is 20 requests of 122 bytes,
// all dispatched within 3 ms and each measured at 1.0-7.1 s, against a
// workflow surface whose p95 SQL total is 0 ms: the API was queueing on its
// own chrome while the All Data page query and the facet batch waited behind
// it (perf/ALL_DATA_LATENCY_2026-09-22.md).
//
// The badge still counts EVERY authenticated organization - the list simply
// rides in one `orgIds` parameter, and backend/workflowNotificationHandlers.js
// authorizes each id exactly as it authorized a single `orgId`, refusing the
// whole read if any one of them is out of scope. The shared in-flight promise
// means the render chrome, the poll timer, the visibility handler and a
// mark-read refresh can all ask at once and still produce one request.
const WORKFLOW_NOTIFICATION_COUNT_POLL_MS = 60000;
const WORKFLOW_NOTIFICATION_COUNT_MIN_GAP_MS = 30000;
let workflowNotificationCountInFlight = null;
let workflowNotificationCountSettledAtMs = 0;

async function requestWorkflowNotificationUnreadCount({ force = false, minGapMs = 0 } = {}) {
  ensureWorkflowNotificationSession();
  if (!workflowNotificationsAvailable()) return;
  if (workflowNotificationCountInFlight) {
    // THIS HANDLE NEVER REJECTS, and that is the whole point of it. Every
    // entry point - the render chrome, the deferred boot callback, the poll
    // timer, the visibility handler - calls this function without awaiting it,
    // so handing them the read itself turned an already-handled refusal into
    // an uncaught page exception: the badge hid correctly and the page still
    // raised "Notifications are temporarily unavailable." one tick later
    // (Manage Users browser proof, 2026-09-22).
    if (!force) return workflowNotificationCountInFlight;
    // A forced re-read (a mark-read just changed the number) waits for the
    // read already on the wire rather than racing it, then asks once.
    await workflowNotificationCountInFlight;
  }
  if (!force && workflowNotificationState.countStatus === "pending") return;
  if (minGapMs > 0 && workflowNotificationCountSettledAtMs && Date.now() - workflowNotificationCountSettledAtMs < minGapMs) return;
  const sequence = ++workflowNotificationCountRequestSequence;
  const sessionKey = workflowNotificationState.sessionKey;
  const options = teamStructureOrgOptions();
  const orgIds = options.map((option) => String(option.orgId || "")).filter((orgId) => orgId && !workflowNotificationOutOfScopeOrgIds.has(orgId));
  if (!orgIds.length) return;
  const token = workflowNotificationApiToken();
  const access = workflowAccessScopedRequestKey({});
  const current = () => sequence === workflowNotificationCountRequestSequence && sessionKey === workflowNotificationSessionKey() && token === workflowNotificationApiToken() && access === workflowAccessScopedRequestKey({});
  // Only a count we actually HAD can go up. The first answer of a session is
  // not an arrival, and must not trigger a second My Work read at boot.
  const previousUnread = workflowNotificationState.countStatus === "ready" ? Math.max(0, Number(workflowNotificationState.aggregateUnreadCount || 0)) : null;
  workflowNotificationState.countStatus = "pending";
  const read = workflowNotificationCountRead(orgIds, current);
  workflowNotificationCountInFlight = read;
  try {
    const outcome = await read;
    if (!current()) return;
    if (outcome.ok) {
      workflowNotificationState.aggregateUnreadCount = Math.max(0, Number(outcome.body.unreadCount || 0));
      workflowNotificationState.countStatus = "ready";
      workflowNotificationState.error = "";
      if (previousUnread !== null && workflowNotificationState.aggregateUnreadCount > previousUnread && typeof noteMyWorkAttentionChanged === "function") noteMyWorkAttentionChanged();
    } else {
      // Quietly: the badge hides rather than showing a number nobody can
      // trust, and nothing is thrown at the page.
      workflowNotificationState.aggregateUnreadCount = 0;
      workflowNotificationState.countStatus = "error";
      workflowNotificationState.error = String(outcome.error?.message || "Notifications are temporarily unavailable.");
    }
  } finally {
    if (workflowNotificationCountInFlight === read) workflowNotificationCountInFlight = null;
    workflowNotificationCountSettledAtMs = Date.now();
    if (!current() && sequence === workflowNotificationCountRequestSequence && workflowNotificationState.countStatus === "pending") workflowNotificationState.countStatus = "idle";
  }
  renderWorkflowNotificationChrome();
}

// ONE request for every organization, and a refusal that NAMES one narrows
// the list instead of blanking the badge for the rest of the session. The
// server still refuses the whole read when an id is out of scope - it must,
// a partial total would read as "nothing is waiting" - and says which id it
// was, so this drops that id and asks once more. At most ONE retry: a
// narrowed list refused again degrades quietly rather than walking the scope
// one request at a time, which is the fan-out this replaced.
// It resolves an outcome and never rejects, because its handle is shared.
async function workflowNotificationCountRead(orgIds, current) {
  let requested = orgIds;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const params = new URLSearchParams({ orgIds: JSON.stringify(requested), unreadOnly: "true", countOnly: "true" });
    try {
      return { ok: true, body: await workflowNotificationFetch(`/api/workflow/notifications?${params.toString()}`, {}, current) };
    } catch (error) {
      const refused = workflowNotificationRefusedOrgId(error);
      if (!refused || !requested.includes(refused)) return { ok: false, error };
      workflowNotificationOutOfScopeOrgIds.add(refused);
      requested = requested.filter((orgId) => orgId !== refused);
      if (!requested.length) return { ok: false, error };
    }
  }
  return { ok: false, error: new Error("Notifications are temporarily unavailable.") };
}

// The org id in a 403 is the caller's own input coming back, so it discloses
// nothing - and it is the only authority this page has, because the client
// scope the list was built from is exactly the thing that went stale.
function workflowNotificationRefusedOrgId(error) {
  return Number(error?.status) === 403 && error?.reason === "organization_out_of_scope" ? String(error.orgId || "") : "";
}

function workflowNotificationEnsureOrg() {
  const options = teamStructureOrgOptions();
  if (!options.some((option) => option.orgId === workflowNotificationState.orgId)) {
    workflowNotificationState.orgId = assignmentScopeUiState?.orgId && options.some((option) => option.orgId === assignmentScopeUiState.orgId)
      ? assignmentScopeUiState.orgId
      : (options[0]?.orgId || "");
    workflowNotificationState.paging = { index: 0, cursors: [""], starts: [0] };
    workflowNotificationState.rows = [];
    workflowNotificationState.nextCursor = "";
    workflowNotificationState.status = "idle";
  }
  return options;
}

function openWorkflowNotifications() {
  if (!workflowNotificationsAvailable()) return denyAction(PERMISSIONS.VIEW_ASSIGNMENTS, "open workflow notifications");
  ensureWorkflowNotificationSession();
  workflowNotificationState.returnFocus = document.activeElement;
  // Ask for the portal org directory before the company list paints. Until
  // now it was only requested by Manage Users, so a biller who opened the bell
  // first saw the handful of org ids the loaded claim/fee rows happened to
  // carry - one real company labelled "Company name unavailable" and none of
  // the other nineteen (hosted TEST, 2026-09-02 shell census). The request is
  // a no-op while a directory read is pending or already held.
  if (typeof requestPortalOrgDirectory === "function") requestPortalOrgDirectory();
  workflowNotificationEnsureOrg();
  els.workflowNotificationsModal?.classList.remove("hidden");
  renderWorkflowNotificationsModal();
  requestWorkflowNotifications();
  activateForegroundModal(els.workflowNotificationsModal, {
    initialFocus: els.workflowNotificationsCloseBtn,
    onEscape: closeWorkflowNotifications,
    returnFocus: workflowNotificationState.returnFocus
  });
}

function closeWorkflowNotifications({ restoreFocus = true } = {}) {
  if (!els.workflowNotificationsModal || els.workflowNotificationsModal.classList.contains("hidden")) return;
  workflowNotificationListRequestSequence += 1;
  if (workflowNotificationState.status === "pending") workflowNotificationState.status = "idle";
  els.workflowNotificationsModal.classList.add("hidden");
  deactivateForegroundModal(els.workflowNotificationsModal, { restoreFocus });
  workflowNotificationState.returnFocus = null;
}

function selectWorkflowNotificationOrg(orgId) {
  if (!teamStructureOrgOptions().some((option) => option.orgId === orgId)) return;
  workflowNotificationListRequestSequence += 1;
  workflowNotificationState.orgId = orgId;
  workflowNotificationState.paging = { index: 0, cursors: [""], starts: [0] };
  workflowNotificationState.rows = [];
  workflowNotificationState.nextCursor = "";
  workflowNotificationState.totalCount = 0;
  workflowNotificationState.unreadCount = 0;
  workflowNotificationState.status = "idle";
  workflowNotificationState.error = "";
  renderWorkflowNotificationsModal();
  requestWorkflowNotifications();
}

async function requestWorkflowNotifications({ append = false, pageAction = "" } = {}) {
  if (!workflowNotificationsAvailable() || els.workflowNotificationsModal?.classList.contains("hidden")) return;
  const options = workflowNotificationEnsureOrg();
  const orgId = workflowNotificationState.orgId;
  if (!orgId || !options.some((option) => option.orgId === orgId)) return;
  if (workflowNotificationState.status === "pending" || (append && !workflowNotificationState.nextCursor)) return;
  const paging = workflowNotificationState.paging || (workflowNotificationState.paging = { index: 0, cursors: [""], starts: [0] });
  const target = append ? paging.index + 1 : pageAction === "prev" ? paging.index - 1 : pageAction === "retry" ? (paging.failedIndex ?? paging.index) : 0;
  if (target < 0) return;
  const cursor = target === paging.index + 1 ? workflowNotificationState.nextCursor : paging.cursors[target];
  if (target > 0 && !cursor) return;
  const start = target === paging.index + 1 ? (paging.starts[paging.index] || 0) + workflowNotificationState.rows.length : (paging.starts[target] || 0);
  const sequence = ++workflowNotificationListRequestSequence;
  const sessionKey = workflowNotificationState.sessionKey;
  const token = workflowNotificationApiToken();
  const access = workflowAccessScopedRequestKey({});
  const current = () => sequence === workflowNotificationListRequestSequence && sessionKey === workflowNotificationSessionKey() && orgId === workflowNotificationState.orgId && token === workflowNotificationApiToken() && access === workflowAccessScopedRequestKey({}) && !els.workflowNotificationsModal?.classList.contains("hidden") && workflowNotificationsAvailable();
  const params = new URLSearchParams({ orgId, limit: "50" });
  if (cursor) params.set("cursor", cursor);
  workflowNotificationState.status = "pending";
  workflowNotificationState.error = "";
  paging.failedIndex = target;
  paging.slow = false;
  renderWorkflowNotificationsModal();
  const controller = new AbortController();
  const slowTimer = setTimeout(() => { if (current()) { paging.slow = true; renderWorkflowNotificationsModal(); } }, 4000);
  let timeout;
  try {
    const body = await Promise.race([
      workflowNotificationFetch(`/api/workflow/notifications?${params.toString()}`, { signal: controller.signal }, current),
      new Promise((resolve, reject) => { timeout = setTimeout(() => { controller.abort(); reject(new Error("notification_read_timeout")); }, 30000); })
    ]);
    if (!current()) return;
    const received = Array.isArray(body.notifications) ? body.notifications : [];
    const byId = new Map(received.map(row => [String(row.notificationId || ""), row]));
    workflowNotificationState.rows = [...byId.values()];
    workflowNotificationState.nextCursor = String(body.nextCursor || "");
    workflowNotificationState.totalCount = Math.max(0, Number(body.totalCount || 0));
    workflowNotificationState.unreadCount = Math.max(0, Number(body.unreadCount || 0));
    paging.cursors = paging.cursors.slice(0, target);
    paging.starts = paging.starts.slice(0, target);
    paging.cursors[target] = cursor || "";
    paging.starts[target] = start;
    paging.index = target;
    paging.failedIndex = null;
    workflowNotificationState.status = "ready";
  } catch (_) {
    if (!current()) return;
    workflowNotificationState.status = "error";
    workflowNotificationState.error = "Work notifications could not be loaded. Your current page is unchanged. Try again.";
  } finally {
    clearTimeout(slowTimer);
    clearTimeout(timeout);
    if (current()) renderWorkflowNotificationsModal();
    else if (sequence === workflowNotificationListRequestSequence && workflowNotificationState.status === "pending") workflowNotificationState.status = "idle";
  }
}

function renderWorkflowNotificationPager() {
  const next = els.workflowNotificationsLoadOlderBtn;
  if (!next?.parentElement) return;
  let host = els.workflowNotificationsModal.querySelector("[data-workflow-notification-pages]");
  if (!host) {
    next.insertAdjacentHTML("beforebegin", '<nav class="report-pager" aria-label="Work notification pages" data-workflow-notification-pages></nav>');
    host = els.workflowNotificationsModal.querySelector("[data-workflow-notification-pages]");
  }
  const paging = workflowNotificationState.paging || { index: 0 };
  const busy = workflowNotificationState.status === "pending";
  const hadFocus = host.contains(document.activeElement);
  const statusHadFocus = document.activeElement === els.workflowNotificationsStatus;
  if (hadFocus) paging.focusAction = document.activeElement === next ? "next" : document.activeElement.getAttribute?.("data-workflow-notification-page");
  const button = (action, label, disabled) => '<button class="ghost-button" type="button" data-workflow-notification-page="' + action + '"' + (disabled || busy ? ' disabled' : '') + '>' + label + '</button>';
  host.innerHTML = button("first", "First", paging.index === 0) + button("prev", "Previous", paging.index === 0) + (workflowNotificationState.status === "error" ? button("retry", "Retry", false) : '');
  host.appendChild(next);
  host.querySelectorAll("[data-workflow-notification-page]").forEach(button => button.addEventListener("click", () => requestWorkflowNotifications({ pageAction: button.getAttribute("data-workflow-notification-page") })));
  if (hadFocus || paging.restoreFocus || (statusHadFocus && paging.focusAction)) {
    paging.restoreFocus = false;
    const target = paging.focusAction === "next" ? next : host.querySelector('[data-workflow-notification-page="' + paging.focusAction + '"]');
    const focus = !busy && target && !target.disabled ? target : els.workflowNotificationsStatus;
    focus?.setAttribute("tabindex", "-1");
    focus?.focus({ preventScroll: true });
  }
}

function renderWorkflowNotificationsModal() {
  if (!els.workflowNotificationsModal || els.workflowNotificationsModal.classList.contains("hidden")) return;
  const activePagerControl = document.activeElement;
  if (activePagerControl === els.workflowNotificationsLoadOlderBtn || activePagerControl?.closest?.("[data-workflow-notification-pages]")) {
    const paging = workflowNotificationState.paging || (workflowNotificationState.paging = { index: 0, starts: [0], cursors: [""] });
    paging.focusAction = activePagerControl === els.workflowNotificationsLoadOlderBtn ? "next" : activePagerControl.getAttribute("data-workflow-notification-page");
    paging.restoreFocus = true;
  }
  const options = workflowNotificationEnsureOrg();
  if (els.workflowNotificationsOrg) {
    els.workflowNotificationsOrg.innerHTML = options.map((option) => `<option value="${escapeHtml(option.orgId)}"${option.orgId === workflowNotificationState.orgId ? " selected" : ""}>${escapeHtml(option.label)}</option>`).join("");
    els.workflowNotificationsOrg.disabled = Boolean(activeClientGroupTenant()) || workflowNotificationState.status === "pending";
  }
  const rows = workflowNotificationState.rows;
  const unreadRows = rows.filter((row) => !row.readAtUtc);
  if (els.workflowNotificationsStatus) {
    els.workflowNotificationsStatus.classList.toggle("is-error", workflowNotificationState.status === "error");
    els.workflowNotificationsStatus.textContent = workflowNotificationState.status === "pending"
      ? (workflowNotificationState.paging?.slow ? "Still loading your work notifications. Please wait…" : "Loading your work notifications…")
      : workflowNotificationState.status === "error"
        ? workflowNotificationState.error
        : workflowNotificationState.unreadCount
          ? `${workflowNotificationState.unreadCount} unread for this company.`
          : "You are caught up for this company.";
  }
  if (els.workflowNotificationsList) {
    els.workflowNotificationsList.setAttribute("aria-busy", String(workflowNotificationState.status === "pending"));
    els.workflowNotificationsList.innerHTML = rows.length
      ? rows.map(workflowNotificationRowHtml).join("")
      : (workflowNotificationState.status === "pending" ? "" : `<div class="workflow-notifications-empty"><strong>No notifications here.</strong><span>New assignments and review decisions will appear in this inbox.</span></div>`);
  }
  if (els.workflowNotificationsMarkReadBtn) {
    els.workflowNotificationsMarkReadBtn.disabled = workflowNotificationState.status === "pending" || !unreadRows.length;
    els.workflowNotificationsMarkReadBtn.textContent = unreadRows.length ? `Mark shown read (${unreadRows.length})` : "Shown notifications read";
  }
  if (els.workflowNotificationsLoadOlderBtn) {
    els.workflowNotificationsLoadOlderBtn.classList.remove("hidden");
    els.workflowNotificationsLoadOlderBtn.disabled = workflowNotificationState.status === "pending" || !workflowNotificationState.nextCursor;
    els.workflowNotificationsLoadOlderBtn.textContent = "Next";
    renderWorkflowNotificationPager();
  }
  if (els.workflowNotificationsCount) {
    const shown = rows.length;
    const paging = workflowNotificationState.paging || { index: 0, starts: [0] };
    const start = Number(paging.starts[paging.index] || 0);
    els.workflowNotificationsCount.setAttribute("role", "status");
    els.workflowNotificationsCount.setAttribute("aria-live", "polite");
    els.workflowNotificationsCount.textContent = "Showing " + (shown ? start + 1 : 0).toLocaleString() + "–" + (shown ? start + shown : 0).toLocaleString() + " of " + workflowNotificationState.totalCount.toLocaleString() + " · Page " + (paging.index + 1).toLocaleString();
  }
}

function workflowNotificationRowHtml(row = {}) {
  const unread = !row.readAtUtc;
  const when = workflowNotificationWhen(row.createdAtUtc);
  return `<button type="button" class="workflow-notification-row${unread ? " is-unread" : ""}" data-workflow-notification-open="${escapeHtml(row.notificationId || "")}">
    <span class="workflow-notification-dot" aria-hidden="true"></span>
    <span class="workflow-notification-copy">
      <strong>${escapeHtml(row.title || "Work was updated")}</strong>
      <span>${escapeHtml(row.body || "Open My Work for the current details.")}</span>
      <small>${escapeHtml(when)}</small>
    </span>
    <span class="workflow-notification-open" aria-hidden="true">›</span>
  </button>`;
}

function workflowNotificationWhen(value) {
  const parsed = new Date(value || "");
  if (!Number.isFinite(parsed.getTime())) return "";
  const relative = typeof formatRelativeOrDate === "function" ? formatRelativeOrDate(parsed.toISOString()) : "";
  const exact = parsed.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return relative ? `${relative} · ${exact}` : exact;
}

async function markWorkflowNotificationsRead(notificationIds = []) {
  const ids = [...new Set(notificationIds.map(String).filter(Boolean))].slice(0, 100);
  if (!ids.length) return true;
  const orgId = workflowNotificationState.orgId;
  const previousStatus = workflowNotificationState.status;
  const token = workflowNotificationApiToken();
  const access = workflowAccessScopedRequestKey({});
  const sessionKey = workflowNotificationSessionKey();
  const requestSequence = workflowNotificationListRequestSequence;
  const current = () => orgId === workflowNotificationState.orgId && token === workflowNotificationApiToken() && access === workflowAccessScopedRequestKey({}) && sessionKey === workflowNotificationSessionKey() && requestSequence === workflowNotificationListRequestSequence && !els.workflowNotificationsModal?.classList.contains("hidden");
  workflowNotificationState.status = "pending";
  workflowNotificationState.error = "";
  renderWorkflowNotificationsModal();
  try {
    const result = await workflowNotificationFetch("/api/workflow/notifications/read", {
      method: "POST",
      body: JSON.stringify({ orgId, notificationIds: ids })
    }, current);
    if (!current()) return false;
    const readAtUtc = new Date().toISOString();
    const idSet = new Set(ids);
    workflowNotificationState.rows = workflowNotificationState.rows.map((row) => idSet.has(String(row.notificationId || "")) ? { ...row, readAtUtc } : row);
    workflowNotificationState.unreadCount = Math.max(0, Number(result.unreadCount || 0));
    workflowNotificationState.status = "ready";
    workflowNotificationState.aggregateUnreadCount = Math.max(0, Number(workflowNotificationState.aggregateUnreadCount || 0) - Number(result.updatedCount || 0));
    renderWorkflowNotificationsModal();
    renderWorkflowNotificationChrome();
    requestWorkflowNotificationUnreadCount({ force: true });
    return true;
  } catch (error) {
    if (!current()) return false;
    workflowNotificationState.status = previousStatus === "idle" ? "ready" : previousStatus;
    workflowNotificationState.error = String(error?.message || "The read update was not recorded.");
    if (els.workflowNotificationsStatus) {
      els.workflowNotificationsStatus.classList.add("is-error");
      els.workflowNotificationsStatus.textContent = `${workflowNotificationState.error} This notification was not opened.`;
    }
    renderWorkflowNotificationsModal();
    return false;
  } finally {
    if (!current() && requestSequence === workflowNotificationListRequestSequence && workflowNotificationState.status === "pending") workflowNotificationState.status = "idle";
  }
}

function markShownWorkflowNotificationsRead() {
  return markWorkflowNotificationsRead(workflowNotificationState.rows.filter((row) => !row.readAtUtc).map((row) => row.notificationId));
}

async function openWorkflowNotification(notificationId) {
  const row = workflowNotificationState.rows.find((item) => String(item.notificationId || "") === String(notificationId || ""));
  if (!row) return;
  // Read acknowledgement is the gate, not a best-effort side effect. If it
  // fails, keep the inbox open and do not tell the user the item was handled.
  if (!row.readAtUtc && !(await markWorkflowNotificationsRead([row.notificationId]))) return;
  const target = validatedWorkflowNotificationTarget(row.deepLink);
  if (!target) {
    workflowNotificationState.status = "error";
    workflowNotificationState.error = "This notification's destination is no longer safe or available.";
    renderWorkflowNotificationsModal();
    return;
  }
  closeWorkflowNotifications({ restoreFocus: false });
  resetAssignmentWorkflowScope({ preserveSelection: true });
  assignmentScopeUiState.orgId = target.orgId;
  assignmentScopeUiState.mode = assignmentScopeAllowedModes().includes("my") ? "my" : assignmentScopeDefaultMode();
  assignmentScopeUiState.memberUserId = "";
  assignmentScopeUiState.memberIdentities = [];
  if (target.workTab) {
    oversightWorkReadController?.abort();
    const model = oversightState();
    model.tab = target.workTab; model.orgId = target.orgId;
    model.page = 1; model.pagination = null; model.request += 1; model.status = "idle";
  }
  navigateToView("assignments");
  if (!target.workTab) window.setTimeout(() => openServerClaimDrawer(target.claimId, { orgId: target.orgId, queueContext: { source: "workflow_notifications" } }), 0);
}

function validatedWorkflowNotificationTarget(deepLink) {
  try {
    const current = new URL(window.location.href);
    const target = new URL(String(deepLink || ""), current);
    const orgId = String(target.searchParams.get("orgId") || "");
    const claimId = String(target.searchParams.get("claimId") || "");
    if (target.origin !== current.origin || target.pathname !== current.pathname) return null;
    if (target.searchParams.get("view") !== "assignments" || !orgId || !claimId) return null;
    if (!teamStructureOrgOptions().some((option) => option.orgId === orgId)) return null;
    const workTab = String(target.searchParams.get("workTab") || "");
    if (workTab && (!["approvals", "requests"].includes(workTab) || !oversightWorkTabs().some(([key]) => key === workTab))) return null;
    return workTab ? { orgId, claimId, workTab } : { orgId, claimId };
  } catch (_) {
    return null;
  }
}

function teamStructureEnsureOrgSelection() {
  const options = teamStructureOrgOptions();
  if (!options.some((option) => option.orgId === teamStructureUiState.orgId)) {
    teamStructureUiState.orgId = options[0]?.orgId || "";
    teamStructureUiState.status = "idle";
    teamStructureUiState.requestKey = "";
    teamStructureUiState.members = [];
    teamStructureUiState.eligibleUsers = [];
    teamStructureUiState.error = "";
    teamStructureUiState.notice = "";
    teamStructureUiState.dirty = false;
  }
  return options;
}

function setUserMgmtView(view = "people", { load = true } = {}) {
  ensureStaffTeamSession();
  syncStaffTeamDraftFromForm(document.querySelector("#userMgmtTeamsPane [data-staff-team-form]"));
  const requested = view === "team" ? "structure" : String(view || "people").trim().toLowerCase();
  const orgMapAllowed = isTrustedSuperAdminSession();
  const next = ["people", "teams", "structure"].includes(requested) || (requested === "orgmap" && orgMapAllowed) ? requested : "people";
  userMgmtDirectoryState.tab = next;
  teamStructureUiState.view = next === "structure" ? "team" : "people";
  const people = document.getElementById("userMgmtPeoplePane");
  const teams = document.getElementById("userMgmtTeamsPane");
  const structure = document.getElementById("userMgmtTeamView");
  people?.classList.toggle("hidden", next !== "people");
  teams?.classList.toggle("hidden", next !== "teams");
  structure?.classList.toggle("hidden", next !== "structure");
  document.getElementById("userMgmtOrgMapPane")?.classList.toggle("hidden", next !== "orgmap");
  const orgMapTab = document.getElementById("userMgmtOrgMapTab");
  if (orgMapTab) orgMapTab.hidden = !orgMapAllowed;
  document.querySelectorAll("[data-user-mgmt-tab]").forEach((tab) => {
    const selected = tab.dataset.userMgmtTab === next;
    tab.classList.toggle("user-mgmt-tab--active", selected);
    tab.classList.toggle("is-active", selected);
    tab.setAttribute("aria-selected", selected ? "true" : "false");
    tab.tabIndex = selected ? 0 : -1;
  });
  const peopleOnly = [document.getElementById("userMgmtExportBtn"), document.getElementById("userMgmtNewInviteBtn")];
  peopleOnly.forEach((control) => { if (control) control.hidden = next !== "people"; });
  if (next === "teams") {
    renderStaffTeams();
    if (load && staffTeamState.status === "idle") queueMicrotask(loadStaffTeams);
  }
  if (next === "orgmap") {
    renderOrgMap();
    if (load) loadOrgMap();
  }
  if (next === "structure") {
    teamStructureEnsureOrgSelection();
    renderTeamStructureView();
    if (load && teamStructureUiState.orgId && teamStructureUiState.status === "idle") requestTeamStructure();
  }
}

function teamStructureRequestKey(orgId = teamStructureUiState.orgId) {
  return workflowAccessScopedRequestKey({ readScope: additionalPortalDataReadScope(), kind: "team-structure", base: teamStructureApiBase(), orgId: String(orgId || "") });
}

function normalizeTeamStructureUiPayload(body = {}, orgId = "", eligibleFallback = []) {
  const structure = body?.structure && typeof body.structure === "object" ? body.structure : {};
  const eligibleSource = Array.isArray(body?.eligibleUsers) ? body.eligibleUsers : eligibleFallback;
  const eligibleUsers = (Array.isArray(eligibleSource) ? eligibleSource : [])
    .map((user) => ({
      userId: String(user?.userId || "").trim(),
      displayName: String(user?.displayName || user?.email || user?.userId || "").trim(),
      email: String(user?.email || "").trim(),
      role: canonicalRole(user?.role || "")
    }))
    .filter((user) => user.userId && ["SuperAdmin", "StatesideBiller", "OffshoreBiller"].includes(user.role))
    .sort((left, right) => compareText(left.displayName || left.userId, right.displayName || right.userId));
  const savedRows = Array.isArray(structure.members) ? structure.members : [];
  const savedSupervisors = new Map(savedRows.map((row) => [String(row?.userId || "").trim().toLowerCase(), String(row?.supervisorUserId || "").trim()]));
  const eligibleKeys = new Set(eligibleUsers.map((user) => user.userId.toLowerCase()));
  const members = eligibleUsers.map((user) => ({
    userId: user.userId,
    supervisorUserId: eligibleKeys.has(String(savedSupervisors.get(user.userId.toLowerCase()) || "").toLowerCase())
      ? savedSupervisors.get(user.userId.toLowerCase())
      : ""
  }));
  const savedKeys = new Set(savedRows.map((row) => String(row?.userId || "").trim().toLowerCase()).filter(Boolean));
  const rosterChanged = savedKeys.size !== eligibleKeys.size || [...eligibleKeys].some((key) => !savedKeys.has(key));
  return {
    orgId: String(structure.orgId || orgId || "").trim(),
    version: Number.isSafeInteger(Number(structure.version)) && Number(structure.version) >= 0 ? Number(structure.version) : 0,
    members,
    eligibleUsers,
    directoryLanes: body?.directoryLanes || null,
    updatedAtUtc: String(structure.updatedAtUtc || ""),
    updatedByUserId: String(structure.updatedByUserId || ""),
    rosterChanged
  };
}

async function requestTeamStructure({ force = false } = {}) {
  const orgId = teamStructureUiState.orgId;
  const base = teamStructureApiBase();
  if (!orgId || !base || typeof fetch !== "function") {
    teamStructureUiState.status = "error";
    teamStructureUiState.error = !orgId ? "Choose a company to load its reporting lines." : "Reporting lines require the portal API.";
    renderTeamStructureView();
    return false;
  }
  const requestKey = teamStructureRequestKey(orgId);
  if (!force && teamStructureUiState.status === "ok" && teamStructureUiState.requestKey === requestKey) return true;
  const requestSeq = ++teamStructureRequestSeq;
  teamStructureUiState.status = "loading";
  teamStructureUiState.requestKey = requestKey;
  teamStructureUiState.error = "";
  teamStructureUiState.notice = "";
  teamStructureUiState.saving = false;
  renderTeamStructureView();
  const token = workflowApiToken() || trackerApiToken();
  try {
    const response = await fetch(additionalPortalDataReadUrl(`${base}/api/workflow/team-structure?orgId=${encodeURIComponent(orgId)}`), {
      method: "GET",
      cache: "no-store",
      headers: { accept: "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) }
    });
    const body = await response.json().catch(() => ({}));
    if (requestSeq !== teamStructureRequestSeq || teamStructureUiState.orgId !== orgId || teamStructureRequestKey(orgId) !== requestKey) return false;
    if (!response.ok || body.error || !body.structure || !Array.isArray(body.eligibleUsers)) {
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      teamStructureUiState.status = response.status === 409 ? "conflict" : "error";
      teamStructureUiState.error = String(body.message || body.error || `Reporting lines could not be loaded (HTTP ${response.status}).`);
      renderTeamStructureView();
      return false;
    }
    const normalized = normalizeTeamStructureUiPayload(body, orgId);
    Object.assign(teamStructureUiState, normalized, {
      status: "ok",
      requestKey,
      error: "",
      notice: normalized.rosterChanged ? "Active staff changed since these reporting lines were saved. Review who is at the top, then save." : "",
      dirty: normalized.rosterChanged,
      saving: false
    });
    renderTeamStructureView();
    return true;
  } catch (error) {
    if (requestSeq !== teamStructureRequestSeq || teamStructureUiState.orgId !== orgId || teamStructureRequestKey(orgId) !== requestKey) return false;
    teamStructureUiState.status = "error";
    teamStructureUiState.error = error?.name === "AbortError" ? "Reporting lines took too long to load." : "Reporting lines are temporarily unavailable.";
    renderTeamStructureView();
    return false;
  }
}

function teamStructureDraftCycleMessage(members = teamStructureUiState.members) {
  const parent = new Map((Array.isArray(members) ? members : []).map((row) => [String(row.userId || "").toLowerCase(), String(row.supervisorUserId || "")]));
  for (const row of (Array.isArray(members) ? members : [])) {
    const origin = String(row.userId || "");
    const seen = new Set([origin.toLowerCase()]);
    let next = String(row.supervisorUserId || "");
    while (next) {
      const key = next.toLowerCase();
      if (seen.has(key)) return "That reporting line would create a cycle. Choose a different supervisor.";
      seen.add(key);
      next = parent.get(key) || "";
    }
  }
  return "";
}

function teamStructureSummary() {
  const members = teamStructureUiState.members || [];
  const topLevel = members.filter((row) => !row.supervisorUserId).length;
  const supervisors = new Set(members.map((row) => row.supervisorUserId).filter(Boolean)).size;
  return `${formatCount(members.length)} ${members.length === 1 ? "person" : "people"} · ${formatCount(topLevel)} at the top · ${formatCount(supervisors)} ${supervisors === 1 ? "supervisor" : "supervisors"}`;
}

function teamStructureMemberMatchesSearch(row = {}, directory = new Map(), search = teamStructureUiState.search) {
  const needle = String(search || "").trim().toLowerCase();
  if (!needle) return true;
  const user = directory.get(String(row.userId || "").toLowerCase()) || {};
  return [user.displayName, user.email, roleProfiles[user.role]?.label, user.role]
    .map((value) => String(value || "").toLowerCase())
    .some((value) => value.includes(needle));
}

function renderTeamStructureView() {
  const host = document.getElementById("userMgmtTeamView");
  if (!host) return;
  const options = teamStructureEnsureOrgSelection();
  const orgSelect = options.length
    ? `<label class="team-structure-org"><span>Company</span><select id="teamStructureOrgSelect">${options.map((option) => `<option value="${escapeHtml(option.orgId)}"${option.orgId === teamStructureUiState.orgId ? " selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}</select></label>`
    : `<div class="team-structure-empty"><strong>No company is available.</strong><span>Your authenticated company scope does not include a portal organization.</span></div>`;
  const statusMessage = teamStructureUiState.error
    ? `<div class="team-structure-status team-structure-status--error" role="alert"><strong>${escapeHtml(teamStructureUiState.status === "conflict" ? "These reporting lines changed in another session." : teamStructureUiState.status === "ok" ? "Check this reporting line." : "Reporting lines are unavailable.")}</strong><span>${escapeHtml(teamStructureUiState.error)}</span></div>`
    : teamStructureUiState.notice
      ? `<div class="team-structure-status" role="status"><span>${escapeHtml(teamStructureUiState.notice)}</span></div>`
      : "";
  const directory = new Map(teamStructureUiState.eligibleUsers.map((user) => [user.userId.toLowerCase(), user]));
  const canDisplayDraft = teamStructureUiState.status === "ok" || teamStructureUiState.status === "conflict";
  const search = String(teamStructureUiState.search || "");
  const visibleMembers = canDisplayDraft ? teamStructureUiState.members.filter((row) => teamStructureMemberMatchesSearch(row, directory, search)) : [];
  const resultCount = search.trim()
    ? `${formatCount(visibleMembers.length)} of ${formatNounCount(teamStructureUiState.members.length, "person", "people")}`
    : formatNounCount(teamStructureUiState.members.length, "person", "people");
  const memberPage = managementListPageWindow("reporting", visibleMembers, JSON.stringify([teamStructureUiState.orgId, search]));
  const rows = canDisplayDraft ? memberPage.rows.map((row) => {
    const user = directory.get(row.userId.toLowerCase()) || { userId: row.userId, displayName: row.userId, role: "" };
    const supervisorOptions = teamStructureUiState.eligibleUsers
      .filter((candidate) => candidate.userId.toLowerCase() !== row.userId.toLowerCase())
      .map((candidate) => `<option value="${escapeHtml(candidate.userId)}"${candidate.userId.toLowerCase() === String(row.supervisorUserId || "").toLowerCase() ? " selected" : ""}>${escapeHtml(candidate.displayName || candidate.userId)}</option>`)
      .join("");
    return `<div class="team-structure-row" data-team-structure-person="${escapeHtml(row.userId)}">
      <div class="team-structure-person">
        <span class="user-mgmt-avatar" data-role="${escapeHtml(user.role)}" aria-hidden="true">${escapeHtml(userMgmtInitials(user.displayName || user.userId))}</span>
        <span><strong>${escapeHtml(user.displayName || user.userId)}</strong><small>${escapeHtml([roleProfiles[user.role]?.label || user.role, user.email].filter(Boolean).join(" · "))}</small></span>
      </div>
      <label><span>Reports to</span><select data-team-supervisor="${escapeHtml(row.userId)}" aria-label="Supervisor for ${escapeHtml(user.displayName || user.userId)}"><option value="">No supervisor · top level</option>${supervisorOptions}</select></label>
    </div>`;
  }).join("") : "";
  const searchControl = `<label class="team-structure-search"><span>Find a person</span><span class="team-structure-search-control"><input id="teamStructureSearch" type="search" value="${escapeHtml(search)}" placeholder="Name, email, or role" autocomplete="off" aria-label="Find a person" aria-describedby="teamStructureSearchStatus"><button class="ghost-button" data-team-structure-search-clear type="button"${search ? "" : " disabled"}>Clear</button></span><small id="teamStructureSearchStatus" aria-live="polite">${search.trim() ? `${escapeHtml(resultCount)} shown.` : "Search by name, email, or role."}</small></label>`;
  const body = teamStructureUiState.status === "loading"
    ? `<div class="team-structure-empty" role="status"><strong>Loading reporting lines…</strong><span>Reading the staff list and saved direct supervisors.</span></div>`
    : canDisplayDraft && teamStructureUiState.members.length
      ? `<div class="team-structure-summary"><strong>${escapeHtml(teamStructureSummary())}</strong><span>${teamStructureUiState.updatedAtUtc ? `Saved ${escapeHtml(formatDateTime(teamStructureUiState.updatedAtUtc))}` : "Not saved yet"}</span></div>${searchControl}${managementListPagerHtml(memberPage, "Reporting lines")}${rows ? `<div class="team-structure-list">${rows}</div>` : '<div class="team-structure-empty team-structure-no-results"><strong>No people match.</strong><span>Try another name, email, or role, or clear the search.</span><button class="ghost-button" data-team-structure-search-clear type="button">Clear search</button></div>'}`
      : canDisplayDraft
        ? `<div class="team-structure-empty"><strong>No eligible internal staff.</strong><span>Only active Super Admin, Stateside Biller, and Offshore Biller accounts in this company can appear in reporting lines.</span></div>`
        : "";
  const actionNote = teamStructureUiState.notice || "Review the people above, then save their reporting lines.";
  const actions = teamStructureUiState.orgId ? `<div class="team-structure-actions">
      <span class="team-structure-save-note">${escapeHtml(actionNote)}</span>
      <button class="ghost-button" data-team-structure-reload type="button"${teamStructureUiState.saving ? " disabled" : ""}>Reload</button>
      <button class="primary-button" data-team-structure-save type="button"${teamStructureUiState.status !== "ok" || !teamStructureUiState.dirty || teamStructureUiState.saving ? " disabled" : ""}>${teamStructureUiState.saving ? "Saving…" : "Save reporting lines"}</button>
    </div>` : "";
  host.innerHTML = `<div class="team-structure-guide" aria-labelledby="teamStructureGuideTitle">
      <div><span class="team-structure-step">1</span><p><strong id="teamStructureGuideTitle">Choose a company.</strong><span>Reporting lines are saved separately for each company.</span></p></div>
      <div><span class="team-structure-step">2</span><p><strong>Choose each person's direct supervisor.</strong><span>Pick “No supervisor” for the people at the top.</span></p></div>
      <div><span class="team-structure-step">3</span><p><strong>Save reporting lines.</strong><span>This organizes Team Activity. It never changes anyone's company access or permissions.</span></p></div>
    </div><div class="team-structure-toolbar">
      <div><h4>Who reports to whom</h4><p>For each person, choose the one supervisor who should see their work in Team Activity.</p></div>
      ${orgSelect}
    </div>${statusMessage}${body}${actions}`;
  bindManagementListPager(host, "reporting", renderTeamStructureView);
}

function handleTeamStructureSearchInput(event) {
  if (event.target?.id !== "teamStructureSearch") return;
  teamStructureUiState.search = event.target.value;
  const selectionStart = event.target.selectionStart;
  const selectionEnd = event.target.selectionEnd;
  renderTeamStructureView();
  window.requestAnimationFrame(() => {
    const nextInput = document.getElementById("teamStructureSearch");
    nextInput?.focus?.({ preventScroll: true });
    nextInput?.setSelectionRange?.(selectionStart, selectionEnd);
  });
}

function focusTeamStructureSupervisor(userId) {
  const host = document.getElementById("userMgmtTeamView");
  const control = Array.from(host?.querySelectorAll?.("[data-team-supervisor]") || [])
    .find((candidate) => candidate.dataset.teamSupervisor === String(userId || ""));
  control?.focus?.({ preventScroll: true });
}

function handleTeamStructureChange(event) {
  if (event.target?.id === "teamStructureOrgSelect") {
    const nextOrgId = String(event.target.value || "");
    if (nextOrgId === teamStructureUiState.orgId) return;
    if (teamStructureUiState.dirty && typeof confirm === "function" && !confirm("Discard unsaved reporting-line changes and switch companies?")) {
      event.target.value = teamStructureUiState.orgId;
      return;
    }
    teamStructureUiState.orgId = nextOrgId;
    teamStructureUiState.status = "idle";
    teamStructureUiState.requestKey = "";
    teamStructureUiState.members = [];
    teamStructureUiState.eligibleUsers = [];
    teamStructureUiState.error = "";
    teamStructureUiState.notice = "";
    teamStructureUiState.dirty = false;
    teamStructureUiState.search = "";
    requestTeamStructure({ force: true });
    return;
  }
  const supervisor = event.target?.closest?.("[data-team-supervisor]");
  if (!supervisor || teamStructureUiState.status !== "ok") return;
  const row = teamStructureUiState.members.find((member) => member.userId === supervisor.dataset.teamSupervisor);
  if (!row) return;
  const previous = row.supervisorUserId;
  row.supervisorUserId = String(supervisor.value || "");
  const cycle = teamStructureDraftCycleMessage();
  if (cycle) {
    row.supervisorUserId = previous;
    teamStructureUiState.error = cycle;
    teamStructureUiState.notice = "";
  } else {
    teamStructureUiState.error = "";
    teamStructureUiState.notice = "Unsaved reporting-line changes.";
    teamStructureUiState.dirty = true;
  }
  renderTeamStructureView();
  focusTeamStructureSupervisor(row.userId);
}

function handleTeamStructureClick(event) {
  if (event.target?.closest?.("[data-team-structure-search-clear]")) {
    teamStructureUiState.search = "";
    renderTeamStructureView();
    window.requestAnimationFrame(() => document.getElementById("teamStructureSearch")?.focus?.({ preventScroll: true }));
    return;
  }
  if (event.target?.closest?.("[data-team-structure-reload]")) {
    if (teamStructureUiState.dirty && typeof confirm === "function" && !confirm("Discard unsaved reporting-line changes and reload?")) return;
    requestTeamStructure({ force: true });
    return;
  }
  if (event.target?.closest?.("[data-team-structure-save]")) saveTeamStructure();
}

async function saveTeamStructure() {
  if (!isTrustedUserManagerSession() || teamStructureUiState.status !== "ok" || !teamStructureUiState.dirty || teamStructureUiState.saving) return;
  const cycle = teamStructureDraftCycleMessage();
  if (cycle) {
    teamStructureUiState.error = cycle;
    renderTeamStructureView();
    return;
  }
  const base = teamStructureApiBase();
  const orgId = teamStructureUiState.orgId;
  const requestKey = teamStructureRequestKey(orgId);
  const requestSeq = ++teamStructureRequestSeq;
  teamStructureUiState.saving = true;
  teamStructureUiState.error = "";
  teamStructureUiState.notice = "";
  renderTeamStructureView();
  const token = workflowApiToken() || trackerApiToken();
  try {
    const response = await fetch(`${base}/api/workflow/team-structure`, {
      method: "PUT",
      cache: "no-store",
      headers: { "content-type": "application/json", accept: "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        orgId,
        expectedVersion: teamStructureUiState.version,
        members: teamStructureUiState.members.map((row) => ({ userId: row.userId, supervisorUserId: row.supervisorUserId || "" })),
        commandId: newOrganizationOnboardingCommandKey()
      })
    });
    const body = await response.json().catch(() => ({}));
    if (requestSeq !== teamStructureRequestSeq || teamStructureRequestKey(orgId) !== requestKey) return false;
    if (!response.ok || body.error || !body.structure) {
      teamStructureUiState.saving = false;
      teamStructureUiState.status = response.status === 409 ? "conflict" : "ok";
      teamStructureUiState.error = String(body.message || body.error || `Reporting lines could not be saved (HTTP ${response.status}).`);
      renderTeamStructureView();
      return false;
    }
    const normalized = normalizeTeamStructureUiPayload(body, orgId, teamStructureUiState.eligibleUsers);
    Object.assign(teamStructureUiState, normalized, {
      status: "ok",
      requestKey,
      error: "",
      notice: body.structure.unchanged ? "Reporting lines were already up to date." : "Reporting lines saved.",
      dirty: false,
      saving: false
    });
    renderTeamStructureView();
    showToast(body.structure.unchanged ? "Reporting lines are already up to date." : "Reporting lines saved.");
    return true;
  } catch (_error) {
    if (requestSeq !== teamStructureRequestSeq || teamStructureRequestKey(orgId) !== requestKey) return false;
    teamStructureUiState.saving = false;
    teamStructureUiState.status = "ok";
    teamStructureUiState.error = "Reporting lines are temporarily unavailable. Your unsaved choices are still on this screen.";
    renderTeamStructureView();
    return false;
  }
}

let foregroundModalSession = null;

function foregroundModalFocusableElements(modal) {
  if (!modal || typeof modal.querySelectorAll !== "function") return [];
  return Array.from(modal.querySelectorAll('a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])'))
    .filter((control) => !control.disabled
      && control.getAttribute("aria-disabled") !== "true"
      && control.getClientRects().length > 0
      && !control.closest('[hidden], .hidden, [aria-hidden="true"]'));
}

function setForegroundModalBackgroundLocked(modal, locked) {
  if (locked) {
    const background = Array.from(document.body?.children || [])
      .filter((node) => node !== modal && node.id !== "toast" && node.id !== "interactionProgress")
      .map((node) => ({ node, inert: Boolean(node.inert) }));
    foregroundModalSession.background = background;
    background.forEach(({ node }) => { node.inert = true; });
    document.documentElement?.classList?.add("foreground-modal-open");
    document.body?.classList?.add("foreground-modal-open");
    return;
  }
  (foregroundModalSession?.background || []).forEach(({ node, inert }) => {
    if (node?.isConnected) node.inert = inert;
  });
  document.documentElement?.classList?.remove("foreground-modal-open");
  document.body?.classList?.remove("foreground-modal-open");
}

function handleForegroundModalKeydown(event) {
  const session = foregroundModalSession;
  const modal = session?.modal;
  if (!modal || !modal.isConnected || modal.classList.contains("hidden")) return;
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopImmediatePropagation();
    session.onEscape?.();
    return;
  }
  if (event.key !== "Tab") return;
  const focusable = foregroundModalFocusableElements(modal);
  if (!focusable.length) {
    event.preventDefault();
    modal.focus({ preventScroll: true });
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!modal.contains(document.activeElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus({ preventScroll: true });
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function activateForegroundModal(modal, { initialFocus = null, onEscape = null, returnFocus = document.activeElement } = {}) {
  if (!modal) return;
  if (foregroundModalSession?.modal && foregroundModalSession.modal !== modal) {
    deactivateForegroundModal(foregroundModalSession.modal, { restoreFocus: false });
  }
  foregroundModalSession = { modal, onEscape, returnFocus, background: [] };
  if (typeof modal.hasAttribute === "function" && !modal.hasAttribute("tabindex")) {
    modal.setAttribute?.("tabindex", "-1");
  }
  setForegroundModalBackgroundLocked(modal, true);
  document.addEventListener?.("keydown", handleForegroundModalKeydown, true);
  const focusInitialControl = () => {
    const target = typeof initialFocus === "string" ? modal.querySelector?.(initialFocus) : initialFocus;
    (target || foregroundModalFocusableElements(modal)[0] || modal).focus?.({ preventScroll: true });
  };
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") window.requestAnimationFrame(focusInitialControl);
  else focusInitialControl();
}

function deactivateForegroundModal(modal, { restoreFocus = true } = {}) {
  const session = foregroundModalSession;
  if (!session || session.modal !== modal) return null;
  const returnTarget = session.returnFocus;
  setForegroundModalBackgroundLocked(modal, false);
  document.removeEventListener?.("keydown", handleForegroundModalKeydown, true);
  foregroundModalSession = null;
  if (restoreFocus && returnTarget?.isConnected) returnTarget.focus?.({ preventScroll: true });
  return returnTarget;
}

let userMgmtReturnFocus = null;

function userMgmtFocusableElements(modal = document.getElementById("userMgmtModal")) {
  if (!modal) return [];
  return Array.from(modal.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])'))
    .filter((control) => !control.hidden && control.getClientRects().length > 0 && !control.closest(".hidden"));
}

function handleUserMgmtScrollablePaneKeydown(event) {
  const nestedPane = event.target?.matches?.(".staff-team-scroll-region") ? event.target : null;
  const nestedOverflow = nestedPane && typeof getComputedStyle === "function" ? getComputedStyle(nestedPane).overflowY : "";
  const pane = nestedPane && ["auto", "scroll"].includes(nestedOverflow) ? nestedPane : event.currentTarget;
  if (!pane || (!nestedPane && event.target !== pane)) return;
  const scrollingElement = document.scrollingElement || document.documentElement;
  const backgroundTop = scrollingElement.scrollTop;
  const bodyTop = document.body.scrollTop;
  const restoreBackground = () => {
    if (!document.documentElement.classList.contains("user-mgmt-modal-open")) return;
    scrollingElement.scrollTop = backgroundTop;
    document.body.scrollTop = bodyTop;
  };
  const pageStep = Math.max(120, Math.round((pane.clientHeight || 0) * 0.8));
  const keySteps = {
    ArrowDown: 48,
    ArrowUp: -48,
    PageDown: pageStep,
    PageUp: -pageStep,
    " ": event.shiftKey ? -pageStep : pageStep
  };
  if (event.key === "Home") {
    event.preventDefault();
    event.stopPropagation();
    pane.scrollTo({ top: 0, behavior: "auto" });
    restoreBackground();
    return;
  }
  if (event.key === "End") {
    event.preventDefault();
    event.stopPropagation();
    pane.scrollTo({ top: pane.scrollHeight, behavior: "auto" });
    restoreBackground();
    return;
  }
  if (!Object.prototype.hasOwnProperty.call(keySteps, event.key)) return;
  event.preventDefault();
  event.stopPropagation();
  pane.scrollBy({ top: keySteps[event.key], behavior: "auto" });
  restoreBackground();
  // Chromium can apply a residual page-scroll step after a responsive reflow
  // even when keydown was cancelled. Re-pin the already locked background on
  // the next paint; the foreground pane keeps the requested movement.
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(restoreBackground);
}

function setUserMgmtBackgroundLocked(locked) {
  document.documentElement.classList.toggle("user-mgmt-modal-open", Boolean(locked));
  document.body.classList.toggle("user-mgmt-modal-open", Boolean(locked));
  const shell = document.querySelector(".app-shell");
  if (shell) shell.inert = Boolean(locked);
}

function closeUserMgmtModal({ restoreFocus = true } = {}) {
  const modal = document.getElementById("userMgmtModal");
  if (!modal || modal.classList.contains("hidden")) return;
  modal.classList.add("hidden");
  setUserMgmtBackgroundLocked(false);
  userMgmtDirectoryState.editing = false;
  userMgmtDirectoryState.editDraft = null;
  const returnTarget = userMgmtReturnFocus;
  userMgmtReturnFocus = null;
  if (restoreFocus && returnTarget?.isConnected) returnTarget.focus({ preventScroll: true });
}

function handleUserMgmtModalKeydown(event) {
  // Native supervision and access-history dialogs own their focus and Escape.
  if (event.target?.closest?.('dialog[open]')) return;
  const modal = document.getElementById("userMgmtModal");
  const wizard = document.getElementById("userAddWizard");
  const dialog = wizard && !wizard.classList.contains("hidden") ? wizard : modal;
  if (!dialog || dialog.classList.contains("hidden")) return;
  const activeTab = event.target?.closest?.("[data-user-mgmt-tab]");
  if (activeTab && modal.contains(activeTab) && ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
    const tabs = Array.from(modal.querySelectorAll('[role="tab"][data-user-mgmt-tab]'))
      .filter((tab) => !tab.hidden && !tab.disabled && tab.getClientRects().length > 0);
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex >= 0 && tabs.length) {
      let nextIndex = currentIndex;
      if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      else if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
      else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = tabs.length - 1;
      const nextTab = tabs[nextIndex];
      event.preventDefault();
      setUserMgmtTab(nextTab.dataset.userMgmtTab);
      nextTab.focus({ preventScroll: true });
      return;
    }
  }
  if (event.key === "Escape") {
    event.preventDefault();
    if (dialog === wizard) requestUserAddWizardExit(); else closeUserMgmtModal();
    return;
  }
  if (event.key !== "Tab") return;
  const focusable = userMgmtFocusableElements(dialog);
  if (!focusable.length) {
    event.preventDefault();
    dialog.focus?.({ preventScroll: true });
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!dialog.contains(document.activeElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus({ preventScroll: true });
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function openUserMgmtModal() {
  if (!isTrustedUserManagerSession()) {
    showToast("You do not have permission to manage users.");
    return;
  }
  const modal = document.getElementById("userMgmtModal");
  if (!modal) return;
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }
  userMgmtReturnFocus = document.activeElement;
  modal.classList.remove("hidden");
  setUserMgmtBackgroundLocked(true);
  userMgmtDirectoryState.tab = userMgmtDirectoryState.tab || "people";
  userMgmtDirectoryState.editing = false;
  userMgmtDirectoryState.editDraft = null;
  // Warm the server company roster (org ID -> name) so the directory, rail,
  // and edit checklists label companies by real name. No-op when the tracker
  // API is offline — labels fall back to loaded data, then raw IDs.
  try { requestClientPaymentChecks(); } catch (_) { /* fall back to raw IDs */ }
  // D3 S4: the portal roster feeds the same pickers, and it is the only source
  // that can name a group with no claims. Fire-and-forget like its neighbour.
  try { requestPortalOrgDirectory(); } catch (_) { /* pickers keep the claim-derived roster */ }
  try { refreshUserActivityAdmin({ force: false }); } catch (_) { /* activity is optional */ }
  recordUserActivity({ featureKey: "users", actionKey: "manage_users_open", route: "manage-users" });
  // Directory first, invite lifecycle second. This removes browser-only rows
  // before pending server invites are merged into the same shared roster.
  refreshAuthoritativeUserGovernance();
  renderUserMgmtList();
  setUserMgmtView(teamStructureUiState.view, { load: true });
  if (teamStructureUiState.view === "people" && window.matchMedia?.("(min-width: 761px)").matches) {
    document.getElementById("userMgmtSearch")?.focus({ preventScroll: true });
  }
}

function staffTeamSessionKey() {
  return portalUserDirectoryAccessScopedRequestKey({ kind: "staff-teams", base: portalUserDirectoryApiBase() });
}

function ensureStaffTeamSession() {
  const requestKey = staffTeamSessionKey();
  if (staffTeamState.requestKey !== requestKey) {
    staffTeamState.controller?.abort();
    staffTeamState.peopleController?.abort();
    staffTeamState.personMemberships?.controller?.abort();
    // The key contains session credentials: it stays in memory and is never logged.
    const pendingUserKey = staffTeamState.requestKey === undefined ? staffTeamState.pendingUserKey : "";
    staffTeamState = { requestKey, status: "idle", teams: [], selectedId: "", editing: false, draft: null, error: "", pendingUserKey, saving: false, formError: "", notice: "", search: "", archivingId: "" };
  }
  return requestKey;
}

function updateStaffTeamChoiceCounts(form) {
  form?.querySelectorAll("[data-staff-team-choice-count]").forEach((count) => {
    const choices = [...count.closest("fieldset").querySelectorAll('input[type="checkbox"]')];
    count.textContent = `${choices.filter((choice) => choice.checked).length} of ${choices.length} selected`;
  });
}

function bindStaffTeamChoiceSearch(form) {
  form?.querySelectorAll("fieldset:not([data-server-members])").forEach((fieldset) => {
    const choices = [...fieldset.querySelectorAll('input[type="checkbox"]')];
    if (!choices.length) return;
    const legend = fieldset.querySelector("legend")?.textContent || "options";
    const search = document.createElement("input");
    search.type = "search";
    search.placeholder = `Search ${legend.toLowerCase()}`;
    search.setAttribute("aria-label", search.placeholder);
    search.dataset.staffTeamChoiceSearch = "";
    const count = document.createElement("small");
    count.dataset.staffTeamChoiceCount = "";
    const empty = document.createElement("p");
    empty.className = "staff-team-choice-empty";
    empty.textContent = "No matches. Try a different search.";
    empty.hidden = true;
    fieldset.prepend(count);
    fieldset.prepend(search);
    fieldset.append(empty);
    search.addEventListener("input", () => {
      const needle = search.value.trim().toLowerCase();
      let visible = 0;
      choices.forEach((choice) => {
        const label = choice.closest("label");
        label.hidden = !label.textContent.toLowerCase().includes(needle);
        if (!label.hidden) visible += 1;
      });
      empty.hidden = visible > 0;
    });
  });
  updateStaffTeamChoiceCounts(form);
}

function syncStaffTeamDraft(form) {syncStaffTeamDraftFromForm(form);return staffTeamState.draft || {};}

function staffTeamFormError(message) {
  staffTeamState.formError = message;
  renderStaffTeams();
  document.querySelector(".staff-team-form-error")?.focus();
}

function userMgmtPersonKey(user = {}) {
  return String(user.email || user.userId || user.username || user.id || "").trim().toLowerCase();
}

function staffTeamUserKey(user = {}) {
  return String(user.userId || user.username || user.email || "").trim().toLowerCase();
}

function staffTeamSessionKey() {
  return portalUserDirectoryAccessScopedRequestKey({ kind: "staff-teams", base: portalUserDirectoryApiBase() });
}

function ensureStaffTeamSession() {
  const requestKey = staffTeamSessionKey();
  if (staffTeamState.requestKey !== requestKey) {
    // The key contains session credentials: it stays in memory and is never logged.
    const pendingUserKey = staffTeamState.requestKey === undefined ? staffTeamState.pendingUserKey : "";
    staffTeamState = { requestKey, status: "idle", teams: [], selectedId: "", editing: false, draft: null, error: "", pendingUserKey, saving: false, formError: "", notice: "", search: "", archivingId: "" };
  }
  return requestKey;
}

function staffTeamsForUser(user = {}) {
  ensureStaffTeamSession();
  if (!isTrustedUserManagerSession()) return [];
  const userKey = staffTeamUserKey(user);
  if (portalUserDirectoryHostedMode()) return staffTeamState.personMemberships?.userKey === userKey ? staffTeamState.personMemberships.teams : [];
  return (staffTeamState.teams || []).filter(team => normalizeUserScopeList(team.memberUserKeys).some(key => String(key).trim().toLowerCase() === userKey));
}

function renderSelectedPersonTeams(user) {
  const host=document.querySelector('[data-user-team-memberships]');
  if(!host || host.dataset.userTeamMemberships!==staffTeamUserKey(user))return;
  const membership=staffTeamState.personMemberships;
  if(!membership || membership.userKey!==staffTeamUserKey(user) || membership.status==='loading'){host.textContent='Loading team memberships…';return;}
  if(membership.status!=='ready'){host.innerHTML='<span>Team memberships could not load.</span><button type="button" class="ghost-button" data-person-team-retry>Retry</button>';host.querySelector('[data-person-team-retry]').addEventListener('click',()=>loadSelectedPersonTeams(user,{force:true}));return;}
  const count=Number(membership.pageInfo.totalRows||0),view=managementServerPage('person-teams',membership.teams,membership.pageInfo);
  host.innerHTML='<span>'+ (count ? escapeHtml(formatNounCount(count,'team'))+': '+escapeHtml(membership.teams.map(team=>team.name || 'Unnamed team').join(', ')) : 'Not on a team yet')+'</span>'+managementListPagerHtml(view,'Team memberships');
  bindManagementServerPager(host,'person-teams',page=>loadSelectedPersonTeams(user,{page,force:true}));
}

async function loadSelectedPersonTeams(user,{page=1,force=false}={}) {
  if(!portalUserDirectoryHostedMode() || !isTrustedUserManagerSession())return false;
  const session=ensureStaffTeamSession(),userKey=staffTeamUserKey(user),key=JSON.stringify([session,userKey,page]);
  if(!userKey)return false;
  const prior=staffTeamState.personMemberships;
  if(!force && prior?.key===key && prior.status==='loading'){renderSelectedPersonTeams(user);return false;}
  prior?.controller?.abort();const controller=new AbortController();
  const membership={key,userKey,controller,status:'loading',teams:[],pageInfo:{page,pageSize:50,totalRows:0}};
  staffTeamState.personMemberships=membership;
  const current=()=>staffTeamState.personMemberships===membership && staffTeamSessionKey()===session && isTrustedUserManagerSession();
  renderSelectedPersonTeams(user);
  try{
    const query=new URLSearchParams({memberUserKey:userKey,page:String(page),pageSize:'50'});
    const response=await fetch(portalUserDirectoryApiBase()+'/api/users/teams?'+query,{cache:'no-store',signal:controller.signal,headers:{authorization:'Bearer '+(workflowApiToken() || trackerApiToken())}});
    const body=await response.json().catch(()=>({}));if(!current())return false;
    if(!response.ok || !Array.isArray(body.teams) || !body.page)throw Error('Team memberships unavailable');
    Object.assign(membership,{status:'ready',teams:body.teams,pageInfo:body.page});return true;
  }catch(error){if(current())Object.assign(membership,{status:'error',teams:[]});return false;}
  finally{if(current())renderSelectedPersonTeams(user);}
}

function renderUserMgmtTabs() {
  setUserMgmtView(userMgmtDirectoryState.tab, { load: false });
}

// ---- Org Map (Manage Users, Super Admin only) --------------------------------
// Who is responsible for each company: the stateside owner, the Colombia and
// India staff whose logins reach it, and the client logins with the primary
// contacts starred; plus a works-for chart and the shared people. Labels only:
// the server (backend/orgMap.js) derives company lists from existing logins and
// never changes access from anything saved here.
const ORG_MAP_LOCATION_LABELS = { stateside: "Stateside", colombia: "Colombia", india: "India" };
const ORG_MAP_ROLE_LABELS = { SuperAdmin: "Super Admin", StatesideBiller: "Stateside Biller", OffshoreBiller: "Offshore Biller", Client: "Client", RedactedClient: "Client (no financials)" };
let orgMapState = { session: "", status: "idle", error: "", map: null, view: "companies", search: "", saving: false, notice: "", reviewOpen: false, controller: null };

function orgMapSessionKey() {
  return portalUserDirectoryAccessScopedRequestKey({ kind: "org-map", base: portalUserDirectoryApiBase() });
}

function ensureOrgMapSession() {
  const session = orgMapSessionKey();
  if (orgMapState.session !== session) {
    orgMapState.controller?.abort();
    orgMapState = { ...orgMapState, session, status: "idle", error: "", map: null, saving: false, notice: "", reviewOpen: false, controller: null };
  }
  return session;
}

async function loadOrgMap(force = false) {
  if (!isTrustedSuperAdminSession()) return false;
  const session = ensureOrgMapSession();
  if (!force && ["ready", "loading"].includes(orgMapState.status)) return orgMapState.status === "ready";
  const base = portalUserDirectoryApiBase(), token = workflowApiToken() || trackerApiToken();
  if (!base || !token) { Object.assign(orgMapState, { status: "error", error: "The Org Map is unavailable until the portal server is connected." }); renderOrgMap(); return false; }
  orgMapState.controller?.abort();
  const controller = new AbortController();
  Object.assign(orgMapState, { controller, status: "loading", error: "" });
  if (!orgMapState.map) renderOrgMap();
  try {
    const response = await fetch(additionalPortalDataReadUrl(`${base}/api/users/org-map`), { cache: "no-store", signal: controller.signal, headers: { authorization: `Bearer ${token}` } });
    const payload = await response.json().catch(() => ({}));
    if (orgMapSessionKey() !== session || controller.signal.aborted) return false;
    if (!response.ok || !Array.isArray(payload.companies)) throw Error(payload.message || "The Org Map could not load.");
    Object.assign(orgMapState, { status: "ready", map: payload, error: "" });
    renderOrgMap();
    return true;
  } catch (error) {
    if (error?.name === "AbortError" || orgMapSessionKey() !== session) return false;
    Object.assign(orgMapState, { status: "error", error: error.message || "The Org Map could not load." });
    renderOrgMap();
    return false;
  }
}

async function saveOrgMapEdits(edits, successNotice = "Saved.") {
  if (!isTrustedSuperAdminSession() || orgMapState.saving || !edits.length) return false;
  const session = ensureOrgMapSession();
  const base = portalUserDirectoryApiBase(), token = workflowApiToken() || trackerApiToken();
  if (!base || !token) { showToast("The Org Map is unavailable until the portal server is connected."); return false; }
  Object.assign(orgMapState, { saving: true, notice: "" });
  renderOrgMap();
  try {
    const response = await fetch(`${base}/api/users/org-map`, { method: "POST", cache: "no-store", headers: { "content-type": "application/json", authorization: `Bearer ${token}` }, body: JSON.stringify({ edits }) });
    const payload = await response.json().catch(() => ({}));
    if (orgMapSessionKey() !== session) return false;
    if (!response.ok || !Array.isArray(payload.companies)) {
      Object.assign(orgMapState, { saving: false, notice: payload.message || "That change could not be saved." });
      if (response.status === 409) { orgMapState.status = "idle"; loadOrgMap(true); }
      renderOrgMap();
      return false;
    }
    Object.assign(orgMapState, { saving: false, map: payload, status: "ready", notice: successNotice });
    renderOrgMap();
    return true;
  } catch (_error) {
    if (orgMapSessionKey() !== session) return false;
    Object.assign(orgMapState, { saving: false, notice: "That change could not be saved. Check your connection and try again." });
    renderOrgMap();
    return false;
  }
}

function orgMapPeople() {
  return new Map((orgMapState.map?.people || []).map((person) => [person.userKey, person]));
}

function orgMapPersonName(people, userKey) {
  return people.get(userKey)?.displayName || "Unknown person";
}

function orgMapLocationBadge(location) {
  return location ? `<span class="org-map-badge org-map-badge--${escapeHtml(location)}">${escapeHtml(ORG_MAP_LOCATION_LABELS[location] || location)}</span>` : "";
}

function orgMapChip(people, userKey, { showLocation = false } = {}) {
  const person = people.get(userKey);
  if (!person) return "";
  const title = [person.email, ORG_MAP_ROLE_LABELS[person.role] || person.role, person.responsibility].filter(Boolean).join(" · ");
  return `<span class="org-map-chip" title="${escapeHtml(title)}">${escapeHtml(person.displayName)}${showLocation ? orgMapLocationBadge(person.location) : ""}${person.status === "invited" ? '<small class="org-map-pending">invite pending</small>' : ""}</span>`;
}

function orgMapMatches(values) {
  const needle = String(orgMapState.search || "").trim().toLowerCase();
  return !needle || values.some((value) => String(value || "").toLowerCase().includes(needle));
}

function orgMapSuggestionLines(people) {
  const suggestions = orgMapState.map?.suggestions || { edits: [] };
  const companies = new Map((orgMapState.map?.companies || []).map((company) => [company.orgId, company.name]));
  return suggestions.edits.map((edit) => {
    if (edit.kind === "company") return `${companies.get(edit.orgId) || "Company"}: owner ${orgMapPersonName(people, edit.ownerUserKey)}`;
    const parts = [];
    if (edit.location) parts.push(ORG_MAP_LOCATION_LABELS[edit.location] || edit.location);
    if (edit.reportsToUserKey) parts.push(`works for ${orgMapPersonName(people, edit.reportsToUserKey)}`);
    if (edit.responsibility) parts.push(`“${edit.responsibility}”`);
    return `${orgMapPersonName(people, edit.userKey)}: ${parts.join(", ")}`;
  });
}

function renderOrgMapSuggestions(people) {
  const suggestions = orgMapState.map?.suggestions;
  if (!suggestions || (!suggestions.edits.length && !suggestions.unmatched.length && !suggestions.ambiguous.length)) return "";
  const lines = orgMapSuggestionLines(people);
  const missing = [
    suggestions.unmatched.length ? `<p><strong>No login found:</strong> ${escapeHtml(suggestions.unmatched.join(", "))}</p>` : "",
    suggestions.ambiguous.length ? `<p><strong>More than one login matches:</strong> ${escapeHtml(suggestions.ambiguous.join(", "))}. Set these by hand.</p>` : ""
  ].join("");
  if (!lines.length) return `<details class="org-map-suggest"><summary>Team map names without a single matching login</summary>${missing}</details>`;
  return `<section class="org-map-suggest" aria-label="Team map suggestions">
    <div class="org-map-suggest-head"><span><strong>${formatNounCount(lines.length, "suggestion")} from the team map.</strong> Review them, then apply. Nothing changes until you do.</span>
      <span class="org-map-suggest-actions"><button class="ghost-button" data-org-map-review type="button" aria-expanded="${orgMapState.reviewOpen ? "true" : "false"}">${orgMapState.reviewOpen ? "Hide" : "Review"}</button><button class="primary-button btn--utility" data-org-map-apply type="button">Apply all</button></span></div>
    ${orgMapState.reviewOpen ? `<ul class="org-map-suggest-list">${lines.map((line) => `<li>${escapeHtml(line)}</li>`).join("")}</ul>${missing}` : ""}
  </section>`;
}

function renderOrgMapCompanies(people) {
  const map = orgMapState.map;
  const owners = map.people.filter((person) => ["SuperAdmin", "StatesideBiller"].includes(person.role));
  const cards = map.companies.filter((company) => orgMapMatches([company.name, ...[company.ownerUserKey, ...company.stateside, ...company.colombia, ...company.india, ...company.locationNotSet, ...company.clientContacts].map((key) => orgMapPersonName(people, key))]));
  if (!cards.length) return '<div class="user-mgmt-empty"><strong>No companies match.</strong><span>Try another company or person name.</span></div>';
  const row = (label, keys, opts = {}) => keys.length ? `<div class="org-map-row${opts.warn ? " org-map-row--warn" : ""}"><dt>${escapeHtml(label)}</dt><dd>${keys.map((key) => orgMapChip(people, key, opts)).join("")}</dd></div>` : "";
  return `<div class="org-map-grid">${cards.map((company) => {
    const contacts = company.clientContacts.map((key) => {
      const person = people.get(key);
      if (!person) return "";
      return `<li><button class="org-map-star${person.isPrimaryContact ? " is-on" : ""}" data-org-map-star="${escapeHtml(key)}" type="button" aria-pressed="${person.isPrimaryContact ? "true" : "false"}" title="${person.isPrimaryContact ? "Primary contact. Click to remove" : "Mark as primary contact"}" aria-label="${escapeHtml(`${person.isPrimaryContact ? "Remove" : "Mark"} ${person.displayName} as primary contact`)}">${person.isPrimaryContact ? "★" : "☆"}</button><span>${escapeHtml(person.displayName)}${person.email && person.email !== person.displayName ? ` <small>${escapeHtml(person.email)}</small>` : ""}${person.status === "invited" ? '<small class="org-map-pending">invite pending</small>' : ""}</span></li>`;
    }).join("");
    return `<article class="org-map-company" aria-label="${escapeHtml(company.name)}">
      <header><h5>${escapeHtml(company.name)}</h5>
        <label class="org-map-owner"><span>Stateside owner</span><select data-org-map-owner="${escapeHtml(company.orgId)}" aria-label="Stateside owner for ${escapeHtml(company.name)}"><option value="">No owner set</option>${owners.map((person) => `<option value="${escapeHtml(person.userKey)}"${person.userKey === company.ownerUserKey ? " selected" : ""}>${escapeHtml(person.displayName)}</option>`).join("")}</select></label></header>
      ${company.ownerMissing ? '<p class="org-map-warn">The saved owner no longer has a staff login. Choose a new owner.</p>' : ""}
      <dl class="org-map-rows">
        ${row("Colombia", company.colombia)}
        ${row("India", company.india)}
        ${row("Other stateside", company.stateside)}
        ${row("Location not set", company.locationNotSet, { warn: true })}
        ${company.teams.length ? `<div class="org-map-row"><dt>Teams</dt><dd>${company.teams.map((team) => `<span class="org-map-chip org-map-chip--team">${escapeHtml(team.name)}</span>`).join("")}</dd></div>` : ""}
      </dl>
      ${!company.colombia.length && !company.india.length && !company.stateside.length && !company.locationNotSet.length ? '<p class="org-map-muted">No staff logins beyond the shared team.</p>' : ""}
      <div class="org-map-contacts"><h6>Client contacts</h6>${contacts ? `<ul>${contacts}</ul>` : '<p class="org-map-muted">No client logins yet.</p>'}</div>
    </article>`;
  }).join("")}</div>`;
}

function renderOrgMapChart(people) {
  const chart = orgMapState.map.chart;
  const seen = new Set();
  const node = (key) => {
    if (seen.has(key)) return "";
    seen.add(key);
    const person = people.get(key);
    if (!person) return "";
    const children = (chart.children[key] || []).slice().sort((a, b) => orgMapPersonName(people, a).localeCompare(orgMapPersonName(people, b)));
    return `<li><div class="org-map-node"><strong>${escapeHtml(person.displayName)}</strong>${orgMapLocationBadge(person.location)}<small>${escapeHtml(ORG_MAP_ROLE_LABELS[person.role] || person.role)}${person.responsibility ? ` · ${escapeHtml(person.responsibility)}` : ""}</small></div>${children.length ? `<ul>${children.map(node).join("")}</ul>` : ""}</li>`;
  };
  const roots = chart.roots.slice().sort((a, b) => ((chart.children[b] || []).length > 0) - ((chart.children[a] || []).length > 0) || orgMapPersonName(people, a).localeCompare(orgMapPersonName(people, b)));
  const withReports = roots.filter((key) => (chart.children[key] || []).length);
  const alone = roots.filter((key) => !(chart.children[key] || []).length);
  return `<div class="org-map-chart">
    ${withReports.length ? `<ul class="org-map-tree">${withReports.map(node).join("")}</ul>` : '<div class="user-mgmt-empty"><strong>No works-for lines yet.</strong><span>Set “Works for” on the Staff details view, or apply the team map suggestions.</span></div>'}
    ${alone.length ? `<section class="org-map-alone"><h6>Not placed in the chart</h6><p class="org-map-muted">These staff have no “Works for” line and nobody works for them.</p><div>${alone.map((key) => orgMapChip(people, key, { showLocation: true })).join("")}</div></section>` : ""}
  </div>`;
}

function renderOrgMapShared(people) {
  const shared = orgMapState.map.shared.map((key) => people.get(key)).filter(Boolean);
  if (!shared.length) return '<div class="user-mgmt-empty"><strong>Nobody covers every company.</strong><span>People whose logins reach all companies appear here.</span></div>';
  return `<p class="org-map-muted">These logins reach every company, so they are listed here instead of on each company.</p>
    <table class="org-map-table"><thead><tr><th scope="col">Name</th><th scope="col">Location</th><th scope="col">Role</th><th scope="col">Responsibility</th></tr></thead><tbody>
    ${shared.map((person) => `<tr><td>${escapeHtml(person.displayName)}</td><td>${orgMapLocationBadge(person.location) || '<span class="org-map-muted">Not set</span>'}</td><td>${escapeHtml(ORG_MAP_ROLE_LABELS[person.role] || person.role)}</td><td>${person.responsibility ? escapeHtml(person.responsibility) : '<span class="org-map-muted">None</span>'}</td></tr>`).join("")}
    </tbody></table>`;
}

function renderOrgMapStaff(people) {
  const staff = orgMapState.map.people.filter((person) => person.internal && orgMapMatches([person.displayName, person.email, person.responsibility, ORG_MAP_LOCATION_LABELS[person.location]]));
  if (!staff.length) return '<div class="user-mgmt-empty"><strong>No staff match.</strong><span>Try another name.</span></div>';
  const everyone = orgMapState.map.people.filter((person) => person.internal);
  return `<p class="org-map-muted">Changes save as you make them. These are labels for the Org Map only. They never change what anyone can see.</p>
    <table class="org-map-table org-map-table--edit"><thead><tr><th scope="col">Name</th><th scope="col">Location</th><th scope="col">Works for</th><th scope="col">Responsibility</th><th scope="col">Companies</th></tr></thead><tbody>
    ${staff.map((person) => `<tr data-org-map-person="${escapeHtml(person.userKey)}">
      <td><strong>${escapeHtml(person.displayName)}</strong><small>${escapeHtml(ORG_MAP_ROLE_LABELS[person.role] || person.role)}</small></td>
      <td><select data-org-map-field="location" aria-label="Location for ${escapeHtml(person.displayName)}"><option value="">Not set</option>${orgMapState.map.locations.map((location) => `<option value="${escapeHtml(location)}"${location === person.location ? " selected" : ""}>${escapeHtml(ORG_MAP_LOCATION_LABELS[location] || location)}</option>`).join("")}</select></td>
      <td><select data-org-map-field="reportsToUserKey" aria-label="Who ${escapeHtml(person.displayName)} works for"><option value="">Nobody</option>${everyone.filter((other) => other.userKey !== person.userKey).map((other) => `<option value="${escapeHtml(other.userKey)}"${other.userKey === person.reportsToUserKey ? " selected" : ""}>${escapeHtml(other.displayName)}</option>`).join("")}</select></td>
      <td><input data-org-map-field="responsibility" type="text" maxlength="400" value="${escapeHtml(person.responsibility)}" placeholder="e.g. Payment posting" aria-label="Responsibility for ${escapeHtml(person.displayName)}"></td>
      <td>${person.coversAllCompanies ? "All companies" : formatNounCount(person.orgIds.length, "company", "companies")}</td>
    </tr>`).join("")}
    </tbody></table>`;
}

function renderOrgMap() {
  const host = document.getElementById("userMgmtOrgMapPane");
  if (!host || userMgmtDirectoryState.tab !== "orgmap") return;
  if (!isTrustedSuperAdminSession()) { host.innerHTML = ""; return; }
  ensureOrgMapSession();
  if (!orgMapState.map) {
    host.innerHTML = orgMapState.status === "error"
      ? `<div class="user-mgmt-empty" role="alert"><strong>The Org Map could not load.</strong><span>${escapeHtml(orgMapState.error)}</span><button class="ghost-button" data-org-map-retry type="button">Try again</button></div>`
      : '<div class="user-mgmt-empty" role="status">Loading the Org Map…</div>';
    host.querySelector("[data-org-map-retry]")?.addEventListener("click", () => loadOrgMap(true));
    return;
  }
  const people = orgMapPeople();
  const views = [["companies", "Companies"], ["chart", "Org chart"], ["shared", "Shared roles"], ["staff", "Staff details"]];
  const body = orgMapState.view === "chart" ? renderOrgMapChart(people)
    : orgMapState.view === "shared" ? renderOrgMapShared(people)
      : orgMapState.view === "staff" ? renderOrgMapStaff(people)
        : renderOrgMapCompanies(people);
  const focused = document.activeElement && host.contains(document.activeElement) ? document.activeElement : null;
  const focusKey = focused?.matches?.("[data-org-map-search]") ? "search" : "";
  host.innerHTML = `<div class="org-map">
    <div class="org-map-toolbar"><div><h4>Org Map</h4><p>Who is responsible for each company. Company lists come from each person's login access. Nothing here changes access.</p></div>
      <button class="ghost-button" data-org-map-retry type="button">Refresh</button></div>
    ${renderOrgMapSuggestions(people)}
    ${orgMapState.notice ? `<div class="staff-team-notice" role="status">${escapeHtml(orgMapState.notice)}</div>` : ""}
    <div class="org-map-controls"><div class="org-map-views" role="group" aria-label="Org Map view">${views.map(([key, label]) => `<button class="org-map-view${orgMapState.view === key ? " is-active" : ""}" data-org-map-view="${key}" type="button" aria-pressed="${orgMapState.view === key ? "true" : "false"}">${label}</button>`).join("")}</div>
      ${["companies", "staff"].includes(orgMapState.view) ? `<input class="org-map-search" data-org-map-search type="search" value="${escapeHtml(orgMapState.search)}" placeholder="${orgMapState.view === "staff" ? "Find a person" : "Find a company or person"}" aria-label="Search the Org Map" autocomplete="off">` : ""}</div>
    <div class="org-map-body">${body}</div>
  </div>`;
  if (focusKey === "search") { const input = host.querySelector("[data-org-map-search]"); input?.focus({ preventScroll: true }); input?.setSelectionRange?.(input.value.length, input.value.length); }
  host.querySelector("[data-org-map-retry]")?.addEventListener("click", () => { orgMapState.notice = ""; loadOrgMap(true); });
  host.querySelector("[data-org-map-review]")?.addEventListener("click", () => { orgMapState.reviewOpen = !orgMapState.reviewOpen; renderOrgMap(); });
  host.querySelector("[data-org-map-apply]")?.addEventListener("click", () => {
    const edits = orgMapState.map?.suggestions?.edits || [];
    saveOrgMapEdits(edits, `Applied ${formatNounCount(edits.length, "suggestion")} from the team map.`).then((saved) => { if (saved) orgMapState.reviewOpen = false; });
  });
  host.querySelectorAll("[data-org-map-view]").forEach((button) => button.addEventListener("click", () => { orgMapState.view = button.dataset.orgMapView; orgMapState.notice = ""; renderOrgMap(); }));
  host.querySelector("[data-org-map-search]")?.addEventListener("input", (event) => { orgMapState.search = event.target.value; renderOrgMap(); });
  host.querySelectorAll("[data-org-map-owner]").forEach((select) => select.addEventListener("change", () => {
    const company = orgMapState.map.companies.find((row) => row.orgId === select.dataset.orgMapOwner);
    if (!company) return;
    saveOrgMapEdits([{ kind: "company", orgId: company.orgId, ownerUserKey: select.value, expectedVersion: company.version }], `${company.name}: owner ${select.value ? `set to ${orgMapPersonName(people, select.value)}` : "cleared"}.`);
  }));
  host.querySelectorAll("[data-org-map-star]").forEach((button) => button.addEventListener("click", () => {
    const person = people.get(button.dataset.orgMapStar);
    if (!person) return;
    saveOrgMapEdits([{ kind: "person", userKey: person.userKey, isPrimaryContact: !person.isPrimaryContact, expectedVersion: person.version }], `${person.displayName} ${person.isPrimaryContact ? "is no longer" : "is now"} a primary contact.`);
  }));
  host.querySelectorAll("[data-org-map-person]").forEach((row) => {
    const person = people.get(row.dataset.orgMapPerson);
    if (!person) return;
    const commit = (field, value) => {
      if (String(person[field] || "") === value) return;
      saveOrgMapEdits([{ kind: "person", userKey: person.userKey, [field]: value, expectedVersion: person.version }], `${person.displayName}: saved.`);
    };
    row.querySelectorAll("select[data-org-map-field]").forEach((select) => select.addEventListener("change", () => commit(select.dataset.orgMapField, select.value)));
    const input = row.querySelector("input[data-org-map-field]");
    input?.addEventListener("change", () => commit("responsibility", input.value.trim().replace(/\s+/g, " ")));
    input?.addEventListener("keydown", (event) => { if (event.key === "Enter") { event.preventDefault(); input.blur(); } });
  });
  if (orgMapState.saving) host.querySelectorAll("button,input,select").forEach((control) => { control.disabled = true; });
}

function setUserMgmtTab(nextTab) {
  setUserMgmtView(nextTab, { load: true });
}

async function loadStaffTeams(force=false) {
  if(!isTrustedUserManagerSession())return false;
  const session=ensureStaffTeamSession(),page=staffTeamState.page || 1,search=String(staffTeamState.search || "");
  const base=portalUserDirectoryApiBase(),token=workflowApiToken() || trackerApiToken();
  if(!base || !token){Object.assign(staffTeamState,{status:"error",error:"Teams are unavailable until the portal server is connected."});renderStaffTeams();return false;}
  const key=JSON.stringify([session,page,search]);
  if(force){staffTeamState.personMemberships?.controller?.abort();staffTeamState.personMemberships=null;}
  if(!force && staffTeamState.readKey===key && ["ready","loading"].includes(staffTeamState.status))return staffTeamState.status==="ready";
  syncStaffTeamDraftFromForm(document.querySelector('[data-staff-team-form]'));
  staffTeamState.controller?.abort();const controller=new AbortController();staffTeamState.controller=controller;
  const sequence=++staffTeamsReadSequence,current=()=>sequence===staffTeamsReadSequence && staffTeamSessionKey()===session && isTrustedUserManagerSession();
  Object.assign(staffTeamState,{readKey:key,status:"loading",error:""});
  if(!staffTeamState.teams.length && !staffTeamState.editing)renderStaffTeams();
  try{
    const query=new URLSearchParams({page:String(page),pageSize:"50",search});
    if (activeClientGroupTenant()) query.set("companyOrgId", activeClientGroupServerOrgId());
    const response=await fetch(additionalPortalDataReadUrl(`${portalUserDirectoryApiBase()}/api/users/teams?${query}`),{cache:"no-store",signal:controller.signal,headers:{authorization:`Bearer ${workflowApiToken() || trackerApiToken()}`}});
    const payload=await response.json().catch(()=>({}));if(!current())return false;
    if(!response.ok){if([401,403].includes(response.status)){Object.assign(staffTeamState,{teams:[],selectedTeam:null,selectedId:"",draft:null,editing:false});}throw Error(payload.message || "Teams could not load.");}
    if(!Array.isArray(payload.teams))throw Error("Teams could not load.");
    Object.assign(staffTeamState,{status:"ready",teams:payload.teams,pageInfo:payload.page || {page,pageSize:50,totalRows:payload.count || 0},error:""});
    renderStaffTeams();return true;
  }catch(error){if(!current())return false;Object.assign(staffTeamState,{status:"error",error:error.message || "Teams could not load."});renderStaffTeams();return false;}
}

function blankStaffTeamDraft(team = null) {
  const internalUsers = governanceUserRows().filter((user) => !isExternalClientRole(user.role));
  const defaultMember = staffTeamState.pendingUserKey || "";
  return {
    teamId: String(team?.teamId || ""),
    version: Number(team?.version || 0),
    name: String(team?.name || ""),
    leadUserKey: String(team?.leadUserKey || defaultMember || ""),
    memberUserKeys: normalizeUserScopeList(team?.memberUserKeys || (defaultMember ? [defaultMember] : [])),
    orgIds: normalizeUserScopeList(team?.orgIds || activeClientGroupOrgIds()),
    followUpActions: normalizeUserScopeList(team?.followUpActions || getSelectableActionRules().map((rule) => rule.action)),
    knownUserKeys: internalUsers.map(staffTeamUserKey).filter(Boolean)
  };
}

function staffTeamMemberName(userKey) {
  const key = String(userKey || "").toLowerCase();
  const user = [...(staffTeamState.people || []),...(staffTeamState.selectedTeam?.people || []),...governanceUserRows()].find((row) => staffTeamUserKey(row) === key);
  return user?.name || user?.displayName || userKey || "Unknown person";
}

function staffTeamMatchesSearch(team = {}, search = staffTeamState.search) {
  const needle = String(search || "").trim().toLowerCase();
  if (!needle) return true;
  const memberNames = normalizeUserScopeList(team.memberUserKeys).map(staffTeamMemberName);
  return [team.name, staffTeamMemberName(team.leadUserKey), ...memberNames]
    .map((value) => String(value || "").toLowerCase())
    .some((value) => value.includes(needle));
}

function renderStaffTeams() {
  ensureStaffTeamSession();
  const host = document.getElementById("userMgmtTeamsPane");
  if (!host || userMgmtDirectoryState.tab !== "teams") return;
  if (!isTrustedUserManagerSession()) { host.innerHTML = ""; return; }
  if (staffTeamState.status === "loading" && !staffTeamState.teams.length && !staffTeamState.editing) { host.innerHTML = '<div class="user-mgmt-empty" role="status">Loading teams…</div>'; return; }
  if (staffTeamState.status === "error" && !staffTeamState.teams.length && !staffTeamState.editing && !staffTeamState.selectedTeam) { host.innerHTML = `<div class="user-mgmt-empty" role="alert"><strong>Teams could not load.</strong><span>${escapeHtml(staffTeamState.notice || staffTeamState.error)}</span><button class="ghost-button" data-staff-team-retry type="button">Try again</button></div>`; return; }
  const pendingName = staffTeamState.pendingUserKey ? staffTeamMemberName(staffTeamState.pendingUserKey) : "";
  const selected = staffTeamState.selectedTeam || null;
  const detail = staffTeamState.editing ? renderStaffTeamForm(staffTeamState.draft || blankStaffTeamDraft(selected)) : renderStaffTeamDetail(selected);
  const search = String(staffTeamState.search || "");
  const visibleTeams = staffTeamState.teams;
  const teamPage = managementServerPage("teams",visibleTeams,staffTeamState.pageInfo);
  const resultCount = formatNounCount(staffTeamState.pageInfo?.totalRows || 0,"team");
  host.innerHTML = `<div class="staff-team-toolbar"><div><h4>Teams</h4><p>A team is a saved group of coworkers who handle the same work. Joining a team never adds company access.</p>${pendingName ? `<p class="staff-team-pending">Choose a team for <strong>${escapeHtml(pendingName)}</strong>.</p>` : ""}</div><div class="staff-team-toolbar-actions"><button class="ghost-button" data-staff-team-retry type="button">Refresh teams</button><button class="primary-button" data-staff-team-new type="button">Create team</button></div></div>
    ${staffTeamState.notice ? `<div class="staff-team-notice" role="status">${escapeHtml(staffTeamState.notice)}</div>` : ""}
    ${staffTeamState.status === "error" ? `<div class="staff-team-form-error" role="alert">${escapeHtml(staffTeamState.error)}</div>` : ""}
    <div class="staff-team-guide" aria-label="How to set up a team"><div><b>1</b><span><strong>Create or select a team.</strong><small>Give it a short name people will recognize.</small></span></div><div><b>2</b><span><strong>Choose the lead and members.</strong><small>The lead must also be a member.</small></span></div><div><b>3</b><span><strong>Choose its work.</strong><small>Select the companies and follow-up actions this team handles.</small></span></div></div>
    <div class="staff-team-layout"><section class="staff-team-list staff-team-scroll-region" role="region" aria-label="Saved teams" tabindex="0"><div class="staff-team-list-controls"><div class="staff-team-list-head"><strong>Saved teams</strong><span>${escapeHtml(resultCount)}</span></div><label class="staff-team-search"><span>Find a team</span><span class="staff-team-search-control"><input type="search" data-staff-team-search value="${escapeHtml(search)}" placeholder="Team, lead, or member" autocomplete="off" aria-label="Find a team" aria-describedby="staffTeamSearchStatus"><button class="ghost-button" data-staff-team-search-clear type="button"${search ? "" : " disabled"}>Clear</button></span><small id="staffTeamSearchStatus" aria-live="polite">${search.trim() ? `${escapeHtml(resultCount)} shown.` : "Search by team name, lead, or member."}</small></label>${managementListPagerHtml(teamPage, "Teams")}</div>${visibleTeams.length ? teamPage.rows.map((team) => {
      const members = normalizeUserScopeList(team.memberUserKeys);
      return `<button class="staff-team-card${String(team.teamId) === staffTeamState.selectedId ? " is-active" : ""}" data-staff-team-select="${escapeHtml(team.teamId || "")}" type="button"><strong>${escapeHtml(team.name || "Unnamed team")}</strong><span>${formatNounCount(team.memberCount ?? members.length, "member")} · Lead: ${escapeHtml(staffTeamMemberName(team.leadUserKey) || "Not set")}</span>${pendingName ? '<small>Add to this team</small>' : ""}</button>`;
    }).join("") : staffTeamState.teams.length ? '<div class="user-mgmt-empty staff-team-no-results"><strong>No teams match.</strong><span>Try another team, lead, or member, or clear the search.</span><button class="ghost-button" data-staff-team-search-clear type="button">Clear search</button></div>' : '<div class="user-mgmt-empty"><strong>No teams yet.</strong><span>Create the first team to group billers and their allowed actions.</span></div>'}</section><aside class="staff-team-detail staff-team-scroll-region" aria-label="Team details and setup" tabindex="0">${detail}</aside></div>`;
  const searchInput = host.querySelector("[data-staff-team-search]");
  searchInput?.addEventListener("input", () => {
    syncStaffTeamDraftFromForm(teamForm);
    staffTeamState.search = searchInput.value;
    staffTeamState.page=1;
    clearTimeout(staffTeamState.searchTimer);
    staffTeamState.searchTimer=setTimeout(()=>loadStaffTeams(true),180);
    const selectionStart = searchInput.selectionStart;
    const selectionEnd = searchInput.selectionEnd;
    renderStaffTeams();
    window.requestAnimationFrame(() => {
      const nextInput = host.querySelector("[data-staff-team-search]");
      nextInput?.focus?.({ preventScroll: true });
      nextInput?.setSelectionRange?.(selectionStart, selectionEnd);
    });
  });
  const teamForm = host.querySelector("[data-staff-team-form]");
  bindManagementServerPager(host,"teams",page=>{ syncStaffTeamDraftFromForm(teamForm);staffTeamState.page=page;loadStaffTeams(true); });
  bindStaffTeamChoiceSearch(teamForm);
  bindManagementServerPager(host,"team-people",page=>{syncStaffTeamDraftFromForm(teamForm);loadStaffTeamPeople({page,search:staffTeamState.peopleSearch || ""});});
  host.querySelector("[data-team-person-search]")?.addEventListener("input",event=>{syncStaffTeamDraftFromForm(teamForm);clearTimeout(staffTeamState.peopleTimer);const search=event.target.value;staffTeamState.peopleTimer=setTimeout(()=>loadStaffTeamPeople({page:1,search}),180);});
  if (staffTeamState.saving) host.querySelectorAll("button,input,select").forEach((control) => { control.disabled = true; });
  teamForm?.addEventListener("input", () => syncStaffTeamDraftFromForm(teamForm));
  teamForm?.addEventListener("change", (event) => {
    syncStaffTeamDraftFromForm(teamForm);
    if (event.target?.matches?.('input[name="followUpActions"]')) updateStaffTeamActionSelectionSummary(teamForm);
    updateStaffTeamChoiceCounts(teamForm);
  });
}

function renderUserMgmtActionSummary(summary, actions = []) {
  const names = normalizeUserScopeList(actions);
  if (!names.length) return `<span>${escapeHtml(summary || "No actions allowed")}</span>`;
  return `<details class="user-mgmt-action-summary"><summary><span>${escapeHtml(summary)}</span><span class="user-mgmt-action-summary-toggle"><span class="user-mgmt-action-summary-closed">Show actions</span><span class="user-mgmt-action-summary-open">Hide actions</span></span></summary><small>${escapeHtml(names.join(", "))}</small></details>`;
}

function renderStaffTeamDetail(team) {
  if (!team) return '<div class="user-mgmt-rail-empty">Select a team, or create one.</div>';
  const members = normalizeUserScopeList(team.memberUserKeys);
  const actions = normalizeUserScopeList(team.followUpActions);
  const orgLabels = userMgmtDisplayOrgLabelMap();
  const archiving = staffTeamState.archivingId === String(team.teamId || "");
  return `<div class="user-mgmt-rail-head"><span>Team</span><strong>${escapeHtml(team.name || "Unnamed team")}</strong></div>
    <div class="user-mgmt-rail-section"><b>Lead</b><span>${escapeHtml(staffTeamMemberName(team.leadUserKey) || "Not set")}</span></div>
    <div class="user-mgmt-rail-section"><b>Members</b><span>${members.length ? escapeHtml(members.map(staffTeamMemberName).join(", ")) : "No members yet"}</span></div>
    <div class="user-mgmt-rail-section"><b>Companies</b><span>${normalizeUserScopeList(team.orgIds).length ? escapeHtml(normalizeUserScopeList(team.orgIds).map((orgId) => orgLabels.get(orgId) || portalCompanyLabel(orgId)).join(", ")) : "No team company list"}</span><small>Each person's own company access still applies.</small></div>
    <div class="user-mgmt-rail-section"><b>Follow-up actions</b>${renderUserMgmtActionSummary(actions.length ? formatNounCount(actions.length, "selected action") : "No actions allowed", actions)}</div>
    <div class="staff-team-detail-actions">
      <button class="primary-button" data-staff-team-edit type="button"${archiving ? " disabled" : ""}>Edit team</button>
      <button class="ghost-button staff-team-archive" data-staff-team-archive type="button"${archiving ? " disabled" : ""}>${archiving ? "Archiving…" : "Archive team"}</button>
      <small>Archive removes this team from the active list. Its saved record and audit history are kept.</small>
    </div>`;
}

function renderStaffTeamForm(draft = {}) {
  const internalUsers = (staffTeamState.people || []).filter((user) => !isExternalClientRole(user.role));
  const selectedPeople = normalizeUserScopeList(draft.memberUserKeys).filter(key => !internalUsers.some(user=>staffTeamUserKey(user)===key)).map(key=>({userId:key,name:staffTeamMemberName(key)}));
  const leadChoices = [...internalUsers,...selectedPeople];
  if(draft.leadUserKey && !leadChoices.some(user=>staffTeamUserKey(user)===draft.leadUserKey))leadChoices.push({userId:draft.leadUserKey,name:staffTeamMemberName(draft.leadUserKey)});
  const members = new Set(normalizeUserScopeList(draft.memberUserKeys));
  const orgs = new Set(normalizeUserScopeList(draft.orgIds));
  const actions = new Set(normalizeUserScopeList(draft.followUpActions).map((action) => normalizeFollowUpActionKey(canonicalFollowUpAction(action) || action)));
  const selectableActions = getSelectableActionRules();
  const selectedActionCount = selectableActions.filter((rule) => actions.has(normalizeFollowUpActionKey(rule.action))).length;
  return `<form data-staff-team-form aria-busy="${Boolean(staffTeamState.saving)}"><div class="staff-team-form-error${staffTeamState.formError ? "" : " hidden"}" role="alert" tabindex="-1">${escapeHtml(staffTeamState.formError || "")}</div><div class="user-mgmt-rail-head"><span>${draft.teamId ? "Edit team" : "New team"}</span><strong>${escapeHtml(draft.name || "Team setup")}</strong></div>
    <label>Team name<input name="name" value="${escapeHtml(draft.name || "")}" maxlength="120" required></label>
    <label>Team lead<select name="leadUserKey"><option value="">Choose a lead</option>${leadChoices.map((user) => { const key = staffTeamUserKey(user); return `<option value="${escapeHtml(key)}"${key === draft.leadUserKey ? " selected" : ""}>${escapeHtml(user.name)}</option>`; }).join("")}</select></label>
    <fieldset data-server-members><legend>Members</legend><label>Search all staff<input type="search" data-team-person-search value="${escapeHtml(staffTeamState.peopleSearch || "")}" autocomplete="off"></label><small>${formatCount(members.size)} selected</small>${managementListPagerHtml(managementServerPage("team-people",internalUsers,staffTeamState.peoplePage),"Staff choices")}${internalUsers.map((user) => { const key = staffTeamUserKey(user); return `<label><input type="checkbox" name="memberUserKeys" value="${escapeHtml(key)}"${members.has(key) ? " checked" : ""}> ${escapeHtml(user.name)}</label>`; }).join("")}</fieldset>
    <fieldset><legend>Company list</legend><p>This list helps route work. It does not give anyone new company access.</p>${[...userMgmtDisplayOrgLabelMap()].map(([orgId, label]) => `<label><input type="checkbox" name="orgIds" value="${escapeHtml(orgId)}"${orgs.has(orgId) ? " checked" : ""}${activeClientGroupTenant() ? " disabled" : ""}> ${escapeHtml(label)}</label>`).join("")}</fieldset>
    <details class="staff-team-actions-disclosure"><summary><span><strong>Allowed follow-up actions</strong><small data-staff-team-action-count>${formatCount(selectedActionCount)} of ${formatCount(selectableActions.length)} selected</small></span><span class="staff-team-disclosure-action">Review or change</span></summary><fieldset><legend class="visually-hidden">Allowed follow-up actions</legend>${selectableActions.map((rule) => `<label><input type="checkbox" name="followUpActions" value="${escapeHtml(rule.action)}"${actions.has(normalizeFollowUpActionKey(rule.action)) ? " checked" : ""}> ${escapeHtml(rule.action)}</label>`).join("")}</fieldset></details>
    <div class="user-mgmt-rail-actions"><span class="staff-team-save-note">Review the companies and actions above, then save the team.</span><button class="primary-button" type="submit">${staffTeamState.saving ? "Saving…" : "Save team"}</button><button class="ghost-button" data-staff-team-cancel type="button">Cancel</button></div></form>`;
}

function syncStaffTeamDraftFromForm(form) {
  if(!staffTeamState.editing || !form?.matches?.('[data-staff-team-form]') || staffTeamState.saving)return;
  const data=new FormData(form),draft=staffTeamState.draft || {},visible=new Set([...form.querySelectorAll('input[name="memberUserKeys"]')].map(input=>input.value));
  staffTeamState.draft={...draft,name:String(data.get("name") || ""),leadUserKey:String(data.get("leadUserKey") || ""),
    memberUserKeys:[...new Set([...(draft.memberUserKeys || []).filter(key=>!visible.has(key)),...data.getAll("memberUserKeys").map(String)])],
    orgIds:activeClientGroupTenant() ? [...(draft.orgIds || activeClientGroupOrgIds())] : data.getAll("orgIds").map(String),followUpActions:data.getAll("followUpActions").map(String)};
}

function updateStaffTeamActionSelectionSummary(form) {
  const count = form?.querySelector?.("[data-staff-team-action-count]");
  const choices = Array.from(form?.querySelectorAll?.('input[name="followUpActions"]') || []);
  if (!count || !choices.length) return;
  const selected = choices.filter((choice) => choice.checked).length;
  count.textContent = `${formatCount(selected)} of ${formatCount(choices.length)} selected`;
}

async function saveStaffTeam(form) {
  if (staffTeamState.saving || !isTrustedUserManagerSession()) return;
  const previousKey = staffTeamState.requestKey;
  const requestKey = ensureStaffTeamSession();
  if (previousKey !== requestKey) { renderStaffTeams(); return; }
  const draft = syncStaffTeamDraft(form);
  const base = portalUserDirectoryApiBase();
  const token = workflowApiToken() || trackerApiToken();
  const body = { teamId: String(draft.teamId || ""), expectedVersion: Number(draft.version || 0), name: String(draft.name || "").trim(), leadUserKey: String(draft.leadUserKey || "").trim(), memberUserKeys: [...(draft.memberUserKeys || [])], orgIds: [...(draft.orgIds || [])], followUpActions: [...(draft.followUpActions || [])] };
  if (!body.name) { staffTeamFormError("Enter a team name before saving."); document.querySelector('[data-staff-team-form] [name="name"]')?.focus(); return; }
  if (!body.orgIds.length) { staffTeamFormError("Choose at least one company for this team."); return; }
  if (!base || !token) { staffTeamFormError("Connect to the portal server before saving. Your changes are still here."); return; }
  if (body.leadUserKey && !body.memberUserKeys.includes(body.leadUserKey)) body.memberUserKeys.push(body.leadUserKey);
  Object.assign(staffTeamState, { saving: true, formError: "", notice: "" });
  renderStaffTeams();
  let saved = false;
  try {
    const response = await fetch(`${base}/api/users/teams`, { method: "POST", cache: "no-store", headers: { "content-type": "application/json", authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
    const payload = await response.json().catch(() => ({}));
    if (staffTeamSessionKey() !== requestKey || !isTrustedUserManagerSession()) return;
    if (!response.ok) {
      const safeMessages = {
        staff_team_stale: "This team changed since you opened it. Your edits are still here. Refresh teams, then reopen the team to review the latest version.",
        team_member_company_mismatch: "Every team member must already have access to all selected companies. Adjust the members or company list, then save again.",
        team_member_not_found: "A selected person is no longer available. Refresh teams and people, then review the members before saving.",
        team_lead_role_not_allowed: "Choose an internal TMS staff member as the team lead.",
        team_member_role_not_allowed: "Only internal TMS staff accounts can join a team. Review the selected members.",
        team_company_required: "Choose at least one company for this team.",
        team_name_required: "Enter a team name before saving.",
        team_too_large: "A team can have at most 250 people. Reduce the selected members.",
        team_not_found: "This team is no longer available. Refresh teams before making another change."
      };
      staffTeamState.formError = safeMessages[payload.error] || "The team could not be saved. Check the selections and your connection, then try again. Your changes are still here.";
      return;
    }
    if (!payload.team || typeof payload.team.teamId !== "string" || !payload.team.teamId.trim()) {
      staffTeamState.formError = "We could not confirm the save. Your changes are still here. Refresh teams to check whether it was saved before trying again.";
      return;
    }
    saved = true;
    // A successful POST must never remain a blank create form if a later GET fails.
    Object.assign(staffTeamState, { editing: false, draft: null, selectedId: String(payload.team?.teamId || body.teamId || ""), pendingUserKey: "" });
    staffTeamState.selectedTeam=payload.team;
    const teamsReady = await loadStaffTeams(true);
    if (staffTeamSessionKey() !== requestKey || !isTrustedUserManagerSession()) return;
    const peopleReady = await refreshAuthoritativeUserGovernance({ force: true });
    if (staffTeamSessionKey() !== requestKey || !isTrustedUserManagerSession()) return;
    staffTeamState.notice = teamsReady && peopleReady ? "Team saved. Members and access details are up to date." : "The server saved your team, but the latest details could not refresh. Refresh teams before making another change.";
    if (teamsReady && peopleReady) showToast("Team saved.");
  } catch (_) {
    if (staffTeamSessionKey() !== requestKey || !isTrustedUserManagerSession()) return;
    if (saved) staffTeamState.notice = "The server saved your team, but the latest details could not refresh. Refresh teams before making another change.";
    else staffTeamState.formError = "We could not confirm the save. Your changes are still here. Refresh teams to check whether it was saved before trying again.";
  } finally {
    if (staffTeamSessionKey() === requestKey && isTrustedUserManagerSession()) {
      staffTeamState.saving = false;
      renderStaffTeams();
      if (staffTeamState.formError) document.querySelector(".staff-team-form-error")?.focus();
    }
  }
}

async function archiveStaffTeam(team = null) {
  if (!isTrustedUserManagerSession()) return false;
  const previousKey = staffTeamState.requestKey;
  const requestKey = ensureStaffTeamSession();
  if (previousKey !== requestKey) { renderStaffTeams(); return false; }
  const current = () => requestKey === staffTeamSessionKey() && isTrustedUserManagerSession();
  const teamId = String(team?.teamId || "");
  if (!teamId || staffTeamState.archivingId) return false;
  const label = String(team?.name || "this team");
  if (typeof confirm === "function" && !confirm(`Archive ${label}?\n\nIt will disappear from active Teams. Its saved record and audit history will be kept.`)) return false;
  const base = portalUserDirectoryApiBase();
  const token = workflowApiToken() || trackerApiToken();
  staffTeamState.archivingId = teamId;
  renderStaffTeams();
  try {
    const response = await fetch(`${base}/api/users/teams/archive`, {
      method: "POST",
      cache: "no-store",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify({ teamId, expectedVersion: Number(team.version || 0) })
    });
    const payload = await response.json().catch(() => ({}));
    if (!current()) return false;
    if (!response.ok) {
      showToast(payload.message || "The team could not be archived.");
      return false;
    }
    if (payload.team?.teamId !== teamId || payload.team?.status !== "archived") { showToast("The archive could not be confirmed. Refresh teams before trying again."); return false; }
    Object.assign(staffTeamState, { selectedId: "", editing: false, draft: null, pendingUserKey: "" });
    await loadStaffTeams(true);
    try { await refreshAuthoritativeUserGovernance({ force: true }); } catch (_) { /* team is already archived; directory refresh remains recoverable */ }
    if (!current()) return false;
    showToast("Team archived.");
    return true;
  } finally {
    if (current() && staffTeamState.archivingId === teamId) staffTeamState.archivingId = "";
    if (current()) renderStaffTeams();
  }
}

document.addEventListener("click", (event) => {
  if (event.target.closest("#userMgmtTeamsPane") && staffTeamState.saving) return;
  if (event.target.closest("[data-staff-team-retry]")) { loadStaffTeams(true); refreshAuthoritativeUserGovernance({ force: true }).catch(() => {}); return; }
  if (event.target.closest("[data-staff-team-search-clear]")) {
    syncStaffTeamDraftFromForm(document.querySelector("#userMgmtTeamsPane [data-staff-team-form]"));
    staffTeamState.search = "";
    staffTeamState.page=1;
    loadStaffTeams(true);
    renderStaffTeams();
    window.requestAnimationFrame(() => document.querySelector("[data-staff-team-search]")?.focus?.({ preventScroll: true }));
    return;
  }
  if (event.target.closest("[data-staff-team-new]")) {
    Object.assign(staffTeamState, { editing: true, selectedId: "", selectedTeam:null,draft: blankStaffTeamDraft(), formError: "", notice: "" });
    loadStaffTeamPeople({page:1,search:""});
    renderStaffTeams();
    return;
  }
  const select = event.target.closest("[data-staff-team-select]");
  if (select) {
    const teamId = String(select.dataset.staffTeamSelect || "");
    staffTeamState.formError = ""; staffTeamState.notice = "";
    selectStaffTeam(teamId).catch(()=>showToast("Team details could not load."));
    return;
  }
  if (event.target.closest("[data-staff-team-edit]")) {
    staffTeamState.formError = ""; staffTeamState.notice = "";
    selectStaffTeam(staffTeamState.selectedId,{edit:true}).catch(()=>showToast("Team details could not load."));
    return;
  }
  if (event.target.closest("[data-staff-team-archive]")) {
    const team = staffTeamState.selectedTeam || null;
    archiveStaffTeam(team).catch(() => showToast("The team could not be archived."));
    return;
  }
  if (event.target.closest("[data-staff-team-cancel]")) {
    Object.assign(staffTeamState, { editing: false, draft: null, pendingUserKey: "", formError: "" });
    renderStaffTeams();
  }
});

document.addEventListener("submit", (event) => {
  if (!event.target.matches("[data-staff-team-form]")) return;
  event.preventDefault();
  saveStaffTeam(event.target);
});

// Fixed directory order: most powerful access first, then alphabetical.
// There is no sort control — the list always reads the same way.
const USER_MGMT_ROLE_RANK = { SuperAdmin: 0, StatesideBiller: 1, OffshoreBiller: 2, Client: 3, RedactedClient: 4 };

function userMgmtPersonKey(user = {}) {
  return String(user.email || user.userId || user.username || user.id || "").trim().toLowerCase();
}

function userMgmtSearchQuery() {
  return String(document.getElementById("userMgmtSearch")?.value || "").trim();
}

function userMgmtFilteredRows(query = userMgmtSearchQuery()) {
  const needle = String(query || "").trim().toLowerCase();
  const orgLabels = userMgmtOrgLabelMap();
  return governanceUserRows()
    .map((user, index) => ({ user, index }))
    .filter(({ user }) => !activeClientGroupTenant() || user.allOrgs || normalizeUserScopeList(user.orgIds).some(id => activeClientGroupOrgIds().includes(id)))
    .filter(({ user }) => portalUserDirectoryHostedMode() ? peopleDirectoryState().pageKeys.includes(userMgmtPersonKey(user)) : (!needle || userMgmtSearchText(user, orgLabels).includes(needle)))
    .sort((left, right) =>
      (USER_MGMT_ROLE_RANK[canonicalRole(left.user.role)] ?? 9) - (USER_MGMT_ROLE_RANK[canonicalRole(right.user.role)] ?? 9)
      || compareText(left.user.name, right.user.name)
      || compareText(left.user.email, right.user.email) || left.index - right.index);
}

function userMgmtSearchText(user = {}, orgLabels = userMgmtOrgLabelMap()) {
  const role = canonicalRole(user.role);
  return [
    user.name,
    user.email,
    role,
    roleProfiles[role]?.label || "",
    normalizeUserScopeList(user.clientNames).join(" "),
    // Exact database-key lookup remains available without printing the key in
    // any visible company label or scope summary.
    normalizeUserScopeList(user.orgIds).join(" "),
    userMgmtSeesScope(user, orgLabels).entries.map((entry) => entry.label).join(" ")
  ].join(" ").toLowerCase();
}

// Org ID -> company display name. Server roster names (the checks endpoint
// returns {orgId, name} for all-org principals) win over names derived from
// loaded data; unknown org IDs fall back to the raw ID.
function userMgmtOrgLabelMap() {
  const map = new Map();
  databaseOrgScopeOptions({ includeFeeRows: true }).forEach((option) => {
    map.set(option.orgId, [option.portalOrgName, option.clientName, option.orgName].map((name) => clientActionCompanyName(name, option.orgId)).find(Boolean) || option.orgId);
  });
  ((clientPaymentChecksApiState.response || {}).organizations || []).forEach((org) => {
    const orgId = String((org && org.orgId) || "").trim();
    const name = String((org && org.name) || "").trim();
    if (orgId && clientActionCompanyName(name, orgId)) map.set(orgId, name);
  });
  // D3 S4, LAST on purpose. The roster above is a GROUP BY over the `client`
  // column of claim data - the right answer while nobody has named the group,
  // the wrong one the moment somebody has. A name typed into Manage Groups is
  // the name that person will look for.
  portalOrgDirectoryState.orgs.forEach((org) => {
    if (org.orgId && clientActionCompanyName(org.orgName, org.orgId)) map.set(org.orgId, org.orgName);
  });
  return map;
}

function userMgmtUnavailableCompanyLabels(orgIds = []) {
  const ids = normalizeUserScopeList(orgIds).sort(compareText);
  return new Map(ids.map((orgId, index) => [
    orgId,
    ids.length === 1 ? "Company name unavailable" : `Company name unavailable ${index + 1}`
  ]));
}

// Rendering-only view of the label map. The raw map intentionally retains an
// exact orgId fallback because scope resolution uses it to pair late-arriving
// client names. Visible controls and summaries must never expose that key.
function userMgmtDisplayOrgLabelMap(orgLabels = userMgmtOrgLabelMap()) {
  const missing = [...orgLabels.entries()]
    .filter(([orgId, label]) => !String(label || "").trim() || String(label).trim() === orgId)
    .map(([orgId]) => orgId);
  const replacements = userMgmtUnavailableCompanyLabels(missing);
  return new Map([...orgLabels.entries()].map(([orgId, label]) => [orgId, replacements.get(orgId) || label]));
}

// The resolved "what they see" scope: one entry per database org (labeled by
// client name when known) plus any client names with no org on file yet.
function userMgmtSeesScope(user = {}, orgLabels = userMgmtOrgLabelMap()) {
  const scope = accessProfileForUser(user).scope || {};
  const orgIds = normalizeUserScopeList(scope.orgIds || user.orgIds);
  const clientNames = normalizeUserScopeList(scope.clientNames || user.clientNames);
  const entries = orgIds.map((orgId) => ({ orgId, label: orgLabels.get(orgId) || orgId }));
  const seen = new Set(entries.map((entry) => String(entry.label).toLowerCase()));
  // Client names with no matching org label first fill in orgs the loaded
  // data could not name (label === raw ID), so "one org + one client name"
  // stays one entry; only truly extra names become entries of their own.
  const unlabeled = entries.filter((entry) => entry.label === entry.orgId);
  clientNames.forEach((clientName) => {
    const key = String(clientName).toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const slot = unlabeled.shift();
    if (slot) {
      slot.label = clientName;
      return;
    }
    entries.push({ orgId: "", label: clientName });
  });
  const unresolvedLabels = userMgmtUnavailableCompanyLabels(entries
    .filter((entry) => entry.orgId && entry.label === entry.orgId)
    .map((entry) => entry.orgId));
  entries.forEach((entry) => {
    if (entry.orgId && entry.label === entry.orgId) entry.label = unresolvedLabels.get(entry.orgId) || "Company name unavailable";
  });
  return { orgIds, clientNames, entries };
}

// Plain-English "Sees" column: { strong, small, tone } with tone "" | "warn"
// | "danger". Canonical noun: "companies". Missing directory names use a
// plain placeholder; internal org IDs remain non-rendered scope metadata.
function userMgmtSeesLabel(user = {}, orgLabels = userMgmtOrgLabelMap()) {
  const role = canonicalRole(user.role);
  if (role === "SuperAdmin") return { strong: "Everything — all companies", small: "", tone: "" };
  const { orgIds, entries } = userMgmtSeesScope(user, orgLabels);
  const names = entries.map((entry) => entry.label);
  const shortList = names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${formatCount(names.length - 2)} more` : "");
  if (role === "OffshoreBiller") {
    if (!entries.length) return { strong: "Nothing yet — pick companies", small: "", tone: "warn" };
    if (userMgmtCoversAllCompanies(user, orgLabels)) return { strong: "All companies", small: "", tone: "" };
    return { strong: shortList, small: "", tone: "" };
  }
  if (isExternalClientRole(role)) {
    if (!entries.length) return { strong: "No company yet — assign one", small: "", tone: "warn" };
    if (orgIds.length > 1 || entries.length > 1) return { strong: "More than one company — fix scope", small: "", tone: "danger" };
    return { strong: `${entries[0].label} only`, small: "", tone: "" };
  }
  if (!entries.length) return { strong: "Nothing yet — pick companies", small: "", tone: "warn" };
  if (userMgmtCoversAllCompanies(user, orgLabels)) return { strong: "All companies", small: "", tone: "" };
  return { strong: shortList, small: "", tone: "" };
}

// VOB_USERS_MODULE_SPEC 4.3. This used to return { key: "built-in" } for
// every non-custom account, so the STATUS column rendered PROVENANCE and
// STATE as peers in two different colours. "Built-in" says where an account
// came from; "Active" says whether it works. They are not alternatives, and
// VOB keeps status as the pill and everything else as plain text.
//
// Built-in accounts are short-circuited rather than fed to
// normalizeUserStatus(): they carry no invite row and no `status` field, and
// that helper defaults an unknown status to "pending_invite" - which would
// have made the directory report that Platform Admin had an invite outstanding.
function userMgmtStatusMeta(user = {}) {
  if (!user.custom && !user.directoryAuthoritative) return { key: "active", label: "Active" };
  const status = normalizeUserStatus(user.status);
  return { key: status, label: userStatusLabel(status) };
}

// Provenance, rendered as plain text beside the name (VOB's `-`-joined
// metadata line), never as a coloured badge in the STATUS column.
function userMgmtProvenanceLabel(user = {}) {
  if (user.directoryAuthoritative) {
    const sources = Array.isArray(user.sources) ? user.sources : [];
    if (sources.includes("sql-credential") && sources.includes("static-json")) return "Multiple managed sign-in methods";
    if (sources.includes("sql-credential")) return "Portal account";
    if (sources.includes("static-json")) return "Managed login";
    return "Portal directory";
  }
  return user.custom ? "" : "Built-in";
}

function userMgmtProfileIsPortalManaged(user = {}) {
  if (!user.directoryAuthoritative) return Boolean(user.custom);
  const sources = Array.isArray(user.sources) ? user.sources : [];
  return Boolean(user.custom && user.profileManaged === true && sources.includes("sql-credential") && !sources.includes("static-json")
    && user.profileVersion && !portalUserAuthDisagreement(user));
}

function mapServerInviteStatus(value) {
  const v = String(value == null ? "" : value).toLowerCase();
  if (v === "accepted") return "active";
  if (v === "revoked" || v === "expired") return "disabled";
  return "pending_invite";
}

// THE INVITE ROW IS NOT THE ACCOUNT. Disabling someone changes their
// CREDENTIAL; their invite row stays "accepted" forever, so mapping the invite
// status alone reported a locked-out person as Active — and because the roster
// then believed nothing had changed, no later save would ever send a re-enable.
// That is the original lie inverted, and the direction support wastes days on.
//
// The server already computes the truth: listInvites attaches a per-user
// setupStatus derived from the credential, where `disabled` beats everything
// else (see backend/portalUserSetupStatus.js). Prefer it; fall back to the
// invite status only when it is missing or unknown.
function rosterStatusFromServerRow(inv) {
  const state = String((inv && inv.setupStatus && inv.setupStatus.state) || "").toLowerCase();
  if (state === "disabled") return "disabled";
  if (state === "active") return "active";
  if (state === "invited" || state === "draft" || state === "invite_expired") return "pending_invite";
  return mapServerInviteStatus(inv && inv.status);
}

// THE ACCOUNT STATUS AND THE SETUP STORY ARE TWO DIFFERENT FACTS. The three
// values above are what saves, the last-SuperAdmin guard, the edit rail's
// <select> and assignableOffshoreBillers() all read, so they stay three.
// What used to be thrown away is the DETAIL underneath "pending_invite":
// invited / draft / invite_expired collapsed into one amber "Invite sent"
// pill, so a link that dies tonight, a link that was never sent, and a link
// that is already dead all looked identical - and the expiry the server had
// already computed never reached the screen. Keep the derived detail beside
// the status and let the STATUS column render it.
const USER_SETUP_BADGE_STATES = ["active", "invited", "draft", "invite_expired", "disabled"];
const USER_SETUP_BADGE_LABELS = {
  active: "Active",
  invited: "Invite pending",
  draft: "Invite not sent",
  invite_expired: "Invite expired",
  disabled: "Disabled"
};

// Keeps only what the badge renders. An unrecognized state becomes "unknown"
// (a dash) rather than a guess, matching what the server means by it.
function normalizeUserSetupStatus(setupStatus) {
  const state = String((setupStatus && setupStatus.state) || "").trim().toLowerCase();
  if (!state) return null;
  const detail = { state: USER_SETUP_BADGE_STATES.includes(state) ? state : "unknown" };
  const expiresAtUtc = String((setupStatus && setupStatus.expiresAtUtc) || "").trim();
  if (expiresAtUtc) detail.expiresAtUtc = expiresAtUtc;
  return detail;
}

// Ported from the VOB portal's formatExpiryRelative (src/lib/user-setup-status.ts).
// FUTURE timestamps only, floor-based, and never "in 0 minutes".
// Deliberately NOT the server's daysLeft: that counts partial days UP so an
// invite with six hours left reports 1, and the row would promise "expires in
// 1 day" for a link that dies this afternoon. Garbage in returns "" - the
// badge then renders without a countdown instead of throwing.
function formatUserSetupExpiryRelative(expiresAt, now) {
  const expiresMs = new Date(String(expiresAt == null ? "" : expiresAt)).getTime();
  const nowMs = now instanceof Date ? now.getTime() : new Date(now || Date.now()).getTime();
  if (!Number.isFinite(expiresMs) || !Number.isFinite(nowMs)) return "";
  const deltaMs = expiresMs - nowMs;
  if (deltaMs <= 0) return "in a moment";
  const minutes = Math.floor(deltaMs / 60000);
  const hours = Math.floor(deltaMs / 3600000);
  const days = Math.floor(deltaMs / 86400000);
  if (days >= 1) return `in ${days} day${days === 1 ? "" : "s"}`;
  if (hours >= 1) return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  if (minutes >= 1) return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;
  return "in a moment";
}

// The STATUS column badge. A row that carries the server's derived detail
// renders it; a row that does not (the local roster with no API behind it)
// falls back to exactly the pill it rendered before, so nothing offline
// changes. Only the invited state gets a countdown - invite_expired carries
// no timestamp, here or in VOB, and inventing "expired 2 days ago" would mean
// inventing the data behind it.
function userMgmtSetupBadge(user, now) {
  const detail = normalizeUserSetupStatus(user && user.setupStatus);
  if (!detail) {
    const status = userMgmtStatusMeta(user || {});
    return { key: status.key, label: status.label, note: "", known: true };
  }
  // An unresolvable setup state used to render a dash, and that made ONE ROW disagree with
  // ITSELF: the roster cell showed "--" while the edit rail beside it read Active off the
  // account status. A dash also says the wrong thing — it reads as "this person has no
  // status", when what is actually missing is only the setup DETAIL. The account status is a
  // fact we hold either way, so fall back to exactly the chip a row with no detail renders,
  // and keep known:false so the cell still carries the "setup status unavailable" tooltip.
  // The row now says one thing, and the thing it says is the one the rail agrees with.
  if (detail.state === "unknown") {
    const status = userMgmtStatusMeta(user || {});
    return { key: status.key, label: status.label, note: "", known: false };
  }
  const relative = detail.state === "invited" ? formatUserSetupExpiryRelative(detail.expiresAtUtc, now || new Date()) : "";
  return {
    key: detail.state,
    label: USER_SETUP_BADGE_LABELS[detail.state],
    note: relative ? `expires ${relative}` : "",
    known: true
  };
}

// Pill on top, relative expiry underneath as plain text - the strong+small
// shape the SEES and ACTIVITY columns already use. The countdown is not
// stuffed into the chip: the chip clips with an ellipsis by design.
function userMgmtStatusCellHtml(user, now) {
  const badge = userMgmtSetupBadge(user, now);
  const attrs = badge.known ? "" : ` title="Account setup status unavailable" aria-label="Account setup status unavailable"`;
  const disagreement = portalUserAuthDisagreement(user) ? `<small class="user-mgmt-row-status-note user-mgmt-row-status-note--warning">${escapeHtml(portalUserAuthDisagreementMessage(user))}</small>` : "";
  return `<span class="user-mgmt-row-status-cell"><span class="user-mgmt-row-status user-mgmt-row-status--${escapeHtml(badge.key)}"${attrs}>${escapeHtml(badge.label)}</span>${badge.note ? `<small class="user-mgmt-row-status-note">${escapeHtml(badge.note)}</small>` : ""}${disagreement}</span>`;
}

function mergeServerInvitesIntoRoster(invites) {
  let changed = 0;
  (Array.isArray(invites) ? invites : []).forEach((inv) => {
    const email = String((inv && inv.email) || "").trim().toLowerCase();
    if (!email) return;
    const status = rosterStatusFromServerRow(inv);
    const setupStatus = normalizeUserSetupStatus(inv && inv.setupStatus);
    const scope = inv.scope || {};
    const orgIds = normalizeUserScopeList(scope.orgIds);
    // Org ids are the tenant boundary. Display names are legacy fallback only;
    // carrying both makes the frontend's OR-based matcher wider than the API.
    const clientNames = orgIds.length ? [] : normalizeUserScopeList(scope.clientNames);
    const payerNames = normalizeUserScopeList(scope.payerNames || scope.providerNames);
    const existing = users.find((u) => String(u.email || "").trim().toLowerCase() === email);
    if (existing) {
      existing.status = status;
      existing.role = canonicalRole(inv.role || existing.role);
      if (orgIds.length) existing.orgIds = orgIds;
      if (clientNames.length) existing.clientNames = clientNames;
      if (payerNames.length) existing.payerNames = payerNames;
      existing.inviteId = String(inv.inviteId || existing.inviteId || "");
      existing.invitedAt = String(inv.sentAtUtc || inv.createdAtUtc || existing.invitedAt || "");
      // Cleared rather than kept when a refresh stops carrying it: a stale
      // countdown is worse than no countdown.
      if (setupStatus) existing.setupStatus = setupStatus; else delete existing.setupStatus;
      existing.custom = true;
    } else {
      users.push({
        name: String(inv.displayName || email),
        email: String(inv.email || ""),
        role: canonicalRole(inv.role),
        custom: true,
        orgIds: orgIds,
        clientNames: clientNames,
        payerNames: payerNames,
        status: status,
        inviteId: String(inv.inviteId || ""),
        invitedAt: String(inv.sentAtUtc || inv.createdAtUtc || ""),
        setupStatus: setupStatus,
        directoryAuthoritative: portalUserDirectoryHostedMode()
      });
    }
    changed += 1;
  });
  return changed;
}

function portalUserDirectoryFetchErrorMessage(error) {
  const raw = String(error && error.message || "").trim();
  if (error && error.name === "AbortError") return "The user directory timed out. Try again.";
  if (!raw || /failed to fetch|networkerror|load failed|network request failed/i.test(raw)) {
    return "The portal could not be reached. Check your connection and try again.";
  }
  return raw;
}

async function refreshUserMgmtRosterFromServer() {
  return portalUserDirectoryHostedMode() ? requestPortalUserDirectory({force:true}) : true;
}

async function refreshAuthoritativeUserGovernance({ force = false } = {}) {
  if (!portalUserDirectoryHostedMode()) return true;
  const directoryReady = await requestPortalUserDirectory({ force });
  if (!directoryReady) return false;
  // The paged directory includes scoped invitation metadata; importing the
  // complete invitation roster here would undo bounded People pages.
  return portalUserDirectoryIsCurrent();
}

function renderUserMgmtList() {
  const list = document.getElementById("userMgmtList");
  if (!list) return;
  if (portalUserDirectoryHostedMode() && !portalUserDirectoryIsCurrent()) {
    renderUserMgmtCount();
    list.innerHTML = `<div class="user-mgmt-empty"><strong>${escapeHtml(portalUserDirectoryUnavailableMessage())}</strong><span>No browser-saved or demo users are shown as live accounts.</span>${portalUserDirectoryState.status === "error" ? `<button class="ghost-button" data-user-directory-retry type="button">Try again</button>` : ""}</div>`;
    list.querySelector("[data-user-directory-retry]")?.addEventListener("click", () => refreshAuthoritativeUserGovernance({ force: true }));
    renderUserMgmtRail();
    return;
  }
  refreshUserActivityAdmin();
  const query = userMgmtSearchQuery();
  const orgLabels = userMgmtOrgLabelMap();
  const rows = userMgmtFilteredRows(query);
  const userPage = portalUserDirectoryHostedMode() ? managementServerPage("people", rows, peopleDirectoryState().pageInfo) : managementListPageWindow("people", rows, query);
  if (!governanceUserRows()[userMgmtDirectoryState.selectedIndex] && !userMgmtDirectoryState.selectedPersonKey) {
    userMgmtDirectoryState.selectedIndex = rows[0]?.index ?? 0;
    userMgmtDirectoryState.editing = false;
    userMgmtDirectoryState.editDraft = null;
  }
  renderUserMgmtCount();
  const groupLabels = { SuperAdmin: "Super admins", StatesideBiller: "Stateside billers", OffshoreBiller: "Offshore billers", Client: "Clients", RedactedClient: "Redacted clients" };
  const groupCounts = portalUserDirectoryHostedMode() ? peopleDirectoryState().roleCounts || {} : userMgmtRoleCounts(rows.map((row) => row.user));
  let lastGroupRole = null;
  list.innerHTML = rows.length ? `${managementListPagerHtml(userPage, "People")}
    <div class="user-mgmt-row user-mgmt-row--header" aria-hidden="true">
      <span>User</span><span>Sees</span><span>Status</span><span>Activity</span><span></span>
    </div>
    ${userPage.rows.map(({ user: u, index: idx }) => {
      const sees = userMgmtSeesLabel(u, orgLabels);
      const activity = userActivitySummaryForUser(u);
      // VOB's metadata line: `-`-joined plain text under the name. Provenance
      // lives here now instead of as a blue badge in the STATUS column.
      const provenance = userMgmtProvenanceLabel(u);
      const personMeta = [
        u.email ? escapeHtml(u.email) : "",
        provenance ? `<span class="user-mgmt-row-provenance">${escapeHtml(provenance)}</span>` : ""
      ].filter(Boolean).join(" · ");
      const role = canonicalRole(u.role);
      const groupHead = role === lastGroupRole
        ? ""
        : `<div class="user-mgmt-group-head">${escapeHtml(groupLabels[role] || role)} · ${formatCount(groupCounts[role] || 0)}</div>`;
      lastGroupRole = role;
      return `${groupHead}
    <div class="user-mgmt-row ${idx === userMgmtDirectoryState.selectedIndex ? "user-mgmt-row--selected" : ""}" data-user-role="${escapeHtml(role)}" data-user-row="${idx}">
      <div class="user-mgmt-person">
        <span class="user-mgmt-avatar" data-role="${escapeHtml(role)}" aria-hidden="true">${escapeHtml(userMgmtInitials(u.name))}</span>
        <strong>${escapeHtml(u.name)}</strong>
        ${personMeta ? `<span>${personMeta}</span>` : ""}
      </div>
      <span class="user-mgmt-row-sees${sees.tone ? ` user-mgmt-row-sees--${sees.tone}` : ""}">
        <strong>${escapeHtml(sees.strong)}</strong>
        ${sees.small ? `<small>${escapeHtml(sees.small)}</small>` : ""}
      </span>
      ${userMgmtStatusCellHtml(u)}
      <span class="user-mgmt-row-activity"><strong>${escapeHtml(userActivityLastLoginLabel(activity))}</strong><small>${escapeHtml(userActivityLastSeenLabel(activity))}</small></span>
      <div class="user-mgmt-row-actions">
        ${userMgmtProfileIsPortalManaged(u) ? `<button class="ghost-button" data-user-edit="${idx}" type="button" aria-label="Edit ${escapeHtml(u.name)}">Edit</button>` : ""}
      </div>
    </div>
  `;
    }).join("")}
  ` : `${managementListPagerHtml(userPage, "People")}<div class="user-mgmt-empty"><strong>No one matches "${escapeHtml(query)}".</strong><button class="ghost-button" data-user-mgmt-clear-search type="button">Clear search</button></div>`;
  if (portalUserDirectoryHostedMode()) bindManagementServerPager(list,"people",page=>requestPortalUserDirectory({page,force:true}));
  else bindManagementListPager(list, "people", renderUserMgmtList);
  list.querySelectorAll("[data-user-row]").forEach((row) => {
    row.addEventListener("click", (event) => {
      if (event.target?.closest("button")) return;
      const idx = Number(row.dataset.userRow);
      if (!Number.isInteger(idx)) return;
      if (idx !== userMgmtDirectoryState.selectedIndex) {
        if (userMgmtDirectoryState.editing && !confirm("Discard the unsaved access changes?")) return;
        userMgmtDirectoryState.selectedIndex = idx;
        userMgmtDirectoryState.selectedPersonKey = userMgmtPersonKey(users[idx]);
        if (portalUserDirectoryHostedMode()) peopleDirectoryState().selected = users[idx];
        userMgmtDirectoryState.editing = false;
        userMgmtDirectoryState.editDraft = null;
      }
      renderUserMgmtList();
    });
  });
  list.querySelector("[data-user-mgmt-clear-search]")?.addEventListener("click", () => {
    const search = document.getElementById("userMgmtSearch");
    if (search) search.value = "";
    if (portalUserDirectoryHostedMode()) requestPortalUserDirectory({search:"",page:1,force:true}); else renderUserMgmtList();
  });
  list.querySelectorAll("[data-user-edit]").forEach((btn) => {
    btn.addEventListener("click", () => beginUserMgmtEdit(Number(btn.dataset.userEdit)));
  });
  renderUserMgmtRail();
}

// "{N} people - {N} invites pending" — zero segments drop out.
function renderUserMgmtCount() {
  const host = document.getElementById("userMgmtCount");
  if (!host) return;
  if (portalUserDirectoryHostedMode() && !portalUserDirectoryIsCurrent()) {
    host.textContent = portalUserDirectoryState.status === "pending" ? "Loading directory" : "Directory unavailable";
    return;
  }
  const directoryUsers = governanceUserRows();
  if (portalUserDirectoryHostedMode() && peopleDirectoryState().pageInfo) { host.textContent = formatNounCount(peopleDirectoryState().pageInfo.totalRows,"person","people"); return; }
  const pending = directoryUsers.filter((u) => u.custom && normalizeUserStatus(u.status) === "pending_invite").length;
  const parts = [
    directoryUsers.length ? `${formatCount(directoryUsers.length)} ${directoryUsers.length === 1 ? "person" : "people"}` : "",
    pending ? `${formatCount(pending)} invite${pending === 1 ? "" : "s"} pending` : ""
  ].filter(Boolean);
  host.textContent = parts.join(" · ");
}

async function removeUserMgmtUser(index) {
  if (!isTrustedUserManagerSession()) return;
  if (!Number.isInteger(index) || !users[index] || !users[index].custom) return;
  // Last-admin guard (mirrors saveUserMgmtEdit): never allow a removal that
  // leaves zero active Super Admins. Unreachable while the built-in
  // Platform Admin exists (it is not removable), but the invariant stays
  // explicit for future custom-admin rosters.
  if (!portalUserDirectoryHostedMode() && canonicalRole(users[index].role) === "SuperAdmin" && normalizeUserStatus(users[index].status) !== "disabled"
    && !users.some((u, i) => i !== index && canonicalRole(u.role) === "SuperAdmin" && normalizeUserStatus(u.status) !== "disabled")) {
    showToast("Not removed. At least one active Super Admin must remain.");
    return;
  }
  const name = users[index].name;
  // Offboard safety: never orphan a departing biller's open claims. If any
  // are still routed to them, return those claims to the unassigned pool
  // (visible to stateside billers for re-routing) before removing the user.
  const openClaims = claimsAssignedTo(name);
  if (openClaims.length) {
    const proceed = typeof confirm !== "function" || confirm(
      `${name} still has ${formatNounCount(openClaims.length, "open claim")} routed to them.\n\n`
      + `Removing ${name} will return those claims to Unassigned so they can be re-routed. Continue?`
    );
    if (!proceed) return;
  } else if (typeof confirm === "function" && !confirm(`Remove ${name}? They lose portal access immediately. This can't be undone.`)) {
    return;
  }
  // "They lose portal access immediately" has to be TRUE. Remove used to be a
  // splice plus a localStorage write and nothing else, so the more common
  // offboarding action was the less honest one: the person kept their session,
  // their trusted device and their API access while vanishing from the admin's
  // screen. Removing someone is at least as final as disabling them, so it goes
  // through the same revocation and refuses on the same terms.
  const removalTarget = users[index];
  const hostedAuthority = portalUserDirectoryHostedMode();
  // The SAME transport the Disabled control uses — there is one revocation path,
  // not one per button. `reason` is required by the server and is the durable
  // audit record.
  const actor = state?.authUserScope?.displayName || state?.authUserScope?.username || state?.activeUserName || "an administrator";
  const revoke = await sendUserAccessChangeRequest(removalTarget, "disable", `Removed from Manage Users by ${actor}`);
  // `no_email` and `no_server_account` are ANSWERS, not failures: the row has
  // no invite-created login to revoke, and removing it from the roster is still
  // correct. Everything else means the server did not change anything, so
  // neither does the roster.
  const nothingToRevoke = revoke.code === "no_server_account" || revoke.code === "no_email";
  const couldNotReach = revoke.code === "offline" || revoke.code === "unreachable";
  if (!revoke.ok && (hostedAuthority || (!nothingToRevoke && !couldNotReach))) {
    showToast(`Not removed. ${userAccessChangeFailureMessage(revoke, "disable", removalTarget)}`);
    return;
  }
  if (openClaims.length) reassignClaims(openClaims, "");
  if (hostedAuthority) {
    await refreshAuthoritativeUserGovernance({ force: true });
    showToast(revoke.ok
      ? `${name}'s server access was changed and the authoritative roster was refreshed.`
      : `Not removed. The authoritative roster still controls this account.`);
    return;
  }
  users.splice(index, 1);
  if (userMgmtDirectoryState.selectedIndex === index) {
    userMgmtDirectoryState.selectedIndex = 0;
    userMgmtDirectoryState.editing = false;
    userMgmtDirectoryState.editDraft = null;
  } else if (userMgmtDirectoryState.selectedIndex > index) {
    userMgmtDirectoryState.selectedIndex -= 1;
  }
  persistCustomUsers();
  populateUserSelect();
  renderUserMgmtList();
  if (!revoke.ok) {
    showToast(`${name} removed from the roster${openClaims.length ? `; ${formatNounCount(openClaims.length, "claim")} returned to Unassigned` : ""}. No portal sign-in was revoked${couldNotReach ? " (the portal is temporarily unavailable)" : " — a separately managed login may require administrator action"}.`);
    return;
  }
  showToast(openClaims.length
    ? `${name} removed and signed out; ${formatNounCount(openClaims.length, "claim")} returned to Unassigned.`
    : `${name} removed.`);
}

// Right rail: the selected person's access profile. Read mode by default;
// custom users can swap the rail to a small edit form.
function renderUserMgmtRail() {
  const panel = document.getElementById("userMgmtSelectionPanel");
  if (!panel) return;
  const user = governanceUserRows()[userMgmtDirectoryState.selectedIndex];
  if (!user) {
    userMgmtDirectoryState.editing = false;
    userMgmtDirectoryState.editDraft = null;
    panel.innerHTML = `<div class="user-mgmt-rail-empty">Select a person to review their access.</div>`;
    return;
  }
  if (userMgmtDirectoryState.editing && user.custom) {
    if(panel.dataset.editingPerson===userMgmtPersonKey(user) && panel.querySelector(".user-mgmt-edit"))return;
    panel.dataset.editingPerson=userMgmtPersonKey(user);
    panel.innerHTML = renderUserMgmtEditForm(user);
    wireUserMgmtEditForm(user);
    return;
  }
  userMgmtDirectoryState.editing = false;
  panel.dataset.editingPerson="";
  panel.innerHTML = renderUserMgmtSelectionPanel(user);
  if(!isExternalClientRole(user.role))loadSelectedPersonTeams(user);
  refreshUserActivityAdmin();
  panel.querySelector("[data-user-oversight]")?.addEventListener("click", () => openOversightPolicyEditor(user));
  panel.querySelector("[data-user-access-history]")?.addEventListener("click", () => openOversightAccessHistory(user));
  panel.querySelectorAll("[data-user-rail-edit]").forEach((button) => button.addEventListener("click", () => beginUserMgmtEdit(userMgmtDirectoryState.selectedIndex)));
  panel.querySelector("[data-user-rail-remove]")?.addEventListener("click", () => removeUserMgmtUser(userMgmtDirectoryState.selectedIndex));
  panel.querySelector("[data-user-rail-resend]")?.addEventListener("click", () => resendUserMgmtInvite(userMgmtDirectoryState.selectedIndex));
  panel.querySelector("[data-user-rail-revoke-invite]")?.addEventListener("click", () => revokeUserMgmtInvitation(userMgmtDirectoryState.selectedIndex));
  panel.querySelector("[data-user-rail-invite-link]")?.addEventListener("click", () => generateUserMgmtInviteLink(userMgmtDirectoryState.selectedIndex));
  panel.querySelector("[data-user-rail-invite-link-copy]")?.addEventListener("click", () => copyUserMgmtInviteLink(panel));
  panel.querySelector("[data-user-add-team]")?.addEventListener("click", () => {
    staffTeamState.pendingUserKey = staffTeamUserKey(user);
    staffTeamState.search = "";
    userMgmtDirectoryState.tab = "teams";
    renderUserMgmtTabs();
  });
  wireUserActivityPanel(panel);
}

// True when a user's explicit org scope covers every company the portal
// currently knows about (loaded data + server roster).
function userMgmtCoversAllCompanies(user = {}, orgLabels = userMgmtOrgLabelMap()) {
  const orgSet = new Set(normalizeUserScopeList(user.orgIds));
  const knownOrgIds = Array.from(orgLabels.keys());
  return knownOrgIds.length > 0 && knownOrgIds.every((orgId) => orgSet.has(orgId));
}

function userActivitySummaryForUser(user = {}) {
  const keys = userActivityKeysForUser(user);
  return (userActivityAdminState.summaries || []).find((summary) => keys.has(String(summary.userId || "").toLowerCase()) || keys.has(String(summary.email || "").toLowerCase()) || keys.has(String(summary.displayName || "").toLowerCase())) || null;
}

function userActivityEventsForUser(user = {}) {
  if(userActivityAdminState.selectedKey!==staffTeamUserKey(user))return [];
  return userActivityAdminState.events || [];
}

function userActivityKeysForUser(user = {}) {
  return new Set([user.name, user.email, user.username, user.userId, user.displayName].map((value) => String(value || "").trim().toLowerCase()).filter(Boolean));
}

function userActivityLastLoginLabel(summary = null) {
  if (userActivityAdminState.status === "loading") return "Loading";
  if (!summary || !summary.lastLoginAtUtc) return "No login yet";
  return formatDateTime(summary.lastLoginAtUtc);
}

function userActivityLastSeenLabel(summary = null) {
  if (!summary || !summary.lastSeenAtUtc) return userActivityAdminState.status === "error" ? "Activity unavailable" : "No events";
  return `Last seen ${formatDateTime(summary.lastSeenAtUtc)}`;
}

function userActivityHealthLabel(summary = null) {
  if (userActivityAdminState.status === "error") return "Telemetry offline";
  if (!summary) return "Waiting for first event";
  if (summary.errorEventCount || summary.deniedEventCount) return `${formatCount((summary.errorEventCount || 0) + (summary.deniedEventCount || 0))} denied/error`;
  // A snapshot-served summary counts the person's whole history, not the
  // chosen window; say so rather than let the number pass for a 30-day figure.
  return `${formatCount(summary.totalEventCount || 0)} event${Number(summary.totalEventCount || 0) === 1 ? "" : "s"}${summary.countsBasis === "all-time" ? " (all time)" : ""}`;
}

function ensureUserActivityAdminScope() {
  const scopeKey = workflowAccessScopedRequestKey({ kind: "user-activity-options", base: workflowApiBase() });
  if (userActivityAdminState.featureOptionsScopeKey !== scopeKey) {
    userActivityAdminState = { status: "idle", summaries: [], events: [], loadedAt: "", error: "", filters: { days: "30", feature: "All", deniedOnly: false }, featureOptionsScopeKey: scopeKey, availableFeatures: [] };
  }
  return scopeKey;
}

function userActivityFeatureOptions() {
  return ['All',...new Set([...(userActivityAdminState.featureOptions || []),userActivityAdminState.filters?.feature].filter(value=>value && value!=='All'))].sort((a,b)=>a==='All'?-1:b==='All'?1:String(a).localeCompare(String(b)));
}

function renderUserActivityPanel(user = {}) {
  const summary = userActivitySummaryForUser(user);
  const events = userActivityEventsForUser(user);
  const filters = userActivityAdminState.filters || {};
  return `
    <div class="user-mgmt-rail-section user-activity-panel">
      <b>Activity</b>
      <!-- VOB_USERS_MODULE_SPEC 4.2. These three slots hold PHRASES ("No login
           yet", "Waiting for first event"); VOB's Stat and AuditBucket hold
           COUNTS. A sentence in a numeric tile is the design error that the
           3.6x truncation was only a symptom of. This is VOB's metadata
           pattern - a label/value list. The class avoids "-metrics" on
           purpose: that substring is a global (0,3,1) selector, and matching
           it is what forced these labels to 24px uppercase in an 80px box. -->
      <dl class="user-activity-facts">
        <dt>Last login</dt><dd>${escapeHtml(userActivityLastLoginLabel(summary))}</dd>
        <dt>Last feature</dt><dd>${escapeHtml(summary?.lastFeatureKey || "None")}</dd>
        <dt>Activity health</dt><dd>${escapeHtml(userActivityHealthLabel(summary))}</dd>
      </dl>
      <div class="user-activity-controls">
        <label>Range
          <select data-user-activity-days>
            ${["7", "30", "90"].map((days) => `<option value="${days}" ${String(filters.days || "30") === days ? "selected" : ""}>${days} days</option>`).join("")}
          </select>
        </label>
        <label>Feature
          <select data-user-activity-feature>
            ${userActivityFeatureOptions().map((feature) => `<option value="${escapeHtml(feature)}" ${String(filters.feature || "All") === feature ? "selected" : ""}>${escapeHtml(feature)}</option>`).join("")}
          </select>
        </label>
        <label class="user-activity-check"><input type="checkbox" data-user-activity-denied ${filters.deniedOnly ? "checked" : ""}> Denied/errors</label>
      </div>
      ${managementListPagerHtml(managementServerPage("person-activity",events,userActivityAdminState.eventPage || {}),"Activity events")}
      <div class="user-activity-timeline">
        ${events.length ? events.map((event) => `
          <div class="user-activity-event">
            <strong>${escapeHtml(event.actionKey || event.eventType || "activity")}</strong>
            <span>${escapeHtml(event.featureKey || "portal")} ${event.route ? `· ${escapeHtml(event.route)}` : ""}</span>
            <small>${escapeHtml(formatDateTime(event.createdAtUtc) || "Unknown time")} ${event.statusCode ? `· ${escapeHtml(String(event.statusCode))}` : ""} ${event.decision ? `· ${escapeHtml(event.decision)}` : ""}</small>
          </div>
        `).join("") : `<div class="user-activity-empty">${escapeHtml(userActivityAdminState.status === "loading" ? "Loading activity..." : userActivityAdminState.historyError || "No activity events match this user and filter.")}</div>`}
      </div>
      <div class="user-activity-actions">
        <!-- THE LOG DOES NOT LIVE HERE. This rail is 350px of SETTINGS (can
             see / cannot see / can open) - short, static, glanceable. An audit
             trail needs width, search, sort and who/what/when/which-claim on
             one line, and cramming one into this column is why it could not be
             read. The panel keeps the glanceable summary; the record opens
             full-width on Activity & audit, pre-filtered to this person. -->
        <button class="ghost-button" data-user-activity-open-trail="${escapeHtml(user.userId || user.id || "")}" type="button">View full activity</button>
        <button class="ghost-button" data-user-activity-refresh type="button">Refresh activity</button>
      </div>
    </div>
  `;
}

function wireUserActivityPanel(panel) {
  bindManagementServerPager(panel,"person-activity",page=>refreshUserActivityAdmin({page,force:true}));
  panel.querySelector("[data-user-activity-refresh]")?.addEventListener("click", () => refreshUserActivityAdmin({ force: true }));
  panel.querySelector("[data-user-activity-open-trail]")?.addEventListener("click", (event) => {
    const userId = event.currentTarget?.dataset?.userActivityOpenTrail || "";
    recordUserActivity({ featureKey: "users", actionKey: "activity_open_full_trail", route: "manage-users", metadata: { hasUser: userId ? "yes" : "no" } });
    openSupervisorTrailForUser(userId);
  });
  panel.querySelector("[data-user-activity-days]")?.addEventListener("change", (event) => {
    userActivityAdminState.filters = { ...(userActivityAdminState.filters || {}), days: event.target.value || "30" };
    recordUserActivity({ featureKey: "users", actionKey: "activity_filter_changed", route: "manage-users", metadata: { filter: "days" } });
    refreshUserActivityAdmin({ force: true, page:1 });
  });
  panel.querySelector("[data-user-activity-feature]")?.addEventListener("change", (event) => {
    userActivityAdminState.filters = { ...(userActivityAdminState.filters || {}), feature: event.target.value || "All" };
    recordUserActivity({ featureKey: "users", actionKey: "activity_filter_changed", route: "manage-users", metadata: { filter: "feature" } });
    refreshUserActivityAdmin({ force: true, page:1 });
  });
  panel.querySelector("[data-user-activity-denied]")?.addEventListener("change", (event) => {
    userActivityAdminState.filters = { ...(userActivityAdminState.filters || {}), deniedOnly: Boolean(event.target.checked) };
    recordUserActivity({ featureKey: "users", actionKey: "activity_filter_changed", route: "manage-users", metadata: { filter: "deniedOnly" } });
    refreshUserActivityAdmin({ force: true, page:1 });
  });
}

async function refreshUserActivityAdmin({force=false,page}={}) {
  const scope=portalUserDirectoryAccessScopedRequestKey({kind:'people-activity'});
  if(!isTrustedUserManagerSession() || !hasPermission(PERMISSIONS.SECURITY_AUDIT)){
    document.querySelectorAll('.user-activity-panel').forEach(panel=>panel.textContent='Activity unavailable for this session.');
    userActivityAdminState.controller?.abort();Object.assign(userActivityAdminState,{scope,status:'forbidden',summaries:[],events:[],featureOptions:[],eventPage:null,requestKey:''});return;
  }
  if(userActivityAdminState.scope!==scope){document.querySelectorAll('.user-activity-panel').forEach(panel=>panel.textContent='Loading authorized activity…');userActivityAdminState.controller?.abort();Object.assign(userActivityAdminState,{scope,summaries:[],events:[],featureOptions:[],eventPage:null,requestKey:'',status:'idle',filters:{days:'30',feature:'All',deniedOnly:false}});}
  const base=workflowApiBase(),token=workflowApiToken();if(!base || !token || typeof fetch!=="function"){userActivityAdminState.status="offline";return;}
  const rows=userMgmtFilteredRows(),visible=rows.map(row=>staffTeamUserKey(row.user)).filter(Boolean),selected=governanceUserRows()[userMgmtDirectoryState.selectedIndex],selectedKey=selected?staffTeamUserKey(selected):'';
  const keys=[...new Set([...visible,selectedKey].filter(Boolean))];if(!keys.length)return;
  const filters={...(userActivityAdminState.filters || {})},eventPage=page || (userActivityAdminState.selectedKey===selectedKey?userActivityAdminState.requestedPage || 1:1);
  const requestKey=JSON.stringify({scope,keys,selectedKey,filters,eventPage});
  // A failed read is retried on the next open after a short cool-down instead
  // of pinning "Activity unavailable" for the rest of the session.
  const recentFailure=userActivityAdminState.status==='error' && Date.now()-Number(userActivityAdminState.failedAt||0)<30000;
  if(!force && userActivityAdminState.requestKey===requestKey && (['loading','ok'].includes(userActivityAdminState.status) || recentFailure))return;
  userActivityAdminState.controller?.abort();const controller=new AbortController();
  Object.assign(userActivityAdminState,{controller,requestKey,status:'loading',selectedKey,requestedPage:eventPage,events:[],eventPage:null});
  const current=()=>userActivityAdminState.controller===controller && portalUserDirectoryAccessScopedRequestKey({kind:'people-activity'})===scope && isTrustedUserManagerSession() && hasPermission(PERMISSIONS.SECURITY_AUDIT);
  const params=new URLSearchParams();if(filters.deniedOnly)params.set('deniedOrErrors','true');if(filters.feature && filters.feature!=='All')params.set('featureKey',filters.feature);
  const days=Number(filters.days || 30);if(Number.isFinite(days)&&days>0)params.set('fromDate',new Date(Date.now()-days*86400000).toISOString());
  const read=async(route,query)=>{const response=await fetch(base+route+'?'+query,{cache:'no-store',signal:controller.signal,headers:{authorization:'Bearer '+token}});const body=await response.json().catch(()=>({}));if(!response.ok || body.warning)throw Error('Activity unavailable');return body;};
  try{
    const summaryReads=[];for(let offset=0;offset<keys.length;offset+=50){const query=new URLSearchParams(params);query.set('userKeys',JSON.stringify(keys.slice(offset,offset+50)));summaryReads.push(read('/api/admin/users/activity/summary',query));}
    const eventParams=new URLSearchParams(params);eventParams.set('userId',selectedKey);eventParams.set('page',String(eventPage));eventParams.set('pageSize','50');
    // One person's history must not blank everyone's summary: it settles on its own.
    const [summaries,historyRead]=await Promise.all([Promise.all(summaryReads),selectedKey?read('/api/admin/users/activity',eventParams).then(value=>({value}),()=>({failed:true})):Promise.resolve({value:{events:[],featureOptions:[]}})]);
    if(!current())return;
    if(summaries.some(body=>body.complete!==true || !Array.isArray(body.summaries)))throw Error('Complete activity unavailable');
    const history=!historyRead.failed && (!selectedKey || historyRead.value.page?.complete) ? historyRead.value : null;
    Object.assign(userActivityAdminState,{status:'ok',summaries:summaries.flatMap(body=>body.summaries),events:history?.events || [],featureOptions:history?.featureOptions || [],eventPage:history?.page || null,historyError:history?'':'Activity history for this person is unavailable right now. Reopen to retry.',loadedAt:new Date().toISOString(),error:''});
    renderUserMgmtList();
  }catch(error){if(!current())return;Object.assign(userActivityAdminState,{status:'error',failedAt:Date.now(),summaries:[],events:[],error:'Activity unavailable'});renderUserMgmtList();}
}

function recordUserActivity(event = {}) {
  const base = workflowApiBase();
  const token = workflowApiToken();
  if (!base || !token || typeof fetch !== "function") return;
  const payload = {
    eventType: event.eventType || "ui.activity",
    featureKey: event.featureKey || "ui",
    actionKey: event.actionKey || "activity",
    route: event.route || state.view || "",
    targetType: event.targetType || "",
    targetId: event.targetId || "",
    metadata: event.metadata || {}
  };
  if (Number.isFinite(event.durationMs)) payload.durationMs = Math.max(0, Math.trunc(event.durationMs));
  const requestInit = {
    method: "POST",
    cache: "no-store",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify(payload)
  };
  // Unload / tab-hidden path: keepalive keeps the POST alive through teardown
  // while preserving the Bearer header (navigator.sendBeacon cannot send auth).
  if (event.keepalive) requestInit.keepalive = true;
  fetch(`${base}/api/activity/events`, requestInit).catch(() => {});
}

// --- PHASE-1 productivity telemetry (see DATA_COLLECTION_PLAN.md) ---
// Reuses recordUserActivity -> /api/activity/events (append-only, tenant-scoped,
// PHI-redacted). NEVER route note text / patient PHI here: ids, queue keys, action
// names and counts only. All emits are best-effort and never throw.
function beginClaimWorkTelemetry(claimId, options = {}) {
  try {
    const id = String(claimId || "").trim();
    if (!id) return;
    // Re-render of an already-open drawer (addNote -> re-open, etc.) keeps the
    // original openedAt so the session duration is not reset. Only a genuine open stamps.
    if (claimWorkTelemetry.claimId === id && claimWorkTelemetry.openedAt) return;
    // Drawer switched straight to another claim without an explicit close: flush the
    // prior claim first so its duration stays self-contained.
    if (claimWorkTelemetry.openedAt && claimWorkTelemetry.claimId && claimWorkTelemetry.claimId !== id) {
      endClaimWorkTelemetry("switch");
    }
    const serviceId = String(options.serviceId || "").trim();
    const queueContext = options.queueContext || {};
    const queueKey = String(queueContext.queueKey || "").slice(0, 80);
    const source = String(queueContext.source || options.source || "").slice(0, 80);
    claimWorkTelemetry = { claimId: id, serviceId, queueKey, source, openedAt: Date.now() };
    recordUserActivity({
      eventType: "claim.open",
      featureKey: "claim-work",
      actionKey: "claim_open",
      targetType: "claim",
      targetId: id,
      metadata: {
        ...(serviceId ? { serviceId } : {}),
        ...(queueKey ? { queueKey } : {}),
        ...(source ? { source } : {})
      }
    });
  } catch (_) {}
}

function endClaimWorkTelemetry(reason = "close", options = {}) {
  try {
    if (!claimWorkTelemetry.openedAt) return;
    const durationMs = Math.max(0, Date.now() - claimWorkTelemetry.openedAt);
    const id = claimWorkTelemetry.claimId;
    const serviceId = claimWorkTelemetry.serviceId;
    const queueKey = claimWorkTelemetry.queueKey;
    // Self-contained: send the computed duration so a lost open cannot corrupt it.
    claimWorkTelemetry.openedAt = 0;
    recordUserActivity({
      eventType: "claim.close",
      featureKey: "claim-work",
      actionKey: "claim_close",
      targetType: "claim",
      targetId: id,
      durationMs,
      keepalive: !!options.keepalive,
      metadata: {
        durationMs,
        reason: String(reason || "close").slice(0, 24),
        ...(serviceId ? { serviceId } : {}),
        ...(queueKey ? { queueKey } : {})
      }
    });
  } catch (_) {}
}

function recordClaimWorkAction(claimId, actionKey, options = {}) {
  try {
    const id = String(claimId || "").trim();
    if (!id) return;
    const serviceId = String(options.serviceId || "").trim();
    recordUserActivity({
      eventType: "claim.action",
      featureKey: "claim-work",
      actionKey: String(actionKey || "claim_action").slice(0, 60),
      targetType: "claim",
      targetId: id,
      metadata: {
        ...(serviceId ? { serviceId } : {}),
        ...(options.actionType ? { actionType: String(options.actionType).slice(0, 60) } : {})
      }
    });
  } catch (_) {}
}

// Flush the open claim session on tab-hidden / unload so time-on-claim is not lost
// when the user closes the tab or switches away without closing the drawer.
if (typeof document !== "undefined" && document.addEventListener) {
  document.addEventListener("visibilitychange", () => {
    try {
      if (document.visibilityState === "hidden") {
        endClaimWorkTelemetry("hidden", { keepalive: true });
      } else if (
        document.visibilityState === "visible"
        && claimWorkTelemetry.claimId && !claimWorkTelemetry.openedAt
        && els.claimDrawer && els.claimDrawer.classList && els.claimDrawer.classList.contains("open")
      ) {
        // Drawer still open after returning to the tab: start a fresh session.
        beginClaimWorkTelemetry(claimWorkTelemetry.claimId, {
          serviceId: claimWorkTelemetry.serviceId,
          queueContext: { queueKey: claimWorkTelemetry.queueKey, source: claimWorkTelemetry.source }
        });
      }
    } catch (_) {}
  });
}
if (typeof window !== "undefined" && window.addEventListener) {
  window.addEventListener("beforeunload", () => {
    try { endClaimWorkTelemetry("unload", { keepalive: true }); } catch (_) {}
  });
}

// Rail copy: what the selected person CAN and CAN'T see — plain sentences
// plus a check-marked, name-only company list. Returns { can, cant } inner
// HTML; an empty cant omits the section (warn/danger states self-explain).
function userMgmtRailVisibility(user = {}, orgLabels = userMgmtOrgLabelMap()) {
  const role = canonicalRole(user.role);
  const line = (text, tone = "") => `<span${tone ? ` class="user-mgmt-sees--${tone}"` : ""}>${escapeHtml(text)}</span>`;
  const list = (names) => names.length ? `<ul class="user-mgmt-rail-scope">${names.map((name) => `<li>${escapeHtml(name)}</li>`).join("")}</ul>` : "";
  if (role === "SuperAdmin") {
    return { can: line("Everything — every company, every view."), cant: line("Nothing is hidden from this account.") };
  }
  const { entries } = userMgmtSeesScope(user, orgLabels);
  const names = entries.map((entry) => entry.label);
  const totalKnown = orgLabels.size;
  const others = totalKnown > entries.length ? totalKnown - entries.length : 0;
  if (role === "OffshoreBiller") {
    if (!entries.length) {
      return { can: line("Nothing — no companies picked yet.", "warn"), cant: line("Everything. Pick their companies to give them work.") };
    }
    return {
      can: line("Every claim at:") + list(names),
      cant: line(others
        ? `Everything at ${formatNounCount(others, "other company")}, plus reports, fee schedules and user management.`
        : "Everything at any other company, plus reports, fee schedules and user management.")
    };
  }
  if (isExternalClientRole(role)) {
    if (entries.length !== 1) {
      const isolation = userMgmtClientIsolation(user);
      return { can: line(isolation ? isolation.detail : "Assign exactly one company.", entries.length ? "danger" : "warn"), cant: "" };
    }
    return {
      can: list(names) + line("Their own data only. This portal is isolated."),
      cant: line("Every other company.")
    };
  }
  if (!entries.length) {
    return { can: line("Nothing yet — pick their companies in Edit.", "warn"), cant: "" };
  }
  if (userMgmtCoversAllCompanies(user, orgLabels)) {
    return { can: line(`All companies (${formatCount(totalKnown)}).`), cant: line("Nothing — they work every company.") };
  }
  return {
    can: line("Everything at:") + list(names),
    cant: line(others ? `${formatNounCount(others, "other company")}.` : "Companies outside this list.")
  };
}

function renderUserMgmtSelectionPanel(user = {}) {
  const role = canonicalRole(user.role);
  const profile = accessProfileForUser(user);
  const permissions = new Set(profile.permissions || []);
  const followUpPolicy = followUpActionPolicyForRole(user.followUpActionAccess || user.followUpActionPolicy || {}, role);
  const allowedActions = Object.entries(followUpPolicy.actions || {})
    .filter(([, mode]) => mode !== "denied")
    .map(([action]) => action);
  const blockedActionCount = Object.values(followUpPolicy.actions || {}).filter((mode) => mode === "denied").length;
  const orgLabels = userMgmtOrgLabelMap();
  const visibility = userMgmtRailVisibility(user, orgLabels);
  const views = navigableViewsForRole(role, permissions).map(viewLabel).filter(Boolean);
  const status = userMgmtStatusMeta(user);
  const authDisagreement = portalUserAuthDisagreement(user);
  const seesSummary = authDisagreement
    ? { strong: user.followUpActionPolicyConflict ? "Conflicting follow-up action access" : "Conflicting authentication access", small: "Review each authentication lane below" }
    : userMgmtSeesLabel(user, orgLabels);
  const authLaneSummaries = portalUserAuthLaneSummaries(user);
  const authLaneSection = authDisagreement ? `
    <div class="user-mgmt-rail-section user-mgmt-rail-section--warning">
      <b>Authentication lanes disagree</b>
      <span>${escapeHtml(portalUserAuthDisagreementMessage(user))}</span>
      ${authLaneSummaries.map((summary) => `<span>${escapeHtml(summary)}</span>`).join("")}
    </div>
  ` : "";
  // Persistent, not a toast: the pill reads "Disabled" and only half of that is
  // Shown for every server-confirmed disable, because whether this person ALSO
  // holds a static users-JSON login is NOT computable: that lane is keyed by
  // `username` and this one by email, and the users-JSON schema has no email
  // field to join on. (The two example usernames that used to sit here were real
  // logins, in a file served anonymously.) The old boolean claimed to know and
  // was false for exactly the people it existed to flag. Left visible until the
  // account is re-enabled or the Key Vault procedure is carried out.
  const staticLaneSection = user.custom && user.staticLaneNotRevoked && status.key === "disabled" ? `
    <div class="user-mgmt-rail-section">
      <b>Check the static users list</b>
      <span>Their invite-created portal login is disabled and signed out. This screen cannot see or revoke a static users-list login, so <b>if they also have one it is still live</b>. Set <code>"disabled": true</code> on their entry in the <code>portal-auth-users-json</code> Key Vault secret and force a settings recycle. See AUTH_ONBOARDING_RUNBOOK.md.</span>
    </div>
  ` : "";
  const staticProfileSection = user.directoryAuthoritative && Array.isArray(user.sources) && user.sources.includes("static-json") ? `
    <div class="user-mgmt-rail-section user-mgmt-rail-section--warning">
      <b>${user.followUpActionPolicyConflict ? "Follow-up access must be reconciled" : "Managed in Key Vault"}</b>
      <span>${user.followUpActionPolicyConflict ? "This person's managed login and portal account grant different follow-up action permissions. Editing only one sign-in method would leave unsafe access in place, so this profile stays read-only until an administrator reconciles both." : "This centrally managed login cannot be edited here. Contact an administrator to update it."}</span>
    </div>
  ` : "";
  // VOB pairs Resend with "Get shareable invite link" (admin-user-forms.tsx:398)
  // for the invite whose email never arrived - a spam filter that drops it or a
  // client network that blocks the portal domain leaves an admin pressing Resend
  // into the same hole. Ours sits in the Invite section beside Resend because
  // that is where an unaccepted invite is already being discussed.
  const inviteLinkPending = Boolean(userMgmtDirectoryState.inviteLink
    && userMgmtDirectoryState.inviteLink.userKey === userMgmtInviteResendKey(user)
    && userMgmtDirectoryState.inviteLink.pending);
  const inviteActionPending = userMgmtInviteActionPending(user);
  const inviteRevokePending = Boolean(userMgmtDirectoryState.inviteRevoke?.pending
    && userMgmtDirectoryState.inviteRevoke.inviteId === user.inviteId
    && userMgmtDirectoryState.inviteRevoke.requestKey === userMgmtInviteRevokeRequestKey());
  const inviteSection = user.custom && status.key === "pending_invite" ? `
    <div class="user-mgmt-rail-section">
      <b>Invite</b>
      <span>Invite sent${user.invitedAt ? ` ${escapeHtml(formatDateTime(user.invitedAt))}` : ""}${user.email ? ` to ${escapeHtml(user.email)}` : ""}</span>
      <div class="user-mgmt-invite-actions">
        <button class="ghost-button" data-user-rail-resend type="button"${inviteActionPending ? " disabled" : ""}>${userMgmtDirectoryState.inviteResend?.pending && inviteActionPending ? "Resending…" : "Resend invite"}</button>
        <button class="ghost-button" data-user-rail-invite-link type="button"${inviteActionPending ? " disabled" : ""}>${inviteLinkPending ? "Generating link..." : "Get shareable invite link"}</button>
        ${user.inviteId ? `<button class="user-mgmt-row-remove" data-user-rail-revoke-invite type="button"${inviteActionPending ? " disabled" : ""}>${inviteRevokePending ? "Revoking…" : "Revoke invitation"}</button>` : ""}
      </div>
      ${renderUserMgmtInviteLinkBox(user)}
    </div>
  ` : "";
  return `
    <div class="user-mgmt-rail-head">
      <span>Access profile</span>
      <strong>${escapeHtml(user.name || "")}</strong>
      ${user.email ? `<small>${escapeHtml(user.email)}</small>` : (user.custom ? `<small>No login email — add one to send an invite.</small>` : "")}
      <div class="user-mgmt-rail-pills">
        ${userMgmtStatusCellHtml(user)}
        ${userMgmtProfileIsPortalManaged(user) ? '<button class="primary-button" data-user-rail-edit type="button">Edit access</button>' : ""}
      </div>
      <!-- VOB_USERS_MODULE_SPEC 3.3 step 2 / 2.3: role is plain text, never a
           chip - VOB renders it through a roleLabels map and colours status
           only. Status also stopped being said twice: the pill above already
           carries the word this line used to repeat. -->
      <div class="user-mgmt-access-line"><span class="user-mgmt-role-text" data-role-plain>${escapeHtml(authDisagreement ? "Conflicting access" : (roleProfiles[role]?.label || role))}</span> · ${escapeHtml(seesSummary.strong)}${authDisagreement ? "" : ` · ${escapeHtml(formatCount(views.length))} view${views.length === 1 ? "" : "s"}`}</div>
    </div>
    ${authLaneSection}
    <div class="user-mgmt-rail-section">
      <b>Can see</b>
      ${visibility.can}
    </div>
    ${visibility.cant ? `<div class="user-mgmt-rail-section">
      <b>Can't see</b>
      ${visibility.cant}
    </div>` : ""}
    <div class="user-mgmt-rail-section">
      <b>Can open</b>
      <span>${escapeHtml(formatNounCount(views.length, "available view"))}</span>
      <details class="user-mgmt-disclosure"><summary>View accessible pages</summary><ul>${views.map((view) => `<li>${escapeHtml(view)}</li>`).join("")}</ul></details>
    </div>
    ${renderOversightProfile(user)}
    <div class="user-mgmt-rail-section">
      <b>Follow-up actions</b>
      ${isExternalClientRole(role)
        ? '<span>Cannot change follow-up actions</span>'
        : renderUserMgmtActionSummary(`${formatNounCount(allowedActions.length, "allowed action")}${blockedActionCount ? ` · ${formatNounCount(blockedActionCount, "blocked action")}` : ""}`, allowedActions)}
    </div>
    ${!isExternalClientRole(role) ? (() => { const memberships = staffTeamsForUser(user); return `<div class="user-mgmt-rail-section"><b>Teams</b><div data-user-team-memberships="${escapeHtml(staffTeamUserKey(user))}">${portalUserDirectoryHostedMode() ? "Loading team memberships…" : memberships.length ? escapeHtml(memberships.map((team) => team.name || "Unnamed team").join(", ")) : "Not on a team yet"}</div><button class="ghost-button" data-user-add-team type="button">Add to team</button></div>`; })() : ""}
    ${renderUserActivityPanel(user)}
    ${staticProfileSection}
    ${staticLaneSection}
    ${inviteSection}
    <div class="user-mgmt-rail-actions">
      ${userMgmtProfileIsPortalManaged(user)
        ? `<button class="ghost-button" data-user-rail-edit type="button">Edit</button>
      <button class="user-mgmt-row-remove" data-user-rail-remove type="button">Remove</button>`
        : `<small class="user-mgmt-rail-note">${user.directoryAuthoritative ? "This person's sign-in is managed elsewhere." : "Built-in demo user — not editable"}</small>`}
    </div>
  `;
}

function userMgmtInviteRevokeRequestKey() {
  return portalUserDirectoryAccessScopedRequestKey({ kind: "user-invite-revoke", base: portalInviteApiBase() });
}

async function revokeUserMgmtInvitation(index) {
  if (!isTrustedUserManagerSession()) return false;
  const user = users[index];
  if (!user?.custom || !user.inviteId || normalizeUserStatus(user.status) !== "pending_invite") return false;
  if (userMgmtInviteActionPending(user)) return false;
  const requestKey = userMgmtInviteRevokeRequestKey();
  if (userMgmtDirectoryState.inviteRevoke?.pending && userMgmtDirectoryState.inviteRevoke.requestKey === requestKey) return false;
  if (!portalInviteApiReady()) { showToast("Invitations are temporarily unavailable. Try again later."); return false; }
  if (typeof confirm === "function" && !confirm(`Revoke the invitation for ${user.name || user.email}?\n\nUnused setup links for this invitation's access will stop working. This does not change an existing account's access.`)) return false;
  const current = () => userMgmtInviteRevokeRequestKey() === requestKey && isTrustedUserManagerSession();
  const pending = { requestKey, inviteId: String(user.inviteId), pending: true };
  userMgmtDirectoryState.inviteRevoke = pending;
  renderUserMgmtRail();
  let confirmed = false;
  try {
    const response = await fetch(`${portalInviteApiBase()}/api/users/invites/revoke`, {
      method: "POST", cache: "no-store",
      headers: { "content-type": "application/json", authorization: `Bearer ${workflowApiToken() || trackerApiToken()}` },
      body: JSON.stringify({ inviteId: user.inviteId, email: user.email || "", reason: "Pending invitation revoked from Manage Users" })
    });
    const payload = await response.json().catch(() => ({}));
    if (!current()) return false;
    if (!response.ok || payload.invite?.inviteId !== String(user.inviteId) || payload.invite?.status !== "revoked") {
      showToast((payload.reason || payload.error) === "invite_accepted" ? "This invitation was already accepted. Use the account's access controls to manage sign-in." : "The revocation could not be confirmed. Refresh the directory before trying again.");
      return false;
    }
    confirmed = true;
    if (userMgmtDirectoryState.inviteLink?.userKey === userMgmtInviteResendKey(user)) userMgmtDirectoryState.inviteLink = null;
    const refreshed = await refreshAuthoritativeUserGovernance({ force: true });
    if (!current()) return false;
    showToast(refreshed ? "Invitation revoked. Unused setup links for this access no longer work." : "The server revoked the invitation, but the directory could not refresh. Reload Manage Users to see the latest status.");
    return true;
  } catch (_) {
    if (current()) showToast(confirmed ? "The server revoked the invitation, but the directory could not refresh. Reload Manage Users to see the latest status." : "The revocation could not be confirmed. Refresh the directory before trying again.");
    return false;
  } finally {
    if (userMgmtDirectoryState.inviteRevoke === pending) {
      userMgmtDirectoryState.inviteRevoke = null;
      if (current()) renderUserMgmtRail();
    }
  }
}

function userMgmtInviteActionPending(user = {}) {
  const requestKey = userMgmtInviteRevokeRequestKey();
  // These are singleton operation slots: keep all invitation controls locked
  // for this session until its active operation settles, even across row changes.
  return [userMgmtDirectoryState.inviteRevoke, userMgmtDirectoryState.inviteResend, userMgmtDirectoryState.inviteLink]
    .some((entry) => entry?.pending && entry.requestKey === requestKey);
}

async function resendUserMgmtInvite(index) {
  if (!isTrustedUserManagerSession()) return false;
  const user = users[index];
  if (!user?.custom || userMgmtInviteActionPending(user)) return false;
  if (!user.inviteId) { showToast("Refresh the directory to load the invitation before resending it."); return false; }
  if (!portalInviteApiReady()) { showToast("Invites are temporarily unavailable. Please try again later."); return false; }
  const requestKey = userMgmtInviteRevokeRequestKey();
  const userKey = userMgmtInviteResendKey(user);
  const current = () => isTrustedUserManagerSession() && userMgmtInviteRevokeRequestKey() === requestKey && userMgmtInviteResendKey(user) === userKey;
  const pending = { requestKey, inviteId: String(user.inviteId), pending: true };
  userMgmtDirectoryState.inviteResend = pending;
  renderUserMgmtRail();
  try {
    const response = await fetch(`${portalInviteApiBase()}/api/users/invites/resend`, {
      method: "POST", cache: "no-store",
      headers: { "content-type": "application/json", authorization: `Bearer ${workflowApiToken() || trackerApiToken()}` },
      body: JSON.stringify({ inviteId: user.inviteId, email: user.email || "" })
    });
    const body = await response.json().catch(() => ({}));
    if (!current()) return false;
    if (!response.ok || body.error || body.invite?.inviteId !== String(user.inviteId) || body.invite?.status !== "pending_invite") {
      showToast((body.reason || body.error) === "invite_accepted" ? "This invitation was already accepted. No new setup link was sent." : "The invitation could not be resent. Refresh the directory before trying again.");
      return false;
    }
    userMgmtDirectoryState.inviteLink = null;
    const refreshed = await refreshAuthoritativeUserGovernance({ force: true });
    if (!current()) return false;
    showToast(body.sent === false ? "The setup link was renewed, but the email was not sent. Try Resend invite again or get a shareable invite link."
      : refreshed ? `Invite re-sent to ${user.email}.` : "The invitation was resent, but the directory could not refresh. Reload Manage Users for the latest status.");
    return body.sent !== false;
  } catch (_) {
    if (current()) showToast("The resend could not be confirmed. Refresh the directory before trying again.");
    return false;
  } finally {
    if (userMgmtDirectoryState.inviteResend === pending) {
      userMgmtDirectoryState.inviteResend = null;
      if (current()) renderUserMgmtRail();
    }
  }
}

function userMgmtInviteResendKey(user = {}) {
  return JSON.stringify({
    name: String(user.name || ""),
    email: String(user.email || ""),
    role: canonicalRole(user.role),
    orgIds: normalizeUserScopeList(user.orgIds),
    clientNames: normalizeUserScopeList(user.clientNames),
    payerNames: normalizeUserScopeList(user.payerNames)
  });
}

// The returned link (VOB admin-user-forms.tsx:436-457): the URL in a code box
// with a Copy button, the 7-day life, and the warning that whoever holds it can
// set the password. Both warnings are carried in substance, and the second one
// is the one that makes this action safe to offer at all.
//
// Keyed to the selected person. A setup link opens exactly ONE account, so a
// stale box must never be readable under another name - if the selection or the
// person's identity moved, the box is simply gone.
function renderUserMgmtInviteLinkBox(user = {}) {
  const link = userMgmtDirectoryState.inviteLink;
  if (!link || link.userKey !== userMgmtInviteResendKey(user)) return "";
  if (link.error) return `<span class="user-mgmt-invite-link-error" role="alert">${escapeHtml(link.error)}</span>`;
  if (!link.url) return "";
  return `
    <div class="user-mgmt-invite-link">
      <span class="user-mgmt-invite-link-lead">Send this to ${escapeHtml(user.name || "this person")} from your own email, Teams or text - do not post it anywhere shared.</span>
      <div class="user-mgmt-invite-link-row">
        <code class="user-mgmt-invite-link-url">${escapeHtml(link.url)}</code>
        <button class="ghost-button" data-user-rail-invite-link-copy type="button">Copy</button>
      </div>
      <span class="user-mgmt-invite-link-note">Link works for 7 days.</span>
      <strong class="user-mgmt-invite-link-warning">Anyone with this link can set the account's password - share it only with this user.</strong>
    </div>
  `;
}

// Ask the server for a fresh setup link instead of mailing one. The token is
// rotated server-side, so the link in the undelivered email stops working - the
// shared one becomes the only way in.
async function generateUserMgmtInviteLink(index) {
  if (!isTrustedUserManagerSession()) return;
  const user = users[index];
  if (!user || !user.custom) return;
  if (userMgmtInviteActionPending(user)) return;
  const requestKey = userMgmtInviteRevokeRequestKey();
  if (!user.email) { showToast("No invite email on file."); return; }
  if (!portalInviteApiReady()) {
    showToast("Setup links are temporarily unavailable. Please try again later.");
    return;
  }
  const linkKey = userMgmtInviteResendKey(user);
  const pending = { userKey: linkKey, requestKey, url: "", error: "", pending: true };
  userMgmtDirectoryState.inviteLink = pending;
  renderUserMgmtRail();
  const current = () => isTrustedUserManagerSession() && userMgmtInviteRevokeRequestKey() === requestKey;
  try {
    const result = await sendPortalInviteLinkRequest(user);
    if (!current() || userMgmtDirectoryState.inviteLink !== pending) return;
    // A setup link belongs to the selected identity and stays only in tab memory.
    if (users[userMgmtDirectoryState.selectedIndex] !== user || userMgmtInviteResendKey(user) !== linkKey) return;
    userMgmtDirectoryState.inviteLink = result.ok
      ? { userKey: linkKey, requestKey, url: result.link, error: "", pending: false }
      : { userKey: linkKey, requestKey, url: "", error: result.message, pending: false };
    renderUserMgmtRail();
  } finally {
    // Abandoned requests must release their own interlock without clearing a
    // newer person's request or exposing this person's link under another row.
    if (userMgmtDirectoryState.inviteLink === pending) {
      userMgmtDirectoryState.inviteLink = null;
      if (current()) renderUserMgmtRail();
    }
  }
}

async function copyUserMgmtInviteLink(panel) {
  const link = userMgmtDirectoryState.inviteLink;
  const button = panel && panel.querySelector("[data-user-rail-invite-link-copy]");
  if (!link || !link.url || !button) return;
  try {
    await navigator.clipboard.writeText(link.url);
  } catch (_error) {
    // Clipboard access can be blocked (denied permission, insecure context). The
    // URL is still selectable in the box, so this is a soft failure and never an
    // error banner - VOB makes the same call (admin-user-forms.tsx:363).
    return;
  }
  button.textContent = "Copied";
  clearTimeout(userMgmtInviteLinkCopyTimer);
  userMgmtInviteLinkCopyTimer = setTimeout(() => {
    if (button.isConnected) button.textContent = "Copy";
  }, 2000);
}

// ---- Rail edit mode (custom users only) ----

function beginUserMgmtEdit(index) {
  if (!isTrustedUserManagerSession()) return;
  const user = users[index];
  if (!user || !userMgmtProfileIsPortalManaged(user)) return;
  userMgmtDirectoryState.selectedIndex = index;
  userMgmtDirectoryState.selectedPersonKey = userMgmtPersonKey(user);
  if(portalUserDirectoryHostedMode())peopleDirectoryState().selected=user;
  userMgmtDirectoryState.editing = true;
  userMgmtDirectoryState.editDraft = {
    name: user.name || "",
    email: user.email || "",
    status: normalizeUserStatus(user.status),
    role: canonicalRole(user.role),
    scopeValues: userMgmtEditScopeValues(user),
    // The offshore biller's BOOK (backend scope.payerNames). Held as the raw
    // text the admin types; normalizeUserScopeList parses it on save, exactly
    // like every other scope list here.
    payerNames: normalizeUserScopeList(user.payerNames).join(", "),
    followUpActionPolicy: followUpActionPolicyForRole(user.followUpActionAccess || user.followUpActionPolicy || {}, canonicalRole(user.role)),
    followUpPolicySearch: "",
    followUpPolicyNotice: ""
  };
  renderUserMgmtList();
}

// Scope options for the edit form - the shared company picker for internal
// roles, the one-company radio group for client roles: every known client
// database, plus synthetic rows for any scope already on the user that the
// loaded data does not cover (so saving never silently drops scope).
function userMgmtEditScopeOptions(user = {}) {
  const options = userMgmtTenantClientDatabaseOptions().map((option) => ({ ...option }));
  const coveredOrgs = new Set(options.map((option) => (option.orgIds || [])[0]));
  const orgLabels = userMgmtOrgLabelMap();
  const displayOrgLabels = userMgmtDisplayOrgLabelMap(orgLabels);
  normalizeUserScopeList(user.orgIds).forEach((orgId) => {
    if (coveredOrgs.has(orgId)) return;
    coveredOrgs.add(orgId);
    const rawLabel = orgLabels.get(orgId) || orgId;
    const clientName = rawLabel === orgId ? "" : rawLabel;
    const label = displayOrgLabels.get(orgId) || (clientName || "Company name unavailable");
    options.push({
      value: `${clientName || orgId}|||${orgId}`,
      label,
      clientName,
      orgIds: [orgId]
    });
  });
  const coveredClients = new Set(options.map((option) => String(option.clientName || "").toLowerCase()).filter(Boolean));
  normalizeUserScopeList(user.clientNames).forEach((clientName) => {
    const key = clientName.toLowerCase();
    if (coveredClients.has(key)) return;
    coveredClients.add(key);
    options.push({ value: `${clientName}|||`, label: clientName, clientName, orgIds: [] });
  });
  return options;
}

function userMgmtEditScopeValues(user = {}) {
  const orgIds = new Set(normalizeUserScopeList(user.orgIds));
  const clientNames = new Set(normalizeUserScopeList(user.clientNames).map((name) => name.toLowerCase()));
  return userMgmtEditScopeOptions(user)
    .filter((option) => (option.orgIds || []).some((orgId) => orgIds.has(orgId))
      || (option.clientName && clientNames.has(String(option.clientName).toLowerCase())))
    .map((option) => option.value);
}

function renderUserMgmtEditForm(user = {}) {
  const draft = userMgmtDirectoryState.editDraft || {};
  const role = canonicalRole(draft.role);
  return `
    <div class="user-mgmt-rail-head">
      <span>Access profile</span>
      <strong>Edit ${escapeHtml(user.name || "")}</strong>
    </div>
    <div class="user-mgmt-edit">
      <label>Name<input id="userMgmtEditName" type="text" value="${escapeHtml(draft.name || "")}" autocomplete="off" readonly aria-describedby="userMgmtIdentityNote"></label>
      <label>Email<input id="userMgmtEditEmail" type="email" value="${escapeHtml(draft.email || "")}" autocomplete="off" readonly aria-describedby="userMgmtIdentityNote"></label>
      <p id="userMgmtIdentityNote" class="user-mgmt-edit-scope-note">Identity fields are read-only here. This editor changes role and company access for portal-managed accounts.</p>
      <label>Status
        <select id="userMgmtEditStatus">
          <option value="active"${draft.status === "active" ? " selected" : ""}>Active</option>
          <option value="pending_invite"${draft.status === "pending_invite" ? " selected" : ""}>Invite sent</option>
          <option value="disabled"${draft.status === "disabled" ? " selected" : ""}>Disabled</option>
        </select>
      </label>
      <p class="user-mgmt-edit-scope-note">Disabling signs them out everywhere and revokes their trusted devices. It goes through the portal server — if that call fails, the status is not saved.</p>
      <label>Role
        <select id="userMgmtEditRole">
          ${userManagerAssignableRoles().map((value) => `<option value="${value}"${role === value ? " selected" : ""}>${escapeHtml(roleProfiles[value]?.label || value)}</option>`).join("")}
        </select>
      </label>
      ${renderUserMgmtEditScopeControl(user, draft)}
      ${renderFollowUpActionPolicyEditor(draft, role, { idPrefix: "userMgmtEditFollowUp" })}
      ${renderUserMgmtEditLiveWarning(user, draft)}
      <div class="user-mgmt-rail-actions">
        <button id="userMgmtEditSaveBtn" class="primary-button" type="button">Save changes</button>
        <button id="userMgmtEditCancelBtn" class="ghost-button" type="button">Cancel</button>
      </div>
    </div>
  `;
}

function renderUserMgmtEditActionAccess(role, draft = {}) {
  if (isExternalClientRole(role)) return '<p class="user-mgmt-edit-scope-note">Clients cannot change follow-up actions.</p>';
  if (role === "SuperAdmin") return '<p class="user-mgmt-edit-scope-note"><strong>Follow-up actions:</strong> All actions.</p>';
  const selected = new Set(normalizeUserScopeList(draft.followUpActions).map((action) => normalizeFollowUpActionKey(canonicalFollowUpAction(action) || action)));
  return `<fieldset class="user-mgmt-edit-scope user-mgmt-action-access">
    <legend>Follow-up actions this person can use</legend>
    <p class="user-mgmt-edit-scope-note">Checked actions are available. Unchecked actions stay visible here so you can see what is blocked.</p>
    ${getSelectableActionRules().map((rule) => {
      const key = normalizeFollowUpActionKey(rule.action);
      return `<label class="user-mgmt-edit-scope-row"><input type="checkbox" data-user-action-access value="${escapeHtml(rule.action)}"${selected.has(key) ? " checked" : ""}><span>${escapeHtml(rule.action)}</span></label>`;
    }).join("")}
  </fieldset>`;
}

// Live (pre-save) warning slot. Stateside with zero companies is saveable
// but warns in amber; save-time blocks overwrite this with red "Not saved."
function renderUserMgmtEditLiveWarning(user = {}, draft = {}) {
  const role = canonicalRole(draft.role);
  const who = draft.name || user.name || "this user";
  const noCompanies = (role === "StatesideBiller" || role === "OffshoreBiller") && !(draft.scopeValues || []).length;
  const noBook = roleRequiresBook(role) && !noCompanies && !normalizeUserScopeList(draft.payerNames).length;
  const warn = noCompanies
    ? `No companies selected — ${who} will see nothing until you pick at least one.`
    : (noBook ? `No payers listed — ${who} has no book, so access cannot be completed until you add at least one.` : "");
  return `<div id="userMgmtEditWarning" class="user-mgmt-isolation-warning${warn ? " user-mgmt-isolation-warning--warn" : ""}" role="status" aria-live="polite">${warn ? escapeHtml(warn) : ""}</div>`;
}

function renderUserMgmtEditScopeControl(user = {}, draft = {}) {
  if (activeClientGroupTenant()) return `<p class="user-mgmt-edit-scope-note">Company: ${escapeHtml(activeClientGroupLabel())}. Existing access is preserved. Choose All clients at the top to change a person’s company access.</p>`;
  const role = canonicalRole(draft.role);
  if (role === "SuperAdmin") return `<p class="user-mgmt-edit-scope-note">Sees everything — no company scope.</p>`;
  const options = userMgmtEditScopeOptions(user);
  const selected = new Set(draft.scopeValues || []);
  // Both external client roles are one-company logins — radios, never
  // checkboxes. A ClientBiller reads the grid for ONE practice.
  if (isExternalClientRole(role)) {
    return `
      <fieldset class="user-mgmt-edit-scope">
        <legend>Their company — exactly one</legend>
        ${options.map((option) => `
        <label class="user-mgmt-edit-scope-row">
          <input type="radio" name="userMgmtEditScope" value="${escapeHtml(option.value)}"${selected.has(option.value) ? " checked" : ""}>
          <span>${escapeHtml(option.clientName || option.label)}</span>
        </label>`).join("") || `<p class="user-mgmt-edit-scope-note">No client groups are available yet.</p>`}
      </fieldset>
      <p class="user-mgmt-edit-scope-note">A client portal is isolated to one company's data.</p>
    `;
  }
  // Internal roles get the SHARED picker (renderScopeCompanyPicker) - the same
  // component the add-user wizard's companies step renders. The fieldset and the
  // legend stay: they are what makes this read as one control, and the picker
  // still emits the checkbox grid's name="userMgmtEditScope" values inside it.
  return `
    <fieldset class="user-mgmt-edit-scope user-mgmt-edit-scope--picker">
      <legend>Companies they work on</legend>
      ${renderScopeCompanyPicker(userMgmtEditScopePickerConfig(user, draft))}
    </fieldset>
    ${renderUserMgmtEditPayerBook(role, draft)}
  `;
}

// The rail's half of the shared picker. Built here rather than inline so the
// search box can re-render just the option list from exactly the config the
// first render used - a second, hand-copied config is how a filtered list
// starts disagreeing with the chips above it.
function userMgmtEditScopePickerConfig(user = {}, draft = {}) {
  return {
    options: userMgmtEditScopeOptions(user).map((option) => ({
      value: option.value,
      label: option.clientName || option.label || option.value,
      searchText: (option.orgIds || [])[0] || ""
    })),
    selectedValues: draft.scopeValues || [],
    search: draft.scopeSearch || "",
    idPrefix: "userMgmtEditScope",
    valueName: "userMgmtEditScope",
    emptyChipText: "No companies selected yet.",
    noOptionsText: "No client groups are available yet."
  };
}

// The offshore biller's BOOK OF PAYERS (backend scope.payerNames).
//
// Companies alone are NOT a book. backend/portalRoleCatalog.js requires an
// OffshoreBiller to carry payerNames | clientNames | assignedOnly and denies
// them outright with a typed 403 (book_not_configured) otherwise — and this
// editor stores orgIds only (userMgmtScopeFromEditValues deliberately clears
// clientNames for offshore) and never sets assignedOnly. So payerNames is the
// ONLY book it can produce, which is why the field exists and why the save
// below requires it: without it no offshore biller could be provisioned through
// the product at all.
//
// Plain text input, comma or semicolon separated — normalizeUserScopeList
// parses the same shape it parses for orgIds/clientNames.
function renderUserMgmtEditPayerBook(role, draft = {}) {
  if (!roleRequiresBook(role)) return "";
  const value = typeof draft.payerNames === "string"
    ? draft.payerNames
    : normalizeUserScopeList(draft.payerNames).join(", ");
  return `
    <label>Payers they work
      <input id="userMgmtEditPayerNames" type="text" value="${escapeHtml(value)}" autocomplete="off" spellcheck="false" placeholder="Aetna, Cigna, UnitedHealthcare">
    </label>
    <p class="user-mgmt-edit-scope-note">Payers this biller works, comma separated. Required — an offshore biller with no book sees nothing.</p>
  `;
}

function wireUserMgmtEditForm(user) {
  const panel = document.getElementById("userMgmtSelectionPanel");
  const draft = userMgmtDirectoryState.editDraft;
  if (!panel || !draft) return;
  const nameEl = panel.querySelector("#userMgmtEditName");
  nameEl?.addEventListener("input", () => { draft.name = nameEl.value; });
  const emailEl = panel.querySelector("#userMgmtEditEmail");
  emailEl?.addEventListener("input", () => { draft.email = emailEl.value; });
  const statusEl = panel.querySelector("#userMgmtEditStatus");
  statusEl?.addEventListener("change", () => { draft.status = normalizeUserStatus(statusEl.value); });
  const roleEl = panel.querySelector("#userMgmtEditRole");
  roleEl?.addEventListener("change", () => {
    const prevRole = canonicalRole(draft.role);
    const nextRole = canonicalRole(roleEl.value);
    if (nextRole !== prevRole) {
      const values = draft.scopeValues || [];
      // Stateside <-> Offshore now share one company model (per-user orgIds),
      // so the picks carry over untouched.
      if (isExternalClientRole(nextRole) && values.length > 1) {
        // Never silently keep one of many — clear and force an explicit pick.
        draft.scopeValues = [];
      }
      // Role changes can widen action authority without changing company scope.
      // Reset instead of carrying a stronger policy across that boundary.
      draft.followUpActionPolicy = followUpActionPolicyPreset(nextRole, "role_default");
      draft.followUpPolicyNotice = "Role changed. Follow-up actions were reset to this role's safe defaults; review them before saving.";
    }
    draft.role = nextRole;
    renderUserMgmtRail();
  });
  wireFollowUpActionPolicyEditor(panel, draft, canonicalRole(draft.role), renderUserMgmtRail);
  const payersEl = panel.querySelector("#userMgmtEditPayerNames");
  payersEl?.addEventListener("input", () => { draft.payerNames = payersEl.value; });
  panel.querySelectorAll("[data-user-action-access]").forEach((input) => input.addEventListener("change", () => {
    draft.followUpActionAccessConfigured = true;
    draft.followUpActions = [...panel.querySelectorAll("[data-user-action-access]:checked")].map((entry) => entry.value);
  }));
  panel.querySelectorAll('input[name="userMgmtEditScope"]').forEach((input) => {
    input.addEventListener("change", () => {
      if (input.type === "radio") {
        draft.scopeValues = input.checked ? [input.value] : [];
      } else {
        const selected = new Set(draft.scopeValues || []);
        if (input.checked) selected.add(input.value);
        else selected.delete(input.value);
        draft.scopeValues = Array.from(selected);
      }
      renderUserMgmtRail();
    });
  });
  // The shared company picker. The one-company radio group is still wired by
  // the name="userMgmtEditScope" block above; this drives the SAME
  // draft.scopeValues, so the live warning and saveUserMgmtEdit see exactly
  // what the checkbox grid used to hand them.
  const pickScopeCompany = (value) => {
    const picked = new Set(draft.scopeValues || []);
    if (picked.has(value)) picked.delete(value);
    else picked.add(value);
    draft.scopeValues = Array.from(picked);
    renderUserMgmtRail();
  };
  wireScopeCompanyPickerPicks(panel, pickScopeCompany);
  panel.querySelectorAll("[data-scope-remove]").forEach((chip) => chip.addEventListener("click", () => {
    const value = chip.dataset.scopeRemove;
    draft.scopeValues = (draft.scopeValues || []).filter((entry) => entry !== value);
    renderUserMgmtRail();
  }));
  panel.querySelectorAll("[data-scope-action]").forEach((button) => button.addEventListener("click", () => {
    draft.scopeValues = button.dataset.scopeAction === "all"
      ? userMgmtEditScopeOptions(user).map((option) => option.value)
      : [];
    renderUserMgmtRail();
  }));
  const scopeSearch = panel.querySelector("#userMgmtEditScopeSearch");
  scopeSearch?.addEventListener("input", () => {
    draft.scopeSearch = scopeSearch.value;
    const list = panel.querySelector("#userMgmtEditScopeList");
    if (!list) return;
    list.innerHTML = scopeCompanyPickerOptionsHtml(userMgmtEditScopePickerConfig(user, draft));
    wireScopeCompanyPickerPicks(list, pickScopeCompany);
  });
  panel.querySelector("#userMgmtEditSaveBtn")?.addEventListener("click", () => saveUserMgmtEdit(userMgmtDirectoryState.selectedIndex));
  panel.querySelector("#userMgmtEditCancelBtn")?.addEventListener("click", () => {
    userMgmtDirectoryState.editing = false;
    userMgmtDirectoryState.editDraft = null;
    renderUserMgmtList();
  });
}

// The only two status transitions that MUST reach the server. Everything else
// is the local roster catching up with something the server already knows:
// pending_invite -> active is an invite being accepted, and there is no account
// to enable and no session to revoke at that point.
function userAccessChangeFor(previousStatus, nextStatus) {
  if (nextStatus === "disabled" && previousStatus !== "disabled") return "disable";
  // ANY move off "disabled", not just to "active". "Invite sent" is selectable
  // in the same dropdown, and treating it as a local-only change would drop the
  // Disabled pill while the credential stayed disabled — the pill now carries a
  // security meaning and every exit from it has to mean the same thing.
  if (previousStatus === "disabled" && nextStatus !== "disabled") return "enable";
  return "";
}

// THE SINGLE REVOCATION TRANSPORT: POST /api/users/account-status, the same
// endpoint removeUserMgmtUser uses. Two branches independently built a disable
// control (one on this route, one on POST /api/users/{disable,enable}); shipping
// both would have left two definitions of "revoked" free to drift apart, which
// is the failure NORTH_STAR.md section 6 names for money and which is worse for
// auth — a lane that silently stops revoking looks exactly like one that works.
// Same permission (users:manage) as the invite calls beside it.
async function sendUserAccessChangeRequest(user = {}, action = "disable", reason = "") {
  const email = String(user.email || "").trim();
  if (!email) return { ok: false, code: "no_email" };
  if (!portalInviteApiReady()) return { ok: false, code: "offline" };
  const expectedStatus = action === "disable" ? "disabled" : "active";
  try {
    const apiBase = portalInviteApiBase();
    const apiToken = workflowApiToken() || trackerApiToken();
    const response = await fetch(`${apiBase}/api/users/account-status`, {
      method: "POST",
      cache: "no-store",
      headers: { "content-type": "application/json", authorization: `Bearer ${apiToken}` },
      // `reason` is REQUIRED by the server and is the durable audit record of
      // who locked this person out and why.
      body: JSON.stringify({ userKey: email.toLowerCase(), status: expectedStatus, reason })
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.error) {
      return { ok: false, code: String(body.reason || body.error || `http_${response.status}`), status: response.status, body };
    }
    // A 200 IS NOT A CONFIRMATION. A gateway interstitial, an SPA fallback, or
    // an older API build all return 200 with a body this code cannot read, and
    // treating that as success would put "Disabled" over a live account --
    // the same lie in a new transport. Only the server's own assertion counts.
    if (String(body.account?.status || "") !== expectedStatus) {
      return { ok: false, code: "unconfirmed_response", status: response.status, body };
    }
    return { ok: true, status: response.status, body: body.account || {} };
  } catch (error) {
    return { ok: false, code: "unreachable", status: 0, body: {}, error };
  }
}

// Every branch names the lane and what was NOT done. A generic "could not
// save" here would recreate the original defect in a smaller font: the admin
// walks away believing the person is locked out.
function userAccessChangeFailureMessage(result = {}, action = "disable", user = {}) {
  const who = user.name || user.email || "this user";
  const verb = action === "disable" ? "disabled" : "re-enabled";
  const code = String(result.code || "");
  const nothingHappened = action === "disable" ? "Nothing was revoked." : "Nothing was restored.";
  if (code === "no_email") return `${who} has no login email, so there is no portal account to ${action}. ${nothingHappened}`;
  if (code === "offline") return `Not connected to the portal server, so ${who} was NOT ${verb}. Reconnect and try again.`;
  // The server answers `no_server_account` for BOTH "never existed" and "real
  // person, wrong lane" — it tells them apart with the staticJsonLane block
  // rather than by status code, because only one of the two has a procedure
  // that works.
  if (code === "no_server_account") {
    const procedure = String(result.body?.staticJsonLane?.procedure || "");
    if (procedure) return `${who} signs in through the static users list, which this screen cannot change — ${nothingHappened.toLowerCase()} ${procedure}`;
    return `There is no invite-created portal account for ${user.email || who}, so ${nothingHappened.toLowerCase()}${action === "disable" ? " An invite that was never accepted has no account — revoke the invite instead." : ""}`;
  }
  if (code === "cannot_disable_self") return "You cannot disable your own account — ask another administrator.";
  if (code === "missing_reason") return "The server needs a reason for this change. Try again.";
  if (code === "unconfirmed_response") return `The server answered, but did not confirm the change, so ${who} may or may not be ${verb}. Re-open Manage Users to see the real status before relying on this.`;
  if (result.status === 401) return `Your admin session expired, so ${who} was NOT ${verb}. Sign in again and retry.`;
  if (result.status === 403) return `You do not have permission to manage this user in your current role and organization scope. ${who} was NOT ${verb}.`;
  if (code === "account_status_not_configured" || code === "account_status_not_updated" || result.status === 503) return `The server cannot change account access right now, so ${who} was NOT ${verb}. Contact your administrator.`;
  const detail = String(result.body?.message || result.error?.message || code || "");
  return detail ? `${who} was NOT ${verb}: ${detail}` : `${who} was NOT ${verb}. Please try again.`;
}

// Reports what the server actually revoked, including the lane it could not.
function userAccessChangeToast(name = "", action = "disable", body = {}) {
  if (action === "enable") {
    return `${name} can sign in again. Old sessions and trusted devices stay revoked — they will sign in fresh.`;
  }
  // Someone who never accepted their invite was never signed in, so saying
  // "signed out" would describe an event that did not happen.
  const neverSignedIn = String(body.previousStatus || "") === "pending_invite";
  const killed = [];
  if (Number(body.sessionsRevoked || 0)) killed.push(formatNounCount(Number(body.sessionsRevoked), "active session"));
  if (Number(body.devicesRevoked || 0)) killed.push(formatNounCount(Number(body.devicesRevoked), "trusted device"));
  if (Number(body.invitesRevoked || 0)) killed.push(formatNounCount(Number(body.invitesRevoked), "pending invite"));
  const parts = [
    `${name} is ${neverSignedIn ? "blocked" : "disabled and signed out"}${killed.length ? ` — ${killed.join(", ")} revoked` : ""}.`
  ];
  // Scope, not a guess: this endpoint only ever reaches the invite-created
  // lane, and it cannot tell whether this person also has a static login.
  if (body.staticJsonLane && body.staticJsonLane.coversStaticLane === false) {
    parts.push("A static users-list login, if they have one, was NOT revoked.");
  }
  return parts.join(" ");
}

// Async ONLY when the status transition needs the server (userAccessChangeFor).
// With no transition nothing awaits, so every existing caller keeps its
// synchronous behaviour and the claim-reassignment prompts below still run
// before the function returns.
async function saveUserMgmtEdit(index) {
  if (!isTrustedUserManagerSession()) return;
  const user = users[index];
  const draft = userMgmtDirectoryState.editDraft;
  if (!user || !userMgmtProfileIsPortalManaged(user) || !draft) return;
  const warning = document.getElementById("userMgmtEditWarning");
  const setWarning = (message) => {
    if (!warning) return;
    if (warning.classList && warning.classList.remove) warning.classList.remove("user-mgmt-isolation-warning--warn");
    warning.innerHTML = `<strong>Not saved.</strong> ${escapeHtml(message)}`;
  };
  const name = String(draft.name || "").trim().replace(/\s+/g, " ");
  const email = String(draft.email || "").trim();
  const role = canonicalRole(draft.role);
  if (!userManagerCanAssignRole(role)) { setWarning("You cannot assign a role above your own user-management authority."); return; }
  if (!name) { setWarning("Enter a name."); return; }
  if (users.some((u, i) => i !== index && String(u.name).toLowerCase() === name.toLowerCase())) {
    setWarning("Someone with that name already exists.");
    return;
  }
  if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setWarning("That email doesn't look right."); return; }
  if (isExternalClientRole(role) && (draft.scopeValues || []).length !== 1) {
    setWarning("Pick exactly one company — a client portal is isolated to one company's data.");
    return;
  }
  // Deny-by-default is real for offshore billers now: an empty company list is
  // not "all companies", it is "no rows". Block the save instead of silently
  // creating a login that opens All Data to nothing.
  if (role === "OffshoreBiller" && !(draft.scopeValues || []).length) {
    setWarning("Pick at least one company — an offshore biller with no companies sees nothing.");
    return;
  }
  // Same deny-by-default rule one level down: companies establish the tenant
  // boundary, the BOOK narrows it, and backend/portalRoleCatalog.js refuses an
  // offshore principal with no book (typed 403 book_not_configured). Block the
  // save rather than shipping a login that 403s on every request.
  if (roleRequiresBook(role) && !normalizeUserScopeList(draft.payerNames).length) {
    setWarning("List at least one payer before saving this offshore biller.");
    return;
  }
  // Only SuperAdmin carries no company scope; every other role is scoped by
  // its explicit per-user org list.
  const keepsScope = role !== "SuperAdmin";
  const scope = keepsScope ? userMgmtScopeFromEditValues(draft.scopeValues || [], role) : { orgIds: [], clientNames: [] };
  const proposedPayers = roleRequiresBook(role) ? normalizeUserScopeList(draft.payerNames) : [];
  const proposedFollowUpPolicy = isExternalClientRole(role)
    ? followUpActionPolicyPreset(role, "hidden")
    : followUpActionPolicyForRole(draft.followUpActionPolicy || {}, role);
  let authoritativeProfileChanged = false;
  if (user.directoryAuthoritative) {
    const same = (left, right) => normalizeUserScopeList(left).slice().sort().join("|") === normalizeUserScopeList(right).slice().sort().join("|");
    authoritativeProfileChanged = role !== canonicalRole(user.role)
      || !same(scope.orgIds, user.orgIds)
      || !same(scope.clientNames, user.clientNames)
      || !same(proposedPayers, user.payerNames)
      || JSON.stringify(proposedFollowUpPolicy.actions) !== JSON.stringify(followUpActionPolicyForRole(user.followUpActionAccess || user.followUpActionPolicy || {}, canonicalRole(user.role)).actions);
    if (authoritativeProfileChanged && Array.isArray(user.sources) && user.sources.includes("static-json")) {
      setWarning("This person has a centrally managed login that cannot be edited here. Contact an administrator to update it.");
      return;
    }
  }
  const oldName = user.name;
  const wasOffshore = canonicalRole(user.role) === "OffshoreBiller";
  let newStatus = normalizeUserStatus(draft.status);
  // Last-admin guard: block any save that would leave zero active Super
  // Admins — losing the last admin would lock user management shut.
  // Unreachable while the built-in Platform Admin exists, but explicit so
  // future custom-admin rosters inherit the invariant.
  if (canonicalRole(user.role) === "SuperAdmin" && normalizeUserStatus(user.status) !== "disabled"
    && (role !== "SuperAdmin" || newStatus === "disabled")
    && !users.some((u, i) => i !== index && canonicalRole(u.role) === "SuperAdmin" && normalizeUserStatus(u.status) !== "disabled")) {
    setWarning("At least one active Super Admin must remain.");
    return;
  }
  // CONSENT IS COLLECTED HERE, APPLIED BELOW. These prompts used to reassign
  // claims on the spot; now they only record what to do, because the status
  // flip between them and the commit is a network call that can fail. Deciding
  // first and mutating once means a Cancel, a refused revocation, and a dead
  // connection all leave the roster AND the claim routing exactly as they were.
  const pendingReassignments = [];
  // Company restriction vs open work: claims already routed to this biller
  // at now-excluded companies return to Unassigned — with consent.
  if (role === "OffshoreBiller" && newStatus !== "disabled" && scope.orgIds.length) {
    const outOfScopeClaims = claimsAssignedTo(oldName).filter((claim) => !scope.orgIds.includes(String(claim.orgId || "")));
    if (outOfScopeClaims.length) {
      const proceed = typeof confirm !== "function" || confirm(
        `${name} has ${formatNounCount(outOfScopeClaims.length, "open claim")} at companies outside this restriction.\n\n`
        + `Saving will return those claims to Unassigned so they can be re-routed. Claims at allowed companies stay with ${name}. Continue?`
      );
      if (!proceed) return;
      pendingReassignments.push([outOfScopeClaims, ""]);
    }
  }
  // Offboard safety (mirrors Remove): a demoted or disabled offshore biller
  // can no longer work claims, so any still routed to them return to the
  // unassigned pool (visible to stateside billers for re-routing).
  if (wasOffshore && (role !== "OffshoreBiller" || newStatus === "disabled")) {
    const openClaims = claimsAssignedTo(oldName);
    if (openClaims.length) {
      const proceed = typeof confirm !== "function" || confirm(
        `${oldName} still has ${formatNounCount(openClaims.length, "open claim")} routed to them.\n\n`
        + `Saving this change will return those claims to Unassigned so they can be re-routed. Continue?`
      );
      if (!proceed) return;
      pendingReassignments.push([openClaims, ""]);
    }
  }
  // An ACTIVE offshore biller being renamed keeps their work; the rename just
  // re-points it. Mutually exclusive with the offboard branch above, which
  // returns the same claims to Unassigned instead.
  const renamingOffshore = !(wasOffshore && (role !== "OffshoreBiller" || newStatus === "disabled"))
    && (wasOffshore || role === "OffshoreBiller") && oldName !== name;
  if (renamingOffshore) {
    // Offboard-safe rename: an active offshore biller's name change re-points
    // their routed claims to the new name BEFORE persisting (mirrors Remove).
    //
    // MINUS WHAT THE RESTRICTION BRANCH ALREADY CLAIMED. This branch and the
    // one above CAN co-fire — restricting an offshore biller's companies and
    // renaming them in the same save — and while these were inline
    // reassignClaims calls the first one had already emptied claimsAssignedTo
    // by the time this ran. Deferring both made this capture the out-of-scope
    // claims too and, applying last, route them TO the renamed biller at a
    // company they can no longer see: the opposite of the "returned to
    // Unassigned" the admin just agreed to, pushed to the server workflow
    // mirror by enqueueWorkflowCommand.
    // Keyed on the claim OBJECTS, not their ids: both arrays are filtered out
    // of the same sourceClaims in the same tick, and a row with a blank claimId
    // would otherwise put "" in the set and drop every other blank-id claim.
    const alreadyQueued = new Set(pendingReassignments.flatMap(([claims]) => claims));
    pendingReassignments.push([claimsAssignedTo(oldName).filter((claim) => !alreadyQueued.has(claim)), name]);
  }

  // THE STATUS FLIP IS A SERVER CALL NOW. Until 2026-08-03 this control ended
  // at localStorage: no request left the browser, so an admin who disabled a
  // departed employee revoked precisely nothing while the roster showed
  // "Disabled". The status is committed below only if the server confirms it,
  // and a failure names the lane that was NOT revoked instead of a generic
  // "could not save". This sits AFTER the prompts and BEFORE every mutation so
  // there is exactly one point of no return.
  // NO EMAIL MEANS NO ACCOUNT. Invites are keyed by email, so a roster row
  // without one has nothing on any server to revoke and its status is local
  // bookkeeping (it still drops them from the assignee dropdown and the active-
  // user picker). Sending it anyway refused the entire save - name, role and
  // scope included. Any UI-added biller can lack an email (the field is optional
  // until an invite is sent), so that took away the only reversible way to retire
  // one of them. The evidence used to be "the shipped default roster is five such
  // rows"; that roster is gone, the branch is not.
  const hasLoginEmail = Boolean(String(user.email || "").trim());
  const previousStatus = normalizeUserStatus(user.status);
  const accessChange = hasLoginEmail ? userAccessChangeFor(previousStatus, newStatus) : "";
  if (authoritativeProfileChanged && accessChange) {
    setWarning("Save the role/company change first, then change account status. Keeping those commands separate prevents a partial access update.");
    return;
  }
  if (authoritativeProfileChanged) {
    const profileResult = await sendUserProfileChangeRequest(user, role, scope, proposedPayers, proposedFollowUpPolicy);
    if (!profileResult.ok) {
      setWarning(profileResult.body?.message || "The portal server refused the access-profile change. Your draft is still here and nothing was presented as saved.");
      if (profileResult.status === 409) await refreshAuthoritativeUserGovernance({ force: true });
      return;
    }
    try {
      pendingReassignments.forEach(([claims, assignee]) => reassignClaims(claims, assignee));
    } catch (error) {
      setWarning("The access profile was changed and sessions were ended, but claim re-routing failed. Re-open Manage Users and repair the assignments.");
      throw error;
    }
    await refreshAuthoritativeUserGovernance({ force: true });
    renderUserMgmtList();
    showToast(`Saved. ${name}'s portal role and company access were updated, and their sessions were ended.`);
    return;
  }
  let accessResult = null;
  if (accessChange) {
    const actor = state?.authUserScope?.displayName || state?.authUserScope?.username || state?.activeUserName || "an administrator";
    accessResult = await sendUserAccessChangeRequest(user, accessChange, `${accessChange === "disable" ? "Disabled" : "Re-enabled"} from Manage Users by ${actor}`);
    if (!accessResult.ok) {
      setWarning(userAccessChangeFailureMessage(accessResult, accessChange, user));
      return;
    }
  }

  try {
    pendingReassignments.forEach(([claims, assignee]) => reassignClaims(claims, assignee));
  } catch (error) {
    // The server change has already landed. Say so rather than reject silently
    // and leave the roster showing the OLD status over the new reality.
    if (accessChange) setWarning(`${name} was ${accessChange === "disable" ? "disabled" : "re-enabled"} on the server, but re-routing their claims failed. Re-open Manage Users to see the current state.`);
    throw error;
  }
  // A person on BOTH lanes is disabled here and still holds a live users-JSON
  // bearer token. The toast says so, but a toast is gone in a couple of
  // seconds and "Disabled" stays on screen indefinitely — so the caveat is
  // stored on the row and rendered in the rail for as long as the pill is.
  if (accessChange === "disable") {
    user.staticLaneNotRevoked = accessResult?.body?.staticJsonLane?.coversStaticLane === false;
  } else if (accessChange === "enable") {
    user.staticLaneNotRevoked = false;
  }
  user.name = name;
  user.email = email;
  user.role = role;
  user.status = newStatus;
  user.orgIds = scope.orgIds;
  user.clientNames = scope.clientNames;
  // The book is offshore-only: payerNames on any other role would be a scope
  // blob the backend never reads and an admin could easily misread as a limit.
  user.payerNames = proposedPayers;
  user.followUpActionPolicy = proposedFollowUpPolicy;
  user.followUpActionAccess = sanitizeFollowUpActionAccessProjection(proposedFollowUpPolicy);
  // Only close the editor if this save is still the one on screen. The status
  // flip awaits the network, and an admin who moved on to edit someone else in
  // the meantime would otherwise have their draft silently discarded.
  if (userMgmtDirectoryState.editDraft === draft) {
    userMgmtDirectoryState.editing = false;
    userMgmtDirectoryState.editDraft = null;
  }
  if (user.directoryAuthoritative) {
    await refreshAuthoritativeUserGovernance({ force: true });
    renderUserMgmtList();
    if (accessChange && accessResult) showToast(userAccessChangeToast(name, accessChange, accessResult.body || {}));
    return;
  }
  persistCustomUsers();
  populateUserSelect();
  renderUserMgmtList();
  // A revocation reports what it revoked; the "now sees" line would be beside
  // the point for someone who can no longer sign in.
  if (accessChange && accessResult) {
    showToast(userAccessChangeToast(name, accessChange, accessResult.body || {}));
    return;
  }
  // A status change on a row with no login email is roster bookkeeping, and the
  // toast has to say which — silence here would read as a revocation.
  if (!hasLoginEmail && newStatus !== previousStatus) {
    showToast(`${name} is now ${userStatusLabel(newStatus).toLowerCase()} in this roster. They have no login email, so there was no portal account to change.`);
    return;
  }
  // Nothing reached the server on this save, so the toast is about the roster
  // only — and must not read as a revocation.
  const seesNow = userMgmtSeesLabel(user);
  showToast(`Saved. ${name} now sees ${seesNow.strong}${seesNow.small ? ` — ${seesNow.small.toLowerCase()}` : ""}.`);
}

function userMgmtScopeFromEditValues(values = [], role = "") {
  const scope = values.reduce((acc, value) => {
    const [clientPart = "", orgPart = ""] = String(value || "").split("|||");
    const orgIds = normalizeUserScopeList(orgPart);
    orgIds.forEach((orgId) => {
      if (!acc.orgIds.includes(orgId)) acc.orgIds.push(orgId);
    });
    // EVERY role stores the database orgId when the entry carries one --
    // never the display name beside it. canAccessClaim ORs stored
    // clientNames with orgIds, so a stored name would widen frontend access
    // beyond the orgIds the backend predicates enforce (confirmed live: a
    // stale clientNames entry survived a no-op save and granted cross-org
    // visibility). A clientName persists ONLY for legacy name-only entries
    // (no orgId), so saving never silently drops that access.
    if (orgIds.length) return acc;
    normalizeUserScopeList(clientPart).forEach((clientName) => {
      if (!acc.clientNames.includes(clientName)) acc.clientNames.push(clientName);
    });
    return acc;
  }, { orgIds: [], clientNames: [] });
  // An offshore company restriction stores orgIds ONLY. canAccessClaim ORs
  // clientNames with orgIds while bulk-assign routing and the backend
  // predicates check orgIds alone — a stored client name would widen the
  // frontend match beyond what the server enforces. Display labels come
  // from userMgmtOrgLabelMap, never from stored names.
  if (canonicalRole(role) === "OffshoreBiller") scope.clientNames = [];
  return scope;
}

function databaseOrgScopeOptions({ includeFeeRows = false } = {}) {
  const options = new Map();
  const add = (orgId, metadata = {}) => {
    const value = String(orgId || "").trim();
    if (!value || /^all$/i.test(value)) return;
    const existing = options.get(value) || { value, label: "Company name unavailable", orgId: value, clientName: "", orgName: "", portalOrgName: "" };
    const orgName = String(metadata.orgName || existing.orgName || "").trim();
    const clientName = String(metadata.clientName || existing.clientName || "").trim();
    // D3 S4: the portal roster's name is a THIRD field rather than another
    // writer of orgName. That makes "a name someone typed beats a name scraped
    // from claim data" a property of this list, not a property of the order two
    // callers happened to run in.
    const portalOrgName = String(metadata.portalOrgName || existing.portalOrgName || "").trim();
    const display = [portalOrgName, orgName, clientName].find((text) => text && text !== value) || "";
    options.set(value, {
      value,
      orgId: value,
      orgName,
      clientName,
      portalOrgName,
      label: display || "Company name unavailable"
    });
  };

  // The authenticated scoped inventory is awaited before the application boots
  // for every role. Include off-page companies; page rows only enrich names.
  const inventoryScope = currentAccessProfile().scope || {};
  const inventoryOrgIds = new Set((inventoryScope.orgIds || []).map(String));
  databaseTenantClientOptions().forEach(option => {
    (option.orgIds || []).forEach(orgId => {
      // An entry may group multiple org IDs under one company name. Selecting
      // that entry by any overlap must not expose its other organizations.
      if (inventoryScope.allOrgs || inventoryScope.crossTenant || inventoryOrgIds.has(String(orgId))) {
        add(orgId, { clientName: option.label || option.value });
      }
    });
  });
  // The management roster below also includes provisioned groups without claims.
  // Global All Data facets are partial summaries and never an org inventory.
  if (trackerApiPageState.status === "ok") {
    (trackerApiPageState.response?.rows || []).forEach((row) => {
      add(row.orgId || row.org_id, {
        clientName: row.client || row.tenant || row.tenantId || "",
        orgName: row.orgName || row.org_name || ""
      });
    });
  }
  sourceClaims.forEach((claim) => add(claim.orgId || claim.org_id, {
    clientName: claim.client || claim.tenant || "",
    orgName: claim.orgName || claim.org_name || ""
  }));
  if (includeFeeRows) {
    const feeRows = (typeof state !== "undefined" && Array.isArray(state?.feeScheduleRows))
      ? state.feeScheduleRows
      : (sourceFeeScheduleRows || []);
    // The bundled demo rates carry three fictional companies (RiverbendABA,
    // HarborPointABA, CedarLaneABA - org ids 9000000x). Against a live API they
    // are never a company anyone can pick, yet they were reaching every list
    // this function feeds: Notifications, Reporting lines, the user wizard and
    // the fee-schedule org picker on hosted TEST (2026-09-02 shell census).
    const liveApi = typeof trackerApiEnabled === "function" && trackerApiEnabled();
    feeRows.forEach((row) => {
      if (liveApi && ["Demo", "Local Seed"].includes(String(row.duplicateCheck || ""))) return;
      add(row.orgId || row.org_id, {
        clientName: row.tenantId || row.tenant_id || "",
        orgName: row.orgName || row.org_name || ""
      });
    });
  }
  // D3 S4 - UNION the portal's own client-group roster in. Every source above
  // this line is claim data, which is why a client group with no claims was
  // invisible to every picker in the product: the wizard, the edit rail and fee
  // schedules all read this one function.
  //
  // Deduped through the SAME add(), so `value === orgId` still holds. That is
  // not cosmetic: stored user scope is matched against this value, so a picker
  // whose value shape changed would silently re-point saved scope at nothing.
  portalOrgDirectoryState.orgs.forEach((org) => add(org.orgId, { portalOrgName: org.orgName }));
  return Array.from(options.values()).sort((a, b) => compareText(a.value, b.value));
}

// Two-letter monogram for directory avatars. Uses the first letter of the
// first two words, falling back to the first two characters of one word.
function userMgmtInitials(name = "") {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Per-client portal isolation status. A Client user should map to exactly
// ONE database org (one practice = one portal). More than one org — or
// all-org scope — means data from different clients could mix, which is
// never allowed for an external client login.
function userMgmtClientIsolation(user = {}) {
  if (!isExternalClientRole(user.role)) return null;
  const scope = accessProfileForUser(user).scope || {};
  const orgIds = normalizeUserScopeList(scope.orgIds || user.orgIds);
  const clientNames = normalizeUserScopeList(scope.clientNames || user.clientNames);
  const distinctOrgs = new Set(orgIds).size;
  const distinctClients = new Set(clientNames).size;
  if (scope.allOrgs) {
    return { ok: false, label: "All-org access", detail: "A client login must never see every org." };
  }
  if (!distinctOrgs && !distinctClients) {
    return { ok: false, label: "No portal assigned", detail: "Assign exactly one organization so this client gets an isolated portal." };
  }
  if (distinctOrgs > 1 || distinctClients > 1) {
    return { ok: false, label: "Mixed clients", detail: "This client login spans more than one organization. Split it into one login per organization." };
  }
  return { ok: true, label: "Isolated portal", detail: "Scoped to a single organization — no cross-client access." };
}

function compareText(left = "", right = "") {
  return String(left || "").localeCompare(String(right || ""), undefined, { sensitivity: "base" });
}

async function requestPeopleDirectoryExport() {
  if(!isTrustedUserManagerSession())return;
  const scope=preparedReportExportResetScope();if(!scope)return;
  const filters={search:userMgmtSearchQuery(),role:peopleDirectoryState().role || "",timeZone:Intl.DateTimeFormat().resolvedOptions().timeZone};
  const key=JSON.stringify({report:"peopleDirectory",scope,filters});
  if(preparedReportExportRequests.has(key))return;preparedReportExportRequests.add(key); renderPreparedReportExportStatus();
  try{
    const response=await fetch(portalUserDirectoryApiBase()+"/api/report-export-jobs",{method:"POST",cache:"no-store",headers:{"content-type":"application/json",authorization:"Bearer "+(workflowApiToken() || trackerApiToken())},body:JSON.stringify({report:"peopleDirectory",filters})});
    const body=await response.json().catch(()=>({}));if(preparedReportExportAccess()!==scope)return;
    if ([404, 501, 503].includes(response.status)) { await downloadPeopleDirectoryDirectCsv(filters, scope); return; }
    if(response.status!==202 || !(body.id || body.jobId))throw Error(body.message || "The complete directory export could not be queued.");
    const id=body.id || body.jobId,job={...body,id,scope,report:"peopleDirectory"};preparedReportExportJobs.set(id,job);
    while(preparedReportExportJobs.size>50)preparedReportExportJobs.delete(preparedReportExportJobs.keys().next().value);
    renderPreparedReportExportStatus();
    if(job.status==="completed")await downloadPreparedReportCsv(job);
    else{showToast("Your export is being prepared. You can keep working.");schedulePreparedReportExportPoll();}
  }catch(error){if(preparedReportExportAccess()===scope){showToast("We couldn’t create your download. Please try again.");restorePreparedReportExportJobs(true);}}
  finally{preparedReportExportRequests.delete(key); renderPreparedReportExportStatus();}
}

function exportUserMgmtDirectory() {
  if(portalUserDirectoryHostedMode()){requestPeopleDirectoryExport();return;}
  // The directory export contains every user's name/email/scope — silent
  // deny unless the session is a trusted Super Admin (same gate as the
  // other user-management actions).
  if (!isTrustedUserManagerSession()) return;
  const rows = userMgmtFilteredRows();
  // "Provenance" is its own column because STATUS stopped carrying it
  // (VOB_USERS_MODULE_SPEC 4.3 moved "Built-in" into the person cell as plain
  // text). Without it an access reviewer reading this export sees a built-in
  // demo account as an ordinary live login - the export over-reports access,
  // which is the direction that wastes an auditor's time rather than hiding
  // a risk, but it is still information this screen used to carry.
  const headers = ["Name", "Email", "Role", "Status", "Provenance", "Sees", "Org IDs", "Client Names", "Payers", "Last Login", "Last Seen", "Last Feature", "Last Action", "Activity Events", "Denied/Error Events", "Invite ID", "Invited At"];
  const orgLabels = userMgmtOrgLabelMap();
  const lines = [
    headers.map(csvEscape).join(","),
    ...rows.map(({ user }) => {
      const activity = userActivitySummaryForUser(user) || {};
      return [
      user.name || "",
      user.email || "",
      roleProfiles[canonicalRole(user.role)]?.label || canonicalRole(user.role),
      userMgmtStatusMeta(user).label,
      userMgmtProvenanceLabel(user) || "Provisioned",
      [userMgmtSeesLabel(user, orgLabels).strong, userMgmtSeesLabel(user, orgLabels).small].filter(Boolean).join(" — "),
      normalizeUserScopeList(user.orgIds).join("; "),
      normalizeUserScopeList(user.clientNames).join("; "),
      normalizeUserScopeList(user.payerNames).join("; "),
      activity.lastLoginAtUtc ? formatDateTime(activity.lastLoginAtUtc) : "",
      activity.lastSeenAtUtc ? formatDateTime(activity.lastSeenAtUtc) : "",
      activity.lastFeatureKey || "",
      activity.lastActionKey || "",
      Number(activity.totalEventCount || 0),
      Number(activity.deniedEventCount || 0) + Number(activity.errorEventCount || 0),
      user.inviteId || "",
      user.invitedAt ? formatDateTime(user.invitedAt) : ""
      ].map(csvEscape).join(",");
    })
  ];
  downloadTextPayload(lines.join("\n"), `tms-access-directory-${localDateString()}.csv`, "text/csv");
  recordUserActivity({ featureKey: "users", actionKey: "activity_summary_export", route: "manage-users", metadata: { rowCount: rows.length } });
  showToast(`Exported ${formatCount(rows.length)} user${rows.length === 1 ? "" : "s"}.`);
}

function csvEscape(value) {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function completeInlineExportRows(body = {}) {
  if (body.complete !== true || body.truncated === true) return null;
  if (Array.isArray(body.exportRows)) return body.exportRows;
  return Array.isArray(body.rows) ? body.rows : null;
}

function reportExportCell(value) {
  if (value === null || value === undefined) return "";
  return typeof value === "object" ? JSON.stringify(value) : value;
}

function downloadCompleteInlineCsv(body = {}, { filename = "tms-report.csv", emptyMessage = "No rows match the current filters." } = {}) {
  const rows = completeInlineExportRows(body);
  if (!rows) throw new Error(body.message || "The complete export response was unavailable.");
  if (!rows.length) { showToast(emptyMessage); return 0; }
  const columns = [...new Set(rows.flatMap((row) => Object.keys(row && typeof row === "object" ? row : {})))];
  if (!columns.length) throw new Error("The complete export did not contain usable columns.");
  const lines = [columns.map(csvEscape).join(","), ...rows.map((row) => columns.map((column) => csvEscape(reportExportCell(row[column]))).join(","))];
  downloadTextPayload(lines.join("\r\n"), filename, "text/csv;charset=utf-8");
  return rows.length;
}

async function downloadPeopleDirectoryDirectCsv(filters = {}, scope = "") {
  const pageSize = 200, collected = [], seen = new Set();
  let expectedTotal = null, page = 1;
  while (true) {
    const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize), search: filters.search || "", role: filters.role || "" });
    const response = await fetch(portalUserDirectoryApiBase() + "/api/users/directory?" + query, { cache: "no-store", headers: { accept: "application/json", authorization: "Bearer " + (workflowApiToken() || trackerApiToken()) } });
    const body = await response.json().catch(() => ({}));
    if (scope !== preparedReportExportAccess()) return 0;
    if (!response.ok || !Array.isArray(body.users)) throw new Error(body.message || "The complete directory export is unavailable.");
    const total = Number(body.page?.totalRows ?? body.count);
    if (!Number.isSafeInteger(total) || total < 0) throw new Error("The directory did not provide a complete row count.");
    if (expectedTotal === null) expectedTotal = total;
    else if (total !== expectedTotal) throw new Error("The directory changed during export. Export again for one consistent version.");
    for (const row of body.users) {
      const mapped = mapPortalUserDirectoryRow(row), key = userMgmtPersonKey(mapped);
      if (!key || seen.has(key)) throw new Error("The directory changed during export. Export again for one consistent version.");
      seen.add(key); collected.push(mapped);
    }
    if (collected.length >= expectedTotal) break;
    if (!body.users.length || page >= Math.max(1, Math.ceil(expectedTotal / pageSize))) throw new Error("The complete directory export stopped before every person was read.");
    page += 1;
  }
  if (collected.length !== expectedTotal) throw new Error("The directory changed during export. Export again for one consistent version.");
  if (!collected.length) { showToast("No people match the current filters."); return 0; }
  const headers = ["Name", "Email", "Role", "Status", "Provenance", "Org IDs", "Payers", "Last sign-in", "Invite ID", "Invited at"];
  const lines = [headers.map(csvEscape).join(","), ...collected.map((user) => [
    user.name, user.email, roleProfiles[canonicalRole(user.role)]?.label || canonicalRole(user.role), userMgmtStatusMeta(user).label,
    userMgmtProvenanceLabel(user) || "Provisioned", normalizeUserScopeList(user.orgIds).join("; "), normalizeUserScopeList(user.payerNames).join("; "),
    user.lastSignInAtUtc || "", user.inviteId || "", user.invitedAt || ""
  ].map(csvEscape).join(","))];
  downloadTextPayload(lines.join("\r\n"), `tms-access-directory-${localDateString()}.csv`, "text/csv;charset=utf-8");
  recordUserActivity({ featureKey: "users", actionKey: "activity_summary_export", route: "manage-users", metadata: { rowCount: collected.length, delivery: "direct-paged" } });
  showToast(`Exported ${formatCount(collected.length)} user${collected.length === 1 ? "" : "s"}.`);
  return collected.length;
}

function userMgmtRoleCounts(userRows = []) {
  return userRows.reduce((counts, user) => {
    const role = canonicalRole(user.role);
    counts[role] = (counts[role] || 0) + 1;
    return counts;
  }, {});
}

function userMgmtTenantClientDatabaseOptions() {
  // Built from the merged org label map (loaded data + server roster), so
  // the wizard and edit checklists list every known company by real name.
  const orgLabels = userMgmtOrgLabelMap();
  const displayOrgLabels = userMgmtDisplayOrgLabelMap(orgLabels);
  return Array.from(orgLabels.entries())
    .map(([orgId, name]) => {
      const clientName = name && name !== orgId ? name : "";
      return {
        value: `${clientName || orgId}|||${orgId}`,
        label: clientName || displayOrgLabels.get(orgId) || "Company name unavailable",
        clientName,
        orgIds: [orgId]
      };
    })
    .sort((a, b) => compareText(a.clientName || a.label, b.clientName || b.label));
}

function portalInviteApiBase() {
  return (workflowApiBase() || trackerApiBase() || "").replace(/\/$/, "");
}

function portalInvitePayloadFromDraft(draft = {}) {
  const role = canonicalRole(draft.role);
  const scope = accessProfileForUser(draft).scope || {};
  const orgIds = normalizeUserScopeList(scope.orgIds || draft.orgIds);
  const clientNames = normalizeUserScopeList(scope.clientNames || draft.clientNames);
  return {
    displayName: draft.name,
    email: draft.email,
    role,
    ...(role === "OffshoreBiller" && draft.team ? { team: draft.team } : {}),
    status: "pending_invite",
    tenantId: clientNames[0] || orgIds[0] || (scope.allOrgs ? "all-orgs" : role),
    orgId: orgIds[0] || (scope.allOrgs ? "all-orgs" : role),
    scope: {
      allOrgs: Boolean(scope.allOrgs),
      orgIds,
      clientNames,
      followUpActionPolicy: isExternalClientRole(role)
        ? followUpActionPolicyPreset(role, "hidden")
        : followUpActionPolicyForRole(draft.followUpActionPolicy || draft.followUpActionAccess || {}, role)
    }
  };
}

function portalInviteEvidenceFromResponse(body = {}) {
  const invite = body.invite || {};
  const delivery = body.delivery || {};
  return {
    inviteId: String(invite.inviteId || invite.invite_id || ""),
    inviteProvider: String(delivery.provider || invite.emailProvider || ""),
    inviteMessageId: String(delivery.messageId || invite.emailMessageId || ""),
    invitedAt: String(delivery.sentAtUtc || invite.sentAtUtc || timestamp())
  };
}

// ---------------------------------------------------------------------------
// Step-by-step "Add a user" wizard
// ---------------------------------------------------------------------------
// The guided alternative to the dense invite form: a full-screen flow that
// asks exactly one question per screen in plain language — who you're adding
// (biller / client company / your own team), what they can see, who they are --
// and ends on a human-readable review before anything is created. Billers are
// created active and assignable immediately; client and team users go through
// the same secure-invite pipeline as the inspector form.

let userAddWizardState = null;
// Where focus was when the wizard opened. Leaving puts it back there, which is
// NOT always #userMgmtNewInviteBtn: the same wizard opens from Manage Groups
// (openUserAddWizardForOrganization), and the hard-coded Manage Users button
// dropped focus on the floor on that path.
let userAddWizardReturnFocus = null;
// True when THIS wizard, rather than the Manage Users modal underneath it, put
// the page into its locked/inert state. Only whoever locked it may release it,
// or closing the wizard would unlock a modal that is still open.
let userAddWizardOwnsBackgroundLock = false;
// The signature of the draft as the flow OPENED, so "has anything been
// entered?" means changed-by-this-admin rather than non-empty.
let userAddWizardOpenedSignature = "";

// NOTHING IS PRE-GRANTED. The header company used to arrive here already
// selected, which made the company an admin happened to be looking at the
// company a new account was created under unless they noticed and undid it.
// A grant is an act, not a side effect of a view: the header company is now
// sorted first and labelled "Currently viewing" (userAddWizardCompanyOptions),
// and it is still the admin who picks it.
function blankUserAddWizardState() {
  return {
    stepIndex: 0,
    type: "",            // "biller" | "client" | "team"
    level: "",           // team only: "StatesideBiller" | "Admin" | "SuperAdmin"
    company: "",         // client only: composite company value
    companyLabel: "",
    companies: [],        // stateside only: composite values
    allCompanies: false,
    companySearch: "",
    clientAccess: "",     // client only: "owner" -> Client | "billing" -> ClientBiller
    payerBook: [],        // offshore only: scope.payerNames — the BOOK
    payerSearch: "",
    followUpActionPolicy: null,
    followUpPolicySearch: "",
    followUpPolicyNotice: "",
    followUpPolicyExpanded: false,
    name: "",
    email: "",
    sending: false,
    error: "",
    done: false,
    inviteSent: false,
    inviteEmailFailed: false
  };
}

// The teams an offshore biller can be on; the keys are Org Map locations.
const USER_ADD_WIZARD_BILLER_TEAMS = Object.freeze([["india", "India"], ["colombia", "Colombia"]]);

// The screen sequence adapts to the answer on screen one, so a biller is two
// questions and done, while scoped invites earn their extra step.
function userAddWizardFlow(s = userAddWizardState || {}) {
  // "clientAccess" is the PHI split (Client = statements; ClientBiller = the
  // claim-level grid). It is a full screen rather than a dropdown row because
  // the two roles differ by who outside TMS can read patient-level detail, and
  // that is not a choice to bury in a select.
  if (s.type === "client") return ["type", "company", "clientAccess", "person", "review"];
  if (s.type === "team") {
    return s.level === "SuperAdmin"
      ? ["type", "level", "person", "actions", "review"]
      : ["type", "level", "companies", "person", "actions", "review"];
  }
  // Offshore billers are company-scoped. The actions screen uses the same
  // server-enforced per-action policy as the People editor, so the invite review
  // states exactly which follow-up work the new person may perform.
  if (s.type === "biller") return ["type", "team", "name", "companies", "actions", "review"];
  return ["type", "details", "review"];
}

// `returnFocus` exists for the Manage Groups entry point, which closes its own
// modal before opening this one: by then document.activeElement is gone, and
// the Manage Users button this used to fall back to is not on that path at all.
// Safe to call as a plain click listener — an Event carries no returnFocus.
function openUserAddWizard(options = {}) {
  if (!isTrustedUserManagerSession()) {
    showToast("You do not have permission to manage users.");
    return;
  }
  const wizard = document.getElementById("userAddWizard");
  if (!wizard) return;
  if (wizard.parentElement !== document.body) document.body.appendChild(wizard);
  const requestedFocus = options && options.returnFocus && typeof options.returnFocus.focus === "function" ? options.returnFocus : null;
  userAddWizardReturnFocus = requestedFocus || document.activeElement;
  userAddWizardState = blankUserAddWizardState();
  userAddWizardOpenedSignature = userAddWizardInputSignature(userAddWizardState);
  userAddWizardInviteRequestKey = "";
  wizard.classList.remove("hidden");
  // Manage Users normally owns the background lock already. Opened from
  // anywhere else nothing did, and the page behind a full-screen wizard stayed
  // scrollable and reachable by Tab. Whoever locks it is the one who releases it.
  if (!document.documentElement.classList.contains("user-mgmt-modal-open")) {
    setUserMgmtBackgroundLocked(true);
    userAddWizardOwnsBackgroundLock = true;
  }
  // A throw in the first render must not leave an inert page with no dialog on
  // it — the one failure here a user cannot recover from. Nothing has been
  // entered yet, so unwinding costs nothing.
  try {
    renderUserAddWizard();
  } catch (error) {
    closeUserAddWizard();
    throw error;
  }
  // The invite scope is a tenant boundary. Load the authoritative, server-scoped
  // company directory when this flow opens instead of depending on another
  // screen having warmed it first. An unavailable directory stays unavailable;
  // the wizard never substitutes an unverified free-text tenant name.
  try { requestPortalOrgDirectory({ force: portalOrgDirectoryState.status !== "ok" || !portalOrgDirectoryState.orgs.length }); } catch (_) {}
}

// "Has this person typed anything?" Choosing WHO you are adding is not input —
// it is the first question, and confirming it would put a dialog in front of an
// admin who has done nothing. A name, an email, a company or a payer is.
//
// Compared against the draft as the flow OPENED, not against empty. The Manage
// Groups entry point opens this wizard with a company already chosen, and an
// admin who has touched nothing must not be asked to discard a choice the
// portal made for them.
function userAddWizardInputSignature(s = {}) {
  return JSON.stringify([
    String(s.name || "").trim(),
    String(s.email || "").trim(),
    String(s.company || ""),
    Boolean(s.allCompanies),
    [...(s.companies || [])].map(String).sort(),
    normalizeUserScopeList(s.payerBook).slice().sort()
  ]);
}

function userAddWizardHasInput(s = userAddWizardState) {
  if (!s || s.done) return false;
  const opened = userAddWizardOpenedSignature || userAddWizardInputSignature({});
  return userAddWizardInputSignature(s) !== opened;
}

// THE ONE WAY OUT.
//
// Cancel, the close button, Escape, Back on the first screen and the browser's
// own Back all arrive here, so "how do I get out of this?" has ONE answer and
// ONE confirmation rather than five behaviours — and so no exit path can be
// added later that forgets to ask before discarding a half-entered person.
//
// Nothing is created and no invitation is sent on any of them. The only path
// that creates anything is confirmUserAddWizard, from the review screen.
function requestUserAddWizardExit({ viaPopstate = false } = {}) {
  const s = userAddWizardState;
  if (!s) return true;
  // ONE refusal path, so no reason for staying can forget the repair. By the
  // time a popstate asks, the browser has ALREADY consumed entries; whatever
  // we decide, the ladder is rebuilt to match the screen before we return.
  // Skipping this after a declined discard is what let a later Cancel unwind
  // further than there were entries and walk the admin off the portal.
  const stay = () => {
    if (viaPopstate) syncUserAddWizardHistory(s);
    return false;
  };
  // A create is in flight. Leaving now would strand a request whose result
  // decides whether an account exists; the review screen already says "Working…".
  if (s.sending) return stay();
  if (userAddWizardHasInput(s) && typeof confirm === "function"
    && !confirm("Discard this new user?\n\nNothing has been created and no invitation has been sent. What you entered will be lost.")) {
    return stay();
  }
  closeUserAddWizard();
  return true;
}

function closeUserAddWizard() {
  const returnTarget = userAddWizardReturnFocus;
  // Read the truth from history itself BEFORE tearing down, rather than from a
  // tally that can be one ahead of reality.
  const depth = userAddWizardHistoryEntriesAboveBase();
  userAddWizardState = null;
  userAddWizardInviteRequestKey = "";
  userAddWizardReturnFocus = null;
  userAddWizardOpenedSignature = "";
  try {
    document.getElementById("userAddWizard")?.classList.add("hidden");
  } finally {
    // In a finally, because an inert page with no dialog on it is the one
    // failure state here that a user cannot click their way out of.
    if (userAddWizardOwnsBackgroundLock) {
      userAddWizardOwnsBackgroundLock = false;
      setUserMgmtBackgroundLocked(false);
    }
  }
  // Drop this wizard's own history entries, so the portal's Back button behaves
  // afterwards exactly as it did before the wizard opened. `depth` is what the
  // current entry SAYS, so a popstate that already landed on the base page
  // reports 0 and this cannot overshoot.
  if (depth > 0 && window.history && typeof window.history.go === "function") {
    try { window.history.go(-depth); } catch (_) {}
  }
  // Focus goes to the first control that is genuinely ON SCREEN. The captured
  // opener comes first, but the Manage Groups path closes its own modal before
  // opening this wizard, so that button is hidden by the time we hand focus
  // back — and calling focus() on it silently drops focus onto the body, which
  // is where a keyboard user loses their place entirely.
  const onScreen = (node) => Boolean(node)
    && node !== document.body
    && node.isConnected !== false
    && typeof node.focus === "function"
    && (typeof node.getClientRects !== "function" || node.getClientRects().length > 0)
    && !(typeof node.closest === "function" && node.closest(".hidden"));
  const focusTarget = [
    returnTarget,
    document.getElementById("userMgmtNewInviteBtn"),
    document.getElementById("manageUsersBtn")
  ].find(onScreen);
  focusTarget?.focus?.({ preventScroll: true });
}

// HISTORY IS READ, NEVER COUNTED.
//
// Each entry this wizard pushes carries its own step index, so "how many of my
// entries are above the page I started on?" is answered by the CURRENT entry
// rather than by a tally. The first version kept a tally, and a tally drifts:
// a declined discard after the browser's Back pushed ONE entry back while the
// tally had already been told about three, so the next Cancel ran
// history.go(-2) with one entry to unwind and left the admin on about:blank —
// the very defect these exits exist to prevent. Nothing below keeps a total.
function userAddWizardHistoryEntriesAboveBase() {
  const state = window.history ? window.history.state : null;
  const step = state && typeof state.tmsUserAddWizardStep === "number" ? state.tmsUserAddWizardStep : null;
  return step !== null && step >= 0 ? step + 1 : 0;
}

// One history entry per step, so the browser's Back button moves back A STEP
// and then leaves — rather than leaving the portal out from under a wizard that
// is still on screen, which is what "back, back, back doesn't take me back" was.
function userAddWizardPushHistoryStep(stepIndex) {
  if (!window.history || typeof window.history.pushState !== "function") return;
  try {
    window.history.pushState({ ...(window.history.state || {}), tmsUserAddWizardStep: stepIndex }, "", window.location.href);
  } catch (_) {}
}

// Brings the ladder up to "one entry per step shown" FROM WHEREVER IT ACTUALLY
// IS. That makes it both the forward push on render and the repair after a
// refused exit, which are the same job: make history describe the screen.
function syncUserAddWizardHistory(s) {
  if (!s || s.done) return;
  let guard = 0;
  while (userAddWizardHistoryEntriesAboveBase() < s.stepIndex + 1 && guard < 24) {
    userAddWizardPushHistoryStep(userAddWizardHistoryEntriesAboveBase());
    guard += 1;
  }
}

// Starting over reuses the open wizard, so the finished flow's entries have to
// go with it — otherwise Back would walk an admin forward through the steps of
// somebody who has already been created.
function userAddWizardUnwindHistoryToStart() {
  const extra = userAddWizardHistoryEntriesAboveBase() - 1;
  if (extra > 0 && window.history && typeof window.history.go === "function") {
    try { window.history.go(-extra); } catch (_) {}
  }
}

// Returns true when the wizard consumed the popstate, so the portal's own
// route handler does not also repaint underneath a dialog that is closing.
function handleUserAddWizardPopstate(event) {
  const s = userAddWizardState;
  if (!s) return false;
  // A CREATE IS IN FLIGHT. The review screen is the only honest screen to be on
  // while it is: moving off it put the server's refusal on an unrelated question
  // ("How should their follow-up work be approved?") and left the admin able to
  // walk forward and submit the same draft a second time from a screen that had
  // never asked. Put the consumed entry back and stay put.
  if (s.sending) {
    syncUserAddWizardHistory(s);
    return true;
  }
  const state = event && event.state;
  const step = state && typeof state.tmsUserAddWizardStep === "number" ? state.tmsUserAddWizardStep : null;
  if (step === null || s.done) {
    // Backed out past the wizard's first entry, or off a finished flow: leave to
    // Manage Users. Never out of the portal.
    requestUserAddWizardExit({ viaPopstate: true });
    return true;
  }
  if (step !== s.stepIndex) {
    s.error = "";
    s.stepIndex = step;
    renderUserAddWizard();
  }
  return true;
}

function userAddWizardBack() {
  const s = userAddWizardState;
  if (!s || s.sending || s.done) return;
  // THE FIRST SCREEN IS NOT A DEAD END. Back used to be hidden here and do
  // nothing. The footer control is now hidden on screen one because the top bar
  // carries the labelled exit and two controls called "Cancel" is one too many —
  // but the BEHAVIOUR stays, so a keyboard or programmatic Back still gets out.
  if (s.stepIndex <= 0) {
    requestUserAddWizardExit();
    return;
  }
  // Let history do the moving, so the wizard's Back and the browser's Back are
  // the same movement and cannot leave orphan entries behind each other.
  if (userAddWizardHistoryEntriesAboveBase() > 1 && window.history && typeof window.history.back === "function") {
    window.history.back();
    return;
  }
  s.error = "";
  s.stepIndex -= 1;
  renderUserAddWizard();
}

// THE GRANT LIST IS NOT THE VIEW.
//
// Which companies an admin may hand to a NEW user is a property of that
// admin's own authority, never of the company they happen to be looking at.
// For one release (df8db0bf) the header company filtered this list, which is
// how "add a biller" came to offer exactly one company out of twenty. The
// rule that change was made under (COMPANY_HEADER_SCOPE.md) describes itself
// as "a display/request filter inside the authenticated scope; it never grants
// access" and names new-user forms as DEFAULTS. A default is not a cap, so the
// header comes off the pickers that decide what to GRANT and stays on the
// lists of existing people and work, which really are "what am I looking at".
//
// What narrows this list instead is the only boundary that can refuse:
// authorizeUserTarget (backend/userGovernancePolicy.js) rejects any target org
// outside the acting admin's own orgIds, and createInvite re-checks the same
// ids against the server-scoped org directory. Offering more than that would
// be offering a choice the server is about to throw away.
function userAddWizardGrantableOrgIds() {
  // Read the actor exactly the way userManagerCanAssignScope does. If these
  // two ever disagreed, the wizard would offer a company its own submit gate
  // refuses - so they read the same thing, on purpose.
  const actorRole = canonicalRole(state?.authUserScope?.role || state?.role);
  if (actorRole === "SuperAdmin") return null; // null: no narrowing, not "none"
  return normalizeUserScopeList((currentAccessProfile().scope || {}).orgIds);
}

// THE GRANTABLE SET IS THE ORG DIRECTORY — NOT THE LABEL MAP.
//
// userMgmtTenantClientDatabaseOptions() unions three sources: tracker facets,
// claim rows and the bundled fee-schedule seed. So it carries org ids that are
// not portal organizations at all (the seed alone contributes three), and ones
// the directory holds as provisioning rather than active. Offering those is not
// cosmetic: createInvite re-resolves every requested id through
// orgDirectory.listOrgs and refuses an unknown or inactive one with
// target_org_not_found — at the LAST step, after the person has been named.
// This picker therefore offers exactly the set that gate will accept.
//
// null means "not known yet", never "everything". Callers turn that into a
// loading or an unavailable screen rather than into a list.
function userAddWizardDirectoryOrgIds() {
  // A re-read under the SAME key deliberately keeps the rows it already has
  // (requestPortalOrgDirectory), so treating "pending" as "nothing known"
  // throws away a roster that is still on the screen. On the review step that
  // was not cosmetic: mid-refresh, every company on the draft counted as
  // unnamed and a perfectly good pick was refused. A refresh is not a loss.
  const rows = portalOrgDirectoryState.orgs || [];
  const refreshing = portalOrgDirectoryState.status === "pending" && rows.length > 0;
  if (portalOrgDirectoryState.status !== "ok" && !refreshing) return null;
  return new Set(rows
    .map((org) => String((org && org.orgId) || "").trim())
    .filter(Boolean));
}

function userAddWizardDirectoryCompanies() {
  const directory = userAddWizardDirectoryOrgIds();
  if (!directory) return [];
  const grantable = userAddWizardGrantableOrgIds();
  return userMgmtTenantClientDatabaseOptions().filter((option) =>
    (option.orgIds || []).some((orgId) => directory.has(String(orgId)))
    && (!grantable || (option.orgIds || []).some((orgId) => grantable.includes(String(orgId)))));
}

// Directory organizations nobody has named yet. NEVER offered — the owner's
// rule is that a selectable row always carries a real name — but counted, so
// the screen can say why the list is shorter than the directory instead of
// quietly dropping a company.
function userAddWizardUnnamedGrantableCount() {
  return userAddWizardDirectoryCompanies().filter((option) => !option.clientName).length;
}

function userAddWizardCompanyOptions() {
  const viewing = activeClientGroupTenant() ? activeClientGroupOrgIds().map((orgId) => String(orgId)) : [];
  const options = userAddWizardDirectoryCompanies()
    .filter((option) => Boolean(option.clientName))
    .map((option) => ({
      value: option.value,
      label: option.clientName,
      // Keep the database key available for exact search and payload scope, but
      // never print it under the company name. A manager is choosing a company,
      // not validating an internal join key.
      searchText: (option.orgIds || [])[0] || "",
      orgIds: option.orgIds || [],
      // Always true now, and kept as an explicit flag rather than dropped: the
      // shared picker also serves the Manage Users edit rail, whose options carry
      // no such flag, and the row note keys on `named === false` exactly.
      named: true,
      // Helpful, not limiting. The header company sorts first and says so; every
      // other company the admin may grant is still right there under it.
      viewing: viewing.length > 0 && (option.orgIds || []).some((orgId) => viewing.includes(String(orgId)))
    }));
  return [...options.filter((option) => option.viewing), ...options.filter((option) => !option.viewing)];
}

// ONE answer to "what should this screen show?", because three callers ask and
// they have to agree: the single-company step, the multi-company step and the
// submit gate.
//
//   "loading"      the roster read is in flight
//   "unavailable"  it failed, or there is no roster route at all — retry,
//                  never a list guessed from claim data
//   "empty"        the roster answered and this admin may grant nothing
//   "ready"        a list, every row of it carrying a real name
function userAddWizardCompanyListState() {
  // Loading means there is nothing to show yet. A re-read that still holds
  // the previous rows is a refresh, and blanking a list the admin is reading
  // mid-scroll would be a worse answer than the slightly stale one.
  const refreshing = portalOrgDirectoryState.status === "pending" && (portalOrgDirectoryState.orgs || []).length > 0;
  if (portalOrgDirectoryState.status === "pending" && !refreshing) return "loading";
  if (portalOrgDirectoryState.status !== "ok" && !refreshing) return "unavailable";
  return userAddWizardCompanyOptions().length ? "ready" : "empty";
}

// The body of both company screens, so "loading", "unavailable", "empty" and
// "ready" cannot mean one thing on the single-company screen and another on the
// multi-company one — which is exactly how the multi-company screen ended up
// with no loading branch at all.
function userAddWizardCompanyScreenBodyHtml(s, { multi = false } = {}) {
  const listState = userAddWizardCompanyListState();
  if (listState === "loading") return userAddWizardCompanyLoadingHtml();
  if (listState === "unavailable") {
    const detail = String(portalOrgDirectoryState.error || "").trim();
    return `<div class="user-wizard-empty">The company list could not be loaded, so there is nothing safe to choose from yet.${detail ? `<br><small>${escapeHtml(detail)}</small>` : ""}</div>
      <button class="ghost-button" type="button" data-wizard-company-retry>Retry company list</button>`;
  }
  if (listState === "empty") {
    const unnamed = userAddWizardUnnamedGrantableCount();
    return `<div class="user-wizard-empty">${unnamed
      ? "No company you can grant has a name yet. Name them under Manage Groups first — a company nobody can identify is not one anybody should be granted."
      : "No company is available for you to grant. Add the client company under Manage Groups first."}</div>`;
  }
  const list = multi
    ? renderScopeCompanyPicker(userAddWizardCompaniesPickerConfig(s))
    : `<div id="userAddWizardCompanyList" class="user-wizard-options">${userAddWizardCompanyListHtml(s)}</div>`;
  return `${list}${userAddWizardUnnamedCompanyNoticeHtml()}`;
}

// A company the directory holds but nobody has named is NOT hidden silently.
// It is not offered, and the screen says why, so a shorter list than expected
// reads as a naming job rather than as missing access.
function userAddWizardUnnamedCompanyNoticeHtml() {
  const count = userAddWizardUnnamedGrantableCount();
  if (!count) return "";
  return `<p class="user-wizard-unnamed-note">${count === 1
    ? "One company in your directory has no name yet, so it cannot be granted. Name it under Manage Groups first."
    : `${escapeHtml(formatCount(count))} companies in your directory have no name yet, so they cannot be granted. Name them under Manage Groups first.`}</p>`;
}

function userAddWizardCompanyLoadingHtml() {
  return `<div class="user-wizard-loading" role="status" aria-live="polite">
      <span class="user-wizard-loading-row" aria-hidden="true"></span>
      <span class="user-wizard-loading-row" aria-hidden="true"></span>
      <span class="user-wizard-loading-row" aria-hidden="true"></span>
      <span class="user-wizard-loading-text">Loading companies…</span>
    </div>`;
}

// One line under a company name, in words rather than data: the company the
// header is on says so, and a company the roster could not name explains what
// that means instead of leaving an admin to decode "unavailable 2". Never an
// org id - the id stays searchable, not printed (PORTAL_DIRECTIVES.md).
function userAddWizardCompanyOptionNoteHtml(option = {}) {
  if (option.viewing) return `<small class="user-wizard-option-note">Currently viewing</small>`;
  if (option.named === false) return `<small class="user-wizard-option-note">Not named yet — an administrator can name it under Manage Groups. Picking it still grants the right company.</small>`;
  return "";
}

// Companies are stored as orgIds (userAddWizardScopeFromValues is orgIds-first),
// but the admin picked a NAME. The review screen is the last sanity check before
// an account exists, and "At: 90000005" is not something anyone can sanity-check;
// the standing rule in this product is names, never IDs. Falls back to the raw id
// only when the option list cannot resolve it, so an unknown org still shows
// SOMETHING rather than silently vanishing from the summary.
function userAddWizardCompanyLabels(draft = {}) {
  const names = normalizeUserScopeList(draft.clientNames);
  if (names.length) return names;
  // EVERY orgId of each option, not just the first: an option covering several
  // orgs would otherwise resolve one name and leave the rest as raw IDs.
  const byOrgId = new Map();
  userAddWizardCompanyOptions().forEach((option) => {
    normalizeUserScopeList(option.orgIds && option.orgIds.length ? option.orgIds : option.detail)
      .forEach((orgId) => byOrgId.set(String(orgId), option.label));
  });
  // An id is NEVER a label (standing owner rule), so an org the picker cannot
  // name is dropped here rather than printed as a number. How many were
  // dropped is answered by userAddWizardUnnamedDraftOrgIds, and every screen
  // that names companies says the same sentence about them.
  return normalizeUserScopeList(draft.orgIds)
    .map((orgId) => byOrgId.get(String(orgId)) || "")
    .filter(Boolean);
}

// The review screen's company line. Three names and an ellipsis is fine for a
// handful; it is NOT fine for every company in the business, which is what one
// tap on "Select all" now means across twenty of them. Say that out loud here,
// on the last screen before the account exists, rather than letting the widest
// grant in the product look like the three companies that sort first.
// ONE wording for "the picker cannot name this company", used by the chip, by
// the review sentence above it and by the every-company list. They sat on the
// same screen saying different things about the same org — the sentence naming
// it by its id while the chip said it had no name.
const USER_ADD_WIZARD_UNNAMED_COMPANY_TEXT = "a company with no name on file — name it under Manage Groups first";

function userAddWizardCompanyScopeSummary(names = [], { unresolved = 0 } = {}) {
  // An id the picker could not name is NOT summarised as if it were a company.
  if (unresolved) return USER_ADD_WIZARD_UNNAMED_COMPANY_TEXT;
  const list = names.filter(Boolean);
  if (!list.length) return "nothing — pick a company";
  const total = userAddWizardCompanyOptions().length;
  if (total > 1 && list.length >= total) return `every company — all ${formatCount(list.length)}`;
  if (list.length <= 3) return escapeHtml(list.join(", "));
  return `${escapeHtml(list.slice(0, 3).join(", "))}, +${formatCount(list.length - 3)} more`;
}

// Companies on the draft that the picker cannot name. After the directory gate
// this should be empty by construction; it is checked anyway because the review
// screen is the last place a POSITIONAL placeholder could be read as an answer —
// "Company name unavailable 1" is not even stable between loads, which is how
// the same company was "1" on one render and "2" on the next.
function userAddWizardUnnamedDraftOrgIds(draft = {}) {
  const named = new Set();
  userAddWizardCompanyOptions().forEach((option) => {
    (option.orgIds || []).forEach((orgId) => named.add(String(orgId)));
  });
  return normalizeUserScopeList(draft.orgIds).filter((orgId) => !named.has(String(orgId)));
}

// True when this draft grants EVERY company the admin can grant. The widest
// grant in the product, and the one place in this flow that earns friction.
function userAddWizardGrantsEveryCompany(draft = {}) {
  const total = userAddWizardCompanyOptions().length;
  if (total < 2) return false;
  return normalizeUserScopeList(draft.orgIds).length >= total;
}

function userAddWizardScopeFromValues(values = []) {
  return values.reduce((scope, value) => {
    const [clientPart = "", orgPart = ""] = String(value || "").split("|||");
    const orgIds = normalizeUserScopeList(orgPart);
    orgIds.forEach((orgId) => {
      if (!scope.orgIds.includes(orgId)) scope.orgIds.push(orgId);
    });
    // orgIds-first, names only for legacy name-only entries — mirrors
    // userMgmtScopeFromEditValues so wizard adds and edit saves agree.
    if (orgIds.length) return scope;
    normalizeUserScopeList(clientPart).forEach((clientName) => {
      if (!scope.clientNames.includes(clientName)) scope.clientNames.push(clientName);
    });
    return scope;
  }, { orgIds: [], clientNames: [] });
}

function userAddWizardDraft() {
  const s = userAddWizardState || {};
  const name = String(s.name || "").trim().replace(/\s+/g, " ");
  const email = String(s.email || "").trim();
  if (s.type === "biller") {
    // Offshore billers are org-scoped like stateside billers: "All companies"
    // pins every company explicitly. An empty list is NOT "everything" — it is
    // deny-by-default, so it must never be what we save.
    const billerValues = s.allCompanies
      ? userAddWizardCompanyOptions().map((option) => option.value)
      : (s.companies || []);
    const scope = userAddWizardScopeFromValues(billerValues);
    return { name, email, team: s.team || "", role: "OffshoreBiller", orgIds: scope.orgIds, clientNames: scope.clientNames, payerNames: [], followUpActionPolicy: followUpActionPolicyForRole(s.followUpActionPolicy || {}, "OffshoreBiller"), status: "pending_invite" };
  }
  if (s.type === "client") {
    const scope = userAddWizardScopeFromValues(s.company ? [s.company] : []);
    // THE PHI SPLIT. "billing" is ClientBiller, which carries client:workbench
    // and therefore the claim-level grid; "owner" is Client, statements only.
    //
    // Anything other than an explicit "billing" resolves to the NARROWER role,
    // so an unanswered or half-answered screen can never be the reason somebody
    // outside TMS was handed patient-level detail. Least privilege has to be
    // what falls out of the default, not what an admin remembers to pick.
    const role = s.clientAccess === "redacted" ? "RedactedClient" : "Client";
    return { name, email, role, orgIds: scope.orgIds, clientNames: scope.clientNames, followUpActionPolicy: followUpActionPolicyPreset(role, "hidden"), status: "pending_invite" };
  }
  const role = s.level === "SuperAdmin" ? "SuperAdmin" : "StatesideBiller";
  const values = s.allCompanies
    ? userAddWizardCompanyOptions().map((option) => option.value)
    : (s.companies || []);
  const scope = role === "SuperAdmin" ? { orgIds: [], clientNames: [] } : userAddWizardScopeFromValues(values);
  return { name, email, role, orgIds: scope.orgIds, clientNames: scope.clientNames, followUpActionPolicy: followUpActionPolicyForRole(s.followUpActionPolicy || {}, role), status: "pending_invite" };
}

// The one place that answers "is this draft shippable?" for the book rule.
//
// Derived from the ROLE of the assembled draft, never from the wizard's own
// `type` — so a flow that never showed the book screen (a future branch, a
// restored draft, a mutated stepIndex) is still caught, and a second
// book-requiring role inherits the gate without a new call site.
function wizardDraftBookError(draft = {}) {
  const role = canonicalRole(draft.role);
  if (!roleRequiresBook(role) || scopeHasBook(draft)) return "";
  const label = roleProfiles[role]?.label || role;
  return `${label} needs a book — at least one payer. The companies you picked set which practices exist for them; the book sets which of that work is theirs.`;
}

function renderUserAddWizard() {
  const wizard = document.getElementById("userAddWizard");
  const body = document.getElementById("userAddWizardBody");
  const dots = document.getElementById("userAddWizardDots");
  const backBtn = document.getElementById("userAddWizardBackBtn");
  const nextBtn = document.getElementById("userAddWizardNextBtn");
  if (!wizard || !body || !userAddWizardState) return;
  const s = userAddWizardState;
  const flow = userAddWizardFlow(s);
  if (s.stepIndex > flow.length - 1) s.stepIndex = flow.length - 1;
  const step = s.done ? "done" : flow[s.stepIndex];
  syncUserAddWizardHistory(s);
  if (dots) {
    dots.innerHTML = flow.map((_, i) => `<span class="user-wizard-dot${!s.done && i === s.stepIndex ? " is-active" : ""}${s.done || i < s.stepIndex ? " is-done" : ""}"></span>`).join("");
  }
  // The labelled exit is on screen at every step, and stands down only while a
  // create is actually in flight — the one moment when leaving would strand a
  // request that decides whether an account exists.
  const cancelBtn = document.getElementById("userAddWizardCancelBtn");
  if (cancelBtn) {
    cancelBtn.style.visibility = s.sending ? "hidden" : "visible";
    cancelBtn.textContent = s.done ? "Close" : "Cancel";
  }
  const closeBtn = document.getElementById("userAddWizardCloseBtn");
  if (closeBtn) closeBtn.style.visibility = s.sending ? "hidden" : "visible";
  // The footer control is Back, and only Back. It used to read "Cancel" on the
  // first screen, which put two controls with the same accessible name on one
  // screen; the labelled exit lives in the top bar, is there on every step, and
  // is the one an admin is meant to find. userAddWizardBack() still exits from
  // step one, so a keyboard or programmatic Back is not a dead end either.
  if (backBtn) {
    backBtn.style.visibility = !s.done && !s.sending && s.stepIndex > 0 ? "visible" : "hidden";
    backBtn.textContent = "Back";
  }
  if (nextBtn) {
    const tapAdvance = step === "type" || step === "team" || step === "level" || step === "clientAccess"
      || (step === "company" && userAddWizardCompanyOptions().length > 0);
    nextBtn.style.display = tapAdvance ? "none" : "";
    nextBtn.disabled = Boolean(s.sending);
    nextBtn.textContent = s.done
      ? "Done"
      : step === "review"
        ? (s.sending ? "Working..." : (s.type === "biller" ? "Add biller" : "Send invite"))
        : "Continue";
  }
  body.innerHTML = userAddWizardScreenHtml(step, s);
  wireUserAddWizardScreen(step, s);
  const focusTarget = body.querySelector("input[data-wizard-autofocus]") || body.querySelector("#userAddWizardTitle");
  if (focusTarget) {
    if (focusTarget.tagName !== "INPUT") focusTarget.tabIndex = -1;
    focusTarget.focus?.({ preventScroll: true });
  }
}

function userAddWizardScreenHtml(step, s) {
  const error = s.error ? `<div class="user-wizard-error" role="alert">${escapeHtml(s.error)}</div>` : "";
  if (step === "type" || step === "details") {
    return `
      <div class="user-wizard-screen">
        <h2 id="userAddWizardTitle" class="user-wizard-question">Who are you adding?</h2>
        <p class="user-wizard-sub">Tap one. You can always add more people later.</p>
        ${error}
        <div class="user-wizard-cards">
          <button class="user-wizard-card${s.type === "biller" ? " is-selected" : ""}" type="button" data-wizard-type="biller">
            <span class="user-wizard-card-icon" data-tone="blue" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="2.5" y="7" width="19" height="13" rx="2"/><path d="M8.5 7V5.5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2V7"/><path d="M2.5 12.5h19"/></svg></span>
            <span class="user-wizard-card-text"><strong>A biller</strong><small>Offshore biller. Full claim, Authorization, and Credentialing work inside the companies you pick.</small></span>
            <span class="user-wizard-card-arrow" aria-hidden="true">→</span>
          </button>
          <button class="user-wizard-card${s.type === "client" ? " is-selected" : ""}" type="button" data-wizard-type="client">
            <span class="user-wizard-card-icon" data-tone="green" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M4 21V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v15"/><path d="M15 10h3a2 2 0 0 1 2 2v9"/><path d="M8 8h3M8 12h3M8 16h3"/><path d="M2.5 21h19"/></svg></span>
            <span class="user-wizard-card-text"><strong>A client</strong><small>Someone at a company you bill for — Riverbend, Harborview. They see only their own company’s data; you pick how much of it next.</small></span>
            <span class="user-wizard-card-arrow" aria-hidden="true">→</span>
          </button>
          <button class="user-wizard-card${s.type === "team" ? " is-selected" : ""}" type="button" data-wizard-type="team">
            <span class="user-wizard-card-icon" data-tone="neutral" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 3 4 6v6c0 5 3.5 7.5 8 9 4.5-1.5 8-4 8-9V6l-8-3Z"/><path d="m9 12 2 2 4-4"/></svg></span>
            <span class="user-wizard-card-text"><strong>TMS staff</strong><small>Stateside biller or admin. Trusted internal access.</small></span>
            <span class="user-wizard-card-arrow" aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    `;
  }
  if (step === "team") {
    return `
      <div class="user-wizard-screen">
        <h2 id="userAddWizardTitle" class="user-wizard-question">Which team are they on?</h2>
        <p class="user-wizard-sub">This is shown on the Org Map. It does not change what they can see.</p>
        ${error}
        <div class="user-wizard-cards">
          ${USER_ADD_WIZARD_BILLER_TEAMS.map(([key, label]) => `
          <button class="user-wizard-card${s.team === key ? " is-selected" : ""}" type="button" data-wizard-team="${key}">
            <span class="user-wizard-card-text"><strong>${label}</strong></span>
            <span class="user-wizard-card-arrow" aria-hidden="true">→</span>
          </button>`).join("")}
        </div>
      </div>
    `;
  }
  if (step === "level") {
    return `
      <div class="user-wizard-screen">
        <h2 id="userAddWizardTitle" class="user-wizard-question">How much access?</h2>
        <p class="user-wizard-sub">Start small — you can always grant more later.</p>
        ${error}
        <div class="user-wizard-cards">
          <button class="user-wizard-card${s.level === "StatesideBiller" ? " is-selected" : ""}" type="button" data-wizard-level="StatesideBiller">
            <span class="user-wizard-card-icon" data-tone="blue" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M5 21v-1.5A4.5 4.5 0 0 1 9.5 15h5a4.5 4.5 0 0 1 4.5 4.5V21"/></svg></span>
            <span class="user-wizard-card-text"><strong>Stateside biller</strong><small>Works claims across the companies you choose on the next screen.</small></span>
            <span class="user-wizard-card-arrow" aria-hidden="true">→</span>
          </button>
          ${userManagerCanAssignRole("SuperAdmin") ? `<button class="user-wizard-card${s.level === "SuperAdmin" ? " is-selected" : ""}" type="button" data-wizard-level="SuperAdmin">
            <span class="user-wizard-card-icon" data-tone="neutral" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="8" cy="16" r="3.5"/><path d="M10.5 13.5 20 4"/><path d="m14.5 9.5 2.5 2.5"/><path d="m17 7 2.5 2.5"/></svg></span>
            <span class="user-wizard-card-text"><strong>Super admin</strong><small>Everything — every company, every setting, user management. Keep this rare.</small></span>
            <span class="user-wizard-card-arrow" aria-hidden="true">→</span>
          </button>` : ""}
        </div>
      </div>
    `;
  }
  if (step === "company") {
    const ready = userAddWizardCompanyListState() === "ready";
    const all = ready ? userAddWizardCompanyOptions() : [];
    const searchBox = all.length > 6
      ? `<input id="userAddWizardCompanySearch" class="user-wizard-input user-wizard-input--search" type="search" placeholder="Search companies" value="${escapeHtml(s.companySearch || "")}" autocomplete="off">`
      : "";
    return `
      <div class="user-wizard-screen">
        <h2 id="userAddWizardTitle" class="user-wizard-question">Which company are they from?</h2>
        <p class="user-wizard-sub">They'll only ever see this one company's data.</p>
        ${error}
        ${searchBox}
        ${userAddWizardCompanyScreenBodyHtml(s, { multi: false })}
      </div>
    `;
  }
  if (step === "clientAccess") {
    const where = escapeHtml(s.companyLabel || "");
    return `
      <div class="user-wizard-screen">
        <h2 id="userAddWizardTitle" class="user-wizard-question">What do they do${where ? ` at ${where}` : ""}?</h2>
        <p class="user-wizard-sub">These two see different things. Pick the one that matches the job.</p>
        ${error}
        <div class="user-wizard-cards">
          <button class="user-wizard-card${s.clientAccess === "full" ? " is-selected" : ""}" type="button" data-wizard-client-access="full">
            <span class="user-wizard-card-icon" data-tone="green" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M3 17.5 9.5 11l4 4 7-7"/><path d="M15 8h5.5v5.5"/></svg></span>
            <span class="user-wizard-card-text"><strong>Client</strong><small>Full read-only AR and Reports, plus Authorization, Credentialing, and Patient Statements.</small></span>
            <span class="user-wizard-card-arrow" aria-hidden="true">→</span>
          </button>
          <button class="user-wizard-card${s.clientAccess === "redacted" ? " is-selected" : ""}" type="button" data-wizard-client-access="redacted">
            <span class="user-wizard-card-icon" data-tone="blue" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z"/><path d="M14 3v5h5"/><path d="M9 13h6M9 17h4"/></svg></span>
            <span class="user-wizard-card-text"><strong>Redacted client</strong><small>Read-only AR without insurance-side financial amounts. Patient responsibility and statement balances remain.</small></span>
            <span class="user-wizard-card-arrow" aria-hidden="true">→</span>
          </button>
        </div>
        <p class="user-wizard-sub">Both roles can read patient-level detail for this company. Redacted clients cannot see insurance financial amounts.</p>
      </div>
    `;
  }
  if (step === "book") {
    const book = normalizeUserScopeList(s.payerBook);
    const known = userAddWizardPayerOptions();
    // No "Select all". Every other multi-select here has one, and leaving it out
    // is the point: a book that covers every payer is not a book, it is the
    // whole-org read the book exists to replace. There is deliberately no
    // one-click way to make it meaningless.
    const chips = book.length
      ? book.map((payer) => `
          <span class="permission-chip active user-wizard-chip">${escapeHtml(payer)}<button class="user-wizard-chip-remove" type="button" data-wizard-payer-remove="${escapeHtml(payer)}" aria-label="Remove ${escapeHtml(payer)}">×</button></span>
        `).join("")
      : `<span class="user-wizard-chip-empty">No payers yet — this account would see nothing.</span>`;
    return `
      <div class="user-wizard-screen">
        <h2 id="userAddWizardTitle" class="user-wizard-question">Which payers are theirs?</h2>
        <p class="user-wizard-sub">This is their <strong>book</strong> — the only claims they will ever see, inside the companies you just picked. Every other payer at those companies stays invisible to them.</p>
        ${error}
        <div class="permission-chip-row user-wizard-chip-row">${chips}</div>
        <div class="user-wizard-scope-controls">
          <div class="user-wizard-scope-actions">
            <button id="userAddWizardClearPayers" class="user-wizard-scope-link" type="button">Clear all</button>
          </div>
          <span class="user-wizard-scope-count">${formatCount(book.length)} payer${book.length === 1 ? "" : "s"} in their book</span>
        </div>
        <input id="userAddWizardPayerSearch" class="user-wizard-input user-wizard-input--search" type="search" placeholder="${known.length ? `Search ${formatCount(known.length)} payers, or type a new one` : "Type a payer name, then press Enter"}" value="${escapeHtml(s.payerSearch || "")}" autocomplete="off" spellcheck="false">
        <div id="userAddWizardPayerList" class="user-wizard-options">${userAddWizardPayerListHtml(s)}</div>
      </div>
    `;
  }
  if (step === "actions") {
    const role = s.type === "biller" ? "OffshoreBiller" : (s.level === "SuperAdmin" ? "SuperAdmin" : "StatesideBiller");
    if (!s.followUpActionPolicy) s.followUpActionPolicy = followUpActionPolicyPreset(role, "role_default");
    const policyCounts = followUpActionPolicyCounts(followUpActionPolicyForRole(s.followUpActionPolicy, role));
    return `<div class="user-wizard-screen user-wizard-screen--policy">
      <h2 id="userAddWizardTitle" class="user-wizard-question">How should their follow-up work be approved?</h2>
      <p class="user-wizard-sub">The safe starting point is already selected. Continue, or review individual actions.</p>
      ${error}
      <details class="user-wizard-policy-disclosure" data-wizard-policy-disclosure${s.followUpPolicyExpanded ? " open" : ""}>
        <summary>
          <span><strong>Safe starting permissions</strong><small>${policyCounts.approval_required} need approval · ${policyCounts.denied} hidden · ${policyCounts.direct} direct</small></span>
          <span class="user-wizard-policy-disclosure-action"><span class="user-wizard-policy-disclosure-closed">Review or change</span><span class="user-wizard-policy-disclosure-open">Hide choices</span></span>
        </summary>
        ${renderFollowUpActionPolicyEditor(s, role, { idPrefix: "userAddWizardFollowUp", wide: true })}
      </details>
    </div>`;
  }
  if (step === "companies") {
    const isBillerCompanies = s.type === "biller";
    const heading = isBillerCompanies ? "Which companies can they be assigned?" : "Which companies can they work on?";
    const sub = isBillerCompanies
      ? "Start by selecting at least one company. Use Select all only if this biller should be assignable at every company."
      : "Pick as many as you like.";
    // Same rule as the single-company screen, through the same helper: no
    // half-named list, and no count ("0 of 5 selected") describing a list that
    // is about to change under it.
    const body = userAddWizardCompanyScreenBodyHtml(s, { multi: true });
    return `
      <div class="user-wizard-screen">
        <h2 id="userAddWizardTitle" class="user-wizard-question">${heading}</h2>
        <p class="user-wizard-sub">${sub}</p>
        ${error}
        ${body}
      </div>
    `;
  }
  if (step === "name" || step === "person") {
    const isPerson = step === "person";
    return `
      <div class="user-wizard-screen">
        <h2 id="userAddWizardTitle" class="user-wizard-question">${isPerson ? "Who is it?" : "What's their name?"}</h2>
        <p class="user-wizard-sub">${isPerson
          ? (s.type === "client" && s.companyLabel ? `The secure invite email goes straight to them at ${escapeHtml(s.companyLabel)}.` : "The secure invite email goes straight to them.")
          : "This is the name you'll see in “Assign to…”."}</p>
        ${error}
        <div class="user-wizard-fields">
          <label class="user-wizard-field">
            <span class="user-wizard-field-label">Full name <em>required</em></span>
            <input id="userAddWizardName" data-wizard-autofocus data-wizard-enter class="user-wizard-input" type="text" placeholder="e.g. Jordan Lee" value="${escapeHtml(s.name || "")}" autocomplete="name" required>
          </label>
          <label class="user-wizard-field">
            <span class="user-wizard-field-label">Email <em>required</em></span>
            <input id="userAddWizardEmail" data-wizard-enter class="user-wizard-input" type="email" inputmode="email" placeholder="e.g. jordan@example.com" value="${escapeHtml(s.email || "")}" autocomplete="email" required>
          </label>
        </div>
      </div>
    `;
  }
  if (step === "review") {
    const draft = userAddWizardDraft();
    const unresolved = userAddWizardUnnamedDraftOrgIds(draft).length;
    const everyCompany = userAddWizardGrantsEveryCompany(draft);
    const chips = [
      `<span class="permission-chip active">${escapeHtml(roleProfiles[draft.role]?.label || draft.role)}</span>`,
      draft.status === "active"
        ? `<span class="permission-chip active">Active immediately</span>`
        : `<span class="permission-chip active">Email invite · expires in 7 days</span>`
    ];
    if (!isExternalClientRole(draft.role)) {
      const policyCounts = followUpActionPolicyCounts(draft.followUpActionPolicy);
      chips.push(`<span class="permission-chip">Actions: ${policyCounts.direct} direct · ${policyCounts.approval_required} supervised · ${policyCounts.denied} hidden</span>`);
    }
    if (roleRequiresBook(draft.role)) {
      const billerCompanies = userAddWizardCompanyLabels(draft);
      const book = normalizeUserScopeList(draft.payerNames);
      chips.push(`<span class="permission-chip">At: ${userAddWizardCompanyScopeSummary(billerCompanies, { unresolved })}</span>`);
      // Two chips, not one, because they are two different things: the company
      // list is the tenant boundary, the book is what of it is theirs. Collapsing
      // them into a single "Sees:" chip is what let a bookless account look
      // provisioned.
      chips.push(`<span class="permission-chip active">Book: ${book.length ? escapeHtml(book.slice(0, 3).join(", ")) + (book.length > 3 ? `, +${formatCount(book.length - 3)} more` : "") : "empty — they would see nothing"}</span>`);
    } else if (canonicalRole(draft.role) === "RedactedClient") {
      const clientWhere = userAddWizardCompanyLabels(draft);
      chips.push(`<span class="permission-chip">Sees: ${userAddWizardCompanyScopeSummary(clientWhere, { unresolved })}</span>`);
      chips.push(`<span class="permission-chip active">Insurance amounts hidden</span>`);
    } else if (draft.clientNames.length || draft.orgIds.length) {
      // The plain Client is the NARROWEST role and the one an admin most wants
      // to confirm before an external account exists — it was the only branch
      // still reading raw clientNames, so an orgIds-first pick rendered no chip.
      const clientCompanies = userAddWizardCompanyLabels(draft);
      chips.push(`<span class="permission-chip">Sees: ${userAddWizardCompanyScopeSummary(clientCompanies, { unresolved })}</span>`);
    } else if (draft.role === "SuperAdmin") {
      chips.push(`<span class="permission-chip">Sees: everything</span>`);
    }
    return `
      <div class="user-wizard-screen">
        <h2 id="userAddWizardTitle" class="user-wizard-question">Does this look right?</h2>
        ${error}
        <div class="user-wizard-review">
          ${userAddWizardReviewHtml(s, draft)}
          <div class="permission-chip-row">${chips.join("")}</div>
          ${everyCompany ? userAddWizardEveryCompanyConfirmHtml(s, draft) : ""}
        </div>
        <p class="user-wizard-sub">${s.type === "biller"
          ? "You can change or remove them anytime from the directory."
          : "Their access is locked before they ever sign in, and you can disable them anytime."}</p>
      </div>
    `;
  }
  if (step === "done") {
    const draft = userAddWizardDraft();
    const headline = s.inviteEmailFailed ? "User added. Email not sent."
      : s.inviteSent ? "Invite sent." : `${draft.name} is added.`;
    const sub = s.inviteEmailFailed
      ? "The user is created, but the invite email didn't go out. Use Resend invite or get a shareable invite link from Manage Users."
      : s.inviteSent
        ? `A secure sign-in link was sent to ${draft.email}. It expires in 7 days. Access begins after they accept the invitation.`
        : "The user was saved, but the invitation could not be sent. Please try again later.";
    return `
      <div class="user-wizard-screen user-wizard-screen--done">
        <span class="user-wizard-check" aria-hidden="true">✓</span>
        <h2 id="userAddWizardTitle" class="user-wizard-question">${escapeHtml(headline)}</h2>
        <p class="user-wizard-sub">${escapeHtml(sub)}</p>
        <button id="userAddWizardAddAnotherBtn" class="ghost-button user-wizard-add-another" type="button">Add another person</button>
      </div>
    `;
  }
  return "";
}

// ONE tap on "Select all" is the widest grant this product can make, and the
// review screen used to describe it as three company names and an ellipsis.
// Here it is named for what it is, listed in full (collapsed, because twenty
// names is a wall), and acknowledged once. The acknowledgement is deliberately
// the ONLY new friction in this flow: every narrower grant is unchanged.
function userAddWizardEveryCompanyConfirmHtml(s = {}, draft = {}) {
  const names = userAddWizardCompanyLabels(draft);
  // The widest grant in the product is not confirmed against a list with a
  // number in it. If anything on the draft cannot be named, say so and stop.
  if (userAddWizardUnnamedDraftOrgIds(draft).length) {
    return `<div class="user-wizard-grant-all"><p>${escapeHtml(USER_ADD_WIZARD_UNNAMED_COMPANY_TEXT)}.</p></div>`;
  }
  return `
    <div class="user-wizard-grant-all">
      <details class="user-wizard-grant-names">
        <summary>Every company you can grant — all ${escapeHtml(formatCount(names.length))}</summary>
        <ul>${names.map((name) => `<li>${escapeHtml(name)}</li>`).join("")}</ul>
      </details>
      <label class="user-wizard-grant-ack">
        <input type="checkbox" id="userAddWizardEveryCompanyAck"${s.everyCompanyAcknowledged ? " checked" : ""}>
        <span>Yes — give this person every one of these ${escapeHtml(formatCount(names.length))} companies.</span>
      </label>
    </div>
  `;
}

function userAddWizardCompanyListHtml(s) {
  const query = String(s.companySearch || "").trim().toLowerCase();
  const options = userAddWizardCompanyOptions().filter((option) =>
    !query
    || option.label.toLowerCase().includes(query)
    || String(option.searchText).toLowerCase().includes(query));
  if (!options.length) {
    return `<div class="user-wizard-empty">No company matches “${escapeHtml(s.companySearch || "")}”.</div>`;
  }
  return options.map((option) => `
    <button class="user-wizard-option${option.value === s.company ? " is-selected" : ""}" type="button" data-wizard-company="${escapeHtml(option.value)}" data-wizard-company-label="${escapeHtml(option.label)}">
      <strong>${escapeHtml(option.label)}</strong>
      ${userAddWizardCompanyOptionNoteHtml(option)}
      <span class="user-wizard-card-arrow" aria-hidden="true">→</span>
    </button>
  `).join("");
}

// ===========================================================================
// THE COMPANY SCOPE PICKER - ONE component, TWO call sites.
//
// The add-user wizard's companies step and the Manage Users edit rail pick the
// same thing out of the same option source, so they render from the same
// function. They were forked for one release and the fork is exactly what it
// looks like: the wizard grew chips, search, +Add rows, Select all / Clear all
// and two empty states while the rail was still a raw checkbox grid.
//
// THE PAYLOAD DOES NOT MOVE. When a caller passes valueName the picker emits
// one hidden input per option carrying that name - the SELECTED ones enabled,
// the rest disabled - so the submitted value set is byte-for-byte what the
// checkbox grid submitted. userMgmtEditScopeValues() and saveUserMgmtEdit() are
// untouched by this port; if either needed editing, the port would be wrong.
//
// THE ONE-COMPANY CLIENT ROLES DO NOT USE THIS. Client and ClientBiller keep
// the radio group in renderUserMgmtEditScopeControl. That group is not styling:
// PORTAL_DIRECTIVES.md records that the single-org rule for external client
// logins is NOT capped in the data model and that the UI is the enforcement
// point, so the radios ARE the tenant boundary. A multi-select picker there
// would quietly turn that boundary into a preference.
// ===========================================================================
function renderScopeCompanyPicker(config = {}) {
  const options = config.options || [];
  const selected = new Set(config.selectedValues || []);
  const idPrefix = config.idPrefix || "scopeCompanyPicker";
  // Chips read off the option list so a stale composite value can't render a
  // ghost chip; order follows the canonical option order, not click order.
  const selectedOptions = options.filter((option) => selected.has(option.value));
  const chips = selectedOptions.length
    ? selectedOptions.map((option) => `
        <span class="permission-chip active user-wizard-chip">${escapeHtml(option.label)}<button class="user-wizard-chip-remove" type="button" data-scope-remove="${escapeHtml(option.value)}" aria-label="Remove ${escapeHtml(option.label)}">×</button></span>
      `).join("")
    : `<span class="user-wizard-chip-empty">${escapeHtml(config.emptyChipText || "No companies selected yet.")}</span>`;
  // Every option chosen is the state where the list below renders "All
  // companies selected." and the search box has nothing left to search. Same
  // condition as that branch (selectedOptions is options filtered by the
  // selection, so equal lengths means nothing is unselected); flagged on the
  // input rather than removing it, because the search handler re-renders the
  // option list ALONE and a box dropped from the markup could not come back.
  const allSelected = options.length > 0 && selectedOptions.length === options.length;
  const values = config.valueName
    ? options.map((option) => `<input type="hidden" name="${escapeHtml(config.valueName)}" value="${escapeHtml(option.value)}"${selected.has(option.value) ? "" : " disabled"}>`).join("")
    : "";
  return `
    ${values}
    <div class="permission-chip-row user-wizard-chip-row">${chips}</div>
    <div class="user-wizard-scope-controls">
      <div class="user-wizard-scope-actions">
        <button id="${escapeHtml(idPrefix)}SelectAll" class="user-wizard-scope-link" type="button" data-scope-action="all">Select all</button>
        <span class="user-wizard-scope-sep" aria-hidden="true">·</span>
        <button id="${escapeHtml(idPrefix)}ClearAll" class="user-wizard-scope-link" type="button" data-scope-action="clear">Clear all</button>
      </div>
      <span class="user-wizard-scope-count">${formatCount(selected.size)} of ${formatCount(options.length)} selected</span>
    </div>
    <input id="${escapeHtml(idPrefix)}Search" class="user-wizard-input user-wizard-input--search${allSelected ? " user-wizard-input--search-idle" : ""}" type="search" placeholder="Search ${formatCount(options.length)} companies…" value="${escapeHtml(config.search || "")}" autocomplete="off">
    <div id="${escapeHtml(idPrefix)}List" class="user-wizard-options">${scopeCompanyPickerOptionsHtml(config)}</div>
  `;
}

// The filtered "not yet selected" pool rendered as +Add rows. Anything already
// chosen is hidden, so the list and the chips above it never show the same
// company twice. The "all selected" check comes BEFORE the search filter on
// purpose: with everything picked there is nothing to search for, and "No
// matches" would blame the query for a full selection.
function scopeCompanyPickerOptionsHtml(config = {}) {
  const options = config.options || [];
  const selected = new Set(config.selectedValues || []);
  const query = String(config.search || "").trim().toLowerCase();
  // Nothing to pick from at all is a THIRD state. Falling through to "All
  // companies selected." would tell an admin with an empty option list that
  // they had granted everything.
  if (!options.length) {
    return `<div class="user-wizard-empty">${escapeHtml(config.noOptionsText || "No companies to pick from yet.")}</div>`;
  }
  const unselected = options.filter((option) => !selected.has(option.value));
  if (!unselected.length) {
    return `<div class="user-wizard-empty">All companies selected.</div>`;
  }
  const matches = unselected.filter((option) =>
    !query
    || String(option.label || "").toLowerCase().includes(query)
    || String(option.searchText || "").toLowerCase().includes(query));
  if (!matches.length) {
    return `<div class="user-wizard-empty">No matches. Try a different search.</div>`;
  }
  return matches.map((option) => `
    <button class="user-wizard-option" type="button" data-scope-pick="${escapeHtml(option.value)}">
      <strong>${escapeHtml(option.label)}</strong>
      ${userAddWizardCompanyOptionNoteHtml(option)}
      <span class="user-wizard-option-add" aria-hidden="true">+ Add</span>
    </button>
  `).join("");
}

// Scoped binder for the +Add rows, so a search re-render of JUST the list (not
// the whole screen) keeps them live. Shared by both call sites for the same
// reason the renderer is.
function wireScopeCompanyPickerPicks(host, onPick) {
  if (!host || typeof onPick !== "function") return;
  host.querySelectorAll("[data-scope-pick]").forEach((row) => row.addEventListener("click", () => onPick(row.dataset.scopePick)));
}

// The wizard's half of the shared picker. "All companies" is a sentinel in the
// wizard state, so it resolves to the full value list here - the picker itself
// only ever sees an explicit selection.
function userAddWizardCompaniesPickerConfig(s = {}) {
  const all = userAddWizardCompanyOptions();
  return {
    options: all,
    selectedValues: s.allCompanies ? all.map((option) => option.value) : (s.companies || []),
    search: s.companySearch || "",
    idPrefix: "userAddWizardCompanies",
    noOptionsText: "No client groups are available yet."
  };
}

// THE OFFSHORE BOOK PICKER.
//
// Payer names come from whatever the grid has already faceted, because there is
// no payer master anywhere in this product (the facets route is the only
// source, and it is scoped to the caller). That list is therefore a CONVENIENCE,
// never a constraint: an admin provisioning a biller for a payer nobody has
// billed yet must still be able to type it. So the same box searches the known
// list and accepts a brand-new name, and the "+ Add" row is how both are
// committed — one interaction, whether or not the cache happens to be warm.
function userAddWizardPayerOptions() {
  return uniqueSorted([
    ...trackerApiFacetValues("primaryPayer"),
    ...trackerApiFacetValues("secondaryPayer")
  ].filter((value) => value && value !== "Blank"));
}

function userAddWizardPayerListHtml(s) {
  const query = String(s.payerSearch || "").trim();
  const lowerQuery = query.toLowerCase();
  const chosen = new Set(normalizeUserScopeList(s.payerBook).map((payer) => payer.toLowerCase()));
  const matches = userAddWizardPayerOptions()
    .filter((payer) => !chosen.has(payer.toLowerCase()))
    .filter((payer) => !lowerQuery || payer.toLowerCase().includes(lowerQuery));
  const rows = matches.map((payer) => `
    <button class="user-wizard-option" type="button" data-wizard-payer-add="${escapeHtml(payer)}">
      <strong>${escapeHtml(payer)}</strong>
      <span class="user-wizard-option-add" aria-hidden="true">+ Add</span>
    </button>
  `);
  // The free-text row: offered whenever what was typed is not already an exact
  // match, so a payer missing from the cache is one Enter away rather than a
  // reason to abandon the wizard and hand-edit a JSON blob later.
  const exact = userAddWizardPayerOptions().some((payer) => payer.toLowerCase() === lowerQuery)
    || chosen.has(lowerQuery);
  if (query && !exact) {
    rows.unshift(`
      <button class="user-wizard-option" type="button" data-wizard-payer-add="${escapeHtml(query)}">
        <strong>Add “${escapeHtml(query)}”</strong>
        <span class="user-wizard-option-add" aria-hidden="true">+ Add</span>
      </button>
    `);
  }
  if (!rows.length) {
    if (query) return `<div class="user-wizard-empty">“${escapeHtml(query)}” is already in their book.</div>`;
    if (!userAddWizardPayerOptions().length) return `<div class="user-wizard-empty">No payer list has loaded yet. Type each payer name exactly as it appears on the claim, then press Enter.</div>`;
    if (chosen.size) return `<div class="user-wizard-empty">Every payer we know about is already in their book. Type a name to add one we haven’t seen.</div>`;
    return `<div class="user-wizard-empty">No payer list has loaded yet. Type each payer name exactly as it appears on the claim, then press Enter.</div>`;
  }
  return rows.join("");
}

// Accepts a pasted list as readily as a single name — normalizeUserScopeList
// splits on commas/semicolons/newlines, which is the same shape the Edit rail's
// payer field has always parsed.
function userAddWizardAddPayer(s, raw) {
  const additions = normalizeUserScopeList(raw).map((payer) => payer.replace(/\s+/g, " "));
  if (!additions.length) return false;
  const book = normalizeUserScopeList(s.payerBook);
  const seen = new Set(book.map((payer) => payer.toLowerCase()));
  let added = false;
  additions.forEach((payer) => {
    if (seen.has(payer.toLowerCase())) return;
    seen.add(payer.toLowerCase());
    book.push(payer);
    added = true;
  });
  if (!added) return false;
  s.payerBook = book;
  s.payerSearch = "";
  s.error = "";
  return true;
}

// Scoped binder so the search box can re-render just the list and keep the
// +Add rows live — mirrors wireUserAddWizardCompanyChecks.
function wireUserAddWizardPayerAdds(host, s) {
  host.querySelectorAll("[data-wizard-payer-add]").forEach((row) => row.addEventListener("click", () => {
    userAddWizardAddPayer(s, row.dataset.wizardPayerAdd);
    renderUserAddWizard();
  }));
}

// The review reads as one sentence a non-technical admin can sanity-check --
// who, where they belong, and exactly what they will see.
function userAddWizardReviewHtml(s, draft = userAddWizardDraft()) {
  const name = escapeHtml(draft.name);
  // The SENTENCE is a visible label too. It used to read the raw id straight
  // out of the label map and print it directly above a chip that said the same
  // company had no name — one screen contradicting itself, with an id on it.
  const unnamedCompany = userAddWizardUnnamedDraftOrgIds(draft).length > 0;
  const unnamedText = escapeHtml(USER_ADD_WIZARD_UNNAMED_COMPANY_TEXT);
  if (s.type === "biller") {
    const billerCompanies = userAddWizardCompanyLabels(draft);
    const billerWhere = unnamedCompany
      ? `<strong>${unnamedText}</strong>`
      : billerCompanies.length
      ? `<strong>${escapeHtml(billerCompanies.slice(0, 4).join(", "))}${billerCompanies.length > 4 ? ", …" : ""}</strong>`
      : "the companies you pick";
    return `<p class="user-wizard-review-line"><strong>${name}</strong> joins the <strong>${escapeHtml((USER_ADD_WIZARD_BILLER_TEAMS.find(([key]) => key === s.team) || [])[1] || "")}</strong> team as an <strong>offshore biller</strong> at ${billerWhere}. They can work every claim plus Authorization and Credentialing inside those companies.${draft.email ? ` The secure invite goes to <strong>${escapeHtml(draft.email)}</strong>.` : ""}</p>`;
  }
  if (s.type === "client") {
    // Resolve through the helper before the generic literal, so an orgIds-only
    // state (no companyLabel) still names the company instead of degrading to
    // "their company" in a sentence whose whole job is to be checkable.
    const company = unnamedCompany
      ? unnamedText
      : escapeHtml(s.companyLabel || userAddWizardCompanyLabels(draft)[0] || "their company");
    if (canonicalRole(draft.role) === "RedactedClient") {
      return `<p class="user-wizard-review-line"><strong>${name}</strong> at <strong>${company}</strong> gets a secure invite as a <strong>redacted client</strong>. They can read claim detail and work Authorization, Credentialing, and Patient Statements, but cannot open Reports or see insurance-side financial amounts.</p>`;
    }
    return `<p class="user-wizard-review-line"><strong>${name}</strong> at <strong>${company}</strong> gets a secure invite as a <strong>client</strong> with full read-only AR and Reports plus Authorization, Credentialing, and Patient Statements.</p>`;
  }
  if (draft.role === "SuperAdmin") {
    return `<p class="user-wizard-review-line"><strong>${name}</strong> gets a secure email invite at <strong>${escapeHtml(draft.email)}</strong> as a <strong>super admin</strong> — full access to every company, every claim, and user management.</p>`;
  }
  // The LAST call site that still printed raw orgIds. It sat directly under a
  // chip that already showed names, so one screen said "CedarLaneABA" and
  // "90000005" about the same company — worse than the original bug.
  const names = userAddWizardCompanyLabels(draft);
  const count = names.length;
  const list = names.slice(0, 4).join(", ");
  if (unnamedCompany) {
    return `<p class="user-wizard-review-line"><strong>${name}</strong> gets a secure email invite at <strong>${escapeHtml(draft.email)}</strong> as a <strong>stateside biller</strong>, but one of the chosen companies is <strong>${unnamedText}</strong>.</p>`;
  }
  return `<p class="user-wizard-review-line"><strong>${name}</strong> gets a secure email invite at <strong>${escapeHtml(draft.email)}</strong> as a <strong>stateside biller</strong> working across <strong>${formatCount(count)} compan${count === 1 ? "y" : "ies"}</strong>${list ? ` (${escapeHtml(list)}${count > 4 ? ", …" : ""})` : ""}.</p>`;
}

function wireUserAddWizardScreen(step, s) {
  const body = document.getElementById("userAddWizardBody");
  if (!body) return;
  body.querySelectorAll("[data-wizard-type]").forEach((card) => card.addEventListener("click", () => {
    s.type = card.dataset.wizardType;
    const role = s.type === "biller" ? "OffshoreBiller" : (s.type === "client" ? "Client" : "StatesideBiller");
    s.followUpActionPolicy = followUpActionPolicyPreset(role, "role_default");
    s.followUpPolicyNotice = "";
    s.followUpPolicyExpanded = false;
    s.error = "";
    s.stepIndex = 1;
    renderUserAddWizard();
  }));
  body.querySelectorAll("[data-wizard-level]").forEach((card) => card.addEventListener("click", () => {
    s.level = card.dataset.wizardLevel;
    s.followUpActionPolicy = followUpActionPolicyPreset(s.level === "SuperAdmin" ? "SuperAdmin" : "StatesideBiller", "role_default");
    s.followUpPolicyNotice = "Access level changed, so follow-up actions were reset to the safe role default.";
    s.followUpPolicyExpanded = false;
    s.error = "";
    s.stepIndex = 2;
    renderUserAddWizard();
  }));
  body.querySelectorAll("[data-wizard-team]").forEach((card) => card.addEventListener("click", () => {
    s.team = card.dataset.wizardTeam;
    s.error = "";
    s.stepIndex += 1;
    renderUserAddWizard();
  }));
  body.querySelectorAll("[data-wizard-client-access]").forEach((card) => card.addEventListener("click", () => {
    s.clientAccess = card.dataset.wizardClientAccess;
    s.error = "";
    s.stepIndex += 1;
    renderUserAddWizard();
  }));
  wireFollowUpActionPolicyEditor(body, s, s.type === "biller" ? "OffshoreBiller" : (s.level === "SuperAdmin" ? "SuperAdmin" : "StatesideBiller"), renderUserAddWizard);
  const policyDisclosure = body.querySelector("[data-wizard-policy-disclosure]");
  policyDisclosure?.addEventListener("toggle", () => {
    s.followUpPolicyExpanded = policyDisclosure.open;
  });
  wireUserAddWizardCompanyRows(body, s);
  const search = body.querySelector("#userAddWizardCompanySearch");
  search?.addEventListener("input", () => {
    s.companySearch = search.value;
    const list = body.querySelector("#userAddWizardCompanyList");
    if (list) {
      list.innerHTML = userAddWizardCompanyListHtml(s);
      wireUserAddWizardCompanyRows(list, s);
    }
  });
  body.querySelector("#userAddWizardAllCompanies")?.addEventListener("click", () => {
    s.allCompanies = !s.allCompanies;
    if (s.allCompanies) s.companies = [];
    s.error = "";
    renderUserAddWizard();
  });
  const toggleWizardCompany = (value) => {
    s.allCompanies = false;
    const selected = new Set(s.companies || []);
    if (selected.has(value)) selected.delete(value);
    else selected.add(value);
    s.companies = Array.from(selected);
    s.error = "";
    renderUserAddWizard();
  };
  wireScopeCompanyPickerPicks(body, toggleWizardCompany);
  // Multi-company chip picker: the search box filters the +Add pool in place
  // (re-render keeps the chips, count, and list in sync); the chip × and the
  // +Add rows both flow through the same toggle mutation as the legacy checks.
  const companiesSearch = body.querySelector("#userAddWizardCompaniesSearch");
  companiesSearch?.addEventListener("input", () => {
    s.companySearch = companiesSearch.value;
    const list = body.querySelector("#userAddWizardCompaniesList");
    if (list) {
      list.innerHTML = scopeCompanyPickerOptionsHtml(userAddWizardCompaniesPickerConfig(s));
      wireScopeCompanyPickerPicks(list, toggleWizardCompany);
    }
  });
  body.querySelectorAll("[data-scope-remove]").forEach((chip) => chip.addEventListener("click", () => {
    const value = chip.dataset.scopeRemove;
    // Removing a chip while "All companies" is on means: stop being all, keep
    // everyone except this one — so the user lands on an explicit subset.
    const base = s.allCompanies ? userAddWizardCompanyOptions().map((option) => option.value) : (s.companies || []);
    const selected = new Set(base);
    selected.delete(value);
    s.allCompanies = false;
    s.companies = Array.from(selected);
    s.error = "";
    renderUserAddWizard();
  }));
  body.querySelectorAll("[data-scope-action]").forEach((button) => button.addEventListener("click", () => {
    s.allCompanies = button.dataset.scopeAction === "all";
    s.companies = [];
    s.error = "";
    renderUserAddWizard();
  }));
  // Book screen. Enter COMMITS what was typed rather than advancing the wizard:
  // on a screen whose entire job is building a list, Enter meaning "next" would
  // silently discard the name still sitting in the box — and the person doing
  // the discarding would be told they had no book.
  const payerSearch = body.querySelector("#userAddWizardPayerSearch");
  payerSearch?.addEventListener("input", () => {
    s.payerSearch = payerSearch.value;
    const list = body.querySelector("#userAddWizardPayerList");
    if (list) {
      list.innerHTML = userAddWizardPayerListHtml(s);
      wireUserAddWizardPayerAdds(list, s);
    }
  });
  payerSearch?.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (userAddWizardAddPayer(s, payerSearch.value)) renderUserAddWizard();
  });
  wireUserAddWizardPayerAdds(body, s);
  body.querySelectorAll("[data-wizard-payer-remove]").forEach((chip) => chip.addEventListener("click", () => {
    const value = String(chip.dataset.wizardPayerRemove || "").toLowerCase();
    s.payerBook = normalizeUserScopeList(s.payerBook).filter((payer) => payer.toLowerCase() !== value);
    s.error = "";
    renderUserAddWizard();
  }));
  body.querySelector("#userAddWizardClearPayers")?.addEventListener("click", () => {
    s.payerBook = [];
    s.error = "";
    renderUserAddWizard();
  });
  body.querySelector("#userAddWizardEveryCompanyAck")?.addEventListener("change", (event) => {
    s.everyCompanyAcknowledged = Boolean(event.target.checked);
    s.error = "";
  });
  const nameInput = body.querySelector("#userAddWizardName");
  nameInput?.addEventListener("input", () => { s.name = nameInput.value; });
  const emailInput = body.querySelector("#userAddWizardEmail");
  emailInput?.addEventListener("input", () => { s.email = emailInput.value; });
  body.querySelector("[data-wizard-company-retry]")?.addEventListener("click", () => {
    s.error = "";
    requestPortalOrgDirectory({ force: true });
    renderUserAddWizard();
  });
  body.querySelectorAll("input[data-wizard-enter]").forEach((input) => input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      advanceUserAddWizard();
    }
  }));
  body.querySelector("#userAddWizardAddAnotherBtn")?.addEventListener("click", () => {
    userAddWizardState = blankUserAddWizardState();
    // A fresh blank draft is not "input". Without this the signature captured
    // when the flow opened - which on the Manage Groups path already carries a
    // pre-chosen company - would differ from the blank one, and leaving would
    // ask them to discard a form they have not touched.
    userAddWizardOpenedSignature = userAddWizardInputSignature(userAddWizardState);
    userAddWizardUnwindHistoryToStart();
    renderUserAddWizard();
  });
}

function wireUserAddWizardCompanyRows(host, s) {
  host.querySelectorAll("[data-wizard-company]").forEach((row) => row.addEventListener("click", () => {
    s.company = row.dataset.wizardCompany;
    s.companyLabel = row.dataset.wizardCompanyLabel || "";
    s.error = "";
    s.stepIndex += 1;
    renderUserAddWizard();
  }));
}

function advanceUserAddWizard() {
  const s = userAddWizardState;
  if (!s || s.sending) return;
  if (s.done) {
    closeUserAddWizard();
    return;
  }
  const flow = userAddWizardFlow(s);
  const step = flow[s.stepIndex];
  s.error = "";
  if ((step === "type" || step === "details") && !s.type) {
    s.error = "Pick who you're adding.";
    renderUserAddWizard();
    return;
  }
  if (step === "team" && !s.team) {
    s.error = "Pick which team they are on.";
    renderUserAddWizard();
    return;
  }
  if (step === "company") {
    if (!userAddWizardCompanyOptions().length) {
      s.error = portalOrgDirectoryState.status === "error"
        ? "Load the company list before continuing."
        : "A configured company is required before inviting this user.";
      renderUserAddWizard();
      return;
    }
    if (!s.company) {
      s.error = "Pick the company they belong to.";
      renderUserAddWizard();
      return;
    }
  }
  if (step === "companies" && !s.allCompanies && !(s.companies || []).length) {
    s.error = "Pick at least one company, or choose “All companies”.";
    renderUserAddWizard();
    return;
  }
  if (step === "clientAccess" && !s.clientAccess) {
    s.error = "Choose whether they may see insurance financial amounts.";
    renderUserAddWizard();
    return;
  }
  // Gate 2 of 4: the book screen cannot be walked past empty.
  if (step === "book" && !normalizeUserScopeList(s.payerBook).length) {
    s.error = "Add at least one payer. This is their book — with none, they sign in to no claims at all.";
    renderUserAddWizard();
    return;
  }
  if (step === "name" || step === "person") {
    const name = String(s.name || "").trim().replace(/\s+/g, " ");
    const email = String(s.email || "").trim();
    const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
    if (!name) {
      s.error = "Enter their name.";
      renderUserAddWizard();
      return;
    }
    if (users.some((u) => String(u.name).toLowerCase() === name.toLowerCase())) {
      s.error = "Someone with that name already exists.";
      renderUserAddWizard();
      return;
    }
    if (!emailOk) {
      s.error = "Enter a valid email — the secure invite is sent there.";
      renderUserAddWizard();
      return;
    }
  }
  if (step === "review") {
    confirmUserAddWizard();
    return;
  }
  s.stepIndex += 1;
  renderUserAddWizard();
}

async function confirmUserAddWizard() {
  if (!isTrustedUserManagerSession()) return;
  const s = userAddWizardState;
  if (!s || s.sending) return;
  const draft = userAddWizardDraft();
  if (!userManagerCanAssignRole(draft.role)) {
    s.error = "You cannot assign a role above your own user-management authority.";
    renderUserAddWizard();
    return;
  }
  if (!userManagerCanAssignScope(draft)) {
    s.error = "Pick only companies already in your own access scope.";
    renderUserAddWizard();
    return;
  }
  if (!draft.name) {
    s.error = "Enter their name.";
    renderUserAddWizard();
    return;
  }
  if (users.some((u) => String(u.name).toLowerCase() === draft.name.toLowerCase())) {
    s.error = "Someone with that name already exists.";
    renderUserAddWizard();
    return;
  }
  if (draft.role !== "SuperAdmin" && !draft.orgIds.length && !draft.clientNames.length) {
    s.error = "Pick at least one company first.";
    renderUserAddWizard();
    return;
  }
  // M3. A company the picker cannot name must never reach an account. The
  // directory gate should make this unreachable; it is a gate rather than an
  // assumption because the failure it stops is an account pointed at an org
  // nobody in the building can identify.
  if (userAddWizardUnnamedDraftOrgIds(draft).length) {
    s.error = "One of the chosen companies has no name on file. Name it under Manage Groups, then add this person.";
    renderUserAddWizard();
    return;
  }
  if (userAddWizardGrantsEveryCompany(draft) && !s.everyCompanyAcknowledged) {
    s.error = "This grants every company you can grant. Tick the box to confirm that is what you mean.";
    renderUserAddWizard();
    return;
  }
  // Gate 3 of 4. Re-derived from the assembled draft's ROLE, not from the
  // wizard's `type`, so a flow that never showed the book screen still cannot
  // submit an account the API is about to refuse.
  const bookError = wizardDraftBookError(draft);
  if (bookError) {
    s.error = bookError;
    renderUserAddWizard();
    showToast("This account needs a book before it can be created.");
    return;
  }
  // Every added user gets a real invite emailed to them, so a valid email is
  // required up front (clearer than a server-side rejection).
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email)) {
    s.error = "Enter a valid email address — the secure invite is sent there.";
    renderUserAddWizard();
    showToast("A valid email is required to invite someone.");
    return;
  }
  // Adding a user ALWAYS sends a real, server-side secure invite. If we cannot
  // reach the portal API with an admin token, refuse — never pretend an invite
  // was sent (the old flow silently added a local-only row and toasted success).
  if (!portalInviteApiReady()) {
    s.sending = false;
    s.error = portalInviteApiBase()
      ? "Your session cannot send invitations. Sign in again as an administrator and retry."
      : "User invitations are temporarily unavailable.";
    userAddWizardInviteRequestKey = "";
    renderUserAddWizard();
    showToast("Can't send the invitation. Sign in again and retry.");
    return;
  }
  s.sending = true;
  s.error = "";
  const inviteRequestParts = { kind: "user-add-invite", sequence: ++userAddWizardInviteRequestSeq, draft, base: portalInviteApiBase() };
  const inviteRequestKey = workflowAccessScopedRequestKey(inviteRequestParts);
  userAddWizardInviteRequestKey = inviteRequestKey;
  renderUserAddWizard();
  const result = await sendPortalInviteRequest(draft);
  if (userAddWizardState !== s || userAddWizardInviteRequestKey !== inviteRequestKey || workflowAccessScopedRequestKey(inviteRequestParts) !== inviteRequestKey) return;
  if (!result.sent && result.created) {
    // The server created the account but the invite email failed to send.
    // Keep the truth: the user exists as pending_invite, and the admin should
    // use Resend invite from Manage Users. Never pretend the email went out.
    await refreshAuthoritativeUserGovernance({ force: true });
    s.sending = false;
    s.done = true;
    s.inviteSent = false;
    s.inviteEmailFailed = true;
    userAddWizardInviteRequestKey = "";
    renderUserAddWizard();
    showToast("User created — the invite email didn't go out. Use Resend invite from Manage Users.");
    return;
  }
  if (!result.sent) {
    s.sending = false;
    s.error = portalInviteFailureMessage(result);
    userAddWizardInviteRequestKey = "";
    renderUserAddWizard();
    showToast("Secure invite failed.");
    return;
  }
  await refreshAuthoritativeUserGovernance({ force: true });
  s.sending = false;
  s.done = true;
  s.inviteSent = true;
  userAddWizardInviteRequestKey = "";
  renderUserAddWizard();
  showToast(result.body?.teamSaved === false ? "Secure invite sent, but their team did not save. Set it on the Org Map." : "Secure invite sent.");
}


// Shared invite transport: the wizard and the inspector form both create
// app-owned invites through the same endpoint with the same payload shape.
function portalInviteApiReady() {
  return Boolean(portalInviteApiBase() && (workflowApiToken() || trackerApiToken()) && typeof fetch === "function");
}

async function sendPortalInviteRequest(draft) {
  try {
    const apiBase = portalInviteApiBase();
    const apiToken = workflowApiToken() || trackerApiToken();
    const response = await fetch(`${apiBase}/api/users/invites`, {
      method: "POST",
      cache: "no-store",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${apiToken}`
      },
      body: JSON.stringify(portalInvitePayloadFromDraft(draft))
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.error) {
      const error = new Error(body.message || body.reason || body.error || `HTTP ${response.status}`);
      return { sent: false, evidence: {}, error, status: response.status, body };
    }
    // Manage Users P1: a 200 can still mean "user created but the invite email
    // did not send" (created === true, sent === false). Report that honestly
    // instead of treating every 200 as a delivered invite.
    if (body.created === true && body.sent === false) {
      return { sent: false, created: true, emailError: String(body.emailError || body.emailReason || body.message || ""), evidence: portalInviteEvidenceFromResponse(body), status: response.status, body };
    }
    return { sent: true, evidence: portalInviteEvidenceFromResponse(body), status: response.status, body };
  } catch (error) {
    return { sent: false, evidence: {}, error, status: 0, body: {} };
  }
}

function portalInviteFailureMessage(result) {
  const status = result && result.status;
  // SAY WHICH WALL. A company the directory simply does not hold came back as
  // "you do not have permission", which sent admins hunting for a permission
  // they already had instead of naming or re-activating the company.
  const reason = String((result && result.body && (result.body.reason || result.body.error)) || "");
  if (reason === "target_org_not_found") return "That company is not an active client company in this portal, so nobody can be given access to it. Add or re-activate it under Manage Groups, then try again.";
  if (reason === "org_directory_unavailable") return "The company directory is unavailable right now, so access cannot be granted safely. Try again shortly.";
  if (reason === "target_org_out_of_scope") return "That company is outside your own access, so you cannot grant it. Ask a Super Admin to add this person.";
  if (reason === "target_org_scope_required") return "Pick at least one company before adding this person.";
  if (status === 401) return "Your admin session expired. Sign in again, then re-send the invite.";
  if (status === 403) return "You do not have permission to invite that role or organization scope.";
  if (status === 404 || status === 503) return "User invites are not set up on the server yet. Contact your administrator.";
  if (status === 0) return "Could not reach the portal server. Check your connection and try again.";
  const detail = result && result.error && result.error.message;
  return detail ? `The invite could not be sent: ${detail}` : "The invite could not be sent. Please try again.";
}

// The shareable-link transport. Its own route, not the create/resend one: the
// server records "an admin was handed a live setup link" separately from "an
// invite email went out", and only one of those puts a credential in a chat
// window. Sends the addressing fields only - the server holds the invite row.
async function sendPortalInviteLinkRequest(user) {
  const payload = portalInvitePayloadFromDraft(user);
  try {
    const apiBase = portalInviteApiBase();
    const apiToken = workflowApiToken() || trackerApiToken();
    const response = await fetch(`${apiBase}/api/users/invites/link`, {
      method: "POST",
      cache: "no-store",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${apiToken}`
      },
      body: JSON.stringify(user.inviteId ? { email: payload.email, inviteId: user.inviteId } : { email: payload.email, tenantId: payload.tenantId, orgId: payload.orgId })
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.error || !body.link) {
      return { ok: false, message: portalInviteLinkFailureMessage(response.status, body) };
    }
    return { ok: true, link: String(body.link), expiresAtUtc: String(body.expiresAtUtc || "") };
  } catch (_error) {
    return { ok: false, message: portalInviteLinkFailureMessage(0, {}) };
  }
}

function portalInviteLinkFailureMessage(status, body = {}) {
  // The audit refusals are named first and named plainly. The server will not
  // mint a link it cannot record, so "try again" would send an admin round a
  // loop that cannot close until the access log is writable again.
  if (body.error === "invite_link_audit_unavailable" || body.error === "invite_link_not_audited") {
    return "No link was created: the portal could not record it in the access log, and an unlogged link is never handed out.";
  }
  if (status === 401) return "Your admin session expired. Sign in again, then ask for the link.";
  if (status === 403) return "You do not have permission to create a link for that user scope.";
  if (status === 404) return "There is no outstanding invite for this person to share. Send an invite first.";
  if (status === 503) return "Shareable invite links are not set up on the server yet. Contact your administrator.";
  if (status === 0) return "Could not reach the portal server. Check your connection and try again.";
  const detail = String(body.message || body.reason || body.error || "");
  return detail ? `The link could not be created: ${detail}` : "The link could not be created. Please try again.";
}

// Populate the user picker with users matching the current role. Lets
// the demo viewer see how different per-user org scopes shape the data.
function populateUserSelect() {
  const sel = els.userSelect;
  if (!sel) return;
  const role = canonicalRole(state?.role);
  // Disabled users cannot sign in (enforced at the auth layer too), so the
  // demo user picker must not offer them as an active identity.
  const matching = users.filter((u) => canonicalRole(u.role) === role && normalizeUserStatus(u.status) !== "disabled");
  if (!matching.length) { sel.innerHTML = ""; sel.disabled = true; return; }
  sel.disabled = false;
  sel.innerHTML = matching.map((u) => {
    const clientNames = normalizeUserScopeList(u.clientNames);
    const orgIds = normalizeUserScopeList(u.orgIds);
    const scope = clientNames.length
      ? `${clientNames.length} tenant${clientNames.length === 1 ? "" : "s"}`
      : (orgIds.length ? `${orgIds.length} org${orgIds.length === 1 ? "" : "s"}` : (role === "SuperAdmin" ? "All tenants" : "No tenant scope"));
    return `<option value="${escapeHtml(u.name)}" title="${escapeHtml([...clientNames, ...orgIds.map((orgId) => portalCompanyLabel(orgId))].join(", "))}">${escapeHtml(u.name)} - ${escapeHtml(scope)}</option>`;
  }).join("");
  if (state.activeUserName && matching.some((u) => u.name === state.activeUserName)) {
    sel.value = state.activeUserName;
  } else {
    state.activeUserName = matching[0].name;
    sel.value = state.activeUserName;
  }
}

function updateUserScopeHint() {
  const host = els.userScopeHint;
  if (!host) return;
  if (!showsInternalDiagnostics()) { host.style.display = "none"; host.textContent = ""; return; }
  host.style.display = "";
  const role = canonicalRole(state?.role);
  const scopedUser = currentUser();
  const clientScope = normalizeUserScopeList(scopedUser?.clientNames);
  const orgScope = normalizeUserScopeList(scopedUser?.orgIds);
  if (role === "SuperAdmin") {
    host.textContent = "Cross-tenant: every client/tenant visible.";
    return;
  }
  const visibleScope = clientScope.length ? clientScope : orgScope.map((orgId) => orgId.replace(/^ORG-/, ""));
  if (!visibleScope.length) {
    host.textContent = "No tenant scope - would see zero claims in production.";
    return;
  }
  host.textContent = `Scope: ${visibleScope.length} tenant${visibleScope.length === 1 ? "" : "s"} (${visibleScope.join(", ")})`;
}

// Map any legacy/persisted role to one of the four canonical roles.
// Unknown or missing roles resolve to Client (least privilege) — admin
// authority must always be explicit, never a fallback. Demo boot still
// lands on SuperAdmin because defaultState() sets the role explicitly.
function canonicalRole(role) {
  if (!role) return "Client";
  if (roleProfiles[role]) return role;
  return __legacyRoleAlias[role] || "Client";
}

// "Is this principal outside TMS?" — asked ONCE, here, so every client
// protection covers ClientBiller by construction instead of by someone
// remembering. Mirrors isExternalClientRole / EXTERNAL_CLIENT_ROLES in
// backend/portalRoleCatalog.js.
//
// Deliberately routes through canonicalRole, so a legacy alias (Provider,
// ReadOnly) and an unknown role answer the same way here as they do everywhere
// else in the UI. The literal role === "Client" checks this replaces answered
// "no" for those and quietly skipped the very protections least-privilege had
// just applied.
function isExternalClientRole(role) {
  const canonical = canonicalRole(role);
  return canonical === "Client" || canonical === "RedactedClient";
}

// Admin authority must come from the authenticated login when one is
// present. The demo Role switcher flips state.role client-side; for a real
// session only the login payload's role can unlock admin actions (user
// management, directory export). Pure demo sessions (no authUserScope)
// keep trusting the switchable role.
function isRedactedClientRole(role = state?.role) {
  return canonicalRole(role) === "RedactedClient";
}

function isTrustedSuperAdminSession() {
  if (state?.authUserScope) return canonicalRole(state.authUserScope.role) === "SuperAdmin";
  return canonicalRole(state?.role) === "SuperAdmin";
}

function isTrustedUserManagerSession() {
  const role = canonicalRole(state?.authUserScope?.role || state?.role);
  if (!["SuperAdmin", "StatesideBiller"].includes(role)) return false;
  const profile = roleProfiles[role];
  return Boolean(profile && profile.permissions.includes(PERMISSIONS.MANAGE_USERS));
}

function userManagementRoleRank(role) {
  return { SuperAdmin: 4, StatesideBiller: 3, OffshoreBiller: 2, Client: 1, RedactedClient: 1  }[canonicalRole(role)] || 0;
}

function userManagerCanAssignRole(role) {
  const actorRole = canonicalRole(state?.authUserScope?.role || state?.role);
  const targetRank = userManagementRoleRank(role);
  const actorRank = userManagementRoleRank(actorRole);
  return targetRank > 0 && actorRank >= 2 && targetRank <= actorRank;
}

function userManagerAssignableRoles() {
  return Object.keys(roleProfiles).filter(userManagerCanAssignRole);
}

function userManagerCanAssignScope(draft = {}) {
  const actorRole = canonicalRole(state?.authUserScope?.role || state?.role);
  if (actorRole === "SuperAdmin") return true;
  const targetOrgIds = normalizeUserScopeList(draft.orgIds);
  if (!targetOrgIds.length || normalizeUserScopeList(draft.clientNames).length) return false;
  const actorScope = currentAccessProfile().scope || {};
  const actorOrgIds = new Set(normalizeUserScopeList(actorScope.orgIds));
  return targetOrgIds.every((orgId) => actorOrgIds.has(orgId));
}

// Authenticated login scope applies only while the active role still matches
// the role the user actually logged in with; the demo role-switcher flips
// state.role without re-login, and must fall back to the demo-store path.
function authenticatedScopeForRole(role) {
  const scope = state?.authUserScope;
  if (!scope || !scope.role || scope.role !== role) return null;
  return scope;
}

function scopedTenantBoundary(source = {}, role = state?.role) {
  const canonicalRoleName = canonicalRole(role || source.role);
  const internalOrgWide = canonicalRoleName === "StatesideBiller" || canonicalRoleName === "OffshoreBiller";
  return {
    allOrgs: false,
    crossTenant: false,
    orgIds: normalizeUserScopeList(source.orgIds),
    clientNames: normalizeUserScopeList(source.clientNames),
    ...(internalOrgWide ? {} : {
      payerNames: normalizeUserScopeList(source.payerNames),
      assignedOnly: source.assignedOnly === true,
      assignedName: String(source.assignedName || "").trim()
    }),
    followUpActionAccessConfigured: source.followUpActionAccessConfigured === true,
    followUpActions: normalizeUserScopeList(source.followUpActions),
    teamIds: normalizeUserScopeList(source.teamIds)
  };
}

function currentAccessProfile() {
  const role = canonicalRole(state?.role);
  const profile = roleProfiles[role] || roleProfiles.Client;
  if (role === "SuperAdmin") return profile;

  const authScope = authenticatedScopeForRole(role);
  const user = (typeof currentUser === "function" ? currentUser() : null) || {};
  if (authScope) {
    return {
      ...profile,
      user: canonicalRole(user.role) === role && user.name === state?.activeUserName ? user : null,
      scope: scopedTenantBoundary(authScope, role)
    };
  }

  const userRoleMatches = canonicalRole(user.role) === role;
  const activeUserMatches = Boolean(state?.activeUserName && user.name === state.activeUserName && userRoleMatches);
  return {
    ...profile,
    user: userRoleMatches ? user : null,
    scope: scopedTenantBoundary(activeUserMatches ? user : (profile.scope || {}), role)
  };
}

function hasPermission(permission) {
  return currentAccessProfile().permissions.includes(permission);
}

function canAccessView(view) {
  if (view === "companyOversight") return canOpenCompanyOversight();
  if (view === "backupHistory") {
    const scope = currentAccessProfile().scope || {};
    return isTrustedSuperAdminSession() && canonicalRole(state.role) === "SuperAdmin"
      && scope.allOrgs === true && scope.crossTenant === true && hasPermission(PERMISSIONS.SECURITY_AUDIT);
  }
  // Per PORTAL_DIRECTIVES: nav visibility is driven by the role's permissions.
  // SuperAdmin (allPermissions) sees every surface; StatesideBiller sees the
  // operational stack. OffshoreBiller sees its explicitly granted work surfaces
  // and has no Patient Statements grant under Nathan's August 20 decision.
  // Client roles see only the surfaces in their current permission sets.
  // We do NOT hard-cap nav to a curated subset here — the role permission set
  // is the gate. (uiNavViews is kept as legacy metadata in case other call
  // sites still want a "which views show in the sidebar at all" answer, but
  // it's no longer the access gate.)
  // The role-aware My Work surface is on by default. A deployment may set
  // window.TMS_ASSIGNMENTS_NAV = false as a reversible emergency kill switch;
  // ordinary sessions must not need a browser flag to reach current workflow.
  if (view === "assignments" && !assignmentsNavEnabled()) return false;
  // Review (the old offshore approval queue under Operations) does not do
  // useful work. Hide the tab and bounce deep links to All Data. VIEW_REVIEWS
  // stays on the role so we do not invent a second permission story.
  if (view === "reviews") return false;
  // A view may declare EITHER a single permission or a list of alternatives
  // (Client v2 §3): the same surface can be reachable by a biller grant or a
  // client grant without either role inheriting the other's meaning. Still
  // deny-by-default — an unknown view or an empty list grants nothing.
  return viewPermissionGateSatisfied(view, hasPermission);
}

// "Is this surface this role's?" -- the classification half of nav visibility.
// Deny-by-default is NOT this function's job: canAccessView has already denied
// anything the role holds no grant for. This only withholds a surface that
// belongs to a different audience.
function viewOfferedToRole(view) {
  // A permission can outlive a retired/merged destination. Only views present
  // in the current navigation model are routable; this makes stale saved URLs
  // such as the retired Follow-Up Rules page fall back to the role-aware home.
  if (!navigationSections.some((section) => (section.views || []).includes(view))) return false;
  const audience = VIEW_AUDIENCE[view] || DEFAULT_VIEW_AUDIENCE;
  // A retired surface belongs to NOBODY. Deliberately checked before the role
  // classification, so no role - present or invented next month - can be the
  // exception that puts it back in a sidebar.
  if (audience === "retired") return false;
  if (audience === "external-client") return isExternalClientRole(state?.role);
  if (audience === "internal-staff") return !isExternalClientRole(state?.role);
  return true;
}

// THE ONE GATE for the sidebar, the landing and routing: permitted AND theirs.
// canAccessView stays the PERMISSION answer on its own, because the access
// directory and the security audit must keep reporting what a role actually
// HOLDS -- reporting a surface as ungranted because it is merely unlisted would
// be a lie told to the one screen built to be believed.
function viewIsAvailable(view) {
  return canAccessView(view) && viewOfferedToRole(view);
}

function assignmentsNavEnabled() {
  try {
    if (typeof window !== "undefined" && window.TMS_ASSIGNMENTS_NAV != null) return Boolean(window.TMS_ASSIGNMENTS_NAV);
  } catch (_) {}
  return true;
}

// Keep the retired Planner-style offshore board independent from the current
// My Work navigation. Offshore All Data stays the real org-scoped tracker even
// though My Work is available as its own destination. The old board can be
// restored only with its own explicit diagnostic flag.
function offshoreBoardMode() {
  try {
    return canonicalRole(state?.role) === "OffshoreBiller"
      && typeof window !== "undefined"
      && window.TMS_LEGACY_OFFSHORE_BOARD === true;
  } catch (_) {
    return false;
  }
}

function accessibleViews() {
  return Object.keys(viewPermissions).filter(viewIsAvailable);
}

function accessibleNavigationSections() {
  return navigationSections
    .map((section) => ({
      ...section,
      views: section.views.filter(viewIsAvailable)
    }))
    .filter((section) => section.views.length > 0);
}

// Deny by default, exactly like viewIsAvailable: an entry with no declared gate
// is not "ungated", it is denied.
function navActionIsAvailable(action) {
  const gate = navActionGates[action];
  return typeof gate === "function" ? Boolean(gate()) : false;
}

// Same shape as accessibleNavigationSections, and the same final filter for the
// same reason: a section whose entries are all denied is not returned at all,
// so its label has nothing to key on and never renders.
function accessibleNavActionSections() {
  return navActionSections
    .map((section) => ({
      ...section,
      actions: section.actions.filter(navActionIsAvailable)
    }))
    .filter((section) => section.actions.length > 0);
}

// THE ONE PASS THAT DECIDES WHAT THE SIDEBAR SHOWS. Split out of renderView so a
// contract can execute it against the shipped markup: "the Manage users entry is
// absent for a StatesideBiller, and no heading stands over an empty body" is a
// claim about rendered nodes, not about a predicate, and only this function
// turns one into the other.
function applyNavVisibility(items = [], sections = []) {
  // A UNION, deliberately: one key can be carried by both kinds of entry.
  // "governance" now holds only Manage Users / Manage Groups actions. Keeping
  // the section decision as a union lets action-only sections use this same
  // visibility path, while roles with neither action get no empty heading.
  const visibleSections = new Set([
    ...accessibleNavigationSections().map((section) => section.key),
    ...accessibleNavActionSections().map((section) => section.key)
  ]);
  const actionSectionKeys = new Set(navActionSections.map((section) => section.key));
  items.forEach((item) => {
    const action = item.dataset.navAction;
    const allowed = action ? navActionIsAvailable(action) : viewIsAvailable(item.dataset.view);
    item.hidden = !allowed;
    if (action) applyNavDenyBelt(item, allowed);
  });
  sections?.forEach((section) => {
    const key = section.dataset.navSection;
    const allowed = visibleSections.has(key);
    section.hidden = !allowed;
    if (actionSectionKeys.has(key)) applyNavDenyBelt(section, allowed);
  });
}

// The [hidden] attribute only hides a .nav-item because styles.css says so
// (.nav-item is display:flex, which beats the UA default - that is what the
// rule at styles.css ".nav-item[hidden]" exists for). For an ADMIN group that is
// one stylesheet edit away from being on screen, so the denial is also written
// inline. It only ever clears what it set, so the collapsed-sidebar rule keeps
// owning the display of a group this function allows.
function applyNavDenyBelt(node, allowed) {
  if (!node || !node.style) return;
  if (!allowed) {
    node.style.display = "none";
    return;
  }
  if (node.style.display === "none") node.style.display = "";
}

function firstAccessibleView() {
  // NATHAN'S RULE (2026-09-17): after login, billers, admins and every other
  // INTERNAL role land on the HOME page - My Work (view key "assignments").
  // EXTERNAL CLIENTS land on Reports when they can open their reports, and on
  // Authorizations when they cannot. Every branch below asks viewIsAvailable
  // first, so no role - present or invented next month - can be routed into a
  // surface it is not allowed to open; the ordered fallbacks exist only so a
  // role that holds neither preferred surface still lands somewhere real.
  if (isExternalClientRole(state?.role)) {
    if (viewIsAvailable("reports")) return "reports";
    // A Redacted Client holds no reports grant. Under Nathan's rule its home is
    // Authorizations - a surface it both views and manages - which is a DECISION
    // rather than whichever board happens to sort first. Client Home sat here
    // until 2026-08-21 and All Data from then until today; All Data stays below
    // as the next fallback so the older answer is still reachable for a client
    // role that cannot open Authorizations.
    if (viewIsAvailable("authorizations")) return "authorizations";
    if (viewIsAvailable("workbench")) return "workbench";
    if (viewIsAvailable("statements")) return "statements";
    return accessibleViews()[0] || "workbench";
  }
  // My Work is the internal home page. All Data remains the fallback for a
  // session that cannot open it - a role without view:assignments, or the
  // reversible TMS_ASSIGNMENTS_NAV=false emergency kill switch - and
  // accessibleViews()[0] catches anything narrower than both.
  if (viewIsAvailable("assignments")) return "assignments";
  if (viewIsAvailable("workbench")) return "workbench";
  return accessibleViews()[0] || "workbench";
}

// If a mid-session role switch or stale deep link points at a surface the active
// role cannot open, fall back silently to THAT ROLE'S HOME - whatever
// firstAccessibleView resolves for it under Nathan's 2026-09-17 rule - and
// forget the unreachable value. Deliberately not a hard-coded "workbench": All
// Data stopped being the universal landing on 2026-09-17, and hard-coding it
// here would drop an internal user one screen away from their home and would
// hand an external client a surface the landing rule no longer chooses.
// Ordinary authenticated boot already selects firstAccessibleView before URL
// state is applied, so this guard primarily protects navigation and role changes.
function normalizeCurrentView() {
  if (viewIsAvailable(state.view)) return;
  const next = firstAccessibleView();
  if (next === state.view) return;
  state.view = next;
  saveState();
}

// Boot-only fallback for unauthenticated/local preview mode. Authenticated boot
// already selects firstAccessibleView; applyUrlState runs before this function,
// so an explicit valid ?view= deep link continues to win.
function applyBootDefaultView() {
  if (__bootViewIsDefault) {
    state.view = firstAccessibleView();
    if (state.view === "reports" && isExternalClientRole(state?.role)) reportArState.view = "ar";
  }
  __bootViewIsDefault = false;
}

function navigateToHome() {
  const home = firstAccessibleView();
  if (home === "reports") reportArState.view = "ar";
  navigateToView(home);
}

function pushViewHistoryState(view) {
  if (!window.history?.pushState || !window.location?.href) return;
  try {
    const url = new URL(window.location.href);
    const currentView = url.searchParams.get("view") === "client" ? "reports" : url.searchParams.get("view");
    if (currentView === view && !(view !== "reports" && url.searchParams.has("clientReport"))) return;
    url.searchParams.set("view", view);
    if (view !== "reports") url.searchParams.delete("clientReport");
    window.history.pushState({}, "", `${url.pathname}?${url.searchParams.toString()}${url.hash || ""}`);
  } catch (_) {
    // Navigation must remain usable if an embedding harness provides a partial
    // Location/History implementation.
  }
}

function navigateToView(view) {
  cancelClaimScreenshot();
  closeTransientOverlays();
  if (!canAccessView(view)) {
    denyAction(viewPermissions[view] || "view:unknown", `open ${view || "unknown"} view`);
    normalizeCurrentView();
    render();
    return;
  }
  // Permitted, but not this role's surface (VIEW_AUDIENCE) -- a deep link or a
  // stale ?view= into somebody else's nav. NOT denyAction: the grant is real, so
  // logging a permission failure would put a false denial in the access audit.
  // Leave them where they are if that is still reachable, or send them to their
  // own landing if it is not.
  if (!viewOfferedToRole(view)) {
    normalizeCurrentView();
    render();
    return;
  }
  if (state.view !== view) denialIntelligenceHideVisibleHistory();
  // The My Work count reads are NOT cancelled on the way out any more: since
  // 2026-09-23 they also feed the sidebar badge, which is on every screen.
  // Each read still carries its own 12 s deadline.
  const enteringMyWork = view === "assignments" && state.view !== "assignments";
  state.view = view;
  // OPENING A PAGE IS AN EXPLICIT REQUEST TO PAINT IT. The publication guard
  // defers background repaints while the reader is interacting; the navigation
  // click itself counted as interaction, so a page opened while a new hourly
  // publication was pending painted nothing and never asked for its data
  // (Credentialing came up blank on a client's first visit, 2026-10-13).
  try { trackerPublicationWatch.explicitRenderUntil = Math.max(trackerPublicationWatch.explicitRenderUntil || 0, Date.now() + 1500); } catch (_) { /* watcher not initialised yet: nothing to defer */ }
  // Each module is a real browser destination. Sidebar navigation previously
  // changed only in-memory state, so Back left the single-entry portal and
  // showed a blank page. Keep ?view= synchronized so Back, Forward, refresh,
  // copied links, and recovery all resolve to the same rendered module.
  pushViewHistoryState(view);
  // Optional-chained on window.location: this branch used to fire only for the
  // deleted "client" key, which nothing navigated to programmatically. It now
  // fires on every navigation to Reports, including harnesses whose window stub
  // carries no location.
  if (view === "reports" && !(new URLSearchParams(window.location?.search || "").get("clientReport"))) {
    state.clientReportOpenKey = "";
  }
  // A cached error from an earlier visit is not a verdict. Entering the page
  // again is the user asking once more; keep a successful paint.
  if (view === "missingRates" && feeScheduleMissingApiState.status === "error") {
    feeScheduleMissingApiState.status = "idle";
  }
  if (view === "statements" && statementPreviewState.status === "error") {
    statementPreviewState.status = "idle";
    statementPreviewState.key = "";
  }
  if (shouldCollapseSidebarAfterNavigation()) state.sidebarCollapsed = true;
  saveState();
  render();
  resetViewScrollPosition();
  // AFTER the paint: My Work has just rendered from the background cache, and
  // only now does anything go to the server to check that cache.
  if (enteringMyWork && typeof noteMyWorkOpened === "function") noteMyWorkOpened();
}

function resetViewScrollPosition() {
  if (typeof window === "undefined" || typeof window.scrollTo !== "function") return;
  try {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  } catch (_) {
    window.scrollTo(0, 0);
  }
}

function shouldCollapseSidebarAfterNavigation() {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(max-width: 960px)").matches;
}

function normalizeResponsiveLayoutState() {
  if (shouldCollapseSidebarAfterNavigation()) state.sidebarCollapsed = true;
}

function closeTransientOverlays() {
  try { closeColumnFilterMenu(); } catch (_) {}
  try { closeDrawer(); } catch (_) {}
  try { closeAuthorizationDrawer(); } catch (_) {}
  try { closeWorkflowNotifications({ restoreFocus: false }); } catch (_) {}
  try { endTour(); } catch (_) {}
  try {
    closeUserMgmtModal({ restoreFocus: false });
  } catch (_) {}
}

function canAccessClaim(claim) {
  const scope = currentAccessProfile().scope || {};
  if (scope.allOrgs) return true;
  const orgIds = normalizeUserScopeList(scope.orgIds);
  const clientNames = normalizeUserScopeList(scope.clientNames);
  if (!orgIds.length && !clientNames.length) return false;
  const tenantMatchesClient = clientNames.includes(String(claim.client || ""));
  const tenantMatchesOrg = orgIds.includes(String(claim.orgId || ""));
  if (clientNames.length && !tenantMatchesClient) return false;
  if (orgIds.length && !tenantMatchesOrg) return false;
  const payerNames = normalizeUserScopeList(scope.payerNames);
  if (payerNames.length && !payerNames.includes(String(claim.primaryPayer || "")) && !payerNames.includes(String(claim.secondaryPayer || ""))) return false;
  const providerNames = normalizeUserScopeList(scope.providerNames);
  if (providerNames.length && !providerNames.includes(String(claim.renderingProvider || claim.provider || ""))) return false;
  if (scope.assignedOnly) {
    const assignedName = String(scope.assignedName || "").trim();
    if (!assignedName || String(claim.assignedTo || "").trim() !== assignedName) return false;
  }
  return true;
}

function canActOnClaim(claim, permission) {
  return Boolean(claim && canAccessClaim(claim) && hasPermission(permission));
}

function denyAction(permission, detail = "permission denied") {
  auditAuthorizationFailure(permission, detail);
  showToast("You do not have permission for that action.");
  return false;
}

function requireClaimPermission(claimId, permission, detail = claimId) {
  const claim = getClaim(claimId);
  if (!canActOnClaim(claim, permission)) return denyAction(permission, detail);
  return claim;
}

function auditAuthorizationFailure(permission, detail) {
  if (!state?.audit) return;
  state.audit = [
    ...state.audit.slice(-199),
    { at: timestamp(), by: activeInternalUser(), text: `Authorization denied: ${permission} (${detail})` }
  ];
  saveState();
}

function render() {
  resetDerivedCaches();
  // GLOBAL CLIENT SWITCHER: push the one knob into each surface's own filter
  // state BEFORE anything builds a query, so a page opened for the first time
  // after the pick is narrowed on its first paint rather than flashing every
  // client first.
  applyActiveClientGroupToSurfaces();
  if (els.roleSelect) els.roleSelect.value = state.role;
  if (els.rowModeSelect) els.rowModeSelect.value = state.rowMode || "service";
  normalizeCurrentView();
  applyLayoutState();
  renderView();
  if (state.view === "workbench") renderMetrics();
  renderActivePanel();
  applyPermissionChrome();
  window.requestAnimationFrame(installWideTableScrollRails);
  // After renderActivePanel(), because renderReportFilterOptions() rebuilds
  // the Reports tenant dropdown on every pass and would otherwise hand back
  // an unlocked second knob.
  renderClientScopeSwitcher();
  enforceClientScopeSelectLocks();
  renderClientScopeNotice();
  renderClientScopeModalNotes();
  renderWorkflowNotificationChrome();
  updateActingAsBanner(); // UI-100 #59
}

function renderActivePanel() {
  syncReportReadContext();
  if (state.view !== "workbench") cancelTrackerFilterPreparation();
  if (state.view === "workbench") {
    renderSlicers();
    renderClaimsTable();
  } else if (state.view === "assignments") {
    renderAssignments();
  } else if (state.view === "reviews") {
    renderReviews();
  } else if (state.view === "reports") {
    renderReports();
  } else if (state.view === "trackerExport") {
    renderTrackerExportStudio();
  } else if (state.view === "feeSchedules") {
    renderFeeSchedules();
  } else if (state.view === "missingRates") {
    renderMissingRates();
  } else if (state.view === "followUpRules") {
    renderFollowUpRules();
  } else if (state.view === "queryMap") {
    renderQueryMap();
  } else if (state.view === "companyOversight") {
    renderCompanyOversight();
  } else if (state.view === "backupHistory") {
    window.TMSBackupHistory?.render();
  } else if (state.view === "feedback") {
    renderFeedbackAdmin();
  } else if (state.view === "audit") {
    renderAudit();
  } else if (state.view === "clientHome") {
    renderClientHome();
  } else if (state.view === "statements") {
    renderStatements();
  } else if (state.view === "authorizations") {
    renderAuthorizations();
  } else if (state.view === "credentialing") {
    renderCredentialing();
  } else if (state.view === "clientActionPending") {
    renderClientActionPending();
  } else if (state.view === "denialIntelligence") {
    renderDenialIntelligence();
  } else if (state.view === "training") {
    renderTraining();
  }
}

// Internal biller chrome that must never render for an external client role.
// Enforced in JS with an INLINE display, which beats a stylesheet rule, so this
// holds no matter what styles.css currently keys on.
const EXTERNAL_CLIENT_HIDDEN_CHROME = [
  ".queue-board",
  ".biller-workstrip",
  ".bulk-card",
  "#workflowSyncStatus",
  "#clientTrackerExport"
];

function enforceExternalClientChrome() {
  const hide = isExternalClientRole(state.role);
  EXTERNAL_CLIENT_HIDDEN_CHROME.forEach((selector) => {
    document.querySelectorAll(selector).forEach((node) => {
      if (hide) {
        node.style.display = "none";
        node.setAttribute("aria-hidden", "true");
        return;
      }
      // Only CLEAR what this function set. Writing "" unconditionally stomped
      // #clientTrackerExport for internal roles, whose own view renderer owns
      // that node's display — and the microtask re-run made it stomp AFTER the
      // renderer had spoken.
      if (node.style.display === "none" && node.getAttribute("aria-hidden") === "true") node.style.removeProperty("display");
      node.removeAttribute("aria-hidden");
    });
  });
}

function applyLayoutState() {
  document.querySelector(".app-shell")?.classList.toggle("sidebar-collapsed", booleanFlagValue(state.sidebarCollapsed));
  // Expose current view on body for view-scoped CSS (e.g. hide the AR metric
  // strip outside the workbench).
  document.body.dataset.view = state.view || "workbench";
  // Expose role on body so role-scoped CSS can hide chrome an OffshoreBiller
  // doesn't need (filter row, bulk-card, pager when the queue is small).
  document.body.dataset.role = canonicalRole(state.role);
  // EXTERNAL-CLIENT CHROME GATE (2026-08-03).
  //
  // styles.css hides the internal biller chrome with `body[data-role="Client"]`
  // selectors. That is the same literal-string trap `isExternalClientRole` was
  // written to close on the server: the moment a SIBLING client role exists,
  // every one of those rules stops matching and a ClientBiller lands on All Data
  // looking at the Work Queues board, the biller workstrip and the workflow sync
  // panel. No write opens (each control is separately permission-gated), but the
  // internal work-queue vocabulary is TMS work product and has no business on a
  // client's screen.
  //
  // The attribute is emitted for the stylesheet to key on once it is re-pointed;
  // enforceExternalClientChrome() below does not wait for that, because a role
  // gate that lives only in a stylesheet is a role gate that silently lapses.
  document.body.dataset.externalClient = isExternalClientRole(state.role) ? "on" : "off";
  enforceExternalClientChrome();
  // The local-fallback ops strip is role-gated (see updateDemoDataCallout).
  // It used to be evaluated once at boot, so switching INTO a client role left
  // an internal warning standing on the client's screen until a reload.
  try { updateDemoDataCallout(); } catch (e) { console.error("[tms.render] updateDemoDataCallout", e); }
  // Re-run after the current render settles: some of these nodes are recreated
  // by a later pass, which drops the inline style and leaves them hidden only by
  // the stylesheet rule this enforcement exists to stop depending on.
  if (typeof queueMicrotask === "function") queueMicrotask(enforceExternalClientChrome);
  // The offshore "assignments inbox" chrome-stripping CSS keys off THIS flag,
  // not the role: an autonomous org-scoped offshore biller needs the filter
  // row, slicers and pager that the deprecated task-board look hid.
  document.body.dataset.offshoreBoard = offshoreBoardMode() ? "on" : "off";
  // Don't offer what the session can't open; each action has its own gate.
  document.getElementById("manageUsersBtn")?.classList.toggle("hidden", !isTrustedUserManagerSession());
  document.getElementById("manageGroupsBtn")?.classList.toggle("hidden", !isTrustedSuperAdminSession());
  const emailAdminButton = document.getElementById("emailNotificationsBtn");
  if (emailAdminButton) { emailAdminButton.hidden = !isTrustedSuperAdminSession(); emailAdminButton.classList.toggle("hidden", !isTrustedSuperAdminSession()); }
  if (!isTrustedSuperAdminSession()) document.getElementById("emailNotificationsDialog")?.close();
  // UI-100 #67: aria-expanded + aria-controls + return-focus when sidebar collapses.
  if (els.sidebarToggleBtn) {
    els.sidebarToggleBtn.setAttribute("aria-label", state.sidebarCollapsed ? "Show sidebar" : "Hide sidebar");
    els.sidebarToggleBtn.setAttribute("aria-expanded", state.sidebarCollapsed ? "false" : "true");
    els.sidebarToggleBtn.setAttribute("aria-controls", "appSidebar");
    // In the rail the button is a collapse/expand chevron; in the top bar it
    // opens the drawer and stays a menu icon.
    els.sidebarToggleBtn.textContent = els.sidebarToggleBtn.parentElement?.id === "sidebarToggleSlot"
      ? (state.sidebarCollapsed ? "»" : "«")
      : "☰";
  }
  // If the focused element is now hidden (inside the collapsed sidebar), move
  // focus back to the toggle button so keyboard users don't get stranded.
  const ae = document.activeElement;
  if (state.sidebarCollapsed && ae && ae.closest && ae.closest(".sidebar")) {
    try { els.sidebarToggleBtn?.focus(); } catch (_) {}
  }
}

let responsiveSidebarMediaQuery = null;

function installResponsiveSidebarGuard() {
  if (responsiveSidebarMediaQuery || typeof window === "undefined" || typeof window.matchMedia !== "function") return;
  responsiveSidebarMediaQuery = window.matchMedia("(max-width: 960px)");
  const handleResponsiveSidebarChange = (event) => {
    // Browser zoom changes the CSS-pixel viewport without reloading the app.
    // Crossing into the off-canvas layout must not leave the previously open
    // desktop rail sitting over the workspace and intercepting every click.
    if (event.matches) collapseSidebar({ restoreFocus: false });
  };
  if (typeof responsiveSidebarMediaQuery.addEventListener === "function") {
    responsiveSidebarMediaQuery.addEventListener("change", handleResponsiveSidebarChange);
  } else if (typeof responsiveSidebarMediaQuery.addListener === "function") {
    responsiveSidebarMediaQuery.addListener(handleResponsiveSidebarChange);
  }
}

function collapseSidebar({ restoreFocus = false } = {}) {
  if (!state.sidebarCollapsed) {
    state.sidebarCollapsed = true;
    saveState();
    applyLayoutState();
    window.requestAnimationFrame(updateTopGridScroll);
  }
  if (restoreFocus) window.requestAnimationFrame(() => els.sidebarToggleBtn?.focus?.({ preventScroll: true }));
}

// Billers lost the collapse control in the top bar (owner 2026-10-15). On
// desktop it lives in the sidebar on the Operations line, visible in both the
// full sidebar and the collapsed rail. At 960px and below the sidebar is an
// off-canvas drawer, so the same button returns to the top bar to open it.
// One element, one id: every caller and check keeps using #sidebarToggleBtn.
function placeSidebarToggle() {
  const button = els.sidebarToggleBtn;
  const slot = document.getElementById("sidebarToggleSlot");
  const titleRow = document.querySelector(".topbar .title-row");
  if (!button || !slot || !titleRow) return;
  const drawer = typeof window.matchMedia === "function" && window.matchMedia("(max-width: 960px)").matches;
  if (drawer && button.parentElement !== titleRow) titleRow.insertBefore(button, titleRow.firstChild);
  if (!drawer && button.parentElement !== slot) slot.appendChild(button);
  button.textContent = button.parentElement === slot ? (state.sidebarCollapsed ? "»" : "«") : "☰";
}

function installSidebarTogglePlacement() {
  placeSidebarToggle();
  // Resize as well as the media query: some embedded viewports change width
  // without firing the query change event. Placement is idempotent and cheap.
  window.addEventListener("resize", placeSidebarToggle, { passive: true });
  if (typeof window.matchMedia !== "function") return;
  const query = window.matchMedia("(max-width: 960px)");
  if (typeof query.addEventListener === "function") query.addEventListener("change", placeSidebarToggle);
  else if (typeof query.addListener === "function") query.addListener(placeSidebarToggle);
}

function toggleSidebar() {
  state.sidebarCollapsed = !state.sidebarCollapsed;
  saveState();
  applyLayoutState();
  window.requestAnimationFrame(updateTopGridScroll);
}

// ===========================================================================
// TRAINING VIDEOS (Nathan, 2026-10-15: "publish it for logged-in clients ...
// where are they going to look? It should be on the website somewhere.")
// ===========================================================================
// Narrated tours of each client page, recorded on the current portal with a
// fictional practice and fictional patients (no PHI). They live in two places a
// client will look: Help > Training in the sidebar, and a "Watch the tour" chip
// beside the page title of every page that has one.
//
// A tour is offered only when this session can open the page it teaches -- the
// same canAccessView + viewOfferedToRole pair the sidebar uses -- so a Redacted
// Client without Reports never sees the Reports tour or its chip. The files are
// static assets under assets/training/ (same origin, so the CSP default-src
// 'self' already covers <video>).
const TRAINING_ASSET_BASE = "./assets/training/";
const TRAINING_VIDEOS = [
  {
    key: "ar", views: ["workbench"], title: "Accounts Receivable (AR)", duration: "2:26",
    summary: "Read every claim's next step, filter by what is paid and what is not, and open the billing entry in CentralReach in one click.",
    learn: ["Waiting on You", "Follow-up Action and the latest claim note", "Finalized Match: Paid, Not Paid, Under Paid", "Follow-up Due, Aging and Service Month", "EHR Entry ID opens CentralReach", "Advanced Filters and search"]
  },
  {
    key: "authorizations", views: ["authorizations"], title: "Authorizations", duration: "1:48",
    summary: "See every session waiting on an authorization and clear many claims at once by recording it one time.",
    learn: ["What each status means", "Filter the board and view lines", "Record an authorization", "Preview exactly what it resolves"]
  },
  {
    key: "credentialing", views: ["credentialing", "clientActionPending"], title: "Credentialing & Client Action Pending", duration: "1:55",
    summary: "Add a provider's effective date to release parked sessions, and answer our requests in one shared conversation.",
    learn: ["Why a claim is parked", "Add credentialing to release sessions", "Request tabs: waiting on you, with TMS, resolved", "Reply in the shared conversation"]
  },
  {
    key: "reports", views: ["reports"], title: "Reports", duration: "1:33",
    summary: "Filter once, drill from an aging bucket to payer and patient, and see cash coming in, trends and open work.",
    learn: ["Report filters and quick dates", "Still outstanding, by who owes it", "Drill into an aging bucket", "Cash, Trends and Open Work", "Print to PDF or export to CSV"]
  },
  {
    key: "statements", views: ["statements"], title: "Patient Statements", duration: "1:36",
    summary: "Review final patient balances, print ready-to-mail statements, and brand them for your practice.",
    learn: ["Which lines are ready to bill", "Deductible, coinsurance and copay detail", "Show: to print, printed, paid", "Quick select and print", "Branding with a live preview"]
  },
  {
    // Teaches out-of-network learned rates, so it is offered only once that
    // feature has shipped: the tour appears the first time a build carries the
    // learned-rate renderer (OON_LEARNED_RATES_PLAN.md), with no release
    // coordination between the two changes.
    key: "expected-reimbursement", views: ["feeSchedules", "reports"], title: "Expected Reimbursement & Learned OON Rates", duration: "3:20",
    requires: () => typeof renderOonLearnedRatesPanel === "function",
    summary: "How Insurance Amount Expected is calculated, and how out-of-network rates are learned per child, with rate cuts and underpayments caught.",
    learn: ["Units × rate, minus the family's share", "Paid, Under Paid and Not Paid", "How an out-of-network rate is learned", "Median of the last 5 payments", "Confirmed rate cuts, effective dated", "Underpaid checks against the learned rate"]
  }
];
const TRAINING_VIEW_LABELS = { workbench: "AR", authorizations: "Authorizations", credentialing: "Credentialing", clientActionPending: "Client Action Pending", reports: "Reports", statements: "Patient Statements", feeSchedules: "Fee Schedules" };
const trainingState = { selected: "", renderedKey: "" };

function trainingViewOpen(view) {
  return canAccessView(view) && viewOfferedToRole(view);
}

function trainingVideosForSession() {
  return TRAINING_VIDEOS.filter((video) => (!video.requires || video.requires()) && video.views.some(trainingViewOpen));
}

function trainingVideoForView(view) {
  return trainingVideosForSession().find((video) => video.views.includes(view)) || null;
}

function openTrainingVideo(key) {
  trainingState.selected = String(key || "");
  trainingState.renderedKey = "";
  navigateToView("training");
}

function updateViewTourButton() {
  const button = document.getElementById("viewTourBtn");
  if (!button) return;
  const tour = state.view === "training" || !trainingViewOpen("training") ? null : trainingVideoForView(state.view);
  button.hidden = !tour;
  if (!tour) return;
  button.dataset.trainingKey = tour.key;
  const label = document.getElementById("viewTourBtnText");
  if (label) label.textContent = `Watch the ${tour.duration} tour`;
  if (!button.dataset.bound) {
    button.dataset.bound = "1";
    button.addEventListener("click", () => openTrainingVideo(button.dataset.trainingKey));
  }
}

function trainingCardMarkup(video, active) {
  return `<button type="button" class="training-card${active ? " is-active" : ""}" data-training-pick="${escapeHtml(video.key)}" aria-pressed="${active ? "true" : "false"}">
      <span class="training-thumb">
        <img src="${TRAINING_ASSET_BASE}${escapeHtml(video.key)}.jpg" alt="" loading="lazy">
        <span class="training-play" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m9 7 9 5-9 5Z"/></svg></span>
        <span class="training-duration-badge">${escapeHtml(video.duration)}</span>
        ${active ? '<span class="training-now">Now playing</span>' : ""}
      </span>
      <span class="training-card-body">
        <strong>${escapeHtml(video.title)}</strong>
        <span>${escapeHtml(video.summary)}</span>
      </span>
    </button>`;
}

function renderTraining() {
  const host = document.getElementById("trainingBody");
  if (!host) return;
  const videos = trainingVideosForSession();
  if (!videos.length) {
    trainingState.renderedKey = "";
    host.innerHTML = '<div class="training-empty"><strong>No tours for your pages yet</strong><span>Tours appear here for the pages your account can open.</span></div>';
    return;
  }
  const current = videos.find((video) => video.key === trainingState.selected) || videos[0];
  trainingState.selected = current.key;
  // A routine re-render (sync tick, badge refresh) must not restart a video the
  // viewer is watching: repaint only when the selection or the offered set moves.
  const renderKey = current.key + "|" + videos.map((video) => video.key).join(",");
  if (trainingState.renderedKey === renderKey && host.querySelector("#trainingPlayer")) return;
  trainingState.renderedKey = renderKey;
  const openView = current.views.find(trainingViewOpen) || "";
  const isFirst = current.key === videos[0].key;
  host.innerHTML = `
    <section class="training-feature" aria-label="${escapeHtml(current.title)} tour">
      <div class="training-player">
        <video id="trainingPlayer" controls preload="metadata" playsinline poster="${TRAINING_ASSET_BASE}${escapeHtml(current.key)}.jpg" src="${TRAINING_ASSET_BASE}${escapeHtml(current.key)}.mp4"></video>
      </div>
      <div class="training-feature-copy">
        <span class="training-eyebrow">${isFirst ? "Start here" : "Tour"} &middot; ${escapeHtml(current.duration)}</span>
        <h3>${escapeHtml(current.title)}</h3>
        <p class="training-summary">${escapeHtml(current.summary)}</p>
        <span class="training-learn-label">What you'll learn</span>
        <ul class="training-learn">${current.learn.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>
        ${openView ? `<button type="button" class="primary-button training-open" data-training-open="${escapeHtml(openView)}">Open ${escapeHtml(TRAINING_VIEW_LABELS[openView] || openView)}</button>` : ""}
      </div>
    </section>
    <section class="training-library" aria-label="All tours">
      <div class="training-library-head">
        <h3>All tours</h3>
        <span>${videos.length} narrated video${videos.length === 1 ? "" : "s"} &middot; recorded on sample data, no real patients</span>
      </div>
      <div class="training-grid">${videos.map((video) => trainingCardMarkup(video, video.key === current.key)).join("")}</div>
    </section>`;
  host.querySelectorAll("[data-training-pick]").forEach((card) => card.addEventListener("click", () => {
    trainingState.selected = card.getAttribute("data-training-pick");
    renderTraining();
    const player = document.getElementById("trainingPlayer");
    host.querySelector(".training-feature")?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (player && typeof player.play === "function") player.play().catch(() => {});
  }));
  host.querySelector("[data-training-open]")?.addEventListener("click", (event) => navigateToView(event.currentTarget.getAttribute("data-training-open")));
}

// OVERSIGHT (OVERSIGHT_PLAN.md, Phase 1, Nathan 2026-10-27). Global admins only.
// Every company's open insurance balance, WHY it is unpaid, and the money no one
// is working, read from GET /api/oversight/* (SQL over the T1a snapshot, never a
// browser fold). Every figure opens All Data filtered to the lines behind it.
const COV_REASON_COLORS = {
  client: "#d97706", notBilled: "#64748b", noRate: "#7c3aed", denied: "#dc2626",
  secondaryNotBilled: "#0891b2", secondaryUnpaid: "#0e7490", ratePattern: "#a16207",
  underpaid: "#ca8a04", smallBalance: "#cbd5e1", noPayment: "#b91c1c",
  waitingPayer: "#16a34a", noBillDate: "#94a3b8", other: "#94a3b8"
};
const COV_LATE_AGING = ["31-60", "61-90", "91-120", "121+"];
const COV_TREND_SERIES = [
  { key: "owed", label: "Outstanding", color: "var(--brand)" },
  { key: "pastDueOwed", label: "Past due", color: "#d97706" },
  { key: "notWorkedOwed", label: "Not worked since due", color: "#dc2626" },
  { key: "unownedOwed", label: "No one on it", color: "#7c3aed" }
];
const covState = { key: "", trainee: null, traineeStatus: "idle", traineeError: "", traineeKey: "", location: "", summary: null, summaryStatus: "idle", summaryError: "", work: null, workStatus: "idle", workError: "", orgId: "", client: null, clientStatus: "idle", clientError: "", tab: "why", section: "overview", team: "", sort: "owed", peopleSort: "workedPerDay", reasonFocus: "", controllers: {}, access: { key: "", status: "unknown" }, filters: { orgs: [], payers: [], reasons: [], aging: [], status: [], actions: [] }, grandOwed: 0, filterOpen: "", filterSearch: "", guideOpen: false, reloadTimer: 0 };

// Only the account owner sees Oversight for now (Nathan, 2026-10-27). The
// browser cannot know who that is, so the server decides: one access check per
// session, and until it answers "allowed" the menu entry stays hidden.
function canOpenCompanyOversight() {
  const scope = currentAccessProfile().scope || {};
  return isTrustedSuperAdminSession() && canonicalRole(state.role) === "SuperAdmin"
    && scope.allOrgs === true && scope.crossTenant === true
    && hasPermission(PERMISSIONS.SECURITY_AUDIT) && hasPermission(PERMISSIONS.VIEW_FINANCIALS)
    && covAccessAllowed();
}

function covAccessAllowed() {
  const key = covAccessKey();
  if (covState.access.key !== key) {
    covState.access = { key, status: "checking" };
    void checkCovAccess(key);
  }
  return covState.access.status === "allowed";
}

async function checkCovAccess(key) {
  let status = "denied";
  try {
    if (typeof workflowApiBase === "function" && workflowApiBase()) {
      const response = await fetch(`${workflowApiBase()}/api/oversight/access`, { headers: { authorization: `Bearer ${workflowApiToken()}` }, cache: "no-store" });
      if (response.ok && (await response.json().catch(() => ({}))).allowed === true) status = "allowed";
    }
  } catch (_) { /* unreachable means not shown */ }
  if (covState.access.key !== key) return;
  covState.access.status = status;
  try { applyNavVisibility(els.navItems, els.navSections); } catch (_) { /* the next render applies it */ }
  if (state.view === "companyOversight") {
    if (status === "allowed") renderCompanyOversight();
    else navigateToView(firstAccessibleView());
  }
}

// The page's filters travel with every read ("" = no action / no payer).
function covQuery(path, params = {}) {
  const query = new URLSearchParams(params);
  Object.entries(covState.filters).forEach(([key, values]) => { if (values.length) query.set(key, JSON.stringify(values)); });
  const text = query.toString();
  return text ? `${path}?${text}` : path;
}

// The company picked at the top of the portal scopes Oversight (Nathan,
// 2026-10-30: "I don't want to see other companies here when I'm working in
// this company"). Only "All companies" there shows every company. A pick whose
// org IDs did not resolve reads nothing rather than widening to every company.
function covHeaderOrgIds() {
  if (!activeClientGroupTenant()) return [];
  const orgIds = activeClientGroupOrgIds();
  return orgIds.length ? orgIds : ["__COMPANY_SCOPE_UNAVAILABLE__"];
}

function covHeaderScoped() {
  return covHeaderOrgIds().length > 0;
}

function covScopeName() {
  return covHeaderScoped() ? activeClientGroupLabel() || "this company" : "every company";
}

// Daily history for the header company (its orgs summed by day), else every company.
function covTrendSeries() {
  const trend = covState.work?.trend || {};
  if (!covHeaderScoped()) return trend.portfolio || [];
  const days = new Map();
  covHeaderOrgIds().forEach((orgId) => (trend.byOrg?.[orgId] || []).forEach((row) => {
    const total = days.get(row.day) || { day: row.day };
    Object.entries(row).forEach(([field, value]) => { if (field !== "day") total[field] = (Number(total[field]) || 0) + (Number(value) || 0); });
    days.set(row.day, total);
  }));
  return [...days.values()].sort((a, b) => a.day.localeCompare(b.day));
}

function covCompanyName(orgId) {
  const name = String(portalCompanyLabel(orgId, "") || "").trim();
  return name && name !== String(orgId) ? name : "Company name unavailable";
}

// One controller per channel: the summary, the people/approvals work read and a
// company drill load side by side, and only a newer read of the SAME kind cancels.
async function covRead(path, key, channel) {
  covState.controllers[channel]?.abort();
  const controller = new AbortController();
  covState.controllers[channel] = controller;
  const response = await fetch(`${workflowApiBase()}${path}`, { headers: { authorization: `Bearer ${workflowApiToken()}` }, cache: "no-store", signal: controller.signal });
  if (response.status === 401) { notePortalSessionExpired(); throw new Error("Your session expired. Sign in again."); }
  const body = await response.json().catch(() => ({}));
  if (key !== covState.key) throw Object.assign(new Error("stale"), { name: "AbortError" });
  if (!response.ok) throw new Error(body.message || "Oversight could not load. Try again in a minute.");
  return body;
}

function covAccessKey() {
  return workflowAccessScopedRequestKey({ view: "companyOversight" });
}

async function loadCovSummary(force = false) {
  const key = covAccessKey();
  if (key !== covState.key) Object.assign(covState, { key, summary: null, summaryStatus: "idle", work: null, workStatus: "idle", orgId: "", client: null, clientStatus: "idle", filters: { ...covState.filters, orgs: covHeaderOrgIds() }, grandOwed: 0 });
  if (!force && (covState.summaryStatus === "loading" || covState.summaryStatus === "ready")) return;
  covState.summaryStatus = "loading";
  covState.summaryError = "";
  renderCompanyOversight();
  void loadCovWork(force);
  try {
    covState.summary = await covRead(covQuery("/api/oversight/summary"), key, "summary");
    if (!covAnyFilter()) covState.grandOwed = Number(covState.summary?.totals?.owed) || 0;
    covState.summaryStatus = "ready";
  } catch (error) {
    if (error.name === "AbortError") return;
    covState.summaryStatus = "error";
    covState.summaryError = error.message === "Failed to fetch" ? "Oversight could not be reached. Try again." : error.message;
  }
  if (state.view === "companyOversight") renderCompanyOversight();
}

// Trainee review is its own read, made only when the tab is opened. It covers
// every company and every person, so the page's filters do not apply to it.
async function loadCovTrainee(force = false) {
  const key = covState.key;
  if (!force && covState.traineeKey === key && (covState.traineeStatus === "loading" || covState.traineeStatus === "ready")) return;
  Object.assign(covState, { traineeKey: key, traineeStatus: "loading", traineeError: "" });
  try {
    covState.trainee = await covRead("/api/oversight/trainee-review", key, "trainee");
    covState.traineeStatus = "ready";
  } catch (error) {
    if (error.name === "AbortError") return;
    covState.traineeStatus = "error";
    covState.traineeError = error.message === "Failed to fetch" ? "Trainee review could not be reached. Try again." : error.message;
  }
  if (state.view === "companyOversight" && covState.section === "trainee") renderCompanyOversight();
}

// People, approvals, worked per day and the trend come from a second, slower
// read (T1a lines joined to the portal's follow-up records). The money above
// never waits for it; its parts say "Loading" until it lands.
async function loadCovWork(force = false) {
  const key = covState.key;
  if (!force && (covState.workStatus === "loading" || covState.workStatus === "ready")) return;
  covState.workStatus = "loading";
  covState.workError = "";
  try {
    covState.work = await covRead(covQuery("/api/oversight/work"), key, "work");
    covState.workStatus = "ready";
  } catch (error) {
    if (error.name === "AbortError") return;
    covState.workStatus = "error";
    covState.workError = error.message === "Failed to fetch" ? "People and approvals could not be reached. Try again." : error.message;
  }
  if (state.view === "companyOversight") renderCompanyOversight();
}

async function openCovClient(orgId, tab = "why") {
  const key = covState.key;
  Object.assign(covState, { orgId, client: null, clientStatus: "loading", clientError: "", tab });
  renderCompanyOversight();
  try {
    const client = await covRead(covQuery("/api/oversight/client", { orgId }), key, "client");
    if (covState.orgId !== orgId) return;
    covState.client = client;
    covState.clientStatus = "ready";
  } catch (error) {
    if (error.name === "AbortError") return;
    covState.clientStatus = "error";
    covState.clientError = error.message === "Failed to fetch" ? "This company could not be reached. Try again." : error.message;
  }
  if (state.view === "companyOversight") renderCompanyOversight();
}

function covWorkOrg(orgId) {
  return (covState.work?.orgs || []).find((row) => row.orgId === orgId) || null;
}

// DRILL: replace every All Data filter with exactly the lines behind a figure.
// Service-line rows (Oversight counts lines) with money still owed (> $0).
// Claim drills (not worked since due) narrow by claim ID, ten at most: the
// All Data exact-ID search holds ten.
function openCovLines(drill) {
  if (!drill || (!drill.clientKey && !drill.allCompanies)) return;
  clearOperationalFilters();
  state.queueFilter = "all";
  if (state.rowMode !== "service") {
    state.rowMode = "service";
    if (els.rowModeSelect) els.rowModeSelect.value = "service";
    try { updateGridModeMirror(); } catch (_) { /* mirror is optional */ }
  }
  const filters = { ...(drill.clientKey && drill.clientKey !== "*" ? { client: [drill.clientKey] } : {}), ...(drill.filters || {}) };
  const ranges = { owed: { op: "gt", value: 0 }, ...(drill.ranges || {}) };
  state.columnFilters = ensureColumnFilterState(filters);
  state.columnFilterModes = ensureFilterModeState(drill.modes || {}, state.columnFilters);
  state.columnFilterRanges = ensureColumnFilterRangeState(ranges);
  setClaimIdSearchValue(Array.isArray(drill.claimIds) ? drill.claimIds.slice(0, 10) : "");
  resetTrackerPage();
  saveState();
  navigateToView("workbench");
  try { renderSlicers(); } catch (_) { /* slicers paint on their own render */ }
  renderClaimsTable();
  showToast(`All Data: ${drill.label}`);
}

// Approvals open where they are decided: My Work, Needs my approval, for that
// company (the same hand-off a workflow notification uses).
function openCovApprovals(orgId) {
  resetAssignmentWorkflowScope({ preserveSelection: true });
  if (orgId) assignmentScopeUiState.orgId = orgId;
  assignmentScopeUiState.memberUserId = "";
  assignmentScopeUiState.memberIdentities = [];
  if (oversightWorkTabs().some(([tab]) => tab === "approvals")) {
    oversightWorkReadController?.abort();
    const model = oversightState();
    model.tab = "approvals"; if (orgId) model.orgId = orgId;
    model.page = 1; model.pagination = null; model.request += 1; model.status = "idle";
  }
  navigateToView("assignments");
}

// With filters on, every drill also narrows All Data to the same lines: each
// filter becomes the All Data filter it means, intersected with the drill's own.
// A drill the filters cannot be expressed on, or that no selected value can
// satisfy, is disabled with the reason rather than opening other lines.
const COV_STATUS_DRILLS = {
  noAction: () => ({ filters: { action: ["Blank"] } }),
  noDate: () => ({ filters: { action: ["Blank"] }, modes: { action: FILTER_MODE_EXCLUDE }, ranges: { followUpDate: { op: "blank" } } }),
  late30: (today) => ({ filters: { action: ["Blank"] }, modes: { action: FILTER_MODE_EXCLUDE }, ranges: { followUpDate: { op: "before", value: covShiftDay(today, -30) } } }),
  late: (today) => ({ filters: { action: ["Blank"] }, modes: { action: FILTER_MODE_EXCLUDE }, ranges: { followUpDate: { op: "between", value: covShiftDay(today, -30), value2: covShiftDay(today, -1) } } }),
  upcoming: (today) => ({ filters: { action: ["Blank"] }, modes: { action: FILTER_MODE_EXCLUDE }, ranges: { followUpDate: { op: "after", value: covShiftDay(today, -1) } } })
};

function covShiftDay(day, delta) {
  const date = new Date(`${day || new Date().toISOString().slice(0, 10)}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

// Narrow one All Data filter key to `values`, keeping the drill's own rule.
function covNarrow(filters, modes, key, values) {
  const own = filters[key];
  if (!own) { filters[key] = values; return true; }
  const set = new Set(own.map((value) => String(value).toLowerCase()));
  const kept = values.filter((value) => (modes[key] === FILTER_MODE_EXCLUDE) !== set.has(String(value).toLowerCase()));
  if (!kept.length) return false;
  filters[key] = kept;
  delete modes[key];
  return true;
}

function covFilteredDrill(drill) {
  if (!drill) return null;
  const f = covState.filters;
  if (!covAnyFilter()) return drill;
  const filters = { ...(drill.filters || {}) };
  const modes = { ...(drill.modes || {}) };
  const ranges = { ...(drill.ranges || {}) };
  const refuse = (why) => ({ refused: why });
  if (f.actions.length && !covNarrow(filters, modes, "action", f.actions.map((action) => action || "Blank"))) return refuse("None of these lines has a selected follow-up action");
  if (f.payers.length && !covNarrow(filters, modes, "primaryPayer", f.payers.map((payer) => (payer === "(no payer)" ? "Blank" : payer)))) return refuse("None of these lines has a selected payer");
  if (f.aging.length && !covNarrow(filters, modes, "aging", f.aging.map((bucket) => (bucket === "(blank)" ? "Blank" : bucket)))) return refuse("None of these lines is in a selected age");
  let clientKey = drill.clientKey;
  if (f.orgs.length) {
    if (drill.orgId && !f.orgs.includes(drill.orgId)) return refuse("This company is not in the company filter");
    if (drill.allCompanies) {
      const keys = (covState.summary?.clients || []).filter((client) => f.orgs.includes(client.orgId)).map((client) => client.clientKey).filter(Boolean);
      if (!keys.length) return refuse("The selected companies have no lines here");
      filters.client = keys;
    }
  }
  if (f.status.length) {
    if (f.status.length > 1) return refuse("All Data can open one follow-up status at a time; pick one");
    const status = COV_STATUS_DRILLS[f.status[0]]?.(covState.summary?.today);
    if (!status) return refuse("This follow-up status cannot be opened in All Data");
    if (ranges.followUpDate && !["late30", "late"].includes(f.status[0])) return refuse("These lines are past due; the status filter excludes them");
    Object.assign(ranges, status.ranges || {});
    for (const [key, values] of Object.entries(status.filters || {})) {
      if (status.modes?.[key] === FILTER_MODE_EXCLUDE) {
        if (filters[key] && modes[key] !== FILTER_MODE_EXCLUDE) { const kept = filters[key].filter((value) => !values.includes(value)); if (!kept.length) return refuse("No line matches both"); filters[key] = kept; }
        else if (!filters[key]) { filters[key] = values; modes[key] = FILTER_MODE_EXCLUDE; }
      } else if (!covNarrow(filters, modes, key, values)) return refuse("No line matches both");
    }
  }
  if (f.reasons.length) {
    if (drill.reasonKey) { if (!f.reasons.includes(drill.reasonKey)) return refuse("This reason is not in the reason filter"); }
    else if (f.reasons.length > 1) return refuse("All Data can open one reason at a time; pick one");
    else {
      const mapped = covReasonDrill({ key: f.reasons[0], label: "" }, clientKey || "*", "", covState.summary?.actions?.map((row) => ({ action: row.action })) || []);
      if (!mapped) return refuse("This reason has no matching All Data filter");
      for (const [key, values] of Object.entries(mapped.filters || {})) if (!covNarrow(filters, modes, key, values)) return refuse("No line matches both");
    }
  }
  return { ...drill, filters, modes, ranges, label: `${drill.label} (${covFilterLabel()})` };
}

function covDrillAttr(drill) {
  if (!drill) return "";
  const filtered = covFilteredDrill(drill);
  if (!filtered || filtered.refused) return ` disabled title="${escapeHtml(filtered?.refused || "None of these lines matches the filters")}"`;
  return ` data-cov-drill="${escapeHtml(JSON.stringify(filtered))}" title="${escapeHtml(`Open these lines in All Data: ${filtered.label}`)}"`;
}

function covReasonDrill(reason, clientKey, companyName, actions = []) {
  const base = { clientKey, reasonKey: reason.key, label: `${companyName}, ${reason.label}` };
  const capActions = actions.map((row) => row.action).filter((action) => /^client action pending/i.test(action));
  switch (reason.key) {
    case "client": return capActions.length ? { ...base, filters: { action: capActions } } : null;
    case "notBilled": return { ...base, filters: { claimStatus: ["Ready to Submit"] } };
    case "noRate": return { ...base, filters: { match: ["No Primary Rate", "No Secondary Rate"] } };
    case "ratePattern":
    case "underpaid": return { ...base, label: `${companyName}, underpaid`, filters: { match: ["Under Paid"] } };
    case "smallBalance": return { ...base, filters: { match: ["Paid"] } };
    case "waitingPayer": return { ...base, filters: { match: ["Not Paid"], aging: ["0-30"] } };
    case "noPayment": return { ...base, filters: { match: ["Not Paid"], aging: COV_LATE_AGING } };
    default: return null;
  }
}

function covUnownedDrill(clientKey, companyName) {
  return { clientKey, label: `${companyName}, no follow-up action, billed 30+ days ago`, filters: { action: ["Blank"], aging: COV_LATE_AGING, match: ["Paid"] }, modes: { match: FILTER_MODE_EXCLUDE } };
}

// Past due counts lines WITH a follow-up action whose date has passed, so the
// drill leaves out lines with no action.
function covPastDueDrill(clientKey, companyName, today) {
  return { clientKey, label: `${companyName}, follow-up date passed`, filters: { action: ["Blank"] }, modes: { action: FILTER_MODE_EXCLUDE }, ranges: { followUpDate: { op: "before", value: today } } };
}

function covClaimDrill(clientKey, companyName, claimIds, label) {
  const ids = [...new Set(claimIds.filter(Boolean))].slice(0, 10);
  return ids.length ? { clientKey, claimIds: ids, label: `${companyName}, ${label}` } : null;
}

// Week-over-week: today's figure against the newest saved day at least 7 days
// back. Every figure here is money that should shrink, so up is bad.
function covTrendDelta(series, key) {
  const rows = Array.isArray(series) ? series : [];
  const latest = rows[rows.length - 1];
  if (!latest) return null;
  const cutoff = new Date(`${latest.day}T12:00:00Z`);
  cutoff.setUTCDate(cutoff.getUTCDate() - 7);
  const before = [...rows].reverse().find((row) => row.day <= cutoff.toISOString().slice(0, 10));
  return before ? { change: (Number(latest[key]) || 0) - (Number(before[key]) || 0), since: before.day } : null;
}

function covDeltaHtml(delta) {
  if (!delta) return "";
  const change = Math.round(delta.change);
  if (!change) return `<em class="cov-delta">No change in 7 days</em>`;
  const worse = change > 0;
  return `<em class="cov-delta ${worse ? "is-worse" : "is-better"}">${worse ? "▲" : "▼"} ${escapeHtml(compactMoney(Math.abs(change)))} since ${escapeHtml(formatDate(delta.since))}</em>`;
}

function covShare(part, whole) {
  return whole > 0 ? `${Math.round(part / whole * 100)}%` : "0%";
}

function covTile(label, amount, lines, note, drill, tone = "", delta = null, pending = false) {
  return `<button type="button" class="cov-tile ${tone}"${drill ? covDrillAttr(drill) : " disabled"}>
    <span>${escapeHtml(label)}</span>
    <strong>${pending ? "…" : escapeHtml(compactMoney(amount))}</strong>
    <small>${pending ? "Loading" : `${escapeHtml(money(amount))} · ${escapeHtml(formatNounCount(lines, "line"))}`}</small>
    <em>${escapeHtml(note)}</em>
    ${covDeltaHtml(delta)}
  </button>`;
}

function covReasonBar(reasons = [], total = 0) {
  if (!total) return '<div class="cov-bar is-empty"></div>';
  return `<div class="cov-bar" role="img" aria-label="${escapeHtml(reasons.map((r) => `${r.label} ${Math.round(r.share * 100)}%`).join(", "))}">${reasons
    .map((r) => `<i style="width:${(r.owed / total * 100).toFixed(2)}%;background:${COV_REASON_COLORS[r.key] || "#94a3b8"}" title="${escapeHtml(`${r.label}: ${money(r.owed)} (${Math.round(r.share * 100)}%)`)}"></i>`).join("")}</div>`;
}

function covAttentionItems(summary, work) {
  const items = [];
  (summary.clients || []).forEach((client) => {
    const name = covCompanyName(client.orgId);
    const reason = (key) => (client.reasons || []).find((r) => r.key === key);
    if (client.unownedOwed >= 10000) items.push({ owed: client.unownedOwed, orgId: client.orgId, text: `${name}: ${money(client.unownedOwed)} on ${formatNounCount(client.unownedLines, "line")} has no follow-up action and was billed 30+ days ago.`, drill: covUnownedDrill(client.clientKey, name) });
    const workOrg = work ? (work.orgs || []).find((row) => row.orgId === client.orgId) : null;
    if (workOrg && workOrg.notWorkedOwed >= 25000) items.push({ owed: workOrg.notWorkedOwed, orgId: client.orgId, tab: "people", text: `${name}: ${money(workOrg.notWorkedOwed)} on ${formatNounCount(workOrg.notWorkedLines, "line")} is past its follow-up date and no one has touched it since.` });
    else if (client.pastDue30Owed >= 25000 && client.pastDue30Owed >= client.owed * 0.25) items.push({ owed: client.pastDue30Owed, orgId: client.orgId, text: `${name}: ${money(client.pastDue30Owed)} is more than 30 days past its follow-up date (${Math.round(client.pastDue30Owed / client.owed * 100)}% of the balance).`, drill: covPastDueDrill(client.clientKey, name, summary.today) });
    const noPayment = reason("noPayment");
    if (noPayment && noPayment.owed >= 50000) items.push({ owed: noPayment.owed, orgId: client.orgId, text: `${name}: ${money(noPayment.owed)} billed 30+ days ago with no payment at all.`, drill: covReasonDrill(noPayment, client.clientKey, name) });
    const rate = reason("ratePattern");
    if (rate && rate.owed >= 10000) items.push({ owed: rate.owed, orgId: client.orgId, text: `${name}: payers pay a different rate than ours on ${formatNounCount(rate.lines, "line")} (${money(rate.owed)}). Check the fee schedule.`, drill: covReasonDrill(rate, client.clientKey, name) });
  });
  // Requests waiting more than 3 days on lines that still owe money: the
  // biller's work is done and stuck behind a reviewer. Ranked as if worth $25K
  // so a stale queue is not buried under large balances.
  (work?.approvals?.byOrg || []).forEach((row) => {
    if (row.over3Days > 0 && row.owed > 0) items.push({ owed: 25000 + row.owed, orgId: row.orgId, approvals: true, text: `${covCompanyName(row.orgId)}: ${formatNounCount(row.lines, "follow-up request")} waiting for approval, the oldest since ${formatDate(row.oldestAt)} (${formatNounCount(row.over3Days, "request")} over 3 days).` });
  });
  return items.sort((a, b) => b.owed - a.owed).slice(0, 8);
}

const COV_SORTS = {
  name: (a, b) => covCompanyName(a.orgId).localeCompare(covCompanyName(b.orgId)),
  owed: (a, b) => b.owed - a.owed,
  pastDueOwed: (a, b) => b.pastDueOwed - a.pastDueOwed,
  pastDue30Owed: (a, b) => b.pastDue30Owed - a.pastDue30Owed,
  notWorkedOwed: (a, b) => (covWorkOrg(b.orgId)?.notWorkedOwed || 0) - (covWorkOrg(a.orgId)?.notWorkedOwed || 0),
  onTime: (a, b) => (covWorkOrg(a.orgId)?.onTimeRate ?? 2) - (covWorkOrg(b.orgId)?.onTimeRate ?? 2),
  workedPerDay: (a, b) => (covWorkOrg(b.orgId)?.workedPerDay || 0) - (covWorkOrg(a.orgId)?.workedPerDay || 0),
  unownedOwed: (a, b) => b.unownedOwed - a.unownedOwed,
  approvals: (a, b) => (covWorkOrg(b.orgId)?.approvalLines || 0) - (covWorkOrg(a.orgId)?.approvalLines || 0),
  oldestDue: (a, b) => String(a.oldestDue || "9999").localeCompare(String(b.oldestDue || "9999"))
};

function covFocusOwed(client) {
  return ((client.reasons || []).find((r) => r.key === covState.reasonFocus) || {}).owed || 0;
}

function covWorkPending(label) {
  if (covState.workStatus === "error") return `<div class="cov-message is-error" role="alert">${escapeHtml(covState.workError || `${label} could not load.`)} <button type="button" class="ghost-button" data-cov-work-retry>Try again</button></div>`;
  return `<div class="cov-message" role="status">Loading ${escapeHtml(label.toLowerCase())}…</div>`;
}

function renderCovCompanies(summary) {
  const focus = (summary.reasons || []).find((r) => r.key === covState.reasonFocus);
  const sorter = focus ? (a, b) => covFocusOwed(b) - covFocusOwed(a) : (COV_SORTS[covState.sort] || COV_SORTS.owed);
  const clients = [...(summary.clients || [])].sort(sorter);
  const work = covState.work;
  const header = (key, label, numeric = true) => `<th class="${numeric ? "is-num" : ""}"><button type="button" data-cov-sort="${key}" aria-pressed="${!focus && covState.sort === key}">${escapeHtml(label)}</button></th>`;
  return `<section class="cov-card" aria-label="Companies">
      <div class="cov-card-head"><h4>Companies</h4><p>${focus ? `Ranked by <strong>${escapeHtml(focus.label)}</strong>. <button type="button" class="cov-linkish" data-cov-focus="">Show all</button>` : "Click a company to see why its money is outstanding and who is working it."}</p></div>
      <div class="cov-table-wrap"><table class="cov-table">
        <thead><tr>${header("name", "Company", false)}${header("owed", "Outstanding")}${focus ? `<th class="is-num">${escapeHtml(focus.label)}</th>` : ""}${header("pastDueOwed", "Past due")}${header("notWorkedOwed", "Not worked since due")}${header("onTime", "Touched on time")}${header("workedPerDay", "Worked / weekday")}${header("unownedOwed", "No one on it")}${header("approvals", "Approvals")}${header("oldestDue", "Oldest past due", false)}<th>Why</th></tr></thead>
        <tbody>${clients.map((client) => {
          const name = covCompanyName(client.orgId);
          const workOrg = covWorkOrg(client.orgId);
          return `<tr>
            <th scope="row"><button type="button" class="cov-company" data-cov-client="${escapeHtml(client.orgId)}">${escapeHtml(name)}</button></th>
            <td class="is-num"><button type="button" class="cov-num"${covDrillAttr({ clientKey: client.clientKey, orgId: client.orgId, label: `${name}, all open lines` })}>${escapeHtml(money(client.owed))}</button></td>
            ${focus ? `<td class="is-num">${escapeHtml(money(covFocusOwed(client)))}</td>` : ""}
            <td class="is-num"><button type="button" class="cov-num"${covDrillAttr(covPastDueDrill(client.clientKey, name, summary.today))}>${escapeHtml(money(client.pastDueOwed))}</button><small>${escapeHtml(formatCount(client.pastDueLines))}</small></td>
            <td class="is-num ${workOrg?.notWorkedOwed ? "is-danger" : ""}">${work ? `<button type="button" class="cov-num" data-cov-client="${escapeHtml(client.orgId)}" data-cov-client-tab="people" title="See who owns these lines">${escapeHtml(money(workOrg?.notWorkedOwed || 0))}</button><small>${escapeHtml(formatCount(workOrg?.notWorkedLines || 0))}</small>` : "…"}</td>
            <td class="is-num ${covRateTone(workOrg?.onTimeRate)}">${work ? escapeHtml(covPercent(workOrg?.onTimeRate)) : "…"}</td>
            <td class="is-num">${work ? escapeHtml(String(workOrg?.workedPerDay || 0)) : "…"}</td>
            <td class="is-num ${client.unownedOwed > 0 ? "is-danger" : ""}"><button type="button" class="cov-num"${covDrillAttr(covUnownedDrill(client.clientKey, name))}>${escapeHtml(money(client.unownedOwed))}</button><small>${escapeHtml(formatCount(client.unownedLines))}</small></td>
            <td class="is-num">${work ? (workOrg?.approvalLines ? `<button type="button" class="cov-num" data-cov-approvals="${escapeHtml(client.orgId)}" title="Open in My Work, Needs my approval">${escapeHtml(formatCount(workOrg.approvalLines))}</button>` : "0") : "…"}</td>
            <td>${escapeHtml(client.oldestDue ? formatDate(client.oldestDue) : "None")}</td>
            <td class="cov-why-cell">${covReasonBar(client.reasons, client.owed)}</td>
          </tr>`;
        }).join("")}</tbody>
      </table></div>
    </section>`;
}

function covPersonCell(person) {
  const meta = person.kind === "person" ? [person.role, (person.teams || []).length ? person.teams.join(", ") : "No team"].filter(Boolean).join(" · ") : person.kind === "none" ? "Lines with no follow-up action at all" : person.key === "*system" ? "Set by the portal's automatic follow-up rules" : "Action came from the import; no one in the portal set it";
  return `<th scope="row"><strong>${escapeHtml(person.name)}</strong><small>${escapeHtml(meta)}</small></th>`;
}

const COV_PEOPLE_SORTS = {
  name: (a, b) => a.name.localeCompare(b.name),
  workedPerDay: (a, b) => (b.workedPerDay || 0) - (a.workedPerDay || 0) || (b.workedLast7 || 0) - (a.workedLast7 || 0),
  workedLast7: (a, b) => (b.workedLast7 || 0) - (a.workedLast7 || 0),
  onTime: (a, b) => (a.onTimeRate ?? 2) - (b.onTimeRate ?? 2),
  notWorkedOwed: (a, b) => (b.notWorkedOwed || 0) - (a.notWorkedOwed || 0),
  backlogDays: (a, b) => (b.backlogDays ?? Infinity) - (a.backlogDays ?? Infinity),
  sentBack: (a, b) => (b.sentBackRate ?? -1) - (a.sentBackRate ?? -1),
  ownedOwed: (a, b) => (b.ownedOwed || 0) - (a.ownedOwed || 0)
};

function covBacklogText(days) {
  if (days == null) return "Not working it";
  if (!days) return "None";
  return `${days >= 10 ? Math.round(days) : days} days`;
}

function renderCovPeople() {
  const work = covState.work;
  if (!work) return covWorkPending("People and teams");
  const teams = [...new Set(work.people.flatMap((p) => p.teams || []))].sort((a, b) => a.localeCompare(b));
  const locations = [...new Set(work.people.filter((p) => p.kind === "person").map((p) => p.location || "notSet"))].sort((a, b) => (COV_LOCATION_LABELS[a] || a).localeCompare(COV_LOCATION_LABELS[b] || b));
  const visible = work.people
    .filter((p) => !covState.team || (covState.team === "*none" ? p.kind === "person" && !(p.teams || []).length : (p.teams || []).includes(covState.team)))
    .filter((p) => !covState.location || (p.kind === "person" && (p.location || "notSet") === covState.location));
  const people = visible.filter((p) => p.kind === "person").sort(COV_PEOPLE_SORTS[covState.peopleSort] || COV_PEOPLE_SORTS.workedPerDay);
  const pseudo = visible.filter((p) => p.kind !== "person");
  const active = people.filter((p) => p.workedPerDay > 0);
  const perDay = active.reduce((sum, p) => sum + p.workedPerDay, 0);
  const pastDue = people.reduce((sum, p) => sum + p.pastDueLines, 0);
  const notWorked = people.reduce((sum, p) => sum + p.notWorkedLines, 0);
  const decided = people.reduce((sum, p) => sum + (p.requestsDecided || 0), 0);
  const sentBack = people.reduce((sum, p) => sum + (p.requestsSentBack || 0), 0);
  const chip = (key, label) => `<button type="button" data-cov-team="${escapeHtml(key)}" aria-pressed="${covState.team === key}">${escapeHtml(label)}</button>`;
  const num = (value, tone = "") => `<td class="is-num ${value && tone ? tone : ""}">${escapeHtml(formatCount(value || 0))}</td>`;
  const head = (key, label, title = "") => `<th class="is-num"${title ? ` title="${escapeHtml(title)}"` : ""}><button type="button" data-cov-people-sort="${key}" aria-pressed="${covState.peopleSort === key}">${escapeHtml(label)}</button></th>`;
  return `<section class="cov-card" aria-label="People and teams">
      <div class="cov-card-head"><h4>Productivity by person</h4><p>How much each person works, whether they keep up with what comes due, and how often their requests come back. Click a heading to rank by it.</p></div>
      <div class="cov-chips" role="group" aria-label="Team">${chip("", "Everyone")}${teams.map((team) => chip(team, team)).join("")}${chip("*none", "No team")}</div>
      <div class="cov-chips" role="group" aria-label="Location"><button type="button" data-cov-location="" aria-pressed="${!covState.location}">Every location</button>${locations.map((key) => `<button type="button" data-cov-location="${escapeHtml(key)}" aria-pressed="${covState.location === key}">${escapeHtml(COV_LOCATION_LABELS[key] || key)}</button>`).join("")}</div>
      <div class="cov-figures">
        <span><strong>${escapeHtml(formatCount(active.length))}</strong> ${active.length === 1 ? "person" : "people"} worked in the last ${escapeHtml(String(work.worked?.perDayWeekdays || 10))} weekdays</span>
        <span><strong>${escapeHtml(active.length ? (perDay / active.length).toFixed(1) : "0")}</strong> lines per person per weekday</span>
        <span><strong>${escapeHtml(covPercent(pastDue ? 1 - notWorked / pastDue : null))}</strong> of past-due lines touched on time</span>
        <span><strong>${escapeHtml(covPercent(decided ? sentBack / decided : null, "None"))}</strong> of requests sent back</span>
      </div>
      <div class="cov-table-wrap"><table class="cov-table cov-people">
        <thead><tr><th><button type="button" data-cov-people-sort="name" aria-pressed="${covState.peopleSort === "name"}">Person</button></th>${head("workedPerDay", "Per weekday", `Distinct lines changed per weekday, last ${work.worked?.perDayWeekdays || 10} weekdays (today excluded)`)}${head("workedLast7", "Last 7 days")}${head("onTime", "Touched on time", "Of the past-due lines this person owns, the share someone touched on or after the due date")}${head("notWorkedOwed", "Not worked since due")}${head("backlogDays", "Backlog", "Past-due lines not worked since due, divided by this person's lines per weekday")}${head("sentBack", "Sent back", "Share of this person's requests, decided in the last 30 days, that a reviewer sent back")}${head("ownedOwed", "Owns")}<th class="is-num">Waiting approval</th><th class="is-num">Reviewed, 7 days</th><th>Last worked</th></tr></thead>
        <tbody>${people.map((p) => `<tr>
          ${covPersonCell(p)}
          <td class="is-num"><strong>${escapeHtml(String(p.workedPerDay || 0))}</strong></td>
          ${num(p.workedLast7)}
          <td class="is-num ${covRateTone(p.onTimeRate)}">${escapeHtml(covPercent(p.onTimeRate))}<small>${escapeHtml(`${formatCount(p.pastDueLines)} past due`)}</small></td>
          <td class="is-num ${p.notWorkedLines ? "is-danger" : ""}">${escapeHtml(money(p.notWorkedOwed))}<small>${escapeHtml(formatCount(p.notWorkedLines))}</small></td>
          <td class="is-num ${p.backlogDays == null && p.notWorkedLines ? "is-danger" : p.backlogDays >= 10 ? "is-danger" : p.backlogDays >= 3 ? "is-warn" : ""}">${escapeHtml(covBacklogText(p.backlogDays))}</td>
          <td class="is-num ${p.sentBackRate >= 0.25 ? "is-danger" : p.sentBackRate >= 0.1 ? "is-warn" : ""}">${escapeHtml(covPercent(p.sentBackRate, "None"))}<small>${escapeHtml(p.requestsDecided ? `${formatCount(p.requestsSentBack)} of ${formatCount(p.requestsDecided)}` : "No decisions")}</small></td>
          <td class="is-num">${escapeHtml(money(p.ownedOwed))}<small>${escapeHtml(formatNounCount(p.ownedLines, "line"))}</small></td>
          ${num(p.approvalLines, "is-warn")}
          ${num(p.reviewedLast7)}
          <td>${escapeHtml(p.lastWorkedDay ? formatDate(p.lastWorkedDay) : "Not in 30 days")}</td>
        </tr>`).join("")}</tbody>
      </table></div>
    </section>
    ${pseudo.length ? `<section class="cov-card" aria-label="Lines no person owns">
      <div class="cov-card-head"><h4>Lines no person owns</h4><p>Follow-ups set by automatic rules or the import, and lines with no follow-up action. Past-due lines here need a person.</p></div>
      <div class="cov-table-wrap"><table class="cov-table cov-people">
        <thead><tr><th>Source</th><th class="is-num">Owns</th><th class="is-num">Past due</th><th class="is-num">Not worked since due</th></tr></thead>
        <tbody>${pseudo.map((p) => `<tr class="is-pseudo">${covPersonCell(p)}<td class="is-num"><strong>${escapeHtml(money(p.ownedOwed))}</strong><small>${escapeHtml(formatNounCount(p.ownedLines, "line"))}</small></td><td class="is-num">${escapeHtml(formatCount(p.pastDueLines))}</td><td class="is-num ${p.notWorkedLines ? "is-danger" : ""}">${escapeHtml(money(p.notWorkedOwed))}<small>${escapeHtml(formatCount(p.notWorkedLines))}</small></td></tr>`).join("")}</tbody>
      </table></div>
    </section>` : ""}`;
}

// TRAINEE REVIEW (Max's request, portal ticket 2026-10-26). Approval requests
// per person, what came back and why, and how fast each supervisor decides. The
// server does the counting (backend/oversightTraineeReview.js).
const COV_LOCATION_LABELS = { stateside: "In-house (stateside)", colombia: "Colombia", india: "India", notSet: "Location not set" };

function covHours(hours) {
  if (hours == null || !Number.isFinite(hours)) return "None yet";
  return hours >= 48 ? `${Math.round(hours / 24)} days` : `${Math.round(hours)} hours`;
}

function covChange(now, before) {
  if (now == null || before == null) return "";
  const points = Math.round((now - before) * 100);
  return points === 0 ? "No change from the 4 weeks before" : `${points < 0 ? "Down" : "Up"} ${Math.abs(points)} points from the 4 weeks before`;
}

function renderCovTrainee() {
  if (covState.traineeStatus === "error") return `<div class="cov-message is-error" role="alert">${escapeHtml(covState.traineeError)} <button type="button" class="ghost-button" data-cov-trainee-retry>Try again</button></div>`;
  const review = covState.trainee;
  if (!review) return '<div class="cov-message">Loading trainee review…</div>';
  const totals = review.totals || {};
  if (!totals.requests) return '<div class="cov-message">No approval requests yet.</div>';
  const rateTone = (rate) => rate >= 0.2 ? "is-danger" : rate >= 0.1 ? "is-warn" : "";
  const num = (value, tone = "") => `<td class="is-num ${value && tone ? tone : ""}">${escapeHtml(formatCount(value || 0))}</td>`;
  const peopleRows = (review.people || []).map((p) => {
    const resubmit = p.resubmitted ? `${formatCount(p.resubmittedApproved)} of ${formatCount(p.resubmitted)}` : "None";
    return `<tr>
      <th scope="row"><strong>${escapeHtml(p.name)}</strong><small>${escapeHtml(COV_LOCATION_LABELS[p.location || "notSet"])}</small></th>
      <td class="is-num"><strong>${escapeHtml(formatCount(p.thisWeek))}</strong><small>${escapeHtml(`${formatCount(p.lastWeek)} last week`)}</small></td>
      <td class="is-num">${escapeHtml(formatCount(p.requests))}</td>
      <td class="is-num ${rateTone(p.sentBackRate)}">${escapeHtml(covPercent(p.sentBackRate, "None decided"))}<small>${escapeHtml(`${formatCount(p.sentBack)} of ${formatCount(p.decided)}`)}</small></td>
      <td class="is-num ${rateTone(p.recentRate)}" title="${escapeHtml(covChange(p.recentRate, p.priorRate))}">${escapeHtml(covPercent(p.recentRate, "None decided"))}<small>${escapeHtml(covPercent(p.priorRate, "none") + " before")}</small></td>
      ${num(p.changedAfterApproval, "is-warn")}
      ${num(p.simpleMistakes, "is-warn")}
      ${num(p.hardClaims)}
      <td class="is-num">${escapeHtml(resubmit)}</td>
      ${num(p.waiting)}
      <td>${p.repeats.length ? escapeHtml(p.repeats.map((item) => `${item.label} (${formatCount(item.count)})`).join(", ")) : "None"}</td>
    </tr>`;
  }).join("");
  const reasonCards = (review.people || []).filter((p) => p.reasons.length).map((p) => `<details class="cov-trainee-reasons">
      <summary><strong>${escapeHtml(p.name)}</strong> <span>${escapeHtml(p.categories.map((item) => `${item.label}: ${formatCount(item.count)}`).join(" · "))}</span></summary>
      <ul>${p.reasons.map((reason) => `<li><span class="cov-trainee-note">${escapeHtml(reason.text)}</span><small>${escapeHtml([reason.category, reason.action, reason.by ? `from ${reason.by}` : "", reason.lastAt ? formatDate(reason.lastAt) : "", formatNounCount(reason.lines, "line")].filter(Boolean).join(" · "))}</small></li>`).join("")}</ul>
    </details>`).join("");
  return `<section class="cov-card" aria-label="Trainee review summary">
      <div class="cov-card-head"><h4>Trainee review</h4><p>Every approval request, one per service line, grouped by the week it was sent. Internal to TMS. The filters above do not change this tab.</p></div>
      <div class="cov-figures">
        <span><strong>${escapeHtml(formatCount(totals.thisWeek))}</strong> requests this week, ${escapeHtml(formatCount(totals.lastWeek))} last week</span>
        <span><strong>${escapeHtml(covPercent(totals.recentRate, "None"))}</strong> sent back, last 4 weeks (${escapeHtml(covPercent(totals.priorRate, "none"))} the 4 weeks before)</span>
        <span><strong>${escapeHtml(formatCount(totals.changedAfterApproval))}</strong> changed by the supervisor within a day of approving</span>
        <span><strong>${escapeHtml(covHours(totals.medianHours))}</strong> typical wait for a decision</span>
        <span><strong>${escapeHtml(formatCount(totals.waiting))}</strong> waiting now</span>
      </div>
      <div class="cov-table-wrap"><table class="cov-table cov-people cov-trainee-table">
        <thead><tr><th>Person</th><th class="is-num">This week</th><th class="is-num">All requests</th><th class="is-num">Sent back</th><th class="is-num" title="Share sent back in the last 4 weeks, against the 4 weeks before">Last 4 weeks</th><th class="is-num" title="Approved, then changed by the same supervisor within 24 hours">Changed after approval</th><th class="is-num" title="Wrong action, missing reason or misread EOB, read from the supervisor's note. A best guess.">Simple mistakes</th><th class="is-num" title="The note asks for more research. A best guess.">Harder claims</th><th class="is-num" title="Requests sent again after coming back, and how many of those were approved">Resubmits approved</th><th class="is-num">Waiting</th><th title="The same kind of simple mistake in two or more different weeks">Repeat mistakes</th></tr></thead>
        <tbody>${peopleRows}</tbody>
      </table></div>
    </section>
    <section class="cov-card" aria-label="Weekly requests and send-backs">
      <div class="cov-card-head"><h4>Week by week</h4><p>The latest week fills in as requests are decided.</p></div>
      <div class="cov-table-wrap"><table class="cov-table">
        <thead><tr><th>Week of</th><th class="is-num">Requests</th><th class="is-num">Decided</th><th class="is-num">Sent back</th><th class="is-num">Share sent back</th></tr></thead>
        <tbody>${(review.weeks || []).slice().reverse().map((row) => `<tr><th scope="row">${escapeHtml(formatDate(row.week))}</th><td class="is-num">${escapeHtml(formatCount(row.requests))}</td><td class="is-num">${escapeHtml(formatCount(row.decided))}</td><td class="is-num">${escapeHtml(formatCount(row.sentBack))}</td><td class="is-num ${rateTone(row.sentBackRate)}">${escapeHtml(covPercent(row.sentBackRate, "None decided"))}</td></tr>`).join("")}</tbody>
      </table></div>
    </section>
    <section class="cov-card" aria-label="Supervisor turnaround">
      <div class="cov-card-head"><h4>Supervisor turnaround</h4><p>How long requests waited before a decision. Waiting now goes to whoever last decided that person's requests for that company${totals.waitingUnrouted ? `; ${escapeHtml(formatCount(totals.waitingUnrouted))} have no earlier decision to go by` : ""}.</p></div>
      <div class="cov-table-wrap"><table class="cov-table">
        <thead><tr><th>Supervisor</th><th class="is-num">Decisions</th><th class="is-num">Approved</th><th class="is-num">Sent back</th><th class="is-num">Typical wait</th><th class="is-num">Slowest 10%</th><th class="is-num">Waiting now</th><th>Oldest waiting</th></tr></thead>
        <tbody>${(review.supervisors || []).map((row) => `<tr><th scope="row">${escapeHtml(row.name)}</th>${num(row.decisions)}${num(row.approved)}${num(row.sentBack)}<td class="is-num">${escapeHtml(covHours(row.medianHours))}</td><td class="is-num">${escapeHtml(covHours(row.slowestTenthHours))}</td>${num(row.waiting, "is-warn")}<td>${escapeHtml(row.oldestWaitingAt ? formatDate(row.oldestWaitingAt) : "None")}</td></tr>`).join("")}</tbody>
      </table></div>
    </section>
    <section class="cov-card" aria-label="By location">
      <div class="cov-card-head"><h4>By location</h4><p>Where each person is based comes from the Org Map in Manage Users.</p></div>
      <div class="cov-table-wrap"><table class="cov-table">
        <thead><tr><th>Location</th><th class="is-num">People</th><th class="is-num">Requests</th><th class="is-num">Sent back</th><th class="is-num">Share sent back</th><th class="is-num">Waiting</th></tr></thead>
        <tbody>${(review.locations || []).map((row) => `<tr><th scope="row">${escapeHtml(COV_LOCATION_LABELS[row.key] || row.key)}</th>${num(row.people)}${num(row.requests)}${num(row.sentBack)}<td class="is-num ${rateTone(row.sentBackRate)}">${escapeHtml(covPercent(row.sentBackRate, "None decided"))}</td>${num(row.waiting)}</tr>`).join("")}</tbody>
      </table></div>
    </section>
    ${reasonCards ? `<section class="cov-card" aria-label="Why requests came back">
      <div class="cov-card-head"><h4>Why requests came back</h4><p>The supervisor's own notes, newest first. The category is a best guess from the wording.</p></div>
      ${reasonCards}
    </section>` : ""}`;
}

// STAFFING (Nathan, 2026-10-29: "figure out how many people are really needed
// per account"). The server sizes each company from its own work and the
// team's real output per person; this only lays it out.
function renderCovStaffing() {
  const work = covState.work;
  if (!work) return covWorkPending("Staffing");
  const staffing = work.staffing;
  if (!staffing) return '<div class="cov-message">Staffing could not be worked out.</div>';
  const rows = (staffing.orgs || []).filter((row) => !covState.filters.orgs.length || covState.filters.orgs.includes(row.orgId));
  const fte = (value) => (value == null ? "Unknown" : value.toFixed(1));
  const gapCell = (row) => {
    if (row.peopleNeeded == null) return '<td class="is-num">Unknown</td>';
    const gap = Math.round((row.peopleNeeded - (row.peopleWorking || 0)) * 10) / 10;
    return `<td class="is-num ${gap >= 1 ? "is-danger" : gap >= 0.3 ? "is-warn" : ""}"><strong>${escapeHtml(gap > 0 ? `${gap.toFixed(1)} short` : gap < 0 ? `${(-gap).toFixed(1)} spare` : "Even")}</strong></td>`;
  };
  const totals = staffing.totals || {};
  return `<section class="cov-card" aria-label="Staffing">
      <div class="cov-card-head"><h4>People needed per company</h4><p>Work needed each weekday = follow-ups coming due in the next ${escapeHtml(String(staffing.dueAheadWeekdays))} weekdays, plus the backlog (past due and not worked since, or no action and billed 30+ days ago) cleared over ${escapeHtml(String(staffing.backlogClearWeekdays))} weekdays. One person = what the average active person works in a weekday: <strong>${escapeHtml(String(staffing.perPersonPerDay))} lines</strong> (${escapeHtml(formatNounCount(staffing.activePeople, "person", "people"))}). People working = the company's lines per weekday in people. ${covAnyFilter() ? "Staffing always counts every line of a company; only the company filter applies here." : ""}</p></div>
      <div class="cov-figures"><span>Needed <strong>${escapeHtml(fte(totals.peopleNeeded))}</strong> people</span><span>Working <strong>${escapeHtml(fte(totals.peopleWorking))}</strong> people</span><span><strong>${escapeHtml(formatCount(Math.round(totals.neededPerDay || 0)))}</strong> lines needed per weekday</span><span><strong>${escapeHtml(formatCount(Math.round(totals.workedPerDay || 0)))}</strong> worked per weekday</span></div>
      <div class="cov-table-wrap"><table class="cov-table">
        <thead><tr><th>Company</th><th class="is-num" title="Lines whose follow-up date falls in the next ${escapeHtml(String(staffing.dueAheadWeekdays))} weekdays">Coming due</th><th class="is-num" title="Past due and not worked since, plus no action and billed 30+ days ago">Backlog</th><th class="is-num">Needed / weekday</th><th class="is-num">Worked / weekday</th><th class="is-num">People needed</th><th class="is-num">People working</th><th class="is-num">Short or spare</th><th class="is-num" title="Different people who worked this company in the last 7 days">People who touched it, 7 days</th></tr></thead>
        <tbody>${rows.map((row) => `<tr>
          <th scope="row"><button type="button" class="cov-company" data-cov-client="${escapeHtml(row.orgId)}" data-cov-client-tab="people">${escapeHtml(covCompanyName(row.orgId))}</button></th>
          <td class="is-num">${escapeHtml(formatCount(row.dueSoonLines))}</td>
          <td class="is-num ${row.backlogLines ? "is-warn" : ""}">${escapeHtml(formatCount(row.backlogLines))}</td>
          <td class="is-num">${escapeHtml(String(row.neededPerDay))}</td>
          <td class="is-num">${escapeHtml(String(row.workedPerDay))}</td>
          <td class="is-num"><strong>${escapeHtml(fte(row.peopleNeeded))}</strong></td>
          <td class="is-num">${escapeHtml(fte(row.peopleWorking))}</td>
          ${gapCell(row)}
          <td class="is-num">${escapeHtml(formatCount(row.peopleLast7))}</td>
        </tr>`).join("")}</tbody>
      </table></div>
    </section>`;
}

function covAgo(iso) {
  const hours = (Date.now() - Date.parse(iso)) / 3600000;
  if (!Number.isFinite(hours)) return "Unknown";
  if (hours < 1) return "Under an hour";
  if (hours < 48) return `${Math.round(hours)} hours`;
  return `${Math.round(hours / 24)} days`;
}

function renderCovApprovals() {
  const work = covState.work;
  if (!work) return covWorkPending("Approvals");
  const approvals = work.approvals || {};
  const reviewers = (work.people || []).filter((p) => p.reviewDecisions > 0).sort((a, b) => b.reviewDecisions - a.reviewDecisions);
  return `<section class="cov-card" aria-label="Approvals waiting">
      <div class="cov-card-head"><h4>Approvals waiting</h4><p>Follow-up requests a biller sent for approval and no one has decided yet. Open owed is what those lines still owe today.</p></div>
      <div class="cov-ages">${(approvals.ages || []).map((age) => `<div class="cov-age ${age.key === "older" && age.lines ? "is-danger" : age.key === "week" && age.lines ? "is-warn" : ""}"><span>${escapeHtml(age.label)}</span><strong>${escapeHtml(formatCount(age.lines))}</strong><small>${escapeHtml(money(age.owed))} open</small></div>`).join("")}</div>
      ${approvals.lines ? `<div class="cov-split">
        <div><h5>By company</h5><div class="cov-table-wrap"><table class="cov-table">
          <thead><tr><th>Company</th><th class="is-num">Requests</th><th class="is-num">Open owed</th><th class="is-num">Over 3 days</th><th>Oldest</th><th></th></tr></thead>
          <tbody>${(approvals.byOrg || []).map((row) => `<tr><th scope="row">${escapeHtml(covCompanyName(row.orgId))}</th><td class="is-num">${escapeHtml(formatCount(row.lines))}</td><td class="is-num">${escapeHtml(money(row.owed))}</td><td class="is-num ${row.over3Days ? "is-danger" : ""}">${escapeHtml(formatCount(row.over3Days))}</td><td>${escapeHtml(row.oldestAt ? `${covAgo(row.oldestAt)} ago` : "Unknown")}</td><td><button type="button" class="ghost-button" data-cov-approvals="${escapeHtml(row.orgId)}">Review</button></td></tr>`).join("")}</tbody>
        </table></div></div>
        <div><h5>By who asked</h5><div class="cov-table-wrap"><table class="cov-table">
          <thead><tr><th>Person</th><th class="is-num">Requests</th><th class="is-num">Open owed</th><th>Oldest</th></tr></thead>
          <tbody>${(approvals.bySubmitter || []).map((row) => `<tr><th scope="row">${escapeHtml(row.name)}</th><td class="is-num">${escapeHtml(formatCount(row.lines))}</td><td class="is-num">${escapeHtml(money(row.owed))}</td><td>${escapeHtml(row.oldestAt ? `${covAgo(row.oldestAt)} ago` : "Unknown")}</td></tr>`).join("")}</tbody>
        </table></div></div>
      </div>` : '<div class="cov-message">Nothing is waiting for approval.</div>'}
    </section>
    <section class="cov-card" aria-label="Reviewers">
      <div class="cov-card-head"><h4>Reviewers, last 30 days</h4><p>Decisions made and how long requests waited before a decision.</p></div>
      ${reviewers.length ? `<div class="cov-table-wrap"><table class="cov-table">
        <thead><tr><th>Reviewer</th><th class="is-num">Decisions</th><th class="is-num">Approved</th><th class="is-num">Sent back</th><th class="is-num">Average wait</th></tr></thead>
        <tbody>${reviewers.map((p) => `<tr><th scope="row">${escapeHtml(p.name)}</th><td class="is-num">${escapeHtml(formatCount(p.reviewDecisions))}</td><td class="is-num">${escapeHtml(formatCount(p.reviewApproved))}</td><td class="is-num">${escapeHtml(formatCount(p.reviewRejected))}</td><td class="is-num ${p.avgReviewHours >= 72 ? "is-danger" : p.avgReviewHours >= 24 ? "is-warn" : ""}">${escapeHtml(p.avgReviewHours == null ? "Unknown" : p.avgReviewHours >= 48 ? `${(p.avgReviewHours / 24).toFixed(1)} days` : `${p.avgReviewHours} hours`)}</td></tr>`).join("")}</tbody>
      </table></div>` : '<div class="cov-message">No approval decisions in the last 30 days.</div>'}
    </section>`;
}

// Small inline SVG charts. Theme tokens only, so dark mode follows.
function covBarChart(days) {
  const width = 900, height = 180, pad = 26, gap = 4;
  const max = Math.max(1, ...days.map((d) => d.worked + d.reviewed));
  const bar = (width - pad * 2) / days.length;
  const y = (value) => height - pad - (value / max) * (height - pad * 2);
  return `<svg class="cov-chart" viewBox="0 0 ${width} ${height}" role="img" aria-label="Lines worked and reviewed per day, last ${days.length} days">
    <line x1="${pad}" x2="${width - pad}" y1="${height - pad}" y2="${height - pad}" class="cov-axis"/>
    <text x="${pad}" y="14" class="cov-axis-label">${escapeHtml(formatCount(max))} lines</text>
    ${days.map((d, index) => {
      const x = pad + index * bar + gap / 2;
      const w = Math.max(1, bar - gap);
      const weekday = new Date(`${d.day}T12:00:00Z`).getUTCDay();
      return `<g><title>${escapeHtml(`${formatDate(d.day)}: ${formatCount(d.worked)} worked, ${formatCount(d.reviewed)} reviewed, ${formatNounCount(d.people, "person", "people")}`)}</title>
        <rect x="${x}" y="${y(d.worked)}" width="${w}" height="${height - pad - y(d.worked)}" class="cov-bar-worked"/>
        <rect x="${x}" y="${y(d.worked + d.reviewed)}" width="${w}" height="${y(d.worked) - y(d.worked + d.reviewed)}" class="cov-bar-reviewed"/>
        ${weekday === 1 ? `<text x="${x}" y="${height - 8}" class="cov-axis-label">${escapeHtml(d.day.slice(5))}</text>` : ""}</g>`;
    }).join("")}
  </svg>`;
}

// One small chart per figure, each on its own scale: on one shared axis the
// $7M total flattens every smaller line and two close figures hide each other.
function covLineChart(series, key, color) {
  const width = 420, height = 120, pad = 8;
  const values = series.map((row) => Number(row[key]) || 0);
  const low = Math.min(...values), high = Math.max(...values);
  const span = high - low || Math.max(1, high * 0.05);
  const x = (index) => pad + index * ((width - pad * 2) / Math.max(1, series.length - 1));
  const y = (value) => height - pad - ((value - low) / span) * (height - pad * 2);
  const points = series.map((row, index) => `${x(index).toFixed(1)},${y(Number(row[key]) || 0).toFixed(1)}`).join(" ");
  return `<svg class="cov-chart" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" aria-label="${escapeHtml(`From ${money(values[0])} on ${formatDate(series[0].day)} to ${money(values[values.length - 1])} on ${formatDate(series[series.length - 1].day)}`)}" style="color:${color}">
    <polygon points="${pad},${height - pad} ${points} ${x(series.length - 1).toFixed(1)},${height - pad}" fill="currentColor" opacity=".08"/>
    <polyline fill="none" stroke="currentColor" stroke-width="2.5" vector-effect="non-scaling-stroke" points="${points}"/>
  </svg>`;
}

function renderCovWorked(summary) {
  const work = covState.work;
  if (!work) return covWorkPending("Worked per day");
  const days = work.worked?.days || [];
  const orgRows = (summary.clients || []).map((client) => ({ client, workOrg: covWorkOrg(client.orgId) || {} }))
    .sort((a, b) => (b.workOrg.workedLast7 || 0) - (a.workOrg.workedLast7 || 0) || (b.workOrg.notWorkedOwed || 0) - (a.workOrg.notWorkedOwed || 0));
  return `<section class="cov-card" aria-label="Worked per day">
      <div class="cov-card-head"><h4>Lines worked per day</h4><p>Distinct lines people changed each day (dark) and approvals or send-backs (light), last ${escapeHtml(String(days.length))} days. Automatic rules are not counted.${covAnyFilter() ? " This tab always shows all work; filters do not apply." : ""}</p></div>
      <div class="cov-figures"><span><strong>${escapeHtml(formatCount(work.totals.workedToday))}</strong> today</span><span><strong>${escapeHtml(formatCount(work.totals.workedLast7))}</strong> last 7 days</span><span><strong>${escapeHtml(String(work.totals.workedPerDay))}</strong> per weekday</span><span><strong>${escapeHtml(formatCount(work.totals.reviewedLast7))}</strong> reviewed, 7 days</span></div>
      ${covBarChart(days)}
    </section>
    <section class="cov-card" aria-label="Worked by company">
      <div class="cov-card-head"><h4>By company</h4><p>Where the work went, next to the past-due money no one has touched.</p></div>
      <div class="cov-table-wrap"><table class="cov-table">
        <thead><tr><th>Company</th><th class="is-num">Worked today</th><th class="is-num">Last 7 days</th><th>Last worked</th><th class="is-num">Not worked since due</th><th class="is-num">Outstanding</th></tr></thead>
        <tbody>${orgRows.map(({ client, workOrg }) => `<tr>
          <th scope="row"><button type="button" class="cov-company" data-cov-client="${escapeHtml(client.orgId)}" data-cov-client-tab="people">${escapeHtml(covCompanyName(client.orgId))}</button></th>
          <td class="is-num">${escapeHtml(formatCount(workOrg.workedToday || 0))}</td>
          <td class="is-num">${escapeHtml(formatCount(workOrg.workedLast7 || 0))}</td>
          <td class="${workOrg.lastWorkedDay ? "" : "is-warn"}">${escapeHtml(workOrg.lastWorkedDay ? formatDate(workOrg.lastWorkedDay) : "Not in 30 days")}</td>
          <td class="is-num ${workOrg.notWorkedOwed ? "is-danger" : ""}">${escapeHtml(money(workOrg.notWorkedOwed || 0))}<small>${escapeHtml(formatCount(workOrg.notWorkedLines || 0))}</small></td>
          <td class="is-num">${escapeHtml(money(client.owed))}</td>
        </tr>`).join("")}</tbody>
      </table></div>
    </section>`;
}

function renderCovTrend() {
  const work = covState.work;
  if (!work) return covWorkPending("Trend");
  const series = covTrendSeries();
  const first = series[0]?.day;
  const note = !work.trend?.available
    ? "Saved history is not available yet; it starts after the next release adds its table. Today's figures are shown."
    : series.length < 2 ? `History starts ${escapeHtml(formatDate(first || work.today))}. One point is saved for every company each day, so the lines fill in from tomorrow and the week-over-week arrows appear after 7 days.` : `Saved daily since ${escapeHtml(formatDate(first))}. The last save of each day stands for that day.`;
  const last = series[series.length - 1] || {};
  return `<section class="cov-card" aria-label="Trend">
      <div class="cov-card-head"><h4>Trend, ${escapeHtml(covScopeName())}</h4><p>${note}${covAnyFilter() ? " This tab always shows every line; filters do not apply." : ""}</p></div>
      <div class="cov-trend-grid">${COV_TREND_SERIES.map((s) => {
        const values = series.map((row) => Number(row[s.key]) || 0);
        return `<div class="cov-trend-card">
          <div class="cov-trend-head"><span><i style="background:${s.color}"></i>${escapeHtml(s.label)}</span><strong>${escapeHtml(compactMoney(last[s.key] || 0))}</strong></div>
          ${covDeltaHtml(covTrendDelta(series, s.key))}
          ${series.length > 1 ? `${covLineChart(series, s.key, s.color)}<div class="cov-trend-range"><span>${escapeHtml(formatDate(series[0].day))}</span><span>Low ${escapeHtml(compactMoney(Math.min(...values)))} · High ${escapeHtml(compactMoney(Math.max(...values)))}</span><span>${escapeHtml(formatDate(last.day))}</span></div>` : ""}
        </div>`;
      }).join("")}</div>
    </section>`;
}

function covSectionTabs() {
  const tab = (key, label, count = "") => `<button type="button" role="tab" data-cov-section="${key}" aria-selected="${covState.section === key}">${escapeHtml(label)}${count !== "" ? ` <span class="cov-count">${escapeHtml(count)}</span>` : ""}</button>`;
  const work = covState.work;
  return `<div class="cov-tabs cov-section-tabs" role="tablist" aria-label="Oversight views">${tab("overview", "Overview")}${tab("companies", "Companies", formatCount(covState.summary?.clients?.length || 0))}${tab("people", "People and productivity")}${tab("staffing", "Staffing")}${tab("approvals", "Approvals waiting", work ? formatCount(work.approvals?.lines || 0) : "")}${tab("trainee", "Trainee review")}${tab("worked", "Worked per day")}${tab("trend", "Trend")}</div>`;
}

// FILTERS (Nathan, 2026-10-27: "filters for each follow-up action"; 2026-10-29:
// "the filter should make it easier to see what's really going on"). Company,
// payer, reason, age since billed, follow-up status and follow-up action. Each
// choice shows its dollars under the OTHER filters; the selection narrows every
// tab, the company view and every All Data drill.
const COV_FILTERS = [
  { key: "orgs", label: "Company", all: "All companies", noun: "companies", name: (value) => covCompanyName(value) },
  { key: "payers", label: "Payer", all: "All payers", noun: "payers", name: (value) => (value && value !== "(no payer)" ? value : "No payer") },
  { key: "reasons", label: "Why unpaid", all: "All reasons", noun: "reasons", name: (value) => covCatalogLabel("reasons", value) },
  { key: "aging", label: "Age since billed", all: "Any age", noun: "ages", name: (value) => (value && value !== "(blank)" ? `${value} days` : BILL_DATE_NOT_BILLED) },
  { key: "status", label: "Follow-up status", all: "Any status", noun: "statuses", name: (value) => covCatalogLabel("statuses", value) },
  { key: "actions", label: "Follow-up action", all: "All actions", noun: "actions", name: (value) => covActionName(value) }
];

function covActionName(action) {
  return action || "No follow-up action";
}

function covCatalogLabel(kind, key) {
  const row = (covState.summary?.catalog?.[kind] || []).find((item) => item.key === key);
  return row ? row.label : key || "None";
}

// The header company is the page's scope, not a filter.
function covAnyFilter() {
  return Object.entries(covState.filters).some(([key, values]) => values.length && !(key === "orgs" && covHeaderScoped()));
}

function covFilterLabel() {
  const parts = COV_FILTERS.filter((dim) => covState.filters[dim.key].length).map((dim) => {
    const values = covState.filters[dim.key];
    return values.length === 1 ? dim.name(values[0]) : `${formatCount(values.length)} ${dim.noun}`;
  });
  return parts.length ? parts.join(", ") : "Everything";
}

function setCovFilter(key, values) {
  covState.filters = { ...covState.filters, [key]: [...new Set(values)] };
  covState.reasonFocus = "";
  clearTimeout(covState.reloadTimer);
  covState.reloadTimer = setTimeout(() => {
    void loadCovSummary(true);
    if (covState.orgId) void openCovClient(covState.orgId, covState.tab);
  }, 250);
  renderCompanyOversight();
}

function clearCovFilters() {
  Object.keys(covState.filters).forEach((key) => { covState.filters[key] = []; });
  covState.filters.orgs = covHeaderOrgIds();
  setCovFilter("actions", []);
}

// Kept for the "Show only" buttons on the follow-up action tables.
function setCovActions(actions) {
  setCovFilter("actions", actions);
}

function covFacets() {
  return (covState.orgId && covState.client?.facets) || covState.summary?.facets || {};
}

function renderCovFilter() {
  const facets = covFacets();
  const updating = covState.summaryStatus === "loading" || covState.workStatus === "loading";
  const shown = Number(covState.summary?.totals?.owed) || 0;
  const grand = covState.grandOwed || shown;
  const open = COV_FILTERS.find((dim) => dim.key === covState.filterOpen);
  const button = (dim) => {
    if (dim.key === "orgs" && covState.orgId) return "";
    if (dim.key === "orgs" && covHeaderScoped()) return `<label class="cov-filter-field"><span>${escapeHtml(dim.label)}</span><span class="cov-filter-toggle is-active" title="Change the company at the top of the portal">${escapeHtml(covScopeName())}</span></label>`;
    const values = covState.filters[dim.key];
    const text = !values.length ? dim.all : values.length === 1 ? dim.name(values[0]) : `${formatCount(values.length)} ${dim.noun}`;
    return `<label class="cov-filter-field"><span>${escapeHtml(dim.label)}</span><button type="button" class="cov-filter-toggle ${values.length ? "is-active" : ""}" data-cov-filter-toggle="${dim.key}" aria-expanded="${covState.filterOpen === dim.key}">${escapeHtml(text)} <span aria-hidden="true">▾</span></button></label>`;
  };
  const chips = COV_FILTERS.filter((dim) => !(dim.key === "orgs" && covHeaderScoped())).flatMap((dim) => covState.filters[dim.key].map((value) => `<span class="cov-chip"><b>${escapeHtml(dim.label)}:</b> ${escapeHtml(dim.name(value))}<button type="button" data-cov-filter-value="${escapeHtml(value)}" data-cov-filter-key="${dim.key}" aria-label="${escapeHtml(`Remove ${dim.name(value)}`)}">×</button></span>`)).join("");
  let panel = "";
  if (open) {
    const selected = new Set(covState.filters[open.key].map((value) => value.toLowerCase()));
    const search = covState.filterSearch.trim().toLowerCase();
    const rows = facets[open.key] || [];
    panel = `<div class="cov-filter-panel" role="group" aria-label="${escapeHtml(`Choose ${open.label.toLowerCase()}`)}">
      <div class="cov-filter-tools">
        <input type="search" class="cov-filter-search" data-cov-filter-search placeholder="${escapeHtml(`Find ${open.label.toLowerCase()}`)}" value="${escapeHtml(covState.filterSearch)}" aria-label="${escapeHtml(`Find ${open.label.toLowerCase()}`)}">
        <button type="button" class="ghost-button" data-cov-filter-all="${open.key}">Select all shown</button>
        <button type="button" class="ghost-button" data-cov-filter-clear="${open.key}">Clear</button>
        <button type="button" class="primary-button" data-cov-filter-toggle="">Done</button>
      </div>
      <div class="cov-filter-head" aria-hidden="true"><span></span><span>${escapeHtml(open.label)}</span><span>Outstanding</span><span>Lines</span><span>Past due</span></div>
      <div class="cov-filter-list">${rows.length ? rows.map((row) => `<button type="button" class="cov-action-option" data-cov-filter-key="${open.key}" data-cov-filter-value="${escapeHtml(row.value)}" aria-pressed="${selected.has(row.value.toLowerCase())}"${search && !open.name(row.value).toLowerCase().includes(search) ? " hidden" : ""}>
          <span class="cov-check" aria-hidden="true"></span>
          <span class="cov-action-name">${escapeHtml(open.name(row.value))}</span>
          <strong>${escapeHtml(money(row.owed))}</strong>
          <small>${escapeHtml(formatCount(row.lines))}</small>
          <small class="${row.pastDueLines ? "is-warn" : ""}">${escapeHtml(formatCount(row.pastDueLines))}</small>
        </button>`).join("") : '<div class="cov-message">Nothing matches the other filters.</div>'}</div>
    </div>`;
  }
  return `<section class="cov-filter" aria-label="Filters">
      <div class="cov-filter-row">${COV_FILTERS.map(button).join("")}
        <span class="cov-filter-scope">${updating ? "Updating…" : covAnyFilter() ? `Showing ${escapeHtml(money(shown))} of ${escapeHtml(money(grand))} (${covShare(shown, grand)})` : `${escapeHtml(money(shown))} outstanding, nothing filtered`}</span>
      </div>
      ${chips ? `<div class="cov-filter-chips">${chips}<button type="button" class="cov-linkish" data-cov-filters-clear>Clear all filters</button></div>` : ""}
      ${panel}
    </section>`;
}

function covPercent(rate, empty = "None due") {
  return rate == null || !Number.isFinite(rate) ? empty : `${Math.round(rate * 100)}%`;
}

// Touched-on-time: under half is a problem, under 80% needs a look.
function covRateTone(rate) {
  if (rate == null) return "";
  return rate < 0.5 ? "is-danger" : rate < 0.8 ? "is-warn" : "is-good";
}

function covKpi(label, value, detail, tone = "", title = "") {
  return `<div class="cov-kpi ${tone}"${title ? ` title="${escapeHtml(title)}"` : ""}><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong><small>${escapeHtml(detail)}</small></div>`;
}

// THE SCORECARD: rates, not just dollars, so a company of $50K and one of $1M
// read on the same scale, and growth shows up as people needed.
function renderCovScorecard(totals, workTotals, staffing, people, scope = "every company") {
  const work = covState.work;
  const pending = !work;
  const pastDueShare = totals.owed > 0 ? totals.pastDueOwed / totals.owed : null;
  const onTime = workTotals?.onTimeRate ?? null;
  const decided = (people || []).reduce((sum, p) => sum + (p.requestsDecided || 0), 0);
  const sentBack = (people || []).reduce((sum, p) => sum + (p.requestsSentBack || 0), 0);
  const active = workTotals?.activePeople || 0;
  const perPerson = active ? (workTotals.workedPerDay / active) : 0;
  const backlog = workTotals?.backlogDays;
  const need = staffing?.peopleNeeded;
  const have = staffing?.peopleWorking;
  return `<section class="cov-kpis" aria-label="${escapeHtml(`Scorecard, ${scope}`)}">
      ${covKpi("Past due", pastDueShare == null ? "None" : `${Math.round(pastDueShare * 100)}%`, `of outstanding is past its follow-up date (${compactMoney(totals.pastDueOwed || 0)})`, pastDueShare >= 0.4 ? "is-danger" : pastDueShare >= 0.2 ? "is-warn" : "is-good")}
      ${covKpi("Touched on time", pending ? "…" : covPercent(onTime), pending ? "Loading" : `of past-due lines were worked on or after the due date`, covRateTone(onTime), "Past-due lines a person touched on or after the follow-up date, out of all past-due lines")}
      ${covKpi("Backlog", pending ? "…" : backlog == null ? "No one working" : `${backlog >= 10 ? Math.round(backlog) : backlog} days`, pending ? "Loading" : `of work at today's pace to catch up on ${formatCount(workTotals?.notWorkedLines || 0)} untouched past-due lines`, backlog == null || backlog >= 10 ? "is-danger" : backlog >= 3 ? "is-warn" : "is-good", "Past-due lines not worked since due, divided by lines worked per weekday")}
      ${covKpi("Output per person", pending ? "…" : perPerson ? `${perPerson.toFixed(1)} lines` : "None", pending ? "Loading" : `per weekday, ${formatNounCount(active, "person", "people")} working (${formatCount(Math.round(workTotals?.workedPerDay || 0))} lines a day)`, "")}
      ${covKpi("People needed", pending ? "…" : need == null ? "Unknown" : need.toFixed(1), pending ? "Loading" : have == null ? "" : `vs ${have.toFixed(1)} working now${need - have >= 0.5 ? `, short ${(need - have).toFixed(1)}` : ""}`, need != null && have != null && need - have >= 1 ? "is-danger" : need != null && have != null && need - have >= 0.3 ? "is-warn" : "is-good", "Work needed per weekday (coming due + backlog over 4 weeks) divided by one person's average day")}
      ${covKpi("Sent back", pending ? "…" : covPercent(decided ? sentBack / decided : null, "None"), pending ? "Loading" : decided ? `of ${formatCount(decided)} requests decided in 30 days${scope === "every company" ? "" : ", by the people on this company (all their companies)"}` : "No decisions in 30 days", decided && sentBack / decided >= 0.25 ? "is-danger" : decided && sentBack / decided >= 0.1 ? "is-warn" : decided ? "is-good" : "")}
    </section>`;
}

function renderCovGuide() {
  const term = (name, text) => `<div><dt>${escapeHtml(name)}</dt><dd>${escapeHtml(text)}</dd></div>`;
  return `<details class="cov-guide"${covState.guideOpen ? " open" : ""} data-cov-guide>
      <summary>How to read this page</summary>
      <dl>
        ${term("Outstanding", "Insurance balance still owed on every open line (the AR report's open total). Written-off lines are left out.")}
        ${term("Past due", "Lines with a follow-up action whose follow-up date has passed.")}
        ${term("Not worked since due", "Past due, and no person has touched the line on or after its follow-up date. Automatic rules do not count as work.")}
        ${term("No one on it", "No follow-up action at all, billed more than 30 days ago, and not paid.")}
        ${term("Owns", "The person who set the line's current follow-up action. Automatic rules and imported actions are shown as their own rows.")}
        ${term("Worked", "Lines a person changed (follow-up, request, update). Approvals and send-backs count as reviewed, not worked.")}
        ${term("Approvals waiting", "Follow-up requests sent for approval that no one has decided yet.")}
        ${term("Touched on time", "Of the lines past their follow-up date, the share a person touched on or after that date. The higher the better.")}
        ${term("Backlog", "Past-due lines no one has touched since they came due, divided by the lines worked per weekday: how many workdays it would take to catch up at today's pace.")}
        ${term("Output per person", "Distinct lines changed per weekday over the last 10 weekdays, divided by the people who worked.")}
        ${term("People needed", "Follow-ups coming due in the next 10 weekdays plus the backlog cleared over 20 weekdays, divided by one person's average day. People working is the same company's actual lines per weekday in people.")}
        ${term("Sent back", "Share of a person's requests decided in the last 30 days that a reviewer sent back.")}
        ${term("Filters", "Company, payer, why unpaid, age since billed, follow-up status and follow-up action. Each choice shows its dollars under the other filters. Worked per day, Trend and Staffing always count every line.")}
      </dl>
    </details>`;
}

function renderCovOverview(summary) {
  const totals = summary.totals || {};
  const attention = covAttentionItems(summary, covState.work);
  const actions = (summary.actions || []).filter((row) => !covState.filters.actions.length || covState.filters.actions.some((action) => action.toLowerCase() === row.action.toLowerCase()));
  const actionTotal = actions.reduce((sum, row) => sum + row.owed, 0);
  return `${attention.length ? `<section class="cov-card cov-attention" aria-label="Needs attention">
      <div class="cov-card-head"><h4>Needs attention</h4><p>Largest problems first. Each opens what is behind it.</p></div>
      <ol>${attention.map((item) => `<li><span>${escapeHtml(item.text)}</span><span class="cov-row-actions">${item.approvals ? `<button type="button" class="ghost-button" data-cov-approvals="${escapeHtml(item.orgId)}">Review</button>` : `<button type="button" class="ghost-button" data-cov-client="${escapeHtml(item.orgId)}"${item.tab ? ` data-cov-client-tab="${escapeHtml(item.tab)}"` : ""}>${item.tab === "people" ? "Who" : "Why"}</button>`}${item.drill ? `<button type="button" class="ghost-button"${covDrillAttr(item.drill)}>Open lines</button>` : ""}</span></li>`).join("")}</ol>
    </section>` : `<section class="cov-card"><div class="cov-message">Nothing needs attention${covAnyFilter() ? " for these filters" : ""}.</div></section>`}
    <section class="cov-card" aria-label="Why it is outstanding, every company">
      <div class="cov-card-head"><h4>Why it's outstanding</h4><p>Every open dollar has exactly one reason. Click a reason to rank the companies by it.</p></div>
      ${covReasonBar(summary.reasons, totals.owed)}
      <div class="cov-legend">${(summary.reasons || []).map((r) => `<button type="button" data-cov-focus="${r.key}" aria-pressed="${covState.reasonFocus === r.key}" title="${escapeHtml(r.detail)}"><i style="background:${COV_REASON_COLORS[r.key]}"></i><span>${escapeHtml(r.label)}</span><strong>${escapeHtml(compactMoney(r.owed))}</strong><small>${Math.round(r.share * 100)}%</small></button>`).join("")}</div>
    </section>
    <section class="cov-card" aria-label="By follow-up action, every company">
      <div class="cov-card-head"><h4>By follow-up action</h4><p>Every company's open money by its current follow-up action. "Show only" filters the whole page to that action.</p></div>
      <div class="cov-table-wrap"><table class="cov-table">
        <thead><tr><th>Follow-up action</th><th class="is-num">Outstanding</th><th>Share</th><th class="is-num">Lines</th><th class="is-num">Past due</th><th></th></tr></thead>
        <tbody>${actions.map((row) => {
          const share = actionTotal ? row.owed / actionTotal : 0;
          const only = covState.filters.actions.length === 1 && covState.filters.actions[0].toLowerCase() === row.action.toLowerCase();
          return `<tr class="${row.action ? "" : "is-unowned"}">
            <th scope="row">${escapeHtml(covActionName(row.action))}${row.action ? "" : `<small>${escapeHtml(`${money(totals.unownedOwed)} of it was billed 30+ days ago and is unpaid ("No one on it"); the rest was billed recently, is paid to within $10, or has no billed date.`)}</small>`}</th>
            <td class="is-num"><strong>${escapeHtml(money(row.owed))}</strong></td>
            <td><span class="cov-share"><i style="width:${Math.max(2, share * 100).toFixed(1)}%;background:var(--brand)"></i></span><small>${Math.round(share * 100)}%</small></td>
            <td class="is-num">${escapeHtml(formatCount(row.lines))}</td>
            <td class="is-num ${row.pastDueLines ? "is-warn" : ""}">${escapeHtml(formatCount(row.pastDueLines))}<small>${escapeHtml(money(row.pastDueOwed))}</small></td>
            <td><span class="cov-row-actions">${only ? "" : `<button type="button" class="ghost-button" data-cov-action-only="${escapeHtml(row.action)}">Show only</button>`}<button type="button" class="ghost-button"${covDrillAttr({ allCompanies: true, label: `every company, ${covActionName(row.action)}`, filters: { action: [row.action || "Blank"] } })}>Open lines</button></span></td>
          </tr>`;
        }).join("")}</tbody>
      </table></div>
    </section>`;
}

function renderCovPortfolio(summary) {
  const totals = summary.totals || {};
  const work = covState.work;
  // Arrows compare whole-company history, so they are hidden while filtered.
  const trend = covAnyFilter() ? [] : covTrendSeries();
  const section = covState.section === "people" ? renderCovPeople()
    : covState.section === "staffing" ? renderCovStaffing()
    : covState.section === "approvals" ? renderCovApprovals()
    : covState.section === "trainee" ? renderCovTrainee()
      : covState.section === "worked" ? renderCovWorked(summary)
        : covState.section === "trend" ? renderCovTrend()
          : covState.section === "companies" ? renderCovCompanies(summary)
            : renderCovOverview(summary);
  const staffingOrgs = (work?.staffing?.orgs || []).filter((row) => !covState.filters.orgs.length || covState.filters.orgs.includes(row.orgId));
  const staffing = work?.staffing ? (covState.filters.orgs.length ? {
    peopleNeeded: staffingOrgs.every((row) => row.peopleNeeded != null) ? staffingOrgs.reduce((sum, row) => sum + row.peopleNeeded, 0) : null,
    peopleWorking: staffingOrgs.every((row) => row.peopleWorking != null) ? staffingOrgs.reduce((sum, row) => sum + row.peopleWorking, 0) : null
  } : work.staffing.totals) : null;
  return `
    ${renderCovScorecard(totals, work?.totals, staffing, (work?.people || []).filter((p) => p.kind === "person"))}
    <section class="cov-tiles cov-tiles-5" aria-label="All companies">
      ${covTile("Outstanding", totals.owed, totals.lines, covAnyFilter() ? "Open insurance balance, these filters" : `Open insurance balance, ${covScopeName()}`, null, "", covTrendDelta(trend, "owed"))}
      ${covTile("Past due", totals.pastDueOwed, totals.pastDueLines, `${covShare(totals.pastDueOwed, totals.owed)} of outstanding: follow-up date has passed`, null, "is-warn", covTrendDelta(trend, "pastDueOwed"))}
      ${covTile("30+ days past due", totals.pastDue30Owed, totals.pastDue30Lines, `${covShare(totals.pastDue30Owed, totals.pastDueOwed)} of past due: date passed over 30 days ago`, null, "is-danger", covTrendDelta(trend, "pastDue30Owed"))}
      ${covTile("Not worked since due", work?.totals?.notWorkedOwed || 0, work?.totals?.notWorkedLines || 0, `${work ? covShare(work.totals.notWorkedOwed, totals.pastDueOwed) : "…"} of past due: no one has touched it since`, null, "is-danger", covTrendDelta(trend, "notWorkedOwed"), !work)}
      ${covTile("No one on it", totals.unownedOwed, totals.unownedLines, `${covShare(totals.unownedOwed, totals.owed)} of outstanding: no action, billed 30+ days ago`, null, "is-danger", covTrendDelta(trend, "unownedOwed"))}
    </section>
    ${covSectionTabs()}
    ${section}`;
}

function renderCovClientPeople(name, clientKey) {
  const work = covState.work;
  if (!work) return covWorkPending("People");
  const workOrg = covWorkOrg(covState.orgId);
  if (!workOrg) return '<div class="cov-message">No one has worked this company in the last 30 days and nothing is waiting for approval.</div>';
  const notWorked = workOrg.notWorked || [];
  return `<div class="cov-figures"><span><strong>${escapeHtml(formatCount(workOrg.workedToday))}</strong> lines worked today</span><span><strong>${escapeHtml(formatCount(workOrg.workedLast7))}</strong> last 7 days</span><span>Last worked <strong>${escapeHtml(workOrg.lastWorkedDay ? formatDate(workOrg.lastWorkedDay) : "not in 30 days")}</strong></span><span>${workOrg.approvalLines ? `<button type="button" class="cov-linkish" data-cov-approvals="${escapeHtml(covState.orgId)}">${escapeHtml(formatNounCount(workOrg.approvalLines, "request"))} waiting for approval</button>` : "No approvals waiting"}</span></div>
    <div class="cov-table-wrap"><table class="cov-table cov-people">
      <thead><tr><th>Person</th><th class="is-num">Owns</th><th class="is-num">Past due</th><th class="is-num">Not worked since due</th><th class="is-num">Worked, 7 days</th><th class="is-num">Waiting approval</th></tr></thead>
      <tbody>${(workOrg.people || []).map((p) => `<tr class="${p.kind === "person" ? "" : "is-pseudo"}"><th scope="row">${escapeHtml(p.name)}</th><td class="is-num"><strong>${escapeHtml(money(p.ownedOwed))}</strong><small>${escapeHtml(formatNounCount(p.ownedLines, "line"))}</small></td><td class="is-num">${escapeHtml(formatCount(p.pastDueLines))}</td><td class="is-num ${p.notWorkedLines ? "is-danger" : ""}">${escapeHtml(money(p.notWorkedOwed))}<small>${escapeHtml(formatCount(p.notWorkedLines))}</small></td><td class="is-num">${escapeHtml(formatCount(p.workedLast7))}</td><td class="is-num">${escapeHtml(formatCount(p.approvalLines))}</td></tr>`).join("")}</tbody>
    </table></div>
    ${notWorked.length ? `<div class="cov-card-head cov-subhead"><h4>Largest lines not worked since due</h4><p>${escapeHtml(formatNounCount(workOrg.notWorkedLines, "line"))} in all; the ${escapeHtml(String(notWorked.length))} largest are listed. ${(() => { const drill = covClaimDrill(clientKey, name, notWorked.map((l) => l.claimId), "largest lines not worked since due"); return drill ? `<button type="button" class="cov-linkish"${covDrillAttr(drill)}>Open the top ${escapeHtml(String(drill.claimIds.length))} claims in All Data</button>` : ""; })()}</p></div>
    <div class="cov-table-wrap"><table class="cov-table">
      <thead><tr><th>Claim</th><th class="is-num">Owed</th><th>Follow-up action</th><th>Due</th><th>Last worked</th><th>Owner</th></tr></thead>
      <tbody>${notWorked.map((line) => `<tr><th scope="row"><button type="button" class="cov-company"${covDrillAttr(covClaimDrill(clientKey, name, [line.claimId], `claim ${line.claimId}`))}>${escapeHtml(line.claimId)}</button></th><td class="is-num">${escapeHtml(money(line.owed))}</td><td>${escapeHtml(line.action || "None")}</td><td class="is-warn">${escapeHtml(formatDate(line.dueDate))}</td><td>${escapeHtml(line.lastWorkedDay ? formatDate(line.lastWorkedDay) : "Never")}</td><td>${escapeHtml(line.ownerName || "")}</td></tr>`).join("")}</tbody>
    </table></div>` : ""}`;
}

function renderCovClient(summary, client) {
  const portfolioRow = (summary?.clients || []).find((row) => row.orgId === covState.orgId) || {};
  const name = covCompanyName(covState.orgId);
  const clientKey = client?.totals?.clientKey || portfolioRow.clientKey || "";
  const workOrg = covWorkOrg(covState.orgId);
  const trend = covState.work?.trend?.byOrg?.[covState.orgId] || [];
  const head = `<div class="cov-client-head">
      <button type="button" class="ghost-button" data-cov-back>← All companies</button>
      <h3>${escapeHtml(name)}</h3>
      ${clientKey ? `<button type="button" class="ghost-button"${covDrillAttr({ clientKey, label: `${name}, all open lines` })}>Open all lines in All Data</button>` : ""}
    </div>`;
  if (covState.clientStatus === "loading") return `${head}<div class="cov-message" role="status">Loading ${escapeHtml(name)}…</div>`;
  if (covState.clientStatus === "error" || !client) return `${head}<div class="cov-message is-error" role="alert">${escapeHtml(covState.clientError || "This company could not load.")} <button type="button" class="ghost-button" data-cov-client="${escapeHtml(covState.orgId)}">Try again</button></div>`;
  const totals = client.totals || {};
  const tab = (key, label) => `<button type="button" role="tab" data-cov-tab="${key}" aria-selected="${covState.tab === key}">${escapeHtml(label)}</button>`;
  const why = `<div class="cov-table-wrap"><table class="cov-table cov-reasons">
      <thead><tr><th>Reason and next step</th><th class="is-num">Outstanding</th><th>Share</th><th class="is-num">Past due</th><th>Age since billed</th><th>Top payers</th><th></th></tr></thead>
      <tbody>${(client.reasons || []).map((r) => {
        const drill = covReasonDrill(r, clientKey, name, client.actions);
        return `<tr>
          <th scope="row"><i class="cov-dot" style="background:${COV_REASON_COLORS[r.key]}"></i><strong>${escapeHtml(r.label)}</strong><small>${escapeHtml(r.detail)}</small><small class="cov-next">Next: ${escapeHtml(r.next)}</small></th>
          <td class="is-num"><strong>${escapeHtml(money(r.owed))}</strong><small>${escapeHtml(formatNounCount(r.lines, "line"))}</small></td>
          <td><span class="cov-share"><i style="width:${Math.max(2, r.share * 100).toFixed(1)}%;background:${COV_REASON_COLORS[r.key]}"></i></span><small>${Math.round(r.share * 100)}%</small></td>
          <td class="is-num">${escapeHtml(formatCount(r.pastDueLines))}</td>
          <td class="cov-aging">${(r.aging || []).map((a) => `<span title="${escapeHtml(`${a.bucket} days: ${money(a.owed)}`)}"><b>${escapeHtml(a.bucket)}</b>${escapeHtml(compactMoney(a.owed))}</span>`).join("")}</td>
          <td class="cov-payers">${(r.topPayers || []).map((p) => `<span title="${escapeHtml(`${p.payer}: ${money(p.owed)}, ${formatNounCount(p.lines, "line")}`)}">${escapeHtml(p.payer)} <b>${escapeHtml(compactMoney(p.owed))}</b></span>`).join("")}</td>
          <td>${drill ? `<button type="button" class="ghost-button"${covDrillAttr(drill)}>Open</button>` : ""}</td>
        </tr>`;
      }).join("")}</tbody>
    </table></div>`;
  const actions = `<div class="cov-table-wrap"><table class="cov-table">
      <thead><tr><th>Follow-up action</th><th class="is-num">Outstanding</th><th class="is-num">Lines</th><th class="is-num">Past due</th><th class="is-num">30+ days past due</th><th class="is-num">No follow-up date</th><th>Oldest past due</th><th></th></tr></thead>
      <tbody>${(client.actions || []).map((a) => {
        const label = a.action || "No follow-up action";
        return `<tr class="${a.action ? "" : "is-unowned"}">
          <th scope="row">${escapeHtml(label)}${a.action ? "" : `<small>${escapeHtml(`${money(a.unownedOwed)} of this was billed 30+ days ago`)}</small>`}</th>
          <td class="is-num"><strong>${escapeHtml(money(a.owed))}</strong></td>
          <td class="is-num">${escapeHtml(formatCount(a.lines))}</td>
          <td class="is-num ${a.pastDueLines ? "is-warn" : ""}">${escapeHtml(formatCount(a.pastDueLines))}</td>
          <td class="is-num ${a.pastDue30Lines ? "is-danger" : ""}">${escapeHtml(formatCount(a.pastDue30Lines))}</td>
          <td class="is-num">${escapeHtml(formatCount(a.noDueDateLines))}</td>
          <td>${escapeHtml(a.oldestDue ? formatDate(a.oldestDue) : "None")}</td>
          <td><button type="button" class="ghost-button"${covDrillAttr({ clientKey, label: `${name}, ${label}`, filters: { action: [a.action || "Blank"] } })}>Open</button></td>
        </tr>`;
      }).join("")}</tbody>
    </table></div>`;
  const body = covState.tab === "actions" ? actions : covState.tab === "people" ? renderCovClientPeople(name, clientKey) : why;
  const staffRow = (covState.work?.staffing?.orgs || []).find((row) => row.orgId === covState.orgId) || null;
  const orgPeople = (workOrg?.people || []).filter((p) => p.kind === "person").map((p) => (covState.work?.people || []).find((person) => person.key === p.key)).filter(Boolean);
  const orgWorkTotals = workOrg ? { ...workOrg, activePeople: workOrg.peopleLast7 || 0 } : (covState.work ? { onTimeRate: null, backlogDays: 0, workedPerDay: 0, activePeople: 0, notWorkedLines: 0 } : null);
  return `${head}
    ${renderCovScorecard(totals, orgWorkTotals, staffRow, orgPeople, name)}
    <section class="cov-tiles cov-tiles-5" aria-label="${escapeHtml(name)}">
      ${covTile("Outstanding", totals.owed, totals.lines, "Open insurance balance", { clientKey, orgId: covState.orgId, label: `${name}, all open lines` }, "", covTrendDelta(trend, "owed"))}
      ${covTile("Past due", totals.pastDueOwed, totals.pastDueLines, `${covShare(totals.pastDueOwed, totals.owed)} of outstanding: follow-up date has passed`, covPastDueDrill(clientKey, name, client.today), "is-warn", covTrendDelta(trend, "pastDueOwed"))}
      ${covTile("30+ days past due", totals.pastDue30Owed, totals.pastDue30Lines, `${covShare(totals.pastDue30Owed, totals.pastDueOwed)} of past due: date passed over 30 days ago`, null, "is-danger", covTrendDelta(trend, "pastDue30Owed"))}
      ${covTile("Not worked since due", workOrg?.notWorkedOwed || 0, workOrg?.notWorkedLines || 0, `${workOrg ? covShare(workOrg.notWorkedOwed, totals.pastDueOwed) : "0%"} of past due: no one has touched it since`, null, "is-danger", covTrendDelta(trend, "notWorkedOwed"), !covState.work)}
      ${covTile("No one on it", totals.unownedOwed, totals.unownedLines, `${covShare(totals.unownedOwed, totals.owed)} of outstanding: no action, billed 30+ days ago`, covUnownedDrill(clientKey, name), "is-danger", covTrendDelta(trend, "unownedOwed"))}
    </section>
    <section class="cov-card">
      <div class="cov-card-head"><h4>Why it's outstanding</h4></div>
      ${covReasonBar(client.reasons, totals.owed)}
      <div class="cov-tabs" role="tablist">${tab("why", "By reason")}${tab("actions", "By follow-up action")}${tab("people", "People and approvals")}</div>
      ${body}
    </section>`;
}

function renderCompanyOversight() {
  const host = document.getElementById("companyOversightPanel");
  if (!host) return;
  if (!canOpenCompanyOversight()) { host.replaceChildren(); return; }
  if (covAccessKey() !== covState.key || covState.summaryStatus === "idle") { void loadCovSummary(); if (covState.summaryStatus === "idle") return; }
  if (!host.dataset.covBound) {
    host.addEventListener("click", handleCovClick);
    // Typing in the filter's search hides non-matching actions in place, so the
    // box keeps focus (a full render would replace it).
    host.addEventListener("input", (event) => {
      if (!event.target.matches("[data-cov-filter-search]")) return;
      covState.filterSearch = event.target.value;
      const search = covState.filterSearch.trim().toLowerCase();
      host.querySelectorAll(".cov-action-option").forEach((option) => { option.hidden = Boolean(search) && !option.querySelector(".cov-action-name").textContent.toLowerCase().includes(search); });
    });
    host.addEventListener("toggle", (event) => { if (event.target.matches("[data-cov-guide]")) covState.guideOpen = event.target.open; }, true);
    host.dataset.covBound = "1";
  }
  const summary = covState.summary;
  const built = summary?.totals?.builtAtUtc ? new Date(summary.totals.builtAtUtc) : null;
  const loading = covState.summaryStatus === "loading" || covState.workStatus === "loading";
  const intro = `<div class="cov-intro">
      <div><span class="cov-eyebrow">Internal to TMS · Account owner only</span>
      <h3>What's outstanding, why, and who is on it</h3>
      <p>Open insurance balance for ${escapeHtml(covScopeName())}, the reason each dollar is unpaid, who is working it, what is waiting for approval, and whether it is getting better. ${built && Number.isFinite(built.getTime()) ? `Report snapshot of ${escapeHtml(built.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }))}.` : ""}</p></div>
      <button type="button" class="ghost-button" data-cov-refresh ${loading ? "disabled" : ""}>${loading ? "Loading…" : "Refresh"}</button>
    </div>
    ${renderCovGuide()}
    ${summary ? renderCovFilter() : ""}`;
  let body = "";
  if (covState.summaryStatus === "loading" && !summary) body = `<div class="cov-message" role="status">Adding up ${escapeHtml(covScopeName())}…</div>`;
  else if (covState.summaryStatus === "error" && !summary) body = `<div class="cov-message is-error" role="alert">${escapeHtml(covState.summaryError)}</div>`;
  else if (summary && covState.orgId) body = renderCovClient(summary, covState.client);
  else if (summary) body = renderCovPortfolio(summary);
  host.innerHTML = intro + body;
}

function handleCovClick(event) {
  const target = event.target.closest("button");
  if (!target || target.disabled) return;
  if (target.hasAttribute("data-cov-drill")) {
    try { openCovLines(JSON.parse(target.getAttribute("data-cov-drill"))); } catch (_) { /* malformed drill is ignored */ }
    return;
  }
  if (target.hasAttribute("data-cov-approvals")) { openCovApprovals(target.getAttribute("data-cov-approvals")); return; }
  if (target.hasAttribute("data-cov-client")) { void openCovClient(target.getAttribute("data-cov-client"), target.getAttribute("data-cov-client-tab") || "why"); return; }
  if (target.hasAttribute("data-cov-back")) { covState.orgId = ""; covState.client = null; renderCompanyOversight(); return; }
  if (target.hasAttribute("data-cov-refresh")) {
    covState.client = null;
    void loadCovSummary(true).then(() => { if (covState.orgId) void openCovClient(covState.orgId, covState.tab); });
    return;
  }
  if (target.hasAttribute("data-cov-filter-toggle")) {
    const key = target.getAttribute("data-cov-filter-toggle");
    covState.filterOpen = covState.filterOpen === key ? "" : key;
    covState.filterSearch = "";
    renderCompanyOversight();
    if (covState.filterOpen) document.querySelector("[data-cov-filter-search]")?.focus();
    return;
  }
  if (target.hasAttribute("data-cov-filter-value")) {
    const key = target.getAttribute("data-cov-filter-key");
    const value = target.getAttribute("data-cov-filter-value");
    const current = covState.filters[key] || [];
    const on = current.some((item) => item.toLowerCase() === value.toLowerCase());
    setCovFilter(key, on ? current.filter((item) => item.toLowerCase() !== value.toLowerCase()) : [...current, value]);
    return;
  }
  if (target.hasAttribute("data-cov-action-only")) { covState.filterOpen = ""; setCovActions([target.getAttribute("data-cov-action-only")]); return; }
  if (target.hasAttribute("data-cov-filter-clear")) { setCovFilter(target.getAttribute("data-cov-filter-clear"), []); return; }
  if (target.hasAttribute("data-cov-filters-clear")) { covState.filterOpen = ""; clearCovFilters(); return; }
  if (target.hasAttribute("data-cov-filter-all")) {
    const key = target.getAttribute("data-cov-filter-all");
    const shown = [...document.querySelectorAll(".cov-action-option:not([hidden])")].map((option) => option.getAttribute("data-cov-filter-value"));
    setCovFilter(key, [...covState.filters[key], ...shown]);
    return;
  }
  if (target.hasAttribute("data-cov-people-sort")) { covState.peopleSort = target.getAttribute("data-cov-people-sort"); renderCompanyOversight(); return; }
  if (target.hasAttribute("data-cov-work-retry")) { void loadCovWork(true); renderCompanyOversight(); return; }
  if (target.hasAttribute("data-cov-sort")) { covState.sort = target.getAttribute("data-cov-sort"); covState.reasonFocus = ""; renderCompanyOversight(); return; }
  if (target.hasAttribute("data-cov-focus")) { const key = target.getAttribute("data-cov-focus"); covState.reasonFocus = covState.reasonFocus === key ? "" : key; covState.section = "companies"; renderCompanyOversight(); return; }
  if (target.hasAttribute("data-cov-trainee-retry")) { void loadCovTrainee(true); renderCompanyOversight(); return; }
  if (target.hasAttribute("data-cov-location")) { covState.location = target.getAttribute("data-cov-location"); renderCompanyOversight(); return; }
  if (target.hasAttribute("data-cov-section")) {
    covState.section = target.getAttribute("data-cov-section");
    if (covState.section === "trainee") void loadCovTrainee();
    renderCompanyOversight();
    return;
  }
  if (target.hasAttribute("data-cov-team")) { covState.team = target.getAttribute("data-cov-team"); renderCompanyOversight(); return; }
  if (target.hasAttribute("data-cov-tab")) { covState.tab = target.getAttribute("data-cov-tab"); renderCompanyOversight(); }
}

function renderView() {
  window.TMSBackupHistory?.setContext({
    allowed: canAccessView("backupHistory"), role: canonicalRole(state.role),
    scope: currentAccessProfile().scope || {}, base: workflowApiBase(), token: workflowApiToken(),
    accessKey: workflowAccessScopedRequestKey({ view: "backupHistory" }),
    onSessionExpired: () => { if (typeof notePortalSessionExpired === "function") notePortalSessionExpired(); }
  });
  const titles = {
    workbench: ["Accounts Receivable", "Prioritized AR claim data with status, payer follow-up, and aging signals."],
    assignments: ["My Work", "Your tasks, requests and approvals."],
    reviews: ["Review", "Submitted offshore work awaiting approval or bounce-back."],
    reports: ["Reports", "Executive RCM analytics by tenant, payer, aging, and risk."],
    feeSchedules: ["Fee Schedules", "Workbook-style Rates table for expected reimbursement setup."],
    missingRates: ["Missing Rates", "Service lines that found no rate, ranked by open AR — add the rate, or match the payer to a parent schedule."],
    followUpRules: ["Rules & Help", "Searchable guidance for follow-up actions and automatic changes."],
    queryMap: ["Rules & Help", "Searchable guidance for follow-up actions and automatic changes."],
    feedback: ["Feedback", "Software development tickets submitted from the portal."],
    backupHistory: ["Backups & History", "Verified daily checkpoints, historical reports, and recovery evidence."],
    companyOversight: ["Oversight", "What every company has outstanding, why it is unpaid, and what no one is working."],
    audit: ["Activity & audit", "Who changed what, on the durable record. Read-only."],
    clientHome: ["Home", "Your money at a glance — what is owed, what came in, and what is waiting on you."],
    statements: ["Patient Statements", "Review and print patient-responsibility statements."],
    authorizations: ["Authorizations", "On-hold claims grouped by patient — record one authorization to clear many lines."],
    credentialing: ["Credentialing", "Claims parked until the rendering provider is credentialed with the payer."],
    clientActionPending: ["Client Action Pending", "Post-submission requests waiting on the facility or TMS."],
    denialIntelligence: ["Denial Intelligence", "Read what billers documented in comparable cases and open the original notes."],
    trackerExport: ["Export Tracker", "Scope it, filter it, download the current tracker Excel."],
    training: ["Training", "Short narrated tours of TMS Vista, recorded on sample data. Watch any time."]
  };

  els.navItems.forEach((item) => item.classList.toggle("active", item.dataset.view === state.view));
  els.viewPanels.forEach((panel) => panel.classList.toggle("active", panel.id === `${state.view}View`));
  applyNavVisibility(els.navItems, els.navSections);
  refreshClientApiAccessAvailability();
  // ROLE-AWARE TITLE. An offshore biller opening "All Data" does not get the AR
  // grid — they get their My Tasks board (To Do / In Progress / Submitted /
  // Bounced Back). Showing "Prioritized AR claim data with status, payer
  // follow-up, and aging signals" over a task kanban is a plain mismatch: the
  // header describes a screen that is not the one in front of them, which is
  // exactly the tribal knowledge an offshore biller should never need.
  const activeTitle = viewTitleFor(state.view, titles);
  els.viewTitle.textContent = activeTitle[0];
  els.viewSubtitle.textContent = activeTitle[1];
  updateViewTourButton();
  const trackerExportIntro = document.getElementById("trackerExportIntro");
  if (trackerExportIntro) {
    trackerExportIntro.textContent = `The same ${trackerWorkbookExportSheetCount()}-tab Excel the current tracker publishes. Large books build in the background and download when they are ready.`;
  }
}

function viewTitleFor(view, titles) {
  const base = titles[view];
  if (view === "workbench" && offshoreBoardMode()) {
    return ["My Tasks", "Claims routed to you — pick the next one to work, then submit it for review."];
  }
  // Reports is one view with two audiences now. A practice owner opening it
  // gets their own payments, receivables and collection rate - not "executive
  // RCM analytics by tenant, payer, aging, and risk", which names three things
  // their session cannot filter by and one it is never shown.
  if (view === "reports" && clientOnlyReportsSession()) {
    return ["Reports", "Your payments, receivables, and collection performance."];
  }
  // A full Client (with the shared Reports suite) is still a client: "tenant"
  // is TMS vocabulary for the companies it serves (2026-10-13).
  if (view === "reports" && isExternalClientRole(state?.role)) {
    return ["Reports", "Your revenue cycle by payer, aging, and risk."];
  }
  return base;
}

function applyPermissionChrome() {
  // Client chrome (2026-10-13): Status checks is the billers' claim-level
  // aging tool, and "Assignment Groups" is desk language for the per-claim view.
  const externalClientChrome = isExternalClientRole(state?.role);
  const statusChecksButton = document.getElementById("claimStatusChecksButton");
  if (statusChecksButton) statusChecksButton.hidden = externalClientChrome;
  document.querySelectorAll('#rowModeSelect option[value="claim"]').forEach((option) => {
    if (!option.dataset.staffLabel) option.dataset.staffLabel = option.textContent;
    option.textContent = externalClientChrome ? "Claim groups" : option.dataset.staffLabel;
  });
  document.querySelectorAll('.grid-mode-mirror-btn[data-grid-mode="claim"]').forEach((button) => {
    if (!button.dataset.staffTitle) button.dataset.staffTitle = button.title;
    button.title = externalClientChrome ? "One row per claim" : button.dataset.staffTitle;
  });
  if (els.importBtn) els.importBtn.hidden = !hasPermission(PERMISSIONS.IMPORT_WORKFLOW);
  if (els.exportBtn) els.exportBtn.hidden = !hasPermission(PERMISSIONS.EXPORT_WORKFLOW);
  // Destructive browser-state reset controls do not belong in company-wide chrome.
  if (els.resetDemoBtn) els.resetDemoBtn.hidden = true;
  // The demo Role/User switcher is for demo sessions and authenticated
  // Super Admins only. A real non-admin login must never see it — flipping
  // state.role would unlock admin chrome client-side even though the server
  // token still scopes the API data.
  const roleBox = typeof document.querySelector === "function" ? document.querySelector(".role-box") : null;
  if (roleBox) {
    const authScope = state?.authUserScope;
    roleBox.style.display = authScope && canonicalRole(authScope.role) !== "SuperAdmin" ? "none" : "";
  }
  if (els.addFeeRowBtn) els.addFeeRowBtn.hidden = !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES);
  if (els.exportFeeScheduleBtn) els.exportFeeScheduleBtn.hidden = !hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES);
  if (els.clearFeeScheduleFiltersBtn) els.clearFeeScheduleFiltersBtn.hidden = !hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES) && !hasPermission(PERMISSIONS.VIEW_OWN_FEE_SCHEDULE);
  if (els.downloadFeeScheduleTemplateBtn) els.downloadFeeScheduleTemplateBtn.hidden = !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES);
  if (els.importFeeScheduleCsvBtn) els.importFeeScheduleCsvBtn.hidden = !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES);
  if (els.bulkEditFeeScheduleBtn) els.bulkEditFeeScheduleBtn.hidden = !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES);
  if (els.feeScheduleBulkPanel && !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) els.feeScheduleBulkPanel.hidden = true;
  if (els.addFollowUpRuleBtn) els.addFollowUpRuleBtn.hidden = followUpRulesHostedMode() || !hasPermission(PERMISSIONS.MANAGE_FOLLOW_UP_RULES);
  requestNavigationPendingCounts();
  // The hub rides the same lifecycle as the sidebar badges: same principal
  // marker, same invalidation on sign-in/sign-out, so it can never paint one
  // practice's requests under another practice's session.
  requestWaitingOnYou();
  // The My Work sidebar badge rides the same render lifecycle. It asks for
  // nothing until the first grid rows are up (deferred like the counts above),
  // and after that it only repaints from cache unless its data is missing.
  if (typeof requestMyWorkAttention === "function") requestMyWorkAttention();
}

const NAVIGATION_PENDING_PERMISSIONS = [
  PERMISSIONS.AUTHORIZATIONS_MANAGE,
  PERMISSIONS.AUTHORIZATIONS_SUBMIT,
  PERMISSIONS.CREDENTIALING_MANAGE,
  PERMISSIONS.CREDENTIALING_SUBMIT
];

const navigationPendingCountState = {
  key: "",
  generation: 0,
  status: "idle", // idle | loading | ready | error
  controller: null,
  authorization: { total: 0, available: false },
  credentialing: { total: 0, available: false },
  clientAction: { total: 0, available: false }
};

function navigationPendingPrincipalMarker() {
  const stored = typeof storedPortalAuthUserSnapshot === "function" ? storedPortalAuthUserSnapshot() : {};
  const scope = state?.authUserScope || {};
  return JSON.stringify({
    principal: String(stored.userId || stored.id || stored.username || stored.email || state?.activeUserName || "").trim(),
    role: canonicalRole(scope.role || state?.role || stored.role || ""),
    scopeOrgIds: normalizeUserScopeList(scope.orgIds).slice().sort(),
    scopeClientNames: normalizeUserScopeList(scope.clientNames).slice().sort(),
    assignedName: String(scope.assignedName || "").trim()
  });
}

function navigationPendingRequestDescriptor() {
  const token = typeof trackerApiToken === "function" ? trackerApiToken() : "";
  const base = typeof trackerApiBase === "function" ? trackerApiBase() : "";
  if (!token || !base || typeof fetch !== "function") return null;
  const permissions = NAVIGATION_PENDING_PERMISSIONS.filter((permission) => hasPermission(permission)).sort();
  if (!permissions.length) return null;
  const orgIds = Array.from(new Set(activeClientGroupOrgIds().map((value) => String(value || "").trim()).filter(Boolean))).sort();
  return {
    base,
    token,
    orgIds,
    // Never put the bearer token in this key. Login/logout explicitly invalidates
    // the generation even when the same principal signs in again.
    key: JSON.stringify({ principal: navigationPendingPrincipalMarker(), permissions, orgIds })
  };
}

function emptyNavigationPendingCounts() {
  return { total: 0, available: false };
}

function invalidateNavigationPendingCounts(nextKey = "") {
  try { navigationPendingCountState.controller?.abort(); } catch (_) {}
  navigationPendingCountState.controller = null;
  navigationPendingCountState.generation += 1;
  navigationPendingCountState.key = String(nextKey || "");
  navigationPendingCountState.status = "idle";
  navigationPendingCountState.authorization = emptyNavigationPendingCounts();
  navigationPendingCountState.credentialing = emptyNavigationPendingCounts();
  updateAuthorizationsNavBadge();
  updateCredentialingNavBadge();
  if (typeof resetMyWorkAttention === "function") resetMyWorkAttention();
}

function normalizedNavigationPendingFeature(payload, singular, plural) {
  const node = payload && typeof payload === "object"
    ? (payload[singular] || payload[plural] || {})
    : {};
  return {
    available: node.available === true,
    total: node.available === true ? Math.max(0, Math.floor(Number(node.total) || 0)) : 0
  };
}

// A section the server is still counting answers "pending" with the others;
// the badges already counted show at once and the pending one is asked again.
function requestNavigationPendingCounts(options = {}) {
  const followUp = options && options.followUp === true;
  const descriptor = navigationPendingRequestDescriptor();
  if (followUp && (!descriptor || navigationPendingCountState.key !== descriptor.key
    || navigationPendingCountState.status !== "ready" || navigationPendingCountState.controller)) return;
  if (!descriptor) {
    if (navigationPendingCountState.key || navigationPendingCountState.status !== "idle") invalidateNavigationPendingCounts();
    else {
      updateAuthorizationsNavBadge();
      updateCredentialingNavBadge();
      updateClientActionBadge();
    }
    return;
  }
  if (!followUp && navigationPendingCountState.key !== descriptor.key) invalidateNavigationPendingCounts(descriptor.key);
  if (!followUp && navigationPendingCountState.status !== "idle") return;
  // Three sidebar numbers. They are not worth a place in front of the first
  // grid paint - measured at 7.8-10.0 s for a 246-byte answer while it queued
  // behind the boot flood (perf/ALL_DATA_LATENCY_2026-09-22.md).
  if (!followUp && deferBootRequestUntilFirstGridPaint("navigationPendingCounts", requestNavigationPendingCounts)) return;

  const generation = navigationPendingCountState.generation;
  const key = navigationPendingCountState.key;
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  navigationPendingCountState.controller = controller;
  if (!followUp) {
    navigationPendingCountState.followUps = 0;
    navigationPendingCountState.status = "loading";
    updateAuthorizationsNavBadge();
    updateCredentialingNavBadge();
    updateClientActionBadge();
  }

  const params = new URLSearchParams();
  descriptor.orgIds.forEach((orgId) => params.append("orgId", orgId));
  const query = params.toString() ? "?" + params.toString() : "";
  fetch(descriptor.base + "/api/navigation/pending-counts" + query, {
    method: "GET",
    cache: "no-store",
    headers: { authorization: "Bearer " + descriptor.token },
    signal: controller ? controller.signal : undefined
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (generation !== navigationPendingCountState.generation || key !== navigationPendingCountState.key) return;
    navigationPendingCountState.controller = null;
    // A failed follow-up keeps the counts already shown.
    if (followUp && (!response.ok || (payload && payload.error))) {
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      return;
    }
    if (response.status === 401 && typeof notePortalSessionExpired === "function") {
      notePortalSessionExpired();
      navigationPendingCountState.status = "error";
    } else if (!response.ok || (payload && payload.error)) {
      navigationPendingCountState.status = "error";
      navigationPendingCountState.authorization = emptyNavigationPendingCounts();
      navigationPendingCountState.credentialing = emptyNavigationPendingCounts();
      navigationPendingCountState.clientAction = emptyNavigationPendingCounts();
    } else {
      navigationPendingCountState.status = "ready";
      const pending = [["authorization", "authorizations"], ["credentialing", "credentialing"], ["clientAction", "clientAction"]]
        .filter(([singular, plural]) => (payload?.[singular] || payload?.[plural] || {}).reason === "pending");
      [["authorization", "authorizations"], ["credentialing", "credentialing"], ["clientAction", "clientAction"]].forEach(([singular, plural]) => {
        if (!pending.some(([name]) => name === singular)) navigationPendingCountState[singular] = normalizedNavigationPendingFeature(payload, singular, plural);
      });
      if (pending.length && (navigationPendingCountState.followUps || 0) < 8) {
        navigationPendingCountState.followUps = (navigationPendingCountState.followUps || 0) + 1;
        const retryAfter = Math.min(10000, Math.max(1000, Number(pending.map(([singular, plural]) => (payload?.[singular] || payload?.[plural]).retryAfterMs)[0]) || 3000));
        setTimeout(() => {
          if (generation === navigationPendingCountState.generation && key === navigationPendingCountState.key) requestNavigationPendingCounts({ followUp: true });
        }, retryAfter);
      }
    }
    updateAuthorizationsNavBadge();
    updateCredentialingNavBadge();
    updateClientActionBadge();
  }).catch(() => {
    if (generation !== navigationPendingCountState.generation || key !== navigationPendingCountState.key) return;
    navigationPendingCountState.controller = null;
    if (followUp) return;
    navigationPendingCountState.status = "error";
    navigationPendingCountState.authorization = emptyNavigationPendingCounts();
    navigationPendingCountState.credentialing = emptyNavigationPendingCounts();
    navigationPendingCountState.clientAction = emptyNavigationPendingCounts();
    updateAuthorizationsNavBadge();
    updateCredentialingNavBadge();
    updateClientActionBadge();
  });
}

function renderMetrics() {
  if (!els.metricPastDue || !els.metricReview || !els.metricOwed || !els.metricAging) return;
  const claims = getClaims();
  const pastDue = claims.filter(isPastDue).length;
  const review = claims.filter((claim) => claim.assignmentStatus === "Submitted For Review").length;
  const owed = claims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
  const aging = claims.filter((claim) => claimAgingBand(claim) === "121+").length;
  els.metricPastDue.textContent = pastDue;
  els.metricReview.textContent = review;
  els.metricOwed.textContent = money(owed);
  els.metricAging.textContent = aging;
}

// EVERY FUNCTION BELOW IS KEYED BY A CARD ID, NOT BY AN ALL DATA SLICER KEY.
// A bare key still means the All Data rail and behaves exactly as it did; a
// "report:" one means the Reports filter bar. See filterCardHost.
function slicerSelectionsForRender(key) {
  if (slicerDraftKey === key && slicerDraftState) {
    return Array.isArray(slicerDraftState[key]) ? slicerDraftState[key] : [];
  }
  const host = filterCardHost(key);
  return host.selections(host.key);
}

function slicerModeForRender(key) {
  if (slicerDraftKey === key && slicerDraftModeState) return filterModeFor(slicerDraftModeState, key);
  const host = filterCardHost(key);
  return filterModeFor(host.modes(), host.key);
}

function slicerExcludesForRender(key) {
  return slicerModeForRender(key) === FILTER_MODE_EXCLUDE;
}

function beginSlicerDraft(key, returnFocus = null) {
  const host = filterCardHost(key);
  if (host.surface === "slicer" && !Object.prototype.hasOwnProperty.call(emptySlicers, key)) return;
  if (host.surface === "report" && !reportFilterKeyIsMultiSelect(host.key)) return;
  // The draft is keyed by CARD ID in both cases, so one card's staged values can
  // never be read as another card's - including across the two surfaces.
  slicerDraftState = host.surface === "report"
    ? { [key]: host.selections(host.key).slice() }
    : ensureSlicerState(state.slicers);
  slicerDraftModeState = host.surface === "report"
    ? (filterModeExcludes(host.modes(), host.key) ? { [key]: FILTER_MODE_EXCLUDE } : {})
    : { ...slicerModeState() };
  slicerDraftKey = key;
  // The card session's starting point, kept across auto-applied commits so
  // Cancel and Escape can put the report back the way the reader found it.
  slicerDraftBaseline = { values: host.selections(host.key).slice(), exclude: filterModeExcludes(host.modes(), host.key) };
  slicerDraftReturnFocus = returnFocus || document.querySelector(`[data-slicer-panel="${key}"] > summary`);
  refreshSlicerDraftControls(key);
}

function slicerDraftChanged(key = slicerDraftKey) {
  if (!key || !slicerDraftState) return false;
  const host = filterCardHost(key);
  const applied = JSON.stringify(host.selections(host.key).slice().sort());
  const draft = JSON.stringify((slicerDraftState[key] || []).slice().sort());
  if (applied !== draft) return true;
  // A direction can be staged before values. Empty lists have no applied filter.
  if (!(slicerDraftState[key] || []).length) return false;
  // Flipping ONLY the direction is a real change: same values, opposite book.
  // Without this leg Apply stayed disabled and the switch did nothing.
  return filterModeFor(host.modes(), host.key) !== filterModeFor(slicerDraftModeState || {}, key);
}

function refreshSlicerDraftControls(key = slicerDraftKey) {
  if (!key) return;
  const panel = document.querySelector(`[data-slicer-panel="${key}"]`);
  const selected = new Set(slicerSelectionsForRender(key));
  panel?.querySelectorAll?.(`input[data-slicer-key="${key}"]`)?.forEach?.((input) => { input.checked = selected.has(input.dataset.slicerValue); });
  // APPLY IS NEVER DISABLED ANY MORE. Before auto-apply it was greyed out when
  // there was nothing staged, which was honest then: the button WAS the only
  // way to commit. Now its job is "commit now and close" and that is always
  // available - and a button that greys itself out 400ms after a tick is a
  // button the reader (and every shipped proof that clicks it) reaches for and
  // finds dead. With nothing staged it just closes the card.
  const apply = panel?.querySelector?.("[data-slicer-apply]");
  if (apply) apply.disabled = false;
  const undo = panel?.querySelector?.("[data-slicer-undo]");
  if (undo) undo.hidden = !(filterCardUndoState && filterCardUndoState.key === key);
  const status = panel?.querySelector?.("[data-slicer-draft-status]");
  const excluding = slicerExcludesForRender(key);
  const word = excluding ? "to exclude" : "selected";
  // "Applying..." while the burst settles, because a control that appears to do
  // nothing for 400ms reads as broken. "N staged" survives for the hand-pressed
  // path (Apply is still there and still immediate); once the commit lands the
  // line goes back to stating what is SELECTED, or what is being EXCLUDED.
  if (status) {
    status.textContent = filterCardAutoApplyPending(key)
      ? `Applying ${selected.size} ${word}\u2026`
      : (slicerDraftChanged(key) ? `${selected.size} staged` : `${selected.size} ${word}`);
  }
  refreshSlicerModeControls(key);
  updateSlicerPanelSummary(key, panel?.querySelectorAll?.(`input[data-slicer-key="${key}"]`)?.length || 0, "", Array.from(selected));
}

// THE DIRECTION SWITCH AND THE ONE-CLICK FAMILY.
//
// Two radios in one named group, so arrow keys move between them and a screen
// reader announces the group - a <button> pair would have needed all of that
// written by hand and would still not be a choice of one from two. The family
// button is a toggle and says so through aria-pressed.
function ensureSlicerModeControls(key) {
  if (!filterKeySupportsExclusion(key)) return;
  const panel = document.querySelector(`[data-slicer-panel="${key}"]`);
  const body = panel?.querySelector?.(".slicer-filter-body");
  const host = panel?.querySelector?.(".slicer-options");
  if (!body || !host) return;
  let controls = body.querySelector(".slicer-mode-controls");
  if (!controls) {
    controls = document.createElement("div");
    controls.className = "slicer-mode-controls";
    controls.innerHTML = `
      <div class="slicer-mode-switch" role="radiogroup" aria-label="${escapeHtml(slicerLabel(key))} filter direction">
        <label class="slicer-mode-option"><input type="radio" name="slicer-mode-${escapeHtml(key)}" value="${FILTER_MODE_INCLUDE}" data-slicer-mode="${escapeHtml(key)}"><span>Include only these</span></label>
        <label class="slicer-mode-option"><input type="radio" name="slicer-mode-${escapeHtml(key)}" value="${FILTER_MODE_EXCLUDE}" data-slicer-mode="${escapeHtml(key)}"><span>Exclude these</span></label>
      </div>
      <button class="ghost-button slicer-family-button" type="button" data-slicer-family="${escapeHtml(key)}" aria-pressed="false" hidden>${escapeHtml(CLIENT_ACTION_PENDING_FAMILY)} (all)</button>
    `;
    host.insertAdjacentElement("beforebegin", controls);
    controls.querySelectorAll("[data-slicer-mode]").forEach((input) => {
      input.addEventListener("change", () => { if (input.checked) setSlicerDraftMode(key, input.value); });
    });
    controls.querySelector("[data-slicer-family]")?.addEventListener("click", () => toggleSlicerDraftFamily(key));
  }
  refreshSlicerModeControls(key);
}

function slicerOfferedValues(key) {
  const panel = document.querySelector(`[data-slicer-panel="${key}"]`);
  return Array.from(panel?.querySelectorAll?.(`input[data-slicer-key="${key}"]`) || [])
    .map((input) => input.dataset.slicerValue)
    .filter(Boolean);
}

function refreshSlicerModeControls(key) {
  if (!filterKeySupportsExclusion(key)) return;
  const panel = document.querySelector(`[data-slicer-panel="${key}"]`);
  const controls = panel?.querySelector?.(".slicer-mode-controls");
  if (!controls) return;
  const mode = slicerModeForRender(key);
  controls.querySelectorAll("[data-slicer-mode]").forEach((input) => { input.checked = input.value === mode; });
  const family = controls.querySelector("[data-slicer-family]");
  if (!family) return;
  // The one-click family belongs to the FOLLOW-UP ACTION filter on either
  // surface and to nothing else: "Client Action Pending" is a follow-up action
  // vocabulary, and offering the button over payers would be a button that
  // matches nothing.
  const values = filterCardKey(key) === "action" ? followUpFamilyValues(slicerOfferedValues(key)) : [];
  family.hidden = values.length === 0;
  if (family.hidden) return;
  const selected = new Set(slicerSelectionsForRender(key));
  const all = values.every((value) => selected.has(value));
  family.setAttribute("aria-pressed", all ? "true" : "false");
  family.textContent = `${CLIENT_ACTION_PENDING_FAMILY} (all ${values.length})`;
  family.title = all
    ? `Remove every ${CLIENT_ACTION_PENDING_FAMILY} variant from this filter`
    : `${mode === FILTER_MODE_EXCLUDE ? "Exclude" : "Include"} every ${CLIENT_ACTION_PENDING_FAMILY} variant: ${values.join(", ")}`;
}

function setSlicerDraftMode(key, mode) {
  if (!filterKeySupportsExclusion(key)) return;
  if (slicerDraftKey !== key || !slicerDraftState) beginSlicerDraft(key);
  if (!slicerDraftModeState) slicerDraftModeState = {};
  if (String(mode) === FILTER_MODE_EXCLUDE) slicerDraftModeState[key] = FILTER_MODE_EXCLUDE;
  else delete slicerDraftModeState[key];
  scheduleFilterCardAutoApply(key);
  refreshSlicerDraftControls(key);
}

// One click, both directions: if every offered variant is already ticked the
// button takes them all off, otherwise it puts them all on. Whether that means
// "show only these" or "hide these" is the switch's job, not this button's.
function toggleSlicerDraftFamily(key) {
  if (slicerDraftKey !== key || !slicerDraftState) beginSlicerDraft(key);
  if (!slicerDraftState) return;
  const values = filterCardKey(key) === "action" ? followUpFamilyValues(slicerOfferedValues(key)) : [];
  if (!values.length) return;
  const selected = new Set(slicerDraftState[key] || []);
  const all = values.every((value) => selected.has(value));
  values.forEach((value) => { if (all) selected.delete(value); else selected.add(value); });
  slicerDraftState[key] = Array.from(selected);
  scheduleFilterCardAutoApply(key);
  refreshSlicerDraftControls(key);
}

function setSlicerDraftSelection(key, value, checked) {
  if (slicerDraftKey !== key || !slicerDraftState) beginSlicerDraft(key);
  if (!slicerDraftState) return;
  const selected = new Set(slicerDraftState[key] || []);
  if (checked) selected.add(value);
  else {
    selected.delete(value);
    if (filterCardKey(key) === "aging") agingSlicerBandValues(selected, value).forEach((twin) => selected.delete(twin));
  }
  slicerDraftState[key] = Array.from(selected);
  scheduleFilterCardAutoApply(key);
  refreshSlicerDraftControls(key);
}

function selectAllSlicerDraft(key) {
  if (slicerDraftKey !== key || !slicerDraftState) beginSlicerDraft(key);
  if (!slicerDraftState) return;
  const panel = document.querySelector(`[data-slicer-panel="${key}"]`);
  const visible = Array.from(panel?.querySelectorAll?.(`input[data-slicer-key="${key}"]`) || [])
    .filter((input) => !input.closest?.(".slicer-option")?.hidden)
    .map((input) => input.dataset.slicerValue);
  slicerDraftState[key] = Array.from(new Set([...(slicerDraftState[key] || []), ...visible]));
  scheduleFilterCardAutoApply(key);
  refreshSlicerDraftControls(key);
}

function clearSlicerDraft(key) {
  if (slicerDraftKey !== key || !slicerDraftState) beginSlicerDraft(key);
  if (!slicerDraftState) return;
  slicerDraftState[key] = [];
  // A direction over nothing is not a filter; leaving "exclude" staged on an
  // empty list is how a cleared card comes back looking active.
  if (slicerDraftModeState) delete slicerDraftModeState[key];
  scheduleFilterCardAutoApply(key);
  refreshSlicerDraftControls(key);
}

// CANCEL AND ESCAPE MEAN "PUT IT BACK", and under auto-apply that is a real
// action rather than just dropping a draft: the burst may already have been
// committed, so anything that moved since this card was opened is committed
// BACK to the baseline. Without this leg Cancel would be a no-op on exactly the
// changes a reader most wants to take back.
//
// ONLY WHEN ASKED, THOUGH. This function is also how a card that simply CLOSES
// drops its draft - opening a second card closes the first, and the accordion
// and the panel do the same. Reverting there would silently undo a filter the
// reader had already watched apply, just because they moved on to the next
// card. So `revert` is passed by the Cancel button and the Escape key and by
// nothing else.
function discardSlicerDraft({ close = false, returnFocus = true, revert = false } = {}) {
  const key = slicerDraftKey;
  const focusTarget = slicerDraftReturnFocus;
  const baseline = slicerDraftBaseline;
  cancelFilterCardAutoApply();
  slicerDraftState = null;
  slicerDraftModeState = null;
  slicerDraftKey = "";
  slicerDraftReturnFocus = null;
  slicerDraftBaseline = null;
  const panel = key ? document.querySelector(`[data-slicer-panel="${key}"]`) : null;
  if (close && panel) panel.open = false;
  if (key && baseline && revert) {
    const host = filterCardHost(key);
    const applied = host.selections(host.key);
    const drifted = JSON.stringify(applied.slice().sort()) !== JSON.stringify(baseline.values.slice().sort())
      || filterModeExcludes(host.modes(), host.key) !== baseline.exclude;
    if (drifted) {
      filterCardUndoState = null;
      host.commit(host.key, baseline.values.slice(), baseline.exclude);
      if (returnFocus) focusTarget?.focus?.();
      return;
    }
  }
  if (key) updateSlicerPanelSummary(key, panel?.querySelectorAll?.(`input[data-slicer-key="${key}"]`)?.length || 0);
  if (returnFocus) focusTarget?.focus?.();
}

// One level, and only for an AUTO-applied change: a hand-pressed Apply closes
// the card, which is itself the signal that the reader meant it.
function undoFilterCardChange(key) {
  const undo = filterCardUndoState;
  if (!undo || undo.key !== key) return;
  filterCardUndoState = null;
  cancelFilterCardAutoApply();
  const host = filterCardHost(key);
  slicerDraftState = null;
  slicerDraftModeState = null;
  slicerDraftKey = "";
  host.commit(host.key, undo.values.slice(), undo.exclude, { revalidate: true });
}

// THE ALL DATA HOST'S COMMIT. Extracted from applySlicerDraft so the shared
// card can hand a committed (values, direction) pair to whichever surface it is
// sitting on without knowing anything about that surface.
function applyAppliedSlicerSelection(key, values, exclude, { revalidate = false } = {}) {
  state.slicers = ensureSlicerState(state.slicers);
  state.slicers[key] = migrateSlicerSelectionValues(key, values || []);
  // Values and direction move together or the grid and the card disagree.
  const nextModes = { ...(state.slicerModes || {}) };
  if (exclude) nextModes[key] = FILTER_MODE_EXCLUDE;
  else delete nextModes[key];
  state.slicerModes = ensureFilterModeState(nextModes, state.slicers);
  resetTrackerPage();
  saveState();
  // Commit freshness before starting this filter read. The bubbling click
  // check would otherwise wait for it, discard it, and fetch it again.
  if (revalidate) void checkTrackerPublication();
  renderSlicers();
  renderClaimsTable();
}

// `auto` is the debounced commit: it must NOT close the card and must NOT move
// the keyboard, because the reader is still standing in the checklist. The
// hand-pressed path is unchanged - immediate, closes, returns focus - so Apply
// remains the no-wait escape hatch it always was.
function applySlicerDraft(key, { auto = false } = {}) {
  if (slicerDraftKey !== key || !slicerDraftState) return;
  // NOTHING STAGED IS NOT A COMMIT. Under auto-apply the debounce may already
  // have written exactly this selection, and re-committing it would spend a
  // second tracker query on a question the grid has already answered.
  if (!slicerDraftChanged(key)) {
    if (!auto) {
      const closing = document.querySelector(`[data-slicer-panel="${key}"]`);
      const focusNode = slicerDraftReturnFocus;
      cancelFilterCardAutoApply();
      slicerDraftState = null;
      slicerDraftModeState = null;
      slicerDraftKey = "";
      slicerDraftReturnFocus = null;
      slicerDraftBaseline = null;
      if (closing) closing.open = false;
      focusNode?.focus?.();
    }
    return;
  }
  const host = filterCardHost(key);
  const values = slicerDraftState[key] || [];
  const exclude = filterModeFor(slicerDraftModeState || {}, key) === FILTER_MODE_EXCLUDE;
  const focusTarget = slicerDraftReturnFocus;
  const returnFocusNode = auto ? null : focusTarget;
  const baseline = slicerDraftBaseline;
  const panel = document.querySelector(`[data-slicer-panel="${key}"]`);
  if (!auto) cancelFilterCardAutoApply();
  // What Undo goes back to: the state in front of THIS commit.
  filterCardUndoState = auto
    ? { key, values: host.selections(host.key).slice(), exclude: filterModeExcludes(host.modes(), host.key) }
    : null;
  slicerDraftState = null;
  slicerDraftModeState = null;
  slicerDraftKey = "";
  slicerDraftReturnFocus = null;
  if (!auto) slicerDraftBaseline = null;
  if (panel && !auto) panel.open = false;
  host.commit(host.key, values, exclude, { revalidate: !auto });
  markFilterCardApplying(key, false);
  if (auto) {
    // Re-seat the draft on the freshly applied selection so the card keeps
    // working and its status line goes back to "N selected" - while KEEPING the
    // session baseline, which is what Cancel and Escape still revert to.
    beginSlicerDraft(key, focusTarget);
    if (slicerDraftKey === key) {
      slicerDraftBaseline = baseline;
      // Clearing old values must not erase a direction chosen for the next ones.
      if (!values.length && exclude) slicerDraftModeState[key] = FILTER_MODE_EXCLUDE;
    }
    refreshSlicerDraftControls(key);
    return;
  }
  returnFocusNode?.focus?.();
}

// THE ACTION BAR IS BUILT ONCE, AND ALWAYS READ BACK OFF THE DRAFT.
//
// This bar is static markup, but it was rewritten from innerHTML on EVERY call
// - and every slicer repaint calls it. So the live [data-slicer-draft-status]
// was replaced by an empty one and Apply came back enabled, while nothing on
// the repaint path re-read the draft. A /api/tracker/facets or
// /api/tracker/query answer landing right after "Select all" therefore wiped
// "1 staged" off a card whose boxes were still ticked and whose draft still
// held exactly the searched value: the card stopped saying what it would send.
// Rebuilding it only when it is missing keeps the handlers (and the reader's
// staged count) alive across repaints, and the refresh below makes the bar
// agree with the draft every time the options underneath are redrawn.
function ensureSlicerPanelActions(key) {
  const panel = document.querySelector(`[data-slicer-panel="${key}"]`);
  const body = panel?.querySelector?.(".slicer-filter-body");
  if (!body) return;
  let actions = body.querySelector(".slicer-panel-actions");
  if (!actions) {
    actions = document.createElement("div");
    actions.className = "slicer-panel-actions";
    actions.setAttribute("role", "group");
    actions.setAttribute("aria-label", `${slicerLabel(key)} selection actions`);
    // APPLY STAYS. It is no longer the only way to commit, but it is still the
    // immediate one - press it and the card closes now rather than in 400ms -
    // and the shipped browser proofs that click it keep working unchanged.
    // UNDO appears only after an auto-applied change and takes back exactly that
    // one commit.
    actions.innerHTML = `<button class="ghost-button" type="button" data-slicer-select-all>Select all</button><span data-slicer-draft-status role="status" aria-live="polite"></span><button class="ghost-button" type="button" data-slicer-undo hidden>Undo</button><button class="ghost-button" type="button" data-slicer-cancel>Cancel</button><button class="primary-button" type="button" data-slicer-apply>Apply</button>`;
    body.appendChild(actions);
    actions.querySelector("[data-slicer-select-all]")?.addEventListener("click", () => selectAllSlicerDraft(key));
    actions.querySelector("[data-slicer-undo]")?.addEventListener("click", () => undoFilterCardChange(key));
    actions.querySelector("[data-slicer-cancel]")?.addEventListener("click", () => discardSlicerDraft({ close: true, revert: true }));
    actions.querySelector("[data-slicer-apply]")?.addEventListener("click", () => applySlicerDraft(key));
    // ESCAPE IS CANCEL, on a card that now commits by itself. Without it the
    // only way back from an auto-applied change is a mouse trip to a button.
    const panelNode = document.querySelector(`[data-slicer-panel="${key}"]`);
    if (panelNode && !panelNode.dataset.slicerEscapeBound) {
      panelNode.dataset.slicerEscapeBound = "1";
      panelNode.addEventListener("keydown", (event) => {
        if (event.key !== "Escape" || slicerDraftKey !== key) return;
        event.preventDefault();
        event.stopPropagation();
        discardSlicerDraft({ close: true, revert: true });
      });
    }
  }
  ensureSlicerModeControls(key);
  if (slicerDraftKey === key && slicerDraftState) refreshSlicerDraftControls(key);
}

function renderSlicers() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("workbench")) return;
  const slicerProfileStart = workbenchProfileNow();
  state.slicers = ensureSlicerState(state.slicers);
  normalizeSlicerSelections();
  state.slicerSize = state.slicerSize === "expanded" ? "expanded" : "compact";
  applySlicerSizePreference();
  const query = buildTrackerQueryRequest(state.rowMode || "service");
  if (trackerApiEnabled()) trackerApiPageForQuery(query);
  const receivedFacets = trackerApiFacetsForQuery(query);
  prepareTrackerFilterChoices(query);
  const summaryKey = trackerApiFacetCacheKey(trackerFacetQueryRequest(query));
  const serverFacets = !trackerApiEnabled() || (trackerApiFacetState.status === "ok" && trackerApiFacetState.key === summaryKey)
    ? receivedFacets : null;
  // WHERE SLICER COUNTS ARE ALLOWED TO COME FROM (2026-08-17).
  // Page rows are never a valid fallback: they omit values that happen to live on
  // another page and make the rail depend on pagination - the same rule the
  // column-filter menu already states in columnFilterOptions. currentSlicerClaims()
  // returns the CURRENT PAGE whenever the API lane is on, so it may only be read
  // when that lane is off, where it resolves to the whole role-filtered getClaims()
  // set. When the API owns the data and its facets are not here, the rail says so
  // instead of counting the rows that happen to be on screen.
  const facetLane = slicerFacetLane(serverFacets);
  const claims = facetLane === "local" ? currentSlicerClaims() : [];
  const slicerCounts = facetLane === "server"
    ? buildSlicerValueCountsFromFacets(serverFacets.facets || {})
    : buildSlicerValueCounts(claims);
  renderQueueCards(claims, serverFacets, facetLane);
  slicerDefinitions.forEach((definition) => {
    const container = els[definition.element];
    if (!container) return;
    // An open card owns a scoped paged read, independent of the shared rail batch.
    if (trackerApiEnabled() && document.querySelector(`[data-slicer-panel="${definition.key}"]`)?.open) {
      requestPagedSlicerChoices(definition.key, document.querySelector(`[data-slicer-search="${definition.key}"]`)?.value || "");
      return;
    }
    if (trackerApiEnabled()) {
      const read = trackerSlicerFacetReadState(definition.key, query);
      const selected = slicerSelectionsForRender(definition.key);
      if (read.status !== "ready") {
        const label = { unloaded: "Open to load", loading: "Loading", error: "Unavailable" }[read.status];
        container.innerHTML = read.status === "loading" ? slicerLanePlaceholderHtml("waiting") : '<p class="muted-note">' + label + '</p>';
        updateSlicerPanelSummary(definition.key, 0, label, selected);
        return;
      }
      const facetCounts = buildSlicerValueCountsFromFacets(read.response.facets || {});
      slicerCounts.set(definition.key, facetCounts.get(definition.key) || new Map());
    }
    // The whole card is replaced, not just its counts: requiredValues would still
    // paint "Paid 0 / Not Paid 0" off an empty map, and an asserted zero is the
    // same lie as a page-derived count.
    if (!trackerApiEnabled() && facetLane !== "server" && facetLane !== "local") {
      container.innerHTML = slicerLanePlaceholderHtml(facetLane);
      updateSlicerPanelSummary(definition.key, 0, facetLane === "waiting" ? "Loading" : "Unavailable");
      return;
    }
    const counts = slicerCounts.get(definition.key) || new Map();
    const selectedValues = slicerSelectionsForRender(definition.key);
    const values = sortedSlicerValuesFromCounts(counts, definition, selectedValues);
    container.innerHTML = values.map((value) => {
      const count = counts.get(value) || 0;
      const active = selectedValues.includes(value);
        const displayValue = slicerDisplayValue(definition.key, value);
        return `
        <label class="slicer-option ${active ? "active" : ""}">
          <input type="checkbox" ${active ? "checked" : ""} data-slicer-key="${definition.key}" data-slicer-value="${escapeHtml(value)}">
          <span>${escapeHtml(displayValue)}</span>
          <strong>${count}</strong>
        </label>
      `;
    }).join("");
    ensureSlicerPanelActions(definition.key);
    updateSlicerPanelSummary(definition.key, values.length, "", selectedValues);
    const search = document.querySelector(`[data-slicer-search="${definition.key}"]`);
    applySlicerOptionSearch(definition.key, search?.value || "");
  });

  // The REPORT cards bind their own inputs as they are painted
  // (renderReportFilterCards); excluding them here keeps one listener per box
  // rather than a second one added on every All Data repaint.
  Array.from(document.querySelectorAll('input[data-slicer-key]:not([data-report-filter-option])')).forEach((input) => {
    input.addEventListener("change", () => setSlicerDraftSelection(input.dataset.slicerKey, input.dataset.slicerValue, input.checked));
  });
  renderActiveSlicerText();
  workbenchRenderProfileState.lastSlicerFacetMs = moneyNumber(workbenchProfileNow() - slicerProfileStart);
  if (workbenchRenderProfileState.current) {
    workbenchRenderProfileState.current.slicerFacetMs = workbenchRenderProfileState.lastSlicerFacetMs;
  }
}

function updateSlicerPanelSummary(key, optionCount = 0, status = "", selectedValues = slicerSelectionsForRender(key)) {
  const summary = document.querySelector(`[data-slicer-summary="${key}"]`);
  const panel = document.querySelector(`[data-slicer-panel="${key}"]`);
  const selectedCount = Array.isArray(selectedValues) ? selectedValues.length : 0;
  if (summary) {
    // "3 selected" over an EXCLUSION is a lie the size of the book: it names the
    // three the reader is hiding as though they were the three being shown.
    const settled = slicerExcludesForRender(key) ? "excluded" : "selected";
    summary.textContent = selectedCount
      ? `${selectedCount} ${slicerDraftKey === key && slicerDraftChanged(key) ? "staged" : settled}`
      : (status || `${optionCount} option${optionCount === 1 ? "" : "s"}`);
  }
  panel?.classList?.toggle("has-selection", selectedCount > 0);
}

function applySlicerOptionSearch(key, rawQuery = "") {
  const panel = document.querySelector(`[data-slicer-panel="${key}"]`);
  if (!panel) return;
  // A REPORT CARD SEARCHES ITS OWN LIST, AND ASKS THE SERVER FOR MORE.
  //
  // The two surfaces read different paged-choice endpoints - All Data uses the
  // tracker page/facet lane, Reports uses /api/tracker/facets through
  // reportFacetSetPreference - so this is the one place the card has to know
  // which it is on. The local hide/show below then runs for both, so a search
  // narrows what is on screen immediately instead of waiting for a round trip.
  if (filterCardSurface(key) === "report") {
    // Network has no facet lane - its three buckets are a fixed vocabulary, not
    // a read of anybody's book - so there is nothing to page and the local
    // narrowing below is the whole of its search.
    const facet = REPORT_FILTER_FACET_KEYS[filterCardKey(key)];
    if (facet && trackerApiEnabled()) reportFacetSetPreference(facet, { search: String(rawQuery || ""), page: 1 }, true);
  } else if (trackerApiEnabled() && panel.open) { requestPagedSlicerChoices(key, rawQuery, true); return; }
  const query = String(rawQuery || "").trim().toLocaleLowerCase();
  Array.from(panel.querySelectorAll(`[data-slicer-key="${key}"]`)).forEach((input) => {
    const option = input.closest?.(".slicer-option");
    const actionKey = filterCardKey(key);
    const label = actionKey === "action" ? slicerDisplayValue("action", input.dataset.slicerValue) : option?.textContent || input.dataset.slicerValue || "";
    if (option) option.hidden = actionKey === "action" ? !followUpActionChoiceMatches("action", label, query) : Boolean(query && !String(label).toLocaleLowerCase().includes(query));
  });
}

// THE SLICER RAIL HAS EXACTLY TWO LEGITIMATE COUNT SOURCES.
//   "server" - /api/tracker/facets, which aggregates the whole filtered
//              population before pagination.
//   "local"  - the API lane is off, so currentSlicerClaims() resolves to
//              getClaims(): the entire role-filtered dataset, not a page.
// Everything else is a state where we do not know the dataset-wide values yet,
// and the rail must look like it does not know. This is the fix for the failure
// mode of 2026-08-11: /api/tracker/facets answered HTTP 400 on 100% of calls, the
// rail quietly re-counted the 500 loaded rows, and nothing on screen said a word.
// The failure cooldown is a lane state too - it is why no request is in flight.
function slicerFacetLane(serverFacets) {
  if (serverFacets) return "server";
  if (!trackerApiEnabled()) return "local";
  if (trackerApiFacetState.status === "denied") return "denied";
  if (trackerApiFacetState.status === "error" || trackerApiFacetRequestSuppressed()) return "unavailable";
  return "waiting";
}

// Fixed copy only. trackerApiFacetState.error is assembled from the server body
// and can carry SQL text or column names; the rail never repeats it. The typed
// scope denial IS server-authored user-safe copy, so that one is shown.
function slicerLaneMessage(lane) {
  if (lane === "denied") return trackerApiFacetState.scopeDenial?.message || "Filter values are outside your access.";
  if (lane === "unavailable") return "Filter values unavailable";
  return "Loading filter values";
}

function slicerLanePlaceholderHtml(lane) {
  // Waiting is a wait, not a warning: skeleton pills only, sized inside the
  // reserved .slicer-options min-height so the board cannot inflate when facets
  // land. A stated failure gets words.
  if (lane === "waiting") return '<span class="skeleton-pill" aria-hidden="true"></span>'.repeat(3);
  return `<p class="muted-note">${escapeHtml(slicerLaneMessage(lane))}</p>`;
}

function normalizeSlicerSelections() {
  if (!state?.slicers) return;
  Object.keys(state.slicers).forEach((key) => {
    if (!Array.isArray(state.slicers[key])) {
      state.slicers[key] = [];
      return;
    }
    // Expand the retired collapsed "No Rate Found" selection BEFORE canonicalizing:
    // canonicalMatchSlicerValue resolves it to a single bucket, which would silently
    // narrow a saved filter that used to mean "either rate missing".
    const raw = migrateSlicerSelectionValues(key, state.slicers[key]);
    const canonical = raw.map((value) => canonicalSlicerValue(key, value)).filter(Boolean);
    state.slicers[key] = Array.from(new Set(canonical));
    if (key === "claimDue") {
      state.slicers[key] = state.slicers[key].filter((value) => value === "PAST DUE" || value === "DUE TODAY");
    }
  });
}

function buildSlicerValueCountsFromFacets(facets = {}) {
  const countsByKey = new Map(slicerDefinitions.map((definition) => [definition.key, new Map()]));
  const mapping = {
    renderingProvider: "provider",
    match: "match",
    aging: "aging",
    action: "action",
    primaryPayer: "primaryPayer",
    secondaryPayer: "secondaryPayer",
    facility: "facility",
    procedure: "procedure",
    claimStatus: "claimStatus",
    serviceMonth: "serviceMonth",
    followUpDue: "followUpDue",
    primaryPayerClass: "primaryPayerClass",
    secondaryPayerClass: "secondaryPayerClass",
    patClassification: "patClassification",
    secondaryCalc: "secondaryCalc",
    claimClassification: "claimClassification",
    // The server bucket names ARE the chip names: the facet CASE in
    // trackerQueryExecutionPlan emits In Network / OON / Unknown from the same
    // vocabulary networkBucket uses here, so no translation is needed. Without
    // this line the rail would paint three chips reading 0 on the LIVE lane while
    // the local lane counted correctly - an asserted zero, which is worse than a
    // blank and exactly what the note below this map warns about.
    network: "network"
  };
  Object.entries(mapping).forEach(([slicerKey, facetKey]) => {
    const counts = countsByKey.get(slicerKey);
    if (!counts) return;
    (facets[facetKey] || []).forEach((row) => {
      const value = canonicalSlicerValue(slicerKey, row.value);
      if (!value) return;
      counts.set(value, (counts.get(value) || 0) + nonNegativeNumber(row.count, 0));
    });
  });
  return countsByKey;
}

function canonicalSlicerValue(key, value) {
  const text = cleanFacetDisplayValue(value);
  if (key === "action") return followUpFilterValue(value);
  if (key !== "match") return text;
  return canonicalMatchSlicerValue(text);
}

function canonicalMatchSlicerValue(value) {
  const text = cleanFacetDisplayValue(value);
  const key = text.toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (key === "true" || key === "paid") return "Paid";
  if (key === "false" || key === "notpaid" || key === "unpaid") return "Not Paid";
  if (key === "underpaid" || key === "partialpaid" || key === "partialpayment") return "Under Paid";
  if (key === "overpaid") return "Over Paid";
  if (key === "missingfeeschedule") return "Missing Fee Schedule";
  if (key === "noprimaryrate") return "No Primary Rate";
  if (key === "nosecondaryrate") return "No Secondary Rate";
  // Retired collapsed bucket. A row/facet still carrying it cannot say WHICH rate is
  // missing, so it resolves to the primary bucket — the same tie-break CT-TRACKER
  // itself applies when both rates are missing (build_lite_snapshot.py:2529-2540).
  // Persisted user SELECTIONS are not resolved this way: migrateMatchSlicerValues
  // expands them to both buckets so no saved filter silently narrows.
  if (key === "noratefound") return "No Primary Rate";
  if (key === "patbaloverexpected") return "Pat Bal Over Expected";
  if (key === "patpaidoverexpected") return "Pat Paid Over Expected";
  return text;
}

// Saved-filter migration for the No Rate Found -> No Primary/No Secondary split.
// Any selection list that still names the collapsed bucket keeps selecting the same
// ROWS by selecting both replacement buckets. Order follows MATCH_SLICER_ORDER.
function migrateMatchSlicerValues(values = []) {
  const list = Array.isArray(values) ? values : [];
  if (!list.some(matchValueIsLegacyCollapsedNoRate)) return list;
  const expanded = [];
  list.forEach((value) => {
    if (matchValueIsLegacyCollapsedNoRate(value)) {
      NO_RATE_MATCH_VALUES.forEach((replacement) => expanded.push(replacement));
      return;
    }
    expanded.push(value);
  });
  return Array.from(new Set(expanded));
}

function matchValueIsLegacyCollapsedNoRate(value) {
  return String(value == null ? "" : value).trim().toLowerCase().replace(/[^a-z0-9]+/g, "") === "noratefound";
}

// The ONE list of persisted-SELECTION migrations, applied wherever stored slicer state
// re-enters the app: ensureSlicerState (localStorage restore, every saved AR view, the
// state import) and normalizeSlicerSelections (every render). Both entries EXPAND rather
// than rewrite, so a stored value never stops matching the feed that produced it, and
// both are idempotent so re-running them changes nothing.
function migrateSlicerSelectionValues(key, values) {
  const list = Array.isArray(values) ? values : [];
  if (key === "match") return migrateMatchSlicerValues(list);
  if (key === "aging") return migrateAgingSlicerValues(list);
  return list;
}

function cleanFacetDisplayValue(value) {
  const text = String(value == null ? "" : value).trim();
  if (/^live\s+synapse\s+review$/i.test(text)) return "Blank";
  return text || "Blank";
}

// Filter-only vocabulary. Keep the wire token Blank and never add it to assignment pickers.
function followUpFilterValue(value) {
  const text = String(value == null ? "" : value).trim();
  return !text || /^(blank|live\s+synapse\s+review)$/i.test(text) ? "Blank" : text;
}

// Migrate historical filter placeholders only, never actual row/facet action names.
function followUpFilterSelectionValue(value) {
  return /^(\(blank\)|none \(blank\)|no action|none)$/i.test(String(value || "").trim()) ? "Blank" : followUpFilterValue(value);
}

function followUpFilterRowValue(claim = {}, service = null) {
  const keys = ["action", "followUpAction", "follow_up_action"];
  // An explicit empty/null line action is authoritative; only absent fields inherit.
  for (const row of [service, claim]) {
    if (!row) continue;
    const rowKeys = row === service ? ["followUpAction", "action", "follow_up_action"] : keys;
    for (const key of rowKeys) {
      if (Object.prototype.hasOwnProperty.call(row, key)) return followUpFilterValue(row[key]);
    }
  }
  return "Blank";
}

function followUpFilterValues(values = []) {
  return ["Blank", ...Array.from(new Set(values.map(followUpFilterValue))).filter(value => value !== "Blank")];
}

function followUpFilterOptions(options = []) {
  const counts = new Map([["Blank", 0]]);
  options.forEach(option => {
    const value = followUpFilterValue(option.value);
    counts.set(value, (counts.get(value) || 0) + (Number(option.count) || 0));
  });
  return Array.from(counts, ([value, count]) => ({ value, count }));
}

function slicerDisplayValue(key, value) {
  const text = cleanFacetDisplayValue(value);
  if (key === "action" && followUpFilterValue(value) === "Blank") return "No follow-up action";
  if (key === "claimDue") {
    if (text === "1" || /^true$/i.test(text)) return "Open AR";
    if (text === "0" || /^false$/i.test(text)) return "No Open AR";
  }
  return text;
}

function applySlicerSizePreference() {
  const board = document.getElementById("slicerBoard");
  const btn = document.getElementById("moreFiltersToggle");
  const advancedSections = [document.getElementById("slicerPayerSection"), document.getElementById("slicerServiceSection")].filter(Boolean);
  const expanded = state?.slicerSize === "expanded";
  if (board) board.classList.toggle("slicer-board--expanded", expanded);
  advancedSections.forEach((section) => { section.open = expanded; });
  if (btn) {
    btn.setAttribute("aria-expanded", expanded ? "true" : "false");
    btn.innerHTML = expanded
      ? 'Advanced Filters <span class="more-filters-caret" aria-hidden="true">▴</span>'
      : 'Advanced Filters <span class="more-filters-caret" aria-hidden="true">▾</span>';
  }
}

function renderServerFacetControls(response = {}) {
  if (!trackerApiEnabled() || !response?.facets) return;
  const values = (key) => (response.facets[key] || [])
    .map((row) => cleanFacetDisplayValue(row.value))
    .filter(Boolean);
  const matchDefinition = slicerDefinitions.find((definition) => definition.key === "match");
  const matchCounts = buildSlicerValueCountsFromFacets({ match: response.facets.match || [] }).get("match") || new Map();
  const matchValues = matchDefinition ? sortedSlicerValuesFromCounts(matchCounts, matchDefinition) : values("match");
  const preserveWorkingScope = { retainMissingSelection: true };
  const inventory = allDataTenantClientValues();
  if (inventory.length || Object.hasOwn(response.facets, "tenant") || Object.hasOwn(response.facets, "client")) {
    replaceSelectOptions(els.tenantFilter, inventory.length ? inventory : (values("tenant").length ? values("tenant") : values("client")), preserveWorkingScope);
  }
  if (Object.hasOwn(response.facets, "provider")) replaceSelectOptions(els.providerFilter, values("provider"), preserveWorkingScope);
  if (Object.hasOwn(response.facets, "primaryPayer")) replaceSelectOptions(els.primaryFilter, values("primaryPayer"), preserveWorkingScope);
  if (Object.hasOwn(response.facets, "secondaryPayer")) replaceSelectOptions(els.secondaryFilter, values("secondaryPayer"), preserveWorkingScope);
  if (Object.hasOwn(response.facets, "action")) replaceSelectOptions(els.actionFilter, values("action"), preserveWorkingScope);
  if (Object.hasOwn(response.facets, "match")) replaceSelectOptions(els.matchFilter, matchValues, { preserveOrder: true, retainMissingSelection: true });
  if (Object.hasOwn(response.facets, "claimStatus")) replaceSelectOptions(els.claimStatusFilter, values("claimStatus"), preserveWorkingScope);
  const lifecycleValues = values("lifecycle").filter((value) => value !== "Blank");
  if (Object.hasOwn(response.facets, "lifecycle")) replaceSelectOptions(els.lifecycleFilter, lifecycleValues, preserveWorkingScope);
  // Gold currently has no authoritative lifecycle_stage value. A dropdown
  // containing only "Blank" is not a useful filter and the old hard-coded
  // derived choices all returned an empty live grid. Keep the control available
  // on sources that actually serve lifecycle values; otherwise do not offer a
  // choice the server cannot truthfully answer.
  const lifecycleControl = els.lifecycleFilter?.closest?.("label");
  if (lifecycleControl && Object.hasOwn(response.facets, "lifecycle")) lifecycleControl.hidden = lifecycleValues.length === 0;
  // The aging option list is re-cut from whatever aging vocabulary the FEED serves, and
  // replaceSelectOptions keeps the current value only when the new list contains it VERBATIM — so a
  // selection made under one vocabulary is dropped to "All" the moment the source flips under a live
  // session. Re-resolve it onto the band the new list does serve instead of widening in silence.
  const agingBeforeFacets = els.agingFilter?.value || "All";
  if (Object.hasOwn(response.facets, "aging")) {
    replaceSelectOptions(els.agingFilter, values("aging"), preserveWorkingScope);
    if (els.agingFilter) els.agingFilter.value = agingFilterValueForOptions(els.agingFilter, agingBeforeFacets);
  }
  if (Object.hasOwn(response.facets, "owner")) replaceSelectOptions(els.ownerFilter, values("owner"), preserveWorkingScope);
  seedServerFacetAuxiliaryControls(response);
}

function seedServerFacetAuxiliaryControls(response = {}) {
  const inventoryValues = allDataTenantClientValues();
  const tenantValues = inventoryValues.length
    ? inventoryValues
    : (response.facets?.tenant || response.facets?.client || [])
      .map((row) => cleanFacetDisplayValue(row.value))
      .filter(Boolean);
  if (tenantValues.length) {
    replaceSelectOptions(els.clientSelect, tenantValues);
    replaceSelectOptions(els.reportRunTenantFilter, tenantValues);
    trackerExportTenantControls().forEach((control) => replaceSelectOptions(control, tenantValues));
    syncTrackerExportControls(primaryTrackerExportControlSet());
    replaceSelectOptions(els.feeScheduleOrgFilter, tenantValues);
  }
}

function buildSlicerValueCounts(claims) {
  const countsByKey = new Map(slicerDefinitions.map((definition) => [definition.key, new Map()]));
  claims.forEach((claim) => {
    slicerDefinitions.forEach((definition) => {
      const counts = countsByKey.get(definition.key);
      if (definition.serviceField) {
        const values = serviceSlicerValues(claim, definition);
        const canonical = new Set((values.length ? values : [""]).map((value) => canonicalSlicerValue(definition.key, value || "Blank")));
        canonical.forEach((value) => {
          if (!value) return;
          counts.set(value, (counts.get(value) || 0) + 1);
        });
        return;
      }
      const value = claimSlicerValue(claim, definition);
      if (!value) return;
      counts.set(value, (counts.get(value) || 0) + 1);
    });
  });
  return countsByKey;
}

// Sums the facet rows a queue can arrive under. Returns null (not a zero row)
// when the source carries no facet for the queue at all, so "this source does
// not model this queue" stays distinguishable from "this queue is empty".
function queueFacetForKey(queueCounts, queueKey) {
  const spellings = [...(QUEUE_SOURCE_VALUES[queueKey] || []), queueKey];
  const matches = spellings.map((spelling) => queueCounts.get(spelling)).filter(Boolean);
  if (!matches.length) return null;
  return matches.reduce((total, entry) => ({
    count: total.count + nonNegativeNumber(entry.count, 0),
    owed: total.owed + moneyNumber(entry.owed || 0)
  }), { count: 0, owed: 0 });
}

function renderQueueCards(claims, serverFacets = null, facetLane = serverFacets ? "server" : "local") {
  if (!els.queueCards) return;
  // Same rule as the slicer rail: these totals are dataset-wide or they are not
  // stated at all. "0" and "$0.00 open" off a lane that has no counts yet is an
  // asserted zero - the same class of lie as a page-derived count.
  const summaryReady = facetLane === "server";
  const canSeeMoney = hasPermission(PERMISSIONS.VIEW_FINANCIALS);
  const queueFacetRows = serverFacets?.facets?.queue || [];
  const queueCounts = new Map(queueFacetRows.map((row) => [cleanFacetDisplayValue(row.value), {
    count: nonNegativeNumber(row.count, 0),
    owed: moneyNumber(row.owed || 0)
  }]));
  const visibleQueues = serverFacets
    ? queueDefinitions.filter((queue) => SERVER_QUEUE_CARD_KEYS.has(queue.key))
    : queueDefinitions;
  els.queueCards.innerHTML = visibleQueues.map((queue) => {
    // Look the facet up under every spelling the SOURCE uses for this queue,
    // then under the portal key itself (a source that one day serves the portal
    // vocabulary must keep working). See QUEUE_SOURCE_VALUES for why the plain
    // key lookup found nothing.
    const serverQueue = queueFacetForKey(queueCounts, queue.key);
    const queueClaims = serverFacets ? [] : claims.filter((claim) => matchesQueue(claim, queue.key));
    const count = serverFacets ? (queue.key === "all" ? serverFacetTotalRows(serverFacets) : (serverQueue?.count || 0)) : queueClaims.length;
    const owed = serverFacets ? (queue.key === "all" ? serverFacetTotalOwed(serverFacets) : (serverQueue?.owed || 0)) : queueClaims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
    const active = state.queueFilter === queue.key;
    const neededKeys = queue.key === "all" ? ["tenant", "client"] : ["queue"];
    const laneHasCounts = facetLane === "local" || (summaryReady && neededKeys.every(key => Array.isArray(serverFacets?.facets?.[key])));
    const countText = laneHasCounts ? String(count) : "—";
    const owedText = laneHasCounts && canSeeMoney
      ? `${money(owed)} open`
      : (facetLane === "waiting" ? "Loading" : "Not available");
    return `
      <button class="queue-card ${active ? "active" : ""}" type="button" data-queue-key="${queue.key}">
        <span>${escapeHtml(queue.label)}</span>
        <strong>${countText}</strong>
        <small>${escapeHtml(owedText)}</small>
      </button>
    `;
  }).join("");

  Array.from(els.queueCards.querySelectorAll("[data-queue-key]")).forEach((button) => {
    button.addEventListener("click", () => setQueueFilter(button.dataset.queueKey));
  });
}

function savedOperationsViewMatchCount(viewState, allClaims) {
  if (!Array.isArray(allClaims) || !allClaims.length) return 0;
  const queue = viewState?.queueFilter;
  const minBalance = nonNegativeMoneyFilterValue(viewState?.minBalance);
  const slicers = viewState?.slicers || {};
  const claimDueSlicer = Array.isArray(slicers.claimDue) ? slicers.claimDue : [];
  return allClaims.filter((claim) => {
    if (queue && queue !== "all" && !matchesQueue(claim, queue)) return false;
    if (minBalance > 0 && claimOutstanding(claim) < minBalance) return false;
    if (claimDueSlicer.includes("PAST DUE") && !isPastDue(claim)) return false;
    return true;
  }).length;
}

function renderSavedOperationsViews(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.savedOperationsViews) return;
  const views = normalizeSavedOperationsViews(state.savedOperationsViews);
  state.savedOperationsViews = views;
  const activeSnapshot = captureOperationsViewState();
  const activeId = views.find((view) => sameOperationsView(view.state, activeSnapshot))?.id || "";
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const allClaims = getClaims();
  const counts = new Map(views.map((view) => [view.id, savedOperationsViewMatchCount(view.state, allClaims)]));
  els.savedOperationsViews.innerHTML = `
    <div class="saved-ops-head">
      <div>
        <span>Saved AR Views</span>
        <strong>${formatNounCount(views.length, "view")} · ${formatNounCount(claims.length, "claim")} in current view</strong>
      </div>
      <button class="ghost-button" type="button" data-save-ops-view>Save Current View</button>
    </div>
    <div class="saved-ops-grid">
      ${views.map((view) => renderSavedOperationsView(view, activeId, counts.get(view.id))).join("")}
    </div>
  `;
  els.savedOperationsViews.querySelector("[data-save-ops-view]")?.addEventListener("click", saveCurrentOperationsView);
  Array.from(els.savedOperationsViews.querySelectorAll("[data-ops-view-id]")).forEach((button) => {
    button.addEventListener("click", () => applySavedOperationsView(button.dataset.opsViewId));
  });
  Array.from(els.savedOperationsViews.querySelectorAll("[data-delete-ops-view]")).forEach((button) => {
    button.addEventListener("click", () => deleteSavedOperationsView(button.dataset.deleteOpsView));
  });
}

function renderSavedOperationsView(view, activeId = "", matchCount) {
  const queue = view.state?.queueFilter && view.state.queueFilter !== "all" ? queueLabel(view.state.queueFilter) : "All queues";
  const filters = savedOperationsViewSummary(view.state);
  const count = Number.isFinite(matchCount) ? matchCount : null;
  const countClass = count == null ? "" : count === 0 ? " is-empty" : "";
  return `
    <article class="saved-ops-card ${view.id === activeId ? "is-active" : ""}">
      <button type="button" data-ops-view-id="${escapeHtml(view.id)}">
        <span class="saved-ops-card-row">
          <span class="saved-ops-card-label">${escapeHtml(view.label)}</span>
          ${count != null ? `<span class="saved-ops-card-count${countClass}" title="${count} matching claim${count === 1 ? "" : "s"}">${count}</span>` : ""}
        </span>
        <strong>${escapeHtml(queue)}</strong>
        <small>${escapeHtml(filters)}</small>
      </button>
      ${view.builtIn ? "" : `<button class="saved-ops-delete" type="button" data-delete-ops-view="${escapeHtml(view.id)}" aria-label="Delete ${escapeHtml(view.label)}">×</button>`}
    </article>
  `;
}

function defaultSavedOperationsViews() {
  return [
    savedOperationsView("builtin-overdue-high-dollar", "Overdue High-Dollar AR", {
      queueFilter: "highDollar",
      slicers: { claimDue: ["PAST DUE"] },
      minBalance: "1000"
    }, true),
    savedOperationsView("builtin-payer-no-response", "No Payer Response", {
      queueFilter: "missingPayerResponse"
    }, true),
    savedOperationsView("builtin-denials", "Denials And Appeals", {
      queueFilter: "denied"
    }, true),
    savedOperationsView("builtin-secondary", "Secondary Follow-Up", {
      queueFilter: "waitingSecondary"
    }, true),
    savedOperationsView("builtin-client-action", "Client / Provider Action", {
      queueFilter: "clientAction"
    }, true)
  ];
}

function savedOperationsView(id, label, patch = {}, builtIn = false) {
  return {
    id,
    label,
    builtIn,
    createdAt: builtIn ? "system" : timestamp(),
    state: normalizeOperationsViewState({
      ...emptyOperationsViewState(),
      ...patch,
      slicers: {
        ...structuredClone(emptySlicers),
        ...(patch.slicers || {})
      }
    })
  };
}

function normalizeSavedOperationsViews(value) {
  const byId = new Map(defaultSavedOperationsViews().map((view) => [view.id, view]));
  if (Array.isArray(value)) {
    value.forEach((view) => {
      if (!view || typeof view !== "object" || !view.id || !view.label) return;
      byId.set(String(view.id), {
        id: String(view.id),
        label: String(view.label).slice(0, 80),
        builtIn: Boolean(view.builtIn),
        createdAt: view.createdAt || "",
        state: normalizeOperationsViewState(view.state)
      });
    });
  }
  return Array.from(byId.values());
}

// Exact Claim ID searches share one representation across requests and saved
// views: a legacy string for zero/one ID, or an array for multiple IDs.
function normalizeClaimIdSearchValues(value) {
  const seen = new Set();
  return (Array.isArray(value) ? value : [value])
    .map((id) => String(id == null ? "" : id).trim()).filter(Boolean)
    .filter((id) => {
      const key = id.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 10);
}

function claimIdSearchFilterValue(value) {
  const ids = normalizeClaimIdSearchValues(value);
  return ids.length > 1 ? ids : ids[0] || "";
}

function claimIdSearchInputs() {
  if (els.claimIdSearchList) return Array.from(els.claimIdSearchList.querySelectorAll("[data-claim-id-search]"));
  return els.claimIdSearchInput ? [els.claimIdSearchInput] : [];
}

function collectClaimIdSearchValue() {
  return claimIdSearchFilterValue(claimIdSearchInputs().map((input) => input.value));
}

function appendClaimIdSearchInput(value = "") {
  if (!els.claimIdSearchList || claimIdSearchInputs().length >= 10) return;
  const row = document.createElement("div");
  row.className = "claim-id-search-row";
  const input = document.createElement("input");
  input.type = "search";
  input.setAttribute("data-claim-id-search", "");
  input.setAttribute("aria-describedby", "claimIdSearchHint");
  input.autocomplete = "off";
  input.value = value;
  const remove = document.createElement("button");
  remove.type = "button";
  remove.className = "claim-id-remove";
  remove.setAttribute("data-remove-claim-id", "");
  remove.textContent = "×";
  row.append(input, remove);
  els.claimIdSearchList.append(row);
}

function syncClaimIdSearchInputs() {
  if (!els.claimIdSearchList) return;
  let inputs = claimIdSearchInputs();
  // Keep the user's active input in place. Only unused trailing fields can be
  // removed; an empty field in the middle stays available for another ID.
  while (inputs.length > 1 && !inputs[inputs.length - 1].value.trim()
    && inputs[inputs.length - 1] !== document.activeElement
    && inputs.slice(0, -1).some((input) => !input.value.trim())) {
    inputs.pop().parentElement.remove();
  }
  if (inputs.length < 10 && inputs.every((input) => input.value.trim())) appendClaimIdSearchInput();
  inputs = claimIdSearchInputs();
  inputs.forEach((input, index) => {
    input.id = index === 0 ? "claimIdSearchInput" : "claimIdSearchInput" + (index + 1);
    input.placeholder = index === 0 ? "Claim ID" : "Another Claim ID";
    input.setAttribute("aria-label", "Claim ID " + (index + 1));
    const remove = input.parentElement.querySelector("[data-remove-claim-id]");
    if (remove) {
      remove.hidden = !input.value.trim();
      remove.setAttribute("aria-label", "Remove Claim ID " + (index + 1));
    }
  });
  const populated = inputs.filter((input) => input.value.trim()).length;
  if (els.clearClaimIdsBtn) els.clearClaimIdsBtn.hidden = populated === 0;
  if (els.claimIdSearchHint) els.claimIdSearchHint.textContent = populated === 10 ? "10 of 10 claim IDs" : "Up to 10 claim IDs";
}

function setClaimIdSearchValue(value) {
  cancelClaimScreenshot();
  const ids = normalizeClaimIdSearchValues(value);
  if (els.claimIdSearchInput) els.claimIdSearchInput.value = ids[0] || "";
  if (!els.claimIdSearchList) return;
  claimIdSearchInputs().slice(1).forEach((input) => input.parentElement.remove());
  ids.slice(1).forEach((id) => appendClaimIdSearchInput(id));
  syncClaimIdSearchInputs();
}

// Only the existing exact-ID filter receives recognized IDs. Images, filenames,
// OCR text and recognition errors never enter telemetry, persistence or requests.
function claimScreenshotContextKey() {
  return JSON.stringify({
    inputs: claimIdSearchInputs().map((input) => input.value),
    filters: collectTrackerFilters(),
    slicers: state?.slicers,
    columnFilters: state?.columnFilters,
    access: trackerAccessEnvelope("service"),
    token: trackerApiToken(),
    view: state?.view
  });
}

function setClaimScreenshotStatus(message) {
  if (els.claimScreenshotStatus) els.claimScreenshotStatus.textContent = message;
}

function cancelClaimScreenshot({ announce = false } = {}) {
  claimScreenshotGeneration += 1;
  claimScreenshotActiveJob = null;
  claimScreenshotReviewContext = null;
  try {
    const canceled = window.TmsClaimScreenshot?.cancel?.();
    if (canceled && typeof canceled.catch === "function") canceled.catch(() => {});
  } catch (_) {}
  if (els.claimScreenshotReview) els.claimScreenshotReview.hidden = true;
  if (els.claimScreenshotReviewInput) els.claimScreenshotReviewInput.value = "";
  if (els.claimScreenshotReviewCount) els.claimScreenshotReviewCount.textContent = "";
  if (els.claimScreenshotCancelBtn) els.claimScreenshotCancelBtn.hidden = true;
  if (els.claimScreenshotFileInput) els.claimScreenshotFileInput.value = "";
  els.claimIdFilter?.classList?.remove("is-dragover");
  setClaimScreenshotStatus(announce ? "Screenshot canceled." : "");
}

function loadClaimScreenshotRuntime() {
  if (window.TmsClaimScreenshot?.recognize) return Promise.resolve(window.TmsClaimScreenshot);
  if (claimScreenshotRuntimePromise) return claimScreenshotRuntimePromise;
  claimScreenshotRuntimePromise = new Promise((resolve, reject) => {
    const appScript = Array.from(document.scripts || []).find((script) => {
      try { return new URL(script.src, document.baseURI).pathname.endsWith("/app.js"); } catch (_) { return false; }
    });
    const appUrl = new URL(appScript?.src || "./app.js", document.baseURI);
    const url = new URL("./assets/claimScreenshot.js", appUrl);
    if (url.origin !== window.location.origin) { reject(new Error("Screenshot reader unavailable")); return; }
    const version = appUrl.searchParams.get("v");
    if (version) url.searchParams.set("v", version);
    const script = document.createElement("script");
    script.src = url.href;
    script.async = true;
    script.onload = () => {
      if (window.TmsClaimScreenshot?.recognize) resolve(window.TmsClaimScreenshot);
      else { script.remove(); reject(new Error("Screenshot reader unavailable")); }
    };
    script.onerror = () => { script.remove(); reject(new Error("Screenshot reader unavailable")); };
    document.head.append(script);
  }).catch((error) => { claimScreenshotRuntimePromise = null; throw error; });
  return claimScreenshotRuntimePromise;
}

function uniqueClaimScreenshotIds(values) {
  const seen = new Set();
  return (Array.isArray(values) ? values : []).filter((value) => typeof value === "string" || typeof value === "number")
    .map((value) => String(value).trim()).filter((id) => {
      if (!id || seen.has(id.toLowerCase())) return false;
      seen.add(id.toLowerCase());
      return true;
    });
}

function claimScreenshotResultIds(result) {
  // The reader marks accepted IDs separately from uncertain row candidates.
  // Review never turns arbitrary row text or other numbers into a claim ID.
  return uniqueClaimScreenshotIds(result?.ids);
}

function claimScreenshotReviewValues() {
  return uniqueClaimScreenshotIds(String(els.claimScreenshotReviewInput?.value || "").split(/[\s,;]+/));
}

function updateClaimScreenshotReview() {
  const ids = claimScreenshotReviewValues();
  if (els.claimScreenshotReviewCount) els.claimScreenshotReviewCount.textContent = ids.length > 10
    ? ids.length + " claim IDs. Keep up to 10."
    : ids.length ? ids.length + " unique claim IDs (up to 10)." : "Enter at least one claim ID.";
  if (els.claimScreenshotApplyBtn) els.claimScreenshotApplyBtn.disabled = ids.length === 0 || ids.length > 10;
}

function applyClaimScreenshotIds(ids, applyFilters, duplicateCount = 0) {
  // Check before the legacy setter, which intentionally bounds ordinary typed
  // saved views to ten. Screenshot overflow must always be reviewed in full.
  if (!ids.length || ids.length > 10) return false;
  setClaimIdSearchValue(ids);
  applyFilters();
  const duplicates = Math.max(0, Math.floor(Number(duplicateCount) || 0));
  setClaimScreenshotStatus("Searching " + ids.length + " claim ID" + (ids.length === 1 ? "." : "s.")
    + (duplicates ? " " + duplicates + " duplicate" + (duplicates === 1 ? " removed." : "s removed.") : ""));
  return true;
}

async function startClaimScreenshot(file, applyFilters) {
  cancelClaimScreenshot();
  if (!file || state?.view !== "workbench" || !canAccessView("workbench")) return;
  const job = { generation: claimScreenshotGeneration, context: claimScreenshotContextKey() };
  claimScreenshotActiveJob = job;
  const current = () => claimScreenshotActiveJob === job && job.generation === claimScreenshotGeneration
    && job.context === claimScreenshotContextKey();
  if (els.claimScreenshotCancelBtn) els.claimScreenshotCancelBtn.hidden = false;
  setClaimScreenshotStatus("Reading screenshot…");
  try {
    const reader = await loadClaimScreenshotRuntime();
    if (!current()) return;
    let lastProgress = -1;
    const result = await reader.recognize(file, { onProgress: (event) => {
      if (!current()) return;
      const progress = typeof event === "number" ? event : event?.progress;
      const percent = Number.isFinite(progress) ? Math.round(Math.max(0, Math.min(1, progress)) * 10) * 10 : -1;
      if (percent >= 0 && percent !== lastProgress) {
        lastProgress = percent;
        setClaimScreenshotStatus("Reading claim IDs… " + percent + "%");
      }
    } });
    if (!current()) return;
    const ids = claimScreenshotResultIds(result);
    if (!ids.length) {
      cancelClaimScreenshot();
      setClaimScreenshotStatus("Could not find clear claim IDs. Crop the Claim ID column with its heading and try again.");
      return;
    }
    if (result.needsReview || ids.length > 10) {
      claimScreenshotActiveJob = null;
      claimScreenshotReviewContext = { generation: job.generation, context: job.context, applyFilters, duplicateCount: result.duplicateCount };
      els.claimScreenshotReviewInput.value = ids.join("\n");
      els.claimScreenshotReview.hidden = false;
      updateClaimScreenshotReview();
      setClaimScreenshotStatus(ids.length > 10 ? "Choose up to 10 claim IDs below." : "Some rows need a closer look. Check and complete the claim IDs below.");
      els.claimScreenshotReviewInput.focus();
      return;
    }
    applyClaimScreenshotIds(ids, applyFilters, result.duplicateCount);
  } catch (error) {
    if (!current()) return;
    const code = String(error?.code || "");
    cancelClaimScreenshot();
    const messages = {
      "unsupported-image": "Choose a PNG, JPG or WebP screenshot.",
      "reload-required": "The screenshot reader could not start. Refresh the portal, then try again.",
      "image-too-large": "This image is too large. Crop the Claim ID column and try again.",
      "image-too-small": "This image is too small to read. Try a larger screenshot.",
      "cancelled": "Screenshot canceled."
    };
    setClaimScreenshotStatus(messages[code] || "Could not read this screenshot. Try a clearer crop of the Claim ID column with its heading.");
  } finally {
    // A changed filter/scope can make a job stale without a DOM input event.
    // Retire only that job, never a newer screenshot or its review panel.
    if (claimScreenshotActiveJob === job && !current()) cancelClaimScreenshot();
  }
}

function bindClaimScreenshotInputs(applyFilters) {
  if (!els.claimIdFilter || !els.claimScreenshotBtn) return;
  const chooseFiles = (files) => {
    const items = Array.from(files || []);
    if (items.length !== 1) {
      cancelClaimScreenshot();
      setClaimScreenshotStatus("Choose one screenshot at a time.");
      return;
    }
    void startClaimScreenshot(items[0], applyFilters);
  };
  const warm = () => { loadClaimScreenshotRuntime().then((reader) => reader.warmup()).catch(() => {}); };
  els.claimScreenshotBtn.addEventListener("pointerenter", warm);
  els.claimScreenshotBtn.addEventListener("focus", warm);
  els.claimScreenshotBtn.addEventListener("click", () => els.claimScreenshotFileInput?.click());
  els.claimScreenshotFileInput?.addEventListener("change", (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length) chooseFiles(files);
  });
  els.claimScreenshotCancelBtn?.addEventListener("click", () => cancelClaimScreenshot({ announce: true }));
  els.claimScreenshotReviewInput?.addEventListener("input", updateClaimScreenshotReview);
  els.claimScreenshotApplyBtn?.addEventListener("click", () => {
    const review = claimScreenshotReviewContext;
    if (!review) return;
    if (review.generation !== claimScreenshotGeneration || review.context !== claimScreenshotContextKey()) {
      cancelClaimScreenshot();
      setClaimScreenshotStatus("Claim filters changed. Choose the screenshot again.");
      return;
    }
    applyClaimScreenshotIds(claimScreenshotReviewValues(), review.applyFilters, review.duplicateCount);
  });
  els.claimIdFilter.addEventListener("paste", (event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const images = items.filter((item) => item.kind === "file" && String(item.type).startsWith("image/"))
      .map((item) => item.getAsFile()).filter(Boolean);
    if (!images.length) images.push(...Array.from(event.clipboardData?.files || []).filter((file) => String(file.type).startsWith("image/")));
    if (!images.length) return; // Ordinary text keeps the browser's native paste behavior.
    event.preventDefault();
    event.stopPropagation();
    chooseFiles(images);
  });
  const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes("Files");
  els.claimIdFilter.addEventListener("dragover", (event) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    els.claimIdFilter.classList.add("is-dragover");
  });
  els.claimIdFilter.addEventListener("dragleave", (event) => {
    if (!els.claimIdFilter.contains(event.relatedTarget)) els.claimIdFilter.classList.remove("is-dragover");
  });
  els.claimIdFilter.addEventListener("drop", (event) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    els.claimIdFilter.classList.remove("is-dragover");
    chooseFiles(event.dataTransfer.files);
  });
}

function emptyOperationsViewState() {
  return {
    queueFilter: "all",
    claimIdSearch: "",
    serviceIdSearch: "",
    search: "",
    noteSearch: "",
    tenant: "All",
    provider: "All",
    primary: "All",
    secondary: "All",
    action: "All",
    match: "All",
    claimStatus: "All",
    lifecycle: "All",
    aging: "All",
    network: "All",
    owner: "All",
    minBalance: "",
    slicers: structuredClone(emptySlicers),
    columnFilters: structuredClone(emptyColumnFilters),
    slicerModes: structuredClone(emptyFilterModes),
    columnFilterModes: structuredClone(emptyFilterModes),
    columnFilterRanges: structuredClone(emptyColumnFilterRanges)
  };
}

function captureOperationsViewState() {
  return normalizeOperationsViewState({
    queueFilter: state.queueFilter || "all",
    claimIdSearch: collectClaimIdSearchValue(),
    serviceIdSearch: els.serviceIdSearchInput?.value || "",
    search: els.searchInput?.value || "",
    noteSearch: els.noteSearchInput?.value || "",
    tenant: els.tenantFilter?.value || "All",
    provider: els.providerFilter?.value || "All",
    primary: els.primaryFilter?.value || "All",
    secondary: els.secondaryFilter?.value || "All",
    action: els.actionFilter?.value || "All",
    match: els.matchFilter?.value || "All",
    claimStatus: els.claimStatusFilter?.value || "All",
    lifecycle: els.lifecycleFilter?.value || "All",
    aging: els.agingFilter?.value || "All",
    owner: els.ownerFilter?.value || "All",
    fromDos: trackerServiceDateRange().fromDos || "",
    toDos: trackerServiceDateRange().toDos || "",
    minBalance: els.minBalanceFilter?.value || "",
    slicers: ensureSlicerState(state.slicers),
    columnFilters: ensureColumnFilterState(state.columnFilters),
    slicerModes: slicerModeState(),
    columnFilterModes: columnFilterModeState(),
    columnFilterRanges: columnFilterRangeState()
  });
}

// A saved AR view stores the Match dropdown as ONE value, which cannot express
// "either rate bucket" after the No Rate Found split. Rather than silently picking
// one half (or leaving a dead option that selects nothing), the legacy value is
// lifted into the multi-select match slicer — same rows, no loss — and the
// dropdown is released back to "All".
function migrateOperationsViewMatchFilter(value = {}) {
  if (!matchValueIsLegacyCollapsedNoRate(value.match)) return value;
  const slicers = value.slicers && typeof value.slicers === "object" ? { ...value.slicers } : {};
  const existing = Array.isArray(slicers.match) ? slicers.match : [];
  slicers.match = Array.from(new Set([...existing, ...NO_RATE_MATCH_VALUES]));
  return { ...value, match: "All", slicers };
}

// A saved AR view stores the Aging dropdown as ONE value, matched by exact string equality both
// locally (`claim.aging === filters.aging`) and on the server (`filters.aging`). The feeds disagree
// on the vocabulary, so the stored label is canonicalized onto its CT band key here — one spelling
// in storage — and applyOperationsViewState then resolves that band back onto whichever spelling the
// live option list offers. Canonicalizing ALONE would be a regression (the CT key is not an option
// under the mirror or the demo snapshot); the two halves together hold under every live vocabulary
// and under the documented rollback.
// Behaviour preserved exactly: "All", empty/nullish (-> "All") and genuinely unknown text are
// returned as they are today. An unrecognized value must NOT collapse to "All" — that is the very
// fail-open this change exists to close.
function migrateOperationsViewAgingFilter(value) {
  const stored = String(value || "All");
  return normalizeAgingBucketLabel(stored) || stored;
}

function normalizeOperationsViewState(rawValue = {}) {
  const value = migrateOperationsViewMatchFilter(rawValue || {});
  const base = emptyOperationsViewState();
  return {
    queueFilter: queueDefinitions.some((queue) => queue.key === value.queueFilter) ? value.queueFilter : base.queueFilter,
    claimIdSearch: claimIdSearchFilterValue(value.claimIdSearch),
    serviceIdSearch: String(value.serviceIdSearch || ""),
    search: String(value.search || ""),
    noteSearch: String(value.noteSearch || ""),
    tenant: String(value.tenant || "All"),
    provider: String(value.provider || "All"),
    primary: String(value.primary || "All"),
    secondary: String(value.secondary || "All"),
    action: String(value.action || "All"),
    match: String(value.match || "All"),
    claimStatus: String(value.claimStatus || "All"),
    lifecycle: String(value.lifecycle || "All"),
    aging: migrateOperationsViewAgingFilter(value.aging),
    network: String(value.network || "All"),
    owner: String(value.owner || "All"),
    fromDos: String(value.fromDos || ""),
    toDos: String(value.toDos || ""),
    minBalance: value.minBalance == null ? "" : String(value.minBalance),
    slicers: ensureSlicerState(value.slicers),
    columnFilters: migrateColumnFilters(ensureColumnFilterState(value.columnFilters)),
    slicerModes: ensureFilterModeState(value.slicerModes, ensureSlicerState(value.slicers)),
    columnFilterModes: ensureFilterModeState(value.columnFilterModes, migrateColumnFilters(ensureColumnFilterState(value.columnFilters))),
    columnFilterRanges: ensureColumnFilterRangeState(value.columnFilterRanges)
  };
}

function applySavedOperationsView(viewId) {
  const view = normalizeSavedOperationsViews(state.savedOperationsViews).find((item) => item.id === viewId);
  if (!view) return;
  applyOperationsViewState(view.state);
  resetTrackerPage();
  saveState();
  renderSlicers();
  renderClaimsTable();
  showToast(`View: ${view.label}`);
}

function applyOperationsViewState(viewState) {
  const next = normalizeOperationsViewState(viewState);
  const fromInput = document.getElementById("trackerServiceDateFrom"), toInput = document.getElementById("trackerServiceDateTo");
  if (fromInput) fromInput.value = trackerServiceDateDisplay(next.fromDos);
  if (toInput) toInput.value = trackerServiceDateDisplay(next.toDos);
  state.queueFilter = next.queueFilter;
  setClaimIdSearchValue(next.claimIdSearch);
  if (els.serviceIdSearchInput) els.serviceIdSearchInput.value = next.serviceIdSearch;
  if (els.searchInput) els.searchInput.value = next.search;
  if (els.noteSearchInput) els.noteSearchInput.value = next.noteSearch;
  if (els.tenantFilter) els.tenantFilter.value = next.tenant;
  if (els.providerFilter) els.providerFilter.value = next.provider;
  if (els.primaryFilter) els.primaryFilter.value = next.primary;
  if (els.secondaryFilter) els.secondaryFilter.value = next.secondary;
  if (els.actionFilter) els.actionFilter.value = next.action;
  if (els.matchFilter) els.matchFilter.value = next.match;
  if (els.claimStatusFilter) els.claimStatusFilter.value = next.claimStatus;
  if (els.lifecycleFilter) els.lifecycleFilter.value = next.lifecycle;
  if (els.agingFilter) els.agingFilter.value = agingFilterValueForOptions(els.agingFilter, next.aging);
  if (els.ownerFilter) els.ownerFilter.value = next.owner;
  if (els.minBalanceFilter) els.minBalanceFilter.value = next.minBalance;
  state.slicers = ensureSlicerState(next.slicers);
  state.columnFilters = ensureColumnFilterState(next.columnFilters);
  state.slicerModes = ensureFilterModeState(next.slicerModes, state.slicers);
  state.columnFilterModes = ensureFilterModeState(next.columnFilterModes, state.columnFilters);
  state.columnFilterRanges = ensureColumnFilterRangeState(next.columnFilterRanges);
  state.selectedClaims = [];
}

function saveCurrentOperationsView() {
  const viewState = captureOperationsViewState();
  const label = generatedOperationsViewLabel(viewState);
  const id = `custom-${Date.now()}`;
  state.savedOperationsViews = normalizeSavedOperationsViews([
    ...(state.savedOperationsViews || []),
    savedOperationsView(id, label, viewState, false)
  ]).slice(-20);
  saveState();
  renderClaimsTable();
  showToast(`Saved view: ${label}`);
}

function deleteSavedOperationsView(viewId) {
  state.savedOperationsViews = normalizeSavedOperationsViews(state.savedOperationsViews).filter((view) => view.builtIn || view.id !== viewId);
  saveState();
  renderClaimsTable();
  showToast("Saved view removed.");
}

function generatedOperationsViewLabel(viewState) {
  const parts = [];
  if (viewState.queueFilter && viewState.queueFilter !== "all") parts.push(queueLabel(viewState.queueFilter));
  if (viewState.primary && viewState.primary !== "All") parts.push(viewState.primary);
  if (viewState.aging && viewState.aging !== "All") parts.push(`${viewState.aging} aging`);
  if (viewState.minBalance) parts.push(`$${viewState.minBalance}+`);
  return parts.slice(0, 3).join(" / ") || `Custom AR View ${new Date().toISOString().slice(5, 10)}`;
}

function savedOperationsViewSummary(viewState = {}) {
  const stateValue = normalizeOperationsViewState(viewState);
  const summary = [
    stateValue.tenant !== "All" ? stateValue.tenant : "",
    stateValue.primary !== "All" ? stateValue.primary : "",
    stateValue.aging !== "All" ? `${stateValue.aging} aging` : "",
    stateValue.lifecycle !== "All" ? stateValue.lifecycle : "",
    stateValue.minBalance ? `$${stateValue.minBalance}+` : "",
    ...Object.entries(stateValue.slicers)
      .filter(([, values]) => values.length)
      .map(([key, values]) => `${slicerLabel(key)}: ${values.slice(0, 2).map((value) => key === "action" ? slicerDisplayValue(key, value) : value).join(", ")}`)
  ].filter(Boolean);
  return summary.length ? summary.slice(0, 3).join(" · ") : "One-click operational filter";
}

function sameOperationsView(a, b) {
  return stableOperationsViewKey(a) === stableOperationsViewKey(b);
}

function stableOperationsViewKey(value) {
  const stateValue = normalizeOperationsViewState(value);
  return JSON.stringify(stateValue);
}

function renderOperationsIntelligence(filtered = [], pageWindow = trackerPageWindow(filtered), mode = state.rowMode || "service", sharedClaims) {
  if (!els.operationsIntelligence) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const visibleClaims = visibleClaimsFromPage(pageWindow, mode);
  const openClaims = openArClaims(claims);
  const recommendations = billingOperationsRecommendations(openClaims, visibleClaims);
  const headline = recommendations[0];
  els.operationsIntelligence.innerHTML = `
    <section class="ops-command-lead ops-command-lead--${escapeHtml(headline?.tone || "blue")}">
      <span>Billing Operations Intelligence</span>
      <strong>${escapeHtml(headline?.title || "No open AR in current filter")}</strong>
      <p>${escapeHtml(headline?.detail || "Adjust filters or clear the queue to review broader AR.")}</p>
      ${headline?.action ? operationsActionButton(headline.action) : ""}
    </section>
    <div class="ops-command-grid">
      ${recommendations.slice(1).map(renderOperationsRecommendation).join("")}
    </div>
  `;
  Array.from(els.operationsIntelligence.querySelectorAll("[data-ops-focus]")).forEach((button) => {
    button.addEventListener("click", () => applyBillerFocus(button.dataset.opsFocus));
  });
  Array.from(els.operationsIntelligence.querySelectorAll("[data-ops-queue]")).forEach((button) => {
    button.addEventListener("click", () => setQueueFilter(button.dataset.opsQueue));
  });
}

function renderRcmExceptionCommand(filtered = [], mode = state.rowMode || "service") {
  if (!els.rcmExceptionCommand) return;
  const claims = claimsFromTrackerRows(filtered, mode);
  const model = rcmExceptionCommandModel(claims);
  els.rcmExceptionCommand.innerHTML = `
    <section class="rcm-exception-shell" aria-label="RCM exception command">
      <div class="rcm-exception-head">
        <div>
          <span>RCM Exception Command</span>
          <strong>${formatNounCount(model.totals.exceptionRows, "exception")} · ${money(model.totals.exposure)} exposed</strong>
          <p>Excel-clean exception queue across assignment, review, client proof, timely filing, expected reimbursement, payer sequence, payment posting, and appeals.</p>
        </div>
        <div class="rcm-exception-proof">
          <span>Filtered Formula</span>
          <strong>${money(model.totals.delta)}</strong>
          <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
        </div>
      </div>
      <div class="rcm-exception-summary">
        <div><span>Affected Claims</span><strong>${model.totals.affectedClaims}</strong><small>${formatNounCount(model.totals.totalClaims, "filtered claim")}</small></div>
        <div><span>Owner Gaps</span><strong>${model.totals.ownerGaps}</strong><small>Needs assignment or manager decision</small></div>
        <div><span>Client / Proof Gaps</span><strong>${model.totals.proofGaps}</strong><small>Auth, COB, records, ERA, rate</small></div>
        <div><span>Deadline Risk</span><strong>${model.totals.deadlineRisk}</strong><small>Expired or due within 14 days</small></div>
      </div>
      <div class="rcm-exception-table">
        <div class="rcm-exception-row rcm-exception-header">
          <span>Exception / Claim</span>
          <span>Assigned Toward</span>
          <span>Review Owner</span>
          <span>Exposure / Delta</span>
          <span>Proof Needed</span>
          <span>Next Action</span>
        </div>
        ${model.rows.length ? model.rows.slice(0, 10).map(renderRcmExceptionRow).join("") : '<div class="rcm-exception-empty">No active exceptions in the filtered AR scope.</div>'}
      </div>
    </section>
  `;
}

function rcmExceptionCommandModel(claims = []) {
  const safeClaims = Array.isArray(claims) ? claims : [];
  const rows = safeClaims.flatMap((claim) => rcmExceptionRowsForClaim(claim));
  const affectedClaims = new Set(rows.map((row) => row.claimId));
  const claimTotals = safeClaims.reduce((sum, claim) => {
    const financial = claimFinancialView(claim);
    sum.expected += financial.expectedReimbursement;
    sum.paid += financial.totalInsurancePaid;
    sum.outstanding += financial.sourceOutstanding;
    sum.delta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    return sum;
  }, { expected: 0, paid: 0, outstanding: 0, delta: 0 });
  const totals = rows.reduce((sum, row) => {
    sum.exposure += row.exposure;
    if (row.ownerGap) sum.ownerGaps += 1;
    if (row.proofGap) sum.proofGaps += 1;
    if (row.deadlineRisk) sum.deadlineRisk += 1;
    return sum;
  }, { exposure: 0, ownerGaps: 0, proofGaps: 0, deadlineRisk: 0 });
  return {
    rows: rows.sort((a, b) => rcmExceptionRank(a) - rcmExceptionRank(b)
      || b.exposure - a.exposure
      || Math.abs(b.delta) - Math.abs(a.delta)
      || a.claimId.localeCompare(b.claimId)),
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      expected: moneyNumber(claimTotals.expected),
      paid: moneyNumber(claimTotals.paid),
      outstanding: moneyNumber(claimTotals.outstanding),
      delta: moneyNumber(claimTotals.delta),
      exceptionRows: rows.length,
      affectedClaims: affectedClaims.size,
      totalClaims: safeClaims.length
    }
  };
}

function rcmExceptionRowsForClaim(claim = {}) {
  const financial = claimFinancialView(claim);
  const guards = evaluateClaimCalculationGuards(claim);
  const filing = timelyFilingRiskRow(claim);
  const submission = claimSubmissionReadinessRow(claim);
  const route = assignmentRouteModel(claim, { mode: claim.assignmentStatus === "Submitted For Review" ? "review" : "work" });
  const delta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
  const base = {
    claimId: claim.claimId || "",
    client: claim.client || "Unknown client",
    payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
    assignedToward: route.assignedTo || "Unassigned",
    reviewOwner: route.decisionOwner || "Manager / Admin",
    exposure: financial.sourceOutstanding,
    delta
  };
  const rows = [];
  if (financial.sourceOutstanding > 0 && !claim.assignedTo) {
    rows.push(rcmExceptionRow(base, "Owner Gap", "Operations", "Assign claim owner and due date", "Assign the highest priority owner before this AR sits untouched.", { ownerGap: true, tone: "red" }));
  }
  if (claim.assignmentStatus === "Submitted For Review") {
    rows.push(rcmExceptionRow(base, "Manager Review", "Manager / Admin", "Completion note, next action, and evidence packet", "Approve, bounce back, or hold with a documented manager decision.", { ownerGap: true, tone: "amber" }));
  }
  if (needsClientAction(claim) || hasClientAuthorizationSignal(claim) || hasClientDocumentationSignal(claim)) {
    rows.push(rcmExceptionRow(base, "Client Proof", "Client / Account Manager", clientOpenNeed(claim), "Request the exact missing client proof and keep it out of clean submission.", { proofGap: true, tone: "amber" }));
  }
  if (filing && ["Expired", "0-14 Days"].includes(filing.status)) {
    rows.push(rcmExceptionRow(base, "Timely Filing", "RCM Lead", filing.deadlineLabel || filing.status, filing.nextAction, { deadlineRisk: true, tone: "red" }));
  }
  if (guards.length || Math.abs(delta) > 0) {
    rows.push(rcmExceptionRow(base, "Formula / Setup", "Billing Setup", calculationGuardSummary(guards) || "Expected - Paid - AR delta", "Fix rate, PR/adjustment detail, or source math before the row is certified.", { proofGap: true, tone: guards.length ? "red" : "amber" }));
  }
  if (sameExternalPayerConflict(claim) || financial.unmatchedPaid > 0 || guards.some((guard) => guard.code === "PAYER_SEQUENCE_REVIEW")) {
    rows.push(rcmExceptionRow(base, "Payer Sequence", "Billing Review", paymentAttributionExplanation(claim), "Resolve primary/secondary attribution before secondary billing, closeout, or client reporting.", { proofGap: true, tone: "red" }));
  }
  if (needsPaymentReview(claim)) {
    rows.push(rcmExceptionRow(base, "Payment / ERA", "Cash Posting", rcmPaymentExceptionProof(claim), "Tie payment, ERA/EOB, PR, overpayment, or underpayment evidence to the service line.", { proofGap: true, tone: "blue" }));
  }
  if (isDeniedClaim(claim) || submission.laneKey !== "ready") {
    rows.push(rcmExceptionRow(base, isDeniedClaim(claim) ? "Denial / Appeal" : "Submission Hold", claimSubmissionLaneOwner(submission.laneKey), submission.guardSummary || submission.deadlineStatus || "Submission readiness hold", submission.nextAction, { proofGap: submission.laneKey !== "ready", deadlineRisk: submission.laneKey === "deadline", tone: isDeniedClaim(claim) || submission.laneKey === "deadline" ? "red" : "amber" }));
  }
  return dedupeRcmExceptionRows(rows);
}

function rcmExceptionRow(base, exception, ownerLane, proofNeeded, nextAction, options = {}) {
  return {
    ...base,
    exception,
    ownerLane,
    proofNeeded: proofNeeded || "Operational proof required",
    nextAction: nextAction || "Review and document next action.",
    ownerGap: Boolean(options.ownerGap),
    proofGap: Boolean(options.proofGap),
    deadlineRisk: Boolean(options.deadlineRisk),
    tone: options.tone || "blue"
  };
}

function dedupeRcmExceptionRows(rows = []) {
  const seen = new Set();
  return rows.filter((row) => {
    const key = `${row.claimId}|${row.exception}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function rcmExceptionRank(row = {}) {
  if (row.deadlineRisk) return 0;
  if (row.ownerGap) return 1;
  if (row.exception === "Formula / Setup" || row.exception === "Payer Sequence") return 2;
  if (row.exception === "Denial / Appeal") return 3;
  if (row.proofGap) return 4;
  return 9;
}

function rcmPaymentExceptionProof(claim = {}) {
  const financial = claimFinancialView(claim);
  if (financial.underpayment > 0) return `${money(financial.underpayment)} underpayment recovery`;
  if (financial.overpayment > 0) return `${money(financial.overpayment)} overpayment/credit review`;
  if (financial.unappliedPayments > 0) return `${money(financial.unappliedPayments)} unapplied payment`;
  if (financial.paymentVariance !== 0) return `${money(financial.paymentVariance)} payment variance`;
  return paymentAttributionExplanation(claim);
}

function claimSubmissionLaneOwner(laneKey) {
  return {
    ready: "Billing",
    client: "Client / Account Manager",
    setup: "Billing Setup",
    sequence: "Billing Review",
    deadline: "RCM Lead",
    followup: "Biller"
  }[laneKey] || "Operations";
}

function renderRcmExceptionRow(row) {
  return `
    <button type="button" class="rcm-exception-row rcm-exception-row--${escapeHtml(row.tone)}" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Exception / Claim">
        ${pill(row.exception, row.tone)}
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.client)} · ${escapeHtml(row.payer)}</small>
      </span>
      <span data-label="Assigned Toward"><strong>${escapeHtml(row.assignedToward)}</strong><small>${escapeHtml(row.ownerLane)}</small></span>
      <span data-label="Review Owner"><strong>${escapeHtml(row.reviewOwner)}</strong><small>${row.ownerGap ? "Owner decision needed" : "Route clear"}</small></span>
      <span data-label="Exposure / Delta"><strong>${money(row.exposure)}</strong><small>Delta ${money(row.delta)}</small></span>
      <span data-label="Proof Needed">${escapeHtml(row.proofNeeded)}</span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function renderEligibilityBenefitsCommand(filtered = [], mode = state.rowMode || "service") {
  if (!els.eligibilityBenefitsCommand) return;
  const claims = claimsFromTrackerRows(filtered, mode);
  const model = eligibilityBenefitsModel(claims);
  els.eligibilityBenefitsCommand.innerHTML = `
    <section class="eligibility-benefits-shell" aria-label="Eligibility and benefits verification">
      <div class="eligibility-benefits-head">
        <div>
          <span>Eligibility & Benefits Verification</span>
          <strong>${formatNounCount(model.totals.alerts, "verification alert")} · ${money(model.totals.benefitRisk)} benefit-risk AR</strong>
          <p>Front-door RCM control for active coverage, COB/secondary rules, authorization/benefit limits, PR evidence, payer policy, and Expected - Paid - AR proof.</p>
        </div>
        <div class="eligibility-benefits-proof">
          <span>Benefits Formula</span>
          <strong>${money(model.totals.delta)}</strong>
          <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
        </div>
      </div>
      <div class="eligibility-benefits-summary">
        <div><span>Coverage / COB</span><strong>${model.totals.coverageCob}</strong><small>Eligibility, benefit, secondary, or coordination checks</small></div>
        <div><span>Auth / Limits</span><strong>${model.totals.authLimits}</strong><small>Authorization, unit, or payer rule risk</small></div>
        <div><span>PR Exposure</span><strong>${money(model.totals.patientResponsibility)}</strong><small>Only PR 1/2/3 counts for patient responsibility</small></div>
        <div><span>Verification Rate</span><strong>${model.totals.verifiedRate}%</strong><small>${formatNounCount(model.totals.verified, "verified or monitor claim")}</small></div>
      </div>
      <div class="eligibility-benefits-list">
        <div class="eligibility-benefits-row eligibility-benefits-header">
          <span>Claim / Benefit Status</span>
          <span>Payer / State</span>
          <span>Verification Owner</span>
          <span>Benefit Risk</span>
          <span>Proof Needed</span>
          <span>Next Action</span>
        </div>
        ${model.rows.length ? model.rows.slice(0, 10).map(renderEligibilityBenefitsRow).join("") : '<div class="eligibility-benefits-empty">No eligibility or benefits rows in the active filter.</div>'}
      </div>
    </section>
  `;
}

function eligibilityBenefitsModel(claims = []) {
  const rows = (Array.isArray(claims) ? claims : [])
    .map(eligibilityBenefitsRow)
    .sort((a, b) => eligibilityBenefitsRank(a.status) - eligibilityBenefitsRank(b.status)
      || b.benefitRisk - a.benefitRisk
      || Math.abs(b.delta) - Math.abs(a.delta)
      || a.claimId.localeCompare(b.claimId));
  const totals = rows.reduce((sum, row) => {
    sum.expected += row.expected;
    sum.paid += row.paid;
    sum.outstanding += row.outstanding;
    sum.delta += row.delta;
    sum.patientResponsibility += row.patientResponsibility;
    if (row.alert) {
      sum.alerts += 1;
      sum.benefitRisk += row.benefitRisk;
    } else {
      sum.verified += 1;
    }
    if (["Coverage Proof Needed", "COB / Secondary Verification"].includes(row.status)) sum.coverageCob += 1;
    if (["Auth / Benefit Limit", "Payer Rule Missing"].includes(row.status)) sum.authLimits += 1;
    return sum;
  }, { expected: 0, paid: 0, outstanding: 0, delta: 0, patientResponsibility: 0, benefitRisk: 0, alerts: 0, verified: 0, coverageCob: 0, authLimits: 0 });
  return {
    rows,
    totals: {
      ...totals,
      expected: moneyNumber(totals.expected),
      paid: moneyNumber(totals.paid),
      outstanding: moneyNumber(totals.outstanding),
      delta: moneyNumber(totals.delta),
      patientResponsibility: moneyNumber(totals.patientResponsibility),
      benefitRisk: moneyNumber(totals.benefitRisk),
      claims: rows.length,
      verifiedRate: rows.length ? Math.round((totals.verified / rows.length) * 100) : 100
    }
  };
}

function eligibilityBenefitsRow(claim = {}) {
  const financial = claimFinancialView(claim);
  const guards = evaluateClaimCalculationGuards(claim);
  const text = claimOperationalText(claim);
  const stateValue = clientStateValue(claim) || "State Not Tagged";
  const primary = claim.primaryPayer || "Missing primary payer";
  const secondary = claim.secondaryPayer || "";
  const coverageSignal = /eligib|benefit|coverage|inactive|terminated/.test(text);
  const cobSignal = /cob|coordination|primary eob|secondary/.test(text) || Boolean(secondary);
  const authSignal = hasClientAuthorizationSignal(claim) || /auth|authorization|unit limit|benefit limit/.test(text);
  const patientResponsibility = moneyNumber(financial.patientResponsibility || 0);
  const ezClaimPatientFlip = (claim.services || []).some((service) => String(service.responsibleParty || "").toLowerCase().includes("patient") && truePatientResponsibility(service) <= 0);
  const payerRuleMissing = !payerOperatingRule(primary);
  const missingExpected = guards.some((guard) => guard.code === "EXPECTED_REIMBURSEMENT_INCOMPLETE");
  let status = "Verified / Monitor";
  if (coverageSignal) status = "Coverage Proof Needed";
  else if (cobSignal) status = "COB / Secondary Verification";
  else if (authSignal || missingExpected) status = "Auth / Benefit Limit";
  else if (patientResponsibility > 0 || ezClaimPatientFlip) status = "PR / Patient Benefit";
  else if (payerRuleMissing && financial.sourceOutstanding > 0) status = "Payer Rule Missing";
  const alert = status !== "Verified / Monitor";
  return {
    claimId: claim.claimId || "",
    client: claim.client || "Unknown client",
    payer: [primary, secondary].filter(Boolean).join(" / "),
    state: stateValue,
    owner: eligibilityBenefitsOwner(status),
    status,
    tone: eligibilityBenefitsTone(status),
    expected: financial.expectedReimbursement,
    paid: financial.totalInsurancePaid,
    outstanding: financial.sourceOutstanding,
    delta: moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding),
    patientResponsibility,
    benefitRisk: alert ? financial.sourceOutstanding : 0,
    proofNeeded: eligibilityBenefitsProof(status, { claim, guards, patientResponsibility, ezClaimPatientFlip }),
    nextAction: eligibilityBenefitsNextAction(status),
    alert
  };
}

function eligibilityBenefitsOwner(status) {
  return {
    "Coverage Proof Needed": "Client / Intake",
    "COB / Secondary Verification": "Benefits / Billing Review",
    "Auth / Benefit Limit": "Authorization Team",
    "PR / Patient Benefit": "Payment Posting",
    "Payer Rule Missing": "Billing Setup",
    "Verified / Monitor": "Operations"
  }[status] || "Operations";
}

function eligibilityBenefitsProof(status, context = {}) {
  if (status === "Coverage Proof Needed") return "Active coverage or benefit verification proof";
  if (status === "COB / Secondary Verification") return "COB update, primary EOB, or secondary coverage proof";
  if (status === "Auth / Benefit Limit") return calculationGuardSummary(context.guards || []) || "Authorization, unit limit, or benefit-limit proof";
  if (status === "PR / Patient Benefit") {
    if (context.ezClaimPatientFlip && !context.patientResponsibility) return "EZClaim patient flip is not PR 1/2/3 evidence";
    return `${money(context.patientResponsibility || 0)} PR 1/2/3 evidence`;
  }
  if (status === "Payer Rule Missing") return "Payer filing/auth/contract rule needs setup";
  return "No active benefit blocker in filtered evidence";
}

function eligibilityBenefitsNextAction(status) {
  if (status === "Coverage Proof Needed") return "Verify active eligibility and benefits before clean submission or rebill.";
  if (status === "COB / Secondary Verification") return "Confirm coordination and secondary route before billing or follow-up.";
  if (status === "Auth / Benefit Limit") return "Validate authorization, units, and benefit limits against expected reimbursement.";
  if (status === "PR / Patient Benefit") return "Confirm PR 1/2/3 detail before patient statement or insurance closeout.";
  if (status === "Payer Rule Missing") return "Configure payer policy/rate evidence before relying on automated status.";
  return "Keep in normal monitoring; no eligibility hold found in active evidence.";
}

function eligibilityBenefitsTone(status) {
  if (["Coverage Proof Needed", "Auth / Benefit Limit", "Payer Rule Missing"].includes(status)) return "red";
  if (["COB / Secondary Verification", "PR / Patient Benefit"].includes(status)) return "amber";
  return "green";
}

function eligibilityBenefitsRank(status) {
  return {
    "Coverage Proof Needed": 0,
    "Auth / Benefit Limit": 1,
    "COB / Secondary Verification": 2,
    "PR / Patient Benefit": 3,
    "Payer Rule Missing": 4,
    "Verified / Monitor": 9
  }[status] ?? 9;
}

function renderEligibilityBenefitsRow(row) {
  return `
    <button type="button" class="eligibility-benefits-row eligibility-benefits-row--${escapeHtml(row.tone)}" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Benefit Status"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.client)}</small>${pill(row.status, row.tone)}</span>
      <span data-label="Payer / State"><strong>${escapeHtml(row.payer)}</strong><small>${escapeHtml(row.state)}</small></span>
      <span data-label="Verification Owner"><strong>${escapeHtml(row.owner)}</strong><small>${row.alert ? "Verification hold" : "Monitor"}</small></span>
      <span data-label="Benefit Risk"><strong>${money(row.benefitRisk)}</strong><small>Delta ${money(row.delta)}</small></span>
      <span data-label="Proof Needed">${escapeHtml(row.proofNeeded)}</span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function renderRcmLifecycleCommand(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.rcmLifecycleCommand) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const model = rcmLifecycleCommandModel(claims);
  els.rcmLifecycleCommand.innerHTML = `
    <section class="rcm-lifecycle-head">
      <div>
        <span>RCM Lifecycle Command</span>
        <strong>${formatNounCount(model.totals.claims, "filtered claim")} · ${money(model.totals.outstanding)} open AR</strong>
        <p>Single control tower from pre-bill quality through payer follow-up, denial/appeal, payment review, client evidence, write-off review, and closed cash.</p>
      </div>
      <div class="rcm-lifecycle-proof">
        <span>Formula Proof</span>
        <strong>${money(model.totals.delta)}</strong>
        <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
      </div>
    </section>
    <div class="rcm-lifecycle-summary">
      <div>
        <span>Open Stages</span>
        <strong>${model.openStages}</strong>
        <small>${formatNounCount(model.totals.guarded, "guarded claim")}</small>
      </div>
      <div>
        <span>Client Blocked</span>
        <strong>${money(model.clientBlocked)}</strong>
        <small>Evidence, auth, COB, records</small>
      </div>
      <div>
        <span>Payer / Appeal AR</span>
        <strong>${money(model.payerAppealAr)}</strong>
        <small>Follow-up, denial, recovery</small>
      </div>
      <div>
        <span>Closed / Paid</span>
        <strong>${model.closedClaims}</strong>
        <small>${money(model.closedPaid)} paid in filtered scope</small>
      </div>
    </div>
    <div class="rcm-lifecycle-lanes">
      ${model.stages.map(renderRcmLifecycleStage).join("")}
    </div>
  `;
}

function rcmLifecycleCommandModel(claims = []) {
  const stages = rcmLifecycleStageDefinitions().map((definition) => ({
    ...definition,
    claims: [],
    expected: 0,
    paid: 0,
    outstanding: 0,
    delta: 0,
    guarded: 0
  }));
  claims.forEach((claim) => {
    const stage = stages.find((item) => item.key === rcmLifecycleStageKey(claim)) || stages.find((item) => item.key === "readyMonitor");
    const financial = claimFinancialView(claim);
    stage.claims.push(claim);
    stage.expected += financial.expectedReimbursement;
    stage.paid += financial.totalInsurancePaid;
    stage.outstanding += financial.sourceOutstanding;
    stage.delta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    if (evaluateClaimCalculationGuards(claim).length) stage.guarded += 1;
  });
  stages.forEach((stage) => {
    stage.expected = moneyNumber(stage.expected);
    stage.paid = moneyNumber(stage.paid);
    stage.outstanding = moneyNumber(stage.outstanding);
    stage.delta = moneyNumber(stage.delta);
    stage.count = stage.claims.length;
    stage.rows = stage.claims.slice().sort(sortClaims).slice(0, 3).map((claim) => {
      const financial = claimFinancialView(claim);
      return {
        claimId: claim.claimId,
        client: claim.client,
        payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
        reason: workReason(claim),
        amount: financial.sourceOutstanding
      };
    });
  });
  const totals = stages.reduce((sum, stage) => {
    sum.claims += stage.count;
    sum.expected += stage.expected;
    sum.paid += stage.paid;
    sum.outstanding += stage.outstanding;
    sum.delta += stage.delta;
    sum.guarded += stage.guarded;
    return sum;
  }, { claims: 0, expected: 0, paid: 0, outstanding: 0, delta: 0, guarded: 0 });
  Object.keys(totals).forEach((key) => {
    if (key !== "claims" && key !== "guarded") totals[key] = moneyNumber(totals[key]);
  });
  return {
    stages,
    totals,
    openStages: stages.filter((stage) => stage.count > 0 && stage.key !== "closedPaid").length,
    clientBlocked: sumStageOutstanding(stages, ["clientEvidence", "prebillScrub"]),
    payerAppealAr: sumStageOutstanding(stages, ["payerFollowUp", "denialAppeal", "paymentReview"]),
    closedClaims: stages.find((stage) => stage.key === "closedPaid")?.count || 0,
    closedPaid: stages.find((stage) => stage.key === "closedPaid")?.paid || 0
  };
}

function renderRcmLifecycleStage(stage) {
  const tone = stage.tone || "blue";
  return `
    <article class="rcm-lifecycle-stage rcm-lifecycle-stage--${escapeHtml(tone)}">
      <div class="rcm-lifecycle-stage-head">
        <div>
          <span>${escapeHtml(stage.owner)}</span>
          <strong>${escapeHtml(stage.label)}</strong>
        </div>
        ${pill(formatNounCount(stage.count, "claim"), tone)}
      </div>
      <div class="rcm-lifecycle-stage-math">
        <span><small>Expected</small><strong>${money(stage.expected)}</strong></span>
        <span><small>Paid</small><strong>${money(stage.paid)}</strong></span>
        <span><small>AR</small><strong>${money(stage.outstanding)}</strong></span>
        <span><small>Delta</small><strong>${money(stage.delta)}</strong></span>
      </div>
      <p>${escapeHtml(stage.nextAction)}</p>
      <div class="rcm-lifecycle-stage-list">
        ${stage.rows.length ? stage.rows.map((row) => `
          <button type="button" data-open="${escapeHtml(row.claimId)}">
            <span><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.client)} · ${escapeHtml(row.payer)}</small></span>
            <span><strong>${money(row.amount)}</strong><small>${escapeHtml(row.reason)}</small></span>
          </button>
        `).join("") : '<div>No claims in this stage for the active filters.</div>'}
      </div>
    </article>
  `;
}

function rcmLifecycleStageDefinitions() {
  return [
    { key: "prebillScrub", label: "Pre-Bill Scrub", owner: "RCM Quality", tone: "amber", nextAction: "Fix rate, authorization, payer sequence, or missing claim data before billing/rebilling." },
    { key: "clientEvidence", label: "Client Evidence", owner: "Client", tone: "amber", nextAction: "Request authorization, COB, records, coverage, or provider proof from the client." },
    { key: "payerFollowUp", label: "Payer Follow-Up", owner: "Biller", tone: "blue", nextAction: "Check payer portal/EOB/ERA, document response, and set a follow-up date." },
    { key: "denialAppeal", label: "Denial / Appeal", owner: "Appeals", tone: "red", nextAction: "Prepare appeal/corrected claim packet and validate evidence readiness." },
    { key: "paymentReview", label: "Payment / ERA Review", owner: "Cash Posting", tone: "blue", nextAction: "Resolve underpayment, overpayment, unapplied payment, or payer attribution exceptions." },
    { key: "writeOffReview", label: "Write-Off Review", owner: "Manager", tone: "red", nextAction: "Manager/admin must validate the exhausted recovery path and save a write-off review record." },
    { key: "readyMonitor", label: "Ready / Monitor", owner: "Operations", tone: "green", nextAction: "Claim is clean enough for normal queue monitoring or billing handoff." },
    { key: "closedPaid", label: "Closed / Paid", owner: "Cash", tone: "green", nextAction: "No open payer AR in the active tracker math." }
  ];
}

function rcmLifecycleStageKey(claim = {}) {
  const financial = claimFinancialView(claim);
  if (hasWriteOffWorkflow(claim)) return "writeOffReview";
  if (isDeniedClaim(claim)) return "denialAppeal";
  if (clientEvidenceRequestRows([claim]).length) return "clientEvidence";
  if (financial.sourceOutstanding <= 0 || claim.match === "Paid" || claim.match === "Over Paid") return "closedPaid";
  if (needsPaymentReview(claim)) return "paymentReview";
  if (needsPrebillScrub(claim)) return "prebillScrub";
  if (matchesQueue(claim, "payerFollowUp") || matchesQueue(claim, "missingPayerResponse") || matchesQueue(claim, "waitingPrimary") || matchesQueue(claim, "waitingSecondary")) return "payerFollowUp";
  return "readyMonitor";
}

function needsPrebillScrub(claim = {}) {
  const guards = evaluateClaimCalculationGuards(claim).map((guard) => guard.code);
  return guards.includes("EXPECTED_REIMBURSEMENT_INCOMPLETE")
    || guards.includes("PAYER_SEQUENCE_REVIEW")
    || guards.includes("PR_DETAIL_REVIEW")
    || (hasCalculationGuards(claim) && !isDeniedClaim(claim));
}

function needsPaymentReview(claim = {}) {
  const financial = claimFinancialView(claim);
  const attribution = String(claim.paymentAttribution?.status || "").toLowerCase();
  return financial.underpayment > 0
    || financial.overpayment > 0
    || financial.paymentVariance !== 0
    || attribution.includes("manual review")
    || sameExternalPayerConflict(claim);
}

function hasWriteOffWorkflow(claim = {}) {
  const workflow = state.workflowByClaim?.[claim.claimId] || {};
  return Boolean(workflow.writeOffSnapshot)
    || String(claim.action || "").toLowerCase().includes("write-off")
    || String(workflow.action || "").toLowerCase().includes("write-off");
}

function sumStageOutstanding(stages = [], keys = []) {
  return moneyNumber(stages
    .filter((stage) => keys.includes(stage.key))
    .reduce((sum, stage) => sum + stage.outstanding, 0));
}

function renderAuthorizationUtilizationCommand(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.authorizationUtilizationCommand) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const model = authorizationUtilizationModel(claims);
  els.authorizationUtilizationCommand.innerHTML = `
    <section class="authorization-utilization-head">
      <div>
        <span>Authorization Utilization Command</span>
        <strong>${formatNounCount(model.rows.length, "claim")} ${model.rows.length === 1 ? "needs" : "need"} auth or unit attention</strong>
        <p>Prevents auth denials by tying authorization status, service units, payer, due date, and expected-paid-AR math into one queue.</p>
      </div>
      <div class="authorization-utilization-proof">
        <span>Auth Math</span>
        <strong>${money(model.totals.delta)}</strong>
        <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
      </div>
    </section>
    <div class="authorization-utilization-summary">
      <div><span>Auth Exposure</span><strong>${money(model.totals.outstanding)}</strong><small>${formatNounCount(model.totals.claims, "claim")}</small></div>
      <div><span>Units At Risk</span><strong>${formatDecimal(model.totals.units, 2)}</strong><small>${formatNounCount(model.totals.serviceLines, "service line")}</small></div>
      <div><span>Denied / CO197</span><strong>${model.totals.denied}</strong><small>Authorization denial signals</small></div>
      <div><span>Missing Rate/Auth</span><strong>${model.totals.missing}</strong><small>Needs clean proof before billing</small></div>
    </div>
    <div class="authorization-utilization-list">
      <div class="authorization-utilization-row authorization-utilization-header">
        <span>Claim / Client</span>
        <span>Payer / State</span>
        <span>Units</span>
        <span>Exposure</span>
        <span>Status</span>
        <span>Next Action</span>
      </div>
      ${model.rows.length ? model.rows.slice(0, 8).map(renderAuthorizationUtilizationRow).join("") : '<div class="authorization-utilization-empty">No authorization or utilization blockers in the active filters.</div>'}
    </div>
  `;
}

function authorizationUtilizationModel(claims = []) {
  const rows = claims
    .map(authorizationUtilizationRow)
    .filter(Boolean)
    .sort((a, b) => authorizationStatusRank(a.status) - authorizationStatusRank(b.status)
      || b.exposure - a.exposure
      || b.units - a.units);
  const totals = rows.reduce((sum, row) => {
    sum.claims += 1;
    sum.serviceLines += row.serviceLines;
    sum.units += row.units;
    sum.expected += row.expected;
    sum.paid += row.paid;
    sum.outstanding += row.exposure;
    sum.delta += row.delta;
    if (row.denied) sum.denied += 1;
    if (row.missing) sum.missing += 1;
    return sum;
  }, { claims: 0, serviceLines: 0, units: 0, expected: 0, paid: 0, outstanding: 0, delta: 0, denied: 0, missing: 0 });
  totals.units = Number(totals.units.toFixed(2));
  totals.expected = moneyNumber(totals.expected);
  totals.paid = moneyNumber(totals.paid);
  totals.outstanding = moneyNumber(totals.outstanding);
  totals.delta = moneyNumber(totals.delta);
  return { rows, totals };
}

function authorizationUtilizationRow(claim = {}) {
  const services = Array.isArray(claim.services) ? claim.services : [];
  const text = claimOperationalText(claim);
  const authSignal = hasClientAuthorizationSignal(claim);
  const denied = /co.?197|authorization denial|auth denial|denied for auth|authorization denied/.test(text);
  const missing = /no rate|missing|authorization needed|auth needed|authorization missing|rate schedule missing/.test(text)
    || claim.claimStatus === "Authorization Needed"
    || isNoRateMatch(claim.match);
  const unitSignal = services.some((service) => quantityNumber(service.units) > 0);
  if (!authSignal && !denied && !missing && !unitSignal) return null;
  const financial = claimFinancialView(claim);
  if (financial.sourceOutstanding <= 0 && !denied && !missing && !authSignal) return null;
  const units = services.reduce((sum, service) => sum + quantityNumber(service.units), 0);
  const serviceLines = services.length;
  const status = authorizationUtilizationStatus(claim, { denied, missing, authSignal });
  return {
    claimId: claim.claimId || "",
    client: claim.client || "Unknown client",
    payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
    state: clientStateValue(claim) || "State Not Tagged",
    units: Number(units.toFixed(2)),
    serviceLines,
    expected: financial.expectedReimbursement,
    paid: financial.totalInsurancePaid,
    exposure: financial.sourceOutstanding,
    delta: moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding),
    denied,
    missing,
    status,
    nextAction: authorizationUtilizationNextAction(status)
  };
}

function renderAuthorizationUtilizationRow(row) {
  const tone = row.status === "Auth Denial" ? "red" : row.status === "Missing Auth/Rate" ? "amber" : row.status === "Client Proof Needed" ? "amber" : "blue";
  return `
    <button class="authorization-utilization-row" type="button" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Client"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.client)}</small></span>
      <span data-label="Payer / State"><strong>${escapeHtml(row.payer)}</strong><small>${escapeHtml(row.state)}</small></span>
      <span data-label="Units"><strong>${formatDecimal(row.units, 2)}</strong><small>${formatNounCount(row.serviceLines, "line")}</small></span>
      <span data-label="Exposure"><strong>${money(row.exposure)}</strong><small>Delta ${money(row.delta)}</small></span>
      <span data-label="Status">${pill(row.status, tone)}</span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function authorizationUtilizationStatus(claim, evidence) {
  if (evidence.denied) return "Auth Denial";
  if (evidence.missing) return "Missing Auth/Rate";
  if (claim.followUpDate && isPastDue(claim)) return "Past Due Proof";
  if (evidence.authSignal) return "Client Proof Needed";
  return "Unit Review";
}

function authorizationStatusRank(status) {
  return {
    "Auth Denial": 0,
    "Missing Auth/Rate": 1,
    "Past Due Proof": 2,
    "Client Proof Needed": 3,
    "Unit Review": 4
  }[status] ?? 9;
}

function authorizationUtilizationNextAction(status) {
  if (status === "Auth Denial") return "Build appeal/corrected-claim packet with authorization proof or payer denial evidence.";
  if (status === "Missing Auth/Rate") return "Load authorization or rate evidence before billing, rebilling, or closing AR.";
  if (status === "Past Due Proof") return "Escalate client proof request and document the exact authorization/coverage item needed.";
  if (status === "Client Proof Needed") return "Request authorization approval, coverage proof, COB, or provider credentialing support.";
  return "Validate service units against available authorization and expected reimbursement.";
}

function renderTimelyFilingCommand(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.timelyFilingCommand) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const model = timelyFilingRiskModel(claims);
  els.timelyFilingCommand.innerHTML = `
    <section class="timely-filing-head">
      <div>
        <span>Timely Filing Risk Command</span>
        <strong>${formatNounCount(model.rows.length, "deadline-sensitive claim")}</strong>
        <p>Protects AR from filing-window loss by pairing last DOS, payer policy placeholders, blockers, and expected-paid-AR math.</p>
      </div>
      <div class="timely-filing-proof">
        <span>Formula Proof</span>
        <strong>${money(model.totals.delta)}</strong>
        <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
      </div>
    </section>
    <div class="timely-filing-summary">
      <div><span>At Risk AR</span><strong>${money(model.totals.outstanding)}</strong><small>${formatNounCount(model.totals.claims, "claim")}</small></div>
      <div><span>Deadline Alerts</span><strong>${model.totals.alerts}</strong><small>Expired or under 30 days</small></div>
      <div><span>Expired</span><strong>${model.totals.expired}</strong><small>Needs appeal/proof path</small></div>
      <div><span>Policy Placeholder</span><strong>${model.totals.policyGaps}</strong><small>Configure payer filing rules</small></div>
    </div>
    <div class="timely-filing-list">
      <div class="timely-filing-row timely-filing-header">
        <span>Claim / Client</span>
        <span>Payer / State</span>
        <span>DOS Window</span>
        <span>Deadline</span>
        <span>AR / Delta</span>
        <span>Status</span>
        <span>Next Action</span>
      </div>
      ${model.rows.length ? model.rows.slice(0, 9).map(renderTimelyFilingRiskRow).join("") : '<div class="timely-filing-empty">No filing-window risk in the active filters.</div>'}
    </div>
  `;
}

function timelyFilingRiskModel(claims = []) {
  const rows = timelyFilingRiskRows(claims);
  const totals = rows.reduce((sum, row) => {
    sum.claims += 1;
    sum.expected += row.expected;
    sum.paid += row.paid;
    sum.outstanding += row.outstanding;
    sum.delta += row.delta;
    if (["Expired", "0-14 Days", "15-30 Days"].includes(row.status)) sum.alerts += 1;
    if (row.status === "Expired") sum.expired += 1;
    if (row.policyPlaceholder) sum.policyGaps += 1;
    if (row.status === "No DOS") sum.missingDos += 1;
    return sum;
  }, { claims: 0, expected: 0, paid: 0, outstanding: 0, delta: 0, alerts: 0, expired: 0, policyGaps: 0, missingDos: 0 });
  totals.expected = moneyNumber(totals.expected);
  totals.paid = moneyNumber(totals.paid);
  totals.outstanding = moneyNumber(totals.outstanding);
  totals.delta = moneyNumber(totals.delta);
  return { rows, totals };
}

function timelyFilingRiskRows(claims = []) {
  return (Array.isArray(claims) ? claims : [])
    .map(timelyFilingRiskRow)
    .filter(Boolean)
    .sort((a, b) => timelyFilingStatusRank(a.status) - timelyFilingStatusRank(b.status)
      || a.daysRemainingSort - b.daysRemainingSort
      || b.outstanding - a.outstanding);
}

function timelyFilingRiskRow(claim = {}) {
  const financial = claimFinancialView(claim);
  const blocker = isDeniedClaim(claim) || needsClientAction(claim) || needsPrebillScrub(claim) || matchesQueue(claim, "payerFollowUp");
  if (financial.sourceOutstanding <= 0 && !blocker) return null;
  const firstDos = claimFirstDos(claim);
  const lastDos = claimLastDos(claim);
  const deadlineDays = timelyFilingDeadlineDays(claim);
  const deadlineDate = lastDos ? addDays(lastDos, deadlineDays) : "";
  const daysRemaining = timelyFilingDaysRemaining(deadlineDate);
  const status = timelyFilingStatus({ lastDos, daysRemaining });
  const shouldSurface = financial.sourceOutstanding > 0 || ["Expired", "0-14 Days", "15-30 Days", "No DOS"].includes(status);
  if (!shouldSurface) return null;
  return {
    claimId: claim.claimId || "",
    client: claim.client || "Unknown client",
    payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
    state: clientStateValue(claim) || "State Not Tagged",
    firstDos,
    lastDos,
    deadlineDays,
    deadlineDate,
    daysRemaining,
    daysRemainingSort: Number.isFinite(daysRemaining) ? daysRemaining : 99999,
    expected: financial.expectedReimbursement,
    paid: financial.totalInsurancePaid,
    outstanding: financial.sourceOutstanding,
    delta: moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding),
    status,
    policyPlaceholder: true,
    blocker: timelyFilingBlocker(claim),
    nextAction: timelyFilingNextAction(claim, status)
  };
}

function renderTimelyFilingRiskRow(row) {
  const tone = row.status === "Expired" ? "red" : ["0-14 Days", "15-30 Days", "No DOS"].includes(row.status) ? "amber" : "blue";
  return `
    <button class="timely-filing-row" type="button" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Client"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.client)}</small></span>
      <span data-label="Payer / State"><strong>${escapeHtml(row.payer)}</strong><small>${escapeHtml(row.state)}</small></span>
      <span data-label="DOS Window"><strong>${escapeHtml(row.firstDos ? `${formatDate(row.firstDos)} - ${formatDate(row.lastDos)}` : "Missing DOS")}</strong><small>${formatNounCount(row.deadlineDays, "day")} placeholder</small></span>
      <span data-label="Deadline"><strong>${escapeHtml(row.deadlineDate ? formatDate(row.deadlineDate) : "Needs DOS")}</strong><small>${escapeHtml(timelyFilingDueText(row))}</small></span>
      <span data-label="AR / Delta"><strong>${money(row.outstanding)}</strong><small>Delta ${money(row.delta)}</small></span>
      <span data-label="Status">${pill(row.status, tone)}<small>${escapeHtml(row.blocker)}</small></span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function timelyFilingDeadlineDays(claim = {}) {
  const payerText = `${claim.primaryPayer || ""} ${claim.secondaryPayer || ""}`.toLowerCase();
  if (/medicaid|tenncare|care\s*source|caresource|peachstate|sentara|trillium|carelon|alliance|partners/.test(payerText)) return 180;
  if (/aetna|cigna|united|uhc|optum|bcbs|blue cross|anthem|kaiser/.test(payerText)) return 365;
  return 180;
}

function claimFirstDos(claim = {}) {
  const values = claimServiceDosValues(claim);
  return values.length ? values[0] : "";
}

function claimLastDos(claim = {}) {
  const values = claimServiceDosValues(claim);
  return values.length ? values.at(-1) : "";
}

function claimServiceDosValues(claim = {}) {
  return (Array.isArray(claim.services) ? claim.services : [])
    .map((service) => serviceDosValue(service))
    .filter(Boolean)
    .sort();
}

function serviceDosValue(service = {}) {
  return normalizeDateValue(firstPresent(
    service.dos,
    service.dateOfService,
    service.serviceDate,
    service.service_date,
    service.srvcDate,
    service["Srvc Date"],
    service.SrvFromDate,
    service.SrvDate,
    service.fromDate,
    service.from_date,
    service.serviceFromDate
  )) || "";
}

function normalizeDateValue(value) {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) return localDateString(value);
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return dateOnlyFilterString(text);
  const leadingDate = /^(\d{4}-\d{2}-\d{2})[T\s]/.exec(text);
  if (leadingDate) return dateOnlyFilterString(leadingDate[1]);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  return localDateString(parsed);
}

function timelyFilingDaysRemaining(deadlineDate) {
  if (!deadlineDate) return null;
  const deadline = localDateFromDateOnly(deadlineDate);
  if (!deadline) return null;
  return Math.ceil((deadline - todayStart()) / 86400000);
}

function timelyFilingStatus(row = {}) {
  if (!row.lastDos) return "No DOS";
  if (row.daysRemaining < 0) return "Expired";
  if (row.daysRemaining <= 14) return "0-14 Days";
  if (row.daysRemaining <= 30) return "15-30 Days";
  if (row.daysRemaining <= 60) return "31-60 Days";
  return "60+ Days";
}

function timelyFilingStatusRank(status) {
  return {
    "Expired": 0,
    "0-14 Days": 1,
    "15-30 Days": 2,
    "31-60 Days": 3,
    "No DOS": 4,
    "60+ Days": 5
  }[status] ?? 9;
}

function timelyFilingBlocker(claim = {}) {
  if (isDeniedClaim(claim)) return "Denial / Appeal";
  if (needsClientAction(claim)) return "Client Evidence";
  if (needsPrebillScrub(claim)) return "Pre-Bill Scrub";
  if (needsPaymentReview(claim)) return "Payment Review";
  return "Open AR";
}

function timelyFilingNextAction(claim = {}, status = "") {
  const blocker = timelyFilingBlocker(claim);
  if (status === "No DOS") return "Load or map date-of-service before deadline math can be trusted.";
  if (status === "Expired") return "Escalate proof of timely filing, appeal route, or manager write-off review before closure.";
  if (status === "0-14 Days") return "Submit, rebill, or document payer proof immediately; do not leave in normal follow-up.";
  if (blocker === "Client Evidence") return "Get authorization, COB, coverage, or record proof from the client before the filing window tightens.";
  if (blocker === "Denial / Appeal") return "Prepare corrected claim or appeal packet with proof of original timely filing.";
  if (blocker === "Pre-Bill Scrub") return "Resolve rate, auth, payer sequence, or PR guard before rebilling.";
  return "Document payer status, deadline owner, and next follow-up date.";
}

function timelyFilingDueText(row = {}) {
  if (row.status === "No DOS") return "Deadline unavailable";
  if (row.daysRemaining < 0) return `${formatNounCount(Math.abs(row.daysRemaining), "day")} expired`;
  if (row.daysRemaining === 0) return "Due today";
  return `${formatNounCount(row.daysRemaining, "day")} left`;
}

function renderArForecastPanel(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.arForecastPanel) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const forecast = arCashAndSlaForecast(claims);
  els.arForecastPanel.innerHTML = `
    <section class="ar-forecast-head">
      <div>
        <span>AR Cash + SLA Forecast</span>
        <strong>${escapeHtml(money(forecast.projectedNext14))} projected in 14 days</strong>
        <p>Filtered workbench forecast using open balance, payer response confidence, SLA due dates, underpayment risk, and calculation guards.</p>
      </div>
      <div>
        <span>Collection Confidence</span>
        <strong>${forecast.confidence}%</strong>
        <small>${formatNounCount(forecast.readyClaims, "ready claim")} / ${formatNounCount(forecast.openClaims, "open claim")} · ${formatNounCount(forecast.blockedClaims, "blocked claim")}</small>
      </div>
    </section>
    <div class="ar-forecast-grid">
      ${forecast.cards.map(renderArForecastCard).join("")}
    </div>
    <div class="ar-forecast-lanes">
      ${forecast.lanes.map(renderArForecastLane).join("")}
    </div>
  `;
  Array.from(els.arForecastPanel.querySelectorAll("[data-ar-forecast-focus]")).forEach((button) => {
    button.addEventListener("click", () => applyArForecastFocus(button.dataset.arForecastFocus));
  });
}

function arCashAndSlaForecast(claims = []) {
  const openClaims = openArClaims(claims);
  const scored = openClaims.map((claim) => {
    const financial = claimFinancialView(claim);
    const confidence = collectionConfidenceForClaim(claim);
    const bucket = collectionWindowForClaim(claim, confidence);
    return {
      claim,
      claimId: claim.claimId || "",
      bucket,
      confidence,
      outstanding: financial.sourceOutstanding,
      projected: moneyNumber(financial.sourceOutstanding * (confidence / 100)),
      blocked: confidence < 35,
      ready: confidence >= 65,
      slaBucket: slaBucket(claim),
      owner: claim.assignedTo || "Unassigned",
      reason: workReason(claim)
    };
  });
  const projectedNext7 = sumForecastRows(scored.filter((row) => row.bucket === "0-7 Days"));
  const projectedNext14 = sumForecastRows(scored.filter((row) => ["0-7 Days", "8-14 Days"].includes(row.bucket)));
  const readyClaims = scored.filter((row) => row.ready).length;
  const blockedClaims = scored.filter((row) => row.blocked).length;
  const openOwed = sumClaimOutstanding(openClaims);
  const projectedTotal = sumForecastRows(scored);
  const confidence = openOwed > 0 ? Math.round((projectedTotal / openOwed) * 100) : 100;
  const overdue = scored.filter((row) => row.slaBucket === "Overdue");
  const noDate = scored.filter((row) => row.slaBucket === "No Date");
  const pressureOwner = ownerWorkloadRows(openClaims)[0];
  const next7Count = scored.filter((row) => row.bucket === "0-7 Days").length;
  return {
    openClaims: openClaims.length,
    readyClaims,
    blockedClaims,
    projectedNext7,
    projectedNext14,
    projectedTotal,
    confidence,
    cards: [
      {
        key: "next7",
        label: "Next 7 Days",
        value: money(projectedNext7),
        detail: `${formatNounCount(next7Count, "claim")} ${next7Count === 1 ? "is" : "are"} likely to move`,
        tone: "green",
        focus: "next7"
      },
      {
        key: "slaRisk",
        label: "SLA Risk",
        value: overdue.length,
        detail: `${money(sumForecastRows(overdue))} projected but overdue`,
        tone: overdue.length ? "red" : "green",
        focus: "overdue"
      },
      {
        key: "blocked",
        label: "Blocked Cash",
        value: money(sumForecastRows(scored.filter((row) => row.blocked))),
        detail: `${formatNounCount(blockedClaims, "claim")} ${blockedClaims === 1 ? "needs" : "need"} evidence, math, or payer response`,
        tone: blockedClaims ? "amber" : "green",
        focus: "blocked"
      },
      {
        key: "owner",
        label: "Staff Pressure",
        value: pressureOwner ? pressureOwner.owner : "None",
        detail: pressureOwner ? `${pressureOwner.openClaims} open · ${pressureOwner.pastDue} past due` : "No open AR pressure",
        tone: pressureOwner?.pastDue ? "amber" : "blue",
        focus: "owner"
      },
      {
        key: "noDate",
        label: "No Follow-Up Date",
        value: noDate.length,
        detail: `${money(sumForecastRows(noDate))} has no SLA target`,
        tone: noDate.length ? "red" : "green",
        focus: "noDate"
      }
    ],
    lanes: ["0-7 Days", "8-14 Days", "15-30 Days", "Blocked"].map((bucket) => {
      const rows = scored.filter((row) => row.bucket === bucket).sort((a, b) => b.projected - a.projected).slice(0, 4);
      return {
        bucket,
        rows,
        projected: sumForecastRows(scored.filter((row) => row.bucket === bucket)),
        count: scored.filter((row) => row.bucket === bucket).length
      };
    })
  };
}

function collectionConfidenceForClaim(claim) {
  const financial = claimFinancialView(claim);
  if (financial.sourceOutstanding <= 0) return 100;
  let score = 45;
  if (financial.totalInsurancePaid > 0) score += 20;
  if (isPartiallyPaidClaim(claim)) score += 10;
  if (missingPayerResponse(claim)) score -= 15;
  if (needsClientAction(claim)) score -= 25;
  if (isDeniedClaim(claim)) score -= 20;
  if (sameExternalPayerConflict(claim) || financial.unmatchedPaid > 0) score -= 20;
  if (hasCalculationGuards(claim)) score -= 15;
  if (isPastDue(claim)) score -= 8;
  if (slaBucket(claim) === "No Date") score -= 10;
  if (/paid|processed|eob|era|response/i.test(claimOperationalText(claim))) score += 12;
  return Math.max(5, Math.min(95, score));
}

function collectionWindowForClaim(claim, confidence) {
  if (confidence < 35 || needsClientAction(claim) || hasCalculationGuards(claim)) return "Blocked";
  const bucket = slaBucket(claim);
  if (bucket === "Overdue" || bucket === "Due Today" || bucket === "Due 1-7") return "0-7 Days";
  if (bucket === "Due 8-14") return "8-14 Days";
  return "15-30 Days";
}

function sumForecastRows(rows = []) {
  return moneyNumber(rows.reduce((sum, row) => sum + moneyNumber(row.projected), 0));
}

function renderArForecastCard(card) {
  return `
    <article class="ar-forecast-card ar-forecast-card--${escapeHtml(card.tone)}">
      <span>${escapeHtml(card.label)}</span>
      <strong>${escapeHtml(String(card.value))}</strong>
      <small>${escapeHtml(card.detail)}</small>
      <button type="button" class="ghost-button" data-ar-forecast-focus="${escapeHtml(card.focus)}">Focus</button>
    </article>
  `;
}

function renderArForecastLane(lane) {
  return `
    <article class="ar-forecast-lane">
      <div class="ar-forecast-lane-head">
        <span>${escapeHtml(lane.bucket)}</span>
        <strong>${escapeHtml(money(lane.projected))}</strong>
        <small>${lane.count} claim${lane.count === 1 ? "" : "s"}</small>
      </div>
      <div class="ar-forecast-lane-rows">
        ${lane.rows.length ? lane.rows.map((row) => `
          <button type="button" data-ar-forecast-focus="${escapeHtml(`claim:${row.claimId}`)}">
            <span><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.owner)} · ${escapeHtml(row.reason)}</small></span>
            <b>${escapeHtml(money(row.projected))}</b>
          </button>
        `).join("") : '<div class="ar-forecast-empty">No claims in this window.</div>'}
      </div>
    </article>
  `;
}

function applyArForecastFocus(focus) {
  if (String(focus || "").startsWith("claim:")) {
    focusWorkClaim(String(focus).slice(6), { open: true });
    return;
  }
  if (focus === "overdue" || focus === "next7") {
    state.slicers = ensureSlicerState(state.slicers);
    state.slicers.claimDue = focus === "overdue" ? ["PAST DUE"] : ["PAST DUE", "DUE TODAY"];
    resetTrackerPage();
    saveState();
    renderSlicers();
    renderClaimsTable();
    return;
  }
  if (focus === "blocked") {
    setQueueFilter("missingInfo");
    return;
  }
  if (focus === "noDate") {
    setQueueFilter("stale");
    return;
  }
  if (focus === "owner") {
    const owner = ownerWorkloadRows(openArClaims(getClaims()))[0]?.owner;
    if (owner && els.ownerFilter) {
      els.ownerFilter.value = owner;
      resetTrackerPage();
      renderClaimsTable();
    }
  }
}

function renderClaimSubmissionCommand(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.claimSubmissionCommand) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const model = claimSubmissionReadinessModel(claims);
  els.claimSubmissionCommand.innerHTML = `
    <section class="claim-submission-head">
      <div>
        <span>Claim Submission Readiness</span>
        <strong>${model.readyClaims} ready · ${model.holdClaims} held</strong>
        <p>Batch-control view for clean claim submission and rebilling. It combines payer rules, contract math, auth/client evidence, timely filing, payer sequence, and calculation guards.</p>
      </div>
      <div class="claim-submission-proof">
        <span>Batch Math</span>
        <strong>${money(model.totals.delta)}</strong>
        <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
      </div>
    </section>
    <div class="claim-submission-summary">
      <div><span>Ready Expected</span><strong>${money(model.readyExpected)}</strong><small>${formatNounCount(model.readyClaims, "clean claim")}</small></div>
      <div><span>Held AR</span><strong>${money(model.holdAr)}</strong><small>${formatNounCount(model.holdClaims, "claim")} blocked</small></div>
      <div><span>Deadline Escalations</span><strong>${model.deadlineEscalations}</strong><small>Expired or 0-14 days</small></div>
      <div><span>Clean Claim Rate</span><strong>${model.cleanRate}%</strong><small>Filtered submission scope</small></div>
    </div>
    <div class="claim-submission-lanes">
      ${model.lanes.map(renderClaimSubmissionLane).join("")}
    </div>
  `;
}

function claimSubmissionReadinessModel(claims = []) {
  const rows = (Array.isArray(claims) ? claims : []).map(claimSubmissionReadinessRow);
  const laneDefinitions = claimSubmissionLaneDefinitions();
  const lanes = laneDefinitions.map((lane) => {
    const laneRows = rows.filter((row) => row.laneKey === lane.key)
      .sort((a, b) => b.priority - a.priority || b.outstanding - a.outstanding || b.expected - a.expected);
    return {
      ...lane,
      rows: laneRows,
      claims: laneRows.length,
      expected: moneyNumber(laneRows.reduce((sum, row) => sum + row.expected, 0)),
      outstanding: moneyNumber(laneRows.reduce((sum, row) => sum + row.outstanding, 0)),
      delta: moneyNumber(laneRows.reduce((sum, row) => sum + row.delta, 0))
    };
  });
  const totals = rows.reduce((sum, row) => {
    sum.expected += row.expected;
    sum.paid += row.paid;
    sum.outstanding += row.outstanding;
    sum.delta += row.delta;
    return sum;
  }, { expected: 0, paid: 0, outstanding: 0, delta: 0 });
  Object.keys(totals).forEach((key) => { totals[key] = moneyNumber(totals[key]); });
  const readyLane = lanes.find((lane) => lane.key === "ready");
  const deadlineLane = lanes.find((lane) => lane.key === "deadline");
  const readyClaims = readyLane?.claims || 0;
  const holdClaims = rows.length - readyClaims;
  return {
    rows,
    lanes,
    totals,
    readyClaims,
    holdClaims,
    readyExpected: readyLane?.expected || 0,
    holdAr: moneyNumber(lanes.filter((lane) => lane.key !== "ready").reduce((sum, lane) => sum + lane.outstanding, 0)),
    deadlineEscalations: deadlineLane?.claims || 0,
    cleanRate: rows.length ? Math.round((readyClaims / rows.length) * 100) : 0
  };
}

function claimSubmissionLaneDefinitions() {
  return [
    { key: "ready", label: "Ready To Submit", tone: "green", owner: "Billing", nextAction: "Submit or rebill through the normal batch after final spot-check." },
    { key: "client", label: "Hold For Client", tone: "amber", owner: "Client", nextAction: "Request authorization, COB, coverage, documentation, or provider proof." },
    { key: "setup", label: "Hold For Setup", tone: "red", owner: "Billing Setup", nextAction: "Fix contract rate, expected reimbursement, payer rule, or missing data before billing." },
    { key: "sequence", label: "Hold For Payer Sequence", tone: "amber", owner: "Billing Review", nextAction: "Resolve primary/secondary attribution, same external ID, or unmatched payment ambiguity." },
    { key: "deadline", label: "Deadline Escalation", tone: "red", owner: "RCM Lead", nextAction: "Do not leave in normal queue; submit/rebill now or document timely filing proof/appeal path." },
    { key: "followup", label: "Post-Submission Follow-Up", tone: "blue", owner: "Biller", nextAction: "Already submitted or paid partially; work payer portal/EOB/ERA response and next follow-up." }
  ];
}

function claimSubmissionReadinessRow(claim = {}) {
  const financial = claimFinancialView(claim);
  const filing = timelyFilingRiskRow(claim);
  const guards = evaluateClaimCalculationGuards(claim);
  const guardSummary = calculationGuardSummary(guards);
  const clientBlocker = needsClientAction(claim) || hasClientAuthorizationSignal(claim) || hasClientDocumentationSignal(claim);
  const setupBlocker = needsPrebillScrub(claim) || guards.some((guard) => ["EXPECTED_REIMBURSEMENT_INCOMPLETE", "ADJUSTMENT_DETAIL_MISSING", "PR_REASON_DETAIL_MISSING"].includes(guard.code));
  const sequenceBlocker = sameExternalPayerConflict(claim) || financial.unmatchedPaid > 0 || guards.some((guard) => guard.code === "PAYER_SEQUENCE_REVIEW");
  const deadlineStatus = filing?.status || "60+ Days";
  let laneKey = "ready";
  if (["Expired", "0-14 Days"].includes(deadlineStatus)) laneKey = "deadline";
  else if (clientBlocker) laneKey = "client";
  else if (setupBlocker) laneKey = "setup";
  else if (sequenceBlocker) laneKey = "sequence";
  else if (financial.totalInsurancePaid > 0 || matchesQueue(claim, "payerFollowUp") || matchesQueue(claim, "waitingPrimary") || matchesQueue(claim, "waitingSecondary")) laneKey = "followup";
  return {
    claimId: claim.claimId || "",
    client: claim.client || "Unknown client",
    payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
    expected: financial.expectedReimbursement,
    paid: financial.totalInsurancePaid,
    outstanding: financial.sourceOutstanding,
    delta: moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding),
    laneKey,
    priority: workPriorityScore(claim),
    deadlineStatus,
    guardSummary,
    nextAction: claimSubmissionNextAction(laneKey, deadlineStatus, guardSummary)
  };
}

function claimSubmissionNextAction(laneKey, deadlineStatus, guardSummary) {
  if (laneKey === "deadline") return deadlineStatus === "Expired" ? "Escalate timely filing proof or appeal route before write-off review." : "Submit/rebill immediately or document payer proof before deadline expires.";
  if (laneKey === "client") return "Collect client evidence before submitting so the claim does not become preventable denial AR.";
  if (laneKey === "setup") return `Fix setup guard before billing: ${guardSummary || "contract/rate/payer rule"}.`;
  if (laneKey === "sequence") return "Resolve payer sequence and attribution before submission or secondary handoff.";
  if (laneKey === "followup") return "Keep out of new-submission batch; work payer response, ERA/EOB, or secondary follow-up.";
  return "Ready for clean claim submission/rebill spot-check.";
}

function renderClaimSubmissionLane(lane) {
  return `
    <article class="claim-submission-lane claim-submission-lane--${escapeHtml(lane.tone)}">
      <div class="claim-submission-lane-head">
        <div>
          <span>${escapeHtml(lane.owner)}</span>
          <strong>${escapeHtml(lane.label)}</strong>
          <small>${formatNounCount(lane.claims, "claim")} · ${money(lane.outstanding)} AR · delta ${money(lane.delta)}</small>
        </div>
        ${pill(String(lane.claims), lane.tone)}
      </div>
      <p>${escapeHtml(lane.nextAction)}</p>
      <div class="claim-submission-rows">
        ${lane.rows.slice(0, 3).map(renderClaimSubmissionRow).join("") || '<div class="claim-submission-empty">No claims in this lane.</div>'}
      </div>
    </article>
  `;
}

function renderClaimSubmissionRow(row) {
  return `
    <button type="button" class="claim-submission-row" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Client"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.client)}</small></span>
      <span data-label="Payer / Deadline"><strong>${escapeHtml(row.payer)}</strong><small>${escapeHtml(row.deadlineStatus)}</small></span>
      <span data-label="Expected / AR"><strong>${money(row.expected)} / ${money(row.outstanding)}</strong><small>Delta ${money(row.delta)}</small></span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function renderProviderCredentialingCommand(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.providerCredentialingCommand) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const model = providerCredentialingReadinessModel(claims);
  els.providerCredentialingCommand.innerHTML = `
    <section class="provider-credentialing-head">
      <div>
        <span>Provider Credentialing Readiness</span>
        <strong>${formatNounCount(model.reviewRows, "review row")} · ${money(model.reviewAr)} AR</strong>
        <p>Provider, payer, state, and place-of-service control view for enrollment, credentialing, location, and rendering-provider blockers before claims are billed or appealed.</p>
      </div>
      <div class="provider-credentialing-proof">
        <span>Provider Math</span>
        <strong>${money(model.totals.delta)}</strong>
        <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
      </div>
    </section>
    <div class="provider-credentialing-summary">
      <div><span>Provider/Payer Rows</span><strong>${model.rows.length}</strong><small>${formatNounCount(model.providerCount, "provider")}</small></div>
      <div><span>Enrollment Review</span><strong>${model.enrollmentRows}</strong><small>Payer setup or network gaps</small></div>
      <div><span>POS Review</span><strong>${model.posRows}</strong><small>Location/place mismatch risk</small></div>
      <div><span>Credential Proof</span><strong>${model.credentialRows}</strong><small>Provider proof signals</small></div>
    </div>
    <div class="provider-credentialing-list">
      <div class="provider-credentialing-row provider-credentialing-header">
        <span>Provider / Payer</span>
        <span>State / POS</span>
        <span>Claims</span>
        <span>Open AR</span>
        <span>Status</span>
        <span>Next Action</span>
      </div>
      ${model.rows.slice(0, 9).map(renderProviderCredentialingRow).join("") || '<div class="provider-credentialing-empty">No provider credentialing rows in the active filters.</div>'}
    </div>
  `;
}

function providerCredentialingReadinessModel(claims = []) {
  const rows = providerCredentialingRows(claims);
  const totals = rows.reduce((sum, row) => {
    sum.expected += row.expected;
    sum.paid += row.paid;
    sum.outstanding += row.outstanding;
    sum.delta += row.delta;
    return sum;
  }, { expected: 0, paid: 0, outstanding: 0, delta: 0 });
  Object.keys(totals).forEach((key) => { totals[key] = moneyNumber(totals[key]); });
  return {
    rows,
    totals,
    providerCount: new Set(rows.map((row) => row.provider)).size,
    reviewRows: rows.filter((row) => row.status !== "Ready").length,
    reviewAr: moneyNumber(rows.filter((row) => row.status !== "Ready").reduce((sum, row) => sum + row.outstanding, 0)),
    enrollmentRows: rows.filter((row) => row.status === "Enrollment Review").length,
    posRows: rows.filter((row) => row.status === "POS / Location Review").length,
    credentialRows: rows.filter((row) => row.status === "Credentialing Proof").length
  };
}

function providerCredentialingRows(claims = []) {
  const grouped = new Map();
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    payerNamesForClaim(claim).forEach((payer) => {
      const provider = claim.renderingProvider || "Unknown Provider";
      const place = claim.place || (claim.services || []).find((service) => service.place)?.place || "Missing POS";
      const stateValue = clientStateValue(claim) || "State Not Tagged";
      const key = [provider, payer, stateValue, place].join("|");
      const row = grouped.get(key) || {
        provider,
        payer,
        state: stateValue,
        place,
        claims: new Set(),
        expected: 0,
        paid: 0,
        outstanding: 0,
        delta: 0,
        credentialSignals: 0,
        enrollmentGaps: 0,
        posGaps: 0,
        highestPriority: 0
      };
      const financial = claimFinancialView(claim);
      row.claims.add(claim.claimId);
      row.expected += financial.expectedReimbursement;
      row.paid += financial.totalInsurancePaid;
      row.outstanding += financial.sourceOutstanding;
      row.delta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
      row.highestPriority = Math.max(row.highestPriority, workPriorityScore(claim));
      const text = claimOperationalText(claim);
      if (/credential|provider|rendering|enrollment|npi/.test(text)) row.credentialSignals += 1;
      if (!provider || provider === "Unknown Provider" || !payerOperatingRule(payer)) row.enrollmentGaps += 1;
      if (!place || /^missing/i.test(place) || contractPlaceCode(place) === "99") row.posGaps += 1;
      grouped.set(key, row);
    });
  });
  return Array.from(grouped.values()).map((row) => {
    const status = providerCredentialingStatus(row);
    return {
      ...row,
      firstClaimId: Array.from(row.claims)[0] || "",
      claims: row.claims.size,
      expected: moneyNumber(row.expected),
      paid: moneyNumber(row.paid),
      outstanding: moneyNumber(row.outstanding),
      delta: moneyNumber(row.delta),
      status,
      nextAction: providerCredentialingNextAction(status)
    };
  }).sort((a, b) => providerCredentialingStatusRank(a.status) - providerCredentialingStatusRank(b.status)
    || b.outstanding - a.outstanding
    || b.highestPriority - a.highestPriority);
}

function providerCredentialingStatus(row = {}) {
  if (row.credentialSignals > 0) return "Credentialing Proof";
  if (row.posGaps > 0) return "POS / Location Review";
  if (row.enrollmentGaps > 0) return "Enrollment Review";
  return "Ready";
}

function providerCredentialingStatusRank(status = "") {
  return {
    "Credentialing Proof": 0,
    "POS / Location Review": 1,
    "Enrollment Review": 2,
    "Ready": 3
  }[status] ?? 9;
}

function providerCredentialingNextAction(status) {
  if (status === "Credentialing Proof") return "Collect provider credentialing, NPI, enrollment, or payer roster evidence before billing/appeal.";
  if (status === "POS / Location Review") return "Validate place of service, telehealth/home/office location, and payer location rules.";
  if (status === "Enrollment Review") return "Confirm payer enrollment/network status and provider-to-payer rule mapping.";
  return "Provider, payer, state, and POS look ready for normal submission monitoring.";
}

function renderProviderCredentialingRow(row) {
  const tone = row.status === "Ready" ? "green" : row.status === "Enrollment Review" ? "amber" : "red";
  return `
    <button type="button" class="provider-credentialing-row" data-open="${escapeHtml(row.firstClaimId || "")}">
      <span data-label="Provider / Payer"><strong>${escapeHtml(row.provider)}</strong><small>${escapeHtml(row.payer)}</small></span>
      <span data-label="State / POS"><strong>${escapeHtml(row.state)}</strong><small>POS ${escapeHtml(row.place)}</small></span>
      <span data-label="Claims"><strong>${row.claims}</strong><small>Priority ${row.highestPriority}</small></span>
      <span data-label="Open AR"><strong>${money(row.outstanding)}</strong><small>Delta ${money(row.delta)}</small></span>
      <span data-label="Status">${pill(row.status, tone)}</span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function renderPrebillQualityPanel(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.prebillQualityPanel) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const profile = prebillQualityProfile(claims);
  els.prebillQualityPanel.innerHTML = `
    <section class="prebill-quality-head">
      <div>
        <span>Pre-Bill Quality Scrub</span>
        <strong>${formatNounCount(profile.blockerClaims, "blocker claim")} · ${formatNounCount(profile.reviewClaims, "review claim")}</strong>
        <p>Claim scrub logic before billing or rebilling: authorization, documentation, payer sequence, expected reimbursement, PR reason detail, and adjustment evidence.</p>
      </div>
      <div class="prebill-quality-math">
        <span>At-risk expected</span>
        <strong>${escapeHtml(money(profile.expectedAtRisk))}</strong>
        <small>${formatNounCount(profile.cleanClaims, "clean claim")} / ${formatNounCount(claims.length, "filtered claim")}</small>
      </div>
    </section>
    <div class="prebill-quality-grid">
      ${profile.groups.map(renderPrebillQualityGroup).join("")}
    </div>
  `;
  Array.from(els.prebillQualityPanel.querySelectorAll("[data-prebill-open]")).forEach((button) => {
    button.addEventListener("click", () => focusWorkClaim(button.dataset.prebillOpen, { open: true }));
  });
  Array.from(els.prebillQualityPanel.querySelectorAll("[data-prebill-focus]")).forEach((button) => {
    button.addEventListener("click", () => applyPrebillQualityFocus(button.dataset.prebillFocus));
  });
}

function prebillQualityProfile(claims = []) {
  const safeClaims = Array.isArray(claims) ? claims : [];
  const groups = prebillQualityGroups(safeClaims);
  const blockerIds = new Set(groups.filter((group) => group.severity === "blocker").flatMap((group) => group.rows.map((row) => row.claimId)));
  const reviewIds = new Set(groups.filter((group) => group.severity !== "blocker").flatMap((group) => group.rows.map((row) => row.claimId)));
  const issueIds = new Set(groups.flatMap((group) => group.rows.map((row) => row.claimId)));
  return {
    groups,
    blockerClaims: blockerIds.size,
    reviewClaims: Array.from(reviewIds).filter((id) => !blockerIds.has(id)).length,
    cleanClaims: safeClaims.filter((claim) => !issueIds.has(String(claim.claimId || ""))).length,
    expectedAtRisk: moneyNumber(safeClaims
      .filter((claim) => issueIds.has(String(claim.claimId || "")))
      .reduce((sum, claim) => sum + claimFinancialView(claim).expectedReimbursement, 0))
  };
}

function prebillQualityGroups(claims = []) {
  const definitions = [
    {
      key: "authorization",
      label: "Authorization / Coverage",
      severity: "blocker",
      owner: "Client",
      nextStep: "Get authorization, coverage, credentialing, or provider correction proof before billing/rebilling.",
      filter: (claim) => hasClientAuthorizationSignal(claim),
      reason: (claim) => clientOpenNeed(claim)
    },
    {
      key: "documentation",
      label: "Documentation / COB",
      severity: "blocker",
      owner: "Client",
      nextStep: "Collect COB, records, medical documentation, or requested client proof before submission.",
      filter: (claim) => hasClientDocumentationSignal(claim) && !hasClientAuthorizationSignal(claim),
      reason: (claim) => clientOpenNeed(claim)
    },
    {
      key: "expected",
      label: "Expected Reimbursement",
      severity: "blocker",
      owner: "Billing Setup",
      nextStep: "Resolve missing fee schedule, allowed amount, or procedure rate before relying on AR math.",
      filter: (claim) => evaluateClaimCalculationGuards(claim).some((guard) => guard.code === "EXPECTED_REIMBURSEMENT_INCOMPLETE"),
      reason: (claim) => calculationGuardSummary(evaluateClaimCalculationGuards(claim))
    },
    {
      key: "payerSequence",
      label: "Payer Sequence",
      severity: "review",
      owner: "Billing Review",
      nextStep: "Resolve primary/secondary ambiguity, same external payer IDs, or unmatched payment attribution.",
      filter: (claim) => sameExternalPayerConflict(claim) || claimFinancialView(claim).unmatchedPaid > 0,
      reason: (claim) => paymentAttributionExplanation(claim)
    },
    {
      key: "prDetail",
      label: "PR / Adjustment Detail",
      severity: "review",
      owner: "Payment Posting",
      nextStep: "Confirm CARC/PR detail and adjustment evidence before patient billing or write-off review.",
      filter: (claim) => evaluateClaimCalculationGuards(claim).some((guard) => ["PR_REASON_DETAIL_MISSING", "ADJUSTMENT_DETAIL_MISSING"].includes(guard.code)),
      reason: (claim) => calculationGuardSummary(evaluateClaimCalculationGuards(claim))
    }
  ];
  return definitions.map((definition) => {
    const rows = claims
      .filter(definition.filter)
      .map((claim) => ({
        claim,
        claimId: String(claim.claimId || ""),
        client: claim.client || "Unknown client",
        payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
        expected: claimFinancialView(claim).expectedReimbursement,
        owed: claimFinancialView(claim).sourceOutstanding,
        reason: definition.reason(claim)
      }))
      .sort((a, b) => b.expected - a.expected || b.owed - a.owed);
    return {
      ...definition,
      rows,
      expected: moneyNumber(rows.reduce((sum, row) => sum + row.expected, 0))
    };
  }).sort((a, b) => b.expected - a.expected || b.rows.length - a.rows.length);
}

function renderPrebillQualityGroup(group) {
  const firstClaimId = group.rows[0]?.claimId || "";
  return `
    <article class="prebill-quality-card prebill-quality-card--${escapeHtml(group.severity)}">
      <div class="prebill-quality-card-head">
        <div>
          <span>${escapeHtml(group.owner)} · ${escapeHtml(group.severity)}</span>
          <strong>${escapeHtml(group.label)}</strong>
        </div>
        ${pill(formatNounCount(group.rows.length, "claim"), group.severity === "blocker" ? "red" : "amber")}
      </div>
      <div class="prebill-quality-card-metrics">
        <span><small>Expected at risk</small><strong>${escapeHtml(money(group.expected))}</strong></span>
        <span><small>Top claim</small><strong>${escapeHtml(firstClaimId || "None")}</strong></span>
      </div>
      <p>${escapeHtml(group.nextStep)}</p>
      <div class="prebill-quality-rows">
        ${group.rows.slice(0, 3).map(renderPrebillQualityRow).join("") || '<div class="prebill-quality-empty">No issues in current filter.</div>'}
      </div>
      <div class="prebill-quality-actions">
        ${firstClaimId ? `<button type="button" class="small-button" data-prebill-open="${escapeHtml(firstClaimId)}">Review First</button>` : ""}
        <button type="button" class="ghost-button" data-prebill-focus="${escapeHtml(group.key)}">${escapeHtml(prebillFocusLabel(group.key))}</button>
      </div>
    </article>
  `;
}

function renderPrebillQualityRow(row) {
  return `
    <button type="button" class="prebill-quality-row" data-prebill-open="${escapeHtml(row.claimId)}">
      <span>
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.client)} · ${escapeHtml(row.payer)}</small>
      </span>
      <span>
        <strong>${escapeHtml(money(row.expected))}</strong>
        <small>${escapeHtml(row.reason || "Review required")}</small>
      </span>
    </button>
  `;
}

function prebillFocusLabel(key) {
  if (key === "authorization" || key === "documentation") return "Filter Client Action";
  if (key === "expected" || key === "prDetail") return "Filter Missing Info";
  if (key === "payerSequence") return "Open Sequence";
  return "Focus";
}

function applyPrebillQualityFocus(key) {
  if (key === "authorization" || key === "documentation") {
    setQueueFilter("clientAction");
    return;
  }
  if (key === "expected" || key === "prDetail") {
    setQueueFilter("missingInfo");
    return;
  }
  const group = prebillQualityGroups(claimsFromTrackerRows(getFilteredServiceRows(buildTrackerQueryRequest("service")), "service"))
    .find((item) => item.key === key);
  if (group?.rows[0]?.claimId) focusWorkClaim(group.rows[0].claimId, { open: true });
}

function renderDenialAppealCommand(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.denialAppealCommand) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const model = denialAppealCommandModel(claims);
  els.denialAppealCommand.innerHTML = `
    <section class="denial-appeal-head">
      <div>
        <span>Denial / Appeal Command</span>
        <strong>${model.totals.packetClaims} packet claim${model.totals.packetClaims === 1 ? "" : "s"} · ${money(model.totals.exposure)} exposure</strong>
        <p>Prioritized appeal, corrected-claim, underpayment, and client-evidence queue built from the same evidence packet used in claim detail.</p>
      </div>
      <div class="denial-appeal-score">
        <span>Packet readiness</span>
        <strong>${model.totals.averageReadiness}%</strong>
        <small>${model.totals.ready} ready · ${model.totals.missingEvidence} need evidence</small>
      </div>
    </section>
    <div class="denial-appeal-metrics">
      <div><span>Appeal Ready</span><strong>${model.totals.ready}</strong><small>Can route with manager approval</small></div>
      <div><span>Needs Evidence</span><strong>${model.totals.missingEvidence}</strong><small>Payer, math, adjustment, or workflow gap</small></div>
      <div><span>Client Evidence</span><strong>${model.totals.clientEvidence}</strong><small>Auth, COB, records, or documentation</small></div>
      <div><span>Underpayment</span><strong>${money(model.totals.underpayment)}</strong><small>Recovery path before write-off</small></div>
    </div>
    <div class="denial-appeal-list">
      ${model.rows.length ? model.rows.slice(0, 7).map(renderDenialAppealRow).join("") : '<div class="denial-appeal-empty">No denial or appeal packet rows in the active filter.</div>'}
    </div>
  `;
  Array.from(els.denialAppealCommand.querySelectorAll("[data-denial-open]")).forEach((button) => {
    button.addEventListener("click", () => focusWorkClaim(button.dataset.denialOpen, { open: true }));
  });
  Array.from(els.denialAppealCommand.querySelectorAll("[data-denial-focus]")).forEach((button) => {
    button.addEventListener("click", () => applyDenialAppealFocus(button.dataset.denialFocus));
  });
}

function denialAppealCommandModel(claims = []) {
  const rows = (Array.isArray(claims) ? claims : [])
    .map((claim) => {
      const packet = appealPacketReadiness(claim);
      const financial = claimFinancialView(claim);
      const missingLabels = packet.items.filter((item) => !item.ready).map((item) => item.label);
      const isAppealCandidate = isDeniedClaim(claim)
        || financial.underpayment > 0
        || needsClientAction(claim)
        || packet.route === "Appeal / Corrected Claim"
        || packet.route === "Underpayment Recovery";
      if (!isAppealCandidate || packet.exposure <= 0) return null;
      const readinessPct = packet.items.length ? Math.round((packet.readyCount / packet.items.length) * 100) : 0;
      return {
        claim,
        claimId: claim.claimId,
        client: claim.client || "Unknown client",
        payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
        route: packet.route,
        statusLabel: packet.statusLabel,
        readinessPct,
        ready: packet.missingCount === 0,
        missingCount: packet.missingCount,
        missingLabels,
        nextAction: packet.nextAction,
        nextDetail: packet.nextDetail,
        exposure: packet.exposure,
        underpayment: financial.underpayment,
        clientEvidence: packet.route === "Client Evidence Request" || needsClientAction(claim),
        reason: packet.routeDetail
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.missingCount - a.missingCount || b.exposure - a.exposure || b.underpayment - a.underpayment || a.claimId.localeCompare(b.claimId));
  const totals = rows.reduce((sum, row) => {
    sum.packetClaims += 1;
    sum.exposure += row.exposure;
    sum.underpayment += row.underpayment;
    sum.ready += row.ready ? 1 : 0;
    sum.missingEvidence += row.ready ? 0 : 1;
    sum.clientEvidence += row.clientEvidence ? 1 : 0;
    sum.readinessTotal += row.readinessPct;
    return sum;
  }, { packetClaims: 0, exposure: 0, underpayment: 0, ready: 0, missingEvidence: 0, clientEvidence: 0, readinessTotal: 0, averageReadiness: 0 });
  totals.exposure = moneyNumber(totals.exposure);
  totals.underpayment = moneyNumber(totals.underpayment);
  totals.averageReadiness = totals.packetClaims ? Math.round(totals.readinessTotal / totals.packetClaims) : 100;
  return { rows, totals };
}

function renderDenialAppealRow(row) {
  const tone = row.ready ? "green" : row.missingCount >= 4 ? "red" : "amber";
  const missing = row.missingLabels.length
    ? `${row.missingLabels.slice(0, 3).join(", ")}${row.missingLabels.length > 3 ? ` +${row.missingLabels.length - 3}` : ""}`
    : "Complete";
  return `
    <button type="button" class="denial-appeal-row" data-denial-open="${escapeHtml(row.claimId)}">
      <div data-label="Claim">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small title="${escapeHtml(`${row.client} · ${row.payer}`)}">${escapeHtml(compactRcmLabel(row.client, 30))} · ${escapeHtml(compactRcmLabel(row.payer, 42))}</small>
      </div>
      <span data-label="Route">${escapeHtml(row.route)}</span>
      <span data-label="Exposure">${money(row.exposure)}</span>
      <span data-label="Readiness">${pill(`${row.readinessPct}%`, tone)}</span>
      <small data-label="Missing">${escapeHtml(missing)}</small>
      <small data-label="Next">${escapeHtml(row.nextAction)}</small>
    </button>
  `;
}

function compactRcmLabel(value, max = 36) {
  const normalized = String(value || "")
    .replace(/\([^)]*\)/g, "")
    .replace(/\b(P:|TN:|INN|OON|MCO|FFS)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (normalized.length <= max) return normalized || "Unknown";
  return `${normalized.slice(0, Math.max(0, max - 3)).trim()}...`;
}

function applyDenialAppealFocus(key) {
  if (key === "denied") {
    setQueueFilter("denied");
    return;
  }
  if (key === "clientEvidence") {
    setQueueFilter("clientAction");
    return;
  }
  setQueueFilter("partial");
}

function renderPaymentReconciliationQueue(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.paymentReconciliationQueue) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const groups = paymentReconciliationGroups(claims);
  const issueGroups = groups.filter((group) => group.rows.length);
  const totalIssues = issueGroups.reduce((sum, group) => sum + group.rows.length, 0);
  const totalExposure = issueGroups.reduce((sum, group) => sum + group.amount, 0);
  const formulaDelta = moneyNumber(claims.reduce((sum, claim) => {
    const financial = claimFinancialView(claim);
    return sum + financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding;
  }, 0));
  els.paymentReconciliationQueue.innerHTML = `
    <section class="payment-recon-head">
      <div>
        <span>Payment / ERA Reconciliation</span>
        <strong>${escapeHtml(money(totalExposure))} exposure across ${formatNounCount(totalIssues, "issue")}</strong>
        <p>Excel-style exception buckets for underpayment recovery, overpayment review, unapplied payments, and payer sequence conflicts.</p>
      </div>
      <div class="payment-recon-formula" title="Filtered claims formula check">
        <span>fx</span>
        <code>=Expected - Paid - Outstanding = ${escapeHtml(money(formulaDelta))}</code>
      </div>
    </section>
    <div class="payment-recon-grid">
      ${groups.map(renderPaymentReconciliationGroup).join("")}
    </div>
    ${renderCashPostingCloseout(claims)}
  `;
  Array.from(els.paymentReconciliationQueue.querySelectorAll("[data-payment-open]")).forEach((button) => {
    button.addEventListener("click", () => focusWorkClaim(button.dataset.paymentOpen, { open: true }));
  });
  Array.from(els.paymentReconciliationQueue.querySelectorAll("[data-payment-focus]")).forEach((button) => {
    button.addEventListener("click", () => applyPaymentReconciliationFocus(button.dataset.paymentFocus));
  });
}

function cashPostingCloseoutModel(claims = []) {
  const laneDefinitions = [
    { key: "ready", label: "Ready To Post", owner: "Cash Posting", tone: "green", action: "Post/confirm payment and keep normal audit trail." },
    { key: "eraPr", label: "ERA / PR Proof Needed", owner: "Cash Posting", tone: "amber", action: "Attach ERA/EOB and validate PR 1/2/3 before patient balance or closeout." },
    { key: "attribution", label: "Payer Attribution Review", owner: "Payer Sequencing", tone: "red", action: "Resolve primary/secondary attribution before posting final status." },
    { key: "underpayment", label: "Underpayment Recovery", owner: "AR Recovery", tone: "red", action: "Compare contract, ERA, and allowed amount before appeal or corrected-payment follow-up." },
    { key: "overpayment", label: "Overpayment / Credit", owner: "Cash Posting", tone: "amber", action: "Confirm true credit before refund, recoupment, or payer correction." },
    { key: "unapplied", label: "Unapplied Payment", owner: "Cash Posting", tone: "blue", action: "Tie payment detail back to service line, payer, and remittance." },
    { key: "followup", label: "Not Ready", owner: "Biller", tone: "slate", action: "Keep in payer/client follow-up until proof or payment is posted." }
  ];
  const lanes = new Map(laneDefinitions.map((definition) => [definition.key, {
    ...definition,
    claims: 0,
    expected: 0,
    paid: 0,
    outstanding: 0,
    delta: 0,
    rows: []
  }]));
  claims.forEach((claim) => {
    const laneKey = cashPostingCloseoutLane(claim);
    const lane = lanes.get(laneKey) || lanes.get("followup");
    const financial = claimFinancialView(claim);
    const delta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    const row = {
      claim,
      claimId: claim.claimId || "",
      client: claim.client || "Unknown client",
      payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
      expected: financial.expectedReimbursement,
      paid: financial.totalInsurancePaid,
      outstanding: financial.sourceOutstanding,
      delta,
      issue: cashPostingCloseoutIssue(claim, laneKey),
      nextAction: lane.action
    };
    lane.claims += 1;
    lane.expected += row.expected;
    lane.paid += row.paid;
    lane.outstanding += row.outstanding;
    lane.delta += row.delta;
    lane.rows.push(row);
  });
  const laneRows = Array.from(lanes.values()).map((lane) => ({
    ...lane,
    expected: moneyNumber(lane.expected),
    paid: moneyNumber(lane.paid),
    outstanding: moneyNumber(lane.outstanding),
    delta: moneyNumber(lane.delta),
    rows: lane.rows.sort((a, b) => b.outstanding - a.outstanding || Math.abs(b.delta) - Math.abs(a.delta) || a.claimId.localeCompare(b.claimId))
  }));
  const totals = laneRows.reduce((sum, lane) => {
    sum.claims += lane.claims;
    sum.expected += lane.expected;
    sum.paid += lane.paid;
    sum.outstanding += lane.outstanding;
    sum.delta += lane.delta;
    if (lane.key === "ready") sum.ready += lane.claims;
    if (lane.key !== "ready") sum.blocked += lane.claims;
    return sum;
  }, { claims: 0, ready: 0, blocked: 0, expected: 0, paid: 0, outstanding: 0, delta: 0 });
  return {
    lanes: laneRows,
    totals: {
      ...totals,
      expected: moneyNumber(totals.expected),
      paid: moneyNumber(totals.paid),
      outstanding: moneyNumber(totals.outstanding),
      delta: moneyNumber(totals.delta),
      readyRate: totals.claims ? Math.round((totals.ready / totals.claims) * 100) : 100
    }
  };
}

function cashPostingCloseoutLane(claim) {
  const financial = claimFinancialView(claim);
  const guards = evaluateClaimCalculationGuards(claim);
  const guardCodes = guards.map((guard) => guard.code);
  if (sameExternalPayerConflict(claim) || financial.unmatchedPaid > 0 || guardCodes.includes("PAYER_SEQUENCE_REVIEW")) return "attribution";
  if (financial.unappliedPayments > 0 || financial.paymentVariance !== 0) return "unapplied";
  if (financial.underpayment > 0) return "underpayment";
  if (financial.overpayment > 0) return "overpayment";
  if (guardCodes.includes("PR_REASON_DETAIL_MISSING") || guardCodes.includes("ADJUSTMENT_DETAIL_MISSING")) return "eraPr";
  if (financial.totalInsurancePaid > 0 && financial.sourceOutstanding <= 0 && guards.length === 0) return "ready";
  if (financial.totalInsurancePaid > 0 && guards.length === 0) return "ready";
  return "followup";
}

function cashPostingCloseoutIssue(claim, laneKey) {
  const financial = claimFinancialView(claim);
  const guardSummary = calculationGuardSummary(evaluateClaimCalculationGuards(claim));
  if (laneKey === "ready") return "Payment math and closeout proof are clean enough for posting review.";
  if (laneKey === "eraPr") return guardSummary;
  if (laneKey === "attribution") return paymentAttributionExplanation(claim);
  if (laneKey === "underpayment") return `${money(financial.underpayment)} expected reimbursement shortfall`;
  if (laneKey === "overpayment") return `${money(financial.overpayment)} possible credit/overpayment`;
  if (laneKey === "unapplied") return `${money(Math.max(financial.unappliedPayments, Math.abs(financial.paymentVariance || 0)))} payment variance`;
  return claim.action || "No payment or proof ready for closeout.";
}

function renderCashPostingCloseout(claims = []) {
  const model = cashPostingCloseoutModel(claims);
  return `
    <section class="cash-posting-closeout" aria-label="Cash posting closeout command">
      <div class="cash-posting-closeout-head">
        <div>
          <span>Cash Posting Closeout</span>
          <strong>${model.totals.readyRate}% ready · ${model.totals.blocked} blocked claim${model.totals.blocked === 1 ? "" : "s"}</strong>
          <p>Batch-control view for payment posting, ERA/PR proof, payer attribution, underpayment, overpayment, and unapplied-payment closeout.</p>
        </div>
        <div>
          <span>Posting Formula</span>
          <strong>${money(model.totals.delta)}</strong>
          <small>Expected - Paid - AR across ${formatNounCount(model.totals.claims, "filtered claim")}</small>
        </div>
      </div>
      <div class="cash-posting-closeout-summary">
        <div><span>Expected</span><strong>${money(model.totals.expected)}</strong><small>Filtered claim expected</small></div>
        <div><span>Paid</span><strong>${money(model.totals.paid)}</strong><small>Insurance paid</small></div>
        <div><span>Open AR</span><strong>${money(model.totals.outstanding)}</strong><small>Remaining payer/client balance</small></div>
        <div><span>Ready Claims</span><strong>${model.totals.ready}</strong><small>Posting-ready lane</small></div>
      </div>
      <div class="cash-posting-closeout-lanes">
        ${model.lanes.map(renderCashPostingCloseoutLane).join("") || '<div class="cash-posting-empty">No claims in the active payment closeout scope.</div>'}
      </div>
    </section>
  `;
}

function renderCashPostingCloseoutLane(lane) {
  const topRow = lane.rows[0];
  const clickableAttr = topRow ? `data-payment-open="${escapeHtml(topRow.claimId)}"` : "";
  return `
    <button type="button" class="cash-posting-lane cash-posting-lane--${escapeHtml(lane.tone)}" ${clickableAttr}>
      <span>${escapeHtml(lane.label)}</span>
      <strong>${lane.claims.toLocaleString()} claim${lane.claims === 1 ? "" : "s"}</strong>
      <small>${money(lane.outstanding)} AR · ${money(lane.delta)} delta · ${escapeHtml(lane.owner)}</small>
      <em>${escapeHtml(topRow ? `${topRow.claimId}: ${topRow.issue}` : lane.action)}</em>
    </button>
  `;
}

function renderCreditBalanceCommand(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.creditBalanceCommand) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const model = creditBalanceCommandModel(claims);
  els.creditBalanceCommand.innerHTML = `
    <section class="credit-balance-shell" aria-label="Credit balance refund and recoupment command">
      <div class="credit-balance-head">
        <div>
          <span>Credit Balance / Refund Command</span>
          <strong>${formatNounCount(model.totals.creditClaims, "credit-risk claim")} · ${money(model.totals.creditExposure)} exposure</strong>
          <p>Separate control for overpayments, payer credits, refunds, recoupment watch, unapplied cash, and patient-billing stops before money is closed or written off.</p>
        </div>
        <div class="credit-balance-proof">
          <span>Credit Formula</span>
          <strong>${money(model.totals.delta)}</strong>
          <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
        </div>
      </div>
      <div class="credit-balance-summary">
        <div><span>Overpaid / Credit</span><strong>${money(model.totals.overpayment)}</strong><small>Potential refund or payer correction</small></div>
        <div><span>Unapplied Cash</span><strong>${money(model.totals.unapplied)}</strong><small>Payment variance to tie out</small></div>
        <div><span>Recoupment Watch</span><strong>${model.totals.recoupmentWatch}</strong><small>Payer takeback/credit language</small></div>
        <div><span>Patient Stop</span><strong>${model.totals.patientStops}</strong><small>Do not bill patient until credit clears</small></div>
      </div>
      <div class="credit-balance-list">
        <div class="credit-balance-row credit-balance-header">
          <span>Claim / Credit Status</span>
          <span>Payer / Client</span>
          <span>Owner</span>
          <span>Credit / Delta</span>
          <span>Proof Needed</span>
          <span>Next Action</span>
        </div>
        ${model.rows.length ? model.rows.slice(0, 10).map(renderCreditBalanceRow).join("") : '<div class="credit-balance-empty">No credit balance, refund, or recoupment risks in the active filter.</div>'}
      </div>
    </section>
  `;
}

function creditBalanceCommandModel(claims = []) {
  const rows = (Array.isArray(claims) ? claims : [])
    .map(creditBalanceRow)
    .sort((a, b) => creditBalanceRank(a.status) - creditBalanceRank(b.status)
      || b.creditExposure - a.creditExposure
      || Math.abs(b.delta) - Math.abs(a.delta)
      || a.claimId.localeCompare(b.claimId));
  const claimTotals = (Array.isArray(claims) ? claims : []).reduce((sum, claim) => {
    const financial = claimFinancialView(claim);
    sum.expected += financial.expectedReimbursement;
    sum.paid += financial.totalInsurancePaid;
    sum.outstanding += financial.sourceOutstanding;
    sum.delta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    return sum;
  }, { expected: 0, paid: 0, outstanding: 0, delta: 0 });
  const totals = rows.reduce((sum, row) => {
    sum.creditExposure += row.creditExposure;
    sum.overpayment += row.overpayment;
    sum.unapplied += row.unapplied;
    if (row.creditExposure > 0) sum.creditClaims += 1;
    if (row.status === "Recoupment Watch") sum.recoupmentWatch += 1;
    if (row.patientStop) sum.patientStops += 1;
    return sum;
  }, { creditExposure: 0, overpayment: 0, unapplied: 0, creditClaims: 0, recoupmentWatch: 0, patientStops: 0 });
  return {
    rows,
    totals: {
      ...totals,
      creditExposure: moneyNumber(totals.creditExposure),
      overpayment: moneyNumber(totals.overpayment),
      unapplied: moneyNumber(totals.unapplied),
      expected: moneyNumber(claimTotals.expected),
      paid: moneyNumber(claimTotals.paid),
      outstanding: moneyNumber(claimTotals.outstanding),
      delta: moneyNumber(claimTotals.delta)
    }
  };
}

function creditBalanceRow(claim = {}) {
  const financial = claimFinancialView(claim);
  const text = claimOperationalText(claim);
  const overpayment = moneyNumber(financial.overpayment || 0);
  const unapplied = moneyNumber(Math.max(financial.unappliedPayments || 0, Math.abs(financial.paymentVariance || 0)));
  const creditText = /refund|credit|recoup|takeback|overpay|over paid|offset|negative balance/.test(text);
  const patientStop = overpayment > 0 || unapplied > 0 || creditText || String(claim.match || "").toLowerCase().includes("over paid");
  let status = "No Credit Risk";
  if (/recoup|takeback|offset/.test(text)) status = "Recoupment Watch";
  else if (overpayment > 0 || String(claim.match || "").toLowerCase().includes("over paid")) status = "Overpayment Review";
  else if (unapplied > 0) status = "Unapplied Cash";
  else if (/refund|credit|negative balance/.test(text)) status = "Refund Review";
  const creditExposure = moneyNumber(Math.max(overpayment, unapplied, status === "Refund Review" || status === "Recoupment Watch" ? financial.sourceOutstanding : 0));
  return {
    claimId: claim.claimId || "",
    client: claim.client || "Unknown client",
    payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
    status,
    tone: creditBalanceTone(status),
    owner: creditBalanceOwner(status),
    overpayment,
    unapplied,
    creditExposure,
    expected: financial.expectedReimbursement,
    paid: financial.totalInsurancePaid,
    outstanding: financial.sourceOutstanding,
    delta: moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding),
    patientStop,
    proofNeeded: creditBalanceProof(status, { overpayment, unapplied }),
    nextAction: creditBalanceNextAction(status, patientStop)
  };
}

function creditBalanceOwner(status) {
  return {
    "Overpayment Review": "Cash Posting",
    "Unapplied Cash": "Cash Posting",
    "Refund Review": "Manager / Cash",
    "Recoupment Watch": "AR Recovery",
    "No Credit Risk": "Operations"
  }[status] || "Cash Posting";
}

function creditBalanceProof(status, { overpayment = 0, unapplied = 0 } = {}) {
  if (status === "Overpayment Review") return `${money(overpayment)} overpayment proof, payer EOB/ERA, and contract check`;
  if (status === "Unapplied Cash") return `${money(unapplied)} unapplied payment tie-out to service line`;
  if (status === "Refund Review") return "Refund authorization, payer request, and manager approval evidence";
  if (status === "Recoupment Watch") return "Payer recoupment/offset notice and service-line impact proof";
  return "No credit proof required";
}

function creditBalanceNextAction(status, patientStop = false) {
  const stop = patientStop ? " Keep patient billing stopped until resolved." : "";
  if (status === "Overpayment Review") return `Validate true credit before refund, recoupment, or payer correction.${stop}`;
  if (status === "Unapplied Cash") return `Match payment/ERA detail to service line before posting final status.${stop}`;
  if (status === "Refund Review") return `Route refund packet for manager approval and no-write preview tracking.${stop}`;
  if (status === "Recoupment Watch") return `Track payer takeback against expected reimbursement and open AR before closing recovery.${stop}`;
  return "No credit action needed in active evidence.";
}

function creditBalanceTone(status) {
  if (status === "Recoupment Watch" || status === "Refund Review") return "red";
  if (status === "Overpayment Review" || status === "Unapplied Cash") return "amber";
  return "green";
}

function creditBalanceRank(status) {
  return {
    "Recoupment Watch": 0,
    "Refund Review": 1,
    "Overpayment Review": 2,
    "Unapplied Cash": 3,
    "No Credit Risk": 9
  }[status] ?? 9;
}

function renderCreditBalanceRow(row) {
  return `
    <button type="button" class="credit-balance-row credit-balance-row--${escapeHtml(row.tone)}" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Credit Status"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.status)}</small>${pill(row.patientStop ? "Patient Stop" : "Monitor", row.patientStop ? "red" : "green")}</span>
      <span data-label="Payer / Client"><strong>${escapeHtml(row.payer)}</strong><small>${escapeHtml(row.client)}</small></span>
      <span data-label="Owner"><strong>${escapeHtml(row.owner)}</strong><small>${row.status === "No Credit Risk" ? "No credit lane" : "Credit lane"}</small></span>
      <span data-label="Credit / Delta"><strong>${money(row.creditExposure)}</strong><small>Delta ${money(row.delta)}</small></span>
      <span data-label="Proof Needed">${escapeHtml(row.proofNeeded)}</span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function renderPatientResponsibilityCommand(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.patientResponsibilityCommand) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const model = patientResponsibilityCommandModel(claims);
  els.patientResponsibilityCommand.innerHTML = `
    <section class="patient-responsibility-shell" aria-label="Patient responsibility and statement command">
      <div class="patient-responsibility-head">
        <div>
          <span>Patient Responsibility / Statement Command</span>
          <strong>${money(model.totals.truePr)} true PR · ${model.totals.statementReady} statement-ready line${model.totals.statementReady === 1 ? "" : "s"}</strong>
          <p>Internal statement control: only ERA PR 1, PR 2, or PR 3 becomes patient responsibility. EZClaim patient flips, missing PR detail, a secondary on the claim, and insurance AR stay out of patient billing.</p>
        </div>
        <div class="patient-responsibility-proof">
          <span>PR Formula</span>
          <strong>${money(model.totals.delta)}</strong>
          <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
        </div>
      </div>
      <div class="patient-responsibility-summary">
        <div><span>True PR 1/2/3</span><strong>${money(model.totals.truePr)}</strong><small>ERA-backed patient responsibility</small></div>
        <div><span>Ready Statements</span><strong>${model.totals.statementReady}</strong><small>Unprinted PR lines</small></div>
        <div><span>Printed</span><strong>${model.totals.printed}</strong><small>Statement audit retained</small></div>
        <div><span>Blocked Patient Billing</span><strong>${model.totals.blocked}</strong><small>Insurance, secondary on claim, or missing PR proof</small></div>
        <div><span>Payer AR Protected</span><strong>${money(model.totals.payerAr)}</strong><small>Still insurance follow-up</small></div>
      </div>
      <div class="patient-responsibility-list">
        <div class="patient-responsibility-row patient-responsibility-header">
          <span>Claim / Service</span>
          <span>PR Status</span>
          <span>Owner</span>
          <span>PR / Payer AR</span>
          <span>Proof Needed</span>
          <span>Next Action</span>
        </div>
        ${model.rows.length ? model.rows.slice(0, 10).map(renderPatientResponsibilityRow).join("") : '<div class="patient-responsibility-empty">No patient responsibility or blocked patient-billing rows in the active filter.</div>'}
      </div>
    </section>
  `;
}

function patientResponsibilityCommandModel(claims = []) {
  const rows = (Array.isArray(claims) ? claims : [])
    .flatMap(patientResponsibilityRowsForClaim)
    .sort((a, b) => patientResponsibilityRank(a.status) - patientResponsibilityRank(b.status)
      || b.patientResponsibility - a.patientResponsibility
      || b.payerAr - a.payerAr
      || a.claimId.localeCompare(b.claimId)
      || a.serviceId.localeCompare(b.serviceId));
  const claimTotals = (Array.isArray(claims) ? claims : []).reduce((sum, claim) => {
    const financial = claimFinancialView(claim);
    sum.expected += financial.expectedReimbursement;
    sum.paid += financial.totalInsurancePaid;
    sum.outstanding += financial.sourceOutstanding;
    sum.delta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    return sum;
  }, { expected: 0, paid: 0, outstanding: 0, delta: 0 });
  const totals = rows.reduce((sum, row) => {
    sum.truePr += row.patientResponsibility;
    sum.payerAr += row.payerAr;
    if (row.status === "Ready To Statement") sum.statementReady += 1;
    if (row.status === "Statement Printed") sum.printed += 1;
    if (row.patientStop) sum.blocked += 1;
    return sum;
  }, { truePr: 0, payerAr: 0, statementReady: 0, printed: 0, blocked: 0 });
  return {
    rows,
    totals: {
      ...totals,
      truePr: moneyNumber(totals.truePr),
      payerAr: moneyNumber(totals.payerAr),
      expected: moneyNumber(claimTotals.expected),
      paid: moneyNumber(claimTotals.paid),
      outstanding: moneyNumber(claimTotals.outstanding),
      delta: moneyNumber(claimTotals.delta)
    }
  };
}

function patientResponsibilityRowsForClaim(claim = {}) {
  const financial = claimFinancialView(claim);
  // Owner ruling 2026-08-20/23, re-confirmed 2026-09-17: a real secondary on the
  // claim means the money left over belongs to the secondary payer, not the patient.
  // The report and the statement lane already clamp to $0 there; this panel used to
  // keep quoting the raw PR 1/2/3 evidence as collectible, so one claim read as
  // statement-ready here and $0 everywhere else. The ERA evidence stays visible as
  // proof text - only the collectible amount goes to zero.
  const claimHasSecondary = patientResponsibilityClaimHasSecondary(claim);
  return (claim.services || []).map((service) => {
    const serviceFinancial = serviceFinancialView(service, claim);
    const prEvidence = moneyNumber(truePatientResponsibility(service));
    const prAmount = claimHasSecondary ? 0 : prEvidence;
    const payerAr = moneyNumber(serviceFinancial.payerResponsibility || 0);
    const responsibleParty = String(service.responsibleParty || "").toLowerCase();
    const ezClaimPatientFlip = responsibleParty.includes("patient") && prAmount <= 0;
    const printed = Boolean(state.statementPrints?.[service.serviceId]);
    const missingPrDetail = (serviceFinancial.patientBalance > 0 || serviceFinancial.patientResponsibility > 0 || ezClaimPatientFlip) && prAmount <= 0;
    const hasPayerAr = payerAr > 0 || financial.sourceOutstanding > 0;
    let status = "Insurance Follow-Up";
    // The secondary test comes first: while a secondary is on the claim there is no
    // patient-billing status to reach, and "go find PR proof" would be wrong advice
    // because the proof would still clamp to $0.
    if (claimHasSecondary && (prEvidence > 0 || ezClaimPatientFlip || missingPrDetail)) status = "Secondary On Claim";
    else if (prAmount > 0 && printed) status = "Statement Printed";
    else if (prAmount > 0) status = "Ready To Statement";
    else if (ezClaimPatientFlip || missingPrDetail) status = "Blocked - PR Proof Missing";
    else if (!hasPayerAr) status = "No Patient Balance";
    const patientStop = status === "Blocked - PR Proof Missing" || status === "Insurance Follow-Up" || status === "Secondary On Claim";
    return {
      claimId: claim.claimId || "",
      serviceId: service.serviceId || "",
      client: claim.client || "Unknown client",
      patient: claim.patient || "Client",
      payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
      status,
      owner: patientResponsibilityOwner(status),
      tone: patientResponsibilityTone(status),
      patientResponsibility: prAmount,
      payerAr,
      expected: serviceFinancial.expectedReimbursement,
      paid: serviceFinancial.insurancePaid,
      outstanding: serviceFinancial.sourceOutstanding,
      delta: moneyNumber(serviceFinancial.expectedReimbursement - serviceFinancial.insurancePaid - serviceFinancial.sourceOutstanding),
      codes: prCodes(service),
      reasons: prReasons(service),
      patientStop,
      proofNeeded: patientResponsibilityProof(status, service, { prAmount, payerAr, ezClaimPatientFlip, prEvidence, printed }),
      nextAction: patientResponsibilityNextAction(status, { printed, payerAr })
    };
  }).filter((row) => row.patientResponsibility > 0 || row.payerAr > 0 || row.patientStop || row.status !== "No Patient Balance");
}

function patientResponsibilityOwner(status) {
  return {
    "Ready To Statement": "Patient Billing",
    "Statement Printed": "Patient Billing",
    "Blocked - PR Proof Missing": "Insurance AR",
    "Secondary On Claim": "Insurance AR",
    "Insurance Follow-Up": "Insurance AR",
    "No Patient Balance": "Operations"
  }[status] || "Insurance AR";
}

function patientResponsibilityProof(status, service = {}, { prAmount = 0, payerAr = 0, ezClaimPatientFlip = false, prEvidence = 0, printed = false } = {}) {
  if (status === "Ready To Statement") return `${money(prAmount)} ERA PR 1/2/3 proof: ${prCodes(service)} · ${prReasons(service)}`;
  if (status === "Secondary On Claim") {
    // A secondary can land after the patient was already billed. The status and the
    // $0 clamp are right, but hiding the print would hide that a patient holds a
    // statement for money the secondary now owes - so the print audit stays on screen.
    const evidence = prEvidence > 0
      ? `${money(prEvidence)} ERA PR 1/2/3 evidence (${prCodes(service)}) belongs to the secondary payer, not the patient`
      : "Secondary payer on the claim - nothing here is collectible from the patient";
    return printed ? `${evidence} · Statement already printed - review for correction` : evidence;
  }
  if (status === "Statement Printed") return `${money(prAmount)} printed statement audit plus PR 1/2/3 evidence`;
  if (status === "Blocked - PR Proof Missing") return ezClaimPatientFlip ? "EZClaim patient flip is not PR 1/2/3 evidence" : "Patient balance requires ERA PR 1/2/3 detail before statement";
  if (status === "Insurance Follow-Up") return `${money(payerAr)} payer responsibility remains insurance AR`;
  return "No patient statement proof required";
}

function patientResponsibilityNextAction(status, { printed = false, payerAr = 0 } = {}) {
  if (status === "Ready To Statement") return "Statement may print only after confirming PR 1/2/3 detail and no payer AR block.";
  if (status === "Secondary On Claim") return printed
    ? "Statement already went to the patient - review for a correction, then bill the secondary payer."
    : "Bill the secondary payer; do not statement the patient while a secondary is on the claim.";
  if (status === "Statement Printed") return "Keep print audit; do not reissue unless PR evidence or client request changes.";
  if (status === "Blocked - PR Proof Missing") return "Do not bill patient; route line back to insurance follow-up until PR evidence exists.";
  if (status === "Insurance Follow-Up") return payerAr > 0 ? "Continue payer follow-up; patient statement is blocked." : "Keep patient billing stopped until insurance status is resolved.";
  return printed ? "Monitor printed statement audit." : "No patient billing action.";
}

function patientResponsibilityTone(status) {
  if (status === "Ready To Statement" || status === "Statement Printed") return "green";
  if (status === "Blocked - PR Proof Missing") return "red";
  if (status === "Secondary On Claim") return "amber";
  if (status === "Insurance Follow-Up") return "amber";
  return "blue";
}

function patientResponsibilityRank(status) {
  return {
    "Blocked - PR Proof Missing": 0,
    "Ready To Statement": 1,
    "Insurance Follow-Up": 2,
    "Secondary On Claim": 2,
    "Statement Printed": 3,
    "No Patient Balance": 9
  }[status] ?? 9;
}

function renderPatientResponsibilityRow(row) {
  return `
    <button type="button" class="patient-responsibility-row patient-responsibility-row--${escapeHtml(row.tone)}" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Service"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.serviceId)} · ${escapeHtml(row.patient)}</small></span>
      <span data-label="PR Status">${pill(row.status, row.tone)}<small>${escapeHtml(row.codes)} · ${escapeHtml(row.payer)}</small></span>
      <span data-label="Owner"><strong>${escapeHtml(row.owner)}</strong><small>${row.patientStop ? "Patient billing blocked" : "Statement lane"}</small></span>
      <span data-label="PR / Payer AR"><strong>${money(row.patientResponsibility)} PR</strong><small>${money(row.payerAr)} payer AR · Delta ${money(row.delta)}</small></span>
      <span data-label="Proof Needed">${escapeHtml(row.proofNeeded)}</span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function renderPayerContactCommand(filtered = [], mode = state.rowMode || "service", sharedClaims) {
  if (!els.payerContactCommand) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  const model = payerContactCommandModel(claims);
  els.payerContactCommand.innerHTML = `
    <section class="payer-contact-shell" aria-label="Payer contact and follow-up command">
      <div class="payer-contact-head">
        <div>
          <span>Payer Contact / Follow-Up Command</span>
          <strong>${model.totals.callDue} call due · ${model.totals.noResponse} no-response claim${model.totals.noResponse === 1 ? "" : "s"}</strong>
          <p>Call-log control for payer AR: every open payer balance needs contact status, proof/reference evidence, next action, and ownership before it can leave follow-up.</p>
        </div>
        <div class="payer-contact-proof">
          <span>Payer Formula</span>
          <strong>${money(model.totals.delta)}</strong>
          <small>Expected ${money(model.totals.expected)} - Paid ${money(model.totals.paid)} - AR ${money(model.totals.outstanding)}</small>
        </div>
      </div>
      <div class="payer-contact-summary">
        <div><span>Payer AR</span><strong>${money(model.totals.payerAr)}</strong><small>Open insurance responsibility</small></div>
        <div><span>Call Due</span><strong>${model.totals.callDue}</strong><small>Due today, overdue, or no date</small></div>
        <div><span>No Response</span><strong>${model.totals.noResponse}</strong><small>Portal/EOB response missing</small></div>
        <div><span>Reference Missing</span><strong>${model.totals.referenceMissing}</strong><small>Call/ref/ticket proof gap</small></div>
        <div><span>Escalations</span><strong>${model.totals.escalations}</strong><small>High dollar or aged payer AR</small></div>
      </div>
      <div class="payer-contact-list">
        <div class="payer-contact-row payer-contact-header">
          <span>Claim / Payer</span>
          <span>Contact Status</span>
          <span>Owner</span>
          <span>AR / Formula</span>
          <span>Proof Needed</span>
          <span>Next Action</span>
        </div>
        ${model.rows.length ? model.rows.slice(0, 10).map(renderPayerContactRow).join("") : '<div class="payer-contact-empty">No payer contact work in the active filter.</div>'}
      </div>
    </section>
  `;
}

function payerContactCommandModel(claims = []) {
  const safeClaims = Array.isArray(claims) ? claims : [];
  const rows = safeClaims
    .map(payerContactRow)
    .filter((row) => row.payerAr > 0 || row.status !== "Monitor")
    .sort((a, b) => payerContactRank(a.status) - payerContactRank(b.status)
      || b.payerAr - a.payerAr
      || b.agingDays - a.agingDays
      || a.claimId.localeCompare(b.claimId));
  const claimTotals = safeClaims.reduce((sum, claim) => {
    const financial = claimFinancialView(claim);
    sum.expected += financial.expectedReimbursement;
    sum.paid += financial.totalInsurancePaid;
    sum.outstanding += financial.sourceOutstanding;
    sum.delta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    return sum;
  }, { expected: 0, paid: 0, outstanding: 0, delta: 0 });
  const totals = rows.reduce((sum, row) => {
    sum.payerAr += row.payerAr;
    if (row.callDue) sum.callDue += 1;
    if (row.status === "No Response") sum.noResponse += 1;
    if (row.referenceMissing) sum.referenceMissing += 1;
    if (row.status === "Escalate") sum.escalations += 1;
    return sum;
  }, { payerAr: 0, callDue: 0, noResponse: 0, referenceMissing: 0, escalations: 0 });
  return {
    rows,
    totals: {
      ...totals,
      payerAr: moneyNumber(totals.payerAr),
      expected: moneyNumber(claimTotals.expected),
      paid: moneyNumber(claimTotals.paid),
      outstanding: moneyNumber(claimTotals.outstanding),
      delta: moneyNumber(claimTotals.delta)
    }
  };
}

function payerContactRow(claim = {}) {
  const financial = claimFinancialView(claim);
  const text = claimOperationalText(claim);
  const profile = buildClaimStatusProfile(claim);
  const payerAr = moneyNumber(financial.payerResponsibility || financial.sourceOutstanding || 0);
  const callDue = !claim.followUpDate || isPastDue(claim) || claimSlaBucket(claim) === "Due Today";
  const hasReference = /ref(?:erence)?\s*#?|call id|ticket|confirmation|portal id|trace|claim status #|auth #|case #/.test(text);
  const noResponse = matchesQueue(claim, "missingPayerResponse") || /no response|no payment yet|payer\/status review|missing payer response|needs payer/.test(text);
  const escalation = payerAr >= 1000 || agingDays(claim) >= 120 || /escalat|supervisor|second level|medical director/.test(text);
  let status = "Monitor";
  if (escalation && payerAr > 0) status = "Escalate";
  else if (noResponse) status = "No Response";
  else if (callDue && payerAr > 0) status = "Call Due";
  else if (!hasReference && needsPayerFollowUp(claim)) status = "Reference Missing";
  else if (needsPayerFollowUp(claim)) status = "Documented Follow-Up";
  return {
    claimId: claim.claimId || "",
    client: claim.client || "Unknown client",
    payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
    status,
    tone: payerContactTone(status),
    owner: payerContactOwner(status, profile),
    payerAr,
    expected: financial.expectedReimbursement,
    paid: financial.totalInsurancePaid,
    outstanding: financial.sourceOutstanding,
    delta: moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding),
    followUpDate: claim.followUpDate || "",
    slaBucket: claimSlaBucket(claim),
    agingDays: agingDays(claim),
    callDue,
    referenceMissing: !hasReference && status !== "Monitor",
    proofNeeded: payerContactProof(status, { claim, hasReference }),
    nextAction: payerContactNextAction(status, { claim, hasReference })
  };
}

function payerContactOwner(status, profile = {}) {
  if (status === "Escalate") return "AR Manager";
  if (status === "No Response") return "Payer Follow-Up";
  if (status === "Call Due") return profile.flags?.waitingSecondary ? "Secondary AR" : "Payer Follow-Up";
  if (status === "Reference Missing") return "Biller";
  if (status === "Documented Follow-Up") return "Biller";
  return "Operations";
}

function payerContactProof(status, { claim = {}, hasReference = false } = {}) {
  const due = claim.followUpDate ? formatDate(claim.followUpDate) : "No due date";
  if (status === "Escalate") return `Payer reference, supervisor path, aging proof, and ${money(claimFinancialView(claim).sourceOutstanding)} AR tie-out`;
  if (status === "No Response") return "Payer portal screenshot, EOB/ERA status, or call reference proving no response";
  if (status === "Call Due") return `Contact attempt due ${due}; add payer rep, reference, result, and next date`;
  if (status === "Reference Missing") return "Existing note/action needs call reference, ticket, portal confirmation, or EOB trace";
  if (status === "Documented Follow-Up") return hasReference ? "Reference exists; verify result and next payer date" : "Document payer contact result";
  return "No payer contact proof required";
}

function payerContactNextAction(status, { claim = {}, hasReference = false } = {}) {
  const nextDate = claim.followUpDate ? formatDate(claim.followUpDate) : "today";
  if (status === "Escalate") return "Escalate payer AR with manager review; keep expected-paid-AR delta visible until resolved.";
  if (status === "No Response") return "Call or portal-check payer, capture response/reference, then reset follow-up date.";
  if (status === "Call Due") return `Complete payer contact ${nextDate}, then update action, proof, owner, and next follow-up.`;
  if (status === "Reference Missing") return "Add the payer reference before marking reviewed, appealed, or complete.";
  if (status === "Documented Follow-Up") return hasReference ? "Verify the documented payer result still supports current AR." : "Document payer result before handoff.";
  return "Monitor only.";
}

function payerContactTone(status) {
  if (status === "Escalate" || status === "No Response") return "red";
  if (status === "Call Due" || status === "Reference Missing") return "amber";
  if (status === "Documented Follow-Up") return "blue";
  return "green";
}

function payerContactRank(status) {
  return {
    "Escalate": 0,
    "No Response": 1,
    "Call Due": 2,
    "Reference Missing": 3,
    "Documented Follow-Up": 4,
    "Monitor": 9
  }[status] ?? 9;
}

function renderPayerContactRow(row) {
  return `
    <button type="button" class="payer-contact-row payer-contact-row--${escapeHtml(row.tone)}" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Payer"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.payer)} · ${escapeHtml(row.client)}</small></span>
      <span data-label="Contact Status">${pill(row.status, row.tone)}<small>${escapeHtml(row.slaBucket)} · ${formatNounCount(row.agingDays, "day")}</small></span>
      <span data-label="Owner"><strong>${escapeHtml(row.owner)}</strong><small>${row.referenceMissing ? "Reference proof gap" : "Contact lane"}</small></span>
      <span data-label="AR / Formula"><strong>${money(row.payerAr)} AR</strong><small>Delta ${money(row.delta)}</small></span>
      <span data-label="Proof Needed">${escapeHtml(row.proofNeeded)}</span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function paymentReconciliationGroups(claims = []) {
  const safeClaims = Array.isArray(claims) ? claims : [];
  const definitions = [
    {
      key: "underpayment",
      label: "Underpayment Recovery",
      tone: "red",
      owner: "AR Recovery",
      nextStep: "Compare allowed amount, ERA, and fee schedule before appeal or corrected-payment follow-up.",
      reason: (claim) => `${money(claimUnderpayment(claim))} payer shortfall`
    },
    {
      key: "overpayment",
      label: "Overpayment / Credit Review",
      tone: "amber",
      owner: "Payment Posting",
      nextStep: "Confirm true credit before refund, recoupment, or payer correction.",
      reason: (claim) => `${money(claimOverpayment(claim))} possible overpayment`
    },
    {
      key: "unapplied",
      label: "Unapplied Payment Variance",
      tone: "blue",
      owner: "Cash Posting",
      nextStep: "Tie payment detail back to service line, payer, and remittance before closing the claim.",
      reason: (claim) => `${money(claimFinancialView(claim).unappliedPayments)} unapplied`
    },
    {
      key: "attribution",
      label: "Attribution Review",
      tone: "slate",
      owner: "Payer Sequencing",
      nextStep: "Resolve primary/secondary payer sequence before final paid/outstanding decisions.",
      reason: (claim) => paymentAttributionExplanation(claim)
    }
  ];
  return definitions.map((definition) => {
    const rows = safeClaims
      .filter((claim) => paymentReconciliationAmountForGroup(definition.key, claim) > 0)
      .map((claim) => {
        const financial = claimFinancialView(claim);
        return {
          claim,
          claimId: claim.claimId || "",
          payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
          client: claim.client || "Unknown client",
          amount: paymentReconciliationAmountForGroup(definition.key, claim),
          outstanding: financial.sourceOutstanding,
          expected: financial.expectedReimbursement,
          paid: financial.totalInsurancePaid,
          guardSummary: calculationGuardSummary(evaluateClaimCalculationGuards(claim)),
          reason: definition.reason(claim)
        };
      })
      .sort((a, b) => b.amount - a.amount || b.outstanding - a.outstanding);
    return {
      ...definition,
      rows,
      amount: moneyNumber(rows.reduce((sum, row) => sum + row.amount, 0))
    };
  }).sort((a, b) => b.amount - a.amount || b.rows.length - a.rows.length);
}

function paymentReconciliationAmountForGroup(key, claim) {
  const financial = claimFinancialView(claim);
  if (key === "underpayment") return moneyNumber(financial.underpayment);
  if (key === "overpayment") return moneyNumber(financial.overpayment);
  if (key === "unapplied") return moneyNumber(financial.unappliedPayments);
  if (key === "attribution") {
    const status = String(claim?.paymentAttribution?.status || "");
    const needsReview = sameExternalPayerConflict(claim) || financial.unmatchedPaid > 0 || /manual review|conflict|unmatched/i.test(status);
    return needsReview ? moneyNumber(Math.max(financial.unmatchedPaid, financial.sourceOutstanding, financial.expectedReimbursement - financial.totalInsurancePaid, 1)) : 0;
  }
  return 0;
}

function renderPaymentReconciliationGroup(group) {
  const topRows = group.rows.slice(0, 3);
  const firstClaimId = topRows[0]?.claimId || "";
  return `
    <article class="payment-recon-card payment-recon-card--${escapeHtml(group.tone)}">
      <div class="payment-recon-card-head">
        <span>${escapeHtml(group.label)}</span>
        <strong>${escapeHtml(money(group.amount))}</strong>
      </div>
      <div class="payment-recon-meta">
        <span>${formatNounCount(group.rows.length, "claim")}</span>
        <span>${escapeHtml(group.owner)}</span>
      </div>
      <p>${escapeHtml(group.nextStep)}</p>
      <div class="payment-recon-rows">
        ${topRows.length ? topRows.map(renderPaymentReconciliationRow).join("") : `<div class="payment-recon-empty">No exceptions in current filter.</div>`}
      </div>
      <div class="payment-recon-actions">
        ${firstClaimId ? `<button type="button" class="small-button" data-payment-open="${escapeHtml(firstClaimId)}">Review First</button>` : ""}
        <button type="button" class="ghost-button" data-payment-focus="${escapeHtml(group.key)}">${escapeHtml(paymentReconciliationFocusLabel(group.key))}</button>
      </div>
    </article>
  `;
}

function renderPaymentReconciliationRow(row) {
  return `
    <button type="button" class="payment-recon-row" data-payment-open="${escapeHtml(row.claimId)}">
      <span>
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.client)} · ${escapeHtml(row.payer)}</small>
      </span>
      <span>
        <strong>${escapeHtml(money(row.amount))}</strong>
        <small>${escapeHtml(row.reason)} · ${escapeHtml(row.guardSummary)}</small>
      </span>
    </button>
  `;
}

function paymentReconciliationFocusLabel(key) {
  if (key === "underpayment") return "Filter Underpaid";
  if (key === "overpayment") return "Filter Overpaid";
  if (key === "unapplied") return "Open Unapplied";
  if (key === "attribution") return "Open Attribution";
  return "Focus";
}

function applyPaymentReconciliationFocus(key) {
  const claims = claimsFromTrackerRows(getFilteredServiceRows(buildTrackerQueryRequest("service")), "service");
  const groups = paymentReconciliationGroups(claims);
  const group = groups.find((row) => row.key === key);
  const firstClaimId = group?.rows[0]?.claimId;
  if (key === "underpayment" && els.matchFilter) {
    els.matchFilter.value = "Under Paid";
    resetTrackerPage();
    renderClaimsTable();
    return;
  }
  if (key === "overpayment" && els.matchFilter) {
    els.matchFilter.value = "Over Paid";
    resetTrackerPage();
    renderClaimsTable();
    return;
  }
  if (firstClaimId) focusWorkClaim(firstClaimId, { open: true });
}

function billingOperationsRecommendations(openClaims, visibleClaims) {
  const workFirst = openClaims.filter((claim) => matchesQueue(claim, "workFirst"));
  const agingDanger = openClaims.filter((claim) => agingDays(claim) >= 90);
  const stalled = openClaims.filter((claim) => matchesQueue(claim, "stale"));
  const underpaid = openClaims.filter((claim) => claimUnderpayment(claim) > 0);
  const payerRows = payerWorklistRows(openClaims);
  const topPayer = payerRows[0];
  const topClaim = openClaims.slice().sort(sortClaims)[0];
  const topUnderpaid = underpaid.slice().sort((a, b) => claimUnderpayment(b) - claimUnderpayment(a))[0];
  const visibleImpact = sumClaimOutstanding(visibleClaims);
  const openAr = sumClaimOutstanding(openClaims);
  const agingAr = sumClaimOutstanding(agingDanger);
  const stalledAr = sumClaimOutstanding(stalled);
  const underpaymentAr = sumClaimMoney(underpaid, claimUnderpayment);
  const headline = topClaim
    ? {
      title: `Work ${topClaim.claimId} first`,
      value: `${workPriorityScore(topClaim)} ${priorityBand(workPriorityScore(topClaim))}`,
      detail: `${workReason(topClaim)} · ${money(claimOutstanding(topClaim))} open · ${topClaim.primaryPayer || "Unknown payer"}`,
      tone: workPriorityScore(topClaim) >= 80 ? "red" : "amber",
      action: { kind: "focus", key: "dueNow", label: "Focus due work" }
    }
    : {
      title: "No open AR in this view",
      value: "Clear",
      detail: "Current filters do not expose open operational AR.",
      tone: "green",
      action: { kind: "queue", key: "all", label: "Clear queue" }
    };
  return [
    headline,
    {
      title: "Highest Impact",
      value: money(openAr),
      detail: `${formatNounCount(openClaims.length, "open claim")} · visible page ${money(visibleImpact)}`,
      tone: openAr > 0 ? "blue" : "green",
      action: { kind: "focus", key: "highBalance", label: "High balance" }
    },
    {
      title: "Work First Queue",
      value: String(workFirst.length),
      detail: `${money(sumClaimOutstanding(workFirst))} tied to urgent work signals`,
      tone: workFirst.length ? "red" : "green",
      action: { kind: "queue", key: "workFirst", label: "Open queue" }
    },
    {
      title: "Aging Danger",
      value: money(agingAr),
      detail: `${formatNounCount(agingDanger.length, "claim")} at 91+ days or 120+ aging`,
      tone: agingDanger.length ? "red" : "green",
      action: { kind: "focus", key: "dueNow", label: "Due work" }
    },
    {
      title: "Stalled Too Long",
      value: String(stalled.length),
      detail: `${money(stalledAr)} with no recent touch or weak activity evidence`,
      tone: stalled.length ? "amber" : "green",
      action: { kind: "focus", key: "untouched", label: "Untouched" }
    },
    {
      title: "Payer Pressure",
      value: topPayer ? topPayer.payer : "Clear",
      detail: topPayer ? `${formatNounCount(topPayer.claims, `${topPayer.queue} claim`)} · ${money(topPayer.owed || topPayer.outstanding || 0)}` : "No concentrated payer follow-up pressure",
      tone: topPayer ? "blue" : "green",
      action: { kind: "focus", key: "payer", label: "Payer work" }
    },
    {
      title: "Likely Underpaid",
      value: money(underpaymentAr),
      detail: topUnderpaid ? `${formatNounCount(underpaid.length, "claim")} · top: ${topUnderpaid.claimId} / ${topUnderpaid.primaryPayer || "Unknown payer"}` : "No underpayment exposure in this filter",
      tone: underpaid.length ? "amber" : "green",
      action: { kind: "focus", key: "highBalance", label: "Review impact" }
    }
  ];
}

function renderOperationsRecommendation(item) {
  return `
    <article class="ops-command-card ops-command-card--${escapeHtml(item.tone || "blue")}">
      <span>${escapeHtml(item.title)}</span>
      <strong>${escapeHtml(String(item.value))}</strong>
      <p>${escapeHtml(item.detail || "")}</p>
      ${item.action ? operationsActionButton(item.action) : ""}
    </article>
  `;
}

function operationsActionButton(action) {
  if (action.kind === "queue") {
    return `<button class="small-button" type="button" data-ops-queue="${escapeHtml(action.key)}">${escapeHtml(action.label || "Open")}</button>`;
  }
  return `<button class="small-button" type="button" data-ops-focus="${escapeHtml(action.key)}">${escapeHtml(action.label || "Focus")}</button>`;
}

function workbenchClaimIdsFromRows(rows = [], mode = state.rowMode || "service") {
  const safeRows = Array.isArray(rows) ? rows : [];
  const ids = mode === "service"
    ? safeRows.map((row) => row?.claim?.claimId || row?.claimId)
    : safeRows.map((row) => row?.claimId);
  return Array.from(new Set(ids.filter(Boolean).map(String)));
}

function focusedClaimIdForRows(rows = [], mode = state.rowMode || "service") {
  const ids = workbenchClaimIdsFromRows(rows, mode);
  if (!ids.length) return "";
  if (state.focusedWorkClaimId && ids.includes(state.focusedWorkClaimId)) return state.focusedWorkClaimId;
  return ids[0];
}

function updateFocusedWorkControls(rows = [], mode = state.rowMode || "service") {
  const ids = workbenchClaimIdsFromRows(rows, mode);
  const focusedId = focusedClaimIdForRows(rows, mode);
  const focusedClaim = getClaim(focusedId);
  const index = ids.indexOf(focusedId);
  const disabled = !ids.length;
  if (els.prevWorkClaimBtn) els.prevWorkClaimBtn.disabled = disabled;
  if (els.nextWorkClaimBtn) els.nextWorkClaimBtn.disabled = disabled;
  if (els.openFocusedClaimBtn) els.openFocusedClaimBtn.disabled = disabled || !focusedId;
  if (els.focusedClaimText) {
    els.focusedClaimText.textContent = focusedId
      ? `${index + 1}/${ids.length} · ${focusedId}${focusedClaim ? ` · ${money(claimOutstanding(focusedClaim))}` : ""}`
      : "No claim focus";
  }
}

function currentWorkbenchRows(mode = state.rowMode || "service") {
  const query = buildTrackerQueryRequest(mode);
  const apiPage = trackerApiPageForQuery(query);
  if (apiPage) return { mode, rows: apiPage.rows, serverBacked: true };
  return {
    mode,
    rows: mode === "service" ? getFilteredServiceRows(query) : getFilteredClaims(query),
    serverBacked: false
  };
}

function focusWorkClaim(claimId, { open = false, announce = true } = {}) {
  if (!claimId) return null;
  const { mode, rows, serverBacked } = currentWorkbenchRows();
  const ids = workbenchClaimIdsFromRows(rows, mode);
  if (!ids.includes(String(claimId))) return null;
  state.focusedWorkClaimId = String(claimId);
  if (!serverBacked) {
    const rowIndex = rows.findIndex((row) => (mode === "service" ? row?.claim?.claimId : row?.claimId) === String(claimId));
    if (rowIndex >= 0) state.trackerPage = Math.floor(rowIndex / trackerPageSizeValue(state.trackerPageSize)) + 1;
  }
  saveState();
  renderClaimsTable();
  scrollFocusedClaimIntoView();
  if (open) openDrawer(String(claimId));
  else if (announce) showToast(`Focused ${claimId}.`);
  return String(claimId);
}

function navigateWorkClaim(direction = "next") {
  const { mode, rows } = currentWorkbenchRows();
  const ids = workbenchClaimIdsFromRows(rows, mode);
  if (!ids.length) {
    showToast("No claims match the current work view.");
    return null;
  }
  const current = focusedClaimIdForRows(rows, mode);
  const index = Math.max(0, ids.indexOf(current));
  const delta = direction === "previous" ? -1 : 1;
  const nextIndex = (index + delta + ids.length) % ids.length;
  return focusWorkClaim(ids[nextIndex], { announce: true });
}

function openFocusedWorkClaim() {
  const { mode, rows } = currentWorkbenchRows();
  const focusedId = focusedClaimIdForRows(rows, mode);
  if (!focusedId) return showToast("No focused claim to open.");
  return focusWorkClaim(focusedId, { open: true, announce: false });
}

function handleWorkbenchKeyboardNavigation(event) {
  if (!event || state.view !== "workbench" || event.defaultPrevented) return;
  if (event.key === "Escape" && slicerDraftKey) {
    event.preventDefault();
    // Escape is the reader saying "put it back", so it reverts an auto-applied
    // burst rather than only dropping the staged half of one.
    discardSlicerDraft({ close: true, revert: true });
    return;
  }
  const target = event.target;
  const tag = String(target?.tagName || "").toLowerCase();
  if (target?.isContentEditable || ["input", "select", "textarea", "button"].includes(tag)) return;
  if (event.altKey && event.key === "ArrowDown") {
    event.preventDefault();
    navigateWorkClaim("next");
  } else if (event.altKey && event.key === "ArrowUp") {
    event.preventDefault();
    navigateWorkClaim("previous");
  } else if (event.altKey && event.key === "Enter") {
    event.preventDefault();
    openFocusedWorkClaim();
  } else if (event.key === "Escape" && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey) {
    event.preventDefault();
    resetWorkbenchToAllData();
  }
}

function scrollFocusedClaimIntoView() {
  if (typeof document === "undefined" || !state.focusedWorkClaimId) return;
  const run = typeof window !== "undefined" && typeof window.requestAnimationFrame === "function" ? window.requestAnimationFrame.bind(window) : (callback) => callback();
  run(() => {
    const row = document.querySelector(`[data-row-claim-id="${escapeCss(state.focusedWorkClaimId)}"]`);
    row?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  });
}

const BILLER_FOCUS_ACTIONS = [
  { key: "dueNow", label: "Due Now", tone: "red", queueMatch: null, counter: (claims) => claims.filter(isPastDue).length },
  { key: "denials", label: "Denials", tone: "red", queueMatch: "denied", counter: (claims) => claims.filter((c) => matchesQueue(c, "denied")).length },
  { key: "payer", label: "Payer Work", tone: "amber", queueMatch: "payerFollowUp", counter: (claims) => claims.filter((c) => matchesQueue(c, "payerFollowUp")).length },
  { key: "secondary", label: "Secondary", tone: "amber", queueMatch: "waitingSecondary", counter: (claims) => claims.filter((c) => matchesQueue(c, "waitingSecondary")).length },
  { key: "highBalance", label: "High Balance", tone: "amber", queueMatch: "highDollar", counter: (claims) => claims.filter((c) => matchesQueue(c, "highDollar")).length },
  { key: "untouched", label: "Untouched", tone: "slate", queueMatch: "stale", counter: (claims) => claims.filter((c) => matchesQueue(c, "stale")).length },
  { key: "client", label: "Client Action", tone: "blue", queueMatch: "clientAction", counter: (claims) => claims.filter((c) => matchesQueue(c, "clientAction")).length }
];

function activeBillerFocusKey() {
  const queue = state.queueFilter || "all";
  if (queue === "all") {
    const dueSlicer = Array.isArray(state.slicers?.claimDue) ? state.slicers.claimDue : [];
    if (dueSlicer.includes("PAST DUE")) return "dueNow";
    return null;
  }
  const match = BILLER_FOCUS_ACTIONS.find((focus) => focus.queueMatch === queue);
  return match ? match.key : null;
}

function renderBillerFocusActions(claims) {
  const host = document.getElementById("billerFocusActions");
  if (!host) return;
  const active = activeBillerFocusKey();
  const visibleFocusActions = trackerApiEnabled()
    ? BILLER_FOCUS_ACTIONS.filter((focus) => SERVER_BILLER_FOCUS_KEYS.has(focus.key))
    : BILLER_FOCUS_ACTIONS;
  host.innerHTML = visibleFocusActions.map((focus) => {
    const count = focus.counter(claims);
    const isActive = focus.key === active;
    const isEmpty = count === 0;
    return `<button type="button" class="focus-action focus-action--${focus.tone}${isActive ? " is-active" : ""}${isEmpty ? " is-empty" : ""}" data-quick-focus="${focus.key}" aria-pressed="${isActive}">
      <span class="focus-action-label">${escapeHtml(focus.label)}</span>
      <span class="focus-action-count" aria-label="${formatNounCount(count, "item")}">${count}</span>
    </button>`;
  }).join("");
}

function renderBillerWorkstrip(filtered = [], pageWindow = trackerPageWindow(filtered), mode = state.rowMode || "service", sharedClaims) {
  if (!els.billerTaskSummary) return;
  const claims = sharedClaims || claimsFromTrackerRows(filtered, mode);
  // Keep quick-focus pressed state synchronized even while the filtered page is
  // loading or legitimately empty. Returning before this render left Due Now
  // visibly unpressed although its claimDue predicate was already active.
  renderBillerFocusActions(claims);
  const lane = typeof trackerApiLaneState === "function" ? trackerApiLaneState() : "";
  if (typeof trackerApiEnabled === "function" && trackerApiEnabled() && (lane === "pending" || lane === "unasked")) {
    els.billerTaskSummary.innerHTML = `<div class="workstrip-empty"><span>Filtered AR</span><strong>—</strong><small>Loading this view. This is not $0 on the book.</small></div>`;
    return;
  }
  if (!claims.length) {
    els.billerTaskSummary.innerHTML = `<div class="workstrip-empty"><span>Filtered AR</span><strong>—</strong><small>Nothing in this view. That is not $0 on the book.</small></div>`;
    return;
  }
  const visibleClaims = visibleClaimsFromPage(pageWindow, mode);
  const owed = sumClaimOutstanding(claims);
  const visibleOwed = sumClaimOutstanding(visibleClaims);
  const dueNow = claims.filter(isPastDue).length;
  const critical = claims.filter((claim) => workPriorityScore(claim) >= 80).length;
  const untouched = claims.filter((claim) => matchesQueue(claim, "stale")).length;
  const topClaim = claims.slice().sort(sortClaims)[0];
  const topPayer = topPayerWorkload(claims);
  const focusedId = focusedClaimIdForRows(filtered, mode);
  if (focusedId) state.focusedWorkClaimId = focusedId;
  updateFocusedWorkControls(filtered, mode);
  const nextThree = claims.slice().sort(sortClaims).slice(0, 3);
  els.billerTaskSummary.innerHTML = [
    ["Filtered AR", money(owed)],
    ["Visible Page", money(visibleOwed)],
    ["Due Now", dueNow],
    ["Critical", critical],
    ["Untouched", untouched],
    ["Top Payer", topPayer ? `${topPayer.payer} · ${topPayer.claims}` : "None"],
    ["Focused", focusedId || "None"],
    ["Next Claim", topClaim ? `${topClaim.claimId} · ${money(claimOutstanding(topClaim))}` : "None"]
  ].map(([label, value]) => {
    const openId = label === "Next Claim" && topClaim ? topClaim.claimId : (label === "Focused" && focusedId ? focusedId : "");
    const body = openId
      ? `<button type="button" class="workstrip-open" data-workstrip-open="${escapeHtml(openId)}">${escapeHtml(String(value))}</button>`
      : `<strong>${escapeHtml(String(value))}</strong>`;
    return `<div><span>${escapeHtml(label)}</span>${body}</div>`;
  }).join("") + (nextThree.length ? `<div class="workstrip-today"><span>Work these first</span><ol>${nextThree.map((claim) => `<li><button type="button" class="workstrip-open" data-workstrip-open="${escapeHtml(claim.claimId)}">${escapeHtml(claim.claimId)}</button> · ${escapeHtml(claim.primaryPayer || "No payer")} · ${money(claimOutstanding(claim))}</li>`).join("")}</ol></div>` : "");
  if (typeof els.billerTaskSummary.querySelectorAll === "function") {
    Array.from(els.billerTaskSummary.querySelectorAll("[data-workstrip-open]")).forEach((btn) => {
      btn.addEventListener("click", () => openDrawer(btn.getAttribute("data-workstrip-open")));
    });
  }
}

// Microsoft Planner-style task board for the OffshoreBiller. Replaces
// the All Data table with a 4-column kanban:
//   To Do - In Progress - Submitted For Review - Bounced Back
// Each card = one routed claim. Click → opens the drawer.
function renderOffshorePlannerBoard() {
  if (!els.claimRows) return;
  // "My Tasks" means MY tasks. Since the offshore autonomy change getClaims()
  // returns the biller's whole org-scoped book, so this board narrows to the
  // claims routed to the active user itself — a board AFFORDANCE on top of the
  // server's tenant scope, never a security boundary (same shape as
  // renderAssignments' boardOwner filter).
  const boardOwner = String((currentAccessProfile().scope || {}).assignedName || currentUser()?.name || "").trim();
  const claims = getClaims().filter((claim) => String(claim.assignedTo || "").trim() === boardOwner);
  // Bucket each claim by its current routing status.
  const buckets = [
    { key: "todo", label: "To Do", description: "Claims routed to you — pick the next one to work", tone: "blue", match: (c) => /Routed/i.test(c.assignmentStatus || "") || c.assignmentStatus === "Assigned" || !c.assignmentStatus },
    { key: "inProgress", label: "In Progress", description: "You've started but haven't submitted yet", tone: "amber", match: (c) => /In Progress|Working/i.test(c.assignmentStatus || "") },
    { key: "submitted", label: "Approval Pending", description: "Sent to your supervisor for a decision", tone: "teal", match: (c) => /Submitted/i.test(c.assignmentStatus || "") },
    { key: "bounced", label: "Changes Required", description: "Your supervisor returned it — another pass is required", tone: "red", match: (c) => /Bounced/i.test(c.assignmentStatus || "") }
  ];
  buckets.forEach((b) => { b.claims = []; });
  claims.forEach((claim) => {
    const hit = buckets.find((b) => b.match(claim));
    (hit || buckets[0]).claims.push(claim);
  });
  // Inject the board into the workbench so the existing chrome (header,
  // demo callout, drawer) keeps working. We hide the original table
  // wrapper and render the board in its place.
  const tableWrap = document.getElementById("trackerTableWrap");
  if (tableWrap) tableWrap.style.display = "none";
  let board = document.getElementById("offshorePlannerBoard");
  if (!board) {
    board = document.createElement("div");
    board.id = "offshorePlannerBoard";
    board.className = "offshore-planner-board";
    if (tableWrap && tableWrap.parentElement) {
      tableWrap.parentElement.insertBefore(board, tableWrap);
    }
  }
  board.innerHTML = `
    <header class="offshore-planner-head">
      <h2>My Tasks</h2>
      <p>Claims routed to you for review · ${claims.length} total</p>
    </header>
    <div class="offshore-planner-columns">
      ${buckets.map((bucket) => `
        <section class="offshore-planner-col offshore-planner-col--${escapeHtml(bucket.tone)}">
          <header class="offshore-planner-col-head">
            <strong>${escapeHtml(bucket.label)}</strong>
            <span class="offshore-planner-col-count">${bucket.claims.length}</span>
          </header>
          <p class="offshore-planner-col-desc">${escapeHtml(bucket.description)}</p>
          <div class="offshore-planner-col-cards">
            ${bucket.claims.length === 0
              ? `<div class="offshore-planner-empty">No tasks in this lane.</div>`
              : bucket.claims.map(offshoreTaskCardHtml).join("")}
          </div>
        </section>
      `).join("")}
    </div>
  `;
  // Inline action edits + date edits — same handlers as the main grid.
  board.querySelectorAll(".offshore-task-action[data-action-edit]").forEach((sel) => {
    sel.addEventListener("change", (e) => {
      e.stopPropagation();
      try { openFollowUpEditor(sel.dataset.actionEdit, { orgId: sel.dataset.actionOrgId || "", serviceId: sel.dataset.serviceId || "", action: sel.value, sourceSurface: "assignment-board-action" }); } catch (_) {}
    });
    sel.addEventListener("click", (e) => e.stopPropagation());
  });
  board.querySelectorAll(".offshore-task-date[data-date-edit]").forEach((inp) => {
    inp.addEventListener("change", (e) => {
      e.stopPropagation();
      try { openFollowUpEditor(inp.dataset.dateEdit, { orgId: inp.dataset.dateOrgId || "", serviceId: inp.dataset.serviceId || "", followUpDate: inp.value, sourceSurface: "assignment-board-date" }); } catch (_) {}
    });
    inp.addEventListener("click", (e) => e.stopPropagation());
  });
  // "Open detail →" button opens the drawer
  board.querySelectorAll("[data-task-open]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      try { openDrawer(btn.dataset.taskOpen); } catch (_) {}
    });
  });
  // Per-card status progression — Start Work + Submit for Review,
  // right on the kanban card. No need to open the drawer for the
  // common "move it forward" flow.
  board.querySelectorAll("[data-status-start]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const cid = btn.dataset.statusStart;
      const orgId = btn.dataset.statusOrgId || "";
      startAssignmentWork(cid, { orgId });
      renderOffshorePlannerBoard();
    });
  });
  board.querySelectorAll("[data-status-withdraw]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      withdrawSubmittedReview(btn.dataset.statusWithdraw, { orgId: btn.dataset.statusOrgId || "" });
    });
  });
  // Submit for Review: reveals an inline note form right on the card
  board.querySelectorAll("[data-status-submit]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const cid = btn.dataset.statusSubmit;
      const form = board.querySelector(`[data-submit-form="${escapeCss(cid)}"]`);
      form?.classList.remove("hidden");
      form?.querySelector("textarea")?.focus();
    });
  });
  board.querySelectorAll("[data-submit-next-action]").forEach((select) => {
    select.addEventListener("change", (e) => {
      e.stopPropagation();
      const cid = select.dataset.submitNextAction;
      const dateInput = board.querySelector(`[data-submit-next-date="${escapeCss(cid)}"]`);
      if (dateInput) dateInput.value = defaultFollowUpDateForAction(select.value);
    });
    select.addEventListener("click", (e) => e.stopPropagation());
  });
  board.querySelectorAll("[data-submit-next-date]").forEach((input) => {
    input.addEventListener("click", (e) => e.stopPropagation());
    input.addEventListener("change", (e) => e.stopPropagation());
  });
  board.querySelectorAll("[data-submit-cancel]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const form = board.querySelector(`[data-submit-form="${escapeCss(btn.dataset.submitCancel)}"]`);
      form?.classList.add("hidden");
    });
  });
  board.querySelectorAll("[data-submit-confirm]").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const cid = btn.dataset.submitConfirm;
      const form = board.querySelector(`[data-submit-form="${escapeCss(cid)}"]`);
      const note = (form?.querySelector("textarea")?.value || "").trim();
      const nextAction = board.querySelector(`[data-submit-next-action="${escapeCss(cid)}"]`)?.value || "";
      const nextDate = board.querySelector(`[data-submit-next-date="${escapeCss(cid)}"]`)?.value || "";
      if (await submitForReview(cid, note, nextAction, nextDate, { orgId: btn.dataset.submitOrgId || "" })) {
        renderOffshorePlannerBoard();
      }
    });
  });
  // Update status pills in topbar — the rest of the workbench chrome
  // is already hidden by body[data-role=OffshoreBiller] CSS.
  if (els.workbenchStatus) {
    els.workbenchStatus.innerHTML = `
      <span class="sheet-status-chip"><em>Total</em><strong>${claims.length}</strong></span>
      ${buckets.map((b) => `<span class="sheet-status-chip"><em>${escapeHtml(b.label)}</em><strong>${b.claims.length}</strong></span>`).join("")}
    `;
  }
}

function offshoreTaskCardHtml(claim) {
  const fin = (() => { try { return claimFinancialView(claim); } catch (_) { return {}; } })();
  const due = claim.followUpDate ? formatDate(claim.followUpDate) : "Not scheduled";
  const pastDue = (() => { try { return isPastDue(claim); } catch (_) { return false; } })();
  const owed = fin && Number.isFinite(fin.sourceOutstanding) ? money(fin.sourceOutstanding) : "—";
  // Inline editors so an offshore biller can change action + date right
  // on the card. Per user direction: 'the follow-up action should always
  // be able to be edited.' Click anywhere except the inputs → drawer.
  const rules = (() => { try { return getSelectableActionRules(); } catch (_) { return []; } })();
  const currentActionOptions = rules.map((r) =>
    `<option value="${escapeHtml(r.action)}"${r.action === claim.action ? " selected" : ""}>${escapeHtml(r.action)}</option>`
  ).join("");
  const blankActionOptions = rules.map((r) =>
    `<option value="${escapeHtml(r.action)}">${escapeHtml(r.action)}</option>`
  ).join("");
  return `
    <article class="offshore-task-card${pastDue ? " is-pastdue" : ""}" data-task-claim="${escapeHtml(claim.claimId)}" aria-label="Claim ${escapeHtml(claim.claimId)}">
      <div class="offshore-task-card-head">
        <strong>${escapeHtml(claim.claimId)}</strong>
        ${pastDue ? '<span class="offshore-task-flag">Past due</span>' : ""}
      </div>
      <div class="offshore-task-patient">${escapeHtml(claim.patient || "Patient unknown")}</div>
      <div class="offshore-task-meta">
        <span>${escapeHtml(claim.primaryPayer || "Payer unknown")}</span>
      </div>
      ${/Bounced/i.test(String(claim.assignmentStatus || "")) && claimBounceReason(claim) ? `
      <div class="offshore-bounce-callout" role="note">
        <span class="offshore-bounce-callout-label">Changes required</span>
        <p>${escapeHtml(claimBounceReason(claim))}</p>
      </div>` : ""}
      <div class="offshore-task-edit">
        <label class="offshore-task-edit-row">
          <span class="offshore-task-edit-label">Action</span>
          <select class="offshore-task-action grid-select" data-action-edit="${escapeHtml(claim.claimId)}" data-action-org-id="${escapeHtml(claim.orgId || "")}" data-service-id="${escapeHtml(String(claim.services?.[0]?.serviceId || "").trim())}" aria-label="Follow-up action for ${escapeHtml(claim.claimId)}">
            <option value="">Choose action…</option>
            ${currentActionOptions}
          </select>
        </label>
        <label class="offshore-task-edit-row">
          <span class="offshore-task-edit-label">Date</span>
          <input class="offshore-task-date grid-date" type="date" data-date-edit="${escapeHtml(claim.claimId)}" data-date-org-id="${escapeHtml(claim.orgId || "")}" data-service-id="${escapeHtml(String(claim.services?.[0]?.serviceId || "").trim())}" value="${escapeHtml(claim.followUpDate || "")}" aria-label="Follow-up date for ${escapeHtml(claim.claimId)}">
        </label>
      </div>
      <footer class="offshore-task-card-foot">
        <span class="offshore-task-due${pastDue ? " is-pastdue" : ""}">Due ${escapeHtml(due)}</span>
        <span class="offshore-task-owed">${escapeHtml(owed)}</span>
      </footer>
      ${offshoreCardStatusButtons(claim)}
      <div class="offshore-task-submit-form hidden" data-submit-form="${escapeHtml(claim.claimId)}">
        <textarea class="offshore-task-submit-note" rows="2" placeholder="What did you do? What does stateside need to know?"></textarea>
        <label class="offshore-task-submit-field">
          <span>Next follow-up action</span>
          <select class="grid-select" data-submit-next-action="${escapeHtml(claim.claimId)}" data-current-action="${escapeHtml(claim.action || "")}">
            <option value="">Choose next action...</option>
            ${blankActionOptions}
          </select>
        </label>
        <label class="offshore-task-submit-field">
          <span>Next due date</span>
          <input class="grid-date" type="date" data-submit-next-date="${escapeHtml(claim.claimId)}" value="">
        </label>
        <div class="offshore-task-submit-actions">
          <button type="button" class="offshore-task-submit-cancel" data-submit-cancel="${escapeHtml(claim.claimId)}">Cancel</button>
          <button type="button" class="offshore-task-submit-confirm" data-submit-confirm="${escapeHtml(claim.claimId)}" data-submit-org-id="${escapeHtml(claim.orgId || "")}">Send to Review</button>
        </div>
      </div>
      <button type="button" class="offshore-task-open" data-task-open="${escapeHtml(claim.claimId)}">Open detail →</button>
    </article>
  `;
}

// Per-card action buttons. The set depends on the claim's current
// lane — Planner-style "advance the task forward" UX.
function offshoreCardStatusButtons(claim) {
  const status = String(claim.assignmentStatus || "");
  const isInProgress = /In Progress|Working/i.test(status);
  const isSubmitted = /Submitted/i.test(status);
  const isBounced = /Bounced/i.test(status);
  if (isSubmitted) {
    return `<div class="offshore-task-status-bar offshore-task-status-bar--awaiting"><span>Approval pending</span><button type="button" class="offshore-task-status-btn" data-status-withdraw="${escapeHtml(claim.claimId)}" data-status-org-id="${escapeHtml(claim.orgId || "")}">Withdraw and edit</button></div>`;
  }
  // Same gate as the Assignments work card (E2E finding 5) — one rule, one tooltip.
  const gate = (() => { try { return submitReviewGate(claim); } catch (_) { return { blocked: false, title: "" }; } })();
  return `
    <div class="offshore-task-status-bar">
      ${!isInProgress ? `<button type="button" class="offshore-task-status-btn offshore-task-status-btn--start" data-status-start="${escapeHtml(claim.claimId)}" data-status-org-id="${escapeHtml(claim.orgId || "")}">${isBounced ? "Re-open" : "Start Work"}</button>` : `<span class="offshore-task-in-progress-tag">In Progress</span>`}
      <button type="button" class="offshore-task-status-btn offshore-task-status-btn--submit" data-status-submit="${escapeHtml(claim.claimId)}"${gate.blocked ? ` disabled title="${escapeHtml(gate.title)}"` : gate.title ? ` title="${escapeHtml(gate.title)}"` : ""}>Submit for Review</button>
    </div>
  `;
}

function renderClaimsTable(options = {}) {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("workbench")) return;
  const selectionOnly = options && options.selectionOnly === true;
  const renderProfile = beginWorkbenchRenderProfile({ selectionOnly });
  workbenchRenderProfileState.current = renderProfile;
  try {
  // DEPRECATED offshore surface: a Microsoft Planner-style board of the tasks
  // routed to them. Preserved behind the same flag as the Assignments nav. With
  // the flag off (default since offshore autonomy) they get the real tracker
  // table, scoped by their orgIds like any other biller — the board would show
  // an empty queue now that claims are no longer assigned.
  if (offshoreBoardMode()) {
    renderOffshorePlannerBoard();
    return;
  }
  // Non-offshore: make sure the table wrap is visible + planner board
  // (if previously rendered) is removed so role switches stay clean.
  const tw = document.getElementById("trackerTableWrap");
  if (tw) tw.style.display = "";
  document.getElementById("offshorePlannerBoard")?.remove();
  const mode = state.rowMode || "service";
  state.rowMode = mode;
  if (els.rowModeSelect) els.rowModeSelect.value = mode;
  renderProfile.mode = mode;
  let query;
  let apiPage;
  let staleApiPage;
  let filtered;
  let pageWindow;
  profileWorkbenchStep(renderProfile, "queryFilterMs", () => {
    query = buildTrackerQueryRequest(mode);
    apiPage = trackerApiPageForQuery(query);
    staleApiPage = apiPage ? null : trackerApiStalePageForQuery(query);
    filtered = apiPage ? apiPage.rows : staleApiPage ? staleApiPage.rows : (mode === "service" ? getFilteredServiceRows(query) : getFilteredClaims(query));
    pageWindow = apiPage ? apiPage.pageWindow : staleApiPage ? staleApiPage.pageWindow : trackerPageWindow(filtered, query.page, { mutateState: !trackerApiEnabled() });
  });
  const serverBacked = Boolean(apiPage || staleApiPage);
  // Sheet mode is the only supported All Data mode and CSS permanently hides
  // the legacy command-panel dashboard. Do not spend a deferred main-thread
  // pass building 17 invisible panels after every API page response.
  const commandPanelsVisible = state.workbenchSheetMode !== true;
  const deferServerPagePanels = Boolean(commandPanelsVisible && serverBacked && !selectionOnly && trackerApiEnabled() && options.deferPagePanels !== false);
  renderProfile.serverBacked = serverBacked;
  renderProfile.rowsFiltered = filtered.length;
  renderProfile.totalRows = pageWindow.totalRows || filtered.length || 0;
  const rendered = pageWindow.rows;
  renderProfile.rowsRendered = rendered.length;
  // Slice #4: these analytics panels do NOT depend on selection. On a pure
  // selection toggle (renderClaimsTable({ selectionOnly:true })) `filtered` is
  // unchanged, so re-running them would just reproduce identical content — skip
  // them. Rows, selection chrome, select-all, and the selection-dependent
  // formula-audit panel still render below.
  // The workbench ANALYTICS COMMAND PANELS are internal operations surfaces:
  // their rows carry offshore team names, RCM lead assignments, manager-review
  // state and appeal routing — TMS work product about the client, which is the
  // same category as serviceLineNotes and is redacted from the client grid for
  // the same reason. They are hidden by the sheet-mode CSS today, which means a
  // density preference is doing a role gate's job. Skip RENDERING them: nothing
  // to reveal beats nothing visible.
  if (commandPanelsVisible && !selectionOnly && !deferServerPagePanels && !externalClientSession()) {
    // Perf: the analytics panels below each derived claimsFromTrackerRows(filtered, mode)
    // independently - identical inputs, ~14x duplicate work per render. Compute once.
    const panelClaims = claimsFromTrackerRows(filtered, mode);
    profileWorkbenchStep(renderProfile, "financialCalcMs", () => {
      renderRcmLifecycleCommand(filtered, mode, panelClaims);
      renderAuthorizationUtilizationCommand(filtered, mode, panelClaims);
      renderTimelyFilingCommand(filtered, mode, panelClaims);
      renderArForecastPanel(filtered, mode, panelClaims);
      renderClaimSubmissionCommand(filtered, mode, panelClaims);
      renderProviderCredentialingCommand(filtered, mode, panelClaims);
      renderPrebillQualityPanel(filtered, mode, panelClaims);
      renderDenialAppealCommand(filtered, mode, panelClaims);
      renderOperationsIntelligence(filtered, pageWindow, mode, panelClaims);
      renderPaymentReconciliationQueue(filtered, mode, panelClaims);
      renderCreditBalanceCommand(filtered, mode, panelClaims);
      renderPatientResponsibilityCommand(filtered, mode, panelClaims);
      renderPayerContactCommand(filtered, mode, panelClaims);
      renderSavedOperationsViews(filtered, mode, panelClaims);
      renderBillerWorkstrip(filtered, pageWindow, mode, panelClaims);
    });
  }
  profileWorkbenchStep(renderProfile, "tableRenderMs", () => {
    els.workbenchHeader.innerHTML = mode === "service" ? serviceHeaderHtml() : claimHeaderHtml();
  });
  // Delegated and idempotent: #workbenchHeader itself survives every render, so
  // this binds once and keeps working after each innerHTML repaint.
  try { installAllDataColumnReorder(); } catch (err) { console.warn("[workbench] installAllDataColumnReorder failed:", err); }
  // In API mode `sourceClaims` is intentionally empty (loadSourceData returns
  // claims: []), so the local fallback can NEVER produce rows. Changing a
  // column filter mints a new query cache key, which makes trackerApiPageForQuery
  // return null until Azure responds — and the old empty-fallback path rendered
  // a silent blank grid (worse, it stuck blank if the request errored). Show an
  // explicit loading / error state instead so a filter change never looks broken.
  profileWorkbenchStep(renderProfile, "tableRenderMs", () => {
    // ONE HTML STRING, ONE ASSIGNMENT, ONE POLICY MEMO. The rows have always
    // been built as a single string rather than per-row appends; the memo is
    // what stops the per-row cells re-deriving page-constant follow-up policy
    // ~50 times each. It is closed in the finally so nothing outside this
    // synchronous build can ever read a memoized answer.
    const policyMemo = beginFollowUpPolicyBuildMemo();
    try {
      els.claimRows.innerHTML = serverBacked
        ? renderTrackerApiRows(rendered, mode)
        : (trackerApiEnabled()
            ? trackerApiPendingRowsHtml(mode)
            : (mode === "service" ? renderServiceRows(rendered) : renderClaimRows(rendered)));
    } finally {
      endFollowUpPolicyBuildMemo(policyMemo);
    }
  });
  renderWorkbenchPresetBar();
  renderColumnViewButton();
  ensureColumnViewsLoaded();
  try { renderActiveFilterChips(); } catch (err) { console.warn("[workbench] renderActiveFilterChips failed:", err); } // UI-100 #33
  // Repaint the filter summary on every grid render, not just when a response
  // lands. Without this the line is only refreshed by renderSlicers() from the
  // fetch handlers, so during a slow query it keeps advertising the previous
  // query's terms - which is exactly how a working filter looks broken.
  try { renderActiveSlicerText(); } catch (err) { console.warn("[workbench] renderActiveSlicerText failed:", err); }
  try { updateGridModeMirror(); } catch (err) { console.warn("[workbench] updateGridModeMirror failed:", err); } // UI-100 #39
  // Tracker-parity: tag consecutive same-claim-id rows so the repeat
  // copies fade. Eye locks onto the first row of each claim group.
  try {
    let prev = null;
    Array.from(document.querySelectorAll(".claims-table tbody tr[data-row-claim-id]")).forEach((row) => {
      const id = row.dataset.rowClaimId;
      row.classList.toggle("is-claim-id-repeat", prev === id);
      prev = id;
    });
  } catch (err) { console.warn("[workbench] claim-id repeat tagging failed:", err); }
  applyWorkbenchDensity();
  tagWorkbenchCells(mode);

  const visibleIds = serverBacked
    ? uniqueClaimIds(rendered.map((row) => trackerApiClaimId(row)))
    : (mode === "service" ? uniqueClaimIds(rendered.map((row) => row.claim)) : uniqueClaimIds(rendered));
  const previousSelectionCount = state.selectedClaims.length;
  const canBulkWork = hasPermission(PERMISSIONS.ASSIGN_CLAIMS) || hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP);
  // Off-page bulk selections are pruned to the current page each render; tell the
  // user instead of silently discarding them when they page/filter away.
  const droppedOffPageSelections = canBulkWork
    ? state.selectedClaims.filter((claimId) => !visibleIds.includes(claimId)).length
    : 0;
  state.selectedClaims = canBulkWork ? state.selectedClaims.filter((claimId) => visibleIds.includes(claimId)) : [];
  state.selectedClaimTargets = Object.fromEntries(Object.entries(state.selectedClaimTargets || {})
    .filter(([claimId]) => state.selectedClaims.includes(claimId)));
  if (previousSelectionCount !== state.selectedClaims.length) saveState();
  if (droppedOffPageSelections > 0) {
    showToast(`${droppedOffPageSelections} off-page selection${droppedOffPageSelections === 1 ? "" : "s"} cleared.`);
  }
  // Count the ROWS the bulk action will write, not the distinct claims they
  // belong to. Select-all registers one target per rendered service line, so a
  // 10-line/6-claim page used to read "6 selected" while the type-to-confirm
  // guard - correctly - asked for 10. A biller typing the number in front of
  // them got "Not correct", which is exactly the friction the guard must not
  // add. One number on screen, and it is the number that gets written.
  const bulkSelectedRowCount = state.selectedClaims.length ? bulkFollowUpLineTargets().length : 0;
  const bulkSelectedClaimCount = bulkSelectedRowCount
    ? new Set(bulkFollowUpLineTargets().map((target) => `${String(target.orgId || "").trim()}\u0000${normalizeClaimId(target.claimId)}`)).size
    : 0;
  setText(els.selectionCount, bulkSelectedRowCount
    ? `${formatNounCount(bulkSelectedRowCount, "service line")} across ${formatNounCount(bulkSelectedClaimCount, "claim")} selected`
    : "Select rows to update follow-up");
  const bulkCard = document.querySelector(".bulk-card");
  bulkCard?.classList.toggle("hidden", !canBulkWork);
  bulkCard?.classList.toggle("has-selection", state.selectedClaims.length > 0);
  setDisabled(els.bulkAction, !hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP));
  setDisabled(els.bulkActionDate, !hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP));
  setDisabled(els.bulkNote, !hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP));
  setDisabled(els.bulkFollowUpBtn, !hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP));
  // Last writer on the commit button: re-arms the type-to-confirm guard
  // against the CURRENT selection, so changing the selection mid-entry
  // invalidates a now-stale typed count instead of silently accepting it.
  updateBulkFollowUpGuard();
  setDisabled(els.rowModeSelect, false);
  setDisabled(els.ownerFilter, !hasPermission(PERMISSIONS.ASSIGN_CLAIMS));
  if (commandPanelsVisible) renderWorkflowSyncStatus();
  if (!selectionOnly) {
    profileWorkbenchStep(renderProfile, "statusFormulaMs", () => {
      renderWorkbenchStatus(filtered, mode, pageWindow);
      renderSheetFormulaBar(filtered, mode, pageWindow);
    });
    profileWorkbenchStep(renderProfile, "financialCalcMs", () => {
      renderSheetTotalsBar(filtered, mode, pageWindow, serverBacked);
      renderAllDataTotalsFooter(query, filtered, mode, serverBacked);
      if (commandPanelsVisible) renderDataSourceConfidence(filtered, mode, serverBacked, pageWindow);
    });
  }
  if (commandPanelsVisible) {
    profileWorkbenchStep(renderProfile, "statusFormulaMs", () => {
      renderWorkbenchFormulaAudit(filtered, mode, serverBacked, pageWindow);
    });
  }
  if (!selectionOnly) {
    // These two sit OUTSIDE the guarded block above, so they need the role gate
    // spelled out again: their rows carry review owner, next action and lane
    // owner labels ("RCM Lead", "Billing Setup") — TMS work product.
    if (commandPanelsVisible && !deferServerPagePanels && !externalClientSession()) {
      renderRcmExceptionCommand(filtered, mode);
      renderEligibilityBenefitsCommand(filtered, mode);
    }
    renderTrackerPager(pageWindow, { navigationDisabled: Boolean(staleApiPage) });
  }
  els.selectAllVisible = document.getElementById("selectAllVisible");
  if (els.selectAllVisible) {
    const renderedServiceTargets = Object.values(serviceTargetsForRenderedRows());
    els.selectAllVisible.checked = renderedServiceTargets.length > 0
      ? renderedServiceTargets.every((target) => serviceTargetSelected(target.orgId, target.claimId, target.serviceId))
      : (visibleIds.length > 0 && visibleIds.every((claimId) => state.selectedClaims.includes(claimId)));
    els.selectAllVisible.addEventListener("change", () => {
      state.selectedClaims = els.selectAllVisible.checked ? visibleIds : [];
      state.selectedClaimTargets = els.selectAllVisible.checked ? workflowTargetsForRenderedRows() : {};
      state.selectedServiceTargets = els.selectAllVisible.checked ? serviceTargetsForRenderedRows() : {};
      saveState();
      renderClaimsTable({ selectionOnly: true });
    });
  }

  Array.from(document.querySelectorAll(".claim-select")).forEach((checkbox) => {
    checkbox.addEventListener("change", () => toggleClaimSelection(checkbox.dataset.claimId, checkbox.checked, checkbox.dataset.claimOrgId || "", checkbox.dataset.serviceId || ""));
  });

  Array.from(document.querySelectorAll("[data-open]")).forEach((button) => {
    if (button.closest?.("#claimRows")) return;
    button.addEventListener("click", () => openDrawer(button.dataset.open, {
      serviceId: button.dataset.serviceId || "",
      orgId: button.dataset.orgId || button.closest?.("[data-row-org-id]")?.dataset?.rowOrgId || ""
    }));
  });

  // The no-scope panel's two controls. "Check again" resets the lane and re-reads
  // the session; it is not a Retry, and the word is deliberately absent.
  [els.claimRows].forEach((host) => bindScopeDenialControls(host, () => {
    resetTrackerApiAutoRetry();
    trackerApiPageState = trackerApiInitialState();
    renderClaimsTable();
  }));

  // Retry a failed API-backed page request without forcing a full reload.
  Array.from(document.querySelectorAll("[data-tracker-api-retry]")).forEach((button) => {
    button.addEventListener("click", () => {
      // A person asking again must always be allowed to, however many times the
      // automatic retry has already been spent on this query.
      resetTrackerApiAutoRetry();
      trackerApiPageState = trackerApiInitialState();
      renderClaimsTable();
    });
  });

  // UI-100 #56: clicking anywhere in a claim row (except interactive controls)
  // opens the drawer (mouse convenience). Keyboard users open via the per-row
  // "Open" [data-open] button, so rows are NOT individual tab stops (#14).
  Array.from(document.querySelectorAll("[data-row-claim-id]")).forEach((row) => {
    row.addEventListener("click", (e) => {
      const t = e.target;
      // Ignore clicks on actual interactive controls inside the row.
      // A native disclosure (the Notes cell's "Both notes") is a control too:
      // opening it, or selecting the text it reveals, must not open the drawer.
      if (!t || t.closest("button, a, input, select, textarea, details, summary, [data-column-filter], [data-column-sort], .grid-select, .grid-date, .claim-select, .followup-pill, [data-followup-pill]")) return;
      const cid = row.dataset.rowClaimId;
      if (cid) openDrawer(cid, { serviceId: row.dataset.rowServiceId || "", orgId: row.dataset.rowOrgId || "" });
    });
  });

  Array.from(document.querySelectorAll("[data-formula-open]")).forEach((button) => {
    button.addEventListener("click", () => focusWorkClaim(button.dataset.formulaOpen, { open: true }));
  });

  // UI-100 #19: click column header label to cycle sort (none → asc → desc → none).
  // UI-100 #49: wire the empty-state reset button to the existing Reset View action.
  document.querySelector("[data-claims-empty-reset]")?.addEventListener("click", () => {
    state.tableSort = { column: null, dir: null };
    try { resetWorkbenchToAllData(); } catch (err) { console.warn("[workbench] resetWorkbenchToAllData failed:", err); showToast("Couldn\u2019t reset the view \u2014 showing All Data."); renderClaimsTable(); }
  });

  Array.from(document.querySelectorAll("[data-column-sort]")).forEach((button) => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      const key = button.dataset.columnSort;
      const cur = state.tableSort || { column: null, dir: null };
      let next;
      if (cur.column !== key) next = { column: key, dir: "asc" };
      else if (cur.dir === "asc") next = { column: key, dir: "desc" };
      else next = { column: null, dir: null };
      state.tableSort = next;
      saveState();
      renderClaimsTable();
    });
  });

  Array.from(document.querySelectorAll("[data-column-filter]")).forEach((button) => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      openColumnFilterMenu(button.dataset.columnFilter, button);
    });
  });
  // The carets exist again; a pending focus return is re-seated on the new one.
  restorePendingColumnFilterCaretFocus();

  Array.from(document.querySelectorAll("[data-action-edit]")).forEach((select) => {
    select.addEventListener("change", () => {
      // Blur FIRST. The pill reveal calls focus() + showPicker(), and focus
      // survives the selection, which leaves "Clear follow-up" one arrow key
      // away from the action the biller just set. Dropping focus sends the next
      // keystroke to the page instead of to this control.
      select.blur();
      openFollowUpEditor(select.dataset.actionEdit, { orgId: select.dataset.actionOrgId || "", serviceId: select.dataset.serviceId || "", action: select.value, sourceSurface: "all-data-action", returnFocus: select });
    });
  });

  // Read-mode follow-up pill: in read mode the quiet pill stands in for the
  // <select>; clicking/Enter/Space reveals the real select (which carries all
  // the data-action-edit save wiring) and focuses it. Tab-away/Escape blurs
  // the select, which reverts the cell to the pill without needing a save.
  Array.from(document.querySelectorAll("[data-followup-pill]")).forEach((pill) => {
    const reveal = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const cell = pill.closest(".followup-cell");
      if (!cell) return;
      const select = cell.querySelector(".grid-select");
      if (!select) return;
      cell.classList.add("is-editing");
      select.setAttribute("tabindex", "0");
      enhanceFollowUpActionSelect(select);
      const actionSearch = followUpActionSearchControls.get(select)?.input;
      if (actionSearch && !actionSearch.disabled) {
        actionSearch.value = "";
        followUpActionSearchControls.get(select).refresh();
        actionSearch.focus();
        return;
      }
      select.focus();
      // Open the list on THIS click. focus() alone only unhides the select, so the
      // biller had to click a second time to see the options. showPicker() needs
      // user activation, which a click/Enter/Space handler has. Where it is missing
      // (Firefox, older Chromium/Safari) this degrades to exactly the old
      // focus-only reveal rather than throwing.
      if (typeof select.showPicker === "function") {
        try { select.showPicker(); } catch (err) { /* no user activation, or unsupported */ }
      }
    };
    pill.addEventListener("click", reveal);
    pill.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") reveal(e);
    });
  });
  Array.from(document.querySelectorAll(".followup-cell .grid-select")).forEach((select) => {
    select.addEventListener("blur", (event) => {
      if (select.closest(".followup-action-choice-search")?.contains(event.relatedTarget)) return;
      const cell = select.closest(".followup-cell");
      if (cell) cell.classList.remove("is-editing");
      select.setAttribute("tabindex", "-1");
    });
  });

  Array.from(document.querySelectorAll("[data-date-edit]")).forEach((input) => {
    input.addEventListener("change", () => {
      openFollowUpEditor(input.dataset.dateEdit, { orgId: input.dataset.dateOrgId || "", serviceId: input.dataset.serviceId || "", followUpDate: input.value, sourceSurface: "all-data-date", returnFocus: input });
    });
  });
  profileWorkbenchStep(renderProfile, "tableRenderMs", () => {
    updateTopGridScroll();
  });
  if (deferServerPagePanels && apiPage) {
    scheduleDeferredWorkbenchPanels({ filtered, mode, pageWindow, requestKey: trackerApiPageState.key });
  }
  } finally {
    renderProfile.apiPageStatus = trackerApiPageState.status || "idle";
    renderProfile.apiFacetStatus = trackerApiFacetState.status || "idle";
    renderProfile.apiPageRequestMs = trackerApiPageState.lastRequestMs || 0;
    renderProfile.apiFacetRequestMs = trackerApiFacetState.lastRequestMs || 0;
    renderProfile.apiWaitMs = moneyNumber((renderProfile.apiPageRequestMs || 0) + (renderProfile.apiFacetRequestMs || 0));
    renderProfile.pendingApiPage = trackerApiPageState.status === "pending";
    renderProfile.pendingApiFacet = trackerApiFacetState.status === "pending";
    finishWorkbenchRenderProfile(renderProfile);
  }
}

function scheduleDeferredWorkbenchPanels({ filtered = [], mode = "service", pageWindow = {}, requestKey = "" } = {}) {
  if (typeof clearTimeout === "function" && deferredWorkbenchPanelRenderTimer) {
    clearTimeout(deferredWorkbenchPanelRenderTimer);
  }
  const rowsSnapshot = Array.isArray(filtered) ? filtered.slice() : [];
  const pageWindowSnapshot = {
    ...pageWindow,
    rows: Array.isArray(pageWindow.rows) ? pageWindow.rows.slice() : pageWindow.rows
  };
  const scheduler = typeof setTimeout === "function" ? setTimeout : null;
  if (!scheduler) return;
  deferredWorkbenchPanelRenderTimer = scheduler(() => {
    deferredWorkbenchPanelRenderTimer = 0;
    // THE LIVE LANE'S ROLE GATE. The synchronous guard in renderClaimsTable is
    // SKIPPED whenever the API lane is on (deferServerPagePanels), so gating
    // only there would have left every real ClientBiller — who runs in exactly
    // that lane — seeing all 17 internal panels 900ms after the grid paints.
    // Checked HERE rather than at the call site so a role change between
    // scheduling and firing is caught too.
    if (externalClientSession()) return;
    if (state.view !== "workbench") return;
    if ((state.rowMode || "service") !== mode) return;
    if (requestKey && trackerApiPageState.key !== requestKey) return;
    try {
      const panelClaims = claimsFromTrackerRows(rowsSnapshot, mode);
      renderRcmLifecycleCommand(rowsSnapshot, mode, panelClaims);
      renderAuthorizationUtilizationCommand(rowsSnapshot, mode, panelClaims);
      renderTimelyFilingCommand(rowsSnapshot, mode, panelClaims);
      renderArForecastPanel(rowsSnapshot, mode, panelClaims);
      renderClaimSubmissionCommand(rowsSnapshot, mode, panelClaims);
      renderProviderCredentialingCommand(rowsSnapshot, mode, panelClaims);
      renderPrebillQualityPanel(rowsSnapshot, mode, panelClaims);
      renderDenialAppealCommand(rowsSnapshot, mode, panelClaims);
      renderOperationsIntelligence(rowsSnapshot, pageWindowSnapshot, mode, panelClaims);
      renderPaymentReconciliationQueue(rowsSnapshot, mode, panelClaims);
      renderCreditBalanceCommand(rowsSnapshot, mode, panelClaims);
      renderPatientResponsibilityCommand(rowsSnapshot, mode, panelClaims);
      renderPayerContactCommand(rowsSnapshot, mode, panelClaims);
      renderSavedOperationsViews(rowsSnapshot, mode, panelClaims);
      renderBillerWorkstrip(rowsSnapshot, pageWindowSnapshot, mode, panelClaims);
      renderRcmExceptionCommand(rowsSnapshot, mode);
      renderEligibilityBenefitsCommand(rowsSnapshot, mode);
    } catch (err) {
      console.warn("[workbench] deferred panel render failed:", err);
    }
  }, 900);
}

// Builds the freeze plan for one render of the grid. Only the LEADING RUN of
// freezable columns is frozen: the walk stops at the first column that is not in
// the allow-list. That is what makes a reordered view degrade safely - a view
// that leads with "patient" freezes nothing but the checkbox, rather than
// stranding a pinned column in the middle of the sheet or pinning the name.
function workbenchStickyColumnPlan(columns = [], { hasSelection = false } = {}) {
  const plan = new Map();
  let left = 0;
  if (hasSelection) {
    plan.set(STICKY_SELECT_KEY, { className: "sticky-col sticky-select", left: 0 });
    left = STICKY_SELECT_WIDTH;
  }
  for (const column of Array.isArray(columns) ? columns : []) {
    const key = column && column.key;
    if (!FREEZABLE_COLUMN_KEYS.includes(key)) break;
    // Belt and braces: even if someone adds a PHI or money key to the allow-list,
    // it never becomes sticky. The verifier fails the build for it too.
    if (phiColumns.has(key) || financialColumns.has(key)) break;
    plan.set(key, { className: `sticky-col ${FREEZABLE_COLUMN_CLASSES[key]}`, left });
    left += FREEZABLE_COLUMN_WIDTHS[key] || 128;
  }
  return plan;
}

// Emits the class + left-offset attributes for one cell. The left offset rides a
// custom property so styles.css keeps ownership of `position`/`left` and the
// offset stays correct whether or not the selection column is present.
function stickyCellAttrs(plan, key, extraClasses = "") {
  const slot = plan && typeof plan.get === "function" ? plan.get(key) : null;
  const classes = [extraClasses, slot ? slot.className : ""].map((value) => String(value || "").trim()).filter(Boolean).join(" ");
  return `${classes ? ` class="${classes}"` : ""}${slot ? ` style="--sticky-left:${slot.left}px"` : ""}`;
}

function claimHeaderHtml() {
  const columns = currentColumns("claim");
  const hasSelection = canSelectWorkbenchRows();
  const stickyPlan = workbenchStickyColumnPlan(columns, { hasSelection });
  return `
    ${hasSelection ? `<th${stickyCellAttrs(stickyPlan, STICKY_SELECT_KEY)}><input id="selectAllVisible" type="checkbox" aria-label="Select all visible claims"></th>` : ""}
    ${columns.map((column) => headerCell(column, stickyPlan)).join("")}
    <th></th>
  `;
}

function serviceHeaderHtml() {
  const columns = currentColumns("service");
  const hasSelection = canSelectWorkbenchRows();
  const stickyPlan = workbenchStickyColumnPlan(columns, { hasSelection });
  return `
    ${hasSelection ? `<th${stickyCellAttrs(stickyPlan, STICKY_SELECT_KEY)}><input id="selectAllVisible" type="checkbox" aria-label="Select all visible claims"></th>` : ""}
    ${columns.map((column) => headerCell(column, stickyPlan)).join("")}
    <th></th>
  `;
}

// Slice #3 (+#21 aging): the parity-safe set of grid columns the header sort can
// actually reorder. Keys are grid column keys; values are the server sort field
// the live SQL lane (ALLOWED_SORTS) accepts. The JSON resolver maps these back to
// the same columns, so the sortable set is identical in both lanes — the sort
// affordance is shown ONLY for these so the glyph never lies. (aging is
// parity-safe: the JSON comparator ranks buckets chronologically and the server
// maps `aging` → aging_days, so both lanes order it the same way.)
const SERVER_SORTABLE_TRACKER_COLUMNS = {
  claimId: "claim_id",
  serviceId: "service_id",
  owed: "owed",
  aging: "aging",
  dos: "service_date",
  primaryPayer: "primary_payer",
  renderingProvider: "rendering_provider"
};
// WHICH COLUMNS THE SERVER CAN FILTER --------------------------------------
//
// Every header used to render a filter button. For a column the server has no
// database mapping for, that filter compiled a predicate against a FABRICATED
// column name, SQL Server answered "Invalid column name", and the API envelope
// surfaced it as `allowed: false` — a data defect wearing an access-control
// costume. The backend now refuses those by name
// (`tracker-filter-column-not-filterable`), which is the right failure — but the
// button is still an affordance that leads only to an error, so it should not be
// offered at all.
//
// This set MIRRORS trackerQueryExecutionPlan.mapUiColumnToDatabaseColumn (its key
// set plus MODE_FILTER_COLUMNS). The two bill-date columns are deliberately
// ABSENT: they are legal SELECT output but are produced by an OUTER APPLY, so they
// are not columns of the FROM target and can never appear in a WHERE — the same
// reason trackerSynapseSqlBuilder lists them in NON_SORTABLE_COLUMNS. The five
// client-derived columns are absent for a simpler reason: the server never
// computed them, so it cannot filter them.
//
// This only gates the LIVE lane. The local/demo lane filters in the browser and
// is not bound by what the SQL builder can map.
const SERVER_FILTERABLE_TRACKER_COLUMNS = new Set([
  "client", "tenant", "provider", "renderingProvider", "primaryPayer", "secondaryPayer",
  "payer", "claimStatus", "aging", "action", "followUpAction", "owner", "assignedTo", "assignedBy",
  "queue", "workQueue", "facility", "location", "claimId", "serviceId", "orgId",
  "patient", "respPartyName", "primaryExternalId", "secondaryExternalId", "followUpDate",
  "match", "lifecycle", "lifecycleStage", "statusCategory", "recommendedNextAction",
  "calculationGuards", "assignmentStatus", "queueReason", "claimDue", "dos", "procedure",
  "m1", "m2", "place", "charges", "units", "adjs", "totalInsAmtPaid", "totalPatAmtPaid",
  "totalPatBal", "positiveTotalPatBal", "balance", "primaryRate", "primaryExpectedTotal",
  "secondaryRate", "secondaryCalc", "secondaryAllowedTotal", "secondaryExpectedTotal",
  "staticTotalExpected", "finalExpectedTotalNumeric", "finalSecondaryExpectedNumeric",
  "secondaryExpectedChangeNumeric", "totalExpectedChangeNumeric", "writeOffAmount",
  "finalInsuranceExpected", "finalInsurancePaid", "finalInsuranceBalance",
  "paymentExternalPayerIds", "primaryDisbursementTotal", "secondaryDisbursementTotal",
  "unmatchedDisbursementTotal", "totalExpected", "totalPaid", "owed", "paymentStatus",
  "runId", "ingestedAtUtc",
  // MODE_FILTER_COLUMNS: claimClassification maps in both modes;
  // patClassification only in warehouse mode, where it is the only mode that
  // serves the column at all.
  "claimClassification", "patClassification",
  // Service Line Notes (the EZClaim source note). Free text, so unlike every
  // other member of this set its facet is bounded in SQL - TOP 500 by count with
  // a parameterised search - rather than by the column being small. The ledger
  // note ("serviceLineNotes") is deliberately still absent: it is not a warehouse
  // column at all and the server refuses it by name.
  "sourceServiceLineNotes",
  // Notes (2026-09-23): both note sources at once. Its predicate is
  // mergedNotesFilterPredicate, served on the same bounded TOP-N lane.
  "mergedNotes",
  // IN/OUT OF NETWORK. A COMPUTED facet (a bucketing CASE over the T1a
  // `network` column), so it has always been bounded by construction and has
  // always been in the unnamed default expansion - the header simply never
  // offered it, which is the gap this set existed to close and had itself.
  "network",
  // RBT IS RENDERING PROVIDER. trackerWorkbookExport's viewRowValues.rbt is
  // `rendering_provider` verbatim (EZClaim has no separate RBT identity), so
  // the column filters through the same predicate and reads the same option
  // list as Provider rather than acquiring a second definition of itself.
  "rbt",
  // EHR Billing Entry ID - EZClaim SrvCustomField2, the id a biller pastes
  // back into EZClaim. Snapshot-only; the server denies its facet by name on
  // the two legacy lanes rather than compiling an invalid column against them.
  "ehrBillingEntryId",
  // Hold duration (backend/serviceLineHoldEpisodes.js). Snapshot-only, same as above.
  "holdReason", "onHoldSince", "daysOnHold", "holdReleasedOn"
]);
function isTrackerColumnServerFilterable(columnKey) {
  // A live header needs BOTH a legal predicate and a bounded dataset-wide facet;
  // local/demo headers bypass this gate and build options from in-memory rows.
  return Boolean(columnFilterFacetKey(columnKey));
}
function isTrackerColumnSortable(columnKey) {
  return Object.prototype.hasOwnProperty.call(SERVER_SORTABLE_TRACKER_COLUMNS, columnKey);
}
// THE ALIGNMENT HOOK THE HEADERS NEVER HAD ------------------------------
//
// 18 of the grid's 49 columns right-align their CELLS and 0 right-align
// their HEADERS, so every money column reads as a left-aligned label sitting
// over a right-aligned column of figures. VOB_GRID_MODULE_SPEC.md §5.1
// measured three independent blockers and this function was the first:
// headerCell() emitted `class`, `style="--sticky-left"` and `aria-sort` and
// no alignment hook of any kind, while tagWorkbenchCells() set
// `td.dataset.col` on the CELLS only.
//
// Two attributes land here, and they are deliberately different things:
//
//   data-col          the column key, byte-identical to the `td.dataset.col`
//                     tagWorkbenchCells() writes. Header and cell for one
//                     column can now be selected by the SAME key, which is
//                     what makes the pair provable instead of assumed.
//   data-col-align    "right" when the column is in currencyColumns or
//                     numericColumns — the same two Sets the cells read, so
//                     header alignment cannot drift from cell alignment by
//                     someone updating one list.
//
// nth-child was not an option: the column set is user-configurable through
// the column manager and reorderable, and nth-child column keying is exactly
// the class of bug the key-based frozen-column control was built to remove.
// (Say "frozen-column", not the class name: verify_frozen_columns_are_key_based.js
// scans this file for /sticky-[a-z-]+/ to find EMITTED classes and does not skip
// comments, so prose that names one fails the build.)
//
// THE CSS HALF IS NOT IN THIS COMMIT and is not in this lane. styles.css is
// held by a concurrent agent, and `text-align` on the <th> alone will NOT
// reach the label — `.th-inner` is `display:flex; justify-content:
// space-between`, and both `.column-sort-button` and `.th-label` set their
// own `text-align: left`. What that lane needs to add:
//
//   .claims-table th[data-col-align="right"] .th-inner { justify-content: flex-end; }
//   .claims-table th[data-col-align="right"] .column-sort-button { justify-content: flex-end; text-align: right; }
//   .claims-table th[data-col-align="right"] .th-label { flex: 1 1 auto; text-align: right; }
//
// and it can delete `.claims-table th[data-col-currency]` (styles.css:20654)
// while it is there — that selector has never had an emitter and stays dead:
// `data-col-currency` would have covered the 25 money columns and missed the
// 4 numeric ones, which is half the problem.
// The billers' workbook names (Primary_Rate, Secondary_Calc, Secondary_Rate)
// stay for TMS staff; a client reads plain words (2026-10-13).
const CLIENT_COLUMN_LABELS = Object.freeze({ primaryRate: "Primary Rate", secondaryCalc: "Secondary Calc", secondaryRate: "Secondary Rate" });
function headerCell(column, stickyPlan) {
  if (isExternalClientRole(state?.role) && CLIENT_COLUMN_LABELS[column.key]) column = { ...column, label: CLIENT_COLUMN_LABELS[column.key] };
  // EITHER CONTROL LIGHTS THE CARET. A column narrowed only by "Charges > 500"
  // carries no ticked values, so reading the selection list alone would draw an
  // unfiltered caret over a filtered column.
  const active = (state.columnFilters?.[column.key] || []).length > 0
    // typeof-guarded for the same reason the live-lane read below is:
    // verify_grid_header_alignment_hook_contract evaluates this function in a
    // narrow sandbox that defines neither helper, and "no range" is the
    // permissive, pre-existing answer there.
    || (typeof columnFilterRangeFor === "function" && Boolean(columnFilterRangeFor(column.key)));
  const sortable = isTrackerColumnSortable(column.key);
  const sort = state.tableSort || {};
  const isSorted = sortable && sort.column === column.key;
  const dir = isSorted ? sort.dir : null;
  const sortGlyph = dir === "asc" ? "▲" : dir === "desc" ? "▼" : "↕";
  const sortClass = !sortable ? "" : isSorted ? `sorted sorted-${dir}` : "sortable";
  const ariaSort = dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none";
  // A column with a `description` explains itself: the text is the header's
  // tooltip on hover and its accessible description, read from a hidden node
  // (aria-describedby still reads hidden text). Left After Insurance is why this
  // exists - its old name read as the amount the patient owes.
  const columnHelp = String(column.description || "");
  const helpId = columnHelp ? `allDataColumnHelp-${column.key}` : "";
  const describedBy = helpId ? ` aria-describedby="${escapeHtml(helpId)}"` : "";
  const headerTitle = columnHelp || (column.key === "followUpDate" ? "Past due = an open claim (Under Paid or Not Paid) with a follow-up date on or before today." : "");
  const alignsRight = currencyColumns.has(column.key) || numericColumns.has(column.key);
  const labelHtml = sortable
    ? `<button class="column-sort-button" type="button" data-column-sort="${escapeHtml(column.key)}" aria-label="Sort by ${escapeHtml(column.label)}" title="${escapeHtml(columnHelp ? `${column.label}: ${columnHelp}` : `Sort by ${column.label}`)}"${describedBy}><span>${escapeHtml(column.label)}</span><span class="sort-glyph" aria-hidden="true">${sortGlyph}</span></button>`
    : `<span class="th-label"${headerTitle ? ` title="${escapeHtml(headerTitle)}"` : ""}${describedBy}>${escapeHtml(column.label)}</span>`;
  // On the live lane, only offer the filter the server can actually run. An
  // unmappable key used to compile invalid SQL and come back as `allowed: false`.
  // typeof-guarded: verify_grid_header_alignment_hook_contract.js evaluates this
  // function in a narrow sandbox that defines neither helper. Falling back to the
  // LOCAL lane there is the permissive, pre-existing behaviour, and the short
  // circuit means isTrackerColumnServerFilterable is never called in that sandbox.
  const liveLane = typeof trackerApiEnabled === "function" && trackerApiEnabled();
  const filterable = !liveLane || isTrackerColumnServerFilterable(column.key);
  return `
    <th${stickyCellAttrs(stickyPlan, column.key, `${active ? "filtered-column " : ""}${sortClass}`)} data-col="${escapeHtml(column.key)}"${alignsRight ? ` data-col-align="right"` : ""} aria-sort="${ariaSort}"${describedBy}>
      <div class="th-inner">
        ${labelHtml}
        ${filterable ? `<button class="column-filter-button" type="button" data-column-filter="${escapeHtml(column.key)}" aria-label="Filter ${escapeHtml(column.label)}${active ? " (filtered)" : ""}" title="Filter ${escapeHtml(column.label)}">
          <strong>${active ? "●" : "▾"}</strong>
        </button>` : ""}
      </div>
      ${helpId ? `<span id="${escapeHtml(helpId)}" hidden>${escapeHtml(columnHelp)}</span>` : ""}
    </th>
  `;
}

// UI-100 #49: designed empty state for the main claims table.
// Live API mode has no local book to sum (sourceClaims is empty on purpose).
// Local/demo can name the AR the filters hid. Never invent a leftover $.
function emptyClaimsHiddenBook() {
  if (typeof trackerApiEnabled === "function" && trackerApiEnabled()) return null;
  if (typeof getClaims !== "function") return null;
  try {
    const claims = getClaims();
    if (!Array.isArray(claims) || !claims.length) return null;
    const owed = typeof sumClaimOutstanding === "function" ? sumClaimOutstanding(claims) : 0;
    if (!(Number(owed) > 0)) return null;
    return { claims: claims.length, owed: Number(owed) };
  } catch (_e) {
    return null;
  }
}

function emptyClaimsRowHtml(mode) {
  const cols = currentColumns(mode).length + (canSelectWorkbenchRows() ? 1 : 0) + 1; // optional selection + actions
  // The LOCAL/off lane - the one the mark screenshot was taken in, and the one
  // trackerApiPendingRowsHtml never reaches. "Nothing matches the active
  // slicers" is a lie here: nothing was ever offered to match.
  const scopeDenial = typeof portalScopeDenial === "function" ? portalScopeDenial() : null;
  if (scopeDenial) {
    return `<tr class="claims-empty-row"><td colspan="${cols}">${scopeDeniedPanelHtml(scopeDenial)}</td></tr>`;
  }
  const hasFilters = Object.values(state.columnFilters || {}).some((arr) => Array.isArray(arr) && arr.length);
  // The quick filters (Claim ID, Service ID, search, company scope...) narrow the
  // server query just as hard as the slicers do, and this copy used to blame
  // "the active slicers" for a Claim ID lookup that found nothing. Name the
  // filter that actually emptied the grid, from the same describer the formula
  // bar uses, so the two can never disagree about what is applied.
  const quickFilterText = typeof activeTrackerFilterDescriptions === "function" && typeof collectTrackerFilters === "function"
    ? activeTrackerFilterDescriptions(collectTrackerFilters()).join(", ")
    : "";
  const slicerActive = Object.values(state.slicers || {}).some((arr) => Array.isArray(arr) && arr.length);
  const hidden = typeof emptyClaimsHiddenBook === "function" ? emptyClaimsHiddenBook() : null;
  const hiddenLine = hidden
    ? `${typeof money === "function" ? money(hidden.owed) : hidden.owed} across ${typeof formatNounCount === "function" ? formatNounCount(hidden.claims, "claim") : hidden.claims + " claims"}`
    : "";
  let hint;
  if (hasFilters) {
    hint = hidden
      ? `Column filters are hiding ${hiddenLine}. Try clearing column filters or other active restrictions to widen the view. This is not $0 on the book.`
      : "These filters returned no rows. That is not the same as $0 on the book. Try clearing column filters or other active restrictions to widen the view.";
  } else if (quickFilterText) {
    hint = `Nothing matches ${quickFilterText}. That is not the same as $0 on the book. Clear the search or widen the filters above to broaden the worklist.`;
  } else {
    hint = hidden
      ? `Nothing matches the active slicers. The book still has ${hiddenLine}. Adjust the filters above to broaden the worklist.`
      : "Nothing matches the active slicers. That is not the same as $0 on the book. Adjust the filters above to broaden the worklist.";
  }
  const localNotePending = !trackerApiEnabled() && Boolean(collectTrackerFilters().noteSearch) && ["loading", "error"].includes(localNoteSearchState.status);
  if (localNotePending) hint = localNoteSearchState.status === "loading" ? "Searching the current note ledger…" : "Note search could not be completed. Clear and re-enter the search to retry; no empty result is being claimed.";
  return `
    <tr class="claims-empty-row">
      <td colspan="${cols}">
        <div class="claims-empty-state" role="status">
          <div class="claims-empty-glyph" aria-hidden="true">⌕</div>
          <div class="claims-empty-title">${localNotePending ? (localNoteSearchState.status === "loading" ? "Searching notes…" : "Note search unavailable") : "No claims in view"}</div>
          <div class="claims-empty-hint">${escapeHtml(hint)}</div>
          ${hasFilters || quickFilterText || slicerActive ? `<button type="button" class="ghost-button claims-empty-reset" data-claims-empty-reset="1">Reset view</button>` : ""}
        </div>
      </td>
    </tr>
  `;
}

// Both no-scope controls, bound the same way wherever the panel renders.
function bindScopeDenialControls(host, recheck) {
  if (!host || typeof host.querySelectorAll !== "function") return;
  Array.from(host.querySelectorAll("[data-scope-denial-ticket]")).forEach((button) => {
    button.addEventListener("click", () => openFeedbackModal(scopeDenialTicketDraft()));
  });
  Array.from(host.querySelectorAll("[data-scope-denial-recheck]")).forEach((button) => {
    button.addEventListener("click", () => { if (typeof recheck === "function") recheck(); });
  });
}

function canSelectWorkbenchRows() {
  return hasPermission(PERMISSIONS.ASSIGN_CLAIMS) || hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP);
}

function renderClaimRows(filtered) {
  if (!filtered.length) return emptyClaimsRowHtml("claim");
  const canSelect = hasPermission(PERMISSIONS.ASSIGN_CLAIMS) || hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP);
  const columns = currentColumns("claim");
  const stickyPlan = workbenchStickyColumnPlan(columns, { hasSelection: canSelect });
  return filtered.map((claim) => {
    const isSelected = state.selectedClaims.includes(claim.claimId);
    const checked = isSelected ? "checked" : "";
    const classes = [];
    const claimId = escapeHtml(claim.claimId);
    const orgId = escapeHtml(claim.orgId || "");
    const serviceId = escapeHtml(claim.services?.[0]?.serviceId || "");
    if (claim.claimId === state.focusedWorkClaimId) classes.push("focused-work-row");
    if (isSelected) classes.push("row-selected"); // UI-100 #27
    return `
      <tr class="${classes.join(" ")}" data-row-claim-id="${claimId}" data-row-org-id="${orgId}" data-row-service-id="${serviceId}">
        ${canSelect ? `<td${stickyCellAttrs(stickyPlan, STICKY_SELECT_KEY)}><input class="claim-select" type="checkbox" data-claim-id="${claimId}" data-claim-org-id="${orgId}" data-service-id="${serviceId}" ${checked} aria-label="Select ${claimId}"></td>` : ""}
        ${columns.map((column) => claimCellHtml(column.key, claim, stickyPlan)).join("")}
      <td><div class="row-actions">${hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) ? `<button class="small-button" data-open="${claimId}" data-org-id="${orgId}" data-service-id="${serviceId}" type="button">Open</button>` : ""}</div></td>
      </tr>
    `;
  }).join("");
}

function renderServiceRows(filteredRows) {
  if (!filteredRows.length) return emptyClaimsRowHtml("service");
  const canSelect = hasPermission(PERMISSIONS.ASSIGN_CLAIMS) || hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP);
  const columns = currentColumns("service");
  const stickyPlan = workbenchStickyColumnPlan(columns, { hasSelection: canSelect });
  return filteredRows.map(({ claim, service }) => {
    const isSelected = serviceTargetSelected(claim.orgId, claim.claimId, service.serviceId);
    const checked = isSelected ? "checked" : "";
    const classes = [];
    const claimId = escapeHtml(claim.claimId);
    const orgId = escapeHtml(claim.orgId || "");
    const serviceId = escapeHtml(service.serviceId || "");
    if (claim.claimId === state.focusedWorkClaimId) classes.push("focused-work-row");
    if (isSelected) classes.push("row-selected"); // UI-100 #27
    return `
      <tr class="${classes.join(" ")}" data-row-claim-id="${claimId}" data-row-org-id="${orgId}" data-row-service-id="${serviceId}">
        ${canSelect ? `<td${stickyCellAttrs(stickyPlan, STICKY_SELECT_KEY)}><input class="claim-select" type="checkbox" data-claim-id="${claimId}" data-claim-org-id="${orgId}" data-service-id="${serviceId}" ${checked} aria-label="Select ${claimId} from ${serviceId}"></td>` : ""}
        ${columns.map((column) => serviceCellHtml(column.key, claim, service, stickyPlan)).join("")}
      <td><div class="row-actions">${hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) ? `<button class="small-button" data-open="${claimId}" data-org-id="${orgId}" data-service-id="${serviceId}" type="button">Open</button>` : ""}</div></td>
      </tr>
    `;
  }).join("");
}

function claimLineFollowUpSummary(claim, field) {
  const lines = Array.isArray(claim && claim.services) ? claim.services : [];
  const values = lines.map((s) => {
    const fu = state.followUpByService?.[followUpKey(claim.claimId, String(s.serviceId || "").trim())] || {};
    if (field === "action") return firstPresent(fu.action, s.action) || "";
    return firstPresent(fu.followUpDate, s.followUpDate) || "";
  }).filter(Boolean);
  const distinct = Array.from(new Set(values));
  if (distinct.length === 0) return "";
  if (distinct.length === 1) return field === "action" ? distinct[0] : formatDate(distinct[0]);
  return "Multiple";
}

function claimCellHtml(key, claim, stickyPlan) {
  // MONEY CERTAINTY FIRST. A line with no rate on file has no knowable
  // expected amount, so every rate-derived money column says so rather than
  // rendering $0 (NORTH_STAR.md section 6). Checked ONCE, ahead of every
  // per-column branch, so a money column added later cannot quietly skip it.
  if (currencyColumns.has(key)) {
    const unknownReason = unknownMoneyReason(key, claimFinancialView(claim));
    if (unknownReason) return `<td class="money-unknown-cell">${moneyUnknownHtml(unknownReason)}</td>`;
  }
  if (key === "claimId") return `<td${stickyCellAttrs(stickyPlan, "claimId")}><span class="claim-id">${escapeHtml(claim.claimId)}</span><span class="subtext">${formatNounCount(claim.services.length, "service line")}</span></td>`;
  if (key === "action") {
    if (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) return `<td>${escapeHtml(claim.action || "—")}</td>`;
    // Follow-up is per service line and cannot be set from the collapsed claim
    // row (claim rows have no single serviceId — in live data it is often empty).
    // Read-only summary here; set it per line in the Service Line row view.
    const actionLineCount = Array.isArray(claim.services) ? claim.services.length : 0;
    const actionSummary = claimLineFollowUpSummary(claim, "action") || (claim.action || "");
    const actionHint = `Follow-up is per service line. Switch Rows to "Service line"${actionLineCount > 1 ? ` to set it for each of this claim's ${actionLineCount} lines` : " to set it"}.`;
    return `<td>
      <select class="grid-select" disabled title="${escapeHtml(actionHint)}">
        <option value="" selected>${actionSummary ? escapeHtml(actionSummary) : "—"}</option>
      </select>
    </td>`;
  }
  if (key === "followUpDate") {
    if (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) return `<td>${claim.followUpDate ? formatDate(claim.followUpDate) : "—"}<span class="subtext">${isPastDue(claim) ? "Past due" : "Not due"}</span></td>`;
    const dateLineCount = Array.isArray(claim.services) ? claim.services.length : 0;
    const dateSummary = claimLineFollowUpSummary(claim, "followUpDate") || (claim.followUpDate ? formatDate(claim.followUpDate) : "");
    const dateHint = `Follow-up is per service line. Switch Rows to "Service line"${dateLineCount > 1 ? ` to set the date for each of this claim's ${dateLineCount} lines` : " to set the date"}.`;
    return `<td>
      <input class="grid-date" type="text" value="${escapeHtml(dateSummary || "")}" disabled title="${escapeHtml(dateHint)}">
      <span class="subtext">${isPastDue(claim) ? "Past due" : "Not due"}</span>
    </td>`;
  }
  if (key === "match") return `<td>${pill(claim.match, matchTone(claim.match))}</td>`;
  if (key === "aging") return `<td>${pill(claim.aging, claimAgingBand(claim) === "121+" ? "red" : "gray")}</td>`;
  if (["totalExpected", "totalPaid", "owed"].includes(key)) {
    // UI-100 #84: surface the source formula on hover for money cells.
    const fin = claimFinancialView(claim);
    const titles = {
      totalExpected: `Expected reimbursement = sum of service expected (${money(fin.expectedReimbursement)}). Source: contract rate × units.`,
      totalPaid: `Total insurance paid = sum of 835 payment posts (${money(fin.totalInsurancePaid)}). Excludes patient payments.`,
      owed: `Source outstanding = Expected − Insurance Paid − Adjustments (${money(fin.sourceOutstanding)}).`
    };
    return `<td title="${escapeHtml(titles[key] || "")}"><strong>${getColumnValue(key, claim, null)}</strong></td>`;
  }
  if (key === "paymentStatus") return `<td>${paymentSummary(claim)}</td>`;
  if (key === "assignmentStatus") return `<td>${pill(workflowAssignmentStatusLabel(claim.assignmentStatus, assignmentWorkflowPerspective()), statusTone(claim.assignmentStatus))}</td>`;
  if (key === "serviceLineNotes") {
    const notesValue = getColumnValue(key, claim, null);
    return `<td title="${escapeHtml(notesValue)}">${escapeHtml(notesValue)}</td>`;
  }
  return `<td>${escapeHtml(getColumnValue(key, claim, null))}</td>`;
}

function serviceCellHtml(key, claim, service, stickyPlan) {
  // MONEY CERTAINTY FIRST. A line with no rate on file has no knowable
  // expected amount, so every rate-derived money column says so rather than
  // rendering $0 (NORTH_STAR.md section 6). Checked ONCE, ahead of every
  // per-column branch, so a money column added later cannot quietly skip it.
  if (currencyColumns.has(key)) {
    const unknownReason = unknownMoneyReason(key, serviceFinancialView(service, claim));
    if (unknownReason) return `<td class="money-unknown-cell">${moneyUnknownHtml(unknownReason)}</td>`;
  }
  if (key === "claimId") return `<td${stickyCellAttrs(stickyPlan, "claimId")}><span class="claim-id">${escapeHtml(claim.claimId)}</span></td>`;
  if (key === "serviceId") return `<td${stickyCellAttrs(stickyPlan, "serviceId")}>${escapeHtml(service.serviceId)}<span class="subtext">Entry row</span></td>`;
  if (key === "ehrBillingEntryId") {
    // Same rule as the live lane's cell, and deliberately the same helper:
    // two lanes that built the URL separately would be free to disagree -
    // including about how many ids one cell may carry.
    const entryText = getColumnValue(key, claim, service);
    const linkedHtml = ehrBillingEntryCellHtml(entryText, service?.dos);
    if (linkedHtml) return `<td>${linkedHtml}</td>`;
  }
  if (key === "assignmentStatus") return `<td>${pill(workflowAssignmentStatusLabel(claim.assignmentStatus, assignmentWorkflowPerspective()), statusTone(claim.assignmentStatus))}</td>`;
  if (key === "action") {
    // Per-service-line follow-up: read the action from the per-line overlay
    // (state.followUpByService) keyed (claim, service), falling back to the
    // service/claim. Setting it on one line never touches sibling lines.
    const lineFollowUp = state.followUpByService?.[followUpKey(claim.claimId, service.serviceId)] || {};
    const lineAction = firstPresent(lineFollowUp.action, service.action, claim.action) || "";
    if (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) return `<td>${escapeHtml(lineAction || "—")}</td>`;
    return `<td class="followup-cell">
      <button type="button" class="followup-pill${lineAction ? "" : " followup-pill--empty"}${followUpActionIsRestrictedCurrent(lineAction) ? " followup-pill--restricted" : ""}" data-followup-pill aria-label="${followUpActionIsRestrictedCurrent(lineAction) ? `Restricted action: ${escapeHtml(lineAction)}. You cannot change or clear it.` : `Follow-up action: ${lineAction ? escapeHtml(lineAction) : "none set"}. Activate to change.`}" title="${escapeHtml(followUpActionIsRestrictedCurrent(lineAction) ? "Restricted action — ask a supervisor to change it." : (lineAction || ""))}"${followUpActionIsRestrictedCurrent(lineAction) ? " disabled" : ""}><span class="followup-pill__label">${lineAction ? escapeHtml(lineAction) : "Set follow-up"}</span></button>
      <select class="grid-select" tabindex="-1"${followUpActionIsRestrictedCurrent(lineAction) ? " disabled aria-label=\"Restricted action; changes are not allowed\"" : ""} data-action-edit="${escapeHtml(claim.claimId)}" data-action-org-id="${escapeHtml(claim.orgId || "")}" data-service-id="${escapeHtml(String(service.serviceId || "").trim())}">
        ${followUpActionOptionsHtml(lineAction)}
      </select>
      ${systemSetFollowUpMarkerHtml(lineAction, Object.prototype.hasOwnProperty.call(lineFollowUp, "action") ? "" : service.followUpUpdatedBy)}
      ${followUpSyncStatusHtml(claim.claimId, String(service.serviceId || "").trim(), claim.orgId)}
      ${trackerFollowUpReviewHtml(service)}
    </td>`;
  }
  if (key === "followUpDate") {
    // Per-service-line date + past-due derived from the SERVICE line's date,
    // not isPastDue(claim) (which is claim-level).
    const lineFollowUp = state.followUpByService?.[followUpKey(claim.claimId, service.serviceId)] || {};
    const lineDate = firstPresent(lineFollowUp.followUpDate, service.followUpDate, claim.followUpDate) || "";
    const linePastDue = serviceLinePastDue(lineDate, service, claim);
    if (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) return `<td>${lineDate ? formatDate(lineDate) : "—"}<span class="subtext">${linePastDue ? "Past due" : "Not due"}</span></td>`;
    return `<td>
      <input class="grid-date" data-date-edit="${escapeHtml(claim.claimId)}" data-date-org-id="${escapeHtml(claim.orgId || "")}" data-service-id="${escapeHtml(String(service.serviceId || "").trim())}" type="date" value="${escapeHtml(lineDate || "")}"${followUpActionIsRestrictedCurrent(firstPresent(lineFollowUp.action, service.action, claim.action) || "") ? " disabled title=\"Restricted action — the due date cannot be changed.\"" : ""}>
      <span class="subtext">${linePastDue ? "Past due" : "Not due"}</span>
    </td>`;
  }
  if (key === "match") {
    const sFin = serviceFinancialView(service, claim);
    const status = sFin.finalizedMatch || service.status || claim.match;
    return `<td>${pill(status, matchTone(status))}</td>`;
  }
  if (key === "aging") return `<td>${pill(claim.aging, claimAgingBand(claim) === "121+" ? "red" : "gray")}</td>`;
  if (key === "notes") return `<td>${escapeHtml(getColumnValue(key, claim, service))}</td>`;
  // UI-100 #84: money-cell formula tooltip for service-row totals.
  if (["totalExpected", "totalPaid", "owed", "balance", "charges", "underpayment", "overpayment", "patientResponsibility", "payerResponsibility", "truePatientResponsibility"].includes(key)) {
    const sFin = serviceFinancialView(service, claim);
    const titles = {
      totalExpected: `Tracker final expected (${money(sFin.expectedReimbursement || 0)})`,
      totalPaid: `Insurance paid on this service line (${money(sFin.insurancePaid || 0)})`,
      owed: `Source outstanding for this service line (${money(sFin.sourceOutstanding || 0)})`,
      balance: `Service outstanding after adjustments + payments (${money(sFin.sourceOutstanding || 0)})`,
      charges: `Charges = service expected before adjustments (${money(sFin.charges || service.expected || 0)})`,
      underpayment: `Underpayment = Expected − Paid (when positive). (${money(sFin.underpayment || 0)})`,
      overpayment: `Overpayment = Paid − Expected (when positive). (${money(sFin.overpayment || 0)})`,
      patientResponsibility: `Patient responsibility per 835 PR codes (${money(sFin.patientResponsibility || 0)})`,
      payerResponsibility: `Payer responsibility = Expected − Patient Responsibility (${money(sFin.payerResponsibility || 0)})`,
      truePatientResponsibility: `True patient resp. after secondary coverage (${money(sFin.patientResponsibility || 0)})`
    };
    return `<td title="${escapeHtml(titles[key] || "")}">${escapeHtml(getColumnValue(key, claim, service))}</td>`;
  }
  if (key === "mergedNotes") {
    // No drawer opener of its own on this lane: the local grid's note cells have
    // always been plain text (the row itself opens the drawer), and every local
    // proof counts [data-open] as one per row.
    return mergedNoteCellHtml(mergedNoteCellModel(mergedNoteInputsFromClaim(claim, service)), {
      attrs: "",
      claimId: "",
      orgId: String(claim.orgId || ""),
      serviceId: String(service?.serviceId || "")
    });
  }
  if (key === "serviceLineNotes" || key === "sourceServiceLineNotes") {
    const notesValue = getColumnValue(key, claim, service);
    return `<td title="${escapeHtml(notesValue)}">${escapeHtml(notesValue)}</td>`;
  }
  return `<td>${escapeHtml(getColumnValue(key, claim, service))}</td>`;
}

// Assignments view — manager's higher-level cockpit for everything routed to
// offshore. Rebuilt (per user direction) from a stack of ~10 redundant boards
// into four legible bands: a command header (where the money/work sits right
// now), a clickable pipeline strip (intake → approved), an owner ledger (who
// holds the work and who can take more), and the drill-down work queue +
// intake rail. All underlying models are reused unchanged; only the
// presentation is new. Clicking a pipeline stage or owner filters the queue.
let assignmentQueueFilter = { stage: "all", owner: "all" };
// Whether the Team view <details> is open. Kept here, not read back from the
// page: a reload paints a placeholder in its place, and reading the open state
// from that placeholder collapsed Team view after every company/view/person click.
let assignmentTeamViewOpen = false;
let assignmentIntakeSelection = new Set();

function myWorkLane(claim = {}) {
  const stage = assignmentReviewPipelineStage(claim);
  if (stage === "submitted") return { key: "review", label: "Waiting for review", tone: "blue" };
  if (stage === "bounced") return { key: "returned", label: "Sent back", tone: "red" };
  const due = assignmentDueStatus(claim);
  if (["overdue", "dueToday"].includes(due.key)) return { key: "due", label: "Due now", tone: "amber" };
  return { key: "ready", label: "Ready to work", tone: "green" };
}

function renderMyWorkDefault(queue = [], owner = "", completeCounts = null, page = null) {
  const current = queue.filter((claim) => assignmentReviewPipelineStage(claim) !== "approved");
  const counts = { ready: 0, due: 0, review: 0, returned: 0 };
  current.forEach((claim) => { counts[myWorkLane(claim).key] += 1; });
  if (completeCounts) Object.assign(counts, completeCounts);
  const order = { due: 0, returned: 1, ready: 2, review: 3 };
  const cards = current.slice().sort((a, b) => order[myWorkLane(a).key] - order[myWorkLane(b).key] || workPriorityScore(b) - workPriorityScore(a)).map((claim) => {
    const lane = myWorkLane(claim);
    const next = assignmentReviewPipelineNextAction(claim, assignmentReviewPipelineStage(claim), assignmentDueStatus(claim), evaluateClaimCalculationGuards(claim));
    return `<article class="my-work-card">
      <div><span class="my-work-state my-work-state--${escapeHtml(lane.tone)}">${escapeHtml(lane.label)}</span><strong>Claim ${escapeHtml(claim.claimId || "")}</strong>${claimPatientLineHtml(claim)}<small>${escapeHtml(claim.client || claim.company || "")}${claim.primaryPayer ? ` · ${escapeHtml(claim.primaryPayer)}` : ""}</small></div>
      <p><b>Next:</b> ${escapeHtml(next || "Open the claim and review the latest note.")}</p>
      <button class="primary-button" data-open="${escapeHtml(claim.claimId || "")}" data-org-id="${escapeHtml(claim.orgId || "")}" type="button">Open claim</button>
    </article>`;
  }).join("");
  return `<section class="my-work-default" aria-label="My work">
    <header><div><span>My Work</span><h2>${escapeHtml(owner || "Your work")}</h2><p>Start with Due now or Sent back. Open a claim to work it.</p></div></header>
    <div class="my-work-counts">
      <div><strong>${counts.ready}</strong><span>Ready to work</span></div>
      <div><strong>${counts.due}</strong><span>Due now</span></div>
      <div><strong>${counts.review}</strong><span>Waiting for review</span></div>
      <div><strong>${counts.returned}</strong><span>Sent back</span></div>
    </div>
    ${page ? workflowBoardPagerHtml("personal", page) : ""}
    <div class="my-work-list">${cards || '<div class="empty-state"><strong>No work is assigned to you.</strong><p>When work is assigned, it will appear here.</p></div>'}</div>
  </section>`;
}

function renderAssignments() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("assignments")) return;
  if (oversightState().tab === "existing") {
    ensureOversightTabCounts();
    // "kept" = the open Team view was left on screen while it reloads; its
    // heading and tabs are already there, so adding them again would stack a
    // second copy above it and push the page down.
    const kept = renderExistingAssignments() === "kept";
    const host = els.offshoreQueue;
    if (host && !kept) {
      host.insertAdjacentHTML("afterbegin", `<div class="oversight-heading"><div><h2>My Work</h2><p>Assigned claims, action approvals, returned work and team activity.</p></div></div>${oversightTabs(oversightWorkTabsWithCounts(), "existing", "data-work-tab")}`);
      bindOversightWorkTabs(host);
    }
    return;
  }
  return renderOversightWork();
}

function renderExistingAssignments() {
  const isOffshore = canonicalRole(state.role) === "OffshoreBiller";
  // The Team view company picker is built from the same directory-backed label map;
  // ask for it once (no-op while pending/loaded) so companies show by name.
  if (hasPermission(PERMISSIONS.MANAGE_USERS)) requestPortalOrgDirectory();
  let claims;
  let assignable;
  let board = null;
  if (trackerApiEnabled()) {
    // Live mode: sourceClaims is intentionally empty under server-side
    // paging, so the board reads the principal-scoped workflowBoardState
    // cache (LIVE_BOARDS_SPEC item 1). Offshore scoping is enforced by the
    // server's tenant (org) predicate - never client-side. The per-owner
    // narrowing below is a board affordance on top of that server scope, not a
    // security boundary.
    board = workflowBoardDataset();
    if (board.status !== "ready") {
      // An open Team view stays on screen while it reloads, so a click inside it
      // no longer collapses the page and scrolls the reader away. Errors still
      // replace it with the Retry panel.
      if (board.status !== "error" && markAssignmentTeamViewUpdating()) return "kept";
      els.offshoreQueue.innerHTML = workflowBoardPlaceholderHtml("My Work", board);
      bindWorkflowBoardRetry(els.offshoreQueue, renderAssignments);
      return;
    }
    claims = workflowBoardClaims(board.claims);
    // Intake rail = the second bounded query (unassigned, balance > 0, top
    // by owed, server-sorted). Re-filter locally so an in-session assign
    // drops the claim from intake without a refetch.
    assignable = workflowBoardClaims(board.intake)
      .filter((claim) => !claim.assignedTo && claimFinancialView(claim).sourceOutstanding > 0);
  } else {
    claims = getClaims();
    assignable = claims
      .filter((claim) => !claim.assignedTo && claimFinancialView(claim).sourceOutstanding > 0)
      .sort(sortClaims);
  }
  // Offshore sees THEIR queue. scope.assignedName (the server identity key)
  // wins over the roster fallback so real logins match their assignments.
  const boardOwner = String((currentAccessProfile().scope || {}).assignedName || currentUser()?.name || "").trim();
  const personalAliases = new Set([boardOwner, ...((board?.teamScope || assignmentScopeUiState.scope || {}).actorIdentities || [])]
    .map((value) => String(value || "").trim().toLowerCase()).filter(Boolean));
  const personalPool = trackerApiEnabled() ? workflowBoardClaims(board.personalClaims) : claims;
  const personalQueue = personalPool.filter((claim) => personalAliases.has(String(claim.assignedTo || "").trim().toLowerCase()));
  const queue = trackerApiEnabled()
    ? claims.filter((claim) => claim.assignedTo)
    : (isOffshore
      ? claims.filter((claim) => String(claim.assignedTo || "").trim() === boardOwner)
      : claims.filter((claim) => claim.assignedTo));

  const completeModels = board?.summary?.complete ? workflowAssignmentModels(board) : null;
  const overview = completeModels?.overview || assignmentOverviewModel(queue, assignable);
  const pipeline = completeModels?.pipeline || assignmentReviewPipelineModel(claims);
  const capacity = completeModels?.capacity || assignmentCapacityModel(queue, assignable);

  // Every paint rebuilds the Team view <details>; carry its open state across, or a
  // stage / owner filter click inside it (and the directory-arrival repaint) snaps it shut.
  const teamViewOpen = assignmentTeamViewOpen;
  els.offshoreQueue.innerHTML = `${renderMyWorkDefault(personalQueue, assignmentBoardDisplayName(boardOwner), completeModels?.personalCounts, board?.personalPage)}
    ${isOffshore ? "" : `<details class="assignment-team-view"${teamViewOpen ? " open" : ""}><summary>Team view</summary><div class="assignment-board">
      ${board ? renderAssignmentScopeControls(board) : ""}
      <div data-workflow-team-activity>${board ? renderTeamActivityPanel(board) : ""}</div>
      ${renderAssignmentCommandHeader(overview, isOffshore, board)}
      ${renderAssignmentPipelineStrip(pipeline)}
      ${renderAssignmentOwnerLedger(capacity)}
      ${board ? workflowBoardPagerHtml("team", board.page) : ""}
      ${renderAssignmentWorkQueue(queue)}
      ${renderAssignmentIntakeRail(assignable, queue, capacity)}
      ${board ? workflowBoardPagerHtml("intake", board.intakePage) : ""}
    </div></details>`}`;

  bindCardButtons(els.offshoreQueue);
  bindAssignmentFilters();
}

// --- Assignments: filter state + interaction -----------------------------

// Leaves the open Team view in place, dimmed and marked busy, until the reload
// finishes and renderExistingAssignments paints the new data. Returns false
// when there is no open Team view to keep.
function markAssignmentTeamViewUpdating() {
  const view = els.offshoreQueue?.querySelector?.(".assignment-team-view");
  if (!view || !view.open || !assignmentTeamViewOpen) return false;
  view.classList.add("is-updating");
  view.querySelector(".assignment-board")?.setAttribute("aria-busy", "true");
  const status = view.querySelector(".assignment-scope-status");
  if (status) status.textContent = "Updating…";
  // Show the view the reader just picked as selected while it loads.
  view.querySelectorAll("[data-assignment-scope-mode]").forEach((button) => {
    const active = button.dataset.assignmentScopeMode === assignmentScopeUiState.mode;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-pressed", active ? "true" : "false");
  });
  return true;
}

function assignmentStageMatches(stageKey, filterStage) {
  if (filterStage === "all") return true;
  if (filterStage === "active") return stageKey === "assigned" || stageKey === "inProgress";
  return stageKey === filterStage;
}

function bindAssignmentFilters() {
  const host = els.offshoreQueue;
  if (!host || host.dataset.assignmentFilterBound === "1") return;
  host.dataset.assignmentFilterBound = "1";
  // "toggle" does not bubble; capture it so opening or closing Team view is remembered.
  host.addEventListener("toggle", (event) => {
    if (event.target?.classList?.contains("assignment-team-view")) assignmentTeamViewOpen = event.target.open === true;
  }, true);
  host.addEventListener("click", (event) => {
    const pageButton = event.target.closest("[data-workflow-page]");
    if (pageButton && !pageButton.disabled) { const lane = pageButton.dataset.workflowPage; if (Object.hasOwn(workflowBoardPages, lane)) { workflowBoardPages[lane] = Math.max(1, Number(pageButton.dataset.page) || 1); workflowBoardState.status = "idle"; renderAssignments(); } return; }
    const scopeMode = event.target.closest("[data-assignment-scope-mode]");
    if (scopeMode && !scopeMode.disabled) {
      const mode = String(scopeMode.dataset.assignmentScopeMode || "");
      if (assignmentScopeAllowedModes().includes(mode) && assignmentScopeUiState.mode !== mode) {
        assignmentScopeUiState.mode = mode;
        assignmentScopeUiState.memberUserId = "";
        assignmentScopeUiState.memberIdentities = [];
        assignmentQueueFilter = { stage: "all", owner: "all" };
        assignmentIntakeSelection.clear();
        resetWorkflowBoard();
        renderAssignments();
      }
      return;
    }
    const teamMember = event.target.closest("[data-assignment-team-member]");
    if (teamMember) {
      const userId = String(teamMember.dataset.assignmentTeamMember || "");
      const selected = assignmentScopeUiState.memberUserId === userId;
      const member = (workflowBoardState.teamActivity?.members || []).find((candidate) => String(candidate.userId || "") === userId);
      assignmentScopeUiState.memberUserId = selected ? "" : userId;
      assignmentScopeUiState.memberIdentities = selected ? [] : [...(member?.identityAliases || [])];
      assignmentQueueFilter = { stage: "all", owner: "all" };
      resetWorkflowBoard();
      renderAssignments();
      return;
    }
    const assignEl = event.target.closest("[data-assign]");
    if (assignEl) {
      const id = assignEl.getAttribute("data-assign");
      const card = assignEl.closest(".assignment-candidate-card");
      const sel = card ? card.querySelector("[data-assign-select]") : null;
      const due = card ? (card.querySelector("[data-assign-due]")?.value || "") : "";
      const instruction = card ? (card.querySelector("[data-assign-instruction]")?.value || "") : "";
      assignClaimFromBoard(id, sel ? sel.value : "", { due, instruction });
      return;
    }
    const reassignEl = event.target.closest("[data-reassign]");
    if (reassignEl) {
      const id = reassignEl.getAttribute("data-reassign");
      const card = reassignEl.closest(".work-card");
      const sel = card ? card.querySelector("[data-reassign-select]") : null;
      const instruction = card ? (card.querySelector("[data-reassign-instruction]")?.value || "") : "";
      if (sel && sel.value) assignClaimFromBoard(id, sel.value, { instruction });
      return;
    }
    const intakeCheck = event.target.closest("[data-intake-select]");
    if (intakeCheck) {
      const id = intakeCheck.getAttribute("data-intake-select");
      if (assignmentIntakeSelection.has(id)) assignmentIntakeSelection.delete(id);
      else assignmentIntakeSelection.add(id);
      renderAssignments();
      return;
    }
    if (event.target.closest("[data-bulk-assign-selected]")) {
      const bar = event.target.closest(".assignment-bulk-bar");
      const owner = bar ? (bar.querySelector("[data-bulk-owner]")?.value || "") : "";
      const due = bar ? (bar.querySelector("[data-bulk-due]")?.value || "") : "";
      const instruction = bar ? (bar.querySelector("[data-bulk-instruction]")?.value || "") : "";
      bulkAssignFromBoard(Array.from(assignmentIntakeSelection), owner, { due, instruction });
      return;
    }
    if (event.target.closest("[data-bulk-clear-selection]")) {
      assignmentIntakeSelection.clear();
      renderAssignments();
      return;
    }
    const stageEl = event.target.closest("[data-assignment-stage]");
    if (stageEl) {
      const value = stageEl.getAttribute("data-assignment-stage");
      if (value === "unassigned") {
        const intake = host.querySelector(".assignment-intake");
        if (intake) intake.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      assignmentQueueFilter.stage = assignmentQueueFilter.stage === value ? "all" : value;
      workflowBoardPages.team = 1;
      renderAssignments();
      return;
    }
    const ownerEl = event.target.closest("[data-assignment-owner]");
    if (ownerEl) {
      const value = ownerEl.getAttribute("data-assignment-owner");
      assignmentQueueFilter.owner = assignmentQueueFilter.owner === value ? "all" : value;
      workflowBoardPages.team = 1;
      renderAssignments();
      return;
    }
    if (event.target.closest("[data-assignment-clear]")) {
      assignmentQueueFilter = { stage: "all", owner: "all" };
      workflowBoardPages.team = 1;
      renderAssignments();
    }
  });
  host.addEventListener("change", (event) => {
    const company = event.target.closest("[data-assignment-scope-org]");
    if (!company) return;
    const orgId = String(company.value || "").trim();
    if (!teamStructureOrgOptions().some((option) => option.orgId === orgId) || assignmentScopeUiState.orgId === orgId) return;
    assignmentScopeUiState.orgId = orgId;
    assignmentScopeUiState.memberUserId = "";
    assignmentScopeUiState.memberIdentities = [];
    assignmentQueueFilter = { stage: "all", owner: "all" };
    assignmentIntakeSelection.clear();
    resetAssignmentWorkflowScope({ preserveSelection: true });
    resetWorkflowBoard();
    renderAssignments();
  });
}

// --- Assignments: command header -----------------------------------------

function assignmentOverviewModel(queue = [], assignable = []) {
  const lanes = {
    inFlight: { n: 0, ar: 0 },
    review: { n: 0, ar: 0 },
    correction: { n: 0, ar: 0 },
    approved: { n: 0, ar: 0 },
    atRisk: { n: 0, ar: 0 }
  };
  let slaOnTrack = 0;
  let slaOverdue = 0;
  let clearedWeek = 0;
  let turnaroundSum = 0;
  let turnaroundN = 0;
  const velocityNow = Date.now();
  const VELOCITY_WEEK = 7 * 86400000;
  queue.forEach((claim) => {
    const stage = assignmentReviewPipelineStage(claim);
    const ar = claimFinancialView(claim).sourceOutstanding;
    const due = assignmentDueStatus(claim);
    if (stage === "assigned" || stage === "inProgress") { lanes.inFlight.n += 1; lanes.inFlight.ar += ar; }
    else if (stage === "submitted") { lanes.review.n += 1; lanes.review.ar += ar; }
    else if (stage === "bounced") { lanes.correction.n += 1; lanes.correction.ar += ar; }
    else if (stage === "approved") { lanes.approved.n += 1; lanes.approved.ar += ar; }
    if (["overdue", "missingDue", "dueToday"].includes(due.key)) { lanes.atRisk.n += 1; lanes.atRisk.ar += ar; }
    if (due.key === "onTrack") slaOnTrack += 1;
    if (due.key === "overdue") slaOverdue += 1;
    const approvedMs = Date.parse(claim.approvedAt || "");
    const assignedMs = Date.parse(claim.assignedAt || "");
    if (!Number.isNaN(approvedMs) && (velocityNow - approvedMs) <= VELOCITY_WEEK && (velocityNow - approvedMs) >= -86400000) clearedWeek += 1;
    if (!Number.isNaN(approvedMs) && !Number.isNaN(assignedMs) && approvedMs >= assignedMs) { turnaroundSum += approvedMs - assignedMs; turnaroundN += 1; }
  });
  const intakeAr = assignable.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
  const queueAr = queue.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
  return {
    lanes,
    intake: { n: assignable.length, ar: intakeAr },
    activeClaims: queue.length,
    totalClaims: queue.length + assignable.length,
    arInMotion: moneyNumber(queueAr + intakeAr),
    sla: {
      active: queue.length,
      onTrack: slaOnTrack,
      overdue: slaOverdue,
      onTimeRate: queue.length ? Math.round((slaOnTrack / queue.length) * 100) : null
    },
    velocity: {
      clearedWeek,
      avgTurnaroundDays: turnaroundN ? Math.round(turnaroundSum / turnaroundN / 86400000) : null,
      sampled: turnaroundN
    }
  };
}

function renderAssignmentCommandHeader(overview, isOffshore, board = null) {
  const L = overview.lanes;
  const S = overview.sla || { active: 0, onTrack: 0, overdue: 0, onTimeRate: 100 };
  const V = overview.velocity || { clearedWeek: 0, avgTurnaroundDays: null };
  const liveMode = board ? assignmentScopeUiState.mode : "";
  const headline = liveMode === "assignedBy"
    ? `${money(overview.arInMotion)} open AR · ${formatNounCount(overview.activeClaims, "assignment")} created by you`
    : liveMode === "team"
      ? `${money(overview.arInMotion)} AR in motion · ${formatNounCount(overview.totalClaims, "claim")} in team activity`
      : (isOffshore || liveMode === "my")
        ? `${money(overview.arInMotion)} open AR · ${formatNounCount(overview.activeClaims, "claim")} in your queue`
        : `${money(overview.arInMotion)} AR in motion · ${formatNounCount(overview.totalClaims, "claim")} with offshore`;
  const slaHasData = S.active > 0;
  const slaFigure = slaHasData ? `${S.onTimeRate}%` : "—";
  const slaMiss = slaHasData && S.onTrack === 0;
  return `
    <header class="assignment-command">
      <div class="assignment-command-intro">
        <div>
          <span class="assignment-kicker">${board ? "Role-Aware Workflow" : "Offshore Operations"}</span>
          <h3>${headline}</h3>
        </div>
        ${pill(`${L.atRisk.n} at risk`, L.atRisk.n ? "red" : "green")}
      </div>
      <div class="assignment-sla-pulse" role="group" aria-label="SLA and throughput pulse">
        <div><span>On-time SLA</span><strong${slaMiss ? ' class="is-miss"' : ""}>${slaFigure}</strong><small>${S.onTrack}/${S.active} within SLA</small></div>
        <div><span>Overdue</span><strong>${S.overdue}</strong><small>past assignment due</small></div>
        <div><span>At risk</span><strong>${L.atRisk.n}</strong><small>overdue, due today, or no date</small></div>
        <div><span>In review</span><strong>${L.review.n}</strong><small>submitted to manager</small></div>
        <div><span>Cleared 7d</span><strong>${V.clearedWeek == null ? "—" : V.clearedWeek}</strong><small>${V.available === false ? "approval timing unavailable" : "approved this week"}</small></div>
        <div><span>Avg turnaround</span><strong>${V.avgTurnaroundDays == null ? "—" : V.avgTurnaroundDays + "d"}</strong><small>assign → approve</small></div>
      </div>
    </header>
  `;
}

function assignmentScopeAllowedModes() {
  const modes = ["my"];
  const role = canonicalRole(state.role);
  const managerLane = role === "SuperAdmin" || role === "StatesideBiller";
  if (managerLane && (hasPermission(PERMISSIONS.ASSIGN_CLAIMS) || hasPermission(PERMISSIONS.APPROVE_REVIEW))) modes.push("team");
  if (managerLane && hasPermission(PERMISSIONS.ASSIGN_CLAIMS)) modes.push("assignedBy");
  return modes;
}

function assignmentScopeModeLabel(mode) {
  if (mode === "team") return "Team Activity";
  if (mode === "assignedBy") return "Assigned by Me";
  return "My Work";
}

function assignmentScopeStatusMessage(board = {}) {
  const mode = assignmentScopeUiState.mode;
  const scope = board.teamScope || assignmentScopeUiState.scope || {};
  const total = Math.max(0, Number(board.totalRows || 0));
  const shown = Array.isArray(board.claims) ? board.claims.length : 0;
  let message;
  if (mode === "assignedBy") {
    message = `${formatNounCount(total, "assignment")} created by you.`;
  } else if (mode === "team") {
    const reports = Array.isArray(scope.descendantUserIds) ? scope.descendantUserIds.length : 0;
    const focusedMember = assignmentScopeUiState.memberUserId
      ? (board.teamActivity?.members || []).find((member) => String(member.userId || "") === assignmentScopeUiState.memberUserId)
      : null;
    if (focusedMember) {
      const name = focusedMember.displayName || focusedMember.userId;
      message = `${formatNounCount(total, "assigned claim")} for ${name}. Their requests waiting for approval are in Needs my approval. Select ${name} again to show the whole team.`;
    } else if (scope.mode === "configured_not_member") {
      message = "Your account is not in this company's reporting lines, so no team work is shown.";
    } else if (scope.configured && reports === 0) {
      message = "No reporting descendants are configured beneath you, so no team work is shown.";
    } else if (scope.configured) {
      message = `${formatNounCount(total, "claim")} across ${formatNounCount(reports, "recursive report")}.`;
    } else {
      message = `${formatNounCount(total, "claim")}. Reporting lines are not set up for this company, so Team Activity shows everyone with access to it.`;
    }
  } else {
    message = `${formatNounCount(total, "claim")} assigned to you.`;
  }
  if (total > shown) message += ` Showing the first ${shown.toLocaleString()} of ${total.toLocaleString()}.`;
  return message;
}

function renderAssignmentScopeControls(board = {}) {
  const options = teamStructureOrgOptions();
  const modes = assignmentScopeAllowedModes();
  const selectedOrg = assignmentScopeUiState.orgId;
  return `
    <section class="assignment-scope-panel" aria-labelledby="assignmentScopeTitle">
      <div class="assignment-scope-heading">
        <div>
          <span class="assignment-kicker">Work ownership</span>
          <strong id="assignmentScopeTitle">Choose whose routed work to review</strong>
        </div>
        <label class="assignment-scope-company">
          <span>Company</span>
          <select data-assignment-scope-org aria-label="Assignment company"${activeClientGroupTenant() ? " disabled" : ""}>
            ${options.map((option) => `<option value="${escapeHtml(option.orgId)}"${option.orgId === selectedOrg ? " selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
          </select>
        </label>
      </div>
      <div class="assignment-scope-modes" role="group" aria-label="Assignment work view">
        ${modes.map((mode) => `<button type="button" class="assignment-scope-mode${assignmentScopeUiState.mode === mode ? " is-active" : ""}" data-assignment-scope-mode="${mode}" aria-pressed="${assignmentScopeUiState.mode === mode ? "true" : "false"}">${assignmentScopeModeLabel(mode)}</button>`).join("")}
      </div>
      <p class="assignment-scope-status" role="status">${escapeHtml(assignmentScopeStatusMessage(board))}</p>
    </section>
  `;
}

function renderTeamActivityPanel(board = {}) {
  if (assignmentScopeUiState.mode !== "team" || !board.teamActivity) return "";
  const activity = board.teamActivity;
  const summary = activity.summary || {};
  const selectedUserId = assignmentScopeUiState.memberUserId || "";
  // With request/approval counts the server also sends All open and Internal
  // review; without them (older API, or that read failed) the six assignment
  // counts render exactly as before.
  const full = activity.reviewActivityIncluded === true;
  const columns = [
    ...(full ? [["All open", "openCount"]] : []),
    ["Assigned", "assignedCount"],
    ["In progress", "inProgressCount"],
    ["Approval pending", "needsReviewCount"],
    ...(full ? [["Internal review", "internalReviewCount"]] : []),
    ["Changes required", "changesRequestedCount"],
    ["Completed", "completedCount"],
    ["Overdue", "overdueCount"]
  ];
  const metrics = columns.map(([label, key]) => [label, summary[key]]);
  const memberRows = (activity.members || []).map((member) => {
    const selected = selectedUserId === String(member.userId || "");
    const depth = Math.max(0, Number(member.reportingDepth || 0));
    const relationship = member.isDirectReport ? "Direct report" : (depth ? `Reporting depth ${depth}` : "Company member");
    const countsLabel = columns.map(([label, key]) => `${formatCount(member[key] || 0)} ${label.toLowerCase()}`).join(", ");
    return `
      <button type="button" class="assignment-team-member${selected ? " is-active" : ""}" data-assignment-team-member="${escapeHtml(member.userId || "")}" aria-pressed="${selected ? "true" : "false"}" aria-label="${escapeHtml(`${member.displayName}: ${countsLabel}`)}">
        <span class="assignment-team-person"><strong>${escapeHtml(member.displayName || member.userId || "Team member")}</strong><small>${escapeHtml(relationship)}${member.assignedByMeCount ? ` · ${formatNounCount(member.assignedByMeCount, "assignment")} by you` : ""}</small></span>
        ${columns.map(([label, key]) => `<span class="${key === "overdueCount" && member.overdueCount ? "is-overdue" : ""}"><small>${label}</small><strong>${formatCount(member[key] || 0)}</strong></span>`).join("")}
      </button>`;
  }).join("");
  return `
    <section class="assignment-team-activity${full ? " assignment-team-activity--full" : ""}" aria-labelledby="assignmentTeamActivityTitle">
      <header class="assignment-section-head">
        <div>
          <span class="assignment-kicker">Team</span>
          <strong id="assignmentTeamActivityTitle">Team Activity</strong>
          <p>Each person's claims, requests and approvals in this company. Select a person to see only the claims assigned to them.</p>
        </div>
        ${selectedUserId ? '<button type="button" class="small-button" data-assignment-team-member="">Show all team</button>' : ""}
      </header>
      <div class="assignment-team-summary" aria-label="Team lifecycle totals">
        ${metrics.map(([label, value]) => `<div><span>${label}</span><strong>${formatCount(value || 0)}</strong></div>`).join("")}
      </div>
      <div class="assignment-team-members">
        ${memberRows || `<div class="assignment-team-empty"><strong>No direct or indirect reports configured.</strong><span>Use Reporting lines in Users &amp; Access to choose each person's supervisor.</span></div>`}
      </div>
      ${activity.unmappedAssignmentCount ? `<p class="assignment-team-warning">${formatNounCount(activity.unmappedAssignmentCount, "assignment")} use an owner identity that is not mapped to the current portal directory.</p>` : ""}
      ${activity.reviewActivityUnavailable ? '<p class="assignment-team-warning">Request and approval counts could not be loaded, so only Claim assignments are counted. Refresh to try again.</p>' : ""}
    </section>
  `;
}

// --- Assignments: pipeline strip -----------------------------------------

function renderAssignmentPipelineStrip(pipeline) {
  const order = ["unassigned", "assigned", "inProgress", "submitted", "bounced", "approved"];
  const byKey = new Map(pipeline.stages.map((stage) => [stage.key, stage]));
  const stages = order.map((key) => byKey.get(key)).filter(Boolean);
  const max = Math.max(1, ...stages.map((stage) => stage.claims));
  const segments = stages.map((stage) => {
    const active = assignmentQueueFilter.stage === stage.key
      || (assignmentQueueFilter.stage === "active" && (stage.key === "assigned" || stage.key === "inProgress"));
    const fill = Math.round((stage.claims / max) * 100);
    return `
      <button type="button"
        class="assignment-stage assignment-stage--${stage.tone}${stage.claims ? "" : " is-empty"}${active ? " is-active" : ""}"
        data-assignment-stage="${escapeHtml(stage.key)}"
        title="${escapeHtml(stage.label)} — ${escapeHtml(stage.ownerLabel)} lane">
        <span class="assignment-stage-label">${escapeHtml(stage.label)}</span>
        <strong class="assignment-stage-count">${stage.claims}</strong>
        <span class="assignment-stage-ar">${money(stage.outstanding)}</span>
        <span class="assignment-stage-bar"><i style="width:${fill}%"></i></span>
      </button>
    `;
  }).join('<span class="assignment-stage-arrow" aria-hidden="true">›</span>');
  return `
    <section class="assignment-pipeline" aria-label="Assignment to review pipeline">
      <div class="assignment-pipeline-head">
        <div>
          <strong>${formatNounCount(pipeline.totals.claims, "claim")} from intake to approved outcome</strong>
        </div>
        <div class="assignment-pipeline-proof">
          <span><b>${money(pipeline.totals.expected)}</b> Expected</span>
          <span><b>${money(pipeline.totals.paid)}</b> Paid</span>
        </div>
      </div>
      <div class="assignment-pipeline-track">${segments}</div>
    </section>
  `;
}

// --- Assignments: owner ledger -------------------------------------------

function renderAssignmentOwnerLedger(capacity) {
  const rows = capacity.rows || [];
  const recommended = capacity.recommendedOwner?.owner || "";
  const metric = (value) => Number(value) ? `<strong>${value}</strong>` : '<strong class="is-zero">0</strong>';
  const body = rows.length ? rows.map((row) => {
    const atRisk = (row.pastDue || 0) + (row.missingDue || 0);
    const active = assignmentQueueFilter.owner === row.owner;
    const capacityTone = /at capacity|full|over/i.test(row.capacity) ? "red" : /watch|tight|busy/i.test(row.capacity) ? "amber" : Number(row.claims) ? "green" : "gray";
    return `
      <button type="button" class="assignment-owner-row${active ? " is-active" : ""}" data-assignment-owner="${escapeHtml(row.owner)}">
        <span data-label="Owner">
          <strong>${escapeHtml(row.owner)}</strong>
          ${row.owner === recommended ? '<small class="assignment-owner-flag">Next up</small>' : ""}
        </span>
        <span data-label="Owned">${metric(row.claims)}<small>${money(row.owed)} AR</small>${row.financialIncomplete ? "<small>Capacity uses claim and SLA counts; AR is unavailable.</small>" : ""}</span>
        <span data-label="In Review">${metric(row.submitted || 0)}</span>
        <span data-label="Needs Fix">${metric(row.bounced || 0)}</span>
        <span data-label="At Risk">${atRisk ? pill(`${atRisk}`, "red") : pill("0", "gray")}</span>
        <span data-label="Capacity">${pill(row.capacity || "Open", capacityTone)}<small>${row.claims}/${row.ceiling || 8} slots · ${row.utilization || 0}%</small></span>
      </button>
    `;
  }).join("") : empty("No offshore owners hold work right now.");
  return `
    <section class="assignment-owners" aria-label="Offshore owner ledger">
      <div class="assignment-section-head">
        <div>
          <span class="assignment-kicker">Who owns the work</span>
          <strong>Offshore owner load &amp; capacity</strong>
        </div>
        ${recommended ? pill(`Route next: ${recommended}`, "blue") : ""}
      </div>
      <div class="assignment-owner-table">
        <div class="assignment-owner-row assignment-owner-head">
          <span>Owner</span><span>Owned</span><span>In Review</span><span>Needs Fix</span><span>At Risk</span><span>Capacity / Routing</span>
        </div>
        ${body}
      </div>
    </section>
  `;
}

// --- Assignments: work queue (drill-down) --------------------------------

function assignmentFilterLabel() {
  const perspective = assignmentWorkflowPerspective();
  const stageLabels = {
    active: "In Flight",
    submitted: perspective === "reviewer" ? "Approval Required" : "Approval Pending",
    bounced: perspective === "reviewer" ? "Changes Requested" : "Changes Required",
    unassigned: "Ready To Assign",
    approved: "Approved",
    assigned: "Assigned Work",
    inProgress: "In Progress"
  };
  const chips = [];
  if (assignmentQueueFilter.stage !== "all") chips.push(stageLabels[assignmentQueueFilter.stage] || assignmentQueueFilter.stage);
  if (assignmentQueueFilter.owner !== "all") chips.push(assignmentQueueFilter.owner);
  return chips;
}

function assignmentQueueEmptyState(isOffshore, isFiltered, queue = []) {
  if (isFiltered) {
    const book = Array.isArray(queue) ? queue : [];
    const owed = typeof sumClaimOutstanding === "function"
      ? sumClaimOutstanding(book)
      : book.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
    const moneyLine = Number(owed) > 0
      ? ` Filters are hiding ${money(owed)} across ${typeof formatNounCount === "function" ? formatNounCount(book.length, "claim") : book.length + " claims"} still on this queue. This is not $0.`
      : "";
    return `
    <div class="empty-state">
      <strong>No claims match the current filter.</strong>
      <p>Clear the filter to see the rest of the queue.${moneyLine}</p>
    </div>`;
  }
  if (isOffshore) {
    return `
    <div class="empty-state">
      <strong>Nothing routed to you yet.</strong>
      <p>Your manager assigns claims to you here — when they do, they'll appear in this queue, most-urgent SLA first.</p>
    </div>`;
  }
  return `
    <div class="empty-state">
      <strong>No assigned claims yet.</strong>
      <p>Assign claims to an owner to start the offshore queue.</p>
    </div>`;
}

// ONE submit gate for both offshore surfaces — the Assignments work card and
// the My Tasks planner card (E2E finding 5). Advisory-but-consistent: the same
// rule on both, and the tooltip names ONLY the actual blocker(s).
function submitReviewGate(claim, guards = evaluateClaimCalculationGuards(claim), due = assignmentDueStatus(claim)) {
  // Wave 3c guard severity: only BLOCKER guards (missing money data the
  // reviewer needs — e.g. expected reimbursement incomplete, PR reason detail
  // missing) stop the submit. Advisory guards (severity "review", e.g.
  // adjustment detail missing) surface as a tooltip warning but never disable
  // the button. Unknown/absent severity blocks (deny-by-default).
  const advisory = (guards || []).filter((guard) => /^(review|advisory|info|warning)$/i.test(String((guard && guard.severity) || "")));
  const blockingGuards = (guards || []).filter((guard) => !advisory.includes(guard));
  const blockers = [];
  if (blockingGuards.length > 0) blockers.push("resolve the calc guard");
  if (due && due.key === "missingDue") blockers.push("set an assignment due date");
  if (!blockers.length) {
    const warning = advisory.length
      ? `Warning: ${advisory.map((guard) => guard.label || guard.code || "calc guard").join(" / ")} — review suggested; submit is allowed`
      : "";
    return { blocked: false, title: warning };
  }
  const sentence = `${blockers.join(" and ")} before submitting`;
  return { blocked: true, title: sentence.charAt(0).toUpperCase() + sentence.slice(1) };
}

function renderAssignmentWorkQueue(queue) {
  const canDeleteTasks = canDeleteAssignmentTasks();
  const isOffshore = canonicalRole(state.role) === "OffshoreBiller";
  const filtered = queue.filter((claim) => {
    const stage = assignmentReviewPipelineStage(claim);
    if (!assignmentStageMatches(stage, assignmentQueueFilter.stage)) return false;
    if (assignmentQueueFilter.owner !== "all" && String(claim.assignedTo || "") !== assignmentQueueFilter.owner) return false;
    return true;
  }).sort((a, b) => {
    const dueRank = { overdue: 0, dueToday: 1, dueSoon: 2, missingDue: 3, onTrack: 4, unassigned: 5 };
    const ra = dueRank[assignmentDueStatus(a).key] ?? 9;
    const rb = dueRank[assignmentDueStatus(b).key] ?? 9;
    return ra - rb || workPriorityScore(b) - workPriorityScore(a);
  });

  const chips = assignmentFilterLabel();
  const filterBar = chips.length
    ? `<div class="assignment-filter-bar">
        <span>Filtered:</span>
        ${chips.map((chip) => `<span class="assignment-filter-chip">${escapeHtml(chip)}</span>`).join("")}
        <button type="button" class="assignment-filter-clear" data-assignment-clear="1">Clear</button>
      </div>`
    : "";

  const dueTone = { overdue: "red", dueToday: "amber", dueSoon: "amber", missingDue: "amber", onTrack: "green" };
  const workflowPerspective = assignmentWorkflowPerspective();
  const cards = filtered.length ? filtered.map((claim) => {
    const stage = assignmentReviewPipelineStage(claim);
    const due = assignmentDueStatus(claim);
    const guards = evaluateClaimCalculationGuards(claim);
    const directive = assignmentReviewPipelineNextAction(claim, stage, due, guards);
    const canStart = stage === "assigned" || stage === "bounced";
    const canSubmit = stage === "inProgress" || stage === "bounced";
    const canUndoApproval = stage === "approved" && hasPermission(PERMISSIONS.APPROVE_REVIEW);
    const submitGate = submitReviewGate(claim, guards, due);
    const canEditFollowUp = hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP);
    const canReassign = canonicalRole(state.role) !== "OffshoreBiller"
      && hasPermission(PERMISSIONS.ASSIGN_CLAIMS)
      && (stage === "assigned" || stage === "inProgress" || stage === "bounced");
    const serviceLines = Array.isArray(claim.services) ? claim.services.filter((service) => String(service?.serviceId || "").trim()) : [];
    const selectedServiceId = serviceLines.length === 1 ? String(serviceLines[0].serviceId || "").trim() : "";
    const selectedService = serviceLines.find((service) => String(service.serviceId || "").trim() === selectedServiceId) || null;
    const selectedFollowUp = selectedService ? (state.followUpByService?.[followUpKey(claim.claimId, selectedServiceId)] || {}) : {};
    const selectedAction = selectedService ? firstPresent(selectedFollowUp.action, selectedService.action) : "";
    const selectedDate = selectedService ? firstPresent(selectedFollowUp.followUpDate, selectedService.followUpDate) : "";
    return `
    <div class="work-card">
      <div class="work-card-header">
        <div>
          <h4>${escapeHtml(claim.claimId)}</h4>
          ${claimPatientLineHtml(claim)}
          <p>${escapeHtml(claim.client)} · ${escapeHtml(claim.action)} · ${money(workflowClaimOutstanding(claim))} owed</p>
        </div>
        <div class="work-card-badges">
          ${pill(workflowAssignmentStatusLabel(claim.assignmentStatus, workflowPerspective), statusTone(claim.assignmentStatus))}
          ${due.key !== "unassigned" ? pill(due.label, dueTone[due.key] || "blue") : ""}
        </div>
      </div>
      <div class="work-card-next">
        <span class="work-card-next-kicker">Do next</span>
        <span>${escapeHtml(directive)}</span>
      </div>
      ${stage === "bounced" && claimBounceReason(claim) ? `
      <div class="offshore-bounce-callout" role="note">
        <span class="offshore-bounce-callout-label">Bounced back</span>
        <p>${escapeHtml(claimBounceReason(claim))}</p>
        ${claim.bounceBy || claim.bounceAt ? `<small>${escapeHtml([claim.bounceBy || "", claim.bounceAt ? formatDate(claim.bounceAt) : ""].filter(Boolean).join(" · "))}</small>` : ""}
      </div>` : ""}
      ${claim.assignmentInstruction ? `
      <div class="work-card-next work-card-instructions">
        <span class="work-card-next-kicker">Manager instructions</span>
        <span>${escapeHtml(claim.assignmentInstruction)}${claim.instructionBy || claim.instructionAt ? ` — ${escapeHtml([claim.instructionBy || "", claim.instructionAt ? formatDate(claim.instructionAt) : ""].filter(Boolean).join(", "))}` : ""}</span>
      </div>` : ""}
      ${renderAssignmentRouteChain(claim)}
      ${renderAssignmentWorkPacket(claim)}
      <textarea class="work-note" data-completion-note="${escapeHtml(claim.claimId)}" rows="3" placeholder="What did offshore do? Add proof, payer response, portal confirmation, or next issue.">${escapeHtml(stage === "bounced" && !String(claim.bounceReason || "").trim() ? "" : (claim.reviewNote || ""))}</textarea>
      <div class="next-action-row">
        <label>
          Service Line
          <select data-next-service="${escapeHtml(claim.claimId)}" aria-label="Service line for ${escapeHtml(claim.claimId)} follow-up">
            ${serviceLines.length > 1 ? `<option value="">Choose service line...</option>` : ""}
            ${serviceLines.map((service) => {
              const serviceId = String(service.serviceId || "").trim();
              const details = [serviceId, normalizeDateValue(service.dos) ? formatDate(service.dos) : "", service.procedure || ""].filter(Boolean).join(" · ");
              return '<option value="' + escapeHtml(serviceId) + '"' + (serviceId === selectedServiceId ? " selected" : "") + '>' + escapeHtml(details) + '</option>';
            }).join("")}
          </select>
        </label>
        <label>
          Next Follow-Up Action
          <select data-next-action="${escapeHtml(claim.claimId)}"${serviceLines.length ? "" : " disabled"}>
            <option value="">Choose next action...</option>
            ${actionOptions(selectedAction)}
          </select>
        </label>
        <label>
          Next Due Date
          <input type="date" data-next-date="${escapeHtml(claim.claimId)}" value="${escapeHtml(selectedDate || "")}"${serviceLines.length ? "" : " disabled"}>
        </label>
      </div>
      ${canReassign ? `<div class="work-card-reassign">
        <label>Reassign to
          <select data-reassign-select="${escapeHtml(claim.claimId)}">
            ${assignableOffshoreBillers().map((u) => `<option value="${escapeHtml(u.name)}"${u.name === claim.assignedTo ? " selected" : ""}>${escapeHtml(u.name)}</option>`).join("")}
          </select>
        </label>
        <button class="small-button" data-reassign="${escapeHtml(claim.claimId)}" type="button">Reassign</button>
      </div>` : ""}
      <div class="work-card-actions">
        <button class="small-button" data-open="${escapeHtml(claim.claimId)}" data-org-id="${escapeHtml(claim.orgId || "")}" type="button">Open</button>
        ${canEditFollowUp ? `<button class="small-button" data-save-followup="${escapeHtml(claim.claimId)}" data-save-followup-org-id="${escapeHtml(claim.orgId || "")}" type="button">Save Follow-Up</button>` : ""}
        ${canStart ? `<button class="small-button" data-start="${escapeHtml(claim.claimId)}" data-start-org-id="${escapeHtml(claim.orgId || "")}" type="button">Start</button>` : ""}
        ${canSubmit ? `<button class="small-button" data-submit="${escapeHtml(claim.claimId)}" data-submit-org-id="${escapeHtml(claim.orgId || "")}" type="button"${submitGate.blocked ? ` disabled title="${escapeHtml(submitGate.title)}"` : submitGate.title ? ` title="${escapeHtml(submitGate.title)}"` : ""}>Submit For Review</button>` : ""}
        ${stage === "submitted" ? `<span class="work-card-awaiting">${workflowPerspective === "reviewer" ? "Approval required" : "Approval pending"}</span><button class="small-button" data-withdraw-review="${escapeHtml(claim.claimId)}" data-withdraw-review-org-id="${escapeHtml(claim.orgId || "")}" type="button">Withdraw and edit</button>` : ""}
        ${canUndoApproval ? `<button class="small-button" data-undo-approval="${escapeHtml(claim.claimId)}" data-undo-approval-org-id="${escapeHtml(claim.orgId || "")}" type="button">Undo approval</button>` : ""}
        ${canDeleteTasks ? `<button class="small-button" data-delete-task="${escapeHtml(claim.claimId)}" data-delete-task-org-id="${escapeHtml(claim.orgId || "")}" type="button">Delete Task</button>` : ""}
      </div>
    </div>
  `;
  }).join("") : assignmentQueueEmptyState(isOffshore, chips.length > 0, queue);

  return `
    <section class="assignment-queue" aria-label="Offshore work queue">
      <div class="assignment-section-head">
        <div>
          <span class="assignment-kicker">Work queue</span>
          <strong>${trackerApiEnabled() && workflowBoardState.summary?.complete ? formatNounCount(workflowBoardState.totalRows, "matching assignment") : `${filtered.length} of ${queue.length} assigned claim${queue.length === 1 ? "" : "s"}`}</strong>
        </div>
      </div>
      ${filterBar}
      <div class="assignment-queue-list">${cards}</div>
    </section>
  `;
}

// --- Assignments: ready-to-assign rail -----------------------------------

function renderAssignmentIntakeRail(assignable, queue, capacity) {
  if (!assignable.length) return "";
  const recommended = capacity.recommendedOwner?.owner || "";
  const canAssign = hasPermission(PERMISSIONS.ASSIGN_CLAIMS);
  const visible = trackerApiEnabled() ? assignable : assignable.slice(0, 6);
  const selIds = canAssign ? visible.filter((c) => assignmentIntakeSelection.has(c.claimId)).map((c) => c.claimId) : [];
  const bulkBar = selIds.length ? `
      <div class="assignment-bulk-bar">
        <span class="assignment-bulk-count">${selIds.length} selected</span>
        <select class="assignment-bulk-owner" data-bulk-owner aria-label="Bulk assign owner">
          ${assignableOffshoreBillers().map((u) => `<option value="${escapeHtml(u.name)}"${u.name === recommended ? " selected" : ""}>${escapeHtml(u.name)}</option>`).join("")}
        </select>
        <input type="date" class="assignment-bulk-due" data-bulk-due value="${escapeHtml(todayDateString())}" aria-label="Bulk assignment due date">
        <input type="text" class="assignment-bulk-instruction" data-bulk-instruction placeholder="Instructions for the biller (optional)" aria-label="Bulk assignment instructions">
        <button class="small-button primary" type="button" data-bulk-assign-selected>Assign ${selIds.length}</button>
        <button class="small-button" type="button" data-bulk-clear-selection>Clear</button>
      </div>` : "";
  return `
    <section class="assignment-intake" aria-label="Ready to assign">
      <div class="assignment-section-head">
        <div>
          <span class="assignment-kicker">Ready to assign</span>
          <strong>${formatNounCount(capacity.totals?.readyClaims ?? assignable.length, "claim")} waiting for an owner</strong>
          <p>Sorted by RCM priority, past-due follow-up, aging, and open AR.</p>
        </div>
        ${recommended ? pill(`Suggested owner: ${recommended}`, "blue") : ""}
      </div>
      ${bulkBar}
      <div class="assignment-candidate-grid">
        ${visible.map((claim, index) => renderAssignmentCandidateCard(claim, suggestedAssignmentOwner(index, queue))).join("")}
      </div>
    </section>
  `;
}

function assignmentReviewPipelineModel(claims = []) {
  const stages = assignmentReviewPipelineStages().map((stage) => ({
    ...stage,
    claims: 0,
    expected: 0,
    paid: 0,
    outstanding: 0,
    delta: 0,
    owners: new Set(),
    dueRisk: 0,
    guarded: 0,
    rows: []
  }));
  const stageMap = new Map(stages.map((stage) => [stage.key, stage]));
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    const stage = stageMap.get(assignmentReviewPipelineStage(claim)) || stageMap.get("monitor");
    const financial = claimFinancialView(claim);
    const due = assignmentDueStatus(claim);
    const guards = evaluateClaimCalculationGuards(claim);
    const delta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    const owner = claim.assignedTo || (stage.key === "unassigned" ? "Needs owner" : "Manager / Admin");
    const row = {
      claimId: claim.claimId || "",
      owner,
      status: claim.assignmentStatus || "Unassigned",
      dueLabel: due.label,
      dueKey: due.key,
      expected: financial.expectedReimbursement,
      paid: financial.totalInsurancePaid,
      outstanding: financial.sourceOutstanding,
      delta,
      priority: workPriorityScore(claim),
      guardSummary: calculationGuardSummary(guards),
      nextAction: assignmentReviewPipelineNextAction(claim, stage.key, due, guards)
    };
    stage.claims += 1;
    stage.expected += row.expected;
    stage.paid += row.paid;
    stage.outstanding += row.outstanding;
    stage.delta += row.delta;
    stage.owners.add(owner);
    if (["overdue", "missingDue", "dueToday", "dueSoon", "unassigned"].includes(due.key) || stage.key === "bounced") stage.dueRisk += 1;
    if (guards.length) stage.guarded += 1;
    stage.rows.push(row);
  });
  const rows = stages.map((stage) => ({
    ...stage,
    expected: moneyNumber(stage.expected),
    paid: moneyNumber(stage.paid),
    outstanding: moneyNumber(stage.outstanding),
    delta: moneyNumber(stage.delta),
    ownerCount: stage.owners.size,
    owners: Array.from(stage.owners).sort(),
    rows: stage.rows.sort((a, b) => b.priority - a.priority || b.outstanding - a.outstanding || a.claimId.localeCompare(b.claimId)).slice(0, 3)
  }));
  const totals = rows.reduce((sum, stage) => {
    sum.claims += stage.claims;
    sum.expected += stage.expected;
    sum.paid += stage.paid;
    sum.outstanding += stage.outstanding;
    sum.delta += stage.delta;
    sum.blocked += ["unassigned", "bounced"].includes(stage.key) ? stage.claims : stage.dueRisk;
    if (stage.key === "submitted") sum.managerReview += stage.claims;
    if (["assigned", "inProgress", "bounced"].includes(stage.key)) sum.offshoreOwned += stage.claims;
    return sum;
  }, { claims: 0, expected: 0, paid: 0, outstanding: 0, delta: 0, blocked: 0, managerReview: 0, offshoreOwned: 0 });
  return {
    stages: rows,
    totals: {
      ...totals,
      expected: moneyNumber(totals.expected),
      paid: moneyNumber(totals.paid),
      outstanding: moneyNumber(totals.outstanding),
      delta: moneyNumber(totals.delta)
    }
  };
}

function assignmentWorkflowPerspective() {
  if (canonicalRole(state.role) === "OffshoreBiller") return "worker";
  if (hasPermission(PERMISSIONS.APPROVE_REVIEW) && (!trackerApiEnabled() || assignmentScopeUiState.mode === "team")) return "reviewer";
  return "worker";
}

function workflowAssignmentStatusLabel(status, perspective = "worker") {
  const normalized = String(status || "").trim();
  const reviewer = perspective === "reviewer";
  if (normalized === "Submitted For Review") return reviewer ? "Approval required" : "Approval pending";
  if (normalized === "Bounced Back") return reviewer ? "Changes requested" : "Changes required";
  return normalized || "Unassigned";
}

function assignmentReviewPipelineStages() {
  const reviewer = assignmentWorkflowPerspective() === "reviewer";
  return [
    { key: "unassigned", label: "Unassigned Intake", ownerLabel: "Manager", tone: "gray", command: "/api/workflow/assign" },
    { key: "assigned", label: "Assigned Work", ownerLabel: "Offshore", tone: "blue", command: "/api/workflow/follow-up" },
    { key: "inProgress", label: "In Progress", ownerLabel: "Offshore", tone: "blue", command: "/api/workflow/follow-up" },
    { key: "submitted", label: reviewer ? "Approval Required" : "Approval Pending", ownerLabel: "Manager", tone: "blue", command: "/api/workflow/submit-review" },
    { key: "bounced", label: reviewer ? "Changes Requested" : "Changes Required", ownerLabel: "Offshore", tone: "red", command: "Return work for correction" },
    { key: "approved", label: "Approved Outcome", ownerLabel: "Manager", tone: "green", command: "Approve submitted work" },
    { key: "monitor", label: "Closed / Monitor", ownerLabel: "System", tone: "gray", command: "No active workflow command" }
  ];
}

function assignmentReviewPipelineStage(claim = {}) {
  const status = claim.assignmentStatus || "";
  if (status === "Bounced Back") return "bounced";
  if (status === "Submitted For Review") return "submitted";
  if (status === "Approved") return "approved";
  if (status === "In Progress") return "inProgress";
  if (status === "Assigned") return "assigned";
  if (claim.assignedTo) return "assigned";
  if (claimFinancialView(claim).sourceOutstanding > 0) return "unassigned";
  return "monitor";
}

function assignmentReviewPipelineNextAction(claim = {}, stageKey = "", due = {}, guards = []) {
  if (stageKey === "unassigned") return "Assign owner, SLA due date, and required proof packet.";
  if (stageKey === "bounced") return "Correction owner fixes manager reason and resubmits with proof.";
  if (stageKey === "submitted") return "Manager approve, bounce, or escalate with audit note.";
  if (guards.length) return `Resolve guard before manager approval: ${calculationGuardSummary(guards)}.`;
  if (due.key === "overdue") return "Escalate overdue owner and refresh payer/client proof.";
  if (due.key === "missingDue") return "Set assignment due date before work continues.";
  if (stageKey === "approved") return "Preserve approval evidence and keep next follow-up active.";
  if (stageKey === "monitor") return "No active assignment; monitor cash and future payer events.";
  return claim.action || "Work assigned next action and submit for review.";
}

function renderAssignmentReviewPipeline(claims = []) {
  const model = assignmentReviewPipelineModel(claims);
  return `
    <section class="assignment-review-pipeline" aria-label="Assignment and review pipeline">
      <div class="assignment-review-pipeline-head">
        <div>
          <span>Assignment → Review Pipeline</span>
          <strong>${formatNounCount(model.totals.claims, "claim")} classified from intake to approved outcome</strong>
          <p>Manager control view for who owns the next move, which workflow command applies, and whether expected, paid, and AR still tie out.</p>
        </div>
        ${pill(`${model.totals.blocked} needs attention`, model.totals.blocked ? "amber" : "green")}
      </div>
      <div class="assignment-review-pipeline-summary">
        <div><span>Pipeline AR</span><strong>${money(model.totals.outstanding)}</strong><small>${formatNounCount(model.totals.claims, "claim")}</small></div>
        <div><span>Expected</span><strong>${money(model.totals.expected)}</strong><small>Filtered claim expected</small></div>
        <div><span>Paid</span><strong>${money(model.totals.paid)}</strong><small>Insurance paid</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - Paid - AR</small></div>
        <div><span>Manager Review</span><strong>${model.totals.managerReview}</strong><small>Submitted decision lane</small></div>
        <div><span>Offshore Owned</span><strong>${model.totals.offshoreOwned}</strong><small>Assigned / correction work</small></div>
      </div>
      <div class="assignment-review-pipeline-list">
        <div class="assignment-review-pipeline-row assignment-review-pipeline-header">
          <span>Stage</span>
          <span>Owner / Command</span>
          <span>Claims / AR</span>
          <span>Risk</span>
          <span>Top Rows</span>
          <span>Next Action</span>
        </div>
        ${model.stages.map(renderAssignmentReviewPipelineRow).join("")}
      </div>
    </section>
  `;
}

function renderAssignmentReviewPipelineRow(stage) {
  const topRows = stage.rows.map((row) => `${row.claimId} ${money(row.outstanding)}`).join(" · ") || "No rows";
  const firstNextAction = stage.rows[0]?.nextAction || assignmentReviewPipelineNextAction({}, stage.key, {}, []);
  return `
    <div class="assignment-review-pipeline-row">
      <span data-label="Stage">
        ${pill(stage.label, stage.tone)}
        <small>${stage.ownerLabel} lane</small>
      </span>
      <span data-label="Owner / Command">
        <strong>${stage.owners.length ? escapeHtml(stage.owners.slice(0, 2).join(", ")) : escapeHtml(stage.ownerLabel)}</strong>
        <small>${escapeHtml(stage.command)}</small>
      </span>
      <span data-label="Claims / AR">
        <strong>${stage.claims}</strong>
        <small>${money(stage.outstanding)} AR · ${money(stage.expected)} expected</small>
      </span>
      <span data-label="Risk">
        <strong>${stage.dueRisk} SLA / routing</strong>
        <small>${stage.guarded} guarded · delta ${money(stage.delta)}</small>
      </span>
      <span data-label="Top Rows">
        <strong>${escapeHtml(topRows)}</strong>
        <small>${stage.ownerCount} owner${stage.ownerCount === 1 ? "" : "s"}</small>
      </span>
      <span data-label="Next Action">
        <small>${escapeHtml(firstNextAction)}</small>
      </span>
    </div>
  `;
}

// ===== Approval Queue (Phase 1) =====
// Supervisor approve/reject over the per-service-line follow-up ACTIONS the
// offshore team set (memory offshore-approval-oversight-model). Replaces the
// claim-level Review board. Backed by:
//   GET  /api/workflow/followup-review/pending?mode=pending|rework
//   POST /api/workflow/followup-review  (via enqueueWorkflowCommand, one row per
//        selected line, all sharing one batchId per bulk click)
// Two-level grouping (client -> payer) is computed server-side in NODE; the row's
// expectedUpdatedAt (overlay version key) is echoed back so a stale approval is
// caught and the row re-opens as pending ("changed since - re-review").
const approvalQueueState = {
  pending: { key: "", status: "idle", body: null, error: "" },
  rework: { key: "", status: "idle", body: null, error: "" }
};
const approvalQueueSelection = new Set();
const approvalQueueCollapsed = new Set();
let approvalQueueRejectArmed = false;
let approvalQueueRemoveArmed = false;
// Which queue the host currently shows. A reload of the SAME queue keeps its
// rows up instead of flashing "Loading the approval queue..." over them.
let approvalQueueRenderedMode = "";

// The Approval Queue speaks the overlay's vocabulary, where a request to
// REMOVE an action arrives as an EMPTY action. Printed raw it read
// "(no action)" or "Clear follow-up", neither of which tells the reviewer
// that something is about to be taken away, or what.
function approvalQueueActionLabel(row) {
  return followUpActionIsRemoval(row && row.followUpAction) ? "Remove the follow-up action" : String((row && row.followUpAction) || "");
}

function approvalQueueEffectText(row) {
  if (!followUpActionIsRemoval(row && row.followUpAction)) return "";
  return followUpRemovalTarget(row && row.currentAction, row && row.currentFollowUpDate) || "Removes the follow-up action and its date";
}

function approvalQueueRowKey(orgId, claimId, serviceId) {
  return `${String(orgId || "")}::${String(claimId || "")}::${String(serviceId || "")}`;
}

// MY WORK HAS ONE APPROVAL LIST, SO THIS QUEUE HAS NO HOST OF ITS OWN THERE.
//
// "Needs my approval" used to mount this queue in a SECOND section below the
// supervisor-request list. Each section printed its own total and its own
// empty state without ever consulting the other, so a company with nothing in
// the first list and seven items in the second read "No pending action-change
// requests" directly above seven cards awaiting approval (owner, 2026-09-18).
//
// renderSupervisorProposals now draws BOTH routes inside .oversight-results,
// so there is nothing left for this renderer to paint into on My Work.
// Returning null rather than falling through to els.reviewQueue also stops the
// hidden Reviews page being repainted behind the user on the My requests tab.
function approvalQueueHost() {
  return state.view === "assignments" ? null : els.reviewQueue;
}

function approvalQueueMode() {
  if (state.view === "assignments" && oversightState().tab === "requests") return "mine";
  // Supervisors (APPROVE_REVIEW) get the pending queue; workers get their own
  // rejected "needs rework" rail.
  return hasPermission(PERMISSIONS.APPROVE_REVIEW) ? "pending" : "rework";
}

function approvalQueueSelectedOrg() {
  return activeClientGroupServerOrgId() || (state.view === "assignments" ? String(oversightState().orgId || "").trim() : "");
}

function approvalQueueRequestKey(mode) {
  return JSON.stringify({ readScope: additionalPortalDataReadScope(), base: workflowApiBase(), mode, status: mode === "mine" ? oversightState().requestStatus || "" : "", days: mode === "archive" ? approvalArchiveDays(oversightState()) : 0, orgId: approvalQueueSelectedOrg(), access: workflowCommandAccessSnapshot() });
}

function invalidateApprovalQueue() {
  approvalQueueState.pending = { key: "", status: "idle", body: null, error: "" };
  approvalQueueState.rework = { key: "", status: "idle", body: null, error: "" };
  approvalQueueState.mine = { key: "", status: "idle", body: null, error: "" };
  approvalQueueState.archive = { key: "", status: "idle", body: null, error: "" };
}

// Async fetch-then-rerender, mirroring requestClientPaymentChecks. Caches per
// (base, mode, access) so a re-render does not re-fetch; on arrival it re-renders
// the Review view if it is still active.
function requestApprovalQueue(mode) {
  const slice = approvalQueueState[mode] || { key: "", status: "idle", body: null, error: "" };
  if (!workflowApiBase() || typeof fetch !== "function") return slice;
  const key = approvalQueueRequestKey(mode);
  if (slice.key === key && ["pending", "ok", "error", "denied"].includes(slice.status)) return slice;
  approvalQueueState[mode] = { key, status: "pending", body: null, error: "" };
  const pending = approvalQueueState[mode];
  const current = () => approvalQueueState[mode] === pending && approvalQueueRequestKey(mode) === key;
  const token = workflowApiToken();
  const params = new URLSearchParams({ mode });
  if (mode === "mine" && oversightState().requestStatus) params.set("status", oversightState().requestStatus);
  if (mode === "archive") params.set("days", String(approvalArchiveDays(oversightState())));
  const orgId = approvalQueueSelectedOrg();
  if (orgId) params.set("orgId", orgId);
  fetch(additionalPortalDataReadUrl(`${workflowApiBase()}/api/workflow/followup-review/pending?${params.toString()}`), {
    cache: "no-store",
    headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) }
  })
    .then(async (response) => {
      if (!current()) return;
      const body = await response.json().catch(() => ({}));
      if (!current()) return;
      if (response.status === 403 && body && body.scopeDenial === true) {
        // The server refused this reviewer outright. Terminal, and NOT an error:
        // no retry, no "could not load", no empty queue implying nothing to do.
        approvalQueueState[mode] = {
          key,
          status: "denied",
          body: null,
          error: "",
          scopeDenial: { code: body.reason || "", title: body.title || "", message: body.message || "", remedy: body.remedy || "" }
        };
      } else if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        approvalQueueState[mode] = { key, status: "error", body: null, error: "Your session expired. Sign in again — this page will still be here." };
      } else if (!response.ok || body.error) {
        console.warn("[approval-queue] load failed:", body.message || body.error || `HTTP ${response.status}`);
        approvalQueueState[mode] = { key, status: "error", body: null, error: body.message || body.error || `HTTP ${response.status}` };
      } else {
        approvalQueueState[mode] = { key, status: "ok", body, error: "" };
        if (mode !== "archive") pruneApprovalQueueSelection(body);
      }
      renderApprovalQueueLanded();
      // My requests and Needs my approval both draw on this read and both live
      // in the same results container, so they have to be told the answer
      // arrived. So does the tab strip: the unified "Needs my approval" total is
      // unknowable until this route answers, and until then that badge holds
      // rather than printing a partial sum.
      if (state.view === "assignments" && ["requests", "approvals"].includes(oversightState().tab) && oversightState().status === "ready") renderOversightWorkResults();
      if (typeof paintOversightTabCounts === "function") paintOversightTabCounts();
    })
    .catch((error) => {
      if (!current()) return;
      approvalQueueState[mode] = { key, status: "error", body: null, error: error.message };
      if (state.view === "reviews" || (state.view === "assignments" && ["requests", "approvals"].includes(oversightState().tab))) renderReviews();
      if (state.view === "assignments" && oversightState().tab === "requests" && oversightState().status === "ready") renderOversightWorkResults();
      // A route that FAILED is still an answer about the badge: it means the
      // unified total cannot be stated, so the strip must stop showing a number
      // derived from the other route alone.
      if (typeof paintOversightTabCounts === "function") paintOversightTabCounts();
    });
  return approvalQueueState[mode];
}

// Drop selections that are no longer in the queue (approved/rejected away or
// re-edited), so a refetch cleanly reconciles the sticky bar count.
function pruneApprovalQueueSelection(body = {}) {
  if (body.complete !== true || body.truncated === true) {
    approvalQueueSelection.clear();
    approvalQueueRejectArmed = false;
    approvalQueueRemoveArmed = false;
    return;
  }
  const live = new Set((body.rows || [])
    .filter((row) => row.canReview !== false)
    .map((row) => approvalQueueRowKey(row.orgId, row.claimId, row.serviceId)));
  Array.from(approvalQueueSelection).forEach((key) => { if (!live.has(key)) approvalQueueSelection.delete(key); });
}

function renderReviews() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("reviews")) return;
  // On My Work the two approval routes share ONE list, so every repaint this
  // queue's own controls ask for is a repaint of that list. Delegating here,
  // rather than at each call site, means the selection, bulk and retry handlers
  // below need no knowledge of which screen they are running on.
  if (state.view === "assignments") {
    if (oversightState().tab === "approvals") renderOversightWorkResults();
    return;
  }
  if (!approvalQueueHost()) return;
  const mode = approvalQueueMode();
  if (!workflowApiBase() || typeof fetch !== "function") {
    approvalQueueHost().innerHTML = approvalQueueMessageHtml("The approval queue is temporarily unavailable. Please try again.");
    return;
  }
  const slice = requestApprovalQueue(mode);
  if (slice.status === "idle" || slice.status === "pending") {
    if (approvalQueueRenderedMode !== mode || !approvalQueueHost().firstElementChild) {
      approvalQueueHost().innerHTML = approvalQueueMessageHtml("Loading the approval queue…");
      approvalQueueRenderedMode = "";
    }
    return;
  }
  if (slice.status === "denied") {
    approvalQueueHost().innerHTML = `
    <section class="panel">
      <div class="ops-section-heading"><strong>Approval Queue</strong><span>Supervisor sign-off on offshore follow-up actions.</span></div>
      ${scopeDeniedPanelHtml(normalizeScopeDenial(slice.scopeDenial, "response") || portalScopeDenial())}
    </section>
  `;
    bindApprovalQueueEvents(mode);
    return;
  }
  if (slice.status === "error") {
    // The headline used to carry the raw backend string ("Approval Queue read is
    // not configured.") over body copy about All Data filters, with the Retry
    // orphaned outside the dashed border. Headline, body and action now agree,
    // and the raw string stays in the console where it is useful.
    approvalQueueHost().innerHTML = approvalQueueMessageHtml("Could not load the approval queue", {
      body: "The approval queue could not be loaded. This is a connection or configuration problem, not an empty queue.",
      actionsHtml: `<button class="small-button" data-approval-retry type="button">Retry</button>`
    });
    bindApprovalQueueEvents(mode);
    return;
  }
  approvalQueueHost().innerHTML = mode === "mine" ? renderPersonalFollowUpReviews(slice.body || {}) : mode === "rework"
    ? renderReworkRailHtml(slice.body || {})
    : renderApprovalQueueHtml(slice.body || {});
  approvalQueueRenderedMode = mode;
  if (mode === "mine") { bindWorkClaimLinks(approvalQueueHost()); bindManagementListPager(approvalQueueHost(), "personalFollowups", renderReviews); }
  else bindApprovalQueueEvents(mode);
}

function approvalQueueMessageHtml(message, options = {}) {
  return `
    <section class="panel">
      <div class="ops-section-heading"><strong>Approval Queue</strong><span>Supervisor sign-off on offshore follow-up actions.</span></div>
      ${empty(message, options)}
    </section>
  `;
}

// ---- Supervisor pending queue -------------------------------------------------
function renderApprovalQueueHtml(body = {}) {
  const groups = Array.isArray(body.groups) ? body.groups : [];
  const total = Number(body.total || 0);
  if (!total) {
    return `
      <section class="panel">
        <div class="ops-section-heading"><strong>Team approvals</strong><span>Supervisor sign-off on offshore follow-up actions.</span></div>
        ${empty("No other approvals are waiting.", { body: state.view === "assignments" ? "Requests for a specific supervisor appear above." : "This review queue is up to date." })}
      </section>
    `;
  }
  const noteBadge = body.noteVisible ? "" : ` ${pill("Notes restricted", "gray")}`;
  const unmatched = Number(body.provenance?.unmatched || 0);
  const sourceNotice = body.truncated
    ? `<div class="callout amber"><strong>Queue is capped.</strong> Showing ${total} of more than ${Number(body.rowLimit || total)} matching service-line actions.</div>`
    : (unmatched
      ? `<div class="callout amber"><strong>${unmatched} row${unmatched === 1 ? "" : "s"} need review.</strong> They remain visible but cannot be approved because no current service line matched the portal record.</div>`
      : "");
  const populationCertified = body.complete === true && body.truncated !== true;
  const groupsHtml = groups.map((group) => renderApprovalQueueGroupHtml(group, populationCertified)).join("");
  return `
    <section class="panel approval-queue">
      <div class="ops-section-heading">
        <strong>Team approvals</strong>
        <span>Review matching service lines together by claim. Select several claims to approve them together.${noteBadge}</span>
      </div>
      <div class="ops-tab-summary">
        <div><span>Awaiting approval</span><strong data-approval-total>${total}</strong><small>service-line actions</small></div>
        <div><span>Clients</span><strong data-approval-clients>${groups.length}</strong><small>with pending work</small></div>
        <div><span>Selected</span><strong data-approval-selected-count>${approvalQueueSelection.size}</strong><small>ready to decide</small></div>
      </div>
      ${sourceNotice}
      <div class="approval-queue-groups">${groupsHtml}</div>
      ${renderApprovalQueueBulkBarHtml(populationCertified)}
    </section>
  `;
}

function renderApprovalQueueGroupHtml(group = {}, populationCertified = true) {
  const client = String(group.client || "(Unassigned client)");
  const clientKey = `client:${client}`;
  const collapsed = approvalQueueCollapsed.has(clientKey);
  const payers = Array.isArray(group.payers) ? group.payers : [];
  const payersHtml = collapsed ? "" : payers.map((payer) => renderApprovalQueuePayerHtml(client, payer, populationCertified)).join("");
  return `
    <div class="approval-queue-group">
      <div class="approval-queue-group-head">
        <button class="approval-queue-toggle" data-approval-toggle="${escapeHtml(clientKey)}" type="button" aria-expanded="${collapsed ? "false" : "true"}">
          <span class="approval-queue-caret">${collapsed ? "▸" : "▾"}</span>
          <strong>${escapeHtml(client)}</strong>
          ${pill(`${group.count} pending`, "amber")}
        </button>
      </div>
      ${payersHtml}
    </div>
  `;
}

function renderApprovalQueuePayerHtml(client, payer = {}, populationCertified = true) {
  const payerLabel = String(payer.payer || "(No payer)");
  const payerKey = `payer:${client}::${payerLabel}`;
  const collapsed = approvalQueueCollapsed.has(payerKey);
  const rows = Array.isArray(payer.rows) ? payer.rows : [];
  const rowsHtml = collapsed ? "" : supervisorClaimGroups(rows).map((claimRows) => renderApprovalClaimHtml(claimRows, populationCertified)).join("");
  const rowKeys = rows.filter((row) => row.canReview !== false).map((row) => approvalQueueRowKey(row.orgId, row.claimId, row.serviceId));
  const groupDisabled = populationCertified && rowKeys.length ? "" : "disabled";
  return `
    <div class="approval-queue-payer">
      <div class="approval-queue-payer-head">
        <button class="approval-queue-toggle" data-approval-toggle="${escapeHtml(payerKey)}" type="button" aria-expanded="${collapsed ? "false" : "true"}">
          <span class="approval-queue-caret">${collapsed ? "▸" : "▾"}</span>
          <span>${escapeHtml(payerLabel)}</span>
          ${pill(`${payer.count}`, "blue")}
        </button>
        <div class="approval-queue-payer-actions">
          <button class="small-button" data-approval-select-group="${escapeHtml(rowKeys.join("|"))}" type="button" ${groupDisabled}>Select all</button>
          <button class="small-button" data-approval-approve-group="${escapeHtml(rowKeys.join("|"))}" type="button" ${groupDisabled}>Approve all</button>
          <button class="small-button danger" data-approval-reject-group="${escapeHtml(rowKeys.join("|"))}" type="button" ${groupDisabled}>Reject all</button>
        </div>
      </div>
      ${rowsHtml}
    </div>
  `;
}

// Group only identical review instructions, retaining exact service-line identities.
function supervisorClaimGroups(rows, proposed = false) {
  const groups = new Map();
  for (const row of rows || []) {
    const identity = JSON.stringify([row.orgId, row.claimId, proposed ? row.requesterUserId : row.setBy,
      proposed ? row.reviewerUserId : "", proposed ? row.action : row.followUpAction, row.followUpDate || "",
      proposed ? String(row.note || "").trim() : (row.noteClusterKey || row.note || ""), row.status || row.reviewStatus || ""]);
    if (!groups.has(identity)) groups.set(identity, []);
    groups.get(identity).push(row);
  }
  return Array.from(groups.values());
}

function renderApprovalClaimHtml(rows, certified) {
  if (rows.length === 1) return renderApprovalQueueRowHtml(rows[0], certified);
  const row = rows[0];
  const keys = rows.map((entry) => approvalQueueRowKey(entry.orgId, entry.claimId, entry.serviceId));
  const disabled = !certified || rows.some((entry, i) => entry.canReview === false || approvalDecisionIsPending(keys[i]));
  const selected = !disabled && keys.every((key) => approvalQueueSelection.has(key));
  const note = row.noteRestricted ? "Note hidden (no PHI access)" : (row.note || "No current note.");
  return `<article class="oversight-card approval-claim"><div class="oversight-card-top"><strong>Claim ${escapeHtml(row.claimId)}</strong>${agingBucketBadgeHtml(oldestAgingDaysView(rows))}<span>${rows.length} service lines</span></div>
    <p><b>${escapeHtml(approvalQueueActionLabel(row))}</b> · ${escapeHtml(approvalQueueEffectText(row) || `Due ${row.followUpDate || "Not set"}`)}</p><p class="oversight-note">${escapeHtml(note)}</p>
    <div class="oversight-actions"><button class="small-button" type="button" data-approval-select-group="${escapeHtml(keys.join("|"))}" ${disabled ? "disabled" : ""}>${selected ? "Deselect claim" : "Select claim"}</button>
    <button class="small-button" type="button" data-approval-approve-group="${escapeHtml(keys.join("|"))}" ${disabled ? "disabled" : ""}>Approve claim</button>
    <button class="small-button danger" type="button" data-approval-reject-group="${escapeHtml(keys.join("|"))}" ${disabled ? "disabled" : ""}>Send back claim</button></div>
    ${disabled ? '<p class="oversight-hint">Open the service lines to review unavailable decisions.</p>' : ""}
    <details><summary>Review ${rows.length} service lines${rows.some((entry) => entry.newNotesCount > 0) ? " · New notes available" : ""}</summary>${rows.map((entry) => renderApprovalQueueRowHtml(entry, certified)).join("")}</details></article>`;
}

// Phase 2: the next-day EZClaim note(s) that arrived AFTER the biller set the
// follow-up action (first_observed_at_utc > overlay updated_at) -- what the
// supervisor actually judges the approve/reject on. PHI-gated like the note
// above. ONE definition: the unified approval card and the service-line row
// both print it, and a second copy would drift the moment either changed.
function approvalQueueNewNotesHtml(row = {}) {
  const newNotes = Array.isArray(row.newNotes) ? row.newNotes : [];
  if (row.newNotesRestricted) {
    return `<div class="approval-queue-row-newnotes is-restricted"><span class="approval-queue-newnotes-label">New since set</span> <em class="approval-queue-note-restricted">${row.newNotesCount || 0} new note${(row.newNotesCount || 0) === 1 ? "" : "s"} hidden (no PHI access)</em></div>`;
  }
  if (!newNotes.length) return "";
  const items = newNotes.map((entry) => `<li class="approval-queue-newnote"><span class="approval-queue-newnote-text">${escapeHtml(entry.text || "")}</span><span class="approval-queue-newnote-meta muted">${escapeHtml(noteAuthorDisplay(entry.author))}${entry.observedAt ? ` · ${escapeHtml(formatRelativeOrDate(entry.observedAt))}` : ""}</span></li>`).join("");
  return `<div class="approval-queue-row-newnotes"><span class="approval-queue-newnotes-label">New since set</span><ul class="approval-queue-newnote-list">${items}</ul></div>`;
}

// A service line's own approve/reject/remove on the Review page. My Work's
// approval list decides whole change cards instead (decideApprovalChangeCard).
function approvalQueueRowActionsHtml(key, blocked) {
  const disabled = blocked ? "disabled" : "";
  return `<button class="small-button" data-approval-approve-row="${escapeHtml(key)}" type="button" ${disabled}>Approve</button>`
    + `<button class="small-button danger" data-approval-reject-row="${escapeHtml(key)}" type="button" ${disabled}>Reject</button>`
    + `<button class="small-button" data-approval-remove-row="${escapeHtml(key)}" type="button" ${disabled} title="Clear this follow-up action from the line">Remove</button>`;
}

function renderApprovalQueueRowHtml(row = {}, populationCertified = true, options = {}) {
  const key = approvalQueueRowKey(row.orgId, row.claimId, row.serviceId);
  const blocked = row.canReview === false || approvalDecisionIsPending(key);
  const selected = populationCertified && !blocked && approvalQueueSelection.has(key);
  const blockedReason = blocked
    ? `<div class="callout amber"><strong>Review unavailable.</strong> ${escapeHtml(approvalDecisionIsPending(key) ? "This decision is being delivered. Use Workflow Sync to retry a failed delivery." : (row.cannotReviewMessage || "This row cannot be reviewed."))}</div>`
    : "";
  const noteHtml = row.noteRestricted
    ? `<em class="approval-queue-note-restricted">Note hidden (no PHI access)</em>`
    : (row.note ? escapeHtml(row.note) : `<em>No current note.</em>`);
  // Phase 2: the next-day EZClaim note(s) that arrived AFTER the biller set the
  // follow-up action (first_observed_at_utc > overlay updated_at) — what the
  // supervisor actually judges the approve/reject on. PHI-gated like the note above.
  const newSinceSetHtml = approvalQueueNewNotesHtml(row);
  const due = row.followUpDate ? ` · due ${escapeHtml(row.followUpDate)}` : "";
  const dos = row.dos ? `DOS ${escapeHtml(row.dos)}` : "";
  const cpt = row.cpt ? ` · CPT ${escapeHtml(row.cpt)}` : "";
  const setAt = row.setAt ? formatRelativeOrDate(row.setAt) : "";
  return `
    <div class="approval-queue-row${selected ? " is-selected" : ""}${blocked ? " is-disabled" : ""}" data-approval-row="${escapeHtml(key)}">
      <label class="approval-queue-check">
        <input type="checkbox" data-approval-select="${escapeHtml(key)}" ${selected ? "checked" : ""} ${blocked || !populationCertified ? "disabled" : ""} aria-label="Select ${escapeHtml(row.claimId)} service ${escapeHtml(row.serviceId)}">
      </label>
      <div class="approval-queue-row-body">
        <div class="approval-queue-row-top">
          ${workClaimLinkHtml(row)} <span class="muted">svc ${escapeHtml(row.serviceId)}</span>
          ${agingBucketBadgeHtml(agingDaysView(row))}<span class="approval-queue-facts">${dos}${cpt}</span>
        </div>
        <div class="approval-queue-row-action">${pill(escapeHtml(approvalQueueActionLabel(row)), "blue")}<span class="muted">${escapeHtml(approvalQueueEffectText(row)) || due}</span></div>
        <div class="approval-queue-row-note"><span class="muted">Current note:</span> ${noteHtml}</div>
        ${newSinceSetHtml}
        ${blockedReason}
        <div class="approval-queue-row-meta muted">Set by ${escapeHtml(row.setBy || "offshore")}${setAt ? ` · ${escapeHtml(setAt)}` : ""}</div>
      </div>
      <div class="approval-queue-row-actions">${approvalQueueRowActionsHtml(key, blocked)}</div>
    </div>
  `;
}

// SWAP THE ROWS, NOT THE SCREEN.
//
// A decision used to be followed by invalidateApprovalQueue() and a full
// renderReviews() on a 1200 ms timer: the panel the reviewer was reading was
// rebuilt from scratch, losing its scroll position and every open group, and
// the timer was a guess -- if the POST took longer, the refetch read the queue
// before the decision landed, the row came back, and the batch reconciler then
// repainted it away a second time. The row flickered back into a queue it had
// already left.
//
// This replaces only the rows and the two numbers above them. The panel, the
// heading, the summary strip and the bulk bar keep their identity. Anything it
// cannot swap safely -- a different mode on screen, a queue that has just
// become empty, a read that has not answered -- falls back to the full render,
// because a wrong in-place update is worse than an honest repaint.
function renderApprovalQueueResults() {
  const mode = approvalQueueMode();
  const host = approvalQueueHost();
  const slice = approvalQueueState[mode];
  const panel = host && typeof host.querySelector === "function" ? host.querySelector(".approval-queue") : null;
  const groupsHost = panel ? panel.querySelector(".approval-queue-groups") : null;
  const bulkBar = panel ? panel.querySelector(".approval-queue-bulk-bar") : null;
  const body = slice && slice.status === "ok" ? slice.body || {} : null;
  const groups = body && Array.isArray(body.groups) ? body.groups : [];
  if (mode !== "pending" || approvalQueueRenderedMode !== mode || !panel || !groupsHost || !bulkBar || !body || !groups.length) {
    renderReviews();
    return;
  }
  const scrollY = typeof window !== "undefined" && Number.isFinite(window.scrollY) ? window.scrollY : 0;
  const certified = body.complete === true && body.truncated !== true;
  groupsHost.innerHTML = groups.map((group) => renderApprovalQueueGroupHtml(group, certified)).join("");
  // Both halves that carry handlers are replaced together, so re-binding the
  // host cannot leave a second listener on a button that survived.
  bulkBar.outerHTML = renderApprovalQueueBulkBarHtml(certified);
  const total = panel.querySelector("[data-approval-total]");
  if (total) total.textContent = String(Number(body.total || 0));
  const clients = panel.querySelector("[data-approval-clients]");
  if (clients) clients.textContent = String(groups.length);
  bindApprovalQueueEvents(mode);
  if (typeof window !== "undefined" && typeof window.scrollTo === "function" && window.scrollY !== scrollY) window.scrollTo(0, scrollY);
}

// The queue read has answered. Swap where we can, repaint where we cannot.
function renderApprovalQueueLanded() {
  if (!(state.view === "reviews" || (state.view === "assignments" && ["requests", "approvals"].includes(oversightState().tab)))) return;
  renderApprovalQueueResults();
}

// After a decision: ask the server again, but KEEP THE ROWS ON SCREEN while it
// answers. Blanking the queue to a placeholder is the freeze the per-line queue
// was fixed for, and it is the same reviewer at the same desk.
function refreshApprovalQueueAfterDecision() {
  invalidateApprovalQueue();
  if (!(state.view === "reviews" || (state.view === "assignments" && ["requests", "approvals"].includes(oversightState().tab)))) return;
  requestApprovalQueue(approvalQueueMode());
}

function renderApprovalQueueBulkBarHtml(populationCertified = true) {
  const count = populationCertified ? approvalQueueSelection.size : 0;
  const rejectPanel = approvalQueueRejectArmed
    ? `
      <div class="approval-queue-reject-panel">
        <input type="text" data-approval-reject-reason placeholder="Reason to reject (required)" aria-label="Rejection reason">
        <button class="small-button danger" data-approval-confirm-reject type="button">Confirm reject ${count}</button>
        <button class="small-button" data-approval-cancel-reject type="button">Cancel</button>
      </div>`
    : (approvalQueueRemoveArmed
      ? `
      <div class="approval-queue-reject-panel">
        <input type="text" data-approval-remove-note placeholder="Reason to remove the follow-up (required)" aria-label="Removal reason">
        <button class="small-button danger" data-approval-confirm-remove type="button">Remove ${count} follow-up${count === 1 ? "" : "s"}</button>
        <button class="small-button" data-approval-cancel-remove type="button">Cancel</button>
      </div>`
      : "");
  return `
    <div class="approval-queue-bulk-bar${count ? " has-selection" : ""}">
      <span class="approval-queue-bulk-count">${count} selected</span>
      <div class="approval-queue-bulk-actions">
        <button class="small-button" data-approval-approve-selected type="button" ${populationCertified && count ? "" : "disabled"}>Approve selected</button>
        <button class="small-button danger" data-approval-reject-selected type="button" ${populationCertified && count ? "" : "disabled"}>Reject selected</button>
        <button class="small-button" data-approval-remove-selected type="button" ${populationCertified && count ? "" : "disabled"}>Remove selected</button>
      </div>
      ${rejectPanel}
    </div>
  `;
}

// ---- Offshore "Needs rework" rail --------------------------------------------
function renderReworkRailHtml(body = {}) {
  const rows = Array.isArray(body.rows) ? body.rows : [];
  if (!rows.length) {
    return `
      <section class="panel">
        <div class="ops-section-heading"><strong>Needs Rework</strong><span>Follow-up actions a supervisor sent back to you.</span></div>
        ${empty("Nothing needs rework. All your follow-up actions are approved or pending review.")}
      </section>
    `;
  }
  const cards = rows.map((row) => {
    const due = row.followUpDate ? ` · due ${escapeHtml(row.followUpDate)}` : "";
    const dos = row.dos ? `DOS ${escapeHtml(row.dos)}` : "";
    const cpt = row.cpt ? ` · CPT ${escapeHtml(row.cpt)}` : "";
    return `
      <div class="work-card">
        <div class="work-card-header">
          <div>
            <h4>${escapeHtml(row.claimId)} <span class="muted">svc ${escapeHtml(row.serviceId)}</span></h4>
            <p>${escapeHtml(row.client || "")} · ${escapeHtml(row.primaryPayer || "")}${row.secondaryPayer ? ` → ${escapeHtml(row.secondaryPayer)}` : ""}</p>
          </div>
          ${pill("Needs rework", "red")}
        </div>
        <p class="approval-queue-facts">${dos}${cpt}</p>
        <p>${pill(escapeHtml(approvalQueueActionLabel(row)), "blue")}<span class="muted">${escapeHtml(approvalQueueEffectText(row)) || due}</span></p>
        <div class="bounce-callout">
          <strong>Supervisor sent this back${row.rejectedBy ? ` (${escapeHtml(row.rejectedBy)})` : ""}:</strong>
          <p>${row.rejectReason ? escapeHtml(row.rejectReason) : "No reason recorded."}</p>
          <small>Re-edit the follow-up action on AR to re-open it for review.</small>
        </div>
      </div>
    `;
  }).join("");
  return `
    <section class="panel">
      <div class="ops-section-heading"><strong>Needs Rework</strong><span>${rows.length} follow-up action${rows.length === 1 ? "" : "s"} a supervisor sent back to you.</span></div>
      <div class="stack-list">${cards}</div>
    </section>
  `;
}

// ---- Events -------------------------------------------------------------------
function approvalQueueLiveRows() {
  const slice = approvalQueueState.pending;
  return slice.body && Array.isArray(slice.body.rows) ? slice.body.rows : [];
}

function approvalQueuePopulationCertified() {
  const slice = approvalQueueState.pending;
  let body = slice && slice.body;
  // Every decision re-reads this queue. While that re-read runs the list keeps
  // showing the last answer (unifiedApprovalSources); certify from that same
  // answer, minus the rows just decided, so the next card stays clickable
  // instead of locking until the re-read lands.
  if (!body && slice && (slice.status === "idle" || slice.status === "pending")) {
    const held = approvalQueueState.lastPendingAnswer;
    if (held && held.key === approvalQueueRequestKey("pending")) body = held.body;
  }
  return Boolean(body && body.complete === true && body.truncated !== true);
}

function approvalQueueRowsForKeys(keys) {
  const wanted = new Set(keys);
  return approvalQueueLiveRows().filter((row) => row.canReview !== false && wanted.has(approvalQueueRowKey(row.orgId, row.claimId, row.serviceId)));
}

// `hostOverride` is how My Work hands this its container: on that screen the
// queue has no host of its own, because its rows live inside the unified
// approval list. Everything below is unchanged -- same handlers, same
// endpoints, same guards.
function bindApprovalQueueEvents(mode, hostOverride) {
  const host = hostOverride || approvalQueueHost();
  if (!host) return;
  const retry = host.querySelector("[data-approval-retry]");
  if (retry) retry.addEventListener("click", () => { invalidateApprovalQueue(); renderReviews(); });
  bindScopeDenialControls(host, () => { invalidateApprovalQueue(); renderReviews(); });
  bindWorkClaimLinks(host);
  if (mode !== "pending") return;

  host.querySelectorAll("[data-approval-toggle]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.dataset.approvalToggle;
      if (approvalQueueCollapsed.has(key)) approvalQueueCollapsed.delete(key); else approvalQueueCollapsed.add(key);
      renderReviews();
    });
  });
  host.querySelectorAll("[data-approval-select]").forEach((box) => {
    box.addEventListener("change", () => {
      if (!approvalQueuePopulationCertified()) return;
      const key = box.dataset.approvalSelect;
      if (box.checked) approvalQueueSelection.add(key); else approvalQueueSelection.delete(key);
      approvalQueueRejectArmed = false;
      approvalQueueRemoveArmed = false;
      renderReviews();
    });
  });
  host.querySelectorAll("[data-approval-select-group]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (!approvalQueuePopulationCertified()) return;
      const keys = String(btn.dataset.approvalSelectGroup || "").split("|").filter(Boolean);
      const allSelected = keys.every((key) => approvalQueueSelection.has(key));
      keys.forEach((key) => { if (allSelected) approvalQueueSelection.delete(key); else approvalQueueSelection.add(key); });
      renderReviews();
    });
  });
  host.querySelectorAll("[data-approval-approve-row]").forEach((btn) => {
    btn.addEventListener("click", () => submitApprovalDecisions(approvalQueueRowsForKeys([btn.dataset.approvalApproveRow]), "approved"));
  });
  host.querySelectorAll("[data-approval-reject-row]").forEach((btn) => {
    btn.addEventListener("click", () => armApprovalReject([btn.dataset.approvalRejectRow]));
  });
  host.querySelectorAll("[data-approval-approve-group]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (!approvalQueuePopulationCertified()) return;
      submitApprovalDecisions(approvalQueueRowsForKeys(String(btn.dataset.approvalApproveGroup || "").split("|").filter(Boolean)), "approved");
    });
  });
  host.querySelectorAll("[data-approval-reject-group]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (!approvalQueuePopulationCertified()) return;
      armApprovalReject(String(btn.dataset.approvalRejectGroup || "").split("|").filter(Boolean));
    });
  });
  const approveSelected = host.querySelector("[data-approval-approve-selected]");
  if (approveSelected) approveSelected.addEventListener("click", () => {
    if (!approvalQueuePopulationCertified()) return;
    submitApprovalDecisions(approvalQueueRowsForKeys(Array.from(approvalQueueSelection)), "approved");
  });
  const rejectSelected = host.querySelector("[data-approval-reject-selected]");
  if (rejectSelected) rejectSelected.addEventListener("click", () => {
    if (!approvalQueuePopulationCertified()) return;
    armApprovalReject(Array.from(approvalQueueSelection));
  });
  const confirmReject = host.querySelector("[data-approval-confirm-reject]");
  if (confirmReject) confirmReject.addEventListener("click", () => {
    const reason = String((host.querySelector("[data-approval-reject-reason]") || {}).value || "").trim();
    if (!reason) { showToast("Enter a reason to reject."); return; }
    submitApprovalDecisions(approvalQueueRowsForKeys(Array.from(approvalQueueSelection)), "rejected", reason);
  });
  const cancelReject = host.querySelector("[data-approval-cancel-reject]");
  if (cancelReject) cancelReject.addEventListener("click", () => { approvalQueueRejectArmed = false; renderReviews(); });
  host.querySelectorAll("[data-approval-remove-row]").forEach((btn) => {
    btn.addEventListener("click", () => armApprovalRemove([btn.dataset.approvalRemoveRow]));
  });
  const removeSelected = host.querySelector("[data-approval-remove-selected]");
  if (removeSelected) removeSelected.addEventListener("click", () => {
    if (!approvalQueuePopulationCertified()) return;
    armApprovalRemove(Array.from(approvalQueueSelection));
  });
  const confirmRemove = host.querySelector("[data-approval-confirm-remove]");
  if (confirmRemove) confirmRemove.addEventListener("click", () => {
    const reason = String((host.querySelector("[data-approval-remove-note]") || {}).value || "").trim();
    if (!reason) { showToast("Enter a reason to remove the follow-up."); return; }
    if (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) { denyAction(PERMISSIONS.UPDATE_FOLLOW_UP, "remove follow-up"); return; }
    submitApprovalDecisions(approvalQueueRowsForKeys(Array.from(approvalQueueSelection)), "removed", reason);
  });
  const cancelRemove = host.querySelector("[data-approval-cancel-remove]");
  if (cancelRemove) cancelRemove.addEventListener("click", () => { approvalQueueRemoveArmed = false; renderReviews(); });
  const removeNote = host.querySelector("[data-approval-remove-note]");
  if (removeNote) removeNote.focus();
}

// Reject always flows through the sticky bar so one reason applies to the click.
function armApprovalReject(keys) {
  const list = (keys || []).filter(Boolean);
  if (!list.length) { showToast("Select one or more rows to reject."); return; }
  list.forEach((key) => approvalQueueSelection.add(key));
  approvalQueueRejectArmed = true;
  approvalQueueRemoveArmed = false;
  renderReviews();
}

// Remove works like Reject: the rows join the selection and one required reason
// covers the click. It is the "Change action" decision with no action, so one
// locked write clears the action and records the reviewer's decision, and the
// line leaves the queue instead of returning as a blank row awaiting approval.
function armApprovalRemove(keys) {
  const list = (keys || []).filter(Boolean);
  if (!list.length) { showToast("Select one or more rows to remove."); return; }
  list.forEach((key) => approvalQueueSelection.add(key));
  approvalQueueRemoveArmed = true;
  approvalQueueRejectArmed = false;
  renderReviews();
}

// One bulk click = one batchId; enqueue one idempotent workflow command per row
// (single-decision shape the backend accepts) carrying the row's expectedUpdatedAt
// version key. Clears the selection, then invalidates and refetches so stale-version
// rows re-appear as pending ("changed since - re-review").
//
// TRUTH, NOT OPTIMISM (design review 2026-08-02, defect 2). This used to toast
// "Approved 30 ✓" before a single response had come back, and the server can answer
// HTTP 200 having recorded NOTHING for a row (its overlay row vanished between the
// queue load and the click, or the segregation-of-duties guard refused it). The
// batch is tracked and the REAL count is announced by closeApprovalDecisionBatch
// once every command has been delivered.
function submitApprovalDecisions(rows, decision, reason = "") {
  let alreadyQueued = false;
  const list = (rows || []).filter((row) => {
    if (!row || row.canReview === false) return false;
    if (approvalDecisionIsPending(approvalQueueRowKey(row.orgId, row.claimId, row.serviceId))) {
      alreadyQueued = true;
      return false;
    }
    return true;
  });
  if (!list.length) { showToast(alreadyQueued ? "A decision is already queued. Check Workflow Sync for delivery." : "No rows to decide."); return; }
  if (!hasPermission(PERMISSIONS.APPROVE_REVIEW)) { denyAction(PERMISSIONS.APPROVE_REVIEW, "approve review"); return; }
  if (decision === "rejected" && !String(reason || "").trim()) { showToast("A reason is required to reject."); return; }
  if (decision === "removed" && !String(reason || "").trim()) { showToast("A reason is required to remove a follow-up."); return; }
  const removing = decision === "removed";
  const batchId = claimNoteCommandId("review-batch");
  const queued = [];
  list.forEach((row) => {
    const rowKey = approvalQueueRowKey(row.orgId, row.claimId, row.serviceId);
    const command = enqueueWorkflowCommand("followup-review", {
      claimId: row.claimId,
      serviceId: row.serviceId,
      orgId: row.orgId || "",
      decision: removing ? "changed" : decision,
      ...(removing ? { newAction: "None" } : {}),
      reason: decision === "rejected" || removing ? reason : "",
      followupUpdatedAtUtc: row.expectedUpdatedAt || "",
      batchId
    });
    if (command) queued.push(command);
    approvalQueueSelection.delete(rowKey);
  });
  approvalQueueRejectArmed = false;
  approvalQueueRemoveArmed = false;
  const reconciled = trackApprovalDecisionBatch(batchId, decision, queued);
  // With no workflow API configured there is no server to reconcile against, so
  // the offline queue keeps its own acknowledgement - honestly labelled.
  const pendingVerb = { approved: "Approving", removed: "Removing" }[decision] || "Sending back";
  const doneVerb = { approved: "Approved", removed: "Removed" }[decision] || "Rejected";
  if (reconciled) showToast(`${pendingVerb} ${list.length}…`);
  else showToast(`${doneVerb} ${list.length} ✓ (queued offline)`);
  // NO TIMER. The refresh happens when the decisions actually answer
  // (closeApprovalDecisionBatch), not 1200 ms later on the hope that they
  // have. Where there is no server to reconcile against there is nothing to
  // reconcile, but the SELECTION just changed under the reviewer's hands, so
  // the panel is drawn from local state immediately -- nothing is fetched, so
  // there is no half-read to flash.
  if (!reconciled) {
    if (state.view === "reviews" || (state.view === "assignments" && ["requests", "approvals"].includes(oversightState().tab))) renderReviews();
    return;
  }
}

// ---------------------------------------------------------------------------
// APPROVAL DECISION RECONCILIATION (design review 2026-08-02, defect 2).
//
// A followup-review 200 is an ENVELOPE, not an outcome. The backend decides each
// row independently and reports per row: `decisionRecorded` (it is in the
// append-only ledger) and `projectionUpdated` (it also flipped the line out of
// the queue). Reading response.ok - or the item's `status` alone - is exactly
// what let a reviewer be told 30 lines were approved when nothing was written.
// ---------------------------------------------------------------------------

// The items the server did NOT record. Backends that predate the honest per-item
// result do not send decisionRecorded at all; for those fall back to the envelope
// status rather than crying wolf on every row.
function followUpReviewUnrecordedItems(responseBody) {
  const results = Array.isArray(responseBody && responseBody.results) ? responseBody.results : [];
  return results.filter((item) => {
    if (!item || typeof item !== "object") return false;
    if (Object.prototype.hasOwnProperty.call(item, "decisionRecorded")) return item.decisionRecorded !== true;
    return String(item.status || "ok") !== "ok";
  });
}

// Server reason codes rendered in the reviewer's own words. An unmapped reason is
// dropped rather than leaked raw - the count alone is still honest.
const APPROVAL_NOT_RECORDED_REASONS = {
  self_review_forbidden: "you set that follow-up yourself, so someone else has to review it",
  follow_up_not_found: "that follow-up row is no longer there",
  claim_out_of_scope: "the claim is outside your access",
  claim_org_mismatch: "the claim belongs to another company",
  stale_version_override_refused: "the line changed after the queue loaded",
  override_no_change: "that line already has no follow-up action"
};

function approvalNotRecordedReasonText(items) {
  const reasons = [];
  (items || []).forEach((item) => {
    const text = APPROVAL_NOT_RECORDED_REASONS[String(item && item.reason || "")] || "";
    if (text && !reasons.includes(text)) reasons.push(text);
  });
  return reasons.length ? ` — ${reasons.join("; ")}` : "";
}

// One bulk click = one batch of N single-decision commands. Held here until every
// one has come back so the reviewer gets ONE truthful summary instead of N toasts.
const approvalDecisionBatches = new Map();
function approvalDecisionIsPending(key) {
  return (state.workflowCommandQueue || []).some((command) => command.command === "followup-review"
    && ["pending", "sending", "failed"].includes(command.status)
    && approvalQueueRowKey(command.body?.orgId, command.body?.claimId, command.body?.serviceId) === key);
}

// Returns false when there is nothing to reconcile against (no workflow API), so
// the caller can keep the offline-queue acknowledgement.
function trackApprovalDecisionBatch(batchId, decision, commands) {
  const list = (commands || []).filter(Boolean);
  if (!batchId || !list.length || !workflowApiBase()) return false;
  approvalDecisionBatches.set(batchId, {
    decision,
    total: list.length,
    pending: new Set(list.map((command) => command.commandId)),
    recorded: 0,
    notApplied: 0,
    unconfirmed: 0,
    unrecorded: [],
    undelivered: 0
  });
  // Safety net: a command that never comes back (offline, tab suspended) must not
  // leave the reviewer with no answer at all. Close on what we know.
  // The same ceiling the per-line queue waits, from the same variable, so one
  // reviewer deciding in two queues is not told two different stories about how
  // long a decision may take.
  window.setTimeout(() => closeApprovalDecisionBatch(batchId), supervisorDecisionTimeoutMs);
  return true;
}

function recordApprovalDecisionDelivery(command, status, result) {
  const batchId = String(command && command.body && command.body.batchId || "");
  const batch = approvalDecisionBatches.get(batchId);
  if (!batch || !batch.pending.has(command.commandId)) return;
  batch.pending.delete(command.commandId);
  const unrecorded = Array.isArray(result && result.unrecorded) ? result.unrecorded : [];
  if (status === "synced") {
    const items = result?.response?.results;
    if (Array.isArray(items) && items.length && items.every((item) => item.decisionRecorded === true && item.projectionUpdated === true)) batch.recorded += 1;
    else if (Array.isArray(items) && items.length && items.every((item) => item.decisionRecorded === true)) batch.notApplied += 1;
    else batch.unconfirmed += 1;
  }
  else if (unrecorded.length) batch.unrecorded = batch.unrecorded.concat(unrecorded);
  else batch.undelivered += 1;
  if (!batch.pending.size) closeApprovalDecisionBatch(batchId);
}

function closeApprovalDecisionBatch(batchId) {
  const batch = approvalDecisionBatches.get(batchId);
  if (!batch) return;
  approvalDecisionBatches.delete(batchId);
  const verb = { approved: "Approved", removed: "Removed" }[batch.decision] || "Sent back";
  // A command that has not come back is SLOW, not lost. Counting it as "not
  // recorded" told the reviewer their decision had failed when the only thing
  // that had happened was that the deadline arrived first.
  const notRecorded = batch.unrecorded.length + batch.undelivered;
  const stillPending = batch.pending.size;
  const missed = notRecorded + stillPending;
  if (batch.unconfirmed) {
    showToast(`${verb} ${batch.recorded} of ${batch.total} — ${batch.unconfirmed} result(s) could not be confirmed. Reload the current queue before retrying.`);
    refreshApprovalQueueAfterDecision();
    return;
  }
  if (batch.notApplied) {
    showToast(`${verb} ${batch.recorded} of ${batch.total} — ${batch.notApplied} decision(s) recorded but not applied; review the current queue again.${notRecorded ? ` ${notRecorded} not recorded.` : ""}${stillPending ? ` ${stillPending} still being delivered.` : ""}`);
    refreshApprovalQueueAfterDecision();
    return;
  }
  if (!missed) {
    showToast(`${verb} ${batch.recorded} ✓`);
    refreshApprovalQueueAfterDecision();
    return;
  }
  if (!notRecorded) {
    showToast(`${verb} ${batch.recorded} of ${batch.total} — ${stillPending} still being delivered. Workflow Sync will show the result.`);
    refreshApprovalQueueAfterDecision();
    return;
  }
  notifyApprovalDecisionNotRecorded(batch, verb, notRecorded, stillPending);
}

// The reviewer was already shown "Approving N...". When the server says a line never
// landed, correct the record OUT LOUD - silence here is precisely the failure the
// design review flagged (a reviewer told work was recorded when nothing was
// written). Refetch too, so the missed lines come back visibly pending.
function notifyApprovalDecisionNotRecorded(batch, verb, notRecorded, stillPending = 0) {
  showToast(`${verb} ${batch.recorded} of ${batch.total} — ${notRecorded} not recorded${approvalNotRecordedReasonText(batch.unrecorded)}. Those lines are still in the queue.${stillPending ? ` ${stillPending} still being delivered.` : ""}`);
  refreshApprovalQueueAfterDecision();
}

// Best-effort relative label for a set-at timestamp; falls back to the date.
function formatRelativeOrDate(iso) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return String(iso || "").slice(0, 10);
  const diff = Date.now() - ms;
  const day = 86400000;
  if (diff < day && diff >= 0) return "today";
  if (diff < 2 * day && diff >= 0) return "yesterday";
  return new Date(ms).toISOString().slice(0, 10);
}

// Planner-style Review kanban. Lanes mirror the workflow:
//   Submitted For Review - In Review - Approved - Bounced Back
function renderReviewPlannerBoardHtml(claims) {
  const lanes = [
    { key: "submitted", label: "Approval Required", description: "Submitted work needs your decision", tone: "blue",
      match: (c) => c.assignmentStatus === "Submitted For Review" },
    { key: "inReview", label: "In Review", description: "You've started looking but haven't decided yet", tone: "amber",
      match: (c) => c.assignmentStatus === "In Review" },
    { key: "approved", label: "Approved", description: "Signed off — ready to close", tone: "teal",
      match: (c) => c.assignmentStatus === "Approved" },
    { key: "bounced", label: "Changes Requested", description: "Returned to the worker with a reason", tone: "red",
      match: (c) => c.assignmentStatus === "Bounced Back" }
  ];
  lanes.forEach((l) => { l.claims = []; });
  claims.forEach((claim) => {
    const hit = lanes.find((l) => l.match(claim));
    if (hit) hit.claims.push(claim);
  });
  const inFlight = lanes.filter((l) => l.key === "submitted" || l.key === "inReview").reduce((sum, l) => sum + l.claims.length, 0);
  return `
    <header class="review-planner-head">
      <h2>Review Workflow</h2>
      <p>${formatNounCount(inFlight, "submitted package")} awaiting your decision · approve, request changes, or open for detail</p>
    </header>
    <div class="review-planner-columns">
      ${lanes.map((lane) => `
        <section class="review-planner-col review-planner-col--${escapeHtml(lane.tone)}">
          <header class="review-planner-col-head">
            <strong>${escapeHtml(lane.label)}</strong>
            <span class="review-planner-col-count">${lane.claims.length}</span>
          </header>
          <p class="review-planner-col-desc">${escapeHtml(lane.description)}</p>
          <div class="review-planner-col-cards">
            ${lane.claims.length === 0
              ? `<div class="review-planner-empty">No packages in this lane.</div>`
              : lane.claims.map((claim) => reviewTaskCardHtml(claim, lane.key)).join("")}
          </div>
        </section>
      `).join("")}
    </div>
  `;
}

function reviewTaskCardHtml(claim, laneKey) {
  const fin = (() => { try { return claimFinancialView(claim); } catch (_) { return {}; } })();
  const knownOutstanding = claim._workflowFinancialSummary ? workflowClaimOutstanding(claim) : fin?.sourceOutstanding;
  const owed = isUnknownMoney(knownOutstanding) ? money(knownOutstanding) : Number.isFinite(knownOutstanding) ? money(knownOutstanding) : "—";
  const submittedBy = claim.assignedTo || "Offshore";
  // Action buttons depend on the lane.
  const actions = (() => {
    if (laneKey === "submitted" || laneKey === "inReview") {
      return `
        <button type="button" class="review-card-btn review-card-btn--approve" data-review-approve="${escapeHtml(claim.claimId)}" data-review-org-id="${escapeHtml(claim.orgId || "")}">Approve</button>
        <button type="button" class="review-card-btn review-card-btn--bounce" data-review-bounce="${escapeHtml(claim.claimId)}" data-review-org-id="${escapeHtml(claim.orgId || "")}">Request Changes</button>
      `;
    }
    if (laneKey === "bounced") {
      return `<button type="button" class="review-card-btn review-card-btn--open" data-review-open="${escapeHtml(claim.claimId)}">Open detail →</button>`;
    }
    if (laneKey === "approved") {
      return `<button type="button" class="review-card-btn review-card-btn--undo" data-review-undo-approval="${escapeHtml(claim.claimId)}" data-review-org-id="${escapeHtml(claim.orgId || "")}">Undo approval</button>`;
    }
    return `<button type="button" class="review-card-btn review-card-btn--open" data-review-open="${escapeHtml(claim.claimId)}">Open detail →</button>`;
  })();
  return `
    <article class="review-task-card" data-review-claim="${escapeHtml(claim.claimId)}">
      <div class="review-task-card-head">
        <strong>${escapeHtml(claim.claimId)}</strong>
        <span class="review-task-owed">${escapeHtml(owed)}</span>
      </div>
      <div class="review-task-patient">${escapeHtml(claim.patient || "Patient unknown")}</div>
      <div class="review-task-meta">${escapeHtml(claim.primaryPayer || "Payer unknown")} · Submitted by ${escapeHtml(submittedBy)}</div>
      ${laneKey === "submitted" && String(claim.bounceReason || "").trim() ? `<div class="review-task-note review-task-note--prev-bounce">Previous bounce: ${escapeHtml(String(claim.bounceReason || "").trim())}${claim.bounceBy ? ` — ${escapeHtml(claim.bounceBy)}` : ""}</div>` : ""}
      ${laneKey === "bounced" && claimBounceReason(claim) ? `<div class="offshore-bounce-callout" role="note"><span class="offshore-bounce-callout-label">Changes requested</span><p>${escapeHtml(claimBounceReason(claim))}</p></div>` : ""}
      ${claim.reviewNote && (laneKey !== "bounced" || String(claim.bounceReason || "").trim()) ? `<div class="review-task-note"><strong>Offshore note:</strong> "${escapeHtml(claim.reviewNote)}"</div>` : ""}
      <footer class="review-task-card-foot">${actions}</footer>
      <div class="review-task-bounce-form hidden" data-bounce-form="${escapeHtml(claim.claimId)}">
        <textarea class="review-task-bounce-reason" placeholder="What does offshore need to fix? (e.g. 'Missing ERA detail — re-pull from payer portal.')" rows="2"></textarea>
        <div class="review-task-bounce-actions">
          <button type="button" class="review-task-bounce-cancel" data-bounce-cancel="${escapeHtml(claim.claimId)}">Cancel</button>
          <button type="button" class="review-task-bounce-confirm" data-bounce-confirm="${escapeHtml(claim.claimId)}" data-bounce-org-id="${escapeHtml(claim.orgId || "")}">Send back</button>
        </div>
      </div>
    </article>
  `;
}

function bindReviewPlannerCardActions(host) {
  if (!host) return;
  host.querySelectorAll("[data-review-approve]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      try { approveReview(btn.dataset.reviewApprove, "Approved via Review board.", { orgId: btn.dataset.reviewOrgId || "" }); } catch (_) {}
    });
  });
  host.querySelectorAll("[data-review-undo-approval]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      undoReviewApproval(btn.dataset.reviewUndoApproval, { orgId: btn.dataset.reviewOrgId || "" });
    });
  });
  // Bounce Back: reveal an inline reason form on the card. Confirm
  // submits + bounces. Cancel hides the form.
  host.querySelectorAll("[data-review-bounce]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const claimId = btn.dataset.reviewBounce;
      const form = host.querySelector(`[data-bounce-form="${escapeCss(claimId)}"]`);
      if (form) {
        form.classList.toggle("hidden");
        form.querySelector("textarea")?.focus();
      }
    });
  });
  host.querySelectorAll("[data-bounce-cancel]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const form = host.querySelector(`[data-bounce-form="${escapeCss(btn.dataset.bounceCancel)}"]`);
      form?.classList.add("hidden");
    });
  });
  host.querySelectorAll("[data-bounce-confirm]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const claimId = btn.dataset.bounceConfirm;
      const form = host.querySelector(`[data-bounce-form="${escapeCss(claimId)}"]`);
      const reason = (form?.querySelector("textarea")?.value || "").trim();
      if (!reason) {
        showToast("Please tell offshore what needs to be fixed.");
        form?.querySelector("textarea")?.focus();
        return;
      }
      bounceReviewBack(claimId, reason, { orgId: btn.dataset.bounceOrgId || "" });
    });
  });
  host.querySelectorAll("[data-review-open]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      try { openDrawer(btn.dataset.reviewOpen); } catch (_) {}
    });
  });
  // Card click → drawer (skip if click landed on a button)
  host.querySelectorAll("[data-review-claim]").forEach((card) => {
    card.addEventListener("click", (e) => {
      if (e.target.closest("button")) return;
      try { openDrawer(card.dataset.reviewClaim); } catch (_) {}
    });
  });
}

// Light-weight bounce-back helper. Sets assignmentStatus, keeps the reviewer's
// reason in the internal bounce field, and enqueues a workflow command. Bounce
// reasons are workflow evidence, not shared Claim Notes.
function bounceReviewBack(claimId, reason, options = {}) {
  if (!requireClaimPermission(claimId, PERMISSIONS.APPROVE_REVIEW, "review bounce-back")) return;
  const cleanReason = String(reason || "").trim();
  if (!cleanReason) return showToast("Please tell offshore what needs to be fixed.");
  const claim = getClaim(claimId);
  if (!claim) return;
  // Manager reason lives in bounceReason (OFFSHORE_HANDOFF_SPEC §1); the
  // offshore-owned reviewNote survives the bounce untouched.
  updateWorkflow(claimId, {
    assignmentStatus: "Bounced Back",
    bounceReason: cleanReason,
    bounceBy: activeInternalUser(),
    bounceAt: new Date().toISOString()
  });
  enqueueWorkflowCommand("review", { claimId, ...workflowTargetFieldsForClaim(claimId, options), assignmentStatus: "Bounced Back", note: cleanReason });
  audit(`${claimId}: review bounced back`, activeInternalUser());
  saveState();
  showToast("Bounced back to offshore.");
  renderReviews();
}

function reviewDayCloseCommandModel(claims = []) {
  const submittedClaims = claims.filter((claim) => claim.assignmentStatus === "Submitted For Review");
  const claimById = new Map(claims.map((claim) => [claim.claimId, claim]));
  const cockpit = reviewDecisionCockpitModel(submittedClaims);
  const outcomes = reviewOutcomeAccountabilityModel(claims);
  const submittedRows = cockpit.rows.map((row) => {
    const claim = claimById.get(row.claimId) || {};
    return {
      claimId: row.claimId,
      owner: row.reviewer || "Manager / Admin",
      submittedBy: row.submittedBy || "Assigned owner",
      decision: row.recommendation,
      exposure: moneyNumber(row.exposure),
      formulaDelta: moneyNumber(row.formulaDelta),
      nextAction: row.reason || row.nextAction || "Manager decision required.",
      dueRisk: isPastDue(claim),
      dueLabel: claim.followUpDate ? clientEvidenceDueLabel(claim.followUpDate) : "No follow-up date"
    };
  });
  const outcomeRows = outcomes.rows.map((row) => ({
    claimId: row.claimId,
    owner: row.ownerLabel,
    submittedBy: row.submittedBy,
    decision: row.status,
    exposure: moneyNumber(row.financialImpact),
    formulaDelta: moneyNumber(row.formulaDelta),
    nextAction: row.requiredNextStep,
    dueRisk: row.status === "Bounced Back",
    dueLabel: row.nextDate || "No follow-up date"
  }));
  const lanes = [
    reviewDayCloseLane("Approve Ready", "Manager / Admin", "Approve submitted work", submittedRows.filter((row) => row.decision === "Approve"), "green", "Approve only when evidence, next action, follow-up date, and formula proof are present."),
    reviewDayCloseLane("Bounce Back Today", "Manager / Admin → Assigned owner", "Return work for correction", submittedRows.filter((row) => row.decision === "Bounce Back"), "amber", "Return packages with exact missing evidence and owner fix language."),
    reviewDayCloseLane("Escalation / Hold", "Manager / Admin", "Manager escalation / hold", submittedRows.filter((row) => row.decision === "Escalate"), "red", "Hold status changes until guard, formula, or high-exposure risk is resolved."),
    reviewDayCloseLane("Correction Follow-Up", "Assigned owner", "Resubmit corrected work", outcomeRows.filter((row) => row.decision === "Bounced Back"), "amber", "Track bounced packages until corrected and resubmitted."),
    reviewDayCloseLane("Approved Monitor", "Manager / Admin", "Continue approved follow-up", outcomeRows.filter((row) => row.decision === "Approved"), "green", "Keep approved next actions active and preserve manager proof.")
  ];
  const totals = lanes.reduce((sum, lane) => {
    sum.claims += lane.count;
    sum.exposure += lane.exposure;
    sum.delta += lane.delta;
    sum.dueRisk += lane.dueRisk;
    return sum;
  }, { claims: 0, exposure: 0, delta: 0, dueRisk: 0 });
  return {
    lanes,
    totals: {
      ...totals,
      waiting: submittedRows.length,
      approve: submittedRows.filter((row) => row.decision === "Approve").length,
      bounce: submittedRows.filter((row) => row.decision === "Bounce Back").length,
      escalate: submittedRows.filter((row) => row.decision === "Escalate").length,
      correction: outcomeRows.filter((row) => row.decision === "Bounced Back").length,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta)
    }
  };
}

function reviewDayCloseLane(label, owner, command, rows, tone, nextAction) {
  const exposure = moneyNumber(rows.reduce((sum, row) => sum + row.exposure, 0));
  const delta = moneyNumber(rows.reduce((sum, row) => sum + row.formulaDelta, 0));
  const dueRisk = rows.filter((row) => row.dueRisk).length;
  const owners = new Set(rows.map((row) => row.submittedBy || row.owner).filter(Boolean));
  return {
    label,
    owner,
    command,
    tone,
    rows,
    count: rows.length,
    exposure,
    delta,
    dueRisk,
    owners: owners.size,
    examples: rows.slice(0, 3).map((row) => row.claimId).join(", ") || "No claims in this lane",
    nextAction
  };
}

function renderReviewDayCloseCommand(claims = []) {
  const model = reviewDayCloseCommandModel(claims);
  return `
    <section class="review-day-close-command" aria-label="Review day close command">
      <div class="review-day-close-head">
        <div>
          <span>Review Day-Close Command</span>
          <strong>${model.totals.waiting} waiting review package${model.totals.waiting === 1 ? "" : "s"} · ${model.totals.correction} correction follow-up${model.totals.correction === 1 ? "" : "s"}</strong>
          <p>Manager closeout surface for who owns each review lane, which command path is next, how much cash is under decision, and whether the formula still ties out.</p>
        </div>
        ${pill(`${model.totals.dueRisk} due risk`, model.totals.dueRisk ? "amber" : "green")}
      </div>
      <div class="review-day-close-summary">
        <div><span>Waiting Review</span><strong>${model.totals.waiting}</strong><small>Submitted packages</small></div>
        <div><span>Approve Ready</span><strong>${model.totals.approve}</strong><small>Evidence and math ready</small></div>
        <div><span>Bounce / Escalate</span><strong>${model.totals.bounce + model.totals.escalate}</strong><small>Manager text required</small></div>
        <div><span>Correction Follow-Up</span><strong>${model.totals.correction}</strong><small>Returned owner lanes</small></div>
        <div><span>Command Exposure</span><strong>${money(model.totals.exposure)}</strong><small>Submitted plus outcome lanes</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - Paid - AR</small></div>
      </div>
      <div class="review-day-close-list">
        <div class="review-day-close-row review-day-close-header">
          <span>Lane</span>
          <span>Owner / Command</span>
          <span>Claims / Due Risk</span>
          <span>Exposure / Delta</span>
          <span>Next Action</span>
        </div>
        ${model.lanes.map(renderReviewDayCloseRow).join("")}
      </div>
    </section>
  `;
}

function renderReviewDayCloseRow(lane) {
  return `
    <div class="review-day-close-row">
      <span data-label="Lane"><strong>${escapeHtml(lane.label)}</strong><small>${pill(`${lane.count} claim${lane.count === 1 ? "" : "s"}`, lane.tone)} ${escapeHtml(lane.examples)}</small></span>
      <span data-label="Owner / Command"><strong>${escapeHtml(lane.owner)}</strong><small>${escapeHtml(lane.command)}</small></span>
      <span data-label="Claims / Due Risk"><strong>${formatNounCount(lane.count, "claim")} · ${formatNounCount(lane.dueRisk, "due risk")}</strong><small>${formatNounCount(lane.owners, "owner")} in lane</small></span>
      <span data-label="Exposure / Delta"><strong>${money(lane.exposure)}</strong><small>Delta ${money(lane.delta)}</small></span>
      <span data-label="Next Action">${escapeHtml(lane.nextAction)}</span>
    </div>
  `;
}

function reviewSlaBoardModel(reviews = []) {
  const decisions = new Map(reviewDecisionCockpitModel(reviews).rows.map((row) => [row.claimId, row]));
  const rows = reviews.map((claim) => {
    const decision = decisions.get(claim.claimId) || {};
    const financial = claimFinancialView(claim);
    const exposure = moneyNumber(reviewFinancialExposure(reviewQualityProfile(claim)));
    const formulaDelta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    const due = reviewDecisionDueStatus(claim);
    return {
      claim,
      claimId: claim.claimId,
      client: claim.client || "Unknown client",
      submittedBy: claim.assignedTo || "Assigned owner",
      reviewOwner: "Manager / Admin",
      decisionBy: due.label,
      dueKey: due.key,
      dueDays: due.days,
      status: due.status,
      recommendation: decision.recommendation || "Review",
      reason: decision.reason || reviewEscalationReason(reviewQualityProfile(claim), reviewDecisionDossier(claim)),
      exposure,
      formulaDelta,
      requiredAction: reviewSlaRequiredAction(claim, due, decision)
    };
  }).sort((a, b) => reviewSlaRank(b) - reviewSlaRank(a)
    || b.exposure - a.exposure
    || Math.abs(b.formulaDelta) - Math.abs(a.formulaDelta)
    || a.claimId.localeCompare(b.claimId));
  const totals = rows.reduce((sum, row) => {
    sum.rows += 1;
    sum.exposure += row.exposure;
    sum.delta += row.formulaDelta;
    if (row.dueKey === "overdue") sum.overdue += 1;
    if (row.dueKey === "dueToday") sum.dueToday += 1;
    if (row.dueKey === "dueSoon") sum.dueSoon += 1;
    if (row.dueKey === "missing") sum.missingSla += 1;
    if (row.recommendation === "Approve") sum.approve += 1;
    if (row.recommendation === "Bounce Back") sum.bounce += 1;
    if (row.recommendation === "Escalate") sum.escalate += 1;
    return sum;
  }, { rows: 0, overdue: 0, dueToday: 0, dueSoon: 0, missingSla: 0, approve: 0, bounce: 0, escalate: 0, exposure: 0, delta: 0 });
  return {
    rows: rows.slice(0, 8),
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta)
    }
  };
}

function reviewDecisionDueStatus(claim = {}) {
  const dateValue = claim.followUpDate || claim.assignmentDue || "";
  if (!dateValue) return { key: "missing", label: "Missing Review SLA", status: "Missing SLA", days: null };
  const due = localDateFromDateOnly(dateValue);
  if (!due) return { key: "missing", label: "Missing Review SLA", status: "Missing SLA", days: null };
  const days = Math.ceil((due - todayStart()) / 86400000);
  if (days < 0) return { key: "overdue", label: formatDate(dateValue), status: "Overdue", days };
  if (days === 0) return { key: "dueToday", label: formatDate(dateValue), status: "Due Today", days };
  if (days <= 2) return { key: "dueSoon", label: formatDate(dateValue), status: "Due Soon", days };
  return { key: "onTrack", label: formatDate(dateValue), status: "On Track", days };
}

function reviewSlaRank(row = {}) {
  const dueRank = { overdue: 100, missing: 85, dueToday: 75, dueSoon: 55, onTrack: 25 };
  const decisionRank = { Escalate: 30, "Bounce Back": 20, Approve: 8 };
  return (dueRank[row.dueKey] || 0) + (decisionRank[row.recommendation] || 0) + Math.min(row.exposure || 0, 1000) / 100;
}

function reviewSlaRequiredAction(claim = {}, due = {}, decision = {}) {
  if (due.key === "missing") return "Set a manager decision-by date before this review can be safely tracked.";
  if (due.key === "overdue") return `Decide immediately: ${decision.recommendation || "approve, bounce, or hold"} with manager note.`;
  if (decision.recommendation === "Approve") return "Approve with accepted evidence, next action, follow-up date, and formula proof.";
  if (decision.recommendation === "Bounce Back") return "Bounce back to submitted owner with exact missing evidence and resubmission requirement.";
  if (decision.recommendation === "Escalate") return "Hold closure and resolve guard, formula, or high-exposure risk.";
  return "Manager decides approve, bounce, or hold before SLA expires.";
}

function renderReviewSlaBoard(reviews = []) {
  const model = reviewSlaBoardModel(reviews);
  return `
    <section class="review-sla-board" aria-label="Manager review SLA board">
      <div class="review-sla-head">
        <div>
          <span>Manager Review SLA Board</span>
          <strong>${formatNounCount(model.totals.rows, "submitted package")} with decision timing, owner, and exposure</strong>
          <p>Daily manager queue that says who submitted each package, who must decide it, when the decision is due, what action is recommended, and whether Expected - Paid - AR still ties.</p>
        </div>
        ${pill(`${model.totals.overdue + model.totals.missingSla} SLA risk`, model.totals.overdue + model.totals.missingSla ? "amber" : "green")}
      </div>
      <div class="review-sla-summary">
        <div><span>Overdue</span><strong>${model.totals.overdue}</strong><small>Decision date passed</small></div>
        <div><span>Due Today</span><strong>${model.totals.dueToday}</strong><small>Manager must decide today</small></div>
        <div><span>Missing SLA</span><strong>${model.totals.missingSla}</strong><small>No decision-by date</small></div>
        <div><span>Approve / Bounce / Hold</span><strong>${model.totals.approve}/${model.totals.bounce}/${model.totals.escalate}</strong><small>Recommended dispositions</small></div>
        <div><span>Exposure</span><strong>${money(model.totals.exposure)}</strong><small>Under manager SLA</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - Paid - AR</small></div>
      </div>
      <div class="review-sla-list">
        <div class="review-sla-row review-sla-header">
          <span>Claim / Client</span>
          <span>Submitted By</span>
          <span>Review Owner / SLA</span>
          <span>Decision</span>
          <span>Exposure / Delta</span>
          <span>Required Manager Action</span>
        </div>
        ${model.rows.length ? model.rows.map(renderReviewSlaRow).join("") : '<div class="review-sla-empty">No submitted packages need manager SLA tracking.</div>'}
      </div>
    </section>
  `;
}

function renderReviewSlaRow(row) {
  const dueTone = row.dueKey === "overdue" || row.dueKey === "missing" ? "red" : row.dueKey === "dueToday" || row.dueKey === "dueSoon" ? "amber" : "green";
  const decisionTone = row.recommendation === "Approve" ? "green" : row.recommendation === "Bounce Back" ? "amber" : row.recommendation === "Escalate" ? "red" : "blue";
  return `
    <button type="button" class="review-sla-row" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Client"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.client)}</small></span>
      <span data-label="Submitted By"><strong>${escapeHtml(row.submittedBy)}</strong><small>Submitted package owner</small></span>
      <span data-label="Review Owner / SLA"><strong>${escapeHtml(row.reviewOwner)}</strong><small>${pill(row.status, dueTone)} Decision by ${escapeHtml(row.decisionBy)}</small></span>
      <span data-label="Decision">${pill(row.recommendation, decisionTone)}<small>${escapeHtml(row.reason)}</small></span>
      <span data-label="Exposure / Delta"><strong>${money(row.exposure)}</strong><small>Delta ${money(row.formulaDelta)}</small></span>
      <span data-label="Required Manager Action">${escapeHtml(row.requiredAction)}</span>
    </button>
  `;
}

function reviewAcceptanceCertificateModel(reviews = []) {
  const cockpitRows = new Map(reviewDecisionCockpitModel(reviews).rows.map((row) => [row.claimId, row]));
  const rows = reviews.map((claim) => {
    const profile = reviewQualityProfile(claim);
    const dossier = reviewDecisionDossier(claim);
    const financial = claimFinancialView(claim);
    const formulaDelta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    const missing = reviewMissingDecisionRequirements(profile, dossier);
    const cockpit = cockpitRows.get(claim.claimId) || {};
    const status = reviewAcceptanceStatus(profile, formulaDelta, missing, cockpit.recommendation);
    const requiredDecision = reviewAcceptanceRequiredDecision(status, cockpit.recommendation);
    const ownerFix = reviewAcceptanceOwnerFix(status, missing, dossier, profile, formulaDelta);
    return {
      claim,
      claimId: claim.claimId,
      client: claim.client || "Unknown client",
      submittedBy: dossier.submittedBy,
      decisionOwner: dossier.reviewedBy || "Manager / Admin",
      status,
      recommendation: cockpit.recommendation || requiredDecision,
      requiredDecision,
      ownerFix,
      missing,
      evidenceStatus: dossier.evidenceStatus,
      proof: reviewAcceptanceProof(profile, dossier, formulaDelta),
      expected: financial.expectedReimbursement,
      paid: financial.totalInsurancePaid,
      outstanding: financial.sourceOutstanding,
      formulaDelta,
      exposure: reviewFinancialExposure(profile),
      nextAction: dossier.nextAction,
      nextDate: dossier.nextDate,
      tone: reviewAcceptanceTone(status)
    };
  }).sort((a, b) => reviewAcceptanceRank(b) - reviewAcceptanceRank(a)
    || b.exposure - a.exposure
    || Math.abs(b.formulaDelta) - Math.abs(a.formulaDelta)
    || a.claimId.localeCompare(b.claimId));
  const totals = rows.reduce((sum, row) => {
    sum.rows += 1;
    sum.exposure += row.exposure;
    sum.delta += row.formulaDelta;
    if (row.status === "Acceptable") sum.acceptable += 1;
    if (row.status === "Bounce Required") sum.bounce += 1;
    if (row.status === "Hold / Escalate") sum.hold += 1;
    if (row.missing.length) sum.missingRequirements += 1;
    return sum;
  }, { rows: 0, acceptable: 0, bounce: 0, hold: 0, missingRequirements: 0, exposure: 0, delta: 0 });
  return {
    rows: rows.slice(0, 8),
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta)
    }
  };
}

function reviewAcceptanceStatus(profile = {}, formulaDelta = 0, missing = [], recommendation = "") {
  if (profile.guardCount > 0 || formulaDelta !== 0 || recommendation === "Escalate") return "Hold / Escalate";
  if (missing.length || recommendation === "Bounce Back") return "Bounce Required";
  return "Acceptable";
}

function reviewAcceptanceRequiredDecision(status, recommendation = "") {
  if (status === "Acceptable") return "Approve with manager note";
  if (status === "Bounce Required") return "Bounce back to assigned owner";
  if (recommendation === "Escalate") return "Escalate before closeout";
  return "Hold for manager review";
}

function reviewAcceptanceOwnerFix(status, missing = [], dossier = {}, profile = {}, formulaDelta = 0) {
  if (status === "Acceptable") return `Accept proof, preserve follow-up date, and keep ${dossier.nextAction} active.`;
  if (missing.length) return `Assigned owner must fix: ${missing.slice(0, 4).join(", ")}.`;
  if (profile.guardCount > 0) return `Resolve guard before approval: ${dossier.guardSummary}.`;
  if (formulaDelta !== 0) return `Reconcile formula delta ${money(formulaDelta)} before approval.`;
  return "Manager must document hold reason and required next evidence.";
}

function reviewAcceptanceProof(profile = {}, dossier = {}, formulaDelta = 0) {
  const parts = [
    profile.hasEvidence ? "proof present" : "proof missing",
    profile.hasNextAction ? "next action present" : "next action missing",
    profile.hasFollowUpDate ? "date present" : "date missing",
    profile.guardCount ? dossier.guardSummary : "guard clear",
    `delta ${money(formulaDelta)}`
  ];
  return parts.join(" / ");
}

function reviewAcceptanceTone(status) {
  if (status === "Acceptable") return "green";
  if (status === "Bounce Required") return "amber";
  return "red";
}

function reviewAcceptanceRank(row = {}) {
  const rank = { "Hold / Escalate": 90, "Bounce Required": 70, Acceptable: 40 };
  return (rank[row.status] || 20) + Math.min(row.exposure || 0, 1000) / 100;
}

function renderReviewAcceptanceCertificate(reviews = []) {
  const model = reviewAcceptanceCertificateModel(reviews);
  return `
    <section class="review-acceptance-certificate" aria-label="Manager review acceptance certificate">
      <div class="review-acceptance-head">
        <div>
          <span>Review Acceptance Certificate</span>
          <strong>${model.totals.acceptable}/${formatNounCount(model.totals.rows, "submitted package")} acceptable for manager approval</strong>
          <p>Single-row decision certificate for each submitted package: submitted owner, decision owner, approve/bounce/hold state, required proof, formula math, and exact owner fix.</p>
        </div>
        ${pill(`${model.totals.bounce + model.totals.hold} blocked`, model.totals.bounce + model.totals.hold ? "amber" : "green")}
      </div>
      <div class="review-acceptance-summary">
        <div><span>Acceptable</span><strong>${model.totals.acceptable}</strong><small>Can approve with manager note</small></div>
        <div><span>Bounce Required</span><strong>${model.totals.bounce}</strong><small>Owner-fixable requirements</small></div>
        <div><span>Hold / Escalate</span><strong>${model.totals.hold}</strong><small>Guard, formula, or high-risk issue</small></div>
        <div><span>Missing Requirements</span><strong>${model.totals.missingRequirements}</strong><small>Proof/action/date/guard gaps</small></div>
        <div><span>Exposure</span><strong>${money(model.totals.exposure)}</strong><small>Open AR / underpayment</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - Paid - AR</small></div>
      </div>
      <div class="review-acceptance-list">
        <div class="review-acceptance-row review-acceptance-header">
          <span>Claim / Client</span>
          <span>Submitted / Decision Owner</span>
          <span>Acceptance State</span>
          <span>Formula Proof</span>
          <span>Required Manager Decision</span>
          <span>Owner Fix</span>
        </div>
        ${model.rows.length ? model.rows.map(renderReviewAcceptanceRow).join("") : '<div class="review-acceptance-empty">No submitted packages need acceptance certification.</div>'}
      </div>
    </section>
  `;
}

function renderReviewAcceptanceRow(row) {
  return `
    <button type="button" class="review-acceptance-row review-acceptance-row--${row.tone}" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Client"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.client)} · ${money(row.exposure)} exposure</small></span>
      <span data-label="Submitted / Decision Owner"><strong>${escapeHtml(row.submittedBy)}</strong><small>Decision owner: ${escapeHtml(row.decisionOwner)}</small></span>
      <span data-label="Acceptance State">${pill(row.status, row.tone)}<small>${escapeHtml(row.proof)}</small></span>
      <span data-label="Formula Proof"><strong>${money(row.expected)} - ${money(row.paid)} - ${money(row.outstanding)}</strong><small>Delta ${money(row.formulaDelta)}</small></span>
      <span data-label="Required Manager Decision"><strong>${escapeHtml(row.requiredDecision)}</strong><small>${escapeHtml(row.nextAction)} · ${escapeHtml(row.nextDate)}</small></span>
      <span data-label="Owner Fix">${escapeHtml(row.ownerFix)}</span>
    </button>
  `;
}

function reviewCloseoutLedgerModel(reviews = [], claims = getClaims()) {
  const acceptanceRows = reviewAcceptanceCertificateModel(reviews).rows.map((row) => {
    const nextOwner = row.status === "Bounce Required" ? row.submittedBy : "Manager / Admin";
    const noteRequirement = row.status === "Acceptable"
      ? `Approve note must accept proof and keep ${row.nextAction} active.`
      : row.status === "Bounce Required"
        ? row.ownerFix
        : `Hold note must resolve ${row.ownerFix}`;
    return {
      claimId: row.claimId,
      stage: "Submitted",
      assignedToward: row.submittedBy,
      reviewOwner: row.decisionOwner,
      decisionRoute: row.requiredDecision,
      nextOwner,
      evidence: row.proof,
      noteRequirement,
      expected: row.expected,
      paid: row.paid,
      outstanding: row.outstanding,
      formulaDelta: row.formulaDelta,
      exposure: row.exposure,
      tone: row.tone,
      rank: reviewCloseoutRank(row.status, row.exposure)
    };
  });
  // Wave 3c: resubmitted claims already sit on this ledger as acceptance rows
  // (stage "Submitted") — mapping their outcome rows too would double-count
  // the claim and its exposure.
  const outcomeRows = reviewOutcomeAccountabilityModel(claims).rows.filter((row) => row.status !== "Submitted For Review").map((row) => ({
    claimId: row.claimId,
    stage: row.status,
    assignedToward: row.submittedBy,
    reviewOwner: "Manager / Admin",
    decisionRoute: row.commandPath,
    nextOwner: row.ownerLabel,
    evidence: row.noteQuality,
    noteRequirement: row.requiredNextStep,
    expected: row.expected,
    paid: row.paid,
    outstanding: row.outstanding,
    formulaDelta: row.formulaDelta,
    exposure: row.financialImpact,
    tone: row.tone,
    rank: reviewCloseoutRank(row.status, row.financialImpact)
  }));
  const rows = [...acceptanceRows, ...outcomeRows]
    .sort((a, b) => b.rank - a.rank || b.exposure - a.exposure || a.claimId.localeCompare(b.claimId))
    .slice(0, 12);
  const totals = rows.reduce((sum, row) => {
    sum.rows += 1;
    sum.exposure += row.exposure;
    sum.delta += row.formulaDelta;
    if (row.stage === "Submitted") sum.submitted += 1;
    if (row.stage === "Approved") sum.approved += 1;
    if (row.stage === "Bounced Back") sum.bounced += 1;
    if (/hold|escalate/i.test(row.decisionRoute)) sum.hold += 1;
    if (row.nextOwner && row.nextOwner !== row.reviewOwner) sum.ownerTransfers += 1;
    return sum;
  }, { rows: 0, submitted: 0, approved: 0, bounced: 0, hold: 0, ownerTransfers: 0, exposure: 0, delta: 0 });
  return {
    rows,
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta)
    }
  };
}

function reviewCloseoutRank(stage = "", exposure = 0) {
  const ranks = {
    "Hold / Escalate": 95,
    "Bounce Required": 85,
    "Bounced Back": 75,
    Submitted: 65,
    Approved: 45,
    Acceptable: 40
  };
  return (ranks[stage] || 55) + Math.min(Number(exposure) || 0, 1000) / 100;
}

function renderReviewCloseoutLedger(reviews = [], claims = getClaims()) {
  const model = reviewCloseoutLedgerModel(reviews, claims);
  return `
    <section class="review-closeout-ledger" aria-label="Manager review closeout ledger">
      <div class="review-closeout-head">
        <div>
          <span>Manager Review Closeout Ledger</span>
          <strong>${model.totals.rows} review row${model.totals.rows === 1 ? "" : "s"} with owner transfer, decision route, evidence, and formula proof</strong>
          <p>Spreadsheet-style control sheet for managers: every row shows who the work is assigned toward, who owns review, where the decision routes next, and whether Expected - Paid - AR still ties.</p>
        </div>
        ${pill(`${model.totals.ownerTransfers} owner transfer${model.totals.ownerTransfers === 1 ? "" : "s"}`, model.totals.ownerTransfers ? "amber" : "green")}
      </div>
      <div class="review-closeout-summary">
        <div><span>Submitted</span><strong>${model.totals.submitted}</strong><small>Awaiting manager decision</small></div>
        <div><span>Approved</span><strong>${model.totals.approved}</strong><small>Manager outcome lane</small></div>
        <div><span>Bounced</span><strong>${model.totals.bounced}</strong><small>Returned to assigned owner</small></div>
        <div><span>Hold / Escalate</span><strong>${model.totals.hold}</strong><small>Blocked before closeout</small></div>
        <div><span>Exposure</span><strong>${money(model.totals.exposure)}</strong><small>Open AR / underpay</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - Paid - AR</small></div>
      </div>
      <div class="review-closeout-table" role="table" aria-label="Review closeout spreadsheet ledger">
        <div class="review-closeout-row review-closeout-header" role="row">
          <span>Claim / Stage</span>
          <span>Assigned Toward</span>
          <span>Review Owner</span>
          <span>Decision Route</span>
          <span>Next Owner</span>
          <span>Evidence / Note</span>
          <span>Expected - Paid - AR</span>
        </div>
        ${model.rows.length ? model.rows.map(renderReviewCloseoutLedgerRow).join("") : '<div class="review-closeout-empty">No submitted or recently decided review rows in the active data.</div>'}
      </div>
    </section>
  `;
}

function renderReviewCloseoutLedgerRow(row) {
  return `
    <button type="button" class="review-closeout-row review-closeout-row--${escapeHtml(row.tone)}" data-open="${escapeHtml(row.claimId)}" role="row">
      <span data-label="Claim / Stage"><strong>${escapeHtml(row.claimId)}</strong><small>${pill(row.stage, row.tone)}</small></span>
      <span data-label="Assigned Toward"><strong>${escapeHtml(row.assignedToward)}</strong><small>Submitted owner</small></span>
      <span data-label="Review Owner"><strong>${escapeHtml(row.reviewOwner)}</strong><small>Manager decision owner</small></span>
      <span data-label="Decision Route"><strong>${escapeHtml(row.decisionRoute)}</strong></span>
      <span data-label="Next Owner"><strong>${escapeHtml(row.nextOwner)}</strong></span>
      <span data-label="Evidence / Note"><strong>${escapeHtml(row.evidence)}</strong><small>${escapeHtml(row.noteRequirement)}</small></span>
      <span data-label="Expected - Paid - AR"><strong>${money(row.expected)} - ${money(row.paid)} - ${money(row.outstanding)}</strong><small>Delta ${money(row.formulaDelta)} · Exposure ${money(row.exposure)}</small></span>
    </button>
  `;
}

function assignmentRouteModel(claim, options = {}) {
  const status = claim.assignmentStatus || "Unassigned";
  const assignedTo = claim.assignedTo || "Unassigned";
  const assignedBy = claim.assignedBy || "None";
  const dueLabel = claim.assignmentDue ? formatDate(claim.assignmentDue) : "No assignment due date";
  const nextAction = claim.action || "No next action";
  const nextDate = claim.followUpDate ? formatDate(claim.followUpDate) : "No next follow-up date";
  const isReview = options.mode === "review" || status === "Submitted For Review";
  const routeLane = isReview ? "Manager Review Lane" : status === "Bounced Back" ? "Correction Lane" : "Active Work Lane";
  const decisionOwner = isReview ? "Manager / Admin" : assignedTo;
  const reviewGate = isReview ? "Approve or bounce back with evidence" : "Submit for manager review";
  return {
    assignedTo,
    assignedBy,
    dueLabel,
    status,
    routeLane,
    decisionOwner,
    nextAction,
    nextDate,
    reviewGate
  };
}

function renderAssignmentRouteChain(claim, options = {}) {
  const route = assignmentRouteModel(claim, options);
  return `
    <div class="assignment-route-chain" aria-label="Assignment route for ${escapeHtml(claim.claimId)}">
      <div>
        <span>Assigned Toward</span>
        <strong>${escapeHtml(route.assignedTo)}</strong>
        <small>Assigned by ${escapeHtml(route.assignedBy)}</small>
      </div>
      <div>
        <span>Due / SLA</span>
        <strong>${escapeHtml(route.dueLabel)}</strong>
        <small>${isPastDue(claim) ? "Past due follow-up" : "Within current queue"}</small>
      </div>
      <div>
        <span>Review Lane</span>
        <strong>${escapeHtml(route.routeLane)}</strong>
        <small>${escapeHtml(route.reviewGate)}</small>
      </div>
      <div>
        <span>Next Action</span>
        <strong>${escapeHtml(route.nextAction)}</strong>
        <small>${escapeHtml(route.nextDate)} · Decision owner: ${escapeHtml(route.decisionOwner)}</small>
      </div>
    </div>
  `;
}

function assignmentWorkPacket(claim) {
  const financial = claimFinancialView(claim);
  const guards = evaluateClaimCalculationGuards(claim);
  const delta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
  const proofNeed = assignmentProofRequirement(claim, guards);
  return {
    priority: workPriorityScore(claim),
    reason: workReason(claim),
    expected: financial.expectedReimbursement,
    paid: financial.totalInsurancePaid,
    outstanding: financial.sourceOutstanding,
    delta,
    guardSummary: guards.length ? calculationGuardSummary(guards) : "Clear",
    guardCount: guards.length,
    proofNeed
  };
}

function assignmentProofRequirement(claim, guards = []) {
  const reason = workReason(claim).toLowerCase();
  if (guards.some((guard) => guard.code === "EXPECTED_REIMBURSEMENT_INCOMPLETE")) return "Resolve rate/contract evidence before relying on AR.";
  if (guards.some((guard) => guard.code === "PAYER_SEQUENCE_REVIEW")) return "Document primary/secondary payer sequence evidence.";
  if (guards.some((guard) => guard.code === "ADJUSTMENT_DETAIL_MISSING")) return "Attach ERA/EOB or payer portal adjustment detail.";
  if (reason.includes("denial") || reason.includes("appeal")) return "Capture denial reason and appeal/correction proof.";
  if (reason.includes("waiting primary") || reason.includes("payer")) return "Capture payer portal status, call reference, or EOB proof.";
  if (reason.includes("client")) return "Document exact client authorization/COB/document request.";
  return "Document payer response, next action, and follow-up date.";
}

function renderAssignmentWorkPacket(claim) {
  const packet = assignmentWorkPacket(claim);
  return `
    <section class="assignment-work-packet" aria-label="Assignment work packet for ${escapeHtml(claim.claimId)}">
      <div class="assignment-work-packet-head">
        <div>
          <span>Work Packet</span>
          <strong>${escapeHtml(packet.reason)}</strong>
        </div>
        ${pill(`${packet.priority} ${priorityBand(packet.priority)}`, packet.priority >= 80 ? "red" : "amber")}
      </div>
      <div class="assignment-work-packet-grid">
        <div><span>Expected</span><strong>${money(packet.expected)}</strong><small>Contract / fee schedule math</small></div>
        <div><span>Paid</span><strong>${money(packet.paid)}</strong><small>Insurance paid</small></div>
        <div><span>Open AR</span><strong>${money(packet.outstanding)}</strong><small>Current balance</small></div>
        <div><span>Formula Delta</span><strong>${money(packet.delta)}</strong><small>Expected - Paid - AR</small></div>
        <div><span>Calc Guard</span><strong>${escapeHtml(packet.guardSummary)}</strong><small>${packet.guardCount ? "Resolve before approval" : "No math guard"}</small></div>
        <div><span>Required Proof</span><strong>${escapeHtml(packet.proofNeed)}</strong><small>Needed in completion note</small></div>
      </div>
    </section>
  `;
}

function renderAssignmentOwnershipBoard(queue, assignable) {
  const ownerRows = assignmentOwnershipRows(queue);
  const nextOwners = users.filter((user) => user.role === "OffshoreBiller").map((user) => {
    const row = ownerRows.find((candidate) => candidate.owner === user.name);
    return row || { owner: user.name, claims: 0, owed: 0, pastDue: 0, submitted: 0, bounced: 0 };
  }).sort((a, b) => a.claims - b.claims || a.owed - b.owed);
  const unassignedOwed = assignable.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
  return `
    <div class="ownership-grid">
      <section class="ownership-panel">
        <div class="ops-section-heading compact">
          <strong>Assigned Toward</strong>
          <span>Current owner load by offshore team.</span>
        </div>
        <div class="ownership-table">
          ${ownerRows.length ? ownerRows.map(renderAssignmentOwnerRow).join("") : empty("No assigned owner load yet.")}
        </div>
      </section>
      <section class="ownership-panel">
        <div class="ops-section-heading compact">
          <strong>Next Routing</strong>
          <span>Lowest-load teams for the next batch.</span>
        </div>
        <div class="routing-list">
          ${nextOwners.map((row, index) => `
            <div class="routing-row ${index === 0 ? "recommended" : ""}">
              <span>${index === 0 ? "Recommended" : "Available"}</span>
              <strong>${escapeHtml(row.owner)}</strong>
              <small>${row.claims} active · ${money(row.owed)} AR</small>
            </div>
          `).join("")}
          <div class="routing-row">
            <span>Unassigned AR</span>
            <strong>${formatNounCount(assignable.length, "claim")}</strong>
            <small>${money(unassignedOwed)} ready to route</small>
          </div>
        </div>
      </section>
    </div>
  `;
}

// Per-owner active-claim ceiling for utilization display. Default; aligns with the
// claims>=8 "At Capacity" threshold in assignmentCapacityLabel. Tune as offshore
// capacity changes (future: make per-owner configurable).
const ASSIGNMENT_WIP_CEILING = 8;

function assignmentCapacityModel(queue = [], assignable = []) {
  const ownerRows = assignmentOwnershipRows(queue);
  const ownerMap = new Map(ownerRows.map((row) => [row.owner, row]));
  // Ledger rows = the roster (people you can route to) UNION any owner who
  // actually holds queued work. Unioning the real queue owners keeps the ledger
  // honest: a claim assigned to someone not on the current roster (legacy data,
  // a removed biller, or a team token) still shows its load here, so per-owner
  // totals reconcile with the command header instead of silently reading $0
  // under a non-zero headline.
  const rosterOwners = users.filter((user) => canonicalRole(user.role) === "OffshoreBiller").map((user) => user.name);
  const ledgerOwners = [];
  const seenOwner = new Set();
  rosterOwners.concat(ownerRows.map((row) => row.owner)).forEach((owner) => {
    if (!owner || owner === "Unassigned" || seenOwner.has(owner)) return;
    seenOwner.add(owner);
    ledgerOwners.push(owner);
  });
  const rows = ledgerOwners.map((owner) => {
    const base = ownerMap.get(owner) || { owner, claims: 0, owed: 0, pastDue: 0, submitted: 0, bounced: 0, inProgress: 0 };
    const missingDue = queue.filter((claim) => claim.assignedTo === owner && assignmentDueStatus(claim).key === "missingDue").length;
    const dueSoon = queue.filter((claim) => claim.assignedTo === owner && ["dueToday", "dueSoon"].includes(assignmentDueStatus(claim).key)).length;
    const loadScore = assignmentCapacityScore({ ...base, missingDue, dueSoon });
    const capacity = assignmentCapacityLabel(loadScore, base.claims);
    return {
      ...base,
      owed: moneyNumber(base.owed || 0),
      missingDue,
      dueSoon,
      loadScore,
      capacity,
      ceiling: ASSIGNMENT_WIP_CEILING,
      utilization: Math.min(100, Math.round((base.claims / ASSIGNMENT_WIP_CEILING) * 100)),
      recommendation: assignmentCapacityRecommendation({ ...base, missingDue, dueSoon, loadScore, capacity })
    };
  }).sort((a, b) => a.loadScore - b.loadScore || a.claims - b.claims || a.owed - b.owed || a.owner.localeCompare(b.owner));
  const recommendedOwner = rows.find((row) => row.capacity !== "At Capacity") || rows[0] || { owner: rosterOwners[0] || "Unassigned", capacity: "Available", loadScore: 0 };
  const overloaded = [...rows].sort((a, b) => b.loadScore - a.loadScore || b.owed - a.owed)[0] || null;
  const unassignedAr = moneyNumber(assignable.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0));
  const criticalReady = assignable.filter((claim) => workPriorityScore(claim) >= 80 || evaluateClaimCalculationGuards(claim).length > 0).length;
  return {
    rows,
    recommendedOwner,
    overloaded,
    totals: {
      readyClaims: assignable.length,
      readyAr: unassignedAr,
      criticalReady,
      availableOwners: rows.filter((row) => row.capacity === "Available").length,
      atCapacityOwners: rows.filter((row) => row.capacity === "At Capacity").length,
      reviewClaims: rows.reduce((sum, row) => sum + row.submitted, 0),
      overdueClaims: rows.reduce((sum, row) => sum + row.pastDue, 0)
    }
  };
}

function assignmentCapacityScore(row = {}) {
  return Math.round(
    (row.claims || 0) * 10
    + (row.inProgress || 0) * 4
    + (row.submitted || 0) * 7
    + (row.bounced || 0) * 18
    + (row.pastDue || 0) * 20
    + (row.missingDue || 0) * 12
    + (row.dueSoon || 0) * 8
    + Math.min((row.owed || 0) / 500, 30)
  );
}

function assignmentCapacityLabel(score, claims) {
  if (claims === 0) return "Available";
  if (score >= 70 || claims >= 8) return "At Capacity";
  if (score >= 40 || claims >= 5) return "Watch Load";
  return "Available";
}

function assignmentCapacityRecommendation(row = {}) {
  if (row.capacity === "Available") return "Route next clean batch here first.";
  if ((row.bounced || 0) > 0) return "Hold new intake until bounced-back corrections are cleared.";
  if ((row.pastDue || 0) > 0) return "Escalate overdue proof and avoid new assignments until SLA is reset.";
  if ((row.submitted || 0) > 0) return "Manager should clear submitted review before adding more work.";
  if ((row.missingDue || 0) > 0) return "Set missing assignment due dates before routing new work.";
  return "Keep load stable; route only high-fit follow-up work.";
}

function renderAssignmentCapacityBoard(queue = [], assignable = []) {
  const model = assignmentCapacityModel(queue, assignable);
  return `
    <section class="assignment-capacity-board" aria-label="Assignment capacity board">
      <div class="assignment-capacity-head">
        <div>
          <span>Assignment Capacity Board</span>
          <strong>Who can take the next batch, and who needs relief</strong>
          <p>Manager routing view across active owner load, SLA risk, review work, bounced-back corrections, and unassigned AR.</p>
        </div>
        ${pill(`Next: ${model.recommendedOwner.owner}`, model.recommendedOwner.capacity === "Available" ? "green" : "amber")}
      </div>
      <div class="assignment-capacity-summary">
        <div><span>Ready Intake</span><strong>${model.totals.readyClaims}</strong><small>${money(model.totals.readyAr)} unassigned AR</small></div>
        <div><span>Critical Ready</span><strong>${model.totals.criticalReady}</strong><small>Score 80+ or guarded</small></div>
        <div><span>Available Owners</span><strong>${model.totals.availableOwners}</strong><small>Can receive next batch</small></div>
        <div><span>At Capacity</span><strong>${model.totals.atCapacityOwners}</strong><small>Hold or rebalance</small></div>
        <div><span>Review Work</span><strong>${model.totals.reviewClaims}</strong><small>Waiting manager decision</small></div>
        <div><span>Overdue Load</span><strong>${model.totals.overdueClaims}</strong><small>SLA pressure</small></div>
      </div>
      <div class="assignment-capacity-list">
        <div class="assignment-capacity-header">
          <span>Owner</span>
          <span>Capacity</span>
          <span>Load</span>
          <span>Risk</span>
          <span>Open AR</span>
          <span>Routing Decision</span>
        </div>
        ${model.rows.map(renderAssignmentCapacityRow).join("")}
      </div>
    </section>
  `;
}

function renderAssignmentCapacityRow(row) {
  const tone = row.capacity === "Available" ? "green" : row.capacity === "Watch Load" ? "amber" : "red";
  return `
    <div class="assignment-capacity-row">
      <span data-label="Owner"><strong>${escapeHtml(row.owner)}</strong><small>${formatNounCount(row.claims, "active claim")}</small></span>
      <span data-label="Capacity">${pill(row.capacity, tone)}<small>Score ${row.loadScore}</small></span>
      <span data-label="Load"><strong>${row.inProgress} in progress</strong><small>${row.submitted} review · ${row.bounced} bounced</small></span>
      <span data-label="Risk"><strong>${row.pastDue} overdue</strong><small>${row.missingDue} missing SLA · ${row.dueSoon} due soon</small></span>
      <span data-label="Open AR"><strong>${money(row.owed)}</strong><small>Assigned balance</small></span>
      <span data-label="Routing Decision">${escapeHtml(row.recommendation)}</span>
    </div>
  `;
}

function assignmentOwnerAccountabilityModel(queue = [], assignable = []) {
  const capacity = assignmentCapacityModel(queue, assignable);
  const intake = assignmentIntakeDecisionModel(queue, assignable);
  const intakeByOwner = new Map();
  intake.rows.forEach((row) => {
    const current = intakeByOwner.get(row.assignedToward) || { claims: 0, ar: 0, critical: 0, guarded: 0, nextClaims: [] };
    current.claims += 1;
    current.ar += row.outstanding;
    if (row.priority >= 80) current.critical += 1;
    if (row.guardCount > 0) current.guarded += 1;
    current.nextClaims.push(row.claimId);
    intakeByOwner.set(row.assignedToward, current);
  });
  // The ledger is the roster UNION the owners who hold queued work (see
  // assignmentCapacityModel) UNION the owners the intake model is recommending
  // work TO. The third arm matters now that no roster ships by default: with an
  // empty roster and an empty queue, the intake model still recommends an owner
  // (suggestedAssignmentOwner falls back to "Offshore"), and without this the
  // board dropped those rows - a non-zero next-intake headline over rows that
  // sum to $0, which is the exact failure the capacity comment above exists to
  // prevent, arrived at from the other side.
  const ledgerRows = capacity.rows.slice();
  const ledgerOwnerNames = new Set(ledgerRows.map((row) => row.owner));
  Array.from(intakeByOwner.keys()).forEach((owner) => {
    if (!owner || ledgerOwnerNames.has(owner)) return;
    ledgerOwnerNames.add(owner);
    ledgerRows.push({ owner, capacity: "Available", loadScore: 0, claims: 0, owed: 0 });
  });
  const rows = ledgerRows.map((owner) => {
    const next = intakeByOwner.get(owner.owner) || { claims: 0, ar: 0, critical: 0, guarded: 0, nextClaims: [] };
    const ownerClaims = queue.filter((claim) => claim.assignedTo === owner.owner);
    const reviewClaims = ownerClaims.filter((claim) => claim.assignmentStatus === "Submitted For Review").length;
    const correctionClaims = ownerClaims.filter((claim) => claim.assignmentStatus === "Bounced Back").length;
    const missingDue = ownerClaims.filter((claim) => assignmentDueStatus(claim).key === "missingDue").length;
    const overdue = ownerClaims.filter((claim) => assignmentDueStatus(claim).key === "overdue").length;
    return {
      owner: owner.owner,
      capacity: owner.capacity,
      loadScore: owner.loadScore,
      activeClaims: owner.claims,
      activeAr: owner.owed,
      nextClaims: next.claims,
      nextAr: moneyNumber(next.ar),
      nextClaimLabels: next.nextClaims.slice(0, 3).join(", ") || "None queued",
      criticalNext: next.critical,
      guardedNext: next.guarded,
      reviewClaims,
      correctionClaims,
      missingDue,
      overdue,
      assignmentLane: assignmentOwnerAccountabilityLane({ owner, next, reviewClaims, correctionClaims, missingDue, overdue }),
      managerAction: assignmentOwnerAccountabilityAction({ owner, next, reviewClaims, correctionClaims, missingDue, overdue })
    };
  });
  const totals = rows.reduce((sum, row) => {
    sum.activeClaims += row.activeClaims;
    sum.activeAr += row.activeAr;
    sum.nextClaims += row.nextClaims;
    sum.nextAr += row.nextAr;
    sum.reviewClaims += row.reviewClaims;
    sum.correctionClaims += row.correctionClaims;
    sum.slaRisk += row.missingDue + row.overdue;
    if (row.nextClaims > 0) sum.ownersWithNext += 1;
    return sum;
  }, { activeClaims: 0, activeAr: 0, nextClaims: 0, nextAr: 0, reviewClaims: 0, correctionClaims: 0, slaRisk: 0, ownersWithNext: 0 });
  return {
    rows,
    totals: {
      ...totals,
      activeAr: moneyNumber(totals.activeAr),
      nextAr: moneyNumber(totals.nextAr)
    }
  };
}

function assignmentOwnerAccountabilityLane({ owner = {}, next = {}, reviewClaims = 0, correctionClaims = 0, missingDue = 0, overdue = 0 }) {
  if (correctionClaims > 0) return "Correction Lane";
  if (reviewClaims > 0) return "Manager Review Lane";
  if (overdue > 0 || missingDue > 0) return "SLA Reset Lane";
  if ((next.claims || 0) > 0 && owner.capacity !== "At Capacity") return "Next Intake Lane";
  if (owner.capacity === "At Capacity") return "Hold New Work";
  return "Available";
}

function assignmentOwnerAccountabilityAction({ owner = {}, next = {}, reviewClaims = 0, correctionClaims = 0, missingDue = 0, overdue = 0 }) {
  if (correctionClaims > 0) return `Clear ${correctionClaims} bounced-back correction${correctionClaims === 1 ? "" : "s"} before routing new AR.`;
  if (reviewClaims > 0) return `Manager should approve or bounce ${formatNounCount(reviewClaims, "submitted package")} from this owner.`;
  if (overdue > 0) return `Escalate ${overdue} overdue assignment${overdue === 1 ? "" : "s"} and reset proof due dates.`;
  if (missingDue > 0) return `Set ${missingDue} missing assignment due date${missingDue === 1 ? "" : "s"} before more handoff.`;
  if ((next.claims || 0) > 0 && owner.capacity !== "At Capacity") return `Assign ${formatNounCount(next.claims, "next intake claim")} toward this owner with proof requirements.`;
  if (owner.capacity === "At Capacity") return "Hold new assignments until active AR or review load drops.";
  return "Available for the next clean assignment batch.";
}

function renderAssignmentOwnerAccountabilityBoard(queue = [], assignable = []) {
  const model = assignmentOwnerAccountabilityModel(queue, assignable);
  return `
    <section class="assignment-accountability-board" aria-label="Assignment owner accountability board">
      <div class="assignment-accountability-head">
        <div>
          <span>Assignment Owner Accountability Board</span>
          <strong>Who owns work now, who receives the next work, and what managers must clear</strong>
          <p>Ownership roster that ties active assignments, next intake routing, review/correction lanes, SLA risk, and manager action into one clear handoff view.</p>
        </div>
        ${pill(`${formatNounCount(model.totals.ownersWithNext, "owner")} with next intake`, model.totals.ownersWithNext ? "blue" : "green")}
      </div>
      <div class="assignment-accountability-summary">
        <div><span>Active Assigned</span><strong>${model.totals.activeClaims}</strong><small>${money(model.totals.activeAr)} current owner AR</small></div>
        <div><span>Next Intake</span><strong>${model.totals.nextClaims}</strong><small>${money(model.totals.nextAr)} ready to assign</small></div>
        <div><span>Manager Review</span><strong>${model.totals.reviewClaims}</strong><small>Submitted packages to clear</small></div>
        <div><span>Corrections</span><strong>${model.totals.correctionClaims}</strong><small>Bounced-back owner fixes</small></div>
        <div><span>SLA Risk</span><strong>${model.totals.slaRisk}</strong><small>Missing or overdue due dates</small></div>
      </div>
      <div class="assignment-accountability-list">
        <div class="assignment-accountability-row assignment-accountability-header">
          <span>Owner</span>
          <span>Current Work</span>
          <span>Next Assign Toward</span>
          <span>Review / Correction</span>
          <span>Lane</span>
          <span>Manager Action</span>
        </div>
        ${model.rows.map(renderAssignmentOwnerAccountabilityRow).join("")}
      </div>
    </section>
  `;
}

function renderAssignmentOwnerAccountabilityRow(row) {
  const tone = row.assignmentLane === "Correction Lane" || row.assignmentLane === "SLA Reset Lane"
    ? "amber"
    : row.assignmentLane === "Hold New Work"
      ? "red"
      : row.assignmentLane === "Available"
        ? "green"
        : "blue";
  return `
    <div class="assignment-accountability-row">
      <span data-label="Owner"><strong>${escapeHtml(row.owner)}</strong><small>${escapeHtml(row.capacity)} · load ${row.loadScore}</small></span>
      <span data-label="Current Work"><strong>${formatNounCount(row.activeClaims, "claim")}</strong><small>${money(row.activeAr)} assigned AR</small></span>
      <span data-label="Next Assign Toward"><strong>${formatNounCount(row.nextClaims, "claim")}</strong><small>${money(row.nextAr)} · ${escapeHtml(row.nextClaimLabels)}</small></span>
      <span data-label="Review / Correction"><strong>${row.reviewClaims} review · ${row.correctionClaims} correction</strong><small>${row.missingDue} missing SLA · ${row.overdue} overdue</small></span>
      <span data-label="Lane">${pill(row.assignmentLane, tone)}<small>${row.criticalNext} critical · ${row.guardedNext} guarded next</small></span>
      <span data-label="Manager Action">${escapeHtml(row.managerAction)}</span>
    </div>
  `;
}

function assignmentReviewReadinessModel(queue = []) {
  const rows = queue
    .filter((claim) => claim.assignedTo && claim.assignmentStatus !== "Approved")
    .map((claim) => {
      const packet = assignmentWorkPacket(claim);
      const due = assignmentDueStatus(claim);
      const note = String(claim.reviewNote || "").trim();
      const hasProofNote = note.length >= 20;
      const hasNextAction = Boolean(claim.action && claim.action !== "No Action");
      const hasFollowUpDate = Boolean(claim.followUpDate);
      const missing = [];
      if (!hasProofNote) missing.push("completion proof note");
      if (!hasNextAction) missing.push("next action");
      if (!hasFollowUpDate) missing.push("follow-up date");
      if (packet.guardCount > 0) missing.push("calculation guard resolution");
      if (due.key === "missingDue") missing.push("assignment due date");
      if (claim.assignmentStatus === "Bounced Back") missing.push("bounce-back correction");
      const status = assignmentReviewReadinessStatus(claim, due, missing);
      return {
        claim,
        claimId: claim.claimId,
        owner: claim.assignedTo || "Unassigned",
        reviewer: "Manager / Admin",
        status,
        due,
        expected: packet.expected,
        paid: packet.paid,
        outstanding: packet.outstanding,
        delta: packet.delta,
        priority: packet.priority,
        guardSummary: packet.guardSummary,
        proofNote: hasProofNote ? "Proof note ready" : "Missing proof note",
        missing,
        nextAction: assignmentReviewReadinessAction(claim, status, missing)
      };
    })
    .sort((a, b) => assignmentReviewReadinessRank(b) - assignmentReviewReadinessRank(a)
      || b.outstanding - a.outstanding
      || b.priority - a.priority
      || a.claimId.localeCompare(b.claimId));
  const totals = rows.reduce((sum, row) => {
    sum.claims += 1;
    sum.exposure += row.outstanding;
    sum.delta += row.delta;
    if (row.status === "Ready To Submit") sum.ready += 1;
    if (row.status === "Needs Completion") sum.needsCompletion += 1;
    if (row.status === "Manager Review") sum.managerReview += 1;
    if (row.status === "Correction Required") sum.corrections += 1;
    if (row.status === "SLA Escalation") sum.slaEscalation += 1;
    return sum;
  }, { claims: 0, exposure: 0, delta: 0, ready: 0, needsCompletion: 0, managerReview: 0, corrections: 0, slaEscalation: 0 });
  return {
    rows: rows.slice(0, 8),
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta)
    }
  };
}

function assignmentReviewReadinessStatus(claim, due, missing = []) {
  if (claim.assignmentStatus === "Submitted For Review") return "Manager Review";
  if (claim.assignmentStatus === "Bounced Back") return "Correction Required";
  if (due.key === "overdue") return "SLA Escalation";
  if (missing.length === 0) return "Ready To Submit";
  return "Needs Completion";
}

function assignmentReviewReadinessRank(row = {}) {
  const statusRank = {
    "SLA Escalation": 90,
    "Correction Required": 80,
    "Manager Review": 70,
    "Needs Completion": 55,
    "Ready To Submit": 40
  };
  const dueRank = { overdue: 30, missingDue: 20, dueToday: 14, dueSoon: 8, onTrack: 0 };
  return (statusRank[row.status] || 20) + (dueRank[row.due?.key] || 0) + Math.min(row.priority || 0, 100) / 10;
}

function assignmentReviewReadinessAction(claim, status, missing = []) {
  if (status === "Manager Review") return "Manager should approve or bounce back with exact evidence language.";
  if (status === "Correction Required") return "Assigned owner must fix bounce-back evidence and resubmit the corrected package.";
  if (status === "SLA Escalation") return "Manager should reset SLA, confirm owner capacity, and document the blocker.";
  if (status === "Ready To Submit") return "Submit package to manager review with proof note, next action, and follow-up date.";
  return `Complete before review: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? ", ..." : ""}.`;
}

function renderAssignmentReviewReadinessGate(queue = []) {
  const model = assignmentReviewReadinessModel(queue);
  return `
    <section class="assignment-review-readiness" aria-label="Assignment review readiness gate">
      <div class="assignment-review-readiness-head">
        <div>
          <span>Assignment Review Readiness Gate</span>
          <strong>What can move to manager review and what is still missing</strong>
          <p>Pre-review control that checks proof notes, next action, follow-up date, calculation guards, correction status, SLA risk, and financial formula before offshore work enters review.</p>
        </div>
        ${pill(`${model.totals.ready} ready`, model.totals.ready ? "green" : "amber")}
      </div>
      <div class="assignment-review-readiness-summary">
        <div><span>Active Checked</span><strong>${model.totals.claims}</strong><small>${money(model.totals.exposure)} assigned AR</small></div>
        <div><span>Ready To Submit</span><strong>${model.totals.ready}</strong><small>Proof/action/date complete</small></div>
        <div><span>Needs Completion</span><strong>${model.totals.needsCompletion}</strong><small>Missing evidence before review</small></div>
        <div><span>Manager Review</span><strong>${model.totals.managerReview}</strong><small>Already submitted</small></div>
        <div><span>Corrections</span><strong>${model.totals.corrections}</strong><small>Bounced-back fixes</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - paid - AR</small></div>
      </div>
      <div class="assignment-review-readiness-list">
        <div class="assignment-review-readiness-row assignment-review-readiness-header">
          <span>Claim / Owner</span>
          <span>Readiness</span>
          <span>Financial Proof</span>
          <span>Missing / Guard</span>
          <span>Reviewer</span>
          <span>Next Step</span>
        </div>
        ${model.rows.length ? model.rows.map(renderAssignmentReviewReadinessRow).join("") : '<div class="assignment-review-readiness-empty">No active assignments need review readiness checks.</div>'}
      </div>
    </section>
  `;
}

function renderAssignmentReviewReadinessRow(row) {
  const tone = row.status === "Ready To Submit"
    ? "green"
    : row.status === "Manager Review"
      ? "blue"
      : row.status === "SLA Escalation" || row.status === "Correction Required"
        ? "red"
        : "amber";
  return `
    <button type="button" class="assignment-review-readiness-row" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Owner"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.owner)} · ${escapeHtml(row.due.label)}</small></span>
      <span data-label="Readiness">${pill(row.status, tone)}<small>${escapeHtml(row.proofNote)}</small></span>
      <span data-label="Financial Proof"><strong>${money(row.outstanding)}</strong><small>Expected ${money(row.expected)} · Paid ${money(row.paid)} · Delta ${money(row.delta)}</small></span>
      <span data-label="Missing / Guard"><strong>${escapeHtml(row.missing.length ? row.missing.slice(0, 2).join(", ") : "Ready")}</strong><small>${escapeHtml(row.guardSummary)}</small></span>
      <span data-label="Reviewer"><strong>${escapeHtml(row.reviewer)}</strong><small>Priority ${row.priority} ${priorityBand(row.priority)}</small></span>
      <span data-label="Next Step">${escapeHtml(row.nextAction)}</span>
    </button>
  `;
}

function assignmentProofAcceptanceModel(queue = []) {
  const rows = (Array.isArray(queue) ? queue : [])
    .filter((claim) => claim.assignedTo && claim.assignmentStatus !== "Approved")
    .map((claim) => {
      const route = assignmentRouteModel(claim, { mode: claim.assignmentStatus === "Submitted For Review" ? "review" : "assignment" });
      const packet = assignmentWorkPacket(claim);
      const due = assignmentDueStatus(claim);
      const noteLength = String(claim.reviewNote || "").trim().length;
      const hasProofNote = noteLength >= 20;
      const hasNextAction = Boolean(claim.action && claim.action !== "No Action");
      const hasFollowUpDate = Boolean(claim.followUpDate);
      const formulaClean = Math.abs(packet.delta) < 0.01;
      const missing = [];
      if (!hasProofNote) missing.push("proof note");
      if (!hasNextAction) missing.push("next action");
      if (!hasFollowUpDate) missing.push("follow-up date");
      if (packet.guardCount > 0) missing.push("guard resolution");
      if (!formulaClean) missing.push("formula delta");
      if (due.key === "missingDue") missing.push("assignment SLA");
      if (claim.assignmentStatus === "Bounced Back") missing.push("bounce-back fix");
      const gateStatus = assignmentProofAcceptanceStatus(claim, due, missing);
      return {
        claim,
        claimId: claim.claimId,
        assignedToward: route.assignedTo,
        assignedBy: route.assignedBy,
        reviewOwner: route.decisionOwner === route.assignedTo ? "Manager / Admin" : route.decisionOwner,
        routeLane: route.routeLane,
        status: claim.assignmentStatus || "Assigned",
        dueLabel: due.label,
        gateStatus,
        evidenceStatus: assignmentProofEvidenceStatus({ hasProofNote, hasNextAction, hasFollowUpDate, packet, formulaClean }),
        requiredProof: packet.proofNeed,
        expected: packet.expected,
        paid: packet.paid,
        outstanding: packet.outstanding,
        formulaDelta: packet.delta,
        missing,
        priority: packet.priority,
        nextCloseAction: assignmentProofAcceptanceNextAction(claim, gateStatus, missing)
      };
    })
    .sort((a, b) => assignmentProofAcceptanceRank(b) - assignmentProofAcceptanceRank(a)
      || b.outstanding - a.outstanding
      || b.priority - a.priority
      || a.claimId.localeCompare(b.claimId));
  const visibleRows = rows.slice(0, 8);
  const totals = visibleRows.reduce((sum, row) => {
    sum.active += 1;
    sum.exposure += row.outstanding;
    sum.formulaDelta += row.formulaDelta;
    if (row.gateStatus === "Accepted For Review" || row.gateStatus === "Ready To Closeout") sum.acceptable += 1;
    if (row.missing.includes("proof note") || row.missing.includes("next action") || row.missing.includes("follow-up date")) sum.missingProof += 1;
    if (row.status === "Bounced Back") sum.corrections += 1;
    if (row.missing.includes("formula delta") || row.missing.includes("guard resolution")) sum.formulaReview += 1;
    return sum;
  }, { active: 0, acceptable: 0, missingProof: 0, corrections: 0, formulaReview: 0, exposure: 0, formulaDelta: 0 });
  return {
    rows: visibleRows,
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      formulaDelta: moneyNumber(totals.formulaDelta)
    }
  };
}

function assignmentProofAcceptanceStatus(claim, due, missing = []) {
  if (claim.assignmentStatus === "Bounced Back") return "Correction Required";
  if (due.key === "overdue") return "SLA Blocked";
  if (claim.assignmentStatus === "Submitted For Review") return missing.length ? "Review Blocked" : "Accepted For Review";
  if (missing.length === 0) return "Ready To Closeout";
  return "Proof Missing";
}

function assignmentProofEvidenceStatus({ hasProofNote = false, hasNextAction = false, hasFollowUpDate = false, packet = {}, formulaClean = false } = {}) {
  const ready = [
    hasProofNote ? "note" : "",
    hasNextAction ? "action" : "",
    hasFollowUpDate ? "date" : "",
    (packet.guardCount || 0) === 0 ? "guards" : "",
    formulaClean ? "math" : ""
  ].filter(Boolean);
  return `${ready.length}/5 ready: ${ready.join(", ") || "none"}`;
}

function assignmentProofAcceptanceRank(row = {}) {
  const gateRank = {
    "SLA Blocked": 95,
    "Correction Required": 90,
    "Review Blocked": 80,
    "Proof Missing": 65,
    "Accepted For Review": 45,
    "Ready To Closeout": 40
  };
  return (gateRank[row.gateStatus] || 30) + Math.min(row.priority || 0, 100) / 10;
}

function assignmentProofAcceptanceNextAction(claim, gateStatus, missing = []) {
  if (gateStatus === "Accepted For Review") return "Manager can approve, bounce, or escalate from a complete proof package.";
  if (gateStatus === "Ready To Closeout") return "Submit for manager review with proof note, next action, follow-up date, and math evidence.";
  if (gateStatus === "Correction Required") return "Assigned owner fixes the manager bounce-back reason and resubmits the corrected proof.";
  if (gateStatus === "SLA Blocked") return "Manager resets SLA and confirms whether the owner can still complete the package.";
  if (gateStatus === "Review Blocked") return `Manager cannot accept yet: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? ", ..." : ""}.`;
  return `Complete proof before closeout: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? ", ..." : ""}.`;
}

function renderAssignmentProofAcceptanceGate(queue = []) {
  const model = assignmentProofAcceptanceModel(queue);
  return `
    <section class="assignment-proof-acceptance" aria-label="Assignment proof acceptance gate">
      <div class="assignment-proof-acceptance-head">
        <div>
          <span>Assignment Proof Acceptance Gate</span>
          <strong>Can this assignment be submitted or accepted for review?</strong>
          <p>Closeout control that makes the assigned-toward owner, assigned-by source, review owner, required proof, evidence readiness, and Expected - Paid - AR math visible before anyone calls work complete.</p>
        </div>
        ${pill(`${model.totals.acceptable} acceptable`, model.totals.acceptable ? "green" : "amber")}
      </div>
      <div class="assignment-proof-acceptance-summary">
        <div><span>Active Gate Rows</span><strong>${model.totals.active}</strong><small>${money(model.totals.exposure)} assignment AR</small></div>
        <div><span>Acceptable</span><strong>${model.totals.acceptable}</strong><small>Ready for review or closeout</small></div>
        <div><span>Missing Proof</span><strong>${model.totals.missingProof}</strong><small>Note/action/date incomplete</small></div>
        <div><span>Corrections</span><strong>${model.totals.corrections}</strong><small>Bounced-back proof fixes</small></div>
        <div><span>Formula Review</span><strong>${model.totals.formulaReview}</strong><small>Guard or delta needs proof</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.formulaDelta)}</strong><small>Expected - Paid - AR</small></div>
      </div>
      <div class="assignment-proof-acceptance-list">
        <div class="assignment-proof-acceptance-row assignment-proof-acceptance-header">
          <span>Claim / Assigned Toward</span>
          <span>Assigned By / Review Owner</span>
          <span>Acceptance State</span>
          <span>Required Proof</span>
          <span>Evidence / Formula Delta</span>
          <span>Next Close Action</span>
        </div>
        ${model.rows.length ? model.rows.map(renderAssignmentProofAcceptanceRow).join("") : '<div class="assignment-proof-acceptance-empty">No active assignment proof packages need acceptance checks.</div>'}
      </div>
    </section>
  `;
}

function renderAssignmentProofAcceptanceRow(row) {
  const tone = row.gateStatus === "Accepted For Review" || row.gateStatus === "Ready To Closeout"
    ? "green"
    : row.gateStatus === "Correction Required" || row.gateStatus === "SLA Blocked"
      ? "red"
      : "amber";
  return `
    <button type="button" class="assignment-proof-acceptance-row" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Assigned Toward"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.assignedToward)} · ${escapeHtml(row.routeLane)}</small></span>
      <span data-label="Assigned By / Review Owner"><strong>${escapeHtml(row.assignedBy)}</strong><small>Review owner: ${escapeHtml(row.reviewOwner)}</small></span>
      <span data-label="Acceptance State">${pill(row.gateStatus, tone)}<small>${escapeHtml(row.status)} · ${escapeHtml(row.dueLabel)}</small></span>
      <span data-label="Required Proof"><strong>${escapeHtml(row.requiredProof)}</strong><small>Missing: ${escapeHtml(row.missing.length ? row.missing.slice(0, 3).join(", ") : "none")}</small></span>
      <span data-label="Evidence / Formula Delta"><strong>${escapeHtml(row.evidenceStatus)}</strong><small>Expected ${money(row.expected)} · Paid ${money(row.paid)} · AR ${money(row.outstanding)} · Delta ${money(row.formulaDelta)}</small></span>
      <span data-label="Next Close Action">${escapeHtml(row.nextCloseAction)}</span>
    </button>
  `;
}

function assignmentIntakeDecisionModel(queue = [], assignable = []) {
  const capacity = assignmentCapacityModel(queue, assignable);
  const ownerRows = capacity.rows.length ? capacity.rows : users
    .filter((user) => user.role === "OffshoreBiller")
    .map((user) => ({ owner: user.name, capacity: "Available", loadScore: 0, claims: 0, owed: 0 }));
  const availableOwners = ownerRows.filter((row) => row.capacity !== "At Capacity");
  const candidateRows = assignable
    .slice()
    .sort(sortClaims)
    .slice(0, 8)
    .map((claim, index) => {
      const packet = assignmentWorkPacket(claim);
      const ownerPool = availableOwners.length ? availableOwners : ownerRows;
      const owner = ownerPool[index % Math.max(ownerPool.length, 1)] || { owner: suggestedAssignmentOwner(index, queue), capacity: "Available", loadScore: 0 };
      const dueDate = assignmentRecommendedDueDate(claim, packet.priority);
      return {
        claim,
        claimId: claim.claimId,
        client: claim.client || "Unknown client",
        payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
        assignedToward: owner.owner,
        ownerCapacity: owner.capacity || "Available",
        ownerLoadScore: owner.loadScore || 0,
        priority: packet.priority,
        priorityBand: priorityBand(packet.priority),
        outstanding: packet.outstanding,
        delta: packet.delta,
        guardSummary: packet.guardSummary,
        guardCount: packet.guardCount,
        proofNeed: packet.proofNeed,
        dueDate,
        dueLabel: formatDate(dueDate),
        routeReason: assignmentIntakeRouteReason(claim, packet, owner),
        nextStep: assignmentIntakeNextStep(claim, packet)
      };
    });
  const totals = candidateRows.reduce((sum, row) => {
    sum.claims += 1;
    sum.ar += row.outstanding;
    if (row.priority >= 80) sum.critical += 1;
    if (row.guardCount > 0) sum.guarded += 1;
    sum.owners.add(row.assignedToward);
    return sum;
  }, { claims: 0, ar: 0, critical: 0, guarded: 0, owners: new Set() });
  return {
    rows: candidateRows,
    totals: {
      claims: totals.claims,
      ar: moneyNumber(totals.ar),
      critical: totals.critical,
      guarded: totals.guarded,
      owners: totals.owners.size,
      availableOwners: capacity.totals.availableOwners,
      recommendedOwner: capacity.recommendedOwner?.owner || "None"
    }
  };
}

function assignmentRecommendedDueDate(claim = {}, priority = 0) {
  const base = todayStart();
  const days = priority >= 90 || isPastDue(claim) ? 1 : priority >= 80 ? 2 : priority >= 60 ? 3 : 5;
  base.setDate(base.getDate() + days);
  const year = base.getFullYear();
  const month = String(base.getMonth() + 1).padStart(2, "0");
  const day = String(base.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function assignmentIntakeRouteReason(claim = {}, packet = {}, owner = {}) {
  if (packet.guardCount > 0) return `Guarded math routed to ${owner.owner} with required proof before review.`;
  if (packet.priority >= 80) return `High-priority AR routed to ${owner.owner} because current capacity is ${owner.capacity}.`;
  if (needsClientAction(claim)) return `Client-proof work routed to ${owner.owner} for exact evidence request documentation.`;
  return `Balanced intake routed to ${owner.owner} based on capacity and active load.`;
}

function assignmentIntakeNextStep(claim = {}, packet = {}) {
  if (packet.guardCount > 0) return "Assign with guard note; require evidence before manager approval.";
  if (needsClientAction(claim)) return "Assign owner to request authorization, COB, coverage, or records.";
  if (packet.priority >= 80) return "Assign immediately with SLA due date and payer proof requirement.";
  return "Assign as standard follow-up with proof note and follow-up date.";
}

function renderAssignmentIntakeDecisionBoard(queue = [], assignable = []) {
  const model = assignmentIntakeDecisionModel(queue, assignable);
  return `
    <section class="assignment-intake-board" aria-label="Assignment intake decision board">
      <div class="assignment-intake-head">
        <div>
          <span>Assignment Intake Decision Board</span>
          <strong>Next claims to route, assigned owner, SLA, and required proof</strong>
          <p>Manager-ready intake list for unassigned AR: each row says who the claim should be assigned toward, why that owner, the Recommended assignment due date, and what proof is required.</p>
        </div>
        ${pill(`Primary route: ${model.totals.recommendedOwner}`, model.totals.availableOwners ? "green" : "amber")}
      </div>
      <div class="assignment-intake-summary">
        <div><span>Intake Rows</span><strong>${model.totals.claims}</strong><small>${money(model.totals.ar)} ready AR</small></div>
        <div><span>Critical</span><strong>${model.totals.critical}</strong><small>Score 80+ routing</small></div>
        <div><span>Guarded</span><strong>${model.totals.guarded}</strong><small>Math/proof required</small></div>
        <div><span>Owners Used</span><strong>${model.totals.owners}</strong><small>${formatNounCount(model.totals.availableOwners, "available owner")}</small></div>
      </div>
      <div class="assignment-intake-list">
        <div class="assignment-intake-header">
          <span>Claim / Payer</span>
          <span>Assign Toward</span>
          <span>Priority / AR</span>
          <span>SLA</span>
          <span>Required Proof</span>
          <span>Routing Decision</span>
        </div>
        ${model.rows.length ? model.rows.map(renderAssignmentIntakeDecisionRow).join("") : '<div class="assignment-intake-empty">No unassigned AR is waiting for intake routing under the current filters.</div>'}
      </div>
    </section>
  `;
}

function renderAssignmentIntakeDecisionRow(row) {
  const tone = row.priority >= 80 || row.guardCount > 0 ? "amber" : "blue";
  return `
    <button type="button" class="assignment-intake-row" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Payer">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.client)} · ${escapeHtml(row.payer)}</small>
      </span>
      <span data-label="Assign Toward">
        <strong>${escapeHtml(row.assignedToward)}</strong>
        <small>${escapeHtml(row.ownerCapacity)} · score ${row.ownerLoadScore}</small>
      </span>
      <span data-label="Priority / AR">
        ${pill(`${row.priority} ${row.priorityBand}`, tone)}
        <small>${money(row.outstanding)} AR · delta ${money(row.delta)}</small>
      </span>
      <span data-label="SLA">
        <strong>${escapeHtml(row.dueLabel)}</strong>
        <small>Recommended assignment due</small>
      </span>
      <span data-label="Required Proof">
        <strong>${escapeHtml(row.guardSummary)}</strong>
        <small>${escapeHtml(row.proofNeed)}</small>
      </span>
      <small data-label="Routing Decision">${escapeHtml(row.routeReason)} ${escapeHtml(row.nextStep)}</small>
    </button>
  `;
}

function assignmentDueStatus(claim) {
  if (!claim.assignedTo) return { key: "unassigned", label: "Unassigned", days: null };
  if (!claim.assignmentDue) return { key: "missingDue", label: "Missing Due Date", days: null };
  const due = localDateFromDateOnly(claim.assignmentDue);
  if (!due) return { key: "missingDue", label: "Missing Due Date", days: null };
  const days = Math.ceil((due - todayStart()) / 86400000);
  if (days < 0) return { key: "overdue", label: "Overdue", days };
  if (days === 0) return { key: "dueToday", label: "Due Today", days };
  if (days <= 2) return { key: "dueSoon", label: "Due 1-2 Days", days };
  return { key: "onTrack", label: "On Track", days };
}

function assignmentSlaRows(queue = [], assignable = []) {
  const active = queue.filter((claim) => claim.assignedTo);
  const rows = active.map((claim) => {
    const due = assignmentDueStatus(claim);
    const financial = claimFinancialView(claim);
    const guards = evaluateClaimCalculationGuards(claim);
    const priority = workPriorityScore(claim);
    const needsEscalation = due.key === "overdue"
      || due.key === "missingDue"
      || claim.assignmentStatus === "Bounced Back"
      || guards.length > 0
      || priority >= 80;
    return {
      claim,
      claimId: claim.claimId,
      owner: claim.assignedTo || "Unassigned",
      assignedBy: claim.assignedBy || "None",
      status: claim.assignmentStatus || "Assigned",
      due,
      outstanding: financial.sourceOutstanding,
      priority,
      guardSummary: calculationGuardSummary(guards),
      needsEscalation,
      action: claim.action || "No next action"
    };
  });
  const totals = rows.reduce((sum, row) => {
    sum.assigned += 1;
    sum.outstanding += row.outstanding;
    if (row.due.key === "overdue") sum.overdue += 1;
    if (row.due.key === "dueToday" || row.due.key === "dueSoon") sum.dueSoon += 1;
    if (row.due.key === "missingDue") sum.missingDue += 1;
    if (row.status === "Submitted For Review") sum.submitted += 1;
    if (row.status === "Bounced Back") sum.bounced += 1;
    if (row.needsEscalation) sum.escalations += 1;
    return sum;
  }, {
    assigned: 0,
    outstanding: 0,
    overdue: 0,
    dueSoon: 0,
    missingDue: 0,
    submitted: 0,
    bounced: 0,
    escalations: 0,
    readyToAssign: assignable.length,
    readyAr: assignable.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0)
  });
  totals.outstanding = moneyNumber(totals.outstanding);
  totals.readyAr = moneyNumber(totals.readyAr);
  const escalationRows = rows
    .filter((row) => row.needsEscalation)
    .sort((a, b) => {
      const dueRank = { overdue: 5, missingDue: 4, dueToday: 3, dueSoon: 2, onTrack: 1 };
      return (dueRank[b.due.key] || 0) - (dueRank[a.due.key] || 0)
        || b.priority - a.priority
        || b.outstanding - a.outstanding
        || a.claimId.localeCompare(b.claimId);
    })
    .slice(0, 6);
  return { totals, rows, escalationRows };
}

function renderAssignmentSlaCommand(queue = [], assignable = []) {
  const { totals, escalationRows } = assignmentSlaRows(queue, assignable);
  return `
    <section class="assignment-sla-command" aria-label="Assignment SLA command">
      <div class="assignment-sla-head">
        <div>
          <span>Assignment SLA Command</span>
          <strong>${formatNounCount(totals.escalations, "escalation")} across ${formatNounCount(totals.assigned, "assigned claim")}</strong>
          <p>Queue-level ownership, due-date, and review-risk view before offshore work gets stuck.</p>
        </div>
        ${pill(`${totals.readyToAssign} ready to assign`, totals.readyToAssign ? "blue" : "green")}
      </div>
      <div class="assignment-sla-metrics">
        <div><span>Assigned AR</span><strong>${money(totals.outstanding)}</strong><small>${formatNounCount(totals.assigned, "active claim")}</small></div>
        <div><span>Overdue</span><strong>${totals.overdue}</strong><small>Assignment due date passed</small></div>
        <div><span>Due Soon</span><strong>${totals.dueSoon}</strong><small>Due today or next 2 days</small></div>
        <div><span>Missing Due</span><strong>${totals.missingDue}</strong><small>Needs assignment SLA</small></div>
        <div><span>In Review</span><strong>${totals.submitted}</strong><small>Manager decision lane</small></div>
        <div><span>Bounced Back</span><strong>${totals.bounced}</strong><small>Needs correction owner</small></div>
      </div>
      <div class="assignment-sla-list">
        ${escalationRows.length ? escalationRows.map(renderAssignmentSlaRow).join("") : '<div class="assignment-sla-empty">No assignment SLA escalations in the current queue.</div>'}
      </div>
    </section>
  `;
}

function renderAssignmentSlaRow(row) {
  const tone = row.due.key === "overdue" || row.status === "Bounced Back" ? "red" : row.due.key === "missingDue" ? "amber" : "blue";
  return `
    <button type="button" class="assignment-sla-row" data-open="${escapeHtml(row.claimId)}">
      <div data-label="Claim">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.action)} · ${money(row.outstanding)} AR</small>
      </div>
      <span data-label="Owner">${escapeHtml(row.owner)}</span>
      <span data-label="Due">${pill(row.due.label, tone)}</span>
      <span data-label="Status">${escapeHtml(row.status)}</span>
      <small data-label="Guard / Priority">${escapeHtml(row.guardSummary)} · ${row.priority} ${priorityBand(row.priority)}</small>
    </button>
  `;
}

function assignmentHandoffRows(queue = [], assignable = []) {
  const activeRows = queue
    .filter((claim) => claim.assignedTo || claim.assignmentStatus === "Submitted For Review")
    .map((claim) => {
      const route = assignmentRouteModel(claim, { mode: claim.assignmentStatus === "Submitted For Review" ? "review" : "assignment" });
      const packet = assignmentWorkPacket(claim);
      const due = assignmentDueStatus(claim);
      const financial = claimFinancialView(claim);
      const status = claim.assignmentStatus || "Assigned";
      return {
        claim,
        claimId: claim.claimId,
        client: claim.client || "Unknown client",
        assignedToward: route.assignedTo,
        assignedBy: route.assignedBy,
        decisionOwner: route.decisionOwner,
        routeLane: route.routeLane,
        status,
        due,
        nextStep: assignmentHandoffNextStep(claim, due, route, packet),
        expected: packet.expected,
        paid: packet.paid,
        outstanding: packet.outstanding,
        delta: packet.delta,
        priority: packet.priority,
        guardSummary: packet.guardSummary
      };
    });
  const assignableRows = assignable.slice(0, 3).map((claim, index) => {
    const packet = assignmentWorkPacket(claim);
    const financial = claimFinancialView(claim);
    return {
      claim,
      claimId: claim.claimId,
      client: claim.client || "Unknown client",
      assignedToward: suggestedAssignmentOwner(index, queue),
      assignedBy: activeInternalUser(),
      decisionOwner: suggestedAssignmentOwner(index, queue),
      routeLane: "Assignment Intake Lane",
      status: "Ready To Assign",
      due: { key: "unassigned", label: "Needs Assignment", days: null },
      nextStep: "Assign owner, SLA due date, and required proof packet.",
      expected: packet.expected,
      paid: packet.paid,
      outstanding: financial.sourceOutstanding,
      delta: packet.delta,
      priority: packet.priority,
      guardSummary: packet.guardSummary
    };
  });
  const rows = [...activeRows, ...assignableRows].sort((a, b) => assignmentHandoffRank(b) - assignmentHandoffRank(a)
    || b.outstanding - a.outstanding
    || b.priority - a.priority
    || a.claimId.localeCompare(b.claimId));
  const visibleRows = rows.slice(0, 8);
  const totals = visibleRows.reduce((sum, row) => {
    sum.claims += 1;
    sum.ar += row.outstanding;
    if (row.status === "Submitted For Review") sum.managerReview += 1;
    if (row.status === "Bounced Back") sum.bounced += 1;
    if (row.due.key === "missingDue") sum.missingDue += 1;
    if (row.status === "Ready To Assign") sum.unassigned += 1;
    if (!sum.owners.has(row.assignedToward)) sum.owners.add(row.assignedToward);
    return sum;
  }, { claims: 0, ar: 0, managerReview: 0, bounced: 0, missingDue: 0, unassigned: 0, owners: new Set() });
  return {
    rows: visibleRows,
    totals: {
      claims: totals.claims,
      ar: moneyNumber(totals.ar),
      managerReview: totals.managerReview,
      bounced: totals.bounced,
      missingDue: totals.missingDue,
      unassigned: totals.unassigned,
      owners: totals.owners.size
    }
  };
}

function assignmentHandoffRank(row) {
  const statusRank = {
    "Bounced Back": 90,
    "Submitted For Review": 80,
    "Ready To Assign": 70,
    "In Progress": 55,
    Assigned: 45,
    Approved: 10
  };
  const dueRank = { overdue: 35, missingDue: 30, dueToday: 20, dueSoon: 10, unassigned: 15, onTrack: 0 };
  return (statusRank[row.status] || 25) + (dueRank[row.due?.key] || 0) + Math.min(row.priority || 0, 100) / 10;
}

function assignmentHandoffNextStep(claim, due, route, packet) {
  if (claim.assignmentStatus === "Submitted For Review") return "Manager approves or bounces back with the exact evidence gap.";
  if (claim.assignmentStatus === "Bounced Back") return "Assigned owner fixes the bounce-back reason and resubmits proof.";
  if (due.key === "missingDue") return "Set an assignment due date before this work leaves intake.";
  if (due.key === "overdue") return "Escalate overdue owner, refresh payer proof, and reset next follow-up.";
  if (packet.guardCount > 0) return "Resolve calculation or payer-sequence guard before review approval.";
  if (claim.assignmentStatus === "In Progress") return "Finish documented payer/client proof and submit for manager review.";
  return "Work the assigned next action, document proof, then submit for review.";
}

function renderAssignmentHandoffCommand(queue = [], assignable = []) {
  const { rows, totals } = assignmentHandoffRows(queue, assignable);
  return `
    <section class="assignment-handoff-command" aria-label="Assignment handoff command">
      <div class="assignment-handoff-head">
        <div>
          <span>Assignment Handoff Command</span>
          <strong>${formatNounCount(totals.owners, "owner")} across ${formatNounCount(totals.claims, "handoff row")}</strong>
          <p>Single view of who work is assigned toward, who makes the next decision, and what must happen before review or reassignment.</p>
        </div>
        ${pill(`${money(totals.ar)} routed AR`, totals.ar > 0 ? "blue" : "green")}
      </div>
      <div class="assignment-handoff-summary">
        <div><span>Manager Review</span><strong>${totals.managerReview}</strong><small>Submitted decision lane</small></div>
        <div><span>Bounced Back</span><strong>${totals.bounced}</strong><small>Correction owner required</small></div>
        <div><span>Missing SLA</span><strong>${totals.missingDue}</strong><small>Needs assignment due date</small></div>
        <div><span>Unassigned AR</span><strong>${totals.unassigned}</strong><small>Previewed intake rows</small></div>
      </div>
      <div class="assignment-handoff-list">
        <div class="assignment-handoff-header">
          <span>Claim / Route</span>
          <span>Assigned Toward</span>
          <span>Decision Owner</span>
          <span>Due / Status</span>
          <span>AR / Delta</span>
          <span>Next Step</span>
        </div>
        ${rows.length ? rows.map(renderAssignmentHandoffRow).join("") : '<div class="assignment-handoff-empty">No assignment handoffs are active yet.</div>'}
      </div>
    </section>
  `;
}

function renderAssignmentHandoffRow(row) {
  const dueTone = row.status === "Bounced Back" || row.due.key === "overdue" ? "red" : row.status === "Submitted For Review" ? "blue" : row.due.key === "missingDue" ? "amber" : "green";
  return `
    <button type="button" class="assignment-handoff-row" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Route">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.client)} · ${escapeHtml(row.routeLane)}</small>
      </span>
      <span data-label="Assigned Toward">
        <strong>${escapeHtml(row.assignedToward)}</strong>
        <small>By ${escapeHtml(row.assignedBy)}</small>
      </span>
      <span data-label="Decision Owner">
        <strong>${escapeHtml(row.decisionOwner)}</strong>
        <small>${escapeHtml(row.guardSummary)}</small>
      </span>
      <span data-label="Due / Status">
        ${pill(row.due.label, dueTone)}
        <small>${escapeHtml(row.status)}</small>
      </span>
      <span data-label="AR / Delta">
        <strong>${money(row.outstanding)}</strong>
        <small>Delta ${money(row.delta)}</small>
      </span>
      <small data-label="Next Step">${escapeHtml(row.nextStep)}</small>
    </button>
  `;
}

function assignmentOwnershipRows(queue) {
  const grouped = new Map();
  queue.forEach((claim) => {
    const owner = claim.assignedTo || "Unassigned";
    if (!grouped.has(owner)) grouped.set(owner, { owner, claims: 0, owed: 0, pastDue: 0, submitted: 0, bounced: 0, inProgress: 0 });
    const row = grouped.get(owner);
    row.claims += 1;
    row.owed += claimFinancialView(claim).sourceOutstanding;
    row.pastDue += isPastDue(claim) ? 1 : 0;
    row.submitted += claim.assignmentStatus === "Submitted For Review" ? 1 : 0;
    row.bounced += claim.assignmentStatus === "Bounced Back" ? 1 : 0;
    row.inProgress += claim.assignmentStatus === "In Progress" ? 1 : 0;
  });
  return Array.from(grouped.values())
    .sort((a, b) => b.pastDue - a.pastDue || b.owed - a.owed || b.claims - a.claims);
}

function renderAssignmentOwnerRow(row) {
  return `
    <div class="ownership-row">
      <div>
        <strong>${escapeHtml(row.owner)}</strong>
        <small>${formatNounCount(row.claims, "claim")} · ${money(row.owed)} open AR</small>
      </div>
      <span>${row.pastDue} overdue</span>
      <span>${row.inProgress} in progress</span>
      <span>${row.bounced} bounced</span>
    </div>
  `;
}

function renderReviewOwnershipBoard(reviews) {
  const rows = assignmentOwnershipRows(reviews);
  const totalOwed = reviews.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
  return `
    <div class="ownership-grid review-ownership-grid">
      <section class="ownership-panel">
        <div class="ops-section-heading compact">
          <strong>Review By Submitter</strong>
          <span>Who sent work back to management.</span>
        </div>
        <div class="ownership-table">
          ${rows.length ? rows.map((row) => `
            <div class="ownership-row">
              <div>
                <strong>${escapeHtml(row.owner)}</strong>
                <small>${row.claims} submitted · ${money(row.owed)} waiting review</small>
              </div>
              <span>${row.pastDue} overdue</span>
              <span>${row.claims ? Math.round((row.owed / Math.max(totalOwed, 1)) * 100) : 0}% of AR</span>
            </div>
          `).join("") : empty("No submitted work grouped by owner.")}
        </div>
      </section>
      <section class="ownership-panel review-standard-panel">
        <div class="ops-section-heading compact">
          <strong>Review Standard</strong>
          <span>Every decision must be explicit.</span>
        </div>
        <div class="review-standard-list">
          <div><span>Approve</span><strong>Payment/action proof is sufficient and next action is clear.</strong></div>
          <div><span>Bounce Back</span><strong>Reason must tell the owner exactly what to fix.</strong></div>
          <div><span>Escalate</span><strong>High-dollar, payer conflict, or missing evidence stays visible in QA.</strong></div>
        </div>
      </section>
    </div>
  `;
}

function renderReviewQualityBoard(reviews) {
  const profiles = reviews.map(reviewQualityProfile);
  const ready = profiles.filter((profile) => profile.ready).length;
  const missingEvidence = profiles.filter((profile) => !profile.hasEvidence).length;
  const guarded = profiles.filter((profile) => profile.guardCount > 0).length;
  const financialImpact = profiles.reduce((sum, profile) => sum + reviewFinancialExposure(profile), 0);
  return `
    <section class="review-quality-board">
      <div class="ops-section-heading compact">
        <strong>Review Quality Gate</strong>
        <span>Approve only when proof, next action, date, and math risk are visible.</span>
      </div>
      <div class="review-quality-metrics">
        <div><span>Ready To Approve</span><strong>${ready}/${reviews.length}</strong><small>All gate checks pass</small></div>
        <div><span>Needs Evidence</span><strong>${missingEvidence}</strong><small>Completion note lacks proof signal</small></div>
        <div><span>Financial Exposure</span><strong>${money(financialImpact)}</strong><small>Open AR or underpayment under review</small></div>
        <div><span>Guarded Claims</span><strong>${guarded}</strong><small>Calc or payer sequencing review</small></div>
      </div>
    </section>
  `;
}

function reviewEscalationReason(profile, dossier) {
  if (!profile.hasEvidence) return "Approval evidence missing";
  if (!profile.hasNextAction) return "Next action missing";
  if (!profile.hasFollowUpDate) return "Follow-up date missing";
  if (profile.guardCount > 0) return `Math guard: ${profile.guardLabel}`;
  if (reviewFinancialExposure(profile) >= 500) return "High financial exposure";
  if (!dossier.approvalReady) return "Manager evidence review";
  return "Ready to approve with note";
}

function reviewEscalationRows(reviews = []) {
  const rows = reviews.map((claim) => {
    const profile = reviewQualityProfile(claim);
    const dossier = reviewDecisionDossier(claim);
    const exposure = reviewFinancialExposure(profile);
    const reason = reviewEscalationReason(profile, dossier);
    const priority = workPriorityScore(claim);
    const needsManagerAction = !profile.ready || exposure >= 500 || priority >= 80;
    return {
      claim,
      claimId: claim.claimId,
      submittedBy: dossier.submittedBy,
      exposure,
      reason,
      priority,
      status: profile.ready ? "Ready" : "Needs Evidence",
      nextAction: dossier.nextAction,
      nextDate: dossier.nextDate,
      guardSummary: dossier.guardSummary,
      needsManagerAction
    };
  });
  const totals = rows.reduce((sum, row) => {
    sum.waiting += 1;
    sum.exposure += row.exposure;
    if (row.status === "Ready") sum.ready += 1;
    if (row.status !== "Ready") sum.needsEvidence += 1;
    if (row.guardSummary !== "Clear") sum.guarded += 1;
    if (row.exposure >= 500) sum.highExposure += 1;
    return sum;
  }, { waiting: 0, ready: 0, needsEvidence: 0, guarded: 0, highExposure: 0, exposure: 0 });
  totals.exposure = moneyNumber(totals.exposure);
  const actionRows = rows
    .filter((row) => row.needsManagerAction)
    .sort((a, b) => {
      const statusRank = { "Needs Evidence": 2, Ready: 1 };
      return (statusRank[b.status] || 0) - (statusRank[a.status] || 0)
        || b.exposure - a.exposure
        || b.priority - a.priority
        || a.claimId.localeCompare(b.claimId);
    })
    .slice(0, 6);
  return { totals, rows, actionRows };
}

function renderReviewEscalationCommand(reviews = []) {
  const { totals, actionRows } = reviewEscalationRows(reviews);
  return `
    <section class="review-escalation-command" aria-label="Manager review command">
      <div class="review-escalation-head">
        <div>
          <span>Manager Review Command</span>
          <strong>${totals.waiting} waiting review · ${money(totals.exposure)} financial exposure</strong>
          <p>Decision-first queue for approvals, bounce-backs, missing evidence, and guarded math.</p>
        </div>
        ${pill(`${totals.needsEvidence} need evidence`, totals.needsEvidence ? "amber" : "green")}
      </div>
      <div class="review-escalation-metrics">
        <div><span>Ready To Approve</span><strong>${totals.ready}</strong><small>All gate checks pass</small></div>
        <div><span>Needs Evidence</span><strong>${totals.needsEvidence}</strong><small>Missing proof, next action, date, or guard clearance</small></div>
        <div><span>Guarded</span><strong>${totals.guarded}</strong><small>Calculation or payer sequence risk</small></div>
        <div><span>High Exposure</span><strong>${totals.highExposure}</strong><small>${money(500)}+ manager review threshold</small></div>
      </div>
      <div class="review-escalation-list">
        ${actionRows.length ? actionRows.map(renderReviewEscalationRow).join("") : '<div class="review-escalation-empty">No manager escalation rows in the current review queue.</div>'}
      </div>
    </section>
  `;
}

function renderReviewEscalationRow(row) {
  const tone = row.status === "Ready" ? "green" : row.guardSummary !== "Clear" ? "red" : "amber";
  return `
    <button type="button" class="review-escalation-row" data-open="${escapeHtml(row.claimId)}">
      <div data-label="Claim">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.reason)}</small>
      </div>
      <span data-label="Submitter">${escapeHtml(row.submittedBy)}</span>
      <span data-label="Exposure">${money(row.exposure)}</span>
      <span data-label="Status">${pill(row.status, tone)}</span>
      <small data-label="Next Action">${escapeHtml(row.nextAction)} · ${escapeHtml(row.nextDate)}</small>
    </button>
  `;
}

function reviewEvidenceMatrixModel(reviews = []) {
  const rows = reviews.map((claim) => {
    const profile = reviewQualityProfile(claim);
    const dossier = reviewDecisionDossier(claim);
    const financial = claimFinancialView(claim);
    const formulaDelta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    const checks = [
      { key: "proof", label: "Proof", ok: profile.hasEvidence, detail: profile.evidenceLabel },
      { key: "nextAction", label: "Next Action", ok: profile.hasNextAction, detail: profile.nextActionLabel },
      { key: "followUpDate", label: "Follow-Up", ok: profile.hasFollowUpDate, detail: profile.followUpDateLabel },
      { key: "mathGuard", label: "Math Guard", ok: profile.guardCount === 0 && formulaDelta === 0, detail: formulaDelta !== 0 ? `Delta ${money(formulaDelta)}` : dossier.guardSummary },
      { key: "financial", label: "Financial", ok: reviewFinancialExposure(profile) > 0, detail: `${money(profile.outstanding)} AR / ${money(profile.underpayment)} underpay` }
    ];
    const failed = checks.filter((check) => !check.ok && check.key !== "financial");
    return {
      claim,
      claimId: claim.claimId,
      submittedBy: dossier.submittedBy,
      client: claim.client || "Unknown client",
      checks,
      failed,
      exposure: reviewFinancialExposure(profile),
      formulaDelta,
      managerQuestion: reviewEvidenceManagerQuestion(profile, dossier, formulaDelta),
      packageStatus: failed.length || profile.guardCount > 0 || formulaDelta !== 0 ? "Incomplete" : "Review Ready"
    };
  }).sort((a, b) => {
    const statusRank = { Incomplete: 2, "Review Ready": 1 };
    return (statusRank[b.packageStatus] || 0) - (statusRank[a.packageStatus] || 0)
      || b.exposure - a.exposure
      || Math.abs(b.formulaDelta) - Math.abs(a.formulaDelta)
      || a.claimId.localeCompare(b.claimId);
  });
  const totals = rows.reduce((sum, row) => {
    sum.rows += 1;
    sum.exposure += row.exposure;
    sum.delta += row.formulaDelta;
    if (row.packageStatus === "Review Ready") sum.ready += 1;
    if (row.failed.some((check) => check.key === "proof")) sum.missingProof += 1;
    if (row.failed.some((check) => check.key === "nextAction")) sum.missingAction += 1;
    if (row.failed.some((check) => check.key === "followUpDate")) sum.missingDate += 1;
    if (row.failed.some((check) => check.key === "mathGuard") || row.formulaDelta !== 0) sum.mathReview += 1;
    return sum;
  }, { rows: 0, ready: 0, missingProof: 0, missingAction: 0, missingDate: 0, mathReview: 0, exposure: 0, delta: 0 });
  return {
    rows: rows.slice(0, 8),
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta)
    }
  };
}

function reviewEvidenceManagerQuestion(profile = {}, dossier = {}, formulaDelta = 0) {
  if (!profile.hasEvidence) return "What proof did offshore verify: payer portal, EOB/ERA, auth, COB, or records?";
  if (!profile.hasNextAction) return "What exact next action should happen after the manager decision?";
  if (!profile.hasFollowUpDate) return "What date should drive the next payer/client follow-up?";
  if (profile.guardCount > 0) return `Can this be approved with guard still present: ${dossier.guardSummary}?`;
  if (formulaDelta !== 0) return `Why does Expected - Paid - AR show ${money(formulaDelta)}?`;
  return "Is the manager approval note specific enough to audit later?";
}

function renderReviewEvidenceMatrix(reviews = []) {
  const model = reviewEvidenceMatrixModel(reviews);
  return `
    <section class="review-evidence-matrix" aria-label="Review evidence matrix">
      <div class="review-evidence-matrix-head">
        <div>
          <span>Review Evidence Matrix</span>
          <strong>${model.totals.ready}/${formatNounCount(model.totals.rows, "package")} ready for manager decision</strong>
          <p>Spreadsheet-style proof grid for submitted work: evidence, next action, follow-up date, math guard, exposure, and the exact manager question.</p>
        </div>
        ${pill(`${model.totals.missingProof} missing proof`, model.totals.missingProof ? "amber" : "green")}
      </div>
      <div class="review-evidence-summary">
        <div><span>Missing Proof</span><strong>${model.totals.missingProof}</strong><small>Completion note lacks evidence</small></div>
        <div><span>Missing Action</span><strong>${model.totals.missingAction}</strong><small>No next workflow step</small></div>
        <div><span>Missing Date</span><strong>${model.totals.missingDate}</strong><small>No follow-up due date</small></div>
        <div><span>Math Review</span><strong>${model.totals.mathReview}</strong><small>Guard or formula delta</small></div>
        <div><span>Exposure</span><strong>${money(model.totals.exposure)}</strong><small>Open AR / underpayment</small></div>
      </div>
      <div class="review-evidence-table" role="table" aria-label="Manager review evidence matrix">
        <div class="review-evidence-row review-evidence-header" role="row">
          <span>Claim / Submitter</span>
          <span>Proof</span>
          <span>Next Action</span>
          <span>Follow-Up</span>
          <span>Math Guard</span>
          <span>Manager Question</span>
        </div>
        ${model.rows.length ? model.rows.map(renderReviewEvidenceMatrixRow).join("") : '<div class="review-evidence-empty">No submitted work is waiting for evidence review.</div>'}
      </div>
    </section>
  `;
}

function renderReviewEvidenceMatrixRow(row) {
  const checkByKey = new Map(row.checks.map((check) => [check.key, check]));
  return `
    <button type="button" class="review-evidence-row" data-open="${escapeHtml(row.claimId)}" role="row">
      <span data-label="Claim / Submitter">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.client)} · ${escapeHtml(row.submittedBy)} · ${money(row.exposure)}</small>
      </span>
      ${["proof", "nextAction", "followUpDate", "mathGuard"].map((key) => renderReviewEvidenceCell(checkByKey.get(key))).join("")}
      <span data-label="Manager Question">
        <strong>${escapeHtml(row.packageStatus)}</strong>
        <small>${escapeHtml(row.managerQuestion)}</small>
      </span>
    </button>
  `;
}

function renderReviewEvidenceCell(check = {}) {
  return `
    <span data-label="${escapeHtml(check.label || "Check")}" class="${check.ok ? "is-pass" : "is-risk"}">
      <strong>${check.ok ? "OK" : "Review"}</strong>
      <small>${escapeHtml(check.detail || "")}</small>
    </span>
  `;
}

function reviewDecisionCockpitModel(reviews = []) {
  const rows = reviews.map((claim) => {
    const profile = reviewQualityProfile(claim);
    const dossier = reviewDecisionDossier(claim);
    const financial = claimFinancialView(claim);
    const formulaDelta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    const exposure = reviewFinancialExposure(profile);
    const missing = reviewMissingDecisionRequirements(profile, dossier);
    const recommendation = reviewDecisionRecommendation(profile, dossier, exposure, formulaDelta, missing);
    return {
      claim,
      claimId: claim.claimId,
      client: claim.client || "Unknown client",
      submittedBy: dossier.submittedBy,
      reviewer: dossier.reviewedBy,
      recommendation: recommendation.label,
      tone: recommendation.tone,
      reason: recommendation.reason,
      missing,
      exposure,
      formulaDelta,
      nextAction: dossier.nextAction,
      nextDate: dossier.nextDate,
      guardSummary: dossier.guardSummary
    };
  }).sort((a, b) => reviewDecisionRank(b) - reviewDecisionRank(a)
    || b.exposure - a.exposure
    || a.claimId.localeCompare(b.claimId));
  const totals = rows.reduce((sum, row) => {
    sum.waiting += 1;
    sum.exposure += row.exposure;
    sum.delta += row.formulaDelta;
    if (row.recommendation === "Approve") sum.approve += 1;
    if (row.recommendation === "Bounce Back") sum.bounce += 1;
    if (row.recommendation === "Escalate") sum.escalate += 1;
    return sum;
  }, { waiting: 0, approve: 0, bounce: 0, escalate: 0, exposure: 0, delta: 0 });
  return {
    rows: rows.slice(0, 8),
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta)
    }
  };
}

function reviewMissingDecisionRequirements(profile = {}, dossier = {}) {
  const missing = [];
  if (!profile.hasEvidence) missing.push("evidence");
  if (!profile.hasNextAction) missing.push("next action");
  if (!profile.hasFollowUpDate) missing.push("follow-up date");
  if (profile.guardCount > 0) missing.push(dossier.guardSummary || "calc guard");
  return missing;
}

function reviewDecisionRecommendation(profile = {}, dossier = {}, exposure = 0, formulaDelta = 0, missing = []) {
  if (profile.guardCount > 0 || Math.abs(formulaDelta) > 0) {
    return { label: "Escalate", tone: "red", reason: `Resolve ${dossier.guardSummary || "formula guard"} before approval.` };
  }
  if (missing.length) {
    return { label: "Bounce Back", tone: "amber", reason: `Missing ${missing.join(", ")} from the submitted package.` };
  }
  if (exposure >= 500) {
    return { label: "Escalate", tone: "red", reason: `${money(exposure)} exposure needs manager confirmation before approval.` };
  }
  return { label: "Approve", tone: "green", reason: "Evidence, next action, follow-up date, and math checks are ready." };
}

function reviewDecisionRank(row) {
  const recommendationRank = { Escalate: 90, "Bounce Back": 70, Approve: 45 };
  return (recommendationRank[row.recommendation] || 20) + Math.min(row.exposure || 0, 1000) / 100;
}

function renderReviewDecisionCockpit(reviews = []) {
  const model = reviewDecisionCockpitModel(reviews);
  return `
    <section class="review-decision-cockpit" aria-label="Review decision cockpit">
      <div class="review-decision-cockpit-head">
        <div>
          <span>Review Decision Cockpit</span>
          <strong>${formatNounCount(model.totals.waiting, "submitted package")} sorted by decision readiness</strong>
          <p>Manager-ready triage for approve, bounce-back, and escalation decisions before opening individual claim cards.</p>
        </div>
        ${pill(formatNounCount(model.totals.escalate, "escalation"), model.totals.escalate ? "red" : "green")}
      </div>
      <div class="review-decision-cockpit-summary">
        <div><span>Approve Ready</span><strong>${model.totals.approve}</strong><small>All evidence and math checks pass</small></div>
        <div><span>Bounce Back</span><strong>${model.totals.bounce}</strong><small>Missing owner-fixable requirements</small></div>
        <div><span>Escalate</span><strong>${model.totals.escalate}</strong><small>Guarded math or high exposure</small></div>
        <div><span>Exposure</span><strong>${money(model.totals.exposure)}</strong><small>Open AR / underpayment under review</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - Paid - AR</small></div>
      </div>
      <div class="review-decision-cockpit-list">
        <div class="review-decision-cockpit-row review-decision-cockpit-header">
          <span>Claim / Submitter</span>
          <span>Decision</span>
          <span>Reason</span>
          <span>Exposure / Delta</span>
          <span>Next Action</span>
        </div>
        ${model.rows.length ? model.rows.map(renderReviewDecisionCockpitRow).join("") : '<div class="review-decision-cockpit-empty">No submitted packages are waiting for a manager decision.</div>'}
      </div>
    </section>
  `;
}

function renderReviewDecisionCockpitRow(row) {
  const missingLabel = row.missing.length ? `Missing: ${row.missing.join(", ")}` : "Complete package";
  return `
    <button type="button" class="review-decision-cockpit-row" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Submitter">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.client)} · submitted by ${escapeHtml(row.submittedBy)}</small>
      </span>
      <span data-label="Decision">${pill(row.recommendation, row.tone)}</span>
      <span data-label="Reason">
        <strong>${escapeHtml(row.reason)}</strong>
        <small>${escapeHtml(missingLabel)}</small>
      </span>
      <span data-label="Exposure / Delta">
        <strong>${money(row.exposure)}</strong>
        <small>Delta ${money(row.formulaDelta)}</small>
      </span>
      <span data-label="Next Action">
        <strong>${escapeHtml(row.nextAction)}</strong>
        <small>${escapeHtml(row.nextDate)} · reviewer ${escapeHtml(row.reviewer)}</small>
      </span>
    </button>
  `;
}

function reviewDispositionControlModel(reviews = []) {
  const cockpit = reviewDecisionCockpitModel(reviews);
  const lanes = ["Approve", "Bounce Back", "Escalate"].map((disposition) => {
    const rows = cockpit.rows.filter((row) => row.recommendation === disposition);
    const exposure = moneyNumber(rows.reduce((sum, row) => sum + row.exposure, 0));
    const delta = moneyNumber(rows.reduce((sum, row) => sum + row.formulaDelta, 0));
    const topRow = rows[0] || null;
    return {
      disposition,
      tone: disposition === "Approve" ? "green" : disposition === "Bounce Back" ? "amber" : "red",
      rows,
      count: rows.length,
      exposure,
      delta,
      command: disposition === "Approve" ? "Approve submitted work" : disposition === "Bounce Back" ? "Return work for correction" : "Manager escalation / hold",
      requiredNote: reviewDispositionRequiredNote(disposition, topRow),
      nextAction: reviewDispositionNextAction(disposition, topRow)
    };
  });
  return {
    lanes,
    totals: {
      waiting: cockpit.totals.waiting,
      exposure: cockpit.totals.exposure,
      delta: cockpit.totals.delta,
      commandReady: lanes.find((lane) => lane.disposition === "Approve")?.count || 0,
      requiresManagerText: lanes.filter((lane) => lane.count > 0).length
    }
  };
}

function reviewDispositionRequiredNote(disposition, topRow) {
  if (disposition === "Approve") return "Approval note must name accepted evidence and next follow-up action.";
  if (disposition === "Bounce Back") return `Bounce-back note must state exact fix${topRow?.missing?.length ? `: ${topRow.missing.join(", ")}` : "."}`;
  return `Escalation note must resolve ${topRow?.guardSummary && topRow.guardSummary !== "Clear" ? topRow.guardSummary : "guard, formula, or high exposure"} before closure.`;
}

function reviewDispositionNextAction(disposition, topRow) {
  if (!topRow) return "No submitted packages in this lane.";
  if (disposition === "Approve") return `Approve ${topRow.claimId}, keep ${topRow.nextAction}, and preserve audit note.`;
  if (disposition === "Bounce Back") return `Return ${topRow.claimId} to ${topRow.submittedBy} with missing evidence requirements.`;
  return `Hold ${topRow.claimId} for manager/admin review before status changes.`;
}

function renderReviewDispositionControl(reviews = []) {
  const model = reviewDispositionControlModel(reviews);
  return `
    <section class="review-disposition-control" aria-label="Review disposition control">
      <div class="review-disposition-head">
        <div>
          <span>Review Disposition Control</span>
          <strong>${formatNounCount(model.totals.waiting, "submitted package")} mapped to approve, bounce-back, or escalation</strong>
          <p>Final manager control before action: every disposition shows required note language, command path, exposure, formula delta, and next operational step.</p>
        </div>
        ${pill(`${model.totals.commandReady} approve-ready`, model.totals.commandReady ? "green" : "amber")}
      </div>
      <div class="review-disposition-summary">
        <div><span>Total Exposure</span><strong>${money(model.totals.exposure)}</strong><small>Open AR / underpayment under decision</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - Paid - AR</small></div>
        <div><span>Manager Notes</span><strong>${model.totals.requiresManagerText}</strong><small>Active disposition lanes require text</small></div>
      </div>
      <div class="review-disposition-lanes">
        ${model.lanes.map(renderReviewDispositionLane).join("")}
      </div>
    </section>
  `;
}

function renderReviewDispositionLane(lane) {
  return `
    <article class="review-disposition-lane review-disposition-lane--${escapeHtml(lane.tone)}">
      <div>
        <span>${escapeHtml(lane.disposition)}</span>
        <strong>${formatNounCount(lane.count, "package")}</strong>
      </div>
      <p>${escapeHtml(lane.requiredNote)}</p>
      <div class="review-disposition-math">
        <span><small>Exposure</small><strong>${money(lane.exposure)}</strong></span>
        <span><small>Delta</small><strong>${money(lane.delta)}</strong></span>
      </div>
      <small>${escapeHtml(lane.command)}</small>
      <em>${escapeHtml(lane.nextAction)}</em>
    </article>
  `;
}

function reviewManagerNoteBuilderModel(reviews = []) {
  const cockpit = reviewDecisionCockpitModel(reviews);
  const rows = cockpit.rows.map((row) => {
    const requiredFields = reviewManagerNoteRequiredFields(row);
    return {
      ...row,
      command: reviewManagerNoteCommand(row),
      noteTemplate: reviewManagerNoteTemplate(row),
      requiredFields,
      requiredFieldsLabel: requiredFields.join(", "),
      auditProof: `${money(row.exposure)} exposure · Delta ${money(row.formulaDelta)} · ${row.guardSummary || "Clear"}`,
      noteStatus: row.recommendation === "Approve"
        ? "Approval note"
        : row.recommendation === "Bounce Back"
          ? "Bounce-back note"
          : "Escalation note"
    };
  });
  const totals = rows.reduce((sum, row) => {
    sum.rows += 1;
    if (row.recommendation === "Approve") sum.approve += 1;
    if (row.recommendation === "Bounce Back") sum.bounce += 1;
    if (row.recommendation === "Escalate") sum.escalate += 1;
    sum.exposure += row.exposure;
    sum.delta += row.formulaDelta;
    sum.requiredFields += row.requiredFields.length;
    return sum;
  }, { rows: 0, approve: 0, bounce: 0, escalate: 0, exposure: 0, delta: 0, requiredFields: 0 });
  return {
    rows,
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta)
    }
  };
}

function reviewManagerNoteCommand(row = {}) {
  if (row.recommendation === "Approve") return "Approve submitted work";
  if (row.recommendation === "Bounce Back") return "Return work for correction";
  return "Manager escalation / hold";
}

function reviewManagerNoteRequiredFields(row = {}) {
  if (row.recommendation === "Approve") {
    return ["Accepted evidence", "Next action", "Follow-up date", "Formula proof"];
  }
  if (row.recommendation === "Bounce Back") {
    const missing = Array.isArray(row.missing) && row.missing.length ? row.missing : ["submitted evidence gap"];
    return [...missing.map((item) => item.charAt(0).toUpperCase() + item.slice(1)), "Owner fix", "Resubmission proof"];
  }
  return ["Escalation reason", "Manager owner", "Resolution before closure", "Formula proof"];
}

function reviewManagerNoteTemplate(row = {}) {
  const missing = row.missing?.length ? row.missing.join(", ") : "submitted evidence gap";
  if (row.recommendation === "Approve") {
    return `Approved ${row.claimId}: accepted evidence for ${row.nextAction}; follow-up remains ${row.nextDate}; formula delta ${money(row.formulaDelta)}; exposure ${money(row.exposure)}.`;
  }
  if (row.recommendation === "Bounce Back") {
    return `Bounce back ${row.claimId}: fix ${missing}; resubmit with payer/client proof, owner correction, next action, and follow-up date.`;
  }
  const guard = row.guardSummary && row.guardSummary !== "Clear" ? row.guardSummary : "manager/admin exposure review";
  return `Escalate ${row.claimId}: hold status change until ${guard} is resolved; exposure ${money(row.exposure)}; formula delta ${money(row.formulaDelta)}.`;
}

function renderReviewManagerNoteBuilder(reviews = []) {
  const model = reviewManagerNoteBuilderModel(reviews);
  return `
    <section class="review-note-builder" aria-label="Manager review note builder">
      <div class="review-note-builder-head">
        <div>
          <span>Manager Note Builder</span>
          <strong>${model.totals.rows} decision note template${model.totals.rows === 1 ? "" : "s"} tied to review disposition</strong>
          <p>Prevents vague approvals and vague bounce-backs: each submitted package gets required note text, command path, exact owner action, exposure, and formula delta.</p>
          <small>Available decisions: Approve · Bounce Back · Manager escalation / hold</small>
        </div>
        ${pill(formatNounCount(model.totals.bounce, "bounce template"), model.totals.bounce ? "amber" : "green")}
      </div>
      <div class="review-note-builder-summary">
        <div><span>Approve Notes</span><strong>${model.totals.approve}</strong><small>Ready evidence accepted</small></div>
        <div><span>Bounce Notes</span><strong>${model.totals.bounce}</strong><small>Exact offshore fix required</small></div>
        <div><span>Escalation Notes</span><strong>${model.totals.escalate}</strong><small>Manager hold language</small></div>
        <div><span>Required Fields</span><strong>${model.totals.requiredFields}</strong><small>Fields managers must name</small></div>
        <div><span>Exposure</span><strong>${money(model.totals.exposure)}</strong><small>Under note control</small></div>
      </div>
      <div class="review-note-builder-list">
        <div class="review-note-builder-row review-note-builder-header">
          <span>Claim / Decision</span>
          <span>Required Fields</span>
          <span>Draft Manager Note</span>
          <span>Command Path</span>
          <span>Audit Proof</span>
        </div>
        ${model.rows.length ? model.rows.map(renderReviewManagerNoteRow).join("") : '<div class="review-note-builder-empty">No submitted work needs manager note templates.</div>'}
      </div>
    </section>
  `;
}

function renderReviewManagerNoteRow(row) {
  return `
    <button type="button" class="review-note-builder-row" data-open="${escapeHtml(row.claimId)}">
      <span data-label="Claim / Decision">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${pill(row.noteStatus, row.tone)} ${escapeHtml(row.submittedBy)}</small>
      </span>
      <span data-label="Required Fields">
        <strong>${escapeHtml(row.requiredFieldsLabel)}</strong>
        <small>${escapeHtml(row.reason)}</small>
      </span>
      <span data-label="Draft Manager Note">
        <strong>${escapeHtml(row.noteTemplate)}</strong>
      </span>
      <span data-label="Command Path">
        <strong>${escapeHtml(row.command)}</strong>
        <small>${escapeHtml(row.nextAction)} · ${escapeHtml(row.nextDate)}</small>
      </span>
      <span data-label="Audit Proof">
        <strong>${escapeHtml(row.auditProof)}</strong>
        <small>Expected - Paid - AR</small>
      </span>
    </button>
  `;
}

function reviewFinancialExposure(profile) {
  return moneyNumber(Math.max(profile?.outstanding || 0, profile?.underpayment || 0));
}

function renderReviewQualityChecklist(claim) {
  const profile = reviewQualityProfile(claim);
  const checks = [
    ["Evidence", profile.hasEvidence, profile.evidenceLabel],
    ["Next Action", profile.hasNextAction, profile.nextActionLabel],
    ["Follow-Up Date", profile.hasFollowUpDate, profile.followUpDateLabel],
    ["Financial Impact", profile.hasFinancialImpact, `${money(profile.outstanding)} AR / ${money(profile.underpayment)} underpay`],
    ["Calc Guards", profile.guardCount === 0, profile.guardLabel]
  ];
  return `
    <div class="review-quality-checklist">
      ${checks.map(([label, ok, detail]) => `
        <div class="${ok ? "is-pass" : "is-risk"}">
          <span>${escapeHtml(label)}</span>
          <strong>${ok ? "OK" : "Review"}</strong>
          <small>${escapeHtml(detail)}</small>
        </div>
      `).join("")}
    </div>
  `;
}

function reviewDecisionDossier(claim) {
  const financial = claimFinancialView(claim);
  const profile = reviewQualityProfile(claim);
  const guards = evaluateClaimCalculationGuards(claim);
  const financialImpact = moneyNumber(Math.max(profile.outstanding, profile.underpayment));
  const approvalRequirements = [
    profile.hasEvidence ? "Evidence signal present" : "Approval evidence required",
    profile.hasNextAction ? "Next action selected" : "Next action required",
    profile.hasFollowUpDate ? "Follow-up date set" : "Follow-up date required",
    guards.length ? `Guard review required: ${calculationGuardSummary(guards)}` : "Calculation guard clear"
  ];
  return {
    submittedBy: claim.assignedTo || "Offshore",
    reviewedBy: activeInternalUser(),
    approvalReady: profile.ready,
    evidenceStatus: profile.hasEvidence ? "Evidence Ready" : "Approval Evidence Required",
    bounceBackStatus: "Bounce-Back Fix Required",
    noSilentCloseStatus: "No Silent Close",
    financialImpact,
    expectedReimbursement: financial.expectedReimbursement,
    insurancePaid: financial.totalInsurancePaid,
    openAr: financial.sourceOutstanding,
    underpayment: financial.underpayment,
    guardSummary: guards.length ? calculationGuardSummary(guards) : "Clear",
    nextAction: claim.action || "No next action selected",
    nextDate: claim.followUpDate ? formatDate(claim.followUpDate) : "No follow-up date",
    approvalRequirements
  };
}

function renderReviewDecisionDossier(claim) {
  const dossier = reviewDecisionDossier(claim);
  return `
    <section class="review-decision-dossier" aria-label="Review decision dossier for ${escapeHtml(claim.claimId)}">
      <div class="review-decision-head">
        <div>
          <span>Review Decision Dossier</span>
          <strong>${escapeHtml(dossier.approvalReady ? "Approval-ready package" : "Manager evidence review required")}</strong>
        </div>
        ${pill(dossier.approvalReady ? "Evidence Ready" : "Needs Manager Review", dossier.approvalReady ? "green" : "amber")}
      </div>
      <div class="review-decision-grid">
        <div class="review-decision-card">
          <span>Financial Impact</span>
          <strong>${money(dossier.financialImpact)}</strong>
          <small>${money(dossier.openAr)} AR · ${money(dossier.underpayment)} underpayment</small>
        </div>
        <div class="review-decision-card">
          <span>Math Guard</span>
          <strong>${escapeHtml(dossier.guardSummary)}</strong>
          <small>${money(dossier.expectedReimbursement)} expected - ${money(dossier.insurancePaid)} paid</small>
        </div>
        <div class="review-decision-card">
          <span>Next Action</span>
          <strong>${escapeHtml(dossier.nextAction)}</strong>
          <small>${escapeHtml(dossier.nextDate)}</small>
        </div>
      </div>
      <div class="review-decision-paths">
        <div>
          <span>${escapeHtml(dossier.evidenceStatus)}</span>
          <strong>Approve only with accepted proof in the manager note.</strong>
          <small>${escapeHtml(dossier.approvalRequirements.join(" · "))}</small>
        </div>
        <div>
          <span>${escapeHtml(dossier.bounceBackStatus)}</span>
          <strong>Bounce back only with the exact missing item or correction.</strong>
          <small>Owner remains ${escapeHtml(dossier.submittedBy)} until the corrected package is resubmitted.</small>
        </div>
        <div>
          <span>${escapeHtml(dossier.noSilentCloseStatus)}</span>
          <strong>No EZClaim write-back and no hidden close from this screen.</strong>
          <small>Reviewer: ${escapeHtml(dossier.reviewedBy)} · decision must leave an audit note.</small>
        </div>
      </div>
    </section>
  `;
}

function reviewDecisionLedgerRows(claims) {
  return claims
    // Wave 3c: bounceReason persists through resubmission, so a resubmitted
    // claim (Submitted For Review with a kept bounceReason) stays on the
    // ledger with its previous bounce visible to the re-reviewing manager.
    .filter((claim) => ["Approved", "Bounced Back"].includes(claim.assignmentStatus)
      || (claim.assignmentStatus === "Submitted For Review" && String(claim.bounceReason || "").trim()))
    .map((claim) => {
      const financial = claimFinancialView(claim);
      const latestNote = (claim.notes || []).at(-1);
      const formulaDelta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
      return {
        claimId: claim.claimId,
        status: claim.assignmentStatus,
        owner: claim.assignmentStatus === "Bounced Back" ? (claim.assignedTo || "Offshore") : "Manager / Admin",
        submittedBy: claim.assignedTo || "Offshore",
        expected: financial.expectedReimbursement,
        paid: financial.totalInsurancePaid,
        outstanding: financial.sourceOutstanding,
        formulaDelta,
        financialImpact: moneyNumber(Math.max(financial.sourceOutstanding, financial.underpayment)),
        nextAction: claim.action || "No next action",
        nextDate: claim.followUpDate ? formatDate(claim.followUpDate) : "No next follow-up date",
        decisionNote: (claim.assignmentStatus === "Bounced Back"
          ? claimBounceReason(claim)
          : claim.assignmentStatus === "Submitted For Review"
            ? `Previous bounce: ${String(claim.bounceReason || "").trim()}${claim.bounceBy ? ` — ${claim.bounceBy}` : ""}`
            : claim.reviewNote) || latestNote?.text || "No decision note recorded.",
        auditBy: latestNote?.by || activeInternalUser(),
        auditAt: latestNote?.at || "Local preview"
      };
    })
    .sort((a, b) => b.financialImpact - a.financialImpact || a.claimId.localeCompare(b.claimId))
    .slice(0, 8);
}

function reviewOutcomeAccountabilityModel(claims = []) {
  const rows = reviewDecisionLedgerRows(claims).map((row) => {
    const isBounce = row.status === "Bounced Back";
    // Wave 3c: a resubmitted claim (Submitted For Review with a persisted
    // bounceReason) stays on the ledger awaiting the manager's re-review.
    const isResubmit = row.status === "Submitted For Review";
    const noteMissing = !row.decisionNote || row.decisionNote === "No decision note recorded.";
    return {
      ...row,
      routeLane: isBounce ? "Correction Owner Lane" : isResubmit ? "Resubmission Review Lane" : "Manager Approved Lane",
      commandPath: isBounce ? "Return work for correction" : isResubmit ? "Resubmit corrected work" : "Approve submitted work",
      noteQuality: noteMissing ? "Missing Decision Note" : row.decisionNote.length < 24 ? "Thin Decision Note" : "Auditable Decision Note",
      requiredNextStep: isBounce
        ? `Owner must fix and resubmit ${row.claimId} with proof.`
        : isResubmit
          ? `Re-review ${row.claimId} against the previous bounce reason.`
          : `Keep ${row.nextAction} active and preserve manager approval proof.`,
      ownerLabel: isBounce ? row.owner : "Manager / Admin",
      tone: isBounce ? "amber" : isResubmit ? "amber" : "green"
    };
  });
  const totals = rows.reduce((sum, row) => {
    sum.decisions += 1;
    sum.exposure += row.financialImpact;
    sum.delta += row.formulaDelta;
    if (row.status === "Approved") sum.approved += 1;
    if (row.status === "Bounced Back") sum.bounced += 1;
    if (row.noteQuality !== "Auditable Decision Note") sum.noteRisk += 1;
    if (row.status === "Bounced Back") sum.correctionOwners.add(row.ownerLabel);
    return sum;
  }, { decisions: 0, approved: 0, bounced: 0, noteRisk: 0, exposure: 0, delta: 0, correctionOwners: new Set() });
  return {
    rows,
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta),
      correctionOwners: totals.correctionOwners.size
    }
  };
}

function renderReviewOutcomeAccountability(claims = []) {
  const model = reviewOutcomeAccountabilityModel(claims);
  return `
    <section class="review-outcome-accountability" aria-label="Review outcome accountability">
      <div class="review-outcome-head">
        <div>
          <span>Review Outcome Accountability</span>
          <strong>${model.totals.decisions} completed review decision${model.totals.decisions === 1 ? "" : "s"} with owner, command, math, and next-step proof</strong>
          <p>Post-decision control so approved work and bounced-back corrections stay auditable after they leave the waiting-review queue.</p>
        </div>
        ${pill(`${model.totals.bounced} correction owner${model.totals.bounced === 1 ? "" : "s"}`, model.totals.bounced ? "amber" : "green")}
      </div>
      <div class="review-outcome-summary">
        <div><span>Approved</span><strong>${model.totals.approved}</strong><small>Manager-owned outcomes</small></div>
        <div><span>Bounced Back</span><strong>${model.totals.bounced}</strong><small>${model.totals.correctionOwners} correction owner${model.totals.correctionOwners === 1 ? "" : "s"}</small></div>
        <div><span>Decision Exposure</span><strong>${money(model.totals.exposure)}</strong><small>Open AR / underpay under outcomes</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - Paid - AR</small></div>
        <div><span>Note Risk</span><strong>${model.totals.noteRisk}</strong><small>Missing or thin decision notes</small></div>
      </div>
      <div class="review-outcome-list">
        <div class="review-outcome-row review-outcome-header">
          <span>Claim / Decision</span>
          <span>Outcome Owner</span>
          <span>Financial Proof</span>
          <span>Command / Audit</span>
          <span>Required Next Step</span>
        </div>
        ${model.rows.length ? model.rows.map(renderReviewOutcomeAccountabilityRow).join("") : '<div class="review-outcome-empty">No completed review outcomes yet.</div>'}
      </div>
    </section>
  `;
}

function renderReviewOutcomeAccountabilityRow(row) {
  return `
    <div class="review-outcome-row">
      <span data-label="Claim / Decision">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${pill(row.status, row.tone)} ${escapeHtml(row.routeLane)}</small>
      </span>
      <span data-label="Outcome Owner">
        <strong>${escapeHtml(row.ownerLabel)}</strong>
        <small>Submitted by ${escapeHtml(row.submittedBy)}</small>
      </span>
      <span data-label="Financial Proof">
        <strong>${money(row.financialImpact)}</strong>
        <small>Expected ${money(row.expected)} · Paid ${money(row.paid)} · AR ${money(row.outstanding)} · Delta ${money(row.formulaDelta)}</small>
      </span>
      <span data-label="Command / Audit">
        <strong>${escapeHtml(row.commandPath)}</strong>
        <small>${escapeHtml(row.auditBy)} · ${escapeHtml(row.auditAt)} · ${escapeHtml(row.noteQuality)}</small>
      </span>
      <span data-label="Required Next Step">
        <strong>${escapeHtml(row.requiredNextStep)}</strong>
        <small>${escapeHtml(row.decisionNote)}</small>
      </span>
    </div>
  `;
}

function reviewAuditTimelineModel(claims = []) {
  const submittedRows = claims
    .filter((claim) => claim.assignmentStatus === "Submitted For Review")
    .map((claim) => reviewAuditTimelineRowFromClaim(claim, "Submitted"));
  // Wave 3c: resubmitted claims (Submitted For Review with a kept
  // bounceReason) already appear via their own submitted-lane rows above --
  // mapping them here too would duplicate the claim and fabricate a
  // "Bounced Back" stage.
  const completedRows = reviewDecisionLedgerRows(claims).filter((row) => row.status !== "Submitted For Review").map((row) => ({
    claimId: row.claimId,
    stage: row.status === "Approved" ? "Approved" : "Bounced Back",
    lane: row.status === "Approved" ? "Manager Approved Lane" : "Correction Owner Lane",
    owner: row.status === "Approved" ? "Manager / Admin" : row.owner,
    reviewer: "Manager / Admin",
    submittedBy: row.submittedBy,
    commandPath: row.status === "Approved" ? "Approve submitted work" : "Return work for correction",
    expected: row.expected,
    paid: row.paid,
    outstanding: row.outstanding,
    formulaDelta: row.formulaDelta,
    financialImpact: row.financialImpact,
    noteQuality: !row.decisionNote || row.decisionNote === "No decision note recorded."
      ? "Missing Decision Note"
      : row.decisionNote.length < 24 ? "Thin Decision Note" : "Auditable Decision Note",
    auditBy: row.auditBy,
    auditAt: row.auditAt,
    nextStep: row.status === "Approved"
      ? `Keep ${row.nextAction} active and preserve approval evidence.`
      : `Return ${row.claimId} to ${row.owner} for correction and resubmission.`,
    note: row.decisionNote,
    rank: row.status === "Bounced Back" ? 85 : 70
  }));
  const rows = [...submittedRows, ...completedRows]
    .sort((a, b) => b.rank - a.rank || b.financialImpact - a.financialImpact || a.claimId.localeCompare(b.claimId))
    .slice(0, 10);
  const totals = rows.reduce((sum, row) => {
    sum.events += 1;
    sum.exposure += row.financialImpact;
    sum.delta += row.formulaDelta;
    if (row.stage === "Submitted") sum.submitted += 1;
    if (row.stage === "Approved") sum.approved += 1;
    if (row.stage === "Bounced Back") sum.bounced += 1;
    if (row.noteQuality !== "Auditable Decision Note") sum.noteRisk += 1;
    return sum;
  }, { events: 0, submitted: 0, approved: 0, bounced: 0, noteRisk: 0, exposure: 0, delta: 0 });
  return {
    rows,
    totals: {
      ...totals,
      exposure: moneyNumber(totals.exposure),
      delta: moneyNumber(totals.delta)
    }
  };
}

function reviewAuditTimelineRowFromClaim(claim, stage = "Submitted") {
  const financial = claimFinancialView(claim);
  const profile = reviewQualityProfile(claim);
  const formulaDelta = moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
  const note = claim.reviewNote || "No submitted review note recorded.";
  const noteQuality = !claim.reviewNote
    ? "Missing Review Note"
    : claim.reviewNote.length < 24 ? "Thin Review Note" : "Auditable Review Note";
  return {
    claimId: claim.claimId,
    stage,
    lane: "Manager Review Lane",
    owner: claim.assignedTo || "Offshore",
    reviewer: "Manager / Admin",
    submittedBy: claim.assignedTo || "Offshore",
    commandPath: "Submit for review",
    expected: financial.expectedReimbursement,
    paid: financial.totalInsurancePaid,
    outstanding: financial.sourceOutstanding,
    formulaDelta,
    financialImpact: reviewFinancialExposure(profile),
    noteQuality,
    auditBy: claim.assignedTo || "Offshore",
    auditAt: "Pending manager decision",
    nextStep: "Manager approves, bounces back, or escalates with exact evidence language.",
    note,
    rank: 95
  };
}

function renderReviewAuditTimeline(claims = []) {
  const model = reviewAuditTimelineModel(claims);
  return `
    <section class="review-audit-timeline" aria-label="Review audit timeline">
      <div class="review-audit-head">
        <div>
          <span>Review Audit Timeline</span>
          <strong>Submitted, approved, and bounced review events with owner transfer and math proof</strong>
          <p>One timeline for manager review handoff, decision command path, audit-note quality, financial proof, formula delta, and required next action.</p>
        </div>
        ${pill(`${model.totals.noteRisk} note risk`, model.totals.noteRisk ? "amber" : "green")}
      </div>
      <div class="review-audit-summary">
        <div><span>Timeline Events</span><strong>${model.totals.events}</strong><small>${money(model.totals.exposure)} exposure</small></div>
        <div><span>Submitted</span><strong>${model.totals.submitted}</strong><small>Waiting manager decision</small></div>
        <div><span>Approved</span><strong>${model.totals.approved}</strong><small>Manager-owned outcomes</small></div>
        <div><span>Bounced Back</span><strong>${model.totals.bounced}</strong><small>Correction owner transfers</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.delta)}</strong><small>Expected - Paid - AR</small></div>
        <div><span>Note Risk</span><strong>${model.totals.noteRisk}</strong><small>Thin or missing audit note</small></div>
      </div>
      <div class="review-audit-list">
        <div class="review-audit-row review-audit-header">
          <span>Claim / Stage</span>
          <span>Owner Transfer</span>
          <span>Financial Proof</span>
          <span>Command / Audit</span>
          <span>Required Next Step</span>
        </div>
        ${model.rows.length ? model.rows.map(renderReviewAuditTimelineRow).join("") : '<div class="review-audit-empty">No review audit events yet.</div>'}
      </div>
    </section>
  `;
}

function renderReviewAuditTimelineRow(row) {
  const tone = row.stage === "Bounced Back" ? "amber" : row.stage === "Approved" ? "green" : "blue";
  return `
    <div class="review-audit-row">
      <span data-label="Claim / Stage"><strong>${escapeHtml(row.claimId)}</strong><small>${pill(row.stage, tone)} ${escapeHtml(row.lane)}</small></span>
      <span data-label="Owner Transfer"><strong>${escapeHtml(row.owner)} → ${escapeHtml(row.reviewer)}</strong><small>Submitted by ${escapeHtml(row.submittedBy)}</small></span>
      <span data-label="Financial Proof"><strong>${money(row.financialImpact)}</strong><small>Expected ${money(row.expected)} · Paid ${money(row.paid)} · AR ${money(row.outstanding)} · Delta ${money(row.formulaDelta)}</small></span>
      <span data-label="Command / Audit"><strong>${escapeHtml(row.commandPath)}</strong><small>${escapeHtml(row.auditBy)} · ${escapeHtml(row.auditAt)} · ${escapeHtml(row.noteQuality)}</small></span>
      <span data-label="Required Next Step"><strong>${escapeHtml(row.nextStep)}</strong><small>${escapeHtml(row.note)}</small></span>
    </div>
  `;
}

function renderReviewDecisionLedger(claims) {
  const rows = reviewDecisionLedgerRows(claims);
  const approved = rows.filter((row) => row.status === "Approved").length;
  const bounced = rows.filter((row) => row.status === "Bounced Back").length;
  // Wave 3c: resubmitted claims (Submitted For Review with a kept bounceReason)
  // stay on the ledger awaiting the manager's re-review — count them honestly
  // in their own chip instead of leaving them uncounted.
  const awaitingReReview = rows.filter((row) => row.status === "Submitted For Review").length;
  return `
    <section class="review-decision-ledger">
      <div class="ops-section-heading compact">
        <strong>Recent Review Decisions</strong>
        <span>Approved, bounced, and resubmitted work stays visible after manager action.</span>
      </div>
      <div class="review-decision-ledger-summary">
        <div><span>Approved</span><strong>${approved}</strong><small>Visible recent decisions</small></div>
        <div><span>Bounced Back</span><strong>${bounced}</strong><small>Returned to assigned owner</small></div>
        <div><span>Awaiting re-review</span><strong>${awaitingReReview}</strong><small>Resubmitted after bounce</small></div>
        <div><span>Decision AR</span><strong>${money(rows.reduce((sum, row) => sum + row.financialImpact, 0))}</strong><small>Open AR / underpay under decisions</small></div>
      </div>
      <div class="review-decision-ledger-list">
        ${rows.length ? rows.map((row) => `
          <div class="review-decision-ledger-row">
            <div>
              <strong>${escapeHtml(row.claimId)}</strong>
              <small>${escapeHtml(row.status)} · ${escapeHtml(row.nextAction)} · ${escapeHtml(row.nextDate)}</small>
            </div>
            <span>${money(row.financialImpact)}</span>
            <span>${escapeHtml(row.owner)}</span>
            <p>${escapeHtml(row.decisionNote)}</p>
          </div>
        `).join("") : empty("No approved or bounced review decisions yet.")}
      </div>
    </section>
  `;
}

function reviewQualityProfile(claim) {
  const note = String(claim.reviewNote || "");
  const financial = claimFinancialView(claim);
  const guards = evaluateClaimCalculationGuards(claim);
  const hasEvidence = /proof|screenshot|portal|payer|eob|era|called|confirmed|attached|processed|appeal|authorization|auth|document|record/i.test(note);
  const hasNextAction = /next action:/i.test(note) || Boolean(claim.action);
  const hasFollowUpDate = Boolean(claim.followUpDate);
  const outstanding = financial.sourceOutstanding;
  const underpayment = financial.underpayment;
  const hasFinancialImpact = outstanding > 0 || underpayment > 0;
  return {
    ready: hasEvidence && hasNextAction && hasFollowUpDate && guards.length === 0,
    hasEvidence,
    hasNextAction,
    hasFollowUpDate,
    hasFinancialImpact,
    outstanding,
    underpayment,
    guardCount: guards.length,
    evidenceLabel: hasEvidence ? "Proof signal present" : "Missing proof signal",
    nextActionLabel: claim.action || "No next action",
    followUpDateLabel: claim.followUpDate ? formatDate(claim.followUpDate) : "No follow-up date",
    guardLabel: guards.length ? calculationGuardSummary(guards) : "Clear"
  };
}

function suggestedAssignmentOwner(index, queue) {
  const teams = users.filter((user) => user.role === "OffshoreBiller").map((user) => user.name);
  if (!teams.length) return "Offshore";
  const load = new Map(teams.map((team) => [team, queue.filter((claim) => claim.assignedTo === team).length]));
  const sorted = teams.slice().sort((a, b) => (load.get(a) || 0) - (load.get(b) || 0) || a.localeCompare(b));
  return sorted[index % sorted.length];
}

function renderAssignmentCandidateCard(claim, suggestedOwner = "Offshore") {
  const financial = claimFinancialView(claim);
  const score = workPriorityScore(claim);
  return `
    <article class="assignment-candidate-card">
      <div class="candidate-topline">
        ${hasPermission(PERMISSIONS.ASSIGN_CLAIMS) ? `<input type="checkbox" class="candidate-check" data-intake-select="${escapeHtml(claim.claimId)}"${assignmentIntakeSelection.has(claim.claimId) ? " checked" : ""} aria-label="Select ${escapeHtml(claim.claimId)} for bulk assign">` : ""}
        <strong>${escapeHtml(claim.claimId)}</strong>
        ${pill(`${score} ${priorityBand(score)}`, score >= 80 ? "red" : "amber")}
      </div>
      <p>${escapeHtml(claim.client)} · ${escapeHtml(claim.primaryPayer || "Unknown payer")}</p>
      <dl>
        <div><dt>Open AR</dt><dd>${money(financial.sourceOutstanding)}</dd></div>
        <div><dt>Aging</dt><dd>${escapeHtml(claim.aging || "Unknown")}</dd></div>
        <div><dt>Due</dt><dd>${claim.followUpDate ? formatDate(claim.followUpDate) : "None"}</dd></div>
        <div><dt>Assign Toward</dt><dd>${escapeHtml(suggestedOwner)}</dd></div>
      </dl>
      <small>${escapeHtml(workReason(claim))}</small>
      <div class="candidate-actions">
        ${hasPermission(PERMISSIONS.ASSIGN_CLAIMS) ? `
        <select class="candidate-assign-select" data-assign-select="${escapeHtml(claim.claimId)}" aria-label="Assign owner for ${escapeHtml(claim.claimId)}">
          ${assignableOffshoreBillers().map((u) => `<option value="${escapeHtml(u.name)}"${u.name === suggestedOwner ? " selected" : ""}>${escapeHtml(u.name)}</option>`).join("")}
        </select>
        <input type="date" class="candidate-assign-due" data-assign-due="${escapeHtml(claim.claimId)}" value="${escapeHtml(todayDateString())}" aria-label="Assignment due date for ${escapeHtml(claim.claimId)}">
        <input type="text" class="candidate-assign-instruction" data-assign-instruction="${escapeHtml(claim.claimId)}" placeholder="Instructions for the biller (optional)" aria-label="Instructions for ${escapeHtml(claim.claimId)}">
        <button class="small-button primary" data-assign="${escapeHtml(claim.claimId)}" type="button">Assign</button>` : ""}
        <button class="small-button" data-open="${escapeHtml(claim.claimId)}" data-org-id="${escapeHtml(claim.orgId || "")}" type="button">Open claim</button>
      </div>
    </article>
  `;
}

function renderQaWatchlistCard(claim) {
  const financial = claimFinancialView(claim);
  const score = workPriorityScore(claim);
  return `
    <article class="qa-watch-card">
      <div>
        <strong>${escapeHtml(claim.claimId)} · ${escapeHtml(claim.primaryPayer || "Unknown payer")}</strong>
        <p>${escapeHtml(workReason(claim))}</p>
      </div>
      <span>${money(financial.sourceOutstanding)}</span>
      ${pill(`${score} ${priorityBand(score)}`, score >= 80 ? "red" : "amber")}
      <button class="small-button" data-open="${escapeHtml(claim.claimId)}" data-org-id="${escapeHtml(claim.orgId || "")}" type="button">Review context</button>
    </article>
  `;
}

// PER USER DIRECTION: the Client view is now ONLY the legacy workbook-style monthly
// pivot — every other client-side panel is removed. Two presentations:
// Excel (the pivot table) and Power BI (the same data as a stacked
// chart). All 9 slicers from the legacy workbook are available.
function renderClientView() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("reports")) return;
  try {
    renderClientReportsView();
    return;
  } catch (e) {
    console.error("[tms.portal] client reports render failed:", e);
    const bodyHost = document.getElementById("clientPivotBody");
    if (bodyHost) {
      bodyHost.innerHTML = `
        <section class="notice-card">
          <strong>Client reports could not render</strong>
          <span>The report workspace hit a local rendering error. No source data was changed.</span>
        </section>
      `;
    }
  }
}

const CLIENT_REPORT_LIBRARY = [
  {
    key: "payments",
    title: "Incoming Payments",
    category: "Client Tools",
    accent: "green",
    summary: "Every payment as it lands, with the disbursement report behind it."
  },
  {
    key: "receivables",
    title: "Receivables",
    category: "Financial Reports",
    accent: "blue",
    summary: "Insurance AR and the amount left after insurance by aging bucket, drillable to the claims behind each balance."
  },
  {
    key: "rcm-performance",
    title: "RCM Performance",
    category: "Financial Reports",
    accent: "purple",
    summary: "Collection-rate performance across payers and clients, from charges to net receivables."
  }
];

// Confirmed OON rate cuts on the client's own kids, at the top of Reports. Both
// client Reports paths call this: renderReports (Client, who holds view:reports)
// and renderClientReportsView. Renders nothing for staff or when there are none.
function renderClientOonRateCutsCard() {
  const host = document.getElementById("clientOonLearnedRates");
  if (!host) return;
  if (isClientRole() && hasPermission(PERMISSIONS.VIEW_FINANCIALS)) renderOonLearnedRatesPanel(host, "client");
  else { host.hidden = true; host.innerHTML = ""; }
}

function renderClientReportsView() {
  if (typeof syncReportReadContext === "function") { syncReportReadContext(); renderReportReadStatus(); }
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("reports")) return;
  renderClientOonRateCutsCard();
  const catalogHost = document.getElementById("clientPivotSlicers");
  const bodyHost = document.getElementById("clientPivotBody");
  const paymentsHost = document.getElementById("clientPaymentsLedger");
  if (!catalogHost || !bodyHost) return;
  const activeReport = clientActiveReport();
  // Only a client-only session has a landing state: an internal session always
  // arrives already holding a report key from the family switcher.
  if (!activeReport) {
    catalogHost.hidden = false;
    catalogHost.innerHTML = renderClientReportCatalog("");
    bodyHost.innerHTML = renderClientReportLibraryPrompt();
    if (paymentsHost) paymentsHost.innerHTML = "";
    bindClientReportControls();
    return;
  }
  // Emptied AND hidden. A zero-height #clientPivotSlicers still occupies a grid
  // row, and between its own 14px margin and the host row-gap that was 34px of
  // nothing between the report sub-tabs and the report - measured 69px of dead
  // space against a 16px baseline on the dataset-backed reports beside it.
  catalogHost.hidden = true;
  catalogHost.innerHTML = "";
  if (activeReport.key === "payments") {
    clientPaymentsLastModel = clientPaymentsChecksModel(trackerApiEnabled() ? [] : getClaims());
    bodyHost.innerHTML = renderClientPaymentsWorkspace(clientPaymentsLastModel);
    if (paymentsHost) paymentsHost.innerHTML = "";
    bindClientPaymentsControls();
    bindClientReportControls();
    return;
  }
  if (trackerApiEnabled()) {
    const filters = clientReportFilterValues();
    requestClientReportAggregate(activeReport.key, filters);
    const aggregate = clientReportAggregateApiState;
    if (aggregate.status !== "ok" || !aggregate.response) {
      bodyHost.innerHTML = aggregate.status === "error"
        ? `<section class="notice-card"><strong>Complete report unavailable</strong><span role="alert">${escapeHtml(aggregate.error || "The report could not be completed.")}</span><button type="button" class="small-button" id="clientReportAggregateRetry">Retry</button></section>`
        : reportLoadingHtml(activeReport.title || "report", aggregate.slow);
      if (paymentsHost) paymentsHost.innerHTML = "";
      bindClientReportControls();
      const retry = document.getElementById("clientReportAggregateRetry");
      if (retry) retry.addEventListener("click", () => { clientReportAggregateApiState = { key: "", status: "idle", response: null, error: "" }; renderClientReportsView(); });
      return;
    }
    const response = aggregate.response;
    if (clientReportServerDrillState.status === "pending") {
      bodyHost.innerHTML = reportLoadingHtml("service lines", clientReportServerDrillState.slow);
      bindClientReportControls();
      return;
    }
    if (clientReportServerDrillState.status === "error") {
      bodyHost.innerHTML = '<section class="notice-card"><strong>Service lines unavailable</strong><p role="alert">' + escapeHtml(clientReportServerDrillState.error) + '</p><button type="button" class="small-button" data-client-server-drill-retry>Retry</button><button type="button" class="small-button" data-client-server-drill-back>Back to report</button></section>';
      bindClientReportControls();
      return;
    }
    if (clientReportServerDrillState.status === "ok" && clientReportServerDrillState.response) {
      bodyHost.innerHTML = renderClientReportServerDrill(clientReportServerDrillState.response);
      bindClientReportControls();
      return;
    }
    const expanded = clientReportExpandedSet(activeReport.key);
    const model = { ...response, serverBacked: true, rows: (response.rows || []).map((row) => ({ ...row, expanded: expanded.has(row.name) })) };
    bodyHost.innerHTML = activeReport.key === "receivables" ? renderClientReceivablesReportWorkspace(model) : renderClientRcmReportWorkspace(model);
    bodyHost.insertAdjacentHTML("beforeend", renderClientReportServerPager(response.page));
    if (paymentsHost) paymentsHost.innerHTML = "";
    bindClientReportControls();
    return;
  }
  const accessibleClaims = clientReportSourceClaims();
  const claimsLoad = clientReportClaimsLoadState();
  if (claimsLoad !== "ok") {
    bodyHost.innerHTML = renderClientReportClaimsNotice(claimsLoad);
    if (paymentsHost) paymentsHost.innerHTML = "";
    bindClientReportControls();
    const retry = document.getElementById("clientReportClaimsRetry");
    if (retry) retry.addEventListener("click", () => {
      clientReportClaimsApiState = { key: "", status: "idle", rows: [], totalRows: 0, truncated: false, error: "", retried: false };
      renderClientReportsView();
    });
    return;
  }
  const filters = normalizeClientReportFilters(clientReportFilterValues(), accessibleClaims);
  const claims = clientReportFilteredClaims(accessibleClaims, filters);
  if (clientReportDrilldownState.active && clientReportDrilldownState.reportKey !== activeReport.key) {
    clientReportDrilldownState.active = false;
    clientReportDrilldownState.claimId = null;
  }
  if (clientReportDrilldownState.active) {
    let clientDrillClaims = claims;
    let clientDrillFooterOverride = null;
    // Receivables aggregates at SERVICE-LINE grain (AR-positive lines only), so
    // the claim-grain drill footer would diverge from the row. Rebuild the drill
    // from the same clientReceivableServiceRows the row was summed from, scoped
    // to this group label, so drill count === row `claims` and drill total ===
    // row `totalInsurance` exactly. Still only ever filters the already-scoped
    // snapshot the report was built from — never widens tenant scope.
    if (activeReport.key === "receivables") {
      const drillGroupBy = clientReportDrilldownState.groupBy === "client" ? "client" : "payer";
      const drillName = clientReportDrilldownState.name || "";
      const arRows = clientReceivableServiceRows(claims).filter((row) => drillGroupBy === "client" ? row.client === drillName : row.payer === drillName);
      const arClaimIds = new Set(arRows.map((row) => row.claimId));
      clientDrillClaims = claims.filter((claim) => arClaimIds.has(claim.claimId || ""));
      clientDrillFooterOverride = {
        count: arClaimIds.size,
        total: moneyNumber(arRows.reduce((sum, row) => sum + row.insuranceAr, 0))
      };
    }
    bodyHost.innerHTML = renderReportRowDrilldown(clientDrillClaims, clientReportDrilldownState, clientDrillFooterOverride);
    if (paymentsHost) paymentsHost.innerHTML = "";
    bindClientReportControls();
    bindClientReportRowDrilldown();
    return;
  }
  const model = activeReport.key === "receivables"
    ? clientReceivablesReportModel(claims, filters)
    : clientRcmReportModel(claims, filters);
  bodyHost.innerHTML = activeReport.key === "receivables"
    ? renderClientReceivablesReportWorkspace(model)
    : renderClientRcmReportWorkspace(model);
  if (trackerApiEnabled()) {
    const api = clientReportClaimsApiState;
    if (api.status === "partial") {
      bodyHost.insertAdjacentHTML("afterbegin", `
        <section class="notice-card">
          <strong>Loading the rest of your claims…</strong>
          <span>Showing the first ${formatNounCount(api.rows.length, "service line")} of ${formatNounCount(api.totalRows, "service line")}; figures update as the rest arrive.</span>
        </section>
      `);
    // TWO CAPS, RECONCILED BY NAMING THE FEED (2026-08-11). These reports sit
    // next to AR Aging and Performance in the same family switcher now, and the
    // two surfaces carry different caps: this one is the first N service lines
    // of the CLIENT CLAIMS feed in fetch order, while reportDatasetCapNoteHtml()
    // describes the report dataset, which is larger and owed-first.
    //
    // Equalising the two numbers was considered and rejected: it would remove
    // one of five reasons these reports disagree and imply the other four are
    // gone. They are two different feeds at two different grains (service line
    // vs claim) using two different balances (the tracker final insurance
    // balance vs expected minus paid) against two different aging anchors (DOS
    // vs original bill date). Matching caps would make them look like they
    // should tie, which is worse than saying plainly that they do not.
    } else if (api.status === "ok" && api.truncated) {
      bodyHost.insertAdjacentHTML("afterbegin", `
        <section class="notice-card">
          <strong>Large dataset</strong>
          <span>Reads the client claims feed — the first ${formatNounCount(Number(CLIENT_REPORT_CLAIMS_FETCH_LIMIT), "service line")}, in fetch order — so totals may be partial; narrow the date range for complete figures. AR Aging and Performance read a different, larger feed and are not expected to tie to this report.</span>
        </section>
      `);
    }
  }
  if (paymentsHost) paymentsHost.innerHTML = "";
  bindClientReportControls();
}

// A session whose ONLY reporting surface is the client report set: it can open
// Reports (client:payments) but holds no view:reports grant. Client and
// ClientBiller today. Asked in one place so "which chassis" has one answer.
function clientOnlyReportsSession() {
  return !hasPermission(PERMISSIONS.VIEW_REPORTS);
}

// The three reports that were the Payments & Exports tab, mapped onto the
// report-family view keys an internal session picks them with.
//
// THE INTERNAL LABELS SAY "CLIENT" ON PURPOSE. Client Receivables and AR Aging
// are NOT the same report and their totals will not tie: different feed (the
// client claims endpoint vs the report dataset), different grain (service line
// vs claim), different balance (the tracker's final insurance balance vs
// expected minus paid), different aging anchor (DOS vs original bill date),
// different buckets (seven vs the five canonical CT bands), and Receivables
// carries patient AR where AR Aging carries none. Same story for Client RCM
// against Performance, which restates its denominator by the 30% rule and caps
// overpayment out of the rate. Two identically-named tabs disagreeing by
// thousands is the confusion this merge exists to end, not to create.
const CLIENT_REPORT_FAMILY_VIEWS = {
  clientPayments: "payments",
  clientReceivables: "receivables",
  clientRcm: "rcm-performance"
};

function clientReportKeyForReportView(view) {
  return Object.prototype.hasOwnProperty.call(CLIENT_REPORT_FAMILY_VIEWS, view)
    ? CLIENT_REPORT_FAMILY_VIEWS[view]
    : "";
}

function reportViewForClientReportKey(key) {
  if (!key) return "";
  return Object.keys(CLIENT_REPORT_FAMILY_VIEWS).find((view) => CLIENT_REPORT_FAMILY_VIEWS[view] === key) || "";
}

// Is a client report the thing actually on screen? state.view === "reports" is
// no longer enough - it is also true for the fifteen internal reports, none of
// which asked the payment-checks lane for anything.
function clientReportSurfaceActive() {
  return state.view === "reports" && Boolean(clientActiveReport());
}

// Read generations are independent of the warehouse publication: assignments,
// cases, rates and credentialing can change without a new tracker snapshot.
let additionalPortalDataEpoch = 0;
let clientActionListReadSequence = 0;
let clientActionDetailReadSequence = 0;
let clientActionNotesReadSequence = 0;
let portalOrgDirectoryReadSequence = 0;
// The read currently in flight, so a caller that awaits a request already
// under way waits for the ANSWER rather than for nothing.
let portalOrgDirectoryInFlight = null;
let portalUserDirectoryReadSequence = 0;
let staffTeamsReadSequence = 0;

function additionalPortalDataReadScope() {
  return JSON.stringify({ epoch: additionalPortalDataEpoch, trackerBase: trackerApiBase(),
    workflowBase: workflowApiBase(), trackerToken: trackerApiToken(), workflowToken: workflowApiToken(),
    company: activeClientGroupTenant(), companyOrgIds: activeClientGroupOrgIds(),
    access: workflowCommandAccessSnapshot() });
}

function additionalPortalDataReadUrl(url) {
  return typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(url) : url;
}

function additionalPortalDataViewSupported(view) {
  return ["clientHome", "assignments", "reviews", "clientActionPending", "credentialing", "feeSchedules", "missingRates"].includes(view) || (typeof systemPortalDataViewSupported === "function" && systemPortalDataViewSupported(view));
}

function additionalPortalDataRequestsPending() {
  if (typeof systemPortalDataRequestsPending === "function" && systemPortalDataRequestsPending()) return true;
  if (waitingOnYouState.status === "loading") return true;
  switch (state.view) {
    case "clientHome": return [clientReportClaimsApiState.status, clientPaymentChecksApiState.status, reportCollectionsState.status].some((status) => ["pending", "loading"].includes(status));
    case "assignments": return [assignmentScopeUiState.status, workflowBoardState.status, oversightState().status, approvalQueueState.pending.status, approvalQueueState.rework.status].some((status) => ["pending", "loading"].includes(status));
    case "reviews": return [approvalQueueState.pending.status, approvalQueueState.rework.status].includes("pending");
    case "clientActionPending": return [clientActionWorkspaceState.status, clientActionWorkspaceState.detailStatus, clientActionWorkspaceState.notes.status].includes("loading");
    case "credentialing": return [credentialingState.status, credentialingState.queue.status].includes("loading");
    case "feeSchedules": case "missingRates": return [feeScheduleApiState.status, feeScheduleMissingApiState.status, feeParentMappingApiState.status].includes("pending");
    default: return false;
  }
}

function additionalPortalDataInteractionActive() {
  return (typeof systemPortalDataInteractionActive === "function" && systemPortalDataInteractionActive()) || Boolean(staffTeamState.editing || staffTeamState.saving) || (["assignments", "reviews"].includes(state.view) && (approvalQueueSelection.size > 0 || approvalQueueRejectArmed)) || Boolean(feeParentMatchDraft)
    || credentialingDrawerState.open || credentialingDrawerState.saving
    || Object.keys(credentialingState.pendingFileByDecisionId || {}).length > 0
    || clientActionWorkspaceState.summaryStatus === "saving" || clientActionWorkspaceState.workflowStatus === "saving"
    || feeScheduleApiState.status === "saving" || Boolean(teamStructureUiState.dirty || teamStructureUiState.saving);
}

function resetClientActionReadResponses() {
  clientActionListReadSequence += 1;
  clientActionDetailReadSequence += 1;
  clientActionNotesReadSequence += 1;
  try { clientActionWorkspaceState.controller?.abort(); clientActionWorkspaceState.detailController?.abort(); } catch (_) {}
  Object.assign(clientActionWorkspaceState, { key: "", status: "idle", rows: [], detail: null, detailStatus: "idle", error: "", readScope: "", prepared: false, page: 1, counts: null, pageInfo: null, permittedOrgIds: [] });
  Object.assign(clientActionWorkspaceState.notes, { key: "", status: "idle", items: [] });
}

function resetCredentialingReadResponses() {
  try { credentialingState.controller?.abort(); } catch (_) {}
  credentialingState.requestSeq += 1;
  credentialingState.queueRequestSeq += 1;
  credentialingState.boardRequestId += 1;
  credentialingState.queueRequestId += 1;
  Object.assign(credentialingState, { status: "idle", board: null, controller: null, error: "", generatedAtUtc: "", confirmedOrgId: "", readScope: "",
    reviewAccess: { mode: "loading", canUpdate: false, reason: "Credentialing update access is being confirmed by the server.", membershipRole: "" } });
  Object.assign(credentialingState.queue, { status: "idle", tasks: [], alerts: null, error: "" });
}

function resetAdditionalPortalDataReadState(options = {}) {
  // Proposal decisions own their My Work list/count reconciliation. Keep the
  // same principal's displayed work while fencing every pre-write read; a real
  // access/company reset still clears it through the default path.
  const preservedWork = options.preserveMyWork === true ? oversightState() : null;
  const previousWorkKey = preservedWork ? JSON.stringify([additionalPortalDataReadScope(), preservedWork.orgId || ""]) : "";
  const previousApprovalKey = preservedWork && typeof approvalQueueRequestKey === "function" ? approvalQueueRequestKey("pending") : "";
  const preservedWorkSession = preservedWork?.session || "";
  const heldWork = preservedWork ? { status: preservedWork.status, items: preservedWork.items,
    proposals: preservedWork.proposals, teams: preservedWork.teams, pagination: preservedWork.pagination } : null;
  additionalPortalDataEpoch += 1;
  if (typeof resetSystemPortalDataReadState === "function") resetSystemPortalDataReadState();
  resetAssignmentWorkflowScope({ preserveSelection: true });
  resetWorkflowBoard();
  invalidateApprovalQueue();
  const work = oversightState();
  work.request += 1;
  Object.assign(work, { status: "idle", items: [], proposals: [], error: "" });
  if (heldWork && work === preservedWork) {
    Object.assign(work, heldWork);
    // Move only this same-scope display state to the new read epoch. Keeping
    // its object also preserves decisions and per-line errors still in flight.
    if (work.approvalChangeView?.scope === JSON.stringify([preservedWorkSession, work.orgId || ""])) {
      work.approvalChangeView.scope = JSON.stringify([work.session || "", work.orgId || ""]);
    }
    if (previousApprovalKey && approvalQueueState.lastPendingAnswer?.key === previousApprovalKey) {
      approvalQueueState.lastPendingAnswer.key = approvalQueueRequestKey("pending");
    }
    const nextWorkKey = JSON.stringify([additionalPortalDataReadScope(), work.orgId || ""]);
    if (oversightTabCountsShown?.key === previousWorkKey) oversightTabCountsShown.key = nextWorkKey;
    if (myWorkAttentionState.shown?.key === previousWorkKey) {
      myWorkAttentionState.shown.key = nextWorkKey;
      if (myWorkAttentionState.shown.count) myWorkAttentionState.shown.count = { ...myWorkAttentionState.shown.count, key: nextWorkKey };
    }
  }
  if (clientActionWorkspaceState.prepared && clientActionWorkspaceState.readScope === clientActionReadScope()) {
    // Publication and unrelated writes may invalidate the next read while an
    // operator is replying. Keep the current case and draft intact.
    clientActionWorkspaceState.key = "";
  } else resetClientActionReadResponses();
  try { waitingOnYouState.controller?.abort(); } catch (_) {}
  waitingOnYouState.generation += 1;
  Object.assign(waitingOnYouState, { key: "", status: "idle", data: null, controller: null });
  resetCredentialingReadResponses();
  feeScheduleListRequestSequence += 1;
  feeParentMappingRequestSequence += 1;
  feeScheduleApiState = { ...feeScheduleApiState, key: "", status: "idle", rows: [], complete: false };
  feeScheduleMissingApiState = { ...feeScheduleMissingApiState, key: "", requestId: feeScheduleMissingApiState.requestId + 1, status: "idle", rows: [], complete: false };
  feeParentMappingApiState = { ...feeParentMappingApiState, key: "", status: "idle", rows: [] };
  // Directory modals refresh when reopened; do not repaint a user's open editor.
  portalOrgDirectoryReadSequence += 1;
  portalUserDirectoryReadSequence += 1;
  staffTeamsReadSequence += 1;
  portalOrgDirectoryState = { ...portalOrgDirectoryState, key: "", status: "idle", orgs: [] };
  portalUserDirectoryState = { ...portalUserDirectoryState, requestKey: "", status: "idle" };
  staffTeamState.status = "idle";
  // An open Teams list owns its replacement read after publication invalidates
  // an in-flight response. Keep drafts and closed/hidden panes undisturbed.
  const teamsSession = staffTeamSessionKey();
  queueMicrotask(() => {
    const modal = document.getElementById("userMgmtModal");
    if (staffTeamSessionKey() !== teamsSession || !isTrustedUserManagerSession()
        || !modal || modal.classList.contains("hidden")
        || userMgmtDirectoryState.tab !== "teams" || staffTeamState.status !== "idle"
        || staffTeamState.editing || staffTeamState.saving) return;
    loadStaffTeams();
  });
  teamStructureRequestSeq += 1;
  teamStructureUiState.requestKey = "";
  if (!teamStructureUiState.dirty && !teamStructureUiState.saving) teamStructureUiState.status = "idle";
}

function refreshAdditionalPortalDataView(view) {
  resetAdditionalPortalDataReadState();
  return additionalPortalDataViewSupported(view);
}

function renderAdditionalPortalDataView(view) {
  // The Waiting on You hub belongs to the shell, including tracker/report pages.
  requestWaitingOnYou();
  switch (view) {
    case "clientHome": renderClientHome(); break;
    case "assignments": renderAssignments(); break;
    case "reviews": renderReviews(); break;
    case "clientActionPending": renderClientActionPending(); break;
    case "credentialing": renderCredentialing(); break;
    case "feeSchedules": renderFeeSchedules(); break;
    case "missingRates": renderMissingRates(); break;
    default: return typeof renderSystemPortalDataView === "function" ? renderSystemPortalDataView(view) : false;
  }
  return true;
}

function refreshClientMoneySurfaces() {
  if (clientReportSurfaceActive()) renderClientView();
  if (state.view === "clientHome") renderClientHome();
}

// WHICH client report is open, answered per audience. A client-only session
// drives them from its own card catalog (state.clientReportOpenKey); an
// internal session drives them from the Reports family switcher, because
// inside Reports that switcher IS the report picker and a second picker
// underneath it would be two controls answering one question.
function clientActiveReport() {
  const key = clientOnlyReportsSession()
    ? (CLIENT_REPORT_LIBRARY.some((report) => report.key === state.clientReportOpenKey) ? state.clientReportOpenKey : "")
    : clientReportKeyForReportView(reportArState.view);
  return key ? (CLIENT_REPORT_LIBRARY.find((report) => report.key === key) || null) : null;
}

function renderClientReportCatalog(activeKey = "payments") {
  const byCategory = CLIENT_REPORT_LIBRARY.reduce((map, report) => {
    if (!map.has(report.category)) map.set(report.category, []);
    map.get(report.category).push(report);
    return map;
  }, new Map());
  return `
    <aside class="client-report-catalog" aria-label="Client report library">
      ${Array.from(byCategory.entries()).map(([category, reports]) => `
        <section class="client-report-category">
          <h3>${escapeHtml(category)}</h3>
          <div class="client-report-links">
            ${reports.map((report) => `
              <button type="button" class="client-report-link client-report-link--${escapeHtml(report.accent)} ${report.key === activeKey ? "is-active" : ""}" data-client-report="${escapeHtml(report.key)}">
                <strong>${escapeHtml(report.title)}</strong>
                <span>${escapeHtml(report.summary)}</span>
                <span class="client-report-link-hint">Select to open details.</span>
              </button>
            `).join("")}
          </div>
        </section>
      `).join("")}
    </aside>
  `;
}

function renderClientReportLibraryPrompt() {
  return `
    <section class="client-report-empty-state" aria-label="Client report library instructions">
      <strong>Select Payments to open details.</strong>
      <span>Receivables and RCM Performance sit alongside it.</span>
    </section>
  `;
}

// The report name always renders; only the BACK BUTTON is audience-specific.
// It belongs to the client-only chassis, where the report catalog is literally
// the screen behind it. An internal session arrived from the report family
// switcher, which is still on screen one row above, so a "back" button pointing
// at a catalog that session never sees would be a dead control sitting directly
// under a live one that already does the job. Dropping the whole strip instead
// would take the report title with it and leave an internal session looking at
// an unlabelled table.
function clientReportCrumbHtml(title) {
  const back = clientOnlyReportsSession()
    ? `<button type="button" class="small-button" data-client-report-back>Reports</button>`
    : "";
  return `<div class="client-report-crumb">${back}<strong>${escapeHtml(title)}</strong></div>`;
}

function renderClientReportClaimsNotice(loadState = "pending") {
  if (loadState === "error") {
    return `
      <section class="notice-card">
        <strong>Report data could not load</strong>
        <span>${escapeHtml(clientReportClaimsApiState.error || "Claims could not be loaded.")}</span>
        <button type="button" class="small-button" id="clientReportClaimsRetry">Retry</button>
      </section>
    `;
  }
  return `
    <section class="notice-card">
      <strong>Loading report data…</strong>
      <span>Please hold while we load your claims.</span>
    </section>
  `;
}

function clientReportRouteUrl(reportKey = "") {
  const url = new URL(window.location.href);
  url.searchParams.set("view", "reports");
  if (reportKey) url.searchParams.set("clientReport", reportKey);
  else url.searchParams.delete("clientReport");
  return `${url.pathname}?${url.searchParams.toString()}${url.hash || ""}`;
}

function openClientReportRoute(reportKey = "") {
  state.view = "reports";
  const key = CLIENT_REPORT_LIBRARY.some((report) => report.key === reportKey) ? reportKey : "";
  if (key !== state.clientReportOpenKey) {
    clientReportAggregatePage = 1;
    clientReportAggregatePageIdentity = "";
    clientReportAggregateApiState = { key: "", status: "idle", response: null, error: "", requestId: 0 };
    clientReportServerDrillState = { status: "idle", groupName: "", response: null, error: "", page: 1, requestId: 0 };
  }
  state.clientReportOpenKey = key;
  // Keep the two pickers in step: an internal session selects these from the
  // family switcher, so a link into one has to move the switcher as well.
  const familyView = reportViewForClientReportKey(key);
  if (familyView && !clientOnlyReportsSession()) reportArState.view = familyView;
  if (window.history?.pushState) {
    window.history.pushState({}, "", clientReportRouteUrl(state.clientReportOpenKey));
  }
  saveState();
  // Through renderReports(), not straight at the client body: renderReports()
  // owns which chassis is on screen (filter bar, family switcher, client host),
  // and skipping it leaves the previous report's chrome standing.
  if (els.reportsBody) renderReports();
  else renderClientView();
}

function clientReportFilterValues() {
  const grouping = document.querySelector("input[name='clientReportGrouping']:checked")?.value || "payer";
  return {
    groupBy: document.querySelector("input[name='clientReportGrouping']:checked") ? (grouping === "client" ? "client" : "payer") : (clientReportAggregateFilters.groupBy || "payer"),
    client: activeClientGroupTenant() || document.getElementById("clientReportClientFilter")?.value || clientReportAggregateFilters.client || "All",
    payer: document.getElementById("clientReportPayerFilter")?.value || clientReportAggregateFilters.payer || "All",
    fromDate: document.getElementById("clientReportFromDate")?.value ?? clientReportAggregateFilters.fromDate ?? "",
    toDate: document.getElementById("clientReportToDate")?.value ?? clientReportAggregateFilters.toDate ?? "",
    slicers: state.clientReportSlicers || {}
  };
}

function normalizeClientReportFilters(filters = {}, accessibleClaims = clientReportSourceClaims()) {
  const clients = clientReportClientOptions(accessibleClaims);
  const payers = uniqueReportPayers(accessibleClaims);
  return {
    ...filters,
    client: filters.client && filters.client !== "All" && clients.includes(filters.client) ? filters.client : "All",
    payer: filters.payer && filters.payer !== "All" && payers.includes(filters.payer) ? filters.payer : "All",
    slicers: normalizeClientReportSlicers(filters.slicers)
  };
}

function clientReportFilteredClaims(allClaims = [], filters = {}) {
  const fromDate = normalizeDateValue(filters.fromDate);
  const toDate = normalizeDateValue(filters.toDate);
  const activeSlicers = normalizeClientReportSlicers(filters.slicers);
  return allClaims.map((claim) => {
    if (filters.client && filters.client !== "All" && claim.client !== filters.client) return false;
    if (filters.payer && filters.payer !== "All" && claim.primaryPayer !== filters.payer && claim.secondaryPayer !== filters.payer) return false;
    if (!clientReportClaimMatchesSlicers(claim, activeSlicers)) return false;
    if (!fromDate && !toDate) return claim;
    const services = (claim.services || []).filter((service) => {
      const dos = serviceDosValue(service) || normalizeDateValue(claim.dos);
      if (!dos) return false;
      if (fromDate && dos < fromDate) return false;
      if (toDate && dos > toDate) return false;
      return true;
    });
    return services.length ? { ...claim, services } : false;
  }).filter(Boolean);
}

function clientReportClientOptions(claims = clientReportSourceClaims()) {
  const scope = currentAccessProfile()?.scope || {};
  const scopedClients = normalizeUserScopeList(scope.clientNames);
  if (!scope.allOrgs && scopedClients.length) return scopedClients;
  const claimClients = uniqueValues(claims, "client");
  if (!scope.allOrgs) return claimClients;
  const inventoryClients = allDataTenantClientOptions().map((option) => option.value).filter(Boolean);
  return uniqueSorted(inventoryClients.length ? inventoryClients : claimClients);
}

const CLIENT_REPORT_SLICERS = [
  { key: "action", label: "Follow-up Action", get: (claim) => followUpFilterRowValue(claim) },
  { key: "writeOff", label: "Write-Off", get: (claim) => hasWriteOffWorkflow(claim) || claimFinancialView(claim).writeOffAmount > 0 || /write.?off/i.test(String(claim.action || "")) ? "Write-off" : "No write-off" },
  { key: "match", label: "Match", get: (claim) => claim.match || "No match" },
  { key: "aging", label: "Aging", get: (claim) => claim.aging || "No aging" },
  { key: "claimStatus", label: "Claim Status", get: (claim) => claim.claimStatus || "No status" },
  { key: "claimClassification", label: "Claim Class", get: (claim) => claim.claimClassification || claim.claClassification || "No class" },
  { key: "network", label: "In/Out of Network", get: (claim) => claim.network || "Unknown" },
  { key: "secondaryPayer", label: "Secondary Payer", get: (claim) => claim.secondaryPayer || "No secondary" }
];

// Client report slicers are persisted per user and matched by selected.includes(value) in
// clientReportClaimMatchesSlicers, so a stored aging selection has to survive the warehouse
// vocabulary flip here as well or a client's saved report silently reports zeros
// (migrateAgingSlicerValues: keep the stored label, add its CT band alongside it).
function normalizeClientReportSlicers(value = {}) {
  const allowed = new Set(CLIENT_REPORT_SLICERS.map((slicer) => slicer.key));
  return Object.fromEntries(Object.entries(value || {})
    .filter(([key]) => allowed.has(key))
    .map(([key, values]) => {
      const list = normalizeUserScopeList(values);
      return [key, key === "aging" ? migrateAgingSlicerValues(list) : (key === "action" ? Array.from(new Set(list.map(followUpFilterValue))) : list)];
    }));
}

function clientReportClaimMatchesSlicers(claim = {}, activeSlicers = {}) {
  return CLIENT_REPORT_SLICERS.every((slicer) => {
    const selected = activeSlicers[slicer.key] || [];
    if (!selected.length) return true;
    const value = cleanClientReportSlicerValue(slicer.get(claim));
    return slicer.key === "action" ? selected.map(followUpFilterValue).includes(followUpFilterValue(value)) : selected.includes(value);
  });
}

function cleanClientReportSlicerValue(value = "") {
  const text = String(value || "").trim();
  return text && text !== "undefined" ? text : "Blank";
}

function clientReportSlicerValues(claims = [], slicer = {}) {
  const values = uniqueSorted((Array.isArray(claims) ? claims : [])
    .map((claim) => cleanClientReportSlicerValue(slicer.get(claim)))
    .filter(Boolean));
  return slicer.key === "action" ? followUpFilterValues(values) : values;
}

// Drill-down state: which grouped rows are expanded, keyed per report so the
// RCM and Receivables tables remember their own open groups independently.
function normalizeClientReportExpanded(value = {}) {
  const allowed = new Set(CLIENT_REPORT_LIBRARY.map((report) => report.key));
  const source = value && typeof value === "object" ? value : {};
  return Object.fromEntries(Object.entries(source)
    .filter(([key]) => allowed.has(key))
    .map(([key, names]) => [key, (Array.isArray(names) ? names : []).map((name) => String(name)).slice(0, 200)]));
}

function clientReportExpandedSet(reportKey = "") {
  const map = state.clientReportExpanded || {};
  const list = Array.isArray(map[reportKey]) ? map[reportKey] : [];
  return new Set(list.map((name) => String(name)));
}

function toggleClientReportExpandedRow(reportKey = "", name = "") {
  if (!reportKey || !name) return;
  const map = (state.clientReportExpanded && typeof state.clientReportExpanded === "object") ? state.clientReportExpanded : {};
  const list = Array.isArray(map[reportKey]) ? map[reportKey].map((entry) => String(entry)) : [];
  const key = String(name);
  map[reportKey] = list.includes(key) ? list.filter((entry) => entry !== key) : [...list, key];
  state.clientReportExpanded = map;
  saveState();
  renderClientReportsView();
}

let clientReportDrilldownState = { active: false, reportKey: "", label: "", dims: [], predicate: null, claimId: null, profile: "biller", allowGoto: false };

// Client report drill-down: reuse the biller drill-down substrate
// (renderReportRowDrilldown) so a client sees the identical claim-group ->
// service-line view, but drive re-render through renderClientReportsView and
// keep the goto-workbench action off (clients have no work queue). Operates
// only over the already-scoped clientReportFilteredClaims snapshot the report
// aggregates were built from — never widens tenant scope.
function clientReportGroupLabel(claim, groupBy) {
  return groupBy === "client"
    ? (claim.client || "Unassigned client")
    : (claim.primaryPayer || claim.secondaryPayer || "Unassigned payer");
}

function clientReportDrillIntoRow(context) {
  const ctx = context || {};
  clientReportDrilldownState = {
    active: true,
    reportKey: ctx.reportKey || "",
    label: ctx.label || "",
    dims: Array.isArray(ctx.dims) ? ctx.dims : [],
    predicate: typeof ctx.predicate === "function" ? ctx.predicate : (() => true),
    groupBy: ctx.groupBy === "client" ? "client" : "payer",
    name: ctx.name || "",
    claimId: null,
    profile: clientReportDrilldownState.profile || "biller",
    allowGoto: false
  };
  renderClientReportsView();
}

function closeClientReportRowDrilldown() {
  clientReportDrilldownState = { active: false, reportKey: "", label: "", dims: [], predicate: null, claimId: null, profile: clientReportDrilldownState.profile || "biller", allowGoto: false };
  renderClientReportsView();
}

function bindClientReportRowDrilldown() {
  const host = document.getElementById("clientPivotBody");
  if (!host) return;
  host.querySelector("[data-report-drill-close]")?.addEventListener("click", closeClientReportRowDrilldown);
  host.querySelectorAll("[data-report-drill-view]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.getAttribute("data-report-drill-view");
      clientReportDrilldownState.claimId = String(clientReportDrilldownState.claimId) === id ? null : id;
      renderClientReportsView();
    });
  });
  host.querySelectorAll("[data-report-drill-profile]").forEach((btn) => {
    btn.addEventListener("click", () => {
      clientReportDrilldownState.profile = btn.getAttribute("data-report-drill-profile");
      renderClientReportsView();
    });
  });
}

// Patient-side figures the calculation engine can source honestly. The RCM
// report only tracks the patient's net responsibility (post-insurance), the
// outstanding patient balance, and the implied patient-paid amount — there is
// no patient analog for gross charges, sales adjustments, bad debt, or
// overpayments, so those columns stay at zero rather than carry invented data.
function rcmServicePatientView(service = {}, claim = {}) {
  const fin = serviceFinancialView(service, claim);
  const responsibility = moneyNumber(Math.max(fin.patientResponsibility || 0, 0));
  const receivable = moneyNumber(Math.max(fin.positiveTotalPatientBalance || 0, 0));
  const paid = moneyNumber(Math.max(responsibility - receivable, 0));
  return { responsibility, receivable, paid };
}

// Report cap (REPORTING_TRUTH_CONTRACT 4a): a claim's receivable is the sum of its lines'
// never-negative balances, and its overpayment is the rated-line excess over expected.
function clientRcmCappedOf(financial = {}) {
  return ((financial && financial.serviceFinancials) || []).reduce((sum, line) => {
    const capped = reportCappedLineMeasures(line);
    sum.remaining += capped.remainingBalance;
    sum.overpaid += capped.overpaid;
    return sum;
  }, { remaining: 0, overpaid: 0 });
}

function clientRcmReportModel(claims = [], filters = {}) {
  const allClaims = clientReportSourceClaims();
  const serviceRows = claims.flatMap((claim) => (claim.services || []).map((service) => ({ claim, service })));
  const claimFinancials = claims.map(claimFinancialView);
  const patientRows = serviceRows.map((row) => rcmServicePatientView(row.service, row.claim));
  const totals = {
    claims: claims.length,
    serviceRows: serviceRows.length,
    charges: serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).charges, 0),
    adjustedCharges: serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).expectedReimbursement, 0),
    payments: claimFinancials.reduce((sum, fin) => sum + fin.totalInsurancePaid, 0),
    receivables: claimFinancials.reduce((sum, fin) => sum + clientRcmCappedOf(fin).remaining, 0),
    overpayments: claimFinancials.reduce((sum, fin) => sum + clientRcmCappedOf(fin).overpaid, 0),
    patientCharges: moneyNumber(patientRows.reduce((sum, row) => sum + row.responsibility, 0)),
    patientPayments: moneyNumber(patientRows.reduce((sum, row) => sum + row.paid, 0)),
    patientReceivables: moneyNumber(patientRows.reduce((sum, row) => sum + row.receivable, 0))
  };
  totals.salesAdjustments = moneyNumber(Math.max(totals.charges - totals.adjustedCharges, 0));
  totals.grossCollectionRate = totals.adjustedCharges > 0 ? (totals.payments - totals.overpayments) / totals.adjustedCharges : null;
  totals.netCollectionRate = totals.adjustedCharges > 0 ? (totals.payments - totals.overpayments) / totals.adjustedCharges : null;
  totals.patientCollectionRate = totals.patientCharges > 0 ? totals.patientPayments / totals.patientCharges : null;
  const expanded = clientReportExpandedSet("rcm-performance");
  const rows = clientRcmGroupedRows(claims, filters.groupBy || "payer").map((row) => ({ ...row, expanded: expanded.has(row.name) }));
  return {
    filters,
    options: {
      clients: clientReportClientOptions(allClaims),
      payers: uniqueReportPayers(allClaims)
    },
    totals,
    gauges: [
      { label: "GCR", value: totals.grossCollectionRate, detail: "Gross collection rate" },
      { label: "NCR", value: totals.netCollectionRate, detail: "Net collection rate" }
    ],
    rows,
    chartRows: rows.slice(0, 32)
  };
}

function clientRcmGroupedRows(claims = [], groupBy = "payer") {
  const rows = new Map();
  claims.forEach((claim) => {
    const label = groupBy === "client" ? (claim.client || "Unassigned client") : (claim.primaryPayer || claim.secondaryPayer || "Unassigned payer");
    const row = rows.get(label) || {
      name: label,
      clients: new Set(),
      charges: 0,
      salesAdjustments: 0,
      adjustedCharges: 0,
      payments: 0,
      receivables: 0,
      overpayments: 0,
      badDebt: 0,
      patientCharges: 0,
      patientPayments: 0,
      patientReceivables: 0,
      claims: 0,
      details: []
    };
    const financial = claimFinancialView(claim);
    const services = claim.services || [];
    const charges = services.reduce((sum, service) => sum + serviceFinancialView(service, claim).charges, 0);
    const adjustedCharges = services.reduce((sum, service) => sum + serviceFinancialView(service, claim).expectedReimbursement, 0);
    const patient = services.reduce((acc, service) => {
      const view = rcmServicePatientView(service, claim);
      acc.responsibility += view.responsibility;
      acc.paid += view.paid;
      acc.receivable += view.receivable;
      return acc;
    }, { responsibility: 0, paid: 0, receivable: 0 });
    row.clients.add(claim.client || "Unassigned client");
    row.charges += charges;
    row.salesAdjustments += Math.max(charges - adjustedCharges, 0);
    row.adjustedCharges += adjustedCharges;
    row.payments += financial.totalInsurancePaid;
    const capped = clientRcmCappedOf(financial);
    row.receivables += capped.remaining;
    row.overpayments += capped.overpaid;
    row.patientCharges += patient.responsibility;
    row.patientPayments += patient.paid;
    row.patientReceivables += patient.receivable;
    row.claims += 1;
    row.details.push({
      claimId: claim.claimId || "—",
      label: groupBy === "client" ? (claim.primaryPayer || claim.secondaryPayer || "Unassigned payer") : (claim.client || "Unassigned client"),
      payments: moneyNumber(financial.totalInsurancePaid),
      receivables: moneyNumber(capped.remaining),
      overpayments: moneyNumber(capped.overpaid)
    });
    rows.set(label, row);
  });
  return Array.from(rows.values()).map((row) => {
    const grossCollectionRate = row.adjustedCharges > 0 ? (row.payments - row.overpayments) / row.adjustedCharges : null;
    const netCollectionRate = row.adjustedCharges > 0 ? (row.payments - row.overpayments) / row.adjustedCharges : null;
    const patientCollectionRate = row.patientCharges > 0 ? row.patientPayments / row.patientCharges : null;
    return {
      ...row,
      clients: row.clients.size,
      charges: moneyNumber(row.charges),
      salesAdjustments: moneyNumber(row.salesAdjustments),
      adjustedCharges: moneyNumber(row.adjustedCharges),
      payments: moneyNumber(row.payments),
      receivables: moneyNumber(row.receivables),
      overpayments: moneyNumber(row.overpayments),
      patientCharges: moneyNumber(row.patientCharges),
      patientPayments: moneyNumber(row.patientPayments),
      patientReceivables: moneyNumber(row.patientReceivables),
      grossCollectionRate,
      netCollectionRate,
      patientCollectionRate,
      details: row.details.sort((a, b) => b.receivables - a.receivables).slice(0, 12)
    };
  }).sort((a, b) => b.receivables - a.receivables || b.overpayments - a.overpayments || compareText(a.name, b.name));
}

const CLIENT_RECEIVABLE_BUCKETS = [
  { key: "0-30", label: "0-30 Days", min: 0, max: 30, tone: "cyan" },
  { key: "31-60", label: "31-60 Days", min: 31, max: 60, tone: "teal" },
  { key: "61-90", label: "61-90 Days", min: 61, max: 90, tone: "sky" },
  { key: "91-120", label: "91-120 Days", min: 91, max: 120, tone: "blue" },
  { key: "121-180", label: "121-180 Days", min: 121, max: 180, tone: "navy" },
  { key: "181-365", label: "181-365 Days", min: 181, max: 365, tone: "gold" },
  { key: "366+", label: "366+ Days", min: 366, max: Infinity, tone: "rose" }
];

function clientReceivablesReportModel(claims = [], filters = {}) {
  const allClaims = clientReportSourceClaims();
  const serviceRows = clientReceivableServiceRows(claims);
  const expanded = clientReportExpandedSet("receivables");
  const rows = clientReceivableGroupedRows(serviceRows, filters.groupBy || "payer").map((row) => ({ ...row, expanded: expanded.has(row.name) }));
  const buckets = CLIENT_RECEIVABLE_BUCKETS.map((bucket) => {
    const bucketRows = serviceRows.filter((row) => row.bucketKey === bucket.key);
    const insurance = moneyNumber(bucketRows.reduce((sum, row) => sum + row.insuranceAr, 0));
    return {
      ...bucket,
      insurance,
      patient: moneyNumber(bucketRows.reduce((sum, row) => sum + row.patientAr, 0)),
      claims: new Set(bucketRows.map((row) => row.claimId)).size,
      points: clientReceivableBucketPoints(bucketRows, insurance)
    };
  });
  const totals = {
    arOutstanding: moneyNumber(buckets.reduce((sum, bucket) => sum + bucket.insurance, 0)),
    patientOutstanding: moneyNumber(buckets.reduce((sum, bucket) => sum + bucket.patient, 0)),
    overpayments: moneyNumber(claims.reduce((sum, claim) => sum + claimFinancialView(claim).overpayment, 0)),
    arOver90: moneyNumber(buckets.filter((bucket) => ["91-120", "121-180", "181-365", "366+"].includes(bucket.key)).reduce((sum, bucket) => sum + bucket.insurance, 0)),
    claims: new Set(serviceRows.map((row) => row.claimId)).size,
    serviceRows: serviceRows.length
  };
  totals.arOver90Rate = totals.arOutstanding > 0 ? totals.arOver90 / totals.arOutstanding : null;
  return {
    filters,
    options: {
      clients: clientReportClientOptions(allClaims),
      payers: uniqueReportPayers(allClaims)
    },
    buckets,
    rows,
    totals
  };
}

function clientReceivableServiceRows(claims = []) {
  return claims.flatMap((claim) => {
    const claimFin = claimFinancialView(claim);
    return (claim.services || []).map((service) => {
      const financial = serviceFinancialView(service, claim);
      const dos = serviceDosValue(service) || normalizeDateValue(claim.dos);
      const agingDays = clientReceivableAgingDays(dos);
      const bucket = clientReceivableBucketForDays(agingDays);
      const insuranceAr = moneyNumber(Math.max(financial.sourceOutstanding || 0, 0));
      const patientAr = moneyNumber(Math.max(financial.positiveTotalPatientBalance || 0, 0));
      return {
        claimId: claim.claimId || "",
        serviceId: service.serviceId || service.id || "",
        payer: claim.primaryPayer || claim.secondaryPayer || "Unassigned payer",
        client: claim.client || "Unassigned client",
        dos,
        agingDays,
        bucketKey: bucket.key,
        insuranceAr,
        patientAr,
        overpayment: moneyNumber(claimFin.overpayment || 0)
      };
    }).filter((row) => row.insuranceAr > 0 || row.patientAr > 0);
  });
}

function clientReceivableAgingDays(dos = "") {
  const date = localDateFromDateOnly(dos);
  if (!date) return 0;
  return Math.max(0, Math.floor((todayStart() - date) / 86400000));
}

function clientReceivableBucketForDays(days = 0) {
  return CLIENT_RECEIVABLE_BUCKETS.find((bucket) => days >= bucket.min && days <= bucket.max) || CLIENT_RECEIVABLE_BUCKETS[0];
}

function clientReceivableBucketPoints(rows = [], bucketTotal = 0) {
  const points = rows
    .filter((row) => row.insuranceAr > 0)
    .sort((a, b) => b.insuranceAr - a.insuranceAr)
    .slice(0, 18);
  const count = points.length;
  return points.map((row, index) => ({
    ...row,
    // Deterministic, evenly spread columns (largest AR on the left) instead of a
    // modulo pattern that read as random noise.
    x: count > 1 ? 12 + (index / (count - 1)) * 74 : 50,
    y: 78 - Math.min(62, Math.max(8, bucketTotal > 0 ? (row.insuranceAr / bucketTotal) * 100 : 8)),
    r: Math.max(4, Math.min(16, Math.sqrt(row.insuranceAr) / 16))
  }));
}

function clientReceivableGroupedRows(serviceRows = [], groupBy = "payer") {
  const rows = new Map();
  serviceRows.forEach((serviceRow) => {
    const label = groupBy === "client" ? serviceRow.client : serviceRow.payer;
    const row = rows.get(label) || {
      name: label,
      claims: new Set(),
      insurance: Object.fromEntries(CLIENT_RECEIVABLE_BUCKETS.map((bucket) => [bucket.key, 0])),
      patient: Object.fromEntries(CLIENT_RECEIVABLE_BUCKETS.map((bucket) => [bucket.key, 0])),
      totalInsurance: 0,
      totalPatient: 0,
      details: []
    };
    row.claims.add(serviceRow.claimId);
    row.insurance[serviceRow.bucketKey] += serviceRow.insuranceAr;
    row.patient[serviceRow.bucketKey] += serviceRow.patientAr;
    row.totalInsurance += serviceRow.insuranceAr;
    row.totalPatient += serviceRow.patientAr;
    row.details.push({
      claimId: serviceRow.claimId || "—",
      label: groupBy === "client" ? serviceRow.payer : serviceRow.client,
      dos: serviceRow.dos || "",
      agingDays: serviceRow.agingDays || 0,
      bucketKey: serviceRow.bucketKey,
      insuranceAr: serviceRow.insuranceAr,
      patientAr: serviceRow.patientAr
    });
    rows.set(label, row);
  });
  return Array.from(rows.values()).map((row) => ({
    ...row,
    claims: row.claims.size,
    totalInsurance: moneyNumber(row.totalInsurance),
    totalPatient: moneyNumber(row.totalPatient),
    insurance: Object.fromEntries(Object.entries(row.insurance).map(([key, value]) => [key, moneyNumber(value)])),
    patient: Object.fromEntries(Object.entries(row.patient).map(([key, value]) => [key, moneyNumber(value)])),
    details: row.details.sort((a, b) => b.insuranceAr - a.insuranceAr).slice(0, 12)
  })).sort((a, b) => b.totalInsurance - a.totalInsurance || compareText(a.name, b.name));
}

function clientReportFiltersActive(filters = {}) {
  const slicers = typeof normalizeClientReportSlicers === "function"
    ? normalizeClientReportSlicers(filters.slicers || {})
    : (filters.slicers || {});
  return Boolean(
    (filters.client && filters.client !== "All")
    || (filters.payer && filters.payer !== "All")
    || String(filters.fromDate || "").trim()
    || String(filters.toDate || "").trim()
    || Object.values(slicers).some((values) => Array.isArray(values) && values.length)
  );
}

// Local filters rebuild the model from the narrowed claim set, so totals go
// to $0 with the rows. Name AR already on the loaded book. Never invent a
// leftover statement dollar.
function clientReportHiddenAr(model = {}, kind = "receivables") {
  if (!clientReportFiltersActive(model.filters || {})) return null;
  const totals = model.totals || {};
  const fromTotals = kind === "rcm" ? Number(totals.receivables) : Number(totals.arOutstanding);
  if (Number.isFinite(fromTotals) && fromTotals > 0) return { amount: moneyNumber(fromTotals) };
  if (model.serverBacked) return { amount: null };
  if (typeof clientReportSourceClaims !== "function") return { amount: null };
  const book = clientReportSourceClaims();
  if (!Array.isArray(book) || !book.length) return { amount: null };
  if (kind === "rcm") {
    const amount = moneyNumber(book.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0));
    return { amount: amount > 0 ? amount : null };
  }
  const serviceRows = typeof clientReceivableServiceRows === "function" ? clientReceivableServiceRows(book) : [];
  const amount = moneyNumber(serviceRows.reduce((sum, row) => sum + Number(row.insuranceAr || 0), 0));
  return { amount: amount > 0 ? amount : null };
}

function clientReceivablesEmptyNote(model = {}) {
  const hidden = clientReportHiddenAr(model, "receivables");
  if (hidden && hidden.amount != null) {
    return `No receivable rows match the active report filters. Filters are hiding ${clientReportMoneyLabel(hidden.amount)} still on this book. This is not $0.`;
  }
  if (hidden) return "No receivable rows match the active report filters. That is not the same as $0 on this book.";
  return "No receivable rows match the active report filters.";
}

function clientRcmEmptyNote(model = {}) {
  const hidden = clientReportHiddenAr(model, "rcm");
  if (hidden && hidden.amount != null) {
    return `No rows match the active report filters. Filters are hiding ${clientReportMoneyLabel(hidden.amount)} still on this book. This is not $0.`;
  }
  if (hidden) return "No rows match the active report filters. That is not the same as $0 on this book.";
  return "No rows match the active report filters.";
}

function renderClientRcmReportWorkspace(model = {}) {
  const filters = model.filters || {};
  const totals = model.totals || {};
  return `
    <section class="client-rcm-report" aria-label="Client RCM performance report">
      ${leftAfterInsuranceHelpNodeHtml("clientRcmLeftAfterInsuranceHelp")}
      <div class="client-report-toolbar">
        ${clientReportCrumbHtml("RCM Performance")}
        <div class="client-report-toolbar-filters">
          <label>Client<select id="clientReportClientFilter">${renderClientReportOptions(model.options?.clients || [], filters.client || "All", "All clients")}</select></label>
          <label>Payer<select id="clientReportPayerFilter">${renderClientReportOptions(model.options?.payers || [], filters.payer || "All", "All payers")}</select></label>
          <label>From DOS<input id="clientReportFromDate" type="date" value="${escapeHtml(filters.fromDate || "")}"></label>
          <label>To DOS<input id="clientReportToDate" type="date" value="${escapeHtml(filters.toDate || "")}"></label>
          <div class="client-report-radio" role="radiogroup" aria-label="Group report by">
            <label><input type="radio" name="clientReportGrouping" value="payer" ${filters.groupBy !== "client" ? "checked" : ""}> Payer</label>
            <label><input type="radio" name="clientReportGrouping" value="client" ${filters.groupBy === "client" ? "checked" : ""}> Client</label>
          </div>
          ${hasPermission(PERMISSIONS.REPORT_EXPORT) ? '<button type="button" class="small-button client-report-export-btn" data-client-report-export aria-label="Export RCM report to CSV">Export CSV</button>' : ""}
        </div>
      </div>
      ${renderClientReportSlicerCollapse(model)}
      <div class="client-rcm-layout">
        <aside class="client-rcm-gauges">${model.gauges.map(renderClientRcmGauge).join("")}</aside>
        <div class="client-rcm-chart-panel">
          <div class="client-rcm-chart-title">
            <div><strong>${filters.groupBy === "client" ? "Receivables by Client" : "Receivables by Payer"}</strong><span>${formatNounCount(model.rows.length, "group")} · ${formatNounCount(totals.claims, "claim")}</span></div>
            <div><span>Receivables ${clientReportMoneyLabel(totals.receivables)}</span><span>Overpayments ${clientReportMoneyLabel(totals.overpayments)}</span></div>
          </div>
          ${renderClientRcmBubbleChart(model.chartRows || [], filters.groupBy || "payer")}
        </div>
      </div>
      <div class="client-rcm-table-wrap">
        <table class="client-rcm-table">
          <thead>
            <tr>
              <th rowspan="2">Name</th><th colspan="2">Charges</th><th colspan="2">Sales Adjustments</th><th colspan="2">Adjusted Charges</th><th colspan="2">Payments</th><th colspan="2">Gross Collection Rate</th><th colspan="2">Bad Debt</th><th colspan="2">Net Collection Rate</th><th colspan="2">Receivables</th><th colspan="2">Over Payments</th>
            </tr>
            <tr>${Array.from({ length: 9 }).map((_, index) => index === 7 ? `<th>Insurance</th>${leftAfterInsuranceHeaderHtml("clientRcmLeftAfterInsuranceHelp")}` : "<th>Insurance</th><th>Patient</th>").join("")}</tr>
          </thead>
          <tbody>${model.rows.length ? model.rows.map(renderClientRcmTableRow).join("") : `<tr><td colspan="19">${escapeHtml(clientRcmEmptyNote(model))}</td></tr>`}</tbody>
          <tfoot>${renderClientRcmTotalsRow(totals)}</tfoot>
        </table>
      </div>
    </section>
  `;
}

function renderClientReceivablesReportWorkspace(model = {}) {
  const filters = model.filters || {};
  const totals = model.totals || {};
  return `
    <section class="client-receivables-report" aria-label="Client receivables report">
      ${leftAfterInsuranceHelpNodeHtml("clientReceivablesLeftAfterInsuranceHelp")}
      <div class="client-report-toolbar client-receivables-toolbar">
        ${clientReportCrumbHtml("Receivables")}
        <div class="client-report-toolbar-filters">
          <label>Client<select id="clientReportClientFilter">${renderClientReportOptions(model.options?.clients || [], filters.client || "All", "All clients")}</select></label>
          <label>Payer<select id="clientReportPayerFilter">${renderClientReportOptions(model.options?.payers || [], filters.payer || "All", "All payers")}</select></label>
          <label>From DOS<input id="clientReportFromDate" type="date" value="${escapeHtml(filters.fromDate || "")}"></label>
          <label>To DOS<input id="clientReportToDate" type="date" value="${escapeHtml(filters.toDate || "")}"></label>
          <label>Sort<select id="clientReportSortMode"><option>Sort by payer</option><option>Sort by balance</option></select></label>
          <div class="client-report-radio" role="radiogroup" aria-label="Group report by">
            <label><input type="radio" name="clientReportGrouping" value="payer" ${filters.groupBy !== "client" ? "checked" : ""}> Payer</label>
            <label><input type="radio" name="clientReportGrouping" value="client" ${filters.groupBy === "client" ? "checked" : ""}> Client</label>
          </div>
          ${hasPermission(PERMISSIONS.REPORT_EXPORT) ? '<button type="button" class="small-button client-report-export-btn" data-client-report-export aria-label="Export receivables report to CSV">Export CSV</button>' : ""}
        </div>
      </div>
      ${renderClientReportSlicerCollapse(model)}
      <div class="client-receivables-scorecards stmt-scorecards">
        <div class="stmt-metric"><span class="stmt-metric-label">AR Outstanding</span><strong class="stmt-metric-value">${clientReportMoneyLabel(totals.arOutstanding)}</strong></div>
        <div class="stmt-metric"><span class="stmt-metric-label">Over Payments</span><strong class="stmt-metric-value">${totals.overpayments == null ? "Unavailable" : `(${money(Math.abs(totals.overpayments))})`}</strong></div>
        <div class="stmt-metric"><span class="stmt-metric-label">AR &gt;90 Days</span><strong class="stmt-metric-value">${totals.arOver90Rate == null ? "Unavailable" : `${Math.round(totals.arOver90Rate * 100)}%`}</strong></div>
        <div class="stmt-metric"><span class="stmt-metric-label">Aging Breakdown</span><strong class="stmt-metric-value">${formatNounCount(totals.claims, "claim")}</strong></div>
      </div>
      <div class="client-receivables-visual">
        <div class="client-receivables-aging-panel">
          <div class="client-receivables-aging-head">
            <div><strong>AR Aging by ${filters.groupBy === "client" ? "Client" : "Payer"}</strong><span>${clientReportMoneyLabel(totals.arOutstanding)} insurance AR · <span title="${escapeHtml(LEFT_AFTER_INSURANCE_POSITIVE_HELP)}" aria-describedby="clientReceivablesLeftAfterInsuranceHelp">${clientReportMoneyLabel(totals.patientOutstanding)} left after insurance</span></span></div>
            <div class="client-receivables-legend"><span><i class="crb-legend--blue"></i> 0-120 days</span><span><i class="crb-legend--amber"></i> 120+ days</span></div>
          </div>
          <div class="client-receivables-buckets">
            ${(() => { const _crbMax = Math.max(1, ...model.buckets.map((_b) => Number(_b.insurance) || 0)); return model.buckets.map((_b) => renderClientReceivablesBucket(_b, _crbMax)).join(""); })()}
          </div>
        </div>
      </div>
      <div class="client-receivables-table-wrap">
        <table class="client-receivables-table">
          <thead>
            <tr>
              <th rowspan="2">Name</th>
              ${CLIENT_RECEIVABLE_BUCKETS.map((bucket) => `<th colspan="2">${escapeHtml(bucket.label)}</th>`).join("")}
              <th colspan="2">Totals</th>
            </tr>
            <tr>${Array.from({ length: CLIENT_RECEIVABLE_BUCKETS.length + 1 }).map(() => `<th>Insurance</th>${leftAfterInsuranceHeaderHtml("clientReceivablesLeftAfterInsuranceHelp")}`).join("")}</tr>
          </thead>
          <tbody>${model.rows.length ? model.rows.map(renderClientReceivablesTableRow).join("") : `<tr><td colspan="17">${escapeHtml(clientReceivablesEmptyNote(model))}</td></tr>`}</tbody>
          <tfoot>${renderClientReceivablesTotalsRow(model.buckets, totals)}</tfoot>
        </table>
      </div>
    </section>
  `;
}

// Choices are bounded and scoped independently from the displayed financial rows.
const clientReportFacetReads = new Map();
let clientReportFacetScope = "", clientReportFacetDisclosureScope = "", clientReportFacetDisclosureOpen = false;
function clientReportFacetScopeKey() {
  return JSON.stringify({ publication: reportReadPublicationIdentity(), base: trackerApiBase(), token: trackerApiToken(), scope: trackerAccessEnvelope("service"), report: clientActiveReport()?.key || "", filters: clientReportFilterValues() });
}
function syncClientReportFacetScope() {
  const scope = clientReportFacetScopeKey();
  if (scope !== clientReportFacetScope) {
    for (const record of clientReportFacetReads.values()) { record.controller?.abort(); clearTimeout(record.timer); }
    clientReportFacetReads.clear(); clientReportFacetScope = scope;
  }
  return scope;
}
function clientReportFacetDisclosureIdentity() {
  return JSON.stringify({ base: trackerApiBase(), token: trackerApiToken(), scope: trackerAccessEnvelope("service"), report: clientActiveReport()?.key || "" });
}
function clientReportFacetValues(body, key) { return (key === "client" ? body.options?.clients : key === "payer" ? body.options?.payers : body.facets?.[key]) || []; }
function clientReportFacetBindSelections(host) {
  host.querySelectorAll("[data-client-report-slicer]").forEach(button => {
    if (button.dataset.boundClientReportSlicer) return; button.dataset.boundClientReportSlicer = "1";
    button.addEventListener("click", () => {
      const key = button.dataset.clientReportSlicer;
      state.clientReportSlicers = normalizeClientReportSlicers(state.clientReportSlicers);
      state.clientReportSlicers[key] = toggleSlicerSelectionValue(key, state.clientReportSlicers[key] || [], button.dataset.clientReportSlicerValue);
      saveState(); clientReportAggregatePage = 1; clientReportAggregatePageIdentity = ""; renderClientReportsView();
    });
  });
}
function paintClientReportFacet(key) {
  const record = clientReportFacetReads.get(key); if (!record) return;
  const select = document.getElementById(key === "client" ? "clientReportClientFilter" : key === "payer" ? "clientReportPayerFilter" : "");
  const card = document.querySelector('[data-client-facet-card="' + key + '"]');
  const host = select?.parentElement || card; if (!host) return;
  if (record.status === "ok") {
    if (select) select.innerHTML = renderClientReportOptions(record.values, select.value, key === "client" ? "All clients" : "All payers");
    else {
      const selected = normalizeClientReportSlicers(state.clientReportSlicers)[key] || [], chosen = new Set(selected);
      const values = slicerChipValuesWithSelection(key, record.values, selected);
      const choices = card.querySelector(".client-pivot-slicer-values");
      choices.innerHTML = values.map(value => '<button type="button" class="client-pivot-slicer-val ' + (chosen.has(value) ? 'is-active' : '') + '" data-client-report-slicer="' + key + '" data-client-report-slicer-value="' + escapeHtml(value) + '">' + escapeHtml(value) + '</button>').join("") || '<span class="muted">No matching choices</span>';
      clientReportFacetBindSelections(choices);
    }
  }
  let controls = host.querySelector("[data-client-facet-choices]");
  if (!controls) {
    controls = document.createElement("div"); controls.className = "report-facet-choices"; controls.dataset.clientFacetChoices = key;
    controls.innerHTML = '<input type="search" data-client-facet-search aria-label="Search ' + escapeHtml(key === "client" ? "clients" : key === "payer" ? "payers" : CLIENT_REPORT_SLICERS.find(item => item.key === key)?.label || key) + '" placeholder="Search choices"><div class="tracker-choice-pager"><button type="button" data-client-facet-page="prev" class="small-button">Previous choices</button><span role="status"></span><button type="button" data-client-facet-page="next" class="small-button">Next choices</button><button type="button" data-client-facet-retry class="small-button" hidden>Retry</button></div>';
    host.appendChild(controls);
    controls.querySelector("input").addEventListener("input", event => {
      const current = clientReportFacetReads.get(key); clearTimeout(current?.timer);
      const scope = clientReportFacetScope, search = event.target.value;
      if (current) current.timer = setTimeout(() => { if (scope === clientReportFacetScopeKey()) requestClientReportFacets([key], { search, page: 1 }); }, 250);
    });
    controls.querySelectorAll("[data-client-facet-page]").forEach(button => button.addEventListener("click", () => {
      const current = clientReportFacetReads.get(key); if (!current) return;
      requestClientReportFacets([key], { search: current.search, page: Math.max(1, current.page + (button.dataset.clientFacetPage === "prev" ? -1 : 1)) });
    }));
    controls.querySelector("[data-client-facet-retry]").addEventListener("click", () => { const current = clientReportFacetReads.get(key); requestClientReportFacets([key], { search: current?.search || "", page: current?.page || 1 }); });
  }
  const input = controls.querySelector("input"); if (document.activeElement !== input) input.value = record.search;
  const meta = record.meta || {}, count = Number(meta.matchingOptions) || 0, size = Number(meta.pageSize) || 50;
  controls.querySelector('[role="status"]').textContent = record.status === "pending" ? "Loading choices…" : record.status === "error" ? "Choices unavailable" : (count ? ((record.page - 1) * size + 1) + "–" + Math.min(record.page * size, count) : "0") + " of " + count.toLocaleString();
  controls.querySelector('[data-client-facet-page="prev"]').disabled = record.status !== "ok" || record.page <= 1;
  controls.querySelector('[data-client-facet-page="next"]').disabled = record.status !== "ok" || !meta.hasMore;
  controls.querySelector('[data-client-facet-retry]').hidden = record.status !== "error";
}
function requestClientReportFacets(keys, preference) {
  if (!trackerApiEnabled() || !clientActiveReport()) return;
  const scope = syncClientReportFacetScope(), active = clientActiveReport();
  keys = [...new Set(keys)].filter(key => ["client", "payer", ...CLIENT_REPORT_SLICERS.map(item => item.key)].includes(key));
  if (!preference) keys = keys.filter(key => !clientReportFacetReads.has(key));
  if (!keys.length) return;
  const controller = new AbortController(), records = new Map(), facetOptions = {}, priorControllers = new Set();
  for (const key of keys) {
    const previous = clientReportFacetReads.get(key); clearTimeout(previous?.timer); if (previous?.controller) priorControllers.add(previous.controller);
    const record = { search: preference?.search || "", page: Math.max(1, Number(preference?.page) || 1), status: "pending", values: previous?.values || [], controller };
    records.set(key, record); clientReportFacetReads.set(key, record); facetOptions[key] = { search: record.search, page: record.page, pageSize: 50 }; paintClientReportFacet(key);
  }
  for (const prior of priorControllers) if (![...clientReportFacetReads.values()].some(record => record.controller === prior && record.status === "pending")) prior.abort();
  const params = clientReportOperationParams(active, "facets"); params.set("facetKeys", JSON.stringify(keys)); params.set("facetOptions", JSON.stringify(facetOptions));
  const timer = setTimeout(() => controller.abort(), 10000);
  reportSessionFetch(trackerApiBase() + "/api/client/reports/aggregate?" + params, { cache: "no-store", headers: reportSessionHeaders(), signal: controller.signal }).then(async response => {
    const body = await response.json();
    if (!response.ok || body.complete !== true || body.sourceMode !== "warehouse-prepared") throw new Error("Choices unavailable");
    if (controller.signal.aborted || scope !== clientReportFacetScopeKey()) return;
    for (const [key, record] of records) if (clientReportFacetReads.get(key) === record) { Object.assign(record, { status: "ok", values: clientReportFacetValues(body, key), meta: body.facetPages?.[key] || {}, page: Number(body.facetPages?.[key]?.page) || record.page }); paintClientReportFacet(key); }
  }).catch(() => {
    if (scope !== clientReportFacetScopeKey()) return;
    for (const [key, record] of records) if (clientReportFacetReads.get(key) === record) { record.status = "error"; paintClientReportFacet(key); }
  }).finally(() => clearTimeout(timer));
}
function bindClientReportFacetControls() {
  if (!trackerApiEnabled()) return;
  syncClientReportFacetScope();
  const body = clientReportAggregateApiState.response;
  for (const key of ["client", "payer"]) {
    if (!clientReportFacetReads.has(key) && body?.facetPages?.[key]) clientReportFacetReads.set(key, { status: "ok", search: "", page: 1, values: clientReportFacetValues(body, key), meta: body.facetPages[key] });
    paintClientReportFacet(key);
  }
  const details = document.querySelector(".client-report-slicer-collapse");
  if (details && !details.dataset.clientFacetBound) {
    details.dataset.clientFacetBound = "1";
    details.addEventListener("toggle", () => {
      if (!details.isConnected) return;
      clientReportFacetDisclosureScope = clientReportFacetDisclosureIdentity(); clientReportFacetDisclosureOpen = details.open;
      if (details.open) requestClientReportFacets(CLIENT_REPORT_SLICERS.map(item => item.key));
    });
  }
  if (details?.open) requestClientReportFacets(CLIENT_REPORT_SLICERS.map(item => item.key));
  for (const key of clientReportFacetReads.keys()) paintClientReportFacet(key);
}

function renderClientReportSlicerCollapse(model = {}) {
  const active = normalizeClientReportSlicers(model.filters?.slicers || state.clientReportSlicers || {});
  // One entry per BAND: an aging selection legitimately holds two labels for one band
  // while the two vocabularies coexist, and "Aging: 60-89, 61-90" reads like a filter bug.
  const activeParts = CLIENT_REPORT_SLICERS.filter((slicer) => (active[slicer.key] || []).length).map((slicer) => `${slicer.label}: ${activeSlicerSummaryValues(slicer.key, active[slicer.key] || []).map((value) => slicer.key === "action" ? slicerDisplayValue(slicer.key, value) : value).join(", ")}`);
  const activeCount = activeParts.length;
  const summary = activeCount ? activeParts.join(" · ") : "No filters applied";
  return `
    <details class="client-report-slicer-collapse"${(clientReportFacetDisclosureScope === clientReportFacetDisclosureIdentity() ? clientReportFacetDisclosureOpen : activeCount > 0) ? " open" : ""}>
      <summary class="client-report-slicer-toggle">
        <span class="slicer-toggle-label">Show filters <span aria-hidden="true">▾</span></span>
        <span class="slicer-summary">${escapeHtml(summary)}</span>
      </summary>
      ${renderClientReportSlicerPanel(model)}
    </details>
  `;
}

function renderClientReportSlicerPanel(model = {}) {
  const allClaims = trackerApiEnabled() ? [] : clientReportSourceClaims();
  const active = normalizeClientReportSlicers(model.filters?.slicers || state.clientReportSlicers || {});
  return `
    <section class="client-report-slicer-panel client-pivot-slicers" aria-label="Client report slicers">
      ${CLIENT_REPORT_SLICERS.map((slicer) => {
        // Chips are derived from the claims, so any selection this feed does not serve
        // (an aging twin from migrateAgingSlicerValues, or a value pushed past the
        // 18-chip cap) is appended rather than dropped — an active filter always has a
        // visible control, and an aging band has exactly one.
        const selectedValues = active[slicer.key] || [];
        const serverValues = Array.isArray(model.facets?.[slicer.key]) ? model.facets[slicer.key] : [];
        const values = slicerChipValuesWithSelection(slicer.key, (trackerApiEnabled() ? serverValues : clientReportSlicerValues(allClaims, slicer).slice(0, 18)), selectedValues);
        const selected = new Set(selectedValues);
        return `
          <article class="client-pivot-slicer" data-client-facet-card="${escapeHtml(slicer.key)}">
            <header>
              <strong>${escapeHtml(slicer.label)}</strong>
              ${selected.size ? `<button type="button" class="client-pivot-slicer-clear" data-client-report-slicer-clear="${escapeHtml(slicer.key)}">Clear</button>` : ""}
            </header>
            <div class="client-pivot-slicer-values">
              ${values.map((value) => `
                <button type="button" class="client-pivot-slicer-val ${selected.has(value) ? "is-active" : ""}" data-client-report-slicer="${escapeHtml(slicer.key)}" data-client-report-slicer-value="${escapeHtml(value)}">${escapeHtml(slicer.key === "action" ? slicerDisplayValue("action", value) : value)}</button>
              `).join("") || '<span class="muted">No values</span>'}
            </div>
          </article>
        `;
      }).join("")}
    </section>
  `;
}

function renderClientReceivablesBucket(bucket = {}, maxInsurance = 0) {
  const _val = Number(bucket.insurance) || 0;
  const _max = Number(maxInsurance) > 0 ? Number(maxInsurance) : 1;
  const _heightPct = _val > 0 ? Math.max(3, Math.round((_val / _max) * 100)) : 0;
  const _barTone = ["121-180", "181-365", "366+"].includes(bucket.key) ? "amber" : "blue";
  return `
    <article class="client-receivables-bucket client-receivables-bucket--${escapeHtml(bucket.tone)}">
      <header><span>${escapeHtml(bucket.label)}</span><strong>${clientReportMoneyLabel(bucket.insurance)}</strong></header>
      <div class="client-receivables-bucket-plot">
        <div class="client-receivables-bucket-bar client-receivables-bucket-bar--${_barTone}" style="height:${_heightPct}%" title="${escapeHtml(`${bucket.label}: ${clientReportMoneyLabel(bucket.insurance)}`)}"></div>
      </div>
    </article>
  `;
}

function crbMoneyCell(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '<span class="crb-unknown">Unavailable</span>';
  const _n = Number(value) || 0;
  return _n === 0 ? `<span class="crb-zero">${money(0)}</span>` : money(_n);
}

function renderClientReceivablesTableRow(row = {}) {
  const expanded = Boolean(row.expanded);
  return `
    <tr${expanded ? ' class="is-expanded"' : ""}>
      <td><button class="client-rcm-expand" type="button" data-client-report-expand="${escapeHtml(row.name)}" aria-expanded="${expanded ? "true" : "false"}" aria-label="${expanded ? "Collapse" : "Expand"} ${escapeHtml(row.name)}">${expanded ? "−" : "+"}</button>${escapeHtml(row.name)} <button class="small-button" type="button" data-client-report-drill="${escapeHtml(row.name)}">Service lines</button></td>
      ${CLIENT_RECEIVABLE_BUCKETS.map((bucket) => `<td>${crbMoneyCell(row.insurance[bucket.key])}</td><td>${crbMoneyCell(row.patient[bucket.key])}</td>`).join("")}
      <td>${clientReportMoneyLabel(row.totalInsurance)}</td><td>${clientReportMoneyLabel(row.totalPatient)}</td>
    </tr>
    ${expanded ? renderClientReceivablesDetailRow(row) : ""}
  `;
}

function renderClientReceivablesDetailRow(row = {}) {
  const details = row.details || [];
  const bucketLabel = (key) => (CLIENT_RECEIVABLE_BUCKETS.find((bucket) => bucket.key === key) || {}).label || key;
  const body = details.length
    ? details.map((item) => `
        <tr>
          <td>${escapeHtml(item.claimId)}</td>
          <td>${escapeHtml(item.label || "—")}</td>
          <td>${escapeHtml(item.dos || "—")}</td>
          <td>${escapeHtml(bucketLabel(item.bucketKey))}</td>
          <td>${clientReportMoneyLabel(item.insuranceAr)}</td>
          <td>${clientReportMoneyLabel(item.patientAr)}</td>
        </tr>
      `).join("")
    : '<tr><td colspan="6">No claim-level detail for this group.</td></tr>';
  return `
    <tr class="client-report-detail-row">
      <td colspan="17">
        <div class="client-report-detail">
          <table class="client-report-detail-table">
            <thead><tr><th>Claim</th><th>Client / Payer</th><th>DOS</th><th>Aging bucket</th><th>Insurance AR</th>${leftAfterInsuranceHeaderHtml("clientReceivablesLeftAfterInsuranceHelp")}</tr></thead>
            <tbody>${body}</tbody>
          </table>
          <small>Top ${formatNounCount(Math.min(details.length, 12), "service")} by insurance AR for ${escapeHtml(row.name)}.</small>
        </div>
      </td>
    </tr>
  `;
}

function renderClientReceivablesTotalsRow(buckets = [], totals = {}) {
  return `
    <tr>
      <td>Totals</td>
      ${buckets.map((bucket) => `<td>${clientReportMoneyLabel(bucket.insurance)}</td><td>${clientReportMoneyLabel(bucket.patient)}</td>`).join("")}
      <td>${clientReportMoneyLabel(totals.arOutstanding)}</td><td>${clientReportMoneyLabel(totals.patientOutstanding)}</td>
    </tr>
  `;
}

// Abbreviated money for tight chrome. The MAGNITUDE picks the K/M suffix, but
// the SIGN has to survive: this used to be `Math.abs(...)` all the way through
// and then `money(amount)`, so -$4,200 rendered as "$4.2K" and a refund read as
// a receipt. Negative is a direction, not a rounding detail.
function compactMoney(value = 0) {
  if (isUnknownMoney(value)) return MONEY_UNKNOWN_TEXT;
  const number = moneyNumber(value);
  const amount = Math.abs(number);
  const sign = number < 0 ? "-" : "";
  if (amount >= 1000000) return `${sign}$${(amount / 1000000).toFixed(amount >= 10000000 ? 0 : 2)}M`;
  if (amount >= 1000) return `${sign}$${(amount / 1000).toFixed(amount >= 10000 ? 0 : 1)}K`;
  return money(number);
}

function renderClientReportOptions(values = [], selected = "All", allLabel = "All") {
  const options = [...new Set(["All", ...values.filter(Boolean), ...(selected && selected !== "All" ? [selected] : [])])];
  return options.map((value) => {
    const label = value === "All" ? allLabel : value;
    return `<option value="${escapeHtml(value)}"${value === selected ? " selected" : ""}>${escapeHtml(label)}</option>`;
  }).join("");
}

function renderClientRcmGauge(gauge = {}) {
  const pct = gauge.value == null ? 0 : Math.max(0, Math.min(100, gauge.value * 100));
  const label = gauge.value == null ? "N/A" : `${Math.round(gauge.value * 100)}%`;
  return `
    <div class="client-rcm-gauge" style="--client-rcm-gauge:${pct}">
      <div class="client-rcm-gauge-ring"><strong>${escapeHtml(label)}</strong><span>${escapeHtml(gauge.label)}</span></div>
      <small>${escapeHtml(gauge.detail || "")}</small>
    </div>
  `;
}

function renderClientRcmBubbleChart(rows = [], groupBy = "payer") {
  if (!rows.length) return `<div class="client-rcm-empty">No chart rows for the active filters.</div>`;
  const knownRows = rows.filter((row) => row.receivables !== null && row.receivables !== undefined && row.receivables !== ""
    && row.overpayments !== null && row.overpayments !== undefined && row.overpayments !== ""
    && Number.isFinite(Number(row.receivables)) && Number.isFinite(Number(row.overpayments)));
  const omitted = rows.length - knownRows.length;
  if (!knownRows.length) return `<div class="client-rcm-empty">Chart unavailable because receivables or overpayment values are unknown for every group.</div>`;
  const width = 980;
  const height = 300;
  const maxReceivable = Math.max(1, ...knownRows.map((row) => Number(row.receivables)));
  const maxOverpayment = Math.max(1, ...knownRows.map((row) => Number(row.overpayments)));
  const maxClaims = Math.max(1, ...knownRows.map((row) => Number(row.claims) || 0));
  const plot = { left: 42, right: 24, top: 26, bottom: 44 };
  const innerW = width - plot.left - plot.right;
  const innerH = height - plot.top - plot.bottom;
  const points = knownRows.map((row, index) => {
    const x = plot.left + (row.receivables / maxReceivable) * innerW;
    const y = plot.top + innerH - (row.overpayments / maxOverpayment) * innerH;
    const r = 5 + Math.sqrt(row.claims / maxClaims) * 20;
    const color = row.overpayments > 0 ? "#ef8fa0" : "#47b5d9";
    return `
      <circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="${r.toFixed(2)}" fill="${color}" opacity="0.72"><title>${escapeHtml(`${row.name}: ${money(row.receivables)} receivables, ${money(row.overpayments)} overpayments, ${formatNounCount(row.claims, "claim")}`)}</title></circle>
      ${index < 10 ? `<text x="${Math.min(width - 150, x + r + 5).toFixed(2)}" y="${Math.max(12, y + 4).toFixed(2)}">${escapeHtml(truncateChartLabel(row.name, 22))}</text>` : ""}
    `;
  }).join("");
  return `
    ${omitted ? `<p class="report-dataset-note report-partial-note" role="status">${escapeHtml(formatNounCount(omitted, "group"))} omitted from this chart because a plotted money value is unavailable; the table retains ${omitted === 1 ? "it" : "them"}.</p>` : ""}
    <svg class="client-rcm-bubble-chart" viewBox="0 0 ${width} ${height}" role="img" aria-label="RCM receivables and overpayments by ${escapeHtml(groupBy)}">
      <line x1="${plot.left}" x2="${width - plot.right}" y1="${plot.top + innerH}" y2="${plot.top + innerH}" class="client-rcm-axis"></line>
      <line x1="${plot.left}" x2="${plot.left}" y1="${plot.top}" y2="${plot.top + innerH}" class="client-rcm-axis"></line>
      <line x1="${plot.left}" x2="${width - plot.right}" y1="${plot.top + innerH * 0.5}" y2="${plot.top + innerH * 0.5}" class="client-rcm-grid"></line>
      <line x1="${plot.left + innerW * 0.5}" x2="${plot.left + innerW * 0.5}" y1="${plot.top}" y2="${plot.top + innerH}" class="client-rcm-grid"></line>
      ${points}
      <text x="${plot.left}" y="${height - 12}" class="client-rcm-axis-label">Receivables</text>
      <text x="${width - 210}" y="${height - 12}" class="client-rcm-axis-label">${money(maxReceivable)}</text>
      <text x="${plot.left + 4}" y="16" class="client-rcm-axis-label">Overpayments</text>
    </svg>
  `;
}

function renderClientRcmTableRow(row = {}) {
  const expanded = Boolean(row.expanded);
  return `
    <tr${expanded ? ' class="is-expanded"' : ""}>
      <td><button class="client-rcm-expand" type="button" data-client-report-expand="${escapeHtml(row.name)}" aria-expanded="${expanded ? "true" : "false"}" aria-label="${expanded ? "Collapse" : "Expand"} ${escapeHtml(row.name)}">${expanded ? "−" : "+"}</button>${escapeHtml(row.name)} <button class="small-button" type="button" data-client-report-drill="${escapeHtml(row.name)}">Service lines</button></td>
      <td>${clientReportMoneyLabel(row.charges)}</td><td>${clientReportMoneyLabel(row.patientCharges)}</td>
      <td>${clientReportMoneyLabel(row.salesAdjustments)}</td><td>$0.00</td>
      <td>${clientReportMoneyLabel(row.adjustedCharges)}</td><td>${clientReportMoneyLabel(row.patientCharges)}</td>
      <td>${clientReportMoneyLabel(row.payments)}</td><td>${clientReportMoneyLabel(row.patientPayments)}</td>
      <td>${percentOrZero(row.grossCollectionRate)}</td><td>${percentOrZero(row.patientCollectionRate)}</td>
      <td>${clientReportMoneyLabel(row.badDebt)}</td><td>$0.00</td>
      <td>${percentOrZero(row.netCollectionRate)}</td><td>${percentOrZero(row.patientCollectionRate)}</td>
      <td>${clientReportMoneyLabel(row.receivables)}</td><td>${clientReportMoneyLabel(row.patientReceivables)}</td>
      <td>${clientReportMoneyLabel(row.overpayments)}</td><td>$0.00</td>
    </tr>
    ${expanded ? renderClientRcmDetailRow(row) : ""}
  `;
}

function renderClientRcmDetailRow(row = {}) {
  const details = row.details || [];
  const body = details.length
    ? details.map((item) => `
        <tr>
          <td>${escapeHtml(item.claimId)}</td>
          <td>${escapeHtml(item.label)}</td>
          <td>${clientReportMoneyLabel(item.payments)}</td>
          <td>${clientReportMoneyLabel(item.receivables)}</td>
          <td>${clientReportMoneyLabel(item.overpayments)}</td>
        </tr>
      `).join("")
    : '<tr><td colspan="5">No claim-level detail for this group.</td></tr>';
  return `
    <tr class="client-report-detail-row">
      <td colspan="19">
        <div class="client-report-detail">
          <table class="client-report-detail-table">
            <thead><tr><th>Claim</th><th>Client / Payer</th><th>Payments</th><th>Receivables</th><th>Overpayments</th></tr></thead>
            <tbody>${body}</tbody>
          </table>
          <small>Top ${formatNounCount(Math.min(details.length, 12), "claim")} by receivable for ${escapeHtml(row.name)}.</small>
        </div>
      </td>
    </tr>
  `;
}

function renderClientRcmTotalsRow(totals = {}) {
  return `
    <tr>
      <td>Totals</td><td>${clientReportMoneyLabel(totals.charges)}</td><td>${clientReportMoneyLabel(totals.patientCharges)}</td><td>${clientReportMoneyLabel(totals.salesAdjustments)}</td><td>$0.00</td><td>${clientReportMoneyLabel(totals.adjustedCharges)}</td><td>${clientReportMoneyLabel(totals.patientCharges)}</td><td>${clientReportMoneyLabel(totals.payments)}</td><td>${clientReportMoneyLabel(totals.patientPayments)}</td><td>${percentOrZero(totals.grossCollectionRate)}</td><td>${percentOrZero(totals.patientCollectionRate)}</td><td>$0.00</td><td>$0.00</td><td>${percentOrZero(totals.netCollectionRate)}</td><td>${percentOrZero(totals.patientCollectionRate)}</td><td>${clientReportMoneyLabel(totals.receivables)}</td><td>${clientReportMoneyLabel(totals.patientReceivables)}</td><td>${clientReportMoneyLabel(totals.overpayments)}</td><td>$0.00</td>
    </tr>
  `;
}

function percentOrZero(value) {
  if (value == null || Number.isNaN(Number(value))) return "Unavailable";
  return `${Math.round(value * 100)}%`;
}

function clientReportRateCsv(value) {
  return value == null || Number.isNaN(Number(value)) ? "" : (Math.round(value * 1000) / 10).toFixed(1);
}

function exportClientReportCsv() {
  const active = clientActiveReport();
  if (!active) return;
  if (trackerApiEnabled()) {
    exportClientReportServerCsv(active);
    return;
  }
  const accessibleClaims = clientReportSourceClaims();
  const filters = normalizeClientReportFilters(clientReportFilterValues(), accessibleClaims);
  const claims = clientReportFilteredClaims(accessibleClaims, filters);
  const groupingLabel = filters.groupBy === "client" ? "Client" : "Payer";
  let headers = [];
  let bodyRows = [];
  let filename = "";
  if (active.key === "receivables") {
    const model = clientReceivablesReportModel(claims, filters);
    headers = [groupingLabel, ...CLIENT_RECEIVABLE_BUCKETS.flatMap((bucket) => [`${bucket.label} Insurance`, `${bucket.label} Patient`]), "Total Insurance", "Total Patient"];
    bodyRows = model.rows.map((row) => [row.name, ...CLIENT_RECEIVABLE_BUCKETS.flatMap((bucket) => [row.insurance[bucket.key] || 0, row.patient[bucket.key] || 0]), row.totalInsurance, row.totalPatient]);
    bodyRows.push(["Totals", ...model.buckets.flatMap((bucket) => [bucket.insurance, bucket.patient]), model.totals.arOutstanding, model.totals.patientOutstanding]);
    filename = `client-receivables-${filters.groupBy}-${localDateString()}.csv`;
  } else {
    const model = clientRcmReportModel(claims, filters);
    headers = [
      groupingLabel,
      "Charges (Ins)", "Charges (Pat)", "Sales Adjustments (Ins)", "Sales Adjustments (Pat)",
      "Adjusted Charges (Ins)", "Adjusted Charges (Pat)", "Payments (Ins)", "Payments (Pat)",
      "GCR % (Ins)", "GCR % (Pat)", "Bad Debt (Ins)", "Bad Debt (Pat)",
      "NCR % (Ins)", "NCR % (Pat)", "Receivables (Ins)", "Receivables (Pat)",
      "Overpayments (Ins)", "Overpayments (Pat)"
    ];
    bodyRows = model.rows.map((row) => [
      row.name, row.charges, row.patientCharges, row.salesAdjustments, 0,
      row.adjustedCharges, row.patientCharges, row.payments, row.patientPayments,
      clientReportRateCsv(row.grossCollectionRate), clientReportRateCsv(row.patientCollectionRate), row.badDebt || 0, 0,
      clientReportRateCsv(row.netCollectionRate), clientReportRateCsv(row.patientCollectionRate), row.receivables, row.patientReceivables,
      row.overpayments, 0
    ]);
    const t = model.totals;
    bodyRows.push([
      "Totals", t.charges, t.patientCharges, t.salesAdjustments, 0,
      t.adjustedCharges, t.patientCharges, t.payments, t.patientPayments,
      clientReportRateCsv(t.grossCollectionRate), clientReportRateCsv(t.patientCollectionRate), 0, 0,
      clientReportRateCsv(t.netCollectionRate), clientReportRateCsv(t.patientCollectionRate), t.receivables, t.patientReceivables,
      t.overpayments, 0
    ]);
    filename = `client-rcm-performance-${filters.groupBy}-${localDateString()}.csv`;
  }
  const csv = [headers, ...bodyRows].map((cols) => cols.map(csvEscape).join(",")).join("\r\n");
  downloadTextPayload(csv, filename, "text/csv;charset=utf-8");
  showToast(`${active.title} exported to CSV.`);
}

function clientReportMoneyLabel(value) {
  return value === null || value === undefined || !Number.isFinite(Number(value)) ? "Unavailable" : money(value);
}

function clientReportOperationParams(active, operation, groupName = "") {
  const filters = clientReportFilterValues();
  return new URLSearchParams({ operation, report: active.key, groupBy: filters.groupBy || "payer", client: filters.client || "All", payer: filters.payer || "All",
    fromDate: filters.fromDate || "", toDate: filters.toDate || "", slicers: JSON.stringify(normalizeClientReportSlicers(filters.slicers || state.clientReportSlicers || {})),
    page: String(operation === "drill" ? (clientReportServerDrillState.page || 1) : 1), pageSize: "50", groupName,
    expectedGeneration: clientReportAggregateApiState.response?.generation || "" });
}

async function exportClientReportServerCsv(active) {
  if (!hasPermission(PERMISSIONS.REPORT_EXPORT)) { preparedReportExportResetScope(); return; }
  const scope = preparedReportExportResetScope(); if (!scope || !active) return;
  const generation = clientReportAggregateApiState.response?.generation || "";
  const key = JSON.stringify({ scope, report: active.key, generation, publication: reportReadPublicationIdentity(), filters: clientReportFilterValues() });
  if (preparedReportExportRequests.has(key)) return;
  preparedReportExportRequests.add(key); renderPreparedReportExportStatus();
  try {
    const response = await reportSessionFetch(trackerApiBase() + "/api/client/reports/aggregate?" + clientReportOperationParams(active, "export"), { cache: "no-store", headers: reportSessionHeaders() });
    const body = await response.json().catch(() => ({}));
    if (preparedReportExportAccess() !== scope) return;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      showToast("Your session expired. Sign in again, then export.", "error");
      return;
    }
    if (response.status === 200) {
      const returnedGeneration = body.generationId || body.generation || "";
      if (generation && returnedGeneration && generation !== returnedGeneration) throw new Error("The export version did not match the requested report.");
      const rowCount = downloadCompleteInlineCsv(body, { filename: `tms-${active.key}-${localDateString()}.csv` });
      if (rowCount) showToast(`Exported ${formatCount(rowCount)} complete report row${rowCount === 1 ? "" : "s"}.`);
      return;
    }
    if (response.status !== 202 || !(body.id || body.jobId)) throw new Error(body.error?.message || body.message || "The complete export could not be queued.");
    if (generation && body.generationId && generation !== body.generationId) throw new Error("The export version did not match the requested report.");
    const id = body.id || body.jobId, previous = preparedReportExportJobs.get(id);
    const job = { ...previous, ...body, id, scope, report: active.key === "receivables" ? "clientReceivables" : "clientRcm" };
    preparedReportExportJobs.set(id, job);
    while (preparedReportExportJobs.size > 50) preparedReportExportJobs.delete(preparedReportExportJobs.keys().next().value);
    renderPreparedReportExportStatus();
    if (job.status === "completed") await downloadPreparedReportCsv(job);
    else { showToast("Your export is being prepared. You can keep working."); schedulePreparedReportExportPoll(); }
  } catch (error) { if (preparedReportExportAccess() === scope) { showToast("We couldn’t create your download. Please try again.", "error"); restorePreparedReportExportJobs(true); } }
  finally { preparedReportExportRequests.delete(key); renderPreparedReportExportStatus(); }
}

let clientReportDrillRequestSequence = 0;
function requestClientReportServerDrill(active, groupName) {
  const base = trackerApiBase(), token = trackerApiToken();
  const requestId = ++clientReportDrillRequestSequence;
  const requestPage = clientReportServerDrillState.page || 1;
  const key = JSON.stringify({ base, token, scope: trackerAccessEnvelope("service"), report: active.key, filters: clientReportFilterValues(), groupName, page: requestPage, pageIdentity: clientReportAggregatePageIdentity });
  const current = () => requestId === clientReportDrillRequestSequence && clientReportServerDrillState.key === key && JSON.stringify({ base: trackerApiBase(), token: trackerApiToken(), scope: trackerAccessEnvelope("service"), report: active.key, filters: clientReportFilterValues(), groupName, page: clientReportServerDrillState.page || 1, pageIdentity: clientReportAggregatePageIdentity }) === key;
  clientReportServerDrillState = { ...clientReportServerDrillState, status: "pending", key, groupName, response: null, error: "", slow: false };
  renderClientReportsView();
  reportReadJson("client-drill", base + "/api/client/reports/aggregate?" + clientReportOperationParams(active, "drill", groupName), { cache: "no-store", headers: { ...(token ? { authorization: "Bearer " + token } : {}) } }, () => {
    if (current()) { clientReportServerDrillState.slow = true; renderClientReportsView(); }
  }).then(({ response, body }) => {
    if (!current()) return;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      clientReportServerDrillState = { ...clientReportServerDrillState, status: "error", response: null, error: "Your session expired. Sign in again — this page will still be here." };
    } else {
      clientReportServerDrillState = !response.ok || body.complete !== true || body.sourceMode !== "warehouse-prepared"
        ? { ...clientReportServerDrillState, status: "error", response: null, error: reportLoadFailureReason(body.message || "Drill-down unavailable.") }
        : { ...clientReportServerDrillState, status: "ok", response: body, error: "" };
    }
    renderClientReportsView();
  }).catch((error) => {
    if (current()) { clientReportServerDrillState = { ...clientReportServerDrillState, status: "error", response: null, error: reportLoadFailureReason(error.message) }; renderClientReportsView(); }
  });
}

function renderClientReportServerDrill(result={}) {
  const rows=Array.isArray(result.rows)?result.rows:[];
  const page=result.page||{}; const pager=Number(page.totalPages)>1?`<nav class="client-report-server-pager"><button type="button" data-client-server-drill-page="prev" ${Number(page.number)<=1?"disabled":""}>Previous</button><span>Page ${Number(page.number)} of ${Number(page.totalPages)}</span><button type="button" data-client-server-drill-page="next" ${Number(page.number)>=Number(page.totalPages)?"disabled":""}>Next</button></nav>`:"";
  return `<section class="client-report-drill"><button type="button" class="small-button" data-client-server-drill-back>Back to report</button><h3>${escapeHtml(result.groupName||"Service lines")}</h3>${leftAfterInsuranceHelpNodeHtml("clientDrillLeftAfterInsuranceHelp")}${result.financialCompleteness?.complete===false?'<p class="notice-card">Some money is unavailable in this generation; affected cells are shown as unavailable.</p>':""}<div class="report-table-wrap"><table><thead><tr><th>Claim</th><th>Service</th><th>Client</th><th>Payer</th><th>DOS</th><th>Insurance AR</th>${leftAfterInsuranceHeaderHtml("clientDrillLeftAfterInsuranceHelp")}</tr></thead><tbody>${rows.map((row)=>`<tr><td>${escapeHtml(row.claimId)}</td><td>${escapeHtml(row.serviceId)}</td><td>${escapeHtml(row.client)}</td><td>${escapeHtml(row.payer)}</td><td>${row.dos?formatDate(row.dos):"—"}</td><td>${row.insuranceAr==null?"Unavailable":money(row.insuranceAr)}</td><td>${row.patientAr==null?"Unavailable":money(row.patientAr)}</td></tr>`).join("")||'<tr><td colspan="7">No service lines in this group.</td></tr>'}</tbody></table></div>${pager}</section>`;
}

function bindClientReportControls() {
  bindClientReportFacetControls();
  Array.from(document.querySelectorAll("#clientPivotBody select, #clientPivotBody input")).forEach((control) => {
    if (control.dataset.boundClientReport || control.hasAttribute("data-client-facet-search")) return;
    control.dataset.boundClientReport = "1";
    control.addEventListener("change", () => { clientReportAggregatePage = 1; clientReportAggregatePageIdentity = ""; renderClientReportsView(); });
    control.addEventListener("input", () => {
      if (control.type === "date") { clientReportAggregatePage = 1; clientReportAggregatePageIdentity = ""; renderClientReportsView(); }
    });
  });
  Array.from(document.querySelectorAll("[data-client-report]")).forEach((button) => {
    if (button.dataset.boundClientReportNav) return;
    button.dataset.boundClientReportNav = "1";
    button.addEventListener("click", () => {
      openClientReportRoute(button.dataset.clientReport || "rcm-performance");
    });
  });
  Array.from(document.querySelectorAll("[data-client-report-back]")).forEach((button) => {
    if (button.dataset.boundClientReportBack) return;
    button.dataset.boundClientReportBack = "1";
    button.addEventListener("click", () => openClientReportRoute(""));
  });
  Array.from(document.querySelectorAll("[data-client-report-export]")).forEach((button) => {
    if (button.dataset.boundClientReportExport) return;
    button.dataset.boundClientReportExport = "1";
    button.addEventListener("click", () => exportClientReportCsv());
  });
  Array.from(document.querySelectorAll("[data-client-report-expand]")).forEach((button) => {
    if (button.dataset.boundClientReportExpand) return;
    button.dataset.boundClientReportExpand = "1";
    button.addEventListener("click", () => {
      const active = clientActiveReport();
      if (active) toggleClientReportExpandedRow(active.key, button.dataset.clientReportExpand || "");
    });
  });
  Array.from(document.querySelectorAll("[data-client-report-drill]")).forEach((button) => {
    if (button.dataset.boundClientReportDrill) return;
    button.dataset.boundClientReportDrill = "1";
    button.addEventListener("click", () => {
      const active = clientActiveReport();
      if (!active) return;
      const groupBy = clientReportFilterValues().groupBy === "client" ? "client" : "payer";
      const name = button.dataset.clientReportDrill || "";
      if (trackerApiEnabled()) { clientReportServerDrillState.page = 1; requestClientReportServerDrill(active, name); return; }
      const dimLabel = `${groupBy === "client" ? "Client" : "Payer"}: ${name}`;
      clientReportDrillIntoRow({
        reportKey: active.key,
        label: dimLabel,
        dims: [dimLabel],
        groupBy,
        name,
        predicate: (claim) => clientReportGroupLabel(claim, groupBy) === name
      });
    });
  });
  Array.from(document.querySelectorAll("[data-client-report-slicer]")).forEach((button) => {
    if (button.dataset.boundClientReportSlicer) return;
    button.dataset.boundClientReportSlicer = "1";
    button.addEventListener("click", () => {
      const key = button.dataset.clientReportSlicer;
      const value = button.dataset.clientReportSlicerValue;
      state.clientReportSlicers = normalizeClientReportSlicers(state.clientReportSlicers);
      // Deselecting clears the whole aging band (toggleSlicerSelectionValue): the twin
      // label carries no chip of its own on this data-derived strip, so leaving it
      // selected would be an invisible filter matching nothing.
      state.clientReportSlicers[key] = toggleSlicerSelectionValue(key, state.clientReportSlicers[key] || [], value);
      saveState();
      clientReportAggregatePage = 1;
      clientReportAggregatePageIdentity = "";
      renderClientReportsView();
    });
  });
  Array.from(document.querySelectorAll("[data-client-report-slicer-clear]")).forEach((button) => {
    if (button.dataset.boundClientReportSlicerClear) return;
    button.dataset.boundClientReportSlicerClear = "1";
    button.addEventListener("click", () => {
      state.clientReportSlicers = normalizeClientReportSlicers(state.clientReportSlicers);
      state.clientReportSlicers[button.dataset.clientReportSlicerClear] = [];
      saveState();
      clientReportAggregatePage = 1;
      clientReportAggregatePageIdentity = "";
      renderClientReportsView();
    });
  });
  document.querySelector("[data-client-server-drill-retry]")?.addEventListener("click", () => requestClientReportServerDrill(clientActiveReport(), clientReportServerDrillState.groupName || ""));
  document.querySelector("[data-client-server-drill-back]")?.addEventListener("click", () => {
    clientReportServerDrillState = { status: "idle", key: "", response: null, error: "", page: 1 };
    renderClientReportsView();
  });
  document.querySelectorAll("[data-client-server-drill-page]").forEach((button)=>button.addEventListener("click",()=>{const current=clientReportServerDrillState.response;if(!current)return;clientReportServerDrillState.page=Math.max(1,(clientReportServerDrillState.page||1)+(button.dataset.clientServerDrillPage==="prev"?-1:1));requestClientReportServerDrill(clientActiveReport(),current.groupName||"");}));
  Array.from(document.querySelectorAll("[data-client-report-page]")).forEach((button) => {
    button.addEventListener("click", () => {
      const pages = Math.max(1, Number(clientReportAggregateApiState.response?.page?.totalPages) || 1);
      const action = button.dataset.clientReportPage;
      clientReportAggregatePage = action === "first" ? 1 : action === "last" ? pages : Math.min(pages, Math.max(1, clientReportAggregatePage + (action === "prev" ? -1 : 1)));
      renderClientReportsView();
    });
  });
}

const CLIENT_PIVOT_SLICERS = [
  { key: "patClassification", label: "Pat Classification", get: (c) => c.patClassification },
  { key: "primaryPayer",      label: "Primary Payer",      get: (c) => c.primaryPayer },
  { key: "match",             label: "Match",              get: (c) => c.match },
  { key: "aging",             label: "Aging",              get: (c) => c.aging },
  { key: "action",            label: "Follow up Action",   get: (c) => followUpFilterRowValue(c) },
  { key: "network",           label: "In/Out of Network",  get: (c) => c.network },
  { key: "claimStatus",       label: "Claim Status",       get: (c) => c.claimStatus },
  { key: "claimClassification", label: "Cla Classification", get: (c) => c.claimClassification },
  { key: "secondaryPayer",    label: "Secondary Payer",    get: (c) => c.secondaryPayer }
];

const CLIENT_PIVOT_METRICS = [
  { key: "srvcDateCount",       label: "Count of Srvc Date",                          type: "int" },
  { key: "units",               label: "Sum of Units",                                type: "int" },
  { key: "primaryExpected",     label: "Sum of Primary Expected Total",               type: "money" },
  { key: "secondaryExpected",   label: "Sum of Secondary Expected Total",             type: "money" },
  { key: "totalExpected",       label: "Sum of Total Expected",                       type: "money" },
  { key: "writeOff",            label: "Sum of Write-off Amount",                     type: "money" },
  { key: "positivePatBal",      label: "Sum of Left After Insurance (+)",             type: "money", internalOnly: true },
  { key: "expectedNoWoPr",      label: "Sum of Expected Ins. Reimbursement W/O Write-offs & PR", type: "money" },
  { key: "totalInsAmtPaid",     label: "Sum of Total Ins Amt Paid",                   type: "money" },
  { key: "payerBalance",        label: "Sum of Payer Balance",                        type: "money" },
  { key: "collectionPct",       label: "Sum of Collections %",                        type: "percent" }
];

// Left After Insurance is internal-only (owner, 2026-09-23): an external client
// role does not get the column. The figure is still computed - Expected W/O
// Write-offs & PR subtracts it - so no other cell changes.
function clientPivotMetricColumns() {
  return CLIENT_PIVOT_METRICS.filter((metric) => !(metric.internalOnly && isClientRole()));
}

function clientPivotMetricHeaderHtml(metric) {
  return metric.key === "positivePatBal"
    ? `<th class="client-pivot-th-col" title="${escapeHtml(LEFT_AFTER_INSURANCE_POSITIVE_HELP)}" aria-describedby="clientPivotLeftAfterInsuranceHelp">${escapeHtml(metric.label)}${leftAfterInsuranceHelpNodeHtml("clientPivotLeftAfterInsuranceHelp")}</th>`
    : `<th class="client-pivot-th-col">${escapeHtml(metric.label)}</th>`;
}

function clientPivotState() {
  state.clientPivot = normalizeClientPivotState(state.clientPivot);
  return state.clientPivot;
}

function renderClientMonthlyPivot() {
  const slicersHost = document.getElementById("clientPivotSlicers");
  const bodyHost = document.getElementById("clientPivotBody");
  if (!slicersHost || !bodyHost) return;
  const pivot = clientPivotState();
  const allClaims = clientReportSourceClaims();
  // Apply slicer filters
  const filtered = allClaims.filter((claim) => {
    return CLIENT_PIVOT_SLICERS.every((slicer) => {
      const active = pivot.slicers[slicer.key] || [];
      if (!active.length) return true;
      const v = String(slicer.get(claim) ?? "(blank)") || "(blank)";
      return active.includes(v);
    });
  });
  const buckets = buildMonthlyPivotBuckets(filtered);
  // Render slicers
  slicersHost.innerHTML = CLIENT_PIVOT_SLICERS.map((slicer) => {
    const valueCounts = new Map();
    allClaims.forEach((claim) => {
      const v = String(slicer.get(claim) ?? "(blank)") || "(blank)";
      valueCounts.set(v, (valueCounts.get(v) || 0) + 1);
    });
    const dataValues = [...valueCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 24)
      .map(([v]) => v);
    const selectedValues = pivot.slicers[slicer.key] || [];
    // Chips come from the DATA, so a selection this feed does not serve — an aging band
    // expanded across both vocabularies, or a value pushed past the 24-chip cap — would
    // render no chip at all and could never be cleared. One chip per band, always clearable.
    const values = slicerChipValuesWithSelection(slicer.key, dataValues, selectedValues);
    const active = new Set(selectedValues);
    return `
      <section class="client-pivot-slicer">
        <header class="client-pivot-slicer-head">
          <strong>${escapeHtml(slicer.label)}</strong>
          ${active.size ? `<button type="button" class="client-pivot-slicer-clear" data-pivot-slicer-clear="${escapeHtml(slicer.key)}">Clear</button>` : ""}
        </header>
        <div class="client-pivot-slicer-values">
          ${values.map((v) => `
            <button type="button" class="client-pivot-slicer-val ${active.has(v) ? "is-active" : ""}" data-pivot-slicer="${escapeHtml(slicer.key)}" data-pivot-val="${escapeHtml(v)}">${escapeHtml(slicer.key === "action" ? slicerDisplayValue("action", v) : v)}</button>
          `).join("")}
        </div>
      </section>
    `;
  }).join("");
  // Wire slicer toggles
  slicersHost.querySelectorAll("[data-pivot-slicer]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.dataset.pivotSlicer;
      const val = btn.dataset.pivotVal;
      // Deselecting clears the whole aging band, twin label included — the twin has no
      // chip of its own here, so it would otherwise survive as an invisible filter.
      pivot.slicers[key] = toggleSlicerSelectionValue(key, pivot.slicers[key] || [], val);
      saveState();
      renderClientMonthlyPivot();
    });
  });
  slicersHost.querySelectorAll("[data-pivot-slicer-clear]").forEach((btn) => {
    btn.addEventListener("click", () => {
      pivot.slicers[btn.dataset.pivotSlicerClear] = [];
      saveState();
      renderClientMonthlyPivot();
    });
  });
  // Render body
  if (pivot.view === "chart") {
    bodyHost.innerHTML = renderClientPivotChart(buckets);
    const schedule = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (callback) => callback();
    schedule(() => renderClientPivotChartPlot(buckets));
  } else {
    bodyHost.innerHTML = renderClientPivotTable(buckets);
  }
  // Wire view-toggle buttons
  document.querySelectorAll(".client-pivot-view-btn[data-pivot-view]").forEach((btn) => {
    btn.classList.toggle("is-active", btn.dataset.pivotView === pivot.view);
    if (btn.dataset.boundClientPivot) return;
    btn.dataset.boundClientPivot = "1";
    btn.addEventListener("click", () => {
      pivot.view = btn.dataset.pivotView;
      saveState();
      renderClientMonthlyPivot();
    });
  });
}

function buildMonthlyPivotBuckets(claims) {
  const months = new Map();
  let grand = emptyPivotRow();
  claims.forEach((claim) => {
    (claim.services || []).forEach((service) => {
      const dos = service.dos || claim.dos || "";
      const normalizedDos = normalizeDateValue(dos);
      if (!normalizedDos) return;
      const ym = normalizedDos.slice(0, 7);
      const row = months.get(ym) || emptyPivotRow();
      const sFin = (() => { try { return serviceFinancialView(service, claim); } catch (_) { return {}; } })();
      const expected = moneyNumber(sFin.expectedReimbursement ?? service.expected ?? 0);
      const paid = moneyNumber(sFin.insurancePaid ?? service.paid ?? 0);
      const writeOff = moneyNumber(service.writeOffAmount ?? service.writeoffAmount ?? 0);
      const positivePatBal = Math.max(0, moneyNumber(service.patientBalance ?? 0));
      const payerBalance = Math.max(0, moneyNumber(sFin.sourceOutstanding ?? 0));
      const primaryExpected = moneyNumber(sFin.primaryExpected ?? sFin.expectedReimbursement ?? 0);
      const secondaryExpected = moneyNumber(sFin.secondaryExpected ?? 0);
      const expectedNoWoPr = Math.max(0, expected - writeOff - positivePatBal);
      const units = quantityNumber(service.units);
      [row, grand].forEach((r) => {
        r.srvcDateCount += 1;
        r.units += units;
        r.primaryExpected += primaryExpected;
        r.secondaryExpected += secondaryExpected;
        r.totalExpected += expected;
        r.writeOff += writeOff;
        r.positivePatBal += positivePatBal;
        r.expectedNoWoPr += expectedNoWoPr;
        r.totalInsAmtPaid += paid;
        r.payerBalance += payerBalance;
      });
      months.set(ym, row);
    });
  });
  const rows = [...months.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([ym, r]) => ({ label: ym, ...r, collectionPct: r.totalExpected > 0 ? (r.totalInsAmtPaid / r.totalExpected) : null }));
  grand.collectionPct = grand.totalExpected > 0 ? (grand.totalInsAmtPaid / grand.totalExpected) : null;
  return { rows, grand };
}

function emptyPivotRow() {
  return { srvcDateCount: 0, units: 0, primaryExpected: 0, secondaryExpected: 0, totalExpected: 0, writeOff: 0, positivePatBal: 0, expectedNoWoPr: 0, totalInsAmtPaid: 0, payerBalance: 0 };
}

function monthLabelFromYearMonth(value) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(value || ""));
  if (!match) return "";
  const month = Number(match[2]);
  const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return labels[month - 1] || "";
}

function fmtPivotValue(value, type) {
  if (value == null) return "—";
  if (type === "money") return money(value);
  if (type === "percent") return `${(value * 100).toFixed(2)}%`;
  if (type === "int") return Math.round(value).toLocaleString();
  return String(value);
}

function renderClientPivotTable(buckets) {
  if (!buckets.rows.length) return `<div class="client-pivot-empty">No service-line rows match the active slicers.</div>`;
  const yearGroups = new Map();
  buckets.rows.forEach((r) => {
    const [y] = r.label.split("-");
    if (!yearGroups.has(y)) yearGroups.set(y, []);
    yearGroups.get(y).push({ ...r, monthLabel: monthLabelFromYearMonth(r.label) });
  });
  const pivotMetrics = clientPivotMetricColumns();
  return `
    <div class="client-pivot-table-wrap">
      <table class="client-pivot-table">
        <thead>
          <tr>
            <th class="client-pivot-th-row">Row Labels</th>
            ${pivotMetrics.map(clientPivotMetricHeaderHtml).join("")}
          </tr>
        </thead>
        <tbody>
          ${[...yearGroups.entries()].map(([year, rows]) => {
            const yearTotals = rows.reduce((acc, r) => {
              pivotMetrics.forEach((m) => {
                if (m.key === "collectionPct") return; // computed below
                acc[m.key] = (acc[m.key] || 0) + (r[m.key] || 0);
              });
              return acc;
            }, {});
            yearTotals.collectionPct = yearTotals.totalExpected > 0 ? (yearTotals.totalInsAmtPaid / yearTotals.totalExpected) : null;
            return `
              <tr class="client-pivot-row client-pivot-row--year">
                <td class="client-pivot-row-label client-pivot-row-year">▾ ${escapeHtml(year)}</td>
                ${pivotMetrics.map((m) => `<td class="client-pivot-cell">${escapeHtml(fmtPivotValue(yearTotals[m.key], m.type))}</td>`).join("")}
              </tr>
              ${rows.map((r) => `
                <tr class="client-pivot-row">
                  <td class="client-pivot-row-label">${escapeHtml(r.monthLabel)}</td>
                  ${pivotMetrics.map((m) => `<td class="client-pivot-cell">${escapeHtml(fmtPivotValue(r[m.key], m.type))}</td>`).join("")}
                </tr>
              `).join("")}
            `;
          }).join("")}
          <tr class="client-pivot-row client-pivot-row--grand">
            <td class="client-pivot-row-label">Grand Total</td>
            ${pivotMetrics.map((m) => `<td class="client-pivot-cell">${escapeHtml(fmtPivotValue(buckets.grand[m.key], m.type))}</td>`).join("")}
          </tr>
        </tbody>
      </table>
    </div>
  `;
}

function renderClientPivotChart(buckets) {
  if (!buckets.rows.length) return `<div class="client-pivot-empty">No service-line rows match the active slicers.</div>`;
  return `
    <div class="client-pivot-chart-grid">
      <div class="client-pivot-chart-card">
        <h4>Expected · Collected · Outstanding by Month</h4>
        <div id="clientPivotChart1" class="client-pivot-chart-host"></div>
      </div>
      <div class="client-pivot-chart-card">
        <h4>Collection % by Month</h4>
        <div id="clientPivotChart2" class="client-pivot-chart-host"></div>
      </div>
      <div class="client-pivot-chart-card client-pivot-chart-wide">
        <h4>Monthly Mix — Total Expected vs Total Paid vs Payer Balance</h4>
        <div id="clientPivotChart3" class="client-pivot-chart-host"></div>
      </div>
    </div>
  `;
}

function renderClientPivotChartPlot(buckets) {
  if (typeof window.Plotly === "undefined") {
    schedulePlotlyLoad(() => renderClientMonthlyPivot());
    return;
  }
  const labels = buckets.rows.map((r) => {
    const [y] = r.label.split("-");
    return `${monthLabelFromYearMonth(r.label)} ${y}`;
  });
  const base = (typeof plotlyBaseLayout === "function") ? plotlyBaseLayout() : { paper_bgcolor: "transparent", plot_bgcolor: "transparent", margin: { t: 10, r: 16, b: 48, l: 64 } };
  // Chart 1: stacked Expected vs Collected vs Outstanding
  const c1 = document.getElementById("clientPivotChart1");
  if (c1) {
    window.Plotly.newPlot(c1, [
      { type: "bar", name: "Total Expected", x: labels, y: buckets.rows.map((r) => r.totalExpected), marker: { color: plotlySeriesColor(0) } },
      { type: "bar", name: "Total Paid",     x: labels, y: buckets.rows.map((r) => r.totalInsAmtPaid), marker: { color: plotlySeriesColor(1) } },
      { type: "bar", name: "Payer Balance",  x: labels, y: buckets.rows.map((r) => r.payerBalance), marker: { color: plotlySeriesColor(3) } }
    ], { ...base, barmode: "group", yaxis: { ...base.yaxis, tickformat: "$,.0f" } }, { responsive: true, displayModeBar: false });
  }
  // Chart 2: collection percent
  const c2 = document.getElementById("clientPivotChart2");
  if (c2) {
    window.Plotly.newPlot(c2, [
      { type: "scatter", mode: "lines+markers", name: "Collection %", x: labels, y: buckets.rows.map((r) => r.collectionPct == null ? null : r.collectionPct * 100), line: { color: plotlySeriesColor(1), width: 2.5 }, marker: { color: plotlySeriesColor(1), size: 7 } }
    ], { ...base, yaxis: { ...base.yaxis, tickformat: ".0f", ticksuffix: "%" } }, { responsive: true, displayModeBar: false });
  }
  // Chart 3: full mix
  const c3 = document.getElementById("clientPivotChart3");
  if (c3) {
    window.Plotly.newPlot(c3, [
      { type: "bar", name: "Primary Expected",  x: labels, y: buckets.rows.map((r) => r.primaryExpected), marker: { color: plotlySeriesColor(0) } },
      { type: "bar", name: "Secondary Expected", x: labels, y: buckets.rows.map((r) => r.secondaryExpected), marker: { color: plotlySeriesColor(4) } },
      { type: "bar", name: "Ins Paid", x: labels, y: buckets.rows.map((r) => r.totalInsAmtPaid), marker: { color: plotlySeriesColor(1) } },
      ...(isClientRole() ? [] : [{ type: "bar", name: "Left After Insurance (+)", x: labels, y: buckets.rows.map((r) => r.positivePatBal), marker: { color: plotlySeriesColor(2) } }]),
      { type: "bar", name: "Write-off", x: labels, y: buckets.rows.map((r) => r.writeOff), marker: { color: plotlySeriesColor(6) } }
    ], { ...base, barmode: "stack", yaxis: { ...base.yaxis, tickformat: "$,.0f" } }, { responsive: true, displayModeBar: false });
  }
}

// ---------------------------------------------------------------------------
// Payments report (Client View): the money-in feed at check level.
// One row per payment from GET /api/client/payments/checks, each expandable
// into its disbursement report (client names, CPT, units, paid per line) from
// GET /api/client/payments/checks/detail. When the live payment mirror is
// offline, the loaded tracker payment evidence is grouped into pseudo-checks
// so the report keeps working locally.
// ---------------------------------------------------------------------------

const CLIENT_PAYMENTS_VISIBLE_ROW_LIMIT = 75;

function clientPaymentsChecksModel(claims = []) {
  requestClientPaymentChecks();
  const companies = clientPaymentsCompanyOptions(claims);
  const liveApi = trackerApiEnabled();
  const apiState = clientPaymentChecksApiState || {};
  const api = apiState.status === "ok" ? apiState.response : null;
  if (api && Array.isArray(api.rows)) {
    const serverCompanies = Array.isArray(api.organizations)
      ? api.organizations
        .map((entry) => ({ orgId: String(entry.orgId || "").trim(), name: String(entry.name || "").trim() || String(entry.orgId || "").trim() }))
        .filter((entry) => entry.orgId)
      : [];
    const rows = api.rows.map((row, index) => ({
      key: `${row.paymentId || index}|${row.orgId || ""}`,
      paymentId: String(row.paymentId || ""),
      orgId: String(row.orgId || ""),
      paymentDate: normalizeDateValue(row.paymentDate) || "",
      payer: row.payer || "No payer",
      reference: row.reference || "",
      method: row.method || "",
      hitsBankDate: normalizeDateValue(row.hitsBankDate) || "",
      amount: row.amount == null || !Number.isFinite(Number(row.amount)) ? null : moneyNumber(row.amount),
      unreadableAmountRows: Number(row.unreadableAmountRows) || 0,
      serviceLines: Number(row.serviceLines) || 0,
      clients: Number(row.clients) || 0,
      lines: null
    })).filter((row) => row.amount === null || row.amount > 0);
    return {
      live: true,
      rows,
      serverBacked: api.complete === true && api.sourceMode === "warehouse",
      financiallyComplete: api.financiallyComplete === true,
      financialIncompleteReason: api.financialIncompleteReason || "",
      totals: api.totals || {},
      facets: api.facets || {},
      page: api.page || {},
      companies: serverCompanies.length ? serverCompanies : companies,
      note: "",
      emptyText: "No payments to show yet."
    };
  }
  if (liveApi && (apiState.status === "idle" || apiState.status === "pending")) {
    return {
      live: true,
      loading: true,
      rows: [],
      companies,
      note: "Please hold while we load your payments.",
      emptyText: "Loading payments..."
    };
  }
  if (liveApi && apiState.status === "error") {
    return {
      live: true,
      error: apiState.error || "Payment feed unavailable.",
      rows: [],
      companies,
      note: "",
      emptyText: "Payment feed unavailable."
    };
  }
  const groups = new Map();
  clientPaymentLedgerRows(claims).forEach((line) => {
    const reference = line.reference && !/^Payment \d+$/.test(line.reference) ? line.reference : "";
    const groupKey = `${line.orgId || ""}|${reference ? `${reference}|${line.payer}` : `${line.payer}|${line.paymentDate}`}`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        key: `local-${groups.size}`,
        paymentId: "",
        orgId: line.orgId || "",
        paymentDate: line.paymentDate,
        payer: line.payer || "No payer",
        reference,
        method: "",
        hitsBankDate: "",
        amount: 0,
        serviceLines: 0,
        clientNames: new Set(),
        lines: []
      });
    }
    const group = groups.get(groupKey);
    group.amount = moneyNumber(group.amount + line.amount);
    group.serviceLines += 1;
    if (line.client) group.clientNames.add(line.client);
    if (timestampValue(line.paymentDate) > timestampValue(group.paymentDate)) group.paymentDate = line.paymentDate;
    if (timestampValue(line.hitsBankDate) > timestampValue(group.hitsBankDate)) group.hitsBankDate = line.hitsBankDate;
    group.lines.push(line);
  });
  const rows = Array.from(groups.values()).map((group) => ({
    key: group.key,
    paymentId: group.paymentId,
    orgId: group.orgId,
    paymentDate: group.paymentDate,
    payer: group.payer,
    reference: group.reference,
    method: group.method,
    hitsBankDate: group.hitsBankDate,
    amount: group.amount,
    serviceLines: group.serviceLines,
    clients: group.clientNames.size,
    lines: group.lines
  })).sort((a, b) => timestampValue(b.paymentDate) - timestampValue(a.paymentDate) || b.amount - a.amount || a.payer.localeCompare(b.payer));
  const scopedRows = clientPaymentsDashState.org ? rows.filter((row) => row.orgId === clientPaymentsDashState.org) : rows;
  return {
    live: false,
    rows: scopedRows,
    companies,
    note: "Bank dates and check references appear as payments clear.",
    emptyText: "No payments to show yet."
  };
}

function clientPaymentsCompanyOptions(claims = []) {
  const byOrg = new Map();
  claims.forEach((claim) => {
    const orgId = String(claim.orgId || "").trim();
    if (!orgId || byOrg.has(orgId)) return;
    byOrg.set(orgId, String(claim.client || "").trim() || orgId);
  });
  return Array.from(byOrg.entries())
    .map(([orgId, name]) => ({ orgId, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function clientPaymentsKpis(rows = [], today = localDateString()) {
  const weekStart = addDays(today, -(new Date(`${today}T00:00:00`).getDay()));
  const monthStart = `${today.slice(0, 7)}-01`;
  const kpis = {
    today: { amount: 0, count: 0 },
    soon: { amount: 0, count: 0 },
    week: { amount: 0, count: 0 },
    month: { amount: 0, count: 0 }
  };
  rows.forEach((row) => {
    const posted = normalizeDateValue(row.paymentDate) || "";
    const lands = row.hitsBankDate || posted;
    if (lands && lands === today) {
      kpis.today.amount = moneyNumber(kpis.today.amount + row.amount);
      kpis.today.count += 1;
    }
    if (row.hitsBankDate && row.hitsBankDate > today) {
      kpis.soon.amount = moneyNumber(kpis.soon.amount + row.amount);
      kpis.soon.count += 1;
    }
    if (posted && weekStart && posted >= weekStart && posted <= today) {
      kpis.week.amount = moneyNumber(kpis.week.amount + row.amount);
      kpis.week.count += 1;
    }
    if (posted && posted >= monthStart && posted <= today) {
      kpis.month.amount = moneyNumber(kpis.month.amount + row.amount);
      kpis.month.count += 1;
    }
  });
  return kpis;
}

function clientPaymentsFilterRows(rows = [], dash = clientPaymentsDashState, today = localDateString()) {
  const search = String(dash.search || "").trim().toLowerCase();
  const weekStart = addDays(today, -(new Date(`${today}T00:00:00`).getDay()));
  const monthStart = `${today.slice(0, 7)}-01`;
  const last30 = addDays(today, -30);
  return rows.filter((row) => {
    if (dash.payer && dash.payer !== "All" && row.payer !== dash.payer) return false;
    const posted = normalizeDateValue(row.paymentDate) || "";
    if (dash.range === "today" && posted !== today) return false;
    if (dash.range === "week" && (!posted || posted < weekStart)) return false;
    if (dash.range === "month" && (!posted || posted < monthStart)) return false;
    if (dash.range === "last30" && (!posted || posted < last30)) return false;
    if (search && !`${row.payer} ${row.reference}`.toLowerCase().includes(search)) return false;
    return true;
  });
}

function clientPaymentsSortRows(rows = [], dash = clientPaymentsDashState) {
  const direction = dash.dir === "asc" ? 1 : -1;
  const sorted = [...rows];
  if (dash.sort === "payer") {
    sorted.sort((a, b) => direction * a.payer.localeCompare(b.payer) || b.amount - a.amount);
  } else if (dash.sort === "amount") {
    sorted.sort((a, b) => direction * (a.amount - b.amount) || a.payer.localeCompare(b.payer));
  } else {
    sorted.sort((a, b) => direction * (timestampValue(a.hitsBankDate || a.paymentDate) - timestampValue(b.hitsBankDate || b.paymentDate)) || b.amount - a.amount);
  }
  return sorted;
}

function clientPaymentMoneyLabel(value) {
  return value === null || value === undefined || !Number.isFinite(Number(value))
    ? "Unavailable"
    : Formatters.moneyExact(value);
}

function clientPaymentsFiltersActive(dash = clientPaymentsDashState) {
  return Boolean((dash.search || "").trim() || (dash.payer && dash.payer !== "All") || (dash.range && dash.range !== "all") || dash.org);
}

// Local filters can empty the list while Landed Today / This Month still
// show loaded cash. Name that cash. Never invent a leftover statement dollar.
function clientPaymentsHiddenFilterBook(allRows = [], visibleRows = [], dash = clientPaymentsDashState) {
  if (!clientPaymentsFiltersActive(dash)) return null;
  const book = Array.isArray(allRows) ? allRows : [];
  const visible = Array.isArray(visibleRows) ? visibleRows : [];
  if (visible.length) return null;
  const amount = book.reduce((sum, row) => {
    const n = Number(row && row.amount);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
  return {
    payments: book.length,
    amount: book.length && Number.isFinite(amount) && amount > 0 ? moneyNumber(amount) : null
  };
}

function clientPaymentsEmptyNote(allRows = [], visibleRows = [], dash = clientPaymentsDashState, emptyText = "") {
  const hidden = clientPaymentsHiddenFilterBook(allRows, visibleRows, dash);
  if (hidden && hidden.amount != null) {
    return `Filters are hiding ${clientPaymentMoneyLabel(hidden.amount)} across ${formatNounCount(hidden.payments, "payment")} still on this book. This is not $0.`;
  }
  if (hidden) {
    return "No payments match these filters. That is not the same as no money on the book.";
  }
  return emptyText || "No payments to show yet.";
}

function renderClientPaymentsWorkspace(model = {}) {
  const dash = clientPaymentsDashState;
  const today = localDateString();
  const allRows = Array.isArray(model.rows) ? model.rows : [];
  const filtered = model.serverBacked ? allRows : clientPaymentsSortRows(clientPaymentsFilterRows(allRows, dash, today), dash);
  const visibleRows = model.serverBacked ? filtered : filtered.slice(0, CLIENT_PAYMENTS_VISIBLE_ROW_LIMIT);
  const kpis = model.serverBacked ? (model.totals?.kpis || clientPaymentsKpis([], today)) : clientPaymentsKpis(allRows, today);
  const payers = model.serverBacked ? (model.facets?.payers || []) : Array.from(new Set(allRows.map((row) => row.payer))).sort((a, b) => a.localeCompare(b));
  const shownTotal = model.serverBacked ? (model.totals?.amount == null ? null : moneyNumber(model.totals.amount)) : moneyNumber(filtered.reduce((sum, row) => sum + row.amount, 0));
  const landed = allRows
    .map((row) => ({ amount: row.amount, lands: row.hitsBankDate || normalizeDateValue(row.paymentDate) || "" }))
    .filter((entry) => entry.lands && entry.lands <= today)
    .sort((a, b) => b.lands.localeCompare(a.lands));
  // THE SERVER NAMES THE LATEST DEPOSIT DATE, NOT ITS AMOUNT. This used to pin amount: 0,
  // which the tile printed as "Last deposit 8/19/2026 — $0.00" directly above a ledger
  // showing that day's real deposits (measured hosted TEST 2026-09-03). The amount is
  // known only when the whole date group is on this page: first page, newest-first, and
  // the page runs past that date. Otherwise the tile states the date alone - unknown is
  // not $0.00.
  const latestDate = model.serverBacked ? String(model.totals?.latestDate || "") : "";
  const latestDateRows = latestDate ? landed.filter((entry) => entry.lands === latestDate) : [];
  const latestDateComplete = latestDateRows.length > 0
    && Number(model.page?.number || 1) === 1
    && dash.sort === "date" && dash.dir === "desc"
    && landed.some((entry) => entry.lands < latestDate);
  const lastLanded = model.serverBacked && latestDate
    ? { lands: latestDate, amount: latestDateComplete ? moneyNumber(latestDateRows.reduce((sum, entry) => sum + Number(entry.amount || 0), 0)) : null }
    : (landed.length ? landed[0] : null);
  const renderLimitText = filtered.length > visibleRows.length
    ? `; showing first ${formatNounCount(visibleRows.length, "payment")}`
    : "";
  // A REPORT THAT HAS NOT LOADED HAS NO TOTALS. These four tiles used to print
  // $0.00 and "0 payments" while the feed was still in flight or had failed, which
  // states a financial fact - that no money landed - nobody measured. Nathan read
  // exactly that on production on 2026-09-22: four $0.00 tiles and "0 payments"
  // under "Please hold while we load your payments." Client Home already answers
  // this honestly ("This is not $0.") and now so does the report. No LOADED number
  // changes: these helpers only apply while model.loading or model.error is set.
  const kpiUnknown = Boolean(model.loading || model.error);
  const kpiUnknownDetail = model.error
    ? "Not loaded. This is not $0."
    : "Still loading. This is not $0.";
  const kpiValue = (amount) => (kpiUnknown ? "—" : clientPaymentMoneyLabel(amount));
  const kpiDetail = (detail) => (kpiUnknown ? kpiUnknownDetail : detail);
  const kpiTone = (tone) => (kpiUnknown ? "quiet" : tone);
  const paymentRowsHtml = model.loading
    ? `<div class="client-payments-empty" role="status">Loading payments...</div>`
    : model.error
      ? `<div class="client-payments-empty" role="alert">Payment feed unavailable: ${escapeHtml(model.error)} <button type="button" class="small-button" data-payments-retry>Retry</button></div>`
      : visibleRows.length
        ? visibleRows.map((row) => renderClientPaymentCheckRow(row, today)).join("")
        : `<div class="client-payments-empty">${escapeHtml(clientPaymentsEmptyNote(allRows, visibleRows, dash, model.emptyText))}</div>`;
  const sortArrow = (column) => dash.sort === column ? (dash.dir === "asc" ? " ▲" : " ▼") : "";
  return `
    <section class="client-payments-dash" aria-label="Payments report">
      <div class="client-report-toolbar">
        ${clientReportCrumbHtml("Payments")}
        <div class="client-report-toolbar-filters">
          ${model.companies && model.companies.length > 1 ? `<label>Client<select data-payments-org ${activeClientGroupTenant() ? `disabled title="Narrowed to ${escapeHtml(activeClientGroupLabel())} by the client switcher in the header."` : ""}><option value="">All clients</option>${model.companies.map((company) => `<option value="${escapeHtml(company.orgId)}" ${dash.org === company.orgId ? "selected" : ""}>${escapeHtml(company.name)}</option>`).join("")}</select></label>` : ""}
          <label>Search<input type="search" data-payments-search value="${escapeHtml(dash.search || "")}" placeholder="Payer or reference"></label>
          <label>Payer<select data-payments-payer>${renderClientReportOptions(payers, dash.payer || "All", "All payers")}</select></label>
          <label>Posted<select data-payments-range>
            <option value="all" ${dash.range === "all" || !dash.range ? "selected" : ""}>All dates</option>
            <option value="today" ${dash.range === "today" ? "selected" : ""}>Today</option>
            <option value="week" ${dash.range === "week" ? "selected" : ""}>This week</option>
            <option value="month" ${dash.range === "month" ? "selected" : ""}>This month</option>
            <option value="last30" ${dash.range === "last30" ? "selected" : ""}>Last 30 days</option>
          </select></label>
          <button type="button" class="small-button" data-payments-clear ${clientPaymentsFiltersActive(dash) ? "" : "disabled"}>Clear Filter</button>
          ${model.serverBacked ? "" : '<button type="button" class="small-button client-report-export-btn" data-payments-export aria-label="Export payments to CSV">Export CSV</button>'}
        </div>
      </div>
      ${model.note ? `<p class="client-payments-dash-note">${escapeHtml(model.note)}</p>` : ""}
      ${model.serverBacked && model.financiallyComplete === false ? `<p class="client-payments-dash-note" role="status"><strong>Payment amounts unavailable for ${Number(model.totals?.unreadableAmountRows || 0).toLocaleString()} row${Number(model.totals?.unreadableAmountRows || 0) === 1 ? "" : "s"}.</strong> ${escapeHtml(model.financialIncompleteReason || "Some payment amounts could not be read, so affected totals are not shown.")}</p>` : ""}
      <div class="ar-kpi-grid client-payments-kpis">
        ${arKpiCard("Landed Today", kpiValue(kpis.today.amount), kpiDetail(kpis.today.count ? `${kpis.today.count} payment${kpis.today.count === 1 ? "" : "s"} hit the bank today` : (lastLanded ? `Last deposit ${formatDate(lastLanded.lands)}${lastLanded.amount == null ? "" : ` — ${clientPaymentMoneyLabel(lastLanded.amount)}`}` : "No deposits landing today")), kpiTone(kpis.today.count ? "primary" : "quiet"))}
        ${arKpiCard("Arriving Soon", kpiValue(kpis.soon.amount), kpiDetail(kpis.soon.count ? `${formatNounCount(kpis.soon.count, "payment")} on the way to the bank` : "No upcoming deposits"), kpiTone(kpis.soon.count ? "accent" : "quiet"))}
        ${arKpiCard("This Week", kpiValue(kpis.week.amount), kpiDetail(`${formatNounCount(kpis.week.count, "payment")} posted this week`), kpiTone(kpis.week.count ? "" : "quiet"))}
        ${arKpiCard("This Month", kpiValue(kpis.month.amount), kpiDetail(`${formatNounCount(kpis.month.count, "payment")} posted this month`), kpiTone(kpis.month.count ? "" : "quiet"))}
      </div>
      <div class="client-payments-checks" aria-label="Payments received">
        <div class="client-payments-check-grid client-payments-check-header">
          <button type="button" data-payments-sort="date">Date${sortArrow("date")}</button>
          <button type="button" data-payments-sort="payer">Payer${sortArrow("payer")}</button>
          <button type="button" data-payments-sort="amount">Amount${sortArrow("amount")}</button>
          <span>Covers</span>
          <span>Reference</span>
          <span aria-hidden="true"></span>
        </div>
        ${paymentRowsHtml}
      </div>
      <div class="client-payments-dash-foot">
        <span>${model.serverBacked ? `${formatNounCount(model.page?.totalRows || 0, "payment")} across the complete filtered population` : `${filtered.length === allRows.length ? formatNounCount(allRows.length, "payment") : `${formatNounCount(filtered.length, "payment")} of ${formatNounCount(allRows.length, "payment")} shown`}${renderLimitText}`}</span>
        <strong>${clientPaymentMoneyLabel(shownTotal)} ${model.serverBacked ? "filtered total" : "total shown"}</strong>
      </div>
      ${model.serverBacked && Number(model.page?.totalPages) > 1 ? `<nav class="client-report-server-pager" aria-label="Payment pages"><button type="button" class="small-button" data-payments-page="prev" ${Number(model.page.number) <= 1 ? "disabled" : ""}>Previous</button><span>Page ${Number(model.page.number).toLocaleString()} of ${Number(model.page.totalPages).toLocaleString()}</span><button type="button" class="small-button" data-payments-page="next" ${Number(model.page.number) >= Number(model.page.totalPages) ? "disabled" : ""}>Next</button></nav>` : ""}
    </section>
  `;
}

function renderClientPaymentCheckRow(row, today = localDateString()) {
  const isOpen = clientPaymentsDashState.expanded === row.key;
  const posted = normalizeDateValue(row.paymentDate) || "";
  const lands = row.hitsBankDate || posted;
  let badge = "";
  if (row.hitsBankDate && row.hitsBankDate === today) {
    badge = pill("Hits bank today", "green");
  } else if (row.hitsBankDate && row.hitsBankDate > today) {
    const days = Math.round((timestampValue(row.hitsBankDate) - timestampValue(today)) / 86400000);
    badge = pill(`Hits bank in ${days} day${days === 1 ? "" : "s"}`, "amber");
  }
  const secondary = row.hitsBankDate && posted && row.hitsBankDate !== posted
    ? `Posted ${formatDate(posted)}`
    : clientPaymentsDaysAgoLabel(posted, today);
  return `
    <div class="client-payments-check ${isOpen ? "is-open" : ""}">
      <button type="button" class="client-payments-check-grid client-payments-check-row" data-payments-expand="${escapeHtml(row.key)}" aria-expanded="${isOpen ? "true" : "false"}">
        <span data-label="Date"><strong>${lands ? formatDate(lands) : "No date"}</strong>${badge}<small>${escapeHtml(secondary)}</small></span>
        <span data-label="Payer"><strong>${escapeHtml(row.payer)}</strong>${row.method ? `<small>${escapeHtml(row.method)}</small>` : ""}</span>
        <span data-label="Amount"><strong>${clientPaymentMoneyLabel(row.amount)}</strong>${row.unreadableAmountRows ? `<small>${Number(row.unreadableAmountRows).toLocaleString()} unreadable amount row${Number(row.unreadableAmountRows) === 1 ? "" : "s"}</small>` : ""}</span>
        <span data-label="Covers"><strong>${row.serviceLines.toLocaleString()} service line${row.serviceLines === 1 ? "" : "s"}</strong>${row.clients ? `<small>${row.clients} client${row.clients === 1 ? "" : "s"}</small>` : ""}</span>
        <span data-label="Reference"><strong>${escapeHtml(row.reference || "No reference")}</strong></span>
        <span class="client-payments-chevron" aria-hidden="true">▸</span>
      </button>
      ${isOpen ? renderClientPaymentCheckDetail(row) : ""}
    </div>
  `;
}

function clientPaymentsDaysAgoLabel(posted, today = localDateString()) {
  if (!posted) return "No posted date";
  const days = Math.round((timestampValue(today) - timestampValue(posted)) / 86400000);
  if (!Number.isFinite(days)) return "";
  // A FUTURE POSTED DATE IS NOT TODAY (2026-08-10). `days <= 0` folded
  // "posted today" together with "dated a month from now", so the live $24.91
  // BUCKEYE check dated 9/8/2026 read "Posted today" on 8/10/2026 - and sorted
  // to the top, because the feed orders by payment date DESC. The backend now
  // excludes unsettled payments at three layers (clientPaymentChecks.js, the
  // same clamp collectionsReport.js carries); this is the last line of defence
  // for any row that reaches the label another way.
  if (days < 0) return "Not posted yet";
  if (days === 0) return "Posted today";
  if (days === 1) return "Posted yesterday";
  return `Posted ${days} days ago`;
}

function clientPaymentDetailIncompleteMessage(detail) {
  if (detail.truncated) return "Only the first loaded disbursements are available because this payment exceeds the detail limit. The subtotal covers loaded rows only. CSV export is unavailable until complete detail is available.";
  if (detail.attributionDegraded) return "Some service labels could not be loaded. Payment amounts remain scoped, but the detail is incomplete. CSV export is unavailable until the service labels are available.";
  return "The complete disbursement list could not be verified. The subtotal covers loaded rows only. CSV export is unavailable.";
}

function renderClientPaymentCheckDetail(row) {
  const detail = clientPaymentCheckDetailModel(row);
  if (detail.status === "pending") {
    return `<div class="client-payments-check-detail"><p class="client-payments-detail-note">Please hold while we load payment details.</p></div>`;
  }
  if (detail.status === "error") {
    // A dead end the reader can act on, rather than a sentence they can only
    // stare at. Retry clears this row's cache entry and re-renders, which is
    // what re-issues the fetch.
    return `<div class="client-payments-check-detail">
      <p class="client-payments-detail-note">${escapeHtml(detail.error || "Disbursement report unavailable.")}</p>
      <button type="button" class="small-button" data-payments-detail-retry="${escapeHtml(row.key)}">Try again</button>
    </div>`;
  }
  if (!detail.rows.length && detail.complete) {
    return `<div class="client-payments-check-detail"><p class="client-payments-detail-note">No disbursements match this payment in your authorized scope.</p></div>`;
  }
  // Same gate the drawer itself enforces, asked here so the affordance and the
  // door agree: a row a user cannot open must not look openable.
  // Permission-derived, exactly like openDrawer itself. Full Client users now
  // carry the scoped workbench/claim-view grant, so they may follow a payment
  // line into their own claim detail; RedactedClient still has no Reports lane.
  const canDrillIntoClaim = hasPermission(PERMISSIONS.VIEW_WORKBENCH);
  const paging = managementListPageWindow("payment-detail", detail.rows, clientPaymentCheckDetailCacheKey(row));
  return `
    <div class="client-payments-check-detail">
      <div class="client-payments-detail-head">
        <strong>Disbursement report</strong>
        <button type="button" class="small-button" data-payments-detail-export="${escapeHtml(row.key)}" ${detail.complete ? "" : "disabled"}>Download CSV</button>
      </div>
      ${!detail.complete ? '<p class="client-payments-detail-note" role="status">' + escapeHtml(clientPaymentDetailIncompleteMessage(detail)) + '</p>' : ""}
      <div class="client-payments-detail-table" role="table" aria-label="Disbursement report">
        <div class="client-payments-detail-row client-payments-detail-header" role="row">
          <span>Client</span><span>Date of Service</span><span>CPT</span><span>Units</span><span>Paid</span>
        </div>
        ${paging.rows.map((line) => {
          const drillClaimId = canDrillIntoClaim ? String(line.claimId || "") : "";
          // No claim id -> no pointer, no hover, no tab stop, no role="button".
          // The line still renders its money: it is a real disbursement we just
          // cannot attribute, so it stays inert rather than looking clickable and
          // then failing. The accessible name deliberately carries claim/CPT/DOS
          // and NOT the patient name.
          const rowAttrs = drillClaimId
            // The accessible name REPLACES the row contents for a screen reader,
            // so it has to carry the facts the sighted user gets. The patient name
            // is left out on purpose (PHI in a label that assistive tech may cache
            // or speak aloud); the paid amount is not PHI and is restored here, so
            // a screen-reader user is not the only one who cannot hear the money.
            ? `class="client-payments-detail-row drillable" role="button" tabindex="0" data-payments-open-claim="${escapeHtml(drillClaimId)}" data-payments-org-id="${escapeHtml(String(line.orgId || row.orgId || ""))}" data-payments-service-id="${escapeHtml(String(line.serviceId || ""))}" title="Open the full claim detail" aria-label="Open claim ${escapeHtml(drillClaimId)} detail, CPT ${escapeHtml(line.cpt || "unknown")}, date of service ${escapeHtml(line.serviceDate ? formatDate(line.serviceDate) : "unknown")}, paid ${escapeHtml(Formatters.moneyExact(line.paid))}"`
            : `class="client-payments-detail-row" role="row"`;
          return `
          <div ${rowAttrs}>
            <span data-label="Client">${escapeHtml(line.client || "Unattributed")}</span>
            <span data-label="Date of Service">${line.serviceDate ? formatDate(line.serviceDate) : "-"}</span>
            <span data-label="CPT">${escapeHtml(line.cpt || "-")}</span>
            <span data-label="Units">${line.units ? line.units.toLocaleString() : "-"}</span>
            <span data-label="Paid">${Formatters.moneyExact(line.paid)}</span>
          </div>`;
        }).join("")}
        <div class="client-payments-detail-row client-payments-detail-total" role="row">
          <span>${detail.complete ? "Total" : "Loaded subtotal"}</span><span></span><span></span><span></span><span>${Formatters.moneyExact(detail.totals.paid)}</span>
        </div>
      </div>
      ${managementListPagerHtml(paging, detail.complete ? "Disbursements" : "Loaded disbursements")}
      ${canDrillIntoClaim && detail.rows.length && !detail.rows.some((line) => line.claimId)
        ? `<p class="client-payments-detail-note client-payments-detail-unmatched-note">None of these lines are matched to a claim yet, so they cannot be opened. The payment itself is unaffected.</p>`
        : ""}
    </div>
  `;
}

// A REQUEST WITH NO TIMEOUT IS A SPINNER THAT NEVER RESOLVES.
//
// The disbursement fetch had no AbortController and no deadline, sitting behind
// a 60s server-side SQL budget and a ~240s gateway. Whenever it was slow it was
// indistinguishable from broken, which is exactly what the owner reported on
// 2026-08-11: "Please hold while we load payment details." and then nothing, forever.
//
// WHY 90s AND NOT SOMETHING TIGHTER. The client deadline must sit ABOVE the
// server’s own budget, never below it. Below, the browser gives up on a request
// the server was still going to answer, throws away the server’s real error, and
// substitutes a guess — turning a slow success into a reported failure. That is a
// live defect this codebase has already paid for: the Reports lane shipped a 45s
// watchdog against the same 60s server budget and had to be raised to 90s.
//
// This constant was itself 25s for exactly that wrong reason. 25s was picked as
// "2.4x the worst expand observed" — but that worst case (10.3s) came from a quiet
// API. The speed audit measured this same endpoint at 24,601ms under load, which
// makes 25s a 1.016x margin: a coin flip on a request that was going to succeed.
//
// 90s matches the report dataset and rollup lanes, so this is no longer an
// outlier for anyone to "harmonise" later. It is above the server’s 60s budget
// (see clientPaymentChecks.js, where BOTH drill-down queries now share one 60s
// deadline rather than taking 60s each) and comfortably below the ~240s gateway,
// so the failure the reader is shown is the server’s actual answer whenever the
// server has one — and our own honest "this took too long" only when it does not.
const CLIENT_PAYMENT_DETAIL_TIMEOUT_MS = 90000;

function clientPaymentCheckDetailCacheKey(row = {}, base = trackerApiBase()) {
  return JSON.stringify({
    publicationEpoch: reportReadPublicationIdentity(),
    base: String(base || ""),
    rowKey: String(row.key || ""),
    paymentId: String(row.paymentId || ""),
    orgId: String(row.orgId || ""),
    access: trackerAccessEnvelope("service")
  });
}

function clientPaymentCheckDetailModel(row = {}) {
  if (Array.isArray(row.lines)) {
    const rows = row.lines.map((line) => ({
      client: line.client || "",
      serviceDate: line.serviceDate || "",
      cpt: line.cpt || "",
      units: moneyNumber(line.units),
      paid: moneyNumber(line.amount),
      orgId: String(line.orgId || row.orgId || ""),
      claimId: String(line.claimId || ""),
      serviceId: String(line.serviceId || "")
    }));
    return { status: "ok", rows, complete: row.complete === true && row.truncated !== true && row.attributionDegraded !== true, truncated: row.truncated === true, attributionDegraded: row.attributionDegraded === true, totals: { paid: moneyNumber(rows.reduce((sum, line) => sum + line.paid, 0)) } };
  }
  const cacheKey = clientPaymentCheckDetailCacheKey(row);
  const cached = clientPaymentCheckDetailCache.get(cacheKey);
  if (cached && cached.status === "ok") {
    const body = cached.response || {};
    // claimId/serviceId are carried through so a disbursement line can open the
    // real claim drawer by id. claimId can legitimately be "" (the disbursement
    // is real but the tracker leg has no attribution for it) - callers must treat
    // empty as "no drawer target" and NEVER reverse-look-up by patient+DOS+CPT,
    // which mis-targets whenever a patient repeats a CPT on another date.
    const rows = (Array.isArray(body.rows) ? body.rows : []).map((line) => ({
      client: line.client || "",
      serviceDate: normalizeDateValue(line.serviceDate) || "",
      cpt: line.cpt || "",
      units: moneyNumber(line.units),
      paid: moneyNumber(line.paid),
      orgId: String(line.orgId || row.orgId || ""),
      claimId: String(line.claimId || ""),
      serviceId: String(line.serviceId || "")
    }));
    return { status: "ok", rows, complete: body.complete === true && body.truncated !== true && body.attributionDegraded !== true, truncated: body.truncated === true, attributionDegraded: body.attributionDegraded === true, totals: { paid: moneyNumber(body.totals?.paid ?? rows.reduce((sum, line) => sum + line.paid, 0)) } };
  }
  if (cached && cached.status === "error") {
    return { status: "error", rows: [], totals: { paid: 0 }, error: `Disbursement report unavailable: ${cached.error || "unknown error"}` };
  }
  requestClientPaymentCheckDetail(row);
  return { status: "pending", rows: [], totals: { paid: 0 } };
}

const CLIENT_REPORT_CLAIMS_FETCH_LIMIT = 10000;
const CLIENT_REPORT_CLAIMS_FIRST_CHUNK = 2500;
const CLIENT_REPORT_CLAIMS_CHUNK = 2500;
// Deliberate pause between hydration chunks - do not "optimize" it away.
// The chunks used to fire back-to-back inside the previous chunk's .then,
// which built a convoy of up to four large requests. On the single-worker
// API a user's click then queued behind up to 10,000 rows of background
// hydration. Measured on the deployed API, same five requests, same session:
// during hydration 3,082 / 10,311 / 1,705 / 6,483 / 936 ms; after 6s idle
// 125 / 141 / 134 / 155 / 146 ms - 20-70x on identical requests. That is
// server-side queueing, not main-thread blocking (longtask blockMax was 0ms
// in every run). The gap lets an interactive request interleave, and costs
// ~750ms of total hydration time (three gaps) - the deliberate trade.
const CLIENT_REPORT_CLAIMS_CHUNK_GAP_MS = 250;

function clientReportAggregateRequestKey(report, filters, base = trackerApiBase()) {
  return JSON.stringify({ publicationEpoch: reportReadPublicationIdentity(), base, token: trackerApiToken(), scope: trackerAccessEnvelope("service"), report, filters, page: clientReportAggregatePage, pageIdentity: clientReportAggregatePageIdentity });
}

let clientReportAggregateRequestSequence = 0;
function requestClientReportAggregate(report, filters = {}) {
  if (!trackerApiEnabled() || typeof fetch !== "function") return;
  const base = trackerApiBase();
  if (!base) return;
  const normalized = { ...filters, slicers: normalizeClientReportSlicers(filters.slicers || state.clientReportSlicers || {}) };
  if (JSON.stringify(clientReportAggregateFilters) !== JSON.stringify(normalized)) clientReportServerDrillState = { status: "idle", key: "", response: null, error: "", page: 1 };
  clientReportAggregateFilters = normalized;
  const key = clientReportAggregateRequestKey(report, normalized, base);
  if (clientReportAggregateApiState.key === key && ["pending", "ok", "error"].includes(clientReportAggregateApiState.status)) return;
  const requestId = ++clientReportAggregateRequestSequence;
  clientReportAggregateApiState = { key, status: "pending", response: null, error: "", slow: false };
  const params = new URLSearchParams({
    report, groupBy: normalized.groupBy || "payer", client: normalized.client || "All", payer: normalized.payer || "All",
    fromDate: normalized.fromDate || "", toDate: normalized.toDate || "", slicers: JSON.stringify(normalized.slicers),
    page: String(clientReportAggregatePage), pageSize: "50"
  });
  if (clientReportAggregatePage > 1 && clientReportAggregatePageIdentity) params.set("pageIdentity", clientReportAggregatePageIdentity);
  const token = trackerApiToken();
  const current = () => requestId === clientReportAggregateRequestSequence && clientReportAggregateApiState.key === key && clientReportAggregateRequestKey(report, normalized, base) === key;
  reportReadJson("client-aggregate", base + "/api/client/reports/aggregate?" + params.toString(), { cache: "no-store", headers: { ...(token ? { authorization: "Bearer " + token } : {}) } }, () => {
    if (!current()) return;
    clientReportAggregateApiState.slow = true;
    if (clientReportSurfaceActive()) renderClientReportsView();
  }).then(({ response, body }) => {
    if (!current()) return;
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok || body.error || body.complete !== true || body.sourceMode !== "warehouse-prepared" || !body.generation || !body.pageIdentity) {
      clientReportAggregateApiState = { key, status: "error", response: null, error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : reportLoadFailureReason(body.message || body.error || ("HTTP " + response.status)) };
    } else {
      clientReportAggregatePageIdentity = body.pageIdentity;
      clientReportAggregateApiState = { key: clientReportAggregateRequestKey(report, normalized, base), status: "ok", response: body, error: "" };
    }
    if (clientReportSurfaceActive()) renderClientReportsView();
  }).catch((error) => {
    if (!current()) return;
    clientReportAggregateApiState = { key, status: "error", response: null, error: reportLoadFailureReason(error.message) };
    if (clientReportSurfaceActive()) renderClientReportsView();
  });
}

function renderClientReportServerPager(page = {}) {
  const total = Math.max(0, Number(page.totalRows) || 0);
  const pages = Math.max(1, Number(page.totalPages) || 1);
  const number = Math.min(pages, Math.max(1, Number(page.number) || 1));
  const size = Math.max(1, Number(page.size || page.pageSize) || 50);
  const first = total ? (number - 1) * size + 1 : 0;
  const last = Math.min(number * size, total);
  const button = (action, text, disabled) => '<button type="button" class="small-button" data-client-report-page="' + action + '"' + (disabled ? ' disabled' : '') + '>' + text + '</button>';
  return '<nav class="client-report-server-pager report-pager" aria-label="Report pages">'
    + button("first", "First", number <= 1) + button("prev", "Previous", number <= 1)
    + '<span role="status">Showing ' + first.toLocaleString() + '–' + last.toLocaleString() + ' of ' + total.toLocaleString() + ' groups · Page ' + number.toLocaleString() + ' of ' + pages.toLocaleString() + '</span>'
    + button("next", "Next", number >= pages) + button("last", "Last", number >= pages) + '</nav>';
}

// The client report library (RCM Performance / Receivables) computes its
// models in the browser from claim objects. In live-API mode the boot-time
// claim snapshot is intentionally empty, so the reports hydrate from the
// client-scoped report claims feed and convert the flat service rows back
// into claims via claimsFromServiceGrainRows. Rows arrive progressively in
// 2,500-row chunks (one backend mirror page each, so a chunk costs a single
// count + page query) and the figures update as they land.
function clientReportSourceClaims() {
  if (!trackerApiEnabled()) return getClaims();
  requestClientReportClaims();
  const api = clientReportClaimsApiState;
  if (api.status === "partial" || api.status === "ok") {
    return claimsFromServiceGrainRows(api.rows);
  }
  return [];
}

function clientReportClaimsLoadState() {
  if (!trackerApiEnabled()) return "ok";
  if (clientReportClaimsApiState.status === "ok" || clientReportClaimsApiState.status === "partial") return "ok";
  return clientReportClaimsApiState.status === "error" ? "error" : "pending";
}

function clientReportClaimsCacheKey(base = trackerApiBase()) {
  return JSON.stringify({
    publicationEpoch: reportReadPublicationIdentity(),
    base,
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: trackerAccessEnvelope("service")
  });
}

function requestClientReportClaims() {
  if (!trackerApiEnabled() || typeof fetch !== "function") return;
  const base = trackerApiBase();
  if (!base) return;
  const key = clientReportClaimsCacheKey(base);
  if (clientReportClaimsApiState.key === key && clientReportClaimsApiState.status !== "idle") return;
  clientReportClaimsApiState = { key, status: "pending", rows: [], totalRows: 0, truncated: false, error: "", retried: false };
  fetchClientReportClaimsChunk(key, 0, CLIENT_REPORT_CLAIMS_FIRST_CHUNK);
}

function fetchClientReportClaimsChunk(key, offset, limit) {
  const base = trackerApiBase();
  const token = trackerApiToken();
  const requestStillCurrent = () => clientReportClaimsApiState.key === key && clientReportClaimsCacheKey(base) === key;
  const fail = (message) => {
    const api = clientReportClaimsApiState;
    if (!requestStillCurrent()) return;
    if (!api.retried) {
      api.retried = true;
      window.setTimeout(() => {
        if (requestStillCurrent()) fetchClientReportClaimsChunk(key, offset, limit);
      }, 4000);
      return;
    }
    api.status = "error";
    api.error = message;
    refreshClientMoneySurfaces();
  };
  reportSessionFetch((typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(`${base}/api/client/reports/claims?limit=${limit}&offset=${offset}`) : `${base}/api/client/reports/claims?limit=${limit}&offset=${offset}`), {
    cache: "no-store",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        fail(response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body.message || body.error || `HTTP ${response.status}`));
        return;
      }
      const api = clientReportClaimsApiState;
      const chunkRows = Array.isArray(body.rows) ? body.rows : [];
      api.rows = api.rows.concat(chunkRows);
      api.totalRows = Math.max(Number(body.totalRows) || 0, api.rows.length);
      api.retried = false;
      const more = chunkRows.length > 0 && api.rows.length < Math.min(api.totalRows, CLIENT_REPORT_CLAIMS_FETCH_LIMIT);
      api.status = more ? "partial" : "ok";
      if (!more) api.truncated = api.totalRows > api.rows.length;
      refreshClientMoneySurfaces();
      if (more) window.setTimeout(() => {
        if (requestStillCurrent()) fetchClientReportClaimsChunk(key, api.rows.length, CLIENT_REPORT_CLAIMS_CHUNK);
      }, CLIENT_REPORT_CLAIMS_CHUNK_GAP_MS);
    })
    .catch((error) => {
      if (!requestStillCurrent()) return;
      fail(error.message);
    });
}

// The payment feed answers in 101-377 ms server-side at "All" scope (measured
// against production 2026-09-22, n=3, 16,128 payments). Anything past this bound is
// not a slow query, it is a read that will not land, and the reader is told so.
const CLIENT_PAYMENT_CHECKS_TIMEOUT_MS = 30000;
let clientPaymentChecksReadSequence = 0;
let clientPaymentChecksController = null;
function requestClientPaymentChecks() {
  if (!trackerApiEnabled() || typeof fetch !== "function") return;
  const base = trackerApiBase();
  if (!base) return;
  const key = JSON.stringify({
    publicationEpoch: reportReadPublicationIdentity(),
    base,
    token: trackerApiToken(),
    access: trackerAccessEnvelope("service"),
    org: clientPaymentsDashState.org || "",
    search: clientPaymentsDashState.search || "",
    payer: clientPaymentsDashState.payer || "All",
    range: clientPaymentsDashState.range || "all",
    sort: clientPaymentsDashState.sort || "date",
    dir: clientPaymentsDashState.dir || "desc",
    page: clientPaymentsDashState.page || 1
  });
  const requestStillCurrent = () => checksRequestId === clientPaymentChecksReadSequence
    && !controller.signal.aborted && clientPaymentChecksApiState.key === key && JSON.stringify({
    publicationEpoch: reportReadPublicationIdentity(),
    base,
    token: trackerApiToken(),
    access: trackerAccessEnvelope("service"),
    org: clientPaymentsDashState.org || "",
    search: clientPaymentsDashState.search || "",
    payer: clientPaymentsDashState.payer || "All",
    range: clientPaymentsDashState.range || "all",
    sort: clientPaymentsDashState.sort || "date",
    dir: clientPaymentsDashState.dir || "desc",
    page: clientPaymentsDashState.page || 1
  }) === key;
  if (clientPaymentChecksApiState.key === key && ["pending", "ok", "error"].includes(clientPaymentChecksApiState.status)) return;
  clientPaymentChecksApiState = { key, status: "pending", response: null, error: "" };
  // A READ WITH NO DEADLINE IS A SPINNER WITH NO END. This lane carries the only
  // money surface an external client has, and it had neither a deadline nor a slow
  // state: in production no report read session is configured, so reportSessionFetch
  // falls through to a bare fetch and nothing bounded it. Same shape as the
  // turnaround and workflow-board reads - a pending slot becomes an honest error the
  // reader can retry, instead of "Please hold while we load your payments." forever.
  const checksRequestId = ++clientPaymentChecksReadSequence;
  clientPaymentChecksController?.abort();
  const controller = new AbortController();
  clientPaymentChecksController = controller;
  const settleDeadline = () => {
    if (checksRequestId !== clientPaymentChecksReadSequence || clientPaymentChecksApiState.key !== key
      || clientPaymentChecksApiState.status !== "pending") return;
    clientPaymentChecksApiState = { key, status: "error", response: null,
      error: "Payments are taking longer than expected to load. Retry, or narrow the filters." };
    controller.abort();
    if (clientReportSurfaceActive()) renderClientView();
    if (state.view === "clientHome") renderClientHome();
  };
  const deadline = setTimeout(settleDeadline, CLIENT_PAYMENT_CHECKS_TIMEOUT_MS);
  const clearDeadline = () => clearTimeout(deadline);
  // A response that arrived for a superseded identity must not render, but it must
  // not leave the panel stranded either: repaint so the CURRENT identity issues its
  // own read, instead of the model waiting on an answer nobody will ever deliver.
  const supersededRepaint = () => {
    clearDeadline();
    if (checksRequestId !== clientPaymentChecksReadSequence) return;
    if (clientReportSurfaceActive()) renderClientView();
  };
  const token = trackerApiToken();
  const checksParams = new URLSearchParams({ pageSize: "75", page: String(clientPaymentsDashState.page || 1), search: clientPaymentsDashState.search || "", payer: clientPaymentsDashState.payer || "All", range: clientPaymentsDashState.range || "all", sort: clientPaymentsDashState.sort || "date", dir: clientPaymentsDashState.dir || "desc" });
  if (clientPaymentsDashState.org) checksParams.set("orgId", clientPaymentsDashState.org);
  reportSessionFetch((typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(`${base}/api/client/payments/checks?${checksParams.toString()}`) : `${base}/api/client/payments/checks?${checksParams.toString()}`), {
    cache: "no-store",
    signal: controller.signal,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json();
      clearDeadline();
      if (!requestStillCurrent()) { supersededRepaint(); return; }
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        clientPaymentChecksApiState = { key, status: "error", response: null, error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body.message || body.error || `HTTP ${response.status}`) };
      } else {
        clientPaymentChecksApiState = { key, status: "ok", response: body, error: "" };
      }
      if (clientReportSurfaceActive()) renderClientView();
      // Company names just arrived: refresh the open Manage Users modal so
      // raw org IDs swap to roster names.
      const userMgmtModalEl = typeof document !== "undefined" ? document.getElementById("userMgmtModal") : null;
      if (userMgmtModalEl && !userMgmtModalEl.classList.contains("hidden") && clientPaymentChecksApiState.status === "ok") {
        renderUserMgmtList();
      }
    })
    .catch((error) => {
      clearDeadline();
      if (!requestStillCurrent()) { supersededRepaint(); return; }
      clientPaymentChecksApiState = { key, status: "error", response: null,
        error: error && error.name === "AbortError"
          ? "Loading payments was interrupted. Retry to read them again."
          : ((error && error.message) || "The payment feed could not be reached.") };
      if (clientReportSurfaceActive()) renderClientView();
      if (state.view === "clientHome") renderClientHome();
    });
}

function requestClientPaymentCheckDetail(row = {}) {
  const base = trackerApiBase();
  const cacheKey = clientPaymentCheckDetailCacheKey(row, base);
  const requestStillCurrent = () => clientPaymentCheckDetailCacheKey(row, base) === cacheKey;
  if (!row.key || clientPaymentCheckDetailCache.has(cacheKey)) return;
  if (!row.paymentId || !trackerApiEnabled() || typeof fetch !== "function" || !base) {
    clientPaymentCheckDetailCache.set(cacheKey, { status: "error", response: null, error: "live payment feed offline" });
    return;
  }
  clientPaymentCheckDetailCache.set(cacheKey, { status: "pending", response: null, error: "" });
  const token = trackerApiToken();
  const params = new URLSearchParams({ paymentId: row.paymentId });
  if (row.orgId) params.set("orgId", row.orgId);
  // The deadline. clearTimeout runs on BOTH settle paths below, so a fast
  // response never leaves a timer armed against a request that already landed.
  const clientPaymentDetailAbort = typeof AbortController === "function" ? new AbortController() : null;
  const clientPaymentDetailTimer = clientPaymentDetailAbort && typeof window !== "undefined" && typeof window.setTimeout === "function"
    ? window.setTimeout(() => { try { clientPaymentDetailAbort.abort(); } catch (e) {} }, CLIENT_PAYMENT_DETAIL_TIMEOUT_MS)
    : null;
  const clearClientPaymentDetailTimer = () => {
    if (clientPaymentDetailTimer !== null && typeof window !== "undefined" && typeof window.clearTimeout === "function") window.clearTimeout(clientPaymentDetailTimer);
  };
  reportSessionFetch((typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(`${base}/api/client/payments/checks/detail?${params.toString()}`) : `${base}/api/client/payments/checks/detail?${params.toString()}`), {
    cache: "no-store",
    ...(clientPaymentDetailAbort ? { signal: clientPaymentDetailAbort.signal } : {}),
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      clearClientPaymentDetailTimer();
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) {
        clientPaymentCheckDetailCache.delete(cacheKey);
        return;
      }
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        clientPaymentCheckDetailCache.set(cacheKey, {
          status: "error",
          response: null,
          error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body.message || body.error || `HTTP ${response.status}`)
        });
      } else {
        clientPaymentCheckDetailCache.set(cacheKey, { status: "ok", response: body, error: "" });
      }
      if (clientReportSurfaceActive()) renderClientView();
    })
    .catch((error) => {
      clearClientPaymentDetailTimer();
      if (!requestStillCurrent()) {
        clientPaymentCheckDetailCache.delete(cacheKey);
        return;
      }
      // A DEADLINE WE HIT IS NOT THE SAME NEWS AS A SERVER THAT REFUSED.
      // An aborted request says nothing about whether the report exists, so
      // it is reported as "took too long", is retryable, and never claims the
      // payment has no detail.
      const clientPaymentDetailTimedOut = Boolean(error) && (error.name === "AbortError" || /aborted/i.test(String(error.message || "")));
      const clientPaymentDetailMessage = clientPaymentDetailTimedOut
        ? `took longer than ${Math.round(CLIENT_PAYMENT_DETAIL_TIMEOUT_MS / 1000)} seconds and was stopped`
        : (error && error.message) || "the request failed";
      clientPaymentCheckDetailCache.set(cacheKey, { status: "error", response: null, error: clientPaymentDetailMessage, timedOut: clientPaymentDetailTimedOut });
      if (clientReportSurfaceActive()) renderClientView();
    });
}

function exportClientPaymentsChecksCsv(model = {}) {
  const today = localDateString();
  const rows = clientPaymentsSortRows(clientPaymentsFilterRows(Array.isArray(model.rows) ? model.rows : [], clientPaymentsDashState, today), clientPaymentsDashState);
  if (!rows.length) {
    showToast("No payments to export.");
    return;
  }
  const lines = [["Date Posted", "Hits Bank", "Payer", "Method", "Amount", "Service Lines", "Clients", "Reference"].map(csvEscape).join(",")];
  rows.forEach((row) => {
    lines.push([
      normalizeDateValue(row.paymentDate) || "",
      row.hitsBankDate || "",
      row.payer,
      row.method,
      row.amount.toFixed(2),
      row.serviceLines,
      row.clients,
      row.reference
    ].map(csvEscape).join(","));
  });
  downloadTextPayload(lines.join("\n"), `tms-payments-${today}.csv`, "text/csv;charset=utf-8");
  showToast("Payments CSV downloaded.");
}

function exportClientPaymentCheckDetailCsv(row = {}) {
  const detail = clientPaymentCheckDetailModel(row);
  if (detail.status !== "ok" || !detail.rows.length) {
    showToast("Disbursement report is not ready to export yet.");
    return;
  }
  if (!detail.complete) {
    showToast(clientPaymentDetailIncompleteMessage(detail));
    return;
  }
  const lines = [["Client", "Date of Service", "CPT", "Units", "Paid", "Payer", "Date Posted", "Reference"].map(csvEscape).join(",")];
  detail.rows.forEach((line) => {
    lines.push([
      line.client,
      line.serviceDate,
      line.cpt,
      line.units,
      line.paid.toFixed(2),
      row.payer,
      normalizeDateValue(row.paymentDate) || "",
      row.reference
    ].map(csvEscape).join(","));
  });
  const slug = String(row.reference || row.paymentId || "payment").replace(/[^A-Za-z0-9_-]+/g, "-");
  downloadTextPayload(lines.join("\n"), `tms-disbursement-${slug}-${localDateString()}.csv`, "text/csv;charset=utf-8");
  showToast("Disbursement report downloaded.");
}

function bindClientPaymentsControls() {
  const body = document.getElementById("clientPivotBody");
  if (!body) return;
  bindManagementListPager(body, "payment-detail", renderClientReportsView);
  // An honest error the reader cannot act on is still a dead end. Retry clears the
  // slot so the model issues a fresh read on the very next paint.
  body.querySelector("[data-payments-retry]")?.addEventListener("click", () => {
    clientPaymentChecksApiState = { key: "", status: "idle", response: null, error: "" };
    renderClientReportsView();
  });
  const search = body.querySelector("[data-payments-search]");
  if (search) {
    search.addEventListener("input", () => {
      clientPaymentsDashState.search = search.value;
      clientPaymentsDashState.page = 1;
      clientPaymentsDashState.focusSearch = true;
      renderClientReportsView();
    });
    if (clientPaymentsDashState.focusSearch) {
      clientPaymentsDashState.focusSearch = false;
      const caret = search.value.length;
      search.focus();
      try { search.setSelectionRange(caret, caret); } catch (_) { /* some input types reject selection ranges */ }
    }
  }
  const payerSelect = body.querySelector("[data-payments-payer]");
  if (payerSelect) {
    payerSelect.addEventListener("change", () => {
      clientPaymentsDashState.payer = payerSelect.value || "All";
      clientPaymentsDashState.page = 1;
      renderClientReportsView();
    });
  }
  const rangeSelect = body.querySelector("[data-payments-range]");
  if (rangeSelect) {
    rangeSelect.addEventListener("change", () => {
      clientPaymentsDashState.range = rangeSelect.value || "all";
      clientPaymentsDashState.page = 1;
      renderClientReportsView();
    });
  }
  const orgSelect = body.querySelector("[data-payments-org]");
  if (orgSelect) {
    orgSelect.addEventListener("change", () => {
      clientPaymentsDashState.org = orgSelect.value || "";
      clientPaymentsDashState.expanded = "";
      clientPaymentsDashState.page = 1;
      renderClientReportsView();
    });
  }
  const clearButton = body.querySelector("[data-payments-clear]");
  if (clearButton) {
    clearButton.addEventListener("click", () => {
      clientPaymentsDashState.search = "";
      clientPaymentsDashState.payer = "All";
      clientPaymentsDashState.range = "all";
      clientPaymentsDashState.org = "";
      clientPaymentsDashState.expanded = "";
      clientPaymentsDashState.focusSearch = false;
      clientPaymentsDashState.page = 1;
      renderClientReportsView();
    });
  }
  Array.from(body.querySelectorAll("[data-payments-sort]")).forEach((button) => {
    button.addEventListener("click", () => {
      const column = button.dataset.paymentsSort || "date";
      if (clientPaymentsDashState.sort === column) {
        clientPaymentsDashState.dir = clientPaymentsDashState.dir === "asc" ? "desc" : "asc";
      } else {
        clientPaymentsDashState.sort = column;
        clientPaymentsDashState.dir = column === "payer" ? "asc" : "desc";
      }
      clientPaymentsDashState.page = 1;
      renderClientReportsView();
    });
  });
  Array.from(body.querySelectorAll("[data-payments-page]")).forEach((button) => {
    button.addEventListener("click", () => {
      clientPaymentsDashState.page = Math.max(1, Number(clientPaymentsDashState.page || 1) + (button.dataset.paymentsPage === "prev" ? -1 : 1));
      clientPaymentsDashState.expanded = "";
      renderClientReportsView();
    });
  });
  Array.from(body.querySelectorAll("[data-payments-expand]")).forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.paymentsExpand || "";
      clientPaymentsDashState.expanded = clientPaymentsDashState.expanded === key ? "" : key;
      renderClientReportsView();
    });
  });
  const exportButton = body.querySelector("[data-payments-export]");
  if (exportButton) {
    exportButton.addEventListener("click", () => {
      if (clientPaymentsLastModel) exportClientPaymentsChecksCsv(clientPaymentsLastModel);
    });
  }
  Array.from(body.querySelectorAll("[data-payments-detail-export]")).forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.paymentsDetailExport || "";
      const row = ((clientPaymentsLastModel && clientPaymentsLastModel.rows) || []).find((entry) => entry.key === key);
      if (row) exportClientPaymentCheckDetailCsv(row);
    });
  });
  Array.from(body.querySelectorAll("[data-payments-detail-retry]")).forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.paymentsDetailRetry || "";
      const row = ((clientPaymentsLastModel && clientPaymentsLastModel.rows) || []).find((entry) => entry.key === key);
      if (!row) return;
      // Dropping the cache entry is what makes the next render re-request;
      // clientPaymentCheckDetailModel only fetches when there is no entry.
      clientPaymentCheckDetailCache.delete(clientPaymentCheckDetailCacheKey(row));
      renderClientReportsView();
    });
  });
  // Open the real claim drawer from a disbursement line. openDrawer is the one
  // funnel every claim opener goes through, so this inherits the role gate, the
  // permission gate and - critically - the server-side tenant scoping on
  // /api/tracker/claims/:claimId, which resolves the claim against the scope of
  // the calling principal. No id-keyed lookup that bypasses scope is introduced.
  //
  // The payments view is not re-rendered on open: the drawer is a document-level
  // overlay, clientPaymentsDashState.expanded is untouched, and closeDrawer
  // restores focus to __drawerPrevFocus - this row - so the user lands back on
  // the exact line they left, with the payment still expanded.
  Array.from(body.querySelectorAll("[data-payments-open-claim]")).forEach((rowEl) => {
    const openFromDisbursementRow = () => {
      const claimId = rowEl.dataset.paymentsOpenClaim || "";
      if (!claimId) return;
      openDrawer(claimId, {
        orgId: rowEl.dataset.paymentsOrgId || "",
        serviceId: rowEl.dataset.paymentsServiceId || "",
        queueContext: { source: "payments-disbursement" }
      });
    };
    rowEl.addEventListener("click", openFromDisbursementRow);
    rowEl.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
      event.preventDefault();
      openFromDisbursementRow();
    });
  });
}

function clientPaymentLedgerRows(claims = []) {
  const rows = [];
  claims.forEach((claim) => {
    const services = Array.isArray(claim.services) ? claim.services : [];
    const serviceById = new Map(services.map((service) => [String(service.serviceId || service.id || ""), service]));
    const clientName = firstPresent(claim.patient, claim.patientName, claim.client);
    const claimPayments = Array.isArray(claim.payments) ? claim.payments : [];
    claimPayments.forEach((payment, index) => {
      const amount = moneyNumber(firstPresent(payment.applied, payment.amount, payment["Payment Amount"], 0));
      if (amount <= 0) return;
      // NOT String(firstPresent(..., "")). firstPresent is values.find(hasPresent)
      // and hasPresent rejects "", so a trailing "" fallback is never selectable:
      // find() returns undefined and String(undefined) is the literal "undefined",
      // a truthy string that reads like a real service id downstream.
      const serviceId = String(firstPresent(payment.serviceId, payment.serviceID, payment["Service ID"]) || "");
      const service = serviceById.get(serviceId) || services[index] || services[0] || {};
      rows.push({
        orgId: String(claim.orgId || ""),
        paymentDate: normalizeDateValue(firstPresent(payment.date, payment.paymentDate, payment.postedDate, claim.dos)) || "",
        hitsBankDate: normalizeDateValue(payment.hitsBankDate) || "",
        payer: firstPresent(payment.source, payment.payer, payment["Payment Payer"], claim.primaryPayer, claim.secondaryPayer),
        amount,
        claimId: claim.claimId || "",
        serviceId,
        client: clientName,
        cpt: firstPresent(service.procedure, service.cpt),
        units: moneyNumber(service.units),
        serviceDate: normalizeDateValue(firstPresent(service.dos, claim.dos)) || "",
        status: firstPresent(payment.attributedTo, payment.matchedTo, claim.paymentAttribution?.status, "Unmatched"),
        reference: firstPresent(payment.ref, payment.reference, payment.checkNumber, payment.externalPayerId, `Payment ${index + 1}`),
        source: firstPresent(payment.ref, payment.reference, payment.checkNumber, payment.externalPayerId, `Payment ${index + 1}`),
        sourceKind: "payment"
      });
    });
    if (claimPayments.some((payment) => moneyNumber(firstPresent(payment.applied, payment.amount, payment["Payment Amount"], 0)) > 0)) return;
    services.forEach((service) => {
      const financial = (() => { try { return serviceFinancialView(service, claim); } catch (_) { return {}; } })();
      const amount = moneyNumber(firstPresent(financial.insurancePaid, service.paid, service.totalInsAmtPaid, 0));
      if (amount <= 0) return;
      rows.push({
        orgId: String(claim.orgId || ""),
        paymentDate: normalizeDateValue(firstPresent(service.paymentDate, service.pmtDate, service.dos, claim.dos)) || "",
        payer: firstPresent(claim.primaryPayer, claim.secondaryPayer, "No payer"),
        amount,
        claimId: claim.claimId || "",
        serviceId: service.serviceId || service.id || "",
        client: clientName,
        cpt: firstPresent(service.procedure, service.cpt),
        units: moneyNumber(service.units),
        serviceDate: normalizeDateValue(firstPresent(service.dos, claim.dos)) || "",
        status: firstPresent(claim.paymentAttribution?.status, claim.match, "Posted"),
        reference: "Tracker paid total",
        source: "Tracker paid total",
        sourceKind: "service-paid"
      });
    });
  });
  return rows;
}

function renderClientFormulaReconciliation(claims = [], serviceRows = [], totals = {}) {
  const model = clientFormulaReconciliationModel(claims, serviceRows, totals);
  return `
    <section class="client-formula-shell ${model.ready ? "client-formula-shell--green" : "client-formula-shell--amber"}">
      <div class="client-formula-head">
        <div>
          <span>Client Workbook Certification</span>
          <strong>${model.ready ? "Client view totals reconcile" : "Client view needs reconciliation"}</strong>
          <p>${escapeHtml(model.scopeLabel)} · ${formatNounCount(model.claims, "claim")} · ${formatNounCount(model.serviceLines, "service line")} · ${formatNounCount(model.monthlyRows, "monthly payer row")}</p>
        </div>
        <div class="client-formula-proof">
          <span>Expected - Paid - AR</span>
          <strong>${money(model.claimFormula.delta)}</strong>
          <small>${money(model.claimFormula.expected)} - ${money(model.claimFormula.paid)} - ${money(model.claimFormula.outstanding)}</small>
        </div>
      </div>
      <div class="client-formula-cards">
        ${model.cards.map((card) => `
          <div class="client-formula-card client-formula-card--${card.tone}">
            <span>${escapeHtml(card.label)}</span>
            <strong>${escapeHtml(card.value)}</strong>
            <small>${escapeHtml(card.detail)}</small>
          </div>
        `).join("")}
      </div>
      <div class="client-formula-ledger">
        <div class="client-formula-row client-formula-row--header">
          <span>Check</span>
          <span>Formula Proof</span>
          <span>Delta</span>
          <span>Status</span>
        </div>
        ${model.rows.map((row) => `
          <div class="client-formula-row client-formula-row--${row.tone}">
            <span data-label="Check"><strong>${escapeHtml(row.label)}</strong><small>${escapeHtml(row.grain)}</small></span>
            <span data-label="Formula Proof">${escapeHtml(row.proof)}</span>
            <span data-label="Delta"><strong>${money(row.delta)}</strong></span>
            <span data-label="Status">${pill(row.status, row.tone === "green" ? "green" : "amber")}</span>
          </div>
        `).join("")}
      </div>
    </section>
  `;
}

function clientFormulaReconciliationModel(claims = [], serviceRows = [], totals = {}) {
  const monthlyRows = monthlyPayerSummaryRows(serviceRows);
  const claimExpected = moneyNumber(totals.totalExpected ?? claims.reduce((sum, claim) => sum + claimFinancialView(claim).expectedReimbursement, 0));
  const claimPaid = moneyNumber(totals.totalPaid ?? claims.reduce((sum, claim) => sum + claimFinancialView(claim).totalInsurancePaid, 0));
  const claimOutstanding = moneyNumber(totals.totalOwed ?? claims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0));
  const serviceExpected = moneyNumber(totals.serviceExpected ?? serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).expectedReimbursement, 0));
  const servicePaid = moneyNumber(totals.servicePaid ?? serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).insurancePaid, 0));
  const serviceOutstanding = moneyNumber(totals.serviceOwed ?? serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).sourceOutstanding, 0));
  const monthlyExpected = moneyNumber(monthlyRows.reduce((sum, row) => sum + row.expectedInsurance, 0));
  const monthlyPaid = moneyNumber(monthlyRows.reduce((sum, row) => sum + row.insurancePaid, 0));
  const monthlyOutstanding = moneyNumber(monthlyRows.reduce((sum, row) => sum + row.payerBalance, 0));
  const claimFormula = {
    expected: claimExpected,
    paid: claimPaid,
    outstanding: claimOutstanding,
    delta: moneyNumber(claimExpected - claimPaid - claimOutstanding)
  };
  const serviceTieDelta = moneyNumber((claimExpected - serviceExpected) + (claimPaid - servicePaid) + (claimOutstanding - serviceOutstanding));
  const monthlyDelta = moneyNumber(monthlyExpected - monthlyPaid - monthlyOutstanding);
  const insuranceExpectedDelta = moneyNumber(claimFormula.delta - monthlyDelta);
  const selectedClient = totals.selectedClient || "All";
  const selectedState = totals.selectedState || "All";
  const selectedInsurance = totals.selectedInsurance || "All";
  const scopeLabel = [
    selectedClient === "All" ? "All clients" : selectedClient,
    selectedState === "All" ? "All states" : selectedState,
    selectedInsurance === "All" ? "All insurance" : selectedInsurance
  ].join(" / ");
  const rows = [
    {
      label: "Claim Formula",
      grain: "Filtered claim rollup",
      proof: `${money(claimExpected)} expected - ${money(claimPaid)} paid - ${money(claimOutstanding)} AR`,
      delta: claimFormula.delta
    },
    {
      label: "Service-Line Tie-Out",
      grain: "Claim totals vs service rows",
      proof: `${money(serviceExpected)} expected / ${money(servicePaid)} paid / ${money(serviceOutstanding)} AR`,
      delta: serviceTieDelta
    },
    {
      label: "Monthly Payer Pivot",
      grain: "Workbook monthly data summary by payer",
      proof: `${money(monthlyExpected)} expected insurance - ${money(monthlyPaid)} paid - ${money(monthlyOutstanding)} payer balance`,
      delta: monthlyDelta
    },
    {
      label: "Insurance Expected Bridge",
      grain: "Claim formula compared with monthly payer formula",
      proof: `${money(claimFormula.delta)} claim delta vs ${money(monthlyDelta)} monthly payer delta`,
      delta: insuranceExpectedDelta
    }
  ].map((row) => ({
    ...row,
    tone: moneyNumber(row.delta) === 0 ? "green" : "amber",
    status: moneyNumber(row.delta) === 0 ? "Tied" : "Review"
  }));
  const ready = rows.every((row) => row.tone === "green");
  return {
    ready,
    claims: claims.length,
    serviceLines: serviceRows.length,
    monthlyRows: monthlyRows.length,
    scopeLabel,
    claimFormula,
    rows,
    cards: [
      {
        label: "Expected",
        value: money(claimExpected),
        detail: `${formatNounCount(serviceRows.length, "service line")} in selected scope`,
        tone: "blue"
      },
      {
        label: "Paid",
        value: money(claimPaid),
        detail: claimExpected > 0 ? `${Math.round((claimPaid / claimExpected) * 100)}% of expected` : "No expected denominator",
        tone: "green"
      },
      {
        label: "Open AR",
        value: money(claimOutstanding),
        detail: `${formatNounCount(claims.filter((claim) => claimFinancialView(claim).sourceOutstanding > 0).length, "claim")} still open`,
        tone: claimOutstanding > 0 ? "amber" : "green"
      },
      {
        label: "Workbook Status",
        value: ready ? "Certified" : "Needs Review",
        detail: clientFormulaWorkbookStatusDetail(rows.filter((row) => row.status === "Tied").length, rows.length),
        tone: ready ? "green" : "amber"
      }
    ]
  };
}

function clientFormulaWorkbookStatusDetail(tiedCount = 0, totalChecks = 0) {
  return `${Number(tiedCount || 0).toLocaleString()}/${formatNounCount(totalChecks, "check")} tied`;
}

function renderClientRcmCockpit(claims, statementRows) {
  const categories = clientRcmWorkCategories(claims, statementRows);
  const statementCommand = clientStatementCommandModel(claims, statementRows);
  const totalClientOwned = categories
    .filter((category) => category.owner === "Client")
    .reduce((sum, category) => sum + category.outstanding, 0);
  const totalTmsOwned = categories
    .filter((category) => category.owner !== "Client")
    .reduce((sum, category) => sum + category.outstanding, 0);
  const clientActions = categories.filter((category) => category.owner === "Client").reduce((sum, category) => sum + category.count, 0);
  const tmsActions = categories.filter((category) => category.owner !== "Client").reduce((sum, category) => sum + category.count, 0);
  return `
    <div class="client-rcm-summary">
      <div>
        <span>Client-Owned Items</span>
        <strong>${clientActions}</strong>
        <small>${money(totalClientOwned)} tied to client action</small>
      </div>
      <div>
        <span>TMS-Owned Items</span>
        <strong>${tmsActions}</strong>
        <small>${money(totalTmsOwned)} in RCM follow-up</small>
      </div>
      <div>
        <span>Filtered Queue Scope</span>
        <strong>${claims.length}</strong>
        <small>${statementRows.length} PR statement lines</small>
      </div>
    </div>
    ${renderClientStatementCommand(statementCommand)}
    <div class="client-rcm-card-grid">
      ${categories.map(renderClientRcmWorkCard).join("")}
    </div>
  `;
}

function clientStatementCommandModel(claims = [], statementRows = getStatementRows(claims)) {
  const printableRows = statementRows.filter((row) => !state.statementPrints?.[row.service.serviceId]);
  const printedRows = statementRows.filter((row) => state.statementPrints?.[row.service.serviceId]);
  const blockedRows = claims.flatMap((claim) => (claim.services || [])
    .filter((service) => {
      if (statementEligibilityBlockReason(service, claim)) {
        return truePatientResponsibility(service) > 0
          || moneyNumber(service.patientBalance || 0) > 0
          || String(service.responsibleParty || "").toLowerCase().includes("patient");
      }
      return false;
    })
    .map((service) => ({ claim, service, blockReason: statementEligibilityBlockReason(service, claim) })));
  const printableAmount = moneyNumber(printableRows.reduce((sum, row) => sum + truePatientResponsibility(row.service), 0));
  const printedAmount = moneyNumber(printedRows.reduce((sum, row) => sum + truePatientResponsibility(row.service), 0));
  const blockedAmount = moneyNumber(blockedRows.reduce((sum, row) => sum + moneyNumber(row.service.patientBalance || 0), 0));
  const rows = [
    ...printableRows.map((row) => clientStatementCommandRow(row, "Ready To Print")),
    ...printedRows.map((row) => clientStatementCommandRow(row, "Printed")),
    ...blockedRows.map((row) => clientStatementCommandRow(row, "Blocked"))
  ].sort((a, b) => statementCommandRank(a.status) - statementCommandRank(b.status)
    || b.amount - a.amount
    || a.claimId.localeCompare(b.claimId));
  return {
    printableRows,
    printedRows,
    blockedRows,
    rows: rows.slice(0, 8),
    totals: {
      printable: printableRows.length,
      printed: printedRows.length,
      blocked: blockedRows.length,
      printableAmount,
      printedAmount,
      blockedAmount,
      allStatementAmount: moneyNumber(printableAmount + printedAmount)
    }
  };
}

function clientStatementCommandRow(row, status) {
  const isBlocked = status === "Blocked";
  const amount = isBlocked ? moneyNumber(row.service.patientBalance || 0) : truePatientResponsibility(row.service);
  const blockedReason = row.blockReason === "secondary-on-claim"
    ? "Secondary on claim - patient responsibility $0"
    : row.blockReason === "open-follow-up"
      ? "Active follow-up blocks printing"
      : patientResponsibilityStatus(row.service, row.claim);
  return {
    claimId: row.claim.claimId || "",
    serviceId: row.service.serviceId || "",
    dos: row.service.dos || "",
    status,
    amount,
    prCode: prCodes(row.service),
    reason: isBlocked ? blockedReason : prReasons(row.service),
    nextAction: clientStatementNextAction(row.service, status, row.blockReason)
  };
}

function statementCommandRank(status) {
  if (status === "Ready To Print") return 1;
  if (status === "Blocked") return 2;
  return 3;
}

function clientStatementNextAction(service, status, blockReason = "") {
  if (status === "Ready To Print") return "Print statement only for verified PR 1/2/3 responsibility.";
  if (status === "Printed") return "Keep print audit; do not rebill patient unless statement needs reissue.";
  if (blockReason === "secondary-on-claim") return "Bill or resolve the secondary payer; do not bill the patient.";
  if (blockReason === "open-follow-up") return "Finish or clear the follow-up, then recheck every print rule.";
  if (moneyNumber(service.patientBalance || 0) > 0) return "Keep balance in insurance follow-up until ERA PR 1/2/3 detail exists.";
  return "Do not print; responsible-party flip alone is not true patient responsibility.";
}

function renderClientStatementCommand(model) {
  return `
    <section class="client-statement-command" aria-label="Patient responsibility statement command">
      <div class="client-statement-command-head">
        <div>
          <span>Patient Responsibility Command</span>
          <strong>${model.totals.printable} printable PR line${model.totals.printable === 1 ? "" : "s"} · ${model.totals.blocked} blocked balance${model.totals.blocked === 1 ? "" : "s"}</strong>
          <p>Only eligible ERA PR 1, PR 2, or PR 3 lines can print. Any secondary on the claim, the 7-day grace period, or an active follow-up action blocks patient billing (Write-Off, Underpayment Review Required, Missing Fee Schedule, Claim Status Checkup Required, Primary Claim In Process and the automatic underpayment check's Insurance Follow-Up do not).</p>
        </div>
        ${pill(model.totals.blocked ? "Guarded PR" : "Statement ready", model.totals.blocked ? "amber" : "green")}
      </div>
      <div class="client-statement-summary">
        <div><span>Ready To Print</span><strong>${money(model.totals.printableAmount)}</strong><small>${formatNounCount(model.totals.printable, "service line")}</small></div>
        <div><span>Printed</span><strong>${money(model.totals.printedAmount)}</strong><small>${formatNounCount(model.totals.printed, "audited line")}</small></div>
        <div><span>Blocked Patient Balance</span><strong>${money(model.totals.blockedAmount)}</strong><small>Needs PR 1/2/3 proof</small></div>
        <div><span>Statement Total</span><strong>${money(model.totals.allStatementAmount)}</strong><small>Verified true PR only</small></div>
      </div>
      <div class="client-statement-ledger" role="table" aria-label="Patient responsibility statement command ledger">
        <div class="client-statement-row client-statement-header" role="row">
          <span>Claim / Service</span>
          <span>Status</span>
          <span>PR Proof</span>
          <span>Amount</span>
          <span>Next Action</span>
        </div>
        ${model.rows.length ? model.rows.map(renderClientStatementCommandRow).join("") : '<div class="client-statement-empty">No PR 1/2/3 statement rows or blocked patient-balance rows in the active filters.</div>'}
      </div>
    </section>
  `;
}

function renderClientStatementCommandRow(row) {
  const tone = row.status === "Ready To Print" ? "green" : row.status === "Blocked" ? "amber" : "blue";
  return `
    <div class="client-statement-row" role="row">
      <span data-label="Claim / Service"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.serviceId)} · ${escapeHtml(formatDate(row.dos))}</small></span>
      <span data-label="Status">${pill(row.status, tone)}</span>
      <span data-label="PR Proof"><strong>${escapeHtml(row.prCode)}</strong><small>${escapeHtml(row.reason)}</small></span>
      <span data-label="Amount"><strong>${money(row.amount)}</strong></span>
      <span data-label="Next Action"><small>${escapeHtml(row.nextAction)}</small></span>
    </div>
  `;
}

function clientRcmWorkCategories(claims, statementRows) {
  const unprintedStatements = statementRows.filter((row) => !state.statementPrints?.[row.service.serviceId]);
  const authorizationClaims = claims.filter((claim) => hasClientAuthorizationSignal(claim));
  const documentationClaims = claims.filter((claim) => hasClientDocumentationSignal(claim) && !authorizationClaims.includes(claim));
  const secondaryClaims = claims.filter((claim) => hasSecondarySignal(claim));
  const deniedClaims = claims.filter((claim) => isDeniedClaim(claim));
  const payerClaims = claims.filter((claim) => {
    if (authorizationClaims.includes(claim) || documentationClaims.includes(claim) || secondaryClaims.includes(claim)) return false;
    return claimFinancialView(claim).sourceOutstanding > 0 && matchesQueue(claim, "payerFollowUp");
  });
  return [
    clientRcmCategory("authorization", "Authorization / Coverage", "Client", authorizationClaims, "Upload authorization, coverage, credentialing, or provider correction proof."),
    clientRcmCategory("documents", "COB / Records / Documentation", "Client", documentationClaims, "Send COB, medical records, provider documentation, or requested client proof."),
    clientRcmStatementCategory(unprintedStatements),
    clientRcmCategory("secondary", "Secondary / Coordination", "Shared", secondaryClaims, "Confirm secondary coverage, auth, or primary EOB before billing secondary."),
    clientRcmCategory("denials", "Denials / Appeals", "TMS", deniedClaims, "Triage denial reason, appeal/correct claim, and document payer response."),
    clientRcmCategory("payer", "Payer Follow-Up", "TMS", payerClaims, "Check payer portal, EOB/ERA, underpayment, and next follow-up date.")
  ];
}

function clientRcmCategory(key, label, owner, claims, nextStep) {
  const sortedClaims = [...claims].sort(sortClaims);
  return {
    key,
    label,
    owner,
    count: sortedClaims.length,
    outstanding: sortedClaims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0),
    pastDue: sortedClaims.filter(isPastDue).length,
    nextStep,
    rows: sortedClaims.slice(0, 3).map((claim) => ({
      id: claim.claimId,
      status: clientStatus(claim),
      detail: clientOpenNeed(claim),
      amount: claimFinancialView(claim).sourceOutstanding
    }))
  };
}

function clientRcmStatementCategory(statementRows) {
  const amount = statementRows.reduce((sum, row) => sum + truePatientResponsibility(row.service), 0);
  return {
    key: "statements",
    label: "Patient Statements",
    owner: "Client",
    count: statementRows.length,
    outstanding: amount,
    pastDue: 0,
    nextStep: "Print or review PR 1/2/3 statement lines only; non-PR balances stay in insurance follow-up.",
    rows: statementRows.slice(0, 3).map((row) => ({
      id: row.service.serviceId,
      status: row.claim.claimId,
      detail: prCodes(row.service) || "PR 1/2/3",
      amount: truePatientResponsibility(row.service)
    }))
  };
}

function renderClientRcmWorkCard(category) {
  const tone = category.owner === "Client" ? "amber" : category.owner === "Shared" ? "blue" : "green";
  return `
    <article class="client-rcm-card client-rcm-${escapeHtml(category.key)}">
      <div class="client-rcm-card-head">
        <div>
          <span>${escapeHtml(category.owner)} owned</span>
          <strong>${escapeHtml(category.label)}</strong>
        </div>
        ${pill(formatNounCount(category.count, "item"), tone)}
      </div>
      <div class="client-rcm-card-metrics">
        <span><small>Open AR / Amount</small><strong>${money(category.outstanding)}</strong></span>
        <span><small>Past Due</small><strong>${category.pastDue}</strong></span>
      </div>
      <p>${escapeHtml(category.nextStep)}</p>
      <div class="client-rcm-card-list">
        ${category.rows.length ? category.rows.map((row) => `
          <div>
            <strong>${escapeHtml(row.id)}</strong>
            <span>${escapeHtml(row.status)} · ${escapeHtml(row.detail)}</span>
            <b>${money(row.amount)}</b>
          </div>
        `).join("") : '<div><span>No rows in the active filters.</span></div>'}
      </div>
    </article>
  `;
}

function clientActionCommitmentModel(claims = []) {
  const evidenceRows = clientEvidenceRequestRows(claims);
  const rows = evidenceRows.map(clientActionCommitmentRowFromEvidence)
    .sort((a, b) => clientEvidenceStatusRank(a.status) - clientEvidenceStatusRank(b.status)
      || b.cashUnlock - a.cashUnlock
      || a.claimId.localeCompare(b.claimId));
  const visibleRows = rows.slice(0, 8);
  const totals = rows.reduce((acc, row) => {
    acc.commitments += 1;
    acc.cashUnlock += row.cashUnlock;
    acc.expected += row.expected;
    acc.paid += row.paid;
    acc.formulaDelta += row.formulaDelta;
    if (row.status === "Past Due") acc.pastDue += 1;
    if (row.status === "Due Soon" || row.status === "No Due Date") acc.needsDateOrDueSoon += 1;
    if (row.status === "Ready To Review") acc.readyToReview += 1;
    return acc;
  }, {
    commitments: 0,
    cashUnlock: 0,
    expected: 0,
    paid: 0,
    formulaDelta: 0,
    pastDue: 0,
    needsDateOrDueSoon: 0,
    readyToReview: 0
  });
  return {
    rows: visibleRows,
    allRows: rows,
    totals: {
      ...totals,
      cashUnlock: moneyNumber(totals.cashUnlock),
      expected: moneyNumber(totals.expected),
      paid: moneyNumber(totals.paid),
      formulaDelta: moneyNumber(totals.formulaDelta)
    }
  };
}

function clientActionCommitmentRowFromEvidence(row) {
  const formulaDelta = moneyNumber(row.expected - row.paid - row.impact);
  return {
    claimId: row.claimId,
    requestType: row.requestType,
    state: row.state,
    insurance: row.insurance,
    cashUnlock: moneyNumber(row.impact),
    expected: moneyNumber(row.expected),
    paid: moneyNumber(row.paid),
    formulaDelta,
    dueDate: row.dueDate,
    dueLabel: clientEvidenceDueLabel(row.dueDate),
    status: row.status,
    clientDeliverable: clientActionCommitmentDeliverable(row),
    tmsNextStep: clientActionCommitmentTmsNextStep(row),
    openNeed: row.openNeed
  };
}

function clientActionCommitmentDeliverable(row) {
  if (row.status === "Ready To Review") return "Proof received; no new client upload needed unless TMS finds a gap.";
  return row.nextAction || "Send the requested proof so TMS can continue payer follow-up.";
}

function clientActionCommitmentTmsNextStep(row) {
  if (row.status === "Ready To Review") return "Validate evidence, attach it to the payer packet, and resume follow-up.";
  if (row.requestType === "Authorization") return "Apply authorization proof to the affected services and clear auth blockers.";
  if (row.requestType === "COB / Coordination") return "Use the EOB or COB update to route primary/secondary payer follow-up.";
  if (row.requestType === "Records / Documentation") return "Attach the documentation packet and submit or appeal with proof.";
  if (row.requestType === "Coverage / Provider Proof") return "Apply coverage/provider proof and confirm payer-ready claim status.";
  return "Review proof, update the claim packet, and continue payer follow-up.";
}

function renderClientActionCommitmentBoard(claims = []) {
  const model = clientActionCommitmentModel(claims);
  return `
    <section class="client-action-commitment-board" aria-label="Client action commitment board">
      <div class="client-action-commitment-head">
        <div>
          <span>Client Action Commitment Board</span>
          <strong>${model.totals.commitments} client commitment${model.totals.commitments === 1 ? "" : "s"} blocking ${money(model.totals.cashUnlock)} cash path</strong>
          <p>Client-safe commitment tracker: each row shows the deliverable, due status, cash unlock, TMS next step, and formula proof from the same active slicers.</p>
        </div>
        ${pill(model.totals.pastDue ? `${model.totals.pastDue} past due` : "Current", model.totals.pastDue ? "red" : "green")}
      </div>
      <div class="client-action-commitment-summary">
        <div><span>Commitments</span><strong>${model.totals.commitments}</strong><small>Client-owned request rows</small></div>
        <div><span>Cash Unlock</span><strong>${money(model.totals.cashUnlock)}</strong><small>Open AR tied to proof</small></div>
        <div><span>Past Due</span><strong>${model.totals.pastDue}</strong><small>Needs escalation</small></div>
        <div><span>Due / No Date</span><strong>${model.totals.needsDateOrDueSoon}</strong><small>Due soon or missing date</small></div>
        <div><span>Ready To Review</span><strong>${model.totals.readyToReview}</strong><small>TMS validation queue</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.formulaDelta)}</strong><small>Expected - paid - cash unlock</small></div>
      </div>
      <div class="client-action-commitment-ledger" role="table" aria-label="Client action commitment ledger">
        <div class="client-action-commitment-row client-action-commitment-header" role="row">
          <span>Commitment / Claim</span>
          <span>State / Insurance</span>
          <span>Cash Unlock</span>
          <span>Due / Status</span>
          <span>Client Deliverable</span>
          <span>TMS Next Step</span>
        </div>
        ${model.rows.length ? model.rows.map(renderClientActionCommitmentRow).join("") : '<div class="client-action-commitment-empty">No client commitments in the active slicers.</div>'}
      </div>
    </section>
  `;
}

function renderClientActionCommitmentRow(row) {
  const tone = row.status === "Past Due" ? "red" : row.status === "Due Soon" ? "amber" : row.status === "Ready To Review" ? "green" : "blue";
  return `
    <div class="client-action-commitment-row" role="row">
      <span data-label="Commitment / Claim"><strong>${escapeHtml(row.requestType)}</strong><small>${escapeHtml(row.claimId)} · ${escapeHtml(row.openNeed)}</small></span>
      <span data-label="State / Insurance"><strong>${escapeHtml(row.state)}</strong><small>${escapeHtml(row.insurance)}</small></span>
      <span data-label="Cash Unlock"><strong>${money(row.cashUnlock)}</strong><small>Delta ${money(row.formulaDelta)}</small></span>
      <span data-label="Due / Status"><strong>${row.dueDate ? formatDate(row.dueDate) : "No date"}</strong><small>${pill(row.status, tone)} ${escapeHtml(row.dueLabel)}</small></span>
      <span data-label="Client Deliverable">${escapeHtml(row.clientDeliverable)}</span>
      <span data-label="TMS Next Step">${escapeHtml(row.tmsNextStep)}</span>
    </div>
  `;
}

function clientDocumentIntakeModel(claims = []) {
  const commitmentModel = clientActionCommitmentModel(claims);
  const rows = commitmentModel.allRows.map(clientDocumentIntakeRowFromCommitment)
    .sort((a, b) => clientDocumentIntakeRank(a.intakeStatus) - clientDocumentIntakeRank(b.intakeStatus)
      || clientEvidenceStatusRank(a.status) - clientEvidenceStatusRank(b.status)
      || b.cashUnlock - a.cashUnlock
      || a.claimId.localeCompare(b.claimId));
  const totals = rows.reduce((acc, row) => {
    acc.packets += 1;
    acc.cashUnlock += row.cashUnlock;
    acc.expected += row.expected;
    acc.paid += row.paid;
    acc.formulaDelta += row.formulaDelta;
    if (row.intakeStatus === "Awaiting Upload") acc.awaitingUpload += 1;
    if (row.intakeStatus === "Intake QA") acc.intakeQa += 1;
    if (row.intakeStatus === "Ready To File") acc.readyToFile += 1;
    return acc;
  }, {
    packets: 0,
    cashUnlock: 0,
    expected: 0,
    paid: 0,
    formulaDelta: 0,
    awaitingUpload: 0,
    intakeQa: 0,
    readyToFile: 0
  });
  return {
    rows: rows.slice(0, 8),
    allRows: rows,
    totals: {
      ...totals,
      cashUnlock: moneyNumber(totals.cashUnlock),
      expected: moneyNumber(totals.expected),
      paid: moneyNumber(totals.paid),
      formulaDelta: moneyNumber(totals.formulaDelta)
    }
  };
}

function clientDocumentIntakeRowFromCommitment(row) {
  const intakeStatus = clientDocumentIntakeStatus(row);
  return {
    claimId: row.claimId,
    requestType: row.requestType,
    status: row.status,
    state: row.state,
    insurance: row.insurance,
    dueDate: row.dueDate,
    dueLabel: row.dueLabel,
    cashUnlock: moneyNumber(row.cashUnlock),
    expected: moneyNumber(row.expected),
    paid: moneyNumber(row.paid),
    formulaDelta: moneyNumber(row.formulaDelta),
    intakeStatus,
    uploadPacket: clientDocumentIntakePacketName(row),
    acceptableProof: clientDocumentIntakeProof(row.requestType),
    uploadRoute: `/client-upload/${encodeURIComponent(row.claimId)}`,
    storageMode: "Approval controlled",
    intakeNextStep: clientDocumentIntakeNextStep(row, intakeStatus)
  };
}

function clientDocumentIntakeStatus(row) {
  if (row.status === "Ready To Review") return "Ready To File";
  if (row.status === "Past Due" || row.status === "Due Soon" || row.status === "No Due Date") return "Awaiting Upload";
  return "Intake QA";
}

function clientDocumentIntakeRank(status) {
  return {
    "Awaiting Upload": 0,
    "Intake QA": 1,
    "Ready To File": 2
  }[status] ?? 9;
}

function clientDocumentIntakePacketName(row) {
  if (row.requestType === "Authorization") return "Authorization packet";
  if (row.requestType === "COB / Coordination") return "COB / primary EOB packet";
  if (row.requestType === "Records / Documentation") return "Records packet";
  if (row.requestType === "Coverage / Provider Proof") return "Coverage / provider proof packet";
  return "Client proof packet";
}

function clientDocumentIntakeProof(requestType) {
  if (requestType === "Authorization") return "Authorization approval, corrected auth, denial, or coverage note.";
  if (requestType === "COB / Coordination") return "COB update, primary EOB, secondary coordination, or payer coverage proof.";
  if (requestType === "Records / Documentation") return "Medical records, clinical documentation, or payer-requested support.";
  if (requestType === "Coverage / Provider Proof") return "Coverage proof, credentialing support, or provider correction evidence.";
  return "Requested proof named in the client action row.";
}

function clientDocumentIntakeNextStep(row, intakeStatus) {
  if (intakeStatus === "Ready To File") return "TMS validates the received packet and files it to payer follow-up.";
  if (row.status === "No Due Date") return "Set a client due date, then upload the requested packet.";
  if (row.status === "Past Due") return "Upload now or confirm ETA so the payer packet can move.";
  return "Upload packet through the client intake route for TMS validation.";
}

function renderClientDocumentIntakeBoard(claims = []) {
  const model = clientDocumentIntakeModel(claims);
  return `
    <section class="client-document-intake-board" aria-label="Client document intake board">
      <div class="client-document-intake-head">
        <div>
          <span>Client Document Intake</span>
          <strong>${model.totals.packets} packet${model.totals.packets === 1 ? "" : "s"} mapped to ${money(model.totals.cashUnlock)} cash unlock</strong>
          <p>Read-only intake control for authorizations, COB/EOBs, records, and coverage proof tied to the same client-safe request math.</p>
        </div>
        ${pill(model.totals.awaitingUpload ? `${model.totals.awaitingUpload} awaiting upload` : "Filed / QA", model.totals.awaitingUpload ? "amber" : "green")}
      </div>
      <div class="client-document-intake-summary">
        <div><span>Packets</span><strong>${model.totals.packets}</strong><small>From active slicers</small></div>
        <div><span>Awaiting Upload</span><strong>${model.totals.awaitingUpload}</strong><small>Client action needed</small></div>
        <div><span>Intake QA</span><strong>${model.totals.intakeQa}</strong><small>TMS validates packet</small></div>
        <div><span>Ready To File</span><strong>${model.totals.readyToFile}</strong><small>Proof received</small></div>
        <div><span>Cash Unlock</span><strong>${money(model.totals.cashUnlock)}</strong><small>Open AR tied to packets</small></div>
        <div><span>Formula Delta</span><strong>${money(model.totals.formulaDelta)}</strong><small>Expected - paid - cash unlock</small></div>
      </div>
      <div class="client-document-intake-ledger" role="table" aria-label="Client document intake ledger">
        <div class="client-document-intake-row client-document-intake-header" role="row">
          <span>Packet / Claim</span>
          <span>State / Insurance</span>
          <span>Upload Status</span>
          <span>Proof Accepted</span>
          <span>Cash / Math</span>
          <span>Next Step</span>
        </div>
        ${model.rows.length ? model.rows.map(renderClientDocumentIntakeRow).join("") : '<div class="client-document-intake-empty">No client upload packets in the active slicers.</div>'}
      </div>
    </section>
  `;
}

function renderClientDocumentIntakeRow(row) {
  const tone = row.intakeStatus === "Awaiting Upload" ? "amber" : row.intakeStatus === "Ready To File" ? "green" : "blue";
  return `
    <div class="client-document-intake-row" role="row">
      <span data-label="Packet / Claim"><strong>${escapeHtml(row.uploadPacket)}</strong><small>${escapeHtml(row.claimId)} · ${escapeHtml(row.uploadRoute)}</small></span>
      <span data-label="State / Insurance"><strong>${escapeHtml(row.state)}</strong><small>${escapeHtml(row.insurance)}</small></span>
      <span data-label="Upload Status"><strong>${pill(row.intakeStatus, tone)}</strong><small>${escapeHtml(row.storageMode)} · ${row.dueDate ? formatDate(row.dueDate) : "No date"}</small></span>
      <span data-label="Proof Accepted">${escapeHtml(row.acceptableProof)}</span>
      <span data-label="Cash / Math"><strong>${money(row.cashUnlock)}</strong><small>Expected ${money(row.expected)} · Paid ${money(row.paid)} · Delta ${money(row.formulaDelta)}</small></span>
      <span data-label="Next Step">${escapeHtml(row.intakeNextStep)}</span>
    </div>
  `;
}

function renderClientEvidenceCenter(claims = []) {
  const rows = clientEvidenceRequestRows(claims);
  const totalImpact = rows.reduce((sum, row) => sum + row.impact, 0);
  const pastDue = rows.filter((row) => row.status === "Past Due").length;
  const missingDates = rows.filter((row) => row.status === "No Due Date").length;
  const readyCount = rows.filter((row) => row.status === "Ready To Review").length;
  const topType = topEvidenceValue(rows, "requestType") || "No requests";
  return `
    <div class="client-evidence-summary">
      <div>
        <span>Open Requests</span>
        <strong>${rows.length}</strong>
        <small>${readyCount} ready to review</small>
      </div>
      <div>
        <span>Client AR Blocked</span>
        <strong>${money(totalImpact)}</strong>
        <small>Expected - paid - open AR impact</small>
      </div>
      <div>
        <span>Past Due</span>
        <strong>${pastDue}</strong>
        <small>${missingDates} missing due dates</small>
      </div>
      <div>
        <span>Top Request Type</span>
        <strong>${escapeHtml(topType)}</strong>
        <small>From active slicers</small>
      </div>
    </div>
    <div class="client-evidence-ledger" role="table" aria-label="Client evidence request ledger">
      <div class="client-evidence-row client-evidence-header" role="row">
        <span>Claim / Request</span>
        <span>State / Insurance</span>
        <span>Impact</span>
        <span>Status</span>
        <span>Due</span>
        <span>Next Client Action</span>
      </div>
      ${rows.length ? rows.map(renderClientEvidenceRequestRow).join("") : empty("No client evidence requests in the active slicers.")}
    </div>
  `;
}

function clientEvidenceRequestRows(claims = []) {
  return claims
    .filter((claim) => {
      return hasClientAuthorizationSignal(claim)
          || hasClientDocumentationSignal(claim)
          || String(claim.action || "").startsWith("Client Action")
          || clientStatus(claim) === "Client Action Needed";
    })
    .map((claim) => {
      const financial = claimFinancialView(claim);
      const requestType = clientEvidenceRequestType(claim);
      return {
        claimId: claim.claimId,
        requestType,
        state: clientStateValue(claim) || "State Not Tagged",
        insurance: claim.primaryPayer || claim.secondaryPayer || "No payer tagged",
        impact: financial.sourceOutstanding,
        expected: financial.expectedReimbursement,
        paid: financial.totalInsurancePaid,
        dueDate: claim.followUpDate || "",
        status: clientEvidenceStatus(claim),
        openNeed: clientOpenNeed(claim),
        nextAction: clientEvidenceNextAction(claim, requestType)
      };
    })
    .sort((a, b) => clientEvidenceStatusRank(a.status) - clientEvidenceStatusRank(b.status)
      || b.impact - a.impact
      || String(a.claimId).localeCompare(String(b.claimId)));
}

function renderClientEvidenceRequestRow(row) {
  const tone = row.status === "Past Due" ? "red" : row.status === "Due Soon" ? "amber" : row.status === "Ready To Review" ? "green" : "blue";
  const formulaDelta = moneyNumber(row.expected - row.paid - row.impact);
  return `
    <div class="client-evidence-row" role="row">
      <span data-label="Claim / Request">
        <strong>${escapeHtml(row.claimId)}</strong>
        <small>${escapeHtml(row.requestType)} · ${escapeHtml(row.openNeed)}</small>
      </span>
      <span data-label="State / Insurance">
        <strong>${escapeHtml(row.state)}</strong>
        <small>${escapeHtml(row.insurance)}</small>
      </span>
      <span data-label="Impact">
        <strong>${money(row.impact)}</strong>
        <small>Delta ${money(formulaDelta)}</small>
      </span>
      <span data-label="Status">${pill(row.status, tone)}</span>
      <span data-label="Due">
        <strong>${row.dueDate ? formatDate(row.dueDate) : "No date"}</strong>
        <small>${escapeHtml(clientEvidenceDueLabel(row.dueDate))}</small>
      </span>
      <span data-label="Next Client Action">${escapeHtml(row.nextAction)}</span>
    </div>
  `;
}

function clientEvidenceRequestType(claim = {}) {
  const text = claimOperationalText(claim);
  if (/auth|authorization/.test(text)) return "Authorization";
  if (/coverage|credential|provider/.test(text)) return "Coverage / Provider Proof";
  if (/cob|coordination|primary eob|secondary/.test(text)) return "COB / Coordination";
  if (/medical record|records|documentation|document/.test(text)) return "Records / Documentation";
  return "Client Proof";
}

function clientEvidenceStatus(claim = {}) {
  const text = claimOperationalText(claim);
  if (/uploaded|received|sent proof|proof received|ready to review/.test(text)) return "Ready To Review";
  if (!claim.followUpDate) return "No Due Date";
  const due = localDateFromDateOnly(claim.followUpDate);
  if (!due) return "No Due Date";
  const days = Math.ceil((due - todayStart()) / 86400000);
  if (days < 0 || isPastDue(claim)) return "Past Due";
  if (days <= 3) return "Due Soon";
  return "Open";
}

function clientEvidenceStatusRank(status) {
  return {
    "Past Due": 0,
    "No Due Date": 1,
    "Due Soon": 2,
    "Open": 3,
    "Ready To Review": 4
  }[status] ?? 9;
}

function clientEvidenceDueLabel(dateValue) {
  if (!dateValue) return "Needs owner date";
  const due = localDateFromDateOnly(dateValue);
  if (!due) return "Needs owner date";
  const days = Math.ceil((due - todayStart()) / 86400000);
  if (days < 0) return `${formatNounCount(Math.abs(days), "day")} late`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due in ${days} days`;
}

function clientEvidenceNextAction(claim = {}, requestType = "Client Proof") {
  if (requestType === "Authorization") return "Upload authorization approval, denial, or corrected authorization details.";
  if (requestType === "Coverage / Provider Proof") return "Send coverage proof, credentialing proof, or provider correction support.";
  if (requestType === "COB / Coordination") return "Send COB update, primary EOB, or secondary coordination confirmation.";
  if (requestType === "Records / Documentation") return "Upload the requested records or documentation packet for payer review.";
  return clientOpenNeed(claim) === "Authorization needed"
    ? "Upload authorization approval or corrected authorization details."
    : "Send the requested client proof so TMS can continue payer follow-up.";
}

function topEvidenceValue(rows = [], key) {
  const counts = rows.reduce((map, row) => {
    const value = row[key] || "";
    if (!value) return map;
    const current = map.get(value) || { value, count: 0, impact: 0 };
    current.count += 1;
    current.impact += row.impact || 0;
    map.set(value, current);
    return map;
  }, new Map());
  return Array.from(counts.values()).sort((a, b) => b.count - a.count || b.impact - a.impact)[0]?.value || "";
}

function hasClientAuthorizationSignal(claim) {
  const text = claimOperationalText(claim);
  return /auth|authorization|coverage|credential|provider/.test(text);
}

function hasClientDocumentationSignal(claim) {
  const text = claimOperationalText(claim);
  return /cob|coordination|document|documentation|medical record|records|client action/.test(text);
}

function hasSecondarySignal(claim) {
  const text = claimOperationalText(claim);
  return /secondary|primary eob|coordination/.test(text);
}

function clientInsuranceValues(claims) {
  return Array.from(new Set(claims.flatMap((claim) => [claim.primaryPayer, claim.secondaryPayer]).filter(Boolean)))
    .sort((a, b) => String(a).localeCompare(String(b)));
}

function clientStateValues(claims) {
  return Array.from(new Set(claims.map(clientStateValue).filter(Boolean)))
    .sort((a, b) => String(a).localeCompare(String(b)));
}

function clientStateValue(claim = {}) {
  if (claim.state) return claim.state;
  const text = [
    claim.facility,
    claim.client,
    claim.orgId,
    claim.orgName,
    claim.primaryPayer,
    claim.secondaryPayer
  ].filter(Boolean).join(" ").toUpperCase();
  const statePatterns = [
    ["GA", /\b(GA|GEORGIA)\b/],
    ["MD", /\b(MD|MARYLAND)\b/],
    ["NC", /\b(NC|NORTH CAROLINA|CAROLINA)\b/],
    ["TN", /\b(TN|TENNESSEE|TENNCARE)\b/],
    ["VA", /\b(VA|VIRGINIA)\b/],
    ["CT", /\b(CT|CONNECTICUT)\b/],
    ["NJ", /\b(NJ|NEW JERSEY)\b/],
    ["NY", /\b(NY|NEW YORK)\b/]
  ];
  return statePatterns.find(([, pattern]) => pattern.test(text))?.[0] || "";
}

function renderClientCharts(claims, serviceRows, totals) {
  const allClaims = getClaims();
  const stateRows = groupedClientChartRows(claims, (claim) => clientStateValue(claim) || "Unknown");
  const payerRows = groupedClientChartRows(claims, (claim) => claim.primaryPayer || "Unknown");
  const agingRows = CT_AR_AGING_BUCKET_KEYS.map((aging) => {
    const agingClaims = claims.filter((claim) => claimAgingBand(claim) === aging);
    return {
      label: aging,
      claims: agingClaims.length,
      value: agingClaims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0)
    };
  }).filter((row) => row.claims || row.value);
  const expectedRows = [
    { label: "Expected", value: totals.totalExpected, claims: serviceRows.length },
    { label: "Paid", value: totals.totalPaid, claims: claims.length },
    { label: "Outstanding", value: totals.totalOwed, claims: claims.filter((claim) => claimFinancialView(claim).sourceOutstanding > 0).length }
  ];
  return [
    renderClientBoardroomChartWall(claims, serviceRows, totals),
    renderClientSlicerCommand(allClaims, totals),
    renderClientFilterImpactBoard(allClaims, claims, totals),
    renderClientSlicerVisualCommand(allClaims, claims, serviceRows, totals),
    renderClientChartContext(claims, serviceRows, totals),
    renderClientChartAudit(claims, serviceRows, totals),
    renderClientChartAssuranceBoard(claims, serviceRows, totals),
    renderClientChartPackExport(claims, serviceRows, totals),
    renderClientCollectionWaterfall(claims, serviceRows, totals),
    renderClientCashForecast(claims, serviceRows, totals),
    renderClientAgingDenialHeatmap(claims, serviceRows, totals),
    renderClientBoardroomScorecard(claims, serviceRows, totals),
    renderClientBoardroomNarrative(claims, serviceRows, totals),
    renderClientChartDecisionMatrix(claims, serviceRows, totals),
    renderClientMeetingBrief(claims, serviceRows, totals),
    renderClientExecutiveInsights(claims, serviceRows, totals),
    renderClientRiskTimeline(claims, totals),
    renderClientPayerStateDrilldown(claims, totals),
    renderClientFilteredClaimLedger(claims, serviceRows, totals),
    renderClientPowerBiDeck(claims, serviceRows, totals),
    renderClientPayerActionBoard(claims, serviceRows, totals),
    renderClientMonthlyPayerAnalytics(serviceRows),
    renderClientPivotHeatmap(claims, totals),
    renderClientBarChart("Outstanding By State", stateRows, "state filter"),
    renderClientBarChart("Outstanding By Insurance", payerRows, "insurance filter"),
    renderClientBarChart("Aging Mix", agingRows, "follow-up priority"),
    renderClientBarChart("Expected vs Paid", expectedRows, "math rollup")
  ].join("");
}

function renderClientBoardroomChartWall(claims = [], serviceRows = [], totals = {}) {
  const model = clientBoardroomChartWallModel(claims, serviceRows, totals);
  return `
    <section class="client-chart-wall" aria-label="Client boardroom chart wall">
      <div class="client-chart-wall-head">
        <div>
          <span>Boardroom Chart Wall</span>
          <strong>Client RCM performance by month, state, and insurance</strong>
          <p>First-screen chart pack for client meetings. Every visual is driven by the active client, state, and insurance filters and reconciles to expected, paid, and open AR.</p>
        </div>
        <div class="client-chart-wall-proof">
          <span>Filtered Formula</span>
          <strong>${money(model.formula.delta)}</strong>
          <small>${money(model.formula.expected)} expected - ${money(model.formula.paid)} paid - ${money(model.formula.openAr)} AR</small>
        </div>
      </div>
      <div class="client-chart-wall-filters">
        ${model.filters.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong>${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-chart-wall-kpis">
        ${model.kpis.map((item) => `
          <span class="client-chart-wall-kpi client-chart-wall-kpi--${escapeHtml(item.tone)}">
            <small>${escapeHtml(item.label)}</small>
            <strong>${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-chart-wall-grid">
        ${model.charts.map(renderClientBoardroomChartCard).join("")}
      </div>
    </section>
  `;
}

function clientBoardroomChartWallModel(claims = [], serviceRows = [], totals = {}) {
  const monthlyRows = monthlyPayerTrendRows(monthlyPayerSummaryRows(serviceRows)).slice(-8);
  const stateRows = clientSlicerVisualRows(claims, "state").slice(0, 7);
  const insuranceRows = clientSlicerVisualRows(claims, "insurance").slice(0, 7);
  const totalExpected = moneyNumber(totals.totalExpected || 0);
  const totalPaid = moneyNumber(totals.totalPaid || 0);
  const totalOwed = moneyNumber(totals.totalOwed || 0);
  const serviceExpected = moneyNumber(totals.serviceExpected || 0);
  const servicePaid = moneyNumber(totals.servicePaid || 0);
  const serviceOwed = moneyNumber(totals.serviceOwed || 0);
  const formula = {
    expected: totalExpected,
    paid: totalPaid,
    openAr: totalOwed,
    delta: moneyNumber(totalExpected - totalPaid - totalOwed)
  };
  const serviceFormula = {
    expected: serviceExpected,
    paid: servicePaid,
    openAr: serviceOwed,
    delta: moneyNumber(serviceExpected - servicePaid - serviceOwed)
  };
  const openClaims = claims.filter((claim) => claimFinancialView(claim).sourceOutstanding > 0).length;
  const collectionRate = formula.expected > 0 ? formula.paid / formula.expected : null;
  const topState = stateRows[0] || { label: "No state", outstanding: 0, claims: 0 };
  const topInsurance = insuranceRows[0] || { label: "No insurance", outstanding: 0, claims: 0 };
  const clientProofAr = moneyNumber(claims
    .filter((claim) => clientStatus(claim) === "Client Action Needed" || clientOpenNeed(claim) !== "None")
    .reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0));
  const chartModels = [
    {
      title: "Monthly Cash / AR Trend",
      subtitle: "Expected insurance, paid cash, and payer balance by service month",
      type: "line",
      labels: monthlyRows.map((row) => row.month),
      series: [
        { label: "Expected Insurance", valueType: "money", values: monthlyRows.map((row) => row.expectedInsurance) },
        { label: "Paid", valueType: "money", values: monthlyRows.map((row) => row.insurancePaid) },
        { label: "Open AR", valueType: "money", values: monthlyRows.map((row) => row.payerBalance) }
      ],
      tableRows: monthlyRows.map((row) => ({
        label: row.month,
        expected: row.expectedInsurance,
        paid: row.insurancePaid,
        openAr: row.payerBalance,
        delta: moneyNumber(row.expectedInsurance - row.insurancePaid - row.payerBalance)
      }))
    },
    {
      title: "Outstanding By State",
      subtitle: "State filter exposure with expected, paid, AR, and formula delta",
      type: "bar",
      labels: stateRows.map((row) => row.label),
      series: [
        { label: "Expected", valueType: "money", values: stateRows.map((row) => row.expected) },
        { label: "Paid", valueType: "money", values: stateRows.map((row) => row.paid) },
        { label: "Open AR", valueType: "money", values: stateRows.map((row) => row.outstanding) }
      ],
      tableRows: stateRows.map((row) => ({
        label: row.label,
        expected: row.expected,
        paid: row.paid,
        openAr: row.outstanding,
        delta: row.formulaDelta
      }))
    },
    {
      title: "Outstanding By Insurance",
      subtitle: "Insurance slicer exposure with collection rate and client-proof AR",
      type: "combo",
      labels: insuranceRows.map((row) => row.label),
      series: [
        { label: "Open AR", valueType: "money", values: insuranceRows.map((row) => row.outstanding) },
        { label: "Client Proof AR", valueType: "money", values: insuranceRows.map((row) => row.clientProofAr) },
        { label: "Collection %", valueType: "percent", values: insuranceRows.map((row) => row.collectionPct == null ? null : Math.round(row.collectionPct * 100)) }
      ],
      tableRows: insuranceRows.map((row) => ({
        label: row.label,
        expected: row.expected,
        paid: row.paid,
        openAr: row.outstanding,
        delta: row.formulaDelta
      }))
    }
  ];
  return {
    formula,
    serviceFormula,
    filters: [
      { label: "Client", value: totals.selectedClient === "All" ? "All clients" : (totals.selectedClient || "All clients"), detail: formatNounCount(claims.length, "claim") },
      { label: "State Filter", value: totals.selectedState === "All" ? "All states" : (totals.selectedState || "All states"), detail: formatNounCount(stateRows.length, "visible state bucket") },
      { label: "Insurance Filter", value: totals.selectedInsurance === "All" ? "All insurance" : (totals.selectedInsurance || "All insurance"), detail: formatNounCount(insuranceRows.length, "visible insurance bucket") },
      { label: "Service Rows", value: serviceRows.length.toLocaleString(), detail: `Service formula delta ${money(serviceFormula.delta)}` }
    ],
    kpis: [
      { label: "Expected", value: money(formula.expected), detail: "Filtered expected reimbursement", tone: "blue" },
      { label: "Paid", value: money(formula.paid), detail: collectionRate == null ? "No expected denominator" : `${Math.round(collectionRate * 100)}% net collection`, tone: collectionRate != null && collectionRate >= 0.85 ? "green" : "amber" },
      { label: "Open AR", value: money(formula.openAr), detail: formatNounCount(openClaims, "open claim"), tone: formula.openAr > 0 ? "amber" : "green" },
      { label: "Top State", value: topState.label, detail: `${money(topState.outstanding)} AR / ${formatNounCount(topState.claims, "claim")}`, tone: "slate" },
      { label: "Top Insurance", value: topInsurance.label, detail: `${money(topInsurance.outstanding)} AR / ${formatNounCount(topInsurance.claims, "claim")}`, tone: "slate" },
      { label: "Client Proof AR", value: money(clientProofAr), detail: "Client-owned blocker exposure", tone: clientProofAr > 0 ? "amber" : "green" }
    ],
    charts: chartModels
  };
}

function renderClientBoardroomChartCard(chart) {
  const labels = Array.isArray(chart.labels) ? chart.labels : [];
  const series = Array.isArray(chart.series) ? chart.series : [];
  const tableRows = Array.isArray(chart.tableRows) ? chart.tableRows : [];
  return `
    <article class="client-chart-wall-card">
      <div class="client-chart-wall-card-head">
        <div>
          <strong>${escapeHtml(chart.title)}</strong>
          <span>${escapeHtml(chart.subtitle)}</span>
        </div>
        <b>${escapeHtml(chart.type)}</b>
      </div>
      <div class="client-chart-wall-canvas">${renderChartCanvas(chart.type, labels, series)}</div>
      <div class="client-chart-wall-legend">
        ${series.map((item, index) => `
          <span><i style="background:${chartSeriesColor(index)}"></i>${escapeHtml(item.label)}</span>
        `).join("")}
      </div>
      <details class="client-chart-wall-table">
        <summary>Table</summary>
        <div>
          <table>
            <thead>
              <tr>
                <th>Bucket</th>
                <th class="num">Expected</th>
                <th class="num">Paid</th>
                <th class="num">Open AR</th>
                <th class="num">Delta</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows.length ? tableRows.map((row) => `
                <tr>
                  <td>${escapeHtml(row.label)}</td>
                  <td class="num">${money(row.expected)}</td>
                  <td class="num">${money(row.paid)}</td>
                  <td class="num">${money(row.openAr)}</td>
                  <td class="num">${money(row.delta)}</td>
                </tr>
              `).join("") : '<tr><td colspan="5">No chart rows for this filter.</td></tr>'}
            </tbody>
          </table>
        </div>
      </details>
    </article>
  `;
}

function renderClientBoardroomNarrative(claims, serviceRows, totals) {
  const model = clientBoardroomNarrativeModel(claims, serviceRows, totals);
  return `
    <section class="client-boardroom-narrative" aria-label="Client boardroom narrative">
      <div class="client-boardroom-narrative-head">
        <div>
          <span>Boardroom Storyline</span>
          <strong>What the charts mean under the active slicers</strong>
          <p>Client-safe narrative cards that translate the filtered workbook, monthly payer pivot, and Power BI-style charts into decisions.</p>
        </div>
        ${pill(model.formulaDelta === 0 ? "Math tied" : "Math review", model.formulaDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-boardroom-narrative-grid">
        ${model.cards.map(renderClientBoardroomNarrativeCard).join("")}
      </div>
      <div class="client-boardroom-driver-strip">
        ${model.drivers.map((driver) => `
          <span>
            <small>${escapeHtml(driver.label)}</small>
            <strong>${escapeHtml(driver.value)}</strong>
            <em>${escapeHtml(driver.detail)}</em>
          </span>
        `).join("")}
      </div>
    </section>
  `;
}

function clientBoardroomNarrativeModel(claims = [], serviceRows = [], totals = {}) {
  const monthlyRows = monthlyPayerSummaryRows(serviceRows);
  const monthTrend = monthlyPayerTrendRows(monthlyRows);
  const payerRows = payerPowerBiRows(monthlyRows);
  const stateRows = clientStatePowerBiRows(claims);
  const watchRows = monthlyPayerWatchlistRows(monthlyRows);
  const clientActionRows = claims.filter((claim) => clientStatus(claim) === "Client Action Needed" || clientOpenNeed(claim) !== "None");
  const formulaDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  const collectionRate = moneyRatio(totals.totalPaid, totals.totalExpected);
  const latestMonth = monthTrend.at(-1) || { month: "No month", expectedInsurance: 0, insurancePaid: 0, payerBalance: 0 };
  const previousMonth = monthTrend.length > 1 ? monthTrend.at(-2) : null;
  const latestBalanceMove = previousMonth ? moneyNumber(latestMonth.payerBalance - previousMonth.payerBalance) : null;
  const topPayer = payerRows[0] || { payer: "No payer", payerBalance: 0, expectedInsurance: 0, collectionsPct: null };
  const topState = stateRows[0] || { state: "No state", outstanding: 0, expected: 0, paid: 0 };
  const watchBalance = moneyNumber(watchRows.reduce((sum, row) => sum + row.payerBalance, 0));
  const clientActionAr = moneyNumber(clientActionRows.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0));
  const cards = [
    {
      title: "Cash Story",
      metric: collectionRate == null ? "N/A" : `${Math.round(collectionRate * 100)}%`,
      label: "net collection against expected",
      tone: collectionRate == null ? "muted" : collectionRate >= 0.9 ? "green" : collectionRate >= 0.7 ? "amber" : "red",
      proof: `${money(totals.totalPaid || 0)} paid from ${money(totals.totalExpected || 0)} expected`,
      action: collectionRate == null ? "No expected reimbursement under this filter." : "Use payer and month drilldowns to explain cash movement."
    },
    {
      title: "Monthly Movement",
      metric: money(latestMonth.payerBalance),
      label: `${latestMonth.month} payer balance`,
      tone: latestBalanceMove == null ? "blue" : latestBalanceMove > 0 ? "amber" : "green",
      proof: latestBalanceMove == null ? `${latestMonth.month} is the only visible period` : `${money(latestBalanceMove)} versus prior month`,
      action: latestBalanceMove != null && latestBalanceMove > 0 ? "Review new unpaid payer blocks before the client meeting." : "Show the client where AR is shrinking or stable."
    },
    {
      title: "Payer Story",
      metric: money(topPayer.payerBalance),
      label: topPayer.payer,
      tone: topPayer.payerBalance > 0 ? "amber" : "green",
      proof: `${topPayer.collectionsPct == null ? "N/A" : `${Math.round(topPayer.collectionsPct * 100)}%`} collections on ${money(topPayer.expectedInsurance)} expected`,
      action: topPayer.payerBalance > 0 ? "Anchor payer discussion here first." : "No payer concentration needs escalation."
    },
    {
      title: "State Story",
      metric: money(topState.outstanding),
      label: `${topState.state} open AR`,
      tone: topState.outstanding > 0 ? "blue" : "green",
      proof: `${money(topState.paid)} paid from ${money(topState.expected)} expected`,
      action: "Use state slicers to isolate authorization, payer, and filing patterns."
    },
    {
      title: "Client Ask",
      metric: money(clientActionAr),
      label: formatNounCount(clientActionRows.length, "client-owned row"),
      tone: clientActionRows.length ? "amber" : "green",
      proof: `${money(watchBalance)} payer/month watchlist AR`,
      action: clientActionRows.length ? "Start the client agenda with missing auth, COB, or documentation." : "No client-owned blockers in this filter."
    }
  ];
  return {
    cards,
    formulaDelta,
    drivers: [
      { label: "Active Claim Set", value: formatNounCount(claims.length, "claim"), detail: formatNounCount(serviceRows.length, "service row") },
      { label: "Formula Proof", value: money(formulaDelta), detail: "Expected - Paid - AR" },
      { label: "Watchlist AR", value: money(watchBalance), detail: `${watchRows.length} payer/month blocks` },
      { label: "Top Payer", value: topPayer.payer, detail: money(topPayer.payerBalance) },
      { label: "Top State", value: topState.state, detail: money(topState.outstanding) }
    ]
  };
}

function renderClientBoardroomNarrativeCard(card) {
  return `
    <article class="client-boardroom-narrative-card client-boardroom-narrative-card--${escapeHtml(card.tone || "blue")}">
      <span>${escapeHtml(card.title)}</span>
      <strong>${escapeHtml(card.metric)}</strong>
      <small>${escapeHtml(card.label)}</small>
      <p>${escapeHtml(card.proof)}</p>
      <em>${escapeHtml(card.action)}</em>
    </article>
  `;
}

function renderClientChartDecisionMatrix(claims, serviceRows, totals) {
  const model = clientChartDecisionMatrixModel(claims, serviceRows, totals);
  return `
    <section class="client-chart-decision-matrix" aria-label="Client chart decision matrix">
      <div class="client-chart-decision-head">
        <div>
          <span>Client Chart Decision Matrix</span>
          <strong>What changed, who owns it, and what unlocks cash</strong>
          <p>Client-facing decision layer for the active state and insurance filters. It turns the chart pack into a meeting-ready set of signals with proof math and one next action per signal.</p>
        </div>
        ${pill(model.formulaDelta === 0 ? "Decision math tied" : "Decision math review", model.formulaDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-chart-decision-summary">
        ${model.summary.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong>${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-chart-decision-list" role="table" aria-label="Client chart decisions">
        <div class="client-chart-decision-row client-chart-decision-header" role="row">
          <span>Chart Signal</span>
          <span>What Changed</span>
          <span>Impact</span>
          <span>Owner</span>
          <span>Proof</span>
          <span>Next Client-Safe Action</span>
        </div>
        ${model.rows.map((row) => renderClientChartDecisionRow(row, model.maxImpact)).join("")}
      </div>
    </section>
  `;
}

function clientChartDecisionMatrixModel(claims = [], serviceRows = [], totals = {}) {
  const monthlyRows = monthlyPayerSummaryRows(serviceRows);
  const monthTrend = monthlyPayerTrendRows(monthlyRows);
  const payerRows = payerPowerBiRows(monthlyRows);
  const stateRows = clientStatePowerBiRows(claims);
  const clientEvidenceRows = clientEvidenceRequestRows(claims);
  const agingRiskClaims = claims.filter((claim) => isAgingBandAtLeast(claim, "61-90") || matchesQueue(claim, "denied"));
  const formulaDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  const totalOpen = moneyNumber(totals.totalOwed || 0);
  const latestMonth = monthTrend.at(-1) || { month: "No month", expectedInsurance: 0, insurancePaid: 0, payerBalance: 0 };
  const priorMonth = monthTrend.length > 1 ? monthTrend.at(-2) : null;
  const monthMove = priorMonth ? moneyNumber(latestMonth.payerBalance - priorMonth.payerBalance) : null;
  const topPayer = payerRows[0] || { payer: "No payer", expectedInsurance: 0, insurancePaid: 0, payerBalance: 0, collectionsPct: null };
  const topState = stateRows[0] || { state: "No state", claims: 0, expected: 0, paid: 0, outstanding: 0 };
  const clientEvidenceAr = moneyNumber(clientEvidenceRows.reduce((sum, row) => sum + row.impact, 0));
  const agingRiskAr = moneyNumber(agingRiskClaims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0));
  const rows = [
    {
      signal: "State concentration",
      change: `${topState.state} holds ${clientPercentLabel(totalOpen > 0 ? topState.outstanding / totalOpen : 0)} of visible AR`,
      impact: topState.outstanding,
      owner: topState.outstanding > 0 ? "Shared" : "Monitor",
      proof: `${money(topState.expected)} expected - ${money(topState.paid)} paid - ${money(topState.outstanding)} AR = ${money(moneyNumber(topState.expected - topState.paid - topState.outstanding))}`,
      action: topState.state === "Unknown" ? "Validate state mapping before presenting the chart." : "Use state slicer to discuss payer and authorization patterns.",
      tone: topState.outstanding > 0 ? "blue" : "green"
    },
    {
      signal: "Insurance concentration",
      change: `${topPayer.payer} is the largest payer block`,
      impact: topPayer.payerBalance,
      owner: topPayer.payerBalance > 0 ? "TMS" : "Monitor",
      proof: `${topPayer.collectionsPct == null ? "N/A" : clientPercentLabel(topPayer.collectionsPct)} collected on ${money(topPayer.expectedInsurance)} expected`,
      action: topPayer.payerBalance > 0 ? "Open payer performance and explain next payer touch." : "No payer concentration needs escalation.",
      tone: topPayer.payerBalance > 0 ? "amber" : "green"
    },
    {
      signal: "Monthly movement",
      change: monthMove == null ? `${latestMonth.month} is the only visible month` : `${latestMonth.month} moved ${money(monthMove)} from prior visible month`,
      impact: latestMonth.payerBalance,
      owner: monthMove != null && monthMove > 0 ? "Shared" : "TMS",
      proof: `${money(latestMonth.expectedInsurance)} expected - ${money(latestMonth.insurancePaid)} paid - ${money(latestMonth.payerBalance)} balance = ${money(moneyNumber(latestMonth.expectedInsurance - latestMonth.insurancePaid - latestMonth.payerBalance))}`,
      action: monthMove != null && monthMove > 0 ? "Explain the payer or client-proof driver behind the increase." : "Show stable or improving monthly AR movement.",
      tone: monthMove != null && monthMove > 0 ? "amber" : "green"
    },
    {
      signal: "Aging / denial risk",
      change: `${formatNounCount(agingRiskClaims.length, "claim")} in aging or denial risk`,
      impact: agingRiskAr,
      owner: agingRiskClaims.length ? "TMS Lead" : "Monitor",
      proof: `${money(agingRiskAr)} tied to 61+ aging or denial signals`,
      action: agingRiskClaims.length ? "Review appeal, timely filing, and payer escalation path before the client meeting." : "No aging or denial pressure under this filter.",
      tone: agingRiskClaims.length ? "red" : "green"
    },
    {
      signal: "Client-owned blockers",
      change: `${clientEvidenceRows.length} evidence requests`,
      impact: clientEvidenceAr,
      owner: clientEvidenceRows.length ? "Client" : "Monitor",
      proof: `${money(clientEvidenceAr)} blocked by authorization, COB, coverage, records, or provider proof`,
      action: clientEvidenceRows.length ? "Ask client for the exact missing proof packets and due dates." : "No client-owned blockers in this filter.",
      tone: clientEvidenceRows.length ? "amber" : "green"
    },
    {
      signal: "Math confidence",
      change: formulaDelta === 0 ? "All visible chart totals reconcile" : "Visible chart totals need review",
      impact: Math.abs(formulaDelta),
      owner: formulaDelta === 0 ? "Audited" : "TMS QA",
      proof: `${money(totals.totalExpected || 0)} expected - ${money(totals.totalPaid || 0)} paid - ${money(totals.totalOwed || 0)} AR = ${money(formulaDelta)}`,
      action: formulaDelta === 0 ? "Safe to use these chart numbers in the client conversation." : "Reconcile expected, paid, and AR before distributing the dashboard.",
      tone: formulaDelta === 0 ? "green" : "red"
    }
  ];
  return {
    rows,
    formulaDelta,
    maxImpact: Math.max(1, ...rows.map((row) => Math.abs(moneyNumber(row.impact || 0)))),
    summary: [
      { label: "Signals", value: `${rows.length}`, detail: `${formatNounCount(claims.length, "claim")} / ${formatNounCount(serviceRows.length, "service row")}` },
      { label: "Visible AR", value: money(totalOpen), detail: `${money(formulaDelta)} formula delta` },
      { label: "Largest Payer", value: topPayer.payer, detail: money(topPayer.payerBalance) },
      { label: "Client Ask", value: money(clientEvidenceAr), detail: formatNounCount(clientEvidenceRows.length, "evidence row") }
    ]
  };
}

function renderClientChartDecisionRow(row, maxImpact = 1) {
  const width = row.impact > 0 ? Math.max(6, Math.min(100, Math.round((Math.abs(row.impact) / Math.max(1, maxImpact)) * 100))) : 3;
  return `
    <div class="client-chart-decision-row client-chart-decision-row--${escapeHtml(row.tone || "blue")}" role="row">
      <span data-label="Chart Signal"><strong>${escapeHtml(row.signal)}</strong></span>
      <span data-label="What Changed">${escapeHtml(row.change)}</span>
      <span data-label="Impact">
        <strong>${money(row.impact)}</strong>
        <small><i style="width:${width}%"></i></small>
      </span>
      <span data-label="Owner">${pill(row.owner, row.owner === "Client" ? "amber" : row.owner === "TMS" || row.owner === "TMS Lead" ? "blue" : row.owner === "TMS QA" ? "red" : row.owner === "Audited" ? "green" : "gray")}</span>
      <span data-label="Proof">${escapeHtml(row.proof)}</span>
      <span data-label="Next Client-Safe Action">${escapeHtml(row.action)}</span>
    </div>
  `;
}

function clientPercentLabel(value) {
  return value == null || Number.isNaN(Number(value)) ? "N/A" : `${Math.round(Number(value) * 100)}%`;
}

function renderClientMeetingBrief(claims, serviceRows, totals) {
  const model = clientMeetingBriefModel(claims, serviceRows, totals);
  return `
    <section class="client-meeting-brief" aria-label="Client meeting brief">
      <div class="client-meeting-brief-head">
        <div>
          <span>Client Meeting Brief</span>
          <strong>Executive agenda generated from the active slicers</strong>
          <p>Boardroom-ready sequence that turns the Power BI-style charts, monthly payer pivot, and client evidence queue into a clean client conversation.</p>
        </div>
        ${pill(model.mathDelta === 0 ? "Brief reconciled" : "Math review", model.mathDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-meeting-brief-summary">
        ${model.summary.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong>${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-meeting-brief-list" role="table" aria-label="Client meeting agenda">
        <div class="client-meeting-brief-row client-meeting-brief-header" role="row">
          <span>Agenda</span>
          <span>Proof</span>
          <span>Owner</span>
          <span>Next Client-Safe Action</span>
        </div>
        ${model.rows.map(renderClientMeetingBriefRow).join("")}
      </div>
    </section>
  `;
}

function clientMeetingBriefModel(claims = [], serviceRows = [], totals = {}) {
  const monthlyRows = monthlyPayerSummaryRows(serviceRows);
  const monthTrend = monthlyPayerTrendRows(monthlyRows);
  const payerRows = payerPowerBiRows(monthlyRows);
  const stateRows = clientStatePowerBiRows(claims);
  const riskRows = clientRiskTimelineRows(claims);
  const clientEvidenceRows = clientEvidenceRequestRows(claims);
  const mathDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  const collectionPct = moneyRatio(totals.totalPaid, totals.totalExpected);
  const latestMonth = monthTrend.at(-1) || { month: "No month", expectedInsurance: 0, insurancePaid: 0, payerBalance: 0 };
  const priorMonth = monthTrend.length > 1 ? monthTrend.at(-2) : null;
  const monthMove = priorMonth ? moneyNumber(latestMonth.payerBalance - priorMonth.payerBalance) : null;
  const topPayer = payerRows[0] || { payer: "No payer", expectedInsurance: 0, insurancePaid: 0, payerBalance: 0, collectionsPct: null };
  const topState = stateRows[0] || { state: "No state", claims: 0, expected: 0, paid: 0, outstanding: 0 };
  const topRisk = riskRows[0] || null;
  const clientOwnedAr = moneyNumber(clientEvidenceRows.reduce((sum, row) => sum + row.outstanding, 0));
  const rows = [
    {
      agenda: "Cash and AR position",
      detail: `${collectionPct == null ? "N/A" : `${Math.round(collectionPct * 100)}%`} collected under this filter`,
      proof: `${money(totals.totalExpected || 0)} expected / ${money(totals.totalPaid || 0)} paid / ${money(totals.totalOwed || 0)} open`,
      owner: "TMS",
      action: mathDelta === 0 ? "Open with reconciled expected, paid, and AR totals." : "Reconcile formula delta before sending this brief."
    },
    {
      agenda: "Monthly movement",
      detail: `${latestMonth.month} payer balance`,
      proof: monthMove == null ? `${money(latestMonth.payerBalance)} current balance` : `${money(monthMove)} change from prior visible month`,
      owner: monthMove != null && monthMove > 0 ? "Shared" : "TMS",
      action: monthMove != null && monthMove > 0 ? "Explain which payer or proof issue drove the increase." : "Show where AR is stable or improving."
    },
    {
      agenda: "Payer focus",
      detail: topPayer.payer,
      proof: `${money(topPayer.payerBalance)} balance / ${topPayer.collectionsPct == null ? "N/A" : `${Math.round(topPayer.collectionsPct * 100)}%`} collected`,
      owner: topPayer.payerBalance > 0 ? "TMS" : "Monitor",
      action: topPayer.payerBalance > 0 ? "Use payer drilldown to discuss the largest open payer block first." : "No payer concentration needs escalation."
    },
    {
      agenda: "State exposure",
      detail: topState.state,
      proof: `${money(topState.outstanding)} open across ${topState.claims} claim${topState.claims === 1 ? "" : "s"}`,
      owner: topState.outstanding > 0 ? "Shared" : "Monitor",
      action: topState.state === "Unknown" ? "Validate state mapping before presenting state-level trends." : "Use the state slicer for authorization and payer pattern discussion."
    },
    {
      agenda: "Client-owned requests",
      detail: `${clientEvidenceRows.length} proof item${clientEvidenceRows.length === 1 ? "" : "s"}`,
      proof: `${money(clientOwnedAr)} tied to authorization, COB, coverage, or records`,
      owner: clientEvidenceRows.length ? "Client" : "Monitor",
      action: clientEvidenceRows.length ? "Ask for the exact missing proof items before payer follow-up stalls." : "No client-owned blockers in this filter."
    },
    {
      agenda: "Risk timing",
      detail: topRisk ? topRisk.timing : "No urgent risk",
      proof: topRisk ? `${topRisk.claimId} / ${money(topRisk.outstanding)} / ${topRisk.urgency}` : "No auth, proof, or timely filing risk rows",
      owner: topRisk?.owner || "Monitor",
      action: topRisk ? topRisk.nextAction : "Keep normal payer follow-up cadence."
    }
  ];
  return {
    mathDelta,
    summary: [
      { label: "Agenda Items", value: `${rows.length}`, detail: `${formatNounCount(claims.length, "claim")} / ${formatNounCount(serviceRows.length, "service row")}` },
      { label: "Open AR", value: money(totals.totalOwed || 0), detail: `${money(mathDelta)} formula delta` },
      { label: "Client Ask", value: money(clientOwnedAr), detail: formatNounCount(clientEvidenceRows.length, "proof row") },
      { label: "Top Payer", value: topPayer.payer, detail: money(topPayer.payerBalance) }
    ],
    rows
  };
}

function renderClientMeetingBriefRow(row) {
  return `
    <div class="client-meeting-brief-row" role="row">
      <span data-label="Agenda"><strong>${escapeHtml(row.agenda)}</strong><small>${escapeHtml(row.detail)}</small></span>
      <span data-label="Proof">${escapeHtml(row.proof)}</span>
      <span data-label="Owner">${pill(row.owner, row.owner === "Client" ? "amber" : row.owner === "TMS" ? "blue" : row.owner === "Shared" ? "green" : "gray")}</span>
      <span data-label="Next Client-Safe Action">${escapeHtml(row.action)}</span>
    </div>
  `;
}

function renderClientBoardroomScorecard(claims, serviceRows, totals) {
  const model = clientBoardroomScorecardModel(claims, serviceRows, totals);
  return `
    <section class="client-boardroom-scorecard" aria-label="Client boardroom RCM scorecard">
      <div class="client-boardroom-head">
        <div>
          <span>Boardroom RCM Scorecard</span>
          <strong>Client-ready performance, blockers, and math proof</strong>
          <p>High-level visuals for the active state and insurance slicers before the client drills into payer, month, or claim-level detail.</p>
        </div>
        ${pill(model.mathDelta === 0 ? "Formula audited" : "Formula review", model.mathDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-boardroom-kpis">
        ${model.cards.map(renderClientBoardroomCard).join("")}
      </div>
      <div class="client-boardroom-bars">
        ${model.bars.map(renderClientBoardroomBar).join("")}
      </div>
    </section>
  `;
}

function renderClientCollectionWaterfall(claims = [], serviceRows = [], totals = {}) {
  const model = clientCollectionWaterfallModel(claims, serviceRows, totals);
  return `
    <section class="client-collection-waterfall" aria-label="Client collection waterfall">
      <div class="client-waterfall-head">
        <div>
          <span>Expected Reimbursement Waterfall</span>
          <strong>Where the active-slicer dollars stand right now</strong>
          <p>Client-facing bridge from expected reimbursement to paid cash, client-owned blockers, payer follow-up AR, and formula variance. This is the executive math visual that ties the charts back to the ledger.</p>
        </div>
        ${pill(model.formulaDelta === 0 ? "Waterfall tied" : "Waterfall review", model.formulaDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-waterfall-summary">
        ${model.summary.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong>${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-waterfall-visual">
        ${renderClientWaterfallSvg(model.steps)}
      </div>
      <div class="client-waterfall-list" role="table" aria-label="Expected reimbursement waterfall rows">
        <div class="client-waterfall-row client-waterfall-header" role="row">
          <span>Waterfall Step</span>
          <span>Amount</span>
          <span>Share</span>
          <span>Owner</span>
          <span>Client-ready meaning</span>
        </div>
        ${model.steps.map(renderClientWaterfallRow).join("")}
      </div>
    </section>
  `;
}

function clientCollectionWaterfallModel(claims = [], serviceRows = [], totals = {}) {
  const expected = moneyNumber(totals.totalExpected || 0);
  const paid = moneyNumber(totals.totalPaid || 0);
  const openAr = moneyNumber(totals.totalOwed || 0);
  const formulaDelta = moneyNumber(expected - paid - openAr);
  const clientRows = clientEvidenceRequestRows(claims);
  const clientOwnedAr = moneyNumber(clientRows.reduce((sum, row) => sum + row.impact, 0));
  const payerFollowUpAr = moneyNumber(Math.max(openAr - clientOwnedAr, 0));
  const paidPct = expected > 0 ? paid / expected : null;
  const openPct = expected > 0 ? openAr / expected : null;
  const steps = [
    {
      key: "expected",
      label: "Expected Reimbursement",
      amount: expected,
      share: expected > 0 ? 1 : 0,
      owner: "Contract / fee schedule",
      tone: "blue",
      meaning: "Allowed reimbursement expected under the active client, state, and insurance filters."
    },
    {
      key: "paid",
      label: "Paid Cash",
      amount: paid,
      share: expected > 0 ? paid / expected : paid > 0 ? 1 : 0,
      owner: "Posted insurance payments",
      tone: "green",
      meaning: "Insurance cash already captured and reconciled to this filtered view."
    },
    {
      key: "clientProof",
      label: "Client Proof AR",
      amount: clientOwnedAr,
      share: expected > 0 ? clientOwnedAr / expected : clientOwnedAr > 0 ? 1 : 0,
      owner: "Client",
      tone: "amber",
      meaning: "Open AR blocked by authorization, COB, coverage, documentation, or records the client can help resolve."
    },
    {
      key: "payerFollowUp",
      label: "Payer Follow-up AR",
      amount: payerFollowUpAr,
      share: expected > 0 ? payerFollowUpAr / expected : payerFollowUpAr > 0 ? 1 : 0,
      owner: "TMS",
      tone: "teal",
      meaning: "Open AR TMS should continue through payer follow-up, appeals, payment posting, or underpayment review."
    },
    {
      key: "formulaDelta",
      label: "Formula Delta",
      amount: formulaDelta,
      share: expected > 0 ? Math.abs(formulaDelta) / expected : formulaDelta !== 0 ? 1 : 0,
      owner: formulaDelta === 0 ? "Audited" : "Review",
      tone: formulaDelta === 0 ? "green" : "red",
      meaning: "Expected reimbursement minus paid cash minus open AR. Should be zero before client distribution."
    }
  ];
  return {
    expected,
    paid,
    openAr,
    clientOwnedAr,
    payerFollowUpAr,
    formulaDelta,
    steps,
    summary: [
      { label: "Expected", value: money(expected), detail: `${formatNounCount(claims.length, "claim")} / ${formatNounCount(serviceRows.length, "service row")}` },
      { label: "Paid Cash", value: money(paid), detail: paidPct == null ? "No expected denominator" : `${Math.round(paidPct * 100)}% collected` },
      { label: "Open AR", value: money(openAr), detail: openPct == null ? "No expected denominator" : `${Math.round(openPct * 100)}% still open` },
      { label: "Client-Owned AR", value: money(clientOwnedAr), detail: `${clientRows.length} proof request${clientRows.length === 1 ? "" : "s"}` },
      { label: "Formula Delta", value: money(formulaDelta), detail: "Expected - paid - open AR" }
    ]
  };
}

function renderClientWaterfallSvg(steps = []) {
  const width = 760;
  const height = 240;
  const padding = { top: 20, right: 24, bottom: 54, left: 30 };
  const maxAmount = Math.max(1, ...steps.map((step) => Math.abs(step.amount || 0)));
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const columnW = innerW / Math.max(1, steps.length);
  const bars = steps.map((step, index) => {
    const value = Math.abs(step.amount || 0);
    const barH = Math.max(value > 0 ? 4 : 2, (value / maxAmount) * innerH);
    const x = padding.left + index * columnW + columnW * 0.2;
    const y = padding.top + innerH - barH;
    const toneClass = `client-waterfall-bar--${step.tone || "blue"}`;
    const labelX = padding.left + index * columnW + columnW * 0.5;
    return `
      <rect class="client-waterfall-bar ${toneClass}" x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${(columnW * 0.6).toFixed(2)}" height="${barH.toFixed(2)}" rx="6">
        <title>${escapeHtml(`${step.label}: ${money(step.amount)}`)}</title>
      </rect>
      <text class="client-waterfall-value" x="${labelX.toFixed(2)}" y="${Math.max(14, y - 7).toFixed(2)}">${escapeHtml(chartFormatAxis(step.amount, "money"))}</text>
      <text class="client-waterfall-label" x="${labelX.toFixed(2)}" y="${height - 24}">${escapeHtml(truncateChartLabel(step.label, 14))}</text>
    `;
  }).join("");
  return `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" class="client-waterfall-svg" role="img" aria-label="Expected reimbursement waterfall">
    <line class="chart-grid-line" x1="${padding.left}" x2="${width - padding.right}" y1="${padding.top + innerH}" y2="${padding.top + innerH}"></line>
    ${bars}
  </svg>`;
}

function renderClientWaterfallRow(row) {
  const shareLabel = `${Math.round((row.share || 0) * 100)}%`;
  return `
    <div class="client-waterfall-row" role="row">
      <span data-label="Waterfall Step"><strong>${escapeHtml(row.label)}</strong><small>${escapeHtml(row.key)}</small></span>
      <span data-label="Amount"><strong>${money(row.amount)}</strong><small>${row.amount < 0 ? "Negative variance" : "Filtered dollars"}</small></span>
      <span data-label="Share"><strong>${escapeHtml(shareLabel)}</strong><small>of expected reimbursement</small></span>
      <span data-label="Owner">${pill(row.owner, row.tone === "red" ? "red" : row.tone === "amber" ? "amber" : row.tone === "green" ? "green" : "blue")}</span>
      <span data-label="Client-ready meaning">${escapeHtml(row.meaning)}</span>
    </div>
  `;
}

function renderClientCashForecast(claims = [], serviceRows = [], totals = {}) {
  const model = clientCashForecastModel(claims, serviceRows, totals);
  return `
    <section class="client-cash-forecast" aria-label="Client cash forecast">
      <div class="client-cash-forecast-head">
        <div>
          <span>Client Cash Forecast</span>
          <strong>Expected cash movement by owner, timing, and blocker</strong>
          <p>Forecast view for the active client, state, and insurance slicers. It separates likely near-term collections from client-proof holds, payer follow-up, appeal risk, and stale aging so the client sees what can move and who controls the next step.</p>
        </div>
        ${pill(model.formulaDelta === 0 ? "Forecast tied" : "Forecast review", model.formulaDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-cash-forecast-summary">
        ${model.summary.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong>${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-cash-forecast-chart">
        ${renderClientCashForecastSvg(model.lanes)}
      </div>
      <div class="client-cash-forecast-list" role="table" aria-label="Client cash forecast lanes">
        <div class="client-cash-forecast-row client-cash-forecast-header" role="row">
          <span>Forecast Lane</span>
          <span>Open AR</span>
          <span>Projected Cash</span>
          <span>Confidence</span>
          <span>Owner / Next Action</span>
        </div>
        ${model.lanes.map(renderClientCashForecastRow).join("")}
      </div>
    </section>
  `;
}

function clientCashForecastModel(claims = [], serviceRows = [], totals = {}) {
  const lanes = clientCashForecastLaneDefinitions().map((lane) => ({ ...lane, claims: 0, expected: 0, paid: 0, openAr: 0, projectedCash: 0, formulaDelta: 0, examples: [] }));
  const laneByKey = new Map(lanes.map((lane) => [lane.key, lane]));
  claims.forEach((claim) => {
    const financial = claimFinancialView(claim);
    if (financial.sourceOutstanding <= 0) return;
    const laneKey = clientCashForecastLaneForClaim(claim);
    const lane = laneByKey.get(laneKey) || laneByKey.get("payerFollowUp");
    lane.claims += 1;
    lane.expected += financial.expectedReimbursement;
    lane.paid += financial.totalInsurancePaid;
    lane.openAr += financial.sourceOutstanding;
    lane.projectedCash += financial.sourceOutstanding * lane.confidence;
    lane.formulaDelta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    if (lane.examples.length < 2) lane.examples.push(claim.claimId);
  });
  lanes.forEach((lane) => {
    lane.expected = moneyNumber(lane.expected);
    lane.paid = moneyNumber(lane.paid);
    lane.openAr = moneyNumber(lane.openAr);
    lane.projectedCash = moneyNumber(lane.projectedCash);
    lane.formulaDelta = moneyNumber(lane.formulaDelta);
    const totalOwed = moneyNumber(totals.totalOwed || 0);
    lane.share = totalOwed > 0 ? lane.openAr / totalOwed : lane.openAr > 0 ? 1 : 0;
  });
  const openAr = moneyNumber(lanes.reduce((sum, lane) => sum + lane.openAr, 0));
  const projectedCash = moneyNumber(lanes.reduce((sum, lane) => sum + lane.projectedCash, 0));
  const blockedAr = moneyNumber(lanes.filter((lane) => lane.key === "clientProof" || lane.key === "appealRisk" || lane.key === "staleAging").reduce((sum, lane) => sum + lane.openAr, 0));
  const formulaDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  return {
    lanes,
    openAr,
    projectedCash,
    blockedAr,
    formulaDelta,
    summary: [
      { label: "Open AR", value: money(openAr), detail: `${formatNounCount(claims.length, "claim")} / ${formatNounCount(serviceRows.length, "service line")}` },
      { label: "Projected Cash", value: money(projectedCash), detail: "Weighted by lane confidence" },
      { label: "Blocked / Risk AR", value: money(blockedAr), detail: "Client proof, appeal, or stale aging" },
      { label: "Near-Term Cash", value: money((laneByKey.get("readyNow")?.projectedCash || 0) + (laneByKey.get("next30")?.projectedCash || 0)), detail: "0-30 day forecast lanes" },
      { label: "Formula Delta", value: money(formulaDelta), detail: "Expected - paid - open AR" }
    ]
  };
}

function clientCashForecastLaneDefinitions() {
  return [
    { key: "readyNow", label: "0-14 Day Cash", owner: "TMS", confidence: 0.85, tone: "green", nextAction: "Complete near-term payer touch and post payment evidence." },
    { key: "next30", label: "15-30 Day Cash", owner: "TMS", confidence: 0.65, tone: "blue", nextAction: "Keep scheduled payer follow-up and monitor response window." },
    { key: "payerFollowUp", label: "Payer Follow-up", owner: "TMS", confidence: 0.45, tone: "teal", nextAction: "Work payer portal, ERA, underpayment, or secondary follow-up." },
    { key: "clientProof", label: "Client Proof Hold", owner: "Client", confidence: 0.15, tone: "amber", nextAction: "Send authorization, COB, coverage, record, or provider proof." },
    { key: "appealRisk", label: "Appeal / Denial Risk", owner: "Shared", confidence: 0.25, tone: "red", nextAction: "Prepare appeal, corrected claim, or denial evidence packet." },
    { key: "staleAging", label: "Stale Aging Risk", owner: "TMS Lead", confidence: 0.1, tone: "red", nextAction: "Escalate aging, deadline, write-off review, or payer policy path." }
  ];
}

function clientCashForecastLaneForClaim(claim = {}) {
  const text = claimOperationalText(claim);
  if (clientStatus(claim) === "Client Action Needed" || clientOpenNeed(claim) !== "None") return "clientProof";
  if (isDeniedClaim(claim) || /appeal|denial|corrected claim|co-?197|co-?16|denied/.test(text)) return "appealRisk";
  if (isAgingBandAtLeast(claim, "91-120")) return "staleAging";
  const due = localDateFromDateOnly(claim.followUpDate);
  if (due) {
    const days = Math.ceil((due - todayStart()) / 86400000);
    if (days <= 14) return "readyNow";
    if (days <= 30) return "next30";
  }
  if (isPastDue(claim)) return "readyNow";
  return "payerFollowUp";
}

function renderClientCashForecastSvg(lanes = []) {
  const width = 760;
  const height = 250;
  const padding = { top: 18, right: 24, bottom: 60, left: 64 };
  const maxValue = Math.max(1, ...lanes.map((lane) => lane.openAr || 0));
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const groupW = innerW / Math.max(1, lanes.length);
  const bars = lanes.map((lane, index) => {
    const openHeight = Math.max(lane.openAr > 0 ? 3 : 1, (lane.openAr / maxValue) * innerH);
    const projectedHeight = Math.max(lane.projectedCash > 0 ? 3 : 1, (lane.projectedCash / maxValue) * innerH);
    const x = padding.left + groupW * index + groupW * 0.18;
    const projectedX = x + groupW * 0.32;
    const openY = padding.top + innerH - openHeight;
    const projectedY = padding.top + innerH - projectedHeight;
    const labelX = padding.left + groupW * (index + 0.5);
    return `
      <rect class="client-cash-forecast-bar client-cash-forecast-bar--${escapeHtml(lane.tone)}" x="${x.toFixed(2)}" y="${openY.toFixed(2)}" width="${(groupW * 0.28).toFixed(2)}" height="${openHeight.toFixed(2)}" rx="5">
        <title>${escapeHtml(`${lane.label} open AR: ${money(lane.openAr)}`)}</title>
      </rect>
      <rect class="client-cash-forecast-bar client-cash-forecast-bar--projected" x="${projectedX.toFixed(2)}" y="${projectedY.toFixed(2)}" width="${(groupW * 0.28).toFixed(2)}" height="${projectedHeight.toFixed(2)}" rx="5">
        <title>${escapeHtml(`${lane.label} projected cash: ${money(lane.projectedCash)}`)}</title>
      </rect>
      <text class="client-cash-forecast-value" x="${labelX.toFixed(2)}" y="${Math.max(14, Math.min(openY, projectedY) - 7).toFixed(2)}">${escapeHtml(chartFormatAxis(lane.openAr, "money"))}</text>
      <text class="client-cash-forecast-label" x="${labelX.toFixed(2)}" y="${height - 34}">${escapeHtml(truncateChartLabel(lane.label, 13))}</text>
    `;
  }).join("");
  return `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" class="client-cash-forecast-svg" role="img" aria-label="Client cash forecast chart">
    <line class="chart-grid-line" x1="${padding.left}" x2="${width - padding.right}" y1="${padding.top + innerH}" y2="${padding.top + innerH}"></line>
    ${bars}
    <text class="client-cash-forecast-legend" x="${padding.left}" y="${height - 10}">Solid: open AR · Light: projected cash</text>
  </svg>`;
}

function renderClientCashForecastRow(row) {
  const confidenceLabel = `${Math.round((row.confidence || 0) * 100)}%`;
  return `
    <div class="client-cash-forecast-row" role="row">
      <span data-label="Forecast Lane"><strong>${escapeHtml(row.label)}</strong><small>${formatNounCount(row.claims, "claim")}${row.examples.length ? ` · ${row.examples.map(escapeHtml).join(", ")}` : ""}</small></span>
      <span data-label="Open AR"><strong>${money(row.openAr)}</strong><small>${Math.round((row.share || 0) * 100)}% of open AR</small></span>
      <span data-label="Projected Cash"><strong>${money(row.projectedCash)}</strong><small>Weighted forecast</small></span>
      <span data-label="Confidence"><strong>${escapeHtml(confidenceLabel)}</strong><small>Delta ${money(row.formulaDelta)}</small></span>
      <span data-label="Owner / Next Action">${pill(row.owner, row.tone === "red" ? "red" : row.tone === "amber" ? "amber" : row.tone === "green" ? "green" : "blue")}<small>${escapeHtml(row.nextAction)}</small></span>
    </div>
  `;
}

function renderClientAgingDenialHeatmap(claims = [], serviceRows = [], totals = {}) {
  const model = clientAgingDenialHeatmapModel(claims, serviceRows, totals);
  return `
    <section class="client-aging-denial-heatmap" aria-label="Client aging and denial heatmap">
      <div class="client-aging-denial-head">
        <div>
          <span>Aging + Denial Heatmap</span>
          <strong>Where aging AR and denial exposure are concentrated</strong>
          <p>Client-facing payer/state matrix for the active slicers. Each heat block is backed by expected reimbursement, paid cash, open AR, denial count, and formula delta.</p>
        </div>
        ${pill(model.formulaDelta === 0 ? "Heatmap tied" : "Math review", model.formulaDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-aging-denial-summary">
        ${model.summary.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong>${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-aging-denial-visual">
        ${renderClientAgingDenialHeatmapSvg(model.rows, model.buckets)}
      </div>
      <div class="client-aging-denial-grid" role="table" aria-label="Aging and denial heatmap by payer and state">
        <div class="client-aging-denial-row client-aging-denial-header" role="row">
          <span>Payer / State</span>
          ${model.buckets.map((bucket) => `<span>${escapeHtml(bucket)}</span>`).join("")}
          <span>Formula / Action</span>
        </div>
        ${model.rows.map((row) => renderClientAgingDenialRow(row, model.maxCellAr)).join("") || '<div class="client-aging-denial-empty">No open aging or denial exposure in the active slicers.</div>'}
      </div>
    </section>
  `;
}

function clientAgingDenialHeatmapModel(claims = [], serviceRows = [], totals = {}) {
  const buckets = CT_AR_AGING_BUCKET_KEYS;
  const groups = new Map();
  claims.forEach((claim) => {
    const financial = claimFinancialView(claim);
    const band = claimAgingBand(claim);
    // Unknown / blank band falls to the YOUNGEST bucket, not "(blank)" — see the divergence note
    // on arAgingBucketFor (guardian finding N4). Kept as-is: it is the client-safe direction.
    const bucket = buckets.includes(band) ? band : buckets[0];
    const stateValue = clientStateValue(claim) || "State Not Tagged";
    const payer = claim.primaryPayer || claim.secondaryPayer || "Unknown payer";
    const key = `${payer}::${stateValue}`;
    const group = groups.get(key) || {
      key,
      payer,
      state: stateValue,
      claims: 0,
      expected: 0,
      paid: 0,
      openAr: 0,
      denialClaims: 0,
      denialAr: 0,
      formulaDelta: 0,
      cells: Object.fromEntries(buckets.map((agingBucket) => [agingBucket, clientAgingDenialEmptyCell(agingBucket)]))
    };
    const cell = group.cells[bucket] || clientAgingDenialEmptyCell(bucket);
    group.claims += 1;
    group.expected += financial.expectedReimbursement;
    group.paid += financial.totalInsurancePaid;
    group.openAr += financial.sourceOutstanding;
    group.formulaDelta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    cell.claims += 1;
    cell.expected += financial.expectedReimbursement;
    cell.paid += financial.totalInsurancePaid;
    cell.openAr += financial.sourceOutstanding;
    cell.formulaDelta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
    if (isDeniedClaim(claim)) {
      group.denialClaims += 1;
      group.denialAr += financial.sourceOutstanding;
      cell.denialClaims += 1;
      cell.denialAr += financial.sourceOutstanding;
    }
    if (cell.examples.length < 2) cell.examples.push(claim.claimId);
    groups.set(key, group);
  });
  const rows = Array.from(groups.values()).map((row) => {
    buckets.forEach((bucket) => {
      const cell = row.cells[bucket];
      cell.expected = moneyNumber(cell.expected);
      cell.paid = moneyNumber(cell.paid);
      cell.openAr = moneyNumber(cell.openAr);
      cell.denialAr = moneyNumber(cell.denialAr);
      cell.formulaDelta = moneyNumber(cell.formulaDelta);
      cell.hotScore = moneyNumber(cell.openAr + cell.denialAr * 0.6 + (agingBucketOrdinal(bucket) >= agingBucketOrdinal("91-120") ? cell.openAr * 0.35 : 0));
    });
    row.expected = moneyNumber(row.expected);
    row.paid = moneyNumber(row.paid);
    row.openAr = moneyNumber(row.openAr);
    row.denialAr = moneyNumber(row.denialAr);
    row.formulaDelta = moneyNumber(row.formulaDelta);
    row.aging61PlusAr = moneyNumber(CT_AR_AGING_BUCKET_KEYS.filter((bucket) => agingBucketOrdinal(bucket) >= agingBucketOrdinal("61-90")).reduce((sum, bucket) => sum + row.cells[bucket].openAr, 0));
    row.hotScore = moneyNumber(row.openAr + row.denialAr * 0.6 + row.aging61PlusAr * 0.35);
    row.owner = row.denialClaims ? "TMS Appeals" : row.aging61PlusAr > 0 ? "TMS Lead" : "TMS";
    row.nextAction = clientAgingDenialNextAction(row);
    return row;
  }).sort((a, b) => b.hotScore - a.hotScore || b.openAr - a.openAr || a.payer.localeCompare(b.payer)).slice(0, 8);
  const maxCellAr = Math.max(1, ...rows.flatMap((row) => buckets.map((bucket) => row.cells[bucket].openAr)));
  const denialAr = moneyNumber(rows.reduce((sum, row) => sum + row.denialAr, 0));
  const agedAr = moneyNumber(rows.reduce((sum, row) => sum + row.aging61PlusAr, 0));
  const formulaDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  const hottest = rows[0] || null;
  return {
    buckets,
    rows,
    maxCellAr,
    formulaDelta,
    summary: [
      { label: "Heatmap Rows", value: `${rows.length}`, detail: `${formatNounCount(claims.length, "claim")} / ${formatNounCount(serviceRows.length, "service row")}` },
      { label: "Open AR", value: money(totals.totalOwed || 0), detail: "Active slicer balance" },
      { label: "Denial Risk AR", value: money(denialAr), detail: formatNounCount(rows.reduce((sum, row) => sum + row.denialClaims, 0), "denied claim block") },
      { label: "61+ Aging AR", value: money(agedAr), detail: "Escalation aging bands" },
      { label: "Top Heat Block", value: hottest ? `${hottest.state} / ${truncateChartLabel(hottest.payer, 18)}` : "None", detail: hottest ? money(hottest.openAr) : "No exposure" },
      { label: "Formula Delta", value: money(formulaDelta), detail: "Expected - paid - open AR" }
    ]
  };
}

function clientAgingDenialEmptyCell(bucket) {
  return { bucket, claims: 0, expected: 0, paid: 0, openAr: 0, denialClaims: 0, denialAr: 0, formulaDelta: 0, hotScore: 0, examples: [] };
}

function clientAgingDenialNextAction(row) {
  if (row.denialClaims > 0 && row.aging61PlusAr > 0) return "Lead the client discussion with appeal status, payer proof, and aging escalation for this block.";
  if (row.denialClaims > 0) return "Show denial/appeal workstream and evidence needed to reverse this payer block.";
  if (row.aging61PlusAr > 0) return "Escalate aging follow-up and document payer response path before it becomes stale AR.";
  if (row.openAr > 0) return "Keep payer follow-up cadence visible and compare against next cash forecast.";
  return "No action needed beyond monitoring.";
}

function renderClientAgingDenialHeatmapSvg(rows = [], buckets = []) {
  const width = 760;
  const rowHeight = 36;
  const height = Math.max(170, 84 + rows.length * rowHeight);
  const padding = { top: 34, right: 22, bottom: 32, left: 180 };
  const cellW = (width - padding.left - padding.right) / Math.max(1, buckets.length);
  const maxScore = Math.max(1, ...rows.flatMap((row) => buckets.map((bucket) => row.cells[bucket]?.hotScore || 0)));
  const bucketLabels = buckets.map((bucket, index) => `
    <text class="client-aging-denial-svg-axis" x="${(padding.left + index * cellW + cellW / 2).toFixed(2)}" y="22">${escapeHtml(bucket)}</text>
  `).join("");
  const rowLabels = rows.map((row, rowIndex) => {
    const y = padding.top + rowIndex * rowHeight + rowHeight / 2 + 4;
    return `<text class="client-aging-denial-svg-row-label" x="12" y="${y.toFixed(2)}">${escapeHtml(truncateChartLabel(`${row.state} / ${row.payer}`, 26))}</text>`;
  }).join("");
  const cells = rows.flatMap((row, rowIndex) => buckets.map((bucket, bucketIndex) => {
    const cell = row.cells[bucket] || clientAgingDenialEmptyCell(bucket);
    const intensity = Math.max(0.08, Math.min(1, cell.hotScore / maxScore));
    const x = padding.left + bucketIndex * cellW + 4;
    const y = padding.top + rowIndex * rowHeight + 4;
    const tone = cell.denialClaims ? "red" : agingBucketOrdinal(bucket) >= agingBucketOrdinal("91-120") ? "amber" : "blue";
    return `
      <rect class="client-aging-denial-svg-cell client-aging-denial-svg-cell--${tone}" x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${Math.max(4, cellW - 8).toFixed(2)}" height="${rowHeight - 8}" rx="5" opacity="${intensity.toFixed(2)}">
        <title>${escapeHtml(`${row.state} / ${row.payer} / ${bucket}: ${money(cell.openAr)} AR, ${formatNounCount(cell.denialClaims, "denial")}, delta ${money(cell.formulaDelta)}`)}</title>
      </rect>
      <text class="client-aging-denial-svg-value" x="${(x + (cellW - 8) / 2).toFixed(2)}" y="${(y + 21).toFixed(2)}">${escapeHtml(cell.openAr ? chartFormatAxis(cell.openAr, "money") : "")}</text>
    `;
  })).join("");
  return `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" class="client-aging-denial-svg" role="img" aria-label="Aging and denial heatmap">
    ${bucketLabels}
    ${rowLabels}
    ${cells}
    <text class="client-aging-denial-svg-legend" x="${padding.left}" y="${height - 10}">Darker cells = more open AR, denial risk, or stale-aging exposure</text>
  </svg>`;
}

function renderClientAgingDenialRow(row, maxCellAr = 1) {
  const buckets = CT_AR_AGING_BUCKET_KEYS;
  return `
    <div class="client-aging-denial-row" role="row">
      <span data-label="Payer / State"><strong>${escapeHtml(row.payer)}</strong><small>${escapeHtml(row.state)} · ${formatNounCount(row.claims, "claim")}</small></span>
      ${buckets.map((bucket) => renderClientAgingDenialCell(row.cells[bucket], maxCellAr)).join("")}
      <span data-label="Formula / Action">
        <strong>${money(row.formulaDelta)}</strong>
        <small>${pill(row.owner, row.owner === "TMS Appeals" ? "red" : row.owner === "TMS Lead" ? "amber" : "blue")}${escapeHtml(row.nextAction)}</small>
      </span>
    </div>
  `;
}

function renderClientAgingDenialCell(cell = clientAgingDenialEmptyCell(""), maxCellAr = 1) {
  const intensity = Math.max(0, Math.min(1, (cell.openAr || 0) / Math.max(maxCellAr, 1)));
  const tone = cell.denialClaims ? "red" : agingBucketOrdinal(cell.bucket) >= agingBucketOrdinal("91-120") ? "amber" : "blue";
  return `
    <span class="client-aging-denial-cell client-aging-denial-cell--${tone}" data-label="${escapeHtml(cell.bucket)}" style="--heat:${intensity.toFixed(2)}">
      <strong>${money(cell.openAr)}</strong>
      <small>${formatNounCount(cell.claims, "claim")} · ${formatNounCount(cell.denialClaims, "denial")}</small>
      <em>Δ ${money(cell.formulaDelta)}</em>
    </span>
  `;
}

function clientBoardroomScorecardModel(claims = [], serviceRows = [], totals = {}) {
  const openClaims = claims.filter((claim) => claimFinancialView(claim).sourceOutstanding > 0);
  const expected = moneyNumber(totals.totalExpected || 0);
  const paid = moneyNumber(totals.totalPaid || 0);
  const outstanding = moneyNumber(totals.totalOwed || 0);
  const mathDelta = moneyNumber(expected - paid - outstanding);
  const collectionPct = expected > 0 ? paid / expected : null;
  const arPct = expected > 0 ? outstanding / expected : null;
  const clientOwnedRows = clientFilteredClaimLedgerRows(claims).filter((row) => row.status === "Client Action Needed" || /authorization|document|coverage|client/i.test(row.openNeed));
  const clientOwnedAr = moneyNumber(clientOwnedRows.reduce((sum, row) => sum + row.outstanding, 0));
  const payerRows = groupedClientChartRows(claims, (claim) => claim.primaryPayer || claim.secondaryPayer || "Unknown payer");
  const topPayer = payerRows[0] || { label: "No payer", value: 0, claims: 0 };
  const payerConcentration = outstanding > 0 ? topPayer.value / outstanding : 0;
  const agingBuckets = CT_AR_AGING_BUCKET_KEYS.map((bucket) => {
    const bucketClaims = claims.filter((claim) => claimAgingBand(claim) === bucket);
    return {
      label: bucket,
      value: moneyNumber(bucketClaims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0)),
      claims: bucketClaims.length
    };
  });
  const agingRisk = agingBuckets.filter((row) => agingBucketOrdinal(row.label) >= agingBucketOrdinal("61-90")).reduce((sum, row) => sum + row.value, 0);
  const stateRows = groupedClientChartRows(claims, (claim) => clientStateValue(claim) || "Unknown");
  const topState = stateRows[0] || { label: "No state", value: 0, claims: 0 };
  const cards = [
    {
      label: "Net Collection",
      value: collectionPct == null ? "N/A" : `${Math.round(collectionPct * 100)}%`,
      detail: `${money(paid)} paid of ${money(expected)} expected`,
      tone: collectionPct == null || collectionPct >= 0.8 ? "green" : collectionPct >= 0.55 ? "amber" : "red"
    },
    {
      label: "Open AR",
      value: money(outstanding),
      detail: `${openClaims.length} open claim${openClaims.length === 1 ? "" : "s"} · ${arPct == null ? "N/A" : `${Math.round(arPct * 100)}%`} of expected`,
      tone: outstanding > 0 ? "amber" : "green"
    },
    {
      label: "Client Blockers",
      value: money(clientOwnedAr),
      detail: `${clientOwnedRows.length} authorization, COB, coverage, or document item${clientOwnedRows.length === 1 ? "" : "s"}`,
      tone: clientOwnedRows.length ? "amber" : "green"
    },
    {
      label: "Top Payer",
      value: topPayer.label,
      detail: `${money(topPayer.value)} · ${Math.round(payerConcentration * 100)}% of filtered AR`,
      tone: payerConcentration >= 0.5 ? "red" : payerConcentration >= 0.25 ? "amber" : "blue"
    },
    {
      label: "Math Proof",
      value: money(mathDelta),
      detail: `${formatNounCount(serviceRows.length, "service row")} · Expected - Paid - AR`,
      tone: mathDelta === 0 ? "green" : "amber"
    }
  ];
  const bars = [
    {
      label: "Expected vs Paid vs Open",
      detail: `${totals.selectedState || "All states"} · ${totals.selectedInsurance || "All insurance"}`,
      segments: [
        { label: "Paid", value: paid, total: expected || paid || outstanding || 1, tone: "green" },
        { label: "Open AR", value: outstanding, total: expected || paid || outstanding || 1, tone: "amber" }
      ]
    },
    {
      label: "Aging Risk",
      detail: `${money(agingRisk)} in 61+ / 120+ aging`,
      segments: agingBuckets.map((row, index) => ({
        label: row.label,
        value: row.value,
        total: outstanding || 1,
        tone: index < 2 ? "blue" : index === 2 ? "amber" : "red"
      }))
    },
    {
      label: "Payer / State Concentration",
      detail: `${topPayer.label} · ${topState.label}`,
      segments: [
        { label: "Top payer", value: topPayer.value, total: outstanding || topPayer.value || 1, tone: payerConcentration >= 0.5 ? "red" : "blue" },
        { label: "Top state", value: topState.value, total: outstanding || topState.value || 1, tone: "teal" }
      ]
    }
  ];
  // agingBuckets / agingRisk are exposed (not just baked into the "Aging Risk" card copy) so the
  // client-visible aggregate is directly assertable — see the aging-aggregate block in
  // backend/verify_frontend_operations_workbench_contract.js. Render path is unchanged.
  return { cards, bars, mathDelta, collectionPct, clientOwnedAr, payerConcentration, agingBuckets, agingRisk };
}

function renderClientBoardroomCard(card) {
  return `
    <article class="client-boardroom-card client-boardroom-card--${escapeHtml(card.tone)}">
      <span>${escapeHtml(card.label)}</span>
      <strong>${escapeHtml(card.value)}</strong>
      <small>${escapeHtml(card.detail)}</small>
    </article>
  `;
}

function renderClientBoardroomBar(bar) {
  return `
    <div class="client-boardroom-bar">
      <div class="client-boardroom-bar-head">
        <strong>${escapeHtml(bar.label)}</strong>
        <span>${escapeHtml(bar.detail)}</span>
      </div>
      <div class="client-boardroom-segments">
        ${bar.segments.map((segment) => {
          const width = Math.max(segment.value > 0 ? 3 : 0, Math.min(100, Math.round((segment.value / Math.max(segment.total, 1)) * 100)));
          return `
            <div class="client-boardroom-segment">
              <span><i class="client-boardroom-dot client-boardroom-dot--${escapeHtml(segment.tone)}"></i>${escapeHtml(segment.label)}</span>
              <div class="client-boardroom-track"><b class="client-boardroom-fill client-boardroom-fill--${escapeHtml(segment.tone)}" style="width:${width}%"></b></div>
              <strong>${money(segment.value)}</strong>
            </div>
          `;
        }).join("")}
      </div>
    </div>
  `;
}

function renderClientExecutiveInsights(claims, serviceRows, totals) {
  const insight = clientExecutiveInsightModel(claims, serviceRows, totals);
  return `
    <section class="client-executive-insights" aria-label="Client executive RCM insights">
      <div class="client-executive-insights-head">
        <div>
          <span>Executive RCM Insights</span>
          <strong>Filtered state and insurance story for client review</strong>
          <p>Every insight is tied to the active slicers and the same expected, paid, and payer balance math used in the charts.</p>
        </div>
        ${pill(insight.mathDelta === 0 ? "Math ties" : "Formula review", insight.mathDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-executive-insight-grid">
        ${insight.cards.map(renderClientExecutiveInsightCard).join("")}
      </div>
    </section>
  `;
}

function clientExecutiveInsightModel(claims = [], serviceRows = [], totals = {}) {
  const claimRows = clientFilteredClaimLedgerRows(claims);
  const openRows = claimRows.filter((row) => row.outstanding > 0);
  const payerRows = groupedClientChartRows(claims, (claim) => claim.primaryPayer || "Unknown");
  const stateRows = groupedClientChartRows(claims, (claim) => clientStateValue(claim) || "Unknown");
  const clientActionRows = claimRows.filter((row) => row.status === "Client Action Needed" || /authorization|document|coverage|client/i.test(row.openNeed));
  const guardedClaims = claims.filter((claim) => evaluateClaimCalculationGuards(claim).length > 0);
  const topPayer = payerRows[0] || { label: "No payer", value: 0, claims: 0 };
  const topState = stateRows[0] || { label: "No state", value: 0, claims: 0 };
  const topStateLabel = topState.label === "Unknown" ? "State Not Tagged" : topState.label;
  const collectionRate = moneyRatio(totals.totalPaid, totals.totalExpected);
  const mathDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  const clientActionAmount = moneyNumber(clientActionRows.reduce((sum, row) => sum + row.outstanding, 0));
  const topOpenRow = openRows[0] || null;
  const cards = [
    {
      tone: moneyNumber(totals.totalOwed || 0) > 0 ? "amber" : "green",
      label: "Collection Gap",
      value: money(totals.totalOwed || 0),
      detail: `${collectionRate == null ? "N/A" : `${Math.round(collectionRate * 100)}%`} collected across ${formatNounCount(claims.length, "filtered claim")}`,
      action: topOpenRow ? `Start with ${topOpenRow.claimId}: ${topOpenRow.openNeed}` : "No open AR in the active slicers."
    },
    {
      tone: topPayer.value > 0 ? "red" : "green",
      label: "Payer Risk",
      value: topPayer.label,
      detail: `${money(topPayer.value)} open across ${formatNounCount(topPayer.claims, "claim")}`,
      action: topPayer.value > 0 ? "Use the insurance slicer to isolate this payer before the client meeting." : "No payer balance to escalate."
    },
    {
      tone: topState.value > 0 ? "blue" : "green",
      label: "State Exposure",
      value: topStateLabel,
      detail: `${money(topState.value)} open across ${formatNounCount(topState.claims, "claim")}`,
      action: topState.label === "Unknown"
        ? "Validate state mapping before presenting state-level trends."
        : topState.value > 0 ? "Use the state slicer to compare authorization and payer patterns." : "No state exposure under this filter."
    },
    {
      tone: clientActionRows.length ? "amber" : "green",
      label: "Client-Owned Blockers",
      value: `${clientActionRows.length}`,
      detail: `${money(clientActionAmount)} waiting on authorization, coverage, COB, or documents`,
      action: clientActionRows.length ? "Client meeting agenda should include these blockers first." : "No client-owned blockers in the active slicers."
    },
    {
      tone: mathDelta === 0 && guardedClaims.length === 0 ? "green" : "amber",
      label: "Math Confidence",
      value: money(mathDelta),
      detail: `${formatNounCount(guardedClaims.length, "guarded claim")} · ${formatNounCount(serviceRows.length, "service row")}`,
      action: mathDelta === 0 ? "Expected - paid - AR ties for this filtered view." : "Reconcile formula delta before presenting totals."
    }
  ];
  return { cards, mathDelta, guardedClaims };
}

function renderClientExecutiveInsightCard(card) {
  return `
    <article class="client-executive-insight-card client-executive-insight-card--${escapeHtml(card.tone)}">
      <span>${escapeHtml(card.label)}</span>
      <strong>${escapeHtml(card.value)}</strong>
      <small>${escapeHtml(card.detail)}</small>
      <p>${escapeHtml(card.action)}</p>
    </article>
  `;
}

function renderClientRiskTimeline(claims = [], totals = {}) {
  const model = clientRiskTimelineModel(claims, totals);
  return `
    <section class="client-risk-timeline" aria-label="Client risk timeline">
      <div class="client-risk-timeline-head">
        <div>
          <span>Client Risk Timeline</span>
          <strong>Authorization, proof, and timely filing exposure by active slicers</strong>
          <p>Client-safe deadline view that turns state and insurance filters into near-term action rows without exposing internal assignment or offshore workflow.</p>
        </div>
        ${pill(model.urgentRows ? `${model.urgentRows} urgent` : "Monitor", model.urgentRows ? "red" : "green")}
      </div>
      <div class="client-risk-timeline-summary">
        <span><small>Risk Rows</small><strong>${model.rows.length}</strong><em>${escapeHtml(totals.selectedState || "All states")}</em></span>
        <span><small>AR At Risk</small><strong>${money(model.arAtRisk)}</strong><em>Filtered client view</em></span>
        <span><small>Client Proof</small><strong>${model.clientProofRows}</strong><em>Auth, COB, records, provider proof</em></span>
        <span><small>Filing Alerts</small><strong>${model.filingAlerts}</strong><em>Expired or 30-day window</em></span>
        <span><small>Formula Delta</small><strong>${money(model.delta)}</strong><em>Expected - Paid - AR</em></span>
      </div>
      <div class="client-risk-timeline-list">
        <div class="client-risk-timeline-row client-risk-timeline-header">
          <span>Claim / Risk</span>
          <span>Payer / State</span>
          <span>Timing</span>
          <span>AR / Delta</span>
          <span>Owner</span>
          <span>Client-Safe Next Step</span>
        </div>
        ${model.rows.slice(0, 10).map(renderClientRiskTimelineRow).join("") || '<div class="client-risk-timeline-empty">No authorization, proof, or timely filing risk rows in the active slicers.</div>'}
      </div>
    </section>
  `;
}

function clientRiskTimelineModel(claims = [], totals = {}) {
  const rows = clientRiskTimelineRows(claims);
  const aggregate = rows.reduce((sum, row) => {
    sum.arAtRisk += row.outstanding;
    sum.delta += row.delta;
    if (row.owner === "Client") sum.clientProofRows += 1;
    if (row.riskKind === "Timely Filing" && ["Expired", "0-14 Days", "15-30 Days", "No DOS"].includes(row.status)) sum.filingAlerts += 1;
    if (row.urgency <= 1) sum.urgentRows += 1;
    return sum;
  }, { arAtRisk: 0, delta: 0, clientProofRows: 0, filingAlerts: 0, urgentRows: 0 });
  return {
    rows,
    selectedState: totals.selectedState || "All",
    selectedInsurance: totals.selectedInsurance || "All",
    arAtRisk: moneyNumber(aggregate.arAtRisk),
    delta: moneyNumber(aggregate.delta),
    clientProofRows: aggregate.clientProofRows,
    filingAlerts: aggregate.filingAlerts,
    urgentRows: aggregate.urgentRows
  };
}

function clientRiskTimelineRows(claims = []) {
  const byClaim = new Map((Array.isArray(claims) ? claims : []).map((claim) => [claim.claimId, claim]));
  const authRows = authorizationUtilizationModel(claims).rows.map((row) => clientRiskTimelineRowFromAuth(row, byClaim.get(row.claimId)));
  const filingRows = timelyFilingRiskModel(claims).rows
    .filter((row) => ["Expired", "0-14 Days", "15-30 Days", "No DOS"].includes(row.status) || row.blocker === "Client Evidence")
    .map((row) => clientRiskTimelineRowFromFiling(row, byClaim.get(row.claimId)));
  const deduped = new Map();
  [...authRows, ...filingRows].filter(Boolean).forEach((row) => {
    const key = `${row.claimId}::${row.riskKind}`;
    const existing = deduped.get(key);
    if (!existing || row.urgency < existing.urgency || row.outstanding > existing.outstanding) deduped.set(key, row);
  });
  return Array.from(deduped.values())
    .sort((a, b) => a.urgency - b.urgency
      || b.outstanding - a.outstanding
      || a.claimId.localeCompare(b.claimId));
}

function clientRiskTimelineRowFromAuth(row = {}, claim = {}) {
  const owner = row.status === "Client Proof Needed" || hasClientAuthorizationSignal(claim) ? "Client" : "TMS";
  return {
    claimId: row.claimId || "",
    riskKind: row.status === "Auth Denial" ? "Authorization Denial" : "Authorization / Proof",
    payer: row.payer || "Unknown payer",
    state: row.state || "State Not Tagged",
    timing: clientEvidenceDueLabel(claim?.followUpDate || ""),
    dueSort: clientRiskTimingRank(clientEvidenceStatus(claim || {})),
    expected: row.expected || 0,
    paid: row.paid || 0,
    outstanding: row.exposure || 0,
    delta: row.delta || 0,
    status: row.status || "Review",
    owner,
    urgency: clientRiskUrgency("auth", row.status, claim),
    nextAction: clientRiskTimelineNextStep("auth", row.status, owner, row.payer)
  };
}

function clientRiskTimelineRowFromFiling(row = {}, claim = {}) {
  const owner = row.blocker === "Client Evidence" ? "Client" : "TMS";
  return {
    claimId: row.claimId || "",
    riskKind: "Timely Filing",
    payer: row.payer || "Unknown payer",
    state: row.state || "State Not Tagged",
    timing: row.deadlineDate ? `${formatDate(row.deadlineDate)} · ${timelyFilingDueText(row)}` : "Needs DOS",
    dueSort: Number.isFinite(row.daysRemaining) ? row.daysRemaining : 9999,
    expected: row.expected || 0,
    paid: row.paid || 0,
    outstanding: row.outstanding || 0,
    delta: row.delta || 0,
    status: row.status || "Review",
    owner,
    urgency: clientRiskUrgency("filing", row.status, claim),
    nextAction: clientRiskTimelineNextStep("filing", row.status, owner, row.payer)
  };
}

function clientRiskTimingRank(status = "") {
  return {
    "Past Due": -1,
    "No Due Date": 0,
    "Due Soon": 1,
    "Ready To Review": 2,
    "Open": 3
  }[status] ?? 4;
}

function clientRiskUrgency(kind, status, claim = {}) {
  if (kind === "filing") {
    if (status === "Expired") return 0;
    if (status === "0-14 Days" || status === "No DOS") return 1;
    if (status === "15-30 Days") return 2;
    return 4;
  }
  if (status === "Auth Denial") return 0;
  if (status === "Missing Auth/Rate" || isPastDue(claim)) return 1;
  if (status === "Past Due Proof" || status === "Client Proof Needed") return 2;
  return 4;
}

function clientRiskTimelineNextStep(kind, status, owner, payer = "payer") {
  if (kind === "filing") {
    if (owner === "Client") return `Send authorization, COB, coverage, records, or provider proof needed for ${payer} before the filing window tightens.`;
    if (status === "Expired") return "TMS should document timely filing proof, appeal path, or corrected-claim evidence before this is closed.";
    if (status === "No DOS") return "TMS should validate date-of-service mapping before deadline math is presented.";
    return `TMS should submit, rebill, or document payer status for ${payer} before the deadline.`;
  }
  if (owner === "Client") return "Upload authorization approval, coverage proof, COB, records, or provider documentation requested by TMS.";
  if (status === "Auth Denial") return "TMS should prepare corrected-claim or appeal packet and request any missing client proof.";
  if (status === "Missing Auth/Rate") return "TMS should validate authorization/rate setup and tell the client exactly what proof is missing.";
  return "TMS should monitor payer status and keep next follow-up date current.";
}

function renderClientRiskTimelineRow(row) {
  const tone = row.urgency <= 0 ? "red" : row.urgency <= 2 ? "amber" : row.owner === "Client" ? "amber" : "blue";
  return `
    <div class="client-risk-timeline-row">
      <span data-label="Claim / Risk"><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.riskKind)} · ${escapeHtml(row.status)}</small></span>
      <span data-label="Payer / State"><strong>${escapeHtml(row.payer)}</strong><small>${escapeHtml(row.state)}</small></span>
      <span data-label="Timing"><strong>${escapeHtml(row.timing)}</strong><small>${row.dueSort < 0 ? "Past due" : "Deadline/proof timing"}</small></span>
      <span data-label="AR / Delta"><strong>${money(row.outstanding)}</strong><small>Delta ${money(row.delta)}</small></span>
      <span data-label="Owner">${pill(row.owner, row.owner === "Client" ? "amber" : "blue")}</span>
      <span data-label="Client-Safe Next Step">${escapeHtml(row.nextAction)}</span>
    </div>
  `;
}

function renderClientPayerStateDrilldown(claims = [], totals = {}) {
  const model = clientPayerStateDrilldownModel(claims, totals);
  return `
    <section class="client-payer-state-drilldown" aria-label="Client payer and state drilldown">
      <div class="client-payer-state-head">
        <div>
          <span>Payer x State Drilldown</span>
          <strong>Which payer and state combinations are driving AR</strong>
          <p>Client-facing action map from the same active slicers, claim ledger rows, and expected-paid-AR math.</p>
        </div>
        ${pill(model.delta === 0 ? "Math ties" : "Formula review", model.delta === 0 ? "green" : "amber")}
      </div>
      <div class="client-payer-state-summary">
        <span><small>Combos</small><strong>${model.rows.length}</strong><em>${escapeHtml(totals.selectedState || "All states")}</em></span>
        <span><small>Open AR</small><strong>${money(model.outstanding)}</strong><em>Filtered claims</em></span>
        <span><small>Client Owned</small><strong>${money(model.clientOwned)}</strong><em>Auth/COB/docs</em></span>
        <span><small>TMS Owned</small><strong>${money(model.tmsOwned)}</strong><em>Payer/appeal/review</em></span>
        <span><small>Formula Delta</small><strong>${money(model.delta)}</strong><em>Expected - Paid - AR</em></span>
      </div>
      <div class="client-payer-state-list">
        <div class="client-payer-state-row client-payer-state-header">
          <span>Payer / State</span>
          <span>Claims</span>
          <span>Open AR</span>
          <span>Owner</span>
          <span>Formula</span>
          <span>Client Meeting Action</span>
        </div>
        ${model.rows.slice(0, 8).map(renderClientPayerStateRow).join("") || '<div class="client-payer-state-empty">No payer/state AR combinations in the active slicers.</div>'}
      </div>
    </section>
  `;
}

function clientPayerStateDrilldownModel(claims = [], totals = {}) {
  const grouped = new Map();
  const selectedInsurance = totals.selectedInsurance || "All";
  claims.forEach((claim) => {
    const stateValue = clientStateValue(claim) || "State Not Tagged";
    const payerValues = selectedInsurance === "All"
      ? [claim.primaryPayer || claim.secondaryPayer || "Unknown payer"]
      : [selectedInsurance].filter((payer) => claim.primaryPayer === payer || claim.secondaryPayer === payer);
    const financial = claimFinancialView(claim);
    payerValues.forEach((payer) => {
      const key = `${stateValue}::${payer}`;
      const current = grouped.get(key) || {
        state: stateValue,
        payer,
        claims: 0,
        expected: 0,
        paid: 0,
        outstanding: 0,
        delta: 0,
        clientOwned: 0,
        tmsOwned: 0,
        guarded: 0,
        statuses: new Map()
      };
      current.claims += 1;
      current.expected += financial.expectedReimbursement;
      current.paid += financial.totalInsurancePaid;
      current.outstanding += financial.sourceOutstanding;
      current.delta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
      if (clientPayerStateOwner(claim) === "Client") current.clientOwned += financial.sourceOutstanding;
      else current.tmsOwned += financial.sourceOutstanding;
      if (evaluateClaimCalculationGuards(claim).length) current.guarded += 1;
      const status = clientOpenNeed(claim);
      current.statuses.set(status, (current.statuses.get(status) || 0) + 1);
      grouped.set(key, current);
    });
  });
  const rows = Array.from(grouped.values()).map((row) => {
    row.expected = moneyNumber(row.expected);
    row.paid = moneyNumber(row.paid);
    row.outstanding = moneyNumber(row.outstanding);
    row.delta = moneyNumber(row.delta);
    row.clientOwned = moneyNumber(row.clientOwned);
    row.tmsOwned = moneyNumber(row.tmsOwned);
    row.owner = row.clientOwned > row.tmsOwned ? "Client" : "TMS";
    row.topNeed = Array.from(row.statuses.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] || "None";
    row.nextAction = clientPayerStateNextAction(row);
    return row;
  }).sort((a, b) => b.outstanding - a.outstanding || b.claims - a.claims || a.payer.localeCompare(b.payer));
  const aggregate = rows.reduce((sum, row) => {
    sum.outstanding += row.outstanding;
    sum.clientOwned += row.clientOwned;
    sum.tmsOwned += row.tmsOwned;
    sum.delta += row.delta;
    return sum;
  }, { outstanding: 0, clientOwned: 0, tmsOwned: 0, delta: 0 });
  return {
    rows,
    outstanding: moneyNumber(aggregate.outstanding),
    clientOwned: moneyNumber(aggregate.clientOwned),
    tmsOwned: moneyNumber(aggregate.tmsOwned),
    delta: moneyNumber(aggregate.delta)
  };
}

function renderClientPayerStateRow(row) {
  return `
    <div class="client-payer-state-row">
      <span data-label="Payer / State"><strong>${escapeHtml(row.payer)}</strong><small>${escapeHtml(row.state)}</small></span>
      <span data-label="Claims"><strong>${row.claims}</strong><small>${row.guarded} guarded</small></span>
      <span data-label="Open AR"><strong>${money(row.outstanding)}</strong><small>${money(row.expected)} expected</small></span>
      <span data-label="Owner">${pill(row.owner, row.owner === "Client" ? "amber" : "blue")}</span>
      <span data-label="Formula"><strong>${money(row.delta)}</strong><small>Expected - paid - AR</small></span>
      <span data-label="Client Meeting Action">${escapeHtml(row.nextAction)}</span>
    </div>
  `;
}

function clientPayerStateOwner(claim = {}) {
  return clientStatus(claim) === "Client Action Needed" || needsClientAction(claim) ? "Client" : "TMS";
}

function clientPayerStateNextAction(row) {
  if (row.owner === "Client") return `Resolve ${row.topNeed.toLowerCase()} for ${row.payer} in ${row.state}.`;
  if (row.guarded > 0) return `Review ${row.guarded} guarded claim${row.guarded === 1 ? "" : "s"} before presenting totals.`;
  if (row.outstanding > 0) return `TMS to work payer follow-up and report progress on ${row.payer}.`;
  return "No open AR action for this payer/state combination.";
}

function renderClientFilteredClaimLedger(claims, serviceRows, totals) {
  const rows = clientFilteredClaimLedgerRows(claims).slice(0, 12);
  const totalDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  const openClaims = claims.filter((claim) => claimFinancialView(claim).sourceOutstanding > 0).length;
  return `
    <section class="client-drilldown-ledger" aria-label="Client filtered claim drilldown">
      <div class="client-drilldown-head">
        <div>
          <span>Filtered Claim Drilldown</span>
          <strong>Every chart number ties back to these claim rows</strong>
        </div>
        ${pill(totalDelta === 0 ? "Math ties" : "Review delta", totalDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-drilldown-summary">
        <span><small>Claims</small><strong>${claims.length}</strong><em>${openClaims} open</em></span>
        <span><small>Service Lines</small><strong>${serviceRows.length}</strong><em>Active filters</em></span>
        <span><small>Expected</small><strong>${money(totals.totalExpected)}</strong><em>Claim total</em></span>
        <span><small>Paid</small><strong>${money(totals.totalPaid)}</strong><em>Insurance paid</em></span>
        <span><small>Outstanding</small><strong>${money(totals.totalOwed)}</strong><em>Open AR</em></span>
        <span><small>Formula Delta</small><strong>${money(totalDelta)}</strong><em>Expected - Paid - AR</em></span>
      </div>
      <div class="client-drilldown-table-wrap">
        <table class="client-drilldown-table">
          <thead>
            <tr>
              <th>Claim</th>
              <th>State</th>
              <th>Insurance</th>
              <th>Status</th>
              <th class="num">Expected</th>
              <th class="num">Paid</th>
              <th class="num">Outstanding</th>
              <th class="num">Delta</th>
              <th>Open Need</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => `
              <tr>
                <td><strong>${escapeHtml(row.claimId)}</strong><span>${escapeHtml(row.facility)}</span></td>
                <td>${escapeHtml(row.state)}</td>
                <td>${escapeHtml(row.insurance)}</td>
                <td>${escapeHtml(row.status)}</td>
                <td class="num">${money(row.expected)}</td>
                <td class="num">${money(row.paid)}</td>
                <td class="num">${money(row.outstanding)}</td>
                <td class="num">${money(row.delta)}</td>
                <td>${escapeHtml(row.openNeed)}</td>
              </tr>
            `).join("") || '<tr><td colspan="9">No claim rows match the active slicers.</td></tr>'}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function clientFilteredClaimLedgerRows(claims) {
  return claims.map((claim) => {
    const financial = claimFinancialView(claim);
    return {
      claimId: claim.claimId,
      facility: claim.facility || "Unknown facility",
      state: clientStateValue(claim) || "Unknown",
      insurance: claim.primaryPayer || claim.secondaryPayer || "Unknown",
      status: clientStatus(claim),
      expected: financial.expectedReimbursement,
      paid: financial.totalInsurancePaid,
      outstanding: financial.sourceOutstanding,
      delta: moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding),
      openNeed: clientOpenNeed(claim)
    };
  }).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || b.outstanding - a.outstanding || a.claimId.localeCompare(b.claimId));
}

function renderClientSlicerCommand(allClaims, totals) {
  const stateRows = clientSlicerRows(allClaims, "state", totals).slice(0, 10);
  const insuranceRows = clientSlicerRows(allClaims, "insurance", totals).slice(0, 10);
  return `
    <section class="client-slicer-command" aria-label="Client state and insurance slicers">
      <div class="client-slicer-command-head">
        <div>
          <span>Excel Slicer Command</span>
          <strong>Filter the charts by state and insurance</strong>
        </div>
        <button class="ghost-button" type="button" data-client-clear-slicers>Clear slicers</button>
      </div>
      <div class="client-slicer-lanes">
        <div>
          <div class="client-slicer-lane-title">
            <strong>State Slicer</strong>
            <span>${escapeHtml(totals.selectedState === "All" ? "All states" : totals.selectedState)}</span>
          </div>
          <div class="client-slicer-chip-row">
            ${renderClientSlicerChip({ label: "All States", value: "All", claims: allClaims.length, amount: null }, "state", totals.selectedState === "All")}
            ${stateRows.map((row) => renderClientSlicerChip(row, "state", row.value === totals.selectedState)).join("")}
          </div>
        </div>
        <div>
          <div class="client-slicer-lane-title">
            <strong>Insurance Slicer</strong>
            <span>${escapeHtml(totals.selectedInsurance === "All" ? "All insurance" : totals.selectedInsurance)}</span>
          </div>
          <div class="client-slicer-chip-row">
            ${renderClientSlicerChip({ label: "All Insurance", value: "All", claims: allClaims.length, amount: null }, "insurance", totals.selectedInsurance === "All")}
            ${insuranceRows.map((row) => renderClientSlicerChip(row, "insurance", row.value === totals.selectedInsurance)).join("")}
          </div>
        </div>
      </div>
    </section>
  `;
}

function clientSlicerRows(allClaims, dimension, totals) {
  const selectedClient = totals.selectedClient || "All";
  const selectedState = totals.selectedState || "All";
  const selectedInsurance = totals.selectedInsurance || "All";
  const grouped = new Map();
  allClaims.forEach((claim) => {
    if (selectedClient !== "All" && claim.client !== selectedClient) return;
    if (dimension === "state" && selectedInsurance !== "All"
      && claim.primaryPayer !== selectedInsurance && claim.secondaryPayer !== selectedInsurance) return;
    if (dimension === "insurance" && selectedState !== "All" && clientStateValue(claim) !== selectedState) return;
    const values = dimension === "insurance"
      ? [claim.primaryPayer, claim.secondaryPayer].filter(Boolean)
      : [clientStateValue(claim) || "Unknown"];
    values.forEach((value) => {
      const current = grouped.get(value) || { label: value, value, claims: 0, amount: 0 };
      current.claims += 1;
      current.amount += claimFinancialView(claim).sourceOutstanding;
      grouped.set(value, current);
    });
  });
  return Array.from(grouped.values())
    .map((row) => ({ ...row, amount: moneyNumber(row.amount) }))
    .sort((a, b) => b.amount - a.amount || b.claims - a.claims || a.label.localeCompare(b.label));
}

function renderClientSlicerChip(row, dimension, active) {
  const dataAttr = dimension === "state" ? "data-client-state-slicer" : "data-client-insurance-slicer";
  return `
    <button class="client-slicer-chip ${active ? "is-active" : ""}" type="button" ${dataAttr}="${escapeHtml(row.value)}" title="${escapeHtml(row.label)}">
      <strong>${escapeHtml(row.label)}</strong>
      <span>${formatNounCount(row.claims, "claim")}${row.amount == null ? "" : ` · ${money(row.amount)}`}</span>
    </button>
  `;
}

function renderClientFilterImpactBoard(allClaims = [], filteredClaims = [], totals = {}) {
  const model = clientFilterImpactModel(allClaims, filteredClaims, totals);
  return `
    <section class="client-filter-impact" aria-label="Client filter impact board">
      <div class="client-filter-impact-head">
        <div>
          <span>Filter Impact Board</span>
          <strong>What the active state and insurance slicers changed</strong>
          <p>Side-by-side Excel/Power BI slicer proof: the current chart scope versus the selected client book before state and insurance filters.</p>
        </div>
        ${pill(model.formulaDelta === 0 ? "Filtered math tied" : "Filtered math review", model.formulaDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-filter-impact-context">
        ${model.context.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong title="${escapeHtml(item.value)}">${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-filter-impact-list" role="table" aria-label="Client filter impact metrics">
        <div class="client-filter-impact-row client-filter-impact-header" role="row">
          <span>Measure</span>
          <span>Selected Scope</span>
          <span>Client Book</span>
          <span>Share</span>
          <span>Delta / Meaning</span>
        </div>
        ${model.rows.map(renderClientFilterImpactRow).join("")}
      </div>
    </section>
  `;
}

function clientFilterImpactModel(allClaims = [], filteredClaims = [], totals = {}) {
  const selectedClient = totals.selectedClient || "All";
  const clientBookClaims = allClaims.filter((claim) => selectedClient === "All" || claim.client === selectedClient);
  const selected = clientFilterImpactSummary(filteredClaims);
  const book = clientFilterImpactSummary(clientBookClaims);
  const formulaDelta = moneyNumber(selected.expected - selected.paid - selected.outstanding);
  const rows = [
    clientFilterImpactRow("Claims", selected.claims, book.claims, "count", "How much of the client book is visible in the charts."),
    clientFilterImpactRow("Expected", selected.expected, book.expected, "money", "Expected reimbursement inside the active slicers."),
    clientFilterImpactRow("Paid", selected.paid, book.paid, "money", "Insurance cash captured by the active slicers."),
    clientFilterImpactRow("Open AR", selected.outstanding, book.outstanding, "money", "Outstanding payer/client exposure in the current view."),
    clientFilterImpactRow("Client Proof", selected.clientProofAr, book.clientProofAr, "money", "AR blocked by authorization, COB, coverage, or documentation."),
    clientFilterImpactRow("Past Due", selected.pastDue, book.pastDue, "count", "Follow-up items already past the planned next action date.")
  ];
  return {
    formulaDelta,
    context: [
      { label: "Client", value: selectedClient === "All" ? "All clients" : selectedClient, detail: formatNounCount(book.claims, "book claim") },
      { label: "State Slicer", value: totals.selectedState === "All" ? "All states" : totals.selectedState || "All states", detail: `${formatNounCount(selected.stateCount, "state")} visible` },
      { label: "Insurance Slicer", value: totals.selectedInsurance === "All" ? "All insurance" : totals.selectedInsurance || "All insurance", detail: `${formatNounCount(selected.payerCount, "payer")} visible` },
      { label: "Formula Proof", value: money(formulaDelta), detail: "Expected - Paid - AR in selected scope" }
    ],
    rows
  };
}

function clientFilterImpactSummary(claims = []) {
  const summary = claims.reduce((sum, claim) => {
    const financial = claimFinancialView(claim);
    sum.claims += 1;
    sum.expected += financial.expectedReimbursement;
    sum.paid += financial.totalInsurancePaid;
    sum.outstanding += financial.sourceOutstanding;
    if (isPastDue(claim)) sum.pastDue += 1;
    if (clientStatus(claim) === "Client Action Needed" || clientOpenNeed(claim) !== "None") {
      sum.clientProof += 1;
      sum.clientProofAr += financial.sourceOutstanding;
    }
    sum.states.add(clientStateValue(claim) || "Unknown");
    if (claim.primaryPayer) sum.payers.add(claim.primaryPayer);
    if (claim.secondaryPayer) sum.payers.add(claim.secondaryPayer);
    return sum;
  }, { claims: 0, expected: 0, paid: 0, outstanding: 0, pastDue: 0, clientProof: 0, clientProofAr: 0, states: new Set(), payers: new Set() });
  return {
    ...summary,
    expected: moneyNumber(summary.expected),
    paid: moneyNumber(summary.paid),
    outstanding: moneyNumber(summary.outstanding),
    clientProofAr: moneyNumber(summary.clientProofAr),
    stateCount: summary.states.size,
    payerCount: summary.payers.size
  };
}

function clientFilterImpactRow(label, selectedValue, bookValue, valueType, meaning) {
  const share = bookValue > 0 ? selectedValue / bookValue : selectedValue > 0 ? 1 : 0;
  const delta = moneyNumber(selectedValue - bookValue);
  return {
    label,
    selectedValue: moneyNumber(selectedValue),
    bookValue: moneyNumber(bookValue),
    valueType,
    share,
    shareLabel: bookValue > 0 ? `${Math.round(share * 100)}%` : selectedValue > 0 ? "100%" : "0%",
    delta,
    meaning
  };
}

function renderClientFilterImpactRow(row) {
  const barWidth = Math.max(row.share > 0 ? 5 : 0, Math.min(100, Math.round(row.share * 100)));
  return `
    <div class="client-filter-impact-row" role="row">
      <span data-label="Measure"><strong>${escapeHtml(row.label)}</strong><small>${escapeHtml(row.meaning)}</small></span>
      <span data-label="Selected Scope"><strong>${escapeHtml(formatClientImpactValue(row.selectedValue, row.valueType))}</strong><small>Active charts</small></span>
      <span data-label="Client Book"><strong>${escapeHtml(formatClientImpactValue(row.bookValue, row.valueType))}</strong><small>Before state/insurance slicers</small></span>
      <span data-label="Share">
        <strong>${escapeHtml(row.shareLabel)}</strong>
        <i class="client-filter-impact-track"><b style="width:${barWidth}%"></b></i>
      </span>
      <span data-label="Delta / Meaning"><strong>${escapeHtml(formatClientImpactValue(row.delta, row.valueType))}</strong><small>Selected minus book</small></span>
    </div>
  `;
}

function renderClientSlicerVisualCommand(allClaims = [], filteredClaims = [], serviceRows = [], totals = {}) {
  const model = clientSlicerVisualCommandModel(allClaims, filteredClaims, serviceRows, totals);
  return `
    <section class="client-slicer-visual-command" aria-label="Client slicer visual command">
      <div class="client-slicer-visual-head">
        <div>
          <span>Client Slicer Visual Command</span>
          <strong>State and insurance charts tied to active filters</strong>
          <p>Power BI-style visuals with Excel slicer proof: every chart below is driven by the selected client, state, and insurance filters and reconciles expected minus paid minus AR.</p>
        </div>
        ${pill(model.formulaDelta === 0 ? "Selected math tied" : "Selected math review", model.formulaDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-slicer-visual-summary">
        ${model.context.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong title="${escapeHtml(item.value)}">${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-slicer-visual-grid">
        ${renderClientSlicerVisualChart("State Filter Chart", model.stateSubtitle, model.stateRows, model.selectedState)}
        ${renderClientSlicerVisualChart("Insurance Filter Chart", model.insuranceSubtitle, model.insuranceRows, model.selectedInsurance)}
      </div>
      <div class="client-slicer-visual-action">
        <div>
          <span>Next Report View</span>
          <strong>${escapeHtml(model.nextAction.title)}</strong>
          <p>${escapeHtml(model.nextAction.detail)}</p>
        </div>
        ${pill(model.nextAction.toneLabel, model.nextAction.tone)}
      </div>
    </section>
  `;
}

function clientSlicerVisualCommandModel(allClaims = [], filteredClaims = [], serviceRows = [], totals = {}) {
  const selectedClient = totals.selectedClient || "All";
  const selectedState = totals.selectedState || "All";
  const selectedInsurance = totals.selectedInsurance || "All";
  const clientBookClaims = allClaims.filter((claim) => selectedClient === "All" || claim.client === selectedClient);
  const selected = clientSlicerVisualSummary(filteredClaims);
  const book = clientSlicerVisualSummary(clientBookClaims);
  const formulaDelta = moneyNumber(selected.expected - selected.paid - selected.outstanding);
  const visibleArShare = book.outstanding > 0 ? selected.outstanding / book.outstanding : selected.outstanding > 0 ? 1 : 0;
  const stateRows = clientSlicerVisualRows(clientBookClaims, "state");
  const insuranceRows = clientSlicerVisualRows(clientBookClaims, "insurance");
  const activeStateRows = selectedState === "All" ? stateRows : stateRows.filter((row) => row.label === selectedState);
  const activeInsuranceRows = selectedInsurance === "All" ? insuranceRows : insuranceRows.filter((row) => row.label === selectedInsurance);
  const nextAction = clientSlicerVisualNextAction({
    selected,
    book,
    selectedState,
    selectedInsurance,
    visibleArShare,
    formulaDelta,
    activeStateRows,
    activeInsuranceRows
  });
  return {
    selectedClient,
    selectedState,
    selectedInsurance,
    selected,
    book,
    stateRows,
    insuranceRows,
    activeStateRows,
    activeInsuranceRows,
    formulaDelta,
    visibleArShare,
    nextAction,
    stateSubtitle: selectedState === "All"
      ? "All states in the selected client book"
      : `${selectedState} highlighted against the full client book`,
    insuranceSubtitle: selectedInsurance === "All"
      ? "All primary/secondary insurance in the selected client book"
      : `${selectedInsurance} highlighted across visible states`,
    context: [
      { label: "Active State", value: selectedState === "All" ? "All states" : selectedState, detail: `${formatNounCount(selected.stateCount, "state")} visible` },
      { label: "Active Insurance", value: selectedInsurance === "All" ? "All insurance" : selectedInsurance, detail: `${formatNounCount(selected.payerCount, "payer")} visible` },
      { label: "Visible AR Share", value: `${Math.round(visibleArShare * 100)}%`, detail: `${money(selected.outstanding)} of ${money(book.outstanding)} book AR` },
      { label: "Selected Claims", value: formatNounCount(selected.claims, "claim"), detail: formatNounCount(serviceRows.length, "visible service line") },
      { label: "Selected Formula Proof", value: money(formulaDelta), detail: "Expected - paid - open AR" }
    ]
  };
}

function clientSlicerVisualRows(claims = [], dimension = "state") {
  const grouped = new Map();
  claims.forEach((claim) => {
    const values = dimension === "insurance"
      ? Array.from(new Set([claim.primaryPayer, claim.secondaryPayer].filter(Boolean))).slice(0, 2)
      : [clientStateValue(claim) || "Unknown"];
    values.forEach((value) => {
      const current = grouped.get(value) || {
        label: value,
        claims: 0,
        expected: 0,
        paid: 0,
        outstanding: 0,
        clientProofAr: 0,
        pastDue: 0
      };
      const financial = claimFinancialView(claim);
      current.claims += 1;
      current.expected += financial.expectedReimbursement;
      current.paid += financial.totalInsurancePaid;
      current.outstanding += financial.sourceOutstanding;
      if (clientStatus(claim) === "Client Action Needed" || clientOpenNeed(claim) !== "None") {
        current.clientProofAr += financial.sourceOutstanding;
      }
      if (isPastDue(claim)) current.pastDue += 1;
      grouped.set(value, current);
    });
  });
  const rows = Array.from(grouped.values()).map((row) => {
    const expected = moneyNumber(row.expected);
    const paid = moneyNumber(row.paid);
    const outstanding = moneyNumber(row.outstanding);
    const clientProofAr = moneyNumber(row.clientProofAr);
    return {
      ...row,
      expected,
      paid,
      outstanding,
      clientProofAr,
      collectionPct: expected > 0 ? paid / expected : null,
      formulaDelta: moneyNumber(expected - paid - outstanding),
      share: 0
    };
  }).sort((a, b) => b.outstanding - a.outstanding || b.clientProofAr - a.clientProofAr || b.claims - a.claims || a.label.localeCompare(b.label));
  const totalOutstanding = rows.reduce((sum, row) => sum + row.outstanding, 0);
  return rows.map((row) => ({
    ...row,
    share: totalOutstanding > 0 ? row.outstanding / totalOutstanding : row.outstanding > 0 ? 1 : 0
  }));
}

function clientSlicerVisualSummary(claims = []) {
  const summary = claims.reduce((sum, claim) => {
    const financial = claimFinancialView(claim);
    sum.claims += 1;
    sum.expected += financial.expectedReimbursement;
    sum.paid += financial.totalInsurancePaid;
    sum.outstanding += financial.sourceOutstanding;
    if (clientStatus(claim) === "Client Action Needed" || clientOpenNeed(claim) !== "None") {
      sum.clientProof += 1;
      sum.clientProofAr += financial.sourceOutstanding;
    }
    if (isPastDue(claim)) sum.pastDue += 1;
    sum.states.add(clientStateValue(claim) || "Unknown");
    if (claim.primaryPayer) sum.payers.add(claim.primaryPayer);
    if (claim.secondaryPayer) sum.payers.add(claim.secondaryPayer);
    return sum;
  }, { claims: 0, expected: 0, paid: 0, outstanding: 0, clientProof: 0, clientProofAr: 0, pastDue: 0, states: new Set(), payers: new Set() });
  return {
    ...summary,
    expected: moneyNumber(summary.expected),
    paid: moneyNumber(summary.paid),
    outstanding: moneyNumber(summary.outstanding),
    clientProofAr: moneyNumber(summary.clientProofAr),
    stateCount: summary.states.size,
    payerCount: summary.payers.size
  };
}

function clientSlicerVisualNextAction(model = {}) {
  const selected = model.selected || {};
  if (model.formulaDelta !== 0) {
    return {
      title: "Review formula variance before client presentation",
      detail: `The selected scope has ${money(model.formulaDelta)} expected-minus-paid-minus-AR variance. Reconcile this before exporting the client pack.`,
      tone: "amber",
      toneLabel: "Math review"
    };
  }
  if ((selected.clientProofAr || 0) > 0) {
    return {
      title: "Open the client action pack for proof blockers",
      detail: `${money(selected.clientProofAr)} of visible AR needs client-side authorization, COB, coverage, or documentation proof before payer follow-up can finish.`,
      tone: "red",
      toneLabel: "Client proof"
    };
  }
  if ((selected.pastDue || 0) > 0) {
    return {
      title: "Prioritize past-due follow-up inside this slicer",
      detail: `${selected.pastDue} visible claims are already beyond the planned next action date. Work those before expanding the slicer.`,
      tone: "amber",
      toneLabel: "Past due"
    };
  }
  if (model.selectedState === "All" || model.selectedInsurance === "All") {
    return {
      title: "Narrow to one state and one insurance for the meeting view",
      detail: "Use the slicers above to isolate the payer/state combination clients can act on during the call.",
      tone: "blue",
      toneLabel: "Slicer ready"
    };
  }
  return {
    title: "Use this filtered view as the client-ready chart pack",
    detail: `${model.selectedState} and ${model.selectedInsurance} are isolated, math is tied, and no client-proof blocker is open in the selected chart scope.`,
    tone: "green",
    toneLabel: "Ready"
  };
}

function renderClientSlicerVisualChart(title, subtitle, rows = [], activeValue = "All") {
  const displayRows = rows.slice(0, 8);
  const labels = displayRows.map((row) => row.label);
  const chart = renderChartCanvas("combo", labels, [
    { label: "Open AR", valueType: "money", values: displayRows.map((row) => row.outstanding) },
    { label: "Client Proof AR", valueType: "money", values: displayRows.map((row) => row.clientProofAr) },
    { label: "Collection %", valueType: "percent", values: displayRows.map((row) => row.collectionPct == null ? null : Math.round(row.collectionPct * 100)) }
  ]);
  return `
    <article class="client-slicer-visual-chart">
      <div class="client-slicer-visual-chart-head">
        <div>
          <strong>${escapeHtml(title)}</strong>
          <span>${escapeHtml(subtitle)}</span>
        </div>
        ${pill(activeValue === "All" ? "All selected" : activeValue, activeValue === "All" ? "blue" : "green")}
      </div>
      <div class="chart-canvas">${chart}</div>
      <div class="client-slicer-visual-list" role="table" aria-label="${escapeHtml(title)} detail rows">
        <div class="client-slicer-visual-row client-slicer-visual-header" role="row">
          <span>Filter Value</span>
          <span>Open AR</span>
          <span>Paid / Expected</span>
          <span>Share</span>
          <span>Status</span>
        </div>
        ${displayRows.map((row) => renderClientSlicerVisualRow(row, activeValue)).join("") || `<div class="chart-empty">No chart rows for this filter.</div>`}
      </div>
    </article>
  `;
}

function renderClientSlicerVisualRow(row, activeValue = "All") {
  const isActive = activeValue !== "All" && row.label === activeValue;
  const status = row.formulaDelta !== 0 ? "Math review" : row.clientProofAr > 0 ? "Client proof" : row.pastDue > 0 ? "Past due" : "Ready";
  const tone = row.formulaDelta !== 0 ? "amber" : row.clientProofAr > 0 ? "red" : row.pastDue > 0 ? "amber" : "green";
  return `
    <div class="client-slicer-visual-row ${isActive ? "is-active" : ""}" role="row">
      <span data-label="Filter Value"><strong>${escapeHtml(row.label)}</strong><small>${formatNounCount(row.claims, "claim")}</small></span>
      <span data-label="Open AR"><strong>${money(row.outstanding)}</strong><small>${money(row.clientProofAr)} client proof</small></span>
      <span data-label="Paid / Expected"><strong>${money(row.paid)} / ${money(row.expected)}</strong><small>${row.collectionPct == null ? "No expected value" : `${Math.round(row.collectionPct * 100)}% collected`}</small></span>
      <span data-label="Share"><strong>${Math.round(row.share * 100)}%</strong><small>${row.pastDue} past due</small></span>
      <span data-label="Status">${pill(status, tone)}<small>Delta ${money(row.formulaDelta)}</small></span>
    </div>
  `;
}

function formatClientImpactValue(value, valueType) {
  if (valueType === "money") return money(value);
  return nonNegativeIntegerValue(value).toLocaleString();
}

function bindClientSlicerButtons() {
  document.querySelectorAll("#clientCharts [data-client-state-slicer]").forEach((button) => {
    button.addEventListener("click", () => {
      if (!els.clientStateFilter) return;
      els.clientStateFilter.value = button.dataset.clientStateSlicer || "All";
      renderClientView();
    });
  });
  document.querySelectorAll("#clientCharts [data-client-insurance-slicer]").forEach((button) => {
    button.addEventListener("click", () => {
      if (!els.clientInsuranceFilter) return;
      els.clientInsuranceFilter.value = button.dataset.clientInsuranceSlicer || "All";
      renderClientView();
    });
  });
  document.querySelector("#clientCharts [data-client-clear-slicers]")?.addEventListener("click", () => {
    if (els.clientStateFilter) els.clientStateFilter.value = "All";
    if (els.clientInsuranceFilter) els.clientInsuranceFilter.value = "All";
    renderClientView();
  });
  document.querySelector("#clientCharts [data-client-chart-pack-export]")?.addEventListener("click", exportClientChartPack);
}

function renderClientChartContext(claims, serviceRows, totals) {
  const filters = [
    ["Client", totals.selectedClient === "All" ? "All clients" : totals.selectedClient],
    ["State", totals.selectedState === "All" ? "All states" : totals.selectedState],
    ["Insurance", totals.selectedInsurance === "All" ? "All insurance" : totals.selectedInsurance]
  ];
  const deltas = [
    ["Expected", totals.totalExpected, totals.serviceExpected],
    ["Paid", totals.totalPaid, totals.servicePaid],
    ["Outstanding", totals.totalOwed, totals.serviceOwed]
  ];
  return `
    <section class="client-chart-context">
      <div class="client-filter-context">
        ${filters.map(([label, value]) => `
          <span title="${escapeHtml(value)}"><small>${escapeHtml(label)}</small><strong>${escapeHtml(value)}</strong></span>
        `).join("")}
      </div>
      <div class="client-reconciliation-card">
        <div class="client-reconciliation-title">
          <strong>Reconciliation</strong>
          ${pill(totals.mathOk ? "Claim totals tie" : "Review rollup", totals.mathOk ? "green" : "amber")}
        </div>
        <div class="client-reconciliation-grid">
          ${deltas.map(([label, claimTotal, serviceTotal]) => {
            const delta = moneyNumber(claimTotal - serviceTotal);
            return `
              <div>
                <span>${escapeHtml(label)}</span>
                <strong>${money(claimTotal)}</strong>
                <small>Service rollup ${money(serviceTotal)} · Delta ${money(delta)}</small>
              </div>
            `;
          }).join("")}
        </div>
        <p>Chart filters and claim table use the same claim set: ${formatNounCount(claims.length, "claim")} / ${formatNounCount(serviceRows.length, "service row")}.</p>
      </div>
    </section>
  `;
}

function renderClientChartAudit(claims, serviceRows, totals) {
  const model = clientChartAuditModel(claims, serviceRows, totals);
  return `
    <section class="client-chart-audit" aria-label="Client chart filter and math audit">
      <div class="client-chart-audit-head">
        <div>
          <span>Chart Filter + Math Audit</span>
          <strong>Every visual uses the same state, insurance, and claim/service scope</strong>
          <p>Boardroom guardrail for client conversations: slicers, chart rows, and workbook-backed pivots must reconcile before the dashboard is trusted.</p>
        </div>
        ${pill(model.auditReady ? "Chart math ready" : "Review chart math", model.auditReady ? "green" : "amber")}
      </div>
      <div class="client-chart-audit-filters">
        ${model.filters.map((filter) => `
          <span>
            <small>${escapeHtml(filter.label)}</small>
            <strong title="${escapeHtml(filter.value)}">${escapeHtml(filter.value)}</strong>
            <em>${escapeHtml(filter.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-chart-audit-grid">
        ${model.audits.map(renderClientChartAuditCard).join("")}
      </div>
    </section>
  `;
}

function clientChartAuditModel(claims = [], serviceRows = [], totals = {}) {
  const monthlyRows = monthlyPayerSummaryRows(serviceRows);
  const stateRows = clientStatePowerBiRows(claims);
  const payerRows = payerPowerBiRows(monthlyRows);
  const claimDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  const serviceDelta = moneyDelta(totals.serviceExpected, totals.servicePaid, totals.serviceOwed);
  const monthlyExpected = moneyNumber(monthlyRows.reduce((sum, row) => sum + row.expectedInsurance, 0));
  const monthlyPaid = moneyNumber(monthlyRows.reduce((sum, row) => sum + row.insurancePaid, 0));
  const monthlyBalance = moneyNumber(monthlyRows.reduce((sum, row) => sum + row.payerBalance, 0));
  const stateExpected = moneyNumber(stateRows.reduce((sum, row) => sum + row.expected, 0));
  const statePaid = moneyNumber(stateRows.reduce((sum, row) => sum + row.paid, 0));
  const stateOutstanding = moneyNumber(stateRows.reduce((sum, row) => sum + row.outstanding, 0));
  const payerExpected = moneyNumber(payerRows.reduce((sum, row) => sum + row.expectedInsurance, 0));
  const payerPaid = moneyNumber(payerRows.reduce((sum, row) => sum + row.insurancePaid, 0));
  const payerBalance = moneyNumber(payerRows.reduce((sum, row) => sum + row.payerBalance, 0));
  const audits = [
    {
      label: "Claim Ledger",
      value: money(claimDelta),
      proof: `${money(totals.totalExpected || 0)} expected - ${money(totals.totalPaid || 0)} paid - ${money(totals.totalOwed || 0)} AR`,
      detail: `${formatNounCount(claims.length, "claim")} · claim-level totals`,
      ready: claimDelta === 0
    },
    {
      label: "Service Rollup",
      value: money(serviceDelta),
      proof: `${money(totals.serviceExpected || 0)} expected - ${money(totals.servicePaid || 0)} paid - ${money(totals.serviceOwed || 0)} AR`,
      detail: `${formatNounCount(serviceRows.length, "service row")} · sheet grain`,
      ready: serviceDelta === 0
    },
    {
      label: "Monthly Payer Pivot",
      value: money(moneyNumber(monthlyExpected - monthlyPaid - monthlyBalance)),
      proof: `${money(monthlyExpected)} expected insurance - ${money(monthlyPaid)} paid - ${money(monthlyBalance)} payer balance`,
      detail: formatNounCount(monthlyRows.length, "month/payer row"),
      ready: moneyNumber(monthlyExpected - monthlyPaid - monthlyBalance) === 0
    },
    {
      label: "State Chart",
      value: money(moneyNumber(stateExpected - statePaid - stateOutstanding)),
      proof: `${money(stateExpected)} expected - ${money(statePaid)} paid - ${money(stateOutstanding)} AR`,
      detail: formatNounCount(stateRows.length, "state bucket"),
      ready: moneyNumber(stateExpected - statePaid - stateOutstanding) === 0
    },
    {
      label: "Payer Chart",
      value: money(moneyNumber(payerExpected - payerPaid - payerBalance)),
      proof: `${money(payerExpected)} expected insurance - ${money(payerPaid)} paid - ${money(payerBalance)} payer balance`,
      detail: formatNounCount(payerRows.length, "payer bucket"),
      ready: moneyNumber(payerExpected - payerPaid - payerBalance) === 0
    }
  ];
  return {
    auditReady: audits.every((audit) => audit.ready),
    filters: [
      { label: "Client", value: totals.selectedClient === "All" ? "All clients" : totals.selectedClient || "All clients", detail: formatNounCount(claims.length, "filtered claim") },
      { label: "State", value: totals.selectedState === "All" ? "All states" : totals.selectedState || "All states", detail: formatNounCount(stateRows.length, "visible bucket") },
      { label: "Insurance", value: totals.selectedInsurance === "All" ? "All insurance" : totals.selectedInsurance || "All insurance", detail: formatNounCount(payerRows.length, "payer bucket") },
      { label: "Data Grain", value: "Claim + service line", detail: formatNounCount(serviceRows.length, "service row") }
    ],
    audits
  };
}

function renderClientChartAuditCard(audit) {
  return `
    <article class="client-chart-audit-card client-chart-audit-card--${audit.ready ? "green" : "amber"}">
      <span>${escapeHtml(audit.label)}</span>
      <strong>${escapeHtml(audit.value)}</strong>
      <small>${escapeHtml(audit.detail)}</small>
      <p>${escapeHtml(audit.proof)}</p>
    </article>
  `;
}

function clientChartAssuranceModel(claims = [], serviceRows = [], totals = {}) {
  const audit = clientChartAuditModel(claims, serviceRows, totals);
  const monthlyRows = monthlyPayerSummaryRows(serviceRows);
  const payerRows = payerPowerBiRows(monthlyRows);
  const stateRows = clientStatePowerBiRows(claims);
  const clientEvidenceRows = clientEvidenceRequestRows(claims);
  const formulaDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  const totalOpen = moneyNumber(totals.totalOwed || 0);
  const topPayer = payerRows[0] || { payer: "No payer", payerBalance: 0, expectedInsurance: 0, insurancePaid: 0 };
  const topState = stateRows[0] || { state: "No state", outstanding: 0, expected: 0, paid: 0 };
  const clientEvidenceAr = moneyNumber(clientEvidenceRows.reduce((sum, row) => sum + row.impact, 0));
  const chartPackBlocked = !audit.auditReady || clientEvidenceRows.length > 0 || Math.abs(formulaDelta) > 0.01;
  const rows = [
    {
      gate: "Math Certification",
      status: audit.auditReady ? "Certified" : "Needs Reconciliation",
      owner: audit.auditReady ? "TMS QA" : "TMS Analyst",
      proof: `${money(totals.totalExpected || 0)} expected - ${money(totals.totalPaid || 0)} paid - ${money(totals.totalOwed || 0)} AR = ${money(formulaDelta)}`,
      risk: audit.auditReady ? "All chart grains reconcile." : "One or more chart grains does not tie to the workbook math.",
      closeAction: audit.auditReady ? "Use chart pack in the client conversation." : "Reconcile claim, service, monthly payer, state, and payer chart totals before export.",
      tone: audit.auditReady ? "green" : "red"
    },
    {
      gate: "Filter Scope",
      status: `${formatNounCount(claims.length, "claim")} / ${formatNounCount(serviceRows.length, "service row")}`,
      owner: "Client Success",
      proof: `${totals.selectedClient || "All clients"} · ${totals.selectedState || "All states"} · ${totals.selectedInsurance || "All insurance"}`,
      risk: claims.length ? "Filtered scope is visible and boardroom-safe." : "No claims are visible under the active slicers.",
      closeAction: claims.length ? "Present the filter context before chart interpretation." : "Clear or adjust state/insurance slicers before presenting.",
      tone: claims.length ? "blue" : "amber"
    },
    {
      gate: "Payer Concentration",
      status: topPayer.payer,
      owner: topPayer.payerBalance > 0 ? "TMS Follow-Up" : "Monitor",
      proof: `${money(topPayer.expectedInsurance)} expected - ${money(topPayer.insurancePaid)} paid - ${money(topPayer.payerBalance)} payer balance`,
      risk: topPayer.payerBalance > 0 ? `${topPayer.payer} is the largest open payer story.` : "No payer balance concentration in scope.",
      closeAction: topPayer.payerBalance > 0 ? "Show payer action board and next payer touch." : "Keep payer view in monitor mode.",
      tone: topPayer.payerBalance > 0 ? "amber" : "green"
    },
    {
      gate: "State Exposure",
      status: topState.state,
      owner: topState.outstanding > 0 ? "Shared" : "Monitor",
      proof: `${money(topState.expected)} expected - ${money(topState.paid)} paid - ${money(topState.outstanding)} AR`,
      risk: topState.outstanding > 0 ? `${topState.state} carries ${clientPercentLabel(totalOpen > 0 ? topState.outstanding / totalOpen : 0)} of open AR.` : "No state exposure in scope.",
      closeAction: topState.outstanding > 0 ? "Use state slicer to isolate payer, auth, and deadline patterns." : "No state-specific action needed.",
      tone: topState.outstanding > 0 ? "blue" : "green"
    },
    {
      gate: "Client Evidence",
      status: `${clientEvidenceRows.length} request${clientEvidenceRows.length === 1 ? "" : "s"}`,
      owner: clientEvidenceRows.length ? "Client" : "Monitor",
      proof: `${money(clientEvidenceAr)} blocked by client-owned proof requests`,
      risk: clientEvidenceRows.length ? "Chart pack includes client-owned blockers that need a clear ask." : "No client-owned evidence blockers in scope.",
      closeAction: clientEvidenceRows.length ? "Open Client Action Commitment Board and request exact proof packets." : "Do not add client action pressure to this meeting.",
      tone: clientEvidenceRows.length ? "amber" : "green"
    },
    {
      gate: "Export Readiness",
      status: chartPackBlocked ? "Hold Export" : "Ready To Export",
      owner: chartPackBlocked ? "TMS Lead" : "Client Success",
      proof: `${audit.audits.filter((row) => row.ready).length}/${audit.audits.length} audit checks ready · ${clientEvidenceRows.length} client blockers`,
      risk: chartPackBlocked ? "At least one chart assurance item needs explanation before sending." : "Chart pack is safe to export from the active filter set.",
      closeAction: chartPackBlocked ? "Resolve math issues or add client-safe explanation before exporting." : "Export chart pack and use it as the meeting artifact.",
      tone: chartPackBlocked ? "amber" : "green"
    }
  ];
  return {
    rows,
    chartPackBlocked,
    summary: [
      { label: "Assurance Gates", value: `${rows.length}`, detail: `${rows.filter((row) => row.tone === "green").length} green` },
      { label: "Chart Pack Status", value: chartPackBlocked ? "Hold" : "Ready", detail: audit.auditReady ? "Math certified" : "Math review" },
      { label: "Client Blockers", value: `${clientEvidenceRows.length}`, detail: money(clientEvidenceAr) },
      { label: "Formula Delta", value: money(formulaDelta), detail: "Expected - Paid - AR" }
    ]
  };
}

function renderClientChartAssuranceBoard(claims = [], serviceRows = [], totals = {}) {
  const model = clientChartAssuranceModel(claims, serviceRows, totals);
  return `
    <section class="client-chart-assurance" aria-label="Client chart assurance board">
      <div class="client-chart-assurance-head">
        <div>
          <span>Client Chart Assurance Board</span>
          <strong>Can this chart pack be trusted in front of the client?</strong>
          <p>Boardroom gate for chart readiness: math certification, active filters, payer/state exposure, client evidence blockers, export readiness, and the exact owner for each follow-up.</p>
        </div>
        ${pill(model.chartPackBlocked ? "Hold before export" : "Ready for client", model.chartPackBlocked ? "amber" : "green")}
      </div>
      <div class="client-chart-assurance-summary">
        ${model.summary.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong>${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-chart-assurance-list" role="table" aria-label="Client chart assurance gates">
        <div class="client-chart-assurance-row client-chart-assurance-header" role="row">
          <span>Assurance Gate</span>
          <span>Status</span>
          <span>Owner</span>
          <span>Proof</span>
          <span>Risk</span>
          <span>Close Action</span>
        </div>
        ${model.rows.map(renderClientChartAssuranceRow).join("")}
      </div>
    </section>
  `;
}

function renderClientChartAssuranceRow(row) {
  return `
    <div class="client-chart-assurance-row client-chart-assurance-row--${escapeHtml(row.tone || "blue")}" role="row">
      <span data-label="Assurance Gate"><strong>${escapeHtml(row.gate)}</strong></span>
      <span data-label="Status">${pill(row.status, row.tone === "red" ? "red" : row.tone === "amber" ? "amber" : row.tone === "green" ? "green" : "blue")}</span>
      <span data-label="Owner"><strong>${escapeHtml(row.owner)}</strong></span>
      <span data-label="Proof">${escapeHtml(row.proof)}</span>
      <span data-label="Risk">${escapeHtml(row.risk)}</span>
      <span data-label="Close Action">${escapeHtml(row.closeAction)}</span>
    </div>
  `;
}

function renderClientChartPackExport(claims = [], serviceRows = [], totals = {}) {
  const model = clientChartPackExportModel(claims, serviceRows, totals);
  return `
    <section class="client-chart-pack-export" aria-label="Client chart pack export">
      <div class="client-chart-pack-head">
        <div>
          <span>Client Chart Pack Export</span>
          <strong>Download the active state and insurance view for client review</strong>
          <p>Client-safe JSON package with slicer context, chart datasets, payer watchlist, meeting agenda, client proof requests, and math certification.</p>
        </div>
        ${pill(model.mathReady ? "Export ready" : "Math review", model.mathReady ? "green" : "amber")}
      </div>
      <div class="client-chart-pack-summary">
        ${model.summary.map((item) => `
          <span>
            <small>${escapeHtml(item.label)}</small>
            <strong>${escapeHtml(item.value)}</strong>
            <em>${escapeHtml(item.detail)}</em>
          </span>
        `).join("")}
      </div>
      <div class="client-chart-pack-manifest" role="table" aria-label="Client chart pack manifest">
        <div class="client-chart-pack-row client-chart-pack-header" role="row">
          <span>Export Section</span>
          <span>Rows</span>
          <span>Purpose</span>
          <span>Math / Safety</span>
        </div>
        ${model.manifest.map((row) => `
          <div class="client-chart-pack-row" role="row">
            <span data-label="Export Section"><strong>${escapeHtml(row.section)}</strong><small>${escapeHtml(row.source)}</small></span>
            <span data-label="Rows"><strong>${row.rows.toLocaleString()}</strong><small>${escapeHtml(row.grain)}</small></span>
            <span data-label="Purpose">${escapeHtml(row.purpose)}</span>
            <span data-label="Math / Safety">${pill(row.ready ? "Ready" : "Review", row.ready ? "green" : "amber")}<small>${escapeHtml(row.proof)}</small></span>
          </div>
        `).join("")}
      </div>
      <div class="client-chart-pack-action">
        <div>
          <span>Package Schema</span>
          <strong>${escapeHtml(model.schema)}</strong>
          <p>${escapeHtml(model.action)}</p>
        </div>
        <button class="primary-button" data-client-chart-pack-export type="button">Export Client Chart Pack</button>
      </div>
    </section>
  `;
}

function clientChartPackExportModel(claims = [], serviceRows = [], totals = {}) {
  const payload = clientChartPackPayload(claims, serviceRows, totals);
  const formulaDelta = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  const serviceDelta = moneyDelta(totals.serviceExpected, totals.servicePaid, totals.serviceOwed);
  const monthlyRows = payload.datasets.monthlyPayerSummary.rows;
  const payerRows = payload.datasets.payerPerformance.rows;
  const stateRows = payload.datasets.stateExposure.rows;
  const insuranceRows = payload.datasets.insuranceExposure.rows;
  const watchRows = payload.datasets.monthlyPayerWatchlist.rows;
  const evidenceRows = payload.datasets.clientEvidenceRequests.rows;
  const manifest = [
    {
      section: "State Exposure Chart",
      source: "Active state slicer",
      rows: stateRows.length,
      grain: "state bucket",
      purpose: "Show open AR, expected reimbursement, and paid cash by state.",
      ready: stateRows.every((row) => row.formulaDelta === 0),
      proof: "Expected - paid - AR by state"
    },
    {
      section: "Insurance Exposure Chart",
      source: "Active insurance slicer",
      rows: insuranceRows.length,
      grain: "primary/secondary payer bucket",
      purpose: "Show insurance concentration and client-proof AR by payer.",
      ready: insuranceRows.every((row) => row.formulaDelta === 0),
      proof: "Expected - paid - AR by insurance"
    },
    {
      section: "Monthly Payer Pivot",
      source: "Tracker monthly payer summary",
      rows: monthlyRows.length,
      grain: "month + payer",
      purpose: "Mirror the Excel monthly payer view in a clean client package.",
      ready: moneyNumber(monthlyRows.reduce((sum, row) => sum + row.formulaDelta, 0)) === 0,
      proof: "Expected insurance - paid - payer balance"
    },
    {
      section: "Payer Action Board",
      source: "Power BI payer rollup",
      rows: payerRows.length,
      grain: "payer",
      purpose: "Give each payer a collection rate, open AR, owner, and next action.",
      ready: payerRows.every((row) => row.formulaDelta === 0),
      proof: "Expected insurance - paid - balance"
    },
    {
      section: "Client Evidence Requests",
      source: "Client-safe request queue",
      rows: evidenceRows.length,
      grain: "claim request",
      purpose: "List authorization, COB, coverage, and documentation asks in client-safe language.",
      ready: !payload.safety.internalTermsDetected,
      proof: payload.safety.internalTermsDetected ? "Review client-safe language" : "Client-safe metadata only"
    }
  ];
  return {
    schema: payload.schema,
    mathReady: formulaDelta === 0 && serviceDelta === 0 && manifest.every((row) => row.ready),
    payload,
    manifest,
    summary: [
      { label: "Active Claims", value: `${claims.length.toLocaleString()}`, detail: formatNounCount(serviceRows.length, "service line") },
      { label: "Open AR", value: money(totals.totalOwed || 0), detail: `${money(totals.totalExpected || 0)} expected / ${money(totals.totalPaid || 0)} paid` },
      { label: "Formula Delta", value: money(formulaDelta), detail: "Claim expected - paid - AR" },
      { label: "Export Sections", value: `${manifest.length}`, detail: `${formatNounCount(watchRows.length, "watch row")} / ${formatNounCount(evidenceRows.length, "client ask")}` }
    ],
    action: `Exports ${stateRows.length + insuranceRows.length + monthlyRows.length + payerRows.length + watchRows.length + evidenceRows.length} client-safe data rows for the active filters.`
  };
}

function clientChartPackPayload(claims = [], serviceRows = [], totals = {}) {
  const monthlyRows = monthlyPayerSummaryRows(serviceRows).map(clientChartPackMonthlyRow);
  const stateRows = clientStatePowerBiRows(claims).map((row) => ({
    state: row.state,
    claims: row.claims,
    expected: row.expected,
    paid: row.paid,
    openAr: row.outstanding,
    formulaDelta: moneyNumber(row.expected - row.paid - row.outstanding)
  }));
  const insuranceRows = clientSlicerVisualRows(claims, "insurance").map((row) => ({
    insurance: row.label,
    claims: row.claims,
    expected: row.expected,
    paid: row.paid,
    openAr: row.outstanding,
    clientProofAr: row.clientProofAr,
    collectionPct: row.collectionPct,
    formulaDelta: row.formulaDelta
  }));
  const payerAction = clientPayerActionBoardModel(claims, serviceRows, totals);
  const meetingBrief = clientMeetingBriefModel(claims, serviceRows, totals);
  const evidenceRows = clientEvidenceRequestRows(claims).map((row) => ({
    claimId: row.claimId,
    requestType: row.requestType,
    state: row.state,
    insurance: row.insurance,
    impact: moneyNumber(row.impact),
    status: row.status,
    dueDate: row.dueDate,
    nextClientAction: row.nextAction,
    formulaDelta: moneyNumber(row.expected - row.paid - row.impact)
  }));
  const payload = {
    schema: "tms-client-chart-pack.v1",
    exportedAt: new Date().toISOString(),
    filters: {
      client: totals.selectedClient || "All",
      state: totals.selectedState || "All",
      insurance: totals.selectedInsurance || "All"
    },
    mathProof: {
      expected: moneyNumber(totals.totalExpected || 0),
      paid: moneyNumber(totals.totalPaid || 0),
      openAr: moneyNumber(totals.totalOwed || 0),
      formulaDelta: moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed),
      serviceExpected: moneyNumber(totals.serviceExpected || 0),
      servicePaid: moneyNumber(totals.servicePaid || 0),
      serviceOpenAr: moneyNumber(totals.serviceOwed || 0),
      serviceFormulaDelta: moneyDelta(totals.serviceExpected, totals.servicePaid, totals.serviceOwed)
    },
    summary: {
      claims: claims.length,
      serviceLines: serviceRows.length,
      states: Array.from(new Set(claims.map((claim) => clientStateValue(claim) || "Unknown"))).length,
      insuranceBuckets: insuranceRows.length,
      clientEvidenceRequests: evidenceRows.length
    },
    datasets: {
      stateExposure: { grain: "state", rows: stateRows },
      insuranceExposure: { grain: "insurance", rows: insuranceRows },
      monthlyPayerSummary: { grain: "month+payer", rows: monthlyRows },
      payerPerformance: {
        grain: "payer",
        rows: payerAction.rows.map((row) => ({
          payer: row.payer,
          lines: row.lines,
          expectedInsurance: row.expectedInsurance,
          paid: row.paid,
          payerBalance: row.balance,
          collectionPct: row.collectionPct,
          formulaDelta: row.formulaDelta,
          clientRequests: row.clientRequests,
          clientOwnedAr: row.clientOwnedAr,
          owner: row.owner,
          risk: row.risk,
          nextAction: row.nextAction
        }))
      },
      monthlyPayerWatchlist: {
        grain: "month+payer",
        rows: monthlyPayerWatchlistRows(monthlyPayerSummaryRows(serviceRows)).map(clientChartPackWatchlistRow)
      },
      meetingAgenda: {
        grain: "agenda",
        rows: meetingBrief.rows.map((row) => ({
          agenda: row.agenda,
          detail: row.detail,
          proof: row.proof,
          owner: row.owner,
          nextClientSafeAction: row.action
        }))
      },
      clientEvidenceRequests: { grain: "claim-request", rows: evidenceRows }
    },
    safety: {
      clientSafe: true,
      excluded: [
        "internal workflow detail",
        "staff assignment metadata",
        "review metadata",
        "audit metadata"
      ],
      internalTermsDetected: false
    }
  };
  const safetyScan = JSON.stringify({
    filters: payload.filters,
    mathProof: payload.mathProof,
    summary: payload.summary,
    datasets: payload.datasets
  });
  payload.safety.internalTermsDetected = /\boffshore\b|bounce-back|review note|assignedTo|assignedBy|workflowByClaim/i.test(safetyScan);
  return payload;
}

function clientChartPackMonthlyRow(row) {
  return {
    month: row.month,
    payer: row.payer,
    lines: row.lines,
    units: row.units,
    expectedInsurance: moneyNumber(row.expectedInsurance),
    insurancePaid: moneyNumber(row.insurancePaid),
    payerBalance: moneyNumber(row.payerBalance),
    patientBalance: Math.max(0, moneyNumber(row.patientBalance)),
    writeOff: moneyNumber(row.writeOff),
    formulaDelta: moneyNumber(row.expectedInsurance - row.insurancePaid - row.payerBalance)
  };
}

function clientChartPackWatchlistRow(row) {
  return {
    month: row.month,
    payer: row.payer,
    reason: row.reason,
    action: row.action,
    expectedInsurance: moneyNumber(row.expectedInsurance),
    insurancePaid: moneyNumber(row.insurancePaid),
    payerBalance: moneyNumber(row.payerBalance),
    collectionPct: row.collectionPct,
    formulaDelta: moneyNumber(row.formulaDelta)
  };
}

function renderClientPowerBiDeck(claims, serviceRows, totals) {
  const monthlyRows = monthlyPayerSummaryRows(serviceRows);
  const monthRows = monthlyPayerTrendRows(monthlyRows).slice(-8);
  const payerRows = payerPowerBiRows(monthlyRows).slice(0, 8);
  const stateRows = clientStatePowerBiRows(claims).slice(0, 8);
  const chartModels = [
    {
      title: "Monthly Cash And AR Trend",
      subtitle: "Expected insurance, paid cash, and payer balance by month",
      type: "line",
      labels: monthRows.map((row) => row.month),
      series: [
        { label: "Expected Insurance", valueType: "money", values: monthRows.map((row) => row.expectedInsurance) },
        { label: "Total Ins Amt Paid", valueType: "money", values: monthRows.map((row) => row.insurancePaid) },
        { label: "Payer Balance", valueType: "money", values: monthRows.map((row) => row.payerBalance) }
      ]
    },
    {
      title: "Payer Collection Performance",
      subtitle: "Top payers by expected insurance with balance and collection rate",
      type: "combo",
      labels: payerRows.map((row) => row.payer),
      series: [
        { label: "Expected Insurance", valueType: "money", values: payerRows.map((row) => row.expectedInsurance) },
        { label: "Paid", valueType: "money", values: payerRows.map((row) => row.insurancePaid) },
        { label: "Collections", valueType: "percent", values: payerRows.map((row) => row.collectionsPct == null ? null : Math.round(row.collectionsPct * 100)) }
      ]
    },
    {
      title: "State Exposure Mix",
      subtitle: "Expected, paid, and outstanding AR by active state filter",
      type: "bar",
      labels: stateRows.map((row) => row.state),
      series: [
        { label: "Expected", valueType: "money", values: stateRows.map((row) => row.expected) },
        { label: "Paid", valueType: "money", values: stateRows.map((row) => row.paid) },
        { label: "Outstanding", valueType: "money", values: stateRows.map((row) => row.outstanding) }
      ]
    }
  ];
  const reconciled = moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed);
  return `
    <section class="client-powerbi-deck">
      <div class="client-powerbi-head">
        <div>
          <span>Power BI Chart Deck</span>
          <strong>Client Revenue Cycle Command View</strong>
          <p>Filter-aware visual layer for state, insurance, payer, month, expected reimbursement, payments, and open AR.</p>
        </div>
        <div class="client-powerbi-proof">
          <span>Math proof</span>
          <strong>${money(totals.totalExpected)} - ${money(totals.totalPaid)} - ${money(totals.totalOwed)}</strong>
          <small>Reconciliation residual ${money(reconciled)} across ${formatNounCount(claims.length, "claim")} / ${formatNounCount(serviceRows.length, "service row")}.</small>
        </div>
      </div>
      <div class="client-powerbi-grid">
        ${chartModels.map(renderClientPowerBiChart).join("")}
      </div>
    </section>
  `;
}

function payerPowerBiRows(rows) {
  const grouped = new Map();
  rows.forEach((row) => {
    const current = grouped.get(row.payer) || { payer: row.payer, expectedInsurance: 0, insurancePaid: 0, payerBalance: 0, lines: 0 };
    current.expectedInsurance += row.expectedInsurance;
    current.insurancePaid += row.insurancePaid;
    current.payerBalance += row.payerBalance;
    current.lines += row.lines;
    grouped.set(row.payer, current);
  });
  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      expectedInsurance: moneyNumber(row.expectedInsurance),
      insurancePaid: moneyNumber(row.insurancePaid),
      payerBalance: moneyNumber(row.payerBalance),
      collectionsPct: row.expectedInsurance > 0 ? row.insurancePaid / row.expectedInsurance : null
    }))
    .sort((a, b) => b.expectedInsurance - a.expectedInsurance || b.payerBalance - a.payerBalance);
}

function clientStatePowerBiRows(claims) {
  const grouped = new Map();
  claims.forEach((claim) => {
    const stateLabel = clientStateValue(claim) || "Unknown";
    const current = grouped.get(stateLabel) || { state: stateLabel, claims: 0, expected: 0, paid: 0, outstanding: 0 };
    const financial = claimFinancialView(claim);
    current.claims += 1;
    current.expected += financial.expectedReimbursement;
    current.paid += financial.totalInsurancePaid;
    current.outstanding += financial.sourceOutstanding;
    grouped.set(stateLabel, current);
  });
  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      expected: moneyNumber(row.expected),
      paid: moneyNumber(row.paid),
      outstanding: moneyNumber(row.outstanding)
    }))
    .sort((a, b) => b.outstanding - a.outstanding || b.expected - a.expected || a.state.localeCompare(b.state));
}

function renderClientPowerBiChart(chart) {
  const labels = Array.isArray(chart.labels) ? chart.labels : [];
  const series = Array.isArray(chart.series) ? chart.series : [];
  const latestKpis = series.map((item, index) => {
    const values = Array.isArray(item.values) ? item.values : [];
    const latest = [...values].reverse().find((value) => value != null);
    return `
      <span>
        <i style="background:${chartSeriesColor(index)}"></i>
        <small>${escapeHtml(item.label)}</small>
        <strong>${escapeHtml(formatChartValue(latest, item.valueType))}</strong>
      </span>
    `;
  }).join("");
  const legend = series.map((item, index) => `
    <span class="client-powerbi-legend-item">
      <i style="background:${chartSeriesColor(index)}"></i>${escapeHtml(item.label)}
    </span>
  `).join("");
  return `
    <article class="client-powerbi-chart">
      <div class="client-powerbi-chart-head">
        <div>
          <strong>${escapeHtml(chart.title)}</strong>
          <span>${escapeHtml(chart.subtitle)}</span>
        </div>
        <b>${escapeHtml(chart.type)}</b>
      </div>
      <div class="client-powerbi-kpis">${latestKpis}</div>
      <div class="chart-canvas">${renderChartCanvas(chart.type, labels, series)}</div>
      <div class="client-powerbi-legend">${legend}</div>
      <details class="client-chart-table">
        <summary>Table</summary>
        ${renderChartDataTable(labels, series)}
      </details>
    </article>
  `;
}

function renderClientPayerActionBoard(claims = [], serviceRows = [], totals = {}) {
  const model = clientPayerActionBoardModel(claims, serviceRows, totals);
  return `
    <section class="client-payer-action-board" aria-label="Client payer action board">
      <div class="client-payer-action-head">
        <div>
          <span>Payer Action Board</span>
          <strong>Collection performance with owner and next action by payer</strong>
          <p>Client-safe operating view from the same monthly payer pivot and active state/insurance slicers: payer balance, collection rate, client proof requests, risk, owner, and next action.</p>
        </div>
        ${pill(model.totals.formulaDelta === 0 ? "Payer math tied" : "Payer math review", model.totals.formulaDelta === 0 ? "green" : "amber")}
      </div>
      <div class="client-payer-action-summary">
        <span><small>Payers</small><strong>${model.totals.payers}</strong><em>${formatNounCount(model.totals.lines, "service line")}</em></span>
        <span><small>Open Payer AR</small><strong>${money(model.totals.balance)}</strong><em>${formatNounCount(model.totals.openPayers, "payer")} with balance</em></span>
        <span><small>Collections</small><strong>${model.totals.collectionPct == null ? "N/A" : `${Math.round(model.totals.collectionPct * 100)}%`}</strong><em>${money(model.totals.paid)} paid of ${money(model.totals.expectedInsurance)}</em></span>
        <span><small>Client Proof</small><strong>${model.totals.clientRequests}</strong><em>${money(model.totals.clientOwnedAr)} waiting on client evidence</em></span>
        <span><small>Formula Delta</small><strong>${money(model.totals.formulaDelta)}</strong><em>Expected insurance - paid - balance</em></span>
      </div>
      <div class="client-payer-action-list" role="table" aria-label="Client payer action list">
        <div class="client-payer-action-row client-payer-action-header" role="row">
          <span>Payer</span>
          <span>Collection</span>
          <span>Open AR</span>
          <span>Client Proof</span>
          <span>Owner / Risk</span>
          <span>Next Action</span>
        </div>
        ${model.rows.length ? model.rows.map(renderClientPayerActionRow).join("") : '<div class="client-payer-action-empty">No payer action rows in the active filters.</div>'}
      </div>
    </section>
  `;
}

function clientPayerActionBoardModel(claims = [], serviceRows = [], totals = {}) {
  const monthlyRows = monthlyPayerSummaryRows(serviceRows);
  const payerRows = payerPowerBiRows(monthlyRows);
  const claimRowsByPayer = new Map();
  claims.forEach((claim) => {
    const payer = claim.primaryPayer || "Unknown";
    const current = claimRowsByPayer.get(payer) || [];
    current.push(claim);
    claimRowsByPayer.set(payer, current);
  });
  const rows = payerRows.map((payerRow) => {
    const payerClaims = claimRowsByPayer.get(payerRow.payer) || [];
    const clientProofRows = payerClaims.filter((claim) => clientStatus(claim) === "Client Action Needed" || clientOpenNeed(claim) !== "None");
    const olderAr = payerClaims
      .filter((claim) => isAgingBandAtLeast(claim, "61-90"))
      .reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
    const formulaDelta = moneyNumber(payerRow.expectedInsurance - payerRow.insurancePaid - payerRow.payerBalance);
    const balanceRate = payerRow.expectedInsurance > 0 ? payerRow.payerBalance / payerRow.expectedInsurance : null;
    const collectionPct = payerRow.collectionsPct;
    const clientOwnedAr = moneyNumber(clientProofRows.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0));
    const risk = clientPayerActionRisk({ payerRow, formulaDelta, balanceRate, collectionPct, clientProofRows, olderAr });
    return {
      payer: payerRow.payer,
      lines: payerRow.lines,
      expectedInsurance: payerRow.expectedInsurance,
      paid: payerRow.insurancePaid,
      balance: payerRow.payerBalance,
      collectionPct,
      formulaDelta,
      clientRequests: clientProofRows.length,
      clientOwnedAr,
      olderAr: moneyNumber(olderAr),
      owner: clientProofRows.length ? "Client + TMS" : payerRow.payerBalance > 0 ? "TMS" : "Monitor",
      risk,
      nextAction: clientPayerActionNextStep({ payer: payerRow.payer, risk, formulaDelta, clientProofRows, balance: payerRow.payerBalance, collectionPct, olderAr })
    };
  }).sort((a, b) => clientPayerActionRiskRank(b.risk) - clientPayerActionRiskRank(a.risk) || b.balance - a.balance || b.expectedInsurance - a.expectedInsurance);
  const expectedInsurance = moneyNumber(payerRows.reduce((sum, row) => sum + row.expectedInsurance, 0));
  const paid = moneyNumber(payerRows.reduce((sum, row) => sum + row.insurancePaid, 0));
  const balance = moneyNumber(payerRows.reduce((sum, row) => sum + row.payerBalance, 0));
  return {
    rows: rows.slice(0, 8),
    totals: {
      payers: payerRows.length,
      openPayers: payerRows.filter((row) => row.payerBalance > 0).length,
      lines: monthlyRows.reduce((sum, row) => sum + row.lines, 0),
      expectedInsurance,
      paid,
      balance,
      collectionPct: expectedInsurance > 0 ? paid / expectedInsurance : null,
      clientRequests: rows.reduce((sum, row) => sum + row.clientRequests, 0),
      clientOwnedAr: moneyNumber(rows.reduce((sum, row) => sum + row.clientOwnedAr, 0)),
      formulaDelta: moneyNumber(expectedInsurance - paid - balance)
    },
    filters: {
      client: totals.selectedClient || "All",
      state: totals.selectedState || "All",
      insurance: totals.selectedInsurance || "All"
    }
  };
}

function clientPayerActionRisk({ payerRow, formulaDelta, balanceRate, collectionPct, clientProofRows, olderAr }) {
  if (formulaDelta !== 0) return "Formula Review";
  if (clientProofRows.length && payerRow.payerBalance > 0) return "Client Proof Blocking";
  if (olderAr > 0) return "Aging AR";
  if (payerRow.payerBalance > 0 && payerRow.insurancePaid <= 0) return "No Cash Posted";
  if (balanceRate != null && balanceRate >= 0.5) return "High Balance";
  if (collectionPct != null && collectionPct < 0.75 && payerRow.payerBalance > 0) return "Collections Lag";
  return payerRow.payerBalance > 0 ? "Monitor AR" : "Healthy";
}

function clientPayerActionRiskRank(risk = "") {
  return {
    "Formula Review": 80,
    "Client Proof Blocking": 70,
    "Aging AR": 65,
    "No Cash Posted": 60,
    "High Balance": 50,
    "Collections Lag": 40,
    "Monitor AR": 20,
    Healthy: 5
  }[risk] || 0;
}

function clientPayerActionNextStep({ payer, risk, formulaDelta, clientProofRows, balance, collectionPct, olderAr }) {
  if (formulaDelta !== 0) return "Reconcile expected insurance, paid, and payer balance before sharing this payer total.";
  if (clientProofRows.length) return `Request exact authorization, COB, coverage, or record proof blocking ${payer}.`;
  if (olderAr > 0) return "Escalate 61+ day AR with payer portal proof, denial status, or appeal/correction plan.";
  if (risk === "No Cash Posted") return "Confirm payer response, denial, ERA, or missing payment posting before client review.";
  if (risk === "High Balance" || risk === "Collections Lag") return `${money(balance)} remains open; review underpayment, aging, and next payer touch.`;
  if (collectionPct != null && collectionPct >= 0.95) return "Keep payer in normal monitoring; collection performance is strong.";
  return "Keep payer on normal monthly review cadence.";
}

function renderClientPayerActionRow(row) {
  const tone = row.risk === "Healthy" ? "green" : row.risk === "Formula Review" || row.risk === "Client Proof Blocking" || row.risk === "Aging AR" ? "amber" : "blue";
  return `
    <div class="client-payer-action-row" role="row">
      <span data-label="Payer"><strong title="${escapeHtml(row.payer)}">${escapeHtml(row.payer)}</strong><small>${row.lines} service line${row.lines === 1 ? "" : "s"}</small></span>
      <span data-label="Collection"><strong>${row.collectionPct == null ? "N/A" : `${Math.round(row.collectionPct * 100)}%`}</strong><small>${money(row.paid)} paid / ${money(row.expectedInsurance)} expected</small></span>
      <span data-label="Open AR"><strong>${money(row.balance)}</strong><small>Formula delta ${money(row.formulaDelta)}</small></span>
      <span data-label="Client Proof"><strong>${row.clientRequests}</strong><small>${money(row.clientOwnedAr)} client-owned AR</small></span>
      <span data-label="Owner / Risk">${pill(row.owner, row.owner.includes("Client") ? "amber" : row.owner === "TMS" ? "blue" : "gray")}<small>${escapeHtml(row.risk)}</small></span>
      <span data-label="Next Action">${escapeHtml(row.nextAction)}</span>
    </div>
  `;
}

function renderClientMonthlyPayerAnalytics(serviceRows) {
  const rows = monthlyPayerSummaryRows(serviceRows);
  const totals = rows.reduce((sum, row) => {
    sum.lines += row.lines;
    sum.units += row.units;
    sum.primaryExpected += row.primaryExpected;
    sum.secondaryExpected += row.secondaryExpected;
    sum.totalExpected += row.totalExpected;
    sum.writeOff += row.writeOff;
    sum.patientBalance += row.patientBalance;
    sum.patientPaid += row.patientPaid;
    sum.expectedInsurance += row.expectedInsurance;
    sum.insurancePaid += row.insurancePaid;
    sum.payerBalance += row.payerBalance;
    return sum;
  }, {
    lines: 0,
    units: 0,
    primaryExpected: 0,
    secondaryExpected: 0,
    totalExpected: 0,
    writeOff: 0,
    patientBalance: 0,
    patientPaid: 0,
    expectedInsurance: 0,
    insurancePaid: 0,
    payerBalance: 0
  });
  const months = Array.from(new Set(rows.map((row) => row.month))).length;
  const payers = Array.from(new Set(rows.map((row) => row.payer))).length;
  const collectionRate = totals.expectedInsurance > 0 ? totals.insurancePaid / totals.expectedInsurance : null;
  const writeOffRate = totals.totalExpected - totals.patientBalance > 0 ? totals.writeOff / (totals.totalExpected - totals.patientBalance) : null;
  const formulaBalance = moneyNumber(Math.max(totals.expectedInsurance - totals.insurancePaid, 0));
  const formulaDelta = moneyNumber(formulaBalance - totals.payerBalance);
  const monthRows = monthlyPayerTrendRows(rows);
  const payerRows = payerConcentrationRows(rows).slice(0, 6);
  const maxMonth = Math.max(...monthRows.map((row) => row.expectedInsurance), 1);
  const maxPayer = Math.max(...payerRows.map((row) => row.payerBalance), 1);
  const tableRows = rows.slice(0, 18);
  const chartBody = monthRows.length ? monthRows.map((row) => {
    const expectedWidth = Math.max(4, Math.round((row.expectedInsurance / maxMonth) * 100));
    const paidWidth = row.expectedInsurance > 0 ? Math.max(0, Math.round((row.insurancePaid / row.expectedInsurance) * 100)) : 0;
    const balanceWidth = row.expectedInsurance > 0 ? Math.max(0, Math.round((row.payerBalance / row.expectedInsurance) * 100)) : 0;
    return `
      <div class="client-monthly-trend-row">
        <span>${escapeHtml(row.month)}</span>
        <div class="client-monthly-stack" title="${escapeHtml(`${row.month}: ${money(row.expectedInsurance)} expected insurance, ${money(row.insurancePaid)} paid, ${money(row.payerBalance)} payer balance`)}">
          <i class="client-monthly-expected" style="width:${expectedWidth}%"></i>
          <i class="client-monthly-paid" style="width:${paidWidth}%"></i>
          <i class="client-monthly-balance" style="width:${balanceWidth}%"></i>
        </div>
        <b>${money(row.payerBalance)}</b>
      </div>
    `;
  }).join("") : `<div class="chart-empty">No monthly payer rows for this filter.</div>`;
  const payerBody = payerRows.length ? payerRows.map((row) => {
    const width = Math.max(4, Math.round((row.payerBalance / maxPayer) * 100));
    return `
      <div class="client-monthly-payer-row">
        <span title="${escapeHtml(row.payer)}">${escapeHtml(row.payer)}</span>
        <div><i style="width:${width}%"></i></div>
        <b>${money(row.payerBalance)}</b>
      </div>
    `;
  }).join("") : `<div class="chart-empty">No payer balance rows.</div>`;
  return `
    <section class="client-monthly-payer-panel">
      <div class="client-chart-title">
        <strong>Monthly Payer Summary</strong>
        <span>Power BI overview from workbook MONTHLY DATA SUMMARY BY PAYER</span>
      </div>
      <div class="client-monthly-slicers" aria-label="Excel slicer chips">
        <span><small>Workbook tab</small><strong>MONTHLY DATA SUMMARY BY PAYER</strong></span>
        <span><small>Rows</small><strong>${formatNounCount(months, "month")}</strong></span>
        <span><small>Columns</small><strong>${formatNounCount(payers, "payer")}</strong></span>
        <span><small>Source grain</small><strong>${formatNounCount(totals.lines, "service line")}</strong></span>
      </div>
      <div class="client-monthly-kpis powerbi-kpis">
        <span><small>Total Expected</small><strong>${money(totals.totalExpected)}</strong><em>Primary ${money(totals.primaryExpected)} · Secondary ${money(totals.secondaryExpected)}</em></span>
        <span><small>Expected Ins. W/O Write-offs</small><strong>${money(totals.expectedInsurance)}</strong><em>After write-off and patient balance</em></span>
        <span><small>Total Ins Amt Paid</small><strong>${money(totals.insurancePaid)}</strong><em>${collectionRate == null ? "No denominator" : `${Math.round(collectionRate * 100)}% collections`}</em></span>
        <span><small>Payer Balance</small><strong>${money(totals.payerBalance)}</strong><em>Formula delta ${money(formulaDelta)}</em></span>
        <span><small>Write-off Amount</small><strong>${money(totals.writeOff)}</strong><em>Write Off % ${writeOffRate == null ? "N/A" : `${Math.round(writeOffRate * 100)}%`}</em></span>
        <span><small>Patient Balance</small><strong>${money(totals.patientBalance)}</strong><em>True PR / positive patient balance</em></span>
      </div>
      <div class="client-monthly-formula-strip">
        <span><strong>Total Expected</strong><b>${money(totals.totalExpected)}</b></span>
        <i>-</i>
        <span><strong>Write-off</strong><b>${money(totals.writeOff)}</b></span>
        <i>-</i>
        <span><strong>PR / Patient Balance</strong><b>${money(totals.patientBalance + totals.patientPaid)}</b></span>
        <i>-</i>
        <span><strong>Paid</strong><b>${money(totals.insurancePaid)}</b></span>
        <i>=</i>
        <span><strong>Payer Balance</strong><b>${money(totals.payerBalance)}</b></span>
      </div>
      <div class="client-monthly-visuals">
        <div class="client-monthly-chart">
          <div class="client-monthly-chart-head">
            <strong>Expected Insurance vs Paid</strong>
            <span><i></i> Expected <i></i> Paid <i></i> Payer Balance</span>
          </div>
          ${chartBody}
        </div>
        <div class="client-monthly-chart">
          <div class="client-monthly-chart-head">
            <strong>Top Payer Balances</strong>
            <span>Filtered payer concentration</span>
          </div>
          ${payerBody}
        </div>
      </div>
      ${renderClientPayerMonthMatrix(rows)}
      ${renderClientMonthlyPayerWatchlist(rows)}
      <details class="client-monthly-table" open>
        <summary>Monthly payer pivot table</summary>
        <div>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Payer</th>
                <th class="num">Lines</th>
                <th class="num">Units</th>
                <th class="num">Primary Expected</th>
                <th class="num">Secondary Expected</th>
                <th class="num">Total Expected</th>
                <th class="num">Write-off</th>
                <th class="num">Patient Balance</th>
                <th class="num">Total Pat Amt Paid</th>
                <th class="num">Expected Ins. W/O Write-offs & PR</th>
                <th class="num">Total Ins Amt Paid</th>
                <th class="num">Payer Balance</th>
                <th class="num">Collections %</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows.map((row) => `
                <tr>
                  <td>${escapeHtml(row.month)}</td>
                  <td>${escapeHtml(row.payer)}</td>
                  <td class="num">${row.lines}</td>
                  <td class="num">${formatDecimal(row.units, 2)}</td>
                  <td class="num">${money(row.primaryExpected)}</td>
                  <td class="num">${money(row.secondaryExpected)}</td>
                  <td class="num">${money(row.totalExpected)}</td>
                  <td class="num">${money(row.writeOff)}</td>
                  <td class="num">${money(row.patientBalance)}</td>
                  <td class="num">${money(row.patientPaid)}</td>
                  <td class="num">${money(row.expectedInsurance)}</td>
                  <td class="num">${money(row.insurancePaid)}</td>
                  <td class="num">${money(row.payerBalance)}</td>
                  <td class="num">${row.collectionsPct == null ? "N/A" : `${Math.round(row.collectionsPct * 100)}%`}</td>
                </tr>
              `).join("") || '<tr><td colspan="14">No monthly payer pivot rows for this filter.</td></tr>'}
            </tbody>
            <tfoot>
              <tr>
                <th colspan="2">Visible Total</th>
                <td class="num">${totals.lines}</td>
                <td class="num">${formatDecimal(totals.units, 2)}</td>
                <td class="num" colspan="2"></td>
                <td class="num">${money(totals.totalExpected)}</td>
                <td class="num"></td>
                <td class="num">${money(totals.patientBalance)}</td>
                <td class="num">${money(totals.patientPaid)}</td>
                <td class="num">${money(totals.expectedInsurance)}</td>
                <td class="num">${money(totals.insurancePaid)}</td>
                <td class="num">${money(totals.payerBalance)}</td>
                <td class="num">${collectionRate == null ? "N/A" : `${Math.round(collectionRate * 100)}%`}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </details>
    </section>
  `;
}

function monthlyPayerSummaryRows(serviceRows) {
  const buckets = new Map();
  serviceRows.forEach(({ claim, service }) => {
    const month = service.dos ? service.dos.slice(0, 7) : "No DOS";
    const payer = claim.primaryPayer || "Unknown";
    const key = `${month}||${payer}`;
    const financial = serviceFinancialView(service, claim);
    const writeOff = moneyNumber(service.writeOffAmount || service.writeOff || claim.writeOffAmount || 0);
    const patientBalance = moneyNumber(Math.max(financial.patientResponsibility || 0, financial.patientBalance || 0, truePatientResponsibility(service) || 0));
    const patientPaid = moneyNumber(service.totalPatAmtPaid || service.patientPaid || service.patientAmountPaid || 0);
    const expectedInsurance = moneyNumber(Math.max(financial.expectedReimbursement - writeOff - patientBalance - patientPaid, 0));
    const current = buckets.get(key) || {
      month,
      payer,
      lines: 0,
      units: 0,
      primaryExpected: 0,
      secondaryExpected: 0,
      totalExpected: 0,
      writeOff: 0,
      patientBalance: 0,
      patientPaid: 0,
      expectedInsurance: 0,
      insurancePaid: 0,
      payerBalance: 0,
      collectionsPct: null
    };
    current.lines += 1;
    current.units += quantityNumber(service.units);
    current.primaryExpected += moneyNumber(service.primaryExpectedTotal ?? service.primaryExpected ?? financial.primaryExpected ?? financial.expectedReimbursement);
    current.secondaryExpected += moneyNumber(service.secondaryExpectedTotal ?? service.secondaryExpected ?? financial.secondaryExpected ?? 0);
    current.totalExpected += financial.expectedReimbursement;
    current.writeOff += writeOff;
    current.patientBalance += patientBalance;
    current.patientPaid += patientPaid;
    current.expectedInsurance += expectedInsurance;
    current.insurancePaid += financial.insurancePaid;
    current.payerBalance += moneyNumber(Math.max(expectedInsurance - financial.insurancePaid, 0));
    buckets.set(key, current);
  });
  return Array.from(buckets.values())
    .map((row) => {
      Object.keys(row).forEach((key) => {
        if (typeof row[key] === "number") row[key] = moneyNumber(row[key]);
      });
      row.collectionsPct = row.expectedInsurance > 0 ? row.insurancePaid / row.expectedInsurance : null;
      return row;
    })
    .sort((a, b) => b.month.localeCompare(a.month) || b.payerBalance - a.payerBalance || a.payer.localeCompare(b.payer));
}

function monthlyPayerTrendRows(rows) {
  const grouped = new Map();
  rows.forEach((row) => {
    const current = grouped.get(row.month) || { month: row.month, expectedInsurance: 0, insurancePaid: 0, payerBalance: 0 };
    current.expectedInsurance += row.expectedInsurance;
    current.insurancePaid += row.insurancePaid;
    current.payerBalance += row.payerBalance;
    grouped.set(row.month, current);
  });
  return Array.from(grouped.values())
    .map((row) => ({
      month: row.month,
      expectedInsurance: moneyNumber(row.expectedInsurance),
      insurancePaid: moneyNumber(row.insurancePaid),
      payerBalance: moneyNumber(row.payerBalance)
    }))
    .sort((a, b) => a.month.localeCompare(b.month));
}

function payerConcentrationRows(rows) {
  const grouped = new Map();
  rows.forEach((row) => {
    const current = grouped.get(row.payer) || { payer: row.payer, lines: 0, payerBalance: 0 };
    current.lines += row.lines;
    current.payerBalance += row.payerBalance;
    grouped.set(row.payer, current);
  });
  return Array.from(grouped.values())
    .map((row) => ({ payer: row.payer, lines: row.lines, payerBalance: moneyNumber(row.payerBalance) }))
    .sort((a, b) => b.payerBalance - a.payerBalance || b.lines - a.lines);
}

function monthlyPayerWatchlistRows(rows) {
  return (rows || [])
    .map((row) => {
      const formulaDelta = moneyNumber(row.expectedInsurance - row.insurancePaid - row.payerBalance);
      const collectionPct = row.expectedInsurance > 0 ? row.insurancePaid / row.expectedInsurance : null;
      const balanceRate = row.expectedInsurance > 0 ? row.payerBalance / row.expectedInsurance : null;
      let reason = "Monitor";
      let action = "Keep in normal monthly review.";
      let severity = 0;
      if (formulaDelta !== 0) {
        reason = "Formula Review";
        action = "Reconcile expected, paid, and payer balance before reporting.";
        severity = 100 + Math.abs(formulaDelta);
      } else if (row.payerBalance > 0 && row.insurancePaid <= 0) {
        reason = "No Cash Posted";
        action = "Confirm payer response, denial, or missing ERA/payment posting.";
        severity = 80 + row.payerBalance;
      } else if (balanceRate != null && balanceRate >= 0.5) {
        reason = "High AR Balance";
        action = "Prioritize payer follow-up for this month/payer block.";
        severity = 60 + row.payerBalance;
      } else if (collectionPct != null && collectionPct < 0.75 && row.payerBalance > 0) {
        reason = "Collections Lag";
        action = "Review underpayment and aging before client meeting.";
        severity = 40 + row.payerBalance;
      }
      return {
        ...row,
        formulaDelta,
        collectionPct,
        balanceRate,
        reason,
        action,
        severity
      };
    })
    .filter((row) => row.severity > 0)
    .sort((a, b) => b.severity - a.severity || b.payerBalance - a.payerBalance || b.expectedInsurance - a.expectedInsurance)
    .slice(0, 8);
}

function renderClientMonthlyPayerWatchlist(rows) {
  const watchRows = monthlyPayerWatchlistRows(rows);
  const totalBalance = moneyNumber(watchRows.reduce((sum, row) => sum + row.payerBalance, 0));
  const totalDelta = moneyNumber(watchRows.reduce((sum, row) => sum + row.formulaDelta, 0));
  return `
    <section class="client-monthly-watchlist">
      <div class="client-monthly-chart-head">
        <strong>Monthly Payer Watchlist</strong>
        <span>Client meeting view · highest-risk payer/month intersections from the pivot</span>
      </div>
      <div class="client-monthly-watchlist-summary">
        <span><small>Watch Rows</small><strong>${watchRows.length}</strong><em>Filtered payer/month blocks</em></span>
        <span><small>Watchlist AR</small><strong>${money(totalBalance)}</strong><em>Open payer balance</em></span>
        <span><small>Formula Delta</small><strong>${money(totalDelta)}</strong><em>Expected Ins. - Paid - Balance</em></span>
      </div>
      <div class="client-monthly-watchlist-table-wrap">
        <table class="client-monthly-watchlist-table">
          <thead>
            <tr>
              <th>Month / Payer</th>
              <th>Reason</th>
              <th class="num">Expected Ins.</th>
              <th class="num">Paid</th>
              <th class="num">Payer Balance</th>
              <th class="num">Collections</th>
              <th class="num">Delta</th>
              <th>Next Action</th>
            </tr>
          </thead>
          <tbody>
            ${watchRows.map((row) => `
              <tr>
                <td data-label="Month / Payer"><strong>${escapeHtml(row.month)}</strong><span>${escapeHtml(row.payer)} · ${formatNounCount(row.lines, "line")}</span></td>
                <td data-label="Reason">${pill(row.reason, row.formulaDelta !== 0 ? "amber" : row.insurancePaid <= 0 ? "red" : "blue")}</td>
                <td data-label="Expected Ins." class="num">${money(row.expectedInsurance)}</td>
                <td data-label="Paid" class="num">${money(row.insurancePaid)}</td>
                <td data-label="Payer Balance" class="num">${money(row.payerBalance)}</td>
                <td data-label="Collections" class="num">${row.collectionPct == null ? "N/A" : `${Math.round(row.collectionPct * 100)}%`}</td>
                <td data-label="Delta" class="num">${money(row.formulaDelta)}</td>
                <td data-label="Next Action">${escapeHtml(row.action)}</td>
              </tr>
            `).join("") || '<tr><td colspan="8">No payer/month watchlist rows in the active slicers.</td></tr>'}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function renderClientPayerMonthMatrix(rows) {
  const months = Array.from(new Set(rows.map((row) => row.month)))
    .sort((a, b) => b.localeCompare(a))
    .slice(0, 6)
    .reverse();
  const payers = payerConcentrationRows(rows)
    .slice(0, 7)
    .map((row) => row.payer);
  if (!months.length || !payers.length) {
    return `
      <div class="client-monthly-matrix">
        <div class="client-monthly-chart-head">
          <strong>Payer x Month Matrix</strong>
          <span>Power BI matrix visual</span>
        </div>
        <div class="chart-empty">No payer-by-month matrix rows for this filter.</div>
      </div>
    `;
  }
  const byKey = new Map(rows.map((row) => [`${row.payer}||${row.month}`, row]));
  const maxBalance = Math.max(...rows.map((row) => row.payerBalance), 1);
  const payerTotals = payers.map((payer) => rows
    .filter((row) => row.payer === payer)
    .reduce((sum, row) => {
      sum.lines += row.lines;
      sum.expectedInsurance += row.expectedInsurance;
      sum.insurancePaid += row.insurancePaid;
      sum.payerBalance += row.payerBalance;
      return sum;
    }, { payer, lines: 0, expectedInsurance: 0, insurancePaid: 0, payerBalance: 0 }));
  const monthTotals = months.map((month) => rows
    .filter((row) => row.month === month && payers.includes(row.payer))
    .reduce((sum, row) => {
      sum.lines += row.lines;
      sum.payerBalance += row.payerBalance;
      return sum;
    }, { month, lines: 0, payerBalance: 0 }));
  return `
    <div class="client-monthly-matrix">
      <div class="client-monthly-chart-head">
        <strong>Payer x Month Matrix</strong>
        <span>Power BI matrix visual · conditional payer balance</span>
      </div>
      <div class="client-monthly-matrix-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Payer</th>
              ${months.map((month) => `<th scope="col">${escapeHtml(month)}</th>`).join("")}
              <th scope="col">Total</th>
              <th scope="col">Collections %</th>
            </tr>
          </thead>
          <tbody>
            ${payerTotals.map((payerTotal) => `
              <tr>
                <th scope="row">
                  <strong>${escapeHtml(payerTotal.payer)}</strong>
                  <span>${formatNounCount(payerTotal.lines, "line")} · ${money(payerTotal.expectedInsurance)} expected</span>
                </th>
                ${months.map((month) => {
                  const row = byKey.get(`${payerTotal.payer}||${month}`) || { lines: 0, expectedInsurance: 0, insurancePaid: 0, payerBalance: 0, collectionsPct: null };
                  const heat = row.payerBalance > 0 ? Math.max(0.08, row.payerBalance / maxBalance) : 0;
                  return `
                    <td style="--heat:${heat.toFixed(3)}" title="${escapeHtml(`${payerTotal.payer} / ${month}: ${money(row.payerBalance)} payer balance, ${formatNounCount(row.lines, "line")}`)}">
                      <strong>${money(row.payerBalance)}</strong>
                      <span>${row.collectionsPct == null ? "N/A" : `${Math.round(row.collectionsPct * 100)}%`} coll.</span>
                    </td>
                  `;
                }).join("")}
                <td>
                  <strong>${money(payerTotal.payerBalance)}</strong>
                  <span>${formatNounCount(payerTotal.lines, "line")}</span>
                </td>
                <td>
                  <strong>${payerTotal.expectedInsurance > 0 ? `${Math.round((payerTotal.insurancePaid / payerTotal.expectedInsurance) * 100)}%` : "N/A"}</strong>
                  <span>${money(payerTotal.insurancePaid)} paid</span>
                </td>
              </tr>
            `).join("")}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Month Total</th>
              ${monthTotals.map((row) => `<td><strong>${money(row.payerBalance)}</strong><span>${formatNounCount(row.lines, "line")}</span></td>`).join("")}
              <td><strong>${money(payerTotals.reduce((sum, row) => sum + row.payerBalance, 0))}</strong><span>${formatNounCount(payerTotals.reduce((sum, row) => sum + row.lines, 0), "line")}</span></td>
              <td><strong>Matrix</strong><span>Filtered top payers</span></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  `;
}

// Bare decimal (no currency symbol) for units and hours. It used to call
// toLocaleString(undefined, ...), so on a non-US machine it rendered
// "1.234,56" beside "$1,234.56" in the same table. Same core, same locale.
function formatDecimal(value, places = 2) {
  return formatMoneyValue(value, { decimals: places, currency: false });
}

function groupedClientChartRows(claims, labelForClaim) {
  const grouped = new Map();
  claims.forEach((claim) => {
    const label = labelForClaim(claim);
    const current = grouped.get(label) || { label, claims: 0, value: 0 };
    current.claims += 1;
    current.value += claimFinancialView(claim).sourceOutstanding;
    grouped.set(label, current);
  });
  return Array.from(grouped.values()).sort((a, b) => b.value - a.value || b.claims - a.claims).slice(0, 8);
}

function renderClientPivotHeatmap(claims, totals) {
  const model = clientStateInsuranceHeatmapModel(claims, totals);
  const { rowLabels, columnLabels } = model;
  if (!rowLabels.length || !columnLabels.length) {
    return `
      <section class="client-pivot-heatmap">
        <div class="client-chart-title"><strong>State x Insurance Pivot</strong><span>Excel-style slicer view</span></div>
        <div class="chart-empty">No pivot rows for this filter.</div>
      </section>
    `;
  }
  const { cells, maxOutstanding, columnTotals } = model;
  const body = rowLabels.map((stateLabel) => {
    const rowClaims = claims.filter((claim) => (clientStateValue(claim) || "Unknown") === stateLabel);
    const rowOutstanding = rowClaims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
    return `
      <tr>
        <th scope="row">
          <strong>${escapeHtml(stateLabel)}</strong>
          <span>${formatNounCount(rowClaims.length, "claim")} · ${money(rowOutstanding)}</span>
        </th>
        ${columnLabels.map((payerLabel) => {
          const cell = cells.get(`${stateLabel}||${payerLabel}`) || { claims: 0, expected: 0, paid: 0, outstanding: 0 };
          const intensity = cell.outstanding > 0 ? Math.max(0.08, cell.outstanding / maxOutstanding) : 0;
          const delta = moneyNumber(cell.expected - cell.paid - cell.outstanding);
          return `
            <td style="--heat:${intensity.toFixed(3)}" title="${escapeHtml(`${stateLabel} / ${payerLabel}: ${formatNounCount(cell.claims, "claim")}, ${money(cell.outstanding)} outstanding`)}">
              <strong>${money(cell.outstanding)}</strong>
              <span>${cell.claims} clm · Δ ${money(delta)}</span>
            </td>
          `;
        }).join("")}
      </tr>
    `;
  }).join("");
  return `
    <section class="client-pivot-heatmap">
      <div class="client-chart-title">
        <strong>State x Insurance Pivot</strong>
        <span>Excel-style slicer view</span>
      </div>
      <div class="client-pivot-summary">
        <span><small>Rows</small><strong>${formatNounCount(rowLabels.length, "state")}</strong></span>
        <span><small>Columns</small><strong>${formatNounCount(columnLabels.length, "payer")}</strong></span>
        <span><small>Expected</small><strong>${money(totals.totalExpected)}</strong></span>
        <span><small>Paid</small><strong>${money(totals.totalPaid)}</strong></span>
        <span><small>Outstanding</small><strong>${money(totals.totalOwed)}</strong></span>
      </div>
      ${renderClientStateInsuranceHeatmapVisual(model)}
      <div class="client-pivot-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">State</th>
              ${columnLabels.map((label) => `<th scope="col" title="${escapeHtml(label)}">${escapeHtml(label)}</th>`).join("")}
            </tr>
          </thead>
          <tbody>${body}</tbody>
          <tfoot>
            <tr>
              <th scope="row">Column Total</th>
              ${columnTotals.map((row) => `<td><strong>${money(row.outstanding)}</strong><span>${formatNounCount(row.claims, "claim")}</span></td>`).join("")}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  `;
}

function clientStateInsuranceHeatmapModel(claims = [], totals = {}) {
  const rowLabels = Array.from(new Set(claims.map((claim) => clientStateValue(claim) || "Unknown")))
    .sort((a, b) => String(a).localeCompare(String(b)))
    .slice(0, 8);
  const columnLabels = groupedClientChartRows(claims, (claim) => claim.primaryPayer || "Unknown")
    .map((row) => row.label)
    .slice(0, 6);
  const cells = new Map();
  claims.forEach((claim) => {
    const stateLabel = clientStateValue(claim) || "Unknown";
    const payerLabel = claim.primaryPayer || "Unknown";
    if (!rowLabels.includes(stateLabel) || !columnLabels.includes(payerLabel)) return;
    const key = `${stateLabel}||${payerLabel}`;
    const current = cells.get(key) || { state: stateLabel, payer: payerLabel, claims: 0, expected: 0, paid: 0, outstanding: 0 };
    const financial = claimFinancialView(claim);
    current.claims += 1;
    current.expected += financial.expectedReimbursement;
    current.paid += financial.totalInsurancePaid;
    current.outstanding += financial.sourceOutstanding;
    cells.set(key, current);
  });
  cells.forEach((cell) => {
    cell.expected = moneyNumber(cell.expected);
    cell.paid = moneyNumber(cell.paid);
    cell.outstanding = moneyNumber(cell.outstanding);
    cell.formulaDelta = moneyNumber(cell.expected - cell.paid - cell.outstanding);
    cell.collectionPct = cell.expected > 0 ? cell.paid / cell.expected : null;
  });
  const maxOutstanding = Math.max(...Array.from(cells.values()).map((cell) => cell.outstanding), 1);
  const columnTotals = columnLabels.map((label) => {
    const columnClaims = claims.filter((claim) => (claim.primaryPayer || "Unknown") === label);
    const expected = columnClaims.reduce((sum, claim) => sum + claimFinancialView(claim).expectedReimbursement, 0);
    const paid = columnClaims.reduce((sum, claim) => sum + claimFinancialView(claim).totalInsurancePaid, 0);
    const outstanding = columnClaims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
    return {
      label,
      claims: columnClaims.length,
      expected: moneyNumber(expected),
      paid: moneyNumber(paid),
      outstanding: moneyNumber(outstanding),
      formulaDelta: moneyNumber(expected - paid - outstanding)
    };
  });
  const rowTotals = rowLabels.map((label) => {
    const rowClaims = claims.filter((claim) => (clientStateValue(claim) || "Unknown") === label);
    const expected = rowClaims.reduce((sum, claim) => sum + claimFinancialView(claim).expectedReimbursement, 0);
    const paid = rowClaims.reduce((sum, claim) => sum + claimFinancialView(claim).totalInsurancePaid, 0);
    const outstanding = rowClaims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
    return {
      label,
      claims: rowClaims.length,
      expected: moneyNumber(expected),
      paid: moneyNumber(paid),
      outstanding: moneyNumber(outstanding),
      formulaDelta: moneyNumber(expected - paid - outstanding)
    };
  });
  const hotCells = Array.from(cells.values())
    .sort((a, b) => b.outstanding - a.outstanding || b.claims - a.claims || a.state.localeCompare(b.state))
    .slice(0, 4);
  return {
    rowLabels,
    columnLabels,
    cells,
    maxOutstanding,
    columnTotals,
    rowTotals,
    hotCells,
    totals: {
      expected: moneyNumber(totals.totalExpected || 0),
      paid: moneyNumber(totals.totalPaid || 0),
      outstanding: moneyNumber(totals.totalOwed || 0),
      delta: moneyDelta(totals.totalExpected, totals.totalPaid, totals.totalOwed)
    }
  };
}

function renderClientStateInsuranceHeatmapVisual(model = {}) {
  const rowLabels = model.rowLabels || [];
  const columnLabels = model.columnLabels || [];
  if (!rowLabels.length || !columnLabels.length) return "";
  const cellW = 104;
  const cellH = 54;
  const left = 102;
  const top = 42;
  const width = left + columnLabels.length * cellW + 20;
  const height = top + rowLabels.length * cellH + 58;
  const cells = model.cells || new Map();
  const maxOutstanding = model.maxOutstanding || 1;
  const rects = [];
  columnLabels.forEach((payer, colIndex) => {
    const x = left + colIndex * cellW;
    rects.push(`<text x="${x + cellW / 2}" y="22" text-anchor="middle" class="client-heatmap-axis">${escapeHtml(truncateChartLabel(payer, 12))}</text>`);
  });
  rowLabels.forEach((stateLabel, rowIndex) => {
    const y = top + rowIndex * cellH;
    rects.push(`<text x="${left - 12}" y="${y + 31}" text-anchor="end" class="client-heatmap-axis">${escapeHtml(stateLabel)}</text>`);
    columnLabels.forEach((payer, colIndex) => {
      const x = left + colIndex * cellW;
      const cell = cells.get(`${stateLabel}||${payer}`) || { claims: 0, expected: 0, paid: 0, outstanding: 0, formulaDelta: 0 };
      const heat = cell.outstanding > 0 ? Math.max(0.1, cell.outstanding / maxOutstanding) : 0;
      const fill = heat ? `rgba(15, 95, 191, ${Math.min(0.9, 0.12 + heat * 0.68).toFixed(3)})` : "#f8fbff";
      rects.push(`
        <g>
          <rect x="${x}" y="${y}" width="${cellW - 8}" height="${cellH - 8}" rx="6" fill="${fill}" stroke="#dbe7f3"></rect>
          <text x="${x + 9}" y="${y + 20}" class="client-heatmap-money">${escapeHtml(money(cell.outstanding))}</text>
          <text x="${x + 9}" y="${y + 38}" class="client-heatmap-sub">${cell.claims} clm · Δ ${escapeHtml(money(cell.formulaDelta))}</text>
          <title>${escapeHtml(`${stateLabel} / ${payer}: ${formatNounCount(cell.claims, "claim")}, ${money(cell.expected)} expected, ${money(cell.paid)} paid, ${money(cell.outstanding)} open AR`)}</title>
        </g>
      `);
    });
  });
  const hotCells = model.hotCells || [];
  return `
    <div class="client-state-insurance-visual">
      <div class="client-state-insurance-head">
        <div>
          <strong>State x Insurance Exposure Map</strong>
          <span>Visual heatmap from the active Excel slicers</span>
        </div>
        <b>${money(model.totals?.delta || 0)} formula delta</b>
      </div>
      <div class="client-state-insurance-svg-wrap">
        <svg class="client-state-insurance-svg chart-svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="State by insurance exposure heatmap">
          <rect x="0" y="0" width="${width}" height="${height}" rx="10" fill="#ffffff"></rect>
          ${rects.join("")}
          <text x="${left}" y="${height - 22}" class="client-heatmap-sub">Darker cells have higher open AR. Each cell displays open AR, claim count, and expected-paid-AR delta.</text>
        </svg>
      </div>
      <div class="client-state-insurance-hotlist">
        ${hotCells.length ? hotCells.map((cell) => `
          <span>
            <small>${escapeHtml(cell.state)} / ${escapeHtml(cell.payer)}</small>
            <strong>${money(cell.outstanding)}</strong>
            <em>${formatNounCount(cell.claims, "claim")} · ${cell.collectionPct == null ? "N/A" : `${Math.round(cell.collectionPct * 100)}%`} collected · Delta ${money(cell.formulaDelta)}</em>
          </span>
        `).join("") : '<span><small>No exposure</small><strong>$0</strong><em>No state/insurance hot cells in this filter.</em></span>'}
      </div>
    </div>
  `;
}

function renderClientBarChart(title, rows, hint) {
  const max = Math.max(...rows.map((row) => row.value), 1);
  const body = rows.length ? rows.map((row) => {
    const width = Math.max(4, Math.round((row.value / max) * 100));
    return `
      <div class="client-chart-row">
        <div class="client-chart-label"><strong>${escapeHtml(row.label)}</strong><span>${formatNounCount(row.claims, "claim")}</span></div>
        <div class="client-chart-track"><i style="width: ${width}%"></i></div>
        <b>${money(row.value)}</b>
      </div>
    `;
  }).join("") : `<div class="chart-empty">No chart rows for this filter.</div>`;
  const table = rows.length ? `
    <details class="client-chart-table">
      <summary>Table</summary>
      <table>
        <thead><tr><th>${escapeHtml(title)}</th><th>Claims</th><th>Amount</th></tr></thead>
        <tbody>
          ${rows.map((row) => `
            <tr>
              <td>${escapeHtml(row.label)}</td>
              <td class="num">${row.claims}</td>
              <td class="num">${money(row.value)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </details>
  ` : "";
  return `
    <article class="client-chart-card">
      <div class="client-chart-title"><strong>${escapeHtml(title)}</strong><span>${escapeHtml(hint)}</span></div>
      ${body}
      ${table}
    </article>
  `;
}

// ===========================================================================
// REPORTS — AR Aging revamp.
// Audience: internal RCM team + owners. Job: see the money. Default date
// basis: Date of Service (slicers). Headline: Net Collection Rate. Primary
// report: AR Aging — insurance AR (Expected - Paid) aged from claim
// submission date into 0-30 / 31-60 / 61-90 / 91-120 / 120+ buckets, shown as
// a payer/client/provider matrix over a sortable, exportable detail table.
// Output: on-screen, CSV (opens in Excel), and Print / Save-as-PDF.
// ===========================================================================
// AR Aging bands = the canonical CT bands (see CT_AR_AGING_BUCKETS near the top of this
// file). The report used to keep its own copy whose last band was keyed "120+" while the
// grid/warehouse used yet other labels — parity audit 2026-07-22, finding 2-C.
const AR_AGING_BUCKETS = CT_AR_AGING_BUCKETS;

// AR Detail renders a WINDOW, not the whole book. The unwindowed table put one
// <tr> per claim into a single innerHTML assignment — 22,165 rows on the live
// all-orgs dataset, measured at ~2.0s of parse+layout in headless Chromium, paid
// on every entry to the Reports tab AND again on every sort click, because
// sorting re-runs renderReports() over the full array.
//
// This is a DISPLAY window and nothing else. The KPI totals, the aging matrix and
// the Detail CSV export are all computed from the full, unwindowed rows array
// (arReportTotals / arBuildMatrix / arExportCsv) and are deliberately untouched —
// a cap that reached any of those would be the row-cap defect this codebase just
// spent a day removing from the report rollups. The panel header states the full
// claim count next to the visible range so a windowed view can never be misread
// as a complete one.
const AR_DETAIL_PAGE_SIZE = 250;
const AR_DETAIL_COLUMNS = [
  { key: "claimId", label: "Claim ID", type: "text" },
  { key: "tenant", label: "Client", type: "text" },
  { key: "payer", label: "Payer", type: "text" },
  { key: "provider", label: "Provider", type: "text" },
  { key: "status", label: "Status", type: "text" },
  // "Network" and "Bill Date" were REMOVED here on 2026-08-10, and the reason is
  // the same for both: neither column has a source, so both rendered "--" on
  // every row of every report, forever. Measured live: 0 of 60,000 rows non-empty.
  //   network         — no such column exists on the mirror, on gold, or in the
  //                     dataset SELECT. The only "network" in the schema is a
  //                     RATE DIMENSION on portal.fee_schedule_rates, which is a
  //                     property of a contract, not of a claim
  //                     (trackerColumnSourceMap.js already recommended removal).
  //   billDate        — gold carries [Bill Date Aging] (a DAY COUNT) but no bill
  //                     DATE, so original_bill_date is CAST(NULL AS date). Aging
  //                     itself is unaffected: arSubmissionAgingDays reads
  //                     billDateAging first and only falls back to this.
  // An always-empty column is not neutral. It occupies the width a real column
  // would have and it teaches the reader that the data is missing rather than
  // that the column is. Sorting, the CSV export and the colspan all derive from
  // this array, so removing the entries removes the column everywhere at once.
  { key: "agingDays", label: "Aging (days)", type: "num" },
  { key: "bucketLabel", label: "Bucket", type: "text" },
  { key: "charges", label: "Charges", type: "money" },
  { key: "expected", label: "Expected", type: "money" },
  { key: "paid", label: "Paid", type: "money" },
  { key: "writeOff", label: "Write-off", type: "money" },
  { key: "balance", label: "Balance", type: "money" },
  { key: "insuranceAr", label: "Insurance AR", type: "money" }
];

const reportArState = { dimension: "payer", sortKey: "insuranceAr", sortDir: "desc", view: "ar", detailPage: 1 };
let reportCollectionsState = { key: "", status: "idle", response: null, error: "", code: "" };
let reportTurnaroundState = { key: "", status: "idle", response: null, error: "", code: "", httpStatus: 0 };
let reportTurnaroundSortState = { sortKey: "averageDays", sortDir: "desc" };
let reportRateIssueSortState = { sortKey: "insurancePaid", sortDir: "desc" };
let reportPayerClassSortState = { sortKey: "openAr", sortDir: "desc" };
let reportReviewQueueSortState = { sortKey: "openAr", sortDir: "desc" };
const reportCollectionsSortState = { sortKey: "month", sortDir: "desc" };
// Collections on the portal should feel like the Excel tracker: a sheet you
// can slice, plus a Power BI-style chart of the same money. presentation
// is excel|charts. slicers.payer / slicers.client are the "who is coming
// in" chips, applied to the already-loaded payment-month rows.
const reportCollectionsUiState = { presentation: "excel", slicers: { payer: [], client: [] } };
const reportEvpState = { dimension: "payer", sortKey: "outstanding", sortDir: "desc", detailPage: 1 };
const reportDenialsState = { dimension: "payer", sortKey: "atRisk", sortDir: "desc", detailPage: 1 };
const reportPerformanceState = { includeYoungClaims: false };
// Client Action carries no per-view toggles — held claims of any age matter,
// so there is no <60-day lens to mirror from Performance. detailPage is the by-claim table's
// display-window index: that table has no dimension or sort control, so a page number is the
// only display state it needs, and it uses the same page size as every other detail table.
const reportClientActionState = { detailPage: 1 };
const reportBillDateAgingState = { sortKey: "bucket", sortDir: "asc", granularity: "weekly" };
let reportRowDrilldownState = { active: false, view: "", label: "", dims: [], predicate: null, claimIds: null, footerOverride: null, claimId: null, profile: "biller", detailPage: 1 };

// Live tracker-API mode: reports read ONE principal-scoped dataset from the
// mirror (POST /api/reports/dataset) instead of getClaims(), which is
// intentionally empty while live paging is on. Only the DOS range is
// server-side; every other report filter stays client-side over the cached
// mapped claims. The cache key folds in base/role/user so a login switch
// refetches instead of leaking the previous principal’s rows.
const REPORT_DATASET_TIMEOUT_MS = 90000;
let reportDatasetState = { status: "idle", key: null, rows: null, claims: null, capped: false, cap: 0, rowCount: 0, error: null, fetchedAt: null, pendingSince: 0 };

function reportDatasetKey() {
  const filters = reportFilterValues();
  return JSON.stringify({
    publicationEpoch: reportReadPublicationIdentity(),
    base: trackerApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: reportAccessCacheEnvelope(),
    fromDos: filters.fromDate || "",
    toDos: filters.toDate || "",
    // The six control filters are part of the KEY because they are now part of
    // the QUERY (reportDatasetServerFilters). While they were client-side only,
    // leaving them out was correct  the same rows served every filter. Now a
    // filter change is a different population, so a stale key would serve one
    // payer's rows under another payer's selection.
    filters: reportDatasetServerFilters()
  });
}

// TENANT / PAYER / PROVIDER / LOCATION / AGING / CLAIM STATUS GO TO SQL.
//
// These were honoured only in getReportClaims(), over an array the server had
// already truncated at 60,000 rows  so every filtered total was a slice of a
// slice, with no per-filter warning. Sending them means the server queries THAT
// population, and because a filtered book is usually far under the cap, most
// filtered pulls now come back complete rather than capped.
//
// getReportClaims still applies them client-side and that is deliberate: the
// second pass is idempotent over an already-filtered set, and it keeps local /
// demo mode (which has no server) behaving exactly as before.
//
// `network` IS sent now (2026-08-21). It has no source column anywhere, but the
// T1a snapshot build derives it from the primary fee-schedule contract that priced
// each line and stores it, so the server can filter on it after all. Against a
// NON-snapshot source the column does not exist; reportDatasetSource answers that
// case honestly rather than dropping the predicate (see the gate there).
// THE WIRE SHAPE IS THE BACKEND'S NORMAL FORM, AND A SINGLE VALUE IS STILL A
// BARE STRING. backend/reportFilterSelection.js normalizeReportFilterValue()
// answers the same way, so one selected payer sends the identical body, hits
// the identical cache key and compiles the identical SQL it did before
// multi-select existed - which is what keeps a one-value report's numbers
// unchanged. A list or an exclusion sends { values, mode }.
function reportDatasetServerFilters() {
  const filters = reportFilterValues();
  const body = {};
  const tenant = String(filters.tenant || "").trim();
  if (tenant && tenant !== "All") body.tenant = tenant;
  const modes = filters.modes || {};
  REPORT_FILTER_MULTI_KEYS.forEach((key) => {
    const values = Array.isArray(filters.selections?.[key]) ? filters.selections[key] : [];
    if (!values.length) return;
    const exclude = filterModeExcludes(modes, key);
    body[key] = values.length === 1 && !exclude
      ? values[0]
      : { values: values.slice(), mode: exclude ? FILTER_MODE_EXCLUDE : FILTER_MODE_INCLUDE };
  });
  return body;
}

function reportDatasetRequestBody() {
  const filters = reportFilterValues();
  const body = {};
  if (filters.fromDate) body.fromDos = filters.fromDate;
  if (filters.toDate) body.toDos = filters.toDate;
  const serverFilters = reportDatasetServerFilters();
  if (Object.keys(serverFilters).length) body.filters = serverFilters;
  return body;
}

function resetReportDataset() {
  // Facets are per-principal, per-source state; a login switch or a source
  // switch must not inherit the previous principal's facet values.
  reportFilterFacets = {};
  reportDatasetState = { status: "idle", key: null, rows: null, claims: null, capped: false, cap: 0, rowCount: 0, error: null, fetchedAt: null, pendingSince: 0 };
}

function reportDatasetTimedOut(key = reportDatasetKey()) {
  if (reportDatasetState.status !== "pending" || reportDatasetState.key !== key) return false;
  const pendingSince = Number(reportDatasetState.pendingSince) || 0;
  return !pendingSince || Date.now() - pendingSince > REPORT_DATASET_TIMEOUT_MS;
}

function failReportDatasetPending(key, message) {
  if (reportDatasetState.status !== "pending" || reportDatasetState.key !== key || reportDatasetKey() !== key) return false;
  reportDatasetState = { status: "error", key, rows: null, claims: null, capped: false, cap: 0, rowCount: 0, error: message, fetchedAt: null, pendingSince: 0 };
  if (state.view === "reports") renderReports();
  else if (state.view === "clientHome") renderClientHome();
  return true;
}

function requestReportDataset(key = reportDatasetKey(), body = reportDatasetRequestBody()) {
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") return;
  const requestStillCurrent = () => (
    reportDatasetState.key === key
    && reportDatasetKey() === key
  );
  reportDatasetState = { status: "pending", key, rows: null, claims: null, capped: false, cap: 0, rowCount: 0, error: null, fetchedAt: null, pendingSince: Date.now() };
  const token = trackerApiToken();
  const controller = new AbortController();
  const timeoutMessage = "Report data did not finish loading in time. Retry, or narrow the date range.";
  const timeoutId = window.setTimeout(() => {
    failReportDatasetPending(key, timeoutMessage);
    try { controller.abort(); } catch (_) {}
  }, REPORT_DATASET_TIMEOUT_MS);
  reportSessionFetch((typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(`${base}/api/reports/dataset`) : `${base}/api/reports/dataset`), {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body),
    signal: controller.signal
  })
    .then(async (response) => {
      window.clearTimeout(timeoutId);
      const payload = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (!response.ok || payload.status === "error" || payload.error) {
        if (response.status === 401) {
          if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
          reportDatasetState = {
            status: "error",
            key,
            rows: null,
            claims: null,
            capped: false,
            cap: 0,
            rowCount: 0,
            error: "Your session expired. Sign in again — this page will still be here.",
            fetchedAt: null,
            pendingSince: 0
          };
        } else {
          const errorCode = typeof payload.error === "string" ? payload.error : String(payload?.error?.code || "");
          let message = payload?.error?.message || payload.message || `HTTP ${response.status}`;
          if (errorCode === "dataset_cache_not_ready") {
            message = "Report data is still being prepared. Click Retry when you are ready.";
          } else if (errorCode === "dataset_too_large") {
            // THE SERVER COUNTED. IT KNOWS THE NUMBER; THIS SENTENCE DID NOT.
            //
            // The refusal now carries the actual row count, the limit and a remedy
            // ("105,463 service lines, above the 75,000 a single dataset response can
            // carry"). Substituting generic copy threw all of that away and left the
            // reader guessing how much to narrow by. Prefer the server's sentence and
            // keep this one only as the fallback for an older API.
            message = typeof payload.message === "string" && payload.message.trim()
              ? payload.message.trim()
              : "This range returns too many rows to load — narrow the Reports date filters (From/To date of service) and retry.";
          }
          reportDatasetState = { status: "error", key, rows: null, claims: null, capped: false, cap: 0, rowCount: 0, error: message, fetchedAt: null, pendingSince: 0 };
        }
      } else {
        const rows = Array.isArray(payload.rows) ? payload.rows : [];
        // Server rows arrive principal-scoped (deny-by-default). Mirror the
        // sheet’s visibleClaimsFromPage: map once, do NOT re-filter through
        // canAccessClaim — mapped claims may lack orgId and org-scoped users
        // would zero out.
        reportDatasetState = {
          status: "ready",
          key,
          rows,
          // Dataset rows are service-line grain: merge lines per claim.
          // claimsFromTrackerRows keeps only the FIRST line per claim, so
          // claimFinancialView would understate charges/expected against the
          // claim-level paid figure (fake over-collection in Expected vs Paid).
          claims: claimsFromServiceGrainRows(rows),
          capped: Boolean(payload.capped),
          // WHICH totals the cap understates, MEASURED server-side rather than
          // guessed here: the row query is owed-first, so a capped pull still
          // carries every open balance. An older API that does not send the
          // field is read as "complete"  we do not invent a warning, and
          // `capped` alone still drives the partial marking below.
          openArComplete: payload.openArComplete !== false,
          cap: Number(payload.cap) || 0,
          rowCount: Number(payload.rowCount) || rows.length,
          error: null,
          fetchedAt: payload.generatedAt || timestamp(),
          pendingSince: 0
        };
      }
      if (state.view === "reports") renderReports();
      else if (state.view === "clientHome") renderClientHome();
    })
    .catch((error) => {
      window.clearTimeout(timeoutId);
      if (!requestStillCurrent()) return;
      reportDatasetState = { status: "error", key, rows: null, claims: null, capped: false, cap: 0, rowCount: 0, error: error && error.name === "AbortError" ? timeoutMessage : error.message, fetchedAt: null, pendingSince: 0 };
      if (state.view === "reports") renderReports();
      else if (state.view === "clientHome") renderClientHome();
    });
}

// ---------------------------------------------------------------------------
// A TRUNCATED TOTAL MUST NEVER RENDER AS A COMPLETE ONE
// ---------------------------------------------------------------------------
// POST /api/reports/dataset serves at most `cap` service lines, owed-first.
// Whatever the frontend then sums over that array is a sum over a PARTIAL
// population. Measured 2026-08-10 on one quarter: 7,184 of 10,262 lines and
// $1,821,003 of collected dollars were absent, and because the loss falls on
// paid/closed history the %-collected figures came out INFLATED, not merely
// small. These helpers exist so no such figure is ever printed bare.
//
// THE EXCEPTION IS NOT AN EXCEPTION TO HONESTY, IT IS HONESTY. The owed-first
// ORDER BY means the cap drops paid history and keeps every open balance, so
// AR Aging's Outstanding Insurance AR is COMPLETE while Charges / Expected /
// Paid / Write-off beside it are not. Marking all six would be its own small
// lie, so the server MEASURES the distinction (openArSurvivedCap) and ships
// it, and reportOpenArComplete() is the only thing allowed to exempt a figure.
function reportTotalsArePartial() {
  return Boolean(trackerApiEnabled()
    && reportDatasetState.status === "ready"
    && reportDatasetState.capped);
}

function reportOpenArComplete() {
  return reportDatasetState.openArComplete !== false;
}

// Appended, never substituted  the same rule reportExpectedKpiCard follows
// for its coverage caveat: the caveat says how complete the number is, and
// must not cost the tile the sentence that says what the number MEANS.
function reportPartialDetail(detail) {
  if (!reportTotalsArePartial()) return detail;
  const shown = Number(reportDatasetState.rowCount || (reportDatasetState.rows || []).length) || 0;
  return `${detail} \u00b7 PARTIAL \u2014 computed over the ${formatNounCount(shown, "line")} the server returned, not the full book`;
}

function reportPartialTotalsNoticeHtml() {
  if (!reportTotalsArePartial()) return "";
  const shown = Number(reportDatasetState.rowCount || (reportDatasetState.rows || []).length) || 0;
  const cap = Number(reportDatasetState.cap) || shown;
  const arNote = reportOpenArComplete()
    ? " Outstanding Insurance AR is <strong>not</strong> affected \u2014 the server returns open balances first, so every unpaid line is present."
    : "";
  return `<p class="report-dataset-note report-partial-note reports-no-print" role="status">These money totals are <strong>incomplete</strong>: they are computed over the ${escapeHtml(formatNounCount(shown, "line"))} the server returned (cap ${escapeHtml(formatNounCount(cap, "line"))}), not the full book \u2014 so charges, expected, paid and write-offs are understated and every %-collected figure reads high.${arNote} Narrow the date range for complete totals.</p>`;
}

// ---------------------------------------------------------------------------
// SERVER-SIDE ROLLUPS  (POST /api/reports/rollup)
// ---------------------------------------------------------------------------
// The row lane above is CAPPED, so anything summed over its array is a partial
// total. This lane asks the server for the ROLLUP instead of the rows: a
// windowed fold over consecutive DOS windows (backend/reportRollupSource.js)
// that runs the same SQL and the same money engine the row lane does, one
// window at a time, so the answer covers the whole book with bounded memory.
//
// It carries NO arithmetic of its own \u2014 the group rows come back already
// summed, and the model adapters below only rename fields into the shapes
// rollforwardBuildRows / cptPayerBuildRows already produce. That is the point:
// the report renderers are untouched, so there is no second implementation of
// a report to drift.
//
// Hosted/API mode fails closed while this lane is pending or failed: no capped
// browser fold is rendered as a substitute for the server population. Local
// development can still exercise the pure builders without claiming live scope.
const REPORT_ROLLUP_TIMEOUT_MS = 90000;
let reportRollupState = { key: null, byGroup: {} };
let reportRollupRequestSequence = 0;
let preparedSnapshotReportState = { key: "", sessionKey: "", view: "", status: "idle", response: null, error: "", requestId: 0, page: 1 };
const patientResponsibilityReportState = { view: "patient", search: "", patient: null, scopeKey: "" };
const preparedClaimsExpandedViews = new Set();

// A 409 FROM /claims OR /drill MEANS THE BOOK MOVED, NOT THAT THE CLICK FAILED.
//
// The prepared model carries the T1a generation it was summed from; the claim page and
// every drill rung are checked against the LIVE generation. When the hourly rebuild lands
// while a report is open, each of those calls answers 409. This used to reset the drill in
// silence ("View payers" did nothing) and file the claim page as "no claim rows". Measured
// live 2026-09-03 for the whole hour after the 02:30 rebuild. The honest response is to
// reload the model from the current snapshot and say so, once, in the report head.
let preparedReportReloadNotice = "";
function notePreparedReportReload() {
  if (reportReadContextCurrent(reportReadContext)) {
    reportReadContext.expired = true;
    reportReadContext.error = "This report view is no longer available. Choose Load latest to continue.";
    renderReportReadStatus();
  }
}
function preparedReportReloadNoticeHtml() {
  if (!preparedReportReloadNotice) return "";
  const text = preparedReportReloadNotice;
  preparedReportReloadNotice = "";
  return '<p class="report-dataset-note" role="status" data-prepared-reload-notice>' + escapeHtml(text) + "</p>";
}
// ONE CLAIM IS ONE CLAIM. The server's per-group claim counts are distinct within their
// group, so summed over service months a claim with lines in two months counted twice
// (89,088 against AR Aging's 81,828 on the same book, measured live 2026-09-03). The claim
// page COUNT and the companion total are distinct over the population; the smallest served
// figure is the one that cannot overstate.
function preparedPopulationClaimCount(model = {}) {
  const totals = model.totals || {};
  const candidates = [model.claimPage && model.claimPage.totalRows, model.claimCount, totals.claimCount]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 0);
  return candidates.length ? Math.min.apply(null, candidates) : 0;
}
// "Rows a-b of N" for the claims disclosure. Previous/Next alone did not say where the
// biller was, and a page with no rows read the same as an empty book.
function preparedClaimsPageCaption(model = {}, claimTotal = 0) {
  const claimPage = model.claimPage || {};
  const page = Math.max(1, Number(claimPage.page) || 1);
  const size = Math.max(1, Number(claimPage.pageSize) || 100);
  const shown = Array.isArray(claimPage.rows) ? claimPage.rows.length : 0;
  const total = Math.max(0, Number(claimTotal) || 0);
  if (!total) return "Rows 0-0 of 0";
  const pages = Math.max(1, Math.ceil(total / size));
  if (claimPage.pending) return "Loading page " + page.toLocaleString() + " of " + pages.toLocaleString();
  if (!shown) return "No rows on page " + page.toLocaleString() + " of " + pages.toLocaleString();
  const first = (page - 1) * size + 1;
  return "Rows " + first.toLocaleString() + "-" + (first + shown - 1).toLocaleString() + " of " + total.toLocaleString();
}
// The generation stamp arrives as an ISO instant; a biller reads a local date and time.
function preparedReportStampLabel(value) {
  const formatted = typeof formatDateTime === "function" ? formatDateTime(value) : "";
  return formatted && formatted !== "None" ? formatted : String(value || "");
}
// A drill step is named by its LEVEL. The old fallback chain (payer || patient || claimId)
// printed the PATIENT on the claim step, so the crumb read "Payer > Patient > Patient" and the
// service-line heading said "on claim <patient name>".
function hostedDrillStepLabel(step) {
  if (!step) return "";
  if (step.level === "claim") return String(step.claimId || "");
  if (step.level === "patient") return String(step.patient || step.patientId || "");
  if (step.level === "payer") return String(step.payer || "");
  return String(step.payer || step.patient || step.claimId || step.level || "");
}
function hostedDrillCrumbLabel(step) {
  return step && step.level === "claim" && step.claimId ? "Claim " + String(step.claimId) : hostedDrillStepLabel(step);
}

function preparedSnapshotDimension(view) {
  if (view === "expectedVsPaid") return ["payer", "client", "provider", "rateSource"].includes(reportEvpState.dimension) ? reportEvpState.dimension : "payer";
  if (view === "patientResponsibility") return ["patient", "payer", "dosMonth"].includes(patientResponsibilityReportState.view) ? patientResponsibilityReportState.view : "patient";
  if (view === "ar") return "arAgingBucket";
  if (view === "performance") return "dosMonth";
  if (view === "reviewQueue") return "queueReason";
  if (view === "billDateMonthlyAging") return "billDateMonth";
  return "billDateBucket";
}
function preparedSnapshotFilters(view) {
  const dates = reportFilterValues();
  const filters = { ...reportDatasetServerFilters(), fromDate: dates.fromDate || "", toDate: dates.toDate || "" };
  if (["denials", "clientAction", "performance", "reviewQueue", "patientResponsibility"].includes(view)) filters._preparedSignal = view;
  if (view === "patientResponsibility") {
    syncPatientResponsibilitySearchScope();
    if (patientResponsibilityReportState.search) filters._patientSearch = patientResponsibilityReportState.search;
    if (patientResponsibilityReportState.patient) {
      filters._patientOrgId = patientResponsibilityReportState.patient.orgId;
      filters._patientId = patientResponsibilityReportState.patient.patientId;
    }
  }
  if (view === "performance") filters._includeYoungClaims = reportPerformanceState.includeYoungClaims === true;
  return filters;
}
function preparedSnapshotQueryKey(view) { return JSON.stringify({ view, dimension:preparedSnapshotDimension(view), filters:preparedSnapshotFilters(view) }); }

function preparedSnapshotReportRequest(view, page = 1) {
  if (view === "rateIssues") return { report: view, filters: preparedSnapshotFilters(view), page, pageSize: 50,
    expectedGeneration: page > 1 ? (preparedSnapshotReportState.response?.generation || "") : "", expectedEpoch: page > 1 ? (preparedSnapshotReportState.response?.epoch || "") : "",
    pageIdentity: page > 1 ? (preparedSnapshotReportState.response?.groupPage?.pageIdentity || "") : "" };
  return { report: view, dimension: preparedSnapshotDimension(view), filters: preparedSnapshotFilters(view), page: 1, pageSize: 50,
    groupPage: page, groupPageSize: reportPageSize(),
    expectedGeneration: page > 1 ? (preparedSnapshotReportState.response?.generation || "") : "",
    pageIdentity: "" };
}
function preparedReportTitle(view) {
  return ({ peopleDirectory:"People directory", ar: "AR Aging", expectedVsPaid: "Expected vs Paid", billDateMonthlyAging: "Bill Date Monthly Aging Summary", billDateAging: "Bill Date Weekly Aging Summary", denials: "Denials", clientAction: "Claims Waiting on Client Action", performance: "Performance", reviewQueue: "Review Queue", rollforward: "Executive Expected vs Paid Rollforward", monthlySummary: "LITE Monthly Summary", patientResponsibility: "Patient Responsibility", cptPayer: "Average Reimbursement Per Unit by CPT and Primary Payer", turnaround: "Average Payment Cycle Per Payer", payerClass: "Payer Classification", rateIssues: "Rate Issues", clientReceivables: "Receivables", clientRcm: "RCM Performance" }[view]) || "Prepared Report";
}
function preparedReportGroupHeading(view, dimension) {
  if (view === "expectedVsPaid") return arDimensionLabel(dimension);
  if (view === "patientResponsibility") return ({ patient: "Patient", payer: "Primary insurance", dosMonth: "Service month" }[dimension]) || "Patient";
  return ({ performance: "Month", reviewQueue: "Queue", ar: "Aging bucket", billDateMonthlyAging: "Bill month", billDateAging: "Bill-date age", denials: "Payer", clientAction: "Client · Claim · Needed action · Owner · Follow-up due", payerClass: "Payer role", expectedVsPaid: "Group", rollforward: "Service month", monthlySummary: "Service month", cptPayer: "Procedure · Primary payer" }[view]) || dimension || "Group";
}
function preparedReportGroupLabel(view, dims) {
  if (view === "patientResponsibility" && patientResponsibilityReportState.view === "patient") {
    if (!Array.isArray(dims) || dims.length < 4) return patientResponsibilityPatientLabel(dims?.[0]);
    return [String(dims?.[3] || "").trim(), String(dims?.[2] || "").trim()].filter(Boolean).join(" · ") || "—";
  }
  // Hosted grains Client Action on [orgId, client, claimId, followUpAction, owner,
  // followUpDate]: the org key is the tenant identity the drill carries, not a label, so
  // it is dropped; the due date is read like every other date on the report (MM/DD/YYYY)
  // instead of the raw ISO the lane groups on. The browser lane grains on ONE
  // "client · claim" string - slicing that single dim away printed "—" on every row.
  const visibleDims = view === "clientAction" && Array.isArray(dims) && dims.length > 1
    ? dims.slice(1).map((value, index) => (index === 4 ? (reportFormatDate(value) || value) : value))
    : dims;
  const raw = (Array.isArray(visibleDims) ? visibleDims : []).filter(Boolean).join(" · ") || "—";
  if (["billDateMonthlyAging", "billDateAging"].includes(view) && raw === "(blank)") return BILL_DATE_NOT_BILLED;
  if (["performance", "billDateMonthlyAging"].includes(view) && /^\d{4}-\d{2}$/.test(raw) && typeof collectionsMonthLabel === "function") return collectionsMonthLabel(raw);
  return raw;
}
function preparedSnapshotSessionKey() { return JSON.stringify({ publicationEpoch: reportReadPublicationIdentity(), base: trackerApiBase(), token: trackerApiToken(), scope: trackerAccessEnvelope("service") }); }
function preparedSnapshotReportKey(request) { return JSON.stringify({ session: preparedSnapshotSessionKey(), request }); }
function reportLoadingHtml(title, slow = false) {
  return '<section class="panel report-loading" aria-busy="true"><div role="status" aria-live="polite"><span class="report-loading-spinner" aria-hidden="true"></span><strong>Loading ' + escapeHtml(title) + '…</strong><p>' + (slow ? 'Still working through this selection. You can change reports or filters while this finishes. If the request cannot finish, a retry option will appear.' : 'Preparing the complete results for your selection.') + '</p></div></section>';
}

// Each report lane owns cancellation and a deadline through response-body decoding.
// Request identity remains the authority for whether a completed response may render.
const reportReadControllers = new Map();

// A report stays on the version the reader opened. Only navigation or Load latest
// starts a new read session; background publication changes this small status bar.
const REPORT_SESSION_RENEW_MS = 60000;
const REPORT_SESSION_CACHE_MAX_BYTES = 8 * 1024 * 1024;
const REPORT_SESSION_CACHE_MAX_ENTRIES = 40;
let reportReadContext = null;
let reportReadContextSequence = 0;
let reportSessionMaintenanceTimer = null;
let reportSessionVisibilityListener = null;
let reportDataRefreshJob = null;
let reportDataRefreshTimer = null;

function reportReadSurface() {
  if (window.TMS_PORTAL_PREPARED_READS !== true) return "";
  if (!trackerApiEnabled() || !trackerApiToken()) return "";
  if (state.view === "statements") return hasPermission(PERMISSIONS.STATEMENTS_VIEW) && hasPermission(PERMISSIONS.VIEW_PHI) ? "statements" : "";
  if (!hasPermission(PERMISSIONS.VIEW_FINANCIALS)) return "";
  if (!hasPermission(PERMISSIONS.VIEW_REPORTS)) {
    if (state.view !== "reports" || !hasPermission(PERMISSIONS.CLIENT_PAYMENTS)) return "";
    return reportViewForClientReportKey(clientActiveReport()?.key || "");
  }
  return state.view === "reports" ? String(reportArState.view || "ar") : "";
}
function reportReadPrincipalKey() {
  return JSON.stringify({ base: trackerApiBase(), token: trackerApiToken(), role: state.role,
    scope: trackerAccessEnvelope("service") });
}
function reportReadContextCurrent(context) {
  return Boolean(context && reportReadContext === context && context.report === reportReadSurface()
    && context.principalKey === reportReadPrincipalKey());
}
function reportReadPublicationIdentity() {
  return reportReadContextCurrent(reportReadContext) ? "report-view-" + reportReadContext.sequence
    : (typeof trackerPublicationEpoch === "number" ? trackerPublicationEpoch : 0);
}
function clearReportSessionResults() {
  reportReadControllers.forEach((controller) => controller.abort());
  reportReadControllers.clear();
  resetReportDataset();
  reportFilterFacetReadState = { key: "", status: "idle", response: null, error: "" };
  reportFilterFacetRequestSequence += 1;
  reportRollupState = { key: null, byGroup: {} };
  preparedSnapshotReportState = { ...preparedSnapshotReportState, key: "", sessionKey: "", queryKey: "",
    status: "idle", response: null, error: "", requestId: preparedSnapshotReportState.requestId + 1 };
  resetReportPaymentEvidence(); resetHostedReportDrill();
  reportCollectionsState = { key: "", status: "idle", response: null, error: "", code: "" };
  reportTurnaroundReadSequence += 1; reportTurnaroundReadController?.abort();
  reportTurnaroundState = { key: "", status: "idle", response: null, error: "", code: "", httpStatus: 0 };
  reportApiState = { key: "", status: "idle", response: null, error: "" };
  reportQueueDrilldownState = { key: "", status: "idle", response: null, error: "", request: null };
  clientReportAggregateRequestSequence += 1; clientReportDrillRequestSequence += 1;
  clientReportAggregatePageIdentity = "";
  clientReportAggregateApiState = { key: "", status: "idle", response: null, error: "" };
  clientReportServerDrillState = { ...clientReportServerDrillState, key: "", status: "idle", response: null, error: "" };
  clientReportClaimsApiState = { key: "", status: "idle", rows: [], totalRows: 0, truncated: false, error: "", retried: false };
  clientPaymentChecksApiState = { key: "", status: "idle", response: null, error: "" };
  clientPaymentCheckDetailCache.clear();
  if (state.view === "statements") {
    statementPreviewState.controller?.abort(); statementPreviewState.requestId += 1;
    Object.assign(statementPreviewState, { key: "", status: "idle", data: null, controller: null, error: "" });
  }
}
function stopReportReadSession() {
  const previous = reportReadContext;
  reportReadContext = null;
  if (previous) {
    previous.controller.abort(); previous.controllers.forEach((controller) => controller.abort());
    previous.cache.clear(); previous.cacheBytes = 0; clearReportSessionResults();
  }
  clearInterval(reportSessionMaintenanceTimer); reportSessionMaintenanceTimer = null;
  if (reportSessionVisibilityListener) document.removeEventListener("visibilitychange", reportSessionVisibilityListener);
  reportSessionVisibilityListener = null;
  clearTimeout(reportDataRefreshTimer); reportDataRefreshTimer = null; reportDataRefreshJob = null;
  renderReportReadStatus();
}
function syncReportReadContext(force = false) {
  const report = reportReadSurface();
  const principalKey = report ? reportReadPrincipalKey() : "";
  if (!force && reportReadContext && reportReadContext.report === report && reportReadContext.principalKey === principalKey) return reportReadContext;
  const previous = reportReadContext;
  if (previous) {
    previous.controller.abort(); previous.controllers.forEach((controller) => controller.abort());
    previous.cache.clear();
  }
  reportReadContext = report ? { report, principalKey, sequence: ++reportReadContextSequence,
    controller: new AbortController(), controllers: new Set(), cache: new Map(), cacheBytes: 0,
    lease: null, promise: null, error: "", expired: false, updateAvailable: false, maintaining: false } : null;
  if (previous || reportReadContext) clearReportSessionResults();
  if (reportDataRefreshJob && reportDataRefreshJob.principalKey !== principalKey) {
    reportDataRefreshJob = null; clearTimeout(reportDataRefreshTimer); reportDataRefreshTimer = null;
  }
  if (reportReadContext) {
    void ensureReportReadSession(reportReadContext).catch(() => {});
    if (reportSessionMaintenanceTimer === null) {
      reportSessionMaintenanceTimer = setInterval(() => { void maintainReportReadSession(); }, REPORT_SESSION_RENEW_MS);
      reportSessionVisibilityListener = () => { if (document.visibilityState !== "hidden") void maintainReportReadSession(); };
      document.addEventListener("visibilitychange", reportSessionVisibilityListener);
    }
  }
  renderReportReadStatus();
  return reportReadContext;
}
function reportSessionHeaders() {
  return { "content-type": "application/json", authorization: "Bearer " + trackerApiToken() };
}
function reportSessionStopped(message = "This report view has expired. Choose Load latest to continue.") {
  const error = new Error(message); error.code = "report-read-session-stopped"; return error;
}
async function ensureReportReadSession(context = syncReportReadContext()) {
  if (!context || !reportReadContextCurrent(context)) throw reportSessionStopped("This report is no longer open.");
  if (context.expired) throw reportSessionStopped(context.error);
  if (context.lease) {
    if (Math.min(Date.parse(context.lease.expiresAt), Date.parse(context.lease.absoluteExpiresAt)) <= Date.now()) {
      context.expired = true; context.error = "This report view has expired. Choose Load latest to continue.";
      context.cache.clear(); context.cacheBytes = 0; renderReportReadStatus(); throw reportSessionStopped(context.error);
    }
    return context.lease;
  }
  if (!context.promise) context.promise = (async () => {
    const timeout = setTimeout(() => context.controller.abort(), 10000);
    try {
      const response = await fetch(trackerApiBase() + "/api/report-read-sessions", { method: "POST", cache: "no-store",
        headers: reportSessionHeaders(), body: JSON.stringify({ report: context.report }), signal: context.controller.signal });
      const body = await response.json();
      if (!reportReadContextCurrent(context) || context.controller.signal.aborted) throw reportSessionStopped("This report is no longer open.");
      if (!response.ok || !body.id || !body.generation || !Number.isFinite(Date.parse(body.expiresAt)) || !Number.isFinite(Date.parse(body.absoluteExpiresAt))) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        throw reportSessionStopped(response.status === 403 ? "Your report access changed. Reopen an authorized report." : "This report could not be opened. Choose Load latest to retry.");
      }
      context.lease = body; context.error = ""; renderReportReadStatus(); return body;
    } catch (error) {
      if (reportReadContextCurrent(context)) { context.error = error.name === "AbortError" ? "Report opening was interrupted. Choose Load latest to retry." : error.message; renderReportReadStatus(); }
      throw error;
    } finally { clearTimeout(timeout); }
  })();
  return context.promise;
}
function reportPinnedReadUrl(url, lease) {
  const parsed = new URL(String(url), window.location?.href || trackerApiBase() + "/");
  if ((parsed.searchParams.get("expectedGeneration") && parsed.searchParams.get("expectedGeneration") !== String(lease.generation))
    || (parsed.searchParams.get("readSessionId") && parsed.searchParams.get("readSessionId") !== String(lease.id))) {
    throw reportSessionStopped("The selected rows belong to a previous report view. Choose Load latest.");
  }
  parsed.searchParams.delete("refresh");
  parsed.searchParams.set("readSessionId", lease.id);
  parsed.searchParams.set("expectedGeneration", lease.generation);
  return parsed.toString();
}
function reportSessionRoute(url) {
  const path = new URL(String(url), window.location?.href || trackerApiBase() + "/").pathname;
  return /^\/api\/reports\/(prepared|claims|drill|payment-evidence|rollup|collections|turnaround(?:\/drill)?|query|claim-queues\/drilldown|dataset)$/.test(path)
    || /^\/api\/client\/(reports\/|payments\/checks(?:\/|$))/.test(path)
    || path === "/api/statements/preview"
    || path === "/api/reports/tracker-workbook-export"
    || path === "/api/tracker/facets";
}
async function reportSessionFetch(url, options = {}) {
  if (!reportReadSurface() || !reportSessionRoute(url)) return fetch(url, options);
  const context = syncReportReadContext();
  const lease = await ensureReportReadSession(context);
  if (!reportReadContextCurrent(context) || options.signal?.aborted) throw new DOMException("The request was superseded.", "AbortError");
  const pinnedUrl = reportPinnedReadUrl(url, lease);
  let body = null;
  if (typeof options.body === "string") {
    try { body = JSON.parse(options.body); } catch (_) { throw reportSessionStopped("The report request could not be read."); }
    if ((body.expectedGeneration && String(body.expectedGeneration) !== String(lease.generation))
      || (body.readSessionId && String(body.readSessionId) !== String(lease.id))) {
      throw reportSessionStopped("The selected rows belong to a previous report view. Choose Load latest.");
    }
  }
  const controller = new AbortController(); context.controllers.add(controller);
  // Pinned reads default to 10 s; a caller whose read is known to run longer
  // (complete statements, Notes lists) passes its own budget, capped at 120 s.
  const readTimeoutMs = Math.min(120000, Math.max(10000, Number(options.timeoutMs) || 10000));
  const timeout = setTimeout(() => controller.abort(), readTimeoutMs);
  const abort = () => controller.abort(); options.signal?.addEventListener("abort", abort, { once: true });
  const { timeoutMs: _readTimeout, ...fetchOptions } = options;
  const request = { ...fetchOptions, signal: controller.signal };
  if (body) request.body = JSON.stringify({ ...body, readSessionId: lease.id, expectedGeneration: lease.generation });
  const cacheKey = JSON.stringify([context.principalKey, lease.generation, pinnedUrl, options.method || "GET", request.body || ""]);
  try {
    const cached = context.cache.get(cacheKey);
    if (cached) { context.cache.delete(cacheKey); context.cache.set(cacheKey, cached); return new Response(cached, { status: 200, headers: { "content-type": "application/json" } }); }
    const response = await fetch(pinnedUrl, request);
    if (controller.signal.aborted || !reportReadContextCurrent(context)) throw new DOMException("The request was superseded.", "AbortError");
    if (response.status === 409 || response.status === 410) {
      context.expired = true; context.error = "This saved report view is no longer available. Choose Load latest to continue.";
      context.cache.clear(); context.cacheBytes = 0; renderReportReadStatus(); throw reportSessionStopped(context.error);
    }
    if (response.status === 401 || response.status === 403) {
      context.expired = true; context.error = "Your report access changed. Sign in again or reopen an authorized report.";
      context.cache.clear(); context.cacheBytes = 0; renderReportReadStatus();
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    }
    if (response.ok && response.headers?.get("content-type")?.includes("application/json")) {
      const serialized = await response.clone().text();
      if (controller.signal.aborted || !reportReadContextCurrent(context)) throw new DOMException("The request was superseded.", "AbortError");
      const value = JSON.parse(serialized);
      if (value.generation && String(value.generation) !== String(lease.generation)) throw reportSessionStopped("The response did not match this report view. Choose Load latest.");
      // Keep small successful reads in this tab's memory only. Downloads and
      // incomplete responses never occupy or satisfy this cache.
      const size = serialized.length * 2;
      if (response.status === 200 && size <= 2 * 1024 * 1024 && !value.error && value.complete !== false && value.truncated !== true
        && body?.operation !== "export" && body?.exportAll !== true && !/\/export(?:\?|$)/.test(pinnedUrl)
        && !pinnedUrl.includes("/tracker-workbook-export") && !pinnedUrl.includes("/statements/preview")) {
        context.cache.set(cacheKey, serialized); context.cacheBytes += size;
        while (context.cache.size > REPORT_SESSION_CACHE_MAX_ENTRIES || context.cacheBytes > REPORT_SESSION_CACHE_MAX_BYTES) {
          const first = context.cache.keys().next().value; context.cacheBytes -= context.cache.get(first).length * 2; context.cache.delete(first);
        }
      }
    }
    return response;
  } finally { clearTimeout(timeout); context.controllers.delete(controller); options.signal?.removeEventListener("abort", abort); }
}
async function maintainReportReadSession() {
  const context = reportReadContext;
  if (document.visibilityState === "hidden" || !reportReadContextCurrent(context) || context.maintaining || context.expired) return;
  context.maintaining = true;
  const controller = new AbortController(); context.controllers.add(controller);
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    const lease = await ensureReportReadSession(context);
    const base = trackerApiBase() + "/api/report-read-sessions/" + encodeURIComponent(lease.id);
    const results = await Promise.all([fetch(base + "/renew", { method: "POST", cache: "no-store", headers: reportSessionHeaders(), body: "{}", signal: controller.signal }),
      fetch(base + "/status", { cache: "no-store", headers: reportSessionHeaders(), signal: controller.signal })]);
    const bodies = await Promise.all(results.map((response) => response.json()));
    if (!reportReadContextCurrent(context)) return;
    if (results.some((response) => [401, 403, 409, 410].includes(response.status))) {
      context.expired = true; context.cache.clear(); context.cacheBytes = 0;
      context.error = "This report view has expired or your access changed. Choose Load latest to continue.";
    } else {
      if (results[0].ok && bodies[0].expiresAt) context.lease = { ...lease, expiresAt: bodies[0].expiresAt, absoluteExpiresAt: bodies[0].absoluteExpiresAt || lease.absoluteExpiresAt };
      if (results[1].ok) context.updateAvailable = bodies[1].updateAvailable === true || Boolean(bodies[1].latestGeneration && String(bodies[1].latestGeneration) !== String(lease.generation));
      context.error = results.every((response) => response.ok) ? "" : "Update status is temporarily unavailable. Your displayed report is unchanged.";
    }
  } catch (error) { if (reportReadContextCurrent(context) && !context.expired) context.error = "Update status is temporarily unavailable. Your displayed report is unchanged."; }
  finally { clearTimeout(timeout); context.controllers.delete(controller); context.maintaining = false; if (reportReadContextCurrent(context)) renderReportReadStatus(); }
}
function reportReadStatusText() {
  const context = reportReadContext;
  if (!context) return "";
  if (context.error) return context.error;
  if (!context.lease) return "Opening report…";
  const stamp = context.lease.sourceAsOf || context.lease.publishedAt;
  const asOf = stamp ? "Data through " + preparedReportStampLabel(stamp) + ". " : "Report ready. ";
  return asOf + (context.updateAvailable ? "Newer data is available. Your current view is unchanged." : "Updates are prepared hourly. Your current view stays unchanged.");
}
function renderReportReadStatus() {
  const context = reportReadContext;
  ["reportReadStatus", "statementsReadStatus"].forEach((id) => {
    const host = document.getElementById(id); if (!host) return;
    const expectedHost = context?.report === "statements" ? "statementsReadStatus" : "reportReadStatus";
    host.hidden = !reportReadContextCurrent(context) || id !== expectedHost;
    if (host.hidden) return;
    if (!host.querySelector("[data-report-read-status]")) {
      host.innerHTML = '<div class="report-read-copy"><span data-report-read-status role="status" aria-live="polite"></span><small data-report-refresh-status role="status" aria-live="polite"></small></div><div class="report-read-actions"><button type="button" class="ghost-button" data-report-refresh-data>Refresh data</button><button type="button" class="ghost-button" data-report-load-latest>Load latest</button></div>';
      host.querySelector("[data-report-load-latest]").addEventListener("click", loadLatestReportData);
      host.querySelector("[data-report-refresh-data]").addEventListener("click", () => { void requestReportDataRefresh(); });
    }
    host.querySelector("[data-report-read-status]").textContent = reportReadStatusText();
    const job = reportDataRefreshJob?.principalKey === context.principalKey ? reportDataRefreshJob : null;
    const refreshing = job && !["published", "completed", "succeeded", "failed", "cancelled", "deferred"].includes(job.status);
    host.querySelector("[data-report-refresh-status]").textContent = job ? (job.status === "failed" ? "The data update did not finish. Refresh data to retry; your report is unchanged."
      // A cycle deferred while client data was still loading (portalDataRefreshJobs).
      : job.status === "deferred" ? "Client data is still loading, so this update waits. It runs again within a few minutes; your report is unchanged."
      : refreshing ? "Updating data in the background. You can keep reading."
      : "Data update finished. Choose Load latest when you are ready.") : "";
    host.querySelector("[data-report-refresh-data]").disabled = Boolean(refreshing);
    host.querySelector("[data-report-load-latest]").disabled = !context.lease && !context.error;
  });
  if (typeof updateLastSyncedIndicator === "function") { try { updateLastSyncedIndicator(); } catch (_) {} }
}
function loadLatestReportData() {
  if (!reportReadSurface()) return;
  if (state.view === "statements" && (statementActionState.busy || statementPayState.open || statementBrandingEditor.open)) {
    if (reportReadContext) reportReadContext.error = "Finish or close the open statement edit before loading the latest data. Your draft is unchanged.";
    renderReportReadStatus(); return;
  }
  syncReportReadContext(true);
  if (state.view === "statements") renderStatements(); else renderReports();
}
async function requestReportDataRefresh() {
  const context = reportReadContext;
  if (!reportReadContextCurrent(context)) return;
  if (reportDataRefreshJob && !["published", "completed", "succeeded", "failed", "cancelled", "deferred"].includes(reportDataRefreshJob.status)) return;
  const principalKey = context.principalKey;
  const idempotencyKey = reportDataRefreshJob?.principalKey === principalKey && reportDataRefreshJob.status === "failed" && reportDataRefreshJob.idempotencyKey
    ? reportDataRefreshJob.idempotencyKey : crypto.randomUUID();
  reportDataRefreshJob = { principalKey, status: "queued", id: "", idempotencyKey }; renderReportReadStatus();
  try {
    const response = await fetch(trackerApiBase() + "/api/data-refresh-jobs", { method: "POST", cache: "no-store", headers: reportSessionHeaders(), body: JSON.stringify({ idempotencyKey }) });
    const body = await response.json();
    if (reportReadPrincipalKey() !== principalKey) return;
    if (!response.ok || !body.id) throw new Error("refresh unavailable");
    reportDataRefreshJob = { ...body, principalKey }; renderReportReadStatus(); scheduleReportDataRefreshPoll();
  } catch (_) {
    if (reportReadPrincipalKey() === principalKey) { reportDataRefreshJob = { principalKey, status: "failed", id: "", idempotencyKey }; renderReportReadStatus(); }
  }
}
function scheduleReportDataRefreshPoll() {
  clearTimeout(reportDataRefreshTimer);
  reportDataRefreshTimer = setTimeout(() => { void pollReportDataRefresh(); }, 2000);
}
async function pollReportDataRefresh() {
  const job = reportDataRefreshJob;
  if (!job?.id || job.principalKey !== reportReadPrincipalKey() || ["published", "completed", "succeeded", "failed", "cancelled", "deferred"].includes(job.status)) return;
  if (document.visibilityState === "hidden") { scheduleReportDataRefreshPoll(); return; }
  try {
    const response = await fetch(trackerApiBase() + "/api/data-refresh-jobs/" + encodeURIComponent(job.id), { cache: "no-store", headers: reportSessionHeaders() });
    const body = await response.json();
    if (reportDataRefreshJob !== job || job.principalKey !== reportReadPrincipalKey()) return;
    if (response.ok) {
      reportDataRefreshJob = { ...body, id: job.id, principalKey: job.principalKey };
      if (["published", "completed", "succeeded"].includes(body.status) && reportReadContextCurrent(reportReadContext)) reportReadContext.updateAvailable = true;
    }
  } catch (_) { /* Keep the accepted job and retry status without resubmitting it. */ }
  renderReportReadStatus(); scheduleReportDataRefreshPoll();
}
// End report read-session controller.

async function reportReadJson(lane, url, options = {}, onSlow = () => {}) {
  reportReadControllers.get(lane)?.abort();
  const controller = new AbortController();
  reportReadControllers.set(lane, controller);
  let deadline;
  const slowTimer = setTimeout(onSlow, 8000);
  try {
    return await Promise.race([
      reportSessionFetch(typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(url) : url, { ...options, signal: controller.signal }).then(async (response) => ({ response, body: await response.json().catch(() => ({})) })),
      new Promise((resolve, reject) => {
        deadline = setTimeout(() => {
          reject(new Error("This report is taking longer than expected. Retry or narrow the date range."));
          controller.abort();
        }, REPORT_ROLLUP_TIMEOUT_MS);
      })
    ]);
  } finally {
    clearTimeout(slowTimer);
    clearTimeout(deadline);
    if (reportReadControllers.get(lane) === controller) reportReadControllers.delete(lane);
  }
}

function preparedReportOrderedGroups(view, rows = []) {
  const temporal = ["performance", "billDateMonthlyAging", "rollforward", "monthlySummary"].includes(view)
    || (view === "patientResponsibility" && patientResponsibilityReportState.view === "dosMonth");
  if (!temporal) return rows;
  return rows.slice().sort((a, b) => {
    const left = String(a.dims?.[0] || "");
    const right = String(b.dims?.[0] || "");
    const isDate = (value) => /^[0-9]{4}-[0-9]{2}(?:-[0-9]{2})?$/.test(value);
    return Number(isDate(right)) - Number(isDate(left))
      || (isDate(left) && isDate(right) ? right.localeCompare(left) : 0)
      || JSON.stringify(a.dims || []).localeCompare(JSON.stringify(b.dims || []), undefined, { numeric: true });
  });
}

function requestPreparedSnapshotReport(view, page = 1) {
  const base = trackerApiBase(); if (!base || typeof fetch !== "function") return;
  const request = preparedSnapshotReportRequest(view, page);
  const key = preparedSnapshotReportKey(request);
  const sessionKey = preparedSnapshotSessionKey();
  const queryKey = preparedSnapshotQueryKey(view);
  const claimPageNumber = preparedSnapshotReportState.view === view && preparedSnapshotReportState.queryKey === queryKey && preparedSnapshotReportState.sessionKey === sessionKey ? (preparedSnapshotReportState.response?.claimPage?.page || preparedSnapshotReportState.claimPageNumber || 1) : 1;
  const requestId = ++preparedSnapshotReportState.requestId;
  const current = () => preparedSnapshotReportState.requestId === requestId && preparedSnapshotSessionKey() === sessionKey && preparedSnapshotQueryKey(view) === queryKey;
  preparedSnapshotReportState = { key, sessionKey, queryKey, view, status: "pending", response: null, error: "", requestId, page, claimPageNumber, slow: false };
  const token = trackerApiToken();
  reportReadJson("prepared", (typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(base + "/api/reports/prepared") : base + "/api/reports/prepared"), { method: "POST", cache: "no-store", headers: { "content-type": "application/json", ...(token ? { authorization: "Bearer " + token } : {}) }, body: JSON.stringify(request) }, () => {
    if (!current()) return;
    preparedSnapshotReportState.slow = true;
    if (state.view === "reports") renderReports({ settledRead: true });
  }).then(({ response, body }) => {
    if (!current()) return;
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    preparedSnapshotReportState = response.ok && body.complete === true
      ? { key, sessionKey, queryKey, view, status: "ok", response: body, error: "", requestId, page, claimPageNumber }
      : { key, sessionKey, queryKey, view, status: "error", response: null, error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : reportLoadFailureReason(body.message || ("HTTP " + response.status)), requestId, page, claimPageNumber };
    if (state.view === "reports") renderReports({ settledRead: true });
  }).catch((error) => {
    if (!current()) return;
    preparedSnapshotReportState = { key, sessionKey, queryKey, view, status: "error", response: null, error: reportLoadFailureReason(error.message), requestId, page, claimPageNumber };
    if (state.view === "reports") renderReports({ settledRead: true });
  });
}

function requestPreparedClaimPage(view, page = 1) {
  const base = trackerApiBase(); if (!base || typeof fetch !== "function") return;
  const model = preparedSnapshotReportState.response;
  if (!model || preparedSnapshotReportState.view !== view) return;
  const sessionKey = preparedSnapshotSessionKey();
  const queryKey = preparedSnapshotQueryKey(view);
  const requestId = preparedSnapshotReportState.requestId;
  const claimRequestId = (preparedSnapshotReportState.claimRequestId || 0) + 1;
  preparedSnapshotReportState.claimRequestId = claimRequestId;
  preparedSnapshotReportState.claimPageNumber = page;
  model.claimPage = { ...(model.claimPage || {}), page, pending: true, inFlight: true, error: "" };
  const token = trackerApiToken();
  reportReadJson("prepared-claims", base + "/api/reports/claims", {
    method: "POST",
    cache: "no-store",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      filters: preparedSnapshotFilters(view),
      page,
      pageSize: model.claimPage?.pageSize || 100,
      expectedGeneration: model.generation || "",
      pageIdentity: page > 1 ? (model.claimPage?.pageIdentity || "") : ""
    })
  })
    .then(({ response, body }) => {
      if (preparedSnapshotReportState.requestId !== requestId || preparedSnapshotReportState.claimRequestId !== claimRequestId || preparedSnapshotSessionKey() !== sessionKey || preparedSnapshotQueryKey(view) !== queryKey) return;
      if (!preparedSnapshotReportState.response) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        const lastPage = preparedSnapshotReportState.response.claimPage || {};
        preparedSnapshotReportState = {
          ...preparedSnapshotReportState,
          claimPageNumber: page,
          response: {
            ...preparedSnapshotReportState.response,
            claimPage: {
              ...lastPage,
              pending: false,
              error: "Your session expired. Sign in again — this page will still be here.",
              rows: Array.isArray(lastPage.rows) ? lastPage.rows : []
            }
          }
        };
        if (state.view === "reports") renderReports({ settledRead: true });
        return;
      }
      if (response.status === 409) { notePreparedReportReload(); return; }
      preparedSnapshotReportState = {
        ...preparedSnapshotReportState,
        claimPageNumber: page,
        response: {
          ...preparedSnapshotReportState.response,
          claimPage: response.ok && body.complete === true
            ? { ...body, pending: false, totalRows: Number(body.totalRows || preparedSnapshotReportState.response.claimCount || 0) }
            : { ...(preparedSnapshotReportState.response.claimPage || {}), pending: false, error: body.message || "Claim page unavailable", rows: [] }
        }
      };
      if (state.view === "reports") renderReports({ settledRead: true });
    })
    .catch((error) => {
      if (preparedSnapshotReportState.requestId !== requestId || preparedSnapshotReportState.claimRequestId !== claimRequestId || preparedSnapshotSessionKey() !== sessionKey || preparedSnapshotQueryKey(view) !== queryKey) return;
      if (!preparedSnapshotReportState.response) return;
      preparedSnapshotReportState = {
        ...preparedSnapshotReportState,
        response: {
          ...preparedSnapshotReportState.response,
          claimPage: { ...(preparedSnapshotReportState.response.claimPage || {}), pending: false, error: error.message || "Claim page unavailable", rows: [] }
        }
      };
      if (state.view === "reports") renderReports({ settledRead: true });
    });
}

function preparedSnapshotHiddenOutstanding(totals = {}) {
  const owed = Number(totals && totals.outstanding);
  return Number.isFinite(owed) && owed > 0 ? owed : null;
}

function preparedSnapshotEmptyGroupsText(totals = {}) {
  const owed = preparedSnapshotHiddenOutstanding(totals);
  return owed != null
    ? `No matching groups. ${money(owed)} remains outstanding outside the current selection.`
    : "No matching groups. This does not mean the outstanding balance is $0.";
}

function preparedSnapshotEmptyGroupsHtml(totals = {}) {
  return `<tr><td colspan="6">${escapeHtml(preparedSnapshotEmptyGroupsText(totals))}</td></tr>`;
}

function preparedSnapshotEmptyClaimsHtml(model = {}) {
  const totals = model.totals || {};
  const owed = preparedSnapshotHiddenOutstanding(totals);
  const claimTotal = preparedPopulationClaimCount(model);
  let note = "No matching claims. This does not mean the outstanding balance is $0.";
  if (claimTotal > 0) {
    note = `This page has no claim rows. ${formatNounCount(claimTotal, "claim")} remain on the book. This is not $0.`;
  } else if (owed != null) {
    note = `No matching claims. ${money(owed)} remains outstanding outside the current selection.`;
  }
  return `<tr><td colspan="9">${escapeHtml(note)}</td></tr>`;
}

// ---------------------------------------------------------------------------
// DEFINITION-DRIVEN REPORT TABLES.
//
// Mirror of backend/reportDefinitions.js. app.js is a plain browser script and cannot
// require() it, so the shape is duplicated here and pinned against the backend by
// backend/verify_report_definitions_app_mirror.js — the same drift-assertion pattern
// followUpActionCatalog.js uses. backend/verify_report_definitions_app_mirror.js pins the
// two together — change one, change the other, or it goes red.
//
// Every entry names a measure key the rollup already returns. There is no arithmetic
// here beyond dividing a ratio's two component measures.
const REPORT_COL_GROUPS = ["DRILLDOWN", "PERIOD & VOLUME", "EXPECTED BRIDGE", "FINAL INSURANCE SPLIT", "APPLIED PAYMENTS", "BALANCE & PERFORMANCE"];

function reportMeasureCol(key, label, group, kind) {
  return { type: "measure", key, label, group, kind };
}
function reportRatioCol(key, label, group, numerator, denominator, denominatorPlus) {
  return { type: "ratio", key, label, group, kind: "percent", numerator, denominator, denominatorPlus: denominatorPlus || null };
}

const REPORT_FINANCIAL_SPINE = [
  reportMeasureCol("lines", "Service Line Count", "PERIOD & VOLUME", "number"),
  reportMeasureCol("units", "Units", "PERIOD & VOLUME", "number"),
  reportMeasureCol("totalExpected", "Gross Expected", "EXPECTED BRIDGE", "currency"),
  reportMeasureCol("patientResp", "Patient Responsibility", "EXPECTED BRIDGE", "currency"),
  reportMeasureCol("writeOff", "Write-off Amount", "EXPECTED BRIDGE", "currency"),
  reportMeasureCol("insuranceFloorAdjustment", "Insurance Floor Adjustment", "EXPECTED BRIDGE", "currency"),
  reportMeasureCol("finalInsuranceExpected", "Insurance Amount Expected", "EXPECTED BRIDGE", "currency"),
  reportMeasureCol("primaryExpected", "Primary Insurance Expected", "FINAL INSURANCE SPLIT", "currency"),
  reportMeasureCol("secondaryExpected", "Secondary Insurance Expected", "FINAL INSURANCE SPLIT", "currency"),
  reportMeasureCol("primaryApplied", "Primary Applied Payments", "APPLIED PAYMENTS", "currency"),
  reportMeasureCol("secondaryApplied", "Secondary Applied Payments", "APPLIED PAYMENTS", "currency"),
  reportMeasureCol("unattributedApplied", "Unattributed Applied Payments", "APPLIED PAYMENTS", "currency"),
  reportMeasureCol("insurancePaid", "Total Applied Payments", "APPLIED PAYMENTS", "currency"),
  // REPORTS CAP EACH LINE AT WHAT WAS EXPECTED (Nathan, 2026-10-12). Overpaid lines used to
  // carry negatives into the signed net, so one line's overpayment hid another line's balance
  // and Collections % could read above 100%. Per line now:
  //   Paid Toward Expected + Overpaid + Paid - No Rate on File = Total Applied Payments
  //   Remaining Balance = expected - paid, never below $0
  // Collections % divides Paid Toward Expected by expected on rated lines only. The signed
  // net and owed-based Open Insurance AR stay in the rollup for reconciliation, not on screen.
  // Mirror of backend/reportDefinitions.js FINANCIAL_SPINE.
  reportMeasureCol("paidTowardExpected", "Paid Toward Expected", "APPLIED PAYMENTS", "currency"),
  reportMeasureCol("overpaid", "Overpaid", "APPLIED PAYMENTS", "currency"),
  reportMeasureCol("overpaidLines", "Overpaid Lines (over $10)", "APPLIED PAYMENTS", "number"),
  reportMeasureCol("paidNoRate", "Paid – No Rate on File", "APPLIED PAYMENTS", "currency"),
  reportMeasureCol("remainingBalance", "Remaining Balance", "BALANCE & PERFORMANCE", "currency"),
  reportRatioCol("collectionsPct", "Collections %", "BALANCE & PERFORMANCE", "paidTowardExpected", "expectedRated"),
  reportRatioCol("writeOffPct", "Write-off %", "BALANCE & PERFORMANCE", "writeOff", "finalInsuranceExpected", "writeOff")
];

// Each report names its grain column and how to drill a row. `drill` returns a claim
// predicate for the clicked value; drillIntoReportRow then opens the matching lines, which
// is the same mechanism the Bill-Date Aging rows already used.
const REPORT_DEFINITIONS_UI = {
  ar: {
    title: "AR Aging Summary", sortBy: "bucket",
    grainOf: (claim) => arAgingBucketForClaim(claim).key,
    grainLabel: "Aging Bucket", columns: REPORT_FINANCIAL_SPINE, drillLabel: "payers",
    drillTitle: (v) => `Aging bucket ${v}`,
    // arAgingBucketFor takes DAYS and returns {key,label,min,max}. Comparing its return
    // to a string matched nothing; and .label uses an EN DASH while .key uses a hyphen,
    // so .key is the one that matches a rendered row.
    drill: (v) => (claim) => arAgingBucketForClaim(claim).key === v
  },
  billDateAging: {
    title: "Bill Date Weekly Aging Summary", sortBy: "label",
    grainOf: (claim) => billDateWeeklyBucketFor(billDateAgingDaysOrNull(claim)),
    grainLabel: "Bill Date Aging", columns: REPORT_FINANCIAL_SPINE, drillLabel: "payers",
    drillTitle: (v) => `Bill-date bucket ${v}`,
    drill: (v) => (claim) => billDateWeeklyBucketFor(billDateAgingDaysOrNull(claim)) === v
  },
  billDateMonthlyAging: {
    title: "Bill Date Monthly Aging Summary", sortBy: "label",
    grainOf: (claim) => billDateMonthlyGroupFor(claim),
    grainLabel: "Bill Month", columns: REPORT_FINANCIAL_SPINE, drillLabel: "payers",
    drillTitle: (v) => `Bill month ${v}`,
    drill: (v) => (claim) => billDateMonthlyGroupFor(claim) === v
  },
  monthlySummary: {
    title: "LITE Monthly Summary", sortBy: "label",
    grainOf: (claim, service) => reportMonthOfDate(service.dos),
    grainLabel: "Service Month", columns: REPORT_FINANCIAL_SPINE, drillLabel: "payers",
    drillTitle: (v) => `Service month ${v}`,
    drill: reportDrillByGrain((claim, service) => reportMonthOfDate(service.dos))
  },
  rollforward: {
    title: "Executive Expected vs Paid Rollforward", sortBy: "label",
    grainOf: (claim, service) => reportMonthOfDate(service.dos),
    grainLabel: "Service Month", drillLabel: "payers",
    // NOT the shared spine — a rollforward's whole point is the bridge from what was
    // BILLED to what expected became. Without Expected Revision this was Monthly Summary
    // under a different heading.
    columns: [
      reportMeasureCol("lines", "Service Line Count", "PERIOD & VOLUME", "number"),
      reportMeasureCol("units", "Units", "PERIOD & VOLUME", "number"),
      reportMeasureCol("billedExpected", "Billed Expected", "EXPECTED BRIDGE", "currency"),
      reportMeasureCol("expectedRevision", "Expected Revision", "EXPECTED BRIDGE", "currency"),
      reportMeasureCol("finalExpectedTotal", "Gross Expected", "EXPECTED BRIDGE", "currency"),
      reportMeasureCol("patientResp", "Patient Responsibility", "EXPECTED BRIDGE", "currency"),
      reportMeasureCol("writeOff", "Write-off Amount", "EXPECTED BRIDGE", "currency"),
      reportMeasureCol("insuranceFloorAdjustment", "Insurance Floor Adjustment", "EXPECTED BRIDGE", "currency"),
      reportMeasureCol("finalInsuranceExpected", "Insurance Amount Expected", "EXPECTED BRIDGE", "currency"),
      reportMeasureCol("primaryExpected", "Final Primary Insurance Expected", "FINAL INSURANCE SPLIT", "currency"),
      reportMeasureCol("secondaryExpected", "Final Secondary Insurance Expected", "FINAL INSURANCE SPLIT", "currency"),
      reportMeasureCol("insurancePaid", "Total Applied Payments", "APPLIED PAYMENTS", "currency"),
      reportMeasureCol("overpaid", "Overpaid", "APPLIED PAYMENTS", "currency"),
      reportMeasureCol("remainingBalance", "Remaining Balance", "BALANCE & PERFORMANCE", "currency"),
      reportRatioCol("collectionsPct", "Collections %", "BALANCE & PERFORMANCE", "paidTowardExpected", "expectedRated"),
      reportRatioCol("writeOffPct", "Write-off %", "BALANCE & PERFORMANCE", "writeOff", "finalInsuranceExpected", "writeOff")
    ],
    drillTitle: (v) => `Service month ${v}`,
    drill: reportDrillByGrain((claim, service) => reportMonthOfDate(service.dos))
  },
  expectedVsPaid: {
    // Names the grouping the reader chose (Payer / Client / Provider / Rate source).
    get title() { return `Expected vs Paid by ${arDimensionLabel(preparedSnapshotDimension("expectedVsPaid"))}`; },
    grainOf: (claim) => reportPayerOf(claim),
    grainLabel: "Payer", columns: REPORT_FINANCIAL_SPINE, drillLabel: "clients",
    drillTitle: (v) => `Payer ${v}`,
    drill: (v) => (claim) => reportPayerOf(claim) === v
  },
  denials: {
    title: "Denials by Payer",
    // This lane had NO population: locally "Denials by Payer" was the whole book, cell
    // for cell the same table as Expected vs Paid under a heading that promised denials.
    // Mirrors the hosted denial signal (backend/reportPreparedSignals.js denialFlag).
    population: (claim) => /denied|denial|reject|appeal|correct/i.test(reportSignalTextOf(claim)),
    grainOf: (claim) => reportPayerOf(claim),
    grainLabel: "Payer", columns: REPORT_FINANCIAL_SPINE, drillLabel: "clients",
    drillTitle: (v) => `Payer ${v}`,
    drill: (v) => (claim) => reportPayerOf(claim) === v
  },
  payerClass: {
    title: "Payer Role, Class and Network",
    // The browser's payer-class report counts a payer wherever it appears (primary OR
    // secondary) and attributes money only to primary. The generic one-row-per-service
    // fold cannot represent that second appearance without dropping it or duplicating
    // money, so keep the purpose-built line-grain renderer/binder in the browser lane.
    browserLineGrainRenderer: true,
    // Mirrors the hosted groupBy ["payerRole","payerClass","payer"] (reportLaneContract.js:20)
    // so both lanes grain a row the same way. Payer Class and Network are not shipped by the
    // warehouse yet; they render as their own stated value rather than silently collapsing
    // into the payer, so a missing dimension reads as missing.
    grainOf: (claim) => [
      String(claim.payerRole || "").trim() || "Primary",
      String(claim.payerClass || "").trim() || "Unclassified",
      reportPayerOf(claim)
    ].join(" · "),
    grainLabel: "Payer Role · Class · Payer", columns: REPORT_FINANCIAL_SPINE,
    drillLabel: "payers",
    drillTitle: (v) => String(v).split(" · ").pop(),
    drill: (v) => (claim) => reportPayerOf(claim) === String(v).split(" · ").pop()
  },
  patientResponsibility: {
    title: "Patient Responsibility",
    grainLabel: "Patient", drillLabel: "claims", sortBy: "label",
    // Patient is the default summary. Insurance and month are explicit alternate
    // views; each row drills through the exact same eligible service-line set.
    columns: [
      reportMeasureCol("patientCount", "Patient Count", "PERIOD & VOLUME", "number"),
      reportMeasureCol("claimCount", "Claim Count", "PERIOD & VOLUME", "number"),
      reportMeasureCol("lines", "Service Line Count", "PERIOD & VOLUME", "number"),
      reportMeasureCol("patientResp", "Patient Responsibility", "EXPECTED BRIDGE", "currency")
    ],
    grainOf: patientResponsibilityReportGrainOf,
    drillTitle: (v) => `${patientResponsibilityReportState.view === "payer" ? "Primary insurance" : patientResponsibilityReportState.view === "dosMonth" ? "Service month" : "Patient"} ${patientResponsibilityReportState.view === "patient" ? patientResponsibilityPatientLabel(v) : v}`,
    drill: reportDrillByGrain(patientResponsibilityReportGrainOf)
  },
  cptPayer: {
    title: "Average Reimbursement Per Unit by CPT and Primary Payer",
    grainLabel: "CPT · Payer", drillLabel: "claims",
    cellDrillAttr: "data-cpt-payer-drill-key",
    cellDrillKey: (row) => cptPayerGroupAttr({ procedure: String(row.dims[0] || "").split(" · ")[0], payer: String(row.dims[0] || "").split(" · ")[1] || "" }),
    // Per-UNIT, which is what the title promises. Declared as ratios so the totals row is
    // sum(amount)/sum(units) — a weighted average — rather than the mean of row averages,
    // which would let one low-volume CPT swing the total.
    columns: [
      reportMeasureCol("lines", "Service Line Count", "PERIOD & VOLUME", "number"),
      reportMeasureCol("units", "Units", "PERIOD & VOLUME", "number"),
      Object.assign(reportRatioCol("avgExpectedPerUnit", "Avg Expected Allowed Per Unit", "EXPECTED BRIDGE", "finalInsuranceExpected", "units"), { kind: "perUnit" }),
      reportMeasureCol("finalInsuranceExpected", "Insurance Amount Expected", "EXPECTED BRIDGE", "currency"),
      Object.assign(reportRatioCol("avgActualPerUnit", "Avg Actual Allowed Per Unit", "APPLIED PAYMENTS", "insurancePaid", "units"), { kind: "perUnit" }),
      reportMeasureCol("insurancePaid", "Total Applied Payments", "APPLIED PAYMENTS", "currency"),
      reportMeasureCol("overpaid", "Overpaid", "APPLIED PAYMENTS", "currency"),
      reportMeasureCol("remainingBalance", "Remaining Balance", "BALANCE & PERFORMANCE", "currency"),
      reportRatioCol("collectionsPct", "Collections %", "BALANCE & PERFORMANCE", "paidTowardExpected", "expectedRated")
    ],
    grainOf: (claim, service) => {
      const cpt = String(service.cpt || service.procedure || service.procedureCode || "").trim();
      return cpt ? `${cpt} · ${reportPayerOf(claim)}` : "";
    },
    drillTitle: (v) => `CPT and payer ${v}`,
    drill: (v) => (claim) => {
      const payer = reportPayerOf(claim);
      return (claim.services || []).some((service) => {
        const cpt = String(service.cpt || service.procedure || service.procedureCode || "").trim();
        return cpt && `${cpt} · ${payer}` === v;
      });
    }
  },
  performance: {
    title: "Performance", sortBy: "label",
    // Billed claims only, as the hosted methodology line states (reportPreparedSignals.js
    // performanceEligibleBase): ready-to-submit and client-action-pending are out.
    population: (claim) => {
      const status = String(claim.claimStatus || claim.status || "").trim().toLowerCase();
      const action = String(claim.followUpAction || claim.action || "").trim().toLowerCase();
      return status !== "ready to submit" && status !== "on hold" && !action.startsWith("client action pending");
    },
    grainOf: (claim, service) => reportMonthOfDate(service.dos),
    grainLabel: "Service Month", columns: REPORT_FINANCIAL_SPINE, drillLabel: "payers",
    drillTitle: (v) => `Service month ${v}`,
    drill: reportDrillByGrain((claim, service) => reportMonthOfDate(service.dos))
  },
  reviewQueue: {
    title: "Review Queue",
    // Hosted grains this by queueReason (reportLaneContract.js:36). A browser-lane claim has
    // no queueReason, so this reads the follow-up action that put it in the queue — the same
    // question, asked of the data this lane actually carries.
    // Open queues only, as the hosted note beneath the report states (reportPreparedSignals.js
    // reviewOpenFlag): a closed queue, or a closed claim where no queue is recorded, is out.
    population: (claim) => {
      const queue = String(claim.queueReason || "").trim().toLowerCase();
      if (queue) return queue !== "closed";
      const category = String(claim.statusCategory || claim.claimStatus || claim.status || "").trim().toLowerCase();
      return category !== "closed" && category !== "paid/closed";
    },
    grainOf: (claim) => reportQueueGrainOf(claim),
    grainLabel: "Work Queue", columns: REPORT_FINANCIAL_SPINE,
    drillLabel: "payers",
    drillTitle: (v) => `Queue: ${v}`,
    drill: (v) => (claim) => reportQueueGrainOf(claim) === v
  },
  clientAction: {
    title: "Claims Waiting on Client Action",
    // Keyed on the follow-up ACTION marker, which is what the legacy tracker's own report
    // keys on. Deliberately not "status is On Hold OR the marker" — that OR admits every
    // on-hold claim into a report about claims blocked on the CLIENT.
    population: (claim) => /^client action pending/i.test(String(claim.followUpAction || claim.action || "").trim()),
    grainOf: (claim) => `${reportClientOf(claim)} · ${normalizeClaimId(claim.claimId || claim.id || "")}`,
    grainLabel: "Client · Claim · Needed action · Owner · Follow-up due", columns: REPORT_FINANCIAL_SPINE, drillLabel: "claims",
    drillTitle: (v) => `Claim waiting on client: ${v}`,
    drill: (v) => (claim) => `${reportClientOf(claim)} · ${normalizeClaimId(claim.claimId || claim.id || "")}` === v
  }
};

// Grain readers used by the drill predicates above. Kept next to the definitions so a new
// report declares its grain in one place.
function reportQueueGrainOf(claim) {
  return String(claim.queueReason || claim.followUpAction || claim.action || "").trim() || "Unassigned";
}

// The text the hosted denial signal reads (status, classification, follow-up action,
// notes), assembled from whichever of those a browser-lane claim carries. Notes can be
// a string or a list of note records; only the text is read.
function reportSignalTextOf(claim) {
  const notes = Array.isArray(claim.notes)
    ? claim.notes.map((note) => (note && typeof note === "object" ? String(note.text || note.note || note.body || "") : String(note || ""))).join(" ")
    : String(claim.notes || "");
  return [claim.claimStatus, claim.status, claim.classification, claim.claimClassification, claim.followUpAction, claim.action, notes]
    .map((value) => String(value || "")).join(" ");
}

function reportMonthOfDate(value) {
  const raw = String(value || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  const us = raw.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})/);
  return us ? `${us[3]}-${us[1]}` : "";
}

// SERVICE DATES LIVE ON THE LINES, not the claim, and one claim can straddle two months.
// A claim therefore belongs to every month it has a line in, and a month row opens the
// claim if ANY line falls there — matching one date would drop the rest of a
// month-straddling claim from its own row.
function reportClaimServiceMonths(claim) {
  const lines = (claim && claim.services) || [];
  const months = new Set();
  lines.forEach((line) => {
    const month = reportMonthOfDate(line && (line.dos || line.serviceDate || line.srvcDate));
    if (month) months.add(month);
  });
  if (!months.size) {
    const fallback = reportMonthOfDate(claim && (claim.serviceDate || claim.srvcDate || claim.dos));
    if (fallback) months.add(fallback);
  }
  return [...months];
}

function reportServiceMonthOf(claim) {
  return reportClaimServiceMonths(claim)[0] || "";
}
function reportPayerOf(claim) {
  return String(claim && (claim.primaryPayer || claim.payer) || "").trim() || "\u2014";
}
function reportClientOf(claim) {
  return String(claim && (claim.client || claim.tenantName) || "").trim() || "\u2014";
}

function reportDefinitionUi(view) {
  return Object.prototype.hasOwnProperty.call(REPORT_DEFINITIONS_UI, view) ? REPORT_DEFINITIONS_UI[view] : null;
}

// The tracker's formats: currency to the cent with NEGATIVES IN PARENTHESES, percent as a
// fraction rendered x100 to 2dp, counts plain. money() elsewhere is 0-decimal and renders
// -$1,234, which is why reports do not reuse it.
const reportCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2, currencySign: "accounting"
});

// Dates in reports are MM/DD/YYYY, zero-padded: 08/06/1997.
// Parsed via localDateFromDateOnly so a date-only string is not shifted a day by UTC.
// formatDate() elsewhere renders 8/6/1997 unpadded, which is why this is separate.
function reportFormatDate(value) {
  const raw = String(value == null ? "" : value).trim();
  if (!raw) return "";
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const parsed = (typeof localDateFromDateOnly === "function" ? localDateFromDateOnly(raw) : null)
    || (Number.isFinite(Date.parse(raw)) ? new Date(Date.parse(raw)) : null);
  if (!parsed || Number.isNaN(parsed.getTime())) return raw;
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${parsed.getFullYear()}`;
}

function reportFormatCell(kind, value) {
  // A date column on a totals row has no range to show. Blank is the truth; "Unavailable"
  // implies something went wrong.
  if ((kind === "date" || kind === "text") && (value === null || value === undefined || String(value).trim() === "")) return "";
  if (value === null || value === undefined || value === "") return "Unavailable";
  // A text cell is not a number. Without this branch Number() returns NaN and the value
  // rendered as "Unavailable".
  if (kind === "date") return reportFormatDate(value);
  if (kind === "text" && String(value).trim() === "") return "";
  if (kind === "text") return String(value);
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Unavailable";
  if (kind === "currency") return reportCurrencyFormatter.format(numeric);
  if (kind === "perUnit") return reportCurrencyFormatter.format(numeric);
  if (kind === "percent") return `${(numeric * 100).toFixed(2)}%`;
  return numeric.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

// A ratio is computed from its component measures on whatever row it is given. Handing it
// the totals row therefore yields sum(numerator)/sum(denominator) for free, which is the
// only correct way to total a percentage — averaging the row percentages is visibly wrong.
function reportRatioValue(col, row) {
  if (!row) return null;
  const num = Number(row[col.numerator]);
  let den = Number(row[col.denominator]);
  if (col.denominatorPlus) den += Number(row[col.denominatorPlus] || 0);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
  return num / den;
}

function reportCellValue(col, row) {
  return col.type === "ratio" ? reportRatioValue(col, row) : (row ? row[col.key] : null);
}

// Marks the first column of each band so a vertical rule can separate the bands. Without
// it seventeen columns read as one undifferentiated wall of numbers.
// A band must appear ONCE. Interleaving columns from two groups produced a header reading
// EXPECTED BRIDGE | APPLIED PAYMENTS | EXPECTED BRIDGE | APPLIED PAYMENTS — the loudest row
// in the table lying about the grouping. Columns are sorted into band order so the run
// grouping below cannot split a band, whatever order a definition lists them in.
function reportColumnsInBandOrder(columns) {
  const list = (columns || []).slice();
  list.sort((a, b) => REPORT_COL_GROUPS.indexOf(a.group) - REPORT_COL_GROUPS.indexOf(b.group));
  return list;
}

function reportBandStarts(columns) {
  const starts = new Set();
  let previous = null;
  columns.forEach((col, index) => {
    if (col.group !== previous) starts.add(index);
    previous = col.group;
  });
  return starts;
}

function reportCellClass(col, index, bandStarts, value) {
  const classes = [`report-col-${col.kind}`];
  if (bandStarts.has(index)) classes.push("is-band-start");
  if (col.kind === "currency" && Number(value) < 0) classes.push("is-negative");
  return classes.join(" ");
}

const REPORT_PAYMENT_EVIDENCE_KEYS = new Set(["primaryApplied", "secondaryApplied", "unattributedApplied", "insurancePaid"]);
function canOpenReportPaymentEvidence() {
  return hasPermission(PERMISSIONS.VIEW_REPORTS) && hasPermission(PERMISSIONS.VIEW_FINANCIALS) && hasPermission(PERMISSIONS.VIEW_PHI);
}
function reportPaymentEvidenceCellHtml(col, value, context, row = {}) {
  const formatted = escapeHtml(reportFormatCell(col.kind, value));
  if (!context || !REPORT_PAYMENT_EVIDENCE_KEYS.has(col.key) || value == null || !Number.isFinite(Number(value)) || !canOpenReportPaymentEvidence()) return formatted;
  // Oversized groups show the plain amount (owner 2026-10-07: no per-row hint); drill into a smaller group to open payment detail.
  if (Number(row.lines) > 1000 || Number(row.claimCount) > 50) return formatted;
  return `<button type="button" class="linkbutton report-payment-evidence-link" data-report-payment-role="${escapeHtml(col.key)}" data-report-payment-amount="${escapeHtml(String(Number(value)))}" data-report-payment-context="${escapeHtml(JSON.stringify(context))}" aria-label="View payment detail for ${escapeHtml(col.label)}, ${escapeHtml(reportFormatCell(col.kind, value))}">${formatted}</button>`;
}

function reportBandedHeaderHtml(columns, grainLabel, showDrill) {
  const bands = [];
  let run = null;
  columns.forEach((col) => {
    if (run && run.group === col.group) { run.span += 1; return; }
    run = { group: col.group, span: 1 };
    bands.push(run);
  });
  const drillHead = showDrill ? `<th class="report-band report-drill-band">Drilldown</th>` : "";
  const bandRow = `<tr class="report-band-row">${drillHead}<th class="report-grain-head"></th>${bands.map((b) => `<th colspan="${b.span}" class="report-band is-band-start">${escapeHtml(b.group)}</th>`).join("")}</tr>`;
  const starts = reportBandStarts(columns);
  const headRow = `<tr class="report-head-row">${showDrill ? '<th class="report-grain-head"></th>' : ""}<th class="report-grain-head">${escapeHtml(grainLabel)}</th>${columns.map((c, i) => `<th class="report-col-${c.kind}${starts.has(i) ? " is-band-start" : ""}">${escapeHtml(c.label)}</th>`).join("")}</tr>`;
  return `<thead>${bandRow}${headRow}</thead>`;
}

// Returns null when the view has no definition, so undefined reports keep their old table.
function renderReportDefinitionTable(view, model, groups, totals, override) {
  const def = reportDefinitionUi(view);
  if (!def && !override) return null;
  const columns = reportColumnsInBandOrder((override && override.columns) || def.columns);
  const grainLabel = (override && override.grainLabel) || (def && def.grainLabel) || preparedReportGroupHeading(view, model.dimension);
  const starts = reportBandStarts(columns);
  const drillAttr = (override && override.drillAttr) || "data-report-drill";
  // Derived, never declared per report: the first rung is always the head of
  // REPORT_DRILL_CHAIN, so cptPayer/clientAction declaring "claims" printed "View claims →"
  // on a button that opens payers.
  const firstRungKey = reportDrillChainForView(view)[0];
  const firstRungLabel = (REPORT_DRILL_LEVELS_UI[firstRungKey] || {}).label || "detail";
  const drillLabel = override ? override.drillLabel : (def && def.drillLabel ? firstRungLabel : "");
  // A DRILL BUTTON ONLY RENDERS WHERE IT CAN BE BOUND. bindReportDrillControls is reached
  // only behind !trackerApiEnabled() (the definition lane), while renderPreparedSnapshotReport
  // shares this table in hosted mode — so hosted pages were emitting "View payers →" buttons
  // that no handler ever attached to. A button that does nothing is worse than no button.
  const drillIsBindable = override ? true : (typeof trackerApiEnabled !== "function" || !trackerApiEnabled());
  // A DRILL RUNG SUPPLIES ITS OWN PER-ROW ACTION. Its next rung is not a report grain and
  // changes shape at the leaf, where "View detail" becomes "Open claim", so rowDrill counts
  // as drillable by itself. Absent, every existing caller keeps the exact button it had.
  const rowDrill = override && typeof override.rowDrill === "function" ? override.rowDrill : null;
  const canDrill = Boolean(rowDrill) || (drillIsBindable && (override ? Boolean(override.drillLabel) : (def && typeof def.drill === "function")));
  const rows = (groups || []).map((g) => {
    // A DRILL ROW IS NOT A REPORT GROUP. It has an identity (payer / patient / claim /
    // service line), not a dims tuple, so it names its own row header. Same table either way.
    const label = override && typeof override.rowLabel === "function" ? override.rowLabel(g) : preparedReportGroupLabel(view, g.dims);
    const cells = columns.map((col, i) => {
      const value = reportCellValue(col, g);
      const paymentContext = override && typeof override.paymentContext === "function" ? override.paymentContext(g) : null;
      return `<td class="${reportCellClass(col, i, starts, value)}">${reportPaymentEvidenceCellHtml(col, value, paymentContext, g)}</td>`;
    }).join("");
    // The button carries the drill, NOT the <tr>. role="button" on a row overrides
    // role="row" and takes the header/cell associations with it, which leaves a screen
    // reader with one button named after a run-together string of numbers.
    // THE BUTTON CARRIES THE GRAIN KEY, NOT THE DISPLAY LABEL. preparedReportGroupLabel
    // formats some grains for reading — a month row keys on "2025-11" and reads "Nov 2025" —
    // while the drill predicate matches on the KEY. Sending the label made performance drill
    // into a month matching no claims, dead-ending on an empty payer list under a breadcrumb
    // that claimed it had gone somewhere. The label rides along for the breadcrumb to read.
    const drillValue = override && typeof override.drillValue === "function" ? override.drillValue(g) : String(g.dims && g.dims.length ? g.dims[0] : label);
    const drillCell = rowDrill
      ? `<td class="report-drill-cell">${rowDrill(g)}</td>`
      : canDrill
      ? `<td class="report-drill-cell"><button type="button" class="small-button report-drill-btn" ${drillAttr}="${escapeHtml(String(drillValue))}" data-report-drill-label="${escapeHtml(label)}">View ${escapeHtml(drillLabel || "detail")} \u2192</button></td>`
      : "";
    // Some reports carry a SECOND, cell-grain drill contract that predates this table
    // (cptPayer: data-cpt-payer-drill-key). Its verifier pins the trigger COUNT, which is
    // what catches a drill wired to the wrong cell, so the key is emitted here rather than
    // the contract being dropped when this lane took the view over.
    // The key is an ENCODED COMPOSITE IDENTIFIER (procedure||payer, URI-encoded), not the
    // display label. Emitting the label made the trigger unresolvable — cptPayerCellLines
    // matches on cptPayerGroupAttr(row), so a label key finds no lines, which is precisely
    // the "drill opens the wrong cell" failure this contract exists to catch.
    // ...AND ONLY WHERE ITS BINDER RUNS. bindCptPayerReportControls is called from the
    // browser-lane branch of renderReports only; the hosted prepared lane shares this
    // table and was emitting the same role="button" tabindex="0" cell - blue, underlined,
    // pointer cursor - that no handler ever attached to. Measured on hosted TEST
    // 2026-09-03: click and Enter on a CPT x Payer cell changed nothing. Same rule as the
    // row drill button above (drillIsBindable); a drill rung (override) is never a report
    // grain and never carries the cell contract.
    const cellDrillable = !override && drillIsBindable && def && def.cellDrillAttr && typeof def.cellDrillKey === "function";
    const cellKey = cellDrillable
      ? ` ${def.cellDrillAttr}="${escapeHtml(def.cellDrillKey(g))}" role="button" tabindex="0"`
      : "";
    const grainClass = cellDrillable ? "report-grain-cell ar-drillable" : "report-grain-cell";
    return `<tr class="${canDrill ? "is-drillable" : ""}">${drillCell}<th scope="row" class="${grainClass}"${cellKey}>${escapeHtml(label)}</th>${cells}</tr>`;
  }).join("");
  const empty = `<tr><td colspan="${columns.length + (canDrill ? 2 : 1)}">${escapeHtml((override && override.emptyText) || preparedSnapshotEmptyGroupsText(totals))}</td></tr>`;
  const totalCells = columns.map((col, i) => {
    const value = reportCellValue(col, totals);
    return `<td class="${reportCellClass(col, i, starts, value)}">${escapeHtml(reportFormatCell(col.kind, value))}</td>`;
  }).join("");
  // A PAGED RUNG HAS NO HONEST FOOTER. The drill returns one page of N; summing the visible
  // page would print a total smaller than the row that opened it - the same class of lie
  // backend/verify_report_drill_date_range.js exists to prevent. hideTotals omits it rather
  // than inventing one. The report lane never passes it, so its footer is unchanged.
  const foot = override && override.hideTotals
    ? ""
    : `<tfoot><tr>${canDrill ? '<td class="report-drill-cell"></td>' : ""}<th scope="row" class="report-grain-cell">Total</th>${totalCells}</tr></tfoot>`;
  const tableClass = `report-definition-table${override && override.tableClass ? ` ${override.tableClass}` : ""}`;
  return `<div class="table-wrap report-definition-wrap"><table class="${tableClass}">${reportBandedHeaderHtml(columns, grainLabel, canDrill)}<tbody>${rows || empty}</tbody>${foot}</table></div>`;
}


// ---------------------------------------------------------------------------
// MEASURE ROWS FROM CLAIMS.
//
// The reports lane on the server SUMs t1a columns (backend/reportT1aSql.MEASURE_SQL).
// In the browser the same figures already exist per line on
// claimFinancialView().serviceFinancials — the engine's own output — so this groups those
// lines and sums the SAME fields. It is a projection, not a calculation: every term below
// mirrors one MEASURE_SQL term, and nothing here re-derives money.
//
//   MEASURE_SQL.totalExpected           COALESCE(NULLIF(static_total_expected,0),
//                                                final_expected_total_numeric, total_expected, 0)
//   MEASURE_SQL.finalInsuranceExpected  COALESCE(final_insurance_expected, ...)
//   MEASURE_SQL.insurancePaid           COALESCE(final_insurance_paid, total_paid, 0)
//   MEASURE_SQL.outstanding             COALESCE(final_insurance_balance, owed, balance, 0)
//
// Patient responsibility uses the LINE'S patientResponsibility, which is the engine's
// authoritative figure (financialCalculationEngine.js:608). Recomputing it from
// positive-balance + patient-paid is the second definition trackerWorkbookExport.js:477
// warns about, and it understates PR on any line carrying adjustment detail.
const REPORT_MEASURE_KEYS_UI = ["lines", "units", "charges", "totalExpected", "patientResp",
  "writeOff", "insuranceFloorAdjustment", "finalInsuranceExpected", "primaryExpected",
  "secondaryExpected", "insurancePaid", "primaryApplied", "secondaryApplied", "unattributedApplied", "outstanding",
  "billedExpected", "finalExpectedTotal", "expectedRevision",
  "expectedRated", "paidTowardExpected", "overpaid", "overpaidLines", "paidNoRate", "remainingBalance"];

// Mirror of backend/reportT1aSql.EXPECTED_DEPENDENT_MEASURES, less the three this lane
// does not project (owed, openLines, openOwed). A group with no priced line has no
// figure for any of these; see reportMeasureRowsFromClaims.
const REPORT_EXPECTED_DEPENDENT_KEYS_UI = ["totalExpected", "finalInsuranceExpected", "writeOff", "outstanding",
  "primaryExpected", "secondaryExpected", "insuranceFloorAdjustment", "billedExpected", "finalExpectedTotal", "expectedRevision",
  "remainingBalance"];

// The engine derives finalInsuranceExpected from finalExpectedTotalNumeric, so anything
// that has to foot against it reads the same term. Explicit 0 is honoured.
function reportFinalExpectedOf(line) {
  const fe = line && line.finalExpectedTotalNumeric;
  if (fe !== null && fe !== undefined && String(fe).trim() !== "") return reportNum(fe);
  return reportNum(line && line.staticTotalExpected);
}

function reportEmptyMeasureRow() {
  const row = {};
  REPORT_MEASURE_KEYS_UI.forEach((key) => { row[key] = 0; });
  return row;
}

function reportNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// grainOf(claim, service, lineFinancial) -> a string key, or "" to drop the line.
// EVERY grain value a claim contributes to a report, read through the report's OWN grainOf
// over the SAME projection the rows are built from.
//
// Drill predicates used to re-derive the grain with a parallel helper. On `performance` that
// silently disagreed with grainOf: the report listed a "Nov 2025" row and clicking it matched
// zero claims, so the chain dead-ended on an empty payer list. A predicate that does not use
// the function that built the row can always drift from it; this one cannot.
function reportGrainValuesOf(claim, grainOf) {
  const fin = claimFinancialView(claim);
  const lines = (fin && fin.serviceFinancials) || [];
  const services = claim.services || [];
  const values = new Set();
  lines.forEach((line, index) => {
    values.add(String(grainOf(claim, services[index] || {}, line) || "") || "(blank)");
  });
  return values;
}

// Drill by grain: the row you clicked and the claims you get are the same computation.
function reportDrillByGrain(grainOf) {
  return (value) => (claim) => reportGrainValuesOf(claim, grainOf).has(value);
}

// Mirror of backend/reportT1aSql REPORT_NO_RATE_ROW_SQL and the capped MEASURE_SQL entries.
// A line with no rate on file (or an unknown expected) has nothing to cap against.
const REPORT_CAPPED_MEASURE_KEYS_UI = ["expectedRated", "paidTowardExpected", "overpaid", "overpaidLines", "paidNoRate", "remainingBalance"];
const REPORT_NO_RATE_STATUSES_UI = new Set(["no primary rate", "no secondary rate", "missing fee schedule"]);
function reportCappedLineMeasures(line = {}) {
  const rawExpected = line.finalInsuranceExpected;
  const noRate = rawExpected === null || rawExpected === undefined || rawExpected === ""
    || REPORT_NO_RATE_STATUSES_UI.has(String(line.finalizedMatch || "").trim().toLowerCase());
  const expected = reportNum(rawExpected);
  const paid = reportNum(line.finalInsurancePaid);
  const over = !noRate && paid > expected;
  return {
    expectedRated: noRate ? 0 : expected,
    paidTowardExpected: noRate ? 0 : (over ? expected : paid),
    overpaid: over ? paid - expected : 0,
    overpaidLines: over && paid - expected > 10 ? 1 : 0,
    paidNoRate: noRate ? paid : 0,
    remainingBalance: expected > paid ? expected - paid : 0
  };
}

function reportMeasureRowsFromClaims(claims, grainOf) {
  const byGrain = new Map();
  (claims || []).forEach((claim) => {
    const fin = claimFinancialView(claim);
    const lines = (fin && fin.serviceFinancials) || [];
    const services = claim.services || [];
    lines.forEach((line, index) => {
      // serviceFinancials is built from claim.services in order, so index pairs them.
      // The service carries the date and CPT; the line carries the money.
      const service = services[index] || {};
      // A line whose grain will not resolve — no CPT, an unparseable service date — used
      // to be DROPPED, so the report silently understated its own book. One unreadable
      // date could take 90% of the money off the screen with no warning. It now files
      // into (blank), which is the legacy tracker's own convention and keeps every
      // report footing to the same total.
      const key = String(grainOf(claim, service, line) || "") || "(blank)";
      let row = byGrain.get(key);
      if (!row) {
        row = reportEmptyMeasureRow();
        row.dims = [key];
        row.claimIdSet = new Set();
        row.unratedClaimIdSet = new Set();
        row.unratedLines = 0;
        byGrain.set(key, row);
      }
      row.lines += 1;
      // An unpriced line contributes nothing to expected. Counting it keeps the report
      // able to SAY so, instead of letting a missing rate read as a confident $0.00.
      if (line.primaryRateMissing || line.primaryRateAbsent) {
        row.unratedLines = (row.unratedLines || 0) + 1;
        row.unratedClaimIdSet.add(claim.claimId);
      }
      row.units += reportNum(service.units !== undefined ? service.units : line.units);
      row.charges += reportNum(line.charges);
      // ONE BASIS. The floor plug and finalInsuranceExpected are both derived from the FINAL
      // expected, so a gross value on the static basis left the bridge mixed and able to print a
      // negative expected. The rollforward shows the billed figure separately.
      row.totalExpected += reportFinalExpectedOf(line);
      // Billed-at-submission vs what expected became. Their difference is the rollforward.
      row.billedExpected += reportNum(line.staticTotalExpected) || reportNum(line.finalExpectedTotalNumeric);
      // An explicit 0 is a REAL final expected (takeback / full reversal). `||` treats it as
      // missing and falls back to the billed figure, which prints gross against the engine's
      // zero and breaks billed + revision = gross.
      row.finalExpectedTotal += reportFinalExpectedOf(line);
      // totalExpectedChangeNumeric IS this quantity, computed by the engine. Deriving it
      // again meant that a source-supplied change value and our subtraction could disagree.
      row.expectedRevision += line.totalExpectedChangeNumeric !== undefined
        && line.totalExpectedChangeNumeric !== null
        ? reportNum(line.totalExpectedChangeNumeric)
        : (reportNum(line.finalExpectedTotalNumeric) || reportNum(line.staticTotalExpected))
          - (reportNum(line.staticTotalExpected) || reportNum(line.finalExpectedTotalNumeric));
      row.patientResp += Math.max(0, reportNum(line.patientResponsibility));
      row.writeOff += reportNum(line.writeOffAmount);
      // CT :3461 / trackerWorkbookExport.js:504 — the plug that keeps the bridge footing.
      // Summed PER LINE because SUM(MAX(0,x)) is not MAX(0,SUM(x)); computing it from the
      // group totals would silently under-report the clip.
      row.insuranceFloorAdjustment += Math.max(0, reportNum(line.writeOffAmount)
        + Math.max(0, reportNum(line.patientResponsibility))
        - (reportNum(line.finalExpectedTotalNumeric) || reportNum(line.staticTotalExpected)));
      row.finalInsuranceExpected += reportNum(line.finalInsuranceExpected);
      // CT :3465-3466 / trackerWorkbookExport.js:512-514 — secondary is capped at insurance
      // expected first, primary takes the remainder, so the split sums to Final Insurance
      // expected rather than to gross. The raw pair summed to the billed expectation and carried no
      // information, which the export documents as a defect it already fixed once.
      const lineFinalIns = reportNum(line.finalInsuranceExpected);
      const cappedSecondary = Math.min(Math.max(0, reportNum(line.secondaryExpected)), lineFinalIns);
      row.secondaryExpected += cappedSecondary;
      row.primaryExpected += Math.max(0, lineFinalIns - cappedSecondary);
      row.insurancePaid += reportNum(line.finalInsurancePaid);
      row.primaryApplied += reportNum(firstPresent(line.primaryPaid, service.primaryPaid, 0));
      row.secondaryApplied += reportNum(firstPresent(line.secondaryPaid, service.secondaryPaid, 0));
      row.unattributedApplied += reportNum(firstPresent(line.unmatchedPaid, service.unmatchedPaid, 0));
      row.outstanding += reportNum(line.finalInsuranceBalance);
      const capped = reportCappedLineMeasures(line);
      REPORT_CAPPED_MEASURE_KEYS_UI.forEach((key) => { row[key] += capped[key]; });
      row.claimIdSet.add(claim.claimId);
    });
  });
  const rows = [...byGrain.values()].map((row) => {
    row.claimCount = row.claimIdSet.size;
    row.unratedClaims = row.unratedClaimIdSet.size;
    delete row.claimIdSet;
    delete row.unratedClaimIdSet;
    // ALL-UNRATED IS UNKNOWN, NOT $0.00. The note under the table already said so ("every
    // expected, balance and %-collected figure below is unavailable rather than zero")
    // while the cells beside it printed $0.00 - the local Client Action report did exactly
    // that. Same rule the SQL lane applies (reportT1aSql roundMeasures over
    // EXPECTED_DEPENDENT_MEASURES): a group with no priced line has no expected-dependent
    // figure, and reportFormatCell prints null as "Unavailable". A PARTLY unrated group
    // keeps its partial sum and the note names the shortfall, as before.
    if (row.lines > 0 && row.unratedLines >= row.lines) REPORT_EXPECTED_DEPENDENT_KEYS_UI.forEach((key) => { row[key] = null; });
    return row;
  });
  return rows;
}

function patientResponsibilityPatientIdentity(claim = {}) {
  const orgId = String(claim.orgId || claim.organizationId || claim.tenantId || claim.client || "").trim();
  const patientId = String(claim.patientId || claim.patientKey || "").trim();
  const patientName = String(claim.patient || claim.patientName || "").trim() || "—";
  return JSON.stringify([orgId, patientId || `name:${patientName}`]);
}

function patientResponsibilityPatientGrain(claim = {}) {
  const patient = String(claim.patient || claim.patientName || "Unknown patient").trim() || "Unknown patient";
  return [patientResponsibilityPatientIdentity(claim), patient, reportClientOf(claim)].join("\u001f");
}

function patientResponsibilityPatientLabel(value) {
  const parts = String(value || "").split("\u001f");
  return parts.length >= 3 ? [parts[1], parts[2]].filter(Boolean).join(" · ") || "—" : String(value || "—");
}

function reportMeasureTotals(rows) {
  const totals = reportEmptyMeasureRow();
  totals.patientCount = 0;
  totals.claimCount = 0;
  totals.unratedLines = 0;
  totals.unratedClaims = 0;
  (rows || []).forEach((row) => {
    REPORT_MEASURE_KEYS_UI.forEach((key) => { totals[key] += reportNum(row[key]); });
    totals.patientCount += reportNum(row.patientCount);
    totals.claimCount += reportNum(row.claimCount);
    totals.unratedLines += reportNum(row.unratedLines);
    totals.unratedClaims += reportNum(row.unratedClaims);
  });
  // The totals row follows the same rule as its rows: nothing priced, nothing stated.
  if (totals.lines > 0 && totals.unratedLines >= totals.lines) REPORT_EXPECTED_DEPENDENT_KEYS_UI.forEach((key) => { totals[key] = null; });
  return totals;
}

// Sort so a report reads the way the tracker's does: buckets and months ascending,
// everything else by size so the biggest problem is at the top.
function reportSortRows(view, rows) {
  const def = reportDefinitionUi(view);
  const ordered = (rows || []).slice();
  if (preparedReportOrderedGroups(view, ordered) !== ordered) return preparedReportOrderedGroups(view, ordered);
  if (def && def.sortBy === "label") {
    ordered.sort((a, b) => String(a.dims[0]).localeCompare(String(b.dims[0]), undefined, { numeric: true }));
  } else if (def && def.sortBy === "bucket" && Array.isArray(AR_AGING_BUCKETS)) {
    // Not Billed leads: it precedes aging entirely, and it is the queue to clear first.
    const order = [AR_NOT_BILLED_BUCKET.key, ...AR_AGING_BUCKETS.map((b) => b.key), "(blank)"];
    const rank = (k) => { const i = order.indexOf(k); return i === -1 ? order.length : i; };
    ordered.sort((a, b) => rank(a.dims[0]) - rank(b.dims[0]));
  } else {
    // BIGGEST PROBLEM FIRST MEANS BIGGEST COLLECTABLE PROBLEM FIRST.
    //
    // This sorted on `outstanding`, the SIGNED net - which carries overpaid and closed
    // lines as negatives. A payer sitting on a large credit therefore sank to the
    // BOTTOM of a report whose whole purpose is to put the worst row on top; measured
    // live, one payer group read -$647,957. `openOwed` is the open-AR basis (the same
    // one AR Aging now renders as "Open Insurance AR") and is what a biller can
    // actually act on, so it leads when the rollup supplies it.
    //
    // `outstanding` stays as the tiebreak rather than being replaced: a lane that does
    // not serve openOwed must still sort by something, and falling back to the signed
    // net is better than falling back to nothing.
    ordered.sort((a, b) => {
      const remaining = reportNum(b.remainingBalance) - reportNum(a.remainingBalance);
      if (remaining) return remaining;
      const open = reportNum(b.openOwed) - reportNum(a.openOwed);
      if (open) return open;
      return reportNum(b.outstanding) - reportNum(a.outstanding);
    });
  }
  return ordered;
}

// Full panel for any report that has a definition and a line-grain reader.
// An expected figure computed over partly-unpriced lines is not wrong, but it is not the
// whole book either, and every %-collected figure built on it reads high. Say so, using
// the same coverage language the other reports use (REPORT_EXPECTED_UNKNOWN_SENTENCE).
function reportUnpricedNoteHtml(totals = {}, claimCount = 0) {
  const unratedLines = Math.max(0, Number(totals.unratedLines) || 0);
  if (!unratedLines) return "";
  const unratedClaims = Math.max(0, Number(totals.unratedClaims) || 0);
  const lines = Math.max(0, Number(totals.lines) || 0);
  const allUnknown = lines > 0 && unratedLines >= lines;
  const detail = allUnknown
    ? `No line in this view carries a rate, so every expected, balance and %-collected figure below is unavailable rather than zero.`
    : `${escapeHtml(formatNounCount(unratedLines, "service line"))} of ${escapeHtml(formatNounCount(lines, "service line"))}${unratedClaims ? ` (across ${escapeHtml(formatNounCount(unratedClaims, "claim"))})` : ""} carry no rate. They contribute <strong>nothing</strong> to Gross Expected — that is unknown, not $0.00 — so expected is understated here and every %-collected figure reads high.`;
  return `<p class="report-dataset-note report-partial-note reports-no-print" role="status">${detail} ${escapeHtml(REPORT_EXPECTED_UNKNOWN_SENTENCE)}</p>`;
}

// The legacy tracker prints one line under every report:
//   "LITE Monthly Summary | Showing 1-20 of 20 rows | Blossom ABA | Updated 08-17-2026 11:54 AM ET"
// It is the two things a biller reads first to decide whether to trust the number — how
// much of the book is on screen, and how stale it is. We had neither.
function reportCaptionHtml(def, rowCount, slice = null) {
  // NOT guessed this time. state.tenantName is read elsewhere but never assigned, and
  // reportDataAsOfLabel does not exist — both would have rendered nothing forever.
  // formatDateTime + reportDatasetState.fetchedAt is what clientHomeAsOfLabel uses.
  const tenant = String(state?.tenantName || "").trim();
  const fetchedAt = reportDatasetState && reportDatasetState.fetchedAt;
  const prepared = fetchedAt ? formatDateTime(fetchedAt) : "";
  const stamp = prepared && prepared !== "None" ? prepared : "";
  const total = Math.max(0, Number(rowCount) || 0);
  const first = total > 0 && slice ? Math.max(1, Number(slice.start) + 1 || 1) : (total > 0 ? 1 : 0);
  const visibleCount = slice && Array.isArray(slice.rows) ? slice.rows.length : total;
  const last = total > 0 ? Math.min(total, first + Math.max(0, visibleCount) - 1) : 0;
  const visibleRange = total === 0
    ? "0 of 0 rows"
    : total === 1
      ? "1 row"
      : `${first}-${last} of ${total} rows`;
  const parts = [
    escapeHtml(def.title || ""),
    `Showing ${visibleRange}`
  ];
  if (tenant) parts.push(escapeHtml(tenant));
  if (stamp) parts.push(`Updated ${escapeHtml(stamp)}`);
  return `<p class="report-caption-line">${parts.join(" | ")}</p>`;
}

// The tracker puts a short band of figures above every report — Gross Expected,
// Insurance Amount Expected, Total Applied Payments, Open Insurance AR and the signed
// net — so the headline is readable
// without parsing a 15-column table. Driven off the definition's own columns, so a report
// that does not carry a measure simply does not get a tile for it.
const REPORT_KPI_KEYS = [
  ["totalExpected", "Gross Expected"],
  ["finalInsuranceExpected", "Insurance Amount Expected"],
  ["insurancePaid", "Total Applied Payments"],
  // OPEN INSURANCE AR SITS BESIDE THE NET, ON THE HEADLINE, NOT ONLY IN THE TABLE.
  //
  // The spine fix gave the TABLE two columns - "Open Insurance AR" (positive-only
  // owed) next to "Net Balance (incl. credits)" - but this band was left with the
  // signed net alone. Measured live for one practice on the same day: AR Aging
  // headlined MINUS $565,234.16 while Client Receivables, the other screen the same
  // client can open, reported $2,585,357.14 of insurance AR - a $3,150,591.30
  // disagreement between two screens about the same book at the same moment. The 121+
  // bucket read minus $1,938,073.64 because $446,376.61 of collectable AR was netted
  // against $2,384,450.25 of credits.
  //
  // The negative is NOT the defect and is not clamped: a signed net including credits
  // is a real figure and the reference portal shows it too. The defect was showing the
  // client ONLY that figure. Summing the dataset's positive-only owed per bucket comes
  // to $2,585,357.14 - the Receivables aggregate to the cent - so the reconciling
  // number was already computed and served (reportT1aSql openOwed, reportRollupSource
  // openOwed) and simply never made the headline.
  //
  // reportKpiChosenKeys filters to measures the active report actually carries, so a
  // definition without openOwed (rollforward, cptPayer) is untouched.
  ["openOwed", "Open Insurance AR"],
  // Capped per line (2026-10-12); the excess sits in its own Overpaid tile.
  ["remainingBalance", "Remaining Balance"],
  ["overpaid", "Overpaid"],
  ["outstanding", "Remaining Balance"]
];

// Which measures appear as tiles is the user's call — the tracker calls this "Configure
// summary metrics". Default is the four headline figures. The picker offers only measures
// the report actually carries, so it can never offer a tile the table cannot back.
const reportKpiChoice = { keys: null };
let reportKpiPickerOpen = false;

function reportKpiCandidates(def) {
  return (def.columns || []).filter((c) => c.kind === "currency" || c.kind === "number");
}

function reportKpiChosenKeys(def) {
  const present = new Set(reportKpiCandidates(def).map((c) => c.key));
  if (reportKpiChoice.keys) return reportKpiChoice.keys.filter((k) => present.has(k));
  const headline = REPORT_KPI_KEYS.map(([k]) => k).filter((k) => present.has(k));
  // A REPORT WITH NONE OF THE HEADLINE MEASURES STILL GETS A HEADLINE. Patient
  // Responsibility carries none of the five (its money column is patientResp), so it
  // rendered an empty band with only "Configure summary metrics" under the caption - a
  // headline the reader had to switch on by hand on every visit. The report's own count
  // and money columns are the honest default; the picker still narrows them.
  return headline.length ? headline : reportKpiCandidates(def).map((c) => c.key);
}

function reportKpiPickerHtml(def) {
  const chosen = new Set(reportKpiChosenKeys(def));
  const options = reportKpiCandidates(def).map((c) =>
    `<button type="button" class="report-slicer-option${chosen.has(c.key) ? " is-on" : ""}" data-report-kpi="${escapeHtml(c.key)}">${escapeHtml(c.label)}</button>`).join("");
  return `<div class="report-kpi-picker"><button type="button" class="ghost-button" data-report-kpi-toggle>Configure summary metrics</button>${reportKpiPickerOpen ? `<div class="report-slicer-options">${options}</div>` : ""}</div>`;
}

function bindReportKpiPicker() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelector("[data-report-kpi-toggle]")?.addEventListener("click", () => {
    reportKpiPickerOpen = !reportKpiPickerOpen;
    renderReports();
  });
  host.querySelectorAll("[data-report-kpi]").forEach((el) => el.addEventListener("click", () => {
    const def = reportDefinitionUi(reportArState.view);
    const key = el.getAttribute("data-report-kpi");
    const current = new Set(reportKpiChosenKeys(def));
    if (current.has(key)) current.delete(key); else current.add(key);
    // Keep the report's own column order, not click order.
    reportKpiChoice.keys = reportKpiCandidates(def).map((c) => c.key).filter((k) => current.has(k));
    renderReports();
  }));
}

function reportKpiTilesHtml(def, totals) {
  const labelOf = new Map((def.columns || []).map((c) => [c.key, c.label]));
  const tiles = reportKpiChosenKeys(def).map((key) => {
    // THE DEFINITION'S OWN LABEL WINS. This read the generic table FIRST, so the tile
    // over `outstanding` still said "Remaining Balance" long after the spine column
    // was renamed "Net Balance (incl. credits)" (see REPORT_FINANCIAL_SPINE) - the
    // headline called a credit-inclusive net a remaining balance while the column two
    // inches below it did not. Reports that genuinely label outstanding "Remaining
    // Balance" in their own columns (rollforward, cptPayer) still read that way.
    const label = labelOf.get(key) || (REPORT_KPI_KEYS.find(([k]) => k === key) || [])[1] || key;
    const value = reportNum(totals[key]);
    // Remaining Balance is the one a biller opens the report for, so it carries the accent;
    // a NEGATIVE balance is an overpayment and reads as its own thing, not as a win.
    const tone = key === "remainingBalance" ? "primary" : key === "outstanding" ? (value < 0 ? "warn" : "primary") : "";
    const detail = key === "outstanding" && value < 0 ? "Overpaid — collected above expected" : "";
    const col = (def.columns || []).find((c) => c.key === key);
    // MISSING IS NOT ZERO. reportNum() turns an absent or guarded-unknown measure into
    // 0, which on a currency tile reads as a real $0.00. The table cell for the same
    // measure already renders "Unavailable" (reportFormatCell), and the tile must say
    // the same thing - otherwise a lane that does not compute openOwed would headline
    // "Open Insurance AR $0.00" over a book with millions open.
    const served = totals[key] !== null && totals[key] !== undefined && totals[key] !== "" && Number.isFinite(Number(totals[key]));
    return arKpiCard(label, reportFormatCell(col ? col.kind : "currency", served ? value : null), detail, tone);
  }).join("");
  return `${tiles ? `<div class="ar-kpi-grid report-kpi-grid">${tiles}</div>` : ""}${reportKpiPickerHtml(def)}`;
}

// Hosted reports mirror the six complete-population SQL filters already owned by the
// report toolbar. Options come from those authoritative controls, never the current claim
// page, so paging cannot make a payer/client disappear from the slicer board.
const HOSTED_REPORT_SLICERS = [
  { key: "tenant", label: "Client", control: "reportTenantFilter" },
  { key: "payer", label: "Payer", control: "reportPayerFilter" },
  { key: "provider", label: "Provider", control: "reportProviderFilter" },
  { key: "location", label: "Location", control: "reportLocationFilter" },
  { key: "aging", label: "Aging", control: "reportAgingFilter" },
  { key: "status", label: "Claim Status", control: "reportStatusFilter" }
];

// THE HOSTED MIRROR TELLS THE TRUTH ABOUT A LIST IT CANNOT SHOW.
//
// This strip is six <select>s mirroring six of the bar's filters, and a
// <select> cannot express "Aetna and UHC" or "not these five". Rather than
// showing "All" over a narrowed report - which is the exact untruth the filter
// bar's own badge exists to prevent - a multi-value or excluding filter shows a
// selected, disabled option that states the narrowing in the same sentence the
// chip rail uses, and directs the reader to the card that owns it.
function hostedReportSlicerMirrorState(key) {
  const values = reportFilterKeyIsMultiSelect(key) ? reportFilterSelections(key) : [];
  const exclude = reportFilterKeyIsMultiSelect(key) && reportFilterExcludes(key);
  if (key === "tenant") {
    const tenant = String(els.reportTenantFilter?.value || "All");
    return { active: tenant !== "All", multi: false, value: tenant, summary: "" };
  }
  if (!values.length) return { active: false, multi: false, value: "All", summary: "" };
  if (values.length === 1 && !exclude) return { active: true, multi: false, value: values[0], summary: "" };
  return { active: true, multi: true, value: "", summary: filterSelectionSummaryText(key, values, exclude) };
}

function hostedReportSlicerPanelHtml() {
  const active = HOSTED_REPORT_SLICERS.reduce((count, slicer) => count + (hostedReportSlicerMirrorState(slicer.key).active ? 1 : 0), 0);
  const controls = HOSTED_REPORT_SLICERS.map((slicer) => {
    const source = els[slicer.control];
    const mirror = hostedReportSlicerMirrorState(slicer.key);
    const options = Array.from(source?.options || []).map((option) => {
      const value = String(option.value == null ? "" : option.value);
      const label = String(option.textContent || option.label || value || "All");
      return `<option value="${escapeHtml(value)}"${!mirror.multi && mirror.value === value ? " selected" : ""}>${escapeHtml(label)}</option>`;
    }).join("") || '<option value="All">All</option>';
    const multiOption = mirror.multi
      ? `<option value="__multi__" selected disabled>${escapeHtml(mirror.summary)}</option>`
      : "";
    const note = mirror.multi
      ? `<small class="muted-note">Set in Report filters &rsaquo; ${escapeHtml(slicer.label)}</small>`
      : "";
    return `<label class="report-slicer"><span class="report-slicer-label">${escapeHtml(slicer.label)}</span><select data-hosted-report-slicer="${escapeHtml(slicer.key)}">${multiOption}${options}</select>${note}</label>`;
  }).join("");
  return `<section class="panel report-slicer-panel" data-hosted-report-slicers><div class="report-slicer-bar"><strong>Report filters${active ? ` (${active} active)` : ""}</strong><span class="muted-note">${active ? "Filtered complete population" : "Showing the complete population"}</span><button type="button" class="ghost-button" data-hosted-report-slicer-open>${reportSlicersOpen ? "Hide filters" : "Show filters"}</button></div>${reportSlicersOpen ? `<div class="report-slicer-grid">${controls}</div>` : ""}</section>`;
}

function patientResponsibilityClaimHasSecondary(claim = {}) {
  const claimSecondary = firstPresent(claim.secondaryPayer, claim.secondary_payer, claim.secondaryPayerExternalId, claim.secondaryExternalId);
  if (booleanFromSource(firstPresent(claim.hasSecondaryPayer, claim.has_secondary_payer, null)) === true || hasRealPayer(claimSecondary)) return true;
  return (claim.services || []).some((service) => booleanFromSource(firstPresent(service.hasSecondaryPayer, service.has_secondary_payer, null)) === true
    || hasRealPayer(firstPresent(service.secondaryPayer, service.secondary_payer, service.secondaryPayerExternalId, service.secondaryExternalId)));
}

function patientResponsibilityReportGrainOf(claim = {}, service = {}) {
  if (patientResponsibilityReportState.view === "payer") return reportPayerOf(claim);
  if (patientResponsibilityReportState.view === "dosMonth") return reportMonthOfDate(service.dos);
  return patientResponsibilityPatientGrain(claim);
}

function patientResponsibilityReportLineEligible(claim = {}, service = {}, line = serviceFinancialView(service, claim)) {
  if (patientResponsibilityClaimHasSecondary(claim)) return false;
  const pr = reportNum(line?.patientResponsibility);
  const balance = reportNum(firstPresent(line?.positiveTotalPatientBalance, service.positiveTotalPatientBalance, service.positive_total_pat_bal, service.patientBalance, service.totalPatBal, 0));
  // This report requires affirmative PR-1/2/3 evidence. The financial view can
  // legitimately retain a legacy balance fallback, which is not report eligibility.
  // Nonempty adjustment detail wins over a stale scalar, including signed reversals.
  const adjustments = Array.isArray(service.adjustments) ? service.adjustments : [];
  const evidence = adjustments.length
    ? adjustments.filter(isTruePatientResponsibilityAdjustment).reduce((sum, adjustment) => sum + reportNum(adjustment.amount), 0)
    : reportNum(firstPresent(service.prAdjustmentTotal, service.pr_adjustment_total, 0));
  return pr > 0 && balance > 0 && evidence > 0
    && Math.round(evidence * 100) === Math.round(pr * 100)
    && Math.round(balance * 100) >= Math.round(pr * 100);
}

function patientResponsibilityEligibleClaims(claims = []) {
  const search = patientResponsibilityReportState.search.toLowerCase();
  const dates = reportFilterValues();
  return (claims || []).flatMap((claim) => {
    const searchable = `${claim.patient || claim.patientName || ""} ${claim.patientId || claim.patientKey || ""} ${claim.client || ""}`.toLowerCase();
    const selected = patientResponsibilityReportState.patient;
    if (selected ? String(claim.orgId || "") !== selected.orgId || String(claim.patientId || "") !== selected.patientId : search && !searchable.includes(search)) return [];
    const services = (claim.services || []).filter((service) =>
      (!(dates.fromDate || dates.toDate) || serviceDateInDateRange(service.dos, dates.fromDate, dates.toDate))
      && patientResponsibilityReportLineEligible(claim, service));
    return services.length ? [{ ...claim, services }] : [];
  });
}

function patientResponsibilityRowsFromClaims(claims = [], grainOf) {
  const eligible = patientResponsibilityEligibleClaims(claims);
  const rows = reportMeasureRowsFromClaims(eligible, grainOf);
  rows.forEach((row) => {
    const key = String(row.dims?.[0] || "");
    const matching = eligible.filter((claim) => reportGrainValuesOf(claim, grainOf).has(key));
    row.patientCount = new Set(matching.map(patientResponsibilityPatientIdentity)).size;
    row.claimCount = new Set(matching.map((claim) => `${String(claim.orgId || claim.organizationId || "")}\u0001${String(claim.claimId || "")}`)).size;
  });
  if (patientResponsibilityReportState.view === "dosMonth") rows.sort((a, b) => String(b.dims?.[0] || "").localeCompare(String(a.dims?.[0] || "")));
  return { eligible, rows };
}

function patientResponsibilitySearchScopeKey(includeDates = true) {
  const dates = includeDates ? reportFilterValues() : {};
  // A new data publication is not a different patient/company selection.
  // Auth and access remain in this identity; only the publication epoch is omitted.
  const session = JSON.parse(preparedSnapshotSessionKey());
  delete session.publicationEpoch;
  return JSON.stringify({ session, filters: reportDatasetServerFilters(), fromDate: dates.fromDate || "", toDate: dates.toDate || "" });
}

function syncPatientResponsibilitySearchScope() {
  // Dates narrow the selected patient; access/company/filter changes reset it.
  // Suggestion and render identities still include dates to reject obsolete work.
  const key = patientResponsibilitySearchScopeKey(false);
  if (patientResponsibilityReportState.scopeKey && patientResponsibilityReportState.scopeKey !== key) {
    patientResponsibilityReportState.search = "";
    patientResponsibilityReportState.patient = null;
  }
  patientResponsibilityReportState.scopeKey = key;
}

function bindPatientResponsibilitySuggestions(host) {
  const input = host.querySelector("#patientResponsibilitySearch");
  const list = host.querySelector("#patientResponsibilitySuggestions");
  const status = host.querySelector("#patientResponsibilitySearchStatus");
  if (!input || !list || !status) return;
  let timer = null, controller = null, version = 0, options = [], active = -1, optionsSession = "";
  const scopeKey = patientResponsibilitySearchScopeKey();
  const current = () => input.isConnected && reportArState.view === "patientResponsibility" && scopeKey === patientResponsibilitySearchScopeKey();
  function close(message = "") {
    version += 1;
    clearTimeout(timer);
    if (controller) controller.abort();
    options = []; active = -1;
    list.innerHTML = ""; list.hidden = true;
    input.setAttribute("aria-expanded", "false"); input.removeAttribute("aria-activedescendant");
    status.textContent = message;
  }
  function highlight(index) {
    active = index;
    Array.from(list.children).forEach((option, i) => option.setAttribute("aria-selected", String(i === active)));
    if (active >= 0) { input.setAttribute("aria-activedescendant", list.children[active].id); list.children[active].scrollIntoView({ block: "nearest" }); }
    else input.removeAttribute("aria-activedescendant");
  }
  function choose(index) {
    const patient = options[index];
    if (!patient || !current()) return;
    if (optionsSession !== preparedSnapshotSessionKey()) { close("Results have updated. Type again to find current matches."); return; }
    patientResponsibilityReportState.patient = patient;
    patientResponsibilityReportState.search = patient.patientName || patient.patientId;
    close();
    reportPageState.page = 1;
    resetHostedReportDrill();
    preparedSnapshotReportState.status = "idle";
    renderReports();
    host.querySelector("#patientResponsibilitySearch")?.focus();
  }
  async function load(query, requestVersion) {
    if (!current() || requestVersion !== version) return;
    const requestSession = preparedSnapshotSessionKey();
    controller = new AbortController();
    status.textContent = "Finding matching patients…";
    const filters = { ...preparedSnapshotFilters("patientResponsibility") };
    delete filters._patientSearch; delete filters._patientOrgId; delete filters._patientId;
    try {
      const response = await reportSessionFetch(trackerApiBase() + "/api/reports/prepared", { method: "POST", cache: "no-store", signal: controller.signal,
        headers: { "content-type": "application/json", authorization: "Bearer " + trackerApiToken() },
        body: JSON.stringify({ report: "patientResponsibility", operation: "patientSuggestions", search: query, filters }) });
      const body = await response.json();
      if (!current() || requestVersion !== version || input.value.trim().slice(0, 120) !== query) return;
      if (requestSession !== preparedSnapshotSessionKey()) { close("Results have updated. Type again to find current matches."); return; }
      optionsSession = requestSession;
      if (!response.ok || !Array.isArray(body.suggestions)) throw new Error("lookup unavailable");
      options = body.suggestions.filter((row) => row && row.orgId && row.patientId).slice(0, 10);
      list.innerHTML = options.map((row, index) => '<li role="option" aria-selected="false" id="pr-patient-option-' + index + '" data-patient-option="' + index + '"><strong>' + escapeHtml(row.patientName || "Unnamed patient") + '</strong><span>Patient ID ' + escapeHtml(row.patientId) + ' · ' + escapeHtml(row.client || row.orgId) + '</span></li>').join("");
      list.hidden = options.length === 0;
      input.setAttribute("aria-expanded", String(options.length > 0));
      status.textContent = options.length ? options.length + " matching patient" + (options.length === 1 ? "" : "s") + (body.more ? ". Type more to narrow the results." : ". Use the arrow keys to choose a patient.") : "No matching patients with responsibility in these filters.";
    } catch (error) {
      if (error.name !== "AbortError" && current() && requestVersion === version) close("Suggestions are unavailable. Enter a name or patient ID and choose Search.");
    }
  }
  input.addEventListener("input", () => {
    close();
    const query = input.value.trim().slice(0, 120);
    if (!query && patientResponsibilityReportState.search) {
      patientResponsibilityReportState.search = "";
      patientResponsibilityReportState.patient = null;
      reportPageState.page = 1;
      resetHostedReportDrill();
      preparedSnapshotReportState.status = "idle";
      renderReports();
      host.querySelector("#patientResponsibilitySearch")?.focus();
      return;
    }
    if (query.length < 3) { status.textContent = "Type at least 3 letters or numbers for suggestions."; return; }
    const requestVersion = version;
    timer = setTimeout(() => load(query, requestVersion), 300);
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") { event.preventDefault(); close(); return; }
    if (!options.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      highlight(event.key === "ArrowDown" ? (active + 1) % options.length : (active < 0 ? options.length - 1 : (active - 1 + options.length) % options.length));
    } else if (event.key === "Enter" && active >= 0) { event.preventDefault(); choose(active); }
  });
  // Retain input focus through pointerdown so the listbox click can select the option.
  list.addEventListener("pointerdown", (event) => event.preventDefault());
  list.addEventListener("click", (event) => { const option = event.target.closest("[data-patient-option]"); if (option) choose(Number(option.dataset.patientOption)); });
  input.addEventListener("blur", () => close());
  host.querySelector("[data-patient-responsibility-search-form]")?.addEventListener("submit", () => close());
  host.querySelector("[data-patient-responsibility-search-clear]")?.addEventListener("click", () => close());
}

// Keep the active search form connected while background report updates render.
// Replacing the entire body loses drafts, focus and pointer targets between down/up.
function renderPatientResponsibilityBody(host, switcher, markup) {
  const key = JSON.stringify({ scope: patientResponsibilitySearchScopeKey(), view: patientResponsibilityReportState.view, search: patientResponsibilityReportState.search, patient: patientResponsibilityReportState.patient });
  const parsed = document.createElement("div");
  parsed.innerHTML = markup;
  const freshControls = parsed.querySelector(".patient-responsibility-controls");
  if (!freshControls) { host.innerHTML = switcher + markup; return; }
  freshControls.remove();
  const existing = host.querySelector(".patient-responsibility-controls");
  const output = host.querySelector("[data-patient-responsibility-output]");
  if (existing && output && existing.patientSearchRenderKey === key) {
    host.querySelector("[data-patient-responsibility-switcher]").innerHTML = switcher;
    output.innerHTML = parsed.innerHTML;
    return;
  }
  host.innerHTML = '<div data-patient-responsibility-switcher>' + switcher + '</div>' + freshControls.outerHTML + '<div data-patient-responsibility-output>' + parsed.innerHTML + '</div>';
  host.querySelector(".patient-responsibility-controls").patientSearchRenderKey = key;
}

function patientResponsibilityControlsHtml() {
  syncPatientResponsibilitySearchScope();
  const mode = patientResponsibilityReportState.view;
  const dates = reportFilterValues();
  return `<section class="panel patient-responsibility-controls reports-no-print" aria-label="Patient responsibility view controls">
    <div class="patient-responsibility-intro"><h3>Patient responsibility</h3><p>Find a patient to review what they owe, by date and service.</p></div>
    <form class="patient-responsibility-search" data-patient-responsibility-search-form>
      <label for="patientResponsibilitySearch">Search patient name or ID</label>
      <div><div class="patient-responsibility-autocomplete"><input id="patientResponsibilitySearch" type="search" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="patientResponsibilitySuggestions" aria-describedby="patientResponsibilitySearchStatus" value="${escapeHtml(patientResponsibilityReportState.search)}" placeholder="Patient name or ID" autocomplete="off" maxlength="120"><ul id="patientResponsibilitySuggestions" role="listbox" aria-label="Matching patients" hidden></ul></div><button class="primary-button" type="submit">Search</button><button class="ghost-button" type="button" data-patient-responsibility-search-clear>Clear</button></div>
      <small id="patientResponsibilitySearchStatus" role="status" aria-live="polite">Type 3 or more characters for matches, or enter an ID and press Search.</small>
    </form>
    <div class="patient-responsibility-browse"><span>Group results by</span><div class="ar-dim-toggle" role="group" aria-label="Group patient responsibility">${[["patient", "By patient"], ["payer", "By insurance"], ["dosMonth", "By service month"]].map(([key, label]) => `<button type="button" class="ar-dim-btn${mode === key ? " is-active" : ""}" data-patient-responsibility-view="${key}" aria-pressed="${mode === key ? "true" : "false"}">${label}</button>`).join("")}</div></div>
    <form class="patient-responsibility-dates" data-patient-responsibility-dates-form aria-label="Service date range">
      <fieldset><legend>Service date range</legend><div class="patient-responsibility-date-fields">
        <label for="patientResponsibilityFromDate">Start date<input id="patientResponsibilityFromDate" type="date" value="${escapeHtml(dates.fromDate || "")}"></label>
        <label for="patientResponsibilityToDate">End date<input id="patientResponsibilityToDate" type="date" value="${escapeHtml(dates.toDate || "")}"></label>
        <button class="primary-button" type="submit">Apply dates</button><button class="ghost-button" type="button" data-patient-responsibility-dates-clear>All dates</button>
      </div></fieldset>
      <small>Filters dates of service, including the start and end dates. Leave either date blank for an open-ended range.</small>
      <p class="patient-responsibility-date-error" data-patient-responsibility-date-error role="alert" hidden></p>
    </form>
    ${patientResponsibilityRulesHtml()}
  </section>`;
}

function patientResponsibilityRulesHtml() {
  return `<div class="patient-responsibility-rules"><details><summary>What are these charges, and why might a service be missing?</summary><div class="patient-responsibility-rule-line"><strong>All 3 required:</strong><span>PR-1 / PR-2 / PR-3 only</span><span>No secondary insurance</span><span>Service balance covers eligible PR</span></div><p><strong>PR-1 · Deductible:</strong> the part assigned toward the plan deductible. <strong>PR-2 · Coinsurance:</strong> the patient's share of the covered cost. <strong>PR-3 · Copay:</strong> the plan's fixed patient charge.</p><p>A service appears only when its ERA assigns PR-1, PR-2 or PR-3, the same service has a positive patient balance at least as large as the eligible ERA amount to the cent, and the claim has no secondary insurance. The amount shown comes from qualifying ERA codes; the patient balance is only a safety check. A claim with secondary insurance is excluded from this report. A higher balance may include other adjustment codes and does not increase the amount charged. Other PR codes, zero or negative balances, and balances below the eligible PR amount are excluded too. Your company and date filters still apply.</p><p>This report helps explain qualifying responsibility. No matching services does not establish a $0 patient balance. Statement printing has its own eligibility checks; a report row alone does not approve a statement.</p></details></div>`;
}

function patientResponsibilitySummaryHtml(summary, caption) {
  if (!summary || summary.patientResp == null) return "";
  return `<div class="patient-responsibility-summary" aria-label="Responsibility totals"><div class="patient-responsibility-amount"><span>Amount owed · qualifying services</span><strong>${reportFormatCell("currency", summary.patientResp)}</strong></div><div><span>Services</span><strong>${summary.lines == null ? "Unavailable" : Number(summary.lines).toLocaleString()}</strong></div>${summary.serviceDateCount == null ? "" : `<div><span>Dates of service</span><strong>${Number(summary.serviceDateCount).toLocaleString()}</strong></div>`}</div><p class="muted-note">${escapeHtml(caption)}</p>`;
}

function patientResponsibilityResultsHtml(model, groups, totals) {
  const slice = preparedReportPageSlice(model, groups);
  const patient = patientResponsibilityReportState.view === "patient";
  const columns = [reportMeasureCol("patientResp", "Amount owed", "RESPONSIBILITY", "currency"), reportMeasureCol("lines", "Services", "RESPONSIBILITY", "number"), ...(!patient ? [reportMeasureCol("patientCount", "Patients", "RESPONSIBILITY", "number")] : []), ...(totals.serviceDateCount == null ? [] : [reportMeasureCol("serviceDateCount", "Dates of service", "RESPONSIBILITY", "number")])];
  return `<section class="panel ar-report-panel patient-responsibility-results" role="region" aria-label="Patient Responsibility results" tabindex="0"><div class="ar-report-head"><div><h3>${patient ? "Patient search results" : "Qualifying patient responsibility"}</h3><p>${patient ? "Choose a patient to see the exact dates, services and amounts." : "Choose a group, then a patient to see their services."}</p></div></div>${patientResponsibilitySummaryHtml(totals, "Complete total for this search and current report filters. " + (patient && groups.length > 1 ? "More than one patient matches; choose the correct name and ID below." : ""))}${preparedReportReloadNoticeHtml()}${renderReportDefinitionTable("patientResponsibility", model, slice.rows, totals, { columns, grainLabel: preparedReportGroupHeading("patientResponsibility", model.dimension), rowLabel: (group) => preparedReportGroupLabel("patientResponsibility", group.dims) + (patient && group.dims?.[1] && !String(group.dims[1]).startsWith("name:") ? " · Patient ID " + (String(group.dims[1]).startsWith(String(group.dims[0]) + ":") ? String(group.dims[1]).slice(String(group.dims[0]).length + 1) : group.dims[1]) : ""), ...(hasPermission(PERMISSIONS.VIEW_PHI) ? { drillLabel: patient ? "services" : "patients", drillAttr: "data-hosted-report-drill", drillValue: (group) => JSON.stringify(group.dims || []) } : {}), emptyText: "No qualifying services match this search and these filters. This does not mean the patient owes $0." })}${reportPagerHtml(slice)}</section>`;
}

function patientResponsibilityDrillHtml(model, rows, level, crumbs) {
  const service = level === "service";
  const cell = (value) => escapeHtml(value == null || value === "" ? "—" : value);
  const body = rows.map((row, index) => service
    ? `${index === 0 || String(rows[index - 1].serviceDate || "") !== String(row.serviceDate || "") ? `<tr class="patient-responsibility-date-total"><th colspan="6" scope="rowgroup">${row.serviceDate ? formatDate(row.serviceDate) : "Date unavailable"}${row.datePatientResp == null || row.dateServiceCount == null ? "" : ` · ${Number(row.dateServiceCount).toLocaleString()} service${Number(row.dateServiceCount) === 1 ? "" : "s"} · ${reportFormatCell("currency", row.datePatientResp)} owed <small>Total for this date (all pages)</small>`}</th></tr>` : ""}<tr><th scope="row">${row.serviceDate ? formatDate(row.serviceDate) : "Date unavailable"}</th><td><strong>${cell(row.procedureCode)}</strong><small>Service ID ${cell(row.serviceId)}</small></td><td>${row.units == null ? "Unavailable" : Number(row.units).toLocaleString()}</td><td class="patient-responsibility-owed">${row.patientResp == null ? "Unavailable" : reportFormatCell("currency", row.patientResp)}</td><td>Deductible, coinsurance or copay<small>Open claim to confirm the exact PR code and breakdown.</small></td><td>${cell(row.claimId)}<small>${cell(row.client)}</small><button type="button" class="small-button" data-hosted-drill-claim="${cell(row.claimId)}" data-hosted-drill-org="${cell(row.orgId)}" data-hosted-drill-service="${cell(row.serviceId)}">Open claim</button></td></tr>`
    : `<tr><th scope="row">${cell(row.patient)}<small>Patient ID ${cell(row.patientId)} · ${cell(row.client)}</small></th><td class="patient-responsibility-owed">${row.patientResp == null ? "Unavailable" : reportFormatCell("currency", row.patientResp)}</td><td>${Number(row.lines || 0).toLocaleString()}</td><td><button type="button" class="small-button" data-hosted-drill-next="${escapeHtml(JSON.stringify({ level, patient: row.patient, patientId: row.patientId, orgId: row.orgId }))}">View services →</button></td></tr>`).join("");
  const last = hostedReportDrillState.path.at(-1);
  const title = last?.patient || preparedReportGroupLabel("patientResponsibility", (hostedReportDrillState.root?.dimensions || []).map((d) => d.value));
  return `<section class="panel ar-report-panel patient-responsibility-results"><nav class="report-drill-breadcrumbs" aria-label="Report detail breadcrumb">${crumbs}</nav><h3>${cell(title)} · ${service ? "Services owed" : "Patients"}</h3>${patientResponsibilitySummaryHtml(model.summary, "Complete total for this selection, including every page below.")}<p>${service ? "Each row is one service. The same date may have more than one service. Newest dates appear first." : "Choose the matching patient to see dates and services."}</p><div class="table-wrap"><table class="patient-responsibility-detail"><thead><tr>${service ? "<th>Date of service</th><th>Service / CPT</th><th>Units</th><th>Amount owed</th><th>What it is for</th><th>Claim / evidence</th>" : "<th>Patient</th><th>Amount owed</th><th>Services</th><th>Details</th>"}</tr></thead><tbody>${body || `<tr><td colspan="${service ? 6 : 4}">No qualifying services in this selection.</td></tr>`}</tbody></table></div><div class="report-pager" aria-label="Report detail pages">${model.page > 1 ? '<button type="button" data-hosted-drill-page="prev">Previous</button>' : ""}<span>Page ${Number(model.page || 1).toLocaleString()} of ${Math.max(1, Math.ceil(Number(model.totalRows || 0) / Math.max(1, Number(model.pageSize || 100)))).toLocaleString()} · ${Number(model.totalRows || 0).toLocaleString()} ${service ? "services" : "patients"}</span>${Number(model.page || 1) * Number(model.pageSize || 100) < Number(model.totalRows || 0) ? '<button type="button" data-hosted-drill-page="next">Next</button>' : ""}</div></section>`;
}

function bindPatientResponsibilityControls() {
  const host = els.reportsBody;
  if (!host || reportArState.view !== "patientResponsibility") return;
  const controls = host.querySelector(".patient-responsibility-controls");
  if (!controls || controls.patientSearchBound) return;
  controls.patientSearchBound = true;
  bindPatientResponsibilitySuggestions(host);
  const dateForm = controls.querySelector("[data-patient-responsibility-dates-form]");
  const fromInput = controls.querySelector("#patientResponsibilityFromDate");
  const toInput = controls.querySelector("#patientResponsibilityToDate");
  const dateError = controls.querySelector("[data-patient-responsibility-date-error]");
  function clearDateError() {
    toInput.setCustomValidity("");
    dateError.hidden = true;
    dateError.textContent = "";
  }
  function applyDates(fromDate, toDate) {
    if (!els.reportFromDate || !els.reportToDate) return;
    const draft = host.querySelector("#patientResponsibilitySearch")?.value || "";
    els.reportFromDate.value = fromDate;
    els.reportToDate.value = toDate;
    reportPageState.page = 1;
    resetHostedReportDrill();
    preparedSnapshotReportState.status = "idle";
    renderReports();
    const searchInput = host.querySelector("#patientResponsibilitySearch");
    if (searchInput) searchInput.value = draft;
  }
  dateForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    clearDateError();
    if (fromInput.value && toInput.value && fromInput.value > toInput.value) {
      dateError.textContent = "End date must be on or after the start date.";
      dateError.hidden = false;
      toInput.setCustomValidity(dateError.textContent);
    }
    if (!dateForm.reportValidity()) return;
    applyDates(fromInput.value, toInput.value);
  });
  [fromInput, toInput].forEach((input) => input?.addEventListener("input", clearDateError));
  controls.querySelector("[data-patient-responsibility-dates-clear]")?.addEventListener("click", () => {
    clearDateError();
    fromInput.value = "";
    toInput.value = "";
    applyDates("", "");
  });
  host.querySelectorAll("[data-patient-responsibility-view]").forEach((button) => button.addEventListener("click", () => {
    patientResponsibilityReportState.view = button.dataset.patientResponsibilityView || "patient";
    reportPageState.page = 1;
    reportPageState.size = patientResponsibilityReportState.view === "dosMonth" ? 24 : 25;
    resetHostedReportDrill();
    preparedSnapshotReportState.status = "idle";
    renderReports();
  }));
  host.querySelector("[data-patient-responsibility-search-form]")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const query = String(host.querySelector("#patientResponsibilitySearch")?.value || "").trim().slice(0, 120);
    if (query !== patientResponsibilityReportState.search) patientResponsibilityReportState.patient = null;
    patientResponsibilityReportState.search = query;
    reportPageState.page = 1;
    resetHostedReportDrill();
    preparedSnapshotReportState.status = "idle";
    renderReports();
  });
  host.querySelector("[data-patient-responsibility-search-clear]")?.addEventListener("click", () => {
    const input = host.querySelector("#patientResponsibilitySearch");
    if (input) input.value = "";
    patientResponsibilityReportState.search = "";
    patientResponsibilityReportState.patient = null;
    reportPageState.page = 1;
    resetHostedReportDrill();
    preparedSnapshotReportState.status = "idle";
    renderReports();
  });
}

function bindHostedReportSlicers() {
  const host = els.reportsBody;
  if (!host) return;
  requestReportFilterFacets();
  renderReportFacetChoiceControls();
  host.querySelectorAll("[data-hosted-report-slicer]").forEach((select) => select.addEventListener("change", () => {
    const slicer = HOSTED_REPORT_SLICERS.find((candidate) => candidate.key === select.getAttribute("data-hosted-report-slicer"));
    const source = slicer && els[slicer.control];
    if (!source) return;
    const value = String(select.value || "All");
    if (value === "__multi__") return;
    // A pick here is a ONE-VALUE pick, committed through the same function the
    // card commits through, so the mirror, the card, the chip rail and the
    // request body can never disagree about what is selected.
    if (reportFilterKeyIsMultiSelect(slicer.key)) {
      applyReportFilterSelection(slicer.key, value === "All" ? [] : [value], false);
      return;
    }
    source.value = value;
    saveReportFilterSelections();
    reportPageState.page = 1;
    renderReports();
  }));
  host.querySelector("[data-hosted-report-slicer-open]")?.addEventListener("click", () => {
    reportSlicersOpen = !reportSlicersOpen;
    renderReports();
  });
}


// ---------------------------------------------------------------------------
// REPORT SLICERS.
//
// The legacy tracker ships fifteen of these — seven shown, eight behind an "Advanced
// filters" disclosure — each a searchable multi-select with Select all / Clear all. They
// are the tracker's defining chrome: a report you cannot narrow is a report you can only
// look at.
//
// Options are derived from the claims ALREADY IN SCOPE, never from a global facet table.
// That is deliberate: CT-TRACKER's own refresh_slicer_values builds its facets with
// where_sql = "" — an unscoped full-table scan — and porting that shape would offer one
// tenant's payers to another. See AGENTS.md.
const REPORT_SLICERS = [
  { key: "payer",   label: "Payer",            of: (c) => reportPayerOf(c) },
  { key: "client",  label: "Client",           of: (c) => reportClientOf(c) },
  { key: "status",  label: "Claim Status",     of: (c) => String(c.claimStatus || c.status || "").trim() },
  { key: "action",  label: "Follow-Up Action", of: (c) => followUpFilterRowValue(c) },
  { key: "aging",   label: "Aging",            of: (c) => arAgingBucketForClaim(c).key },
  { key: "patient", label: "Patient",          of: (c) => String(c.patient || c.patientName || "").trim() }
];

// key -> Set of selected values. Empty set means "no filter", NOT "nothing selected" —
// a slicer with nothing chosen must not blank the report.
const reportSlicerState = {};
const reportSlicerSearch = {};
let reportSlicersOpen = false;
let reportSlicersExpanded = false;

function reportSlicerSelected(key) {
  if (!reportSlicerState[key]) reportSlicerState[key] = new Set();
  if (key === "action") reportSlicerState[key] = new Set(Array.from(reportSlicerState[key], value => value === "(blank)" ? "Blank" : followUpFilterValue(value)));
  return reportSlicerState[key];
}

function reportSlicerOptions(claims, slicer) {
  const seen = new Map(slicer.key === "action" ? [["Blank", 0]] : []);
  (claims || []).forEach((claim) => {
    const value = String(slicer.of(claim) || "").trim() || "(blank)";
    seen.set(value, (seen.get(value) || 0) + 1);
  });
  return [...seen.entries()]
    .sort((a, b) => (slicer.key === "action" ? (a[0] === "Blank" ? -1 : b[0] === "Blank" ? 1 : 0) : 0) || String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true }))
    .map(([value, count]) => ({ value, count }));
}

function reportApplySlicers(claims) {
  return (claims || []).filter((claim) => REPORT_SLICERS.every((slicer) => {
    const chosen = reportSlicerSelected(slicer.key);
    if (!chosen.size) return true;
    return chosen.has(String(slicer.of(claim) || "").trim() || "(blank)");
  }));
}

function reportActiveSlicerCount() {
  return REPORT_SLICERS.reduce((n, s) => n + (reportSlicerSelected(s.key).size ? 1 : 0), 0);
}

function reportSlicerPanelHtml(claims) {
  // Options come from the UNFILTERED book so a slicer never hides its own other values —
  // narrowing by payer must still let you widen back to a payer you just excluded.
  const shown = reportSlicersExpanded ? REPORT_SLICERS : REPORT_SLICERS.slice(0, 3);
  const active = reportActiveSlicerCount();
  const panels = shown.map((slicer) => {
    const chosen = reportSlicerSelected(slicer.key);
    const search = String(reportSlicerSearch[slicer.key] || "").toLowerCase();
    const options = reportSlicerOptions(claims, slicer)
      .filter((o) => slicer.key === "action" ? followUpActionChoiceMatches("action", slicerDisplayValue("action", o.value), search) : !search || o.value.toLowerCase().includes(search));
    const buttons = options.slice(0, 60).map((o) => `
      <button type="button" class="report-slicer-option${chosen.has(o.value) ? " is-on" : ""}"
        data-report-slicer="${escapeHtml(slicer.key)}" data-report-slicer-value="${escapeHtml(o.value)}">
        ${escapeHtml(slicer.key === "action" ? slicerDisplayValue("action", o.value) : o.value)} <span class="report-slicer-count">${o.count}</span>
      </button>`).join("");
    return `
      <div class="report-slicer">
        <div class="report-slicer-head">
          <span class="report-slicer-label">${escapeHtml(slicer.label)}</span>
          <button type="button" class="linkbutton" data-report-slicer-all="${escapeHtml(slicer.key)}">Select all</button>
          <button type="button" class="linkbutton" data-report-slicer-clear="${escapeHtml(slicer.key)}">Clear</button>
        </div>
        <input type="search" class="report-slicer-search" placeholder="Search ${escapeHtml(slicer.label)}"
          value="${escapeHtml(reportSlicerSearch[slicer.key] || "")}" data-report-slicer-search="${escapeHtml(slicer.key)}">
        <div class="report-slicer-options">${buttons || '<span class="muted-note">No values</span>'}</div>
      </div>`;
  }).join("");
  return `
    <section class="panel report-slicer-panel">
      <div class="report-slicer-bar">
        <strong>Report filters${active ? ` (${active} active)` : ""}</strong>
        <span class="muted-note">${active ? "Filtered results" : "Showing all report data"}</span>
        <button type="button" class="ghost-button" data-report-slicer-open>${reportSlicersOpen ? "Hide filters" : "Show filters"}</button>
        ${reportSlicersOpen ? `<button type="button" class="ghost-button" data-report-slicer-toggle>${reportSlicersExpanded ? "Advanced Filters" : "Advanced Filters"}</button>` : ""}
        ${active ? '<button type="button" class="ghost-button" data-report-slicer-reset>Clear report filters</button>' : ""}
      </div>
      ${reportSlicersOpen ? `<div class="report-slicer-grid">${panels}</div>` : ""}
    </section>`;
}

function bindReportSlicers(claims) {
  const host = els.reportsBody;
  if (!host) return;
  const rerender = () => renderReports();
  host.querySelectorAll("[data-report-slicer]").forEach((el) => el.addEventListener("click", () => {
    const set = reportSlicerSelected(el.getAttribute("data-report-slicer"));
    const value = el.getAttribute("data-report-slicer-value");
    if (set.has(value)) set.delete(value); else set.add(value);
    rerender();
  }));
  host.querySelectorAll("[data-report-slicer-all]").forEach((el) => el.addEventListener("click", () => {
    const key = el.getAttribute("data-report-slicer-all");
    const slicer = REPORT_SLICERS.find((s) => s.key === key);
    const set = reportSlicerSelected(key);
    reportSlicerOptions(claims, slicer).forEach((o) => set.add(o.value));
    rerender();
  }));
  host.querySelectorAll("[data-report-slicer-clear]").forEach((el) => el.addEventListener("click", () => {
    reportSlicerSelected(el.getAttribute("data-report-slicer-clear")).clear();
    rerender();
  }));
  host.querySelectorAll("[data-report-slicer-search]").forEach((el) => el.addEventListener("input", () => {
    reportSlicerSearch[el.getAttribute("data-report-slicer-search")] = el.value;
    rerender();
    const again = els.reportsBody.querySelector(`[data-report-slicer-search="${el.getAttribute("data-report-slicer-search")}"]`);
    if (again) { again.focus(); again.setSelectionRange(again.value.length, again.value.length); }
  }));
  host.querySelector("[data-report-slicer-open]")?.addEventListener("click", () => {
    reportSlicersOpen = !reportSlicersOpen;
    rerender();
  });
  host.querySelector("[data-report-slicer-toggle]")?.addEventListener("click", () => {
    reportSlicersExpanded = !reportSlicersExpanded;
    rerender();
  });
  host.querySelector("[data-report-slicer-reset]")?.addEventListener("click", () => {
    REPORT_SLICERS.forEach((s) => reportSlicerSelected(s.key).clear());
    rerender();
  });
}

// The tracker pages at 25/50/100 with Previous/Next and says which slice you are on.
// Ours rendered every row into one tbody — fine at 6 rows, not at the 2,823 its CPT report
// carries. TOTALS ARE NOT PAGED: they stay the whole filtered book, or the footer would
// silently describe one page while the caption claims the set.
const reportPageState = { page: 1, size: 50 };

function reportPageSize() {
  if (reportArState.view === "rateIssues" && preparedSnapshotReportState.response?.groupPage) return 50;
  return reportArState.view === "patientResponsibility" && patientResponsibilityReportState.view === "dosMonth"
    ? 24
    : ([25, 50, 100].includes(reportPageState.size) ? reportPageState.size : 50);
}

function preparedReportPageSlice(model, rows) {
  const serverPage = model?.groupPage;
  if (!serverPage) return reportPageSlice(rows);
  const page = Number(serverPage.page) || 1, size = Number(serverPage.pageSize) || 50, total = Number(serverPage.totalRows) || 0;
  reportPageState.page = page;
  return { rows, page, pages: Math.max(1, Math.ceil(total / size)), start: (page - 1) * size, total };
}

function reportPageSlice(rows) {
  const identity = JSON.stringify({ view: reportArState.view, filters: reportFilterValues(), slicers: Object.entries(reportSlicerState).map(([key, values]) => [key, Array.from(values || []).sort()]), patient: patientResponsibilityReportState });
  if (reportPageState.identity !== identity) { reportPageState.page = 1; reportPageState.identity = identity; }
  const size = reportPrintComplete ? Math.max(1, rows.length) : reportPageSize();
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const page = reportPrintComplete ? 1 : Math.min(Math.max(1, Math.floor(Number(reportPageState.page) || 1)), pages);
  if (!reportPrintComplete) reportPageState.page = page;
  const start = (page - 1) * size;
  return { rows: rows.slice(start, start + size), page, pages, start, total: rows.length };
}

function reportPagerHtml(slice) {
  if (reportPrintComplete || !slice) return "";
  const first = slice.total ? slice.start + 1 : 0;
  const size = reportPageSize();
  const last = Math.min(slice.start + size, slice.total);
  const pageSizes = reportArState.view === "rateIssues" && preparedSnapshotReportState.response?.groupPage ? [50] : reportArState.view === "patientResponsibility" && patientResponsibilityReportState.view === "dosMonth" ? [24] : [25, 50, 100];
  const button = (action, label, disabled) => '<button type="button" class="ghost-button" data-report-page="' + action + '"' + (disabled ? ' disabled' : '') + '>' + label + '</button>';
  return '<nav class="report-pager" aria-label="Report result pages" tabindex="-1">'
    + '<label>Rows per page <select data-report-page-size>' + pageSizes.map((n) => '<option value="' + n + '"' + (n === size ? ' selected' : '') + '>' + n + ' rows</option>').join("") + '</select></label>'
    + '<span class="muted-note" role="status" aria-live="polite">Showing ' + first.toLocaleString() + '–' + last.toLocaleString() + ' of ' + slice.total.toLocaleString() + ' · Page ' + slice.page.toLocaleString() + ' of ' + slice.pages.toLocaleString() + '</span>'
    + button("first", "First", slice.page <= 1) + button("prev", "Previous", slice.page <= 1)
    + button("next", "Next", slice.page >= slice.pages) + button("last", "Last", slice.page >= slice.pages) + '</nav>';
}

function bindReportPager() {
  const host = els.reportsBody;
  if (!host) return;
  const restore = (selector) => {
    const control = host.querySelector(selector);
    (control && !control.disabled ? control : host.querySelector('.report-pager'))?.focus({ preventScroll: true });
  };
  host.querySelector("[data-report-page-size]")?.addEventListener("change", (e) => {
    const requestedSize = Number(e.target.value);
    reportPageState.size = [25, 50, 100].includes(requestedSize) ? requestedSize : 50;
    reportPageState.page = 1;
    if (preparedSnapshotReportState.response?.groupPage) requestPreparedSnapshotReport(reportArState.view, 1);
    renderReports();
    restore("[data-report-page-size]");
  });
  host.querySelectorAll("[data-report-page]").forEach((el) => el.addEventListener("click", () => {
    const action = el.getAttribute("data-report-page");
    const serverPage = preparedSnapshotReportState.response?.groupPage;
    if (serverPage) {
      const pages = Math.max(1, Math.ceil(Number(serverPage.totalRows) / Number(serverPage.pageSize || 50)));
      const next = action === "first" ? 1 : action === "last" ? pages : Number(serverPage.page || 1) + (action === "next" ? 1 : -1);
      requestPreparedSnapshotReport(reportArState.view, Math.min(pages, Math.max(1, next))); renderReports(); return;
    }
    reportPageState.page = action === "first" ? 1 : action === "last" ? Number.MAX_SAFE_INTEGER : reportPageState.page + (action === "next" ? 1 : -1);
    renderReports();
    restore('[data-report-page="' + action + '"]');
  }));
}

function renderDefinitionReportFromClaims(view, claims) {
  const def = reportDefinitionUi(view);
  if (!def || typeof def.grainOf !== "function") return null;
  // A report that names a population must SHOW that population. Client Action Pending was
  // rendering the whole book under a heading that promised only the blocked claims.
  // Population first (what the report is ABOUT), then the user's slicers on top of it.
  const population = typeof def.population === "function" ? (claims || []).filter(def.population) : claims;
  const scoped = reportApplySlicers(population);
  const patientModel = view === "patientResponsibility" ? patientResponsibilityRowsFromClaims(scoped, def.grainOf) : null;
  const rows = patientModel ? patientModel.rows : reportSortRows(view, reportMeasureRowsFromClaims(scoped, def.grainOf));
  const totals = reportMeasureTotals(rows);
  // The TABLE shows one page; the TOTALS stay the whole filtered book, or the footer would
  // silently describe a page while the caption describes the set.
  const slice = reportPageSlice(rows);
  const grainLabel = view === "patientResponsibility" ? preparedReportGroupHeading(view, patientResponsibilityReportState.view) : def.grainLabel;
  const table = renderReportDefinitionTable(view, { dimension: grainLabel }, slice.rows, totals,
    view === "patientResponsibility" ? {
      grainLabel,
      drillLabel: def.drillLabel,
      emptyText: "No rows match this search and filter selection. This does not prove the current patient balance is $0."
    } : null);
  const visibleClaims = patientModel ? patientModel.eligible : scoped;
  const claimCount = new Set((visibleClaims || []).map((c) => c.claimId)).size;
  // The global Report filters disclosure already controls this exact local
  // population. A second collapsed "Report filters" panel consumed a full row,
  // repeated the same concept, and pushed the first result below the fold.
  return `${view === "patientResponsibility" ? patientResponsibilityControlsHtml() : ""}${view === "ar" ? clientOutstandingHeadlineHtml(rows, totals, { complete: true, drill: "local" }) : ""}
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>${escapeHtml(def.title || preparedReportTitle(view))}</h3>
        <p>${formatNounCount(claimCount, "claim")} · ${formatNounCount(totals.lines, "service line")} in the active filter.</p>
      ${reportCaptionHtml(def, rows.length, slice)}
      </div>
      ${reportExportActionsHtml(view === "patientResponsibility" ? `
        <button type="button" class="ghost-button" data-ar-export="matrix">Download summary (CSV)</button>
        <button type="button" class="ghost-button" data-ar-export="detail">Download details (CSV)</button>
        <button type="button" class="primary-button" data-ar-print>Print or save PDF</button>` : `
        <button type="button" class="ghost-button" data-ar-export="matrix">Matrix → CSV</button>
        <button type="button" class="ghost-button" data-ar-export="detail">Detail → CSV</button>
        <button type="button" class="primary-button" data-ar-print>Print / PDF</button>`)}
    </div>
    ${reportKpiTilesHtml(def, totals)}
    ${table}
    ${reportPagerHtml(slice)}
    ${reportUnpricedNoteHtml(totals, claimCount)}
  </section>`;
}


// ---------------------------------------------------------------------------
// THE DRILL CHAIN.
//
// Order taken from the live tracker capture (ct-tracker-capture 08_DRILLDOWN.md):
//   report grain -> payer -> patient -> claim -> service lines
// The level below a payer is the PATIENT, not the client — the tracker's payer rows link
// "View patients" and its claim API keys on patient_key. Client is a filter, not a rung.
//
// Each level is ITS OWN PROJECTION. The payer and patient levels drop the expected bridge
// and add Patient/Claim counts and a DOS range; only then does it become a claim list.
const REPORT_DRILL_CHAIN = ["payer", "patient", "claim", "serviceLines"];
const PATIENT_RESPONSIBILITY_DRILL_CHAIN = ["patient", "claim", "serviceLines"];

function reportDrillChainForView(view) {
  if (view !== "patientResponsibility") return REPORT_DRILL_CHAIN;
  return patientResponsibilityReportState.view === "patient" ? ["claim", "serviceLines"] : PATIENT_RESPONSIBILITY_DRILL_CHAIN;
}

// Heading vocabulary per rung. grainLabel names the COLUMN; these name the PLACE.
const REPORT_DRILL_TITLES = {
  payer: { title: "Payers", prep: "in" },
  patient: { title: "Patients", prep: "in" },
  claim: { title: "Claims", prep: "for" },
  serviceLines: { title: "Service lines", prep: "on claim" }
};

const REPORT_DRILL_LEVELS_UI = {
  payer: {
    label: "payers",
    grainLabel: "Primary Payer",
    of: (claim) => reportPayerOf(claim),
    next: "patient"
  },
  patient: {
    label: "patients",
    grainLabel: "Patient",
    of: (claim) => reportDrillState.view === "patientResponsibility"
      ? patientResponsibilityPatientIdentity(claim)
      : String(claim.patient || claim.patientName || "").trim() || "—",
    next: "claim"
  },
  claim: {
    label: "claims",
    grainLabel: "Claim",
    of: (claim) => String(claim.claimId || "").trim(),
    next: "serviceLines"
  },
  serviceLines: {
    label: "service lines",
    grainLabel: "Service Line",
    of: (claim) => String(claim.claimId || "").trim(),
    next: null
  }
};

// SCOPE columns for the browser-lane aggregate rungs: who and how many, over what dates.
// These lead; the MONEY columns behind them are the report's own (reportDrillColumnsFor).
//
// Superseded rationale, kept so it is not "restored": this list used to be a fixed eleven
// columns declared "deliberately NOT the report's spine". That reasoning cost the biller
// the rest of the bridge and one of the two percentages the moment they drilled. Owner
// directive 2026-08-22 — "on the drill down for all the reports it should have all of the
// columns available, just like on the main report" — replaces it. The counts and the DOS
// range that made the old list useful are still here; the report's columns now follow them.
const REPORT_DRILL_SCOPE_COLUMNS = [
  reportMeasureCol("patientCount", "Patient Count", "PERIOD & VOLUME", "number"),
  reportMeasureCol("claimCount", "Claim Count", "PERIOD & VOLUME", "number"),
  reportMeasureCol("firstDos", "First DOS", "PERIOD & VOLUME", "date"),
  reportMeasureCol("lastDos", "Last DOS", "PERIOD & VOLUME", "date")
];

// A rung offers exactly what its report offers. reportMeasureRowsFromClaims already sums
// every one of REPORT_MEASURE_KEYS_UI onto each drill row, so this widens the VIEW over
// figures the lane already had — it adds no arithmetic and reads no new field.
function reportDrillMeasureColumns(view, levelKey) {
  if (view === "turnaround") return levelKey === "service"
    ? [reportMeasureCol("cycleDays", "Days to Pay", "PAYMENT CYCLE", "number")]
    : [reportMeasureCol("lines", "Service Line Count", "PERIOD & VOLUME", "number")];
  const def = reportDefinitionUi(view);
  const columns = (def && def.columns) || REPORT_FINANCIAL_SPINE;
  return view === "patientResponsibility" && levelKey
    ? columns.filter((column) => !["patientCount", "claimCount"].includes(column.key))
    : columns;
}

function reportDrillColumnsFor(view, levelKey) {
  return [...REPORT_DRILL_SCOPE_COLUMNS, ...reportDrillMeasureColumns(view, levelKey)];
}

// Where the user currently is. path is the ordered ancestor selections.
const reportDrillState = { view: "", path: [] };

function reportDrillCurrentLevel() {
  const depth = reportDrillState.path.length;
  return depth === 0 ? null : reportDrillChainForView(reportDrillState.view)[depth - 1];
}

function reportDrillNextLevel() {
  const depth = reportDrillState.path.length;
  return reportDrillChainForView(reportDrillState.view)[depth] || null;
}

// Claims still in scope after applying the report grain and every drill step so far.
// True when a claim contributes at least one line the report grain could not resolve —
// i.e. a line that landed in (blank). Uses the definition's own grainOf, so it cannot
// disagree with how the row was built.
function reportClaimHasUnresolvedGrain(claim, def) {
  if (!def || typeof def.grainOf !== "function") return false;
  const fin = claimFinancialView(claim);
  const lines = (fin && fin.serviceFinancials) || [];
  const services = claim.services || [];
  return lines.some((line, index) => !String(def.grainOf(claim, services[index] || {}, line) || ""));
}

function reportDrillScopedClaims() {
  const def = reportDefinitionUi(reportDrillState.view);
  let claims = getReportClaims();
  if (def && typeof def.drill === "function" && reportDrillState.path.length) {
    const grainValue = reportDrillState.path[0].value;
    // A (blank) row is real money — lines whose grain would not resolve are filed there so
    // nothing is dropped. Every drill predicate matches real values only, so clicking it
    // opened an EMPTY drill: money visible in the summary, unreachable underneath. It now
    // selects exactly the complement — the lines no other row claimed.
    claims = reportDrillState.view === "patientResponsibility"
      ? patientResponsibilityEligibleClaims(claims).filter(def.drill(grainValue))
      : grainValue === "(blank)"
        ? claims.filter((claim) => !def.drill(grainValue)(claim) && reportClaimHasUnresolvedGrain(claim, def))
        : claims.filter(def.drill(grainValue));
  }
  reportDrillState.path.slice(1).forEach((step) => {
    const level = REPORT_DRILL_LEVELS_UI[step.level];
    if (level) claims = claims.filter((claim) => level.of(claim) === step.value && (!step.orgId || String(claim.orgId || "") === String(step.orgId)));
  });
  // Both the claim and service-line rungs key on claimId, so the same filter serves them.

  return claims;
}

function reportDrillRowsFor(levelKey, claims) {
  const level = REPORT_DRILL_LEVELS_UI[levelKey];
  const rows = reportMeasureRowsFromClaims(claims, (claim) => level.of(claim));
  // Counts and the DOS range are per-group facts, so they are collected here rather than
  // summed like money.
  const extra = new Map();
  claims.forEach((claim) => {
    const key = level.of(claim);
    if (!key) return;
    let e = extra.get(key);
    if (!e) { e = { patients: new Set(), claims: new Set(), dates: [], label: String(claim.patient || claim.patientName || key || "—") }; extra.set(key, e); }
    e.patients.add(String(claim.patient || claim.patientName || ""));
    e.claims.add(String(claim.claimId || ""));
    (claim.services || []).forEach((service) => { if (service.dos) e.dates.push(String(service.dos)); });
  });
  rows.forEach((row) => {
    const e = extra.get(row.dims[0]) || { patients: new Set(), claims: new Set(), dates: [] };
    row.patientCount = e.patients.size;
    row.claimCount = e.claims.size;
    const sorted = e.dates.slice().sort();
    row.firstDos = sorted[0] || "";
    row.lastDos = sorted[sorted.length - 1] || "";
    if (levelKey === "patient" && reportDrillState.view === "patientResponsibility") row.drillLabel = e.label;
  });
  return rows.sort((a, b) => reportNum(b.outstanding) - reportNum(a.outstanding));
}

function reportDrillBreadcrumbHtml() {
  const def = reportDefinitionUi(reportDrillState.view);
  // The crumb keeps `linkbutton` so anything asserting on it still matches; `report-crumb`
  // carries the trail styling, which neutralises the boxed-button look that made a
  // four-rung path read as a row of jammed, mismatched boxes.
  const crumbs = [`<button type="button" class="linkbutton report-crumb" data-report-drill-to="0">${escapeHtml(def && def.title ? def.title : "Report")}</button>`];
  reportDrillState.path.forEach((step, index) => {
    const isLast = index === reportDrillState.path.length - 1;
    const label = escapeHtml(step.label || step.value);
    crumbs.push(isLast
      ? `<span class="report-crumb report-crumb-current" aria-current="page">${label}</span>`
      : `<button type="button" class="linkbutton report-crumb" data-report-drill-to="${index + 1}">${label}</button>`);
  });
  return `<nav class="report-breadcrumb" aria-label="Drill path">${crumbs.join('<span class="report-crumb-sep" aria-hidden="true">›</span>')}</nav>`;
}

// The bottom rung: real claims, each opening the claim drawer.
function renderReportDrillClaimList(claims) {
  const rows = claims.map((claim) => {
    const fin = claimFinancialView(claim);
    const dates = (claim.services || []).map((s) => String(s.dos || "")).filter(Boolean).sort();
    // TWO options at the bottom rung, as the tracker has: stay in the drilldown and read
    // the service lines, or leave for the claim in All Data. role=button is NOT put on
    // the row — it overrides role=row and takes the header/cell associations with it.
    return `<tr class="is-drillable">
      <td class="report-drill-cell report-drill-cell-dual">
        <button type="button" class="small-button report-drill-btn" data-report-claim-lines="${escapeHtml(claim.claimId)}" data-report-claim-org="${escapeHtml(String(claim.orgId || ""))}">View service lines</button>
        <button type="button" class="linkbutton report-goto-claim" data-report-open-claim="${escapeHtml(claim.claimId)}" data-report-open-org="${escapeHtml(String(claim.orgId || ""))}">Go to claim</button>
      </td>
      <th scope="row" class="report-grain-cell ar-drillable" data-report-claim-lines="${escapeHtml(claim.claimId)}" data-report-claim-org="${escapeHtml(String(claim.orgId || ""))}">${escapeHtml(claim.claimId)}</th>
      <td class="report-col-text is-band-start">${escapeHtml(claim.patient || "")}</td>
      <td class="report-col-text">${escapeHtml(claim.primaryPayer || "")}</td>
      <td class="report-col-number">${(claim.services || []).length}</td>
      <td class="report-col-text is-band-start">${escapeHtml(reportFormatDate(dates[0]))}</td>
      <td class="report-col-text">${escapeHtml(reportFormatDate(dates[dates.length - 1]))}</td>
      <td class="report-col-currency is-band-start">${escapeHtml(reportFormatCell("currency", fin.patientResponsibility))}</td>
      <td class="report-col-currency is-band-start">${escapeHtml(reportFormatCell("currency", fin.finalInsuranceExpected))}</td>
      <td class="report-col-currency">${escapeHtml(reportFormatCell("currency", fin.totalInsurancePaid))}</td>
      <td class="report-col-currency${reportNum(fin.finalInsuranceBalance) < 0 ? " is-negative" : ""}">${escapeHtml(reportFormatCell("currency", fin.finalInsuranceBalance))}</td>
    </tr>`;
  }).join("");
  return `<div class="table-wrap report-definition-wrap"><table class="report-definition-table">
    <thead>
      <tr class="report-band-row"><th class="report-band report-drill-band">Drilldown</th><th class="report-grain-head"></th><th colspan="3" class="report-band is-band-start">CLAIM</th><th colspan="2" class="report-band is-band-start">SERVICE DATES</th><th colspan="4" class="report-band is-band-start">MONEY</th></tr>
      <tr class="report-head-row"><th class="report-grain-head"></th><th class="report-grain-head">Claim ID</th><th class="is-band-start">Patient</th><th>Primary Payer</th><th class="report-col-number">Service Lines</th><th class="is-band-start">First DOS</th><th>Last DOS</th><th class="report-col-currency is-band-start">Patient Responsibility</th><th class="report-col-currency">Insurance Amount Expected</th><th class="report-col-currency">Total Applied Payments</th><th class="report-col-currency">Remaining Balance</th></tr>
    </thead>
    <tbody>${rows || `<tr><td colspan="11">No claims at this level.</td></tr>`}</tbody>
  </table></div>`;
}

// The rung KPI row. At the top level the biller's eye lands on Remaining Balance; one rung
// down the loudest thing became a navy band header, which is most of why a drill rung felt
// like a different, worse product. These RESTATE the three measures this rung's own tfoot
// already totals, off the same `totals` object and the same formatter - no new aggregation.
// Deliberately only these three, and they stay three now that a rung carries the report's
// whole column set: three tiles answer "where do I stand", fifteen would just be the table
// again in a louder font. Every key here is in REPORT_FINANCIAL_SPINE and so is present on
// every report's columns; a KPI key with no matching measure would quietly render as $0.00,
// and a wrong number beats no number only in the sense that it is worse.
function reportDrillKpisHtml(totals) {
  if (!totals) return "";
  // An unrated line contributes NOTHING to expected - that is unknown, not $0.00. The
  // top-level report says so above its tiles; a rung that shows the same three figures
  // without the caveat is a confident dollar number over a partly-unpriced book, and the
  // Remaining Balance card is exactly where that misleads. Same helper, same wording.
  const unpriced = typeof reportUnpricedNoteHtml === "function" ? reportUnpricedNoteHtml(totals, reportNum(totals.claimCount)) : "";
  const tiles = [
    ["Insurance Amount Expected", "finalInsuranceExpected", ""],
    ["Paid Toward Expected", "paidTowardExpected", ""],
    ["Remaining Balance", "remainingBalance", "primary"]
  ].map(([label, key, tone]) => {
    const value = reportNum(totals[key]);
    // A negative balance is an overpayment, not a win - same reading as the top level.
    const negative = key === "outstanding" && value < 0;
    return arKpiCard(label, reportFormatCell("currency", value), negative ? "Overpaid — collected above expected" : "", negative ? "warn" : tone);
  }).join("");
  return `${unpriced}<div class="ar-kpi-grid report-drill-kpis">${tiles}</div>`;
}

function renderReportDrillPanel() {
  const levelKey = reportDrillCurrentLevel();
  const claims = reportDrillScopedClaims();
  const nextKey = reportDrillNextLevel();
  const level = REPORT_DRILL_LEVELS_UI[levelKey];
  // "Payers in 0-30" beats "Primary Payer": four rungs down, a bare singular noun does not
  // tell a biller which slice of the report they are standing in.
  const drillParent = reportDrillState.path.length ? reportDrillState.path[reportDrillState.path.length - 1] : null;
  const drillScope = drillParent ? String(drillParent.label || drillParent.value || "") : "";
  const drillTitle = level ? (REPORT_DRILL_TITLES[levelKey] || { title: level.grainLabel, prep: "in" }) : { title: "", prep: "in" };
  const headingText = drillTitle.title + (drillScope ? ` ${drillTitle.prep} ${drillScope}` : "");
  const heading = `${reportDrillBreadcrumbHtml()}<h3 class="report-drill-title">${escapeHtml(headingText)}</h3>`;

  if (levelKey === "serviceLines") {
    const claim = claims[0];
    const fin = claim ? claimFinancialView(claim) : null;
    const lines = (fin && fin.serviceFinancials) || [];
    const services = (claim && claim.services) || [];
    const body = lines.map((line, index) => {
      const service = services[index] || {};
      return `<tr>
        <th scope="row" class="report-grain-cell">${escapeHtml(service.serviceId || String(index + 1))}</th>
        <td class="report-col-text is-band-start">${escapeHtml(reportFormatDate(service.dos))}</td>
        <td class="report-col-text">${escapeHtml(service.procedure || "")}</td>
        <td class="report-col-text">${escapeHtml([service.m1, service.m2].filter(Boolean).join(" "))}</td>
        <td class="report-col-number">${escapeHtml(reportFormatCell("number", service.units))}</td>
        <td class="report-col-currency is-band-start">${escapeHtml(reportFormatCell("currency", line.charges))}</td>
        <td class="report-col-currency">${escapeHtml(reportFormatCell("currency", line.staticTotalExpected || line.finalExpectedTotalNumeric))}</td>
        <td class="report-col-currency">${escapeHtml(reportFormatCell("currency", line.patientResponsibility))}</td>
        <td class="report-col-currency">${escapeHtml(reportFormatCell("currency", line.writeOffAmount))}</td>
        <td class="report-col-currency">${escapeHtml(reportFormatCell("currency", line.finalInsuranceExpected))}</td>
        <td class="report-col-currency is-band-start">${escapeHtml(reportFormatCell("currency", line.finalInsurancePaid))}</td>
        <td class="report-col-currency${reportNum(line.finalInsuranceBalance) < 0 ? " is-negative" : ""}">${escapeHtml(reportFormatCell("currency", line.finalInsuranceBalance))}</td>
        <td class="report-col-text">${escapeHtml(line.finalizedMatch || "")}</td>
      </tr>`;
    }).join("");
    const table = `<div class="table-wrap report-definition-wrap"><table class="report-definition-table">
      <thead>
        <tr class="report-band-row"><th class="report-grain-head"></th><th colspan="4" class="report-band is-band-start">SERVICE</th><th colspan="5" class="report-band is-band-start">EXPECTED BRIDGE</th><th colspan="3" class="report-band is-band-start">APPLIED &amp; BALANCE</th></tr>
        <tr class="report-head-row"><th class="report-grain-head">Service ID</th><th class="is-band-start">Srvc Date</th><th>Procedure</th><th>Modifiers</th><th class="report-col-number">Units</th><th class="report-col-currency is-band-start">Charge Amount</th><th class="report-col-currency">Gross Expected</th><th class="report-col-currency">Patient Responsibility</th><th class="report-col-currency">Write-off Amount</th><th class="report-col-currency">Insurance Amount Expected</th><th class="report-col-currency is-band-start">Total Applied Payments</th><th class="report-col-currency">Remaining Balance</th><th>Finalized Match</th></tr>
      </thead>
      <tbody>${body || `<tr><td colspan="13">No service lines on this claim.</td></tr>`}</tbody>
    </table></div>`;
    return `<section class="panel ar-report-panel"><div class="ar-report-head"><div>${heading}
      <p>${formatNounCount(lines.length, "service line")} on claim ${escapeHtml(claim ? claim.claimId : "")}.
      ${claim ? `<button type="button" class="linkbutton report-goto-claim" data-report-open-claim="${escapeHtml(claim.claimId)}" data-report-open-org="${escapeHtml(String(claim.orgId || ""))}">Go to claim \u2192</button>` : ""}</p>
    </div></div>${reportDrillKpisHtml(reportMeasureTotals(reportDrillRowsFor(levelKey, claim ? [claim] : [])))}${table}</section>`;
  }

  if (levelKey === "claim") {
    return `<section class="panel ar-report-panel"><div class="ar-report-head"><div>${heading}
      <p>${formatNounCount(claims.length, "claim")} \u2014 click one to open its service lines.</p>
    </div></div>${reportDrillKpisHtml(reportMeasureTotals(reportDrillRowsFor(levelKey, claims)))}${renderReportDrillClaimList(claims)}</section>`;
  }

  const rows = reportDrillRowsFor(levelKey, claims);
  const totals = reportMeasureTotals(rows);
  totals.patientCount = new Set(claims.map((c) => String(c.patient || ""))).size;
  totals.claimCount = new Set(claims.map((c) => String(c.claimId || ""))).size;
  totals.firstDos = "";
  totals.lastDos = "";
  const table = renderReportDefinitionTable(reportDrillState.view, { dimension: level.grainLabel }, rows, totals, {
    columns: reportDrillColumnsFor(reportDrillState.view, levelKey),
    grainLabel: level.grainLabel,
    rowLabel: (row) => row.drillLabel || String(row.dims?.[0] || "—"),
    drillLabel: nextKey ? REPORT_DRILL_LEVELS_UI[nextKey].label : "",
    drillAttr: "data-report-drill-down"
  });
  return `<section class="panel ar-report-panel"><div class="ar-report-head"><div>${heading}
    <p>${formatNounCount(rows.length, level.grainLabel.toLowerCase())} \u00b7 ${formatNounCount(claims.length, "claim")}${nextKey ? ` \u2014 click a row to see its ${escapeHtml(REPORT_DRILL_LEVELS_UI[nextKey].label)}.` : ""}</p>
  </div></div>${reportDrillKpisHtml(totals)}${table}</section>`;
}


function bindReportDrillControls() {
  const host = els.reportsBody;
  if (!host) return;

  // Enter the chain from a report row, at its first rung.
  host.querySelectorAll("[data-report-drill]").forEach((el) => {
    const open = () => {
      const value = el.getAttribute("data-report-drill");
      const def = reportDefinitionUi(reportArState.view);
      if (!def || typeof def.drill !== "function") return;
      reportDrillState.view = reportArState.view;
      // value is the grain KEY (what drill() matches); label is what the breadcrumb reads.
      reportDrillState.path = [{ level: "__grain__", value, label: el.getAttribute("data-report-drill-label") || value }];
      renderReports();
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });

  // Step down a rung.
  host.querySelectorAll("[data-report-drill-down]").forEach((el) => {
    const open = () => {
      // The clicked value belongs to the level currently ON SCREEN, not the one below.
      // Pushing it as the next level made the filter look for a PATIENT named after a payer.
      const level = reportDrillCurrentLevel();
      if (!level || !reportDrillNextLevel()) return;
      reportDrillState.path.push({ level, value: el.getAttribute("data-report-drill-down"), label: el.getAttribute("data-report-drill-label") || el.getAttribute("data-report-drill-down") });
      renderReports();
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });

  // Breadcrumb: index 0 is the report itself, so truncating to the index climbs back.
  host.querySelectorAll("[data-report-drill-to]").forEach((el) => {
    el.addEventListener("click", () => {
      reportDrillState.path = reportDrillState.path.slice(0, Number(el.getAttribute("data-report-drill-to")) || 0);
      if (!reportDrillState.path.length) reportDrillState.view = "";
      renderReports();
    });
  });

  // Bottom rung: open the claim itself.
  // "View service lines" keeps the biller inside the drilldown; "Go to claim" leaves.
  host.querySelectorAll("[data-report-claim-lines]").forEach((el) => {
    el.addEventListener("click", (event) => {
      event.stopPropagation();
      // (org_id, claim_id) is the key - trap 3. Without the org the service-line rung took the
      // first claim wearing this id, which on the live book is another practice 46.7% of the time.
      reportDrillState.path.push({ level: "claim", value: el.getAttribute("data-report-claim-lines"), orgId: el.getAttribute("data-report-claim-org") || "" });
      renderReports();
    });
  });

  host.querySelectorAll("[data-report-open-claim]").forEach((el) => {
    const open = () => {
      const claimId = el.getAttribute("data-report-open-claim");
      // A claim id does not identify a claim (ENGINEERING_TRAPS #3): carry the org the row
      // was rendered from, on the DOM and through this binder, so the drawer opens this
      // practice's claim and not another practice's claim with the same number.
      const orgId = el.getAttribute("data-report-open-org") || "";
      // openDrawer is the house function the All Data grid uses. The previous chain
      // guessed at openClaimDrawer/focusClaim, NEITHER OF WHICH EXISTS, so "Go to claim"
      // silently fell through to a filter instead of opening the claim.
      if (typeof openDrawer === "function") openDrawer(claimId, orgId ? { orgId } : {});
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
}

const reportPaymentEvidenceState = { view: "", status: "idle", response: null, error: "", requestId: 0, sessionKey: "", queryKey: "", request: null, page: 1 };
function resetReportPaymentEvidence() { const requestId = reportPaymentEvidenceState.requestId + 1; Object.assign(reportPaymentEvidenceState, { view: "", status: "idle", response: null, error: "", requestId, sessionKey: "", queryKey: "", request: null, page: 1 }); }
async function requestReportPaymentEvidence({ role, clickedAmount, context } = {}) {
  const model = preparedSnapshotReportState.response;
  if (!model || !canOpenReportPaymentEvidence() || !REPORT_PAYMENT_EVIDENCE_KEYS.has(role) || !context) return;
  const requestView = reportArState.view; const requestSessionKey = preparedSnapshotSessionKey(); const requestQueryKey = preparedSnapshotQueryKey(requestView); const requestId = ++reportPaymentEvidenceState.requestId;
  const request = { report: requestView, dimension: model.dimension || "", expectedGeneration: model.generation, filters: preparedSnapshotFilters(requestView), root: context.root, path: context.path || [], ...(context.selectedService ? { selectedService: context.selectedService } : {}), role, clickedAmount: Number(clickedAmount) };
  const stillCurrent = () => reportPaymentEvidenceState.requestId === requestId && reportArState.view === requestView && preparedSnapshotSessionKey() === requestSessionKey && preparedSnapshotQueryKey(requestView) === requestQueryKey && canOpenReportPaymentEvidence();
  Object.assign(reportPaymentEvidenceState, { view: requestView, status: "pending", response: null, error: "", retryable: true, sessionKey: requestSessionKey, queryKey: requestQueryKey, request, page: 1 }); renderReports({ settledRead: true });
  try {
    const token = trackerApiToken();
    const { response, body } = await reportReadJson("payment-evidence", `${trackerApiBase()}/api/reports/payment-evidence`, { method: "POST", cache: "no-store", headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(request) });
    if (!stillCurrent()) return;
    if (response.status === 401) { if (typeof notePortalSessionExpired === "function") notePortalSessionExpired(); throw new Error("Your session expired. Sign in again — this page will still be here."); }
    if (!response.ok || body.complete !== true || body.generation !== model.generation || !Array.isArray(body.events)) {
      const error = new Error(body.message || "Payment detail is unavailable.");
      error.retryable = ![400, 409, 422].includes(response.status);
      throw error;
    }
    Object.assign(reportPaymentEvidenceState, { status: "ok", response: body, error: "" });
  } catch (error) { if (!stillCurrent()) return; Object.assign(reportPaymentEvidenceState, { status: "error", error: error.message || "Payment detail is unavailable.", retryable: error.retryable !== false }); }
  if (stillCurrent()) renderReports({ settledRead: true });
}
function reportPaymentEvidenceHtml(view) {
  const stateEvidence = reportPaymentEvidenceState; const response = stateEvidence.response || {}; const all = response.events || []; const size = 100; const pages = Math.max(1, Math.ceil(all.length / size)); const page = Math.min(pages, Math.max(1, stateEvidence.page || 1)); const rows = all.slice((page - 1) * size, page * size);
  const back = `<button type="button" class="linkbutton" data-report-payment-back>Back to ${hostedReportDrillState.view === view && hostedReportDrillState.root ? "report detail" : escapeHtml(preparedReportTitle(view))}</button>`;
  if (stateEvidence.status === "pending") return `<section class="panel"><nav aria-label="Payment detail breadcrumb">${back}<span class="report-crumb-sep" aria-hidden="true">›</span>Payment detail</nav><p role="status" aria-busy="true"><span class="report-loading-spinner" aria-hidden="true"></span>Loading payment detail…</p></section>`;
  if (stateEvidence.status === "error") return `<section class="panel"><nav aria-label="Payment detail breadcrumb">${back}<span class="report-crumb-sep" aria-hidden="true">›</span>Payment detail</nav><p role="alert">${escapeHtml(stateEvidence.error)}</p>${stateEvidence.retryable === false ? "<p>Return using the link above and choose a smaller or refreshed report detail.</p>" : '<button type="button" data-report-payment-retry>Retry</button>'}</section>`;
  const body = rows.map((event) => `<tr><td><button type="button" class="small-button" data-report-payment-claim="${escapeHtml(event.claimId)}" data-report-payment-org="${escapeHtml(event.orgId)}" data-report-payment-service="${escapeHtml(event.serviceLineId)}">Open claim</button></td><td>${escapeHtml(reportFormatDate(event.date))}</td><td>${escapeHtml(event.source || "No payer")}</td><td>${escapeHtml(event.attributedTo || "Unmatched")}</td><td>${escapeHtml(event.ref || "—")}</td><td class="report-col-currency${Number(event.applied) < 0 ? " is-negative" : ""}">${escapeHtml(reportFormatCell("currency", event.applied))}</td></tr>`).join("");
  return `<section class="panel ar-report-panel"><nav class="report-drill-breadcrumbs" aria-label="Payment detail breadcrumb">${back}<span class="report-crumb-sep" aria-hidden="true">›</span>Payment detail</nav><h3>${escapeHtml(({ primaryApplied: "Primary insurance payments", secondaryApplied: "Secondary insurance payments", unattributedApplied: "Unattributed payments", insurancePaid: "Total insurance payments" })[response.role] || "Applied payments")}</h3><p><strong>${escapeHtml(reportFormatCell("currency", response.amount))}</strong> across ${Number(response.events?.length || 0).toLocaleString()} signed payment events. Negative amounts are recoupments.</p><div class="table-wrap report-definition-wrap"><table class="report-definition-table"><thead><tr><th>Actions</th><th>Date</th><th>Payer</th><th>Applied to</th><th>Reference</th><th class="report-col-currency">Amount</th></tr></thead><tbody>${body || '<tr><td colspan="6">No payment events in this exact selection.</td></tr>'}</tbody></table></div><div class="report-pager" aria-label="Payment detail pages">${page > 1 ? '<button type="button" data-report-payment-page="prev">Previous</button>' : ""}<span>Page ${page} of ${pages}</span>${page < pages ? '<button type="button" data-report-payment-page="next">Next</button>' : ""}</div></section>`;
}

const HOSTED_REPORT_DRILL_VIEWS = new Set(["ar", "expectedVsPaid", "billDateMonthlyAging", "billDateAging", "denials", "clientAction", "performance", "reviewQueue", "rollforward", "monthlySummary", "patientResponsibility", "cptPayer"]);
const hostedReportDrillState = { view: "", root: null, path: [], targetLevel: "payer", status: "idle", response: null, error: "", requestId: 0, sessionKey: "", queryKey: "" };
const HOSTED_REPORT_DRILL_LEVELS = ["payer", "patient", "claim", "service"];
const HOSTED_PATIENT_RESPONSIBILITY_DRILL_LEVELS = ["patient", "claim", "service"];
function hostedReportDrillLevels(view) {
  if (view !== "patientResponsibility") return HOSTED_REPORT_DRILL_LEVELS;
  return patientResponsibilityReportState.view === "patient" ? ["service"] : ["patient", "service"];
}
// Display vocabulary for the hosted rungs. `head` is the grain column header (the raw
// level key used to be printed there verbatim, lowercase); `title` and `prep` build a
// heading that says WHERE you are rather than just naming the grain.
const HOSTED_REPORT_DRILL_LABELS = {
  payer: { head: "Primary Payer", title: "Payers", prep: "in" },
  patient: { head: "Patient", title: "Patients", prep: "in" },
  claim: { head: "Claim", title: "Claims", prep: "for" },
  service: { head: "Service Line", title: "Service lines", prep: "on claim" }
};

function resetHostedReportDrill() { const requestId = hostedReportDrillState.requestId + 1; Object.assign(hostedReportDrillState, { view: "", root: null, path: [], targetLevel: "payer", status: "idle", response: null, error: "", requestId, sessionKey: "", queryKey: "" }); }
async function requestHostedReportDrill(page = 1) {
  const parent = preparedSnapshotReportState.response;
  if ((!parent && hostedReportDrillState.view !== "turnaround") || !hostedReportDrillState.root) return;
  const requestId = ++hostedReportDrillState.requestId;
  const requestView = hostedReportDrillState.view;
  const requestSessionKey = preparedSnapshotSessionKey();
  const requestQueryKey = requestView === "turnaround" ? JSON.stringify(turnaroundReportFilters()) : preparedSnapshotQueryKey(requestView);
  const requestRoot = JSON.parse(JSON.stringify(hostedReportDrillState.root));
  const requestPath = JSON.parse(JSON.stringify(hostedReportDrillState.path));
  const requestTargetLevel = hostedReportDrillState.targetLevel;
  const requestPage = Math.max(1, Number(page) || 1);
  const requestStillCurrent = () => hostedReportDrillState.requestId === requestId && hostedReportDrillState.view === requestView && preparedSnapshotSessionKey() === requestSessionKey && (requestView === "turnaround" ? JSON.stringify(turnaroundReportFilters()) : preparedSnapshotQueryKey(requestView)) === requestQueryKey && JSON.stringify(hostedReportDrillState.root) === JSON.stringify(requestRoot) && JSON.stringify(hostedReportDrillState.path) === JSON.stringify(requestPath) && hostedReportDrillState.targetLevel === requestTargetLevel;
  hostedReportDrillState.status = "pending"; hostedReportDrillState.error = ""; renderReports({ settledRead: true });
  try {
    const token = trackerApiToken();
    const turnaround = requestView === "turnaround";
    const requestBody = turnaround ? { filters: turnaroundReportFilters(), payer: requestRoot, path: requestPath, targetLevel: requestTargetLevel, page: requestPage, pageSize: 100 } : { report: requestView, dimension: parent.dimension || "", expectedGeneration: parent.generation, filters: preparedSnapshotFilters(requestView), root: requestRoot, path: requestPath, targetLevel: requestTargetLevel, page: requestPage, pageSize: 100, stateIdentity: hostedReportDrillState.response?.stateIdentity || "" };
    const { response, body } = await reportReadJson("report-drill", `${trackerApiBase()}${turnaround ? "/api/reports/turnaround/drill" : "/api/reports/drill"}`, { method: "POST", cache: "no-store", headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(requestBody) });
    if (!requestStillCurrent()) return;
    if (response.status === 409) { resetHostedReportDrill(); notePreparedReportReload(); return; }
    if (!response.ok) throw new Error(body.message || "Report detail is unavailable.");
    hostedReportDrillState.status = "ok"; hostedReportDrillState.response = body;
  } catch (error) { if (!requestStillCurrent()) return; hostedReportDrillState.status = "error"; hostedReportDrillState.error = error.message || "Report detail is unavailable."; }
  if (requestStillCurrent()) renderReports({ settledRead: true });
}
// SCOPE columns per rung: which record is this, how many things does it cover, over what
// dates. The MEASURE columns are deliberately absent here — those are the report's own
// def.columns, so a rung can never offer a narrower money set than the row that opened it.
function hostedReportDrillScopeColumns(level) {
  const client = reportMeasureCol("client", "Client", "PERIOD & VOLUME", "text");
  const firstDos = reportMeasureCol("firstDos", "First DOS", "PERIOD & VOLUME", "date");
  const lastDos = reportMeasureCol("lastDos", "Last DOS", "PERIOD & VOLUME", "date");
  const claimCount = reportMeasureCol("claimCount", "Claim Count", "PERIOD & VOLUME", "number");
  if (level === "payer") return [client, reportMeasureCol("patientCount", "Patient Count", "PERIOD & VOLUME", "number"), claimCount, firstDos, lastDos];
  if (level === "patient") return [client, claimCount, firstDos, lastDos];
  if (level === "claim") return [client, reportMeasureCol("claimStatus", "Claim Status", "PERIOD & VOLUME", "text"), firstDos, lastDos];
  // The leaf names the line itself. Srvc Date and Procedure are what a biller reads a
  // service line by; the browser-lane service table already shows both.
  return [client, reportMeasureCol("serviceDate", "Srvc Date", "PERIOD & VOLUME", "date"), reportMeasureCol("procedureCode", "Procedure", "PERIOD & VOLUME", "text"), reportMeasureCol("finalizedMatch", "Finalized Match", "BALANCE & PERFORMANCE", "text")];
}

function hostedReportDrillHtml(view) {
  if (reportPrintComplete) return `<section class="panel"><h3>Complete report detail requires an export</h3><p>This detail is loaded in server pages. Return to the report and use its complete export; a single detail page is not a complete report.</p></section>`;
  const stateDrill = hostedReportDrillState;
  const crumbs = [`<button type="button" class="linkbutton report-crumb" data-hosted-drill-home>${escapeHtml(preparedReportTitle(view))}</button>`, ...stateDrill.path.map((step, index) => `<button type="button" class="linkbutton report-crumb" data-hosted-drill-back="${index}">${escapeHtml(hostedDrillCrumbLabel(step))}</button>`)].join('<span class="report-crumb-sep" aria-hidden="true">›</span>');
  // NAME WHAT IS LOADING AND WHAT IT IS LOADING FROM.
  //
  // "Loading report detail..." is true of every rung of every report, so it tells a
  // reader nothing about whether the thing they clicked is the thing that is working.
  // Measured live, the 121+ bucket - 164,065 service lines - took 22 seconds with
  // nothing on screen naming it, and a user cannot tell "working" from "broken" in
  // that gap. The level and the scope are both already in state; say them.
  if (stateDrill.status === "pending") {
    const pendingLevel = HOSTED_REPORT_DRILL_LABELS[stateDrill.targetLevel] || { title: "Detail", prep: "in" };
    const pendingParent = stateDrill.path.length ? stateDrill.path[stateDrill.path.length - 1] : null;
    const pendingScope = pendingParent
      ? hostedDrillStepLabel(pendingParent)
      : String((stateDrill.root && Array.isArray(stateDrill.root.dimensions) && stateDrill.root.dimensions.map((d) => d && d.value).filter(Boolean).join(" · ")) || "");
    const pendingWhere = pendingScope ? ` ${pendingLevel.prep} ${escapeHtml(pendingScope)}` : "";
    return `<section class="panel"><p>${crumbs}</p><p role="status" aria-busy="true"><span class="report-loading-spinner" aria-hidden="true"></span>Loading ${escapeHtml(pendingLevel.title.toLowerCase())}${pendingWhere}… this can take about 20 seconds on a large bucket.</p></section>`;
  }
  if (stateDrill.status === "error") return `<section class="panel"><p>${crumbs}</p><p role="alert">${escapeHtml(stateDrill.error)}</p><button data-hosted-drill-retry>Retry</button></section>`;
  const model = stateDrill.response || {}; const rows = model.rows || []; const level = stateDrill.targetLevel;
  if (view === "patientResponsibility") return patientResponsibilityDrillHtml(model, rows, level, crumbs);
  const drillLabels = HOSTED_REPORT_DRILL_LABELS[level] || { head: level, title: level, prep: "in" };
  const drillParent = stateDrill.path.length ? stateDrill.path[stateDrill.path.length - 1] : null;
  const drillScope = drillParent ? hostedDrillStepLabel(drillParent) : String(preparedReportTitle(view) || "");
  // COLUMN PARITY WITH THE REPORT THAT OPENED THIS RUNG (owner directive 2026-08-22).
  // This rung used to hand-roll ten cells, so drilling a sixteen-column AR row landed on
  // eight numbers and neither percentage. The measure columns are now the report's OWN
  // def.columns — the same objects the table above renders — so a column added to a report
  // reaches its drill for free and the two can no longer disagree about what a report offers.
  // NOTHING IS RECOMPUTED HERE. renderReportDefinitionTable prints the server's own measures
  // through reportFormatCell, and a percentage is reportRatioValue over the same two measures
  // the report divides — never a second definition of the same ratio.
  const def = reportDefinitionUi(view);
  const columns = [...hostedReportDrillScopeColumns(level), ...reportDrillMeasureColumns(view, level)];
  const rowIdentity = (row) => String((level === "payer" ? row.payer : level === "patient" ? row.patient : level === "claim" ? row.claimId : row.serviceId) || "—");
  const rowDrill = (row) => level === "service"
    ? `<button type="button" class="small-button" data-hosted-drill-claim="${escapeHtml(row.claimId)}" data-hosted-drill-org="${escapeHtml(row.orgId)}" data-hosted-drill-service="${escapeHtml(row.serviceId)}">Open claim</button>`
    : `<button type="button" class="small-button" data-hosted-drill-next="${escapeHtml(JSON.stringify({ level, payer: row.payer, patient: row.patient, patientId: row.patientId, orgId: row.orgId, claimId: row.claimId, serviceId: row.serviceId }))}">View detail →</button>`;
  // Unchanged from the hand-rolled rung: an evidence link needs the ancestor chain, and at the
  // leaf the exact service line rather than one more path step.
  const paymentContext = (row) => ({ root: stateDrill.root, path: level === "service" ? stateDrill.path : [...stateDrill.path, { level, payer: row.payer, patient: row.patient, patientId: row.patientId, orgId: row.orgId, claimId: row.claimId }], ...(level === "service" ? { selectedService: { orgId: row.orgId, serviceId: row.serviceId } } : {}) });
  const table = renderReportDefinitionTable(view, { dimension: drillLabels.head }, rows, {}, {
    columns, grainLabel: drillLabels.head, tableClass: "report-hosted-drill",
    rowLabel: rowIdentity, rowDrill, paymentContext,
    // One page of N. A footer summing the visible page would read lower than the row above it.
    hideTotals: true, emptyText: "No detail in this selection."
  });
  return `<section class="panel ar-report-panel"><div class="ar-report-head"><div><nav class="report-drill-breadcrumbs" aria-label="Report detail breadcrumb">${crumbs}</nav><h3 class="report-drill-title">${escapeHtml(drillLabels.title)}${drillScope ? ` ${escapeHtml(drillLabels.prep)} ${escapeHtml(drillScope)}` : ""}</h3><p>${Number(model.totalRows || 0).toLocaleString()} results. Amounts reflect the current calculations.</p>${view === "turnaround" ? "" : reportRateCoverageNoteHtml(model)}${level === "claim" || level === "service" ? `<p class="muted-note">Newest ${level === "claim" ? "Last DOS" : "service date"} first. Undated records appear last.</p>` : ""}</div></div>${table}<div class="report-pager" aria-label="Report detail pages">${model.page > 1 ? '<button type="button" data-hosted-drill-page="prev">Previous</button>' : ""}<span>Page ${Number(model.page || 1).toLocaleString()} of ${Math.max(1, Math.ceil(Number(model.totalRows || 0) / Math.max(1, Number(model.pageSize || 100)))).toLocaleString()}</span>${Number(model.page || 1) * Number(model.pageSize || 100) < Number(model.totalRows || 0) ? '<button type="button" data-hosted-drill-page="next">Next</button>' : ""}</div></section>`;
}

// ---------------------------------------------------------------------------
// STILL OUTSTANDING - THE CLIENT LANDING HEADLINE (Nathan, 2026-09-23).
// ---------------------------------------------------------------------------
// A Client lands on Reports at AR Aging Summary. This headline sits on top of
// that report and is built from THAT REPORT'S OWN MODEL - the same rows and the
// same totals the table beneath it renders, under the same filters - so it
// computes nothing and can never disagree with the report it introduces:
//
//   Insurance still owes - the report's Open Insurance AR (openOwed), the
//     open-AR basis (owed > 0 lines only), per aging bucket and in total.
//   Patient owes - the report's Patient Responsibility (patientResp), the PR
//     1/2/3 share on every arm since the 2026-09-17 ruling, per bucket and in
//     total.
//
// THE HEADLINE MUST EQUAL THE SUM OF THE ROWS IT LINKS TO. Both totals are
// checked to the cent against their own bucket rows on every paint; a total
// whose rows are incomplete, unavailable or do not add up renders Unavailable
// with a sentence, never a forced figure. No combined "insurance + patient"
// figure is shown: the report does not compute one, so neither does this.
//
// Each bucket opens that bucket in the report - the report's own drill, on the
// same attribute the table's drill buttons carry, so the same binder handles it
// and it opens only where the table itself offers the drill.
//
// Client only: an external client holding financials. RedactedClient has no
// Reports and sees no money; internal roles keep the report exactly as it was.
function clientOutstandingHeadlineVisible() {
  // A signed-in role, explicitly: canonicalRole reads a missing role as Client
  // (fail closed for access), which must not be read as "show the client view".
  return Boolean(state?.role) && isExternalClientRole(state.role) && !isRedactedClientRole() && hasPermission(PERMISSIONS.VIEW_FINANCIALS);
}

function clientOutstandingHeadlineModel(rows = [], totals = {}, { complete = true } = {}) {
  // The capped Remaining Balance is the report's AR column; a lane that does not serve it
  // keeps the owed-based basis for every bucket and the total alike.
  const arKey = totals && totals.remainingBalance !== undefined ? "remainingBalance" : "openOwed";
  const buckets = (Array.isArray(rows) ? rows : []).map((row) => ({
    key: String(row?.dims?.[0] ?? ""),
    dims: Array.isArray(row?.dims) ? row.dims : [],
    label: preparedReportGroupLabel("ar", row?.dims),
    insurance: outstandingWaterfallAmount(arKey === "remainingBalance" ? row?.remainingBalance : row?.openOwed),
    patient: outstandingWaterfallAmount(row?.patientResp)
  }));
  const side = (field, total) => {
    const reported = outstandingWaterfallAmount(total);
    const values = buckets.map((bucket) => bucket[field]);
    const rowCents = complete === true && values.every((value) => value !== null)
      ? values.reduce((sum, value) => sum + Math.round(value * 100), 0)
      : null;
    const foots = reported !== null && rowCents !== null && Math.round(reported * 100) === rowCents;
    return { reported, rowsKnown: rowCents !== null, foots, value: foots ? reported : null };
  };
  const insurance = side("insurance", totals?.[arKey]);
  const patient = side("patient", totals?.patientResp);
  let note = "";
  for (const [name, part] of [["Insurance still owes", insurance], ["Patient owes", patient]]) {
    if (part.foots) continue;
    note = part.reported === null
      ? name + " is not available for this selection yet; the report below has the detail."
      : !part.rowsKnown
        ? "Not every aging bucket is available yet, so " + name + " is not shown as a total; the report below has the detail."
        : "The aging buckets do not add up to the report total for " + name + " yet, so it is not shown; the report below has the detail.";
    break;
  }
  return { buckets, insurance, patient, note, foots: insurance.foots && patient.foots };
}

function clientOutstandingHeadlineHtml(rows = [], totals = {}, { complete = true, drill = "none" } = {}) {
  if (!clientOutstandingHeadlineVisible()) return "";
  const model = clientOutstandingHeadlineModel(rows, totals, { complete });
  const bucketHtml = (bucket) => {
    const body = `<span>${escapeHtml(bucket.label)}</span><strong data-client-outstanding-bucket-insurance>${clientReportMoneyLabel(bucket.insurance)}</strong><small data-client-outstanding-bucket-patient>Patient ${clientReportMoneyLabel(bucket.patient)}</small>`;
    if (drill === "hosted") return `<button type="button" class="client-outstanding-bucket" data-client-outstanding-bucket="${escapeHtml(bucket.key)}" data-hosted-report-drill="${escapeHtml(JSON.stringify(bucket.dims))}">${body}</button>`;
    if (drill === "local") return `<button type="button" class="client-outstanding-bucket" data-client-outstanding-bucket="${escapeHtml(bucket.key)}" data-report-drill="${escapeHtml(bucket.key)}" data-report-drill-label="${escapeHtml(bucket.label)}">${body}</button>`;
    return `<div class="client-outstanding-bucket" data-client-outstanding-bucket="${escapeHtml(bucket.key)}">${body}</div>`;
  };
  return `
  <section class="client-outstanding-headline" data-client-outstanding-headline="ready" data-headline-foots="${model.foots ? "true" : "false"}" aria-label="What is still outstanding">
    <div class="client-outstanding-head">
      <h2>Still outstanding</h2>
      <p>What is still owed on the claims in this report, split by who owes it.${drill === "none" ? "" : " Select an aging bucket to open it."}</p>
    </div>
    <div class="client-outstanding-split">
      <div class="client-outstanding-figure" data-client-outstanding-part="insurance">
        <span>Insurance still owes</span>
        <strong data-client-outstanding-value="insurance">${clientReportMoneyLabel(model.insurance.value)}</strong>
        <small>Open insurance balance: the Open Insurance AR column below.</small>
      </div>
      <div class="client-outstanding-figure" data-client-outstanding-part="patient">
        <span>Patient owes</span>
        <strong data-client-outstanding-value="patient">${clientReportMoneyLabel(model.patient.value)}</strong>
        <small>Deductible, coinsurance and copay the payers assigned to patients (PR 1/2/3): the Patient Responsibility column below. The Patient Responsibility report and Patient Statements count only lines that can be billed to the patient, so their totals can be lower.</small>
      </div>
    </div>
    <div class="client-outstanding-aging" role="group" aria-label="Still outstanding by aging bucket">
      ${model.buckets.map(bucketHtml).join("")}
    </div>
    ${model.note ? `<p class="client-outstanding-note" data-client-outstanding-note>${escapeHtml(model.note)}</p>` : ""}
  </section>`;
}

function renderPreparedSnapshotReport(view) {
  if (reportPaymentEvidenceState.view && (reportPaymentEvidenceState.view !== view || reportPaymentEvidenceState.sessionKey !== preparedSnapshotSessionKey() || reportPaymentEvidenceState.queryKey !== preparedSnapshotQueryKey(view) || !canOpenReportPaymentEvidence())) resetReportPaymentEvidence();
  if (hostedReportDrillState.view && (hostedReportDrillState.view !== view || hostedReportDrillState.sessionKey !== preparedSnapshotSessionKey() || hostedReportDrillState.queryKey !== preparedSnapshotQueryKey(view))) resetHostedReportDrill();
  const patientControls = view === "patientResponsibility" ? patientResponsibilityControlsHtml() : "";
  if (view === "patientResponsibility" && patientResponsibilityReportState.view === "patient" && !patientResponsibilityReportState.search.trim()) {
    return `${patientControls}<section class="panel ar-report-panel patient-responsibility-search-prompt" role="region" aria-label="Patient Responsibility patient search"><h3>Ready to find a patient</h3><p>Search above, or browse balances by insurance or service month.</p></section>`;
  }
  if (preparedSnapshotReportState.view !== view || preparedSnapshotReportState.sessionKey !== preparedSnapshotSessionKey() || preparedSnapshotReportState.queryKey !== preparedSnapshotQueryKey(view) || preparedSnapshotReportState.status === "idle") { requestPreparedSnapshotReport(view, preparedSnapshotReportState.view === view && preparedSnapshotReportState.sessionKey === preparedSnapshotSessionKey() && preparedSnapshotReportState.queryKey === preparedSnapshotQueryKey(view) ? (preparedSnapshotReportState.page || 1) : 1); return `${patientControls}${reportLoadingHtml(preparedReportTitle(view))}`; }
  if (preparedSnapshotReportState.status === "pending") return `${patientControls}${reportLoadingHtml(preparedReportTitle(view), preparedSnapshotReportState.slow)}`;
  if (preparedSnapshotReportState.status !== "ok") {
    // A REFUSED SEARCH IS THE READER'S OWN INPUT, NOT AN OUTAGE. A patient search matching
    // more than 100 people is refused by the server (400, patient_responsibility_search_*);
    // heading that "Patient Responsibility unavailable" and offering Retry told the biller
    // the service was down and invited them to repeat the exact request that was refused.
    // The message and the search form above are the remedy. Recognised by the server's own
    // sentence (trackerApi handlePreparedReportModel patientSearchMessage) so the fetch
    // handler, which another lane owns, is untouched.
    const inputProblem = view === "patientResponsibility" && patientResponsibilityReportState.view === "patient"
      && /matches too many patients|Search by patient name or patient ID/i.test(String(preparedSnapshotReportState.error || ""));
    return `${patientControls}<section class="panel"><h3>${escapeHtml(preparedReportTitle(view))}${inputProblem ? "" : " unavailable"}</h3><p role="alert">${escapeHtml(preparedSnapshotReportState.error)}</p>${view === "patientResponsibility" ? '<p>Change or clear the patient search above, or choose By insurance or By service month.</p>' : ""}${inputProblem ? "" : '<button type="button" class="primary-button" data-prepared-report-retry>Retry</button>'}</section>`;
  }
  const model = preparedSnapshotReportState.response; const totals = model.totals || {}; const groups = preparedReportOrderedGroups(view, model.groups || []); const claimsExpanded = preparedClaimsExpandedViews.has(view); const claims = claimsExpanded ? (model.claimPage?.rows || []) : [];
  if (reportPaymentEvidenceState.view === view && reportPaymentEvidenceState.status !== "idle") return reportPaymentEvidenceHtml(view);
  if (hostedReportDrillState.view === view && hostedReportDrillState.root) return `${patientControls}${hostedReportDrillHtml(view)}`;
  if (view === "patientResponsibility") return `${patientControls}${patientResponsibilityResultsHtml(model, groups, totals)}`;
  const moneyCell = (value) => value == null ? "Unavailable" : money(value);
  if (view === "rateIssues") {
    const serverPage = model.groupPage;
    const rateSlice = serverPage ? { rows: groups, page: Number(serverPage.page) || 1, pages: Math.max(1, Math.ceil(Number(serverPage.totalRows) / Number(serverPage.pageSize || 50))),
      start: ((Number(serverPage.page) || 1) - 1) * Number(serverPage.pageSize || 50), total: Number(serverPage.totalRows) || 0 } : reportPageSlice(groups);
    if (serverPage) reportPageState.page = rateSlice.page;
    const rows = rateSlice.rows.map((row) => `<tr><th>${escapeHtml((row.dims || []).filter(Boolean).join(" · ") || "—")}</th><td>${Number(row.services || 0).toLocaleString()}</td><td>${Number(row.claimCount || 0).toLocaleString()}</td><td>${row.units == null ? "Unavailable" : Number(row.units).toLocaleString()}</td><td>${moneyCell(row.openAr)}</td></tr>`).join("");
    return `<section class="panel ar-report-panel"><div class="ar-report-head"><div><h3>Rate Issues — Complete Missing Rates Authority</h3><p>${formatNounCount(totals.groups || 0,"rate combination")} · ${formatNounCount(totals.services || 0,"service line")} · ${formatNounCount(totals.claims || 0,"claim")}.</p><p>Only aggregate rate combinations are shown; no patient or claim identifiers are included.</p></div>${hasPermission(PERMISSIONS.REPORT_EXPORT)?'<button type="button" class="ghost-button" data-prepared-report-export>Export complete rate combinations</button>':""}</div><div class="table-wrap"><table><thead><tr><th>Client · Role · Payer · CPT</th><th>Services</th><th>Claims</th><th>Units</th><th>Open AR</th></tr></thead><tbody>${rows || '<tr><td colspan="5">No missing-rate combinations in this complete scope.</td></tr>'}</tbody><tfoot><tr><th>Total</th><td>${Number(totals.services||0).toLocaleString()}</td><td>${Number(totals.claims||0).toLocaleString()}</td><td>—</td><td>${moneyCell(totals.openAr)}</td></tr></tfoot></table></div>${reportPagerHtml(rateSlice)}</section>`;
  }
  const performanceLens = view === "performance" ? `<label class="checkbox-control"><input type="checkbox" data-prepared-performance-young ${reportPerformanceState.includeYoungClaims ? "checked" : ""}> Include claims under 60 days from bill date</label>` : "";
  // The server sends one group page plus independent complete totals.
  const groupSlice = preparedReportPageSlice(model, groups);
  const groupRows = groupSlice.rows.map((g) => `<tr><th>${escapeHtml(preparedReportGroupLabel(view, g.dims))}</th><td>${Number(g.claimCount || 0).toLocaleString()}</td><td>${moneyCell(g.charges)}</td><td>${moneyCell(g.totalExpected)}</td><td>${moneyCell(g.insurancePaid)}</td><td>${moneyCell(g.remainingBalance ?? g.outstanding)}</td></tr>`).join("");
  const claimRows = claims.map((row) => `<tr><td>${escapeHtml(row.claimId)}</td><td>${escapeHtml(row.client)}</td><td>${escapeHtml(view === "payerClass" ? `${row.payerRole} · ${row.payerClass} · ${row.payer}` : row.payer)}</td><td>${escapeHtml(row.claimStatus || row.statusCategory)}</td><td>${row.lastDos ? formatDate(row.lastDos) : "—"}</td><td>${row.billDateAging == null ? "Unavailable" : `${Number(row.billDateAging).toLocaleString()} days`}</td><td>${moneyCell(row.totalExpected)}</td><td>${moneyCell(row.insurancePaid)}</td><td>${moneyCell(row.outstanding)}</td></tr>`).join("");
  const claimBody = model.claimPage?.pending
    ? '<tr><td colspan="9" role="status">Loading claims…</td></tr>'
    : model.claimPage?.error
      ? `<tr><td colspan="9" role="alert">${escapeHtml(model.claimPage.error)} <button type="button" class="ghost-button" data-prepared-claims-retry>Retry</button></td></tr>`
      : (claimRows || preparedSnapshotEmptyClaimsHtml(model));
  const claimTotal = preparedPopulationClaimCount(model);
  // CAPPED PER LINE, NOT CLAMPED (Nathan, 2026-10-12, superseding the uncapped rate).
  // Each line counts at most what was expected, so one line's overpayment can no longer
  // stand in for another line's shortfall. The excess stays visible as its own Overpaid
  // figure; lines with no rate on file stay out of both terms.
  const expectedValue = Number(totals.expectedRated);
  const paidValue = Number(totals.paidTowardExpected);
  const collectionRate = Number.isFinite(expectedValue) && expectedValue > 0 && Number.isFinite(paidValue) ? (paidValue / expectedValue) * 100 : null;
  const performanceKpis = view === "performance" ? `<p class="muted-note performance-methodology">Billed claims only — ready-to-submit and client-action-pending are excluded. Claims under 60 days from bill date stay hidden unless you include them. Collection rate is ${collectionRate == null ? "unavailable" : `${collectionRate.toFixed(2)}%`} (paid ÷ expected) for the current selection.</p>` : "";
  const reviewNote = view === "reviewQueue" ? "<p>This is the Operational Review Queue, grouped by open work queue; it is not the separate payment-attribution approval queue. Closed queues are excluded.</p>" : "";
  const clientActionPurpose = view === "clientAction" ? "<p>This worklist shows claims blocked until the client provides authorization or coverage information, COB details, credentialing, or EVV. Use it to see the needed action, owner, follow-up due date, and expected insurance reimbursement being delayed. A row leaves this report when its Client Action Pending marker is cleared, even if the broader claim status is Closed.</p>" : "";
  const populationLine = view === "clientAction"
    ? `<p>${formatNounCount(preparedPopulationClaimCount(model),"claim")} across ${formatNounCount(model.population?.linesScanned || 0,"service line")} waiting on client action.</p>`
    : `<p>Report calculated ${escapeHtml(preparedReportStampLabel(model.generatedAt || model.generation || ""))} · ${formatNounCount(preparedPopulationClaimCount(model),"claim")} · ${formatNounCount(model.population?.linesScanned || 0,"service line")}.</p>`;
  const t1aNote = ["rateIssues", "clientAction"].includes(view)
    ? ""
    : `<p class="report-dataset-note" role="status">Totals include all service lines for the current selection.</p>`;
  const def = reportDefinitionUi(view);
  const hostedFirstDrillLabel = (HOSTED_REPORT_DRILL_LABELS[hostedReportDrillLevels(view)[0]] || {}).title?.toLowerCase() || "detail";
  const claimsDisclosure = view === "patientResponsibility"
    ? `<p class="muted-note">Open a ${patientResponsibilityReportState.view === "patient" ? "patient" : patientResponsibilityReportState.view === "payer" ? "primary insurance" : "service month"} to see the exact ${patientResponsibilityReportState.view === "patient" ? "claims and service lines" : "patients, claims, and service lines"}.</p>`
    : `<details class="prepared-claims-disclosure" data-prepared-claims${claimsExpanded ? " open" : ""}><summary>Underlying claims (${claimTotal.toLocaleString()})</summary><p class="muted-note">Newest Last DOS first. Undated claims appear last.</p>${claimsExpanded ? `<div class="table-wrap"><table><thead><tr><th>Claim</th><th>Client</th><th>Payer</th><th>Status</th><th>Last DOS</th><th>Bill-date age</th><th>Expected</th><th>Paid</th><th>Outstanding</th></tr></thead><tbody>${claimBody}</tbody></table></div><div class="report-pager">${model.claimPage?.page > 1 ? '<button data-prepared-report-page="prev">Previous</button>' : ""}<span class="muted-note" data-prepared-claims-caption>${escapeHtml(preparedClaimsPageCaption(model, claimTotal))}</span>${model.claimPage?.page * model.claimPage?.pageSize < claimTotal ? '<button data-prepared-report-page="next">Next</button>' : ""}</div>` : `<p class="muted-note">Expand to load the first page. Summary totals and export remain complete.</p>`}</details>`;
  const preparedExportButton = view !== "patientResponsibility" && hasPermission(PERMISSIONS.REPORT_EXPORT)
    ? `<button type="button" class="ghost-button" data-prepared-report-export>${view === "clientAction" ? "Download this worklist" : "Export complete prepared claims"}</button>`
    : "";
  return `${patientControls}${view === "ar" ? clientOutstandingHeadlineHtml(groups, totals, { complete: !model.groupPage || Number(model.groupPage.totalRows) <= groups.length, drill: HOSTED_REPORT_DRILL_VIEWS.has(view) && hasPermission(PERMISSIONS.VIEW_PHI) ? "hosted" : "none" }) : ""}<section class="panel ar-report-panel" role="region" aria-label="${escapeHtml(preparedReportTitle(view))} results" tabindex="0"><div class="ar-report-head"><div><h3>${escapeHtml(preparedReportTitle(view))}</h3>${populationLine}${clientActionPurpose}${t1aNote}${reportRateCoverageNoteHtml(model)}${performanceLens}${reviewNote}${preparedReportReloadNoticeHtml()}${model.financiallyComplete === false ? `<p role="status"><strong>Expected-dependent figures are unavailable in affected groups.</strong> Known paid, charge, and population figures remain shown.</p>` : ""}</div>${preparedExportButton}</div>${view === "expectedVsPaid" ? evpPreparedDimensionHtml(model, groups) : ""}${def ? reportCaptionHtml(def, groupSlice.total, groupSlice) : ""}${def ? reportKpiTilesHtml(def, totals) : ""}${performanceKpis}${renderReportDefinitionTable(view, model, groupSlice.rows, totals, HOSTED_REPORT_DRILL_VIEWS.has(view) && hasPermission(PERMISSIONS.VIEW_PHI) ? { columns: def?.columns || [], grainLabel: preparedReportGroupHeading(view, model.dimension), drillLabel: hostedFirstDrillLabel, drillAttr: "data-hosted-report-drill", drillValue: (group) => JSON.stringify(group.dims || []), paymentContext: (group) => ({ root: { dimensions: (model.groupBy || [model.dimension]).map((key, index) => ({ key, value: String(group.dims?.[index] ?? "") })) }, path: [] }), emptyText: view === "patientResponsibility" ? "No rows match this search and filter selection. This does not prove the current patient balance is $0." : undefined } : null) || `<div class="table-wrap"><table><thead><tr><th>${escapeHtml(preparedReportGroupHeading(view, model.dimension))}</th><th>Claims</th><th>Charges</th><th>Expected</th><th>Paid</th><th>Outstanding</th></tr></thead><tbody>${groupRows || preparedSnapshotEmptyGroupsHtml(totals)}</tbody><tfoot><tr><th>Total</th><td>${Number(model.claimCount||0).toLocaleString()}</td><td>${moneyCell(totals.charges)}</td><td>${moneyCell(totals.totalExpected)}</td><td>${moneyCell(totals.insurancePaid)}</td><td>${moneyCell(totals.remainingBalance ?? totals.outstanding)}</td></tr></tfoot></table></div>`}${reportPagerHtml(groupSlice)}${claimsDisclosure}</section>`;
}
function bindPreparedSnapshotReport() {
  const host = els.reportsBody;
  if (!host) return;
  bindHostedReportSlicers();
  bindReportPager();
  bindReportKpiPicker();
  bindPatientResponsibilityControls();
  // Expected vs Paid grouping (Payer / Client / Provider / Rate source). The
  // dimension is part of preparedSnapshotQueryKey, so the re-render re-requests.
  host.querySelectorAll("[data-evp-dim]").forEach((button) => button.addEventListener("click", () => {
    reportEvpState.dimension = button.getAttribute("data-evp-dim");
    reportEvpState.detailPage = 1;
    renderReports();
  }));
  host.querySelectorAll("[data-report-payment-role]").forEach((button) => button.addEventListener("click", () => {
    let context; try { context = JSON.parse(button.dataset.reportPaymentContext || ""); } catch (_error) { return; }
    requestReportPaymentEvidence({ role: button.dataset.reportPaymentRole, clickedAmount: button.dataset.reportPaymentAmount, context });
  }));
  host.querySelector("[data-report-payment-back]")?.addEventListener("click", () => { resetReportPaymentEvidence(); renderReports(); });
  host.querySelector("[data-report-payment-retry]")?.addEventListener("click", () => { const request = reportPaymentEvidenceState.request; if (!request) return; resetReportPaymentEvidence(); requestReportPaymentEvidence({ role: request.role, clickedAmount: request.clickedAmount, context: { root: request.root, path: request.path, selectedService: request.selectedService } }); });
  host.querySelectorAll("[data-report-payment-page]").forEach((button) => button.addEventListener("click", () => { reportPaymentEvidenceState.page = Math.max(1, reportPaymentEvidenceState.page + (button.dataset.reportPaymentPage === "next" ? 1 : -1)); renderReports(); }));
  host.querySelectorAll("[data-report-payment-claim]").forEach((button) => button.addEventListener("click", () => openDrawer(button.dataset.reportPaymentClaim, { orgId: button.dataset.reportPaymentOrg, serviceId: button.dataset.reportPaymentService })));
  host.querySelectorAll("[data-hosted-report-drill]").forEach((button) => button.addEventListener("click", () => {
    const model = preparedSnapshotReportState.response; const value = button.getAttribute("data-hosted-report-drill");
    let rootDims = []; try { rootDims = JSON.parse(value || "[]"); } catch (_error) { return; }
    if (!Array.isArray(rootDims)) return;
    const group = (model.groups || []).find((row) => JSON.stringify(row.dims || []) === JSON.stringify(rootDims)); if (!group) return;
    hostedReportDrillState.view = reportArState.view; hostedReportDrillState.sessionKey = preparedSnapshotSessionKey(); hostedReportDrillState.queryKey = preparedSnapshotQueryKey(reportArState.view); hostedReportDrillState.root = { dimensions: (model.groupBy || [model.dimension]).map((key, index) => ({ key, value: String(group.dims?.[index] ?? "") })) }; hostedReportDrillState.path = []; hostedReportDrillState.targetLevel = hostedReportDrillLevels(reportArState.view)[0]; hostedReportDrillState.response = null; requestHostedReportDrill(1);
  }));
  host.querySelector("[data-hosted-drill-home]")?.addEventListener("click", () => { resetHostedReportDrill(); renderReports(); });
  host.querySelectorAll("[data-hosted-drill-back]").forEach((button) => button.addEventListener("click", () => { const count = Number(button.dataset.hostedDrillBack) || 0; hostedReportDrillState.path = hostedReportDrillState.path.slice(0, count); hostedReportDrillState.targetLevel = hostedReportDrillLevels(hostedReportDrillState.view)[count]; hostedReportDrillState.response = null; requestHostedReportDrill(1); }));
  host.querySelectorAll("[data-hosted-drill-next]").forEach((button) => button.addEventListener("click", () => { const row = JSON.parse(button.dataset.hostedDrillNext || "{}"); hostedReportDrillState.path.push(row); hostedReportDrillState.targetLevel = hostedReportDrillLevels(hostedReportDrillState.view)[hostedReportDrillState.path.length]; hostedReportDrillState.response = null; requestHostedReportDrill(1); }));
  host.querySelectorAll("[data-hosted-drill-claim]").forEach((button) => button.addEventListener("click", () => openDrawer(button.dataset.hostedDrillClaim, { orgId: button.dataset.hostedDrillOrg, serviceId: button.dataset.hostedDrillService })));
  host.querySelector("[data-hosted-drill-retry]")?.addEventListener("click", () => requestHostedReportDrill(hostedReportDrillState.response?.page || 1));
  host.querySelectorAll("[data-hosted-drill-page]").forEach((button) => button.addEventListener("click", () => requestHostedReportDrill(Math.max(1, Number(hostedReportDrillState.response?.page || 1) + (button.dataset.hostedDrillPage === "next" ? 1 : -1)))));
  host.querySelector("[data-prepared-claims]")?.addEventListener("toggle", (event) => {
    const details = event.currentTarget;
    if (!details.open) { preparedClaimsExpandedViews.delete(reportArState.view); return; }
    preparedClaimsExpandedViews.add(reportArState.view);
    const page = preparedSnapshotReportState.response?.claimPage || {};
    // NOT WHILE A PAGE IS ALREADY ON ITS WAY. Every renderReports() recreates this
    // <details open>, and Chrome fires toggle on an element inserted open. Clicking Next
    // empties the rows (pending: true) and re-renders, so this handler saw "no rows" and
    // requested page 1 - which landed after page 2 and reset the disclosure to it. Measured
    // on the QA server with the claim page mocked: Next fired page 2, then page 1, and the
    // table stayed on page 1. The Next handler marks the page it asked for as in flight;
    // the served page (spread from the response) never carries the marker. The backend's
    // own `pending: true` on a fresh model means "not loaded yet" and must still fetch.
    if ((!Array.isArray(page.rows) || !page.rows.length) && page.inFlight !== true) requestPreparedClaimPage(reportArState.view, preparedSnapshotReportState.claimPageNumber || 1);
  });
  host.querySelector("[data-prepared-report-retry]")?.addEventListener("click", () => { preparedSnapshotReportState.status = "idle"; renderReports(); });
  host.querySelector("[data-prepared-claims-retry]")?.addEventListener("click", () => {
    const page = Math.max(1, Number(preparedSnapshotReportState.response?.claimPage?.page || preparedSnapshotReportState.claimPageNumber || 1));
    if (preparedSnapshotReportState.response) {
      preparedSnapshotReportState.response.claimPage = { ...(preparedSnapshotReportState.response.claimPage || {}), pending: true, error: "", rows: [] };
      renderReports();
    }
    requestPreparedClaimPage(reportArState.view, page);
  });
  host.querySelector("[data-prepared-performance-young]")?.addEventListener("change", (event) => {
    reportPerformanceState.includeYoungClaims = event.target.checked === true;
    preparedSnapshotReportState.status = "idle";
    renderReports();
  });
  host.querySelectorAll("[data-prepared-report-page]").forEach((button) => button.addEventListener("click", () => {
    if (!preparedSnapshotReportState.response || preparedSnapshotReportState.response.claimPage?.inFlight === true) return;
    const nextPage = Math.max(1, (preparedSnapshotReportState.response?.claimPage?.page || preparedSnapshotReportState.claimPageNumber || 1) + (button.dataset.preparedReportPage === "next" ? 1 : -1));
    if (preparedSnapshotReportState.response) {
      preparedSnapshotReportState.response.claimPage = { ...(preparedSnapshotReportState.response.claimPage || {}), pending: true, inFlight: true, rows: [] };
      preparedSnapshotReportState.claimPageNumber = nextPage;
      renderReports();
      requestPreparedClaimPage(reportArState.view, nextPage);
      return;
    }

  }));
  host.querySelector("[data-prepared-report-export]")?.addEventListener("click", () => requestPreparedReportCsv(reportArState.view));
  restorePreparedReportExportJobs();
}

// Durable report CSV job controller. Its state is session-only and independent
// of the viewed report generation, so navigation never cancels accepted work.
const preparedReportExportJobs = new Map();
const preparedReportExportRequests = new Set();
let preparedReportExportScope = "", preparedReportExportRestoredAt = 0, preparedReportExportPollTimer = null;
// THE CAPABILITY LATCH. null = not asked yet, true/false = the API has said.
// It is deliberately NOT cleared by preparedReportExportResetScope: whether
// this deployment has a durable export job service is a property of the API,
// not of the signed-in principal, so switching users must not make the page
// start asking again. Before 2026-09-22 nothing latched at all and every
// Reports open spent a 503 on /api/report-export-jobs in production.
let preparedReportExportJobsAvailable = null;
function preparedReportExportAccess() { return hasPermission(PERMISSIONS.REPORT_EXPORT) || hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES) || hasPermission(PERMISSIONS.MANAGE_USERS) ? reportReadPrincipalKey() : ""; }
function preparedReportExportResetScope() {
  const scope = preparedReportExportAccess();
  if (scope !== preparedReportExportScope) { preparedReportExportJobs.clear(); preparedReportExportRequests.clear(); preparedReportExportRestoredAt = 0; preparedReportExportScope = scope; }
  return scope;
}
function renderPreparedReportExportStatus() {
  const scope = preparedReportExportResetScope();
  let host = document.getElementById("preparedReportExportStatus");
  if (!host) {
    host = document.createElement("span"); host.id = "preparedReportExportStatus"; host.className = "tracker-export-job-chip";
    host.setAttribute("role", "status"); host.setAttribute("aria-live", "polite");
    const parent = document.getElementById("trackerExportJobChip")?.parentElement;
    (parent || document.body).appendChild(host);
  }
  const peopleModal=document.getElementById("userMgmtModal");
  const peopleParent=peopleModal && !peopleModal.classList.contains("hidden") && ([...preparedReportExportJobs.values()].some(job=>job.report==="peopleDirectory") || [...preparedReportExportRequests].some(key=>JSON.parse(key).report==="peopleDirectory")) ? document.getElementById("userMgmtExportBtn")?.parentElement : null;
  const exportParent=peopleParent || document.getElementById("trackerExportJobChip")?.parentElement;
  if(exportParent && host.parentElement!==exportParent)exportParent.appendChild(host);
  host.replaceChildren();
  // Status belongs to requested work, never to an unavailable background capability.
  const recent = [...preparedReportExportJobs.values()].slice(-5);
  host.hidden = !scope || (!preparedReportExportRequests.size && !recent.length);
  host.classList.toggle("hidden", host.hidden);
  if (host.hidden) return;
  if (preparedReportExportRequests.size) {
    const label = document.createElement("span");
    label.textContent = "Preparing your download…";
    host.appendChild(label);
  }
  for (const job of recent) {
    const item = document.createElement("span"), label = document.createElement("span");
    const title = job.report === "feeScheduleRates" ? "Fee schedules" : job.report ? preparedReportTitle(job.report) : "Report export";
    const status = job.status === "completed" ? (job.downloaded ? "Downloaded." : "Download ready.") : job.status === "failed" ? "We couldn’t create your download. Please try again." : "Your export is being prepared. You can keep working.";
    label.textContent = title + ": " + status + (job.rowCount != null && job.status === "completed" ? " · " + Number(job.rowCount).toLocaleString() + " rows" : ""); item.appendChild(label);
    if (job.status === "completed") { const button = document.createElement("button"); button.type = "button"; button.className = "tracker-export-job-action"; button.textContent = "Download"; button.addEventListener("click", () => downloadPreparedReportCsv(job)); item.append(" ", button); }
    if (["completed","failed"].includes(job.status)) { const dismiss = document.createElement("button"); dismiss.type = "button"; dismiss.className = "tracker-export-job-dismiss"; dismiss.textContent = "×"; dismiss.setAttribute("aria-label", "Dismiss " + title + " export status"); dismiss.addEventListener("click", () => { preparedReportExportJobs.delete(job.id); renderPreparedReportExportStatus(); }); item.append(" ", dismiss); }
    host.appendChild(item);
  }
}
async function downloadPreparedReportCsv(job) {
  const scope = preparedReportExportResetScope(); if (!scope || job.scope !== scope || job.downloading) return;
  job.downloading = true;
  try {
    const response = await fetch(trackerApiBase() + "/api/report-export-jobs/" + encodeURIComponent(job.id) + "/download", {cache:"no-store",headers:reportSessionHeaders()});
    if (!response.ok) { const error = await response.json().catch(() => ({})); throw new Error(error.message || "The report download is unavailable."); }
    const blob = await response.blob();
    if (preparedReportExportAccess() !== scope) return;
    if (response.headers.get("x-tms-data-generation") && response.headers.get("x-tms-data-generation") !== job.generationId) throw new Error("The export version did not match the requested report.");
    downloadBlobPayload(blob, job.filename || "report-complete.csv"); job.downloaded = true;
  } catch (error) { if (preparedReportExportAccess() === scope) showToast("We couldn’t create your download. Please try again."); }
  finally { job.downloading = false; renderPreparedReportExportStatus(); }
}
function schedulePreparedReportExportPoll() {
  clearTimeout(preparedReportExportPollTimer);
  if (preparedReportExportJobsAvailable === false) return;
  if (![...preparedReportExportJobs.values()].some(job => ["queued","running"].includes(job.status))) return;
  preparedReportExportPollTimer = setTimeout(pollPreparedReportExports, 3000);
}
async function pollPreparedReportExports() {
  const scope = preparedReportExportResetScope(); if (!scope) { renderPreparedReportExportStatus(); return; }
  for (const job of [...preparedReportExportJobs.values()]) {
    if (!["queued","running"].includes(job.status)) continue;
    try {
      const response = await fetch(trackerApiBase() + "/api/report-export-jobs/" + encodeURIComponent(job.id), {cache:"no-store",headers:reportSessionHeaders()});
      const body = await response.json().catch(() => ({}));
      if (preparedReportExportAccess() !== scope) break;
      if (!response.ok) { if ([401,403,404].includes(response.status)) preparedReportExportJobs.delete(job.id); continue; }
      Object.assign(job, body, {id:job.id,scope});
      if (job.status === "completed") await downloadPreparedReportCsv(job);
      if (job.status === "failed") showToast("We couldn’t create your download. Please try again.");
    } catch (_) { /* Durable accepted jobs remain available after network recovery. */ }
  }
  renderPreparedReportExportStatus(); schedulePreparedReportExportPoll();
}
async function restorePreparedReportExportJobs(force = false) {
  const scope = preparedReportExportResetScope(); renderPreparedReportExportStatus();
  // STOP CALLING IT. Once the API has answered available:false the answer
  // cannot change without a redeploy, so re-asking on every Reports open is
  // exactly what put a failed request in the console on every visit.
  if (preparedReportExportJobsAvailable === false) return;
  if (!scope || !force && Date.now() - preparedReportExportRestoredAt < 30000) return;
  preparedReportExportRestoredAt = Date.now();
  try {
    const response = await fetch(trackerApiBase() + "/api/report-export-jobs", {cache:"no-store",headers:reportSessionHeaders()});
    const body = await response.json().catch(() => ({}));
    if (preparedReportExportAccess() !== scope) return;
    // A 200 that says the capability is off is an ANSWER, not a failure, and
    // this is the only place the page learns it — so it latches here.
    //
    // report_export_unavailable is the SAME FACT in the un-upgraded API's only
    // word for it: the API returns that code when the service is absent and in
    // no other case, so an API that has not shipped the server half yet also
    // stops the page re-asking. It does NOT make the failed request vanish
    // from the console — only the API can do that — but it caps the cost at
    // one, instead of one per Reports open. Any OTHER failure stays a failure:
    // a transient 503 must not permanently disable the controls.
    if (body.available === false || body.error === "report_export_unavailable") {
      preparedReportExportJobsAvailable = false;
      clearTimeout(preparedReportExportPollTimer);
      renderPreparedReportExportStatus();
      return;
    }
    if (!response.ok) return;
    preparedReportExportJobsAvailable = true;
    for (const row of (Array.isArray(body) ? body : body.jobs || []).slice(0,50).reverse()) {
      const id = row.id || row.jobId; if (!id || preparedReportExportJobs.has(id)) continue;
      preparedReportExportJobs.set(id, {...row,id,scope,downloaded:row.status === "completed"});
    }
    renderPreparedReportExportStatus(); schedulePreparedReportExportPoll();
  } catch (_) { /* Reopen Reports to recover durable jobs when the connection returns. */ }
}
async function requestPreparedReportCsv(report) {
  if (!hasPermission(PERMISSIONS.REPORT_EXPORT)) { preparedReportExportResetScope(); return; }
  const model = preparedSnapshotReportState.response, scope = preparedReportExportResetScope();
  if (!model || !scope) return;
  const filters = preparedSnapshotFilters(report), key = JSON.stringify({report,filters,generation:model.generation,scope});
  if (preparedReportExportRequests.has(key)) return;
  preparedReportExportRequests.add(key); renderPreparedReportExportStatus();
  try {
    const specialized = report === "rateIssues" || report === "payerClass";
    const response = await reportSessionFetch(trackerApiBase() + (specialized ? "/api/reports/prepared" : "/api/reports/claims"), {method:"POST",cache:"no-store",headers:{...reportSessionHeaders(),"content-type":"application/json"},body:JSON.stringify({report,filters,expectedGeneration:model.generation,...(specialized?{operation:"export",expectedEpoch:model.epoch||""}:{exportAll:true})})});
    const body = await response.json().catch(() => ({}));
    if (preparedReportExportAccess() !== scope) return;
    if (response.status === 413 && body.error === "prepared_report_export_too_large") {
      showToast("This export exceeds 5,000 claims. Narrow the client, payer, or date filters and export again.");
      return;
    }
    if (response.status === 200) {
      const returnedGeneration = body.generationId || body.generation || "";
      if (model.generation && returnedGeneration && model.generation !== returnedGeneration) throw new Error("The export version did not match the requested report.");
      const rowCount = downloadCompleteInlineCsv(body, { filename: `tms-${report}-${localDateString()}.csv` });
      if (rowCount) showToast(`Exported ${formatCount(rowCount)} complete report row${rowCount === 1 ? "" : "s"}.`);
      return;
    }
    if (response.status !== 202 || !(body.id || body.jobId)) throw new Error(body.message || "The complete export could not be queued.");
    const id = body.id || body.jobId, previous = preparedReportExportJobs.get(id);
    const job = {...previous,...body,id,scope,report}; preparedReportExportJobs.set(id,job);
    while (preparedReportExportJobs.size > 50) preparedReportExportJobs.delete(preparedReportExportJobs.keys().next().value);
    renderPreparedReportExportStatus();
    if (job.status === "completed") await downloadPreparedReportCsv(job);
    else { showToast("Your export is being prepared. You can keep working."); schedulePreparedReportExportPoll(); }
  } catch (error) { if (preparedReportExportAccess() === scope) { showToast("We couldn’t create your download. Please try again."); restorePreparedReportExportJobs(true); } }
  finally { preparedReportExportRequests.delete(key); renderPreparedReportExportStatus(); }
}
// End durable report CSV job controller.

function reportRollupGroupKey(groupBy) {
  return (Array.isArray(groupBy) ? groupBy : []).join(",");
}

// Same identity as the dataset key: base + principal + every server-side
// filter. A rollup answers a QUESTION, and the question changes with the
// filters, so a stale key must never satisfy a new one.
function reportRollupCacheKey() {
  const filters = reportFilterValues();
  return JSON.stringify({
    base: trackerApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: reportAccessCacheEnvelope(),
    fromDos: filters.fromDate || "",
    toDos: filters.toDate || "",
    filters: reportDatasetServerFilters()
  });
}

// Returns the completed rollup for this grouping, or null \u2014 and kicks off the
// request when there isn't one in flight. Null means "render the fallback and
// label it", never "render zeros".
function reportRollupFor(groupBy) {
  if (!trackerApiEnabled()) return null;
  const cacheKey = reportRollupCacheKey();
  if (reportRollupState.key !== cacheKey) reportRollupState = { key: cacheKey, byGroup: {} };
  const groupKey = reportRollupGroupKey(groupBy);
  const entry = reportRollupState.byGroup[groupKey];
  if (entry && entry.status === "ready") return entry.rollup;
  if (entry && entry.status === "pending") {
    if (Date.now() - entry.pendingSince > REPORT_ROLLUP_TIMEOUT_MS) {
      entry.status = "error";
      entry.error = "Report totals did not finish loading in time. Click Retry to try again.";
    }
    return null;
  }
  if (entry && entry.status === "error") return null;
  requestReportRollup(cacheKey, groupBy);
  return null;
}

function reportRollupPending(groupBy) {
  const entry = reportRollupState.byGroup[reportRollupGroupKey(groupBy)];
  return Boolean(entry && entry.status === "pending");
}

// "Failed to fetch" IS A BROWSER TypeError, NOT A SENTENCE FOR A BILLER.
//
// The rollup's raw error text was rendered straight into the panel, so a dropped
// request read as `AR Aging unavailable / Failed to fetch / Retry`. That names no
// cause a person can act on and, worse, reads like a claim about the data rather
// than about the connection. The raw text still reaches the console and the audit
// log; the screen gets the plain-English version.
function reportLoadFailureReason(raw) {
  const text = String(raw || "");
  if (/Failed to fetch|NetworkError|network request failed/i.test(text)) return "The reporting service did not answer.";
  if (/\b(408|timeout|timed out)\b/i.test(text)) return "It took too long to add up the whole book.";
  if (/\b(401|403|sign|permission)\b/i.test(text)) return "Your session may have expired — sign in again.";
  if (/\b5\d\d\b|Internal Server Error|Bad Gateway|Service Unavailable/i.test(text)) return "The reporting service hit an error on its side.";
  return text ? `The reporting service said: ${text}` : "The reporting service did not say why.";
}

function renderReportRollupGate(groupBy, reportTitle) {
  const groupKey = reportRollupGroupKey(groupBy);
  const entry = reportRollupState.byGroup[groupKey];
  if (entry?.status === "error") {
    return `<section class="panel ar-report-panel">
      <div class="ar-report-head">
        <div><h3>${escapeHtml(reportTitle)}</h3><p role="alert">${escapeHtml(reportTitle)} could not load. ${escapeHtml(reportLoadFailureReason(entry.error))} Nothing was lost — try again.</p></div>
        <div class="ar-report-actions reports-no-print"><button type="button" class="primary-button" data-report-rollup-retry="${escapeHtml(groupKey)}">Retry</button></div>
      </div>
    </section>`;
  }
  return `<section class="panel ar-report-panel">
    <div class="ar-report-head"><div><h3>${escapeHtml(reportTitle)}</h3><p role="status">Adding up every service line in the book. This usually takes about 20 seconds.</p></div></div>
  </section>`;
}

function bindReportRollupRetry(groupBy) {
  const host = els.reportsBody;
  const groupKey = reportRollupGroupKey(groupBy);
  host?.querySelector(`[data-report-rollup-retry="${groupKey}"]`)?.addEventListener("click", () => {
    delete reportRollupState.byGroup[groupKey];
    renderReports();
  });
}

function requestReportRollup(cacheKey, groupBy) {
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") return;
  const groupKey = reportRollupGroupKey(groupBy);
  const requestId = ++reportRollupRequestSequence;
  reportRollupState.byGroup[groupKey] = { status: "pending", pendingSince: Date.now(), rollup: null, error: null, requestId };
  const filters = reportFilterValues();
  const body = { groupBy: Array.isArray(groupBy) ? groupBy : [], filters: { ...reportDatasetServerFilters() } };
  if (filters.fromDate) body.filters.fromDos = filters.fromDate;
  if (filters.toDate) body.filters.toDos = filters.toDate;
  const token = trackerApiToken();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => { try { controller.abort(); } catch (_) {} }, REPORT_ROLLUP_TIMEOUT_MS);
  const stillCurrent = () => (
    reportRollupState.key === cacheKey
    && reportRollupCacheKey() === cacheKey
    && reportRollupState.byGroup[groupKey]?.requestId === requestId
  );
  reportSessionFetch(`${base}/api/reports/rollup`, {
    method: "POST",
    cache: "no-store",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
    signal: controller.signal
  })
    .then(async (response) => {
      window.clearTimeout(timeoutId);
      const payload = await response.json().catch(() => ({}));
      if (!stillCurrent()) return;
      if (!response.ok || payload.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        reportRollupState.byGroup[groupKey] = { status: "error", pendingSince: 0, rollup: null, error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (payload?.message || `HTTP ${response.status}`), requestId };
      } else if (payload.complete !== true) {
        reportRollupState.byGroup[groupKey] = {
          status: "error",
          pendingSince: 0,
          rollup: null,
          error: payload.incompleteReason || "the server could not certify the full filtered population",
          requestId
        };
      } else {
        reportRollupState.byGroup[groupKey] = { status: "ready", pendingSince: 0, rollup: payload, error: null, requestId };
      }
      if (state.view === "reports") renderReports();
    })
    .catch((error) => {
      window.clearTimeout(timeoutId);
      if (!stillCurrent()) return;
      reportRollupState.byGroup[groupKey] = { status: "error", pendingSince: 0, rollup: null, error: error && error.message ? error.message : "rollup failed", requestId };
      if (state.view === "reports") renderReports();
    });
}

// Rollforward + Monthly Summary read ONE model. These field names are the
// contract rollforwardBuildRows already emits; the renderers are unchanged.
function rollupMoneyValue(value) {
  return value === null || value === undefined || !Number.isFinite(Number(value)) ? UNKNOWN_MONEY : Number(value);
}

function rollforwardModelFromRollup(rollup) {
  const row = (g) => ({
    month: g.dosMonth,
    units: g.units,
    totalExpected: rollupMoneyValue(g.totalExpected),
    finalInsuranceExpected: rollupMoneyValue(g.finalInsuranceExpected),
    primaryApplied: rollupMoneyValue(g.primaryApplied),
    secondaryApplied: rollupMoneyValue(g.secondaryApplied),
    unattributedApplied: rollupMoneyValue(g.unattributedApplied),
    insurancePaid: rollupMoneyValue(g.insurancePaid),
    patientResp: rollupMoneyValue(g.patientResp),
    writeOff: rollupMoneyValue(g.writeOff),
    outstanding: rollupMoneyValue(g.outstanding)
  });
  // (blank) is the server's label for a line whose DOS does not parse. The
  // client-side builder DROPPED those lines outright (rollforwardLineRows
  // returns early on a blank month), which quietly removed their money from
  // the footer. Keeping the bucket is the honest form.
  const list = rollup.groups.map(row).sort((a, b) => String(a.month).localeCompare(String(b.month)));
  const t = rollup.totals || {};
  return {
    list,
    totals: {
      units: t.units ?? 0,
      totalExpected: rollupMoneyValue(t.totalExpected),
      finalInsuranceExpected: rollupMoneyValue(t.finalInsuranceExpected),
      primaryApplied: rollupMoneyValue(t.primaryApplied),
      secondaryApplied: rollupMoneyValue(t.secondaryApplied),
      unattributedApplied: rollupMoneyValue(t.unattributedApplied),
      insurancePaid: rollupMoneyValue(t.insurancePaid),
      patientResp: rollupMoneyValue(t.patientResp),
      writeOff: rollupMoneyValue(t.writeOff),
      outstanding: rollupMoneyValue(t.outstanding)
    }
  };
}

// Same sort cptPayerBuildRows applies: paid desc, then units desc.
function cptPayerModelFromRollup(rollup) {
  const list = rollup.groups.map((g) => ({
    procedure: g.procedure,
    payer: g.payer,
    units: g.units,
    primaryExpected: rollupMoneyValue(g.primaryExpected),
    insurancePaid: rollupMoneyValue(g.insurancePaid)
  })).sort((a, b) => {
    const aPaid = isUnknownMoney(a.insurancePaid) ? Number.NEGATIVE_INFINITY : Number(a.insurancePaid);
    const bPaid = isUnknownMoney(b.insurancePaid) ? Number.NEGATIVE_INFINITY : Number(b.insurancePaid);
    return (bPaid - aPaid) || (b.units - a.units);
  });
  const t = rollup.totals || {};
  return { list, totals: { units: t.units ?? 0, primaryExpected: rollupMoneyValue(t.primaryExpected), insurancePaid: rollupMoneyValue(t.insurancePaid) } };
}

// THE PROVENANCE LINE. Every view driven by this lane says which population
// its totals cover, in all three states, because "complete" and "partial" are
// not cosmetic here \u2014 they are the difference between $70.1M collected and
// $10.9M collected on the same screen (measured 2026-08-10, full book).
function reportRateCoverageNoteHtml(model = {}) {
  const coverage = model?.rateCoverage;
  const count = coverage?.missingPrimaryRateRows;
  const known = coverage?.known === true && typeof count === "number" && Number.isSafeInteger(count) && count >= 0;
  if (!known) return '<p class="report-dataset-note report-partial-note" role="status">Primary-rate coverage is not available for this report.</p>';
  if (count === 0) return "";
  return `<p class="report-dataset-note report-partial-note" role="status"><strong>${escapeHtml(formatNounCount(count, "service line"))} ${count === 1 ? "is" : "are"} missing a primary rate.</strong> Expected reimbursement and balances may change when rates are added.</p>`;
}

function reportRollupNoteHtml(rollup, groupBy) {
  if (rollup && rollup.complete) {
    const lines = Number(rollup.population && rollup.population.linesScanned) || 0;
    const expectedUnknown = Number(rollup.population?.expectedUnknownRows || 0);
    const unreadable = Number(rollup.population?.unreadableRows || 0);
    const financialNote = rollup.financiallyComplete === false
      ? ` <strong>Affected financial figures are unavailable.</strong>${expectedUnknown ? ` ${escapeHtml(formatNounCount(expectedUnknown, "line"))} have unknown expected reimbursement.` : ""}${unreadable ? ` ${escapeHtml(formatNounCount(unreadable, "line"))} contain unreadable financial inputs.` : ""} Known measures and population figures remain visible.`
      : "";
    return `<p class="report-dataset-note${rollup.financiallyComplete === false ? " report-partial-note" : ""} reports-no-print" role="status">Totals computed server-side over the full ${escapeHtml(formatNounCount(lines, "service line"))} in the current filters.${financialNote}</p>${reportRateCoverageNoteHtml(rollup)}`;
  }
  if (rollup && !rollup.complete) {
    return `<p class="report-dataset-note report-partial-note reports-no-print" role="status">These totals are <strong>incomplete</strong>: ${escapeHtml(String(rollup.incompleteReason || "the server could not fold the whole population"))}. Narrow the date range and retry.</p>`;
  }
  if (reportRollupPending(groupBy)) {
    return `<p class="report-dataset-note reports-no-print" role="status">Computing full-book totals\u2026 the figures below are from the capped sample until it lands.</p>${reportPartialTotalsNoticeHtml()}`;
  }
  return reportPartialTotalsNoticeHtml();
}

function reportDatasetCapNoteHtml(context = "reports") {
  if (!trackerApiEnabled() || reportDatasetState.status !== "ready" || !reportDatasetState.capped) return "";
  const shown = Number(reportDatasetState.rowCount || (reportDatasetState.rows || []).length) || 0;
  const cap = Number(reportDatasetState.cap) || shown;
  // WHAT ACTUALLY SURVIVES THE CAP (corrected 2026-08-10). This sentence used to
  // say "the most recent N rows", which describes an ORDER BY the query does not
  // have. The server sorts OWED-FIRST, then most recent (reportDatasetSource's
  // cappedOrderBy), precisely so open balances outlive the cut — a reader who
  // believed the old wording would have concluded that older open AR had been
  // dropped when it is the very first thing kept, and gone looking for money that
  // was on the screen. Note what this does NOT claim: if open rows alone exceed
  // the cap then open rows are cut too, so the promise is a PRIORITY, not a
  // guarantee of completeness.
  return `<p class="report-dataset-note reports-no-print">Showing ${formatNounCount(shown, "row")} of a larger set (server cap ${formatNounCount(cap, "row")}) — rows with an open balance are kept first, then the most recent. Narrow the date range for complete totals.</p>`;
}

// Live boards (LIVE_BOARDS_SPEC.md): in tracker-API mode sourceClaims stays
// empty (server-side paging), so Assignments / Reviews / the Home throughput
// card read ONE principal-scoped workflow slice from the tracker lane instead
// of getClaims(). Two bounded queries: (1) claim-mode rows whose DISPLAYED
// assignment status is an active workflow status (routes through the server's
// workflow-aware predicates over portal.claim_workflow_state); (2) the intake
// rail's unassigned, balance > 0 top slice. Scope stays 100% server-side -
// offshore's org-scope predicate runs in SQL; the client never widens or
// narrows visibility. The cache key folds in base/role/user so a login switch
// refetches instead of leaking the previous principal's rows.
const WORKFLOW_BOARD_TIMEOUT_MS = 12000;
const WORKFLOW_BOARD_STATUSES = ["Assigned", "In Progress", "Submitted For Review", "Bounced Back", "Approved"];
const WORKFLOW_BOARD_PAGE_SIZE = 50;
let workflowBoardPages = { team: 1, personal: 1, intake: 1 };
let workflowBoardController = null;
const WORKFLOW_BOARD_INTAKE_SIZE = 50;
const ASSIGNMENT_SCOPE_NO_MATCH = "__NO_MATCHING_WORKFLOW_IDENTITY__";
let assignmentScopeUiState = { mode: "", orgId: "", memberUserId: "", memberIdentities: [], status: "idle", scope: null, error: "", requestKey: "", pendingSince: 0 };
let assignmentScopeRequestSequence = 0;
let workflowBoardState = { status: "idle", key: null, claims: null, personalClaims: null, intake: null, totalRows: 0, intakeTotalRows: 0, teamScope: null, teamActivity: null, error: null, fetchedAt: null, pendingSince: 0 };

function assignmentScopeDefaultMode() {
  const modes = assignmentScopeAllowedModes();
  return modes.includes("my") ? "my" : modes[0];
}

function assignmentWorkflowScopeRequestKey(orgId = assignmentScopeUiState.orgId) {
  return JSON.stringify({
    readScope: additionalPortalDataReadScope(),
    base: teamStructureApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    orgId,
    access: reportAccessCacheEnvelope()
  });
}

function ensureAssignmentScopeSelection() {
  const options = teamStructureOrgOptions();
  const allowedModes = assignmentScopeAllowedModes();
  if (!allowedModes.includes(assignmentScopeUiState.mode)) assignmentScopeUiState.mode = assignmentScopeDefaultMode();
  const nextOrgId = options.some((option) => option.orgId === assignmentScopeUiState.orgId)
    ? assignmentScopeUiState.orgId
    : (options[0]?.orgId || "");
  if (nextOrgId !== assignmentScopeUiState.orgId) {
    assignmentScopeUiState.orgId = nextOrgId;
    resetAssignmentWorkflowScope({ preserveSelection: true });
    resetWorkflowBoard();
  }
  const requestKey = assignmentWorkflowScopeRequestKey(nextOrgId);
  if (assignmentScopeUiState.requestKey && assignmentScopeUiState.requestKey !== requestKey) {
    resetAssignmentWorkflowScope({ preserveSelection: true });
    resetWorkflowBoard();
  }
  return options;
}

function resetAssignmentWorkflowScope({ preserveSelection = true } = {}) {
  assignmentScopeRequestSequence += 1;
  const mode = preserveSelection ? assignmentScopeUiState.mode : "";
  const orgId = preserveSelection ? assignmentScopeUiState.orgId : "";
  const memberUserId = preserveSelection ? assignmentScopeUiState.memberUserId : "";
  const memberIdentities = preserveSelection ? [...(assignmentScopeUiState.memberIdentities || [])] : [];
  assignmentScopeUiState = { mode, orgId, memberUserId, memberIdentities, status: "idle", scope: null, error: "", requestKey: "", pendingSince: 0 };
}

function requestAssignmentWorkflowScope() {
  const base = teamStructureApiBase();
  const orgId = assignmentScopeUiState.orgId;
  if (!base || !orgId || typeof fetch !== "function") return;
  const requestKey = assignmentWorkflowScopeRequestKey(orgId);
  const sequence = ++assignmentScopeRequestSequence;
  const current = () => sequence === assignmentScopeRequestSequence && assignmentScopeUiState.requestKey === requestKey && assignmentWorkflowScopeRequestKey() === requestKey;
  assignmentScopeUiState = { ...assignmentScopeUiState, status: "pending", scope: null, error: "", requestKey, pendingSince: Date.now() };
  const controller = new AbortController();
  const timeoutMessage = "Reporting-line scope did not finish loading in time. Retry.";
  const timeoutId = window.setTimeout(() => {
    if (!current()) return;
    assignmentScopeUiState = { ...assignmentScopeUiState, status: "error", scope: null, error: timeoutMessage, pendingSince: 0 };
    try { controller.abort(); } catch (_) {}
    rerenderWorkflowBoardViews();
  }, WORKFLOW_BOARD_TIMEOUT_MS);
  const token = trackerApiToken();
  fetch(additionalPortalDataReadUrl(`${base}/api/workflow/team-scope?orgId=${encodeURIComponent(orgId)}`), {
    method: "GET",
    cache: "no-store",
    headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) },
    signal: controller.signal
  }).then(async (response) => {
    if (!current()) return;
    const payload = await response.json().catch(() => ({}));
    if (!current()) return;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      const expired = new Error("Your session expired. Sign in again — this page will still be here.");
      expired.sessionExpired = true;
      throw expired;
    }
    if (!response.ok || payload.error) throw new Error(payload?.message || payload?.error?.message || `HTTP ${response.status}`);
    return payload;
  }).then((scope) => {
    window.clearTimeout(timeoutId);
    if (!current()) return;
    assignmentScopeUiState = { ...assignmentScopeUiState, status: "ready", scope, error: "", pendingSince: 0 };
    resetWorkflowBoard();
    rerenderWorkflowBoardViews();
  }).catch((error) => {
    window.clearTimeout(timeoutId);
    if (!current()) return;
    assignmentScopeUiState = {
      ...assignmentScopeUiState,
      status: "error",
      scope: null,
      error: error?.sessionExpired
        ? "Your session expired. Sign in again — this page will still be here."
        : (error?.name === "AbortError" ? timeoutMessage : (error?.message || String(error))),
      pendingSince: 0
    };
    rerenderWorkflowBoardViews();
  });
}

function assignmentWorkflowScopeDataset() {
  const options = ensureAssignmentScopeSelection();
  if (!options.length || !assignmentScopeUiState.orgId) {
    return { status: "error", error: "No company is available in your authenticated access scope." };
  }
  const requestKey = assignmentWorkflowScopeRequestKey();
  if (assignmentScopeUiState.status === "idle" || assignmentScopeUiState.requestKey !== requestKey) requestAssignmentWorkflowScope();
  return assignmentScopeUiState;
}

function workflowBoardKey(scopeState = assignmentScopeUiState) {
  return JSON.stringify({
    readScope: additionalPortalDataReadScope(),
    base: trackerApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: reportAccessCacheEnvelope(),
    pageFilter: assignmentQueueFilter,
    pages: workflowBoardPages,
    assignmentScope: {
      mode: scopeState.mode || "",
      orgId: scopeState.orgId || "",
      memberUserId: scopeState.memberUserId || "",
      memberIdentities: [...(scopeState.memberIdentities || [])].sort(),
      requestKey: scopeState.requestKey || "",
      version: Number(scopeState.scope?.version || 0)
    }
  });
}

function resetWorkflowBoard() {
  workflowBoardController?.abort();
  workflowBoardPages = { team: 1, personal: 1, intake: 1 };
  workflowBoardState = { status: "idle", key: null, claims: null, personalClaims: null, intake: null, totalRows: 0, intakeTotalRows: 0, teamScope: null, teamActivity: null, error: null, fetchedAt: null, pendingSince: 0 };
}

function workflowBoardQueryRequest(scopeState = assignmentScopeUiState) {
  const access = trackerAccessEnvelope("claim");
  // Boards need the workflow overlay fields (due / instruction / bounce /
  // note) even when the saved column view hides them; unauthorized extras
  // are denied-and-redacted server-side, never an error.
  const columns = Array.from(new Set([
    ...access.allowedColumns.filter((column) => !fastGridExcludedApiColumns.has(column)),
    "claimId",
    "orgId",
    "client",
    "assignedTo",
    "assignedBy",
    "assignmentStatus",
    "assignmentDue",
    "assignmentInstruction",
    "bounceReason",
    "reviewNote",
    "owed"
  ]));
  const columnFilters = {
    assignmentStatus: [...WORKFLOW_BOARD_STATUSES],
    orgId: [scopeState.orgId]
  };
  const scope = scopeState.scope || {};
  if (scopeState.mode === "assignedBy") {
    columnFilters.assignedBy = Array.isArray(scope.actorIdentities) && scope.actorIdentities.length
      ? [...scope.actorIdentities]
      : [ASSIGNMENT_SCOPE_NO_MATCH];
  } else if (scopeState.mode === "team") {
    if (scope.configured) {
      const memberIdentities = scopeState.memberUserId ? scopeState.memberIdentities : scope.descendantIdentities;
      columnFilters.assignedTo = Array.isArray(memberIdentities) && memberIdentities.length
        ? [...memberIdentities]
        : [ASSIGNMENT_SCOPE_NO_MATCH];
    } else if (scopeState.memberUserId) {
      columnFilters.assignedTo = Array.isArray(scopeState.memberIdentities) && scopeState.memberIdentities.length
        ? [...scopeState.memberIdentities]
        : [ASSIGNMENT_SCOPE_NO_MATCH];
    }
  } else {
    columnFilters.assignedTo = Array.isArray(scope.actorIdentities) && scope.actorIdentities.length
      ? [...scope.actorIdentities]
      : [ASSIGNMENT_SCOPE_NO_MATCH];
  }
  return {
    resource: "claims",
    mode: "claim",
    page: { number: 1, size: WORKFLOW_BOARD_PAGE_SIZE },
    sort: [
      { field: "workPriority", direction: "desc" },
      { field: "pastDue", direction: "asc" },
      { field: "aging", direction: "desc" },
      { field: "owed", direction: "desc" }
    ],
    filters: {},
    slicers: {},
    columnFilters,
    access,
    responseShape: {
      columns,
      includeFinancials: access.includeFinancials,
      includePhi: access.includePhi,
      includeInternalNotes: false
    }
  };
}

// Intake rail: unassigned (displays Blank), open balance, top N by owed.
function workflowBoardIntakeRequest(scopeState = assignmentScopeUiState) {
  const base = workflowBoardQueryRequest(scopeState);
  return {
    ...base,
    page: { number: 1, size: WORKFLOW_BOARD_INTAKE_SIZE },
    sort: [{ field: "owed", direction: "desc" }],
    filters: { minBalance: 0.01 },
    columnFilters: { orgId: [scopeState.orgId], assignedTo: ["Blank"] }
  };
}

function requestWorkflowBoard(key = workflowBoardKey(), scopeState = assignmentScopeUiState) {
  if (window.TMS_PORTAL_PREPARED_READS !== true) return requestLegacyWorkflowBoard(key, scopeState);
  const base = trackerApiBase(); if (!base || typeof fetch !== "function") return;
  workflowBoardController?.abort(); const controller = new AbortController(); workflowBoardController = controller;
  const previous = workflowBoardState;
  const requestStillCurrent = () => workflowBoardController === controller && workflowBoardState.key === key && workflowBoardKey(assignmentScopeUiState) === key;
  workflowBoardState = { ...previous, status: "pending", key, error: null, teamScope: scopeState.scope, teamActivity: null, pendingSince: Date.now() };
  const token = trackerApiToken(), timeoutMessage = "Workflow data did not finish loading in time. Retry.";
  const timeout = setTimeout(() => { if (requestStillCurrent() && workflowBoardState.status === "pending") { workflowBoardState = { ...workflowBoardState, status: "error", error: timeoutMessage }; rerenderWorkflowBoardViews(); } controller.abort(); }, WORKFLOW_BOARD_TIMEOUT_MS);
  const post = async (request, lane, personal = false) => {
    const response = await fetch(additionalPortalDataReadUrl(base + "/api/workflow/board-summary"), { method: "POST", cache: "no-store", headers: { "content-type": "application/json", ...(token ? { authorization: "Bearer " + token } : {}) }, signal: controller.signal,
      body: JSON.stringify({ request: { ...request, page: { number: workflowBoardPages[lane] || 1, size: 50 } }, pageFilter: lane === "team" ? assignmentQueueFilter : {}, personal }) });
    const payload = await response.json().catch(() => ({}));
    if (!requestStillCurrent()) return null;
    if (response.status === 401) { if (typeof notePortalSessionExpired === "function") notePortalSessionExpired(); throw Object.assign(new Error("Your session expired. Sign in again — this page will still be here."), { sessionExpired: true }); }
    if (!response.ok || payload.error || !payload.workflowSummary?.complete) throw new Error(payload.message || payload.error?.message || "Complete assignment data could not be loaded.");
    return payload;
  };
  const includeIntake = scopeState.mode === "team" && canonicalRole(state.role) !== "OffshoreBiller" && hasPermission(PERMISSIONS.ASSIGN_CLAIMS);
  const empty = { rows: [], page: { totalRows: 0, number: 1, size: 50 }, workflowSummary: { complete: true, groups: [] } };
  const data = Promise.all([
    post(workflowBoardQueryRequest(scopeState), "team"),
    post(workflowBoardQueryRequest({ ...scopeState, mode: "my", memberUserId: "", memberIdentities: [] }), "personal", true),
    includeIntake ? post(workflowBoardIntakeRequest(scopeState), "intake") : Promise.resolve(empty)
  ]).then(([boardResult, personalResult, intakeResult]) => {
    if (!requestStillCurrent() || !boardResult || !personalResult || !intakeResult) return;
    workflowBoardState = { ...workflowBoardState, status: "ready", key,
      claims: workflowAuthoritativeClaims(boardResult.rows, boardResult.claimSummaries), personalClaims: workflowAuthoritativeClaims(personalResult.rows, personalResult.claimSummaries), intake: workflowAuthoritativeClaims(intakeResult.rows, intakeResult.claimSummaries),
      page: boardResult.page, personalPage: personalResult.page, intakePage: intakeResult.page,
      summary: boardResult.workflowSummary, personalSummary: personalResult.workflowSummary, intakeSummary: intakeResult.workflowSummary,
      totalRows: Number(boardResult.page.totalRows) || 0, intakeTotalRows: Number(intakeResult.page.totalRows) || 0,
      teamScope: scopeState.scope, error: null, fetchedAt: timestamp(), pendingSince: 0 };
    rerenderWorkflowBoardViews();
  }).catch(error => {
    if (!requestStillCurrent()) return;
    workflowBoardState = { ...workflowBoardState, status: "error", error: error.name === "AbortError" ? timeoutMessage : error.message, pendingSince: 0 };
    rerenderWorkflowBoardViews();
  });
  // Activity is an independent panel. A slow directory/history lookup must not
  // hold the useful claims, or replace an inline note after those claims render.
  const activity = scopeState.mode !== "team" ? Promise.resolve() : fetch(additionalPortalDataReadUrl(teamStructureApiBase() + "/api/workflow/team-activity?orgId=" + encodeURIComponent(scopeState.orgId)), { cache: "no-store", headers: token ? { authorization: "Bearer " + token } : {}, signal: controller.signal }).then(async response => {
    const body = await response.json().catch(() => ({})); if (!requestStillCurrent() || !response.ok || body.error) return;
    workflowBoardState.teamActivity = body;
    const host = els.offshoreQueue?.querySelector('[data-workflow-team-activity]');
    if (host) host.innerHTML = renderTeamActivityPanel(workflowBoardState);
  }).catch(() => {});
  Promise.allSettled([data, activity]).then(() => clearTimeout(timeout));
}

// Review-mode compatibility: preserve the deployed 9765c0bd claim loader and
// its existing limits. Only prepared mode promises complete board summaries.
function legacyWorkflowBoardQueryRequest(scopeState = assignmentScopeUiState) {
  return { ...workflowBoardQueryRequest(scopeState), page: { number: 1, size: 2500 } };
}
function legacyWorkflowBoardIntakeRequest(scopeState = assignmentScopeUiState) {
  return { ...workflowBoardIntakeRequest(scopeState), page: { number: 1, size: 200 } };
}
function requestLegacyWorkflowBoard(key = workflowBoardKey(), scopeState = assignmentScopeUiState) {
  const WORKFLOW_BOARD_TIMEOUT_MS = 45000;
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") return;
  const requestStillCurrent = () => workflowBoardState.key === key && workflowBoardKey(assignmentScopeUiState) === key;
  const previousClaims = workflowBoardState.claims;
  const previousPersonalClaims = workflowBoardState.personalClaims;
  const previousIntake = workflowBoardState.intake;
  const previousTotalRows = workflowBoardState.totalRows;
  const previousIntakeTotalRows = workflowBoardState.intakeTotalRows;
  const previousTeamActivity = workflowBoardState.teamActivity;
  const previousFetchedAt = workflowBoardState.fetchedAt;
  workflowBoardState = { status: "pending", key, claims: previousClaims, personalClaims: previousPersonalClaims, intake: previousIntake, totalRows: previousTotalRows, intakeTotalRows: previousIntakeTotalRows, teamScope: scopeState.scope, teamActivity: previousTeamActivity, error: null, fetchedAt: previousFetchedAt, pendingSince: Date.now() };
  const token = trackerApiToken();
  const controller = new AbortController();
  const timeoutMessage = "Workflow data did not finish loading in time. Retry.";
  const timeoutId = window.setTimeout(() => {
    if (requestStillCurrent() && workflowBoardState.status === "pending") {
      workflowBoardState = { status: "error", key, claims: previousClaims, personalClaims: previousPersonalClaims, intake: previousIntake, totalRows: previousTotalRows, intakeTotalRows: previousIntakeTotalRows, teamScope: scopeState.scope, teamActivity: previousTeamActivity, error: timeoutMessage, fetchedAt: previousFetchedAt, pendingSince: 0 };
      rerenderWorkflowBoardViews();
    }
    try { controller.abort(); } catch (_) {}
  }, WORKFLOW_BOARD_TIMEOUT_MS);
  const post = (body) => fetch(additionalPortalDataReadUrl(`${base}/api/tracker/query`), {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body),
    signal: controller.signal
  }).then(async (response) => {
    if (!requestStillCurrent()) return;
    const payload = await response.json().catch(() => ({}));
    if (!requestStillCurrent()) return;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      const expired = new Error("Your session expired. Sign in again — this page will still be here.");
      expired.sessionExpired = true;
      throw expired;
    }
    if (!response.ok || payload.error) throw new Error(payload?.error?.message || payload.message || `HTTP ${response.status}`);
    return { rows: Array.isArray(payload.rows) ? payload.rows : [], page: payload.page || {} };
  });
  const getTeamActivity = () => fetch(additionalPortalDataReadUrl(`${teamStructureApiBase()}/api/workflow/team-activity?orgId=${encodeURIComponent(scopeState.orgId)}`), {
    method: "GET",
    cache: "no-store",
    headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) },
    signal: controller.signal
  }).then(async (response) => {
    if (!requestStillCurrent()) return;
    const payload = await response.json().catch(() => ({}));
    if (!requestStillCurrent()) return;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      const expired = new Error("Your session expired. Sign in again — this page will still be here.");
      expired.sessionExpired = true;
      throw expired;
    }
    if (!response.ok || payload.error) throw new Error(payload?.message || payload?.error?.message || `HTTP ${response.status}`);
    return payload;
  });
  // Intake is manager-only chrome. Offshore issues ONE query and relies on
  // the server's tenant scope - never a client-side assignee filter.
  const includeIntake = scopeState.mode === "team" && canonicalRole(state.role) !== "OffshoreBiller" && hasPermission(PERMISSIONS.ASSIGN_CLAIMS);
  Promise.all([
    post(legacyWorkflowBoardQueryRequest(scopeState)),
    includeIntake ? post(legacyWorkflowBoardIntakeRequest(scopeState)) : Promise.resolve({ rows: [], page: { totalRows: 0 } }),
    scopeState.mode === "team" ? getTeamActivity() : Promise.resolve(null),
    scopeState.mode === "my" ? Promise.resolve(null) : post(legacyWorkflowBoardQueryRequest({ ...scopeState, mode: "my", memberUserId: "", memberIdentities: [] }))
  ])
    .then(([boardResult, intakeResult, teamActivity, personalResult]) => {
      window.clearTimeout(timeoutId);
      if (!requestStillCurrent()) return;
      workflowBoardState = {
        status: "ready",
        key,
        claims: claimsFromTrackerRows(boardResult.rows, "claim"),
        personalClaims: claimsFromTrackerRows((personalResult || boardResult).rows, "claim"),
        intake: claimsFromTrackerRows(intakeResult.rows, "claim"),
        totalRows: Math.max(0, Number(boardResult.page?.totalRows ?? boardResult.rows.length)),
        intakeTotalRows: Math.max(0, Number(intakeResult.page?.totalRows ?? intakeResult.rows.length)),
        teamScope: scopeState.scope,
        teamActivity,
        error: null,
        fetchedAt: timestamp(),
        pendingSince: 0
      };
      rerenderWorkflowBoardViews();
    })
    .catch((error) => {
      window.clearTimeout(timeoutId);
      if (!requestStillCurrent()) return;
      const sessionExpired = Boolean(error && error.sessionExpired);
      workflowBoardState = {
        status: "error",
        key,
        claims: sessionExpired ? previousClaims : null,
        personalClaims: sessionExpired ? previousPersonalClaims : null,
        intake: sessionExpired ? previousIntake : null,
        totalRows: sessionExpired ? previousTotalRows : 0,
        intakeTotalRows: sessionExpired ? previousIntakeTotalRows : 0,
        teamScope: scopeState.scope,
        teamActivity: sessionExpired ? previousTeamActivity : null,
        error: sessionExpired
          ? "Your session expired. Sign in again — this page will still be here."
          : (error && error.name === "AbortError" ? timeoutMessage : (error.message || String(error))),
        fetchedAt: sessionExpired ? previousFetchedAt : null,
        pendingSince: 0
      };
      rerenderWorkflowBoardViews();
    });
}

function workflowAuthoritativeClaims(rows, summaries = []) {
  const byClaim = new Map(summaries.map(row => [JSON.stringify([String(row.orgId), String(row.claimId)]), row]));
  return claimsFromServiceGrainRows(rows).map(claim => ({ ...claim, _workflowReadAuthoritative: true, _workflowFinancialSummary: byClaim.get(JSON.stringify([String(claim.orgId), String(claim.claimId)])) }));
}
function workflowClaimOutstanding(claim) {
  const summary = claim?._workflowFinancialSummary;
  return summary ? (summary.owed == null ? UNKNOWN_MONEY : summary.owed) : claimFinancialView(claim).sourceOutstanding;
}

function workflowBoardPagerHtml(lane, page = {}) {
  const total = Number(page.totalRows) || 0, number = Number(page.number) || 1;
  if (!total) return "";
  return '<div class="tracker-choice-pager" aria-label="Assignment pages"><span role="status">' + ((number - 1) * 50 + 1) + '–' + Math.min(number * 50, total) + ' of ' + total + ' claims</span><button type="button" class="small-button" data-workflow-page="' + lane + '" data-page="' + (number - 1) + '"' + (number <= 1 ? ' disabled' : '') + '>Previous</button><button type="button" class="small-button" data-workflow-page="' + lane + '" data-page="' + (number + 1) + '"' + (number * 50 >= total ? ' disabled' : '') + '>Next</button></div>';
}
function workflowAssignmentModels(board) {
  const groups = board.summary?.groups || [], owned = groups.filter(row => row.owner), intakeGroups = board.intakeSummary?.groups || [];
  const n = row => Number(row.count) || 0;
  const sum = (rows, key) => ["owed", "expected", "paid"].includes(key) && rows.some(row => row[key] == null || isUnknownMoney(row[key])) ? UNKNOWN_MONEY : moneyNumber(rows.reduce((value, row) => value + (Number(row[key]) || 0), 0));
  const intake = { n: sum(intakeGroups, "count"), ar: sum(intakeGroups, "owed") }, active = sum(owned, "count");
  const lanes = Object.fromEntries(["inFlight", "review", "correction", "approved", "atRisk"].map(key => [key, { n: 0, ar: 0 }]));
  owned.forEach(row => { const key = ["assigned", "inProgress"].includes(row.stage) ? "inFlight" : row.stage === "submitted" ? "review" : row.stage === "bounced" ? "correction" : row.stage === "approved" ? "approved" : ""; const add = lane => { lane.n += n(row); lane.ar = lane.ar == null || isUnknownMoney(lane.ar) || row.owed == null ? UNKNOWN_MONEY : moneyNumber(lane.ar + Number(row.owed)); }; if (key) add(lanes[key]); if (["overdue", "missingDue", "dueToday"].includes(row.due)) add(lanes.atRisk); });
  const onTrack = sum(owned.filter(row => row.due === "onTrack"), "count"), overdue = sum(owned.filter(row => row.due === "overdue"), "count");
  const overview = { lanes, intake, activeClaims: active, totalClaims: active + intake.n, arInMotion: sum([...owned, ...intakeGroups], "owed"), sla: { active, onTrack, overdue, onTimeRate: active ? Math.round(onTrack / active * 100) : null }, velocity: { clearedWeek: null, avgTurnaroundDays: null, available: false } };
  const stages = assignmentReviewPipelineStages().map(stage => { const rows = groups.filter(row => row.stage === stage.key); return { ...stage, claims: sum(rows, "count"), expected: sum(rows, "expected"), paid: sum(rows, "paid"), outstanding: sum(rows, "owed") }; });
  const pipeline = { stages, totals: { claims: sum(groups, "count"), expected: sum(groups, "expected"), paid: sum(groups, "paid"), outstanding: sum(groups, "owed") } };
  const owners = [...new Set([...users.filter(user => canonicalRole(user.role) === "OffshoreBiller").map(user => user.name), ...owned.map(row => row.owner)])].filter(Boolean);
  const rows = owners.map(owner => { const entries = owned.filter(row => row.owner === owner); const base = { owner, claims: sum(entries, "count"), owed: sum(entries, "owed"), pastDue: sum(entries, "pastDue"), submitted: sum(entries.filter(row => row.stage === "submitted"), "count"), bounced: sum(entries.filter(row => row.stage === "bounced"), "count"), inProgress: sum(entries.filter(row => row.stage === "inProgress"), "count"), missingDue: sum(entries.filter(row => row.due === "missingDue"), "count"), dueSoon: sum(entries.filter(row => ["dueToday", "dueSoon"].includes(row.due)), "count") }; const loadScore = assignmentCapacityScore({ ...base, owed: isUnknownMoney(base.owed) ? 0 : base.owed }), capacity = assignmentCapacityLabel(loadScore, base.claims); return { ...base, financialIncomplete: isUnknownMoney(base.owed), loadScore, capacity, ceiling: ASSIGNMENT_WIP_CEILING, utilization: Math.min(100, Math.round(base.claims / ASSIGNMENT_WIP_CEILING * 100)), recommendation: assignmentCapacityRecommendation({ ...base, loadScore, capacity }) }; }).sort((a,b) => a.loadScore - b.loadScore || a.claims - b.claims || a.owed - b.owed || a.owner.localeCompare(b.owner));
  const capacity = { rows, recommendedOwner: rows.find(row => row.capacity !== "At Capacity") || rows[0] || null, overloaded: [...rows].sort((a,b) => b.loadScore - a.loadScore || b.owed - a.owed)[0] || null, totals: { readyClaims: intake.n, readyAr: intake.ar, criticalReady: sum(intakeGroups, "critical"), availableOwners: rows.filter(row => row.capacity === "Available").length, atCapacityOwners: rows.filter(row => row.capacity === "At Capacity").length, reviewClaims: lanes.review.n, overdueClaims: sum(owned, "pastDue") } };
  const personalCounts = { ready: 0, due: 0, review: 0, returned: 0 };
  (board.personalSummary?.groups || []).filter(row => row.stage !== "approved").forEach(row => { personalCounts[row.stage === "submitted" ? "review" : row.stage === "bounced" ? "returned" : ["overdue", "dueToday"].includes(row.due) ? "due" : "ready"] += n(row); });
  return { overview, pipeline, capacity, personalCounts };
}


// Keep ready My Work data until a user action or scope change, never age expiration.
function workflowBoardDataset() {
  const scopeState = assignmentWorkflowScopeDataset();
  if (scopeState.status !== "ready") return { status: scopeState.status, error: scopeState.error || "", scopeError: true };
  const key = workflowBoardKey(scopeState);
  if (workflowBoardState.status === "idle" || workflowBoardState.key !== key) requestWorkflowBoard(key, scopeState);
  if (workflowBoardState.status === "pending" && workflowBoardState.key === key) {
    const pendingSince = Number(workflowBoardState.pendingSince) || 0;
    if (!pendingSince || Date.now() - pendingSince > WORKFLOW_BOARD_TIMEOUT_MS) {
      workflowBoardState = { status: "error", key, claims: null, personalClaims: null, intake: null, totalRows: 0, intakeTotalRows: 0, teamScope: scopeState.scope, teamActivity: null, error: "Workflow data did not finish loading in time. Retry.", fetchedAt: null, pendingSince: 0 };
    }
  }
  return workflowBoardState;
}

// Re-apply in-session optimistic workflow edits (assign / approve / bounce
// write state.workflowByClaim) on top of the cached board rows - the same
// merge getClaims() does in local mode.
function workflowBoardClaims(pool) {
  return (Array.isArray(pool) ? pool : []).map((claim) => ({
    ...claim,
    ...(claim._workflowReadAuthoritative ? {} : (state.workflowByClaim[claim.claimId] || {}))
  }));
}

// Board actions resolve their claims from the same cache the boards render
// (getClaim falls back here in live mode). Board claims carry orgId, so
// canAccessClaim scope checks and per-org workflow command targeting hold.
function currentWorkflowBoardClaim(claimId) {
  const normalizedId = normalizeClaimId(claimId);
  if (!normalizedId || !trackerApiEnabled() || workflowBoardState.status !== "ready") return null;
  const pools = [workflowBoardState.claims, workflowBoardState.personalClaims, workflowBoardState.intake];
  for (const pool of pools) {
    const claim = (pool || []).find((candidate) => String(candidate.claimId) === normalizedId);
    if (claim) return { ...claim, ...(claim._workflowReadAuthoritative ? {} : (state.workflowByClaim[claim.claimId] || {})) };
  }
  return null;
}

function rerenderWorkflowBoardViews() {
  if (typeof updateMyWorkNavBadge === "function") updateMyWorkNavBadge();
  if (state.view === "assignments") renderAssignments();
  else if (state.view === "reviews" || (state.view === "assignments" && ["requests", "approvals"].includes(oversightState().tab))) renderReviews();
}

// Loading / error states follow the Reports live-dataset pattern: an honest
// pending line, then a calm failure line with a primary Retry.
function workflowBoardPlaceholderHtml(title, board) {
  if (board.status === "error") {
    return `
    <section class="panel ar-report-panel">
      <div class="ar-report-head">
        <div><h3>${escapeHtml(title)}</h3>
        <p>${escapeHtml(title)} data failed to load: ${escapeHtml(board.error || "Unknown error")}</p></div>
        <div class="ar-report-actions">
          <button type="button" class="primary-button" data-workflow-board-retry>Retry</button>
        </div>
      </div>
    </section>`;
  }
  return `
    <section class="panel ar-report-panel">
      <div class="ar-report-head">
        <div><h3>${escapeHtml(title)}</h3>
        <p role="status">Loading live workflow data. This may take a few seconds.</p></div>
      </div>
    </section>`;
}

function bindWorkflowBoardRetry(host, rerender) {
  if (!host || typeof host.addEventListener !== "function" || !host.dataset) return;
  if (host.dataset.workflowBoardRetryBound === "1") return;
  host.dataset.workflowBoardRetryBound = "1";
  host.addEventListener("click", (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== "function") return;
    if (target.closest("[data-workflow-board-retry]")) {
      resetAssignmentWorkflowScope({ preserveSelection: true });
      resetWorkflowBoard();
      rerender();
    }
  });
}

const statementPreviewState = { key: "", requestId: 0, status: "idle", data: null, error: "", expanded: {}, search: "", printedFilter: "all", payer: "", payerOptions: [], _focusSearch: false, expandedOrg: {}, focusOrg: "", heldOpen: false, reconciliationOpen: false, page: 1, pageSize: 50 };
const statementSelection = { keys: new Set(), dataKey: "", scopeKey: "", refreshContext: null };
const statementActionState = { busy: false, message: "", error: false, printCommandFingerprint: "", printIdempotencyKey: "" };
let statementActionSuccessTimer = null;

function setStatementActionSuccess(message) {
  if (statementActionSuccessTimer && typeof window !== "undefined") window.clearTimeout(statementActionSuccessTimer);
  statementActionSuccessTimer = null;
  statementActionState.error = false;
  statementActionState.message = String(message || "");
  if (!statementActionState.message || typeof window === "undefined" || typeof window.setTimeout !== "function") return;
  const expected = statementActionState.message;
  statementActionSuccessTimer = window.setTimeout(() => {
    statementActionSuccessTimer = null;
    if (statementActionState.busy || statementActionState.error || statementActionState.message !== expected) return;
    statementActionState.message = "";
    renderStatements();
  }, 4800);
}

function clearStatementActionSuccess() {
  if (statementActionSuccessTimer && typeof window !== "undefined") window.clearTimeout(statementActionSuccessTimer);
  statementActionSuccessTimer = null;
  if (!statementActionState.busy && !statementActionState.error) statementActionState.message = "";
}
const statementPayState = {
  open: false,
  amount: "",
  paidOnDate: "",
  method: "check",
  applyToOldest: true,
  note: "",
  disclaimer: false,
  lastReceipt: null,
  commandKey: ""
};
const STATEMENT_PAYMENT_DISCLAIMER = "TMS does not collect this payment and does not verify that it was received. Marking a line paid or unpaid is your practice's record only. TMS is not responsible for amounts recorded here.";

function statementsFilters() {
  return { fromDos: (typeof state !== "undefined" && state.statementsFromDos) || "", toDos: (typeof state !== "undefined" && state.statementsToDos) || "" };
}

function statementPreviewKey(localControls = true) {
  const f = statementsFilters();
  // focusOrg IS part of the key. It used not to be, and the preview is held
  // in module state keyed on this string: narrowing to another client would
  // have re-rendered the PREVIOUS client's patients under the new client's
  // name. That is the leak the global client switcher exists to prevent, so
  // it cannot be left outside the key that decides what gets shown.
  return workflowAccessScopedRequestKey({ base: trackerApiBase(), role: state.role, user: state.activeUserName || "", from: f.fromDos, to: f.toDos, focusOrg: String(statementPreviewState.focusOrg || ""), page: localControls ? statementPreviewState.page : undefined, pageSize: localControls ? statementPreviewState.pageSize : undefined, search: localControls ? statementPreviewState.search : undefined, printedFilter: localControls ? statementPreviewState.printedFilter : undefined, payer: statementPreviewState.payer || "", heldPage: statementPreviewState.heldPage || 1, reviewPage: statementPreviewState.reviewPage || 1, publicationEpoch: reportReadPublicationIdentity() });
}

async function requestCurrentStatementValidation(options = {}) {
  const key = statementPreviewKey(), token = trackerApiToken();
  const selected = new Set(options.selectedKeys || Array.from(statementSelection.keys));
  const serviceKeys = [...new Map(statementsAllLines(statementPreviewState.data || { patients: [] })
    .filter(entry => selected.has(entry.key)).map(entry => {
      const value = { orgId: String(entry.p.orgId), claimId: String(entry.svc.claimId), serviceId: String(entry.svc.serviceId) };
      return [JSON.stringify(value), value];
    })).values()];
  if (!serviceKeys.length) return null;
  // The server re-checks at most 5,000 exact services per request; a full page
  // of a large company holds more (7,768 in October 2026). Re-check in chunks
  // and merge, so one service is never in two chunks and every line is checked.
  const CHUNK = 4000;
  let merged = null;
  const byPatient = new Map();
  for (let start = 0; start < serviceKeys.length; start += CHUNK) {
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(trackerApiBase() + "/api/statements/preview?refresh=current", {
        method: "POST", cache: "no-store", headers: { "content-type": "application/json", authorization: "Bearer " + token },
        body: JSON.stringify({ ...statementsFilters(), orgId: String(statementPreviewState.focusOrg || ""), serviceKeys: serviceKeys.slice(start, start + CHUNK), complete: true }), signal: controller.signal
      });
      if (response.status === 401) {
        // Say why nothing printed: the sign-in expired, not the balances.
        statementPreviewState.validationSessionExpired = true;
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return null;
      }
      const payload = await response.json();
      if (!response.ok || payload.error || key !== statementPreviewKey() || token !== trackerApiToken()) return null;
      if (!merged) merged = { ...payload, patients: [] };
      if (payload.truncated) merged.truncated = true;
      (payload.patients || []).forEach((patient) => {
        const pk = statementsPatientKey(patient);
        const existing = byPatient.get(pk);
        if (existing) existing.services = existing.services.concat(patient.services || []);
        else { const copy = { ...patient, services: (patient.services || []).slice() }; byPatient.set(pk, copy); merged.patients.push(copy); }
      });
    } catch (_) { return null; } finally { clearTimeout(timeout); }
  }
  return merged;
}

function requestStatementPreview(options = {}) {
  if (options.requireCurrent) return requestCurrentStatementValidation(options);
  if (typeof syncReportReadContext === "function") syncReportReadContext();
  if (typeof statementsEnsureSelectionScope === "function") statementsEnsureSelectionScope();
  const base = trackerApiBase();
  const requestId = ++statementPreviewState.requestId;
  if (!base || typeof fetch !== "function") { statementPreviewState.status = "unavailable"; statementPreviewState.data = null; return Promise.resolve(null); }
  const token = trackerApiToken();
  const f = statementsFilters();
  const focusOrg = String(statementPreviewState.focusOrg || "");
  const key = statementPreviewKey();
  const completeDataKey = statementPreviewKey(false);
  const isCurrent = () => statementPreviewState.requestId === requestId && statementPreviewState.key === key
    && statementPreviewKey() === key && trackerApiToken() === token;
  const previousData = statementPreviewState.data;
  statementPreviewState.controller?.abort();
  statementPreviewState.key = key;
  statementPreviewState.status = "loading";
  statementPreviewState.data = null;
  statementPreviewState.error = "";
  const controller = new AbortController();
  statementPreviewState.controller = controller;
  // A complete preview walks bounded 5,000-row SQL pages on the server, then
  // preserves whole patient groups for browser paging and exact-key actions.
  // Large organizations can exceed the ordinary 10-second read budget.
  const timeoutId = window.setTimeout(() => { try { controller.abort(); } catch (_e) {} }, 120000);
  const url = `${base}/api/statements/preview`;
  return reportSessionFetch(url, {
    method: "POST",
    cache: "no-store",
    headers: Object.assign({ "content-type": "application/json" }, token ? { authorization: `Bearer ${token}` } : {}),
    body: JSON.stringify({ fromDos: f.fromDos, toDos: f.toDos, orgId: focusOrg, complete: true, page: statementPreviewState.page, pageSize: statementPreviewState.pageSize, search: statementPreviewState.search, printedFilter: statementPreviewState.printedFilter, payer: statementPreviewState.payer || "", heldPage: statementPreviewState.heldPage || 1, reviewPage: statementPreviewState.reviewPage || 1 }),
    signal: controller.signal,
    timeoutMs: 120000
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!isCurrent()) return null;
    if (!response.ok || (payload && payload.error)) {
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      statementPreviewState.status = payload && payload.availability === "unavailable" ? "unavailable" : "error";
      statementPreviewState.error = response.status === 401
        ? "Your session expired. Sign in again — this page will still be here."
        // A user with several companies must pick one: statements are printed
        // per company, and the server refuses an unnarrowed multi-company read.
        : (response.status === 403 && payload && payload.reason === "statements_out_of_scope" && !focusOrg)
          ? "Choose one company in the Company menu at the top to see its patient statements."
          : String((payload && (payload.message || payload.error)) || ("http_" + response.status));
    } else if (focusOrg && String((payload && payload.focusOrgId) || "") !== focusOrg) {
      statementPreviewState.status = "error";
      statementPreviewState.error = "The statement company changed. Reload the statement list.";
    } else {
      statementPreviewState.status = "ready";
      statementPreviewState.data = payload || { patients: [], totals: {} };
      statementPreviewState.completeDataKey = completeDataKey;
      // Kept between loads so the Insurance dropdown does not vanish while the
      // next page is fetched. The server lists insurers before applying the
      // insurance filter, so choosing one never hides the others.
      statementPreviewState.payerOptions = Array.isArray(payload && payload.payers) ? payload.payers : [];
      if (typeof statementsReconcileRefreshedSelection === "function") statementsReconcileRefreshedSelection(statementPreviewState.data);
      clientScopeServerEcho.statements = String((payload && payload.focusOrgId) || "");
      statementPreviewState.error = "";
      if (options.render !== false) populateStatementBrandingForPreview(statementPreviewState.data);
    }
    if (options.render !== false && state.view === "statements") renderStatements();
    return statementPreviewState.status === "ready" ? statementPreviewState.data : null;
  }).catch((err) => {
    if (!isCurrent()) return null;
    const retained = err?.code === "report-read-session-stopped" && previousData;
    statementPreviewState.status = retained ? "ready" : "error";
    statementPreviewState.data = retained || null;
    statementPreviewState.error = retained ? err.message : (err && err.name === "AbortError") ? "timeout" : "network_error";
    if (options.render !== false && state.view === "statements") renderStatements();
    return null;
  }).finally(() => window.clearTimeout(timeoutId));
}

const statementBrandingCache = {}; // Only the current authenticated scope; keyed by orgId.
let statementBrandingCacheScope = "";
const statementBrandingPending = new Map();
function statementBrandingReadScope() {
  return JSON.stringify({ base: statementsBrandingApiBase(), token: statementsBrandingApiToken(), company: activeClientGroupTenant(), companyOrgIds: activeClientGroupOrgIds(), access: workflowCommandAccessSnapshot() });
}
function ensureStatementBrandingScope() {
  const scope = statementBrandingReadScope();
  if (scope !== statementBrandingCacheScope) {
    statementBrandingPending.forEach((read) => read.controller.abort());
    statementBrandingPending.clear();
    Object.keys(statementBrandingCache).forEach((key) => delete statementBrandingCache[key]);
    statementBrandingCacheScope = scope;
  }
  return scope;
}
function statementBrandingForOrg(orgId) {
  ensureStatementBrandingScope();
  return statementBrandingCache[String(orgId == null ? "" : orgId)] || null;
}

function statementsBrandingApiBase() {
  if (typeof portalInviteApiBase === "function") return portalInviteApiBase();
  if (typeof workflowApiBase === "function" && workflowApiBase()) return workflowApiBase();
  if (typeof trackerApiBase === "function" && trackerApiBase()) return trackerApiBase();
  return "";
}
function statementsBrandingApiToken() {
  return (typeof workflowApiToken === "function" && workflowApiToken()) || (typeof trackerApiToken === "function" && trackerApiToken()) || "";
}
// Companies whose branding could not be READ (error, network, expired
// session), as opposed to companies that have none. Printing must not treat a
// failed read as "no branding" and send statements without remit details.
const statementBrandingReadFailed = new Set();
async function fetchStatementBranding(orgId) {
  const id = String(orgId == null ? "" : orgId);
  if (!id) return null;
  const scope = ensureStatementBrandingScope();
  if (statementBrandingPending.has(id)) return statementBrandingPending.get(id).promise;
  const base = statementsBrandingApiBase(), token = statementsBrandingApiToken();
  const read = { controller: new AbortController(), promise: null };
  const current = () => scope === statementBrandingReadScope() && statementBrandingPending.get(id) === read;
  statementBrandingPending.set(id, read);
  read.promise = (async () => {
    try {
      const resp = await fetch(base + "/api/statements/branding?orgId=" + encodeURIComponent(id), { cache: "no-store", headers: { authorization: "Bearer " + token }, signal: read.controller.signal });
      if (!current()) return null;
      if (resp.status === 401) {
        statementBrandingReadFailed.add(id);
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return null;
      }
      if (!resp.ok) { statementBrandingReadFailed.add(id); if (!(id in statementBrandingCache)) statementBrandingCache[id] = null; return statementBrandingCache[id] || null; }
      const body = await resp.json().catch(() => null);
      if (!current()) return null;
      if (!body) { statementBrandingReadFailed.add(id); return statementBrandingCache[id] || null; }
      statementBrandingReadFailed.delete(id);
      statementBrandingCache[id] = body.branding ? body.branding : null;
      return statementBrandingCache[id];
    } catch (_e) { if (current()) statementBrandingReadFailed.add(id); return current() ? (statementBrandingCache[id] || null) : null; }
    finally { if (statementBrandingPending.get(id) === read) statementBrandingPending.delete(id); }
  })();
  return read.promise;
}
function populateStatementBrandingForPreview(data) {
  if (state.view !== "statements") return;
  const scope = ensureStatementBrandingScope();
  const orgs = {};
  ((data && data.patients) ? data.patients : []).forEach((p) => { const id = String(p.orgId == null ? "" : p.orgId); if (id) orgs[id] = true; });
  const missing = Object.keys(orgs).filter((id) => !(id in statementBrandingCache));
  if (!missing.length) return;
  Promise.all(missing.map((id) => fetchStatementBranding(id))).then(() => {
    if (scope === statementBrandingReadScope() && statementPreviewState.data === data && state.view === "statements") renderStatements();
  });
}

// ---- Statement branding editor (per-org provider details on printed statements) ----
// Gated on statements:generate (billers + clients). The server forces the org
// from scope; we still send orgId so a multi-org biller targets the focused org.
const statementBrandingEditor = { open: false, orgId: "", loading: false, saving: false, error: "", form: { displayName: "", address: "", phone: "", email: "", footer: "", logo: "", paymentTerms: "", dueDate: "", npi: "", taxId: "", paymentInstructions: "", logoIncludesName: false, showRemitTo: false, remitName: "", remitAddress: "" } };

function statementsCanEditBranding() {
  return typeof hasPermission === "function" && hasPermission(PERMISSIONS.STATEMENTS_GENERATE);
}

function statementBrandingResolveOrg() {
  if (activeClientGroupTenant()) return activeClientGroupServerOrgId();
  const focus = String(statementPreviewState.focusOrg || "");
  if (focus) return focus;
  const opts = statementsFocusOptions();
  if (opts.length === 1) return String(opts[0].orgId || "");
  // The option list is derived from the PATIENTS ON SCREEN. A DOS window or a
  // search that matches nobody empties it, and the editor then opened on "No
  // client selected." with nothing to select — even for a Client user whose one
  // company was never in doubt. The header switcher still names the client.
  return typeof activeClientGroupServerOrgId === "function" ? String(activeClientGroupServerOrgId() || "").trim() : "";
}

// Clients the branding editor may be pointed at: the clients in the preview,
// or — when the preview has none — the caller's own scope roster, which the
// server re-validates on save. Never empty for a caller with a scope.
function statementBrandingPickerOptions() {
  if (activeClientGroupTenant()) return [{ orgId: activeClientGroupServerOrgId(), client: activeClientGroupLabel() }];
  const opts = statementsFocusOptions();
  if (opts.length || typeof authorizationOrgOptions !== "function") return opts;
  return authorizationOrgOptions()
    .map((option) => ({ orgId: String(option.value || ""), client: String(option.label || option.value || "") }))
    .filter((option) => option.orgId);
}

function statementBrandingClientLabel(orgId) {
  const id = String(orgId == null ? "" : orgId);
  const match = statementsFocusOptions().find((o) => String(o.orgId) === id);
  return match ? String(match.client || "") : "";
}

// The five ways a practice can take money, in the order they print. Every one is
// optional and "other" repeats, so a client that only takes cheques and a client
// that only takes card-by-phone are both expressible without code changes.
const STATEMENT_PAYMENT_KINDS = [
  { kind: "online", label: "Pay online", placeholder: "pay.yourpractice.com" },
  { kind: "phone", label: "Pay by phone", placeholder: "Call (555) 123-4567, Mon-Fri 9-5" },
  { kind: "mail", label: "Pay by mail", placeholder: "Uses the payable-to and remit-to below" },
  { kind: "inPerson", label: "Pay in person", placeholder: "Front desk, Mon-Fri 9-5" }
];

// Builds the editor rows: the four standard kinds always shown (ticked only if
// the client uses them), then any custom entries the client added.
function statementsPaymentOptionsForEditor(br) {
  const saved = Array.isArray(br && br.paymentOptions) ? br.paymentOptions : null;
  const rows = STATEMENT_PAYMENT_KINDS.map((k) => {
    const hit = saved ? saved.find((o) => o && o.kind === k.kind) : null;
    return {
      kind: k.kind,
      enabled: hit ? hit.enabled !== false : false,
      label: (hit && hit.label) || k.label,
      detail: (hit && hit.detail) || ""
    };
  });
  // Legacy brandings predate the list: carry their freeform text and remit flag
  // across so nothing a client already configured silently disappears.
  if (!saved) {
    const legacy = String((br && br.paymentInstructions) || "").trim();
    if (legacy) rows.push({ kind: "other", enabled: true, label: "How to pay", detail: legacy });
    const mailRow = rows.find((r) => r.kind === "mail");
    if (mailRow && br && br.showRemitTo) mailRow.enabled = true;
  } else {
    saved.filter((o) => o && o.kind === "other").forEach((o) => {
      rows.push({ kind: "other", enabled: o.enabled !== false, label: o.label || "Other", detail: o.detail || "" });
    });
  }
  return rows;
}

function statementBrandingFillFormFromCache(orgId) {
  const id = String(orgId == null ? "" : orgId);
  const label = statementBrandingClientLabel(id);
  const b = statementBrandingForOrg(id);
  // The cache entry may be the friendly record { displayName, branding:{...} }
  // or just the inner branding object — handle both (mirrors the print code).
  const br = (b && b.branding) ? b.branding : (b || {});
  const displayName = (b && b.displayName) ? String(b.displayName) : label;
  statementBrandingEditor.form = {
    displayName: displayName || "",
    address: (Array.isArray(br.addressLines) ? br.addressLines : []).join("\n"),
    phone: br.phone || "",
    email: br.email || "",
    footer: br.footerMessage || "",
    logo: br.logoDataUrl || "",
    logoIncludesName: !!br.logoIncludesName,
    paymentTerms: br.paymentTerms || "",
    dueDate: br.dueDate || "",
    paymentInstructions: br.paymentInstructions || "",
    npi: br.npi || "",
    taxId: br.taxId || "",
    showRemitTo: !!br.showRemitTo,
    paymentOptions: statementsPaymentOptionsForEditor(br),
    remitName: br.remitName || "",
    remitAddress: (Array.isArray(br.remitAddressLines) ? br.remitAddressLines : []).join("\n")
  };
}

function openStatementBrandingEditor(orgId) {
  if (!statementsCanEditBranding()) return;
  // A completed statement action is transient feedback, not page state. Do not
  // leave its floating pill behind while an unrelated branding modal is open.
  clearStatementActionSuccess();
  statementBrandingEditor.open = true;
  statementBrandingEditor.orgId = String(orgId || statementBrandingResolveOrg() || "");
  statementBrandingEditor.error = "";
  statementBrandingEditor.saving = false;
  if (statementBrandingEditor.orgId) {
    statementBrandingEditor.loading = true;
    statementBrandingFillFormFromCache(statementBrandingEditor.orgId);
    renderStatementBrandingEditor();
    const org = statementBrandingEditor.orgId, scope = ensureStatementBrandingScope();
    const request = statementBrandingEditor.readRequest = (statementBrandingEditor.readRequest || 0) + 1;
    fetchStatementBranding(org).then(() => {
      if (!statementBrandingEditor.open || statementBrandingEditor.orgId !== org || statementBrandingEditor.readRequest !== request || scope !== statementBrandingReadScope()) return;
      statementBrandingEditor.loading = false;
      statementBrandingFillFormFromCache(org);
      renderStatementBrandingEditor();
    });
  } else {
    statementBrandingEditor.loading = false;
    renderStatementBrandingEditor();
  }
}

function closeStatementBrandingEditor() {
  statementBrandingEditor.readRequest = (statementBrandingEditor.readRequest || 0) + 1;
  statementBrandingEditor.open = false;
  document.getElementById("statementBrandingModal")?.remove();
}

// ESCAPE CLOSES THIS DIALOG, like every other dialog in the portal. It did not:
// the modal re-renders its whole body while branding loads, which throws focus
// back to <body>, so a keydown listener on the dialog itself would never hear
// the key. Listening on the document and checking the editor's own open flag is
// the only place the key reliably arrives.
if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !statementBrandingEditor.open) return;
    if (statementBrandingEditor.saving) return;
    event.preventDefault();
    closeStatementBrandingEditor();
  });
}

// Live-preview helpers for the branding editor. The sample data mirrors the
// exact shape statementsPrintableHtml() iterates: data.patients[].services[].prLines[].
// Lines use a sentinel org id so we can drop a throwaway branding cache entry and
// never touch a real client’s cache.
const STATEMENT_CPT_LABELS = { "97151":"Behavior assessment", "97152":"Behavior assessment", "97153":"Adaptive behavior treatment", "97154":"Group adaptive behavior treatment", "97155":"Adaptive behavior treatment w/ protocol modification", "97156":"Family adaptive behavior guidance", "97157":"Group family guidance", "97158":"Group adaptive behavior treatment" };
const STATEMENT_BRAND_PREVIEW_ORG = "__brandpreview__";
function statementsBrandingSampleData(clientLabel) {
  return {
    patients: [{
      orgId: STATEMENT_BRAND_PREVIEW_ORG,
      // Print falls back to p.client when displayName is blank, so the preview
      // has to carry the ORG's name here or clearing the display name would
      // show the sample patient as the practice. The patient rows stay fake.
      client: String(clientLabel == null ? "" : clientLabel).trim() || "Your Provider",
      patientName: "Sample Patient",
      services: [
        { claimId: "__bp1__", serviceId: "__bp1s__", serviceDate: "2026-01-05", procedureCode: "97153", prLines: [{ label: "Deductible (PR-1)", prReason: "pr-1", amount: 40, isDefault: true }] },
        { claimId: "__bp2__", serviceId: "__bp2s__", serviceDate: "2026-01-12", procedureCode: "97153", prLines: [{ label: "Coinsurance (PR-2)", prReason: "coinsurance", amount: 30, isDefault: true }] },
        { claimId: "__bp3__", serviceId: "__bp3s__", serviceDate: "2026-01-19", procedureCode: "97153", prLines: [{ label: "Copay (PR-3)", prReason: "copay", amount: 20, isDefault: true }] }
      ]
    }]
  };
}
function renderStatementBrandingPreview() {
  const host = document.getElementById("statementBrandingPreview");
  if (!host) return;
  const f = statementBrandingEditor.form || {};
  const branding = {
    displayName: String(f.displayName || ""),
    addressLines: String(f.address || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean),
    phone: String(f.phone || "").trim(),
    email: String(f.email || "").trim(),
    npi: String(f.npi || "").trim(),
    taxId: String(f.taxId || "").trim(),
    footerMessage: String(f.footer || "").trim(),
    logoDataUrl: String(f.logo || "").trim(),
    logoIncludesName: !!f.logoIncludesName,
    paymentTerms: String(f.paymentTerms || "").trim(),
    dueDate: String(f.dueDate || "").trim(),
    paymentInstructions: String(f.paymentInstructions || "").trim(),
    paymentOptions: (f.paymentOptions || []).map((o) => ({ kind: o.kind, enabled: !!o.enabled, label: String(o.label || "").trim() || o.kind, detail: String(o.detail || "").trim() })),
    showRemitTo: (f.paymentOptions || []).some((o) => o.kind === "mail" && o.enabled),
    remitName: String(f.remitName || "").trim(),
    remitAddressLines: String(f.remitAddress || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
  };
  const data = statementsBrandingSampleData(statementBrandingClientLabel(statementBrandingEditor.orgId));
  const sampleKeys = statementsAllLines(data).map((x) => x.key);
  const savedSel = statementSelection.keys;
  try {
    ensureStatementBrandingScope();
    statementBrandingCache[STATEMENT_BRAND_PREVIEW_ORG] = { orgId: STATEMENT_BRAND_PREVIEW_ORG, displayName: branding.displayName, branding: branding };
    statementSelection.keys = new Set(sampleKeys);
    const _prevScroll = host.scrollTop || 0;
    // Lay the sample out at the printed content width (8.5in less 44px margins
    // = 728px) and shrink it to the pane, so the preview wraps exactly like the
    // paper. In a half-width pane the name and address used to be squeezed
    // beside the logo (owner report 2026-10-15).
    // The markup stays byte-identical to the printed sheet; only a style
    // variable on the pane sets the zoom (see .stmt-branding-preview rules).
    host.innerHTML = statementsPrintableHtml(data);
    const available = Number(host.clientWidth || 0) - 48;
    if (available > 0 && host.style && typeof host.style.setProperty === "function") host.style.setProperty("--stmt-preview-zoom", String(Math.min(1, available / 728)));
    // The preview pane is its own scroller: repainting it must not scroll it.
    if (_prevScroll) host.scrollTop = _prevScroll;
  } catch (_e) {
    host.innerHTML = '<div class="panel-note">Preview unavailable.</div>';
  } finally {
    statementSelection.keys = savedSel;
    delete statementBrandingCache[STATEMENT_BRAND_PREVIEW_ORG];
  }
}
// The dialog body (.stmt-branding-body) is the single scroll container for both
// columns, and renderStatementBrandingEditor() replaces the whole window markup.
// Ticking a payment method therefore threw the reader back to the top of the
// dialog mid-click. Structural changes (adding/removing a way to pay, choosing a
// logo) still need a full render, so those go through here: scroll offset and the
// focused control - by its data attribute, since the node itself is gone - are
// captured before and restored after.
function statementBrandingFocusSelector(el) {
  if (!el || typeof el.getAttribute !== "function") return "";
  const attrs = ["data-branding-field", "data-branding-check", "data-payopt-enabled", "data-payopt-detail", "data-payopt-label", "data-payopt-remove", "data-payopt-add", "data-branding-logo", "data-branding-org"];
  for (let i = 0; i < attrs.length; i += 1) {
    const a = attrs[i];
    if (typeof el.hasAttribute === "function" && el.hasAttribute(a)) {
      const v = el.getAttribute(a);
      return v ? `[${a}="${v}"]` : `[${a}]`;
    }
  }
  return "";
}
function statementBrandingRenderPreservingPosition() {
  const before = document.getElementById("statementBrandingModal");
  const bodyBefore = before ? before.querySelector(".stmt-branding-body") : null;
  const scrollTop = bodyBefore ? bodyBefore.scrollTop : 0;
  const active = (typeof document !== "undefined") ? document.activeElement : null;
  const selector = statementBrandingFocusSelector(active);
  const caret = (active && typeof active.selectionStart === "number") ? active.selectionStart : null;
  renderStatementBrandingEditor();
  const after = document.getElementById("statementBrandingModal");
  const bodyAfter = after ? after.querySelector(".stmt-branding-body") : null;
  if (bodyAfter && scrollTop) bodyAfter.scrollTop = scrollTop;
  if (selector && after) {
    const next = after.querySelector(selector);
    if (next && typeof next.focus === "function") {
      try { next.focus({ preventScroll: true }); } catch (_e) { try { next.focus(); } catch (_e2) {} }
      if (caret != null && typeof next.setSelectionRange === "function") { try { next.setSelectionRange(caret, caret); } catch (_e3) {} }
    }
  }
}
// Ticked with no logo means the statement carries no practice name at all, so
// say so where the tick happens rather than letting the preview be the only clue.
function statementBrandingSyncNoNameWarn(modal) {
  const host = modal || document.getElementById("statementBrandingModal");
  const warn = host ? host.querySelector("[data-branding-noname-warn]") : null;
  if (!warn) return;
  const f = statementBrandingEditor.form || {};
  warn.hidden = !(f.logoIncludesName && !f.logo);
}
function renderStatementBrandingEditor() {
  let modal = document.getElementById("statementBrandingModal");
  if (!statementBrandingEditor.open) { modal?.remove(); return; }
  if (!modal) {
    modal = document.createElement("div");
    modal.id = "statementBrandingModal";
    modal.className = "column-view-modal stmt-branding-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-labelledby", "stmtBrandingTitle");
    document.body.appendChild(modal);
  }
  const ed = statementBrandingEditor;
  const opts = statementBrandingPickerOptions();
  const needsPicker = !ed.orgId && opts.length > 1;
  let bodyHtml = "";
  if (needsPicker) {
    bodyHtml = `
      <label class="stmt-branding-field">
        <span class="stmt-branding-label">Client</span>
        <select data-branding-org aria-label="Client">
          <option value="">Choose a client…</option>
          ${opts.map((o) => `<option value="${statementsEscape(o.orgId)}">${statementsEscape(o.client)}</option>`).join("")}
        </select>
      </label>`;
  } else if (ed.loading) {
    bodyHtml = `<div class="panel-note">Loading branding…</div>`;
  } else if (ed.orgId) {
    const f = ed.form;
    const pickerRow = opts.length > 1 ? `
      <label class="stmt-branding-field">
        <span class="stmt-branding-label">Client</span>
        <select data-branding-org aria-label="Client">
          ${opts.map((o) => `<option value="${statementsEscape(o.orgId)}"${String(o.orgId) === String(ed.orgId) ? " selected" : ""}>${statementsEscape(o.client)}</option>`).join("")}
        </select>
      </label>` : "";
    bodyHtml = `
      ${pickerRow}
      <label class="stmt-branding-field">
        <span class="stmt-branding-label">Display name</span>
        <input type="text" data-branding-field="displayName" value="${statementsEscape(f.displayName)}" placeholder="Provider name on statements">
      </label>
      <div class="stmt-branding-field">
        <label class="stmt-branding-label">Logo (shown at the top of printed statements)</label>
        ${ed.form.logo ? `<div class="stmt-branding-logo-row"><img class="stmt-branding-logo-preview" src="${statementsEscape(ed.form.logo)}" alt="Logo preview"><button type="button" class="ghost-button" data-branding-logo-remove>Remove</button></div>` : ""}
        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" data-branding-logo>
        <span class="stmt-branding-help">PNG, JPG, GIF or WebP, under 440 KB.</span>
      </div>
      <label class="stmt-branding-check"><input type="checkbox" data-branding-check="logoIncludesName"${ed.form.logoIncludesName ? " checked" : ""}> My logo already includes my practice name (don’t print the name as text)</label>
      <p class="stmt-branding-warn" data-branding-noname-warn${(ed.form.logoIncludesName && !ed.form.logo) ? "" : " hidden"}>No logo uploaded \u2014 with this ticked, statements print with no practice name at the top.</p>
      <label class="stmt-branding-field">
        <span class="stmt-branding-label">Address</span>
        <textarea data-branding-field="address" rows="3" placeholder="Street&#10;City, ST ZIP">${statementsEscape(f.address)}</textarea>
      </label>
      <div class="stmt-branding-pair">
        <label class="stmt-branding-field">
          <span class="stmt-branding-label">Phone</span>
          <input type="text" data-branding-field="phone" value="${statementsEscape(f.phone)}" placeholder="(555) 123-4567">
        </label>
        <label class="stmt-branding-field">
          <span class="stmt-branding-label">Email</span>
          <input type="text" data-branding-field="email" value="${statementsEscape(f.email)}" placeholder="billing@example.com">
        </label>
      </div>
      <div class="stmt-branding-pair">
        <label class="stmt-branding-field">
          <span class="stmt-branding-label">NPI (optional)</span>
          <input type="text" data-branding-field="npi" value="${statementsEscape(f.npi)}" placeholder="10-digit provider NPI">
        </label>
        <label class="stmt-branding-field">
          <span class="stmt-branding-label">Tax ID / EIN (optional)</span>
          <input type="text" data-branding-field="taxId" value="${statementsEscape(f.taxId)}" placeholder="12-3456789">
        </label>
      </div>
      <label class="stmt-branding-field">
        <span class="stmt-branding-label">Footer message</span>
        <textarea data-branding-field="footer" rows="2" placeholder="e.g. Questions about your bill? Call …">${statementsEscape(f.footer)}</textarea>
      </label>
      <label class="stmt-branding-field">
        <span class="stmt-branding-label">Payment terms</span>
        <input type="text" data-branding-field="paymentTerms" value="${statementsEscape(f.paymentTerms)}" placeholder="e.g. Due upon receipt">
        <span class="stmt-branding-help">e.g. Due upon receipt. Shown under the statement date (defaults to “Upon receipt”).</span>
      </label>
      <label class="stmt-branding-field">
        <span class="stmt-branding-label">Payment due date (optional)</span>
        <input type="text" data-branding-field="dueDate" value="${statementsEscape(f.dueDate)}" placeholder="e.g. Jul 22, 2026 — blank = Upon receipt">
        <span class="stmt-branding-help">A fixed date stops printing once it has passed (the payment terms show instead). For a rolling date use Payment terms, e.g. Due within 30 days.</span>
      </label>
      <div class="stmt-payopts">
        <span class="stmt-branding-label">How can patients pay?</span>
        <p class="stmt-branding-help">Tick only what this client actually accepts. Statements print these in this order; unticked ways never appear.</p>
        ${(f.paymentOptions || []).map((o, i) => {
          const meta = STATEMENT_PAYMENT_KINDS.find((k) => k.kind === o.kind);
          const isOther = o.kind === "other";
          return `<div class="stmt-payopt-row${o.enabled ? " is-on" : ""}" data-payopt-row="${i}">
            <label class="stmt-payopt-toggle">
              <input type="checkbox" data-payopt-enabled="${i}"${o.enabled ? " checked" : ""}>
              ${isOther
                ? `<input type="text" class="stmt-payopt-label-input" data-payopt-label="${i}" value="${statementsEscape(o.label)}" placeholder="Name this way to pay">`
                : `<span class="stmt-payopt-name">${statementsEscape(o.label)}</span>`}
            </label>
            ${isOther ? `<button type="button" class="stmt-payopt-remove" data-payopt-remove="${i}" aria-label="Remove ${statementsEscape(o.label)}">Remove</button>` : ""}
            <input type="text" class="stmt-payopt-detail" data-payopt-detail="${i}" value="${statementsEscape(o.detail)}"
                   placeholder="${statementsEscape((meta && meta.placeholder) || "Details patients need")}"${o.enabled ? "" : " disabled"}>
          </div>`;
        }).join("")}
        <button type="button" class="stmt-payopt-add" data-payopt-add>+ Add another way to pay</button>
      </div>
      <div data-remit-group style="display:${(f.paymentOptions || []).some((o) => o.kind === "mail" && o.enabled) ? "" : "none"};">
      <label class="stmt-branding-field">
        <span class="stmt-branding-label">Make checks payable to</span>
        <input type="text" data-branding-field="remitName" value="${statementsEscape(f.remitName)}" placeholder="Practice / billing entity name">
      </label>
      <label class="stmt-branding-field">
        <span class="stmt-branding-label">Remit-to address</span>
        <textarea data-branding-field="remitAddress" rows="3" placeholder="Where to mail payment (leave blank to use the address above)">${statementsEscape(f.remitAddress)}</textarea>
      </label>
      </div>
      <p class="stmt-branding-help">Leave blank to use defaults.</p>`;
  } else {
    bodyHtml = `<div class="panel-note">No client is in view, so there is nothing to brand yet. Pick a client in the header switcher or the Client filter, or open Branding from a client's \u201cDefault branding\u201d link in the list.</div>`;
  }
  const errHtml = ed.error ? `<div class="stmt-branding-error">${statementsEscape(ed.error)}</div>` : "";
  const canSave = Boolean(ed.orgId) && !ed.loading && !ed.saving;
  modal.innerHTML = `
    <div class="column-view-backdrop" data-branding-close="1"></div>
    <section class="column-view-window stmt-branding-window">
      <div class="column-view-head">
        <div>
          <h3 id="stmtBrandingTitle">Statement branding</h3>
          <p>Appears on printed patient statements.</p>
        </div>
        <button type="button" class="ghost-button" data-branding-close="1" aria-label="Close statement branding">×</button>
      </div>
      <div class="stmt-branding-body stmt-branding-cols">
        <div class="stmt-branding-form-col">${bodyHtml}</div>
        <div class="stmt-branding-preview-wrap"><div class="stmt-branding-preview-label">Live preview</div><div id="statementBrandingPreview" class="stmt-branding-preview"></div></div>
      </div>
      ${errHtml}
      <div class="stmt-branding-foot">
        <button type="button" class="ghost-button" data-branding-cancel="1">Cancel</button>
        <button type="button" class="primary-button" data-branding-save="1"${canSave ? "" : " disabled"}>${ed.saving ? "Saving…" : "Save branding"}</button>
      </div>
    </section>`;
  bindStatementBrandingEditor(modal);
  renderStatementBrandingPreview();
}

function bindStatementBrandingEditor(modal) {
  // Debounced live-preview refresh (~200ms) so typing repaints the sample statement.
  let __brandPreviewT = null;
  const schedulePreview = () => { if (__brandPreviewT) clearTimeout(__brandPreviewT); __brandPreviewT = setTimeout(renderStatementBrandingPreview, 200); };
  modal.querySelectorAll("[data-branding-close], [data-branding-cancel]").forEach((node) => node.addEventListener("click", closeStatementBrandingEditor));
  modal.querySelectorAll("[data-branding-field]").forEach((el) => el.addEventListener("input", (e) => {
    const field = e.target.getAttribute("data-branding-field");
    if (field && field in statementBrandingEditor.form) statementBrandingEditor.form[field] = e.target.value || "";
    schedulePreview();
  }));
  // Payment options: edit in place, re-render only on structural changes (add /
  // remove / toggle) so typing a detail never steals focus mid-keystroke.
  const payForm = () => (statementBrandingEditor.form.paymentOptions = statementBrandingEditor.form.paymentOptions || []);
  const syncRemitGroup = () => {
    const grp = modal.querySelector("[data-remit-group]");
    if (grp) grp.style.display = payForm().some((o) => o.kind === "mail" && o.enabled) ? "" : "none";
  };
  modal.querySelectorAll("[data-payopt-enabled]").forEach((cb) => cb.addEventListener("change", (e) => {
    const i = Number(cb.getAttribute("data-payopt-enabled"));
    const row = payForm()[i];
    if (!row) return;
    row.enabled = !!e.target.checked;
    // Patch this row in place. A full render here rebuilt .stmt-branding-body and
    // reset its scrollTop, which is why ticking Pay online jumped the dialog to
    // the top and dropped focus off the box the reader had just clicked.
    const rowEl = modal.querySelector(`[data-payopt-row="${i}"]`);
    if (rowEl && rowEl.classList) { if (row.enabled) rowEl.classList.add("is-on"); else rowEl.classList.remove("is-on"); }
    const detailEl = modal.querySelector(`[data-payopt-detail="${i}"]`);
    if (detailEl) detailEl.disabled = !row.enabled;
    syncRemitGroup();
    renderStatementBrandingPreview();
  }));
  modal.querySelectorAll("[data-payopt-detail]").forEach((el) => el.addEventListener("input", (e) => {
    const row = payForm()[Number(el.getAttribute("data-payopt-detail"))];
    if (row) row.detail = e.target.value || "";
    schedulePreview();
  }));
  modal.querySelectorAll("[data-payopt-label]").forEach((el) => el.addEventListener("input", (e) => {
    const row = payForm()[Number(el.getAttribute("data-payopt-label"))];
    if (row) row.label = e.target.value || "";
    schedulePreview();
  }));
  modal.querySelectorAll("[data-payopt-remove]").forEach((btn) => btn.addEventListener("click", () => {
    payForm().splice(Number(btn.getAttribute("data-payopt-remove")), 1);
    statementBrandingRenderPreservingPosition();
  }));
  modal.querySelectorAll("[data-payopt-add]").forEach((btn) => btn.addEventListener("click", () => {
    payForm().push({ kind: "other", enabled: true, label: "", detail: "" });
    statementBrandingRenderPreservingPosition();
  }));
  // Instant, not debounced: the owner asked to see the name come off the sheet
  // the moment the box is ticked. Nothing structural changes in the form, so the
  // warning is patched in place and the dialog never re-renders.
  modal.querySelectorAll("[data-branding-check]").forEach((cb) => cb.addEventListener("change", (e) => {
    const f = cb.getAttribute("data-branding-check");
    if (f in statementBrandingEditor.form) statementBrandingEditor.form[f] = !!e.target.checked;
    if (f === "showRemitTo") { const grp = modal.querySelector("[data-remit-group]"); if (grp) grp.style.display = e.target.checked ? "" : "none"; }
    statementBrandingSyncNoNameWarn(modal);
    renderStatementBrandingPreview();
  }));
  const orgSel = modal.querySelector("[data-branding-org]");
  if (orgSel) orgSel.addEventListener("change", (e) => {
    const next = String(e.target.value || "");
    statementBrandingEditor.orgId = next;
    statementBrandingEditor.error = "";
    if (next) {
      statementBrandingEditor.loading = true;
      statementBrandingFillFormFromCache(next);
      renderStatementBrandingEditor();
      const scope = ensureStatementBrandingScope();
      const request = statementBrandingEditor.readRequest = (statementBrandingEditor.readRequest || 0) + 1;
      fetchStatementBranding(next).then(() => {
        if (!statementBrandingEditor.open || statementBrandingEditor.orgId !== next || statementBrandingEditor.readRequest !== request || scope !== statementBrandingReadScope()) return;
        statementBrandingEditor.loading = false;
        statementBrandingFillFormFromCache(next);
        renderStatementBrandingEditor();
      });
    } else {
      renderStatementBrandingEditor();
    }
  });
  const logoInput = modal.querySelector("[data-branding-logo]");
  if (logoInput) logoInput.addEventListener("change", (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    // Instant: the chosen file is drawn into the sample statement client-side, from
    // a data URL. Nothing is uploaded to preview it.
    // The server keeps only PNG/JPEG/GIF/WebP data URLs up to 600,000 characters
    // (statementBrandingContract.js) and silently drops anything else, so refuse
    // it here rather than preview a logo that will never print.
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      if (dataUrl.length > 600000 || !["png", "jpeg", "jpg", "gif", "webp"].some((kind) => dataUrl.toLowerCase().startsWith("data:image/" + kind + ";base64,"))) {
        statementBrandingEditor.error = "Use a PNG, JPEG, GIF or WebP logo under 440 KB.";
        e.target.value = "";
        statementBrandingRenderPreservingPosition();
        return;
      }
      statementBrandingEditor.error = "";
      statementBrandingEditor.form.logo = dataUrl;
      statementBrandingRenderPreservingPosition();
    };
    reader.readAsDataURL(file);
  });
  const logoRemove = modal.querySelector("[data-branding-logo-remove]");
  if (logoRemove) logoRemove.addEventListener("click", () => { statementBrandingEditor.form.logo = ""; statementBrandingRenderPreservingPosition(); });
  const saveBtn = modal.querySelector("[data-branding-save]");
  if (saveBtn) saveBtn.addEventListener("click", saveStatementBranding);
}

async function saveStatementBranding() {
  const org = statementBrandingEditor.orgId;
  if (!org) { statementBrandingEditor.error = "Choose a client first."; renderStatementBrandingEditor(); return; }
  const scope = ensureStatementBrandingScope();
  const request = statementBrandingEditor.readRequest = (statementBrandingEditor.readRequest || 0) + 1;
  const current = () => scope === statementBrandingReadScope() && statementBrandingEditor.open && statementBrandingEditor.orgId === org && statementBrandingEditor.readRequest === request;
  const f = statementBrandingEditor.form;
  const branding = { addressLines: String(f.address || "").split(/\r?\n/).map((s) => s.trim()).filter(Boolean), phone: String(f.phone || "").trim(), email: String(f.email || "").trim(), footerMessage: String(f.footer || "").trim(), logoDataUrl: String(f.logo || "").trim(), logoIncludesName: !!f.logoIncludesName, npi: String(f.npi||"").trim(), taxId: String(f.taxId||"").trim(), paymentTerms: String(f.paymentTerms || "").trim(), dueDate: String(f.dueDate||"").trim(), paymentInstructions: String(f.paymentInstructions||"").trim(), paymentOptions: (f.paymentOptions || []).filter((o) => o && (o.kind !== "other" || String(o.label || "").trim() || String(o.detail || "").trim())).map((o) => ({ kind: o.kind, enabled: !!o.enabled, label: String(o.label || "").trim(), detail: String(o.detail || "").trim() })), showRemitTo: (f.paymentOptions || []).some((o) => o.kind === "mail" && o.enabled), remitName: String(f.remitName || "").trim(), remitAddressLines: String(f.remitAddress || "").split(/\r?\n/).map((s) => s.trim()).filter(Boolean) };
  statementBrandingEditor.saving = true; statementBrandingEditor.error = ""; renderStatementBrandingEditor();
  try {
    const resp = await fetch(`${statementsBrandingApiBase()}/api/statements/branding`, { method: "PUT", cache: "no-store", headers: { "content-type": "application/json", authorization: `Bearer ${statementsBrandingApiToken()}` }, body: JSON.stringify({ orgId: org, displayName: String(f.displayName || "").trim(), branding }) });
    const body = await resp.json().catch(() => ({}));
    if (!current()) return;
    if (resp.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      statementBrandingEditor.saving = false;
      statementBrandingEditor.error = "Your session expired. Sign in again — this page will still be here.";
      renderStatementBrandingEditor();
      return;
    }
    if (!resp.ok || body.error) { statementBrandingEditor.saving = false; statementBrandingEditor.error = body.message || body.error || `HTTP ${resp.status}`; renderStatementBrandingEditor(); return; }
    statementBrandingPending.get(org)?.controller.abort();
    statementBrandingPending.delete(org);
    statementBrandingCache[org] = body.branding || null;
    statementBrandingEditor.saving = false; statementBrandingEditor.open = false; renderStatementBrandingEditor();
    if (typeof showToast === "function") showToast("Statement branding saved.");
    if (state.view === "statements" && typeof renderStatements === "function") renderStatements();
  } catch (e) { if (!current()) return; statementBrandingEditor.saving = false; statementBrandingEditor.error = "Couldn\u2019t save — check your connection."; renderStatementBrandingEditor(); }
}

function statementsEscape(value) {
  return String(value == null ? "" : value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

// Patient statements: only ERA CARC group=PR reason codes 1/2/3 (deductible/
// coinsurance/copay) are true patient responsibility and may be billed. The
// source (backend statementSource.js SQL + JS guard) never emits any other PR
// code, so there is no non-billable "other charge" opt-in path in the UI.

// Statements are always reconciled to the cent. This used to prepend "$" to a
// hand-formatted number, which put the sign AFTER the symbol ($-1,234.56) - a
// rendering no other surface in the product used and no reader expects.
function statementsMoney(value) {
  return formatMoneyValue(value, { decimals: 2 });
}

// EZClaim ids are per company database, so the same claim/service/reason
// repeats across companies (1,399 claim/service pairs did in October 2026).
// The company is part of the key so the all-companies list can never select,
// print or mark one company's line through another company's identical ids.
function statementsLineKey(svc, line, patient) {
  const orgId = (svc && svc.orgId != null && svc.orgId !== "") ? svc.orgId : (patient && patient.orgId);
  return [orgId, svc.claimId, svc.serviceId, line.prReason].map((x) => String(x == null ? "" : x)).join("|");
}

function statementsDataKey(data) {
  return JSON.stringify((data && data.patients ? data.patients : []).map((p) => [p.patientName, (p.services || []).map((s) => [s.claimId, s.serviceId, (s.prLines || []).map((l) => l.prReason + ":" + l.amount)])]));
}

function statementsSelectionScopeKey() {
  // Memory only. Publication epochs deliberately do not change selection scope.
  return workflowAccessScopedRequestKey({ base: trackerApiBase(), token: trackerApiToken(), filters: statementsFilters(), orgId: String(statementPreviewState.focusOrg || "") });
}

function statementsEnsureSelectionScope() {
  const scopeKey = statementsSelectionScopeKey();
  if (statementSelection.scopeKey && statementSelection.scopeKey !== scopeKey) {
    statementSelection.keys = new Set();
    statementSelection.dataKey = "";
    statementSelection.refreshContext = null;
  }
  statementSelection.scopeKey = scopeKey;
  return scopeKey;
}

function statementsSelectionViewKey() {
  return JSON.stringify({ page: statementPreviewState.page || 1, pageSize: statementPreviewState.pageSize || 50, search: statementPreviewState.search || "", printedFilter: statementPreviewState.printedFilter || "all", payer: statementPreviewState.payer || "" });
}

function statementsPrepareAutomaticSelectionRefresh() {
  const scopeKey = statementsEnsureSelectionScope();
  if (statementPreviewState.status !== "ready" || !statementPreviewState.data) return;
  statementSelection.refreshContext = { scopeKey, viewKey: statementsSelectionViewKey() };
}

function statementsReconcileRefreshedSelection(data) {
  const refresh = statementSelection.refreshContext;
  const scopeKey = statementsEnsureSelectionScope();
  if (!refresh) return;
  statementSelection.refreshContext = null;
  if (refresh.scopeKey !== scopeKey || refresh.viewKey !== statementsSelectionViewKey()) return;
  // Keep exactly the reader's chosen stable keys that are still eligible; a
  // new default line is not a new billing target until the reader selects it.
  const eligible = new Set(statementsAllLines(data)
    .filter((entry) => Number(entry.line.outstanding ?? entry.line.amount ?? 0) > 0)
    .map((entry) => entry.key));
  statementSelection.keys = new Set(Array.from(statementSelection.keys).filter((key) => eligible.has(key)));
  statementSelection.dataKey = statementsDataKey({ patients: statementsPageModel(data).patients }) + "|" + String(statementPreviewState.printedFilter || "all");
}

function statementsEnsureSelection(data, patients) {
  if (typeof statementsEnsureSelectionScope === "function") statementsEnsureSelectionScope();
  const visible = Array.isArray(patients) ? patients : statementsPageModel(data).patients;
  // The Show filter decides which lines are visible, so a filter change reseeds
  // the selection even when the same patients come back.
  const dk = statementsDataKey({ patients: visible }) + "|" + String(statementPreviewState.printedFilter || "all");
  if (statementSelection.dataKey === dk) return;
  statementSelection.dataKey = dk;
  // Seed the run instead of starting empty. Every unprinted default line (the
  // deductible / coinsurance / copay rows the statement would carry anyway) is
  // pre-picked, so the biller lands on a ready-to-print run and the summary's
  // "default" figure matches what is actually selected. Previously this cleared
  // the set while the UI claimed lines were "selected by default".
  // Only lines the current Show filter displays: under "Printed" or "Paid" the
  // patient's hidden unprinted lines must not become an invisible print run.
  const shown = typeof statementsFilteredLines === "function"
    ? new Set(visible.flatMap((p) => (p.services || []).flatMap((svc) => statementsFilteredLines(svc))))
    : null;
  statementSelection.keys = new Set(
    statementsAllLines({ patients: visible })
      // Fully paid lines have nothing to bill, so they are not pre-picked.
      .filter((x) => x.line && x.line.isDefault && !x.line.alreadyPrinted && x.line.paidStatus !== "paid" && (!shown || shown.has(x.line)))
      .map((x) => x.key)
  );
}

function statementsAllLines(data) {
  const out = [];
  (data && data.patients ? data.patients : []).forEach((p) => (p.services || []).forEach((svc) => (svc.prLines || []).forEach((line) => out.push({ p: p, svc: svc, line: line, key: statementsLineKey(svc, line, p) }))));
  return out;
}

function statementsOrgDefaultLineKeys(orgKey) {
  const data = statementPreviewState.data || { patients: [] };
  const out = [];
  (data.patients || []).forEach((p) => {
    if (String(p.orgId || p.client || "-") !== orgKey) return;
    if (typeof statementsPatientMatches === "function" && !statementsPatientMatches(p)) return;
    // A fully paid line has nothing to bill; including it made the print re-check refuse the run.
    (p.services || []).forEach((svc) => statementsFilteredLines(svc).forEach((line) => { if (line.isDefault && Number(line.outstanding ?? line.amount ?? 0) > 0) out.push(statementsLineKey(svc, line, p)); }));
  });
  return out;
}
function statementsMarkPrintedForKeys(keys, done, printedLines) {
  const arr = Array.isArray(keys) ? keys : Array.from(keys || []);
  if (!arr.length) { if (typeof done === "function") done(); return; }
  const saved = new Set(statementSelection.keys);
  statementSelection.keys = new Set(arr);
  try { markSelectedStatements(printedLines); } finally { statementSelection.keys = saved; }
  if (typeof done === "function") done();
}
function statementsDismissPrintPrompt() {
  const el = document.getElementById("stmtPrintPromptToast");
  if (el && el.parentNode) el.parentNode.removeChild(el);
  window.clearTimeout(statementsDismissPrintPrompt._t);
}
function statementsPromptMarkPrinted(keys, count, printedLines) {
  // Browsers fire afterprint when the dialog CLOSES, including when the user
  // cancels. A closed dialog is not evidence that paper/PDF was produced, so do
  // not write the durable printed ledger until the user explicitly confirms.
  // Each print captures its own keys; a later print replaces the unanswered
  // prompt rather than leaving two ambiguous confirmation bars on screen.
  const arr = Array.isArray(keys) ? keys : Array.from(keys || []);
  if (!arr.length) return () => {};
  let active = true;
  const onAfter = () => {
    if (!active) return;
    active = false;
    if (window.removeEventListener) window.removeEventListener("afterprint", onAfter);
    const n = Number(count || arr.length) || arr.length;
    statementsDismissPrintPrompt();
    const toast = document.createElement("div");
    toast.id = "stmtPrintPromptToast";
    toast.className = "stmt-toast stmt-toast--confirm";
    toast.setAttribute("role", "status");
    const msg = document.createElement("span");
    msg.className = "stmt-toast-msg";
    msg.textContent = "Did printing finish for " + n + " line" + (n === 1 ? "" : "s") + "?";
    const confirmBtn = document.createElement("button");
    confirmBtn.type = "button";
    confirmBtn.className = "stmt-toast-confirm";
    confirmBtn.textContent = "Yes, mark printed";
    confirmBtn.addEventListener("click", () => {
      statementsDismissPrintPrompt();
      statementsMarkPrintedForKeys(arr, undefined, printedLines);
    });
    const dismissBtn = document.createElement("button");
    dismissBtn.type = "button";
    dismissBtn.className = "stmt-toast-dismiss";
    dismissBtn.textContent = "No, leave unmarked";
    dismissBtn.addEventListener("click", statementsDismissPrintPrompt);
    toast.appendChild(msg);
    toast.appendChild(confirmBtn);
    toast.appendChild(dismissBtn);
    document.body.appendChild(toast);
  };
  const cancel = () => {
    if (!active) return;
    active = false;
    if (window.removeEventListener) window.removeEventListener("afterprint", onAfter);
  };
  if (window.addEventListener) window.addEventListener("afterprint", onAfter, { once: true });
  else onAfter();
  return cancel;
}
function statementsOrgSelectedLineKeys(orgKey) {
  const data = statementPreviewState.data || { patients: [] };
  const out = [];
  (data.patients || []).forEach((p) => {
    if (String(p.orgId || p.client || "-") !== orgKey) return;
    (p.services || []).forEach((svc) => (svc.prLines || []).forEach((line) => {
      const k = statementsLineKey(svc, line, p);
      if (statementSelection.keys.has(k)) out.push(k);
    }));
  });
  return out;
}
function printOrgStatements(orgKey) {
  const selectedKeys = statementsOrgSelectedLineKeys(orgKey);
  const keys = selectedKeys.length ? selectedKeys : statementsOrgDefaultLineKeys(orgKey);
  if (!keys.length) { statementActionState.message = "No statements to print for this client."; statementActionState.error = true; renderStatements(); return; }
  return printSelectedStatements(keys);
}
function statementsVisibleLineKeys() {
  const data = statementPreviewState.data || { patients: [] };
  const out = [];
  statementsPageModel(data).patients.forEach((p) => {
    (p.services || []).forEach((svc) => statementsFilteredLines(svc).forEach((line) => out.push(statementsLineKey(svc, line, p))));
  });
  return out;
}
function statementsVisibleDefaultLineKeys() {
  const data = statementPreviewState.data || { patients: [] };
  const out = [];
  statementsPageModel(data).patients.forEach((p) => {
    (p.services || []).forEach((svc) => statementsFilteredLines(svc).forEach((line) => { if (line.isDefault) out.push(statementsLineKey(svc, line, p)); }));
  });
  return out;
}

// Statements UI iconography. Inline so the tab has no asset dependency and the
// glyphs inherit currentColor (which is what makes them survive dark mode).
const STMT_ICONS = {
  chevron: '<svg class="stmt-chev" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  search: '<svg class="stmt-ico" viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.25" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M10.2 10.2L14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
  print: '<svg class="stmt-ico" viewBox="0 0 16 16" aria-hidden="true"><path d="M4.75 6.25V2.75h6.5v3.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><rect x="2.25" y="6.25" width="11.5" height="5" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M4.75 9.75h6.5v3.5h-6.5z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>',
  refresh: '<svg class="stmt-ico" viewBox="0 0 16 16" aria-hidden="true"><path d="M13.25 8a5.25 5.25 0 1 1-1.62-3.78" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M13.4 2.4v3h-3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  brand: '<svg class="stmt-ico" viewBox="0 0 16 16" aria-hidden="true"><rect x="2.25" y="3.25" width="11.5" height="9.5" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.25 6.25h11.5" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>',
  info: '<svg class="stmt-ico" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 7.4v3.9M8 5.05v.95" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
};

// The three server-side eligibility holds (backend/statementSource.js:179-182).
// The API returns these rows in `held`; before this they were silently dropped
// from the UI, so a biller could not tell "owes nothing" from "not billable yet".
const STATEMENT_HELD_REASONS = {
  "secondary-on-claim": { label: "Secondary insurance on the claim", hint: "Patient responsibility is $0 while any secondary exists anywhere on this claim." },
  "within-grace-period": { label: "Inside the 7-day grace period", hint: "Wait 7 days from the latest contributing PR 1/2/3 adjustment; a later correction restarts the wait." },
  "open-follow-up": { label: "Open follow-up action", hint: "Every named follow-up action blocks printing except Write-Off, Underpayment Review Required, Missing Fee Schedule, Claim Status Checkup Required, Primary Claim In Process and an Insurance Follow-Up set by the automatic underpayment check. Blank or None is cleared, and every other print rule must still pass." },
  "claim-not-final": { label: "Claim not final", hint: "The claim is Ready to Submit or On Hold in EZClaim, so insurance may still change the patient's share." }
};

function statementsToolbar() {
  const f = statementsFilters();
  const mode = statementPreviewState.printedFilter || "all";
  const q = statementsEscape(statementPreviewState.search || "");
  const seg = (val, lbl, hint) => '<button type="button" class="stmt-seg' + (mode === val ? " is-on" : "") + '" data-stmt-printed="' + val + '" title="' + hint + '">' + lbl + "</button>";
  const focusOpts = statementsFocusOptions();
  const focus = String(statementPreviewState.focusOrg || "");
  let scope = "";
  // The global client switcher already answered "which client"; a second
  // dropdown here would let the two disagree, so it degrades to the chip.
  const globalScopeLabel = typeof activeClientGroupLabel === "function" ? activeClientGroupLabel() : "";
  if (globalScopeLabel) {
    scope = '<span class="stmt-field"><span class="stmt-field-label">Client</span><span class="stmt-scope-chip">' + statementsEscape(globalScopeLabel) + "</span></span>";
  } else if (focusOpts.length > 1) {
    scope = '<label class="stmt-field"><span class="stmt-field-label">Client</span><select class="stmt-input stmt-select" data-stmt-focus>'
      + '<option value=""' + (focus === "" ? " selected" : "") + ">All clients</option>"
      + focusOpts.map((o) => '<option value="' + statementsEscape(o.orgId) + '"' + (focus === o.orgId ? " selected" : "") + ">" + statementsEscape(o.client) + "</option>").join("")
      + "</select></label>";
  } else if (focusOpts.length === 1) {
    scope = '<span class="stmt-field"><span class="stmt-field-label">Client</span><span class="stmt-scope-chip">' + statementsEscape(focusOpts[0].client) + "</span></span>";
  }
  const insurance = statementsInsuranceField();
  const branding = statementsCanEditBranding()
    ? '<button type="button" class="stmt-btn stmt-btn-quiet" data-stmt-branding title="Logo, address and pay-by details printed on the statement">' + STMT_ICONS.brand + "<span>Branding</span></button>"
    : "";
  return `
    <div class="stmt-toolbar">
      ${scope}
      ${insurance}
      <label class="stmt-field stmt-field--grow">
        <span class="stmt-field-label">Search</span>
        <span class="stmt-search-wrap">${STMT_ICONS.search}<input type="search" class="stmt-input stmt-search" data-stmt-search placeholder="Patient or client name" value="${q}" aria-label="Search patient or client"></span>
      </label>
      <div class="stmt-field">
        <span class="stmt-field-label">Show</span>
        <div class="stmt-segctl" role="group" aria-label="Filter statements">${seg("all", "All", "Every line")}${seg("unprinted", "To print", "Not printed yet")}${seg("printed", "Printed", "Already printed")}${seg("unpaid", "Unpaid", "Still outstanding")}${seg("paid", "Paid", "Marked paid")}</div>
      </div>
      <label class="stmt-field">
        <span class="stmt-field-label">From DOS</span>
        <input type="date" class="stmt-input stmt-date" data-stmt-from value="${statementsEscape(f.fromDos)}">
      </label>
      <label class="stmt-field">
        <span class="stmt-field-label">To DOS</span>
        <input type="date" class="stmt-input stmt-date" data-stmt-to value="${statementsEscape(f.toDos)}">
      </label>
      <div class="stmt-toolbar-end">
        <button type="button" class="stmt-btn stmt-btn-quiet" data-stmt-refresh title="Reload from the source">${STMT_ICONS.refresh}<span>Refresh</span></button>
        ${branding}
      </div>
    </div>`;
}

const STATEMENT_NO_PAYER = "__none__";

// Clients sometimes print by insurer. The options are the primary payers with
// statement lines in the current company and service-date range.
function statementsInsuranceField() {
  const options = Array.isArray(statementPreviewState.payerOptions) ? statementPreviewState.payerOptions : [];
  const current = String(statementPreviewState.payer || "");
  if (!options.length && !current) return "";
  const label = (payer) => (payer === STATEMENT_NO_PAYER ? "No insurance recorded" : payer);
  const listed = options.some((o) => String(o.payer) === current);
  return '<label class="stmt-field"><span class="stmt-field-label">Insurance</span><select class="stmt-input stmt-select" data-stmt-payer aria-label="Filter by insurance">'
    + '<option value=""' + (current === "" ? " selected" : "") + ">All insurances</option>"
    + options.map((o) => '<option value="' + statementsEscape(o.payer) + '"' + (current === String(o.payer) ? " selected" : "") + ">" + statementsEscape(label(String(o.payer))) + "</option>").join("")
    + (current && !listed ? '<option value="' + statementsEscape(current) + '" selected>' + statementsEscape(label(current)) + "</option>" : "")
    + "</select></label>";
}

function statementsSummaryStrip(totals, data) {
  const t = totals || {};
  const lines = Number(t.lineCount || 0);
  const printed = Number(t.alreadyPrintedCount || 0);
  const remaining = Math.max(0, lines - printed);
  const patients = Number(t.patientCount || 0);
  const hasRows = !!(data && data.patients && data.patients.length);
  const source = statementsSourceLabel(data);
  const quick = (type, label) => '<button type="button" class="stmt-quick" data-stmt-prtype="' + type + '" title="Select every ' + label.toLowerCase() + ' line">' + label + "</button>";
  const tools = hasRows ? `
      <div class="stmt-overview-tools">
        <span class="stmt-quick-label">Quick select</span>
        ${quick("deductible", "Deductible")}${quick("coinsurance", "Coinsurance")}${quick("copay", "Copay")}
        <button type="button" class="stmt-quick" data-stmt-selectall-visible title="Select every line currently shown">All visible</button>
      </div>` : "";
  return `
    <div class="stmt-overview">
      <div class="stmt-overview-figures">
        <div class="stmt-hero">
          <span class="stmt-hero-label">Outstanding on statements</span>
          <strong class="stmt-hero-value">${statementsMoney(t.outstanding != null ? t.outstanding : t.openAmount)}</strong>
          <small class="stmt-hero-note" title="Only lines that can be billed to the patient now: no secondary insurance on the claim, no open follow-up action, and the patient share posted at least 7 days ago. AR Aging and the Patient Responsibility report include more lines, so their totals can be higher.">Lines ready to bill the patient</small>
        </div>
        <dl class="stmt-facts">
          <div class="stmt-fact"><dt>Patients</dt><dd>${patients}</dd></div>
          <div class="stmt-fact"><dt>Lines</dt><dd>${lines}</dd></div>
          <div class="stmt-fact stmt-fact--go"><dt>To print</dt><dd>${remaining}</dd></div>
          <div class="stmt-fact stmt-fact--quiet"><dt>Printed</dt><dd>${printed}</dd></div>
          <div class="stmt-fact"><dt>Patient paid</dt><dd>${statementsMoney(t.patientPaid || 0)}</dd></div>
        </dl>
        ${source ? `<span class="stmt-source-note">Source: ${statementsEscape(source)}</span>` : ""}
      </div>
      ${tools}
    </div>`;
}

function statementsSourceLabel(data) {
  const source = String((data && data.source) || "");
  if (source === "prepared") return "Saved report view";
  if (source === "warehouse") return "Current";
  if (source === "mirror-compatibility") return "Legacy mirror (compatibility mode)";
  return "";
}

function statementsEvidencePager(page, kind) {
  if (!page || Number(page.totalRows) <= Number(page.pageSize)) return "";
  const current = Math.max(1, Number(page.page) || 1), count = Math.max(1, Math.ceil(Number(page.totalRows) / Number(page.pageSize)));
  return '<nav class="stmt-pager" aria-label="' + kind + ' pages"><span>Page ' + current + ' of ' + count + ' · ' + Number(page.totalRows).toLocaleString() + ' service lines</span><button type="button" class="stmt-page-move" data-stmt-evidence="' + kind + '" data-stmt-evidence-page="' + (current - 1) + '"' + (current <= 1 ? ' disabled' : '') + '>Previous</button><button type="button" class="stmt-page-move" data-stmt-evidence="' + kind + '" data-stmt-evidence-page="' + (current + 1) + '"' + (current >= count ? ' disabled' : '') + '>Next</button></nav>';
}

function statementsHeldNotice(data) {
  const held = Array.isArray(data && data.held) ? data.held : [];
  const count = Number((data && data.totals && data.totals.heldCount) || held.length || 0);
  if (!count) return "";
  const open = statementPreviewState.heldOpen === true;
  const groups = new Map();
  held.forEach((h) => {
    const key = String(h.reason || "other");
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(h);
  });
  const body = Array.from(groups.entries()).map((entry) => {
    const meta = STATEMENT_HELD_REASONS[entry[0]] || { label: entry[0], hint: "" };
    const names = entry[1].map((h) => '<li class="stmt-held-name">' + statementsEscape(h.patientName || "Unknown patient")
      + (h.claimId ? '<span class="stmt-held-claim">' + statementsEscape(h.claimId) + "</span>" : "") + "</li>").join("");
    return `
      <div class="stmt-held-group">
        <div class="stmt-held-reason">
          <span class="stmt-held-reason-label">${statementsEscape(meta.label)}</span>
          <span class="stmt-held-count">${entry[1].length}</span>
        </div>
        ${meta.hint ? '<p class="stmt-held-hint">' + statementsEscape(meta.hint) + "</p>" : ""}
        <ul class="stmt-held-names">${names}</ul>
      </div>`;
  }).join("");
  return `
    <div class="stmt-held${open ? " is-open" : ""}">
      <button type="button" class="stmt-held-head" data-stmt-held-toggle aria-expanded="${open ? "true" : "false"}">
        <span class="stmt-held-icon">${STMT_ICONS.info}</span>
        <span class="stmt-held-headline"><strong>${count}</strong> service line${count === 1 ? "" : "s"} held pending eligibility</span>
        <span class="stmt-held-sub">Not billable to the patient yet &mdash; excluded from the totals above</span>
        <span class="stmt-held-more">${open ? "Hide" : "Review"}${STMT_ICONS.chevron}</span>
      </button>
      ${open ? '<div class="stmt-held-body">' + body + statementsEvidencePager(data.heldPage, "held") + "</div>" : ""}
    </div>`;
}

const STATEMENT_RECONCILIATION_REASONS = {
  "negative-patient-balance": { label: "Negative patient balance", hint: "This service line cannot offset another service line." },
  "zero-patient-balance": { label: "PR with no patient balance", hint: "PR 1/2/3 exists, but this service line currently shows no amount due from the patient." },
  "pr-balance-mismatch": { label: "Patient balance is below eligible PR", hint: "This service line's current patient balance is less than its signed PR 1/2/3 total." },
  "signed-pr-conflict": { label: "Adjustment stream does not reconcile", hint: "Positive and negative PR buckets cannot be presented safely without manual review." },
  "nonpositive-net-pr": { label: "PR adjustments net to zero or less", hint: "No positive patient amount can be printed from this service line." },
  "patient-balance-conflict": { label: "Conflicting patient balances", hint: "The source returned more than one balance for this service line." },
  "patient-balance-unavailable": { label: "Balance evidence unavailable", hint: "The exact service-line patient balance could not be proven." }
};

function statementsCanViewReconciliation() {
  const role = canonicalRole(state && state.role);
  return ["SuperAdmin", "StatesideBiller"].includes(role)
    && typeof hasPermission === "function"
    && hasPermission(PERMISSIONS.STATEMENTS_RECONCILIATION_VIEW);
}

function statementsReconciliationReview(data) {
  if (!statementsCanViewReconciliation()) return "";
  const rows = Array.isArray(data && data.internalReview) ? data.internalReview : [];
  const count = Number(data?.totals?.internalReviewCount || rows.length);
  if (!count) return "";
  const open = statementPreviewState.reconciliationOpen === true;
  const body = rows.map((row) => {
    const meta = STATEMENT_RECONCILIATION_REASONS[String(row.reason || "")] || { label: "Manual review required", hint: "This service line is excluded from patient statements." };
    const balance = row.patientBalance === null || row.patientBalance === undefined ? "Unknown" : statementsMoney(row.patientBalance);
    const difference = row.difference === null || row.difference === undefined ? "Unknown" : statementsMoney(row.difference);
    return `
      <div class="stmt-recon-row">
        <div class="stmt-recon-patient"><strong>${statementsEscape(row.patientName || "Unknown patient")}</strong><span>${statementsEscape(row.client || "")}</span></div>
        <div class="stmt-recon-service"><strong>${statementsEscape(row.serviceDate || "Date unavailable")}${row.procedureCode ? " · " + statementsEscape(row.procedureCode) : ""}</strong><span>Claim ${statementsEscape(row.claimId || "—")} · Service ${statementsEscape(row.serviceId || "—")}</span></div>
        <div class="stmt-recon-money"><span>Net PR 1/2/3</span><strong>${statementsMoney(row.prAmount || 0)}</strong></div>
        <div class="stmt-recon-money"><span>Patient balance</span><strong>${balance}</strong></div>
        <div class="stmt-recon-money"><span>Difference</span><strong>${difference}</strong></div>
        <div class="stmt-recon-reason"><strong>${statementsEscape(meta.label)}</strong><span>${statementsEscape(meta.hint)}</span></div>
      </div>`;
  }).join("");
  return `
    <section class="stmt-recon${open ? " is-open" : ""}" aria-label="Adjustment review">
      <button type="button" class="stmt-recon-head" data-stmt-reconciliation-toggle aria-expanded="${open ? "true" : "false"}">
        <span class="stmt-held-icon">${STMT_ICONS.info}</span>
        <span class="stmt-recon-title"><strong>Adjustment review</strong><span>${count} service line${count === 1 ? "" : "s"}</span></span>
        <span class="stmt-recon-sub">Internal TMS review only · excluded from patient statements and totals</span>
        <span class="stmt-held-more">${open ? "Hide" : "Review"}${STMT_ICONS.chevron}</span>
      </button>
      ${open ? '<div class="stmt-recon-body">' + body + statementsEvidencePager(data.internalReviewPage, "review") + "</div>" : ""}
    </section>`;
}

function statementsSelectionDock(data) {
  const sum = statementsSelectionSummary(data);
  const status = statementActionState.message
    ? '<span class="stmt-dock-msg' + (statementActionState.error ? " is-error" : "") + '" role="status">' + statementsEscape(statementActionState.message) + "</span>"
    : "";
  // Nothing selected and nothing to say => no dock at all. The old bar sat on
  // screen permanently with three dead buttons; this is the main clutter fix.
  if (!sum.lines) {
    return status ? '<div class="stmt-dock-wrap"><div class="stmt-dock stmt-dock--msg">' + status + "</div></div>" : "";
  }
  if (data && data.truncated) {
    return '<div class="stmt-dock-wrap"><div class="stmt-dock stmt-dock--msg" role="status">Actions paused: this preview reached its source row limit. Narrow the service dates before printing or recording a payment.</div></div>';
  }
  const canMark = typeof hasPermission === "function" && hasPermission(PERMISSIONS.STATEMENTS_GENERATE);
  const busy = statementActionState.busy ? " disabled" : "";
  return `
    <div class="stmt-dock-wrap">
      <div class="stmt-dock" role="region" aria-label="Selected statement lines">
        <span class="stmt-dock-count">
          <strong>${statementsMoney(sum.amount)}</strong>
          <span class="stmt-dock-sub">${sum.lines} line${sum.lines === 1 ? "" : "s"} &middot; ${sum.patients} patient${sum.patients === 1 ? "" : "s"} selected</span>
        </span>
        ${status}
        <span class="stmt-dock-actions">
          <button type="button" class="stmt-btn stmt-btn-ghost" data-stmt-clear>Clear</button>
          ${canMark ? '<button type="button" class="stmt-btn stmt-btn-secondary" data-stmt-mark' + busy + ">Mark printed</button>" : ""}
          ${canMark && sum.printed ? '<button type="button" class="stmt-btn stmt-btn-ghost" data-stmt-unmark-printed' + busy + ' title="Removes one print from each selected printed line">Undo last print</button>' : ""}
          ${canMark ? '<button type="button" class="stmt-btn stmt-btn-secondary" data-stmt-pay' + busy + ">Mark paid</button>" : ""}
          ${canMark && sum.paid ? '<button type="button" class="stmt-btn stmt-btn-ghost" data-stmt-unmark-paid' + busy + ">Unmark paid</button>" : ""}
          ${statementPayState.lastReceipt ? '<button type="button" class="stmt-btn stmt-btn-ghost" data-stmt-receipt>Print receipt</button>' : ""}
          ${canMark ? `<button type="button" class="stmt-btn stmt-btn-primary" data-stmt-print${busy}>${STMT_ICONS.print}<span>Print ${sum.lines} line${sum.lines === 1 ? "" : "s"}</span></button>` : ""}
        </span>
      </div>
    </div>`;
}

function statementsNotice(title, body, action) {
  return '<div class="stmt-notice"><p class="stmt-notice-title">' + title + '</p><p class="stmt-notice-body">' + body + "</p>" + (action || "") + "</div>";
}

function statementsTruncatedNotice(data) {
  if (!data || !data.truncated) return "";
  const limit = Number(data.rowLimit || 0);
  return statementsNotice(
    "This statement list is incomplete",
    "The source returned more than " + (limit ? limit.toLocaleString() : "the allowed number of") + " rows. Totals below cover only the visible slice, so print and payment actions are paused. Narrow the service dates and reload.",
    ""
  );
}

function statementsSkeleton() {
  const row = '<div class="stmt-skel-row"><span class="stmt-skel stmt-skel--check"></span><span class="stmt-skel stmt-skel--name"></span><span class="stmt-skel stmt-skel--amt"></span></div>';
  let rows = "";
  for (let i = 0; i < 6; i += 1) rows += row;
  // The prepared read now seeks to the selected company: about a second warm,
  // a few seconds cold. One static line says what is loading and roughly how
  // long — no timer, no interval, no state to keep in sync.
  const note = '<p class="stmt-skeleton-note">Loading patient balances. This usually takes a few seconds.</p>';
  return '<div class="stmt-skeleton" role="status" aria-label="Loading patient responsibility">' + note + rows + "</div>";
}

function statementsHiddenFilterBook(patients) {
  const all = Array.isArray(patients) ? patients : [];
  if (!all.length) return null;
  const visible = all.filter(statementsPatientMatches);
  if (visible.length) return null;
  const owed = all.reduce((sum, patient) => sum + Number(patient.openAmount || 0), 0);
  return { patients: all.length, owed };
}

function statementsEmptyRow(patients) {
  const hidden = statementsHiddenFilterBook(patients);
  const moneyLine = hidden && hidden.owed > 0
    ? ("Filters are hiding " + statementsMoney(hidden.owed) + " across " + hidden.patients + " patient" + (hidden.patients === 1 ? "" : "s") + " still on this date range. This is not $0. ")
    : "";
  return '<div class="stmt-empty">'
    + '<p class="stmt-empty-title">No patients match your filters</p>'
    + '<p class="stmt-empty-sub">' + moneyLine + "Clear the search box, set Show back to All, or choose All insurances.</p>"
    + '<button type="button" class="stmt-btn stmt-btn-secondary stmt-btn-sm" data-stmt-reset-filters>Clear filters</button>'
    + "</div>";
}

function statementsSelectionSummary(data) {
  const all = statementsAllLines(data);
  const sel = all.filter((x) => statementSelection.keys.has(x.key));
  const patients = new Set(sel.map((x) => String(x.p.orgId) + "::" + String(x.p.patientRef || x.p.patientName || "").toLowerCase()));
  const amount = sel.reduce((s, x) => s + Number(x.line.outstanding != null ? x.line.outstanding : x.line.amount || 0), 0);
  return {
    lines: sel.length,
    patients: patients.size,
    amount: Math.round(amount * 100) / 100,
    totalLines: all.length,
    printed: sel.filter((x) => x.line.alreadyPrinted).length,
    paid: sel.filter((x) => Number(x.line.patientPaid || 0) > 0).length
  };
}

function statementsSelectedLines(data) {
  return statementsAllLines(data).filter((x) => statementSelection.keys.has(x.key)).map((x) => ({
    claimId: x.svc.claimId,
    serviceId: x.svc.serviceId,
    patientRef: x.p.patientRef || x.p.patientName,
    patientName: x.p.patientName,
    serviceDate: x.svc.serviceDate,
    procedureCode: x.svc.procedureCode,
    prReason: x.line.prReason,
    prAmount: x.line.amount,
    patientPaid: x.line.patientPaid,
    paymentIds: Array.isArray(x.line.paymentIds) ? x.line.paymentIds.slice() : [],
    alreadyPrinted: x.line.alreadyPrinted === true,
    outstanding: x.line.outstanding != null ? x.line.outstanding : x.line.amount,
    orgId: x.p.orgId,
    tenantId: x.p.orgId
  }));
}

function statementsHumanDate(value) {
  try {
    const raw = String(value == null ? "" : value);
    const d = (value instanceof Date) ? value : new Date(raw + (raw.length === 10 ? "T00:00:00" : ""));
    if (isNaN(d.getTime())) return raw;
    // US patients receive these; never the viewer's own locale format.
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch (_e) { return String(value == null ? "" : value); }
}

// The Eastern business day, so a biller whose clock is already tomorrow (or
// still yesterday) never dates a statement differently from the server.
function statementsStatementDate() {
  const eastern = typeof easternDateOnlyString === "function" ? easternDateOnlyString() : "";
  return statementsHumanDate(eastern || new Date());
}

// Deterministic short statement number. Derives only from its inputs so reprints
// of the same (org, patient, date) always produce the same number (no Date.now/random).
function statementsShortHash(str) {
  let h = 5381;
  const t = String(str == null ? "" : str);
  for (let i = 0; i < t.length; i++) { h = ((h << 5) + h + t.charCodeAt(i)) >>> 0; }
  return (h >>> 0).toString(36).toUpperCase().slice(0, 6).padStart(6, "0");
}
// Same value printed as "Reference #:" on the sheet. Used for the Save-as-PDF
// filename so reprints of the same statement keep the same name.
// Keyed on the stable patient identity for the same reason the print grouping is:
// two same-name patients must not share a reference number.
function statementsReferenceNumber(orgId, patientIdentity, stmtDate) {
  const date = stmtDate || statementsStatementDate();
  const hash = statementsShortHash(String(orgId == null ? "" : orgId) + "|" + String(patientIdentity || "") + "|" + date);
  const ymd = statementsYmd(date);
  return ymd ? (ymd + "-" + hash) : hash;
}
function statementsClientLabel(patient) {
  const orgId = String(patient && patient.orgId != null ? patient.orgId : "");
  const cached = statementBrandingForOrg(orgId);
  const branded = cached && (cached.displayName || (cached.branding && cached.branding.displayName));
  return String(branded || (patient && (patient.client || patient.orgId)) || "Statement").trim();
}
function statementsSafePrintTitle(parts) {
  const raw = (Array.isArray(parts) ? parts : [parts])
    .map((part) => String(part == null ? "" : part).trim())
    .filter(Boolean)
    .join(" ");
  return raw
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\.+$/g, "")
    .slice(0, 150) || "Patient Statement";
}
function statementsSelectedPatients(data) {
  const seen = new Set();
  const patients = [];
  statementsAllLines(data).filter((x) => statementSelection.keys.has(x.key)).forEach((x) => {
    const key = statementsPatientKey(x.p);
    if (seen.has(key)) return;
    seen.add(key);
    patients.push(x.p);
  });
  return patients;
}
// Chrome / Edge "Save as PDF" uses document.title as the suggested filename.
// Without this every statement downloaded as "TMS Claim Insight Portal.pdf"
// and overwrote the last one.
function statementsPrintFilename(data) {
  const patients = statementsSelectedPatients(data);
  const stmtDate = statementsStatementDate();
  if (patients.length === 1) {
    const patient = patients[0];
    return statementsSafePrintTitle([
      statementsClientLabel(patient),
      patient.patientName,
      statementsReferenceNumber(patient.orgId, patient.patientRef || patient.patientName, stmtDate)
    ]);
  }
  const clients = Array.from(new Set(patients.map((patient) => statementsClientLabel(patient))));
  const ymd = statementsYmd(stmtDate);
  if (clients.length === 1) {
    return statementsSafePrintTitle([clients[0], patients.length + "-statements", ymd]);
  }
  return statementsSafePrintTitle([patients.length + "-statements", ymd]);
}
function statementsReceiptPrintFilename(receipt) {
  const pay = (receipt && receipt.payment) || {};
  const data = (typeof statementPreviewState === "object" && statementPreviewState && statementPreviewState.data) || { patients: [] };
  const match = (data.patients || []).find((row) =>
    String(row.patientRef || row.patientName || "").toLowerCase() === String(pay.patientRef || "").toLowerCase()
  );
  return statementsSafePrintTitle([
    (receipt && receipt.clientLabel) || (match ? statementsClientLabel(match) : (pay.orgName || "")),
    statementsReceiptPatientName(receipt),
    "receipt",
    pay.paymentId || pay.paidOnDate || statementsYmd(new Date())
  ]);
}
let statementsPrintTitleRestore = null;
function statementsBeginPrintDocument(title) {
  if (typeof document === "undefined") return;
  if (statementsPrintTitleRestore == null) statementsPrintTitleRestore = document.title;
  document.title = statementsSafePrintTitle(title);
}
function statementsEndPrintDocument() {
  if (typeof document === "undefined") return;
  if (statementsPrintTitleRestore == null) return;
  document.title = statementsPrintTitleRestore;
  statementsPrintTitleRestore = null;
}
// Best-effort YYYYMMDD from a date-ish string (display or ISO); "" if unparseable.
function statementsYmd(value) {
  try {
    const raw = String(value == null ? "" : value);
    const d = (value instanceof Date) ? value : new Date(raw + (raw.length === 10 ? "T00:00:00" : ""));
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear(); const m = d.getMonth() + 1; const day = d.getDate();
    return String(y) + String(m).padStart(2, "0") + String(day).padStart(2, "0");
  } catch (_e) { return ""; }
}
// A fixed due date typed into Branding once would otherwise keep printing on
// every later statement after it has passed. A date before today (Eastern) is
// not printed; the payment terms are used instead.
function statementsCurrentDueDate(text) {
  const raw = String(text == null ? "" : text).trim();
  if (!raw) return "";
  if (!/\d{4}/.test(raw)) return raw;
  const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw + "T00:00:00" : raw);
  if (!Number.isFinite(parsed.getTime())) return raw;
  const day = parsed.getFullYear() + "-" + String(parsed.getMonth() + 1).padStart(2, "0") + "-" + String(parsed.getDate()).padStart(2, "0");
  const today = typeof easternDateOnlyString === "function" ? easternDateOnlyString() : new Date().toISOString().slice(0, 10);
  return day < today ? "" : raw;
}
function statementsPrintableHtml(data) {
  const selected = statementsAllLines(data).filter((x) => statementSelection.keys.has(x.key));
  const groups = new Map();
  selected.forEach((x) => {
    // Group by the STABLE patient identity, never the display name. Two people
    // in one practice can share a name (backend/statementSource.js:514 keys them
    // apart by ClaPatFID for exactly this reason); grouping by name merged their
    // service lines onto a single statement addressed to whichever responsible
    // party happened to be seen first. statementsPatientKey is the same key the
    // on-screen list, the selection dock and the print confirmation already use.
    const gk = statementsPatientKey(x.p);
    if (!groups.has(gk)) groups.set(gk, { patient: x.p, items: [] });
    groups.get(gk).items.push(x);
  });
  const stmtDate = statementsStatementDate();
  return Array.from(groups.values()).map((grp) => {
    const p = grp.patient;
    const b = statementBrandingForOrg(p.orgId);
    const br = (b && b.branding) ? b.branding : (b || {});
    const providerName = b && b.displayName ? b.displayName : (p.client || p.orgId || "Your Provider");
    const provider = statementsEscape(providerName);
    const items = grp.items.slice().sort((a, b) => String(a.svc.serviceDate || "").localeCompare(String(b.svc.serviceDate || "")));
    if (items.length === 0) return ""; // FIX1
    const _term = (br.paymentTerms && br.paymentTerms.trim()) ? br.paymentTerms.trim() : "Upon receipt";
    let _termClean = _term.replace(/^(payment\s+)?due[:\s]+/i, "").trim() || _term;
    _termClean = _termClean ? _termClean.charAt(0).toUpperCase() + _termClean.slice(1) : _termClean;
    // "How to pay" is built from the client's own list of payment options, in
    // their order, showing only what they enabled. Practices differ - some take
    // card online, some only cheques by post, some only over the phone - so this
    // is a list rather than a fixed set of fields, and "other" can repeat with a
    // custom label for anything not covered.
    const payOpts = Array.isArray(br.paymentOptions) ? br.paymentOptions.filter((o) => o && o.enabled !== false) : [];
    const remitNameResolved = (br.remitName && String(br.remitName).trim()) ? String(br.remitName).trim() : providerName;
    const remitSourceLines = (Array.isArray(br.remitAddressLines) && br.remitAddressLines.length) ? br.remitAddressLines : (Array.isArray(br.addressLines) ? br.addressLines : []);
    const remitLines = remitSourceLines.map((l) => String(l == null ? "" : l).trim()).filter(Boolean).map((l) => `<div>${statementsEscape(l)}</div>`).join("");
    const payOptionHtml = (opt) => {
      const label = statementsEscape(String(opt.label || "").trim() || "Pay");
      const detail = String(opt.detail == null ? "" : opt.detail).trim();
      const detailLines = detail.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
        .map((l) => `<div class="ps-payopt-detail">${statementsEscape(l)}</div>`).join("");
      if (opt.kind === "mail") {
        // The mail option carries the remit-to block rather than a free string:
        // a cheque needs a payee and an address, not a sentence.
        return `<div class="ps-payopt"><div class="ps-payopt-label">${label}</div>`
          + `<div class="ps-payopt-detail">Make checks payable to: ${statementsEscape(remitNameResolved)}</div>`
          + remitLines.replace(/<div>/g, '<div class="ps-payopt-detail">')
          + detailLines
          + `</div>`;
      }
      return `<div class="ps-payopt"><div class="ps-payopt-label">${label}</div>${detailLines}</div>`;
    };
    // Nothing configured: say who to contact rather than printing an empty box.
    const payFallback = (br.phone && String(br.phone).trim())
      ? `<div class="ps-payopt-detail">To pay or ask about this bill, call ${statementsEscape(String(br.phone).trim())}.</div>`
      : `<div class="ps-payopt-detail">Please contact your provider's billing office to pay.</div>`;
    const payBody = payOpts.length ? payOpts.map(payOptionHtml).join("") : payFallback;
    const payInfo = `<div class="ps-payinfo"><div class="ps-payinfo-head">How to pay</div><div class="ps-payopts">${payBody}</div></div>`;
    // Legacy remit block is now folded into the mail option above; keep the
    // variable so the tail assembly below is untouched.
    const remit = "";
    // Address on its own lines, contact details beneath - the conventional
    // letterhead stack. Joining all of it into one middot-separated run wrapped
    // mid-list and left a dangling separator at the end of the line.
    const addressLines = (br.addressLines || [])
      .map((ln) => String(ln == null ? "" : ln).trim())
      .filter(Boolean)
      .map((t) => `<div class="ps-lh-addr">${statementsEscape(t)}</div>`)
      .join("");
    const reachParts = [];
    if (br.phone) reachParts.push(statementsEscape(br.phone));
    if (br.email) reachParts.push(statementsEscape(br.email));
    const contactLine = addressLines + (reachParts.length ? `<div class="ps-lh-reach">${reachParts.join(" &middot; ")}</div>` : "");
    const statementDate = statementsEscape(stmtDate);
    const patientName = p.patientName || "";
    // Mailing panel for a #10 double-window envelope. The backend supplies the
    // claim insured (sequence 1) - the subscriber, i.e. the party actually
    // responsible for the bill. EZClaim has no patient-address table, and for a
    // paediatric practice the subscriber is the parent, which is who the
    // statement should be addressed to regardless.
    const mt = p.mailTo || null;
    const mailName = mt ? [mt.firstName, mt.lastName].filter(Boolean).join(" ").trim() : "";
    // A nine-digit ZIP stored without its hyphen prints as ZIP+4.
    const mailZip = mt ? (/^[0-9]{9}$/.test(String(mt.zip || "").trim()) ? String(mt.zip).trim().slice(0, 5) + "-" + String(mt.zip).trim().slice(5) : String(mt.zip || "").trim()) : "";
    const mailCsz = mt ? [mt.city, mt.state].filter(Boolean).join(", ") + (mailZip ? " " + mailZip : "") : "";
    // Relation 1 = self; anything else means we are writing to someone other than
    // the patient, so name the patient or the reader cannot tell whose bill it is.
    const mailIsSelf = !mt || String(mt.relation || "") === "1";
    const mailBlock = (mt && mt.address && mailName)
      ? `<div class="ps-mailto">`
        + `<div class="ps-mailto-name">${statementsEscape(mailName)}</div>`
        + `<div class="ps-mailto-line">${statementsEscape(mt.address)}</div>`
        + (mailCsz.trim() ? `<div class="ps-mailto-line">${statementsEscape(mailCsz.trim())}</div>` : "")
        + (mailIsSelf ? "" : `<div class="ps-mailto-for">Statement for ${statementsEscape(patientName)}</div>`)
        + `</div>`
      : `<div class="ps-mailto ps-mailto-missing"><div class="ps-mailto-name">${statementsEscape(patientName)}</div><div class="ps-mailto-line">No mailing address on file</div></div>`;
    const refNumber = statementsReferenceNumber(p.orgId, p.patientRef || patientName, stmtDate);
    const refLine = "Reference #: " + statementsEscape(refNumber);
    const _npi = String(br.npi == null ? "" : br.npi).trim();
    const _taxId = String(br.taxId == null ? "" : br.taxId).trim();
    const npiTaxLine = (_npi || _taxId) ? `<div class="ps-lh-sub">${_npi ? "NPI " + statementsEscape(_npi) : ""}${_npi && _taxId ? " &middot; " : ""}${_taxId ? "Tax ID " + statementsEscape(_taxId) : ""}</div>` : "";
    const PER_PAGE = 14;
    const pages = [];
    for (let _i = 0; _i < items.length; _i += PER_PAGE) pages.push(items.slice(_i, _i + PER_PAGE));
    const pageCount = pages.length;
    const lineDue = (x) => Number(x.line.outstanding != null ? x.line.outstanding : x.line.amount || 0);
    const grandTotal = items.reduce((s, x) => s + lineDue(x), 0);
    const alreadyPaid = items.reduce((s, x) => s + Number(x.line.patientPaid || 0), 0);
    let runningThroughThisPage = 0;
    const tail = `<div class="ps-tail">`
      + (alreadyPaid > 0 ? `<div class="ps-broughtfwd"><span class="ps-broughtfwd-label">Already marked paid</span><span class="ps-broughtfwd-amt">${statementsMoney(alreadyPaid)}</span></div>` : "")
      + `<div class="ps-total"><span>Total amount due</span><span class="ps-total-amt">${statementsMoney(grandTotal)}</span></div>`
      + payInfo
      + remit
      + (br && br.footerMessage ? `<p class="print-note">${statementsEscape(br.footerMessage)}</p>` : `<p class="print-note">Please pay the total amount due. If you have questions about this statement, please contact ${provider}\u0027s billing office.</p>`)
      + `<div class="ps-insurance-note">This statement shows only your portion after your insurance has been applied.</div>`
      + `<div class="ps-legend">Deductible, copay, and coinsurance are the standard portions your insurance plan leaves to you.</div>`
      + `</div>`;
    return pages.map((pageItems, pi) => {
      const broughtForward = runningThroughThisPage; // sum of all rows on prior pages
      // logoIncludesName suppresses the text name on its own, with or without a
      // logo. It used to require a logo, which made ticking the box do nothing
      // and made the editor's live preview show a name the owner had just told
      // us to remove. The editor warns when it is ticked with no logo uploaded.
      const letterhead = `<div class="ps-letterhead">`
        + `<div class="ps-lh-brand">`
        + (br.logoDataUrl ? `<img class="ps-lh-logo" src="${statementsEscape(br.logoDataUrl)}" alt="">` : "")
        + `<div class="ps-lh-id">${br.logoIncludesName ? "" : `<div class="ps-lh-name${br.logoDataUrl ? ' ps-lh-name-haslogo' : ''}">${provider}</div>`}${contactLine ? `<div class="ps-lh-contact">${contactLine}</div>` : ""}</div>`
        + `</div>`
        + `<div class="ps-lh-meta">`
        + `<div class="ps-lh-title">Patient Statement</div>`
        + `<div class="ps-lh-due">Amount due <span class="ps-lh-due-amt">${statementsMoney(grandTotal)}</span></div>`
        + `<div class="ps-lh-sub">Statement date: ${statementDate}</div>`
        + `<div class="ps-lh-sub">Payment due: ${statementsCurrentDueDate(br.dueDate) ? statementsEscape(statementsCurrentDueDate(br.dueDate)) : statementsEscape(_termClean)}</div>`
        + (refLine ? `<div class="ps-lh-sub">${refLine}</div>` : "")
        + npiTaxLine
        + (pageCount > 1 ? `<div class="ps-pageno">Page ${pi + 1} of ${pageCount}</div>` : "")
        + `</div>`
        + `</div>`
        ;
      // Deliberately NOT part of the letterhead: it would land in the envelope
      // window band and show through alongside the address.
      const patientLine = `<div class="ps-lh-patient"><span class="ps-lh-patient-label">Patient</span> <span class="ps-lh-patient-name">${statementsEscape(patientName)}</span></div>`;
      const rows = pageItems.map((x) => { const _desc = String(x.line.label == null ? "" : x.line.label).replace(/\s*\((?:PR|CO|OA|PI|CR)[-\s]?\d+\)\s*$/i, "").trim(); const due = lineDue(x); const paid = Number(x.line.patientPaid || 0); return `<tr data-statement-amount="${due}"><td>${statementsEscape(statementsHumanDate(x.svc.serviceDate))}</td><td>${(function(){ const cpt = String(x.svc.procedureCode || ""); const plain = STATEMENT_CPT_LABELS[cpt]; return plain ? `<div>${statementsEscape(plain)}</div><div class="ps-proc-code">${statementsEscape(cpt)}</div>` : statementsEscape(cpt); })()}</td><td>${statementsEscape(_desc)}${paid > 0 ? ' <span class="ps-proc-code">paid ' + statementsMoney(paid) + "</span>" : ""}</td><td class="amt">${statementsMoney(due)}</td></tr>`; }).join("");
      runningThroughThisPage += pageItems.reduce((s, x) => s + lineDue(x), 0);
      const isFinal = pi === pageCount - 1;
      const footer = isFinal
        ? tail
        : `<div class="ps-carryfwd"><span class="ps-carryfwd-label">Subtotal carried forward</span><span class="ps-carryfwd-amt">${statementsMoney(runningThroughThisPage)}</span></div><div class="ps-continued">Continued on page ${pi + 2} of ${pageCount} &rarr;</div>`;
      const broughtFwdLine = pi > 0
        ? `<div class="ps-broughtfwd"><span class="ps-broughtfwd-label">Balance brought forward</span><span class="ps-broughtfwd-amt">${statementsMoney(broughtForward)}</span></div>`
        : "";
      // Page 1 carries the mailing panel and the fold guides; continuation pages
      // do not (only the first page faces the envelope window).
      const mailZone = pi === 0
        ? `<div class="ps-mailzone">${mailBlock}</div>`
        : "";
      const foldMarks = `<div class="ps-fold ps-fold-1" aria-hidden="true"></div><div class="ps-fold ps-fold-2" aria-hidden="true"></div>`;
      return `<section class="print-statement${pi === 0 ? " ps-page-first" : ""}">`
        + foldMarks
        + letterhead
        + mailZone
        + patientLine
        + broughtFwdLine
        + `<table class="print-table"><thead><tr><th>Date of service</th><th>Procedure</th><th>Your responsibility</th><th class="amt">Amount</th></tr></thead><tbody>${rows}</tbody></table>`
        + footer
        + `</section>`;
    }).join("");
  }).join("");
}

// A large print can still become a large durable ledger write after the user
// confirms the print completed. Live data carries 125 patients / 4,737 lines in
// a single window and the default selection covers all of them, which puts a
// 125-patient run one confirmation away.
// Matches the bulk-assignment confirm idiom at app.js:38593.
const STATEMENT_BULK_PRINT_CONFIRM_PATIENTS = 10;

// Clients in the selection that would print with default branding. Their
// statements carry no address, no phone and no pay instructions, so the patient
// is handed a bill with literally no way to pay it - worth stopping for.
function statementsUnbrandedClientsInSelection(data) {
  const seen = new Map();
  statementsAllLines(data).forEach((x) => {
    if (!statementSelection.keys.has(x.key)) return;
    const orgId = String(x.p.orgId || "");
    if (seen.has(orgId) || statementsOrgIsBranded(orgId)) return;
    seen.set(orgId, String(x.p.client || x.p.orgId || "this client"));
  });
  return Array.from(seen.values());
}

function statementsConfirmPrintRun(data) {
  if (typeof window === "undefined" || typeof window.confirm !== "function") return true;
  const unbranded = statementsUnbrandedClientsInSelection(data);
  if (unbranded.length) {
    const names = unbranded.slice(0, 4).join(", ") + (unbranded.length > 4 ? " and " + (unbranded.length - 4) + " more" : "");
    const ok = window.confirm(
      "Print without billing details for " + names + "?\n\n" +
      "These statements will show no address, no phone number and no payment instructions, so the patient has no way to pay them.\n\n" +
      "Set the details up first with Branding in the toolbar."
    );
    if (!ok) return false;
  }
  return statementsConfirmLargeRun(data);
}

function statementsConfirmLargeRun(data) {
  const sum = statementsSelectionSummary(data);
  if (sum.patients < STATEMENT_BULK_PRINT_CONFIRM_PATIENTS) return true;
  if (typeof window === "undefined" || typeof window.confirm !== "function") return true;
  return window.confirm(
    "Print statements for " + sum.patients + " patients?\n\n" +
    sum.lines + " line" + (sum.lines === 1 ? "" : "s") + " totalling " + statementsMoney(sum.amount) + ".\n\n" +
    "After the print dialog closes, confirm whether printing finished before anything is marked printed. A mistaken printed mark can be reversed later with a required reason; the print history remains in the audit ledger."
  );
}

async function statementsFitPrintPages(area, onProgress) {
  // Measure the exact print rules at US Letter width. Fixed row counts cannot
  // account for wrapped procedure names, branding, or payment instructions.
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  const rules = [];
  for (const sheet of Array.from(document.styleSheets || [])) {
    let sheetRules;
    try { sheetRules = sheet.cssRules; } catch (_e) { continue; }
    for (const rule of Array.from(sheetRules || [])) {
      if (!rule.media || !String(rule.media.mediaText || "").includes("print")) continue;
      for (const printRule of Array.from(rule.cssRules || [])) {
        if (String(printRule.selectorText || "").includes("#statementsPrintArea")) {
          rules.push(printRule.cssText.split("#statementsPrintArea").join("#statementsPrintMeasureArea"));
        }
      }
    }
  }
  if (!rules.length) throw new Error("Statement print styles could not be loaded. Reload before printing.");
  const style = document.createElement("style");
  style.textContent = rules.join("\n");
  const measure = document.createElement("div");
  measure.id = "statementsPrintMeasureArea";
  // contain:strict with a fixed box makes the measuring area its own layout root:
  // measuring a page no longer re-lays-out the whole portal behind it (93% of a
  // 3,200-line run was spent there). Only one page is inside at a time, and a
  // page is at most 1,056px tall, so 2,400px leaves room for an overflowing one.
  measure.style.cssText = "display:block!important;position:fixed;left:-10000px;top:0;width:816px;height:2400px;overflow:hidden;contain:strict;visibility:hidden;pointer-events:none";
  measure.innerHTML = area.innerHTML;
  document.head.appendChild(style);
  document.body.appendChild(measure);
  const maxHeight = 1024; // 11in at 96px/in, with 32px spare below each sheet.
  // Name the statement that does not fit: in a large run the reader otherwise
  // cannot tell which patient's address or service line to fix.
  let currentPatient = "";
  const fail = () => { throw new Error((currentPatient ? "The statement for " + currentPatient + " does not fit a printed page: its " : "Statement ") + "billing details, mailing address, or a service description are too long. Review Branding and the mailing address before printing."); };
  try {
    await Promise.all(Array.from(measure.querySelectorAll("img")).map((img) => typeof img.decode === "function" ? img.decode() : Promise.resolve()));
    const groups = [];
    for (const section of Array.from(measure.querySelectorAll(".print-statement"))) {
      if (section.classList.contains("ps-page-first") || !groups.length) groups.push([]);
      groups[groups.length - 1].push(section);
    }
    measure.innerHTML = "";
    // Only the page being filled stays in the measuring box. Finished pages are
    // set aside: leaving them in made every row re-lay-out every earlier page
    // (a 3,200-line run took 16-26 s and froze the tab).
    const finished = [];
    const totalRows = measure.querySelectorAll(".print-table tbody tr").length || groups.reduce((sum, group) => sum + group.reduce((n, section) => n + section.querySelectorAll(".print-table tbody tr").length, 0), 0);
    let laidOut = 0;
    for (const original of groups) {
      const first = original[0];
      currentPatient = String((first.querySelector(".ps-lh-patient-name") || {}).textContent || "").trim();
      const rows = original.flatMap((section) => Array.from(section.querySelectorAll(".print-table tbody tr")));
      const tail = original[original.length - 1].querySelector(".ps-tail");
      if (!rows.length || !tail) fail();
      const amount = (row) => {
        const value = Number(row.getAttribute("data-statement-amount"));
        if (!row.hasAttribute("data-statement-amount") || !Number.isFinite(value)) fail();
        return value;
      };
      const sum = (items) => items.reduce((total, row) => total + amount(row), 0);
      const pages = [];
      let consumed = 0;
      const makePage = () => {
        const page = first.cloneNode(true);
        page.querySelector(".print-table tbody").replaceChildren();
        page.querySelectorAll(".ps-tail,.ps-carryfwd,.ps-continued,.ps-broughtfwd").forEach((node) => node.remove());
        if (pages.length) {
          page.classList.remove("ps-page-first");
          page.querySelectorAll(".ps-mailzone").forEach((node) => node.remove());
          const brought = document.createElement("div");
          brought.className = "ps-broughtfwd";
          brought.innerHTML = '<span class="ps-broughtfwd-label">Balance brought forward</span><span class="ps-broughtfwd-amt">' + statementsMoney(sum(rows.slice(0, consumed))) + '</span>';
          page.insertBefore(brought, page.querySelector(".print-table"));
        }
        let number = page.querySelector(".ps-pageno");
        if (!number) { number = document.createElement("div"); number.className = "ps-pageno"; page.querySelector(".ps-lh-meta").appendChild(number); }
        number.textContent = "Page 999 of 999"; // Reserve the same header height before packing.
        if (pages.length) pages[pages.length - 1].remove();
        measure.appendChild(page);
        pages.push(page);
        return page;
      };
      const footer = (page, final) => {
        page.querySelectorAll(".ps-tail,.ps-carryfwd,.ps-continued").forEach((node) => node.remove());
        if (final) page.appendChild(tail.cloneNode(true));
        else {
          const carry = document.createElement("div");
          carry.className = "ps-carryfwd";
          carry.innerHTML = '<span class="ps-carryfwd-label">Subtotal carried forward</span><span class="ps-carryfwd-amt">' + statementsMoney(sum(rows.slice(0, consumed))) + '</span>';
          const continued = document.createElement("div"); continued.className = "ps-continued"; continued.textContent = "Continued on page 999 of 999 →";
          page.append(carry, continued);
        }
      };
      // Sideways overflow is checked once per finished page (overflowFree): a
      // row that is too wide never fits on any page, so scanning every node
      // after every row only cost time - 16-19 s for a 3,200-line run.
      const overflowFree = (page) => !Array.from(page.querySelectorAll("*")).some((node) => node.clientWidth > 0 && node.scrollWidth > node.clientWidth + 1);
      const fits = (page) => {
        if (page.getBoundingClientRect().height > maxHeight) return false;
        const mail = page.querySelector(".ps-mailto");
        if (mail) {
          if (mail.scrollHeight > mail.clientHeight + 1) return false;
          const box = mail.getBoundingClientRect();
          if (page.querySelector(".ps-letterhead").getBoundingClientRect().bottom + 4 > box.top || box.bottom + 4 > page.querySelector(".ps-lh-patient").getBoundingClientRect().top) return false;
        }
        return true;
      };
      let page = makePage();
      while (consumed < rows.length) {
        // Let the browser breathe on long runs so the tab never looks frozen.
        // Counted across the whole run, so many short statements pause too.
        if (laidOut && laidOut % 200 === 0) {
          if (typeof onProgress === "function") onProgress(laidOut, totalRows);
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
        laidOut += 1;
        const body = page.querySelector(".print-table tbody");
        body.appendChild(rows[consumed]);
        consumed += 1;
        footer(page, consumed === rows.length);
        if (fits(page)) continue;
        const last = body.lastElementChild;
        if (body.children.length > 1) {
          last.remove(); consumed -= 1; footer(page, false);
          if (!fits(page) || !overflowFree(page)) fail();
          page = makePage();
          continue;
        }
        // A long payment block may need a separate, clearly numbered summary
        // page. Keep the service row on its own page; never omit any text/row.
        if (consumed === rows.length) {
          footer(page, false);
          if (!fits(page) || !overflowFree(page)) fail();
          page = makePage(); page.querySelector(".print-table").remove(); footer(page, true);
          if (!fits(page)) fail();
          break;
        }
        fail();
      }
      if (!overflowFree(page)) fail();
      pages.forEach((page, index) => {
        page.querySelector(".ps-pageno").textContent = "Page " + (index + 1) + " of " + pages.length;
        const continued = page.querySelector(".ps-continued");
        if (continued) continued.textContent = "Continued on page " + (index + 2) + " of " + pages.length + " →";
      });
      if (pages.reduce((count, page) => count + page.querySelectorAll(".print-table tbody tr").length, 0) !== rows.length) fail();
      pages.forEach((done) => finished.push(done));
      pages[pages.length - 1].remove();
    }
    area.innerHTML = finished.map((done) => done.outerHTML).join("");
  } finally { measure.remove(); style.remove(); }
}

async function printSelectedStatements(requestedKeys) {
  if (statementActionState.busy) return;
  if (typeof hasPermission === "function" && !hasPermission(PERMISSIONS.STATEMENTS_GENERATE)) return;
  const printedKeys = Array.isArray(requestedKeys) ? requestedKeys.slice() : Array.from(statementSelection.keys);
  if (!printedKeys.length) { statementActionState.message = "Select at least one line to print."; statementActionState.error = true; renderStatements(); return; }
  // An unanswered "Did printing finish?" belongs to paper that may already be
  // in envelopes. A new print used to replace it, so that earlier run was never
  // marked printed and came back as Not printed. Answer it first.
  const unansweredPrint = typeof document !== "undefined" ? document.getElementById("stmtPrintPromptToast") : null;
  if (unansweredPrint && unansweredPrint.id === "stmtPrintPromptToast") {
    statementActionState.message = "First answer \"Did printing finish?\" for the previous print, so it is marked correctly.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const key = statementPreviewKey();
  const token = trackerApiToken();
  const current = () => statementPreviewKey() === key && trackerApiToken() === token && state.view === "statements";
  statementActionState.busy = true;
  statementActionState.error = false;
  statementActionState.message = "Checking current statement balances…";
  let savedSelection;
  let changedAmounts = 0;
  try {
  let data = statementPreviewState.data || { patients: [] };
  if (trackerApiBase()) {
    // New statements always read the current eligible lines and payment ledger.
    // A historical preview is never an attestation for a new bill.
    statementPreviewState.validationSessionExpired = false;
    const pending = requestStatementPreview({ render: false, requireCurrent: true, selectedKeys: printedKeys });
    renderStatements();
    data = await pending;
    if (!current()) return;
    if (!data) { statementActionState.message = statementPreviewState.validationSessionExpired ? "Your session expired. Sign in again — nothing was printed." : "Current balances could not be verified. Reload the statement list before printing."; statementActionState.error = true; return; }
    const eligible = new Set(statementsAllLines(data).filter((entry) => Number(entry.line.outstanding ?? entry.line.amount ?? 0) > 0).map((entry) => entry.key));
    if (printedKeys.some((selected) => !eligible.has(selected))) {
      statementSelection.keys = new Set(printedKeys.filter((selected) => eligible.has(selected)));
      statementActionState.message = "Some selected lines were paid or are no longer eligible. Review the updated selection before printing.";
      statementActionState.error = true;
      return;
    }
    // The paper uses the current amounts. Say so when they differ from the list
    // on screen, so the person printing is not surprised by the sheet.
    const shownDue = new Map(statementsAllLines(statementPreviewState.data || { patients: [] }).map((entry) => [entry.key, Number(entry.line.outstanding ?? entry.line.amount ?? 0)]));
    changedAmounts = statementsAllLines(data).filter((entry) => printedKeys.includes(entry.key) && shownDue.has(entry.key) && Math.abs(shownDue.get(entry.key) - Number(entry.line.outstanding ?? entry.line.amount ?? 0)) > 0.004).length;
    const selected = new Set(printedKeys);
    const orgs = [...new Set(statementsAllLines(data).filter((entry) => selected.has(entry.key)).map((entry) => String(entry.p.orgId || "")).filter(Boolean))];
    await Promise.all(orgs.map((orgId) => fetchStatementBranding(orgId)));
    if (!current()) return;
    const unreadable = typeof statementBrandingReadFailed === "undefined" ? [] : orgs.filter((orgId) => statementBrandingReadFailed.has(orgId));
    if (unreadable.length) {
      const names = unreadable.map((orgId) => { const hit = statementsAllLines(data).find((entry) => String(entry.p.orgId || "") === orgId); return hit ? statementsClientLabel(hit.p) : "a company"; });
      statementActionState.message = "The billing details for " + names.slice(0, 3).join(", ") + (names.length > 3 ? " and " + (names.length - 3) + " more" : "") + " could not be loaded, so nothing was printed. Try again.";
      statementActionState.error = true;
      return;
    }
  }
  savedSelection = statementSelection.keys;
  statementSelection.keys = new Set(printedKeys);
  statementActionState.message = "";
  if (data.truncated) { statementActionState.message = "Narrow the service dates before printing an incomplete statement list."; statementActionState.error = true; renderStatements(); return; }
  if (!statementsConfirmPrintRun(data)) return;
  const html = statementsPrintableHtml(data);
  if (!html) { statementActionState.message = "Select at least one line to print."; statementActionState.error = true; renderStatements(); return; }
  const area = document.getElementById("statementsPrintArea");
  if (!area) { statementActionState.message = "Print area unavailable."; statementActionState.error = true; renderStatements(); return; }
  const printFilename = statementsPrintFilename(data);
  // The ledger must record the amounts that went on paper: the current
  // validated lines, not the on-screen list they were checked against.
  const printedLines = statementsSelectedLines(data);
  // The selected-key override is only for synchronous rendering. Font/image
  // measurement must not hold an old selection across a scope or UI change.
  statementSelection.keys = savedSelection;
  savedSelection = undefined;
  area.innerHTML = html;
  // Long runs take several seconds to lay out: say what is happening.
  const fitProgress = (done, total) => {
    statementActionState.error = false;
    statementActionState.message = "Preparing pages: " + Math.min(done, total).toLocaleString("en-US") + " of " + total.toLocaleString("en-US") + " lines…";
    const shown = typeof document !== "undefined" && document.querySelector(".stmt-dock-msg");
    if (shown) shown.textContent = statementActionState.message; else if (typeof renderStatements === "function") renderStatements();
  };
  try { await statementsFitPrintPages(area, fitProgress); } catch (error) {
    area.innerHTML = "";
    statementActionState.message = error && error.message ? error.message : "Statement layout could not be verified. Reload before printing.";
    statementActionState.error = true;
    return;
  }
  if (!current()) { area.innerHTML = ""; return; }
  if (area.parentNode !== document.body) document.body.appendChild(area);
  document.body.classList.add("statements-printing");
  statementsBeginPrintDocument(printFilename);
  const cleanup = () => {
    document.body.classList.remove("statements-printing");
    area.innerHTML = "";
    statementsEndPrintDocument();
    if (window.removeEventListener) window.removeEventListener("afterprint", cleanup);
  };
  if (window.addEventListener) window.addEventListener("afterprint", cleanup);
  // Register before print(): in Chromium print() blocks and afterprint can fire
  // before the call returns, so a listener added afterward can miss the event.
  const cancelPrintConfirmation = statementsPromptMarkPrinted(printedKeys, printedKeys.length, printedLines);
  try {
    window.print();
  } catch (_e) {
    cancelPrintConfirmation();
    cleanup();
    return;
  }
  if (changedAmounts) {
    statementActionState.error = false;
    statementActionState.message = changedAmounts + " line" + (changedAmounts === 1 ? "" : "s") + " printed with a current amount that changed since this list loaded. Refresh to see the update.";
  }
  window.setTimeout(() => {
    // A browser that never emits afterprint must not leave this selection armed
    // for a later, unrelated print event.
    cancelPrintConfirmation();
    cleanup();
  }, 1500);
  } finally {
    if (savedSelection && current()) statementSelection.keys = savedSelection;
    statementActionState.busy = false;
    if (current()) renderStatements();
  }
}

function statementsApplyLocalPrinted() {
  const data = statementPreviewState.data || { patients: [] };
  let marked = 0;
  (data.patients || []).forEach((p) => (p.services || []).forEach((svc) => (svc.prLines || []).forEach((line) => {
    const k = statementsLineKey(svc, line, p);
    if (statementSelection.keys.has(k)) { if (!line.alreadyPrinted) marked += 1; line.printCount = Number(line.printCount || 0) + 1; line.alreadyPrinted = true; }
  })));
  if (data.totals) data.totals.alreadyPrintedCount = Number(data.totals.alreadyPrintedCount || 0) + marked;
  return marked;
}

// Plain words for the server's statement refusals; the code stays in brackets
// so support can still match it.
const STATEMENT_ACTION_ERROR_TEXT = {
  statement_line_stale_or_ineligible: "a selected line is no longer eligible. Refresh the list and try again",
  statement_line_mismatch: "a selected amount changed since this list loaded. Refresh the list and try again",
  statement_preview_truncated: "the statement list is incomplete. Narrow the service dates and try again",
  statement_source_unavailable: "statement data is temporarily unavailable. Try again shortly",
  duplicate_statement_line: "a line was selected twice. Refresh the list and try again"
};
function statementsActionErrorText(code) {
  const key = String(code || "");
  return STATEMENT_ACTION_ERROR_TEXT[key] ? STATEMENT_ACTION_ERROR_TEXT[key] + " [" + key + "]" : key;
}

function markSelectedStatements(printedLines) {
  if (typeof hasPermission === "function" && !hasPermission(PERMISSIONS.STATEMENTS_GENERATE)) return;
  const data = statementPreviewState.data || { patients: [] };
  const explicit = Array.isArray(printedLines) && printedLines.length > 0;
  if (!explicit && data.truncated) { statementActionState.message = "Narrow the service dates before marking an incomplete statement list."; statementActionState.error = true; renderStatements(); return; }
  const lines = explicit ? printedLines.slice() : statementsSelectedLines(data);
  if (!lines.length) { statementActionState.message = "Select at least one line first."; statementActionState.error = true; renderStatements(); return; }
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") { const n = statementsApplyLocalPrinted(); statementActionState.busy = false; setStatementActionSuccess("Marked " + n + " as printed (saved locally)."); renderStatements(); return; }
  const token = trackerApiToken();
  const printCommandFingerprint = JSON.stringify({
    orgId: String(statementPreviewState.focusOrg || lines[0].orgId || ""),
    lines: lines.map((line) => [line.claimId, line.serviceId, line.prReason]).sort()
  });
  if (statementActionState.printCommandFingerprint !== printCommandFingerprint || !statementActionState.printIdempotencyKey) {
    statementActionState.printCommandFingerprint = printCommandFingerprint;
    statementActionState.printIdempotencyKey = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `print-${Date.now()}`;
  }
  statementActionState.busy = true; statementActionState.error = false; statementActionState.message = "Marking as printed…"; renderStatements();
  // The server records a print run for ONE client. An all-clients selection is
  // sent as one request per client; each keeps a stable key derived from this
  // command, so a retry after a partial failure replays the finished clients
  // as no-ops instead of reprinting them.
  const linesByOrg = new Map();
  lines.forEach((line) => {
    const org = String(line.orgId || statementPreviewState.focusOrg || "");
    if (!linesByOrg.has(org)) linesByOrg.set(org, []);
    linesByOrg.get(org).push(line);
  });
  // The API accepts request bodies up to 1 MB (about 3,200 lines); a full page
  // of the largest company reached 3,205. Each client is sent in batches whose
  // keys derive from this command, so a retry replays finished batches.
  const STATEMENT_RUN_BATCH = 500;
  const requests = [];
  linesByOrg.forEach((orgLines, org) => {
    for (let start = 0; start < orgLines.length; start += STATEMENT_RUN_BATCH) requests.push({ org, orgLines: orgLines.slice(start, start + STATEMENT_RUN_BATCH), batch: start / STATEMENT_RUN_BATCH, batches: Math.ceil(orgLines.length / STATEMENT_RUN_BATCH) });
  });
  const baseKey = statementActionState.printIdempotencyKey;
  (async () => {
    let printed = 0;
    let reprinted = 0;
    let okOrgs = 0;
    let failure = "";
    let networkFailure = false;
    const okByOrg = new Map();
    for (const { org, orgLines, batch, batches } of requests) {
      try {
        const response = await fetch(`${base}/api/statements/runs`, {
          method: "POST",
          cache: "no-store",
          headers: Object.assign({ "content-type": "application/json" }, token ? { authorization: `Bearer ${token}` } : {}),
          body: JSON.stringify({
            deliveryMethod: "print",
            orgId: org,
            idempotencyKey: (linesByOrg.size > 1 ? baseKey + ":" + org : baseKey) + (batches > 1 ? ":" + batch : ""),
            lines: orgLines
          })
        });
        const payload = await response.json().catch(() => ({}));
        if (response.status === 401) {
          if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
          failure = "session";
          break;
        }
        if (!response.ok || (payload && payload.error)) {
          failure = String((payload && payload.error) || ("http_" + response.status));
          continue;
        }
        okByOrg.set(org, (okByOrg.get(org) || 0) + 1);
        if (okByOrg.get(org) === batches) okOrgs += 1;
        printed += Number(payload.printedCount || 0);
        reprinted += Number(payload.reprintedCount || 0);
      } catch (_error) {
        // A live API exists but the request failed. Deliberately do NOT fall
        // back to a local-only mark: the statement ledger is the audit record
        // that stops a patient being billed twice. Report it and keep the
        // selection so the run can be retried.
        networkFailure = true;
        failure = failure || "network";
      }
    }
    statementActionState.busy = false;
    const summary = "Marked " + printed + " printed" + (reprinted ? (", " + reprinted + " reprinted") : "");
    // Any finished batch means some lines were recorded, even if no client finished.
    const anyRecorded = okByOrg.size > 0;
    if (failure === "session") {
      statementActionState.error = true;
      statementActionState.message = (anyRecorded ? summary + ". " : "") + "Your session expired. Sign in again — " + (anyRecorded ? "the remaining lines were not marked printed." : "nothing was marked printed.");
    } else if (failure && anyRecorded && !okOrgs) {
      statementActionState.error = true;
      statementActionState.message = summary + ". The remaining lines could not be marked (" + statementsActionErrorText(failure) + "); retry to finish.";
    } else if (failure && !okOrgs && networkFailure) {
      statementActionState.error = true;
      statementActionState.message = "Could not reach the server - nothing was marked printed. Check your connection and try again.";
    } else if (failure && !okOrgs) {
      statementActionState.error = true;
      statementActionState.message = "Could not mark printed: " + statementsActionErrorText(failure) + ".";
    } else if (failure) {
      // Keep this exact selection and command key: reloading would reseed every
      // unprinted line, and the retry must repeat this command, not a new one.
      statementActionState.error = true;
      statementActionState.message = summary + " for " + okOrgs + " of " + linesByOrg.size + " clients. The rest could not be marked (" + statementsActionErrorText(failure) + "); retry to finish.";
    } else {
      setStatementActionSuccess(summary + ".");
      statementActionState.printCommandFingerprint = "";
      statementActionState.printIdempotencyKey = "";
      statementPreviewState.status = "idle";
      statementPreviewState.key = "";
    }
    renderStatements();
  })();
}

function reverseSelectedPrintedStatements() {
  if (typeof hasPermission === "function" && !hasPermission(PERMISSIONS.STATEMENTS_GENERATE)) return;
  const data = statementPreviewState.data || { patients: [] };
  const lines = statementsSelectedLines(data).filter((line) => line.alreadyPrinted);
  if (!lines.length) {
    statementActionState.message = "Select at least one printed line first.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  if (new Set(lines.map((line) => String(line.orgId || ""))).size > 1) {
    statementActionState.message = "Unmark printed for one client at a time. Pick a client, then select its lines.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const reason = typeof window !== "undefined" && typeof window.prompt === "function"
    ? window.prompt("Why undo the last print of these lines? Each line loses one print; a line printed once becomes Not printed. The reason is kept in the audit history.", "")
    : "";
  if (reason == null) return;
  if (!String(reason).trim()) {
    statementActionState.message = "A reason is required to undo a print.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  if (typeof window !== "undefined" && typeof window.confirm === "function" && !window.confirm(
    "Undo the last print of " + lines.length + " line" + (lines.length === 1 ? "" : "s") + "?\n\nEach line loses one print. Every print and this undo stay in the audit history."
  )) return;
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") {
    statementActionState.message = "The portal is temporarily unavailable, so printed status was not changed.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const token = trackerApiToken();
  const idempotencyKey = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `print-reverse-${Date.now()}`;
  statementActionState.busy = true;
  statementActionState.error = false;
  statementActionState.message = "Unmarking printed…";
  renderStatements();
  // Batched under the API's 1 MB body limit, like Mark printed.
  const REVERSE_BATCH = 500;
  const batches = [];
  for (let start = 0; start < lines.length; start += REVERSE_BATCH) batches.push(lines.slice(start, start + REVERSE_BATCH));
  (async () => {
    let reversedCount = 0;
    for (let index = 0; index < batches.length; index += 1) {
      const response = await fetch(`${base}/api/statements/printed/reverse`, {
        method: "POST",
        cache: "no-store",
        headers: Object.assign({ "content-type": "application/json" }, token ? { authorization: `Bearer ${token}` } : {}),
        body: JSON.stringify({
          orgId: String(statementPreviewState.focusOrg || lines[0].orgId || ""),
          reason: String(reason).trim(),
          idempotencyKey: batches.length > 1 ? idempotencyKey + ":" + index : idempotencyKey,
          lines: batches[index]
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      if (!response.ok || (payload && payload.error)) return { response, payload, reversedCount, failed: true };
      reversedCount += Number(payload.reversedCount || 0);
    }
    return { reversedCount, failed: false };
  })().then(async ({ response, payload, reversedCount, failed }) => {
    statementActionState.busy = false;
    if (failed) {
      statementActionState.error = true;
      statementActionState.message = (reversedCount ? reversedCount + " line" + (reversedCount === 1 ? "" : "s") + " had a print removed. " : "") + "Could not undo the print (" + String((payload && (payload.message || statementsActionErrorText(payload.error))) || ("http_" + response.status)) + ").";
      if (reversedCount) { statementPreviewState.status = "idle"; statementPreviewState.key = ""; }
      renderStatements();
      return;
    }
    setStatementActionSuccess("Removed one print from " + reversedCount + " line" + (reversedCount === 1 ? "" : "s") + ".");
    statementSelection.keys.clear();
    statementPreviewState.status = "idle";
    statementPreviewState.key = "";
    renderStatements();
  }).catch(() => {
    statementActionState.busy = false;
    statementActionState.error = true;
    statementActionState.message = "Could not reach the server — printed status was not changed.";
    renderStatements();
  });
}

async function voidSelectedStatementPayment() {
  if (typeof hasPermission === "function" && !hasPermission(PERMISSIONS.STATEMENTS_GENERATE)) return;
  const lines = statementsSelectedLines(statementPreviewState.data || { patients: [] }).filter((line) => Number(line.patientPaid || 0) > 0);
  const orgs = [...new Set(lines.map((line) => String(line.orgId || "")).filter(Boolean))];
  const patients = [...new Set(lines.map((line) => String(line.patientRef || "")).filter(Boolean))];
  const paymentIds = [...new Set(lines.reduce((ids, line) => ids.concat(Array.isArray(line.paymentIds) ? line.paymentIds : []), []).filter(Boolean))];
  if (!lines.length || !paymentIds.length) {
    statementActionState.message = "Select a line with a portal-recorded patient payment first.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  if (orgs.length !== 1 || patients.length !== 1) {
    statementActionState.message = "Reverse patient payments for one patient and one client at a time.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") {
    statementActionState.message = "The portal is temporarily unavailable, so the payment was not changed.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const token = trackerApiToken();
  let paymentId = paymentIds[0];
  let payment = null;
  if (paymentIds.length > 1) {
    statementActionState.busy = true;
    statementActionState.error = false;
    statementActionState.message = "Loading recorded payments…";
    renderStatements();
    try {
      let choice = null;
      let paymentPage = 1;
      while (!choice) {
        const response = await fetch(`${base}/api/statements/payments/list`, {
          method: "POST",
          // Not filtered by patientRef: a payment recorded before EZClaim patient
          // ids carries the legacy name reference. paymentIds already limits the
          // choices to this selection's payments.
          body: JSON.stringify({ orgId: orgs[0], paymentIds, page: paymentPage, pageSize: 50 }),
          cache: "no-store",
          headers: token ? { "content-type": "application/json", authorization: `Bearer ${token}` } : { "content-type": "application/json" }
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.error) throw new Error(String(payload.message || payload.error || ("http_" + response.status)));
        const choices = (payload.payments || []).filter((row) => paymentIds.includes(row.paymentId));
        if (!choices.length) throw new Error("No active receipt remains for the selected lines.");
        const totalPages = Number(payload.page?.totalPages || 1);
        const answer = typeof window !== "undefined" && typeof window.prompt === "function"
          ? window.prompt("Which recorded payment should be reversed? Enter its number. The entire receipt and all its allocations will be reversed.\n\n" + choices.map((row, index) => (index + 1) + ". " + statementsMoney(row.amount) + " · " + String(row.paidOnDate || "") + " · " + String(row.method || "")).join("\n") + "\n\nPage " + paymentPage + " of " + totalPages + " · " + Number(payload.page?.totalRows || choices.length) + " receipts" + (paymentPage < totalPages ? "\nN: Next page" : "") + (paymentPage > 1 ? "\nP: Previous page" : ""), "1")
          : "";
        if (answer == null) { statementActionState.busy = false; renderStatements(); return; }
        if (String(answer).trim().toLowerCase() === "n" && paymentPage < totalPages) { paymentPage++; continue; }
        if (String(answer).trim().toLowerCase() === "p" && paymentPage > 1) { paymentPage--; continue; }
        choice = choices[Number(answer) - 1];
        if (!choice) throw new Error("Choose one of the listed payments.");
      }
      statementActionState.busy = false;
      paymentId = choice.paymentId;
      payment = choice;
    } catch (error) {
      statementActionState.busy = false;
      statementActionState.error = true;
      statementActionState.message = "Could not choose a payment (" + String(error.message || error) + ").";
      renderStatements();
      return;
    }
  }
  const reason = typeof window !== "undefined" && typeof window.prompt === "function"
    ? window.prompt("Why is this recorded patient payment being reversed? This reason is kept in the audit history.", "")
    : "";
  if (reason == null) return;
  if (!String(reason).trim()) {
    statementActionState.message = "A reason is required to unmark paid.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const amountLabel = payment ? statementsMoney(payment.amount) : "this payment";
  if (typeof window !== "undefined" && typeof window.confirm === "function" && !window.confirm(
    "Reverse " + amountLabel + "?\n\nThis reverses the entire recorded receipt and every statement-line allocation it made. Source EZClaim payment data is not changed."
  )) return;
  const idempotencyKey = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `payment-void-${Date.now()}`;
  statementActionState.busy = true;
  statementActionState.error = false;
  statementActionState.message = "Reversing recorded payment…";
  renderStatements();
  fetch(`${base}/api/statements/payments/void`, {
    method: "POST",
    cache: "no-store",
    headers: Object.assign({ "content-type": "application/json" }, token ? { authorization: `Bearer ${token}` } : {}),
    body: JSON.stringify({ orgId: orgs[0], paymentId, reason: String(reason).trim(), idempotencyKey })
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    statementActionState.busy = false;
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok || payload.error) {
      statementActionState.error = true;
      statementActionState.message = "Could not reverse payment (" + String(payload.message || payload.error || ("http_" + response.status)) + ").";
      renderStatements();
      return;
    }
    setStatementActionSuccess("Recorded payment reversed. Statement balances will reload without its allocations.");
    statementSelection.keys.clear();
    statementPayState.lastReceipt = null;
    statementPreviewState.status = "idle";
    statementPreviewState.key = "";
    renderStatements();
  }).catch(() => {
    statementActionState.busy = false;
    statementActionState.error = true;
    statementActionState.message = "Could not reach the server — the payment was not changed.";
    renderStatements();
  });
}

function openStatementPayModal() {
  const data = statementPreviewState.data || { patients: [] };
  if (data.truncated) { statementActionState.message = "Narrow the service dates before recording a payment against an incomplete statement list."; statementActionState.error = true; renderStatements(); return; }
  const lines = statementsSelectedLines(data);
  if (!lines.length) {
    statementActionState.message = "Select at least one line first.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  // The server records a payment for one patient at one client. The default
  // selection spans every patient, so say so before the form is filled in.
  if (new Set(lines.map((line) => String(line.orgId || "") + "|" + String(line.patientRef || "").toLowerCase())).size > 1) {
    statementActionState.message = "Record a payment for one patient at a time. Select only that patient's lines.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const outstanding = lines.reduce((sum, line) => sum + Number(line.outstanding != null ? line.outstanding : line.prAmount || 0), 0);
  const today = (typeof todayDateString === "function" ? todayDateString() : new Date().toISOString().slice(0, 10));
  statementPayState.open = true;
  // One command key per opened payment form. Every Save in this form reuses it,
  // so a retry after a lost response replays instead of recording a second
  // payment; reopening the form starts a new payment with a new key.
  statementPayState.commandKey = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `pay-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  statementPayState.amount = outstanding > 0 ? String(Math.round(outstanding * 100) / 100) : "";
  statementPayState.paidOnDate = today;
  statementPayState.method = "check";
  statementPayState.applyToOldest = true;
  statementPayState.note = "";
  statementPayState.disclaimer = false;
  statementPayState.lastReceipt = null;
  renderStatements();
}

function statementsPayModalMarkup(data) {
  if (!statementPayState.open) return "";
  const lines = statementsSelectedLines(data || { patients: [] });
  const outstanding = lines.reduce((sum, line) => sum + Number(line.outstanding != null ? line.outstanding : line.prAmount || 0), 0);
  const patients = [...new Set(lines.map((line) => line.patientName || statementsPatientRefLabel(line.patientRef)).filter(Boolean))];
  return `
    <div class="stmt-pay-scrim" data-stmt-pay-close></div>
    <aside class="stmt-pay-modal" role="dialog" aria-label="Record patient payment">
      <header class="stmt-pay-head">
        <h4>Record patient payment</h4>
        <button type="button" class="stmt-btn stmt-btn-quiet" data-stmt-pay-close>Close</button>
      </header>
      <p class="stmt-pay-context">${statementsEscape(patients.join(", ") || "Selected lines")} · ${lines.length} line${lines.length === 1 ? "" : "s"} · ${statementsMoney(outstanding)} outstanding</p>
      <label class="stmt-field"><span class="stmt-field-label">Amount paid</span>
        <input class="stmt-input" type="number" min="0.01" step="0.01" value="${statementsEscape(statementPayState.amount)}" data-stmt-pay-amount>
      </label>
      <label class="stmt-field"><span class="stmt-field-label">Paid on</span>
        <input class="stmt-input" type="date" value="${statementsEscape(statementPayState.paidOnDate)}" data-stmt-pay-date>
      </label>
      <label class="stmt-field"><span class="stmt-field-label">How paid</span>
        <select class="stmt-input stmt-select" data-stmt-pay-method>
          ${["check", "cash", "card", "ach", "other"].map((method) => `<option value="${method}"${statementPayState.method === method ? " selected" : ""}>${method.charAt(0).toUpperCase() + method.slice(1)}</option>`).join("")}
        </select>
      </label>
      <label class="stmt-checkline"><input type="checkbox" data-stmt-pay-oldest${statementPayState.applyToOldest ? " checked" : ""}> Apply to the oldest dates of service first</label>
      <label class="stmt-field"><span class="stmt-field-label">Note (optional)</span>
        <textarea class="stmt-input" rows="2" data-stmt-pay-note>${statementsEscape(statementPayState.note)}</textarea>
      </label>
      <label class="stmt-checkline stmt-checkline--legal"><input type="checkbox" data-stmt-pay-disclaimer${statementPayState.disclaimer ? " checked" : ""}> ${statementsEscape(STATEMENT_PAYMENT_DISCLAIMER)}</label>
      <div class="stmt-pay-actions">
        <button type="button" class="stmt-btn stmt-btn-primary" data-stmt-pay-save${statementActionState.busy ? " disabled" : ""}>${statementActionState.busy ? "Saving…" : "Save payment"}</button>
        <button type="button" class="stmt-btn stmt-btn-quiet" data-stmt-pay-close>Cancel</button>
      </div>
    </aside>`;
}

const STATEMENT_RECEIPT_PR_LABELS = { deductible: "Deductible", coinsurance: "Coinsurance", copay: "Copay" };
function statementsReceiptPatientName(receipt) {
  return String((receipt && receipt.patientName) || statementsPatientRefLabel(receipt && receipt.payment && receipt.payment.patientRef) || "");
}
// The patient and company are captured when the receipt is made: the statement
// list is usually reloading when the receipt prints, so it cannot be looked up.
function statementsReceiptContext(patient) {
  return patient ? { patientName: String(patient.patientName || ""), clientLabel: statementsClientLabel(patient) } : {};
}
function statementsReceiptHtml(receipt) {
  if (!receipt || !receipt.payment) return "";
  const pay = receipt.payment;
  const rows = (receipt.allocations || []).map((row) => `
    <tr><td>${statementsEscape(statementsHumanDate(row.serviceDate))}</td><td>${statementsEscape(row.claimId || "")}</td>
    <td>${statementsEscape(STATEMENT_RECEIPT_PR_LABELS[row.prReason] || row.prReason || "")}</td><td class="amt">${statementsMoney(row.allocatedAmount)}</td></tr>`).join("");
  return `
    <section class="print-statement stmt-receipt-sheet">
      <div class="ps-letterhead">
        <div class="ps-lh-brand"><div class="ps-lh-id"><div class="ps-lh-name">${statementsEscape(receipt.clientLabel || "Patient payment receipt")}</div>${receipt.clientLabel ? '<div class="ps-lh-sub">Patient payment receipt</div>' : ""}</div></div>
        <div class="ps-lh-meta">
          <div class="ps-lh-title">${pay.isVoided ? "Reversed payment - not a receipt" : "Receipt"}</div>
          <div class="ps-lh-due">Paid <span class="ps-lh-due-amt">${statementsMoney(pay.amount)}</span></div>
          <div class="ps-lh-sub">${statementsEscape(({ ach: "ACH" })[String(pay.method || "")] || (String(pay.method || "").charAt(0).toUpperCase() + String(pay.method || "").slice(1)))} · ${statementsEscape(statementsHumanDate(pay.paidOnDate || ""))}</div>
        </div>
      </div>
      <div class="ps-lh-patient"><span class="ps-lh-patient-label">Patient</span> <span class="ps-lh-patient-name">${statementsEscape(statementsReceiptPatientName(receipt))}</span></div>
      <table class="print-table"><thead><tr><th>Date of service</th><th>Claim</th><th>Type</th><th class="amt">Applied</th></tr></thead><tbody>${rows}</tbody></table>
      <p class="print-note">${statementsEscape("Thank you for your payment. If you have questions about this receipt, please contact " + (receipt.clientLabel || "your provider") + ".")}</p>
    </section>`;
}

function statementsReceiptMarkup() {
  const html = statementsReceiptHtml(statementPayState.lastReceipt);
  return html ? `<div id="statementsReceiptArea" class="stmt-receipt">${html}</div>` : "";
}

function printStatementReceipt(receipt) {
  const html = statementsReceiptHtml(receipt || statementPayState.lastReceipt);
  const area = document.getElementById("statementsPrintArea");
  if (!html || !area) {
    statementActionState.message = "No receipt is ready to print.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  area.innerHTML = html;
  if (area.parentNode !== document.body) document.body.appendChild(area);
  document.body.classList.add("statements-printing");
  statementsBeginPrintDocument(statementsReceiptPrintFilename(receipt || statementPayState.lastReceipt));
  const cleanup = () => {
    document.body.classList.remove("statements-printing");
    area.innerHTML = "";
    statementsEndPrintDocument();
    if (window.removeEventListener) window.removeEventListener("afterprint", cleanup);
  };
  if (window.addEventListener) window.addEventListener("afterprint", cleanup);
  try { window.print(); } catch (_e) {}
  window.setTimeout(cleanup, 1500);
}

function printPatientReceipt(patientKey) {
  const data = statementPreviewState.data || { patients: [] };
  const patient = (data.patients || []).find((row) => statementsPatientKey(row) === patientKey);
  if (!patient) return;
  const last = statementPayState.lastReceipt;
  const lastMatches = last?.payment && String(last.payment.orgId || "") === String(patient.orgId || "") && String(last.payment.patientRef || "").toLowerCase() === String(patient.patientRef || patient.patientName || "").toLowerCase();
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") {
    statementActionState.message = "The portal is temporarily unavailable, so the receipt cannot be loaded.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const token = trackerApiToken();
  if (statementPayState.receiptController) statementPayState.receiptController.abort();
  const controller = new AbortController();
  statementPayState.receiptController = controller;
  const requestId = Number(statementPayState.receiptRequestId || 0) + 1;
  statementPayState.receiptRequestId = requestId;
  const receiptContext = () => workflowAccessScopedRequestKey({ view: state.view, preview: statementPreviewState.key || "", patientKey, orgId: patient.orgId || "" });
  const startedContext = receiptContext();
  const patientPaymentIds = [...new Set((patient.services || []).flatMap((svc) => (svc.prLines || []).flatMap((line) => (Array.isArray(line.paymentIds) ? line.paymentIds : []))).filter(Boolean))].slice(0, 500);
  const requestCurrent = () => statementPayState.receiptRequestId === requestId && startedContext === receiptContext();
  statementActionState.busy = true;
  statementActionState.error = false;
  statementActionState.message = "Loading receipt…";
  renderStatements();
  fetch(`${base}/api/statements/payments/list`, {
    // POST, not GET: patientRef can BE a patient name. Keeps it out of the URL.
    method: "POST",
    // A payment recorded before EZClaim patient ids carries the legacy name
    // reference, so the latest receipt is found by the payment ids on this
    // patient's lines when there are any, not by the current reference.
    body: JSON.stringify(lastMatches
      ? { orgId: patient.orgId || "", patientRef: patient.patientRef || patient.patientName || "", paymentId: last.payment.paymentId, includeVoided: true }
      : (patientPaymentIds.length
        ? { orgId: patient.orgId || "", paymentIds: patientPaymentIds, pageSize: 1, includeAllocations: true }
        : { orgId: patient.orgId || "", patientRef: patient.patientRef || patient.patientName || "", pageSize: 1, includeAllocations: true })),
    cache: "no-store",
    signal: controller.signal,
    headers: token
      ? { "content-type": "application/json", authorization: `Bearer ${token}` }
      : { "content-type": "application/json" }
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!requestCurrent()) return;
    statementActionState.busy = false;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      statementActionState.error = true;
      statementActionState.message = "Your session expired. Sign in again — this page will still be here.";
      renderStatements();
      return;
    }
    if (!response.ok || (payload && payload.error) || !(payload.payments || []).length) {
      statementActionState.error = true;
      statementActionState.message = "No recorded payment was found for this patient.";
      renderStatements();
      return;
    }
    const payment = payload.payments[0];
    if (payment.isVoided) {
      // Reversed (possibly in another session) since this receipt was cached.
      // A reversed payment must never be handed to a patient as a receipt.
      statementPayState.lastReceipt = null;
      statementActionState.error = true;
      statementActionState.message = "That payment was reversed, so its receipt was not printed. Choose Print receipt again for this patient's current payments.";
      renderStatements();
      return;
    }
    const receipt = {
      payment,
      allocations: (payload.allocations || []).filter((row) => row.paymentId === payment.paymentId),
      ...statementsReceiptContext(patient)
    };
    statementPayState.lastReceipt = receipt;
    setStatementActionSuccess("Receipt ready.");
    renderStatements();
    window.setTimeout(() => printStatementReceipt(receipt), 50);
  }).catch(() => {
    if (!requestCurrent() || controller.signal.aborted) return;
    statementActionState.busy = false;
    statementActionState.error = true;
    statementActionState.message = "Could not reach the server for a receipt.";
    renderStatements();
  });
}

function saveStatementPayment() {
  const data = statementPreviewState.data || { patients: [] };
  const lines = statementsSelectedLines(data);
  if (!lines.length) return;
  if (!statementPayState.disclaimer) {
    statementActionState.message = "Confirm the disclaimer before recording a payment.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const amount = Number(statementPayState.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    statementActionState.message = "Enter how much the patient paid.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  if (Math.abs(amount * 100 - Math.round(amount * 100)) > 1e-6) {
    statementActionState.message = "Enter the amount in dollars and cents (at most two decimal places).";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") {
    statementActionState.message = "The portal is temporarily unavailable, so the payment was not recorded.";
    statementActionState.error = true;
    renderStatements();
    return;
  }
  const token = trackerApiToken();
  statementActionState.busy = true;
  statementActionState.error = false;
  statementActionState.message = "Recording payment…";
  renderStatements();
  if (!statementPayState.commandKey) statementPayState.commandKey = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `pay-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const idempotencyKey = statementPayState.commandKey;
  fetch(`${base}/api/statements/payments`, {
    method: "POST",
    cache: "no-store",
    headers: Object.assign({ "content-type": "application/json" }, token ? { authorization: `Bearer ${token}` } : {}),
    body: JSON.stringify({
      orgId: String(statementPreviewState.focusOrg || lines[0].orgId || ""),
      patientRef: lines[0].patientRef,
      amount,
      paidOnDate: statementPayState.paidOnDate,
      method: statementPayState.method,
      note: statementPayState.note,
      applyToOldest: statementPayState.applyToOldest,
      disclaimerAccepted: true,
      idempotencyKey,
      selectedKeys: lines.map((line) => [line.orgId, line.claimId, line.serviceId, line.prReason].join("|")),
      lines
    })
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    statementActionState.busy = false;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      statementActionState.error = true;
      statementActionState.message = "Your session expired. Sign in again — nothing was marked paid.";
      renderStatements();
      return;
    }
    if (!response.ok || (payload && payload.error)) {
      statementActionState.error = true;
      statementActionState.message = "Could not record payment (" + String((payload && (payload.message || statementsActionErrorText(payload.error))) || ("http_" + response.status)) + ").";
      renderStatements();
      return;
    }
    statementPayState.open = false;
    statementPayState.commandKey = "";
    const receiptPatient = (data.patients || []).find((row) => String(row.orgId || "") === String(lines[0].orgId || "")
      && String(row.patientRef || row.patientName || "").toLowerCase() === String(lines[0].patientRef || "").toLowerCase());
    statementPayState.lastReceipt = { ...payload, ...statementsReceiptContext(receiptPatient || { orgId: lines[0].orgId, patientName: lines[0].patientName }) };
    setStatementActionSuccess("Recorded " + statementsMoney(payload.payment && payload.payment.amount) + " paid"
      + (Number(payload.unallocatedAmount) > 0 ? (" · " + statementsMoney(payload.unallocatedAmount) + " not applied") : "") + ".");
    statementPreviewState.status = "idle";
    statementPreviewState.key = "";
    renderStatements();
    window.setTimeout(() => printStatementReceipt(), 50);
  }).catch(() => {
    statementActionState.busy = false;
    statementActionState.error = true;
    // The request may have reached the server before the connection dropped. The
    // form keeps its command key, so saving again cannot record it twice.
    statementActionState.message = "Could not confirm the payment with the server. Save again — it will not be recorded twice.";
    renderStatements();
  });
}

function bindStatementPayModal(host) {
  if (!host) return;
  host.querySelectorAll("[data-stmt-pay-close]").forEach((btn) => btn.addEventListener("click", () => {
    statementPayState.open = false;
    renderStatements();
  }));
  host.querySelector("[data-stmt-pay-amount]")?.addEventListener("input", (event) => { statementPayState.amount = event.target.value; });
  host.querySelector("[data-stmt-pay-date]")?.addEventListener("change", (event) => { statementPayState.paidOnDate = event.target.value; });
  host.querySelector("[data-stmt-pay-method]")?.addEventListener("change", (event) => { statementPayState.method = event.target.value; });
  host.querySelector("[data-stmt-pay-oldest]")?.addEventListener("change", (event) => { statementPayState.applyToOldest = event.target.checked; });
  host.querySelector("[data-stmt-pay-note]")?.addEventListener("input", (event) => { statementPayState.note = event.target.value; });
  host.querySelector("[data-stmt-pay-disclaimer]")?.addEventListener("change", (event) => { statementPayState.disclaimer = event.target.checked; });
  host.querySelector("[data-stmt-pay-save]")?.addEventListener("click", saveStatementPayment);
}

function statementsPatientKey(p) {
  return String(p.orgId || "") + "::" + String(p.patientRef || p.patientName || "");
}
function statementsPatientRefLabel(value) {
  const ref = String(value || "");
  const data = (typeof statementPreviewState === "object" && statementPreviewState && statementPreviewState.data) || { patients: [] };
  const match = (data.patients || []).find((patient) => String(patient.patientRef || "").toLowerCase() === ref.toLowerCase());
  if (match && match.patientName) return String(match.patientName);
  const separator = ref.indexOf("|");
  if (ref.startsWith("ezclaim:")) return separator >= 0 ? ref.slice(separator + 1) : "";
  return ref;
}
function statementsPatientDefaultLineKeys(patientKey) {
  const data = statementPreviewState.data || { patients: [] };
  const out = [];
  (data.patients || []).forEach((p) => {
    if (statementsPatientKey(p) !== patientKey) return;
    // A fully paid line has nothing to bill; including it made the print re-check refuse the run.
    (p.services || []).forEach((svc) => statementsFilteredLines(svc).forEach((line) => { if (line.isDefault && Number(line.outstanding ?? line.amount ?? 0) > 0) out.push(statementsLineKey(svc, line, p)); }));
  });
  return out;
}
function statementsPatientSelectedLineKeys(patientKey) {
  const data = statementPreviewState.data || { patients: [] };
  const out = [];
  (data.patients || []).forEach((p) => {
    if (statementsPatientKey(p) !== patientKey) return;
    (p.services || []).forEach((svc) => (svc.prLines || []).forEach((line) => {
      const k = statementsLineKey(svc, line, p);
      if (statementSelection.keys.has(k)) out.push(k);
    }));
  });
  return out;
}
function printPatientStatements(patientKey) {
  const selectedKeys = statementsPatientSelectedLineKeys(patientKey);
  const keys = selectedKeys.length ? selectedKeys : statementsPatientDefaultLineKeys(patientKey);
  if (!keys.length) { statementActionState.message = "No statements to print for this patient."; statementActionState.error = true; renderStatements(); return; }
  return printSelectedStatements(keys);
}

function statementsFilteredLines(svc) {
  const mode = statementPreviewState.printedFilter || "all";
  return (svc.prLines || []).filter((line) =>
    mode === "unprinted" ? !line.alreadyPrinted
      : (mode === "printed" ? line.alreadyPrinted
        : (mode === "reprinted" ? Number(line.printCount) > 1
          : (mode === "unpaid" ? String(line.paidStatus || "unpaid") !== "paid"
            : (mode === "paid" ? line.paidStatus === "paid" : true)))));
}

function statementsServiceVisible(svc) {
  return statementsFilteredLines(svc).length > 0;
}

function statementsPatientMatches(p) {
  // Every word must appear somewhere: names are "LAST, FIRST", so "jamie sample"
  // finds SAMPLE, JAMIE (the server applies the same rule).
  const words = String(statementPreviewState.search || "").trim().toLowerCase().split(/[\s,]+/).filter(Boolean).slice(0, 6);
  const haystack = [p.patientName, p.client, p.orgId].map((value) => String(value || "").toLowerCase()).join(" ");
  const searchOk = words.every((word) => haystack.includes(word));
  if (!searchOk) return false;
  if ((statementPreviewState.printedFilter || "all") === "all") return true;
  return (p.services || []).some(statementsServiceVisible);
}

const STATEMENT_PAGE_SIZES = Object.freeze([25, 50, 100, 200]);

function statementsPageSize(value) {
  const wanted = Math.trunc(Number(value));
  return STATEMENT_PAGE_SIZES.includes(wanted) ? wanted : 50;
}

function statementsPageModel(data) {
  const source = data && Array.isArray(data.patients) ? data.patients : [];
  const focus = String(statementPreviewState.focusOrg || "");
  if (data?.patientPage) {
    const server = data.patientPage, page = Math.max(1, Number(server.page) || 1), pageSize = statementsPageSize(server.pageSize);
    const totalRows = Math.max(0, Number(server.totalRows) || 0), startIndex = (page - 1) * pageSize;
    return { patients: source, page, pageSize, totalRows, totalPages: Math.max(1, Math.ceil(totalRows / pageSize)), startIndex, endIndex: Math.min(totalRows, startIndex + source.length) };
  }
  const filtered = source.filter((patient) => (!focus || String(patient.orgId || "") === focus) && statementsPatientMatches(patient));
  const pageSize = statementsPageSize(statementPreviewState.pageSize);
  const totalRows = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const page = Math.min(totalPages, Math.max(1, Math.trunc(Number(statementPreviewState.page)) || 1));
  statementPreviewState.page = page;
  statementPreviewState.pageSize = pageSize;
  const startIndex = (page - 1) * pageSize;
  return {
    patients: filtered.slice(startIndex, startIndex + pageSize),
    page,
    pageSize,
    totalRows,
    totalPages,
    startIndex,
    endIndex: Math.min(totalRows, startIndex + pageSize)
  };
}

function statementsPager(data) {
  const model = statementsPageModel(data);
  if (!model.totalRows) return "";
  const pageButtons = [];
  const candidates = new Set([1, model.totalPages, model.page - 2, model.page - 1, model.page, model.page + 1, model.page + 2]);
  const pages = Array.from(candidates).filter((page) => page >= 1 && page <= model.totalPages).sort((a, b) => a - b);
  let previous = 0;
  pages.forEach((page) => {
    if (previous && page > previous + 1) pageButtons.push('<span class="stmt-page-gap" aria-hidden="true">&hellip;</span>');
    pageButtons.push('<button type="button" class="stmt-page-number' + (page === model.page ? ' is-current' : '') + '" data-stmt-page="' + page + '"' + (page === model.page ? ' aria-current="page"' : '') + ' aria-label="Page ' + page + '">' + page.toLocaleString() + '</button>');
    previous = page;
  });
  return `<nav class="stmt-pager" aria-label="Patient statement pages">
    <div class="stmt-page-summary">Showing <strong>${(model.startIndex + 1).toLocaleString()}&ndash;${model.endIndex.toLocaleString()}</strong> of <strong>${model.totalRows.toLocaleString()}</strong> patients</div>
    <div class="stmt-page-controls">
      <button type="button" class="stmt-page-move" data-stmt-page="${Math.max(1, model.page - 1)}"${model.page <= 1 ? " disabled" : ""}>Previous</button>
      <span class="stmt-page-numbers">${pageButtons.join("")}</span>
      <button type="button" class="stmt-page-move" data-stmt-page="${Math.min(model.totalPages, model.page + 1)}"${model.page >= model.totalPages ? " disabled" : ""}>Next</button>
    </div>
    <label class="stmt-page-size">Patients per page <select data-stmt-page-size>${STATEMENT_PAGE_SIZES.map((size) => `<option value="${size}"${size === model.pageSize ? " selected" : ""}>${size}</option>`).join("")}</select></label>
  </nav>`;
}

function statementsServiceRowsHtml(p) {
  // The same visit (date, procedure, charge type) on two claims is usually two
  // sessions, but can be a duplicate claim. Flag it so it is checked before mailing.
  const claimsByVisit = new Map();
  (p.services || []).forEach((svc) => (svc.prLines || []).forEach((line) => {
    const visit = [svc.serviceDate, svc.procedureCode, line.prReason].join("|");
    if (!claimsByVisit.has(visit)) claimsByVisit.set(visit, new Set());
    claimsByVisit.get(visit).add(String(svc.claimId));
  }));
  return (p.services || []).filter(statementsServiceVisible).map((svc) => {
    const lines = statementsFilteredLines(svc).map((line) => {
      const key = statementsLineKey(svc, line, p);
      const checked = statementSelection.keys.has(key);
      // Always say how many times the line went out: "Not printed", "Printed 1x",
      // "Printed 2x" ... with the last print date, so nobody has to guess.
      const printTimes = Math.max(1, Number(line.printCount) || 1);
      // The ledger stores UTC; show the Eastern business day (an evening print is
      // already tomorrow in UTC). A zone-less SQL value is UTC.
      const lastPrintedRaw = String(line.lastPrintedAt || "");
      const lastPrintedInstant = lastPrintedRaw ? new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(lastPrintedRaw) ? lastPrintedRaw : lastPrintedRaw + "Z") : null;
      const lastPrintedDay = lastPrintedInstant && Number.isFinite(lastPrintedInstant.getTime()) ? (typeof easternDateOnlyString === "function" ? easternDateOnlyString(lastPrintedInstant) : lastPrintedRaw.slice(0, 10)) : "";
      const lastPrinted = lastPrintedDay ? statementsHumanDate(lastPrintedDay) : "";
      const printTag = line.alreadyPrinted
        ? '<span class="stmt-tag stmt-tag--done" title="' + statementsEscape("Printed " + printTimes + (printTimes === 1 ? " time" : " times") + (lastPrinted ? ", last on " + lastPrinted : "")) + '">Printed ' + printTimes + "&times;" + (lastPrinted ? " &middot; " + statementsEscape(lastPrinted) : "") + "</span>"
        : '<span class="stmt-tag stmt-tag--todo">Not printed</span>';
      return `
        <label class="stmt-line${line.alreadyPrinted ? " is-printed" : ""}${checked ? " is-picked" : ""}">
          <input type="checkbox" class="stmt-check" data-stmt-line="${statementsEscape(key)}"${checked ? " checked" : ""}>
          <span class="stmt-line-label">${statementsEscape(line.label)}</span>
          <span class="stmt-line-flag">${printTag}${(claimsByVisit.get([svc.serviceDate, svc.procedureCode, line.prReason].join("|")) || new Set()).size > 1 ? '<span class="stmt-tag stmt-tag--todo" title="This date, procedure and charge type also appear on another claim for this patient. Check it is a separate session, not a duplicate claim, before mailing.">Also on another claim</span>' : ""}${line.paidStatus === "paid" ? '<span class="stmt-tag stmt-tag--paid">Paid</span>' : (line.paidStatus === "partial" ? '<span class="stmt-tag">Partial</span>' : "")}</span>
          <span class="stmt-line-amt">${statementsMoney(line.outstanding != null ? line.outstanding : line.amount)}${Number(line.patientPaid) > 0 ? '<small class="stmt-line-paid">billed ' + statementsMoney(line.amount) + " · paid " + statementsMoney(line.patientPaid) + "</small>" : ""}</span>
        </label>`;
    }).join("");
    return `
      <div class="stmt-svc">
        <div class="stmt-svc-head">
          <span class="stmt-svc-date">${statementsEscape(statementsHumanDate(svc.serviceDate))}</span>
          <span class="stmt-svc-cpt">${statementsEscape(svc.procedureCode || "")}</span>
          <span class="stmt-svc-total">${statementsMoney(svc.serviceTotal)}</span>
        </div>
        <div class="stmt-lines">${lines}</div>
      </div>`;
  }).join("");
}

function statementsGroupsByOrg(patients) {
  const groups = new Map();
  (patients || []).forEach((p) => {
    const key = String(p.orgId || p.client || "-");
    if (!groups.has(key)) groups.set(key, { key: key, client: String(p.client || p.orgId || "Client"), patients: [] });
    groups.get(key).patients.push(p);
  });
  return Array.from(groups.values()).sort((a, b) => a.client.localeCompare(b.client));
}

// Renders a "set up branding" prompt chip when an org has effectively default/unset
// branding, otherwise a quiet "Branded" marker.
// An org counts as branded once it carries anything a patient could use to reach
// or pay it. Shared by the list cue and the pre-print guard so they cannot drift.
function statementsOrgIsBranded(orgId) {
  const entry = statementBrandingForOrg(orgId);
  const br = (entry && entry.branding) ? entry.branding : null;
  return !!(br && (br.logoDataUrl || (Array.isArray(br.addressLines) && br.addressLines.length) || br.phone || br.email));
}

function statementsBrandStatusChip(orgId) {
  const branded = statementsOrgIsBranded(orgId);
  // A filled amber chip repeated on every group out-shouts the data it sits next
  // to. A dot plus quiet text stays discoverable without competing with money.
  if (branded) return "";
  return '<button type="button" class="stmt-brandcue" data-stmt-brand-setup="' + statementsEscape(orgId) + '" title="This client will print with default branding &mdash; click to set a logo and address"><span class="stmt-brandcue-dot" aria-hidden="true"></span>Default branding</button>';
}
function statementsTable(patients) {
  const focus = String(statementPreviewState.focusOrg || "");
  const scoped = focus ? (patients || []).filter((p) => String(p.orgId || "") === focus) : (patients || []);
  const groups = statementsGroupsByOrg(scoped);
  // Single-org users (every Client role) never see group chrome; give them the
  // bare list rather than a one-item accordion.
  if (groups.length <= 1) {
    const only = groups.length === 1 ? groups[0].patients : scoped;
    return '<div class="stmt-groups stmt-groups--flat">' + statementsPatientTable(only) + "</div>";
  }
  const searching = String(statementPreviewState.search || "").trim() !== "";
  const sections = groups.map((g) => {
    const visiblePatients = (g.patients || []).filter(statementsPatientMatches);
    if (!visiblePatients.length) return "";
    const isOpen = statementPreviewState.expandedOrg[g.key] === true || searching;
    const openTotal = visiblePatients.reduce((sum, p) => sum + Number(p.openAmount || 0), 0);
    const groupKeys = visiblePatients.reduce((acc, p) => acc.concat((p.services || []).reduce((a, svc) => a.concat(statementsFilteredLines(svc).map((line) => statementsLineKey(svc, line, p))), [])), []);
    const groupChecked = groupKeys.length > 0 && groupKeys.every((k) => statementSelection.keys.has(k));
    const printedPatients = visiblePatients.reduce((n, p) => {
      const bl = (p.services || []).reduce((acc, svc) => acc.concat((svc.prLines || [])), []);
      return n + ((bl.length > 0 && bl.every((line) => line.alreadyPrinted)) ? 1 : 0);
    }, 0);
    return `
      <section class="stmt-group${isOpen ? " is-open" : ""}">
        <div class="stmt-ghead">
          <input type="checkbox" class="stmt-check" data-stmt-org-check="${statementsEscape(g.key)}"${groupChecked ? " checked" : ""} aria-label="Select all in ${statementsEscape(g.client)}">
          <button type="button" class="stmt-disclose" data-stmt-org="${statementsEscape(g.key)}" aria-expanded="${isOpen ? "true" : "false"}" aria-label="${isOpen ? "Collapse" : "Expand"} ${statementsEscape(g.client)}">${STMT_ICONS.chevron}</button>
          <span class="stmt-gident">
            <span class="stmt-gname">${statementsEscape(g.client)}</span>
            <span class="stmt-gmeta">${visiblePatients.length} patient${visiblePatients.length === 1 ? "" : "s"} &middot; ${printedPatients}/${visiblePatients.length} printed</span>
          </span>
          <span class="stmt-gflag">${statementsBrandStatusChip(g.key)}</span>
          <span class="stmt-gamt">${statementsMoney(openTotal)}</span>
          ${(typeof hasPermission !== "function" || hasPermission(PERMISSIONS.STATEMENTS_GENERATE)) ? `<button type="button" class="stmt-btn stmt-btn-secondary stmt-btn-sm" data-stmt-print-org="${statementsEscape(g.key)}">${STMT_ICONS.print}<span>Print all</span></button>` : ""}
        </div>
        ${isOpen ? statementsPatientTable(g.patients) : ""}
      </section>`;
  }).join("");
  return '<div class="stmt-groups">' + (sections || statementsEmptyRow(scoped)) + "</div>";
}

function statementsPatientTable(patients) {
  const visible = (patients || []).filter(statementsPatientMatches);
  if (!visible.length) return statementsEmptyRow(patients);
  const rows = visible.map((p) => {
    const pk = statementsPatientKey(p);
    const isOpen = statementPreviewState.expanded[pk] === true;
    const patKeys = (p.services || []).reduce((acc, svc) => acc.concat(statementsFilteredLines(svc).map((line) => statementsLineKey(svc, line, p))), []);
    const patChecked = patKeys.length > 0 && patKeys.every((k) => statementSelection.keys.has(k));
    const billable = (p.services || []).reduce((acc, svc) => acc.concat((svc.prLines || [])), []);
    const allPrinted = billable.length > 0 && billable.every((line) => line.alreadyPrinted);
    const dates = new Set((p.services || []).filter(statementsServiceVisible).map((s) => String(s.serviceDate || "")));
    return `
      <div class="stmt-patient${isOpen ? " is-open" : ""}${patChecked ? " is-picked" : ""}" data-stmt-patient="${statementsEscape(pk)}">
        <div class="stmt-prow">
          <input type="checkbox" class="stmt-check" data-stmt-patient-check="${statementsEscape(pk)}"${patChecked ? " checked" : ""} aria-label="Select ${statementsEscape(p.patientName)}">
          <button type="button" class="stmt-disclose" data-stmt-toggle="${statementsEscape(pk)}" aria-expanded="${isOpen ? "true" : "false"}" aria-label="${isOpen ? "Collapse" : "Expand"} ${statementsEscape(p.patientName)}">${STMT_ICONS.chevron}</button>
          <span class="stmt-pident">
            <span class="stmt-pname">${statementsEscape(p.patientName)}</span>
            <span class="stmt-pmeta">${patKeys.length} line${patKeys.length === 1 ? "" : "s"} &middot; ${dates.size} date${dates.size === 1 ? "" : "s"} of service</span>
          </span>
          <span class="stmt-pflag">${Number(p.patientPaid) > 0 && Number(p.outstanding || p.openAmount) <= 0 ? '<span class="stmt-tag stmt-tag--paid">Paid</span>' : (Number(p.patientPaid) > 0 ? '<span class="stmt-tag">Partial</span>' : "")}${allPrinted ? '<span class="stmt-tag stmt-tag--done">Printed</span>' : ""}</span>
          <span class="stmt-pcols">
            <span class="stmt-pcol"><small>Paid</small><strong>${statementsMoney(p.patientPaid || 0)}</strong></span>
            <span class="stmt-pcol"><small>Outstanding</small><strong>${statementsMoney(p.outstanding != null ? p.outstanding : p.openAmount)}</strong></span>
          </span>
          <span class="stmt-pactions">
            ${Number(p.patientPaid) > 0 ? `<button type="button" class="stmt-btn stmt-btn-ghost stmt-btn-sm" data-stmt-receipt-patient="${statementsEscape(pk)}">Receipt</button>` : ""}
            ${(typeof hasPermission !== "function" || hasPermission(PERMISSIONS.STATEMENTS_GENERATE)) ? `<button type="button" class="stmt-btn stmt-btn-secondary stmt-btn-sm" data-stmt-print-patient="${statementsEscape(pk)}">${STMT_ICONS.print}<span>Print</span></button>` : ""}
          </span>
        </div>
        ${isOpen ? '<div class="stmt-services">' + statementsServiceRowsHtml(p) + "</div>" : ""}
      </div>`;
  }).join("");
  return '<div class="stmt-list">' + rows + "</div>";
}

function statementsFocusOptions() {
  const data = statementPreviewState.data || { patients: [] };
  if (Array.isArray(data.organizations)) return data.organizations;
  const seen = new Map();
  (data.patients || []).forEach((p) => {
    const k = String(p.orgId || p.client || "");
    if (k && !seen.has(k)) seen.set(k, String(p.client || p.orgId || k));
  });
  return Array.from(seen.entries()).map((e) => ({ orgId: e[0], client: e[1] })).sort((a, b) => a.client.localeCompare(b.client));
}

function statementsMarkup() {
  const s = statementPreviewState;
  const shell = (body) => '<div class="stmt-shell"><div id="statementsReadStatus" class="report-read-status" hidden></div>' + statementsToolbar() + body + "</div>";
  // A window that runs backwards is a typo, not an empty book. Without this it
  // rendered "Nothing to bill for these dates", which reads as a fact about the
  // patients when it is a fact about the two date boxes.
  const window_ = statementsFilters();
  if (window_.fromDos && window_.toDos && window_.fromDos > window_.toDos) {
    return shell(statementsNotice("Check the date range", "Choose a From DOS that is on or before the To DOS."));
  }
  if (s.status === "loading") return shell(statementsSkeleton());
  if (s.status === "unavailable") {
    const source = statementsSourceLabel(s.data);
    return shell(statementsNotice(source ? "Statement source unavailable" : "Statements are not connected here yet",
      source
        ? statementsEscape(source) + " could not be reached. No empty statement result is being shown; try again when the source is available."
        : "Statement data has not been wired up in this environment &mdash; your TMS team is on it. Nothing is missing on your end.",
      '<button type="button" class="stmt-btn stmt-btn-primary stmt-btn-sm" data-stmt-refresh>Try again</button>'));
  }
  if (s.status === "error") {
    return shell(statementsNotice("Could not load statements", statementsEscape(s.error || "Unknown error"),
      '<button type="button" class="stmt-btn stmt-btn-primary stmt-btn-sm" data-stmt-refresh>Try again</button>'));
  }
  const data = s.data || { patients: [], totals: {} };
  const reconciliation = statementsReconciliationReview(data);
  const held = statementsHeldNotice(data);
  const truncated = statementsTruncatedNotice(data);
  if (!data.patients || !data.patients.length) {
    return shell(statementsSummaryStrip(data.totals, data) + truncated + reconciliation + held
      + statementsNotice("Nothing to bill for these dates", "No patient responsibility is open in the selected date range."));
  }
  const pageModel = statementsPageModel(data);
  statementsEnsureSelection(data, pageModel.patients);
  return shell(statementsSummaryStrip(data.totals, data) + truncated + reconciliation + held + statementsTable(pageModel.patients) + statementsPager(data)
    + '<p class="stmt-disclaimer">' + statementsEscape(STATEMENT_PAYMENT_DISCLAIMER) + "</p>"
    + '<div class="stmt-dock-host">' + statementsSelectionDock(data) + "</div>"
    + statementsPayModalMarkup(data)
    + statementsReceiptMarkup());
}

// Selection touches only checkbox states, two row classes and the dock, so it
// is patched in place. Re-rendering the whole tab per click cost 159ms at 1000
// patients (17k nodes rebuilt) and threw away focus on every tick.
function statementsSelectionKeysFor(patients) {
  return (patients || []).reduce((acc, p) => acc.concat((p.services || []).reduce((a, svc) =>
    a.concat(statementsFilteredLines(svc).map((line) => statementsLineKey(svc, line, p))), [])), []);
}

function statementsApplyTriState(el, keys) {
  const total = keys.length;
  const sel = keys.filter((k) => statementSelection.keys.has(k)).length;
  el.checked = total > 0 && sel === total;
  el.indeterminate = total > 0 && sel > 0 && sel < total;
  return { total: total, sel: sel };
}

function bindStatementsDock(scope) {
  const dock = scope.querySelector(".stmt-dock-host");
  if (!dock) return;
  dock.querySelectorAll("[data-stmt-clear]").forEach((btn) => btn.addEventListener("click", () => {
    statementSelection.keys.clear();
    statementsSyncSelectionDom(scope);
  }));
  dock.querySelectorAll("[data-stmt-print]").forEach((btn) => btn.addEventListener("click", printSelectedStatements));
  dock.querySelectorAll("[data-stmt-mark]").forEach((btn) => btn.addEventListener("click", markSelectedStatements));
  dock.querySelectorAll("[data-stmt-unmark-printed]").forEach((btn) => btn.addEventListener("click", reverseSelectedPrintedStatements));
  dock.querySelectorAll("[data-stmt-pay]").forEach((btn) => btn.addEventListener("click", openStatementPayModal));
  dock.querySelectorAll("[data-stmt-unmark-paid]").forEach((btn) => btn.addEventListener("click", voidSelectedStatementPayment));
  dock.querySelectorAll("[data-stmt-receipt]").forEach((btn) => btn.addEventListener("click", () => printStatementReceipt()));
}

function statementsSyncSelectionDom(host) {
  const scope = host || document.getElementById("statementsBody");
  if (!scope) return;
  const data = statementPreviewState.data || { patients: [] };
  scope.querySelectorAll("[data-stmt-line]").forEach((cb) => {
    const on = statementSelection.keys.has(cb.getAttribute("data-stmt-line"));
    cb.checked = on;
    const row = cb.closest(".stmt-line");
    if (row) row.classList.toggle("is-picked", on);
  });
  scope.querySelectorAll("[data-stmt-patient-check]").forEach((el) => {
    const pk = el.getAttribute("data-stmt-patient-check");
    const p = (data.patients || []).find((x) => statementsPatientKey(x) === pk);
    const st = statementsApplyTriState(el, statementsSelectionKeysFor(p ? [p] : []));
    const card = el.closest(".stmt-patient");
    if (card) card.classList.toggle("is-picked", st.total > 0 && st.sel === st.total);
  });
  scope.querySelectorAll("[data-stmt-org-check]").forEach((el) => {
    const key = el.getAttribute("data-stmt-org-check");
    statementsApplyTriState(el, statementsSelectionKeysFor(statementsPageModel(data).patients.filter((x) => String(x.orgId || x.client || "-") === key)));
  });
  const dockHost = scope.querySelector(".stmt-dock-host");
  if (dockHost) {
    dockHost.innerHTML = statementsSelectionDock(data);
    bindStatementsDock(scope);
  }
}

function bindStatementsControls(host) {
  const data = statementPreviewState.data || { patients: [] };
  const refresh = () => { statementPreviewState.status = "idle"; statementPreviewState.key = ""; renderStatements(); };
  host.querySelectorAll("[data-stmt-refresh]").forEach((btn) => btn.addEventListener("click", refresh));
  const from = host.querySelector("[data-stmt-from]");
  const to = host.querySelector("[data-stmt-to]");
  if (from) from.addEventListener("change", (e) => { state.statementsFromDos = e.target.value || ""; statementPreviewState.page = 1; statementPreviewState.status = "idle"; renderStatements(); });
  if (to) to.addEventListener("change", (e) => { state.statementsToDos = e.target.value || ""; statementPreviewState.page = 1; statementPreviewState.status = "idle"; renderStatements(); });
  host.querySelectorAll("[data-stmt-toggle]").forEach((btn) => btn.addEventListener("click", () => {
    const pk = btn.getAttribute("data-stmt-toggle");
    statementPreviewState.expanded[pk] = !(statementPreviewState.expanded[pk] === true);
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-org]").forEach((btn) => btn.addEventListener("click", () => {
    const k = btn.getAttribute("data-stmt-org");
    statementPreviewState.expandedOrg[k] = !(statementPreviewState.expandedOrg[k] === true);
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-focus]").forEach((sel) => sel.addEventListener("change", (e) => {
    statementPreviewState.focusOrg = e.target.value || "";
    // Another company has its own insurers; a carried-over choice could match nothing.
    statementPreviewState.payer = "";
    statementPreviewState.payerOptions = [];
    statementPreviewState.page = 1;
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-line]").forEach((cb) => cb.addEventListener("change", (e) => {
    const k = cb.getAttribute("data-stmt-line");
    if (e.target.checked) statementSelection.keys.add(k); else statementSelection.keys.delete(k);
    statementsSyncSelectionDom(host);
  }));
  host.querySelectorAll("[data-stmt-patient-check]").forEach((cb) => cb.addEventListener("change", (e) => {
    const pk = cb.getAttribute("data-stmt-patient-check");
    const data = statementPreviewState.data || { patients: [] };
    const p = (data.patients || []).find((x) => statementsPatientKey(x) === pk);
    if (!p) return;
    const checked = cb.checked;
    (p.services || []).forEach((svc) => statementsFilteredLines(svc).forEach((line) => {
      const k = statementsLineKey(svc, line, p);
      if (checked) statementSelection.keys.add(k); else statementSelection.keys.delete(k);
    }));
    statementsSyncSelectionDom(host);
  }));
  host.querySelectorAll("[data-stmt-org-check]").forEach((cb) => cb.addEventListener("change", (e) => {
    e.stopPropagation();
    const key = cb.getAttribute("data-stmt-org-check");
    const data = statementPreviewState.data || { patients: [] };
    const patients = statementsPageModel(data).patients.filter((x) => String(x.orgId || x.client || "-") === key);
    const checked = cb.checked;
    patients.forEach((p) => (p.services || []).forEach((svc) => statementsFilteredLines(svc).forEach((line) => {
      const k = statementsLineKey(svc, line, p);
      if (checked) statementSelection.keys.add(k); else statementSelection.keys.delete(k);
    })));
    statementsSyncSelectionDom(host);
  }));
  host.querySelectorAll("[data-stmt-prtype]").forEach((btn) => btn.addEventListener("click", () => {
    const t = btn.getAttribute("data-stmt-prtype");
    // Only lines the Show filter displays, like "All visible": a quick select must
    // never add hidden lines to the run.
    const shown = new Set(statementsPageModel(data).patients.flatMap((p) => (p.services || []).flatMap((svc) => statementsFilteredLines(svc))));
    const targets = statementsAllLines({ patients: statementsPageModel(data).patients }).filter((x) => x.line.prReason === t && shown.has(x.line));
    const allSel = targets.length > 0 && targets.every((x) => statementSelection.keys.has(x.key));
    targets.forEach((x) => { if (allSel) statementSelection.keys.delete(x.key); else statementSelection.keys.add(x.key); });
    statementsSyncSelectionDom(host);
  }));
  host.querySelectorAll("[data-stmt-selectall-visible]").forEach((btn) => btn.addEventListener("click", () => {
    const keys = statementsVisibleLineKeys();
    const allSel = keys.length > 0 && keys.every((k) => statementSelection.keys.has(k));
    keys.forEach((k) => { if (allSel) statementSelection.keys.delete(k); else statementSelection.keys.add(k); });
    statementsSyncSelectionDom(host);
  }));
  host.querySelectorAll("[data-stmt-clear]").forEach((btn) => btn.addEventListener("click", () => { statementSelection.keys.clear(); statementsSyncSelectionDom(host); }));
  host.querySelectorAll("[data-stmt-print-org]").forEach((btn) => btn.addEventListener("click", (e) => { e.stopPropagation(); printOrgStatements(btn.getAttribute("data-stmt-print-org")); }));
  host.querySelectorAll("[data-stmt-print-patient]").forEach((btn) => btn.addEventListener("click", (e) => { e.stopPropagation(); printPatientStatements(btn.getAttribute("data-stmt-print-patient")); }));
  host.querySelectorAll("[data-stmt-receipt-patient]").forEach((btn) => btn.addEventListener("click", (e) => { e.stopPropagation(); printPatientReceipt(btn.getAttribute("data-stmt-receipt-patient")); }));
  host.querySelectorAll("[data-stmt-print]").forEach((btn) => btn.addEventListener("click", printSelectedStatements));
  host.querySelectorAll("[data-stmt-mark]").forEach((btn) => btn.addEventListener("click", markSelectedStatements));
  host.querySelectorAll("[data-stmt-unmark-printed]").forEach((btn) => btn.addEventListener("click", reverseSelectedPrintedStatements));
  // The statement run seeds its default eligible lines before the first full
  // render. Mark paid / Unmark paid therefore need their handlers here, not
  // only in bindStatementsDock(), which runs after a user changes a checkbox.
  // Without these bindings both enabled buttons were inert on first arrival.
  host.querySelectorAll("[data-stmt-pay]").forEach((btn) => btn.addEventListener("click", openStatementPayModal));
  host.querySelectorAll("[data-stmt-unmark-paid]").forEach((btn) => btn.addEventListener("click", voidSelectedStatementPayment));
  host.querySelectorAll("[data-stmt-receipt]").forEach((btn) => btn.addEventListener("click", () => printStatementReceipt()));
  host.querySelectorAll("[data-stmt-search]").forEach((el) => el.addEventListener("input", (e) => {
    statementPreviewState.search = e.target.value || "";
    statementPreviewState.page = 1;
    statementPreviewState._focusSearch = true;
    clearTimeout(statementPreviewState.searchTimer);
    statementPreviewState.searchTimer = setTimeout(() => renderStatements(), 250);
  }));
  host.querySelectorAll("[data-stmt-payer]").forEach((sel) => sel.addEventListener("change", (e) => {
    statementPreviewState.payer = e.target.value || "";
    statementPreviewState.page = 1;
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-printed]").forEach((btn) => btn.addEventListener("click", () => {
    statementPreviewState.printedFilter = btn.getAttribute("data-stmt-printed") || "all";
    statementPreviewState.page = 1;
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-page]").forEach((btn) => btn.addEventListener("click", () => {
    const next = Math.trunc(Number(btn.getAttribute("data-stmt-page")));
    if (!Number.isFinite(next) || next < 1 || btn.disabled) return;
    statementPreviewState.page = next;
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-page-size]").forEach((select) => select.addEventListener("change", (event) => {
    statementPreviewState.pageSize = statementsPageSize(event.target.value);
    statementPreviewState.page = 1;
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-branding]").forEach((btn) => btn.addEventListener("click", () => openStatementBrandingEditor()));
  host.querySelectorAll("[data-stmt-brand-setup]").forEach((btn) => btn.addEventListener("click", (e) => { e.stopPropagation(); openStatementBrandingEditor(btn.getAttribute("data-stmt-brand-setup")); }));
  host.querySelectorAll("[data-stmt-evidence]").forEach(btn => btn.addEventListener("click", () => {
    const page = Math.max(1, Number(btn.getAttribute("data-stmt-evidence-page")) || 1);
    statementPreviewState[btn.getAttribute("data-stmt-evidence") === "held" ? "heldPage" : "reviewPage"] = page;
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-held-toggle]").forEach((btn) => btn.addEventListener("click", () => {
    statementPreviewState.heldOpen = !(statementPreviewState.heldOpen === true);
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-reconciliation-toggle]").forEach((btn) => btn.addEventListener("click", () => {
    statementPreviewState.reconciliationOpen = !(statementPreviewState.reconciliationOpen === true);
    renderStatements();
  }));
  host.querySelectorAll("[data-stmt-reset-filters]").forEach((btn) => btn.addEventListener("click", () => {
    statementPreviewState.search = "";
    statementPreviewState.printedFilter = "all";
    statementPreviewState.payer = "";
    statementPreviewState.focusOrg = "";
    statementPreviewState.page = 1;
    renderStatements();
  }));
  // stmt indeterminate pass: reflect partial selection on group + patient checkboxes
  (function setStatementIndeterminate() {
    const data = statementPreviewState.data || { patients: [] };
    const keysFor = (patients) => (patients || []).reduce((acc, p) => acc.concat((p.services || []).reduce((a, svc) => a.concat(statementsFilteredLines(svc).map((line) => statementsLineKey(svc, line, p))), [])), []);
    const applyState = (el, keys) => {
      const total = keys.length;
      const sel = keys.filter((k) => statementSelection.keys.has(k)).length;
      el.indeterminate = total > 0 && sel > 0 && sel < total;
    };
    host.querySelectorAll("[data-stmt-org-check]").forEach((el) => {
      const key = el.getAttribute("data-stmt-org-check");
      const patients = statementsPageModel(data).patients.filter((x) => String(x.orgId || x.client || "-") === key);
      applyState(el, keysFor(patients));
    });
    host.querySelectorAll("[data-stmt-patient-check]").forEach((el) => {
      const pk = el.getAttribute("data-stmt-patient-check");
      const p = (data.patients || []).find((x) => statementsPatientKey(x) === pk);
      applyState(el, keysFor(p ? [p] : []));
    });
  })();
  if (statementPreviewState._focusSearch) {
    const sb = host.querySelector("[data-stmt-search]");
    if (sb) { sb.focus(); const v = sb.value; try { sb.setSelectionRange(v.length, v.length); } catch (_e) {} }
    statementPreviewState._focusSearch = false;
  }
  bindStatementPayModal(host);
}

// ===========================================================================
// On-Hold Authorization Tracker (ON_HOLD_TRACKER_DESIGN.md §2-§8 + the
// 2026-07-22 Fable ADDENDUM rulings R1/R3, which supersede §3/§4 where they
// disagree).
//
// The client's billing specialist is THE primary user: an authorization
// arrived, get it into the system in under 60 seconds so TMS can submit the
// claims. The board is a work queue of PATIENT cards (worst status first, then
// $ desc); the drawer records ONE authorization (number + date range + file)
// that resolves every covered on-hold line at once.
//
// R1 (density): patient cards are EXPANDED by default — expanded shows WORK
// UNITS (payer group, needs-auth summary, actions, recorded auths), never raw
// lines. The line table is always an explicit "View lines (N)" disclosure.
// R3 (the signature moment): the coverage preview is computed client-side from
// the already-loaded lines, renders before anything is typed, reads as a
// primary headline directly above the buttons, makes "outside range"
// actionable, and the Save button carries the consequence.
//
// Everything consumed here is the exact shape produced by
// backend/authorizationBoard.js `groupOnHoldRowsIntoBoard` and returned by
// GET /api/authorizations/board:
//   { board: { summary: { patientCount, onHoldLineCount, onHoldExpectedTotal,
//                         needsAuthorizationLineCount,
//                         needsAuthorizationExpectedTotal, expectedTotal },
//              patients: [ { patientKey, patientLabel, worstStatus,
//                            worstStatusRank, onHoldLineCount,
//                            onHoldExpectedTotal, needsAuthorizationLineCount,
//                            needsAuthorizationExpectedTotal, expectedTotal,
//                            payers: [ { payerName, payerLabel, worstStatus,
//                                        worstStatusRank, onHoldLineCount,
//                                        onHoldExpectedTotal,
//                                        needsAuthorizationLineCount,
//                                        needsAuthorizationExpectedTotal,
//                                        expectedTotal,
//                                        needsAuthorization: { lineCount,
//                                          expectedTotal, dosStart, dosEnd,
//                                          cptCodes, lines: [{claimId,
//                                          serviceLineId, dos, cpt, expected}] },
//                                        authorizations: [ { authorizationId,
//                                          status, statusRank, authNumber,
//                                          effectiveStartDate, effectiveEndDate,
//                                          cptCodes, note, fileCount, archived,
//                                          archivedAtUtc, lineCount,
//                                          expectedTotal, dosStart, dosEnd,
//                                          lines: [...] } ] } ] } ] },
//     generatedAtUtc, overlayConfigured, includeArchived }
// The line projection carries claimId / serviceLineId / dos / cpt / expected.
//
// TWO COUNTING BASES (guardian fix 2026-07-22) — never mix them in one phrase:
//   onHoldLineCount / onHoldExpectedTotal              = ALL on-hold lines
//     (inventory; does NOT shrink when an authorization is recorded — the line
//     stays on hold until the payer pays).
//   needsAuthorizationLineCount / needsAuthorizationExpectedTotal
//     = only lines with no active authorization link (WORK REMAINING; this is
//     what shrinks on save).
// The card headline pair and the board header BOTH render the needs-auth basis,
// which is the basis the optimistic post-save update maintains — so the numbers
// never jump when the server refresh lands. Pairing onHoldLineCount with the
// needs-auth $ was the display bug this fix removed.
// Units + place-of-service are NOT in the payload today (the design text listed
// them; the CODE is the authority) — the line table renders those two columns
// only if a future payload starts carrying them. Per the addendum's PHI
// allow-list this board never carries payments, adjustments, patient
// responsibility or AR aging, so nothing here reads them.
// ===========================================================================

const AUTHZ_STATUS = {
  NEEDS_AUTHORIZATION: "Needs authorization",
  PENDING_CLIENT: "Authorization Pending — Client",
  PENDING_PAYER: "Authorization Pending — Payer",
  APPROVED: "Authorization Approved",
  DENIED: "Authorization Denied",
  NAR: "NAR — No Auth Required",
  CREDENTIALING_APPROVED: "Credentialing Approved",
  WRITE_OFF: "Write-Off",
  TMS_BILLED: "TMS Billed"
};

// Mirrors RECORDABLE_STATUSES in backend/authorizationBoard.js — "Needs
// authorization" is derived, never written.
const AUTHZ_RECORDABLE_STATUSES = [
  AUTHZ_STATUS.APPROVED,
  AUTHZ_STATUS.PENDING_PAYER,
  AUTHZ_STATUS.PENDING_CLIENT,
  AUTHZ_STATUS.CREDENTIALING_APPROVED,
  AUTHZ_STATUS.NAR,
  AUTHZ_STATUS.DENIED,
  AUTHZ_STATUS.WRITE_OFF,
  AUTHZ_STATUS.TMS_BILLED
];

// Terminal statuses — the only cards R1 permits auto-collapsing on a big org.
const AUTHZ_TERMINAL_STATUSES = [
  AUTHZ_STATUS.APPROVED,
  AUTHZ_STATUS.NAR,
  AUTHZ_STATUS.WRITE_OFF,
  AUTHZ_STATUS.TMS_BILLED
];

// Design §6 pill tones.
const AUTHZ_STATUS_TONE = {
  [AUTHZ_STATUS.NEEDS_AUTHORIZATION]: "red",
  [AUTHZ_STATUS.DENIED]: "red",
  [AUTHZ_STATUS.PENDING_CLIENT]: "amber",
  [AUTHZ_STATUS.PENDING_PAYER]: "amber",
  [AUTHZ_STATUS.CREDENTIALING_APPROVED]: "blue",
  [AUTHZ_STATUS.APPROVED]: "green",
  [AUTHZ_STATUS.NAR]: "gray",
  [AUTHZ_STATUS.WRITE_OFF]: "gray",
  [AUTHZ_STATUS.TMS_BILLED]: "gray"
};

// Short pill copy — the card header has to stay scannable at a glance.
const AUTHZ_STATUS_PILL_LABEL = {
  [AUTHZ_STATUS.NEEDS_AUTHORIZATION]: "Needs auth",
  [AUTHZ_STATUS.DENIED]: "Denied",
  [AUTHZ_STATUS.PENDING_CLIENT]: "Pending — client",
  [AUTHZ_STATUS.PENDING_PAYER]: "Pending — payer",
  [AUTHZ_STATUS.CREDENTIALING_APPROVED]: "Credentialing",
  [AUTHZ_STATUS.APPROVED]: "Approved — TMS will submit",
  [AUTHZ_STATUS.NAR]: "No auth required",
  [AUTHZ_STATUS.WRITE_OFF]: "Written off",
  [AUTHZ_STATUS.TMS_BILLED]: "TMS billed"
};

const AUTHZ_STATUS_HELP = {
  [AUTHZ_STATUS.APPROVED]: "The payer approved it. Enter the authorization number — TMS submits these claims next.",
  [AUTHZ_STATUS.PENDING_PAYER]: "Submitted; the payer is still processing. No number needed yet.",
  [AUTHZ_STATUS.PENDING_CLIENT]: "Waiting on the practice to send or chase the authorization.",
  [AUTHZ_STATUS.CREDENTIALING_APPROVED]: "Provider credentialing cleared for this payer.",
  [AUTHZ_STATUS.NAR]: "This payer needs no authorization. TMS bills it with NAR as the authorization number.",
  [AUTHZ_STATUS.DENIED]: "The payer denied it. Add the reason in the note so TMS knows what happened.",
  [AUTHZ_STATUS.WRITE_OFF]: "Money is being abandoned — a note is required.",
  [AUTHZ_STATUS.TMS_BILLED]: "TMS-side stamp: these lines were billed out."
};

const AUTHZ_ALLOWED_FILE_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg", ".heic"];
const AUTHZ_MAX_FILE_BYTES = 10 * 1024 * 1024;
const AUTHZ_LINE_PREVIEW_CAP = 25;
// R1: expand every card up to this many; above it, only terminal-status cards
// auto-collapse. Actionable work is NEVER collapsed for the biller.
// R3.1: the preview is instant; the debounce only coalesces keystrokes.
const AUTHZ_COVERAGE_DEBOUNCE_MS = 150;
const AUTHZ_FOLD_MS = 250;
const AUTHZ_COLLAPSE_STORAGE_PREFIX = "tms.authorizations.collapsed.";

const authorizationsState = {
  status: "idle", // idle | loading | ready | error
  error: "",
  board: { summary: { patientCount: 0, onHoldLineCount: 0, onHoldExpectedTotal: 0, needsAuthorizationLineCount: 0, needsAuthorizationExpectedTotal: 0, expectedTotal: 0 }, patients: [] },
  generatedAtUtc: "",
  overlayConfigured: false,
  truncated: false,
  authorizationFieldUnavailable: false,
  claimStatusUnavailable: false,
  refreshHeldOrgs: [],
  includeArchived: false,
  search: "",
  statusFilter: "All",
  payerFilter: "All",
  patientFilter: "",
  sortOrder: "name",
  page: 0,
  directoryScope: null,
  orgId: "",
  // Compact directory defaults; this map only records the biller's
  // explicit overrides (patientKey -> true/false), persisted per user per org.
  collapseOverrides: {},
  collapseLoadedFor: "",
  openLineTables: {},   // groupKey -> true once "View lines" is clicked
  showAllLines: {},     // groupKey -> true past the preview cap
  resolvedOpen: false,
  // R3.7: the reward is the resolution — the just-created auth row folds in.
  foldingGroupKey: "",
  justCreatedAuthorizationId: "",
  // R3 "never dead-end": the post-save strip with [Next patient needing auth →]
  lastSave: null,
  // authorizationId -> { fileName, message } for the "upload failed — retry"
  // slot. The auth itself is already saved; only the attachment is missing.
  uploadFailures: {},
  pendingFileByAuthId: {},
  // D2: the org each pending file belongs to, so a retry never re-derives it
  // from the Company selector and posts the attachment at the wrong tenant.
  pendingFileOrgByAuthId: {}
};

// The record/edit drawer. Kept OUT of `state` so a half-typed authorization is
// never persisted to localStorage (PHI discipline).
const authorizationDrawerState = {
  open: false,
  mode: "record", // record | edit | extend
  groupKey: "",
  // D2: the PATIENT'S OWN company, resolved from the board card. This — never
  // the Company selector — is the orgId every save/upload ships.
  orgId: "",
  orgLabel: "",
  patientKey: "",
  patientLabel: "",
  payerName: "",
  payerLabel: "",
  authorizationId: "",
  lines: [],
  form: {
    status: AUTHZ_STATUS.APPROVED,
    authNumber: "",
    effectiveStart: "",
    effectiveEnd: "",
    note: ""
  },
  uncheckedLineKeys: {},
  checklistOpen: false,
  file: null,
  fileError: "",
  saving: false,
  errors: [],
  errorBanner: "",
  zeroCoverageConfirmed: false
};

let authorizationCoverageTimer = 0;

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------
function authorizationsApiBase() {
  return typeof trackerApiBase === "function" ? trackerApiBase() : "";
}

function authorizationsApiToken() {
  return typeof trackerApiToken === "function" ? trackerApiToken() : "";
}

function authorizationsApiHeaders(extra = {}) {
  const token = authorizationsApiToken();
  return Object.assign({}, extra, token ? { authorization: `Bearer ${token}` } : {});
}

let authorizationBoardRequestSequence = 0;
let authorizationBoardRequestController = null;

function operationalBoardSearchFocus(host, selector) {
  const input = host?.querySelector(selector);
  if (!input || document.activeElement !== input) return () => {};
  const start = input.selectionStart, end = input.selectionEnd;
  return () => { const next = host.querySelector(selector); if (next) { next.focus({ preventScroll: true }); try { next.setSelectionRange(start, end); } catch (_) {} } };
}
function operationalBoardPagerHtml(page, key, label) {
  if (!page) return "";
  return managementListPagerHtml({ key, page: Number(page.number), pages: Number(page.totalPages), total: Number(page.totalRows), first: Number(page.from), last: Number(page.to) }, label);
}
let authorizationBoardSearchTimer = null;
let credentialingBoardSearchTimer = null;
function requestOperationalBoardSearch(kind) {
  if (kind === "authorizations") {
    clearTimeout(authorizationBoardSearchTimer); authorizationBoardRequestController?.abort();
    authorizationBoardSearchTimer = setTimeout(() => { authorizationBoardSearchTimer = null; requestAuthorizationBoard(); }, 250);
  } else {
    clearTimeout(credentialingBoardSearchTimer); credentialingState.boardRequestId += 1; credentialingState.requestSeq += 1; credentialingState.controller?.abort();
    credentialingBoardSearchTimer = setTimeout(() => { credentialingBoardSearchTimer = null; requestCredentialingBoard({ preserveBoard: true }); }, 250);
  }
}
function authorizationBoardRequestKey() {
  return workflowAccessScopedRequestKey({
    base: authorizationsApiBase(),
    token: authorizationsApiToken(),
    orgId: String(authorizationsState.orgId || "").trim(),
    includeArchived: Boolean(authorizationsState.includeArchived),
    search: String(authorizationsState.search || ""), statusFilter: authorizationsState.statusFilter || "All", payerFilter: authorizationsState.payerFilter || "All",
    patientFilter: authorizationsState.patientFilter || "", sortOrder: authorizationsState.sortOrder || "name",
    page: authorizationsState.page || 1, resolvedPage: authorizationsState.resolvedPage || 1
  });
}

function resetAuthorizationBoardReadState() {
  authorizationBoardRequestSequence += 1;
  try { authorizationBoardRequestController?.abort(); } catch (_) {}
  authorizationBoardRequestController = null;
  Object.assign(authorizationsState, {
    requestKey: "", status: "idle", board: null, error: "", generatedAtUtc: "",
    overlayConfigured: false, truncated: false, authorizationFieldUnavailable: false,
    claimStatusUnavailable: false, refreshHeldOrgs: []
  });
  clientScopeServerEcho.authorizations = "";
}

function requestAuthorizationBoard(options = {}) {
  const requestId = ++authorizationBoardRequestSequence;
  try { authorizationBoardRequestController?.abort(); } catch (_) {}
  authorizationBoardRequestController = null;
  const base = authorizationsApiBase();
  if (!base || typeof fetch !== "function") {
    authorizationsState.requestKey = authorizationBoardRequestKey();
    authorizationsState.board = null;
    authorizationsState.generatedAtUtc = "";
    clientScopeServerEcho.authorizations = "";
    authorizationsState.status = "error"; authorizationsState.errorAt = Date.now();
    authorizationsState.error = "The portal is temporarily unavailable. Sign in again or contact TMS.";
    if (state.view === "authorizations") renderAuthorizations();
    return;
  }
  const token = authorizationsApiToken();
  const requestOrgId = String(authorizationsState.orgId || "").trim();
  const requestKey = authorizationBoardRequestKey();
  const current = () => requestId === authorizationBoardRequestSequence
    && authorizationsState.requestKey === requestKey
    && authorizationBoardRequestKey() === requestKey;
  // Another company, principal or archive filter must never inherit the old board.
  if (authorizationsState.requestKey !== requestKey) {
    authorizationsState.board = null;
    authorizationsState.generatedAtUtc = "";
    clientScopeServerEcho.authorizations = "";
  }
  authorizationsState.requestKey = requestKey;
  authorizationsState.status = "loading";
  authorizationsState.error = "";
  if (options.rerender !== false && state.view === "authorizations") renderAuthorizations();
  // Capture both narrowing filters before await; scope is still enforced by the server.
  const boardParams = new URLSearchParams();
  if (authorizationsState.includeArchived) boardParams.set("showArchived", "true");
  if (requestOrgId) boardParams.set("orgId", requestOrgId);
  boardParams.set("page", String(authorizationsState.page || 1)); boardParams.set("pageSize", "50");
  boardParams.set("resolvedPage", String(authorizationsState.resolvedPage || 1));
  boardParams.set("patientFilter", authorizationsState.patientFilter || ""); boardParams.set("sortOrder", authorizationsState.sortOrder || "name");
  boardParams.set("search", String(authorizationsState.search || ""));
  boardParams.set("statusFilter", authorizationsState.statusFilter || "All"); boardParams.set("payerFilter", authorizationsState.payerFilter || "All");
  const query = boardParams.toString() ? `?${boardParams.toString()}` : "";
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  authorizationBoardRequestController = controller;
  const timeoutId = window.setTimeout(() => { try { controller?.abort(); } catch (_e) {} }, 60000);
  const url = `${base}/api/authorizations/board${query}`;
  return fetch(typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(url) : url, {
    method: "GET",
    cache: "no-store",
    headers: token ? { authorization: `Bearer ${token}` } : {},
    signal: controller ? controller.signal : undefined
  }).then(async (response) => {
    window.clearTimeout(timeoutId);
    if (!current()) return;
    const payload = await response.json().catch(() => ({}));
    if (!current()) return;
    authorizationBoardRequestController = null;
    const focusOrgId = String((payload && payload.focusOrgId) || "").trim();
    if (response.ok && !(payload && payload.error) && requestOrgId && focusOrgId !== requestOrgId) {
      authorizationsState.status = "error"; authorizationsState.errorAt = Date.now();
      authorizationsState.error = "Authorizations could not confirm the selected company. Nothing was shown.";
      authorizationsState.board = null;
      authorizationsState.generatedAtUtc = "";
      clientScopeServerEcho.authorizations = "";
    } else if (response.status === 503 && payload && payload.retryable === true && !options.retried) {
      // A retryable 503 is a slow first read after a board rebuild; the next
      // read is warm. Retry once quietly instead of showing "could not load".
      requestAuthorizationBoard({ ...options, retried: true, rerender: false });
      return;
    } else if (!response.ok || (payload && payload.error)) {
      authorizationsState.status = "error"; authorizationsState.errorAt = Date.now();
      authorizationsState.error = authorizationsHttpMessage(response.status, payload);
      authorizationsState.truncated = false;
      authorizationsState.authorizationFieldUnavailable = false;
      authorizationsState.claimStatusUnavailable = false;
      authorizationsState.refreshHeldOrgs = [];
    } else {
      authorizationsState.status = "ready";
      authorizationsState.error = "";
      authorizationsState.board = (payload && payload.board) || { summary: { patientCount: 0, onHoldLineCount: 0, onHoldExpectedTotal: 0, needsAuthorizationLineCount: 0, needsAuthorizationExpectedTotal: 0, expectedTotal: 0 }, patients: [] };
      authorizationsState.generatedAtUtc = String((payload && payload.generatedAtUtc) || "");
      authorizationsState.overlayConfigured = Boolean(payload && payload.overlayConfigured);
      authorizationsState.truncated = Boolean(payload && payload.truncated);
      authorizationsState.authorizationFieldUnavailable = Boolean(payload && payload.authorizationFieldUnavailable);
      authorizationsState.claimStatusUnavailable = Boolean(payload && payload.claimStatusUnavailable);
      authorizationsState.refreshHeldOrgs = Array.isArray(payload && payload.refreshHeldOrgs) ? payload.refreshHeldOrgs.map(String) : [];
      clientScopeServerEcho.authorizations = focusOrgId;
    }
    if (state.view === "authorizations") renderAuthorizations();
    updateAuthorizationsNavBadge();
  }).catch((error) => {
    window.clearTimeout(timeoutId);
    if (!current()) return;
    authorizationBoardRequestController = null;
    authorizationsState.status = "error"; authorizationsState.errorAt = Date.now();
    authorizationsState.error = (error && error.name === "AbortError")
      ? "The authorization board took too long to load. Try again."
      : "Could not reach the portal. Check your connection and try again.";
    authorizationsState.truncated = false;
    authorizationsState.authorizationFieldUnavailable = false;
    authorizationsState.claimStatusUnavailable = false;
    authorizationsState.refreshHeldOrgs = [];
    if (state.view === "authorizations") renderAuthorizations();
  });
}

function authorizationsHttpMessage(status, payload = {}) {
  const serverMessage = String((payload && (payload.message || payload.error)) || "").trim();
  if (status === 401) {
    if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    return "Your session expired. Sign in again — this page will still be here.";
  }
  if (status === 403) return "You do not have access to the authorization board for this company.";
  if (status === 501) return "Authorization storage is not configured yet. TMS is finishing the setup.";
  return serverMessage || `The authorization board failed to load (HTTP ${status}).`;
}

// ---------------------------------------------------------------------------
// Org resolution.
//
// D2 FIX (2026-07-22, found live as an all-orgs SuperAdmin) — the Company
// selector is a FILTER and NOTHING ELSE. It used to be the source of the orgId
// on every save: activeAuthorizationOrgId() fell back to "the first tenant in
// the catalog", so recording an authorization on any of the other 13 companies'
// patients shipped the WRONG orgId. The server's org lock then correctly dropped
// every line — the drawer promised "resolves 120 lines" and linked zero,
// silently. The board payload now carries `orgId` on every patient/payer card
// (backend/authorizationBoard.js) and THAT is the only org a save may use; see
// authorizationDrawerState.orgId / saveAuthorizationFromDrawer.
//
// The selector's other job is honesty: with no company selected the board shows
// EVERY company the principal can see and each card wears its own company chip,
// so no card can ever appear under another company's name.
// ---------------------------------------------------------------------------
function authorizationOrgOptions() {
  const scope = currentAccessProfile().scope || {};
  const scoped = normalizeUserScopeList(scope.orgIds);
  if (scoped.length) return scoped.map((orgId) => ({ value: orgId, label: authorizationOrgLabel(orgId) }));
  if (!scope.allOrgs && !scope.crossTenant) return [];
  const catalog = typeof databaseTenantClientOptions === "function" ? databaseTenantClientOptions() : [];
  const seen = new Set();
  const options = [];
  catalog.forEach((entry) => {
    (Array.isArray(entry.orgIds) ? entry.orgIds : []).forEach((orgId) => {
      const value = String(orgId || "").trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ value, label: String(entry.label || entry.value || value) });
    });
  });
  return options.sort((a, b) => a.label.localeCompare(b.label));
}

function authorizationOrgLabel(orgId) {
  const target = String(orgId || "").trim();
  if (!target) return "";
  const catalog = typeof databaseTenantClientOptions === "function" ? databaseTenantClientOptions() : [];
  const match = catalog.find((entry) => (Array.isArray(entry.orgIds) ? entry.orgIds : []).some((value) => String(value) === target));
  return match ? String(match.label || match.value || target) : target;
}

// The company the board is currently SCOPED to, or "" for "all companies".
// NEVER a save org — see saveAuthorizationFromDrawer, which reads the patient's
// own orgId. A single-org principal is implicitly scoped to their one org.
function activeAuthorizationOrgId() {
  if (activeClientGroupTenant()) return activeClientGroupServerOrgId();
  const options = authorizationOrgOptions();
  if (options.length === 1) return options[0].value;
  const current = String(authorizationsState.orgId || "").trim();
  return current && options.some((option) => option.value === current) ? current : "";
}

// Distinct companies actually present in the loaded board (server-reported when
// available, derived otherwise so an older API payload still reads honestly).
function authorizationBoardOrgCount() {
  const summary = authorizationsState.board?.summary || {};
  if (summary.orgCount != null) return Number(summary.orgCount) || 0;
  if (authorizationsState.board?.facets?.orgCount != null) return Number(authorizationsState.board.facets.orgCount) || 0;
  return new Set(authorizationPatients().map((patient) => String(patient.orgId || "")).filter(Boolean)).size;
}

// True when the visible board spans more than one company — the case where a
// single company name in the header would be a lie about 13 other tenants.
function authorizationBoardIsMultiOrg() {
  if (activeAuthorizationOrgId()) return false;
  return authorizationBoardOrgCount() > 1;
}

// The org label for one card. Prefer the server-sent label (the row's own client
// name), fall back to the tenant catalog, then to the raw id.
function authorizationPatientOrgLabel(patient = {}) {
  const sent = String(patient.orgLabel || "").trim();
  if (sent) return sent;
  return authorizationOrgLabel(patient.orgId) || String(patient.orgId || "");
}

function authorizationBoardTitle() {
  const scopedTo = activeAuthorizationOrgId();
  if (scopedTo) return authorizationOrgLabel(scopedTo) || "Authorizations";
  const orgCount = authorizationBoardOrgCount();
  // Honesty rule: with several companies in view the header names NONE of them.
  return orgCount > 1 ? `Authorizations · all ${Formatters.int(orgCount)} companies` : "Authorizations";
}

// ---------------------------------------------------------------------------
// Client-side date helpers — deliberately mirror parseDateOnly() in
// backend/authorizationBoard.js so the live coverage preview and the
// server-side resolver agree line-for-line (R3.1: the number the biller sees
// IS the set that gets submitted).
// ---------------------------------------------------------------------------
function authzParseDateOnly(value) {
  if (value == null || value === "") return null;
  const text = String(value).trim();
  if (!text) return null;
  const mdy = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (mdy) {
    const month = Number(mdy[1]);
    const day = Number(mdy[2]);
    const year = Number(mdy[3]);
    return authzValidYmd(year, month, day) ? Date.UTC(year, month - 1, day) : null;
  }
  const ymd = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (ymd) {
    const year = Number(ymd[1]);
    const month = Number(ymd[2]);
    const day = Number(ymd[3]);
    return authzValidYmd(year, month, day) ? Date.UTC(year, month - 1, day) : null;
  }
  const parsed = new Date(text);
  if (!Number.isFinite(parsed.getTime())) return null;
  return Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
}

function authzValidYmd(year, month, day) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

function authzIsoDate(value) {
  const parsed = authzParseDateOnly(value);
  return parsed == null ? "" : new Date(parsed).toISOString().slice(0, 10);
}

// Compact US display date; the biller reads 03/02 not 2026-03-02.
function authzShortDate(value) {
  const parsed = authzParseDateOnly(value);
  if (parsed == null) return "—";
  const date = new Date(parsed);
  return `${String(date.getUTCMonth() + 1).padStart(2, "0")}/${String(date.getUTCDate()).padStart(2, "0")}`;
}

function authzFullDate(value) {
  const parsed = authzParseDateOnly(value);
  if (parsed == null) return "—";
  const date = new Date(parsed);
  return `${String(date.getUTCMonth() + 1).padStart(2, "0")}/${String(date.getUTCDate()).padStart(2, "0")}/${date.getUTCFullYear()}`;
}

function authzDateSpan(start, end) {
  const from = authzShortDate(start);
  const to = authzShortDate(end);
  if (from === "—" && to === "—") return "no dated lines";
  if (from === to) return from;
  return `${from}–${to}`;
}

// The reason an authorization line's expected amount is unknown. Same shape as
// the grid's NO_PRIMARY_RATE_REASON: it must deny the $0 reading explicitly,
// because "--" alone is ambiguous between "nothing" and "not known".
const AUTHZ_NO_EXPECTED_REASON = "No rate on file for this service line, so the expected amount is not known. This is not $0.";

// $0 / missing is NOT a lie we tell the biller (design §8) — but a KNOWN $0 is
// not missing, and must render as $0. See A2 in the ruling: the old
// `|| amount <= 0` clause relabelled a genuine, asserted zero as
// "rates pending", which is the same lie this file exists to prevent, mirrored.
function authzMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "— (rates pending)";
  return money(amount);
}

function authzLineKey(line = {}) {
  return `${String(line.claimId || "")} ${String(line.serviceLineId || "")}`;
}

function authzStatusTone(status) {
  return AUTHZ_STATUS_TONE[String(status || "").trim()] || "gray";
}

function authzStatusPill(status, extraClass = "", { billed = "" } = {}) {
  const value = String(status || "").trim();
  if (!value) return "";
  if (billed === "credentialing") {
    return `<span class="pill amber ${extraClass}" title="The authorization is done. These claims still carry CPH, so they are waiting on the Credentialing board, not on TMS.">${escapeHtml(AUTHZ_WAITING_ON_CREDENTIALING_LABEL)}</span>`;
  }
  if (billed === "no_lines") {
    return `<span class="pill gray ${extraClass}" title="This authorization is recorded, but no claim line is attached to it, so there is nothing for TMS to submit.">${escapeHtml(AUTHZ_NOTHING_TO_BILL_LABEL)}</span>`;
  }
  if (billed === "confirmed") {
    return `<span class="pill blue ${extraClass}" title="TMS billed these claims and they no longer show as waiting to be billed, so nothing here is left to send.">${escapeHtml(AUTHZ_BILLED_LABEL)}</span>`;
  }
  if (billed === "submitted") {
    return `<span class="pill blue ${extraClass}" title="TMS marked these claims sent from EZClaim. This clears by itself once the claims no longer show as On Hold.">${escapeHtml(AUTHZ_BILLED_AWAITING_LABEL)}</span>`;
  }
  const label = AUTHZ_STATUS_PILL_LABEL[value] || value;
  return `<span class="pill ${authzStatusTone(value)} ${extraClass}" title="${escapeHtml(value)}">${escapeHtml(label)}</span>`;
}

// "Approved — TMS will submit" must not linger after TMS marked the claims sent
// (Nathan, 2026-10-29): a release-status authorization whose bill-out task is
// submitted reads "Billed — awaiting confirmation" until its lines leave On Hold,
// so the board and the "claims to bill out" card never disagree. The same goes
// once that task is confirmed (Nathan, 2026-11-02, Avenues): those claims left the
// unbilled population, yet the card still promised "TMS will submit" over an empty
// "To bill" lane. A patient can stay on the board after that through a different
// payer's lines, such as a write-off.
const AUTHZ_BILLED_AWAITING_LABEL = "Billed — awaiting confirmation";
const AUTHZ_BILLED_LABEL = "Billed";
const AUTHZ_NOTHING_TO_BILL_LABEL = "Approved — nothing to bill yet";
const AUTHZ_WAITING_ON_CREDENTIALING_LABEL = "Approved — waiting on credentialing";
const AUTHZ_RELEASE_STATUSES = [AUTHZ_STATUS.APPROVED, AUTHZ_STATUS.NAR];

function authzBilledAuthorizationStates() {
  const states = new Map();
  (Array.isArray(authorizationSendState.tasks) ? authorizationSendState.tasks : []).forEach((task) => {
    if (task && (task.waitingOnCredentialing || task.status === "submitted" || task.status === "confirmed")) states.set(`${String(task.orgId || "")}|${String(task.authorizationId || "")}`, task.waitingOnCredentialing ? "credentialing" : task.status);
  });
  return states;
}

// "submitted", "confirmed", "no_lines" when no claim line is attached (Nathan,
// 2026-11-02: an Avenues card promised "TMS will submit" with 0 lines and no task),
// or "" when it still has to be billed.
function authzAuthBilledState(auth = {}, orgId = "", states = authzBilledAuthorizationStates()) {
  if (!AUTHZ_RELEASE_STATUSES.includes(String(auth.status || ""))) return "";
  const authorizationId = String(auth.authorizationId || "");
  const org = String(auth.orgId || orgId || "");
  const noLines = auth.lineCount !== undefined && Number(auth.lineCount) === 0 ? "no_lines" : "";
  if (org) return states.get(`${org}|${authorizationId}`) || noLines;
  // No org on hand (an auth card renders without its patient): match the id alone.
  const match = Array.from(states.entries()).find(([key]) => key.endsWith(`|${authorizationId}`));
  return match ? match[1] : noLines;
}

// A payer or patient reads "billed" only when its worst status is a release
// status (nothing still needs work) and EVERY live release authorization under
// it has been marked sent. It stays "awaiting confirmation" until all confirm.
function authzAuthsBilledState(worstStatus, auths = [], orgId = "") {
  if (!AUTHZ_RELEASE_STATUSES.includes(String(worstStatus || ""))) return "";
  const states = authzBilledAuthorizationStates();
  const release = auths.filter((auth) => auth && !auth.archived && AUTHZ_RELEASE_STATUSES.includes(String(auth.status || "")));
  const billed = release.map((auth) => authzAuthBilledState(auth, orgId, states));
  if (!billed.length || billed.includes("")) return "";
  if (billed.includes("submitted")) return "submitted";
  if (billed.includes("credentialing")) return "credentialing";
  return billed.includes("confirmed") ? "confirmed" : "no_lines";
}

// ---------------------------------------------------------------------------
// Board shaping — filters, search, and the "recently resolved" split.
// ---------------------------------------------------------------------------
function authorizationPatients() {
  return Array.isArray(authorizationsState.board?.patients) ? authorizationsState.board.patients : [];
}

// D2: a card's identity is ORG + patient. On an all-orgs board two different
// people at two different companies can normalize to the same name-key (P1
// identity is a name — design Q3); keying anything on patientKey alone would
// collapse them, toggle both at once, and let a save target the wrong company.
function authorizationPatientCardKey(patient = {}) {
  return `${String(patient.orgId || "")}||${String(patient.patientKey || "")}`;
}

// The org+patient+payer key the drawer and the board markup share. It mirrors
// composeGroupKey() in backend/authorizationBoard.js — same three parts, in the
// same order.
function authorizationGroupKeyFor(patient = {}, payer = {}) {
  return `${String(patient.orgId || "")}||${String(patient.patientKey || "")}||${String(payer.payerName || "")}`;
}

// A patient whose payer groups all have ZERO on-hold lines left but DO carry a
// recorded authorization has fallen off the tracker — that is the closure
// signal Excel never gave the biller (design §6).
function authorizationPatientIsResolved(patient = {}) {
  const payers = Array.isArray(patient.payers) ? patient.payers : [];
  if (!payers.length) return false;
  const anyAuth = payers.some((payer) => (payer.authorizations || []).length > 0);
  const anyOnHold = payers.some((payer) => Number(payer.onHoldLineCount || 0) > 0);
  return anyAuth && !anyOnHold;
}

function authorizationPatientNeedsAuth(patient = {}) {
  return String(patient.worstStatus || "") === AUTHZ_STATUS.NEEDS_AUTHORIZATION;
}

// --- basis readers ---------------------------------------------------------
// The needs-authorization basis (work remaining). Falls back to the legacy
// field names so a board served by an older API still renders the right thing:
// `expectedTotal` has always been the needs-auth sum, and the per-payer
// needsAuthorization block has always carried the needs-auth line count.
function authzNeedsLineCount(node = {}) {
  if (node.needsAuthorizationLineCount != null) return Number(node.needsAuthorizationLineCount) || 0;
  if (node.needsAuthorization) return Number(node.needsAuthorization.lineCount) || 0;
  const payers = Array.isArray(node.payers) ? node.payers : null;
  if (payers) return payers.reduce((sum, entry) => sum + authzNeedsLineCount(entry), 0);
  return 0;
}

function authzNeedsExpectedTotal(node = {}) {
  if (node.needsAuthorizationExpectedTotal != null) return Number(node.needsAuthorizationExpectedTotal) || 0;
  return Number(node.expectedTotal) || 0;
}

// Polish #6 — how many of the needs-auth lines actually carry a rate.
function authzNeedsPricedCount(node = {}) {
  if (node.needsAuthorizationPricedLineCount != null) return Number(node.needsAuthorizationPricedLineCount) || 0;
  if (node.needsAuthorization && node.needsAuthorization.pricedLineCount != null) return Number(node.needsAuthorization.pricedLineCount) || 0;
  const payers = Array.isArray(node.payers) ? node.payers : null;
  if (payers) return payers.reduce((sum, entry) => sum + authzNeedsPricedCount(entry), 0);
  return 0;
}

// How many needs-auth lines have NO expected at all. Distinct from
// "lineCount - pricedCount", which also sweeps in a genuine, asserted $0.
function authzNeedsUnknownCount(node = {}) {
  if (node.needsAuthorizationUnknownExpectedLineCount != null) return Number(node.needsAuthorizationUnknownExpectedLineCount) || 0;
  if (node.needsAuthorization && node.needsAuthorization.unknownExpectedLineCount != null) return Number(node.needsAuthorization.unknownExpectedLineCount) || 0;
  const payers = Array.isArray(node.payers) ? node.payers : null;
  if (payers) return payers.reduce((sum, entry) => sum + authzNeedsUnknownCount(entry), 0);
  return 0;
}

// Every needs-auth line on this node is unknown, so there is no total — not
// even a floor. NORTH_STAR.md §6: a blank that says "unknown" beats a figure
// that is wrong, and a bold "$0 expected" in a money slot is a figure.
function authzExpectedAllUnknown(node = {}) {
  const lines = authzNeedsLineCount(node);
  return lines > 0 && authzNeedsUnknownCount(node) >= lines;
}

// TWO-TIER RULE. When the WHOLE board is unknown (today: the rate feed is dead
// for all 20 orgs) the header states it once and every card below simply omits
// the money fragment — repeating "expected not yet known" on 150+ rows carries
// no information per repetition. When only SOME groups are unknown, those
// groups must say so out loud, because their priced neighbours show dollars and
// silence would be read as $0 by omission.
function authzBoardExpectedAllUnknown() {
  return authzExpectedAllUnknown(authorizationsState.board?.summary || {});
}

// The grey, unbolded prose form of an unknown money value. Bold on this screen
// means "a real figure"; an unknown is never bold and never a warning colour.
function authzUnknownExpectedHtml(text) {
  return `<span class="money-unknown-text">${escapeHtml(text)}</span>`;
}

// Polish #6 — "120 lines - 48 priced - $14,989".
//
// Found live: narrowing the range from 120 lines to 48 left the dollar figure
// sitting at $14,989. That was arithmetically RIGHT — 72 of the 120 lines expect
// $0 because their rates are not loaded — and it read as a broken, frozen
// number. The priced count only appears when some lines are unpriced, so a
// fully-rated group keeps the clean two-part phrase.
//
// When EVERY line is unknown there is no figure to qualify: "0 priced - $0" is
// a count of nothing next to a number we do not have, so both fragments drop
// and the phrase says so in words (or says nothing, when the board header has
// already said it — see authzBoardExpectedAllUnknown).
function authzLineCountMoneyText(lineCount, pricedCount, amount, unknownCount = 0) {
  const lines = Number(lineCount) || 0;
  const priced = Math.max(0, Math.min(lines, Number(pricedCount) || 0));
  const unknown = Math.max(0, Math.min(lines, Number(unknownCount) || 0));
  const head = `${Formatters.int(lines)} line${lines === 1 ? "" : "s"}`;
  if (lines && unknown >= lines) {
    return authzBoardExpectedAllUnknown() ? head : `${head} · ${authzUnknownExpectedHtml("expected not yet known")}`;
  }
  const pricedPart = lines && priced < lines ? ` · ${Formatters.int(priced)} priced` : "";
  return `${head}${pricedPart} · ${escapeHtml(authzMoney(amount))}`;
}

// The card headline. Count and $ are BOTH the needs-auth basis, so they always
// describe the same lines. A patient whose on-hold lines are all covered would
// otherwise read "0 lines - $0" and look broken/empty, so that state says so in
// words instead (such a patient usually also sits in Recently resolved, but a
// patient with covered-yet-still-on-hold lines legitimately stays actionable).
function authorizationPatientMetaText(patient = {}) {
  const needsCount = authzNeedsLineCount(patient);
  if (needsCount > 0) {
    const head = `${Formatters.int(needsCount)} line${needsCount === 1 ? "" : "s"} needing auth`;
    if (authzExpectedAllUnknown(patient)) {
      return authzBoardExpectedAllUnknown() ? head : `${head} · ${authzUnknownExpectedHtml("expected not yet known")}`;
    }
    const priced = authzNeedsPricedCount(patient);
    const pricedPart = priced < needsCount ? ` · ${Formatters.int(priced)} priced` : "";
    return `${head}${pricedPart} · ${escapeHtml(authzMoney(authzNeedsExpectedTotal(patient)))}`;
  }
  const onHold = Number(patient.onHoldLineCount || 0);
  if (onHold > 0) {
    return `All ${Formatters.int(onHold)} on-hold line${onHold === 1 ? "" : "s"} covered`;
  }
  return "No on-hold lines";
}

// A publication refresh changes data, not the selected person or page.
// Reset directory choices only when the signed-in principal, access or company changes.
function workflowDirectoryScope(orgId) {
  return workflowAccessScopedRequestKey({ orgId: String(orgId || ""),
    trackerBase: trackerApiBase(), workflowBase: workflowApiBase(),
    trackerToken: trackerApiToken(), workflowToken: workflowApiToken() });
}

function syncWorkflowDirectoryScope(directory, scope) {
  if (directory.directoryScope !== null && directory.directoryScope !== scope) {
    directory.page = 1;
    directory.patientFilter = "";
    directory.providerFilter = "";
    directory.search = "";
    if ("statusFilter" in directory) directory.statusFilter = "All";
    if ("payerFilter" in directory) directory.payerFilter = "All";
    if ("bucketFilter" in directory) directory.bucketFilter = "All";
  }
  directory.directoryScope = scope;
}

function workflowDirectoryPage(rows, directory) {
  const size = 20;
  directory.page = Math.max(0, Math.min(Math.floor(Number(directory.page) || 0), Math.max(0, Math.ceil(rows.length / size) - 1)));
  const start = directory.page * size;
  return { rows: rows.slice(start, start + size), start, end: Math.min(start + size, rows.length), total: rows.length, page: directory.page };
}

function workflowDirectoryPagerMarkup(page, lane) {
  return "<nav class=\"workflow-directory-pager\" aria-label=\"" + (lane === "authz" ? "Patients" : "Providers") + " pages\">"
    + "<span>Showing " + Formatters.int(page.total ? page.start + 1 : 0) + "–" + Formatters.int(page.end) + " of " + Formatters.int(page.total) + "</span>"
    + "<div><button type=\"button\" class=\"ghost-button\" data-" + lane + "-page=\"" + (page.page - 1) + "\"" + (page.page === 0 ? " disabled" : "") + ">Previous</button>"
    + "<button type=\"button\" class=\"ghost-button\" data-" + lane + "-page=\"" + (page.page + 1) + "\"" + (page.end >= page.total ? " disabled" : "") + ">Next</button></div></nav>";
}

function workflowDirectorySortMarkup(lane, value) {
  return "<label class=\"authz-filter\"><span>Sort</span><select data-" + lane + "-sort>"
    + "<option value=\"name\"" + (value === "lines" ? "" : " selected") + ">Name A–Z</option>"
    + "<option value=\"lines\"" + (value === "lines" ? " selected" : "") + ">Most outstanding lines</option></select></label>";
}

function authorizationDirectoryControlsMarkup() {
  const options = authorizationVisiblePatients({ ignorePatient: true, ignoreStatus: true, alphabetical: true });
  const optionCount = authorizationsState.board?.facets?.directoryCounts?.allPatientCount ?? options.length;
  return "<label class=\"authz-filter workflow-directory-select\"><span>Patient</span><select id=\"authzPatientFilter\"><option value=\"\">All patients (" + optionCount + ")</option>"
    + options.map((patient) => {
      const key = authorizationPatientCardKey(patient);
      const company = authorizationBoardIsMultiOrg() ? " · " + authorizationPatientOrgLabel(patient) : "";
      return "<option value=\"" + escapeHtml(key) + "\"" + (authorizationsState.patientFilter === key ? " selected" : "") + ">"
        + escapeHtml(String(patient.patientLabel || patient.patientKey || "") + company + " · " + Formatters.int(authzNeedsLineCount(patient)) + (authzNeedsLineCount(patient) === 1 ? " line" : " lines") + " needing auth") + "</option>";
    }).join("") + "</select></label>" + workflowDirectorySortMarkup("authz", authorizationsState.sortOrder);
}

function authorizationDirectoryBreakdownMarkup(visible) {
  const candidates = authorizationVisiblePatients({ ignoreStatus: true });
  const complete = authorizationsState.board?.facets?.directoryCounts;
  const counts = complete?.statusCounts || authorizationStatusOptionsWithCounts(candidates);
  const lineCount = complete?.lineCount ?? visible.reduce((sum, patient) => sum + authzNeedsLineCount(patient), 0);
  const patientCount = complete?.patientCount ?? visible.length;
  const statusPatientCount = complete?.statusPatientCount ?? candidates.length;
  return "<section class=\"panel workflow-directory-overview\" aria-label=\"Authorization breakdown\"><strong>Filtered results: " + Formatters.int(patientCount) + (patientCount === 1 ? " patient · " : " patients · ")
    + Formatters.int(lineCount) + (lineCount === 1 ? " line" : " lines") + " needing authorization</strong>"
    + "<div class=\"workflow-directory-breakdown\" aria-label=\"Patient status filters\"><span>Patients by status:</span>"
    + [{ status: "All", count: statusPatientCount }].concat(counts).map((entry) => "<button type=\"button\" class=\"ghost-button\" data-authz-status=\"" + escapeHtml(entry.status) + "\" aria-pressed=\"" + (authorizationsState.statusFilter === entry.status) + "\">"
      + escapeHtml(entry.status === "All" ? "All statuses" : entry.status === AUTHZ_STATUS.APPROVED ? "Approved" : AUTHZ_STATUS_PILL_LABEL[entry.status] || entry.status) + " (" + Formatters.int(entry.count) + ")</button>").join("")
    + "</div></section>";
}

function credentialingDirectoryControlsMarkup() {
  const options = credentialingProviderCards({ ignoreProvider: true, ignoreBucket: true, alphabetical: true });
  const multiCompany = (credentialingState.board?.facets?.orgCount ?? new Set(credentialingGroups().map((group) => group.orgId)).size) > 1;
  const optionCount = credentialingState.board?.facets?.directoryCounts?.allProviderCount ?? options.length;
  const active = credentialingState.search || credentialingState.providerFilter || credentialingState.bucketFilter !== "All" || credentialingState.sortOrder !== "name";
  return "<label class=\"authz-filter workflow-directory-select\"><span>Provider</span><select data-cred-provider-filter><option value=\"\">All providers (" + optionCount + ")</option>"
    + options.map((card) => "<option value=\"" + escapeHtml(card.key) + "\"" + (credentialingState.providerFilter === card.key ? " selected" : "") + ">"
      + escapeHtml(card.providerDisplay + (multiCompany ? " · " + card.orgLabel : "") + " · " + Formatters.int(card.parkedLineCount) + (card.parkedLineCount === 1 ? " parked line" : " parked lines")) + "</option>").join("")
    + "</select></label>" + workflowDirectorySortMarkup("cred", credentialingState.sortOrder)
    + (active ? "<button type=\"button\" class=\"authz-link-button\" data-cred-clear-filters>Clear filters</button>" : "")
    + "<div class=\"authz-filter-spacer\"></div><span class=\"authz-filter-links\"><button type=\"button\" class=\"authz-link-button\" data-cred-expand-all>Expand all</button><button type=\"button\" class=\"authz-link-button\" data-cred-collapse-all>Collapse all</button></span>";
}

function credentialingDirectoryBreakdownMarkup(cards) {
  const candidates = credentialingGroups().filter((group) => credentialingGroupPassesFilters(group, { ignoreBucket: true }));
  const counts = new Map();
  candidates.forEach((group) => counts.set(group.bucket, (counts.get(group.bucket) || 0) + 1));
  const complete = credentialingState.board?.facets?.directoryCounts;
  const lineCount = complete?.lineCount ?? cards.reduce((sum, card) => sum + card.parkedLineCount, 0);
  const providerCount = complete?.providerCount ?? cards.length;
  const bucketGroupCount = complete?.bucketGroupCount ?? candidates.length;
  const bucketCounts = complete?.bucketCounts ? complete.bucketCounts.map((entry) => [entry.bucket, entry.count]) : Array.from(counts.entries());
  return "<section class=\"panel workflow-directory-overview\" aria-label=\"Credentialing breakdown\"><strong>Filtered results: " + Formatters.int(providerCount) + (providerCount === 1 ? " provider · " : " providers · ")
    + Formatters.int(lineCount) + (lineCount === 1 ? " parked line" : " parked lines") + "</strong>"
    + "<div class=\"workflow-directory-breakdown\" aria-label=\"Credentialing status filters\"><span>Provider / payer pairs:</span>"
    + [["All", bucketGroupCount]].concat(bucketCounts).map(([bucket, count]) => "<button type=\"button\" class=\"ghost-button\" data-cred-bucket=\"" + escapeHtml(bucket) + "\" aria-pressed=\"" + (credentialingState.bucketFilter === bucket) + "\">"
      + escapeHtml(bucket === "All" ? "All statuses" : CRED_BUCKET_LABELS[bucket]?.[0] || bucket) + " (" + Formatters.int(count) + ")</button>").join("")
    + "</div></section>";
}

function authorizationPayerOptions() {
  if (Array.isArray(authorizationsState.board?.facets?.payerOptions)) return authorizationsState.board.facets.payerOptions;
  const seen = new Map();
  authorizationPatients().forEach((patient) => {
    (patient.payers || []).forEach((payer) => {
      const value = String(payer.payerName || "");
      if (!value || seen.has(value)) return;
      seen.set(value, String(payer.payerLabel || payer.payerName || value));
    });
  });
  return Array.from(seen.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
}

function authorizationStatusOptionsWithCounts(patients = []) {
  const counts = new Map();
  patients.forEach((patient) => {
    const status = String(patient.worstStatus || "").trim();
    if (!status) return;
    counts.set(status, (counts.get(status) || 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([status, count]) => ({ status, count }));
}

function authorizationVisiblePatients(options = {}) {
  const search = String(authorizationsState.search || "").trim().toUpperCase();
  const statusFilter = String(authorizationsState.statusFilter || "All");
  const payerFilter = String(authorizationsState.payerFilter || "All");
  // D2: the Company selector FILTERS the board. It used to filter nothing at
  // all while the header named one company over every other tenant's patients.
  const orgFilter = activeAuthorizationOrgId();
  return authorizationPatients().filter((patient) => {
    if (authorizationPatientIsResolved(patient)) return false;
    if (orgFilter && String(patient.orgId || "") && String(patient.orgId) !== orgFilter) return false;
    if (search && !String(patient.patientLabel || patient.patientKey || "").toUpperCase().includes(search)) return false;
    if (!options.ignoreStatus && statusFilter !== "All" && String(patient.worstStatus || "") !== statusFilter) return false;
    if (payerFilter !== "All" && !(patient.payers || []).some((payer) => String(payer.payerName || "") === payerFilter)) return false;
    if (!options.ignorePatient && authorizationsState.patientFilter && authorizationPatientCardKey(patient) !== authorizationsState.patientFilter) return false;
    return true;
  }).sort((a, b) => (authorizationsState.sortOrder === "lines" && !options.alphabetical ? authzNeedsLineCount(b) - authzNeedsLineCount(a) : 0)
    || String(a.patientLabel || a.patientKey || "").localeCompare(String(b.patientLabel || b.patientKey || ""))
    || authorizationPatientCardKey(a).localeCompare(authorizationPatientCardKey(b)));
}

function authorizationResolvedPatients() {
  return authorizationPatients().filter(authorizationPatientIsResolved);
}

// ---------------------------------------------------------------------------
// R1 — density. Compact is the default; the biller's explicit choices are
// the only thing persisted (per user, per org).
// ---------------------------------------------------------------------------
function authorizationCollapseStorageKey() {
  const user = (typeof activeInternalUser === "function" ? activeInternalUser() : "") || String(state?.role || "session");
  return `${AUTHZ_COLLAPSE_STORAGE_PREFIX}${user}.${activeAuthorizationOrgId() || "all"}`;
}

function loadAuthorizationCollapseState() {
  const key = authorizationCollapseStorageKey();
  if (authorizationsState.collapseLoadedFor === key) return;
  authorizationsState.collapseLoadedFor = key;
  authorizationsState.collapseOverrides = {};
  try {
    const raw = safeLocalStorageGet(key);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object") authorizationsState.collapseOverrides = parsed;
  } catch (_e) { /* a corrupt preference must never break the board */ }
}

function saveAuthorizationCollapseState() {
  try { safeLocalStorageSet(authorizationCollapseStorageKey(), JSON.stringify(authorizationsState.collapseOverrides || {})); } catch (_e) {}
}

// Keep the directory compact; opening one patient exposes their work immediately.
function authorizationPatientCollapsedByDefault(patient = {}, visibleCount = 0) {
  return visibleCount > 1 && !authorizationsState.patientFilter;
}

function authorizationPatientCollapsed(patient = {}, visibleCount = 0) {
  const key = authorizationPatientCardKey(patient);
  const override = authorizationsState.collapseOverrides[key];
  if (override === true || override === false) return override;
  return authorizationPatientCollapsedByDefault(patient, visibleCount);
}

function setAllAuthorizationCardsCollapsed(collapsed) {
  const next = { ...(authorizationsState.collapseOverrides || {}) };
  authorizationVisiblePatients().forEach((patient) => { next[authorizationPatientCardKey(patient)] = Boolean(collapsed); });
  authorizationsState.collapseOverrides = next;
  saveAuthorizationCollapseState();
  renderAuthorizations();
}

// ---------------------------------------------------------------------------
// THE SEND LANE (owner directive D2) — the TMS side of the authorization path.
//
// The client's billing specialist records an authorization, and that is where
// the tracked path used to STOP. The claims still have to be sent out of
// EZClaim by a TMS biller, and the only representation of that step was one more
// value in the same status dropdown: a label anyone could type, carrying no
// actor, no timestamp and no reference.
//
// This surface is the task. It reads /api/authorizations/submission-queue (the
// server derives one task per released authorization still holding on-hold
// lines) and posts one transition at a time to /api/authorizations/submission-task.
//
// TWO THINGS ARE DELIBERATE:
//   * "Confirmed" is not an action on this screen. Only the source data can say
//     the claims left the authorization lane, so the server derives it and this
//     UI reports it. A biller can say "I sent it"; only the warehouse can say
//     "it went".
//     SINCE THE 2026-08-11 CONJUNCTION THERE ARE TWO WAYS TO LEAVE THE LANE:
//     the placeholder was replaced with a real authorization number, OR the
//     claim stopped being On Hold (billed out, often toward a write-off). The
//     copy below must not claim an authorization exists when it may not — it
//     says the lines are no longer waiting, which is what the lane proves.
//   * The queue is fetched ONCE per session, not on a timer. That GET persists
//     the confirmations it derives, so it is a bounded write and polling it would
//     turn a read into a repeating one. The single fetch is what lets the nav
//     badge push work at a biller who never opened this tab; Refresh is how they
//     ask for more.
// ---------------------------------------------------------------------------
const authorizationSendState = {
  status: "idle",           // idle | loading | ready | error
  error: "",
  tasks: [],
  alerts: null,
  truncated: false,
  autoConfirmSuppressed: false,
  // TRUE when the server has no send lane at all (no table / no portal-sql), as
  // opposed to a request that genuinely failed. Different cause, different tone,
  // and crucially a different answer to "is there anything I can do about it".
  notConfigured: false,
  generatedAtUtc: "",
  busyTaskId: "",
  actionError: "",
  // submissionTaskId -> { reference, reason, blocking }. Never cleared by a
  // failed save: a biller who typed a batch reference must not lose it because
  // the server said no.
  drafts: {},
  // submissionTaskId -> true while that row's claim lines are expanded.
  openDetails: {},
  lane: "open"
};

const AUTHZ_SEND_LANES = [
  { key: "open", label: "To bill", hint: "Approved and not yet billed out by TMS." },
  { key: "late", label: "Late", hint: "Waiting over a week to be billed out, or billed out and still sitting on hold." },
  { key: "sent", label: "Billed", hint: "Marked billed out — these close themselves once every claim shows as Submitted." },
  { key: "all", label: "All", hint: "Every bill-out task, including the ones that have closed." }
];

const AUTHZ_SEND_STATUS_PILL = {
  ready: { tone: "red", label: "To bill" },
  in_progress: { tone: "amber", label: "Being billed" },
  submitted: { tone: "blue", label: "Billed — awaiting confirmation" },
  blocked: { tone: "red", label: "Blocked" },
  confirmed: { tone: "green", label: "Confirmed — claims submitted" },
  cancelled: { tone: "gray", label: "Cancelled" }
};

function canSendAuthorizationClaims() {
  return hasPermission(PERMISSIONS.AUTHORIZATIONS_SUBMIT);
}

function authorizationSendAlerts() {
  return authorizationSendState.alerts || {};
}

// The badge number for a TMS biller. Confirmed and cancelled work is not work.
function authorizationSendActionableCount() {
  return Number(authorizationSendAlerts().actionable) || 0;
}

// The queue card's counts follow the Company filter, as its rows do: production
// read "3 approved authorizations waiting" over an empty Avenues lane because all
// 3 belonged to other companies (2026-11-02). The nav badge stays whole-book.
function authorizationSendScopedAlerts() {
  const alerts = authorizationSendAlerts();
  const orgFilter = activeAuthorizationOrgId();
  if (!orgFilter || !Array.isArray(alerts.byOrg)) return alerts;
  return alerts.byOrg.find((bucket) => String(bucket.orgId || "") === orgFilter) || {};
}

function authorizationSendDraft(taskId) {
  return authorizationSendState.drafts[taskId] || { reference: "", reason: "", blocking: false };
}

function setAuthorizationSendDraft(taskId, patch) {
  authorizationSendState.drafts[taskId] = { ...authorizationSendDraft(taskId), ...patch };
}

function requestAuthorizationSendQueue(options = {}) {
  // Never fetch for someone who cannot send: the route would 403, and a denial
  // in the access ledger for a request the user never made is noise in the one
  // record that has to stay readable.
  if (!canSendAuthorizationClaims()) return;
  const base = authorizationsApiBase();
  const rerender = () => {
    if (state.view === "authorizations") renderAuthorizations();
    renderReportWorkQueueSendAlert();
    updateAuthorizationsNavBadge();
  };
  if (!base || typeof fetch !== "function") {
    authorizationSendState.status = "error";
    authorizationSendState.error = "The portal is temporarily unavailable, so the send queue could not be loaded.";
    if (options.rerender !== false) rerender();
    return;
  }
  authorizationSendState.status = "loading";
  if (options.rerender !== false && state.view === "authorizations") renderAuthorizations();
  fetch(`${base}/api/authorizations/submission-queue`, {
    method: "GET",
    cache: "no-store",
    headers: authorizationsApiHeaders()
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      authorizationSendState.status = "error";
      authorizationSendState.error = "Your session expired. Sign in again — this page will still be here.";
    } else if (!response.ok || (payload && payload.error)) {
      authorizationSendState.status = "error";
      authorizationSendState.error = authorizationSendMessage(response.status, payload, "queue");
      authorizationSendState.notConfigured = authorizationSendNotConfigured(response.status, payload);
    } else {
      authorizationSendState.status = "ready";
      authorizationSendState.error = "";
      authorizationSendState.tasks = Array.isArray(payload.tasks) ? payload.tasks : [];
      authorizationSendState.alerts = payload.alerts || null;
      authorizationSendState.truncated = Boolean(payload.truncated);
      authorizationSendState.autoConfirmSuppressed = Boolean(payload.autoConfirmSuppressed);
      authorizationSendState.generatedAtUtc = String(payload.generatedAtUtc || "");
    }
    rerender();
  }).catch(() => {
    authorizationSendState.status = "error";
    // A dead network IS retryable, so this path must not inherit a stale
    // not-configured verdict from an earlier response.
    authorizationSendState.notConfigured = false;
    authorizationSendState.error = "Could not reach the portal, so the send queue is not loaded.";
    rerender();
  });
}

// The send lane being absent from this server is a DEPLOYMENT state, not a fault:
// 404 (route not mounted), 501 (no persistence adapter) and the explicit
// not-configured code all mean "there is nothing here yet", which no amount of
// retrying changes. A transient failure - 401, 409, a dead network - is a
// different thing and keeps its retry.
function authorizationSendNotConfigured(status, payload = {}) {
  const code = String((payload && payload.error) || "");
  return code === "authorization_persistence_not_configured" || code === "not_found" || status === 404 || status === 501;
}

function authorizationSendMessage(status, payload = {}, context = "task") {
  const code = String((payload && payload.error) || "");
  const serverMessage = String((payload && payload.message) || "").trim();
  // The server REFUSED rather than resolve against a capped population. Nothing
  // was written, so nothing has to be un-done, and every typed value survives.
  if (code === "onhold_population_truncated") {
    return serverMessage || "There are too many on-hold lines to change this task safely right now, so nothing was changed.";
  }
  if (code === "authorization_persistence_not_configured" || status === 501) {
    return "The send queue is not set up on this server yet, so there is nothing to show here. Nothing has been lost — the authorizations themselves are unaffected.";
  }
  if (status === 401) return "Your session expired. Sign in again — nothing was changed.";
  if (status === 403) return "You are not allowed to send claims for these companies.";
  // Same status, two different truths, depending on which route answered.
  if (status === 404) {
    return context === "queue"
      ? "The send queue is not set up on this server yet, so there is nothing to show here. Nothing has been lost — the authorizations themselves are unaffected."
      : "That send task is no longer in your companies. Refresh the queue.";
  }
  if (status === 409) return serverMessage || "That task has already moved. Refresh the queue to see where it is now.";
  return serverMessage || "The send queue could not be loaded. Try again.";
}

function sendAuthorizationTaskAction(submissionTaskId, action) {
  const base = authorizationsApiBase();
  if (!base || typeof fetch !== "function") {
    authorizationSendState.actionError = "The portal is temporarily unavailable, so nothing was changed.";
    renderAuthorizations();
    return;
  }
  const draft = authorizationSendDraft(submissionTaskId);
  authorizationSendState.busyTaskId = submissionTaskId;
  authorizationSendState.actionError = "";
  renderAuthorizations();
  fetch(`${base}/api/authorizations/submission-task`, {
    method: "POST",
    headers: authorizationsApiHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({
      action,
      submissionTaskId,
      submissionReference: draft.reference || "",
      reason: draft.reason || ""
    })
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    authorizationSendState.busyTaskId = "";
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      authorizationSendState.actionError = "Your session expired. Sign in again — this page will still be here.";
      renderAuthorizations();
      return;
    }
    if (!response.ok || (payload && payload.error)) {
      // The draft is deliberately left intact — see authorizationSendState.drafts.
      authorizationSendState.actionError = authorizationSendMessage(response.status, payload);
      renderAuthorizations();
      return;
    }
    delete authorizationSendState.drafts[submissionTaskId];
    showToast(authorizationSendActionToast(action));
    // Show the saved change at once (Nathan, 2026-11-02: the row sat unchanged
    // until the re-read came back), then re-read: the counts on every other task
    // are derived from the same on-hold population.
    authorizationSendApplySavedTask(submissionTaskId, payload);
    requestAuthorizationSendQueue({ rerender: false });
    renderAuthorizations();
  }).catch(() => {
    authorizationSendState.busyTaskId = "";
    authorizationSendState.actionError = "Could not reach the portal, so nothing was changed.";
    renderAuthorizations();
  });
}

function authorizationSendApplySavedTask(submissionTaskId, payload = {}) {
  const saved = payload.task;
  const tasks = authorizationSendState.tasks;
  const index = Array.isArray(tasks) ? tasks.findIndex((task) => task.submissionTaskId === submissionTaskId) : -1;
  if (!saved || index < 0 || !saved.status) return;
  const prior = tasks[index];
  tasks[index] = { ...prior, ...saved, actionable: !["confirmed", "cancelled"].includes(saved.status) };
  const counter = { ready: "ready", in_progress: "inProgress", submitted: "submitted", blocked: "blocked", cancelled: "cancelled" };
  const alerts = authorizationSendState.alerts;
  if (!alerts || prior.status === saved.status) return;
  const buckets = [alerts].concat((alerts.byOrg || []).filter((bucket) => String(bucket.orgId || "") === String(prior.orgId || "")));
  buckets.forEach((bucket) => {
    if (counter[prior.status]) bucket[counter[prior.status]] = Math.max(0, (Number(bucket[counter[prior.status]]) || 0) - 1);
    if (counter[saved.status]) bucket[counter[saved.status]] = (Number(bucket[counter[saved.status]]) || 0) + 1;
    if (saved.status === "cancelled") bucket.actionable = Math.max(0, (Number(bucket.actionable) || 0) - 1);
  });
}

function authorizationSendActionToast(action) {
  if (action === "submit") return "Marked billed out.";
  if (action === "claim") return "You are sending this batch.";
  if (action === "release") return "Put back in the queue.";
  if (action === "block") return "Blocked, with the reason recorded.";
  if (action === "unblock") return "Unblocked — back in the queue.";
  if (action === "reopen") return "The send was retracted.";
  if (action === "cancel") return "Cancelled, with the reason recorded.";
  return "Saved.";
}

function authorizationSendTasksForLane() {
  // Lines still carrying CPH wait on the Credentialing board, not on TMS.
  const tasks = (Array.isArray(authorizationSendState.tasks) ? authorizationSendState.tasks : []).filter((task) => !task.waitingOnCredentialing);
  // The Company selector is a FILTER and never a write target (ADDENDUM II D2).
  // Named orgFilter, not orgId, so it can never be mistaken for the org a save
  // uses - every send action resolves its org server-side from the task itself.
  const orgFilter = activeAuthorizationOrgId();
  const scoped = orgFilter ? tasks.filter((task) => String(task.orgId || "") === orgFilter) : tasks;
  if (authorizationSendState.lane === "all") return scoped;
  if (authorizationSendState.lane === "late") return scoped.filter((task) => task.overdueToSend || task.stalledAfterSend);
  if (authorizationSendState.lane === "sent") return scoped.filter((task) => task.status === "submitted");
  return scoped.filter((task) => task.actionable && task.status !== "submitted");
}

function authorizationSendQueueMarkup() {
  if (!canSendAuthorizationClaims()) return "";
  const st = authorizationSendState;
  const alerts = authorizationSendScopedAlerts();
  const actionable = Number(alerts.actionable) || 0;
  const late = (Number(alerts.overdueToSend) || 0) + (Number(alerts.stalledAfterSend) || 0);
  // `actionable` includes billed-out tasks still awaiting confirmation
  // (ACTIONABLE_SUBMISSION_STATUSES has "submitted"), which is right for the
  // badge and wrong for this sentence: production read "1 approved authorization
  // waiting for TMS to submit the claims" over an empty "To bill" lane because
  // the one task was already billed out (2026-10-21). Say which is which.
  const submitted = Number(alerts.submitted) || 0;
  const awaitingSend = Math.max(0, actionable - submitted);
  const loading = st.status === "loading";
  // "Loaded" is a precondition for stating a count at all. Zero is an answer;
  // not knowing is a different answer, and they must not render the same.
  const loaded = st.status === "ready" && Boolean(st.alerts);

  let body = "";
  if (st.status === "error") {
    // No retry button on the not-configured branch: an affordance that cannot
    // work is worse than none, because it invites the biller to keep pressing it.
    body = st.notConfigured
      ? `<div class="authz-send-notice authz-send-notice--info">
        <p>${escapeHtml(st.error)}</p>
      </div>`
      : `<div class="authz-send-notice authz-send-notice--error">
        <p>${escapeHtml(st.error)}</p>
        <button id="authzSendRetryBtn" class="ghost-button" type="button">Try again</button>
      </div>`;
  } else if (st.status === "idle" || (loading && !st.tasks.length)) {
    body = `<p class="authz-send-empty-note">Loading the send queue…</p>`;
  } else {
    const rows = authorizationSendTasksForLane();
    body = rows.length
      ? `<div class="authz-send-list">${rows.map(authorizationSendRowMarkup).join("")}</div>`
      : `<p class="authz-send-empty-note">${escapeHtml(authorizationSendEmptyCopy())}</p>`;
  }

  return `
    <section class="panel authz-send-panel">
      <div class="authz-send-head">
        <div>
          <h3>TMS to do: claims to bill out</h3>
          <p class="authz-send-basis">
            ${!loaded
              ? "How much is waiting is not known yet — the queue has not loaded."
              : (awaitingSend
                ? `<strong>${Formatters.int(awaitingSend)}</strong> approved authorization${awaitingSend === 1 ? "" : "s"} waiting for TMS to submit the claims${submitted ? ` · <strong>${Formatters.int(submitted)}</strong> billed out, awaiting confirmation` : ""}${late ? ` · <strong>${Formatters.int(late)}</strong> past the 7-day mark` : ""}`
                : (submitted
                  ? `<strong>${Formatters.int(submitted)}</strong> billed out, awaiting confirmation${late ? ` · <strong>${Formatters.int(late)}</strong> past the 7-day mark` : ""}`
                  : "Nothing is waiting to be sent."))}
          </p>
        </div>
        <button id="authzSendRefreshBtn" class="ghost-button" type="button" ${loading ? "disabled" : ""}>${loading ? "Refreshing…" : "Refresh"}</button>
      </div>
      ${st.autoConfirmSuppressed ? `<p class="authz-send-notice authz-send-notice--warn">There are more on-hold lines than one read can carry, so nothing is being confirmed automatically right now and no task can be changed. The counts below are still worth reading; the closures are not.</p>` : ""}
      ${st.actionError ? `<p class="authz-send-notice authz-send-notice--error">${escapeHtml(st.actionError)}</p>` : ""}
      <div class="authz-send-lanes" role="group" aria-label="Send queue lanes">
        ${AUTHZ_SEND_LANES.map((lane) => `
          <button type="button" class="authz-send-lane${st.lane === lane.key ? " is-active" : ""}" data-authz-send-lane="${escapeHtml(lane.key)}" aria-pressed="${st.lane === lane.key ? "true" : "false"}" title="${escapeHtml(lane.hint)}">${escapeHtml(lane.label)}</button>`).join("")}
      </div>
      ${body}
    </section>`;
}

function authorizationSendEmptyCopy() {
  if (authorizationSendState.lane === "late") return "Nothing is late. Every released authorization is inside the 7-day window.";
  if (authorizationSendState.lane === "sent") return "Nothing is waiting on confirmation.";
  if (authorizationSendState.lane === "all") return "No authorization has been released for sending yet.";
  return "Nothing is waiting to be billed out.";
}

function authorizationSendRowMarkup(task) {
  const id = String(task.submissionTaskId || "");
  const pill = AUTHZ_SEND_STATUS_PILL[task.status] || { tone: "gray", label: task.status || "unknown" };
  const draft = authorizationSendDraft(id);
  const busy = authorizationSendState.busyTaskId === id;
  const remaining = Number(task.remainingLineCount) || 0;
  const money = authzMoney(Number(task.remainingExpectedAmount) || 0);
  const flags = [];
  if (task.overdueToSend) flags.push(`<span class="pill amber">${Formatters.int(task.ageDays || 0)} days waiting to be sent</span>`);
  if (task.stalledAfterSend) flags.push(`<span class="pill amber">Billed out ${Formatters.int(task.awaitingConfirmationDays || 0)} days ago, still on hold</span>`);
  // Returned to To bill by the server: marked billed, still placeholder 5 days on.
  if (task.autoReturnNote) flags.push(`<span class="pill amber" data-authz-auto-return>${escapeHtml(task.autoReturnNote)}</span>`);
  if (task.partiallyCleared) flags.push(`<span class="pill blue">${Formatters.int(task.clearedLineCount || 0)} of ${Formatters.int(task.linkedLineCount || 0)} lines already cleared</span>`);
  // A send nobody logged in the portal. The work is done, so the task closed --
  // but the gap is shown rather than papered over.
  if (task.confirmedWithoutSubmitRecord) flags.push(`<span class="pill gray">Submitted, but nobody marked it billed out here</span>`);

  return `
    <article class="authz-send-row${busy ? " is-busy" : ""}" data-authz-send-task="${escapeHtml(id)}">
      <div class="authz-send-row-main">
        <div class="authz-send-row-identity">
          <strong>${escapeHtml(task.patientKey || "Unnamed patient")}</strong>
          <span>${escapeHtml(task.payerName || "Payer unknown")}</span>
          <span class="authz-send-org">${escapeHtml(authorizationOrgLabel(task.orgId) || portalCompanyLabel(task.orgId, ""))}</span>
        </div>
        <div class="authz-send-row-figures">
          <span class="pill ${pill.tone}">${escapeHtml(pill.label)}</span>
          <span class="authz-send-count">${Formatters.int(remaining)} line${remaining === 1 ? "" : "s"} still on hold</span>
          <span class="authz-send-money">${escapeHtml(money)}</span>
        </div>
      </div>
      ${flags.length ? `<div class="authz-send-flags">${flags.join("")}</div>` : ""}
      ${task.blockedReason ? `<p class="authz-send-blocked">Blocked: ${escapeHtml(task.blockedReason)}</p>` : ""}
      ${task.submissionReference ? `<p class="authz-send-reference">Billing reference <strong>${escapeHtml(task.submissionReference)}</strong></p>` : ""}
      ${authorizationSendAuthDetailMarkup(task)}
      ${authorizationSendRowActionsMarkup(task, draft, busy)}
    </article>`;
}

// What the biller needs to bill the claims out, on the row itself: the auth
// number (copyable), its date range, the attached document and, one click
// away, the exact claim lines it covers. Without this the biller had to find
// the same patient again on the board below and read a small grey card.
function authorizationSendAuthDetailMarkup(task) {
  const id = String(task.submissionTaskId || "");
  const auth = task.authorization || null;
  if (!auth) return "";
  const authId = String(task.authorizationId || "");
  const files = Array.isArray(auth.files) ? auth.files.filter((file) => file && file.authorizationFileId) : [];
  const lines = Array.isArray(task.lines) ? task.lines : [];
  const open = Boolean(authorizationSendState.openDetails[id]);
  const fileSlot = files.length
    ? files.map((file, index) => {
      const filename = String(file.originalFilename || `authorization-file-${index + 1}`);
      return `<button class="ghost-button authz-send-file" type="button" data-authz-download-file="${escapeHtml(file.authorizationFileId)}" data-authz-download-authorization="${escapeHtml(authId)}" data-authz-download-name="${escapeHtml(filename)}" title="Download ${escapeHtml(filename)}">📎 ${escapeHtml(filename)}</button>`;
    }).join("")
    : (Number(auth.fileCount || 0) > 0
      ? `<span class="authz-file-slot failed">📎 ${Formatters.int(auth.fileCount)} file${Number(auth.fileCount) === 1 ? "" : "s"} unavailable — refresh</span>`
      : `<span class="authz-file-slot none">No file attached</span>`);
  const range = auth.effectiveStartDate || auth.effectiveEndDate
    ? `${authzFullDate(auth.effectiveStartDate)} → ${authzFullDate(auth.effectiveEndDate)}`
    : "No date range";
  return `
    <div class="authz-send-auth">
      <div class="authz-send-auth-main">
        <span class="authz-send-auth-label">Auth #</span>
        ${auth.authNumber
          ? `<strong class="authz-send-auth-number">${escapeHtml(auth.authNumber)}</strong>
            <button class="authz-link-button" type="button" data-authz-send-copy="${escapeHtml(auth.authNumber)}" title="Copy the auth number">Copy</button>`
          : `<span class="authz-send-auth-missing">no auth number recorded</span>`}
        <span class="authz-send-auth-range">${escapeHtml(range)}</span>
        ${auth.cptCodes && auth.cptCodes.length ? `<span class="authz-send-auth-cpt">CPT ${escapeHtml(auth.cptCodes.join(", "))}</span>` : ""}
      </div>
      <div class="authz-send-auth-files">${fileSlot}</div>
      ${auth.note ? `<p class="authz-send-auth-note">${escapeHtml(auth.note)}</p>` : ""}
      ${lines.length ? `
        <button class="authz-link-button authz-view-lines" type="button" data-authz-send-details="${escapeHtml(id)}" aria-expanded="${open ? "true" : "false"}">
          ${open ? "Hide claims" : `Show claims to bill (${Formatters.int(lines.length)})`}
        </button>
        ${open ? authorizationSendLineTableMarkup(task, lines) : ""}` : ""}
    </div>`;
}

function authorizationSendLineTableMarkup(task, lines) {
  const drillable = hasPermission(PERMISSIONS.VIEW_WORKBENCH);
  const orgId = String(task.orgId || "");
  return `
    <div class="authz-line-table-wrap">
      <table class="authz-line-table">
        <thead>
          <tr>
            <th scope="col">DOS</th>
            <th scope="col">CPT</th>
            <th scope="col" class="num">Expected</th>
            <th scope="col">Claim / line</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          ${lines.map((line) => `
            <tr class="authz-line-row${drillable ? " drillable" : ""}${line.onHold ? "" : " is-cleared"}" ${drillable ? `data-authz-open-claim="${escapeHtml(String(line.claimId || ""))}" data-authz-service-id="${escapeHtml(String(line.serviceLineId || ""))}" data-authz-org-id="${escapeHtml(orgId)}" tabindex="0" role="button"` : ""}>
              <td>${escapeHtml(authzFullDate(line.dos))}</td>
              <td>${escapeHtml(line.cptCode || "—")}</td>
              <td class="num">${line.expectedAmount == null ? moneyUnknownHtml(AUTHZ_NO_EXPECTED_REASON) : escapeHtml(authzMoney(line.expectedAmount))}</td>
              <td>${escapeHtml(String(line.claimId || "—"))}${line.serviceLineId ? ` / ${escapeHtml(String(line.serviceLineId))}` : ""}</td>
              <td>${line.onHold ? `<span class="pill amber">On hold</span>` : `<span class="pill gray">No longer on hold</span>`}</td>
            </tr>`).join("")}
        </tbody>
      </table>
    </div>`;
}

function authorizationSendRowActionsMarkup(task, draft, busy) {
  const id = escapeHtml(String(task.submissionTaskId || ""));
  const disabled = busy || authorizationSendState.autoConfirmSuppressed ? "disabled" : "";
  if (task.status === "confirmed" || task.status === "cancelled") {
    return `<p class="authz-send-closed">This task is closed. ${task.status === "confirmed"
      ? "Every claim now shows as Submitted. That is the only thing that can close this task."
      : "It was cancelled."}</p>`;
  }
  if (draft.blocking) {
    return `
      <div class="authz-send-actions">
        <label class="authz-send-input">
          <span>Why is this stuck?</span>
          <input type="text" data-authz-send-reason="${id}" value="${escapeHtml(draft.reason || "")}" placeholder="Payer portal down, billing rejection, missing insured ID…" maxlength="500">
        </label>
        <button class="ghost-button" type="button" data-authz-send-action="block" data-authz-send-id="${id}" ${disabled}>Save block</button>
        <button class="authz-link-button" type="button" data-authz-send-action="block-cancel" data-authz-send-id="${id}">Never mind</button>
      </div>`;
  }
  if (task.status === "blocked") {
    return `<div class="authz-send-actions">
        <button class="ghost-button" type="button" data-authz-send-action="unblock" data-authz-send-id="${id}" ${disabled}>Unblock</button>
      </div>`;
  }
  if (task.status === "submitted") {
    return `<div class="authz-send-actions">
        <p class="authz-send-hint">This stays open until every claim shows as Submitted, and is flagged if that takes more than 7 days.</p>
        <button class="authz-link-button" type="button" data-authz-send-action="reopen" data-authz-send-id="${id}" ${disabled}>It was not billed out after all</button>
        <button class="authz-link-button" type="button" data-authz-send-action="block-start" data-authz-send-id="${id}">Block</button>
      </div>`;
  }
  // ready | in_progress — the actual send.
  return `
    <div class="authz-send-actions">
      <button class="primary-button" type="button" data-authz-send-action="submit" data-authz-send-id="${id}" ${disabled}>Mark billed out</button>
      ${task.status === "ready"
        ? `<button class="authz-link-button" type="button" data-authz-send-action="claim" data-authz-send-id="${id}" ${disabled}>I am doing this one</button>`
        : `<button class="authz-link-button" type="button" data-authz-send-action="release" data-authz-send-id="${id}" ${disabled}>Put back</button>`}
      <button class="authz-link-button" type="button" data-authz-send-action="block-start" data-authz-send-id="${id}">Block</button>
    </div>`;
}

function bindAuthorizationSendQueueControls(host) {
  if (!host) return;
  host.querySelector("#authzSendRefreshBtn")?.addEventListener("click", () => requestAuthorizationSendQueue());
  host.querySelector("#authzSendRetryBtn")?.addEventListener("click", () => requestAuthorizationSendQueue());
  host.querySelectorAll("[data-authz-send-lane]").forEach((button) => {
    button.addEventListener("click", () => {
      authorizationSendState.lane = String(button.getAttribute("data-authz-send-lane") || "open");
      renderAuthorizations();
    });
  });
  // Typed values live in state, not only in the DOM, so a re-render never eats a
  // half-typed batch reference.
  host.querySelectorAll("[data-authz-send-reference]").forEach((input) => {
    input.addEventListener("input", (event) => {
      setAuthorizationSendDraft(String(input.getAttribute("data-authz-send-reference") || ""), { reference: String(event.target.value || "") });
    });
  });
  host.querySelectorAll("[data-authz-send-reason]").forEach((input) => {
    input.addEventListener("input", (event) => {
      setAuthorizationSendDraft(String(input.getAttribute("data-authz-send-reason") || ""), { reason: String(event.target.value || "") });
    });
  });
  host.querySelectorAll("[data-authz-send-details]").forEach((button) => {
    button.addEventListener("click", () => {
      const id = String(button.getAttribute("data-authz-send-details") || "");
      if (!id) return;
      authorizationSendState.openDetails[id] = !authorizationSendState.openDetails[id];
      renderAuthorizations();
    });
  });
  host.querySelectorAll("[data-authz-send-copy]").forEach((button) => {
    button.addEventListener("click", async () => {
      const value = String(button.getAttribute("data-authz-send-copy") || "");
      try {
        await navigator.clipboard.writeText(value);
        showToast(`Auth # ${value} copied.`);
      } catch (_error) {
        showToast("Could not copy. Select the number instead.");
      }
    });
  });
  host.querySelectorAll("[data-authz-send-action]").forEach((button) => {
    button.addEventListener("click", () => {
      const id = String(button.getAttribute("data-authz-send-id") || "");
      const action = String(button.getAttribute("data-authz-send-action") || "");
      if (!id || !action) return;
      if (action === "block-start") { setAuthorizationSendDraft(id, { blocking: true }); renderAuthorizations(); return; }
      if (action === "block-cancel") { setAuthorizationSendDraft(id, { blocking: false, reason: "" }); renderAuthorizations(); return; }
      sendAuthorizationTaskAction(id, action);
    });
  });
}

// The strip that carries the send queue into the biller's ranked work surface.
// It does NOT become a lane of the claim-queue drilldown: that lane is per-CLAIM
// server rank from a different endpoint, and a send task is per-AUTHORIZATION.
// Pretending one is the other would put two grains in one table.
function renderReportWorkQueueSendAlert() {
  const host = document.getElementById("reportWorkQueueSendAlert");
  if (!host) return;
  const count = authorizationSendActionableCount();
  const show = canSendAuthorizationClaims() && viewIsAvailable("authorizations") && count > 0;
  host.hidden = !show;
  if (!show) { host.innerHTML = ""; return; }
  const alerts = authorizationSendAlerts();
  const late = (Number(alerts.overdueToSend) || 0) + (Number(alerts.stalledAfterSend) || 0);
  host.innerHTML = `
    <p class="report-work-queue-send-alert-text">
      <strong>${Formatters.int(count)}</strong> authorization${count === 1 ? " is" : "s are"} approved and the claims still need to be billed out${late ? ` · <strong>${Formatters.int(late)}</strong> past the 7-day mark` : ""}.
    </p>
    <button id="reportWorkQueueSendAlertBtn" class="ghost-button" type="button">Open the send queue</button>`;
  host.querySelector("#reportWorkQueueSendAlertBtn")?.addEventListener("click", () => navigateToView("authorizations"));
}

function renderNavigationPendingBadge({ badgeId, view, label, feature }) {
  const badge = document.getElementById(badgeId);
  if (!badge) return;
  const navItem = typeof badge.closest === "function" ? badge.closest(".nav-item") : null;
  const allowed = viewIsAvailable(view);
  const loading = allowed && navigationPendingCountState.status === "loading";
  const ready = allowed && navigationPendingCountState.status === "ready" && feature?.available === true;
  const total = ready ? Math.max(0, Number(feature.total) || 0) : 0;
  badge.textContent = total > 99 ? "99+" : String(total);
  badge.hidden = !(ready && total > 0);
  badge.setAttribute("aria-hidden", "true");
  if (!navItem) return;
  if (loading) navItem.setAttribute("aria-busy", "true");
  else navItem.removeAttribute("aria-busy");
  const stateLabel = loading
    ? "pending count loading"
    : ready
      ? (total > 0 ? String(total) + " pending item" + (total === 1 ? "" : "s") : "no pending items")
      : navigationPendingCountState.status === "error" || navigationPendingCountState.status === "ready"
        ? "pending count unavailable"
        : "pending count not loaded";
  navItem.setAttribute("aria-label", label + ", " + stateLabel);
}

function updateAuthorizationsNavBadge() {
  renderNavigationPendingBadge({
    badgeId: "authorizationsNavBadge",
    view: "authorizations",
    label: "Authorizations",
    feature: navigationPendingCountState.authorization
  });
}

// ===========================================================================
// CREDENTIALING HOLD (CPH) — the provider board.
// ===========================================================================
// WHAT THIS IS. The sibling of the Authorizations board, for the OTHER hold. A
// claim parks here because the RENDERING PROVIDER is not (yet) credentialed with
// the payer being billed. The client records that they now are — with an
// EFFECTIVE DATE — and the claims dated on or after it become a bill-out task
// for a TMS biller.
//
// GROUPED BY PROVIDER, because that is the unit of the problem: a therapist is
// credentialed or they are not, and every claim of theirs parks together. Under
// each provider the rows stay PER PAYER, and that is not a detail — a therapist
// in network with one payer and not another is normal, and a provider-only
// release would send the claims of the payer they are NOT credentialed with,
// which the payer then denies weeks later.
//
// PHI. The server never sends this screen the raw authorization field (a biller
// scratchpad carrying patient names and member ids). Routing already happened
// server-side on a derived enum. The one PHI element here is the patient name on
// a line, which exists so a human can verify WHICH claims a decision will move.
// ---------------------------------------------------------------------------

const CRED_STATUS = {
  NEEDS_REVIEW: "Needs Review",
  CREDENTIALED: "Credentialed",
  NOT_CREDENTIALED: "Not Credentialed"
};

// The four buckets the server computes. Labels are the biller's words, not the
// enum's, and each carries the sentence that says what to DO about it.
const CRED_BUCKET_LABELS = {
  no_relationship: ["Not recorded yet", "Nobody has said whether this therapist is credentialed with this payer."],
  needs_review: ["Needs review", "Recorded as under review — the payer has not answered yet."],
  not_credentialed: ["Not credentialed", "Recorded as out of network with this payer. These claims cannot be billed as they stand."],
  parked_pre_effective: ["Parked before the effective date", "Credentialed — but these dates of service fall BEFORE the effective date, so they stay parked on purpose."]
};

const CRED_BUCKET_TONE = {
  no_relationship: "gray",
  needs_review: "amber",
  not_credentialed: "red",
  parked_pre_effective: "blue"
};

const CRED_LINE_PREVIEW_CAP = 25;

const credentialingState = {
  status: "idle", // idle | loading | ready | error
  error: "",
  board: null,
  generatedAtUtc: "",
  overlayConfigured: false,
  // Every named condition the server can report. Held separately from `board`
  // so the surface can say WHICH of them it is looking at — "no credentialing
  // work", "no credentialing signal" and "the scan never ran" are three
  // different facts that all render as zero if you let them.
  truncated: false,
  authorizationFieldUnavailable: false,
  claimStatusUnavailable: false,
  orphanScanUnavailable: false,
  orphanScanTruncated: false,
  refreshHeldOrgs: [],
  reviewAccess: {
    mode: "loading",
    canUpdate: false,
    reason: "Credentialing update access is being confirmed by the server.",
    membershipRole: ""
  },
  routingAccess: {
    configured: false,
    primaryMode: "unconfigured",
    escalationMode: "inherits_primary",
    emailEnabled: false,
    updatedAtUtc: "",
    version: 0
  },
  orgId: "",
  confirmedOrgId: "",
  requestSeq: 0,
  queueRequestSeq: 0,
  controller: null,
  boardRequestId: 0,
  queueRequestId: 0,
  search: "",
  bucketFilter: "All",
  providerFilter: "",
  sortOrder: "name",
  page: 0,
  directoryScope: null,
  openProviders: {},
  openLineTables: {},
  showAllLines: {},
  queue: { status: "idle", error: "", tasks: [], alerts: null },
  lastSave: null,
  pendingFileByDecisionId: {},
  pendingFileOrgByDecisionId: {},
  uploadFailures: {}
};

// The record drawer. Kept OUT of `state` so a half-typed credentialing decision
// is never written to localStorage (PHI/attribution discipline, same rule the
// authorization drawer follows).
const credentialingDrawerState = {
  open: false,
  relationshipId: "",
  orgId: "",
  orgLabel: "",
  providerKey: "",
  providerDisplay: "",
  payerKey: "",
  payerDisplay: "",
  payerExternalId: "",
  fromStatus: "",
  lines: [],
  form: { effectiveDate: "", reason: "" },
  file: null,
  fileError: "",
  saving: false,
  errors: [],
  errorBanner: ""
};

// Credentialing reviewer routing is deliberately transient. The server owns
// both the eligible same-company roster and the resulting access mode; the
// browser only edits the returned configuration and never turns an email into
// portal authority on its own.
const credentialingRoutingState = {
  open: false,
  status: "idle", // idle | loading | ready | error
  error: "",
  saving: false,
  savedMessage: "",
  orgId: "",
  configuration: null,
  eligibleUsers: [],
  linkResults: null,
  commandId: "",
  form: {
    primaryOwnerUserId: "",
    primaryOwnerEmail: "",
    escalationOwnerUserId: "",
    escalationOwnerEmail: "",
    isEmailEnabled: true
  }
};

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------
function credentialingApiBase() {
  return typeof trackerApiBase === "function" ? trackerApiBase() : "";
}

function credentialingApiHeaders(extra = {}) {
  const token = typeof trackerApiToken === "function" ? trackerApiToken() : "";
  return Object.assign({}, extra, token ? { authorization: `Bearer ${token}` } : {});
}

// A FRESH KEY PER DISTINCT SAVE. The server derives the decision id from
// sha256(org | relationship | idempotencyKey), so reusing a key is a REPLAY that
// returns the first decision and links nothing. Minted at the moment of saving,
// never stored, never reused across two different decisions.
function credentialingIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `cred-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function resetCredentialingForClientScope(orgId = "") {
  const nextOrgId = String(orgId || "").trim();
  if (nextOrgId === String(credentialingState.orgId || "").trim()) return false;
  try { credentialingState.controller?.abort(); } catch (_error) {}
  credentialingState.requestSeq += 1;
  credentialingState.queueRequestSeq += 1;
  credentialingState.controller = null;
  credentialingState.orgId = nextOrgId;
  credentialingState.confirmedOrgId = "";
  credentialingState.status = "idle";
  credentialingState.error = "";
  credentialingState.board = null;
  credentialingState.generatedAtUtc = "";
  credentialingState.overlayConfigured = false;
  credentialingState.truncated = false;
  credentialingState.authorizationFieldUnavailable = false;
  credentialingState.claimStatusUnavailable = false;
  credentialingState.orphanScanUnavailable = false;
  credentialingState.orphanScanTruncated = false;
  credentialingState.refreshHeldOrgs = [];
  credentialingState.reviewAccess = {
    mode: "loading",
    canUpdate: false,
    reason: "Credentialing update access is being confirmed by the server.",
    membershipRole: ""
  };
  credentialingState.routingAccess = {
    configured: false,
    primaryMode: "unconfigured",
    escalationMode: "inherits_primary",
    emailEnabled: false,
    updatedAtUtc: "",
    version: 0
  };
  credentialingState.lastSave = null;
  credentialingState.queue = { status: "idle", error: "", tasks: [], alerts: null };
  credentialingDrawerState.open = false;
  credentialingDrawerState.orgId = "";
  credentialingDrawerState.lines = [];
  credentialingDrawerState.file = null;
  credentialingDrawerState.fileError = "";
  credentialingDrawerState.errors = [];
  credentialingDrawerState.errorBanner = "";
  credentialingRoutingState.open = false;
  credentialingRoutingState.status = "idle";
  credentialingRoutingState.error = "";
  credentialingRoutingState.saving = false;
  credentialingRoutingState.savedMessage = "";
  credentialingRoutingState.orgId = "";
  credentialingRoutingState.configuration = null;
  credentialingRoutingState.eligibleUsers = [];
  credentialingRoutingState.linkResults = null;
  credentialingRoutingState.commandId = "";
  return true;
}

function requestCredentialingBoard(options = {}) {
  const readScope = additionalPortalDataReadScope();
  if (credentialingState.readScope && credentialingState.readScope !== readScope) resetCredentialingReadResponses();
  credentialingState.readScope = readScope;
  const requestId = ++credentialingState.boardRequestId;
  const base = credentialingApiBase();
  if (!base || typeof fetch !== "function") {
    credentialingState.status = "error"; credentialingState.errorAt = Date.now();
    credentialingState.error = "The portal is temporarily unavailable. Sign in again or contact TMS.";
    if (state.view === "credentialing") renderCredentialing();
    return;
  }
  try { credentialingState.controller?.abort(); } catch (_error) {}
  const requestSeq = ++credentialingState.requestSeq;
  const requestOrgId = String(credentialingState.orgId || "").trim();
  credentialingState.readScope = readScope;
  credentialingState.status = "loading";
  credentialingState.error = "";
  if (!options.preserveBoard) credentialingState.board = null;
  if (options.rerender !== false && state.view === "credentialing") renderCredentialing();
  const params = new URLSearchParams();
  if (requestOrgId) params.set("orgId", requestOrgId);
  params.set("page", String(credentialingState.page || 1)); params.set("pageSize", "50");
  params.set("search", String(credentialingState.search || "")); params.set("bucketFilter", credentialingState.bucketFilter || "All");
  params.set("providerFilter", credentialingState.providerFilter || ""); params.set("sortOrder", credentialingState.sortOrder || "name");
  const query = params.toString() ? `?${params.toString()}` : "";
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  credentialingState.controller = controller;
  const timeoutId = window.setTimeout(() => { try { controller?.abort(); } catch (_e) {} }, 60000);
  fetch(additionalPortalDataReadUrl(`${base}/api/credentialing/board${query}`), {
    method: "GET",
    cache: "no-store",
    headers: credentialingApiHeaders(),
    signal: controller ? controller.signal : undefined
  }).then(async (response) => {
    window.clearTimeout(timeoutId);
    if (readScope !== additionalPortalDataReadScope() || requestId !== credentialingState.boardRequestId) return;
    const payload = await response.json().catch(() => ({}));
    if (readScope !== additionalPortalDataReadScope() || requestSeq !== credentialingState.requestSeq || requestOrgId !== String(credentialingState.orgId || "").trim()) return;
    credentialingState.controller = null;
    const focusOrgId = String((payload && payload.focusOrgId) || "").trim();
    if (response.ok && !(payload && payload.error) && requestOrgId && focusOrgId !== requestOrgId) {
      credentialingState.status = "error"; credentialingState.errorAt = Date.now();
      credentialingState.error = "Credentialing could not confirm the selected company. Nothing was shown.";
      credentialingState.board = null;
    } else if (!response.ok || (payload && payload.error)) {
      credentialingState.status = "error"; credentialingState.errorAt = Date.now();
      credentialingState.error = credentialingHttpMessage(response.status, payload);
    } else {
      credentialingState.status = "ready";
      credentialingState.error = "";
      credentialingState.board = (payload && payload.board) || null;
      credentialingState.confirmedOrgId = focusOrgId;
      credentialingState.generatedAtUtc = String((payload && payload.generatedAtUtc) || "");
      credentialingState.overlayConfigured = Boolean(payload && payload.overlayConfigured);
      credentialingState.truncated = Boolean(payload && payload.truncated);
      credentialingState.authorizationFieldUnavailable = Boolean(payload && payload.authorizationFieldUnavailable);
      credentialingState.claimStatusUnavailable = Boolean(payload && payload.claimStatusUnavailable);
      credentialingState.orphanScanUnavailable = Boolean(payload && payload.orphanScanUnavailable);
      credentialingState.orphanScanTruncated = Boolean(payload && payload.orphanScanTruncated);
      credentialingState.refreshHeldOrgs = Array.isArray(payload && payload.refreshHeldOrgs) ? payload.refreshHeldOrgs : [];
      credentialingState.reviewAccess = (payload && payload.reviewAccess) || {
        mode: "access_unavailable",
        canUpdate: false,
        reason: "Credentialing update access could not be confirmed. The inventory remains view-only.",
        membershipRole: ""
      };
      credentialingState.routingAccess = (payload && payload.routingAccess) || {
        configured: false,
        primaryMode: "unconfigured",
        escalationMode: "inherits_primary",
        emailEnabled: false,
        updatedAtUtc: "",
        version: 0
      };
    }
    if (state.view === "credentialing") renderCredentialing();
    updateCredentialingNavBadge();
  }).catch((error) => {
    window.clearTimeout(timeoutId);
    if (readScope !== additionalPortalDataReadScope() || requestId !== credentialingState.boardRequestId) return;
    if (readScope !== additionalPortalDataReadScope() || requestSeq !== credentialingState.requestSeq || requestOrgId !== String(credentialingState.orgId || "").trim()) return;
    credentialingState.controller = null;
    credentialingState.status = "error"; credentialingState.errorAt = Date.now();
    credentialingState.error = (error && error.name === "AbortError")
      ? "The credentialing board took too long to load. Try again."
      : "Could not reach the portal. Check your connection and try again.";
    if (state.view === "credentialing") renderCredentialing();
  });
}

function requestCredentialingQueue(options = {}) {
  const readScope = additionalPortalDataReadScope();
  if (credentialingState.readScope && credentialingState.readScope !== readScope) resetCredentialingReadResponses();
  credentialingState.readScope = readScope;
  const requestId = ++credentialingState.queueRequestId;
  const base = credentialingApiBase();
  const requestOrgId = String(credentialingState.orgId || "").trim();
  const requestSeq = ++credentialingState.queueRequestSeq;
  if (!base || typeof fetch !== "function") {
    credentialingState.queue.status = "error";
    credentialingState.queue.error = "The portal is temporarily unavailable. Sign in again or contact TMS.";
    credentialingState.queue.tasks = [];
    if (options.rerender !== false && state.view === "credentialing") renderCredentialing();
    return;
  }
  credentialingState.queue.status = "loading";
  if (options.rerender !== false && state.view === "credentialing") renderCredentialing();
  const query = requestOrgId ? `?orgId=${encodeURIComponent(requestOrgId)}` : "";
  fetch(additionalPortalDataReadUrl(`${base}/api/credentialing/release-queue${query}`), {
    method: "GET",
    cache: "no-store",
    headers: credentialingApiHeaders()
  }).then(async (response) => {
    if (readScope !== additionalPortalDataReadScope() || requestId !== credentialingState.queueRequestId) return;
    const payload = await response.json().catch(() => ({}));
    if (readScope !== additionalPortalDataReadScope() || requestSeq !== credentialingState.queueRequestSeq || requestOrgId !== String(credentialingState.orgId || "").trim()) return;
    const focusOrgId = String((payload && payload.focusOrgId) || "").trim();
    if (response.ok && !(payload && payload.error)
        && ((requestOrgId && focusOrgId !== requestOrgId)
          || (!requestOrgId && credentialingState.confirmedOrgId && focusOrgId !== credentialingState.confirmedOrgId))) {
      credentialingState.queue.status = "error";
      credentialingState.queue.error = "The bill-out queue could not confirm the selected company. Nothing was shown.";
      credentialingState.queue.tasks = [];
    } else if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      credentialingState.queue.status = "error";
      credentialingState.queue.error = "Your session expired. Sign in again — this page will still be here.";
    } else if (!response.ok || (payload && payload.error)) {
      credentialingState.queue.status = "error";
      // THE QUEUE HAS ITS OWN SENTENCES, and it needs them: reusing the board's
      // 403 text told a TMS biller they had "no access to the credentialing
      // board" while they were looking straight at it. Each refusal here means
      // something different and only one of them is about permission.
      credentialingState.queue.error = credentialingQueueMessage(response.status, payload);
      credentialingState.queue.tasks = [];
    } else {
      credentialingState.queue.status = "ready";
      credentialingState.queue.error = "";
      if (!requestOrgId && focusOrgId) credentialingState.confirmedOrgId = focusOrgId;
      credentialingState.queue.tasks = Array.isArray(payload.tasks) ? payload.tasks : [];
      credentialingState.queue.alerts = payload.alerts || null;
    }
    if (state.view === "credentialing") renderCredentialing();
    updateCredentialingNavBadge();
  }).catch(() => {
    if (readScope !== additionalPortalDataReadScope() || requestId !== credentialingState.queueRequestId) return;
    if (readScope !== additionalPortalDataReadScope() || requestSeq !== credentialingState.queueRequestSeq || requestOrgId !== String(credentialingState.orgId || "").trim()) return;
    credentialingState.queue.status = "error";
    credentialingState.queue.error = "Could not reach the portal.";
    if (state.view === "credentialing") renderCredentialing();
  });
}

// The bill-out queue's OWN failures. Deliberately separate from the board's:
// a 403 on the queue is never "you cannot see credentialing" — the reader is
// looking at the board — it is an unfinished provisioning detail, and saying so
// sends the right person to the right fix.
function credentialingQueueMessage(status, payload = {}) {
  const code = String((payload && payload.error) || "").trim();
  const serverMessage = String((payload && payload.message) || "").trim();
  if (status === 503) return serverMessage || "The bill-out queue is not available yet — credentialing storage is still being set up.";
  if (code === "credentialing_out_of_scope") {
    return "The bill-out queue could not be listed: this account has no company list assigned yet. Ask TMS to finish setting it up — this is not about the board above, which is working.";
  }
  if (status === 403) return "You do not have the send permission for the bill-out queue.";
  if (status === 401) return "Your session expired. Sign in again to see the bill-out queue.";
  return String((payload && payload.message) || `The bill-out queue failed to load (HTTP ${status}).`);
}

function credentialingHttpMessage(status, payload = {}) {
  const serverMessage = String((payload && (payload.message || payload.error)) || "").trim();
  if (status === 401) {
    if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    return "Your session expired. Sign in again — this page will still be here.";
  }
  if (status === 403) return "You do not have access to the credentialing board for this company.";
  if (status === 501) return "Credentialing storage is not configured yet. TMS is finishing the setup.";
  return serverMessage || `The credentialing board failed to load (HTTP ${status}).`;
}

// ---------------------------------------------------------------------------
// Derivations
// ---------------------------------------------------------------------------
function credentialingSelectedOrgIds() {
  const tenant = typeof activeClientGroupTenant === "function" ? String(activeClientGroupTenant() || "").trim() : "";
  if (!tenant) return [];
  const orgIds = typeof activeClientGroupOrgIds === "function" ? activeClientGroupOrgIds() : [];
  return Array.from(new Set((Array.isArray(orgIds) ? orgIds : [])
    .map((orgId) => String(orgId || "").trim()).filter(Boolean)));
}

function credentialingSelectedOrgId() {
  const orgIds = credentialingSelectedOrgIds();
  if (orgIds.length === 1) return orgIds[0];
  const tenant = typeof activeClientGroupTenant === "function" ? String(activeClientGroupTenant() || "").trim() : "";
  return tenant ? "" : String(credentialingState.confirmedOrgId || "").trim();
}

function credentialingGroupMatchesActiveCompany(group = {}) {
  const tenant = typeof activeClientGroupTenant === "function" ? String(activeClientGroupTenant() || "").trim() : "";
  const groupOrgId = String(group.orgId || "").trim();
  if (!groupOrgId) return false;
  if (!tenant) return true;
  return credentialingSelectedOrgIds().includes(groupOrgId);
}

function credentialingGroupCanBeRecorded(group = {}) {
  const selectedOrgId = credentialingSelectedOrgId();
  return Boolean(selectedOrgId && String(group.orgId || "").trim() === selectedOrgId && canRecordCredentialingDecision());
}

function credentialingTaskMatchesSelectedOrg(task = {}) {
  const selectedOrgId = credentialingSelectedOrgId();
  return Boolean(selectedOrgId && String(task.orgId || "").trim() === selectedOrgId);
}

function credentialingQueueTasks() {
  const tasks = Array.isArray(credentialingState.queue.tasks) ? credentialingState.queue.tasks : [];
  // THE BOARD'S OWN COMPANY RULE, REUSED RATHER THAN RESTATED. With no company
  // selected the server now returns every company this operator holds, and the
  // old filter (exactly-one-selected-org) dropped all of them — printing
  // "Nothing is waiting to be sent" over a payload full of work, which is the
  // board defect reborn in the send half. A task with no org is still rejected,
  // for the same reason a group with no org is: un-attributable evidence must
  // not render under any company. SENDING still requires a named company; see
  // credentialingTaskActionsMarkup, which keeps using the strict rule.
  return tasks.filter((task) => credentialingGroupMatchesActiveCompany(task || {}));
}

function credentialingGroups() {
  const groups = Array.isArray(credentialingState.board?.groups) ? credentialingState.board.groups : [];
  // Every server bucket represents claims that are still parked right now. Keep
  // all recognized buckets visible so an existing relationship cannot turn held
  // work into a false empty board, while retaining the exact selected-company
  // boundary enforced by the global client switcher.
  return groups.filter((group) => Object.prototype.hasOwnProperty.call(CRED_BUCKET_LABELS, String(group && group.bucket || ""))
    && credentialingGroupMatchesActiveCompany(group));
}

function credentialingTotals() {
  const groups = credentialingGroups();
  const providers = new Set();
  const claims = new Set();
  const totals = { groupCount: groups.length, undecidedGroupCount: 0, lineCount: 0, claimCount: 0, providerCount: 0, payerCount: groups.length, expectedTotal: 0, unknownExpectedLineCount: 0, parkedExpectedTotal: 0, parkedUnknownExpectedLineCount: 0 };
  // THE NUMBER ON THE BADGE. /api/navigation/pending-counts counts provider x payer
  // relationships with parked lines and no usable decision (buildCredentialingBoard
  // totals.undecidedGroupCount) - not providers, not claims. The sidebar read 14
  // over a header that said 11 providers / 49 claims / 108 lines, and nothing on
  // the page explained where 14 came from. Same predicate, over the same groups
  // this header already totals, so the company filter narrows it the same way.
  totals.undecidedGroupCount = groups.filter((group) => (Number(group.parkedLineCount) || 0) > 0
    && (String(group.bucket || "") === "no_relationship" || String(group.bucket || "") === "needs_review")).length;
  groups.forEach((group) => {
    providers.add(`${String(group.orgId || "")}|${String(group.providerKey || "")}`);
    (Array.isArray(group.lines) ? group.lines : []).forEach((line) => claims.add(`${String(group.orgId || "")}|${String(line.claimId || "")}`));
    totals.lineCount += Number(group.parkedLineCount) || 0;
    totals.expectedTotal += Number(group.parkedExpectedTotal) || 0;
    totals.unknownExpectedLineCount += Number(group.parkedUnknownExpectedLineCount) || 0;
  });
  totals.providerCount = providers.size;
  totals.claimCount = claims.size || groups.reduce((sum, group) => sum + (Number(group.claimCount) || 0), 0);
  totals.expectedTotal = Math.round(totals.expectedTotal * 100) / 100;
  totals.parkedExpectedTotal = totals.expectedTotal;
  totals.parkedUnknownExpectedLineCount = totals.unknownExpectedLineCount;
  return totals;
}

function credentialingLastCheckedNote() {
  const stamp = Date.parse(String(credentialingState.generatedAtUtc || ""));
  if (!Number.isFinite(stamp)) return "Rechecked every time you open Credentialing.";
  const clock = new Date(stamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return "Last checked " + clock + ". Rechecked every time you open Credentialing.";
}

function credentialingMeta() {
  return credentialingState.board?.meta || {};
}

function activeCredentialingOrgId() {
  const selected = typeof activeClientGroupServerOrgId === "function" ? activeClientGroupServerOrgId() : "";
  if (selected) return selected;
  const scope = currentAccessProfile().scope || {};
  const scoped = normalizeUserScopeList(scope.orgIds);
  return scoped.length === 1 ? scoped[0] : "";
}

function credentialingCompanyLabel() {
  const focused = String(credentialingState.orgId || "").trim();
  if (focused) return authorizationOrgLabel(focused) || focused;
  if (Number(credentialingState.board?.facets?.orgCount) > 1) return `All ${Formatters.int(credentialingState.board.facets.orgCount)} companies`;
  const groups = credentialingGroups();
  const orgIds = Array.from(new Set(groups.map((group) => String(group && group.orgId || "").trim()).filter(Boolean)));
  if (orgIds.length > 1) return `All ${Formatters.int(orgIds.length)} companies`;
  if (orgIds.length === 1) {
    const group = groups.find((candidate) => String(candidate && candidate.orgId || "").trim() === orgIds[0]) || {};
    return String(group.orgLabel || "").trim() || authorizationOrgLabel(orgIds[0]) || orgIds[0];
  }
  return "All scoped companies";
}

function credentialingMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return money(amount);
}
function credentialingExpectedText(expectedTotal, unknownCount = 0, lineCount = 0, options = {}) {
  const expected = Number(expectedTotal);
  const unknown = Math.max(0, Number(unknownCount) || 0);
  const lines = Math.max(0, Number(lineCount) || 0);
  if (lines > 0 && unknown >= lines) return options.compact ? "Expected unavailable" : "Expected reimbursement not yet known";
  const amount = credentialingMoney(Number.isFinite(expected) ? expected : 0);
  if (unknown > 0) return `${amount} known expected · ${unknown} line${unknown === 1 ? "" : "s"} not priced`;
  return options.compact ? `${amount} expected` : `${amount} expected reimbursement`;
}


// PROVIDER IS THE OUTER GRAIN. The server groups at org x provider x payer
// (which is what a release acts on); this folds those rows up by provider for
// DISPLAY only. Money is summed from values the server already computed — this
// screen never recomputes a dollar figure of its own.
function credentialingProviderCards(options = {}) {
  const byProvider = new Map();
  credentialingGroups().forEach((group) => {
    if (!credentialingGroupPassesFilters(group, options)) return;
    const key = `${String(group.orgId || "")}|${String(group.providerKey || "")}`;
    let card = byProvider.get(key);
    if (!card) {
      card = {
        key,
        orgId: String(group.orgId || ""),
        providerDisplay: String(group.providerDisplay || "").trim() || "(no rendering provider)",
        orgLabel: String(group.orgLabel || "").trim() || authorizationOrgLabel(String(group.orgId || "")) || String(group.orgId || ""),
        payers: [],
        parkedLineCount: 0,
        parkedExpectedTotal: 0,
        parkedUnknownExpectedLineCount: 0,
        claimCount: 0,
        payerCount: 0
      };
      byProvider.set(key, card);
    }
    card.payers.push(group);
    card.parkedLineCount += Number(group.parkedLineCount) || 0;
    card.parkedExpectedTotal += Number(group.parkedExpectedTotal) || 0;
    card.parkedUnknownExpectedLineCount += Number(group.parkedUnknownExpectedLineCount) || 0;
    card.claimCount += Number(group.claimCount) || 0;
    card.payerCount += 1;
  });
  const cards = Array.from(byProvider.values());
  cards.forEach((card) => {
    card.parkedExpectedTotal = Math.round(card.parkedExpectedTotal * 100) / 100;
    card.payers.sort((a, b) => (Number(b.parkedExpectedTotal) || 0) - (Number(a.parkedExpectedTotal) || 0)
      || (Number(b.parkedUnknownExpectedLineCount) || 0) - (Number(a.parkedUnknownExpectedLineCount) || 0)
      || String(a.payerDisplay || "").localeCompare(String(b.payerDisplay || "")));
  });
  cards.sort((a, b) => (credentialingState.sortOrder === "lines" && !options.alphabetical ? b.parkedLineCount - a.parkedLineCount : 0)
    || a.providerDisplay.localeCompare(b.providerDisplay) || a.key.localeCompare(b.key));
  return cards;
}

function credentialingGroupPassesFilters(group = {}, options = {}) {
  const bucketFilter = String(credentialingState.bucketFilter || "All");
  if (!options.ignoreBucket && bucketFilter !== "All" && String(group.bucket || "") !== bucketFilter) return false;
  if (!options.ignoreProvider && credentialingState.providerFilter && `${String(group.orgId || "")}|${String(group.providerKey || "")}` !== credentialingState.providerFilter) return false;
  const search = String(credentialingState.search || "").trim().toUpperCase();
  if (!search) return true;
  return `${group.providerDisplay || ""} ${group.payerDisplay || ""}`.toUpperCase().includes(search);
}

function credentialingBuckets() {
  return Array.isArray(credentialingState.board?.buckets) ? credentialingState.board.buckets : [];
}

function canRecordCredentialingDecision() {
  return credentialingState.reviewAccess?.canUpdate === true;
}

function canManageCredentialingRouting() {
  // A designated reviewer may update credentialing status but cannot appoint
  // themself or another reviewer. Configuration keeps the base role gate and
  // is re-authorized on both routing endpoints.
  return hasPermission(PERMISSIONS.CREDENTIALING_MANAGE);
}

function canSendCredentialedClaims() {
  return hasPermission(PERMISSIONS.CREDENTIALING_SUBMIT);
}

function credentialingActionableTaskCount() {
  return credentialingQueueTasks().filter((task) => task && task.actionable).length;
}

function updateCredentialingNavBadge() {
  renderNavigationPendingBadge({
    badgeId: "credentialingNavBadge",
    view: "credentialing",
    label: "Credentialing",
    feature: navigationPendingCountState.credentialing
  });
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Denial Intelligence. Source-linked historical summaries for internal staff.
// Dedicated read-only case evidence does not widen ordinary claim write access.
// ---------------------------------------------------------------------------
// System-view reads observe existing records. They never start a producer or a job.
let systemPortalDataEpoch = 0;
let systemPortalDataPrincipal = "";
let systemPortalFeedbackWrites = 0;
const systemPortalDataReads = new Map();

function systemPortalPrincipalKey() {
  return JSON.stringify({ trackerBase: trackerApiBase(), workflowBase: workflowApiBase(), trackerToken: trackerApiToken(), workflowToken: workflowApiToken(), access: workflowCommandAccessSnapshot() });
}

function systemPortalDataReadScope() {
  return JSON.stringify([systemPortalDataEpoch, systemPortalPrincipalKey(), activeClientGroupTenant(), activeClientGroupOrgIds()]);
}

function beginSystemPortalDataRead(lane) {
  systemPortalDataReads.get(lane)?.controller.abort();
  const scope = systemPortalDataReadScope();
  const controller = new AbortController();
  const entry = { controller, pending: true };
  systemPortalDataReads.set(lane, entry);
  const timeout = window.setTimeout(() => controller.abort(), 95000);
  const owns = () => systemPortalDataReads.get(lane) === entry;
  const current = () => owns() && systemPortalDataReadScope() === scope;
  return {
    owns,
    current,
    fetch: (url, options = {}) => fetch(portalFreshReadUrl(url), { ...options, signal: controller.signal }),
    finish: () => {
      window.clearTimeout(timeout);
      if (systemPortalDataReads.get(lane) === entry) entry.pending = false;
      if (typeof queueTrackerPublicationApply === "function") queueTrackerPublicationApply();
    }
  };
}

function systemPortalDataViewSupported(view) {
  return ["denialIntelligence", "feedback", "audit", "queryMap"].includes(view);
}

function systemPortalDataRequestsPending() {
  if (!systemPortalDataViewSupported(state.view)) return false;
  const prefix = ({ denialIntelligence: "denial-", feedback: "feedback-", audit: "audit-", queryMap: "query-map-" })[state.view];
  return [...systemPortalDataReads].some(([lane, entry]) => lane.startsWith(prefix) && entry.pending);
}

function systemPortalDataInteractionActive() {
  return systemPortalFeedbackWrites > 0
    || (state.view === "feedback" && Boolean(els.saveFeedbackEmailBtn?.disabled
      || (els.feedbackNotificationEmail && String(els.feedbackNotificationEmail.value || "").trim() !== String(feedbackAdminState.notificationEmail || "").trim())))
    || (state.view === "audit" && Boolean(supervisorTrailState.expandedEventId))
    || (state.view === "denialIntelligence" && denialIntelligenceState.refreshStatus === "loading");
}

function resetSystemPortalDataReadState() {
  const principal = systemPortalPrincipalKey();
  const changed = Boolean(systemPortalDataPrincipal && principal !== systemPortalDataPrincipal);
  const resumeDenialRefreshStatus = !changed && denialIntelligenceState.refreshStatus === "loading";
  systemPortalDataPrincipal = principal;
  systemPortalDataEpoch += 1;
  denialIntelligenceHideVisibleHistory();
  systemPortalDataReads.forEach((entry) => entry.controller.abort());
  systemPortalDataReads.clear();
  const replay = denialIntelligenceState.lastSearchKey && denialIntelligenceState.lastSearchKey === systemDenialSearchKey();
  Object.assign(denialIntelligenceState, { searchStatus: "idle", result: null, error: "", lookupStatus: "idle", lookupMessage: "", choices: [], choiceIndex: -1, replaySearch: !changed && Boolean(replay) });
  if (typeof denialIntelligencePollTimers !== "undefined") { denialIntelligencePollTimers.forEach((timer) => window.clearTimeout(timer)); denialIntelligencePollTimers.clear(); }
  if (changed) Object.assign(denialIntelligenceState, { payerQuery: "", carcDraft: "", remarkDraft: "", searchMode: "main", queryText: "", query: null, selectedLabel: "", recent: [], lastSearchKey: "", lastAnalysisId: "", refreshStatus: "idle", refreshMessage: "" });
  denialIntelligenceDrawerCache.clear();
  feedbackAdminRequestKey = "";
  Object.assign(feedbackAdminState, { tickets: [], loading: false, error: "" });
  if (changed) {
    feedbackAdminState.notificationEmail = "";
    if (els.feedbackNotificationEmail) els.feedbackNotificationEmail.value = "";
  }
  supervisorTrailRequestKey = "";
  Object.assign(supervisorTrailState, { status: "idle", error: "", people: [], orgs: [], actions: [], events: [], window: {} });
  reportRunTrailRequestKey = "";
  reportExportJobTrailRequestKey = "";
  exportGovernanceActivityTrailRequestKey = "";
  claimQueueWorkAuditTrailRequestKey = "";
  state.reportRunTrail = [];
  state.reportExportJobTrail = [];
  state.exportGovernanceActivityTrail = changed ? [] : (state.exportGovernanceActivityTrail || []).filter((row) => row.source === "local-preview");
  state.claimQueueWorkAuditTrail = [];
  state.claimQueueWorkOutcomeSummary = null;
  queryMapRulebookRequestKey = "";
  queryMapRulebookState = { status: "idle", response: null, error: "", requestKey: "" };
  resetLiveQueryMapState();
  // A manual summary build may be running across an ordinary view refresh.
  // Resume its GET-only status check; never start the producer POST here.
  if (resumeDenialRefreshStatus) window.setTimeout(loadDenialIntelligenceRefreshStatus, 0);
}

function ensureSystemPortalDataScope() {
  const principal = systemPortalPrincipalKey();
  if (systemPortalDataPrincipal && principal !== systemPortalDataPrincipal) resetSystemPortalDataReadState();
  systemPortalDataPrincipal = principal;
}

function renderSystemPortalDataView(view) {
  if (view === "denialIntelligence") {
    if (denialIntelligenceState.replaySearch) { denialIntelligenceState.replaySearch = false; void searchDenialIntelligence({ refreshOnly: true }); }
    else renderDenialIntelligence();
  } else if (view === "feedback") renderFeedbackAdmin();
  else if (view === "audit") renderAudit();
  else if (view === "queryMap") renderQueryMap();
  else return false;
  return true;
}

function systemDenialSearchKey() {
  return denialIntelligenceState.query ? JSON.stringify([denialIntelligenceState.searchMode, denialIntelligenceState.payerQuery, denialIntelligenceState.carcDraft, denialIntelligenceState.remarkDraft, denialIntelligenceState.queryText, denialIntelligenceState.query]) : "";
}

const denialIntelligenceState = {
  payerQuery: "", carcDraft: "", remarkDraft: "", searchMode: "main",
  queryText: "", query: null, selectedLabel: "", choices: [], choiceIndex: -1,
  lookupStatus: "idle", lookupMessage: "", searchStatus: "idle", result: null, error: "",
  refreshStatus: "idle", refreshMessage: "", lastSearchKey: "", lastAnalysisId: "", replaySearch: false,
  feedbackStatus: "", recent: []
};
const denialIntelligenceDrawerCache = new Map();
const denialIntelligencePollTimers = new Map();
const denialIntelligenceValidationVersions = new Map();
let denialIntelligenceVisibilityEpoch = 0;
function denialIntelligenceStopPoll(key) {
  window.clearTimeout(denialIntelligencePollTimers.get(key));
  denialIntelligencePollTimers.delete(key);
}
function denialIntelligenceHideAnalysis(analysisId) {
  if (!analysisId) return;
  denialIntelligenceValidationVersions.set(analysisId, (denialIntelligenceValidationVersions.get(analysisId) || 0) + 1);
  const hidden = function (record) { return { analysisId, query: record?.query, status: "stale", needsRevalidation: true }; };
  if (denialIntelligenceState.lastAnalysisId === analysisId || denialIntelligenceState.result?.analysisId === analysisId) {
    denialIntelligenceState.result = hidden(denialIntelligenceState.result);
    denialIntelligenceState.searchStatus = "ready";
    denialIntelligenceStopPoll("search");
  }
  denialIntelligenceDrawerCache.forEach(function (entry, key) {
    if (entry.payload?.analysisId === analysisId) {
      denialIntelligenceDrawerCache.set(key, { ...entry, status: "ready", payload: hidden(entry.payload) });
      denialIntelligenceStopPoll("drawer");
    }
  });
  // Remove private text, including an open note panel, from the DOM immediately.
  document.querySelectorAll("[data-di-analysis]").forEach(function (node) {
    if (node.dataset.diAnalysis === analysisId) node.innerHTML = '<p role="status">Current source history needs rechecking. Previous text and notes are hidden.</p><button type="button" class="ghost-button" data-di-regenerate>Refresh this summary</button><section class="denial-history-evidence" data-di-evidence-panel hidden tabindex="-1"></section>';
  });
}
function denialIntelligenceHideVisibleHistory() {
  denialIntelligenceVisibilityEpoch += 1;
  const ids = new Set([denialIntelligenceState.lastAnalysisId]);
  denialIntelligenceDrawerCache.forEach(function (entry) { ids.add(entry.payload?.analysisId); });
  document.querySelectorAll("[data-di-analysis]").forEach(function (node) { ids.add(node.dataset.diAnalysis); });
  ids.forEach(denialIntelligenceHideAnalysis);
  if (denialIntelligenceState.searchStatus === "loading") denialIntelligenceState.searchStatus = "idle";
  denialIntelligencePollTimers.forEach(function (timer) { window.clearTimeout(timer); });
  denialIntelligencePollTimers.clear();
}
function denialIntelligenceRevalidateVisibleHistory() {
  if (document.hidden || !denialIntelligenceCanRead()) return;
  if (state.view === "denialIntelligence" && denialIntelligenceState.lastAnalysisId && denialIntelligenceState.query) void searchDenialIntelligence({ refreshOnly: true });
  const context = typeof activeClaimDrawerContext !== "undefined" ? activeClaimDrawerContext : null;
  if (context?.claim && context.focusServiceId) void loadDenialIntelligenceDrawer(context.claim, context.focusServiceId, { refreshOnly: true });
}
document.addEventListener("visibilitychange", function () {
  if (document.hidden) denialIntelligenceHideVisibleHistory();
  else denialIntelligenceRevalidateVisibleHistory();
});
window.addEventListener("focus", denialIntelligenceRevalidateVisibleHistory);
let denialIntelligencePayerTimer = null;

function denialIntelligenceBase() {
  return ((typeof trackerApiBase === "function" && trackerApiBase()) || (typeof workflowApiBase === "function" && workflowApiBase()) || "").replace(/\/$/, "");
}
function denialIntelligenceHeaders() {
  const token = (typeof trackerApiToken === "function" && trackerApiToken()) || (typeof workflowApiToken === "function" && workflowApiToken()) || "";
  return Object.assign({ "content-type": "application/json" }, token ? { authorization: "Bearer " + token } : {});
}
function denialIntelligenceCanRead() {
  return !isExternalClientRole(state?.role) && hasPermission(PERMISSIONS.DENIAL_INTELLIGENCE_VIEW) && hasPermission(PERMISSIONS.DENIAL_INTELLIGENCE_EVIDENCE_VIEW);
}
function denialIntelligenceError(status, code = "") {
  if (code === "denial_history_not_enabled") return "Denial history is not enabled in this environment. Searching again will not enable it.";
  return status === 401 ? "Your sign-in expired. Sign in again to continue." : status === 403 ? "Your account cannot read this billing history." : "Biller history is unavailable right now. Your search has been kept.";
}
function denialIntelligenceQueryLabel(query) {
  if (!query) return "";
  const parts = [];
  if (query.externalPayerId) parts.push("External payer ID " + String(query.externalPayerId));
  if (query.reasonCode) parts.push("Denial " + (query.groupCode ? query.groupCode + "-" : "") + query.reasonCode);
  if (Array.isArray(query.remarkCodes) && query.remarkCodes.length) parts.push("Remarks " + query.remarkCodes.join(", "));
  if (query.caseContext) parts.push("Claim " + query.caseContext.claimId + (query.caseContext.serviceId ? " · Service " + query.caseContext.serviceId : ""));
  return parts.join(" · ");
}
function denialIntelligenceGrain(result) {
  const raw = String((result && (result.matchGrain || result.matchBreadth || result.grain)) || "");
  const labels = {
    exact: "Same payer, denial, and remarks",
    all_payers_carc: "Same denial code · payer may differ",
    code: "Same denial code · payer may differ",
    payer_only: "Same payer · denial may differ",
    case_context: "Selected claim history",
    payer_carc: "Same payer and denial · remarks may differ"
  };
  // Descriptive server match breadth is authoritative. Never infer same-payer
  // matching merely because a broader phrase contains the word payer.
  const key = raw === "exact" || raw === "case_context" ? "exact" : raw === "payer_only" || raw === "payer_carc" ? "payer_carc" : "all_payers_carc";
  return { key, label: labels[raw] || raw || "Related documented history · match details unavailable" };
}
function denialIntelligenceDate(value, precision) {
  if (!value || precision === "unknown") return "Date not recorded";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text) || precision === "day" || precision === "date") return text.slice(0, 10);
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? "Date not recorded" : date.toLocaleString();
}
function denialIntelligenceBiller(value) {
  const name = String(typeof value === "string" ? value : (value && (value.authorName || value.displayName || value.name)) || "").trim();
  return /^(user|unknown|system|unattributed)$/i.test(name) ? "" : name;
}
function denialIntelligenceSourceMap(result) {
  const sources = new Map();
  (Array.isArray(result?.cases) ? result.cases : []).forEach(function (row, index) {
    if (!row?.evidenceId) return;
    (Array.isArray(row.sourceIds) ? row.sourceIds : []).forEach(function (sourceId) {
      if (sourceId && !sources.has(String(sourceId))) sources.set(String(sourceId), { evidenceId: row.evidenceId, caseNumber: index + 1 });
    });
  });
  return sources;
}
function denialIntelligenceStatementsHtml(statements, result, options) {
  const sources = denialIntelligenceSourceMap(result);
  return (Array.isArray(statements) ? statements : []).map(function (statement) {
    const ids = Array.from(new Set(Array.isArray(statement?.sourceIds) ? statement.sourceIds.map(String) : []));
    // Unsupported statements are never displayed as factual summaries.
    if (!statement?.text || !ids.length || ids.some(function (id) { return !sources.has(id); })) return "";
    const links = ids.map(function (id, index) {
      const ref = sources.get(id);
      return '<button type="button" class="denial-history-source" data-di-evidence="' + escapeHtml(ref.evidenceId) + '" data-di-source="' + escapeHtml(id) + '" aria-label="Read supporting note ' + (index + 1) + ' in case ' + ref.caseNumber + '">Case ' + ref.caseNumber + ' · note ' + (index + 1) + '</button>';
    }).join(" ");
    return '<li><p>' + escapeHtml(statement.text) + '</p><div class="denial-history-sources">' + links + '</div></li>';
  }).join("");
}
function denialIntelligenceCoverageHtml(coverage) {
  if (!coverage) return '<p class="denial-history-gap">Source coverage has not been established.</p>';
  const messages = [];
  if (coverage.truncated) messages.push("Only part of the available history is included.");
  if (coverage.complete === false) messages.push("The available source history is incomplete.");
  if (Array.isArray(coverage.missingSources) && coverage.missingSources.length) messages.push("Some note sources are unavailable.");
  return messages.length ? '<p class="denial-history-gap">' + escapeHtml(messages.join(" ")) + '</p>' : "";
}
function denialIntelligenceCasesHtml(result) {
  const cases = Array.isArray(result?.cases) ? result.cases : [];
  return cases.filter(function (row) { return row && row.evidenceId; }).map(function (row, index) {
    const names = Array.from(new Set((Array.isArray(row.billers) ? row.billers : []).map(denialIntelligenceBiller).filter(Boolean)));
    const range = row.dateRange;
    const dates = typeof range === "string" ? range : range ? [range.from || range.start, range.to || range.end].filter(Boolean).map(function (date) { return denialIntelligenceDate(date, "day"); }).join(" – ") : "";
    return '<article class="denial-history-case"><div class="denial-history-case-heading"><strong>Case ' + (index + 1) + ' · ' + escapeHtml(row.companyName || "Company name unavailable") + '</strong><span class="denial-intelligence-match">' + escapeHtml(denialIntelligenceGrain(row).label) + '</span></div>' +
      '<p>Claim <strong>' + escapeHtml(row.claimId || "Not recorded") + '</strong>' + (row.serviceId ? ' · Service ' + escapeHtml(row.serviceId) : ' · Claim-level history') + '</p>' +
      (row.payerLabel ? '<p>' + escapeHtml(row.payerLabel) + '</p>' : '') +
      '<p class="denial-history-case-meta">' + escapeHtml(names.length ? "Recorded biller: " + names.join(", ") : "Author not recorded") + (dates ? ' · ' + escapeHtml(dates) : '') + '</p>' +
      '<button type="button" class="ghost-button" data-di-evidence="' + escapeHtml(row.evidenceId) + '">Read case notes</button></article>';
  }).join("");
}
function denialIntelligenceResultHtml(result, options) {
  options = options || {};
  if (!result) return '<div class="denial-intelligence-empty"><strong>No matching documented history found.</strong></div>';
  const status = String(result.status || "unavailable");
  const statusText = {
    selection_required: "This claim has more than one matching payer or denial episode. Choose the history to read.",
    pending: "Reading the matching notes… The summary is being prepared.",
    stale: "The source history changed or needs rechecking. The previous text is hidden. Refresh this summary to read the current history.",
    insufficient_history: "No matching documented history found.",
    unavailable: "A summary is unavailable right now. Any available source notes are shown below.",
    budget_paused: "New summaries are paused for this month. Available source notes can still be read."
  };
  const ready = status === "ready";
  const hidden = status === "stale";
  const section = function (heading, rows, empty) {
    const items = ready ? denialIntelligenceStatementsHtml(rows, result, options) : "";
    return '<section class="denial-history-section"><h3>' + heading + '</h3>' + (items ? '<ul class="denial-history-statements">' + items + '</ul>' : '<p class="denial-history-gap">' + empty + '</p>') + '</section>';
  };
  const summary = ready ? denialIntelligenceStatementsHtml(result.summary, result, options) : "";
  const cases = hidden ? "" : denialIntelligenceCasesHtml(result);
  const choices = (Array.isArray(result.choices) ? result.choices : []).filter(function (choice) { return choice?.query; });
  const choiceHtml = choices.length ? '<div class="denial-history-episode-choices"><p>Choose the matching payer and denial history:</p>' + choices.map(function (choice, index) { return '<button type="button" class="ghost-button" data-di-analysis-choice="' + index + '">' + escapeHtml(choice.label || denialIntelligenceQueryLabel(choice.query)) + '</button>'; }).join('') + '</div>' : '';
  const feedback = ready && result.analysisId ? '<div class="denial-history-feedback"><span>Does this match the notes?</span>' + [['helpful', 'Helpful'], ['not_applicable', 'Not applicable'], ['incorrect', 'Incorrect']].map(function (row) { return '<button type="button" class="ghost-button" data-di-feedback="' + row[0] + '" data-di-analysis-id="' + escapeHtml(result.analysisId) + '">' + row[1] + '</button>'; }).join('') + '<span data-di-feedback-status role="status"></span></div>' : '';
  return '<div class="denial-intelligence-result denial-history-result" data-di-analysis="' + escapeHtml(result.analysisId || "") + '">' +
    '<header class="denial-intelligence-result-head"><h2>What the notes show</h2>' + (ready && result.generatedAt ? '<span class="denial-intelligence-freshness">Summarized ' + escapeHtml(denialIntelligenceDate(result.generatedAt)) + '</span>' : '') + '</header>' +
    (result.query ? '<p class="denial-history-result-context">' + escapeHtml(denialIntelligenceQueryLabel(result.query)) + '</p>' : '') +
    (!ready ? '<p class="denial-history-status" role="status">' + escapeHtml(statusText[status] || statusText.unavailable) + '</p>' : '') + choiceHtml +
    (["stale", "unavailable"].includes(status) ? '<button type="button" class="ghost-button" data-di-regenerate>' + (status === 'stale' ? 'Refresh this summary' : 'Retry summary') + '</button>' : '') +
    (ready && result.summaryNotice ? '<p class="denial-history-gap" role="status">' + escapeHtml(result.summaryNotice) + '</p>' : '') +
    (summary ? '<ul class="denial-history-statements denial-intelligence-summary">' + summary + '</ul>' : ready ? '<p class="denial-history-gap">A source-linked summary is not available for this history.</p>' : '') +
    (ready ? section('Actions recorded', result.actions, 'No source-linked action description is available in this summary.') +
      section('Responses and outcomes', [].concat(result.responses || [], result.outcomes || []), 'No source-linked response or outcome description is available in this summary.') : '') +
    (cases ? '<section class="denial-history-section"><h3>Supporting cases</h3><div class="denial-history-cases">' + cases + '</div></section>' : '') +
    (ready ? section('Differences and gaps', result.gaps, 'No additional differences or gaps are described in this summary.') : '') +
    (hidden ? '' : denialIntelligenceCoverageHtml(result.coverage)) +
    '<section class="denial-history-evidence" data-di-evidence-panel hidden aria-label="Supporting case notes"></section>' + feedback + '</div>';
}
function denialIntelligencePayerListHtml() {
  if (denialIntelligenceState.lookupStatus === "loading") return '<p class="denial-history-search-message" role="status">Finding matching history…</p>';
  const choices = denialIntelligenceState.choices;
  if (!choices.length) return denialIntelligenceState.lookupMessage ? '<p class="denial-history-search-message" role="status">' + escapeHtml(denialIntelligenceState.lookupMessage) + '</p>' : '';
  return '<div id="' + (denialIntelligenceState.searchMode === 'claim' ? 'denialIntelligenceClaimChoices' : 'denialIntelligenceSearchChoices') + '" class="denial-intelligence-options denial-history-options" role="listbox" aria-label="Choose the matching payer or claim">' + choices.map(function (choice, index) {
    const kind = ({ payer: "Payer", external_payer_id: "External payer ID", carc: "Denial code", denial_code: "Denial code", rarc: "Remark code", remark_code: "Remark code", patient: "Patient", claim: "Claim", case: "Claim" })[choice.kind] || "Matching history";
    return '<button type="button" id="denialHistoryChoice' + index + '" role="option" aria-selected="' + (index === denialIntelligenceState.choiceIndex) + '" class="denial-intelligence-option' + (index === denialIntelligenceState.choiceIndex ? ' is-active' : '') + '" data-di-choice="' + index + '"><strong>' + escapeHtml(choice.label || denialIntelligenceQueryLabel(choice.query)) + '</strong><span>' + escapeHtml(kind) + '</span></button>';
  }).join('') + '</div>' + (denialIntelligenceState.lookupMessage ? '<p class="denial-history-search-message" role="status">' + escapeHtml(denialIntelligenceState.lookupMessage) + '</p>' : '');
}
function renderDenialIntelligencePayerOptions() {
  const claim = denialIntelligenceState.searchMode === "claim";
  const input = document.querySelector(claim ? "[data-di-claim-input]" : "[data-di-payer-input]");
  const host = document.querySelector(claim ? "[data-di-claim-options-host]" : "[data-di-payer-options-host]");
  const otherHost = document.querySelector(claim ? "[data-di-payer-options-host]" : "[data-di-claim-options-host]");
  if (otherHost) otherHost.innerHTML = "";
  if (!input || !host) return;
  host.innerHTML = denialIntelligencePayerListHtml();
  input.setAttribute("aria-expanded", String(Boolean(denialIntelligenceState.choices.length)));
  if (denialIntelligenceState.choiceIndex >= 0) input.setAttribute("aria-activedescendant", "denialHistoryChoice" + denialIntelligenceState.choiceIndex);
  else input.removeAttribute("aria-activedescendant");
}
function denialIntelligenceLookupInput() {
  if (denialIntelligenceState.searchMode === "claim") return { caseQuery: String(denialIntelligenceState.queryText || "").trim() };
  const payerQuery = String(denialIntelligenceState.payerQuery || "").trim();
  const code = String(denialIntelligenceState.carcDraft || "").trim().toUpperCase();
  const full = code.match(/^(CO|PR|OA|PI|CR)[\s-]*([A-Z]?\d{1,4})$/);
  if (code && !full && !/^[A-Z]?\d{1,4}$/.test(code)) throw new Error("Enter a denial code such as 197 or CO-197.");
  const remarkCodes = Array.from(new Set(String(denialIntelligenceState.remarkDraft || "").toUpperCase().split(/[\s,;]+/).map(function (value) { return value.replace(/-/g, ""); }).filter(Boolean)));
  if (remarkCodes.some(function (value) { return !/^[A-Z]{1,3}\d{1,4}$/.test(value); })) throw new Error("Enter remark codes such as N130 or M15, separated by commas or spaces.");
  return { payerQuery, groupCode: full ? full[1] : "", reasonCode: full ? full[2] : code, remarkCodes };
}
function denialIntelligenceHasInput() {
  return Boolean(String(denialIntelligenceState.payerQuery || "").trim() || String(denialIntelligenceState.carcDraft || "").trim() || String(denialIntelligenceState.remarkDraft || "").trim());
}
function renderDenialIntelligence(options) {
  options = options || {};
  ensureSystemPortalDataScope();
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("denialIntelligence")) return;
  const host = document.getElementById("denialIntelligenceBody");
  if (!host) return;
  if (!denialIntelligenceCanRead()) {
    host.innerHTML = '<div class="denial-intelligence-empty"><strong>You do not have access to Denial Intelligence.</strong></div>';
    return;
  }
  if (!options.validated && (denialIntelligenceState.result?.status === "ready" || denialIntelligenceState.result?.needsRevalidation || (denialIntelligenceState.result?.status !== "stale" && denialIntelligenceState.result?.cases?.length)) && denialIntelligenceState.query) {
    void searchDenialIntelligence({ refreshOnly: true });
    return;
  }
  let body = '<div class="denial-intelligence-welcome"><strong>Find documented billing experience.</strong><span>Search by payer, denial code, remark code, or a combination of those details.</span></div>';
  if (denialIntelligenceState.searchStatus === "loading") body = '<div class="denial-intelligence-loading" role="status"><span></span><strong>Finding matching billing history…</strong></div>';
  if (denialIntelligenceState.result) body = denialIntelligenceResultHtml(denialIntelligenceState.result);
  if (denialIntelligenceState.searchStatus === "not-enabled") body = '<div class="denial-intelligence-empty" role="status"><strong>Denial history is not enabled.</strong><span>' + escapeHtml(denialIntelligenceState.error) + '</span></div>';
  if (denialIntelligenceState.searchStatus === "error") body = '<div class="denial-intelligence-empty" role="alert"><strong>Biller history could not load.</strong><span>' + escapeHtml(denialIntelligenceState.error) + '</span><button type="button" class="ghost-button" data-di-retry>Retry this search</button></div>';
  const resultsHost = host.querySelector(".denial-intelligence-results");
  if (options.resultsOnly && resultsHost) {
    resultsHost.innerHTML = body;
    resultsHost.setAttribute("aria-busy", String(denialIntelligenceState.searchStatus === "loading" || denialIntelligenceState.result?.status === "pending"));
    return;
  }
  const main = denialIntelligenceState.searchMode !== "claim";
  const choices = denialIntelligencePayerListHtml();
  const expanded = Boolean(denialIntelligenceState.choices.length);
  host.innerHTML = '<main class="denial-intelligence-body denial-history-workspace"><div class="denial-history-intro"><p>Real cases. Recorded actions. Original notes.</p><span>Read what billers documented and the responses they recorded.</span></div>' +
    '<form class="denial-intelligence-search" data-di-search><div class="denial-intelligence-fields">' +
    '<label class="denial-intelligence-payer-field"><span>Payer name or external payer ID</span><input data-di-payer-input type="text" role="combobox" aria-autocomplete="list" aria-controls="denialIntelligenceSearchChoices" aria-expanded="' + (main && expanded) + '" autocomplete="off" maxlength="200" value="' + escapeHtml(denialIntelligenceState.payerQuery) + '" placeholder="Name or external ID"></label>' +
    '<label><span>Denial code</span><input data-di-carc type="text" autocomplete="off" maxlength="20" value="' + escapeHtml(denialIntelligenceState.carcDraft) + '" placeholder="197 or CO-197"></label>' +
    '<label><span>Remark code(s)</span><input data-di-rarc type="text" autocomplete="off" maxlength="160" value="' + escapeHtml(denialIntelligenceState.remarkDraft) + '" placeholder="N130, M15"></label></div>' +
    '<p class="denial-history-search-help">Use any field on its own, or combine them to narrow the history.</p><div data-di-payer-options-host>' + (main ? choices : '') + '</div>' +
    '<div class="denial-intelligence-search-actions"><button type="submit" class="primary-button"' + (!denialIntelligenceHasInput() ? ' disabled' : '') + '>Search history</button><button type="button" class="ghost-button" data-di-clear>Clear</button></div>' +
    (denialIntelligenceState.query ? '<p class="denial-history-selection">Selected: <strong>' + escapeHtml(denialIntelligenceState.selectedLabel || denialIntelligenceQueryLabel(denialIntelligenceState.query)) + '</strong></p>' : '') +
    '<details class="denial-history-claim-lookup" data-di-claim-lookup' + (!main ? ' open' : '') + '><summary>Find a claim</summary><label class="denial-history-search-label" for="denialHistoryClaimQuery">Patient name or claim ID</label>' +
    '<div class="denial-history-search-row"><input id="denialHistoryClaimQuery" data-di-claim-input type="text" role="combobox" aria-autocomplete="list" aria-controls="denialIntelligenceClaimChoices" aria-expanded="' + (!main && expanded) + '" autocomplete="off" maxlength="200" value="' + escapeHtml(denialIntelligenceState.queryText) + '" placeholder="Patient name or claim ID"><button type="button" class="ghost-button" data-di-claim-submit>Find claim</button></div>' +
    '<p class="denial-history-search-help">Patient and claim lookup uses your assigned companies.</p><div data-di-claim-options-host>' + (!main ? choices : '') + '</div></details></form>' +
    '<section class="denial-intelligence-results" aria-live="polite" aria-busy="' + (denialIntelligenceState.searchStatus === 'loading' || denialIntelligenceState.result?.status === 'pending') + '">' + body + '</section></main>';
}
async function loadDenialIntelligencePayers(query, options) {
  options = options || {};
  const base = denialIntelligenceBase();
  if (!denialIntelligenceCanRead()) return;
  let input;
  try { input = denialIntelligenceLookupInput(); } catch (error) {
    denialIntelligenceState.lookupStatus = "error";
    denialIntelligenceState.lookupMessage = error.message;
    renderDenialIntelligencePayerOptions(); return;
  }
  if (!base || (denialIntelligenceState.searchMode === "claim" ? !input.caseQuery : !denialIntelligenceHasInput())) return;
  const signature = JSON.stringify(input);
  const read = beginSystemPortalDataRead("denial-payers");
  const current = () => { try { return read.current() && JSON.stringify(denialIntelligenceLookupInput()) === signature; } catch (_) { return false; } };
  denialIntelligenceState.lookupStatus = "loading";
  denialIntelligenceState.lookupMessage = "";
  renderDenialIntelligencePayerOptions();
  let submitQuery = null;
  try {
    const response = await read.fetch(base + "/api/denial-intelligence/v2/lookup", { method: "POST", cache: "no-store", headers: denialIntelligenceHeaders(), body: signature });
    const payload = await response.json();
    if (!current()) return;
    if (!response.ok) throw Object.assign(new Error("lookup_failed"), { status: response.status, code: payload.error });
    denialIntelligenceState.choices = (Array.isArray(payload.choices) ? payload.choices : []).filter(function (choice) { return choice?.query; }).slice(0, 30);
    denialIntelligenceState.choiceIndex = -1;
    denialIntelligenceState.lookupStatus = "ready";
    if (payload.ready && payload.query) {
      denialIntelligenceState.query = payload.query;
      denialIntelligenceState.lookupMessage = "Match found. Search history to read the documented cases.";
      if (options.submit) submitQuery = payload.query;
    } else denialIntelligenceState.lookupMessage = payload.message || (denialIntelligenceState.choices.length ? "Choose the match you mean." : "No matching documented history found.");
  } catch (error) {
    if (!current()) return;
    denialIntelligenceState.choices = [];
    denialIntelligenceState.lookupStatus = "error";
    denialIntelligenceState.lookupMessage = denialIntelligenceError(error.status, error.code);
  } finally { read.finish(); }
  if (!current()) return;
  renderDenialIntelligencePayerOptions();
  if (submitQuery) await searchDenialIntelligence();
}
function selectDenialIntelligencePayer(index) {
  const choice = denialIntelligenceState.choices[Number(index)];
  if (!choice?.query) return;
  denialIntelligenceState.query = choice.query;
  denialIntelligenceState.selectedLabel = choice.label || denialIntelligenceQueryLabel(choice.query);
  denialIntelligenceState.choices = [];
  denialIntelligenceState.choiceIndex = -1;
  denialIntelligenceState.lookupMessage = "";
  void searchDenialIntelligence();
}
function denialIntelligenceSchedulePoll(key, result, callback, current) {
  denialIntelligenceStopPoll(key);
  if (!result?.analysisId || (!(["pending", "ready"].includes(result.status)) && !(result.status !== "stale" && result.cases?.length))) return;
  const id = window.setTimeout(function () {
    denialIntelligencePollTimers.delete(key);
    if (current() && !document.hidden && denialIntelligenceCanRead()) callback();
    else if (document.hidden || !denialIntelligenceCanRead() || (key === "search" && state.view !== "denialIntelligence")) denialIntelligenceHideAnalysis(result.analysisId);
  }, result.status === "pending" ? 2500 : 30000);
  denialIntelligencePollTimers.set(key, id);
}
async function searchDenialIntelligence(options) {
  options = options || {};
  if (!denialIntelligenceCanRead()) return;
  if (!denialIntelligenceState.query) {
    await loadDenialIntelligencePayers(denialIntelligenceState.queryText, { submit: true });
    return;
  }
  const base = denialIntelligenceBase();
  const submittedKey = systemDenialSearchKey();
  const id = options.refreshOnly ? denialIntelligenceState.lastAnalysisId : "";
  if (options.refreshOnly && (!id || denialIntelligenceState.lastSearchKey !== submittedKey || denialIntelligenceState.searchStatus === "loading" || state.view !== "denialIntelligence" || document.hidden)) return;
  denialIntelligenceStopPoll("search");
  denialIntelligenceHideAnalysis(denialIntelligenceState.lastAnalysisId);
  const visibilityEpoch = denialIntelligenceVisibilityEpoch;
  const version = denialIntelligenceValidationVersions.get(id) || 0;
  const versionsAtStart = new Map(denialIntelligenceValidationVersions);
  const read = beginSystemPortalDataRead("denial-search");
  const current = () => read.current() && systemDenialSearchKey() === submittedKey && state.view === "denialIntelligence" && !document.hidden && denialIntelligenceVisibilityEpoch === visibilityEpoch
    && (!id || (denialIntelligenceValidationVersions.get(id) || 0) === version);
  denialIntelligenceState.lastSearchKey = submittedKey;
  denialIntelligenceState.searchStatus = "loading";
  denialIntelligenceState.error = "";
  denialIntelligenceState.result = null;
  renderDenialIntelligence({ validated: true, resultsOnly: Boolean(options.refreshOnly) });
  try {
    if (!base) throw new Error("unavailable");
    const response = await read.fetch(base + "/api/denial-intelligence/v2/analyses" + (id ? "/" + encodeURIComponent(id) : ""), { method: id ? "GET" : "POST", cache: "no-store", headers: denialIntelligenceHeaders(), ...(id ? {} : { body: JSON.stringify({ query: denialIntelligenceState.query }) }) });
    const result = await response.json();
    if (!current()) return;
    if (!response.ok || (id && result.analysisId !== id) || (["ready", "pending"].includes(result.status) && !result.analysisId)) throw Object.assign(new Error("analysis_failed"), { status: response.status, code: result.error });
    if ((denialIntelligenceValidationVersions.get(result.analysisId) || 0) !== (versionsAtStart.get(result.analysisId) || 0)) {
      denialIntelligenceState.lastAnalysisId = result.analysisId;
      denialIntelligenceHideAnalysis(result.analysisId);
      renderDenialIntelligence({ validated: true, resultsOnly: Boolean(options.refreshOnly) });
      return;
    }
    denialIntelligenceState.result = result;
    denialIntelligenceState.lastAnalysisId = result.analysisId || "";
    denialIntelligenceState.searchStatus = "ready";
    denialIntelligenceSchedulePoll("search", result, function () { void searchDenialIntelligence({ refreshOnly: true }); }, current);
  } catch (error) {
    if (!current()) return;
    denialIntelligenceState.result = null;
    denialIntelligenceState.searchStatus = error.code === "denial_history_not_enabled" ? "not-enabled" : "error";
    denialIntelligenceState.error = denialIntelligenceError(error.status, error.code);
  } finally { read.finish(); }
  if (current()) renderDenialIntelligence({ validated: true, resultsOnly: Boolean(options.refreshOnly) });
}
function denialIntelligenceDrawerHtml(claim, serviceId, options) {
  if (!denialIntelligenceCanRead() || !serviceId) return "";
  const key = [claim.orgId || "", claim.claimId || "", serviceId].join("|");
  const cached = denialIntelligenceDrawerCache.get(key);
  let entry = cached?.readScope === systemPortalDataReadScope() ? cached : { status: "idle" };
  if (entry.status === "loading" && (entry.drawerContext !== activeClaimDrawerContext || entry.visibilityEpoch !== denialIntelligenceVisibilityEpoch)) entry = { status: "idle" };
  if (!options?.validated && entry.payload?.analysisId && entry.status !== "loading") {
    // A reopened drawer must recheck its cached result before displaying it.
    Promise.resolve().then(function () { void loadDenialIntelligenceDrawer(claim, serviceId, { refreshOnly: true }); });
    entry = { status: "loading" };
  }
  const button = '<button type="button" class="ghost-button" data-di-load-drawer data-di-claim-id="' + escapeHtml(claim.claimId || "") + '" data-di-org-id="' + escapeHtml(claim.orgId || "") + '" data-di-service-id="' + escapeHtml(serviceId) + '">Check biller history</button>';
  let body = '<p>Find the notes recorded for this service line and comparable denial histories.</p>' + button;
  if (entry.status === "loading") body = '<p class="denial-intelligence-drawer-loading" role="status">Finding matching billing history…</p>';
  if (entry.status === "error") body = '<div role="alert"><p>' + escapeHtml(entry.error) + '</p>' + button + '</div>';
  if (entry.payload) body = denialIntelligenceResultHtml(entry.payload);
  return '<section class="panel denial-intelligence-drawer" data-di-drawer-key="' + escapeHtml(key) + '" data-di-claim-id="' + escapeHtml(claim.claimId || '') + '" data-di-org-id="' + escapeHtml(claim.orgId || '') + '" data-di-service-id="' + escapeHtml(serviceId) + '"><div class="panel-heading-row"><div><h3>Denial Intelligence</h3><p>Documented billing experience and the original supporting notes.</p></div></div>' + body + '</section>';
}
function renderDenialIntelligenceDrawer(claim, serviceId) {
  const context = typeof activeClaimDrawerContext !== "undefined" ? activeClaimDrawerContext : null;
  if (!context || String(context.claim?.orgId || "") !== String(claim.orgId || "") || String(context.claimId) !== String(claim.claimId) || String(context.focusServiceId) !== String(serviceId)) return;
  const key = [claim.orgId || "", claim.claimId || "", serviceId].join("|");
  const host = Array.from(document.querySelectorAll("[data-di-drawer-key]")).find(function (node) { return node.dataset.diDrawerKey === key; });
  if (host) host.outerHTML = denialIntelligenceDrawerHtml(claim, serviceId, { validated: true });
}
async function loadDenialIntelligenceDrawer(claim, serviceId, options) {
  options = options || {};
  if (!denialIntelligenceCanRead() || !claim?.orgId || !claim.claimId || !serviceId) return;
  const key = [claim.orgId, claim.claimId, serviceId].join("|");
  const readScope = systemPortalDataReadScope();
  const cached = denialIntelligenceDrawerCache.get(key);
  if (cached?.readScope === readScope && cached.status === "loading" && cached.drawerContext === activeClaimDrawerContext && cached.visibilityEpoch === denialIntelligenceVisibilityEpoch) return;
  const id = options.refreshOnly ? cached?.payload?.analysisId : "";
  if (options.refreshOnly && (!id || document.hidden)) return;
  denialIntelligenceStopPoll("drawer");
  denialIntelligenceHideAnalysis(id || cached?.payload?.analysisId);
  const visibilityEpoch = denialIntelligenceVisibilityEpoch;
  const version = denialIntelligenceValidationVersions.get(id) || 0;
  const drawerContext = typeof activeClaimDrawerContext !== "undefined" ? activeClaimDrawerContext : null;
  const versionsAtStart = new Map(denialIntelligenceValidationVersions);
  const read = beginSystemPortalDataRead("denial-drawer");
  const current = () => read.current() && !document.hidden && denialIntelligenceVisibilityEpoch === visibilityEpoch && activeClaimDrawerContext === drawerContext && (!id || (denialIntelligenceValidationVersions.get(id) || 0) === version);
  denialIntelligenceDrawerCache.set(key, { readScope, status: "loading", drawerContext, visibilityEpoch, payload: id ? { analysisId: id, query: cached.payload.query, status: "pending" } : null });
  renderDenialIntelligenceDrawer(claim, serviceId);
  try {
    const base = denialIntelligenceBase();
    if (!base) throw new Error("unavailable");
    const query = options.query || { externalPayerId: "", groupCode: "", reasonCode: "", remarkCodes: [], caseContext: { orgId: claim.orgId, claimId: claim.claimId, serviceId } };
    const response = await read.fetch(base + "/api/denial-intelligence/v2/analyses" + (id ? "/" + encodeURIComponent(id) : ""), { method: id ? "GET" : "POST", cache: "no-store", headers: denialIntelligenceHeaders(), ...(id ? {} : { body: JSON.stringify({ query }) }) });
    const payload = await response.json();
    if (!current()) return;
    if (!response.ok || (id && payload.analysisId !== id) || (["ready", "pending"].includes(payload.status) && !payload.analysisId)) throw Object.assign(new Error("history_failed"), { status: response.status, code: payload.error });
    if ((denialIntelligenceValidationVersions.get(payload.analysisId) || 0) !== (versionsAtStart.get(payload.analysisId) || 0)) {
      denialIntelligenceDrawerCache.set(key, { readScope, status: "ready", payload: { analysisId: payload.analysisId, query: payload.query, status: "stale", needsRevalidation: true }, error: "" });
      renderDenialIntelligenceDrawer(claim, serviceId);
      return;
    }
    denialIntelligenceDrawerCache.set(key, { readScope, status: "ready", payload, error: "" });
    denialIntelligenceSchedulePoll("drawer", payload, function () { void loadDenialIntelligenceDrawer(claim, serviceId, { refreshOnly: true }); }, function () {
      const context = typeof activeClaimDrawerContext !== "undefined" ? activeClaimDrawerContext : null;
      return current() && context && String(context.claim?.orgId || "") === String(claim.orgId) && String(context.claimId) === String(claim.claimId) && String(context.focusServiceId) === String(serviceId);
    });
  } catch (error) {
    if (!current()) return;
    denialIntelligenceDrawerCache.set(key, { readScope, status: "error", payload: null, error: denialIntelligenceError(error.status, error.code) });
  } finally { read.finish(); }
  if (current()) renderDenialIntelligenceDrawer(claim, serviceId);
}
function denialIntelligenceEvidenceHtml(payload, selectedSource) {
  const item = payload?.case;
  if (!item) return '<p role="alert">This supporting history is no longer available.</p>';
  const notes = (Array.isArray(item.notes) ? item.notes : []).filter(function (note) { return note && note.sourceId && !["voided", "superseded"].includes(String(note.revision?.status || note.revision || "").toLowerCase()); });
  const seen = new Set();
  const history = notes.filter(function (note) { if (seen.has(String(note.sourceId))) return false; seen.add(String(note.sourceId)); return true; }).map(function (note) {
    const author = denialIntelligenceBiller(note.authorName);
    const date = denialIntelligenceDate(note.eventAt || note.eventDate, note.timePrecision) + (!note.eventAt && note.eventTime ? " " + note.eventTime + (note.timeZone === "unknown" ? " (time zone not recorded)" : "") : "");
    const imported = note.observedAt ? ' · Observed ' + denialIntelligenceDate(note.observedAt) : '';
    const kinds = { portalNotes: 'Portal note', legacyServiceNotes: 'Imported billing note', ezclaimNotes: 'EZClaim note', workflowTouches: 'Recorded workflow action', workflowCommands: 'Completed workflow command', portal_note: 'Portal note', 'portal-note': 'Portal note', legacy_note: 'Imported billing note', 'legacy-service-note': 'Imported billing note', ezclaim_note: 'EZClaim note', 'ezclaim-note': 'EZClaim note', workflow_action: 'Recorded workflow action', 'workflow-touch': 'Recorded workflow action', 'completed-command': 'Completed workflow command', payment: 'Payment record', adjustment: 'Denial adjustment' };
    return '<article tabindex="-1" class="denial-history-note' + (String(note.sourceId) === String(selectedSource) ? ' is-selected' : '') + '" data-di-note-source="' + escapeHtml(note.sourceId) + '"><header><strong>' + escapeHtml(kinds[note.kind] || 'Source record') + '</strong><span>' + escapeHtml(date) + '</span></header><p class="denial-history-note-meta">' + escapeHtml(author || 'Author not recorded') + ' · ' + escapeHtml(note.serviceId ? 'Service ' + note.serviceId : 'Claim-level record') + escapeHtml(imported) + '</p><blockquote>' + escapeHtml(note.text || 'No note text recorded.') + '</blockquote></article>';
  }).join('');
  return '<header class="denial-history-evidence-heading"><div><h3>Supporting case notes</h3><p>' + escapeHtml(item.companyName || 'Company name unavailable') + ' · Claim ' + escapeHtml(item.claimId || '') + (item.serviceId ? ' · Service ' + escapeHtml(item.serviceId) : '') + '</p></div><button type="button" class="ghost-button" data-di-close-evidence>Close notes</button></header>' +
    '<p class="denial-history-readonly">Read-only source history</p>' + denialIntelligenceCoverageHtml(item.coverage || payload.coverage) +
    (selectedSource && !seen.has(String(selectedSource)) ? '<p class="denial-history-gap" role="status">The cited record is no longer available in the current history.</p>' : '') +
    (history || '<p>No current note text is available for this case.</p>');
}
async function openDenialIntelligenceEvidence(button) {
  if (!denialIntelligenceCanRead()) return;
  const container = button.closest(".denial-history-result");
  let panel = container?.querySelector("[data-di-evidence-panel]");
  const analysisId = container?.dataset.diAnalysis;
  const evidenceId = button.dataset.diEvidence;
  if (!panel || !evidenceId || !analysisId) return;
  let version = denialIntelligenceValidationVersions.get(analysisId) || 0;
  const visibilityEpoch = denialIntelligenceVisibilityEpoch;
  const read = beginSystemPortalDataRead("denial-evidence");
  panel.hidden = false;
  panel.innerHTML = '<p role="status">Loading the current supporting notes…</p>';
  panel._denialEvidenceOpener = button;
  const current = () => read.current() && !document.hidden && denialIntelligenceVisibilityEpoch === visibilityEpoch && panel.isConnected && !panel.hidden && (denialIntelligenceValidationVersions.get(analysisId) || 0) === version;
  const suppress = function () {
    denialIntelligenceHideAnalysis(analysisId);
    version = denialIntelligenceValidationVersions.get(analysisId) || 0;
    panel = container.querySelector("[data-di-evidence-panel]");
    if (panel) { panel.hidden = false; panel._denialEvidenceOpener = container.querySelector("[data-di-regenerate]"); }
  };
  try {
    const analysisResponse = await read.fetch(denialIntelligenceBase() + "/api/denial-intelligence/v2/analyses/" + encodeURIComponent(analysisId), { method: "GET", cache: "no-store", headers: denialIntelligenceHeaders() });
    const analysis = await analysisResponse.json();
    if (!current()) return;
    const citedCase = (analysis.cases || []).find(function (item) { return item.evidenceId === evidenceId; });
    if (!analysisResponse.ok || analysis.analysisId !== analysisId || !["ready", "budget_paused", "unavailable"].includes(analysis.status)
      || !(analysis.cases || []).some(function (item) { return item.evidenceId === evidenceId && (!button.dataset.diSource || (item.sourceIds || []).includes(button.dataset.diSource)); })) {
      suppress();
      return;
    }
    if (analysis.status !== "ready") suppress();
    const response = await read.fetch(denialIntelligenceBase() + "/api/denial-intelligence/v2/evidence/" + encodeURIComponent(evidenceId), { cache: "no-store", headers: denialIntelligenceHeaders() });
    const payload = await response.json();
    if (!current()) return;
    if (!response.ok) throw Object.assign(new Error("evidence_failed"), { status: response.status, code: payload.error });
    const notes = (payload?.case?.notes || []).filter(function (note) { return note && !["voided", "superseded"].includes(String(note.revision?.status || note.revision || "").toLowerCase()); });
    if (!payload?.case || !citedCase?.sourceVersion || payload.case.sourceVersion !== citedCase.sourceVersion
      || (button.dataset.diSource && !notes.some(function (note) { return String(note.sourceId) === button.dataset.diSource; }))) suppress();
    if (!panel) return;
    panel.innerHTML = denialIntelligenceEvidenceHtml(payload, button.dataset.diSource);
    const target = Array.from(panel.querySelectorAll("[data-di-note-source]")).find(function (node) { return node.dataset.diNoteSource === button.dataset.diSource; }) || panel.querySelector("[data-di-close-evidence]");
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: "nearest", behavior: "auto" });
  } catch (error) {
    if (!current()) return;
    suppress();
    if (panel) panel.innerHTML = '<p role="alert">' + escapeHtml(denialIntelligenceError(error.status, error.code)) + '</p><button type="button" class="ghost-button" data-di-close-evidence>Close notes</button>';
  } finally { read.finish(); }
}
async function submitDenialIntelligenceFeedback(button) {
  if (!denialIntelligenceCanRead()) return;
  const container = button.closest(".denial-history-feedback");
  const status = container?.querySelector("[data-di-feedback-status]");
  if (!container || !status || container.dataset.saving === 'true') return;
  container.dataset.saving = 'true';
  const buttons = Array.from(container.querySelectorAll("[data-di-feedback]"));
  buttons.forEach(function (node) { node.disabled = true; });
  status.textContent = "Saving feedback…";
  const read = beginSystemPortalDataRead("denial-feedback");
  try {
    const response = await read.fetch(denialIntelligenceBase() + "/api/denial-intelligence/v2/analyses/" + encodeURIComponent(button.dataset.diAnalysisId) + "/feedback", { method: "POST", cache: "no-store", headers: denialIntelligenceHeaders(), body: JSON.stringify({ rating: button.dataset.diFeedback }) });
    if (!read.current() || !container.isConnected) return;
    if (!response.ok) throw new Error("feedback_failed");
    status.textContent = "Feedback recorded.";
  } catch (_) {
    if (!read.current() || !container.isConnected) return;
    status.textContent = "Feedback could not be saved.";
  } finally {
    read.finish();
    if (container.isConnected) { container.dataset.saving = 'false'; buttons.forEach(function (node) { node.disabled = false; }); }
  }
}

async function refreshDenialIntelligenceSummaries() {
  if (!isTrustedSuperAdminSession() || denialIntelligenceState.refreshStatus === "loading") return;
  const base = denialIntelligenceBase();
  if (!base) return;
  Object.assign(denialIntelligenceState, { refreshStatus: "loading", refreshMessage: "Refreshing saved billing notes in the background. You can keep using the portal." });
  renderDenialIntelligence();
  try {
    const response = await fetch(base + "/api/denial-intelligence/refresh", { method: "POST", cache: "no-store", headers: denialIntelligenceHeaders() });
    const payload = await response.json().catch(function () { return {}; });
    if (!response.ok) throw Object.assign(new Error(payload.message || "The summaries could not be refreshed."), { status: response.status });
    denialIntelligenceState.refreshMessage = payload.alreadyRunning ? "A summary refresh is already running." : "Summary refresh started. The last completed summaries stay available until it finishes.";
    window.setTimeout(loadDenialIntelligenceRefreshStatus, 1500);
  } catch (error) {
    Object.assign(denialIntelligenceState, { refreshStatus: "error", refreshMessage: error.status === 403 ? "Only a portal administrator can refresh every summary." : (error.message || "The summaries could not be refreshed.") });
    renderDenialIntelligence();
  }
}

async function loadDenialIntelligenceRefreshStatus() {
  const base = denialIntelligenceBase();
  if (!base || denialIntelligenceState.refreshStatus !== "loading") return;
  const read = beginSystemPortalDataRead("denial-status");
  try {
    const response = await read.fetch(base + "/api/denial-intelligence/refresh/status", { cache: "no-store", headers: denialIntelligenceHeaders() });
    const payload = await response.json().catch(function () { return {}; });
    if (!read.current()) return;
    if (!response.ok) throw new Error("status_failed");
    const refresh = payload.refresh || payload;
    if (refresh.refreshing) {
      denialIntelligenceState.refreshMessage = "Refreshing saved billing notes in the background. The last completed summaries are still available.";
      renderDenialIntelligence();
      window.setTimeout(loadDenialIntelligenceRefreshStatus, 2000);
      return;
    }
    const failed = refresh.lastAttempt && refresh.lastAttempt.state === "failed";
    Object.assign(denialIntelligenceState, { refreshStatus: failed ? "error" : "ready", refreshMessage: failed ? "The refresh did not finish. The last completed summaries are still available." : "The latest summary refresh completed." });
  } catch (_) {
    if (!read.current()) return;
    Object.assign(denialIntelligenceState, { refreshStatus: "error", refreshMessage: "Refresh status could not be checked. The last completed summaries are still available." });
  } finally { read.finish(); }
  if (read.current()) renderDenialIntelligence();
}


document.addEventListener("input", function (event) {
  const target = event.target;
  if (!target.matches("[data-di-payer-input], [data-di-carc], [data-di-rarc], [data-di-claim-input]")) return;
  denialIntelligenceHideAnalysis(denialIntelligenceState.lastAnalysisId);
  const claim = target.matches("[data-di-claim-input]");
  if (claim) denialIntelligenceState.queryText = target.value;
  else if (target.matches("[data-di-payer-input]")) denialIntelligenceState.payerQuery = target.value;
  else if (target.matches("[data-di-carc]")) denialIntelligenceState.carcDraft = target.value;
  else denialIntelligenceState.remarkDraft = target.value;
  Object.assign(denialIntelligenceState, { searchMode: claim ? "claim" : "main", query: null, selectedLabel: "", choices: [], choiceIndex: -1, lookupStatus: "idle", lookupMessage: "", result: null, searchStatus: "idle", error: "", lastSearchKey: "", lastAnalysisId: "" });
  const submit = target.closest("[data-di-search]")?.querySelector('button[type="submit"]');
  if (submit) submit.disabled = !denialIntelligenceHasInput();
  renderDenialIntelligencePayerOptions();
  window.clearTimeout(denialIntelligencePayerTimer);
  denialIntelligencePayerTimer = window.setTimeout(function () { void loadDenialIntelligencePayers(); }, 250);
});
document.addEventListener("keydown", function (event) {
  if (!event.target.matches("[data-di-payer-input], [data-di-claim-input]")) return;
  const choices = denialIntelligenceState.choices;
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    const direction = event.key === "ArrowDown" ? 1 : -1;
    denialIntelligenceState.choiceIndex = choices.length ? (denialIntelligenceState.choiceIndex + direction + choices.length) % choices.length : -1;
    renderDenialIntelligencePayerOptions();
  } else if (event.key === "Enter" && choices.length && denialIntelligenceState.choiceIndex >= 0) {
    event.preventDefault(); selectDenialIntelligencePayer(denialIntelligenceState.choiceIndex);
  } else if (event.key === "Enter" && event.target.matches("[data-di-claim-input]")) {
    event.preventDefault(); denialIntelligenceState.searchMode = "claim"; void searchDenialIntelligence();
  } else if (event.key === "Escape") {
    denialIntelligenceState.choices = []; denialIntelligenceState.choiceIndex = -1; denialIntelligenceState.lookupMessage = ""; renderDenialIntelligencePayerOptions();
  }
});
document.addEventListener("click", function (event) {
  if (event.target.closest("[data-di-claim-submit]")) {
    if (denialIntelligenceState.searchMode !== "claim") denialIntelligenceState.query = null;
    denialIntelligenceState.searchMode = "claim"; void searchDenialIntelligence(); return;
  }
  const regenerate = event.target.closest("[data-di-regenerate]");
  if (regenerate) {
    const drawerHost = regenerate.closest("[data-di-drawer-key]");
    if (drawerHost) {
      const payload = denialIntelligenceDrawerCache.get(drawerHost.dataset.diDrawerKey)?.payload;
      void loadDenialIntelligenceDrawer({ orgId: drawerHost.dataset.diOrgId, claimId: drawerHost.dataset.diClaimId }, drawerHost.dataset.diServiceId, { query: payload?.query });
    } else void searchDenialIntelligence();
    return;
  }
  const analysisChoice = event.target.closest("[data-di-analysis-choice]");
  if (analysisChoice) {
    const drawerHost = analysisChoice.closest("[data-di-drawer-key]");
    const payload = drawerHost ? denialIntelligenceDrawerCache.get(drawerHost.dataset.diDrawerKey)?.payload : denialIntelligenceState.result;
    const choice = (payload?.choices || [])[Number(analysisChoice.dataset.diAnalysisChoice)];
    if (!choice?.query) return;
    if (drawerHost) void loadDenialIntelligenceDrawer({ orgId: drawerHost.dataset.diOrgId, claimId: drawerHost.dataset.diClaimId }, drawerHost.dataset.diServiceId, { query: choice.query });
    else { denialIntelligenceState.query = choice.query; denialIntelligenceState.selectedLabel = choice.label || denialIntelligenceQueryLabel(choice.query); void searchDenialIntelligence(); }
    return;
  }
  const choice = event.target.closest("[data-di-choice]");
  if (choice) { selectDenialIntelligencePayer(choice.dataset.diChoice); return; }
  const drawer = event.target.closest("[data-di-load-drawer]");
  if (drawer) { void loadDenialIntelligenceDrawer({ claimId: drawer.dataset.diClaimId, orgId: drawer.dataset.diOrgId }, drawer.dataset.diServiceId); return; }
  const evidence = event.target.closest("[data-di-evidence]");
  if (evidence) { void openDenialIntelligenceEvidence(evidence); return; }
  const close = event.target.closest("[data-di-close-evidence]");
  if (close) {
    const panel = close.closest("[data-di-evidence-panel]");
    const opener = panel?._denialEvidenceOpener;
    if (panel) { panel.hidden = true; panel.innerHTML = ''; }
    if (opener?.isConnected) opener.focus();
    return;
  }
  const feedback = event.target.closest("[data-di-feedback]");
  if (feedback) { void submitDenialIntelligenceFeedback(feedback); return; }
  if (event.target.closest("[data-di-retry]")) { void searchDenialIntelligence(); return; }
  if (event.target.closest("[data-di-clear]")) {
    Object.assign(denialIntelligenceState, { payerQuery: "", carcDraft: "", remarkDraft: "", searchMode: "main", queryText: "", query: null, selectedLabel: "", choices: [], choiceIndex: -1, lookupStatus: "idle", lookupMessage: "", searchStatus: "idle", result: null, error: "", lastSearchKey: "", lastAnalysisId: "" });
    window.clearTimeout(denialIntelligencePayerTimer);
    renderDenialIntelligence();
    document.querySelector("[data-di-payer-input]")?.focus();
  }
});
document.addEventListener("submit", function (event) {
  if (!event.target.matches("[data-di-search]")) return;
  event.preventDefault();
  window.clearTimeout(denialIntelligencePayerTimer);
  if (denialIntelligenceState.searchMode !== "main") denialIntelligenceState.query = null;
  denialIntelligenceState.searchMode = "main";
  void searchDenialIntelligence();
});

// Post-submission Client Action Pending workspace. The API owns tenant scope,
// marker membership and history; this module owns triage, reading and replying.
//
// Lanes map 1:1 onto the case's `responsibility` value. They used to be derived
// per role, which quietly broke the whole tab: the sync INSERTs every new case
// as `waiting_on_client`, the internal mapping sent those to "tms-reviewing",
// and the view defaulted to "needs-response" — so every biller and admin opened
// this workspace onto "Nothing is waiting here." while the real queue sat one
// unlabelled tab over. "Open" is now the default lane and always contains every
// open case, so this workspace cannot open blank while work exists.
const CLIENT_ACTION_LANES = [
  { id: "open", tms: "Open", client: "Open" },
  { id: "waiting-on-client", tms: "Waiting on client", client: "Needs your response" },
  { id: "client-responded", tms: "Client responded", client: "You responded" },
  { id: "tms-working", tms: "TMS working", client: "With TMS" },
  { id: "resolved", tms: "Resolved", client: "Resolved" }
];
const clientActionWorkspaceState = {
  prepared: false, page: 1, pageInfo: null, counts: null, categoryCounts: null, permittedOrgIds: [],
  controller: null, detailController: null, detailPage: 1, messagePage: 1, historyPage: 1, refreshJob: null, refreshPending: false, refreshTimer: null,
  status: "idle",        // idle | loading | ready | error
  key: "",
  rows: [],
  resolvedLoaded: false, // resolved cases cost an extra query, so they load on demand
  selectedId: "",
  selectedOrgId: "",     // captured from the queue row: commands must never build /api/client-action//<id>
  detail: null,
  detailStatus: "idle",  // idle | loading | ready | error
  // The biller's own words, read LIVE from the shared claim-note ledger each
  // time a request is opened. Never snapshotted into the case row - see
  // backend/clientActionRequestSummary.js for why one copy of note text, under
  // the ledger's own permission, is the whole point.
  notes: { status: "idle", key: "", items: [], claims: 0, truncated: false },
  summaryStatus: "idle", // idle | saving
  summaryError: "",
  workflowStatus: "idle", // idle | saving | error
  workflowError: "",
  lane: "open",
  category: "all",
  // The company this queue is narrowed to on screen, or "" for every company in
  // the header's client scope. NEVER an isolation boundary: the server has
  // already refused every org outside this principal's scope, and it refuses
  // the ?orgId= below the same way. This is the on-screen narrowing every other
  // org-keyed surface already had and this one did not.
  orgId: "",
  // Queue search by client (patient) or rendering provider. searchDraft is what
  // is typed; search is what the server last filtered by. searchOptions holds
  // the names in the current lane/category for the dropdown.
  search: "",
  searchDraft: "",
  searchOptions: null,
  error: ""
};
const clientActionCategories = ["all", "other", "authorization", "secondary-authorization", "coverage", "credentialing", "cob", "evv"];
// THE BOARD CATEGORY (Nathan, 2026-10-21): a secondary authorization request keeps the stored
// category "authorization" and the follow-up action Client Action Pending-Authorization, but the
// board files it under its own Secondary authorization filter and tag. The server sends
// boardCategory; the title is the fallback for an older payload (clientActionRequestSummary.js).
function clientActionBoardCategory(row = {}) {
  if (row.boardCategory) return String(row.boardCategory).toLowerCase();
  const stored = String(row.category || "other").toLowerCase();
  return stored === "authorization" && String(row.title || "").startsWith("Secondary authorization needed") ? "secondary-authorization" : stored;
}
const CLIENT_ACTION_SECONDARY_AUTHORIZATION_TEMPLATE = "Secondary authorization\nAuthorization number: \nApproved units: \nDates covered: ";
function clientActionReadScope() { return JSON.stringify([typeof reportReadPrincipalKey === "function" ? reportReadPrincipalKey() : navigationPendingPrincipalMarker(), typeof activeClientGroupOrgIds === "function" ? activeClientGroupOrgIds() : []]); }
function clientActionBase() { return (trackerApiBase() || "").replace(/\/$/, ""); }
function clientActionHeaders(json = false) {
  const token = trackerApiToken();
  return { ...(json ? { "content-type": "application/json" } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}) };
}
function clientActionId(row = {}) { return String(row.caseId || row.id || ""); }
function clientActionOrg(row = {}) { return String(row.orgId || row.companyId || ""); }
function clientActionTitle(row = {}) { return String(row.whatWeNeed || row.request || row.title || "Information needed"); }
function clientActionOwner(row = {}) {
  return String(row.assignedTmsBiller?.displayName || row.assignedTmsBiller || row.ownerName || row.assignedTmsUserId || "Unassigned");
}
function clientActionResponsibility(value) {
  // A client reads its own board in the second person (2026-10-13): "Waiting
  // on client" on the client's own screen read as if about someone else.
  if (typeof state !== "undefined" && isExternalClientRole(state?.role)) {
    const own = ({ waiting_on_client: "Waiting on you", client_responded: "You responded", tms_working: "With TMS", "Waiting on Client": "Waiting on you", "Client Responded": "You responded", "TMS Working": "With TMS" })[value];
    if (own) return own;
  }
  return ({ waiting_on_client: "Waiting on client", client_responded: "Client responded", tms_working: "TMS working", "Waiting on Client": "Waiting on client", "Client Responded": "Client responded", "TMS Working": "TMS working" })[value] || String(value || "Unassigned");
}
// "Waiting on client" is a CLAIM ABOUT A PERSON OUTSIDE THIS BUILDING, and it is
// only true once we have actually asked them. A case sitting in the
// waiting_on_client lane with no delivery stamp is still ours; saying otherwise
// on the biller board is how a backlog of un-sent requests came to look like a
// backlog of unresponsive clients.
function clientActionResponsibilityLabel(row = {}) {
  // A RESOLVED CASE IS NOT WAITING ON ANYONE. The stored responsibility is whatever
  // it was when the last marker cleared, so the Resolved lane on hosted TEST printed a
  // green "Waiting on client" pill on 377 closed requests and the detail header said
  // "Responsible now: Waiting on client" over a request with 0 active lines.
  if (clientActionLaneFor(row) === "resolved") return "Resolved";
  // STAFF READ WHETHER THE PRACTICE HAS SEEN IT (Nathan, 2026-10-30). Nothing is
  // emailed: a request reaches the practice only when one of its users opens the
  // portal, which stamps askedAtUtc and starts the 14-day window. "Not sent yet"
  // beside "Due in 6 days" left billers asking what we were meant to send.
  if (clientActionLaneFor(row) === "waiting-on-client" && !(typeof state !== "undefined" && isExternalClientRole(state?.role))) {
    return String(row.askedAtUtc || "").trim() ? "Client viewed it" : "Client hasn't viewed it";
  }
  return clientActionResponsibility(row.responsibility);
}
function clientActionCategoryLabel(value) {
  const category = String(value || "other").toLowerCase();
  if (category === "all") return "All";
  if (category === "other") return "General";
  if (category === "cob" || category === "evv") return category.toUpperCase();
  if (category === "secondary-authorization") return "Secondary authorization";
  return category[0].toUpperCase() + category.slice(1);
}
function clientActionLane(value) {
  const lane = CLIENT_ACTION_LANES.find((entry) => entry.id === value);
  if (!lane) return String(value || "");
  return isExternalClientRole(state.role) ? lane.client : lane.tms;
}
// One case, one lane. Every open case also counts toward "open", so no
// responsibility value — including one this build has never seen — can hide a
// case from the default lane.
function clientActionLaneFor(row = {}) {
  if (String(row.lifecycle || "").toLowerCase() === "resolved") return "resolved";
  const responsibility = String(row.responsibility || "").toLowerCase().replace(/ /g, "_");
  if (responsibility === "client_responded") return "client-responded";
  if (responsibility === "tms_working") return "tms-working";
  return "waiting-on-client";
}
function clientActionItems(row = {}) {
  return Array.isArray(row.items) ? row.items : Array.isArray(row.memberships) ? row.memberships : Array.isArray(row.linkedLines) ? row.linkedLines : [];
}
function clientActionItemActive(item = {}) { return item.active !== false && item.markerStillPresent !== false; }
// The card counts only lines that still carry the marker. Detached lines stay in
// the payload on purpose — the detail view shows them as history — but counting
// them inflated every "N linked lines" the moment a line moved on.
function clientActionSummary(row = {}) {
  const items = clientActionItems(row);
  const active = items.filter(clientActionItemActive);
  const priced = active.filter((item) => item.expected !== null && item.expected !== undefined && String(item.expected).trim() !== "" && Number.isFinite(Number(item.expected)));
  const unique = (values) => [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))];
  return {
    items,
    active,
    expected: priced.reduce((sum, item) => sum + Number(item.expected), 0),
    pricedExpectedLineCount: priced.length,
    unknownExpectedLineCount: active.length - priced.length,
    payers: unique(active.map((item) => item.payer || item.payerLabel)),
    providers: unique(active.map((item) => item.provider || item.providerLabel)),
    activeLineCount: active.length,
    ...(row.summary || {}),
    detached: row.summary?.detachedLineCount ?? items.length - active.length
  };
}
// Top level, deliberately. This lived INSIDE clientActionSummary, which hoisted
// it out of every caller's reach: renderClientActionQueue and the linked-claims
// heading both call it, and both threw ReferenceError for any role that is not a
// Redacted Client - i.e. the whole queue was blank for every biller, while the
// client view (which skips the money text) looked fine.

// Service dates are calendar dates. SQL snapshots can contain Date.toString()
// text; preserve its written day instead of truncating it or shifting its zone.
function clientActionSummaryDate(value) {
  const text = String(value || "").trim();
  const iso = /^(\d{4}-\d{2}-\d{2})(?:$|[T\s])/.exec(text);
  if (iso) return dateOnlyFilterString(iso[1]);
  const written = /^(?:Sun|Mon|Tue|Wed|Thu|Fri|Sat) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2}) (\d{4})(?:$|\s)/.exec(text);
  if (written) {
    const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].indexOf(written[1]) + 1;
    return dateOnlyFilterString(`${written[3]}-${String(month).padStart(2, "0")}-${written[2].padStart(2, "0")}`);
  }
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  return us ? dateOnlyFilterString(`${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`) : "";
}

function clientActionSummaryText(row = {}, summary = clientActionSummary(row)) {
  const savedSummary = String(row.summaryText || row.summary_text || "").trim();
  if (savedSummary) return savedSummary;
  const active = summary.active || [];
  const dates = (summary.firstDos || summary.lastDos ? [summary.firstDos, summary.lastDos] : active.map((item) => item.serviceDate || item.dateOfService)).map(clientActionSummaryDate).filter(Boolean).sort();
  const dateText = dates.length ? (dates[0] === dates[dates.length - 1] ? ` for ${formatDate(dates[0])}` : ` from ${formatDate(dates[0])} through ${formatDate(dates[dates.length - 1])}`) : "";
  const needs = ({
    authorization: "We need the authorization number and the dates it covers.",
    "secondary-authorization": "We need the secondary insurance authorization: the authorization number, the approved units and the dates it covers. The primary insurance has already been billed.",
    coverage: "We need the correct insurance coverage and its effective dates.",
    credentialing: "We need the provider enrollment information and effective date.",
    cob: "We need the current coordination of benefits information.",
    evv: "We need the missing visit verification information."
  })[clientActionBoardCategory(row)] || String(row.requestText || "We need the missing information before billing can continue.");
  const count = Number(summary.activeLineCount ?? active.length) || 0;
  return `${needs} This summary covers ${count} current service line${count === 1 ? "" : "s"}${dateText}.`;
}

function clientActionSummaryUpdatedText(row = {}, summary = clientActionSummary(row)) {
  const savedSummary = String(row.summaryText || row.summary_text || "").trim();
  if (!savedSummary) return `No saved summary yet. Refresh it after replies or service lines change.`;
  const updated = String(row.summaryUpdatedAtUtc || row.summary_updated_at_utc || "");
  const when = updated ? (formatDateTime(updated) || formatDate(updated)) : "previously";
  const sourceCount = Number(row.summarySourceCount ?? row.summary_source_count);
  return `Updated ${when}${Number.isFinite(sourceCount) && sourceCount > 0 ? ` from ${sourceCount} saved item${sourceCount === 1 ? "" : "s"} (service lines and replies)` : ""}.`;
}

function clientActionSelectableRules() {
  const scope = currentAccessProfile()?.scope || {};
  const configured = scope.followUpActionAccessConfigured === true;
  const allowed = new Set(normalizeUserScopeList(scope.followUpActions).map((action) => normalizeFollowUpActionKey(canonicalFollowUpAction(action) || action)));
  return getSelectableActionRules().filter((rule) => !configured || allowed.has(normalizeFollowUpActionKey(rule.action)));
}

function clientActionActionOptions() {
  return '<option value="None">— Clear follow-up</option>' + clientActionSelectableRules().map((rule) => `<option value="${escapeHtml(rule.action)}">${escapeHtml(rule.action)}</option>`).join("");
}

function clientActionExpectedText(summary = {}, options = {}) {
  const active = Number(summary.activeLineCount ?? summary.active?.length) || 0;
  const unknown = Number(summary.unknownExpectedLineCount) || 0;
  if (active > 0 && unknown >= active) return "Expected reimbursement not yet known";
  const amount = money(Number(summary.expected) || 0);
  if (unknown > 0) return `${amount} known expected · ${unknown} line${unknown === 1 ? "" : "s"} not priced`;
  return options.compact ? `${amount} expected` : `${amount} expected reimbursement`;
}
// The client-facing response window, mirroring CLIENT_RESPONSE_WINDOW_DAYS in
// clientActionWorkspace.js, which is where the date itself is computed. Named
// here so the two can be seen to agree rather than both being a bare 14.
const CLIENT_ACTION_RESPONSE_WINDOW_DAYS = 14;
// "13 days overdue" beside "asked 27 days ago" looked contradictory; the
// answer is due a fixed window after the ask, so say both.
function clientActionDueExplanation(row = {}) {
  const due = String(row.dueDate || row.due_date || "").trim();
  if (!due) return "";
  const parsed = typeof localDateFromDateOnly === "function" ? localDateFromDateOnly(due.slice(0, 10)) : null;
  const date = parsed || new Date(due);
  if (!date || Number.isNaN(date.getTime())) return "";
  const dueText = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return String(row.askedAtUtc || "").trim()
    ? `Answer due ${dueText}, ${CLIENT_ACTION_RESPONSE_WINDOW_DAYS} days after we asked.`
    : `Answer due ${dueText}.`;
}
function clientActionDue(row = {}) {
  const due = String(row.dueDate || "").trim();
  // A closed request has no deadline to count down and was never "not sent": the
  // card and the detail header render nothing for it (the state pill says Resolved).
  if (String(row.lifecycle || "").toLowerCase() === "resolved") return { label: "", tone: "resolved" };
  // THREE STATES, NOT TWO.
  //
  // The backend strips the due date from a case nobody has been sent, so "No due
  // date" came to mean two different things: an ask with no deadline, and an ask
  // that never left the building. On the biller board three of five cards read
  // "No due date" beside an amber "Waiting on client" pill - asserting the
  // practice was the blocker on requests the practice has never seen.
  //
  // The data was already here. withDeliveryStamp puts askedAtUtc on every read,
  // and app.js contained zero occurrences of it, so the never-asked backlog was
  // invisible to the only people who can clear it.
  if (!due) {
    return String(row.askedAtUtc || "").trim()
      ? { label: "No due date", tone: "none" }
      : { label: "Client hasn't viewed it", tone: "unsent" };
  }
  const parsed = typeof localDateFromDateOnly === "function" ? localDateFromDateOnly(due) : null;
  const date = parsed || new Date(due);
  if (!date || Number.isNaN(date.getTime())) return { label: "No due date", tone: "none" };
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const days = Math.round((date.getTime() - startOfToday.getTime()) / 86400000);
  if (days < 0) return { label: `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`, tone: "overdue" };
  if (days === 0) return { label: "Due today", tone: "soon" };
  if (days <= 7) return { label: `Due in ${days} day${days === 1 ? "" : "s"}`, tone: "soon" };
  // THE WHOLE WINDOW COUNTS DOWN.
  //
  // Now that the due date runs from the ask rather than from the case's birth, a
  // freshly delivered request is exactly CLIENT_ACTION_RESPONSE_WINDOW_DAYS out -
  // which fell past the `days <= 7` branch and printed a bare calendar date. "Due
  // 9/7/2026" does not name the promise we had just made them; "Due in 14 days"
  // does. The TONE still turns at seven: a countdown is information, amber is a
  // warning, and fourteen days of amber is neither.
  //
  // + 1 IS THE CALENDAR BOUNDARY, NOT SLACK IN THE PROMISE. clientResponseDueDate
  // adds the window to the UTC date of the ask; this function measures from LOCAL
  // midnight. Between 8pm and midnight Eastern those are different days, so an
  // ordinary evening delivery is genuinely fifteen local days out. Without the
  // extra day the most common evening ask of all falls straight past the countdown
  // and prints a bare calendar date.
  if (days <= CLIENT_ACTION_RESPONSE_WINDOW_DAYS + 1) return { label: `Due in ${days} days`, tone: "ok" };
  return { label: `Due ${formatDate(due)}`, tone: "ok" };
}
// Org id -> company name, resolved from the same roster Manage Users reads. The
// case payload carries no company name, so every card used to render a blank
// line where the client belonged.
function clientActionNarrowLayout() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 800px)").matches;
}
function clientActionCompanyName(value, orgId) {
  const name = String(value || "").trim();
  if (!name || name === String(orgId || "").trim() || /^org\s*:/i.test(name) || /^\d+$/.test(name) || /^Company name unavailable(?: \d+)?$/i.test(name)) return "";
  return name;
}
// One resolver for every surface that used to print an org ID next to (or
// instead of) a company name. It reads the authorized directory first, then the
// Manage Users label map, and falls back to the neutral unavailable state the
// directives require. It never returns an identifier: clientActionCompanyName
// rejects an ID-shaped "name" (bare digits, "org:...", the ID itself).
// An org-PREFIXED placeholder is not a company name either. The tenant mapping
// synthesises "Org <id>" for a tenant with no name on file, and letting that
// through would put the identifier back on screen wearing a name's clothes.
function portalCompanyNameCandidate(value, orgId) {
  const name = clientActionCompanyName(value, orgId);
  return name && !/^org[\s:_-]/i.test(name) ? name : "";
}
function portalCompanyLabel(orgId, fallback = "Company name unavailable") {
  const id = String(orgId || "").trim();
  if (!id) return fallback;
  let name = "";
  try { name = portalCompanyNameCandidate(tenantNameForOrgId(id), id); } catch (_error) { name = ""; }
  if (!name) {
    try { name = portalCompanyNameCandidate(userMgmtOrgLabelMap().get(id), id); } catch (_error) { name = ""; }
  }
  return name || fallback;
}
// Company setup cards/notices carry their own orgName from the onboarding
// record; fall through to the directory before the unavailable state.
function organizationOnboardingCompanyName(row = {}) {
  const orgId = String(row.orgId || "").trim();
  const own = portalCompanyNameCandidate(row.orgName, orgId);
  return own || portalCompanyLabel(orgId);
}
function clientActionOrgLabels() {
  let labels;
  try { labels = userMgmtDisplayOrgLabelMap(); } catch (_) { labels = new Map(); }
  for (const [orgId, label] of labels) {
    labels.set(orgId, clientActionCompanyName(label, orgId) || "Company name unavailable");
  }
  // The authenticated tenant inventory is available to clients too. It supplies
  // exact org/name pairs without requesting a management-only directory.
  const names = new Map();
  const inventory = typeof databaseTenantClientOptions === "function" ? databaseTenantClientOptions() : [];
  for (const entry of inventory) {
    for (const value of Array.isArray(entry.orgIds) ? entry.orgIds : []) {
      const orgId = String(value || "").trim();
      const label = clientActionCompanyName(entry.label, orgId) || clientActionCompanyName(entry.value, orgId);
      if (!orgId || !label) continue;
      if (!names.has(orgId)) names.set(orgId, new Set());
      names.get(orgId).add(label);
    }
  }
  for (const [orgId, candidates] of names) {
    const prior = clientActionCompanyName(labels.get(orgId), orgId);
    if (candidates.size === 1 && !prior) {
      labels.set(orgId, [...candidates][0]);
    }
  }
  return labels;
}
function clientActionCompany(row = {}, labels = null) {
  const orgId = clientActionOrg(row);
  return clientActionCompanyName(row.companyName, orgId)
    || clientActionCompanyName(row.company, orgId)
    || clientActionCompanyName(labels && labels.get(orgId), orgId)
    || "Company name unavailable";
}
// WHY THIS PAGE NARROWS BY COMPANY AT ALL.
//
// The queue is a projection of a follow-up marker, and a marker belongs to one
// practice. A biller whose book is Practice A was reading Practice A's requests
// interleaved with four other practices' - same list, same lanes, same counts -
// because this surface was the only org-keyed one in the portal that never
// joined the header client picker (applyActiveClientGroupToSurfaces) and never
// grew a company control of its own.
//
// Two narrowings, in this order:
//   1. the header client picker  - activeClientGroupOrgIds(), portal-wide
//   2. this page's own select    - clientActionWorkspaceState.orgId
// Neither is security. Both are honesty about whose work is on screen.
function clientActionGroupOrgIds() {
  const ids = typeof activeClientGroupOrgIds === "function" ? activeClientGroupOrgIds() : [];
  return (Array.isArray(ids) ? ids : []).map((value) => String(value || "").trim()).filter(Boolean);
}

// Options come from the ROWS ON HAND rather than the tenant catalog: a company
// with nothing waiting is not a choice worth offering, and the label resolves
// through the same roster Manage Users reads.
function clientActionOrgOptions() {
  const labels = clientActionOrgLabels();
  const group = clientActionGroupOrgIds();
  const permitted = group.length ? new Set(group) : null;
  const seen = new Map();
  for (const orgId of clientActionWorkspaceState.permittedOrgIds || []) {
    if (!permitted || permitted.has(orgId)) seen.set(orgId, clientActionCompany({ orgId }, labels));
  }
  for (const row of clientActionWorkspaceState.rows) {
    const orgId = clientActionOrg(row);
    if (!orgId || (permitted && !permitted.has(orgId))) continue;
    if (!seen.has(orgId)) seen.set(orgId, clientActionCompany(row, labels));
  }
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label: String(label) }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

// The company the queue is currently scoped to, re-resolved against what is
// actually loaded on every read (the authorizations pattern). A pick that no
// longer exists - the last request closed, the header scope moved, a different
// login on the same browser - degrades to "all companies" instead of pinning
// the page to an empty list under a company name.
function activeClientActionOrgId() {
  const options = clientActionOrgOptions();
  if (options.length === 1) return options[0].value;
  const current = String(clientActionWorkspaceState.orgId || "").trim();
  return current && options.some((option) => option.value === current) ? current : "";
}

// null means "no narrowing" so callers can skip the filter pass entirely.
function clientActionScopeOrgIds() {
  const picked = activeClientActionOrgId();
  if (picked) return new Set([picked]);
  const group = clientActionGroupOrgIds();
  return group.length ? new Set(group) : null;
}

// Every count on this page - lane totals and the sidebar badge - reads through
// here, so a narrowed list can never sit under a number that counts companies
// it is not showing.
function scopedClientActionRows() {
  if (clientActionWorkspaceState.prepared) return clientActionWorkspaceState.rows;
  const scope = clientActionScopeOrgIds();
  if (!scope) return clientActionWorkspaceState.rows;
  return clientActionWorkspaceState.rows.filter((row) => scope.has(clientActionOrg(row)));
}
// Only offer a category chip when the current lane actually has requests in it:
// a facility with no EVV work should never see an EVV chip that opens onto
// nothing. The prepared read counts every category for the lane regardless of
// the category filter, so those counts decide; the legacy read has every row.
function availableClientActionCategories() {
  const { lane, categoryCounts } = clientActionWorkspaceState;
  const present = new Set();
  if (clientActionWorkspaceState.prepared && !categoryCounts) return clientActionCategories;
  if (clientActionWorkspaceState.prepared) {
    for (const [category, total] of Object.entries(categoryCounts)) if (Number(total) > 0) present.add(String(category).toLowerCase());
  } else {
    for (const row of scopedClientActionRows()) {
      const rowLane = clientActionLaneFor(row);
      if (lane === "open" ? rowLane !== "resolved" : rowLane === lane) present.add(clientActionBoardCategory(row));
    }
  }
  return clientActionCategories.filter((category) => category === "all" || present.has(category));
}

// Mirrors the server: credentialing searches providers, authorization,
// coverage, COB and EVV search clients, General and All search both.
function clientActionSearchFields(category = clientActionWorkspaceState.category) {
  if (category === "credentialing") return ["provider"];
  if (["authorization", "secondary-authorization", "coverage", "cob", "evv"].includes(category)) return ["client"];
  return ["client", "provider"];
}
function clientActionSearchLabel(category = clientActionWorkspaceState.category) {
  const fields = clientActionSearchFields(category);
  return fields.length > 1 ? "Client or provider" : fields[0] === "provider" ? "Provider" : "Client";
}
function clientActionRowNames(row = {}) {
  const active = clientActionItems(row).filter(clientActionItemActive);
  const unique = (values) => [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))];
  return { clients: unique(active.map((item) => item.patient || item.patientName)), providers: unique(active.map((item) => item.provider || item.providerLabel)) };
}
function clientActionSearchMatches(row, term = clientActionWorkspaceState.search) {
  const needle = String(term || "").trim().toLowerCase();
  if (!needle) return true;
  const fields = clientActionSearchFields(), names = clientActionRowNames(row);
  return [...(fields.includes("client") ? names.clients : []), ...(fields.includes("provider") ? names.providers : [])].some((name) => name.toLowerCase().includes(needle));
}
// Names for the picker, grouped by kind: the server's list on the paged read,
// otherwise the names on the rows already in hand for this lane and category.
// The list is NOT narrowed by the current pick, so a biller can move straight
// from one client to the next without clearing the filter in between.
function clientActionSearchNameGroups() {
  const fields = clientActionSearchFields();
  const options = clientActionWorkspaceState.searchOptions;
  const groups = { clients: new Set(), providers: new Set() };
  if (clientActionWorkspaceState.prepared) {
    if (options) for (const key of ["clients", "providers"]) for (const name of options[key] || []) if (String(name).trim()) groups[key].add(String(name));
  } else {
    const { lane, category } = clientActionWorkspaceState;
    for (const row of scopedClientActionRows()) {
      const rowLane = clientActionLaneFor(row);
      if (lane === "open" ? rowLane === "resolved" : rowLane !== lane) continue;
      if (category !== "all" && clientActionBoardCategory(row) !== category) continue;
      const found = clientActionRowNames(row);
      found.clients.forEach((name) => groups.clients.add(name));
      found.providers.forEach((name) => groups.providers.add(name));
    }
  }
  const sorted = (set) => [...set].sort((left, right) => left.localeCompare(right));
  return { clients: fields.includes("client") ? sorted(groups.clients) : [], providers: fields.includes("provider") ? sorted(groups.providers) : [] };
}
function clientActionSearchNames() {
  const groups = clientActionSearchNameGroups();
  return [...new Set([...groups.clients, ...groups.providers])].sort((left, right) => left.localeCompare(right));
}
// One dropdown filters the queue to a client (authorization, coverage, COB,
// EVV), a provider (credentialing) or either (General, All). A typed search box
// with a datalist stood here before and nobody found it: the request was still
// "there is no easy way to filter to a client", so the names are now the control.
function clientActionNamePicker() {
  const fields = clientActionSearchFields();
  const picked = clientActionWorkspaceState.search;
  const groups = clientActionSearchNameGroups();
  const option = (name) => `<option value="${escapeHtml(name)}"${name === picked ? " selected" : ""}>${escapeHtml(name)}</option>`;
  const known = [...groups.clients, ...groups.providers];
  const count = fields.length > 1 ? known.length : (fields[0] === "provider" ? groups.providers : groups.clients).length;
  const noun = fields.length > 1 ? "clients and providers" : fields[0] === "provider" ? "providers" : "clients";
  const body = fields.length > 1
    ? `<optgroup label="Clients">${groups.clients.map(option).join("")}</optgroup><optgroup label="Providers">${groups.providers.map(option).join("")}</optgroup>`
    : (fields[0] === "provider" ? groups.providers : groups.clients).map(option).join("");
  // A pick that no longer appears in this lane or category still filters the
  // queue, so it stays visible in the control instead of silently showing "All".
  const stale = picked && !known.includes(picked) ? option(picked) : "";
  return `<label class="client-action-search" for="clientActionNameSelect"><span>${escapeHtml(clientActionSearchLabel())}</span><select id="clientActionNameSelect" data-cap-name-select aria-label="Filter requests by ${escapeHtml(clientActionSearchLabel().toLowerCase())}">
        <option value=""${picked ? "" : " selected"}>All ${noun} (${count})</option>${stale}${body}
      </select></label>`;
}
function visibleClientActionRows() {
  if (clientActionWorkspaceState.prepared) return clientActionWorkspaceState.rows;
  const { lane, category } = clientActionWorkspaceState;
  return scopedClientActionRows()
    .filter((row) => {
      const rowLane = clientActionLaneFor(row);
      if (lane === "open" ? rowLane === "resolved" : rowLane !== lane) return false;
      if (category !== "all" && clientActionBoardCategory(row) !== category) return false;
      return clientActionSearchMatches(row);
    })
    // MONEY FIRST. DUE DATE DOES NOT DISCRIMINATE ON THIS BOARD.
    //
    // This sorted by due date ascending, which reads as the obvious choice until you
    // look at the data: 51 of the 52 open cases are already past due, the oldest since
    // 2026-04-13. Sorting a list where almost every row is overdue by HOW overdue puts
    // the oldest paperwork on top, not the most valuable - measured live, a $13,787.77
    // case sat at position 20 of 52 underneath cases worth $88 and $153.
    //
    // So: dollars descending, due date only as the tiebreak. The expected figure is the
    // same one the card prints, computed by clientActionSummary, so the order a biller
    // sees always matches the numbers they are reading.
    .map((row) => ({ row, expected: Number(clientActionSummary(row).expected) || 0 }))
    .sort((left, right) => {
      if (left.expected !== right.expected) return right.expected - left.expected;
      const leftDue = String(left.row.dueDate || "9999-12-31");
      const rightDue = String(right.row.dueDate || "9999-12-31");
      if (leftDue !== rightDue) return leftDue < rightDue ? -1 : 1;
      return String(left.row.createdAtUtc || "").localeCompare(String(right.row.createdAtUtc || ""));
    })
    .map((entry) => entry.row);
}
function clientActionCounts() {
  if (clientActionWorkspaceState.prepared && clientActionWorkspaceState.counts) return clientActionWorkspaceState.counts;
  const counts = Object.fromEntries(CLIENT_ACTION_LANES.map((lane) => [lane.id, 0]));
  for (const row of scopedClientActionRows()) {
    const lane = clientActionLaneFor(row);
    counts[lane] = (counts[lane] || 0) + 1;
    if (lane !== "resolved") counts.open += 1;
  }
  return counts;
}
function updateClientActionBadge() {
  const badge = document.getElementById("clientActionNavBadge");
  if (!badge) return;
  // OFF ITS OWN SCREEN, THIS COUNT CAME FROM A LIST NOBODY HAD FETCHED.
  //
  // clientActionCounts reads clientActionWorkspaceState.rows, which is populated
  // only by the Client Action screen itself - so the sidebar read a bare
  // "Client Action Pending" from All Data and from Reports, and showed the number
  // only once you were already looking at it. Its two siblings badge from every
  // screen because they are served by /api/navigation/pending-counts on boot, and
  // this lane simply was not in that response. It is now.
  //
  // The loaded rows still win when they exist: they are the live, company-scoped
  // list the user is actually looking at, and the preloaded total is a boot-time
  // snapshot. The fallback only fills the gap before that list arrives.
  const counts = clientActionCounts();
  // A client sees what it owes us; staff see the whole open book. Counting the
  // signed-in party's "needs response" lane alone kept this badge hidden for
  // every internal user, because staff cases sit in the other lanes.
  const loaded = clientActionWorkspaceState.status === "ready";
  const preloaded = navigationPendingCountState.clientAction || { total: 0, available: false };
  const count = loaded
    ? (isExternalClientRole(state.role) ? counts["waiting-on-client"] || 0 : counts.open || 0)
    : (preloaded.available ? Number(preloaded.total) || 0 : 0);
  badge.textContent = String(count);
  badge.hidden = count === 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// "WAITING ON YOU" — the client's hub
// ═══════════════════════════════════════════════════════════════════════════
//
// A practice owner signs in and lands on Reports (Client) or All Data
// (RedactedClient); Client Home was retired for every role on 2026-08-21. The
// three lanes that can be waiting on them — information requests, authorizations,
// credentialing — each sit behind their own tab and each show only a number. So
// the answer to "what does TMS need from me" lived nowhere, and 24 open requests
// worth $71,333.54 for one practice had never reached a human at all.
//
// This paints it directly under the header, on EVERY screen, for external client
// roles only. It is an addition to the shell, not a new one: same panel surface,
// same type ladder, same chips and buttons as the surfaces either side of it.
//
// AGING IS THE POINT. Each request says how long it has been sitting, measured
// from WHEN WE ASKED (backend/clientWaitingOnYou.js). A request the client has
// never been sent carries no age and no due date and can never render as late —
// on 2026-08-23 this exact panel's data source was telling one practice it was
// "200 days overdue" on things nobody had ever asked them for.
//
// NOTHING IS EVER SHOWN AS ZERO BECAUSE IT FAILED TO LOAD. Each lane carries its
// own `available`; a lane that did not answer renders "Unavailable", never "0".
const WAITING_HUB_MAX_VISIBLE = 4;
// COLLAPSED IS THE SHAPE THIS SHIPS IN.
//
// Measured: 619px at 1380 wide, 745px on an ordinary 1366x728 laptop (102% of
// the viewport) and 1367px at 390 (162%). On All Data - a client's own landing
// page - not one grid row or filter was above the fold. Collapsed it is 138px,
// and the header still carries the headline, the oldest age and all three lane
// counts, so nothing about the ask is hidden.
//
// AND THIS DOES NOT WEAKEN DELIVERY. The stamp fires server-side on the feed
// read, not on expansion; the panel being closed changes nothing about what was
// served. (It is also why the server now stamps only the items it serves.)
//
// The preference survives a reload, because "Hide details" that resets every
// time you open the portal is not a preference, it is a nuisance.
const WAITING_HUB_COLLAPSE_KEY = "tms.waitingHub.collapsed";
function waitingHubCollapsedPreference() {
  try {
    const stored = window.localStorage.getItem(WAITING_HUB_COLLAPSE_KEY);
    return stored === null ? true : stored === "1";
  } catch (_) { return true; }
}
const waitingOnYouState = {
  status: "idle", // idle | loading | ready | error
  key: "",
  generation: 0,
  controller: null,
  data: null,
  collapsed: waitingHubCollapsedPreference()
};

// External client roles only. This is the CHROME test — the server re-derives the
// principal and its scope on every call, and the role switcher (which flips
// state.role without re-authenticating) cannot widen what comes back.
function waitingOnYouEligible() {
  if (typeof isExternalClientRole !== "function" || !isExternalClientRole(state.role)) return false;
  return hasPermission(PERMISSIONS.CLIENT_ACTION_VIEW);
}

function waitingOnYouDescriptor() {
  const token = typeof trackerApiToken === "function" ? trackerApiToken() : "";
  const base = typeof trackerApiBase === "function" ? trackerApiBase() : "";
  if (!token || !base || typeof fetch !== "function") return null;
  const orgIds = Array.from(new Set(activeClientGroupOrgIds().map((value) => String(value || "").trim()).filter(Boolean))).sort();
  return {
    base: base.replace(/\/$/, ""),
    token,
    orgIds,
    // Auth-scoped and generation-bound, held only in runtime memory.
    key: JSON.stringify({ readScope: additionalPortalDataReadScope(), principal: navigationPendingPrincipalMarker(), orgIds })
  };
}

function requestWaitingOnYou(force = false) {
  if (!waitingOnYouEligible()) {
    if (waitingOnYouState.status !== "idle" || waitingOnYouState.data) {
      waitingOnYouState.generation += 1;
      Object.assign(waitingOnYouState, { status: "idle", key: "", data: null, controller: null });
    }
    renderWaitingOnYouHub();
    return;
  }
  const descriptor = waitingOnYouDescriptor();
  if (!descriptor) { renderWaitingOnYouHub(); return; }
  if (descriptor.key !== waitingOnYouState.key) {
    try { waitingOnYouState.controller?.abort(); } catch (_) {}
    waitingOnYouState.generation += 1;
    Object.assign(waitingOnYouState, { status: "idle", key: descriptor.key, data: null, controller: null });
  } else if (waitingOnYouState.status === "loading" || (waitingOnYouState.status !== "idle" && !force)) {
    renderWaitingOnYouHub();
    return;
  }
  const generation = ++waitingOnYouState.generation;
  const current = () => generation === waitingOnYouState.generation && descriptor.key === waitingOnYouDescriptor()?.key;
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timeoutId = window.setTimeout(() => { try { controller?.abort(); } catch (_) {} }, 60000);
  waitingOnYouState.controller = controller;
  waitingOnYouState.status = "loading";
  renderWaitingOnYouHub();
  const params = new URLSearchParams();
  descriptor.orgIds.forEach((orgId) => params.append("orgId", orgId));
  const query = params.toString() ? "?" + params.toString() : "";
  fetch(additionalPortalDataReadUrl(descriptor.base + "/api/client/waiting-on-you" + query), {
    method: "GET",
    cache: "no-store",
    headers: { authorization: "Bearer " + descriptor.token },
    signal: controller ? controller.signal : undefined
  }).then(async (response) => {
    window.clearTimeout(timeoutId);
    if (!current()) return;
    const payload = await response.json().catch(() => ({}));
    if (!current()) return;
    waitingOnYouState.controller = null;
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok || (payload && payload.error)) {
      waitingOnYouState.status = "error";
      waitingOnYouState.data = null;
    } else {
      waitingOnYouState.status = "ready";
      waitingOnYouState.data = payload;
    }
    renderWaitingOnYouHub();
  }).catch(() => {
    window.clearTimeout(timeoutId);
    if (!current()) return;
    waitingOnYouState.controller = null;
    waitingOnYouState.status = "error";
    waitingOnYouState.data = null;
    renderWaitingOnYouHub();
  });
}

// Colour follows the promise we made them, not a hunch: CLIENT_RESPONSE_WINDOW_DAYS
// is 14 days, so 14+ is past the window we quoted and half of it is the warning.
// A request never sent is never late, whatever its dates say.
function waitingHubAgeTone(agingDays) {
  if (agingDays === null || agingDays === undefined) return "is-unsent";
  const days = Number(agingDays);
  if (!Number.isFinite(days)) return "is-unsent";
  if (days >= 14) return "is-late";
  if (days >= 7) return "is-warn";
  return "is-fresh";
}

// "Unavailable", never a number, for a lane that did not answer. A believable 0
// over a lane that threw reads exactly like good news.
//
// A LANE WITH WORK IN IT IS A DOOR. Authorizations and Credentialing contribute
// COUNTS, not items - neither of those tables records when the client was asked,
// and deriving an age from the biller's follow-up date is the "200 days overdue"
// defect this whole lane exists to kill - so the chip itself is the only way in.
// Without that, a practice whose entire backlog is three authorizations reads
// "3 things need your practice" and the one button on the panel opens an empty
// requests page. The destination travels in the payload; the click handler
// re-checks it against the nav gate before moving anyone.
function waitingHubLaneChip(lane = {}) {
  const label = escapeHtml(String(lane.label || ""));
  if (lane.available !== true) return `<span class="waiting-hub-lane is-unavailable"><span>${label}</span><b>Unavailable</b></span>`;
  const total = Math.max(0, Math.floor(Number(lane.total) || 0));
  const view = String(lane.view || "");
  const body = `<span>${label}</span><b>${formatCount(total)}</b>`;
  if (!total || !view) return `<span class="waiting-hub-lane${total ? "" : " is-clear"}">${body}</span>`;
  return `<button type="button" class="waiting-hub-lane is-actionable" data-waiting-go="${escapeHtml(view)}">${body}</button>`;
}

function waitingHubItemHtml(item = {}, showCompany = false) {
  const tone = waitingHubAgeTone(item.agingDays);
  // A COMPANY NEVER FALLS BACK TO SILENCE.
  //
  // orgName arrives empty whenever the directory could not name the org, and the
  // label was rendered only when non-empty - so a client holding two practices
  // saw multiCompany layout with no company on either row and no way to tell
  // which practice an ask belonged to. The id is ugly; nothing is worse.
  const companyName = String(item.orgName || "").trim() || String(item.orgId || "").trim();
  const company = showCompany
    ? `<span class="waiting-hub-company">${escapeHtml(companyName || "Company not named")}</span>`
    : "";
  const lines = Number(item.lineCount) > 0 ? `<span class="waiting-hub-meta">${escapeHtml(formatNounCount(item.lineCount, "service line"))}</span>` : "";
  // THE AGE LEADS, AND IT IS THE SAME WIDTH ON EVERY ROW.
  //
  // Measured left edges down the list were 304, 304, 426, 426: the optional
  // category chip shoved the one element the panel exists to make scannable by
  // 122px, so the ages zigzagged. The chip is gone with it - the title already
  // opens with the category on every request the summary builder writes
  // ("Authorization needed — BALL, ADALIS"), and where it did not the chip was
  // vaguer than the title beside it ("Information needed" next to "Documentation
  // needed"). Two names for one thing, one of them worse.
  return `<li class="waiting-hub-item">
      <div class="waiting-hub-item-main">
        <div class="waiting-hub-item-head"><span class="waiting-hub-age ${tone}">${escapeHtml(String(item.agingLabel || ""))}</span>${company}</div>
        <h4>${escapeHtml(String(item.title || "Information needed"))}</h4>
        <p>${escapeHtml(String(item.requestText || ""))}</p>
      </div>
      <div class="waiting-hub-item-aside">
        ${lines}
        <button type="button" class="small-button" data-waiting-case="${escapeHtml(String(item.caseId || ""))}" data-waiting-org="${escapeHtml(String(item.orgId || ""))}">Open request</button>
      </div>
    </li>`;
}

const WAITING_HUB_HIDDEN_VIEWS = new Set(["statements", "clientActionPending", "authorizations", "credentialing", "training"]);
function renderWaitingOnYouHub() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender(state.view)) return;
  const host = document.getElementById("waitingOnYouHub");
  if (!host) return;
  if (!waitingOnYouEligible()) { host.hidden = true; host.innerHTML = ""; return; }
  // ONE LINE, AND NOT WHERE IT IS IN THE WAY (Nathan, 2026-10-13: "taking up
  // way too much room", clients reading Patient Statements kept seeing it).
  // The pages that ARE the work (requests, authorizations, credentialing) list
  // it themselves, and Patient Statements is unrelated. The feed is still read
  // on every screen (requestWaitingOnYou), so server-side delivery stamping is
  // unchanged; only the display is narrower.
  if (WAITING_HUB_HIDDEN_VIEWS.has(state.view)) { host.hidden = true; host.innerHTML = ""; return; }
  host.hidden = false;
  bindWaitingOnYouHub(host);

  if (waitingOnYouState.status === "loading" || waitingOnYouState.status === "idle") {
    // NEVER "0 things need you" while the answer is still in flight — that is the
    // one message that would make a client stop looking.
    host.innerHTML = `<div class="waiting-hub-head"><div class="waiting-hub-heading">
        <span class="waiting-hub-eyebrow">Waiting on you</span>
        <h3 id="waitingOnYouHubTitle">Checking what needs your practice&hellip;</h3>
      </div></div>`;
    return;
  }
  if (waitingOnYouState.status === "error" || !waitingOnYouState.data) {
    host.innerHTML = `<div class="waiting-hub-head"><div class="waiting-hub-heading">
        <span class="waiting-hub-eyebrow">Waiting on you</span>
        <h3 id="waitingOnYouHubTitle">We could not check just now</h3>
        <p>We could not check what is waiting on you just now. Nothing has been lost &mdash; try again in a moment.</p>
      </div><div class="waiting-hub-actions"><button type="button" class="ghost-button" data-waiting-retry>Try again</button></div></div>`;
    return;
  }

  const feed = waitingOnYouState.data || {};
  const lanes = Array.isArray(feed.lanes) ? feed.lanes : [];
  const items = Array.isArray(feed.items) ? feed.items : [];
  const totalAvailable = feed.totalAvailable === true;
  const total = totalAvailable ? Math.max(0, Math.floor(Number(feed.total) || 0)) : null;
  const unavailable = Array.isArray(feed.unavailableLanes) ? feed.unavailableLanes : [];
  const showCompany = feed.multiCompany === true;

  // A REASSURING HEADLINE IS A CLAIM ABOUT THE WHOLE BOARD.
  //
  // totalAvailable only means SOME lane answered. With the requests lane down and
  // the other two answering zero, this printed "Nothing needs you right now" over
  // "there is nothing to do today" - byte-identical to a genuinely clear board -
  // while the only lane carrying dollar-bearing asks was dark with twelve unread
  // requests behind it. A reviewer's verdict was the entire defect: "I read the
  // bold line, see nothing to do today, and close the tab."
  //
  // So reassurance requires EVERY lane to have answered. A partial answer says
  // which part it is, and promises nothing about the rest.
  const everyLaneAnswered = unavailable.length === 0;
  let headline;
  if (!totalAvailable) headline = "We could not check just now";
  else if (total === 0 && !everyLaneAnswered) headline = "Nothing in the areas we could check";
  else if (total === 0) headline = "Nothing needs you right now";
  else headline = `${escapeHtml(formatNounCount(total, "open request"))} ${total === 1 ? "needs" : "need"} your practice`;

  const notSent = Math.max(0, Math.floor(Number(feed.notYetSentCount) || 0));
  const subParts = [];
  if (totalAvailable && total === 0) subParts.push(everyLaneAnswered
    ? "Anything we need from you shows up here first &mdash; there is nothing to do today."
    : "Part of your board did not answer, so this is not the whole picture.");
  // NAME THE LANE THE AGE BELONGS TO.
  //
  // oldestAgingDays is computed over the information requests alone, because they
  // are the only lane that records when the client was asked - the backend sets
  // agingTracked: false on the other two and says why. app.js read that flag
  // nowhere, so a bare "Oldest: asked 34 days ago" was laundered into a claim
  // about all three lanes: a practice triages on that ("worst is 34 days, Friday
  // will do") while six of eleven items could be six months old.
  if (feed.oldestAgingLabel) subParts.push(`Oldest information request: ${escapeHtml(String(feed.oldestAgingLabel).toLowerCase())}`);
  // A LANE THAT ANSWERED FOR SOME COMPANIES AND NOT OTHERS IS NOT A CLEAN NUMBER.
  // The backend computes `partial` for exactly that and app.js discarded it, so a
  // shortfall the server had already detected was shown as a whole count.
  const partialLanes = lanes.filter((lane) => lane.partial === true);
  if (partialLanes.length) subParts.push(`${partialLanes.map((lane) => escapeHtml(String(lane.label))).join(" and ")} could not be counted for every company.`);
  // And the case the server DELIBERATELY declines to fabricate: past six
  // companies it answers the count lanes once for the whole scope rather than
  // inventing a per-company split. The reader is entitled to know that.
  if (feed.companyBreakdownAvailable === false) subParts.push("You hold more companies than we can break these counts down by, so the totals cover all of them together.");
  const countOnlyLanes = lanes.filter((lane) => lane.available === true && lane.agingTracked === false && Number(lane.total) > 0);
  // Internal bookkeeping (which lanes record an ask date, requests not yet
  // sent) is not the client's concern and made the one line unreadable.
  void countOnlyLanes; void notSent;
  if (unavailable.length) subParts.push(`${escapeHtml(formatNounCount(unavailable.length, "area"))} we could not check`);

  // A COUNT-ONLY BACKLOG MUST NOT DEAD-END.
  //
  // A practice whose whole backlog is three authorizations read "3 things need
  // your practice", showed no items, and the only button-shaped thing on the
  // panel opened an empty requests page. When there is nothing to list, the
  // primary action points at the board that actually holds the work.
  const workingCountLane = items.length
    ? null
    : lanes.find((lane) => lane.available === true && Number(lane.total) > 0 && String(lane.view || ""));
  const primaryView = workingCountLane ? String(workingCountLane.view) : "clientActionPending";
  const primaryLabel = workingCountLane ? `Open ${String(workingCountLane.label).toLowerCase()}` : "Open my requests";
  // Nothing to act on and nothing unknown: the panel says so and stops. Chips
  // reading three zeroes and a button into an empty page are furniture.
  const boardIsClear = totalAvailable && total === 0 && everyLaneAnswered;
  const laneChips = boardIsClear ? "" : lanes.map(waitingHubLaneChip).join("");
  // PER COMPANY, WHEN THE SERVER SPLIT IT. The backend builds feed.companies and
  // app.js read it nowhere, so a two-practice principal got one merged number and
  // no way to see which practice it belonged to. Absence is not zero here either:
  // a company whose lanes all failed reads Unavailable, never 0.
  const companyRows = showCompany && feed.companyBreakdownAvailable !== false && Array.isArray(feed.companies) && feed.companies.length > 1
    ? `<ul class="waiting-hub-companies">${feed.companies.map((company) => {
        const name = escapeHtml(String(company.orgName || "").trim() || String(company.orgId || "").trim() || "Company not named");
        const count = company.totalAvailable === true
          ? escapeHtml(formatCount(Math.max(0, Math.floor(Number(company.total) || 0))))
          : "Unavailable";
        return `<li><span>${name}</span><b>${count}</b></li>`;
      }).join("")}</ul>`
    : "";
  const visible = waitingOnYouState.collapsed ? [] : items.slice(0, WAITING_HUB_MAX_VISIBLE);
  const hidden = Math.max(0, items.length - visible.length) + Math.max(0, Math.floor(Number(feed.moreItems) || 0));
  const list = visible.length
    ? `<ul class="waiting-hub-list">${visible.map((item) => waitingHubItemHtml(item, showCompany)).join("")}</ul>`
    : "";
  const more = !waitingOnYouState.collapsed && hidden
    // NAME THE LANE. The headline sums all three lanes and this footer counts one
    // of them, so "45 things need your practice" over four rows and "20 more
    // requests" invited the reader to check 4 + 20 against 45 and find it wrong.
    ? `<div class="waiting-hub-more"><button type="button" class="link-button" data-waiting-go="clientActionPending">${escapeHtml(formatNounCount(hidden, "more information request"))} &rarr;</button></div>`
    : "";

  const actions = [];
  if (items.length) actions.push(`<button type="button" class="ghost-button" data-waiting-collapse>${waitingOnYouState.collapsed ? "Show details" : "Hide details"}</button>`);
  if (!boardIsClear) actions.push(`<button type="button" class="primary-button" data-waiting-go="${escapeHtml(primaryView)}">${escapeHtml(primaryLabel)}</button>`);

  // Compact: the headline, the one age that matters and the way in. Lane counts
  // already sit on the sidebar badges; the request cards live on the requests
  // page. A multi-company split stays, because it answers "which practice?".
  void list; void more; void actions;
  // Area chips stay only when an area failed or answered partly: then
  // "Unavailable" must be visible per area, never an absent number.
  const showLaneChips = Boolean(laneChips) && (unavailable.length > 0 || partialLanes.length > 0);
  const goButton = boardIsClear ? "" : `<button type="button" class="primary-button" data-waiting-go="${escapeHtml(primaryView)}">${escapeHtml(primaryLabel)}</button>`;
  host.innerHTML = `<div class="waiting-hub-head waiting-hub-head--compact">
      <div class="waiting-hub-heading">
        <h3 id="waitingOnYouHubTitle">${headline}</h3>
        ${subParts.length ? `<p>${subParts.join(" &middot; ")}</p>` : ""}
      </div>
      <div class="waiting-hub-actions">${goButton}</div>
    </div>
    ${showLaneChips ? `<div class="waiting-hub-lanes">${laneChips}</div>` : ""}
    ${companyRows}`;
}

function bindWaitingOnYouHub(host) {
  if (!host || host.dataset.waitingHubBound === "1") return;
  host.dataset.waitingHubBound = "1";
  host.addEventListener("click", (event) => {
    const closest = (selector) => (event.target && event.target.closest ? event.target.closest(selector) : null);
    if (closest("[data-waiting-retry]")) { requestWaitingOnYou(true); return; }
    if (closest("[data-waiting-collapse]")) {
      waitingOnYouState.collapsed = !waitingOnYouState.collapsed;
      try { window.localStorage.setItem(WAITING_HUB_COLLAPSE_KEY, waitingOnYouState.collapsed ? "1" : "0"); } catch (_) {}
      renderWaitingOnYouHub();
      return;
    }
    const caseButton = closest("[data-waiting-case]");
    if (caseButton) {
      // Re-checked at click time. The panel is chrome; the nav gate is the door.
      if (!viewIsAvailable("clientActionPending")) return;
      navigateToView("clientActionPending");
      if (typeof loadClientActionDetail === "function") {
        loadClientActionDetail(caseButton.getAttribute("data-waiting-case") || "", caseButton.getAttribute("data-waiting-org") || "");
      }
      return;
    }
    const go = closest("[data-waiting-go]");
    if (go) {
      const view = go.getAttribute("data-waiting-go") || "";
      if (view && viewIsAvailable(view)) navigateToView(view);
    }
  });
}
function clientActionLoadMessage(status) {
  if (status === 401) return "Your sign-in expired. Sign in again to see these requests.";
  if (status === 403) return "Your account cannot see this client's requests.";
  if (status === 503) return "This workspace is still gathering requests from the tracker. Try again in a moment.";
  return "We couldn't load these requests. Try again.";
}
async function loadClientActionCases(force = false) {
  if (!hasPermission(PERMISSIONS.CLIENT_ACTION_VIEW)) return;
  const base = clientActionBase();
  if (!base) {
    clientActionWorkspaceState.status = "error";
    clientActionWorkspaceState.error = "This workspace is unavailable right now.";
    renderClientActionPending();
    return;
  }
  // Prepared queues request only their selected page, lane and category.
  const needResolved = clientActionWorkspaceState.lane === "resolved" || clientActionWorkspaceState.resolvedLoaded;
  // The header client scope is part of the KEY, not just the query. Without it a
  // client-group switch reused the previous company's cached response and the
  // page looked unchanged - which is how a scoping control fails silently.
  const serverOrgId = activeClientActionOrgId() || (typeof activeClientGroupServerOrgId === "function" ? activeClientGroupServerOrgId() : "");
  const groupOrgIds = clientActionGroupOrgIds();
  const readScope = clientActionReadScope();
  if (clientActionWorkspaceState.readScope && clientActionWorkspaceState.readScope !== readScope) resetClientActionReadResponses();
  const key = JSON.stringify({ readScope, base, needResolved, serverOrgId, groupOrgIds, lane: clientActionWorkspaceState.lane, category: clientActionWorkspaceState.category, page: clientActionWorkspaceState.page, search: clientActionWorkspaceState.search, user: state.activeUserName, role: state.role });
  if (!force && clientActionWorkspaceState.key === key && ["loading", "ready"].includes(clientActionWorkspaceState.status)) return;
  try { clientActionWorkspaceState.controller?.abort(); } catch (_) {}
  const controller = new AbortController(); clientActionWorkspaceState.controller = controller;
  const sequence = ++clientActionListReadSequence;
  const current = () => sequence === clientActionListReadSequence && readScope === clientActionReadScope() && clientActionWorkspaceState.key === key;
  Object.assign(clientActionWorkspaceState, { key, readScope, status: "loading", error: "" });
  renderClientActionPending();
  const query = new URLSearchParams();
  if (needResolved) query.set("includeResolved", "1");
  query.set("lane", clientActionWorkspaceState.lane); query.set("category", clientActionWorkspaceState.category); query.set("page", String(clientActionWorkspaceState.page));
  if (clientActionWorkspaceState.search) query.set("search", clientActionWorkspaceState.search);
  if (groupOrgIds.length) query.set("orgIds", JSON.stringify(groupOrgIds));
  // Sent only for a tenant that owns exactly ONE org id, exactly as the
  // authorizations board does. It NARROWS; the server re-derives the permitted
  // set from the principal and refuses an org outside it rather than trusting
  // this value. A multi-org tenant is narrowed on screen instead.
  if (serverOrgId) query.set("orgId", serverOrgId);
  // Writes re-read current transactional decisions; source refresh has a separate durable receipt.
  try {
    // A worker that is still starting answers 502/503/504 once; retry that
    // single transient answer before telling anyone the requests could not load.
    const readCases = () => fetch(`${base}/api/client-action?${query}`, { cache: "no-store", headers: clientActionHeaders(), signal: controller.signal });
    let response = await readCases();
    if ([502, 503, 504].includes(response.status) && current() && !controller.signal.aborted) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      if (!current() || controller.signal.aborted) return;
      response = await readCases();
    }
    if (!current()) return;
    if (!response.ok) throw Object.assign(new Error("client_action_request_failed"), { status: response.status });
    const payload = await response.json();
    if (!current()) return;
    clientActionWorkspaceState.rows = Array.isArray(payload.cases) ? payload.cases : Array.isArray(payload.items) ? payload.items : [];
    clientActionWorkspaceState.prepared = payload.prepared === true;
    clientActionWorkspaceState.pageInfo = payload.page || null; clientActionWorkspaceState.counts = payload.counts || null;
    clientActionWorkspaceState.searchOptions = payload.search || null;
    clientActionWorkspaceState.categoryCounts = payload.categoryCounts || null; clientActionWorkspaceState.permittedOrgIds = payload.permittedOrgIds || payload.orgIds || [];
    clientActionWorkspaceState.resolvedLoaded = payload.prepared === true || needResolved;
    clientActionWorkspaceState.status = "ready";
    const rows = visibleClientActionRows();
    if (!clientActionWorkspaceState.selectedId || (!clientActionWorkspaceState.prepared && !rows.some((row) => clientActionId(row) === clientActionWorkspaceState.selectedId))) {
      // On a narrow screen the detail pane replaces the queue, so auto-selecting
      // would drop someone into one request instead of showing them the list.
      clientActionSelect(clientActionNarrowLayout() ? null : rows[0] || null);
      return;
    }
    if (clientActionWorkspaceState.selectedId && clientActionWorkspaceState.detailStatus === "idle") {
      loadClientActionDetail(clientActionWorkspaceState.selectedId, clientActionWorkspaceState.selectedOrgId);
    } else renderClientActionPending();
  } catch (error) {
    if (!current()) return;
    // Keeping the status makes the next "it doesn't load" report diagnosable
    // instead of collapsing 401, 403 and 503 into one generic sentence.
    console.error("Client Action Pending list failed", error?.status || error);
    clientActionWorkspaceState.status = "error";
    clientActionWorkspaceState.error = clientActionLoadMessage(error?.status);
    renderClientActionPending();
  }
}
function clientActionSelect(row) {
  if (!row) {
    Object.assign(clientActionWorkspaceState, { selectedId: "", selectedOrgId: "", detail: null, detailStatus: "idle", notes: { status: "idle", key: "", items: [], claims: 0, truncated: false } });
    renderClientActionPending();
    return;
  }
  loadClientActionDetail(clientActionId(row), clientActionOrg(row));
}
async function loadClientActionDetail(id, orgId = "", paging = {}) {
  id = String(id || "");
  if (!id) return;
  const row = clientActionWorkspaceState.rows.find((entry) => clientActionId(entry) === id);
  const org = String(orgId || clientActionOrg(row || {}) || clientActionOrg(clientActionWorkspaceState.detail || {}) || "");
  const same = clientActionWorkspaceState.selectedId === id && clientActionWorkspaceState.selectedOrgId === org;
  Object.assign(clientActionWorkspaceState, { selectedId: id, selectedOrgId: org, detail: same ? clientActionWorkspaceState.detail : null, detailStatus: "loading", detailPage: paging.page || (same ? clientActionWorkspaceState.detailPage : 1), messagePage: paging.messagePage || (same ? clientActionWorkspaceState.messagePage : 1), historyPage: paging.historyPage || (same ? clientActionWorkspaceState.historyPage : 1) });
  renderClientActionPending();
  if (!org) {
    clientActionWorkspaceState.detailStatus = "error";
    renderClientActionPending();
    return;
  }
  const readScope = clientActionReadScope();
  try { clientActionWorkspaceState.detailController?.abort(); } catch (_) {}
  const controller = new AbortController(); clientActionWorkspaceState.detailController = controller;
  const sequence = ++clientActionDetailReadSequence;
  const current = () => sequence === clientActionDetailReadSequence && readScope === clientActionReadScope() && clientActionWorkspaceState.selectedId === id && clientActionWorkspaceState.selectedOrgId === org;
  try {
    const query = new URLSearchParams({ page: String(clientActionWorkspaceState.detailPage), messagePage: String(clientActionWorkspaceState.messagePage), historyPage: String(clientActionWorkspaceState.historyPage) });
    const response = await fetch(`${clientActionBase()}/api/client-action/${encodeURIComponent(org)}/${encodeURIComponent(id)}?${query}`, { cache: "no-store", headers: clientActionHeaders(), signal: controller.signal });
    if (!current()) return;
    if (!response.ok) throw Object.assign(new Error("client_action_detail_failed"), { status: response.status });
    const payload = await response.json();
    if (!current()) return;
    clientActionWorkspaceState.detail = payload.case || payload;
    clientActionWorkspaceState.detailStatus = "ready";
    renderClientActionPending();
    // Deliberately not awaited: the request itself is on screen immediately and
    // the notes fill in under it. A slow note read must never hold the ask back.
    loadClientActionNotes(clientActionWorkspaceState.detail);
  } catch (error) {
    if (!current()) return;
    console.error("Client Action Pending detail failed", error?.status || error);
    clientActionWorkspaceState.detailStatus = "error";
    renderClientActionPending();
  }
}
// HOW MANY CLAIMS WE WILL READ NOTES FOR.
//
// A case is grouped by patient (or provider) and payer, so it is normally one
// claim and occasionally a handful. The cap exists because the note endpoint is
// one round trip PER CLAIM and an unbounded case would open twenty; when it
// bites, the pane says so rather than silently showing part of the story.
const CLIENT_ACTION_NOTE_CLAIM_LIMIT = 4;

// Read through the SAME endpoint the claim drawer uses, so note visibility is
// decided in exactly one place: the ledger checks claim:notes:view, re-derives
// the tenant scope from the principal, and applies its own per-source rules.
// Nothing here re-implements any of that, and nothing here can widen it.
async function loadClientActionNotes(row = {}) {
  const org = clientActionOrg(row) || clientActionWorkspaceState.selectedOrgId;
  const claimIds = [...new Set(clientActionSummary(row).active.map((item) => String(item.claimId || "")).filter(Boolean))];
  const readScope = clientActionReadScope();
  const sequence = ++clientActionNotesReadSequence;
  const key = JSON.stringify([readScope, org, claimIds]);
  const current = () => sequence === clientActionNotesReadSequence && readScope === clientActionReadScope() && clientActionWorkspaceState.notes.key === key;
  const notes = clientActionWorkspaceState.notes;
  if (!hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) || !org || !claimIds.length) {
    Object.assign(notes, { status: "ready", key, items: [], claims: 0, truncated: false });
    return;
  }
  const wanted = claimIds.slice(0, CLIENT_ACTION_NOTE_CLAIM_LIMIT);
  Object.assign(notes, { status: "loading", key, items: [], claims: wanted.length, truncated: claimIds.length > wanted.length });
  renderClientActionPending();
  const base = clientActionBase();
  const token = trackerApiToken();
  try {
    const envelopes = await Promise.all(wanted.map((claimId) => {
      const query = new URLSearchParams({ orgId: org, limit: "25" });
      return claimDrawerFetchJson(additionalPortalDataReadUrl(`${base}/api/tracker/claims/${encodeURIComponent(claimId)}/notes?${query}`), token, { isCurrent: current })
        .then((envelope) => ({ claimId, envelope }))
        .catch(() => ({ claimId, envelope: { ok: false, status: 0, body: {} } }));
    }));
    if (!current()) return;
    const items = [];
    const repeats = new Map();
    let refused = 0;
    for (const { claimId, envelope } of envelopes) {
      if (!envelope.ok) { refused += 1; continue; }
      const normalized = normalizeClaimNotesResponse(envelope.body);
      for (const item of normalized.items) {
        if (item.voided) continue;
        // An imported note is stored once per service line, so a claim with
        // three lines carried the same author, time and text three times and
        // the card read as three notes (production, 2026-10-21). Collapse exact
        // repeats within a claim and say how many lines carry the note;
        // distinct notes stay separate.
        const repeatKey = JSON.stringify([claimId, item.authorFullName || "", item.at || "", item.text || ""]);
        const repeated = repeats.get(repeatKey);
        if (repeated) { repeated.lineCount += 1; continue; }
        const entry = { ...item, claimId, lineCount: 1 };
        repeats.set(repeatKey, entry);
        items.push(entry);
      }
    }
    // Newest first: the last thing the biller did is the thing that explains
    // why this request is open.
    items.sort((left, right) => String(right.at || "").localeCompare(String(left.at || "")));
    Object.assign(clientActionWorkspaceState.notes, {
      status: refused && !items.length ? "error" : "ready",
      items,
      claims: wanted.length,
      truncated: claimIds.length > wanted.length
    });
  } catch (_error) {
    if (!current()) return;
    clientActionWorkspaceState.notes.status = "error";
  }
  renderClientActionPending();
}

function renderClientActionNotes() {
  if (!hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES)) return "";
  const notes = clientActionWorkspaceState.notes;
  if (notes.status === "loading") {
    return `<section class="client-action-notes"><h3>What TMS has done so far</h3><p class="client-action-note">Loading the notes on these claims&hellip;</p></section>`;
  }
  if (notes.status === "error") {
    // "Open the claim from AR to read them" is a recovery step only a
    // biller can take - the Client role has All Data read-only - so for a client
    // it is a dead end announcing a fault they cannot do anything about. They get
    // nothing rather than a door that does not open.
    if (isExternalClientRole(state.role)) return "";
    return `<section class="client-action-notes"><h3>What TMS has done so far</h3><p class="client-action-note">The notes on these claims could not be loaded. Open the claim from AR to read them.</p></section>`;
  }
  if (!notes.items.length) {
    // "Nobody has written anything" and "we could not fetch it" are different
    // facts and get different sentences.
    return `<section class="client-action-notes"><h3>What TMS has done so far</h3><p class="client-action-note">No notes have been written on these claims yet.</p></section>`;
  }
  const shown = notes.items.slice(0, 8);
  const more = notes.items.length - shown.length;
  return `<section class="client-action-notes">
      <h3>What TMS has done so far <span class="client-action-count">${notes.items.length} note${notes.items.length === 1 ? "" : "s"}${notes.truncated ? ` &middot; first ${notes.claims} claims` : ""}</span></h3>
      ${shown.map((item) => `<article class="client-action-note-entry">
          <header>
            <strong>${escapeHtml(item.authorFullName || "TMS")}</strong>
            <span>Claim ${escapeHtml(item.claimId)}${item.lineCount > 1 ? ` · ${item.lineCount} service lines` : ""}</span>
            <time>${escapeHtml(formatDateTime(item.at) || "")}</time>
          </header>
          <p>${escapeHtml(item.text)}</p>
        </article>`).join("")}
      ${more > 0 ? `<p class="client-action-note">${more} older note${more === 1 ? "" : "s"} ${more === 1 ? "is" : "are"} on these claims. Open the claim from AR for the full history.</p>` : ""}
    </section>`;
}

function captureClientActionDraft(host) {
  const pane = host?.querySelector('.client-action-detail');
  if (!pane || pane.dataset.capDetailCase !== clientActionWorkspaceState.selectedId) return null;
  const key = input => input.dataset.capLine !== undefined ? 'line:' + input.dataset.claimId + ':' + input.dataset.serviceId : input.closest('[data-cap-workflow]') ? 'workflow:' + (input.dataset.followupActionSearch !== undefined ? 'action-search' : (input.name || 'select-all')) : 'reply:' + input.name;
  const controls = [...pane.querySelectorAll('[data-cap-workflow] input,[data-cap-workflow] select,[data-cap-workflow] textarea,[data-cap-composer] textarea,[data-cap-line]')];
  return { caseId: clientActionWorkspaceState.selectedId, scroll: (pane.querySelector('.client-action-detail-scroll') || pane).scrollTop, controls: controls.map(input => ({ key: key(input), value: input.value, checked: input.checked, focused: document.activeElement === input, start: input.selectionStart, end: input.selectionEnd })) };
}
function restoreClientActionDraft(host, draft) {
  if (!draft || draft.caseId !== clientActionWorkspaceState.selectedId) return;
  const pane = host?.querySelector('.client-action-detail'); if (!pane) return;
  const byKey = new Map(draft.controls.map(control => [control.key, control]));
  for (const input of pane.querySelectorAll('[data-cap-workflow] input,[data-cap-workflow] select,[data-cap-workflow] textarea,[data-cap-composer] textarea,[data-cap-line]')) {
    const key = input.dataset.capLine !== undefined ? 'line:' + input.dataset.claimId + ':' + input.dataset.serviceId : input.closest('[data-cap-workflow]') ? 'workflow:' + (input.dataset.followupActionSearch !== undefined ? 'action-search' : (input.name || 'select-all')) : 'reply:' + input.name;
    const saved = byKey.get(key); if (!saved) continue;
    if (input.type === 'checkbox') input.checked = saved.checked; else input.value = saved.value;
    if (saved.focused) { input.focus({ preventScroll: true }); if (typeof input.setSelectionRange === 'function' && saved.start !== null) { try { input.setSelectionRange(saved.start, saved.end); } catch (_) {} } }
  }
  // The pane itself never scrolls; its inner .client-action-detail-scroll does.
  // Restoring the outer element left the conversation jumping back to the top
  // on every repaint (notes arriving, a background publication, a summary save).
  const scroller = pane.querySelector('.client-action-detail-scroll') || pane;
  scroller.scrollTop = draft.scroll;
}
function clientActionPageControls(page, kind, label) {
  if (!page || !Number.isFinite(Number(page.totalRows))) return "";
  const total = Number(page.totalRows), number = Number(page.number) || 1, size = Number(page.size) || 50, pages = Math.max(1, Math.ceil(total / size));
  return '<div class="client-action-pager" role="group" aria-label="' + escapeHtml(label) + ' pages"><span>' + total + ' ' + escapeHtml(label) + ' · Page ' + number + ' of ' + pages + '</span> <button type="button" class="small-button" data-cap-page-kind="' + kind + '" data-cap-page="' + (number - 1) + '"' + (number <= 1 ? ' disabled' : '') + '>Previous</button> <button type="button" class="small-button" data-cap-page-kind="' + kind + '" data-cap-page="' + (number + 1) + '"' + (number >= pages ? ' disabled' : '') + '>Next</button></div>';
}
function renderClientActionRefreshStatus() {
  const host = document.querySelector('[data-cap-refresh-status]'); if (!host) return;
  const job = clientActionWorkspaceState.refreshJob;
  const complete = job && ['published', 'completed', 'succeeded'].includes(job.status);
  host.innerHTML = clientActionWorkspaceState.refreshPending ? 'Requesting refresh…' : !job ? '' : complete ? 'New prepared data is available. <button class="small-button" type="button" data-cap-load-current>Load latest</button>' : job.status === 'failed' ? 'Refresh failed. Current requests remain available.' : 'Refresh is queued or running. You can keep working.';
}
async function requestClientActionRefresh() {
  if (clientActionWorkspaceState.refreshPending || (clientActionWorkspaceState.refreshJob && !['published','completed','succeeded','failed','cancelled'].includes(clientActionWorkspaceState.refreshJob.status))) return;
  const scope = clientActionReadScope(); clientActionWorkspaceState.refreshPending = true; renderClientActionRefreshStatus();
  try {
    const response = await fetch(clientActionBase() + '/api/client-action?refresh=1', { cache: 'no-store', headers: clientActionHeaders() });
    const body = await response.json(); if (scope !== clientActionReadScope()) return;
    if (!response.ok) throw new Error('refresh unavailable');
    if (response.status === 202 && body.refreshJob) { clientActionWorkspaceState.refreshJob = body.refreshJob; scheduleClientActionRefreshPoll(scope); }
    else await loadClientActionCases(true);
  } catch (_) { if (scope === clientActionReadScope()) clientActionWorkspaceState.refreshJob = { status: 'failed' }; }
  finally { if (scope === clientActionReadScope()) { clientActionWorkspaceState.refreshPending = false; renderClientActionRefreshStatus(); } }
}
function scheduleClientActionRefreshPoll(scope) {
  clearTimeout(clientActionWorkspaceState.refreshTimer);
  clientActionWorkspaceState.refreshTimer = setTimeout(async () => {
    const job = clientActionWorkspaceState.refreshJob; if (scope !== clientActionReadScope() || !job?.id) return;
    try {
      const response = await fetch(clientActionBase() + '/api/data-refresh-jobs/' + encodeURIComponent(job.id), { cache: 'no-store', headers: clientActionHeaders() });
      const body = await response.json(); if (scope !== clientActionReadScope()) return;
      if (response.ok) clientActionWorkspaceState.refreshJob = body;
      if ([401,403,404].includes(response.status)) { clientActionWorkspaceState.refreshJob = null; return; }
    } catch (_) { /* Durable status can be retried after a transient connection failure. */ }
    renderClientActionRefreshStatus();
    if (!['published','completed','succeeded','failed','cancelled'].includes(clientActionWorkspaceState.refreshJob?.status)) scheduleClientActionRefreshPoll(scope);
  }, 5000);
}
// Identity of the list the queue shows; a repaint of the same list keeps its
// scroll position, a different list starts at the top.
let clientActionRenderedQueueKey = "";
function clientActionQueueKey() {
  const { lane, category, orgId, page, search, status } = clientActionWorkspaceState;
  return JSON.stringify([lane, category, orgId || "", page || 1, search || "", status]);
}
function renderClientActionPending() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("clientActionPending")) return;
  if (clientActionWorkspaceState.readScope && clientActionWorkspaceState.readScope !== clientActionReadScope()) resetClientActionReadResponses();
  const host = document.getElementById("clientActionPendingBody");
  if (!host) return;
  const draft = captureClientActionDraft(host);
  // NAMES, NOT ORG IDs - AND A NAME MAP NOBODY FETCHED IS AN EMPTY MAP.
  //
  // clientActionCompany already resolves orgId -> company name through
  // userMgmtOrgLabelMap, and falls back to the raw id when the map has no entry.
  // Nothing on this screen ever requested the directory that fills it, so on a
  // session that had not opened Manage Groups the map was empty and the fallback
  // fired for every row: the company picker listed `10086987`, `11626652`,
  // `40699714` ... and every card printed one of those numbers directly under its
  // request title, while the header picker two inches away listed the same
  // companies BY NAME. A biller cannot filter their own worklist by a number they
  // would have to look up. REPORTING_TRUTH_CONTRACT is explicit: names, not IDs.
  //
  // The request is idempotent (it returns early when pending or already loaded)
  // and its completion re-renders, so this costs one call per session.
  if (typeof requestPortalOrgDirectory === "function") requestPortalOrgDirectory();
  updateClientActionBadge();
  if (!hasPermission(PERMISSIONS.CLIENT_ACTION_VIEW)) {
    host.innerHTML = '<div class="client-action-empty"><strong>You do not have access to this workspace.</strong></div>';
    return;
  }
  const counts = clientActionCounts();
  const ready = clientActionWorkspaceState.status === "ready";
  const lanes = CLIENT_ACTION_LANES.map((lane) => {
    const active = lane.id === clientActionWorkspaceState.lane;
    // A count of 0 for resolved is a guess until the resolved rows are fetched,
    // so show nothing rather than a confident wrong number.
    const known = ready && (clientActionWorkspaceState.prepared || lane.id !== "resolved" || clientActionWorkspaceState.resolvedLoaded);
    return `<button type="button" class="client-action-lane${active ? " is-active" : ""}" data-cap-lane="${lane.id}" aria-pressed="${active}"><span>${escapeHtml(clientActionLane(lane.id))}</span><strong>${known ? Number(counts[lane.id] || 0) : "&mdash;"}</strong></button>`;
  }).join("");
  const available = availableClientActionCategories();
  // A lane switch can leave the chosen category empty; fall back to All rather
  // than strand the person on a filter that has no chip and no requests.
  if (ready && !available.includes(clientActionWorkspaceState.category)) {
    clientActionWorkspaceState.category = "all"; clientActionWorkspaceState.page = 1;
    if (clientActionWorkspaceState.prepared) queueMicrotask(() => loadClientActionCases());
  }
  const categories = (available.length > 1 ? available : []).map((category) => {
    const active = category === clientActionWorkspaceState.category;
    return `<button type="button" class="client-action-chip${active ? " is-active" : ""}" data-cap-category="${category}" aria-pressed="${active}">${escapeHtml(clientActionCategoryLabel(category))}</button>`;
  }).join("");
  // Offered only when there is a choice to make. One company in the queue is
  // already scoped, and a select with a single option is furniture.
  const orgOptions = clientActionOrgOptions();
  const activeOrgId = activeClientActionOrgId();
  const orgPicker = orgOptions.length > 1
    ? `<label class="client-action-org" for="clientActionOrgSelect"><span>Company</span><select id="clientActionOrgSelect" data-cap-org-select>
        <option value=""${activeOrgId ? "" : " selected"}>All companies (${orgOptions.length})</option>
        ${orgOptions.map((option) => `<option value="${escapeHtml(option.value)}"${option.value === activeOrgId ? " selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
      </select></label>`
    : "";
  const searchBox = clientActionNamePicker();
  // THE QUEUE MUST NOT JUMP TO THE TOP ON EVERY REPAINT.
  //
  // This function replaces the whole workspace with innerHTML, and the queue is
  // its own scroll box. Every repaint therefore reset the list to the top: pick
  // the bottom card and the detail load repainted twice (loading, then ready);
  // scroll down and a background tracker publication repainted underneath you.
  // Carry the queue's and the page's scroll positions across a repaint of the
  // same list, and start at the top only when the list itself changed (lane,
  // category, company, page or name filter).
  const queueKey = clientActionQueueKey();
  const previousQueue = host.querySelector(".client-action-queue");
  const queueScroll = previousQueue && clientActionRenderedQueueKey === queueKey ? previousQueue.scrollTop : 0;
  const pageScroll = typeof window !== "undefined" ? { x: window.scrollX || 0, y: window.scrollY || 0 } : null;
  host.innerHTML = `<div class="client-action-page">
      <div class="client-action-lanes" role="group" aria-label="Request lanes">${lanes}</div>
      <div class="client-action-toolbar">
        <div class="client-action-categories" role="group" aria-label="Request categories">${categories}</div>
        <div class="client-action-toolbar-end">
          ${searchBox}
          ${orgPicker}
          <button class="ghost-button" type="button" data-cap-refresh${clientActionWorkspaceState.status === "loading" ? " disabled" : ""}>${clientActionWorkspaceState.status === "loading" ? "Refreshing&hellip;" : "Refresh"}</button>
        </div>
      </div>
      <p class="client-action-note" role="status" data-cap-refresh-status></p>
      <div class="client-action-workspace${clientActionWorkspaceState.selectedId ? " has-selection" : ""}">
        <section class="client-action-queue" aria-label="Request queue">${renderClientActionQueue()}</section>
        <section class="client-action-detail" data-cap-detail-case="${escapeHtml(clientActionWorkspaceState.selectedId)}" aria-label="Request detail">${renderClientActionDetail()}</section>
      </div>
    </div>`;
  restoreClientActionDraft(host, draft);
  clientActionRenderedQueueKey = queueKey;
  const nextQueue = host.querySelector(".client-action-queue");
  if (nextQueue && queueScroll) nextQueue.scrollTop = queueScroll;
  if (pageScroll && (window.scrollY !== pageScroll.y || window.scrollX !== pageScroll.x)) window.scrollTo(pageScroll.x, pageScroll.y);
  renderClientActionRefreshStatus();
  if (clientActionWorkspaceState.status === "idle") queueMicrotask(() => loadClientActionCases());
}
function renderClientActionQueue() {
  if (clientActionWorkspaceState.status === "loading") {
    return `<div class="client-action-skeletons" aria-busy="true" aria-label="Loading requests">${'<div class="client-action-skeleton"></div>'.repeat(4)}</div>`;
  }
  if (clientActionWorkspaceState.status === "error") {
    return `<div class="client-action-empty"><strong>Requests could not load.</strong><span>${escapeHtml(clientActionWorkspaceState.error)}</span><button class="small-button" type="button" data-cap-refresh>Try again</button></div>`;
  }
  const rows = visibleClientActionRows();
  if (!rows.length && clientActionWorkspaceState.search) return `<div class="client-action-empty"><strong>No requests for "${escapeHtml(clientActionWorkspaceState.search)}".</strong><span>Nothing in this lane and category names this ${escapeHtml(clientActionSearchLabel().toLowerCase())}.</span><button class="small-button" type="button" data-cap-search-clear>Show all</button></div>`;
  if (!rows.length && clientActionWorkspaceState.prepared) return clientActionPageControls(clientActionWorkspaceState.pageInfo, "queue", "requests") + '<div class="client-action-empty"><strong>No requests on this page.</strong><span>Choose another lane, category, company, or page.</span></div>';
  if (!rows.length) {
    // "Nothing here" and "nothing here FOR THIS COMPANY" are different facts and
    // must not read the same, or a narrowed queue looks like an empty one.
    const scoped = scopedClientActionRows().length;
    const total = clientActionWorkspaceState.rows.length;
    if (!scoped && total) {
      const label = clientActionOrgOptions().find((option) => option.value === activeClientActionOrgId());
      return `<div class="client-action-empty"><strong>Nothing is waiting for ${escapeHtml(label ? label.label : "this company")}.</strong><span>${total} request${total === 1 ? "" : "s"} ${total === 1 ? "is" : "are"} open for other companies in your scope — choose All companies to see them.</span></div>`;
    }
    // A CATEGORY CHIP EMPTIES THE LANE DIFFERENTLY FROM A LANE SWITCH. With EVV
    // selected inside Open, this read "1 request sits in the other lanes — try Open"
    // while the reader was already on Open: the request was in this lane, under a
    // different category. Name the chip that hid it and point at All.
    const { lane, category } = clientActionWorkspaceState;
    const inLane = category === "all" ? 0 : scopedClientActionRows().filter((row) => {
      const rowLane = clientActionLaneFor(row);
      return lane === "open" ? rowLane !== "resolved" : rowLane === lane;
    }).length;
    if (inLane) {
      return `<div class="client-action-empty"><strong>No ${escapeHtml(clientActionCategoryLabel(category))} requests in this lane.</strong><span>${inLane} request${inLane === 1 ? " is" : "s are"} here under other categories — choose All.</span></div>`;
    }
    return `<div class="client-action-empty"><strong>${scoped ? "Nothing is waiting here." : "No open requests."}</strong><span>${scoped
      ? `${scoped} request${scoped === 1 ? " sits" : "s sit"} in ${lane === "open" ? "Resolved." : "the other lanes — try Open."}`
      : "Changing the tracker action away from Client Action Pending removes the item from this open queue."}</span></div>`;
  }
  const labels = clientActionOrgLabels();
  return clientActionPageControls(clientActionWorkspaceState.pageInfo, "queue", "requests") + rows.map((row) => {
    const id = clientActionId(row);
    const summary = clientActionSummary(row);
    const due = clientActionDue(row);
    const lines = summary.activeLineCount;
    // Labeled (2026-10-13): a bare clinician name after the payer read as a
    // second, unexplained person on the card.
    const context = [summary.payers[0] ? `Payer ${summary.payers[0]}` : "", summary.providers[0] ? `Provider ${summary.providers[0]}` : ""].filter(Boolean).map(escapeHtml).join(" &middot; ");
    const dueTitle = clientActionDueExplanation(row);
    return `<button class="client-action-card${id === clientActionWorkspaceState.selectedId ? " is-active" : ""}" type="button" data-cap-case="${escapeHtml(id)}" data-cap-org="${escapeHtml(clientActionOrg(row))}">
        <span class="client-action-card-top">
          <span class="client-action-tag">${escapeHtml(clientActionCategoryLabel(clientActionBoardCategory(row)))}</span>
          ${!due.label || due.tone === "unsent" ? "" : `<span class="client-action-due client-action-due--${due.tone}"${dueTitle ? ` title="${escapeHtml(dueTitle)}"` : ""}>${escapeHtml(due.label)}</span>`}
        </span>
        <strong>${escapeHtml(clientActionTitle(row))}</strong>
        <span class="client-action-card-company">${escapeHtml(clientActionCompany(row, labels))}</span>
        ${context ? `<span class="client-action-card-context">${context}</span>` : ""}
        <span class="client-action-card-foot">
          <span>${lines} line${lines === 1 ? "" : "s"}${lines && !isRedactedClientRole() ? ` &middot; ${escapeHtml(clientActionExpectedText(summary, { compact: true }))}` : ""}</span>
          <span class="client-action-state client-action-state--${escapeHtml(clientActionLaneFor(row))}">${escapeHtml(clientActionResponsibilityLabel(row))}</span>
        </span>
      </button>`;
  }).join("");
}
function renderClientActionDetail() {
  if (!clientActionWorkspaceState.selectedId) {
    return '<div class="client-action-detail-empty"><strong>Select a request</strong><span>Its conversation, linked claims and current owner appear here.</span></div>';
  }
  if (clientActionWorkspaceState.detailStatus === "error") {
    return '<div class="client-action-detail-empty"><strong>This request could not load.</strong><span>Select it again to retry.</span></div>';
  }
  const row = clientActionWorkspaceState.detail;
  if (!row) return '<div class="client-action-detail-empty"><strong>Loading request&hellip;</strong></div>';
  const labels = clientActionOrgLabels();
  const summary = clientActionSummary(row);
  const due = clientActionDue(row);
  const messages = Array.isArray(row.messages) ? row.messages : [];
  const manage = hasPermission(PERMISSIONS.CLIENT_ACTION_MANAGE);
  const respond = hasPermission(PERMISSIONS.CLIENT_ACTION_RESPOND);
  const lineHtml = summary.items.length
    ? summary.items.map((item) => {
        const active = clientActionItemActive(item);
        const itemExpected = item.expected !== null && item.expected !== undefined && String(item.expected).trim() !== "" && Number.isFinite(Number(item.expected))
          ? `${money(Number(item.expected))} expected reimbursement`
          : "Expected reimbursement not yet known";
        const facts = [item.payer || item.payerLabel, item.provider || item.providerLabel, isRedactedClientRole() ? "" : itemExpected]
          .filter(Boolean).map(escapeHtml).join(" &middot; ");
        const claimId = String(item.claimId || item.claim_id || "");
        const serviceId = String(item.serviceLineId || item.service_line_id || "");
        const expectedUpdatedAt = String(item.followUpUpdatedAt || item.follow_up_updated_at_utc || "");
        return `<div class="client-action-line${active ? "" : " is-detached"}">
            ${manage && active ? `<label class="client-action-line-select"><input type="checkbox" data-cap-line data-claim-id="${escapeHtml(claimId)}" data-service-id="${escapeHtml(serviceId)}" data-expected-updated-at="${escapeHtml(expectedUpdatedAt)}" checked><span class="sr-only">Select this service line</span></label>` : ""}
            <div>
              <strong>Claim ${escapeHtml(item.claimDisplayId || claimId || "—")}</strong>
              <span>Service line ${escapeHtml(item.serviceDisplayId || serviceId || "—")}${facts ? ` &middot; ${facts}` : ""}</span>
            </div>
            ${active ? "" : '<span class="client-action-line-flag">Marker removed</span>'}
          </div>`;
      }).join("")
    : '<p class="client-action-note">Linked service details will appear here.</p>';
  return `<button class="client-action-back" type="button" data-cap-back>&larr; All requests</button>
    <div class="client-action-detail-scroll">
      <header class="client-action-detail-head">
        <div>
          <span class="client-action-tag">${escapeHtml(clientActionCategoryLabel(clientActionBoardCategory(row)))}</span>
          <h2>${escapeHtml(clientActionTitle(row))}</h2>
          <p>${escapeHtml(clientActionCompany(row, labels))}</p>
        </div>
        <div class="client-action-responsibility">
          <span>Responsible now</span>
          <strong>${escapeHtml(clientActionResponsibilityLabel(row))}</strong>
          ${isExternalClientRole(state.role) ? "" : `<small>TMS owner: ${escapeHtml(clientActionOwner(row))}</small>`}
          ${row.requestedAtUtc ? `<small>Published to the client portal ${escapeHtml(formatDateTime(row.requestedAtUtc) || row.requestedAtUtc)}</small>` : ""}
          ${row.askedAtUtc ? `<small>First viewed ${escapeHtml(formatDateTime(row.askedAtUtc) || row.askedAtUtc)}</small>` : ""}
          ${!due.label || due.tone === "unsent" ? "" : `<small class="client-action-due client-action-due--${due.tone}">${escapeHtml(due.label)}</small>`}
        </div>
      </header>
      <section class="client-action-need">
        <span>What we need</span>
        <p>${escapeHtml(clientActionSummaryText(row, summary))}</p>
        <small>${escapeHtml(clientActionSummaryUpdatedText(row, summary))}</small>
        ${manage ? `<button class="small-button" type="button" data-cap-summary-refresh${clientActionWorkspaceState.summaryStatus === "saving" ? " disabled" : ""}>${clientActionWorkspaceState.summaryStatus === "saving" ? "Refreshing…" : "Refresh summary"}</button>` : ""}
        ${clientActionWorkspaceState.summaryError ? `<small class="form-warning" role="alert">${escapeHtml(clientActionWorkspaceState.summaryError)}</small>` : ""}
      </section>
      ${renderClientActionNotes()}
      ${manage ? `<section class="client-action-controls">
        <span class="client-action-controls-label">Move responsibility</span>
        <button class="small-button" type="button" data-cap-responsibility="waiting_on_client">Request client response</button>
        <button class="small-button" type="button" data-cap-responsibility="tms_working">TMS is working</button>
      </section>` : ""}
      <section class="client-action-linked">
        <h3>Linked claims and services <span class="client-action-count">${summary.activeLineCount} active${summary.detached ? ` &middot; ${summary.detached} removed` : ""}${summary.activeLineCount && !isRedactedClientRole() ? ` &middot; ${escapeHtml(clientActionExpectedText(summary, { compact: true }))}` : ""}</span></h3>
        ${clientActionPageControls(row.itemPage, "items", "current lines")}
        ${clientActionPageControls(row.historyPage, "history", "removed lines")}
        ${lineHtml}
      </section>
      ${manage && summary.active.length ? `<form class="client-action-workflow" data-cap-workflow>
        <div class="client-action-workflow-head">
          <div><strong>Update selected service lines</strong><span>Choose the lines above. You can select one line or apply the same change to many.</span></div>
          <label><input type="checkbox" data-cap-select-all checked> Select current lines on this page</label>
        </div>
        <div class="client-action-workflow-fields">
          <label><span>Follow-up action</span><select name="action" required><option value="">Choose an action</option>${clientActionActionOptions()}</select></label>
          <label><span>Follow-up due</span><input name="followUpDate" type="date"></label>
        </div>
        <label class="client-action-workflow-note"><span>Note for the selected service lines</span><textarea name="note" rows="3" maxlength="4000" placeholder="Example: New coverage starts 8/1. Resubmit dates on or after 8/1." required></textarea></label>
        ${clientActionWorkspaceState.workflowError ? `<p class="form-warning" role="alert">${escapeHtml(clientActionWorkspaceState.workflowError)}</p>` : ""}
        <button class="primary-button" type="submit"${clientActionWorkspaceState.workflowStatus === "saving" ? " disabled" : ""}>${clientActionWorkspaceState.workflowStatus === "saving" ? "Saving…" : "Apply to selected"}</button>
      </form>` : ""}
      <section class="client-action-conversation">
        <h3>Shared conversation</h3>
        ${clientActionPageControls(row.messagePage, "messages", "replies")}
        ${messages.length ? messages.map((message) => `<article class="client-action-message">
            <header>
              <strong>${escapeHtml(message.authorDisplayName || message.authorName || "Portal user")}</strong>
              <time>${escapeHtml(message.createdAtLabel || formatDateTime(message.createdAtUtc || message.createdAt) || "")}</time>
            </header>
            <p>${escapeHtml(message.body || message.text || "")}</p>
          </article>`).join("") : '<p class="client-action-note">No replies yet.</p>'}
      </section>
      <section class="client-action-auto-clear">
        ${isExternalClientRole(state.role)
          ? `<strong>Nothing here for you to close</strong>
        <p>Reply above with what we have asked for and we will take it from there. This request clears itself once we have applied your answer &mdash; there is no button for you to press.</p>`
          : `<strong>This list updates itself</strong>
        <p>A service line leaves this request when you set its next step here&mdash;for example, Appeal, Resubmit, or Write-Off. The request leaves the open queue when no service lines are still waiting for client action.</p>`}
      </section>
    </div>
    ${respond ? `<form class="client-action-composer" data-cap-composer>
      ${clientActionBoardCategory(row) === "secondary-authorization" && hasPermission(PERMISSIONS.AUTHORIZATIONS_MANAGE) ? `<p class="client-action-template"><button class="small-button" type="button" data-cap-secondary-record>Add secondary authorization</button><small>Opens this patient on Authorizations, where the secondary insurance waits on hold, to record the number, dates and CPT codes.</small></p>` : ""}
      ${clientActionBoardCategory(row) === "secondary-authorization" ? `<p class="client-action-template"><button class="small-button" type="button" data-cap-secondary-template>Send secondary authorization</button><small>Fills in the authorization number, approved units and dates covered for you to complete.</small></p>` : ""}
      <label for="clientActionReply">Reply to the shared conversation</label>
      <textarea id="clientActionReply" name="body" rows="3" maxlength="4000" placeholder="Everyone on this request sees this reply." required></textarea>
      <div><button class="primary-button" type="submit">Send reply</button></div>
    </form>` : ""}`;
}
async function clientActionCommand(path, body) {
  const id = clientActionWorkspaceState.selectedId;
  const org = clientActionWorkspaceState.selectedOrgId;
  if (!id || !org) throw new Error("client_action_no_selection");
  const endpoint = path === "messages" ? "messages" : path === "workflow" ? "workflow" : path === "summary" ? "summary" : "transition";
  const commandId = claimNoteCommandId("cap");
  const response = await fetch(`${clientActionBase()}/api/client-action/${encodeURIComponent(org)}/${encodeURIComponent(id)}/${endpoint}`, {
    method: "POST",
    headers: clientActionHeaders(true),
    body: JSON.stringify({
      ...body,
      reason: body.reason || "Responsibility updated",
      expectedVersion: clientActionWorkspaceState.detail?.version,
      commandId,
      ...(path === "workflow" ? { batchId: claimNoteCommandId("capbatch") } : {})
    })
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw Object.assign(new Error(String(payload?.message || "client_action_command_failed")), { status: response.status, payload });
  // Clear only the submitted draft on the same request; text typed while the
  // command was in flight belongs to the user and must survive the readback.
  if (clientActionWorkspaceState.selectedId === id && clientActionWorkspaceState.selectedOrgId === org) {
    const selector = path === "messages" ? "[data-cap-composer] textarea" : path === "workflow" ? '[data-cap-workflow] [name="note"]' : "";
    const input = selector ? document.querySelector(selector) : null;
    const sent = path === "messages" ? body.body : body.note;
    if (input && String(input.value || "").trim() === String(sent || "").trim()) input.value = "";
  }
  if (clientActionWorkspaceState.selectedId !== id || clientActionWorkspaceState.selectedOrgId !== org) { await loadClientActionCases(true); return payload; }
  if ((path === "workflow" || path === "summary") && payload.case) {
    clientActionWorkspaceState.detail = payload.case;
    clientActionWorkspaceState.detailStatus = "ready";
  } else {
    await loadClientActionDetail(id, org);
  }
  await loadClientActionCases(true);
  if (path === "workflow" && payload.leftOpenQueue) {
    Object.assign(clientActionWorkspaceState, { selectedId: "", selectedOrgId: "", detail: null, detailStatus: "idle" });
    renderClientActionPending();
  }
  return payload;
}
document.addEventListener("click", (event) => {
  const lane = event.target.closest("[data-cap-lane]")?.dataset.capLane;
  if (lane) {
    clientActionWorkspaceState.lane = lane; clientActionWorkspaceState.page = 1;
    if (clientActionWorkspaceState.prepared || (lane === "resolved" && !clientActionWorkspaceState.resolvedLoaded)) loadClientActionCases();
    else renderClientActionPending();
    return;
  }
  if (event.target.closest("[data-cap-secondary-record]")) {
    // The same lines wait on the Authorizations On Hold board under the secondary
    // payer (secondaryAuthorizationHold.js): open that patient there.
    const row = clientActionWorkspaceState.detail || {};
    const patient = clientActionItems(row).map((item) => String(item.patient || item.patientLabel || item.patientName || "").trim()).find(Boolean)
      || String(clientActionTitle(row).split(" — ")[1] || "").trim();
    authorizationsState.orgId = clientActionOrg(row);
    // Settle the company change first: a company switch clears the directory's
    // search, which would otherwise wipe the patient set below.
    syncWorkflowDirectoryScope(authorizationsState, workflowDirectoryScope(authorizationsState.orgId));
    authorizationsState.search = patient;
    authorizationsState.patientFilter = "";
    authorizationsState.page = 1;
    navigateToView("authorizations");
    return;
  }
  if (event.target.closest("[data-cap-secondary-template]")) {
    // Fill, never overwrite: someone may already have started typing.
    const reply = document.getElementById("clientActionReply");
    if (reply) {
      if (!reply.value.trim()) reply.value = CLIENT_ACTION_SECONDARY_AUTHORIZATION_TEMPLATE;
      else if (!reply.value.includes("Authorization number:")) reply.value = `${reply.value.replace(/\s+$/, "")}\n\n${CLIENT_ACTION_SECONDARY_AUTHORIZATION_TEMPLATE}`;
      // Grow the box so every template line is visible without scrolling.
      reply.rows = Math.max(Number(reply.rows) || 3, reply.value.split("\n").length + 1);
      reply.focus();
      const field = reply.value.indexOf("Authorization number: ") + "Authorization number: ".length;
      reply.setSelectionRange(field, field);
    }
    return;
  }
  const category = event.target.closest("[data-cap-category]")?.dataset.capCategory;
  if (category) {
    // A provider name means nothing on a client search and vice versa, so a
    // switch that changes what the box searches starts it empty.
    if (clientActionSearchLabel(category) !== clientActionSearchLabel()) { clearTimeout(clientActionSearchTimer); Object.assign(clientActionWorkspaceState, { search: "", searchDraft: "" }); }
    clientActionWorkspaceState.category = category; clientActionWorkspaceState.page = 1;
    if (clientActionWorkspaceState.prepared) loadClientActionCases(); else renderClientActionPending();
    return;
  }
  const card = event.target.closest("[data-cap-case]");
  if (card) { loadClientActionDetail(card.dataset.capCase, card.dataset.capOrg || ""); return; }
  if (event.target.closest("[data-cap-refresh]")) { requestClientActionRefresh(); return; }
  if (event.target.closest("[data-cap-search-clear]")) { applyClientActionSearch(""); return; }
  if (event.target.closest("[data-cap-load-current]")) { loadClientActionCases(true); return; }
  const pageButton = event.target.closest("[data-cap-page]");
  if (pageButton) {
    const page = Number(pageButton.dataset.capPage), kind = pageButton.dataset.capPageKind;
    if (kind === "queue") { clientActionWorkspaceState.page = page; loadClientActionCases(); }
    else loadClientActionDetail(clientActionWorkspaceState.selectedId, clientActionWorkspaceState.selectedOrgId, { [kind === "messages" ? "messagePage" : kind === "history" ? "historyPage" : "page"]: page });
    return;
  }
  if (event.target.closest("[data-cap-summary-refresh]")) {
    Object.assign(clientActionWorkspaceState, { summaryStatus: "saving", summaryError: "" });
    renderClientActionPending();
    clientActionCommand("summary", {})
      .then(() => showToast("Summary refreshed from the current service lines and replies."))
      .catch((error) => { clientActionWorkspaceState.summaryError = error.message || "The summary could not be refreshed."; })
      .finally(() => { clientActionWorkspaceState.summaryStatus = "idle"; renderClientActionPending(); });
    return;
  }
  if (event.target.closest("[data-cap-back]")) {
    Object.assign(clientActionWorkspaceState, { selectedId: "", selectedOrgId: "", detail: null, detailStatus: "idle", notes: { status: "idle", key: "", items: [], claims: 0, truncated: false } });
    renderClientActionPending();
    return;
  }
  const responsibility = event.target.closest("[data-cap-responsibility]")?.dataset.capResponsibility;
  if (responsibility) clientActionCommand("transition", { responsibility }).catch(() => showToast("That responsibility change could not be saved."));
  const selectAll = event.target.closest("[data-cap-select-all]");
  if (selectAll) document.querySelectorAll(".client-action-detail [data-cap-line]").forEach((input) => { input.checked = selectAll.checked; });
});
// A name pick applies at once. A new filter starts at page 1 and reselects the
// first match.
let clientActionSearchTimer = null;
function applyClientActionSearch(value) {
  clearTimeout(clientActionSearchTimer);
  const term = String(value || "").replace(/\s+/g, " ").trim();
  clientActionWorkspaceState.searchDraft = String(value || "");
  if (term === clientActionWorkspaceState.search) return;
  Object.assign(clientActionWorkspaceState, { search: term, page: 1, selectedId: "", selectedOrgId: "", detail: null, detailStatus: "idle", notes: { status: "idle", key: "", items: [], claims: 0, truncated: false } });
  if (clientActionWorkspaceState.prepared) loadClientActionCases();
  else {
    const rows = visibleClientActionRows();
    clientActionSelect(clientActionNarrowLayout() ? null : rows[0] || null);
  }
}
document.addEventListener("change", (event) => {
  const select = event.target.closest?.("[data-cap-name-select]");
  if (select) applyClientActionSearch(select.value);
});
// Company changes request one bounded server page and retain the current case
// only when it remains in the selected company scope.
document.addEventListener("change", (event) => {
  const select = event.target.closest?.("[data-cap-org-select]");
  if (!select) return;
  clientActionWorkspaceState.orgId = String(select.value || ""); clientActionWorkspaceState.page = 1;
  const stillVisible = clientActionWorkspaceState.prepared ? (!clientActionWorkspaceState.orgId || clientActionWorkspaceState.orgId === clientActionWorkspaceState.selectedOrgId) : visibleClientActionRows().some((row) => clientActionId(row) === clientActionWorkspaceState.selectedId);
  if (!stillVisible) Object.assign(clientActionWorkspaceState, { selectedId: "", selectedOrgId: "", detail: null, detailStatus: "idle", notes: { status: "idle", key: "", items: [], claims: 0, truncated: false } });
  if (clientActionWorkspaceState.prepared) loadClientActionCases(); else renderClientActionPending();
  updateClientActionBadge();
});
document.addEventListener("submit", (event) => {
  if (event.target.matches("[data-cap-workflow]")) {
    event.preventDefault();
    const form = event.target;
    const targets = [...document.querySelectorAll(".client-action-detail [data-cap-line]:checked")].map((input) => ({
      claimId: input.dataset.claimId || "",
      serviceId: input.dataset.serviceId || "",
      expectedUpdatedAt: input.dataset.expectedUpdatedAt || ""
    }));
    const data = new FormData(form);
    if (!targets.length) {
      clientActionWorkspaceState.workflowError = "Select at least one service line.";
      let warning = form.querySelector(".form-warning");
      if (!warning) { warning = document.createElement("p"); warning.className = "form-warning"; warning.setAttribute("role", "alert"); form.appendChild(warning); }
      warning.textContent = clientActionWorkspaceState.workflowError;
      return;
    }
    if (targets.some((target) => !target.expectedUpdatedAt)) { clientActionWorkspaceState.workflowError = "These service lines need a fresh summary before they can be changed."; renderClientActionPending(); return; }
    Object.assign(clientActionWorkspaceState, { workflowStatus: "saving", workflowError: "" });
    renderClientActionPending();
    clientActionCommand("workflow", { targets, action: String(data.get("action") || ""), followUpDate: String(data.get("followUpDate") || ""), note: String(data.get("note") || "").trim() })
      .then((payload) => showToast(payload.leftOpenQueue ? "The request is complete and left the open queue." : "The selected service lines were updated."))
      .catch((error) => { clientActionWorkspaceState.workflowError = error.status === 409 ? "One of these lines changed. Refresh the summary and try again." : (error.message || "The selected lines could not be updated."); })
      .finally(() => { clientActionWorkspaceState.workflowStatus = "idle"; renderClientActionPending(); });
    return;
  }
  if (!event.target.matches("[data-cap-composer]")) return;
  event.preventDefault();
  const input = event.target.querySelector("textarea");
  const body = String(input?.value || "").trim();
  if (!body) return;
  const button = event.target.querySelector("button");
  button.disabled = true;
  clientActionCommand("messages",{body})
    .then(() => { input.value = ""; })
    .catch(() => showToast("Your reply could not be sent. Try again."))
    .finally(() => { button.disabled = false; });
});

function renderCredentialing() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("credentialing")) return;
  if (credentialingState.readScope && credentialingState.readScope !== additionalPortalDataReadScope()) resetCredentialingReadResponses();
  const host = document.getElementById("credentialingBody");
  if (!host) return;
  const restoreSearchFocus = operationalBoardSearchFocus(host, "[data-cred-search]");
  if (!hasPermission(PERMISSIONS.CREDENTIALING_VIEW)) {
    host.innerHTML = `
      <section class="panel authz-deny-panel">
        <h3>Credentialing</h3>
        <div class="authz-empty">
          <p class="authz-empty-title">You do not have access to Credentialing.</p>
          <p class="authz-empty-note">Ask your TMS contact to grant the Credentialing permission for your company.</p>
        </div>
      </section>`;
    return;
  }
  // Match Authorizations: establish the narrowing org before the first board
  // request. The server still re-attests the bearer principal and org scope.
  credentialingState.orgId = activeCredentialingOrgId();
  syncWorkflowDirectoryScope(credentialingState, workflowDirectoryScope(credentialingState.orgId));
  // A failed board is asked again when someone comes back to it, not shown
  // as the old error until they find "Try again".
  if (credentialingState.status === "idle" || (credentialingState.status === "error" && Date.now() - Number(credentialingState.errorAt || 0) > 15000)) requestCredentialingBoard({ rerender: false });
  if (canSendCredentialedClaims() && credentialingState.queue.status === "idle") {
    requestCredentialingQueue({ rerender: false });
  }
  host.innerHTML = credentialingMarkup();
  bindCredentialingControls(host);
  restoreSearchFocus();
  updateCredentialingNavBadge();
}

function credentialingMarkup() {
  if (credentialingState.status === "loading" && !credentialingState.board) {
    return `<section class="panel"><div class="authz-empty"><p class="authz-empty-title">Loading credentialing…</p></div></section>`;
  }
  if (credentialingState.status === "error") {
    return `
      <section class="panel">
        <div class="authz-empty">
          <p class="authz-empty-title">Credentialing could not be loaded.</p>
          <p class="authz-empty-note">${escapeHtml(credentialingState.error)}</p>
          <button class="btn" data-cred-retry>Try again</button>
        </div>
      </section>`;
  }
  return `
    ${credentialingHeaderMarkup()}
    ${authorizationCentralReachMarkup()}
    ${credentialingAccessBannerMarkup()}
    ${credentialingConditionsMarkup()}
    ${credentialingSaveStripMarkup()}
    <div class="cred-board">${credentialingBoardMarkup()}</div>
    ${credentialingQueueMarkup()}
    ${credentialingDrawerMarkup()}
    ${credentialingRoutingMarkup()}
  `;
}

function credentialingAccessBannerMarkup() {
  // Reviewer routing, notification mode and access-mode labels are TMS
  // configuration; a client reading its board does not need them (2026-10-13).
  if (isExternalClientRole(state?.role)) return "";
  const access = credentialingState.reviewAccess || {};
  const routing = credentialingState.routingAccess || {};
  const canUpdate = access.canUpdate === true;
  const modeLabel = access.mode === "designated_reviewer"
    ? "Designated reviewer"
    : access.mode === "role_grant" ? "Role access" : "View only";
  const routeLabel = routing.primaryMode === "portal_member"
    ? "Primary route: linked portal member"
    : routing.primaryMode === "email_only"
      ? "Primary route: email only"
      : "Primary route: not configured";
  const selectedOrgId = credentialingSelectedOrgId();
  return `
    <section class="panel cred-access-banner ${canUpdate ? "cred-access-banner--write" : "cred-access-banner--view"}" aria-label="Credentialing review access">
      <div class="cred-access-copy">
        <div class="cred-access-title-row">
          <span class="cred-pill ${canUpdate ? "cred-pill--blue" : "cred-pill--gray"}">${escapeHtml(modeLabel)}</span>
          <strong>${canUpdate ? "You can update credentialing statuses." : "Credentialing is view-only for you."}</strong>
        </div>
        <p>${escapeHtml(access.reason || "Credentialing update access could not be confirmed.")}</p>
        <p class="cred-access-route">${escapeHtml(routeLabel)}${routing.emailEnabled ? " · Email notifications on" : " · Email notifications off"}</p>
        ${routing.primaryMode === "email_only" ? `<p class="cred-access-warning">Email recipients receive notifications, but email alone never grants permission to update credentialing.</p>` : ""}
      </div>
      ${canManageCredentialingRouting() && selectedOrgId
        ? `<button class="btn" type="button" data-cred-routing-open>Manage reviewer routing</button>`
        : ""}
    </section>`;
}

function credentialingRoutingForm(configuration = {}) {
  return {
    primaryOwnerUserId: String(configuration.primaryOwnerUserId || ""),
    primaryOwnerEmail: String(configuration.primaryOwnerEmail || ""),
    escalationOwnerUserId: String(configuration.escalationOwnerUserId || ""),
    escalationOwnerEmail: String(configuration.escalationOwnerEmail || ""),
    isEmailEnabled: configuration.isEmailEnabled !== false && configuration.emailEnabled !== false
  };
}

function credentialingRoutingErrorMessage(status, payload = {}) {
  if (status === 401) return "Your session expired. Sign in again before changing reviewer routing.";
  if (status === 403) return "Your role cannot configure credentialing reviewer routing.";
  if (status === 409) return "Someone else changed this routing first. Close and reopen the panel to load the current version.";
  return String(payload.message || "Credentialing reviewer routing could not be saved.");
}

function openCredentialingRouting() {
  const orgId = credentialingSelectedOrgId();
  if (!canManageCredentialingRouting() || !orgId) return;
  credentialingDrawerState.open = false;
  Object.assign(credentialingRoutingState, {
    open: true,
    status: "loading",
    error: "",
    saving: false,
    savedMessage: "",
    orgId,
    configuration: null,
    eligibleUsers: [],
    linkResults: null,
    commandId: ""
  });
  renderCredentialing();
  requestCredentialingRouting(orgId);
}

function closeCredentialingRouting() {
  credentialingRoutingState.open = false;
  credentialingRoutingState.error = "";
  credentialingRoutingState.saving = false;
  credentialingRoutingState.savedMessage = "";
  credentialingRoutingState.commandId = "";
  renderCredentialing();
}

function requestCredentialingRouting(orgId = credentialingRoutingState.orgId) {
  const requestedOrgId = String(orgId || "").trim();
  const base = credentialingApiBase();
  if (!requestedOrgId || !base || typeof fetch !== "function") {
    credentialingRoutingState.status = "error";
    credentialingRoutingState.error = "The reviewer directory is temporarily unavailable.";
    renderCredentialing();
    return;
  }
  fetch(`${base}/api/credentialing/routing?orgId=${encodeURIComponent(requestedOrgId)}`, {
    method: "GET",
    cache: "no-store",
    headers: credentialingApiHeaders()
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!credentialingRoutingState.open || requestedOrgId !== String(credentialingRoutingState.orgId || "").trim()) return;
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok || payload.error) {
      credentialingRoutingState.status = "error";
      credentialingRoutingState.error = credentialingRoutingErrorMessage(response.status, payload);
    } else {
      const configuration = payload.routing || {};
      credentialingRoutingState.status = "ready";
      credentialingRoutingState.error = "";
      credentialingRoutingState.configuration = configuration;
      credentialingRoutingState.eligibleUsers = Array.isArray(payload.eligibleUsers) ? payload.eligibleUsers : [];
      credentialingRoutingState.form = credentialingRoutingForm(configuration);
      credentialingRoutingState.commandId = "";
    }
    renderCredentialing();
  }).catch(() => {
    if (!credentialingRoutingState.open || requestedOrgId !== String(credentialingRoutingState.orgId || "").trim()) return;
    credentialingRoutingState.status = "error";
    credentialingRoutingState.error = "Could not reach the portal. No reviewer routing was changed.";
    renderCredentialing();
  });
}

function credentialingRoutingLinkText(result = {}, label = "Primary") {
  if (result.mode === "portal_member") return `${label}: linked to an active portal member of this company.`;
  if (result.mode === "email_only") return `${label}: email only — notifications are allowed, but this address has no portal update authority.`;
  if (result.mode === "inherits_primary") return `${label}: inherits the primary route.`;
  return `${label}: not configured.`;
}

function saveCredentialingRouting() {
  if (credentialingRoutingState.saving || credentialingRoutingState.status !== "ready") return;
  const orgId = credentialingSelectedOrgId();
  if (!orgId || orgId !== String(credentialingRoutingState.orgId || "").trim() || !canManageCredentialingRouting()) {
    credentialingRoutingState.error = "Select the company again before changing reviewer routing.";
    renderCredentialing();
    return;
  }
  const form = credentialingRoutingState.form || {};
  if (!String(form.primaryOwnerUserId || "").trim() && !String(form.primaryOwnerEmail || "").trim()) {
    credentialingRoutingState.error = "Choose a primary portal member or enter a primary notification email.";
    renderCredentialing();
    return;
  }
  const commandId = credentialingRoutingState.commandId || credentialingIdempotencyKey();
  credentialingRoutingState.commandId = commandId;
  credentialingRoutingState.saving = true;
  credentialingRoutingState.error = "";
  credentialingRoutingState.savedMessage = "";
  renderCredentialing();
  fetch(`${credentialingApiBase()}/api/credentialing/routing`, {
    method: "POST",
    cache: "no-store",
    headers: credentialingApiHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({
      orgId,
      expectedVersion: Number(credentialingRoutingState.configuration?.version || 0),
      commandId,
      primaryOwnerUserId: String(form.primaryOwnerUserId || ""),
      primaryOwnerEmail: String(form.primaryOwnerEmail || ""),
      escalationOwnerUserId: String(form.escalationOwnerUserId || ""),
      escalationOwnerEmail: String(form.escalationOwnerEmail || ""),
      isEmailEnabled: form.isEmailEnabled !== false
    })
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!credentialingRoutingState.open || orgId !== String(credentialingRoutingState.orgId || "").trim()) return;
    credentialingRoutingState.saving = false;
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok || payload.error) {
      credentialingRoutingState.error = credentialingRoutingErrorMessage(response.status, payload);
      renderCredentialing();
      return;
    }
    const configuration = payload.routing || {};
    credentialingRoutingState.status = "ready";
    credentialingRoutingState.configuration = configuration;
    credentialingRoutingState.form = credentialingRoutingForm(configuration);
    credentialingRoutingState.linkResults = payload.linkResults || null;
    credentialingRoutingState.commandId = "";
    credentialingRoutingState.savedMessage = payload.replayed
      ? "This exact routing change was already saved."
      : "Reviewer routing saved.";
    credentialingState.routingAccess = {
      configured: Boolean(configuration.configured),
      primaryMode: String(configuration.primaryMode || "unconfigured"),
      escalationMode: String(configuration.escalationMode || "inherits_primary"),
      emailEnabled: configuration.emailEnabled !== false,
      updatedAtUtc: String(configuration.updatedAtUtc || ""),
      version: Number(configuration.version || 0)
    };
    renderCredentialing();
    requestCredentialingBoard({ rerender: false, preserveBoard: true });
  }).catch(() => {
    if (!credentialingRoutingState.open || orgId !== String(credentialingRoutingState.orgId || "").trim()) return;
    credentialingRoutingState.saving = false;
    credentialingRoutingState.error = "Could not reach the portal. Retry safely; the same command will not create a duplicate change.";
    renderCredentialing();
  });
}

function credentialingHeaderMarkup() {
  const totals = credentialingTotals();
  return `
    <section class="panel authz-header-panel">
      <div class="authz-header">
        <div class="authz-header-identity">
          <!-- HEADER_UNIFICATION_SPEC: topbar names the view; this chip names
               the company scope so a multi-org board cannot look single-tenant. -->
          <span class="cred-pill cred-pill--blue">${escapeHtml(credentialingCompanyLabel())}</span>
          <p>Claims parked because the provider isn't credentialed yet. Add the date their credentialing took effect and we'll release them.</p>
        </div>
        <div class="authz-header-counts">
          <span title="Provider and payer pairs with parked claims and no credentialing decision recorded yet. This is the number on the Credentialing badge."><strong>${Number(totals.undecidedGroupCount) || 0}</strong> awaiting a decision</span>
          <span><strong>${Number(totals.providerCount) || 0}</strong> providers</span>
          <span><strong>${Number(totals.claimCount) || 0}</strong> claims</span>
          <span><strong>${Number(totals.lineCount) || 0}</strong> lines</span>
          ${isRedactedClientRole() ? "" : `<span><strong>${escapeHtml(credentialingExpectedText(totals.expectedTotal, totals.unknownExpectedLineCount, totals.lineCount, { compact: true }))}</strong></span>`}
        </div>
        <div class="authz-header-actions">${centralReachCheckButtonsMarkup()}</div>
      </div>
      <div class="authz-filter-row">
        <label class="authz-search">
          <span class="authz-search-icon" aria-hidden="true">⌕</span>
          <input type="search" placeholder="Search provider or payer" aria-label="Search provider or payer" value="${escapeHtml(credentialingState.search)}" data-cred-search>
        </label>
        ${credentialingDirectoryControlsMarkup()}
      </div>
    </section>`;
}

// EVERY NAMED CONDITION THE SERVER CAN REPORT, RENDERED AS ITS OWN SENTENCE.
//
// These are not decoration. Each one is a state in which the number on the
// screen means something different from what it looks like, and the whole point
// of the server naming them is that a human sees WHICH one. An empty board with
// no explanation is the failure this lane already shipped once.
function credentialingConditionsMarkup() {
  const meta = credentialingMeta();
  const notes = [];

  if (credentialingState.authorizationFieldUnavailable) {
    notes.push(["red", "This board cannot read the credentialing signal right now.",
      "The routing field is not available on the current data source, so an empty board here says nothing about whether you have credentialing work. Tell TMS."]);
  }
  if (credentialingState.claimStatusUnavailable) {
    notes.push(["red", "This board cannot read claim status right now.",
      "A claim parks here only when it is On Hold AND carries a credentialing placeholder. Without the status half, nothing can route. Tell TMS."]);
  }
  if (credentialingState.truncated) {
    notes.push(["amber", "This board is showing a capped view.",
      "There were more parked lines than one read can carry, so the counts and money below are a floor, not the whole picture — and no release can be recorded until it clears."]);
  }
  if (credentialingState.refreshHeldOrgs.length) {
    notes.push(["amber", `${credentialingState.refreshHeldOrgs.length} company/companies are not in these totals.`,
      "Their latest information is not ready, so parked claims remain withheld until it can be verified."]);
  }

  // THE ORPHANS. Lines whose classification column says credentialing and whose
  // authorization field does not corroborate it. They belong to NEITHER lane, so
  // without this they reach nobody — and they are deliberately NOT folded into
  // the count above, because they are not work this board can act on.
  // The billed-out, mis-marked and undecidable line notes are TMS data-quality
  // work in EZClaim. A client cannot act on them and read them as contradicting
  // the board (2026-10-13), so they show to TMS staff only.
  const staffNotes = !isExternalClientRole(state?.role);
  const billedOutCount = Number(meta.excludedBilledOutMarker) || 0;
  if (staffNotes && billedOutCount > 0) {
    notes.push(["gray", `${billedOutCount} billed-out CPH line${billedOutCount === 1 ? "" : "s"} stay off this board.`,
      "Those claims already went out (often toward a write-off). On Hold is what makes credentialing the client's problem — billed-out CPH is internal and is not counted above."]);
  }

  const orphanCount = Number(meta.orphanedLegacyCredentialingRows) || 0;
  const spellings = Array.isArray(meta.unrecognisedCredentialingSpellings) ? meta.unrecognisedCredentialingSpellings : [];
  if (credentialingState.orphanScanUnavailable) {
    notes.push(["gray", "An empty board here is not proof there is nothing to do.",
      "One of the checks behind this screen could not run, so claims marked as credentialing work may exist that it cannot see. Tell TMS before you treat this as clear."]);
  } else if (staffNotes && orphanCount > 0) {
    notes.push(["amber", `${orphanCount} line${orphanCount === 1 ? "" : "s"} say credentialing but are not on this board.`,
      `Somebody marked ${orphanCount === 1 ? "it" : "them"} as credentialing work in the classification column, but the authorization field on the claim carries no CPH placeholder, so ${orphanCount === 1 ? "it routes" : "they route"} nowhere. ${spellings.length ? `Spellings found: ${spellings.join(", ")}. ` : ""}These need correcting in EZClaim — they are not counted above.` +
      (credentialingState.orphanScanTruncated ? " The scan was also capped, so there may be more." : "")]);
  }
  // THE ROWS THE SCAN COULD NOT DECIDE. Their classification column says credentialing
  // but the read carried no authorization-marker lane to check it against, so they
  // are neither orphans nor board rows - and until now the count (83 on the live
  // board, 2026-09-03) reached nobody. Under its own name, never folded into the
  // orphan figure: an over-count there would send a biller to correct EZClaim rows
  // that may be perfectly fine.
  const undecidable = Number(meta.orphanScanUndecidableRows) || 0;
  if (staffNotes && !credentialingState.orphanScanUnavailable && undecidable > 0) {
    notes.push(["gray", `${undecidable} line${undecidable === 1 ? "" : "s"} marked credentialing could not be checked.`,
      `${undecidable === 1 ? "Its" : "Their"} classification column says credentialing${spellings.length ? ` (${spellings.join(", ")})` : ""}, but this read could not compare ${undecidable === 1 ? "it" : "them"} against the authorization field, so ${undecidable === 1 ? "it is" : "they are"} neither on this board nor counted above. Tell TMS if this number persists.`]);
  }

  // A MISSING BUTTON NEEDS A REASON. The board lists every company this
  // operator holds, but recording a credentialing date resolves ONE company's
  // claims, so the add control needs a named company. Without this line the
  // all-companies board looks read-only for no stated reason.
  if (credentialingState.overlayConfigured && !credentialingSelectedOrgId() && credentialingGroups().length) {
    notes.push(["gray", "Pick one company in the header to add a credentialing date.",
      "This board shows every company you cover. A credentialing date releases one company's parked claims, so it is recorded against one company at a time."]);
  }

  if (!credentialingState.overlayConfigured) {
    notes.push(["gray", "Credentialing cannot be added yet.",
      "The board below is live and shows what is parked, but credentialing storage is still being set up, so an entry will be refused rather than silently lost."]);
  }

  if (!notes.length) return "";
  return `<section class="panel cred-conditions">${notes.map(([tone, title, detail]) => `
    <div class="cred-condition cred-condition--${tone}">
      <p class="cred-condition-title">${escapeHtml(title)}</p>
      <p class="cred-condition-detail">${escapeHtml(detail)}</p>
    </div>`).join("")}</section>`;
}

function credentialingSaveStripMarkup() {
  const save = credentialingState.lastSave;
  if (!save) return "";
  return `
    <section class="panel authz-save-strip">
      <p class="authz-save-strip-message">${escapeHtml(save.message)}</p>
      ${save.retryDecisionId ? `<button class="btn" type="button" data-cred-retry-upload="${escapeHtml(save.retryDecisionId)}">Retry document upload</button>` : ""}
      <button class="authz-link-button" data-cred-dismiss-save>Dismiss</button>
    </section>`;
}

function credentialingHiddenFilterBook() {
  const filtersActive = Boolean(String(credentialingState.search || "").trim() || credentialingState.providerFilter || credentialingState.bucketFilter !== "All");
  if (!filtersActive) return null;
  const totals = credentialingTotals();
  const lines = Number(totals.lineCount) || 0;
  const expected = Number(totals.expectedTotal);
  if (!lines) return null;
  return {
    lines,
    expected: Number.isFinite(expected) ? expected : null,
    unknownExpectedLineCount: Number(totals.unknownExpectedLineCount) || 0
  };
}

function credentialingBoardMarkup() {
  if (credentialingState.status === "loading" && credentialingState.board?.page) return '<section class="panel"><p role="status">Loading matching credentialing work…</p></section>';
  const cards = credentialingProviderCards();
  if (!cards.length) {
    const meta = credentialingMeta();
    const undecidable = Number(meta.orphanScanUndecidableRows) || 0;
    const hidden = credentialingHiddenFilterBook();
    // "No credentialing work" and "no credentialing signal" are different facts.
    // The conditions block above already names the second; this says the first
    // plainly rather than showing a blank rectangle.
    const title = hidden
      ? "Nothing matches these filters."
      : (undecidable > 0
        ? "Credentialing checks are incomplete."
        : (meta.conventionAdoptionPending && !credentialingState.authorizationFieldUnavailable
          ? "No CPH claims are currently on hold."
          : "Nothing matches these filters."));
    let note = undecidable > 0
      ? "Some lines marked for credentialing could not be verified. Check those lines before treating this queue as empty."
      : "A claim appears here only when it is On Hold and its authorization field carries a CPH credentialing placeholder.";
    if (!isRedactedClientRole() && hidden) {
      note = `Filters are hiding ${credentialingExpectedText(hidden.expected, hidden.unknownExpectedLineCount, hidden.lines)} across ${Number(hidden.lines).toLocaleString()} parked line${hidden.lines === 1 ? "" : "s"}.`;
    } else if (hidden && hidden.lines > 0) {
      note = `Filters are hiding ${Number(hidden.lines).toLocaleString()} parked line${hidden.lines === 1 ? "" : "s"}. That is not $0 on this board.`;
    }
    const emptyAndFullyChecked = !hidden
      && undecidable === 0
      && !String(credentialingState.search || "").trim()
      && !credentialingState.providerFilter
      && String(credentialingState.bucketFilter || "All") === "All"
      && !credentialingState.truncated
      && !credentialingState.authorizationFieldUnavailable
      && !credentialingState.claimStatusUnavailable
      && !credentialingState.orphanScanUnavailable
      && !credentialingState.orphanScanTruncated
      && !(Array.isArray(credentialingState.refreshHeldOrgs) && credentialingState.refreshHeldOrgs.length);
    if (emptyAndFullyChecked) {
      return "<section class=\"panel authz-empty-panel authz-empty-celebrate\"><div class=\"authz-empty\">"
        + "<p class=\"authz-empty-emoji\" aria-hidden=\"true\">🎉</p>"
        + "<p class=\"authz-empty-title\">Nothing is parked for credentialing right now.</p>"
        + "<p class=\"authz-empty-note\">" + escapeHtml(credentialingLastCheckedNote()) + "</p>"
        + "</div></section>";
    }
    return (hidden ? credentialingDirectoryBreakdownMarkup(cards) : "") + `<section class="panel"><div class="authz-empty">
      <p class="authz-empty-title">${escapeHtml(title)}</p>
      <p class="authz-empty-note">${escapeHtml(note)}</p>
      ${hidden ? `<button class="ghost-button" type="button" data-cred-clear-filters>Clear filters</button>` : ""}
    </div></section>`;
  }
  if (credentialingState.board?.page) return credentialingDirectoryBreakdownMarkup(cards)
    + operationalBoardPagerHtml(credentialingState.board.page, "credentialing", "Credentialing")
    + cards.map((card) => credentialingProviderCardMarkup(card, credentialingState.board.page.totalRows)).join("");
  const page = workflowDirectoryPage(cards, credentialingState);
  return credentialingDirectoryBreakdownMarkup(cards) + workflowDirectoryPagerMarkup(page, "cred")
    + page.rows.map((card) => credentialingProviderCardMarkup(card, cards.length)).join("");
}

function credentialingProviderCardMarkup(card, visibleCount = 0) {
  const override = credentialingState.openProviders[card.key];
  const open = typeof override === "boolean" ? override : visibleCount <= 1 || Boolean(credentialingState.providerFilter);
  return `
    <section class="panel cred-provider-card" data-cred-provider="${escapeHtml(card.key)}">
      <div class="cred-provider-head">
        <button class="cred-provider-toggle" data-cred-toggle-provider="${escapeHtml(card.key)}" aria-expanded="${open ? "true" : "false"}">
          <span class="authz-caret">${open ? "▾" : "▸"}</span>
          <span class="cred-provider-name">${escapeHtml(card.providerDisplay)}</span>
          <span class="authz-patient-org" title="Company">${escapeHtml(card.orgLabel || authorizationOrgLabel(card.orgId) || portalCompanyLabel(card.orgId))}</span>
        </button>
        <div class="cred-provider-meta">
          <span>${card.payerCount} payer${card.payerCount === 1 ? "" : "s"}</span>
          <span>${card.claimCount} claim${card.claimCount === 1 ? "" : "s"}</span>
          <span>${card.parkedLineCount} parked line${card.parkedLineCount === 1 ? "" : "s"}</span>
          ${isRedactedClientRole() ? "" : `<strong>${escapeHtml(credentialingExpectedText(card.parkedExpectedTotal, card.parkedUnknownExpectedLineCount, card.parkedLineCount, { compact: true }))}</strong>`}
        </div>
      </div>
      ${open ? `<div class="cred-provider-body">${card.payers.map(credentialingPayerRowMarkup).join("")}</div>` : ""}
    </section>`;
}

function credentialingPayerRowMarkup(group) {
  const linesOpen = Boolean(credentialingState.openLineTables[group.relationshipId]);
  return `
    <div class="cred-payer-row" data-cred-relationship="${escapeHtml(group.relationshipId)}">
      <div class="cred-payer-head">
        <span class="cred-payer-name">${escapeHtml(group.payerDisplay || "(no payer)")}</span>
      </div>
      <div class="cred-payer-meta">
        <span>${Number(group.parkedLineCount) || 0} parked line${(Number(group.parkedLineCount) || 0) === 1 ? "" : "s"}</span>
        <span>${Number(group.claimCount) || 0} claim${(Number(group.claimCount) || 0) === 1 ? "" : "s"}</span>
        ${isRedactedClientRole() ? "" : `<strong>${escapeHtml(credentialingExpectedText(group.parkedExpectedTotal, group.parkedUnknownExpectedLineCount, group.parkedLineCount))}</strong>`}
        ${group.firstServiceDate ? `<span>${escapeHtml(group.firstServiceDate)} → ${escapeHtml(group.lastServiceDate || group.firstServiceDate)}</span>` : ""}
      </div>
      <div class="cred-payer-actions">
        ${String(group.bucket || "") === "parked_pre_effective"
          ? `<span class="cred-readonly-note">Already credentialed; pre-effective claims stay parked.</span>`
          : (credentialingGroupCanBeRecorded(group)
            ? `<button class="btn btn-primary" data-cred-record="${escapeHtml(group.relationshipId)}">Add credentialing</button>`
            : `<span class="cred-readonly-note">${credentialingSelectedOrgId() ? "Only the practice can add credentialing." : "Select this company to add credentialing."}</span>`)}
        <button class="authz-link-button" data-cred-lines="${escapeHtml(group.relationshipId)}">${linesOpen ? "Hide" : "View"} lines</button>
      </div>
      ${linesOpen ? credentialingLineTableMarkup(group) : ""}
    </div>`;
}

function credentialingLineTableMarkup(group) {
  const lines = Array.isArray(group.lines) ? group.lines : [];
  const showAll = Boolean(credentialingState.showAllLines[group.relationshipId]);
  const visible = showAll ? lines : lines.slice(0, CRED_LINE_PREVIEW_CAP);
  const rows = visible.map((line) => `
    <tr>
      <td>${escapeHtml(line.patient || "")}</td>
      <td>${escapeHtml(line.claimId || "")}</td>
      <td>${escapeHtml(line.dos || "")}</td>
      <td>${escapeHtml(line.cptCode || "")}</td>
      ${isRedactedClientRole() ? "" : `<td class="num">${line.expectedAmount == null ? "Expected not yet known" : escapeHtml(credentialingMoney(line.expectedAmount))}</td>`}
    </tr>`).join("");
  return `
    <div class="authz-line-table-wrap cred-line-table-wrap">
      <table class="authz-line-table cred-line-table">
        <thead><tr><th>Patient</th><th>Claim</th><th>DOS</th><th>CPT</th>${isRedactedClientRole() ? "" : `<th class="num">Expected reimbursement</th>`}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
      ${lines.length > visible.length ? `<button class="authz-link-button" data-cred-show-all="${escapeHtml(group.relationshipId)}">Show all ${lines.length} lines</button>` : ""}
    </div>`;
}

// ---------------------------------------------------------------------------
// The BILL-OUT queue (credentialing.submit).
// ---------------------------------------------------------------------------
function credentialingQueueMarkup() {
  if (!canSendCredentialedClaims()) return "";
  const queue = credentialingState.queue;
  if (queue.status === "idle" || queue.status === "loading") {
    return `<section class="panel cred-queue"><h4>Claims to bill out</h4><p class="cred-condition-detail">Loading the bill-out queue…</p></section>`;
  }
  if (queue.status === "error") {
    return `<section class="panel cred-queue"><h4>Claims to bill out</h4><p class="cred-condition-detail">${escapeHtml(queue.error)}</p></section>`;
  }
  const tasks = credentialingQueueTasks();
  if (!tasks.length) {
    return "<section class=\"panel cred-queue\"><h4>Claims to bill out</h4>"
      + "<p class=\"cred-condition-detail\">Nothing is waiting to be sent. A task appears here the moment a credentialing approval releases parked claims.</p></section>";
  }
  const rows = tasks.map((task) => `
    <tr class="cred-queue-row">
      <td data-label="Provider">${escapeHtml(task.providerDisplay || "")}</td>
      <td data-label="Payer">${escapeHtml(task.payerDisplay || "")}</td>
      <td data-label="Effective date">${escapeHtml(task.credentialedEffectiveDate || "")}</td>
      <td data-label="Still held">${Number(task.remainingLineCount) || 0} of ${Number(task.linkedLineCount) || 0}</td>
      <td class="num" data-label="Expected reimbursement">${escapeHtml(credentialingExpectedText(task.remainingExpectedTotal, task.remainingUnknownExpectedLineCount, task.remainingLineCount, { compact: true }))}</td>
      <td data-label="Status"><span class="cred-pill cred-pill--${task.overdueToSend || task.autoReturnNote ? "red" : "blue"}" title="${escapeHtml(task.status || "")}">${escapeHtml(credentialingTaskStatusLabel(task.status))}</span>${task.autoReturnNote ? `<div class="cred-condition-detail" data-cred-auto-return>${escapeHtml(task.autoReturnNote)}</div>` : ""}</td>
      <td data-label="Next step" data-cred-queue-cell="actions">${credentialingTaskActionsMarkup(task)}</td>
    </tr>`).join("");
  return `
    <section class="panel cred-queue">
      <h4>Claims to bill out</h4>
      <p class="cred-condition-detail">A credentialing approval released these claims. Bill them out, then mark them billed out — the task closes on its own only once every claim shows as Submitted, and is flagged if that takes more than 7 days.</p>
      ${credentialingSelectedOrgId() ? "" : `<p class="cred-condition-detail">Pick one company in the header to mark any of these billed out. Billing out is recorded per company.</p>`}
      <div class="authz-line-table-wrap cred-queue-table-wrap">
        <table class="authz-line-table cred-queue-table">
          <thead><tr><th>Provider</th><th>Payer</th><th>Effective</th><th>Still held</th><th class="num">Expected reimbursement</th><th>Status</th><th></th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </section>`;
}

// The same plain words the Authorizations board uses for the same steps, never
// the raw task code (Nathan, 2026-10-29: "make sure CPH is not as confusing").
function credentialingTaskStatusLabel(status) {
  const labels = {
    ready: "Ready to bill",
    in_progress: "Billing in progress",
    submitted: "Billed — awaiting confirmation",
    blocked: "Blocked",
    confirmed: "Confirmed — claims left On Hold",
    cancelled: "Cancelled"
  };
  return labels[String(status || "").trim()] || String(status || "");
}

function credentialingTaskActionsMarkup(task) {
  if (!credentialingTaskMatchesSelectedOrg(task)) return "";
  // ACTIONS COME FROM THE TASK'S OWN `actionable` FLAG, which the server derives
  // from a whitelist — never "everything except closed". An unreadable row must
  // not be offered a send button.
  if (!task || !task.actionable) return "";
  const id = escapeHtml(task.submissionTaskId || "");
  if (task.status === "ready") return `<button class="authz-link-button" data-cred-task="${id}" data-cred-task-action="claim">Start</button>`;
  if (task.status === "in_progress") return `<button class="authz-link-button" data-cred-task="${id}" data-cred-task-action="submit">Mark billed out</button>`;
  return "";
}

// ---------------------------------------------------------------------------
// The record drawer.
// ---------------------------------------------------------------------------
function credentialingDrawerMarkup() {
  if (!credentialingDrawerState.open) return "";
  const form = credentialingDrawerState.form;
  const errors = credentialingDrawerState.errors || [];
  return `
    <div class="cred-drawer-scrim" data-cred-close-drawer></div>
    <aside class="cred-drawer" role="dialog" aria-label="Add credentialing">
      <header class="cred-drawer-head">
        <h4>Add credentialing</h4>
        <button class="authz-link-button" data-cred-close-drawer>Close</button>
      </header>
      <p class="cred-drawer-context">
        <strong>${escapeHtml(credentialingDrawerState.providerDisplay)}</strong> with
        <strong>${escapeHtml(credentialingDrawerState.payerDisplay)}</strong>
        — ${credentialingDrawerState.lines.length} parked line${credentialingDrawerState.lines.length === 1 ? "" : "s"}
      </p>
      ${credentialingDrawerState.errorBanner ? `<p class="cred-drawer-error">${escapeHtml(credentialingDrawerState.errorBanner)}</p>` : ""}
      <label class="cred-field">
        <span>Credentialing effective date</span>
        <input type="date" value="${escapeHtml(form.effectiveDate)}" data-cred-form="effectiveDate" required>
      </label>
      <p class="cred-drawer-note">Claims dated before this effective date remain on hold. No termination date is needed.</p>
      <label class="cred-field">
        <span>Note (optional)</span>
        <textarea rows="3" data-cred-form="reason">${escapeHtml(form.reason)}</textarea>
      </label>
      <label class="cred-field authz-file-field">
        <span>Credentialing document (optional)</span>
        <input type="file" data-cred-file accept=".pdf,.png,.jpg,.jpeg,.heic,application/pdf,image/png,image/jpeg,image/heic">
        ${credentialingDrawerState.file ? `<span class="authz-file-chosen">${escapeHtml(credentialingDrawerState.file.name)} · ${escapeHtml(authzFileSize(credentialingDrawerState.file.size))}</span>` : ""}
        <small>PDF, PNG, JPG or HEIC, up to 10 MB.</small>
        ${credentialingDrawerState.fileError ? `<span class="authz-field-error">${escapeHtml(credentialingDrawerState.fileError)}</span>` : ""}
      </label>
      ${errors.length ? `<ul class="cred-drawer-errors">${errors.map((error) => `<li>${escapeHtml(error.message || "")}</li>`).join("")}</ul>` : ""}
      <div class="cred-drawer-actions">
        <button class="btn btn-primary" data-cred-save ${credentialingDrawerState.saving ? "disabled" : ""}>${credentialingDrawerState.saving ? "Saving…" : "Add credentialing"}</button>
        <button class="authz-link-button" data-cred-close-drawer>Cancel</button>
      </div>
    </aside>`;
}

function credentialingRoutingMarkup() {
  if (!credentialingRoutingState.open) return "";
  const status = credentialingRoutingState.status;
  const form = credentialingRoutingState.form || {};
  const users = Array.isArray(credentialingRoutingState.eligibleUsers) ? credentialingRoutingState.eligibleUsers : [];
  const primaryId = String(form.primaryOwnerUserId || "");
  const escalationId = String(form.escalationOwnerUserId || "");
  const optionMarkup = (selectedId, blankLabel) => {
    const found = users.some((user) => String(user.userId || "") === selectedId);
    const missing = selectedId && !found
      ? `<option value="${escapeHtml(selectedId)}" selected disabled>Previously linked member (no longer eligible)</option>`
      : "";
    return `<option value="">${escapeHtml(blankLabel)}</option>${missing}${users.map((user) => {
      const id = String(user.userId || "");
      const detail = [user.displayName || user.email || id, user.role].filter(Boolean).join(" · ");
      return `<option value="${escapeHtml(id)}" ${id === selectedId ? "selected" : ""}>${escapeHtml(detail)}</option>`;
    }).join("")}`;
  };
  const linkResults = credentialingRoutingState.linkResults;
  return `
    <div class="cred-drawer-scrim" data-cred-routing-close></div>
    <aside class="cred-drawer cred-routing-drawer" role="dialog" aria-modal="true" aria-labelledby="credRoutingTitle" data-cred-routing-dialog>
      <header class="cred-drawer-head">
        <div>
          <h4 id="credRoutingTitle">Credentialing reviewer routing</h4>
          <p class="cred-routing-subtitle">${escapeHtml(credentialingCompanyLabel())}</p>
        </div>
        <button class="authz-link-button cred-routing-close" type="button" data-cred-routing-close aria-label="Close reviewer routing">Close</button>
      </header>
      <p class="cred-drawer-note">Choose an active portal member of this company, or enter an email. An exact email match auto-links only when it belongs to one active member of this same company. Every other address stays email-only and cannot update statuses.</p>
      ${status === "loading" ? `<div class="cred-routing-loading" role="status">Loading eligible company members…</div>` : ""}
      ${status === "error" ? `
        <div class="cred-drawer-error" role="alert">${escapeHtml(credentialingRoutingState.error)}</div>
        <button class="btn" type="button" data-cred-routing-retry>Try again</button>` : ""}
      ${status === "ready" ? `
        ${credentialingRoutingState.error ? `<div class="cred-drawer-error" role="alert">${escapeHtml(credentialingRoutingState.error)}</div>` : ""}
        ${credentialingRoutingState.savedMessage ? `<div class="cred-routing-success" role="status" aria-live="polite">${escapeHtml(credentialingRoutingState.savedMessage)}</div>` : ""}
        <fieldset class="cred-routing-group">
          <legend>Primary reviewer</legend>
          <label class="cred-field">
            <span>Portal member</span>
            <select data-cred-routing-form="primaryOwnerUserId">${optionMarkup(primaryId, "Match by email or keep email-only")}</select>
          </label>
          <label class="cred-field">
            <span>Notification email</span>
            <input type="email" autocomplete="email" value="${escapeHtml(form.primaryOwnerEmail || "")}" data-cred-routing-form="primaryOwnerEmail" placeholder="reviewer@example.com">
          </label>
        </fieldset>
        <fieldset class="cred-routing-group">
          <legend>Escalation reviewer</legend>
          <label class="cred-field">
            <span>Portal member</span>
            <select data-cred-routing-form="escalationOwnerUserId">${optionMarkup(escalationId, "Inherit primary or match by email")}</select>
          </label>
          <label class="cred-field">
            <span>Notification email (optional)</span>
            <input type="email" autocomplete="email" value="${escapeHtml(form.escalationOwnerEmail || "")}" data-cred-routing-form="escalationOwnerEmail" placeholder="escalation@example.com">
          </label>
        </fieldset>
        <label class="cred-routing-checkbox">
          <input type="checkbox" data-cred-routing-form="isEmailEnabled" ${form.isEmailEnabled !== false ? "checked" : ""}>
          <span>Send credentialing email notifications</span>
        </label>
        ${linkResults ? `<div class="cred-routing-results" aria-live="polite">
          <p>${escapeHtml(credentialingRoutingLinkText(linkResults.primary, "Primary"))}</p>
          <p>${escapeHtml(credentialingRoutingLinkText(linkResults.escalation, "Escalation"))}</p>
        </div>` : ""}
        <div class="cred-drawer-actions">
          <button class="btn btn-primary" type="button" data-cred-routing-save ${credentialingRoutingState.saving ? "disabled" : ""}>${credentialingRoutingState.saving ? "Saving…" : "Save routing"}</button>
          <button class="authz-link-button" type="button" data-cred-routing-close>Cancel</button>
        </div>` : ""}
    </aside>`;
}

function openCredentialingDrawer(relationshipId) {
  const group = credentialingGroups().find((candidate) => String(candidate.relationshipId) === String(relationshipId));
  if (!group || !credentialingGroupCanBeRecorded(group)) {
    credentialingDrawerState.open = false;
    credentialingDrawerState.orgId = "";
    credentialingDrawerState.lines = [];
    return;
  }
  credentialingDrawerState.open = true;
  credentialingDrawerState.relationshipId = String(group.relationshipId);
  // THE GROUP'S OWN ORG, never a company selector. The server re-validates it
  // against the principal's scope, but sending the row's own org is what keeps a
  // multi-company biller from saving against the wrong tenant in the first place.
  credentialingDrawerState.orgId = String(group.orgId || "");
  credentialingDrawerState.providerKey = String(group.providerKey || "");
  credentialingDrawerState.providerDisplay = String(group.providerDisplay || "");
  credentialingDrawerState.payerKey = String(group.payerKey || "");
  credentialingDrawerState.payerDisplay = String(group.payerDisplay || "");
  credentialingDrawerState.payerExternalId = String(group.payerExternalId || "");
  credentialingDrawerState.fromStatus = String(group.status || "");
  credentialingDrawerState.lines = Array.isArray(group.lines) ? group.lines : [];
  credentialingDrawerState.form = { effectiveDate: "", reason: "" };
  credentialingDrawerState.file = null;
  credentialingDrawerState.fileError = "";
  credentialingDrawerState.errors = [];
  credentialingDrawerState.errorBanner = "";
  credentialingDrawerState.saving = false;
  renderCredentialing();
}

function closeCredentialingDrawer() {
  credentialingDrawerState.open = false;
  credentialingDrawerState.file = null;
  credentialingDrawerState.fileError = "";
  credentialingDrawerState.errors = [];
  credentialingDrawerState.errorBanner = "";
  renderCredentialing();
}

function saveCredentialingDecision() {
  if (credentialingDrawerState.saving) return;
  const selectedOrgId = credentialingSelectedOrgId();
  if (!selectedOrgId || selectedOrgId !== String(credentialingDrawerState.orgId || "").trim()) {
    credentialingDrawerState.open = false;
    credentialingDrawerState.orgId = "";
    credentialingDrawerState.lines = [];
    if (typeof showToast === "function") showToast("Select the company again before adding credentialing.");
    renderCredentialing();
    return;
  }
  const form = credentialingDrawerState.form;
  if (!String(form.effectiveDate || "").trim()) {
    credentialingDrawerState.errors = [{ message: "A credentialing effective date is required." }];
    renderCredentialing();
    return;
  }
  if (credentialingDrawerState.fileError) {
    credentialingDrawerState.errors = [{ message: credentialingDrawerState.fileError }];
    renderCredentialing();
    return;
  }
  credentialingDrawerState.saving = true;
  credentialingDrawerState.errors = [];
  credentialingDrawerState.errorBanner = "";
  renderCredentialing();
  const base = credentialingApiBase();
  const body = {
    orgId: credentialingDrawerState.orgId,
    selectedOrgId,
    relationshipId: credentialingDrawerState.relationshipId,
    fromStatus: credentialingDrawerState.fromStatus,
    status: CRED_STATUS.CREDENTIALED,
    effectiveDate: form.effectiveDate,
    reason: form.reason,
    providerKey: credentialingDrawerState.providerKey,
    providerDisplay: credentialingDrawerState.providerDisplay,
    payerKey: credentialingDrawerState.payerKey,
    payerDisplay: credentialingDrawerState.payerDisplay,
    payerExternalId: credentialingDrawerState.payerExternalId,
    // A FRESH KEY PER SAVE. Reusing one is a replay that links nothing.
    idempotencyKey: credentialingIdempotencyKey()
  };
  const pendingFile = credentialingDrawerState.file;
  const saveOrgId = credentialingDrawerState.orgId;
  const providerDisplay = credentialingDrawerState.providerDisplay;
  fetch(`${base}/api/credentialing/release`, {
    method: "POST",
    headers: credentialingApiHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(body)
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    credentialingDrawerState.saving = false;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      credentialingDrawerState.errorBanner = "Your session expired. Sign in again — this page will still be here.";
      credentialingDrawerState.errors = [];
      renderCredentialing();
      return;
    }
    if (!response.ok || (payload && payload.error)) {
      // THE SERVER'S OWN SENTENCE, verbatim. Its refusals explain themselves
      // (a zero-link release names the effective-date rule; a truncated read says
      // nothing was saved), and paraphrasing them here would lose the reason.
      credentialingDrawerState.errorBanner = String((payload && payload.message) || `That could not be saved (HTTP ${response.status}).`);
      credentialingDrawerState.errors = Array.isArray(payload && payload.errors) ? payload.errors : [];
      renderCredentialing();
      return;
    }
    const decisionId = String(payload.decisionId || "");
    const linked = Number(payload.linkedCount) || 0;
    if (pendingFile && decisionId) {
      credentialingState.pendingFileByDecisionId[decisionId] = pendingFile;
      credentialingState.pendingFileOrgByDecisionId[decisionId] = saveOrgId;
      return uploadCredentialingFile(decisionId, pendingFile, saveOrgId)
        .then(() => ({ decisionId, linked, uploaded: true }))
        .catch(() => ({ decisionId, linked, uploaded: false }));
    }
    return { decisionId, linked, uploaded: pendingFile ? false : null };
  }).then((outcome) => {
    if (!outcome) return;
    credentialingDrawerState.saving = false;
    credentialingDrawerState.open = false;
    const releaseNote = outcome.linked
      ? ` ${outcome.linked} on-hold line${outcome.linked === 1 ? " was" : "s were"} released.`
      : " No currently eligible lines were released.";
    credentialingState.lastSave = {
      message: `Credentialing added for ${providerDisplay}.${releaseNote}${outcome.uploaded === false ? " The credentialing was saved, but the document did not upload." : (outcome.uploaded ? " Document attached." : "")}`,
      retryDecisionId: outcome.uploaded === false && outcome.decisionId ? outcome.decisionId : ""
    };
    requestCredentialingBoard({ rerender: false });
    if (canSendCredentialedClaims()) requestCredentialingQueue({ rerender: false });
    renderCredentialing();
  }).catch(() => {
    credentialingDrawerState.saving = false;
    credentialingDrawerState.errorBanner = "Could not reach the portal. Nothing was saved.";
    renderCredentialing();
  });
}

function validateCredentialingFile(file) {
  return validateAuthorizationFile(file);
}

// The credentialing record is saved first. A document failure is therefore a
// retryable attachment problem and never rolls back or hides the saved record.
function uploadCredentialingFile(decisionId, file, orgId = "") {
  const base = credentialingApiBase();
  const targetOrgId = String(orgId || credentialingState.pendingFileOrgByDecisionId[decisionId] || "").trim();
  if (!base || typeof fetch !== "function" || typeof FormData !== "function") {
    credentialingState.uploadFailures[decisionId] = {
      fileName: file && file.name ? file.name : "",
      message: "Document upload is unavailable in this session — the credentialing record is saved."
    };
    return Promise.reject(new Error("upload_unavailable"));
  }
  const payload = new FormData();
  payload.append("file", file, file.name);
  payload.append("orgId", targetOrgId);
  return fetch(`${base}/api/credentialing/decisions/${encodeURIComponent(decisionId)}/files`, {
    method: "POST",
    headers: credentialingApiHeaders(),
    body: payload
  }).then((response) => {
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok) {
      credentialingState.uploadFailures[decisionId] = {
        fileName: file.name,
        message: response.status === 404
          ? "Document upload is not available yet — the credentialing record is saved."
          : `Document upload failed (HTTP ${response.status}) — the credentialing record is saved.`
      };
      throw new Error("upload_failed");
    }
    delete credentialingState.uploadFailures[decisionId];
    delete credentialingState.pendingFileByDecisionId[decisionId];
    delete credentialingState.pendingFileOrgByDecisionId[decisionId];
    return true;
  }).catch((error) => {
    if (!credentialingState.uploadFailures[decisionId]) {
      credentialingState.uploadFailures[decisionId] = {
        fileName: file.name,
        message: "Document upload failed — the credentialing record is saved. Retry when you can."
      };
    }
    throw error;
  });
}

function retryCredentialingFileUpload(decisionId) {
  const file = credentialingState.pendingFileByDecisionId[decisionId];
  if (!file) {
    showToast("Re-attach the document by adding credentialing again.");
    return;
  }
  showToast("Retrying the document upload…");
  uploadCredentialingFile(decisionId, file, credentialingState.pendingFileOrgByDecisionId[decisionId] || "")
    .then(() => {
      credentialingState.lastSave = { message: "Credentialing was already saved. Document attached.", retryDecisionId: "" };
      showToast("Document attached.");
      renderCredentialing();
    })
    .catch(() => {
      credentialingState.lastSave = { message: "Credentialing is saved, but the document still did not upload.", retryDecisionId: decisionId };
      showToast("The document still would not upload. The credentialing record is safe.");
      renderCredentialing();
    });
}

function moveCredentialingTask(submissionTaskId, action) {
  const base = credentialingApiBase();
  const selectedOrgId = credentialingSelectedOrgId();
  const task = (credentialingState.queue.tasks || []).find((candidate) => String(candidate.submissionTaskId) === String(submissionTaskId));
  if (!task || !selectedOrgId || !credentialingTaskMatchesSelectedOrg(task)) return;
  fetch(`${base}/api/credentialing/submission-task`, {
    method: "POST",
    headers: credentialingApiHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ orgId: task.orgId, selectedOrgId, submissionTaskId, action })
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      credentialingState.queue.error = "Your session expired. Sign in again — this page will still be here.";
      renderCredentialing();
      return;
    }
    if (!response.ok || (payload && payload.error)) {
      credentialingState.queue.error = String((payload && payload.message) || `That change could not be saved (HTTP ${response.status}).`);
    } else if (payload && payload.task && payload.task.status) {
      // Show the saved change at once; the re-read below reconciles it.
      Object.assign(task, payload.task);
    }
    requestCredentialingQueue({ rerender: false });
    renderCredentialing();
  }).catch(() => {
    credentialingState.queue.error = "Could not reach the portal. Nothing was changed.";
    renderCredentialing();
  });
}

function bindCredentialingControls(host) {
  bindCentralReachCheckButtons(host, "credentialing");
  host.querySelectorAll('[data-management-list="credentialing"]').forEach(button => button.addEventListener("click", () => {
    credentialingState.page = Number(button.dataset.managementPage); requestCredentialingBoard({ preserveBoard: true });
  }));
  host.querySelector("[data-cred-retry]")?.addEventListener("click", () => {
    credentialingState.status = "idle";
    requestCredentialingBoard();
  });
  const search = host.querySelector("[data-cred-search]");
  if (search) {
    search.addEventListener("input", () => {
      credentialingState.search = String(search.value || ""); credentialingState.page = 1;
      credentialingState.providerFilter = "";
      requestOperationalBoardSearch("credentialing");
    });
  }
  Array.from(host.querySelectorAll("[data-cred-bucket]")).forEach((button) => {
    button.addEventListener("click", () => {
      credentialingState.bucketFilter = String(button.dataset.credBucket || "All"); credentialingState.page = 1;
      requestCredentialingBoard({ preserveBoard: true });
    });
  });
  host.querySelectorAll("[data-cred-clear-filters]").forEach((button) => button.addEventListener("click", () => {
    credentialingState.search = "";
    credentialingState.bucketFilter = "All";
    credentialingState.providerFilter = "";
    credentialingState.sortOrder = "name";
    credentialingState.page = 1;
    requestCredentialingBoard({ preserveBoard: true });
  }));
  host.querySelector("[data-cred-provider-filter]")?.addEventListener("change", (event) => {
    credentialingState.providerFilter = String(event.target.value || "");
    credentialingState.page = 1;
    if (credentialingState.providerFilter) credentialingState.openProviders[credentialingState.providerFilter] = true;
    requestCredentialingBoard({ preserveBoard: true });
  });
  host.querySelector("[data-cred-sort]")?.addEventListener("change", (event) => {
    credentialingState.sortOrder = event.target.value === "lines" ? "lines" : "name";
    credentialingState.page = 1;
    requestCredentialingBoard({ preserveBoard: true });
  });
  host.querySelectorAll("[data-cred-page]").forEach((button) => button.addEventListener("click", () => {
    credentialingState.page = Number(button.dataset.credPage) || 0;
    renderCredentialing();
    document.querySelector(".cred-board .workflow-directory-pager")?.scrollIntoView({ block: "nearest" });
  }));
  ["expand", "collapse"].forEach((action) => host.querySelector("[data-cred-" + action + "-all]")?.addEventListener("click", () => {
    credentialingProviderCards().forEach((card) => { credentialingState.openProviders[card.key] = action === "expand"; });
    renderCredentialing();
  }));
  Array.from(host.querySelectorAll("[data-cred-toggle-provider]")).forEach((button) => {
    button.addEventListener("click", () => {
      const key = String(button.dataset.credToggleProvider || "");
      credentialingState.openProviders[key] = button.getAttribute("aria-expanded") !== "true";
      renderCredentialing();
    });
  });
  Array.from(host.querySelectorAll("[data-cred-lines]")).forEach((button) => {
    button.addEventListener("click", () => {
      const key = String(button.dataset.credLines || "");
      credentialingState.openLineTables[key] = !credentialingState.openLineTables[key];
      renderCredentialing();
    });
  });
  Array.from(host.querySelectorAll("[data-cred-show-all]")).forEach((button) => {
    button.addEventListener("click", () => {
      credentialingState.showAllLines[String(button.dataset.credShowAll || "")] = true;
      renderCredentialing();
    });
  });
  Array.from(host.querySelectorAll("[data-cred-record]")).forEach((button) => {
    button.addEventListener("click", () => openCredentialingDrawer(String(button.dataset.credRecord || "")));
  });
  Array.from(host.querySelectorAll("[data-cred-close-drawer]")).forEach((button) => {
    button.addEventListener("click", closeCredentialingDrawer);
  });
  Array.from(host.querySelectorAll("[data-cred-form]")).forEach((field) => {
    const key = String(field.dataset.credForm || "");
    const handler = () => {
      credentialingDrawerState.form[key] = String(field.value || "");
    };
    field.addEventListener("change", handler);
    field.addEventListener("input", () => { credentialingDrawerState.form[key] = String(field.value || ""); });
  });
  host.querySelector("[data-cred-file]")?.addEventListener("change", (event) => {
    const file = event.target.files && event.target.files[0];
    const check = validateCredentialingFile(file);
    credentialingDrawerState.fileError = check.error;
    credentialingDrawerState.file = check.error ? null : file;
    renderCredentialing();
  });
  host.querySelector("[data-cred-save]")?.addEventListener("click", saveCredentialingDecision);
  host.querySelector("[data-cred-retry-upload]")?.addEventListener("click", (event) => {
    retryCredentialingFileUpload(String(event.currentTarget.dataset.credRetryUpload || ""));
  });
  host.querySelector("[data-cred-dismiss-save]")?.addEventListener("click", () => {
    credentialingState.lastSave = null;
    renderCredentialing();
  });
  Array.from(host.querySelectorAll("[data-cred-task]")).forEach((button) => {
    button.addEventListener("click", () => moveCredentialingTask(
      String(button.dataset.credTask || ""),
      String(button.dataset.credTaskAction || "")
    ));
  });
  host.querySelector("[data-cred-routing-open]")?.addEventListener("click", openCredentialingRouting);
  Array.from(host.querySelectorAll("[data-cred-routing-close]")).forEach((button) => {
    button.addEventListener("click", closeCredentialingRouting);
  });
  host.querySelector("[data-cred-routing-retry]")?.addEventListener("click", () => {
    credentialingRoutingState.status = "loading";
    credentialingRoutingState.error = "";
    renderCredentialing();
    requestCredentialingRouting();
  });
  Array.from(host.querySelectorAll("[data-cred-routing-form]")).forEach((field) => {
    const key = String(field.dataset.credRoutingForm || "");
    const update = () => {
      credentialingRoutingState.form[key] = field.type === "checkbox" ? Boolean(field.checked) : String(field.value || "");
      credentialingRoutingState.commandId = "";
      credentialingRoutingState.savedMessage = "";
      credentialingRoutingState.linkResults = null;
      if (field.tagName === "SELECT" && field.value) {
        const user = credentialingRoutingState.eligibleUsers.find((candidate) => String(candidate.userId || "") === String(field.value));
        const emailKey = key === "primaryOwnerUserId" ? "primaryOwnerEmail" : "escalationOwnerEmail";
        if (user && !String(credentialingRoutingState.form[emailKey] || "").trim()) {
          credentialingRoutingState.form[emailKey] = String(user.email || "");
          renderCredentialing();
        }
      }
    };
    field.addEventListener("change", update);
    if (field.tagName === "INPUT" && field.type !== "checkbox") field.addEventListener("input", update);
  });
  host.querySelector("[data-cred-routing-save]")?.addEventListener("click", saveCredentialingRouting);
  host.querySelector("[data-cred-routing-dialog]")?.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeCredentialingRouting();
  });
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------
function renderAuthorizations() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("authorizations")) return;
  const host = document.getElementById("authorizationsBody");
  if (!host) return;
  const restoreSearchFocus = operationalBoardSearchFocus(host, "#authzSearchInput");
  if (!hasPermission(PERMISSIONS.AUTHORIZATIONS_VIEW)) {
    host.innerHTML = `
      <section class="panel authz-deny-panel">
        <h3>Authorizations</h3>
        <div class="authz-empty">
          <p class="authz-empty-title">You do not have access to Authorizations.</p>
          <p class="authz-empty-note">Ask your TMS contact to grant the Authorizations permission for your company.</p>
        </div>
      </section>`;
    return;
  }
  // FIRST-LOAD SCOPE ORDERING (defect 2, 2026-08-11). The board's org scope is
  // ASSIGNED BEFORE THE FIRST REQUEST IS ISSUED, never after. These two lines
  // used to run the other way round, so the FIRST /api/authorizations/board of
  // a session went out with no `orgId=` at all - the global tenant switcher's
  // narrowing filter - and the first paint showed the caller's whole scope
  // rather than the company they had selected. NOT a disclosure:
  // handleAuthorizationsBoard re-scopes every read from the bearer principal
  // (org_id IN (@scope_org_*) on the SQL lane, canAccessTenantScopedRecord on
  // the JSON lane) and `orgId=` is a filter that can only REMOVE rows - but it
  // was a wasted full-scope read and a first paint that disagreed with the
  // company selector rendered a few lines below.
  // KEEP THE ASSIGNMENT ABOVE THE REQUEST. Asserted by
  // backend/verify_frontend_authorizations_scope_contract.js.
  authorizationsState.orgId = activeAuthorizationOrgId();
  syncWorkflowDirectoryScope(authorizationsState, workflowDirectoryScope(authorizationsState.orgId));
  if (authorizationsState.status === "idle" || (authorizationsState.status === "error" && Date.now() - Number(authorizationsState.errorAt || 0) > 15000)
    || (authorizationsApiToken() && authorizationsState.requestKey !== authorizationBoardRequestKey())) requestAuthorizationBoard({ rerender: false });
  loadAuthorizationCollapseState();
  if (authorizationSendState.status === "idle" && authorizationsApiToken()) requestAuthorizationSendQueue({ rerender: false });
  host.innerHTML = authorizationsMarkup();
  bindAuthorizationsControls(host);
  restoreSearchFocus();
  bindAuthorizationSendQueueControls(host);
  updateAuthorizationsNavBadge();
}

function authorizationsMarkup() {
  return `
    ${authorizationsHeaderMarkup()}
    ${authorizationCentralReachMarkup()}
    ${authorizationWaitingReportPanelMarkup()}
    ${authorizationSendQueueMarkup()}
    ${authorizationsCompletenessMarkup()}
    ${authorizationsSaveStripMarkup()}
    <div class="authz-board">${authorizationsBoardMarkup()}</div>
    ${authorizationsResolvedMarkup()}
  `;
}

// ---------------------------------------------------------------------------
// CentralReach authorization check (Nathan, 2026-10-29). No install: the
// Check CentralReach button pops CentralReach open in a new tab; after logging
// in, the user clicks the "TMS CentralReach check" bookmark (dragged once to
// the bookmarks bar). The bookmark READS billing entries and authorization
// numbers in the user's own CentralReach session - it never changes anything
// there - and hands them back to this portal tab, which sends them to the
// server. The server records every line whose DOS, CPT and insurance all
// match; everything else stays on hold with its reason.
// ---------------------------------------------------------------------------
// Derived from the one CentralReach constant (verify_ehr_entry_centralreach_link).
const CENTRAL_REACH_ORIGIN = CENTRAL_REACH_BILLING_MANAGER_URL.replace(/^(https:\/\/[^/]+).*$/, "$1");
// RETHINK (Nathan, 2026-10-30): the same check for clients on Rethink. Their
// EHR Entry ID is Rethink's Appointment ID (verified on Shining Moments' lines),
// so the bookmark finds each visit by that ID, never by name. One bookmark
// serves both systems: it reads whichever one it is clicked on.
const RETHINK_ORIGIN = "https://webapp.rethinkbehavioralhealth.com";
const EHR_CHECK_SYSTEMS = Object.freeze({
  centralreach: Object.freeze({ key: "centralreach", label: "CentralReach", origin: CENTRAL_REACH_ORIGIN, start: `${CENTRAL_REACH_ORIGIN}/` }),
  rethink: Object.freeze({ key: "rethink", label: "Rethink", origin: RETHINK_ORIGIN, start: `${RETHINK_ORIGIN}/HealthCare#/` })
});
function ehrCheckSystem(key = authorizationCentralReachState.system) {
  return EHR_CHECK_SYSTEMS[key] || EHR_CHECK_SYSTEMS.centralreach;
}
const authorizationCentralReachState = { status: "idle", message: "", result: null, requestId: "", window: null, orgId: "", entries: null, helloPending: false, system: "centralreach", progress: null, oldBookmark: false };
const AUTHZ_CENTRAL_REACH_BUSY = ["worklist", "waiting", "reading", "recording"];

// THE CHECK PROGRAM. Since 2026-11-02 the portal hands it to the permanent
// bookmark (tmsEhrLoaderBookmark below) on every run, so updates reach everyone
// with the portal release. An older bookmark holds its own frozen copy of it.
// Runs on the CentralReach tab, so it must be self-contained:
// it may use nothing from outside this function. The ONLY CentralReach
// requests it can send are READ_REQUESTS - the reads CentralReach's own pages
// use to display a billing entry and an authorization - and read() refuses any
// other path before sending. backend/verify_centralreach_authorization_lookup.js
// pins that list to the server's and fails on any write-shaped request.
function tmsCentralReachBookmark(portalOrigin, centralReachOrigin, rethinkOrigin, programVersion) {
  const system = window.location.origin === centralReachOrigin ? "centralreach"
    : (rethinkOrigin && window.location.origin === rethinkOrigin ? "rethink" : "");
  const label = system === "rethink" ? "Rethink" : "CentralReach";
  if (!system) {
    window.alert("Drag this TMS EHR check button to your bookmarks bar. Then click it on the CentralReach or Rethink tab that the portal opens.");
    return;
  }
  const portal = window.opener;
  if (!portal) {
    window.alert(`Start from Check ${label} on the TMS portal's Authorizations page, then click this bookmark in the ${label} tab it opens.`);
    return;
  }
  const READ_REQUESTS = [
    "/crxapi/internal/billing/query",
    "/api/?contacts.loadcontactauthorizations",
    "/api/?resources.getresource",
    "/api/?resources.loadresourceauthorizationSettings",
    "/api/?resources.loadresourceauthorizationsetting"
  ];
  // Rethink: three GET reads its own pages use (a visit by Appointment ID, the
  // client's authorizations, the client's insurances). Nothing else is sent.
  const RETHINK_READ_REQUESTS = [
    "/core/api/v2/scheduling/scheduling/GetAppointmentsByIdAsync",
    "/core/api/v2/client/client/GetClientAuthorizations",
    "/core/api/v2/client/client/getclientfunders"
  ];
  const banner = document.createElement("div");
  const bannerStyle = "position:fixed;z-index:2147483647;top:12px;right:12px;max-width:360px;padding:12px 16px;border-radius:8px;color:#fff;font:14px/1.4 system-ui,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.3);";
  const show = (text, percent) => {
    banner.setAttribute("style", `${bannerStyle}background:${percent === undefined ? "#013a6e" : `linear-gradient(90deg,#0b7a75 ${percent}%,#013a6e ${percent}%)`}`);
    banner.textContent = `TMS ${label} check (read-only${programVersion ? `, version ${programVersion}` : ""}): ${text}`;
    if (!banner.isConnected) document.body.appendChild(banner);
  };
  show("waiting for the portal to prepare the list. This can take a minute or two…");
  const reply = (message) => portal.postMessage({ source: "tms-centralreach-bookmark", ...message }, portalOrigin);
  // The worklist takes 30 to 70 seconds on a big company (production, 2026-11-02),
  // so a 20-second "did not answer" told people a healthy run had failed.
  const timer = window.setTimeout(() => show(`the portal did not answer. Start again from Check ${label} on the portal.`), 180000);

  async function readAll(entries, onProgress) {
    const allowed = new Set(system === "rethink" ? RETHINK_READ_REQUESTS : READ_REQUESTS);
    const offset = new Date().getTimezoneOffset();
    const csrf = decodeURIComponent((document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith("csrf-token=")) || "").split("=").slice(1).join("="));
    let requests = 0;
    async function read(path, body, suffix = "") {
      if (!allowed.has(path)) throw new Error(`Refused a ${label} request that is not on the read-only list: ${path}`);
      // Rethink reads are GETs; the only thing after the path is a numeric id.
      if (suffix && !/^(\/\d{1,15}|\?appointmentId=\d{1,15}&memberId=&startDate=&isRedirectionFromDraft=&includeDeleted=false)$/.test(suffix)) throw new Error(`Refused a ${label} request with an unexpected suffix`);
      const rethink = system === "rethink";
      const headers = rethink ? { Accept: "application/json" } : { "Content-Type": "application/json; charset=UTF-8", Accept: "application/json" };
      if (!rethink && path.startsWith("/api/?")) Object.assign(headers, { "X-CSRF-Token": csrf, "X-Requested-With": "XMLHttpRequest", "X-CrApi-ClientSource": "members-spa", "members-api-action": path.slice(6) });
      requests += 1;
      const response = await fetch(rethink ? `${path}${suffix}` : path, rethink
        ? { method: "GET", credentials: "include", headers }
        : { method: "POST", credentials: "include", headers, body: JSON.stringify({ _utcOffsetMinutes: offset, ...body }) });
      if (!String(response.headers.get("content-type") || "").includes("json")) { const error = new Error(`Log in to ${label} first, then click the bookmark again.`); error.notLoggedIn = true; throw error; }
      return response.json();
    }
    const iso = (value) => {
      const text = String(value || "");
      let match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return `${match[1]}-${match[2]}-${match[3]}`;
      match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      return match ? `${match[3]}-${match[1].padStart(2, "0")}-${match[2].padStart(2, "0")}` : "";
    };
    const shift = (day, days) => { const date = new Date(`${day}T12:00:00Z`); date.setUTCDate(date.getUTCDate() + days); return date.toISOString().slice(0, 10); };
    const findKey = (value, key) => {
      if (!value || typeof value !== "object") return undefined;
      if (Object.prototype.hasOwnProperty.call(value, key)) return value[key];
      for (const child of Object.values(value)) { const found = findKey(child, key); if (found !== undefined) return found; }
      return undefined;
    };
    const memo = new Map();
    const once = (key, load) => { if (!memo.has(key)) memo.set(key, load()); return memo.get(key); };
    const started = Date.now();
    let done = 0;
    let found = 0;
    let missing = 0;
    let failed = 0;
    const authorizationsRead = new Set();
    async function lookup({ ehrBillingEntryId, dos }) {
      const query = await read("/crxapi/internal/billing/query", { isApplied: true, isDeferred: false, startDate: shift(dos, -90), endDate: shift(dos, 90), dateRange: "x", billingEntryId: ehrBillingEntryId, page: 1, pageSize: 50 });
      const item = (query.items || []).find((row) => String(row.Id) === String(ehrBillingEntryId));
      if (!item) return { ehrBillingEntryId, entry: { found: false }, groups: [] };
      const entryDos = iso(item.DateOfService);
      const clientAuths = await once(`client:${item.ClientId}`, () => read("/api/?contacts.loadcontactauthorizations", { contactId: item.ClientId }));
      const resourceIds = new Set((clientAuths.items || []).filter((auth) => iso(auth.StartDate) <= entryDos && entryDos <= iso(auth.EndDate)).map((auth) => String(auth.ResourceId)));
      if (item.AuthorizationResourceId) resourceIds.add(String(item.AuthorizationResourceId));
      const groups = [];
      for (const resourceId of resourceIds) {
        const [resource, settings] = await Promise.all([
          once(`resource:${resourceId}`, () => read("/api/?resources.getresource", { resourceId })),
          once(`settings:${resourceId}`, () => read("/api/?resources.loadresourceauthorizationSettings", { resourceId: Number(resourceId), showDeleted: false }))
        ]);
        const payorName = String((resource.resource || {}).AuthContactPayorName || "");
        for (const group of settings.authorizationSettings || []) {
          if (group.DeletedBy) continue;
          const start = iso(group.StartDate);
          const end = iso(group.EndDate);
          if (!(start <= entryDos && entryDos <= end)) continue;
          const detail = await once(`setting:${group.Id}`, () => read("/api/?resources.loadresourceauthorizationsetting", { authorizationSettingId: group.Id }));
          groups.push({ settingId: String(group.Id), resourceId, startDate: start, endDate: end, serviceCodes: (group.Authorizations || []).filter((code) => !code.DeletedBy).map((code) => String(code.serviceCode || "")), payorName, authorizationNumber: String(findKey(detail, "AuthorizationNumber") || ""), secondaryAuthorizationNumber: String(findKey(detail, "SecondaryAuthorizationNumber") || "") });
        }
      }
      return { ehrBillingEntryId, entry: { found: true, dateOfService: entryDos, procedureCode: String(item.ProcedureCodeString || ""), payorName: String(item.PayorName || "") }, groups };
    }
    // Rethink, in the CentralReach finding shape so the server decides both the
    // same way: the visit's insurance as "P:/S: <funder>", each authorization as
    // "Primary:/Secondary: <funder>" (Rethink labels every authorization's plan).
    async function lookupRethink({ ehrBillingEntryId }) {
      const visit = await read("/core/api/v2/scheduling/scheduling/GetAppointmentsByIdAsync", null, `?appointmentId=${ehrBillingEntryId}&memberId=&startDate=&isRedirectionFromDraft=&includeDeleted=false`);
      const appointment = visit && visit.appointment;
      const clientId = String(appointment && appointment.clientId || "");
      if (!appointment || String(appointment.id) !== String(ehrBillingEntryId) || !/^\d{1,15}$/.test(clientId)) return { ehrBillingEntryId, entry: { found: false }, groups: [] };
      const [auths, funders] = await Promise.all([
        once(`rt-auth:${clientId}`, () => read("/core/api/v2/client/client/GetClientAuthorizations", null, `/${clientId}`)),
        once(`rt-funders:${clientId}`, () => read("/core/api/v2/client/client/getclientfunders", null, `/${clientId}`))
      ]);
      const authList = Array.isArray(auths) ? auths : [];
      const funderList = Array.isArray(funders && funders.funders) ? funders.funders : [];
      const authFor = (funderId) => authList.find((auth) => String(auth.funderId) === String(funderId) && auth.funderInsurancePlanTypeId);
      const funderFor = (funderId) => funderList.find((funder) => String(funder.funderId) === String(funderId));
      const planOf = (funderId) => Number((authFor(funderId) || {}).funderInsurancePlanTypeId
        || findKey(funderFor(funderId) || {}, "insuranceTypeId") || findKey(funderFor(funderId) || {}, "insurancePlanTypeId")) || 0;
      const funderName = (funderId) => String((authFor(funderId) || {}).funderName || (funderFor(funderId) || {}).funderName || "");
      const word = (plan) => plan === 2 ? ["S", "Secondary"] : plan === 3 ? ["T", "Tertiary"] : plan === 1 ? ["P", "Primary"] : ["", ""];
      const entryDos = iso(appointment.startDate);
      let cpt = "";
      authList.forEach((auth) => (auth.billingCodes || []).forEach((code) => { if (!cpt && String(code.providerServiceId) === String(appointment.providerServiceId) && code.name) cpt = String(code.name); }));
      if (!cpt) cpt = String(findKey(appointment, "providerServiceName") || findKey(appointment, "serviceName") || "");
      const entryShort = word(planOf(appointment.funderId))[0];
      const groups = authList.filter((auth) => { const start = iso(auth.startDate); const end = iso(auth.endDate); return start && end && start <= entryDos && entryDos <= end; })
        .map((auth) => {
          const long = word(Number(auth.funderInsurancePlanTypeId) || 0)[1];
          return { settingId: String(auth.id || ""), resourceId: clientId, startDate: iso(auth.startDate), endDate: iso(auth.endDate),
            serviceCodes: (auth.billingCodes || []).map((code) => String(code.name || "")),
            payorName: `${long ? `${long}: ` : ""}${String(auth.funderName || "")}`,
            authorizationNumber: String(auth.authorizationNumber == null ? "" : auth.authorizationNumber), secondaryAuthorizationNumber: "" };
        });
      return { ehrBillingEntryId, entry: { found: true, dateOfService: entryDos, procedureCode: cpt, payorName: `${entryShort ? `${entryShort}: ` : ""}${funderName(appointment.funderId)}` }, groups };
    }
    const findings = new Array(entries.length);
    let next = 0;
    await Promise.all(Array.from({ length: 6 }, async () => {
      while (next < entries.length) {
        const index = next++;
        try {
          findings[index] = await (system === "rethink" ? lookupRethink : lookup)(entries[index]);
        } catch (error) {
          if (error && error.notLoggedIn) throw error;
          findings[index] = { ehrBillingEntryId: entries[index].ehrBillingEntryId, error: String(error && error.message || error).slice(0, 200) };
        }
        done += 1;
        const finding = findings[index];
        if (finding.error) failed += 1;
        else if (finding.entry && finding.entry.found) found += 1;
        else missing += 1;
        (finding.groups || []).forEach((group) => authorizationsRead.add(group.settingId));
        show(`read ${done} of ${entries.length} ${system === "rethink" ? "visits" : "billing entries"}, ${authorizationsRead.size} authorizations…`, Math.round((done / entries.length) * 100));
        // Counts only: no ids, names or dates leave this tab for the progress bar.
        onProgress({ done, total: entries.length, found, missing, failed, authorizations: authorizationsRead.size });
      }
    }));
    return { findings, stats: { entries: entries.length, requests, ms: Date.now() - started } };
  }

  const onMessage = async (event) => {
    if (event.source !== portal || event.origin !== portalOrigin) return;
    const data = event.data || {};
    if (data.source !== "tms-portal" || data.type !== "centralreach-worklist") return;
    window.removeEventListener("message", onMessage);
    window.clearTimeout(timer);
    const entries = (Array.isArray(data.entries) ? data.entries : []).slice(0, 3000)
      .map((entry) => ({ ehrBillingEntryId: String(entry && entry.ehrBillingEntryId || ""), dos: String(entry && entry.dos || "").slice(0, 10) }))
      .filter((entry) => /^\d{1,15}$/.test(entry.ehrBillingEntryId) && /^\d{4}-\d{2}-\d{2}$/.test(entry.dos));
    try {
      show(`reading ${entries.length} ${system === "rethink" ? "visits" : "billing entries"}…`);
      const result = await readAll(entries, (counts) => reply({ type: "progress", requestId: data.requestId, ...counts }));
      reply({ type: "result", requestId: data.requestId, findings: result.findings, stats: result.stats });
      show(`done. Go back to the portal tab for the results. Nothing was changed in ${label}.`);
    } catch (error) {
      const message = String(error && error.message || error);
      reply({ type: "error", requestId: data.requestId, message });
      show(message);
    }
  };
  window.addEventListener("message", onMessage);
  reply({ type: "hello", system, liveProgress: true });
}

function authorizationCentralReachBookmarkHref() {
  // Percent-encoded: the program contains "#" and "%", which a javascript: URL
  // would otherwise treat as a fragment marker or an escape.
  return `javascript:${encodeURIComponent(`(${tmsEhrLoaderBookmark.toString()})(${JSON.stringify(window.location.origin)});void(0)`)}`;
}

// THE PERMANENT BOOKMARK (Nathan, 2026-11-02): billers drag this once. It holds
// no EHR logic: it asks the portal tab that opened it for the current check
// program and runs it, so an update never needs a new drag. It accepts the
// program only from that tab at the origin baked in when it was dragged, and the
// portal sends it only to the EHR window it opened. It runs as an inline script
// because a javascript: URL needs the same page permission, so no eval is needed.
function tmsEhrLoaderBookmark(portalOrigin) {
  const portal = window.opener;
  if (!portal || window.location.origin === portalOrigin) {
    window.alert("Drag this TMS EHR check button to your bookmarks bar. Then click Check CentralReach or Check Rethink on the portal, log in on the tab it opens, and click this bookmark there.");
    return;
  }
  const finish = () => { window.removeEventListener("message", onMessage); window.clearTimeout(timer); };
  const timer = window.setTimeout(() => {
    window.removeEventListener("message", onMessage);
    window.alert("The TMS portal did not answer. Log in first, and start from the Check button on the portal's Authorizations page, then click this bookmark on the tab it opens.");
  }, 20000);
  function onMessage(event) {
    const data = event.data || {};
    if (event.source !== portal || event.origin !== portalOrigin || data.source !== "tms-portal" || data.type !== "ehr-check-program" || typeof data.program !== "string") return;
    finish();
    window.__tmsEhrCheckStarted = 0;
    const script = document.createElement("script");
    script.textContent = data.program;
    (document.head || document.documentElement).appendChild(script);
    script.remove();
    if (window.__tmsEhrCheckStarted !== 1) {
      portal.postMessage({ source: "tms-ehr-loader", type: "blocked" }, portalOrigin);
      window.alert("This website blocked the TMS check, and nothing was read. Go back to the portal tab.");
    }
  }
  window.addEventListener("message", onMessage);
  portal.postMessage({ source: "tms-ehr-loader", type: "need-program" }, portalOrigin);
}

// A short fingerprint of the program text, shown on the EHR tab's banner, so
// anyone can see which version ran.
function authorizationEhrCheckProgramVersion(text) {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function authorizationEhrCheckProgramText() {
  const program = tmsCentralReachBookmark.toString();
  return `window.__tmsEhrCheckStarted=1;(${program})(${JSON.stringify(window.location.origin)}, ${JSON.stringify(CENTRAL_REACH_ORIGIN)}, ${JSON.stringify(RETHINK_ORIGIN)}, ${JSON.stringify(authorizationEhrCheckProgramVersion(program))});`;
}

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("message", (event) => {
    const state = authorizationCentralReachState;
    if (event.origin !== ehrCheckSystem().origin || !state.window || event.source !== state.window) return;
    const data = event.data || {};
    if (data.source === "tms-ehr-loader") {
      if (data.type === "need-program") {
        state.window.postMessage({ source: "tms-portal", type: "ehr-check-program", program: authorizationEhrCheckProgramText() }, ehrCheckSystem().origin);
      } else if (data.type === "blocked" && AUTHZ_CENTRAL_REACH_BUSY.includes(state.status)) {
        setAuthorizationCentralReachState("error", `${ehrCheckSystem().label} blocked the TMS check, so nothing was read or recorded.`);
      }
      return;
    }
    if (data.source !== "tms-centralreach-bookmark") return;
    if (data.type === "hello") {
      // A bookmark dragged before 2026-11-02 still works but sends no live count.
      state.oldBookmark = !data.liveProgress;
      if (state.entries) sendAuthorizationCentralReachWorklist();
      else state.helloPending = true;
    } else if (data.type === "progress" && data.requestId === state.requestId && state.status === "reading") {
      noteAuthorizationCentralReachProgress(data);
    } else if (data.type === "result" && data.requestId === state.requestId && state.status === "reading") {
      const findings = Array.isArray(data.findings) ? data.findings : [];
      state.progress = authorizationCentralReachFindingCounts(findings);
      applyAuthorizationCentralReachFindings(findings);
    } else if (data.type === "error" && data.requestId === state.requestId) {
      setAuthorizationCentralReachState("error", String(data.message || `${ehrCheckSystem().label} could not be read.`));
    }
  });
}

// PROGRESS (Nathan, 2026-11-02): how many it is reading, how many it has gone
// through, and the outcome, so anyone running the check can see it working.
function authorizationCentralReachFindingCounts(findings) {
  const counts = { done: findings.length, total: findings.length, found: 0, missing: 0, failed: 0, authorizations: 0 };
  const authorizationsRead = new Set();
  findings.forEach((finding) => {
    if (!finding || finding.error) counts.failed += 1;
    else if (finding.entry && finding.entry.found) counts.found += 1;
    else counts.missing += 1;
    ((finding && finding.groups) || []).forEach((group) => authorizationsRead.add(group.settingId));
  });
  counts.authorizations = authorizationsRead.size;
  return counts;
}

function noteAuthorizationCentralReachProgress(data) {
  const state = authorizationCentralReachState;
  const count = (value) => Math.max(0, Math.floor(Number(value)) || 0);
  const total = (state.entries || []).length;
  state.progress = { done: Math.min(count(data.done), total), total, found: count(data.found), missing: count(data.missing), failed: count(data.failed), authorizations: count(data.authorizations) };
  // In place: a whole panel render per entry would rebuild the bookmark link each time.
  document.querySelectorAll("[data-cr-progress]").forEach((host) => { host.innerHTML = authorizationCentralReachProgressMarkup(); });
}

function authorizationCentralReachFoundFacts(progress) {
  const facts = [`${Formatters.int(progress.found)} found`, `${Formatters.int(progress.missing)} not found`];
  if (progress.failed) facts.push(`${Formatters.int(progress.failed)} could not be read`);
  facts.push(`${Formatters.int(progress.authorizations)} authorization${progress.authorizations === 1 ? "" : "s"} read`);
  return facts.join(" · ");
}

function authorizationCentralReachProgressMarkup() {
  const state = authorizationCentralReachState;
  const noun = state.system === "rethink" ? "visits" : "billing entries";
  const label = ehrCheckSystem().label;
  const total = (state.entries || []).length;
  const working = (text) => `<div class="authz-check-bar is-working" role="progressbar" aria-label="${escapeHtml(text)}"><span></span></div><p class="authz-check-count">${escapeHtml(text)}</p>`;
  if (state.status === "worklist") return working("Finding the lines to check");
  if (state.status === "recording") {
    const progress = state.progress || { done: total, found: 0, missing: 0, failed: 0, authorizations: 0 };
    return `${working(`All ${Formatters.int(progress.done)} ${noun} read. Recording the results`)}<p class="authz-empty-note">${authorizationCentralReachFoundFacts(progress)}</p>`;
  }
  if (state.status === "reading" && state.oldBookmark) return working(`Reading ${Formatters.int(total)} ${noun} in ${label}`);
  const progress = (state.status === "reading" && state.progress) || { done: 0, total, found: 0, missing: 0, failed: 0, authorizations: 0 };
  const percent = total ? Math.round((progress.done / total) * 100) : 0;
  return `<div class="authz-check-bar" role="progressbar" aria-label="${escapeHtml(`${noun} read in ${label}`)}" aria-valuemin="0" aria-valuemax="${total}" aria-valuenow="${progress.done}"><span style="width:${percent}%"></span></div>
    <p class="authz-check-count"><strong>${Formatters.int(progress.done)}</strong> of <strong>${Formatters.int(total)}</strong> ${noun} read · ${percent}%</p>
    <p class="authz-empty-note">${authorizationCentralReachFoundFacts(progress)}</p>`;
}

function authorizationCentralReachOutcomeMarkup({ recorded, released, alreadyRecorded, held, authorizations }) {
  const stillHeld = Formatters.int(held);
  const lines = recorded + released + alreadyRecorded + held;
  const share = (value) => (lines ? (value / lines) * 100 : 0).toFixed(2);
  const tile = (value, text, tone = "") => `<div class="authz-check-tile${tone ? ` is-${tone}` : ""}"><strong>${Formatters.int(value)}</strong><span>${escapeHtml(text)}</span></div>`;
  const progress = authorizationCentralReachState.progress;
  return `<div class="authz-check-outcome">
    <div class="authz-check-stack" role="img" aria-label="${Formatters.int(recorded + released)} of ${Formatters.int(lines)} lines ready to bill, ${Formatters.int(alreadyRecorded)} already recorded, ${stillHeld} still on hold">
      <span class="is-ready" style="width:${share(recorded + released)}%"></span><span class="is-already" style="width:${share(alreadyRecorded)}%"></span><span class="is-held" style="width:${share(held)}%"></span>
    </div>
    <div class="authz-check-tiles">
      ${tile(lines, "Lines checked")}
      ${tile(recorded, "Lines recorded", "ready")}
      ${released ? tile(released, "CPH lines released", "ready") : ""}
      ${tile(alreadyRecorded, "Already recorded", "already")}
      ${tile(held, "Still on hold", "held")}
      ${tile(authorizations, "Authorizations used")}
    </div>
    ${progress ? `<p class="authz-empty-note">Read ${Formatters.int(progress.done)} of ${Formatters.int(progress.total)} ${authorizationCentralReachState.system === "rethink" ? "visits" : "billing entries"}: ${authorizationCentralReachFoundFacts(progress)}.</p>` : ""}
  </div>`;
}

function setAuthorizationCentralReachState(status, message, result = null) {
  Object.assign(authorizationCentralReachState, { status, message, result });
  renderAuthorizationCentralReachPanel();
}

function authorizationCentralReachOrgId() {
  const selected = activeAuthorizationOrgId();
  if (selected) return selected;
  const options = authorizationOrgOptions();
  return options.length === 1 ? String(options[0].value || "") : "";
}

async function authorizationCentralReachPost(route, body) {
  const response = await fetch(`${authorizationsApiBase()}/api/authorizations/centralreach/${route}`, {
    method: "POST",
    headers: authorizationsApiHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ ...body, system: authorizationCentralReachState.system })
  });
  const payload = await response.json().catch(() => ({}));
  if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
  if (!response.ok) throw new Error(String(payload.message || payload.error || `The ${ehrCheckSystem().label} check failed (HTTP ${response.status}).`));
  return payload;
}

function sendAuthorizationCentralReachWorklist() {
  const state = authorizationCentralReachState;
  if (!state.window || state.window.closed || !state.entries) return;
  state.helloPending = false;
  state.window.postMessage({ source: "tms-portal", type: "centralreach-worklist", requestId: state.requestId, entries: state.entries }, ehrCheckSystem().origin);
  setAuthorizationCentralReachState("reading", `${ehrCheckSystem().label} is reading ${Formatters.int(state.entries.length)} ${state.system === "rethink" ? "visits" : "billing entries"}…`);
}

async function runCentralReachAuthorizationCheck({ source = "authorizations", system = "centralreach" } = {}) {
  const state = authorizationCentralReachState;
  if (AUTHZ_CENTRAL_REACH_BUSY.includes(state.status)) return;
  const ehr = ehrCheckSystem(system);
  if (!hasPermission(PERMISSIONS.AUTHORIZATIONS_MANAGE)) return denyAction(PERMISSIONS.AUTHORIZATIONS_MANAGE, `check ${ehr.label}`);
  state.source = source;
  state.system = ehr.key;
  const orgId = source === "credentialing" ? activeCredentialingOrgId() : authorizationCentralReachOrgId();
  if (!orgId) {
    return setAuthorizationCentralReachState("error", source === "credentialing"
      ? `Choose one company with the company switcher at the top first, then click Check ${ehr.label}.`
      : `Choose one company in the Company filter first, then click Check ${ehr.label}.`);
  }
  // Opened synchronously inside the click so the browser does not block the pop-up.
  const opened = window.open(ehr.start, "tmsEhrCheck");
  if (!opened) return setAuthorizationCentralReachState("error", `The browser blocked the ${ehr.label} tab. Allow pop-ups for this portal and click Check ${ehr.label} again.`);
  Object.assign(state, { window: opened, orgId, entries: null, helloPending: false, progress: null, oldBookmark: false, requestId: `cr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` });
  try {
    setAuthorizationCentralReachState("worklist", "Finding on-hold placeholder lines. This can take a minute or two…");
    const worklist = await authorizationCentralReachPost("worklist", { orgId });
    if (!worklist.entries.length) {
      return setAuthorizationCentralReachState("done", "", { counts: {}, records: [], outcomes: [], nothingToCheck: true });
    }
    state.entries = worklist.entries;
    setAuthorizationCentralReachState("waiting", `${ehr.label} opened in a new tab. Log in there, then click your "TMS EHR check" bookmark (an older "TMS CentralReach check" bookmark still works for CentralReach only). ${Formatters.int(worklist.entries.length)} ${ehr.key === "rethink" ? "visits" : "billing entries"} are waiting to be checked (authorization placeholders${Number(worklist.credentialingLines) ? ` and ${Formatters.int(worklist.credentialingLines)} CPH lines` : ""}).`);
    if (state.helloPending) sendAuthorizationCentralReachWorklist();
  } catch (error) {
    setAuthorizationCentralReachState("error", String(error && error.message || error));
  }
}

async function applyAuthorizationCentralReachFindings(findings) {
  const state = authorizationCentralReachState;
  try {
    setAuthorizationCentralReachState("recording", "Matching date of service, CPT and insurance, then recording what matched…");
    const result = await authorizationCentralReachPost("apply", { orgId: state.orgId, findings });
    setAuthorizationCentralReachState("done", "", result);
    // One run changes both boards and both bill-out queues: refresh them all, so
    // nobody has to reload the portal to see what it just found (Nathan, 2026-11-02).
    requestAuthorizationBoard();
    requestAuthorizationSendQueue({ rerender: false });
    if (typeof requestCredentialingBoard === "function") requestCredentialingBoard({ preserveBoard: true });
    if (typeof canSendCredentialedClaims === "function" && canSendCredentialedClaims()) requestCredentialingQueue({ rerender: false });
  } catch (error) {
    setAuthorizationCentralReachState("error", String(error && error.message || error));
  }
}

// ONE CHECK, TWO DOORS (Nathan, 2026-10-29): the same button and results panel
// sit on Authorizations and on Credentialing, because one run checks both the
// authorization placeholders and the CPH lines. Markers, not ids, because both
// views can be in the page at once; the run state is shared.
function centralReachCheckButtonsMarkup() {
  if (!hasPermission(PERMISSIONS.AUTHORIZATIONS_MANAGE)) return "";
  const busy = AUTHZ_CENTRAL_REACH_BUSY.includes(authorizationCentralReachState.status);
  // TWO BUTTONS, ONE BOOKMARK (Nathan, 2026-10-30): pick the client's EHR.
  return Object.values(EHR_CHECK_SYSTEMS).map((ehr) => `<button data-cr-check="${ehr.key}" class="primary-button" type="button" title="Check ${ehr.label} (read-only) for authorization placeholders and CPH lines; records or releases the lines whose EHR entry, date of service, CPT and insurance match" ${busy ? "disabled" : ""}>${busy && authorizationCentralReachState.system === ehr.key ? `Checking ${ehr.label}…` : `Check ${ehr.label}`}</button>`).join("")
    + `<button data-cr-setup class="authz-link-button" type="button">Bookmark setup</button>`;
}

function bindCentralReachCheckButtons(host, source) {
  if (!host) return;
  host.querySelectorAll("[data-cr-check]").forEach((button) => button.addEventListener("click", () => runCentralReachAuthorizationCheck({ source, system: button.dataset.crCheck || "centralreach" })));
  host.querySelectorAll("[data-cr-setup]").forEach((button) => button.addEventListener("click", () => {
    authorizationCentralReachState.source = source;
    if (!AUTHZ_CENTRAL_REACH_BUSY.includes(authorizationCentralReachState.status)) setAuthorizationCentralReachState("setup", "");
  }));
  bindAuthorizationCentralReachPanel(host);
}

function bindAuthorizationCentralReachPanel(root = document) {
  bindAuthorizationWaitingReportButtons(root);
  // Idempotent: a panel re-bound by a view render and by a panel render must not
  // fire its handlers twice.
  root.querySelectorAll("[data-cr-dismiss]").forEach((button) => {
    if (button.dataset.crBound) return;
    button.dataset.crBound = "1";
    button.addEventListener("click", () => {
      Object.assign(authorizationCentralReachState, { status: "idle", message: "", result: null, entries: null, progress: null });
      renderAuthorizationCentralReachPanel();
    });
  });
  root.querySelectorAll("[data-cr-bookmark]").forEach((link) => {
    if (link.dataset.crBound) return;
    link.dataset.crBound = "1";
    link.addEventListener("click", (event) => {
      event.preventDefault();
      window.alert("Drag this button to your bookmarks bar (press Ctrl+Shift+B if the bar is hidden). Then click it on the CentralReach tab after you log in.");
    });
  });
}

function renderAuthorizationCentralReachPanel() {
  const hosts = Array.from(document.querySelectorAll("[data-cr-panel]"));
  if (!hosts.length) {
    if (typeof renderAuthorizations === "function") renderAuthorizations();
    return;
  }
  const markup = authorizationCentralReachMarkup();
  hosts.forEach((host) => { host.outerHTML = markup; });
  bindAuthorizationCentralReachPanel();
  const busy = AUTHZ_CENTRAL_REACH_BUSY.includes(authorizationCentralReachState.status);
  document.querySelectorAll("[data-cr-check]").forEach((button) => {
    const ehr = ehrCheckSystem(button.dataset.crCheck || "centralreach");
    button.disabled = busy;
    button.textContent = busy && authorizationCentralReachState.system === ehr.key ? `Checking ${ehr.label}…` : `Check ${ehr.label}`;
  });
}

function authorizationCentralReachBookmarkMarkup() {
  return `<p class="authz-empty-note">First time? Drag this button to your bookmarks bar (Ctrl+Shift+B shows the bar): <a data-cr-bookmark class="ghost-button" href="${escapeHtml(authorizationCentralReachBookmarkHref())}" draggable="true">TMS EHR check</a> It works on both CentralReach and Rethink and updates itself, so this is the last time you need to drag it. Delete any older TMS bookmark.</p>`;
}

function authorizationCentralReachMarkup() {
  const state = authorizationCentralReachState;
  if (state.status === "idle") return `<div data-cr-panel hidden></div>`;
  const dismiss = `<button data-cr-dismiss class="authz-link-button" type="button">Dismiss</button>`;
  if (state.status === "setup") {
    return `<section data-cr-panel class="panel" role="status">
      <h3>Set up the EHR check (CentralReach and Rethink)</h3>
      ${authorizationCentralReachBookmarkMarkup()}
      <p class="authz-empty-note">Then click Check CentralReach or Check Rethink, whichever the client uses. It opens that system; log in and click the bookmark there. It only reads and never changes anything.</p>
      ${dismiss}
    </section>`;
  }
  if (state.status === "error") {
    return `<section data-cr-panel class="panel authz-error-panel" role="alert">
      <h3>${escapeHtml(ehrCheckSystem().label)} check stopped</h3>
      <div class="authz-empty"><p class="authz-empty-note">${escapeHtml(state.message)}</p>${dismiss}</div>
    </section>`;
  }
  if (state.status !== "done") {
    return `<section data-cr-panel class="panel" role="status" aria-live="polite">
      <h3>Checking ${escapeHtml(ehrCheckSystem().label)}</h3>
      <p class="authz-empty-note">${escapeHtml(state.message)}</p>
      <div data-cr-progress class="authz-check-progress" aria-live="off">${authorizationCentralReachProgressMarkup()}</div>
      ${state.status === "reading" && state.oldBookmark ? `<p class="authz-empty-note">It is working, but this bookmark is an older copy, so it cannot show a live count. Drag the new one to your bookmarks bar for next time; it updates itself from then on.</p>` : ""}
      ${state.status === "waiting" || (state.status === "reading" && state.oldBookmark) ? authorizationCentralReachBookmarkMarkup() : ""}
      <p class="authz-empty-note">Read-only: nothing is changed in ${escapeHtml(ehrCheckSystem().label)}.</p>
      ${dismiss}
    </section>`;
  }
  const result = state.result || {};
  if (result.nothingToCheck) {
    return `<section data-cr-panel class="panel" role="status">
      <h3>${escapeHtml(ehrCheckSystem().label)} check</h3>
      <p class="authz-empty-note">No open authorization-placeholder or CPH lines with an EHR Billing Entry ID to check for this company.</p>${dismiss}
    </section>`;
  }
  const outcomes = Array.isArray(result.outcomes) ? result.outcomes : [];
  const recorded = outcomes.filter((entry) => entry.outcome === "matched");
  const authorizations = (result.records || []).filter((entry) => entry.ok).length;
  const held = outcomes.filter((entry) => !["matched", "already_recorded"].includes(entry.outcome));
  // Lines that already had an authorization are skipped, not re-checked. Say so,
  // or a run with nothing new reads as "0 recorded" and looks like a failure.
  const alreadyRecorded = outcomes.filter((entry) => entry.outcome === "already_recorded").length;
  const reasonRowsFor = (entriesHeld) => {
    const byReason = new Map();
    entriesHeld.forEach((entry) => {
      if (!byReason.has(entry.label)) byReason.set(entry.label, []);
      byReason.get(entry.label).push(entry);
    });
    return Array.from(byReason.entries()).sort((a, b) => b[1].length - a[1].length).map(([label, entries]) => `
    <details class="authz-centralreach-reason">
      <summary><strong>${Formatters.int(entries.length)}</strong> · ${escapeHtml(label)}</summary>
      <ul>${entries.map((entry) => `<li>${escapeHtml(entry.patientLabel || "")}${entry.providerLabel ? ` · ${escapeHtml(entry.providerLabel)}` : ""} · ${escapeHtml(entry.dos || "")} · ${escapeHtml(entry.cpt || "")}${entry.centralReachPayer ? ` · CentralReach: ${escapeHtml(entry.centralReachPayer)}` : ""}</li>`).join("")}</ul>
    </details>`).join("");
  };
  const reasonRows = reasonRowsFor(held);
  // CPH (credentialing hold) lines checked in the same click.
  const credentialing = result.credentialing || {};
  const cphOutcomes = Array.isArray(credentialing.outcomes) ? credentialing.outcomes : [];
  const cphReleased = cphOutcomes.filter((entry) => entry.outcome === "credentialing_hold_released");
  const cphHeld = cphOutcomes.filter((entry) => entry.outcome !== "credentialing_hold_released");
  const cphSection = cphOutcomes.length || credentialing.note ? `
    <h4>Credentialing holds (CPH)</h4>
    ${cphOutcomes.length ? `<p class="authz-empty-note"><strong>${Formatters.int(cphReleased.length)}</strong> CPH line${cphReleased.length === 1 ? "" : "s"} released to bill (CentralReach no longer shows CPH or HCF; date of service, CPT and insurance matched). Only those lines were released; no provider was marked credentialed. <strong>${Formatters.int(cphHeld.length)}</strong> still parked.</p>${reasonRowsFor(cphHeld)}` : ""}
    ${credentialing.note ? `<p class="authz-empty-note">${escapeHtml(credentialing.note)}</p>` : ""}` : "";
  return `<section data-cr-panel class="panel" role="status">
    <h3>${escapeHtml(ehrCheckSystem().label)} check finished</h3>
    ${authorizationCentralReachOutcomeMarkup({ recorded: recorded.length, released: cphReleased.length, alreadyRecorded, held: held.length + cphHeld.length, authorizations })}
    <h4>Authorizations</h4>
    <p class="authz-empty-note"><strong>${Formatters.int(recorded.length)}</strong> line${recorded.length === 1 ? "" : "s"} recorded as Authorization Approved or NAR (ready to bill) across <strong>${Formatters.int(authorizations)}</strong> authorization${authorizations === 1 ? "" : "s"} (date of service, CPT and insurance all matched). <strong>${Formatters.int(held.length)}</strong> still on hold.${alreadyRecorded ? ` <strong>${Formatters.int(alreadyRecorded)}</strong> line${alreadyRecorded === 1 ? " already had its" : "s already had their"} authorization recorded and ${alreadyRecorded === 1 ? "was" : "were"} skipped.` : ""}</p>
    ${reasonRows}
    ${cphSection}
    ${result.waitingReport ? authorizationWaitingReportMarkup(result.waitingReport) : ""}
    <p class="authz-empty-note">Read-only: nothing was changed in ${escapeHtml(ehrCheckSystem().label)}. Checked ${escapeHtml(String(result.checkedAtUtc || "").slice(0, 16).replace("T", " "))} UTC.</p>
    ${dismiss}
  </section>`;
}

// ---------------------------------------------------------------------------
// WAITING ON THE CLIENT (Nathan, 2026-10-30): after every CentralReach check,
// and from the "Waiting on client" button, the patients and dates of service
// still waiting on a PRIMARY and on a SECONDARY authorization, plus a ready
// email the biller copies and sends from their own inbox so the client can reply
// (the portal sends nothing). backend/authorizationWaitingReport.js builds both.
// ---------------------------------------------------------------------------
const authorizationWaitingReportState = { status: "idle", report: null, message: "", orgId: "" };

function authzWaitingDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${Number(match[2])}/${Number(match[3])}/${match[1]}` : String(value || "");
}

function authorizationWaitingLaneMarkup(title, lane = {}) {
  const patients = Array.isArray(lane.patients) ? lane.patients : [];
  if (!patients.length) return `<h5>${escapeHtml(title)}</h5><p class="authz-empty-note">Nothing waiting.</p>`;
  const patientCount = Number(lane.patientCount) || patients.length;
  const dateCount = Number(lane.dateCount) || 0;
  const rows = patients.flatMap((patient) => (patient.payers || []).map((payer, index) => `<tr>
      <td>${index === 0 ? escapeHtml(patient.patient) : ""}</td>
      <td>${escapeHtml(payer.payer)}</td>
      <td>${escapeHtml((payer.dates || []).map(authzWaitingDate).join(", ") || "Date not available")}</td>
    </tr>`)).join("");
  return `<h5>${escapeHtml(title)} · ${Formatters.int(patientCount)} client${patientCount === 1 ? "" : "s"} · ${Formatters.int(dateCount)} date${dateCount === 1 ? "" : "s"} of service</h5>
    <div class="authz-waiting-table-wrap"><table class="authz-waiting-table">
      <thead><tr><th>Client</th><th>Insurance</th><th>Dates of service</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>`;
}

function authorizationWaitingReportMarkup(report = {}, { standalone = false } = {}) {
  const email = report.email || {};
  const body = report.waiting
    ? `${authorizationWaitingLaneMarkup("Primary insurance authorization needed", report.primary)}
      ${authorizationWaitingLaneMarkup("Secondary insurance authorization needed", report.secondary)}`
    : `<p class="authz-empty-note">Nothing is waiting on a primary or a secondary authorization for ${escapeHtml(report.orgLabel || "this company")}.</p>`;
  return `<div class="authz-waiting-report" data-waiting-report-body>
    ${standalone ? "" : `<h4>Still waiting on the client${report.orgLabel ? ` · ${escapeHtml(report.orgLabel)}` : ""}</h4>`}
    ${body}
    ${report.waiting ? `<p class="authz-waiting-actions"><button class="primary-button" type="button" data-waiting-email>Email to client</button><button class="ghost-button" type="button" data-waiting-print>Print</button></p>
    <dialog class="authz-waiting-email" data-waiting-email-dialog aria-label="Email to client">
      <h3>Email to client</h3>
      <p class="authz-empty-note">Copy this into your own email and send it, so the client can reply to you. The portal does not send it.</p>
      <label>Subject <input type="text" readonly data-waiting-email-subject value="${escapeHtml(email.subject || "")}"></label>
      <button class="ghost-button" type="button" data-waiting-copy="subject">Copy subject</button>
      <label>Message <textarea readonly rows="14" data-waiting-email-body>${escapeHtml(email.body || "")}</textarea></label>
      <p class="authz-waiting-actions"><button class="primary-button" type="button" data-waiting-copy="body">Copy message</button><button class="ghost-button" type="button" data-waiting-email-close>Close</button></p>
    </dialog>` : ""}
  </div>`;
}

function bindAuthorizationWaitingReportButtons(root = document) {
  root.querySelectorAll("[data-waiting-email]").forEach((button) => {
    if (button.dataset.waitingBound) return;
    button.dataset.waitingBound = "1";
    button.addEventListener("click", () => {
      const dialog = button.closest("[data-waiting-report-body]")?.querySelector("[data-waiting-email-dialog]");
      if (dialog && typeof dialog.showModal === "function") dialog.showModal();
    });
  });
  root.querySelectorAll("[data-waiting-email-close]").forEach((button) => {
    if (button.dataset.waitingBound) return;
    button.dataset.waitingBound = "1";
    button.addEventListener("click", () => button.closest("dialog")?.close());
  });
  root.querySelectorAll("[data-waiting-copy]").forEach((button) => {
    if (button.dataset.waitingBound) return;
    button.dataset.waitingBound = "1";
    button.addEventListener("click", async () => {
      const field = button.closest("dialog")?.querySelector(button.dataset.waitingCopy === "subject" ? "[data-waiting-email-subject]" : "[data-waiting-email-body]");
      const textValue = field ? field.value : "";
      try { await navigator.clipboard.writeText(textValue); }
      catch (_) { field?.select(); document.execCommand?.("copy"); }
      button.textContent = "Copied";
    });
  });
  root.querySelectorAll("[data-waiting-print]").forEach((button) => {
    if (button.dataset.waitingBound) return;
    button.dataset.waitingBound = "1";
    button.addEventListener("click", () => {
      const source = button.closest("[data-waiting-report-body]")?.cloneNode(true);
      source?.querySelectorAll("dialog").forEach((node) => node.remove());
      const html = source?.innerHTML || "";
      const popup = window.open("", "_blank");
      if (!popup) return;
      popup.document.write(`<!doctype html><title>Authorizations we are still waiting on</title><style>body{font:14px system-ui,sans-serif;margin:24px}table{border-collapse:collapse;width:100%;margin-bottom:16px}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;vertical-align:top}button,textarea{display:none}</style>${html}`);
      popup.document.close();
      popup.focus();
      popup.print();
    });
  });
  root.querySelectorAll("[data-waiting-dismiss]").forEach((button) => {
    if (button.dataset.waitingBound) return;
    button.dataset.waitingBound = "1";
    button.addEventListener("click", () => {
      Object.assign(authorizationWaitingReportState, { status: "idle", report: null, message: "" });
      renderAuthorizations();
    });
  });
}

function authorizationWaitingReportButtonMarkup() {
  if (!hasPermission(PERMISSIONS.AUTHORIZATIONS_VIEW)) return "";
  const busy = authorizationWaitingReportState.status === "loading";
  return `<button data-waiting-open class="ghost-button" type="button" title="Clients and dates of service still waiting on a primary or a secondary authorization" ${busy ? "disabled" : ""}>${busy ? "Loading…" : "Waiting on client"}</button>`;
}

function authorizationWaitingReportPanelMarkup() {
  const state = authorizationWaitingReportState;
  if (state.status === "idle") return "";
  const dismiss = `<button data-waiting-dismiss class="authz-link-button" type="button">Close</button>`;
  if (state.status === "loading") return `<section class="panel" role="status" aria-live="polite"><h3>Waiting on client</h3><p class="authz-empty-note">Loading the list…</p></section>`;
  if (state.status === "error") return `<section class="panel authz-error-panel" role="alert"><h3>Waiting on client</h3><p class="authz-empty-note">${escapeHtml(state.message)}</p>${dismiss}</section>`;
  const report = state.report || {};
  return `<section class="panel" role="status">
    <h3>Authorizations we are still waiting on${report.orgLabel ? ` · ${escapeHtml(report.orgLabel)}` : ""}</h3>
    ${authorizationWaitingReportMarkup(report, { standalone: true })}
    <p class="authz-empty-note">Record an authorization on the patient's card below, or enter it in CentralReach and the next check picks it up.</p>
    ${dismiss}
  </section>`;
}

async function openAuthorizationWaitingReport() {
  const orgId = authorizationCentralReachOrgId();
  if (!orgId) {
    Object.assign(authorizationWaitingReportState, { status: "error", report: null, message: "Choose one company in the Company filter first, then click Waiting on client." });
    renderAuthorizations();
    return;
  }
  Object.assign(authorizationWaitingReportState, { status: "loading", report: null, message: "", orgId });
  renderAuthorizations();
  try {
    const response = await fetch(`${authorizationsApiBase()}/api/authorizations/waiting-report?orgId=${encodeURIComponent(orgId)}`, { headers: authorizationsApiHeaders() });
    const payload = await response.json().catch(() => ({}));
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok) throw new Error(String(payload.message || payload.error || `The list could not load (HTTP ${response.status}).`));
    Object.assign(authorizationWaitingReportState, { status: "done", report: payload.waitingReport || null, message: "" });
  } catch (error) {
    Object.assign(authorizationWaitingReportState, { status: "error", report: null, message: String(error?.message || "The list could not load.") });
  }
  renderAuthorizations();
}

function bindAuthorizationWaitingReport(host) {
  if (!host) return;
  host.querySelectorAll("[data-waiting-open]").forEach((button) => button.addEventListener("click", () => openAuthorizationWaitingReport()));
  bindAuthorizationWaitingReportButtons(host);
  // The client email links to ?view=authorizations&report=waiting: open the list
  // once the board has loaded far enough to know the company.
  // Someone with several companies is asked to pick one; the list opens then.
  if (!window.__authzWaitingReportAutoOpen || !["idle", "error"].includes(authorizationWaitingReportState.status)) return;
  if (authorizationCentralReachOrgId()) {
    window.__authzWaitingReportAutoOpen = false;
    openAuthorizationWaitingReport();
  } else if (authorizationWaitingReportState.status === "idle" && authorizationOrgOptions().length) {
    Object.assign(authorizationWaitingReportState, { status: "error", report: null, message: "Choose your company in the Company filter to see the authorizations we are still waiting on." });
    renderAuthorizations();
  }
}

function authorizationsCompletenessMarkup() {
  if (authorizationsState.authorizationFieldUnavailable || authorizationsState.claimStatusUnavailable) {
    return `<section class="panel authz-error-panel" role="alert">
      <h3>Authorization source unavailable</h3>
      <div class="authz-empty">
        <p class="authz-empty-note">The source could not provide ${authorizationsState.authorizationFieldUnavailable ? "the authorization marker" : "claim status"}. This is not a confirmed empty queue.</p>
        <button id="authzRetryBtn" class="primary-button" type="button">Try again</button>
      </div>
    </section>`;
  }
  if (authorizationsState.truncated) {
    return `<section class="panel authz-error-panel" role="alert">
      <h3>Authorization results are incomplete</h3>
      <div class="authz-empty"><p class="authz-empty-note">The row limit was reached. Totals and the empty state are withheld until the full queue can be loaded.</p></div>
    </section>`;
  }
  if (authorizationsState.refreshHeldOrgs.length) {
    return `<section class="panel authz-error-panel" role="status">
      <h3>Some companies are temporarily withheld</h3>
      <div class="authz-empty"><p class="authz-empty-note">${Formatters.int(authorizationsState.refreshHeldOrgs.length)} compan${authorizationsState.refreshHeldOrgs.length === 1 ? "y is" : "ies are"} waiting for a source refresh. Current totals exclude that work.</p></div>
    </section>`;
  }
  return "";
}

function authorizationsHeaderMarkup() {
  const summary = authorizationsState.board?.summary || {};
  const visible = authorizationVisiblePatients();
  const orgOptions = authorizationOrgOptions();
  const payerOptions = authorizationPayerOptions();
  const actionable = authorizationVisiblePatients({ ignoreStatus: true, ignorePatient: true });
  const statusCounts = authorizationsState.board?.facets?.statusCounts || authorizationStatusOptionsWithCounts(actionable);
  const loading = authorizationsState.status === "loading";
  const freshness = authorizationsState.generatedAtUtc
    ? `Data as of ${escapeHtml(String(authorizationsState.generatedAtUtc).slice(0, 16).replace("T", " "))} UTC`
    : "Data freshness unavailable";
  const filtersActive = Boolean(authorizationsState.search || authorizationsState.patientFilter) || authorizationsState.statusFilter !== "All" || authorizationsState.payerFilter !== "All" || authorizationsState.sortOrder !== "name" || authorizationsState.includeArchived;
  const headerNeedsCount = authzNeedsLineCount(summary);
  const headerPricedCount = authzNeedsPricedCount(summary);
  const headerAllUnknown = authzExpectedAllUnknown(summary);
  const sourceIncomplete = authorizationsState.authorizationFieldUnavailable || authorizationsState.claimStatusUnavailable || authorizationsState.truncated;
  return `
    <section class="panel authz-header-panel">
      <div class="authz-header">
        <div class="authz-header-identity">
          <h3>${escapeHtml(authorizationBoardTitle())}</h3>
          <p class="authz-header-counts">
            ${sourceIncomplete ? `<strong>Counts unavailable</strong>` : `<strong>${Formatters.int(summary.patientCount || 0)}</strong> patients ·
            <strong>${Formatters.int(authzNeedsLineCount(summary))}</strong> lines needing authorization${isRedactedClientRole() ? "" : (headerAllUnknown
              ? ` · ${authzUnknownExpectedHtml("expected value not yet known")}`
              : `${headerPricedCount < headerNeedsCount ? ` · <strong>${Formatters.int(headerPricedCount)}</strong> priced` : ""} · <strong>${escapeHtml(authzMoney(authzNeedsExpectedTotal(summary)))}</strong> expected`)}`}
          </p>
          ${!isRedactedClientRole() && !sourceIncomplete && headerAllUnknown
            ? `<p class="authz-header-rates-note">No rates have loaded yet, so there is no expected value to show. The line counts are complete — only the money is missing.</p>`
            : (!sourceIncomplete && headerPricedCount < headerNeedsCount ? `<p class="authz-header-rates-note">Rates are pending on ${Formatters.int(headerNeedsCount - headerPricedCount)} line${headerNeedsCount - headerPricedCount === 1 ? "" : "s"}, so the expected total is a floor.</p>` : "")}
          <p class="authz-header-freshness">${freshness}${loading ? " · refreshing…" : ""}</p>
        </div>
        <div class="authz-header-actions">
          <label class="authz-search">
            <span class="authz-search-icon" aria-hidden="true">⌕</span>
            <input id="authzSearchInput" type="search" placeholder="Search patient…" autocomplete="off" value="${escapeHtml(authorizationsState.search)}" aria-label="Search patients">
          </label>
          ${centralReachCheckButtonsMarkup()}
          ${authorizationWaitingReportButtonMarkup()}
          <button id="authzRefreshBtn" class="ghost-button" type="button" ${loading ? "disabled" : ""}>${loading ? "Refreshing…" : "Refresh"}</button>
        </div>
      </div>
      <div class="authz-filter-row">
        ${orgOptions.length > 1 ? `
          <label class="authz-filter">
            <span>Company</span>
            <select id="authzOrgSelect" ${activeClientGroupTenant() ? `disabled title="Narrowed to ${escapeHtml(activeClientGroupLabel())} by the client switcher in the header."` : ""}>
              <option value="" ${activeAuthorizationOrgId() ? "" : "selected"}>All companies</option>
              ${orgOptions.map((option) => `<option value="${escapeHtml(option.value)}" ${option.value === activeAuthorizationOrgId() ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
            </select>
          </label>` : ""}
        ${authorizationDirectoryControlsMarkup()}
        <label class="authz-filter">
          <span>Status</span>
          <select id="authzStatusFilter">
            <option value="All" ${authorizationsState.statusFilter === "All" ? "selected" : ""}>All statuses</option>
            ${statusCounts.map((entry) => `<option value="${escapeHtml(entry.status)}" ${authorizationsState.statusFilter === entry.status ? "selected" : ""}>${escapeHtml(AUTHZ_STATUS_PILL_LABEL[entry.status] || entry.status)} (${entry.count})</option>`).join("")}
          </select>
        </label>
        <label class="authz-filter">
          <span>Payer</span>
          <select id="authzPayerFilter">
            <option value="All" ${authorizationsState.payerFilter === "All" ? "selected" : ""}>All payers</option>
            ${payerOptions.map((option) => `<option value="${escapeHtml(option.value)}" ${authorizationsState.payerFilter === option.value ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
          </select>
        </label>
        <label class="authz-toggle">
          <input id="authzArchivedToggle" type="checkbox" ${authorizationsState.includeArchived ? "checked" : ""}>
          <span>Show archived</span>
        </label>
        <div class="authz-filter-spacer"></div>
        ${filtersActive ? `<button id="authzClearFilters" class="authz-link-button" type="button">Clear filters (${authorizationsState.board?.page?.totalRows ?? visible.length} shown)</button>` : ""}
        <span class="authz-filter-links"><button id="authzExpandAll" class="authz-link-button" type="button">Expand all</button><button id="authzCollapseAll" class="authz-link-button" type="button">Collapse all</button></span>
      </div>
    </section>
  `;
}

// R3 "never dead-end": after a save the board keeps the batch moving.
function authorizationsSaveStripMarkup() {
  const last = authorizationsState.lastSave;
  if (!last) return "";
  const next = authorizationNextPatientNeedingAuth(`${String(last.orgId || "")}||${String(last.patientKey || "")}`);
  return `
    <div class="authz-save-strip" role="status">
      <span class="authz-save-strip-message">${escapeHtml(last.message || "Saved.")}</span>
      ${next ? `<button class="primary-button authz-next-btn" type="button" id="authzNextPatientBtn">Next patient needing auth →</button>` : (Number(authorizationsState.board?.page?.totalRows) > 0 ? `<span class="authz-save-strip-done">Use the page controls to continue reviewing patients.</span>` : `<span class="authz-save-strip-done">Nothing else is waiting on an authorization. 🎉</span>`)}
      <button class="authz-link-button" type="button" id="authzDismissSaveStrip">Dismiss</button>
    </div>`;
}

function authorizationNextPatientNeedingAuth(afterCardKey = "") {
  const candidates = authorizationVisiblePatients({ ignorePatient: true }).filter(authorizationPatientNeedsAuth);
  if (!candidates.length) return null;
  const skip = String(afterCardKey || "");
  return candidates.find((patient) => authorizationPatientCardKey(patient) !== skip) || null;
}

function authorizationsHiddenFilterBook() {
  if (authorizationsState.board?.page && Number(authorizationsState.board.page.totalRows) === 0 && Number(authorizationsState.board.facets?.activePatientCount) > 0) {
    const summary = authorizationsState.board.summary || {};
    return { patients: Number(authorizationsState.board.facets.activePatientCount), lines: authzNeedsLineCount(summary), expected: authzNeedsExpectedTotal(summary), unknown: authzNeedsUnknownCount(summary) };
  }
  const orgFilter = typeof activeAuthorizationOrgId === "function" ? activeAuthorizationOrgId() : "";
  const all = authorizationPatients().filter((patient) => {
    if (authorizationPatientIsResolved(patient)) return false;
    if (orgFilter && String(patient.orgId || "") && String(patient.orgId) !== orgFilter) return false;
    return true;
  });
  if (!all.length) return null;
  if (authorizationVisiblePatients().length) return null;
  return {
    patients: all.length,
    lines: all.reduce((sum, patient) => sum + authzNeedsLineCount(patient), 0),
    expected: all.reduce((sum, patient) => sum + authzNeedsExpectedTotal(patient), 0),
    unknown: all.reduce((sum, patient) => sum + authzNeedsUnknownCount(patient), 0)
  };
}

function authorizationsBoardMarkup() {
  if (authorizationsState.status === "loading" && !authorizationPatients().length) {
    return authorizationsSkeletonMarkup();
  }
  if (authorizationsState.status === "error") {
    return `
      <section class="panel authz-error-panel">
        <h3>Authorizations could not load</h3>
        <div class="authz-empty">
          <p class="authz-empty-note">${escapeHtml(authorizationsState.error || "Something went wrong.")}</p>
          <button id="authzRetryBtn" class="primary-button" type="button">Try again</button>
        </div>
      </section>`;
  }
  if (authorizationsState.authorizationFieldUnavailable || authorizationsState.claimStatusUnavailable || authorizationsState.truncated) return "";
  const visible = authorizationVisiblePatients();
  if (!visible.length) {
    const filtersActive = authorizationsState.search || authorizationsState.patientFilter || authorizationsState.statusFilter !== "All" || authorizationsState.payerFilter !== "All";
    if (filtersActive) {
      const hidden = authorizationsHiddenFilterBook();
      let note = "";
      if (isRedactedClientRole() && hidden && hidden.lines > 0) {
        note = `Filters are hiding ${Formatters.int(hidden.lines)} line${hidden.lines === 1 ? "" : "s"} needing authorization.`;
      } else if (hidden && hidden.lines > 0 && hidden.unknown >= hidden.lines) {
        note = `Filters are hiding ${Formatters.int(hidden.lines)} line${hidden.lines === 1 ? "" : "s"} needing auth. Expected is not yet known — this is not $0.`;
      } else if (hidden && hidden.expected > 0) {
        note = `Filters are hiding ${authzMoney(hidden.expected)} across ${Formatters.int(hidden.lines)} line${hidden.lines === 1 ? "" : "s"} needing auth. This is not $0.`;
      } else if (hidden && hidden.patients > 0) {
        note = `Filters are hiding ${Formatters.int(hidden.patients)} patient${hidden.patients === 1 ? "" : "s"} still on this board.`;
      }
      return authorizationDirectoryBreakdownMarkup(visible) + `
        <section class="panel authz-empty-panel">
          <div class="authz-empty">
            <p class="authz-empty-title">No patients match these filters.</p>
            ${note ? `<p class="authz-empty-note">${escapeHtml(note)}</p>` : ""}
            <button id="authzClearFiltersEmpty" class="ghost-button" type="button">Clear filters</button>
          </div>
        </section>`;
    }
    if (authorizationsState.refreshHeldOrgs.length) {
      return `<section class="panel authz-empty-panel"><div class="authz-empty">
        <p class="authz-empty-title">No visible claims are waiting on authorizations.</p>
        <p class="authz-empty-note">This is not a confirmed empty queue because ${Formatters.int(authorizationsState.refreshHeldOrgs.length)} compan${authorizationsState.refreshHeldOrgs.length === 1 ? "y is" : "ies are"} waiting for a source refresh.</p>
      </div></section>`;
    }
    // Emptiness is an achievement — celebrate it (design §8).
    return `
      <section class="panel authz-empty-panel authz-empty-celebrate">
        <div class="authz-empty">
          <p class="authz-empty-emoji" aria-hidden="true">🎉</p>
          <p class="authz-empty-title">No claims are waiting on authorizations.</p>
          <p class="authz-empty-note">Nothing to do here. New on-hold claims will show up as soon as they land.</p>
        </div>
      </section>`;
  }
  if (authorizationsState.board?.page) return authorizationDirectoryBreakdownMarkup(visible)
    + operationalBoardPagerHtml(authorizationsState.board.page, "authorizations", "Authorizations")
    + visible.map((patient) => authorizationPatientCardMarkup(patient, authorizationsState.board.page.totalRows)).join("");
  const page = workflowDirectoryPage(visible, authorizationsState);
  return authorizationDirectoryBreakdownMarkup(visible) + workflowDirectoryPagerMarkup(page, "authz")
    + page.rows.map((patient) => authorizationPatientCardMarkup(patient, visible.length)).join("");
}

function authorizationsSkeletonMarkup() {
  return Array.from({ length: 3 }).map(() => `
    <section class="panel authz-skeleton-card" aria-hidden="true">
      <div class="authz-skeleton-row authz-skeleton-wide"></div>
      <div class="authz-skeleton-row"></div>
      <div class="authz-skeleton-row authz-skeleton-short"></div>
    </section>`).join("");
}

function authorizationPatientCardMarkup(patient = {}, visibleCount = 0) {
  const key = String(patient.patientKey || "");
  const cardKey = authorizationPatientCardKey(patient);
  const collapsed = authorizationPatientCollapsed(patient, visibleCount);
  const payers = Array.isArray(patient.payers) ? patient.payers : [];
  const payerLabel = payers.length === 1
    ? String(payers[0].payerLabel || payers[0].payerName || "")
    : `${payers.length} payers`;
  // D2: when the board spans several companies EVERY card names its own, so a
  // patient can never be read under another company's heading.
  const orgLabel = authorizationBoardIsMultiOrg() ? authorizationPatientOrgLabel(patient) : "";
  return `
    <article class="panel authz-patient-card${collapsed ? "" : " open"}" data-patient-key="${escapeHtml(key)}" data-org-id="${escapeHtml(String(patient.orgId || ""))}">
      <div class="authz-patient-head">
        <button class="authz-patient-toggle" type="button" data-authz-toggle-patient="${escapeHtml(cardKey)}" aria-expanded="${collapsed ? "false" : "true"}">
          <span class="authz-caret" aria-hidden="true">${collapsed ? "▸" : "▾"}</span>
          <span class="authz-patient-name">${escapeHtml(patient.patientLabel || key)}</span>
          ${orgLabel ? `<span class="authz-patient-org" title="Company">${escapeHtml(orgLabel)}</span>` : ""}
          <span class="authz-patient-payer">${escapeHtml(payerLabel)}</span>
          ${authzStatusPill(patient.worstStatus, "", { billed: authzAuthsBilledState(patient.worstStatus, payers.flatMap((entry) => Array.isArray(entry.authorizations) ? entry.authorizations : []), patient.orgId) })}
          <span class="authz-patient-meta">${authorizationPatientMetaText(patient)}</span>
        </button>
        <div class="authz-patient-overflow">
          <button class="icon-button authz-overflow-btn" type="button" data-authz-archive-patient="${escapeHtml(cardKey)}" title="Archive patient" aria-label="Archive ${escapeHtml(patient.patientLabel || key)}">⋯</button>
        </div>
      </div>
      <div class="authz-patient-body"${collapsed ? " hidden" : ""}>
        ${collapsed ? "" : payers.map((payer) => authorizationPayerGroupMarkup(patient, payer)).join("")}
      </div>
    </article>`;
}

function authorizationPayerGroupMarkup(patient = {}, payer = {}) {
  const payerName = String(payer.payerName || "");
  const groupKey = authorizationGroupKeyFor(patient, payer);
  const needs = payer.needsAuthorization || { lineCount: 0, expectedTotal: 0, cptCodes: [], lines: [] };
  const auths = Array.isArray(payer.authorizations) ? payer.authorizations : [];
  const folding = authorizationsState.foldingGroupKey === groupKey;
  return `
    <section class="authz-payer-group${folding ? " folding" : ""}" data-group-key="${escapeHtml(groupKey)}">
      <header class="authz-payer-head">
        <span class="authz-payer-name">${escapeHtml(payer.payerLabel || payerName)}</span>
        ${payer.secondary ? `<span class="authz-secondary-tag" title="The primary insurance is billed; this needs the SECONDARY insurance authorization (secondary placeholder).">Secondary insurance</span>` : ""}
        ${authzStatusPill(payer.worstStatus, "", { billed: authzAuthsBilledState(payer.worstStatus, auths, patient.orgId) })}
      </header>
      ${needs.lineCount ? `
        <div class="authz-needs-row">
          <div class="authz-needs-block">
            <span class="authz-needs-title">Needs authorization</span>
            <span class="authz-needs-detail">
              ${escapeHtml(authzDateSpan(needs.dosStart, needs.dosEnd))} ·
              ${escapeHtml((needs.cptCodes || []).join(", ") || "no CPT")} ·
              ${isRedactedClientRole() ? `${Formatters.int(needs.lineCount)} line${Number(needs.lineCount) === 1 ? "" : "s"}` : authzLineCountMoneyText(needs.lineCount, authzNeedsPricedCount(payer), needs.expectedTotal, authzNeedsUnknownCount(payer))}
            </span>
          </div>
          <div class="authz-needs-actions">
            <button class="primary-button authz-record-btn" type="button" data-authz-record="${escapeHtml(groupKey)}">Record authorization</button>
            <div class="authz-other-action">
              <button class="ghost-button authz-other-btn" type="button" data-authz-other="${escapeHtml(groupKey)}" aria-haspopup="true" aria-expanded="false">Other action ▾</button>
              <div class="authz-other-menu" hidden>
                ${AUTHZ_RECORDABLE_STATUSES.filter((status) => status !== AUTHZ_STATUS.APPROVED).map((status) => `
                  <button class="authz-other-item" type="button" data-authz-other-status="${escapeHtml(status)}" data-authz-group="${escapeHtml(groupKey)}">
                    <span class="pill ${authzStatusTone(status)}">${escapeHtml(AUTHZ_STATUS_PILL_LABEL[status] || status)}</span>
                    <span class="authz-other-item-label">${escapeHtml(status)}</span>
                  </button>`).join("")}
              </div>
            </div>
          </div>
        </div>
        ${authorizationLineDisclosureMarkup(groupKey, needs.lines || [], String(patient.orgId || payer.orgId || ""))}
      ` : `
        <div class="authz-needs-row authz-needs-clear">
          <div class="authz-needs-block">
            <span class="authz-needs-title">Nothing waiting on an authorization</span>
            <span class="authz-needs-detail">Every on-hold line for this payer is covered by a recorded authorization.</span>
          </div>
          <div class="authz-needs-actions">
            <button class="ghost-button" type="button" data-authz-record="${escapeHtml(groupKey)}">Record another</button>
          </div>
        </div>`}
      ${auths.length ? `<div class="authz-auth-list">${auths.map((auth) => authorizationCardMarkup(groupKey, auth)).join("")}</div>` : ""}
    </section>`;
}

function authorizationCardMarkup(groupKey, auth = {}) {
  const authId = String(auth.authorizationId || "");
  const justCreated = authorizationsState.justCreatedAuthorizationId === authId;
  const uploadFailure = authorizationsState.uploadFailures[authId];
  const files = Array.isArray(auth.files) ? auth.files.filter((file) => file && file.authorizationFileId) : [];
  const fileSlot = uploadFailure
    ? `<span class="authz-file-slot failed" title="${escapeHtml(uploadFailure.message || "Upload failed")}">📎 upload failed — <button class="authz-link-button" type="button" data-authz-retry-upload="${escapeHtml(authId)}">retry</button></span>`
    : (files.length
      ? `<span class="authz-file-slot ok"><span>📎 ${Formatters.int(files.length)} file${files.length === 1 ? "" : "s"}</span><span class="authz-file-links">${files.map((file, index) => {
        const filename = String(file.originalFilename || `authorization-file-${index + 1}`);
        return `<button class="authz-link-button authz-file-download" type="button" data-authz-download-file="${escapeHtml(file.authorizationFileId)}" data-authz-download-authorization="${escapeHtml(authId)}" data-authz-download-name="${escapeHtml(filename)}" title="Download ${escapeHtml(filename)}">Download ${files.length === 1 ? "file" : index + 1}</button>`;
      }).join("")}</span></span>`
      : (Number(auth.fileCount || 0) > 0
        ? `<span class="authz-file-slot failed">📎 ${Formatters.int(auth.fileCount)} file${Number(auth.fileCount) === 1 ? "" : "s"} unavailable — refresh</span>`
      : `<span class="authz-file-slot none">no file attached</span>`));
  return `
    <div class="authz-auth-card${auth.archived ? " archived" : ""}${justCreated ? " just-created" : ""}" data-authorization-id="${escapeHtml(authId)}">
      <div class="authz-auth-main">
        ${authzStatusPill(auth.status, "", { billed: authzAuthBilledState(auth) })}
        <span class="authz-auth-number">${escapeHtml(auth.authNumber || "no auth number")}</span>
        <span class="authz-auth-range">${escapeHtml(authzFullDate(auth.effectiveStartDate))} → ${escapeHtml(authzFullDate(auth.effectiveEndDate))}</span>
        ${fileSlot}
        <span class="authz-auth-lines">${Formatters.int(auth.lineCount || 0)} line${Number(auth.lineCount || 0) === 1 ? "" : "s"} covered${isRedactedClientRole() || (Number(auth.lineCount || 0) > 0 && Number(auth.unknownExpectedLineCount || 0) >= Number(auth.lineCount || 0)) ? "" : ` · ${escapeHtml(authzMoney(auth.expectedTotal))}`}</span>
        ${auth.archived ? `<span class="pill gray">Archived</span>` : ""}
      </div>
      ${auth.note ? `<p class="authz-auth-note">${escapeHtml(auth.note)}</p>` : ""}
      <div class="authz-auth-actions">
        <button class="authz-link-button" type="button" data-authz-edit="${escapeHtml(authId)}" data-authz-group="${escapeHtml(groupKey)}">Edit</button>
        <button class="authz-link-button" type="button" data-authz-extend="${escapeHtml(authId)}" data-authz-group="${escapeHtml(groupKey)}">Extend range</button>
        ${hasPermission(PERMISSIONS.AUTHORIZATIONS_MANAGE) ? `<button class="authz-link-button" type="button" data-authz-choose-file="${escapeHtml(authId)}" data-authz-group="${escapeHtml(groupKey)}">${files.length ? "Add another file" : "Choose file"}</button>` : ""}
      </div>
    </div>`;
}

// R1: raw lines NEVER render unbidden. The card shows a single "View lines (N)"
// disclosure; the slim READ-ONLY table appears only once it is clicked. There
// is deliberately no per-line inline editing — a line click opens the existing
// claim drawer for full history.
function authorizationLineDisclosureMarkup(groupKey, lines = [], orgId = "") {
  if (!lines.length) return "";
  const open = Boolean(authorizationsState.openLineTables[groupKey]);
  return `
    <div class="authz-line-disclosure">
      <button class="authz-link-button authz-view-lines" type="button" data-authz-view-lines="${escapeHtml(groupKey)}" aria-expanded="${open ? "true" : "false"}">
        ${open ? "Hide lines" : `View lines (${Formatters.int(lines.length)})`}
      </button>
      ${open ? authorizationLineTableMarkup(groupKey, lines, orgId) : ""}
    </div>`;
}

// THE ROW CARRIES ITS ORG (ENGINEERING_TRAPS #3). A claim id is an EZClaim
// per-practice sequence, and this board spans every company the caller holds,
// so a row that opened the drawer by claim id alone was letting
// openServerClaimDrawer resolve the tenant from whichever row matched first.
// The org rides on the DOM and through BOTH binders (click and keydown).
function authorizationLineTableMarkup(groupKey, lines = [], orgId = "") {
  const showAll = Boolean(authorizationsState.showAllLines[groupKey]);
  const shown = showAll ? lines : lines.slice(0, AUTHZ_LINE_PREVIEW_CAP);
  const hidden = lines.length - shown.length;
  const drillable = hasPermission(PERMISSIONS.VIEW_WORKBENCH);
  // Units + POS are not in the board payload today; render the columns only if
  // a future payload starts carrying them (addendum R2 allows both).
  const hasUnits = lines.some((line) => line.units != null && line.units !== "");
  const hasPos = lines.some((line) => line.pos != null && line.pos !== "");
  return `
    <div class="authz-line-table-wrap">
      <table class="authz-line-table">
        <thead>
          <tr>
            <th scope="col">DOS</th>
            <th scope="col">CPT</th>
            ${hasUnits ? `<th scope="col" class="num">Units</th>` : ""}
            ${hasPos ? `<th scope="col">POS</th>` : ""}
            ${isRedactedClientRole() ? "" : `<th scope="col" class="num">Expected</th>`}
            <th scope="col">Claim / line</th>
          </tr>
        </thead>
        <tbody>
          ${shown.map((line) => `
            <tr class="authz-line-row${drillable ? " drillable" : ""}" ${drillable ? `data-authz-open-claim="${escapeHtml(String(line.claimId || ""))}" data-authz-service-id="${escapeHtml(String(line.serviceLineId || ""))}" data-authz-org-id="${escapeHtml(String(orgId || line.orgId || ""))}" tabindex="0" role="button"` : ""}>
              <td>${escapeHtml(authzFullDate(line.dos))}</td>
              <td>${escapeHtml(line.cpt || "—")}</td>
              ${hasUnits ? `<td class="num">${escapeHtml(String(line.units == null ? "—" : line.units))}</td>` : ""}
              ${hasPos ? `<td>${escapeHtml(String(line.pos == null ? "—" : line.pos))}</td>` : ""}
              ${isRedactedClientRole() ? "" : `<td class="num">${line.expected == null ? moneyUnknownHtml(AUTHZ_NO_EXPECTED_REASON) : escapeHtml(authzMoney(line.expected))}</td>`}
              <td class="authz-line-ids">${escapeHtml(String(line.claimId || "—"))}${line.serviceLineId ? ` / ${escapeHtml(String(line.serviceLineId))}` : ""}</td>
            </tr>`).join("")}
        </tbody>
      </table>
      ${hidden > 0
        ? `<button class="authz-link-button" type="button" data-authz-show-all="${escapeHtml(groupKey)}">…and ${Formatters.int(hidden)} more — show all</button>`
        : (showAll && lines.length > AUTHZ_LINE_PREVIEW_CAP ? `<button class="authz-link-button" type="button" data-authz-show-all="${escapeHtml(groupKey)}">Show fewer</button>` : "")}
    </div>`;
}

function authorizationsResolvedMarkup() {
  const resolved = authorizationResolvedPatients();
  if (!resolved.length) return "";
  const open = Boolean(authorizationsState.resolvedOpen);
  const count = authorizationsState.board?.resolvedPage?.totalRows ?? resolved.length;
  return `
    <section class="panel authz-resolved-panel">
      <button class="authz-resolved-head" type="button" id="authzResolvedToggle" aria-expanded="${open ? "true" : "false"}">
        <span class="authz-caret" aria-hidden="true">${open ? "▾" : "▸"}</span>
        <span>Recently resolved</span>
        <span class="authz-resolved-count">${Formatters.int(count)} patient${count === 1 ? "" : "s"} fell off the tracker</span>
      </button>
      <div class="authz-resolved-body"${open ? "" : " hidden"}>
        ${operationalBoardPagerHtml(authorizationsState.board?.resolvedPage, "authorizations-resolved", "Resolved authorizations")}
        ${resolved.map((patient) => `
          <div class="authz-resolved-row">
            <span class="authz-resolved-name">${escapeHtml(patient.patientLabel || patient.patientKey)}</span>
            ${(patient.payers || []).flatMap((payer) => (payer.authorizations || []).map((auth) => `
              <span class="authz-resolved-auth">
                ${authzStatusPill(auth.status)}
                ${escapeHtml(auth.authNumber || "no number")} ·
                ${escapeHtml(authzFullDate(auth.effectiveStartDate))} → ${escapeHtml(authzFullDate(auth.effectiveEndDate))} ·
                ${Formatters.int(auth.lineCount || 0)} lines
              </span>`)).join("")}
          </div>`).join("")}
      </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// Board interactions
// ---------------------------------------------------------------------------
function bindAuthorizationsControls(host) {
  if (!host) return;
  bindCentralReachCheckButtons(host, "authorizations");
  bindAuthorizationWaitingReport(host);
  host.querySelectorAll('[data-management-list="authorizations"], [data-management-list="authorizations-resolved"]').forEach(button => button.addEventListener("click", () => {
    authorizationsState[button.dataset.managementList === "authorizations-resolved" ? "resolvedPage" : "page"] = Number(button.dataset.managementPage);
    requestAuthorizationBoard();
  }));
  const search = host.querySelector("#authzSearchInput");
  if (search) {
    search.addEventListener("input", (event) => {
      authorizationsState.search = String(event.target.value || ""); authorizationsState.page = 1;
      authorizationsState.patientFilter = "";
      requestOperationalBoardSearch("authorizations");
    });
  }
  host.querySelector("#authzPatientFilter")?.addEventListener("change", (event) => {
    authorizationsState.patientFilter = String(event.target.value || "");
    authorizationsState.page = 1;
    if (authorizationsState.patientFilter) authorizationsState.collapseOverrides[authorizationsState.patientFilter] = false;
    requestAuthorizationBoard();
  });
  host.querySelector("[data-authz-sort]")?.addEventListener("change", (event) => {
    authorizationsState.sortOrder = event.target.value === "lines" ? "lines" : "name";
    authorizationsState.page = 1;
    requestAuthorizationBoard();
  });
  host.querySelectorAll("[data-authz-page]").forEach((button) => button.addEventListener("click", () => {
    authorizationsState.page = Number(button.dataset.authzPage) || 0;
    renderAuthorizations();
    document.querySelector(".authz-board .workflow-directory-pager")?.scrollIntoView({ block: "nearest" });
  }));
  host.querySelectorAll("[data-authz-status]").forEach((button) => button.addEventListener("click", () => {
    authorizationsState.statusFilter = String(button.dataset.authzStatus || "All");
    authorizationsState.page = 1;
    requestAuthorizationBoard();
  }));
  host.querySelector("#authzRefreshBtn")?.addEventListener("click", () => requestAuthorizationBoard());
  host.querySelector("#authzRetryBtn")?.addEventListener("click", () => requestAuthorizationBoard());
  host.querySelector("#authzStatusFilter")?.addEventListener("change", (event) => {
    authorizationsState.statusFilter = String(event.target.value || "All");
    authorizationsState.page = 1;
    authorizationsState.patientFilter = "";
    requestAuthorizationBoard();
  });
  host.querySelector("#authzPayerFilter")?.addEventListener("change", (event) => {
    authorizationsState.payerFilter = String(event.target.value || "All");
    authorizationsState.page = 1;
    authorizationsState.patientFilter = "";
    requestAuthorizationBoard();
  });
  host.querySelector("#authzOrgSelect")?.addEventListener("change", (event) => {
    authorizationsState.orgId = String(event.target.value || "");
    authorizationsState.collapseLoadedFor = ""; authorizationsState.page = 1; authorizationsState.resolvedPage = 1;
    renderAuthorizations();
  });
  host.querySelector("#authzArchivedToggle")?.addEventListener("change", (event) => {
    authorizationsState.includeArchived = Boolean(event.target.checked);
    authorizationsState.page = 1;
    authorizationsState.patientFilter = "";
    requestAuthorizationBoard();
  });
  const clearFilters = () => {
    authorizationsState.search = "";
    authorizationsState.statusFilter = "All";
    authorizationsState.payerFilter = "All";
    authorizationsState.patientFilter = "";
    authorizationsState.page = 1;
    authorizationsState.sortOrder = "name";
    authorizationsState.includeArchived = false;
    requestAuthorizationBoard();
  };
  host.querySelector("#authzClearFilters")?.addEventListener("click", clearFilters);
  host.querySelector("#authzClearFiltersEmpty")?.addEventListener("click", clearFilters);
  host.querySelector("#authzExpandAll")?.addEventListener("click", () => setAllAuthorizationCardsCollapsed(false));
  host.querySelector("#authzCollapseAll")?.addEventListener("click", () => setAllAuthorizationCardsCollapsed(true));
  host.querySelector("#authzResolvedToggle")?.addEventListener("click", () => {
    authorizationsState.resolvedOpen = !authorizationsState.resolvedOpen;
    renderAuthorizations();
  });
  host.querySelector("#authzDismissSaveStrip")?.addEventListener("click", () => {
    authorizationsState.lastSave = null;
    renderAuthorizations();
  });
  host.querySelector("#authzNextPatientBtn")?.addEventListener("click", () => {
    const last = authorizationsState.lastSave || {};
    const next = authorizationNextPatientNeedingAuth(`${String(last.orgId || "")}||${String(last.patientKey || "")}`);
    if (!next) return;
    const payer = (next.payers || []).find((entry) => Number(entry.needsAuthorization?.lineCount || 0) > 0) || (next.payers || [])[0];
    if (!payer) return;
    authorizationsState.lastSave = null;
    const nextKey = authorizationPatientCardKey(next);
    if (authorizationsState.patientFilter) {
      authorizationsState.patientFilter = nextKey;
      authorizationsState.page = 0;
    } else {
      const nextIndex = authorizationVisiblePatients().findIndex((patient) => authorizationPatientCardKey(patient) === nextKey);
      authorizationsState.page = Math.floor(Math.max(0, nextIndex) / 20);
    }
    authorizationsState.collapseOverrides[nextKey] = false;
    renderAuthorizations();
    openAuthorizationDrawer({ groupKey: authorizationGroupKeyFor(next, payer), mode: "record" });
  });

  host.addEventListener("click", handleAuthorizationBoardClick);
  host.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const row = event.target.closest?.("[data-authz-open-claim]");
    if (!row) return;
    event.preventDefault();
    openServerClaimDrawer(row.dataset.authzOpenClaim, { orgId: row.dataset.authzOrgId || "", serviceId: row.dataset.authzServiceId || "", queueContext: { source: "authorizations" } });
  });
}

function handleAuthorizationBoardClick(event) {
  const target = event.target;
  if (!target || typeof target.closest !== "function") return;

  const toggle = target.closest("[data-authz-toggle-patient]");
  if (toggle) {
    const key = toggle.dataset.authzTogglePatient;
    const visible = authorizationVisiblePatients();
    const patient = visible.find((entry) => authorizationPatientCardKey(entry) === key) || {};
    const currentlyCollapsed = authorizationPatientCollapsed(patient, visible.length);
    authorizationsState.collapseOverrides[key] = !currentlyCollapsed;
    saveAuthorizationCollapseState();
    renderAuthorizations();
    return;
  }
  const viewLines = target.closest("[data-authz-view-lines]");
  if (viewLines) {
    const key = viewLines.dataset.authzViewLines;
    authorizationsState.openLineTables[key] = !authorizationsState.openLineTables[key];
    renderAuthorizations();
    return;
  }
  const showAll = target.closest("[data-authz-show-all]");
  if (showAll) {
    const key = showAll.dataset.authzShowAll;
    authorizationsState.showAllLines[key] = !authorizationsState.showAllLines[key];
    renderAuthorizations();
    return;
  }
  const record = target.closest("[data-authz-record]");
  if (record) {
    openAuthorizationDrawer({ groupKey: record.dataset.authzRecord, mode: "record" });
    return;
  }
  const otherBtn = target.closest("[data-authz-other]");
  if (otherBtn) {
    const menu = otherBtn.parentElement?.querySelector(".authz-other-menu");
    const open = menu && menu.hidden;
    document.querySelectorAll(".authz-other-menu").forEach((node) => { node.hidden = true; });
    document.querySelectorAll("[data-authz-other]").forEach((node) => node.setAttribute("aria-expanded", "false"));
    if (menu) menu.hidden = !open;
    otherBtn.setAttribute("aria-expanded", open ? "true" : "false");
    return;
  }
  const otherStatus = target.closest("[data-authz-other-status]");
  if (otherStatus) {
    openAuthorizationDrawer({
      groupKey: otherStatus.dataset.authzGroup,
      mode: "record",
      status: otherStatus.dataset.authzOtherStatus
    });
    return;
  }
  const edit = target.closest("[data-authz-edit]");
  if (edit) {
    openAuthorizationDrawer({ groupKey: edit.dataset.authzGroup, mode: "edit", authorizationId: edit.dataset.authzEdit });
    return;
  }
  const extend = target.closest("[data-authz-extend]");
  if (extend) {
    openAuthorizationDrawer({ groupKey: extend.dataset.authzGroup, mode: "extend", authorizationId: extend.dataset.authzExtend });
    return;
  }
  const chooseFile = target.closest("[data-authz-choose-file]");
  if (chooseFile) {
    chooseAuthorizationFile(chooseFile.dataset.authzChooseFile, chooseFile.dataset.authzGroup);
    return;
  }
  const retryUpload = target.closest("[data-authz-retry-upload]");
  if (retryUpload) {
    retryAuthorizationFileUpload(retryUpload.dataset.authzRetryUpload);
    return;
  }
  const downloadFile = target.closest("[data-authz-download-file]");
  if (downloadFile) {
    downloadAuthorizationFileFromBoard({
      authorizationId: downloadFile.dataset.authzDownloadAuthorization,
      authorizationFileId: downloadFile.dataset.authzDownloadFile,
      filename: downloadFile.dataset.authzDownloadName,
      button: downloadFile
    });
    return;
  }
  const archivePatient = target.closest("[data-authz-archive-patient]");
  if (archivePatient) {
    archiveAuthorizationPatient(archivePatient.dataset.authzArchivePatient);
    return;
  }
  const claimRow = target.closest("[data-authz-open-claim]");
  if (claimRow) {
    openServerClaimDrawer(claimRow.dataset.authzOpenClaim, { orgId: claimRow.dataset.authzOrgId || "", serviceId: claimRow.dataset.authzServiceId || "", queueContext: { source: "authorizations" } });
  }
}

// D2: three parts — org, patient, payer — so a card on an all-orgs board always
// resolves to the right company's patient. A legacy two-part key (patient||payer)
// still resolves, but only when exactly ONE company matches; an ambiguous legacy
// key refuses rather than guessing a tenant.
function authorizationGroupByKey(groupKey) {
  const parts = String(groupKey || "").split("||");
  const hasOrg = parts.length >= 3;
  const orgId = hasOrg ? parts[0] : "";
  const patientKey = hasOrg ? parts[1] : (parts[0] || "");
  const payerName = hasOrg ? parts.slice(2).join("||") : (parts.slice(1).join("||") || "");
  const candidates = authorizationPatients().filter((entry) =>
    String(entry.patientKey) === patientKey && (!hasOrg || String(entry.orgId || "") === orgId)
  );
  if (candidates.length !== 1) return null;
  const patient = candidates[0];
  const payer = (patient.payers || []).find((entry) => String(entry.payerName) === payerName);
  if (!payer) return null;
  return { patient, payer };
}

// ---------------------------------------------------------------------------
// The record-authorization drawer (design §4 + addendum R3) — the 60-second
// interaction, and the signature moment of the whole feature.
// ---------------------------------------------------------------------------
function openAuthorizationDrawer({ groupKey = "", mode = "record", status = "", authorizationId = "" } = {}) {
  if (!hasPermission(PERMISSIONS.AUTHORIZATIONS_MANAGE)) {
    return denyAction(PERMISSIONS.AUTHORIZATIONS_MANAGE, "record an authorization");
  }
  const group = authorizationGroupByKey(groupKey);
  if (!group) return;
  const needs = group.payer.needsAuthorization || { lines: [], dosStart: "", dosEnd: "" };
  const existing = mode === "record"
    ? null
    : (group.payer.authorizations || []).find((auth) => String(auth.authorizationId) === String(authorizationId)) || null;

  authorizationDrawerState.open = true;
  authorizationDrawerState.mode = mode;
  authorizationDrawerState.groupKey = groupKey;
  authorizationDrawerState.orgId = String(group.patient.orgId || group.payer.orgId || "");
  authorizationDrawerState.orgLabel = authorizationPatientOrgLabel(group.patient);
  authorizationDrawerState.patientKey = String(group.patient.patientKey || "");
  authorizationDrawerState.patientLabel = String(group.patient.patientLabel || group.patient.patientKey || "");
  authorizationDrawerState.payerName = String(group.payer.payerName || "");
  authorizationDrawerState.payerLabel = String(group.payer.payerLabel || group.payer.payerName || "");
  authorizationDrawerState.authorizationId = existing ? String(existing.authorizationId) : "";
  // The coverage preview is computed against the group's CURRENT on-hold,
  // unlinked lines — exactly the set the server-side resolver will see.
  authorizationDrawerState.lines = (needs.lines || []).slice();
  authorizationDrawerState.uncheckedLineKeys = {};
  authorizationDrawerState.checklistOpen = false; // R3.8
  authorizationDrawerState.file = null;
  authorizationDrawerState.fileError = "";
  authorizationDrawerState.saving = false;
  authorizationDrawerState.errors = [];
  authorizationDrawerState.errorBanner = "";
  authorizationDrawerState.zeroCoverageConfirmed = false;
  authorizationDrawerState.form = {
    status: status || (existing ? existing.status : AUTHZ_STATUS.APPROVED),
    authNumber: existing ? String(existing.authNumber || "") : (status === AUTHZ_STATUS.NAR ? "NAR" : ""),
    // R3.2: dates pre-filled to the group's exact DOS span so the preview
    // already reads "resolves N lines" before anything is typed.
    // "Extend range" starts the day AFTER the existing authorization ended so
    // history is never silently rewritten.
    effectiveStart: mode === "extend" && existing
      ? authzNextDay(existing.effectiveEndDate)
      : (existing ? String(existing.effectiveStartDate || "") : authzIsoDate(needs.dosStart)),
    effectiveEnd: existing && mode === "edit"
      ? String(existing.effectiveEndDate || "")
      : authzIsoDate(needs.dosEnd),
    note: existing && mode === "edit" ? String(existing.note || "") : ""
  };
  renderAuthorizationDrawer();
  openAuthorizationDrawerShell();
}

function authzNextDay(value) {
  const parsed = authzParseDateOnly(value);
  if (parsed == null) return "";
  return new Date(parsed + 86400000).toISOString().slice(0, 10);
}

// THE live coverage preview (design §4, addendum R3.1). Recomputed CLIENT-SIDE
// from the group's already-loaded on-hold lines using the SAME in-range rule as
// resolveLinesForAuthorization() — no network call, no spinner, and the number
// the biller reads is by construction the set the server will link.
function authorizationCoveragePreview() {
  const start = authzParseDateOnly(authorizationDrawerState.form.effectiveStart);
  const end = authzParseDateOnly(authorizationDrawerState.form.effectiveEnd);
  const covered = [];
  const outside = [];
  (authorizationDrawerState.lines || []).forEach((line) => {
    const dos = authzParseDateOnly(line.dos);
    const inRange = dos != null && (start == null || dos >= start) && (end == null || dos <= end);
    if (inRange) covered.push(line); else outside.push(line);
  });
  // TRUE per-line selection. The unchecked keys are sent to the server verbatim
  // as `selectedLines`, which the resolver INTERSECTS with its own range-set --
  // so the set the biller sees here is exactly the set that gets linked. (This
  // used to degrade into a CPT restriction, which could not exclude a line that
  // shared a CPT with a checked one.)
  const checked = covered.filter((line) => !authorizationDrawerState.uncheckedLineKeys[authzLineKey(line)]);
  const effective = checked;
  const deselected = covered.filter((line) => authorizationDrawerState.uncheckedLineKeys[authzLineKey(line)]);
  const selectionRestricted = effective.length < covered.length;
  const allDos = (authorizationDrawerState.lines || []).map((line) => authzParseDateOnly(line.dos)).filter((value) => value != null);
  return {
    covered,
    outside,
    checked,
    effective,
    deselected,
    selectionRestricted,
    // The exact wire payload for the server-side intersection filter.
    selectedLines: effective.map((line) => ({ claimId: String(line.claimId || ""), serviceLineId: String(line.serviceLineId || "") })),
    allMinDos: allDos.length ? new Date(Math.min(...allDos)).toISOString().slice(0, 10) : "",
    allMaxDos: allDos.length ? new Date(Math.max(...allDos)).toISOString().slice(0, 10) : "",
    expectedTotal: effective.reduce((sum, line) => (line.expected == null ? sum : sum + (Number(line.expected) || 0)), 0),
    // Polish #6: how many of the covered lines actually carry a rate. Without
    // this the $ looks frozen when the range narrows (120 -> 48 lines both read
    // $14,989, because 72 of the 120 expect $0).
    pricedCount: effective.reduce((count, line) => count + (line.expected != null && (Number(line.expected) || 0) > 0 ? 1 : 0), 0),
    // ...and how many carry no expected AT ALL, which is what decides whether
    // the total above is a floor or is simply not a number.
    unknownCount: effective.reduce((count, line) => count + (line.expected == null ? 1 : 0), 0),
    displayCpts: Array.from(new Set(effective.map((line) => line.cpt).filter(Boolean))).sort()
  };
}

function renderAuthorizationDrawer() {
  const body = document.getElementById("authDrawerBody");
  const kicker = document.getElementById("authDrawerKicker");
  const title = document.getElementById("authDrawerTitle");
  if (!body) return;
  const modeLabel = authorizationDrawerState.mode === "edit"
    ? "Edit authorization"
    : (authorizationDrawerState.mode === "extend" ? "Extend authorization range" : "Record authorization");
  // D2: the drawer names the company it is about to write to, so the biller can
  // see the save target rather than trusting a header that used to be wrong.
  const drawerOrgLabel = authorizationDrawerState.orgLabel || authorizationOrgLabel(authorizationDrawerState.orgId);
  if (kicker) {
    kicker.textContent = [drawerOrgLabel, authorizationDrawerState.patientLabel, authorizationDrawerState.payerLabel]
      .filter(Boolean).join(" · ");
  }
  if (title) title.textContent = modeLabel;

  const form = authorizationDrawerState.form;
  const errorFor = (field) => (authorizationDrawerState.errors.find((entry) => entry.field === field) || {}).message || "";
  const requiresNumber = form.status === AUTHZ_STATUS.APPROVED;
  const narNumber = form.status === AUTHZ_STATUS.NAR;
  const requiresNote = form.status === AUTHZ_STATUS.WRITE_OFF;

  body.innerHTML = `
    <form id="authzDrawerForm" class="authz-drawer-form" novalidate>
      ${authorizationDrawerState.errorBanner ? `<div class="authz-drawer-banner error" role="alert">${escapeHtml(authorizationDrawerState.errorBanner)}</div>` : ""}
      <label class="authz-field">
        <span class="authz-field-label">Status <em>required</em></span>
        <select id="authzStatusInput">
          ${AUTHZ_RECORDABLE_STATUSES.map((status) => `<option value="${escapeHtml(status)}" ${form.status === status ? "selected" : ""}>${escapeHtml(status)}</option>`).join("")}
        </select>
        <span class="authz-field-help">${escapeHtml(AUTHZ_STATUS_HELP[form.status] || "")}</span>
        ${errorFor("status") ? `<span class="authz-field-error">${escapeHtml(errorFor("status"))}</span>` : ""}
      </label>

      <label class="authz-field${requiresNumber ? " required" : ""}">
        <span class="authz-field-label">Authorization number ${requiresNumber ? "<em>required</em>" : (narNumber ? "<em>filled in automatically</em>" : "<em>optional</em>")}</span>
        <input id="authzNumberInput" type="text" autocomplete="off" spellcheck="false" placeholder="e.g. OP00415" value="${escapeHtml(form.authNumber)}">
        ${errorFor("authNumber") ? `<span class="authz-field-error">${escapeHtml(errorFor("authNumber"))}</span>` : ""}
      </label>

      <div class="authz-field authz-range-field">
        <span class="authz-field-label">Effective dates <em>required</em></span>
        <div class="authz-range-inputs">
          <input id="authzStartInput" type="date" value="${escapeHtml(form.effectiveStart)}" aria-label="Effective start date">
          <span class="authz-range-arrow" aria-hidden="true">→</span>
          <input id="authzEndInput" type="date" value="${escapeHtml(form.effectiveEnd)}" aria-label="Effective end date">
        </div>
        <span class="authz-field-help">Pre-filled from this group's dates of service. The range is what resolves the lines.</span>
        ${errorFor("effectiveEnd") ? `<span class="authz-field-error">${escapeHtml(errorFor("effectiveEnd"))}</span>` : ""}
      </div>

      ${authorizationDrawerState.mode === "record" || authorizationDrawerState.mode === "edit" ? `
      <div class="authz-field">
        <span class="authz-field-label">Approval document <em>encouraged</em></span>
        <div class="authz-file-row">
          <label class="ghost-button authz-choose-file">
            <input id="authzFileInput" class="visually-hidden" type="file" accept=".pdf,.png,.jpg,.jpeg,.heic,application/pdf,image/png,image/jpeg,image/heic">
            ${authorizationDrawerState.file ? "Choose a different file" : "Choose file"}
          </label>
          ${authorizationDrawerState.file ? `<span class="authz-file-chosen">${escapeHtml(authorizationDrawerState.file.name)} · ${escapeHtml(authzFileSize(authorizationDrawerState.file.size))}</span>` : ""}
        </div>
        <span class="authz-field-help">PDF, PNG, JPG or HEIC up to 10&nbsp;MB. The number saves even if the file does not.</span>
        ${authorizationDrawerState.fileError ? `<span class="authz-field-error">${escapeHtml(authorizationDrawerState.fileError)}</span>` : ""}
      </div>` : ""}

      <label class="authz-field${requiresNote ? " required" : ""}">
        <span class="authz-field-label">Note ${requiresNote ? "<em>required</em>" : "<em>optional</em>"}</span>
        <textarea id="authzNoteInput" rows="2" placeholder="${requiresNote ? "Why is this money being written off?" : "Anything TMS should know…"}">${escapeHtml(form.note)}</textarea>
        ${errorFor("note") ? `<span class="authz-field-error">${escapeHtml(errorFor("note"))}</span>` : ""}
      </label>

      <div id="authzCoverageHost">${authorizationCoverageMarkup()}</div>

      <div class="authz-drawer-actions">
        <button type="button" class="ghost-button" id="authzCancelBtn">Cancel</button>
        <button type="submit" class="primary-button authz-save-btn" id="authzSaveBtn" ${authorizationDrawerState.saving ? "disabled" : ""}>${authorizationSaveButtonLabel()}</button>
      </div>
    </form>`;

  bindAuthorizationDrawerControls(body);
}

// R3.3 — the coverage line is PRIMARY: headline-sized, plain language, neutral
// tone, sitting directly above the buttons. R3.4 — "outside range" is
// actionable, never scolding. R3.5 — the Save button carries the consequence.
function authorizationCoverageMarkup() {
  const preview = authorizationCoveragePreview();
  const form = authorizationDrawerState.form;
  const count = preview.effective.length;
  // No line in the selection carries an expected: there is no "total above" for
  // a floor note to qualify, so both the figure and the floor wording go.
  const allUnknown = count > 0 && Number(preview.unknownCount || 0) >= count;
  const spanLabel = count ? `${authzDateSpan(form.effectiveStart, form.effectiveEnd)}${preview.displayCpts.length ? ` · ${preview.displayCpts.join(", ")}` : ""}` : "";
  return `
    <section class="authz-coverage">
      <p class="authz-coverage-headline" aria-live="polite">
        ${count
          ? `This resolves <strong>${Formatters.int(count)}</strong> on-hold line${count === 1 ? "" : "s"} <span class="authz-coverage-span">(${escapeHtml(spanLabel)})</span>${isRedactedClientRole() || allUnknown ? "" : ` · <span class="authz-coverage-money">${escapeHtml(authzMoney(preview.expectedTotal))}</span>`}`
          : `This resolves <strong>0</strong> on-hold lines today${authorizationDrawerState.lines.length ? " — nothing in this date range." : "."}`}
      </p>
      ${!isRedactedClientRole() && count && allUnknown ? `
        <p class="authz-coverage-note">No rates have loaded for these lines, so their dollar value is not known. Recording the authorization still resolves all ${Formatters.int(count)}.</p>` : ""}
      ${!isRedactedClientRole() && count && !allUnknown && preview.pricedCount < count ? `
        <p class="authz-coverage-note">${Formatters.int(preview.pricedCount)} of ${Formatters.int(count)} line${count === 1 ? "" : "s"} ${preview.pricedCount === 1 ? "has" : "have"} a loaded rate — the total above is a floor, not the full value.</p>` : ""}
      ${preview.outside.length ? `
        <p class="authz-coverage-outside">
          ${Formatters.int(preview.outside.length)} line${preview.outside.length === 1 ? "" : "s"} fall outside this range and stay on the tracker.
          <button type="button" class="authz-link-button authz-extend-cover" id="authzExtendCoverBtn" data-min="${escapeHtml(preview.allMinDos)}" data-max="${escapeHtml(preview.allMaxDos)}">Extend to cover all ${Formatters.int(authorizationDrawerState.lines.length)} →</button>
        </p>` : ""}
      ${preview.deselected.length ? `
        <p class="authz-coverage-note">${Formatters.int(preview.deselected.length)} line${preview.deselected.length === 1 ? " is" : "s are"} unchecked below and will stay on the tracker.</p>` : ""}
      ${authorizationDrawerState.lines.length ? `
        <button class="authz-link-button" type="button" id="authzChecklistToggle" aria-expanded="${authorizationDrawerState.checklistOpen ? "true" : "false"}">
          ${authorizationDrawerState.checklistOpen ? "Hide" : "Show"} line checklist (${Formatters.int(preview.checked.length)} of ${Formatters.int(authorizationDrawerState.lines.length)} selected)
        </button>
        <div class="authz-checklist"${authorizationDrawerState.checklistOpen ? "" : " hidden"}>
          ${authorizationDrawerState.lines.map((line) => {
            const key = authzLineKey(line);
            const inRange = preview.covered.some((entry) => authzLineKey(entry) === key);
            const checked = inRange && !authorizationDrawerState.uncheckedLineKeys[key];
            return `
              <label class="authz-checklist-row${inRange ? "" : " out-of-range"}">
                <input type="checkbox" data-authz-line-key="${escapeHtml(key)}" ${checked ? "checked" : ""} ${inRange ? "" : "disabled"}>
                <span class="authz-checklist-dos">${escapeHtml(authzFullDate(line.dos))}</span>
                <span class="authz-checklist-cpt">${escapeHtml(line.cpt || "—")}</span>
                ${isRedactedClientRole() ? "" : `<span class="authz-checklist-money">${line.expected == null ? moneyUnknownHtml(AUTHZ_NO_EXPECTED_REASON) : escapeHtml(authzMoney(line.expected))}</span>`}
                <span class="authz-checklist-ids">${escapeHtml(String(line.claimId || ""))}${line.serviceLineId ? ` / ${escapeHtml(String(line.serviceLineId))}` : ""}</span>
                ${inRange ? "" : `<span class="authz-checklist-flag">outside range</span>`}
              </label>`;
          }).join("")}
        </div>` : `<p class="authz-coverage-note">No unlinked on-hold lines remain for this patient and payer.</p>`}
      ${authorizationDrawerState.zeroCoverageConfirmed && !count ? `
        <p class="authz-coverage-note">Saving anyway — this authorization is recorded for future dates of service.</p>` : ""}
    </section>`;
}

function authorizationSaveButtonLabel() {
  if (authorizationDrawerState.saving) return "Saving…";
  const count = authorizationCoveragePreview().effective.length;
  if (authorizationDrawerState.mode === "edit") return "Save changes";
  if (!count) return "Save — resolves 0 lines today";
  return `Save — resolves ${Formatters.int(count)} line${count === 1 ? "" : "s"}`;
}

// R3.1: only the coverage block + save label repaint while the biller types, so
// focus and caret are never stolen and the count updates in place.
function scheduleAuthorizationCoverageUpdate() {
  window.clearTimeout(authorizationCoverageTimer);
  authorizationCoverageTimer = window.setTimeout(updateAuthorizationCoverageUi, AUTHZ_COVERAGE_DEBOUNCE_MS);
}

function updateAuthorizationCoverageUi() {
  const host = document.getElementById("authzCoverageHost");
  if (host) {
    host.innerHTML = authorizationCoverageMarkup();
    bindAuthorizationCoverageControls(host);
  }
  const save = document.getElementById("authzSaveBtn");
  if (save) save.innerHTML = authorizationSaveButtonLabel();
}

function authzFileSize(bytes) {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function bindAuthorizationCoverageControls(host) {
  host.querySelector("#authzChecklistToggle")?.addEventListener("click", () => {
    authorizationDrawerState.checklistOpen = !authorizationDrawerState.checklistOpen;
    updateAuthorizationCoverageUi();
  });
  host.querySelectorAll("[data-authz-line-key]").forEach((input) => {
    input.addEventListener("change", (event) => {
      const key = event.target.dataset.authzLineKey;
      if (event.target.checked) delete authorizationDrawerState.uncheckedLineKeys[key];
      else authorizationDrawerState.uncheckedLineKeys[key] = true;
      updateAuthorizationCoverageUi();
    });
  });
  // R3.4: one click snaps the range to every line in the group.
  host.querySelector("#authzExtendCoverBtn")?.addEventListener("click", (event) => {
    const min = event.currentTarget.dataset.min || "";
    const max = event.currentTarget.dataset.max || "";
    if (min) authorizationDrawerState.form.effectiveStart = min;
    if (max) authorizationDrawerState.form.effectiveEnd = max;
    const startInput = document.getElementById("authzStartInput");
    const endInput = document.getElementById("authzEndInput");
    if (startInput) startInput.value = authorizationDrawerState.form.effectiveStart;
    if (endInput) endInput.value = authorizationDrawerState.form.effectiveEnd;
    updateAuthorizationCoverageUi();
  });
}

function bindAuthorizationDrawerControls(body) {
  body.querySelector("#authzStatusInput")?.addEventListener("change", (event) => {
    const form = authorizationDrawerState.form;
    form.status = String(event.target.value || "");
    // NAR fills its own number, and leaving NAR must not carry "NAR" into a status
    // that needs a real one.
    const typed = String(form.authNumber || "").trim();
    if (form.status === AUTHZ_STATUS.NAR && !typed) form.authNumber = "NAR";
    else if (form.status !== AUTHZ_STATUS.NAR && typed === "NAR") form.authNumber = "";
    authorizationDrawerState.errors = [];
    renderAuthorizationDrawer();
  });
  const numberInput = body.querySelector("#authzNumberInput");
  numberInput?.addEventListener("input", (event) => {
    authorizationDrawerState.form.authNumber = String(event.target.value || "");
  });
  // R3.8: Enter in the auth-number field must NOT fire a 120-line action.
  numberInput?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      document.getElementById("authzSaveBtn")?.focus();
    }
  });
  body.querySelector("#authzNoteInput")?.addEventListener("input", (event) => {
    authorizationDrawerState.form.note = String(event.target.value || "");
  });
  // Dates drive the signature interaction: patch the coverage block in place.
  body.querySelector("#authzStartInput")?.addEventListener("input", (event) => {
    authorizationDrawerState.form.effectiveStart = String(event.target.value || "");
    authorizationDrawerState.zeroCoverageConfirmed = false;
    scheduleAuthorizationCoverageUpdate();
  });
  body.querySelector("#authzEndInput")?.addEventListener("input", (event) => {
    authorizationDrawerState.form.effectiveEnd = String(event.target.value || "");
    authorizationDrawerState.zeroCoverageConfirmed = false;
    scheduleAuthorizationCoverageUpdate();
  });
  body.querySelector("#authzFileInput")?.addEventListener("change", (event) => {
    const file = event.target.files && event.target.files[0];
    const check = validateAuthorizationFile(file);
    authorizationDrawerState.fileError = check.error;
    authorizationDrawerState.file = check.error ? null : file;
    renderAuthorizationDrawer();
  });
  body.querySelector("#authzCancelBtn")?.addEventListener("click", closeAuthorizationDrawer);
  body.querySelector("#authzDrawerForm")?.addEventListener("submit", (event) => {
    event.preventDefault();
    saveAuthorizationFromDrawer();
  });
  const coverageHost = body.querySelector("#authzCoverageHost");
  if (coverageHost) bindAuthorizationCoverageControls(coverageHost);
}

function validateAuthorizationFile(file) {
  if (!file) return { error: "" };
  const name = String(file.name || "").toLowerCase();
  if (!AUTHZ_ALLOWED_FILE_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return { error: "That file type is not supported. Attach a PDF, PNG, JPG or HEIC." };
  }
  if (Number(file.size || 0) > AUTHZ_MAX_FILE_BYTES) {
    return { error: `That file is ${authzFileSize(file.size)} — the limit is 10 MB.` };
  }
  return { error: "" };
}

// Mirrors validateAuthorizationInput() in backend/authorizationBoard.js so the
// biller is told before the round trip, never after.
function validateAuthorizationDrawer() {
  const form = authorizationDrawerState.form;
  const errors = [];
  const status = String(form.status || "").trim();
  if (!status) errors.push({ field: "status", message: "Status is required." });
  else if (!AUTHZ_RECORDABLE_STATUSES.includes(status)) errors.push({ field: "status", message: `Unknown status: ${status}` });
  if (status === AUTHZ_STATUS.APPROVED && !String(form.authNumber || "").trim()) {
    errors.push({ field: "authNumber", message: "Authorization number is required when status is Authorization Approved." });
  }
  if (status === AUTHZ_STATUS.WRITE_OFF && !String(form.note || "").trim()) {
    errors.push({ field: "note", message: "A note is required when writing off — say why the money is being abandoned." });
  }
  const start = authzParseDateOnly(form.effectiveStart);
  const end = authzParseDateOnly(form.effectiveEnd);
  if (start != null && end != null && end < start) {
    errors.push({ field: "effectiveEnd", message: "Effective end date must be on or after the start date." });
  }
  return errors;
}

function saveAuthorizationFromDrawer() {
  const errors = validateAuthorizationDrawer();
  authorizationDrawerState.errors = errors;
  if (errors.length) {
    authorizationDrawerState.errorBanner = "";
    renderAuthorizationDrawer();
    return;
  }
  // D2 — THE org fix. The save org is the PATIENT'S OWN org, taken from the
  // board card, never from the Company selector (which for an all-orgs principal
  // just defaulted to the first tenant in the catalog and made every save on the
  // other 13 companies link zero lines, silently).
  const orgId = String(authorizationDrawerState.orgId || "").trim();
  if (!orgId) {
    authorizationDrawerState.errorBanner = "This patient is not attributed to a company, so the authorization cannot be saved safely. Refresh the board and try again.";
    renderAuthorizationDrawer();
    return;
  }
  // Belt and braces: the drawer may have been open while the board refreshed
  // underneath it. Re-read the card and refuse if the resolved org drifted --
  // a mismatched org is exactly the silent zero-link this fix removes.
  const currentGroup = authorizationGroupByKey(authorizationDrawerState.groupKey);
  if (currentGroup && String(currentGroup.patient.orgId || "") && String(currentGroup.patient.orgId) !== orgId) {
    authorizationDrawerState.errorBanner = "This patient's company changed while the drawer was open. Close and reopen it so the authorization is filed against the right company.";
    renderAuthorizationDrawer();
    return;
  }
  const preview = authorizationCoveragePreview();
  // R3.6: zero coverage is legitimate (pre-recording a future auth) — ask once,
  // inline, never a hard block and never a modal over the drawer.
  if (!preview.effective.length && !authorizationDrawerState.zeroCoverageConfirmed && authorizationDrawerState.mode !== "edit") {
    authorizationDrawerState.zeroCoverageConfirmed = true;
    authorizationDrawerState.errorBanner = "This covers 0 on-hold lines today. Press Save again to record it anyway for future dates of service.";
    renderAuthorizationDrawer();
    return;
  }
  const form = authorizationDrawerState.form;
  const isUpdate = authorizationDrawerState.mode === "edit" && authorizationDrawerState.authorizationId;
  // A FRESH idempotency key per distinct save: a retry of THIS save is a
  // no-op replay server-side, but a genuinely new save is a new record.
  const idempotencyKey = `authz-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const body = {
    orgId,
    patientKey: authorizationDrawerState.patientKey,
    patientLabel: authorizationDrawerState.patientLabel,
    payerName: authorizationDrawerState.payerName,
    payerLabel: authorizationDrawerState.payerLabel,
    status: form.status,
    authNumber: String(form.authNumber || "").trim(),
    note: String(form.note || "").trim(),
    effectiveStart: form.effectiveStart,
    effectiveEnd: form.effectiveEnd,
    idempotencyKey
  };
  // Only send an explicit selection when the checklist actually narrows the set.
  // Omitting it means "every in-range line", which is the resolver's default and
  // keeps the common one-click save on the original, fully backward-compatible
  // range-only path. When present it is an INTERSECTION filter server-side, so
  // the count on the Save button is by construction the set that gets linked.
  // An UPDATE never re-resolves lines (the stored link rows are the recorded
  // selection), and the backend refuses selectedLines there rather than silently
  // ignoring it — so only a record carries a selection.
  if (!isUpdate && preview.selectionRestricted) body.selectedLines = preview.selectedLines;
  // What the Save button promised. The server refuses (nothing written) when it
  // can match none of these lines, instead of saving an empty authorization.
  if (!isUpdate) body.expectedLineCount = preview.effective.length;
  if (isUpdate) body.authorizationId = authorizationDrawerState.authorizationId;

  authorizationDrawerState.saving = true;
  authorizationDrawerState.errorBanner = "";
  renderAuthorizationDrawer();

  const pendingFile = authorizationDrawerState.file;
  const groupKey = authorizationDrawerState.groupKey;
  const patientKey = authorizationDrawerState.patientKey;
  const saveOrgId = orgId;
  const patientLabel = authorizationDrawerState.patientLabel;
  const optimisticStatus = form.status;
  const coveredLines = preview.effective.slice();

  enqueueAuthorizationCommand(isUpdate ? "update" : "record", body)
    .then((result) => {
      const authorizationId = String(result.authorizationId || body.authorizationId || "");
      // The server reports how much of an explicit selection it could NOT apply
      // (a line that moved out of On Hold, changed payer, or was never ours).
      const dropped = Math.max(0, Number(result.droppedCount || 0));
      // GUARD (#4, D2 fallout) — SILENT ZERO-LINK. The drawer promised "resolves
      // N lines"; if the server linked ZERO of them the authorization row exists
      // but nothing was resolved, and showing a green "Saved" would be a lie the
      // biller only discovers weeks later. An idempotent replay legitimately
      // links 0 (the first save already did the linking), so it is exempt.
      const promised = coveredLines.length;
      const linked = Number(result.linkedCount);
      if (!isUpdate && promised > 0 && Number.isFinite(linked) && linked === 0 && !result.idempotentReplay) {
        const failure = new Error(`Saved, but the server linked 0 of the ${promised} line${promised === 1 ? "" : "s"} this authorization was supposed to resolve. Nothing was taken off the tracker. Refresh the board and try again — if it repeats, send TMS this authorization id: ${authorizationId || "(none returned)"}.`);
        failure.authorizationZeroLink = true;
        throw failure;
      }
      // A PARTIAL link (linked < promised, but not zero) is reported too — the
      // biller must never be told N lines resolved when fewer did.
      const shortfall = (!isUpdate && Number.isFinite(linked) && !result.idempotentReplay)
        ? Math.max(0, promised - linked)
        : 0;
      if (pendingFile && authorizationId) {
        authorizationsState.pendingFileByAuthId[authorizationId] = pendingFile;
        authorizationsState.pendingFileOrgByAuthId[authorizationId] = saveOrgId;
        // Save the auth FIRST, then attach: a flaky or not-yet-deployed upload
        // endpoint must NEVER cost the biller the typed authorization number.
        return uploadAuthorizationFile(authorizationId, pendingFile, saveOrgId)
          .then(() => ({ authorizationId, uploaded: true, dropped, shortfall }))
          .catch(() => ({ authorizationId, uploaded: false, dropped, shortfall }));
      }
      return { authorizationId, uploaded: null, dropped, shortfall };
    })
    .then((outcome) => {
      authorizationDrawerState.saving = false;
      closeAuthorizationDrawer();
      // R3.7: the reward is the RESOLUTION. Fold the covered lines into a new
      // green auth row on the card, decrement the count, and let the card
      // re-sort out of the urgent zone — the biller watches the work disappear.
      if (!isUpdate) {
        applyOptimisticAuthorizationResolution({
          groupKey,
          authorizationId: outcome.authorizationId,
          body,
          coveredLines
        });
      }
      const pill = AUTHZ_STATUS_PILL_LABEL[optimisticStatus] || optimisticStatus;
      // Never pretend a partial selection applied in full: if the server could
      // not link some of the lines we named, say so and let the reconcile show
      // them back on the card.
      const droppedNote = outcome.dropped
        ? ` ${outcome.dropped} selected line${outcome.dropped === 1 ? " was" : "s were"} no longer eligible and stayed on the tracker.`
        : "";
      // #4 (partial case): the promise on the Save button was N lines. If the
      // server linked fewer, say the real number rather than repeating N.
      const shortfallNote = (outcome.shortfall && !outcome.dropped)
        ? ` Only ${coveredLines.length - outcome.shortfall} of ${coveredLines.length} line${coveredLines.length === 1 ? "" : "s"} were linked — the rest stayed on the tracker.`
        : "";
      authorizationsState.lastSave = {
        patientKey,
        orgId: saveOrgId,
        message: `${patientLabel} — ${pill}${coveredLines.length ? `, ${coveredLines.length - (outcome.shortfall || 0)} line${coveredLines.length - (outcome.shortfall || 0) === 1 ? "" : "s"} resolved` : ""}.${droppedNote}${shortfallNote}${outcome.uploaded === false ? " The file did not upload — retry it on the card." : ""}`
      };
      renderAuthorizations();
      showToast(outcome.uploaded === false
        ? `Saved — ${pill}. File upload failed; retry on the card.`
        : (outcome.dropped ? `Saved — ${pill}. ${outcome.dropped} selected line${outcome.dropped === 1 ? "" : "s"} could not be applied.`
          : (outcome.shortfall ? `Saved — ${pill}. Only ${coveredLines.length - outcome.shortfall} of ${coveredLines.length} lines linked.` : `Saved — ${pill}.`)));
      // Let the fold play, then reconcile against the server.
      window.setTimeout(() => {
        authorizationsState.foldingGroupKey = "";
        requestAuthorizationBoard({ rerender: false });
      }, AUTHZ_FOLD_MS + 250);
    })
    .catch((error) => {
      authorizationDrawerState.saving = false;
      // Drawer STAYS OPEN with every typed value intact (design §8).
      authorizationDrawerState.errorBanner = String(error && error.message ? error.message : "The authorization could not be saved. Nothing was lost — try again.");
      renderAuthorizationDrawer();
      if (error && error.authorizationZeroLink) {
        // The row DID save — it just resolved nothing. Saying "not saved" here
        // would be a second lie on top of the first; be precise instead, and
        // reconcile the board so the untouched lines reappear where they are.
        showToast("Saved, but 0 lines were resolved — nothing came off the tracker.");
        requestAuthorizationBoard({ rerender: false });
        return;
      }
      showToast("Authorization not saved — your entry is still here.");
    });
}

// Optimistic, server-confirmed: mutate the loaded board so the covered lines
// leave "Needs authorization" and appear under a new authorization row, then
// re-sort. requestAuthorizationBoard() reconciles moments later.
function applyOptimisticAuthorizationResolution({ groupKey = "", authorizationId = "", body = {}, coveredLines = [] } = {}) {
  const group = authorizationGroupByKey(groupKey);
  if (!group) return;
  const { patient, payer } = group;
  const coveredKeys = new Set(coveredLines.map(authzLineKey));
  const needs = payer.needsAuthorization || { lines: [] };
  const remaining = (needs.lines || []).filter((line) => !coveredKeys.has(authzLineKey(line)));
  const remainingDos = remaining.map((line) => authzParseDateOnly(line.dos)).filter((value) => value != null);
  payer.needsAuthorization = {
    lineCount: remaining.length,
    pricedLineCount: remaining.reduce((count, line) => count + (line.expected != null && (Number(line.expected) || 0) > 0 ? 1 : 0), 0),
    unknownExpectedLineCount: remaining.reduce((count, line) => count + (line.expected == null ? 1 : 0), 0),
    expectedTotal: remaining.reduce((sum, line) => (line.expected == null ? sum : sum + (Number(line.expected) || 0)), 0),
    dosStart: remainingDos.length ? new Date(Math.min(...remainingDos)).toISOString().slice(0, 10) : "",
    dosEnd: remainingDos.length ? new Date(Math.max(...remainingDos)).toISOString().slice(0, 10) : "",
    cptCodes: Array.from(new Set(remaining.map((line) => line.cpt).filter(Boolean))).sort(),
    lines: remaining
  };
  payer.authorizations = [{
    authorizationId,
    status: body.status,
    statusRank: 0,
    authNumber: body.authNumber || "",
    effectiveStartDate: authzIsoDate(body.effectiveStart),
    effectiveEndDate: authzIsoDate(body.effectiveEnd),
    cptCodes: Array.isArray(body.cptCodes) ? body.cptCodes : [],
    note: body.note || "",
    fileCount: 0,
    archived: false,
    lineCount: coveredLines.length,
    unknownExpectedLineCount: coveredLines.reduce((count, line) => count + (line.expected == null ? 1 : 0), 0),
    expectedTotal: coveredLines.reduce((sum, line) => (line.expected == null ? sum : sum + (Number(line.expected) || 0)), 0),
    dosStart: "",
    dosEnd: "",
    lines: coveredLines
  }].concat(Array.isArray(payer.authorizations) ? payer.authorizations : []);
  payer.worstStatus = remaining.length ? AUTHZ_STATUS.NEEDS_AUTHORIZATION : body.status;
  // Keep the payer's needs-auth basis in lockstep with its needsAuthorization
  // block. onHoldLineCount / onHoldExpectedTotal are deliberately NOT touched:
  // recording an authorization does not take the line off hold, so the server
  // will report the same inventory on the next refresh.
  payer.needsAuthorizationLineCount = remaining.length;
  payer.needsAuthorizationExpectedTotal = payer.needsAuthorization.expectedTotal;
  payer.needsAuthorizationPricedLineCount = payer.needsAuthorization.pricedLineCount;
  payer.needsAuthorizationUnknownExpectedLineCount = payer.needsAuthorization.unknownExpectedLineCount;
  payer.expectedTotal = payer.needsAuthorization.expectedTotal;

  // Recompute the patient rollup so the card re-sorts out of the urgent zone.
  // GUARDIAN FIX (2026-07-22): this used to overwrite patient.onHoldLineCount
  // with the NEEDS-AUTH sum while the server keeps sending the all-on-hold-lines
  // count — so the card dropped to "1 line" on save and jumped back to "2 lines"
  // on the next refresh with no user action. Both sides now maintain (and the
  // renderer reads) the needs-auth fields only.
  const payers = Array.isArray(patient.payers) ? patient.payers : [];
  patient.needsAuthorizationLineCount = payers.reduce((sum, entry) => sum + Number(entry.needsAuthorization?.lineCount || 0), 0);
  patient.needsAuthorizationExpectedTotal = payers.reduce((sum, entry) => sum + Number(entry.needsAuthorization?.expectedTotal || 0), 0);
  patient.needsAuthorizationPricedLineCount = payers.reduce((sum, entry) => sum + Number(entry.needsAuthorization?.pricedLineCount || 0), 0);
  patient.needsAuthorizationUnknownExpectedLineCount = payers.reduce((sum, entry) => sum + Number(entry.needsAuthorization?.unknownExpectedLineCount || 0), 0);
  patient.expectedTotal = patient.needsAuthorizationExpectedTotal;
  patient.worstStatus = payers.some((entry) => Number(entry.needsAuthorization?.lineCount || 0) > 0)
    ? AUTHZ_STATUS.NEEDS_AUTHORIZATION
    : body.status;
  const summary = authorizationsState.board.summary || {};
  // Header renders the needs-auth basis, so decrement THAT (and its $), not the
  // on-hold inventory the server will re-report unchanged.
  summary.needsAuthorizationLineCount = Math.max(0, authzNeedsLineCount(summary) - coveredLines.length);
  summary.needsAuthorizationExpectedTotal = Math.max(0, authzNeedsExpectedTotal(summary) - coveredLines.reduce((sum, line) => (line.expected == null ? sum : sum + (Number(line.expected) || 0)), 0));
  summary.needsAuthorizationUnknownExpectedLineCount = Math.max(0, authzNeedsUnknownCount(summary) - coveredLines.reduce((count, line) => count + (line.expected == null ? 1 : 0), 0));
  // The priced count has to move with the other two or the header reads
  // "N priced" against a line count that has already shrunk.
  summary.needsAuthorizationPricedLineCount = Math.max(0, authzNeedsPricedCount(summary) - coveredLines.reduce((count, line) => count + (line.expected != null && (Number(line.expected) || 0) > 0 ? 1 : 0), 0));
  summary.expectedTotal = summary.needsAuthorizationExpectedTotal;
  authorizationsState.board.summary = summary;

  authorizationsState.foldingGroupKey = groupKey;
  authorizationsState.justCreatedAuthorizationId = authorizationId;
  authorizationsState.openLineTables[groupKey] = false;
  window.setTimeout(() => {
    authorizationsState.justCreatedAuthorizationId = "";
  }, 1600);
}

// Same discipline as enqueueWorkflowCommand (app.js) — an auditable command
// with an idempotency key — but on the authorization routes, and WITHOUT
// sanitizeWorkflowBody's claim-shaped flattening (the authorization body
// carries a cptCodes array and is not a single-claim mutation).
function enqueueAuthorizationCommand(command, body) {
  const base = authorizationsApiBase();
  const commandId = `authz-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const route = `/api/authorizations/${command}`;
  if (!base || typeof fetch !== "function") {
    return Promise.reject(new Error("The portal is temporarily unavailable, so the authorization was not saved."));
  }
  return fetch(`${base}${route}`, {
    method: "POST",
    headers: authorizationsApiHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ ...body, workflowCommandId: commandId })
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok || (payload && payload.error)) {
      throw new Error(authorizationCommandMessage(response.status, payload));
    }
    return payload || {};
  });
}

function authorizationCommandMessage(status, payload = {}) {
  const errors = Array.isArray(payload && payload.errors) ? payload.errors : [];
  if (errors.length) return errors.map((entry) => entry.message || entry.field).join(" ");
  if (status === 401) return "Your session expired. Sign in again — your entry is still here.";
  if (status === 403) return "You are not allowed to record authorizations for this company.";
  if (status === 501) return "Authorization storage is not configured yet. TMS is finishing the setup.";
  // The server REFUSED rather than link a SUBSET of the lines the drawer
  // promised (the on-hold read hit its row cap). Nothing was written, so the
  // drawer stays open with every typed value intact — same as any save failure.
  if (String((payload && payload.error) || "") === "onhold_population_truncated") {
    return "There are too many on-hold lines to resolve this authorization safely right now, so nothing was saved. Your entry is still here — tell TMS the on-hold population exceeded the load limit.";
  }
  const message = String((payload && (payload.message || payload.error)) || "").trim();
  return message || `The authorization could not be saved (HTTP ${status}).`;
}

// ---------------------------------------------------------------------------
// File upload — POST /api/authorizations/:id/files (multipart). The endpoint
// may not be deployed yet; per design §8 a failure NEVER rolls back the saved
// authorization, it just parks the file slot in "upload failed — retry".
// ---------------------------------------------------------------------------
function uploadAuthorizationFile(authorizationId, file, orgId = "") {
  const base = authorizationsApiBase();
  // D2: the org travels with the authorization, never from the Company selector.
  const targetOrgId = String(orgId || authorizationsState.pendingFileOrgByAuthId[authorizationId] || "").trim();
  if (!base || typeof fetch !== "function" || typeof FormData !== "function") {
    authorizationsState.uploadFailures[authorizationId] = {
      fileName: file && file.name ? file.name : "",
      message: "File upload is unavailable in this session — the authorization is saved."
    };
    return Promise.reject(new Error("upload_unavailable"));
  }
  const payload = new FormData();
  payload.append("file", file, file.name);
  payload.append("orgId", targetOrgId);
  return fetch(`${base}/api/authorizations/${encodeURIComponent(authorizationId)}/files`, {
    method: "POST",
    headers: authorizationsApiHeaders(),
    body: payload
  }).then((response) => {
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      authorizationsState.uploadFailures[authorizationId] = {
        fileName: file.name,
        message: "Your session expired. Sign in again — the authorization is saved."
      };
      throw new Error("upload_failed");
    }
    if (!response.ok) {
      authorizationsState.uploadFailures[authorizationId] = {
        fileName: file.name,
        message: response.status === 404
          ? "File upload is not available yet — the authorization is saved."
          : `Upload failed (HTTP ${response.status}) — the authorization is saved.`
      };
      throw new Error("upload_failed");
    }
    delete authorizationsState.uploadFailures[authorizationId];
    delete authorizationsState.pendingFileByAuthId[authorizationId];
    delete authorizationsState.pendingFileOrgByAuthId[authorizationId];
    return true;
  }).catch((error) => {
    if (!authorizationsState.uploadFailures[authorizationId]) {
      authorizationsState.uploadFailures[authorizationId] = {
        fileName: file.name,
        message: "Upload failed — the authorization is saved. Retry the file when you can."
      };
    }
    throw error;
  });
}

async function downloadAuthorizationFileFromBoard({ authorizationId = "", authorizationFileId = "", filename = "", button = null } = {}) {
  const base = authorizationsApiBase();
  if (!base || !authorizationId || !authorizationFileId || typeof fetch !== "function") {
    showToast("That authorization file is not available in this session.");
    return false;
  }
  const priorText = button ? button.textContent : "";
  if (button) {
    button.disabled = true;
    button.textContent = "Downloading…";
  }
  try {
    const response = await fetch(`${base}/api/authorizations/${encodeURIComponent(authorizationId)}/files/${encodeURIComponent(authorizationFileId)}`, {
      headers: authorizationsApiHeaders()
    });
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok) throw new Error(`download_failed_${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = safeAuthorizationDownloadFilename(filename);
    anchor.hidden = true;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
    showToast("Authorization file downloaded.");
    return true;
  } catch (_error) {
    showToast("The authorization file could not be downloaded. Try again.");
    return false;
  } finally {
    if (button && button.isConnected) {
      button.disabled = false;
      button.textContent = priorText || "Download file";
    }
  }
}

function safeAuthorizationDownloadFilename(value) {
  const safe = String(value || "authorization-document")
    .replace(/[\\/\u0000-\u001f\u007f]+/g, "-")
    .trim()
    .slice(0, 180);
  return safe || "authorization-document";
}

// Choose file on a saved authorization: David's ticket, 2026-10-28. Same checks
// and same upload as the drawer; the org comes from the board row, never the
// Company selector.
function chooseAuthorizationFile(authorizationId, groupKey) {
  if (!hasPermission(PERMISSIONS.AUTHORIZATIONS_MANAGE)) {
    return denyAction(PERMISSIONS.AUTHORIZATIONS_MANAGE, "attach an authorization file");
  }
  const group = authorizationGroupByKey(groupKey);
  if (!group) return;
  const orgId = String(group.patient.orgId || group.payer.orgId || "");
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".pdf,.png,.jpg,.jpeg,.heic,application/pdf,image/png,image/jpeg,image/heic";
  input.addEventListener("change", () => {
    const file = input.files && input.files[0];
    if (!file) return;
    const check = validateAuthorizationFile(file);
    if (check.error) { showToast(check.error); return; }
    authorizationsState.pendingFileByAuthId[authorizationId] = file;
    authorizationsState.pendingFileOrgByAuthId[authorizationId] = orgId;
    showToast("Uploading the file…");
    uploadAuthorizationFile(authorizationId, file, orgId)
      .then(() => {
        showToast("File attached.");
        requestAuthorizationBoard();
      })
      .catch(() => {
        showToast("The file would not upload. The authorization is safe.");
        renderAuthorizations();
      });
  });
  input.click();
}

function retryAuthorizationFileUpload(authorizationId) {
  const file = authorizationsState.pendingFileByAuthId[authorizationId];
  if (!file) {
    showToast("Re-attach the file: open Edit on this authorization.");
    return;
  }
  showToast("Retrying the file upload…");
  uploadAuthorizationFile(authorizationId, file, authorizationsState.pendingFileOrgByAuthId[authorizationId] || "")
    .then(() => {
      showToast("File attached.");
      requestAuthorizationBoard();
    })
    .catch(() => {
      showToast("The file still would not upload. The authorization is safe.");
      renderAuthorizations();
    });
}

// ---------------------------------------------------------------------------
// Archive a patient (never a delete — design §1/§6).
// ---------------------------------------------------------------------------
// `cardKey` is the org||patient card key (D2) — never a bare patient key, or an
// archive on an all-orgs board could hide the same-named patient at the wrong
// company.
function archiveAuthorizationPatient(cardKey) {
  if (!hasPermission(PERMISSIONS.AUTHORIZATIONS_MANAGE)) {
    return denyAction(PERMISSIONS.AUTHORIZATIONS_MANAGE, "archive a patient");
  }
  const patient = authorizationPatients().find((entry) => authorizationPatientCardKey(entry) === String(cardKey));
  if (!patient) {
    showToast("That patient is no longer on the board — refresh and try again.");
    return;
  }
  const patientKey = String(patient.patientKey || "");
  const label = String(patient.patientLabel || patientKey);
  const reason = window.prompt(`Archive ${label}?\n\nThey stay on file (nothing is deleted) and reappear under "Show archived".\n\nWhy are you archiving them?`, "");
  if (reason === null) return;
  if (!String(reason).trim()) {
    showToast("An archive reason is required.");
    return;
  }
  // D2: the patient's OWN company, never the Company selector.
  const orgId = String(patient.orgId || "").trim();
  if (!orgId) {
    showToast("This patient is not attributed to a company, so they cannot be archived. Refresh the board.");
    return;
  }
  enqueueAuthorizationCommand("patient-archive", {
    orgId,
    patientKey,
    reason: String(reason).trim(),
    idempotencyKey: `authz-arch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  }).then(() => {
    showToast(`${label} archived.`);
    requestAuthorizationBoard();
  }).catch((error) => {
    showToast(error && error.message ? error.message : "The patient could not be archived.");
  });
}

// ---------------------------------------------------------------------------
// Drawer shell (reuses the .drawer / .drawer-overlay primitives). No modal is
// ever stacked over it (R3.8).
// ---------------------------------------------------------------------------
function openAuthorizationDrawerShell() {
  const drawer = document.getElementById("authDrawer");
  const overlay = document.getElementById("authDrawerOverlay");
  if (overlay) overlay.hidden = false;
  if (!drawer) return;
  drawer.classList.add("open");
  drawer.setAttribute("aria-hidden", "false");
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(() => {
      try { (drawer.querySelector("#authzStatusInput") || drawer).focus(); } catch (_e) {}
    });
  }
  overlay?.addEventListener("click", closeAuthorizationDrawer, { once: true });
  document.getElementById("closeAuthDrawerBtn")?.addEventListener("click", closeAuthorizationDrawer, { once: true });
  document.addEventListener("keydown", authorizationDrawerKeyTrap);
}

function authorizationDrawerKeyTrap(event) {
  const drawer = document.getElementById("authDrawer");
  if (!drawer || !drawer.classList.contains("open")) return;
  if (event.key === "Escape") {
    event.preventDefault();
    closeAuthorizationDrawer();
    return;
  }
  if (event.key !== "Tab") return;
  const focusables = Array.from(drawer.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"))
    .filter((node) => !node.disabled && node.offsetParent !== null);
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function closeAuthorizationDrawer() {
  const drawer = document.getElementById("authDrawer");
  const overlay = document.getElementById("authDrawerOverlay");
  drawer?.classList.remove("open");
  drawer?.setAttribute("aria-hidden", "true");
  if (overlay) overlay.hidden = true;
  document.removeEventListener("keydown", authorizationDrawerKeyTrap);
  window.clearTimeout(authorizationCoverageTimer);
  authorizationDrawerState.open = false;
  authorizationDrawerState.file = null;
}

function renderStatements() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("statements")) return;
  const host = document.getElementById("statementsBody");
  if (!host) return;
  if (!hasPermission(PERMISSIONS.STATEMENTS_VIEW)) {
    host.innerHTML = '<div class="panel-note">You do not have access to Patient Statements.</div>';
    return;
  }
  const live = typeof trackerApiEnabled === "function" ? trackerApiEnabled() : Boolean(typeof trackerApiBase === "function" && trackerApiBase());
  if (live) {
    const key = statementPreviewKey();
    // Complete previews already contain every patient; local controls reuse that
    // same scoped book. Server-paged or invalidated previews still fetch.
    const completeLocalData = statementPreviewState.status === "ready" && statementPreviewState.key
      && statementPreviewState.data?.complete === true && !statementPreviewState.data.patientPage
      && statementPreviewState.completeDataKey === statementPreviewKey(false);
    if (statementPreviewState.status === "idle" || (statementPreviewState.key !== key && !completeLocalData)) requestStatementPreview();
  } else if (statementPreviewState.status === "idle") {
    statementPreviewState.status = "unavailable";
  }
  host.innerHTML = statementsMarkup();
  bindStatementsControls(host);
  renderReportReadStatus();
}

// Which of the two report chassis is on screen. Internal report navigation
// always keeps the shared filter panel in place so switching report families
// cannot move the page vertically. The three client-report routes carry their
// own functional toolbar, so the shared controls stay visible but disabled on
// those routes. External client-only sessions retain their compact client
// chassis without the internal filter panel.
function setReportsAudienceChrome(showFilterBar, showClientHost, sharedFiltersEnabled = showFilterBar) {
  const filterPanel = els.reportFilterPanel || document.getElementById("reportFilterPanel");
  if (filterPanel) {
    filterPanel.hidden = !showFilterBar;
    filterPanel.classList.toggle("is-report-specific", showFilterBar && !sharedFiltersEnabled);
    const heading = filterPanel.querySelector(".report-filter-bar-text strong");
    if (heading) heading.textContent = sharedFiltersEnabled ? "Report filters" : "Shared filters unavailable for this report";
    renderReportFilterChrome(sharedFiltersEnabled);
    filterPanel.querySelectorAll("select, input, button").forEach((control) => {
      // The Show/Hide control is chrome, not a filter. Disabling it with the
      // rest left a reader who had the panel open with no way to close it on
      // the three client reports, and no way to re-open it afterwards.
      if (control === els.reportFilterToggleBtn || control.id === "reportFilterToggleBtn") return;
      control.disabled = !sharedFiltersEnabled;
      control.setAttribute("aria-disabled", control.disabled ? "true" : "false");
      control.title = sharedFiltersEnabled ? "" : "Use the report-specific filters in the toolbar below";
    });
  }
  const clientHost = els.clientReportsHost || document.getElementById("clientReportsHost");
  if (clientHost) clientHost.hidden = !showClientHost;
  // The Send Feedback strip belongs to the client chassis only. Internal staff
  // reach Feedback from the nav, so on the three client reports it was a second
  // route to the same place, alone in ~150px of empty band above the report.
  const clientHead = document.getElementById("clientPivotHead");
  if (clientHead) clientHead.hidden = !clientOnlyReportsSession();
  // THE BILLER WORK QUEUE IS TORN DOWN, NOT JUST HIDDEN, WHENEVER THE CLIENT
  // CHASSIS IS ON SCREEN. #reportWorkQueuePanel is a SIBLING of #reportsBody,
  // so clearing the body does not touch it, and renderReportWorkQueuePanel()
  // never runs on the client branch - which means a SuperAdmin sitting on Open
  // Work who switches role to Client would have left a panel of internal claim
  // rows standing, still visible, under the client report catalog. Hiding alone
  // is not enough either: the rows would remain in the DOM for anyone who
  // opened devtools. The lanes and the drill-down are emptied.
  if (!showFilterBar) {
    const queuePanel = els.reportWorkQueuePanel || document.getElementById("reportWorkQueuePanel");
    if (queuePanel) queuePanel.hidden = true;
    ["reportWorkQueueLanes", "reportWorkQueueQuickFilters", "reportQueueDrilldown", "reportWorkQueueSendAlert"].forEach((id) => {
      const node = document.getElementById(id);
      if (node) node.innerHTML = "";
    });
  }
}


function reportColumnChooserStorageKey(view, tableIndex, labels) {
  return `tms-report-columns:v1:${view}:${tableIndex}:${labels.join("|")}`;
}

function initializeReportColumnChoosers(host = els.reportsBody) {
  if (!host || typeof document === "undefined") return;
  Array.from(host.querySelectorAll("table")).forEach((table, tableIndex) => {
    if (table.dataset.reportColumnChooserReady === "true") return;
    const headRows = Array.from(table.tHead?.rows || []);
    const leafRow = headRows[headRows.length - 1];
    if (!leafRow || !leafRow.cells.length) return;
    const leafCells = Array.from(leafRow.cells);
    const upperLabels = [];
    headRows.slice(0, -1).forEach((row) => {
      let offset = 0;
      Array.from(row.cells).forEach((cell) => {
        const span = Math.max(1, Number(cell.colSpan || 1));
        const label = String(cell.textContent || "").replace(/\s+/g, " ").trim();
        for (let index = offset; index < offset + span; index += 1) if (label) upperLabels[index] = label;
        offset += span;
      });
    });
    const labels = leafCells.map((cell, index) => String(cell.textContent || upperLabels[index] || `Column ${index + 1}`).replace(/\s+/g, " ").trim() || `Column ${index + 1}`);
    const storageKey = reportColumnChooserStorageKey(reportArState.view, tableIndex, labels);
    let hidden = [];
    try { hidden = JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch (_error) { hidden = []; }
    const hiddenSet = new Set(Array.isArray(hidden) ? hidden.filter((index) => Number.isInteger(index) && index >= 0 && index < leafCells.length) : []);
    const wrap = table.closest(".table-wrap") || table.parentElement;
    if (!wrap) return;
    const chooser = document.createElement("details");
    chooser.className = "report-column-chooser reports-no-print";
    chooser.innerHTML = `<summary>Columns <span data-report-column-count></span></summary><div class="report-column-options">${labels.map((label, index) => `<label><input type="checkbox" data-report-column-index="${index}"${hiddenSet.has(index) ? "" : " checked"}> <span>${escapeHtml(label)}</span></label>`).join("")}</div>`;
    wrap.parentNode.insertBefore(chooser, wrap);

    const apply = () => {
      const visible = leafCells.map((_cell, index) => !hiddenSet.has(index));
      if (!visible.some(Boolean)) { hiddenSet.delete(0); visible[0] = true; }
      leafCells.forEach((cell, index) => { cell.hidden = !visible[index]; });
      Array.from(table.tBodies).forEach((body) => Array.from(body.rows).forEach((row) => applyReportColumnVisibilityToRow(row, visible)));
      if (table.tFoot) Array.from(table.tFoot.rows).forEach((row) => applyReportColumnVisibilityToRow(row, visible));
      headRows.slice(0, -1).forEach((row) => applyReportColumnVisibilityToBandRow(row, visible));
      chooser.querySelector("[data-report-column-count]").textContent = `(${visible.filter(Boolean).length}/${visible.length})`;
      try { localStorage.setItem(storageKey, JSON.stringify([...hiddenSet].sort((a, b) => a - b))); } catch (_error) { /* local persistence is best effort */ }
    };
    chooser.querySelectorAll("[data-report-column-index]").forEach((input) => input.addEventListener("change", () => {
      const index = Number(input.dataset.reportColumnIndex);
      if (input.checked) hiddenSet.delete(index); else hiddenSet.add(index);
      if (hiddenSet.size >= leafCells.length) { hiddenSet.delete(index); input.checked = true; }
      apply();
    }));
    table.dataset.reportColumnChooserReady = "true";
    apply();
  });
}

function applyReportColumnVisibilityToRow(row, visible) {
  const cells = Array.from(row.cells);
  if (cells.length === 1 && Number(cells[0].colSpan || 1) > 1) { cells[0].colSpan = Math.max(1, visible.filter(Boolean).length); return; }
  cells.forEach((cell, index) => { cell.hidden = visible[index] === false; });
}

function applyReportColumnVisibilityToBandRow(row, visible) {
  let offset = 0;
  Array.from(row.cells).forEach((cell) => {
    if (!cell.dataset.reportOriginalColspan) cell.dataset.reportOriginalColspan = String(cell.colSpan || 1);
    const span = Math.max(1, Number(cell.dataset.reportOriginalColspan) || 1);
    const shown = visible.slice(offset, offset + span).filter(Boolean).length;
    cell.hidden = shown === 0;
    if (shown) cell.colSpan = shown;
    offset += span;
  });
}

function renderReports(options = {}) {
  syncReportReadContext();
  renderReportReadStatus();
  // This hosted report retains its search controls during reads. The general
  // publication guard defers focused inputs and drills, including their explicit
  // Search/Enter/selection actions; applying it here strands requests at idle.
  const stablePatientSearch = reportArState.view === "patientResponsibility" && trackerApiEnabled();
  // A current read must paint its slow, success or error state even while a
  // filter keeps focus; publication deferral must not leave Loading on screen.
  if (options.settledRead !== true && !stablePatientSearch && typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("reports")) return;
  const host = els.reportsBody;
  if (!host) return;
  renderClientOonRateCutsCard();
  host.classList?.remove?.("reports-body--work-queue");
  host.classList?.remove?.("reports-body--client-report");

  // AUDIENCE BRANCH, and it is deliberately the FIRST thing this function does.
  //
  // Payments & Exports is gone, so a Client / ClientBiller session now opens
  // THIS view. What they get is exactly what that tab gave them - the three
  // client reports in #clientReportsHost - and nothing else: no filter bar, no
  // family switcher, no biller work queue, and no /api/reports/dataset request.
  // Returning HERE rather than gating further down is the whole point: every
  // branch below reads reportArState and fires the internal dataset lane, and a
  // REJECTED internal request is still an internal request from a session that
  // holds no grant for it.
  //
  // The test is view:reports, NEVER client:payments. SuperAdmin's grant set is
  // allPermissions by construction, so asking "do they hold the client grant"
  // would send every admin down the client branch.
  if (!hasPermission(PERMISSIONS.VIEW_REPORTS)) {
    host.innerHTML = "";
    setReportsAudienceChrome(false, true);
    // renderClientView(), not renderClientReportsView(): the wrapper carries the
    // try/catch that turns a local render error into a notice card. Skipping it
    // would leave a blank panel on an external client's only money surface.
    renderClientView();
    return;
  }
  // FILTER CHOICES BELONG TO THE PRINCIPAL ON SCREEN.
  //
  // renderReportFilterOptions() is called inside the per-lane branches further
  // down, so any render that does not reach one of them - a book this principal
  // cannot read, a deferred publication render, a report with no definition -
  // left the PREVIOUS principal's option lists standing in the bar. Measured on
  // the local lane 2026-09-22: switching from SuperAdmin to a Demo Client A
  // session left Cigna, UHC Oxford, United, Dr. Stein, Dr. Levy, Riverbend NJ
  // and Riverbend NY in Payer / Provider / Location - four other practices'
  // payers, clinicians and sites, named, on a client-facing surface. The Tenant
  // select was already scoped; these four were not.
  resetReportFilterOptionsOnPrincipalChange();
  setReportsAudienceChrome(true, false);
  const reportDateRange = reportFilterValues();
  if (reportDateRange.fromDate && reportDateRange.toDate && reportDateRange.fromDate > reportDateRange.toDate) {
    host.innerHTML = `<section class="panel ar-report-panel" role="alert"><h3>Check the date range</h3><p>Choose a From date that is on or before the To date.</p></section>`;
    return;
  }

  // The work queue lives outside #reportsBody and is driven from here, before
  // any of the per-view branches return, so every report switch keeps it in
  // step with the active family.
  renderReportWorkQueuePanel();

  // Collections and Turnaround both ignore provider, location, aging, and
  // claim status. Disable those controls on either view; Collections alone
  // relabels the date inputs because its endpoint filters payment date.
  const collectionsView = reportArState.view === "collections";
  const turnaroundView = reportArState.view === "turnaround";
  const limitedFilterView = collectionsView || turnaroundView;
  [els.reportProviderFilter, els.reportLocationFilter, els.reportAgingFilter, els.reportStatusFilter, els.reportMatchFilter, els.reportFollowUpDueFilter, els.reportActionFilter, els.reportServiceMonthFilter, els.reportProcedureFilter, els.reportNetworkFilter].forEach((control) => {
    if (!control) return;
    control.disabled = limitedFilterView;
    control.title = collectionsView
      ? "Not used by Collections — filter by tenant, payer, and paid date"
      : turnaroundView ? "Not used by Turnaround Time — filter by tenant, payer, and service date" : "";
  });
  // THE CARD IS THE CONTROL NOW, so the ignored filters have to say so on the
  // card and not only on the <select> hidden inside it - otherwise Collections
  // presents ten working-looking checklists its endpoint never receives.
  ["provider", "location", "aging", "status", "match", "followUpDue", "action", "serviceMonth", "procedure", "network"].forEach((key) => {
    const panel = document.querySelector(`[data-slicer-panel="${filterCardId("report", key)}"]`);
    if (!panel) return;
    panel.classList.toggle("is-unavailable", limitedFilterView);
    panel.title = collectionsView
      ? "Not used by Collections — filter by tenant, payer, and paid date"
      : turnaroundView ? "Not used by Turnaround Time — filter by tenant, payer, and service date" : "";
    if (limitedFilterView && panel.open) panel.open = false;
    panel.querySelectorAll("input, button").forEach((node) => { node.disabled = limitedFilterView; });
  });
  if (els.reportFromDateLabel) els.reportFromDateLabel.textContent = collectionsView ? "From Paid Date" : "From DOS";
  if (els.reportToDateLabel) els.reportToDateLabel.textContent = collectionsView ? "To Paid Date" : "To DOS";

  if (!hasPermission(PERMISSIONS.VIEW_FINANCIALS)) {
    host.innerHTML = `
      <section class="panel ar-report-panel">
        <div class="ar-report-head"><div><h3>Reports</h3>
        <p>Financial reporting requires the Financials permission. Ask an administrator for access.</p></div></div>
      </section>`;
    return;
  }

  // Today's Work Queue is the Open Work family's landing tab, and its body is
  // only the tier-two tabs: the queue itself renders into #reportWorkQueuePanel,
  // a sibling of #reportsBody (index.html), so it lands directly beneath them
  // instead of below four other panels and 400px of scroll. It returns BEFORE
  // the mirror dataset request below on purpose - the queue is its own API lane
  // and needs none of that data, and letting the request run would stack a
  // "Loading report data" panel on top of the one view that exists to show the
  // queue. Any sibling tab still loads the dataset the moment it is picked.
  if (reportArState.view === "workQueue") {
    host.classList?.add?.("reports-body--work-queue");
    host.innerHTML = renderReportViewSwitcher();
    bindReportViewSwitcher();
    return;
  }

  // The three reports that came from Payments & Exports render into
  // #clientReportsHost, below the switcher, off their own endpoints
  // (/api/client/reports/claims, /api/client/payments/checks). Like the work
  // queue above, this returns BEFORE requestReportDataset: pulling 60,000 rows
  // nothing on screen reads is a 45-second tax for no output. The shared filter
  // panel stays in place for internal navigation, but its controls are disabled
  // because these three reports carry their own functional toolbar.
  if (clientReportKeyForReportView(reportArState.view)) {
    host.classList?.add?.("reports-body--client-report");
    host.innerHTML = renderReportViewSwitcher();
    bindReportViewSwitcher();
    // A client never switches to an internal report family, so the disabled
    // panel ("Shared filters unavailable for this report") was only a dead
    // block above the report's own working toolbar (2026-10-13).
    setReportsAudienceChrome(!isExternalClientRole(state?.role), true, false);
    renderClientView();
    return;
  }

  // HOSTED REPORT PREFIX GATE (2026-08-12). These views still have only the
  // capped browser dataset implementation. A 60k prefix is not a population,
  // so it must never be rendered as a complete report. Keep local/demo useful,
  // but fail closed in hosted mode until each view has a prepared server model,
  // export and drill-down bound to one generation.
  // Lanes are owned by backend/reportLaneContract.js. Adding a button without
  // registering it there fails npm run test:snapshot.
  const cappedBrowserReportViews = new Set(["ar", "expectedVsPaid", "denials", "performance", "clientAction", "billDateMonthlyAging", "billDateAging", "rateIssues", "payerClass", "reviewQueue"]);
  const preparedSnapshotReportViews = new Set(["ar", "expectedVsPaid", "billDateMonthlyAging", "billDateAging", "denials", "clientAction", "payerClass", "rateIssues", "performance", "reviewQueue", "rollforward", "monthlySummary", "patientResponsibility", "cptPayer"]);
  // Prepared hosted views return before the shared local/dataset refresh below.
  // Populate their top filter bar from the full-book server facets here too.
  if (trackerApiEnabled()) {
    renderReportFilterOptions([]);
    lockReportTenantFilterForScope([]);
  }
  if (trackerApiEnabled() && preparedSnapshotReportViews.has(reportArState.view)) {
    const switcher = renderReportViewSwitcher();
    const markup = renderPreparedSnapshotReport(reportArState.view);
    if (reportArState.view === "patientResponsibility") renderPatientResponsibilityBody(host, switcher, markup);
    else host.innerHTML = switcher + markup;
    bindReportViewSwitcher(); bindPreparedSnapshotReport(); return;
  }
  if (trackerApiEnabled() && cappedBrowserReportViews.has(reportArState.view)) {
    host.innerHTML = `${renderReportViewSwitcher()}<section class="panel ar-report-panel"><div class="ar-report-head"><div><h3>Complete report migration in progress</h3><p role="status">This report is not shown from a capped browser prefix. Its full-population prepared server query, export, and drill-down must be available before hosted totals can be displayed.</p></div></div></section>`;
    bindReportViewSwitcher();
    return;
  }
  // DEFINITION-DRIVEN REPORTS.
  //
  // Anything with a definition and a line-grain reader renders from the shared column
  // set — the tracker's bands, formats and drillable rows — instead of its own bespoke
  // five-to-eight-column table. Rows are summed from the engine's own per-line output, so
  // no figure is recomputed; the seven reports below all foot to the same book and differ
  // only by GROUP BY, which is the property that made them disagree before.
  //
  // Sits after the hosted-lane branches on purpose: when the tracker API is on, the
  // server-backed path still wins and this never runs.
  // ⚠️ BROWSER LANE ONLY. loadSourceData() returns claims: [] whenever trackerApiEnabled(),
  // so this branch fired in hosted mode with an EMPTY book and returned early — rendering
  // rollforward, monthlySummary, cptPayer and patientResponsibility blank in production and
  // leaving the server-rollup renderers below it as dead code. The earlier comment claimed
  // the server path still won; it did for the nine prepared views and not for these four.
  // A cell-drill opened the older row-drilldown panel (reportRowDrilldownState). The
  // definition lane must YIELD while that is active, or it re-renders the summary over the
  // drill the user just opened — the click would look like it did nothing.
  if (!trackerApiEnabled() && !(typeof reportRowDrilldownState === "object" && reportRowDrilldownState && reportRowDrilldownState.active) && renderDefinitionReportFromClaims && reportDefinitionUi(reportArState.view) && typeof (reportDefinitionUi(reportArState.view).grainOf) === "function" && reportDefinitionUi(reportArState.view).browserLineGrainRenderer !== true) {
    // This branch returns before the shared filter refresh below. Refresh the
    // local report population and scope lock here too, otherwise changing from
    // a one-org Client session to an all-org internal role can leave the Tenant
    // filter visibly and functionally locked from the prior principal.
    const definitionSourceClaims = getClaims();
    renderReportFilterOptions(definitionSourceClaims);
    lockReportTenantFilterForScope(definitionSourceClaims);
    const definitionClaims = getReportClaims(definitionSourceClaims);
    // A drill path replaces the report body until the user climbs back out.
    if (reportDrillState.view === reportArState.view && reportDrillState.path.length) {
      host.innerHTML = `${renderReportViewSwitcher()}${renderReportDrillPanel()}`;
      bindReportViewSwitcher();
      bindReportDrillControls();
      return;
    }
    const definitionMarkup = renderDefinitionReportFromClaims(reportArState.view, definitionClaims);
    if (definitionMarkup) {
      host.innerHTML = `${renderReportViewSwitcher()}${definitionMarkup}`;
      bindReportViewSwitcher();
      bindPreparedSnapshotReport();
      // Export/print live on bindArReportControls; without this the buttons render dead.
      bindArReportControls();
      bindReportDrillControls();
      bindReportSlicers(definitionClaims);
      bindPatientResponsibilityControls();
      bindReportPager();
      bindReportKpiPicker();
      // A report carrying a cell-drill contract needs ITS binder too, or the trigger renders
      // with the right key and nothing listens to it.
      if (reportDefinitionUi(reportArState.view)?.cellDrillAttr && typeof bindCptPayerReportControls === "function") bindCptPayerReportControls();
      return;
    }
  }
  const preparedOrDirectReportView = trackerApiEnabled() && new Set(["collections", "turnaround"]).has(reportArState.view);

  // Live tracker-API mode: loadSourceData() intentionally returns no claims
  // while live paging is on, so reports source scopedClaims from the
  // principal-scoped /api/reports/dataset cache. Local/demo mode keeps the
  // original getClaims() path untouched.
  if (trackerApiEnabled() && !preparedOrDirectReportView) {
    const datasetKey = reportDatasetKey();
    if (reportDatasetState.status === "idle" || reportDatasetState.key !== datasetKey) {
      requestReportDataset(datasetKey, reportDatasetRequestBody());
    }
    if (reportDatasetTimedOut(datasetKey)) {
      failReportDatasetPending(datasetKey, "Report data did not finish loading in time. Retry, or narrow the date range.");
    }
    if (reportDatasetState.status === "error") {
      const reportErrorText = String(reportDatasetState.error || "").trim() || "Report data failed to load for an unknown reason.";
      const reportErrorSentence = /^report data/i.test(reportErrorText) ? reportErrorText : `Report data failed to load: ${reportErrorText}`;
      host.innerHTML = `
    ${renderReportViewSwitcher()}
    <section class="panel ar-report-panel">
      <div class="ar-report-head">
        <div><h3>Reports</h3>
        <p role="alert">${escapeHtml(reportErrorSentence)}</p></div>
        <div class="ar-report-actions reports-no-print">
          <button type="button" class="primary-button" data-report-dataset-retry>Retry</button>
        </div>
      </div>
    </section>`;
      bindReportViewSwitcher();
      host.querySelector("[data-report-dataset-retry]")?.addEventListener("click", () => {
        resetReportDataset();
        renderReports();
      });
      return;
    }
    if (reportDatasetState.status !== "ready") {
      host.innerHTML = `
    ${renderReportViewSwitcher()}
    <section class="panel ar-report-panel">
      <div class="ar-report-head">
        <div><h3>Reports</h3>
        <p role="status">Loading report data. A wide date range can take a minute or two. If it does not populate, retry.</p></div>
        <div class="ar-report-actions reports-no-print">
          <button type="button" class="primary-button" data-report-dataset-retry>Retry</button>
        </div>
      </div>
    </section>`;
      bindReportViewSwitcher();
      host.querySelector("[data-report-dataset-retry]")?.addEventListener("click", () => {
        resetReportDataset();
        renderReports();
      });
      return;
    }
  }
  // ONE claim source for the screen AND every report CSV export. renderReports
  // and arExportCsv/evpExportCsv/denialsExportCsv/rateIssuesExportCsv all read
  // this same call, so an export cannot silently diverge from the surface it
  // exports. Enforced by verify_frontend_report_export_source_contract.js.
  const scopedClaims = preparedOrDirectReportView ? [] : reportSourceClaims();
  const claims = getReportClaims(scopedClaims);
  const localEmptyNote = reportLocalEmptyFilterNote(reportLocalHiddenFilterBook(scopedClaims, claims));
  renderReportFilterOptions(scopedClaims);
  lockReportTenantFilterForScope(scopedClaims);
  // Off scopedClaims, NOT the filtered set two lines down — see
  // reportWriteOffBasisKnown. Filtering to a client with no follow-up actions
  // must not blank a column the rest of the book can answer.
  reportWriteOffKnown = reportWriteOffBasisKnown(scopedClaims);
  // Report-row drill-down renders over the SAME filtered snapshot the
  // aggregates use, so active report filters carry through for free. Leaving
  // the launching view (via the switcher) drops the drill-down.
  if (reportRowDrilldownState.active && reportArState.view !== reportRowDrilldownState.view) {
    reportRowDrilldownState.active = false;
    reportRowDrilldownState.claimId = null;
  }
  if (reportRowDrilldownState.active) {
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderReportRowDrilldown(claims, reportRowDrilldownState, reportRowDrilldownState.footerOverride)}`;
    bindReportViewSwitcher();
    bindReportRowDrilldown();
    return;
  }

  if (reportArState.view === "collections") {
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderCollectionsReport(collectionsReportModel())}
  `;
    bindReportViewSwitcher();
    bindCollectionsReportControls();
    return;
  }

  if (reportArState.view === "turnaround") {
    if (hostedReportDrillState.view === "turnaround" && (hostedReportDrillState.sessionKey !== preparedSnapshotSessionKey() || hostedReportDrillState.queryKey !== JSON.stringify(turnaroundReportFilters()))) resetHostedReportDrill();
    if (hostedReportDrillState.view === "turnaround" && hostedReportDrillState.root) { host.innerHTML = `${renderReportViewSwitcher()}${hostedReportDrillHtml("turnaround")}`; bindReportViewSwitcher(); bindPreparedSnapshotReport(); return; }
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderTurnaroundReport(turnaroundReportModel())}
  `;
    bindReportViewSwitcher();
    bindTurnaroundReportControls();
    return;
  }

  // Expected vs Paid deliberately shares the non-Collections filter path
  // above: all shared filters stay enabled and the date range stays DOS.
  if (reportArState.view === "expectedVsPaid") {
    const evpRows = claims.map(evpClaimRow);
    const evpMatrix = evpBuildMatrix(evpRows, reportEvpState.dimension);
    const evpSorted = evpSortRows(evpRows, reportEvpState.sortKey, reportEvpState.sortDir);
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderEvpKpiHeader(evpReportTotals(evpRows), claims.length)}
    ${renderEvpMatrix(evpMatrix, reportEvpState.dimension, localEmptyNote)}
    ${renderEvpDetail(evpSorted, localEmptyNote)}
  `;
    bindReportViewSwitcher();
    bindEvpReportControls();
    return;
  }

  // Denials shares the same client-side path as Expected vs Paid: shared
  // filters stay enabled, the date range stays DOS, and nothing is fetched.
  // Rows are the filtered claim set narrowed to isDeniedClaim — the
  // documented denial contract used everywhere else in the portal.
  if (reportArState.view === "denials") {
    const deniedRows = claims.filter(isDeniedClaim).map(denialsClaimRow);
    if (!deniedRows.length) {
      host.innerHTML = `
    ${renderReportViewSwitcher()}
    <section class="panel ar-report-panel">
      <div class="ar-report-head"><div><h3>Denials</h3>
      <p>${escapeHtml(denialsEmptyMessage(claims))}</p></div></div>
    </section>`;
      bindReportViewSwitcher();
      return;
    }
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderDenialsKpiHeader(denialsReportTotals(deniedRows), claims.length)}
    ${renderDenialsMatrix(denialsBuildMatrix(deniedRows, reportDenialsState.dimension), reportDenialsState.dimension)}
    ${renderDenialsReasons(denialsReasonRows(deniedRows))}
    ${renderDenialsDetail(denialsSortRows(deniedRows, reportDenialsState.sortKey, reportDenialsState.sortDir))}
  `;
    bindReportViewSwitcher();
    bindDenialsReportControls();
    return;
  }

  // Performance (2026-06-11): port of the TrackerUpload Collections Overview.
  // Shares the Expected vs Paid client-side path — all shared filters stay
  // enabled and the date range stays DOS — then applies its own partition:
  // pre-billing and client-action-pending claims out, claims aging under 60
  // days behind the per-view lens toggle.
  if (reportArState.view === "performance") {
    const perfRows = performancePartition(claims).map(performanceClaimRow);
    if (!perfRows.length) {
      host.innerHTML = `
    ${renderReportViewSwitcher()}
    <section class="panel ar-report-panel">
      <div class="ar-report-head">
        <div><h3>Performance</h3>
        <p>No billed claims in the current filters${reportPerformanceState.includeYoungClaims ? "" : " — claims aging under 60 days are hidden by default"}. Pre-billing and client-action-pending claims are excluded by design.</p></div>
        <div class="ar-report-actions">${renderPerformanceLensToggle()}</div>
      </div>
    </section>`;
      bindReportViewSwitcher();
      bindPerformanceControls();
      return;
    }
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderPerformanceKpiHeader(performanceKpis(perfRows), claims.length)}
    ${renderPerformanceMonthlyChart(performanceMonthRows(perfRows))}
    ${renderPerformanceTopPayers(performancePayerRows(perfRows))}
  `;
    bindReportViewSwitcher();
    bindPerformanceControls();
    return;
  }

  // Client Action (2026-06-11): port of the TrackerUpload "Client Action
  // Pending" tab — revenue held up by client-side blockers (EVV,
  // credentialing, authorization). The exact inverse of Performance's CAP
  // exclusion via the shared isClientActionClaim predicate; no aging lens --
  // held claims of any age matter. Shared filters stay enabled, DOS basis.
  if (reportArState.view === "clientAction") {
    const capRows = clientActionPartition(claims).map(clientActionClaimRow);
    if (!capRows.length) {
      host.innerHTML = `
    ${renderReportViewSwitcher()}
    <section class="panel ar-report-panel">
      <div class="ar-report-head">
        <div><h3>Client Action</h3>
        <p>No claims are currently held for client action in the current filters.</p></div>
      </div>
    </section>`;
      bindReportViewSwitcher();
      return;
    }
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderClientActionKpiHeader(clientActionKpis(capRows), claims.length)}
    ${renderClientActionMonthlyChart(clientActionMonthSeries(capRows))}
    ${renderClientActionReasonBreakdown(clientActionReasonRows(capRows))}
    ${renderClientActionTopPayers(performancePayerRows(capRows))}
    ${renderClientActionByClaimTable(claims)}
  `;
    bindReportViewSwitcher();
    bindClientActionControls();
    return;
  }

  // Monthly and Weekly Bill-Date Aging summaries.
  // Both share the Expected vs Paid
  // client-side path (shared filters enabled, DOS date basis); the filtered
  // claim set grouped into rolling 7-day bands by days since original bill
  // date. Bucket bands mirror backend/ctTrackerAgingBuckets.js EXACTLY.
  if (["billDateMonthlyAging", "billDateAging"].includes(reportArState.view)) {
    if (!claims.length) {
      host.innerHTML = `
    ${renderReportViewSwitcher()}
    <section class="panel ar-report-panel">
      <div class="ar-report-head"><div><h3>${reportArState.view === "billDateMonthlyAging" ? "Bill Date Monthly Aging Summary" : "Bill Date Weekly Aging Summary"}</h3>
      <p>${escapeHtml(localEmptyNote)}</p></div></div>
    </section>`;
      bindReportViewSwitcher();
      return;
    }
    const granularity = reportArState.view === "billDateMonthlyAging" ? "monthly" : "weekly";
    const bdaGroups = new Map();
    claims.forEach((claim) => {
      const fin = claimFinancialView(claim);
      const bucket = granularity === "monthly" ? billDateMonthlyGroupFor(claim) : billDateWeeklyBucketFor(billDateAgingDaysOrNull(claim));
      let g = bdaGroups.get(bucket);
      if (!g) { g = { bucket, claims: 0, expected: 0, paid: 0, writeOff: 0 }; bdaGroups.set(bucket, g); }
      g.claims += 1;
      g.expected += moneyNumber(fin.expectedReimbursement);
      g.paid += moneyNumber(fin.totalInsurancePaid);
      g.writeOff += moneyNumber(fin.writeOffAmount);
    });
    const bdaRows = Array.from(bdaGroups.values()).map((g) => ({
      bucket: g.bucket,
      claims: g.claims,
      expected: Math.round(g.expected * 100) / 100,
      paid: Math.round(g.paid * 100) / 100,
      writeOff: Math.round(g.writeOff * 100) / 100,
      balance: Math.round((g.expected - g.paid) * 100) / 100
    })).sort(granularity === "monthly"
      ? (a, b) => (billDateMonthlySortKey(b.bucket) - billDateMonthlySortKey(a.bucket)) || String(b.bucket).localeCompare(String(a.bucket))
      : (a, b) => (billDateWeeklyBucketStart(a.bucket) - billDateWeeklyBucketStart(b.bucket)) || String(a.bucket).localeCompare(String(b.bucket)));
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderBillDateAgingReport(bdaRows, claims.length, granularity === "monthly" ? "Bill Month" : "Aging Bucket", granularity, localEmptyNote)}
  `;
    bindReportViewSwitcher();
    bindBillDateAgingReportControls();
    return;
  }

  if (reportArState.view === "rollforward") {
    const groupBy = ["dosMonth"];
    const rollup = reportRollupFor(groupBy);
    if (trackerApiEnabled() && !rollup) {
      host.innerHTML = `${renderReportViewSwitcher()}${renderReportRollupGate(groupBy, "Expected vs Paid Rollforward")}`;
      bindReportViewSwitcher();
      bindReportRollupRetry(groupBy);
      return;
    }
    const model = rollup ? rollforwardModelFromRollup(rollup) : rollforwardBuildRows(rollforwardLineRows(claims));
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${reportRollupNoteHtml(rollup, groupBy)}
    ${renderRollforwardReport(model, rollup ? rollup.claimCount : claims.length)}
  `;
    bindReportViewSwitcher();
    bindRollforwardReportControls();
    return;
  }

  if (reportArState.view === "monthlySummary") {
    const groupBy = ["dosMonth"];
    const rollup = reportRollupFor(groupBy);
    if (trackerApiEnabled() && !rollup) {
      host.innerHTML = `${renderReportViewSwitcher()}${renderReportRollupGate(groupBy, "Monthly Summary")}`;
      bindReportViewSwitcher();
      bindReportRollupRetry(groupBy);
      return;
    }
    const model = rollup ? rollforwardModelFromRollup(rollup) : rollforwardBuildRows(rollforwardLineRows(claims));
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${reportRollupNoteHtml(rollup, groupBy)}
    ${renderMonthlySummaryReport(model, rollup ? rollup.claimCount : claims.length)}
  `;
    bindReportViewSwitcher();
    bindMonthlySummaryReportControls();
    return;
  }

  if (reportArState.view === "cptPayer") {
    const groupBy = ["procedure", "payer"];
    const rollup = reportRollupFor(groupBy);
    if (trackerApiEnabled() && !rollup) {
      host.innerHTML = `${renderReportViewSwitcher()}${renderReportRollupGate(groupBy, "Reimbursement by CPT × Payer")}`;
      bindReportViewSwitcher();
      bindReportRollupRetry(groupBy);
      return;
    }
    const model = rollup ? cptPayerModelFromRollup(rollup) : cptPayerBuildRows(cptPayerLineRows(claims));
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${reportRollupNoteHtml(rollup, groupBy)}
    ${renderCptPayerReport(model, rollup ? rollup.claimCount : claims.length)}
  `;
    bindReportViewSwitcher();
    bindCptPayerReportControls();
    return;
  }

  if (reportArState.view === "rateIssues") {
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderRateIssuesReport(rateIssueBuildRows(rateIssueLineRows(claims)), claims.length)}
  `;
    bindReportViewSwitcher();
    bindRateIssuesReportControls();
    return;
  }

  if (reportArState.view === "payerClass") {
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderPayerClassificationReport(payerClassBuildRows(payerClassLineRows(claims)), claims.length)}
  `;
    bindReportViewSwitcher();
    bindPayerClassificationControls();
    return;
  }

  if (reportArState.view === "reviewQueue") {
    host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderReviewQueueReport(reviewQueueBuildRows(reviewQueueLineRows(claims)), claims.length)}
  `;
    bindReportViewSwitcher();
    bindReviewQueueControls();
    return;
  }

  const rows = claims.map(arClaimRow);
  const totals = arReportTotals(rows);
  const matrix = arBuildMatrix(rows, reportArState.dimension);
  const sortedRows = arSortRows(rows, reportArState.sortKey, reportArState.sortDir);

  host.innerHTML = `
    ${renderReportViewSwitcher()}
    ${renderArKpiHeader(totals, claims.length)}
    ${renderArAgingCharts(rows)}
    ${renderArMatrix(matrix, reportArState.dimension, totals)}
    ${renderArDetail(sortedRows, localEmptyNote)}
  `;
  bindReportViewSwitcher();
  bindArReportControls();
}

function arClaimRow(claim) {
  const fin = claimFinancialView(claim);
  const days = arSubmissionAgingDays(claim);
  const bucket = arAgingBucketFor(days);
  return {
    claimId: claim.claimId || "",
    tenant: claim.client || "—",
    payer: claim.primaryPayer || claim.secondaryPayer || "—",
    provider: claim.renderingProvider || "—",
    status: claim.claimStatus || "—",
    agingDays: days,
    bucketKey: bucket.key,
    bucketLabel: bucket.label,
    charges: moneyNumber(fin.charges),
    expected: moneyNumber(fin.expectedReimbursement),
    paid: moneyNumber(fin.totalInsurancePaid),
    writeOff: moneyNumber(fin.writeOffAmount),
    balance: moneyNumber(claim.balance),
    insuranceAr: moneyNumber(fin.expectedOutstanding),
    expectedKnown: reportExpectedKnown(fin)
  };
}

// Aging anchor = claim submission date. Prefer the tracker-computed
// billDateAging (days since Original Bill Date); fall back to computing from
// originalBillDate, then to the DOS-based aging bucket.
function arSubmissionAgingDays(claim) {
  const billDays = billDateAgingDaysOrNull(claim);
  return billDays == null ? agingDays(claim) : billDays;
}

// Bill-date evidence only: null when there is no valid bill age and no usable
// Original Bill Date. Number(null) and Number("") are both 0, so absence is tested
// before coercion; zero stays a valid asserted age. A future bill date is invalid
// evidence, not age 0. Bill-date reports must never borrow the DOS age -- an
// unbilled or undated line stays "(blank)" instead of gaining an invented bill period.
function billDateAgingDaysOrNull(claim) {
  const raw = claim ? claim.billDateAging : null;
  if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
    const billAging = Number(raw);
    if (Number.isFinite(billAging) && billAging >= 0) return Math.round(billAging);
  }
  const billedIso = claim && claim.originalBillDate ? normalizeDateValue(claim.originalBillDate) : "";
  const todayIso = easternDateOnlyString();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(billedIso) || !todayIso) return null;
  // Eastern business day minus the bill calendar day, in pure UTC day math so a DST
  // transition cannot skip or double a day.
  const [by, bm, bd] = billedIso.split("-").map(Number);
  const [ty, tm, td] = todayIso.split("-").map(Number);
  const days = Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(by, bm - 1, bd)) / 86400000);
  return days >= 0 ? days : null;
}

function arParseDate(value) {
  if (!value) return null;
  // Normalize first so MM/DD/YYYY and datetime strings from live exports
  // parse deterministically instead of relying on engine-specific
  // Date.parse behavior.
  const iso = normalizeDateValue(value);
  if (iso) {
    const ts = Date.parse(iso);
    if (Number.isFinite(ts)) return ts;
  }
  const ts = Date.parse(String(value).trim());
  return Number.isFinite(ts) ? ts : null;
}

// NOT BILLED IS NOT AN AGE. A claim carrying no bill date has not gone out yet — a lot of
// it On Hold — so aging it from the date of service reports money as chased when nobody
// has been asked for it, and buries the unbilled inventory inside 0-30.
//
// Keyed on the BILL DATE, not on claim status: "no bill date" is a fact on the record,
// while On Hold is a separate field that can disagree with it. Anything unbilled lands
// here whatever its status says.
const AR_NOT_BILLED_BUCKET = { key: "Not Billed", label: "Not Billed", min: null, max: null };

function arClaimIsUnbilled(claim) {
  if (!claim) return false;
  // Number(null) and Number("") are BOTH 0, not NaN, so a missing bill-date aging read
  // as "billed, zero days old" and landed in 0-30 — the exact burial this bucket exists
  // to stop. Absence is tested before the value is coerced.
  const rawAging = claim.billDateAging;
  const hasAging = rawAging !== null && rawAging !== undefined
    && String(rawAging).trim() !== ""
    && Number.isFinite(Number(rawAging)) && Number(rawAging) >= 0;
  if (hasAging) return false;
  return arParseDate(claim.originalBillDate) == null;
}

async function sendUserProfileChangeRequest(user = {}, role = "", scope = {}, payerNames = [], followUpActionPolicy = null) {
  const userKey = String(user.userId || user.username || user.email || "").trim().toLowerCase();
  if (!userKey) return { ok: false, code: "user_key_required", body: {} };
  if (!portalInviteApiReady()) return { ok: false, code: "offline", body: {} };
  try {
    const apiBase = portalInviteApiBase();
    const apiToken = workflowApiToken() || trackerApiToken();
    const response = await fetch(`${apiBase}/api/users/profile`, {
      method: "POST",
      cache: "no-store",
      headers: { "content-type": "application/json", authorization: `Bearer ${apiToken}` },
      body: JSON.stringify({
        userKey,
        role,
        expectedProfileVersion: String(user.profileVersion || ""),
        ...(scope.changeReason ? { changeReason: scope.changeReason } : {}),
        scope: {
          orgIds: normalizeUserScopeList(scope.orgIds),
          payerNames: normalizeUserScopeList(payerNames),
          assignedOnly: Boolean(user.assignedOnly),
          followUpPolicies: isExternalClientRole(role) ? [] : oversightPolicies(scope.followUpPolicies === undefined ? user.followUpPolicies : scope.followUpPolicies),
          followUpActionPolicy: followUpActionPolicyForRole(followUpActionPolicy || user.followUpActionPolicy || user.followUpActionAccess || {}, role)
        }
      })
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.updated !== true || String(body.account?.userKey || "").toLowerCase() !== userKey) {
      return { ok: false, code: String(body.reason || body.error || `http_${response.status}`), status: response.status, body };
    }
    return { ok: true, status: response.status, body };
  } catch (error) {
    return { ok: false, code: "unreachable", status: 0, body: {}, error };
  }
}

// Claim-level banding. arAgingBucketFor takes DAYS and cannot see that the days it was
// handed were derived from a service date rather than a bill date.
function arAgingBucketForClaim(claim) {
  if (arClaimIsUnbilled(claim)) return AR_NOT_BILLED_BUCKET;
  return arAgingBucketFor(arSubmissionAgingDays(claim));
}

function arAgingBucketFor(days) {
  // Clamp bad data (negative aging) into 0-30 instead of letting the
  // fallback silently park it in 120+.
  // DIVERGENCE, DELIBERATE (guardian finding N4): every OTHER implementation of this band
  // mapping — backend/ctTrackerAgingBuckets.arAgingBucket, the SQL CASE, and the ordinal
  // helpers — yields "(blank)" for blank/negative/unparseable aging. This one (and
  // clientAgingDenialHeatmapModel's `buckets.includes(band) ? band : buckets[0]` fallback)
  // instead files it in the YOUNGEST band. Pre-existing, and in the safe direction for a
  // client-facing surface (an unknown never inflates the aged/at-risk totals), so it is NOT
  // changed here. Do not read this as agreement with the backend banding.
  const safeDays = Number.isFinite(days) ? Math.max(0, days) : 0;
  return AR_AGING_BUCKETS.find((bucket) => safeDays >= bucket.min && safeDays <= bucket.max)
    || AR_AGING_BUCKETS[AR_AGING_BUCKETS.length - 1];
}

// Weekly bill-date-aging bucket — mirrors backend/ctTrackerAgingBuckets.js
// billDateWeeklyBucket EXACTLY (CT-TRACKER build_lite_snapshot.py:929-940):
//   <=7 -> "0-7"; <=112 -> rolling 7-day band "{start}-{start+6}" where
//   start=8+((d-8)//7)*7; <=120 -> "113-120"; else "121+". Blank/invalid -> "(blank)".
// Monthly bill-date group key = inferred bill month (today - days since original
// bill date). Blank/invalid aging remains visibly unclassified.
function billDateMonthlyGroupFor(claim) {
  const days = billDateAgingDaysOrNull(claim);
  if (days === null || days === undefined || !Number.isFinite(Number(days)) || Number(days) < 0) return "(blank)";
  const d = Math.max(0, Math.round(Number(days)));
  // Snapshot day = the EASTERN business day (see todayStart / backend tmsTime), then pure UTC
  // calendar math on that Y-M-D so no DST transition can skip or double a day.
  const iso = easternDateOnlyString();
  if (!iso) return "(blank)";
  const [year, month, day] = iso.split("-").map(Number);
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() - d);
  return base.toISOString().slice(0, 7);
}

// Sort current/recent bill months first and keep "(blank)" last.
function billDateMonthlySortKey(label) {
  if (!label || label === "(blank)") return Number.NEGATIVE_INFINITY;
  const ts = Date.parse(label + "-01T00:00:00Z");
  return Number.isFinite(ts) ? ts : Number.NEGATIVE_INFINITY;
}

// Monthly and Weekly are separate report views; the local/demo toggle navigates
// between them so it matches the hosted report switcher.
function bindBillDateAgingReportControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-bda-gran]").forEach((btn) => {
    btn.addEventListener("click", () => {
      reportArState.view = btn.getAttribute("data-bda-gran") === "monthly" ? "billDateMonthlyAging" : "billDateAging";
      renderReports();
    });
  });
  host.querySelectorAll("[data-bda-drill]").forEach((el) => {
    const open = () => {
      const bucket = el.getAttribute("data-bda-drill");
      const gran = reportArState.view === "billDateMonthlyAging" ? "monthly" : "weekly";
      drillIntoReportRow({
        view: reportArState.view,
        label: `${gran === "monthly" ? "Bill month" : "Bill-date bucket"} ${bucket}`,
        dims: [`Bucket: ${bucket}`],
        predicate: (claim) => (gran === "monthly" ? billDateMonthlyGroupFor(claim) : billDateWeeklyBucketFor(billDateAgingDaysOrNull(claim))) === bucket
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
}

function billDateWeeklyBucketFor(days) {
  if (days === null || days === undefined || !Number.isFinite(Number(days)) || Number(days) < 0) return "(blank)";
  const d = Math.max(0, Math.round(Number(days)));
  if (d <= 7) return "0-7";
  if (d <= 112) { const start = 8 + Math.floor((d - 8) / 7) * 7; return `${start}-${start + 6}`; }
  if (d <= 120) return "113-120";
  return "121+";
}

// Sort key for a weekly bucket label: "(blank)" last, "N+" -> N, "A-B" -> A.
function billDateWeeklyBucketStart(label) {
  if (label === "(blank)") return Number.POSITIVE_INFINITY;
  if (/^\d+\+$/.test(label)) return Number(label.slice(0, -1));
  const m = label.match(/^(\d+)-\d+$/); return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
}

function arReportTotals(rows) {
  return rows.reduce((t, r) => {
    t.claims += 1;
    t.charges += r.charges;
    t.expected += r.expected;
    t.paid += r.paid;
    t.writeOff += r.writeOff;
    t.insuranceAr += r.insuranceAr;
    // Known basis: only claims whose every line carries a rate. Charges and
    // payments are NOT split — they are real money on every claim.
    if (r.expectedKnown) {
      t.expectedKnownBasis += r.expected;
      t.paidOnKnown += r.paid;
      t.writeOffOnKnown += r.writeOff;
      t.insuranceArKnownBasis += r.insuranceAr;
    } else {
      t.unratedClaims += 1;
    }
    return t;
  }, { claims: 0, charges: 0, expected: 0, paid: 0, writeOff: 0, insuranceAr: 0, unratedClaims: 0,
       expectedKnownBasis: 0, paidOnKnown: 0, writeOffOnKnown: 0, insuranceArKnownBasis: 0 });
}

function arDimensionKey(row, dimension) {
  if (dimension === "rateSource") return row.rateSource || "—";
  if (dimension === "client") return row.tenant || "—";
  if (dimension === "provider") return row.provider || "—";
  return row.payer || "—";
}

function arDimensionLabel(dimension) {
  return dimension === "client" ? "Client" : dimension === "provider" ? "Provider" : dimension === "rateSource" ? "Rate source" : "Payer";
}

function arBuildMatrix(rows, dimension) {
  const groups = new Map();
  rows.forEach((row) => {
    const key = arDimensionKey(row, dimension);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        claims: 0,
        total: 0,
        buckets: AR_AGING_BUCKETS.reduce((acc, bucket) => { acc[bucket.key] = 0; return acc; }, {})
      });
    }
    const group = groups.get(key);
    group.buckets[row.bucketKey] += row.insuranceAr;
    group.total += row.insuranceAr;
    group.claims += 1;
  });
  const list = Array.from(groups.values()).sort((a, b) => b.total - a.total);
  const columnTotals = AR_AGING_BUCKETS.reduce((acc, bucket) => {
    acc[bucket.key] = list.reduce((sum, group) => sum + group.buckets[bucket.key], 0);
    return acc;
  }, {});
  const grand = list.reduce((sum, group) => sum + group.total, 0);
  return { list, columnTotals, grand };
}

function arSortRows(rows, key, dir) {
  const col = AR_DETAIL_COLUMNS.find((c) => c.key === key) || AR_DETAIL_COLUMNS[AR_DETAIL_COLUMNS.length - 1];
  const mult = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (col.type === "text") return String(a[col.key]).localeCompare(String(b[col.key])) * mult;
    return ((Number(a[col.key]) || 0) - (Number(b[col.key]) || 0)) * mult;
  });
}

function arPercent(num, den) {
  return den > 0 ? `${Math.round((num / den) * 100)}%` : "N/A";
}

// Export and print controls are REPORT_EXPORT-gated. Full Client intentionally
// keeps that grant under the owner's current policy; RedactedClient does not.
function reportExportActionsHtml(buttons) {
  if (!hasPermission(PERMISSIONS.REPORT_EXPORT)) return "";
  return `<div class="ar-report-actions reports-no-print">${buttons}</div>`;
}

// CAN THIS DATASET EVEN KNOW A WRITE-OFF? (2026-08-10)
//
// A write-off is not a number anybody stores — it is DERIVED from the follow-up
// ACTION, identically here and in CT-TRACKER (financialCalculationEngine ~558;
// build_lite_snapshot.py:3456-3459): if the action is a write-off action the
// amount is max(0, expected − patient responsibility − paid); otherwise it is a
// genuine, asserted zero.
//
// So the follow-up action is the INPUT, and on the Reports lane that input is
// currently absent. gold.MainTracker_VW ships [Follow up Action] NULL on all
// 245,415 lines (measured 2026-08-10), and the reports dataset — unlike the All
// Data grid — carries no merge of the portal's own follow-up overlay. The engine
// therefore asks "is this a write-off action?" of nothing, every line takes the
// else branch, and every write-off figure on this tab renders a confident $0.
// NORTH_STAR.md §6: an absent input reads as unknown, never as $0. This is the
// same rule reportExpectedKpiCard applies to an absent rate, and it is applied
// through the same glyph so the tab has one vocabulary for "not known".
//
// WHY THE TEST IS ON THE DATA, NOT ON THE SOURCE MODE. A `warehouse ? blank`
// check would keep blanking the day gold starts carrying actions, or the day the
// overlay merge lands — someone would have to find this line and delete it.
// Asking the ROWS whether any of them carries the input means the column turns
// itself back on the moment the input exists, with no second edit and no risk of
// a stale suppression outliving its cause.
//
// AND IT IS A DATASET-LEVEL QUESTION, NOT A PER-ROW ONE. One row with no
// follow-up action is the normal case on a healthy book, and its $0 write-off is
// real — blanking it would be this same defect pointed the other way (see
// verify_report_expected_unknown_contract.js §5). Only a set in which NOTHING
// anywhere carries the input is blind. Read off the FULL scoped dataset, before
// the user's filters, so narrowing to one client cannot manufacture a false
// unknown.
function reportWriteOffBasisKnown(claims) {
  const present = (value) => Boolean(String(value == null ? "" : value).trim());
  return (Array.isArray(claims) ? claims : []).some((claim) => {
    if (present(firstPresent(claim.action, claim.followUpAction))) return true;
    if (moneyNumber(claim.writeOffAmount) !== 0) return true;
    return (Array.isArray(claim.services) ? claim.services : []).some((service) => (
      present(firstPresent(service.action, service.followUpAction))
      || moneyNumber(service.writeOffAmount) !== 0
    ));
  });
}

// Recomputed once per Reports render, off the unfiltered scoped dataset.
let reportWriteOffKnown = true;

function reportWriteOffMoney(value) {
  return reportWriteOffKnown ? money(value) : MONEY_UNKNOWN_TEXT;
}

function reportWriteOffPercent(numerator, denominator) {
  return reportWriteOffKnown ? arPercent(numerator, denominator) : MONEY_UNKNOWN_TEXT;
}

// The panel-level sentence. Copy rules are the Reports notice's (ruled 2026-08-03,
// §3 of verify_report_expected_unknown_contract): state a FACT about the figure,
// never diagnose a pipeline, and never hand the customer an instruction they
// cannot act on. Reuses .report-expected-notice rather than minting a class --
// same meaning, already styled, and no new name to trip the global [class*=]
// rules in styles.css.
function reportWriteOffNoticeHtml() {
  if (reportWriteOffKnown) return "";
  return `<p class="report-expected-notice">Write-off amounts are being finalized for this workspace, so write-off figures show ${MONEY_UNKNOWN_TEXT} rather than $0.</p>`;
}

function renderArKpiHeader(totals, claimCount) {
  // Ratios denominated in expected use the KNOWN basis on BOTH sides, so the
  // numerator and denominator always describe the same set of claims. The
  // gross rate stays all-claims: charges and payments are true on every claim.
  const net = arPercent(totals.paidOnKnown, totals.expectedKnownBasis);
  const netOfWriteOffs = arPercent(totals.paidOnKnown, totals.expectedKnownBasis - totals.writeOffOnKnown);
  const gross = arPercent(totals.paid, totals.charges);
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Accounts Receivable — Money Overview</h3>
        <p>${formatNounCount(claimCount, "claim")} in the active filter · insurance AR aged from claim submission date.</p>
      </div>
      ${reportExportActionsHtml(`
        <button type="button" class="ghost-button" data-ar-export="matrix">Matrix → CSV</button>
        <button type="button" class="ghost-button" data-ar-export="detail">Detail → CSV</button>
        <button type="button" class="primary-button" data-ar-print>Print / PDF</button>`)}
    </div>
    <div class="ar-kpi-grid ar-aging-kpis">
      ${reportExpectedKpiCard("Net Collection Rate", net, reportPartialDetail(`${money(totals.paidOnKnown)} paid ÷ ${money(totals.expectedKnownBasis)} expected`), "primary", totals,
        `${money(totals.paid)} collected · awaiting expected values`)}
      ${reportWriteOffKnown
        ? reportExpectedKpiCard("Net of Write-offs", netOfWriteOffs, `Write-offs ${money(totals.writeOffOnKnown)} removed from expected`, "", totals,
          `Write-offs ${money(totals.writeOff)} posted · awaiting expected values`)
        // THIS TILE IS THE QUIET ONE. Its arithmetic is
        // paidOnKnown ÷ (expectedKnown − writeOffOnKnown), so a manufactured
        // write-off of 0 does not make it look broken — it makes it equal the
        // Net Collection Rate tile beside it, to the decimal, while claiming to
        // be a different measurement. Two tiles agreeing reads as corroboration.
        : arKpiCard("Net of Write-offs", MONEY_UNKNOWN_TEXT, "Needs write-off amounts — not the same as the rate on the left", "unknown")}
      ${arKpiCard("Gross Collection Rate", gross, reportPartialDetail(`${money(totals.paid)} paid ÷ ${money(totals.charges)} billed`))}
      ${reportExpectedKpiCard("Outstanding Insurance AR", totals.insuranceArKnownBasis,
        // DELIBERATELY NOT MARKED PARTIAL while the server confirms every
        // open row survived the cap — see the block comment above this grid.
        reportOpenArComplete() ? "Expected − Paid still owed by payers" : reportPartialDetail("Expected − Paid still owed by payers"), "accent", totals,
        "Awaiting expected values — not $0")}
      ${arKpiCard("Collected", money(totals.paid), reportPartialDetail("Insurance payments posted"))}
      ${reportExpectedKpiCard("Expected", totals.expectedKnownBasis, reportPartialDetail("Fee-schedule allowed reimbursement"), "", totals,
        "Awaiting expected values for this workspace")}
    </div>
    ${reportPartialTotalsNoticeHtml()}
    ${reportExpectedNoticeHtml(totals)}
    ${reportWriteOffNoticeHtml()}
  </section>`;
}

// Current / prior month keys, in the same YYYY-MM shape the collections
// report emits. Lived on the deleted biller Home; kept because the client
// landing's "Came In This Month" band reads the same two months.
function homeMonthKey(offset = 0) {
  const now = todayStart();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// CLIENT HOME — the practice owner's landing (CLIENT_EXPERIENCE_V2_SPEC §2)
// ---------------------------------------------------------------------------
// Answers "how is my money doing / what is stuck on me" in about ten seconds,
// one org at a time, READ-ONLY. Four bands, existing tokens only — deliberately
// no filters, no exports, no charts: this is a practice owner glancing, not a
// biller working. Every primitive here is reused from the biller Home/Reports
// surfaces so the portal keeps ONE visual language.
//
// PHI: the client claims feed carries no patient names, CPT codes or internal
// notes, and none are rendered here. Band 2 speaks in dollars and reasons only.
function renderClientHome() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("clientHome")) return;
  const host = document.getElementById("clientHomeBody");
  if (!host) return;
  if (isRedactedClientRole()) {
    const claims = clientReportSourceClaims();
    host.innerHTML = `<section class="panel home-summary"><div class="summary-strip-scope">Your practice</div><p>Insurance-side financial totals are private in this view. Patient responsibility and Patient Statement payments and balances remain available.</p></section>${renderClientHomeWaitingOnYou(claims, clientReportClaimsLoadState())}<div class="home-band2"><div class="home-rail">${renderClientHomeStatementsCard()}</div></div>`;
    bindClientHomeControls(host);
    return;
  }
  if (trackerApiEnabled()) {
    requestClientReportClaims();
    requestClientPaymentChecks();
  }
  const claims = clientReportSourceClaims();
  const claimLoad = clientReportClaimsLoadState();
  const collectionsModel = collectionsReportModel();
  const months = collectionsModel.status === "ok" && collectionsModel.totals && Array.isArray(collectionsModel.totals.months)
    ? collectionsModel.totals.months
    : [];
  host.innerHTML = `
    ${renderClientHomeMoney(claims, collectionsModel.status, months, claimLoad)}
    ${renderClientHomeWaitingOnYou(claims, claimLoad)}
    <div class="home-band2">
      ${renderClientHomeRecentPayments()}
      <div class="home-rail">${renderClientHomeStatementsCard()}</div>
    </div>
  `;
  bindClientHomeControls(host);
}

// Band 1 — "Your money at a glance". Plain-English labels: a practice owner
// does not think in "outstanding insurance AR", they think "what am I owed".
function renderClientHomeMoney(claims, collectionsStatus, months, claimLoad = "ok") {
  const owedKnown = claimLoad === "ok";
  const owed = owedKnown
    ? claims.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0)
    : null;
  const owedLabel = owedKnown ? money(owed) : (claimLoad === "pending" ? "…" : "—");
  const owedDetail = owedKnown
    ? (clientReportClaimsApiState.truncated
      ? "At least this much on the claims we have loaded so far — we are on it."
      : "What payers still owe on your claims — we are on it.")
    : (claimLoad === "pending"
      ? "Loading what payers still owe. This is not $0."
      : "We could not load what payers still owe. This is not $0.");
  const monthMap = new Map(months.map((row) => [String(row.month || ""), moneyNumber(row.collected)]));
  const cameIn = collectionsStatus === "ok"
    ? money(monthMap.get(homeMonthKey(0)) || 0)
    : collectionsStatus === "pending" ? "…" : "—";
  const cameInDetail = collectionsStatus === "ok"
    ? `Last month ${money(monthMap.get(homeMonthKey(-1)) || 0)}`
    : collectionsStatus === "pending" ? "Loading your payments" : "Payment data is not connected yet";
  const onItsWay = clientHomeArrivingSoonTotal();
  // THE FOURTH BUCKET. `.home-kpi-grid` has always been repeat(4, ...) with three
  // cards in it — the slot was drawn and the bucket was never filled.
  //
  // REPORTING_TRUTH_CONTRACT.md §2: a headline number splits four ways, and
  // written-off is the one that must be visible and OUTSIDE "still owed".
  // Without it this screen invites the owner to read "came in + still owed" as
  // the whole picture, which understates what TMS collected against what was
  // ever collectable — the exact way a report makes TMS look like it failed at
  // work it actually did.
  //
  // claimFinancialView already nets writeOffAmount out of expected, so "Still
  // Owed To You" above is correctly write-off-free; this only surfaces the
  // amount that was removed. Follows `owed` for load state: unknown is never $0.
  const writtenOff = owedKnown
    ? claims.reduce((sum, claim) => sum + moneyNumber(claimFinancialView(claim).writeOffAmount), 0)
    : null;
  const writtenOffLabel = owedKnown ? money(writtenOff) : (claimLoad === "pending" ? "…" : "—");
  const writtenOffDetail = owedKnown
    ? (writtenOff > 0
      ? "Balances we stopped pursuing — already removed from what you are owed above."
      : "Nothing written off on your loaded claims.")
    : (claimLoad === "pending"
      ? "Loading what was written off. This is not $0."
      : "We could not load what was written off. This is not $0.");
  return `
  <section class="panel home-summary">
    <div class="summary-strip-scope">Your practice${clientHomeAsOfLabel()}</div>
    <div class="ar-kpi-grid home-kpi-grid">
      ${arKpiCard("Still Owed To You", owedLabel, owedDetail, "accent")}
      ${arKpiCard("Came In This Month", cameIn, cameInDetail)}
      ${arKpiCard("On Its Way To Your Bank", onItsWay.label, onItsWay.detail)}
      ${arKpiCard("Written Off", writtenOffLabel, writtenOffDetail)}
    </div>
  </section>`;
}

// Band 2 — "Waiting on you". The ONLY band that asks the owner to act, so the
// dollars held are the loudest thing in each row and every row carries one
// plain sentence saying what to do. Reuses the same clientActionReasonRows
// grouping the Client Action report uses, so the two can never disagree.
function renderClientHomeWaitingOnYou(claims, claimLoad = "ok") {
  if (claimLoad !== "ok") {
    return `
  <section class="panel client-home-waiting">
    <h3>Waiting on you</h3>
    <p class="empty-state">${claimLoad === "pending"
      ? "Checking what is waiting on you. This is not an empty inbox."
      : "We could not load what is waiting on you. That is not the same as nothing to do."}</p>
  </section>`;
  }
  const rows = clientHomeWaitingRows(claims);
  if (!rows.length) {
    return `
  <section class="panel client-home-waiting">
    <h3>Waiting on you</h3>
    <p class="empty-state">Nothing is waiting on you right now — we are on everything.</p>
  </section>`;
  }
  const body = rows.map((row) => `
      <button type="button" class="client-home-waiting-row" data-client-home-go="${escapeHtml(clientHomeWaitingDestination(row.reason))}">
        <div class="client-home-waiting-reason">
          <b>${escapeHtml(clientHomeReasonLabel(row.reason))}</b>
          <span>${escapeHtml(clientHomeReasonAdvice(row.reason))}</span>
        </div>
        <div class="client-home-waiting-money">
          ${isRedactedClientRole() ? "" : `<b>${money(moneyNumber(row.insAr))}</b>`}
          <span>${formatNounCount(row.claims || 0, "claim")}</span>
        </div>
      </button>`).join("");
  return `
  <section class="panel client-home-waiting">
    <h3>Waiting on you</h3>
    <p class="client-home-waiting-lede">These are held up on something only your practice can clear.</p>
    ${body}
  </section>`;
}

// Defensive: the reason grouping lives on the Client Action report path and its
// input shape has changed before. A landing page must degrade to "nothing
// waiting" rather than throw and blank the whole surface.
function clientHomeWaitingRows(claims) {
  try {
    const actionClaims = (Array.isArray(claims) ? claims : []).filter((claim) => isClientActionClaim(claim));
    if (!actionClaims.length) return [];
    if (typeof clientActionRows !== "function" || typeof clientActionReasonRows !== "function") return [];
    const rows = clientActionReasonRows(clientActionRows(actionClaims));
    return Array.isArray(rows) ? rows : [];
  } catch (_) {
    return [];
  }
}

// Band 3 — the five most recent payments, then hand off to the full view.
function renderClientHomeRecentPayments() {
  const model = clientPaymentsChecksModel([]);
  if (model.loading) {
    return `
  <section class="panel client-home-payments">
    <div class="ar-report-head">
      <div><h3>Recent payments</h3></div>
      <div class="ar-report-actions"><button type="button" class="small-button" data-client-home-go="reports" data-client-home-report="payments">See all payments</button></div>
    </div>
    <p class="empty-state">Checking recent payments. This is not an empty list.</p>
  </section>`;
  }
  if (model.error) {
    return `
  <section class="panel client-home-payments">
    <div class="ar-report-head">
      <div><h3>Recent payments</h3></div>
      <div class="ar-report-actions"><button type="button" class="small-button" data-client-home-go="reports" data-client-home-report="payments">See all payments</button></div>
    </div>
    <p class="empty-state">We could not load recent payments. That is not the same as no money coming in.</p>
  </section>`;
  }
  const rows = (Array.isArray(model.rows) ? model.rows : []).slice(0, 5);
  const body = rows.length
    ? rows.map((row) => `
      <div class="client-home-payment-row">
        <span>${escapeHtml(formatDate(row.paidDate || row.paymentDate || row.date || ""))}</span>
        <span>${escapeHtml(row.payer || row.payerName || "Payer")}</span>
        <b>${money(moneyNumber(row.amount))}</b>
      </div>`).join("")
    : `<p class="empty-state">No payments have posted yet. As soon as one lands it shows here.</p>`;
  return `
  <section class="panel client-home-payments">
    <div class="ar-report-head">
      <div><h3>Recent payments</h3></div>
      <div class="ar-report-actions"><button type="button" class="small-button" data-client-home-go="reports" data-client-home-report="payments">See all payments</button></div>
    </div>
    ${body}
  </section>`;
}

// Band 4 — one card into the statements surface. No preview: statements are a
// task the owner goes and does, not something to skim on a landing page.
function renderClientHomeStatementsCard() {
  return `
  <section class="panel client-home-statements">
    <h3>Patient statements</h3>
    <p>Review and print the statements your patients owe.</p>
    <button type="button" class="small-button" data-client-home-go="statements">Open patient statements</button>
  </section>`;
}

function bindClientHomeControls(host) {
  if (!host || typeof host.addEventListener !== "function" || !host.dataset || host.dataset.clientHomeBound === "1") return;
  host.dataset.clientHomeBound = "1";
  host.addEventListener("click", (event) => {
    const target = event.target && event.target.closest ? event.target.closest("[data-client-home-go]") : null;
    if (!target) return;
    const view = target.getAttribute("data-client-home-go");
    // Re-check access at click time: the nav hides what the role cannot open,
    // and this must not become a second, laxer door into a view.
    if (!view || !viewIsAvailable(view)) return;
    // navigateToView, not setView -- `setView` is not defined anywhere in this
    // codebase, so both Client Home call-to-action buttons ("See all payments",
    // "Open patient statements") threw a ReferenceError and did nothing. On the
    // external client's ONLY money surface, and invisible because the handler
    // swallowed it. Found by a guardian pass over the line above, which is why
    // the contract now EXERCISES this handler instead of reading it.
    navigateToView(view);
    // Reports opens on its report catalog. This button says "see all payments",
    // so it opens the Payments report itself, not the shelf that report sits on.
    const clientHomeReport = target.getAttribute("data-client-home-report") || "";
    // Validated against the report library, not trusted from the attribute: the
    // navigation above already happened, so an unrecognised value must be a
    // no-op rather than a second, looser way to move the user.
    if (clientHomeReport && state.view === view && CLIENT_REPORT_LIBRARY.some((report) => report.key === clientHomeReport)) {
      openClientReportRoute(clientHomeReport);
    }
  });
}

// "On its way to your bank" — the Arriving Soon figure, renamed for a practice
// owner. Quiet, never alarming, and honest when the number is unavailable.
function clientHomeArrivingSoonTotal() {
  const model = clientPaymentsChecksModel([]);
  if (model.loading) return { label: "—", detail: "Loading payments on the way to your bank. This is not $0." };
  if (model.error) return { label: "—", detail: "We could not load payments in transit. This is not $0." };
  const rows = Array.isArray(model.rows) ? model.rows : [];
  if (!rows.length) return { label: "—", detail: "Nothing in transit right now" };
  try {
    const kpis = clientPaymentsKpis(rows);
    const value = moneyNumber((kpis && (kpis.arrivingSoon || kpis.arriving)) || 0);
    return value > 0
      ? { label: money(value), detail: "Payments issued and on their way" }
      : { label: money(0), detail: "Nothing in transit right now" };
  } catch (_) {
    return { label: "—", detail: "Nothing in transit right now" };
  }
}

function clientHomeAsOfLabel() {
  if (!trackerApiEnabled() || reportDatasetState.status !== "ready") return "";
  const prepared = formatDateTime(reportDatasetState.fetchedAt);
  return prepared && prepared !== "None" ? ` · as of ${prepared}` : "";
}

// Reason wording stays in the owner's language, never the biller's shorthand.
function clientHomeReasonLabel(reason) {
  const text = String(reason || "").trim();
  if (/evv/i.test(text)) return "EVV visit verification";
  if (/credential/i.test(text)) return "Provider credentialing";
  if (/auth/i.test(text)) return "Authorization needed";
  if (/hold/i.test(text)) return "On hold";
  return text || "Other";
}

function clientHomeWaitingDestination(reason) {
  const text = String(reason || "");
  if (/credential/i.test(text) && typeof viewIsAvailable === "function" && viewIsAvailable("credentialing")) return "credentialing";
  if (/auth/i.test(text) && typeof viewIsAvailable === "function" && viewIsAvailable("authorizations")) return "authorizations";
  if (typeof viewIsAvailable === "function" && viewIsAvailable("statements")) return "statements";
  return "reports";
}

function clientHomeReasonAdvice(reason) {
  const text = String(reason || "").trim();
  if (/evv/i.test(text)) return "Confirm these visits were verified in your EVV system.";
  if (/credential/i.test(text)) return "A rendering provider still needs credentialing with this payer.";
  if (/auth/i.test(text)) return "An authorization is missing or expired — send us the approval.";
  if (/hold/i.test(text)) return "We are holding these until your team confirms how to proceed.";
  return "Your TMS team will reach out with what they need.";
}

// ===========================================================================
// UNKNOWN EXPECTED ON REPORTS (judge ruling, panel of 2026-08-03).
//
// gold's rate feed is empty, so [Total Expected] is 0 on 233,447 of 234,806
// lines and negative on 1,359 — i.e. NOT ONE line currently carries a known
// positive expected. Every expected-derived figure on this tab was therefore
// rendering a confident $0, and the worst of them were silent: Insurance AR and
// Denials' "Dollars at Risk" both come from expectedOutstanding, which is
// Math.max(expected - paid, 0) — so an unrated denied claim with a real payer
// balance reported $0 outstanding, i.e. "resolved".
//
// The honest per-line signal already exists (serviceFinancialView's
// primaryRateMissing) and claimFinancialView now counts it into
// expectedKnown / unratedLineCount, WITHOUT changing any existing number.
// These helpers turn that into a known-basis total plus an excluded count.
//
// PRIMARY USER of this surface is the BillingManager (the Biller role does not
// hold reports:view at all). Copy is written for their Monday-morning triage:
// can I trust this number, and if not, where do I go.
const REPORT_EXPECTED_UNKNOWN_SENTENCE = "Expected-value reporting is being finalized for this workspace.";

// B1: the KPI tiles moved to the known basis; the matrices below them did NOT
// (they are per-claim tables, and rewriting them is the deferred detail-table
// work). Two totals for one filter sitting inches apart is exactly the
// reconciliation question a BillingManager opens this tab to answer, so the
// one that is NOT captioned has to say which basis it is on. No arithmetic
// changes here — only the label.
function reportAllClaimsTotalLabel(totals) {
  const coverage = reportCoverage(totals);
  return coverage.anyUnknown
    ? `Total (all claims, incl. ${Formatters.int(coverage.unrated)} unpriced)`
    : "Total";
}

// The same fact as a trailing note for a CSV, where there is no room for a
// caption and no tooltip to hover.
function reportAllClaimsCsvNote(totals) {
  const coverage = reportCoverage(totals);
  return coverage.anyUnknown
    ? [`# Totals are ALL claims, including ${coverage.unrated} with no rate on file; the report header's expected figures are the priced-claim basis only.`]
    : null;
}

// Ratios denominated in expected are computed on the priced basis, so when the
// two bases differ the wording has to say which one — otherwise a real
// all-claims dollar figure sits beside a priced-basis percentage and implies a
// third number that is neither (guardian B2).
function reportRatioBasisSuffix(totals) {
  return reportCoverage(totals).anyUnknown ? " on priced claims" : "";
}

// A claim contributes to the KNOWN basis only when every one of its lines is
// rated. Conservative on purpose — a partly-rated claim's expected is a floor,
// and a floor read as a total is the failure this whole change exists to stop.
function reportExpectedKnown(fin) {
  return Boolean(fin && fin.expectedKnown);
}

// "N of M claims priced" for a totals object carrying claims/unratedClaims.
function reportCoverage(totals = {}) {
  const claims = Number(totals.claims) || 0;
  const unrated = Math.max(0, Math.min(claims, Number(totals.unratedClaims) || 0));
  return { claims, unrated, rated: claims - unrated, allUnknown: claims > 0 && unrated >= claims, anyUnknown: unrated > 0 };
}

// The KPI tile for an expected-derived figure.
//   * every claim unrated  -> the portal's one unknown-money glyph, demoted via
//     the "unknown" tone, with the reason in the detail line (a tile is glanced
//     at, never hovered, so a title attribute would be dead weight).
//   * some unrated         -> the known-basis figure, labelled as such IN THE
//     SAME BREATH, so a partial total can never pass as a whole one.
//   * none unrated         -> exactly what it renders today.
// "not $0" is stated explicitly on the tiles where a zero reading triggers
// wrong INACTION (Dollars at Risk, Expected (Held), Insurance AR).
function reportExpectedKpiCard(label, amount, detail, tone, totals, unknownDetail) {
  const coverage = reportCoverage(totals);
  if (coverage.allUnknown) return arKpiCard(label, MONEY_UNKNOWN_TEXT, unknownDetail, "unknown");
  // A pre-formatted value (the ratio cards) passes through untouched.
  if (coverage.anyUnknown) {
    // APPEND the coverage caveat, never substitute it. The ruling's partial
    // wording replaced the tile's own detail line outright, which silently
    // deleted what the number MEANS ("Revenue at risk while the blocker
    // stands") in order to say how complete it is — and a contract test caught
    // exactly that. The caveat still lands "in the same breath", which is what
    // the client lens asked for, without costing the label its sentence.
    return arKpiCard(
      label,
      typeof amount === "string" ? amount : money(amount),
      `${detail} · Based on ${Formatters.int(coverage.rated)} of ${Formatters.int(coverage.claims)} claims with priced rates — ${Formatters.int(coverage.unrated)} excluded (no rate on file)`,
      tone
    );
  }
  return arKpiCard(label, typeof amount === "string" ? amount : money(amount), detail, tone);
}

// One line beside the KPI grid, only when NOTHING in the filter is priced.
// It never replaces a tile — the tiles stay in place so the eye keeps its
// positions and a reader of Denials is answered where they look, not by an
// absence they would read as "nothing to report".
//
// The link is gated on the capability to ACT: telling a client to go add a rate
// they cannot touch is worse than the $0 it replaced (CEO veto), so only roles
// that manage fee schedules get it. The copy states a fact, never a diagnosis --
// no "pipeline", no "vendor", no counts of affected orgs.
function reportExpectedNoticeHtml(totals) {
  if (!reportCoverage(totals).allUnknown) return "";
  const canFix = hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES);
  return `
    <p class="report-expected-notice">
      ${escapeHtml(REPORT_EXPECTED_UNKNOWN_SENTENCE)}
      ${canFix ? `<button type="button" class="report-notice-link reports-no-print" data-report-view="rateIssues">View Rate Issues</button>` : ""}
    </p>`;
}

function arKpiCard(label, value, detail, tone) {
  return `
    <article class="ar-kpi-card${tone ? ` ar-kpi-card--${tone}` : ""}">
      <span class="ar-kpi-label">${escapeHtml(label)}</span>
      <strong class="ar-kpi-value">${escapeHtml(value)}</strong>
      <span class="ar-kpi-detail">${escapeHtml(detail)}</span>
    </article>`;
}

// ===========================================================================
// AR AGING CHARTS (2026-06-11) — port of the TrackerUpload AR Aging tab
// visuals onto the existing AR view. Both charts aggregate the SAME rows the
// matrix and detail table render (every filtered claim through arClaimRow,
// written-off claims included — insuranceAr is Expected − Paid clamped at
// zero) so the whole screen shares one money basis. Buckets and payers are
// fixed dimensions: neither chart follows the matrix dimension toggle.
// ===========================================================================
function arBucketChartRows(rows) {
  const sums = AR_AGING_BUCKETS.reduce((acc, bucket) => { acc[bucket.key] = 0; return acc; }, {});
  rows.forEach((row) => { sums[row.bucketKey] += row.insuranceAr; });
  return AR_AGING_BUCKETS.map((bucket) => ({ key: bucket.key, label: bucket.label, insuranceAr: moneyNumber(sums[bucket.key]) }));
}

// Top 8 payers by total outstanding AR — the work-prioritization cut: who to
// call about the oldest money. Zero-AR payers carry nothing to stack, so
// they never claim one of the 8 slots.
function arPayerBucketStacks(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const key = row.payer || "—";
    if (!groups.has(key)) {
      groups.set(key, {
        payer: key,
        total: 0,
        buckets: AR_AGING_BUCKETS.reduce((acc, bucket) => { acc[bucket.key] = 0; return acc; }, {})
      });
    }
    const group = groups.get(key);
    group.buckets[row.bucketKey] += row.insuranceAr;
    group.total += row.insuranceAr;
  });
  const ranked = Array.from(groups.values()).filter((group) => group.total > 0).sort((a, b) => b.total - a.total);
  return { payers: ranked.slice(0, 8), truncated: ranked.length > 8 };
}

// Long payer names collide as category ticks at narrow widths, so the tick
// label itself is truncated — Plotly hover echoes the same trimmed label,
// and the matrix below keeps the full names.
function arPayerTickLabel(name) {
  const text = String(name || "—");
  return text.length > 14 ? `${text.slice(0, 13).trimEnd()}…` : text;
}

// Truncation can collide two distinct payers into one Plotly category (which
// would silently merge their stacks) — suffix repeats to keep them apart.
function arPayerTickLabels(names) {
  const seen = new Map();
  return names.map((name) => {
    const label = arPayerTickLabel(name);
    const count = (seen.get(label) || 0) + 1;
    seen.set(label, count);
    return count > 1 ? `${label} (${count})` : label;
  });
}

function renderArAgingCharts(rows) {
  const bucketRows = arBucketChartRows(rows);
  const bucketTotal = bucketRows.reduce((sum, row) => sum + row.insuranceAr, 0);
  // A $0 chart draws an empty axis frame — say why in the panel's own voice.
  const bucketChart = bucketTotal > 0
    ? `<div class="chart-canvas">${renderChartCanvas("bar", bucketRows.map((row) => row.label), [
        // Aging severity ramp: each bar wears its bucket's token (--aging-1..5,
        // green → red). colorIndex 3 stays as the positional SVG-fallback red.
        { label: "Insurance AR", valueType: "money", colorIndex: 3, pointColorVars: bucketRows.map((row, idx) => `--aging-${idx + 1}`), values: bucketRows.map((row) => row.insuranceAr) }
      ])}</div>`
    : '<p class="muted-note chart-empty">No outstanding insurance AR in the active filter.</p>';
  const stacks = arPayerBucketStacks(rows);
  const payerLabels = arPayerTickLabels(stacks.payers.map((group) => group.payer));
  const payerChart = stacks.payers.length
    ? `<div class="chart-canvas">${renderChartCanvas("stacked", payerLabels, AR_AGING_BUCKETS.map((bucket, bucketIdx) => ({
        label: bucket.label,
        valueType: "money",
        colorVar: `--aging-${bucketIdx + 1}`,
        values: stacks.payers.map((group) => moneyNumber(group.buckets[bucket.key]))
      })))}</div>`
    : '<p class="muted-note chart-empty">No outstanding insurance AR in the active filter.</p>';
  return `
  <div class="ar-aging-charts-grid">
  <section class="panel ar-report-panel ar-aging-bucket-chart">
    <div class="ar-report-head">
      <div>
        <h3>Insurance AR by Aging Bucket</h3>
        <p>Outstanding insurance AR (Expected − Paid) per aging bucket — same claims as the matrix below.</p>
      </div>
    </div>
    ${bucketChart}
  </section>
  <section class="panel ar-report-panel ar-aging-payer-chart">
    <div class="ar-report-head">
      <div>
        <h3>AR by Top Payers and Aging</h3>
        <p>Payers ranked by outstanding AR, stacked by aging bucket — who to call about the oldest money${stacks.truncated ? " · top 8 shown" : ""}.</p>
      </div>
    </div>
    ${payerChart}
  </section>
  </div>`;
}

// Bill-Date Weekly Aging report table — CT-TRACKER build_bill_date_weekly_aging_summary
// shape: one row per weekly bill-date-aging band with the CT-TRACKER identities
// (Remaining Balance = Expected - Paid; Collections % = Paid/Expected;
// Write-off % = Write-off/(Expected+Write-off)). Reuses the AR/denials table classes.
function renderBillDateAgingReport(rows, claimCount, firstColLabel = "Aging Bucket", granularity = "weekly", emptyNote = "No claims in the active filter.") {
  const totals = rows.reduce((t, r) => {
    t.claims += r.claims; t.expected += r.expected; t.paid += r.paid; t.writeOff += r.writeOff; t.balance += r.balance; return t;
  }, { claims: 0, expected: 0, paid: 0, writeOff: 0, balance: 0 });
  const body = rows.length ? rows.map((r) => `
    <tr data-bda-drill="${escapeHtml(r.bucket)}" role="button" tabindex="0">
      <th scope="row">${escapeHtml(billDateGroupLabel(r.bucket))}</th>
      <td class="num">${r.claims}</td>
      <td class="num">${money(r.expected)}</td>
      <td class="num">${money(r.paid)}</td>
      <td class="num">${money(r.balance)}</td>
      <td class="num">${arPercent(r.paid, r.expected)}</td>
      <td class="num">${reportWriteOffPercent(r.writeOff, r.expected + r.writeOff)}</td>
    </tr>`).join("") : `<tr><td colspan="7">${escapeHtml(emptyNote)}</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>${granularity === "monthly" ? "Bill Date Monthly Aging Summary" : "Bill Date Weekly Aging Summary"}</h3>
        <p>${formatNounCount(claimCount, "claim")} in the active filter · ${granularity === "monthly" ? "grouped month over month by inferred original bill date, most recent first." : "grouped into rolling 7-day bands by days since original bill date."}</p>
      </div>
      <div class="ar-dim-toggle reports-no-print" role="group" aria-label="Bill-date aging granularity">
        ${[["weekly", "Weekly"], ["monthly", "Monthly"]].map(([key, label]) => `<button type="button" class="ar-dim-btn${granularity === key ? " is-active" : ""}" data-bda-gran="${key}">${label}</button>`).join("")}
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-matrix-table">
        <thead><tr><th scope="col">${escapeHtml(firstColLabel)}</th><th class="num">Claims</th><th class="num">Insurance Expected</th><th class="num">Total Paid</th><th class="num">Remaining Balance</th><th class="num">Collections %</th><th class="num">Write-off %</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td class="num"><strong>${totals.claims}</strong></td>
            <td class="num"><strong>${money(totals.expected)}</strong></td>
            <td class="num"><strong>${money(totals.paid)}</strong></td>
            <td class="num"><strong>${money(totals.balance)}</strong></td>
            <td class="num"><strong>${arPercent(totals.paid, totals.expected)}</strong></td>
            <td class="num"><strong>${reportWriteOffPercent(totals.writeOff, totals.expected + totals.writeOff)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>`;
}

function renderArMatrix(matrix, dimension, totals = null) {
  const dimensions = [["payer", "Payer"], ["client", "Client"], ["provider", "Provider"]];
  const maxCell = Math.max(1, ...matrix.list.flatMap((group) => AR_AGING_BUCKETS.map((bucket) => group.buckets[bucket.key])));
  const bucketHead = AR_AGING_BUCKETS.map((bucket) => `<th class="num">${bucket.label}</th>`).join("");
  const body = matrix.list.length ? matrix.list.map((group) => `
    <tr>
      <th scope="row" class="ar-drillable" data-ar-drill-group="${escapeHtml(group.key)}" role="button" tabindex="0">${escapeHtml(group.key)}<span class="ar-row-sub">${formatNounCount(group.claims, "claim")}</span></th>
      ${AR_AGING_BUCKETS.map((bucket) => {
        const value = group.buckets[bucket.key];
        const heat = value > 0 ? Math.max(0.06, value / maxCell) : 0;
        return `<td class="num ar-heat ar-drillable" data-ar-drill-group="${escapeHtml(group.key)}" data-ar-drill-bucket="${bucket.key}" role="button" tabindex="0" style="--heat:${heat.toFixed(3)}">${value ? money(value) : "—"}</td>`;
      }).join("")}
      <td class="num"><strong>${money(group.total)}</strong></td>
    </tr>`).join("") : `<tr><td colspan="${AR_AGING_BUCKETS.length + 2}">No outstanding insurance AR in the active filter.</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>AR Aging Matrix</h3>
        <p>Outstanding insurance AR (Expected − Paid) by aging bucket.</p>
      </div>
      <div class="ar-dim-toggle reports-no-print" role="group" aria-label="Group AR matrix by">
        ${dimensions.map(([key, label]) => `<button type="button" class="ar-dim-btn${dimension === key ? " is-active" : ""}" data-ar-dim="${key}">${label}</button>`).join("")}
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-matrix-table">
        <thead><tr><th scope="col">${escapeHtml(arDimensionLabel(dimension))}</th>${bucketHead}<th class="num">Total</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr>
            <th scope="row">${escapeHtml(totals ? reportAllClaimsTotalLabel(totals) : "Total")}</th>
            ${AR_AGING_BUCKETS.map((bucket) => `<td class="num"><strong>${money(matrix.columnTotals[bucket.key])}</strong></td>`).join("")}
            <td class="num"><strong>${money(matrix.grand)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>`;
}

function renderArDetail(rows, emptyNote = "No claims in the active filter.") {
  const head = AR_DETAIL_COLUMNS.map((col) => {
    const active = reportArState.sortKey === col.key;
    const arrow = active ? (reportArState.sortDir === "asc" ? " ▲" : " ▼") : "";
    const cls = `${col.type === "text" ? "" : "num"} ar-sortable${active ? " is-sorted" : ""}`.trim();
    return `<th class="${cls}" data-ar-sort="${col.key}" role="button" tabindex="0" aria-sort="${active ? (arrow.indexOf("▲") >= 0 ? "ascending" : "descending") : "none"}" aria-label="Sort by ${escapeHtml(col.label)}" title="Sort by ${escapeHtml(col.label)}">${escapeHtml(col.label)}<span aria-hidden="true">${arrow}</span></th>`;
  }).join("");
  // Display window only. total stays the FULL count — the subtitle, the pager
  // status line and every number outside this function are computed from it.
  // The clamp keeps a stale page (left over from a filter that shrank the book)
  // inside the real page count instead of rendering an empty table.
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / AR_DETAIL_PAGE_SIZE));
  const pageNumber = Math.min(Math.max(1, Number(reportArState.detailPage) || 1), pageCount);
  reportArState.detailPage = pageNumber;
  const startIndex = (pageNumber - 1) * AR_DETAIL_PAGE_SIZE;
  const visible = rows.slice(startIndex, startIndex + AR_DETAIL_PAGE_SIZE);
  const rangeNote = pageCount > 1 ? ` · showing ${startIndex + 1}–${startIndex + visible.length}` : "";
  const body = total ? visible.map((row) => `
    <tr data-ar-drill-claim="${escapeHtml(String(row.claimId || ""))}" role="button" tabindex="0">
      ${AR_DETAIL_COLUMNS.map((col) => {
        const value = row[col.key];
        const cell = col.key === "writeOff" ? reportWriteOffMoney(value)
          : col.type === "money" ? money(value) : col.type === "num" ? ((value == null || (typeof value === "number" && !Number.isFinite(value))) ? "—" : String(value)) : escapeHtml(String(value || "—"));
        return `<td class="${col.type === "text" ? "" : "num"}" data-label="${escapeHtml(col.label)}">${cell}</td>`;
      }).join("")}
    </tr>`).join("") : `<tr><td colspan="${AR_DETAIL_COLUMNS.length}">${escapeHtml(emptyNote)}</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>AR Detail</h3>
        <p>${formatNounCount(total, "claim")}${rangeNote} · click a column header to sort · export to work or send.</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>${pageCount > 1 ? `
    <div class="ar-detail-pager">
      <button type="button" class="ghost-button" data-ar-detail-page="prev"${pageNumber <= 1 ? " disabled" : ""} aria-label="Previous page of AR detail">‹ Prev</button>
      <span class="ar-detail-pager-status">Page ${pageNumber} of ${pageCount} · ${formatNounCount(total, "claim")} total · CSV export includes all ${total}</span>
      <button type="button" class="ghost-button" data-ar-detail-page="next"${pageNumber >= pageCount ? " disabled" : ""} aria-label="Next page of AR detail">Next ›</button>
    </div>` : ""}
  </section>`;
}

function bindArReportControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-ar-dim]").forEach((btn) => {
    btn.addEventListener("click", () => {
      reportArState.dimension = btn.getAttribute("data-ar-dim");
      reportArState.detailPage = 1;
      renderReports();
    });
  });
  host.querySelectorAll("[data-ar-sort]").forEach((th) => {
    const apply = () => {
      const key = th.getAttribute("data-ar-sort");
      if (reportArState.sortKey === key) {
        reportArState.sortDir = reportArState.sortDir === "asc" ? "desc" : "asc";
      } else {
        reportArState.sortKey = key;
        const col = AR_DETAIL_COLUMNS.find((c) => c.key === key);
        reportArState.sortDir = col && col.type === "text" ? "asc" : "desc";
      }
      reportArState.detailPage = 1;
      renderReports();
    };
    th.addEventListener("click", apply);
    th.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); apply(); }
    });
  });
  host.querySelectorAll("[data-ar-detail-page]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const dir = btn.getAttribute("data-ar-detail-page");
      const next = dir === "prev" ? Number(reportArState.detailPage || 1) - 1 : Number(reportArState.detailPage || 1) + 1;
      // renderArDetail clamps to the real page count, so an out-of-range value
      // here is corrected on render rather than needing the count duplicated.
      reportArState.detailPage = Math.max(1, next);
      renderReports();
    });
  });
  host.querySelectorAll("[data-ar-drill-claim]").forEach((el) => {
    const open = () => {
      const id = el.getAttribute("data-ar-drill-claim");
      drillIntoReportRow({
        view: reportArState.view,
        label: `Claim ${id}`,
        dims: [`Claim: ${id}`],
        predicate: (claim) => String(claim.claimId || "") === String(id)
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
  host.querySelectorAll("[data-ar-drill-group]").forEach((el) => {
    const open = () => {
      const dim = reportArState.dimension;
      const key = el.getAttribute("data-ar-drill-group");
      const bucket = el.getAttribute("data-ar-drill-bucket") || "";
      const bucketLabel = bucket ? ((AR_AGING_BUCKETS.find((b) => b.key === bucket) || {}).label || bucket) : "";
      const dims = [`${arDimensionLabel(dim)}: ${key}`];
      if (bucketLabel) dims.push(`Aging: ${bucketLabel}`);
      drillIntoReportRow({
        view: reportArState.view,
        label: dims.join(" · "),
        dims,
        predicate: (claim) => {
          const row = arClaimRow(claim);
          if (arDimensionKey(row, dim) !== key) return false;
          if (bucket && row.bucketKey !== bucket) return false;
          return true;
        }
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
  host.querySelectorAll("[data-ar-export]").forEach((btn) => {
    btn.addEventListener("click", () => arExportCsv(btn.getAttribute("data-ar-export")));
  });
  const printBtn = host.querySelector("[data-ar-print]");
  if (printBtn) printBtn.addEventListener("click", () => window.print());
}

// THE EXPORT IS THE TABLE ON SCREEN. Every definition-lane report (Bill Date Weekly/Monthly
// Aging, LITE Monthly Summary, Rollforward, ...) shares the two AR export buttons, and they
// downloaded the AR payer x bucket matrix and its claim list whatever report was showing -
// measured on the QA lane 2026-09-03. A summary export is now the report's own grain rows and
// columns with the same totals row the footer prints; a detail export is the service lines
// those rows were summed from, each tagged with the grain it was filed under. Nothing here
// recomputes money: the rows come from reportMeasureRowsFromClaims and the lines from
// claimFinancialView, exactly as the screen and the service-line rung read them.
// AR Aging keeps its historical payer x bucket files (pinned by
// verify_frontend_report_export_source_contract.js); every other definition view exports itself.
function definitionReportExportCsv(view, claims, kind) {
  const def = reportDefinitionUi(view);
  if (!def || typeof def.grainOf !== "function") return false;
  const population = typeof def.population === "function" ? (claims || []).filter(def.population) : (claims || []);
  const scoped = reportApplySlicers(population);
  const slug = String(view).replace(/[A-Z]/g, (c) => "-" + c.toLowerCase());
  const grainLabel = def.grainLabel || preparedReportGroupHeading(view, "") || "Group";
  const moneyText = (value) => { const n = Number(value); return value === null || value === undefined || value === "" || !Number.isFinite(n) ? "" : n.toFixed(2); };
  if (kind === "matrix") {
    const rows = reportSortRows(view, reportMeasureRowsFromClaims(scoped, def.grainOf));
    const totals = reportMeasureTotals(rows);
    const columns = reportColumnsInBandOrder(def.columns || REPORT_FINANCIAL_SPINE);
    const cell = (col, row) => {
      const value = reportCellValue(col, row);
      if (value === null || value === undefined || value === "") return "";
      const n = Number(value);
      if (!Number.isFinite(n)) return String(value);
      if (col.type === "ratio") return (n * 100).toFixed(2) + "%";
      if (col.kind === "currency" || col.kind === "perUnit") return n.toFixed(2);
      return String(n);
    };
    const header = [grainLabel, ...columns.map((col) => col.label)];
    const lines = rows.map((row) => [preparedReportGroupLabel(view, row.dims), ...columns.map((col) => cell(col, row))]);
    lines.push(["Total", ...columns.map((col) => cell(col, totals))]);
    const unrated = Math.max(0, Number(totals.unratedLines) || 0);
    if (unrated) lines.push(["# " + unrated + " of " + Number(totals.lines || 0) + " service lines carry no rate and contribute nothing to expected; expected-dependent figures are understated, not $0.00."]);
    arDownloadFile(slug + "-summary.csv", arToCsv([header, ...lines]), "text/csv;charset=utf-8;");
    return true;
  }
  const header = ["Claim ID", "Org ID", "Client", "Primary Payer", "Provider", "Claim Status", "Service ID", "Srvc Date", "Procedure", "Units", grainLabel, "Charge Amount", "Gross Expected", "Patient Responsibility", "Write-off Amount", "Insurance Amount Expected", "Total Applied Payments", "Remaining Balance", "Finalized Match"];
  const lines = [];
  scoped.forEach((claim) => {
    const fin = claimFinancialView(claim);
    const serviceLines = (fin && fin.serviceFinancials) || [];
    const services = claim.services || [];
    serviceLines.forEach((line, index) => {
      const service = services[index] || {};
      const key = String(def.grainOf(claim, service, line) || "") || "(blank)";
      lines.push([
        claim.claimId || "", claim.orgId || "", claim.client || "", claim.primaryPayer || claim.secondaryPayer || "", claim.renderingProvider || "", claim.claimStatus || "",
        service.serviceId || String(index + 1), service.dos || "", service.procedure || "", service.units !== undefined ? service.units : (line.units || ""),
        preparedReportGroupLabel(view, [key]),
        moneyText(line.charges), moneyText(line.staticTotalExpected || line.finalExpectedTotalNumeric), moneyText(line.patientResponsibility), moneyText(line.writeOffAmount),
        moneyText(line.finalInsuranceExpected), moneyText(line.finalInsurancePaid), moneyText(line.finalInsuranceBalance), line.finalizedMatch || ""
      ]);
    });
  });
  arDownloadFile(slug + "-detail.csv", arToCsv([header, ...lines]), "text/csv;charset=utf-8;");
  return true;
}

function arExportCsv(kind) {
  const claims = getReportClaims(reportSourceClaims());
  // Patient Responsibility is NOT routed here: its screen rows go through
  // patientResponsibilityEligibleClaims first, and its grain is a patient name. It keeps its
  // previous export until that report owner decides what its CSV should carry.
  if (reportArState.view !== "ar" && reportArState.view !== "patientResponsibility" && !trackerApiEnabled() && definitionReportExportCsv(reportArState.view, claims, kind)) return;
  const rows = claims.map(arClaimRow);
  let table;
  let filename;
  if (kind === "matrix") {
    const matrix = arBuildMatrix(rows, reportArState.dimension);
    const header = [arDimensionLabel(reportArState.dimension), ...AR_AGING_BUCKETS.map((b) => b.label), "Total", "Claims"];
    const lines = matrix.list.map((group) => [group.key, ...AR_AGING_BUCKETS.map((b) => group.buckets[b.key].toFixed(2)), group.total.toFixed(2), group.claims]);
    lines.push(["Total", ...AR_AGING_BUCKETS.map((b) => matrix.columnTotals[b.key].toFixed(2)), matrix.grand.toFixed(2), matrix.list.reduce((sum, g) => sum + g.claims, 0)]);
    const arBasisNote = reportAllClaimsCsvNote(arReportTotals(rows));
    if (arBasisNote) lines.push(arBasisNote);
    table = [header, ...lines];
    filename = "ar-aging-matrix.csv";
  } else {
    const header = AR_DETAIL_COLUMNS.map((col) => col.label);
    const sorted = arSortRows(rows, reportArState.sortKey, reportArState.sortDir);
    const lines = sorted.map((row) => AR_DETAIL_COLUMNS.map((col) => col.type === "money" ? Number(row[col.key]).toFixed(2) : row[col.key]));
    table = [header, ...lines];
    filename = "ar-aging-detail.csv";
  }
  arDownloadFile(filename, arToCsv(table), "text/csv;charset=utf-8;");
}

function arToCsv(table) {
  return table.map((row) => row.map(csvEscape).join(",")).join("\r\n");
}

function arDownloadFile(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ===========================================================================
// REPORTS — Collections. One question: what money actually came in, by
// payment month? Grouping is by date paid — never date of service. API mode
// posts the shared tenant/payer/date filters to /api/reports/collections
// (raw payment mirror); local mode aggregates tracker payment evidence
// client-side so the report still has real content without the mirror.
// ===========================================================================
const COLLECTIONS_DETAIL_COLUMNS = [
  { key: "month", label: "Payment Month", type: "text" },
  { key: "payer", label: "Payer", type: "text" },
  { key: "client", label: "Client", type: "text" },
  { key: "collected", label: "Collected", type: "money" },
  { key: "payments", label: "Payments", type: "num" },
  { key: "claims", label: "Claims", type: "num" }
];

// ===========================================================================
// REPORTS — report-row drill-down (client-side over the report snapshot).
// Owner ask (2026-07-21): click a report aggregate row and see the underlying
// service lines the way legacy CT-TRACKER does. This re-buckets the SAME
// principal-scoped snapshot the aggregates were rendered from (getReportClaims
// -> claims), so the drilled claim set IS the set that composed the clicked
// number. Dual-authority safe: it only DISPLAYS snapshot fields (reusing the
// exact claimFinancialView/arClaimRow the aggregates use) and never recomputes
// money. Distinct from the endpoint-backed reportQueueDrilldown work-queue path.
// TWO OPTIONAL INPUTS BESIDE THE PREDICATE, BOTH OF WHICH THIS FUNCTION USED TO DROP.
//
// LINE-GRAIN views (CPT × Payer, Rate Issues, Payer Classification) aggregate SERVICE LINES
// while this substrate resolves to whole CLAIMS. That is why they shipped without a drill at
// all. The client Receivables report already solved it (renderClientReportsView): resolve the
// claim set from the LINE rows the row was summed from, and hand the footer an override so it
// states the clicked row's own grain instead of silently restating a different one.
//
// Both are accepted as a VALUE OR A FUNCTION, and the function form is the one the biller
// views use. A drill is re-rendered from live state on every renderReports() — that is an
// asserted property, not an accident — so a Set captured at click time would go stale the
// moment the dataset refreshed underneath it, and the drill would keep listing claims the
// report no longer contains. A resolver re-derives, exactly like a predicate does.
//
// claimIds narrows BEFORE the predicate; a view may use either or both.
function drillIntoReportRow(context) {
  const ctx = context || {};
  reportRowDrilldownState = {
    active: true,
    view: ctx.view || reportArState.view || "",
    label: ctx.label || "",
    dims: Array.isArray(ctx.dims) ? ctx.dims : [],
    predicate: typeof ctx.predicate === "function" ? ctx.predicate : (() => true),
    claimIds: ctx.claimIds == null ? null : ctx.claimIds,
    footerOverride: ctx.footerOverride == null ? null : ctx.footerOverride,
    claimId: null,
    profile: reportRowDrilldownState.profile || "biller",
    // A new drill is a new list, so it opens on page 1. The claim-expand toggle deliberately
    // does NOT reset this — paging to 900 and opening a claim's lines must not jump back.
    detailPage: 1
  };
  renderReports();
}

// Set | Array | () => Set|Array | null. Resolved on every render so the narrowing stays live.
function reportRowDrilldownClaimIdSet(st) {
  const raw = st && st.claimIds;
  if (raw == null) return null;
  let value = raw;
  if (typeof raw === "function") {
    try { value = raw(); } catch (_) { return null; }
  }
  if (value instanceof Set) return value;
  if (Array.isArray(value)) return new Set(value.map((id) => String(id)));
  return null;
}

// Object | (rows) => Object | null. Same reason: the footer must describe what is on screen now.
function reportRowDrilldownFooterOverride(footerOverride, rows) {
  if (footerOverride == null) return null;
  if (typeof footerOverride !== "function") return footerOverride;
  try { return footerOverride(rows) || null; } catch (_) { return null; }
}

function closeReportRowDrilldown() {
  reportRowDrilldownState = { active: false, view: "", label: "", dims: [], predicate: null, claimIds: null, footerOverride: null, claimId: null, profile: reportRowDrilldownState.profile || "biller", detailPage: 1 };
  renderReports();
}

function reportRowDrilldownClaims(claims, st = reportRowDrilldownState) {
  const predicate = st.predicate || (() => true);
  const ids = reportRowDrilldownClaimIdSet(st);
  return (Array.isArray(claims) ? claims : []).filter((claim) => {
    if (ids && !ids.has(String(claim.claimId || ""))) return false;
    try { return predicate(claim); } catch (_) { return false; }
  });
}

function reportDrilldownClaimDos(claim) {
  const dates = (Array.isArray(claim.services) ? claim.services : [])
    .map((service) => service.dos)
    .filter(Boolean)
    .sort();
  return dates[0] || "—";
}

// Three column profiles ported from workflow_app_cloud.py CLAIM_DRILLDOWN_DETAIL_VIEWS
// (biller / summary / financial), mapped onto the snapshot service-line fields
// hydrated by trackerApiRowToClaim. get() reads snapshot values only — no math.
function reportDrilldownProfileColumns(profile) {
  const t = (label, get) => ({ label, type: "text", get });
  const n = (label, get) => ({ label, type: "num", get });
  const m = (label, get) => ({ label, type: "money", get });
  if (profile === "financial") {
    return [
      t("Service ID", (s) => s.serviceId),
      t("Srvc Date", (s) => s.dos),
      t("Procedure", (s) => s.procedure),
      m("Charge", (s) => s.charges),
      m("Primary Expected", (s) => s.primaryExpectedTotal),
      m("Secondary Expected", (s) => s.secondaryExpectedTotal),
      m("Total Expected", (s) => (s.staticTotalExpected || s.finalExpectedTotalNumeric)),
      // The gross figure is above, NET here, and the engine's patient responsibility between
      // them — the same three fields the Rollforward table this drill hangs off
      // renders, so the drill and the row it opened cannot disagree.
      m("Final Ins Expected", (s) => firstPresent(s.finalInsuranceExpected, s.finalExpectedTotalNumeric, 0)),
      m("Final Ins Paid", (s) => s.finalInsurancePaid),
      m("Final Ins Balance", (s) => s.finalInsuranceBalance),
      m("Patient Resp", (s) => Math.max(0, moneyNumber(firstPresent(s.patientResponsibility, s.prAdjustmentTotal, s.pr_adjustment_total, s.totalPatBal, 0)))),
      m("Write-off", (s) => s.writeOffAmount)
    ];
  }
  if (profile === "summary") {
    return [
      t("Service ID", (s) => s.serviceId),
      t("Srvc Date", (s) => s.dos),
      t("Procedure", (s) => s.procedure),
      t("Provider", (s, c) => c.renderingProvider),
      n("Units", (s) => s.units),
      t("Primary Payer", (s, c) => (s.primaryPayer || c.primaryPayer)),
      t("Secondary Payer", (s, c) => (s.secondaryPayer || c.secondaryPayer)),
      m("Final Ins Expected", (s) => firstPresent(s.finalInsuranceExpected, s.finalExpectedTotalNumeric, 0)),
      m("Final Ins Paid", (s) => s.finalInsurancePaid),
      m("Final Ins Balance", (s) => s.finalInsuranceBalance),
      t("Finalized Match", (s, c) => c.match),
      t("Follow up Action", (s, c) => c.action),
      t("Follow up Date", (s, c) => c.followUpDate)
    ];
  }
  return [
    t("Follow up Action", (s, c) => c.action),
    t("Follow up Date", (s, c) => c.followUpDate),
    t("Latest Claim Note", (s, c) => firstPresent(c.latestClaimNoteText, c.claimNoteSummary?.text, s.latestClaimNoteText, s.latestClaimNote)),
    t("Srvc Date", (s) => s.dos),
    t("Procedure", (s) => s.procedure),
    n("Units", (s) => s.units),
    t("Primary Payer", (s, c) => (s.primaryPayer || c.primaryPayer)),
    m("Primary Expected", (s) => s.primaryExpectedTotal),
    t("Secondary Payer", (s, c) => (s.secondaryPayer || c.secondaryPayer)),
    t("Finalized Match", (s, c) => c.match),
    t("Claim Status", (s, c) => c.claimStatus),
    t("Aging Bucket", (s, c) => c.aging)
  ];
}

function renderReportRowDrilldown(claims, st = reportRowDrilldownState, footerOverride = null) {
  const rows = reportRowDrilldownClaims(claims, st);
  const override = reportRowDrilldownFooterOverride(footerOverride, rows);
  const totals = rows.reduce((acc, claim) => {
    const fin = claimFinancialView(claim);
    acc.charge += moneyNumber(fin.charges);
    acc.expected += moneyNumber(fin.expectedReimbursement);
    acc.paid += moneyNumber(fin.totalInsurancePaid);
    acc.ar += moneyNumber(fin.expectedOutstanding);
    acc.balance += moneyNumber(claim.balance);
    acc.lines += Array.isArray(claim.services) ? claim.services.length : 0;
    return acc;
  }, { charge: 0, expected: 0, paid: 0, ar: 0, balance: 0, lines: 0 });
  // Dimensions are what the biller filtered on; a "Row ..." entry is the money CAVEAT that a
  // divergent row pushes on. Toned apart because they say different kinds of thing: a dimension
  // names the slice, the caveat explains why two numbers on this header disagree.
  //
  // Prefix-matched rather than typed ON PURPOSE. The caveat is a STOPGAP — the durable fix is
  // footerOverride plumbing through drillIntoReportRow (already named in the ledger for the
  // line-grain slice), and keeping it a separable element is what lets the push at the call site
  // and this test delete cleanly the day that lands. Every real dimension is "Name: value".
  const dimChips = st.dims.length
    ? st.dims.map((d) => {
        const text = String(d);
        return `<span class="pill${/^Row /.test(text) ? " amber" : ""}">${escapeHtml(text)}</span>`;
      }).join("")
    : "";
  // EVERY OVERRIDE FIELD IS OPTIONAL AND FALLS BACK TO WHAT THIS FOOTER ALREADY PRINTED, so a
  // caller that passes {count, total} — which is what the client Receivables path passes --
  // renders byte-identically to before this plumbing existed.
  //
  // `lines` is NOT a substitution, it is a ratio: "12 of 30 service lines". A line-grain row
  // opens onto whole claims, so the claims below carry lines the clicked row never counted,
  // and printing only the row's 12 would make the Charges/Expected/Paid that follow — which
  // are sums over all 30 — read as belonging to 12. Stating both is the only honest sentence.
  //
  // The line-grain views deliberately do NOT override `total`. Their row money is a different
  // quantity from "AR (Exp−Paid)" (Insurance Paid on CPT × Payer, line-level Open AR on the
  // other two); dropping it into that slot is the two-numbers-wearing-one-word failure this
  // ledger has already caught twice. They push their own figures as an amber "Row ..." dim
  // instead, named and grained, beside the claim-level ones.
  const footerClaimCount = override && override.count != null ? override.count : rows.length;
  const footerTotal = override && override.total != null ? override.total : totals.ar;
  const footerLineText = override && override.lines != null
    ? `${Number(override.lines).toLocaleString()} of ${formatNounCount(totals.lines, "service line")}`
    : formatNounCount(totals.lines, "service line");
  const allowGoto = st.allowGoto !== false;
  const selectedId = st.claimId;
  // FULL rows, not the window: the totals above were summed over every claim behind the clicked
  // report row, and an expanded claim must stay expanded when it is paged back into view.
  const selected = selectedId ? rows.find((claim) => String(claim.claimId) === String(selectedId)) : null;
  // DISPLAY WINDOW ONLY — the renderArDetail pattern. This renderer opens onto every claim behind
  // one report cell and calls claimFinancialView() per row, so an unwindowed drill on a busy
  // payer put thousands of priced rows into innerHTML in one pass. The footer sentence, the
  // totals reduce and the {count,total} override above are all computed from the full row set and
  // are deliberately untouched. The clamp corrects a stale page rather than rendering nothing.
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / AR_DETAIL_PAGE_SIZE));
  const pageNumber = Math.min(Math.max(1, Number(st.detailPage) || 1), pageCount);
  st.detailPage = pageNumber;
  const startIndex = (pageNumber - 1) * AR_DETAIL_PAGE_SIZE;
  const visible = rows.slice(startIndex, startIndex + AR_DETAIL_PAGE_SIZE);
  const body = total ? visible.map((claim) => {
    const fin = claimFinancialView(claim);
    const id = String(claim.claimId || "");
    const isOpen = selectedId != null && String(selectedId) === id;
    return `
    <tr${isOpen ? ' class="is-sorted"' : ""}>
      <td data-label="Claim ID">${escapeHtml(id || "—")}</td>
      <td data-label="DOS">${escapeHtml(reportDrilldownClaimDos(claim))}</td>
      <td data-label="Name">${escapeHtml(claim.patient || "—")}</td>
      <td data-label="Primary Payer">${escapeHtml(claim.primaryPayer || "—")}</td>
      <td data-label="Secondary Payer">${escapeHtml(claim.secondaryPayer || "—")}</td>
      <td class="num" data-label="Charge">${money(moneyNumber(fin.charges))}</td>
      <td data-label="Provider">${escapeHtml(claim.renderingProvider || "—")}</td>
      <td class="num" data-label="Lines">${Array.isArray(claim.services) ? claim.services.length : 0}</td>
      <td class="num" data-label="Expected">${money(moneyNumber(fin.expectedReimbursement))}</td>
      <td class="num" data-label="Balance">${money(moneyNumber(claim.balance))}</td>
      <td class="reports-no-print" data-label="Actions">
        <button type="button" class="small-button" data-report-drill-view="${escapeHtml(id)}">${isOpen ? "Hide lines" : "View claim"}</button>
        ${allowGoto ? `<button type="button" class="ghost-button" data-report-drill-goto="${escapeHtml(id)}">Go to claim</button>` : ""}
      </td>
    </tr>`;
  }).join("") : `<tr><td colspan="11">No claims composed this report row in the active filter.</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Drill-down${st.label ? " — " + escapeHtml(st.label) : ""}</h3>
        ${dimChips ? `<p class="ar-drill-dims">${dimChips}</p>` : ""}
        <p>${formatNounCount(footerClaimCount, "claim")} · ${footerLineText} · Charges ${money(totals.charge)} · Expected ${money(totals.expected)} · Paid ${money(totals.paid)} · AR (Exp−Paid) ${money(footerTotal)} · Balance ${money(totals.balance)}</p>
      </div>
      <div class="ar-report-actions reports-no-print">
        <button type="button" class="small-button" data-report-drill-close>← Back to report</button>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>
          <th scope="col">Claim ID</th>
          <th scope="col">DOS</th>
          <th scope="col">Name</th>
          <th scope="col">Primary Payer</th>
          <th scope="col">Secondary Payer</th>
          <th class="num">Charge</th>
          <th scope="col">Provider</th>
          <th class="num">Lines</th>
          <th class="num">Expected</th>
          <th class="num">Balance</th>
          <th scope="col" class="reports-no-print">Actions</th>
        </tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>${pageCount > 1 ? `
    <div class="ar-detail-pager reports-no-print">
      <button type="button" class="ghost-button" data-report-drill-page="prev"${pageNumber <= 1 ? " disabled" : ""} aria-label="Previous page of drilled claims">‹ Prev</button>
      <span class="ar-detail-pager-status">Page ${pageNumber} of ${pageCount} · showing ${startIndex + 1}–${startIndex + visible.length} of ${formatNounCount(total, "claim")} in this drill-down</span>
      <button type="button" class="ghost-button" data-report-drill-page="next"${pageNumber >= pageCount ? " disabled" : ""} aria-label="Next page of drilled claims">Next ›</button>
    </div>` : ""}
  </section>
  ${selected ? renderReportRowDrilldownLines(selected, st) : ""}`;
}

function renderReportRowDrilldownLines(claim, st = reportRowDrilldownState) {
  const profile = ["biller", "summary", "financial"].includes(st.profile) ? st.profile : "biller";
  const cols = reportDrilldownProfileColumns(profile);
  const services = Array.isArray(claim.services) ? claim.services : [];
  const head = cols.map((col) => `<th class="${col.type === "text" ? "" : "num"}" scope="col">${escapeHtml(col.label)}</th>`).join("");
  const body = services.length ? services.map((service) => `<tr>${cols.map((col) => {
    const raw = col.get(service, claim);
    const cell = col.type === "money"
      ? money(moneyNumber(raw))
      : col.type === "num"
        ? escapeHtml(raw == null || raw === "" ? "—" : String(raw))
        : escapeHtml(String(raw || "—"));
    return `<td class="${col.type === "text" ? "" : "num"}" data-label="${escapeHtml(col.label)}">${cell}</td>`;
  }).join("")}</tr>`).join("") : `<tr><td colspan="${cols.length}">No service lines on this claim.</td></tr>`;
  const toggles = [["biller", "Biller"], ["summary", "Summary"], ["financial", "Financial"]];
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Claim ${escapeHtml(String(claim.claimId || ""))} — service lines</h3>
        <p>${formatNounCount(services.length, "service line")} · ${escapeHtml(claim.patient || "—")} · ${escapeHtml(claim.primaryPayer || "—")}</p>
      </div>
      <div class="ar-dim-toggle reports-no-print" role="group" aria-label="Detail view">
        ${toggles.map(([key, label]) => `<button type="button" class="ar-dim-btn${profile === key ? " is-active" : ""}" data-report-drill-profile="${key}">${label}</button>`).join("")}
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </section>`;
}

function bindReportRowDrilldown() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelector("[data-report-drill-close]")?.addEventListener("click", closeReportRowDrilldown);
  host.querySelectorAll("[data-report-drill-page]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const dir = btn.getAttribute("data-report-drill-page");
      const next = dir === "prev" ? Number(reportRowDrilldownState.detailPage || 1) - 1 : Number(reportRowDrilldownState.detailPage || 1) + 1;
      // renderReportRowDrilldown clamps to the real page count, so an out-of-range value here is
      // corrected on render rather than needing the count duplicated.
      reportRowDrilldownState.detailPage = Math.max(1, next);
      renderReports();
    });
  });
  host.querySelectorAll("[data-report-drill-view]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.getAttribute("data-report-drill-view");
      reportRowDrilldownState.claimId = String(reportRowDrilldownState.claimId) === id ? null : id;
      renderReports();
    });
  });
  host.querySelectorAll("[data-report-drill-goto]").forEach((btn) => {
    btn.addEventListener("click", () => reportDrilldownGoToClaim(btn.getAttribute("data-report-drill-goto")));
  });
  host.querySelectorAll("[data-report-drill-profile]").forEach((btn) => {
    btn.addEventListener("click", () => {
      reportRowDrilldownState.profile = btn.getAttribute("data-report-drill-profile");
      renderReports();
    });
  });
}

// Go to claim: jump to the biller work queue (All Data grid) narrowed to this
// claim by seeding the existing Claim ID search input, then navigating.
function reportDrilldownGoToClaim(claimId) {
  const id = String(claimId || "");
  setClaimIdSearchValue(id);
  if (typeof navigateToView === "function") navigateToView("workbench");
  try { resetTrackerPage(); } catch (_) {}
  try { renderClaimsTable(); } catch (_) {}
}

// The 14 reports outgrew one flat segmented control: .ar-dim-toggle is a
// clipped rounded box drawn for 2-4 options, so 14 pills wrapped to three
// rows inside it and the +-sibling divider landed at the start of each
// wrapped row. Tier one is the family (4 options, one row, the control used
// as designed); tier two is the reports inside that family, as light
// underline tabs -- a second bordered control stacked under the first read
// as a wall of chrome.
const REPORT_FAMILIES = [
  // The three ex-Payments-&-Exports reports are filed by WHAT THEY ANSWER, not
  // by who built them: Payments is cash in the door, Client Receivables is
  // aging, Client RCM is a trend. Each sits next to its nearest internal
  // neighbour and keeps its own name, because the pairs do not tie - see
  // CLIENT_REPORT_FAMILY_VIEWS for exactly how their numbers differ. Family
  // Family ORDER is unchanged. What a family LANDS on is now its first
  // visible view (see visibleReportViews), because two families begin with a
  // hidden report.
  // The nine reports the Reports tab offers carry CT-TRACKER's own display
  // names, so a biller reading the tab reads the report he already knows.
  // The other ten keep their names and their entries here because the
  // registry is the routing table, not the menu — HIDDEN_REPORT_VIEWS below
  // is what withholds them from the switcher.
  { key: "aging", label: "Aging", views: [["ar", "AR Aging Summary"], ["clientReceivables", "Client Receivables"], ["billDateMonthlyAging", "Bill Date Monthly Aging Summary"], ["billDateAging", "Bill Date Weekly Aging Summary"]] },
  { key: "cash", label: "Cash", views: [["collections", "Collections"], ["clientPayments", "Incoming Payments"], ["expectedVsPaid", "Expected vs Paid"], ["turnaround", "Average Payment Cycle Per Payer"]] },
  { key: "trends", label: "Trends", views: [["rollforward", "Executive Expected vs Paid Rollforward"], ["monthlySummary", "LITE Monthly Summary"], ["patientResponsibility", "Patient Responsibility"], ["performance", "Performance"], ["clientRcm", "Client RCM"], ["cptPayer", "Average Reimbursement Per Unit by CPT and Primary Payer"]] },
  { key: "work", label: "Open Work", views: [["workQueue", "Today's Work Queue"], ["denials", "Denials"], ["rateIssues", "Rate Issues"], ["reviewQueue", "Review Queue"], ["payerClass", "Payer Classification"]] }
];

// Owner ruling (2026-08-17): the Reports tab normally offers only reports in
// the current claims tracker. Owner exception (2026-08-20): Incoming Payments
// must remain visible even though it is not a tracker report. The other ten are
// HIDDEN, never deleted — backend/reportLaneContract.js still owns every lane,
// they still route, and reportReconciliationRegistry.js still reconciles them.
const HIDDEN_REPORT_VIEWS = new Set([
  "denials",
  "clientAction",
  "collections",
  "performance",
  "expectedVsPaid",
  "clientReceivables",
  "clientRcm",
  "workQueue",
  "rateIssues",
  "reviewQueue",
  "payerClass"
]);

// Hidden UNLESS ACTIVE. A hidden view is withheld from the menu, not from the
// router: ?clientReport= links land an internal session on the client trio and
// the expected-unknown notice links straight at rateIssues. Dropping the
// active view from its own panel would leave a switcher with nothing pressed,
// which reads as broken; the button appears while you are there and goes away
// when you leave.
function visibleReportViews(family) {
  return family.views.filter(([key]) => !HIDDEN_REPORT_VIEWS.has(key) || key === reportArState.view);
}

function reportFamilyForView(view) {
  return REPORT_FAMILIES.find((family) => family.views.some(([key]) => key === view)) || REPORT_FAMILIES[0];
}

function renderReportViewSwitcher() {
  const activeFamily = reportFamilyForView(reportArState.view);
  // Every family panel is rendered; the inactive ones carry `hidden`, which
  // drops them out of the accessibility tree without dropping them out of the
  // DOM (report links elsewhere on the page still resolve against them).
  const panels = REPORT_FAMILIES.map((family) => `
    <div class="report-view-switch" role="group" aria-label="Report" data-report-family-panel="${family.key}"${family.key === activeFamily.key ? "" : " hidden"}>
      ${visibleReportViews(family).map(([key, label]) => `<button type="button" class="report-view-btn${reportArState.view === key ? " is-active" : ""}" data-report-view="${key}" data-label="${label}" aria-pressed="${reportArState.view === key ? "true" : "false"}">${label}</button>`).join("")}
    </div>`).join("");
  const exportFinder = hasPermission(PERMISSIONS.REPORT_EXPORT)
    ? `<p class="muted-note reports-no-print">Need the whole book with slicers? <button type="button" class="link-button" data-open-tracker-export>Open Export Tracker</button></p>`
    : "";
  return `
  <div class="report-switch reports-no-print">
    <div class="ar-dim-toggle report-family-switch" role="group" aria-label="Report group">
      ${REPORT_FAMILIES.map((family) => `<button type="button" class="ar-dim-btn${family.key === activeFamily.key ? " is-active" : ""}" data-report-family="${family.key}" aria-pressed="${family.key === activeFamily.key ? "true" : "false"}">${family.label}</button>`).join("")}
    </div>${panels}
  </div>${exportFinder}${reportDatasetCapNoteHtml()}`;
}

function bindReportViewSwitcher() {
  initializeReportColumnChoosers();
  // A drill path belongs to the report it was opened from; leaving that report drops it,
  // otherwise the next report would render someone else's breadcrumb.
  els.reportsBody?.querySelectorAll("[data-report-view]").forEach((button) => {
    button.addEventListener("click", () => {
      if (button.getAttribute("data-report-view") !== reportDrillState.view) {
        reportDrillState.view = "";
        reportDrillState.path = [];
      }
    });
  });
  const host = els.reportsBody;
  if (!host) return;
  host.querySelector("[data-open-tracker-export]")?.addEventListener("click", () => navigateToView("trackerExport"));
  host.querySelectorAll("[data-report-view]").forEach((btn) => {
    btn.addEventListener("click", () => {
      reportArState.view = btn.getAttribute("data-report-view");
      renderReports();
    });
  });
  // Picking a family lands on its first report rather than showing an empty
  // second tier waiting for a second click.
  host.querySelectorAll("[data-report-family]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const family = REPORT_FAMILIES.find((entry) => entry.key === btn.getAttribute("data-report-family"));
      if (!family) return;
      // Clicking the family you are already in must not move you off the report
      // you picked in tier two.
      if (family.key === reportFamilyForView(reportArState.view).key) return;
      // The first VISIBLE view, not views[0]: Cash begins with Collections and
      // Open Work with the work queue, both hidden, so views[0] would land the
      // user on a report the switcher does not offer.
      reportArState.view = (visibleReportViews(family)[0] || family.views[0])[0];
      renderReports();
    });
  });
}

// Collections defaults to the last 12 months when no dates are set: an
// unbounded payment-month scan of the live mirror runs ~34s, while the
// caption tells the user exactly what window they are looking at. The
// default fromDate snaps to the first of the month 11 months back (11
// full prior months + the current partial month), keeping the default
// request month-aligned and therefore prepared-cache eligible.
function collectionsDefaultRangeActive() {
  const filters = reportFilterValues();
  return !filters.fromDate && !filters.toDate;
}

function collectionsReportFilters() {
  const filters = reportFilterValues();
  // The PAYER travels in the shared wire shape, not as a scalar: a bare string
  // for one included payer (unchanged), { values, mode } for a list or an
  // exclusion. backend/collectionsReport.js reads exactly that form, so a
  // multi-payer Collections request narrows in SQL rather than being flattened
  // to "All" and reported as the whole book.
  const payer = reportDatasetServerFilters().payer;
  if (collectionsDefaultRangeActive()) {
    const start = todayStart();
    start.setDate(1);
    start.setMonth(start.getMonth() - 11);
    return { tenant: filters.tenant, payer, fromDate: localDateString(start), toDate: "" };
  }
  return { tenant: filters.tenant, payer, fromDate: filters.fromDate, toDate: filters.toDate };
}

function collectionsReportCaption() {
  const basis = collectionsDefaultRangeActive()
    ? "Last 12 months · grouped by date paid (payment month), not date of service."
    : "Grouped by date paid (payment month), not date of service.";
  return `${basis} These totals are payment disbursements — money that hit the bank — not the priced claim book. Net of payer recoupments, booked in the month the takeback was applied.`;
}

function collectionsReportModel() {
  if (!reportApiEnabled()) {
    return reportApiMode() === "local" && !trackerApiEnabled()
      ? collectionsLocalModel()
      : { status: "unavailable" };
  }
  const request = { filters: collectionsReportFilters() };
  const key = reportApiCacheKey(request);
  if (reportCollectionsState.key !== key || reportCollectionsState.status === "idle") {
    requestCollectionsReport(request, key);
  }
  if (reportCollectionsState.status === "error") {
    if (reportCollectionsState.code === "collections_report_not_configured") return { status: "unavailable" };
    return { status: "error", error: reportCollectionsState.error };
  }
  if (reportCollectionsState.status === "ok" && reportCollectionsState.response) {
    const body = reportCollectionsState.response;
    const rows = collectionsSortRows((Array.isArray(body.rows) ? body.rows : []).map((row) => ({
      month: String(row.month || ""),
      payer: row.payer || "No payer",
      client: row.client || "Unattributed",
      collected: moneyNumber(row.collected),
      payments: Number(row.payments) || 0,
      claims: Number(row.claims) || 0
    })));
    const totals = body.totals && Array.isArray(body.totals.months) ? body.totals : collectionsLocalTotals(rows);
    totals.months = collectionsZeroFillMonths((Array.isArray(totals.months) ? totals.months : []).map((row) => ({ month: String(row.month || ""), collected: moneyNumber(row.collected) })));
    return {
      status: "ok",
      caption: collectionsReportCaption(),
      rows,
      truncated: Boolean(body.truncated),
      // Which end got cut, and what the server refused to count. The old copy
      // said "First N rows", which was true of an ascending cap and is now
      // exactly backwards.
      truncatedBeforeMonth: String(body.truncatedBeforeMonth || ""),
      groupRowCap: Number(body.groupRowCap) > 0 ? Number(body.groupRowCap) : null,
      totals
    };
  }
  return { status: "pending" };
}

// Collections answers in 117-174 ms at "All" scope on the default 12-month window
// (production, 2026-09-22, n=3). This bound exists for the read that never lands.
const REPORT_COLLECTIONS_TIMEOUT_MS = 45000;
let reportCollectionsReadSequence = 0;
function requestCollectionsReport(request, key = reportApiCacheKey(request)) {
  const base = reportApiBase();
  if (!base || typeof fetch !== "function") return;
  const requestStillCurrent = () => (
    reportCollectionsState.key === key
    && reportApiCacheKey(request) === key
  );
  reportCollectionsState = { key, status: "pending", response: null, error: "", code: "" };
  // Collections went out through reportSessionFetch with no deadline of its own, and
  // in production that call is a bare fetch, so a read that never landed left the
  // report on its loading panel with nothing to click. The server answers this lane
  // in 117-174 ms at "All" scope over the default 12-month window (measured against
  // production 2026-09-22, n=3), so this bound only ever catches a read in trouble.
  const collectionsRequestId = ++reportCollectionsReadSequence;
  const collectionsDeadline = setTimeout(() => {
    if (collectionsRequestId !== reportCollectionsReadSequence || reportCollectionsState.key !== key
      || reportCollectionsState.status !== "pending") return;
    reportCollectionsState = { key, status: "error", response: null,
      error: "Collections is taking longer than expected. Retry, or narrow the paid-date range.", code: "" };
    if (state.view === "clientHome") renderClientHome(); else renderReports();
  }, REPORT_COLLECTIONS_TIMEOUT_MS);
  const clearCollectionsDeadline = () => clearTimeout(collectionsDeadline);
  // A superseded answer must not render, but it must repaint so the CURRENT identity
  // issues its own read instead of the panel waiting on an answer already discarded.
  const collectionsSupersededRepaint = () => {
    clearCollectionsDeadline();
    if (state.view === "reports") renderReports();
  };
  const token = reportApiToken();
  reportSessionFetch((typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(`${base}/api/reports/collections`) : `${base}/api/reports/collections`), {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(request)
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      clearCollectionsDeadline();
      if (!requestStillCurrent()) { collectionsSupersededRepaint(); return; }
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        reportCollectionsState = {
          key,
          status: "error",
          response: null,
          error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body?.error?.message || body.message || `HTTP ${response.status}`),
          code: typeof body.error === "string" ? body.error : body?.error?.code || body.code || ""
        };
        if (state.view === "clientHome") renderClientHome(); else renderReports();
        return;
      }
      reportCollectionsState = { key, status: "ok", response: body, error: "", code: "" };
      if (state.view === "clientHome") renderClientHome(); else renderReports();
    })
    .catch((error) => {
      clearCollectionsDeadline();
      if (!requestStillCurrent()) { collectionsSupersededRepaint(); return; }
      reportCollectionsState = { key, status: "error", response: null,
        error: (error && error.message) || "Collections could not be reached.", code: "" };
      if (state.view === "clientHome") renderClientHome(); else renderReports();
    });
}

// Local fallback mirrors the server contract: tenant/payer/date filters only,
// grouped by payment month x payer x client. The tenant filter is claim-level
// (server: t.[client]); payer + date filters apply per payment (server:
// pay.[payer_name] and date paid), so a DOS-range mental model never leaks in.
function collectionsLocalModel() {
  const filters = collectionsReportFilters();
  const allClaims = getClaims();
  const claims = allClaims.filter((claim) => {
    if (filters.tenant !== "All" && claim.client !== filters.tenant) return false;
    return true;
  });
  const rows = collectionsLocalRows(claims, filters);
  if (!rows.length && !collectionsLocalRows(allClaims, {}).length) {
    return { status: "unavailable" };
  }
  const totals = collectionsLocalTotals(rows);
  return {
    status: "ok",
    caption: collectionsReportCaption(),
    rows,
    truncated: false,
    groupRowCap: null,
    totals
  };
}

function collectionsLocalRows(claims, filters = {}) {
  const groups = new Map();
  claims.forEach((claim) => {
    (Array.isArray(claim.payments) ? claim.payments : []).forEach((payment) => {
      const payer = firstPresent(payment.source, payment.payer, payment["Payment Payer"], claim.primaryPayer, claim.secondaryPayer, "No payer");
      if (filters.payer && filters.payer !== "All" && payer !== filters.payer) return;
      const amount = moneyNumber(firstPresent(payment.applied, payment.amount, payment["Payment Amount"], 0));
      if (amount <= 0) return;
      const date = normalizeDateValue(firstPresent(payment.date, payment.paymentDate, payment.postedDate)) || "";
      if (!date) return;
      if (filters.fromDate && date < filters.fromDate) return;
      if (filters.toDate && date > filters.toDate) return;
      const month = date.slice(0, 7);
      const client = claim.client || "Unattributed";
      const groupKey = `${month}|${payer}|${client}`;
      if (!groups.has(groupKey)) groups.set(groupKey, { month, payer, client, collected: 0, payments: 0, claimIds: new Set() });
      const group = groups.get(groupKey);
      group.collected += amount;
      group.payments += 1;
      group.claimIds.add(claim.claimId || "");
    });
  });
  return collectionsSortRows(Array.from(groups.values()).map((group) => ({
    month: group.month,
    payer: group.payer,
    client: group.client,
    collected: moneyNumber(group.collected),
    payments: group.payments,
    claims: group.claimIds.size
  })));
}

function collectionsSortRows(rows, key = reportCollectionsSortState.sortKey, dir = reportCollectionsSortState.sortDir) {
  const col = COLLECTIONS_DETAIL_COLUMNS.find((candidate) => candidate.key === key) || COLLECTIONS_DETAIL_COLUMNS[0];
  const mult = dir === "asc" ? 1 : -1;
  const compareValue = (a, b) => {
    if (col.type === "text") return String(a[col.key] || "").localeCompare(String(b[col.key] || "")) * mult;
    return ((Number(a[col.key]) || 0) - (Number(b[col.key]) || 0)) * mult;
  };
  return [...rows].sort((a, b) => compareValue(a, b)
    || String(b.month).localeCompare(String(a.month))
    || b.collected - a.collected
    || String(a.payer).localeCompare(String(b.payer)));
}

function collectionsLocalTotals(rows) {
  const byMonth = new Map();
  const byPayer = new Map();
  rows.forEach((row) => {
    byMonth.set(row.month, moneyNumber((byMonth.get(row.month) || 0) + row.collected));
    byPayer.set(row.payer, moneyNumber((byPayer.get(row.payer) || 0) + row.collected));
  });
  const months = Array.from(byMonth.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  const top = Array.from(byPayer.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0] || ["", 0];
  return {
    collected: moneyNumber(rows.reduce((sum, row) => sum + row.collected, 0)),
    payments: rows.reduce((sum, row) => sum + row.payments, 0),
    months: collectionsZeroFillMonths(months.map(([month, collected]) => ({ month, collected }))),
    topPayer: top[0],
    topPayerCollected: top[1],
    latestMonth: months.length ? months[months.length - 1][0] : "",
    latestMonthCollected: months.length ? months[months.length - 1][1] : 0
  };
}

// ===========================================================================
// TURNAROUND TIME (2026-07-28) — "how long is the insurance taking to pay?"
// CT-TRACKER's Average Payment Cycle Per Payer, which the portal previously shipped only as a
// workbook tab that measured the wrong interval (service date -> last payment). This view reads
// /api/reports/turnaround: bill date -> the single primary insurance payment, averaged per payer
// by the CT-exact engine (backend/ctTrackerPaymentCycle.js).
//
// There is deliberately NO local/demo fallback. Payment dates exist only in the raw payment
// mirror — demo claims carry none — so a client-side approximation would be a different number
// wearing this report's name. Without the API this renders an honest unavailable state.
// ===========================================================================

const TURNAROUND_DETAIL_COLUMNS = [
  { key: "payer", label: "Primary Payer", type: "text" },
  { key: "sampleCount", label: "Paid Lines", type: "num" },
  { key: "averageDays", label: "Avg Days To Pay", type: "num" },
  { key: "medianDays", label: "Median Days", type: "num" },
  { key: "minimumDays", label: "Fastest", type: "num" },
  { key: "maximumDays", label: "Slowest", type: "num" }
];

// DOS window, not payment window: the sample is service lines, and an unbounded scan of the
// mirror is the same cost trap Collections hit. Default to the last 12 months and say so.
function turnaroundDefaultRangeActive() {
  const filters = reportFilterValues();
  return !filters.fromDate && !filters.toDate;
}

function turnaroundReportFilters() {
  const filters = reportFilterValues();
  // Same shared wire shape as Collections above; backend/turnaroundTimeReport.js
  // compiles it over the PRIMARY payer column, which is the seat this report
  // measures.
  const payer = reportDatasetServerFilters().payer;
  if (turnaroundDefaultRangeActive()) {
    const start = todayStart();
    start.setDate(1);
    start.setMonth(start.getMonth() - 11);
    return { tenant: filters.tenant, payer, fromDos: localDateString(start), toDos: "" };
  }
  return { tenant: filters.tenant, payer, fromDos: filters.fromDate, toDos: filters.toDate };
}

// 503 means "this service's dependency is unavailable, try again". 502 means "the upstream I
// proxied to answered badly" — a different and, for a dead mirror, untrue claim. Status first so
// a renamed error code cannot quietly re-break the branch; the code list is the backstop for the
// no-response case, where there is no status to read.
const TURNAROUND_OUTAGE_CODES = ["turnaround_report_upstream_unavailable", "turnaround_report_unreachable"];

// A DEADLINE, BECAUSE A 52-SECOND SPINNER AND A HANG LOOK THE SAME (2026-08-10).
//
// Every other long report on this screen carries one — the All Data grid aborts
// at 45s, the report rollup at 90s — and this fetch carried none, so a request
// that never came back left "Measuring payer turnaround..." on screen forever
// with no retry and no way for a biller to tell a slow report from a dead one.
// Measured on the live test API the same day: 52.6s for a full-population run.
//
// 120s, deliberately ABOVE the server's own 90s budget
// (turnaroundTimeReport.DEFAULT_TIMEOUT_MS): whenever the API can produce a
// typed answer — including its own upstream-unavailable outage envelope — that
// answer wins and gets rendered as itself. This only catches the case where
// nothing comes back at all, which is precisely the case the UI could not
// previously distinguish.
const TURNAROUND_TIMEOUT_MS = 120000;
function turnaroundOutageState(entry) {
  if (!entry) return false;
  if (Number(entry.httpStatus) === 503) return true;
  return TURNAROUND_OUTAGE_CODES.indexOf(String(entry.code || "")) >= 0;
}

function turnaroundReportModel() {
  if (!reportApiEnabled()) return { status: "unavailable" };
  const request = { filters: turnaroundReportFilters() };
  const key = reportApiCacheKey(request);
  if (reportTurnaroundState.key !== key || reportTurnaroundState.status === "idle") {
    requestTurnaroundReport(request, key);
  }
  // THREE failure states, not one. The backend already separates "not wired" from "the source is
  // down"; the frontend used to collapse everything but the first into one raw-error line, which
  // told a biller neither whose problem it was nor what to do next (NORTH_STAR section 6, "fail
  // loud, not empty").
  //   unavailable — 503 turnaround_report_not_configured. Nothing is broken; it is not wired here.
  //   outage      — any other 503, or a request that never landed. Retryable, not the biller's
  //                 doing, and NOT "no data": the answer is unknown, not zero.
  //   error       — genuinely unexpected. Stays loud and keeps the sanitized detail on screen.
  if (reportTurnaroundState.status === "error") {
    if (reportTurnaroundState.code === "turnaround_report_not_configured") return { status: "unavailable" };
    if (turnaroundOutageState(reportTurnaroundState)) return { status: "outage" };
    return { status: "error", error: reportTurnaroundState.error };
  }
  if (reportTurnaroundState.status === "ok" && reportTurnaroundState.response) {
    const body = reportTurnaroundState.response;
    const rows = turnaroundSortRows(Array.isArray(body.rows) ? body.rows : []);
    return {
      status: "ok",
      rows,
      totals: body.totals || {},
      coverage: body.coverage || {},
      truncated: Boolean(body.truncated),
      caption: turnaroundReportCaption()
    };
  }
  return { status: "pending" };
}

function turnaroundReportCaption() {
  const basis = turnaroundDefaultRangeActive()
    ? "Last 12 months of service dates"
    : "Service dates in the active range";
  return `${basis} · days from BILL date to the single primary insurance payment. Only cleanly-paid, unworked lines count — see coverage below.`;
}

function turnaroundSortRows(rows) {
  const col = TURNAROUND_DETAIL_COLUMNS.find((candidate) => candidate.key === reportTurnaroundSortState.sortKey)
    || TURNAROUND_DETAIL_COLUMNS[2];
  const dir = reportTurnaroundSortState.sortDir === "asc" ? 1 : -1;
  return rows.slice().sort((a, b) => {
    if (col.type === "text") return dir * String(a[col.key] || "").localeCompare(String(b[col.key] || ""));
    return dir * (Number(a[col.key] || 0) - Number(b[col.key] || 0)) || String(a.payer || "").localeCompare(String(b.payer || ""));
  });
}

let reportTurnaroundReadController = null;
let reportTurnaroundReadSequence = 0;
function requestTurnaroundReport(request, key = reportApiCacheKey(request)) {
  const base = reportApiBase();
  if (!base || typeof fetch !== "function") return;
  const requestId = ++reportTurnaroundReadSequence;
  reportTurnaroundReadController?.abort();
  const requestStillCurrent = () => (
    requestId === reportTurnaroundReadSequence &&
    reportTurnaroundState.key === key
    && reportApiCacheKey(request) === key
  );
  reportTurnaroundState = { key, status: "pending", response: null, error: "", code: "", httpStatus: 0 };
  const slowTimer = setTimeout(() => { if (requestStillCurrent()) { reportTurnaroundState.slow = true; if (state.view === "reports") renderReports(); } }, 8000);
  const token = reportApiToken();
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  reportTurnaroundReadController = controller;
  const timeoutId = controller
    ? window.setTimeout(() => { try { controller.abort(); } catch (_) {} }, TURNAROUND_TIMEOUT_MS)
    : 0;
  const clearDeadline = () => { clearTimeout(slowTimer); if (timeoutId) window.clearTimeout(timeoutId); };
  reportSessionFetch((typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(`${base}/api/reports/turnaround`) : `${base}/api/reports/turnaround`), {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(request),
    ...(controller ? { signal: controller.signal } : {})
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      clearDeadline();
      if (!requestStillCurrent()) return;
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        reportTurnaroundState = {
          key,
          status: "error",
          response: null,
          error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body?.error?.message || body.message || `HTTP ${response.status}`),
          code: typeof body.error === "string" ? body.error : body?.error?.code || body.code || "",
          // Kept so the render can branch on the STATUS as well as the code. A backend rename of
          // the error code must not be able to silently demote an outage into a raw-error render.
          httpStatus: response.status
        };
        if (state.view === "clientHome") renderClientHome(); else renderReports();
        return;
      }
      reportTurnaroundState = { key, status: "ok", response: body, error: "", code: "", httpStatus: response.status };
      if (state.view === "clientHome") renderClientHome(); else renderReports();
    })
    .catch((error) => {
      clearDeadline();
      if (!requestStillCurrent()) return;
      // The request never landed (offline, DNS, CORS, the API process gone). That is the same
      // class of problem as a 503 and is emphatically not an application bug, so it is tagged as
      // an outage rather than surfacing `Failed to fetch` to a biller.
      //
      // OUR OWN DEADLINE LANDS HERE TOO, as an AbortError. It is the same class again — after
      // two minutes with no answer there is nothing to report and nothing to distinguish it
      // from the upstream being gone — but it gets its own sentence rather than the browser's
      // "The user aborted a request", which is both untrue (no user did) and unactionable.
      const timedOut = error && error.name === "AbortError";
      reportTurnaroundState = {
        key,
        status: "error",
        response: null,
        error: timedOut
          ? "Turnaround time did not come back within two minutes. The payment history it scans is the slowest read in the portal; try again, or narrow the date range."
          : error.message,
        code: "turnaround_report_unreachable",
        httpStatus: 0
      };
      if (state.view === "clientHome") renderClientHome(); else renderReports();
    });
}

function renderTurnaroundReport(model) {
  if (model.status === "pending") {
    // The honest version. This report reads every disbursement on the book and takes tens of
    // seconds on a full-population run; a bare "Measuring..." made that read as a stall. Naming
    // the cost and the shape of the work is the difference between waiting and wondering.
    return reportLoadingHtml("payer turnaround", reportTurnaroundState.slow);
  }
  if (model.status === "outage") {
    return turnaroundOutagePanel();
  }
  if (model.status === "error") {
    return turnaroundFailedPanel(model.error);
  }
  if (model.status === "unavailable") {
    return turnaroundUnavailablePanel();
  }
  return `
    ${renderTurnaroundKpiHeader(model)}
    ${renderTurnaroundDetail(model)}
    ${renderTurnaroundCoverage(model.coverage)}
  `;
}

// The report's own shell. Every state below keeps the report TITLE on screen: a biller who lands
// here has to know which report failed, and a bare sentence in an unlabelled panel does not say.
function turnaroundStatePanel(inner) {
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head"><div><h3>Turnaround Time — Days To Payment</h3></div></div>
    ${inner}
  </section>`;
}

// Quiet, in-card empty state (VOB (a)) — used for the transient pending line, which is not a
// failure and must not borrow a failure's colour.
function turnaroundNoticePanel(message) {
  return turnaroundStatePanel(`<p class="vob-empty">${message}</p>`);
}

// NOT CONFIGURED. Neutral hero empty state: nothing is broken, the mirror simply is not wired in
// this environment. No amber, no red — colour here would cry wolf.
function turnaroundUnavailablePanel() {
  return turnaroundStatePanel(`
    <div class="vob-empty--hero" role="status">
      <div class="vob-empty__puck" aria-hidden="true">⚙</div>
      <p class="vob-empty__title">Turnaround Time isn’t set up in this environment</p>
      <p class="vob-empty__body">Payment dates are not available from the source this environment reads, and the sample data carries none. Nothing is broken; there is nothing here to measure.</p>
    </div>`);
}

// UPSTREAM OUTAGE. The amber blocked variant (VOB (c)) — the one the design system reserves for
// "the emptiness BLOCKS a workflow", which is exactly this: the biller wanted a number and the
// source cannot give one. Deliberately carries NO raw error string; the biller cannot act on
// `synapse-mssql-driver-...` and it reads as their fault. It also refuses to imply zero — an
// unknown average is not a fast payer.
function turnaroundOutagePanel() {
  return turnaroundStatePanel(`
    <div class="vob-empty--hero vob-empty--blocked" role="status">
      <div class="vob-empty__puck" aria-hidden="true">⚠</div>
      <p class="vob-empty__title">The payment data source is not answering</p>
      <p class="vob-empty__body">This is not your filters and not your data — payment data is unavailable right now, so turnaround cannot be measured. These numbers are unknown, not zero. Try again in a few minutes; if it keeps failing, tell your portal administrator.</p>
      <div class="vob-empty__cta"><button type="button" class="ghost-button" data-turnaround-retry>Try again</button></div>
    </div>`);
}

// GENUINELY UNEXPECTED. Loud, and a different tier from the two above: red, role="alert", and it
// KEEPS the sanitized server detail, because this is the state where somebody has to read it.
// "Fail loud, not empty" — an unexpected failure must never quietly render an empty report.
function turnaroundFailedPanel(detail) {
  const line = String(detail || "").trim();
  return turnaroundStatePanel(`
    <div class="vob-alert vob-alert--error" role="alert">
      <strong>Turnaround Time did not load.</strong>
      <p>This is an unexpected failure, not a known outage, so the report is unknown rather than empty. Try again; if it happens again, send your portal administrator the detail below.</p>
      ${line ? `<p>Detail: ${escapeHtml(line)}</p>` : ""}
      <div class="vob-empty__cta"><button type="button" class="ghost-button" data-turnaround-retry>Try again</button></div>
    </div>`);
}

function turnaroundDayLabel(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const days = Number(value);
  return `${days.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${days === 1 ? "day" : "days"}`;
}

function renderTurnaroundKpiHeader(model) {
  const totals = model.totals || {};
  const samples = Number(totals.sampleCount || 0);
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Turnaround Time — Days To Payment</h3>
        <p>${escapeHtml(model.caption)}</p>
      </div>
      ${reportExportActionsHtml(`
        <button type="button" class="ghost-button" data-turnaround-export>Detail → CSV</button>
        <button type="button" class="primary-button" data-ar-print>Print / PDF</button>`)}
    </div>
    <div class="ar-kpi-grid">
      ${arKpiCard("Average Across Payers", turnaroundDayLabel(totals.weightedAverageDays), samples ? `Weighted by ${samples.toLocaleString()} paid ${samples === 1 ? "line" : "lines"}` : "No qualifying lines in range", "primary")}
      ${arKpiCard("Fastest Payer", totals.fastestPayer || "—", totals.fastestPayer ? `Pays in ${turnaroundDayLabel(totals.fastestDays)}` : "No qualifying lines in range")}
      ${arKpiCard("Slowest Payer", totals.slowestPayer || "—", totals.slowestPayer ? `Pays in ${turnaroundDayLabel(totals.slowestDays)}` : "No qualifying lines in range")}
    </div>
  </section>`;
}

function renderTurnaroundDetail(model) {
  const rows = Array.isArray(model.rows) ? model.rows : [];
  const slice = reportPageSlice(rows);
  const rowLabel = `${rows.length.toLocaleString()} ${rows.length === 1 ? "payer" : "payers"}`;
  const head = TURNAROUND_DETAIL_COLUMNS.map((col) => {
    const active = reportTurnaroundSortState.sortKey === col.key;
    const arrow = active ? (reportTurnaroundSortState.sortDir === "asc" ? " ▲" : " ▼") : "";
    const cls = `${col.type === "text" ? "" : "num"} ar-sortable${active ? " is-sorted" : ""}`.trim();
    return `<th class="${cls}" data-turnaround-sort="${col.key}" role="button" tabindex="0" aria-sort="${active ? (arrow.indexOf("▲") >= 0 ? "ascending" : "descending") : "none"}" aria-label="Sort by ${escapeHtml(col.label)}" title="Sort by ${escapeHtml(col.label)}">${escapeHtml(col.label)}<span aria-hidden="true">${arrow}</span></th>`;
  }).join("");
  const body = rows.length ? slice.rows.map((row) => `
    <tr>
      ${TURNAROUND_DETAIL_COLUMNS.map((col) => {
        const cell = col.type === "text"
          ? (col.key === "payer" && hasPermission(PERMISSIONS.VIEW_PHI) ? `<button type="button" class="linkbutton" data-turnaround-drill-payer="${escapeHtml(String(row[col.key] || ""))}">${escapeHtml(String(row[col.key] || "—"))}</button>` : escapeHtml(String(row[col.key] || "—")))
          : col.key === "sampleCount"
            ? Number(row[col.key] || 0).toLocaleString()
            : turnaroundDayLabel(row[col.key]);
        return `<td class="${col.type === "text" ? "" : "num"}" data-label="${escapeHtml(col.label)}">${cell}</td>`;
      }).join("")}
    </tr>`).join("") : `<tr><td colspan="${TURNAROUND_DETAIL_COLUMNS.length}">No payer reached the ${Number(model.coverage?.minimumSampleSize || 5)}-paid-line minimum in this range.</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Days To Payment By Payer</h3>
        <p>${rowLabel} · bill date → first primary insurance payment${model.truncated ? " — line cap reached; narrow the range for the full sample" : ""}.</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
    ${reportPagerHtml(slice)}
  </section>`;
}

// A payer missing from the table above reads as a broken report unless the reason is on screen.
// CT drops any line that was worked, part-paid, or paid by more than one payer, and any payer
// under 5 samples — so name every one of those instead of leaving a silent hole.
function renderTurnaroundCoverage(coverage) {
  if (!coverage || !Number(coverage.linesScanned)) return "";
  const reasons = coverage.excludedByReason && typeof coverage.excludedByReason === "object" ? coverage.excludedByReason : {};
  const reasonRows = Object.entries(reasons)
    .sort((a, b) => b[1] - a[1])
    .map(([reason, count]) => `<tr><th scope="row">${escapeHtml(turnaroundReasonLabel(reason))}</th><td class="num">${Number(count).toLocaleString()}</td></tr>`)
    .join("");
  const belowFloor = Array.isArray(coverage.payersBelowSampleFloor) ? coverage.payersBelowSampleFloor : [];
  const belowFloorNote = belowFloor.length
    ? `<p>Under the ${Number(coverage.minimumSampleSize || 5)}-line minimum, so not averaged: ${escapeHtml(belowFloor.slice(0, 12).map((entry) => `${entry.payer} (${entry.sampleCount})`).join(", "))}${belowFloor.length > 12 ? ` +${belowFloor.length - 12} more` : ""}.</p>`
    : "";
  // The payer-role match rate is an inference-quality signal, not a payer signal: a low number means
  // our payer-name matching is failing on this population, and the averages should be read with
  // suspicion rather than taken as payers being fast. Shown only when it was actually measured.
  const matchRate = coverage.payerRoleMatchRate;
  const matchedLines = Number(coverage.payerRoleLinesMatched || 0);
  const askedLines = Number(coverage.payerRoleLinesAsked || 0);
  const missedLines = Math.max(0, askedLines - matchedLines);
  const matchRateNote = matchRate == null
    ? ""
    : askedLines
      ? `<p>Payer-name matching resolved <strong>${matchRate}%</strong> of the payments it was asked about — ${matchedLines.toLocaleString()} of ${askedLines.toLocaleString()} — to the line's primary payer. The other ${missedLines.toLocaleString()} ${missedLines === 1 ? "was" : "were"} paid under a name we could not match to that payer, so ${missedLines === 1 ? "it is" : "they are"} excluded from the averages. Every other exclusion below was never asked the question, and a line that matched and then failed a bill- or payment-date check still counts here as a match.</p>`
      : `<p>Payer-name matching resolved <strong>${matchRate}%</strong> of the payments it was asked about to the line's primary payer. The rest were paid under a name we could not match to that payer, so they are excluded from the averages; every other exclusion below was never asked the question.</p>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>What Counted</h3>
        <p>${Number(coverage.linesIncluded || 0).toLocaleString()} of ${Number(coverage.linesScanned || 0).toLocaleString()} paid lines qualified. ${Number(coverage.payersReported || 0).toLocaleString()} ${Number(coverage.payersReported) === 1 ? "payer" : "payers"} met the ${Number(coverage.minimumSampleSize || 5)}-line minimum.</p>
        ${belowFloorNote}
        ${matchRateNote}
      </div>
    </div>
    ${reasonRows ? `
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr><th>Excluded because</th><th class="num">Lines</th></tr></thead>
        <tbody>${reasonRows}</tbody>
      </table>
    </div>` : ""}
  </section>`;
}

function turnaroundReasonLabel(reason) {
  const labels = {
    "has-service-line-notes": "Line carries biller notes (already worked)",
    "has-follow-up-action": "Line carries a follow-up action (already worked)",
    "no-primary-payer": "No primary payer on the line",
    "not-paid": "Not finalized as Paid",
    "not-exactly-one-active-payment": "More than one insurance payment on the line",
    "single-payment-not-primary": "The paying payer is not the line's primary",
    "invalid-bill-aging": "No usable bill date",
    "invalid-payment-date": "No usable payment date",
    "negative-cycle": "Payment predates the bill date (data issue)"
  };
  return labels[reason] || reason;
}

function bindTurnaroundReportControls() {
  bindReportPager();
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-turnaround-drill-payer]").forEach((button) => button.addEventListener("click", () => { const payer = button.dataset.turnaroundDrillPayer || ""; hostedReportDrillState.view = "turnaround"; hostedReportDrillState.sessionKey = preparedSnapshotSessionKey(); hostedReportDrillState.queryKey = JSON.stringify(turnaroundReportFilters()); hostedReportDrillState.root = { payer }; hostedReportDrillState.path = []; hostedReportDrillState.targetLevel = "payer"; hostedReportDrillState.response = null; requestHostedReportDrill(1); }));
  host.querySelectorAll("[data-turnaround-sort]").forEach((th) => {
    const apply = () => {
      const key = th.getAttribute("data-turnaround-sort");
      if (reportTurnaroundSortState.sortKey === key) {
        reportTurnaroundSortState.sortDir = reportTurnaroundSortState.sortDir === "asc" ? "desc" : "asc";
      } else {
        reportTurnaroundSortState.sortKey = key;
        const col = TURNAROUND_DETAIL_COLUMNS.find((candidate) => candidate.key === key);
        reportTurnaroundSortState.sortDir = col && col.type === "text" ? "asc" : "desc";
      }
      reportPageState.page = 1;
      renderReports();
    };
    th.addEventListener("click", apply);
    th.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); apply(); }
    });
  });
  const exportBtn = host.querySelector("[data-turnaround-export]");
  if (exportBtn) exportBtn.addEventListener("click", turnaroundExportCsv);
  // Retry lives on the outage / unexpected-failure states. Clearing the cache key (not just the
  // status) is what re-fires the request: turnaroundReportModel only calls out when the key
  // differs from the one it is holding, so flipping status alone would render pending forever.
  const retryBtn = host.querySelector("[data-turnaround-retry]");
  if (retryBtn) retryBtn.addEventListener("click", () => {
    reportTurnaroundState = { key: "", status: "idle", response: null, error: "", code: "", httpStatus: 0 };
    renderReports();
  });
  const printBtn = host.querySelector("[data-ar-print]");
  if (printBtn) printBtn.addEventListener("click", () => window.print());
}

function turnaroundExportCsv() {
  const model = turnaroundReportModel();
  if (model.status !== "ok") {
    showToast("Turnaround data is not ready to export yet.");
    return;
  }
  const rows = Array.isArray(model.rows) ? model.rows : [];
  const header = TURNAROUND_DETAIL_COLUMNS.map((col) => col.label);
  const lines = rows.map((row) => TURNAROUND_DETAIL_COLUMNS.map((col) => row[col.key]));
  const filename = model.truncated ? "turnaround-time-by-payer-partial.csv" : "turnaround-time-by-payer.csv";
  arDownloadFile(filename, arToCsv([header, ...lines]), "text/csv;charset=utf-8;");
  if (model.truncated) showToast("Exported the sampled payers — line cap reached; narrow the date range for the full set.");
}

// ===========================================================================
// RATE ISSUES (2026-07-28) — the fee-schedule cleanup queue.
//
// CT ships this as a LITE workbook tab; we had the export but no interactive view, so the only way
// to see the rate backlog was to generate a workbook. Pure client-side over the SAME report dataset
// the other views use — no new API and no new permission, so it works for anyone who can open
// Reports, and in demo mode.
//
// WHY THE MONEY COLUMNS ARE CHARGES AND PAID, NOT EXPECTED. A missing rate resolves to rate 0,
// which zeroes expected (parity audit 2026-07-22 domain 5) — an "Expected" column here would be a
// column of zeroes. The real harm is the LAST column: money the payer already sent on a line we
// have no rate for, so nobody can tell whether it was paid correctly. That is the number that
// justifies the cleanup work, so it is also the default sort.
//
// The payer shown is the one whose rate is missing — primary for a missing primary rate, secondary
// for a missing secondary — because that is the row a biller goes and adds. The two are separate
// buckets for the same reason the Match slicer splits them (app.js:611): a missing primary rate
// blocks the whole line, a missing secondary only blocks the crossover.
// ===========================================================================

const RATE_ISSUE_DETAIL_COLUMNS = [
  { key: "client", label: "Client", type: "text" },
  { key: "payer", label: "Payer Missing A Rate", type: "text" },
  { key: "procedure", label: "CPT", type: "text" },
  { key: "issue", label: "Issue", type: "text" },
  { key: "lines", label: "Lines", type: "num" },
  { key: "units", label: "Units", type: "num" },
  { key: "charges", label: "Charges", type: "money" },
  { key: "insurancePaid", label: "Paid Without A Rate", type: "money" },
  { key: "openAr", label: "Open AR", type: "money" }
];

const RATE_ISSUE_PRIMARY = "Missing primary rate";
const RATE_ISSUE_SECONDARY = "Missing secondary rate";

// One row per affected SERVICE LINE. Reads the line's finalized match through the same precedence
// the grid uses (service.status || service.match || claim.match, app.js:42836) so a line cannot be
// classified differently here than it renders in the workbench.
function rateIssueLineRows(claims) {
  const rows = [];
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    (Array.isArray(claim.services) ? claim.services : []).forEach((service) => {
      // ASK THE ENGINE, DO NOT READ THE STRING.
      //
      // The first cut keyed purely on match === "No Primary Rate" / "No Secondary Rate" and reported
      // ZERO against live data. Those values are produced by our own financial engine; the MIRROR's
      // match_status column carries only Paid / Not Paid / Under Paid / Over Paid, so the string is
      // never present on the live report dataset. A live probe of the match vocabulary confirmed it:
      // 0 no-rate strings across 60,000 rows, while 11,939 of those lines had no primary rate at all
      // and 4,765 of them had already been paid. The report looked clean and was blind.
      //
      // serviceFinancialView runs the same recompute the grid renders from and exposes
      // primaryRateMissing / secondaryRateMissing directly, so this now agrees with the workbench by
      // construction instead of by coincidence. The match string is still honoured as a fallback,
      // because the warehouse gold projection DOES emit the split no-rate values.
      const view = serviceFinancialView(service, claim);
      const match = String((service && (service.status || service.match)) || (claim && claim.match) || "").trim();
      const primaryMissing = Boolean(view.primaryRateMissing) || match === "Missing Fee Schedule" || match === "No Primary Rate" || matchValueIsLegacyCollapsedNoRate(match);
      const secondaryMissing = Boolean(view.secondaryRateMissing) || match === "No Secondary Rate";
      if (!primaryMissing && !secondaryMissing) return;
      // A line can be missing both. Report the PRIMARY issue then, not both: a missing primary rate
      // blocks the whole line, so fixing the secondary first would not make the line billable, and
      // double-counting would overstate the backlog.
      const secondaryOnly = secondaryMissing && !primaryMissing;
      const issue = secondaryOnly ? RATE_ISSUE_SECONDARY : RATE_ISSUE_PRIMARY;
      const payer = secondaryOnly
        ? String(service.secondaryPayer || claim.secondaryPayer || "").trim()
        : String(service.primaryPayer || claim.primaryPayer || "").trim();
      rows.push({
        claimId: String(claim.claimId || ""),
        client: String(claim.client || "—").trim() || "—",
        payer: payer || "—",
        procedure: String(service.procedure || "—").trim() || "—",
        issue,
        units: quantityNumber(service.units),
        charges: moneyNumber(service.charges),
        insurancePaid: moneyNumber(service.finalInsurancePaid),
        openAr: moneyNumber(service.finalInsuranceBalance)
      });
    });
  });
  return rows;
}

// Collapse to the unit of WORK: one row per client x payer x CPT x issue is exactly one fee-schedule
// rate someone has to go add, which is why the KPI counts these groups rather than lines.
// ONE key function for the report AND the drill — see the note on cptPayerGroupKey.
function rateIssueGroupKey(row) {
  return [row.client, row.payer, row.procedure, row.issue].join("\u001f");
}

function rateIssueGroupAttr(row) {
  return encodeURIComponent(rateIssueGroupKey(row));
}

function rateIssueCellLines(attr, claims) {
  return rateIssueLineRows(claims).filter((row) => rateIssueGroupAttr(row) === attr);
}

function rateIssueDrillClaimIds(attr) {
  return () => new Set(rateIssueCellLines(attr, getReportClaims(reportSourceClaims()))
    .map((row) => String(row.claimId || "")).filter(Boolean));
}

function rateIssueDrillFooter(attr) {
  return () => ({ lines: rateIssueCellLines(attr, getReportClaims(reportSourceClaims())).length });
}

function rateIssueBuildRows(lineRows) {
  const groups = new Map();
  (Array.isArray(lineRows) ? lineRows : []).forEach((row) => {
    const key = rateIssueGroupKey(row);
    if (!groups.has(key)) {
      groups.set(key, {
        client: row.client, payer: row.payer, procedure: row.procedure, issue: row.issue,
        lines: 0, units: 0, charges: 0, insurancePaid: 0, openAr: 0
      });
    }
    const g = groups.get(key);
    g.lines += 1;
    g.units += row.units;
    g.charges += row.charges;
    g.insurancePaid += row.insurancePaid;
    g.openAr += row.openAr;
  });
  const rows = Array.from(groups.values());
  const totals = rows.reduce((t, g) => {
    t.lines += g.lines;
    t.units += g.units;
    t.charges += g.charges;
    t.insurancePaid += g.insurancePaid;
    t.openAr += g.openAr;
    if (g.issue === RATE_ISSUE_PRIMARY) t.primaryGroups += 1; else t.secondaryGroups += 1;
    return t;
  }, { lines: 0, units: 0, charges: 0, insurancePaid: 0, openAr: 0, primaryGroups: 0, secondaryGroups: 0 });
  totals.rateKeys = rows.length;
  return { rows: rateIssueSortRows(rows), totals };
}

function rateIssueSortRows(rows) {
  const col = RATE_ISSUE_DETAIL_COLUMNS.find((candidate) => candidate.key === reportRateIssueSortState.sortKey)
    || RATE_ISSUE_DETAIL_COLUMNS[7];
  const dir = reportRateIssueSortState.sortDir === "asc" ? 1 : -1;
  return rows.slice().sort((a, b) => {
    if (col.type === "text") {
      return dir * String(a[col.key] || "").localeCompare(String(b[col.key] || ""))
        || String(a.payer || "").localeCompare(String(b.payer || ""));
    }
    return dir * (Number(a[col.key] || 0) - Number(b[col.key] || 0))
      || String(a.payer || "").localeCompare(String(b.payer || ""));
  });
}

function renderRateIssuesReport(model, scannedClaims) {
  const totals = model.totals;
  const rows = model.rows;
  const head = RATE_ISSUE_DETAIL_COLUMNS.map((col) => {
    const active = reportRateIssueSortState.sortKey === col.key;
    const arrow = active ? (reportRateIssueSortState.sortDir === "asc" ? " ▲" : " ▼") : "";
    const cls = `${col.type === "text" ? "" : "num"} ar-sortable${active ? " is-sorted" : ""}`.trim();
    return `<th class="${cls}" data-rate-issue-sort="${col.key}" role="button" tabindex="0" aria-sort="${active ? (arrow.indexOf("▲") >= 0 ? "ascending" : "descending") : "none"}" aria-label="Sort by ${escapeHtml(col.label)}" title="Sort by ${escapeHtml(col.label)}">${escapeHtml(col.label)}<span aria-hidden="true">${arrow}</span></th>`;
  }).join("");
  const body = rows.length ? rows.map((row) => `
    <tr>
      ${RATE_ISSUE_DETAIL_COLUMNS.map((col, index) => {
        const cell = col.type === "money"
          ? money(row[col.key])
          : col.type === "num"
            ? Number(row[col.key] || 0).toLocaleString()
            : escapeHtml(String(row[col.key] || "—"));
        // The drill handle rides the FIRST cell of the row, which is the unit of work the row
        // names. Only that cell gains the affordance class, so nothing else about the table moves.
        const drill = index === 0
          ? ` data-rate-issue-drill-key="${escapeHtml(rateIssueGroupAttr(row))}" role="button" tabindex="0" title="Show the claims carrying these unrated lines"`
          : "";
        const classes = [col.type === "text" ? "" : "num", index === 0 ? "ar-drillable" : ""].filter(Boolean).join(" ");
        return `<td class="${classes}" data-label="${escapeHtml(col.label)}"${drill}>${cell}</td>`;
      }).join("")}
    </tr>`).join("") : `<tr><td colspan="${RATE_ISSUE_DETAIL_COLUMNS.length}">No rate issues in the active filter — every line resolved to a fee-schedule rate.</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Rate Issues — Fee Schedule Cleanup</h3>
        <p>Service lines whose finalized match is No Primary Rate or No Secondary Rate, grouped into the fee-schedule rows someone has to add. Scanned ${Number(scannedClaims || 0).toLocaleString()} claim ${Number(scannedClaims) === 1 ? "line" : "lines"} in the active filter.</p>
      </div>
      ${reportExportActionsHtml(`
        <button type="button" class="ghost-button" data-rate-issue-export>Detail → CSV</button>
        <button type="button" class="primary-button" data-ar-print>Print / PDF</button>`)}
    </div>
    <div class="ar-kpi-grid">
      ${arKpiCard("Rates To Add", Number(totals.rateKeys || 0).toLocaleString(), totals.rateKeys ? `${Number(totals.primaryGroups || 0).toLocaleString()} primary · ${Number(totals.secondaryGroups || 0).toLocaleString()} secondary` : "Nothing to fix in this filter", "primary")}
      ${arKpiCard("Lines Affected", Number(totals.lines || 0).toLocaleString(), `${Number(totals.units || 0).toLocaleString()} units · ${money(totals.charges)} charged`)}
      ${arKpiCard("Paid Without A Rate", money(totals.insurancePaid), totals.insurancePaid ? "Already paid on lines we cannot validate" : "No payments on unrated lines")}
    </div>
  </section>
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Missing Rates By Payer And CPT</h3>
        <p>One row per fee-schedule rate to add. Add them under Setup → Fee Schedules, which also carries the current worklist and the add-rate flow.</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </section>`;
}

function bindRateIssuesReportControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-rate-issue-sort]").forEach((th) => {
    const apply = () => {
      const key = th.getAttribute("data-rate-issue-sort");
      if (reportRateIssueSortState.sortKey === key) {
        reportRateIssueSortState.sortDir = reportRateIssueSortState.sortDir === "asc" ? "desc" : "asc";
      } else {
        reportRateIssueSortState.sortKey = key;
        const col = RATE_ISSUE_DETAIL_COLUMNS.find((candidate) => candidate.key === key);
        reportRateIssueSortState.sortDir = col && col.type === "text" ? "asc" : "desc";
      }
      renderReports();
    };
    th.addEventListener("click", apply);
    th.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); apply(); }
    });
  });
  host.querySelectorAll("[data-rate-issue-drill-key]").forEach((el) => {
    const open = () => {
      const attr = el.getAttribute("data-rate-issue-drill-key") || "";
      const live = getReportClaims(reportSourceClaims());
      const clicked = rateIssueBuildRows(rateIssueLineRows(live)).rows.find((row) => rateIssueGroupAttr(row) === attr);
      const dims = [["Client", clicked && clicked.client], ["Payer", clicked && clicked.payer],
        ["CPT", clicked && clicked.procedure], ["Issue", clicked && clicked.issue]]
        .filter(([, value]) => value && value !== "—")
        .map(([name, value]) => `${name}: ${value}`);
      const label = dims.join(" · ");
      // Open AR here is the LINE's insurance balance; the footer's "AR (Exp−Paid)" and "Balance"
      // are claim-level. Three concepts, and two of them are called AR — so this row's own
      // figures are named and grained rather than dropped into the footer's slots.
      if (clicked) {
        dims.push(`Row ${formatNounCount(clicked.lines, "service line")} · Charges ${money(clicked.charges)} · Paid Without A Rate ${money(clicked.insurancePaid)} · Open AR ${money(clicked.openAr)} — service-line grain`);
      }
      drillIntoReportRow({
        view: reportArState.view,
        label,
        dims,
        claimIds: rateIssueDrillClaimIds(attr),
        footerOverride: rateIssueDrillFooter(attr)
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
  const exportBtn = host.querySelector("[data-rate-issue-export]");
  if (exportBtn) exportBtn.addEventListener("click", rateIssuesExportCsv);
  const printBtn = host.querySelector("[data-ar-print]");
  if (printBtn) printBtn.addEventListener("click", () => window.print());
}

// THE claim source for the Reports family - renderReports() calls this, and so
// does every report CSV exporter. In live API mode the reports read
// reportDatasetState.claims; sourceClaims (and therefore getClaims()) is deliberately EMPTY there,
// so an export that reaches for getClaims() writes an empty sheet against live data while looking
// like it worked - that shipped, and the AR CSVs wrote 0 rows against a $7.7M screen.
// The gate is trackerApiEnabled(), matching renderReports exactly. It is NOT
// reportApiEnabled(): that one usually tracks the tracker mode but diverges the
// moment TMS_REPORT_API_MODE is set on its own, which is the same drift again.
function reportSourceClaims() {
  return trackerApiEnabled() ? (reportDatasetState.claims || []) : getClaims();
}

function rateIssuesExportCsv() {
  const model = rateIssueBuildRows(rateIssueLineRows(getReportClaims(reportSourceClaims())));
  const header = RATE_ISSUE_DETAIL_COLUMNS.map((col) => col.label);
  const lines = model.rows.map((row) => RATE_ISSUE_DETAIL_COLUMNS.map((col) => col.type === "money" ? Number(row[col.key]).toFixed(2) : row[col.key]));
  arDownloadFile("rate-issues-by-payer-cpt.csv", arToCsv([header, ...lines]), "text/csv;charset=utf-8;");
  if (!model.rows.length) showToast("No rate issues in the active filter — exported an empty sheet.");
}

// ===========================================================================
// PAYER CLASSIFICATION (2026-07-28) — which payers are unclassified, and what that is costing.
//
// MIRROR OF backend/payerClassification.js. There is no stored payer class anywhere the portal can
// reach (confirmed live: primaryPayerClass / payerClassFilter / network absent from all 60,000
// report-dataset rows), so the class is inferred from the payer NAME — the same inference the
// workbook export has always shipped, which stamps Classification Source: export-payer-name.
// backend/verify_payer_classification.js runs one corpus through the backend module AND this copy
// and fails if they disagree, so the two cannot drift.
//
// ORDER IS SIGNIFICANT — Medicaid before Commercial, because Medicaid managed-care plans are often
// operated by commercial carriers (Amerigroup/WellPoint, "BCBS ... Medicaid") and classifying those
// as Commercial would skip the Medicaid rule below.
//
// This is not cosmetic labelling: the financial engine forces a line's positive patient balance to
// ZERO when the payer class is Medicaid. A payer that should read Medicaid but falls through to
// Unclassified therefore carries a patient balance it should not — so the Unclassified bucket is a
// money bug in waiting, which is why it is the KPI and the default sort rather than a footnote.
// ===========================================================================

// The Medicaid test is the FINANCIAL ENGINE's marker list (MEDICAID_MARKERS), not a looser regex.
// This report warns that an unclassified payer may skip the Medicaid patient-balance rule, so it
// must ask the same question that rule asks. Live proof it did not: "PEACHSTATE HEALTH PLAN GEORGIA
// FAMILIES MCAIDADV" (3,847 lines, $490k open AR) read Unclassified and led the queue while the
// engine already treats PEACHSTATE as Medicaid.
const PAYER_CLASS_MEDICAID_MARKERS = [
  "MEDICAID", "TENNCARE", "PEACHSTATE", "CARE SOURCE", "CARESOURCE",
  "AMERIGROUP", "WELLPOINT", "HEALTHKEEPERS", "BETTER HEALTH"
];
const PAYER_CLASS_MEDICAID_EXTRA = /managed care|horizon nj health|mcaid/i;
function isMedicaidPayerNameForClass(payer) {
  const text = String(payer || "").toUpperCase();
  return PAYER_CLASS_MEDICAID_MARKERS.some((marker) => text.includes(marker)) || PAYER_CLASS_MEDICAID_EXTRA.test(text);
}
const PAYER_CLASS_RULES = [
  { className: "Medicaid", test: isMedicaidPayerNameForClass },
  { className: "Tricare", pattern: /tricare|pgba/i },
  { className: "Patient", pattern: /self|patient/i },
  { className: "Commercial", pattern: /bcbs|blue|aetna|cigna|optum|united|uhc|carefirst|anthem|commercial/i }
];
const UNCLASSIFIED_PAYER_CLASS = "Other";
// Placeholder values meaning "no payer" rather than "a payer we failed to classify" — see the
// backend module. Defensive: a live sweep of all 256 payer names found zero of these, but the demo
// dataset carries a literal "None" secondary payer that led the unclassified KPI.
const PAYER_SENTINEL_PATTERN = /^(none|n\/a|unknown|null|nil|no payer|not applicable|tbd|secondary placeholder|-+|\.+)$/i;

// A blank payer returns "" — there is nothing to classify, and counting blanks as Unclassified
// would inflate the cleanup queue with rows nobody can act on.
function classifyPayerName(payer) {
  const text = String(payer == null ? "" : payer).trim();
  if (!text || PAYER_SENTINEL_PATTERN.test(text)) return "";
  const rule = PAYER_CLASS_RULES.find((candidate) => (candidate.test ? candidate.test(text) : candidate.pattern.test(text)));
  return rule ? rule.className : UNCLASSIFIED_PAYER_CLASS;
}

const PAYER_CLASS_DETAIL_COLUMNS = [
  { key: "payer", label: "Payer", type: "text" },
  { key: "payerClass", label: "Inferred Class", type: "text" },
  { key: "roles", label: "Seen As", type: "text" },
  { key: "clients", label: "Clients", type: "num" },
  { key: "lines", label: "Lines", type: "num" },
  { key: "charges", label: "Charges", type: "money" },
  { key: "insurancePaid", label: "Paid", type: "money" },
  { key: "openAr", label: "Open AR", type: "money" }
];

// One row per payer NAME, counting the line wherever that payer appears — primary or secondary.
// Payer identity is the raw name: there is no payer id on the report dataset, and collapsing names
// would hide the very spelling variants a classification cleanup needs to see.
function payerClassLineRows(claims) {
  const rows = [];
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    (Array.isArray(claim.services) ? claim.services : []).forEach((service) => {
      const charges = moneyNumber(service.charges);
      const paid = moneyNumber(service.finalInsurancePaid);
      const openAr = moneyNumber(service.finalInsuranceBalance);
      const client = String(claim.client || "—").trim() || "—";
      // classifyPayerName returns "" for blanks AND placeholder values ("None", "N/A", ...), so it
      // doubles as the "is this a real payer?" test. Without this a placeholder would appear as a
      // payer row carrying a blank class — worse than omitting it, because it looks like a payer
      // nobody classified.
      const primaryName = String(service.primaryPayer || claim.primaryPayer || "").trim();
      const secondaryName = String(service.secondaryPayer || claim.secondaryPayer || "").trim();
      const primary = classifyPayerName(primaryName) ? primaryName : "";
      const secondary = classifyPayerName(secondaryName) ? secondaryName : "";
      // Money is attributed to the PRIMARY payer only. A secondary appearance still counts the
      // line (so the payer shows up and can be classified) but contributes no dollars, or every
      // dual-coverage line would be counted twice across the report.
      const claimId = String(claim.claimId || "");
      if (primary) rows.push({ claimId, payer: primary, role: "Primary", client, lines: 1, charges, insurancePaid: paid, openAr });
      if (secondary) rows.push({ claimId, payer: secondary, role: "Secondary", client, lines: 1, charges: 0, insurancePaid: 0, openAr: 0 });
    });
  });
  return rows;
}

// ONE key function for the report AND the drill — see the note on cptPayerGroupKey. The key is
// the raw payer NAME, deliberately: collapsing spelling variants would hide the rows this
// cleanup exists to find.
function payerClassGroupKey(row) {
  return String(row.payer || "");
}

function payerClassGroupAttr(row) {
  return encodeURIComponent(payerClassGroupKey(row));
}

// A payer's lines are counted wherever it appears, primary OR secondary, so this returns the
// same rows the aggregate counted — including the secondary appearances that carry no money.
function payerClassCellLines(attr, claims) {
  return payerClassLineRows(claims).filter((row) => payerClassGroupAttr(row) === attr);
}

function payerClassDrillClaimIds(attr) {
  return () => new Set(payerClassCellLines(attr, getReportClaims(reportSourceClaims()))
    .map((row) => String(row.claimId || "")).filter(Boolean));
}

function payerClassDrillFooter(attr) {
  return () => ({ lines: payerClassCellLines(attr, getReportClaims(reportSourceClaims())).length });
}

function payerClassBuildRows(lineRows) {
  const groups = new Map();
  (Array.isArray(lineRows) ? lineRows : []).forEach((row) => {
    if (!groups.has(row.payer)) {
      groups.set(row.payer, {
        payer: row.payer, payerClass: classifyPayerName(row.payer),
        _roles: new Set(), _clients: new Set(),
        lines: 0, charges: 0, insurancePaid: 0, openAr: 0
      });
    }
    const g = groups.get(row.payer);
    g._roles.add(row.role);
    g._clients.add(row.client);
    g.lines += row.lines;
    g.charges += row.charges;
    g.insurancePaid += row.insurancePaid;
    g.openAr += row.openAr;
  });
  const rows = Array.from(groups.values()).map((g) => ({
    payer: g.payer,
    payerClass: g.payerClass,
    roles: ["Primary", "Secondary"].filter((role) => g._roles.has(role)).join(" + "),
    clients: g._clients.size,
    lines: g.lines,
    charges: g.charges,
    insurancePaid: g.insurancePaid,
    openAr: g.openAr
  }));
  const totals = rows.reduce((t, r) => {
    t.payers += 1;
    t.lines += r.lines;
    t.charges += r.charges;
    t.openAr += r.openAr;
    if (r.payerClass === UNCLASSIFIED_PAYER_CLASS) {
      t.unclassifiedPayers += 1;
      t.unclassifiedOpenAr += r.openAr;
      t.unclassifiedLines += r.lines;
    }
    t.byClass[r.payerClass] = (t.byClass[r.payerClass] || 0) + 1;
    return t;
  }, { payers: 0, lines: 0, charges: 0, openAr: 0, unclassifiedPayers: 0, unclassifiedOpenAr: 0, unclassifiedLines: 0, byClass: {} });
  return { rows: payerClassSortRows(rows), totals };
}

// Unclassified payers float to the top of any sort. They are the only actionable rows here, and a
// payer with a small balance can still be the one skipping the Medicaid rule.
function payerClassSortRows(rows) {
  const col = PAYER_CLASS_DETAIL_COLUMNS.find((candidate) => candidate.key === reportPayerClassSortState.sortKey)
    || PAYER_CLASS_DETAIL_COLUMNS[7];
  const dir = reportPayerClassSortState.sortDir === "asc" ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const aUnclassified = a.payerClass === UNCLASSIFIED_PAYER_CLASS ? 0 : 1;
    const bUnclassified = b.payerClass === UNCLASSIFIED_PAYER_CLASS ? 0 : 1;
    if (aUnclassified !== bUnclassified) return aUnclassified - bUnclassified;
    if (col.type === "text") return dir * String(a[col.key] || "").localeCompare(String(b[col.key] || ""));
    return dir * (Number(a[col.key] || 0) - Number(b[col.key] || 0)) || String(a.payer || "").localeCompare(String(b.payer || ""));
  });
}

function renderPayerClassificationReport(model, scannedClaims) {
  const totals = model.totals;
  const head = PAYER_CLASS_DETAIL_COLUMNS.map((col) => {
    const active = reportPayerClassSortState.sortKey === col.key;
    const arrow = active ? (reportPayerClassSortState.sortDir === "asc" ? " ▲" : " ▼") : "";
    const cls = `${col.type === "text" ? "" : "num"} ar-sortable${active ? " is-sorted" : ""}`.trim();
    return `<th class="${cls}" data-payer-class-sort="${col.key}" role="button" tabindex="0" aria-sort="${active ? (arrow.indexOf("▲") >= 0 ? "ascending" : "descending") : "none"}" aria-label="Sort by ${escapeHtml(col.label)}" title="Sort by ${escapeHtml(col.label)}">${escapeHtml(col.label)}<span aria-hidden="true">${arrow}</span></th>`;
  }).join("");
  const body = model.rows.length ? model.rows.map((row) => `
    <tr${row.payerClass === UNCLASSIFIED_PAYER_CLASS ? ' class="is-flagged"' : ""}>
      ${PAYER_CLASS_DETAIL_COLUMNS.map((col, index) => {
        const cell = col.type === "money" ? money(row[col.key])
          : col.type === "num" ? Number(row[col.key] || 0).toLocaleString()
          : escapeHtml(String(row[col.key] || "—"));
        // Drill handle on the payer cell — the row IS a payer, so that is the cell that names it.
        const drill = index === 0
          ? ` data-payer-class-drill-key="${escapeHtml(payerClassGroupAttr(row))}" role="button" tabindex="0" title="Show the claims this payer appears on"`
          : "";
        const classes = [col.type === "text" ? "" : "num", index === 0 ? "ar-drillable" : ""].filter(Boolean).join(" ");
        return `<td class="${classes}" data-label="${escapeHtml(col.label)}"${drill}>${cell}</td>`;
      }).join("")}
    </tr>`).join("") : `<tr><td colspan="${PAYER_CLASS_DETAIL_COLUMNS.length}">No payers in the active filter.</td></tr>`;
  const classMix = Object.entries(totals.byClass).sort((a, b) => b[1] - a[1])
    .map(([name, count]) => `${escapeHtml(name)} ${count}`).join(" · ");
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Payer Classification</h3>
        <p>Class is <strong>inferred from the payer name</strong> — no payer-class field exists in the source data. Scanned ${Number(scannedClaims || 0).toLocaleString()} claim ${Number(scannedClaims) === 1 ? "line" : "lines"}. ${classMix ? escapeHtml("Mix: ") + classMix + "." : ""}</p>
      </div>
      ${reportExportActionsHtml(`
        <button type="button" class="ghost-button" data-payer-class-export>Detail → CSV</button>
        <button type="button" class="primary-button" data-ar-print>Print / PDF</button>`)}
    </div>
    <div class="ar-kpi-grid">
      ${arKpiCard("Unclassified Payers", Number(totals.unclassifiedPayers || 0).toLocaleString(), totals.unclassifiedPayers ? `${Number(totals.unclassifiedLines || 0).toLocaleString()} lines · ${money(totals.unclassifiedOpenAr)} open AR` : "Every payer matched a class rule", totals.unclassifiedPayers ? "primary" : "")}
      ${arKpiCard("Payers In Book", Number(totals.payers || 0).toLocaleString(), `${Number(totals.lines || 0).toLocaleString()} lines · ${money(totals.charges)} charged`)}
      ${arKpiCard("Open AR Across Payers", money(totals.openAr), "Primary-payer attribution only")}
    </div>
  </section>
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Payers And Inferred Class</h3>
        <p>Unclassified payers sort first regardless of column. Class drives the Medicaid patient-balance rule, so an unclassified payer can misstate patient responsibility.</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </section>`;
}

function bindPayerClassificationControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-payer-class-sort]").forEach((th) => {
    const apply = () => {
      const key = th.getAttribute("data-payer-class-sort");
      if (reportPayerClassSortState.sortKey === key) {
        reportPayerClassSortState.sortDir = reportPayerClassSortState.sortDir === "asc" ? "desc" : "asc";
      } else {
        reportPayerClassSortState.sortKey = key;
        const col = PAYER_CLASS_DETAIL_COLUMNS.find((candidate) => candidate.key === key);
        reportPayerClassSortState.sortDir = col && col.type === "text" ? "asc" : "desc";
      }
      renderReports();
    };
    th.addEventListener("click", apply);
    th.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); apply(); } });
  });
  host.querySelectorAll("[data-payer-class-drill-key]").forEach((el) => {
    const open = () => {
      const attr = el.getAttribute("data-payer-class-drill-key") || "";
      const live = getReportClaims(reportSourceClaims());
      const clicked = payerClassBuildRows(payerClassLineRows(live)).rows.find((row) => payerClassGroupAttr(row) === attr);
      const dims = [["Payer", clicked && clicked.payer], ["Class", clicked && clicked.payerClass]]
        .filter(([, value]) => value && value !== "—")
        .map(([name, value]) => `${name}: ${value}`);
      const label = dims.join(" · ");
      // The row's money is PRIMARY-attribution only — a secondary appearance counts the line and
      // contributes nothing — while the footer below sums every claim in full, both payers
      // included. That is a real gap, so it is named here rather than left for a biller to
      // discover by subtracting.
      if (clicked) {
        dims.push(`Row ${formatNounCount(clicked.lines, "service line")} · Charges ${money(clicked.charges)} · Paid ${money(clicked.insurancePaid)} · Open AR ${money(clicked.openAr)} — service-line grain, primary attribution only`);
      }
      drillIntoReportRow({
        view: reportArState.view,
        label,
        dims,
        claimIds: payerClassDrillClaimIds(attr),
        footerOverride: payerClassDrillFooter(attr)
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
  const exportBtn = host.querySelector("[data-payer-class-export]");
  if (exportBtn) exportBtn.addEventListener("click", () => {
    const model = payerClassBuildRows(payerClassLineRows(getReportClaims(reportSourceClaims())));
    const header = PAYER_CLASS_DETAIL_COLUMNS.map((col) => col.label);
    const lines = model.rows.map((row) => PAYER_CLASS_DETAIL_COLUMNS.map((col) => col.type === "money" ? Number(row[col.key]).toFixed(2) : row[col.key]));
    arDownloadFile("payer-classification.csv", arToCsv([header, ...lines]), "text/csv;charset=utf-8;");
  });
  const printBtn = host.querySelector("[data-ar-print]");
  if (printBtn) printBtn.addEventListener("click", () => window.print());
}

// ===========================================================================
// REVIEW QUEUE (2026-07-28) — the portal's OPERATIONAL work queue.
//
// NOT a port of CT's Review Queue, and deliberately so. CT's is a payment-attribution lane
// (unmatched remits, disbursement variances — its validation baseline had 21 rows). Every field
// that lane needs is absent from our report dataset: disbursementVariance, paymentPayerReview,
// paymentReconciliationStatus, totalDisbursed and paymentPayers are present on 0 of 60,000 live
// rows. Porting it would render permanently empty, which is exactly how the Rate Issues view
// shipped blind. That port stays blocked on David's payment/EOB load.
//
// What IS fully populated is the portal's own workflow state — queueReason, statusCategory,
// lifecycleStage — on all 60,000 rows. So this answers the question a biller actually has: what
// open work is there, how old is it, and who owns it. Owner directive 2026-07-14: the portal's
// main point is billers and the workflow working.
//
// CLOSED WORK IS EXCLUDED. 41,401 of 60,000 live lines sit in queueReason 'closed'; a queue that
// lists finished work is not a queue. The caption states the exclusion rather than hiding it.
// ===========================================================================

const REVIEW_QUEUE_CLOSED_REASONS = ["closed"];
// The statusCategory equivalents, for the fallback above. Live vocabulary: Paid/Closed, Open AR,
// Secondary.
const REVIEW_QUEUE_CLOSED_STATUS_CATEGORIES = ["paid/closed", "closed"];

const REVIEW_QUEUE_DETAIL_COLUMNS = [
  { key: "queue", label: "Queue", type: "text" },
  { key: "stage", label: "Lifecycle Stage", type: "text" },
  { key: "client", label: "Client", type: "text" },
  { key: "claims", label: "Claims", type: "num" },
  { key: "lines", label: "Lines", type: "num" },
  { key: "unassigned", label: "Unassigned", type: "num" },
  { key: "oldestAging", label: "Oldest Aging", type: "num" },
  { key: "openAr", label: "Open AR", type: "money" }
];

// Openness prefers the explicit work queue, but falls back to statusCategory when the queue is
// absent. That fallback is not decoration: queueReason reaches the browser through the dataset
// mapper, and when that mapper dropped it this view reported ZERO open lines against ~18.6k real
// ones — a silently empty report. statusCategory travels the same path and carries the same
// open/closed meaning, so if either survives the report still tells the truth.
function reviewQueueIsOpen(claim) {
  const reason = String(claim.queueReason || "").trim().toLowerCase();
  if (reason) return !REVIEW_QUEUE_CLOSED_REASONS.includes(reason);
  const category = String(claim.statusCategory || "").trim().toLowerCase();
  if (!category) return false;
  return !REVIEW_QUEUE_CLOSED_STATUS_CATEGORIES.includes(category);
}

// The Review Queue group key (queue x stage x client) in ONE place. The row builder below and
// the drill-down predicate must derive it identically, or the claims the drill shows are not the
// claims that composed the number the biller clicked. Two transcriptions of the same three
// expressions is exactly the drift this avoids.
function reviewQueueGroupKey(claim) {
  return {
    queue: String(claim.queueReason || claim.statusCategory || "—").trim() || "—",
    stage: String(claim.lifecycleStage || "—").trim() || "—",
    client: String(claim.client || "—").trim() || "—"
  };
}

// Predicate for drillIntoReportRow: the claims behind one Review Queue row. It closes over the
// three key STRINGS and never over a resolved claim list - the drill re-renders from live state,
// so a captured list would silently drift from the report it claims to explain. The services
// check is not belt-and-braces: a claim with no service lines emits no line row and so composed
// no group, and without it the drill could show more claims than the row's own Claims figure.
function reviewQueueDrillPredicate(queue, stage, client) {
  return (claim) => {
    if (!reviewQueueIsOpen(claim)) return false;
    if (!(Array.isArray(claim.services) ? claim.services.length : 0)) return false;
    const key = reviewQueueGroupKey(claim);
    return key.queue === queue && key.stage === stage && key.client === client;
  };
}

function reviewQueueLineRows(claims) {
  const rows = [];
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    (Array.isArray(claim.services) ? claim.services : []).forEach((service) => {
      if (!reviewQueueIsOpen(claim)) return;
      const key = reviewQueueGroupKey(claim);
      rows.push({
        // The work QUEUE is claim-level (queueReason/statusCategory live on the claim) while AR is
        // line-level. Carry BOTH grains: counting only lines said "25,836 open" on live data when
        // the number of things a biller actually works was 4,671 claims.
        claimId: String(claim.claimId || "").trim(),
        queue: key.queue,
        statusCategory: String(claim.statusCategory || "").trim(),
        stage: key.stage,
        client: key.client,
        // Aging is the line's own bill-date aging where present, falling back to claim aging days --
        // the same fallback chain the AR views use, so the two agree on how old a line is.
        agingDays: Number(firstPresent(claim.billDateAging, claim.agingDays, 0)) || 0,
        openAr: moneyNumber(service.finalInsuranceBalance),
        unassigned: String(claim.assignedTo || "").trim() ? 0 : 1
      });
    });
  });
  return rows;
}

function reviewQueueBuildRows(lineRows) {
  const groups = new Map();
  (Array.isArray(lineRows) ? lineRows : []).forEach((row) => {
    const key = [row.queue, row.stage, row.client].join("\u001f");
    if (!groups.has(key)) {
      groups.set(key, { queue: row.queue, statusCategory: row.statusCategory, stage: row.stage, client: row.client, _claims: new Set(), claims: 0, lines: 0, unassigned: 0, oldestAging: 0, openAr: 0 });
    }
    const g = groups.get(key);
    if (row.claimId) g._claims.add(row.claimId);
    g.lines += 1;
    g.unassigned += row.unassigned;
    g.oldestAging = Math.max(g.oldestAging, row.agingDays);
    g.openAr += row.openAr;
  });
  const allClaims = new Set();
  const rows = Array.from(groups.values()).map((g) => {
    g._claims.forEach((id) => allClaims.add(id));
    g.claims = g._claims.size;
    return g;
  });
  const totals = rows.reduce((t, g) => {
    t.lines += g.lines;
    t.unassigned += g.unassigned;
    t.openAr += g.openAr;
    t.oldestAging = Math.max(t.oldestAging, g.oldestAging);
    t.byQueue[g.queue] = (t.byQueue[g.queue] || 0) + g.lines;
    return t;
  }, { lines: 0, unassigned: 0, openAr: 0, oldestAging: 0, byQueue: {} });
  // Distinct across the WHOLE report, never a sum of per-group counts: a claim whose lines sit in
  // different stages appears in more than one group, and summing would count it twice.
  totals.claims = allClaims.size;
  return { rows: reviewQueueSortRows(rows), totals };
}

function reviewQueueSortRows(rows) {
  const col = REVIEW_QUEUE_DETAIL_COLUMNS.find((candidate) => candidate.key === reportReviewQueueSortState.sortKey)
    || REVIEW_QUEUE_DETAIL_COLUMNS[6];
  const dir = reportReviewQueueSortState.sortDir === "asc" ? 1 : -1;
  return rows.slice().sort((a, b) => {
    if (col.type === "text") return dir * String(a[col.key] || "").localeCompare(String(b[col.key] || ""));
    return dir * (Number(a[col.key] || 0) - Number(b[col.key] || 0)) || String(a.client || "").localeCompare(String(b.client || ""));
  });
}

function renderReviewQueueReport(model, scannedClaims) {
  const totals = model.totals;
  const head = REVIEW_QUEUE_DETAIL_COLUMNS.map((col) => {
    const active = reportReviewQueueSortState.sortKey === col.key;
    const arrow = active ? (reportReviewQueueSortState.sortDir === "asc" ? " ▲" : " ▼") : "";
    const cls = `${col.type === "text" ? "" : "num"} ar-sortable${active ? " is-sorted" : ""}`.trim();
    return `<th class="${cls}" data-review-queue-sort="${col.key}" role="button" tabindex="0" aria-sort="${active ? (arrow.indexOf("▲") >= 0 ? "ascending" : "descending") : "none"}" aria-label="Sort by ${escapeHtml(col.label)}" title="Sort by ${escapeHtml(col.label)}">${escapeHtml(col.label)}<span aria-hidden="true">${arrow}</span></th>`;
  }).join("");
  const body = model.rows.length ? model.rows.map((row) => `
    <tr>
      ${REVIEW_QUEUE_DETAIL_COLUMNS.map((col) => {
        const cell = col.type === "money" ? money(row[col.key])
          : col.key === "oldestAging" ? `${Number(row[col.key] || 0).toLocaleString()}d`
          : col.type === "num" ? Number(row[col.key] || 0).toLocaleString()
          : escapeHtml(String(row[col.key] || "—"));
        // The Queue cell is the drill trigger, carrying the three values this row was grouped
        // by so a click re-filters the same snapshot down to the claims that composed it.
        // Three separate attributes, not the grouper's \u001f-joined key: a control character
        // inside an HTML attribute is parser roulette.
        const attrs = col.key === "queue"
          ? ` class="ar-drillable" data-rq-drill-queue="${escapeHtml(row.queue)}" data-rq-drill-stage="${escapeHtml(row.stage)}" data-rq-drill-client="${escapeHtml(row.client)}" role="button" tabindex="0"`
          : ` class="${col.type === "text" ? "" : "num"}"`;
        return `<td${attrs} data-label="${escapeHtml(col.label)}">${cell}</td>`;
      }).join("")}
    </tr>`).join("") : `<tr><td colspan="${REVIEW_QUEUE_DETAIL_COLUMNS.length}">No open work in the active filter — every line is closed.</td></tr>`;
  const queueMix = Object.entries(totals.byQueue).sort((a, b) => b[1] - a[1])
    .map(([name, count]) => `${escapeHtml(name)} ${Number(count).toLocaleString()}`).join(" · ");
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Review Queue — Open Work</h3>
        <p>Open service lines by workflow queue, stage, and client. <strong>Closed lines are excluded.</strong> Scanned ${Number(scannedClaims || 0).toLocaleString()} claim ${Number(scannedClaims) === 1 ? "line" : "lines"}. ${queueMix ? escapeHtml("Queues: ") + queueMix + "." : ""}</p>
      </div>
      ${reportExportActionsHtml(`
        <button type="button" class="ghost-button" data-review-queue-export>Detail → CSV</button>
        <button type="button" class="primary-button" data-ar-print>Print / PDF</button>`)}
    </div>
    <div class="ar-kpi-grid">
      ${arKpiCard("Open Claims", Number(totals.claims || 0).toLocaleString(), totals.lines ? `${Number(totals.lines || 0).toLocaleString()} service lines · oldest ${Number(totals.oldestAging || 0).toLocaleString()} days` : "Nothing open in this filter", "primary")}
      ${arKpiCard("Open AR In Queue", money(totals.openAr), "Insurance balance still owed on open lines")}
      ${arKpiCard("Unassigned", Number(totals.unassigned || 0).toLocaleString(), totals.lines ? `${Math.round((totals.unassigned / totals.lines) * 100)}% of open lines have no owner` : "No open lines")}
    </div>
  </section>
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Queue By Stage And Client</h3>
        <p>CT's payment-attribution review queue (unmatched remits, disbursement variance) needs payment-grain data the current source does not carry yet; it is a separate report when that lands.</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </section>`;
}

function bindReviewQueueControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-review-queue-sort]").forEach((th) => {
    const apply = () => {
      const key = th.getAttribute("data-review-queue-sort");
      if (reportReviewQueueSortState.sortKey === key) {
        reportReviewQueueSortState.sortDir = reportReviewQueueSortState.sortDir === "asc" ? "desc" : "asc";
      } else {
        reportReviewQueueSortState.sortKey = key;
        const col = REVIEW_QUEUE_DETAIL_COLUMNS.find((candidate) => candidate.key === key);
        reportReviewQueueSortState.sortDir = col && col.type === "text" ? "asc" : "desc";
      }
      renderReports();
    };
    th.addEventListener("click", apply);
    th.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); apply(); } });
  });
  // Report -> group -> claim -> service line, the CT-TRACKER path, over the same snapshot the
  // aggregate above was computed from. bindReportRowDrilldown() is called centrally by
  // renderReports() once the drill is active, so nothing extra is bound here.
  host.querySelectorAll("[data-rq-drill-queue]").forEach((el) => {
    const open = () => {
      const queue = el.getAttribute("data-rq-drill-queue") || "";
      const stage = el.getAttribute("data-rq-drill-stage") || "";
      const client = el.getAttribute("data-rq-drill-client") || "";
      // Stage is NULL on every live row, so the drill title narrated "Stage: --" — an em-dash
      // reads as a value the biller filtered on rather than as an absence. Dropped from the
      // LABEL only: reviewQueueGroupKey() emits that literal "--", so the predicate below must
      // keep matching on it or the drill returns nothing.
      const dims = [["Queue", queue], ["Stage", stage], ["Client", client]]
        .filter(([, value]) => value && value !== "—")
        .map(([name, value]) => `${name}: ${value}`);
      const label = dims.join(" · ");
      // The row column the biller clicked is Open AR (line-level insurance balance). The drill
      // footer reports "AR (Exp−Paid)" and "Balance", both claim-level — three money concepts,
      // two of them named AR. On live data a $4,459 row opens onto "AR $1,029", and the honest
      // reading of that is that the drill lost 77% of the money. So restate the clicked figure,
      // named and grained, beside the claim-level ones. Recomputed from the live snapshot the
      // same way the CSV export below does, because the drill itself re-derives from live state.
      const clicked = reviewQueueBuildRows(reviewQueueLineRows(getReportClaims(reportSourceClaims()))).rows
        .find((row) => row.queue === queue && row.stage === stage && row.client === client);
      if (clicked) dims.push(`Row Open AR ${money(clicked.openAr)} — line-level`);
      drillIntoReportRow({
        view: reportArState.view,
        label,
        dims,
        predicate: reviewQueueDrillPredicate(queue, stage, client)
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
  const exportBtn = host.querySelector("[data-review-queue-export]");
  if (exportBtn) exportBtn.addEventListener("click", () => {
    const model = reviewQueueBuildRows(reviewQueueLineRows(getReportClaims(reportSourceClaims())));
    const header = REVIEW_QUEUE_DETAIL_COLUMNS.map((col) => col.label);
    const lines = model.rows.map((row) => REVIEW_QUEUE_DETAIL_COLUMNS.map((col) => col.type === "money" ? Number(row[col.key]).toFixed(2) : row[col.key]));
    arDownloadFile("review-queue-open-work.csv", arToCsv([header, ...lines]), "text/csv;charset=utf-8;");
  });
  const printBtn = host.querySelector("[data-ar-print]");
  if (printBtn) printBtn.addEventListener("click", () => window.print());
}

function renderCollectionsReport(model) {
  if (model.status === "pending") {
    return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head"><div><h3>Collections — Money In</h3>
    <p>Loading collections...</p></div></div>
  </section>`;
  }
  if (model.status === "error") {
    return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head"><div><h3>Collections — Money In</h3>
    <p>Collections report unavailable: ${escapeHtml(model.error || "unknown error")}</p></div></div>
  </section>`;
  }
  if (model.status === "unavailable") {
    return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head"><div><h3>Collections — Money In</h3>
    <p>Collections needs live payment data — sample data has no payment dates.</p></div></div>
  </section>`;
  }
  const view = collectionsSlicedModel(model);
  return `
    ${renderCollectionsKpiHeader(view)}
    ${renderCollectionsWorkbench(model, view)}
    ${view.presentation === "charts"
      ? `${renderCollectionsTrend(view.totals, view.truncated, view)}${renderCollectionsPayerChart(view.rows)}`
      : renderCollectionsExcelSheet(view)}
    ${renderCollectionsDetail(view.rows, view.truncated, view.truncatedBeforeMonth, view)}
  `;
}

function renderCollectionsKpiHeader(model) {
  const totals = model.totals;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Collections — Money In</h3>
        <p>${escapeHtml(model.caption)}</p>
      </div>
      ${reportExportActionsHtml(`
        <button type="button" class="ghost-button" data-collections-export>Detail → CSV</button>
        <button type="button" class="primary-button" data-ar-print>Print / PDF</button>`)}
    </div>
    <div class="ar-kpi-grid">
      ${arKpiCard("Total Collected", model.truncated ? `≥ ${money(totals.collected)}` : money(totals.collected), `${Number(totals.payments || 0).toLocaleString()}${model.truncated ? "+" : ""} ${Number(totals.payments || 0) === 1 ? "payment" : "payments"}${model.truncated ? " · row cap reached" : ""}`, "primary")}
      ${/* NO "≥" HERE, AND THAT IS THE FIX, NOT AN OVERSIGHT. The server now caps
           with ORDER BY payment_month DESC, so when the cap binds it is the OLDEST
           history that is cut and the newest month comes back WHOLE. Prefixing an
           exact figure with "at least" is its own small lie — it invites the reader
           to treat a complete number as a floor and go looking for the rest.
           Total Collected and Top Payer keep their "≥": those DO undercount,
           because the months that were dropped had money in them. */""}
      ${arKpiCard("Latest Month", money(totals.latestMonthCollected), totals.latestMonth ? `Collected in ${collectionsMonthLabel(totals.latestMonth)}` : "No payments in range")}
      ${arKpiCard("Top Payer", totals.topPayer || "—", totals.topPayer ? `${model.truncated ? `≥ ${money(totals.topPayerCollected)}` : money(totals.topPayerCollected)} collected` : "No payments in range")}
    </div>
  </section>`;
}

function collectionsMonthLabel(month) {
  const label = monthLabelFromYearMonth(month);
  return label ? `${label} ${String(month).slice(0, 4)}` : String(month || "—");
}

// Gap months must read $0, not vanish: zero-fill every YYYY-MM between the
// first and last payment month so the trend shows the full timeline.
function collectionsZeroFillMonths(months) {
  const rows = Array.isArray(months) ? months : [];
  const byMonth = new Map(rows.map((row) => [String(row.month || ""), moneyNumber(row.collected)]));
  const keys = Array.from(byMonth.keys()).sort();
  if (keys.length < 2 || !keys.every((key) => /^\d{4}-\d{2}$/.test(key))) return rows;
  const filled = [];
  let year = Number(keys[0].slice(0, 4));
  let month = Number(keys[0].slice(5, 7));
  const last = keys[keys.length - 1];
  let key = keys[0];
  while (key <= last) {
    filled.push({ month: key, collected: byMonth.get(key) || 0 });
    month += 1;
    if (month > 12) { month = 1; year += 1; }
    key = `${year}-${String(month).padStart(2, "0")}`;
  }
  return filled;
}

function renderCollectionsTrend(totals, truncated, view = {}) {
  const months = Array.isArray(totals.months) ? totals.months : [];
  const body = months.length ? [...months].sort((a, b) => String(b.month).localeCompare(String(a.month))).map((row) => {
    const value = moneyNumber(row.collected);
    return `
    <tr>
      <th scope="row">${escapeHtml(collectionsMonthLabel(row.month))}</th>
      <td class="num">${money(value)}</td>
    </tr>`;
  }).join("") : `<tr><td colspan="2">${escapeHtml(collectionsEmptyFilterNote(view))}</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Collections by Month</h3>
      </div>
    </div>
    ${(() => {
      const _chr = [...months].sort((a, b) => String(a.month).localeCompare(String(b.month)));
      const _vals = _chr.map((row) => moneyNumber(row.collected));
      if (!_vals.some((v) => v > 0)) return "";
      return `<div class="chart-canvas">${renderChartCanvas("bar", _chr.map((row) => collectionsMonthLabel(row.month)), [{ label: "Collected", valueType: "money", colorIndex: 1, values: _vals }])}</div>`;
    })()}
    <div class="table-wrap collections-month-wrap">
      <table class="client-table ar-matrix-table">
        <thead><tr><th scope="col">Payment Month</th><th class="num">Collected</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><th scope="row">Total</th><td class="num"><strong>${truncated ? `≥ ${money(totals.collected)}` : money(totals.collected)}</strong></td></tr></tfoot>
      </table>
    </div>
  </section>`;
}

function renderCollectionsDetail(rows, truncated, truncatedBeforeMonth = "", view = {}) {
  const rowLabel = `${rows.length.toLocaleString()} ${rows.length === 1 ? "row" : "rows"}`;
  // "First N rows" described an ascending cap. The cap is descending now, so what
  // survives is the most RECENT N and what is missing is history — name the month
  // the record starts at rather than leaving the reader to guess which end is short.
  const truncationNote = !truncated ? ""
    : truncatedBeforeMonth
      ? ` — row cap reached; history before ${collectionsMonthLabel(truncatedBeforeMonth)} is not shown. Narrow the filters or date range for the full set`
      : " — row cap reached; the oldest history is not shown. Narrow the filters or date range for the full set";
  const head = COLLECTIONS_DETAIL_COLUMNS.map((col) => {
    const active = reportCollectionsSortState.sortKey === col.key;
    const arrow = active ? (reportCollectionsSortState.sortDir === "asc" ? " ▲" : " ▼") : "";
    const cls = `${col.type === "text" ? "" : "num"} ar-sortable${active ? " is-sorted" : ""}`.trim();
    return `<th class="${cls}" data-collections-sort="${col.key}" role="button" tabindex="0" aria-sort="${active ? (arrow.indexOf("▲") >= 0 ? "ascending" : "descending") : "none"}" aria-label="Sort by ${escapeHtml(col.label)}" title="Sort by ${escapeHtml(col.label)}">${escapeHtml(col.label)}<span aria-hidden="true">${arrow}</span></th>`;
  }).join("");
  const body = rows.length ? rows.map((row) => `
    <tr>
      ${COLLECTIONS_DETAIL_COLUMNS.map((col) => {
        const value = row[col.key];
        const cell = col.type === "money" ? money(value) : col.type === "num" ? ((value == null || (typeof value === "number" && !Number.isFinite(value))) ? "—" : String(value)) : col.key === "month" ? escapeHtml(collectionsMonthLabel(value)) : escapeHtml(String(value || "—"));
        return `<td class="${col.type === "text" ? "" : "num"}" data-label="${escapeHtml(col.label)}">${cell}</td>`;
      }).join("")}
    </tr>`).join("") : `<tr><td colspan="${COLLECTIONS_DETAIL_COLUMNS.length}">${escapeHtml(collectionsEmptyFilterNote(view))}</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Collections Detail</h3>
        <p>${truncated ? `Most recent ${rowLabel}` : rowLabel} · grouped by month, payer, and client${truncationNote}.</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </section>`;
}

function bindCollectionsReportControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-collections-sort]").forEach((th) => {
    const apply = () => {
      const key = th.getAttribute("data-collections-sort");
      if (reportCollectionsSortState.sortKey === key) {
        reportCollectionsSortState.sortDir = reportCollectionsSortState.sortDir === "asc" ? "desc" : "asc";
      } else {
        reportCollectionsSortState.sortKey = key;
        const col = COLLECTIONS_DETAIL_COLUMNS.find((candidate) => candidate.key === key);
        reportCollectionsSortState.sortDir = col && col.type === "text" ? "asc" : "desc";
      }
      renderReports();
    };
    th.addEventListener("click", apply);
    th.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); apply(); }
    });
  });
  const exportBtn = host.querySelector("[data-collections-export]");
  if (exportBtn) exportBtn.addEventListener("click", collectionsExportCsv);
  const printBtn = host.querySelector("[data-ar-print]");
  if (printBtn) printBtn.addEventListener("click", () => window.print());
  host.querySelectorAll("[data-collections-presentation]").forEach((button) => {
    button.addEventListener("click", () => {
      const next = button.getAttribute("data-collections-presentation") === "charts" ? "charts" : "excel";
      if (reportCollectionsUiState.presentation === next) return;
      reportCollectionsUiState.presentation = next;
      renderReports();
    });
  });
  host.querySelectorAll("[data-collections-slicer]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.getAttribute("data-collections-slicer");
      const value = button.getAttribute("data-collections-slicer-value") || "";
      if (!key || !value) return;
      const current = Array.isArray(reportCollectionsUiState.slicers[key]) ? reportCollectionsUiState.slicers[key] : [];
      reportCollectionsUiState.slicers[key] = current.includes(value)
        ? current.filter((item) => item !== value)
        : current.concat(value);
      renderReports();
    });
  });
  host.querySelectorAll("[data-collections-slicer-clear]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.getAttribute("data-collections-slicer-clear");
      if (!key) return;
      reportCollectionsUiState.slicers[key] = [];
      renderReports();
    });
  });
}

function collectionsSlicedModel(model) {
  if (!model || model.status !== "ok") {
    return { ...(model || {}), presentation: reportCollectionsUiState.presentation || "excel" };
  }
  const rows = collectionsFilteredRows(model.rows || []);
  const slicersOn = Boolean((reportCollectionsUiState.slicers.payer || []).length || (reportCollectionsUiState.slicers.client || []).length);
  const totals = rows.length
    ? { ...model.totals, ...collectionsLocalTotals(rows) }
    : (slicersOn ? model.totals : collectionsLocalTotals(rows));
  const hiddenRows = !rows.length && slicersOn ? (Array.isArray(model.rows) ? model.rows.length : 0) : 0;
  const hiddenCollected = hiddenRows ? moneyNumber((model.totals || {}).collected) : 0;
  return {
    ...model,
    rows,
    totals,
    presentation: reportCollectionsUiState.presentation === "charts" ? "charts" : "excel",
    sourceRowCount: Array.isArray(model.rows) ? model.rows.length : 0,
    hiddenRows,
    hiddenCollected
  };
}

function collectionsEmptyFilterNote(view = {}) {
  const hiddenRows = Number(view.hiddenRows) || 0;
  const hiddenCollected = moneyNumber(view.hiddenCollected);
  if (hiddenRows && hiddenCollected > 0) {
    return `No payments in the active filter. Slicers are hiding ${money(hiddenCollected)} across ${typeof formatNounCount === "function" ? formatNounCount(hiddenRows, "row") : hiddenRows + " rows"} still on this book. This is not $0.`;
  }
  if (hiddenRows) {
    return `No payments in the active filter. Slicers are hiding ${typeof formatNounCount === "function" ? formatNounCount(hiddenRows, "row") : hiddenRows + " rows"} still on this book.`;
  }
  return "No payments in the active filter.";
}

function collectionsFilteredRows(rows = []) {
  const payers = new Set(Array.isArray(reportCollectionsUiState.slicers.payer) ? reportCollectionsUiState.slicers.payer : []);
  const clients = new Set(Array.isArray(reportCollectionsUiState.slicers.client) ? reportCollectionsUiState.slicers.client : []);
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (payers.size && !payers.has(row.payer)) return false;
    if (clients.size && !clients.has(row.client)) return false;
    return true;
  });
}

function collectionsSlicerOptions(rows, key) {
  const counts = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const value = String(row[key] || "").trim() || "(blank)";
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .slice(0, 24)
    .map(([value, count]) => ({ value, count }));
}

function renderCollectionsWorkbench(sourceModel, view) {
  const sourceRows = Array.isArray(sourceModel.rows) ? sourceModel.rows : [];
  const presentation = view.presentation || "excel";
  const payerOptions = collectionsSlicerOptions(sourceRows, "payer");
  const clientOptions = collectionsSlicerOptions(sourceRows, "client");
  const activePayers = new Set(reportCollectionsUiState.slicers.payer || []);
  const activeClients = new Set(reportCollectionsUiState.slicers.client || []);
  const slicerGroup = (key, legend, options, active) => `
    <section class="collections-slicer" aria-label="${escapeHtml(legend)}">
      <header class="collections-slicer-head">
        <strong>${escapeHtml(legend)}</strong>
        ${active.size ? `<button type="button" class="collections-slicer-clear" data-collections-slicer-clear="${escapeHtml(key)}">Clear</button>` : ""}
      </header>
      <div class="collections-slicer-values">
        ${options.map((option) => `
          <button type="button" class="collections-slicer-val${active.has(option.value) ? " is-active" : ""}" data-collections-slicer="${escapeHtml(key)}" data-collections-slicer-value="${escapeHtml(option.value)}">${escapeHtml(option.value)}</button>
        `).join("")}
      </div>
    </section>`;
  return `
  <section class="panel ar-report-panel collections-workbench reports-no-print">
    <div class="collections-workbench-bar">
      <div>
        <span class="collections-workbench-kicker">Collections by month</span>
        <p>Same money as the Excel tracker. Excel is the sheet. Charts is the Power BI view. Slicers show who is coming in.</p>
      </div>
      <div class="ar-dim-toggle" role="group" aria-label="Collections presentation">
        <button type="button" class="ar-dim-btn${presentation === "excel" ? " is-active" : ""}" data-collections-presentation="excel" aria-pressed="${presentation === "excel" ? "true" : "false"}">Excel</button>
        <button type="button" class="ar-dim-btn${presentation === "charts" ? " is-active" : ""}" data-collections-presentation="charts" aria-pressed="${presentation === "charts" ? "true" : "false"}">Charts</button>
      </div>
    </div>
    <div class="collections-slicer-rail">
      ${slicerGroup("payer", "Who is coming in", payerOptions, activePayers)}
      ${slicerGroup("client", "Client", clientOptions, activeClients)}
    </div>
  </section>`;
}

function renderCollectionsExcelSheet(view) {
  const rows = Array.isArray(view.rows) ? view.rows : [];
  const months = Array.from(new Set(rows.map((row) => String(row.month || "").trim()).filter(Boolean))).sort();
  const payerTotals = new Map();
  rows.forEach((row) => payerTotals.set(row.payer, moneyNumber((payerTotals.get(row.payer) || 0) + row.collected)));
  const rankedPayers = Array.from(payerTotals.entries()).sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]));
  const topPayers = rankedPayers.slice(0, 8).map(([payer]) => payer);
  const topSet = new Set(topPayers);
  const hasOther = rankedPayers.length > topPayers.length;
  const columns = topPayers.concat(hasOther ? ["Other"] : []);
  const byMonth = new Map();
  rows.forEach((row) => {
    const month = String(row.month || "");
    const bucket = byMonth.get(month) || { total: 0, payers: new Map() };
    const column = topSet.has(row.payer) ? row.payer : "Other";
    bucket.total += moneyNumber(row.collected);
    bucket.payers.set(column, moneyNumber((bucket.payers.get(column) || 0) + row.collected));
    byMonth.set(month, bucket);
  });
  const yearGroups = new Map();
  months.forEach((month) => {
    const year = month.slice(0, 4) || "";
    if (!yearGroups.has(year)) yearGroups.set(year, []);
    yearGroups.get(year).push(month);
  });
  const cell = (amount) => `<td class="client-pivot-cell">${money(amount)}</td>`;
  const body = months.length ? Array.from(yearGroups.entries()).map(([year, yearMonths]) => {
    const yearTotal = yearMonths.reduce((sum, month) => sum + moneyNumber((byMonth.get(month) || {}).total), 0);
    const yearPayers = new Map();
    yearMonths.forEach((month) => {
      const bucket = byMonth.get(month) || { payers: new Map() };
      columns.forEach((column) => yearPayers.set(column, moneyNumber((yearPayers.get(column) || 0) + moneyNumber(bucket.payers.get(column)))));
    });
    return `
      <tr class="client-pivot-row client-pivot-row--year">
        <td class="client-pivot-row-label client-pivot-row-year">${escapeHtml(year)}</td>
        ${columns.map((column) => cell(yearPayers.get(column) || 0)).join("")}
        ${cell(yearTotal)}
      </tr>
      ${yearMonths.map((month) => {
        const bucket = byMonth.get(month) || { total: 0, payers: new Map() };
        return `
      <tr class="client-pivot-row">
        <td class="client-pivot-row-label">${escapeHtml(collectionsMonthLabel(month))}</td>
        ${columns.map((column) => cell(bucket.payers.get(column) || 0)).join("")}
        ${cell(bucket.total)}
      </tr>`;
      }).join("")}`;
  }).join("") : `<tr><td colspan="${columns.length + 2}">${escapeHtml(collectionsEmptyFilterNote(view))}</td></tr>`;
  const grand = columns.reduce((sum, column) => {
    sum[column] = moneyNumber(payerTotals.get(column) || (column === "Other"
      ? rankedPayers.slice(8).reduce((acc, [, amount]) => acc + amount, 0)
      : 0));
    return sum;
  }, {});
  if (hasOther) grand.Other = rankedPayers.slice(8).reduce((acc, [, amount]) => acc + amount, 0);
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Collections by Month</h3>
        <p>Excel sheet · payment month × who paid. Service-month completeness is Monthly Summary.</p>
      </div>
    </div>
    <div class="table-wrap collections-month-wrap collections-excel-wrap">
      <table class="client-table client-pivot-table ar-matrix-table">
        <thead>
          <tr>
            <th class="client-pivot-th-row" scope="col">Row Labels</th>
            ${columns.map((column) => `<th class="client-pivot-th-col" scope="col">${escapeHtml(column)}</th>`).join("")}
            <th class="client-pivot-th-col" scope="col">Total</th>
          </tr>
        </thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr class="client-pivot-row client-pivot-row--grand">
            <th class="client-pivot-row-label" scope="row">Grand Total</th>
            ${columns.map((column) => `<td class="client-pivot-cell"><strong>${money(grand[column] || 0)}</strong></td>`).join("")}
            <td class="client-pivot-cell"><strong>${money(view.totals.collected)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>`;
}

function renderCollectionsPayerChart(rows = []) {
  const byPayer = new Map();
  rows.forEach((row) => byPayer.set(row.payer, moneyNumber((byPayer.get(row.payer) || 0) + row.collected)));
  const ranked = Array.from(byPayer.entries()).sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0])).slice(0, 12);
  if (!ranked.some(([, amount]) => amount > 0)) return "";
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head"><div><h3>Who is coming in</h3></div></div>
    <div class="chart-canvas">${renderChartCanvas("bar", ranked.map(([payer]) => payer), [{ label: "Collected", valueType: "money", colorIndex: 1, values: ranked.map(([, amount]) => amount) }])}</div>
  </section>`;
}

function collectionsExportCsv() {
  const model = collectionsSlicedModel(collectionsReportModel());
  if (model.status !== "ok") {
    showToast("Collections data is not ready to export yet.");
    return;
  }
  const rows = Array.isArray(model.rows) ? model.rows : [];
  const header = COLLECTIONS_DETAIL_COLUMNS.map((col) => col.label);
  const lines = rows.map((row) => COLLECTIONS_DETAIL_COLUMNS.map((col) => col.type === "money" ? Number(row[col.key]).toFixed(2) : row[col.key]));
  const filename = model.truncated ? "collections-detail-by-month-payer-client-partial.csv" : "collections-detail-by-month-payer-client.csv";
  arDownloadFile(filename, arToCsv([header, ...lines]), "text/csv;charset=utf-8;");
  if (model.truncated) showToast(`Exported first ${rows.length.toLocaleString()} ${rows.length === 1 ? "row" : "rows"} — row cap reached.`);
}

// ===========================================================================
// REPORTS — Expected vs Paid. One question: are we getting paid what we
// should? Pure client-side math from claim financials, exactly like AR
// Aging: expected reimbursement vs insurance payments per claim, grouped by
// payer/client/provider. Insurance side only — patient balances are out of
// scope by design.
// ===========================================================================
const EVP_DETAIL_COLUMNS = [
  { key: "claimId", label: "Claim", type: "text" },
  { key: "payer", label: "Payer", type: "text" },
  { key: "tenant", label: "Client", type: "text" },
  { key: "provider", label: "Provider", type: "text" },
  // Bill Date dropped 2026-08-10 — see AR_DETAIL_COLUMNS. Same dead source
  // (original_bill_date is NULL in warehouse mode), same "--" on every row.
  { key: "expected", label: "Expected", type: "money" },
  { key: "paid", label: "Paid", type: "money" },
  { key: "outstanding", label: "Outstanding", type: "money" },
  { key: "pctCollected", label: "% Collected", type: "percent" }
];

function evpClaimRow(claim) {
  const fin = claimFinancialView(claim);
  const expected = moneyNumber(fin.expectedReimbursement);
  const paid = moneyNumber(fin.totalInsurancePaid);
  return {
    claimId: claim.claimId || "",
    tenant: claim.client || "—",
    payer: claim.primaryPayer || claim.secondaryPayer || "—",
    provider: claim.renderingProvider || "—",
    expected,
    paid,
    outstanding: moneyNumber(fin.expectedOutstanding),
    // Carried so every rollup below can report a KNOWN basis.
    //
    // AN UNRATED CLAIM DOES NOT CONTRIBUTE ZERO. That was the assumption in the
    // first cut of this change and it is FALSE on the live mirror, which ships
    // primary_rate NULL but total_expected = COALESCE(SrvExpectedPriPmt,
    // SrvAllowedAmt, SrvCharges, 0) — a positive, CHARGE-DERIVED number. So an
    // unrated line is flagged unknown AND carries real dollars, and a total that
    // swept it in while the caption said "excluded" was a false provenance claim
    // attached to a money figure: the exact sin this change exists to prevent.
    // Hence the parallel *KnownBasis accumulators below — the figure a tile
    // shows is the one its caption describes.
    expectedKnown: reportExpectedKnown(fin),
    rateSource: evpClaimRateSource(claim, reportExpectedKnown(fin)),
    oonCutTrueUp: evpClaimCutTrueUp(claim),
    pctCollected: expected > 0 ? (paid / expected) * 100 : null
  };
}

// EXPECTED BY RATE SOURCE (browser lane). Mirrors backend/reportT1aSql.js
// RATE_SOURCE_SQL per line; this lane is claim-grain, so a claim whose lines
// were priced differently reads "Mixed rate sources".
const EVP_RATE_SOURCE_LABELS = Object.freeze(["Learned OON rate", "Fee schedule (OON rate learned, not applied)", "Fee schedule (OON rate learning)", "Fee schedule", "No rate on file"]);
function evpServiceRateSource(service = {}, claimKnown = true) {
  const rateState = String(firstPresent(service.primaryRateState, service.primary_rate_state) ?? "").trim().toLowerCase();
  const oonState = String(firstPresent(service.oonRateState, service.oon_rate_state) ?? "").trim().toLowerCase();
  if (rateState === "learned_oon") return EVP_RATE_SOURCE_LABELS[0];
  if (!claimKnown || ["missing", "ambiguous", "unreadable"].includes(rateState)) return EVP_RATE_SOURCE_LABELS[4];
  if (oonState === "learned" || oonState === "rate-cut") return EVP_RATE_SOURCE_LABELS[1];
  if (oonState === "learning") return EVP_RATE_SOURCE_LABELS[2];
  return EVP_RATE_SOURCE_LABELS[3];
}
// Same rule as reportT1aSql OON_CUT_TRUE_UP_ROW_SQL: an open line in a cut period,
// units x cut rate - the Primary Expected it is booked at now.
function evpServiceCutTrueUp(service = {}) {
  const pct = Number(firstPresent(service.oonRateChangePct, service.oon_rate_change_pct));
  const rate = Number(firstPresent(service.oonLearnedRate, service.oon_learned_rate));
  const paid = moneyNumber(firstPresent(service.finalInsurancePaid, service.paid, 0));
  const owed = moneyNumber(firstPresent(service.finalInsuranceBalance, service.owed, 0));
  if (!(pct < 0 && pct > -1 && rate > 0 && paid <= 0 && owed > 0)) return null;
  if (/write.?off/i.test(String(service.action || ""))) return null;
  const booked = moneyNumber(firstPresent(service.primaryExpectedTotal, service.primary_expected_total, service.primaryExpected, 0));
  return Math.round((moneyNumber(service.units) * rate - booked) * 100) / 100;
}
function evpClaimRateSource(claim = {}, claimKnown = true) {
  const services = Array.isArray(claim.services) && claim.services.length ? claim.services : [{}];
  const sources = new Set(services.map((service) => evpServiceRateSource(service, claimKnown)));
  return sources.size === 1 ? [...sources][0] : "Mixed rate sources";
}
function evpClaimCutTrueUp(claim = {}) {
  return (Array.isArray(claim.services) ? claim.services : []).reduce((sum, service) => {
    const amount = evpServiceCutTrueUp(service);
    return amount == null ? sum : { amount: sum.amount + amount, lines: sum.lines + 1 };
  }, { amount: 0, lines: 0 });
}
function evpTrueUpNoteHtml(amount, lines) {
  const body = lines > 0
    ? `<strong>Rate-cut true-up on open OON lines: ${escapeHtml(reportCurrencyFormatter.format(amount))}</strong> across ${formatNounCount(lines, "unpaid line")} dated on or after a confirmed rate cut: the change to Expected from re-pricing them at the cut rate. It reads $0 once learned pricing is on, because those lines are already priced that way.`
    : "<strong>Rate-cut true-up: none.</strong> No unpaid out-of-network line is dated after a confirmed rate cut in this selection.";
  return `<p class="evp-true-up" role="status">${body}</p>`;
}
function evpDimensionToggleHtml(dimension) {
  const dimensions = [["payer", "Payer"], ["client", "Client"], ["provider", "Provider"], ["rateSource", "Rate source"]];
  return `<div class="ar-dim-toggle reports-no-print" role="group" aria-label="Group Expected vs Paid matrix by">
        ${dimensions.map(([key, label]) => `<button type="button" class="ar-dim-btn${dimension === key ? " is-active" : ""}" data-evp-dim="${key}">${label}</button>`).join("")}
      </div>`;
}
function evpPreparedDimensionHtml(model = {}, groups = []) {
  const dimension = preparedSnapshotDimension("expectedVsPaid");
  const trueUp = dimension === "rateSource"
    ? (Array.isArray(groups) ? groups : []).reduce((sum, group) => ({ amount: sum.amount + (Number(group.oonCutTrueUp) || 0), lines: sum.lines + (Number(group.oonCutTrueUpLines) || 0) }), { amount: 0, lines: 0 })
    : null;
  return `<div class="evp-prepared-dims">${evpDimensionToggleHtml(dimension)}${trueUp ? evpTrueUpNoteHtml(Math.round(trueUp.amount * 100) / 100, trueUp.lines) : ""}</div>`;
}

function evpReportTotals(rows) {
  return rows.reduce((t, r) => {
    t.claims += 1;
    t.expected += r.expected;
    t.paid += r.paid;
    t.outstanding += r.outstanding;
    if (r.expectedKnown) {
      t.expectedKnownBasis += r.expected;
      t.paidOnKnown += r.paid;
      t.outstandingKnownBasis += r.outstanding;
    } else {
      t.unratedClaims += 1;
    }
    return t;
  }, { claims: 0, expected: 0, paid: 0, outstanding: 0, unratedClaims: 0,
       expectedKnownBasis: 0, paidOnKnown: 0, outstandingKnownBasis: 0 });
}

function evpBuildMatrix(rows, dimension) {
  const groups = new Map();
  rows.forEach((row) => {
    const key = arDimensionKey(row, dimension);
    if (!groups.has(key)) groups.set(key, { key, claims: 0, expected: 0, paid: 0, outstanding: 0, unrated: 0, oonCutTrueUp: 0, oonCutTrueUpLines: 0 });
    const group = groups.get(key);
    group.claims += 1;
    group.oonCutTrueUp += Number(row.oonCutTrueUp?.amount) || 0;
    group.oonCutTrueUpLines += Number(row.oonCutTrueUp?.lines) || 0;
    group.expected += row.expected;
    group.paid += row.paid;
    group.outstanding += row.outstanding;
    // The matrix stays on the ALL-CLAIMS basis (it is a per-claim table), but it
    // has to know how many of those claims are unpriced or its Total row cannot
    // say which basis it is on — and the KPI header above it is now the priced
    // basis. Without this the label helper reads Number(undefined) || 0 and
    // silently degrades to a bare "Total".
    if (!row.expectedKnown) group.unrated += 1;
  });
  const list = Array.from(groups.values()).sort((a, b) => b.outstanding - a.outstanding);
  const totals = list.reduce((t, g) => {
    t.claims += g.claims;
    t.expected += g.expected;
    t.paid += g.paid;
    t.outstanding += g.outstanding;
    t.unratedClaims += g.unrated;
    t.oonCutTrueUp += g.oonCutTrueUp;
    t.oonCutTrueUpLines += g.oonCutTrueUpLines;
    return t;
  }, { claims: 0, expected: 0, paid: 0, outstanding: 0, unratedClaims: 0, oonCutTrueUp: 0, oonCutTrueUpLines: 0 });
  return { list, totals };
}

function evpSortRows(rows, key, dir) {
  const col = EVP_DETAIL_COLUMNS.find((c) => c.key === key) || EVP_DETAIL_COLUMNS.find((c) => c.key === "outstanding");
  const mult = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (col.type === "text") return String(a[col.key]).localeCompare(String(b[col.key])) * mult;
    return ((Number(a[col.key]) || 0) - (Number(b[col.key]) || 0)) * mult;
  });
}

// EVP shows "--" (not "N/A") when there is no expected value to divide by --
// the matrix row sub-label explains why instead of shouting an error code.

// EVP'S TILES CARRY THE SAME PARTIAL CAVEAT AR AGING'S DO, AND ITS % COLLECTED
// NEEDS IT MOST. Both tiles are summed client-side over reportDatasetState's
// rows, which the server caps at 60k owed-first -- so the cap drops PAID/CLOSED
// history and keeps every open balance, and "% Collected" therefore reads HIGH
// rather than merely small (its denominator loses paid history faster than its
// numerator keeps it). This screen shipped with no caveat at all while AR Aging
// beside it marked every affected figure. reportPartialDetail is a no-op on a
// complete pull, so nothing here changes when the population is whole.
//
// NO reportOpenArComplete() EXEMPTION HERE. That exemption exists because the
// owed-first cut leaves AR Aging's Outstanding Insurance AR intact. EVP's
// "Expected Outstanding" is expected MINUS paid on the known basis, and expected
// is cap-damaged, so the same exemption would be a lie in the other direction.
function evpPercent(num, den) {
  return den > 0 ? `${Math.round((num / den) * 100)}%` : "—";
}

function renderEvpKpiHeader(totals, claimCount) {
  const rate = evpPercent(totals.paidOnKnown, totals.expectedKnownBasis);
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Expected vs Paid</h3>
        <p>${formatNounCount(claimCount, "claim")} in the active filter · service-date basis · insurance payments only.</p>
      </div>
      ${reportExportActionsHtml(`
        <button type="button" class="ghost-button" data-evp-export="matrix">Matrix → CSV</button>
        <button type="button" class="ghost-button" data-evp-export="detail">Detail → CSV</button>
        <button type="button" class="primary-button" data-ar-print>Print / PDF</button>`)}
    </div>
    <div class="ar-kpi-grid">
      ${reportExpectedKpiCard("% Collected", rate, reportPartialDetail(`${money(totals.paidOnKnown)} paid of ${money(totals.expectedKnownBasis)} expected`), "primary", totals,
        `${money(totals.paid)} collected · awaiting expected values`)}
      ${reportExpectedKpiCard("Expected Outstanding", totals.outstandingKnownBasis, reportPartialDetail("Still owed by payers"), "accent", totals,
        "Awaiting expected values — not $0")}
    </div>
    ${reportPartialTotalsNoticeHtml()}
    ${reportExpectedNoticeHtml(totals)}
  </section>`;
}

function renderEvpMatrix(matrix, dimension, emptyNote = "No claims in the active filter.") {
  const body = matrix.list.length ? matrix.list.map((group) => {
    // Inverted heat: the worse the collection rate, the stronger the tint,
    // so the payers leaking the most money pop without reading every number.
    const rate = group.expected > 0 ? group.paid / group.expected : null;
    const heat = rate == null || rate >= 1 ? 0 : Math.max(0.06, Math.min(1, 1 - rate));
    return `
    <tr>
      <th scope="row" class="ar-drillable" data-evp-drill-group="${escapeHtml(group.key)}" role="button" tabindex="0">${escapeHtml(group.key)}<span class="ar-row-sub">${formatNounCount(group.claims, "claim")}</span>${rate == null ? '<span class="ar-row-sub">no expected value</span>' : ""}</th>
      <td class="num">${money(group.expected)}</td>
      <td class="num">${money(group.paid)}</td>
      <td class="num">${money(group.outstanding)}</td>
      <td class="num ar-heat" style="--heat:${heat.toFixed(3)}">${evpPercent(group.paid, group.expected)}</td>
    </tr>`;
  }).join("") : `<tr><td colspan="5">${escapeHtml(emptyNote)}</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Expected vs Paid Matrix</h3>
        <p>Expected, paid, and outstanding by ${escapeHtml(arDimensionLabel(dimension).toLowerCase())} · sorted by outstanding.</p>
      </div>
      ${evpDimensionToggleHtml(dimension)}
    </div>
    ${dimension === "rateSource" ? evpTrueUpNoteHtml(Math.round(matrix.totals.oonCutTrueUp * 100) / 100, matrix.totals.oonCutTrueUpLines) : ""}
    <div class="table-wrap">
      <table class="client-table ar-matrix-table evp-matrix-table">
        <thead><tr><th scope="col">${escapeHtml(arDimensionLabel(dimension))}</th><th class="num">Expected</th><th class="num">Paid</th><th class="num">Outstanding</th><th class="num">% Collected</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr>
            <th scope="row">${escapeHtml(reportAllClaimsTotalLabel(matrix.totals))}</th>
            <td class="num"><strong>${money(matrix.totals.expected)}</strong></td>
            <td class="num"><strong>${money(matrix.totals.paid)}</strong></td>
            <td class="num"><strong>${money(matrix.totals.outstanding)}</strong></td>
            <td class="num"><strong>${evpPercent(matrix.totals.paid, matrix.totals.expected)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>`;
}

function evpDetailCell(row, col) {
  if (col.type === "money") return money(row[col.key]);
  if (col.type === "percent") return row.pctCollected == null ? "—" : `${Math.round(row.pctCollected)}%`;
  return escapeHtml(String(row[col.key] || "—"));
}

function renderEvpDetail(rows, emptyNote = "No claims in the active filter.") {
  const head = EVP_DETAIL_COLUMNS.map((col) => {
    const active = reportEvpState.sortKey === col.key;
    const arrow = active ? (reportEvpState.sortDir === "asc" ? " ▲" : " ▼") : "";
    const cls = `${col.type === "text" ? "" : "num"} ar-sortable${active ? " is-sorted" : ""}`.trim();
    return `<th class="${cls}" data-evp-sort="${col.key}" role="button" tabindex="0" aria-sort="${active ? (arrow.indexOf("▲") >= 0 ? "ascending" : "descending") : "none"}" aria-label="Sort by ${escapeHtml(col.label)}" title="Sort by ${escapeHtml(col.label)}">${escapeHtml(col.label)}<span aria-hidden="true">${arrow}</span></th>`;
  }).join("");
  // DISPLAY WINDOW ONLY — the same one renderArDetail carries, for the same reason. This table
  // is one row per claim and is the largest of the detail tables; unwindowed it pushed the whole
  // filtered book into innerHTML in a single pass. `total` stays the FULL count: the KPI header,
  // the matrix and the Detail CSV (evpExportCsv re-derives its rows from getReportClaims, not
  // from this window) are all computed off the unwindowed array and are deliberately untouched.
  // The clamp keeps a stale page — left over from a filter that shrank the book — inside the
  // real page count instead of rendering an empty table.
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / AR_DETAIL_PAGE_SIZE));
  const pageNumber = Math.min(Math.max(1, Number(reportEvpState.detailPage) || 1), pageCount);
  reportEvpState.detailPage = pageNumber;
  const startIndex = (pageNumber - 1) * AR_DETAIL_PAGE_SIZE;
  const visible = rows.slice(startIndex, startIndex + AR_DETAIL_PAGE_SIZE);
  const rangeNote = pageCount > 1 ? ` · showing ${startIndex + 1}–${startIndex + visible.length}` : "";
  const body = total ? visible.map((row) => `
    <tr>
      ${EVP_DETAIL_COLUMNS.map((col) => `<td class="${col.type === "text" ? "" : "num"}" data-label="${escapeHtml(col.label)}">${evpDetailCell(row, col)}</td>`).join("")}
    </tr>`).join("") : `<tr><td colspan="${EVP_DETAIL_COLUMNS.length}">${escapeHtml(emptyNote)}</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Expected vs Paid Detail</h3>
        <p>${formatNounCount(total, "claim")}${rangeNote} · click a column header to sort.</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>${pageCount > 1 ? `
    <div class="ar-detail-pager">
      <button type="button" class="ghost-button" data-evp-detail-page="prev"${pageNumber <= 1 ? " disabled" : ""} aria-label="Previous page of Expected vs Paid detail">‹ Prev</button>
      <span class="ar-detail-pager-status">Page ${pageNumber} of ${pageCount} · ${formatNounCount(total, "claim")} total · CSV export includes all ${total}</span>
      <button type="button" class="ghost-button" data-evp-detail-page="next"${pageNumber >= pageCount ? " disabled" : ""} aria-label="Next page of Expected vs Paid detail">Next ›</button>
    </div>` : ""}
  </section>`;
}

function bindEvpReportControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-evp-dim]").forEach((btn) => {
    btn.addEventListener("click", () => {
      reportEvpState.dimension = btn.getAttribute("data-evp-dim");
      reportEvpState.detailPage = 1;
      renderReports();
    });
  });
  host.querySelectorAll("[data-evp-detail-page]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const dir = btn.getAttribute("data-evp-detail-page");
      const next = dir === "prev" ? Number(reportEvpState.detailPage || 1) - 1 : Number(reportEvpState.detailPage || 1) + 1;
      // renderEvpDetail clamps to the real page count, so an out-of-range value here is
      // corrected on render rather than needing the count duplicated.
      reportEvpState.detailPage = Math.max(1, next);
      renderReports();
    });
  });
  host.querySelectorAll("[data-evp-drill-group]").forEach((el) => {
    const open = () => {
      const dim = reportEvpState.dimension;
      const key = el.getAttribute("data-evp-drill-group");
      const dims = [`${arDimensionLabel(dim)}: ${key}`];
      drillIntoReportRow({
        view: reportArState.view,
        label: dims.join(" · "),
        dims,
        predicate: (claim) => arDimensionKey(evpClaimRow(claim), dim) === key
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
  host.querySelectorAll("[data-evp-sort]").forEach((th) => {
    const apply = () => {
      const key = th.getAttribute("data-evp-sort");
      if (reportEvpState.sortKey === key) {
        reportEvpState.sortDir = reportEvpState.sortDir === "asc" ? "desc" : "asc";
      } else {
        reportEvpState.sortKey = key;
        const col = EVP_DETAIL_COLUMNS.find((c) => c.key === key);
        reportEvpState.sortDir = col && col.type === "text" ? "asc" : "desc";
      }
      reportEvpState.detailPage = 1;
      renderReports();
    };
    th.addEventListener("click", apply);
    th.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); apply(); }
    });
  });
  host.querySelectorAll("[data-evp-export]").forEach((btn) => {
    btn.addEventListener("click", () => evpExportCsv(btn.getAttribute("data-evp-export")));
  });
  const printBtn = host.querySelector("[data-ar-print]");
  if (printBtn) printBtn.addEventListener("click", () => window.print());
}

function evpExportCsv(kind) {
  const rows = getReportClaims(reportSourceClaims()).map(evpClaimRow);
  let table;
  let filename;
  if (kind === "matrix") {
    const matrix = evpBuildMatrix(rows, reportEvpState.dimension);
    const header = [arDimensionLabel(reportEvpState.dimension), "Expected", "Paid", "Outstanding", "% Collected", "Claims"];
    const lines = matrix.list.map((group) => [group.key, group.expected.toFixed(2), group.paid.toFixed(2), group.outstanding.toFixed(2), group.expected > 0 ? arPercent(group.paid, group.expected) : "", group.claims]);
    lines.push(["Total", matrix.totals.expected.toFixed(2), matrix.totals.paid.toFixed(2), matrix.totals.outstanding.toFixed(2), matrix.totals.expected > 0 ? arPercent(matrix.totals.paid, matrix.totals.expected) : "", matrix.totals.claims]);
    const evpBasisNote = reportAllClaimsCsvNote(matrix.totals);
    if (evpBasisNote) lines.push(evpBasisNote);
    table = [header, ...lines];
    filename = "expected-vs-paid-matrix.csv";
  } else {
    const header = EVP_DETAIL_COLUMNS.map((col) => col.label);
    const sorted = evpSortRows(rows, reportEvpState.sortKey, reportEvpState.sortDir);
    const lines = sorted.map((row) => EVP_DETAIL_COLUMNS.map((col) => {
      if (col.type === "money") return Number(row[col.key]).toFixed(2);
      if (col.type === "percent") return row.pctCollected == null ? "" : row.pctCollected.toFixed(1);
      return row[col.key];
    }));
    table = [header, ...lines];
    filename = "expected-vs-paid-detail.csv";
  }
  arDownloadFile(filename, arToCsv(table), "text/csv;charset=utf-8;");
}

// ===========================================================================
// REPORTS — Executive Expected vs Paid Rollforward (2026-07-21). Client-side
// port of CT-TRACKER build_lite_snapshot.py month rollforward. One row per
// service month (from each service line DOS) + a total row. Reads snapshot
// service-line fields ONLY — the same fields renderReportRowDrilldownLines
// financial profile surfaces — with no recompute and no fabrication.
// Expected-revision and primary/secondary payment split arrive with the
// warehouse cutover. Drill is at CLAIM grain (claims with a line in the month).
// ===========================================================================
function rollforwardLineRows(claims) {
  const rows = [];
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    (Array.isArray(claim.services) ? claim.services : []).forEach((service) => {
      const month = serviceMonth(service.dos);
      if (!month) return;
      rows.push({
        month,
        units: quantityNumber(service.units),
        // TOTAL EXPECTED IS THE GROSS SIDE OF THE BRIDGE; FINAL INS EXPECTED IS
        // THE NET. They used to be the same number by accident: neither derived
        // expected column was projected by the reports dataset, so BOTH ladders
        // resolved to the same value — gold own [Total Expected] where present
        // (94% of lines), the net final_insurance_expected where not — and the
        // table printed one number in two columns. Shipping the gross figure
        // separates them; it changes no engine result (serviceFinancialView is
        // identical to the cent before and after, measured on DOS 2025-04).
        totalExpected: moneyNumber(service.staticTotalExpected || service.finalExpectedTotalNumeric || 0),
        finalInsuranceExpected: moneyNumber(firstPresent(service.finalInsuranceExpected, service.finalExpectedTotalNumeric, 0)),
        primaryApplied: moneyNumber(firstPresent(service.primaryPaid, service.primary_paid, 0)),
        secondaryApplied: moneyNumber(firstPresent(service.secondaryPaid, service.secondary_paid, 0)),
        unattributedApplied: moneyNumber(firstPresent(service.unmatchedPaid, service.unmatched_paid, 0)),
        insurancePaid: moneyNumber(service.finalInsurancePaid),
        // PATIENT RESPONSIBILITY, NOT THE PATIENT BALANCE. This read
        // service.totalPatBal — EZClaim's SIGNED balance — so an overpaid
        // patient printed as NEGATIVE patient responsibility (-$295 ...
        // -$662,232 on the live report, 2026-08-10). The engine's
        // patientResponsibilityTotal is the CT-TRACKER quantity and is >= 0 by
        // construction; the floor covers the legacy fallback for a lane that
        // does not ship it, and is a no-op wherever the engine value is present.
        // NO STATED-BALANCE RUNG. `service.totalPatBal` sat at the end of this
        // ladder until the owner ruled it out on 2026-09-17 (Option B): the patient
        // share is PR 1/2/3, never the balance the source line states. It was
        // already unreachable on hosted rows — every snapshot row carries
        // patient_responsibility and hasPresent(0) is true — so this removes a
        // rung that could only ever have fired on a local/demo fold row, where it
        // would have printed the one number the rule forbids.
        patientResp: Math.max(0, moneyNumber(firstPresent(service.patientResponsibility, service.prAdjustmentTotal, service.pr_adjustment_total, 0))),
        writeOff: moneyNumber(service.writeOffAmount),
        outstanding: moneyNumber(service.finalInsuranceBalance)
      });
    });
  });
  return rows;
}

function rollforwardBuildRows(lineRows) {
  const groups = new Map();
  lineRows.forEach((row) => {
    if (!groups.has(row.month)) groups.set(row.month, { month: row.month, units: 0, totalExpected: 0, finalInsuranceExpected: 0, primaryApplied: 0, secondaryApplied: 0, unattributedApplied: 0, insurancePaid: 0, patientResp: 0, writeOff: 0, outstanding: 0 });
    const g = groups.get(row.month);
    g.units += row.units;
    g.totalExpected += row.totalExpected;
    g.finalInsuranceExpected += row.finalInsuranceExpected;
    g.primaryApplied += row.primaryApplied;
    g.secondaryApplied += row.secondaryApplied;
    g.unattributedApplied += row.unattributedApplied;
    g.insurancePaid += row.insurancePaid;
    g.patientResp += row.patientResp;
    g.writeOff += row.writeOff;
    g.outstanding += row.outstanding;
  });
  const list = Array.from(groups.values()).sort((a, b) => a.month.localeCompare(b.month));
  const totals = list.reduce((t, g) => {
    t.units += g.units;
    t.totalExpected += g.totalExpected;
    t.finalInsuranceExpected += g.finalInsuranceExpected;
    t.primaryApplied += g.primaryApplied;
    t.secondaryApplied += g.secondaryApplied;
    t.unattributedApplied += g.unattributedApplied;
    t.insurancePaid += g.insurancePaid;
    t.patientResp += g.patientResp;
    t.writeOff += g.writeOff;
    t.outstanding += g.outstanding;
    return t;
  }, { units: 0, totalExpected: 0, finalInsuranceExpected: 0, primaryApplied: 0, secondaryApplied: 0, unattributedApplied: 0, insurancePaid: 0, patientResp: 0, writeOff: 0, outstanding: 0 });
  return { list, totals };
}

function rollforwardMonthLabel(month) {
  const label = monthLabelFromYearMonth(month);
  return label ? `${label} ${String(month).slice(0, 4)}` : (month || "—");
}

function renderRollforwardReport(model, claimCount) {
  const body = model.list.length ? model.list.map((g) => `
    <tr>
      <th scope="row" class="ar-drillable" data-rollforward-drill-month="${escapeHtml(g.month)}" role="button" tabindex="0">${escapeHtml(rollforwardMonthLabel(g.month))}</th>
      <td class="num">${Math.round(g.units).toLocaleString()}</td>
      <td class="num">${money(g.totalExpected)}</td>
      <td class="num">${money(g.finalInsuranceExpected)}</td>
      <td class="num">${money(g.insurancePaid)}</td>
      <td class="num">${money(g.patientResp)}</td>
      <td class="num">${reportWriteOffMoney(g.writeOff)}</td>
      <td class="num">${money(g.outstanding)}</td>
    </tr>`).join("") : '<tr><td colspan="8">No service lines with a service date in the active filter.</td></tr>';
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Expected vs Paid Rollforward</h3>
        <p>${formatNounCount(claimCount, "claim")} in the active filter · one row per service month (from date of service).</p>
        <p class="ar-row-sub">Expected revisions and the primary/secondary payment split are not available yet.</p>
        ${reportWriteOffNoticeHtml()}
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-matrix-table">
        <thead><tr><th scope="col">Service Month</th><th class="num">Units</th><th class="num">Total Expected</th><th class="num">Final Ins Expected</th><th class="num">Insurance Paid</th><th class="num">Patient Resp</th><th class="num">Write-off</th><th class="num">Outstanding</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td class="num"><strong>${Math.round(model.totals.units).toLocaleString()}</strong></td>
            <td class="num"><strong>${money(model.totals.totalExpected)}</strong></td>
            <td class="num"><strong>${money(model.totals.finalInsuranceExpected)}</strong></td>
            <td class="num"><strong>${money(model.totals.insurancePaid)}</strong></td>
            <td class="num"><strong>${money(model.totals.patientResp)}</strong></td>
            <td class="num"><strong>${money(model.totals.writeOff)}</strong></td>
            <td class="num"><strong>${money(model.totals.outstanding)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>`;
}

function bindRollforwardReportControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-rollforward-drill-month]").forEach((el) => {
    const open = () => {
      const month = el.getAttribute("data-rollforward-drill-month");
      drillIntoReportRow({
        view: reportArState.view,
        label: `Service Month ${rollforwardMonthLabel(month)}`,
        dims: [`Service Month: ${rollforwardMonthLabel(month)}`],
        predicate: (claim) => (Array.isArray(claim.services) ? claim.services : []).some((s) => serviceMonth(s.dos) === month)
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
}

// ===========================================================================
// REPORTS — Monthly Summary (2026-07-21). Client-side port of CT-TRACKER
// LiteMonthlySummary. Same per-service-month grain as Rollforward but framed
// as a collections-completeness view: expected vs applied/paid vs remaining,
// plus % collected. Reuses the Rollforward line/group builders and the shared
// claim-grain drill. Distinct from Rollforward's stage bridge, which also
// surfaces units, final-ins-expected, patient responsibility, and write-off.
// ===========================================================================
function monthlySummaryPctCollected(paid, expected) {
  if (isUnknownMoney(paid) || isUnknownMoney(expected)) return null;
  return expected > 0 ? Math.round((paid / expected) * 1000) / 10 : null;
}

function renderMonthlySummaryReport(model, claimCount) {
  const body = model.list.length ? model.list.map((g) => `
    <tr>
      <th scope="row" class="ar-drillable" data-monthly-summary-drill-month="${escapeHtml(g.month)}" role="button" tabindex="0">${escapeHtml(rollforwardMonthLabel(g.month))}</th>
      <td class="num">${money(g.finalInsuranceExpected)}</td>
      <td class="num">${money(g.primaryApplied)}</td>
      <td class="num">${money(g.secondaryApplied)}</td>
      <td class="num">${money(g.unattributedApplied)}</td>
      <td class="num">${money(g.insurancePaid)}</td>
      <td class="num">${money(g.outstanding)}</td>
      <td class="num">${monthlySummaryPctCollected(g.insurancePaid, g.finalInsuranceExpected) == null ? MONEY_UNKNOWN_TEXT : `${monthlySummaryPctCollected(g.insurancePaid, g.finalInsuranceExpected).toFixed(1)}%`}</td>
    </tr>`).join("") : '<tr><td colspan="8">No service lines with a service date in the active filter.</td></tr>';
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Monthly Summary</h3>
        <p>${formatNounCount(claimCount, "claim")} in the active filter · one row per service month (from date of service) · expected vs applied vs remaining.</p>
        <p class="ar-row-sub">Collections completeness by service month: how much of each month's expected reimbursement has been applied. The denominator is INSURANCE expected — gross expected less patient responsibility and write-off — so it stays the same basis as the Remaining column beside it (Remaining = Insurance Expected − Applied).</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-matrix-table">
        <thead><tr><th scope="col">Service Month</th><th class="num">Insurance Expected</th><th class="num">Primary Applied Payments</th><th class="num">Secondary Applied Payments</th><th class="num">Unattributed Applied Payments</th><th class="num">Total Applied Payments</th><th class="num">Remaining</th><th class="num">% Collected</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td class="num"><strong>${money(model.totals.finalInsuranceExpected)}</strong></td>
            <td class="num"><strong>${money(model.totals.primaryApplied)}</strong></td>
            <td class="num"><strong>${money(model.totals.secondaryApplied)}</strong></td>
            <td class="num"><strong>${money(model.totals.unattributedApplied)}</strong></td>
            <td class="num"><strong>${money(model.totals.insurancePaid)}</strong></td>
            <td class="num"><strong>${money(model.totals.outstanding)}</strong></td>
            <td class="num"><strong>${monthlySummaryPctCollected(model.totals.insurancePaid, model.totals.finalInsuranceExpected) == null ? MONEY_UNKNOWN_TEXT : `${monthlySummaryPctCollected(model.totals.insurancePaid, model.totals.finalInsuranceExpected).toFixed(1)}%`}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>`;
}

function bindMonthlySummaryReportControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-monthly-summary-drill-month]").forEach((el) => {
    const open = () => {
      const month = el.getAttribute("data-monthly-summary-drill-month");
      drillIntoReportRow({
        view: reportArState.view,
        label: `Service Month ${rollforwardMonthLabel(month)}`,
        dims: [`Service Month: ${rollforwardMonthLabel(month)}`],
        predicate: (claim) => (Array.isArray(claim.services) ? claim.services : []).some((s) => serviceMonth(s.dos) === month)
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
}

// ===========================================================================
// REPORTS — Reimbursement by CPT × Payer (2026-07-21). Client-side port of
// CT-TRACKER CPT×payer reimbursement view. SERVICE-LINE grain, grouped by
// procedure (CPT) × primary payer. Reads snapshot service-line fields ONLY.
// DRILLS, and the note that used to say it could not is why the three line-grain
// views had to land together. The substrate resolves to whole CLAIMS, so it cannot
// show one CPT+payer line in isolation — but it can show exactly the claims that
// carry the lines behind the clicked cell, which is the question a biller is
// actually asking. cptPayerCellLines is the SAME function the aggregate groups
// with, so the cell and its drill cannot drift; the header states "N of M service
// lines" so the extra lines on those claims are declared rather than implied; and
// the clicked cell's own money rides an amber dim, at its own grain, with its own
// name. Payer class and in/out-of-network details are not available yet.
// ===========================================================================
function cptPayerLineRows(claims) {
  const rows = [];
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    (Array.isArray(claim.services) ? claim.services : []).forEach((service) => {
      const procedure = String(service.procedure || "").trim() || "—";
      const payer = String(service.primaryPayer || claim.primaryPayer || "").trim() || "—";
      rows.push({
        claimId: String(claim.claimId || ""),
        procedure,
        payer,
        units: quantityNumber(service.units),
        primaryExpected: moneyNumber(service.primaryExpectedTotal),
        insurancePaid: moneyNumber(service.finalInsurancePaid)
      });
    });
  });
  return rows;
}

// ONE key function for the report AND the drill. Two copies of a grouping rule still render
// and still open — they just open onto the wrong claims, silently.
function cptPayerGroupKey(row) {
  return `${row.procedure}||${row.payer}`;
}

// The DOM-safe form of the same key. encodeURIComponent rather than a separator of our own:
// a payer name containing the separator would otherwise merge two cells into one drill.
function cptPayerGroupAttr(row) {
  return encodeURIComponent(cptPayerGroupKey(row));
}

function cptPayerCellLines(attr, claims) {
  return cptPayerLineRows(claims).filter((row) => cptPayerGroupAttr(row) === attr);
}

function cptPayerBuildRows(lineRows) {
  const groups = new Map();
  lineRows.forEach((row) => {
    const key = cptPayerGroupKey(row);
    if (!groups.has(key)) groups.set(key, { procedure: row.procedure, payer: row.payer, units: 0, primaryExpected: 0, insurancePaid: 0 });
    const g = groups.get(key);
    g.units += row.units;
    g.primaryExpected += row.primaryExpected;
    g.insurancePaid += row.insurancePaid;
  });
  const list = Array.from(groups.values()).sort((a, b) => (b.insurancePaid - a.insurancePaid) || (b.units - a.units));
  const totals = list.reduce((t, g) => {
    t.units += g.units;
    t.primaryExpected += g.primaryExpected;
    t.insurancePaid += g.insurancePaid;
    return t;
  }, { units: 0, primaryExpected: 0, insurancePaid: 0 });
  return { list, totals };
}

function renderCptPayerReport(model, claimCount) {
  const body = model.list.length ? model.list.map((g) => {
    const avg = g.units > 0 && !isUnknownMoney(g.insurancePaid) ? g.insurancePaid / g.units : null;
    const rate = !isUnknownMoney(g.primaryExpected) && !isUnknownMoney(g.insurancePaid) && g.primaryExpected > 0 ? g.insurancePaid / g.primaryExpected : null;
    const heat = rate == null || rate >= 1 ? 0 : Math.max(0.06, Math.min(1, 1 - rate));
    return `
    <tr>
      <th scope="row" class="ar-drillable" data-cpt-payer-drill-key="${escapeHtml(cptPayerGroupAttr(g))}" role="button" tabindex="0" title="Show the claims behind this CPT × payer cell">${escapeHtml(g.procedure)}<span class="ar-row-sub">${escapeHtml(g.payer)}</span></th>
      <td class="num">${Math.round(g.units).toLocaleString()}</td>
      <td class="num">${avg == null ? "—" : money(avg)}</td>
      <td class="num">${money(g.primaryExpected)}</td>
      <td class="num">${money(g.insurancePaid)}</td>
      <td class="num ar-heat" style="--heat:${heat.toFixed(3)}">${rate == null ? "—" : `${Math.round(rate * 100)}%`}</td>
    </tr>`;
  }).join("") : '<tr><td colspan="6">No service lines in the active filter.</td></tr>';
  const totalAvg = model.totals.units > 0 && !isUnknownMoney(model.totals.insurancePaid) ? model.totals.insurancePaid / model.totals.units : null;
  const totalRate = !isUnknownMoney(model.totals.primaryExpected) && !isUnknownMoney(model.totals.insurancePaid) && model.totals.primaryExpected > 0 ? model.totals.insurancePaid / model.totals.primaryExpected : null;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Reimbursement by CPT × Payer</h3>
        <p>${formatNounCount(claimCount, "claim")} in the active filter · service-line grain, grouped by CPT × primary payer · sorted by insurance paid.</p>
        <p class="ar-row-sub">Payer class is not broken out here; filter the whole report by In / Out of Network above. Click a CPT / Payer cell to see the claims carrying those lines — the drill lists whole claims, so it also shows lines outside the cell.</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-matrix-table">
        <thead><tr><th scope="col">CPT / Payer</th><th class="num">Units</th><th class="num">Avg Reimb / Unit</th><th class="num">Total Expected</th><th class="num">Insurance Paid</th><th class="num">Collection %</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td class="num"><strong>${Math.round(model.totals.units).toLocaleString()}</strong></td>
            <td class="num"><strong>${totalAvg == null ? "—" : money(totalAvg)}</strong></td>
            <td class="num"><strong>${money(model.totals.primaryExpected)}</strong></td>
            <td class="num"><strong>${money(model.totals.insurancePaid)}</strong></td>
            <td class="num"><strong>${totalRate == null ? "—" : `${Math.round(totalRate * 100)}%`}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>`;
}

// The binder this view never had. Everything it needs is re-derived from LIVE state inside the
// resolvers, not captured here: the drill must survive a re-render and a dataset refresh, and
// a snapshot taken at click time survives the first and lies through the second.
function bindCptPayerReportControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-cpt-payer-drill-key]").forEach((el) => {
    const open = () => {
      const attr = el.getAttribute("data-cpt-payer-drill-key") || "";
      const live = getReportClaims(reportSourceClaims());
      const clicked = cptPayerBuildRows(cptPayerLineRows(live)).list.find((g) => cptPayerGroupAttr(g) === attr);
      // An em dash is an ABSENCE, not a filter the biller chose. Dropped from the LABEL only --
      // the cell key still matches on whatever the row holds, or the drill returns nothing.
      const dims = [["CPT", clicked && clicked.procedure], ["Payer", clicked && clicked.payer]]
        .filter(([, value]) => value && value !== "—")
        .map(([name, value]) => `${name}: ${value}`);
      const label = dims.join(" · ");
      // MONEY, at the grain the biller clicked. The footer below sums whole claims; this cell is
      // a slice of service lines inside them. Both are true and they are not the same number,
      // so the row's own figures are stated with their own names rather than substituted.
      if (clicked) {
        dims.push(`Row ${formatNounCount(cptPayerCellLines(attr, live).length, "service line")} · ${Math.round(clicked.units).toLocaleString()} units · Expected ${money(clicked.primaryExpected)} · Insurance Paid ${money(clicked.insurancePaid)} — service-line grain`);
      }
      drillIntoReportRow({
        view: reportArState.view,
        label,
        dims,
        claimIds: cptPayerDrillClaimIds(attr),
        footerOverride: cptPayerDrillFooter(attr)
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
}

function cptPayerDrillClaimIds(attr) {
  return () => new Set(cptPayerCellLines(attr, getReportClaims(reportSourceClaims()))
    .map((row) => String(row.claimId || "")).filter(Boolean));
}

function cptPayerDrillFooter(attr) {
  return () => ({ lines: cptPayerCellLines(attr, getReportClaims(reportSourceClaims())).length });
}

// ===========================================================================
// REPORTS — Denials. One question: what's being denied and what is it
// costing us? Client-side only, like Expected vs Paid: the filtered claim
// set narrowed to isDeniedClaim (the documented denial contract), reason
// buckets from denialReason, and dollars at risk = expected reimbursement
// still uncollected on each denial-flagged claim.
// ===========================================================================
const DENIALS_DETAIL_COLUMNS = [
  { key: "claimId", label: "Claim", type: "text" },
  { key: "payer", label: "Payer", type: "text" },
  { key: "tenant", label: "Client", type: "text" },
  { key: "provider", label: "Provider", type: "text" },
  { key: "claimStatus", label: "Status", type: "text" },
  { key: "action", label: "Action", type: "text" },
  { key: "reason", label: "Reason", type: "text" },
  // Bill Date dropped 2026-08-10 — see AR_DETAIL_COLUMNS.
  { key: "atRisk", label: "At Risk", type: "money" }
];

function denialsClaimRow(claim) {
  const fin = claimFinancialView(claim);
  return {
    claimId: claim.claimId || "",
    tenant: claim.client || "—",
    payer: claim.primaryPayer || claim.secondaryPayer || "—",
    provider: claim.renderingProvider || "—",
    claimStatus: claim.claimStatus || "—",
    action: firstPresent(claim.action, claim.followUpAction, claim.follow_up_action, "—"),
    reason: denialReason(claim),
    atRisk: moneyNumber(fin.expectedOutstanding),
    expectedKnown: reportExpectedKnown(fin)
  };
}

function denialsReportTotals(rows) {
  return rows.reduce((t, r) => {
    t.claims += 1;
    t.atRisk += r.atRisk;
    if (r.expectedKnown) t.atRiskKnownBasis += r.atRisk;
    else t.unratedClaims += 1;
    return t;
  }, { claims: 0, atRisk: 0, unratedClaims: 0, atRiskKnownBasis: 0 });
}

// The matrix names each group's modal reason bucket, not a recount of every
// bucket — the reason table below is the full breakdown.
function denialsTopReason(rows) {
  const counts = new Map();
  rows.forEach((row) => counts.set(row.reason, (counts.get(row.reason) || 0) + 1));
  let top = "—";
  let max = 0;
  counts.forEach((count, reason) => {
    if (count > max) { max = count; top = reason; }
  });
  return top;
}

function denialsBuildMatrix(rows, dimension) {
  const groups = new Map();
  rows.forEach((row) => {
    const key = arDimensionKey(row, dimension);
    if (!groups.has(key)) groups.set(key, { key, claims: 0, atRisk: 0, unrated: 0, rows: [] });
    const group = groups.get(key);
    group.claims += 1;
    group.atRisk += row.atRisk;
    if (!row.expectedKnown) group.unrated += 1;
    group.rows.push(row);
  });
  const list = Array.from(groups.values())
    .sort((a, b) => b.atRisk - a.atRisk)
    .map((group) => ({ key: group.key, claims: group.claims, atRisk: group.atRisk, unrated: group.unrated, topReason: denialsTopReason(group.rows) }));
  const totals = list.reduce((t, g) => {
    t.claims += g.claims;
    t.atRisk += g.atRisk;
    t.unratedClaims += g.unrated;
    return t;
  }, { claims: 0, atRisk: 0, unratedClaims: 0 });
  return { list, totals };
}

function denialsReasonRows(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    if (!groups.has(row.reason)) groups.set(row.reason, { reason: row.reason, claims: 0, atRisk: 0 });
    const group = groups.get(row.reason);
    group.claims += 1;
    group.atRisk += row.atRisk;
  });
  return Array.from(groups.values()).sort((a, b) => b.atRisk - a.atRisk);
}

function denialsSortRows(rows, key, dir) {
  const col = DENIALS_DETAIL_COLUMNS.find((c) => c.key === key) || DENIALS_DETAIL_COLUMNS.find((c) => c.key === "atRisk");
  const mult = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (col.type === "text") return String(a[col.key]).localeCompare(String(b[col.key])) * mult;
    return ((Number(a[col.key]) || 0) - (Number(b[col.key]) || 0)) * mult;
  });
}

function renderDenialsKpiHeader(totals, claimCount) {
  const share = claimCount > 0 ? `${Math.round((totals.claims / claimCount) * 100)}%` : "0%";
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Denials</h3>
        <p>${formatNounCount(totals.claims, "denial-flagged claim")} of ${formatNounCount(claimCount, "claim")} in the active filter · service-date basis.</p>
      </div>
      ${reportExportActionsHtml(`
        <button type="button" class="ghost-button" data-denials-export="matrix">Matrix → CSV</button>
        <button type="button" class="ghost-button" data-denials-export="reasons">Reasons → CSV</button>
        <button type="button" class="ghost-button" data-denials-export="detail">Detail → CSV</button>
        <button type="button" class="primary-button" data-ar-print>Print / PDF</button>`)}
    </div>
    <div class="ar-kpi-grid">
      ${reportExpectedKpiCard("Dollars at Risk", totals.atRiskKnownBasis, "Expected reimbursement not yet collected on these claims", "accent", totals,
        "Awaiting expected values — not $0. Counts and reasons below are complete.")}
      ${arKpiCard("Denied Claims", totals.claims.toLocaleString(), `${share} of filtered claims`, "primary")}
    </div>
    ${reportExpectedNoticeHtml(totals)}
  </section>`;
}

function renderDenialsMatrix(matrix, dimension) {
  const dimensions = [["payer", "Payer"], ["client", "Client"], ["provider", "Provider"]];
  // Heat scales against the worst group so the dimension bleeding the most
  // money pops without reading every number — same idea as the EVP tint.
  const maxRisk = matrix.list.reduce((max, group) => Math.max(max, group.atRisk), 0);
  const body = matrix.list.map((group) => {
    const heat = maxRisk > 0 ? Math.max(0.06, Math.min(1, group.atRisk / maxRisk)) : 0;
    return `
    <tr>
      <th scope="row" class="ar-drillable" data-denials-drill-group="${escapeHtml(group.key)}" role="button" tabindex="0">${escapeHtml(group.key)}<span class="ar-row-sub">${formatNounCount(group.claims, "claim")}</span></th>
      <td class="num">${group.claims.toLocaleString()}</td>
      <td class="num ar-heat" style="--heat:${heat.toFixed(3)}">${money(group.atRisk)}</td>
      <td>${escapeHtml(group.topReason)}</td>
    </tr>`;
  }).join("");
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Denials Matrix</h3>
        <p>Denied claims and dollars at risk by ${escapeHtml(arDimensionLabel(dimension).toLowerCase())} · sorted by dollars at risk.</p>
      </div>
      <div class="ar-dim-toggle reports-no-print" role="group" aria-label="Group Denials matrix by">
        ${dimensions.map(([key, label]) => `<button type="button" class="ar-dim-btn${dimension === key ? " is-active" : ""}" data-denials-dim="${key}">${label}</button>`).join("")}
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-matrix-table denials-matrix-table">
        <thead><tr><th scope="col">${escapeHtml(arDimensionLabel(dimension))}</th><th class="num">Denied Claims</th><th class="num">Dollars at Risk</th><th scope="col">Top Reason</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr>
            <th scope="row">${escapeHtml(reportAllClaimsTotalLabel(matrix.totals))}</th>
            <td class="num"><strong>${matrix.totals.claims.toLocaleString()}</strong></td>
            <td class="num"><strong>${money(matrix.totals.atRisk)}</strong></td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>`;
}

function renderDenialsReasons(rows) {
  const body = rows.map((row) => `
    <tr>
      <th scope="row">${escapeHtml(row.reason)}</th>
      <td class="num">${row.claims.toLocaleString()}</td>
      <td class="num">${money(row.atRisk)}</td>
    </tr>`).join("");
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Denials by Reason</h3>
        <p>Reason buckets derived from claim status, actions, and notes · sorted by dollars at risk.</p>
      </div>
    </div>
    <div class="table-wrap denials-reason-wrap">
      <table class="client-table">
        <thead><tr><th scope="col">Reason</th><th class="num">Claims</th><th class="num">Dollars at Risk</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </section>`;
}

function denialsDetailCell(row, col) {
  if (col.type === "money") return money(row[col.key]);
  return escapeHtml(String(row[col.key] || "—"));
}

function renderDenialsDetail(rows) {
  const head = DENIALS_DETAIL_COLUMNS.map((col) => {
    const active = reportDenialsState.sortKey === col.key;
    const arrow = active ? (reportDenialsState.sortDir === "asc" ? " ▲" : " ▼") : "";
    const cls = `${col.type === "text" ? "" : "num"} ar-sortable${active ? " is-sorted" : ""}`.trim();
    return `<th class="${cls}" data-denials-sort="${col.key}" role="button" tabindex="0" aria-sort="${active ? (arrow.indexOf("▲") >= 0 ? "ascending" : "descending") : "none"}" aria-label="Sort by ${escapeHtml(col.label)}" title="Sort by ${escapeHtml(col.label)}">${escapeHtml(col.label)}<span aria-hidden="true">${arrow}</span></th>`;
  }).join("");
  // DISPLAY WINDOW ONLY — the same one renderArDetail carries, for the same reason. `total`
  // stays the FULL count: the KPI header, the matrix, the reason breakdown and the Detail CSV
  // (denialsExportCsv re-derives its rows from getReportClaims, not from this window) are all
  // computed off the unwindowed array and are deliberately untouched. The clamp keeps a stale
  // page inside the real page count instead of rendering an empty table.
  //
  // The empty-row fallback is new: this renderer had none, so an empty book printed a headed
  // table with no body and no sentence. The live branch guards upstream, which is why nobody
  // saw it; the row is here so the guard cannot be the only thing standing between a biller and
  // a blank table.
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / AR_DETAIL_PAGE_SIZE));
  const pageNumber = Math.min(Math.max(1, Number(reportDenialsState.detailPage) || 1), pageCount);
  reportDenialsState.detailPage = pageNumber;
  const startIndex = (pageNumber - 1) * AR_DETAIL_PAGE_SIZE;
  const visible = rows.slice(startIndex, startIndex + AR_DETAIL_PAGE_SIZE);
  const rangeNote = pageCount > 1 ? ` · showing ${startIndex + 1}–${startIndex + visible.length}` : "";
  const body = total ? visible.map((row) => `
    <tr>
      ${DENIALS_DETAIL_COLUMNS.map((col) => `<td class="${col.type === "text" ? "" : "num"}" data-label="${escapeHtml(col.label)}">${denialsDetailCell(row, col)}</td>`).join("")}
    </tr>`).join("") : `<tr><td colspan="${DENIALS_DETAIL_COLUMNS.length}">No denial-flagged claims in the active filter.</td></tr>`;
  return `
  <section class="panel ar-report-panel">
    <div class="ar-report-head">
      <div>
        <h3>Denials Detail</h3>
        <p>${formatNounCount(total, "denial-flagged claim")}${rangeNote} · click a column header to sort.</p>
      </div>
    </div>
    <div class="table-wrap">
      <table class="client-table ar-detail-table">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>${pageCount > 1 ? `
    <div class="ar-detail-pager">
      <button type="button" class="ghost-button" data-denials-detail-page="prev"${pageNumber <= 1 ? " disabled" : ""} aria-label="Previous page of Denials detail">‹ Prev</button>
      <span class="ar-detail-pager-status">Page ${pageNumber} of ${pageCount} · ${formatNounCount(total, "denial-flagged claim")} total · CSV export includes all ${total}</span>
      <button type="button" class="ghost-button" data-denials-detail-page="next"${pageNumber >= pageCount ? " disabled" : ""} aria-label="Next page of Denials detail">Next ›</button>
    </div>` : ""}
  </section>`;
}

function bindDenialsReportControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-denials-dim]").forEach((btn) => {
    btn.addEventListener("click", () => {
      reportDenialsState.dimension = btn.getAttribute("data-denials-dim");
      reportDenialsState.detailPage = 1;
      renderReports();
    });
  });
  host.querySelectorAll("[data-denials-detail-page]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const dir = btn.getAttribute("data-denials-detail-page");
      const next = dir === "prev" ? Number(reportDenialsState.detailPage || 1) - 1 : Number(reportDenialsState.detailPage || 1) + 1;
      // renderDenialsDetail clamps to the real page count, so an out-of-range value here is
      // corrected on render rather than needing the count duplicated.
      reportDenialsState.detailPage = Math.max(1, next);
      renderReports();
    });
  });
  host.querySelectorAll("[data-denials-drill-group]").forEach((el) => {
    const open = () => {
      const dim = reportDenialsState.dimension;
      const key = el.getAttribute("data-denials-drill-group");
      const dims = [`${arDimensionLabel(dim)}: ${key}`];
      drillIntoReportRow({
        view: reportArState.view,
        label: dims.join(" · "),
        dims,
        predicate: (claim) => isDeniedClaim(claim) && arDimensionKey(denialsClaimRow(claim), dim) === key
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
  host.querySelectorAll("[data-denials-sort]").forEach((th) => {
    const apply = () => {
      const key = th.getAttribute("data-denials-sort");
      if (reportDenialsState.sortKey === key) {
        reportDenialsState.sortDir = reportDenialsState.sortDir === "asc" ? "desc" : "asc";
      } else {
        reportDenialsState.sortKey = key;
        const col = DENIALS_DETAIL_COLUMNS.find((c) => c.key === key);
        reportDenialsState.sortDir = col && col.type === "text" ? "asc" : "desc";
      }
      reportDenialsState.detailPage = 1;
      renderReports();
    };
    th.addEventListener("click", apply);
    th.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); apply(); }
    });
  });
  host.querySelectorAll("[data-denials-export]").forEach((btn) => {
    btn.addEventListener("click", () => denialsExportCsv(btn.getAttribute("data-denials-export")));
  });
  const printBtn = host.querySelector("[data-ar-print]");
  if (printBtn) printBtn.addEventListener("click", () => window.print());
}

function denialsExportCsv(kind) {
  const rows = getReportClaims(reportSourceClaims()).filter(isDeniedClaim).map(denialsClaimRow);
  let table;
  let filename;
  if (kind === "matrix") {
    const matrix = denialsBuildMatrix(rows, reportDenialsState.dimension);
    const header = [arDimensionLabel(reportDenialsState.dimension), "Denied Claims", "Dollars at Risk", "Top Reason"];
    const lines = matrix.list.map((group) => [group.key, group.claims, group.atRisk.toFixed(2), group.topReason]);
    lines.push(["Total", matrix.totals.claims, matrix.totals.atRisk.toFixed(2), ""]);
    const denBasisNote = reportAllClaimsCsvNote(matrix.totals);
    if (denBasisNote) lines.push(denBasisNote);
    table = [header, ...lines];
    filename = "denials-matrix.csv";
  } else if (kind === "reasons") {
    const header = ["Reason", "Claims", "Dollars at Risk"];
    table = [header, ...denialsReasonRows(rows).map((row) => [row.reason, row.claims, row.atRisk.toFixed(2)])];
    filename = "denials-reasons.csv";
  } else {
    const header = DENIALS_DETAIL_COLUMNS.map((col) => col.label);
    const sorted = denialsSortRows(rows, reportDenialsState.sortKey, reportDenialsState.sortDir);
    const lines = sorted.map((row) => DENIALS_DETAIL_COLUMNS.map((col) => col.type === "money" ? Number(row[col.key]).toFixed(2) : row[col.key]));
    table = [header, ...lines];
    filename = "denials-detail.csv";
  }
  arDownloadFile(filename, arToCsv(table), "text/csv;charset=utf-8;");
}

// ===========================================================================
// PERFORMANCE REPORT (2026-06-11) — port of the TrackerUpload "Collections
// Overview" engine onto the portal's client-side claim data. No uploads, no
// backend: rows are the shared filtered claim set, financials come from
// claimFinancialView. Partition: pre-billing (Ready to Submit) and CAP rows
// (On Hold / Client Action Pending) are excluded, and the default lens hides
// claims aging under 60 days behind a per-view toggle.
// ===========================================================================
// Shared CAP predicate — a claim is "held" when its status is On Hold or its
// action starts with Client Action Pending. Performance EXCLUDES exactly the
// claims the Client Action report INCLUDES; both partitions call this one
// function so the two views can never drift apart.
function isClientActionClaim(claim) {
  if (String(claim.claimStatus || "").trim().toLowerCase() === "on hold") return true;
  const action = String(firstPresent(claim.action, claim.followUpAction, claim.follow_up_action) ?? "").trim().toLowerCase();
  return action.startsWith("client action pending");
}

// Per-CLAIM membership. The partition below is this predicate over an array and nothing more,
// so the drill-down can re-embed the view's own admission rule instead of transcribing it a
// second time. It reads reportPerformanceState LIVE: a drill opened while the under-60-day lens
// was off must stop admitting young claims the moment the biller turns the lens on.
function performanceIncludesClaim(claim) {
  // Pre-billing: never submitted, so it cannot be judged on collections.
  if (String(claim.claimStatus || "").trim().toLowerCase() === "ready to submit") return false;
  // CAP rows (on hold / client action pending) belong to the Client Action
  // view — the shared isClientActionClaim predicate keeps Performance's
  // exclusion and that view's inclusion exact inverses.
  if (isClientActionClaim(claim)) return false;
  // Default lens: claims under 60 days from submission are still inside
  // normal payer turnaround and would unfairly drag the rates down.
  if (!reportPerformanceState.includeYoungClaims && arSubmissionAgingDays(claim) < 60) return false;
  return true;
}

function performancePartition(claims) {
  return claims.filter(performanceIncludesClaim);
}

// The claims behind ONE Monthly Performance row. Closes over the month STRING and never over a
// resolved claim list — the drill re-renders from live state, so a captured list would silently
// drift from the report it claims to explain. The PARTITION is re-embedded rather than assumed:
// "2026-03" equally identifies pre-billing, held, and (by default) under-60-day claims that never
// entered the row's arithmetic, so a month-only match would show claims the view excluded.
function performanceDrillPredicate(month) {
  return (claim) => performanceIncludesClaim(claim) && claimMonth(claim) === month;
}

// Pure per-claim engine — each rule is a TrackerUpload business decision
// carried over verbatim. Inputs are plain numbers so the math stays testable
// without a full claim object.
function performanceClaimMetrics({ expected = 0, paid = 0, writeOff = 0, patResp = 0, insBalance = 0 }) {
  const clamp0 = (value) => Math.max(0, moneyNumber(value));
  const rawExpected = clamp0(expected);
  const rawPaid = clamp0(paid);
  // Rule 1 — 30% underpayment rule: paid within 30% of expected counts as
  // paid in full (contractual variance, not collectible AR), so the claim's
  // expected is restated to what was actually paid.
  const within30 = rawExpected > 0 && rawPaid < rawExpected && (rawExpected - rawPaid) / rawExpected <= 0.30;
  const effectiveExpected = within30 ? rawPaid : rawExpected;
  // Rule 2 — overpayment cap: overpayment never inflates collected.
  const cappedPaid = Math.min(rawPaid, effectiveExpected);
  // Rule 3 — remaining balance after capped collections.
  const remaining = Math.max(0, moneyNumber(effectiveExpected - cappedPaid));
  // Rule 4 — insurance AR: zero when the 30% rule marked the claim paid,
  // else the clamped outstanding insurance balance.
  const insArRaw = within30 ? 0 : clamp0(insBalance);
  // Rule 5 — write-offs and patient responsibility are capped at the claim's
  // remaining balance so resolution can never exceed 100%.
  return {
    expected: effectiveExpected,
    paid: cappedPaid,
    insArRaw,
    writeOff: Math.min(clamp0(writeOff), remaining),
    patResp: Math.min(clamp0(patResp), remaining)
  };
}

function performanceClaimRow(claim) {
  const fin = claimFinancialView(claim);
  return {
    month: claimMonth(claim),
    payer: claim.primaryPayer || claim.secondaryPayer || "—",
    ...performanceClaimMetrics({
      expected: fin.expectedReimbursement,
      paid: fin.totalInsurancePaid,
      writeOff: fin.writeOffAmount,
      patResp: fin.patientResponsibility,
      // expectedOutstanding is the same balance the AR view's Insurance AR
      // column uses — Expected − Paid clamped at zero.
      insBalance: fin.expectedOutstanding
    }),
    expectedKnown: reportExpectedKnown(fin)
  };
}

// Rule 6 — totals: write-offs resolve AR dollar-for-dollar, and both rates
// are clamped at 100 so the capped inputs can never read as over-collection.
function performanceKpis(rows) {
  const totals = rows.reduce((t, row) => {
    t.claims += 1;
    t.expected += row.expected;
    t.paid += row.paid;
    t.insArRaw += row.insArRaw;
    t.writeOffs += row.writeOff;
    t.patResp += row.patResp;
    if (row.expectedKnown) {
      t.expectedKnownBasis += row.expected;
      t.paidOnKnown += row.paid;
      t.insArRawOnKnown += row.insArRaw;
      t.writeOffsOnKnown += row.writeOff;
      t.patRespOnKnown += row.patResp;
    } else {
      t.unratedClaims += 1;
    }
    return t;
  }, { claims: 0, expected: 0, paid: 0, insArRaw: 0, writeOffs: 0, patResp: 0, unratedClaims: 0,
       expectedKnownBasis: 0, paidOnKnown: 0, insArRawOnKnown: 0, writeOffsOnKnown: 0, patRespOnKnown: 0 });
  totals.insAr = Math.max(0, moneyNumber(totals.insArRaw - totals.writeOffs));
  // Known-basis siblings — the figures the expected-derived tiles actually show,
  // so a caption that says "N of M claims with priced rates" is describing the
  // number beside it rather than a blend that quietly includes the other M-N.
  totals.insArKnownBasis = Math.max(0, moneyNumber(totals.insArRawOnKnown - totals.writeOffsOnKnown));
  // Both sides of every ratio come from the same claim set.
  // Unclamped for the same reason as the per-row rate above: a total that cannot
  // exceed 100% quietly absorbs every over-collection in the book.
  totals.collectionRate = totals.expectedKnownBasis > 0 ? (totals.paidOnKnown / totals.expectedKnownBasis) * 100 : null;
  totals.resolutionRate = totals.expectedKnownBasis > 0 ? Math.min(100, ((totals.paidOnKnown + totals.writeOffsOnKnown + totals.patRespOnKnown) / totals.expectedKnownBasis) * 100) : null;
  return totals;
}

function performanceMonthRows(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    if (!groups.has(row.month)) groups.set(row.month, []);
    groups.get(row.month).push(row);
  });
  return Array.from(groups.keys())
    .sort((a, b) => a.localeCompare(b))
    .map((month) => ({ month, ...performanceKpis(groups.get(month)) }));
}

function performancePayerRows(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    if (!groups.has(row.payer)) groups.set(row.payer, []);
    groups.get(row.payer).push(row);
  });
  const ranked = Array.from(groups.keys())
    .map((payer) => ({ payer, ...performanceKpis(groups.get(payer)) }))
    .filter((group) => group.insAr > 0)
    .sort((a, b) => b.insAr - a.insAr);
  // "top 10 shown" must mean rows were actually cut — a list of exactly 10
  // payers is complete, not truncated.
  return { rows: ranked.slice(0, 10), truncated: ranked.length > 10 };
}

function performancePercent(value) {
  return value == null ? "—" : `${Math.round(value)}%`;
}

function renderPerformanceLensToggle() {
  return `<label class="performance-lens-toggle reports-no-print">
        <input type="checkbox" data-performance-young${reportPerformanceState.includeYoungClaims ? " checked" : ""}>
        <span>Include claims aging under 60 days</span>
      </label>`;
}

function renderPerformanceKpiHeader(totals, claimCount) {
  return `
  <section class="panel ar-report-panel performance-kpis">
    <div class="ar-report-head">
      <div>
        <h3>Performance</h3>
        <p>${formatNounCount(totals.claims, "claim")} of ${formatNounCount(claimCount, "filtered claim")} · pre-billing and client-action-pending claims excluded · service-date basis.</p>
      </div>
      <div class="ar-report-actions">
        ${renderPerformanceLensToggle()}
      </div>
    </div>
    <div class="ar-kpi-grid">
      ${reportExpectedKpiCard("Total Expected", totals.expectedKnownBasis, "Fee-schedule expected after the 30% underpayment rule", "", totals,
        "Awaiting expected values for this workspace")}
      ${arKpiCard("Insurance Collected", money(totals.paid), `${performancePercent(totals.collectionRate)} collection rate${reportRatioBasisSuffix(totals)}`, "primary")}
      ${reportExpectedKpiCard("Insurance AR", totals.insArKnownBasis, "Outstanding payer balance net of write-offs", "accent", totals,
        "Awaiting expected values — not $0")}
      ${arKpiCard("Patient Responsibility Still Open", money(totals.patResp), "Patient responsibility not yet collected, capped at each claim's open balance")}
      ${arKpiCard("Write-offs", money(totals.writeOffs), `${performancePercent(totals.expectedKnownBasis > 0 ? (totals.writeOffsOnKnown / totals.expectedKnownBasis) * 100 : null)} of expected${reportRatioBasisSuffix(totals)}`)}
      ${reportExpectedKpiCard("Resolution Rate", performancePercent(totals.resolutionRate), "Collected + write-offs + patient resp ÷ expected", "", totals,
        "Awaiting expected values — the collected, write-off and patient-responsibility figures below are complete")}
    </div>
    ${reportExpectedNoticeHtml(totals)}
    <p class="muted-note performance-methodology">Paid within 30% of expected counts as paid in full; overpayments don't inflate collections; write-offs and patient responsibility are capped at each claim's remaining balance.</p>
  </section>`;
}

function renderPerformanceMonthlyChart(monthRows) {
  const labels = monthRows.map((row) => collectionsMonthLabel(row.month));
  // renderChartCanvas's combo type draws grouped bars + a trailing line, not
  // stacked bars + a line, so Expected and Resolution % live in the compact
  // month table below the stack instead of bending the shared primitive.
  const chart = renderChartCanvas("stacked", labels, [
    { label: "Collected", valueType: "money", values: monthRows.map((row) => row.paid) },
    { label: "Patient Responsibility Still Open", valueType: "money", values: monthRows.map((row) => row.patResp) },
    { label: "Write-offs", valueType: "money", values: monthRows.map((row) => row.writeOffs) },
    { label: "Insurance AR", valueType: "money", values: monthRows.map((row) => row.insAr) }
  ]);
  const body = monthRows.map((row) => `
    <tr>
      <th scope="row" class="ar-drillable" data-performance-drill-month="${escapeHtml(row.month)}" role="button" tabindex="0">${escapeHtml(collectionsMonthLabel(row.month))}</th>
      <td class="num">${money(row.expected)}</td>
      <td class="num">${money(row.paid)}</td>
      <td class="num">${money(row.insAr)}</td>
      <td class="num">${performancePercent(row.resolutionRate)}</td>
    </tr>`).join("");
  return `
  <section class="panel ar-report-panel performance-monthly">
    <div class="ar-report-head">
      <div>
        <h3>Monthly Performance</h3>
        <p>Collected, patient responsibility, write-offs, and insurance AR stacked by service month.</p>
      </div>
    </div>
    <div class="chart-canvas">${chart}</div>
    <div class="table-wrap performance-month-wrap">
      <table class="client-table">
        <thead><tr><th scope="col">Month</th><th class="num">Expected</th><th class="num">Collected</th><th class="num">Insurance AR</th><th class="num">Resolution %</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </section>`;
}

function renderPerformanceTopPayers(payers) {
  const payerRows = payers.rows;
  if (!payerRows.length) {
    return `
  <section class="panel ar-report-panel performance-payers">
    <div class="ar-report-head">
      <div>
        <h3>Top Payers by Outstanding AR</h3>
        <p>No outstanding insurance AR after write-offs in the current lens — nothing left to chase.</p>
      </div>
    </div>
  </section>`;
  }
  // The shared chart primitive only draws vertical bars; a horizontal ranking
  // would need a new primitive, so the payers run left-to-right instead.
  const chart = renderChartCanvas("bar", payerRows.map((row) => row.payer), [
    // colorIndex 3 = --chart-4 (red): same color Insurance AR wears in the
    // monthly stacked chart above.
    { label: "Insurance AR", valueType: "money", colorIndex: 3, values: payerRows.map((row) => row.insAr) }
  ]);
  return `
  <section class="panel ar-report-panel performance-payers">
    <div class="ar-report-head">
      <div>
        <h3>Top Payers by Outstanding AR</h3>
        <p>Engine-computed insurance AR per payer, write-offs netted out${payers.truncated ? " · top 10 shown" : ""}.</p>
      </div>
    </div>
    <div class="chart-canvas">${chart}</div>
  </section>`;
}

function bindPerformanceControls() {
  const host = els.reportsBody;
  if (!host) return;
  // Report -> month -> claim -> service line, the CT-TRACKER path, over the same snapshot the
  // row above was computed from. bindReportRowDrilldown() is called centrally by renderReports()
  // once the drill is active, so nothing extra is bound here.
  host.querySelectorAll("[data-performance-drill-month]").forEach((el) => {
    const open = () => {
      const month = el.getAttribute("data-performance-drill-month") || "";
      // Em-dash values are absences, not filters, and narrating one reads as a dimension the
      // biller chose. Dropped from the LABEL only — the predicate still matches on whatever the
      // key holds. "Unknown" is deliberately NOT filtered: it is a real month bucket.
      const dims = [["Month", collectionsMonthLabel(month)]]
        .filter(([, value]) => value && value !== "—")
        .map(([name, value]) => `${name}: ${value}`);
      const label = dims.join(" · ");
      // MONEY. The drill footer sums RAW claimFinancialView; this row is the
      // performanceClaimMetrics RESTATEMENT — 30% underpayment rule, overpayment cap, write-offs
      // netted out. So the footer's "AR (Exp−Paid)" and this row's Insurance AR are two numbers
      // wearing one word, and the divergence here is arithmetic rather than a different grain.
      // On the Review Queue the same shape read as a 77% loss of money.
      //
      // Push the row's BASIS, not one figure. AR is not the only number that moves: Expected
      // diverges by exactly the same amount and sits EARLIER in the same sentence, unlabelled. A
      // caveat naming AR alone is worse than naming nothing — it reads as "we checked, and this
      // is the one that differs", certifying Expected as agreeing when it does not. Recomputed
      // from the live snapshot the way the CSV exports do, because the drill itself re-derives
      // from live state.
      //
      // STOPGAP. The durable fix is footerOverride plumbing through drillIntoReportRow, already
      // named in the ledger for the line-grain slice; then the footer states the row's own basis
      // and no caveat is needed. Kept as ONE separable dim so it deletes cleanly that day.
      const clicked = performanceMonthRows(performancePartition(getReportClaims(reportSourceClaims())).map(performanceClaimRow))
        .find((row) => row.month === month);
      if (clicked) dims.push(`Row restated — Expected ${money(clicked.expected)} · Insurance AR ${money(clicked.insAr)} (30% rule, write-offs netted)`);
      drillIntoReportRow({
        view: reportArState.view,
        label,
        dims,
        predicate: performanceDrillPredicate(month)
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
  const toggle = host.querySelector("[data-performance-young]");
  if (toggle) {
    toggle.addEventListener("change", () => {
      reportPerformanceState.includeYoungClaims = toggle.checked;
      renderReports();
    });
  }
}

// ===========================================================================
// CLIENT ACTION REPORT (2026-06-11) — port of the TrackerUpload "Client
// Action Pending" tab: revenue held up by client-side blockers (EVV,
// credentialing, authorization). Rows are the EXACT INVERSE of Performance's
// CAP exclusion via the shared isClientActionClaim predicate, with no aging
// lens — held claims of any age matter. Per-claim money reuses the
// performanceClaimMetrics engine so the two views agree on accounting.
// ===========================================================================
function clientActionPartition(claims) {
  return claims.filter(isClientActionClaim);
}

// The claims behind ONE "Held AR by Reason" row. The isClientActionClaim gate is the load-bearing
// half rather than a formality: clientActionReason() answers "On Hold" for ANY claim without a CAP
// action, so a reason-only match would drill the entire UNHELD book into the On Hold row — claims
// the partition excluded and the number never counted. Closes over the reason STRING, and the
// blocker whitelist is read live inside clientActionReason, so a newly configured CAP option moves
// the report and the drill together.
function clientActionDrillPredicate(reason) {
  return (claim) => isClientActionClaim(claim) && clientActionReason(claim) === reason;
}

// REASON: strip the "Client Action Pending" prefix and any leading separator
// from the action — the remainder is the blocker (EVV, Credentialing,
// Authorization). Bare CAP actions and status-only On Hold rows both fold
// into "On Hold" so the breakdown never shows an unlabeled bucket.
function clientActionPendingRemainder(action) {
  const text = String(action || "").trim();
  if (!/^client action pending/i.test(text)) return null;
  return text.slice("client action pending".length).replace(/^[\s\-–—:]+/, "").trim() || null;
}

// WHITELIST: free-text remainders ("call J. Smith's mom") would mint one
// chart series per typo, so a remainder only keeps its label when it matches
// a known blocker — the canonical TrackerUpload trio plus any CAP options
// configured in the follow-up action rules. Everything else folds to "Other".
function clientActionKnownReasons() {
  const known = new Set(["evv", "credentialing", "authorization"]);
  getActionRules().forEach((rule) => {
    const remainder = clientActionPendingRemainder(rule.action);
    if (remainder) known.add(remainder.toLowerCase());
  });
  return known;
}

function clientActionReason(claim) {
  const action = String(firstPresent(claim.action, claim.followUpAction, claim.follow_up_action) ?? "").trim();
  const remainder = clientActionPendingRemainder(action);
  if (remainder) return clientActionKnownReasons().has(remainder.toLowerCase()) ? remainder : "Other";
  return "On Hold";
}

function clientActionClaimRow(claim) {
  const fin = claimFinancialView(claim);
  return {
    month: claimMonth(claim),
    payer: claim.primaryPayer || claim.secondaryPayer || "—",
    reason: clientActionReason(claim),
    agingDays: arSubmissionAgingDays(claim),
    ...performanceClaimMetrics({
      expected: fin.expectedReimbursement,
      paid: fin.totalInsurancePaid,
      writeOff: fin.writeOffAmount,
      patResp: fin.patientResponsibility,
      insBalance: fin.expectedOutstanding
    }),
    expectedKnown: reportExpectedKnown(fin)
  };
}

function clientActionKpis(rows) {
  const totals = performanceKpis(rows);
  // Aging honesty: unbilled/unknown rows (aging null or <= 0) would drag the
  // mean toward zero, so only billed held claims enter it. Zero eligible
  // rows leaves the tile at "--".
  const aged = rows.filter((row) => Number.isFinite(row.agingDays) && row.agingDays > 0);
  totals.agedClaims = aged.length;
  totals.avgAgingDays = aged.length > 0 ? Math.round(aged.reduce((sum, row) => sum + row.agingDays, 0) / aged.length) : null;
  return totals;
}

function clientActionReasonRows(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    if (!groups.has(row.reason)) groups.set(row.reason, []);
    groups.get(row.reason).push(row);
  });
  return Array.from(groups.keys())
    .map((reason) => ({ reason, ...clientActionKpis(groups.get(reason)) }))
    .sort((a, b) => b.insAr - a.insAr);
}

// Monthly stack: per-claim NETTED held insurance AR — insArRaw minus the
// claim's own write-off (capped at its AR, clamped at zero).
//
// BASIS (changed 2026-08-03): this chart is ALL CLAIMS. The Insurance AR KPI
// tile above it is now the PRICED-CLAIM basis, so the two no longer agree when
// any claim is unrated — the tile carries the caption that says so, this chart
// deliberately keeps every claim because a held claim's AR is worth showing
// even when we cannot price it. Do not "restore" the old shared-basis claim.
// NOTE: per-claim netting can
// differ slightly from the KPI's aggregate netting when a claim's write-off
// exceeds its own AR (the excess nets other claims' AR in the aggregate but
// is clamped per claim here). Invariant: the month-stack total equals the
// sum of per-claim netted AR, is never negative, and never exceeds the raw
// AR total. Top 5 reasons by netted AR keep their own series; the rest fold
// into "Other".
function clientActionNettedAr(row) {
  return Math.max(0, row.insArRaw - Math.min(row.writeOff, row.insArRaw));
}

function clientActionMonthSeries(rows) {
  const reasonTotals = new Map();
  rows.forEach((row) => {
    reasonTotals.set(row.reason, (reasonTotals.get(row.reason) || 0) + clientActionNettedAr(row));
  });
  const topReasons = Array.from(reasonTotals.keys())
    .sort((a, b) => reasonTotals.get(b) - reasonTotals.get(a))
    .slice(0, 5);
  const topSet = new Set(topReasons);
  const folded = reasonTotals.size > topReasons.length;
  const months = Array.from(new Set(rows.map((row) => row.month))).sort((a, b) => a.localeCompare(b));
  const cells = new Map();
  rows.forEach((row) => {
    const key = `${row.month}|${topSet.has(row.reason) ? row.reason : "Other"}`;
    cells.set(key, (cells.get(key) || 0) + clientActionNettedAr(row));
  });
  const seriesKeys = folded ? [...topReasons, "Other"] : topReasons;
  return {
    months,
    folded,
    series: seriesKeys.map((reason) => ({
      label: reason,
      valueType: "money",
      values: months.map((month) => moneyNumber(cells.get(`${month}|${reason}`) || 0))
    }))
  };
}

function renderClientActionKpiHeader(totals, claimCount) {
  return `
  <section class="panel ar-report-panel performance-kpis clientaction-kpis">
    <div class="ar-report-head">
      <div>
        <h3>Client Action</h3>
        <p>${formatNounCount(totals.claims, "claim")} of ${formatNounCount(claimCount, "filtered claim")} held · service-date basis.</p>
      </div>
    </div>
    <div class="ar-kpi-grid">
      ${arKpiCard("Claims Held", totals.claims.toLocaleString(), "On hold or awaiting client action")}
      ${reportExpectedKpiCard("Expected (Held)", totals.expectedKnownBasis, "Revenue at risk while the blocker stands", (Number(totals.expectedKnownBasis) || 0) > 0 ? "primary" : "", totals,
        "Expected value not yet known — held claims still need action")}
      ${reportExpectedKpiCard("Insurance AR", totals.insArKnownBasis, "Held payer balance net of write-offs", (Number(totals.insArKnownBasis) || 0) > 0 ? "accent" : "", totals,
        "Awaiting expected values — not $0")}
      ${arKpiCard("Patient Responsibility Still Open", money(totals.patResp), "Patient responsibility not yet collected, capped at each claim's open balance")}
      ${arKpiCard("Avg Days Aging", totals.avgAgingDays == null ? "—" : formatNounCount(totals.avgAgingDays, "day"), `Across ${formatNounCount(totals.agedClaims, "billed held claim")}`)}
      ${arKpiCard("Write-offs", money(totals.writeOffs), "Written off while held")}
    </div>
    ${reportExpectedNoticeHtml(totals)}
    <p class="muted-note performance-methodology">Claims on hold or awaiting client action — excluded from Performance and Collections so held revenue never inflates results.</p>
  </section>`;
}

function renderClientActionMonthlyChart(monthData) {
  const labels = monthData.months.map((month) => collectionsMonthLabel(month));
  // A $0 stack draws an empty axis frame — say why in the panel's own voice.
  const total = monthData.series.reduce((sum, s) => sum + s.values.reduce((a, b) => a + b, 0), 0);
  const body = total > 0
    ? `<div class="chart-canvas">${renderChartCanvas("stacked", labels, monthData.series)}</div>`
    : '<p class="muted-note chart-empty">No held insurance balance in the current filters.</p>';
  return `
  <section class="panel ar-report-panel clientaction-monthly">
    <div class="ar-report-head">
      <div>
        <h3>Held AR by Month</h3>
        <p>Held insurance balance by service month, stacked by blocker — net of write-offs${monthData.folded ? " · top 5 reasons shown, the rest grouped as Other" : ""}.</p>
      </div>
    </div>
    ${body}
  </section>`;
}

function renderClientActionReasonBreakdown(reasonRows) {
  // A $0 bar chart draws an empty axis frame — swap in the panel's own
  // empty note; the summary table below always renders.
  const totalAr = reasonRows.reduce((sum, row) => sum + row.insAr, 0);
  const chartBlock = totalAr > 0
    ? `<div class="chart-canvas">${renderChartCanvas("bar", reasonRows.map((row) => row.reason), [
        { label: "Insurance AR", valueType: "money", values: reasonRows.map((row) => row.insAr) }
      ])}</div>`
    : '<p class="muted-note chart-empty">No held insurance balance in the current filters.</p>';
  const body = reasonRows.map((row) => `
    <tr>
      <th scope="row" class="ar-drillable" data-clientaction-drill-reason="${escapeHtml(row.reason)}" role="button" tabindex="0">${escapeHtml(row.reason)}</th>
      <td class="num">${row.claims.toLocaleString()}</td>
      <td class="num">${money(row.expected)}</td>
      <td class="num">${money(row.insAr)}</td>
      <td class="num">${row.avgAgingDays == null ? "—" : row.avgAgingDays.toLocaleString()}</td>
    </tr>`).join("");
  return `
  <section class="panel ar-report-panel clientaction-reasons">
    <div class="ar-report-head">
      <div>
        <h3>Held AR by Reason</h3>
        <p>Engine-computed insurance AR per blocker, write-offs netted out.</p>
      </div>
    </div>
    ${chartBlock}
    <div class="table-wrap clientaction-reason-wrap">
      <table class="client-table">
        <thead><tr><th scope="col">Reason</th><th class="num">Claims</th><th class="num">Expected</th><th class="num">Insurance AR</th><th class="num">Avg Days Aging</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </section>`;
}

function renderClientActionTopPayers(payers) {
  const payerRows = payers.rows;
  if (!payerRows.length) {
    return `
  <section class="panel ar-report-panel clientaction-payers">
    <div class="ar-report-head">
      <div>
        <h3>Top Payers Affected</h3>
        <p>No held insurance balance in the current filters.</p>
      </div>
    </div>
  </section>`;
  }
  // Neutral house colors — every series here is "held", so nothing earns the
  // red pin Performance gives outstanding AR.
  const chart = renderChartCanvas("bar", payerRows.map((row) => row.payer), [
    { label: "Insurance AR", valueType: "money", values: payerRows.map((row) => row.insAr) }
  ]);
  return `
  <section class="panel ar-report-panel clientaction-payers">
    <div class="ar-report-head">
      <div>
        <h3>Top Payers Affected</h3>
        <p>Engine-computed held insurance AR per payer, write-offs netted out${payers.truncated ? " · top 10 shown" : ""}.</p>
      </div>
    </div>
    <div class="chart-canvas">${chart}</div>
  </section>`;
}

// The Client Action view had no binder at all: its branch bound the switcher and returned, so a
// control on any of its four panels was inert by construction. Drill-only for now — the lens
// toggle and sortable headers other views carry belong to Performance and the detail tables.
//
// This COMPLEMENTS the by-claim worklist below rather than superseding it. That table is a flat,
// urgency-sorted list of every held claim; this is one blocker's claims and then their service
// lines, with the shared drill's profile toggles and Go-to-claim.
function bindClientActionControls() {
  const host = els.reportsBody;
  if (!host) return;
  host.querySelectorAll("[data-clientaction-detail-page]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const dir = btn.getAttribute("data-clientaction-detail-page");
      const next = dir === "prev" ? Number(reportClientActionState.detailPage || 1) - 1 : Number(reportClientActionState.detailPage || 1) + 1;
      // renderClientActionByClaimTable clamps to the real page count, so an out-of-range value
      // here is corrected on render rather than needing the count duplicated.
      reportClientActionState.detailPage = Math.max(1, next);
      renderReports();
    });
  });
  host.querySelectorAll("[data-clientaction-drill-reason]").forEach((el) => {
    const open = () => {
      const reason = el.getAttribute("data-clientaction-drill-reason") || "";
      // Em-dash values are absences rather than filters — dropped from the LABEL only, so the
      // predicate still matches whatever the key holds. Reasons are named buckets today
      // ("On Hold" / "Other"), so this is a contract, not a live condition.
      const dims = [["Reason", reason]]
        .filter(([, value]) => value && value !== "—")
        .map(([name, value]) => `${name}: ${value}`);
      const label = dims.join(" · ");
      // MONEY, same divergence as Performance and for the same reason: the drill footer sums RAW
      // claimFinancialView while this row is the performanceClaimMetrics restatement. The panel is
      // titled "Held AR by Reason" and the footer says "AR (Exp−Paid)" — two different numbers,
      // one word.
      //
      // Push the row's BASIS, not one figure, for the reason spelled out at the Performance
      // binder: Expected diverges by the identical amount and sits earlier in the same sentence,
      // so naming AR alone certifies Expected as agreeing. Recomputed from the live snapshot,
      // never read back off the attribute. STOPGAP pending footerOverride plumbing through
      // drillIntoReportRow — one separable dim, so it deletes cleanly when that lands.
      const clicked = clientActionReasonRows(clientActionPartition(getReportClaims(reportSourceClaims())).map(clientActionClaimRow))
        .find((row) => row.reason === reason);
      if (clicked) dims.push(`Row restated — Expected ${money(clicked.expected)} · Insurance AR ${money(clicked.insAr)} (30% rule, write-offs netted)`);
      drillIntoReportRow({
        view: reportArState.view,
        label,
        dims,
        predicate: clientActionDrillPredicate(reason)
      });
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); }
    });
  });
}

// CT-TRACKER build_client_action_pending_report (2026-07-20): one actionable
// row per held claim, so billers can work the exact claims behind the Client
// Action KPI/chart aggregates. Reuses claimFinancialView for money (Remaining
// = Expected - Paid via expectedOutstanding), the shared clientActionReason
// blocker label, and arSubmissionAgingDays for the same bill-date aging lens
// the KPIs report. Enriches the existing view — it is not a new switcher tab.
function clientActionByClaimRow(claim) {
  const fin = claimFinancialView(claim);
  const services = Array.isArray(claim.services) ? claim.services : [];
  const dosStamps = services.map((service) => serviceDosValue(service)).filter(Boolean);
  const dosSorted = dosStamps.slice().sort((a, b) => (arParseDate(a) || 0) - (arParseDate(b) || 0));
  const units = services.reduce((sum, service) => sum + quantityNumber(firstPresent(service.units, service.Units, 0)), 0);
  const payers = [claim.primaryPayer, claim.secondaryPayer].map((payer) => String(payer || "").trim()).filter(Boolean);
  const followUp = claim.followUpDate || "";
  const followUpStamp = arParseDate(followUp);
  const agingDays = arSubmissionAgingDays(claim);
  return {
    claimId: String(claim.claimId || ""),
    name: String(claim.patient || "—"),
    firstDos: dosSorted[0] || "",
    lastDos: dosSorted[dosSorted.length - 1] || "",
    agingDays: Number.isFinite(agingDays) ? agingDays : null,
    reason: clientActionReason(claim),
    lines: services.length,
    units: moneyNumber(units),
    expected: fin.expectedReimbursement,
    paid: fin.totalInsurancePaid,
    remaining: fin.expectedOutstanding,
    payers: payers.length ? Array.from(new Set(payers)).join(" / ") : "—",
    followUp,
    followUpStamp: Number.isFinite(followUpStamp) ? followUpStamp : null
  };
}

// Sort: earliest follow-up ascending (blank dates last), then oldest bill-date
// aging first, then claim id — surfaces the most time-sensitive held claims at
// the top of the worklist. Renders nothing when no claims are held.
function renderClientActionByClaimTable(claims) {
  const rows = clientActionPartition(claims).map(clientActionByClaimRow);
  if (!rows.length) return "";
  rows.sort((a, b) => {
    if (a.followUpStamp !== b.followUpStamp) {
      if (a.followUpStamp == null) return 1;
      if (b.followUpStamp == null) return -1;
      return a.followUpStamp - b.followUpStamp;
    }
    const aAging = a.agingDays == null ? -Infinity : a.agingDays;
    const bAging = b.agingDays == null ? -Infinity : b.agingDays;
    if (aAging !== bAging) return bAging - aAging;
    return a.claimId.localeCompare(b.claimId);
  });
  // DISPLAY WINDOW ONLY — the renderArDetail pattern. The sort above runs over the FULL row set
  // (so page 1 is genuinely the earliest follow-up in the whole book, not the earliest of a
  // truncated slice), and every KPI, chart and reason breakdown on this view is computed from
  // the full partition elsewhere. Only the render is windowed. The clamp corrects a stale page
  // instead of rendering an empty table.
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / AR_DETAIL_PAGE_SIZE));
  const pageNumber = Math.min(Math.max(1, Number(reportClientActionState.detailPage) || 1), pageCount);
  reportClientActionState.detailPage = pageNumber;
  const startIndex = (pageNumber - 1) * AR_DETAIL_PAGE_SIZE;
  const visible = rows.slice(startIndex, startIndex + AR_DETAIL_PAGE_SIZE);
  const rangeNote = pageCount > 1 ? ` · showing ${startIndex + 1}–${startIndex + visible.length}` : "";
  const body = visible.map((row) => `
    <tr>
      <th scope="row">${escapeHtml(row.claimId)}</th>
      <td>${escapeHtml(row.name)}</td>
      <td>${escapeHtml(row.firstDos ? formatDate(row.firstDos) : "—")}</td>
      <td>${escapeHtml(row.lastDos ? formatDate(row.lastDos) : "—")}</td>
      <td class="num">${row.agingDays == null ? "—" : row.agingDays.toLocaleString()}</td>
      <td>${escapeHtml(row.reason)}</td>
      <td class="num">${row.lines.toLocaleString()}</td>
      <td class="num">${row.units.toLocaleString()}</td>
      <td class="num">${money(row.expected)}</td>
      <td class="num">${money(row.paid)}</td>
      <td class="num">${money(row.remaining)}</td>
      <td>${escapeHtml(row.payers)}</td>
      <td>${escapeHtml(row.followUp ? formatDate(row.followUp) : "—")}</td>
    </tr>`).join("");
  return `
  <section class="panel ar-report-panel clientaction-by-claim">
    <div class="ar-report-head">
      <div>
        <h4>Client Action — by Claim</h4>
        <p>${formatNounCount(total, "held claim")}${rangeNote} · one actionable row per claim, earliest follow-up first.</p>
      </div>
    </div>
    <div class="table-wrap clientaction-by-claim-wrap">
      <table class="client-table">
        <thead><tr>
          <th scope="col">Claim ID</th>
          <th scope="col">Name</th>
          <th scope="col">First DOS</th>
          <th scope="col">Last DOS</th>
          <th class="num">Max Bill Aging (days)</th>
          <th scope="col">Client Action Types</th>
          <th class="num">Lines</th>
          <th class="num">Units</th>
          <th class="num">Insurance Expected</th>
          <th class="num">Total Paid</th>
          <th class="num">Remaining Balance</th>
          <th scope="col">Payers</th>
          <th scope="col">Earliest Follow-Up</th>
        </tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>${pageCount > 1 ? `
    <div class="ar-detail-pager reports-no-print">
      <button type="button" class="ghost-button" data-clientaction-detail-page="prev"${pageNumber <= 1 ? " disabled" : ""} aria-label="Previous page of client-action claims">‹ Prev</button>
      <span class="ar-detail-pager-status">Page ${pageNumber} of ${pageCount} · ${formatNounCount(total, "held claim")} total</span>
      <button type="button" class="ghost-button" data-clientaction-detail-page="next"${pageNumber >= pageCount ? " disabled" : ""} aria-label="Next page of client-action claims">Next ›</button>
    </div>` : ""}
  </section>`;
}

function normalizedContractText(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function compactContractText(value) {
  return normalizedContractText(value).replace(/\s+/g, "");
}

function feePayerMatches(claimPayer, feePayer, claimPayerId = "", feePayerId = "") {
  const claimId = String(claimPayerId || "").trim().replace(/\.0+$/, "").toUpperCase();
  const feeId = String(feePayerId || "").trim().replace(/\.0+$/, "").toUpperCase();
  if (claimId && feeId && claimId === feeId) return true;
  const claimText = normalizedContractText(claimPayer);
  const feeText = normalizedContractText(feePayer);
  if (!claimText || !feeText) return false;
  if (claimText === feeText) return true;
  const claimCompact = compactContractText(claimText);
  const feeCompact = compactContractText(feeText);
  return claimCompact.includes(feeCompact) || feeCompact.includes(claimCompact);
}

function feeValueMatches(actual, configured) {
  const configuredText = normalizedContractText(configured);
  if (!configuredText) return true;
  return normalizedContractText(actual) === configuredText;
}

function contractProcedureCode(value) {
  const text = String(value || "").toUpperCase();
  const match = text.match(/\b[A-Z]\d{4}\b|\b\d{5}(?:\s*[A-Z]{1,2})?\b/);
  return match ? match[0].replace(/\s+/g, "") : "";
}

function feeProcedureMatches(actual, configured) {
  const configuredText = normalizedContractText(configured);
  if (!configuredText) return true;
  const actualText = normalizedContractText(actual);
  if (actualText === configuredText) return true;
  const actualCode = contractProcedureCode(actual);
  const configuredCode = contractProcedureCode(configured);
  return !!actualCode && !!configuredCode && actualCode === configuredCode;
}

function contractPlaceCode(value) {
  const match = String(value || "").match(/\b\d{1,2}\b/);
  return match ? match[0].padStart(2, "0") : "";
}

// Canonical POS comparison key for the fee schedule chain. Strips leading
// zeros + trailing ".0" so live Synapse rows (`"10.0"`, `"2.0"`) match
// service lines (`"10"`, `"02"`, `"2"`). Mirrors backend canonicalPlaceCode.
function canonicalPlaceCode(value) {
  const text = String(value == null ? "" : value).trim();
  if (!text) return "";
  const m = text.match(/^0*(\d{1,3})(?:\.0+)?$/);
  if (m) return m[1];
  return contractPlaceCode(text) || normalizedContractText(text);
}

function feePlaceMatches(actual, configured) {
  const configuredText = normalizedContractText(configured);
  if (!configuredText) return true;
  if (normalizedContractText(actual) === configuredText) return true;
  const actualPlace = contractPlaceCode(actual);
  const configuredPlace = contractPlaceCode(configured);
  return !!actualPlace && !!configuredPlace && actualPlace === configuredPlace;
}

function feeScheduleSpecificity(row = {}) {
  return ["procedure", "m1", "m2", "place", "state", "network"].reduce((score, key) => score + (normalizedContractText(row[key]) ? 1 : 0), 0);
}

// Mirrors backend/feeScheduleRates.js#resolveFeeScheduleRate. Walks the Rate
// Match Method chains from spec lines 191-196 instead of a single
// specificity sort, so a CPT-only row can't outrank a modifier-only row
// just because it happened to match more peripheral fields.
// ONE definition of every input the match reads, shared by the memo key and by the ladder
// itself, so the cache and the thing it caches cannot drift apart. Both are the exact
// expressions bestFeeScheduleMatch ran inline before the memo landed — no normalization
// added, no order changed.
function feeScheduleMatchPayerId(service = {}) {
  return service.payerId
    || service.payorId
    || service.primaryPayerId
    || service.primaryPayorId
    || service.primaryExternalId
    || service.primaryPayerExternalId
    || service.secondaryPayerId
    || service.secondaryPayorId
    || service.secondaryExternalId
    || service.secondaryPayerExternalId
    || "";
}

function feeScheduleMatchOrgId(service = {}) {
  return String(firstPresent(service.orgId, service.org_id, service.OrgId) || "").trim();
}

// MEMO, NOT A RE-IMPLEMENTATION (2026-08-11). bestFeeScheduleMatchUncached filters the WHOLE
// fee-schedule array seven times and then walks up to six ordered match steps, and
// trackerServiceFinancials calls it TWICE per service line (primary + secondary payer) over
// tens of thousands of lines — measured as the dominant term in a multi-second main-thread
// freeze. A cache hit returns the very row the ladder would have returned; the ladder is
// governed by FEE_SCHEDULE_PARITY.md and is not reordered, simplified or second-guessed here.
//
// The cache is keyed on the feeRows ARRAY OBJECT, so every `state.feeScheduleRows = [...]`
// reassignment invalidates it by identity. But fee rows are also mutated IN PLACE — the inline
// editor does `state.feeScheduleRows[index] = { ...state.feeScheduleRows[index], ... }` and the
// restore path flips `row.status` — and neither changes array identity, which would leave this
// memo pricing a line off an edited-away rate. That is what the epoch is for: every fee-row
// write calls bumpFeeScheduleMatchEpoch(), and the epoch is part of the key.
let feeScheduleMatchCache = new WeakMap();
// Memo cap. Measured 2026-08-11: the full 60,000-line report population produces ~81,496
// distinct cache keys, so 150,000 leaves ~1.8x headroom over the worst case actually observed.
// Below ~85,000 the warm (re-render) path silently degrades without saying so: at a 50,000 cap
// that same population cached only 67.5% of its keys and the re-render speedup fell to 3.85x,
// against 667x for a 25,000-line population (40,633 keys, fully cached). Scale was never the
// problem; the cap was.
//
// THE POLICY IS "STOP INSERTING WHEN FULL". DO NOT "IMPROVE" THIS INTO AN LRU. renderReports()
// walks the row array in order, which is the textbook worst case for an LRU whose capacity is
// under the working set: every access evicts the entry the next pass is about to need and the
// hit rate collapses toward 0%. Stop-inserting keeps a stable prefix of keys cached instead and
// degrades gracefully - the measured 67.5% hit rate above came from caching only 61% of them.
//
// This is a memory/latency tradeoff, NOT money math. Past the cap bestFeeScheduleMatch calls
// through to bestFeeScheduleMatchUncached and returns the identical row, so the cap can never
// change which rate prices a line - only how often the ladder is re-walked.
//
// The constraint is the user's BROWSER TAB, not an API worker; this memo never runs in one.
// For scale: the Reports page already holds up to 60,000 row objects, and a memo entry is a key
// string plus a SHARED REFERENCE to a fee row that is already resident - the ladder returns an
// element of a .filter() chain over feeRows, so no row is ever copied. The memo is a small
// fraction of what the tab is already carrying.
const FEE_SCHEDULE_MATCH_CACHE_LIMIT = 150000;
// U+0001. fromCharCode rather than an escape sequence on purpose: a splice that wrote a
// backslash escape into this file as a raw control character once fail-closed an entire lane.
const FEE_SCHEDULE_MATCH_KEY_SEP = String.fromCharCode(1);
let feeScheduleMatchEpoch = 0;

function bumpFeeScheduleMatchEpoch() {
  feeScheduleMatchEpoch += 1;
  // AND DROP THE OLD MAP. The epoch alone made stale entries unreachable but never removed
  // them, and they still counted against FEE_SCHEDULE_MATCH_CACHE_LIMIT: measured 2026-08-11,
  // 500 keys -> bump -> 500 more left cache.size === 1000, all but 500 of them dead. A session
  // that renders the full report (~81,496 keys), edits one rate and re-renders would therefore
  // cross 150,000 and silently stop caching the TAIL of the second pass - exactly the quiet
  // degradation the cap was raised to eliminate. Reassigning is O(1) and hands every orphaned
  // entry to the collector at once.
  //
  // The epoch STAYS IN THE KEY. It is now belt-and-braces rather than the sole defence against
  // an in-place fee-row edit, and defence in depth on a money path is worth a few bytes per key.
  feeScheduleMatchCache = new WeakMap();
  // Fee rows are an input to every derived money view (serviceFinancialView,
  // claimFinancialView). resetDerivedCaches() only clears when the CLAIMS
  // changed, so a saved rate left the grid and drawer on the old expected until
  // the next data load (measured 2026-09-02: 8 units x a freshly saved $72.25
  // rate still read the pre-save $565). Drop them here, where the rate changed.
  derivedClaimCache = new WeakMap();
  derivedServiceCache = new WeakMap();
}

// Every field the ladder reads, in one place. The separator cannot occur in a payer name, a
// CPT, a modifier, a POS or a date, so two different services cannot collide on one key.
//
// feeRows.length is in the key as a THIRD invalidation signal, beside array identity and the
// epoch: a caller that grows or shrinks the array in place (push / pop / splice) changes
// neither of the other two, and the portal verifier does exactly that — pushes a covering rate
// and re-asks whether the payer is now configured. Length catches it without the caller having
// to know the memo exists. (A pure reorder of a fixed-length array would still need an epoch
// bump; nothing in app.js reorders fee rows in place.)
function feeScheduleMatchCacheKey(claimPayer, service = {}, feeRows = []) {
  return [
    feeScheduleMatchEpoch,
    Array.isArray(feeRows) ? feeRows.length : 0,
    claimPayer,
    feeScheduleMatchPayerId(service),
    feeScheduleMatchOrgId(service),
    service.procedure,
    service.state,
    service.dos,
    service.network,
    service.m1,
    service.m2,
    service.place
  ].map((part) => String(part == null ? "" : part)).join(FEE_SCHEDULE_MATCH_KEY_SEP);
}

function bestFeeScheduleMatch(claimPayer, service = {}, feeRows = []) {
  if (!Array.isArray(feeRows)) return bestFeeScheduleMatchUncached(claimPayer, service, feeRows);
  let cache = feeScheduleMatchCache.get(feeRows);
  if (!cache) {
    cache = new Map();
    feeScheduleMatchCache.set(feeRows, cache);
  }
  const key = feeScheduleMatchCacheKey(claimPayer, service, feeRows);
  if (cache.has(key)) return cache.get(key);
  const match = bestFeeScheduleMatchUncached(claimPayer, service, feeRows);
  // Bounded on purpose: a book with more distinct keys than the cap stops memoizing rather than
  // growing without limit in the user's browser tab. Stop-inserting, never eviction - see the
  // note on FEE_SCHEDULE_MATCH_CACHE_LIMIT before touching this. Correctness is unaffected — an
  // uncached call is still a full ladder walk returning the very same row.
  if (cache.size < FEE_SCHEDULE_MATCH_CACHE_LIMIT) cache.set(key, match);
  return match;
}

function bestFeeScheduleMatchUncached(claimPayer, service = {}, feeRows = []) {
  const claimPayerId = feeScheduleMatchPayerId(service);
  const serviceOrgId = feeScheduleMatchOrgId(service);
  const candidates = (Array.isArray(feeRows) ? feeRows : [])
    // Archived rates are retired: they must never feed expected-reimbursement
    // matching, even when loaded into state.feeScheduleRows by the "Show archived" view.
    .filter((row) => String(row.status || "active") !== "archived")
    .filter((row) => moneyNumber(row.rate || 0) > 0)
    .filter((row) => {
      const rowOrgId = String(firstPresent(row.orgId, row.org_id, row.OrgId) || "").trim();
      return !serviceOrgId || rowOrgId === serviceOrgId;
    })
    .filter((row) => feePayerMatches(claimPayer, row.payer, claimPayerId, row.payorId || row.payerId))
    .filter((row) => feeProcedureMatches(service.procedure, row.procedure))
    .filter((row) => feeStateCompatible(service.state, row.state || row.State))
    .filter((row) => feeDateMatches(service.dos, row.startDate || row["Start Date"] || row.actualStartDate || row.ActualStartDate, row.endDate || row["End Date"] || row.actualEndDate || row.ActualEndDate))
    .filter((row) => feeNetworkRowMatches(service.network, row.network || row["In/Out of Network"]));
  if (!candidates.length) return null;
  const serviceM1 = normalizedContractText(service.m1);
  const serviceM2 = normalizedContractText(service.m2);
  // POS uses canonicalPlaceCode so live Synapse "10.0" matches service "10" / "02".
  const servicePlaceCode = canonicalPlaceCode(service.place);
  const hasServiceModifiers = Boolean(serviceM1 || serviceM2);
  const hasModifierPriority = candidates.some((row) => /modifier\s*priority/i.test(row.rateMatchMethod || row["Rate Match Method"] || ""));
  // DELIBERATELY SHORT: the backend chain also has GA-Medicaid/MCO recovery steps (POS-only, CPT-only) that exist only for Georgia; they must never be copied here without its gaRow + explicitStateRow guards, or a blank-state row would price a GA line.
  const steps = hasModifierPriority
    ? (hasServiceModifiers
      ? [
        (r) => normalizedContractText(r.m1) === serviceM1 && normalizedContractText(r.m2) === serviceM2 && !!normalizedContractText(r.m1),
        (r) => normalizedContractText(r.m1) === serviceM1 && (!normalizedContractText(r.m2) || normalizedContractText(r.m2) === serviceM2)
      ]
      : [
        (r) => !normalizedContractText(r.m1) && !normalizedContractText(r.m2) && canonicalPlaceCode(r.place) === servicePlaceCode && !!servicePlaceCode,
        (r) => !normalizedContractText(r.m1) && !normalizedContractText(r.m2) && !normalizedContractText(r.place)
      ])
    : [
      (r) => normalizedContractText(r.m1) === serviceM1 && normalizedContractText(r.m2) === serviceM2 && !!normalizedContractText(r.m1) && canonicalPlaceCode(r.place) === servicePlaceCode && !!servicePlaceCode,
      (r) => normalizedContractText(r.m1) === serviceM1 && normalizedContractText(r.m2) === serviceM2 && !!normalizedContractText(r.m1) && !normalizedContractText(r.place),
      (r) => normalizedContractText(r.m1) === serviceM1 && !!normalizedContractText(r.m1) && !normalizedContractText(r.m2) && canonicalPlaceCode(r.place) === servicePlaceCode && !!servicePlaceCode,
      (r) => normalizedContractText(r.m1) === serviceM1 && !!normalizedContractText(r.m1) && !normalizedContractText(r.m2) && !normalizedContractText(r.place),
      (r) => !normalizedContractText(r.m1) && !normalizedContractText(r.m2) && canonicalPlaceCode(r.place) === servicePlaceCode && !!servicePlaceCode,
      (r) => !normalizedContractText(r.m1) && !normalizedContractText(r.m2) && !normalizedContractText(r.place)
    ];
  // STATE PREFERENCE IS DECIDED BEFORE MODIFIER/PLACE SPECIFICITY.
  //
  // The SQL orders `(FeeState IS NOT NULL) DESC` AHEAD of `FeeRank DESC`, so an
  // exact-state contract row beats a blank-state one even when the blank row is
  // the more specific match. One pass over the merged pool cannot express that -
  // it would let a blank-state "exact modifier pair" row win over an exact-state
  // "CPT only" row. So the WHOLE step chain is walked over the exact-state pool
  // first, and the blank-state (wildcard) pool is consulted only when nothing in
  // the exact-state pool matched any step.
  //
  // Every candidate is one or the other: the state gate above admits a row only
  // when its state equals the line's classification or is blank.
  // Mirrors backend/feeScheduleRates.js#resolveFeeScheduleChain.
  const exactStatePool = candidates.filter((row) => feeStateMatches(service.state, row.state || row.State));
  const blankStatePool = candidates.filter((row) => !normalizedContractText(row.state || row.State));
  for (const pool of [exactStatePool, blankStatePool]) {
    if (!pool.length) continue;
    for (const step of steps) {
      const matches = pool.filter(step);
      if (matches.length) {
        matches.sort((a, b) => feeScheduleSpecificity(b) - feeScheduleSpecificity(a));
        return matches[0];
      }
    }
  }
  return null;
}

// In/Out of Network match: blank on either side is permissive; tolerates the
// usual INN / OON / "In Network" / "Out of Network" spelling variants.
function feeNetworkRowMatches(actual, configured) {
  const configuredText = normalizedContractText(configured);
  if (!configuredText) return true;
  const actualText = normalizedContractText(actual);
  if (!actualText) return true;
  if (actualText === configuredText) return true;
  const innConfigured = /^(in|inn|in\s*network|in\s*net)$/.test(configuredText);
  const innActual = /^(in|inn|in\s*network|in\s*net)$/.test(actualText);
  const oonConfigured = /^(out|oon|out\s*of\s*network|out\s*net|oo\s*n)$/.test(configuredText);
  const oonActual = /^(out|oon|out\s*of\s*network|out\s*net|oo\s*n)$/.test(actualText);
  return (innConfigured && innActual) || (oonConfigured && oonActual);
}

// EXACT state equality. Kept as its own predicate because the ranking needs to
// tell an exact-state row from a wildcard one, not only whether a row is
// admissible. Mirrors backend/feeScheduleRates.js#feeStateMatches.
function feeStateMatches(actual, configured) {
  const configuredText = normalizedContractText(configured);
  const actualText = normalizedContractText(actual);
  return Boolean(actualText && configuredText && actualText === configuredText);
}

// ADMISSIBILITY. Nathan's 2026-09-17 policy, which is what Max's Claims Insight
// does: a contract row whose State is BLANK applies to any service line that HAS
// a patient classification. A line with a blank or missing classification still
// matches nothing - that half of the 2026-09-15 mandatory-classification ruling
// stands, so this deliberately does NOT treat a blank ACTUAL as permissive the
// way feeNetworkRowMatches treats its blanks.
//
// Admissibility is not precedence: everywhere this widens the pool, the caller
// must still prefer an exact-state row (feeStateMatches) over a blank-state one.
// Mirrors backend/feeScheduleRates.js#feeStateCompatible.
function feeStateCompatible(actual, configured) {
  const actualText = normalizedContractText(actual);
  if (!actualText) return false;
  const configuredText = normalizedContractText(configured);
  if (!configuredText) return true;
  return actualText === configuredText;
}

// DOS range match: startDate <= DOS <= endDate, blank dates open-ended. A
// service without a parseable DOS only matches fully undated fee rows.
// Mirrors backend/feeScheduleRates.js#feeDateMatches.
function feeDateMatches(dos, startDate, endDate) {
  const serviceTime = feeDateTime(dos);
  if (!serviceTime) return !feeDateTime(startDate) && !feeDateTime(endDate);
  const start = feeDateTime(startDate);
  const end = feeDateTime(endDate);
  if (start && serviceTime < start) return false;
  if (end && serviceTime > end) return false;
  return true;
}

function feeDateTime(value) {
  if (!value) return null;
  const time = Date.parse(String(value));
  return Number.isFinite(time) ? time : null;
}

function contractVarianceRows(claims = getClaims(), feeRows = state.feeScheduleRows || []) {
  const groups = new Map();
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    const payer = claim.primaryPayer || claim.secondaryPayer || "Unknown payer";
    (claim.services || []).forEach((service) => {
      const financial = serviceFinancialView(service, claim);
      const units = quantityNumber(service.units);
      const expected = moneyNumber(financial.expectedReimbursement);
      const paid = moneyNumber(financial.insurancePaid);
      const match = bestFeeScheduleMatch(payer, {
        ...service,
        state: firstPresent(service.state, claim.state),
        orgId: firstPresent(service.orgId, service.org_id, claim.orgId, claim.org_id),
        payerId: payerExternalIdForClaimPayer(claim, payer)
      }, feeRows);
      const contractRate = moneyNumber(match?.rate || 0);
      const contractExpected = units > 0 && contractRate > 0 ? moneyNumber(units * contractRate) : 0;
      const variance = contractExpected ? moneyNumber(expected - contractExpected) : 0;
      const key = [
        compactContractText(payer) || "unknown",
        compactContractText(service.procedure) || "unknown",
        compactContractText(service.m1),
        compactContractText(service.m2),
        compactContractText(service.place)
      ].join("|");
      if (!groups.has(key)) {
        groups.set(key, {
          payer,
          procedure: service.procedure || "Unknown procedure",
          modifiers: [service.m1, service.m2].filter(Boolean).join(" / ") || "None",
          place: service.place || "Any",
          services: 0,
          claims: new Set(),
          units: 0,
          expected: 0,
          paid: 0,
          contractExpected: 0,
          variance: 0,
          underpayment: 0,
          missingRates: 0,
          guarded: 0,
          issue: "Aligned"
        });
      }
      const group = groups.get(key);
      group.services += 1;
      group.claims.add(claim.claimId);
      group.units += units;
      group.expected += expected;
      group.paid += paid;
      group.contractExpected += contractExpected;
      group.variance += variance;
      group.underpayment += financial.underpayment;
      if (!contractRate || !match || expected <= 0) group.missingRates += 1;
      if (hasCalculationGuards(claim)) group.guarded += 1;
    });
  });
  return Array.from(groups.values()).map((row) => {
    const absoluteVariance = Math.abs(row.variance);
    let issue = "Aligned";
    if (row.missingRates) issue = "Missing / unmatched contract rate";
    else if (absoluteVariance >= 25) issue = "Expected differs from contract";
    else if (row.underpayment > 0) issue = "Paid below expected";
    return {
      ...row,
      claims: row.claims.size,
      units: Number(row.units.toFixed(2)),
      expected: moneyNumber(row.expected),
      paid: moneyNumber(row.paid),
      contractExpected: moneyNumber(row.contractExpected),
      variance: moneyNumber(row.variance),
      underpayment: moneyNumber(row.underpayment),
      issue
    };
  }).sort((a, b) => b.missingRates - a.missingRates || Math.abs(b.variance) - Math.abs(a.variance) || b.underpayment - a.underpayment || b.expected - a.expected);
}

function contractVarianceSummary(rows = []) {
  return rows.reduce((sum, row) => {
    sum.expected += row.expected;
    sum.paid += row.paid;
    sum.contractDelta += Math.abs(row.variance);
    sum.underpayment += row.underpayment;
    sum.missingRateRows += row.missingRates;
    sum.guardedRows += row.guarded;
    return sum;
  }, { expected: 0, paid: 0, contractDelta: 0, underpayment: 0, missingRateRows: 0, guardedRows: 0 });
}

function renderContractVariancePanel(claims = getClaims(), feeRows = state.feeScheduleRows || [], prepared = null) {
  if (!els.contractVariancePanel) return;
  const rows = prepared?.rows || contractVarianceRows(claims, feeRows);
  const summary = prepared?.summary || contractVarianceSummary(rows);
  const topRows = rows.slice(0, 6);
  els.contractVariancePanel.innerHTML = `
    <div class="contract-variance-head">
      <div>
        <span>Workbook Control</span>
        <strong>Contract Variance Review</strong>
        <p>Compares fee schedule rates against expected reimbursement, paid cash, and missing-rate exposure by payer/procedure before AR math is trusted.</p>
      </div>
      <div>
        <span>Formula</span>
        <code>=Expected - (Units x Contract Rate)</code>
        <small>No patient names; grouped billing math only.</small>
      </div>
    </div>
    <div class="contract-variance-grid">
      ${renderContractVarianceCard("Missing Rate Exposure", summary.missingRateRows, "Service rows without a usable contract match")}
      ${renderContractVarianceCard("Contract Delta", feeReviewMoney(summary.contractDelta), "Absolute expected-vs-contract variance")}
      ${renderContractVarianceCard("Underpayment", feeReviewMoney(summary.underpayment), "Paid below expected payer responsibility")}
      ${renderContractVarianceCard("Guarded Rate Rows", summary.guardedRows, "Rows with calculation or payer sequence guardrails")}
    </div>
    <div class="contract-variance-table-wrap">
      <table class="contract-variance-table">
        <thead>
          <tr>
            <th>Payer / Procedure</th>
            <th>Claims</th>
            <th>Units</th>
            <th>Expected</th>
            <th>Paid</th>
            <th>Contract Expected</th>
            <th>Variance</th>
            <th>Issue</th>
          </tr>
        </thead>
        <tbody>
          ${topRows.length ? topRows.map(renderContractVarianceRow).join("") : `
            <tr><td colspan="8"><span class="contract-variance-empty">No services available for contract variance review.</span></td></tr>
          `}
        </tbody>
      </table>
    </div>
  `;
}

function renderContractVarianceCard(label, value, detail) {
  return `
    <article class="contract-variance-card">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(String(value))}</strong>
      <small>${escapeHtml(detail)}</small>
    </article>
  `;
}

function renderContractVarianceRow(row) {
  const varianceClass = row.missingRates ? "contract-variance-issue--red" : Math.abs(row.variance) >= 25 ? "contract-variance-issue--amber" : "contract-variance-issue--green";
  return `
    <tr>
      <td>
        <strong>${escapeHtml(row.payer)}</strong>
        <small>${escapeHtml(row.procedure)} · ${escapeHtml(row.modifiers)} · POS ${escapeHtml(row.place)}</small>
      </td>
      <td>${escapeHtml(String(row.claims))}</td>
      <td>${escapeHtml(row.units == null ? "Unavailable" : String(row.units))}</td>
      <td>${escapeHtml(feeReviewMoney(row.expected))}</td>
      <td>${escapeHtml(feeReviewMoney(row.paid))}</td>
      <td>${row.contractExpected ? escapeHtml(feeReviewMoney(row.contractExpected)) : "<span>Missing</span>"}</td>
      <td>${escapeHtml(feeReviewMoney(row.variance))}</td>
      <td><span class="contract-variance-issue ${varianceClass}">${escapeHtml(row.issue)}</span></td>
    </tr>
  `;
}

function renderPayerRulesReadinessPanel(claims = getClaims(), feeRows = state.feeScheduleRows || [], prepared = null) {
  if (!els.payerRulesReadinessPanel) return;
  const model = prepared || payerRulesReadinessModel(claims, feeRows, getActionRules());
  els.payerRulesReadinessPanel.innerHTML = `
    <section class="payer-rules-head">
      <div>
        <span>RCM Control Table</span>
        <strong>Payer Rules Readiness</strong>
        <p>Shows whether payer-specific contract rates, filing windows, auth evidence, and denial/follow-up routes are configured before operators trust AR, appeals, or rebilling decisions.</p>
      </div>
      <div>
        <span>Readiness Math</span>
        <strong>${model.readyPayers}/${formatNounCount(model.payers, "payer")} ready</strong>
        <small>${feeReviewMoney(model.openAr)} open AR · ${formatNounCount(model.policyGaps, "policy gap")} · delta ${feeReviewMoney(model.delta)}</small>
      </div>
    </section>
    <div class="payer-rules-summary">
      <div><span>Mapped Contracts</span><strong>${model.contractReady}</strong><small>All active service rows matched</small></div>
      <div><span>Filing Rules</span><strong>${model.filingReady}</strong><small>Specific payer windows, not placeholders</small></div>
      <div><span>Auth Signals</span><strong>${model.authObserved}</strong><small>Payers with auth/evidence work</small></div>
      <div><span>Route Coverage</span><strong>${model.routeReady}</strong><small>Follow-up and appeal routes present</small></div>
    </div>
    <div class="payer-rules-list">
      <div class="payer-rules-row payer-rules-header">
        <span>Payer / States</span>
        <span>Open AR</span>
        <span>Contract Rates</span>
        <span>Filing Window</span>
        <span>Auth / Evidence</span>
        <span>Route</span>
        <span>Setup Next Action</span>
      </div>
      ${model.rows.length ? model.rows.slice(0, 10).map(renderPayerRulesReadinessRow).join("") : '<div class="payer-rules-empty">No payer rows available for readiness review.</div>'}
    </div>
  `;
}

function payerRulesReadinessModel(claims = [], feeRows = [], rules = []) {
  const rows = payerRulesReadinessRows(claims, feeRows, rules);
  const totals = rows.reduce((sum, row) => {
    sum.payers += 1;
    sum.openAr += row.outstanding;
    sum.delta += row.delta;
    if (row.overallStatus === "Ready") sum.readyPayers += 1;
    if (row.contractStatus === "Configured") sum.contractReady += 1;
    if (row.filingStatus === "Configured") sum.filingReady += 1;
    if (row.authStatus !== "No Signal") sum.authObserved += 1;
    if (row.routeStatus === "Configured") sum.routeReady += 1;
    sum.policyGaps += row.gaps.length;
    return sum;
  }, { payers: 0, readyPayers: 0, contractReady: 0, filingReady: 0, authObserved: 0, routeReady: 0, policyGaps: 0, openAr: 0, delta: 0 });
  totals.openAr = moneyNumber(totals.openAr);
  totals.delta = moneyNumber(totals.delta);
  return { ...totals, rows };
}

function payerRulesReadinessRows(claims = [], feeRows = [], rules = []) {
  const grouped = new Map();
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    payerNamesForClaim(claim).forEach((payer) => {
      const row = grouped.get(payer) || {
        payer,
        states: new Set(),
        claims: new Set(),
        services: 0,
        matchedServices: 0,
        expected: 0,
        paid: 0,
        outstanding: 0,
        delta: 0,
        authSignals: 0,
        denialSignals: 0,
        followUpSignals: 0,
        filingDays: timelyFilingDeadlineDays({ primaryPayer: payer }),
        filingSpecific: payerHasSpecificFilingRule(payer)
      };
      row.states.add(clientStateValue(claim) || "State Not Tagged");
      row.claims.add(claim.claimId);
      const financial = claimFinancialView(claim);
      row.expected += financial.expectedReimbursement;
      row.paid += financial.totalInsurancePaid;
      row.outstanding += financial.sourceOutstanding;
      row.delta += moneyNumber(financial.expectedReimbursement - financial.totalInsurancePaid - financial.sourceOutstanding);
      if (hasClientAuthorizationSignal(claim) || /auth|authorization|coverage|cob|credential/.test(claimOperationalText(claim))) row.authSignals += 1;
      if (isDeniedClaim(claim)) row.denialSignals += 1;
      if (matchesQueue(claim, "payerFollowUp") || matchesQueue(claim, "missingPayerResponse") || matchesQueue(claim, "waitingPrimary") || matchesQueue(claim, "waitingSecondary")) row.followUpSignals += 1;
      (claim.services || []).forEach((service) => {
        row.services += 1;
        if (bestFeeScheduleMatch(payer, {
          ...service,
          place: service.place || claim.place,
          state: firstPresent(service.state, claim.state),
          orgId: firstPresent(service.orgId, service.org_id, claim.orgId, claim.org_id),
          payerId: payerExternalIdForClaimPayer(claim, payer)
        }, feeRows)) row.matchedServices += 1;
      });
      grouped.set(payer, row);
    });
  });
  return Array.from(grouped.values()).map((row) => {
    const contractStatus = payerContractReadiness(row);
    const filingStatus = row.filingSpecific ? "Configured" : "Placeholder";
    const authStatus = row.authSignals ? "Observed" : "No Signal";
    const routeStatus = payerRouteReadiness(row, rules);
    const gaps = payerRulesGaps({ ...row, contractStatus, filingStatus, authStatus, routeStatus });
    const overallStatus = gaps.length ? (gaps.length >= 3 ? "Critical Gaps" : "Partial") : "Ready";
    return {
      ...row,
      states: Array.from(row.states).sort().join(", "),
      claims: row.claims.size,
      expected: moneyNumber(row.expected),
      paid: moneyNumber(row.paid),
      outstanding: moneyNumber(row.outstanding),
      delta: moneyNumber(row.delta),
      contractStatus,
      filingStatus,
      authStatus,
      routeStatus,
      gaps,
      overallStatus,
      nextAction: payerRulesNextAction({ contractStatus, filingStatus, authStatus, routeStatus, gaps })
    };
  }).sort((a, b) => payerRulesStatusRank(a.overallStatus) - payerRulesStatusRank(b.overallStatus)
    || b.outstanding - a.outstanding
    || b.gaps.length - a.gaps.length);
}

function payerNamesForClaim(claim = {}) {
  return [claim.primaryPayer, claim.secondaryPayer]
    .map((payer) => String(payer || "").trim())
    .filter((payer, index, payers) => payer && !/^none$/i.test(payer) && payers.indexOf(payer) === index);
}

function payerHasSpecificFilingRule(payer = "") {
  return Boolean(payerOperatingRule(payer)?.timelyFilingDays);
}

function payerOperatingRule(payer = "") {
  const text = normalizedContractText(payer);
  const rules = [
    { pattern: /aetna/, timelyFilingDays: 365, source: "Aetna default contract placeholder" },
    { pattern: /united|uhc|optum/, timelyFilingDays: 365, source: "United/Optum default contract placeholder" },
    { pattern: /medicaid.*ct|medicaidct/, timelyFilingDays: 180, source: "CT Medicaid placeholder" },
    { pattern: /medicaid.*nj|medicaidnj/, timelyFilingDays: 180, source: "NJ Medicaid placeholder" }
  ];
  return rules.find((rule) => rule.pattern.test(text)) || null;
}

function payerContractReadiness(row = {}) {
  if (!row.services) return "Missing";
  if (row.matchedServices === row.services) return "Configured";
  if (row.matchedServices > 0) return "Partial";
  return "Missing";
}

function payerRouteReadiness(row = {}, rules = []) {
  const actions = (Array.isArray(rules) ? rules : []).map((rule) => String(rule.action || "").toLowerCase());
  const hasPayerRoute = actions.some((action) => /payment processing|primary claim in process|submit to secondary|payer|secondary/.test(action));
  const hasAppealRoute = actions.some((action) => /appeal|correction|required|audit|escalate/.test(action));
  if ((row.denialSignals && hasAppealRoute) || (row.followUpSignals && hasPayerRoute)) return "Configured";
  if (hasPayerRoute || hasAppealRoute) return "Base Route";
  return "Missing";
}

function payerRulesGaps(row = {}) {
  const gaps = [];
  if (row.contractStatus !== "Configured") gaps.push("contract rate mapping");
  if (row.filingStatus !== "Configured") gaps.push("payer-specific timely filing rule");
  if (row.authStatus === "No Signal") gaps.push("authorization evidence rule");
  if (row.routeStatus !== "Configured") gaps.push("follow-up/appeal route");
  return gaps;
}

function payerRulesNextAction(row = {}) {
  if (row.contractStatus === "Missing") return "Map payer contract rows by state, CPT, modifier, place of service, and effective date.";
  if (row.contractStatus === "Partial") return "Close partial contract matches before using expected reimbursement for this payer.";
  if (row.filingStatus !== "Configured") return "Replace placeholder filing window with payer-specific timely filing and corrected-claim rules.";
  if (row.authStatus === "No Signal") return "Define auth, COB, coverage, and documentation evidence rules for this payer.";
  if (row.routeStatus !== "Configured") return "Map payer follow-up, appeal, corrected-claim, and secondary routes.";
  return "Ready for production rule review and sign-off.";
}

function payerRulesStatusRank(status = "") {
  return { "Critical Gaps": 0, "Partial": 1, "Ready": 2 }[status] ?? 9;
}

function renderPayerRulesReadinessRow(row) {
  const tone = row.overallStatus === "Ready" ? "green" : row.overallStatus === "Partial" ? "amber" : "red";
  return `
    <div class="payer-rules-row">
      <span data-label="Payer / States"><strong>${escapeHtml(row.payer)}</strong><small>${escapeHtml(row.states)} · ${formatNounCount(row.claims, "claim")}</small></span>
      <span data-label="Open AR"><strong>${feeReviewMoney(row.outstanding)}</strong><small>Delta ${feeReviewMoney(row.delta)}</small></span>
      <span data-label="Contract Rates">${pill(row.contractStatus, row.contractStatus === "Configured" ? "green" : row.contractStatus === "Partial" ? "amber" : "red")}<small>${formatCount(row.matchedServices)}/${formatNounCount(row.services, "service row")}</small></span>
      <span data-label="Filing Window">${pill(row.filingStatus, row.filingStatus === "Configured" ? "green" : "amber")}<small>${formatNounCount(row.filingDays, "day")}</small></span>
      <span data-label="Auth / Evidence">${pill(row.authStatus, row.authStatus === "Observed" ? "blue" : "gray")}<small>${formatNounCount(row.authSignals, "signal")}</small></span>
      <span data-label="Route">${pill(row.routeStatus, row.routeStatus === "Configured" ? "green" : row.routeStatus === "Base Route" ? "amber" : "red")}<small>${formatNounCount(row.denialSignals, "denial")} · ${formatNounCount(row.followUpSignals, "follow-up")}</small></span>
      <span data-label="Setup Next Action"><strong>${pill(row.overallStatus, tone)}</strong><small>${escapeHtml(row.nextAction)}</small></span>
    </div>
  `;
}

// Per user direction: 'each group will have their own fee schedules.'
// Filter fee-schedule rows by the active user's allowed orgIds before
// rendering / contract-variance / payer-rules-readiness.
function feeRowsForActiveUser(rows = state.feeScheduleRows || []) {
  const scope = currentAccessProfile().scope || {};
  // An explicitly assigned-only profile never sees fee schedules: its scope is
  // a queue of rows, not a book of companies, so there is nothing to match on.
  // (Offshore billers are org-scoped and, since 2026-10-12, hold
  // VIEW_FEE_SCHEDULES; the org filter below limits them to their companies.)
  if (scope.assignedOnly) return [];
  const indexed = (Array.isArray(rows) ? rows : []).map((row, sourceIndex) => ({ ...row, __sourceIndex: sourceIndex }));
  if (scope.allOrgs) return indexed;
  const allowedTenants = [
    ...(Array.isArray(scope.tenantIds) ? scope.tenantIds : []),
    ...(Array.isArray(scope.clientNames) ? scope.clientNames : [])
  ].map(String).filter(Boolean);
  const allowedOrgs = Array.isArray(scope.orgIds) ? scope.orgIds.map(String).filter(Boolean) : [];
  if (!allowedTenants.length && !allowedOrgs.length) return [];
  const allowedTenantSet = new Set(allowedTenants);
  const allowedOrgSet = new Set(allowedOrgs);
  return indexed.filter((row) => {
    const tenantKey = feeScheduleTenantKey(row);
    const orgKey = String(row.orgId || "").trim();
    return (tenantKey && allowedTenantSet.has(tenantKey))
      || (orgKey && allowedOrgSet.has(orgKey))
      || allowedTenants.some((tenant) => feeScheduleRowMatchesTenant(row, tenant));
  });
}

// The header company is a mandatory display narrowing inside permission scope.
// Every rate/worklist read uses this helper so Clear cannot widen a client call.
function feeScheduleEffectiveFilters() {
  const filters = { ...(state.feeScheduleFilters || {}) };
  const tenant = activeClientGroupTenant();
  const orgIds = activeClientGroupOrgIds();
  if (tenant) {
    filters.tenantId = tenant;
    filters.orgId = orgIds.length === 1 ? orgIds[0] : (orgIds.includes(String(filters.orgId || "")) ? filters.orgId : "All");
  }
  return filters;
}

function feeScheduleRowInSelectedCompany(row = {}) {
  const tenant = activeClientGroupTenant();
  if (!tenant) return true;
  const orgIds = activeClientGroupOrgIds();
  return orgIds.length ? orgIds.includes(String(row.orgId || row.org_id || "").trim()) : feeScheduleRowMatchesTenant(row, tenant);
}

function compactFeeScheduleSearch(value = "") {
  return String(value || "").toLowerCase().replace(/[^a-z0-9.]+/g, "");
}

// Word match first; then a space-blind match so "peachstate" finds
// "PEACH STATE HEALTH PLAN" (and "peach state" finds "PEACHSTATE").
function feeScheduleSearchMatches(text, search) {
  if (!search || text.includes(search)) return true;
  return compactFeeScheduleSearch(text).includes(compactFeeScheduleSearch(search));
}

function filteredFeeScheduleRows(rows = feeScheduleDisplayRows(), options = {}) {
  const filters = feeScheduleEffectiveFilters();
  const search = normalizedFeeScheduleSearch(filters.search);
  const showArchived = Boolean(filters.showArchived);
  // Excel semantics: when we are building ONE column's value list we leave that
  // column wide open and keep every other narrowing in place.
  const exceptColumn = String((options && options.exceptColumn) || "");
  return rows.filter((row) => {
    if (!feeScheduleRowInSelectedCompany(row)) return false;
    if ((String(row.status || "active") === "archived") !== showArchived) return false;
    if (filters.tenantId && filters.tenantId !== "All" && !feeScheduleRowMatchesTenant(row, filters.tenantId)) return false;
    if (filters.orgId && filters.orgId !== "All" && feeScheduleOrgKey(row) !== String(filters.orgId || "")) return false;
    if (filters.payer && filters.payer !== "All" && String(row.payer || "") !== filters.payer) return false;
    if (filters.state && filters.state !== "All" && String(row.state || "__unavailable__") !== filters.state) return false;
    if (filters.procedure && filters.procedure !== "All" && String(row.procedure || "") !== filters.procedure) return false;
    if (filters.network && filters.network !== "All" && String(row.network || "__unavailable__") !== filters.network) return false;
    if (filters.activeDate && !feeScheduleRowActiveOn(row, filters.activeDate)) return false;
    if (!feeScheduleSearchMatches(feeScheduleRowSearchText(row), search)) return false;
    if (!feeScheduleRowMatchesColumnFilters(row, exceptColumn)) return false;
    return true;
  });
}

// ---------------------------------------------------------------------------
// FEE SCHEDULE COLUMN FILTERS — Excel's AutoFilter, on the rate table.
//
// The owner asked for it in those words: "I want each column to be a dropdown
// filter option, like Excel." The All Data grid has had per-column filter menus
// for months (openColumnFilterMenu / .column-filter-* markup), so this reuses
// that control wholesale — same popup skin, same Select all / Clear / Cancel /
// Apply, same "●" vs "▾" header glyph — because a biller should learn ONE
// filter control, not two.
//
// Where it deliberately differs from All Data:
//   * NO facet API. All Data pages a warehouse of millions of service lines, so
//     it has to ask the server for a column's distinct values. The rate table is
//     ~1,000 rows and already fully client-side; the distinct values are sitting
//     in memory. An endpoint here would be cost with no benefit.
//   * Rate is a MIN/MAX range, not a value list. ~500 distinct money values is
//     not a checklist anyone can use, and the question billers actually ask is
//     "show me everything above X", which a list cannot answer.
//   * Start/End Date are FROM/TO ranges for the same reason, plus one of their
//     own: a blank End Date means "open-ended", i.e. the rate is still live. A
//     date range that silently dropped those would read as missing data, so both
//     range menus carry an "include blanks" toggle that defaults ON.
//   * Escape closes, Tab is trapped, focus returns to the header button. The
//     All Data menu has none of that; it is the older control, not the better one.
//
// This is a DISPLAY filter and nothing else. It narrows what is on screen (and
// what Export writes, which is what "Export" has always meant here); it never
// touches which rows are saved, approved, archived or priced.
// ---------------------------------------------------------------------------

const FEE_SCHEDULE_FILTER_COLUMNS = [
  { key: "orgId", label: "Org ID", type: "list" },
  { key: "client", label: "Client", type: "list" },
  { key: "payer", label: "Payer", type: "list" },
  { key: "state", label: "State", type: "list" },
  { key: "procedure", label: "CPT", type: "list" },
  { key: "m1", label: "M1", type: "list" },
  { key: "m2", label: "M2", type: "list" },
  { key: "place", label: "POS", type: "list" },
  { key: "rate", label: "Rate", type: "number" },
  { key: "secondaryCalc", label: "Secondary Calc", type: "list" },
  { key: "startDate", label: "Start Date", type: "date" },
  { key: "endDate", label: "End Date", type: "date" },
  { key: "network", label: "Network", type: "list" },
  { key: "approvalStatus", label: "Approval", type: "list" }
];

// The token a blank cell filters under. Stored in state, so it must stay stable.
const FEE_SCHEDULE_FILTER_BLANK = "(Blank)";
// Above this many distinct values the menu grows a search box. Payer is the
// column that needs it; State and CPT do not.
const FEE_SCHEDULE_FILTER_SEARCH_THRESHOLD = 8;

function feeScheduleFilterColumn(key = "") {
  return FEE_SCHEDULE_FILTER_COLUMNS.find((column) => column.key === key) || null;
}

// One expression per column, used by BOTH the facet list and the matcher. When
// these drift a user ticks a value and no rows move, which reads as a bug in
// the data. `client` mirrors the cell in renderFeeSchedules exactly.
function feeScheduleColumnValue(row = {}, key = "") {
  if (key === "client") return tenantNameForOrgId(row.orgId) || String(row.orgId || "") || "—";
  if (key === "approvalStatus") return String(row.approvalStatus || "draft").toLowerCase() === "approved" ? "approved" : "draft";
  if (key === "secondaryCalc") {
    const text = String(row.secondaryCalc || "");
    return /pr\s*amount/i.test(text) ? "PR Amount" : /allowed/i.test(text) ? "Allowed Amount" : "";
  }
  return String(row[key] === null || row[key] === undefined ? "" : row[key]).trim();
}

// The token a row files under in a list facet.
function feeScheduleColumnToken(row = {}, key = "") {
  const value = feeScheduleColumnValue(row, key);
  return value === "" ? FEE_SCHEDULE_FILTER_BLANK : value;
}

// What the biller reads in the menu. Only two columns need a translation:
// Approval stores draft/approved, and a blank Secondary Calc IS the default
// rule rather than a hole in the data.
function feeScheduleColumnTokenLabel(key = "", token = "") {
  if (key === "approvalStatus") return token === "approved" ? "Approved" : token === "draft" ? "Draft" : token;
  if (key === "secondaryCalc" && token === FEE_SCHEDULE_FILTER_BLANK) return "Default";
  return token;
}

function feeScheduleFilterNumber(value) {
  const text = String(value === null || value === undefined ? "" : value).replace(/[$,\s]/g, "").trim();
  if (!text) return NaN;
  const number = Number(text);
  return Number.isFinite(number) ? number : NaN;
}

// Rate rows carry ISO dates (2025-01-01) on both lanes, so a string compare is
// the right compare. A non-ISO value is treated as blank rather than guessed at.
function feeScheduleFilterDate(value) {
  const text = String(value === null || value === undefined ? "" : value).trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

function ensureFeeScheduleColumnFilterState(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const clean = {};
  FEE_SCHEDULE_FILTER_COLUMNS.forEach((column) => {
    const entry = source[column.key];
    if (!entry || typeof entry !== "object") return;
    if (column.type === "list") {
      const values = Array.isArray(entry.values) ? entry.values.map(String).filter((value) => value !== "") : [];
      const excludedValues = Array.isArray(entry.excludedValues) ? entry.excludedValues.map(String).filter(Boolean) : [];
      if (excludedValues.length) clean[column.key] = { excludedValues };
      else if (values.length) clean[column.key] = { values };
      return;
    }
    const blanks = entry.blanks === false ? false : true;
    if (column.type === "number") {
      const min = String(entry.min === null || entry.min === undefined ? "" : entry.min).trim();
      const max = String(entry.max === null || entry.max === undefined ? "" : entry.max).trim();
      if (min || max || blanks === false) clean[column.key] = { min, max, blanks };
      return;
    }
    const from = feeScheduleFilterDate(entry.from);
    const to = feeScheduleFilterDate(entry.to);
    if (from || to || blanks === false) clean[column.key] = { from, to, blanks };
  });
  return clean;
}

function feeScheduleColumnFilterEntries() {
  return ensureFeeScheduleColumnFilterState(state.feeScheduleColumnFilters);
}

function feeScheduleColumnFilterCount() {
  return Object.keys(feeScheduleColumnFilterEntries()).length;
}

function feeScheduleRowMatchesColumnFilter(row = {}, column = null, entry = null) {
  if (!column || !entry) return true;
  if (column.type === "list") {
    if (Array.isArray(entry.excludedValues) && entry.excludedValues.length) return !entry.excludedValues.includes(feeScheduleColumnToken(row, column.key));
    if (!Array.isArray(entry.values) || !entry.values.length) return true;
    return entry.values.includes(feeScheduleColumnToken(row, column.key));
  }
  if (column.type === "number") {
    const value = feeScheduleFilterNumber(feeScheduleColumnValue(row, column.key));
    if (!Number.isFinite(value)) return entry.blanks !== false;
    const min = feeScheduleFilterNumber(entry.min);
    const max = feeScheduleFilterNumber(entry.max);
    if (Number.isFinite(min) && value < min) return false;
    if (Number.isFinite(max) && value > max) return false;
    return true;
  }
  const value = feeScheduleFilterDate(feeScheduleColumnValue(row, column.key));
  if (!value) return entry.blanks !== false;
  if (entry.from && value < entry.from) return false;
  if (entry.to && value > entry.to) return false;
  return true;
}

function feeScheduleRowMatchesColumnFilters(row = {}, exceptColumn = "") {
  const entries = feeScheduleColumnFilterEntries();
  return Object.keys(entries).every((key) => {
    if (key === exceptColumn) return true;
    return feeScheduleRowMatchesColumnFilter(row, feeScheduleFilterColumn(key), entries[key]);
  });
}

// Excel shows a column its OWN unfiltered values while every other column stays
// narrowed — otherwise unticking is a one-way door. `exceptColumn` is what buys
// that, and it is why the facet list is recomputed on every open.
function feeScheduleColumnFilterFacets(key = "", scopedRows = null) {
  const column = feeScheduleFilterColumn(key);
  if (!column) return { options: [], blankCount: 0, min: null, max: null };
  const rows = filteredFeeScheduleRows(
    Array.isArray(scopedRows) ? scopedRows : feeScheduleDisplayRows(),
    { exceptColumn: key }
  );
  if (column.type === "list") {
    const counts = new Map();
    rows.forEach((row) => {
      const token = feeScheduleColumnToken(row, key);
      counts.set(token, (counts.get(token) || 0) + 1);
    });
    const options = Array.from(counts.entries())
      .map(([token, count]) => ({ value: token, label: feeScheduleColumnTokenLabel(key, token), count }))
      .sort((a, b) => {
        if (a.value === FEE_SCHEDULE_FILTER_BLANK) return 1;
        if (b.value === FEE_SCHEDULE_FILTER_BLANK) return -1;
        return String(a.label).localeCompare(String(b.label), undefined, { numeric: true, sensitivity: "base" });
      });
    return { options, blankCount: counts.get(FEE_SCHEDULE_FILTER_BLANK) || 0, min: null, max: null };
  }
  let blankCount = 0;
  let min = null;
  let max = null;
  rows.forEach((row) => {
    const raw = feeScheduleColumnValue(row, key);
    if (column.type === "number") {
      const value = feeScheduleFilterNumber(raw);
      if (!Number.isFinite(value)) { blankCount += 1; return; }
      min = min === null || value < min ? value : min;
      max = max === null || value > max ? value : max;
      return;
    }
    const value = feeScheduleFilterDate(raw);
    if (!value) { blankCount += 1; return; }
    min = min === null || value < min ? value : min;
    max = max === null || value > max ? value : max;
  });
  return { options: [], blankCount, min, max };
}

// --- the header -------------------------------------------------------------

function renderFeeScheduleColumnHeaders() {
  if (typeof document === "undefined" || !document.querySelector) return;
  const head = document.querySelector(".fee-schedule-table thead tr");
  if (!head || !head.querySelector) return;
  const entries = feeScheduleColumnFilterEntries();
  const ready = !feeScheduleApiEnabled() || (feeScheduleApiRequestStillCurrent(feeScheduleApiState.key) && ["ok", "saving"].includes(feeScheduleApiState.status) && feeScheduleApiState.complete === true);
  FEE_SCHEDULE_FILTER_COLUMNS.forEach((column) => {
    const cell = head.querySelector('[data-fee-col="' + column.key + '"]');
    if (!cell) return;
    const active = Boolean(entries[column.key]);
    if (cell.classList) cell.classList.toggle("filtered-column", active);
    cell.innerHTML = `
      <div class="fee-th-inner">
        <span class="fee-th-label">${escapeHtml(column.label)}</span>
        <button class="column-filter-button" type="button" ${ready ? "" : "disabled"} data-fee-column-filter="${escapeHtml(column.key)}" aria-haspopup="dialog" aria-expanded="false" aria-label="Filter ${escapeHtml(column.label)}${active ? " (filtered)" : ""}" title="Filter ${escapeHtml(column.label)}${active ? " — filtered" : ""}">
          <strong>${active ? "●" : "▾"}</strong>
        </button>
      </div>
    `;
  });
}

// --- the menu ---------------------------------------------------------------

let feeScheduleColumnFilterContext = null;
let feeScheduleColumnFilterMenuBound = false;

function feeScheduleColumnFilterMenuHost() {
  if (els && els.feeScheduleColumnFilterMenu) return els.feeScheduleColumnFilterMenu;
  if (typeof document === "undefined" || !document.getElementById) return null;
  return document.getElementById("feeScheduleColumnFilterMenu");
}

function openFeeScheduleColumnFilterMenu(key, button) {
  const menu = feeScheduleColumnFilterMenuHost();
  const column = feeScheduleFilterColumn(key);
  if (!menu || !column || !button) return;
  if (feeScheduleApiEnabled() && (!feeScheduleApiRequestStillCurrent(feeScheduleApiState.key) || !["ok", "saving"].includes(feeScheduleApiState.status) || feeScheduleApiState.complete !== true)) { closeFeeScheduleColumnFilterMenu(); return; }
  if (feeScheduleApiEnabled()) return openFeeSetupColumnChoices(key, button, menu, column);
  const facets = feeScheduleColumnFilterFacets(key);
  const entry = feeScheduleColumnFilterEntries()[key] || null;
  feeScheduleColumnFilterContext = { key, button };
  menu.innerHTML = `
    <div class="column-filter-title" id="feeColumnFilterTitle">Filter ${escapeHtml(column.label)}</div>
    ${column.type === "list"
      ? feeScheduleColumnFilterListHtml(column, facets, entry)
      : feeScheduleColumnFilterRangeHtml(column, facets, entry)}
    <div class="column-filter-actions">
      <button class="small-button" type="button" data-fee-filter-clear>Clear</button>
      <button class="small-button" type="button" data-fee-filter-cancel>Cancel</button>
      <button class="primary-button" type="button" data-fee-filter-apply>Apply</button>
    </div>
  `;
  menu.setAttribute("aria-labelledby", "feeColumnFilterTitle");
  menu.hidden = false;
  positionFeeScheduleColumnFilterMenu(menu, button, column);
  if (button.setAttribute) button.setAttribute("aria-expanded", "true");
  wireFeeScheduleColumnFilterMenu(menu, column, facets);
}

async function openFeeSetupColumnChoices(key, button, menu, column) {
  const entry = feeScheduleColumnFilterEntries()[key] || {}, selected = new Set(entry.values || []), excluded = new Set(entry.excludedValues || []);
  const context = { key, button, controller: null }; feeScheduleColumnFilterContext = context;
  let inclusion = selected.size > 0, requestSequence = 0, searchTimer;
  menu.innerHTML = '<div class="column-filter-title">Filter ' + escapeHtml(column.label) + '</div>'
    + (column.type === "list" ? '<input type="search" class="column-filter-search" data-fee-filter-search aria-label="Search ' + escapeHtml(column.label) + ' values"><div data-fee-remote-options></div>' : '<div data-fee-remote-range></div>')
    + '<div class="column-filter-actions"><button class="small-button" data-fee-filter-clear>Clear</button><button class="small-button" data-fee-filter-cancel>Cancel</button><button class="primary-button" data-fee-filter-apply>Apply</button></div>';
  menu.hidden = false; button.setAttribute("aria-expanded", "true"); positionFeeScheduleColumnFilterMenu(menu, button, column);
  const current = () => feeScheduleColumnFilterContext === context && !menu.hidden;
  menu.querySelector('[data-fee-filter-clear]').onclick = () => clearFeeScheduleColumnFilter(key);
  menu.querySelector('[data-fee-filter-cancel]').onclick = () => closeFeeScheduleColumnFilterMenu({ restoreFocus: true });
  menu.querySelector('[data-fee-filter-apply]').onclick = async () => {
    const term = column.type === "list" ? String(menu.querySelector('[data-fee-filter-search]')?.value || "").trim() : "";
    if (term) {
      // Excel semantics: with a search typed, Apply keeps only the ticked values
      // that match it, across every page of matches. Without this an untouched
      // menu (exclude nothing) applied no filter, so "Peachstate" showed every payer.
      const applyButton = menu.querySelector('[data-fee-filter-apply]'); applyButton.disabled = true;
      try {
        const matches = [];
        for (let page = 1; page <= 200; page += 1) {
          const facets = await requestFeeSetupChoices(key, { search: term, page });
          if (!current()) return;
          matches.push(...facets.options.map(option => option.value));
          if (!facets.page?.hasMore) break;
        }
        const values = matches.filter(value => inclusion ? selected.has(value) : !excluded.has(value));
        // An empty list would mean "no filter"; a search that matched nothing must show nothing.
        applyFeeScheduleColumnFilter(key, { values: values.length ? values : ["__no_matching_value__"], total: Infinity });
      } catch (error) { if (current()) applyButton.disabled = false; }
      return;
    }
    if (column.type === "list") applyFeeScheduleColumnFilter(key, inclusion ? { values: Array.from(selected), total: Infinity } : { excludedValues: Array.from(excluded) });
    else applyFeeScheduleColumnFilter(key, { from: menu.querySelector('[data-fee-filter-from]')?.value || "", to: menu.querySelector('[data-fee-filter-to]')?.value || "", blanks: menu.querySelector('[data-fee-filter-blanks]')?.checked !== false });
  };
  menu.onkeydown = event => { if (event.key === "Escape") { event.preventDefault(); closeFeeScheduleColumnFilterMenu({ restoreFocus: true }); } if (event.key === "Tab") { const controls = Array.from(menu.querySelectorAll('input,button')).filter(node => !node.disabled); const first = controls[0], last = controls.at(-1); if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); } } };
  const load = async (page = 1) => {
    const sequence = ++requestSequence; context.controller?.abort(); context.controller = new AbortController();
    const target = menu.querySelector(column.type === "list" ? '[data-fee-remote-options]' : '[data-fee-remote-range]');
    target.innerHTML = '<div role="status">Loading choices…</div>'; menu.querySelector('[data-fee-filter-apply]').disabled = true;
    try {
      const facets = await requestFeeSetupChoices(key, { search: menu.querySelector('[data-fee-filter-search]')?.value || "", page, signal: context.controller.signal });
      if (!current() || sequence !== requestSequence) return;
      menu.querySelector('[data-fee-filter-apply]').disabled = false;
      if (column.type !== "list") { target.innerHTML = feeScheduleColumnFilterRangeHtml(column, facets, entry); target.querySelector('input')?.focus(); return; }
      const meta = facets.page || {}, checked = value => inclusion ? selected.has(value) : !excluded.has(value);
      target.innerHTML = '<label class="column-filter-select-all"><input type="checkbox" data-fee-page-all' + (facets.options.length && facets.options.every(option => checked(option.value)) ? ' checked' : '') + '>Select this page</label><div class="column-filter-options">'
        + facets.options.map(option => '<label class="column-filter-choice"><input type="checkbox" data-fee-filter-choice value="' + escapeHtml(option.value) + '"' + (checked(option.value) ? ' checked' : '') + '><span>' + escapeHtml(option.label) + '</span><strong>' + formatCount(option.count) + '</strong></label>').join('') + '</div>'
        + '<div class="tracker-choice-pager"><button type="button" data-fee-choice-prev' + (page <= 1 ? ' disabled' : '') + '>Previous</button><span>' + formatCount(meta.matchingOptions || 0) + ' choices · Page ' + page + '</span><button type="button" data-fee-choice-next' + (meta.hasMore ? '' : ' disabled') + '>Next</button></div>';
      const update = input => { const set = inclusion ? selected : excluded; if (input.checked === inclusion) set.add(input.value); else set.delete(input.value); };
      target.querySelectorAll('[data-fee-filter-choice]').forEach(input => input.onchange = () => update(input));
      target.querySelector('[data-fee-page-all]').onchange = event => target.querySelectorAll('[data-fee-filter-choice]').forEach(input => { input.checked = event.target.checked; update(input); });
      target.querySelector('[data-fee-choice-prev]').onclick = () => load(page - 1); target.querySelector('[data-fee-choice-next]').onclick = () => load(page + 1);
    } catch (error) { if (current() && sequence === requestSequence && error.name !== "AbortError") { target.innerHTML = '<div role="alert">Choices unavailable.</div><button type="button" data-fee-choice-retry>Retry</button>'; target.querySelector('button').onclick = () => load(page); } }
  };
  const search = menu.querySelector('[data-fee-filter-search]'); if (search) { search.oninput = () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => load(1), 200); }; search.focus(); }
  await load();
}

function feeScheduleColumnFilterListHtml(column, facets, entry) {
  const options = facets.options || [];
  const selected = entry && Array.isArray(entry.values) ? entry.values : [];
  // No stored filter means every value is showing, so every box starts ticked —
  // the Excel reading of an unfiltered column.
  const checked = new Set(selected.length ? selected : options.map((option) => option.value));
  const showSearch = options.length > 0;
  return `
    ${showSearch ? `<input class="column-filter-search" type="search" placeholder="Search ${escapeHtml(column.label)}" aria-label="Search ${escapeHtml(column.label)} values" data-fee-filter-search>` : ""}
    <label class="column-filter-select-all">
      <input type="checkbox" data-fee-filter-select-all${checked.size === options.length && options.length ? " checked" : ""}>
      <span>Select all</span>
      <strong>${options.length}</strong>
    </label>
    <div class="column-filter-options" role="group" aria-label="${escapeHtml(column.label)} values">
      ${options.map((option) => `
        <label class="column-filter-choice">
          <input type="checkbox" value="${escapeHtml(option.value)}" data-fee-filter-choice${checked.has(option.value) ? " checked" : ""}>
          <span>${escapeHtml(option.label)}</span>
          <strong>${option.count.toLocaleString()}</strong>
        </label>
      `).join("") || '<div class="column-filter-empty">No values in view</div>'}
    </div>
  `;
}

function feeScheduleColumnFilterRangeHtml(column, facets, entry) {
  const isDate = column.type === "date";
  const from = entry ? (isDate ? entry.from || "" : entry.min || "") : "";
  const to = entry ? (isDate ? entry.to || "" : entry.max || "") : "";
  const blanks = entry ? entry.blanks !== false : true;
  const low = facets.min === null || facets.min === undefined ? "" : String(isDate ? facets.min : facets.min.toFixed(2));
  const high = facets.max === null || facets.max === undefined ? "" : String(isDate ? facets.max : facets.max.toFixed(2));
  const rangeNote = low && high
    ? (isDate ? `Dates in view run ${low} to ${high}.` : `Rates in view run ${low} to ${high}.`)
    : "No values in view to range over.";
  return `
    <div class="column-filter-range">
      <label class="column-filter-range-field">
        <span>${isDate ? "From" : "Min"}</span>
        <input type="${isDate ? "date" : "number"}"${isDate ? "" : ' step="0.01" min="0"'} value="${escapeHtml(from)}" placeholder="${escapeHtml(low)}" data-fee-filter-from aria-label="${escapeHtml(column.label)} ${isDate ? "from" : "minimum"}">
      </label>
      <label class="column-filter-range-field">
        <span>${isDate ? "To" : "Max"}</span>
        <input type="${isDate ? "date" : "number"}"${isDate ? "" : ' step="0.01" min="0"'} value="${escapeHtml(to)}" placeholder="${escapeHtml(high)}" data-fee-filter-to aria-label="${escapeHtml(column.label)} ${isDate ? "to" : "maximum"}">
      </label>
      ${facets.blankCount ? `
        <label class="column-filter-range-check">
          <input type="checkbox" data-fee-filter-blanks${blanks ? " checked" : ""}>
          <span>Keep the ${facets.blankCount.toLocaleString()} row${facets.blankCount === 1 ? "" : "s"} with no ${escapeHtml(column.label.toLowerCase())}${isDate ? " (open-ended)" : ""}</span>
        </label>
      ` : ""}
      <p class="column-filter-range-note">${escapeHtml(rangeNote)}</p>
    </div>
  `;
}

function positionFeeScheduleColumnFilterMenu(menu, button, column) {
  if (!button.getBoundingClientRect || typeof window === "undefined") return;
  const rect = button.getBoundingClientRect();
  const menuWidth = 300;
  const left = Math.max(window.scrollX + 8, Math.min(window.scrollX + rect.left, window.scrollX + window.innerWidth - menuWidth - 8));
  const chromeHeight = column.type === "list" ? 178 : 210;
  const belowSpace = window.innerHeight - rect.bottom - 12;
  const aboveSpace = rect.top - 12;
  const usableSpace = Math.max(belowSpace, aboveSpace);
  const optionMaxHeight = Math.max(72, Math.min(230, usableSpace - chromeHeight));
  menu.style.left = `${left}px`;
  menu.style.setProperty("--filter-options-max-height", `${optionMaxHeight}px`);
  // Park it below, then measure. The All Data menu estimates its height from a
  // fixed chrome constant, which is close enough for a scrolling value list and
  // ~160px wrong for a two-input range menu — it floated, detached, above the
  // column it belonged to.
  menu.style.top = `${window.scrollY + rect.bottom + 4}px`;
  const height = menu.offsetHeight || (chromeHeight + optionMaxHeight);
  if (belowSpace < height && aboveSpace > belowSpace) {
    menu.style.top = `${window.scrollY + Math.max(8, rect.top - height - 4)}px`;
  }
}

function wireFeeScheduleColumnFilterMenu(menu, column, facets) {
  const query = (selector) => (menu.querySelector ? menu.querySelector(selector) : null);
  const all = (selector) => (menu.querySelectorAll ? Array.from(menu.querySelectorAll(selector)) : []);
  const choices = () => all("[data-fee-filter-choice]");
  const search = query("[data-fee-filter-search]");
  const selectAll = query("[data-fee-filter-select-all]");
  const choiceHidden = (choice) => {
    const row = choice.closest(".column-filter-choice");
    return Boolean(row && row.hidden);
  };

  if (search) {
    search.addEventListener("input", () => {
      // Spaces and punctuation are ignored so "peachstate" finds "PEACH STATE".
      const needle = compactFeeScheduleSearch(search.value);
      choices().forEach((choice) => {
        const row = choice.closest(".column-filter-choice");
        const label = row && row.querySelector ? row.querySelector("span") : null;
        if (row) row.hidden = Boolean(needle) && !compactFeeScheduleSearch(label ? label.textContent : choice.value).includes(needle);
      });
    });
  }
  if (selectAll) {
    selectAll.addEventListener("change", () => {
      choices().forEach((choice) => {
        const row = choice.closest(".column-filter-choice");
        if (!row || !row.hidden) choice.checked = selectAll.checked;
      });
    });
    choices().forEach((choice) => {
      choice.addEventListener("change", () => {
        const list = choices().filter((item) => !choiceHidden(item));
        selectAll.checked = list.length > 0 && list.every((item) => item.checked);
      });
    });
  }

  const clearButton = query("[data-fee-filter-clear]");
  if (clearButton) clearButton.addEventListener("click", () => clearFeeScheduleColumnFilter(column.key));
  const cancelButton = query("[data-fee-filter-cancel]");
  if (cancelButton) cancelButton.addEventListener("click", () => closeFeeScheduleColumnFilterMenu({ restoreFocus: true }));
  const applyButton = query("[data-fee-filter-apply]");
  if (applyButton) {
    applyButton.addEventListener("click", () => {
      if (column.type === "list") {
        const list = choices();
        // Excel semantics: while a search is typed, Apply keeps only the ticked
        // values the search left visible. Every box starts ticked, so counting
        // hidden ones put "Peachstate" back to every payer.
        const checked = list.filter((choice) => choice.checked && !choiceHidden(choice)).map((choice) => choice.value);
        // A search that matched nothing must show nothing, not clear the filter.
        const searching = Boolean(compactFeeScheduleSearch(search?.value));
        applyFeeScheduleColumnFilter(column.key, checked.length || !searching ? { values: checked, total: (facets.options || []).length } : { values: ["__no_matching_value__"], total: Infinity });
        return;
      }
      const fromInput = query("[data-fee-filter-from]");
      const toInput = query("[data-fee-filter-to]");
      const blanksInput = query("[data-fee-filter-blanks]");
      applyFeeScheduleColumnFilter(column.key, {
        from: fromInput ? String(fromInput.value || "").trim() : "",
        to: toInput ? String(toInput.value || "").trim() : "",
        blanks: blanksInput ? Boolean(blanksInput.checked) : true
      });
    });
  }

  const focusables = () => all("input:not([type='hidden']), button, select, textarea").filter((node) => {
    const row = node.closest ? node.closest(".column-filter-choice") : null;
    return !(row && row.hidden);
  });
  const first = focusables()[0];
  if (first && first.focus) first.focus();

  // The keyboard layer All Data never got: Escape closes and hands focus back
  // to the header button, Tab cycles inside the menu instead of walking off
  // into a table the menu is covering.
  if (!menu.dataset || menu.dataset.feeFilterKeysBound !== "true") {
    menu.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeFeeScheduleColumnFilterMenu({ restoreFocus: true });
        return;
      }
      if (event.key !== "Tab") return;
      const list = focusables();
      if (!list.length) return;
      const firstNode = list[0];
      const lastNode = list[list.length - 1];
      if (event.shiftKey && event.target === firstNode) {
        event.preventDefault();
        if (lastNode.focus) lastNode.focus();
      } else if (!event.shiftKey && event.target === lastNode) {
        event.preventDefault();
        if (firstNode.focus) firstNode.focus();
      }
    });
    if (menu.dataset) menu.dataset.feeFilterKeysBound = "true";
  }
}

function closeFeeScheduleColumnFilterMenu(options = {}) {
  const menu = feeScheduleColumnFilterMenuHost();
  const context = feeScheduleColumnFilterContext;
  feeScheduleColumnFilterContext = null;
  if (menu) {
    menu.hidden = true;
    menu.innerHTML = "";
  }
  if (typeof document === "undefined" || !document.querySelector) return;
  // The header is re-rendered on every apply, so the button that opened the
  // menu may be a detached node by now — re-query it by key.
  const key = context ? context.key : "";
  if (!key) return;
  const button = document.querySelector('[data-fee-column-filter="' + key + '"]');
  if (!button) return;
  if (button.setAttribute) button.setAttribute("aria-expanded", "false");
  if (options.restoreFocus && button.focus) button.focus();
}

function applyFeeScheduleColumnFilter(key, payload = {}) {
  const column = feeScheduleFilterColumn(key);
  if (!column) return;
  const next = { ...feeScheduleColumnFilterEntries() };
  if (column.type === "list") {
    const values = Array.isArray(payload.values) ? payload.values : [];
    // Nothing ticked and everything ticked both mean "no filter on this column".
    if (Array.isArray(payload.excludedValues) && payload.excludedValues.length) next[key] = { excludedValues: payload.excludedValues };
    else if (!values.length || values.length === payload.total) delete next[key];
    else next[key] = { values };
  } else if (column.type === "number") {
    next[key] = { min: payload.from || "", max: payload.to || "", blanks: payload.blanks !== false };
  } else {
    next[key] = { from: feeScheduleFilterDate(payload.from), to: feeScheduleFilterDate(payload.to), blanks: payload.blanks !== false };
  }
  state.feeScheduleColumnFilters = ensureFeeScheduleColumnFilterState(next);
  saveState();
  closeFeeScheduleColumnFilterMenu({ restoreFocus: true });
  renderFeeSchedules();
}

function clearFeeScheduleColumnFilter(key) {
  const next = { ...feeScheduleColumnFilterEntries() };
  delete next[key];
  state.feeScheduleColumnFilters = ensureFeeScheduleColumnFilterState(next);
  saveState();
  closeFeeScheduleColumnFilterMenu({ restoreFocus: true });
  renderFeeSchedules();
}

function clearFeeScheduleColumnFilters() {
  state.feeScheduleColumnFilters = {};
  saveState();
  closeFeeScheduleColumnFilterMenu();
  renderFeeSchedules();
}

// A filter you cannot see is how people conclude the data is missing. Every
// narrowed column gets a chip next to the row count, and the chip clears it.
function feeScheduleColumnFilterChipsHtml() {
  const entries = feeScheduleColumnFilterEntries();
  const keys = Object.keys(entries);
  if (!keys.length) return "";
  const chips = FEE_SCHEDULE_FILTER_COLUMNS.filter((column) => entries[column.key]).map((column) => {
    const entry = entries[column.key];
    let text;
    if (column.type === "list") {
      const values = entry.values || entry.excludedValues || [];
      text = entry.excludedValues ? `${column.label}: ${values.length} excluded` : values.length === 1
        ? `${column.label}: ${feeScheduleColumnTokenLabel(column.key, values[0])}`
        : `${column.label}: ${values.length} values`;
    } else if (column.type === "number") {
      const bounds = [entry.min ? `≥ ${entry.min}` : "", entry.max ? `≤ ${entry.max}` : ""].filter(Boolean).join(" and ");
      text = `${column.label}: ${bounds || "no blanks"}${entry.blanks === false && bounds ? ", no blanks" : ""}`;
    } else {
      const bounds = [entry.from ? `from ${entry.from}` : "", entry.to ? `to ${entry.to}` : ""].filter(Boolean).join(" ");
      text = `${column.label}: ${bounds || "no blanks"}${entry.blanks === false && bounds ? ", no blanks" : ""}`;
    }
    return `<button class="fee-column-filter-chip" type="button" data-fee-column-filter-clear="${escapeHtml(column.key)}" aria-label="Clear the ${escapeHtml(column.label)} column filter" title="Clear the ${escapeHtml(column.label)} column filter"><span>${escapeHtml(text)}</span><span class="fee-chip-x" aria-hidden="true">×</span></button>`;
  });
  return chips.join("") + `<button class="fee-column-filter-chip fee-column-filter-chip-clear" type="button" data-fee-column-filter-clear-all="1">Clear column filters</button>`;
}

function wireFeeScheduleColumnFilterControls() {
  if (typeof document === "undefined" || !document.querySelectorAll) return;
  Array.from(document.querySelectorAll("[data-fee-column-filter-clear]")).forEach((button) => {
    button.addEventListener("click", () => clearFeeScheduleColumnFilter(button.dataset.feeColumnFilterClear));
  });
  Array.from(document.querySelectorAll("[data-fee-column-filter-clear-all]")).forEach((button) => {
    button.addEventListener("click", clearFeeScheduleColumnFilters);
  });
  Array.from(document.querySelectorAll("[data-fee-clear-all-filters]")).forEach((button) => {
    button.addEventListener("click", clearFeeScheduleFilters);
  });
  if (feeScheduleColumnFilterMenuBound) return;
  const head = document.querySelector(".fee-schedule-table thead");
  if (!head || !head.addEventListener) return;
  feeScheduleColumnFilterMenuBound = true;
  // Delegated: renderFeeScheduleColumnHeaders replaces the buttons on every
  // render, so a per-button listener would be re-bound (and leak) each time.
  head.addEventListener("click", (event) => {
    const button = event.target && event.target.closest ? event.target.closest("[data-fee-column-filter]") : null;
    if (!button) return;
    event.preventDefault();
    const key = button.dataset ? button.dataset.feeColumnFilter : "";
    if (feeScheduleColumnFilterContext && feeScheduleColumnFilterContext.key === key) {
      closeFeeScheduleColumnFilterMenu({ restoreFocus: true });
      return;
    }
    openFeeScheduleColumnFilterMenu(key, button);
  });
}

// The honest empty state: say the filters are hiding rows and offer to lift
// them. "No data" would send a biller to the warehouse team for nothing.
function feeScheduleEmptyRowHtml(scopedRows = []) {
  const columns = FEE_SCHEDULE_FILTER_COLUMNS.length + 1;
  const filters = state.feeScheduleFilters || {};
  const baseFilterActive = Boolean(
    (filters.tenantId && filters.tenantId !== "All")
    || (filters.orgId && filters.orgId !== "All")
    || (filters.payer && filters.payer !== "All")
    || (filters.state && filters.state !== "All")
    || (filters.procedure && filters.procedure !== "All")
    || (filters.network && filters.network !== "All")
    || filters.activeDate
    || filters.search
  );
  const columnFilterCount = feeScheduleColumnFilterCount();
  const hidden = (Array.isArray(scopedRows) ? scopedRows.length : 0);
  // Live rows arrive already filtered by the server, so an empty page with a
  // filter on means the filters matched nothing, not that the book is empty.
  const serverFiltered = feeScheduleApiEnabled() && (baseFilterActive || columnFilterCount);
  if (!serverFiltered && (!hidden || (!baseFilterActive && !columnFilterCount))) {
    const archived = Boolean(filters.showArchived);
    if (typeof feeScheduleClientReadOnly === "function" && feeScheduleClientReadOnly()) {
      return `<tr class="fee-schedule-empty-row"><td colspan="${columns}"><div class="fee-schedule-empty-state" role="status"><div class="fee-schedule-empty-title">No rates on file for your company yet</div><div class="fee-schedule-empty-hint">Contact your TMS team if you expected to see your contracted rates here.</div></div></td></tr>`;
    }
    return `
      <tr class="fee-schedule-empty-row">
        <td colspan="${columns}">
          <div class="fee-schedule-empty-state" role="status">
            <div class="fee-schedule-empty-title">${archived ? "No archived rates in this editable book" : "No rates in this editable book yet"}</div>
            <div class="fee-schedule-empty-hint">${archived ? "There are no archived rows in this editable book for the clients you can see." : "There are no editable rate rows for the clients you can see. This does not mean the active calculation source has no rates."}</div>
          </div>
        </td>
      </tr>
    `;
  }
  const parts = [];
  if (columnFilterCount) parts.push(`${columnFilterCount} column filter${columnFilterCount === 1 ? "" : "s"}`);
  if (baseFilterActive) parts.push("the filter bar above");
  return `
    <tr class="fee-schedule-empty-row">
      <td colspan="${columns}">
        <div class="fee-schedule-empty-state" role="status">
          <div class="fee-schedule-empty-title">No rates match the filters</div>
          <div class="fee-schedule-empty-hint">${escapeHtml(serverFiltered ? `Nothing matches ${parts.join(" and ")}.` : `${parts.join(" and ")} ${parts.length > 1 ? "are" : "is"} hiding ${formatNounCount(hidden, "rate row")}. The rows are still there.`)}</div>
          <button type="button" class="ghost-button" data-fee-clear-all-filters="1">Clear all filters</button>
        </div>
      </td>
    </tr>
  `;
}
function normalizedFeeScheduleSearch(value = "") {
  return String(value || "").toLowerCase().replace(/[^a-z0-9.]+/g, " ").trim();
}

function feeScheduleRowSearchText(row = {}) {
  return normalizedFeeScheduleSearch([
    row.orgId,
    row.orgName,
    row.tenantId,
    row.payer,
    row.externalPayerId,
    row.payorId,
    row.state,
    row.service,
    row.procedure,
    row.m1,
    row.m2,
    row.place,
    row.network,
    row.rate,
    row.startDate,
    row.endDate,
    row.approvalStatus
  ].filter(Boolean).join(" "));
}

function feeScheduleOrgKey(row = {}) {
  return String(row.orgId || "").trim();
}

function feeScheduleTenantKey(row = {}) {
  return String(firstPresent(
    row.tenantId,
    row.tenant,
    row.client,
    row.companyClient,
    tenantNameForOrgId(row.orgId || row.org_id)
  ) || "").trim();
}

function feeScheduleTenantLabel(row = {}) {
  return feeScheduleTenantKey(row) || "Unassigned client/tenant";
}

function tenantNameForOrgId(orgId = "") {
  const normalizedOrgId = String(orgId || "").trim();
  if (!normalizedOrgId) return "";
  const inventoryTenant = databaseTenantInventory.find((entry) => (entry.orgIds || []).includes(normalizedOrgId));
  if (inventoryTenant) return inventoryTenant.tenantName || inventoryTenant.label || inventoryTenant.value || "";
  const apiRow = trackerApiPageState.status === "ok"
    ? (trackerApiPageState.response?.rows || []).find((row) => String(row.orgId || row.org_id || "").trim() === normalizedOrgId)
    : null;
  if (apiRow) return String(apiRow.client || apiRow.tenant || apiRow.tenantId || "").trim();
  const claim = (Array.isArray(sourceClaims) ? sourceClaims : []).find((row) => String(row.orgId || row.org_id || "").trim() === normalizedOrgId);
  return claim ? String(claim.client || claim.tenant || claim.tenantId || "").trim() : "";
}

function databaseTenantClientOptions() {
  const options = (Array.isArray(databaseTenantInventory) ? databaseTenantInventory : [])
    .map((entry) => ({
      value: entry.value || entry.tenantName || entry.label,
      label: entry.label || entry.tenantName || entry.value,
      orgIds: Array.isArray(entry.orgIds) ? entry.orgIds : []
    }))
    .filter((entry) => entry.value && entry.label)
    .sort((a, b) => compareText(a.label, b.label));
  return tenantOptionsForActiveUser(options);
}

// Tenant dropdowns must never reveal other companies to a scoped login.
// Cross-tenant platform staff see the full database inventory; everyone
// else only sees tenants matching their own org IDs / client names --
// mirroring canAccessClaim's deny-by-default scope rules.
function tenantOptionsForActiveUser(options = []) {
  const scope = currentAccessProfile().scope || {};
  // An explicitly assigned-only profile gets no tenant options — fail closed;
  // its visibility is a routed queue, not a set of companies. Org-scoped
  // billers (including offshore since the autonomy change) fall through and
  // get exactly the tenants their orgIds cover.
  if (scope.assignedOnly) return [];
  if (scope.allOrgs || scope.crossTenant) return options;
  const allowedTenants = new Set([
    ...(Array.isArray(scope.tenantIds) ? scope.tenantIds : []),
    ...(Array.isArray(scope.clientNames) ? scope.clientNames : [])
  ].map((value) => String(value || "").trim()).filter(Boolean));
  const allowedOrgs = new Set(
    (Array.isArray(scope.orgIds) ? scope.orgIds : []).map((value) => String(value || "").trim()).filter(Boolean)
  );
  if (!allowedTenants.size && !allowedOrgs.size) return [];
  return options.filter((option) =>
    allowedTenants.has(String(option.value || "").trim())
    || allowedTenants.has(String(option.label || "").trim())
    || (Array.isArray(option.orgIds) ? option.orgIds : []).some((orgId) => allowedOrgs.has(String(orgId || "").trim())));
}

function orgIdsForTenantName(tenantName = "") {
  const normalized = String(tenantName || "").trim();
  if (!normalized || normalized === "All") return [];
  const match = databaseTenantClientOptions().find((entry) => String(entry.value) === normalized || String(entry.label) === normalized);
  return match ? match.orgIds : [];
}

// ---------------------------------------------------------------------------
// GLOBAL CLIENT SWITCHER (MASTER_BACKLOG.md UI-6)
//
// The owner asked for this three times: "when I come into the portal you get to
// pick which tenant login you want to be for all of them ... it shouldn't be
// buried in there somewhere on every page", plus a View All. The driving reason
// is confidentiality on screen shares and client calls - NOT access control.
//
// So: this is a NARROWING FILTER that rides INSIDE the scope the server already
// enforces. It must never be described or tested as an isolation boundary and
// must never travel as a @scope_* parameter -
// verify_cross_tenant_adversarial_isolation.js records the day a user-supplied
// filters.tenant masked a MISSING scope predicate and made a verifier pass
// falsely. The server decides what this principal MAY see; this only decides
// what is on screen right now.
//
// state.activeClientGroup is the one knob. Rather than making eight surfaces
// read a ninth variable at query time, the selection is PUSHED INTO the filter
// state each surface already owns (els.tenantFilter / els.reportTenantFilter,
// state.feeScheduleFilters, state.securityReviewFilters,
// clientPaymentsDashState.org, statementPreviewState.focusOrg,
// authorizationsState.orgId). Every existing query builder and - critically -
// every existing cache key therefore picks it up unchanged: trackerApiCacheKey
// and reportApiCacheKey/reportDatasetKey already hash `filters`, which is where
// the tenant name now comes from. statementPreviewKey did NOT carry the org and
// is widened below, because a preview cached under client A would otherwise
// render for client B - the exact leak this feature exists to prevent.
// ---------------------------------------------------------------------------

// The permitted tenant roster. tenantOptionsForActiveUser() fails closed
// (assignedOnly -> none) and TENANT_DROPDOWN_SCOPE.md:9-17 guarantees these are
// served client tenant NAMES - never ORG-*, a bare numeric org ID, or a demo
// label. No client name is hardcoded anywhere in this module.
function clientScopeOptions() {
  return typeof databaseTenantClientOptions === "function" ? databaseTenantClientOptions() : [];
}

function activeClientGroupEntry() {
  const stored = (typeof state !== "undefined" && state && state.activeClientGroup) || null;
  const options = clientScopeOptions();
  const wanted = String((stored && stored.value) || (options.length === 1 ? options[0].value : "")).trim();
  if (!wanted) return null;
  // Re-resolved against the LIVE inventory on every read. A pick the principal
  // no longer holds (role change, org removed, a different login on the same
  // browser) falls back to All clients instead of pinning the UI to a company
  // this session cannot see, and the org IDs are today's rather than whatever
  // localStorage remembers.
  const match = options.find((option) => String(option.value) === wanted || String(option.label) === wanted);
  if (!match) return null;
  return {
    value: String(match.value),
    label: String(match.label || match.value),
    orgIds: (Array.isArray(match.orgIds) ? match.orgIds : []).map((orgId) => String(orgId || "").trim()).filter(Boolean)
  };
}

// The served client tenant NAME, for the name-space surfaces. "" = All clients.
function activeClientGroupTenant() {
  const entry = activeClientGroupEntry();
  return entry ? entry.value : "";
}

function activeClientGroupLabel() {
  const entry = activeClientGroupEntry();
  return entry ? entry.label : "";
}

function activeClientGroupOrgIds() {
  const entry = activeClientGroupEntry();
  return entry ? entry.orgIds : [];
}

// Single-organization APIs must never widen a selected company to All clients.
// An unresolved mapping returns a nonmatching internal key and a visible notice.
function activeClientGroupServerOrgId() {
  const orgIds = activeClientGroupOrgIds();
  // Never turn an unresolved or multi-organization company into an all-company read.
  return orgIds.length === 1 ? orgIds[0] : (activeClientGroupTenant() ? "__COMPANY_SCOPE_UNAVAILABLE__" : "");
}

function activeClientGroupIsMultiOrg() {
  return activeClientGroupOrgIds().length > 1;
}

function setActiveClientGroup(value) {
  cancelClaimScreenshot();
  // A pending column choice belongs to the prior company. Closing also fences
  // a late facet response from reopening it over the newly scoped grid.
  if (openColumnFilterContext && els.columnFilterMenu && !els.columnFilterMenu.hidden) closeColumnFilterMenu();
  // The current aggregate belongs to the old selection. Hide it before any
  // page work for the new client begins; requestNavigationPendingCounts() will
  // establish the new generation/key during the render below.
  invalidateNavigationPendingCounts();
  const wanted = String(value || "").trim();
  const match = wanted
    ? clientScopeOptions().find((option) => String(option.value) === wanted || String(option.label) === wanted)
    : null;
  state.activeClientGroup = match
    ? {
      value: String(match.value),
      label: String(match.label || match.value),
      orgIds: (Array.isArray(match.orgIds) ? match.orgIds : []).map((orgId) => String(orgId || "").trim()).filter(Boolean)
    }
    : { value: "", label: "", orgIds: [] };
  // CLEAR THE OLD LOCK BEFORE THE ALL-CLIENTS RENDER COLLECTS FILTERS.
  //
  // enforceClientScopeSelectLocks() normally runs after every panel rebuild so
  // newly-created selects cannot escape a live company scope. That ordering is
  // wrong for the one transition from a named company back to All clients: the
  // workbench/report/export collectors run during render(), see the still-locked
  // old option, and issue one more narrowed query while the header already says
  // All clients. The next unrelated render repaired it, leaving a believable but
  // stale company view on screen in the meantime. Unlock the existing controls
  // before render() only for the clear transition; the normal post-render lock
  // still owns every named-company transition.
  if (!match) {
    lockSelectToClientScope(els.tenantFilter, "");
    lockSelectToClientScope(els.reportTenantFilter, "");
    lockSelectToClientScope(els.trackerExportTenant, "");
    lockSelectToClientScope(els.clientTrackerExportTenant, "");
    enforceClientScopeSelectLocks();
  }
  // DEFAULT = LAST USED, not All. The owner's stated fear is landing in "View
  // All" in the middle of a client call, so the safe resting state is the one
  // he chose last; activeClientGroupEntry() re-validates it every read, so a
  // stale or now-forbidden pick degrades to All rather than sticking.
  saveState();
  render();
}

// The applied scope, remembered so the org-ID surfaces are written on CHANGE
// only. Writing them every render would stomp an operator's own per-page pick,
// and picking "All clients" has to hand each page its controls back rather than
// leaving it pinned to the client selected before it.
let lastAppliedClientScopeKey = null;

function applyActiveClientGroupToSurfaces() {
  if (typeof state === "undefined" || !state) return;
  const tenant = activeClientGroupTenant();
  const serverOrgId = activeClientGroupServerOrgId();
  const key = JSON.stringify([tenant, activeClientGroupOrgIds()]);
  if (key === lastAppliedClientScopeKey) return;
  const firstApply = lastAppliedClientScopeKey === null;
  lastAppliedClientScopeKey = key;

  // The header is the company authority for every role. Invalidate held data
  // before rendering the new company; late responses retain their old read key.
  if (window.__tmsOversight) Object.assign(window.__tmsOversight, { orgId: serverOrgId, page: 1, internalReviewPage: 1, pagination: null, homeSummary: null, status: "idle", items: [], proposals: [] });
  assignmentScopeUiState.orgId = serverOrgId;
  resetAssignmentWorkflowScope({ preserveSelection: false });
  assignmentScopeUiState.orgId = serverOrgId;
  resetWorkflowBoard();
  invalidateApprovalQueue();
  supervisorTrailState.filters.orgId = serverOrgId || "All";
  supervisorTrailState.filters.page = 1;
  supervisorTrailState.window = {};
  supervisorTrailRequestKey = "";
  supervisorTrailState.events = [];
  if (state.reportRunFilters) { state.reportRunFilters.tenantId = tenant || "All"; state.reportRunFilters.orgId = serverOrgId || "All"; }
  if (!firstApply) resetSystemPortalDataReadState();
  ["selectedReportRun", "selectedExportJobDetail", "selectedExportJobDownloadPreview", "reportRunAuditExportPreview", "queueWorkExportPreview", "exportGovernanceActivityExportPreview"].forEach(name => { state[name] = null; });

  clientReportAggregateFilters.client = tenant || "All";

  // Org-ID dialect surfaces.
  if (state.feeScheduleFilters) {
    state.feeScheduleFilters.tenantId = tenant || "All";
    state.feeScheduleFilters.orgId = serverOrgId || "All";
  }
  if (state.securityReviewFilters) state.securityReviewFilters.orgId = serverOrgId || "All";
  if (typeof clientPaymentsDashState === "object" && clientPaymentsDashState) {
    clientPaymentsDashState.org = serverOrgId;
    clientPaymentsDashState.expanded = "";
  }
  if (typeof statementPreviewState === "object" && statementPreviewState) statementPreviewState.focusOrg = serverOrgId;
  if (typeof authorizationsState === "object" && authorizationsState) authorizationsState.orgId = serverOrgId;
  if (typeof clientActionWorkspaceState === "object" && clientActionWorkspaceState) {
    clientActionWorkspaceState.orgId = serverOrgId;
    Object.assign(clientActionWorkspaceState, { selectedId: "", selectedOrgId: "", detail: null, detailStatus: "idle", notes: { status: "idle", key: "", items: [], claims: 0, truncated: false } });
  }
  if (typeof credentialingState === "object" && credentialingState) resetCredentialingForClientScope(serverOrgId);

  // The authorization and credentialing boards are held whole in module state
  // rather than derived from filters, so both must be re-ASKED. Everything else
  // is keyed (trackerApiCacheKey / reportApiCacheKey / reportDatasetKey off
  // `filters`, statementPreviewKey and the payment-checks key off the org) and
  // re-requests itself on the next render.
  if (!firstApply
      && typeof authorizationsState === "object" && authorizationsState
      && authorizationsState.status !== "idle"
      && typeof requestAuthorizationBoard === "function") {
    requestAuthorizationBoard({ rerender: false });
  }
  // Client Action holds its rows in module state like the two boards above, so a
  // scope change has to RE-ASK rather than re-filter: the response itself is
  // narrowed for a single-org tenant.
  if (!firstApply
      && typeof clientActionWorkspaceState === "object" && clientActionWorkspaceState
      && clientActionWorkspaceState.status !== "idle"
      && typeof loadClientActionCases === "function") {
    loadClientActionCases(false);
  }
  if (!firstApply
      && typeof credentialingState === "object" && credentialingState
      && credentialingState.status !== "idle"
      && typeof requestCredentialingBoard === "function") {
    requestCredentialingBoard({ rerender: false });
    if (canSendCredentialedClaims()) requestCredentialingQueue({ rerender: false });
  }
}

// A per-page Tenant dropdown must FOLLOW the switcher, never fight it as a
// second independent knob. While a client is selected the dropdown shows that
// client and nothing else and is disabled; choosing "All clients" hands it
// straight back. Same shape as lockReportTenantFilterForScope(), which already
// does exactly this for single-tenant logins - the two coexist because this
// runs last and is the tighter of the two.
// The option list a locked dropdown had BEFORE it was collapsed to one entry.
// Unlocking has to put it back: leaving the single option in place meant
// choosing "All clients" left the select still reading the old client, and
// collectTrackerFilters() falls back to that select - so All Data would have
// gone on quietly filtering to the client the user had just stepped OUT of.
// Caught in the browser proof, not in review.
const clientScopeSelectSnapshots = new WeakMap();

function lockSelectToClientScope(select, tenant) {
  if (!select) return;
  const wanted = String(tenant || "").trim();
  if (!wanted) {
    if (select.dataset && select.dataset.clientScopeLocked === "1") {
      delete select.dataset.clientScopeLocked;
      select.disabled = false;
      select.title = "";
      const snapshot = clientScopeSelectSnapshots.get(select);
      if (snapshot && "innerHTML" in select) select.innerHTML = snapshot;
      clientScopeSelectSnapshots.delete(select);
      // "All clients" means All on the page too, not "still the last client".
      select.value = "All";
    }
    return;
  }
  if (!clientScopeSelectSnapshots.has(select) && "innerHTML" in select) {
    clientScopeSelectSnapshots.set(select, select.innerHTML);
  }
  if ("innerHTML" in select) {
    select.innerHTML = '<option value="' + escapeHtml(wanted) + '">' + escapeHtml(wanted) + "</option>";
  }
  select.value = wanted;
  select.disabled = true;
  select.title = "Every page is narrowed to " + wanted + " by the client switcher in the header.";
  if (select.dataset) select.dataset.clientScopeLocked = "1";
}

// Run AFTER the panels rebuild their own option lists, or a repopulated Tenant
// dropdown quietly unlocks itself.
function enforceClientScopeSelectLocks() {
  const tenant = activeClientGroupTenant();
  lockSelectToClientScope(els.tenantFilter, tenant);
  lockSelectToClientScope(els.reportTenantFilter, tenant);
  // The workbook export takes its tenant from the switcher too, so its own
  // control must not sit there reading "All" over a narrowed export.
  lockSelectToClientScope(els.trackerExportTenant, tenant);
  lockSelectToClientScope(els.clientTrackerExportTenant, tenant);
  lockSelectToClientScope(document.getElementById("clientReportClientFilter"), tenant);
  lockSelectToClientScope(els.feeScheduleOrgFilter, tenant);
  lockSelectToClientScope(els.reportRunTenantFilter, tenant);
  [els.feeScheduleOrgIdFilter, els.reportRunOrgFilter, els.trailOrgFilter].forEach(select => lockOrgSelectToClientScope(select));
}

function lockOrgSelectToClientScope(select) {
  const tenant = activeClientGroupTenant();
  if (!tenant) return lockSelectToClientScope(select, "");
  lockSelectToClientScope(select, activeClientGroupServerOrgId());
  if (select) { select.innerHTML = '<option value="' + escapeHtml(activeClientGroupServerOrgId()) + '">' + escapeHtml(activeClientGroupLabel()) + "</option>"; select.title = "Company selected at the top of the page"; }
}

function renderClientScopeSwitcher() {
  const wrap = els.clientScopeSwitcher;
  if (!wrap || !wrap.classList) return;
  const options = clientScopeOptions();
  const entry = activeClientGroupEntry();
  // Fails closed with the roster it reads: a principal with no tenant options
  // (assignedOnly is a routed queue, not a set of companies) gets no switcher.
  if (!options.length) {
    wrap.classList.add("hidden");
    return;
  }
  wrap.classList.remove("hidden");
  // One tenant means there is nothing to switch between, so it degrades to a
  // static chip - a dropdown of one is furniture (statementsToolbar precedent).
  const single = options.length === 1;
  const select = els.clientScopeSelect;
  if (select && select.classList) {
    select.classList.toggle("hidden", single);
    if (!single && "innerHTML" in select) {
      const markup = ['<option value="">All clients</option>']
        .concat(options.map((option) => '<option value="' + escapeHtml(option.value) + '">' + escapeHtml(option.label) + "</option>"))
        .join("");
      if (select.innerHTML !== markup) select.innerHTML = markup;
      select.value = entry ? entry.value : "";
    }
  }
  const chip = els.clientScopeChip;
  if (chip && chip.classList) {
    chip.classList.toggle("hidden", !single);
    if (single) chip.textContent = options[0].label;
  }
  wrap.classList.toggle("is-narrowed", Boolean(entry));
  if (wrap.dataset) {
    wrap.dataset.clientScope = entry ? entry.value : "all";
    wrap.dataset.clientScopeOrgId = activeClientGroupServerOrgId();
    // Proof surface: what the SERVER said it bound, echoed back from the two
    // routes that report it. "" until that route has answered.
    wrap.dataset.serverFocusAuthorizations = clientScopeServerEcho.authorizations;
    wrap.dataset.serverFocusStatements = clientScopeServerEcho.statements;
  }
}

// focusOrgId as echoed by the server, per lane. Recorded so the narrowing can
// be proven from the SERVER's answer instead of from the DOM.
const clientScopeServerEcho = { authorizations: "", statements: "" };

// Reference documentation is shared; it contains no company-owned work.
const CLIENT_SCOPE_UNNARROWED_VIEWS = {
  followUpRules: "Follow-up rules are portal-wide",
  queryMap: "The query map is portal-wide",
  // Reference documentation has no company-owned records.
};

function clientScopeNoticeText() {
  const entry = activeClientGroupEntry();
  if (!entry) return "";
  const viewReason = CLIENT_SCOPE_UNNARROWED_VIEWS[state.view];
  if (viewReason) {
    return viewReason + ". Company-specific work remains scoped to " + entry.label + ".";
  }
  if (entry.orgIds.length !== 1) {
    return "The company mapping for " + entry.label + " needs attention. Pages that require one company cannot load until its mapping is resolved.";
  }
  return "";
}

function renderClientScopeNotice() {
  const el = els.clientScopeNotice;
  if (!el || !el.classList) return;
  const text = clientScopeNoticeText();
  el.textContent = text;
  el.classList.toggle("hidden", !text);
}

// Manage Users / Manage Groups are modals, not views, so they get the same
// sentence inside their own header rather than under the topbar.
function renderClientScopeModalNotes() {
  const entry = activeClientGroupEntry();
  const text = entry
    ? "Showing " + entry.label + ". Change the company at the top of the page to view another company."
    : "";
  ["userMgmtClientScopeNote", "userGroupMgmtClientScopeNote"].forEach((id) => {
    const node = typeof document !== "undefined" && document.getElementById ? document.getElementById(id) : null;
    if (!node || !node.classList) return;
    node.textContent = text;
    node.classList.toggle("hidden", !text);
  });
}

function feeScheduleRowMatchesTenant(row = {}, selectedTenant = "") {
  const selected = String(selectedTenant || "").trim();
  if (!selected || selected === "All") return true;
  const tenantKey = feeScheduleTenantKey(row);
  if (tenantKey === selected) return true;
  const rowOrgId = String(row.orgId || row.org_id || "").trim();
  return Boolean(rowOrgId && orgIdsForTenantName(selected).includes(rowOrgId));
}

function feeScheduleRowActiveOn(row = {}, dateValue = "") {
  const active = String(dateValue || "").trim();
  if (!active) return true;
  const start = String(row.startDate || "").trim();
  const end = String(row.endDate || "").trim();
  if (start && start > active) return false;
  if (end && end < active) return false;
  return true;
}

function feeScheduleBlankFilterOptions(rows = [], field = "", selected = "All") {
  const values = rows.map((row) => String(row[field] || "") || "__unavailable__");
  // Shared worklist filters can remain selected when this rate book has no blanks.
  if (selected === "__unavailable__") values.push(selected);
  return uniqueSorted(values).map((value) => ({ value, label: value === "__unavailable__" ? "Not specified" : value }));
}

function syncFeeScheduleFilterControls(rows = feeScheduleDisplayRows()) {
  const filters = feeScheduleEffectiveFilters();
  replaceSelectOptionsFromObjects(els.feeScheduleOrgFilter, feeScheduleTenantOptions(rows), filters.tenantId || "All", "All");
  replaceSelectOptionsFromObjects(els.feeScheduleOrgIdFilter, feeScheduleOrgIdOptions(rows, filters.tenantId || "All"), filters.orgId || "All", "All org IDs");
  replaceSelectOptionsWithLabel(els.feeSchedulePayerFilter, uniqueSorted(rows.map((row) => row.payer)), filters.payer || "All", "All payers");
  replaceSelectOptionsFromObjects(els.feeScheduleStateFilter, feeScheduleBlankFilterOptions(rows, "state", filters.state), filters.state || "All", "All states");
  replaceSelectOptionsWithLabel(els.feeScheduleProcedureFilter, uniqueSorted(rows.map((row) => row.procedure)), filters.procedure || "All", "All procedures");
  replaceSelectOptionsFromObjects(els.feeScheduleNetworkFilter, feeScheduleBlankFilterOptions(rows, "network", filters.network), filters.network || "All", "All networks");
  if (els.feeScheduleActiveDateFilter) els.feeScheduleActiveDateFilter.value = filters.activeDate || "";
  if (els.feeScheduleSearchInput) els.feeScheduleSearchInput.value = filters.search || "";
  if (els.feeScheduleShowArchived) els.feeScheduleShowArchived.checked = Boolean(filters.showArchived);
  const tenant = activeClientGroupTenant();
  lockSelectToClientScope(els.feeScheduleOrgFilter, tenant);
  if (els.feeScheduleOrgIdFilter) {
    els.feeScheduleOrgIdFilter.disabled = Boolean(tenant && activeClientGroupOrgIds().length === 1);
    els.feeScheduleOrgIdFilter.title = tenant ? "Company scope follows the client selected in the header." : "";
  }
  if (feeScheduleApiEnabled()) {
    [[els.feeScheduleOrgFilter,"client",filters.tenantId],[els.feeScheduleOrgIdFilter,"orgId",filters.orgId],[els.feeSchedulePayerFilter,"payer",filters.payer],[els.feeScheduleStateFilter,"state",filters.state],[els.feeScheduleProcedureFilter,"procedure",filters.procedure],[els.feeScheduleNetworkFilter,"network",filters.network]].forEach(([select,key,value]) => {
      if (!select) return; if (value && !Array.from(select.options).some(option => option.value === value)) select.add(new Option(value === "__unavailable__" ? "Not specified" : value, value));
      select.value = value || "All"; select.dataset.feeSetupChoice = key; select.dataset.feeSearchableSelect = "";
      const input = select.nextElementSibling?.querySelector('[data-fee-search-input]'); if (input && document.activeElement !== input) input.value = select.selectedOptions[0]?.textContent || "";
      enhanceFeeSearchSelects(select.parentElement);
      const enhancedInput = select.nextElementSibling?.querySelector("[data-fee-search-input]");
      if (enhancedInput) { enhancedInput.disabled = select.disabled; if (select.disabled) { const choices = select.nextElementSibling?.querySelector(".fee-search-options"); if (choices) choices.hidden = true; enhancedInput.setAttribute("aria-expanded", "false"); } }
    });
  }
}

function feeScheduleTenantOptions(rows = []) {
  const map = new Map();
  const allDataOptions = allDataTenantClientOptions();
  allDataOptions.forEach((option) => {
    map.set(option.value, option.label);
  });
  if (allDataOptions.length) {
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => compareText(a.value, b.value));
  }
  rows.forEach((row) => {
    const key = feeScheduleTenantKey(row);
    if (!key || map.has(key)) return;
    map.set(key, feeScheduleTenantLabel(row));
  });
  return Array.from(map.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => compareText(a.value, b.value));
}

function feeScheduleOrgIdOptions(rows = [], tenantId = "All") {
  const selectedTenant = String(tenantId || "All").trim();
  const map = new Map();
  const addOrg = (orgId, labelTenant = "") => {
    const value = String(orgId || "").trim();
    if (!value || map.has(value)) return;
    const tenant = String(labelTenant || tenantNameForOrgId(value) || "").trim();
    map.set(value, { value, label: tenant ? `${value} - ${tenant}` : value });
  };
  if (selectedTenant && selectedTenant !== "All") {
    orgIdsForTenantName(selectedTenant).forEach((orgId) => addOrg(orgId, selectedTenant));
  }
  (Array.isArray(rows) ? rows : [])
    .filter((row) => !selectedTenant || selectedTenant === "All" || feeScheduleRowMatchesTenant(row, selectedTenant))
    .forEach((row) => addOrg(row.orgId || row.org_id, feeScheduleTenantKey(row)));
  return Array.from(map.values()).sort((a, b) => compareText(a.value, b.value));
}

function allDataTenantClientOptions() {
  const inventoryOptions = databaseTenantClientOptions();
  if (inventoryOptions.length) return inventoryOptions;
  return allDataTenantClientValues()
    .map((value) => ({ value, label: value, orgIds: [] }))
    .sort((a, b) => compareText(a.label, b.label));
}

function allDataTenantClientValues() {
  const inventoryOptions = databaseTenantClientOptions();
  if (inventoryOptions.length) return inventoryOptions.map((option) => option.value);
  const apiValues = trackerApiFacetValues("tenant");
  const apiClientValues = trackerApiFacetValues("client");
  const sourceValues = uniqueValues(getClaims(), "client");
  return uniqueSorted([...apiValues, ...apiClientValues, ...sourceValues].filter((value) => value && value !== "Blank"));
}

function trackerApiFacetValues(key = "") {
  const rows = trackerApiPageState.response?.facets?.[key] || [];
  return rows.map((row) => cleanFacetDisplayValue(row.value)).filter(Boolean);
}

function replaceSelectOptionsFromObjects(select, options = [], selected = "All", allLabel = "All") {
  if (!select) return;
  const normalizedSelected = selected || "All";
  const hasSelected = normalizedSelected === "All" || options.some((option) => String(option.value) === String(normalizedSelected));
  select.innerHTML = [
    `<option value="All">${escapeHtml(allLabel)}</option>`,
    !hasSelected ? `<option value="${escapeHtml(normalizedSelected)}">${escapeHtml(normalizedSelected)} (no matching setup rows yet)</option>` : "",
    ...options.map((option) => `<option value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</option>`)
  ].filter(Boolean).join("");
  select.value = [...select.options].some((option) => option.value === normalizedSelected) ? normalizedSelected : "All";
}

function replaceSelectOptionsWithLabel(select, values = [], selected = "All", allLabel = "All") {
  replaceSelectOptionsFromObjects(select, values.map((value) => ({ value, label: value })), selected, allLabel);
}

function uniqueSorted(values = []) {
  return Array.from(new Set(values.map((value) => String(value || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

function bindFeeScheduleFilter(element, key, property = "value") {
  if (!element) return;
  const update = () => {
    if ((key === "tenantId" || (key === "orgId" && activeClientGroupOrgIds().length === 1)) && activeClientGroupTenant()) {
      syncFeeScheduleFilterControls();
      return;
    }
    const value = element[property] || (key === "activeDate" || key === "search" ? "" : "All");
    if ((state.feeScheduleFilters || {})[key] === value) return;
    state.feeScheduleFilters = { ...(state.feeScheduleFilters || {}), [key]: value };
    if (key === "tenantId") state.feeScheduleFilters.orgId = "All";
    saveState();
    renderFeeSchedules();
  };
  element.addEventListener("input", update);
  element.addEventListener("change", update);
}

function clearFeeScheduleFilters() {
  state.feeScheduleFilters = { tenantId: "All", orgId: "All", payer: "All", state: "All", procedure: "All", network: "All", activeDate: "", search: "" };
  state.feeScheduleFilters = feeScheduleEffectiveFilters();
  state.feeScheduleColumnFilters = {};
  closeFeeScheduleColumnFilterMenu();
  saveState();
  renderFeeScheduleSurfaces();
}

const FEE_SCHEDULE_CSV_COLUMNS = [
  "tenantId",
  "orgId",
  "orgName",
  "payer",
  "externalPayerId",
  "state",
  "service",
  "procedure",
  "m1",
  "m2",
  "place",
  "network",
  "rate",
  "startDate",
  "endDate",
  // Secondary Calc is part of a rate's identity: an export/import round trip
  // without it re-keys PR Amount rates into duplicate Default rates.
  "secondaryCalc",
  "approvalStatus",
  "duplicateCheck"
];

function exportFilteredFeeScheduleRows() {
  if (!hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES)) {
    denyAction(PERMISSIONS.VIEW_FEE_SCHEDULES, "export fee schedule rates");
    return;
  }
  if (feeScheduleApiEnabled()) return requestFeeSetupCsv();
  const rows = filteredFeeScheduleRows(feeScheduleDisplayRows());
  const lines = [
    FEE_SCHEDULE_CSV_COLUMNS.join(","),
    ...rows.map((row) => FEE_SCHEDULE_CSV_COLUMNS.map((column) => csvEscape(feeScheduleCsvValue(row, column))).join(","))
  ];
  downloadTextPayload(lines.join("\n"), `tms-fee-schedule-rates-${localDateString()}.csv`, "text/csv");
  showToast(`Exported ${formatCount(rows.length)} filtered fee schedule row${rows.length === 1 ? "" : "s"}.`);
}

// Reads EVERY rate the current setup filters match, page by page, from one
// revision. The grid holds only the visible page, so anything acting on "all
// filtered rates" (export, bulk edit) reads through here. Returns null when
// stillCurrent() turns false mid-read.
async function readAllFilteredFeeSetupRows(stillCurrent = () => true) {
  const token = workflowApiToken() || trackerApiToken(), rows = [];
  let revision = "", expectedTotal = null, page = 1;
  while (true) {
    const params = feeSetupPageParams(); params.set("page", String(page)); params.set("operation", "page");
    const response = await fetch(workflowApiBase() + "/api/setup/fee-schedule-rates?" + params, { cache: "no-store", headers: { accept: "application/json", ...(token ? { authorization: "Bearer " + token } : {}) } });
    const body = await response.json().catch(() => ({}));
    if (!stillCurrent()) return null;
    if (!response.ok || body.complete !== true || body.truncated === true || !Array.isArray(body.rows)) throw new Error(body.message || "The complete rate list is unavailable.");
    if (!revision) revision = String(body.revision || "");
    else if (!body.revision || String(body.revision) !== revision) throw new Error("Rates changed while reading. Reload rates and try again.");
    const total = Number(body.page?.totalRows);
    if (!Number.isSafeInteger(total) || total < 0) throw new Error("The rate list did not provide a complete row count.");
    if (expectedTotal === null) expectedTotal = total;
    else if (total !== expectedTotal) throw new Error("Rates changed while reading. Reload rates and try again.");
    rows.push(...body.rows);
    if (rows.length >= expectedTotal) break;
    if (!body.rows.length || page >= Math.max(1, Number(body.page?.totalPages) || Math.ceil(expectedTotal / 50))) throw new Error("Reading stopped before every filtered rate was loaded.");
    page += 1;
  }
  if (rows.length !== expectedTotal) throw new Error("Rates changed while reading. Reload rates and try again.");
  return rows;
}

async function downloadFeeSetupDirectCsv(scope = "") {
  const rows = await readAllFilteredFeeSetupRows(() => scope === preparedReportExportAccess());
  if (!rows) return 0;
  if (!rows.length) { showToast("No rates match the current filters."); return 0; }
  const lines = [FEE_SCHEDULE_CSV_COLUMNS.join(","), ...rows.map((row) => FEE_SCHEDULE_CSV_COLUMNS.map((column) => csvEscape(feeScheduleCsvValue(row, column))).join(","))];
  downloadTextPayload(lines.join("\r\n"), `tms-fee-schedule-rates-${localDateString()}.csv`, "text/csv;charset=utf-8");
  showToast(`Exported ${formatCount(rows.length)} filtered fee schedule row${rows.length === 1 ? "" : "s"}.`);
  return rows.length;
}

async function requestFeeSetupCsv() {
  if (!hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES)) return;
  if (!feeScheduleApiRequestStillCurrent(feeScheduleApiState.key) || feeScheduleApiState.status !== "ok" || !feeScheduleApiState.revision) return showToast("Wait for current rates before exporting.");
  const scope = preparedReportExportResetScope(), params = feeSetupPageParams(), report = "feeScheduleRates";
  const rateFilters = Object.fromEntries(new URLSearchParams(feeScheduleApiQuery())); delete rateFilters.limit;
  const filters = { rateFilters, setupFilters: JSON.parse(params.get("setupFilters")), columnFilters: feeScheduleColumnFilterEntries(), feeRevision: feeScheduleApiState.revision };
  const key = JSON.stringify({ scope, report, filters }); if (preparedReportExportRequests.has(key)) return; preparedReportExportRequests.add(key); renderPreparedReportExportStatus();
  try {
    const token = workflowApiToken() || trackerApiToken();
    const response = await fetch(workflowApiBase() + "/api/report-export-jobs", { method: "POST", cache: "no-store", headers: { "content-type": "application/json", ...(token ? { authorization: "Bearer " + token } : {}) }, body: JSON.stringify({ report, filters }) });
    const body = await response.json().catch(() => ({})); if (scope !== preparedReportExportAccess()) return;
    if ([404, 501, 503].includes(response.status)) { await downloadFeeSetupDirectCsv(scope); return; }
    if (response.status !== 202 || !(body.id || body.jobId)) throw new Error(body.error?.message || body.message || "The complete rate export could not be queued.");
    const id = body.id || body.jobId, job = { ...body, id, report, scope }; preparedReportExportJobs.set(id, job);
    while (preparedReportExportJobs.size > 50) preparedReportExportJobs.delete(preparedReportExportJobs.keys().next().value);
    renderPreparedReportExportStatus(); if (job.status === "completed") await downloadPreparedReportCsv(job); else { showToast("Your export is being prepared. You can keep working."); schedulePreparedReportExportPoll(); }
  } catch (error) { if (scope === preparedReportExportAccess()) { showToast("We couldn’t create your download. Please try again."); restorePreparedReportExportJobs(true); } }
  finally { preparedReportExportRequests.delete(key); renderPreparedReportExportStatus(); }
}

// ---------------------------------------------------------------------------
// FEE SCHEDULE BULK EDIT — set one column to one value on every filtered rate.
//
// Owner, 2026-10-19: changing Secondary Calc to PR Amount on 44 PeachState rates
// one dropdown at a time (each a save and reload) was unusable; he wants to
// change fee schedules "in mass" routinely. The filters already select the
// rows; this applies one value to all of them, across every page, in one
// confirmed batch through the existing import route (an upsert by
// feeScheduleRateId, the same write a single grid edit makes, with the same
// server-side scope, validation and approved-rate conflict checks).
// Identity columns (company, payer, CPT, modifiers, POS) are deliberately not
// offered: changing those in bulk would re-key rates, not edit them.
// ---------------------------------------------------------------------------
const FEE_SCHEDULE_BULK_FIELDS = [
  { field: "secondaryCalc", label: "Secondary Calc", options: [["PR Amount", "PR Amount"], ["Allowed Amount", "Allowed Amount"], ["", "— (default)"]] },
  { field: "approvalStatus", label: "Approval", options: [["approved", "Approved"], ["draft", "Draft"]] },
  { field: "rate", label: "Rate", type: "number" },
  { field: "startDate", label: "Start Date", type: "date" },
  { field: "endDate", label: "End Date", type: "date", allowBlank: true },
  { field: "network", label: "Network", type: "text" },
  { field: "state", label: "State", type: "text" }
];
const FEE_SCHEDULE_BULK_CHUNK = 500;
let feeScheduleBulkRunning = false;

function feeScheduleBulkConfig(field = els.feeScheduleBulkField?.value) {
  return FEE_SCHEDULE_BULK_FIELDS.find((entry) => entry.field === field) || FEE_SCHEDULE_BULK_FIELDS[0];
}

function toggleFeeScheduleBulkPanel(open) {
  const panel = els.feeScheduleBulkPanel;
  if (!panel) return;
  const show = typeof open === "boolean" ? open : panel.hidden;
  if (show && !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "bulk edit fee schedule rates");
  panel.hidden = !show;
  els.bulkEditFeeScheduleBtn?.setAttribute("aria-expanded", show ? "true" : "false");
  if (!show) return;
  if (els.feeScheduleBulkField && !els.feeScheduleBulkField.options.length) {
    els.feeScheduleBulkField.innerHTML = FEE_SCHEDULE_BULK_FIELDS.map((entry) => `<option value="${escapeHtml(entry.field)}">${escapeHtml(entry.label)}</option>`).join("");
  }
  renderFeeScheduleBulkValueControl();
  setFeeScheduleBulkStatus("");
  els.feeScheduleBulkField?.focus();
}

function renderFeeScheduleBulkValueControl() {
  const host = els.feeScheduleBulkValueHost;
  if (!host) return;
  const config = feeScheduleBulkConfig();
  host.innerHTML = config.options
    ? `<select id="feeScheduleBulkValue" aria-label="New ${escapeHtml(config.label)}">${config.options.map(([value, label]) => `<option value="${escapeHtml(value)}">${escapeHtml(label)}</option>`).join("")}</select>`
    : `<input id="feeScheduleBulkValue" type="${config.type}" ${config.type === "number" ? 'min="0.01" step="0.01" ' : ""}autocomplete="off" aria-label="New ${escapeHtml(config.label)}"${config.allowBlank ? ' placeholder="Blank clears it"' : ""}>`;
  setFeeScheduleBulkStatus("");
}

function setFeeScheduleBulkStatus(message = "", isError = false) {
  if (!els.feeScheduleBulkStatus) return;
  els.feeScheduleBulkStatus.textContent = message;
  els.feeScheduleBulkStatus.className = "fee-schedule-bulk-status" + (isError ? " fee-schedule-bulk-status--error" : "");
}

function feeScheduleBulkComparable(field, value) {
  const text = value == null ? "" : String(value).trim();
  if (field === "rate") return text === "" ? "" : moneyNumber(text).toFixed(2);
  if (field === "approvalStatus") return text.toLowerCase() === "approved" ? "approved" : "draft";
  if (field === "secondaryCalc") return /pr\s*amount/i.test(text) ? "PR Amount" : /allowed/i.test(text) ? "Allowed Amount" : "";
  if (field === "state") return text.toUpperCase();
  return text;
}

// Pure: which rows change, and to what. Exposed for the focused verifier.
function feeScheduleBulkPlan(rows = [], field = "", rawValue = "") {
  const value = feeScheduleBulkComparable(field, rawValue);
  const active = (Array.isArray(rows) ? rows : []).filter((row) => String(row.status || "active") !== "archived");
  const changes = active
    .filter((row) => feeScheduleBulkComparable(field, row[field]) !== value)
    .map((row) => ({ ...row, [field]: field === "rate" ? normalizeOptionalNumber(value) : value }));
  return { value, matched: active.length, unchanged: active.length - changes.length, changes };
}

async function postFeeScheduleBulkChunk(payloads, token) {
  const response = await fetch(`${workflowApiBase()}/api/setup/fee-schedule-rates/import`, {
    method: "POST",
    cache: "no-store",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ rates: payloads })
  });
  const body = await response.json().catch(() => ({}));
  if (response.status === 401) {
    if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    throw new Error("Your session expired. Sign in again — this page will still be here.");
  }
  const requested = Number(body.requestedRows), persisted = Number(body.persistedRows), failed = Number(body.failedRows);
  const counted = Number.isInteger(requested) && requested === payloads.length && Number.isInteger(persisted) && Number.isInteger(failed) && persisted + failed === requested;
  // An all-rows-failed batch is a 400 that still carries per-row counts.
  if (!counted) throw new Error(body.message || body.error?.message || body.error || `The server did not confirm the save (HTTP ${response.status}).`);
  return { persisted, failed, conflicts: Number(body.conflictFailureRows || 0), firstFailure: Array.isArray(body.failures) && body.failures[0] ? String(body.failures[0].message || "") : "" };
}

async function applyFeeScheduleBulkEdit() {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "bulk edit fee schedule rates");
  if (feeScheduleBulkRunning) return;
  const config = feeScheduleBulkConfig();
  const rawValue = String(document.getElementById("feeScheduleBulkValue")?.value ?? "").trim();
  if (config.field === "rate" && !(moneyNumber(rawValue) > 0)) return setFeeScheduleBulkStatus("Enter a rate greater than 0.", true);
  if (!config.options && !config.allowBlank && !rawValue) return setFeeScheduleBulkStatus(`Enter a new ${config.label}.`, true);
  if (state.feeScheduleFilters?.showArchived) return setFeeScheduleBulkStatus("Turn off Show archived — bulk edit changes active rates only.", true);
  if (feeSchedulePendingRowSaves.size) return setFeeScheduleBulkStatus("A row is still saving. Try again in a moment.", true);
  const apiMode = feeScheduleApiEnabled();
  if (apiMode && !(["ok", "saving"].includes(feeScheduleApiState.status) && feeScheduleApiState.complete === true)) return setFeeScheduleBulkStatus("Wait for the rates to finish loading.", true);
  feeScheduleBulkRunning = true;
  if (els.feeScheduleBulkApplyBtn) els.feeScheduleBulkApplyBtn.disabled = true;
  try {
    setFeeScheduleBulkStatus("Reading every filtered rate…");
    const listKey = feeScheduleApiState.key;
    const rows = apiMode
      ? await readAllFilteredFeeSetupRows(() => feeScheduleApiRequestStillCurrent(listKey))
      : filteredFeeScheduleRows(feeScheduleDisplayRows());
    if (!rows) return setFeeScheduleBulkStatus("Filters changed while reading. Check the filters and try again.", true);
    const plan = feeScheduleBulkPlan(apiMode ? normalizeFeeScheduleRows(rows) : rows, config.field, rawValue);
    const shown = config.options ? (config.options.find(([value]) => value === plan.value)?.[1] || plan.value) : (plan.value || "blank");
    if (!plan.matched) return setFeeScheduleBulkStatus("No active rates match the current filters.", true);
    if (!plan.changes.length) return setFeeScheduleBulkStatus(`All ${formatCount(plan.matched)} filtered rates already have ${config.label} = ${shown}.`);
    const already = plan.unchanged ? ` (${formatCount(plan.unchanged)} already have it)` : "";
    if (typeof window !== "undefined" && typeof window.confirm === "function"
      && !window.confirm(`Set ${config.label} to "${shown}" on ${formatNounCount(plan.changes.length, "rate")}${already}?\n\nThis applies to every rate matching the current filters, not just this page.`)) {
      return setFeeScheduleBulkStatus("Cancelled. Nothing was changed.");
    }
    if (!apiMode) {
      plan.changes.forEach((row) => {
        const index = row.__sourceIndex;
        if (Number.isInteger(index) && state.feeScheduleRows[index]) {
          const { __sourceIndex, ...clean } = row;
          state.feeScheduleRows[index] = clean;
        }
      });
      bumpFeeScheduleMatchEpoch(); resetDerivedCaches();
      audit(`Fee schedule ${config.field} bulk updated on ${plan.changes.length} rates`, activeInternalUser());
      saveState(); renderFeeSchedules();
      setFeeScheduleBulkStatus(`Updated ${formatNounCount(plan.changes.length, "rate")}.`);
      showToast(`Updated ${config.label} on ${formatNounCount(plan.changes.length, "rate")}.`);
      return;
    }
    const payloads = plan.changes.map(feeScheduleApiPayload).filter(Boolean)
      .map((payload) => ({ ...payload, metadata: { ...(payload.metadata || {}), source: "fee-schedules-bulk-edit", bulkField: config.field } }));
    const incomplete = plan.changes.length - payloads.length;
    const token = workflowApiToken() || trackerApiToken();
    const totals = { persisted: 0, failed: 0, conflicts: 0, firstFailure: "" };
    for (let start = 0; start < payloads.length; start += FEE_SCHEDULE_BULK_CHUNK) {
      setFeeScheduleBulkStatus(`Saving ${formatCount(Math.min(start + FEE_SCHEDULE_BULK_CHUNK, payloads.length))} of ${formatCount(payloads.length)}…`);
      const result = await postFeeScheduleBulkChunk(payloads.slice(start, start + FEE_SCHEDULE_BULK_CHUNK), token);
      totals.persisted += result.persisted; totals.failed += result.failed; totals.conflicts += result.conflicts;
      if (!totals.firstFailure && result.firstFailure) totals.firstFailure = result.firstFailure;
    }
    feeScheduleApiState = { ...feeScheduleApiState, savedAt: timestamp() };
    resetDerivedCaches();
    audit(`Fee schedule ${config.field} bulk updated on ${totals.persisted} rates`, activeInternalUser());
    refreshFeeScheduleMissingAfterPersistedMutation();
    const problems = totals.failed + incomplete;
    const detail = [
      totals.conflicts ? `${formatCount(totals.conflicts)} blocked by an overlapping approved rate` : "",
      incomplete ? `${formatCount(incomplete)} missing org, CPT or rate` : "",
      totals.firstFailure && totals.failed > totals.conflicts ? totals.firstFailure : ""
    ].filter(Boolean).join("; ");
    const message = `Updated ${formatNounCount(totals.persisted, "rate")}.` + (problems ? ` ${formatCount(problems)} not changed${detail ? ` — ${detail}` : ""}.` : "");
    setFeeScheduleBulkStatus(message, problems > 0);
    showToast(message);
  } catch (error) {
    setFeeScheduleBulkStatus(`Bulk edit stopped: ${error.message}`, true);
    refreshFeeScheduleMissingAfterPersistedMutation();
  } finally {
    feeScheduleBulkRunning = false;
    if (els.feeScheduleBulkApplyBtn) els.feeScheduleBulkApplyBtn.disabled = false;
  }
}

function downloadFeeScheduleImportTemplate() {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) {
    denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "download fee schedule import template");
    return;
  }
  const example = {
    tenantId: "Example Company Client",
    orgId: "90000003",
    orgName: "Example ABA Company",
    payer: "Example Commercial Payer",
    externalPayerId: "12345",
    state: "NJ",
    service: "ABA Therapy",
    procedure: "97153",
    m1: "",
    m2: "",
    place: "11",
    network: "In Network",
    rate: "95.00",
    startDate: "2026-01-01",
    endDate: "",
    secondaryCalc: "",
    approvalStatus: "draft",
    duplicateCheck: "portal-import"
  };
  const lines = [
    FEE_SCHEDULE_CSV_COLUMNS.join(","),
    FEE_SCHEDULE_CSV_COLUMNS.map((column) => csvEscape(example[column])).join(",")
  ];
  downloadTextPayload(lines.join("\n"), `tms-fee-schedule-import-template-${localDateString()}.csv`, "text/csv");
  showToast("Downloaded fee schedule import template.");
}

function importFeeScheduleCsv(event) {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) {
    denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "import fee schedule rates");
    if (event?.target) event.target.value = "";
    return;
  }
  const file = event?.target?.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    try {
      const parsed = parseFeeScheduleCsv(String(reader.result || ""));
      if (!parsed.rows.length) {
        showToast("No complete fee schedule rows were found in the CSV.");
        return;
      }
      if (parsed.rows.some((row) => !feeScheduleRowInSelectedCompany(row))) throw new Error("The CSV contains a different company. Select All clients to import across your permitted companies.");
      if (feeScheduleApiEnabled()) {
        showToast("Saving " + formatCount(parsed.rows.length) + " CSV rows. Only confirmed rates will appear.");
        persistFeeScheduleRows(parsed.rows, { showToast: true });
      } else {
        state.feeScheduleRows = normalizeFeeScheduleRows([...(state.feeScheduleRows || []), ...parsed.rows]);
        bumpFeeScheduleMatchEpoch(); saveState(); renderFeeSchedules();
        showToast("Added " + formatCount(parsed.rows.length) + " rate rows locally.");
      }
      if (parsed.skippedRows) showToast(formatCount(parsed.skippedRows) + " CSV rows need cleanup and were not imported.");
    } catch (error) {
      showToast(`Fee schedule CSV import failed: ${error.message}`);
    } finally {
      if (event?.target) event.target.value = "";
    }
  };
  reader.onerror = () => {
    showToast("Fee schedule CSV import failed: file could not be read.");
    if (event?.target) event.target.value = "";
  };
  reader.readAsText(file);
}

function parseFeeScheduleCsv(text = "") {
  const rows = parseCsvRows(text);
  if (!rows.length) return { rows: [], skippedRows: 0 };
  const headers = rows[0].map((header) => feeScheduleCsvHeaderKey(header));
  if (!headers.includes("procedure") || !headers.includes("rate")) {
    throw new Error("CSV must include at least procedure and rate columns.");
  }
  let skippedRows = 0;
  const imported = rows.slice(1).map((cells) => {
    const raw = {};
    headers.forEach((header, index) => {
      if (!header) return;
      raw[header] = String(cells[index] || "").trim();
    });
    // Validate the raw cell before normalization: currency punctuation alone is not $0.
    if (!/[0-9]/.test(String(raw.rate || "")) || !Number.isFinite(parseMoneyNumber(raw.rate)) || parseMoneyNumber(raw.rate) < 0) return null;
    return normalizeFeeScheduleRow({
      tenantId: raw.tenantId,
      orgId: raw.orgId,
      orgName: raw.orgName || raw.tenantId,
      payer: raw.payer,
      externalPayerId: raw.externalPayerId,
      state: raw.state,
      service: raw.service,
      procedure: raw.procedure,
      m1: raw.m1,
      m2: raw.m2,
      place: raw.place,
      network: raw.network,
      rate: raw.rate,
      startDate: raw.startDate,
      endDate: raw.endDate,
      secondaryCalc: raw.secondaryCalc,
      approvalStatus: raw.approvalStatus || "draft",
      duplicateCheck: raw.duplicateCheck || "portal-csv-import"
    });
  }).filter((row) => {
    if (!row) { skippedRows += 1; return false; }
    const parsedRate = parseMoneyNumber(row.rate);
    const complete = Boolean(row.orgId && row.procedure && row.rate !== "" && Number.isFinite(parsedRate) && parsedRate >= 0);
    if (!complete) skippedRows += 1;
    return complete;
  });
  return { rows: imported, skippedRows };
}

function parseCsvRows(text = "") {
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  const input = String(text || "").replace(/^\uFEFF/, "");
  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    const next = input[index + 1];
    if (quoted) {
      if (char === "\"" && next === "\"") {
        cell += "\"";
        index += 1;
      } else if (char === "\"") {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === "\"") {
      quoted = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else if (char !== "\r") {
      cell += char;
    }
  }
  row.push(cell);
  rows.push(row);
  return rows.filter((csvRow) => csvRow.some((value) => String(value || "").trim()));
}

function feeScheduleCsvHeaderKey(header = "") {
  const normalized = String(header || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  const aliases = {
    company: "tenantId",
    companyclient: "tenantId",
    client: "tenantId",
    tenant: "tenantId",
    tenantid: "tenantId",
    org: "orgId",
    orgid: "orgId",
    orgname: "orgName",
    companyname: "orgName",
    payer: "payer",
    payor: "payer",
    payername: "payer",
    payorname: "payer",
    externalpayerid: "externalPayerId",
    state: "state",
    service: "service",
    procedure: "procedure",
    procedurecode: "procedure",
    cpt: "procedure",
    m1: "m1",
    modifier1: "m1",
    m2: "m2",
    modifier2: "m2",
    place: "place",
    placeofservice: "place",
    pos: "place",
    network: "network",
    inoutofnetwork: "network",
    rate: "rate",
    amount: "rate",
    contractamountperunit: "rate",
    startdate: "startDate",
    effectivestartdate: "startDate",
    enddate: "endDate",
    effectiveenddate: "endDate",
    secondarycalc: "secondaryCalc",
    secondarycalculation: "secondaryCalc",
    approval: "approvalStatus",
    approvalstatus: "approvalStatus",
    duplicatecheck: "duplicateCheck"
  };
  return aliases[normalized] || "";
}

function feeScheduleCsvValue(row = {}, column = "") {
  if (column === "rate") return moneyNumber(row.rate || 0).toFixed(2);
  return row[column] ?? "";
}

function feeScheduleApiEnabled() {
  return Boolean(workflowApiBase()) && typeof fetch === "function";
}

function feeScheduleMissingAuthoritativeRequired() {
  return trackerApiMode() === "api" || feeScheduleApiEnabled();
}

function feeScheduleMissingAuthoritativeReady() {
  return !feeScheduleMissingAuthoritativeRequired() || feeScheduleMissingApiState.status === "ok";
}

function feeSetupRequestedPage(key, scope) {
  let entry = managementListPages.get(key);
  if (!entry || entry.scope !== scope) { entry = { scope, page: 1 }; managementListPages.set(key, entry); }
  return entry.page;
}

function feeSetupPageParams() {
  const params = new URLSearchParams(feeScheduleApiQuery()); params.delete("limit");
  const setup = feeScheduleEffectiveFilters();
  if (setup.tenantId && setup.tenantId !== "All") setup.orgIds = activeClientGroupTenant() ? activeClientGroupOrgIds() : orgIdsForTenantName(setup.tenantId);
  if (setup.payer && setup.payer !== "All") params.set("payerName", setup.payer);
  if (setup.procedure && setup.procedure !== "All") params.set("procedureCode", setup.procedure);
  params.set("setupFilters", JSON.stringify(setup));
  params.set("columnFilters", JSON.stringify(feeScheduleColumnFilterEntries()));
  const scope = params.toString(); params.set("page", feeSetupRequestedPage("rates", scope));
  return params;
}

function feeReviewMoney(value) { return value === null || value === undefined ? "Unavailable" : money(value); }
let feeSetupReviewState = { key: "", status: "idle", model: null };
let feeSetupReviewSequence = 0;
function feeSetupReviewRoutes() {
  const actions = getActionRules().map(rule => String(rule.action || "").toLowerCase());
  return { payer: actions.some(action => /payment processing|primary claim in process|submit to secondary|payer|secondary/.test(action)), appeal: actions.some(action => /appeal|correction|required|audit|escalate/.test(action)) };
}
function feeSetupReviewKey() {
  const key = JSON.parse(feeScheduleApiKey()), params = new URLSearchParams(key.query); params.delete("page"); key.query = params.toString();
  return JSON.stringify([key, feeScheduleApiState.revision || "", feeSetupReviewRoutes()]);
}
function renderPreparedFeeSetupReviews() {
  if (!document.querySelector(".fee-schedule-review-panel")?.open) return;
  if (!hasPermission(PERMISSIONS.VIEW_FINANCIALS)) {
    for (const panel of [els.payerRulesReadinessPanel, els.contractVariancePanel]) if (panel) panel.innerHTML = '<p class="muted-note">Financial review is outside your access.</p>';
    return;
  }
  const current = feeSetupReviewState.key === feeSetupReviewKey();
  if (current && feeSetupReviewState.status === "ok") {
    renderContractVariancePanel([], [], feeSetupReviewState.model.contractVariance);
    renderPayerRulesReadinessPanel([], [], feeSetupReviewState.model.payerReadiness);
    return;
  }
  // NOT PREPARED IS NOT A FAILURE. Contract variance and payer readiness are built
  // only when the hourly refresh prepares fee pages (TMS_PORTAL_PREPARED_FEES, off
  // since 2026-10-07 because it was the dominant build cost). The route then answers
  // fee_schedule_review_unavailable on every request, so a Retry button can never
  // help; say what is off, once, and point to the screen that does work.
  if (current && feeSetupReviewState.status === "unavailable") {
    const panels = [els.payerRulesReadinessPanel, els.contractVariancePanel].filter(Boolean);
    panels.forEach((panel, index) => {
      panel.innerHTML = index ? "" : '<div class="fee-review-off" role="status"><strong>Contract variance and payer readiness are not prepared right now.</strong><p>The hourly data refresh currently skips the full fee review to keep refreshes fast. Uncovered service combinations are still listed on Missing Rates.</p><button type="button" class="ghost-button" data-fee-review-missing-rates>Open Missing Rates</button></div>';
      panel.querySelector("[data-fee-review-missing-rates]")?.addEventListener("click", () => navigateToView("missingRates"));
    });
    return;
  }
  const failed = current && feeSetupReviewState.status === "error";
  for (const panel of [els.payerRulesReadinessPanel, els.contractVariancePanel]) if (panel) {
    panel.innerHTML = failed ? '<p role="status">' + escapeHtml(feeSetupReviewState.error) + '</p><button type="button" class="ghost-button" data-fee-review-retry>Retry review</button>' : '<p role="status">Loading complete fee review…</p>';
    panel.querySelector('[data-fee-review-retry]')?.addEventListener('click', () => requestFeeSetupReview({ force: true }));
  }
}
function requestFeeSetupReview({ force = false } = {}) {
  if (!feeScheduleApiEnabled() || !hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES) || !hasPermission(PERMISSIONS.VIEW_FINANCIALS) || !document.querySelector(".fee-schedule-review-panel")?.open) return;
  if (!["ok", "saving"].includes(feeScheduleApiState.status) || !feeScheduleApiState.revision) return;
  const key = feeSetupReviewKey();
  if (!force && feeSetupReviewState.key === key && ["pending", "ok", "error", "unavailable"].includes(feeSetupReviewState.status)) return;
  const sequence = ++feeSetupReviewSequence, params = feeSetupPageParams(); params.delete("page"); params.set("operation", "review"); params.set("reviewRoutes", JSON.stringify(feeSetupReviewRoutes()));
  const token = workflowApiToken() || trackerApiToken(), current = () => sequence === feeSetupReviewSequence && key === feeSetupReviewKey();
  feeSetupReviewState = { key, status: "pending", model: null }; renderPreparedFeeSetupReviews();
  reportReadJson("fee-review", workflowApiBase() + "/api/setup/fee-schedule-rates?" + params, { cache: "no-store", headers: { ...(token ? { authorization: "Bearer " + token } : {}) } }).then(({ response, body }) => {
    if (!current()) return;
    if (body.error === "fee_schedule_review_unavailable") {
      feeSetupReviewState = { key, status: "unavailable", model: null };
    } else if (!response.ok || body.complete !== true || !body.contractVariance?.summary || !body.payerReadiness) {
      feeSetupReviewState = { key, status: "error", model: null, error: body.error === "fee-schedule-review-updating" ? "Rate changes are being applied. The review will update shortly." : "The complete fee review could not be loaded. Try again." };
      if (body.error === "fee-schedule-review-updating") setTimeout(() => { if (current()) requestFeeSetupReview({ force: true }); }, 1500);
    } else feeSetupReviewState = { key, status: "ok", model: body };
    renderPreparedFeeSetupReviews();
  }).catch(() => { if (current()) { feeSetupReviewState = { key, status: "error", model: null, error: "The complete fee review could not be loaded. Try again." }; renderPreparedFeeSetupReviews(); } });
}

function feeSetupPageWindow(key, rows, metadata = {}) {
  const page = Number(metadata?.number) || 1, total = Number(metadata?.totalRows) || 0, size = Number(metadata?.size) || 50;
  const entry = managementListPages.get(key); if (entry && metadata?.number) entry.page = page;
  return { key, page, pages: Number(metadata?.totalPages) || 1, total, first: rows.length ? (page - 1) * size + 1 : 0, last: rows.length ? (page - 1) * size + rows.length : 0, rows };
}

async function requestFeeSetupChoices(key, { search = "", page = 1, header = false, signal } = {}) {
  const scope = feeScheduleApiKey(false), params = feeSetupPageParams(); params.delete("page");
  params.set("operation", "facets"); params.set("facetKeys", JSON.stringify([key]));
  params.set("facetOptions", JSON.stringify({ [key]: { search, page, pageSize: 50 } }));
  if (header && !(activeClientGroupTenant() && (key === "client" || (key === "orgId" && activeClientGroupOrgIds().length === 1)))) {
    const field = { payer: "payerName", procedure: "procedureCode", orgId: "orgId", client: "tenantId" }[key]; if (field) params.delete(field);
    const setup = JSON.parse(params.get("setupFilters") || "{}"); delete setup[key]; if (key === "client") { delete setup.tenantId; delete setup.orgIds; } params.set("setupFilters", JSON.stringify(setup));
  }
  const token = workflowApiToken() || trackerApiToken();
  const response = await fetch(workflowApiBase() + "/api/setup/fee-schedule-rates?" + params, { cache: "no-store", signal, headers: { ...(token ? { authorization: "Bearer " + token } : {}) } });
  const body = await response.json();
  if (scope !== feeScheduleApiKey(false)) throw new Error("Fee selection changed");
  if (!response.ok || body.complete !== true) throw new Error(body.message || "Choices could not be loaded");
  return { options: (body.facets?.[key] || []).map(row => ({ ...row, label: feeScheduleColumnTokenLabel(key, row.value) })), page: body.facetPages?.[key], ...(body.facetStats?.[key] || {}) };
}

function feeScheduleApiQuery() {
  const filters = feeScheduleEffectiveFilters();
  const headerTenant = typeof activeClientGroupTenant === "function" ? activeClientGroupTenant() : "";
  const headerOrg = typeof activeClientGroupServerOrgId === "function" ? activeClientGroupServerOrgId() : "";
  const tenantSelected = filters.tenantId && filters.tenantId !== "All"
    ? filters.tenantId
    : (headerTenant || "");
  // Database rows store tenant_id as "org:<orgId>", so a tenant-name selection
  // must be translated to its org id for the server-side filter to match.
  const tenantOrgIds = tenantSelected ? orgIdsForTenantName(tenantSelected) : [];
  const orgSelected = filters.orgId && filters.orgId !== "All"
    ? filters.orgId
    : (tenantOrgIds.length === 1 ? tenantOrgIds[0] : (headerOrg || ""));
  return new URLSearchParams(Object.entries({
    tenantId: tenantSelected && !tenantOrgIds.length ? tenantSelected : "",
    orgId: orgSelected,
    status: filters.showArchived ? "archived" : "active",
    limit: "1000"
  }).filter(([, value]) => value !== "")).toString();
}

function feeScheduleApiKey(includeFreshness = true) {
  return JSON.stringify({
    ...(includeFreshness ? { readScope: additionalPortalDataReadScope() } : {}),
    base: workflowApiBase(), token: workflowApiToken() || trackerApiToken(),
    user: activeInternalUser(),
    role: state.role,
    query: feeSetupPageParams().toString(),
    company: activeClientGroupTenant(),
    access: workflowCommandAccessSnapshot()
  });
}

// Payer/CPT narrowing reaches the complete prepared source before paging.
function feeScheduleMissingApiQuery() {
  const params = new URLSearchParams(feeScheduleApiQuery());
  const filters = feeScheduleEffectiveFilters();
  if (filters.payer && filters.payer !== "All") params.set("payerName", filters.payer);
  if (filters.procedure && filters.procedure !== "All") params.set("procedureCode", filters.procedure);
  return params.toString();
}

let feeMissingFilterIdentity = "", feeMissingRetryTimer = null, feeMissingAbortController = null;
// "Rates are being applied" retries every 1.5s; stop after about a minute and show it.
const FEE_MISSING_UPDATING_RETRY_LIMIT = 40;
function feeMissingRequestedPage() {
  const identity = JSON.stringify([workflowApiBase(), workflowApiToken() || trackerApiToken(), workflowCommandAccessSnapshot(), feeScheduleEffectiveFilters(), activeClientGroupTenant(), state.feeScheduleMissingSort]);
  if (identity !== feeMissingFilterIdentity) { feeMissingFilterIdentity = identity; feeScheduleMissingPage = 1; }
  return feeScheduleMissingPage;
}
function feeScheduleMissingApiKey() {
  return JSON.stringify({
    readScope: additionalPortalDataReadScope(),
    base: workflowApiBase(), token: workflowApiToken() || trackerApiToken(),
    user: activeInternalUser(),
    role: state.role,
    query: feeScheduleMissingApiQuery(),
    company: activeClientGroupTenant(), filters: feeScheduleEffectiveFilters(),
    access: workflowCommandAccessSnapshot(),
    source: "missing-worklist", page: feeMissingRequestedPage(), sort: state.feeScheduleMissingSort
  });
}

function payerExternalIdForClaimPayer(claim = {}, payer = "") {
  const payerKey = normalizedContractText(payer);
  const primaryKey = normalizedContractText(claim.primaryPayer);
  const secondaryKey = normalizedContractText(claim.secondaryPayer);
  if (payerKey && secondaryKey && payerKey === secondaryKey) return firstPresent(claim.secondaryExternalId, claim.secondaryPayerExternalId, claim.secondaryPayerId);
  if (payerKey && primaryKey && payerKey === primaryKey) return firstPresent(claim.primaryExternalId, claim.primaryPayerExternalId, claim.primaryPayerId);
  return firstPresent(claim.primaryExternalId, claim.primaryPayerExternalId, claim.secondaryExternalId, claim.secondaryPayerExternalId);
}

function feeScheduleApiRequestStillCurrent(key) {
  return feeScheduleApiState.key === key && feeScheduleApiKey() === key;
}

let feeScheduleListRequestSequence = 0;
// CLIENT READ-ONLY MODE: the own-company grant without the staff grant. The
// server scopes the rows to the client's company and refuses setup operations;
// this only keeps the page to the rate table they asked to see.
function feeScheduleClientReadOnly() {
  return !hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES) && hasPermission(PERMISSIONS.VIEW_OWN_FEE_SCHEDULE);
}
function feeScheduleRatesReadable() {
  return hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES) || hasPermission(PERMISSIONS.VIEW_OWN_FEE_SCHEDULE);
}
function requestFeeScheduleApiRows(options = {}) {
  if (!feeScheduleApiEnabled() || !feeScheduleRatesReadable()) return;
  const key = feeScheduleApiKey();
  if (!options.force && feeScheduleApiState.key === key && ["pending", "saving", "ok", "error"].includes(feeScheduleApiState.status)) return;
  const requestId = ++feeScheduleListRequestSequence;
  feeScheduleApiState = { key, status: "pending", rows: [], error: "", slow: false, savedAt: feeScheduleApiState.savedAt || "" };
  const token = workflowApiToken() || trackerApiToken();
  const params = feeSetupPageParams();
  const current = () => requestId === feeScheduleListRequestSequence && feeScheduleApiRequestStillCurrent(key);
  reportReadJson("fee-rates", workflowApiBase() + "/api/setup/fee-schedule-rates?" + params.toString(), {
    method: "GET", cache: "no-store", headers: { ...(token ? { authorization: "Bearer " + token } : {}) }
  }, () => { if (current()) { feeScheduleApiState.slow = true; renderFeeSchedules(); } })
    .then(({ response, body }) => {
      if (!current()) return;
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      if (!response.ok || body.error || body.complete !== true || body.truncated !== false || !Array.isArray(body.rows)) {
        feeScheduleApiState = { key, status: "error", rows: [], error: response.status === 401 ? "Your session expired. Sign in again." : "The rate list could not be loaded. Try again.", savedAt: feeScheduleApiState.savedAt || "" };
        renderFeeSchedules();
        return;
      }
      const rows = normalizeFeeScheduleRows(body.rows);
      if (rows.length) {
        mergeFeeScheduleApiRows(rows, { serverOwnsBook: String(body.persistenceMode || "") !== "local-memory" });
        saveState();
      }
      feeScheduleApiState = { key, status: "ok", rows, complete: true, truncated: false, rowLimit: Number(body.rowLimit || 0), page: body.page, summary: body.summary, revision: body.revision, error: "", savedAt: feeScheduleApiState.savedAt || "" };
      renderFeeScheduleSurfaces();
    }).catch(() => {
      if (!current()) return;
      feeScheduleApiState = { key, status: "error", rows: [], error: "The rate list could not be loaded. Try again.", savedAt: feeScheduleApiState.savedAt || "" };
      renderFeeScheduleSurfaces();
    });
}

// Scope and mutable source indexes remain authoritative. The complete response
// only identifies the current list; it never changes the calculation rate book.
function feeScheduleDisplayRows() {
  const scoped = feeRowsForActiveUser(state.feeScheduleRows || []).filter(feeScheduleRowInSelectedCompany);
  if (!feeScheduleApiEnabled()) return scoped;
  if (!feeScheduleApiRequestStillCurrent(feeScheduleApiState.key) || !["ok", "saving"].includes(feeScheduleApiState.status) || feeScheduleApiState.complete !== true) return [];
  const keys = new Set((feeScheduleApiState.rows || []).map(feeScheduleMergeKey));
  return scoped.filter((row) => keys.has(feeScheduleMergeKey(row)));
}

function requestFeeScheduleMissingApiRows(options = {}) {
  if (!feeScheduleApiEnabled() || !hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES)) return;
  const key = feeScheduleMissingApiKey();
  if (!options.force && feeScheduleMissingApiState.key === key && ["pending", "ok", "error"].includes(feeScheduleMissingApiState.status)) return;
  const pageIdentity = feeMissingFilterIdentity;
  const previousPageIdentity = feeScheduleMissingApiState.pageIdentity;
  const requestId = ++feeScheduleMissingRequestSeq;
  feeScheduleMissingApiState = { key, requestId, pageIdentity, status: "pending", rows: feeScheduleMissingApiState.rows || [], truncated: feeScheduleMissingApiState.truncated || false, rowLimit: feeScheduleMissingApiState.rowLimit || 0, error: "", generatedAt: feeScheduleMissingApiState.generatedAt || "" };
  // A parent decision is valid only for the exact authoritative worklist
  // generation that produced it. A refresh can remove or reshape that row, so
  // never let an old live preview/reason silently reactivate after reconnect.
  feeParentMatchDraft = null;
  feeParentPreviewState = { key: "", status: "idle", body: null, error: "" };
  if (options.force) renderFeeScheduleSurfaces();
  const token = workflowApiToken() || trackerApiToken();
  const params = new URLSearchParams(feeScheduleMissingApiQuery()); params.delete("limit");
  const setup = feeScheduleEffectiveFilters(); if (setup.tenantId && setup.tenantId !== "All") setup.orgIds = activeClientGroupTenant() ? activeClientGroupOrgIds() : orgIdsForTenantName(setup.tenantId);
  params.set("setupFilters", JSON.stringify(setup)); params.set("page", feeMissingRequestedPage()); params.set("sort", state.feeScheduleMissingSort || "preparedGap");
  const query = params.toString();
  // A superseded read is cancelled, not just ignored, so it stops holding a connection.
  try { feeMissingAbortController?.abort(); } catch (_e) {}
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  feeMissingAbortController = controller;
  // The deadline covers the whole read -- headers AND body. Clearing it when the headers
  // arrived left a stalled body able to hold the worklist in "pending" forever.
  let timedOut = false;
  let rejectDeadline = null;
  const deadline = new Promise((_resolve, reject) => { rejectDeadline = reject; });
  deadline.catch(() => {});
  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    try { controller?.abort(); } catch (_e) {}
    const error = new Error("Missing Rates timed out");
    error.name = "AbortError";
    rejectDeadline(error);
  }, 50000);
  const updatingRetry = Number(options.updatingRetry) || 0;
  fetch(additionalPortalDataReadUrl(`${workflowApiBase()}/api/setup/fee-schedule-rates/missing${query ? `?${query}` : ""}`), {
    method: "GET",
    cache: "no-store",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    signal: controller ? controller.signal : undefined
  })
    .then(async (response) => {
      const body = await Promise.race([
        response.json().catch((error) => { if (timedOut || (error && error.name === "AbortError")) throw error; return {}; }),
        deadline
      ]);
      if (feeScheduleMissingApiKey() !== key || feeScheduleMissingApiState.key !== key || feeScheduleMissingApiState.requestId !== requestId) return;
      if (!response.ok || body.error || !Array.isArray(body.rows) || body.complete !== true) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        feeScheduleMissingApiState = { key, requestId, status: "error", rows: response.status === 401 ? (feeScheduleMissingApiState.rows || []) : [], truncated: response.status === 401 ? (feeScheduleMissingApiState.truncated || false) : false, rowLimit: response.status === 401 ? (feeScheduleMissingApiState.rowLimit || 0) : 0, error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body.message || body.error || (response.ok ? "Invalid missing-rate response" : `HTTP ${response.status}`)), generatedAt: feeScheduleMissingApiState.generatedAt || "" };
        renderFeeScheduleSurfaces();
        if (body.error === "fee-schedule-missing-updating" && updatingRetry < FEE_MISSING_UPDATING_RETRY_LIMIT) { clearTimeout(feeMissingRetryTimer); feeMissingRetryTimer = setTimeout(() => { if (feeScheduleMissingApiKey() === key && (state.view === "missingRates" || document.querySelector(".fee-schedule-review-panel")?.open)) requestFeeScheduleMissingApiRows({ force: true, updatingRetry: updatingRetry + 1 }); }, 1500); }
        return;
      }
      const normalizedRows = normalizeFeeScheduleMissingApiRows(body.rows);
      const invalidRows = normalizedRows.length !== body.rows.length
        || normalizedRows.some((row) => [row.services, row.claims].some((value) => value == null || value < 0) || (row.units != null && row.units < 0));
      if (invalidRows) {
        feeScheduleMissingApiState = { key, requestId, status: "error", rows: [], truncated: false, rowLimit: 0, error: "Invalid missing-rate row data", generatedAt: feeScheduleMissingApiState.generatedAt || "" };
        renderFeeScheduleSurfaces();
        return;
      }
      feeScheduleMissingApiState = {
        key,
        requestId,
        pageIdentity,
        status: "ok",
        rows: normalizedRows,
        page: body.page, totals: body.totals, revision: body.revision, generation: body.generation,
        // The server takes its row cap INSIDE this caller's tenant scope, so a
        // short list is now genuinely short -- but a cap can still cut a long
        // one, and the panel has to be able to say which it is.
        truncated: Boolean(body.truncated),
        rowLimit: Number(body.rowLimit || 0),
        complete: true,
        source: String(body.source || ""),
        provenance: body.provenance && typeof body.provenance === "object" ? body.provenance : null,
        error: "",
        generatedAt: timestamp()
      };
      if (body.page) feeScheduleMissingPage = Number(body.page.number) || 1;
      else if (previousPageIdentity !== pageIdentity) feeScheduleMissingPage = 1;
      renderFeeScheduleSurfaces();
    })
    .catch((error) => {
      if (feeScheduleMissingApiKey() !== key || feeScheduleMissingApiState.key !== key || feeScheduleMissingApiState.requestId !== requestId) return;
      feeScheduleMissingApiState = {
        key,
        requestId,
        status: "error",
        rows: [],
        truncated: false,
        rowLimit: 0,
        error: timedOut
          ? "Missing Rates timed out. Pick a client in the header and retry."
          : error.message,
        generatedAt: feeScheduleMissingApiState.generatedAt || ""
      };
      renderFeeScheduleSurfaces();
    })
    .finally(() => {
      window.clearTimeout(timeoutId);
      if (feeMissingAbortController === controller) feeMissingAbortController = null;
    });
}

function normalizeFeeScheduleMissingApiRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    parentMapping: row.parentMapping && typeof row.parentMapping === "object" ? { parentPayer: String(row.parentMapping.parentPayer || ""), status: String(row.parentMapping.status || "") } : null,
    tenantId: String(firstPresent(row.tenantId, row.tenant_id) || "").trim(),
    orgId: String(firstPresent(row.orgId, row.org_id) || "").trim(),
    orgName: String(firstPresent(row.orgName, row.org_name, row.tenantId) || "").trim(),
    payer: String(firstPresent(row.payer, row.payerName, row.payer_name) || "").trim(),
    payerRole: String(firstPresent(row.payerRole, row.payer_role) || "").trim(),
    payerId: String(firstPresent(row.payerId, row.externalPayerId, row.external_payer_id) || "").trim(),
    state: String(firstPresent(row.state) || "").trim(),
    service: String(firstPresent(row.service) || "").trim(),
    procedure: String(firstPresent(row.procedure, row.procedureCode, row.procedure_code) || "").trim(),
    m1: String(firstPresent(row.m1, row.modifier1, row.modifier_1) || "").trim(),
    m2: String(firstPresent(row.m2, row.modifier2, row.modifier_2) || "").trim(),
    place: String(firstPresent(row.place, row.placeOfService, row.place_of_service) || "").trim(),
    serviceDate: String(firstPresent(row.serviceDate, row.service_date) || "").trim(),
    serviceStartDate: String(firstPresent(row.serviceStartDate, row.service_start_date, row.serviceDate, row.service_date) || "").trim(),
    serviceEndDate: String(firstPresent(row.serviceEndDate, row.service_end_date, row.serviceDate, row.service_date) || "").trim(),
    network: String(firstPresent(row.network) || "").trim(),
    networkKnown: row.networkKnown !== false,
    services: feeScheduleOptionalCount(row.services),
    claims: feeScheduleOptionalCount(row.claims),
    units: feeScheduleOptionalCount(row.units),
    // Missing is not zero. These figures are supplied by the authoritative
    // worklist query; if that query omits or corrupts one, the UI must keep the
    // uncertainty instead of manufacturing a $0 that looks reconciled.
    expected: feeScheduleOptionalMoney(firstPresent(row.expected, row.expected_amount)),
    openAr: feeScheduleOptionalMoney(firstPresent(row.openAr, row.open_ar)),
    preparedFallbackServices: feeScheduleOptionalCount(firstPresent(row.preparedFallbackServices, row.prepared_fallback_services)),
    preparedFallbackExpected: feeScheduleOptionalMoney(firstPresent(row.preparedFallbackExpected, row.prepared_fallback_expected))
  })).filter((row) => row.orgId && row.procedure && row.payer);
}

function feeScheduleOptionalMoney(value) {
  if (!hasPresent(value)) return UNKNOWN_MONEY;
  if (typeof value === "string" && (!value.trim() || !/[0-9]/.test(value))) return UNKNOWN_MONEY;
  const parsed = parseMoneyNumber(value);
  return Number.isFinite(parsed) ? moneyNumber(parsed) : UNKNOWN_MONEY;
}

function feeScheduleOptionalCount(value) {
  if (!hasPresent(value) || (typeof value === "string" && !value.trim())) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function mergeFeeScheduleApiRows(rows = [], options = {}) {
  const merged = (Array.isArray(state.feeScheduleRows) ? state.feeScheduleRows : []).map(normalizeFeeScheduleRow);
  (Array.isArray(rows) ? rows : []).map(normalizeFeeScheduleRow).forEach((row) => {
    const id = String(row.feeScheduleRateId || "").trim();
    const naturalKey = feeScheduleNaturalMergeKey(row);
    // CSV import paints an optimistic row before the API returns. That row has
    // no feeScheduleRateId, while the saved response does. Matching only by the
    // server ID therefore appended a second, visually identical rate. Reconcile
    // by ID first, then by the complete natural rate key so the response upgrades
    // the optimistic row in place. A distinct effective-start date remains a
    // distinct rate, and an existing saved row never merges with a different ID.
    let index = id
      ? merged.findIndex((candidate) => String(candidate.feeScheduleRateId || "").trim() === id)
      : -1;
    if (index < 0 && naturalKey) {
      index = merged.findIndex((candidate) => {
        const candidateId = String(candidate.feeScheduleRateId || "").trim();
        return feeScheduleNaturalMergeKey(candidate) === naturalKey && (!candidateId || !id || candidateId === id);
      });
    }
    const next = { ...(index >= 0 ? merged[index] : {}), ...row, sourceSystem: row.sourceSystem || "portal-api" };
    if (index >= 0) merged[index] = next;
    else merged.push(next);
  });
  // The "Local Seed" rows exist so a browser with no server still has a grid
  // to show. Once the server has delivered its book they are not rates: on
  // TEST they rendered as five draft rows for clients "90000001..90000005"
  // beside 1,000 real ones and inflated the tenant and org counts by three.
  // Only the LIST response may retire them (a single saved row is not a book),
  // and only from a real lane: the local QA server ("local-memory") is the
  // demo, and its seeds are the grid until a server book exists.
  const serverOwnsBook = Boolean(options.serverOwnsBook) && (Array.isArray(rows) ? rows : []).length > 0;
  state.feeScheduleRows = merged
    .filter((row) => !(serverOwnsBook && isLocalSeedFeeScheduleRow(row)))
    .map(normalizeFeeScheduleRow);
  bumpFeeScheduleMatchEpoch();
}

function isLocalSeedFeeScheduleRow(row = {}) {
  return ["Demo", "Local Seed"].includes(String(row.duplicateCheck || "").trim());
}

function feeScheduleMergeKey(row = {}) {
  return row.feeScheduleRateId || feeScheduleNaturalMergeKey(row);
}

function feeScheduleNaturalMergeKey(row = {}) {
  return [
    row.tenantId,
    row.orgId,
    row.payer,
    row.externalPayerId,
    row.procedure,
    row.m1,
    row.m2,
    row.place,
    row.network,
    row.startDate
  ].map((value) => String(value || "").toLowerCase()).join("|");
}

function refreshFeeScheduleMissingAfterPersistedMutation() {
  requestFeeScheduleApiRows({ force: true });
  feeScheduleMissingPage = 1;
  requestFeeScheduleMissingApiRows({ force: true });
}

function persistFeeScheduleRow(row = {}, options = {}) {
  if (!feeScheduleApiEnabled() || !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return Promise.resolve({ skipped: true });
  const payload = feeScheduleApiPayload(row);
  if (!payload) {
    if (options.showDraftToast) showToast("Fill organization ID, CPT, and rate to save this fee schedule row.");
    return Promise.resolve({ skipped: true });
  }
  const token = workflowApiToken() || trackerApiToken();
  const currentKey = feeScheduleApiKey();
  feeScheduleApiState = { ...feeScheduleApiState, key: currentKey, status: "saving", error: "" };
  const saveRate = () => fetch(`${workflowApiBase()}/api/setup/fee-schedule-rates`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ rate: payload })
  });
  const preview = String(payload.approvalStatus || "").toLowerCase() === "approved"
    ? previewFeeScheduleRateApproval(payload, token)
    : Promise.resolve({ approvalReady: true, conflictCount: 0 });
  return preview
    .then((result) => {
      if (!feeScheduleApiRequestStillCurrent(currentKey)) return null;
      if (result && result.approvalReady === false) {
        const count = Number(result.conflictCount || 0);
        throw new Error(`Approval blocked: ${count.toLocaleString()} overlapping approved active rate${count === 1 ? "" : "s"} must be archived or end-dated first.`);
      }
      return saveRate();
    })
    .then(async (response) => {
      if (!response) return;
      const body = await response.json().catch(() => ({}));
      if (!feeScheduleApiRequestStillCurrent(currentKey)) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        throw new Error("Your session expired. Sign in again — this page will still be here.");
      }
      if (!response.ok || body.error) throw new Error(body.message || body.error || `HTTP ${response.status}`);
      const saved = normalizeFeeScheduleRow(body.row || {});
      if (body.persisted !== true || !saved.feeScheduleRateId || saved.orgId !== payload.orgId || saved.procedure !== payload.procedureCode || (payload.feeScheduleRateId && saved.feeScheduleRateId !== payload.feeScheduleRateId)) throw new Error("The server did not confirm this saved rate. Your changes have not been registered.");
      if (saved.procedure || saved.feeScheduleRateId) mergeFeeScheduleApiRows([saved]);
      feeScheduleApiState = { ...feeScheduleApiState, status: "ok", error: "", savedAt: timestamp() };
      saveState();
      renderFeeSchedules();
      refreshFeeScheduleMissingAfterPersistedMutation();
      if (options.showToast) showToast("Fee schedule saved.");
      return { persisted: true, row: saved };
    })
    .catch((error) => {
      if (!feeScheduleApiRequestStillCurrent(currentKey)) return;
      feeScheduleApiState = { ...feeScheduleApiState, status: feeScheduleApiState.complete ? "ok" : "error", error: error.message };
      showToast(`Fee schedule save failed: ${error.message}`);
      renderFeeSchedules();
      if (options.rejectOnError) throw error;
      return { persisted: false, error };
    });
}

function previewFeeScheduleRateApproval(payload = {}, token = "") {
  return fetch(`${workflowApiBase()}/api/setup/fee-schedule-rates/preview`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ rate: payload })
  }).then(async (response) => {
    const body = await response.json().catch(() => ({}));
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      throw new Error("Your session expired. Sign in again — this page will still be here.");
    }
    if (!response.ok || body.error) throw new Error(body.message || body.error || `HTTP ${response.status}`);
    return body;
  });
}

function persistFeeScheduleRows(rows = [], options = {}) {
  if (!feeScheduleApiEnabled() || !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return;
  const payloads = (Array.isArray(rows) ? rows : []).map(feeScheduleApiPayload).filter(Boolean);
  if (!payloads.length) {
    if (options.showToast) showToast("No complete fee schedule rows were ready to import.");
    return;
  }
  const token = workflowApiToken() || trackerApiToken();
  const currentKey = feeScheduleApiKey();
  feeScheduleApiState = { ...feeScheduleApiState, key: currentKey, status: "saving", error: "" };
  fetch(`${workflowApiBase()}/api/setup/fee-schedule-rates/import`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ rates: payloads })
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!feeScheduleApiRequestStillCurrent(currentKey)) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        throw new Error("Your session expired. Sign in again — this page will still be here.");
      }
      if (!response.ok || body.error) throw new Error(body.message || body.error || `HTTP ${response.status}`);
      const requested = Number(body.requestedRows), persisted = Number(body.persistedRows), failed = Number(body.failedRows);
      if (!Number.isInteger(requested) || requested !== payloads.length || !Number.isInteger(persisted) || !Number.isInteger(failed) || persisted < 0 || failed < 0 || persisted + failed !== requested || !Array.isArray(body.rows)) throw new Error("The server did not confirm the CSV import results.");
      const savedRows = normalizeFeeScheduleRows(body.rows);
      if (savedRows.length !== Math.min(persisted, 25) || savedRows.some((row) => !row.feeScheduleRateId || !payloads.some((requestedRow) => row.orgId === requestedRow.orgId && row.procedure === requestedRow.procedureCode && (!requestedRow.feeScheduleRateId || requestedRow.feeScheduleRateId === row.feeScheduleRateId)))) throw new Error("The import returned an invalid saved rate.");
      if (savedRows.length) mergeFeeScheduleApiRows(savedRows);
      feeScheduleApiState = { ...feeScheduleApiState, status: "ok", error: "", savedAt: timestamp() };
      saveState();
      renderFeeSchedules();
      refreshFeeScheduleMissingAfterPersistedMutation();
      if (options.showToast !== false) showToast(`Imported ${Number(body.persistedRows || 0).toLocaleString()} fee schedule rows.`);
      if (Number(body.conflictFailureRows || 0) > 0) showToast(`${Number(body.conflictFailureRows || 0).toLocaleString()} approved rate conflict${Number(body.conflictFailureRows || 0) === 1 ? "" : "s"} need archive or end-date cleanup.`);
      if (Number(body.failedRows || 0) > 0) showToast(`${Number(body.failedRows || 0).toLocaleString()} fee schedule rows need cleanup.`);
    })
    .catch((error) => {
      if (!feeScheduleApiRequestStillCurrent(currentKey)) return;
      feeScheduleApiState = { ...feeScheduleApiState, status: "error", error: error.message };
      showToast("Fee schedule import failed. Unconfirmed rows were not added; correct the CSV and retry.");
      renderFeeSchedules();
    });
}

function archiveFeeScheduleRowApi(row = {}, options = {}) {
  if (!feeScheduleApiEnabled() || !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return Promise.resolve({ skipped: true });
  const normalized = normalizeFeeScheduleRow(row);
  if (!normalized.feeScheduleRateId || !normalized.orgId) return Promise.resolve({ skipped: true });
  const token = workflowApiToken() || trackerApiToken();
  const currentKey = feeScheduleApiKey();
  feeScheduleApiState = { ...feeScheduleApiState, key: currentKey, status: "saving", error: "" };
  renderFeeSchedules();
  return fetch(`${workflowApiBase()}/api/setup/fee-schedule-rates/archive`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({
      tenantId: normalized.tenantId || normalized.orgName || normalized.orgId || "",
      orgId: normalized.orgId || "",
      feeScheduleRateId: normalized.feeScheduleRateId,
      reason: options.reason || "fee schedule screen archive"
    })
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        throw new Error("Your session expired. Sign in again — this page will still be here.");
      }
      if (!response.ok || body.error) throw new Error(body.message || body.error || `HTTP ${response.status}`);
      if (!feeScheduleApiRequestStillCurrent(currentKey)) {
        const staleError = new Error("Stale fee schedule archive response ignored.");
        staleError.stale = true;
        throw staleError;
      }
      const confirmed = normalizeFeeScheduleRow(body.row || {});
      if (body.archived !== true || confirmed.feeScheduleRateId !== normalized.feeScheduleRateId || confirmed.orgId !== normalized.orgId || confirmed.status !== "archived") throw new Error("The server did not confirm the rate update. No local rate was changed.");
      feeScheduleApiState = { ...feeScheduleApiState, status: "ok", error: "", savedAt: timestamp() };
      refreshFeeScheduleMissingAfterPersistedMutation();
      return { archived: true, row: body.row || null };
    })
    .catch((error) => {
      if (error && error.stale) throw error;
      if (!feeScheduleApiRequestStillCurrent(currentKey)) throw error;
      feeScheduleApiState = { ...feeScheduleApiState, status: "error", error: error.message };
      showToast("Fee schedule archive failed. The row is still visible.");
      renderFeeSchedules();
      throw error;
    });
}

function feeScheduleApiPayload(row = {}) {
  const normalized = normalizeFeeScheduleRow(row);
  const parsedRate = parseMoneyNumber(normalized.rate);
  if (!normalized.orgId || !normalized.procedure || normalized.rate === "" || !Number.isFinite(parsedRate) || parsedRate < 0) return null;
  return {
    feeScheduleRateId: normalized.feeScheduleRateId,
    tenantId: normalized.tenantId || normalized.orgName || normalized.orgId || "",
    orgId: normalized.orgId || "",
    orgName: normalized.orgName || "",
    payorId: normalized.payorId || "",
    externalPayerId: normalized.externalPayerId || "",
    payerName: normalized.payer || "",
    state: normalized.state || "",
    service: normalized.service || "",
    procedureCode: normalized.procedure,
    modifier1: normalized.m1 || "",
    modifier2: normalized.m2 || "",
    placeOfService: normalized.place || "",
    network: normalized.network || "",
    rate: normalized.rate,
    rateMatchMethod: normalized.duplicateCheck || "portal-managed",
    secondaryCalc: normalized.secondaryCalc || "",
    effectiveStartDate: normalized.startDate || "",
    effectiveEndDate: normalized.endDate || "",
    status: "active",
    approvalStatus: normalized.approvalStatus || "draft",
    sourceSystem: "portal-ui",
    metadata: {
      source: "fee-schedules-screen",
      editedBy: activeInternalUser()
    }
  };
}

function feeSchedulePersistenceStatusLabel() {
  if (!feeScheduleApiEnabled()) return "Rate management is unavailable in this session";
  if (feeScheduleApiState.status === "pending") return "Loading rates";
  if (feeScheduleApiState.status === "saving") return "Saving rate";
  if (feeScheduleApiState.status === "ok" && feeScheduleApiState.error) return /^Approval blocked:/i.test(feeScheduleApiState.error) ? feeScheduleApiState.error : "Save not confirmed; previous rate retained";
  if (feeScheduleApiState.status === "error") {
    const safeApprovalConflict = /^Approval blocked:/i.test(String(feeScheduleApiState.error || ""))
      ? String(feeScheduleApiState.error)
      : "";
    return safeApprovalConflict || "Rates are temporarily unavailable";
  }
  if (feeScheduleApiState.status === "ok") return feeScheduleApiState.savedAt ? `Saved ${feeScheduleApiState.savedAt}` : "Current rates";
  return "Current rates configured";
}

function feeScheduleGridControlSnapshot(active) {
  if (!active?.matches?.("#feeScheduleRows [data-fee-index][data-fee-field]")) return null;
  return {
    index: String(active.dataset.feeIndex || ""),
    field: String(active.dataset.feeField || ""),
    value: active.value,
    selectionStart: typeof active.selectionStart === "number" ? active.selectionStart : null,
    selectionEnd: typeof active.selectionEnd === "number" ? active.selectionEnd : null
  };
}

function feeScheduleGridFocusSnapshot() {
  if (typeof document === "undefined") return null;
  return feeScheduleGridControlSnapshot(document.activeElement) || feeScheduleGridFocusIntent;
}

function restoreFeeScheduleGridFocus(snapshot) {
  if (!snapshot || typeof document === "undefined") return;
  const control = Array.from(document.querySelectorAll("#feeScheduleRows [data-fee-index][data-fee-field]"))
    .find((candidate) => candidate.dataset.feeIndex === snapshot.index && candidate.dataset.feeField === snapshot.field);
  if (!control || control.disabled) return;
  // A server refresh may finish between pointer-down and blur. Preserve the
  // user's uncommitted cell value before returning focus so their keystrokes do
  // not disappear or land in the search box when the table DOM is replaced.
  control.value = snapshot.value;
  try { control.focus({ preventScroll: true }); } catch (_) { control.focus(); }
  if (snapshot.selectionStart != null && typeof control.setSelectionRange === "function") {
    try { control.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd ?? snapshot.selectionStart); } catch (_) {}
  }
  feeScheduleGridFocusIntent = feeScheduleGridControlSnapshot(control);
}

function renderFeeSchedules() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("feeSchedules")) return;
  if (!els.feeScheduleRows) return;
  // Independent of the rate list: learned OON rates have their own read.
  if (hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES)) renderOonLearnedRatesPanel(document.getElementById("oonLearnedRatesPanel"), "staff");
  else if (feeScheduleClientReadOnly() && hasPermission(PERMISSIONS.VIEW_FINANCIALS)) renderOonLearnedRatesPanel(document.getElementById("oonLearnedRatesPanel"), "clientFee");
  renderOonCutReviewPanel(document.getElementById("oonRateCutReviewPanel"), { passive: true });
  const clientReadOnly = feeScheduleClientReadOnly();
  document.getElementById("feeSchedulesView")?.classList.toggle("fee-schedule-client-view", clientReadOnly);
  const focusedGridControl = feeScheduleGridFocusSnapshot();
  requestFeeScheduleApiRows();
  const reviewPanel = document.querySelector(".fee-schedule-review-panel");
  if (reviewPanel?.open) requestFeeScheduleMissingApiRows();
  if (reviewPanel && !reviewPanel.dataset.feeLazyBound) { reviewPanel.dataset.feeLazyBound = "true"; reviewPanel.addEventListener("toggle", () => { if (reviewPanel.open) { requestFeeScheduleMissingApiRows(); renderFeeSchedules(); } }); }
  const listReady = !feeScheduleApiEnabled() || (["ok", "saving"].includes(feeScheduleApiState.status) && feeScheduleApiState.complete === true);
  if (els.exportFeeScheduleBtn) els.exportFeeScheduleBtn.disabled = !listReady;
  if (!listReady) {
    closeFeeScheduleColumnFilterMenu();
    const failed = feeScheduleApiState.status === "error";
    if (els.feeScheduleStats) els.feeScheduleStats.innerHTML = '<span role="status">' + (failed ? 'Rates unavailable' : 'Loading rates…') + '</span>';
    if (els.feeScheduleFilterSummary) els.feeScheduleFilterSummary.innerHTML = '';
    renderFeeScheduleColumnHeaders();
    els.feeScheduleRows.innerHTML = '<tr class="fee-schedule-empty-row"><td colspan="15">' + (failed ? '<div role="alert">' + escapeHtml(feeScheduleApiState.error) + '</div><button type="button" class="ghost-button" data-fee-list-retry>Retry</button>' : reportLoadingHtml('rates', feeScheduleApiState.slow)) + '</td></tr>';
    els.feeScheduleRows.querySelector('[data-fee-list-retry]')?.addEventListener('click', () => { requestFeeScheduleApiRows({ force: true }); renderFeeSchedules(); });
    return;
  }
  const scopedRows = feeScheduleDisplayRows();
  syncFeeScheduleFilterControls(scopedRows);
  const rows = feeScheduleApiEnabled() ? scopedRows : filteredFeeScheduleRows(scopedRows);
  const ratePage = feeScheduleApiEnabled() ? feeSetupPageWindow("rates", rows, feeScheduleApiState.page) : managementListPageWindow("rates", rows, JSON.stringify([feeScheduleApiKey(false), state.feeScheduleFilters, state.feeScheduleColumnFilters]));
  const serverSummary = feeScheduleApiEnabled() ? feeScheduleApiState.summary || {} : {};
  const payers = new Set(scopedRows.map((row) => row.payer).filter(Boolean));
  const procedures = new Set(scopedRows.map((row) => row.procedure).filter(Boolean));
  const tenants = new Set(scopedRows.map(feeScheduleTenantKey).filter(Boolean));
  const orgIds = new Set(scopedRows.map(feeScheduleOrgKey).filter(Boolean));
  const approvalCounts = feeScheduleApprovalCounts(scopedRows);
  // A CUT LIST MUST NOT READ AS A COMPLETE ONE. TEST holds 1,320 active rates;
  // the list route caps at 1,000, and this strip said "1,005 of 1,005 rows".
  const loadedRowsCut = feeScheduleApiState.status === "ok" && Boolean(feeScheduleApiState.truncated);
  const loadedRowCap = Number(feeScheduleApiState.rowLimit || 0) || feeScheduleApiState.rows.length;
  if (els.feeScheduleStats && clientReadOnly) {
    els.feeScheduleStats.innerHTML = `
      <span><strong>${formatCount(serverSummary.totalRows ?? rows.length)}</strong> rates</span>
      ${loadedRowsCut ? '<span class="fee-schedule-cut-note">' + pill("first " + formatCount(loadedRowCap) + " of more", "amber") + '</span>' : ""}
      <span><strong>${formatCount(serverSummary.payers ?? payers.size)}</strong> payers</span>
      <span><strong>${formatCount(serverSummary.procedures ?? procedures.size)}</strong> CPTs</span>
      <span class="fee-schedule-client-note">Your contracted rates as TMS has them on file. Contact your TMS team to change a rate.</span>
    `;
  } else if (els.feeScheduleStats) {
    els.feeScheduleStats.innerHTML = `
      <span><strong>${formatCount(serverSummary.totalRows ?? rows.length)}</strong> rows</span>
      ${loadedRowsCut ? '<span class="fee-schedule-cut-note">' + pill("first " + formatCount(loadedRowCap) + " of more", "amber") + '</span>' : ""}
      <span><strong>${formatCount(serverSummary.tenants ?? tenants.size)}</strong> tenants</span>
      <span><strong>${formatCount(serverSummary.orgIds ?? orgIds.size)}</strong> org IDs</span>
      <span><strong>${formatCount(serverSummary.payers ?? payers.size)}</strong> payers</span>
      <span><strong>${formatCount(serverSummary.procedures ?? procedures.size)}</strong> CPTs</span>
      <span><strong>${formatCount(serverSummary.approved ?? approvalCounts.approved)}</strong> approved</span>
      <span><strong>${formatCount(serverSummary.draft ?? approvalCounts.draft)}</strong> draft</span>
      <span class="fee-schedule-database-status"><strong>Rate setup</strong> ${escapeHtml(feeSchedulePersistenceStatusLabel())}</span>
    `;
  }
  if (els.feeScheduleFilterSummary) {
    const activeFilters = feeScheduleActiveFilterLabels();
    const columnFilterChips = feeScheduleColumnFilterChipsHtml();
    els.feeScheduleFilterSummary.innerHTML = `
      <span>${formatNounCount(serverSummary.totalRows ?? rows.length, "matching row")}</span>
      ${loadedRowsCut ? '<strong class="fee-schedule-cut-warning">Server cap of ' + escapeHtml(formatCount(loadedRowCap)) + ' reached &mdash; more rates exist than are loaded. Choose a Tenant to load every rate for that client.</strong>' : ""}
      ${activeFilters.map((label) => `<strong>${escapeHtml(label)}</strong>`).join("")}
      ${columnFilterChips}
      ${activeFilters.length || columnFilterChips ? "" : '<span class="fee-schedule-no-filters">No filters active</span>'}
      ${managementListPagerHtml(ratePage, "Rates")}
    `;
    bindManagementListPager(els.feeScheduleFilterSummary, "rates", renderFeeSchedules);
  }
  const companyFilters = feeScheduleEffectiveFilters();
  const claims = getClaims().filter((claim) => feeScheduleRowInSelectedCompany(claim)
    && (!companyFilters.tenantId || companyFilters.tenantId === "All" || feeScheduleRowMatchesTenant(claim, companyFilters.tenantId))
    && (!companyFilters.orgId || companyFilters.orgId === "All" || String(claim.orgId || claim.org_id || "") === String(companyFilters.orgId)));
  if (reviewPanel?.open) {
    if (feeScheduleApiEnabled()) {
      requestFeeSetupReview(); renderPreparedFeeSetupReviews();
    } else { renderPayerRulesReadinessPanel(claims, rows); renderContractVariancePanel(claims, rows); }
    renderFeeScheduleCoveragePanel(rows, scopedRows);
  }
  const feeReviewMissingSource = feeScheduleMissingApiState.status === "ok"
    ? feeScheduleMissingApiState.rows
    : feeScheduleMissingAuthoritativeRequired()
      ? []
      : feeScheduleMissingRows(claims, rows);
  const feeReviewMissingCount = feeScheduleMissingApiState.status === "ok" && feeScheduleMissingApiState.totals ? Number(feeScheduleMissingApiState.totals.combinations || 0) : Array.isArray(feeReviewMissingSource) ? feeReviewMissingSource.filter(feeScheduleMissingRowMatchesFilters).length : 0;
  const feeReviewSummary = typeof document !== "undefined" && document.querySelector
    ? document.querySelector(".fee-schedule-review-panel > summary")
    : null;
  if (feeReviewSummary) {
    const feeReviewStatus = feeScheduleMissingApiState.status;
    let feeReviewPill;
    if (feeScheduleMissingAuthoritativeRequired() && feeReviewStatus !== "ok") {
      feeReviewPill = feeReviewStatus === "pending" ? pill("checking", "amber") : pill("unavailable", "amber");
    } else if (feeReviewStatus === "pending") {
      feeReviewPill = pill("checking", "amber");
    } else if (feeReviewStatus === "error") {
      feeReviewPill = pill("unavailable", "amber");
    } else {
      feeReviewPill = feeReviewMissingCount > 0 ? pill(`${formatCount(feeReviewMissingCount)} missing`, "red") : pill("covered", "green");
    }
    feeReviewSummary.innerHTML = `Setup review ${feeReviewPill}`;
  }
  renderFeeScheduleColumnHeaders();
  // Render the table from the scoped rows too (was state.feeScheduleRows).
  const feeScheduleRowsHtml = ratePage.rows.map((row, index) => {
    const sourceIndex = Number.isInteger(row.__sourceIndex) ? row.__sourceIndex : ratePage.first - 1 + index;
    const archived = String(row.status || "active") === "archived";
    if (clientReadOnly) return feeScheduleClientRowHtml(row, archived);
    const rowSaving = feeSchedulePendingRowSaves.has(feeScheduleMergeKey(row));
    return `
    <tr class="${archived ? "fee-row-archived" : ""}">
      ${feeField(sourceIndex, "orgId", row.orgId, "text", archived || rowSaving)}
      <td class="fee-client-cell">${escapeHtml(tenantNameForOrgId(row.orgId) || (String(row.orgId || "").trim() ? portalCompanyLabel(row.orgId) : "—"))}</td>
      ${feeField(sourceIndex, "payer", row.payer, "text", archived || rowSaving)}
      ${feeField(sourceIndex, "state", row.state, "text", archived || rowSaving)}
      ${feeField(sourceIndex, "procedure", row.procedure, "text", archived || rowSaving)}
      ${feeField(sourceIndex, "m1", row.m1, "text", archived || rowSaving)}
      ${feeField(sourceIndex, "m2", row.m2, "text", archived || rowSaving)}
      ${feeField(sourceIndex, "place", row.place, "text", archived || rowSaving)}
      ${feeField(sourceIndex, "rate", row.rate, "number", archived || rowSaving)}
      ${feeSecondaryCalcField(sourceIndex, row.secondaryCalc, archived || rowSaving)}
      ${feeField(sourceIndex, "startDate", row.startDate, "date", archived || rowSaving)}
      ${feeField(sourceIndex, "endDate", row.endDate, "date", archived || rowSaving)}
      ${feeField(sourceIndex, "network", row.network, "text", archived || rowSaving)}
      ${feeApprovalField(sourceIndex, row.approvalStatus, archived || rowSaving)}
      <td>${rowSaving ? '<span role="status">Saving…</span>' : archived
        ? `<button class="small-button" type="button" data-restore-fee="${sourceIndex}">Restore</button>`
        : `<button class="small-button" type="button" data-delete-fee="${sourceIndex}">Archive</button>`}</td>
    </tr>
  `;
  }).join("");
  els.feeScheduleRows.innerHTML = feeScheduleRowsHtml || feeScheduleEmptyRowHtml(scopedRows);
  // A loading-time facet popup cannot stay empty above a newly loaded table.
  if (feeScheduleColumnFilterContext && feeScheduleColumnFilterMenuHost()?.querySelector(".column-filter-empty")) {
    const key = feeScheduleColumnFilterContext.key;
    const button = document.querySelector('[data-fee-column-filter="' + key + '"]');
    if (button) openFeeScheduleColumnFilterMenu(key, button);
  }

  Array.from(document.querySelectorAll("[data-fee-field]")).forEach((input) => {
    input.addEventListener("pointerdown", () => { feeScheduleGridFocusIntent = feeScheduleGridControlSnapshot(input); });
    input.addEventListener("focus", () => { feeScheduleGridFocusIntent = feeScheduleGridControlSnapshot(input); });
    input.addEventListener("blur", () => {
      window.setTimeout(() => {
        if (!document.activeElement?.matches?.("#feeScheduleRows [data-fee-index][data-fee-field]")) feeScheduleGridFocusIntent = null;
      }, 0);
    });
    input.addEventListener("change", () => updateFeeScheduleRow(Number(input.dataset.feeIndex), input.dataset.feeField, input.value));
  });
  Array.from(document.querySelectorAll("[data-delete-fee]")).forEach((button) => {
    button.addEventListener("click", () => deleteFeeScheduleRow(Number(button.dataset.deleteFee)));
  });
  Array.from(document.querySelectorAll("[data-restore-fee]")).forEach((button) => {
    button.addEventListener("click", () => restoreFeeScheduleRow(Number(button.dataset.restoreFee)));
  });
  restoreFeeScheduleGridFocus(focusedGridControl);
  wireFeeScheduleColumnFilterControls();
  Array.from(document.querySelectorAll("[data-fee-coverage-filter-org], [data-fee-coverage-filter-procedure]")).forEach((button) => {
    button.addEventListener("click", () => applyFeeScheduleCoverageBucketFilter({
      orgId: button.dataset.feeCoverageFilterOrg || "",
      procedure: button.dataset.feeCoverageFilterProcedure || ""
    }));
  });
}

// Progressively enhance only the marked fee controls; native options remain authoritative.
let feeSearchSelectSequence = 0;
function enhanceFeeSearchSelects(host) {
  if (!host) return;
  host.querySelectorAll("select[data-fee-searchable-select]").forEach((select) => {
    if (select.dataset.feeSearchEnhanced || select.disabled) return;
    const wrapper = document.createElement("div");
    wrapper.className = "fee-search-select";
    const input = document.createElement("input");
    const list = document.createElement("div");
    const id = `fee-search-options-${++feeSearchSelectSequence}`;
    const label = select.closest("label");
    const name = String(label?.firstChild?.textContent || "Choose an option").trim();
    input.type = "text";
    input.autocomplete = "off";
    input.dataset.feeSearchInput = "";
    input.setAttribute("role", "combobox");
    input.setAttribute("aria-label", name);
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("aria-controls", id);
    input.setAttribute("aria-expanded", "false");
    list.id = id;
    list.className = "fee-search-options";
    list.setAttribute("role", "listbox");
    list.setAttribute("aria-label", name);
    list.hidden = true;
    let visible = [], active = -1;
    const selectedLabel = () => select.selectedOptions[0]?.textContent || "";
    const close = () => { list.hidden = true; input.setAttribute("aria-expanded", "false"); input.removeAttribute("aria-activedescendant"); active = -1; };
    const highlight = () => {
      Array.from(list.children).forEach((option, index) => option.setAttribute("aria-selected", index === active ? "true" : "false"));
      if (active >= 0 && visible[active]) {
        input.setAttribute("aria-activedescendant", `${id}-${active}`);
        const item = list.children[active];
        if (item && item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
        else if (item && item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
      } else input.removeAttribute("aria-activedescendant");
    };
    const choose = (option) => {
      if (!option) return;
      if (!Array.from(select.options).some(item => item.value === option.value)) select.add(new Option(option.textContent, option.value));
      select.value = option.value;
      input.value = option.textContent;
      close();
      const identity = select.id ? "#" + select.id : select.hasAttribute("data-fee-parent-state") ? "[data-fee-parent-state]" : select.hasAttribute("data-fee-parent-picker") ? "[data-fee-parent-picker]" : `[data-fee-missing-filter="${select.dataset.feeMissingFilter}"]`;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      const replacement = document.querySelector(identity)?.nextElementSibling?.querySelector("[data-fee-search-input]");
      if (replacement) { replacement.dataset.feeSearchQuietFocus = "true"; replacement.focus({ preventScroll: true }); }
    };
    let remoteSequence = 0, remoteController;
    const remoteShow = async (query = "", page = 1) => {
      const sequence = ++remoteSequence; remoteController?.abort(); remoteController = new AbortController();
      list.hidden = false; input.setAttribute("aria-expanded", "true"); list.innerHTML = '<div role="status">Loading choices…</div>';
      try {
        const candidate = select.hasAttribute("data-fee-parent-picker") || select.hasAttribute("data-fee-parent-state");
        const result = candidate ? await requestFeeParentChoicePage(feeParentMatchDraft?.row || {}, { search: query, page, stateOnly: select.hasAttribute("data-fee-parent-state"), signal: remoteController.signal }) : select.dataset.feeMissingFilter ? await requestFeeMissingChoices(select.dataset.feeMissingFilter, { search: query, page, signal: remoteController.signal }) : await requestFeeSetupChoices(select.dataset.feeSetupChoice, { search: query, page, header: true, signal: remoteController.signal });
        if (sequence !== remoteSequence || !wrapper.isConnected || document.activeElement !== input) return;
        if (result.candidates && feeParentMatchDraft) feeParentCandidateState = { ...feeParentCandidateState, candidates: result.candidates, page: result.page, revision: result.revision };
        visible = [...(select.hasAttribute("data-fee-parent-picker") ? [] : [new Option("All", "All")]), ...result.options.map(option => new Option(option.value === "(Blank)" ? "Not specified" : option.label, option.value === "(Blank)" ? "__unavailable__" : option.value))];
        list.replaceChildren(); visible.forEach((option,index) => { const item = document.createElement("div"); item.id = id + "-" + index; item.setAttribute("role","option"); item.textContent = option.textContent; item.onmousedown = event => event.preventDefault(); item.onclick = () => choose(option); list.append(item); });
        const pager = document.createElement("div"); pager.className = "tracker-choice-pager"; pager.innerHTML = '<button type="button" data-fee-choice-prev' + (page <= 1 ? ' disabled' : '') + '>Previous</button><span>' + formatCount(result.page?.matchingOptions || 0) + ' choices</span><button type="button" data-fee-choice-next' + (result.page?.hasMore ? '' : ' disabled') + '>Next</button>'; pager.onmousedown = event => event.preventDefault(); pager.querySelector('[data-fee-choice-prev]').onclick = () => remoteShow(query,page-1); pager.querySelector('[data-fee-choice-next]').onclick = () => remoteShow(query,page+1); list.append(pager); active = -1;
      } catch (error) { if (sequence === remoteSequence && error.name !== "AbortError") list.innerHTML = '<div role="alert">Choices unavailable. Type to retry.</div>'; }
    };
    const show = (query = "") => {
      if (select.dataset.feeSetupChoice || (feeScheduleApiEnabled() && (select.dataset.feeMissingFilter || select.hasAttribute("data-fee-parent-picker") || select.hasAttribute("data-fee-parent-state")))) { remoteShow(query); return; }
      visible = Array.from(select.options).filter((option) => !option.disabled && option.textContent.toLowerCase().includes(query.toLowerCase()));
      list.replaceChildren();
      visible.forEach((option, index) => {
        const item = document.createElement("div");
        item.id = `${id}-${index}`;
        item.setAttribute("role", "option");
        item.setAttribute("aria-selected", "false");
        item.textContent = option.textContent;
        item.addEventListener("mousedown", (event) => event.preventDefault());
        item.addEventListener("click", () => choose(option));
        list.append(item);
      });
      if (!visible.length) { const empty = document.createElement("div"); empty.textContent = "No matching choices"; empty.setAttribute("role", "status"); list.append(empty); }
      active = -1;
      list.hidden = false;
      input.setAttribute("aria-expanded", "true");
      input.removeAttribute("aria-activedescendant");
    };
    input.value = selectedLabel();
    input.placeholder = "Type to search";
    input.addEventListener("focus", () => {
      if (input.dataset.feeSearchQuietFocus) { delete input.dataset.feeSearchQuietFocus; return; }
      show(); input.select();
    });
    input.addEventListener("click", () => { if (list.hidden) show(); });
    input.addEventListener("input", () => show(input.value));
    input.addEventListener("blur", () => { close(); input.value = selectedLabel(); });
    input.addEventListener("keydown", (event) => {
      if (["ArrowDown", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        if (list.hidden) show();
        if (visible.length) active = event.key === "ArrowDown" ? (active + 1) % visible.length : (active <= 0 ? visible.length - 1 : active - 1);
        highlight();
      } else if (event.key === "Enter" && !list.hidden) { event.preventDefault(); if (active >= 0) choose(visible[active]); }
      else if (event.key === "Escape") { event.preventDefault(); close(); input.value = selectedLabel(); }
      else if (event.key === "Tab") { close(); input.value = selectedLabel(); }
    });
    select.insertAdjacentElement("afterend", wrapper);
    wrapper.append(input, list);
    select.hidden = true;
    select.dataset.feeSearchEnhanced = "true";
  });
}

async function requestFeeMissingChoices(key, { search = "", page = 1, signal } = {}) {
  const identity = feeScheduleMissingApiKey(), params = new URLSearchParams(feeScheduleApiQuery()), setup = feeScheduleEffectiveFilters(); params.delete("limit");
  if (setup.tenantId && setup.tenantId !== "All") setup.orgIds = activeClientGroupTenant() ? activeClientGroupOrgIds() : orgIdsForTenantName(setup.tenantId);
  const locked = activeClientGroupTenant() && key === "orgId" && activeClientGroupOrgIds().length === 1;
  if (!locked) delete setup[key]; const alias = { payer: "payerName", procedure: "procedureCode", orgId: "orgId" }[key]; if (alias && !locked) params.delete(alias);
  params.set("setupFilters", JSON.stringify(setup)); params.set("operation", "facets"); params.set("facetKeys", JSON.stringify([key])); params.set("facetOptions", JSON.stringify({ [key]: { search, page, pageSize: 50 } }));
  const token = workflowApiToken() || trackerApiToken(), response = await fetch(workflowApiBase() + "/api/setup/fee-schedule-rates/missing?" + params, { cache: "no-store", signal, headers: { ...(token ? { authorization: "Bearer " + token } : {}) } }); const body = await response.json();
  if (identity !== feeScheduleMissingApiKey()) throw new Error("Missing Rates selection changed"); if (!response.ok || body.complete !== true) throw new Error(body.message || "Choices unavailable");
  return { options: (body.facets?.[key] || []).map(row => ({ ...row, value: row.value || "__unavailable__", label: !row.value ? "Not specified" : key === "orgId" ? tenantNameForOrgId(row.value) || row.value : row.value })), page: body.facetPages?.[key] };
}

function renderFeeMissingFilterChange() {
  // User actions must apply even while publication redraws protect a draft.
  requestFeeScheduleMissingApiRows();
  if (feeScheduleMissingAuthoritativeReady()) renderFeeParentMatchPanel();
  else if (els.feeParentMatchPanel) els.feeParentMatchPanel.innerHTML = "";
  renderFeeScheduleMissingPanel(getClaims(), filteredFeeScheduleRows(feeRowsForActiveUser(state.feeScheduleRows || [])));
  wireFeeScheduleMissingHandlers();
}

function feeScheduleMissingFilterChips() {
  const filters = feeScheduleEffectiveFilters();
  const labels = { search: "Search", orgId: "Client", payer: "Insurance", procedure: "CPT", state: "Patient state / classification", network: "Network", tenantId: "Tenant", activeDate: "Active date", matched: "Decision" };
  return Object.entries(labels).filter(([key]) => filters[key] && filters[key] !== "All" && !(activeClientGroupTenant() && (key === "tenantId" || (key === "orgId" && activeClientGroupOrgIds().length === 1)))).map(([key, label]) => {
    const raw = filters[key];
    const value = key === "orgId" ? feeScheduleMissingOrgLabels.get(String(raw)) || "Selected client" : raw === "__unavailable__" ? "Unavailable" : key === "matched" ? (raw === "open" ? "Needs a decision" : "Matched to a parent") : raw;
    return `<button class="small-button" type="button" data-fee-missing-clear="${key}" aria-label="Remove ${escapeHtml(label)} filter">${escapeHtml(label)}: ${escapeHtml(value)} ×</button>`;
  }).join("");
}

function updateFeeScheduleMissingFilter(field = "", value = "", options = {}) {
  const allowed = new Set(["search", "orgId", "procedure", "payer", "matched", "state", "network", "tenantId", "activeDate"]);
  if (!allowed.has(field)) return;
  if (activeClientGroupTenant() && (field === "tenantId" || (field === "orgId" && activeClientGroupOrgIds().length === 1))) return;
  const nextValue = ["search", "activeDate"].includes(field) ? value : (value || "All");
  if ((state.feeScheduleFilters || {})[field] === nextValue) return;
  const focusedSearch = document.activeElement?.matches?.('[data-fee-missing-filter="search"]') ? document.activeElement : null;
  const caret = focusedSearch ? [focusedSearch.selectionStart, focusedSearch.selectionEnd] : null;
  state.feeScheduleFilters = {
    ...(state.feeScheduleFilters || {}),
    [field]: ["search", "activeDate"].includes(field) ? value : (value || "All")
  };
  feeScheduleMissingPage = 1;
  saveState();
  if (options.deferApi) {
    renderFeeScheduleMissingPanel(getClaims(), filteredFeeScheduleRows(feeRowsForActiveUser(state.feeScheduleRows || [])));
    wireFeeScheduleMissingHandlers();
    const search = document.querySelector('[data-fee-missing-filter="search"]');
    if (caret && search) { search.focus({ preventScroll: true }); search.setSelectionRange(...caret); }
    return;
  }
  renderFeeMissingFilterChange();
}

// OON LEARNED RATES (OON_LEARNED_RATES_PLAN.md). One read shared by the staff
// Fee Schedules panel and the client Reports landing. GET /api/oon-learned-rates
// scopes rows to the caller, requires data:financials and blanks names without
// data:phi; this only renders what it returns.
let oonLearnedRatesState = { status: "idle", body: null, error: "", requestedAt: 0, token: "" };
let oonLearnedRatesFilter = "all";
// Every learned row is in the browser; the table shows one page at a time and
// search narrows the whole list, so no row is ever out of reach.
let oonLearnedRatesSearch = "";
let oonLearnedRatesPage = 0;
// One filter bar drives every table on the panel (Nathan, 2026-10-27): search,
// client, plan and code narrow all three; the status chips narrow the plan
// lines and the per-patient list (the fee schedule has no learning status).
let oonLearnedRatesFacets = { client: "", plan: "", code: "" };
const OON_LEARNED_RATES_TTL_MS = 5 * 60 * 1000;
const OON_LEARNED_RATES_PAGE_SIZE = 100;

function oonLearnedRatesMatches(row = {}, search = "") {
  const terms = String(search || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const text = [row.clientName, row.patientName, row.payerName, row.procedureCode, row.modifier].map((value) => String(value || "").toLowerCase()).join(" ");
  return terms.every((term) => text.includes(term));
}

function requestOonLearnedRates(onDone, scope = OON_ALL_COMPANIES_SCOPE) {
  const token = workflowApiToken() || trackerApiToken();
  if (!token) {
    oonLearnedRatesState = { status: "unavailable", body: null, error: "", requestedAt: Date.now(), token: "", scopeKey: scope.key };
    return;
  }
  if (oonLearnedRatesState.status === "pending" && oonLearnedRatesState.scopeKey === scope.key) return;
  if (oonLearnedRatesState.status === "ok" && oonLearnedRatesState.scopeKey === scope.key && Date.now() - oonLearnedRatesState.requestedAt < OON_LEARNED_RATES_TTL_MS) return;
  const requestId = (Number(oonLearnedRatesState.requestId) || 0) + 1;
  oonLearnedRatesState = { ...oonLearnedRatesState, status: "pending", requestedAt: Date.now(), token, scopeKey: scope.key, requestId };
  const query = scope.orgIds.length ? `?orgIds=${encodeURIComponent(scope.orgIds.join(","))}` : "";
  // A slower answer for a company the reader has since moved away from is dropped.
  const current = () => oonLearnedRatesState.requestId === requestId;
  fetch(additionalPortalDataReadUrl(`${workflowApiBase()}/api/oon-learned-rates${query}`), {
    method: "GET",
    cache: "no-store",
    headers: { authorization: `Bearer ${token}` }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!current()) return;
      if (!response.ok) {
        oonLearnedRatesState = { status: "error", body: null, error: String(body.message || "Learned out-of-network rates are not available yet."), requestedAt: Date.now(), token, scopeKey: scope.key, requestId };
      } else if (body.available === false) {
        // The server has no learned-rate source (local/QA, or not wired yet). Not an
        // error and not an empty list: the panel says so, the cards stay hidden.
        oonLearnedRatesState = { status: "unavailable", body: null, error: String(body.message || "Learned out-of-network rates are not available yet."), requestedAt: Date.now(), token, scopeKey: scope.key, requestId };
      } else {
        oonLearnedRatesState = { status: "ok", body, error: "", requestedAt: Date.now(), token, scopeKey: scope.key, requestId };
      }
    })
    .catch(() => {
      if (!current()) return;
      oonLearnedRatesState = { status: "error", body: null, error: "Learned out-of-network rates could not be loaded. Try again later.", requestedAt: Date.now(), token, scopeKey: scope.key, requestId };
    })
    .finally(() => { if (typeof onDone === "function") onDone(); });
}

// One cache, one rule for both screens. A result belongs to the sign-in that fetched
// it (a different or newly present sign-in discards it, including a no-token result),
// and every settled result, including "unavailable" and "error", is re-read after
// the TTL, so a transient failure or a lane wired later does not stick for the session.
function oonLearnedRatesSignInChanged(currentToken) {
  return oonLearnedRatesState.status !== "idle" && String(oonLearnedRatesState.token || "") !== String(currentToken || "");
}

// COMPANY SCOPE OF THE ONE SHARED READ. The Fee Schedules panel follows the page's
// Company filter (header company or the page dropdown); My Work and the client
// card read every company the caller may see. The server applies the org list
// inside its own tenant-scoped SQL, so a filter can only narrow, never widen.
const OON_ALL_COMPANIES_SCOPE = Object.freeze({ key: "", orgIds: [], label: "" });
function oonLearnedRatesCompanyScope() {
  if (typeof feeScheduleEffectiveFilters !== "function") return OON_ALL_COMPANIES_SCOPE;
  const filters = feeScheduleEffectiveFilters();
  const tenant = String(filters.tenantId || "").trim();
  const orgId = String(filters.orgId || "").trim();
  let orgIds = [];
  if (orgId && orgId !== "All") orgIds = [orgId];
  else if (tenant && tenant !== "All") orgIds = activeClientGroupTenant() ? activeClientGroupOrgIds() : orgIdsForTenantName(tenant);
  else return OON_ALL_COMPANIES_SCOPE;
  orgIds = [...new Set(orgIds.map((id) => String(id || "").trim()).filter(Boolean))].sort();
  // A company that resolves to no org must never widen to every company.
  if (!orgIds.length) orgIds = ["__no_matching_org__"];
  const label = activeClientGroupTenant() ? activeClientGroupLabel() : (tenant && tenant !== "All" ? tenant : "");
  return { key: orgIds.join(","), orgIds, label };
}

// My Work follows the header company selector (the page has no company dropdown of its own).
function oonLearnedRatesHeaderScope() {
  if (!activeClientGroupTenant()) return OON_ALL_COMPANIES_SCOPE;
  let orgIds = [...new Set(activeClientGroupOrgIds().map((id) => String(id || "").trim()).filter(Boolean))].sort();
  if (!orgIds.length) orgIds = ["__no_matching_org__"];
  return { key: orgIds.join(","), orgIds, label: activeClientGroupLabel() };
}

function oonLearnedRatesScopeChanged(scope) {
  return oonLearnedRatesState.status !== "idle" && String(oonLearnedRatesState.scopeKey || "") !== scope.key;
}

function oonLearnedRatesReadDue() {
  if (oonLearnedRatesState.status === "idle") return true;
  if (oonLearnedRatesState.status === "pending") return false;
  return Date.now() - Number(oonLearnedRatesState.requestedAt || 0) >= OON_LEARNED_RATES_TTL_MS;
}

function oonRateMoney(value) {
  return value === null || value === undefined || value === "" ? "—" : formatMoneyValue(value, { decimals: 2 });
}

function oonRateStatusPill(row = {}) {
  if (row.state === "rate-cut") {
    const pct = Number(row.changePct);
    return pill(Number.isFinite(pct) && pct < 0 ? `Rate cut \u2212${Math.round(-pct * 100)}%` : "Rate cut", "red");
  }
  if (row.state === "learned") return pill(row.trend === "up" ? "Learned ↑" : "Learned", "green");
  return pill(`Learning ${Number(row.pointCount || 0)}/5`, "gray");
}

function oonPriorRate(row = {}) {
  const segments = Array.isArray(row.segments) ? row.segments : [];
  const last = segments[segments.length - 1];
  return last && last.priorRate !== null && last.priorRate !== undefined ? last.priorRate : null;
}

// CLIENT RATE-CUT CARD. A summary line, the 5 biggest cuts, and "View all" for the
// rest, so the top of Reports stays a short read. Biggest cut first; ties by
// patient. The company column appears only when the cuts span more than one.
const OON_CLIENT_TOP_CUTS = 5;
let oonClientRateCutsExpanded = false;

function oonClientRateCutsSorted(rows = []) {
  const pct = (row) => (Number.isFinite(Number(row.changePct)) ? Number(row.changePct) : 0);
  return [...rows].sort((a, b) => pct(a) - pct(b)
    || String(a.patientName || "").localeCompare(String(b.patientName || ""))
    || String(a.payerName || "").localeCompare(String(b.payerName || "")));
}

function oonCutEffectiveDate(row = {}) {
  const segments = Array.isArray(row.segments) ? row.segments : [];
  const last = segments[segments.length - 1];
  return last && last.effectiveStart ? last.effectiveStart : "";
}

function renderOonClientRateCuts(rows = []) {
  const sorted = oonClientRateCutsSorted(rows);
  const total = sorted.length;
  const expanded = oonClientRateCutsExpanded && total > OON_CLIENT_TOP_CUTS;
  const shown = expanded ? sorted : sorted.slice(0, OON_CLIENT_TOP_CUTS);
  const companies = new Set(sorted.map((row) => String(row.clientName || "").trim()).filter(Boolean));
  const showCompany = companies.size > 1;
  const patients = new Set(sorted.map((row) => String(row.patientName || "").trim()).filter(Boolean)).size;
  const toggle = total > OON_CLIENT_TOP_CUTS
    ? `<button type="button" class="ghost-button" data-oon-client-toggle aria-expanded="${expanded}">${expanded ? "Show top 5" : `View all ${total.toLocaleString()}`}</button>`
    : "";
  const subject = patients ? ` for ${formatNounCount(patients, "patient")}` : "";
  return `
    <div class="oon-rates-head oon-client-head">
      <div>
        <span>Out-of-network rates</span>
        <strong>${total.toLocaleString()} confirmed rate ${total === 1 ? "cut" : "cuts"}${escapeHtml(subject)}</strong>
        <p>These plans now pay at least 10% less per unit, confirmed by 5 payments in a row. ${expanded ? "All cuts, biggest first." : total > OON_CLIENT_TOP_CUTS ? "The 5 biggest are shown." : ""}</p>
      </div>
      ${toggle}
    </div>
    <div class="oon-rates-table-wrap${expanded ? "" : " oon-client-top"}">
      <table class="oon-rates-table">
        <thead><tr><th>Patient</th>${showCompany ? "<th>Company</th>" : ""}<th>Payer</th><th>Code</th><th class="num">Was / unit</th><th class="num">Now / unit</th><th>Change</th><th>Since</th></tr></thead>
        <tbody>
          ${shown.map((row) => `
            <tr>
              <td>${escapeHtml(row.patientName || "—")}</td>
              ${showCompany ? `<td>${escapeHtml(row.clientName || "—")}</td>` : ""}
              <td>${escapeHtml(row.payerName || "—")}</td>
              <td>${escapeHtml([row.procedureCode, row.modifier].filter(Boolean).join(" ") || "—")}</td>
              <td class="num">${oonRateMoney(oonPriorRate(row))}</td>
              <td class="num">${oonRateMoney(row.rate)}</td>
              <td>${oonRateStatusPill(row)}</td>
              <td>${oonCutEffectiveDate(row) ? escapeHtml(formatDate(oonCutEffectiveDate(row))) : "—"}</td>
            </tr>`).join("")}
        </tbody>
      </table>
    </div>
    <small class="oon-rates-note">Rates are learned per patient, plan and code from what each plan actually paid (insurance plus patient responsibility).</small>`;
}

// OUT-OF-NETWORK RATE LOOKUP (Nathan, 2026-10-20: search a client and see, very simply,
// what each out-of-network plan pays them; for SuperAdmin, billers and clients alike).
// One line per client, plan and code: the learned per-unit rate across that client's kids
// (one value, or the range and the typical middle value) and how many kids it is learned
// for, still learning or cut. Built from the same rows as the per-kid table below it.
const OON_SUMMARY_PAGE_SIZE = 25;
let oonSummaryExpanded = false;
let oonKidDetailsOpen = false;

function oonMedian(values = []) {
  const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function oonRateSummaryGroups(rows = []) {
  const groups = new Map();
  rows.forEach((row = {}) => {
    const clientName = String(row.clientName || "").trim();
    const payerName = String(row.payerName || "").trim();
    const code = [row.procedureCode, row.modifier].map((value) => String(value || "").trim()).filter(Boolean).join(" ");
    const key = [clientName, payerName, code].map((value) => value.toLowerCase()).join("|");
    let group = groups.get(key);
    if (!group) {
      group = { clientName, payerName, code, rates: [], learned: 0, learning: 0, cuts: 0 };
      groups.set(key, group);
    }
    if (row.state === "learned" || row.state === "rate-cut") {
      const rate = row.rate === null || row.rate === undefined || row.rate === "" ? NaN : Number(row.rate);
      if (Number.isFinite(rate)) group.rates.push(rate);
      group.learned += 1;
      if (row.state === "rate-cut") group.cuts += 1;
    } else {
      group.learning += 1;
    }
  });
  return [...groups.values()].map((group) => ({
    clientName: group.clientName,
    payerName: group.payerName,
    code: group.code,
    learned: group.learned,
    learning: group.learning,
    cuts: group.cuts,
    min: group.rates.length ? Math.min(...group.rates) : null,
    max: group.rates.length ? Math.max(...group.rates) : null,
    typical: oonMedian(group.rates)
  })).sort((a, b) => a.clientName.localeCompare(b.clientName)
    || a.payerName.localeCompare(b.payerName)
    || a.code.localeCompare(b.code, undefined, { numeric: true }));
}

function oonSummaryRateHtml(group = {}) {
  if (group.min === null || group.min === undefined) return '<span class="oon-muted">Not learned yet</span>';
  if (Math.abs(group.max - group.min) < 0.005) return `<strong>${oonRateMoney(group.min)}</strong>`;
  return `<strong>${oonRateMoney(group.min)} \u2013 ${oonRateMoney(group.max)}</strong><small>typical ${oonRateMoney(group.typical)}</small>`;
}

// The configured side: active fee schedule rows marked out of network for the page's
// company. Expected uses these until a kid's rate is learned. Rows the page has not
// loaded (or a caller without the fee list) simply yield none.
function oonFeeScheduleRateRows(search = "") {
  if (typeof feeScheduleDisplayRows !== "function" || typeof networkBucket !== "function") return [];
  // Not loaded yet (or the list failed): unknown, not "none".
  if (typeof feeScheduleApiEnabled === "function" && feeScheduleApiEnabled() && typeof feeScheduleApiState !== "undefined"
    && !(["ok", "saving"].includes(feeScheduleApiState.status) && feeScheduleApiState.complete === true)) return null;
  let rows = [];
  try { rows = feeScheduleDisplayRows() || []; } catch (_) { return []; }
  const today = new Date().toISOString().slice(0, 10);
  const terms = String(search || "").toLowerCase().split(/\s+/).filter(Boolean);
  return rows
    .filter((row) => networkBucket(row.network) === "OON"
      && String(row.status || "active") !== "archived"
      && (!row.endDate || String(row.endDate).slice(0, 10) >= today))
    .map((row) => ({
      clientName: (typeof tenantNameForOrgId === "function" && tenantNameForOrgId(row.orgId))
        || (String(row.orgId || "").trim() && typeof portalCompanyLabel === "function" ? portalCompanyLabel(row.orgId) : ""),
      payerName: String(row.payer || "").trim(),
      state: String(row.state || "").trim(),
      procedure: String(row.procedure || "").trim(),
      code: [row.procedure, row.m1, row.m2].map((value) => String(value || "").trim()).filter(Boolean).join(" "),
      rate: row.rate,
      startDate: row.startDate || ""
    }))
    .filter((row) => {
      if (!terms.length) return true;
      const text = [row.clientName, row.payerName, row.state, row.code].join(" ").toLowerCase();
      return terms.every((term) => text.includes(term));
    })
    .sort((a, b) => a.clientName.localeCompare(b.clientName)
      || a.payerName.localeCompare(b.payerName)
      || a.code.localeCompare(b.code, undefined, { numeric: true }));
}

// Client, plan and code dropdowns. Values are compared case-insensitively; a
// learned row carries procedureCode, a fee schedule row procedure.
function oonRatesFacetMatches(row = {}, facets = {}, skip = "") {
  const same = (value, wanted) => !wanted || String(value || "").trim().toLowerCase() === wanted.toLowerCase();
  return (skip === "client" || same(row.clientName, facets.client))
    && (skip === "plan" || same(row.payerName, facets.plan))
    && (skip === "code" || same(row.procedureCode ?? row.procedure, facets.code));
}

function oonRatesFacetOptions(rows = [], key = "") {
  const values = new Map();
  rows.forEach((row = {}) => {
    const value = String((key === "client" ? row.clientName : key === "plan" ? row.payerName : row.procedureCode ?? row.procedure) || "").trim();
    if (value && !values.has(value.toLowerCase())) values.set(value.toLowerCase(), value);
  });
  return [...values.values()].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

function oonRatesFacetSelectHtml(key, label, allLabel, options = [], selected = "") {
  return `<label class="oon-filter-field"><span>${escapeHtml(label)}</span>
        <select data-oon-facet="${escapeHtml(key)}" aria-label="${escapeHtml(label)}">
          <option value="">${escapeHtml(allLabel)}</option>
          ${options.map((value) => `<option value="${escapeHtml(value)}"${value.toLowerCase() === selected.toLowerCase() ? " selected" : ""}>${escapeHtml(value)}</option>`).join("")}
        </select></label>`;
}

function renderOonLearnedRatesPanel(host, audience = "staff") {
  if (!host) return;
  // A response belongs to the sign-in that fetched it; another user never sees it.
  const currentToken = workflowApiToken() || trackerApiToken();
  if (oonLearnedRatesSignInChanged(currentToken)) {
    oonLearnedRatesState = { status: "idle", body: null, error: "", requestedAt: 0, token: "" };
    oonClientRateCutsExpanded = false;
  }
  const scope = audience === "client" ? OON_ALL_COMPANIES_SCOPE : oonLearnedRatesCompanyScope();
  if (oonLearnedRatesScopeChanged(scope)) {
    oonLearnedRatesState = { status: "idle", body: null, error: "", requestedAt: 0, token: "", requestId: oonLearnedRatesState.requestId };
    oonLearnedRatesPage = 0;
    oonLearnedRatesFacets = { client: "", plan: "", code: "" };
  }
  if (oonLearnedRatesReadDue()) {
    requestOonLearnedRates(() => { if (host.isConnected) renderOonLearnedRatesPanel(host, audience); }, scope);
  }
  const status = oonLearnedRatesState.status;
  const body = oonLearnedRatesState.body || {};
  const rows = Array.isArray(body.rows) ? body.rows : [];
  // Clients see confirmed rate cuts only, and the card only when there is one.
  const clientCuts = rows.filter((row) => row.state === "rate-cut");
  if (audience === "client" && (status !== "ok" || !clientCuts.length)) {
    host.hidden = true;
    host.innerHTML = "";
    return;
  }
  host.hidden = false;
  const head = `
    <div class="oon-rates-head">
      <div>
        ${scope.label ? `<span>${escapeHtml(scope.label)}</span>` : ""}
        <strong>What each out-of-network plan pays, per unit</strong>
        <p>The filters narrow every table below.</p>
      </div>
    </div>`;
  if (status === "pending" || status === "idle") {
    host.innerHTML = `${head}<p class="oon-rates-note">Loading learned rates…</p>`;
    return;
  }
  if (status !== "ok") {
    host.innerHTML = `${head}<p class="oon-rates-note">${escapeHtml(oonLearnedRatesState.error || "Learned out-of-network rates are not available yet.")}</p>`;
    return;
  }
  if (audience === "client") {
    host.innerHTML = renderOonClientRateCuts(clientCuts);
    host.onclick = (event) => {
      if (!event.target.closest("[data-oon-client-toggle]")) return;
      oonClientRateCutsExpanded = !oonClientRateCutsExpanded;
      renderOonLearnedRatesPanel(host, audience);
      host.querySelector("[data-oon-client-toggle]")?.focus();
    };
    return;
  }
  const builtAt = body.builtAtUtc ? new Date(body.builtAtUtc) : null;
  const builtText = builtAt && !Number.isNaN(builtAt.getTime()) ? `Updated ${builtAt.toLocaleString()}` : "";
  // A client sees a Client column only when it has more than one company.
  const clientNames = new Set(rows.map((row) => String(row.clientName || "").trim()).filter(Boolean));
  const showClient = audience === "staff" || clientNames.size > 1;
  const allFeeRows = oonFeeScheduleRateRows("");
  const facetSource = [...rows, ...(allFeeRows || [])];
  // A choice that the narrower lists no longer offer (another client picked) drops away.
  const clientOptions = oonRatesFacetOptions(facetSource, "client");
  const facets = oonLearnedRatesFacets;
  if (facets.client && !clientOptions.some((value) => value.toLowerCase() === facets.client.toLowerCase())) facets.client = "";
  const planOptions = oonRatesFacetOptions(facetSource.filter((row) => oonRatesFacetMatches(row, facets, "plan")), "plan");
  if (facets.plan && !planOptions.some((value) => value.toLowerCase() === facets.plan.toLowerCase())) facets.plan = "";
  const codeOptions = oonRatesFacetOptions(facetSource.filter((row) => oonRatesFacetMatches(row, facets, "code")), "code");
  if (facets.code && !codeOptions.some((value) => value.toLowerCase() === facets.code.toLowerCase())) facets.code = "";
  const searching = Boolean(oonLearnedRatesSearch.trim());
  const filtering = searching || Boolean(facets.client || facets.plan || facets.code);
  const narrowed = rows.filter((row) => oonRatesFacetMatches(row, facets) && oonLearnedRatesMatches(row, oonLearnedRatesSearch));
  // Status counts follow the other filters, so each chip says what it will show.
  const statusCount = (key) => (key === "all" ? narrowed.length : narrowed.filter((row) => row.state === key).length);
  const filters = [
    ["all", "All"],
    ["rate-cut", "Rate cuts"],
    ["learned", "Learned"],
    ["learning", "Still learning"]
  ].map(([key, label]) => [key, label, statusCount(key)]);
  if (!filters.some(([key]) => key === oonLearnedRatesFilter)) oonLearnedRatesFilter = "all";
  const statusOn = oonLearnedRatesFilter !== "all";
  const visible = narrowed.filter((row) => !statusOn || row.state === oonLearnedRatesFilter);
  const pageCount = Math.max(1, Math.ceil(visible.length / OON_LEARNED_RATES_PAGE_SIZE));
  oonLearnedRatesPage = Math.min(Math.max(0, oonLearnedRatesPage), pageCount - 1);
  const firstIndex = oonLearnedRatesPage * OON_LEARNED_RATES_PAGE_SIZE;
  const shown = visible.slice(firstIndex, firstIndex + OON_LEARNED_RATES_PAGE_SIZE);
  const rangeText = visible.length
    ? `Rows ${(firstIndex + 1).toLocaleString()}–${(firstIndex + shown.length).toLocaleString()} of ${visible.length.toLocaleString()}`
    : "No rows";
  // Plan lines keep their full rate range; the status chip picks which lines show.
  const groups = oonRateSummaryGroups(narrowed).filter((group) => !statusOn
    || (oonLearnedRatesFilter === "rate-cut" ? group.cuts > 0 : oonLearnedRatesFilter === "learned" ? group.learned > group.cuts : group.learning > 0));
  const groupsShown = oonSummaryExpanded ? groups : groups.slice(0, OON_SUMMARY_PAGE_SIZE);
  const feeRows = allFeeRows && oonFeeScheduleRateRows(oonLearnedRatesSearch).filter((row) => oonRatesFacetMatches(row, facets));
  const lineWord = (count) => `${count.toLocaleString()} ${count === 1 ? "line" : "lines"}`;
  host.innerHTML = `
    ${head}
    <ol class="oon-how" aria-label="How out-of-network rates work">
      <li><strong>No contract rate.</strong> An out-of-network plan has no agreed rate, so the portal learns what the plan actually pays.</li>
      <li><strong>Learned after 5 payments.</strong> For each patient, plan and code, the rate is the middle of the last 5 payments per unit (insurance paid plus patient responsibility).</li>
      <li><strong>Until then, the fee schedule.</strong> Expected uses the out-of-network fee schedule rate below. A rate cut is confirmed when 5 payments in a row come in at least 10% lower.</li>
    </ol>
    <div class="oon-filter-bar" role="group" aria-label="Filter out-of-network rates">
      <label class="oon-filter-field oon-filter-search"><span>Search</span>
        <input type="search" class="oon-rates-search" data-oon-search placeholder="Client, plan, code or patient" aria-label="Search out-of-network rates" value="${escapeHtml(oonLearnedRatesSearch)}"></label>
      ${showClient && clientOptions.length > 1 ? oonRatesFacetSelectHtml("client", "Client", "All clients", clientOptions, facets.client) : ""}
      ${oonRatesFacetSelectHtml("plan", "Plan", "All plans", planOptions, facets.plan)}
      ${oonRatesFacetSelectHtml("code", "Code", "All codes", codeOptions, facets.code)}
      <div class="oon-filter-field"><span>Status</span>
        <div class="oon-status-chips" role="group" aria-label="Patient rate status">
          ${filters.map(([key, label, count]) => `<button type="button" class="oon-status-chip${key === oonLearnedRatesFilter ? " active" : ""}${key === "rate-cut" && count ? " alert" : ""}" data-oon-filter="${escapeHtml(key)}" aria-pressed="${key === oonLearnedRatesFilter}">${escapeHtml(label)} <b>${count.toLocaleString()}</b></button>`).join("")}
        </div>
      </div>
      ${filtering || statusOn ? '<button type="button" class="ghost-button oon-filter-clear" data-oon-clear>Clear filters</button>' : ""}
    </div>
    <section class="oon-rates-section" aria-label="Learned out-of-network rate by plan and code">
      <h3>Learned rate per unit, by plan and code <small>${lineWord(groups.length)}</small></h3>
      <div class="oon-rates-table-wrap">
        <table class="oon-rates-table oon-plan-table">
          <thead><tr>${showClient ? "<th>Client</th>" : ""}<th>Plan</th><th>Code</th><th class="num">Rate / unit</th><th class="num">Patients learned</th><th class="num">Still learning</th><th class="num">Rate cuts</th></tr></thead>
          <tbody data-oon-summary-rows>
            ${groupsShown.length ? groupsShown.map((group) => `
            <tr>
              ${showClient ? `<td>${escapeHtml(group.clientName || "—")}</td>` : ""}
              <td>${escapeHtml(group.payerName || "—")}</td>
              <td>${escapeHtml(group.code || "—")}</td>
              <td class="num oon-plan-rate">${oonSummaryRateHtml(group)}</td>
              <td class="num">${group.learned.toLocaleString()}</td>
              <td class="num">${group.learning.toLocaleString()}</td>
              <td class="num">${group.cuts ? `<span class="oon-cut-count">${group.cuts.toLocaleString()}</span>` : "0"}</td>
            </tr>`).join("") : `
            <tr><td colspan="${showClient ? 7 : 6}"><strong>${filtering || statusOn ? "No learned rates match these filters." : "No out-of-network payments have been learned yet."}</strong></td></tr>`}
          </tbody>
        </table>
      </div>
      ${groups.length > OON_SUMMARY_PAGE_SIZE ? `<button type="button" class="ghost-button" data-oon-summary-toggle aria-expanded="${oonSummaryExpanded}">${oonSummaryExpanded ? `Show the first ${OON_SUMMARY_PAGE_SIZE}` : `Show all ${groups.length.toLocaleString()}`}</button>` : ""}
    </section>
    <section class="oon-rates-section" aria-label="Out-of-network fee schedule rates">
      <h3>Out-of-network fee schedule rates <small>used until a patient's rate is learned${feeRows ? ` · ${feeRows.length.toLocaleString()} ${feeRows.length === 1 ? "rate" : "rates"}` : ""}</small></h3>
      ${feeRows && feeRows.length ? `
      <div class="oon-rates-table-wrap">
        <table class="oon-rates-table oon-fee-table">
          <thead><tr>${showClient ? "<th>Client</th>" : ""}<th>Plan</th><th>State</th><th>Code</th><th class="num">Rate / unit</th><th>Since</th></tr></thead>
          <tbody data-oon-fee-rows>
            ${feeRows.map((row) => `
            <tr>
              ${showClient ? `<td>${escapeHtml(row.clientName || "—")}</td>` : ""}
              <td>${escapeHtml(row.payerName || "—")}</td>
              <td>${escapeHtml(row.state || "—")}</td>
              <td>${escapeHtml(row.code || "—")}</td>
              <td class="num">${oonRateMoney(row.rate)}</td>
              <td>${row.startDate ? escapeHtml(formatDate(row.startDate)) : "—"}</td>
            </tr>`).join("")}
          </tbody>
        </table>
      </div>` : `<p class="oon-rates-note">${!feeRows ? "The fee schedule is still loading; its out-of-network rates appear here once it has loaded." : filtering ? "No out-of-network fee schedule rates match these filters." : "No out-of-network rates are on the fee schedule for this selection."}</p>`}
    </section>
    <details class="oon-kid-details" data-oon-kids${oonKidDetailsOpen ? " open" : ""}>
      <summary data-oon-kids-toggle>Each patient's rate <small>${filtering || statusOn ? `${visible.length.toLocaleString()} of ` : ""}${rows.length.toLocaleString()} ${rows.length === 1 ? "patient, plan and code" : "patient, plan and code lines"}</small></summary>
      ${pageCount > 1 ? `
      <div class="oon-rates-toolbar">
        <div class="oon-rates-pager">
          <span aria-live="polite">${escapeHtml(rangeText)}</span>
          <button type="button" class="ghost-button" data-oon-page="-1"${oonLearnedRatesPage > 0 ? "" : " disabled"}>Previous</button>
          <span>Page ${(oonLearnedRatesPage + 1).toLocaleString()} of ${pageCount.toLocaleString()}</span>
          <button type="button" class="ghost-button" data-oon-page="1"${oonLearnedRatesPage < pageCount - 1 ? "" : " disabled"}>Next</button>
        </div>
      </div>` : ""}
      <div class="oon-rates-table-wrap">
        <table class="oon-rates-table">
          <thead>
            <tr>
              ${showClient ? "<th>Client</th>" : ""}
              <th>Patient</th>
              <th>Payer</th>
              <th>Code</th>
              <th class="num">Rate / unit</th>
              <th class="num">Was</th>
              <th class="num">Payments</th>
              <th>Last payment DOS</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody data-oon-kid-rows>
            ${shown.length ? shown.map((row) => `
              <tr>
                ${showClient ? `<td>${escapeHtml(row.clientName || "—")}</td>` : ""}
                <td>${escapeHtml(row.patientName || "—")}</td>
                <td>${escapeHtml(row.payerName || "—")}</td>
                <td>${escapeHtml([row.procedureCode, row.modifier].filter(Boolean).join(" "))}</td>
                <td class="num">${oonRateMoney(row.rate)}</td>
                <td class="num">${row.state === "rate-cut" ? oonRateMoney(oonPriorRate(row)) : "—"}</td>
                <td class="num">${Number(row.pointCount || 0).toLocaleString()}</td>
                <td>${row.lastPaymentDos ? escapeHtml(formatDate(row.lastPaymentDos)) : "—"}</td>
                <td>${oonRateStatusPill(row)}</td>
              </tr>`).join("") : `
              <tr><td colspan="${showClient ? 9 : 8}"><strong>${filtering || statusOn ? "No rows match these filters." : "Nothing here."}</strong></td></tr>`}
          </tbody>
        </table>
      </div>
      <small class="oon-rates-note">${escapeHtml([
        body.truncated ? "The list hit its row limit; filter by company to see the rest." : "",
        builtText
      ].filter(Boolean).join(" "))}</small>
    </details>`;
  const rerender = (focusSelector) => {
    renderOonLearnedRatesPanel(host, audience);
    if (focusSelector) host.querySelector(focusSelector)?.focus();
  };
  host.onclick = (event) => {
    const kidsToggle = event.target.closest("[data-oon-kids-toggle]");
    if (kidsToggle) {
      event.preventDefault();
      oonKidDetailsOpen = !oonKidDetailsOpen;
      rerender("[data-oon-kids-toggle]");
      return;
    }
    const summaryToggle = event.target.closest("[data-oon-summary-toggle]");
    if (summaryToggle) {
      oonSummaryExpanded = !oonSummaryExpanded;
      rerender("[data-oon-summary-toggle]");
      return;
    }
    const pageButton = event.target.closest("[data-oon-page]");
    if (pageButton) {
      oonLearnedRatesPage += Number(pageButton.getAttribute("data-oon-page")) || 0;
      rerender(`[data-oon-page="${pageButton.getAttribute("data-oon-page")}"]:not([disabled])`);
      return;
    }
    if (event.target.closest("[data-oon-clear]")) {
      oonLearnedRatesSearch = "";
      oonLearnedRatesFacets = { client: "", plan: "", code: "" };
      oonLearnedRatesFilter = "all";
      oonLearnedRatesPage = 0;
      rerender("[data-oon-search]");
      return;
    }
    const button = event.target.closest("[data-oon-filter]");
    if (!button) return;
    oonLearnedRatesFilter = button.getAttribute("data-oon-filter") || "all";
    oonLearnedRatesPage = 0;
    rerender(`[data-oon-filter="${oonLearnedRatesFilter}"]`);
  };
  host.onchange = (event) => {
    const select = event.target.closest("[data-oon-facet]");
    if (!select) return;
    const key = select.getAttribute("data-oon-facet");
    if (!["client", "plan", "code"].includes(key)) return;
    oonLearnedRatesFacets = { ...oonLearnedRatesFacets, [key]: select.value };
    oonLearnedRatesPage = 0;
    rerender(`[data-oon-facet="${key}"]`);
  };
  host.oninput = (event) => {
    const input = event.target.closest("[data-oon-search]");
    if (!input) return;
    const caret = input.selectionStart;
    oonLearnedRatesSearch = input.value;
    oonLearnedRatesPage = 0;
    renderOonLearnedRatesPanel(host, audience);
    const next = host.querySelector("[data-oon-search]");
    if (next) {
      next.focus();
      try { next.setSelectionRange(caret, caret); } catch (_) { /* search inputs may refuse a range */ }
    }
  };
}

function renderFeeScheduleCoveragePanel(rows = [], scopedRows = []) {
  const host = els.feeScheduleCoveragePanel;
  if (!host) return;
  const health = state.workflowApiHealth || {};
  const alignment = health.dataAlignment || {};
  const coverage = alignment.candidateCoverage || {};
  const zeroGap = coverage.zeroExpectedGap || {};
  const available = Boolean(alignment.configured && alignment.available && coverage && Object.keys(coverage).length);
  const activeFilters = feeScheduleEffectiveFilters();
  if ((activeFilters.tenantId && activeFilters.tenantId !== "All") || (activeFilters.orgId && activeFilters.orgId !== "All")) {
    host.innerHTML = '<div class="fee-coverage-head"><div><span>Company coverage</span><strong>Review Missing Rates for this company</strong><p>The prepared coverage summary is portal-wide. Use the company-filtered Missing Rates worklist for the selected company; portal-wide totals are hidden here.</p></div></div>';
    return;
  }
  const topBuckets = feeScheduleCoverageBucketsForFilters(zeroGap.topProcedureOrgBuckets || [], activeFilters);
  const totalUnique = Number(coverage.uniquePrimaryOnlyRows || 0) + Number(coverage.uniqueSecondaryPresentRows || 0);
  const unresolvedSetup = Number(coverage.noFeeCandidateRows || 0) + Number(coverage.ambiguousCandidateRows || 0);
  const zeroExpectedOpen = Number(zeroGap.noFeeCandidateRows || 0)
    + Number(zeroGap.ambiguousCandidateRows || 0)
    + Number(zeroGap.secondaryPresentRows || 0)
    + Number(zeroGap.primaryOnlyUniqueRows || 0);
  const freshness = preparedCacheFreshnessText(coverage.cacheFreshness);
  const localApproved = feeScheduleApiEnabled() ? Number(feeScheduleApiState.summary?.approved || 0) : feeScheduleApprovalCounts(scopedRows).approved;
  const activeFilterText = feeScheduleActiveCoverageFilterText(activeFilters);
  if (!available) {
    host.innerHTML = `
      <div class="fee-coverage-head">
        <div>
          <span>Prepared Coverage</span>
          <strong>Coverage cache offline</strong>
          <p>The coverage summary is unavailable. Missing Rates continues to use validated rate settings.</p>
        </div>
        ${pill("offline", "gray")}
      </div>
    `;
    return;
  }
  host.innerHTML = `
    <div class="fee-coverage-head">
      <div>
        <span>Prepared Coverage</span>
        <strong>${escapeHtml(unresolvedSetup ? "Setup gaps still open" : "Coverage cache ready")}</strong>
        <p>Read-only evidence from prepared rate-alignment cache tables. This does not change reimbursement formulas; it shows where fee setup still needs approved rates or disambiguation.</p>
        <small>${escapeHtml(activeFilterText)}${freshness ? ` · ${escapeHtml(freshness)}` : ""}</small>
      </div>
      ${pill(coverage.cacheFreshness?.stale ? "cache stale" : "cache ready", coverage.cacheFreshness?.stale ? "amber" : "green")}
    </div>
    <div class="fee-coverage-grid">
      ${[
        ["Approved Setup Rows", localApproved, "Portal-managed rates approved in current scope"],
        ["Unique Rate Candidates", totalUnique, "Prepared rows with one usable primary-rate candidate"],
        ["Setup Gaps", unresolvedSetup, "No-fee or ambiguous prepared candidate rows"],
        ["Zero Expected Open AR", zeroExpectedOpen, "Open-balance rows still guarded by fee/secondary setup"]
      ].map(([label, value, detail]) => `
        <div class="fee-coverage-card">
          <span>${escapeHtml(label)}</span>
          <strong>${Number(value || 0).toLocaleString()}</strong>
          <small>${escapeHtml(detail)}</small>
        </div>
      `).join("")}
    </div>
    <div class="fee-coverage-table-wrap">
      <table class="fee-coverage-table">
        <thead>
          <tr>
            <th>Setup Bucket</th>
            <th>Company</th>
            <th>Procedure</th>
            <th>Rows</th>
            <th>Open Balance</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          ${topBuckets.length ? topBuckets.slice(0, 8).map(renderFeeScheduleCoverageBucketRow).join("") : `
            <tr>
              <td colspan="6"><strong>No matching top setup buckets.</strong><small>The prepared cache did not return an unresolved org/procedure bucket for the active filters.</small></td>
            </tr>
          `}
        </tbody>
      </table>
    </div>
  `;
}

function feeScheduleCoverageBucketsForFilters(buckets = [], filters = {}) {
  const orgFilter = String(filters.orgId || "All");
  const procedureFilter = String(filters.procedure || "All");
  return (Array.isArray(buckets) ? buckets : [])
    .filter((bucket) => orgFilter === "All" || String(bucket.orgId || "") === orgFilter)
    .filter((bucket) => procedureFilter === "All" || String(bucket.procedure || "") === procedureFilter)
    .sort((a, b) => Number(b.rows || 0) - Number(a.rows || 0) || Number(b.balance || 0) - Number(a.balance || 0));
}

function feeScheduleActiveCoverageFilterText(filters = {}) {
  const labels = [];
  if (filters.tenantId && filters.tenantId !== "All") labels.push(`tenant ${filters.tenantId}`);
  if (filters.orgId && filters.orgId !== "All") labels.push(`company ${portalCompanyLabel(filters.orgId)}`);
  if (filters.procedure && filters.procedure !== "All") labels.push(`procedure ${filters.procedure}`);
  if (filters.payer && filters.payer !== "All") labels.push(`payer ${filters.payer}`);
  return labels.length ? `Focused on ${labels.join(", ")}` : "All accessible setup scope";
}

function renderFeeScheduleCoverageBucketRow(bucket = {}) {
  const status = String(bucket.status || "unresolved");
  return `
    <tr>
      <td><strong>${escapeHtml(feeScheduleCoverageStatusLabel(status))}</strong><small>${escapeHtml(status)}</small></td>
      <td>${escapeHtml(portalCompanyLabel(bucket.orgId))}</td>
      <td>${escapeHtml(bucket.procedure || "unknown")}</td>
      <td>${Number(bucket.rows || 0).toLocaleString()}</td>
      <td>${money(bucket.balance || 0)}</td>
      <td>
        <small>${escapeHtml(feeScheduleCoverageNextAction(status))}</small>
        <button class="small-button fee-coverage-focus-btn" type="button" data-fee-coverage-filter-org="${escapeHtml(bucket.orgId || "")}" data-fee-coverage-filter-procedure="${escapeHtml(bucket.procedure || "")}">Focus setup</button>
      </td>
    </tr>
  `;
}

function feeScheduleCoverageStatusLabel(status = "") {
  if (/no_fee/i.test(status)) return "Missing approved fee candidate";
  if (/ambiguous/i.test(status)) return "Ambiguous rate candidate";
  if (/secondary/i.test(status)) return "Secondary expected pending";
  if (/primary/i.test(status)) return "Primary rate present";
  return "Setup review needed";
}

function feeScheduleCoverageNextAction(status = "") {
  if (/no_fee/i.test(status)) return "Add or approve company/payer/procedure rate rows with state, POS, modifier, network, and effective dates.";
  if (/ambiguous/i.test(status)) return "End-date, archive, or refine overlapping rate rows until one approved candidate wins.";
  if (/secondary/i.test(status)) return "Keep secondary logic guarded until secondary expected rules are approved.";
  return "Review prepared evidence before changing money-driving setup.";
}

function applyFeeScheduleCoverageBucketFilter({ orgId = "", procedure = "" } = {}) {
  state.feeScheduleFilters = {
    ...(state.feeScheduleFilters || {}),
    tenantId: "All",
    orgId: orgId || "All",
    procedure: procedure || "All",
    payer: "All",
    state: "All",
    network: "All",
    activeDate: "",
    search: ""
  };
  saveState();
  renderFeeSchedules();
  showToast(`Focused Fee Schedules on ${[orgId ? `company ${portalCompanyLabel(orgId)}` : "", procedure ? `procedure ${procedure}` : ""].filter(Boolean).join(" · ") || "prepared coverage bucket"}.`);
}

function renderFeeScheduleMissingPanel(claims = getClaims(), feeRows = state.feeScheduleRows || []) {
  const host = els.feeScheduleMissingPanel;
  if (!host) return;
  const summaryOpen = Boolean(host.querySelector(".fee-missing-summary")?.open);
  const aboutOpen = Boolean(host.querySelector(".fee-missing-totals-detail")?.open);
  const priorSearch = host.contains?.(document.activeElement) && document.activeElement?.matches?.('[data-fee-missing-filter="search"]') ? document.activeElement : null;
  const searchCaret = priorSearch ? [priorSearch.selectionStart, priorSearch.selectionEnd] : null;
  const priorCombo = host.contains?.(document.activeElement) && document.activeElement?.matches?.("[data-fee-search-input]") ? document.activeElement : null;
  const comboFocus = priorCombo ? { field: priorCombo.closest("label")?.querySelector("[data-fee-missing-filter]")?.dataset.feeMissingFilter, value: priorCombo.value, start: priorCombo.selectionStart, end: priorCombo.selectionEnd, expanded: priorCombo.getAttribute("aria-expanded") === "true" } : null;
  const usingApi = feeScheduleMissingApiState.status === "ok";
  const apiPending = feeScheduleMissingApiState.status === "pending";
  const apiError = feeScheduleMissingApiState.status === "error";
  const authoritativeApiRequired = feeScheduleMissingAuthoritativeRequired();
  if (authoritativeApiRequired && !usingApi) {
    const unavailable = apiError || !feeScheduleApiEnabled();
    host.innerHTML = `
      <div class="fee-missing-head">
        <div>
          <span>Missing Rate Worklist</span>
          <strong>${unavailable ? "Worklist unavailable" : "Loading worklist"}</strong>
          <p>${unavailable ? "The authoritative missing-rate worklist could not be loaded. No browser-derived rows, totals, or setup actions are shown because they may be incomplete." : "The authoritative worklist is loading. Totals and setup actions will appear only when loading is complete."}</p>
          <small>${unavailable ? escapeHtml(String(feeScheduleMissingApiState.error || "Live coverage data is unavailable. Pick a client in the header and retry.")) : "Please hold while current coverage is checked."}</small>
        </div>
        ${pill(unavailable ? "unavailable" : "checking", unavailable ? "red" : "amber")}
      </div>
      <div class="fee-missing-grid">
        ${["Combinations", "Service Rows", "Fallback Expected Currently Used", "Units", "Open AR"].map((label) => `
          <div class="fee-missing-card">
            <span>${escapeHtml(label)}</span>
            <strong>${MONEY_UNKNOWN_TEXT}</strong>
            <small>Unavailable until the worklist loads</small>
          </div>
        `).join("")}
      </div>
      <div class="fee-missing-table-wrap">
        <table class="fee-missing-table">
          <tbody>
            <tr>
              <td><strong>${unavailable ? "Missing-rate data is unavailable." : "Checking current coverage."}</strong><small>${unavailable ? "Retry the authoritative query; local claim rows are intentionally not used as a substitute." : "No setup decisions are available while the query is pending."}</small></td>
              <td>${unavailable ? '<button class="small-button" type="button" data-fee-missing-retry>Retry</button>' : ""}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;
    return;
  }
  const sourceRows = (usingApi ? feeScheduleMissingApiState.rows : feeScheduleMissingRows(claims, feeRows)).filter(feeScheduleRowInSelectedCompany);
  const serverPaged = usingApi && Boolean(feeScheduleMissingApiState.page);
  const rows = serverPaged ? sourceRows : sourceRows.filter(feeScheduleMissingRowMatchesFilters);
  const sortedRows = serverPaged ? rows : feeScheduleMissingSortedRows(rows);
  const pageWindow = feeScheduleMissingPageWindow(sortedRows);
  const visibleRows = pageWindow.rows;
  const hiddenBook = feeScheduleMissingHiddenFilterBook(sourceRows, rows);
  const filterOptions = feeScheduleMissingFilterOptions(sourceRows);
  feeScheduleMissingOrgLabels = new Map(filterOptions.orgs.map((option) => [String(option.value), option.label]));
  const totals = rows.reduce((sum, row) => {
    const expected = feeScheduleOptionalMoney(row.expected);
    const openAr = feeScheduleOptionalMoney(row.openAr);
    const preparedFallbackExpected = feeScheduleOptionalMoney(row.preparedFallbackExpected);
    sum.combinations += 1;
    sum.services += row.services;
    sum.claims += row.claims;
    if (row.units == null) sum.unknownUnits += 1;
    else sum.units += row.units;
    if (isUnknownMoney(expected)) sum.unknownExpected += 1;
    else sum.expected += moneyNumber(expected);
    if (isUnknownMoney(openAr)) sum.unknownOpenAr += 1;
    else {
      const openArValue = moneyNumber(openAr);
      sum.openAr += openArValue;
      // A negative owed balance is a credit, not collectible AR. Netting it
      // into the headline read "Open AR -$51,358" on TEST (161 of 431
      // combinations were credits worth -$209,200 against $157,843 owed).
      // Keep both and name both; never clamp, never hide.
      if (openArValue < 0) { sum.creditAr += openArValue; sum.creditCombinations += 1; }
      else { sum.collectibleAr += openArValue; sum.collectibleCombinations += 1; }
    }
    if (isUnknownMoney(preparedFallbackExpected)) sum.unknownPreparedFallbackExpected += 1;
    else sum.preparedFallbackExpected += moneyNumber(preparedFallbackExpected);
    if (row.preparedFallbackServices == null) sum.unknownPreparedFallbackServices += 1;
    else sum.preparedFallbackServices += Number(row.preparedFallbackServices || 0);
    return sum;
  }, { combinations: 0, services: 0, claims: 0, units: 0, expected: 0, openAr: 0, collectibleAr: 0, creditAr: 0, collectibleCombinations: 0, creditCombinations: 0, preparedFallbackExpected: 0, preparedFallbackServices: 0, unknownUnits: 0, unknownExpected: 0, unknownOpenAr: 0, unknownPreparedFallbackExpected: 0, unknownPreparedFallbackServices: 0 });
  if (serverPaged) Object.assign(totals, feeScheduleMissingApiState.totals || {});
  const partialWorklist = usingApi && feeScheduleMissingApiState.truncated;
  const totalsScopeText = partialWorklist
    ? "Partial totals for matching combinations in the loaded list. More combinations may exist beyond the server limit."
    : "Totals for all matching combinations.";
  const pagerHtml = rows.length && !apiPending ? `
    <nav class="fee-missing-pagination" aria-label="Missing Rates pages">
      <div class="fee-missing-pagination-copy" aria-live="polite" aria-atomic="true">
        <strong>Showing ${pageWindow.from.toLocaleString()}–${pageWindow.to.toLocaleString()} of ${pageWindow.totalRows.toLocaleString()} ${usingApi && feeScheduleMissingApiState.truncated ? "loaded" : "matching"} combinations</strong>
        <small>Page ${pageWindow.page.toLocaleString()} of ${pageWindow.totalPages.toLocaleString()}${usingApi && feeScheduleMissingApiState.truncated ? ". The server limit was reached, so more combinations exist outside this loaded list." : "."}</small>
      </div>
      <div class="fee-missing-pagination-actions">
        <button class="small-button" type="button" data-fee-missing-page="previous" ${pageWindow.page <= 1 ? "disabled" : ""}>Previous</button>
        <button class="small-button" type="button" data-fee-missing-page="next" ${pageWindow.page >= pageWindow.totalPages ? "disabled" : ""}>Next</button>
      </div>
    </nav>
  ` : "";
  host.innerHTML = `
    <div class="fee-missing-head">
      <div>
        <span>Missing Rate Worklist</span>
        <strong>${apiPending ? "Loading worklist" : apiError ? (rows.length ? `${formatCount(rows.length)} missing combination${rows.length === 1 ? "" : "s"} (local estimate)` : "Worklist unavailable") : rows.length ? (partialWorklist ? `${formatCount(rows.length)} loaded missing combination${rows.length === 1 ? "" : "s"} (partial list)` : `${formatCount(serverPaged ? totals.combinations : rows.length)} missing combination${(serverPaged ? totals.combinations : rows.length) === 1 ? "" : "s"}`) : (hiddenBook ? "No missing combinations match these filters" : "No missing combinations in scope")}</strong>
        <details class="fee-missing-totals-detail" ${aboutOpen ? "open" : ""}><summary>About missing rates</summary><p>Exact client/payer/CPT/modifier/POS/network groups where active service rows do not find a configured fee schedule. A configured $0 is a valid contractual rate for a billable non-payable code; only missing, ambiguous, or unreadable matches stay here.</p></details>
        <small>${escapeHtml(feeScheduleMissingPanelStatusText({ usingApi, apiPending, apiError, rows }))}</small>
      </div>
      ${pill(apiPending ? "checking" : apiError ? (rows.length ? "local estimate" : "unavailable") : rows.length ? "needs setup" : (hiddenBook ? "filtered" : "covered"), apiPending ? "amber" : apiError ? (rows.length ? "blue" : "amber") : rows.length ? "red" : (hiddenBook ? "amber" : "green"))}
    </div>
    <details class="fee-missing-summary" ${summaryOpen ? "open" : ""}><summary>Coverage totals and counting notes</summary>
    <p class="muted-note fee-missing-totals-scope" data-fee-missing-totals-scope>${escapeHtml(totalsScopeText)}</p>
    <div class="fee-missing-grid">
      ${[
        [partialWorklist ? "Loaded combinations" : "Combinations", Number(totals.combinations || 0).toLocaleString(), "Grouped missing payer and service setup keys"],
        [partialWorklist ? "Service rows in loaded list" : "Service Rows", Number(totals.services || 0).toLocaleString(), "Summed per combination; a service row missing both its primary and secondary rate counts in each"],
        [partialWorklist ? "Fallback expected in loaded list" : "Fallback Expected Currently Used", money(totals.unknownPreparedFallbackExpected ? UNKNOWN_MONEY : totals.preparedFallbackExpected), totals.unknownPreparedFallbackExpected ? `Unknown for ${formatNounCount(totals.unknownPreparedFallbackExpected, "combination")}; no incomplete total is shown` : totals.unknownPreparedFallbackServices ? `Dollar value is reported; fallback service count is unknown for ${formatNounCount(totals.unknownPreparedFallbackServices, "combination")}` : `${formatNounCount(Number(totals.preparedFallbackServices || 0), "service row")} still using fallback math`],
        [partialWorklist ? "Units in loaded list" : "Units", totals.unknownUnits ? MONEY_UNKNOWN_TEXT : Number(totals.units || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }), totals.unknownUnits ? `Unknown for ${formatNounCount(totals.unknownUnits, "combination")}; no incomplete total is shown` : "Units waiting on rate setup"],
        [partialWorklist ? "Open AR in loaded list" : "Open AR", money(totals.unknownOpenAr ? UNKNOWN_MONEY : totals.collectibleAr), totals.unknownOpenAr ? `Unknown for ${formatNounCount(totals.unknownOpenAr, "combination")}; no incomplete total is shown` : feeScheduleMissingOpenArDetail(totals)]
      ].map(([label, value, detail]) => `
        <div class="fee-missing-card">
          <span>${escapeHtml(label)}</span>
          <strong>${escapeHtml(String(value))}</strong>
          <details class="fee-missing-metric-details"><summary>Details</summary><small>${escapeHtml(detail)}</small></details>
        </div>
      `).join("")}
    </div>
    </details>
    <div class="fee-missing-toolbar">
      <label class="fee-missing-search">Search<input data-fee-missing-filter="search" value="${escapeHtml((state.feeScheduleFilters || {}).search || "")}" placeholder="Insurance, client, CPT or state"></label>
      <label>Company<select data-fee-searchable-select data-fee-missing-filter="orgId" ${activeClientGroupTenant() && activeClientGroupOrgIds().length === 1 ? "disabled" : ""}>${feeScheduleMissingOptionHtml(filterOptions.orgs, feeScheduleEffectiveFilters().orgId || "All", "All orgs")}</select></label>
      <label>CPT<select data-fee-searchable-select data-fee-missing-filter="procedure">${feeScheduleMissingOptionHtml(filterOptions.procedures, (state.feeScheduleFilters || {}).procedure || "All", "All CPTs")}</select></label>
      <label>Insurance<select data-fee-searchable-select data-fee-missing-filter="payer">${feeScheduleMissingOptionHtml(filterOptions.payers, (state.feeScheduleFilters || {}).payer || "All", "All payers")}</select></label>
      <label>Patient state / classification<select data-fee-searchable-select data-fee-missing-filter="state">${feeScheduleMissingOptionHtml(filterOptions.states, (state.feeScheduleFilters || {}).state || "All", "All states / classifications")}</select></label>
      <label>Network<select data-fee-searchable-select data-fee-missing-filter="network">${feeScheduleMissingOptionHtml(filterOptions.networks, (state.feeScheduleFilters || {}).network || "All", "All networks")}</select></label>
      <label>Decision<select data-fee-missing-filter="matched">
        ${[
          ["open", "Needs a decision"],
          ["matched", "Matched to a parent"],
          ["All", "All rows"]
        ].map(([value, label]) => `<option value="${escapeHtml(value)}" ${((state.feeScheduleFilters || {}).matched || "open") === value ? "selected" : ""}>${escapeHtml(label)}</option>`).join("")}
      </select></label>
      <label>Sort<select data-fee-missing-sort>
        ${[
          ["preparedGap", "Fallback expected currently used"],
          ["openAr", "Open AR"],
          ["services", "Service rows"],
          ["expected", "Expected"],
          ["client", "Client / org"],
          ["cpt", "CPT"]
        ].map(([value, label]) => `<option value="${escapeHtml(value)}" ${feeScheduleMissingEffectiveSort(rows) === value ? "selected" : ""}>${escapeHtml(label)}</option>`).join("")}
      </select></label>
    </div>
    <div class="fee-missing-filter-chips" aria-label="Active worklist filters"><button class="small-button" type="button" data-fee-missing-clear="all">Clear filters</button>${feeScheduleMissingFilterChips()}<small>Header client scope still applies.</small></div>
    ${pagerHtml}
    <div class="fee-missing-table-wrap" role="region" aria-label="Missing rate combinations" tabindex="0">
      <table class="fee-missing-table">
        <thead>
          <tr>
            <th>Missing Setup Key</th>
            <th>Client / Org</th>
            <th>Services</th>
            <th>Units</th>
            <th>Fallback Expected Currently Used</th>
            <th>Expected</th>
            <th>Open AR</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          ${apiPending ? `
            <tr>
              <td colspan="8"><strong>Checking current coverage.</strong><small>Please hold while the worklist is prepared.</small></td>
            </tr>
          ` : apiError ? (visibleRows.length ? visibleRows.map(renderFeeScheduleMissingRow).join("") : `
            <tr>
              <td colspan="8"><strong>No missing combinations in the local estimate.</strong><small>${escapeHtml(feeScheduleMissingApiState.error ? `${feeScheduleMissingApiState.error} — ` : "")}Local estimate only; confirm against live coverage data when it reconnects.</small></td>
            </tr>
          `) : visibleRows.length ? visibleRows.map(renderFeeScheduleMissingRow).join("") : `
            <tr>
              <td colspan="8"><strong>${hiddenBook ? "No missing combinations match these filters." : "Fee schedule coverage is clean for the active scope."}</strong><small>${hiddenBook ? escapeHtml(feeScheduleMissingHiddenFilterNote(hiddenBook)) : `No missing setup combinations were found from the ${usingApi ? "current worklist" : "current claim/service rows"}.`}</small></td>
            </tr>
          `}
        </tbody>
      </table>
    </div>
  `;
  const nextSearch = host.querySelector('[data-fee-missing-filter="search"]');
  if (searchCaret && nextSearch) { nextSearch.focus({ preventScroll: true }); nextSearch.setSelectionRange(...searchCaret); }
  if (comboFocus?.field) {
    enhanceFeeSearchSelects(host);
    const nextCombo = host.querySelector(`[data-fee-missing-filter="${comboFocus.field}"]`)?.nextElementSibling?.querySelector("[data-fee-search-input]");
    if (nextCombo) {
      nextCombo.dataset.feeSearchQuietFocus = "true";
      nextCombo.focus({ preventScroll: true });
      if (comboFocus.expanded) {
        nextCombo.value = comboFocus.value;
        nextCombo.dispatchEvent(new Event("input", { bubbles: true }));
        nextCombo.setSelectionRange(comboFocus.start, comboFocus.end);
      }
    }
  }
}

function feeScheduleMissingHiddenFilterBook(sourceRows = [], visibleRows = []) {
  const source = Array.isArray(sourceRows) ? sourceRows : [];
  const visible = Array.isArray(visibleRows) ? visibleRows : [];
  if (!source.length || visible.length) return null;
  let openAr = 0;
  let unknownOpenAr = 0;
  source.forEach((row) => {
    const value = feeScheduleOptionalMoney(row.openAr);
    if (isUnknownMoney(value)) unknownOpenAr += 1;
    else openAr += moneyNumber(value);
  });
  return { combinations: source.length, openAr, unknownOpenAr };
}

function feeScheduleMissingHiddenFilterNote(hidden = {}) {
  const count = Number(hidden.combinations) || 0;
  const countLabel = typeof formatNounCount === "function" ? formatNounCount(count, "combination") : `${count} combinations`;
  if (count > 0 && Number(hidden.unknownOpenAr) >= count) {
    return `Filters are hiding ${countLabel}. Open AR is not yet known — this is not clean coverage.`;
  }
  if (Number(hidden.openAr) > 0) {
    return `Filters are hiding ${money(hidden.openAr)} open AR across ${countLabel}. This is not clean coverage.`;
  }
  return `Filters are hiding ${countLabel} still on the worklist. This is not clean coverage.`;
}

function feeScheduleMissingFilterOptions(rows = []) {
  const orgs = new Map();
  const payers = new Set();
  const procedures = new Set();
  const states = new Set();
  const networks = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    states.add(String(row.state || "") || "__unavailable__");
    networks.add(String(row.network || "") || "__unavailable__");
    if (row.orgId) orgs.set(String(row.orgId), feeScheduleMissingOrgLabelSeed(row));
    if (row.payer) payers.add(String(row.payer));
    if (row.procedure) procedures.add(String(row.procedure));
  });
  return {
    orgs: feeScheduleMissingOrgOptions(orgs),
    payers: Array.from(payers).sort(compareText).map((value) => ({ value, label: value })),
    procedures: Array.from(procedures).sort(compareText).map((value) => ({ value, label: value })),
    states: Array.from(states).sort(compareText).map((value) => ({ value, label: value === "__unavailable__" ? "State unavailable" : value })),
    networks: Array.from(networks).sort(compareText).map((value) => ({ value, label: value === "__unavailable__" ? "Not specified" : value }))
  };
}

function feeScheduleMissingOrgLabelSeed(row = {}) {
  const orgId = String(row.orgId || "").trim();
  const name = [row.orgName, row.tenantId, tenantNameForOrgId(orgId)]
    .map((value) => String(value || "").trim())
    .find((value) => value && value !== orgId);
  return name || "Company name unavailable";
}

// Friendly company labels, never the raw org key. Two locations sharing a name
// are told apart the way Add Rate already does it: "Name · Location 2".
function feeScheduleMissingOrgOptions(orgs = new Map()) {
  const rows = Array.from(orgs, ([value, label]) => ({ value: String(value), label: String(label) }))
    .sort((a, b) => compareText(a.label, b.label) || compareText(a.value, b.value));
  const counts = new Map();
  rows.forEach((row) => counts.set(row.label.toLowerCase(), (counts.get(row.label.toLowerCase()) || 0) + 1));
  const ordinals = new Map();
  return rows.map((row) => {
    const key = row.label.toLowerCase();
    if ((counts.get(key) || 0) <= 1) return row;
    const ordinal = (ordinals.get(key) || 0) + 1;
    ordinals.set(key, ordinal);
    return { value: row.value, label: row.label + " · Location " + ordinal };
  });
}

function feeScheduleMissingOpenArDetail(totals = {}) {
  const credits = Number(totals.creditCombinations || 0);
  if (!credits) return "Open AR on claims carrying these missing combinations";
  return "Owed across " + formatNounCount(Number(totals.collectibleCombinations || 0), "combination") + "; " +
    formatNounCount(credits, "combination") + " carry credit balances of " + money(totals.creditAr) + " (net " + money(totals.openAr) + ")";
}

function feeScheduleMissingOptionHtml(options = [], selected = "All", allLabel = "All") {
  const selectedText = String(selected || "All");
  const hasSelected = selectedText === "All" || options.some((option) => String(option.value) === selectedText);
  const rows = [
    { value: "All", label: allLabel },
    ...(!hasSelected ? [{ value: selectedText, label: selectedText }] : []),
    ...options
  ];
  return rows.map((option) => `<option value="${escapeHtml(option.value)}" ${String(option.value) === selectedText ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("");
}

function feeScheduleMissingSortedRows(rows = []) {
  const sort = feeScheduleMissingEffectiveSort(rows);
  const copy = (Array.isArray(rows) ? rows : []).slice();
  return copy.sort((a, b) => {
    if (sort === "services") return Number(b.services || 0) - Number(a.services || 0) || Number(b.openAr || 0) - Number(a.openAr || 0);
    if (sort === "preparedGap") return Number(b.preparedFallbackExpected || 0) - Number(a.preparedFallbackExpected || 0) || Number(b.openAr || 0) - Number(a.openAr || 0);
    if (sort === "expected") return Number(b.expected || 0) - Number(a.expected || 0) || Number(b.openAr || 0) - Number(a.openAr || 0);
    if (sort === "client") return compareText(a.orgName || a.tenantId || a.orgId, b.orgName || b.tenantId || b.orgId) || compareText(a.payer, b.payer);
    if (sort === "cpt") return compareText(a.procedure, b.procedure) || Number(b.openAr || 0) - Number(a.openAr || 0);
    return Number(b.openAr || 0) - Number(a.openAr || 0) || Number(b.services || 0) - Number(a.services || 0) || compareText(a.payer, b.payer);
  });
}

// The default sort key is the fallback expected the warehouse does not serve
// yet (null on all 431 TEST rows). The comparator already fell through to open
// AR; the Sort control still claimed otherwise. Say what orders the list.
function feeScheduleMissingEffectiveSort(rows = []) {
  const sort = state.feeScheduleMissingSort || "preparedGap";
  if (sort !== "preparedGap") return sort;
  const anyKnown = (Array.isArray(rows) ? rows : []).some((row) => !isUnknownMoney(feeScheduleOptionalMoney(row.preparedFallbackExpected)));
  return anyKnown ? sort : "openAr";
}

function feeScheduleMissingPageWindow(rows = [], requestedPage = feeScheduleMissingPage) {
  if (feeScheduleApiEnabled() && feeScheduleMissingApiState.status === "ok" && feeScheduleMissingApiState.page) { const meta = feeScheduleMissingApiState.page, page = Number(meta.number) || 1, totalRows = Number(meta.totalRows) || 0; return { page, totalPages: Number(meta.totalPages) || 1, totalRows, from: rows.length ? (page-1)*50+1 : 0, to: rows.length ? (page-1)*50+rows.length : 0, rows }; }
  const sortedRows = Array.isArray(rows) ? rows : [];
  const totalRows = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / FEE_SCHEDULE_MISSING_PAGE_SIZE));
  const page = Math.min(totalPages, Math.max(1, Math.trunc(Number(requestedPage) || 1)));
  const startIndex = (page - 1) * FEE_SCHEDULE_MISSING_PAGE_SIZE;
  feeScheduleMissingPage = page;
  return {
    page,
    totalPages,
    totalRows,
    from: totalRows ? startIndex + 1 : 0,
    to: Math.min(totalRows, startIndex + FEE_SCHEDULE_MISSING_PAGE_SIZE),
    rows: sortedRows.slice(startIndex, startIndex + FEE_SCHEDULE_MISSING_PAGE_SIZE)
  };
}

function feeScheduleMissingPanelStatusText({ usingApi = false, apiPending = false, apiError = false, rows = [] } = {}) {
  if (apiPending) return "Please hold while current coverage is checked.";
  if (apiError) return "Local estimate may miss some groups; reload to check live coverage data.";
  if (usingApi) {
    const sourceLabel = feeScheduleMissingApiState.source === "warehouse-canonical-fee-resolution"
      ? "Current worklist"
      : feeScheduleMissingApiState.source === "mirror-compatibility"
        ? "Legacy mirror compatibility worklist"
        : "Current worklist";
    // A CUT LIST MUST NOT READ AS A COMPLETE ONE. Without this the panel says
    // "showing 500 matching combinations" whether 500 is all of them or the
    // first 500 of several thousand -- and the reader works the list to the
    // bottom and concludes the fee schedules are complete. The server sends
    // `truncated` for exactly this sentence.
    const capped = Boolean(feeScheduleMissingApiState.truncated);
    const capLabel = Number(feeScheduleMissingApiState.rowLimit || 0);
    const capNote = capped
      ? ` Row cap${capLabel ? ` of ${capLabel.toLocaleString()}` : ""} reached - there are more missing combinations than are shown here. Narrow the client, payer or CPT filter to see the rest.`
      : "";
    return feeScheduleMissingApiState.generatedAt
      ? `${sourceLabel} refreshed ${feeScheduleMissingApiState.generatedAt}; showing ${capped ? "the first " : ""}${Number(rows.length || 0).toLocaleString()} matching combination${rows.length === 1 ? "" : "s"}.${capNote}`
      : `${sourceLabel}.${capNote}`;
  }
  return "Local estimate only; connect live coverage data for the full worklist.";
}

function feeScheduleMissingRows(claims = [], feeRows = []) {
  const groups = new Map();
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    const orgId = String(firstPresent(claim.orgId, claim.org_id) || "").trim();
    const tenantId = tenantNameForOrgId(orgId) || String(firstPresent(claim.client, claim.tenant, claim.tenantId) || "").trim();
    const claimOpenAr = moneyNumber(claimFinancialView(claim).sourceOutstanding);
    (Array.isArray(claim.services) ? claim.services : []).forEach((service) => {
      ["primary", "secondary"].forEach((payerRole) => {
        const payer = payerRole === "secondary"
          ? String(firstPresent(service.secondaryPayer, claim.secondaryPayer) || "").trim()
          : String(firstPresent(service.primaryPayer, claim.primaryPayer) || "").trim();
        if (!payer || /^none$/i.test(payer)) return;
        const serviceForMatch = {
          ...service,
          place: firstPresent(service.place, claim.place),
          state: firstPresent(service.state, claim.state, clientStateValue(claim)),
          orgId,
          payerId: payerExternalIdForClaimPayer(claim, payer),
          network: firstPresent(service.network, claim.network)
        };
        const match = bestFeeScheduleMatch(payer, serviceForMatch, feeRows);
        if (match && moneyNumber(match.rate || 0) > 0) return;
        const financial = serviceFinancialView(service, claim);
        const units = quantityNumber(service.units);
        const expected = moneyNumber(financial.expectedReimbursement);
        const openAr = moneyNumber(financial.sourceOutstanding || claimOpenAr);
        const row = feeScheduleMissingGroupSeed({ claim, service: serviceForMatch, payer, payerRole, tenantId, orgId });
        const key = feeScheduleMissingKey(row);
        const group = groups.get(key) || row;
        group.services += 1;
        group.claimIds.add(String(claim.claimId || ""));
        group.units += units;
        group.expected += expected;
        group.openAr += openAr;
        groups.set(key, group);
      });
    });
  });
  return Array.from(groups.values())
    .map((row) => ({
      ...row,
      claims: Array.from(row.claimIds).filter(Boolean).length,
      units: Number(row.units.toFixed(2)),
      expected: moneyNumber(row.expected),
      openAr: moneyNumber(row.openAr)
    }))
    .filter(feeScheduleMissingRowMatchesFilters)
    .sort((a, b) => b.openAr - a.openAr || b.services - a.services || compareText(a.payer, b.payer));
}

function feeScheduleMissingGroupSeed({ claim = {}, service = {}, payer = "", payerRole = "", tenantId = "", orgId = "" } = {}) {
  return {
    tenantId,
    orgId,
    orgName: tenantNameForOrgId(orgId) || tenantId,
    payer,
    payerRole,
    payerId: payerExternalIdForClaimPayer(claim, payer),
    state: String(firstPresent(service.state, claim.state, clientStateValue(claim)) || "").trim(),
    service: String(firstPresent(service.service, service.serviceType, service.category) || "").trim(),
    procedure: String(firstPresent(service.procedure, service.procedureCode, service.code) || "").trim(),
    m1: String(firstPresent(service.m1, service.modifier1) || "").trim(),
    m2: String(firstPresent(service.m2, service.modifier2) || "").trim(),
    place: String(firstPresent(service.place, claim.place) || "").trim(),
    network: String(firstPresent(service.network, claim.network) || "").trim(),
    services: 0,
    claimIds: new Set(),
    units: 0,
    expected: 0,
    openAr: 0
  };
}

function feeScheduleMissingKey(row = {}) {
  return [
    row.tenantId,
    row.orgId,
    row.payerRole,
    row.payerId,
    row.payer,
    row.state,
    row.service,
    row.procedure,
    row.m1,
    row.m2,
    row.place,
    row.network
  ].map((value) => normalizedContractText(value)).join("|");
}

function feeScheduleMissingRowMatchesFilters(row = {}) {
  if (!feeScheduleRowInSelectedCompany(row)) return false;
  const filters = feeScheduleEffectiveFilters();
  const search = normalizedFeeScheduleSearch(filters.search);
  if (filters.tenantId && filters.tenantId !== "All" && !feeScheduleRowMatchesTenant(row, filters.tenantId)) return false;
  if (filters.orgId && filters.orgId !== "All" && String(row.orgId || "") !== String(filters.orgId || "")) return false;
  if (filters.payer && filters.payer !== "All" && String(row.payer || "") !== String(filters.payer || "")) return false;
  if (filters.state && filters.state !== "All" && String(row.state || "__unavailable__") !== String(filters.state || "")) return false;
  if (filters.procedure && filters.procedure !== "All" && String(row.procedure || "") !== String(filters.procedure || "")) return false;
  if (filters.network && filters.network !== "All" && String(row.network || "__unavailable__") !== String(filters.network || "")) return false;
  // Only the rollback source needs a client-side parent-mapping decision
  // filter. Warehouse rows already passed the complete mapping census and the
  // canonical resolver; a returned mapped row means no applicable parent rate.
  const matchedFilter = String(filters.matched || "open");
  if (feeScheduleMissingApiState.source === "mirror-compatibility" && matchedFilter !== "All") {
    const hasParent = Boolean(feeParentMappingForMissingRow(row));
    if (matchedFilter === "matched" && !hasParent) return false;
    if (matchedFilter === "open" && hasParent) return false;
  }
  if (!feeScheduleSearchMatches(normalizedFeeScheduleSearch([
    row.tenantId, row.orgId, row.orgName, row.payer, row.payerId, row.state,
    row.service, row.procedure, row.m1, row.m2, row.place, row.serviceDate, row.serviceStartDate, row.serviceEndDate, row.network
  ].filter(Boolean).join(" ")), search)) return false;
  return true;
}

function renderFeeScheduleMissingRow(row = {}) {
  const payload = encodeFeeScheduleMissingPayload(row);
  const dateEvidence = feeScheduleMissingDateEvidence(row);
  const matched = feeParentMappingForMissingRow(row);
  const preparedFallbackExpected = feeScheduleOptionalMoney(row.preparedFallbackExpected);
  const expected = feeScheduleOptionalMoney(row.expected);
  const openAr = feeScheduleOptionalMoney(row.openAr);
  const canManage = hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES);
  return `
    <tr>
      <td>
        <strong>${escapeHtml(row.payer)}</strong>
        <small>${escapeHtml(row.payerRole)}${row.payerId ? ` | external ${escapeHtml(row.payerId)}` : ""}</small>
        <small>${escapeHtml(row.procedure || "No CPT")} | ${escapeHtml([row.m1, row.m2].filter(Boolean).join(" / ") || "no modifiers")} | POS ${escapeHtml(row.place || "Any")} | ${escapeHtml(row.networkKnown === false ? "Network unavailable" : (row.network || "Any network"))}</small>
        <small>${escapeHtml([row.state ? `Patient state / classification: ${row.state}` : "Patient state unavailable", row.service || "Service blank", dateEvidence].filter(Boolean).join(" | "))}</small>
      </td>
      <td><strong>${escapeHtml(feeScheduleMissingOrgLabels.get(String(row.orgId || "")) || clientActionCompanyName(row.orgName, row.orgId) || clientActionCompanyName(row.tenantId, row.orgId) || portalCompanyLabel(row.orgId))}</strong></td>
      <td>${Number(row.services || 0).toLocaleString()}<small>${formatNounCount(Number(row.claims || 0), "grouped claim")}</small></td>
      <td>${row.units == null ? MONEY_UNKNOWN_TEXT : Number(row.units).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
      <td>${money(preparedFallbackExpected)}<small>${isUnknownMoney(preparedFallbackExpected) ? "Unknown from source" : row.preparedFallbackServices == null ? "Fallback service count unknown from source" : `${formatNounCount(Number(row.preparedFallbackServices || 0), "fallback service")} currently using fallback expected math`}</small></td>
      <td>${money(expected)}</td>
      <td>${money(openAr)}</td>
      <td class="fee-missing-action-cell">
        ${canManage ? `<button class="small-button fee-missing-add-btn" type="button" data-fee-missing-add="${escapeHtml(payload)}">Add rate</button>` : ""}
        ${matched
          ? `<span class="fee-missing-matched-note" title="An active parent mapping exists, but rate coverage still needs review for this exact service combination.">Parent: ${escapeHtml(matched.parentPayer)}</span>`
          : canManage ? `<button class="small-button fee-missing-parent-btn" type="button" data-fee-missing-parent="${escapeHtml(payload)}">Match to parent</button>` : "<span>View only</span>"}
        ${renderOonNetworkLabelCell(row, payload, canManage)}
      </td>
    </tr>
  `;
}

function encodeFeeScheduleMissingPayload(row = {}) {
  return encodeURIComponent(JSON.stringify({
    tenantId: row.tenantId || "",
    orgId: row.orgId || "",
    orgName: row.orgName || "",
    payer: row.payer || "",
    // payerRole/payerId travel so the decoded payload rebuilds the SAME
    // feeScheduleMissingKey as the row it came from — that lookup is how the
    // match panel recovers units and service counts the payload does not carry.
    payerRole: row.payerRole || "",
    payerId: row.payerId || row.externalPayerId || "",
    externalPayerId: row.payerId || row.externalPayerId || "",
    state: row.state || "",
    service: row.service || "",
    procedure: row.procedure || "",
    m1: row.m1 || "",
    m2: row.m2 || "",
    place: row.place || "",
    serviceDate: row.serviceDate || "",
    serviceStartDate: row.serviceStartDate || "",
    serviceEndDate: row.serviceEndDate || "",
    network: row.network || "",
    networkKnown: row.networkKnown !== false
  }));
}

function feeScheduleMissingDateEvidence(row = {}) {
  const single = String(row.serviceDate || "").trim();
  const start = String(row.serviceStartDate || "").trim();
  const end = String(row.serviceEndDate || "").trim();
  if (single) return `DOS ${single}`;
  if (start && end && start !== end) return `DOS ${start} to ${end}`;
  if (start || end) return `DOS ${start || end}`;
  return "";
}

function openMissingFeeRate(encoded = "") {
  let payload = {};
  try {
    payload = JSON.parse(decodeURIComponent(String(encoded || "")));
  } catch (_) {
    payload = {};
  }
  openAddFeeRateModal({
    tenantId: payload.tenantId || "",
    orgId: payload.orgId || "",
    orgName: payload.orgName || "",
    payer: payload.payer || "",
    externalPayerId: payload.externalPayerId || "",
    state: payload.state || "",
    service: payload.service || "",
    procedure: payload.procedure || "",
    m1: payload.m1 || "",
    m2: payload.m2 || "",
    place: payload.place || "",
    network: payload.network || "",
    networkKnown: payload.networkKnown !== false,
    startDate: payload.serviceStartDate || payload.serviceDate || "",
    approvalStatus: "draft"
  });
  showToast("Missing fee schedule combination loaded into Add Rate.");
}

// ---------------------------------------------------------------------------
// MISSING RATES — the work queue, and "match to parent"
// ---------------------------------------------------------------------------
// The owner asked for this twice. The engine (feeScheduleParentPayers.js), the
// table (portal.fee_schedule_parent_payers), both read lanes, the verifiers AND
// the four API routes all existed before this file changed; what did not exist
// was any way for a human to reach them, which is why nobody had ever created
// a mapping. Everything below is surface.
//
// A PARENT MAPPING IS NOT A HIERARCHY. It is a curated, effective-dated
// decision that says "this payer has no schedule of its own; price it off that
// one", consulted ONLY after the payer's own lookup has already missed. A
// direct hit always wins, so a mapping can never change a line that already
// had a rate — which is why the panel refuses to save when the live preview
// says the pricing would not move.

let feeParentMappingApiState = { key: "", status: "idle", rows: [], error: "", loadedAt: "" };
// Keyed like feeScheduleMissingApiState so a slow preview for an abandoned
// pick can never paint over the answer for the current one.
let feeParentPreviewState = { key: "", status: "idle", body: null, error: "" };
let feeParentMatchDraft = null;
// Loaded independently of state.feeScheduleRows. That cache is fetched with
// feeScheduleApiQuery(), which carries the missing-rate worklist's payer
// filter — the payer that has NO rate. Asking it for parents can only ever
// return an empty list. This census is org + CPT + approved + active, the
// same pool handleFeeScheduleParentPayerPreview prices from.
let feeParentCandidateState = { key: "", status: "idle", rows: [], error: "" };

function renderFeeScheduleSurfaces() {
  if (state.view === "missingRates") {
    renderMissingRates();
    return;
  }
  renderFeeSchedules();
}

function missingRatesActiveTab() {
  return String(state.missingRatesTab || "worklist") === "parents" ? "parents" : "worklist";
}

function renderMissingRates() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("missingRates")) return;
  if (!els.feeScheduleMissingPanel) return;
  const tab = missingRatesActiveTab();
  if (tab === "parents") requestFeeScheduleParentMappings();
  else { requestFeeScheduleMissingApiRows(); requestOonNetworkLabels(); }
  Array.from(document.querySelectorAll("[data-missing-rates-tab]")).forEach((button) => {
    const active = button.dataset.missingRatesTab === tab;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", active ? "true" : "false");
  });
  if (els.missingRatesWorklistPane) els.missingRatesWorklistPane.hidden = tab !== "worklist";
  if (els.missingRatesParentsPane) els.missingRatesParentsPane.hidden = tab !== "parents";
  if (tab === "parents") {
    renderFeeParentMappingsPanel();
    return;
  }
  if (feeScheduleMissingAuthoritativeReady()) renderFeeParentMatchPanel();
  else if (els.feeParentMatchPanel) els.feeParentMatchPanel.innerHTML = "";
  renderFeeScheduleMissingPanel(getClaims(), filteredFeeScheduleRows(feeRowsForActiveUser(state.feeScheduleRows || [])));
  wireFeeScheduleMissingHandlers();
}

function wireFeeScheduleMissingHandlers() {
  enhanceFeeSearchSelects(els.feeScheduleMissingPanel);
  document.querySelectorAll("[data-fee-missing-clear]").forEach((button) => button.addEventListener("click", () => {
    const field = button.dataset.feeMissingClear;
    if (field !== "all") return updateFeeScheduleMissingFilter(field, ["search", "activeDate"].includes(field) ? "" : "All");
    state.feeScheduleFilters = { ...(state.feeScheduleFilters || {}), search: "", orgId: "All", tenantId: "All", payer: "All", procedure: "All", state: "All", network: "All", activeDate: "", matched: "open" };
    state.feeScheduleFilters = feeScheduleEffectiveFilters();
    feeScheduleMissingPage = 1;
    saveState();
    renderFeeMissingFilterChange();
  }));
  Array.from(document.querySelectorAll("[data-fee-missing-retry]")).forEach((button) => {
    button.addEventListener("click", () => requestFeeScheduleMissingApiRows({ force: true }));
  });
  Array.from(document.querySelectorAll("[data-fee-missing-add]")).forEach((button) => {
    button.addEventListener("click", () => openMissingFeeRate(button.dataset.feeMissingAdd || ""));
  });
  Array.from(document.querySelectorAll("[data-fee-missing-parent]")).forEach((button) => {
    button.addEventListener("click", () => openFeeParentMatch(button.dataset.feeMissingParent || ""));
  });
  Array.from(document.querySelectorAll("[data-oon-label-mark]")).forEach((button) => {
    button.addEventListener("click", () => markMissingRowOutOfNetwork(button.dataset.oonLabelMark || ""));
  });
  Array.from(document.querySelectorAll("[data-oon-label-remove]")).forEach((button) => {
    button.addEventListener("click", () => removeOonNetworkLabel(button.dataset.oonLabelRemove || ""));
  });
  Array.from(document.querySelectorAll("[data-fee-missing-filter]")).forEach((input) => {
    input.addEventListener("change", () => updateFeeScheduleMissingFilter(input.dataset.feeMissingFilter, input.value));
    if (input.dataset.feeMissingFilter === "search") {
      input.addEventListener("input", () => updateFeeScheduleMissingFilter(input.dataset.feeMissingFilter, input.value, { deferApi: true }));
    }
  });
  document.querySelector("[data-fee-missing-sort]")?.addEventListener("change", (event) => {
    state.feeScheduleMissingSort = event.target.value || "openAr";
    feeScheduleMissingPage = 1;
    saveState();
    renderFeeMissingFilterChange();
  });
  Array.from(document.querySelectorAll("[data-fee-missing-page]")).forEach((button) => {
    button.addEventListener("click", () => {
      const direction = button.dataset.feeMissingPage === "previous" ? -1 : 1;
      feeScheduleMissingPage = Math.max(1, feeScheduleMissingPage + direction);
      if (feeScheduleApiEnabled()) requestFeeScheduleMissingApiRows();
      renderFeeScheduleMissingPanel(getClaims(), filteredFeeScheduleRows(feeRowsForActiveUser(state.feeScheduleRows || [])));
      wireFeeScheduleMissingHandlers();
      window.requestAnimationFrame(() => {
        const sameDirection = document.querySelector(`[data-fee-missing-page="${direction < 0 ? "previous" : "next"}"]:not(:disabled)`);
        const fallback = document.querySelector("[data-fee-missing-page]:not(:disabled)");
        const target = sameDirection || fallback;
        if (!target) return;
        try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); }
      });
    });
  });
}

// --- mapping list ----------------------------------------------------------

function feeScheduleParentMappingQuery() {
  const filters = feeScheduleEffectiveFilters();
  const headerTenant = typeof activeClientGroupTenant === "function" ? activeClientGroupTenant() : "";
  const headerOrg = typeof activeClientGroupServerOrgId === "function" ? activeClientGroupServerOrgId() : "";
  const tenantSelected = filters.tenantId && filters.tenantId !== "All" ? filters.tenantId : (headerTenant || "");
  const tenantOrgIds = tenantSelected ? orgIdsForTenantName(tenantSelected) : [];
  const orgSelected = filters.orgId && filters.orgId !== "All"
    ? filters.orgId
    : (tenantOrgIds.length === 1 ? tenantOrgIds[0] : (headerOrg || ""));
  // Deliberately NOT feeScheduleApiQuery(): that one carries status=active|
  // archived for RATE rows, and this route reads `status` as the mapping
  // lifecycle (active vs withdrawn). Sharing it would have made "Show
  // archived rates" silently hide every live mapping.
  return new URLSearchParams(Object.entries({
    orgId: orgSelected,
    setupFilters: JSON.stringify({ orgIds: headerTenant ? activeClientGroupOrgIds() : tenantOrgIds }),
    status: state.missingRatesShowWithdrawn ? "All" : "active",
    search: feeParentMappingSearch
  }).filter(([, value]) => value !== "")).toString();
}

let feeParentMappingSearch = "";
let feeParentMappingRequestSequence = 0;

function feeParentMappingRequestKey(query = feeScheduleParentMappingQuery(), includeFreshness = true) {
  return JSON.stringify([includeFreshness ? additionalPortalDataReadScope() : null, workflowApiBase(), activeInternalUser(), state.role, workflowApiToken() || trackerApiToken(), trackerAccessEnvelope("service"), query, feeSetupRequestedPage("parent-mappings", query)]);
}

function feeParentMappingStateCurrent() {
  return hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES) && feeParentMappingApiState.key === feeParentMappingRequestKey();
}

function requestFeeScheduleParentMappings(options = {}) {
  if (!feeScheduleApiEnabled() || !hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES)) return;
  const baseQuery = feeScheduleParentMappingQuery();
  const key = feeParentMappingRequestKey(baseQuery);
  const params = new URLSearchParams(baseQuery); params.set("page", feeSetupRequestedPage("parent-mappings", baseQuery));
  const query = params.toString();
  if (!options.force && feeParentMappingApiState.key === key && ["pending", "ok", "error"].includes(feeParentMappingApiState.status)) return;
  const requestId = ++feeParentMappingRequestSequence;
  feeParentMappingApiState = { key, status: "pending", rows: [], error: "", loadedAt: "" };
  const token = workflowApiToken() || trackerApiToken();
  fetch(additionalPortalDataReadUrl(`${workflowApiBase()}/api/setup/fee-schedule-parent-payers${query ? `?${query}` : ""}`), {
    method: "GET",
    cache: "no-store",
    headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (feeParentMappingApiState.key !== key || requestId !== feeParentMappingRequestSequence || !feeParentMappingStateCurrent()) return;
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") {
          notePortalSessionExpired();
          feeParentMappingApiState = {
            key,
            status: "error",
            rows: feeParentMappingApiState.rows,
            error: "Your session expired. Sign in again — this page will still be here.",
            loadedAt: feeParentMappingApiState.loadedAt || ""
          };
        } else {
          feeParentMappingApiState = { key, status: "error", rows: [], error: body.message || body.error || `HTTP ${response.status}`, loadedAt: feeParentMappingApiState.loadedAt || "" };
        }
        renderFeeScheduleSurfaces();
        return;
      }
      if (body.complete !== true || body.truncated !== false || !Array.isArray(body.rows) || body.count !== body.rows.length) {
        throw new Error("The complete parent mapping list could not be verified. Retry before using these mappings.");
      }
      feeParentMappingApiState = {
        key,
        complete: true,
        page: body.page, summary: body.summary, revision: body.revision,
        status: "ok",
        rows: Array.isArray(body.rows) ? body.rows : [],
        error: "",
        loadedAt: timestamp()
      };
      renderFeeScheduleSurfaces();
    })
    .catch((error) => {
      if (feeParentMappingApiState.key !== key || requestId !== feeParentMappingRequestSequence || !feeParentMappingStateCurrent()) return;
      feeParentMappingApiState = { key, status: "error", rows: [], error: error.message, loadedAt: feeParentMappingApiState.loadedAt || "" };
      renderFeeScheduleSurfaces();
    });
}

// Rates are PER UNIT and the difference between two candidate rows is often
// cents. money() rounds to whole dollars, which is right for AR totals and
// wrong here: it renders $15.75 and $13.61 as "$16" and "$14", hiding the
// distinction the screen exists to show.
function feeParentRateMoney(value) {
  return formatMoneyValue(value, { decimals: 2 });
}

function feeParentText(value) {
  return String(value == null ? "" : value).trim().toLowerCase();
}

// A mapping covers a worklist row when the org and the source payer agree and
// the mapping is either CPT-wide or names this CPT. Modifiers and POS are NOT
// compared: we never write them (see submitFeeParentMapping), matching the
// legacy tracker, which strips them so a human cannot narrow a parent link by
// a modifier the parent schedule is going to decide anyway.
function feeParentMappingForMissingRow(row = {}) {
  if (feeScheduleApiEnabled() && feeScheduleMissingApiState.page) return row.parentMapping || null;
  if (feeParentMappingApiState.status !== "ok" || !feeParentMappingStateCurrent()) return null;
  return (feeParentMappingApiState.rows || []).find((mapping) => {
    if (feeParentText(mapping.status) !== "active") return false;
    if (feeParentText(mapping.orgId) !== feeParentText(row.orgId)) return false;
    if (feeParentText(mapping.sourcePayer) !== feeParentText(row.payer)) return false;
    const procedure = feeParentText(mapping.procedureCode);
    return !procedure || procedure === feeParentText(row.procedure);
  }) || null;
}

// OUT-OF-NETWORK LABELS (backend/oonNetworkLabels.js, Nathan 2026-10-16). One
// click on a missing-rate row says "this insurance is out of network in this
// state" for that client, with no rate. The next hourly refresh classifies its
// unmatched lines as OON, and OON learned rates learn every CPT from payments.
let oonNetworkLabelState = { key: "", status: "idle", rows: [], error: "" };
let oonNetworkLabelRequestSequence = 0;
const oonNetworkLabelWritesInFlight = new Set();

function oonNetworkLabelRequestKey() {
  return JSON.stringify([workflowApiBase(), activeInternalUser(), state.role, workflowApiToken() || trackerApiToken()]);
}

function oonNetworkLabelText(value) {
  return String(value == null ? "" : value).trim().toUpperCase().replace(/\s+/g, " ");
}

function requestOonNetworkLabels(options = {}) {
  if (!feeScheduleApiEnabled() || !hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES)) return;
  const key = oonNetworkLabelRequestKey();
  if (!options.force && oonNetworkLabelState.key === key && ["pending", "ok", "error"].includes(oonNetworkLabelState.status)) return;
  const requestId = ++oonNetworkLabelRequestSequence;
  oonNetworkLabelState = { key, status: "pending", rows: oonNetworkLabelState.key === key ? oonNetworkLabelState.rows : [], error: "" };
  const token = workflowApiToken() || trackerApiToken();
  fetch(`${workflowApiBase()}/api/setup/oon-network-labels`, {
    method: "GET",
    cache: "no-store",
    headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (requestId !== oonNetworkLabelRequestSequence || oonNetworkLabelState.key !== key) return;
      oonNetworkLabelState = !response.ok || body.error || !Array.isArray(body.rows)
        ? { key, status: "error", rows: [], error: body.message || body.error || `HTTP ${response.status}` }
        : { key, status: "ok", rows: body.rows, error: "" };
      if (missingRatesActiveTab() === "worklist") renderMissingRates();
    })
    .catch((error) => {
      if (requestId !== oonNetworkLabelRequestSequence || oonNetworkLabelState.key !== key) return;
      oonNetworkLabelState = { key, status: "error", rows: [], error: error.message };
    });
}

// Primary payer only: the label sets the primary contract's network, which is
// what OON learned rates learn from.
function oonNetworkLabelForMissingRow(row = {}) {
  if (oonNetworkLabelState.status !== "ok" || oonNetworkLabelState.key !== oonNetworkLabelRequestKey()) return null;
  if (String(row.payerRole || "").toLowerCase() === "secondary") return null;
  return (oonNetworkLabelState.rows || []).find((label) => String(label.status || "") === "active"
    && String(label.orgId || "") === String(row.orgId || "")
    && oonNetworkLabelText(label.payer) === oonNetworkLabelText(row.payer)
    && oonNetworkLabelText(label.state) === oonNetworkLabelText(row.state)) || null;
}

function renderOonNetworkLabelCell(row = {}, payload = "", canManage = false) {
  if (String(row.payerRole || "").toLowerCase() === "secondary") return "";
  const label = oonNetworkLabelForMissingRow(row);
  if (label) {
    return `<span class="fee-missing-matched-note" title="Labelled out of network${label.decisionReason ? `: ${escapeHtml(label.decisionReason)}` : ""}. Lines learn their rate from payments: Learning n/5, then Learned $X.">Out of network · learning</span>${canManage
      ? `<button class="small-button" type="button" data-oon-label-remove="${escapeHtml(label.oonNetworkLabelId)}" ${oonNetworkLabelWritesInFlight.has(label.oonNetworkLabelId) ? "disabled" : ""}>Remove label</button>`
      : ""}`;
  }
  if (!canManage) return "";
  if (!String(row.state || "").trim()) {
    return `<button class="small-button" type="button" disabled title="The patient state is unavailable for this row, and an out-of-network label is per state.">Mark out of network</button>`;
  }
  return `<button class="small-button" type="button" data-oon-label-mark="${escapeHtml(payload)}" title="Mark this insurance out of network in ${escapeHtml(row.state)} for this client. No rate needed: every CPT learns its rate from payments.">Mark out of network</button>`;
}

function markMissingRowOutOfNetwork(encoded = "") {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "mark an insurance out of network");
  let row = {};
  try { row = JSON.parse(decodeURIComponent(String(encoded || ""))); } catch (_) { row = {}; }
  const company = feeScheduleMissingOrgLabels.get(String(row.orgId || "")) || clientActionCompanyName(row.orgName, row.orgId) || portalCompanyLabel(row.orgId);
  if (!row.orgId || !row.payer || !row.state) return showToast("This row is missing its client, insurance or patient state.");
  const reason = typeof window !== "undefined" && typeof window.prompt === "function"
    ? window.prompt(`Mark ${row.payer} out of network in ${row.state} for ${company}?\n\nNo rate is needed. Every CPT for this insurance and state learns its rate from payments (5 paid dates of service per patient). Expected stays blank until then.\n\nWhy is it out of network? The reason is kept on record.`, "")
    : "";
  if (reason == null) return;
  if (!String(reason).trim()) return showToast("Add a reason to mark this insurance out of network.");
  const writeKey = `${row.orgId}|${row.payer}|${row.state}`;
  if (oonNetworkLabelWritesInFlight.has(writeKey)) return;
  oonNetworkLabelWritesInFlight.add(writeKey);
  const token = workflowApiToken() || trackerApiToken();
  fetch(`${workflowApiBase()}/api/setup/oon-network-labels`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ tenantId: row.tenantId, orgId: row.orgId, payer: row.payer, state: row.state, decisionReason: String(reason).trim() })
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      if (!response.ok || body.error || body.persisted !== true) throw new Error(body.message || body.error || `HTTP ${response.status}`);
      showToast(`${row.payer} is out of network in ${row.state} for ${company}. Its lines start learning after the next hourly refresh.`);
      requestOonNetworkLabels({ force: true });
    })
    .catch((error) => showToast(`Could not mark out of network: ${error.message}`))
    .finally(() => oonNetworkLabelWritesInFlight.delete(writeKey));
}

function removeOonNetworkLabel(labelId = "") {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "remove an out-of-network label");
  const label = (oonNetworkLabelState.rows || []).find((candidate) => String(candidate.oonNetworkLabelId || "") === String(labelId));
  if (!label || oonNetworkLabelWritesInFlight.has(label.oonNetworkLabelId)) return;
  if (typeof window !== "undefined" && typeof window.confirm === "function"
    && !window.confirm(`Remove the out-of-network label for ${label.payer} in ${label.state}? Its lines go back to Missing Fee Schedule and stop learning after the next hourly refresh.`)) return;
  oonNetworkLabelWritesInFlight.add(label.oonNetworkLabelId);
  const token = workflowApiToken() || trackerApiToken();
  fetch(`${workflowApiBase()}/api/setup/oon-network-labels/retire`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ tenantId: label.tenantId, orgId: label.orgId, oonNetworkLabelId: label.oonNetworkLabelId, reason: "removed from the portal missing-rate worklist" })
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      if (!response.ok || body.error || body.persisted !== true || body.row?.status !== "inactive") throw new Error(body.message || body.error || `HTTP ${response.status}`);
      showToast(`${label.payer} in ${label.state} is no longer labelled out of network.`);
      requestOonNetworkLabels({ force: true });
    })
    .catch((error) => showToast(`Could not remove the label: ${error.message}`))
    .finally(() => oonNetworkLabelWritesInFlight.delete(label.oonNetworkLabelId));
}

function renderFeeParentMappingsPanel() {
  const host = els.feeParentMappingsPanel;
  if (!host) return;
  requestFeeScheduleParentMappings();
  const current = feeParentMappingStateCurrent();
  const rows = current && feeParentMappingApiState.status === "ok" ? (feeParentMappingApiState.rows || []).filter(feeScheduleRowInSelectedCompany) : [];
  const query = feeParentText(feeParentMappingSearch);
  const filtered = feeScheduleApiEnabled() ? rows : rows.filter((row) => !query || [row.orgId, row.sourcePayer, row.parentPayer, row.sourceState, row.parentState, row.procedureCode, row.decisionReason, row.createdByUserId, row.updatedByUserId, row.status].some((value) => feeParentText(value).includes(query)));
  const paging = feeScheduleApiEnabled() ? feeSetupPageWindow("parent-mappings", rows, feeParentMappingApiState.page) : managementListPageWindow("parent-mappings", filtered, JSON.stringify([feeParentMappingRequestKey(undefined, false), query]));
  const status = current ? feeParentMappingApiState.status : "pending";
  const canManage = hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES);
  const active = feeParentMappingApiState.summary?.active ?? rows.filter((row) => feeParentText(row.status) === "active").length;
  host.innerHTML = `
    <div class="fee-parent-head">
      <div>
        <span>Parent Mappings</span>
        <strong>${status === "pending" ? "Loading mappings" : status === "error" ? "Mappings unavailable" : `${formatCount(active)} active mapping${active === 1 ? "" : "s"}`}</strong>
        <p>The only place that answers &ldquo;why is this payer priced?&rdquo; A mapping redirects the payer identity after that payer's own lookup has already missed. It relaxes nothing else.</p>
        <small>${escapeHtml(status === "error" ? String(feeParentMappingApiState.error || "") : feeParentMappingApiState.loadedAt ? `Loaded ${feeParentMappingApiState.loadedAt}` : "")}</small>
      </div>
      <label class="fee-parent-withdrawn-toggle">
        <input type="checkbox" data-fee-parent-show-withdrawn ${state.missingRatesShowWithdrawn ? "checked" : ""}>
        Show withdrawn
      </label>
    </div>
    <label>Search mappings <input type="search" data-fee-parent-search value="${escapeHtml(feeParentMappingSearch)}" placeholder="Payer, procedure, state, or reason"></label>
    ${status === "error" ? '<button type="button" class="small-button" data-fee-parent-retry>Try again</button>' : ""}
    <div class="fee-parent-table-wrap">
      <table class="fee-parent-table">
        <thead>
          <tr>
            <th>Source payer</th>
            <th>Parent payer</th>
            <th>Procedure</th>
            <th>Reason</th>
            <th>By</th>
            <th>When</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${status === "pending" && !rows.length ? `<tr><td colspan="7"><strong>Loading parent mappings.</strong></td></tr>`
            : status === "error" && !rows.length ? `<tr><td colspan="7"><strong>Parent mappings could not be loaded.</strong><small>${escapeHtml(String(feeParentMappingApiState.error || ""))}</small></td></tr>`
            : filtered.length ? paging.rows.map((row) => renderFeeParentMappingRow(row, canManage)).join("")
            : rows.length ? '<tr><td colspan="7">No parent mappings match this search.</td></tr>'
            : `<tr><td colspan="7"><strong>No parent mappings yet.</strong><small>Open the worklist, pick a row with no rate, and use Match to parent. Nothing here is ever inferred &mdash; a payer inherits only because a human said so.</small></td></tr>`}
        </tbody>
      </table>
    </div>
    ${status === "ok" ? managementListPagerHtml(paging, "Parent mappings") : ""}
    <p class="fee-parent-footnote">Mappings apply to portal calculations only and do not change other billing systems.</p>
  `;
  bindManagementListPager(host, "parent-mappings", renderFeeParentMappingsPanel);
  const search = host.querySelector("[data-fee-parent-search]");
  search?.addEventListener("input", () => {
    feeParentMappingSearch = search.value;
    const caret = search.selectionStart;
    renderFeeParentMappingsPanel();
    const replacement = host.querySelector("[data-fee-parent-search]");
    replacement?.focus();
    if (caret != null) replacement?.setSelectionRange(caret, caret);
  });
  host.querySelector("[data-fee-parent-retry]")?.addEventListener("click", () => {
    requestFeeScheduleParentMappings({ force: true });
    renderFeeParentMappingsPanel();
  });
  const toggle = host.querySelector("[data-fee-parent-show-withdrawn]");
  if (toggle) {
    toggle.addEventListener("change", () => {
      state.missingRatesShowWithdrawn = Boolean(toggle.checked);
      saveState();
      requestFeeScheduleParentMappings({ force: true });
      renderMissingRates();
    });
  }
  Array.from(host.querySelectorAll("[data-fee-parent-unlink]")).forEach((button) => {
    button.addEventListener("click", () => unlinkFeeParentMapping(button.dataset.feeParentUnlink || ""));
  });
}

function renderFeeParentMappingRow(row = {}, canManage = false) {
  const withdrawn = feeParentText(row.status) !== "active";
  return `
    <tr class="${withdrawn ? "fee-parent-row-withdrawn" : ""}">
      <td><strong>${escapeHtml(row.sourcePayer || "")}</strong><small data-fee-parent-source-state>Source state: ${escapeHtml(String(row.sourceState || "").trim() || "All states")}</small></td>
      <td>${escapeHtml(row.parentPayer || "")}<small data-fee-parent-target-state>Parent state: ${escapeHtml(String(row.parentState || "").trim() || "Same as service")}</small></td>
      <td>${escapeHtml(row.procedureCode || "All CPTs")}</td>
      <td class="fee-parent-reason-cell">${escapeHtml(row.decisionReason || "")}</td>
      <td>${escapeHtml(row.createdByUserId || "")}</td>
      <td>${escapeHtml(String(row.updatedAtUtc || row.createdAtUtc || "").slice(0, 19).replace("T", " "))}</td>
      <td>${withdrawn
        ? `<span class="fee-parent-withdrawn-note">Withdrawn</span>`
        : canManage
          ? `<button class="small-button" type="button" data-fee-parent-unlink="${escapeHtml(row.feeScheduleParentPayerId || "")}" ${feeParentUnlinksInFlight.has(String(row.feeScheduleParentPayerId || "")) ? "disabled" : ""}>Unlink</button>`
          : `<button class="small-button" type="button" disabled title="Managing fee schedules is required to withdraw a mapping.">Unlink</button>`}</td>
    </tr>
  `;
}

function unlinkFeeParentMapping(mappingId = "") {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "unlink a parent fee schedule mapping");
  const mapping = (feeParentMappingApiState.rows || []).find((row) => String(row.feeScheduleParentPayerId || "") === String(mappingId));
  if (!mapping || !feeParentMappingStateCurrent() || !feeScheduleRowInSelectedCompany(mapping)) return;
  if (feeParentUnlinksInFlight.has(String(mappingId))) return;
  const contextKey = feeParentMappingRequestKey(undefined, false);
  const company = activeClientGroupTenant();
  const current = () => contextKey === feeParentMappingRequestKey(undefined, false) && company === activeClientGroupTenant();
  feeParentUnlinksInFlight.add(String(mappingId));
  document.querySelectorAll("[data-fee-parent-unlink]").forEach((button) => { if (button.dataset.feeParentUnlink === String(mappingId)) button.disabled = true; });
  const token = workflowApiToken() || trackerApiToken();
  fetch(`${workflowApiBase()}/api/setup/fee-schedule-parent-payers/unlink`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      tenantId: mapping.tenantId,
      orgId: mapping.orgId,
      feeScheduleParentPayerId: mapping.feeScheduleParentPayerId,
      reason: "withdrawn from the portal Parent Mappings list"
    })
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!current()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        throw new Error("Your session expired. Sign in again — this page will still be here.");
      }
      if (!response.ok || body.error) throw new Error(body.message || body.error || `HTTP ${response.status}`);
      const withdrawn = body.row || {};
      if (body.persisted !== true || String(withdrawn.feeScheduleParentPayerId || "") !== String(mappingId) || String(withdrawn.orgId || "") !== String(mapping.orgId) || withdrawn.status !== "withdrawn") throw new Error("The server did not confirm withdrawing this mapping.");
      showToast(`${mapping.sourcePayer} no longer inherits ${mapping.parentPayer}.`);
      requestFeeScheduleParentMappings({ force: true });
      requestFeeScheduleMissingApiRows({ force: true });
      renderMissingRates();
    })
    .catch((error) => { if (current()) showToast(`Unlink failed: ${error.message}`); })
    .finally(() => { feeParentUnlinksInFlight.delete(String(mappingId)); if (current()) renderFeeParentMappingsPanel(); });
}

// --- match to parent -------------------------------------------------------

function openFeeParentMatch(encoded = "") {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "match a payer to a parent fee schedule");
  let row = {};
  try {
    row = JSON.parse(decodeURIComponent(String(encoded || "")));
  } catch (_) {
    row = {};
  }
  const source = (feeScheduleMissingApiState.rows || []).find((candidate) => feeScheduleMissingKey(candidate) === feeScheduleMissingKey(row)) || row;
  feeParentMatchDraft = { row: source, parentPayer: "", reason: "" };
  feeParentPreviewState = { key: "", status: "idle", body: null, error: "" };
  requestFeeParentCandidates(source, { force: true });
  // An explicit open must render before scrolling, even while background
  // publication renders are deferred to protect this newly created draft.
  if (feeScheduleMissingAuthoritativeReady()) renderFeeParentMatchPanel();
  else renderMissingRates();
  const panel = els.feeParentMatchPanel;
  if (panel && !panel.hidden && feeParentMatchDraft) {
    panel.scrollIntoView?.({ block: "start", behavior: "instant" });
    panel.querySelector("[data-fee-parent-close]")?.focus({ preventScroll: true });
  }
}

function closeFeeParentMatch() {
  feeParentMatchDraft = null;
  feeParentPreviewState = { key: "", status: "idle", body: null, error: "" };
  renderMissingRates();
}

// The picker only ever offers payers that ACTUALLY have approved, active rows
// for this org and this CPT — the same rows the preview endpoint prices from.
// Offering anything else produces a preview that resolves to nothing and a
// human wondering which of the two screens is lying. No free text: a parent is
// an existing schedule, not a name someone typed.
function feeParentCandidateKey(row = {}) {
  return [row.orgId, row.procedure].map((value) => String(value || "").trim().toLowerCase()).join("|");
}

let feeParentCandidateSequence = 0;
async function requestFeeParentChoicePage(row, { search = "", page = 1, stateOnly = false, signal } = {}) {
  const access = feeScheduleApiKey(false), token = workflowApiToken() || trackerApiToken();
  const params = new URLSearchParams({ orgId: String(row.orgId || ""), procedureCode: String(row.procedure || ""), status: "active", approvalStatus: "approved", operation: stateOnly ? "facets" : "candidates", page: String(page), search });
  params.set("setupFilters", JSON.stringify({ sourcePayer: row.payer, candidateState: stateOnly ? "All" : feeParentMatchDraft?.scheduleState || "All" }));
  if (stateOnly) { params.delete("search"); params.set("facetKeys", JSON.stringify(["state"])); params.set("facetOptions", JSON.stringify({ state: { search, page, pageSize: 50 } })); }
  const response = await fetch(workflowApiBase() + "/api/setup/fee-schedule-rates?" + params, { cache: "no-store", signal, headers: { ...(token ? { authorization: "Bearer " + token } : {}) } });
  if (response.status === 401) {
    if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    throw Object.assign(new Error("Your session expired. Sign in again — this page will still be here."), { sessionExpired: true });
  }
  const body = await response.json();
  if (access !== feeScheduleApiKey(false)) throw new Error("Fee access changed");
  if (!response.ok || body.complete !== true) throw new Error(body.message || "Parent choices unavailable");
  if (stateOnly) return { options: (body.facets?.state || []).map(option => ({ ...option, value: option.value === "(Blank)" ? "__unspecified__" : option.value, label: option.value === "(Blank)" ? "State not specified" : option.value })), page: body.facetPages?.state };
  return { candidates: body.candidates || [], options: (body.candidates || []).map(row => ({ value: row.payer, label: row.payer })), page: { page: body.page?.number, pageSize: 50, matchingOptions: body.page?.totalRows, hasMore: body.page?.number < body.page?.totalPages }, revision: body.revision };
}

function requestFeeParentCandidates(row = {}, options = {}) {
  if (!feeScheduleApiEnabled() || !hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES) || !String(row.orgId || "").trim()) return;
  const key = feeParentCandidateKey(row);
  if (!options.force && feeParentCandidateState.key === key && ["pending", "ok"].includes(feeParentCandidateState.status)) return;
  const sequence = ++feeParentCandidateSequence;
  const previousCandidates = feeParentCandidateState.key === key ? (feeParentCandidateState.candidates || []) : [];
  feeParentCandidateState = { key, status: "pending", rows: [], candidates: [], error: "" };
  requestFeeParentChoicePage(row).then(result => {
    if (sequence !== feeParentCandidateSequence || feeParentCandidateState.key !== key) return;
    feeParentCandidateState = { key, status: "ok", rows: [], candidates: result.candidates, page: result.page, revision: result.revision, error: "" };
    if (feeParentMatchDraft) renderFeeParentMatchPanel();
  }).catch(error => {
    if (sequence !== feeParentCandidateSequence || feeParentCandidateState.key !== key) return;
    feeParentCandidateState = { key, status: "error", rows: [], candidates: error.sessionExpired ? previousCandidates : [], error: error.message };
    if (feeParentMatchDraft) renderFeeParentMatchPanel();
  });
}

function feeParentPayerOptionPool(row = {}) {
  const local = feeRowsForActiveUser(state.feeScheduleRows || []);
  const key = feeParentCandidateKey(row);
  const remote = feeParentCandidateState.key === key
    ? feeRowsForActiveUser(feeParentCandidateState.rows || [])
    : [];
  return local.concat(remote);
}

function feeParentPayerOptions(row = {}) {
  if (feeScheduleApiEnabled()) { const names = feeParentCandidateState.key === feeParentCandidateKey(row) ? (feeParentCandidateState.candidates || []).map(item => item.payer) : []; if (feeParentMatchDraft?.parentPayer) names.push(feeParentMatchDraft.parentPayer); return uniqueSorted(names); }
  const orgId = feeParentText(row.orgId);
  const procedure = feeParentText(row.procedure);
  const source = feeParentText(row.payer);
  const payers = new Map();
  feeParentPayerOptionPool(row).forEach((rate) => {
    if (feeParentText(rate.orgId) !== orgId) return;
    if (String(rate.status || "active") !== "active") return;
    if (String(rate.approvalStatus || "") !== "approved") return;
    if (procedure && feeParentText(rate.procedure) !== procedure) return;
    const payer = String(rate.payer || "").trim();
    if (!payer || feeParentText(payer) === source) return;
    payers.set(feeParentText(payer), payer);
  });
  return Array.from(payers.values()).sort((left, right) => left.localeCompare(right));
}

function feeParentPayerScheduleStates(row = {}, payer = "") {
  if (feeScheduleApiEnabled()) return (feeParentCandidateState.key === feeParentCandidateKey(row) ? feeParentCandidateState.candidates || [] : []).find(item => feeParentText(item.payer) === feeParentText(payer))?.states?.map(item => item.value) || [];
  const states = new Set();
  feeParentPayerOptionPool(row).forEach((rate) => {
    if (feeParentText(rate.orgId) !== feeParentText(row.orgId)) return;
    if (String(rate.status || "active") !== "active" || String(rate.approvalStatus || "") !== "approved") return;
    if (feeParentText(row.procedure) && feeParentText(rate.procedure) !== feeParentText(row.procedure)) return;
    if (feeParentText(rate.payer) !== feeParentText(payer)) return;
    states.add(String(rate.state || "").trim() || "__unspecified__");
  });
  return Array.from(states).sort(compareText);
}

function feeParentPreviewKey(row = {}, parentPayer = "") {
  return [row.orgId, row.payer, row.procedure, row.m1, row.m2, row.place, row.network, parentPayer].map((value) => String(value || "")).join("|");
}

function requestFeeParentPreview() {
  const draft = feeParentMatchDraft;
  if (!draft || !draft.parentPayer) return;
  const row = draft.row || {};
  const key = feeParentPreviewKey(row, draft.parentPayer);
  if (feeParentPreviewState.key === key && ["pending", "ok", "error"].includes(feeParentPreviewState.status)) return;
  feeParentPreviewState = { key, status: "pending", body: null, error: "" };
  renderFeeParentMatchPanel();
  const token = workflowApiToken() || trackerApiToken();
  fetch(`${workflowApiBase()}/api/setup/fee-schedule-parent-payers/preview`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    // tenantId AND orgId always travel with the request. The server falls back
    // to "default-tenant"/"default-org" when they are absent, which would file
    // the decision somewhere it prices nothing and looks curated — the same
    // silently-dead-mapping failure the source-state refusal exists to prevent.
    // m1/m2/place are sent so the PREVIEW prices this exact line; they are not
    // sent on save (see submitFeeParentMapping).
    body: JSON.stringify({
      tenantId: row.tenantId || "",
      orgId: row.orgId || "",
      sourcePayer: row.payer || "",
      parentPayer: draft.parentPayer,
      procedure: row.procedure || "",
      m1: row.m1 || "",
      m2: row.m2 || "",
      place: row.place || "",
      network: row.network || "",
      dos: row.serviceEndDate || row.serviceDate || ""
    })
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (feeParentPreviewState.key !== key) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        feeParentPreviewState = { key, status: "error", body: null, error: "Your session expired. Sign in again — this page will still be here." };
      } else if (!response.ok || body.error) {
        feeParentPreviewState = { key, status: "error", body: null, error: body.message || body.error || `HTTP ${response.status}` };
      } else {
        feeParentPreviewState = { key, status: "ok", body, error: "" };
      }
      renderFeeParentMatchPanel();
    })
    .catch((error) => {
      if (feeParentPreviewState.key !== key) return;
      feeParentPreviewState = { key, status: "error", body: null, error: error.message };
      renderFeeParentMatchPanel();
    });
}

// changesPricing is a SINGLE flag covering THREE different worlds, and the panel
// used to render one explanation for all of them: "this line already prices
// without a parent". That is the right sentence for exactly one of the three,
// and it flatly contradicted a preview reading "No rate" directly above it.
// One function, used by BOTH the disabled-save reason and the amber note, so
// the two can never diverge again.
function feeParentInertReason(body = {}) {
  const draft = feeParentMatchDraft || {};
  const parent = String(draft.parentPayer || "the parent");
  const cpt = String((draft.row || {}).procedure || "").trim();
  const cptLabel = cpt ? `CPT ${cpt}` : "this CPT";
  const before = body.before || {};
  const parentRowCount = Number(body.parentRowCount || 0);
  // 1. The line already has a rate of its own. A direct hit always wins, so the
  //    parent list is never reached — the original copy, now only where true.
  if (before.resolved) {
    return "This line already prices without a parent, so a parent would never be consulted — the payer's own rows always win. Saving this would change nothing.";
  }
  // 2. The dead end. The parent has nothing for this CPT, so there is nothing to
  //    inherit and no parent can help: this line needs a rate, not a mapping.
  if (!parentRowCount) {
    return `${parent} has no approved, active rate row for ${cptLabel} in this client, so there is nothing to inherit. Add a rate for this payer instead, or approve a rate on the payer you wanted to inherit.`;
  }
  // 3. The parent prices the CPT, just not THIS line. The picker matches on CPT;
  //    the resolver also has to satisfy modifiers, place of service, network and
  //    the effective dates. Saying which is the difference between a useful
  //    screen and a shrug.
  return `${parent} has ${formatCount(parentRowCount)} approved row${parentRowCount === 1 ? "" : "s"} for ${cptLabel}, but none of them match this line's modifiers, place of service, network or dates — so it would still have no rate. Try a different parent, or add a rate for this payer.`;
}

// Every reason the save is refused, in one place, so the button can always say
// WHY in its title instead of just sitting there greyed out.
function feeParentSaveBlockReason() {
  if (feeParentSaveInFlight) return "Saving this mapping. Wait for confirmation.";
  const draft = feeParentMatchDraft;
  if (!draft) return "Pick a worklist row first.";
  if (!feeScheduleRowInSelectedCompany(draft.row || {})) return "Choose a missing rate within the company selected in the header.";
  if (!feeScheduleMissingAuthoritativeReady()) return "The authoritative missing-rate worklist must reconnect before this mapping can be saved.";
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return "Managing fee schedules is required to save a parent mapping.";
  if (!draft.parentPayer) return "Choose the parent payer whose schedule this payer should follow.";
  if (feeParentPreviewState.status === "pending") return "Waiting for the live preview to price this decision.";
  if (feeParentPreviewState.status === "error") return `The preview failed, so the effect of this mapping is unknown: ${feeParentPreviewState.error}`;
  if (feeParentPreviewState.status !== "ok") return "Waiting for the live preview to price this decision.";
  if (!feeParentPreviewState.body) return "Waiting for the live preview to price this decision.";
  if (!feeParentPreviewState.body.changesPricing) return feeParentInertReason(feeParentPreviewState.body);
  if (!String(draft.reason || "").trim()) {
    return "Record why this payer should inherit that schedule. A mapping moves expected reimbursement, and one with no recorded human is indistinguishable from a guess.";
  }
  return "";
}

function renderFeeParentMatchPanel() {
  const host = els.feeParentMatchPanel;
  if (!host) return;
  const restoreCloseFocus = Boolean(document.activeElement && document.activeElement === host.querySelector("[data-fee-parent-close]"));
  const focusedReason = document.activeElement === host.querySelector("[data-fee-parent-reason]") && document.activeElement;
  const reasonCaret = focusedReason ? [focusedReason.selectionStart, focusedReason.selectionEnd] : null;
  const focusedParentSearch = host.contains?.(document.activeElement) && document.activeElement?.matches?.("[data-fee-search-input]") ? document.activeElement : null;
  const parentSearchFocus = focusedParentSearch ? { value: focusedParentSearch.value, start: focusedParentSearch.selectionStart, end: focusedParentSearch.selectionEnd, expanded: focusedParentSearch.getAttribute("aria-expanded") === "true" } : null;
  const draft = feeParentMatchDraft;
  if (draft && !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) {
    host.innerHTML = `
      <div class="fee-parent-match-empty">
        <strong>Parent mapping is view only.</strong>
        <p>Managing fee schedules is required to change rates or parent mappings.</p>
      </div>
    `;
    return;
  }
  if (!draft) {
    host.innerHTML = "";
    host.hidden = true;
    return;
  }
  host.hidden = false;
  const row = draft.row || {};
  const options = feeParentPayerOptions(row);
  const scheduleStates = new Map(options.map((payer) => [payer, feeParentPayerScheduleStates(row, payer)]));
  const stateOptions = Array.from(new Set(Array.from(scheduleStates.values()).flat())).sort(compareText);
  const selectedScheduleState = draft.scheduleState || "All";
  const visibleOptions = feeScheduleApiEnabled() ? options : options.filter((payer) => selectedScheduleState === "All" || scheduleStates.get(payer).includes(selectedScheduleState));
  const stateLabel = (value) => value === "__unspecified__" ? "State not specified" : value;
  const candidateKey = feeParentCandidateKey(row);
  const loadingParents = feeParentCandidateState.key === candidateKey && feeParentCandidateState.status === "pending" && !options.length;
  const candidateError = feeParentCandidateState.key === candidateKey && feeParentCandidateState.status === "error" && !options.length
    ? String(feeParentCandidateState.error || "Could not load parent schedules")
    : "";
  const pickerEmptyLabel = options.length
    ? "Choose a parent schedule\u2026"
    : loadingParents
      ? "Loading parent schedules\u2026"
      : candidateError
        ? "Could not load parent schedules"
        : "No approved parent schedule for this CPT";
  const pickerHelp = options.length
    ? `Only payers with an approved, active row for CPT ${escapeHtml(row.procedure || "")} in this client are offered \u2014 the same pool the preview prices from. Whether one of those rows also matches this line's modifiers, place of service, network and dates is what the preview below answers.`
    : loadingParents
      ? `Loading approved schedules for CPT ${escapeHtml(row.procedure || "")} in this client\u2026`
      : candidateError
        ? escapeHtml(candidateError)
        : `No approved parent rate for CPT ${escapeHtml(row.procedure || "")} in this client. Add a rate to continue.`;
  const blockReason = feeParentSaveBlockReason();
  host.innerHTML = `
    <div class="fee-parent-match-head">
      <div>
        <span>Match to parent</span>
        <strong>${escapeHtml(row.payer || "Unknown payer")} &middot; CPT ${escapeHtml(row.procedure || "—")}</strong>
        <p>Choose an existing insurance schedule, check the preview, then save.</p>
      </div>
      <button class="small-button" type="button" data-fee-parent-close>Close</button>
    </div>
    <dl class="fee-parent-context">
      ${[
        ["Client", `${clientActionCompanyName(row.orgName, row.orgId) || clientActionCompanyName(row.tenantId, row.orgId) || portalCompanyLabel(row.orgId)}`],
        ["Payer", `${row.payer || ""}${row.payerId ? ` · external ${row.payerId}` : ""}`],
        ["Patient state / classification", row.state || "State unavailable"],
        ["CPT", row.procedure || "—"],
        ["Modifiers", [row.m1, row.m2].filter(Boolean).join(" / ") || "none"],
        ["POS", row.place || "any"],
        ["Network", row.network || "any"],
        ["Lines waiting", `${Number(row.services || 0).toLocaleString()} service rows · ${Number(row.units || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} units`]
      ].map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(String(value))}</dd></div>`).join("")}
    </dl>
    ${options.length || (feeScheduleApiEnabled() && selectedScheduleState !== "All") ? `<label class="fee-parent-state">Schedule state<select data-fee-parent-state data-fee-searchable-select><option value="All">All schedule states</option>${stateOptions.map((value) => `<option value="${escapeHtml(value)}" ${selectedScheduleState === value ? "selected" : ""}>${escapeHtml(stateLabel(value))}</option>`).join("")}</select><small>State filters this list only; it does not limit the saved match.</small></label>` : ""}
    <label class="fee-parent-picker">
      Parent insurance
      <select data-fee-searchable-select data-fee-parent-picker ${visibleOptions.length ? "" : "disabled"}>
        <option value="">${escapeHtml(pickerEmptyLabel)}</option>
        ${visibleOptions.map((payer) => `<option value="${escapeHtml(payer)}" ${draft.parentPayer === payer ? "selected" : ""}>${escapeHtml(payer)} — ${escapeHtml(scheduleStates.get(payer).map(stateLabel).join(", "))}</option>`).join("")}
      </select>
      <small>${options.length ? "Search insurance name or schedule state." : pickerHelp}</small>
    </label>
    ${renderFeeParentPreviewBlock(row)}
    <label class="fee-parent-reason">
      Reason for this match (required)
      <textarea data-fee-parent-reason rows="2" placeholder="e.g. Same contract as the parent plan; confirmed with the payer rep on 2026-08-10.">${escapeHtml(draft.reason || "")}</textarea>
    </label>
    <div class="fee-parent-match-actions">
      <button class="primary-button" type="button" data-fee-parent-save ${blockReason ? "disabled" : ""} title="${escapeHtml(blockReason)}">Save mapping</button>
      <button class="small-button" type="button" data-fee-parent-add-rate="${encodeFeeScheduleMissingPayload(row)}">Add a rate instead</button>
      <button class="small-button" type="button" data-fee-parent-close>Cancel</button>
      <small>${escapeHtml(blockReason || "Ready to save this parent mapping.")}</small>
    </div>
    <details class="fee-parent-footnote"><summary>How parent matching works</summary><p>The mapping is saved for this payer and CPT only. Modifiers and POS are not part of it &mdash; the parent schedule's own rows decide those. Mappings apply to portal calculations only and do not change other billing systems.</p><p>Price this payer off an existing schedule. The parent list is consulted only after this payer's own rows have already missed, so a direct hit always wins.</p><p>Schedule state filters the choices only. It does not restrict the saved mapping to that state. State not specified means the schedule has no configured state; it does not mean nationwide.</p><p>${pickerHelp}</p></details>
  `;
  // Empty, loading and failure states do not show a reason or unusable Save form.
  if (!options.length) {
    host.querySelector(".fee-parent-preview")?.remove();
    host.querySelector(".fee-parent-reason")?.remove();
    host.querySelector("[data-fee-parent-save]")?.remove();
    host.querySelector(".fee-parent-match-actions small")?.remove();
    const add = host.querySelector("[data-fee-parent-add-rate]");
    if (add) { add.textContent = "Add rate"; add.className = "primary-button"; }
    if (candidateError) host.querySelector(".fee-parent-match-actions")?.insertAdjacentHTML("afterbegin", '<button class="small-button" type="button" data-fee-parent-retry>Retry loading schedules</button>');
  } else if (!visibleOptions.length) {
    host.querySelector(".fee-parent-picker small").textContent = "No parent schedules match this state.";
    host.querySelector("[data-fee-parent-picker] option").textContent = "No schedules for this state";
    host.querySelector(".fee-parent-picker").insertAdjacentHTML("afterend", '<button class="small-button" type="button" data-fee-parent-clear-state>Clear state filter</button>');
  }
  const changeScheduleState = (value) => {
    const keepPayer = value === "All" || scheduleStates.get(draft.parentPayer)?.includes(value);
    feeParentMatchDraft = { ...draft, scheduleState: value, parentPayer: keepPayer ? draft.parentPayer : "" };
    if (!keepPayer) feeParentPreviewState = { key: "", status: "idle", body: null, error: "" };
    if (feeScheduleApiEnabled()) requestFeeParentCandidates(row, { force: true });
    renderFeeParentMatchPanel();
    host.querySelector("[data-fee-parent-state]")?.focus({ preventScroll: true });
  };
  host.querySelector("[data-fee-parent-state]")?.addEventListener("change", (event) => changeScheduleState(event.target.value));
  host.querySelector("[data-fee-parent-clear-state]")?.addEventListener("click", () => changeScheduleState("All"));
  host.querySelector("[data-fee-parent-retry]")?.addEventListener("click", () => { requestFeeParentCandidates(row, { force: true }); renderFeeParentMatchPanel(); });
  enhanceFeeSearchSelects(host);
  const picker = host.querySelector("[data-fee-parent-picker]");
  if (picker) {
    picker.addEventListener("change", () => {
      feeParentMatchDraft = { ...draft, parentPayer: picker.value || "" };
      feeParentPreviewState = { key: "", status: "idle", body: null, error: "" };
      renderFeeParentMatchPanel();
      requestFeeParentPreview();
    });
  }
  const reason = host.querySelector("[data-fee-parent-reason]");
  if (reason) {
    reason.addEventListener("input", () => {
      feeParentMatchDraft = { ...feeParentMatchDraft, reason: reason.value || "" };
      const save = host.querySelector("[data-fee-parent-save]");
      const blocked = feeParentSaveBlockReason();
      if (save) {
        save.disabled = Boolean(blocked);
        save.title = blocked;
      }
      const note = host.querySelector(".fee-parent-match-actions small");
      if (note) note.textContent = blocked || "Ready to save this parent mapping.";
    });
  }
  Array.from(host.querySelectorAll("[data-fee-parent-close]")).forEach((button) => {
    button.addEventListener("click", closeFeeParentMatch);
  });
  host.querySelector("[data-fee-parent-add-rate]")?.addEventListener("click", (event) => {
    openMissingFeeRate(event.currentTarget.dataset.feeParentAddRate || "");
  });
  host.querySelector("[data-fee-parent-save]")?.addEventListener("click", submitFeeParentMapping);
  if (restoreCloseFocus) host.querySelector("[data-fee-parent-close]")?.focus({ preventScroll: true });
  if (reasonCaret && reason) { reason.focus({ preventScroll: true }); reason.setSelectionRange(...reasonCaret); }
  const parentSearch = host.querySelector("[data-fee-search-input]");
  if (parentSearchFocus && parentSearch) {
    parentSearch.dataset.feeSearchQuietFocus = "true";
    parentSearch.focus({ preventScroll: true });
    if (parentSearchFocus.expanded) {
      parentSearch.value = parentSearchFocus.value;
      parentSearch.dispatchEvent(new Event("input", { bubbles: true }));
      parentSearch.setSelectionRange(parentSearchFocus.start, parentSearchFocus.end);
    }
  }
  if (feeParentSaveInFlight) host.querySelectorAll("input, select, textarea, [data-fee-parent-save]").forEach((control) => { control.disabled = true; });
}

// THE TRUST DEVICE. The legacy tracker shows the parent's rows before you
// commit; ours runs the real resolver twice — without the mapping and with it
// — so the answer is not "here are some rows that look relevant" but "today
// this line has no rate; with this, it prices at $X off this row".
function renderFeeParentPreviewBlock(row = {}) {
  const draft = feeParentMatchDraft || {};
  if (!draft.parentPayer) {
    return `<div class="fee-parent-preview fee-parent-preview-idle">Pick a parent to see a rate example before anything is saved.</div>`;
  }
  if (feeParentPreviewState.status === "pending") {
    return `<div class="fee-parent-preview fee-parent-preview-idle">Checking a rate example against the live rate rows…</div>`;
  }
  if (feeParentPreviewState.status === "error") {
    return `<div class="fee-parent-preview fee-parent-preview-error"><strong>The preview failed.</strong><span>${escapeHtml(String(feeParentPreviewState.error || ""))}</span></div>`;
  }
  const body = feeParentPreviewState.body;
  if (!body) return "";
  const before = body.before || {};
  const after = body.after || {};
  const units = Number(row.units || 0);
  const afterRate = Number(after.rate || 0);
  const parentRows = Array.isArray(body.parentRows) ? body.parentRows : [];
  return `
    <div class="fee-parent-preview ${body.changesPricing ? "fee-parent-preview-live" : "fee-parent-preview-inert"}">
      <p class="fee-parent-example-note">Rate example for the selected line. This is not the total impact of saving the mapping.</p>
      <div class="fee-parent-beforeafter">
        <div>
          <span>Today</span>
          <strong>${before.resolved ? feeParentRateMoney(before.rate) : "No rate"}</strong>
          <small>${escapeHtml(before.resolved ? `${before.matchedRule || "matched"}${before.viaParentPayer ? ` via ${before.parentPayer}` : ""}` : (before.emptiedAt ? `Nothing matched — the pool emptied at ${before.emptiedAt}` : "No fee schedule row matches this line."))}</small>
        </div>
        <div class="fee-parent-arrow" aria-hidden="true">&rarr;</div>
        <div>
          <span>With this parent</span>
          <strong>${after.resolved ? feeParentRateMoney(after.rate) : "Still no rate"}</strong>
          <small>${escapeHtml(after.resolved ? `${after.matchedRule || "matched"}${after.viaParentPayer ? ` via ${after.parentPayer}` : ""}` : "The parent has no row that matches this line either.")}</small>
        </div>
      </div>
      ${after.resolved && units > 0 ? `<p class="fee-parent-line-money">Rate example: ${Number(units).toLocaleString(undefined, { maximumFractionDigits: 2 })} units &times; ${feeParentRateMoney(afterRate)} = <strong>${money(units * afterRate)}</strong></p>` : ""}
      ${body.changesPricing ? "" : `<p class="fee-parent-inert-note">${escapeHtml(feeParentInertReason(body))}</p>`}
      <details class="fee-parent-rows" ${body.changesPricing ? "open" : ""}>
        <summary>${escapeHtml(draft.parentPayer)} &middot; ${formatCount(Number(body.parentRowCount || 0))} row${Number(body.parentRowCount || 0) === 1 ? "" : "s"} for this CPT${Number(body.parentRowCount || 0) > parentRows.length ? " (first 12)" : ""}</summary>
        <div class="fee-parent-rows-scroll" role="region" aria-label="Parent schedule rate rows" tabindex="0">
        <table class="fee-parent-rows-table">
          <thead><tr><th>M1</th><th>M2</th><th>POS</th><th>Rate</th><th>Network</th><th>Method</th><th>Start</th><th>End</th></tr></thead>
          <tbody>
            ${parentRows.length ? parentRows.map((parentRow) => `
              <tr>
                <td>${escapeHtml(parentRow.m1 || "")}</td>
                <td>${escapeHtml(parentRow.m2 || "")}</td>
                <td>${escapeHtml(parentRow.place || "")}</td>
                <td>${feeParentRateMoney(parentRow.rate || 0)}</td>
                <td>${escapeHtml(parentRow.network || "")}</td>
                <td>${escapeHtml(parentRow.rateMatchMethod || "")}</td>
                <td>${escapeHtml(String(parentRow.startDate || "").slice(0, 10))}</td>
                <td>${escapeHtml(String(parentRow.endDate || "").slice(0, 10))}</td>
              </tr>
            `).join("") : `<tr><td colspan="8">This parent has no approved rows for this CPT.</td></tr>`}
          </tbody>
        </table>
        </div>
      </details>
    </div>
  `;
}

let feeParentSaveInFlight = false;
const feeParentUnlinksInFlight = new Set();

function submitFeeParentMapping() {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "save a parent fee schedule mapping");
  const blocked = feeParentSaveBlockReason();
  if (blocked) return showToast(blocked);
  const originalDraft = feeParentMatchDraft;
  const draft = { ...originalDraft, row: { ...(originalDraft.row || {}) } };
  const row = draft.row;
  const contextKey = feeParentMappingRequestKey(undefined, false);
  const company = activeClientGroupTenant();
  const current = () => contextKey === feeParentMappingRequestKey(undefined, false) && company === activeClientGroupTenant() && feeParentMatchDraft === originalDraft && originalDraft.parentPayer === draft.parentPayer && originalDraft.reason === draft.reason;
  feeParentSaveInFlight = true;
  els.feeParentMatchPanel?.querySelectorAll("input, select, textarea, [data-fee-parent-save]").forEach((control) => { control.disabled = true; });
  const token = workflowApiToken() || trackerApiToken();
  fetch(`${workflowApiBase()}/api/setup/fee-schedule-parent-payers`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    // This form has no source-state input and creates an all-states mapping.
    // Never infer pricing state from optional display classification. Exact
    // state mappings use the separate server-attested snapshot write path.
    // NO m1/m2/place either: the mapping identity key excludes them, so
    // re-saving with different modifiers would REPLACE the row and silently
    // change its scope. The parent schedule decides modifiers and POS.
    body: JSON.stringify({
      tenantId: row.tenantId || "",
      orgId: row.orgId || "",
      sourcePayer: row.payer || "",
      parentPayer: draft.parentPayer,
      procedure: row.procedure || "",
      decisionReason: String(draft.reason || "").trim()
    })
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!current()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        throw new Error("Your session expired. Sign in again — this page will still be here.");
      }
      if (!response.ok || body.error) throw new Error(body.message || body.error || `HTTP ${response.status}`);
      const saved = body.row || {};
      if (body.persisted !== true || !saved.feeScheduleParentPayerId || String(saved.orgId) !== String(row.orgId) || feeParentText(saved.sourcePayer) !== feeParentText(row.payer) || feeParentText(saved.parentPayer) !== feeParentText(draft.parentPayer) || String(saved.procedureCode || "") !== String(row.procedure || "") || saved.status !== "active") throw new Error("The server did not confirm this parent mapping.");
      showToast(`${row.payer} now prices off ${draft.parentPayer} for CPT ${row.procedure || "all"}.`);
      feeParentMatchDraft = null;
      feeParentPreviewState = { key: "", status: "idle", body: null, error: "" };
      requestFeeScheduleParentMappings({ force: true });
      requestFeeScheduleMissingApiRows({ force: true });
      renderMissingRates();
    })
    .catch((error) => { if (current()) showToast(`Parent mapping not saved: ${error.message}`); })
    .finally(() => { feeParentSaveInFlight = false; if (contextKey === feeParentMappingRequestKey(undefined, false) && company === activeClientGroupTenant()) renderFeeParentMatchPanel(); });
}

function reportPayerWorklistRows(followUpQueues, claims) {
  const apiRows = Array.isArray(followUpQueues?.payerQueueRows) ? followUpQueues.payerQueueRows : [];
  if (apiRows.length) {
    return apiRows.map((row) => ({
      payer: row.payer || "Unknown",
      queueKey: row.queueKey || queueKeyForLabel(row.queue),
      queue: row.queue || row.queueKey || "Follow-Up",
      claims: nonNegativeIntegerValue(row.claims),
      outstanding: row.outstanding,
      guarded: nonNegativeIntegerValue(row.guarded)
    }));
  }
  return payerWorklistRows(claims);
}

function reportStatusQueueRows(followUpQueues, claims) {
  const apiRows = Array.isArray(followUpQueues?.statusQueueRows) ? followUpQueues.statusQueueRows : [];
  if (apiRows.length) {
    return apiRows.map((row) => ({
      status: row.status || "Unknown",
      queueKey: row.queueKey || queueKeyForLabel(row.queue),
      queue: row.queue || row.queueKey || "Follow-Up",
      claims: nonNegativeIntegerValue(row.claims),
      outstanding: row.outstanding,
      guarded: nonNegativeIntegerValue(row.guarded)
    }));
  }
  const rows = [];
  ["missingPayerResponse", "waitingPrimary", "waitingSecondary", "stale"].forEach((queueKey) => {
    groupedOperationalRows(claims.filter((claim) => matchesQueue(claim, queueKey)), (claim) => claim.claimStatus || claim.match || "Unknown", "status")
      .forEach((row) => rows.push({ ...row, queueKey, queue: queueLabel(queueKey), outstanding: row.owed }));
  });
  return rows.sort((a, b) => moneyNumber(b.outstanding || b.owed || 0) - moneyNumber(a.outstanding || a.owed || 0) || b.guarded - a.guarded);
}

function reportSummaryMetrics({ apiSummary, claims, serviceRows, financialBuckets, totalBilled, finalCollectionRate, apiStatus }) {
  // Reframed positively per user direction: charts must reflect well on TMS.
  // Lead with what TMS has DONE (Collected, Resolution Rate). De-emphasize
  // raw counts. Skip noisy duplicates of topbar metrics.
  const localMetrics = [
    ["Collected by TMS", money(financialBuckets.final.paid)],
    ["Resolution Rate", finalCollectionRate],
    ["Expected Revenue", money(financialBuckets.final.expected)],
    ["AR In Motion", money(financialBuckets.final.owed)],
    ["Claims Worked", claims.length],
    ["Service Lines", serviceRows.length],
    ["Pending Adjudication", financialBuckets.provisional.claims],
    ["Report Source", apiStatus.label]
  ];
  if (!apiSummary) return localMetrics;
  return [
    ["Collected by TMS", formatReportMoney(apiSummary.finalCollected)],
    ["Resolution Rate", formatPercentValue(apiSummary.finalCollectionRate)],
    ["Expected Revenue", formatReportMoney(apiSummary.finalExpected)],
    // TWO FIGURES, 2.76x APART, BOTH CORRECT - SO BOTH SAY WHICH QUESTION THEY ANSWER.
    //
    // finalOutstanding is the signed net and subtracts credit balances, so it reads far
    // lower than what a biller can actually chase - $2,641,636.87 against $7,287,059.97,
    // the difference being $4,645,839.30 owed BACK to payers. Neither number was wrong;
    // the defect was that one label, "AR In Motion", sat over whichever one happened to
    // be served, so a reader had no way to know which they were looking at.
    //
    // Open Insurance AR is the same noun AR Aging uses for this measure. It renders only
    // when the lane serves it - an older cached summary row has no such field, and a
    // believable $0.00 beside a $2.6M headline is worse than an absent tile.
    ["AR In Motion (net of credits)", formatReportMoney(apiSummary.finalOutstanding)],
    ...(apiSummary.collectableOutstanding == null
      ? []
      : [["Open Insurance AR", formatReportMoney(apiSummary.collectableOutstanding)]]),
    ["Claims Worked", apiSummary.claims ?? 0],
    ["Service Lines", apiSummary.serviceLines ?? 0],
    ["Pending Adjudication", apiSummary.guardedClaims ?? 0],
    ["Report Source", apiStatus.label]
  ];
}

function reportAgingMetrics(apiAging, claims) {
  if (Array.isArray(apiAging) && apiAging.length) {
    return apiAging.map((row) => [
      row.bucket || "Unknown",
      `${formatCount(row.claims)} · ${formatReportMoney(row.outstanding)}`
    ]);
  }
  return CT_AR_AGING_BUCKET_KEYS.map((bucket) => {
    const bucketClaims = claims.filter((claim) => claimAgingBand(claim) === bucket);
    const bucketOwed = sumClaimOutstanding(bucketClaims);
    return [bucket, `${bucketClaims.length} · ${money(bucketOwed)}`];
  });
}

function reportRiskMetrics({ apiRisks, claims, financialBuckets, highRisk, openClaims }) {
  // Reframed positively: every label describes work TMS is actively doing
  // (TMS is the billing company — these should read as 'on top of', not as
  // negligence). Removed 'No Recent Touch' entirely (negative framing).
  if (Array.isArray(apiRisks) && apiRisks.length) {
    return apiRisks.map((row) => [row.label || "Active Work", row.count ?? 0]);
  }
  return [
    ["High-Priority Work", highRisk.length],
    ["Active Follow-Ups", claims.filter(isPastDue).length],
    ["Denials In Appeal", claims.filter(isDeniedClaim).length],
    ["Awaiting Payer Response", openClaims.filter((claim) => claim.match === "Not Paid").length],
    ["Underpayment Recovery", openClaims.filter((claim) => claimUnderpayment(claim) > 0).length],
    ["Awaiting Provider Info", claims.filter(hasMissingOperationalInfo).length],
    ["Pending Adjudication", financialBuckets.provisional.claims],
    ["Premium AR Focus", claims.filter((claim) => claimOutstanding(claim) >= 1000).length]
  ];
}

function reportGroupedRows(apiRows, localRows) {
  if (!Array.isArray(apiRows) || !apiRows.length) return localRows;
  return apiRows.map((row) => ({
    ...row,
    expected: row.expected,
    paid: row.paid,
    outstanding: row.outstanding,
    collectionRate: row.collectionRate
  }));
}

function reportHighRiskRows(executiveAnalytics, claims) {
  const apiRows = Array.isArray(executiveAnalytics?.highRiskAr) ? executiveAnalytics.highRiskAr : [];
  if (apiRows.length) {
    return apiRows.slice(0, 20).map((row) => ({
      claimId: row.claimId || "",
      tenant: row.tenant || row.client || "Unknown",
      payer: row.payer || "Unknown",
      status: row.status || row.match || "Open",
      outstanding: row.outstanding,
      priority: nonNegativeNumber(row.priority, 0),
      reason: row.reason || "High-risk AR"
    }));
  }
  return claims.slice()
    .sort(sortClaims)
    .filter((claim) => claimFinancialView(claim).sourceOutstanding > 0)
    .slice(0, 20)
    .map((claim) => {
      const score = workPriorityScore(claim);
      return {
        claimId: claim.claimId,
        tenant: claim.client || "Unknown",
        payer: claim.primaryPayer || "Unknown",
        status: claim.match || claim.claimStatus || "Open",
        outstanding: claimFinancialView(claim).sourceOutstanding,
        priority: score,
        reason: workReason(claim)
      };
    });
}

function reportDenialRows(apiRows, claims) {
  if (Array.isArray(apiRows) && apiRows.length) return apiRows;
  return denialReasonRows(claims);
}

function reportOwnerRows(apiRows, claims) {
  if (Array.isArray(apiRows) && apiRows.length) return apiRows;
  return ownerWorkloadRows(claims);
}

function reportSlaRows(apiRows, claims) {
  if (Array.isArray(apiRows) && apiRows.length) return apiRows;
  return slaAgingRows(claims);
}

function reportProviderRows(apiRows, analyticsRows, claims) {
  if (Array.isArray(apiRows)) return apiRows;
  if (Array.isArray(analyticsRows) && analyticsRows.length) return analyticsRows;
  return providerPerformanceRows(claims, hasPermission(PERMISSIONS.VIEW_FINANCIALS));
}

function reportUnderpaymentRows(apiRows, analyticsRows, claims) {
  if (Array.isArray(apiRows)) return apiRows;
  if (Array.isArray(analyticsRows) && analyticsRows.length) return analyticsRows;
  return underpaymentExposureRows(claims, hasPermission(PERMISSIONS.VIEW_FINANCIALS));
}

function reportDenialTrendRows(apiRows, analyticsRows, claims) {
  if (Array.isArray(apiRows)) return apiRows;
  if (Array.isArray(analyticsRows) && analyticsRows.length) return analyticsRows;
  return denialTrendRows(claims, hasPermission(PERMISSIONS.VIEW_FINANCIALS));
}

function feeScheduleApprovalCounts(rows = []) {
  return (Array.isArray(rows) ? rows : []).reduce((counts, row) => {
    const status = String(row.approvalStatus || "draft").toLowerCase() === "approved" ? "approved" : "draft";
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, { approved: 0, draft: 0 });
}

function reportPayloadPreparedLabel(reportPayload) {
  if (reportPayload?.preparedCache || reportPayload?.cache?.prepared || reportPayload?.summary?.preparedCache) {
    return "Prepared Azure report cache path";
  }
  if (workflowApiBase()) return "Complete report processing is available";
  return "Local QA report path";
}

function feeScheduleActiveFilterLabels() {
  const filters = feeScheduleEffectiveFilters();
  return [
    filters.tenantId && filters.tenantId !== "All" ? `Tenant: ${filters.tenantId}` : "",
    filters.orgId && filters.orgId !== "All" ? `Company: ${portalCompanyLabel(filters.orgId)}` : "",
    filters.payer && filters.payer !== "All" ? `Payer: ${filters.payer}` : "",
    filters.state && filters.state !== "All" ? `State: ${filters.state === "__unavailable__" ? "Not specified" : filters.state}` : "",
    filters.procedure && filters.procedure !== "All" ? `Procedure: ${filters.procedure}` : "",
    filters.network && filters.network !== "All" ? `Network: ${filters.network === "__unavailable__" ? "Not specified" : filters.network}` : "",
    filters.activeDate ? `Active on: ${filters.activeDate}` : "",
    filters.search ? `Search: ${filters.search}` : ""
  ].filter(Boolean);
}

function reportDashboardScope(context = {}) {
  const summary = context.reportPayload?.summary || null;
  if (summary) {
    const claims = nonNegativeIntegerValue(summary.claims);
    const serviceLines = nonNegativeIntegerValue(summary.serviceLines);
    return {
      claims,
      serviceLines,
      label: `${formatNounCount(claims, "claim")} · ${formatNounCount(serviceLines, "service line")} in the current scope`
    };
  }
  const claims = Array.isArray(context.claims) ? context.claims.length : 0;
  return {
    claims,
    serviceLines: Array.isArray(context.claims) ? context.claims.reduce((sum, claim) => sum + ((claim.services || []).length), 0) : 0,
    label: `${formatNounCount(claims, "claim")} in view`
  };
}

function feeScheduleFieldLabel(field = "") {
  return ({
    orgId: "Organization ID",
    payer: "Payer",
    state: "State",
    procedure: "CPT",
    m1: "Modifier 1",
    m2: "Modifier 2",
    place: "Place of service",
    rate: "Rate",
    startDate: "Start date",
    endDate: "End date",
    network: "Network"
  })[String(field || "")] || "Fee schedule value";
}

// Plain text, same column order as the staff grid; the staff-only columns stay
// in the DOM (so headers and cells align) and are hidden by the client view.
function feeScheduleClientRowHtml(row, archived = false) {
  const text = (value) => {
    const raw = value == null || value === "undefined" || value === "null" ? "" : String(value).trim();
    return raw ? escapeHtml(raw) : '<span class="missing-value">—</span>';
  };
  const rateNumber = Number(row.rate);
  const rate = row.rate !== "" && row.rate != null && Number.isFinite(rateNumber) ? escapeHtml(money(rateNumber)) : '<span class="missing-value">—</span>';
  return `
    <tr class="fee-row-readonly${archived ? " fee-row-archived" : ""}">
      <td data-fee-col="orgId"></td>
      <td class="fee-client-cell">${escapeHtml(tenantNameForOrgId(row.orgId) || (String(row.orgId || "").trim() ? portalCompanyLabel(row.orgId) : "—"))}</td>
      <td>${text(row.payer)}</td><td>${text(row.state)}</td><td>${text(row.procedure)}</td>
      <td>${text(row.m1)}</td><td>${text(row.m2)}</td><td>${text(row.place)}</td>
      <td class="fee-rate-cell">${rate}</td><td>${text(row.secondaryCalc)}</td>
      <td>${text(row.startDate)}</td><td>${text(row.endDate)}</td><td>${text(row.network)}</td>
      <td data-fee-col="approvalStatus"></td><td data-fee-col="actions"></td>
    </tr>`;
}

function feeField(index, field, value = "", type = "text", disabled = false) {
  // Defend the grid against stale junk values ("undefined"/"null" strings)
  // that older builds occasionally persisted into a row.
  const raw = (value == null || value === "undefined" || value === "null") ? "" : value;
  const numeric = Number(raw);
  const safe = field === "rate" && raw !== "" && Number.isFinite(numeric) && Math.abs(numeric * 100 - Math.round(numeric * 100)) < 0.0000001 ? numeric.toFixed(2) : raw;
  const accessibleLabel = `${feeScheduleFieldLabel(field)} in fee schedule row ${Number(index) + 1}`;
  return `<td><input class="sheet-input" type="${type}" value="${escapeHtml(safe)}" data-fee-index="${index}" data-fee-field="${field}" aria-label="${escapeHtml(accessibleLabel)}"${disabled ? " disabled" : ""}></td>`;
}

function feeApprovalField(index, value = "draft", disabled = false) {
  const normalized = String(value || "draft").toLowerCase() === "approved" ? "approved" : "draft";
  return `
    <td>
      <select class="sheet-input fee-approval-select" data-fee-index="${index}" data-fee-field="approvalStatus" aria-label="Approval status in fee schedule row ${Number(index) + 1}"${disabled ? " disabled" : ""}>
        <option value="draft"${normalized === "draft" ? " selected" : ""}>Draft</option>
        <option value="approved"${normalized === "approved" ? " selected" : ""}>Approved</option>
      </select>
    </td>
  `;
}

function feeSecondaryCalcField(index, value = "", disabled = false) {
  const text = String(value || "");
  const normalized = /pr\s*amount/i.test(text) ? "PR Amount" : /allowed/i.test(text) ? "Allowed Amount" : "";
  return `
    <td>
      <select class="sheet-input fee-secondary-calc-select" data-fee-index="${index}" data-fee-field="secondaryCalc" aria-label="Secondary expected calculation rule in fee schedule row ${Number(index) + 1}"${disabled ? " disabled" : ""}>
        <option value=""${normalized === "" ? " selected" : ""}>—</option>
        <option value="Allowed Amount"${normalized === "Allowed Amount" ? " selected" : ""}>Allowed Amount</option>
        <option value="PR Amount"${normalized === "PR Amount" ? " selected" : ""}>PR Amount</option>
      </select>
    </td>
  `;
}

function followUpRulesHostedMode() {
  return Boolean(workflowApiBase() && typeof fetch === "function");
}

function openFollowUpRulesInQueryMap() {
  if (!canAccessView("queryMap") || !queryMapRulebookSessionSnapshot().canViewQueryMap) {
    return denyAction(PERMISSIONS.VIEW_QUERY_MAP, "open Query Map from Follow-Up Rules");
  }
  queryMapActiveTab = "rules";
  state.view = "queryMap";
  saveState();
  render();
}

function renderHostedFollowUpRulebook() {
  const host = els.followUpRulebookRoot;
  if (!host) return;
  const session = queryMapRulebookSessionSnapshot();
  if (!session.canViewQueryMap) {
    queryMapRulebookState = { status: "denied", response: null, error: "Missing Rules & Help permission.", requestKey: queryMapRulebookAccessScopedRequestKey({ base: workflowApiBase() }) };
    host.innerHTML = `<div class="panel authz-empty"><p class="authz-empty-title">The server-owned rulebook is unavailable for this role.</p><p class="authz-empty-note">Follow-Up Rules cannot fetch or substitute Query Map authority without the ${escapeHtml(PERMISSIONS.VIEW_QUERY_MAP)} permission.</p></div>`;
    return;
  }
  const currentBase = workflowApiBase();
  const currentRequestKey = queryMapRulebookAccessScopedRequestKey({ base: currentBase });
  if (queryMapRulebookState.requestKey && queryMapRulebookState.requestKey !== currentRequestKey) {
    queryMapRulebookState = { status: "idle", response: null, error: "", requestKey: "" };
  }
  requestQueryMapRulebook();
  if (queryMapRulebookState.status !== "ok" || !queryMapRulebookState.response) {
    const message = queryMapRulebookState.status === "pending"
      ? "Loading the server-owned follow-up rulebook…"
      : queryMapRulebookState.status === "error"
        ? `Follow-up rulebook unavailable: ${queryMapRulebookState.error}`
        : "Rules & Help is temporarily unavailable.";
    host.innerHTML = `<div class="panel authz-empty"><p class="authz-empty-title">${escapeHtml(message)}</p><p class="authz-empty-note">No browser-saved action matrix is substituted for hosted operating rules.</p>${queryMapRulebookState.status === "error" ? `<button type="button" class="btn btn--secondary" data-follow-up-rulebook-retry>Try again</button>` : ""}</div>`;
    host.querySelector("[data-follow-up-rulebook-retry]")?.addEventListener("click", () => {
      queryMapRulebookState = { status: "idle", response: null, error: "", requestKey: "" };
      renderFollowUpRules();
    });
    return;
  }
  const book = queryMapRulebookState.response;
  const canOpenQueryMap = canAccessView("queryMap") && session.canViewQueryMap;
  host.innerHTML = `
    <section class="panel query-map-source-note"><strong>Read-only server authority</strong><p>This setup page shows the same effective rulebook as Query Map and does not edit the hosted sweep. Legacy browser action metadata may still populate action pickers and due-date conveniences elsewhere; it is not presented here as changing server execution.</p>${canOpenQueryMap ? `<button type="button" class="btn btn--secondary" data-open-follow-up-query-map>Open full Query Map</button>` : ""}</section>
    ${queryMapRuntimeBanner(book)}
    <section class="query-map-panel" aria-label="Full follow-up action catalog"><h2>Full action catalog</h2>${followUpActionCatalogMarkup(book.tabs?.rules?.actions || [])}</section>
    <section class="query-map-panel" aria-label="Effective automatic follow-up rules">${queryMapRulesMarkup(book.tabs?.rules || {})}</section>
    <section class="query-map-panel" aria-label="Payer guide readiness"><h2>Payer Guide</h2>${queryMapPayerGuideMarkup(book.tabs?.payerGuide || {})}</section>
    <section class="query-map-panel" aria-label="Follow-up rule source status"><h2>Source status</h2>${queryMapSourcesMarkup(book.tabs?.dataSources || [])}</section>`;
  host.querySelector("[data-open-follow-up-query-map]")?.addEventListener("click", openFollowUpRulesInQueryMap);
}

function renderFollowUpRules() {
  if (!els.followUpRuleRows) return;
  const hosted = followUpRulesHostedMode();
  if (els.addFollowUpRuleBtn) els.addFollowUpRuleBtn.hidden = hosted || !hasPermission(PERMISSIONS.MANAGE_FOLLOW_UP_RULES);
  if (els.followUpLocalEditor) els.followUpLocalEditor.hidden = hosted;
  if (els.followUpRulebookRoot) els.followUpRulebookRoot.hidden = false;
  if (hosted) {
    renderHostedFollowUpRulebook();
    return;
  }
  if (els.followUpRulebookRoot) {
    els.followUpRulebookRoot.innerHTML = `<section class="panel query-map-honest-empty"><span class="query-map-eyebrow">Local/demo only</span><h3>Non-authoritative action matrix</h3><p>Edits below stay in this browser. They can support local action pickers and due-date conveniences, but do not change the hosted sweep, Query Map rulebook, or another user's rules.</p></section>`;
  }
  const rules = getActionRules();
  const timedRules = rules.filter((rule) => Number.isFinite(rule.offset)).length;
  const removeIfPaidRules = rules.filter((rule) => rule.removeIfPaid).length;
  if (els.followUpRuleStats) {
    els.followUpRuleStats.innerHTML = renderSetupSummaryCards([
      ["Actions", rules.length, "Workbook action names"],
      ["Timed Rules", timedRules, "Auto due-date offset"],
      ["Remove If Paid", removeIfPaidRules, "Auto-clears paid work"],
      ["Manual Review", rules.length - removeIfPaidRules, "Keeps denied/escalated items"]
    ]);
  }
  els.followUpRuleRows.innerHTML = rules.map((rule, index) => `
    <tr>
      <td><input class="sheet-input" type="text" value="${escapeHtml(rule.action)}" data-rule-index="${index}" data-rule-field="action"></td>
      <td><input class="sheet-input" type="number" value="${Number.isFinite(rule.offset) ? rule.offset : ""}" data-rule-index="${index}" data-rule-field="offset"></td>
      <td>
        <select class="sheet-input" data-rule-index="${index}" data-rule-field="removeIfPaid">
          <option value="true" ${rule.removeIfPaid ? "selected" : ""}>Yes</option>
          <option value="false" ${!rule.removeIfPaid ? "selected" : ""}>No</option>
        </select>
      </td>
      <td><button class="small-button" type="button" data-delete-rule="${index}">Delete</button></td>
    </tr>
  `).join("") || '<tr><td colspan="4">No follow-up rules configured yet.</td></tr>';

  Array.from(document.querySelectorAll("[data-rule-field]")).forEach((input) => {
    input.addEventListener("change", () => updateFollowUpRule(Number(input.dataset.ruleIndex), input.dataset.ruleField, input.value));
  });
  Array.from(document.querySelectorAll("[data-delete-rule]")).forEach((button) => {
    button.addEventListener("click", () => deleteFollowUpRule(Number(button.dataset.deleteRule)));
  });
}

function queryMapSqlEndpoint(capabilities) {
  const endpoint = String(capabilities?.sqlEndpoint || "").trim();
  return endpoint === "/api/query-map/live-sql" ? endpoint : "";
}

function resetLiveQueryMapState() {
  liveQueryMapRequestKey = "";
  liveQueryMapState = { status: "idle", response: null, error: "", requestKey: "", accessKey: "", endpoint: "" };
}

function queryMapRulebookSessionSnapshot() {
  const auth = (typeof state !== "undefined" && state && state.authUserScope) || storedPortalAuthUserSnapshot() || {};
  const role = canonicalRole(auth.role || state?.role || "");
  const profile = roleProfiles[role] || { permissions: [] };
  return {
    token: String(workflowApiToken() || trackerApiToken() || ""),
    role,
    orgIds: normalizeUserScopeList(auth.orgIds),
    clientNames: normalizeUserScopeList(auth.clientNames),
    assignedOnly: Boolean(auth.assignedOnly),
    assignedName: String(auth.assignedName || "").trim(),
    canViewQueryMap: Array.isArray(profile.permissions) && profile.permissions.includes(PERMISSIONS.VIEW_QUERY_MAP)
  };
}

function queryMapRulebookAccessScopedRequestKey(parts = {}) {
  return JSON.stringify({ ...parts, session: queryMapRulebookSessionSnapshot() });
}

function requestLiveQueryMap(endpoint, accessKey) {
  if (liveQueryMapState.status !== "idle") return;
  const validatedEndpoint = queryMapSqlEndpoint({ sqlEndpoint: endpoint });
  if (!validatedEndpoint) {
    liveQueryMapState = { status: "error", response: null, error: "The requested technical details are unavailable.", requestKey: "", accessKey, endpoint: "" };
    return;
  }
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") return;
  const token = workflowApiToken() || trackerApiToken();
  const requestParts = { base, endpoint: validatedEndpoint };
  const requestKey = queryMapRulebookAccessScopedRequestKey(requestParts);
  const read = beginSystemPortalDataRead("query-map-sql");
  const requestStillCurrent = () => (
    read.current() &&
    liveQueryMapRequestKey === requestKey
    && queryMapRulebookAccessScopedRequestKey(requestParts) === requestKey
  );
  liveQueryMapRequestKey = requestKey;
  const previousResponse = liveQueryMapState.response;
  liveQueryMapState = { status: "pending", response: null, error: "", requestKey, accessKey, endpoint: validatedEndpoint };
  read.fetch(`${base}${validatedEndpoint}`, {
    method: "GET",
    cache: "no-store",
    headers: {
      "accept": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        liveQueryMapState = { status: "error", response: previousResponse, error: "Your session expired. Sign in again — this page will still be here.", requestKey, accessKey, endpoint: validatedEndpoint };
        if (els.queryMapRoot) renderQueryMap();
        return;
      }
      if (!response.ok || body.error) throw new Error(body?.error?.message || body.message || `HTTP ${response.status}`);
      liveQueryMapState = { status: "ok", response: body, error: "", requestKey, accessKey, endpoint: validatedEndpoint };
      if (els.queryMapRoot) renderQueryMap();
    })
    .catch((error) => {
      if (!requestStillCurrent()) return;
      liveQueryMapState = { status: "error", response: null, error: error.message || "Technical details could not be loaded.", requestKey, accessKey, endpoint: validatedEndpoint };
      if (els.queryMapRoot) renderQueryMap();
    }).finally(read.finish);
}

// The operational Query Map is server-owned. SQL is fetched only after an
// authorized user opens the SQL tab; no hand-maintained fallback remains.
function renderQueryMap() {
  ensureSystemPortalDataScope();
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("queryMap")) return;
  const host = els.queryMapRoot;
  if (!host) return;
  if (!queryMapRulebookSessionSnapshot().canViewQueryMap || !canAccessView("queryMap")) {
    queryMapRulebookState = { status: "denied", response: null, error: "Missing Rules & Help permission.", requestKey: queryMapRulebookAccessScopedRequestKey({ base: workflowApiBase() }) };
    host.innerHTML = `<div class="panel authz-empty"><p class="authz-empty-title">Rules & Help is unavailable for this role.</p><p class="authz-empty-note">This account does not have access to staff rules and guidance.</p></div>`;
    return;
  }
  const currentBase = workflowApiBase();
  const currentRequestKey = queryMapRulebookAccessScopedRequestKey({ base: currentBase });
  if (queryMapRulebookState.requestKey && queryMapRulebookState.requestKey !== currentRequestKey) {
    queryMapRulebookState = { status: "idle", response: null, error: "", requestKey: "" };
  }
  if (liveQueryMapState.accessKey && liveQueryMapState.accessKey !== currentRequestKey) resetLiveQueryMapState();
  // Rules & Help has its own authenticated endpoint and must load even when the
  // tracker data lane is not enabled (for example a direct help deep link).
  requestQueryMapRulebook();
  if (queryMapRulebookState.status !== "ok" || !queryMapRulebookState.response) {
    if (liveQueryMapState.status !== "idle") resetLiveQueryMapState();
    const message = queryMapRulebookState.status === "pending"
      ? "Loading Rules & Help…"
      : queryMapRulebookState.status === "error"
        ? `Rulebook unavailable: ${queryMapRulebookState.error}`
        : "Rules & Help is temporarily unavailable.";
    host.innerHTML = `<div class="panel authz-empty"><p class="authz-empty-title">${escapeHtml(message)}</p><p class="authz-empty-note">Please try again shortly.</p>${queryMapRulebookState.status === "error" ? `<button type="button" class="btn btn--secondary" data-query-map-retry>Try again</button>` : ""}</div>`;
    host.querySelector("[data-query-map-retry]")?.addEventListener("click", () => {
      queryMapRulebookState = { status: "idle", response: null, error: "", requestKey: "" };
      renderQueryMap();
    });
    return;
  }
  const book = queryMapRulebookState.response;
  const capabilities = book.capabilities || {};
  const sqlEndpoint = queryMapSqlEndpoint(capabilities);
  if ((!capabilities.sql || !sqlEndpoint) && liveQueryMapState.status !== "idle") resetLiveQueryMapState();
  const tabs = [
    ["overview", "Overview & FAQ", true],
    ["rules", "Automatic rules", true],
    ["actions", "Action guide", true],
    ["statements", "Patient statements", Boolean(capabilities.statementEligibility)],
    ["moneyDefinitions", "Payment definitions", Boolean(capabilities.moneyDefinitions)],
    ["accessGuide", "Roles & access", true]
  ].filter((entry) => entry[2]);
  if (!tabs.some(([key]) => key === queryMapActiveTab)) queryMapActiveTab = "rules";
  if (queryMapActiveTab === "sql") requestLiveQueryMap(sqlEndpoint, currentRequestKey);
  host.innerHTML = `
    ${queryMapHelpHeader(book)}
    <div class="query-map-tabs" role="tablist" aria-label="Workflow Guide sections">
      ${tabs.map(([key, label]) => `<button type="button" role="tab" id="queryMapTab-${key}" aria-controls="queryMapPanel" aria-selected="${queryMapActiveTab === key ? "true" : "false"}" tabindex="${queryMapActiveTab === key ? "0" : "-1"}" data-query-map-tab="${key}">${escapeHtml(label)}</button>`).join("")}
    </div>
    <section id="queryMapPanel" class="query-map-panel" role="tabpanel" aria-labelledby="queryMapTab-${escapeHtml(queryMapActiveTab)}">
      ${queryMapTabMarkup(book, queryMapActiveTab)}
    </section>`;
  bindQueryMapTabs(host);
}

function queryMapRuntimeBanner(book) {
  const runtime = book.runtime || {};
  const rules = Array.isArray(book.tabs?.rules?.automatic) ? book.tabs.rules.automatic : [];
  const checkupThreshold = rules.find((rule) => rule.id === "claim-status-checkup")?.semantics?.effectiveLegacy?.threshold;
  const checkupDays = Number(checkupThreshold?.days);
  const staleDays = Number(rules.find((rule) => rule.id === "stale-insurance-follow-up")?.semantics?.effectiveLegacy?.threshold?.days);
  const timing = [
    checkupThreshold?.kind === "payer-average-plus" ? `payer average + ${Number(checkupThreshold.bufferDays)}-day checkup (${Number(checkupThreshold.fallbackDays)} days without payer history)`
      : Number.isFinite(checkupDays) ? `${checkupDays}-day checkup` : "checkup threshold unavailable",
    Number.isFinite(staleDays) ? `${staleDays}-day stale follow-up` : "stale threshold unavailable"
  ].join(" / ");
  const availableModes = Array.isArray(runtime.availableRuleModes) ? runtime.availableRuleModes : [];
  const ctMode = availableModes.find((mode) => String(mode.id || "").includes("ct-payer-cycle"));
  const ctState = ctMode?.connected || runtime.payerCycleConnected ? "connected" : "available in code but not connected";
  const applyEnabled = runtime.applyPolicyEnabled ?? runtime.applyConfigured;
  const apply = applyEnabled ? "Apply is configured" : "Apply is off";
  return `<section class="panel query-map-runtime">
    <div><span class="query-map-eyebrow">Effective now · ${escapeHtml(queryMapWords(runtime.effectiveRuleMode || "mode unavailable"))}</span><strong>${escapeHtml(timing)}</strong></div>
    <p>CT payer-cycle v2 is <strong>${escapeHtml(ctState)}</strong> to the live sweep. ${escapeHtml(apply)}. A rule may be planned, allowed by write policy, and actually writable—those are three different states shown below.</p>
  </section>`;
}

function queryMapTabMarkup(book, tab) {
  const tabs = book.tabs || {};
  if (tab === "overview") return queryMapOverviewMarkup(book);
  if (tab === "actions") return followUpActionCatalogMarkup(tabs.rules?.actions || []);
  if (tab === "statements") return queryMapStatementEligibilityMarkup(tabs.statementEligibility);
  if (tab === "moneyDefinitions") return queryMapMoneyMarkup(tabs.moneyDefinitions);
  if (tab === "accessGuide") return queryMapAccessGuideMarkup(tabs.accessGuide || {});
  return queryMapRulesMarkup(tabs.rules || {});
}

function queryMapStatementEligibilityMarkup(policy) {
  if (!policy) return '<div class="authz-empty"><p class="authz-empty-title">Patient statement rules are unavailable.</p></div>';
  const responsibility = policy.patientResponsibility || {};
  const print = policy.printEligibility || {};
  const safety = policy.safety || {};
  const report = policy.reportEligibility || {};
  const rules = (Array.isArray(print.rules) ? print.rules : []).filter((rule) => rulesHelpMatches(rule));
  const reportRules = (Array.isArray(report.rules) ? report.rules : []).filter((rule) => rulesHelpMatches(rule));
  const responsibilityRule = rulesHelpMatches(responsibility.rule, ...(responsibility.qualifyingReasons || [])) ? responsibility.rule : "";
  const safetyRule = rulesHelpMatches(safety.explanation) ? safety.explanation : "";
  if (!responsibilityRule && !reportRules.length && !rules.length && !safetyRule) return '<div class="panel rules-help-empty"><strong>No matching patient-responsibility guidance</strong><p>Try “secondary”, “balance”, “PR 1”, “24 months”, “7 days”, or “ready to print”.</p></div>';
  return `<div class="query-map-rule-list rules-help-statement-list">
    ${responsibilityRule ? `<article class="query-map-rule-card"><header><div><span class="query-map-eyebrow">Patient responsibility</span><h3>When the patient can owe money</h3></div></header><p>${escapeHtml(responsibilityRule)}</p></article>` : ""}
    ${reportRules.length ? `<article class="query-map-rule-card"><header><div><span class="query-map-eyebrow">Patient Responsibility report</span><h3>What the report includes</h3></div><span class="query-map-state is-on">Current rule</span></header><ul>${reportRules.map((rule) => `<li>${escapeHtml(rule)}</li>`).join("")}</ul><p><strong>Amount:</strong> ${escapeHtml(report.amountSource || "signed PR amount")}. <strong>Balance:</strong> ${escapeHtml(report.balanceUse || "eligibility gate only")}.</p></article>` : ""}
    ${rules.map((rule, index) => `<article class="query-map-rule-card"><header><div><span class="query-map-eyebrow">Print rule ${index + 1}</span><h3>${index === 0 ? "No secondary on the claim" : index === 1 ? "Wait through the grace period" : index === 2 ? "No active follow-up action" : index === 3 ? "Positive, dated PR 1/2/3 only" : "Complete current list"}</h3></div></header><p>${escapeHtml(rule)}</p></article>`).join("")}
    ${safetyRule ? `<article class="query-map-rule-card"><header><div><span class="query-map-eyebrow">Print-time safety</span><h3>The portal checks again before saving</h3></div></header><p>${escapeHtml(safetyRule)}</p></article>` : ""}
  </div>`;
}

function queryMapHelpHeader(book) {
  const automatic = Array.isArray(book.tabs?.rules?.automatic) ? book.tabs.rules.automatic : [];
  const active = automatic.filter((rule) => rule.writable).length;
  return `<section class="panel rules-help-hero"><div><span class="query-map-eyebrow">Rules &amp; Help</span><h2>How follow-up and patient billing work</h2><p>Search statement eligibility, the action guide, automatic changes, roles and access, and common questions. This page updates from the same rules the portal uses.</p></div><label class="rules-help-search"><span>Search rules and help</span><input type="search" value="${escapeHtml(queryMapSearchText)}" placeholder="Try ‘secondary’, ‘ready to print’, or ‘write-off’" data-rules-help-search></label><small>${active ? `${active} automatic rule${active === 1 ? " is" : "s are"} currently active` : "Automatic writes are paused"}</small></section>`;
}

function rulesHelpMatches(...values) {
  const query = String(queryMapSearchText || "").trim().toLowerCase();
  return !query || values.some((value) => String(value || "").toLowerCase().includes(query));
}

function queryMapOverviewMarkup(book) {
  const automatic = Array.isArray(book.tabs?.rules?.automatic) ? book.tabs.rules.automatic : [];
  const actions = Array.isArray(book.tabs?.rules?.actions) ? book.tabs.rules.actions : [];
  const paidTolerance = queryMapMoneyValue(book.tabs?.moneyDefinitions?.smallBalancePaidToleranceDollars);
  const underpaymentRule = automatic.find((rule) => rule.id === "persistent-underpayment");
  const underpaymentFloor = queryMapEffectiveSemantics(underpaymentRule || {}).threshold?.dollars;
  const underpaymentCandidate = Number.isFinite(Number(underpaymentFloor)) ? queryMapMoneyValue(Number(underpaymentFloor) + 0.01) : "— Unknown";
  const faq = [
    ["When does the portal add a follow-up action?", "Only when every condition under Automatic rules is met and that rule is active. An existing action is never replaced."],
    ["What happens after a small remaining balance?", `The ${paidTolerance} tolerance affects Paid classification only after an insurance payment has posted. A remaining insurance balance at or below that amount can then be treated as paid.`],
    ["When is a persistent underpayment a candidate?", `The threshold is exclusively greater than ${queryMapMoneyValue(underpaymentFloor)}. Automatic additions also require readable financial inputs, no existing action or pending proposal, and stable evidence from two different published data epochs. The rule's current activation appears under Automatic rules.`],
    ["Does a partial payment remove Insurance Follow-Up?", "No. Insurance Follow-Up stays on an underpaid service and clears only when the service is paid or overpaid."],
    ["When does Write-Off disappear?", "Only after insurance money has posted and the remaining write-off amount is effectively zero."],
    ["Why did an action stay on a paid service?", "Some actions intentionally stay after payment. Check the Action guide: actions marked No under Remove when paid require a person to change them."],
    ["Can an automatic rule overwrite a biller’s action?", "Only one: Submit To Secondary becomes Submitted To Secondary after 14 days without a change, so the line comes due if the secondary payer does not pay. Every other automatic addition requires the service to have no current follow-up action."],
    ["What happens to a secondary placeholder claim?", "While a claim is classified as a secondary placeholder (the primary is billed, the secondary is waiting on the client's secondary authorization), each still-owed service with no action gets Client Action Pending-Authorization and a note naming the secondary payer. When the placeholder ends, the portal takes off only the action it added: it becomes Secondary Claim in Process once the claim is submitted to secondary, and is removed otherwise. An action a person set is never replaced."],
    ["What does Missing Fee Schedule mean?", "The service has no usable nonzero rate. The marker clears by itself after the rate is fixed."],
    ["When does a removed automatic action come back?", "If a person removes an automatic action and the service is still open, the portal can add it again 7 days after the latest recorded touch or clear."],
    ["Where do due dates come from?", "Each action has a standard number of business days. Actions marked Manual do not receive an automatic due date."],
    ["When is patient responsibility zero?", "Patient responsibility is $0 whenever any secondary insurance exists anywhere on the claim. The secondary must be handled before the patient can be billed."],
    ["When is a patient statement ready to print?", "All print rules must pass: no secondary on the claim, at least 7 days since the latest contributing PR 1/2/3 adjustment, no active follow-up action on the service line, a positive dated PR 1/2/3 balance, and a complete current statement list."]
  ].filter((row) => rulesHelpMatches(...row));
  const matchingRules = automatic.filter((rule) => { const effective = queryMapEffectiveSemantics(rule); return rulesHelpMatches(rule.id, rule.action, ...(effective.conditions || []), ...(effective.exclusions || [])); });
  const matchingActions = actions.filter((row) => rulesHelpMatches(row.action, row.dueDays, row.removeIfPaid ? "remove paid clears" : "keep paid stays"));
  if (!faq.length && !matchingRules.length && !matchingActions.length) return '<div class="panel rules-help-empty"><strong>No matching guidance</strong><p>Try a different action name, dollar amount, or timing phrase.</p></div>';
  const searching = Boolean(String(queryMapSearchText || "").trim());
  return `<div class="rules-help-overview"><section><h2>At a glance</h2><div class="rules-help-summary"><article><strong>${matchingRules.length}</strong><span>${searching ? "matching" : "configured/planned"} automatic rules</span></article><article><strong>${matchingActions.length}</strong><span>${searching ? "matching" : "configured"} actions</span></article><article><strong>${escapeHtml(paidTolerance)}</strong><span>Paid classification tolerance after a payment</span></article><article><strong>${escapeHtml(underpaymentCandidate)}+</strong><span>underpayment candidate guide · see Automatic rules for activation</span></article></div></section><section><h2>Frequently asked questions</h2><div class="rules-help-faq">${faq.map(([question, answer]) => `<details><summary>${escapeHtml(question)}</summary><p>${escapeHtml(answer)}</p></details>`).join("") || '<p>No questions match this search.</p>'}</div></section></div>`;
}

function queryMapWords(value) {
  return String(value || "").replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function queryMapThreshold(rule) {
  const effective = rule.semantics?.effectiveLegacy || rule.semantics?.effectivePortal || rule;
  const threshold = effective.threshold || rule.effectiveThreshold || rule.threshold || null;
  if (!threshold) return "No numeric threshold stated.";
  if (threshold.kind === "fixed-days") return `After ${Number(threshold.days)} days.`;
  if (threshold.kind === "payer-average-plus") return `At the historical payer average + ${Number(threshold.bufferDays)} days (at least ${Number(threshold.minimumSamples)} paid samples)${threshold.fallbackDays ? `; without reliable payer history, at ${Number(threshold.fallbackDays)} bill-aging days` : ""}.`;
  if (threshold.kind === "first-observed-secondary-submission-days") return `After ${Number(threshold.days)} days from the first recorded observation of submission to secondary.`;
  if (threshold.kind === "exclusive-dollar-floor") return `The remaining balance must be greater than ${queryMapMoneyValue(threshold.dollars)}.`;
  if (threshold.kind === "none") return "As soon as the listed conditions are met.";
  return "When every listed condition is met.";
}

function queryMapEffectiveSemantics(rule) {
  return rule.semantics?.effectiveLegacy || rule.semantics?.effectivePortal || rule;
}

function followUpActionCatalogMarkup(actions = []) {
  const rows = (Array.isArray(actions) ? actions : []).filter((row) => rulesHelpMatches(row.action, row.dueDays, row.removeIfPaid ? "remove paid clears" : "keep paid stays"));
  if (!rows.length) return `<div class="authz-empty"><p class="authz-empty-title">No actions match this search.</p></div>`;
  return `<div class="table-wrap setup-table-wrap"><table class="setup-table follow-up-action-catalog"><thead><tr><th>Action</th><th>Follow up in</th><th>Remove when paid?</th><th>Who can add it?</th><th>Automatic now?</th></tr></thead><tbody>${rows.map((row) => `<tr data-follow-up-catalog-action="${escapeHtml(row.action || "")}"><td><strong>${escapeHtml(row.action || "Unnamed action")}</strong></td><td>${Number.isFinite(Number(row.dueDays)) && row.dueDays !== null ? `${escapeHtml(String(row.dueDays))} business day${Number(row.dueDays) === 1 ? "" : "s"}` : "Manual"}</td><td>${row.removeIfPaid ? "Yes" : "No"}</td><td>${row.systemOnly ? "Portal only" : "Biller"}</td><td>${row.autoWriteEnabled ? "Eligible when active" : (row.autoWriteRetiredOn ? `No \u2014 retired ${escapeHtml(String(row.autoWriteRetiredOn))}` : "No")}</td></tr>`).join("")}</tbody></table></div>`;
}

function queryMapRulesMarkup(rules) {
  const automatic = (Array.isArray(rules.automatic) ? rules.automatic : []).filter((rule) => { const effective = queryMapEffectiveSemantics(rule); return rulesHelpMatches(rule.id, rule.action, ...(effective.conditions || []), ...(effective.exclusions || [])); });
  if (!automatic.length) return `<div class="authz-empty"><p class="authz-empty-title">No automatic rules match this search.</p></div>`;
  return `<div class="query-map-rule-list">${automatic.map((rule) => {
    const effective = queryMapEffectiveSemantics(rule);
    const when = (effective.conditions || []).map(queryMapWords).join(" · ") || "No conditions stated";
    const exclusions = (effective.exclusions || []).map(queryMapWords).join(" · ") || "No exclusions stated";
    const retired = rule.retired && typeof rule.retired === "object" ? rule.retired : null;
    const retiredDetail = retired
      ? [retired.why, retired.instead, retired.stillRemoves].map((part) => String(part || "")).filter(Boolean)
      : [];
    const next = retired
      ? String(retired.what || "This rule no longer makes changes automatically.")
      : rule.writable
      ? "This automatic change is active now."
      : rule.writePolicyEligible ? "This rule is available, but automatic changes are currently paused." : "This rule is shown for guidance and does not make changes automatically.";
    return `<article class="query-map-rule-card">
      <header><div><span class="query-map-eyebrow">Automatic rule</span><h3>${escapeHtml(rule.action || "Remove a completed follow-up")}</h3></div>${queryMapRuleStateMarkup(rule)}</header>
      <dl>
        <div><dt>What happens</dt><dd>${escapeHtml(retired ? `${rule.action} is no longer added automatically. The conditions below are kept for reference.` : rule.action ? `${rule.action} is added when every condition below is met.` : "A completed follow-up action is removed when its payment rule is met.")}</dd></div>
        <div><dt>When</dt><dd>${escapeHtml(queryMapThreshold(rule))}<small>${escapeHtml(when)}</small>${effective.clock ? `<small>${escapeHtml(effective.clock)}</small>` : ""}${effective.cooldownDays ? `<small>Wait ${Number(effective.cooldownDays)} days after the latest recorded touch or clear before adding another action.</small>` : ""}</dd></div>
        <div><dt>When it does not happen</dt><dd>${escapeHtml(exclusions)}</dd></div>
        <div><dt>Status</dt><dd>${escapeHtml(next)}${retiredDetail.map((part) => `<small>${escapeHtml(part)}</small>`).join("")}</dd></div>
      </dl>
    </article>`;
  }).join("")}</div>`;
}

function queryMapModeDivergenceMarkup(rule) {
  const divergences = Array.isArray(rule.divergences) ? rule.divergences : [];
  if (!divergences.length) return "";
  return `<div class="query-map-mode-difference"><dt>Mode difference</dt><dd>${divergences.map((difference) => {
    const effective = difference.effectiveLegacy ?? difference.effectivePortal ?? "not stated";
    return `<span><strong>${escapeHtml(queryMapWords(difference.key))}:</strong> effective ${escapeHtml(queryMapWords(effective))}; CT ${escapeHtml(queryMapWords(difference.ct ?? "not stated"))}</span>`;
  }).join("")}</dd></div>`;
}

function queryMapRuleStateMarkup(rule) {
  const retiredOn = rule && rule.retired && typeof rule.retired === "object" ? String(rule.retired.since || "") : "";
  // A RETIREMENT IS NOT A YES/NO QUESTION, and rendering it as one inverts it.
  // The generic renderer below appends the state's own answer, so a chip labelled
  // "Retired 2026-09-17" whose state is false printed "Retired 2026-09-17: No" 
  // which on the page billers are sent to reads as "this was NOT retired", the
  // opposite of the fact. A retired rule therefore gets plain statements with no
  // answer appended, and says the operative thing in words rather than leaving it
  // to a "Yes"/"No" the reader has to attach to the right noun.
  if (retiredOn) {
    const retiredStates = [`Retired ${retiredOn}`, "Not added automatically"];
    return `<div class="query-map-state-list" aria-label="Rule execution status">${retiredStates.map((label) => `<span class="query-map-state is-off">${escapeHtml(label)}</span>`).join("")}</div>`;
  }
  const states = [
    ["Rule available", Boolean(rule.planned)],
    ["Automatic now", Boolean(rule.writable)]
  ];
  return `<div class="query-map-state-list" aria-label="Rule execution status">${states.map(([label, active]) => `<span class="query-map-state ${active ? "is-on" : "is-off"}">${escapeHtml(label)}: ${active ? "Yes" : "No"}</span>`).join("")}</div>`;
}

function queryMapAccessGuideMarkup(guide) {
  const accessGuide = guide && typeof guide === "object" ? guide : {};
  const roles = Array.isArray(accessGuide.roles) ? accessGuide.roles.filter((role) => role && typeof role === "object") : [];
  const features = Array.isArray(accessGuide.features) ? accessGuide.features.filter((feature) => feature && typeof feature === "object") : [];
  const assignmentRules = Array.isArray(accessGuide.assignmentRules) ? accessGuide.assignmentRules.filter((rule) => rule && typeof rule === "object") : [];
  const notes = Array.isArray(accessGuide.notes) ? accessGuide.notes : [];
  const searching = Boolean(String(queryMapSearchText || "").trim());
  const listText = (value, emptyText = "Not stated") => Array.isArray(value) ? value.map((item) => String(item || "")).filter(Boolean).join(", ") || emptyText : String(value || emptyText);
  const noteText = (note) => typeof note === "string" ? note : String(note?.text || note?.note || "");
  const matchingRoles = roles.filter((role) => rulesHelpMatches(role.key, role.label, role.purpose, role.scope, listText(role.assignedBy)));
  const matchingFeatures = features.filter((feature) => {
    const access = feature.access && typeof feature.access === "object" && !Array.isArray(feature.access) ? feature.access : {};
    const accessTerms = Object.entries(access).flatMap(([roleKey, value]) => [roleKey, value]);
    const roleTerms = roles.flatMap((role) => [role.key, role.label]);
    return rulesHelpMatches(feature.key, feature.label, feature.description, ...accessTerms, ...roleTerms);
  });
  const matchingAssignments = assignmentRules.filter((rule) => rulesHelpMatches(rule.managerRole, rule.managerLabel, listText(rule.canAssign, "None"), rule.scope));
  const matchingNotes = notes.map(noteText).filter((note) => note && rulesHelpMatches(note));
  const hasGuideContent = roles.length || features.length || assignmentRules.length || notes.length;
  const hasMatches = matchingRoles.length || matchingFeatures.length || matchingAssignments.length || matchingNotes.length;
  const noteMarkup = `<section class="panel query-map-source-note"><strong>Standard role permissions</strong><p>These are the portal’s standard permissions. Company assignments still limit what every role can see, except Super Admin, which can work across all companies.</p>${matchingNotes.length ? `<ul>${matchingNotes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>` : ""}</section>`;
  if (searching && !hasMatches) return `${noteMarkup}<div class="panel rules-help-empty"><strong>No matching role or access guidance</strong><p>Try a role name, feature, permission level, or assignment rule.</p></div>`;
  if (!hasGuideContent) return `${noteMarkup}<div class="panel query-map-honest-empty"><span class="query-map-eyebrow">Unavailable</span><h3>Role and access guidance is not available.</h3><p>Please try again after the portal’s role guide has been connected.</p></div>`;
  const roleCards = matchingRoles.length ? `<section aria-label="Portal roles"><h2>Portal roles</h2><div class="query-map-rule-list">${matchingRoles.map((role) => `<article class="query-map-rule-card"><header><div><span class="query-map-eyebrow">Role</span><h3>${escapeHtml(role.label || role.key || "Unnamed role")}</h3></div><span class="query-map-state is-on">Standard role</span></header><dl><div><dt>Purpose</dt><dd>${escapeHtml(role.purpose || "Not stated")}</dd></div><div><dt>Company scope</dt><dd>${escapeHtml(role.scope || "Not stated")}</dd></div><div><dt>Who can assign it</dt><dd>${escapeHtml(listText(role.assignedBy))}</dd></div></dl></article>`).join("")}</div></section>` : "";
  const matrix = matchingFeatures.length && roles.length ? `<section aria-label="Role permission matrix"><h2>What each role can use</h2><div class="table-wrap setup-table-wrap role-access-matrix-wrap"><table class="setup-table role-access-matrix"><thead><tr><th>Feature</th>${roles.map((role) => `<th>${escapeHtml(role.label || role.key || "Unnamed role")}</th>`).join("")}</tr></thead><tbody>${matchingFeatures.map((feature) => `<tr><td data-label="${escapeHtml("Feature")}"><strong>${escapeHtml(feature.label || feature.key || "Unnamed feature")}</strong>${feature.description ? `<small>${escapeHtml(feature.description)}</small>` : ""}</td>${roles.map((role) => { const access = feature.access && typeof feature.access === "object" && !Array.isArray(feature.access) ? feature.access : {}; const value = Object.prototype.hasOwnProperty.call(access, role.key) ? String(access[role.key] ?? "Not stated") : "Not stated"; const unavailable = /^(no access|no changes|none|denied|not allowed|not stated)$/i.test(value.trim()); return `<td data-label="${escapeHtml(role.label || role.key || "Unnamed role")}"><span class="query-map-state ${unavailable ? "is-off" : "is-on"}">${escapeHtml(value)}</span></td>`; }).join("")}</tr>`).join("")}</tbody></table></div></section>` : "";
  const assignmentCards = matchingAssignments.length ? `<section aria-label="Role assignment rules"><h2>Who can assign roles</h2><div class="query-map-rule-list">${matchingAssignments.map((rule) => { const canAssign = listText(rule.canAssign, "None"); const canManage = Array.isArray(rule.canAssign) ? rule.canAssign.some((item) => String(item || "").trim()) : Boolean(String(rule.canAssign || "").trim()) && !/^none$/i.test(String(rule.canAssign).trim()); return `<article class="query-map-rule-card"><header><div><span class="query-map-eyebrow">Assignment rule</span><h3>${escapeHtml(rule.managerLabel || rule.managerRole || "Unnamed manager role")}</h3></div><span class="query-map-state ${canManage ? "is-on" : "is-off"}">${canManage ? "Can manage roles" : "Cannot assign roles"}</span></header><dl><div><dt>Can assign</dt><dd>${escapeHtml(canAssign)}</dd></div><div><dt>Assignment scope</dt><dd>${escapeHtml(rule.scope || "Not stated")}</dd></div></dl></article>`; }).join("")}</div></section>` : "";
  return `<div class="rules-help-overview">${noteMarkup}${roleCards}${matrix}${assignmentCards}</div>`;
}

function queryMapPayerGuideMarkup(guide) {
  const profiles = Array.isArray(guide.profiles) ? guide.profiles : [];
  const grouping = guide.method?.ctPayerCycleImplementationGrouping || guide.method?.currentExecutableGrouping || [];
  if (!profiles.length) return `<div class="panel query-map-honest-empty"><span class="query-map-eyebrow">Unavailable</span><h3>No prepared payer-cycle profiles are connected.</h3><p>The portal cannot give payer-specific timing or instructions yet. It will not invent rows from payer names or show portfolio guesses as payer guidance.</p><p>Current method requires at least ${escapeHtml(String(guide.method?.minimumSamples ?? "an unknown number of"))} clean paid lines grouped by ${escapeHtml(grouping.join(" + ") || "the executable payer key")}.</p><code>${escapeHtml(guide.reasonCode || "reason-not-provided")}</code></div>`;
  return `<div class="query-map-rule-list">${profiles.map((profile) => `<article class="query-map-rule-card"><h3>${escapeHtml(profile.label || "Unnamed payer")}</h3><p>${escapeHtml(profile.instruction || "Instructions unavailable")}</p></article>`).join("")}</div>`;
}

function queryMapMoneyValue(value, suffix = "") {
  if (value === null || value === undefined || value === "") return "— Unknown";
  const amount = Number(value);
  return Number.isFinite(amount) ? `${Formatters.moneyExact(amount)}${suffix}` : "— Unknown";
}

function queryMapAmountPredicatesMarkup(rule) {
  const effective = rule.semantics?.effectivePortal || rule.semantics?.effectiveLegacy || {};
  const ct = rule.semantics?.ctTracker || rule.semantics?.ctPayerCycleV2 || {};
  const groups = [
    ["Effective portal", effective.amountPredicates],
    ["CT reference · not connected", ct.amountPredicates]
  ].filter(([, predicates]) => Array.isArray(predicates) && predicates.length);
  if (!groups.length) return "";
  const field = (predicate, key) => Object.prototype.hasOwnProperty.call(predicate, key) ? String(predicate[key]) : "not supplied";
  return `<div class="query-map-amount-predicates"><dt>Amount predicates</dt><dd>${groups.map(([label, predicates]) => `<section><strong>${escapeHtml(label)}</strong>${predicates.map((predicate) => `<code>left=${escapeHtml(field(predicate, "left"))}; operator=${escapeHtml(field(predicate, "operator"))}; right=${escapeHtml(field(predicate, "right"))}; value=${escapeHtml(field(predicate, "value"))}</code>`).join("")}</section>`).join("")}</dd></div>`;
}

function queryMapMoneyMarkup(definitions) {
  if (!definitions) return `<div class="authz-empty"><p class="authz-empty-title">Money definitions are not available for this role.</p></div>`;
  return `<div class="query-map-money-grid">
    <article class="query-map-definition"><span>Small-balance paid tolerance</span><strong>${escapeHtml(queryMapMoneyValue(definitions.smallBalancePaidToleranceDollars))}</strong><p>Known threshold from the financial calculation engine.</p></article>
    ${(definitions.formulas || []).map((formula) => `<article class="query-map-definition"><span>${escapeHtml(queryMapWords(formula.key))}</span><code>${escapeHtml(formula.expression || "— Unknown")}</code></article>`).join("")}
    <article class="query-map-definition query-map-definition--wide"><span>Classification precedence</span><ol>${(definitions.finalizedMatchPrecedence || []).map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ol><small>${escapeHtml(definitions.sourceModule || "Source unavailable")}</small></article>
  </div>`;
}

function queryMapSourcesMarkup(sources) {
  return `<div class="query-map-source-list">${(Array.isArray(sources) ? sources : []).map((source) => `<article class="query-map-source-row"><div><strong>${escapeHtml(queryMapWords(source.key))}</strong><small>${escapeHtml(source.sourceModule || "No connected source module")}</small></div><span>${escapeHtml(queryMapWords(source.lane))}</span><span class="query-map-state ${source.status === "active" ? "is-on" : "is-off"}">${escapeHtml(queryMapWords(source.status))}</span></article>`).join("") || `<div class="authz-empty"><p class="authz-empty-title">No source lineage returned.</p></div>`}</div>`;
}

function queryMapSqlMarkup() {
  if (liveQueryMapState.status === "pending" || liveQueryMapState.status === "idle") return `<div class="authz-empty"><p class="authz-empty-title">Loading technical details…</p></div>`;
  if (liveQueryMapState.status === "error") return `<div class="authz-empty"><p class="authz-empty-title">Technical details unavailable.</p><button type="button" class="btn btn--secondary" data-query-sql-retry>Try again</button></div>`;
  const catalog = liveQueryMapState.response || {};
  const entries = Array.isArray(catalog.entries) ? catalog.entries : [];
  return `<div class="authz-empty"><p class="authz-empty-title">Technical details are managed by the portal administrator.</p></div>`;
}

function bindQueryMapTabs(host) {
  const buttons = Array.from(host.querySelectorAll("[data-query-map-tab]"));
  const activate = (button) => {
    queryMapActiveTab = String(button?.dataset?.queryMapTab || "rules");
    renderQueryMap();
    document.getElementById(`queryMapTab-${queryMapActiveTab}`)?.focus();
  };
  buttons.forEach((button, index) => {
    button.addEventListener("click", () => activate(button));
    button.addEventListener("keydown", (event) => {
      let next = index;
      if (event.key === "ArrowLeft") next = (index - 1 + buttons.length) % buttons.length;
      else if (event.key === "ArrowRight") next = (index + 1) % buttons.length;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = buttons.length - 1;
      else return;
      event.preventDefault();
      activate(buttons[next]);
    });
  });
  const search = host.querySelector("[data-rules-help-search]");
  search?.addEventListener("input", () => { queryMapSearchText = search.value; renderQueryMap(); requestAnimationFrame(() => { const next = document.querySelector("[data-rules-help-search]"); if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); } }); });
  search?.addEventListener("keydown", (event) => { if (event.key === "Escape") { event.preventDefault(); queryMapSearchText = ""; renderQueryMap(); document.querySelector("[data-rules-help-search]")?.focus(); } });
  host.querySelector("[data-query-sql-retry]")?.addEventListener("click", () => {
    resetLiveQueryMapState();
    renderQueryMap();
  });
}

function requestQueryMapRulebook() {
  if (queryMapRulebookState.status !== "idle") return;
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") return;
  if (!queryMapRulebookSessionSnapshot().canViewQueryMap) return;
  const token = workflowApiToken() || trackerApiToken();
  const requestParts = { base };
  const requestKey = queryMapRulebookAccessScopedRequestKey(requestParts);
  const read = beginSystemPortalDataRead("query-map-rules");
  queryMapRulebookRequestKey = requestKey;
  const previousResponse = queryMapRulebookState.response;
  queryMapRulebookState = { status: "pending", response: previousResponse, error: "", requestKey };
  read.fetch(`${base}/api/query-map/rulebook`, { method: "GET", cache: "no-store", headers: { accept: "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) } })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!read.current() || queryMapRulebookRequestKey !== requestKey || queryMapRulebookAccessScopedRequestKey(requestParts) !== requestKey) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        queryMapRulebookState = { status: "error", response: previousResponse, error: "Your session expired. Sign in again — this page will still be here.", requestKey };
        if (state.view === "queryMap") renderQueryMap();
        else if (state.view === "followUpRules") renderFollowUpRules();
        return;
      }
      if (!response.ok || body.error) throw new Error(body?.error?.message || body.message || `HTTP ${response.status}`);
      queryMapRulebookState = { status: "ok", response: body, error: "", requestKey };
      if (state.view === "queryMap") renderQueryMap();
      else if (state.view === "followUpRules") renderFollowUpRules();
    })
    .catch((error) => {
      if (!read.current() || queryMapRulebookRequestKey !== requestKey || queryMapRulebookAccessScopedRequestKey(requestParts) !== requestKey) return;
      queryMapRulebookState = { status: "error", response: null, error: error.message || "Rulebook request failed.", requestKey };
      if (state.view === "queryMap") renderQueryMap();
      else if (state.view === "followUpRules") renderFollowUpRules();
    }).finally(read.finish);
}

function renderSetupSummaryCards(cards) {
  return cards.map(([label, value, detail]) => `
    <div>
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(String(value))}</strong>
      <small>${escapeHtml(detail)}</small>
    </div>
  `).join("");
}

function renderAudit() {
  ensureSystemPortalDataScope();
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("audit")) return;
  // Client NAMES, never bare org ids: the trail labels companies through the Manage
  // Users directory, and this can be the first screen opened in a session. Gated on
  // the permission the directory route itself requires, so a role without it never
  // writes a denied-access row into the very trail it is reading.
  if (hasPermission(PERMISSIONS.MANAGE_USERS)) requestPortalOrgDirectory();
  // The durable trail first; the legacy per-ledger lists are below the fold.
  refreshSupervisorTrail();
  renderSupervisorTrail();
  syncReportRunFilterControls();
  refreshReportRunTrail();
  refreshReportExportJobTrail();
  refreshExportGovernanceActivityTrail();
  refreshClaimQueueWorkAuditTrail();
  renderReportRunTrail();
  renderReportExportJobTrail();
  renderQueueWorkExportPreview();
  renderExportGovernanceActivityExportPreview();
  renderExportGovernanceActivityTrail();
  renderClaimQueueWorkAuditTrail();
  renderWorkflowCommandTrail();
  if (els.auditTrail) {
    els.auditTrail.innerHTML = state.audit.filter(entry => !activeClientGroupTenant() || activeClientGroupOrgIds().includes(String(entry.orgId || ""))).slice().reverse().map((entry) => `
    <div class="audit-item">
      <strong>${escapeHtml(entry.at)} · ${escapeHtml(entry.by)}</strong>
      <p>${escapeHtml(entry.text)}</p>
    </div>
  `).join("") || '<div class="audit-item">No audit activity yet.</div>';
  }
}

// ===========================================================================
// ACTIVITY & AUDIT — the supervisor surface.
//
// WHAT THIS REPLACED: renderAudit() used to paint state.audit, a browser-local
// array (capped at 200, cleared by a refresh) whose own view-title said
// "for this preview". A supervisor reading it was not reading the record.
// Everything here comes from GET /api/admin/supervisor/activity, which reads
// portal.workflow_touch_events (the before -> after spine) and
// portal.audit_events (governance decisions).
//
// READ-ONLY, precisely: nothing below can MUTATE THE TRAIL. The server route is
// a GET and there is no write path to any ledger from this screen — by
// construction, not by convention.
//
// It is not literally silent: opening the full trail from Manage Users and
// exporting the CSV each POST one user_activity row. That is deliberate — an
// export of the audit trail is exactly the kind of act that should itself
// appear in the audit trail.
//
// PHI: the server already refuses to emit note text, patient identifiers and
// metadata_json. This file additionally renders only named fields, so a future
// server change cannot leak by being spread into the DOM.
// ===========================================================================

const TRAIL_CATEGORY_LABELS = {
  all: "All activity",
  work: "Claim work",
  access: "Admin & access",
  governance: "Admin & access",
  authorization: "Authorizations & credentialing",
  setup: "Rates & setup",
  export: "Exports",
  report: "Reports",
  submission: "Submissions",
  session: "Sign-in & sessions"
};

const supervisorTrailState = {
  status: "idle",
  error: "",
  warnings: [],
  people: [],
  orgs: [],
  actions: [],
  events: [],
  counts: { totalEvents: 0, returnedEvents: 0, people: 0, orgs: 0, claims: 0 },
  pagination: { page: 1, pageSize: 50, totalPages: 1, totalEvents: 0, hasPrevious: false, hasNext: false },
  window: {},
  expandedEventId: "",
  filters: { userId: "All", orgId: "All", preset: "7", fromDate: "", toDate: "", actionKey: "All", search: "", changesOnly: false, category: "all", page: 1, pageSize: 50 }
};
let supervisorTrailRequestKey = "";
let supervisorTrailSearchTimer = 0;

// Deep link target for Manage Users: open the full-width trail filtered to one
// person. The per-user panel in the Access Profile rail is 350px wide and will
// never hold a log; this is where that link lands.
function openSupervisorTrailForUser(userId) {
  supervisorTrailState.filters.userId = String(userId || "All") || "All";
  supervisorTrailState.filters.preset = "30";
  supervisorTrailState.filters.page = 1;
  supervisorTrailState.window = {};
  supervisorTrailState.expandedEventId = "";
  supervisorTrailRequestKey = "";
  // Use the shared navigation path: it closes Manage Users before painting the
  // destination and keeps the audit view's permission/audience guards intact.
  navigateToView("audit");
}

function supervisorTrailQuery() {
  const filters = { ...supervisorTrailState.filters, ...(activeClientGroupTenant() ? { orgId: activeClientGroupServerOrgId() } : {}) };
  const params = new URLSearchParams();
  params.set("preset", filters.preset || "7");
  if ((filters.preset || "") === "custom") {
    if (filters.fromDate) params.set("fromDate", filters.fromDate);
    if (filters.toDate) params.set("toDate", filters.toDate);
  }
  // userId/orgId are sent so the SERVER can narrow its read too. They are not
  // load-bearing for isolation: the API intersects them with the principal's
  // own book and drops anything outside it.
  if (filters.userId && filters.userId !== "All") params.set("userId", filters.userId);
  if (filters.orgId && filters.orgId !== "All") params.set("orgId", filters.orgId);
  if (filters.actionKey && filters.actionKey !== "All") params.set("actionKey", filters.actionKey);
  if (String(filters.search || "").trim()) params.set("search", String(filters.search).trim());
  if (filters.changesOnly) params.set("changesOnly", "true");
  if (filters.category && filters.category !== "all") params.set("category", filters.category);
  params.set("page", String(Math.max(1, Number(filters.page) || 1)));
  params.set("pageSize", String([25, 50, 100, 200].includes(Number(filters.pageSize)) ? Number(filters.pageSize) : 50));
  if (supervisorTrailState.window?.untilUtc) {
    params.set("untilUtc", supervisorTrailState.window.untilUtc);
  }
  return params;
}

async function refreshSupervisorTrail({ force = false } = {}) {
  const base = workflowApiBase();
  // Gate on the permission BEFORE fetching. Without this, every StatesideBiller
  // opening this tab would write a denied-access audit row per visit — the UI
  // would be flooding the very trail it exists to show.
  if (!base || typeof fetch !== "function" || !hasPermission(PERMISSIONS.SECURITY_AUDIT)) {
    supervisorTrailRequestKey = "";
    supervisorTrailState.status = hasPermission(PERMISSIONS.SECURITY_AUDIT) ? "offline" : "forbidden";
    return;
  }
  const params = supervisorTrailQuery();
  const requestParts = { kind: "supervisor-trail", base, query: params.toString() };
  const requestKey = workflowAccessScopedRequestKey(requestParts);
  if (supervisorTrailRequestKey === requestKey && !force) return;
  const read = beginSystemPortalDataRead("audit-supervisor");
  const requestStillCurrent = () => (
    read.current() &&
    supervisorTrailRequestKey === requestKey
    && workflowAccessScopedRequestKey(requestParts) === requestKey
  );
  supervisorTrailRequestKey = requestKey;
  supervisorTrailState.status = "loading";
  supervisorTrailState.error = "";
  supervisorTrailState.warnings = [];
  // Never leave the previous filter/page's rows under a new loading or failure
  // banner. A failed page-2 request displaying stale page-1 records is not a
  // usable fallback on an audit surface; it is a false answer.
  supervisorTrailState.people = [];
  supervisorTrailState.orgs = [];
  supervisorTrailState.actions = [];
  supervisorTrailState.events = [];
  supervisorTrailState.counts = { totalEvents: 0, returnedEvents: 0, people: 0, orgs: 0, claims: 0 };
  supervisorTrailState.pagination = {
    page: Math.max(1, Number(supervisorTrailState.filters.page) || 1),
    pageSize: Number(supervisorTrailState.filters.pageSize || 50),
    totalPages: 1,
    totalEvents: 0,
    hasPrevious: false,
    hasNext: false
  };
  renderSupervisorTrail();
  try {
    const response = await read.fetch(`${base}/api/admin/supervisor/activity?${params}`, {
      method: "GET",
      cache: "no-store",
      headers: feedbackApiHeaders()
    });
    const payload = await response.json().catch(() => ({}));
    if (!requestStillCurrent()) return;
    if (!response.ok) throw new Error(payload.message || payload.error || "Activity trail unavailable.");
    supervisorTrailState.people = Array.isArray(payload.people) ? payload.people : [];
    supervisorTrailState.orgs = Array.isArray(payload.orgs) ? payload.orgs : [];
    supervisorTrailState.actions = Array.isArray(payload.actions) ? payload.actions : [];
    supervisorTrailState.events = Array.isArray(payload.events) ? payload.events : [];
    supervisorTrailState.counts = payload.counts || supervisorTrailState.counts;
    supervisorTrailState.pagination = payload.pagination || supervisorTrailState.pagination;
    supervisorTrailState.filters.page = Number(supervisorTrailState.pagination.page || 1);
    supervisorTrailState.filters.pageSize = Number(supervisorTrailState.pagination.pageSize || supervisorTrailState.filters.pageSize || 50);
    supervisorTrailState.warnings = Array.isArray(payload.warnings) ? payload.warnings : [];
    supervisorTrailState.window = payload.window || {};
    supervisorTrailState.status = "ok";
  } catch (error) {
    if (!requestStillCurrent()) return;
    supervisorTrailState.status = "error";
    supervisorTrailState.error = error.message || "Activity trail unavailable.";
  } finally {
    read.finish();
    if (!requestStillCurrent()) {
      // Directory/session hydration may change the access identity after the
      // request begins. Discard that response, but release its loading key so
      // the current principal can obtain a fresh answer without reloading.
      // An older request must never clear or restart a newer request's lane.
      if (read.owns() && supervisorTrailRequestKey === requestKey) {
        supervisorTrailRequestKey = "";
        supervisorTrailState.status = "idle";
        if (state.view === "audit") window.setTimeout(() => {
          if (state.view !== "audit" || supervisorTrailRequestKey || supervisorTrailState.status !== "idle") return;
          ensureSystemPortalDataScope();
          void refreshSupervisorTrail();
          renderSupervisorTrail();
        }, 0);
      }
      return;
    }
    renderSupervisorTrail();
  }
}

// Client-side narrowing over rows the server already scoped and already
// stripped of PHI. Deliberately ONE predicate, shared by the table, the
// summaries and the CSV: VOB shipped a bug where the export applied a
// different filter set than the screen, and an admin who filtered to
// "Security" and clicked Export got the whole log.
function supervisorTrailVisibleEvents() {
  const filters = supervisorTrailState.filters;
  const needle = String(filters.search || "").trim().toLowerCase();
  return (supervisorTrailState.events || []).filter((event) => {
    if (filters.userId !== "All" && event.actorUserId !== filters.userId) return false;
    if (filters.orgId !== "All" && event.orgId !== filters.orgId) return false;
    if (filters.actionKey !== "All" && event.actionKey !== filters.actionKey) return false;
    if (filters.changesOnly && !(event.changes || []).length) return false;
    if (filters.category !== "all" && trailCategoryKey(event.category) !== filters.category) return false;
    if (!needle) return true;
    return supervisorTrailSearchText(event).includes(needle);
  });
}

// Search covers actor, action, claim/record id AND the old -> new values,
// because "who set this claim back to Insurance Follow-Up" is the question a
// supervisor actually types.
function supervisorTrailSearchText(event) {
  return [
    event.actorUserId,
    event.actorName,
    event.actorRole,
    event.actionKey,
    event.actionLabel,
    event.claimId,
    event.serviceLineId,
    event.orgId,
    event.reason,
    ...(event.changes || []).flatMap((change) => [change.label, change.from, change.to])
  ].filter(Boolean).join(" ").toLowerCase();
}

function trailCategoryKey(category) {
  const key = String(category || "").toLowerCase();
  // "governance" and "access" are the same thing to a supervisor asking
  // "what did this person do as an admin".
  if (key === "governance") return "access";
  return key || "work";
}

// Client NAMES, never bare org ids - a supervisor should not have to translate
// ORG-RVBD-CT in their head. Reuses the Manage Users label map so the two
// screens cannot disagree about what a client is called.
// The server groups events with no org under the literal key "(no org)". That is not
// a company whose name is missing - it is activity that belongs to no client (sign-in,
// exports, admin decisions) - and it cannot be used as a client filter.
const TRAIL_NO_CLIENT_KEYS = new Set(["(no org)", "(no client)"]);
function trailIsNoClientKey(orgId) {
  return TRAIL_NO_CLIENT_KEYS.has(String(orgId || ""));
}
function trailOrgLabel(orgId) {
  const id = String(orgId || "");
  if (!id) return "";
  if (trailIsNoClientKey(id)) return "Not tied to a client";
  try { return userMgmtDisplayOrgLabelMap().get(id) || "Company name unavailable"; } catch (_error) { return "Company name unavailable"; }
}
function trailPersonLabel(userId) {
  const person = (supervisorTrailState.people || []).find((entry) => entry.userId === userId);
  if (person && person.displayName) return `${person.displayName} (${userId})`;
  return userId;
}

function renderSupervisorTrail() {
  if (!els.trailEvents) return;
  syncSupervisorTrailControls();
  bindSupervisorTrailControls();
  renderSupervisorTrailBanner();
  renderSupervisorTrailChips();
  const visible = supervisorTrailVisibleEvents();
  renderSupervisorTrailCountLine(visible);
  renderSupervisorTrailPeople(visible);
  renderSupervisorTrailOrgs(visible);
  renderSupervisorTrailEvents(visible);
}

function syncSupervisorTrailControls() {
  const filters = supervisorTrailState.filters;
  if (els.trailPresetFilter) els.trailPresetFilter.value = filters.preset || "7";
  if (els.trailFromDate) els.trailFromDate.value = filters.fromDate || supervisorTrailState.window.fromDate || "";
  if (els.trailToDate) els.trailToDate.value = filters.toDate || supervisorTrailState.window.toDate || "";
  // Two of the eight filters were a readout of the preset above them, not a
  // control: on "Last 7 days" they showed the preset's own window and
  // typing in either one flipped the range to custom without saying so. The
  // count line already reports the window in words, so they only appear
  // once they are the thing choosing it.
  const customRange = (filters.preset || "7") === "custom";
  [els.trailFromDate, els.trailToDate].forEach((input) => {
    const field = input && typeof input.closest === "function" ? input.closest("label") : null;
    if (field) field.hidden = !customRange;
  });
  if (els.trailSearchInput && els.trailSearchInput.value !== filters.search) els.trailSearchInput.value = filters.search || "";
  if (els.trailChangesOnly) els.trailChangesOnly.checked = Boolean(filters.changesOnly);
  fillTrailSelect(els.trailPersonFilter, "Everyone", (supervisorTrailState.people || []).map((person) => [person.userId, trailPersonLabel(person.userId)]), filters.userId);
  fillTrailSelect(els.trailOrgFilter, "All clients", (supervisorTrailState.orgs || []).filter((org) => !trailIsNoClientKey(org.orgId)).map((org) => [org.orgId, trailOrgLabel(org.orgId)]), filters.orgId);
  lockOrgSelectToClientScope(els.trailOrgFilter);
  fillTrailSelect(els.trailActionFilter, "All actions", (supervisorTrailState.actions || []).map((action) => [action.actionKey, `${action.actionLabel} (${action.count})`]), filters.actionKey);
}

function fillTrailSelect(select, allLabel, options, selected) {
  if (!select) return;
  const chosen = selected || "All";
  const html = [`<option value="All">${escapeHtml(allLabel)}</option>`]
    .concat(options.map(([value, label]) => `<option value="${escapeHtml(value)}">${escapeHtml(label)}</option>`));
  // Preserve a chosen value that has scrolled out of the current window, or the
  // Manage Users deep link would silently reset to "Everyone" on arrival.
  if (chosen !== "All" && !options.some(([value]) => value === chosen)) {
    html.push(`<option value="${escapeHtml(chosen)}">${escapeHtml(chosen)}</option>`);
  }
  select.innerHTML = html.join("");
  select.value = chosen;
}

function bindSupervisorTrailControls() {
  bindTrailControl(els.trailPersonFilter, "change", (event) => setTrailFilter("userId", event.target.value, true));
  bindTrailControl(els.trailOrgFilter, "change", (event) => setTrailFilter("orgId", event.target.value, true));
  bindTrailControl(els.trailPresetFilter, "change", (event) => setTrailFilter("preset", event.target.value, true));
  bindTrailControl(els.trailFromDate, "change", (event) => { supervisorTrailState.filters.preset = "custom"; setTrailFilter("fromDate", event.target.value, true); });
  bindTrailControl(els.trailToDate, "change", (event) => { supervisorTrailState.filters.preset = "custom"; setTrailFilter("toDate", event.target.value, true); });
  bindTrailControl(els.trailActionFilter, "change", (event) => setTrailFilter("actionKey", event.target.value, true));
  bindTrailControl(els.trailSearchInput, "input", (event) => scheduleSupervisorTrailSearch(event.target.value));
  bindTrailControl(els.trailChangesOnly, "change", (event) => setTrailFilter("changesOnly", event.target.checked, true));
  bindTrailControl(els.refreshTrailBtn, "click", () => {
    supervisorTrailState.window = {};
    refreshSupervisorTrail({ force: true });
  });
  bindTrailControl(els.exportTrailBtn, "click", exportSupervisorTrailCsv);
  bindTrailControl(els.trailPeople, "click", onTrailPeopleClick);
  bindTrailControl(els.trailOrgs, "click", onTrailOrgsClick);
  bindTrailControl(els.trailCategoryChips, "click", onTrailChipClick);
  bindTrailControl(els.trailEvents, "click", onTrailEventClick);
}

// Bind once per element, tracked on the node itself: renderSupervisorTrail runs
// on every paint of this view and a naive addEventListener would stack handlers.
function bindTrailControl(element, type, handler) {
  if (!element || element.dataset.trailBound === type) return;
  element.dataset.trailBound = type;
  element.addEventListener(type, handler);
}

// Every visible filter is applied by the server BEFORE the requested page is
// sliced. Search is separately debounced so typing does not issue one complete
// ledger read per keystroke.
function setTrailFilter(key, value, refetch) {
  if (key === "orgId" && activeClientGroupTenant()) value = activeClientGroupServerOrgId();
  supervisorTrailState.filters[key] = value;
  if (key !== "page") supervisorTrailState.filters.page = 1;
  if (["preset", "fromDate", "toDate"].includes(key)) supervisorTrailState.window = {};
  supervisorTrailState.expandedEventId = "";
  if (refetch) refreshSupervisorTrail({ force: true });
  else renderSupervisorTrail();
}

function scheduleSupervisorTrailSearch(value) {
  supervisorTrailState.filters.search = value;
  supervisorTrailState.filters.page = 1;
  supervisorTrailState.expandedEventId = "";
  if (supervisorTrailSearchTimer) clearTimeout(supervisorTrailSearchTimer);
  supervisorTrailSearchTimer = setTimeout(() => {
    supervisorTrailSearchTimer = 0;
    refreshSupervisorTrail({ force: true });
  }, 300);
}

function renderSupervisorTrailBanner() {
  if (!els.trailBanner) return;
  const messages = [];
  if (supervisorTrailState.status === "forbidden") {
    messages.push("Your role cannot read the activity trail. Ask an administrator for audit access.");
  } else if (supervisorTrailState.status === "error") {
    // NOT the same as "no activity". A supervisor must never read a failed
    // fetch as an empty week.
    messages.push(`The activity trail could not be read: ${supervisorTrailState.error}`);
  } else if (supervisorTrailState.status === "offline") {
    messages.push("Activity is temporarily unavailable. Please try again.");
  }
  (supervisorTrailState.warnings || []).forEach((warning) => {
    if (String(warning).indexOf("process_local") >= 0) messages.push("Some recent activity may be missing. Please try again later.");
    else messages.push(`A ledger could not be read (${warning}). What follows is incomplete.`);
  });
  const uniqueMessages = Array.from(new Set(messages));
  els.trailBanner.innerHTML = uniqueMessages.map((message) => `<p>${escapeHtml(message)}</p>`).join("");
  els.trailBanner.hidden = uniqueMessages.length === 0;
  // SEVERITY, not just presence. A partial-data warning ("one ledger was
  // short") and a total read failure ("you are seeing nothing, and not because
  // nothing happened") are different facts and must not share a colour: amber
  // for incomplete, red for unreadable. Without this the two rendered
  // identically and a supervisor could read a dead trail as a quiet week.
  const failed = ["error", "forbidden", "offline"].includes(supervisorTrailState.status);
  els.trailBanner.classList.toggle("is-error", failed);
  els.trailBanner.classList.toggle("is-failure", failed);
}

// Category chips double as the summary and the filter, the one pattern worth
// stealing wholesale from the VOB audit log. "Admin & access" is listed FIRST
// and always shown even at zero: "whatever they were able to do as an admin"
// is the question this screen exists to answer, and a chip that disappears
// when the count is zero cannot answer "nothing" convincingly.
function renderSupervisorTrailChips() {
  if (!els.trailCategoryChips) return;
  const actions = supervisorTrailState.actions || [];
  const counts = new Map();
  actions.forEach((action) => {
    const key = trailCategoryKey(action.category);
    counts.set(key, (counts.get(key) || 0) + Number(action.count || 0));
  });
  const ordered = ["all", "access", "work", "authorization", "setup", "export", "report", "submission", "session"]
    .filter((key) => key === "all" || key === "access" || key === "work" || counts.has(key));
  els.trailCategoryChips.innerHTML = ordered.map((key) => {
    const count = key === "all" ? Number(supervisorTrailState.counts.totalEvents || 0) : (counts.get(key) || 0);
    const active = (supervisorTrailState.filters.category || "all") === key;
    return `<button type="button" class="trail-chip${active ? " active" : ""}" data-trail-category="${escapeHtml(key)}">
      <strong>${escapeHtml(formatCount(count))}</strong>
      <span>${escapeHtml(TRAIL_CATEGORY_LABELS[key] || key)}</span>
    </button>`;
  }).join("");
}

function onTrailChipClick(event) {
  const button = event.target.closest("[data-trail-category]");
  if (!button) return;
  setTrailFilter("category", button.dataset.trailCategory || "all", true);
}

function renderSupervisorTrailCountLine(visible) {
  if (!els.trailCountLine) return;
  if (supervisorTrailState.status === "loading") {
    els.trailCountLine.textContent = "Loading activity...";
    return;
  }
  const total = Number(supervisorTrailState.counts.totalEvents || 0);
  const window = supervisorTrailState.window || {};
  const range = window.fromDate && window.toDate ? ` between ${window.fromDate} and ${window.toDate}` : "";
  const pagination = supervisorTrailState.pagination || {};
  const page = Math.max(1, Number(pagination.page || 1));
  const pageSize = Math.max(1, Number(pagination.pageSize || visible.length || 1));
  const first = total ? ((page - 1) * pageSize) + 1 : 0;
  const last = total ? Math.min(total, first + visible.length - 1) : 0;
  // Exact range vs total, so pagination and any incomplete-ledger warning are
  // visible rather than a capped database chunk masquerading as completion.
  // "entries", not "changes": every row here is an event on the record (authorization
  // checks and list reads included); only rows with a Changed value altered a field.
  els.trailCountLine.textContent = `${formatCount(total)} entr${total === 1 ? "y" : "ies"} on the record${range}. Showing ${formatCount(first)}-${formatCount(last)}.`;
}

// PER-PERSON VOLUME AND OUTCOME. Neither reference has this — VOB counts by
// category, the legacy tracker prints one "N rows found" line — and it is the
// thing the owner actually asked for: open it and understand what each biller
// has been doing.
function renderSupervisorTrailPeople(_visible) {
  if (!els.trailPeople) return;
  const people = (supervisorTrailState.people || []).map((person) => ({
    userId: person.userId || "(unattributed)",
    name: person.displayName || "",
    roles: new Set(person.roles || []),
    claims: { size: Number(person.totals?.claimsTouched || 0) },
    orgs: new Set(person.orgIds || []),
    changes: Number(person.totals?.changes || 0),
    followUps: Number(person.totals?.followUpsSet || 0),
    admin: Number(person.totals?.adminActions || 0),
    denials: Number(person.totals?.denials || 0),
    lastAtUtc: person.lastAtUtc || ""
  }));
  if (!people.length) {
    // The events list below already carries the loading / unreadable message.
    // This slot only speaks when the read SUCCEEDED and was genuinely empty.
    els.trailPeople.innerHTML = supervisorTrailState.status === "ok"
      ? `<p class="trail-empty">${escapeHtml("No activity matches these filters.")}</p>`
      : "";
    return;
  }
  els.trailPeople.innerHTML = people.map((person) => {
    const roles = Array.from(person.roles).filter(Boolean);
    // Changes and claims stay even at zero - "this person touched no claim"
    // is an answer. Follow-ups, admin and denials are absences, and four
    // zeros on every card in the grid taught the eye to skip the line that
    // carries the numbers.
    const stats = [
      trailPersonStat(person.changes, "entry", "entries"),
      trailPersonStat(person.claims.size, "claim", "claims"),
      person.followUps ? trailPersonStat(person.followUps, "follow-up", "follow-ups") : "",
      person.admin ? trailPersonStat(person.admin, "admin action", "admin actions") : "",
      person.denials ? trailPersonStat(person.denials, "denied", "denied", "trail-deny") : ""
    ].filter(Boolean);
    const when = [
      person.orgs.size ? `${escapeHtml(formatCount(person.orgs.size))} client${person.orgs.size === 1 ? "" : "s"}` : "",
      `last ${escapeHtml(formatDateTime(person.lastAtUtc) || person.lastAtUtc)}`
    ].filter(Boolean).join(" &middot; ");
    return `
    <button type="button" class="trail-person" data-trail-person="${escapeHtml(person.userId)}">
      <span class="trail-person-name">${escapeHtml(person.name || person.userId)}</span>
      ${roles.length ? `<span class="trail-person-role">${escapeHtml(roles.join(", "))}</span>` : ""}
      <span class="trail-person-stats">${stats.join(" &middot; ")}</span>
      <span class="trail-person-when">${when}</span>
    </button>
  `;
  }).join("");
}

function trailPersonStat(value, singular, plural, tone) {
  const count = Number(value || 0);
  const bold = `<b${tone ? ` class="${tone}"` : ""}>${escapeHtml(formatCount(count))}</b>`;
  return `<span class="trail-stat">${bold} ${escapeHtml(count === 1 ? singular : plural)}</span>`;
}

function onTrailPeopleClick(event) {
  const button = event.target.closest("[data-trail-person]");
  if (!button) return;
  const userId = button.dataset.trailPerson || "All";
  setTrailFilter("userId", supervisorTrailState.filters.userId === userId ? "All" : userId, true);
}

function renderSupervisorTrailOrgs(_visible) {
  if (activeClientGroupTenant()) { if (els.trailOrgs) els.trailOrgs.innerHTML = "<span class=\"trail-org active\">" + escapeHtml(activeClientGroupLabel()) + "</span>"; return; }
  if (!els.trailOrgs) return;
  const orgs = (supervisorTrailState.orgs || []).map((org) => ({
    orgId: org.orgId || "(no client)",
    changes: Number(org.totals?.changes || 0),
    people: { size: Number(org.people || 0) }
  }));
  // One client and no filter set = the chips add nothing, so stay quiet. But
  // once a client filter IS set, the row must stay: filtering to one client
  // collapses this list to a single entry, and hiding it then would delete the
  // very control that was just used and strand the supervisor on that client.
  const orgFilterActive = (supervisorTrailState.filters.orgId || "All") !== "All";
  if (orgs.length < 2 && !orgFilterActive) { els.trailOrgs.innerHTML = ""; return; }
  const clearChip = orgFilterActive
    ? `<button type="button" class="trail-org" data-trail-org="All">All clients</button>`
    : "";
  els.trailOrgs.innerHTML = clearChip + orgs.map((org) => {
    const label = escapeHtml(trailOrgLabel(org.orgId) || org.orgId);
    const stats = "<b>" + escapeHtml(formatCount(org.changes)) + "</b><small>" + escapeHtml(formatCount(org.people.size)) + " " + (org.people.size === 1 ? "person" : "people") + "</small>";
    // "(no org)" is not a client: the server returns nothing for orgId=(no org), so
    // it is shown as a plain caption rather than a filter that always comes back empty.
    if (trailIsNoClientKey(org.orgId)) {
      return "<span class=\"trail-org trail-org--no-client\" title=\"Sign-ins, exports and admin decisions that are not tied to one client. Not a filter.\">" + label + stats + "</span>";
    }
    return "<button type=\"button\" class=\"trail-org" + (supervisorTrailState.filters.orgId === org.orgId ? " active" : "") + "\" data-trail-org=\"" + escapeHtml(org.orgId) + "\">" + label + stats + "</button>";
  }).join("");
}

function onTrailOrgsClick(event) {
  const button = event.target.closest("[data-trail-org]");
  if (!button) return;
  const orgId = button.dataset.trailOrg || "All";
  // Clicking the active chip, or the explicit "All clients" chip, clears.
  const next = (orgId === "All" || supervisorTrailState.filters.orgId === orgId) ? "All" : orgId;
  setTrailFilter("orgId", next, true);
}

// One row per change, drillable. The expanded row is where before -> after
// lives: old value struck through, new value bold, one line per field, plus the
// claim/service line it happened to.
// An empty list means one of three different things and a supervisor must be
// able to tell them apart: still loading, could not be read, or genuinely
// nothing happened. Only the third is "no activity". The gate here used to be
// `status === "ok"`, which rendered NOTHING AT ALL for the other two - a
// settled-but-empty trail was a blank box indistinguishable from a broken one,
// which is the single worst failure mode an audit screen has.
function trailEmptyStateHtml(message) {
  const status = supervisorTrailState.status;
  if (status === "loading") return '<p class="trail-empty">Loading activity...</p>';
  if (status === "error" || status === "forbidden" || status === "offline") {
    return '<p class="trail-empty">The trail could not be read &mdash; see the message above. This is NOT a report that nothing happened.</p>';
  }
  return `<p class="trail-empty">${escapeHtml(message)}</p>`;
}
function renderSupervisorTrailEvents(visible) {
  if (!els.trailEvents) return;
  if (!visible.length) {
    els.trailEvents.innerHTML = trailEmptyStateHtml("Nothing on the record matches these filters. Widen the date range or clear the search.");
    return;
  }
  const pagination = supervisorTrailPaginationHtml();
  const columnHead = `<div class="trail-head-row" aria-hidden="true"><span>When</span><span>Who</span><span>What</span><span>Where</span><span>Changed</span></div>`;
  els.trailEvents.innerHTML = pagination + columnHead + visible.map((event) => {
    const expanded = supervisorTrailState.expandedEventId === event.eventId;
    const changeCount = (event.changes || []).length;
    return `
    <div class="trail-row${expanded ? " open" : ""}${event.decision === "deny" ? " denied" : ""}">
      <button type="button" class="trail-row-head" data-trail-event="${escapeHtml(event.eventId)}" aria-expanded="${expanded ? "true" : "false"}">
        <span class="trail-row-when" data-label="When">${escapeHtml(formatDateTime(event.atUtc) || event.atUtc)}</span>
        <span class="trail-row-who" data-label="User">${escapeHtml(event.actorName || event.actorUserId || "(unattributed)")}</span>
        <span class="trail-row-what" data-label="What">${escapeHtml(event.actionLabel || event.actionKey)}</span>
        <span class="trail-row-where" data-label="Where">${escapeHtml(event.claimId ? `Claim ${event.claimId}` : (trailOrgLabel(event.orgId) || event.orgId || ""))}</span>
        <span class="trail-row-delta${changeCount || event.decision === "deny" ? "" : " trail-row-delta--none"}" data-label="Changed">${changeCount ? escapeHtml(`${changeCount} field${changeCount === 1 ? "" : "s"}`) : (event.decision === "deny" ? "Denied" : "&mdash;")}</span>
      </button>
      ${expanded ? renderSupervisorTrailDetail(event) : ""}
    </div>`;
  }).join("") + pagination;
}

function supervisorTrailPaginationHtml() {
  const pagination = supervisorTrailState.pagination || {};
  const page = Math.max(1, Number(pagination.page || 1));
  const totalPages = Math.max(1, Number(pagination.totalPages || 1));
  const pageSize = Number(pagination.pageSize || supervisorTrailState.filters.pageSize || 50);
  if (totalPages <= 1 && Number(pagination.totalEvents || 0) <= 25) return "";
  const pages = paginationPageNumbers(page, totalPages);
  const sizes = [25, 50, 100, 200].map((size) => `
    <button type="button" class="trail-page-size${size === pageSize ? " active" : ""}" data-trail-page-size="${size}" aria-pressed="${size === pageSize ? "true" : "false"}">${size}</button>`).join("");
  return `<nav class="trail-pagination" aria-label="Activity pages">
    <div class="trail-page-sizes"><span>Rows per page</span>${sizes}</div>
    <div class="trail-page-buttons">
      <button type="button" data-trail-page="${page - 1}"${page <= 1 ? " disabled" : ""}>Previous</button>
      ${pages.map((number) => number === "…"
        ? '<span class="trail-page-gap" aria-hidden="true">&hellip;</span>'
        : `<button type="button" data-trail-page="${number}"${number === page ? ' class="active" aria-current="page"' : ""}>${number}</button>`).join("")}
      <button type="button" data-trail-page="${page + 1}"${page >= totalPages ? " disabled" : ""}>Next</button>
    </div>
  </nav>`;
}

function paginationPageNumbers(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_entry, index) => index + 1);
  const wanted = Array.from(new Set([1, 2, page - 1, page, page + 1, totalPages - 1, totalPages]))
    .filter((number) => number >= 1 && number <= totalPages)
    .sort((a, b) => a - b);
  const output = [];
  wanted.forEach((number, index) => {
    if (index && number - wanted[index - 1] > 1) output.push("…");
    output.push(number);
  });
  return output;
}

// The ledgers store a raw enum; a supervisor should read a word.
function trailDecisionLabel(decision) {
  const map = { allow: "Allowed", deny: "Denied", record: "Recorded", narrowed: "Narrowed by scope", error: "Failed" };
  const key = String(decision || "").toLowerCase();
  return map[key] || (key ? humanizeTrailValue(key) : "");
}

// A ledger field name is camelCase or snake_case; the stylesheet uppercases
// this label, which turned "followUpAction" into FOLLOWUPACTION - one word,
// unreadable, and indistinguishable from a code.
function trailFieldLabel(field) {
  return humanizeTrailValue(String(field || "").replace(/([a-z0-9])([A-Z])/g, "$1 $2")).toLowerCase().replace(/^./, (character) => character.toUpperCase());
}

function humanizeTrailValue(value) {
  return String(value || "").replace(/[._]+/g, " ").replace(/^./, (character) => character.toUpperCase());
}

function renderSupervisorTrailDetail(event) {
  const changes = (event.changes || []).map((change) => `
    <li>
      <span class="trail-change-field">${escapeHtml(change.label || trailFieldLabel(change.field))}</span>
      <span class="trail-change-from">${escapeHtml(change.from || "(blank)")}</span>
      <span class="trail-change-arrow" aria-hidden="true">&rarr;</span>
      <span class="trail-change-to">${escapeHtml(change.to || "(blank)")}</span>
    </li>`).join("");
  const facts = [
    ["Person", event.actorName ? `${event.actorName} (${event.actorUserId})` : (event.actorUserId || "(unattributed)")],
    ["Role", event.actorRole || ""],
    ["Client", trailOrgLabel(event.orgId) || event.orgId || ""],
    ["Claim", event.claimId || ""],
    ["Service line", event.serviceLineId || ""],
    ["Action", event.actionKey || ""],
    ["Outcome", trailDecisionLabel(event.decision)],
    ["Reason", event.reason || ""],
    ["Note written", event.noteProvided ? "Yes" : "No"],
    ["Source ledger", event.source || ""],
    ["Record id", event.eventId || ""]
  ].filter(([, value]) => String(value || "").length);
  return `
    <div class="trail-row-detail">
      ${changes ? `<ul class="trail-changes">${changes}</ul>` : '<p class="trail-no-change">This action changed no workflow field.</p>'}
      <dl class="trail-facts">${facts.map(([label, value]) => `<dt>${escapeHtml(label)}</dt><dd>${escapeHtml(String(value))}</dd>`).join("")}</dl>
      ${event.claimId ? `<button type="button" class="ghost-button" data-trail-open-claim="${escapeHtml(event.claimId)}">Open claim ${escapeHtml(event.claimId)}</button>` : ""}
    </div>`;
}

function onTrailEventClick(event) {
  const pageSizeButton = event.target.closest("[data-trail-page-size]");
  if (pageSizeButton) {
    setTrailFilter("pageSize", Number(pageSizeButton.dataset.trailPageSize || 50), true);
    return;
  }
  const pageButton = event.target.closest("[data-trail-page]");
  if (pageButton && !pageButton.disabled) {
    setTrailFilter("page", Number(pageButton.dataset.trailPage || 1), true);
    return;
  }
  const claimButton = event.target.closest("[data-trail-open-claim]");
  if (claimButton) {
    openTrailClaim(claimButton.dataset.trailOpenClaim || "");
    return;
  }
  const head = event.target.closest("[data-trail-event]");
  if (!head) return;
  const eventId = head.dataset.trailEvent || "";
  supervisorTrailState.expandedEventId = supervisorTrailState.expandedEventId === eventId ? "" : eventId;
  renderSupervisorTrail();
}

// Drill to the record the change was made to. Falls back to a claim-id search
// on the workbench when the claim is not in the loaded page — the audit trail
// reaches further back than the grid does.
function openTrailClaim(claimId) {
  const id = String(claimId || "").trim();
  if (!id) return;
  if (typeof openDrawer === "function" && typeof getClaim === "function" && getClaim(id)) {
    openDrawer(id);
    return;
  }
  state.view = "workbench";
  state.filters = { ...(state.filters || {}), search: id };
  saveState();
  render();
}

// Exports EXACTLY the rows on screen, through the same predicate the table uses.
// Client-side on already-fetched, already-scoped, already-PHI-stripped rows: it
// creates no new server export surface and therefore no new export-governance
// obligation.
function exportSupervisorTrailCsv() {
  const visible = supervisorTrailVisibleEvents();
  if (!visible.length) {
    showToast("Nothing to export for these filters.");
    return;
  }
  const header = ["When (UTC)", "Person", "Role", "Client", "Action", "Claim", "Service line", "Field", "Before", "After", "Outcome", "Reason", "Record id"];
  const rows = [];
  visible.forEach((event) => {
    // Client NAME, matching the table. An export whose columns disagree with the
    // screen they were exported from is how a filter bug hides.
    const base = [event.atUtc, event.actorName || event.actorUserId, event.actorRole, trailOrgLabel(event.orgId) || event.orgId, event.actionLabel || event.actionKey, event.claimId, event.serviceLineId];
    if (!(event.changes || []).length) {
      rows.push(base.concat(["", "", "", trailDecisionLabel(event.decision), event.reason, event.eventId]));
      return;
    }
    event.changes.forEach((change) => {
      rows.push(base.concat([change.label || change.field, change.from, change.to, trailDecisionLabel(event.decision), event.reason, event.eventId]));
    });
  });
  const csv = [header, ...rows].map((row) => row.map(csvEscape).join(",")).join("\r\n");
  arDownloadFile(`activity-audit-${new Date().toISOString().slice(0, 10)}.csv`, csv, "text/csv;charset=utf-8");
  recordUserActivity({ featureKey: "audit", actionKey: "activity_export", route: "audit", metadata: { rowCount: rows.length } });
}

function renderSecurityReview() {
  // The Access tab is intentionally slim: a plain-language "who can see what"
  // directory plus a few quick access checks. The former governance surface
  // (deployment-readiness pills, simulated authorization-failure logs,
  // permission matrices, and the grant/override command builder) was removed
  // because it shipped hardcoded sample data and added no value for billing
  // operators. The render functions for those sections still exist but become
  // no-ops once their DOM nodes are gone (each guards on its els.* lookup).
  refreshAuthoritativeUserGovernance();
  renderAccessDirectory();
  renderAccessPolicyChecklist();
}

function syncSecurityFilterControls() {
  const filters = state.securityReviewFilters || {};
  if (els.securityOrgFilter) els.securityOrgFilter.value = filters.orgId || "All";
  if (els.securityDecisionFilter) els.securityDecisionFilter.value = filters.decision || "All";
  if (els.securityPermissionFilter) els.securityPermissionFilter.value = filters.permission || "";
  if (els.securityFromDate) els.securityFromDate.value = filters.fromDate || "";
  if (els.securityToDate) els.securityToDate.value = filters.toDate || "";
}

function renderSecuritySummary() {
  if (!els.securitySummary) return;
  const failures = filteredAuthorizationFailures();
  const decisions = filteredAccessDecisions();
  const previews = state.accessAdminPreviews || [];
  const blockedPreviews = previews.filter((preview) => preview.execution?.blocked).length;
  els.securitySummary.innerHTML = [
    ["Failures", failures.length],
    ["Denied Decisions", decisions.filter((decision) => decision.decision === "deny").length],
    ["Grant/Override Evidence", decisions.filter((decision) => decision.grantBacked || decision.overrideBacked).length],
    ["Blocked Admin Writes", blockedPreviews]
  ].map(([label, value]) => `<div><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`).join("");
}

function renderAccessOperatingModel() {
  if (!els.accessOperatingModel) return;
  const profile = currentAccessProfile();
  const rows = accessOperatingModelRows(profile);
  const workflow = [
    ["Invite", "Create identity-provider user, require MFA, assign a role template, and bind tenant/company scope before first login."],
    ["Scope", "Grant explicit orgIds/clientNames/providerNames or assigned-only work queues; cross-tenant visibility is only for platform roles."],
    ["Approve", "Temporary grants and permission overrides require two distinct non-requester approvals before any managed write path is enabled."],
    ["Audit", "Every allow, deny, redaction, override, and failed action writes access-decision evidence with PHI-safe metadata."]
  ];
  els.accessOperatingModel.innerHTML = `
    <div class="access-model-head">
      <div>
        <span>ACCESS MODEL</span>
        <strong>Tenant-scoped identity, roles, invitations, and approvals</strong>
        <p>Use the matrix below to decide who can see each company, biller queue, client portal, setup page, and financial surface.</p>
      </div>
      ${pill(profile.scope?.allOrgs ? "Platform scope" : "Scoped user", profile.scope?.allOrgs ? "blue" : "green")}
    </div>
    <div class="access-model-grid">
      ${rows.map((row) => `
        <article class="access-model-card access-model-card--${escapeHtml(row.tone)}">
          <span>${escapeHtml(row.label)}</span>
          <strong>${escapeHtml(row.value)}</strong>
          <p>${escapeHtml(row.detail)}</p>
        </article>
      `).join("")}
    </div>
    <div class="access-model-flow" role="list" aria-label="Access administration workflow">
      ${workflow.map(([label, detail]) => `
        <div role="listitem">
          <span>${escapeHtml(label)}</span>
          <p>${escapeHtml(detail)}</p>
        </div>
      `).join("")}
    </div>
  `;
}

function accessOperatingModelRows(profile = currentAccessProfile()) {
  const scope = profile.scope || {};
  const usersInRole = users.filter((user) => canonicalRole(user.role) === state.role).length;
  const orgCount = scope.allOrgs ? uniqueValues(getClaims(), "orgId").length : (Array.isArray(scope.orgIds) ? scope.orgIds.length : 0);
  const clientCount = scope.allOrgs ? uniqueValues(getClaims(), "client").length : (Array.isArray(scope.clientNames) ? scope.clientNames.length : 0);
  return [
    {
      label: "Role Template",
      value: profile.label || state.role,
      detail: `${formatNounCount(profile.permissions?.length || 0, "permission")} applied to ${formatNounCount(usersInRole || 1, "visible user profile")}.`,
      tone: "blue"
    },
    {
      label: "Org-ID Scope",
      value: scope.allOrgs ? "All org IDs" : formatNounCount(orgCount, "org"),
      detail: scope.allOrgs ? "Cross-company access is explicit and should stay limited to platform operators." : "User access is narrowed to assigned tenant/company ids.",
      tone: scope.allOrgs ? "amber" : "green"
    },
    {
      label: "Client Scope",
      value: scope.allOrgs ? "All clients" : formatNounCount(clientCount, "client"),
      detail: "Client portal users should receive only client-safe views and no internal notes unless a role explicitly includes them.",
      tone: "green"
    },
    {
      label: "Approval Guard",
      value: "2 approvers",
      detail: "Grant and override previews are blocked until two non-requester approvals are captured and a managed write store is selected.",
      tone: "red"
    }
  ];
}

function renderAccessDirectory() {
  if (!els.accessDirectoryRows) return;
  if (portalUserDirectoryHostedMode() && !portalUserDirectoryIsCurrent()) {
    if (els.accessDirectorySummary) els.accessDirectorySummary.textContent = portalUserDirectoryState.status === "pending" ? "Loading directory" : "Directory unavailable";
    els.accessDirectoryRows.innerHTML = `<div class="authz-empty"><p class="authz-empty-title">${escapeHtml(portalUserDirectoryUnavailableMessage())}</p><p class="authz-empty-note">Access is not inferred from browser storage or demo users.</p>${portalUserDirectoryState.status === "error" ? `<button class="btn btn--secondary" data-access-directory-retry type="button">Try again</button>` : ""}</div>`;
    els.accessDirectoryRows.querySelector("[data-access-directory-retry]")?.addEventListener("click", () => refreshAuthoritativeUserGovernance({ force: true }));
    return;
  }
  const rows = accessDirectoryRows();
  const clientUsers = rows.filter((row) => isExternalClientRole(row.role)).length;
  const crossTenantUsers = rows.filter((row) => row.crossTenant).length;
  if (els.accessDirectorySummary) {
    els.accessDirectorySummary.textContent = `${formatNounCount(rows.length, "user")} · ${formatNounCount(clientUsers, "client user")} · ${formatNounCount(crossTenantUsers, "platform user")}`;
  }
  els.accessDirectoryRows.innerHTML = rows.map((row) => `
    <div class="sync-item access-directory-row">
      <div class="sync-item-header">
        <strong>${escapeHtml(row.name)}</strong>
        ${pill(row.roleLabel, row.crossTenant ? "amber" : isExternalClientRole(row.role) ? "green" : "blue")}
      </div>
      <p class="access-directory-identity">${escapeHtml(row.email || "No invite email on file")} - ${escapeHtml(userStatusLabel(row.status))}</p>
      <div class="access-directory-meta">
        <span><b>Scope</b>${escapeHtml(row.scopeLabel)}</span>
        <span><b>Views</b>${escapeHtml(row.viewLabels.join(", ") || "none")}</span>
        <span><b>Data</b>${escapeHtml(row.dataLabel)}</span>
      </div>
      <div class="permission-chip-row">
        ${row.tags.map((tag) => `<span class="permission-chip ${tag.active ? "active" : ""}">${escapeHtml(tag.label)}</span>`).join("")}
      </div>
      ${row.authDisagreement ? `<div class="authz-warning"><strong>Authentication lanes disagree</strong><p>${escapeHtml(row.authDisagreement)}</p>${row.authLaneSummaries.map((summary) => `<p>${escapeHtml(summary)}</p>`).join("")}</div>` : ""}
      <p>${escapeHtml(row.guidance)}</p>
    </div>
  `).join("");
}

function accessDirectoryRows() {
  return governanceUserRows().map((user) => {
    const role = canonicalRole(user.role);
    const profile = roleProfiles[role] || roleProfiles.Client;
    const scopedProfile = accessProfileForUser(user);
    const permissions = new Set(scopedProfile.permissions || []);
    const viewLabels = accessibleViewsForPermissions(permissions)
      .map((view) => viewLabel(view))
      .filter(Boolean);
    const scope = scopedProfile.scope || {};
    const tags = accessRiskTags({ role, permissions, scope });
    const disagreement = portalUserAuthDisagreement(user);
    const conflictScopeLabel = user.followUpActionPolicyConflict
      ? (user.scopeConflict ? "Authentication lanes disagree on company scope and follow-up action authority" : "Authentication lanes grant different follow-up action authority")
      : "Authentication lanes report different company scopes";
    if (disagreement) tags.unshift({ label: "Auth conflict", active: true });
    return {
      name: user.name,
      email: user.email || "",
      role,
      status: user.status || "active",
      roleLabel: disagreement ? "Conflicting access" : (profile.label || role),
      crossTenant: disagreement
        ? (user.authLanes || []).some((lane) => lane.allOrgs)
        : Boolean(scope.crossTenant || scope.allOrgs),
      scopeLabel: disagreement ? conflictScopeLabel : accessScopeSummary(scope),
      viewLabels: disagreement ? [] : viewLabels,
      // Preserve the raw grant because the shared "Reports" label cannot tell
      // which permission opened the view. Full Client intentionally holds the
      // internal Reports grant under the owner's current policy; RedactedClient
      // does not, so the client-safety audit treats this as risk only for the
      // redacted role.
      holdsInternalReports: permissions.has(PERMISSIONS.VIEW_REPORTS),
      dataLabel: disagreement ? "Not summarized until the auth conflict is resolved" : [
        permissions.has(PERMISSIONS.VIEW_FINANCIALS) ? "financials" : "",
        permissions.has(PERMISSIONS.VIEW_PHI) ? "PHI" : "",
        permissions.has(PERMISSIONS.VIEW_INTERNAL_NOTES) ? "internal notes" : ""
      ].filter(Boolean).join(" + ") || "redacted",
      tags,
      authDisagreement: portalUserAuthDisagreementMessage(user),
      authLaneSummaries: portalUserAuthLaneSummaries(user),
      guidance: disagreement
        ? "Resolve the authentication-lane disagreement before relying on this summary or changing access."
        : accessUserGuidance({ user, role, permissions, scope })
    };
  });
}

function accessProfileForUser(user = {}) {
  const role = canonicalRole(user.role);
  const profile = roleProfiles[role] || roleProfiles.Client;
  if (role === "SuperAdmin") {
    return { ...profile, role, scope: { allOrgs: true, crossTenant: true } };
  }
  return {
    ...profile,
    role,
    scope: scopedTenantBoundary({
      orgIds: normalizeUserScopeList(user.orgIds).length ? user.orgIds : profile.scope?.orgIds,
      clientNames: normalizeUserScopeList(user.clientNames).length ? user.clientNames : profile.scope?.clientNames,
      payerNames: user.payerNames,
      assignedOnly: user.assignedOnly,
      assignedName: user.assignedName || user.userId,
      followUpActionAccessConfigured: user.followUpActionAccessConfigured,
      followUpActions: user.followUpActions,
      teamIds: user.teamIds
    }, role)
  };
}

// Derive another user's visible navigation from that user's role and grants.
// This must not read currentAccessProfile(): the access directory summarizes
// other principals, not the signed-in session.
function navigableViewsForRole(role, permissions = new Set()) {
  return accessibleViewsForPermissions(permissions).filter((view) => {
    if (view === "assignments" && isExternalClientRole(role)) return false;
    if (view === "reviews") return false;
    const audience = VIEW_AUDIENCE[view] || DEFAULT_VIEW_AUDIENCE;
    if (audience === "external-client") return isExternalClientRole(role);
    if (audience === "internal-staff") return !isExternalClientRole(role);
    return true;
  });
}

function accessibleViewsForPermissions(permissions = new Set()) {
  return navigationSections.flatMap((section) => section.views || [])
    .filter((view) => viewPermissionGateSatisfied(view, (permission) => permissions.has(permission)));
}

function viewLabel(view) {
  return {
    workbench: "AR",
    assignments: "My Work",
    reviews: "Review",
    reports: "Reports",
    feeSchedules: "Fee Schedules",
    missingRates: "Missing Rates",
    authorizations: "Authorizations",
    credentialing: "Credentialing",
    clientActionPending: "Client Action Pending",
    companyOversight: "Oversight",
    denialIntelligence: "Denial Intelligence",
    followUpRules: "Follow-Up Rules",
    queryMap: "Query Map",
    security: "Security",
    audit: "Audit",
    clientHome: "Client Home",
    statements: "Patient Statements",
    training: "Training"
  }[view] || view;
}

function accessRiskTags({ role, permissions, scope }) {
  const tags = [];
  tags.push({ label: scope.allOrgs ? "all org IDs" : "scoped", active: !scope.allOrgs });
  if (permissions.has(PERMISSIONS.REPORT_EXPORT)) tags.push({ label: "export", active: true });
  if (permissions.has(PERMISSIONS.VIEW_INTERNAL_NOTES)) {
    tags.push({ label: "internal notes", active: !isExternalClientRole(role) });
  }
  if (permissions.has(PERMISSIONS.MANAGE_ACCESS_GRANTS) || permissions.has(PERMISSIONS.MANAGE_PERMISSION_OVERRIDES)) {
    tags.push({ label: "access admin", active: true });
  }
  if (isExternalClientRole(role)) tags.push({ label: "client-safe", active: true });
  return tags;
}

function accessUserGuidance({ role, permissions, scope }) {
  if (canonicalRole(role) === "RedactedClient") return "Redacted Client: read-only AR plus Authorization, Credentialing, and Patient Statements; Reports and insurance-side financial amounts are withheld, while patient responsibility and statement money remain visible.";
  if (isExternalClientRole(role)) return "Client: approved All Data, Reports, report export, Authorization, Credentialing, Client Action, and Patient Statements, scoped server-side to one approved company; the separate workflow-workbook export remains withheld.";
  if (scope.assignedOnly) return "This user is provisioned assigned-only: visibility is their routed queue intersected with their companies, so routing controls must stay stateside/admin owned.";
  if (scope.allOrgs) return "Platform scope should be rare, named, MFA-backed, and reviewed through access-decision evidence.";
  if (permissions.has(PERMISSIONS.MANAGE_ACCESS_GRANTS)) return "This user can preview access changes; managed writes still require two non-requester approvals.";
  return "Organization scope should be reviewed whenever a new organization, biller, or scoped user is added.";
}

function accessScopeSummary(scope = {}) {
  if (scope.allOrgs) return "All org IDs";
  if (scope.assignedOnly) return `Assigned queue${scope.assignedName ? `: ${scope.assignedName}` : ""}`;
  // (org/client/provider counts below cover every other scoped principal,
  // including offshore billers now that they carry a real company list.)
  const orgCount = Array.isArray(scope.orgIds) ? scope.orgIds.length : 0;
  const clientCount = Array.isArray(scope.clientNames) ? scope.clientNames.length : 0;
  const providerCount = Array.isArray(scope.providerNames) ? scope.providerNames.length : 0;
  const payerCount = Array.isArray(scope.payerNames) ? scope.payerNames.length : 0;
  const parts = [
    orgCount ? `${orgCount} org${orgCount === 1 ? "" : "s"}` : "",
    clientCount ? `${clientCount} client${clientCount === 1 ? "" : "s"}` : "",
    providerCount ? `${providerCount} provider panel${providerCount === 1 ? "" : "s"}` : "",
    // The offshore book is a real limit on what they can read — showing the org
    // count alone overstates their access in this audit.
    payerCount ? `${payerCount} payer${payerCount === 1 ? "" : "s"}` : ""
  ].filter(Boolean);
  return parts.join(" · ") || "No org scope";
}

function renderAccessPolicyChecklist() {
  if (!els.accessPolicyChecklist) return;
  const rows = accessPolicyChecklistRows();
  els.accessPolicyChecklist.innerHTML = rows.map((row) => `
    <div class="sync-item access-policy-row access-policy-row--${escapeHtml(row.tone)}">
      <div class="sync-item-header">
        <strong>${escapeHtml(row.label)}</strong>
        ${pill(row.status, row.tone)}
      </div>
      <p>${escapeHtml(row.detail)}</p>
    </div>
  `).join("");
}

function accessPolicyChecklistRows() {
  const directory = accessDirectoryRows();
  // Client-safe surface allow-list, by the value returned by viewLabel().
  // trackerExport intentionally uses its view-key fallback because viewLabel()
  // has no custom display mapping for that entry. Full Client carries All Data,
  // Reports, and report export under the owner's current policy.
  // RedactedClient keeps its narrower list and the
  // raw holdsInternalReports fact below is a tripwire for widening that role.
  // CREDENTIALING JOINED THIS SET ON 2026-08-11, deliberately. It is the SIBLING
  // of Authorizations — the other hold a claim can sit in, and the one only the
  // practice can clear, because only they can say their therapist is now in
  // network with a payer (owner decision 5). A client shown one hold and not the
  // other is being shown half their own backlog.
  //
  // This widens the CLIENT-SAFE list, so it is worth naming what it does NOT
  // widen: the credentialing board is org-scoped from the authenticated
  // principal, carries expected reimbursement rather than billed charges, routes on a
  // derived PHI-free enum rather than the raw authorization field, and the send
  // lane (credentialing.submit) is withheld from every client role.
  // Fee Schedules joins the full Client's set as a read-only, own-company
  // view (Nathan, 2026-10-13). RedactedClient's set does not change.
  // Training (2026-10-15) is static narrated video recorded on a fictional
  // practice: no account data, so it is client-safe for both client roles.
  const clientAllowedViews = new Set(["Client Home", "AR", "Reports", "trackerExport", "Patient Statements", "Authorizations", "Credentialing", "Client Action Pending", "Fee Schedules", "Training"]);
  // ClientBiller adds exactly ONE surface: the read-only All Data grid
  // (client:workbench). It is allowed for that role alone - a plain Client
  // showing "All Data" is still a real violation and must still fire.
  const redactedClientAllowedViews = new Set(["Client Home", "AR", "Patient Statements", "Authorizations", "Credentialing", "Client Action Pending", "Training"]);
  const clientRisk = directory.filter((row) => {
    if (!isExternalClientRole(row.role)) return false;
    const allowed = canonicalRole(row.role) === "RedactedClient" ? redactedClientAllowedViews : clientAllowedViews;
    return (row.viewLabels || []).some((label) => !allowed.has(label))
      || (canonicalRole(row.role) === "RedactedClient" && row.holdsInternalReports)
      || /internal notes/i.test(row.dataLabel);
  });
  const unscopedNonPlatform = directory.filter((row) => row.role !== "SuperAdmin" && row.scopeLabel === "No org scope");
  const accessAdmins = directory.filter((row) => row.tags.some((tag) => tag.label === "access admin"));
  const crossTenantUsers = directory.filter((row) => row.crossTenant);
  return [
    {
      label: "Client-safe surfaces",
      status: clientRisk.length ? `${clientRisk.length} review` : "clean",
      detail: clientRisk.length ? "One or more external client users can open a surface outside their approved set, or can see internal-only data." : "Client and Redacted Client are limited to their approved one-company read/task surfaces.",
      tone: clientRisk.length ? "red" : "green"
    },
    {
      label: "Named org-ID scope",
      status: unscopedNonPlatform.length ? `${unscopedNonPlatform.length} missing` : "scoped",
      detail: unscopedNonPlatform.length ? "Every non-platform user must be tied to an organization, provider, or assigned queue before login." : "Non-platform users have explicit organization or assignment scope.",
      tone: unscopedNonPlatform.length ? "amber" : "green"
    },
    {
      label: "Access admin separation",
      status: `${accessAdmins.length} admin`,
      detail: "Access changes are preview-only here and remain blocked for writes until the managed access store, MFA identity, and two-approver workflow are active.",
      tone: "amber"
    },
    {
      label: "Platform scope review",
      status: `${crossTenantUsers.length} platform`,
      detail: "Cross-tenant visibility should remain limited to named platform users and must be audited through access-decision evidence.",
      tone: crossTenantUsers.length > 1 ? "amber" : "green"
    }
  ];
}

function syncAccessAdminControlOptions() {
  if (els.accessUserOptions) {
    els.accessUserOptions.innerHTML = users.map((user) => `<option value="${escapeHtml(user.name)}"></option>`).join("");
  }
  if (els.accessPermissionOptions) {
    els.accessPermissionOptions.innerHTML = accessAdminPermissionOptions.map((permission) => `<option value="${escapeHtml(permission)}"></option>`).join("");
  }
  syncSelectOptions(els.accessScopeOrg, [
    { value: "", label: "Current user scope" },
    ...uniqueValues(getClaims(), "orgId").map((orgId) => ({ value: orgId, label: orgId }))
  ]);
  syncSelectOptions(els.accessScopeClient, [
    { value: "", label: "Current client scope" },
    ...uniqueValues(getClaims(), "client").map((client) => ({ value: client, label: client }))
  ]);
}

function syncSelectOptions(select, options = []) {
  if (!select) return;
  const current = select.value;
  select.innerHTML = options.map((option) => `<option value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</option>`).join("");
  if (options.some((option) => option.value === current)) select.value = current;
}

function renderDeploymentReadiness() {
  if (!els.deploymentReadinessPanel) return;
  const health = state.workflowApiHealth || {};
  const base = workflowApiBase();
  const rows = deploymentReadinessRows(health, base);
  const blockers = rows.filter((row) => row.blocker);
  const attentionRows = rows.filter((row) => (row.tone !== "green" || row.alwaysShow) && row.detail);
  els.deploymentReadinessPanel.innerHTML = `
    <div class="sync-section-title">
      <strong>Deployment Readiness</strong>
      <span>${escapeHtml(health.checkedAt ? `checked ${health.checkedAt}` : "not checked yet")}</span>
    </div>
    <div class="sync-item" data-testid="deployment-readiness-panel">
      <div class="sync-item-header">
        <strong>${escapeHtml(base || "Portal unavailable")}</strong>
        ${pill(blockers.length ? `${blockers.length} ${blockers.length === 1 ? "blocker" : "blockers"}` : "ready gate clear", blockers.length ? "amber" : "green")}
      </div>
      <div class="permission-chip-row">
        ${rows.map((row) => `
          <span class="permission-chip ${row.tone === "green" ? "active" : ""}" title="${escapeHtml(row.detail)}">
            ${escapeHtml(row.label)}: ${escapeHtml(row.value)}
          </span>
        `).join("")}
      </div>
      ${renderReadinessTriage(rows)}
      <p>${escapeHtml(deploymentReadinessSummary(rows, health))}</p>
      ${blockers.length ? `<p>${escapeHtml(`${blockers.length === 1 ? "Next blocker" : "Next blockers"}: ${blockers.map((row) => row.detail).join(" | ")}`)}</p>` : ""}
      ${attentionRows.length ? `
        <div class="readiness-note-list">
          ${attentionRows.map((row) => `
            <div class="readiness-note">
              <strong>${escapeHtml(row.label)}</strong>
              <span>
                ${escapeHtml(row.detail)}
                ${Array.isArray(row.details) && row.details.length ? `
                  <span class="readiness-note-sublist">
                    ${row.details.map((detail) => `<em>${escapeHtml(detail)}</em>`).join("")}
                  </span>
                ` : ""}
              </span>
            </div>
          `).join("")}
        </div>
      ` : ""}
    </div>
  `;
}

function renderReadinessTriage(rows = []) {
  const groups = [
    {
      title: "Requires review",
      tone: "amber",
      rows: rows.filter((row) => row.blocker || row.tone === "red")
    },
    {
      title: "Warnings",
      tone: "blue",
      rows: rows.filter((row) => !row.blocker && row.tone !== "green" && row.tone !== "red")
    },
    {
      title: "Ready",
      tone: "green",
      rows: rows.filter((row) => !row.blocker && row.tone === "green")
    }
  ];
  return `
    <div class="readiness-triage" aria-label="Deployment readiness triage">
      ${groups.map((group) => `
        <section class="readiness-triage-lane readiness-triage-lane--${group.tone}">
          <div class="readiness-triage-head">
            <span>${escapeHtml(group.title)}</span>
            <strong>${group.rows.length}</strong>
          </div>
          <div class="readiness-triage-list">
            ${group.rows.length ? group.rows.slice(0, 8).map((row) => `
              <details class="readiness-triage-item">
                <summary>
                  <span>${escapeHtml(row.label)}</span>
                  <b>${escapeHtml(row.value)}</b>
                </summary>
                <p>${escapeHtml(row.detail || "No detail supplied.")}</p>
                ${Array.isArray(row.details) && row.details.length ? `<div>${row.details.slice(0, 4).map((detail) => `<em>${escapeHtml(detail)}</em>`).join("")}</div>` : ""}
              </details>
            `).join("") : '<span class="readiness-triage-empty">None</span>'}
          </div>
        </section>
      `).join("")}
    </div>
  `;
}

function deploymentReadinessRows(health = {}, base = "") {
  const connected = health.status === "ok";
  const liveMode = (mode) => mode === "synapse-adapter";
  const formula = workflowApiFormulaParityLabel(health);
  const alignmentSummary = deploymentFieldAlignmentSummary(health.dataAlignment || {});
  const paymentAttributionEvidence = deploymentPaymentAttributionEvidenceSummary(health.dataAlignment?.paymentAttributionEvidence || {});
  const calculationParity = deploymentCalculationParitySummary(health.calculationParity || {});
  const financialGapLedger = deploymentFinancialGapLedgerSummary({
    financialProductionSignoff: health.financialProductionSignoff || null,
    calculationParity: health.calculationParity || {}
  });
  const financialActions = deploymentFinancialReadinessActionSummary(health.financialReadinessActions || {});
  const financialSignoff = deploymentFinancialSignoffSummary({
    financialProductionSignoff: health.financialProductionSignoff || null,
    financialReadinessActions: health.financialReadinessActions || {},
    calculationParity: health.calculationParity || {}
  });
  const preparedCaches = deploymentPreparedRuntimeCacheSummary(health.preparedRuntimeCaches || {}, health.warehouseRead || {});
  const refreshPlan = deploymentPreparedCacheRefreshPlanSummary(health.preparedCacheRefreshPlan || {});
  const refreshScheduler = deploymentPreparedCacheRefreshSchedulerSummary(health.preparedCacheRefreshScheduler || {}, health.warehouseRead || {});
  return [
    {
      label: "Portal status",
      value: connected ? "connected" : base ? "unreachable" : "not configured",
      detail: connected ? "The portal is ready." : "The portal status could not be confirmed.",
      tone: connected ? "green" : "red",
      blocker: !connected
    },
    {
      label: "Workflow DB",
      value: health.durableWorkflowStore ? "durable" : "not durable",
      detail: health.durableWorkflowStore ? "Workflow changes are being saved." : "Saving could not be confirmed.",
      tone: health.durableWorkflowStore ? "green" : "amber",
      blocker: connected && !health.durableWorkflowStore
    },
    {
      label: "Tracker",
      value: health.trackerQueryMode || "unknown",
      detail: liveMode(health.trackerQueryMode) && health.trackerQueryAdapter ? "Tracker is configured for the Synapse adapter." : "Tracker is not fully confirmed on the live Synapse adapter.",
      tone: liveMode(health.trackerQueryMode) && health.trackerQueryAdapter ? "green" : "amber",
      blocker: connected && (!liveMode(health.trackerQueryMode) || !health.trackerQueryAdapter)
    },
    {
      label: "Reports",
      value: health.reportQueryMode || "unknown",
      detail: liveMode(health.reportQueryMode) && health.reportQueryAdapter ? "Reports are configured for the Synapse adapter." : "Reports are not fully confirmed on the live Synapse adapter.",
      tone: liveMode(health.reportQueryMode) && health.reportQueryAdapter ? "green" : "amber",
      blocker: connected && (!liveMode(health.reportQueryMode) || !health.reportQueryAdapter)
    },
    {
      label: "Queue",
      value: health.claimQueueDrilldownMode || "unknown",
      detail: liveMode(health.claimQueueDrilldownMode) && health.claimQueueDrilldownAdapter ? "Queue drill-down is configured for the Synapse adapter." : "Queue drill-down is not fully confirmed on the live Synapse adapter.",
      tone: liveMode(health.claimQueueDrilldownMode) && health.claimQueueDrilldownAdapter ? "green" : "amber",
      blocker: connected && (!liveMode(health.claimQueueDrilldownMode) || !health.claimQueueDrilldownAdapter)
    },
    {
      label: "Fee Schedules",
      value: health.feeScheduleSourceMode || "unknown",
      detail: liveMode(health.feeScheduleSourceMode) && health.feeScheduleRepository ? "Fee schedules are configured for the Synapse adapter." : "Fee schedules are not fully confirmed on the live Synapse adapter.",
      tone: liveMode(health.feeScheduleSourceMode) && health.feeScheduleRepository ? "green" : "amber",
      blocker: connected && (!liveMode(health.feeScheduleSourceMode) || !health.feeScheduleRepository)
    },
    {
      label: "Raw Payments",
      value: health.rawPaymentMirror?.available ? "ready" : "tracker fallback",
      detail: health.rawPaymentMirror?.available
        ? "Client payment ledger can use the raw payment/disbursement mirror."
        : `Full raw payment history is not ready: the client ledger is using tracker-derived payment evidence until the raw payment/disbursement/payer/claim-insured mirror is synced${health.clientPaymentsFallbackReason ? ` (${health.clientPaymentsFallbackReason})` : ""}.`,
      tone: health.rawPaymentMirror?.available ? "green" : "amber",
      blocker: connected && !health.rawPaymentMirror?.available
    },
    {
      label: "Payment Cache",
      value: health.rawPaymentCache?.ready ? "ready" : "not ready",
      detail: health.rawPaymentCache?.ready
        ? `Client payment ledger repeat reads are cached for ${Math.round(Number(health.rawPaymentCache.ttlMs || 0) / 1000).toLocaleString()}s, bounded to ${Number(health.rawPaymentCache.maxEntries || 0).toLocaleString()} active entries.`
        : "Client payment ledger cache readiness is not confirmed.",
      tone: health.rawPaymentCache?.ready ? "green" : "amber",
      blocker: false
    },
    {
      label: "Field Alignment",
      value: alignmentSummary.value,
      detail: alignmentSummary.detail,
      details: alignmentSummary.details || [],
      tone: alignmentSummary.tone,
      blocker: connected && alignmentSummary.blocker
    },
    {
      label: "Formula Parity",
      value: formula.label || "not checked",
      detail: formula.detail || (formula.label ? `Formula parity status: ${formula.label}.` : "Formula parity evidence has not been supplied."),
      details: formula.details || [],
      tone: formula.tone === "connected" ? "green" : "amber",
      blocker: false
    },
    {
      label: "Attribution Evidence",
      value: paymentAttributionEvidence.value,
      detail: paymentAttributionEvidence.detail,
      details: paymentAttributionEvidence.details || [],
      tone: paymentAttributionEvidence.tone,
      blocker: false
    },
    {
      label: "Prepared Caches",
      value: preparedCaches.value,
      detail: preparedCaches.detail,
      details: preparedCaches.details || [],
      tone: preparedCaches.tone,
      blocker: connected && preparedCaches.blocker
    },
    {
      label: "Refresh Plan",
      value: refreshPlan.value,
      detail: refreshPlan.detail,
      details: refreshPlan.details || [],
      tone: refreshPlan.tone,
      blocker: connected && refreshPlan.blocker,
      alwaysShow: true
    },
    {
      label: "Refresh Scheduler",
      value: refreshScheduler.value,
      detail: refreshScheduler.detail,
      details: refreshScheduler.details || [],
      tone: refreshScheduler.tone,
      blocker: connected && refreshScheduler.blocker,
      alwaysShow: true
    },
    {
      label: "Calc Approval Gates",
      value: calculationParity.value,
      detail: calculationParity.detail,
      details: calculationParity.details || [],
      tone: calculationParity.tone,
      blocker: connected && calculationParity.blocker
    },
    {
      label: "Financial Gap Ledger",
      value: financialGapLedger.value,
      detail: financialGapLedger.detail,
      details: financialGapLedger.details || [],
      tone: financialGapLedger.tone,
      blocker: connected && financialGapLedger.blocker,
      alwaysShow: financialGapLedger.alwaysShow
    },
    {
      label: "Financial Signoff",
      value: financialSignoff.value,
      detail: financialSignoff.detail,
      details: financialSignoff.details || [],
      tone: financialSignoff.tone,
      blocker: connected && financialSignoff.blocker
    },
    {
      label: "Financial Actions",
      value: financialActions.value,
      detail: financialActions.detail,
      details: financialActions.details || [],
      tone: financialActions.tone,
      blocker: connected && financialActions.blocker
    }
  ];
}

function deploymentFinancialGapLedgerSummary({ financialProductionSignoff = null, calculationParity = {} } = {}) {
  const available = calculationParity && calculationParity.available !== false && (calculationParity.totalItems || calculationParity.unresolvedGateCount != null || financialProductionSignoff);
  if (!available) {
    return {
      value: "not checked",
      detail: "Financial comparison evidence is not available yet.",
      details: ["Run packet:financial:gaps after live alignment proof to generate the aggregate-only comparison ledger."],
      tone: "amber",
      blocker: false,
      alwaysShow: false
    };
  }
  const unresolved = Number(financialProductionSignoff?.unresolvedChecklistGateCount ?? calculationParity.unresolvedGateCount ?? 0);
  const total = Number(calculationParity.totalItems || (Number(calculationParity.approvedItems || 0) + unresolved) || 0);
  const approved = Number(calculationParity.approvedItems || Math.max(0, total - unresolved));
  const gateKeys = Array.isArray(financialProductionSignoff?.unresolvedChecklistKeys)
    ? financialProductionSignoff.unresolvedChecklistKeys.slice(0, 8)
    : Array.isArray(calculationParity.unresolvedGates)
      ? calculationParity.unresolvedGates.map((gate) => gate.key).filter(Boolean).slice(0, 8)
      : [];
  const packetCount = Number(financialProductionSignoff?.acceptancePacket?.draftPacketCount || 0);
  return {
    value: unresolved ? `${unresolved.toLocaleString()} open` : "mapped",
    detail: unresolved
      ? `Archived tracker comparison is mapped but not financially approved: ${approved.toLocaleString()} of ${total.toLocaleString()} checklist item${total === 1 ? "" : "s"} approved and ${unresolved.toLocaleString()} old-tracker gap${unresolved === 1 ? "" : "s"} still need resolution or accepted-variance packets.`
      : `Archived tracker comparison is mapped: ${approved.toLocaleString()} of ${total.toLocaleString()} checklist item${total === 1 ? "" : "s"} approved or accepted.`,
    details: [
      gateKeys.length ? `Open checklist keys: ${gateKeys.join(", ")}` : "",
      packetCount ? `${packetCount.toLocaleString()} draft acceptance packet${packetCount === 1 ? "" : "s"} waiting on business_owner and technical_reviewer approvals.` : "",
      "Run packet:financial:gaps -- --write to create the aggregate-only old-tracker-to-portal comparison ledger before changing or accepting money logic.",
      "This ledger is not approval and does not change money logic."
    ].filter(Boolean),
    tone: unresolved ? "amber" : "green",
    blocker: unresolved > 0,
    alwaysShow: true
  };
}

function deploymentFinancialSignoffSummary({ financialProductionSignoff = null, financialReadinessActions = {}, calculationParity = {} } = {}) {
  if (financialProductionSignoff && typeof financialProductionSignoff === "object") {
    const ready = financialProductionSignoff.ready === true;
    const openActions = Number(financialProductionSignoff.openActionCount || 0);
    const unresolvedGates = Number(financialProductionSignoff.unresolvedChecklistGateCount || 0);
    const actionKeys = Array.isArray(financialProductionSignoff.openActionKeys) ? financialProductionSignoff.openActionKeys.slice(0, 9) : [];
    const gateKeys = Array.isArray(financialProductionSignoff.unresolvedChecklistKeys) ? financialProductionSignoff.unresolvedChecklistKeys.slice(0, 9) : [];
    const clearancePlan = Array.isArray(financialProductionSignoff.clearancePlan) ? financialProductionSignoff.clearancePlan.slice(0, 5) : [];
    const acceptance = financialProductionSignoff.acceptancePacket && typeof financialProductionSignoff.acceptancePacket === "object"
      ? financialProductionSignoff.acceptancePacket
      : null;
    const acceptanceDetail = acceptance?.required
      ? `Acceptance packet required: ${acceptance.contract || "backend/financialSignoffAcceptanceContract.js"} with ${Number(acceptance.minimumDistinctApprovals || 2).toLocaleString()} distinct approvals, aggregate-only PHI-safe evidence, and no ${Array.isArray(acceptance.forbiddenContent) ? acceptance.forbiddenContent.slice(0, 4).join("/") : "PHI/secrets/row payloads"}.`
      : "";
    const intakeDetail = acceptance?.required
      ? "Run packet:financial:intake -- --write to create the ignored incoming/ folder for completed packets, then validate them with audit:financial:approvals."
      : "";
    return {
      value: ready ? "ready" : "open",
      detail: ready
        ? "Financial readiness actions and old-tracker calculation approval gates are clear or explicitly accepted."
        : `Financial production signoff remains open: ${openActions.toLocaleString()} financial action${openActions === 1 ? "" : "s"} and ${unresolvedGates.toLocaleString()} old-tracker approval/parity gate${unresolvedGates === 1 ? "" : "s"} remain unresolved or unaccepted.`,
      details: ready ? [] : [
        actionKeys.length ? `Open actions: ${actionKeys.join(", ")}` : "",
        gateKeys.length ? `Checklist gates: ${gateKeys.join(", ")}` : "",
        ...clearancePlan.map((step) => `Step ${Number(step.step || 0).toLocaleString()}: ${step.owner || "Owner"} - ${step.title || step.key || "Financial signoff step"} - ${step.nextStep || "resolve before signoff"}`),
        acceptanceDetail,
        intakeDetail,
        `Use ${financialProductionSignoff.source || "FINANCIAL_PRODUCTION_SIGNOFF_MATRIX.md"} and audit:live:alignment before certifying money logic.`
      ].filter(Boolean),
      tone: ready ? "green" : "amber",
      blocker: !ready
    };
  }
  if (!financialReadinessActions || financialReadinessActions.available === false || !calculationParity || calculationParity.available === false) {
    return {
      value: "not checked",
      detail: "Financial production signoff cannot be evaluated until all readiness checks are available.",
      details: [],
      tone: "amber",
      blocker: true
    };
  }
  const openActions = Number(financialReadinessActions.openActionCount || 0);
  const unresolvedGates = Number(calculationParity.unresolvedGateCount || 0);
  const actionKeys = Array.isArray(financialReadinessActions.actions)
    ? financialReadinessActions.actions.map((action) => action.key).filter(Boolean).slice(0, 9)
    : [];
  const gateKeys = Array.isArray(calculationParity.unresolvedGates)
    ? calculationParity.unresolvedGates.map((gate) => gate.key).filter(Boolean).slice(0, 9)
    : [];
  if (financialReadinessActions.ready === true && calculationParity.ready === true && openActions === 0 && unresolvedGates === 0) {
    return {
      value: "ready",
      detail: "Financial readiness actions and old-tracker calculation approval gates are clear or explicitly accepted.",
      details: [],
      tone: "green",
      blocker: false
    };
  }
  return {
    value: "open",
    detail: `Financial production signoff remains open: ${openActions.toLocaleString()} financial action${openActions === 1 ? "" : "s"} and ${unresolvedGates.toLocaleString()} old-tracker approval/parity gate${unresolvedGates === 1 ? "" : "s"} remain unresolved or unaccepted.`,
    details: [
      actionKeys.length ? `Open actions: ${actionKeys.join(", ")}` : "",
      gateKeys.length ? `Checklist gates: ${gateKeys.join(", ")}` : "",
      "Use FINANCIAL_PRODUCTION_SIGNOFF_MATRIX.md and audit:live:alignment before certifying money logic."
    ].filter(Boolean),
    tone: "amber",
    blocker: true
  };
}

function deploymentFinancialReadinessActionSummary(financialReadinessActions = {}) {
  if (!financialReadinessActions || financialReadinessActions.available === false) {
    return {
      value: "not checked",
      detail: "The financial readiness action plan is not available yet.",
      details: [],
      tone: "amber",
      blocker: true
    };
  }
  const count = Number(financialReadinessActions.openActionCount || 0);
  const actions = Array.isArray(financialReadinessActions.actions) ? financialReadinessActions.actions : [];
  if (!count) {
    return {
      value: "clear",
      detail: "No open financial readiness actions are exposed by current alignment and parity evidence.",
      details: [],
      tone: "green",
      blocker: false
    };
  }
  return {
    value: `${count.toLocaleString()} open`,
    detail: `${count.toLocaleString()} financial readiness action${count === 1 ? "" : "s"} must be resolved or explicitly accepted before production financial signoff.`,
    details: actions.slice(0, 8).map((entry) => `${entry.priority || "P0"} · ${entry.owner || "Owner"} · ${entry.title || entry.key || "Action"} · ${entry.evidence || "evidence pending"} · ${entry.nextStep || "resolve before signoff"}`),
    tone: "amber",
    blocker: true
  };
}

function deploymentPaymentAttributionEvidenceSummary(paymentEvidence = {}) {
  if (!paymentEvidence || paymentEvidence.available === false) {
    return {
      value: "not checked",
      detail: `Prepared payment attribution evidence is not available yet${paymentEvidence?.reason ? ` (${paymentEvidence.reason})` : ""}. Run refresh:payment:attribution after raw payment and tracker mirror refreshes.`,
      details: [],
      tone: "amber",
      blocker: false
    };
  }
  const conflictRows = Number(paymentEvidence.sameExternalIdConflictRows || 0);
  const unmatchedRows = Number(paymentEvidence.unmatchedExternalPayerRows || 0);
  const primaryRows = Number(paymentEvidence.uniquePrimaryExternalMatchRows || 0);
  const secondaryRows = Number(paymentEvidence.uniqueSecondaryExternalMatchRows || 0);
  const noPositiveEvidenceRows = Number(paymentEvidence.noPositivePaymentEvidenceRows || 0);
  const cacheFreshness = paymentEvidence.cacheFreshness || {};
  const ready = paymentEvidence.ready === true && conflictRows === 0 && unmatchedRows === 0;
  const freshnessText = cacheFreshness.available
    ? `Prepared cache refreshed ${Number(cacheFreshness.ageHours || 0).toLocaleString()}h ago; max age ${Number(cacheFreshness.maxAgeHours || 24).toLocaleString()}h.`
    : "Prepared cache refresh time is not available.";
  const topBuckets = Array.isArray(paymentEvidence.topBuckets)
    ? paymentEvidence.topBuckets
      .filter((bucket) => /same_external_id_conflict|unmatched_external_payer/i.test(String(bucket.status || "")))
      .slice(0, 4)
    : [];
  return {
    value: ready ? "clear" : "open",
    detail: ready
      ? `Prepared payment attribution evidence is clear. ${primaryRows.toLocaleString()} primary and ${secondaryRows.toLocaleString()} secondary external-payer match rows are summarized. ${freshnessText}`
      : `Prepared attribution evidence remains open: ${conflictRows.toLocaleString()} same-external-ID conflict rows and ${unmatchedRows.toLocaleString()} unmatched external-payer rows. ${freshnessText}`,
    details: [
      `Matched evidence: ${primaryRows.toLocaleString()} primary, ${secondaryRows.toLocaleString()} secondary, ${noPositiveEvidenceRows.toLocaleString()} no positive payment evidence.`,
      ...topBuckets.map((bucket) => {
        const rows = formatNounCount(Number(bucket.rows || 0), "row");
        const amount = Number(bucket.amountSum || 0);
        // Was `Math.abs(amount)` behind a hand-written "$" - a negative bucket sum
        // (a credit) rendered as a positive amount, and the locale was unpinned.
        const amountText = amount ? `, ${money(amount)}` : "";
        return `${bucket.status || "bucket"}: ${portalCompanyLabel(bucket.orgId)}, proc ${bucket.procedure || "unknown"}, ${rows}${amountText}.`;
      })
    ],
    tone: ready ? "green" : "amber",
    blocker: false
  };
}

function deploymentPreparedCacheRefreshPlanSummary(preparedCacheRefreshPlan = {}) {
  if (!preparedCacheRefreshPlan || preparedCacheRefreshPlan.available === false) {
    return {
      value: "not ready",
      detail: preparedCacheRefreshPlan?.errors?.length
        ? `Prepared cache refresh plan is invalid: ${preparedCacheRefreshPlan.errors.slice(0, 3).join("; ")}.`
        : "The prepared refresh plan is not available yet.",
      details: [],
      tone: "amber",
      blocker: true
    };
  }
  const jobs = Array.isArray(preparedCacheRefreshPlan.jobs) ? preparedCacheRefreshPlan.jobs : [];
  const command = preparedCacheRefreshPlan.schedulerCommand || "npm.cmd --prefix TMS_MASTER_PORTAL run refresh:prepared:all";
  return {
    value: preparedCacheRefreshPlan.ready ? "ready" : "attention",
    detail: `${jobs.length.toLocaleString()} prepared cache refresh job${jobs.length === 1 ? "" : "s"} are available through ${command}.`,
    details: [
      `Contract: ${preparedCacheRefreshPlan.contract || "backend/preparedCacheRefreshContract.js"}`,
      ...jobs.slice(0, 6).map((job) => `${Number(job.order || 0).toLocaleString()}. ${job.npmScript || job.key} -> ${job.cacheTable}${job.secondaryCacheTable ? ` + ${job.secondaryCacheTable}` : ""}`),
      Array.isArray(preparedCacheRefreshPlan.requiredProofCommands) && preparedCacheRefreshPlan.requiredProofCommands.length
        ? `Proof: ${preparedCacheRefreshPlan.requiredProofCommands.slice(0, 4).join(" | ")}`
        : ""
    ].filter(Boolean),
    tone: preparedCacheRefreshPlan.ready ? "green" : "amber",
    blocker: !preparedCacheRefreshPlan.ready
  };
}

function deploymentRetiredPreparedSummary(check, warehouseRead) {
  if (check?.retired !== true || String(warehouseRead?.source || "").trim().toLowerCase() !== "warehouse") return null;
  return { value: "not used", detail: "This older refresh check does not apply to the current reporting source. Check current report freshness separately.", details: [], tone: "slate", blocker: false };
}

function deploymentPreparedCacheRefreshSchedulerSummary(preparedCacheRefreshScheduler = {}, warehouseRead = {}) {
  const retired = deploymentRetiredPreparedSummary(preparedCacheRefreshScheduler, warehouseRead);
  if (retired) return retired;
  if (!preparedCacheRefreshScheduler || preparedCacheRefreshScheduler.available === false) {
    return {
      value: "not checked",
      detail: "Scheduled refresh status is not available yet.",
      details: [],
      tone: "amber",
      blocker: true
    };
  }
  const ready = preparedCacheRefreshScheduler.ready === true;
  const missing = Array.isArray(preparedCacheRefreshScheduler.missingSettings)
    ? preparedCacheRefreshScheduler.missingSettings
    : [];
  const errors = Array.isArray(preparedCacheRefreshScheduler.errors)
    ? preparedCacheRefreshScheduler.errors
    : [];
  const approved = Array.isArray(preparedCacheRefreshScheduler.approvedRunners)
    ? preparedCacheRefreshScheduler.approvedRunners
    : [];
  const runner = preparedCacheRefreshScheduler.runner || "not configured";
  const schedule = preparedCacheRefreshScheduler.schedule || "no cron set";
  const outcome = String(preparedCacheRefreshScheduler.latestOutcome || "");
  const staleJobs = Array.isArray(preparedCacheRefreshScheduler.retainedStaleJobs)
    ? preparedCacheRefreshScheduler.retainedStaleJobs
    : [];
  const unavailableJobs = Array.isArray(preparedCacheRefreshScheduler.unavailableJobs)
    ? preparedCacheRefreshScheduler.unavailableJobs
    : [];
  const degraded = outcome === "FAIL_SAFE_DEGRADED";
  const failed = outcome === "FAILED";
  const value = failed ? "failed" : degraded ? "degraded" : outcome === "SUCCESS" ? "healthy" : ready ? "ready" : "not configured";
  return {
    value,
    detail: failed
      ? "The latest unattended prepared-cache refresh failed. The prior accepted generation remains authoritative; operations should review the durable run journal."
      : degraded
        ? `The latest unattended refresh accepted a new Core generation while explicitly retaining ${staleJobs.length} prior complete derivative or Cash generation${staleJobs.length === 1 ? "" : "s"} as stale.`
        : ready
          ? `Prepared caches are scheduled through ${runner} on ${schedule} (${preparedCacheRefreshScheduler.timezone || "America/New_York"}).`
      : "Prepared cache refresh is not yet proven as an automated production scheduler; normal speed currently depends on the prepared caches being refreshed manually or externally.",
    details: [
      `Command: ${preparedCacheRefreshScheduler.command || "npm.cmd --prefix TMS_MASTER_PORTAL run refresh:prepared:all"}`,
      `Approved runners: ${approved.slice(0, 4).join(", ") || "not supplied"}`,
      missing.length ? `Missing settings: ${missing.join(", ")}` : "",
      errors.length ? `Errors: ${errors.join(", ")}` : "",
      preparedCacheRefreshScheduler.owner ? `Owner: ${preparedCacheRefreshScheduler.owner}` : "",
      outcome ? `Latest outcome: ${outcome}` : "",
      preparedCacheRefreshScheduler.lastAttemptedUtc ? `Last attempt: ${preparedCacheRefreshScheduler.lastAttemptedUtc}` : "",
      preparedCacheRefreshScheduler.lastSuccessUtc ? `Last success: ${preparedCacheRefreshScheduler.lastSuccessUtc}` : "",
      staleJobs.length ? `Retained stale (not current): ${staleJobs.join(", ")}` : "",
      unavailableJobs.length ? `Unavailable (no prior accepted generation): ${unavailableJobs.join(", ")}` : "",
      preparedCacheRefreshScheduler.refreshRunId ? `Run: ${preparedCacheRefreshScheduler.refreshRunId}` : "",
      preparedCacheRefreshScheduler.releaseCommit ? `Release: ${preparedCacheRefreshScheduler.releaseCommit.slice(0, 12)}` : "",
      preparedCacheRefreshScheduler.proofUrl ? `Proof: ${preparedCacheRefreshScheduler.proofUrl}` : "",
      preparedCacheRefreshScheduler.rule || ""
    ].filter(Boolean),
    tone: failed ? "red" : degraded ? "amber" : ready ? "green" : "amber",
    blocker: !ready
  };
}

function deploymentPreparedRuntimeCacheSummary(preparedRuntimeCaches = {}, warehouseRead = {}) {
  const retired = deploymentRetiredPreparedSummary(preparedRuntimeCaches, warehouseRead);
  if (retired) return retired;
  if (!preparedRuntimeCaches || preparedRuntimeCaches.configured === false || preparedRuntimeCaches.available === false) {
    return {
      value: "not ready",
      detail: preparedRuntimeCaches?.reason
        ? `Prepared runtime cache health is unavailable: ${preparedRuntimeCaches.reason}.`
        : "Prepared report status is not available yet.",
      details: [],
      tone: "amber",
      blocker: true
    };
  }
  const caches = Array.isArray(preparedRuntimeCaches.caches) ? preparedRuntimeCaches.caches : [];
  const notReadyCaches = caches.filter((cache) => !cache.ready);
  const details = caches.map((cache) => {
    const count = cache.facetCount != null
      ? formatNounCount(Number(cache.facetCount || 0), "facet")
      : formatNounCount(Number(cache.datasetCount || 0), "dataset");
    const freshness = preparedCacheFreshnessText(cache.cacheFreshness);
    const status = cache.ready ? "ready" : (cache.warnings || []).join(", ") || cache.reason || "not ready";
    return `${cache.label || cache.key || "Prepared cache"}: ${status}; ${count}${freshness ? `; ${freshness}` : ""}`;
  });
  if (!notReadyCaches.length) {
    return {
      value: "ready",
      detail: `${caches.length.toLocaleString()} prepared cache path${caches.length === 1 ? "" : "s"} are present and fresh for normal UI/report use.`,
      details,
      tone: "green",
      blocker: false
    };
  }
  return {
    value: `${notReadyCaches.length.toLocaleString()} attention`,
    detail: `${notReadyCaches.length.toLocaleString()} prepared cache path${notReadyCaches.length === 1 ? "" : "s"} need attention before the portal can be treated as consistently fast.`,
    details,
    tone: "amber",
    blocker: true
  };
}

function deploymentCalculationParitySummary(calculationParity = {}) {
  if (!calculationParity || calculationParity.available === false) {
    return {
      value: "not checked",
      detail: "Calculation comparison approvals are not available yet.",
      details: [],
      tone: "amber",
      blocker: true
    };
  }
  const unresolved = Number(calculationParity.unresolvedGateCount || 0);
  const total = Number(calculationParity.totalItems || 0);
  const approved = Number(calculationParity.approvedItems || 0);
  const approvalBreakdown = calculationParity.approvalBreakdown || {};
  const breakdownText = Object.entries(approvalBreakdown)
    .filter(([state]) => state !== "approved")
    .map(([state, count]) => `${Number(count || 0).toLocaleString()} ${state}`)
    .join("; ");
  const details = Array.isArray(calculationParity.unresolvedGates)
    ? calculationParity.unresolvedGates.slice(0, 8).map((gate) => `${gate.area || gate.key || "calculation"} · ${gate.approvalState || "gate"} · ${gate.productionGate || "approval or parity evidence required"}`)
    : [];
  if (!unresolved) {
    return {
      value: "clear",
      detail: `${approved.toLocaleString()} of ${total.toLocaleString()} old-tracker calculation parity checklist items are approved or accepted.`,
      details: [],
      tone: "green",
      blocker: false
    };
  }
  return {
    value: `${unresolved.toLocaleString()} unresolved`,
    detail: `${approved.toLocaleString()} of ${total.toLocaleString()} old-tracker calculation parity checklist items are approved. Remaining gates: ${breakdownText || `${unresolved.toLocaleString()} unresolved`}.`,
    details,
    tone: "amber",
    blocker: true
  };
}

function deploymentFieldAlignmentSummary(alignment = {}) {
  if (!alignment || !alignment.configured) {
    return {
      value: "not checked",
      detail: "Financial alignment evidence is not available yet.",
      tone: "amber",
      blocker: true
    };
  }
  if (!alignment.available) {
    return {
      value: "check failed",
      detail: `The portal could not verify the tracker financial fields${alignment.reason ? ` (${alignment.reason})` : ""}.`,
      tone: "amber",
      blocker: true
    };
  }
  const coverage = alignment.candidateCoverage || {};
  const coverageFreshness = preparedCacheFreshnessText(coverage.cacheFreshness);
  const totalRows = Number(alignment.totalRows || 0);
  const expectedRows = Number(alignment.rowsWithExpected || 0);
  const primaryRateRows = Number(alignment.rowsWithPrimaryRate || 0);
  const zeroExpected = Number(alignment.zeroExpectedPositiveBalance || 0);
  const uniqueRows = Number(coverage.uniquePrimaryOnlyRows || 0) + Number(coverage.uniqueSecondaryPresentRows || 0);
  const unresolvedRows = Number(coverage.noFeeCandidateRows || 0) + Number(coverage.ambiguousCandidateRows || 0);
  const topBucket = Array.isArray(coverage.topStatusOrgBuckets) && coverage.topStatusOrgBuckets.length ? coverage.topStatusOrgBuckets[0] : null;
  const zeroGap = coverage.zeroExpectedGap || {};
  const zeroGapBuckets = Array.isArray(zeroGap.topStatusOrgBuckets) ? zeroGap.topStatusOrgBuckets : [];
  const topZeroGap = zeroGapBuckets.length ? zeroGapBuckets[0] : null;
  const procedureGapBuckets = Array.isArray(zeroGap.topProcedureOrgBuckets) ? zeroGap.topProcedureOrgBuckets : [];
  const topProcedureGap = procedureGapBuckets.length ? procedureGapBuckets[0] : null;
  const topSetupDetails = procedureGapBuckets.slice(0, 5).map((bucket) => `${bucket.status || "unresolved"} · ${portalCompanyLabel(bucket.orgId)} · proc ${bucket.procedure || "unknown"} · ${formatNounCount(Number(bucket.rows || 0), "row")}${Number(bucket.balance || 0) ? ` · ${money(bucket.balance)}` : ""}`);
  const zeroGapKnown = Number(zeroGap.noFeeCandidateRows || 0)
    + Number(zeroGap.ambiguousCandidateRows || 0)
    + Number(zeroGap.secondaryPresentRows || 0)
    + Number(zeroGap.primaryOnlyUniqueRows || 0);
  if (!alignment.ready) {
    return {
      value: "rates blocked",
      detail: `Expected reimbursement is not ready: ${expectedRows.toLocaleString()} expected rows, ${primaryRateRows.toLocaleString()} primary-rate rows, ${zeroExpected.toLocaleString()} zero-expected open-balance rows${alignment.reason ? `; blocker ${alignment.reason}` : ""}.`,
      tone: "amber",
      blocker: true
    };
  }
  if (zeroExpected > 0 || unresolvedRows > 0) {
    const expectedRowsText = `${formatNounCount(expectedRows, "row")} ${expectedRows === 1 ? "has" : "have"} expected reimbursement`;
    const uniqueRowsText = `${formatNounCount(uniqueRows, "row")} ${uniqueRows === 1 ? "has" : "have"} unique primary-rate candidates`;
    const unresolvedRowsText = `${formatNounCount(unresolvedRows, "row")} still ${unresolvedRows === 1 ? "needs" : "need"} fee coverage or state/POS/network disambiguation`;
    const zeroExpectedText = `${formatNounCount(zeroExpected, "open-balance row")} still ${zeroExpected === 1 ? "has" : "have"} zero expected reimbursement`;
    const topText = topProcedureGap
      ? ` Top setup bucket: ${topProcedureGap.status || "unresolved"} ${portalCompanyLabel(topProcedureGap.orgId)} procedure ${topProcedureGap.procedure || "unknown"} (${formatNounCount(Number(topProcedureGap.rows || 0), "row")}).`
      : topZeroGap
      ? ` Open-balance gap: ${topZeroGap.status || "unresolved"} ${portalCompanyLabel(topZeroGap.orgId)} (${formatNounCount(Number(topZeroGap.rows || 0), "row")}).`
      : topBucket
        ? ` Top gap: ${topBucket.status || "unresolved"} ${portalCompanyLabel(topBucket.orgId)} (${formatNounCount(Number(topBucket.rows || 0), "row")}).`
        : "";
    const zeroGapText = zeroGapKnown
      ? ` Zero-expected breakdown: ${formatNounCount(Number(zeroGap.noFeeCandidateRows || 0), "no fee row")}, ${formatNounCount(Number(zeroGap.ambiguousCandidateRows || 0), "ambiguous row")}, ${formatNounCount(Number(zeroGap.secondaryPresentRows || 0), "secondary pending row")}.`
      : "";
    return {
      value: "guarded warnings",
      detail: `${expectedRowsText} across ${formatNounCount(totalRows, "mirrored row")}. ${uniqueRowsText}; ${unresolvedRowsText}; ${zeroExpectedText}.${zeroGapText}${topText}${coverageFreshness ? ` ${coverageFreshness}` : ""}`,
      details: topSetupDetails,
      tone: "amber",
      blocker: false
    };
  }
  return {
    value: "ready",
    detail: `Tracker financial fields are populated across ${totalRows.toLocaleString()} mirrored rows with no zero-expected open-balance warnings.`,
    tone: "green",
    blocker: false
  };
}

function preparedCacheFreshnessText(freshness = {}) {
  if (!freshness || typeof freshness !== "object") return "";
  if (freshness.available === false || freshness.stale) {
    return `Prepared cache freshness needs attention${freshness.ageHours != null ? ` (${Number(freshness.ageHours).toLocaleString()}h old)` : ""}.`;
  }
  if (freshness.ageHours != null) return `Prepared cache refreshed ${Number(freshness.ageHours).toLocaleString()}h ago.`;
  return "";
}

function deploymentReadinessSummary(rows = [], health = {}) {
  if (health.error) return `Health check failed: ${health.error}`;
  const blockers = rows.filter((row) => row.blocker).length;
  if (blockers) return "The deployment is reachable, but one or more live-readiness gates still need attention.";
  const mismatches = health.adapterModePlanSummary?.mismatchedSurfaces || [];
  if (mismatches.length) return `Runtime is live, but adapter plan evidence still needs cleanup for: ${mismatches.join(", ")}.`;
  return "Core live deployment gates are clear for the current test environment.";
}

function renderTenantBoundaryReview() {
  if (!els.tenantBoundaryReview) return;
  const rows = tenantBoundaryReviewRows();
  const summary = {
    denials: rows.filter((row) => row.kind === "Boundary denial").length,
    narrowed: rows.filter((row) => row.narrowed).length,
    redacted: rows.filter((row) => row.redacted).length,
    crossTenant: rows.filter((row) => row.crossTenantApproved).length
  };
  const topRows = rows.slice(0, 8);
  els.tenantBoundaryReview.innerHTML = `
    <div class="tenant-boundary-heading">
      <div>
        <h4>Tenant Boundary Review</h4>
        <p>Denied, narrowed, redacted, and cross-tenant decisions that prove tenant isolation is being enforced.</p>
      </div>
      ${pill(formatNounCount(rows.length, "event"), rows.length ? "amber" : "green")}
    </div>
    <div class="tenant-boundary-metrics">
      ${[
        ["Boundary Denials", summary.denials],
        ["Narrowed Queries", summary.narrowed],
        ["Redacted Responses", summary.redacted],
        ["Cross-Tenant Evidence", summary.crossTenant]
      ].map(([label, value]) => `<div><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`).join("")}
    </div>
    <div class="tenant-boundary-list">
      ${topRows.length ? topRows.map((row) => `
        <div class="tenant-boundary-item">
          <div class="sync-item-header">
            <strong>${escapeHtml(row.route || row.targetType || "access boundary")}</strong>
            ${pill(row.kind, row.tone)}
          </div>
          <p>${escapeHtml(row.createdAtUtc || "")} · ${escapeHtml(row.userId || "unknown")} · ${escapeHtml(row.permission || "")}</p>
          <p>${escapeHtml(portalCompanyLabel(row.orgId, clientActionCompanyName(row.tenantId, row.orgId) || "Company name unavailable"))}</p>
          <p>${escapeHtml(row.evidence)}</p>
        </div>
      `).join("") : empty("No tenant-boundary events match the current filters.")}
    </div>
  `;
}

function tenantBoundaryReviewRows() {
  const failures = filteredAuthorizationFailures()
    .filter((failure) => isTenantBoundarySignal(failure))
    .map((failure) => ({
      ...failure,
      kind: "Boundary denial",
      tone: "red",
      evidence: failure.reason || "authorization denied"
    }));
  const decisions = filteredAccessDecisions()
    .filter((decision) => isTenantBoundarySignal(decision))
    .map((decision) => {
      const crossTenantApproved = isCrossTenantApprovedDecision(decision);
      const kind = decision.redacted
        ? "Redacted"
        : decision.narrowed
          ? "Narrowed"
          : crossTenantApproved
            ? "Cross-tenant approved"
            : decision.decision === "deny"
              ? "Boundary denial"
              : "Boundary evidence";
      return {
        ...decision,
        kind,
        tone: decision.decision === "deny" ? "red" : crossTenantApproved ? "amber" : "blue",
        crossTenantApproved,
        evidence: [decision.reason, accessDecisionEvidenceSummary(decision)].filter(Boolean).join(" · ") || "access decision evidence"
      };
    });
  return [...failures, ...decisions]
    .sort((a, b) => timestampValue(b.createdAtUtc || b.at) - timestampValue(a.createdAtUtc || a.at));
}

function isTenantBoundarySignal(row = {}) {
  const searchable = [
    row.reason,
    row.route,
    row.permission,
    row.targetType,
    row.targetId,
    ...(row.deniedPermissions || []),
    ...(row.appliedGrantIds || []),
    ...(row.appliedOverrideIds || [])
  ].join(" ").toLowerCase();
  return row.redacted
    || row.narrowed
    || row.scope?.requestedAllOrgs
    || row.scope?.crossTenant
    || row.scope?.effectiveOrgIds?.length
    || isCrossTenantApprovedDecision(row)
    || ["tenant", "scope", "out_of_scope", "cross", "redact", "narrow"].some((token) => searchable.includes(token));
}

function isCrossTenantApprovedDecision(row = {}) {
  return Boolean(
    row.crossTenantApproved
    || row.crossTenant
    || row.scope?.crossTenant
    || row.scope?.requestedAllOrgs
    || String(row.tenantId || "").toLowerCase() === "all-tenants"
    || String(row.orgId || "").toLowerCase() === "all-orgs"
  ) && row.decision !== "deny";
}

function renderPermissionMatrix() {
  if (!els.permissionMatrixRows) return;
  const rows = Object.entries(roleProfiles).map(([roleKey, profile]) => {
    const permissions = new Set(profile.permissions || []);
    const grantedGroups = permissionGroups.map((group) => {
      const granted = group.permissions.filter((permission) => permissions.has(permission)).length;
      return { ...group, granted };
    });
    return { roleKey, profile, grantedGroups };
  });
  els.permissionMatrixRows.innerHTML = rows.map(({ roleKey, profile, grantedGroups }) => `
    <div class="sync-item permission-matrix-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(profile.label || roleKey)}</strong>
        ${pill(scopeLabel(profile.scope), profile.scope?.crossTenant ? "amber" : "green")}
      </div>
      <div class="permission-chip-row">
        ${grantedGroups.map((group) => `
          <span class="permission-chip ${group.granted ? "active" : ""}" title="${escapeHtml(group.permissions.join(", "))}">
            ${escapeHtml(group.label)} ${group.granted}/${group.permissions.length}
          </span>
        `).join("")}
      </div>
    </div>
  `).join("");
}

function renderPermissionGroups() {
  if (!els.permissionGroupRows) return;
  els.permissionGroupRows.innerHTML = permissionGroups.map((group) => `
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(group.label)}</strong>
        ${pill(formatNounCount(group.permissions.length, "permission"), "gray")}
      </div>
      <p>${escapeHtml(group.permissions.join(", "))}</p>
    </div>
  `).join("");
}

function scopeLabel(scope = {}) {
  if (scope.crossTenant) return "cross-tenant";
  if (Array.isArray(scope.orgIds) && scope.orgIds.length) return formatNounCount(scope.orgIds.length, "org");
  if (Array.isArray(scope.providerNames) && scope.providerNames.length) return "provider panel";
  if (scope.assignedOnly) return "assigned only";
  return "scoped";
}

function renderAuthorizationFailures() {
  if (!els.authorizationFailureRows) return;
  const failures = filteredAuthorizationFailures().slice(0, 20);
  els.authorizationFailureRows.innerHTML = failures.length ? failures.map((failure) => `
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(failure.route || "unknown route")}</strong>
        ${pill(failure.reason || "denied", "red")}
      </div>
      <p>${escapeHtml(failure.createdAtUtc || failure.at || "")} · ${escapeHtml(failure.userId || "unknown")} · ${escapeHtml(failure.permission || "")}</p>
      <p>${escapeHtml(portalCompanyLabel(failure.orgId, clientActionCompanyName(failure.tenantId, failure.orgId) || "Company name unavailable"))}</p>
    </div>
  `).join("") : empty("No authorization failures match the current filters.");
}

function renderAccessDecisions() {
  if (!els.accessDecisionRows) return;
  const decisions = filteredAccessDecisions().slice(0, 25);
  els.accessDecisionRows.innerHTML = decisions.length ? decisions.map((decision) => `
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(decision.route || decision.targetType || "access decision")}</strong>
        ${pill(accessDecisionLabel(decision), accessDecisionTone(decision))}
      </div>
      <p>${escapeHtml(decision.createdAtUtc || "")} · ${escapeHtml(decision.userId || "unknown")} · ${escapeHtml(decision.permission || "")}</p>
      <p>${escapeHtml(`${portalCompanyLabel(decision.orgId, clientActionCompanyName(decision.tenantId, decision.orgId) || "Company name unavailable")} · ${decision.targetType || ""} ${decision.targetId || ""}`)}</p>
      <p>${escapeHtml(accessDecisionEvidenceSummary(decision))}</p>
    </div>
  `).join("") : empty("No access decisions match the current filters.");
}

function renderAccessAdminPreviews() {
  if (!els.accessAdminPreviewRows) return;
  const previews = (state.accessAdminPreviews || []).slice(-12).reverse();
  els.accessAdminPreviewRows.innerHTML = previews.length ? previews.map((preview) => `
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(preview.commandType || "access admin preview")}</strong>
        ${pill(preview.approval?.status || "blocked", preview.approval?.approved ? "green" : "amber")}
      </div>
      <p>${escapeHtml(preview.createdAt || "")} · ${escapeHtml(preview.targetId || "")} · ${escapeHtml(preview.row?.permission_key || preview.row?.override_id || "")}</p>
      <p>${escapeHtml(preview.execution?.reason || "managed write disabled")}</p>
    </div>
  `).join("") : empty("No grant or override previews have been created in this session.");
}

function bindSecurityFilter(element, key, property = "value") {
  if (!element) return;
  element.addEventListener("input", () => {
    state.securityReviewFilters = {
      ...(state.securityReviewFilters || {}),
      [key]: property === "checked" ? Boolean(element.checked) : element.value
    };
    state.securityReviewLoadedAt = "";
    saveState();
    renderSecurityReview();
  });
}

function filteredAuthorizationFailures() {
  return (state.authorizationFailures || [])
    .filter((failure) => securityRowMatchesFilters({
      orgId: failure.orgId,
      decision: "deny",
      permission: failure.permission,
      createdAtUtc: failure.createdAtUtc || failure.at
    }))
    .sort((a, b) => timestampValue(b.createdAtUtc || b.at) - timestampValue(a.createdAtUtc || a.at));
}

function filteredAccessDecisions() {
  return (state.accessDecisions || [])
    .filter((decision) => securityRowMatchesFilters(decision))
    .sort((a, b) => timestampValue(b.createdAtUtc) - timestampValue(a.createdAtUtc));
}

function securityRowMatchesFilters(row = {}) {
  const filters = state.securityReviewFilters || {};
  if (filters.orgId && filters.orgId !== "All" && row.orgId !== filters.orgId) return false;
  if (filters.decision === "redacted" && !row.redacted) return false;
  else if (filters.decision === "narrowed" && !row.narrowed) return false;
  else if (filters.decision && filters.decision !== "All" && row.decision !== filters.decision) return false;
  if (filters.permission && !String(row.permission || "").toLowerCase().includes(String(filters.permission).toLowerCase())) return false;
  if (filters.fromDate && timestampValue(row.createdAtUtc) < timestampValue(filters.fromDate)) return false;
  if (filters.toDate && timestampValue(row.createdAtUtc) > timestampValue(filters.toDate)) return false;
  return true;
}

function refreshSecurityReview({ force = false } = {}) {
  if (!canAccessView("security")) return;
  if (!force && state.securityReviewLoadedAt && Date.now() - timestampValue(state.securityReviewLoadedAt) < 30000) return;
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") {
    ensureLocalSecurityReviewData();
    renderSecuritySummary();
    renderTenantBoundaryReview();
    renderAuthorizationFailures();
    renderAccessDecisions();
    return;
  }
  const token = workflowApiToken();
  const headers = { ...(token ? { authorization: `Bearer ${token}` } : {}) };
  const failuresQuery = securityReviewFilterQuery("failures");
  const decisionsQuery = securityReviewFilterQuery("decisions");
  const requestParts = { base, failuresQuery, decisionsQuery };
  const requestKey = workflowAccessScopedRequestKey(requestParts);
  const requestStillCurrent = () => (
    securityReviewRequestKey === requestKey
    && workflowAccessScopedRequestKey(requestParts) === requestKey
  );
  securityReviewRequestKey = requestKey;
  Promise.all([
    fetch(`${base}/api/security/authorization-failures?${failuresQuery}`, { method: "GET", cache: "no-store", headers })
      .then((response) => response.json().catch(() => ({})))
      .catch(() => ({})),
    fetch(`${base}/api/security/access-decisions?${decisionsQuery}`, { method: "GET", cache: "no-store", headers })
      .then((response) => response.json().catch(() => ({})))
      .catch(() => ({}))
  ]).then(([failuresBody, decisionsBody]) => {
    if (!requestStillCurrent()) return;
    if (Array.isArray(failuresBody.failures)) state.authorizationFailures = failuresBody.failures;
    if (Array.isArray(decisionsBody.accessDecisions)) state.accessDecisions = decisionsBody.accessDecisions;
    state.securityReviewLoadedAt = new Date().toISOString();
    saveState();
  });
}

function securityReviewFilterQuery(kind) {
  const filters = { ...(state.securityReviewFilters || {}), ...(activeClientGroupTenant() ? { orgId: activeClientGroupServerOrgId() } : {}) };
  const params = new URLSearchParams();
  params.set("limit", kind === "failures" ? "20" : "50");
  if (filters.orgId && filters.orgId !== "All") params.set("orgId", filters.orgId);
  if (filters.permission) params.set("permission", filters.permission);
  if (filters.fromDate) params.set("fromDate", filters.fromDate);
  if (filters.toDate) params.set("toDate", filters.toDate);
  if (kind === "decisions" && filters.decision && filters.decision !== "All") params.set("decision", filters.decision);
  return params.toString();
}

function ensureLocalSecurityReviewData() {
  state.authorizationFailures = mergeSecurityRows(state.authorizationFailures, localAuthorizationFailures(), (row) => [
    row.createdAtUtc,
    row.route,
    row.userId,
    row.permission,
    row.reason
  ].join("|"));
  state.accessDecisions = mergeSecurityRows(state.accessDecisions, localAccessDecisions(), (row) => row.accessDecisionId);
  if (!Array.isArray(state.accessAdminPreviews)) state.accessAdminPreviews = [];
}

function mergeSecurityRows(currentRows, fallbackRows, keyFn) {
  const merged = Array.isArray(currentRows) ? [...currentRows] : [];
  const keys = new Set(merged.map(keyFn).filter(Boolean));
  fallbackRows.forEach((row) => {
    const key = keyFn(row);
    if (!key || keys.has(key)) return;
    merged.push(row);
    keys.add(key);
  });
  return merged;
}

function localAuthorizationFailures() {
  return [
    {
      createdAtUtc: "2026-05-21T16:10:00.000Z",
      route: "report.export",
      userId: "readonly",
      role: "Read Only",
      tenantId: "Client Group A",
      orgId: "ORG-RVBD-CT",
      permission: "reports:export",
      reason: "missing_report_export_permission"
    },
    {
      createdAtUtc: "2026-05-21T16:14:00.000Z",
      route: "workflow.follow_up",
      userId: "biller",
      role: "Biller",
      tenantId: "Client Group B",
      orgId: "ORG-SKY",
      permission: "claim:updateFollowUp",
      reason: "claim_out_of_scope"
    },
    {
      createdAtUtc: "2026-05-21T16:18:00.000Z",
      route: "tracker.detail",
      userId: "provider",
      role: "Provider",
      tenantId: "Client Group B",
      orgId: "ORG-SKY",
      permission: "claim:view",
      reason: "tenant_scope_boundary_denied"
    }
  ];
}

function localAccessDecisions() {
  return [
    {
      accessDecisionId: "local-report-export-deny",
      createdAtUtc: "2026-05-21T16:10:00.000Z",
      route: "report.export",
      userId: "readonly",
      role: "Read Only",
      tenantId: "Client Group A",
      orgId: "ORG-RVBD-CT",
      permission: "reports:export",
      targetType: "report",
      targetId: "local-report-run",
      decision: "deny",
      reason: "missing_report_export_permission",
      appliedGrantIds: [],
      appliedOverrideIds: ["deny-export-readonly"],
      deniedPermissions: ["reports:export"],
      overrideBacked: true
    },
    {
      accessDecisionId: "local-report-view-grant",
      createdAtUtc: "2026-05-21T16:12:00.000Z",
      route: "report.query",
      userId: "tenant-owner",
      role: "Tenant Owner",
      tenantId: "Client Group A",
      orgId: "ORG-RVBD-CT",
      permission: "reports:view",
      targetType: "report",
      targetId: "local-report-run",
      decision: "allow",
      reason: "",
      appliedGrantIds: ["grant-report-view"],
      appliedOverrideIds: [],
      deniedPermissions: [],
      grantBacked: true
    },
    {
      accessDecisionId: "local-tenant-narrowed-tracker",
      createdAtUtc: "2026-05-21T16:16:00.000Z",
      route: "tracker.query",
      userId: "billing-manager",
      role: "Billing Manager",
      tenantId: "Client Group A",
      orgId: "ORG-RVBD-CT",
      permission: "claim:view",
      targetType: "claim_queue",
      targetId: "all-data-request",
      decision: "allow",
      reason: "tenant_scope_narrowed",
      appliedGrantIds: ["grant-claim-view-org-rvbd"],
      appliedOverrideIds: [],
      deniedPermissions: [],
      grantBacked: true,
      narrowed: true,
      scope: {
        requestedAllOrgs: true,
        effectiveOrgIds: ["ORG-RVBD-CT"]
      }
    },
    {
      accessDecisionId: "local-phi-financial-redaction",
      createdAtUtc: "2026-05-21T16:17:00.000Z",
      route: "tracker.detail",
      userId: "provider",
      role: "Provider",
      tenantId: "Client Group A",
      orgId: "ORG-RVBD-CT",
      permission: "claim:view",
      targetType: "claim",
      targetId: "CLM-1007",
      decision: "allow",
      reason: "financial_phi_redacted_for_provider_scope",
      appliedGrantIds: ["grant-provider-panel"],
      appliedOverrideIds: [],
      deniedPermissions: [],
      grantBacked: true,
      redacted: true
    },
    {
      accessDecisionId: "local-cross-tenant-approved",
      createdAtUtc: "2026-05-21T16:19:00.000Z",
      route: "report.query",
      userId: "super-admin",
      role: "Super Admin",
      tenantId: "all-tenants",
      orgId: "all-orgs",
      permission: "reports:view",
      targetType: "report",
      targetId: "platform-ar-review",
      decision: "allow",
      reason: "cross_tenant_scope_approved",
      appliedGrantIds: ["cross-tenant-platform-admin"],
      appliedOverrideIds: [],
      deniedPermissions: [],
      grantBacked: true,
      crossTenantApproved: true,
      scope: {
        crossTenant: true
      }
    }
  ];
}

function previewAccessAdminCommand(kind) {
  if (!hasPermission(kind === "grant" ? PERMISSIONS.MANAGE_ACCESS_GRANTS : PERMISSIONS.MANAGE_PERMISSION_OVERRIDES)) {
    accessAdminPreviewRequestKey = "";
    return denyAction(kind === "grant" ? PERMISSIONS.MANAGE_ACCESS_GRANTS : PERMISSIONS.MANAGE_PERMISSION_OVERRIDES, "access admin preview");
  }
  const preview = buildLocalAccessAdminPreview(kind);
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") {
    addAccessAdminPreview(preview);
    showToast("Preview created locally. Writes remain blocked.");
    return preview;
  }
  const token = workflowApiToken();
  const route = kind === "grant"
    ? "/api/security/access-grants/preview"
    : "/api/security/permission-overrides/preview";
  const requestBody = accessAdminPreviewRequest(kind);
  const requestParts = { base, route, requestBody };
  const requestKey = workflowAccessScopedRequestKey(requestParts);
  const requestStillCurrent = () => (
    accessAdminPreviewRequestKey === requestKey
    && workflowAccessScopedRequestKey(requestParts) === requestKey
  );
  accessAdminPreviewRequestKey = requestKey;
  fetch(`${base}${route}`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(requestBody)
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      addAccessAdminPreview(normalizeAccessAdminPreviewResponse(body, preview));
      showToast(response.ok ? "Preview loaded. Changes remain disabled." : "Preview is not available.");
    })
    .catch(() => {
      if (!requestStillCurrent()) return;
      addAccessAdminPreview(preview);
      showToast("The portal is temporarily unavailable. A read-only preview was created.");
    });
  return preview;
}

function buildLocalAccessAdminPreview(kind) {
  const request = accessAdminPreviewRequest(kind);
  const scope = request.scope || {};
  const targetId = kind === "grant" ? `grant-${Date.now()}` : `override-${Date.now()}`;
  return {
    commandType: kind === "grant" ? "temporary_grant.preview" : "permission_override.preview",
    targetId,
    createdAt: new Date().toISOString(),
    row: {
      tenant_id: scope.clientNames?.[0] || currentAccessProfile().scope?.clientNames?.[0] || "Client Group A",
      org_id: scope.orgIds?.[0] || (scope.allOrgs ? "all-orgs" : currentAccessProfile().scope?.orgIds?.[0] || "ORG-RVBD-CT"),
      permission_key: request.permission,
      target_user_id: request.targetUserId || "",
      target_role_id: request.targetRoleId || "",
      effect: kind === "override" ? "deny" : "",
      scope_json: JSON.stringify(scope),
      allow_cross_tenant: Boolean(request.allowCrossTenant),
      expires_at_utc: request.expiresAtUtc || "",
      reason: request.reason
    },
    approval: {
      approved: true,
      status: "approved",
      reason: "two_person_approval_captured",
      requiredApprovals: 2,
      approvals: request.approvals
    },
    execution: {
      readyForWrite: false,
      blocked: true,
      reason: "managed_access_admin_write_store_not_selected"
    }
  };
}

function accessAdminPreviewRequest(kind) {
  const permission = els.accessPermissionKey?.value || "reports:view";
  const expiry = accessGrantExpiryIso(els.accessGrantExpiry?.value);
  const scope = accessAdminRequestedScope();
  const targetUserId = els.accessTargetUser?.value || "biller-1";
  const targetRoleId = els.accessTargetRole?.value || "";
  const approvals = accessAdminApprovalInputs();
  return {
    targetUserId: kind === "grant" || !targetRoleId ? targetUserId : "",
    targetRoleId: targetRoleId || (kind === "override" ? "role-biller" : ""),
    effect: kind === "override" ? "deny" : undefined,
    permission,
    scope,
    allowCrossTenant: Boolean(scope.allOrgs || scope.crossTenant),
    expiresAtUtc: kind === "grant" ? expiry : "",
    reason: els.accessReason?.value || "Temporary access review.",
    approvals
  };
}

function accessAdminRequestedScope() {
  const currentScope = currentAccessProfile().scope || {};
  const orgId = els.accessScopeOrg?.value || "";
  const clientName = els.accessScopeClient?.value || "";
  const crossTenant = Boolean(els.accessAllowCrossTenant?.checked);
  if (crossTenant) {
    return {
      allOrgs: true,
      crossTenant: true,
      orgIds: [],
      clientNames: [],
      providerNames: []
    };
  }
  return {
    allOrgs: false,
    crossTenant: false,
    orgIds: orgId ? [orgId] : (Array.isArray(currentScope.orgIds) && currentScope.orgIds.length ? [currentScope.orgIds[0]] : []),
    clientNames: clientName ? [clientName] : (Array.isArray(currentScope.clientNames) && currentScope.clientNames.length ? [currentScope.clientNames[0]] : []),
    providerNames: Array.isArray(currentScope.providerNames) ? currentScope.providerNames.slice(0, 1) : [],
    assignedOnly: Boolean(currentScope.assignedOnly),
    assignedName: currentScope.assignedName || ""
  };
}

function accessAdminApprovalInputs() {
  const first = (els.accessApproverOne?.value || "approver-1").trim();
  const second = (els.accessApproverTwo?.value || "approver-2").trim();
  return [
    { userId: first, role: "Operations Approver", reason: "Operational approval." },
    { userId: second, role: "Security Approver", reason: "Security approval." }
  ].filter((approval) => approval.userId);
}

function accessGrantExpiryIso(value) {
  const fallback = "2026-05-22T19:30:00.000Z";
  const text = String(value || "").trim();
  if (!text) return fallback;
  const dateOnly = dateOnlyFilterString(text);
  if (dateOnly) return `${dateOnly}T00:00:00.000Z`;
  const localMatch = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(text);
  if (localMatch) {
    const [, yearText, monthText, dayText, hourText, minuteText, secondText = "00"] = localMatch;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const hour = Number(hourText);
    const minute = Number(minuteText);
    const second = Number(secondText);
    const date = new Date(year, month - 1, day, hour, minute, second);
    const valid = date.getFullYear() === year
      && date.getMonth() === month - 1
      && date.getDate() === day
      && date.getHours() === hour
      && date.getMinutes() === minute
      && date.getSeconds() === second;
    return valid ? date.toISOString() : fallback;
  }
  const isoDatePart = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/.exec(text)?.[1];
  if (isoDatePart && dateOnlyFilterString(isoDatePart)) {
    const date = new Date(text);
    if (Number.isFinite(date.getTime())) return date.toISOString();
  }
  return fallback;
}

function normalizeAccessAdminPreviewResponse(body = {}, fallback) {
  const command = body.command || {};
  return {
    ...fallback,
    ...command,
    createdAt: new Date().toISOString(),
    row: command.row || fallback.row,
    approval: command.approval || fallback.approval,
    execution: command.execution || fallback.execution
  };
}

function addAccessAdminPreview(preview) {
  state.accessAdminPreviews = [...(state.accessAdminPreviews || []).slice(-49), preview];
  saveState();
  renderAccessAdminPreviews();
  renderSecuritySummary();
}

function accessDecisionLabel(decision = {}) {
  if (decision.decision === "deny") return "Denied";
  if (decision.redacted) return "Allowed · redacted";
  if (decision.narrowed) return "Narrowed";
  return "Allowed";
}

function accessDecisionTone(decision = {}) {
  if (decision.decision === "deny") return "red";
  if (decision.redacted || decision.narrowed) return "amber";
  return "green";
}

function accessDecisionEvidenceSummary(decision = {}) {
  const grants = arraySummary(decision.appliedGrantIds);
  const overrides = arraySummary(decision.appliedOverrideIds);
  const denied = arraySummary(decision.deniedPermissions);
  return `grants: ${grants} · overrides: ${overrides} · denied: ${denied}`;
}

function arraySummary(value) {
  return Array.isArray(value) && value.length ? value.join(", ") : "none";
}

function renderReportRunTrail() {
  if (!els.reportRunTrail) return;
  const runs = (state.reportRunTrail || []).slice(-25);
  renderReportRunExportPreview();
  els.reportRunTrail.innerHTML = `
    <div class="sync-section-title">
      <strong>Report Runs</strong>
      <span>${workflowApiBase() ? "Filtered report history" : "Report history is temporarily unavailable"}</span>
    </div>
    ${runs.length ? runs.map((run) => `
      <div class="sync-item">
        <div class="sync-item-header">
          <strong>${escapeHtml(run.reportType || "report")} · ${escapeHtml(run.tenantId || "scope")}</strong>
          ${pill(reportRunDecisionLabel(run), reportRunDecisionTone(run))}
        </div>
        <p>${escapeHtml(run.createdAtUtc || "")} · ${escapeHtml(run.requestedByRole || "")} · ${escapeHtml(reportRunFilterSummary(run.filters || {}))}</p>
        <p>${escapeHtml(reportRunExportSummary(run))}</p>
        <div class="sync-actions">
          <button class="small-button" data-report-run-detail="${escapeHtml(run.reportRunId)}" type="button">Details</button>
        </div>
      </div>
    `).join("") : empty("No persisted report runs loaded.")}
    ${renderSelectedReportRun()}
  `;
  bindReportRunDetailButtons();
}

function renderReportRunExportPreview() {
  if (!els.reportRunExportPreview) return;
  const preview = state.reportRunAuditExportPreview;
  if (!preview) {
    els.reportRunExportPreview.innerHTML = "";
    return;
  }
  const job = preview.exportJob || {};
  const rows = Array.isArray(preview.rows) ? preview.rows : [];
  const readiness = preview.managedStoreReadiness || buildLocalManagedStoreReadiness(job.execution?.reason || "no_write_export_job_contract");
  els.reportRunExportPreview.innerHTML = `
    <div class="sync-section-title">
      <strong>Audit Export Package</strong>
      <span>${escapeHtml(job.execution?.readyForWrite === false ? "Controlled review" : "Ready")}</span>
    </div>
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(job.exportJobId || "report-run audit export")}</strong>
        ${pill(job.authorization?.allowed ? "Allowed" : "Denied", job.authorization?.allowed ? "green" : "red")}
      </div>
      <p>${escapeHtml(`${formatNounCount(job.rowCount || 0, "row")} · ${job.format || "csv"} · ${job.execution?.reason || ""}`)}</p>
      <p data-testid="report-run-export-managed-store-readiness">${escapeHtml(managedStoreReadinessSummary(readiness))}</p>
      <p>${escapeHtml((job.redactedFields || []).length ? `redacted: ${(job.redactedFields || []).join(", ")}` : "metadata-only extract")}</p>
      ${rows.length ? `
        <div class="report-run-columns">
          <div>
            <strong>Review Rows</strong>
            <p>${escapeHtml(rows.slice(0, 3).map((row) => `${row.reportRunId} · ${row.tenantId} · ${row.authorizationDecision}`).join(" | "))}</p>
          </div>
        </div>
      ` : ""}
    </div>
  `;
}

function renderQueueWorkExportPreview() {
  if (!els.queueWorkExportPreview) return;
  const preview = state.queueWorkExportPreview;
  if (!preview) {
    els.queueWorkExportPreview.innerHTML = "";
    return;
  }
  const job = preview.exportJob || {};
  const rows = Array.isArray(preview.rows) ? preview.rows : [];
  const totals = preview.totals || {};
  const readiness = preview.managedStoreReadiness || buildLocalManagedStoreReadiness(job.execution?.reason || "no_write_queue_work_export_contract");
  els.queueWorkExportPreview.innerHTML = `
    <div class="sync-section-title">
      <strong>Queue Work Export Package</strong>
      <span>${escapeHtml(job.execution?.readyForWrite === false ? "Controlled summary" : "Ready")}</span>
    </div>
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(job.exportJobId || "queue-work export")}</strong>
        ${pill(job.authorization?.allowed ? "Allowed" : "Denied", job.authorization?.allowed ? "green" : "red")}
      </div>
      <p>${escapeHtml(`${formatNounCount(job.rowCount || 0, "aggregate row")} · ${job.format || "csv"} · ${job.execution?.reason || ""}`)}</p>
      <p>${escapeHtml(`${formatNounCount(totals.sessions || 0, "session")} · ${totals.touchedSessions || 0} touched · ${formatNounCount(totals.workflowActions || 0, "action")}`)}</p>
      <p data-testid="queue-work-export-managed-store-readiness">${escapeHtml(managedStoreReadinessSummary(readiness))}</p>
      <p>${escapeHtml((job.redactedFields || []).length ? `redacted: ${(job.redactedFields || []).join(", ")}` : "aggregate-only management extract")}</p>
      ${rows.length ? `
        <div class="report-run-columns">
          <div>
            <strong>Review Rows</strong>
            <p>${escapeHtml(rows.slice(0, 3).map((row) => `${row.day} · ${row.queueKey} · ${row.actorUserId} · ${row.touchRate}%`).join(" | "))}</p>
          </div>
        </div>
      ` : ""}
    </div>
  `;
}

function renderExportGovernanceActivityTrail() {
  if (!els.exportGovernanceActivityTrail) return;
  const allActivity = (Array.isArray(state.exportGovernanceActivityTrail) ? state.exportGovernanceActivityTrail : []).filter(row => !activeClientGroupTenant() || activeClientGroupOrgIds().includes(String(row.orgId || "")));
  const kindFilter = state.reportRunFilters?.exportKind || "All";
  const activity = allActivity
    .filter((row) => !kindFilter || kindFilter === "All" || row.exportKind === kindFilter)
    .slice()
    .sort((a, b) => timestampValue(b.createdAtUtc || b.at) - timestampValue(a.createdAtUtc || a.at))
    .slice(0, 30);
  const counts = exportGovernanceActivityCounts(allActivity);
  els.exportGovernanceActivityTrail.innerHTML = `
    <div class="sync-section-title">
      <strong>Export Governance Activity</strong>
      <span>${workflowApiBase() ? "Durable audit events plus local preview-only actions" : "Local preview-only actions"} · ${counts.total} total · ${counts.denied} denied · ${counts.disabledDownloads} disabled downloads</span>
    </div>
    ${activity.length ? activity.map((row) => `
      <div class="sync-item">
        <div class="sync-item-header">
          <strong>${escapeHtml(exportGovernanceActivityLabel(row))}</strong>
          ${pill(row.decision === "deny" ? "Denied" : "Allowed", row.decision === "deny" ? "red" : "green")}
        </div>
        <p>${escapeHtml(`${row.createdAtUtc || row.at || ""} · ${exportGovernanceTrailField(row.actorUserId)} · ${row.role || ""}`)}</p>
        <p>${escapeHtml(exportGovernanceActivityScopeLine(row))}</p>
        <p>${escapeHtml(exportGovernanceActivitySummary(row))}</p>
      </div>
    `).join("") : empty("No export governance events match the current filters.")}
  `;
}

// Durable governance rows persist the literal string "unknown" when the
// actor had an unrestricted scope (no tenant/org list), which used to render
// as "unknown / unknown". Treat that as missing: em-dash for the actor, and
// drop absent tenant/org segments instead of stacking placeholders.
function exportGovernanceTrailField(value) {
  const text = String(value || "").trim();
  return !text || text.toLowerCase() === "unknown" ? "—" : text;
}

function exportGovernanceActivityScopeLine(row = {}) {
  const scope = [row.tenantId, row.orgId]
    .map((value) => {
      const text = String(value || "").trim();
      return text.toLowerCase() === "unknown" ? "" : text;
    })
    .filter(Boolean)
    .join(" / ");
  return [scope, row.exportKind || "metadata", row.exportJobId || row.targetId || ""].filter(Boolean).join(" · ");
}

function renderExportGovernanceActivityExportPreview() {
  if (!els.exportGovernanceActivityExportPreview) return;
  const preview = state.exportGovernanceActivityExportPreview;
  if (!preview) {
    els.exportGovernanceActivityExportPreview.innerHTML = "";
    return;
  }
  const job = preview.exportJob || {};
  const rows = Array.isArray(preview.rows) ? preview.rows : [];
  const counts = preview.counts || {};
  const readiness = preview.managedStoreReadiness || buildLocalManagedStoreReadiness(job.execution?.reason || "local_preview_not_persisted");
  els.exportGovernanceActivityExportPreview.innerHTML = `
    <div class="sync-section-title">
      <strong>Activity Export Preview</strong>
      <span>${escapeHtml(job.execution?.readyForWrite === false ? "No-write governance manifest" : "Preview")}</span>
    </div>
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(job.exportJobId || "export governance activity")}</strong>
        ${pill(job.authorization?.allowed ? "Allowed" : "Denied", job.authorization?.allowed ? "green" : "red")}
      </div>
      <p>${escapeHtml(`${job.rowCount || 0} metadata rows · ${job.format || "csv"} · ${job.execution?.reason || ""}`)}</p>
      <p>${escapeHtml(`${counts.allowed || 0} allowed · ${counts.denied || 0} denied · ${counts.disabledDownloads || 0} disabled downloads`)}</p>
      <p data-testid="activity-export-managed-store-readiness">${escapeHtml(managedStoreReadinessSummary(readiness))}</p>
      <p>${escapeHtml((job.redactedFields || []).length ? `redacted: ${(job.redactedFields || []).join(", ")}` : "metadata-only activity manifest")}</p>
      ${rows.length ? `
        <div class="report-run-columns">
          <div>
            <strong>Preview Rows</strong>
            <p>${escapeHtml(rows.slice(0, 3).map((row) => `${row.createdAtUtc} · ${exportGovernanceActivityLabel(row)} · ${row.decision} · ${row.exportJobId || row.targetId || ""}`).join(" | "))}</p>
          </div>
        </div>
      ` : ""}
    </div>
  `;
}

function buildLocalManagedStoreReadiness(reason = "managed_export_storage_not_configured") {
  return {
    schema: "tms-master-portal.export-governance-activity-manifest-readiness.v1",
    exportKind: "export_governance_activity_manifest",
    tableReady: false,
    rowShapeReady: true,
    managedReadReady: false,
    readyForWrite: false,
    readyForDownloads: false,
    persisted: false,
    reason,
    storage: {
      configured: false,
      approved: false,
      storageUriExposed: false,
      downloadsEnabled: false
    },
    checks: {
      noWriteContract: true,
      noDownloadContract: true
    }
  };
}

function managedStoreReadinessSummary(readiness = {}) {
  const storeState = readiness.managedReadReady || readiness.persisted ? "ready" : "not ready";
  const writeState = readiness.readyForWrite ? "writes pending review" : "writes disabled";
  const downloadState = readiness.readyForDownloads ? "downloads pending review" : "downloads disabled";
  const reason = readiness.reason ? ` · ${readiness.reason}` : "";
  return `managed store: ${storeState} · ${writeState} · ${downloadState}${reason}`;
}

function exportJobReadinessFromReview(job = {}) {
  return {
    managedReadReady: booleanFlagValue(job.persisted),
    readyForWrite: booleanFlagValue(job.readyForWrite),
    readyForDownloads: false,
    persisted: booleanFlagValue(job.persisted),
    reason: booleanFlagValue(job.persisted) ? "managed_export_downloads_disabled_by_contract" : "managed_export_job_writes_disabled_by_contract"
  };
}

function exportGovernanceActivityCounts(rows = []) {
  return rows.reduce((counts, row) => {
    counts.total += 1;
    if (row.decision === "deny") counts.denied += 1;
    if (row.activityKind === "export_job_download" && row.downloadEnabled === false) counts.disabledDownloads += 1;
    return counts;
  }, { total: 0, denied: 0, disabledDownloads: 0 });
}

function exportGovernanceActivityLabel(row = {}) {
  const labels = {
    report_run_audit_export: "Report-run audit export",
    queue_work_export: "Queue-work export",
    export_job_detail: "Export-job detail",
    export_job_download: "Download preview",
    export_governance_activity_manifest: "Activity export"
  };
  return labels[row.activityKind] || row.eventType || "Export activity";
}

function exportGovernanceActivitySummary(row = {}) {
  const parts = [];
  if (row.reason) parts.push(row.reason);
  const rowCount = nonNegativeIntegerValue(row.rowCount);
  if (rowCount > 0) parts.push(formatNounCount(rowCount, "row"));
  if (row.readyForWrite === false) parts.push("no-write");
  if (row.activityKind === "export_job_download" && row.downloadEnabled === false) parts.push("download disabled");
  if (row.activityKind === "export_job_download" && row.storageConfigured === false) parts.push("storage not configured");
  return parts.length ? parts.join(" · ") : "metadata-only audit event";
}

function appendExportGovernanceActivity(event = {}) {
  const normalized = sanitizeExportGovernanceActivity(event);
  if (!normalized.activityId) return;
  const existing = Array.isArray(state.exportGovernanceActivityTrail) ? state.exportGovernanceActivityTrail : [];
  state.exportGovernanceActivityTrail = [
    ...existing.filter((item) => String(item.activityId || "") !== String(normalized.activityId)),
    normalized
  ].slice(-150);
}

function buildLocalExportGovernanceActivityExportPreview() {
  const createdAtUtc = new Date().toISOString();
  const filters = safeExportMetadataObject(state.reportRunFilters || {});
  const rows = (state.exportGovernanceActivityTrail || [])
    .filter((row) => exportGovernanceActivityMatchesCurrentFilters(row))
    .slice()
    .sort((a, b) => timestampValue(b.createdAtUtc || b.at) - timestampValue(a.createdAtUtc || a.at))
    .slice(0, 100)
    .map(sanitizeExportGovernanceActivity);
  return {
    schema: "tms-master-portal.export-governance-activity-manifest-preview.v1",
    exportJob: {
      exportJobId: `local-export-governance-activity-manifest-${Date.now()}`,
      exportKind: "export_governance_activity_manifest",
      createdAtUtc,
      requestedBy: {
        userId: activeInternalUser(),
        role: currentAccessProfile().label
      },
      format: "csv",
      filters,
      scope: currentAccessProfile().scope || {},
      rowCount: rows.length,
      columns: exportGovernanceActivityPreviewColumns(),
      redactedFields: ["patient", "patientName", "claimId", "claimRows", "serviceRows", "notes", "noteText", "rawRows", "manifestRows", "rows", "storageUri", "storage_uri", "signedDownload", "url", "sql", "query"],
      authorization: {
        allowed: true,
        permission: "reports:export",
        reason: ""
      },
      execution: {
        readyForWrite: false,
        persisted: false,
        readyForDownloads: false,
        reason: "local_preview_not_persisted"
      },
      storageUri: ""
    },
    rows,
    counts: exportGovernanceActivityCounts(rows),
    managedStoreReadiness: buildLocalManagedStoreReadiness("local_preview_not_persisted"),
    exportSummary: {
      exportReady: true,
      exportKind: "export_governance_activity_manifest",
      rowCount: rows.length,
      containsPhi: false,
      containsRawRows: false,
      containsStorageUris: false,
      downloadsEnabled: false
    }
  };
}

function exportGovernanceActivityPreviewColumns() {
  return [
    ["activityId", "Activity ID", "text"],
    ["eventType", "Event Type", "text"],
    ["activityKind", "Activity Kind", "text"],
    ["decision", "Decision", "text"],
    ["createdAtUtc", "Created At UTC", "timestamp"],
    ["actorUserId", "Actor User", "text"],
    ["role", "Role", "text"],
    ["tenantId", "Tenant", "text"],
    ["orgId", "Org", "text"],
    ["exportJobId", "Export Job ID", "text"],
    ["exportKind", "Export Kind", "text"],
    ["rowCount", "Rows", "count"],
    ["readyForWrite", "Ready For Write", "boolean"],
    ["persisted", "Persisted", "boolean"],
    ["downloadEnabled", "Download Enabled", "boolean"],
    ["storageConfigured", "Storage Configured", "boolean"],
    ["reason", "Reason", "text"],
    ["permission", "Permission", "text"]
  ].map(([key, label, valueType]) => ({ key, label, valueType }));
}

function exportGovernanceActivityMatchesCurrentFilters(row = {}) {
  const filters = state.reportRunFilters || {};
  if (filters.tenantId && filters.tenantId !== "All" && row.tenantId !== filters.tenantId) return false;
  if (filters.orgId && filters.orgId !== "All" && row.orgId !== filters.orgId) return false;
  if (filters.exportKind && filters.exportKind !== "All" && row.exportKind !== filters.exportKind) return false;
  const fromDate = dateOnlyFilterString(filters.fromDate);
  const toDate = dateOnlyFilterString(filters.toDate);
  const createdDate = String(row.createdAtUtc || row.at || "").slice(0, 10);
  if (fromDate && createdDate < fromDate) return false;
  if (toDate && createdDate > toDate) return false;
  return true;
}

function sanitizeExportGovernanceActivity(event = {}) {
  const denied = new Set(["rows", "rawRows", "claimRows", "serviceRows", "patient", "patientName", "notes", "noteText", "storageUri", "storage_uri", "signedDownload", "url", "sql", "query"]);
  const safe = Object.fromEntries(Object.entries(event || {}).filter(([key]) => !denied.has(key)));
  const createdAtUtc = safe.createdAtUtc || safe.generatedAtUtc || new Date().toISOString();
  return {
    activityId: safe.activityId || `local-${safe.activityKind || safe.eventType || "export"}-${safe.exportJobId || safe.targetId || Date.now()}-${createdAtUtc}`,
    eventType: safe.eventType || "",
    activityKind: safe.activityKind || "export_governance",
    decision: safe.decision === "deny" ? "deny" : "allow",
    createdAtUtc,
    actorUserId: safe.actorUserId || activeInternalUser(),
    role: safe.role || currentAccessProfile().label,
    tenantId: safe.tenantId || currentAccessProfile().scope?.clientNames?.[0] || "Local",
    orgId: safe.orgId || currentAccessProfile().scope?.orgIds?.[0] || "Local",
    exportJobId: safe.exportJobId || "",
    exportKind: safe.exportKind || "",
    rowCount: nonNegativeIntegerValue(safe.rowCount),
    readyForWrite: booleanFlagValue(safe.readyForWrite),
    persisted: booleanFlagValue(safe.persisted),
    downloadEnabled: booleanFlagValue(safe.downloadEnabled),
    storageConfigured: booleanFlagValue(safe.storageConfigured),
    reason: safe.reason || "",
    permission: safe.permission || "reports:export",
    targetType: safe.targetType || "",
    targetId: safe.targetId || safe.exportJobId || "",
    source: safe.source || "local-preview"
  };
}

function renderReportExportJobTrail() {
  if (!els.reportExportJobTrail) return;
  const allJobs = Array.isArray(state.reportExportJobTrail) ? state.reportExportJobTrail : [];
  const kindFilter = state.reportRunFilters?.exportKind || "All";
  const jobs = allJobs.filter((job) => exportJobMatchesKind(job, kindFilter)).slice(-25);
  const counts = exportJobKindCounts(allJobs);
  els.reportExportJobTrail.innerHTML = `
    <div class="sync-section-title">
      <strong>Export Jobs</strong>
      <span>${workflowApiBase() ? "Metadata review · downloads disabled" : "Export history is temporarily unavailable"} · ${escapeHtml(exportJobKindCountLabel(counts, kindFilter))}</span>
    </div>
    ${jobs.length ? jobs.map((job) => `
      <div class="sync-item">
        <div class="sync-item-header">
          <strong>${escapeHtml(job.exportKind || "export")} · ${escapeHtml(job.tenantId || "scope")}</strong>
          ${pill(job.readyForWrite || job.persisted ? "Review Required" : "No-write", job.readyForWrite || job.persisted ? "amber" : "green")}
        </div>
        <p>${escapeHtml(`${job.createdAtUtc || ""} · ${job.requestedByRole || ""} · ${formatNounCount(job.rowCount, "row")} · ${job.format || "csv"}`)}</p>
        <p data-testid="export-job-managed-store-readiness">${escapeHtml(`${job.executionStatus || "blocked_no_write_contract"} · ${managedStoreReadinessSummary(exportJobReadinessFromReview(job))}`)}</p>
        <p>${escapeHtml((job.redactedFields || []).length ? `redacted: ${(job.redactedFields || []).join(", ")}` : "metadata-only export job")}</p>
        <div class="sync-actions">
          <button class="small-button" data-export-job-detail="${escapeHtml(job.exportJobId || "")}" type="button">Details</button>
          <button class="small-button" data-export-job-preview="${escapeHtml(job.exportJobId || "")}" type="button">Preview Download</button>
        </div>
      </div>
    `).join("") : empty("No export-job metadata loaded.")}
    ${renderSelectedExportJobDetail()}
    ${renderSelectedExportJobDownloadPreview()}
  `;
  bindExportJobPreviewButtons();
  bindExportJobDetailButtons();
}

function exportJobMatchesKind(job = {}, kind = "All") {
  return !kind || kind === "All" || String(job.exportKind || "") === String(kind);
}

function exportJobKindCounts(jobs = []) {
  return jobs.reduce((counts, job) => {
    const kind = job.exportKind || "unknown";
    counts.total += 1;
    counts[kind] = (counts[kind] || 0) + 1;
    return counts;
  }, { total: 0 });
}

function exportJobKindCountLabel(counts = {}, kind = "All") {
  if (kind && kind !== "All") return `${counts[kind] || 0} ${kind}`;
  const reportRuns = counts.report_run_audit_extract || 0;
  const queueWork = counts.queue_work_outcome_summary || 0;
  return `${counts.total || 0} total · ${reportRuns} report-run · ${queueWork} queue-work`;
}

function appendLocalExportJobReview(job = {}) {
  if (!job || !job.exportJobId) return;
  const normalized = {
    ...job,
    executionStatus: job.executionStatus || "blocked_no_write_contract",
    readyForWrite: booleanFlagValue(job.readyForWrite),
    persisted: booleanFlagValue(job.persisted),
    storageUri: job.storageUri || "",
    localPreviewOnly: true
  };
  const existing = Array.isArray(state.reportExportJobTrail) ? state.reportExportJobTrail : [];
  state.reportExportJobTrail = [
    ...existing.filter((item) => String(item.exportJobId || "") !== String(normalized.exportJobId)),
    normalized
  ].slice(-100);
}

function bindExportJobPreviewButtons() {
  if (!els.reportExportJobTrail || typeof els.reportExportJobTrail.querySelectorAll !== "function") return;
  Array.from(els.reportExportJobTrail.querySelectorAll("[data-export-job-preview]")).forEach((button) => {
    button.addEventListener("click", () => previewExportJobDownload(button.dataset.exportJobPreview));
  });
}

function bindExportJobDetailButtons() {
  if (!els.reportExportJobTrail || typeof els.reportExportJobTrail.querySelectorAll !== "function") return;
  Array.from(els.reportExportJobTrail.querySelectorAll("[data-export-job-detail]")).forEach((button) => {
    button.addEventListener("click", () => selectExportJobDetail(button.dataset.exportJobDetail));
  });
}

function selectExportJobDetail(exportJobId) {
  const job = (state.reportExportJobTrail || []).find((item) => String(item.exportJobId || "") === String(exportJobId || ""));
  if (!job) return showToast("Export job was not found.");
  if (!job.localPreviewOnly && workflowApiBase() && typeof fetch === "function") {
    return fetchExportJobDetail(exportJobId, job);
  }
  state.selectedExportJobDetail = sanitizeExportJobDetail(job);
  appendExportGovernanceActivity({
    activityKind: "export_job_detail",
    decision: "allow",
    exportJobId: job.exportJobId,
    exportKind: job.exportKind,
    tenantId: job.tenantId,
    orgId: job.orgId,
    rowCount: job.rowCount,
    readyForWrite: false,
    persisted: false,
    reason: "local_preview_metadata_detail"
  });
  saveState();
  renderReportExportJobTrail();
  renderExportGovernanceActivityTrail();
}

function fetchExportJobDetail(exportJobId, fallbackJob = null) {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") {
    state.selectedExportJobDetail = sanitizeExportJobDetail(fallbackJob || {});
    saveState();
    renderReportExportJobTrail();
    return;
  }
  const token = workflowApiToken();
  const requestParts = { base, exportJobId: String(exportJobId || "") };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const requestStillCurrent = () => (
    exportJobDetailRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  exportJobDetailRequestKey = requestKey;
  fetch(`${base}/api/reports/export-jobs/${encodeURIComponent(exportJobId)}`, {
    method: "GET",
    cache: "no-store",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return;
      }
      if (!response.ok || !body.exportJob) {
        appendExportGovernanceActivity({
          activityKind: "export_job_detail",
          decision: "deny",
          exportJobId,
          exportKind: fallbackJob?.exportKind || "",
          tenantId: fallbackJob?.tenantId || "",
          orgId: fallbackJob?.orgId || "",
          reason: body.reason || body.error || "detail_unavailable"
        });
        if (fallbackJob) {
          state.selectedExportJobDetail = sanitizeExportJobDetail(fallbackJob);
          saveState();
          renderReportExportJobTrail();
          renderExportGovernanceActivityTrail();
        } else {
          showToast("Export-job detail was denied or unavailable.");
        }
        return;
      }
      state.selectedExportJobDetail = {
        ...sanitizeExportJobDetail(body.exportJob),
        exportObjectMetadata: sanitizeExportObjectMetadataRows(body.exportObjectMetadata || []),
        exportObjectMetadataConfigured: booleanFlagValue(body.exportObjectMetadataConfigured),
        authoritativeDetail: true
      };
      appendExportGovernanceActivity({
        activityKind: "export_job_detail",
        decision: "allow",
        exportJobId,
        exportKind: body.exportJob.exportKind,
        tenantId: body.exportJob.tenantId,
        orgId: body.exportJob.orgId,
        rowCount: body.exportJob.rowCount,
        readyForWrite: booleanFlagValue(body.exportJob.readyForWrite),
        persisted: booleanFlagValue(body.exportJob.persisted),
        reason: "api_detail_loaded",
        source: "api"
      });
      saveState();
      renderReportExportJobTrail();
      renderExportGovernanceActivityTrail();
    })
    .catch(() => {
      if (!requestStillCurrent()) return;
      appendExportGovernanceActivity({
        activityKind: "export_job_detail",
        decision: "deny",
        exportJobId,
        exportKind: fallbackJob?.exportKind || "",
        tenantId: fallbackJob?.tenantId || "",
        orgId: fallbackJob?.orgId || "",
        reason: "detail_request_failed"
      });
      if (fallbackJob) {
        state.selectedExportJobDetail = sanitizeExportJobDetail(fallbackJob);
        saveState();
        renderReportExportJobTrail();
        renderExportGovernanceActivityTrail();
      } else {
        showToast("Export-job detail failed.");
      }
    });
}

function sanitizeExportJobDetail(job = {}) {
  return {
    exportJobId: job.exportJobId || "",
    exportKind: job.exportKind || "",
    createdAtUtc: job.createdAtUtc || "",
    tenantId: job.tenantId || "",
    orgId: job.orgId || "",
    requestedByUserId: job.requestedByUserId || "",
    requestedByRole: job.requestedByRole || "",
    format: job.format || "csv",
    rowCount: nonNegativeIntegerValue(job.rowCount),
    executionStatus: job.executionStatus || "blocked_no_write_contract",
    readyForWrite: booleanFlagValue(job.readyForWrite),
    persisted: booleanFlagValue(job.persisted),
    storageReady: Boolean(job.storageUri),
    authorizationAllowed: booleanFlagValue(job.authorizationAllowed),
    authorizationPermission: job.authorizationPermission || "reports:export",
    authorizationReason: job.authorizationReason || "",
    filters: safeExportMetadataObject(job.filters || {}),
    scope: safeExportMetadataObject(job.scope || {}),
    columns: Array.isArray(job.columns) ? job.columns.map((column) => ({
      key: String(column.key || ""),
      label: String(column.label || ""),
      valueType: String(column.valueType || "text")
    })).filter((column) => column.key || column.label) : [],
    redactedFields: Array.isArray(job.redactedFields) ? job.redactedFields.map(String).filter(Boolean) : [],
    localPreviewOnly: booleanFlagValue(job.localPreviewOnly)
  };
}

function sanitizeExportObjectMetadataRows(rows = []) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    exportJobId: String(row.exportJobId || ""),
    objectId: String(row.objectId || ""),
    exportKind: String(row.exportKind || ""),
    tenantId: String(row.tenantId || ""),
    orgId: String(row.orgId || ""),
    format: String(row.format || ""),
    filename: String(row.filename || ""),
    contentType: String(row.contentType || ""),
    storageProvider: String(row.storageProvider || ""),
    objectVersionPresent: booleanFlagValue(row.objectVersionPresent),
    byteLength: nonNegativeIntegerValue(row.byteLength),
    sha256: /^[a-f0-9]{64}$/i.test(String(row.sha256 || "")) ? String(row.sha256) : "",
    rowCount: nonNegativeIntegerValue(row.rowCount),
    columnKeys: Array.isArray(row.columnKeys) ? row.columnKeys.map(String).filter(Boolean).slice(0, 40) : [],
    redactedFields: Array.isArray(row.redactedFields) ? row.redactedFields.map(String).filter(Boolean).slice(0, 40) : [],
    requiredRedactedFields: Array.isArray(row.requiredRedactedFields) ? row.requiredRedactedFields.map(String).filter(Boolean).slice(0, 40) : [],
    metadataReady: booleanFlagValue(row.metadataReady),
    readyForDownloads: false,
    containsPhi: false,
    containsRawClaimRows: false,
    containsStorageUris: false,
    objectKeyExposed: false,
    storageLocationExposed: false,
    retentionUntilUtc: String(row.retentionUntilUtc || ""),
    createdByUserId: String(row.createdByUserId || ""),
    createdAtUtc: String(row.createdAtUtc || "")
  })).filter((row) => row.objectId || row.sha256 || row.filename);
}

function safeExportMetadataObject(value = {}) {
  const denied = new Set(["rows", "rawRows", "claimRows", "serviceRows", "patient", "patientName", "notes", "noteText", "storageUri", "storage_uri"]);
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !denied.has(key))
    .map(([key, item]) => [key, Array.isArray(item) ? item.map(String).join(", ") : String(item)]));
}

function previewExportJobDownload(exportJobId) {
  const job = (state.reportExportJobTrail || []).find((item) => String(item.exportJobId || "") === String(exportJobId || ""));
  if (!job) return showToast("Export job was not found.");
  if (job.localPreviewOnly) {
    state.selectedExportJobDownloadPreview = buildLocalExportJobDownloadPreview(job);
    appendExportGovernanceActivity({
      activityKind: "export_job_download",
      decision: "allow",
      exportJobId: job.exportJobId,
      exportKind: job.exportKind,
      tenantId: job.tenantId,
      orgId: job.orgId,
      rowCount: job.rowCount,
      readyForWrite: false,
      persisted: false,
      downloadEnabled: false,
      storageConfigured: false,
      reason: "local_preview_not_persisted"
    });
    saveState();
    renderReportExportJobTrail();
    renderExportGovernanceActivityTrail();
    return;
  }
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") {
    showToast("This preview is temporarily unavailable.");
    return;
  }
  const token = workflowApiToken();
  const requestParts = { base, exportJobId: String(exportJobId || "") };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const requestStillCurrent = () => (
    exportJobDownloadPreviewRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  exportJobDownloadPreviewRequestKey = requestKey;
  fetch(`${base}/api/reports/export-jobs/${encodeURIComponent(exportJobId)}/download-preview`, {
    method: "GET",
    cache: "no-store",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return;
      }
      if (!response.ok || !body.download) {
        appendExportGovernanceActivity({
          activityKind: "export_job_download",
          decision: "deny",
          exportJobId,
          exportKind: job.exportKind,
          tenantId: job.tenantId,
          orgId: job.orgId,
          downloadEnabled: false,
          storageConfigured: false,
          reason: body.reason || body.error || "download_preview_unavailable"
        });
        showToast("Export-job download preview was denied or unavailable.");
        return;
      }
      state.selectedExportJobDownloadPreview = {
        ...body.download,
        exportObjectMetadata: sanitizeExportObjectMetadataRows(body.download.exportObjectMetadata || []),
        exportObjectMetadataConfigured: booleanFlagValue(body.download.exportObjectMetadataConfigured)
      };
      appendExportGovernanceActivity({
        activityKind: "export_job_download",
        decision: body.download.authorization?.allowed === false ? "deny" : "allow",
        exportJobId,
        exportKind: body.download.exportKind,
        tenantId: body.download.audit?.tenantId || job.tenantId,
        orgId: body.download.audit?.orgId || job.orgId,
        downloadEnabled: booleanFlagValue(body.download.downloadEnabled),
        storageConfigured: booleanFlagValue(body.download.storage?.configured),
        reason: body.download.reason || ""
      });
      saveState();
      renderReportExportJobTrail();
      renderExportGovernanceActivityTrail();
    })
    .catch(() => {
      if (!requestStillCurrent()) return;
      appendExportGovernanceActivity({
        activityKind: "export_job_download",
        decision: "deny",
        exportJobId,
        exportKind: job.exportKind,
        tenantId: job.tenantId,
        orgId: job.orgId,
        downloadEnabled: false,
        storageConfigured: false,
        reason: "download_preview_failed"
      });
      saveState();
      renderExportGovernanceActivityTrail();
      showToast("Export-job download preview failed.");
    });
}

function buildLocalExportJobDownloadPreview(job = {}) {
  const generatedAt = timestamp();
  return {
    schema: "tms-master-portal.report-export-download-preview.v1",
    exportJobId: job.exportJobId || "",
    exportKind: job.exportKind || "",
    generatedAtUtc: generatedAt,
    expiresAtUtc: "",
    downloadEnabled: false,
    reason: "local_preview_not_persisted",
    authorization: {
      allowed: true,
      permission: "reports:export",
      reason: ""
    },
    storage: {
      provider: "browser-local-preview",
      configured: false,
      persisted: false,
      storageObjectPresent: false,
      storageUriExposed: false
    },
    signedDownload: {
      ready: false,
      method: "GET",
      url: "",
      expiresAtUtc: "",
      filename: `${String(job.exportJobId || "export-preview").replace(/[^a-z0-9_-]+/gi, "-")}.${job.format || "csv"}`,
      contentType: job.format === "json" ? "application/json" : "text/csv"
    },
    audit: {
      targetType: "report_export_job",
      targetId: job.exportJobId || "",
      tenantId: job.tenantId || "",
      orgId: job.orgId || "",
      role: currentAccessProfile().label
    },
    policy: {
      tenantScoped: true,
      requiresPermission: "reports:export",
      maxTtlMinutes: 0,
      actualDownloadsBlockedUntil: "managed_export_storage_selected_and_approved",
      deniedFields: ["patient", "patientName", "claimRows", "serviceRows", "notes", "rawRows", "storageUri"]
    }
  };
}

function renderSelectedExportJobDetail() {
  const detail = state.selectedExportJobDetail;
  if (!detail) return "";
  const filterSummary = Object.entries(detail.filters || {}).map(([key, value]) => `${key}: ${key === "action" ? slicerDisplayValue(key, value) : value}`).join(" | ");
  const scopeSummary = Object.entries(detail.scope || {}).map(([key, value]) => `${key}: ${value}`).join(" | ");
  const columnSummary = (detail.columns || []).slice(0, 12).map((column) => `${column.key}${column.valueType ? ` (${column.valueType})` : ""}`).join(", ");
  return `
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(detail.exportJobId || "export job detail")}</strong>
        ${pill(detail.authoritativeDetail ? "Current record" : detail.localPreviewOnly ? "Local Preview" : "Listed record", detail.authoritativeDetail ? "green" : detail.localPreviewOnly ? "amber" : "amber")}
      </div>
      <p>${escapeHtml([detail.exportKind || "export", detail.createdAtUtc, formatNounCount(detail.rowCount || 0, "row"), detail.format || "csv"].filter(Boolean).join(" · "))}</p>
      <p>${escapeHtml(`scope: ${portalCompanyLabel(detail.orgId, clientActionCompanyName(detail.tenantId, detail.orgId) || "Company name unavailable")} · requester: ${detail.requestedByRole || ""} / ${detail.requestedByUserId || ""}`)}</p>
      <p>${escapeHtml(`authorization: ${detail.authorizationAllowed ? "allowed" : "blocked/preview"} · ${detail.authorizationPermission || "reports:export"}${detail.authorizationReason ? ` · ${detail.authorizationReason}` : ""}`)}</p>
      <p>${escapeHtml(`execution: ${detail.executionStatus || ""} · persisted: ${detail.persisted ? "yes" : "no"} · storage: ${detail.storageReady ? "configured" : "not configured"}`)}</p>
      <div class="report-run-columns">
        <div>
          <strong>Filters</strong>
          <p>${escapeHtml(filterSummary || "None")}</p>
        </div>
        <div>
          <strong>Scope</strong>
          <p>${escapeHtml(scopeSummary || "None")}</p>
        </div>
        <div>
          <strong>Allowed Columns</strong>
          <p>${escapeHtml(columnSummary || "None")}</p>
        </div>
        <div>
          <strong>Redacted Fields</strong>
          <p>${escapeHtml((detail.redactedFields || []).join(", ") || "None")}</p>
        </div>
      </div>
      ${renderExportObjectMetadataReview(detail.exportObjectMetadata, {
        configured: detail.exportObjectMetadataConfigured,
        context: "detail"
      })}
    </div>
  `;
}

function renderSelectedExportJobDownloadPreview() {
  const preview = state.selectedExportJobDownloadPreview;
  if (!preview) return "";
  return `
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(preview.exportJobId || "export download preview")}</strong>
        ${pill(preview.downloadEnabled ? "Download Ready" : "Downloads Disabled", preview.downloadEnabled ? "amber" : "green")}
      </div>
      <p>${escapeHtml(`${preview.exportKind || "export"} · ${preview.reason || ""}`)}</p>
      <p>${escapeHtml(`file: ${preview.signedDownload?.filename || ""} · content: ${preview.signedDownload?.contentType || ""}`)}</p>
      <p>${escapeHtml(`storage: ${preview.storage?.provider || ""} · configured: ${preview.storage?.configured ? "yes" : "no"} · signed URL: ${preview.signedDownload?.url ? "present" : "blocked"}`)}</p>
      ${renderExportObjectMetadataReview(preview.exportObjectMetadata, {
        configured: preview.exportObjectMetadataConfigured,
        context: "download preview"
      })}
    </div>
  `;
}

function renderExportObjectMetadataReview(rows = [], { configured = false, context = "" } = {}) {
  const safeRows = sanitizeExportObjectMetadataRows(rows);
  if (!configured && !safeRows.length) return "";
  return `
    <div class="export-object-metadata-review">
      <div class="sync-section-title">
        <strong>Object Metadata</strong>
        <span>${escapeHtml(context || "review")} · ${safeRows.length ? `${safeRows.length} safe object${safeRows.length === 1 ? "" : "s"}` : "no stored object metadata"}</span>
      </div>
      ${safeRows.length ? safeRows.map((row) => `
        <div class="export-object-row">
          <div>
            <span>Object</span>
            <strong>${escapeHtml(row.objectId || "metadata object")}</strong>
          </div>
          <div>
            <span>Hash</span>
            <strong>${escapeHtml(row.sha256 ? `${row.sha256.slice(0, 12)}...${row.sha256.slice(-8)}` : "missing")}</strong>
          </div>
          <div>
            <span>Bytes</span>
            <strong>${escapeHtml(formatExportObjectCount(row.byteLength || 0))}</strong>
          </div>
          <div>
            <span>Rows</span>
            <strong>${escapeHtml(formatExportObjectCount(row.rowCount || 0))}</strong>
          </div>
          <div>
            <span>Retention</span>
            <strong>${escapeHtml(row.retentionUntilUtc || "not set")}</strong>
          </div>
          <div>
            <span>Status</span>
            <strong>${escapeHtml(row.metadataReady ? "metadata ready" : "metadata blocked")} · downloads disabled</strong>
          </div>
        </div>
        <p>${escapeHtml(`file: ${row.filename || "not named"} · ${row.contentType || "content type pending"} · provider: ${row.storageProvider || "managed-export-storage"}`)}</p>
        <p>${escapeHtml(`columns: ${(row.columnKeys || []).slice(0, 12).join(", ") || "none"}`)}</p>
        <p>${escapeHtml(`redacted: ${(row.redactedFields || []).slice(0, 12).join(", ") || "none"} · location suppressed · handle suppressed`)}</p>
      `).join("") : `<p>${escapeHtml("Object metadata repository is configured, but no safe object metadata is available for this export job.")}</p>`}
    </div>
  `;
}

function formatExportObjectCount(value) {
  return formatCount(value);
}

function renderClaimQueueWorkAuditTrail() {
  if (!els.claimQueueWorkAuditTrail) return;
  const rows = (state.claimQueueWorkAuditTrail || []).slice(-25);
  const outcome = state.claimQueueWorkOutcomeSummary || {};
  const outcomeRows = Array.isArray(outcome.rows) ? outcome.rows.slice(0, 8) : [];
  const trendRows = Array.isArray(outcome.trendRows) ? outcome.trendRows.slice(0, 8) : [];
  const exportSummary = outcome.exportSummary || {};
  const totals = outcome.totals || {};
  els.claimQueueWorkAuditTrail.innerHTML = `
    <div class="sync-section-title">
      <strong>Queue Work Audit</strong>
      <span>${workflowApiBase() ? "Metadata-only drill-down review" : "Queue activity is temporarily unavailable"}</span>
    </div>
    ${outcomeRows.length ? `
      <div class="queue-drilldown-summary">
        <div><span>Sessions</span><strong>${formatCount(totals.sessions || outcomeRows.length)}</strong></div>
        <div><span>Touched</span><strong>${formatCount(totals.touchedSessions || 0)}</strong></div>
        <div><span>Untouched</span><strong>${formatCount(totals.untouchedSessions || 0)}</strong></div>
        <div><span>Actions</span><strong>${formatCount(totals.workflowActions || 0)}</strong></div>
      </div>
      ${outcomeRows.map((row) => `
        <div class="sync-item">
          <div class="sync-item-header">
            <strong>${escapeHtml(row.queueKey || "queue")} · ${escapeHtml(row.actorUserId || "user")}</strong>
            ${pill(row.touched ? "Touched" : "Untouched", row.touched ? "green" : "amber")}
          </div>
          <p>${escapeHtml(`${row.queuePayer || "All"} · ${formatNounCount(row.workflowActions || 0, "workflow action")} · ${formatNounCount(row.followUps || 0, "follow-up")} · ${formatNounCount(row.assignments || 0, "assignment")} · ${row.submittedForReview || 0} submitted`)}</p>
          <p>${escapeHtml(`${row.sessionId || ""} · pages ${row.pageViews || 0} · rows ${row.totalRows || 0}`)}</p>
        </div>
      `).join("")}
      ${trendRows.length ? `
        <div class="table-wrap compact-table-wrap">
          <table class="client-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Queue</th>
                <th>User</th>
                <th>Sessions</th>
                <th>Touch Rate</th>
                <th>Actions</th>
                <th>Follow-Ups</th>
                <th>Assignments</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              ${trendRows.map((row) => `
                <tr>
                  <td>${escapeHtml(row.day || "unknown")}</td>
                  <td>${escapeHtml(row.queueKey || "queue")}<span class="subtext">${escapeHtml(row.queuePayer || "All")}</span></td>
                  <td>${escapeHtml(row.actorUserId || "user")}</td>
                  <td>${formatCount(row.sessions || 0)}</td>
                  <td>${nonNegativeNumber(row.touchRate, 0)}%</td>
                  <td>${formatCount(row.workflowActions || 0)}</td>
                  <td>${formatCount(row.followUps || 0)}</td>
                  <td>${formatCount(row.assignments || 0)}</td>
                  <td>${formatCount(row.submittedForReview || 0)}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>
        <p class="sync-note">${escapeHtml(`${exportSummary.exportReady ? "Export-ready summary" : "Summary preview"} · ${formatNounCount(exportSummary.rowCount || trendRows.length, "aggregate row")} · redacted: ${(exportSummary.redactedFields || []).join(", ") || "raw claim fields"}`)}</p>
      ` : ""}
    ` : ""}
    ${rows.length ? rows.map((row) => `
      <div class="sync-item">
        <div class="sync-item-header">
          <strong>${escapeHtml(queueWorkAuditScopeLabel(row))}</strong>
          ${pill(queueWorkDecisionLabel(row), queueWorkDecisionTone(row))}
        </div>
        <p>${escapeHtml(`${row.createdAtUtc || ""} · ${row.actorUserId || ""} · page ${row.pageNumber || 1} · ${queueWorkPageRowsText(row)}`)}</p>
        <p>${escapeHtml(queueWorkAuditFacetLine(row))}</p>
        ${row.redactedPhi || row.redactedFinancials ? `<p>${escapeHtml(`redacted: ${[row.redactedPhi ? "PHI" : "", row.redactedFinancials ? "financials" : ""].filter(Boolean).join(", ")}`)}</p>` : ""}
      </div>
    `).join("") : empty("No queue-work audit rows loaded.")}
  `;
}

// Queue-work rows persist the literal string "unknown" for an unrestricted
// tenant, which rendered as "all · unknown" - the same placeholder cycle 044
// took out of the governance trail directly above it.
function queueWorkAuditScopeLabel(row = {}) {
  const queue = String(row.queueKey || "").trim();
  const tenant = String(row.tenantId || "").trim();
  return [
    !queue || queue.toLowerCase() === "all" ? "All queues" : queue,
    !tenant || tenant.toLowerCase() === "unknown" ? "" : tenant
  ].filter(Boolean).join(" · ");
}

// "All" is this ledger's word for "not narrowed", so printing it back on
// every row says nothing twice. The event type and the source always differ
// enough to be worth the line; the facets only earn it when they are set.
function queueWorkAuditFacetLine(row = {}) {
  return [
    row.eventType || "",
    String(row.queuePayer || "All").toLowerCase() === "all" ? "" : row.queuePayer,
    String(row.slaBucket || "All").toLowerCase() === "all" ? "" : row.slaBucket,
    row.source || "source"
  ].filter(Boolean).join(" · ");
}

function queueWorkPageRowsText(row = {}) {
  const returnedRows = nonNegativeIntegerValue(row.returnedRows || 0);
  const totalRows = nonNegativeIntegerValue(row.totalRows || 0);
  return `${returnedRows.toLocaleString()}/${formatNounCount(totalRows, "row")}`;
}

function queueWorkDecisionLabel(row = {}) {
  if (row.eventType === "claim_queue_work.failed") return "Failed";
  if (row.eventType === "claim_queue_work.denied" || row.decision === "deny") return "Denied";
  if (row.eventType === "claim_queue_work.page") return "Page";
  return "Opened";
}

function queueWorkDecisionTone(row = {}) {
  if (row.eventType === "claim_queue_work.failed") return "red";
  if (row.eventType === "claim_queue_work.denied" || row.decision === "deny") return "red";
  if (row.eventType === "claim_queue_work.page") return "amber";
  return "green";
}

function renderWorkflowSyncStatus() {
  if (!els.workflowSyncStatus) return;
  const summary = workflowCommandSummary();
  const base = workflowApiBase();
  const retryable = summary.failed + summary.localFallback;
  const health = workflowApiHealthSummary();
  els.workflowSyncStatus.innerHTML = `
    <div>
      <strong>Workflow Sync</strong>
      <span>${summary.total ? `${summary.synced} synced · ${summary.pending} pending · ${summary.failed} failed · ${summary.localFallback} local fallback${summary.rejected ? ` · ${summary.rejected} rejected` : ""}` : "No workflow commands queued"}</span>
    </div>
    <div class="sync-actions">
      <span class="sync-pill">${escapeHtml(health.label)}</span>
      <button class="small-button" data-retry-workflow="all" type="button" ${retryable && base ? "" : "disabled"}>Retry</button>
    </div>
  `;
  bindWorkflowRetryButtons(els.workflowSyncStatus);
}

function renderWorkflowCommandTrail() {
  if (!els.workflowCommandTrail) return;
  const commands = (state.workflowCommandQueue || []).filter(command => !activeClientGroupTenant() || activeClientGroupOrgIds().includes(String(command.body?.orgId || ""))).slice(-25).reverse();
  els.workflowCommandTrail.innerHTML = `<div class="sync-section-title"><strong>Workflow Sync</strong></div>` + (commands.length ? commands.map((command) => `
    <div class="sync-item">
      <div class="sync-item-header">
        <strong>${escapeHtml(command.body.claimId || "Unknown claim")} · ${escapeHtml(command.command)}</strong>
        ${pill(workflowCommandStatusLabel(command.status), workflowCommandStatusTone(command.status))}
      </div>
      ${command.status === "rejected" ? `<p class="sync-reject-reason">${escapeHtml(command.result?.response?.error || command.result?.error || ("HTTP " + command.result?.httpStatus))}</p>` : ""}
      <p>${escapeHtml(command.route)} · queued ${escapeHtml(command.queuedAt || "")}${command.deliveredAt ? ` · updated ${escapeHtml(command.deliveredAt)}` : ""}</p>
      ${command.body.queueKey || command.body.queueSessionId ? `<p>${escapeHtml(`Queue context: ${command.body.queueKey || "queue"}${command.body.queueSessionId ? ` · ${command.body.queueSessionId}` : ""}`)}</p>` : ""}
      ${canRetryWorkflowCommand(command) ? `<div class="sync-actions">
        <button class="small-button" data-retry-workflow="${escapeHtml(command.commandId)}" type="button">Retry</button>
      </div>` : ""}
    </div>
  `).join("") : empty("No workflow commands have been queued."));
  bindWorkflowRetryButtons(els.workflowCommandTrail);
}

function bindCardButtons(container = document) {
  Array.from(container.querySelectorAll("[data-open]")).forEach((button) => {
    // Claim ids repeat across EZClaim companies: carry the org the card was rendered
    // for, so openDrawer never resolves a same-id claim cached for another company.
    button.addEventListener("click", () => openDrawer(button.dataset.open, {
      serviceId: button.dataset.serviceId || "",
      orgId: button.dataset.orgId || button.closest?.("[data-row-org-id]")?.dataset?.rowOrgId || ""
    }));
  });
  Array.from(container.querySelectorAll("[data-start]")).forEach((button) => {
    button.addEventListener("click", () => startAssignmentWork(button.dataset.start, { orgId: button.dataset.startOrgId || "" }));
  });
  Array.from(container.querySelectorAll("[data-submit]")).forEach((button) => {
    button.addEventListener("click", () => submitForReview(
      button.dataset.submit,
      getTextareaValue("completion-note", button.dataset.submit),
      getFieldValue("next-action", button.dataset.submit),
      getFieldValue("next-date", button.dataset.submit),
      { orgId: button.dataset.submitOrgId || "" }
    ));
  });
  Array.from(container.querySelectorAll("[data-withdraw-review]")).forEach((button) => {
    button.addEventListener("click", () => withdrawSubmittedReview(button.dataset.withdrawReview, { orgId: button.dataset.withdrawReviewOrgId || "" }));
  });
  Array.from(container.querySelectorAll("[data-undo-approval]")).forEach((button) => {
    button.addEventListener("click", () => undoReviewApproval(button.dataset.undoApproval, { orgId: button.dataset.undoApprovalOrgId || "" }));
  });
  Array.from(container.querySelectorAll("[data-save-followup]")).forEach((button) => {
    button.addEventListener("click", () => saveAssignmentFollowUp(button.dataset.saveFollowup, { orgId: button.dataset.saveFollowupOrgId || "" }));
  });
  Array.from(container.querySelectorAll("[data-delete-task]")).forEach((button) => {
    button.addEventListener("click", () => deleteAssignmentTask(button.dataset.deleteTask, { orgId: button.dataset.deleteTaskOrgId || "" }));
  });
  Array.from(container.querySelectorAll("[data-next-service]")).forEach((select) => {
    select.addEventListener("change", () => {
      const cid = select.dataset.nextService;
      const claim = getClaim(cid);
      const serviceId = String(select.value || "").trim();
      const service = (claim?.services || []).find((row) => String(row.serviceId || "").trim() === serviceId) || null;
      const followUp = service ? (state.followUpByService?.[followUpKey(cid, serviceId)] || {}) : {};
      const actionInput = container.querySelector(`[data-next-action="${escapeCss(cid)}"]`) || document.querySelector(`[data-next-action="${escapeCss(cid)}"]`);
      const dateInput = container.querySelector(`[data-next-date="${escapeCss(cid)}"]`) || document.querySelector(`[data-next-date="${escapeCss(cid)}"]`);
      if (actionInput) actionInput.value = service ? firstPresent(followUp.action, service.action) : "";
      if (dateInput) dateInput.value = service ? firstPresent(followUp.followUpDate, service.followUpDate) : "";
    });
  });
  Array.from(container.querySelectorAll("[data-next-action]")).forEach((select) => {
    select.addEventListener("change", () => {
      const cid = select.dataset.nextAction;
      const dateInput = container.querySelector(`[data-next-date="${escapeCss(cid)}"]`) || document.querySelector(`[data-next-date="${escapeCss(cid)}"]`);
      if (dateInput) dateInput.value = defaultFollowUpDateForAction(select.value, dateInput.value);
    });
  });
  Array.from(container.querySelectorAll("[data-approve]")).forEach((button) => {
    button.addEventListener("click", () => approveReview(button.dataset.approve, getTextareaValue("approval-note", button.dataset.approve), { orgId: button.dataset.approveOrgId || "" }));
  });
  Array.from(container.querySelectorAll("[data-bounce]")).forEach((button) => {
    button.addEventListener("click", () => bounceBack(button.dataset.bounce, getTextareaValue("bounce-note", button.dataset.bounce), { orgId: button.dataset.bounceOrgId || "" }));
  });
}

function getTextareaValue(kind, claimId) {
  const textarea = document.querySelector(`[data-${kind}="${escapeCss(claimId)}"]`);
  return textarea ? textarea.value : "";
}

function getFieldValue(kind, claimId) {
  const field = document.querySelector(`[data-${kind}="${escapeCss(claimId)}"]`);
  return field ? field.value : "";
}

function renderWorkbenchStatusChips(stats) {
  const apiStatus = stats.apiStatus || {};
  const facetStatus = trackerApiFacetStatusSummary();
  // UI-100 #35: explicit "Showing X of Y" when filters narrow the universe.
  const total = Array.isArray(sourceClaims) ? sourceClaims.length : 0;
  const filteredN = parseInt(String(stats.claims).replace(/[^\d]/g, ""), 10);
  const filtersActive = Number.isFinite(filteredN) && total > 0 && filteredN < total;
  const chips = [
    filtersActive ? { label: "Showing", value: `${filteredN.toLocaleString()} of ${total.toLocaleString()}`, tone: "info" } : null,
    { label: "Claims", value: stats.claims, tone: "info" },
    { label: "Service Rows", value: stats.serviceRows, tone: "info" },
    stats.pageRange ? { label: "Page", value: stats.pageRange, tone: "muted" } : null,
    (stats.pastDue === undefined || stats.pastDue === null) ? null : { label: "Past Due", value: stats.pastDue, tone: stats.pastDue > 0 ? "red" : "muted" },
    (stats.owed === undefined || stats.owed === null) ? null : { label: "Owed", value: stats.owed, tone: stats.owedAmount > 0 ? "amber" : "muted" },
    { label: "Status", value: apiStatus.label || "Unavailable", tone: apiStatus.tone || "muted" },
    // Separate chip on purpose: the page lane and the filter lane fail
    // independently, and a healthy Source chip beside a broken rail is what let
    // this go unnoticed.
    facetStatus ? { label: "Filters", value: facetStatus.label, tone: facetStatus.tone } : null
  ].filter(Boolean);
  return chips.map((chip) => `
    <span class="sheet-status-chip sheet-status-chip--${chip.tone}">
      <em>${escapeHtml(chip.label)}</em>
      <strong>${escapeHtml(String(chip.value))}</strong>
    </span>
  `).join("");
}

function setWorkbenchStatusHtml(html) {
  els.workbenchStatus.innerHTML = html;
  if (!("innerText" in els.workbenchStatus) && typeof els.workbenchStatus.textContent === "string") {
    els.workbenchStatus.textContent = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
}

function renderWorkbenchStatus(filtered, mode = "claim", pageWindow = trackerPageWindow(filtered)) {
  if (!els.workbenchStatus) return;
  const apiStatus = trackerApiStatusSummary();
  if (pageWindow.serverBacked) {
    setWorkbenchStatusHtml(renderWorkbenchStatusChips({
      claims: mode === "service" ? "—" : (pageWindow.totalRows || 0).toLocaleString(),
      serviceRows: mode === "service" ? (pageWindow.totalRows || 0).toLocaleString() : "—",
      pageRange: `${pageWindow.startIndex + 1}-${pageWindow.endIndex} / ${pageWindow.totalRows}`,
      apiStatus
    }));
    return;
  }
  const pageRange = filtered.length > pageWindow.rows.length
    ? `${pageWindow.startIndex + 1}-${pageWindow.endIndex} / ${filtered.length}`
    : "";
  if (mode === "service") {
    const claimIds = uniqueClaimIds(filtered.map((row) => row.claim));
    const owedAmount = filtered.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).sourceOutstanding, 0);
    const pastDue = filtered.filter((row) => isPastDue(row.claim)).length;
    setWorkbenchStatusHtml(renderWorkbenchStatusChips({
      claims: claimIds.length.toLocaleString(),
      serviceRows: filtered.length.toLocaleString(),
      pageRange,
      pastDue,
      owed: money(owedAmount),
      owedAmount,
      apiStatus
    }));
    return;
  }
  const serviceRows = filtered.reduce((sum, claim) => sum + claim.services.length, 0);
  const owedAmount = filtered.reduce((sum, claim) => sum + claimFinancialView(claim).sourceOutstanding, 0);
  const pastDue = filtered.filter(isPastDue).length;
  setWorkbenchStatusHtml(renderWorkbenchStatusChips({
    claims: filtered.length.toLocaleString(),
    serviceRows: serviceRows.toLocaleString(),
    pageRange,
    pastDue,
    owed: money(owedAmount),
    owedAmount,
    apiStatus
  }));
}

// Every quick filter the grid can send, with the label it is stated under.
// Keyed by the SAME keys collectTrackerFilters() puts in the request body, so
// the list of filters the reader is shown is derived from the list of filters
// the server was actually given - not hand-maintained beside it.
const TRACKER_FILTER_LABELS = {
  claimIdSearch: "Claim IDs",
  serviceIdSearch: "Service ID",
  search: "SEARCH",
  noteSearch: "Note",
  tenant: "Company",
  provider: "Provider",
  primaryPayer: "Primary",
  secondaryPayer: "Secondary",
  action: "Action",
  match: "Match",
  claimStatus: "Status",
  lifecycle: "Lifecycle",
  aging: "Aging",
  owner: "Owner",
  minBalance: "Owed>=",
  queue: "Queue"
};

// Returns the human statement of one filter, or "" when the value is the
// ABSENCE of a filter ("", null, "All", "all").
//
// This is the rule the 2026-08-03 min-balance defect broke, one layer up from
// where it broke: the grid sent `minBalance: 0`, the server turned it into
// `owed >= 0`, 216,511 rows disappeared, and this bar said "no filters". A
// count is only meaningful next to what it counted, so a filter that reaches
// the query MUST appear here. Unknown keys deliberately fall through to a
// generic `key="value"` rather than being dropped - a filter added later and
// forgotten here still gets stated, ugly and visible, instead of silent.
function describeTrackerFilter(key, value) {
  if (value == null) return "";
  const text = Array.isArray(value) ? value.join(", ") : String(value).trim();
  if (!text || text.toLowerCase() === "all") return "";
  if (key === "fromDos" || key === "toDos") return `${key === "fromDos" ? "Service Date From" : "Service Date To"}="${trackerServiceDateDisplay(text)}"`;
  if (key === "minBalance") return Number(value) > 0 ? `Owed>=${text}` : "";
  if (key === "search") return `SEARCH("${text}")`;
  if (key === "queue") return `Queue="${queueLabel(text)}"`;
  return `${TRACKER_FILTER_LABELS[key] || key}="${key === "action" ? slicerDisplayValue(key, text) : text}"`;
}

// True when ANY filter in the request narrows the book. Derived from the same
// request object and the same describer as the formula bar, so the two can
// never disagree about whether filters are applied.
// Filters the grid can SEND but the server has no column for.
//
// THIS SET IS EMPTY, AND KEEPING IT EMPTY IS THE POINT (2026-08-11). It used to
// hold `match`, `action` and `network`: three quick filters the plan builder
// dropped, so ticking "Not Paid" narrowed the fifty rows already in the browser
// while the pager underneath went on counting the whole book. The owner hit it
// directly — a Match chip next to a claim-id search, one of them server-bound
// and one of them not, in the same query.
//
// What was done about each, rather than disclosing it forever:
//   match    bound. `match_status` is a real column on both lanes; gold's
//            distribution is 100% populated (Paid 170,273 / Over Paid 28,689 /
//            Not Paid 27,862 / Under Paid 14,135 on Dev, 2026-08-11).
//   action   bound through the follow-up OVERLAY, because gold's own
//            [Follow up Action] is NULL on all 245,415 rows and a bare
//            predicate would have returned an empty grid for every value the
//            picker offers.
//   network  REMOVED. No mirror column, no gold column; the only "In/Out of
//            Network" in the warehouse is a fee-schedule RATE dimension, a
//            property of a contract and not of a claim. A dropdown that can
//            never match anything is a lie the reader has to be told about
//            every time they use it, so it stopped being offered.
//
// The machinery below stays. It is the RECIPROCAL of the 2026-08-03 min-balance
// defect — there a filter was applied and not stated; here one would be stated
// and not applied — and the next filter that reaches the request without a
// column must be caught by it rather than by a user. Add the key here and the
// formula bar will name it as unapplied instead of pretending.
const CLIENT_ONLY_TRACKER_FILTER_KEYS = new Set([]);

function trackerFilterIsServerApplied(key) {
  return !CLIENT_ONLY_TRACKER_FILTER_KEYS.has(key) || !trackerApiEnabled();
}

function activeTrackerFilterDescriptions(filters) {
  return Object.entries(filters || {})
    .map(([key, value]) => describeTrackerFilter(key, value))
    .filter(Boolean);
}

// Split into what the server actually narrowed by, and what it ignored.
function partitionTrackerFilterDescriptions(filters) {
  const applied = [];
  const unapplied = [];
  Object.entries(filters || {}).forEach(([key, value]) => {
    const stated = describeTrackerFilter(key, value);
    if (!stated) return;
    (trackerFilterIsServerApplied(key) ? applied : unapplied).push(stated);
  });
  return { applied, unapplied };
}

function renderSheetFormulaBar(filtered, mode = "claim", pageWindow = trackerPageWindow(filtered)) {
  if (!els.sheetFormulaText) return;
  const currentFilters = collectTrackerFilters();
  const formulaSlicerModes = slicerModeState();
  const formulaColumnModes = columnFilterModeState();
  const formulaTerm = (label, key, values, exclude) =>
    `${label} ${exclude ? "NOT IN" : "IN"} (${values.slice(0, 3).map((value) => key === "action" ? slicerDisplayValue(key, value) : value).join(", ")}${values.length > 3 ? ", ..." : ""})`;
  const activeSlicers = Object.entries(ensureSlicerState(state.slicers))
    .filter(([, values]) => Array.isArray(values) && values.length)
    .map(([key, values]) => formulaTerm(slicerLabel(key), key, values, filterModeExcludes(formulaSlicerModes, key)));
  const activeColumnFilters = Object.entries(ensureColumnFilterState(state.columnFilters))
    .filter(([, values]) => Array.isArray(values) && values.length)
    .map(([key, values]) => formulaTerm(columnLabel(key), key, values, filterModeExcludes(formulaColumnModes, key)));
  const quickFilters = partitionTrackerFilterDescriptions(currentFilters);
  const filters = [...activeSlicers, ...activeColumnFilters, ...quickFilters.applied];
  const rowsText = `${pageWindow.totalRows || filtered.length} ${mode === "service" ? "service rows" : "claims"}`;
  const ignoredText = quickFilters.unapplied.length
    ? ` \u00b7 ${quickFilters.unapplied.join(", ")} not available for this view`
    : "";
  els.sheetFormulaText.textContent = filters.length
    ? `=FILTER(AllData, ${filters.join(" * ")})  // ${rowsText}${ignoredText}`
    : `=AllData  // ${rowsText}, no filters${ignoredText}`;
}

function renderSheetTotalsBar(filtered, mode = "claim", pageWindow = trackerPageWindow(filtered), serverBacked = false) {
  if (!els.sheetTotalsBar) return;
  if (serverBacked) {
    els.sheetTotalsBar.innerHTML = `
      <div><span>Rows</span><strong>${(pageWindow.totalRows || filtered.length || 0).toLocaleString()}</strong><small>All matching rows</small></div>
      <div><span>Totals</span><strong>Open Reports</strong><small>See complete financial totals</small></div>
      <div><span>This page</span><strong>${(pageWindow.rows?.length || 0).toLocaleString()}</strong><small>Rows shown now</small></div>
    `;
    return;
  }
  const claimMap = new Map();
  let serviceRows = [];
  if (mode === "service") {
    serviceRows = filtered;
    filtered.forEach((row) => claimMap.set(row.claim.claimId, row.claim));
  } else {
    filtered.forEach((claim) => {
      claimMap.set(claim.claimId, claim);
      claim.services.forEach((service) => serviceRows.push({ claim, service }));
    });
  }
  const claims = Array.from(claimMap.values());
  const expected = serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).expectedReimbursement, 0);
  const paid = serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).insurancePaid, 0);
  const outstanding = serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).sourceOutstanding, 0);
  const underpayment = claims.reduce((sum, claim) => sum + claimUnderpayment(claim), 0);
  const overpayment = claims.reduce((sum, claim) => sum + claimOverpayment(claim), 0);
  const guarded = claims.filter((claim) => evaluateClaimCalculationGuards(claim).length).length;
  const pastDue = claims.filter(isPastDue).length;
  const reconcileDelta = moneyNumber(expected - paid - outstanding);
  // Tracker-parity: 4 primary totals (Claims, Expected, Paid, Outstanding)
  // — the four AR users actually live on. Under/Over rolls into Outstanding's
  // detail line; calculation issues and the difference remain visible but
  // recede when the current rows need no review.
  const primary = [
    ["Claims", claims.length.toLocaleString(), formatNounCount(serviceRows.length, "service row"), ""],
    ["Expected", money(expected), "filtered service total", ""],
    ["Paid", money(paid), "insurance paid", ""],
    ["Outstanding", money(outstanding),
      `${pastDue.toLocaleString()} past due · ${money(underpayment)} under / ${money(overpayment)} over`,
      ""]
  ];
  // Keep both checks visible so "ready" has an explicit reason.
  const guardsHealthy = guarded === 0;
  const deltaHealthy = reconcileDelta === 0;
  const secondary = [
    ["Calculation check", guardsHealthy ? "No issues found" : `${formatNounCount(guarded, "item")} need${guarded === 1 ? "s" : ""} review`,
      guardsHealthy ? "ready" : `${formatNounCount(guarded, "item")} need${guarded === 1 ? "s" : ""} review`,
      guardsHealthy ? "is-secondary" : "is-warning"],
    ["Difference", money(reconcileDelta),
      "expected - paid - outstanding",
      deltaHealthy ? "is-secondary" : "is-warning"]
  ];
  els.sheetTotalsBar.innerHTML = primary.concat(secondary).map(([label, value, detail, cls]) => `
    <div class="${cls}" title="${escapeHtml(detail)}">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
      <small>${escapeHtml(detail)}</small>
    </div>
  `).join("");
}

// ALL DATA TOTALS FOOTER (Nathan, 2026-10-14). Pinned under the grid, like the
// subtotal row at the bottom of the old tracker: Claims, Units, Charges,
// Expected, Paid and Outstanding over EVERY row the current filters match, not
// just the visible page. On the live lane the server sums them in one
// aggregate (POST /api/tracker/totals); paging and sorting never change the
// totals, so they are only re-asked when the filters change. The read waits for
// the grid's own page so it never competes with the first paint.
let allDataTotalsState = { key: "", status: "idle", totals: null, controller: null };

function allDataTotalsKey(query) {
  const { page, sort, responseShape, access, ...filterShape } = query || {};
  return JSON.stringify({
    publicationEpoch: typeof trackerPublicationEpoch === "number" ? trackerPublicationEpoch : 0,
    base: trackerApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    filterShape
  });
}

function requestAllDataTotals(query, key) {
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") return;
  if (allDataTotalsState.controller) allDataTotalsState.controller.abort();
  const controller = new AbortController();
  allDataTotalsState = { key, status: "pending", totals: null, controller };
  const token = trackerApiToken();
  const timeoutId = window.setTimeout(() => controller.abort(), 45000);
  const { responseShape, ...rest } = query;
  const body = { ...rest, page: { number: 1, size: 1 }, totalsOnly: true };
  fetch(portalFreshReadUrl(`${base}/api/tracker/totals`), {
    method: "POST",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
    signal: controller.signal
  })
    .then(async (response) => {
      const payload = await response.json().catch(() => null);
      window.clearTimeout(timeoutId);
      if (allDataTotalsState.controller !== controller) return;
      const ok = response.ok && payload && payload.totals && typeof payload.totals === "object";
      allDataTotalsState = { key, status: ok ? "ok" : "error", totals: ok ? payload.totals : null, controller: null };
      paintAllDataTotalsFooter();
    })
    .catch(() => {
      window.clearTimeout(timeoutId);
      if (allDataTotalsState.controller !== controller) return;
      allDataTotalsState = { key, status: "error", totals: null, controller: null };
      paintAllDataTotalsFooter();
    });
}

function localAllDataTotals(filtered, mode) {
  const serviceRows = mode === "service"
    ? filtered
    : filtered.flatMap((claim) => (claim.services || []).map((service) => ({ claim, service })));
  const claimKeys = new Set();
  const totals = { serviceLines: serviceRows.length, claims: 0, units: 0, charges: 0, totalExpected: 0, totalInsAmtPaid: 0, owed: 0 };
  serviceRows.forEach(({ claim, service }) => {
    claimKeys.add(`${claim.orgId || claim.client || ""}\u0000${claim.claimId}`);
    const fin = serviceFinancialView(service, claim);
    totals.units += Number(service.units) || 0;
    totals.charges += moneyNumber(service.charges || 0);
    totals.totalExpected += fin.expectedReimbursement || 0;
    totals.totalInsAmtPaid += fin.insurancePaid || 0;
    totals.owed += fin.sourceOutstanding || 0;
  });
  totals.claims = claimKeys.size;
  if (!hasPermission(PERMISSIONS.VIEW_FINANCIALS)) {
    ["charges", "totalExpected", "totalInsAmtPaid", "owed"].forEach((key) => { totals[key] = null; });
  }
  return totals;
}

function renderAllDataTotalsFooter(query, filtered, mode, serverBacked) {
  if (!els.allDataTotalsFooter) return;
  if (!trackerApiEnabled()) {
    allDataTotalsState = { key: "local", status: "ok", totals: localAllDataTotals(filtered, mode), controller: null };
  } else {
    const key = allDataTotalsKey(query);
    if (allDataTotalsState.key !== key) {
      if (serverBacked) requestAllDataTotals(query, key);
      else if (allDataTotalsState.controller) {
        allDataTotalsState.controller.abort();
        allDataTotalsState = { key: "", status: "pending", totals: null, controller: null };
      } else if (allDataTotalsState.status !== "pending") {
        allDataTotalsState = { key: "", status: "pending", totals: null, controller: null };
      }
    }
  }
  paintAllDataTotalsFooter();
}

function paintAllDataTotalsFooter() {
  const footer = els.allDataTotalsFooter;
  if (!footer) return;
  const tableHidden = document.getElementById("trackerTableWrap")?.style.display === "none";
  footer.hidden = tableHidden;
  if (tableHidden) return;
  const { status, totals } = allDataTotalsState;
  const pending = status === "pending" || status === "idle";
  const failed = status === "error";
  const units = (value) => Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
  // The server already nulls totals a principal cannot see; this mirrors the
  // grid, so a total never shows for a column this role does not see.
  const permitted = new Set(permittedColumnsForMode("service").map((column) => column.key));
  const cell = (label, field, format, detail) => {
    if (field !== "claims" && !permitted.has(field)) return "";
    if (totals && totals[field] === null) return "";
    const value = pending ? "\u2026" : failed || !totals ? "\u2014" : format(totals[field]);
    return `<div class="all-data-total"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong><small>${escapeHtml(detail)}</small></div>`;
  };
  const lines = totals && !pending ? formatNounCount(totals.serviceLines, "service line") : "service lines";
  footer.innerHTML = `
    <div class="all-data-totals-label" title="${failed ? "Totals could not be loaded. They will retry when the filters change." : "Totals for every row that matches the current filters, not just this page."}">Totals<br>(filtered)</div>
    ${cell("Claims", "claims", (v) => Number(v).toLocaleString(), lines)}
    ${cell("Units", "units", units, "billed units")}
    ${cell("Charges", "charges", money, "billed charges")}
    ${cell("Expected", "totalExpected", money, "total expected")}
    ${cell("Paid", "totalInsAmtPaid", money, "insurance paid")}
    ${cell("Outstanding", "owed", money, "insurance still owes")}
  `;
  footer.classList.toggle("is-pending", pending);
  footer.classList.toggle("is-error", failed);
}

function dataSourceConfidenceModel(filtered = [], mode = "service", serverBacked = false, pageWindow = trackerPageWindow(filtered)) {
  const apiConfigured = trackerApiEnabled();
  const apiOwned = serverBacked || apiConfigured;
  const claimMap = new Map();
  const serviceRows = mode === "service"
    ? filtered.filter((row) => row?.claim && row?.service)
    : filtered.flatMap((claim) => {
      if (!claim?.claimId) return [];
      claimMap.set(claim.claimId, claim);
      return (claim.services || []).map((service) => ({ claim, service }));
    });
  if (mode === "service") {
    serviceRows.forEach((row) => claimMap.set(row.claim.claimId, row.claim));
  }
  const claims = Array.from(claimMap.values());
  const expected = moneyNumber(serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).expectedReimbursement, 0));
  const paid = moneyNumber(serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).insurancePaid, 0));
  const outstanding = moneyNumber(serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).sourceOutstanding, 0));
  const formulaDelta = moneyNumber(expected - paid - outstanding);
  const stamp = liveDataStamp();
  const generatedAt = (stamp && stamp.toISOString()) || sourceDataInfo.generatedAt || "";
  const generatedDate = generatedAt ? new Date(generatedAt) : null;
  const ageDays = generatedDate && !Number.isNaN(generatedDate.getTime())
    ? Math.max(0, Math.floor((todayStart() - new Date(`${localDateString(generatedDate)}T00:00:00`)) / 86400000))
    : null;
  const freshness = dataSourceFreshnessStatus(ageDays, sourceDataInfo.isLocalRealData || apiOwned, apiOwned);
  const guardedClaims = claims.filter((claim) => evaluateClaimCalculationGuards(claim).length).length;
  const translatorReview = claims.filter((claim) => /translator|validation|not yet attributed|conflict|unmatched/i.test(String(claim.externalPayerStatus || claim.paymentAttribution?.status || ""))).length;
  const missingState = claims.filter((claim) => !clientStateValue(claim) || clientStateValue(claim) === "State Not Tagged").length;
  const sourceStats = sourceDataInfo.sourceStats || {};
  const totalSourceClaims = nonNegativeIntegerValue(sourceStats.claims) || sourceClaims.length || 0;
  const totalSourceServices = nonNegativeIntegerValue(sourceStats.serviceLines) || sourceClaims.reduce((sum, claim) => sum + (claim.services || []).length, 0);
  const sourceMode = apiOwned ? "Current information" : sourceDataInfo.isLocalRealData ? "Current information" : "Information unavailable";
  const sourceProof = generatedAt ? `Updated ${formatDateTime(generatedAt)}` : "Last-updated time unavailable";
  const rowLimit = nonNegativeIntegerValue(sourceDataInfo.rowLimit);
  const cappedSource = rowLimit > 0 && totalSourceServices >= rowLimit;
  const certification = dataSourceCertification({ serverBacked: apiOwned, formulaDelta, freshness, guardedClaims, translatorReview, missingState });
  const rows = [
    {
      label: "Information Status",
      status: sourceMode,
      proof: sourceProof,
      risk: apiOwned || sourceDataInfo.isLocalRealData ? "Current information available" : "Current information unavailable",
      tone: apiOwned || sourceDataInfo.isLocalRealData ? "green" : "amber"
    },
    {
      label: "Freshness",
      status: freshness.label,
      proof: generatedAt ? formatDateTime(generatedAt) : "No generated timestamp",
      risk: freshness.detail,
      tone: freshness.tone
    },
    {
      label: "Filtered Scope",
      status: `${formatNounCount(claims.length, "claim")} · ${formatNounCount(serviceRows.length, "service row")}`,
      proof: `${formatNounCount(totalSourceClaims, "claim")} · ${formatNounCount(totalSourceServices, "service line")}`,
      risk: cappedSource ? `The current view reached its ${rowLimit.toLocaleString()}-row limit` : apiOwned ? `${(pageWindow.totalRows || filtered.length || 0).toLocaleString()} rows in the current view` : "Filtered from the current view",
      tone: cappedSource ? "amber" : "blue"
    },
    {
      label: "Formula Proof",
      status: money(formulaDelta),
      proof: `${money(expected)} expected - ${money(paid)} paid - ${money(outstanding)} AR`,
      risk: formulaDelta === 0 ? "Filtered money ties out" : "Reconcile before closing work",
      tone: formulaDelta === 0 ? "green" : "red"
    },
    {
      label: "Guardrails",
      status: `${guardedClaims} guarded · ${translatorReview} payer review`,
      proof: `${missingState} missing state tag · ${claims.length - guardedClaims} unguarded claims`,
      risk: guardedClaims || translatorReview || missingState ? "Review information gaps before final decisions" : "No visible information warnings",
      tone: guardedClaims || translatorReview || missingState ? "amber" : "green"
    },
    {
      label: "Adapter Mode",
      status: apiOwned ? "Current page" : "Current filtered view",
      proof: apiOwned ? "The requested page loaded successfully" : "Rows filtered from the current view",
      risk: apiOwned ? "Use complete reports when reviewing aggregate totals" : "Current information is unavailable for production decisions",
      tone: apiOwned ? "blue" : "amber"
    }
  ];
  return {
    rows,
    totals: {
      sourceMode,
      certification,
      ageDays,
      claims: claims.length,
      serviceRows: serviceRows.length,
      sourceClaims: totalSourceClaims,
      sourceServiceRows: totalSourceServices,
      expected,
      paid,
      outstanding,
      formulaDelta,
      guardedClaims,
      translatorReview,
      missingState,
      serverBacked: apiOwned
    }
  };
}

function dataSourceFreshnessStatus(ageDays, isRealData, apiOwned = false) {
  if (apiOwned) {
    const mins = liveDataStampAgeMinutes();
    if (mins == null) {
      return { label: "Timestamp missing", tone: "amber", detail: "The last-updated time is not available." };
    }
    if (mins < 60) {
      return { label: "Current", tone: "green", detail: `Updated ${formatLiveDataStampAge(mins)}.` };
    }
    if (mins < 1440) {
      return { label: `${Math.round(mins / 60)} h old`, tone: "amber", detail: `Updated ${formatLiveDataStampAge(mins)}. Totals may lag new billing.` };
    }
    return { label: `${Math.round(mins / 1440)} d old`, tone: "red", detail: "This information may be out of date. Refresh before making final decisions." };
  }
  if (!isRealData) return { label: "Local fallback", tone: "amber", detail: "Use for workflow preview, not production cash decisions." };
  if (ageDays == null) return { label: "Timestamp missing", tone: "amber", detail: "The last-updated time cannot be verified." };
  if (ageDays <= 2) return { label: `${ageDays} day${ageDays === 1 ? "" : "s"} old`, tone: "green", detail: "Fresh enough for daily RCM work." };
  if (ageDays <= 7) return { label: `${ageDays} days old`, tone: "amber", detail: "Verify current payer activity before final close." };
  return { label: `${ageDays} days old`, tone: "red", detail: "This information may be out of date. Refresh before making final decisions." };
}

function dataSourceCertification({ serverBacked, formulaDelta, freshness, guardedClaims, translatorReview, missingState }) {
  if (serverBacked) return { label: "Server Page", tone: "blue", detail: "Use complete reports when reviewing aggregate totals." };
  if (Math.abs(formulaDelta) > 0) return { label: "Reconcile", tone: "red", detail: "Filtered expected, paid, and AR do not tie out." };
  if (freshness.tone === "red") return { label: "Refresh Needed", tone: "red", detail: "Data is stale even though current filtered math ties out." };
  if (guardedClaims || translatorReview || missingState || freshness.tone === "amber") return { label: "Guarded", tone: "amber", detail: "Money ties, but source freshness or data guardrails need review." };
  return { label: "Trusted Operational View", tone: "green", detail: "Current information is available and filtered totals reconcile." };
}

function renderDataSourceConfidence(filtered = [], mode = "service", serverBacked = false, pageWindow = trackerPageWindow(filtered)) {
  if (!els.dataSourceConfidence) return;
  els.dataSourceConfidence.innerHTML = "";
  return;
  const model = dataSourceConfidenceModel(filtered, mode, serverBacked, pageWindow);
  const { totals } = model;
  els.dataSourceConfidence.innerHTML = `
    <section class="source-confidence-shell source-confidence-shell--${escapeHtml(totals.certification.tone)}" aria-label="Data source confidence panel">
      <div class="source-confidence-head">
        <div>
          <span>Data Source Confidence</span>
          <strong>${escapeHtml(totals.certification.label)} · ${escapeHtml(totals.sourceMode)}</strong>
          <p>${escapeHtml(totals.certification.detail)} The active workbook scope must show source freshness, row scope, source guardrails, and Expected - Paid - AR proof before users work money.</p>
        </div>
        ${pill(totals.certification.label, totals.certification.tone)}
      </div>
      <div class="source-confidence-summary">
        <div><span>Source Claims</span><strong>${totals.sourceClaims.toLocaleString()}</strong><small>${formatNounCount(totals.sourceServiceRows, "source service row")}</small></div>
        <div><span>Filtered Scope</span><strong>${totals.claims.toLocaleString()}</strong><small>${formatNounCount(totals.serviceRows, "filtered service row")}</small></div>
        <div><span>Expected</span><strong>${money(totals.expected)}</strong><small>Filtered source expectation</small></div>
        <div><span>Paid</span><strong>${money(totals.paid)}</strong><small>Filtered insurance paid</small></div>
        <div><span>AR</span><strong>${money(totals.outstanding)}</strong><small>Filtered tracker balance</small></div>
        <div><span>Formula Delta</span><strong>${money(totals.formulaDelta)}</strong><small>Expected - Paid - AR</small></div>
      </div>
      <div class="source-confidence-list">
        <div class="source-confidence-row source-confidence-header">
          <span>Check</span>
          <span>Status</span>
          <span>Proof</span>
          <span>Risk / Action</span>
        </div>
        ${model.rows.map(renderDataSourceConfidenceRow).join("")}
      </div>
    </section>
  `;
}

function renderDataSourceConfidenceRow(row) {
  return `
    <div class="source-confidence-row">
      <span data-label="Check"><strong>${escapeHtml(row.label)}</strong></span>
      <span data-label="Status">${pill(row.status, row.tone)}</span>
      <span data-label="Proof">${escapeHtml(row.proof)}</span>
      <span data-label="Risk / Action">${escapeHtml(row.risk)}</span>
    </div>
  `;
}

function workbenchFormulaAudit(filtered = [], mode = "service") {
  const serviceRows = mode === "service"
    ? filtered.filter((row) => row?.claim && row?.service)
    : filtered.flatMap((claim) => (claim?.services || []).map((service) => ({ claim, service })));
  const selectedIds = new Set(Array.isArray(state.selectedClaims) ? state.selectedClaims : []);
  const rows = serviceRows.map(({ claim, service }) => {
    const financial = serviceFinancialView(service, claim);
    const guards = evaluateClaimCalculationGuards(claim, service);
    const delta = moneyNumber(financial.expectedReimbursement - financial.insurancePaid - financial.sourceOutstanding);
    const expectedBalance = moneyNumber(Math.max(financial.expectedReimbursement - financial.insurancePaid, 0));
    const guardCodes = guards.map((guard) => guard.code);
    return {
      claim,
      service,
      claimId: claim.claimId || "",
      serviceId: service.serviceId || "",
      payer: claim.primaryPayer || claim.secondaryPayer || "Unknown payer",
      client: claim.client || "Unknown client",
      procedure: service.procedure || service.code || "",
      expected: financial.expectedReimbursement,
      paid: financial.insurancePaid,
      outstanding: financial.sourceOutstanding,
      expectedBalance,
      delta,
      deltaAbs: Math.abs(delta),
      guards,
      guardCodes,
      guardSummary: calculationGuardSummary(guards),
      selected: selectedIds.has(claim.claimId || ""),
      lane: formulaAuditLane({ guards, delta, financial, claim, service }),
      nextAction: formulaAuditNextAction({ guards, delta, financial, claim, service })
    };
  });
  const totals = rows.reduce((sum, row) => {
    sum.expected += row.expected;
    sum.paid += row.paid;
    sum.outstanding += row.outstanding;
    if (row.guards.length) sum.guardedRows += 1;
    if (row.deltaAbs === 0 && !row.guards.length) sum.cleanRows += 1;
    if (row.selected) {
      sum.selectedRows += 1;
      sum.selectedExpected += row.expected;
      sum.selectedPaid += row.paid;
      sum.selectedOutstanding += row.outstanding;
    }
    return sum;
  }, {
    rows: rows.length,
    expected: 0,
    paid: 0,
    outstanding: 0,
    guardedRows: 0,
    cleanRows: 0,
    selectedRows: 0,
    selectedExpected: 0,
    selectedPaid: 0,
    selectedOutstanding: 0
  });
  totals.expected = moneyNumber(totals.expected);
  totals.paid = moneyNumber(totals.paid);
  totals.outstanding = moneyNumber(totals.outstanding);
  totals.formulaBalance = moneyNumber(totals.expected - totals.paid);
  totals.delta = moneyNumber(totals.formulaBalance - totals.outstanding);
  totals.selectedExpected = moneyNumber(totals.selectedExpected);
  totals.selectedPaid = moneyNumber(totals.selectedPaid);
  totals.selectedOutstanding = moneyNumber(totals.selectedOutstanding);
  totals.selectedDelta = moneyNumber(totals.selectedExpected - totals.selectedPaid - totals.selectedOutstanding);
  totals.issueRows = rows.filter((row) => row.deltaAbs !== 0 || row.guards.length).length;
  totals.cleanPercent = totals.rows ? Math.round((totals.cleanRows / totals.rows) * 100) : 100;
  totals.certification = workbenchAuditCertification(totals);
  const lanes = workbenchAuditIssueLanes(rows);
  const reviewRows = rows
    .filter((row) => row.deltaAbs !== 0 || row.guards.length)
    .sort((a, b) => b.deltaAbs - a.deltaAbs || b.outstanding - a.outstanding || a.claimId.localeCompare(b.claimId))
    .slice(0, 7);
  return { totals, rows, lanes, reviewRows };
}

function formulaAuditLane({ guards = [], delta = 0, financial = {}, claim = {} }) {
  const codes = guards.map((guard) => guard.code);
  if (codes.includes("EXPECTED_REIMBURSEMENT_INCOMPLETE")) return "Rate/Auth";
  if (codes.includes("PAYER_SEQUENCE_REVIEW") || sameExternalPayerConflict(claim) || Number(financial.unmatchedPaid || 0) > 0) return "Payer Sequence";
  if (codes.includes("PR_REASON_DETAIL_MISSING") || codes.includes("ADJUSTMENT_DETAIL_MISSING")) return "ERA / PR Proof";
  if (Math.abs(delta) > 0) return "Formula Delta";
  return "Certified";
}

function formulaAuditNextAction({ guards = [], delta = 0, financial = {}, claim = {} }) {
  const codes = guards.map((guard) => guard.code);
  if (codes.includes("EXPECTED_REIMBURSEMENT_INCOMPLETE")) return "Load fee schedule, authorization, or payer-rate evidence before closeout.";
  if (codes.includes("PAYER_SEQUENCE_REVIEW") || sameExternalPayerConflict(claim) || Number(financial.unmatchedPaid || 0) > 0) return "Confirm primary/secondary attribution using external payer ID and payment sequence.";
  if (codes.includes("PR_REASON_DETAIL_MISSING")) return "Verify PR 1/2/3 detail before moving balance to patient responsibility.";
  if (codes.includes("ADJUSTMENT_DETAIL_MISSING")) return "Attach ERA/EOB adjustment proof or keep the row in payer follow-up.";
  if (Math.abs(delta) > 0) return "Compare expected, paid, and outstanding amounts before approving.";
  return "Expected, paid, and outstanding amounts match.";
}

function workbenchAuditIssueLanes(rows = []) {
  const laneOrder = ["Formula Delta", "Rate/Auth", "Payer Sequence", "ERA / PR Proof", "Certified"];
  const laneMap = new Map(laneOrder.map((label) => [label, {
    label,
    rows: 0,
    expected: 0,
    paid: 0,
    outstanding: 0,
    delta: 0
  }]));
  rows.forEach((row) => {
    const lane = laneMap.get(row.lane) || laneMap.get("Formula Delta");
    lane.rows += 1;
    lane.expected += row.expected;
    lane.paid += row.paid;
    lane.outstanding += row.outstanding;
    lane.delta += row.delta;
  });
  return laneOrder.map((label) => {
    const lane = laneMap.get(label);
    return {
      ...lane,
      expected: moneyNumber(lane.expected),
      paid: moneyNumber(lane.paid),
      outstanding: moneyNumber(lane.outstanding),
      delta: moneyNumber(lane.delta)
    };
  }).filter((lane) => lane.rows > 0);
}

function workbenchAuditCertification(totals) {
  if (!totals.rows) return {
    label: "No rows",
    tone: "muted",
    detail: "No service rows match the selected filters."
  };
  if (totals.issueRows === 0 && totals.delta === 0) return {
    label: "Ready",
    tone: "green",
    detail: "Matching rows have no differences or calculation issues."
  };
  if (Math.abs(totals.delta) > 0) return {
    label: "Needs review",
    tone: "amber",
    detail: "Expected, paid, and outstanding amounts do not match."
  };
  return {
    label: "Needs review",
    tone: "blue",
    detail: "Totals match, but some calculation items still need review."
  };
}

function renderWorkbenchFormulaAudit(filtered = [], mode = "service", serverBacked = false, pageWindow = trackerPageWindow(filtered)) {
  if (!els.workbenchFormulaAudit) return;
  if (serverBacked) {
    els.workbenchFormulaAudit.innerHTML = `
      <section class="formula-audit-shell">
        <div class="formula-audit-head">
          <div>
            <span>AR calculation check</span>
            <strong>Open Reports to check all matching rows</strong>
            <p>This page is ready to review. Reports shows the complete totals for every matching row.</p>
          </div>
          <div class="formula-audit-source">
            <small>This page</small>
            <strong>${(pageWindow.rows?.length || 0).toLocaleString()}</strong>
            <em>${(pageWindow.totalRows || filtered.length || 0).toLocaleString()} total rows</em>
          </div>
        </div>
      </section>
    `;
    return;
  }
  const audit = workbenchFormulaAudit(filtered, mode);
  const { totals, lanes, reviewRows } = audit;
  els.workbenchFormulaAudit.innerHTML = `
    <section class="formula-audit-shell">
      <div class="formula-audit-head">
        <div>
          <span>AR calculation check</span>
          <strong>${escapeHtml(money(totals.delta))} difference across ${formatNounCount(totals.rows, "service row")}</strong>
          <p>Compares expected, paid, and outstanding amounts for the matching service lines.</p>
        </div>
        <div class="formula-audit-source">
            <small>Formula</small>
            <strong>Expected - Paid - AR</strong>
            <em>${escapeHtml(money(totals.expected))} - ${escapeHtml(money(totals.paid))} - ${escapeHtml(money(totals.outstanding))}</em>
        </div>
      </div>
      <div class="formula-audit-cert formula-audit-cert--${escapeHtml(totals.certification.tone)}">
        <div>
          <span>Overall check</span>
          <strong>${escapeHtml(totals.certification.label)}</strong>
          <small>${escapeHtml(totals.certification.detail)}</small>
        </div>
        <div>
          <span>Clean Row Rate</span>
          <strong>${totals.cleanPercent.toLocaleString()}%</strong>
          <small>${totals.cleanRows.toLocaleString()} of ${formatNounCount(totals.rows, "service row")}</small>
        </div>
        <div>
          <span>Selected Rows</span>
          <strong>${totals.selectedRows.toLocaleString()}</strong>
          <small>${escapeHtml(money(totals.selectedDelta))} selected difference</small>
        </div>
        <div>
          <span>Workbook Formula</span>
          <strong>=SUM(Expected)-SUM(Paid)-SUM(AR)</strong>
          <small>Same math as the filtered sheet and client charts.</small>
        </div>
      </div>
      <div class="formula-audit-metrics">
        ${[
          ["Expected", money(totals.expected), "filtered service lines"],
          ["Paid", money(totals.paid), "attributed insurance paid"],
          ["AR", money(totals.outstanding), "tracker open balance"],
          ["Ready rows", totals.cleanRows.toLocaleString(), "no difference or calculation issue"],
          ["Rows to review", totals.guardedRows.toLocaleString(), "check these first"],
          ["Issue rows", totals.issueRows.toLocaleString(), "difference or calculation issue"]
        ].map(([label, value, detail]) => `
          <div>
            <span>${escapeHtml(label)}</span>
            <strong>${escapeHtml(value)}</strong>
            <small>${escapeHtml(detail)}</small>
          </div>
        `).join("")}
      </div>
      <div class="formula-audit-lanes" aria-label="Calculation check issue groups">
        ${lanes.map(renderFormulaAuditLane).join("")}
      </div>
      <div class="formula-audit-table" role="table" aria-label="Calculation check rows">
        <div class="formula-audit-row formula-audit-row--head" role="row">
          <span>Claim / Service</span>
          <span>Client / Payer</span>
          <span>Expected</span>
          <span>Paid</span>
          <span>AR</span>
          <span>Difference / Next step</span>
        </div>
        ${reviewRows.length ? reviewRows.map(renderFormulaAuditRow).join("") : `
          <div class="formula-audit-empty">All visible service rows match. No calculation issues found.</div>
        `}
      </div>
    </section>
  `;
}

function formulaAuditLaneLabel(lane) {
  return {
    "Formula Delta": "Totals differ",
    "Rate/Auth": "Rate or authorization",
    "Payer Sequence": "Payer order",
    "ERA / PR Proof": "Payment details",
    Certified: "Ready"
  }[lane] || lane;
}

function renderFormulaAuditLane(lane) {
  const label = formulaAuditLaneLabel(lane.label);
  return `
    <div class="formula-audit-lane">
      <span>${escapeHtml(label)}</span>
      <strong>${lane.rows.toLocaleString()}</strong>
      <small>${escapeHtml(money(lane.outstanding))} AR · ${escapeHtml(money(lane.delta))} difference</small>
    </div>
  `;
}

function renderFormulaAuditRow(row) {
  const tone = row.deltaAbs !== 0 ? "is-warning" : (row.guards.length ? "is-guarded" : "");
  return `
    <button type="button" class="formula-audit-row ${tone}" data-formula-open="${escapeHtml(row.claimId)}" role="row">
      <span><strong>${escapeHtml(row.claimId)}</strong><small>${escapeHtml(row.serviceId || row.procedure || "Service row")}</small></span>
      <span><strong>${escapeHtml(row.client)}</strong><small>${escapeHtml(row.payer)}</small></span>
      <span>${escapeHtml(money(row.expected))}</span>
      <span>${escapeHtml(money(row.paid))}</span>
      <span>${escapeHtml(money(row.outstanding))}</span>
      <span><strong>${escapeHtml(money(row.delta))}</strong><small>${escapeHtml(formulaAuditLaneLabel(row.lane))} · ${escapeHtml(row.nextAction)}</small></span>
    </button>
  `;
}

function columnLabel(key) {
  const column = [...serviceColumns, ...claimColumns].find((item) => item.key === key);
  return column ? column.label : key;
}

// EVERY READ FIRST, THEN EVERY WRITE. This is the last thing a grid render
// does, and it used to read three geometry properties, WRITE the rail's width,
// and then read scrollLeft again - which invalidates and re-lays-out the whole
// document between the two reads. On a 500-row x 48-column page that is two
// full layouts of a 25,000-cell auto-layout table instead of one; measured at
// 680ms of a 690ms render on the QA harness. Reading scrollLeft before the
// width is written cannot change its value - setting the rail's inner width
// does not scroll the grid - so the rail lands in exactly the same place.
function updateTopGridScroll() {
  const table = document.querySelector("#trackerTableWrap .claims-table");
  if (!table || !els.topGridScrollInner || !els.topGridScroll || !els.trackerTableWrap) return;
  const width = Math.max(table.scrollWidth, els.trackerTableWrap.scrollWidth, els.trackerTableWrap.clientWidth);
  const scrollLeft = els.trackerTableWrap.scrollLeft;
  els.topGridScrollInner.style.width = `${width}px`;
  els.topGridScroll.scrollLeft = scrollLeft;
}

function trackerPageWindow(rows, pageRequest = { number: state.trackerPage, size: state.trackerPageSize }, options = {}) {
  const totalRows = rows.length;
  const pageSize = trackerPageSizeValue(pageRequest.size);
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const page = Math.min(Math.max(1, positiveNumber(pageRequest.number, 1)), totalPages);
  const mutateState = options.mutateState !== false;
  if (mutateState && page !== state.trackerPage) state.trackerPage = page;
  if (mutateState && pageSize !== state.trackerPageSize) state.trackerPageSize = pageSize;
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRows);
  return {
    page,
    pageSize,
    totalPages,
    startIndex,
    endIndex,
    totalRows,
    rows: rows.slice(startIndex, endIndex)
  };
}

function trackerApiPageWindow(response) {
  const page = response?.page || {};
  const rows = Array.isArray(response?.rows) ? response.rows : [];
  const pageSize = trackerPageSizeValue(page.size || state.trackerPageSize);
  const totalRows = nonNegativeNumber(page.totalRows, rows.length);
  const totalPages = Math.max(1, positiveNumber(page.totalPages, Math.ceil(totalRows / pageSize)));
  const pageNumber = Math.min(positiveNumber(page.number, 1), totalPages);
  const startIndex = Math.min(totalRows, nonNegativeNumber(page.startIndex, (pageNumber - 1) * pageSize));
  const endIndex = Math.max(startIndex, Math.min(totalRows || rows.length, nonNegativeNumber(page.endIndex, startIndex + rows.length)));
  return {
    page: pageNumber,
    pageSize,
    totalPages,
    startIndex,
    endIndex,
    totalRows,
    rows,
    serverBacked: true
  };
}

function positiveNumber(value, fallback) {
  const number = parseMoneyNumber(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function nonNegativeNumber(value, fallback = 0) {
  const number = parseMoneyNumber(value);
  return Number.isFinite(number) && number >= 0 ? number : fallback;
}

function trackerPageSizeValue(value) {
  const requested = positiveNumber(value, MAX_RENDERED_TRACKER_ROWS);
  return TRACKER_PAGE_SIZE_OPTIONS.includes(requested) ? requested : MAX_RENDERED_TRACKER_ROWS;
}

function trackerApiPageSizeValue(value) {
  return Math.min(trackerPageSizeValue(value), TRACKER_API_PAGE_SIZE);
}

function renderTrackerPager(pageWindow, options = {}) {
  if (!els.trackerPageText || !els.prevTrackerPageBtn || !els.nextTrackerPageBtn) return;
  const navigationDisabled = options.navigationDisabled === true;
  // "Rows 0-0 of 0" is a MEASUREMENT. Printing it while the request is in
  // flight, has failed, or was never issued reports a count nobody counted -
  // and it is the single most convincing thing on the screen, because it looks
  // like arithmetic. Say what is actually true instead.
  //
  // Scoped to a window that is NOT response-backed. During a page turn the
  // grid deliberately keeps the PREVIOUS page on screen while the next one
  // loads (trackerApiStalePageForQuery), and that window's totals ARE measured
  // -- verify_portal.js pins them. Keying on the lane alone would blank a
  // number the user is looking at, every time they pressed Next.
  // LOAD-BEARING: without the denial term the LOCAL fallback lane forces
  // measured = true and reprints "Rows 0-0 of 0" - the exact string in the
  // 2026-08-03 mark screenshot, over a principal the server never served.
  const denied = typeof portalScopeDenial === "function" && Boolean(portalScopeDenial());
  const measured = !denied && (!trackerApiEnabled() || pageWindow.serverBacked === true);
  const lane = measured ? "answered" : trackerApiLaneState();
  const unmeasured = lane === "denied" ? "Rows unavailable - your access is not set up yet"
    : lane === "pending" ? "Loading rows…"
    : lane === "error" ? "Rows unavailable - the request failed"
      : lane === "unasked" ? "Rows not requested yet"
        : "";
  const start = pageWindow.totalRows ? pageWindow.startIndex + 1 : 0;
  const rangeText = `Rows ${formatCount(start)}-${formatCount(pageWindow.endIndex)} of ${formatCount(pageWindow.totalRows)}`;
  const pageText = `Page ${formatCount(pageWindow.page)} of ${formatCount(pageWindow.totalPages)}`;
  els.trackerPageText.textContent = unmeasured || `${rangeText} · ${pageText}`;
  if (els.trackerPageSizeSelect) els.trackerPageSizeSelect.value = String(pageWindow.pageSize);
  if (els.trackerPageNumberInput) {
    els.trackerPageNumberInput.value = String(pageWindow.page);
    els.trackerPageNumberInput.max = String(pageWindow.totalPages);
    els.trackerPageNumberInput.disabled = navigationDisabled || pageWindow.totalPages <= 1;
  }
  els.prevTrackerPageBtn.disabled = navigationDisabled || pageWindow.page <= 1;
  els.nextTrackerPageBtn.disabled = navigationDisabled || pageWindow.page >= pageWindow.totalPages;
}

function setTrackerPage(page) {
  state.trackerPage = Math.max(1, positiveNumber(page, 1));
  saveState();
  renderClaimsTable({ deferPagePanels: trackerApiEnabled() });
}

function abortPendingTrackerApiPageRequest() {
  if (trackerApiPageState.status !== "pending" || !trackerApiPageState.controller) return;
  try {
    trackerApiPageState.controller.abort();
  } catch (_) {}
}

function setTrackerPageSize(pageSize) {
  state.trackerPageSize = trackerApiEnabled() ? trackerApiPageSizeValue(pageSize) : trackerPageSizeValue(pageSize);
  state.trackerPage = 1;
  abortPendingTrackerApiPageRequest();
  resetTrackerApiAutoRetry();
  trackerApiPageState = trackerApiInitialState();
  saveState();
  renderClaimsTable();
}

function resetTrackerPage() {
  state.trackerPage = 1;
  state.trackerPageSize = trackerApiEnabled() ? trackerApiPageSizeValue(state.trackerPageSize) : trackerPageSizeValue(state.trackerPageSize);
  abortPendingTrackerApiPageRequest();
  resetTrackerApiAutoRetry();
  trackerApiPageState = trackerApiInitialState();
}

function syncGridScroll(source, target) {
  if (syncingGridScroll || !source || !target) return;
  syncingGridScroll = true;
  target.scrollLeft = source.scrollLeft;
  window.requestAnimationFrame(() => {
    syncingGridScroll = false;
  });
}

function installWideTableScrollRails() {
  if (typeof document === "undefined") return;
  const selectors = [
    ".table-wrap",
    ".setup-table-wrap",
    ".contract-variance-table-wrap",
    ".queue-drilldown-table-wrap",
    ".chart-data-table-wrap",
    ".client-pivot-table-wrap",
    ".client-pivot-scroll",
    ".client-drilldown-table-wrap",
    ".client-monthly-watchlist-table-wrap",
    ".fee-schedule-table-wrap"
  ].join(",");
  document.querySelectorAll(selectors).forEach((wrapper) => {
    if (!wrapper || wrapper.id === "trackerTableWrap") return;
    if (wrapper.scrollWidth <= wrapper.clientWidth + 8) return;
    let rail = wrapper.previousElementSibling;
    if (!rail || !rail.classList?.contains("wide-table-scroll-rail") || rail.dataset.scrollFor !== wideScrollRailId(wrapper)) {
      rail = document.createElement("div");
      rail.className = "wide-table-scroll-rail";
      rail.dataset.scrollFor = wideScrollRailId(wrapper);
      rail.setAttribute("aria-label", "Horizontal table scroll");
      rail.innerHTML = `<div></div>`;
      wrapper.parentNode?.insertBefore(rail, wrapper);
      rail.addEventListener("scroll", () => syncWideTableScroll(rail, wrapper));
      wrapper.addEventListener("scroll", () => syncWideTableScroll(wrapper, rail));
    }
    const inner = rail.firstElementChild;
    if (inner) inner.style.width = `${wrapper.scrollWidth}px`;
    rail.scrollLeft = wrapper.scrollLeft;
  });
}

let syncingWideTableScroll = false;

function syncWideTableScroll(source, target) {
  if (syncingWideTableScroll || !source || !target) return;
  syncingWideTableScroll = true;
  target.scrollLeft = source.scrollLeft;
  window.requestAnimationFrame(() => {
    syncingWideTableScroll = false;
  });
}

function wideScrollRailId(wrapper) {
  if (wrapper.id) return wrapper.id;
  if (!wrapper.dataset.wideScrollId) wrapper.dataset.wideScrollId = `wide-scroll-${Math.random().toString(36).slice(2)}`;
  return wrapper.dataset.wideScrollId;
}

// Describe every source that narrows the grid: slicers, column filters, and
// tracker filters. A queue, aging bucket, or minimum balance must never leave
// the toolbar claiming that no filters are active.
function renderActiveSlicerText() {
  if (!els.activeSlicerText) return;
  // THE BADGE USED TO COUNT SLICERS ONLY. A claim-ID search, an owner dropdown
  // or a queue card left it reading "0 active" above a grid showing one row of a
  // 234,806-row book. It is written below instead, from the same list of terms
  // this line goes on to state, so a filter can never be active and uncounted.
  const countLabel = typeof document !== "undefined" ? document.getElementById("slicerAppliedCount") : null;
  // Every active slicer is applied by the server before pagination in API mode;
  // the summary therefore describes the whole filtered population, not one page.
  const summarySlicerModes = slicerModeState();
  const summaryColumnModes = columnFilterModeState();
  const active = Object.entries(ensureSlicerState(state.slicers))
    .filter(([, values]) => values.length)
    .map(([key, values]) => filterModeExcludes(summarySlicerModes, key)
      ? `${slicerLabel(key)}: ${filterSelectionSummaryText(key, values, true)}`
      : `${slicerLabel(key)}: ${activeSlicerSummaryValues(key, values).map((value) => slicerDisplayValue(key, value)).join(", ")}`
    );
  Object.entries(ensureColumnFilterState(state.columnFilters))
    .filter(([, values]) => Array.isArray(values) && values.length)
    .forEach(([key, values]) => active.push(filterModeExcludes(summaryColumnModes, key)
      ? `${columnLabel(key)}: ${filterSelectionSummaryText(key, values, true)}`
      : `${columnLabel(key)}: ${values.map((value) => key === "action" ? slicerDisplayValue(key, value) : value).join(", ")}`));
  // A comparison is counted and named exactly as a checklist is: the badge is
  // written from this list, so a filter that narrows the grid without
  // appearing here would leave the toolbar reading "0 active" over a narrowed
  // book - the defect the badge was rewritten to close.
  Object.entries(columnFilterRangeState()).forEach(([key, range]) => active.push(columnFilterRangeChipText(key, range)));
  // Quick filters share the same full-scope server contract as slicers and
  // column facets. Keep the partition helper as a fail-closed invariant, but
  // never present a partial-page filtering mode in the UI.
  const quickFilters = partitionTrackerFilterDescriptions(trackerFilterTermsExcludingScope(collectTrackerFilters()));
  console.assert(!quickFilters.unapplied.length, "Every active tracker filter must be server-applied before pagination.");
  quickFilters.applied.forEach((description) => active.push(description));
  // A tracker query can take ~20s. For most of that the line was describing the
  // request that produced the rows currently on screen, not the one the user
  // just typed - the owner watched it read Claim ID="8523" while the input held
  // 1968 and reasonably concluded the filter was broken. The terms are read live
  // from state and the DOM, so the values were never stale; the REPAINT was.
  // renderClaimsTable() now repaints this line, so it states the in-flight terms,
  // and marks itself while the answer for them is still outstanding.
  if (countLabel) countLabel.textContent = `${active.length} active`;
  // COMPANY SCOPE IS STATED, BUT NOT COUNTED AS A FILTER.
  //
  // Both halves of that are contracts. It genuinely narrows what is on screen,
  // so it has to be NAMED - verify_no_scope_says_no_scope pins exactly this:
  // "picking a client in the header narrows what is on screen; it can never
  // widen what the server serves", and "All clients is the absence of a filter,
  // not a filter named All". But it is not a FILTER: it has its own permanent
  // switcher, "Clear all filters" must never widen a reader to companies they
  // did not ask for, and counting it would leave this line reading "1 active"
  // one click after a clear - the exact thing the owner reported on 2026-09-17.
  // So it gets its own clause, ahead of the count and outside it.
  const scopeTerm = trackerFilterScopeTerm();
  const filterSentence = active.length ? active.join(" | ") : "No filters — showing all rows";
  // "showing all rows" is false under a company scope - it is showing every row
  // FOR THAT COMPANY, which was 4 of 10 on the review's own screenshot.
  const summary = scopeTerm
    ? `${scopeTerm} · ${active.length ? filterSentence : "No other filters — showing every row for this company"}`
    : filterSentence;
  const awaitingServer = trackerApiEnabled() && trackerApiLaneState() === "pending";
  els.activeSlicerText.textContent = awaitingServer ? `${summary} — applying…` : summary;
  // A company scope is not nothing, so the line is not styled as the empty state.
  els.activeSlicerText.classList?.toggle("is-empty", !active.length && !scopeTerm);
}

// One entry per BAND in the active-filter summary: an aging selection legitimately holds
// two labels for one band while the mirror and warehouse vocabularies coexist, and
// "Aging: 60-89, 61-90" reads like a filter bug. The stored (served) label wins.
function activeSlicerSummaryValues(key, values) {
  const list = Array.isArray(values) ? values : [];
  if (key !== "aging") return list;
  const seen = new Set();
  return list.filter((value) => {
    const band = normalizeAgingBucketLabel(value) || String(value);
    if (seen.has(band)) return false;
    seen.add(band);
    return true;
  });
}

function sortedSlicerValues(claims, definition) {
  const values = Array.from(new Set(claims.map((claim) => claimSlicerValue(claim, definition)).filter(Boolean)));
  return sortSlicerValues(values, definition);
}

function sortedSlicerValuesFromCounts(counts, definition, selectedValues = state?.slicers?.[definition.key] || []) {
  const values = new Set(Array.from(counts.keys()).filter(Boolean));
  if (Array.isArray(definition.requiredValues)) {
    definition.requiredValues.forEach((value) => values.add(canonicalSlicerValue(definition.key, value)));
  }
  // An active selection must stay visible (and clearable) even when the
  // current cross-filter leaves it with zero matching rows.
  (selectedValues || []).forEach((value) => {
    const canonical = canonicalSlicerValue(definition.key, value);
    // ...but an aging band selected under BOTH vocabularies (migrateAgingSlicerValues) is
    // still ONE band. Render the label this feed actually serves — the twin would sit
    // beside it at a permanent count of 0 and read as a broken filter.
    if (definition.key === "aging" && agingSlicerBandValues(values, canonical).length) return;
    values.add(canonical);
  });
  return sortSlicerValues(Array.from(values), definition);
}

function sortSlicerValues(values, definition) {
  if (definition.order === "actions") {
    return values.sort((a, b) => (a === "Blank" ? -1 : b === "Blank" ? 1 : compareFollowUpActionChoices(a, b)));
  }
  if (Array.isArray(definition.order)) {
    const ranked = new Map(definition.order.map((value, index) => [value, index]));
    return values.sort((a, b) => (ranked.get(a) ?? 999) - (ranked.get(b) ?? 999) || a.localeCompare(b));
  }
  if (definition.order === "alpha-desc") {
    return values.sort((a, b) => String(b).localeCompare(String(a)));
  }
  return values.sort((a, b) => String(a).localeCompare(String(b)));
}

function claimSlicerValue(claim, definition) {
  if (definition.key === "claimDue") return isPastDue(claim) ? "PAST DUE" : "Not Due";
  if (definition.key === "followUpDue") return followUpDueStatus(claim);
  if (definition.key === "primaryPayerClass") return payerClassFor(claim.primaryPayer);
  if (definition.key === "secondaryPayerClass") return payerClassFor(claim.secondaryPayer);
  if (definition.key === "patClassification") return canonicalSlicerValue(definition.key, firstPresent(claim.patClassification, claim.patientClassification) || "Blank");
  if (definition.key === "secondaryCalc") return canonicalSlicerValue(definition.key, firstPresent(claim.secondaryCalc, claim.Secondary_Calc) || "Blank");
  if (definition.key === "claimClassification") return canonicalSlicerValue(definition.key, firstPresent(claim.claimClassification, claim.claClassification) || "Blank");
  if (definition.serviceField) return canonicalSlicerValue(definition.key, firstServiceSlicerValue(claim, definition) || "Blank");
  const value = claim[definition.field];
  return canonicalSlicerValue(definition.key, value || "Blank");
}

// Per-service-line slicers (Procedure / Service Month) read every service
// line's value; the demo/local grid path keeps a claim when any line matches.
function serviceSlicerValues(claim, definition) {
  const services = Array.isArray(claim && claim.services) ? claim.services : [];
  // Service Month is derived per line from the DOS (serviceMonth(dos) ->
  // "yyyy-MM") so the local/demo filter path agrees with the server's computed
  // month facet; a claim is kept when any line falls in a selected month.
  if (definition.key === "serviceMonth") {
    return services.map((service) => serviceMonth(service && service.dos)).filter((value) => value !== "");
  }
  // In/Out of Network is BUCKETED, not read raw: the stored value is the fee
  // schedule's hand-typed contract text ("IN" / "In Network" / "OON" / "OUT"), and
  // a rail offering four chips for two ideas would let a biller pick one and
  // silently exclude the other's rows. Every line resolves to one of three
  // buckets, so this list is never empty and needs no claim-level fallback.
  if (definition.key === "network") {
    return services.map((service) => networkBucket(firstPresent(
      service && service.contractNetwork, claim && claim.contractNetwork,
      service && service.network, claim && claim.network
    )));
  }
  const field = definition.serviceField;
  const values = services.map((service) => (service && service[field] != null ? service[field] : "")).filter((value) => value !== "");
  if (!values.length && claim && claim[definition.field]) values.push(claim[definition.field]);
  return values;
}

function firstServiceSlicerValue(claim, definition) {
  const values = serviceSlicerValues(claim, definition);
  return values.length ? values[0] : "";
}

function toggleSlicer(key, value) {
  state.slicers = ensureSlicerState(state.slicers);
  if (!state.slicers[key]) return;
  const selected = new Set(state.slicers[key]);
  if (selected.has(value)) {
    selected.delete(value);
    // An aging band can be selected under TWO labels at once while the mirror and the
    // warehouse disagree on the vocabulary (migrateAgingSlicerValues). Dropping only the
    // clicked label would leave its twin selected with no chip to clear it — an invisible
    // filter that matches nothing on this feed. Deselecting clears the whole BAND.
    if (key === "aging") agingSlicerBandValues(selected, value).forEach((twin) => selected.delete(twin));
  } else {
    selected.add(value);
  }
  state.slicers[key] = Array.from(selected);
  resetTrackerPage();
  saveState();
  renderSlicers();
  renderClaimsTable();
}

function setQueueFilter(key) {
  state.queueFilter = queueDefinitions.some((queue) => queue.key === key) ? key : "all";
  // A ?queue= link seeds this on every boot. Once the reader has chosen a queue
  // here - including "all" - the address bar must stop overriding that choice.
  clearTrackerFilterUrlState();
  resetTrackerPage();
  saveState();
  renderSlicers();
  renderClaimsTable();
}

function clearSlicer(key) {
  state.slicers = ensureSlicerState(state.slicers);
  if (!state.slicers[key]) return;
  state.slicers[key] = [];
  state.slicerModes = ensureFilterModeState(state.slicerModes, state.slicers);
  resetTrackerPage();
  saveState();
  renderSlicers();
  renderClaimsTable();
}

// ONE AUTHORITATIVE RESET (2026-09-17). This used to be clearAllSlicers() and
// cleared state.slicers alone, while collectTrackerFilters() went on reading the
// claim-ID boxes and thirteen controls above the grid straight out of the DOM -
// so "Clear" left the grid narrowed and said nothing. Everything the request can
// carry is listed in TRACKER_QUICK_FILTER_RESETS and cleared from there.
//
// Sort goes too. It is not a narrowing, but it rides in the same request, it is
// part of the API cache key, and it appears in the chip rail beside the filters -
// leaving it made the rail read "Filters (1)" one click after a clear, which is
// exactly the "it's still using the filters" the owner reported.
//
// Company scope, row mode, the column layout preset and the page size are NOT
// touched: none of them narrows the book, and clearing the company would widen
// the reader to companies they never asked for.
function clearAllTrackerFilters() {
  discardSlicerDraft({ close: true, returnFocus: false });
  clearOperationalFilters();
  // Clear all means every filter, including the ones turned off in the panel.
  state.disabledFilters = {};
  state.tableSort = { column: null, dir: null };
  clearTrackerFilterUrlState();
  resetTrackerPage();
  saveState();
  renderSlicers();
  renderClaimsTable();
  showToast("All filters cleared.");
}

function renderWorkbenchPresetBar() {
  const host = document.getElementById("workbenchPresetBar");
  if (!host) return;
  host.hidden = false;
  const activePreset = workbenchPresets[state.workbenchPreset] ? state.workbenchPreset : "arScan";
  const mode = state.rowMode || "service";
  const totalForMode = (mode === "service" ? serviceColumns : claimColumns).length;
  const visibleCount = currentColumns(mode).length;
  const buttons = workbenchPresetOrder.map((key) => {
    const preset = workbenchPresets[key];
    const isActive = key === activePreset;
    return `<button type="button" class="preset-chip ${isActive ? "is-active" : ""}" data-workbench-preset="${key}" aria-pressed="${isActive}" title="${escapeHtml(preset.hint)}"><span>${escapeHtml(preset.label)}</span><small>${escapeHtml(preset.hint)}</small></button>`;
  }).join("");
  // Hide the column counter when every column is shown — it's only useful when
  // a preset has narrowed the layout.
  const colCounterHidden = visibleCount >= totalForMode;
  host.innerHTML = `
    <div class="preset-bar-left">
      <div class="preset-chip-row" role="group" aria-label="Column layout presets">${buttons}</div>
    </div>
    <div class="preset-bar-right">
      <span class="preset-column-count ${colCounterHidden ? "is-hidden" : ""}" aria-live="polite">${Number(visibleCount || 0).toLocaleString()} / ${formatNounCount(totalForMode, "column")}</span>
    </div>
  `;
  host.querySelectorAll("[data-workbench-preset]").forEach((btn) => {
    btn.addEventListener("click", () => setWorkbenchPreset(btn.dataset.workbenchPreset));
  });
}

function setWorkbenchPreset(key) {
  if (!workbenchPresets[key]) return;
  if (state.workbenchPreset === key) return;
  state.workbenchPreset = key;
  saveState();
  renderClaimsTable();
  showToast(`Layout: ${workbenchPresets[key].label}`);
}

function applyWorkbenchDensity() {
  const presetKey = workbenchPresets[state.workbenchPreset] ? state.workbenchPreset : "arScan";
  // SHEET MODE IS THE ONLY MODE (2026-08-17). Nobody used anything else, so the
  // toggle and its "Exit Sheet Mode" label are gone and this is simply always on.
  // state.workbenchSheetMode is still written so persisted state stays readable.
  const sheetMode = true;
  state.workbenchSheetMode = true;
  const targets = [
    document.getElementById("workbenchView"),
    document.getElementById("trackerTableWrap")
  ];
  // COMPACT IS THE ONLY DENSITY (2026-08-17). Comfortable went unused, so the
  // toggle is gone; state.rowDensity is kept pinned for persisted-state compatibility.
  const density = "compact";
  targets.forEach((el) => {
    if (!el) return;
    el.classList.toggle("density-compact", true);
    el.classList.toggle("preset-full-sheet", presetKey === "full");
    el.classList.toggle("preset-compact", presetKey !== "full");
    el.classList.toggle("sheet-mode", sheetMode);
  });
  if (document.body && document.body.classList) {
    document.body.classList.toggle("workbench-sheet-mode", sheetMode);
  }
}

function resetWorkbenchToAllData() {
  discardSlicerDraft({ close: true, returnFocus: false });
  clearOperationalFilters();
  state.disabledFilters = {};
  state.queueFilter = "all";
  state.workbenchPreset = "full";
  state.columnViewSelections = defaultColumnViewSelections();
  state.activeColumnViewIds = defaultActiveColumnViewIds();
  state.workbenchSheetMode = true;
  state.trackerPage = { service: 1, claim: 1 };
  resetTrackerPage();
  saveState();
  renderSlicers();
  renderClaimsTable();
  showToast("View reset — Accounts Receivable");
}

function tagWorkbenchCells(mode) {
  const tbody = els.claimRows;
  if (!tbody || !tbody.children) return;
  const columns = currentColumns(mode);
  const canSelect = canSelectWorkbenchRows();
  const leadingOffset = canSelect ? 1 : 0;
  Array.from(tbody.children || []).forEach((tr) => {
    Array.from(tr.children).forEach((td, idx) => {
      const colIdx = idx - leadingOffset;
      const column = columns[colIdx];
      if (!column) return;
      td.dataset.col = column.key;
      if (currencyColumns.has(column.key)) td.classList.add("cell-currency");
      if (numericColumns.has(column.key)) td.classList.add("cell-numeric");
      if (column.key === "aging") {
        td.classList.add("cell-aging");
        if (/120/.test(td.textContent || "")) td.classList.add("cell-aging-critical");
        else if (/91/.test(td.textContent || "")) td.classList.add("cell-aging-high");
        else if (/61/.test(td.textContent || "")) td.classList.add("cell-aging-warn");
      }
    });
  });
}

function applyBillerFocus(focusKey) {
  clearOperationalFilters();
  const focusMap = {
    dueNow: { queue: "all", slicers: { claimDue: ["PAST DUE"] }, label: "due-now follow-ups" },
    denials: { queue: "denied", label: "denial queue" },
    payer: { queue: "payerFollowUp", label: "payer follow-up queue" },
    secondary: { queue: "waitingSecondary", label: "waiting-secondary queue" },
    highBalance: { queue: "highDollar", minBalance: "1000", label: "high-balance AR" },
    untouched: { queue: "stale", label: "untouched AR" },
    client: { queue: "clientAction", label: "client-action queue" }
  };
  const focus = focusMap[focusKey] || focusMap.payer;
  state.queueFilter = focus.queue;
  state.slicers = {
    ...ensureSlicerState(state.slicers),
    ...(focus.slicers || {})
  };
  if (els.minBalanceFilter && focus.minBalance) els.minBalanceFilter.value = focus.minBalance;
  resetTrackerPage();
  saveState();
  renderSlicers();
  // Sheet mode deliberately skips the hidden legacy workstrip renderer. Keep
  // the shortcut's own pressed state truthful at the moment it changes the
  // filters instead of relying on that hidden panel to rerender later.
  renderBillerFocusActions(getClaims());
  renderClaimsTable();
  showToast(`Focused ${focus.label}.`);
}

// A FILTER CLEAR IS ALSO A SELECTION CLEAR. Only state.selectedClaims used to be
// dropped, but the row ticks render from state.selectedServiceTargets - so a row
// stayed ticked through a clear AND through a reload while
// bulkFollowUpLineTargets() answered zero: a tick that means nothing, over a
// bulk bar that would act on nothing. All three maps go together, as they do
// after a successful bulk write.
function clearWorkbenchSelectionState() {
  state.selectedClaims = [];
  state.selectedClaimTargets = {};
  state.selectedServiceTargets = {};
}

// Hand-listing the controls here is what let the queue card - and, before it,
// whatever the next filter turns out to be - slip through a "clear". The list
// now comes from TRACKER_QUICK_FILTER_RESETS, which is keyed off the request
// body itself. Callers that want a queue afterwards (applyBillerFocus) still set
// one immediately after, exactly as before.
//
// preserveTenant is GONE (2026-09-17, review). It only ever guarded the
// "Company scope" SELECT, which is a filter and is now cleared by the table; the
// header switcher is not a DOM control here and survives by construction. Reset
// View and Clear all filters therefore treat company identically.
function clearOperationalFilters() {
  Object.values(TRACKER_QUICK_FILTER_RESETS).forEach((entry) => entry.clear());
  state.slicers = structuredClone(emptySlicers);
  state.columnFilters = structuredClone(emptyColumnFilters);
  state.slicerModes = structuredClone(emptyFilterModes);
  state.columnFilterModes = structuredClone(emptyFilterModes);
  // A comparison narrows the grid exactly as a checklist does, so "Clear all
  // filters" has to take it too - otherwise the badge reads 0 active over a
  // grid that is still showing a third of the book.
  state.columnFilterRanges = structuredClone(emptyColumnFilterRanges);
  clearWorkbenchSelectionState();
}

function selectVisibleWorkClaims(mode = "page") {
  if (!hasPermission(PERMISSIONS.ASSIGN_CLAIMS) && !hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) {
    return denyAction(PERMISSIONS.UPDATE_FOLLOW_UP, "select work claims");
  }
  const rowMode = state.rowMode || "service";
  const query = buildTrackerQueryRequest(rowMode);
  const apiPage = trackerApiPageForQuery(query);
  const filtered = apiPage ? apiPage.rows : (rowMode === "service" ? getFilteredServiceRows(query) : getFilteredClaims(query));
  const pageWindow = apiPage ? apiPage.pageWindow : trackerPageWindow(filtered, query.page);
  const candidates = mode === "top10"
    ? claimsFromTrackerRows(filtered, rowMode).slice(0, 10)
    : visibleClaimsFromPage(pageWindow, rowMode);
  const claimIds = candidates.map((claim) => claim.claimId).filter(Boolean);
  state.selectedClaims = claimIds;
  saveState();
  renderClaimsTable();
  showToast(claimIds.length ? `Selected ${formatNounCount(claimIds.length, "work item")}.` : "No visible work items to select.");
}

// The Clear Selection button had the same ghost-tick defect as the filter clear:
// it dropped state.selectedClaims only, while the row ticks render from
// state.selectedServiceTargets. All three maps go together now.
function clearWorkbenchSelection() {
  clearWorkbenchSelectionState();
  saveState();
  renderClaimsTable();
  showToast("Selection cleared.");
}

function setBulkActionDatePreset(days) {
  if (!els.bulkActionDate) return;
  if (!Number.isFinite(days)) {
    showToast("Bulk follow-up date preset is invalid.");
    return;
  }
  els.bulkActionDate.value = addDays(todayDateString(), days);
  showToast(`Bulk follow-up date set to ${formatDate(els.bulkActionDate.value)}.`);
}

function setDisabled(element, disabled) {
  if (element) element.disabled = Boolean(disabled);
}

function setText(element, text) {
  if (element) element.textContent = text;
}

function getValue(element, fallback = "") {
  return element ? element.value : fallback;
}

function setValue(element, value) {
  if (element) element.value = value;
}

function getFilteredClaims(query = buildTrackerQueryRequest("claim")) {
  return getBaseFilteredClaims(query)
    .filter((claim) => matchesColumnFilters(claim, null, "claim"))
    .sort(sortClaims);
}

function getFilteredServiceRows(query = buildTrackerQueryRequest("service")) {
  const serviceIdSearch = String(query.filters?.serviceIdSearch || "").trim().toLowerCase();
  return getBaseFilteredClaims(query)
    .sort(sortClaims)
    .flatMap((claim) => claim.services.map((service) => ({ claim, service })))
    .filter((row) => trackerServiceDateMatches(row.service, query.filters || {}))
    .filter((row) => !serviceIdSearch || String(row.service?.serviceId || "").toLowerCase() === serviceIdSearch)
    .filter((row) => matchesSlicers({ ...row.claim, services: [row.service] }, ensureSlicerState(query.slicers || state.slicers)))
    .filter((row) => matchesColumnFilters(row.claim, row.service, "service"))
    .sort((a, b) => {
      const sort = state.tableSort;
      if (!sort?.column) return 0;
      const av = getClaimSortValue(a.claim, a.service, sort.column);
      const bv = getClaimSortValue(b.claim, b.service, sort.column);
      const compared = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      return sort.dir === "desc" ? -compared : compared;
    });
}

function renderTrackerApiRows(rows, mode = "service") {
  const columns = currentColumns(mode);
  const hasSelection = canSelectWorkbenchRows();
  const stickyPlan = workbenchStickyColumnPlan(columns, { hasSelection });
  const colspan = columns.length + (hasSelection ? 1 : 0) + 1;
  if (!rows.length) return emptyClaimsRowHtml(mode);
  return rows.map((row) => {
    const claimId = trackerApiClaimId(row);
    const orgId = String(row.orgId || row.org_id || "").trim();
    const serviceId = String(row.serviceId || row.service_id || "").trim();
    const rowSelected = mode === "service" && serviceId
      ? serviceTargetSelected(orgId, claimId, serviceId)
      : state.selectedClaims.includes(claimId);
    return `
    <tr data-row-claim-id="${escapeHtml(claimId)}" data-row-org-id="${escapeHtml(orgId)}" data-row-service-id="${escapeHtml(serviceId)}">
      ${hasSelection ? `<td${stickyCellAttrs(stickyPlan, STICKY_SELECT_KEY)}>${claimId ? `<input class="claim-select" type="checkbox" data-claim-id="${escapeHtml(claimId)}" data-claim-org-id="${escapeHtml(orgId)}" data-service-id="${escapeHtml(serviceId)}" ${rowSelected ? "checked" : ""} aria-label="Select ${escapeHtml(claimId)}${serviceId ? ` service ${escapeHtml(serviceId)}` : ""}">` : ""}</td>` : ""}
      ${columns.map((column) => trackerApiCellHtml(row, column, stickyPlan)).join("")}
      <td><div class="row-actions">${claimId && hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) && !isRedactedClientRole() ? `<button class="small-button" data-open="${escapeHtml(claimId)}" data-org-id="${escapeHtml(orgId)}" data-service-id="${escapeHtml(serviceId)}" type="button">Open</button>` : ""}</div></td>
    </tr>
  `;
  }).join("");
}

// Loading / error placeholder for the claims grid while an API-backed page
// request is in flight (or has failed). API mode keeps no usable local
// fallback, so without this the grid would render an empty body and look like
// the filter "broke" — see renderClaimsTable.
function trackerApiPendingRowsHtml(mode = "service") {
  const columns = currentColumns(mode);
  const colspan = columns.length + (canSelectWorkbenchRows() ? 1 : 0) + 1;
  const status = (typeof trackerApiPageState !== "undefined" && trackerApiPageState.status) || "pending";
  // Above the error branch: a 403 with a typed reason is not a failed request,
  // and the table body is where there is room for the whole sentence.
  const denial = typeof portalScopeDenial === "function" ? portalScopeDenial() : null;
  if (denial) {
    return `<tr class="claims-empty-row"><td colspan="${colspan}">${scopeDeniedPanelHtml(denial)}</td></tr>`;
  }
  if (status === "error") {
    // The raw backend/exception string stays in the console (see
    // requestTrackerApiPage's console.warn). One calm sentence here, pinned by
    // verify_portal.js -- an error cell that pastes a stack trace at a biller is
    // a different kind of failing loudly, and not the useful kind.
    return `<tr class="claims-empty-row"><td colspan="${colspan}"><div class="claims-empty-state" role="alert"><div class="claims-empty-glyph" aria-hidden="true">⚠</div><div class="claims-empty-title">Couldn't load filtered results. Check your connection, or clear filters to reload.</div><div class="claims-empty-hint">This is a connection problem, not an empty worklist.</div><button class="small-button" type="button" data-tracker-api-retry>Retry</button></div></td></tr>`;
  }
  return `
    <tr class="claims-empty-row claims-loading-row">
      <td colspan="${colspan}">
        <div class="claims-empty-state" role="status" aria-live="polite">
          <div class="claims-empty-glyph" aria-hidden="true">⏳</div>
          <div class="claims-empty-title">Loading filtered results…</div>
          <div class="claims-empty-hint">Fetching the latest rows from the tracker.</div>
        </div>
      </td>
    </tr>`;
}

function latestFollowUpWorkflowCommand(claimId, serviceId = "", orgId = "") {
  const targetClaim = String(claimId || "");
  const targetService = String(serviceId || "").trim();
  const targetOrg = String(orgId || "").trim();
  if (!targetClaim || !targetService) return null;
  return [...(state.workflowCommandQueue || [])]
    .reverse()
    .find((command) => command?.command === "follow-up"
      && String(command.body?.claimId || "") === targetClaim
      && String(command.body?.serviceId || "").trim() === targetService
      && (!targetOrg || String(command.body?.orgId || "").trim() === targetOrg)) || null;
}

function followUpSyncStatus(claimId, serviceId = "", orgId = "") {
  const command = latestFollowUpWorkflowCommand(claimId, serviceId, orgId);
  if (!command) return null;
  if (command.status === "synced") {
    const deliveredMs = Number(command.deliveredAtMs || 0) || Date.parse(command.deliveredAt || "") || Number(command.queuedAtMs || 0);
    if (Number.isFinite(deliveredMs) && deliveredMs > 0 && Date.now() - deliveredMs > 12000) return null;
    return { tone: "synced", label: "Synced" };
  }
  if (command.status === "pending") return { tone: "pending", label: "Pending sync" };
  if (command.status === "failed") return { tone: "failed", label: "Not synced - retrying" };
  if (command.status === "local_fallback") return { tone: "failed", label: "Not synced - offline" };
  if (command.status === "rejected") return { tone: "rejected", label: "Rejected - not saved" };
  return null;
}

function followUpSyncStatusHtml(claimId, serviceId = "", orgId = "") {
  const status = followUpSyncStatus(claimId, serviceId, orgId);
  if (!status) return "";
  return `<span class="followup-sync followup-sync--${escapeHtml(status.tone)}">${escapeHtml(status.label)}</span>`;
}

// The row's money certainty is a property of the ROW, and it was being
// recomputed inside EVERY ONE of its 48 cells. Derived once per row for the
// duration of a row build and then discarded with the memo, so a later render
// of the same row object re-reads its Match and guards exactly as before.
function trackerApiRowMoneyCertainty(row) {
  const cache = row && typeof row === "object" && followUpPolicyBuildMemo
    ? followUpPolicyBuildMemo.rowMoneyCertainty
    : null;
  if (cache && cache.has(row)) return cache.get(row);
  const certainty = trackerRowMoneyCertainty(trackerApiCellRawValue(row, "match"),
    row && (row.serviceId || row.service_id) ? row.calculationGuards || row.calculationGuardFlags || row.calculation_guard_flags : "");
  if (cache) cache.set(row, certainty);
  return certainty;
}

// ===========================================================================
// THE MERGED "NOTES" COLUMN (owner, Nathan, 2026-09-23): "one column is enough".
// ===========================================================================
//
// Latest Claim Note (the portal ledger's newest note for the line, with author
// and time) and Service Line Notes (the EZClaim source note) very often carry
// the same words, because the legacy Service Line Notes were imported into the
// ledger. One cell now shows the newer of the two and says where it came from:
//
//   EZClaim                         the EZClaim source note
//   Portal · <author> · <date>      a note written in the portal
//   Tracker import · <date>         a legacy note imported into the ledger
//   EZClaim · also in portal notes  both carry the same text (case and runs of
//                                   whitespace ignored) - shown ONCE
//
// "NEWER" NEEDS A DATE ON BOTH SIDES, and the EZClaim field has no timestamp.
// What it does have is the dated header billers write at the front of an entry
// ("09/12/26 NS - called payer"), which is the same header the legacy importer
// reads to date the ledger copies (legacyServiceLineNoteImporter). So the
// EZClaim note is dated by its newest entry header, read by the same rule the
// importer uses, and compared by calendar day with the portal note:
//   - the later day wins;
//   - a tie goes to the portal note (it carries a time and an author);
//   - an EZClaim note with no readable header date loses to a dated portal note
//     - a note whose date is known is not assumed older than one whose date is
//     not. Its text is still one hover or one click away.
// The key used for "same text" is the one the server groups and filters on
// (backend/mergedNotesColumn.normalizeMergedNoteKey), so the cell, the dropdown
// and the filter agree about when two notes are one.
//
// NO REQUEST PER ROW. Both inputs are already on the row: the EZClaim note is a
// warehouse column and the portal note is the page's one batched timeline read.
const MERGED_NOTES_COLUMN_KEY = "mergedNotes";
const MERGED_NOTE_KEY_WIDTH = 4000;
const MERGED_NOTE_BLANK_KEYS = new Set(["", "blank", "n/a"]);
const MERGED_NOTE_COLLAPSE_MARK = "\u0007";

// Step for step backend/mergedNotesColumn.collapseMergedNoteText, which mirrors
// the SQL replace chain. Proved equal by verify_merged_notes_column_contract.js.
function mergedNoteCollapse(value) {
  let text = String(value === null || value === undefined ? "" : value).slice(0, MERGED_NOTE_KEY_WIDTH);
  text = text.split("\r").join(" ").split("\n").join(" ").split("\t").join(" ");
  text = text.split(" ").join(" " + MERGED_NOTE_COLLAPSE_MARK).split(MERGED_NOTE_COLLAPSE_MARK + " ").join("").split(MERGED_NOTE_COLLAPSE_MARK).join("");
  return text.replace(/^ +/, "").replace(/ +$/, "");
}

function mergedNoteKey(value) {
  const key = mergedNoteCollapse(value).toLowerCase();
  return MERGED_NOTE_BLANK_KEYS.has(key) ? "" : key;
}

// The keys one line carries: its EZClaim key, its portal key when different,
// or the single Blank key "" when it has neither. backend mergedNoteLineKeys.
// Portal notes only (2026-10-16): sourceText is accepted and ignored.
function mergedNoteLineKeys({ portalText = "" } = {}) {
  return [mergedNoteKey(portalText)];
}

// The importer's date token and its calendar rule (parseNoteEntryDate): a
// two-digit year above 39 is ambiguous and yields no date rather than a guess.
const MERGED_NOTE_HEADER_DATE = "(\\d{4}-\\d{1,2}-\\d{1,2}|\\d{1,2}[./-]\\d{1,2}[./-]\\d{2,4})";
const MERGED_NOTE_HEADER_TIME = "(?:\\s+\\d{1,2}:\\d{2}(?:\\s*[AaPp][Mm])?)?";
const MERGED_NOTE_DATE_FIRST_HEADER = new RegExp("^\\s*" + MERGED_NOTE_HEADER_DATE + MERGED_NOTE_HEADER_TIME + "(?:\\s*[-:|]\\s*|\\s+)");
const MERGED_NOTE_INITIALS_FIRST_HEADER = new RegExp("^\\s*(?:(?:[A-Za-z]\\.){2,4}|[A-Z]{2,4})(?:\\s*[-|:]\\s*|\\s+)" + MERGED_NOTE_HEADER_DATE + MERGED_NOTE_HEADER_TIME + "(?:\\s*[-:]\\s*|\\s+)");

function mergedNoteHeaderCalendarDate(token) {
  const parts = String(token || "").trim().split(/[./-]/);
  if (parts.length !== 3) return "";
  let year;
  let month;
  let day;
  if (/^\d{4}$/.test(parts[0])) {
    year = Number(parts[0]); month = Number(parts[1]); day = Number(parts[2]);
  } else {
    let yearText = parts[2];
    if (yearText.length === 2) {
      const shortYear = Number(yearText);
      if (!Number.isFinite(shortYear) || shortYear > 39) return "";
      yearText = String(2000 + shortYear);
    } else if (yearText.length !== 4) {
      return "";
    }
    month = Number(parts[0]); day = Number(parts[1]); year = Number(yearText);
  }
  if (![year, month, day].every((part) => Number.isInteger(part))) return "";
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return "";
  return String(year).padStart(4, "0") + "-" + String(month).padStart(2, "0") + "-" + String(day).padStart(2, "0");
}

function mergedNoteHeaderDate(line) {
  const match = MERGED_NOTE_DATE_FIRST_HEADER.exec(line) || MERGED_NOTE_INITIALS_FIRST_HEADER.exec(line);
  return match ? mergedNoteHeaderCalendarDate(match[1]) : "";
}

// The EZClaim note's date: the newest dated entry header, and only when the
// note OPENS with one - the importer splits and dates entries on exactly that
// condition, so a date buried in prose never dates the note.
function mergedNoteSourceDate(text) {
  const lines = String(text === null || text === undefined ? "" : text).split("\n");
  const first = lines.find((line) => line.trim());
  if (!first || !mergedNoteHeaderDate(first)) return "";
  return lines.map(mergedNoteHeaderDate).filter(Boolean).sort().pop() || "";
}

function mergedNotePortalIsImport(source) {
  return /^ezclaim-/i.test(String(source || "").trim());
}

// The portal note's calendar day. A portal note's instant is read in local
// time; an imported note's timestamp is a DATE surrogate (the legacy header
// date at midnight), so its own date part is the day.
function mergedNotePortalDay(at, isImport) {
  const text = String(at || "").trim();
  if (!text) return "";
  if (isImport) return (/^(\d{4}-\d{2}-\d{2})/.exec(text) || [])[1] || "";
  const millis = strictDateTimeMillis(text);
  return Number.isFinite(millis) ? localDateString(new Date(millis)) : "";
}

function mergedNotePortalLabel({ portalAuthor = "", portalAt = "", portalSource = "" } = {}) {
  const isImport = mergedNotePortalIsImport(portalSource);
  const day = mergedNotePortalDay(portalAt, isImport);
  const date = day ? formatDate(day) : "";
  return (isImport ? ["Tracker import", date] : ["Portal", String(portalAuthor || "").trim(), date]).filter(Boolean).join(" · ");
}

// THE MERGE RULE. Pure: every input is a string off the row.
function mergedNoteCellModel({ sourceText = "", portalText = "", portalAuthor = "", portalAt = "", portalSource = "" } = {}) {
  const source = { origin: "ezclaim", key: mergedNoteKey(sourceText), text: String(sourceText || "").trim(), label: "EZClaim" };
  const portal = {
    origin: mergedNotePortalIsImport(portalSource) ? "import" : "portal",
    key: mergedNoteKey(portalText),
    text: String(portalText || "").trim(),
    label: mergedNotePortalLabel({ portalAuthor, portalAt, portalSource })
  };
  if (!source.key && !portal.key) return { kind: "empty", text: "", label: "", entries: [] };
  if (!portal.key) return { kind: "ezclaim", text: source.text, label: source.label, entries: [source] };
  if (!source.key) return { kind: portal.origin, text: portal.text, label: portal.label, entries: [portal] };
  if (source.key === portal.key) {
    return { kind: "same", text: source.text, label: "EZClaim · also in portal notes", entries: [source, portal] };
  }
  const sourceDay = mergedNoteSourceDate(sourceText);
  const portalDay = mergedNotePortalDay(portalAt, portal.origin === "import");
  const sourceNewer = Boolean(sourceDay) && (!portalDay || sourceDay > portalDay);
  const winner = sourceNewer ? source : portal;
  const other = sourceNewer ? portal : source;
  return { kind: sourceNewer ? "ezclaim-newer" : portal.origin + "-newer", text: winner.text, label: winner.label, entries: [winner, other] };
}

function mergedNoteInputsFromApiRow(row = {}) {
  const summary = row.claimNoteSummary && typeof row.claimNoteSummary === "object"
    ? row.claimNoteSummary
    : (row.ClaimNoteSummary && typeof row.ClaimNoteSummary === "object" ? row.ClaimNoteSummary : {});
  // PORTAL NOTES ONLY (owner, 2026-10-16). Notes shows the latest note written
  // in the portal (or imported into its ledger from the old tracker), never the
  // EZClaim service-line note; that one keeps its own "Service Line Notes" column.
  return {
    sourceText: "",
    portalText: cleanApiDisplayValue(trackerApiCellRawValue(row, "serviceLineNotes"), "serviceLineNotes"),
    portalAuthor: String(firstPresent(row.latestClaimNoteAuthor, row.latest_claim_note_author, summary.authorFullName, summary.author) || ""),
    portalAt: String(firstPresent(row.latestClaimNoteAt, row.latest_claim_note_at_utc, summary.createdAtUtc, summary.at) || ""),
    portalSource: String(firstPresent(row.latestClaimNoteSource, summary.source) || "")
  };
}

function mergedNoteInputsFromClaim(claim = {}, service = null) {
  const summary = claim.claimNoteSummary && typeof claim.claimNoteSummary === "object" ? claim.claimNoteSummary : {};
  return {
    sourceText: "",
    portalText: String(firstPresent(claim.latestClaimNoteText, summary.text, summary.latestText, service?.latestClaimNoteText, service?.latestClaimNote, "") || ""),
    portalAuthor: String(firstPresent(claim.latestClaimNoteAuthor, summary.authorFullName, summary.author, "") || ""),
    portalAt: String(firstPresent(claim.latestClaimNoteAt, summary.createdAtUtc, summary.at, "") || ""),
    portalSource: String(firstPresent(claim.latestClaimNoteSource, summary.source, "") || "")
  };
}

// The cell. The note and its source line open the claim drawer (its full note
// history is unchanged). When the two notes DIFFER a native disclosure shows
// both, labelled; when they are the same text there is nothing more to read, so
// the source line says "also in portal notes" and the hover title names both
// sources - the common legacy-import case stays one compact row.
function mergedNoteCellHtml(model = {}, { attrs = "", claimId = "", orgId = "", serviceId = "" } = {}) {
  const entries = Array.isArray(model.entries) ? model.entries : [];
  const title = entries.length > 1
    ? entries.map((entry) => entry.label + ": " + entry.text).join("\n\n")
    : (model.text ? model.label + ": " + model.text : "No notes");
  const body = model.text
    ? '<span class="merged-note__text">' + escapeHtml(model.text) + '</span><small class="merged-note__source" data-merged-note-source>' + escapeHtml(model.label) + '</small>'
    : '<span class="merged-note__text merged-note__text--empty">No notes</span>';
  const opener = claimId && hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES)
    ? '<button class="claim-note-summary-button merged-note__open" type="button" data-open="' + escapeHtml(claimId) + '" data-org-id="' + escapeHtml(orgId) + '" data-service-id="' + escapeHtml(serviceId) + '">' + body + '</button>'
    : '<div class="merged-note__static">' + body + '</div>';
  const both = entries.length > 1 && model.kind !== "same"
    ? '<details class="merged-note__both" data-merged-note-both><summary>Both notes</summary><dl>'
      + entries.map((entry) => '<dt>' + escapeHtml(entry.label) + '</dt><dd>' + escapeHtml(entry.text) + '</dd>').join("")
      + '</dl></details>'
    : "";
  return '<td' + attrs + ' class="merged-note-cell" data-merged-note-kind="' + escapeHtml(model.kind || "empty") + '" title="' + escapeHtml(title) + '">' + opener + both + '</td>';
}

// The local/demo lane's dropdown: one count per (line, distinct key), labelled
// with the most common spelling, Blank last - the note menu's reading order.
function mergedNoteLocalFilterOptions(claims = []) {
  const groups = new Map();
  const add = (inputs) => {
    const sourceKey = mergedNoteKey(inputs.sourceText);
    mergedNoteLineKeys(inputs).forEach((key) => {
      const group = groups.get(key) || { count: 0, spellings: new Map() };
      group.count += 1;
      const spelling = key ? mergedNoteCollapse(key === sourceKey ? inputs.sourceText : inputs.portalText) : NOTE_FILTER_BLANK_VALUE;
      group.spellings.set(spelling, (group.spellings.get(spelling) || 0) + 1);
      groups.set(key, group);
    });
  };
  claims.forEach((claim) => {
    const services = Array.isArray(claim.services) && claim.services.length ? claim.services : [null];
    services.forEach((service) => add(mergedNoteInputsFromClaim(claim, service)));
  });
  const options = Array.from(groups.entries()).map(([key, group]) => ({
    value: key ? Array.from(group.spellings.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0] : NOTE_FILTER_BLANK_VALUE,
    count: group.count,
    blank: !key
  }));
  options.sort((a, b) => (a.blank !== b.blank ? (a.blank ? 1 : -1) : (b.count - a.count || a.value.localeCompare(b.value))));
  const shown = options.map(({ value, count }) => ({ value, count }));
  shown.totalDistinct = shown.length;
  shown.truncated = false;
  return shown;
}

// The Notes menu's foot: what the list covers, and - from the server - the
// state of its portal half, so an unreadable ledger is SAID rather than shown as
// an EZClaim-only list that looks complete.
function mergedNotesMenuFootHtml(record = {}) {
  const notice = record?.response?.facetNotices?.[MERGED_NOTES_COLUMN_KEY] || null;
  const state = notice ? String(notice.portalNotes || "") : "";
  const warning = notice && state && state !== "complete" && notice.message
    ? '<p class="column-filter-truncated merged-note-menu-notice" data-merged-notes-notice="' + escapeHtml(state) + '" role="status">' + escapeHtml(notice.message) + '</p>'
    : "";
  return warning + '<p class="column-filter-truncated">Lists the latest portal note of every line. Ticking a note returns every line whose latest portal note matches. Very long notes are grouped by their first 4,000 characters.</p>';
}

function trackerFollowUpReviewHtml(row, { includeApproved = false } = {}) {
  if (!["SuperAdmin", "StatesideBiller", "OffshoreBiller"].includes(canonicalRole(state.role))) return "";
  const review = row.followUpReview;
  if (!review) return "";
  if (review.status === "approved" && !includeApproved) return "";
  const labels = { pending: "Pending approval", approved: "Update approved", rejected: "Changes requested", canceled: "Request withdrawn", unavailable: "Approval status unavailable — refresh to check" };
  const label = labels[review.status];
  if (!label) return "";
  if (review.status === "unavailable") return '<span class="followup-review followup-review--unavailable" role="status">' + escapeHtml(label) + '</span>';
  const pending = review.status === "pending";
  return '<details class="followup-review followup-review--' + escapeHtml(review.status) + '"><summary>' + escapeHtml(label) + '</summary><div>'
    + '<p><b>' + (pending ? 'Working action' : 'Reviewed action') + ':</b> ' + escapeHtml(review.action || "Remove follow-up action") + '</p>'
    + '<p><b>' + (pending ? 'Working date' : 'Reviewed date') + ':</b> ' + escapeHtml(review.followUpDate || "Cleared") + '</p>'
    + (review.note ? '<p><b>Submitted note:</b> ' + escapeHtml(review.note) + '</p>' : '')
    + (review.submittedAt ? '<p>Submitted ' + escapeHtml(formatDateTime(review.submittedAt)) + '</p>' : '')
    + (review.reason ? '<p><b>Reviewer feedback:</b> ' + escapeHtml(review.reason) + '</p>' : '')
    + (review.reviewedAt ? '<p>Reviewed ' + escapeHtml(formatDateTime(review.reviewedAt)) + '</p>' : '')
    + (pending ? '<p><b>Previous approved action:</b> ' + escapeHtml(review.approvedAction || 'No action') + '</p><p><b>Previous approved date:</b> ' + escapeHtml(review.approvedFollowUpDate || 'No date') + '</p><p>This action is in use while awaiting approval. Rejection restores the approved action.</p>' : '') + '</div></details>';
}

function trackerApiCellHtml(row, column, stickyPlan) {
  const sourceValue = trackerApiCellRawValue(row, column.key);
  const value = column.key === "totalPatBal" && sourceValue != null && sourceValue !== "" ? Math.max(0, moneyNumber(sourceValue)) : sourceValue;
  // Frozen by KEY (workbenchStickyColumnPlan), never by the cell's index - the
  // index-based version pinned whatever the user's column order put in slot 0/1,
  // patient name included.
  const stickyAttrs = stickyCellAttrs(stickyPlan, column.key);
  const followUpStickyAttrs = stickyCellAttrs(stickyPlan, column.key, "followup-cell");
  // The server's finalized Match is this lane's "no rate" signal - the same
  // truth the Match pill shows, now also reaching the money cell it applies to.
  const moneyCertainty = trackerApiRowMoneyCertainty(row);
  if (column.key === "claimId" && value) {
    const knownClaim = getClaim(value);
    return `
      <td${stickyAttrs}>
        ${(knownClaim || trackerApiEnabled()) && hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) && !isRedactedClientRole() ? `<button class="link-button" data-open="${escapeHtml(value)}" data-org-id="${escapeHtml(String(row.orgId || row.org_id || ""))}" data-service-id="${escapeHtml(String(row.serviceId || row.service_id || ""))}">${escapeHtml(value)}</button>` : `<strong>${escapeHtml(value)}</strong>`}
      </td>
    `;
  }
  if (column.key === "claimId") return `<td${stickyAttrs}><span class="missing-value">Missing Claim ID</span></td>`;
  // Inline follow-up editors. The API-backed grid was render-only, so the live
  // site showed Follow up Action / Date as static text while the local fallback
  // grid offered editable controls (serviceCellHtml/claimCellHtml). Mirror those
  // editors here — gated on UPDATE_FOLLOW_UP — so the live line-level grid has
  // the same dropdown/date picker. The existing [data-action-edit]/[data-date-edit]
  // change handlers bind to these automatically, and getClaim() resolves the
  // claim from the cached API page so the permission check + workflow write work.
  const editClaimId = trackerApiClaimId(row);
  // Follow-up is per service line: editable only when this row carries a real
  // serviceId. Claim-grouped/aggregated rows have no serviceId, so they are
  // read-only here (the API rejects an empty serviceId) — set follow-up per line
  // in the Service line row view.
  const editServiceId = String(row.serviceId || row.service_id || "").trim();
  if (column.key === "action" && editClaimId && editServiceId && hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) {
    const current = String(value || "");
    // Compare against the SELECTABLE list: a system-added action (or a legacy/imported
    // variant) is not offered, so it must ride along as its own pinned option or the biller
    // would silently see the wrong value pre-selected.
    const restricted = followUpActionIsRestrictedCurrent(current);
    return `<td${followUpStickyAttrs}>
      <button type="button" class="followup-pill${current ? "" : " followup-pill--empty"}${restricted ? " followup-pill--restricted" : ""}" data-followup-pill aria-label="${restricted ? `Restricted action: ${escapeHtml(current)}. You cannot change or clear it.` : `Follow-up action: ${current ? escapeHtml(current) : "none set"}. Activate to change.`}" title="${escapeHtml(restricted ? "Restricted action — ask a supervisor to change it." : (current || ""))}"${restricted ? " disabled" : ""}><span class="followup-pill__label">${current ? escapeHtml(current) : "Set follow-up"}</span></button>
      <select class="grid-select" tabindex="-1"${restricted ? " disabled aria-label=\"Restricted action; changes are not allowed\"" : ""} data-action-edit="${escapeHtml(editClaimId)}" data-action-org-id="${escapeHtml(String(row.orgId || row.org_id || "").trim())}" data-service-id="${escapeHtml(String(row.serviceId || row.service_id || "").trim())}">
        ${followUpActionOptionsHtml(current)}
      </select>
      ${systemSetFollowUpMarkerHtml(current, state.followUpByService?.[followUpKey(editClaimId, editServiceId)] ? "" : row.followUpUpdatedBy)}
      ${followUpSyncStatusHtml(editClaimId, editServiceId, String(row.orgId || row.org_id || "").trim())}
      ${trackerFollowUpReviewHtml(row)}
    </td>`;
  }
  if (column.key === "action" && editServiceId && row.followUpReview) return `<td${stickyAttrs}>${formatApiCellValue(column.key, value)}${trackerFollowUpReviewHtml(row)}</td>`;
  if (column.key === "action" && editClaimId && hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) {
    // Claim-grouped/aggregated row (no serviceId): read-only summary + hint.
    const current = String(value || "");
    return `<td${stickyAttrs}>
      <select class="grid-select" disabled title="Follow-up is per service line. Switch Rows to &quot;Service line&quot; to set it for each line.">
        <option value="" selected>${current ? escapeHtml(current) : "—"}</option>
      </select>
    </td>`;
  }
  if (column.key === "followUpDate" && editClaimId && editServiceId && hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) {
    const isoDate = normalizeDateValue(value) || "";
    // Single source of truth: reuse the canonical isPastDue() on a claim mapped
    // from this row (status gate via claim.match) with the cell's resolved
    // follow-up date, so the displayed date and the past-due gate never diverge.
    const pastDue = isPastDue({ ...trackerApiRowToClaim(row), followUpDate: isoDate });
    return `<td${stickyAttrs}>
      <input class="grid-date" data-date-edit="${escapeHtml(editClaimId)}" data-date-org-id="${escapeHtml(String(row.orgId || row.org_id || "").trim())}" data-service-id="${escapeHtml(String(row.serviceId || row.service_id || "").trim())}" type="date" value="${escapeHtml(isoDate)}"${followUpActionIsRestrictedCurrent(trackerApiCellRawValue(row, "action")) ? " disabled title=\"Restricted action — the due date cannot be changed.\"" : ""}>
      <span class="subtext">${pastDue ? "Past due" : "Not due"}</span>
    </td>`;
  }
  if (column.key === "followUpDate") {
    // Read-only / Client viewers: mirror the local claimCellHtml read-only cell --
    // formatted date PLUS Past due/Not due subtext, gated by the canonical rule.
    const isoDate = normalizeDateValue(value) || "";
    const pastDue = isPastDue({ ...trackerApiRowToClaim(row), followUpDate: isoDate });
    return `<td${stickyAttrs}>${formatApiCellValue(column.key, value)}<span class="subtext">${pastDue ? "Past due" : "Not due"}</span></td>`;
  }
  if (column.key === "ehrBillingEntryId") {
    // The DOS is read through trackerApiCellRawValue so the window uses the
    // same service date the Srvc Date cell beside it shows, on either lane,
    // and every id in a multi-id cell sits inside that one row's window.
    const entryText = cleanApiDisplayValue(value, column.key);
    const linkedHtml = ehrBillingEntryCellHtml(entryText, trackerApiCellRawValue(row, "dos"));
    if (!linkedHtml) return `<td${stickyAttrs}>${formatApiCellValue(column.key, value)}</td>`;
    return `<td${stickyAttrs}>${linkedHtml}</td>`;
  }
  if (column.key === "assignmentStatus") {
    return `<td${stickyAttrs}>${pill(workflowAssignmentStatusLabel(value, assignmentWorkflowPerspective()), statusTone(value))}</td>`;
  }
  if (column.key === "mergedNotes") {
    const claimId = trackerApiClaimId(row);
    const html = mergedNoteCellHtml(mergedNoteCellModel(mergedNoteInputsFromApiRow(row)), {
      attrs: stickyAttrs,
      claimId,
      orgId: String(row.orgId || row.org_id || ""),
      serviceId: String(row.serviceId || row.service_id || "")
    });
    const review = row.followUpReview;
    if (review?.status === "pending" && review.note && hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) && ["SuperAdmin", "StatesideBiller", "OffshoreBiller"].includes(canonicalRole(state.role))) {
      return html.replace('>No notes</span>', '>No approved notes</span>').replace('</td>', '<div class="followup-review followup-review--pending"><b>Submitted note · awaiting approval</b><p>' + escapeHtml(review.note) + '</p></div></td>');
    }
    return html;
  }
  if (column.key === "sourceServiceLineNotes") {
    const sourceNote = cleanApiDisplayValue(value, column.key);
    return `<td${stickyAttrs} title="${escapeHtml(sourceNote)}">${escapeHtml(sourceNote)}</td>`;
  }
  if (column.key === "serviceLineNotes") {
    const summary = row.claimNoteSummary && typeof row.claimNoteSummary === "object"
      ? row.claimNoteSummary
      : (row.ClaimNoteSummary && typeof row.ClaimNoteSummary === "object" ? row.ClaimNoteSummary : {});
    const author = firstPresent(row.latestClaimNoteAuthor, row.latest_claim_note_author, summary.authorFullName, summary.author, summary.by);
    const at = firstPresent(row.latestClaimNoteAt, row.latest_claim_note_at_utc, summary.createdAtUtc, summary.at);
    const count = nonNegativeIntegerValue(firstPresent(row.claimNoteCount, row.claim_note_count, summary.count, summary.noteCount));
    const claimId = trackerApiClaimId(row);
    const noteText = cleanApiDisplayValue(value, column.key);
    const meta = [author, at ? formatDateTime(at) : "", count ? formatNounCount(count, "note") : ""].filter(Boolean).join(" · ");
    return `<td${stickyAttrs} title="${escapeHtml(noteText)}">
      ${claimId && hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) && !isRedactedClientRole()
        ? `<button class="claim-note-summary-button" type="button" data-open="${escapeHtml(claimId)}" data-org-id="${escapeHtml(String(row.orgId || row.org_id || ""))}" data-service-id="${escapeHtml(String(row.serviceId || row.service_id || ""))}"><span>${noteText ? escapeHtml(noteText) : "No claim notes"}</span>${meta ? `<small>${escapeHtml(meta)}</small>` : ""}</button>`
        : `${formatApiCellValue(column.key, value)}${meta ? `<span class="subtext">${escapeHtml(meta)}</span>` : ""}`}
    </td>`;
  }
  if (column.key === "primaryExpectedTotal") {
    // Role-gated here too: the server already refuses both client roles.
    const oonTag = trackerOonRateTagAllowed() ? oonRateTagHtml(row) : "";
    if (oonTag) return `<td${stickyAttrs}>${formatApiCellValue(column.key, value, moneyCertainty)}${oonTag}</td>`;
  }
  if (["originalBillDate", "billDateAging"].includes(column.key) && !formatApiCellValue(column.key, value, moneyCertainty)
    && !trackerApiCellRawValue(row, "originalBillDate") && trackerApiCellRawValue(row, "billDateAging") == null) {
    return `<td${stickyAttrs}>${escapeHtml(billDateBlankText(row))}</td>`;
  }
  return `<td${stickyAttrs}>${formatApiCellValue(column.key, value, moneyCertainty)}</td>`;
}

// OON LEARNED-RATE TAG (OON_LEARNED_RATES_PLAN.md). The T1a line facts
// oon_rate_state / oon_learned_rate / oon_rate_change_pct / oon_point_count, shown
// under Primary Expected rather than as a column. In shadow pricing Expected stays
// on the fee schedule and the tag says what the learned rate is; a cut is a line
// fact, present only on lines dated in the current cut period.
function oonRateTagModel(row = {}) {
  const stateValue = String(firstPresent(row.oonRateState, row.oon_rate_state) ?? "").trim().toLowerCase();
  if (!stateValue) return null;
  const rate = Number(firstPresent(row.oonLearnedRate, row.oon_learned_rate));
  const pct = Number(firstPresent(row.oonRateChangePct, row.oon_rate_change_pct));
  const points = Number(firstPresent(row.oonPointCount, row.oon_point_count));
  const rateText = Number.isFinite(rate) && rate > 0 ? formatMoneyValue(rate, { decimals: 2 }) : "";
  if (stateValue === "learning") {
    const seen = Number.isFinite(points) ? Math.max(0, Math.min(5, Math.trunc(points))) : 0;
    return { tone: "learning", text: `Learning ${seen}/5`, title: `Out-of-network rate is learning: ${seen} of 5 payments seen. Expected uses the fee schedule until then.` };
  }
  if (stateValue === "rate-cut" && Number.isFinite(pct) && pct < 0 && pct > -1) {
    return { tone: "cut", text: `Rate cut −${Math.round(-pct * 100)}%`, title: `Confirmed rate cut: 5 payments in a row at least 10% lower.${rateText ? ` Learned rate for this date of service: ${rateText} per unit.` : ""}` };
  }
  if ((stateValue === "learned" || stateValue === "rate-cut") && rateText) {
    return { tone: "learned", text: `Learned ${rateText}`, title: "Out-of-network rate learned from the median of this patient's last 5 payments, per unit." };
  }
  return null;
}

function oonRateTagHtml(row) {
  const model = oonRateTagModel(row);
  return model ? `<span class="oon-rate-tag oon-rate-tag--${model.tone}" title="${escapeHtml(model.title)}">${escapeHtml(model.text)}</span>` : "";
}

// HOLD DURATION (backend/serviceLineHoldEpisodes.js). The portal watches each
// refresh for lines entering and leaving On Hold because EZClaim keeps no status
// history. holdStartObserved = 0 means the line was already on hold when its
// company was first tracked, so the start and the day count are minimums and the
// cell says so ("12+", "10/19/2026 or earlier") instead of implying a real start.
// Dates are the stamp's calendar day, the same day the header filter groups by.
const TRACKER_HOLD_DISPLAY_KEYS = new Set(["holdReason", "onHoldSince", "daysOnHold", "holdReleasedOn"]);

function trackerHoldDate(value) {
  const normalized = normalizeDateValue(value);
  return normalized ? formatDate(normalized) : "";
}

function trackerHoldCellValue(row = {}, key = "") {
  const pick = (camel, snake) => firstPresent(row[camel], row[snake]);
  const startedAt = pick("onHoldSince", "hold_started_at_utc");
  if (startedAt == null || startedAt === "") return "";
  const startKnown = String(firstPresent(pick("holdStartObserved", "hold_start_observed"), "1")) !== "0";
  if (key === "holdReason") return String(pick("holdReason", "hold_reason") || "").trim();
  if (key === "onHoldSince") {
    const date = trackerHoldDate(startedAt);
    return date && !startKnown ? `${date} or earlier` : date;
  }
  if (key === "daysOnHold") {
    const days = Number(pick("daysOnHold", "hold_days"));
    if (!Number.isFinite(days)) return "";
    return startKnown ? String(days) : `${days}+`;
  }
  if (key === "holdReleasedOn") return trackerHoldDate(pick("holdReleasedOn", "hold_released_at_utc"));
  return "";
}

function trackerApiCellRawValue(row = {}, key = "") {
  if (key === "claimId") return trackerApiClaimId(row);
  const claimId = trackerApiClaimId(row);
  const workflow = claimId ? (state.workflowByClaim?.[claimId] || {}) : {};
  const serviceId = String(row.serviceId || row.service_id || "").trim();
  const followUp = claimId ? (state.followUpByService?.[followUpKey(claimId, serviceId)] || {}) : {};
  if (key === "action") {
    return firstPresent(followUp.action, row.action, row.followUpAction, row.follow_up_action);
  }
  if (key === "followUpDate") {
    return firstPresent(followUp.followUpDate, row.followUpDate, row.follow_up_date, row.actionDate);
  }
  if (key === "assignedTo") {
    return firstPresent(workflow.assignedTo, row.assignedTo, row.assigned_to);
  }
  if (key === "assignmentStatus") {
    return firstPresent(workflow.assignmentStatus, row.assignmentStatus, row.assignment_status);
  }
  // IN/OUT OF NETWORK IS BUCKETED IN THE CELL, not shown as raw contract text.
  //
  // The stored value is whatever the fee schedule says, and it says both "IN"
  // and "In Network" for the same idea (1,672 rows against the rate publisher's
  // own spelling). Rendering it raw would put four different words in one column
  // and, worse, disagree with the slicer beside it: a biller filtering
  // "In Network" would see cells reading "IN" and reasonably conclude the filter
  // is broken.
  //
  // There is no parity cost. This column was BLANK on every lane until
  // 2026-08-21, so nothing downstream has ever consumed its values and there is
  // no legacy spelling to preserve. Blank becomes "Unknown", which is a real
  // answer rather than an empty cell.
  if (key === "network") {
    return networkBucket(firstPresent(row.network, row.contractNetwork));
  }
  if (key === "mergedNotes") return mergedNoteCellModel(mergedNoteInputsFromApiRow(row)).text;
  if (key === "serviceLineNotes") {
    const summary = row.claimNoteSummary && typeof row.claimNoteSummary === "object" ? row.claimNoteSummary : {};
    return firstPresent(
      row.latestClaimNote,
      row.latest_claim_note,
      row.latestClaimNoteText,
      row.latest_claim_note_text,
      summary.text,
      summary.latestText,
      // Compatibility alias only: saved views still address this column by its
      // historical key, but the server now fills it from the claim-note projection.
      row.serviceLineNotes
    );
  }
  // CLIENT-DERIVED COLUMNS. backend/trackerColumnSourceMap.js CLIENT_DERIVED_COLUMNS
  // declares these four as derivable in the browser from values the row already
  // carries. They get no SQL column on purpose: a SQL column would be a SECOND
  // definition of a string backend/trackerWorkbookExport.js already defines, and
  // the grid and the exported workbook would then be free to disagree about the
  // same cell. Each is copied verbatim from that file's viewRowValues.
  if (key === "addCode") {
    // trackerWorkbookExport.js viewRowValues.addCode
    return [
      trackerApiCellRawValue(row, "primaryPayer"),
      trackerApiCellRawValue(row, "procedure"),
      trackerApiCellRawValue(row, "m1"),
      trackerApiCellRawValue(row, "m2")
    ].filter(Boolean).join(" | ");
  }
  // trackerWorkbookExport.js weekEnding(dos) — the Saturday ending the DOS week.
  if (key === "weekEnding") return trackerWeekEndingFromDos(trackerApiCellRawValue(row, "dos"));
  // viewRowValues.rbt = rendering_provider. The workbook's RBT column and Rendering
  // Provider hold the same person; EZClaim has no separate RBT identity.
  if (key === "rbt") return trackerApiCellRawValue(row, "renderingProvider");
  // EHR Billing Entry ID — SERVER-SOURCED, NOT DERIVED. It is EZClaim's own
  // SrvCustomField2, which silver.MainTracker_VW publishes as [EHR Billing Entry ID].
  // This line used to end in `serviceId`, on the strength of a comment in
  // backend/trackerColumnSourceMap.js asserting that the EHR billing entry IS the
  // service line. Matched against the published Claim Insight workbook on
  // (claim_id, service_id) that rule was wrong on 112,206 of 112,206 rows, while
  // SrvCustomField2 agrees on 112,144 of 112,149 (99.9955%). A value the active
  // lane cannot supply stays BLANK: a biller pastes this id into EZClaim, so a
  // wrong one is worse than none.
  if (key === "ehrBillingEntryId") return firstPresent(row.ehrBillingEntryId, row.ehr_billing_entry_id);
  // The legacy workbook's duplicate of Bill Date Aging (trackerWorkbookExport.js:1343).
  // Resolved THROUGH billDateAging rather than aliased straight to a row key, so it
  // reads whatever billDateAging reads in either lane and cannot drift from it.
  if (key === "time") return trackerApiCellRawValue(row, "billDateAging");
  if (TRACKER_HOLD_DISPLAY_KEYS.has(key)) return trackerHoldCellValue(row, key);
  const alias = apiRowAliasForColumn(key);
  return firstPresent(row[key], alias && alias !== key ? row[alias] : undefined);
}

// Verbatim port of backend/trackerWorkbookExport.js weekEnding(): the Saturday
// that ends the DOS week, computed in UTC so it is a pure function of the calendar
// date and never shifts with the viewer's zone.
function trackerWeekEndingFromDos(value) {
  const dos = normalizeDateValue(value);
  if (!dos) return "";
  const date = new Date(`${dos}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + ((6 - date.getUTCDay() + 7) % 7));
  return date.toISOString().slice(0, 10);
}

function currentSlicerClaims() {
  if (trackerApiEnabled()) {
    const mode = state.rowMode || "service";
    const query = buildTrackerQueryRequest(mode);
    const apiPage = trackerApiPageForQuery(query);
    if (apiPage) return claimsFromTrackerRows(apiPage.rows, mode);
  }
  return getClaims();
}

function trackerApiClaimId(row = {}) {
  const candidates = [row.claimId, row.claim_id, row["Claim ID"], row.ClaimID, row.id];
  for (const candidate of candidates) {
    const normalized = normalizeApiClaimId(candidate);
    if (normalized) return normalized;
  }
  return "";
}

function normalizeApiClaimId(value) {
  const normalized = normalizeClaimId(value);
  if (!normalized || /^server\s+row$/i.test(normalized) || /^row\s+\d+$/i.test(normalized)) return "";
  return normalized;
}

// A BLANK BILL DATE (portal ticket, 2026-11-02; owner correction 2026-11-03: billed, not built).
// On production almost every such line is On Hold or Ready to Submit. A few hundred
// are marked Submitted or already paid, so those keep the plainer "No bill date"
// rather than claiming something false. Display only.
const BILL_DATE_NOT_BILLED = "Not billed yet";
function billDateBlankText(row = {}) {
  const status = String(firstPresent(row.claimStatus, row.claim_status) || "").trim().toLowerCase();
  const paid = moneyNumber(firstPresent(row.totalPaid, row.total_paid)) > 0;
  return status === "submitted" || paid ? "No bill date" : BILL_DATE_NOT_BILLED;
}
function billDateGroupLabel(label) {
  return !label || label === "(blank)" ? BILL_DATE_NOT_BILLED : label;
}

function formatApiCellValue(key, value, moneyCertainty = null) {
  // UNKNOWN IS CHECKED BEFORE EMPTY. If we know WHY this figure cannot be
  // computed, the cell says so - a bare blank reads as "still loading", and a
  // $0 reads as an asserted zero. Neither is true for a line with no rate.
  const unknownReason = unknownMoneyReason(key, moneyCertainty || {});
  if (unknownReason) return moneyUnknownHtml(unknownReason);
  const displayValue = cleanApiDisplayValue(value, key);
  if (displayValue == null || displayValue === "") return "";
  if (key === "claimDue") return escapeHtml(slicerDisplayValue("claimDue", displayValue));
  if (["dos", "followUpDate", "originalBillDate", "weekEnding"].includes(key)) {
    const normalizedDate = normalizeDateValue(displayValue);
    return normalizedDate ? escapeHtml(formatDate(normalizedDate)) : "";
  }
  if (currencyColumns.has(key)) {
    if (!Number.isFinite(parseMoneyNumber(displayValue))) return moneyUnknownHtml("Amount unavailable.");
    const exact = escapeHtml(formatMoneyValue(displayValue, { decimals: 2 }));
    return `<span title="${exact}" aria-label="${exact}">${money(displayValue)}</span>` + (moneyIsEstimated(key, moneyCertainty || {}) ? '<span class="subtext">Estimated</span>' : "");
  }
  return escapeHtml(displayValue);
}

function cleanApiDisplayValue(value, key = "") {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || /^<\s*blank\s*>$/i.test(text) || /^null$/i.test(text) || /^undefined$/i.test(text)) return "";
  if ((key === "action" || key === "recommendedNextAction") && /^live\s+synapse\s+review$/i.test(text)) return "";
  return text.replace(/\s*,\s*<\s*blank\s*>/ig, "").replace(/<\s*blank\s*>/ig, "").trim();
}

const localNoteSearchState = { key: "", status: "idle", matches: new Set() };
function localNoteSearchMatches(search) {
  if (!search || trackerApiEnabled() || !trackerApiBase()) { if (!search) localNoteSearchState.key = ""; return null; }
  const token = trackerApiToken();
  const key = workflowAccessScopedRequestKey({ search, base: trackerApiBase(), epoch: trackerPublicationEpoch });
  if (localNoteSearchState.key === key) return localNoteSearchState.status === "ready" ? localNoteSearchState.matches : new Set();
  Object.assign(localNoteSearchState, { key, status: "loading", matches: new Set() });
  const current = () => localNoteSearchState.key === key && trackerApiToken() === token
    && workflowAccessScopedRequestKey({ search, base: trackerApiBase(), epoch: trackerPublicationEpoch }) === key;
  (async () => {
    const matches = new Set();
    let total = null;
    for (let number = 1; number <= 20; number += 1) {
      const response = await fetch(trackerApiBase() + "/api/tracker/query", {
        method: "POST", cache: "no-store", headers: { "content-type": "application/json", authorization: "Bearer " + token },
        body: JSON.stringify({ resource: "claims", mode: "claim", page: { number, size: 500 }, filters: { noteSearch: search, queue: "all" }, responseShape: { columns: ["orgId", "claimId"], includePhi: false, includeFinancials: false, includeInternalNotes: false } }), signal: AbortSignal.timeout(30000)
      });
      const body = await response.json();
      if (!current()) return;
      if (!response.ok || body.error || body.decision?.allowed !== true || body.sourceCompleteness?.complete !== true
        || !Array.isArray(body.rows) || !Number.isSafeInteger(body.page?.totalRows) || body.page.totalRows < 0
        || body.page.number !== number || body.page.size !== 500 || (total !== null && total !== body.page.totalRows)) throw Error("incomplete");
      total = body.page.totalRows;
      if (total > 10000 || body.rows.length !== Math.min(500, Math.max(0, total - (number - 1) * 500))) throw Error("incomplete");
      for (const row of body.rows) {
        if (!String(row.orgId || "") || !String(row.claimId || "")) throw Error("identity");
        const id = JSON.stringify([String(row.orgId), String(row.claimId)]);
        if (matches.has(id)) throw Error("duplicate");
        matches.add(id);
      }
      if (matches.size === total) {
        Object.assign(localNoteSearchState, { status: "ready", matches });
        renderClaimsTable(); return;
      }
    }
    throw Error("incomplete");
  })().catch(() => { if (current()) { localNoteSearchState.status = "error"; renderClaimsTable(); } });
  return new Set();
}

function getBaseFilteredClaims(query = buildTrackerQueryRequest()) {
  const { filters } = query;
  // The local/demo lane evaluates the same complete slicer payload the API lane
  // sends to the server, so both modes filter before their page window is built.
  const slicers = ensureSlicerState(query.slicers || state.slicers);
  const claimIdSearch = new Set(normalizeClaimIdSearchValues(filters.claimIdSearch).map((id) => id.toLowerCase()));
  const serviceIdSearch = String(filters.serviceIdSearch || "").trim().toLowerCase();
  const search = String(filters.search || "").trim().toLowerCase();
  const noteSearch = String(filters.noteSearch || "").trim().toLowerCase();
  const persistedNoteMatches = localNoteSearchMatches(noteSearch);

  return getClaims().filter((claim) => {
    const haystack = [
      claim.client,
      claim.patient,
      claim.facility,
      claim.renderingProvider,
      claim.claimStatus,
      claim.action,
      claim.primaryPayer,
      claim.secondaryPayer,
      claim.primaryExternalId,
      claim.secondaryExternalId,
      claim.services.map((service) => [
        claim.orgId,
        service.dos,
        service.procedure,
        service.m1,
        service.m2,
        service.status,
        prCodes(service),
        prReasons(service),
        patientResponsibilityStatus(service, claim),
        statementStatus(service, claim)
      ].join(" ")).join(" ")
    ].join(" ").toLowerCase();
    const claimIdText = String(claim.claimId || "").toLowerCase();
    const serviceIdMatches = !serviceIdSearch || claim.services.some((service) => String(service.serviceId || "").toLowerCase() === serviceIdSearch);
    const ownerValue = claim.assignedTo || "Unassigned";
    const noteHaystack = [
      claim.latestClaimNoteText,
      claim.claimNoteSummary?.text,
      claim.claimNoteSummary?.latestText,
      ...(Array.isArray(claim.notes) ? claim.notes.map((note) => note && note.text) : []),
      ...(claim.services || []).flatMap((service) => [service.latestClaimNoteText, service.latestClaimNote])
    ].filter(Boolean).join(" ").toLowerCase();
    return (!claimIdSearch.size || claimIdSearch.has(claimIdText))
      && serviceIdMatches
      && (!(filters.fromDos || filters.toDos) || claim.services.some(service => trackerServiceDateMatches(service, filters) && (!serviceIdSearch || String(service.serviceId || "").toLowerCase() === serviceIdSearch)))
      && (!search || haystack.includes(search))
      && (!noteSearch || (persistedNoteMatches ? persistedNoteMatches.has(JSON.stringify([String(claim.orgId), String(claim.claimId)])) : noteHaystack.includes(noteSearch)))
      && (filters.queue === "all" || matchesQueue(claim, filters.queue))
      && (filters.tenant === "All" || claim.client === filters.tenant)
      && (filters.provider === "All" || claim.renderingProvider === filters.provider)
      && (filters.primaryPayer === "All" || claim.primaryPayer === filters.primaryPayer)
      && (filters.secondaryPayer === "All" || claim.secondaryPayer === filters.secondaryPayer)
      && (filters.action === "All" || followUpFilterRowValue(claim) === followUpFilterValue(filters.action))
      && (filters.match === "All" || claim.match === filters.match)
      && (filters.claimStatus === "All" || claim.claimStatus === filters.claimStatus)
      && (filters.lifecycle === "All" || buildClaimStatusProfile(claim).lifecycleStage === filters.lifecycle)
      && (filters.aging === "All" || claim.aging === filters.aging)
      && (filters.owner === "All" || ownerValue === filters.owner)
      && (!filters.minBalance || claimFinancialView(claim).sourceOutstanding >= filters.minBalance)
      && matchesSlicers(claim, slicers);
  });
}

// EVERY FILTER THAT CAN NARROW ALL DATA, AND THE ONE WAY TO CLEAR EACH ONE.
//
// Keyed by the SAME keys collectTrackerFilters() puts in the request body, so
// the list of things a clear must undo is derived from the list of things the
// grid actually sends - not hand-maintained beside it.
//
// The owner, 2026-09-17: "you hit Clear Slicers and it has to clear all the
// slicers, the filters, the claim IDs, and everything ... every time we click
// here and there, it's still using the filters". The button cleared
// state.slicers and NOTHING else, so a claim-ID search, any of the thirteen
// controls above the grid, the column-header filters and the queue card all
// survived a "clear" - and none of them produced a chip, so the reader was
// never told. That is the bug class: a filter that is applied and not shown.
//
// `tenant` is deliberately ABSENT. Company scope is not a filter: it has its
// own always-visible switcher, and clearing filters must never widen a reader
// to companies they did not ask for.
//
// backend/verify_all_data_clear_filters.js turns RED if collectTrackerFilters()
// ever grows a key with no entry here, so a filter cannot be added without a
// way out of it.
const TRACKER_QUICK_FILTER_RESETS = {
  fromDos: { clear: () => { const input = document.getElementById("trackerServiceDateFrom"); if (input) { input.value = ""; input.setCustomValidity(""); } const error = document.getElementById("trackerServiceDateError"); if (error) error.textContent = ""; } },
  toDos: { clear: () => { const input = document.getElementById("trackerServiceDateTo"); if (input) { input.value = ""; input.setCustomValidity(""); } const error = document.getElementById("trackerServiceDateError"); if (error) error.textContent = ""; } },
  claimIdSearch: { clear: () => setClaimIdSearchValue("") },
  serviceIdSearch: { clear: () => { if (els.serviceIdSearchInput) els.serviceIdSearchInput.value = ""; } },
  search: { clear: () => { if (els.searchInput) els.searchInput.value = ""; } },
  noteSearch: { clear: () => { if (els.noteSearchInput) els.noteSearchInput.value = ""; } },
  provider: { clear: () => { if (els.providerFilter) els.providerFilter.value = "All"; } },
  primaryPayer: { clear: () => { if (els.primaryFilter) els.primaryFilter.value = "All"; } },
  secondaryPayer: { clear: () => { if (els.secondaryFilter) els.secondaryFilter.value = "All"; } },
  action: { clear: () => { if (els.actionFilter) els.actionFilter.value = "All"; } },
  match: { clear: () => { if (els.matchFilter) els.matchFilter.value = "All"; } },
  claimStatus: { clear: () => { if (els.claimStatusFilter) els.claimStatusFilter.value = "All"; } },
  lifecycle: { clear: () => { if (els.lifecycleFilter) els.lifecycleFilter.value = "All"; } },
  aging: { clear: () => { if (els.agingFilter) els.agingFilter.value = "All"; } },
  owner: { clear: () => { if (els.ownerFilter) els.ownerFilter.value = "All"; } },
  minBalance: { clear: () => { if (els.minBalanceFilter) els.minBalanceFilter.value = ""; } },
  // Dropping the ?queue= seed belongs to the QUEUE, not to one button. The queue
  // chip's x, Reset View and "Clear all filters" all arrive here; setQueueFilter()
  // (the queue cards and Clear queue) does the same for its own route. Clearing a
  // queue anywhere and having a reload put it back is the same "it's still using
  // the filters" the owner reported, one control over.
  // THE "Company scope" SELECT IS A FILTER (2026-09-17, review). It sits in the
  // same strip as Claim IDs, Service ID and Search, and it survived the button
  // with no chip and no count: pick a company here and the grid showed 4 of 10
  // rows over a badge reading "0 active", and "Clear all filters" left it there.
  // Clearing it cannot widen anyone - collectTrackerFilters() reads
  // `activeClientGroupTenant() || els.tenantFilter?.value`, so the HEADER
  // switcher wins whenever it is set, and role scope is enforced server-side.
  // The header switcher is the thing that survives; this select does not.
  tenant: { clear: () => { if (els.tenantFilter) els.tenantFilter.value = "All"; } },
  queue: { clear: () => { state.queueFilter = "all"; clearTrackerFilterUrlState(); } }
};

// The way back in, for the filter panel's OFF switch. Each takes the value
// collectTrackerFilters() reported and returns true only when the control now
// holds exactly that value; a select whose option has gone answers false so the
// panel can flag it rather than apply something else.
function restoreTrackerSelectValue(select, value) {
  if (!select || !Array.from(select.options || []).some((option) => option.value === value)) return false;
  select.value = value;
  return true;
}

function restoreTrackerInputValue(input, value) {
  if (!input || typeof value !== "string") return false;
  input.value = value;
  return true;
}

function restoreTrackerServiceDate(id, value) {
  const input = document.getElementById(id);
  if (!input || !/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  input.value = trackerServiceDateDisplay(value);
  input.setCustomValidity("");
  return true;
}

const TRACKER_QUICK_FILTER_RESTORES = {
  fromDos: (value) => restoreTrackerServiceDate("trackerServiceDateFrom", value),
  toDos: (value) => restoreTrackerServiceDate("trackerServiceDateTo", value),
  claimIdSearch: (value) => { setClaimIdSearchValue(value); return normalizeClaimIdSearchValues(value).length > 0; },
  serviceIdSearch: (value) => restoreTrackerInputValue(els.serviceIdSearchInput, value),
  search: (value) => restoreTrackerInputValue(els.searchInput, value),
  noteSearch: (value) => restoreTrackerInputValue(els.noteSearchInput, value),
  provider: (value) => restoreTrackerSelectValue(els.providerFilter, value),
  primaryPayer: (value) => restoreTrackerSelectValue(els.primaryFilter, value),
  secondaryPayer: (value) => restoreTrackerSelectValue(els.secondaryFilter, value),
  action: (value) => restoreTrackerSelectValue(els.actionFilter, value),
  match: (value) => restoreTrackerSelectValue(els.matchFilter, value),
  claimStatus: (value) => restoreTrackerSelectValue(els.claimStatusFilter, value),
  lifecycle: (value) => restoreTrackerSelectValue(els.lifecycleFilter, value),
  aging: (value) => restoreTrackerSelectValue(els.agingFilter, value),
  owner: (value) => restoreTrackerSelectValue(els.ownerFilter, value),
  minBalance: (value) => restoreTrackerInputValue(els.minBalanceFilter, value),
  tenant: (value) => restoreTrackerSelectValue(els.tenantFilter, value),
  queue: (value) => {
    if (!queueDefinitions.some((queue) => queue.key === value)) return false;
    state.queueFilter = value;
    return true;
  }
};

// Only the HEADER switcher is scope. When it is set it owns `tenant` outright
// (collectTrackerFilters() prefers it), so the term is lifted out of the counted
// filters and stated as scope instead. When the header is at All clients, a
// narrowing can only have come from the "Company scope" SELECT in the filter
// strip - which is an ordinary filter, and is counted and chipped like one.
function trackerFilterTermsExcludingScope(filters = {}) {
  if (!activeClientGroupTenant()) return { ...filters };
  const { tenant, ...rest } = filters;
  return rest;
}

// "" when the reader is looking at every company they are entitled to, which is
// the ABSENCE of a scope and must never be printed as a filter named All.
function trackerFilterScopeTerm() {
  const tenant = String(activeClientGroupTenant() || "").trim();
  return !tenant || tenant.toLowerCase() === "all" ? "" : `Company: ${tenant}`;
}

function clearTrackerQuickFilter(key) {
  const entry = TRACKER_QUICK_FILTER_RESETS[key];
  if (!entry) return;
  entry.clear();
  resetTrackerPage();
  saveState();
  renderSlicers();
  renderClaimsTable();
}

// applyUrlState() re-seeds state.queueFilter from ?queue= on EVERY boot. Clearing
// the filters and then reloading - or reopening the same drill-in link - therefore
// brought the queue straight back, with nothing on screen to say it had. The
// parameter is dropped together with the filter it seeds.
function clearTrackerFilterUrlState() {
  try {
    if (typeof window === "undefined" || !window.history || typeof window.history.replaceState !== "function") return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("queue")) return;
    url.searchParams.delete("queue");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  } catch (_) { /* rewriting the address bar is best effort */ }
}

function trackerServiceDateValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  if (!match) return null;
  const iso = `${match[3]}-${match[1]}-${match[2]}`;
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === iso ? iso : null;
}

function trackerServiceDateDisplay(value) {
  return value ? `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(0, 4)}` : "";
}

function trackerServiceDateRange() {
  const fromDos = trackerServiceDateValue(document.getElementById("trackerServiceDateFrom")?.value);
  const toDos = trackerServiceDateValue(document.getElementById("trackerServiceDateTo")?.value);
  const invalid = fromDos === null || toDos === null || (fromDos && toDos && fromDos > toDos);
  return { fromDos, toDos, invalid };
}

function trackerServiceDateMatches(service, filters) {
  const dos = String(service.dos || service.serviceDate || "").slice(0, 10);
  return (!filters.fromDos && !filters.toDos) || Boolean(dos && (!filters.fromDos || dos >= filters.fromDos) && (!filters.toDos || dos <= filters.toDos));
}

function collectTrackerFilters() {
  return {
    fromDos: trackerServiceDateRange().invalid ? "invalid" : trackerServiceDateRange().fromDos,
    toDos: trackerServiceDateRange().invalid ? "invalid" : trackerServiceDateRange().toDos,
    claimIdSearch: collectClaimIdSearchValue(),
    serviceIdSearch: els.serviceIdSearchInput?.value.trim() || "",
    search: els.searchInput?.value.trim() || "",
    noteSearch: hasPermission(PERMISSIONS.SEARCH_CLAIM_NOTES) ? (els.noteSearchInput?.value.trim() || "") : "",
    // GLOBAL CLIENT SWITCHER is authoritative here. The DOM select is only
    // consulted when no client is selected: a <select> whose options were
    // rebuilt without the chosen name reports "" and would widen the query
    // straight back to All. This also keeps trackerApiCacheKey correct for
    // free, since it hashes `filters`.
    tenant: activeClientGroupTenant() || els.tenantFilter?.value || "All",
    provider: els.providerFilter?.value || "All",
    primaryPayer: els.primaryFilter?.value || "All",
    secondaryPayer: els.secondaryFilter?.value || "All",
    action: els.actionFilter?.value || "All",
    match: els.matchFilter?.value || "All",
    claimStatus: els.claimStatusFilter?.value || "All",
    lifecycle: els.lifecycleFilter?.value || "All",
    aging: els.agingFilter?.value || "All",
    owner: els.ownerFilter?.value || "All",
    // An empty min-balance box is NOT "minimum zero". Sending 0 made the
    // server apply `owed >= 0`, which is not the no-op it looks like: NULL
    // >= 0 is UNKNOWN in SQL, so every line with an unknown owed vanished -
    // 18,295 of 234,806 rows returned, under a pager printing "of 18,295" as
    // if it were the book. "" is the absence of a bound, and it is what
    // describeTrackerFilter() reads too, so the request and the filters the
    // reader is shown cannot drift apart.
    minBalance: nonNegativeMoneyFilterValue(els.minBalanceFilter?.value) || "",
    queue: state.queueFilter || "all"
  };
}

// The API owns every slicer predicate, including derived due-status and payer
// class dimensions. Keeping one normalized payload here guarantees filtering
// happens across the full result set before the server creates a page window.
function serverSlicers(slicers = {}) {
  return ensureSlicerState(slicers);
}

function buildTrackerQueryRequest(mode = state.rowMode || "service") {
  const normalizedMode = mode === "claim" ? "claim" : "service";
  const access = trackerAccessEnvelope(normalizedMode);
  const tableSort = state.tableSort || {};
  const sortField = tableSort.column ? SERVER_SORTABLE_TRACKER_COLUMNS[tableSort.column] : null;
  const userSort = sortField ? [{ field: sortField, direction: tableSort.dir === "desc" ? "desc" : "asc" }] : [];
  return {
    resource: normalizedMode === "service" ? "serviceLines" : "claims",
    mode: normalizedMode,
    page: {
      number: Math.max(1, positiveNumber(state.trackerPage, 1)),
      size: trackerApiEnabled() ? trackerApiPageSizeValue(state.trackerPageSize) : trackerPageSizeValue(state.trackerPageSize)
    },
    // The header sort leads; the standing base sort follows MINUS the column the
    // user already chose. ORDER BY may not name a column twice, so sending
    // [owed ASC, ..., owed DESC] made 4 of the 7 sortable headers answer HTTP 502
    // and the grid print "Rows unavailable - the request failed" (TEST, 2026-09-03).
    // The server dedupes too; this keeps the request honest on its own.
    sort: [
      ...userSort,
      ...[
        // Newest service dates first unless the reader picked a column sort.
        { field: "service_date", direction: "desc" },
        { field: "workPriority", direction: "desc" },
        { field: "pastDue", direction: "asc" },
        { field: "aging", direction: "desc" },
        { field: "owed", direction: "desc" }
      ].filter((entry) => !userSort.some((chosen) => chosen.field === entry.field))
    ],
    filters: collectTrackerFilters(),
    slicers: serverSlicers(ensureSlicerState(state.slicers)),
    columnFilters: ensureColumnFilterState(state.columnFilters),
    // The comparison half of the same filter, on the same keys. Compiled
    // server-side into parameterised predicates and composed with the
    // checklist by AND, exactly as Excel composes them.
    columnFilterRanges: columnFilterRangeState(),
    // Same key space as the two maps above, so the server applies the direction
    // the reader chose over the same selection and the request the grid sends is
    // the whole filter, not half of it.
    slicerModes: slicerModeState(),
    columnFilterModes: columnFilterModeState(),
    access,
    // Snapshot permission scope independently of the visible grid projection.
    facetAccess: trackerAccessEnvelope(normalizedMode, true),
    responseShape: {
      columns: trackerApiResponseColumns(access, normalizedMode),
      includeFinancials: access.includeFinancials,
      includePhi: access.includePhi,
      includeInternalNotes: false
    }
  };
}

// WHY IS THE GRID EMPTY? THERE ARE FIVE ANSWERS AND THEY ARE NOT THE SAME.
//
// NORTH_STAR.md §6: "Fail loud, not empty. 'No rows' and 'not allowed' must
// never look the same." The same rule one layer up: **an empty grid must say
// whether anybody actually asked.** On 2026-08-03 the deployed portal showed
// a SuperAdmin "Rows 0-0 of 0" and "No rows came back, and no filters are
// applied - that usually means the data feed is unavailable" while the API
// was healthy and returning 234,806 rows to the same account. The table BODY
// knew (it renders a loading or retry row); the summary strip and the pager
// did not, and they are what the reader looks at.
//
//   "off"       the API lane is not enabled at all - local JSON mode.
//   "unasked"   the lane is on and NO request has been issued for this query.
//               A grid in this state has no business saying anything about
//               row counts. This is the state the outage was really in.
//   "pending"   asked, waiting. Up to 45s on a cold Azure start.
//   "error"     asked, failed. Says so, and offers a retry.
//   "answered"  asked and answered. Only here does 0 rows mean 0 rows.
// WHO SAYS THIS PRINCIPAL HAS NO SCOPE? THE SERVER DOES - NEVER THIS FILE.
//
// NORTH_STAR.md §6: "'No rows' and 'not allowed' must never look the same."
// The UI's job here is to RENDER a decision, not to make one. A client-side
// `!scope.orgIds.length` check would drift from enforcement the moment the rule
// changes - and it is already wrong today: state.authUserScope carries role /
// orgIds / assignedName and DELIBERATELY not payerNames, so it cannot see a book
// at all. Every BOOKED offshore biller would be told their book is missing.
//
// Two channels, one decision (backend/portalRoleCatalog.principalScopeDenial):
//   response  the 403 this exact query just got back (freshest; route-specific)
//   session   the login payload's own denial, which survives the LOCAL fallback
//             lane where there is no 403 to read at all - the lane the
//             2026-08-03 mark screenshot was taken in.
// Response wins when present; otherwise the session answer stands.
//
// The prose fallback is per-FIELD, never per-denial: today's 403 carries
// `reason` + `message` and no title/remedy, so the SERVER'S OWN SENTENCE still
// reaches the reader and only the two fields it does not send yet degrade. This
// is the one place a sentence is invented client-side, and it is never used to
// DECIDE that a denial exists.
const SCOPE_DENIAL_FALLBACK = {
  title: "Your access hasn't been set up yet",
  message: "This account has not been given anything to see, so this screen has nothing to show. This is a setup gap, not an empty day.",
  remedy: "Nobody can fix this from this screen. Ask your TMS supervisor to finish setting up your account."
};

function normalizeScopeDenial(denial, source = "response") {
  if (!denial || typeof denial !== "object") return null;
  return {
    code: String(denial.code || denial.reason || ""),
    title: String(denial.title || "") || SCOPE_DENIAL_FALLBACK.title,
    message: String(denial.message || "") || SCOPE_DENIAL_FALLBACK.message,
    remedy: String(denial.remedy || "") || SCOPE_DENIAL_FALLBACK.remedy,
    source
  };
}

function portalScopeDenial() {
  const fromResponse = typeof trackerApiPageState !== "undefined" && trackerApiPageState && trackerApiPageState.status === "denied"
    ? trackerApiPageState.scopeDenial
    : null;
  return normalizeScopeDenial(fromResponse, "response")
    || normalizeScopeDenial(typeof state !== "undefined" && state && state.authUserScope ? state.authUserScope.scopeDenial : null, "session");
}

function trackerApiLaneState(query = null) {
  // FIRST, and ahead of "off" on purpose. A scope denial is true in EVERY lane:
  // the local fallback filters client-side and will happily render an empty grid
  // for a principal the server refuses outright, which is exactly the screen the
  // 2026-08-03 mark measured ("Local fallback" badge, top right). The typeof
  // guard is for the verifier sandboxes that extract this function on its own -
  // same reason emptyClaimsRowHtml guards trackerApiEnabled.
  if (typeof portalScopeDenial === "function" && portalScopeDenial()) return "denied";
  if (!trackerApiEnabled()) return "off";
  if (!trackerApiPageState || !trackerApiPageState.key) return "unasked";
  if (query && trackerApiPageState.key !== trackerApiCacheKey(query)) return "unasked";
  if (trackerApiPageState.status === "pending") return "pending";
  if (trackerApiPageState.status === "denied") return "denied";
  if (trackerApiPageState.status === "error") return "error";
  if (trackerApiPageState.status === "ok" && trackerApiPageState.response) return "answered";
  return "unasked";
}

// ONE automatic retry per query key.
//
// trackerApiPageForQuery() only re-requested when the cache key CHANGED or the
// status was "idle". So a single failure - a cold-start timeout is the common
// one, and the client's own abort is 45s - left the lane in a terminal state:
// every later render short-circuited, no request was ever issued again, and
// the grid sat empty until a full page reload. That is exactly the shape of
// "the browser never makes the request".
//
// Bounded at one on purpose. renderClaimsTable() is called FROM the error
// handler, so an unbounded retry here is an unbounded request loop against a
// service that is already struggling. One retry heals a cold start; a second
// failure is a real outage and the user gets a Retry button and an honest
// sentence instead of a hammer.
let trackerApiAutoRetriedKey = "";
function resetTrackerApiAutoRetry() { trackerApiAutoRetriedKey = ""; }

function trackerApiPageForQuery(query) {
  if (!trackerApiEnabled()) return null;
  cancelObsoleteTrackerFilterPreparation(query);
  const key = trackerApiCacheKey(query);
  if (trackerApiPageState.key === key && trackerApiPageState.status === "ok" && trackerApiPageState.response) {
    return {
      rows: Array.isArray(trackerApiPageState.response.rows) ? trackerApiPageState.response.rows : [],
      pageWindow: trackerApiPageWindow(trackerApiPageState.response),
      response: trackerApiPageState.response
    };
  }
  // `ok` with no response body is the same nothing as `idle` and used to be
  // treated as "already handled" - another way to never ask again.
  const answeredNothing = trackerApiPageState.status === "ok" && !trackerApiPageState.response;
  if (trackerApiPageState.key !== key || trackerApiPageState.status === "idle" || answeredNothing) {
    requestTrackerApiPage(query, key);
  } else if (trackerApiPageState.status === "error" && trackerApiAutoRetriedKey !== key) {
    trackerApiAutoRetriedKey = key;
    // DEFERRED, not synchronous. renderClaimsTable() is called FROM the error
    // handler, so retrying inside this render would replace the error the user
    // was about to read with a spinner, and would hit a service that failed a
    // millisecond ago with no pause at all. The guard re-checks the state on
    // the way in, so a retry that has been overtaken by a real answer, a filter
    // change or a manual Retry does nothing.
    window.setTimeout(() => {
      if (trackerApiPageState.key !== key || trackerApiPageState.status !== "error") return;
      requestTrackerApiPage(query, key);
      renderClaimsTable();
    }, 1200);
  }
  return null;
}

function trackerApiCanShowStalePage(query) {
  if (!trackerApiEnabled()) return false;
  if (trackerApiPageState.status !== "pending" && trackerApiPageState.status !== "error") return false;
  if (!trackerApiPageState.previousResponse) return false;
  if (trackerApiPageState.previousStableKey !== trackerApiCurrentStableCacheKey(query)) return false;
  return Array.isArray(trackerApiPageState.previousResponse.rows) && trackerApiPageState.previousResponse.rows.length > 0;
}

function trackerApiStalePageForQuery(query) {
  if (!trackerApiCanShowStalePage(query)) return null;
  const response = trackerApiPageState.previousResponse;
  return {
    rows: Array.isArray(response.rows) ? response.rows : [],
    pageWindow: trackerApiPageWindow(response),
    response,
    stale: true
  };
}

// FACET REQUEST PACING: A DEBOUNCE, AND A NEGATIVE CACHE.
//
// This lane loads a ~900KB claim-queue overlay server-side (652-1387ms
// measured) BEFORE it runs any SQL, so every request it issues is expensive
// whether it succeeds or fails. Two gaps made that cost compound:
//
//   * NO NEGATIVE CACHE. A same-key failure already did not re-fetch (the
//     dispatch below only fires on a NEW key or an idle slot), but every
//     keystroke mints a new key - so a lane that was failing for structural
//     reasons, as /api/tracker/facets was on 2026-08-11 (HTTP 400 on 100% of
//     calls), paid the whole overlay load again per keystroke. The cooldown
//     is therefore keyed on TIME, not on the request key: a per-key negative
//     cache would never be consulted twice.
//   * NO DEBOUNCE ON THIS LANE. The search input debounce (160ms) paces the
//     RE-RENDER; each settled render still minted a fresh facet key and fired
//     a fresh overlay load. 275ms is above the 160ms render debounce on
//     purpose, so a burst of typing collapses to one facet request.
//
// A SCOPE DENIAL IS NOT A FAILURE. The 403 path is a deliberate answer about
// who the caller is; suppressing it would only delay a correct message.
const TRACKER_API_FACET_DEBOUNCE_MS = 275;
const TRACKER_API_FACET_FAILURE_COOLDOWN_MS = 20000;
let trackerApiFacetDebounceTimer = null;
let trackerApiFacetPendingKey = "";
let trackerApiFacetLastFailureAtMs = 0;

function trackerApiFacetRequestSuppressed(nowMs = Date.now()) {
  return trackerApiFacetLastFailureAtMs > 0
    && (nowMs - trackerApiFacetLastFailureAtMs) < TRACKER_API_FACET_FAILURE_COOLDOWN_MS;
}

function noteTrackerApiFacetFailure() {
  trackerApiFacetLastFailureAtMs = Date.now();
}

// Recovery is immediate, not on a timer: one good answer clears the cooldown.
function clearTrackerApiFacetFailure() {
  trackerApiFacetLastFailureAtMs = 0;
}

function scheduleTrackerApiFacetRequest(facetQuery, key) {
  // Keep one latest request through the global failure cooldown. Dropping it
  // leaves a changed/cleared filter stuck until another user action renders.
  // An already-queued request for the SAME key is left alone. Resetting the
  // timer on every render would let a render-heavy screen starve the request
  // forever - a debounce that never fires is an outage, not a saving.
  if (trackerApiFacetDebounceTimer && trackerApiFacetPendingKey === key) return;
  if (trackerApiFacetDebounceTimer) clearTimeout(trackerApiFacetDebounceTimer);
  trackerApiFacetPendingKey = key;
  const scheduledScopeKey = trackerApiFacetCacheKey(trackerFacetQueryRequest(buildTrackerQueryRequest()));
  const delayMs = Math.max(TRACKER_API_FACET_DEBOUNCE_MS,
    trackerApiFacetLastFailureAtMs > 0
      ? TRACKER_API_FACET_FAILURE_COOLDOWN_MS - (Date.now() - trackerApiFacetLastFailureAtMs) : 0);
  trackerApiFacetDebounceTimer = setTimeout(() => {
    trackerApiFacetDebounceTimer = null;
    trackerApiFacetPendingKey = "";
    // A selection or access change can occur while its page read is pending,
    // before a render has scheduled the replacement summary. Never dispatch
    // the old scope after that change (or a publication/session change).
    if (trackerApiFacetCacheKey(trackerFacetQueryRequest(buildTrackerQueryRequest())) !== scheduledScopeKey) return;
    // Another in-flight failure may have extended the cooldown meanwhile.
    if (trackerApiFacetRequestSuppressed()) {
      scheduleTrackerApiFacetRequest(facetQuery, key);
      return;
    }
    requestTrackerApiFacets(facetQuery, key);
  }, delayMs);
}

function cancelQueuedTrackerApiFacetRequest() {
  if (trackerApiFacetDebounceTimer) clearTimeout(trackerApiFacetDebounceTimer);
  trackerApiFacetDebounceTimer = null;
  trackerApiFacetPendingKey = "";
}

function invalidateTrackerApiFacetsAfterWorkflowWrite() {
  cancelQueuedTrackerApiFacetRequest();
  cancelTrackerFilterPreparation();
  // The overlay the workflow facets are derived from just changed. Waiting out
  // the 15 s TTL would offer the reader the value they have already replaced.
  trackerFacetOptionStore.clear();
  Array.from(trackerChoiceReads.keys()).forEach((slot) => cancelTrackerChoiceRead(slot));
  trackerApiFacetState = {
    key: "",
    status: "idle",
    response: null,
    error: "",
    staleResponses: trackerApiFacetState.staleResponses || 0,
    lastStaleAt: trackerApiFacetState.lastStaleAt || ""
  };
}

function invalidatePortalReadsAfterWorkflowWrite(options = {}) {
  // trackerPublicationEpoch is the browser's local read identity as well as a
  // publication-change nonce. Advancing it here fences pre-save callbacks; it
  // does not change or claim a server generation/timestamp. Follow-up overlays
  // advance independently of tracker publication.
  trackerPublicationEpoch += 1;
  abortPendingTrackerApiPageRequest();
  resetTrackerApiAutoRetry();
  trackerApiPageState = trackerApiInitialState({ response: trackerApiPageState.response, stableKey: trackerApiPageState.stableKey });
  if (!reportReadContextCurrent(reportReadContext)) {
  resetReportDataset();
  reportFilterFacetReadState = { key: "", status: "idle", response: null, error: "" };
  reportFilterFacetRequestSequence += 1;
  reportRollupState = { key: null, byGroup: {} };
  preparedSnapshotReportState = {
    ...preparedSnapshotReportState,
    key: "", status: "idle", response: null, error: "", requestId: preparedSnapshotReportState.requestId + 1
  };
  resetReportPaymentEvidence();
  resetHostedReportDrill();
  reportCollectionsState = { key: "", status: "idle", response: null, error: "", code: "" };
  reportTurnaroundReadSequence += 1;
  reportTurnaroundReadController?.abort();
  reportTurnaroundState = { key: "", status: "idle", response: null, error: "", code: "", httpStatus: 0 };
  reportApiState = { key: "", status: "idle", response: null, error: "" };
  reportQueueDrilldownState = { key: "", status: "idle", response: null, error: "", request: null };
  clientReportClaimsApiState = { key: "", status: "idle", rows: [], totalRows: 0, truncated: false, error: "", retried: false };
  clientReportAggregateRequestSequence += 1;
  clientReportAggregatePageIdentity = "";
  clientReportAggregateApiState = { key: "", status: "idle", response: null, error: "" };
  clientReportDrillRequestSequence += 1;
  clientReportServerDrillState = { ...clientReportServerDrillState, key: "", status: "idle", response: null, error: "" };
  } else { renderReportReadStatus(); }
  if (typeof statementsPrepareAutomaticSelectionRefresh === "function") statementsPrepareAutomaticSelectionRefresh();
  Object.assign(statementPreviewState, { key: "", status: "idle", data: null, error: "", requestId: (statementPreviewState.requestId || 0) + 1 });
  if (typeof resetAdditionalPortalDataReadState === "function") resetAdditionalPortalDataReadState(options);
}

function trackerApiFacetsForQuery(query) {
  if (!trackerApiEnabled()) return null;
  // ID lookups used to keep the PREVIOUS facet set to save an aggregation, so a
  // Claim ID search that found 52 rows sat under a rail still saying
  // "Submitted 249,496" - counts for a population the grid was not showing.
  // The facet route already narrows by these filters (52 rows in ~0.5s warm),
  // so the rail now counts the same rows the grid does, like every other filter.
  const pageKey = trackerApiCacheKey(query);
  if (
    trackerApiPageState.key === pageKey
    && trackerApiPageState.status === "pending"
  ) {
    return trackerApiFacetState.response || null;
  }
  const facetQuery = trackerFacetQueryRequest(query);
  const key = trackerApiFacetCacheKey(facetQuery);
  if (trackerApiFacetState.key === key && trackerApiFacetState.status === "ok" && trackerApiFacetState.response) {
    return trackerApiFacetState.response;
  }
  if (trackerApiFacetState.key !== key || trackerApiFacetState.status === "idle") {
    scheduleTrackerApiFacetRequest(facetQuery, key);
  }
  // Stale-while-revalidate: keep the previous facet payload on screen while a
  // refresh is in flight instead of collapsing to page-only counts.
  return trackerApiFacetState.response || null;
}

function trackerApiCachedFacetsForQuery(query) {
  if (!trackerApiEnabled()) return null;
  if (String(query?.filters?.claimIdSearch || "").trim() || String(query?.filters?.serviceIdSearch || "").trim()) {
    return trackerApiFacetState.response || null;
  }
  const pageKey = trackerApiCacheKey(query);
  if (
    trackerApiPageState.key === pageKey
    && trackerApiPageState.status === "ok"
    && trackerApiPageState.response?.facets
  ) {
    return trackerApiPageState.response;
  }
  const facetQuery = trackerFacetQueryRequest(query);
  const facetKey = trackerApiFacetCacheKey(facetQuery);
  if (
    trackerApiFacetState.key === facetKey
    && trackerApiFacetState.status === "ok"
    && trackerApiFacetState.response?.facets
  ) {
    return trackerApiFacetState.response;
  }
  return null;
}

function trackerApiInitialState(overrides = {}) {
  return {
    key: "",
    stableKey: "",
    status: "idle",
    response: null,
    previousResponse: null,
    previousStableKey: "",
    error: "",
    // A typed scope denial is NOT an error string; declared here so the shape
    // exists in one place (see requestTrackerApiPage's 403 capture).
    scopeDenial: null,
    staleResponses: 0,
    lastStaleAt: "",
    ...overrides
  };
}

function trackerFacetQueryRequest(query) {
  const filters = {
    ...(query.filters || {}),
    queue: query.filters?.queue || state.queueFilter || "all"
  };
  const requestedFacets = Array.isArray(query.facets) ? query.facets.map(String).filter(Boolean) : [];
  const facets = requestedFacets.length ? requestedFacets : trackerFacetKeysForCurrentView(query.mode || state.rowMode || "service", state.slicerSize === "expanded", query);
  return {
    ...query,
    access: query.facetAccess || query.access,
    page: { number: 1, size: 1 },
    filters,
    slicers: serverSlicers(ensureSlicerState(query.slicers || state.slicers || emptySlicers)),
    columnFilters: ensureColumnFilterState(query.columnFilters || state.columnFilters || emptyColumnFilters),
    slicerModes: ensureFilterModeState(query.slicerModes || state.slicerModes, ensureSlicerState(query.slicers || state.slicers || emptySlicers)),
    columnFilterModes: ensureFilterModeState(query.columnFilterModes || state.columnFilterModes, ensureColumnFilterState(query.columnFilters || state.columnFilters || emptyColumnFilters)),
    // THE COUNTS MUST DESCRIBE THE POPULATION THE GRID IS SHOWING. A facet
    // request that dropped the ranges would offer "Aetna 7,613" over a grid
    // narrowed to Charges > 500 - the same class of disagreement the overlay
    // three-state resolution exists to prevent, one panel further out.
    columnFilterRanges: ensureColumnFilterRangeState(query.columnFilterRanges || state.columnFilterRanges),
    facets,
    responseShape: {
      columns: ["claimId"],
      includeFinancials: false,
      includePhi: false,
      includeInternalNotes: false
    }
  };
}

// ===========================================================================
// A CARD'S OWN OPTION LIST IS USUALLY ALREADY IN HAND.
// ===========================================================================
//
// Opening the Follow-Up Due slicer card showed "Loading choices..." with both
// pager buttons disabled for 3.1-4.7 s against production (2026-09-22, All
// scope, n=3) - even though the boot facet batch had answered `followUpDue`
// for the IDENTICAL filter state seconds earlier.
//
// THE RE-FETCH IS A KEY MISS, NOT A DIFFERENT QUESTION. trackerApiFacetCacheKey
// folds `facets` into the key, and the batch asks for TRACKER_CORE_FACET_KEYS
// (or the expanded list) while a card asks for `[key]` alone. Every other field
// - epoch, base, role, access, mode, filters, slicers, columnFilters - is
// byte-identical, because requestPagedSlicerChoices builds its query from the
// same buildTrackerQueryRequest() the rail does. So the card was re-asking the
// server a question the page already had the answer to, and waiting on ~900 ms
// of overlay load plus 1-3 s of SQL to be told the same three values.
//
// THE STORE IS KEYED ON THE SCOPE, NOT ON THE FACET LIST. Every facet response -
// the batch and each per-card read alike - deposits its rows per facet key under
// the scope that produced them. A later reader asking for ANY subset of those
// keys at the SAME scope is served from memory, with zero requests.
//
// WHAT THE SCOPE KEY MUST CARRY. Exactly what changes the POPULATION the counts
// describe: the publication epoch, the API base, role/user/access envelope, the
// row mode, and all three filter maps - plus the mode and range maps, which the
// facet request sends and the server compiles into predicates. Those three were
// missing from trackerApiFacetCacheKey itself (the same defect the server-side
// key carried until 2026-09-22): "Follow-Up Action: Appeal" and "everything
// EXCEPT Appeal" carry the identical selection list and differ only by the
// scalar beside it, so without them one scope's options answer the other's
// question. Folded CONDITIONALLY, so a request carrying none of them keeps the
// key it has always had and an in-flight answer is not orphaned mid-session.
//
// FRESHNESS IS THE SERVER'S RULE, MIRRORED. Overlay-derived facets
// (WORKFLOW_BACKED_FACETS in trackerSynapseApiAdapter.js) expire in 15 s here,
// because that is the TTL the server's own workflow facet lane holds them for;
// everything else gets the 60 s the ordinary facet lane uses. A workflow write
// clears the store outright rather than waiting out either.
//
// IT NEVER BLOCKS. A key the store cannot answer is fetched on its own, as a
// single-facet request - the card never waits on the full batch.
const TRACKER_FACET_STORE_MAX_SCOPES = 8;
const TRACKER_FACET_STORE_TTL_MS = 60000;
const TRACKER_FACET_STORE_WORKFLOW_TTL_MS = 15000;
// The shipped WORKFLOW_BACKED_FACETS set from backend/trackerSynapseApiAdapter.js.
// Held here so the browser cannot keep an overlay-derived list longer than the
// server would have served it.
const TRACKER_WORKFLOW_BACKED_FACET_KEYS = new Set(["action", "followUpDate", "followUpDue", "claimDue", "owner", "assignedTo", "assignedBy", "assignmentStatus"]);
const trackerFacetOptionStore = new Map();
// ONE CURRENT READ PER EPOCH, not one per request. portalFreshReadUrl() appends
// ?refresh=current to every tracker read once the publication epoch has moved,
// and backend/portalReadFreshness.js turns that into a full bypass of the
// server's facet memory cache. Measured against production on 2026-09-22, the
// same single-facet request costs 730 ms live and 60-129 ms from that cache.
// Demanding the bypass on EVERY facet read therefore made the server lane the
// adapter built for exactly this - generation-fenced, 15 s for overlay-derived
// facets - unreachable from the browser.
//
// The epoch is the client's own read identity: it advances on a publication
// change, on a scope change and after a local workflow write, and it is already
// folded into every facet cache key here. So the FIRST facet read at a new epoch
// still asks the server for a current answer - which is what preserves
// write-then-see - and the reads after it may be served from the server's cache,
// whose own TTL is the same 15 s the store above honours.
let trackerFacetCurrentReadEpoch = -1;

function trackerFacetStoreTtlMs(facetKey) {
  return TRACKER_WORKFLOW_BACKED_FACET_KEYS.has(String(facetKey || ""))
    ? TRACKER_FACET_STORE_WORKFLOW_TTL_MS
    : TRACKER_FACET_STORE_TTL_MS;
}

function trackerFacetResponseTtlMs(facetKey, body) {
  const fallback = trackerFacetStoreTtlMs(facetKey);
  const lifetime = body?.queryPlanSummary?.facetCacheMaxAgeMs;
  return Number.isFinite(lifetime) && lifetime >= 0 ? Math.min(fallback, lifetime) : fallback;
}

function trackerApiFacetScopeKey(query) {
  const scope = {
    publicationEpoch: typeof trackerPublicationEpoch === "number" ? trackerPublicationEpoch : 0,
    base: trackerApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: query.facetAccess || query.access || trackerAccessEnvelope(query.mode),
    mode: query.mode,
    filters: query.filters,
    slicers: query.slicers,
    columnFilters: query.columnFilters
  };
  trackerFoldFilterModeKeyFields(scope, query);
  return JSON.stringify(scope);
}

// The three maps the facet request sends and the server compiles into
// predicates. Conditional on purpose - see the scope-key note above.
function trackerFoldFilterModeKeyFields(target, query) {
  ["slicerModes", "columnFilterModes", "columnFilterRanges"].forEach((field) => {
    const value = query ? query[field] : null;
    if (value && typeof value === "object" && Object.keys(value).length) target[field] = value;
  });
  return target;
}

// A facets response deposits every WHOLE list it carried. A key the caller paged
// or searched is deliberately NOT stored: that answer is the server's reply to a
// needle, not the column's option list, and storing it would let a later reader
// open the card onto one page of somebody else's search.
function rememberTrackerFacetOptions(query, body) {
  if (!query || !body || !body.facets || typeof body.facets !== "object") return 0;
  const requested = Array.isArray(query.facets) ? query.facets.map(String).filter(Boolean) : [];
  if (!requested.length) return 0;
  const denied = new Set((Array.isArray(body.deniedFacets) ? body.deniedFacets : []).map(String));
  const paged = query.facetOptions && typeof query.facetOptions === "object" ? query.facetOptions : {};
  const scopeKey = trackerApiFacetScopeKey(query);
  const entry = trackerFacetOptionStore.get(scopeKey) || new Map();
  const at = Date.now();
  let stored = 0;
  requested.forEach((facetKey) => {
    if (Object.hasOwn(paged, facetKey)) return;
    if (denied.has(facetKey)) return;
    if (!Object.hasOwn(body.facets, facetKey)) return;
    entry.set(facetKey, {
      rows: body.facets[facetKey],
      page: (body.facetPages && body.facetPages[facetKey]) || null,
      total: (body.facetTotals && body.facetTotals[facetKey]) || null,
      notice: (body.facetNotices && body.facetNotices[facetKey]) || null,
      ttlMs: trackerFacetResponseTtlMs(facetKey, body),
      at
    });
    // The same conclusion noteTrackerFacetShape() draws from a per-card answer,
    // drawn from the batch instead: a list that arrived whole is searched here.
    if (body.facetPages && body.facetPages[facetKey]) trackerWholeFacetKeys.delete(facetKey);
    else trackerWholeFacetKeys.add(facetKey);
    stored += 1;
  });
  if (!entry.size) return 0;
  trackerFacetOptionStore.delete(scopeKey);
  trackerFacetOptionStore.set(scopeKey, entry);
  while (trackerFacetOptionStore.size > TRACKER_FACET_STORE_MAX_SCOPES) {
    trackerFacetOptionStore.delete(trackerFacetOptionStore.keys().next().value);
  }
  return stored;
}

// All of the requested keys, or nothing: a half-answered menu is not an answer.
function trackerFacetOptionsFromStore(query, nowMs = Date.now(), renewalLeadMs = 0, maxAgeMs = null) {
  if (!query) return null;
  const requested = Array.isArray(query.facets) ? query.facets.map(String).filter(Boolean) : [];
  if (!requested.length) return null;
  if (query.facetOptions && Object.keys(query.facetOptions).length) return null;
  const entry = trackerFacetOptionStore.get(trackerApiFacetScopeKey(query));
  if (!entry) return null;
  const facets = {};
  const facetPages = {};
  const facetTotals = {};
  const facetNotices = {};
  for (const facetKey of requested) {
    const held = entry.get(facetKey);
    if (!held) return null;
    // An explicit maxAgeMs replaces the freshness lifetime. Only the closed
    // slicer rail and the automatic renewal pacing pass one; every menu read
    // still requires a fresh answer.
    if (maxAgeMs !== null) {
      if (nowMs - held.at > maxAgeMs) return null;
      facets[facetKey] = held.rows;
      if (held.page) facetPages[facetKey] = held.page;
      if (held.total) facetTotals[facetKey] = held.total;
      if (held.notice) facetNotices[facetKey] = held.notice;
      continue;
    }
    if (nowMs - held.at > Math.min(trackerFacetStoreTtlMs(facetKey), held.ttlMs ?? Infinity)) return null;
    // A filtered ordinary facet can carry the workflow lane's shorter lifetime.
    // Keep at least one third of that actual lifetime before renewing it.
    const lifetimeMs = Math.min(trackerFacetStoreTtlMs(facetKey), held.ttlMs ?? Infinity);
    if (renewalLeadMs > 0 && nowMs - held.at + Math.min(renewalLeadMs, lifetimeMs * 2 / 3) > lifetimeMs) return null;
    facets[facetKey] = held.rows;
    if (held.page) facetPages[facetKey] = held.page;
    if (held.total) facetTotals[facetKey] = held.total;
    if (held.notice) facetNotices[facetKey] = held.notice;
  }
  const response = { facets };
  if (Object.keys(facetPages).length) response.facetPages = facetPages;
  if (Object.keys(facetTotals).length) response.facetTotals = facetTotals;
  if (Object.keys(facetNotices).length) response.facetNotices = facetNotices;
  return response;
}

function trackerChoiceRecordFromFacetStore(key, query, maxAgeMs = null) {
  const response = trackerFacetOptionsFromStore(query, Date.now(), 0, maxAgeMs);
  if (!response) return null;
  return { key, status: "ok", response, controller: null, timer: null, query, fromCache: true };
}

function trackerFacetReadUrl(base) {
  const url = `${base}/api/tracker/facets`;
  const epoch = typeof trackerPublicationEpoch === "number" ? trackerPublicationEpoch : 0;
  if (trackerFacetCurrentReadEpoch === epoch) return url;
  trackerFacetCurrentReadEpoch = epoch;
  return typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(url) : url;
}

// A card that opened before the batch landed is holding a request the store can
// now answer. Dropping it lets the render that follows paint from memory instead
// of waiting the read out; a paged or searched read is left alone, because the
// store never held its answer.
function releaseTrackerChoiceReadsServedFromStore() {
  Array.from(trackerChoiceReads.entries()).forEach(([slot, record]) => {
    if (!record || record.status !== "pending" || !record.query) return;
    if (!trackerFacetOptionsFromStore(record.query)) return;
    cancelTrackerChoiceRead(slot);
  });
}

const TRACKER_CORE_FACET_KEYS = Object.freeze([
  "action",
  "followUpDue",
  "claimStatus",
  "match",
  "aging",
  "serviceMonth",
  // Queue cards are always visible beside the six filters and must retain
  // complete-dataset counts even while the advanced panels stay closed.
  "queue"
]);

const TRACKER_EXPANDED_FACET_KEYS = Object.freeze([
    "tenant",
    "client",
    "orgId",
    "provider",
    "facility",
    "procedure",
    "primaryPayer",
    "secondaryPayer",
    "action",
    "match",
    "claimStatus",
    "serviceMonth",
    "followUpDue",
    "primaryPayerClass",
    "secondaryPayerClass",
    "patClassification",
    "secondaryCalc",
    "claimClassification",
    "lifecycle",
    "aging",
    "owner",
    "queue",
    "network"
]);

const TRACKER_SUMMARY_FACET_KEYS = Object.freeze(["tenant", "client", "queue"]);
function trackerFacetKeysForCurrentView(mode = state.rowMode || "service", expanded = state.slicerSize === "expanded", query = {}) {
  void mode; void expanded;
  // Preserve the measured full-panel plans until their summary shapes are proved.
  const columns = query.columnFilters || state.columnFilters || {};
  const slicers = query.slicers || state.slicers || {};
  const compatibilityPanel = ["claimId", "serviceId", "claimStatus"].some(key => Array.isArray(columns[key]) && columns[key].length)
    || (Array.isArray(slicers.primaryPayerClass) && slicers.primaryPayerClass.length);
  return Array.from(compatibilityPanel ? TRACKER_EXPANDED_FACET_KEYS : TRACKER_SUMMARY_FACET_KEYS);
}

// A closed card shows the last answer for this exact scope for up to ten
// minutes; automatic renewal (prepareTrackerFilterChoices) keeps it current.
const TRACKER_AUTOMATIC_DISPLAY_MAX_AGE_MS = 600000;
function trackerSlicerFacetReadState(key, query) {
  const facet = key === "renderingProvider" ? "provider" : key;
  const choiceQuery = trackerFacetQueryRequest({ ...query, facets: [facet] });
  const choiceKey = trackerApiFacetCacheKey(choiceQuery);
  const cached = trackerChoiceRecordFromFacetStore(choiceKey, choiceQuery, TRACKER_AUTOMATIC_DISPLAY_MAX_AGE_MS);
  if (cached) return { status: "ready", response: cached.response, facet };
  const record = trackerChoiceReads.get("slicer:" + key);
  if (!record || record.key !== choiceKey) return { status: "unloaded", facet };
  // A searched/paged record is not a whole option count for a closed card.
  if (record.query?.facetOptions && Object.keys(record.query.facetOptions).length) return { status: "unloaded", facet };
  // Whole-list reuse must pass the store freshness check above.
  if (record.status === "ok") return { status: "unloaded", facet };
  return { status: record.status === "pending" ? "loading" : "error", facet };
}

function trackerCombinedNotesFacetQuery(query) {
  if (state.view !== "workbench" || query.mode !== "service" || !query.columnFilters?.mergedNotes?.length) return null;
  const facets = trackerFacetQueryRequest(query);
  if (!facets.facets?.length || facets.facets.includes("mergedNotes")) return null;
  const key = trackerApiFacetCacheKey(facets);
  if (trackerApiFacetState.key === key && trackerApiFacetState.status === "ok" && trackerApiFacetState.response) return null;
  return facets;
}

function acceptTrackerCombinedNotesFacets(query, body, sequence, started) {
  if (!query) return false;
  const key = trackerApiFacetCacheKey(query);
  if (sequence !== trackerApiFacetSequence || key !== trackerApiCurrentFacetCacheKey(query)) return false;
  const required = TRACKER_SUMMARY_FACET_KEYS.filter(facet => query.facets.includes(facet)
    && (facet !== "queue" || !query.access?.allowedColumns || query.access.allowedColumns.includes("queue")));
  if (!body || body.error || body.decision?.allowed !== true || required.some(facet => !Array.isArray(body.facets?.[facet]))) {
    throw new Error("Filter summary is incomplete");
  }
  // The page's controller/key fence has already accepted this operation. Retire
  // only the older global summary lane; a newer menu/summary owner wins above.
  cancelQueuedTrackerApiFacetRequest();
  trackerApiFacetController?.abort();
  trackerApiFacetController = null;
  trackerApiFacetSequence += 1;
  clearTrackerApiFacetFailure();
  rememberTrackerFacetOptions(query, body);
  releaseTrackerChoiceReadsServedFromStore();
  trackerApiFacetState = { key, status: "ok", response: body, error: "",
    lastRequestMs: moneyNumber(workbenchProfileNow() - started),
    staleResponses: trackerApiFacetState.staleResponses || 0, lastStaleAt: trackerApiFacetState.lastStaleAt || "" };
  renderServerFacetControls(body);
  return true;
}

function requestTrackerApiPage(query, key = trackerApiCacheKey(query)) {
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") return;
  // ONE REQUEST PER KEY IN FLIGHT. A second caller asking for the page that is
  // already on the wire used to get a second identical POST and overwrite the
  // first requests controller, so the first answer landed as "stale" and was
  // thrown away after the server had already paid for it. The key IS the
  // request: same filters, same sort, same page, same scope. Nothing here
  // loosens the fence - a changed key still aborts and re-asks on the next line.
  if (trackerApiPageState.status === "pending" && trackerApiPageState.key === key && trackerApiPageState.controller) return;
  if (trackerApiPageState.status === "pending" && trackerApiPageState.key !== key) abortPendingTrackerApiPageRequest();
  const requestStartedMs = workbenchProfileNow();
  const controller = new AbortController();
  const stableKey = trackerApiStableCacheKey(query);
  const previousResponse = trackerApiPageState.response || trackerApiPageState.previousResponse || null;
  const previousStableKey = trackerApiPageState.stableKey || trackerApiPageState.previousStableKey || "";
  const canPreservePrevious = Boolean(previousResponse && previousStableKey === stableKey);
  trackerApiPageState = trackerApiInitialState({
    key,
    stableKey,
    status: "pending",
    controller,
    previousResponse: canPreservePrevious ? previousResponse : null,
    previousStableKey: canPreservePrevious ? stableKey : "",
    lastRequestMs: 0,
    staleResponses: trackerApiPageState.staleResponses || 0,
    lastStaleAt: trackerApiPageState.lastStaleAt || ""
  });
  updateDataSourceBox();
  const token = trackerApiToken();
  const timeoutId = window.setTimeout(() => controller.abort(), 45000);
  const combinedFacets = typeof trackerCombinedNotesFacetQuery === "function" ? trackerCombinedNotesFacetQuery(query) : null;
  const combinedFacetSequence = combinedFacets ? trackerApiFacetSequence : null;
  const requestBody = combinedFacets ? { ...query, includeSummaryFacets: true, summaryFacetKeys: combinedFacets.facets } : query;
  fetch((typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(`${base}/api/tracker/query`) : `${base}/api/tracker/query`), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(requestBody),
    signal: controller.signal
  })
    .then(async (response) => {
      // Keep the deadline active while the response body is still arriving.
      const body = await response.json();
      window.clearTimeout(timeoutId);
      // Reset/Apply can replace a request without changing its cache key.
      // A retired completion must never publish into the replacement owner.
      if (trackerApiPageState.controller !== controller || trackerApiPageState.key !== key || trackerApiCurrentCacheKey(query) !== key) {
        recordStaleTrackerApiResponse(key);
        return;
      }
      // A TYPED SCOPE DENIAL IS NOT A CONNECTION PROBLEM (2026-08-03).
      // scopeDenialResponse() sets scopeDenial:true precisely so the UI can tell
      // "you are not allowed" from "the request failed". Landing it in `error`
      // printed "This is a connection problem, not an empty worklist" over a
      // permissions decision, offered a Retry that can never succeed, and burned
      // the one automatic retry on a deterministic 403.
      if (response.status === 403 && body && body.scopeDenial === true) {
        trackerApiPageState = trackerApiInitialState({
          key,
          stableKey,
          status: "denied",
          scopeDenial: { code: body.reason || "", title: body.title || "", message: body.message || "", remedy: body.remedy || "" },
          error: "",
          lastRequestMs: moneyNumber(workbenchProfileNow() - requestStartedMs),
          staleResponses: trackerApiPageState.staleResponses || 0,
          lastStaleAt: trackerApiPageState.lastStaleAt || ""
        });
        updateDataSourceBox();
        renderClaimsTable();
        return;
      }
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        const pendingPreviousResponse = trackerApiPageState.previousResponse || null;
        const pendingPreviousStableKey = trackerApiPageState.previousStableKey || "";
        trackerApiPageState = trackerApiInitialState({
          key,
          stableKey,
          status: "error",
          error: "Your session expired. Sign in again — this page will still be here.",
          previousResponse: pendingPreviousResponse,
          previousStableKey: pendingPreviousStableKey,
          lastRequestMs: moneyNumber(workbenchProfileNow() - requestStartedMs),
          staleResponses: trackerApiPageState.staleResponses || 0,
          lastStaleAt: trackerApiPageState.lastStaleAt || ""
        });
        updateDataSourceBox();
        renderClaimsTable();
        return;
      }
      if (!response.ok || body.error) {
        const pendingPreviousResponse = trackerApiPageState.previousResponse || null;
        const pendingPreviousStableKey = trackerApiPageState.previousStableKey || "";
        trackerApiPageState = trackerApiInitialState({
          key,
          stableKey,
          status: "error",
          error: body?.error?.message || body.message || `HTTP ${response.status}`,
          previousResponse: pendingPreviousResponse,
          previousStableKey: pendingPreviousStableKey,
          lastRequestMs: moneyNumber(workbenchProfileNow() - requestStartedMs),
          staleResponses: trackerApiPageState.staleResponses || 0,
          lastStaleAt: trackerApiPageState.lastStaleAt || ""
        });
        console.warn("[tracker] page query failed:", trackerApiPageState.error);
        updateDataSourceBox();
        renderClaimsTable();
        return;
      }
      if (combinedFacets) acceptTrackerCombinedNotesFacets(combinedFacets, body.summaryFacets, combinedFacetSequence, requestStartedMs);
      const pageOnlyNavigation = Boolean(trackerApiPageState.previousResponse && trackerApiPageState.previousStableKey === stableKey);
      trackerApiPageState = trackerApiInitialState({
        key,
        stableKey,
        status: "ok",
        response: body,
        previousResponse: null,
        previousStableKey: "",
        lastRequestMs: moneyNumber(workbenchProfileNow() - requestStartedMs),
        staleResponses: trackerApiPageState.staleResponses || 0,
        lastStaleAt: trackerApiPageState.lastStaleAt || ""
      });
      reconcileFollowUpOverlayFromApiRows(body.rows || []);
      updateDataSourceBox();
      // Publication revalidation also retains the previous page, but may have
      // invalidated/cancelled its facets. Only skip a ready current facet lane.
      const currentFacetsReady = trackerApiFacetState.status === "ok" && trackerApiFacetState.response
        && trackerApiFacetState.key === trackerApiCurrentFacetCacheKey(trackerFacetQueryRequest(query));
      if (!pageOnlyNavigation || !currentFacetsReady) renderSlicers();
      renderClaimsTable({ deferPagePanels: pageOnlyNavigation });
    })
    .catch((error) => {
      window.clearTimeout(timeoutId);
      if (trackerApiPageState.controller !== controller || trackerApiPageState.key !== key || trackerApiCurrentCacheKey(query) !== key) {
        if (error && error.name === "AbortError") return;
        recordStaleTrackerApiResponse(key);
        return;
      }
      const pendingPreviousResponse = trackerApiPageState.previousResponse || null;
      const pendingPreviousStableKey = trackerApiPageState.previousStableKey || "";
      trackerApiPageState = trackerApiInitialState({
        key,
        stableKey,
        status: "error",
        error: error && error.name === "AbortError" ? "Request timed out — try narrowing filters." : error.message,
        previousResponse: pendingPreviousResponse,
        previousStableKey: pendingPreviousStableKey,
        staleResponses: trackerApiPageState.staleResponses || 0,
        lastStaleAt: trackerApiPageState.lastStaleAt || ""
      });
      console.warn("[tracker] page query failed:", trackerApiPageState.error);
      updateDataSourceBox();
      renderClaimsTable();
    })
    // Whichever way the page read settled - rows, scope denial, expired
    // session, HTTP error, timeout - the grid has had its turn and the boot
    // chrome that stood aside for it may go.
    .finally(() => { releaseFirstGridPaintWork(); });
}

let trackerApiFacetController = null;
let trackerApiFacetSequence = 0;
function requestTrackerApiFacets(query, key = trackerApiFacetCacheKey(query)) {
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function") return;
  trackerApiFacetController?.abort();
  const controller = new AbortController();
  trackerApiFacetController = controller;
  const requestId = ++trackerApiFacetSequence;
  const timeoutId = window.setTimeout(() => controller.abort(), 45000);
  const requestStartedMs = workbenchProfileNow();
  trackerApiFacetState = {
    key,
    status: "pending",
    response: trackerApiFacetState.response || null,
    error: "",
    lastRequestMs: 0,
    staleResponses: trackerApiFacetState.staleResponses || 0,
    lastStaleAt: trackerApiFacetState.lastStaleAt || ""
  };
  const token = trackerApiToken();
  return fetch(trackerFacetReadUrl(base), {
    method: "POST",
    signal: controller.signal,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(query)
  })
    .then(async (response) => {
      const body = await response.json();
      if (requestId !== trackerApiFacetSequence || trackerApiFacetState.key !== key || trackerApiCurrentFacetCacheKey(query) !== key) {
        recordStaleTrackerApiFacetResponse(key);
        return;
      }
      // Same gate, same denial: a denied principal's facet request 403s too, and
      // rendering that as a silent empty slicer rail is the same lie one rail over.
      if (response.status === 403 && body && body.scopeDenial === true) {
        trackerApiFacetState = {
          key,
          status: "denied",
          response: null,
          error: "",
          scopeDenial: { code: body.reason || "", title: body.title || "", message: body.message || "", remedy: body.remedy || "" },
          lastRequestMs: moneyNumber(workbenchProfileNow() - requestStartedMs),
          staleResponses: trackerApiFacetState.staleResponses || 0,
          lastStaleAt: trackerApiFacetState.lastStaleAt || ""
        };
        renderSlicers();
        return;
      }
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        noteTrackerApiFacetFailure();
        trackerApiFacetState = {
          key,
          status: "error",
          response: trackerApiFacetState.response || null,
          error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body?.error?.message || body.message || `HTTP ${response.status}`),
          lastRequestMs: moneyNumber(workbenchProfileNow() - requestStartedMs),
          staleResponses: trackerApiFacetState.staleResponses || 0,
          lastStaleAt: trackerApiFacetState.lastStaleAt || ""
        };
        renderSlicers();
        return;
      }
      const requiredSummary = TRACKER_SUMMARY_FACET_KEYS.filter(facet => query.facets.includes(facet)
        && (facet !== "queue" || !query.access?.allowedColumns || query.access.allowedColumns.includes("queue")));
      if (requiredSummary.some(facet => !Array.isArray(body.facets?.[facet]))) throw new Error("Filter summary is incomplete");
      clearTrackerApiFacetFailure();
      // EVERY CARD THIS BATCH ANSWERED IS NOW IN HAND. Deposited before the
      // renders below, so the slicer rail and an open header menu paint from
      // memory in the same turn instead of each issuing their own read.
      rememberTrackerFacetOptions(query, body);
      releaseTrackerChoiceReadsServedFromStore();
      trackerApiFacetState = {
        key,
        status: "ok",
        response: body,
        error: "",
        lastRequestMs: moneyNumber(workbenchProfileNow() - requestStartedMs),
        staleResponses: trackerApiFacetState.staleResponses || 0,
        lastStaleAt: trackerApiFacetState.lastStaleAt || ""
      };
      renderServerFacetControls(body);
      if (state?.view === "feeSchedules") renderFeeSchedules();
      if (state?.view === "reports") renderReports();
      // Live-refresh an open column-filter popup when its dataset-wide facet
      // lands (first open used to show "No values" until a manual reopen).
      // Runs before renderSlicers on purpose: at this point the facet slot is
      // "ok" for the menu's own query, so the re-render hits the cache instead
      // of re-fetching, and the landed-key guard keeps slicer facet responses
      // from ping-ponging the single facet slot through the open menu.
      if (openColumnFilterContext && els.columnFilterMenu && !els.columnFilterMenu.hidden) {
        try {
          const menuFacetQuery = trackerFacetQueryRequest(buildColumnFilterFacetQuery(openColumnFilterContext.key));
          // ...or when the batch just deposited the menu's own facet for the
          // menu's own scope, which is the ordinary case for a column carrying
          // no filter of its own: the reopen then costs zero requests.
          if (trackerApiFacetCacheKey(menuFacetQuery) === key || trackerFacetOptionsFromStore(menuFacetQuery)) {
            openColumnFilterMenu(openColumnFilterContext.key, openColumnFilterContext.button);
          }
        } catch (_) {}
      }
      renderSlicers();
    })
    .catch((error) => {
      if (requestId !== trackerApiFacetSequence || trackerApiFacetState.key !== key || trackerApiCurrentFacetCacheKey(query) !== key) {
        recordStaleTrackerApiFacetResponse(key);
        return;
      }
      noteTrackerApiFacetFailure();
      trackerApiFacetState = {
        key,
        status: "error",
        response: null,
        error: error.name === "AbortError" ? "Filter choices took too long to load. Reopen the filter to retry." : error.message,
        lastRequestMs: moneyNumber(workbenchProfileNow() - requestStartedMs),
        staleResponses: trackerApiFacetState.staleResponses || 0,
        lastStaleAt: trackerApiFacetState.lastStaleAt || ""
      };
      renderSlicers();
    }).finally(() => {
      window.clearTimeout(timeoutId);
      if (trackerApiFacetController === controller) trackerApiFacetController = null;
    });
}

function recordStaleTrackerApiFacetResponse(staleKey) {
  trackerApiFacetState.staleResponses = Number(trackerApiFacetState.staleResponses || 0) + 1;
  trackerApiFacetState.lastStaleAt = timestamp();
  state.audit = Array.isArray(state.audit) ? state.audit : [];
  state.audit.push({
    at: trackerApiFacetState.lastStaleAt,
    by: "Portal",
    text: `Ignored stale tracker facet response for superseded query ${String(staleKey || "").slice(0, 32)}.`
  });
  state.audit = state.audit.slice(-200);
  saveState();
}

function recordStaleTrackerApiResponse(staleKey) {
  trackerApiPageState.staleResponses = Number(trackerApiPageState.staleResponses || 0) + 1;
  trackerApiPageState.lastStaleAt = timestamp();
  state.audit = Array.isArray(state.audit) ? state.audit : [];
  state.audit.push({
    at: trackerApiPageState.lastStaleAt,
    by: "Portal",
    text: "Ignored an outdated response after the view changed."
  });
  state.audit = state.audit.slice(-200);
  saveState();
  recoverStaleTrackerApiPage(staleKey);
}

function recoverStaleTrackerApiPage(staleKey) {
  if (!trackerApiEnabled()) return;
  if (trackerApiPageState.key !== staleKey || trackerApiPageState.status !== "pending") return;
  let currentKey = "";
  try {
    currentKey = trackerApiCacheKey(buildTrackerQueryRequest(state.rowMode || "service"));
  } catch (_) {
    return;
  }
  if (!currentKey || currentKey === staleKey) return;
  try { renderClaimsTable(); } catch (_) {}
}

function trackerApiCacheKey(query) {
  return JSON.stringify({
    publicationEpoch: typeof trackerPublicationEpoch === "number" ? trackerPublicationEpoch : 0,
    base: trackerApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: query.access || trackerAccessEnvelope(query.mode),
    mode: query.mode,
    page: query.page,
    sort: query.sort || [],
    filters: query.filters,
    slicers: query.slicers,
    columnFilters: query.columnFilters,
    columns: query.responseShape?.columns || []
  });
}

function trackerApiCurrentCacheKey(query) {
  return trackerApiCacheKey({
    ...query,
    access: trackerAccessEnvelope(query.mode)
  });
}

function trackerApiStableCacheKey(query) {
  return JSON.stringify({
    base: trackerApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: query.access || trackerAccessEnvelope(query.mode),
    mode: query.mode,
    sort: query.sort || [],
    filters: query.filters,
    slicers: query.slicers,
    columnFilters: query.columnFilters,
    columns: query.responseShape?.columns || []
  });
}

function trackerApiCurrentStableCacheKey(query) {
  return trackerApiStableCacheKey({
    ...query,
    access: trackerAccessEnvelope(query.mode)
  });
}

function trackerApiFacetCacheKey(query) {
  // The mode and range maps ride along conditionally; see trackerApiFacetScopeKey.
  return JSON.stringify(trackerFoldFilterModeKeyFields({
    publicationEpoch: typeof trackerPublicationEpoch === "number" ? trackerPublicationEpoch : 0,
    base: trackerApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: query.facetAccess || query.access || trackerAccessEnvelope(query.mode),
    mode: query.mode,
    filters: query.filters,
    slicers: query.slicers,
    columnFilters: query.columnFilters,
    facets: query.facets || [],
    facetOptions: query.facetOptions || {}
  }, query));
}

function trackerApiCurrentFacetCacheKey(query) {
  return trackerApiFacetCacheKey({
    ...query,
    ...(query.facetAccess ? { facetAccess: trackerAccessEnvelope(query.mode, true) } : {}),
    access: trackerAccessEnvelope(query.mode)
  });
}

function serverFacetTotalRows(serverFacets = {}) {
  const total = serverFacets.facetTotals?.tenant || serverFacets.facetTotals?.client || serverFacets.facetTotals?.queue;
  if (total && Number.isFinite(Number(total.count))) return nonNegativeNumber(total.count, 0);
  const tenantRows = serverFacets.facets?.tenant || serverFacets.facets?.client || [];
  return tenantRows.reduce((sum, row) => sum + nonNegativeNumber(row.count, 0), 0);
}

function serverFacetTotalOwed(serverFacets = {}) {
  const total = serverFacets.facetTotals?.tenant || serverFacets.facetTotals?.client || serverFacets.facetTotals?.queue;
  if (total && Number.isFinite(Number(total.owed))) return moneyNumber(total.owed);
  const tenantRows = serverFacets.facets?.tenant || serverFacets.facets?.client || [];
  return moneyNumber(tenantRows.reduce((sum, row) => sum + moneyNumber(row.owed || 0), 0));
}

function trackerApiEnabled() {
  return trackerApiMode() === "api" && Boolean(trackerApiBase());
}

function trackerApiMode() {
  return String(window.TMS_TRACKER_API_MODE || safeLocalStorageGet(TRACKER_API_MODE_KEY) || "local").toLowerCase();
}

function trackerApiBase() {
  const configured = window.TMS_TRACKER_API_BASE || safeLocalStorageGet(TRACKER_API_BASE_KEY) || workflowApiBase();
  return String(configured || "").replace(/\/$/, "");
}

function trackerApiToken() {
  return window.TMS_TRACKER_API_TOKEN || safeLocalStorageGet(TRACKER_API_TOKEN_KEY) || workflowApiToken();
}

// Publication checks observe the published warehouse book, not upstream ingestion.
// Check only after an explicit button click; idle pages never refresh themselves.
// Nothing here stamps data as fresh, writes business data, or restarts an export.
const TRACKER_ACTIVE_REVALIDATE_MS = 60000;
let trackerPublicationWatch = { timer: null, listeners: false, scope: "", observedAt: 0, pendingAt: 0, lastRevalidatedAt: 0, appliedAt: 0, checkedAt: 0, forceRevalidate: false, controller: null, renderGuard: false, deferredViews: new Set(), wakeTimer: null, explicitRenderUntil: 0 };

function portalFreshReadUrl(url) {
  const value = String(url || "");
  if (!trackerPublicationEpoch || /[?&]refresh=/.test(value)) return value;
  // Stable explicit freshness signal, authenticated/authorized by the existing
  // route. Never a token, timestamp cache-buster or change to a running job.
  return value + (value.includes("?") ? "&" : "?") + "refresh=current";
}

function trackerPublicationScope() {
  if (!portalAppBooted || !trackerApiEnabled() || !trackerApiToken() || !state.authUserScope) return "";
  // In-memory only: the bearer is used solely to reject an old-session reply.
  // Column visibility is request shape, not a change of authenticated scope.
  // Select all starts its new page before this bubbling click check runs;
  // keying on visible columns erased that read without scheduling another.
  // Keep the full role-permitted set and every actual access/session field.
  // The display name is presentation: loading Manage Users replaces the
  // "Platform Admin" placeholder with the signed-in person's name, and keying on
  // it made the next click reset every read (Manage Users then showed
  // "Directory unavailable" / "Your access changed"). The bearer is the identity.
  const { user: _displayName, ...envelope } = trackerAccessEnvelope("service");
  const access = { ...envelope, allowedColumns: permittedColumnsForMode("service").map((column) => column.key) };
  return JSON.stringify({ base: trackerApiBase(), reportBase: reportApiBase(), token: trackerApiToken(), reportToken: reportApiToken(), role: state.role, access });
}

function trackerPublicationCanCheck() {
  return document.visibilityState !== "hidden" && !document.querySelector(".portal-login-gate") && Boolean(trackerPublicationScope());
}

function trackerPublicationInteractionActive() {
  const focused = document.activeElement;
  return Boolean(followUpEditorState || claimNoteMutationState || foregroundModalSession
    || (typeof slicerDraftKey !== "undefined" && slicerDraftKey)
    || (typeof openColumnFilterContext !== "undefined" && openColumnFilterContext)
    || statementActionState.busy || statementPayState.open || statementBrandingEditor.open
    || (typeof additionalPortalDataInteractionActive === "function" && additionalPortalDataInteractionActive())
    || (state.view === "workbench" && (state.selectedClaims || []).length)
    || Array.from(document.querySelectorAll('.drawer.open, dialog[open], [aria-modal="true"], .modal-overlay')).some((node) => node.getClientRects().length > 0)
    || (focused && /^(INPUT|TEXTAREA|SELECT)$/.test(focused.tagName))
    || focused?.isContentEditable
    || (state.view === "reports" && (hostedReportDrillState.root || reportPaymentEvidenceState.view)));
}

function trackerPublicationRequestsPending() {
  // An obsolete inactive read must not hold the visible page forever. Epoch
  // and request-id guards discard that reply when its cache is invalidated.
  const view = state.view;
  const reportView = reportArState.view;
  const reportSlots = reportView === "collections" ? [reportCollectionsState]
    : reportView === "turnaround" ? [reportTurnaroundState]
    : reportView === "clientPayments" ? [clientPaymentChecksApiState]
    : /^client/.test(reportView) ? [clientReportAggregateApiState, clientReportClaimsApiState, clientReportServerDrillState]
    : reportView === "workQueue" ? [reportApiState, reportQueueDrilldownState]
    : [reportDatasetState, preparedSnapshotReportState, reportFilterFacetReadState];
  const slots = view === "workbench" ? [trackerApiPageState, trackerApiFacetState]
    : view === "trackerExport" ? [trackerExportFacetState, trackerExportCountState]
    : view === "reports" ? reportSlots : [];
  return slots.some((slot) => slot.status === "pending")
    || (view === "statements" && statementPreviewState.status === "loading")
    || (view === "authorizations" && authorizationsState.status === "loading")
    || (view === "reports" && reportView === "clientPayments"
      && Array.from(clientPaymentCheckDetailCache.values()).some((slot) => slot.status === "pending"))
    || (view === "reports" && preparedSnapshotReportState.view === reportView
      && preparedSnapshotReportState.response?.claimPage?.inFlight === true)
    || (typeof additionalPortalDataRequestsPending === "function" && additionalPortalDataRequestsPending());
}

function queueTrackerPublicationApply() {
  if (trackerPublicationWatch.wakeTimer !== null || (!trackerPublicationWatch.pendingAt && !trackerPublicationWatch.deferredViews.size)) return;
  trackerPublicationWatch.wakeTimer = window.setTimeout(() => { trackerPublicationWatch.wakeTimer = null; applyPendingTrackerPublication(); }, 0);
}

function deferTrackerPublicationRender(view) {
  if (view === "reports" && reportReadContextCurrent(reportReadContext)) return false;
  // A completed read already calls its renderer: wake a deferred publication
  // after that paint, instead of making the reader wait another polling cycle.
  queueTrackerPublicationApply();
  if (!trackerPublicationWatch.renderGuard) return false;
  // A filter/input event is an explicit request to repaint. Preserve editor and
  // modal guards for background publication changes, while allowing the user
  // action that caused this renderer to complete immediately.
  if (Date.now() <= trackerPublicationWatch.explicitRenderUntil) {
    trackerPublicationWatch.deferredViews.delete(view);
    return false;
  }
  if (!trackerPublicationCanCheck() || trackerPublicationInteractionActive()) {
    trackerPublicationWatch.deferredViews.add(view);
    return true;
  }
  trackerPublicationWatch.deferredViews.delete(view);
  return false;
}

function renderTrackerPublicationView() {
  if (typeof renderAdditionalPortalDataView === "function") renderAdditionalPortalDataView(state.view);
  if (state.view === "workbench") { renderSlicers(); renderClaimsTable(); }
  else if (state.view === "trackerExport") renderTrackerExportStudio();
  else if (state.view === "reports") renderReports();
  else if (state.view === "statements") renderStatements();
  else if (state.view === "authorizations") renderAuthorizations();
}

function applyPendingTrackerPublication() {
  if (reportReadSurface()) { renderReportReadStatus(); return false; }
  if (!trackerPublicationCanCheck() || trackerPublicationScope() !== trackerPublicationWatch.scope
    || trackerPublicationInteractionActive() || trackerPublicationRequestsPending()) return false;
  if (!trackerPublicationWatch.pendingAt) {
    if (trackerPublicationWatch.deferredViews.size) {
      trackerPublicationWatch.deferredViews.clear();
      renderTrackerPublicationView();
    }
    if (!trackerPublicationRequestsPending()) trackerPublicationWatch.renderGuard = false;
    return false;
  }
  const publicationAt = trackerPublicationWatch.pendingAt;
  const preparedSessionBeforePublication = preparedSnapshotSessionKey();
  trackerPublicationEpoch += 1;
  trackerPublicationWatch.lastRevalidatedAt = Date.now();
  trackerPublicationWatch.appliedAt = Date.now();
  trackerPublicationWatch.pendingAt = 0;
  trackerPublicationWatch.renderGuard = true;
  // Retain the same-page response for the established stale-while-revalidate
  // path. Page, filters, selected columns and all editor drafts stay untouched.
  const pageAt = Date.parse(trackerApiPageState.response?.queryPlanSummary?.refreshedAtUtc || "");
  if (!Number.isFinite(pageAt) || pageAt < publicationAt) {
    resetTrackerApiAutoRetry();
    trackerApiPageState = trackerApiInitialState({ response: trackerApiPageState.response, stableKey: trackerApiPageState.stableKey });
  }
  invalidateTrackerApiFacetsAfterWorkflowWrite();
  clearTrackerApiFacetFailure();
  window.clearTimeout(trackerExportFacetTimer);
  trackerExportFacetState = { key: "", status: "idle", facets: {}, error: "" };
  trackerExportCountState = { key: "", status: "idle", rowCount: 0, claimCount: 0, error: "" };
  // An in-flight workbook keeps its captured preview and independent job record.
  if (state.trackerWorkbookExportPreview?.status !== "pending" && state.trackerWorkbookExportJob?.status !== "building") state.trackerWorkbookExportPreview = null;
  // These caches use the same warehouse source; payment/detail controls remain intact.
  clientPaymentChecksApiState = { key: "", status: "idle", response: null, error: "" };
  clientPaymentCheckDetailCache.clear();
  clientReportClaimsApiState = { key: "", status: "idle", rows: [], totalRows: 0, truncated: false, error: "", retried: false };
  clientReportAggregateRequestSequence += 1;
  // Keep the page number, but do not send an old-generation cursor to its new book.
  clientReportAggregatePageIdentity = "";
  clientReportAggregateApiState = { key: "", status: "idle", response: null, error: "" };
  clientReportDrillRequestSequence += 1;
  clientReportServerDrillState = { ...clientReportServerDrillState, key: "", status: "idle", response: null, error: "" };
  if (typeof statementsPrepareAutomaticSelectionRefresh === "function") statementsPrepareAutomaticSelectionRefresh();
  Object.assign(statementPreviewState, { key: "", status: "idle", data: null, error: "", requestId: (statementPreviewState.requestId || 0) + 1 });
  if (typeof resetAuthorizationBoardReadState === "function") resetAuthorizationBoardReadState();
  resetReportDataset();
  reportFilterFacetReadState = { key: "", status: "idle", response: null, error: "" };
  reportFilterFacetRequestSequence += 1;
  reportRollupState = { key: null, byGroup: {} };
  preparedSnapshotReportState = {
    ...preparedSnapshotReportState,
    page: preparedSnapshotReportState.response?.groupPage?.page || preparedSnapshotReportState.page || 1,
    claimPageNumber: preparedSnapshotReportState.response?.claimPage?.page || preparedSnapshotReportState.claimPageNumber || 1,
    sessionKey: preparedSnapshotReportState.sessionKey === preparedSessionBeforePublication ? preparedSnapshotSessionKey() : preparedSnapshotReportState.sessionKey,
    key: "", status: "idle", response: null, error: "", requestId: preparedSnapshotReportState.requestId + 1
  };
  reportCollectionsState = { key: "", status: "idle", response: null, error: "", code: "" };
  reportTurnaroundState = { key: "", status: "idle", response: null, error: "", code: "", httpStatus: 0 };
  reportApiState = { key: "", status: "idle", response: null, error: "" };
  reportQueueDrilldownState = { key: "", status: "idle", response: null, error: "", request: null };
  if (typeof refreshAdditionalPortalDataView === "function") refreshAdditionalPortalDataView(state.view);
  renderTrackerPublicationView();
  if (typeof updateLastSyncedIndicator === "function") { try { updateLastSyncedIndicator(); } catch (_) {} }
  return true;
}

async function checkTrackerPublication() {
  if (reportReadSurface()) { trackerPublicationWatch.forceRevalidate = false; syncReportReadContext(); renderReportReadStatus(); return; }
  if (!trackerPublicationCanCheck()) return;
  const scope = trackerPublicationScope();
  if (trackerPublicationWatch.scope !== scope) {
    const replacingScope = Boolean(trackerPublicationWatch.scope);
    trackerPublicationWatch.controller?.abort();
    // Boot has already started the first scoped page; do not obsolete it.
    if (trackerPublicationWatch.scope) {
      trackerPublicationEpoch += 1;
      trackerApiPageState = trackerApiInitialState();
      invalidateTrackerApiFacetsAfterWorkflowWrite();
      [trackerExportFacetState, trackerExportCountState, reportDatasetState, preparedSnapshotReportState,
        reportFilterFacetReadState, reportCollectionsState, reportTurnaroundState, reportApiState,
        reportQueueDrilldownState, clientPaymentChecksApiState, clientReportClaimsApiState, clientReportAggregateApiState,
        clientReportServerDrillState, statementPreviewState].forEach((slot) => { slot.key = ""; slot.status = "idle"; });
      reportReadControllers.forEach((pending) => pending.abort());
      reportReadControllers.clear();
      reportRollupState = { key: null, byGroup: {} };
      clientPaymentCheckDetailCache.clear();
      if (typeof resetAdditionalPortalDataReadState === "function") resetAdditionalPortalDataReadState();
      if (typeof resetAuthorizationBoardReadState === "function") resetAuthorizationBoardReadState();
    }
    trackerPublicationWatch.scope = scope;
    trackerPublicationWatch.observedAt = 0;
    trackerPublicationWatch.pendingAt = 0;
    trackerPublicationWatch.lastRevalidatedAt = Date.now();
    trackerPublicationWatch.controller = null;
    trackerPublicationWatch.renderGuard = false;
    trackerPublicationWatch.deferredViews.clear();
    // A replacement principal invalidates reads already started by the click.
    // Schedule its newly fenced read instead of leaving every slot idle.
    if (replacingScope) renderTrackerPublicationView();
  }
  // Overlay and payment updates need not advance the tracker publication.
  // A button click may revalidate the covered active view after a minute, keeping
  // editor/concurrency guards. Elapsed time alone never schedules a check.
  // The pill's Update button is an explicit request: it skips the one-minute
  // spacing and marks anything older than now as due (a first click on a
  // long-open tab otherwise only records a baseline and reloads nothing).
  const forced = trackerPublicationWatch.forceRevalidate;
  const forcedAt = forced ? Date.now() : 0;
  trackerPublicationWatch.forceRevalidate = false;
  // On a view outside that list Update still invalidates the shared caches, so
  // the pill and the next covered view read the current book.
  if ((forced || ((["workbench", "trackerExport", "reports", "statements", "authorizations"].includes(state.view)
      || (typeof additionalPortalDataViewSupported === "function" && additionalPortalDataViewSupported(state.view)))
    && Date.now() - trackerPublicationWatch.lastRevalidatedAt >= TRACKER_ACTIVE_REVALIDATE_MS))
    && !trackerPublicationWatch.pendingAt) trackerPublicationWatch.pendingAt = forced ? Date.now() : (trackerPublicationWatch.observedAt || 1);
  applyPendingTrackerPublication();
  if (trackerPublicationWatch.controller) return;
  const controller = new AbortController();
  trackerPublicationWatch.controller = controller;
  const timeout = window.setTimeout(() => controller.abort(), 10000);
  try {
    const response = await fetch(trackerApiBase() + "/api/health", { cache: "no-store", signal: controller.signal });
    const body = await response.json();
    if (controller.signal.aborted || !trackerPublicationCanCheck() || trackerPublicationScope() !== scope || trackerPublicationWatch.controller !== controller) return;
    const freshness = body?.snapshotFreshness;
    const publicationAt = Date.parse(freshness?.refreshedAt || "");
    if (!response.ok || !["fresh", "stale"].includes(freshness?.state) || !Number.isFinite(publicationAt)) return;
    trackerPublicationWatch.checkedAt = Date.now();
    // THE FIRST OBSERVATION RECORDS THE BASELINE. IT DOES NOT ANNOUNCE A PUBLICATION.
    // It used to set pendingAt, so the first /api/health answer of every cold page
    // scheduled an invalidation of reads that had been issued against that exact
    // snapshot moments earlier. applyPendingTrackerPublication politely waits for
    // those reads to land (trackerPublicationRequestsPending), then bumps the epoch,
    // resets every report slot to idle and re-renders - so the report the reader had
    // just received was thrown away ON ARRIVAL and fetched a second time, and the
    // second read queued behind the rest of the page.
    //
    // Measured against production on 2026-09-22 (Reports > Cash > Incoming Payments,
    // SuperAdmin, three cold loads): /api/client/payments/checks answered 200 OK with
    // the complete page in 524 / 760 / 1367 ms, that answer was discarded, and the
    // forced second read took 15.9 s / 20.0 s / 44.2 s - the whole time showing
    // "Please hold while we load your payments." over four $0.00 tiles. The same
    // bump is what made every All Data load issue its page query twice.
    //
    // Nothing is cached before a first observation, so a baseline-only first
    // observation loses no invalidation: the scope-change branch above and
    // stopTrackerPublicationWatcher both bump the epoch and clear the slots
    // themselves, and every later observation with a strictly newer publication
    // still schedules the apply exactly as before.
    if (!trackerPublicationWatch.observedAt) {
      trackerPublicationWatch.observedAt = publicationAt;
    } else if (publicationAt > trackerPublicationWatch.observedAt) {
      trackerPublicationWatch.observedAt = publicationAt;
      // An Update that already applied re-read the current book; reloading
      // again for the same publication would fetch every view twice.
      if (!(forcedAt && trackerPublicationWatch.appliedAt >= forcedAt)) trackerPublicationWatch.pendingAt = publicationAt;
    }
    applyPendingTrackerPublication();
  } catch (_) { /* Unknown health is never presented as proof of fresh data. */ }
  finally {
    window.clearTimeout(timeout);
    if (trackerPublicationWatch.controller === controller) trackerPublicationWatch.controller = null;
    if (typeof updateLastSyncedIndicator === "function") { try { updateLastSyncedIndicator(); } catch (_) {} }
  }
}

function stopTrackerPublicationWatcher() {
  if (trackerPublicationWatch.scope) trackerPublicationEpoch += 1;
  if (trackerPublicationWatch.timer !== null) window.clearInterval(trackerPublicationWatch.timer);
  window.clearTimeout(trackerPublicationWatch.wakeTimer);
  trackerPublicationWatch.controller?.abort();
  Object.assign(trackerPublicationWatch, { timer: null, scope: "", observedAt: 0, pendingAt: 0, appliedAt: 0, checkedAt: 0, forceRevalidate: false, controller: null, renderGuard: false, wakeTimer: null, explicitRenderUntil: 0 });
  trackerPublicationWatch.deferredViews.clear();
}

function startTrackerPublicationWatcher() {
  if (trackerPublicationWatch.listeners || !trackerPublicationScope()) return;
  trackerPublicationWatch.listeners = true;
  const noteExplicitRender = (event) => {
    const control = event.target?.closest?.("input, select, textarea, [contenteditable=true]");
    if (!control) return;
    trackerPublicationWatch.explicitRenderUntil = Date.now() + 1000;
    queueTrackerPublicationApply();
  };
  document.addEventListener("input", noteExplicitRender, true);
  document.addEventListener("change", noteExplicitRender, true);
  document.addEventListener("click", (event) => {
    const button = event.target?.closest?.('button, [role="button"]');
    if (!button || button.disabled || button.getAttribute("aria-disabled") === "true") return;
    if (button.id === "lastSyncedUpdate") {
      if (reportReadSurface()) { if (typeof loadLatestReportData === "function") loadLatestReportData(); }
      else trackerPublicationWatch.forceRevalidate = true;
    }
    void checkTrackerPublication();
    if (button.id === "lastSyncedUpdate" && typeof updateLastSyncedIndicator === "function") updateLastSyncedIndicator();
  });
}

function trackerApiStatusSummary() {
  if (!trackerApiEnabled()) return { tone: "fallback", label: "Information unavailable" };
  if (trackerApiPageState.status === "ok") {
    // A LIST THAT COULD NOT READ ITS FOLLOW-UPS IS NOT A FINISHED DAY'S WORK.
    //
    // On the warehouse lane the follow-up overlay lives on a different server and is
    // loaded before the query. When that read failed the request still answered HTTP
    // 200 and the follow-up predicate fell through to the warehouse column of the same
    // name, which is NULL on every row - so a biller filtering Follow Up Action =
    // Insurance Follow-Up saw a real 400-claim queue as an empty grid, with the same
    // green "Information ready" chip as a healthy read.
    //
    // The server now REFUSES outright when the filter needs the overlay, so the only
    // reads that reach here are ones whose rows are genuinely fine. What is not fine is
    // the follow-up column those rows display, and this says so. The only signal used
    // to be queryPlanSummary.workflowOverlayMode, which this file never read even once.
    //
    // Keyed on workflowOverlayFailed, never on workflowOverlayMode === "off": "off" is
    // the HEALTHY value on the snapshot lane and in mirror mode, so keying on it would
    // paint this warning across every ordinary page load.
    if (trackerApiPageState.response?.queryPlanSummary?.workflowOverlayFailed === true) {
      return { tone: "stale", label: "Follow-up assignments could not be read - this list is incomplete" };
    }
    const cacheStatus = String(trackerApiPageState.response?.queryPlanSummary?.cacheStatus || "").trim();
    const refreshedAt = String(trackerApiPageState.response?.queryPlanSummary?.refreshedAtUtc || "").trim();
    const cacheLabel = cacheStatus ? ` - ${cacheStatus}${refreshedAt ? ` refreshed ${refreshedAt.slice(0, 16).replace("T", " ")}` : ""}` : "";
    return { tone: "connected", label: `Information ready${cacheLabel}` };
  }
  if (trackerApiPageState.status === "pending") return { tone: "pending", label: "Loading your information…" };
  if (trackerApiPageState.status === "error") {
    const detail = "";
    return { tone: "error", label: `Information unavailable${detail}` };
  }
  if (trackerApiPageState.staleResponses > 0) return { tone: "stale", label: "Information refreshed" };
  return { tone: "configured", label: "Information ready" };
}

// THE FACET LANE FAILS ON ITS OWN, SO IT REPORTS ON ITS OWN.
// /api/tracker/facets answered HTTP 400 on 100% of calls while /query kept
// serving pages perfectly, and the only visible consequence was a rail of wrong
// numbers. The page-lane summary above could not say that, because the page lane
// was healthy. Returns null while the filter lane is fine, so the chip appears
// only when there is something honest to say.
// Copy is fixed: trackerApiFacetState.error carries server text and must not be
// echoed to the user.
function trackerApiFacetStatusSummary() {
  if (!trackerApiEnabled()) return null;
  const status = trackerApiFacetState.status;
  if (status === "denied") return { tone: "error", label: "Filter values are outside your access" };
  if (status === "error") {
    // A previous good payload is still on screen (stale-while-revalidate), so the
    // counts are old rather than absent. Say which.
    return trackerApiFacetState.response
      ? { tone: "stale", label: "Filter counts may be out of date" }
      : { tone: "error", label: "Filter counts unavailable - the rows below are unaffected" };
  }
  if (!trackerApiFacetState.response && (status === "pending" || status === "idle")) {
    return { tone: "pending", label: "Loading filter counts" };
  }
  return null;
}

function buildReportQueryRequest() {
  return {
    filters: reportFilterValues()
  };
}

function trackerWorkbookExportFilters() {
  const controls = primaryTrackerExportControlSet();
  syncTrackerExportControls(controls);
  // The workbook's date window is only what this page shows. It used to be
  // intersected with the All Data grid's service-date filter, which this page
  // never displays, so a range left on the grid silently narrowed the book (and
  // an invalid grid date turned the window into the literal "invalid").
  const fromDate = dateOnlyFilterString(controls.fromDate?.value || "");
  const toDate = dateOnlyFilterString(controls.toDate?.value || "");
  return {
    // A workbook handed to a client on a call must carry the same narrowing
    // the screen did.
    tenant: activeClientGroupTenant() || controls.tenant?.value || "All",
    fromDate,
    toDate,
    fromMonth: fromDate ? fromDate.slice(0, 7) : "",
    toMonth: toDate ? toDate.slice(0, 7) : "",
    slicers: normalizeTrackerExportSlicers(state.trackerExportSlicers)
  };
}

function canExportTrackerWorkbook() {
  return hasPermission(PERMISSIONS.REPORT_EXPORT)
    && hasPermission(PERMISSIONS.VIEW_FINANCIALS)
    && hasPermission(PERMISSIONS.VIEW_PHI);
}

function trackerExportSlicerSummary(slicers = {}) {
  const active = normalizeTrackerExportSlicers(slicers);
  const parts = TRACKER_EXPORT_SLICER_DEFS
    .filter((def) => (active[def.key] || []).length)
    .map((def) => `${def.label} (${active[def.key].length})`);
  return parts.length ? parts.join(" · ") : "None — whole book";
}

function trackerWorkbookExportValidation(filters = trackerWorkbookExportFilters()) {
  if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
    return {
      valid: false,
      reason: "from_after_to",
      message: "Choose a From Date that is on or before the To Date."
    };
  }
  const currentPreviewKey = trackerExportPreviewKey();
  const expectedCountKey = `count:${currentPreviewKey}`;
  const localCountKey = currentPreviewKey;
  const countMatches = trackerExportCountState.key === expectedCountKey || trackerExportCountState.key === localCountKey;
  if (reportApiEnabled() && (!countMatches || trackerExportCountState.status !== "ok")) {
    return {
      valid: false,
      reason: trackerExportCountState.status === "error" && countMatches ? "count_error" : "count_pending",
      message: trackerExportCountState.status === "error" && countMatches
        // The server's sentence says WHY (a slow warehouse, an expired session);
        // the fallback tells the user what the button below does.
        ? `${trackerExportCountState.error && !/^HTTP \d+$/.test(trackerExportCountState.error) ? `${trackerExportCountState.error} ` : ""}Use Refresh summary to check again.`
        : "Checking the selected workbook size before download."
    };
  }
  const countedRows = trackerExportCountState.status === "ok"
    && countMatches
    ? Number(trackerExportCountState.rowCount || 0)
    : 0;
  if (trackerExportCountState.status === "ok" && countMatches && countedRows === 0) {
    return { valid: false, reason: "no_results", message: "Widen the date range, pick a different client, or clear a filter." };
  }
  // The server's own builder limit wins: it refuses above it with a 413, so a
  // looser browser cap showed "Ready to download" for selections that could
  // only fail after the click.
  const serverRowLimit = Number(trackerExportCountState.rowLimit) || 0;
  const rowLimit = serverRowLimit > 0 ? Math.min(serverRowLimit, TRACKER_WORKBOOK_EXPORT_MAX_ROWS) : TRACKER_WORKBOOK_EXPORT_MAX_ROWS;
  if (countedRows > rowLimit) {
    return {
      valid: false,
      reason: "row_cap",
      message: `This selection contains ${countedRows.toLocaleString()} service lines and one workbook holds at most ${rowLimit.toLocaleString()}. Pick a single client, a shorter date range, or more filters, then try again.`
    };
  }
  return { valid: true, reason: "", message: "" };
}

function trackerWorkbookExportClaims() {
  const filters = trackerWorkbookExportFilters();
  const today = todayDateString();
  return getClaims().filter((claim) => {
    if (filters.tenant !== "All" && claim.client !== filters.tenant) return false;
    return (claim.services || []).some((service) => (
      serviceDateInDateRange(service?.dos, filters.fromDate, filters.toDate)
      && claimMatchesTrackerExportSlicers(claim, service, filters.slicers, today)
    ));
  }).map((claim) => ({
    ...claim,
    services: (claim.services || []).filter((service) => (
      serviceDateInDateRange(service?.dos, filters.fromDate, filters.toDate)
      && claimMatchesTrackerExportSlicers(claim, service, filters.slicers, today)
    ))
  })).filter((claim) => claim.services.length);
}

function serviceDateInDateRange(dateText, fromDate, toDate) {
  const dos = dateOnlyFilterString(dateText);
  if (!dos) return false;
  if (fromDate && dos < fromDate) return false;
  if (toDate && dos > toDate) return false;
  return true;
}

function serviceMonth(dateText) {
  const text = String(dateText || "");
  const match = text.match(/^(\d{4}-\d{2})-\d{2}$/);
  return match ? match[1] : "";
}

function availableServiceMonths(claims = []) {
  const months = new Set();
  claims.forEach((claim) => (claim.services || []).forEach((service) => {
    const month = serviceMonth(service.dos);
    if (month) months.add(month);
  }));
  return Array.from(months).sort();
}

function availableServiceDates(claims = []) {
  const dates = new Set();
  claims.forEach((claim) => (claim.services || []).forEach((service) => {
    const dos = dateOnlyFilterString(service.dos);
    if (dos) dates.add(dos);
  }));
  return Array.from(dates).sort();
}

function currentMonthString() {
  // Business month on the Eastern clock (see todayStart) — a browser east of Eastern would
  // otherwise roll to the next month a day early on the 1st.
  const date = todayStart();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function previewTrackerWorkbookExport() {
  // Safe to run mid-build: this used to clear the request key the poller checked,
  // silently abandoning a workbook the server was still building. The poller now
  // keys off the persisted job id, which nothing here touches, and the header
  // chip stays the authoritative signal for the build.
  if (!canExportTrackerWorkbook()) {
    state.trackerWorkbookExportPreview = {
      error: "Current role cannot export tracker workbooks.",
      authorization: { allowed: false, reason: "missing_report_export_permission" }
    };
    renderTrackerWorkbookExportStatus();
    return;
  }
  // "Refresh summary" must actually ask again. Against the live service it
  // rebuilt a local preview (no rows, no coverage) and never re-ran the size
  // check, so the "refresh the preview" advice after a failed check was a dead
  // end. A date-order error is the only thing a new request cannot fix.
  const countInFlight = trackerExportCountState.status === "pending" && trackerExportCountState.key === `count:${trackerExportPreviewKey()}`;
  if (reportApiEnabled() && !countInFlight && trackerWorkbookExportValidation().reason !== "from_after_to") {
    if (state.trackerWorkbookExportPreview?.status !== "pending" && state.trackerWorkbookExportJob?.status !== "building") {
      state.trackerWorkbookExportPreview = null;
    }
    trackerExportCountState = { ...trackerExportCountState, key: "", status: "idle", error: "" };
    trackerExportFacetState = { ...trackerExportFacetState, key: "" };
    renderTrackerWorkbookExportStatus();
    scheduleTrackerExportPreviewRefresh();
    return;
  }
  const validation = trackerWorkbookExportValidation();
  if (!validation.valid) {
    state.trackerWorkbookExportPreview = {
      error: validation.reason === "count_pending" ? "" : validation.message,
      filters: trackerWorkbookExportFilters(),
      validation
    };
    renderTrackerWorkbookExportStatus();
    return;
  }
  state.trackerWorkbookExportPreview = buildLocalTrackerWorkbookExportPreview();
  audit("Previewed tracker workbook export scope.", "Reports");
  saveState();
  renderTrackerWorkbookExportStatus();
}

let trackerTeamSpaceState = { key: "", status: "idle", destinations: [], current: null, error: "" };

const TRACKER_TEAM_SPACE_ROOT = "https://netorgft6969826.sharepoint.com/sites/Collections-Overseas/Shared%20Documents/General/Sanitized%20Client%20Trackers";
const TRACKER_TEAM_SPACE_FILE = "TMS Claims Tracker Light.xlsx";
const TRACKER_TEAM_SPACE_ROWS = [
  ["accurate-aba", "Accurate ABA", ["AccurateABA", "Accurate_ABA"]],
  ["achieve-center-aba", "Achieve Center ABA", ["AchieveCenterABA", "Achieve_Center_ABA"]],
  ["ambitions-aba", "Ambitions ABA", ["AmbitionsABA", "Ambitions_ABA"]],
  ["an-incredibale-me-aba", "An Incredible Me ABA", ["AnIncredibleMeABA", "An_Incredible_Me_ABA", "an-incredible-me-aba"]],
  ["aspire-aba", "Aspire ABA", ["AspireABA", "Aspire_ABA"]],
  ["astro-aba", "Astro ABA", ["AstroABA", "Astro_ABA"]],
  ["avenues-aba", "Avenues ABA", ["AvenuesABA", "Avenues_ABA"]],
  ["big-dreamers-aba", "Big Dreamers ABA", ["BigDreamersABA", "BIG_DREAMERS_ABA"]],
  ["blossom-aba", "Blossom ABA", ["BlossomABA", "Blossom_ABA"]],
  ["bounce-therapy", "Bounce Therapy", ["BounceTherapy2", "Bounce_Therapy_2", "Bounce Therapy 2"]],
  ["bright-builders-aba", "Bright Builders ABA", ["BrightBuildersABA", "Bright_Builders_ABA"]],
  ["epic-minds-aba", "Epic Minds", ["EpicMinds", "Epic_Minds", "Epic Minds ABA"]],
  ["get-set-aba", "Get Set ABA", ["GetSetABA", "Get_Set_ABA"]],
  ["gradual-bh-aba", "Gradual BH", ["GradualBH", "GRADUAL_BH", "GradualBHABA"]],
  ["guided-growth-aba", "Guided Growth", ["GuidedGrowth", "Guided_Growth", "Guided Growth ABA"]],
  ["my-team-aba", "My Team ABA", ["MyTeamABA", "My_Team_ABA"]],
  ["ntc-aba", "NTC ABA", ["NTCABA", "NTC_ABA"]],
  ["perfect-pair-aba", "Perfect Pair ABA", ["PerfectPairABA", "Perfect_Pair_ABA"]],
  ["serenity-aba", "Serenity ABA", ["SerenityABA", "Serenity_ABA"]],
  ["shining-moments-aba", "Shining Moments ABA", ["ShiningMomentsABA", "Shining_Moments_ABA"]],
  ["skyward-spectrum-aba", "Skyward Spectrum", ["SkywardSpectrum", "Skyward_Spectrum", "Skyward Spectrum ABA"]]
];

function compactTeamSpaceKey(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function teamSpaceOpenUrl(slug) {
  return `${TRACKER_TEAM_SPACE_ROOT}/${slug}/${encodeURIComponent(TRACKER_TEAM_SPACE_FILE)}?web=1`;
}

function resolveLocalTeamSpace(tenant) {
  const key = compactTeamSpaceKey(tenant);
  if (!key || key === "all") return null;
  for (const [slug, label, aliases] of TRACKER_TEAM_SPACE_ROWS) {
    const names = [slug, label, ...(aliases || [])];
    if (names.some((name) => compactTeamSpaceKey(name) === key)) {
      return { tenantId: slug, label, fileName: TRACKER_TEAM_SPACE_FILE, openUrl: teamSpaceOpenUrl(slug) };
    }
  }
  return null;
}

// All-clients is not a Team Space. The API already returns current=null when
// more than one destination matches; never fall back to destinations[0].
function pickTeamSpaceCurrent(destinations, tenant) {
  const named = resolveLocalTeamSpace(tenant);
  const list = (Array.isArray(destinations) ? destinations : []).filter((row) => row && row.openUrl);
  if (named) {
    const key = compactTeamSpaceKey(named.tenantId || named.label);
    const match = list.find((row) => [row.tenantId, row.label].some((id) => compactTeamSpaceKey(id) === key));
    return match || named;
  }
  return list.length === 1 ? list[0] : null;
}

function trackerTeamSpaceTenant() {
  const fromFilters = String((trackerWorkbookExportFilters() || {}).tenant || "").trim();
  if (fromFilters && fromFilters !== "All") return fromFilters;
  return String(typeof activeClientGroupTenant === "function" ? activeClientGroupTenant() : "").trim();
}

function currentTrackerTeamSpace() {
  const tenant = trackerTeamSpaceTenant();
  return pickTeamSpaceCurrent(
    [].concat(trackerTeamSpaceState.current || [], trackerTeamSpaceState.destinations || []),
    tenant
  );
}

function trackerTeamSpaceRequestKey() {
  return JSON.stringify({
    base: reportApiBase() || trackerApiBase() || "",
    tenant: trackerTeamSpaceTenant() || "All",
    role: state.role,
    user: state.activeUserName || ""
  });
}

function requestTrackerTeamSpace() {
  const tenant = trackerTeamSpaceTenant();
  const key = trackerTeamSpaceRequestKey();
  const local = resolveLocalTeamSpace(tenant);
  trackerTeamSpaceState = {
    ...trackerTeamSpaceState,
    key,
    status: local ? "ready" : "idle",
    current: pickTeamSpaceCurrent(local ? [local] : [], tenant),
    destinations: local ? [local] : trackerTeamSpaceState.destinations,
    error: ""
  };
  const base = reportApiBase() || trackerApiBase();
  if (!base || typeof fetch !== "function") {
    renderTrackerWorkbookExportStatus();
    return;
  }
  const token = reportApiToken() || trackerApiToken();
  fetch(`${base}/api/reports/tracker-teamspace?tenant=${encodeURIComponent(tenant || "All")}`, {
    cache: "no-store",
    headers: token ? { authorization: `Bearer ${token}` } : {}
  }).then(async (response) => {
    if (trackerTeamSpaceState.key !== key) return;
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.error) {
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        trackerTeamSpaceState = {
          key,
          status: "error",
          destinations: [],
          current: null,
          error: "Your session expired. Sign in again — this page will still be here."
        };
      } else {
        const fallback = resolveLocalTeamSpace(tenant);
        trackerTeamSpaceState = {
          key,
          status: fallback ? "ready" : "error",
          destinations: fallback ? [fallback] : [],
          current: pickTeamSpaceCurrent(fallback ? [fallback] : [], tenant),
          error: fallback ? "" : String(payload.message || payload.error || ("http_" + response.status))
        };
      }
    } else {
      const destinations = Array.isArray(payload.destinations) ? payload.destinations : [];
      trackerTeamSpaceState = {
        key,
        status: "ready",
        destinations,
        current: pickTeamSpaceCurrent(destinations, tenant),
        error: ""
      };
    }
    if (state.view === "trackerExport") renderTrackerWorkbookExportStatus();
  }).catch(() => {
    if (trackerTeamSpaceState.key !== key) return;
    const fallback = resolveLocalTeamSpace(tenant);
    trackerTeamSpaceState = {
      key,
      status: fallback ? "ready" : "error",
      destinations: fallback ? [fallback] : [],
      current: pickTeamSpaceCurrent(fallback ? [fallback] : [], tenant),
      error: fallback ? "" : "network_error"
    };
    if (state.view === "trackerExport") renderTrackerWorkbookExportStatus();
  });
}

function openTrackerTeamSpace() {
  if (!canExportTrackerWorkbook()) {
    showToast("Current role cannot open the current tracker.");
    return;
  }
  const current = currentTrackerTeamSpace();
  if (!current || !current.openUrl) {
    showToast(trackerTeamSpaceTenant() && trackerTeamSpaceTenant() !== "All"
      ? "No Team Space tracker is published for this client yet."
      : "Pick one client first. The current tracker opens that client's Team Space.");
    return;
  }
  audit(`Opened current tracker Team Space ${current.label || current.tenantId}.`, "Reports");
  if (typeof window.open === "function") window.open(current.openUrl, "_blank", "noopener");
}

function downloadTrackerWorkbookExport() {
  if (!canExportTrackerWorkbook()) {
    showToast("Current role cannot export tracker workbooks.");
    return;
  }
  const validation = trackerWorkbookExportValidation();
  if (!validation.valid) {
    state.trackerWorkbookExportPreview = {
      // A pending size check is not an error; the count's answer replaces it.
      error: validation.reason === "count_pending" ? "" : validation.message,
      filters: trackerWorkbookExportFilters(),
      validation
    };
    renderTrackerWorkbookExportStatus();
    showToast(validation.reason === "count_pending"
      ? "Still checking the workbook size — try again in a moment."
      : validation.message || "This selection cannot be exported yet.");
    return;
  }
  if (reportApiEnabled()) {
    requestTrackerWorkbookExport();
    return;
  }
  state.trackerWorkbookExportPreview = {
    ...buildLocalTrackerWorkbookExportPreview(),
    error: "The current tracker workbook requires a connected report service. Reconnect and try again."
  };
  renderTrackerWorkbookExportStatus();
  showToast("Reconnect the report service to download the current tracker.");
}

// The workbook is built by a server-side job: the browser POSTs, gets a jobId
// back, polls, then downloads. That work already survives moving around the
// portal - but until now the only sign it was happening lived on the Export
// Tracker panel, so anyone who navigated away had no way to tell whether their
// export was still coming, and a reload threw the jobId away entirely while the
// server kept building. Hence: remember the job, show it from every view, and
// make the finish impossible to miss.
//
// What this deliberately does NOT promise is "close your browser". The server
// holds the finished bytes in memory for 15 minutes and never writes them to
// disk - that is the managed-export-storage governance boundary, not an
// oversight - so a closed tab still loses the workbook. Delivering to a place
// that outlives the session is Phase 2, and the copy here stays honest about it.
const TRACKER_EXPORT_JOB_MAX_AGE_MS = 15 * 60 * 1000;
// How long the browser keeps watching a build. The server is the authority: it
// fails a job after up to 10 minutes queued plus 13 minutes running, and the
// poller reports that verdict. Giving up at 15 minutes from the click declared
// a still-building workbook dead; this is only a backstop above the server's.
const TRACKER_EXPORT_JOB_BUILD_LIMIT_MS = 30 * 60 * 1000;
const TRACKER_EXPORT_READY_VISIBLE_MS = 15 * 60 * 1000;
const TRACKER_EXPORT_FAILED_VISIBLE_MS = 5 * 60 * 1000;
let trackerExportJobTicker = null;
let trackerExportJobExpiryTimer = null;
// The server reports which step it is in; a step is real progress, unlike the clock.
const TRACKER_EXPORT_STAGE_LABELS = {
  loading_fee_schedules: "loading rates",
  checking_size: "checking size",
  loading_rows: "loading service lines",
  loading_line_history: "loading line history",
  loading_claim_notes: "loading claim notes",
  building_workbook: "writing Excel"
};

// The access snapshot is role + user + permissions + tenant scope. Resuming a
// job across a reload must re-check it: a workbook built for one principal's
// scope must never be handed to a different one just because the tab reopened.
function trackerExportJobScopeFingerprint() {
  try {
    const raw = JSON.stringify(workflowCommandAccessSnapshot());
    let hash = 5381;
    for (let index = 0; index < raw.length; index += 1) hash = ((hash * 33) ^ raw.charCodeAt(index)) >>> 0;
    return `${raw.length}:${hash.toString(36)}`;
  } catch (_) {
    return "";
  }
}

function trackerExportJobRecord() {
  const job = state.trackerWorkbookExportJob;
  if (!job || typeof job !== "object" || !job.status) return null;
  return job;
}

function clearTrackerExportJobRecord() {
  if (trackerExportJobExpiryTimer) {
    clearTimeout(trackerExportJobExpiryTimer);
    trackerExportJobExpiryTimer = null;
  }
  if (state.trackerWorkbookExportJob) {
    state.trackerWorkbookExportJob = null;
    saveState();
  }
  renderTrackerExportJobChip();
}

function setTrackerExportJobRecord(patch) {
  const current = trackerExportJobRecord() || {};
  const next = { ...current, ...patch };
  if (next.status === "building") {
    next.terminalAt = 0;
  } else if ((next.status === "ready" || next.status === "failed")
    && (!Number(current.terminalAt) || current.status !== next.status)) {
    next.terminalAt = Date.now();
  }
  state.trackerWorkbookExportJob = next;
  saveState();
  renderTrackerExportJobChip();
}

function trackerExportTerminalVisibleMs(job) {
  if (job?.status === "ready") return TRACKER_EXPORT_READY_VISIBLE_MS;
  if (job?.status === "failed") return TRACKER_EXPORT_FAILED_VISIBLE_MS;
  return 0;
}

function trackerExportJobExpiresAt(job) {
  const visibleMs = trackerExportTerminalVisibleMs(job);
  if (!visibleMs) return 0;
  // Records from older builds do not have terminalAt. Falling back to their
  // start time ensures an already-stale chip is cleared on the first new boot.
  return Number(job.terminalAt || job.startedAt || 0) + visibleMs;
}

function trackerExportJobExpired(job, now = Date.now()) {
  const expiresAt = trackerExportJobExpiresAt(job);
  return Boolean(expiresAt) && now >= expiresAt;
}

function syncTrackerExportJobExpiryTimer(job) {
  if (trackerExportJobExpiryTimer) {
    clearTimeout(trackerExportJobExpiryTimer);
    trackerExportJobExpiryTimer = null;
  }
  const expiresAt = trackerExportJobExpiresAt(job);
  if (!expiresAt) return;
  trackerExportJobExpiryTimer = setTimeout(() => {
    trackerExportJobExpiryTimer = null;
    const current = trackerExportJobRecord();
    if (current && trackerExportJobExpired(current)) clearTrackerExportJobRecord();
  }, Math.max(0, expiresAt - Date.now()));
  // Node-based focused contract tests should never be held open by a UI timer.
  trackerExportJobExpiryTimer?.unref?.();
}

function trackerExportJobElapsedLabel(startedAt) {
  const seconds = Math.max(0, Math.round((Date.now() - Number(startedAt || Date.now())) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

// One ticker, only while something is actually building.
function syncTrackerExportJobTicker(active) {
  if (active && !trackerExportJobTicker) {
    trackerExportJobTicker = setInterval(() => { try { renderTrackerExportJobChip(); } catch (_) {} }, 1000);
  } else if (!active && trackerExportJobTicker) {
    clearInterval(trackerExportJobTicker);
    trackerExportJobTicker = null;
  }
}

function renderTrackerExportJobChip() {
  const chip = document.getElementById("trackerExportJobChip");
  if (!chip) return;
  const text = document.getElementById("trackerExportJobText");
  const action = document.getElementById("trackerExportJobAction");
  const dismiss = document.getElementById("trackerExportJobDismiss");
  const job = trackerExportJobRecord();
  if (job && trackerExportJobExpired(job)) {
    clearTrackerExportJobRecord();
    return;
  }
  if (!job) {
    chip.classList.add("hidden");
    syncTrackerExportJobTicker(false);
    return;
  }
  syncTrackerExportJobExpiryTimer(job);
  const building = job.status === "building";
  chip.classList.remove("hidden");
  chip.classList.toggle("is-building", building);
  chip.classList.toggle("is-ready", job.status === "ready");
  chip.classList.toggle("is-failed", job.status === "failed");
  if (text) {
    // ELAPSED TIME IS NOT PROGRESS.
    //
    // This read "Building your workbook… 2:55" and nothing else. At 2:55 a user
    // cannot tell 40% from 99%, and the "usually 1-3 minutes" they were given is
    // already wrong for a big range. A real percentage would need the builder to
    // report sheet-by-sheet, which it does not - but the LINE COUNT is known before
    // the click and is the fact that actually distinguishes "this is a big one"
    // from "this is stuck". A true fact beats an invented estimate.
    const buildingSize = Number(job.rowCount) || 0;
    // QUEUED IS NOT BUILDING. Counting elapsed seconds against a build that
    // has not started is the same lie as counting them against progress.
    const queuedPosition = building ? Number(job.queuePosition) || 0 : 0;
    text.textContent = queuedPosition
      ? `Waiting for another export to finish — you are #${queuedPosition} in line…`
      : building
        ? `Building your workbook… ${TRACKER_EXPORT_STAGE_LABELS[job.stage] ? `${TRACKER_EXPORT_STAGE_LABELS[job.stage]} · ` : ""}${buildingSize ? `${buildingSize.toLocaleString()} lines · ` : ""}${trackerExportJobElapsedLabel(job.startedAt)}`
        : job.status === "ready"
          ? `Workbook ready — ${job.filename || "downloaded"}`
          : job.message || "Export stopped.";
  }
  if (action) {
    // "Download again" only while the server still holds the bytes.
    // The server's 15-minute retention runs from when the build FINISHED.
    const reofferable = job.status === "ready" && job.jobId && (Date.now() - Number(job.terminalAt || job.startedAt || 0)) < TRACKER_EXPORT_JOB_MAX_AGE_MS;
    action.classList.toggle("hidden", !reofferable);
    action.textContent = "Download again";
  }
  if (dismiss) dismiss.classList.toggle("hidden", building);
  chip.title = building
    ? "Your workbook is building on the server. Keep the portal open — you can carry on working while it finishes."
    : job.status === "ready"
      ? "The workbook downloaded to this browser."
      : "";
  syncTrackerExportJobTicker(building);
}

function bindTrackerExportJobChip() {
  document.getElementById("trackerExportJobAction")?.addEventListener("click", () => {
    const job = trackerExportJobRecord();
    if (!job?.jobId) return;
    // A swallowed failure here was a dead button: the server keeps finished
    // bytes in memory only, so a restart or the retention window loses them.
    downloadFinishedTrackerExportJob(job.jobId).catch((error) => {
      const gone = error?.code === "export_job_not_found" || error?.code === "export_job_not_ready" || /HTTP 404/.test(error?.message || "");
      const message = gone
        ? "That workbook is no longer held on the server. Run the export again to get a fresh copy."
        : `The workbook could not be downloaded again: ${error?.message || "unknown error"}`;
      setTrackerExportJobRecord({ status: "failed", message });
      showToast(message);
    });
  });
  document.getElementById("trackerExportJobDismiss")?.addEventListener("click", clearTrackerExportJobRecord);
  renderTrackerExportJobChip();
}

// After a reload the server may still be building, or may already be holding a
// finished workbook. Either way the owner should get it without resubmitting.
function resumeTrackerWorkbookExportJob() {
  const job = trackerExportJobRecord();
  if (!job) return;
  if (trackerExportJobExpired(job)) {
    clearTrackerExportJobRecord();
    return;
  }
  if (job.status !== "building") { renderTrackerExportJobChip(); return; }
  if (!job.jobId) {
    setTrackerExportJobRecord({ status: "failed", message: "That export was interrupted before it started. Please run it again." });
    return;
  }
  if ((Date.now() - Number(job.startedAt || 0)) > TRACKER_EXPORT_JOB_BUILD_LIMIT_MS) {
    setTrackerExportJobRecord({ status: "failed", message: "That export did not finish before the workbook expired. Please run it again." });
    return;
  }
  if (job.scope && job.scope !== trackerExportJobScopeFingerprint()) {
    setTrackerExportJobRecord({ status: "failed", message: "Your access changed while that export was building. Please run it again." });
    return;
  }
  if (!reportApiEnabled() || !canExportTrackerWorkbook()) {
    // Not a dead job - the report service simply may not have come up yet on
    // this boot, while the server keeps building. Declaring it failed here
    // would throw away a workbook that is still coming. Look again shortly;
    // the expiry check above bounds the retries.
    renderTrackerExportJobChip();
    setTimeout(resumeTrackerWorkbookExportJob, 3000);
    return;
  }
  renderTrackerExportJobChip();
  pollTrackerWorkbookExportJob(job.jobId, Number(job.startedAt) || Date.now());
}

function trackerExportAuthHeaders() {
  const token = reportApiToken();
  return { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) };
}

async function readTrackerExportErrorBody(response) {
  const body = await response.json().catch(() => ({}));
  const error = new Error(body.reason || body.message || (typeof body.error === "string" ? body.error : body.error?.message) || `HTTP ${response.status}`);
  error.code = typeof body.error === "string" ? body.error : "";
  return error;
}

function isTrackerExportNetworkError(error) {
  return Boolean(error)
    && !error.code
    && (/failed to fetch|networkerror|load failed/i.test(error.message || "") || error.name === "TypeError");
}

// NAME THE SIZE WHEN THE SIZE IS KNOWN. "RETRY" IS BAD ADVICE FOR A BIG RANGE.
//
// This said the service "may still be waking up or your connection may have
// dropped" and told the user to retry. For a genuine blip that is right. But the
// way people actually met this message was a six-month export: the build blew the
// API's heap, the process aborted, and the poll really did fail to fetch - so the
// copy described the symptom accurately and the cause not at all. A biller read
// "bad wifi", waited a moment, clicked the still-enabled button, and lost another
// four minutes. The abort itself is fixed (the payload streams now), but a very
// large range is still the likeliest reason this fires, and the row count is
// already on screen before the click - so say it.
function trackerExportConnectionError() {
  const rows = Number(state.trackerWorkbookExportPreview?.rowCount) || 0;
  const large = rows > TRACKER_EXPORT_LARGE_RANGE_ROWS;
  const error = new Error(large
    ? `This export covers ${rows.toLocaleString()} service lines and did not finish. That is a large range — pick one client, or shorten it to about three months, then try again.`
    : "The tracker export service could not be reached. It may still be waking up or your connection may have dropped. Please wait a moment, then retry the export.");
  error.code = large ? "tracker_export_range_too_large" : "tracker_export_network_error";
  return error;
}

function fetchTrackerExport(url, options, step, attempt = 0) {
  return fetch(url, options).catch((error) => {
    if (!isTrackerExportNetworkError(error) || attempt >= 2) throw trackerExportConnectionError();
    const job = trackerExportJobRecord();
    if (job?.status === "building") setTrackerExportJobRecord({ retryNotice: `Reconnecting… ${step}, attempt ${attempt + 1} of 3.` });
    if (state.trackerWorkbookExportPreview?.status === "pending") {
      state.trackerWorkbookExportPreview = { ...state.trackerWorkbookExportPreview, retryNotice: `Reconnecting… ${step}, attempt ${attempt + 1} of 3.` };
      renderTrackerWorkbookExportStatus();
    }
    const delayMs = attempt === 0 ? 1200 : 3000;
    return new Promise((resolve) => setTimeout(resolve, delayMs)).then(() => fetchTrackerExport(url, options, step, attempt + 1));
  });
}

function failTrackerWorkbookExport(error) {
  // A job lost to a server restart already explains itself; keep the server's
  // sentence instead of double-framing it as a template failure.
  const lostJob = error.code === "export_job_not_found" || /interrupted by a server restart/i.test(error.message || "");
  const connectionLost = error.code === "tracker_export_network_error";
  const message = lostJob || connectionLost ? error.message : `Tracker workbook export failed: ${error.message}`;
  setTrackerExportJobRecord({ status: "failed", message });
  state.trackerWorkbookExportPreview = { ...buildLocalTrackerWorkbookExportPreview(), error: message };
  saveState();
  renderTrackerWorkbookExportStatus();
  showToast(lostJob ? "Export interrupted by a server restart - please resubmit." : connectionLost ? "Tracker export connection failed. Please retry." : "Tracker export failed.");
}

async function finishTrackerWorkbookExportDownload(response) {
  const blob = await response.blob();
  const disposition = response.headers?.get?.("content-disposition") || "";
  const filename = filenameFromContentDisposition(disposition) || trackerWorkbookFilename(buildLocalTrackerWorkbookExportPreview());
  // Download immediately rather than waiting for a click: the server holds
  // these bytes in memory on a timer, so an unclicked "ready" button would
  // quietly become a dead one.
  downloadBlobPayload(blob, filename);
  const preview = buildLocalTrackerWorkbookExportPreview();
  state.trackerWorkbookExportPreview = {
    ...preview,
    downloadedAt: timestamp(),
    filename,
    apiBacked: true,
    templateWorkbook: response.headers?.get?.("x-tms-tracker-template") || preview.templateWorkbook,
    rowCount: Number(response.headers?.get?.("x-tms-tracker-export-rows") || preview.trackerRows.length),
    // The local preview has no live rows, so carry the size check's facts or
    // the card loses its date coverage the moment the download lands.
    claimCount: Number(response.headers?.get?.("x-tms-tracker-export-claims") || trackerExportCountState.claimCount || preview.claimCount || 0),
    firstServiceDate: trackerExportCountState.firstServiceDate || preview.firstServiceDate || "",
    lastServiceDate: trackerExportCountState.lastServiceDate || preview.lastServiceDate || "",
    feeScheduleRowCount: Number(response.headers?.get?.("x-tms-tracker-fee-schedule-rows") || 0),
    missingFeeScheduleRowCount: Number(response.headers?.get?.("x-tms-tracker-missing-fee-schedule-rows") || 0)
  };
  setTrackerExportJobRecord({ status: "ready", filename, retryNotice: "" });
  audit(`Downloaded current tracker workbook ${filename}.`, "Reports");
  saveState();
  renderTrackerWorkbookExportStatus();
  showToast(`Workbook ready — ${filename} downloaded.`);
}

async function downloadFinishedTrackerExportJob(jobId) {
  const base = reportApiBase();
  if (!base) return;
  const download = await fetchTrackerExport(
    `${base}/api/reports/tracker-workbook-export/jobs/${encodeURIComponent(jobId)}/download`,
    { cache: "no-store", headers: trackerExportAuthHeaders() },
    "downloading workbook"
  );
  if (!download.ok) throw await readTrackerExportErrorBody(download);
  await finishTrackerWorkbookExportDownload(download);
}

// Polling keeps each HTTP request short so a big range cannot hit Azure's 240s
// gateway limit. It is driven off the persisted record rather than a closure so
// a reload can pick the same job back up.
function pollTrackerWorkbookExportJob(jobId, startedAtMs) {
  const base = reportApiBase();
  if (!base) return;
  const stillOurs = () => trackerExportJobRecord()?.jobId === jobId;
  if (!stillOurs()) return;
  if (Date.now() - startedAtMs > TRACKER_EXPORT_JOB_BUILD_LIMIT_MS) {
    failTrackerWorkbookExport(new Error("Export job timed out after 30 minutes."));
    return;
  }
  fetchTrackerExport(`${base}/api/reports/tracker-workbook-export/jobs/${encodeURIComponent(jobId)}`, { cache: "no-store", headers: trackerExportAuthHeaders() }, "checking status")
    .then(async (response) => {
      if (!stillOurs()) return;
      if (!response.ok) throw await readTrackerExportErrorBody(response);
      const job = await response.json();
      if (!stillOurs()) return;
      if (job.status === "failed") throw new Error(typeof job.error === "string" ? job.error : job.error?.message || "Export job failed.");
      if (job.status !== "completed") {
        // A job can be admitted but not yet BUILDING: the API caps how many
        // workbooks are under construction at once, because three big ones at
        // the same time used to abort the whole API process. Waiting is a state
        // worth showing - silence is what makes a queue look like a hang.
        const nextQueuePosition = Number(job.queuePosition) || 0;
        const nextStage = String(job.stage || "");
        // Only on a CHANGE: this polls every 3s and setTrackerExportJobRecord
        // writes the whole state back to storage.
        const current = trackerExportJobRecord() || {};
        if ((Number(current.queuePosition) || 0) !== nextQueuePosition || String(current.stage || "") !== nextStage) {
          setTrackerExportJobRecord({ queuePosition: nextQueuePosition, stage: nextStage });
        }
        setTimeout(() => pollTrackerWorkbookExportJob(jobId, startedAtMs), 3000);
        return;
      }
      await downloadFinishedTrackerExportJob(jobId);
    })
    .catch((error) => { if (stillOurs()) failTrackerWorkbookExport(error); });
}

async function requestTrackerWorkbookExport() {
  if (!canExportTrackerWorkbook()) return;
  const base = reportApiBase();
  if (!base || typeof fetch !== "function") return;
  const filters = trackerWorkbookExportFilters();
  const validation = trackerWorkbookExportValidation(filters);
  if (!validation.valid) {
    state.trackerWorkbookExportPreview = { error: validation.message, filters, validation };
    renderTrackerWorkbookExportStatus();
    return;
  }
  const readContext = reportReadSurface() ? syncReportReadContext() : null;
  let readLease = null;
  try { if (readContext) readLease = await ensureReportReadSession(readContext); }
  catch (error) { showToast(error.message || "This report could not be opened for export."); return; }
  if (readContext && !reportReadContextCurrent(readContext)) return;
  if (JSON.stringify(filters) !== JSON.stringify(trackerWorkbookExportFilters())) return;
  const requestKey = JSON.stringify({ filters, generation: readLease?.generation || "current" });
  const activeJob = trackerExportJobRecord();
  // A browser dblclick fires two click handlers before the first POST can
  // return. The API safely deduplicates that race with HTTP 409, but the second
  // failed resource still appears as a console error and needlessly adds load.
  // Keep the already-visible job instead: it is the exact same workbook and its
  // existing poll/download flow is the one the user should continue watching.
  if (activeJob?.status === "building" && activeJob.requestKey === requestKey) {
    renderTrackerExportJobChip();
    showToast("That tracker workbook is already building.");
    return;
  }
  const requestSeq = ++trackerWorkbookExportRequestSeq;
  const requestScope = trackerExportJobScopeFingerprint();
  const stillCurrent = () => requestSeq === trackerWorkbookExportRequestSeq
    && canExportTrackerWorkbook()
    && trackerExportJobScopeFingerprint() === requestScope;
  state.trackerWorkbookExportPreview = { status: "pending", filters, trackerRows: [], claimCount: 0, monthLabel: trackerExportMonthLabel(filters) };
  setTrackerExportJobRecord({
    jobId: "",
    status: "building",
    startedAt: Date.now(),
    requestKey,
    // The size is KNOWN before the click - the pre-count gated this button. Carry
    // it so the chip can say what is building instead of only how long it has been
    // building. See the chip label for why that matters.
    rowCount: Number(trackerExportCountState.rowCount) || 0,
    scope: trackerExportJobScopeFingerprint(),
    filename: "",
    message: "",
    retryNotice: "",
    stage: "",
    queuePosition: 0
  });
  renderTrackerWorkbookExportStatus();
  fetchTrackerExport(`${base}/api/reports/tracker-workbook-export`, {
    method: "POST",
    cache: "no-store",
    headers: trackerExportAuthHeaders(),
    body: JSON.stringify({ async: true, filters, ...(readLease ? { readSessionId: readLease.id, expectedGeneration: readLease.generation } : {}) })
  }, "starting export")
    .then(async (response) => {
      if (!stillCurrent()) return;
      if (response.status === 202) {
        const job = await response.json().catch(() => ({}));
        if (!stillCurrent()) return;
        if (!job.jobId) throw new Error("Export job did not return an id.");
        if (readLease && job.generationId && job.generationId !== readLease.generation) throw new Error("The export version did not match the requested report.");
        setTrackerExportJobRecord({ jobId: job.jobId, generation: job.generationId || readLease?.generation || "" });
        if (job.status === "completed") await downloadFinishedTrackerExportJob(job.jobId);
        else pollTrackerWorkbookExportJob(job.jobId, Date.now());
        return;
      }
      if (response.status === 409) {
        // ATTACH ONLY TO THE SAME REQUEST.
        //
        // This attached to whatever jobId a 409 carried. The server deduped on
        // the USER alone, so asking for a different slicer selection while one
        // export was building returned the FIRST job - and this line downloaded
        // that workbook, under a chip reporting success, with a filename that
        // encodes the dates and the company but not the slicers. You asked for
        // one selection and silently got another.
        //
        // Same filters is still an attach, and should be: it is the same
        // workbook, so a double-click or a reloaded tab joins the build instead
        // of starting a second one. Different filters is now its own error code
        // with no jobId to attach to, and the server explains the wait.
        const body = await response.json().catch(() => ({}));
        if (!stillCurrent()) return;
        if (body.error === "export_already_running" && body.job?.jobId) {
          setTrackerExportJobRecord({ jobId: body.job.jobId });
          pollTrackerWorkbookExportJob(body.job.jobId, Date.now());
          return;
        }
        throw new Error(body.message || "An export with different filters is already running. Wait for it to finish, then export this selection.");
      }
      if (!response.ok) throw await readTrackerExportErrorBody(response);
      if (!stillCurrent()) return;
      // Older API builds ignore the async flag and stream the file directly.
      await finishTrackerWorkbookExportDownload(response);
    })
    .catch((error) => { if (stillCurrent()) failTrackerWorkbookExport(error); });
}

// The workbook is 13 sheets, plus the two claim-note sheets the builder adds only
// for a principal that may export note text. Hardcoding 13 told half the users a
// number their own download contradicts - a small lie, on the one card whose whole
// job is to say what you are about to get.
function trackerWorkbookExportSheetCount() {
  return hasPermission(PERMISSIONS.EXPORT_CLAIM_NOTES) ? 15 : 13;
}

function renderTrackerWorkbookExportStatus() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("trackerExport")) return;
  const targets = trackerWorkbookExportStatusTargets();
  if (!targets.length) return;
  const preview = state.trackerWorkbookExportPreview || buildLocalTrackerWorkbookExportPreview({ dryRun: true });
  // Judge what is on screen now. The stored preview keeps the filters of the
  // LAST answer, so a From after To was checked against the old, valid range
  // and the card sat on "Checking the workbook size" instead of saying why.
  // Only a build in progress is described by its own captured filters.
  const filters = preview.status === "pending" && preview.filters ? preview.filters : trackerWorkbookExportFilters();
  const validation = trackerWorkbookExportValidation(filters);
  const allowed = canExportTrackerWorkbook();
  trackerWorkbookExportButtons().forEach((button) => {
    if (button === els.openTrackerTeamSpaceBtn) { button.disabled = !allowed || !Boolean(currentTrackerTeamSpace()?.openUrl); return; }
    if (button === els.previewTrackerWorkbookBtn || button === els.clientPreviewTrackerWorkbookBtn) { button.disabled = !allowed || validation.reason === "from_after_to"; return; }
    button.disabled = !allowed || !validation.valid;
  });
  if (!validation.valid || preview.error) {
    const headlines = {
      row_cap: "Too many service lines for one workbook",
      no_results: "No service lines match",
      from_after_to: "Check the date range",
      count_pending: "Checking the workbook size",
      count_error: "Workbook size check failed"
    };
    const pending = !preview.error && validation.reason === "count_pending";
    const headline = headlines[validation.reason] || (preview.error ? "Export could not be built" : "Not ready to download");
    setTrackerWorkbookExportStatus(`<div class="tracker-export-readiness tracker-export-readiness--${pending ? "pending" : "warn"}"><strong>${escapeHtml(headline)}</strong><span>${escapeHtml(preview.error || validation.message)}</span></div>`); return;
  }
  if (preview.status === "pending") {
        // The waiting copy is good and stays; the ONE thing it was missing is the size,
    // which is why "usually 1-3 minutes" read as wrong to anyone who had picked a
    // large range. An em dash, not a hyphen, per house typography.
    const pendingRows = Number(trackerExportCountState.rowCount) || 0;
    const pendingSize = pendingRows ? `${pendingRows.toLocaleString()} service lines. ` : "";
    setTrackerWorkbookExportStatus(`<div class="tracker-export-readiness tracker-export-readiness--pending"><strong>Building your workbook…</strong><span>${escapeHtml(preview.retryNotice || `${pendingSize}Carry on working anywhere in the portal — the header chip tracks it and the download starts on its own. Keep the browser open.`)}</span></div>`);
    return;
  }
  // The current selection's own size check wins over whatever the preview last
  // stored (a build finishing after the filters changed stores ITS numbers).
  const countCurrent = trackerExportCountState.status === "ok" && trackerExportCountState.key === `count:${trackerExportPreviewKey()}`;
  const rows = Number(countCurrent ? trackerExportCountState.rowCount : (preview.rowCount || trackerExportCountState.rowCount || preview.trackerRows?.length || 0));
  const claims = Number(countCurrent ? trackerExportCountState.claimCount : (preview.claimCount || trackerExportCountState.claimCount || 0));
  const coverage = countCurrent && trackerExportCountState.firstServiceDate ? trackerExportCountState : preview;
  const picks = Object.values(normalizeTrackerExportSlicers(filters.slicers)).reduce((sum, values) => sum + values.length, 0);
  setTrackerWorkbookExportStatus(`<div class="tracker-export-readiness tracker-export-readiness--compact"><div><strong>Ready to download</strong><span>${escapeHtml(filters.tenant === "All" ? "All clients" : filters.tenant || "All clients")} · ${escapeHtml(trackerExportMonthLabel(filters) || "All service dates")}</span></div><div class="tracker-export-readiness-facts"><span><b>${formatCount(rows)}</b> service lines</span><span><b>${formatCount(claims)}</b> claims</span><span><b>${formatCount(picks)}</b> filters selected</span><span><b>Excel</b> ${trackerWorkbookExportSheetCount()} tabs</span><span>${escapeHtml(trackerExportDateCoverage(coverage))}</span></div></div>`);
}

function trackerWorkbookExportStatusTargets() {
  return [els.trackerWorkbookExportStatus, els.clientTrackerWorkbookExportStatus].filter(Boolean);
}

function trackerExportDateCoverage(preview = {}) {
  const first = String(preview.firstServiceDate || "").slice(0, 10);
  const last = String(preview.lastServiceDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(first) || !/^\d{4}-\d{2}-\d{2}$/.test(last)) return "Service-date coverage unavailable";
  return `Included service dates: ${first} to ${last}`;
}

function trackerExportEnvironmentNotice() {
  let host = "";
  try { host = new URL(reportApiBase()).hostname.toLowerCase(); } catch (_) { return ""; }
  if (host !== "tms-master-portal-test-api.azurewebsites.net") return "";
  return '<p class="tracker-export-environment-notice"><strong>TEST data.</strong> This copy may contain older service dates. <a href="https://vista.therapyms.com/?view=trackerExport" target="_blank" rel="noopener noreferrer">Open the production tracker</a> for current work.</p>';
}

function setTrackerWorkbookExportStatus(html) {
  const notice = trackerExportEnvironmentNotice();
  trackerWorkbookExportStatusTargets().forEach((target) => {
    target.innerHTML = notice + html;
  });
}

function trackerWorkbookExportButtons() {
  return [
    els.previewTrackerWorkbookBtn,
    els.downloadTrackerWorkbookBtn,
    els.clientPreviewTrackerWorkbookBtn,
    els.clientDownloadTrackerWorkbookBtn,
    els.openTrackerTeamSpaceBtn
  ].filter(Boolean);
}

function normalizeTrackerExportSlicers(value = {}) {
  const next = structuredClone(emptyTrackerExportSlicers);
  if (!value || typeof value !== "object") return next;
  TRACKER_EXPORT_SLICER_DEFS.forEach((def) => {
    const raw = Array.isArray(value[def.key]) ? value[def.key] : [];
    next[def.key] = Array.from(new Set(raw.map((item) => {
      const text = String(item || "").trim();
      if (!text) return "";
      if (def.key === "action") return followUpFilterSelectionValue(text);
      if (/^(blank|no action|no status|no class|no secondary|none)$/i.test(text)) return "Blank";
      return def.key === "match" ? canonicalMatchSlicerValue(text) : (def.key === "aging" ? canonicalSlicerValue("aging", text) : text);
    }).filter(Boolean)));
  });
  return next;
}

function claimMatchesTrackerExportSlicers(claim, service, slicers, today) {
  const active = normalizeTrackerExportSlicers(slicers);
  return TRACKER_EXPORT_SLICER_DEFS.every((def) => {
    const selected = active[def.key] || [];
    if (!selected.length) return true;
    const value = trackerExportSlicerValue(def.key, claim, service, today);
    if (selected.includes("Blank") && !value) return true;
    return selected.includes(value);
  });
}

function trackerExportSlicerValue(key, claim, service, today) {
  if (key === "action") return followUpFilterRowValue(claim, service);
  if (key === "claimStatus") return String(claim.claimStatus || "").trim();
  if (key === "aging") return canonicalSlicerValue("aging", claim.aging || service.aging || "");
  if (key === "match") return canonicalMatchSlicerValue(claim.match || service.match || "");
  if (key === "followUpDue") return followUpDueStatusFromDate(service.followUpDate || claim.followUpDate);
  if (key === "patClassification") return String(claim.patClassification || claim.patientClassification || "").trim();
  if (key === "procedure") return String(service.procedure || claim.procedure || "").trim();
  if (key === "secondaryCalc") return String(service.secondaryCalc || claim.secondaryCalc || "").trim();
  if (key === "primaryPayerClass") return payerClassFor(claim.primaryPayer);
  if (key === "primaryPayer") return String(claim.primaryPayer || "").trim();
  if (key === "secondaryPayer") return String(claim.secondaryPayer || "").trim();
  if (key === "claimClassification") return String(claim.claimClassification || claim.claClassification || "").trim();
  return "";
}

let trackerExportFacetState = { key: "", status: "idle", facets: {}, error: "" };
let trackerExportCountState = { key: "", status: "idle", rowCount: 0, claimCount: 0, error: "" };
let trackerExportFacetTimer = 0;
const trackerExportSlicerSearch = {};

function renderTrackerExportStudio() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("trackerExport")) {
    // Deferring the repaint must not defer the data. A publication resets the
    // counts and lands here while focus is still on the client/date control;
    // returning without asking left every count at 0 until something else
    // happened to re-render. The answers paint through the explicit bypass.
    const previewKey = trackerExportPreviewKey();
    if (state.view === "trackerExport"
      && (trackerExportFacetState.key !== `facets:${previewKey}` || trackerExportCountState.key !== `count:${previewKey}`)) {
      scheduleTrackerExportPreviewRefresh();
    }
    return;
  }
  if (!els.trackerExportSlicers) return;
  state.trackerExportSlicers = normalizeTrackerExportSlicers(state.trackerExportSlicers);
  renderTrackerExportSlicers();
  renderTrackerExportActiveChips();
  if (trackerTeamSpaceState.key !== trackerTeamSpaceRequestKey()) requestTrackerTeamSpace();
  renderTrackerWorkbookExportStatus();
  scheduleTrackerExportPreviewRefresh();
}

function renderTrackerExportSlicers() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("trackerExport")) return;
  const host = els.trackerExportSlicers;
  if (!host) return;
  const selected = normalizeTrackerExportSlicers(state.trackerExportSlicers);
  const commonKeys = new Set(["claimStatus", "aging", "match", "followUpDue", "primaryPayer"]);
  // Every count, toggle and clear rebuilds this markup; keep whatever the user
  // opened (a card, or Advanced Filters) open instead of collapsing it under them.
  const openCards = new Set(Array.from(host.querySelectorAll("details[data-export-slicer][open]")).map((node) => node.getAttribute("data-export-slicer")));
  const moreOpen = Boolean(host.querySelector("details.tracker-export-more[open]"));
  const focusedSearch = host.contains(document.activeElement) ? document.activeElement.getAttribute?.("data-export-slicer-search") : null;
  const focusedCaret = focusedSearch ? [document.activeElement.selectionStart, document.activeElement.selectionEnd] : null;
  const renderCard = (def) => {
    const picked = selected[def.key] || [];
    const visible = trackerExportVisibleSlicerRows(def, picked);
    const open = def.key === "action" || openCards.has(def.key) || Boolean(picked.length || trackerExportSlicerSearch[def.key]);
    return `
      <details class="tracker-export-slicer${def.key === "action" ? " tracker-export-slicer--action" : ""}"${open ? " open" : ""} data-export-slicer="${escapeHtml(def.key)}">
        <summary><span>${escapeHtml(def.label)}</span><em>${escapeHtml(picked.length ? `${picked.length} selected` : (trackerExportFacetState.status === "pending" ? "Loading" : trackerExportFacetState.status === "error" ? "Unavailable" : "All"))}</em></summary>
        ${def.searchable ? `<input class="tracker-export-slicer-search" type="search" data-export-slicer-search="${escapeHtml(def.key)}" value="${escapeHtml(trackerExportSlicerSearch[def.key] || "")}" placeholder="Search ${escapeHtml(def.label.toLowerCase())}">` : ""}
        <div class="tracker-export-slicer-actions"><button type="button" data-export-slicer-all="${escapeHtml(def.key)}">Select visible</button><button type="button" data-export-slicer-clear="${escapeHtml(def.key)}">Clear</button></div>
        <div class="tracker-export-slicer-values">${visible.map((row) => `<button type="button" class="tracker-export-slicer-val${picked.includes(row.value) ? " is-active" : ""}" data-export-slicer-toggle="${escapeHtml(def.key)}" data-export-slicer-value="${escapeHtml(row.value)}"><span>${escapeHtml(row.label)}</span><strong>${formatCount(row.count)}</strong></button>`).join("") || '<span class="muted">No matching values</span>'}</div>
      </details>`;
  };
  const action = TRACKER_EXPORT_SLICER_DEFS.filter((def) => def.key === "action").map(renderCard).join("");
  const common = TRACKER_EXPORT_SLICER_DEFS.filter((def) => commonKeys.has(def.key)).map(renderCard).join("");
  const more = TRACKER_EXPORT_SLICER_DEFS.filter((def) => def.key !== "action" && !commonKeys.has(def.key)).map(renderCard).join("");
  host.innerHTML = `<div class="tracker-export-filter-group tracker-export-filter-group--action"><h4>Follow-up action</h4><p>Always shown so every available action can be checked.</p>${action}</div><div class="tracker-export-filter-group"><h4>Common filters</h4><div class="tracker-export-filter-grid">${common}</div></div><details class="tracker-export-more"${moreOpen ? " open" : ""}><summary>Advanced Filters</summary><div class="tracker-export-filter-grid">${more}</div></details>`;
  const notice = document.getElementById("trackerExportFacetNotice");
  if (notice) {
    const stateCopy = trackerExportFacetState.status === "pending" ? "Loading live filter choices…" : trackerExportFacetState.status === "error" ? "Live counts unavailable. Selected filters are preserved." : trackerExportFacetState.status === "ok" ? "" : (!trackerApiEnabled() ? "Sample choices — live counts unavailable." : "Live filter choices will load for this scope.");
    notice.className = `tracker-export-facet-notice is-${escapeHtml(trackerExportFacetState.status || "idle")}`;
    notice.textContent = stateCopy;
  }
  host.querySelectorAll("[data-export-slicer-toggle]").forEach((button) => button.addEventListener("click", () => toggleTrackerExportSlicer(button.getAttribute("data-export-slicer-toggle"), button.getAttribute("data-export-slicer-value"))));
  host.querySelectorAll("[data-export-slicer-clear]").forEach((button) => button.addEventListener("click", () => clearTrackerExportSlicer(button.getAttribute("data-export-slicer-clear"))));
  host.querySelectorAll("[data-export-slicer-all]").forEach((button) => button.addEventListener("click", () => selectVisibleTrackerExportSlicer(button.getAttribute("data-export-slicer-all"))));
  host.querySelectorAll("[data-export-slicer-search]").forEach((input) => input.addEventListener("input", () => { trackerExportSlicerSearch[input.getAttribute("data-export-slicer-search")] = input.value; renderTrackerExportSlicers(); }));
  if (focusedSearch) {
    const input = Array.from(host.querySelectorAll("[data-export-slicer-search]")).find((node) => node.getAttribute("data-export-slicer-search") === focusedSearch);
    if (input) {
      input.focus();
      try { input.setSelectionRange(focusedCaret[0], focusedCaret[1]); } catch (_) {}
    }
  }
}

function trackerExportSlicerOptions(def, facetRows, selected) {
  const counts = new Map();
  (Array.isArray(facetRows) ? facetRows : []).forEach((row) => {
    const raw = row && typeof row === "object" ? row.value : row;
    const value = def.key === "action" ? followUpFilterValue(raw) : !String(raw || "").trim() || isBlankSlicerToken(raw) ? "Blank" : (def.key === "match" ? canonicalMatchSlicerValue(raw) : String(raw));
    if (!value) return;
    counts.set(value, (counts.get(value) || 0) + nonNegativeNumber(row && row.count, 0));
  });
  const required = [
    ...(typeof def.requiredValues === "function" ? def.requiredValues() : (def.requiredValues || []))
  ];
  if (def.blank && !required.includes("Blank")) required.unshift("Blank");
  if (def.key === "action") {
    actionRules.forEach((rule) => {
      if (!required.includes(rule.action)) required.push(rule.action);
    });
  }
  required.forEach((value) => {
    if (!counts.has(value)) counts.set(value, 0);
  });
  selected.forEach((value) => {
    if (!counts.has(value)) counts.set(value, 0);
  });
  // A required catalog name ("Write-Off") beside a live value that differs only
  // in case ("write-Off", 6,238 lines) read as two different actions, one empty.
  // Keep the live spelling: it is the value the server filter matches exactly.
  const liveCase = new Set(Array.from(counts.keys()).filter((value) => counts.get(value) > 0).map((value) => value.toLowerCase()));
  const values = Array.from(counts.keys()).filter((value) => counts.get(value) > 0 || selected.includes(value)
    || (required.includes(value) && !liveCase.has(value.toLowerCase())));
  const order = Array.isArray(def.order) ? def.order : null;
  values.sort((left, right) => {
    if (left === "Blank") return -1;
    if (right === "Blank") return 1;
    if (order) {
      const li = order.indexOf(left);
      const ri = order.indexOf(right);
      if (li !== -1 || ri !== -1) return (li === -1 ? 999 : li) - (ri === -1 ? 999 : ri);
    }
    return left.localeCompare(right);
  });
  return values.map((value) => ({
    value,
    label: slicerDisplayValue(def.key, value) || value,
    count: counts.get(value) || 0
  }));
}

function isBlankSlicerToken(value) {
  return /^(blank|no action|no status|no class|no secondary|none)$/i.test(String(value || "").trim());
}

function renderTrackerExportActiveChips() {
  const host = els.trackerExportActiveChips;
  if (!host) return;
  const selected = normalizeTrackerExportSlicers(state.trackerExportSlicers);
  const chips = TRACKER_EXPORT_SLICER_DEFS.flatMap((def) => (selected[def.key] || []).map((value) => ({
    key: def.key,
    label: def.label,
    value,
    display: def.key === "action" ? slicerDisplayValue("action", value) : value
  })));
  host.innerHTML = chips.map((chip) => `
    <span class="tracker-export-chip">
      ${escapeHtml(chip.label)}: ${escapeHtml(chip.display)}
      <button type="button" data-export-slicer-remove="${escapeHtml(chip.key)}" data-export-slicer-value="${escapeHtml(chip.value)}" aria-label="Remove ${escapeHtml(chip.label)} ${escapeHtml(chip.display)}">×</button>
    </span>
  `).join("");
  host.querySelectorAll("[data-export-slicer-remove]").forEach((button) => {
    button.addEventListener("click", () => toggleTrackerExportSlicer(button.getAttribute("data-export-slicer-remove"), button.getAttribute("data-export-slicer-value")));
  });
}

function toggleTrackerExportSlicer(key, value) {
  state.trackerExportSlicers = normalizeTrackerExportSlicers(state.trackerExportSlicers);
  if (!state.trackerExportSlicers[key]) return;
  const next = new Set(state.trackerExportSlicers[key]);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  state.trackerExportSlicers[key] = Array.from(next);
  saveState();
  renderTrackerExportStudio();
}

function clearTrackerExportSlicer(key) {
  state.trackerExportSlicers = normalizeTrackerExportSlicers(state.trackerExportSlicers);
  state.trackerExportSlicers[key] = [];
  saveState();
  renderTrackerExportStudio();
}

// This button already says "Clear all filters", and the month range IS a filter:
// a workbook still scoped to one month after a clear is the All Data defect one
// dialog over. Company scope stays, for the same reason it stays on the grid.
function clearAllTrackerExportSlicers() {
  const exportControls = primaryTrackerExportControlSet();
  if (exportControls.fromDate) exportControls.fromDate.value = "";
  if (exportControls.toDate) exportControls.toDate.value = "";
  syncTrackerExportControls(exportControls);
  state.trackerExportSlicers = structuredClone(emptyTrackerExportSlicers);
  saveState();
  renderTrackerExportStudio();
}

// The ONE definition of what a card lists, used by the card and by "Select
// visible". They used to differ: the card matched action names by prefix and
// other cards by label, while Select visible matched raw values by substring,
// so typing "pending" showed no actions and Select visible still picked all
// six Client Action Pending actions into the export.
function trackerExportVisibleSlicerRows(def, picked = []) {
  const facets = trackerExportFacetState.facets || {};
  const values = trackerExportSlicerOptions(def, facets[def.facet] || facets[def.key] || [], picked);
  const query = String(trackerExportSlicerSearch[def.key] || "").trim().toLowerCase();
  if (!query) return values;
  return values.filter((row) => def.key === "action"
    ? followUpActionChoiceMatches("action", row.label, query)
    : row.value.toLowerCase().includes(query) || row.label.toLowerCase().includes(query));
}

function selectVisibleTrackerExportSlicer(key) {
  const def = TRACKER_EXPORT_SLICER_DEFS.find((item) => item.key === key);
  if (!def) return;
  const selected = normalizeTrackerExportSlicers(state.trackerExportSlicers);
  const visible = trackerExportVisibleSlicerRows(def, selected[key] || []).map((row) => row.value);
  state.trackerExportSlicers = normalizeTrackerExportSlicers(state.trackerExportSlicers);
  state.trackerExportSlicers[key] = Array.from(new Set([...(state.trackerExportSlicers[key] || []), ...visible]));
  saveState();
  renderTrackerExportStudio();
}

function scheduleTrackerExportPreviewRefresh() {
  window.clearTimeout(trackerExportFacetTimer);
  trackerExportFacetTimer = window.setTimeout(() => {
    // A From after To cannot be answered; asking only spent two warehouse
    // queries on a 400 while the card already says "Check the date range".
    const { fromDate, toDate } = trackerWorkbookExportFilters();
    if (fromDate && toDate && fromDate > toDate) return;
    requestTrackerExportFacets();
    requestTrackerExportCount();
  }, 350);
}

function trackerExportPreviewKey() {
  return JSON.stringify({ publicationEpoch: typeof reportReadPublicationIdentity === "function" ? reportReadPublicationIdentity() : (typeof trackerPublicationEpoch === "number" ? trackerPublicationEpoch : 0), base: typeof trackerApiBase === "function" ? trackerApiBase() : "", reportBase: reportApiBase(), access: typeof trackerAccessEnvelope === "function" ? trackerAccessEnvelope("service") : null, filters: trackerWorkbookExportFilters() });
}

function requestTrackerExportFacets() {
  const base = trackerApiBase();
  if (!base || typeof fetch !== "function" || !trackerApiEnabled()) return;
  const filters = trackerWorkbookExportFilters();
  const key = `facets:${trackerExportPreviewKey()}`;
  trackerExportFacetState = { ...trackerExportFacetState, key, status: "pending", error: "" };
  renderTrackerExportSlicersAfterFacets();
  const token = trackerApiToken();
  fetch((typeof portalFreshReadUrl === "function" ? portalFreshReadUrl(`${base}/api/tracker/facets`) : `${base}/api/tracker/facets`), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({
      resource: "serviceLines",
      mode: "service",
      page: { number: 1, size: 1 },
      filters: {
        tenant: filters.tenant,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        fromDos: filters.fromDate,
        toDos: filters.toDate
      },
      slicers: filters.slicers,
      facets: TRACKER_EXPORT_SLICER_DEFS.map((def) => def.facet),
      access: trackerAccessEnvelope("service")
    })
  }).then(async (response) => {
    const body = await response.json().catch(() => ({}));
    if (trackerExportFacetState.key !== key || key !== `facets:${trackerExportPreviewKey()}`) return;
    if (!response.ok) {
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      trackerExportFacetState = { key, status: "error", facets: trackerExportFacetState.facets || {}, error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body.message || body.error || `HTTP ${response.status}`) };
      renderTrackerExportSlicersAfterFacets();
      return;
    }
    trackerExportFacetState = { key, status: "ok", facets: body.facets || body || {}, error: "" };
    renderTrackerExportSlicersAfterFacets();
  }).catch((error) => {
    if (trackerExportFacetState.key !== key || key !== `facets:${trackerExportPreviewKey()}`) return;
    trackerExportFacetState = { key, status: "error", facets: trackerExportFacetState.facets || {}, error: error.message || "facet request failed" };
    renderTrackerExportSlicersAfterFacets();
  });
}

function renderTrackerWorkbookExportStatusAfterCurrentCount() {
  renderTrackerExportOwnResponse(renderTrackerWorkbookExportStatus);
}
// The page's own answers repaint through the publication guard. That guard
// defers background repaints while a SELECT/INPUT has focus, and focus usually
// stays on the client or date control the user just changed, so without this
// the counts they asked for stayed on screen as "will load…" with every value 0.
function renderTrackerExportOwnResponse(render) {
  if (state.view !== "trackerExport") return;
  const previousExplicitRenderUntil = trackerPublicationWatch.explicitRenderUntil;
  trackerPublicationWatch.explicitRenderUntil = Date.now() + 1000;
  try { render(); }
  finally { trackerPublicationWatch.explicitRenderUntil = previousExplicitRenderUntil; }
}
function renderTrackerExportSlicersAfterFacets() {
  renderTrackerExportOwnResponse(() => {
    renderTrackerExportSlicers();
    renderTrackerExportActiveChips();
  });
}
function requestTrackerExportCount(attempt = 0) {
  if (!reportApiEnabled() || typeof fetch !== "function") {
    const preview = buildLocalTrackerWorkbookExportPreview({ dryRun: true });
    trackerExportCountState = {
      key: trackerExportPreviewKey(),
      status: "ok",
      rowCount: preview.trackerRows.length,
      claimCount: preview.claimCount,
      error: ""
    };
    state.trackerWorkbookExportPreview = {
      ...(state.trackerWorkbookExportPreview || preview),
      ...preview,
      rowCount: preview.trackerRows.length,
      claimCount: preview.claimCount
    };
    renderTrackerWorkbookExportStatusAfterCurrentCount();
    return;
  }
  const filters = trackerWorkbookExportFilters();
  const key = `count:${trackerExportPreviewKey()}`;
  trackerExportCountState = { ...trackerExportCountState, key, status: "pending", error: "" };
  const token = reportApiToken();
  reportSessionFetch(`${reportApiBase()}/api/reports/tracker-workbook-export`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ preview: true, filters })
  }).then(async (response) => {
    const body = await response.json().catch(() => ({}));
    if (trackerExportCountState.key !== key || key !== `count:${trackerExportPreviewKey()}`) return;
    if (!response.ok) {
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      // A busy API answers the size check with a 502/503 now and then. The
      // download stays blocked on this answer, so ask once more before showing
      // an error the user can only clear by changing the selection.
      if (response.status >= 500 && attempt < 1) { retryTrackerExportCount(key, attempt); return; }
      trackerExportCountState = { key, status: "error", rowCount: 0, claimCount: 0, error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body.message || body.error?.message || (typeof body.error === "string" ? body.error : "") || `HTTP ${response.status}`) };
      renderTrackerWorkbookExportStatusAfterCurrentCount();
      return;
    }
    trackerExportCountState = {
      key,
      status: "ok",
      rowCount: Number(body.rowCount || 0),
      claimCount: Number(body.claimCount || 0),
      firstServiceDate: body.firstServiceDate || "",
      lastServiceDate: body.lastServiceDate || "",
      rowLimit: Number(body.rowLimit) || 0,
      error: ""
    };
    if (state.trackerWorkbookExportPreview?.status !== "pending" && state.trackerWorkbookExportJob?.status !== "building") state.trackerWorkbookExportPreview = {
      ...(state.trackerWorkbookExportPreview || {}),
      filters,
      monthLabel: trackerExportMonthLabel(filters),
      rowCount: trackerExportCountState.rowCount,
      claimCount: trackerExportCountState.claimCount,
      firstServiceDate: trackerExportCountState.firstServiceDate,
      lastServiceDate: trackerExportCountState.lastServiceDate,
      error: "",
      validation: null,
      status: "ready"
    };
    renderTrackerWorkbookExportStatusAfterCurrentCount();
  }).catch((error) => {
    if (trackerExportCountState.key !== key || key !== `count:${trackerExportPreviewKey()}`) return;
    if (attempt < 1) { retryTrackerExportCount(key, attempt); return; }
    const message = isTrackerExportNetworkError(error)
      ? "The report service could not be reached; your connection may have dropped."
      : (error.message || "preview failed");
    trackerExportCountState = { key, status: "error", rowCount: 0, claimCount: 0, error: message };
    renderTrackerWorkbookExportStatusAfterCurrentCount();
  });
}

function retryTrackerExportCount(key, attempt) {
  window.setTimeout(() => {
    if (trackerExportCountState.key === key && key === `count:${trackerExportPreviewKey()}`) requestTrackerExportCount(attempt + 1);
  }, 2000);
}

function applyTrackerExportDatePreset(preset) {
  const from = els.clientTrackerExportFromMonth;
  const to = els.clientTrackerExportToMonth;
  if (!from || !to) return;
  if (!preset) {
    from.value = "";
    to.value = "";
  } else {
    const today = todayDateString();
    to.value = today;
    if (preset === "mtd") from.value = `${today.slice(0, 7)}-01`;
    else if (preset === "ytd") from.value = `${today.slice(0, 4)}-01-01`;
    else from.value = addDays(today, -Math.max(1, Number(preset) || 30) + 1);
  }
  syncTrackerExportControls({ tenant: els.clientTrackerExportTenant, fromDate: from, toDate: to });
  renderTrackerExportStudio();
}

function buildLocalTrackerWorkbookExportPreview() {
  const filters = trackerWorkbookExportFilters();
  const claims = trackerWorkbookExportClaims();
  const trackerRows = trackerExportServiceRows(claims);
  const monthlyRows = trackerExportMonthlyRows(trackerRows);
  const payerRows = trackerExportPayerRows(trackerRows);
  const followUpRows = trackerExportFollowUpRows(trackerRows);
  return {
    schema: "tms-master-portal.tracker-workbook-export.local-preview.v1",
    templateWorkbook: "Server-side current tracker template (.xlsx)",
    exactTemplateRequired: true,
    localFallbackFormat: "Preview only",
    filters,
    monthLabel: trackerExportMonthLabel(filters),
    claimCount: claims.length,
    trackerRows,
    workbook: {
      sheets: [
        { name: "LITE Biller View", rowCount: trackerRows.length, tableName: "TMS_01_LITE_Biller_View" },
        { name: "LITE Monthly Summary", rowCount: monthlyRows.length, tableName: "TMS_02_LITE_Monthly_Summary" },
        { name: "Executive Rollforward", rowCount: monthlyRows.length, tableName: "TMS_03_Executive_Rollforward" },
        { name: "Patient Responsibility", rowCount: trackerRows.length, tableName: "TMS_04_Patient_Responsibility" },
        { name: "AR Aging Summary", rowCount: trackerRows.length, tableName: "TMS_05_AR_Aging_Summary" },
        { name: "Bill Date Monthly Aging Summary", rowCount: trackerRows.length, tableName: "TMS_06_Bill_Date_Aging_Summary" },
        { name: "Bill Date Weekly Aging Summary", rowCount: trackerRows.length, tableName: "TMS_07_Bill_Date_Weekly_Aging_Summary" },
        { name: "Client Action Pending", rowCount: followUpRows.length, tableName: "TMS_08_Client_Action_Pending" },
        { name: "Avg Payment Cycle Per Payer", rowCount: payerRows.length, tableName: "TMS_09_Avg_Payment_Cycle_Per_Payer" },
        { name: "Reimbursement by CPT Payer", rowCount: payerRows.length, tableName: "TMS_10_Reimbursement_by_CPT_Payer" },
        { name: "Disbursement Detail", rowCount: trackerRows.length, tableName: "TMS_11_Disbursement_Detail" },
        { name: "Payment Reconciliation", rowCount: payerRows.length, tableName: "TMS_12_Payment_Reconciliation" },
        { name: "Payment Exceptions", rowCount: payerRows.length, tableName: "TMS_13_Payment_Exceptions" }
      ]
    },
    pivots: [],
    monthlyRows,
    payerRows,
    followUpRows
  };
}

function trackerExportServiceRows(claims) {
  return claims.flatMap((claim) => (claim.services || []).map((service) => {
    const row = {};
    // exportLabel: the workbook keeps the tracker's header when the grid renames
    // a column for readers (Left After Insurance is still "Pat Bal" here).
    serviceColumns.forEach((column) => {
      row[column.exportLabel || column.label] = getColumnValue(column.key, claim, service);
    });
    // Exact (unrounded) money values for the monthly/payer/follow-up
    // rollups. The visible cells above are money()-formatted to whole
    // dollars; summing those rounded strings drifts the summaries by up to
    // $0.50 per service line. Non-enumerable so sheet column iteration and
    // JSON exports never see it.
    const claimFinancial = claimFinancialView(claim);
    const serviceFinancial = service ? serviceFinancialView(service, claim) : null;
    const rowFinancial = serviceFinancial || claimFinancial;
    Object.defineProperty(row, "__exactMoney", {
      value: {
        "Total Expected": moneyNumber(serviceFinancial?.finalInsuranceExpected ?? rowFinancial.expectedReimbursement ?? 0),
        "Total Paid": moneyNumber(serviceFinancial?.insurancePaid ?? claimFinancial.totalInsurancePaid ?? 0),
        "Total Ins Owed": moneyNumber(serviceFinancial?.finalInsuranceBalance ?? rowFinancial.sourceOutstanding ?? 0)
      },
      enumerable: false
    });
    return row;
  }));
}

// Prefer the exact stashed value; fall back to parsing the (whole-dollar)
// display string for rows built elsewhere.
function trackerExportExactMoney(row, label) {
  const exact = row && row.__exactMoney ? row.__exactMoney[label] : undefined;
  return Number.isFinite(exact) ? exact : moneyToExportNumber(row ? row[label] : 0);
}

function trackerExportMonthlyRows(rows) {
  const grouped = new Map();
  rows.forEach((row) => {
    const month = serviceMonthFromDisplay(row["Srvc Date"]);
    const payer = row["Primary Payer"] || "Unknown";
    const key = `${month}||${payer}`;
    const current = grouped.get(key) || { Month: month || "Unknown", Payer: payer, "Service Lines": 0, "Total Expected": 0, "Total Paid": 0, "Total Ins Owed": 0 };
    current["Service Lines"] += 1;
    current["Total Expected"] += trackerExportExactMoney(row, "Total Expected");
    current["Total Paid"] += trackerExportExactMoney(row, "Total Paid");
    current["Total Ins Owed"] += trackerExportExactMoney(row, "Total Ins Owed");
    grouped.set(key, current);
  });
  return Array.from(grouped.values())
    .map((row) => ({ ...row, "Total Expected": moneyNumber(row["Total Expected"]), "Total Paid": moneyNumber(row["Total Paid"]), "Total Ins Owed": moneyNumber(row["Total Ins Owed"]) }))
    .sort((a, b) => String(a.Month).localeCompare(String(b.Month)) || String(a.Payer).localeCompare(String(b.Payer)));
}

function trackerExportPayerRows(rows) {
  const grouped = new Map();
  rows.forEach((row) => {
    const payer = row["Primary Payer"] || "Unknown";
    const current = grouped.get(payer) || { Payer: payer, "Service Lines": 0, "Total Expected": 0, "Total Paid": 0, "Total Ins Owed": 0 };
    current["Service Lines"] += 1;
    current["Total Expected"] += trackerExportExactMoney(row, "Total Expected");
    current["Total Paid"] += trackerExportExactMoney(row, "Total Paid");
    current["Total Ins Owed"] += trackerExportExactMoney(row, "Total Ins Owed");
    grouped.set(payer, current);
  });
  return Array.from(grouped.values())
    .map((row) => ({ ...row, "Total Expected": moneyNumber(row["Total Expected"]), "Total Paid": moneyNumber(row["Total Paid"]), "Total Ins Owed": moneyNumber(row["Total Ins Owed"]) }))
    .sort((a, b) => b["Total Ins Owed"] - a["Total Ins Owed"]);
}

function trackerExportFollowUpRows(rows) {
  const grouped = new Map();
  rows.forEach((row) => {
    const action = row["Follow up Action"] || "Blank";
    const current = grouped.get(action) || { "Follow up Action": action, "Service Lines": 0, "Total Ins Owed": 0 };
    current["Service Lines"] += 1;
    current["Total Ins Owed"] += trackerExportExactMoney(row, "Total Ins Owed");
    grouped.set(action, current);
  });
  return Array.from(grouped.values())
    .map((row) => ({ ...row, "Total Ins Owed": moneyNumber(row["Total Ins Owed"]) }))
    .sort((a, b) => b["Total Ins Owed"] - a["Total Ins Owed"]);
}

function serviceMonthFromDisplay(value) {
  const text = String(value || "");
  const iso = text.match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) return `${us[3]}-${String(us[1]).padStart(2, "0")}`;
  return "";
}

function moneyToExportNumber(value) {
  const number = moneyToNumber(value);
  return Number.isFinite(number) ? number : 0;
}

function trackerExportMonthLabel(filters) {
  if (filters.fromDate && filters.toDate && filters.fromDate !== filters.toDate) return `${formatDate(filters.fromDate)} to ${formatDate(filters.toDate)}`;
  if (filters.fromDate || filters.toDate) return formatDate(filters.fromDate || filters.toDate);
  if (filters.fromMonth && filters.toMonth && filters.fromMonth !== filters.toMonth) return `${formatExportMonth(filters.fromMonth)} to ${formatExportMonth(filters.toMonth)}`;
  return filters.fromMonth ? formatExportMonth(filters.fromMonth) : "all dates";
}

function trackerWorkbookFilename(preview) {
  const tenant = String(preview.filters.tenant === "All" ? "all-accessible-companies" : preview.filters.tenant)
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase() || "tracker";
  const range = trackerWorkbookFilenameRange(preview.filters);
  return `tms-tracker-${tenant}-${range || "all-dates"}.xls`;
}

function trackerWorkbookFilenameRange(filters = {}) {
  const from = (filters.fromDate || filters.fromMonth || "start").replace(/[^0-9]+/g, "");
  const to = (filters.toDate || filters.toMonth || "current").replace(/[^0-9]+/g, "");
  return `${from}-to-${to}`.replace(/^-|-$/g, "");
}

function formatExportMonth(month = "") {
  const text = String(month || "");
  if (!/^\d{4}-\d{2}$/.test(text)) return text;
  const [year, monthNumber] = text.split("-");
  return `${Number(monthNumber)}/1/${year}`;
}

function buildTrackerWorkbookXml(preview) {
  const sheets = [
    { name: "LITE All Data View", rows: preview.trackerRows },
    { name: "LITE Build Instructions", rows: trackerPivotInstructionRows() },
    { name: "LITE Monthly Summary", rows: preview.monthlyRows },
    { name: "LITE Biller View", rows: preview.followUpRows },
    { name: "LITE Payment Reconciliation", rows: preview.payerRows },
    { name: "Export Manifest", rows: trackerExportManifestRows(preview) }
  ];
  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="header"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#1f2937" ss:Pattern="Solid"/></Style>
  <Style ss:ID="money"><NumberFormat ss:Format="$#,##0.00;[Red]($#,##0.00)"/></Style>
  <Style ss:ID="date"><NumberFormat ss:Format="m/d/yyyy"/></Style>
 </Styles>
 ${sheets.map(excelXmlSheet).join("\n")}
</Workbook>`;
}

function trackerExportManifestRows(preview) {
  return [
    { Field: "Template workbook required for exact output", Value: preview.templateWorkbook },
    { Field: "Local fallback format", Value: preview.localFallbackFormat },
    { Field: "Exact source sheet", Value: "LITE All Data View" },
    { Field: "Exact source table", Value: "LiteAllDataView" },
    { Field: "Tenant", Value: preview.filters.tenant },
    { Field: "Months", Value: preview.monthLabel },
    { Field: "Tracker rows", Value: preview.trackerRows.length },
    { Field: "Claims", Value: preview.claimCount },
    { Field: "Pivot tabs", Value: preview.pivots.map((pivot) => pivot.name).join(", ") }
  ];
}

function trackerPivotInstructionRows() {
  return [
    {
      Tab: "LITE All Data View",
      Source: "Generated service-line export",
      Rows: "One row per service line",
      Columns: "Use the exported tracker columns in order",
      Values: "No pivot; format as table named LiteAllDataView",
      Filters: "Enable table filters on all columns",
      Notes: "This is the source range for all recreated pivots."
    },
    {
      Tab: "LITE Biller View",
      Source: "LITE All Data View / LiteAllDataView",
      Rows: "Client, Srvc Date, Procedure",
      Columns: "",
      Values: "Count of Srvc Date; Sum of Total Ins Owed",
      Filters: "Follow up Action, Claim Due, Match, Aging, Primary Payer, Secondary Payer, Facility",
      Notes: "Main biller outstanding follow-up queue."
    },
    {
      Tab: "LITE Monthly Summary",
      Source: "LITE All Data View / LiteAllDataView",
      Rows: "Srvc Date grouped by Years and Months; Primary Payer",
      Columns: "",
      Values: "Count of Srvc Date; Sum Units; Sum Primary Expected Total; Sum Secondary Expected Total; Sum Total Expected; Sum Write-off Amount; Sum Positive Total Pat Bal; Sum Total Ins Amt Paid; Sum Total Ins Owed",
      Filters: "Client, Rendering Provider, Facility, Claim Status, Cla Classification, In/Out of Network",
      Notes: "Client-facing monthly payer performance pivot."
    },
    {
      Tab: "LITE Payment Reconciliation",
      Source: "LITE All Data View / LiteAllDataView",
      Rows: "Aging, Match",
      Columns: "",
      Values: "Count of Claim ID; Sum Total Expected; Sum Total Ins Amt Paid; Sum Total Ins Owed; Sum Write-off Amount",
      Filters: "Client, Primary Payer, Secondary Payer, Follow up Action, Claim Due",
      Notes: "Management aging and collection summary."
    },
    {
      Tab: "LITE Payment Exceptions",
      Source: "LITE All Data View / LiteAllDataView",
      Rows: "Client, Primary Payer",
      Columns: "Match",
      Values: "Count of Service ID; Sum Total Expected; Sum Total Ins Amt Paid; Sum Total Ins Owed",
      Filters: "Srvc Date grouped by month, Aging, Claim Due, Follow up Action",
      Notes: "High-level payer/client summary."
    },
    {
      Tab: "Average Payment Cycle Per Payer",
      Source: "LITE All Data View / LiteAllDataView",
      Rows: "Week Ending, Primary Payer",
      Columns: "",
      Values: "Count of Service ID; Sum Units; Sum Total Expected; Sum Total Ins Amt Paid; Sum Total Ins Owed",
      Filters: "Client, Facility, Rendering Provider, Match",
      Notes: "Weekly operating view."
    },
    {
      Tab: "LITE Rate Issues",
      Source: "LITE All Data View / LiteAllDataView",
      Rows: "Client, Primary Payer, Follow up Action",
      Columns: "",
      Values: "Count of Service ID; Sum Write-off Amount; Sum Total Ins Owed",
      Filters: "Follow up Action contains Write-Off, Srvc Date, Aging, Match",
      Notes: "Write-off review pivot."
    },
    {
      Tab: "Avg Reimb Per Unit by CPT",
      Source: "LITE All Data View / LiteAllDataView",
      Rows: "Client, Primary Payer, Procedure",
      Columns: "",
      Values: "Count of Service ID; Sum Units; Sum Total Expected; Sum Total Ins Amt Paid; Sum Total Ins Owed",
      Filters: "In/Out of Network = OON, Srvc Date, Facility, Rendering Provider",
      Notes: "Out-of-network claim review."
    },
    {
      Tab: "LITE Secondary Mapping",
      Source: "LITE All Data View / LiteAllDataView",
      Rows: "Client, Secondary Payer, Claim Status",
      Columns: "",
      Values: "Count of Service ID; Sum Secondary Expected Total; Sum Total Ins Owed",
      Filters: "Cla Classification, Follow up Action, Match, Claim Due",
      Notes: "Secondary billing placeholder/work queue."
    }
  ];
}

function excelXmlSheet(sheet) {
  const rows = Array.isArray(sheet.rows) ? sheet.rows : [];
  const columns = rows.length ? Object.keys(rows[0]) : [];
  return `<Worksheet ss:Name="${excelXmlAttr(sheet.name)}"><Table>
${excelXmlRow(columns, true)}
${rows.map((row) => excelXmlRow(columns.map((column) => row[column]), false)).join("\n")}
</Table><AutoFilter x:Range="R1C1:R${Math.max(1, rows.length + 1)}C${Math.max(1, columns.length)}" xmlns="urn:schemas-microsoft-com:office:excel"/></Worksheet>`;
}

function excelXmlRow(values, header = false) {
  return `<Row>${values.map((value) => excelXmlCell(value, header)).join("")}</Row>`;
}

function excelXmlCell(value, header = false) {
  const number = typeof value === "number" ? value : (typeof value === "string" && /^\$/.test(value) ? moneyToExportNumber(value) : NaN);
  const style = header ? ' ss:StyleID="header"' : (Number.isFinite(number) && value !== "" ? ' ss:StyleID="money"' : "");
  if (!header && Number.isFinite(number) && value !== "") return `<Cell${style}><Data ss:Type="Number">${number}</Data></Cell>`;
  return `<Cell${style}><Data ss:Type="String">${excelXmlText(value)}</Data></Cell>`;
}

function excelXmlText(value) {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function excelXmlAttr(value) {
  return excelXmlText(value).replace(/"/g, "&quot;");
}

function filenameFromContentDisposition(value = "") {
  const match = String(value || "").match(/filename="?([^";]+)"?/i);
  return match ? match[1] : "";
}

function reportApiResponseForRequest(request) {
  if (!reportApiEnabled()) return null;
  const key = reportApiCacheKey(request);
  if (reportApiState.key === key && reportApiState.status === "ok" && reportApiState.response) {
    return { response: reportApiState.response };
  }
  if (reportApiState.key !== key || reportApiState.status === "idle") {
    requestReportApiQuery(request, key);
  }
  return null;
}

function requestReportApiQuery(request, key = reportApiCacheKey(request)) {
  const base = reportApiBase();
  if (!base || typeof fetch !== "function") return;
  const requestStillCurrent = () => (
    reportApiState.key === key
    && reportApiCacheKey(request) === key
  );
  reportApiState = { key, status: "pending", response: null, error: "" };
  const token = reportApiToken();
  reportSessionFetch(`${base}/api/reports/query`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(request)
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        reportApiState = {
          key,
          status: "error",
          response: null,
          error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body?.error?.message || body.message || `HTTP ${response.status}`)
        };
        renderReports();
        return;
      }
      reportApiState = { key, status: "ok", response: body, error: "" };
      renderReports();
    })
    .catch((error) => {
      if (!requestStillCurrent()) return;
      reportApiState = { key, status: "error", response: null, error: error.message };
      renderReports();
    });
}

function previewReportExportPackage() {
  if (!hasPermission(PERMISSIONS.REPORT_EXPORT) && !hasPermission(PERMISSIONS.EXPORT_WORKFLOW)) {
    state.reportExportPackagePreview = {
      error: "Current role cannot preview report export packages.",
      authorization: { allowed: false, reason: "missing_report_export_permission" }
    };
    renderReportExportPackagePreview();
    return;
  }
  const request = {
    ...buildReportQueryRequest(),
    formats: ["csv", "xlsx", "pdf-ready"]
  };
  if (reportApiEnabled()) {
    requestReportExportPackagePreview(request);
    return;
  }
  const claims = getReportClaims(getClaims());
  state.reportExportPackagePreview = buildLocalReportExportPackagePreview({ claims, filters: request.filters });
  audit("Previewed local enterprise report package without managed downloads.", "Reports");
  saveState();
  renderReportExportPackagePreview();
}

function requestReportExportPackagePreview(request) {
  const base = reportApiBase();
  if (!base || typeof fetch !== "function") return;
  const requestParts = { base, request };
  const requestKey = workflowAccessScopedRequestKey(requestParts);
  const requestStillCurrent = () => (
    reportExportPackagePreviewRequestKey === requestKey
    && workflowAccessScopedRequestKey(requestParts) === requestKey
  );
  reportExportPackagePreviewRequestKey = requestKey;
  state.reportExportPackagePreview = { status: "pending", requestedAt: timestamp(), filters: request?.filters || {} };
  renderReportExportPackagePreview();
  const token = reportApiToken();
  fetch(`${base}/api/reports/export-package-preview`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(request)
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        state.reportExportPackagePreview = {
          status: "error",
          error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : (body?.error?.message || body.message || `HTTP ${response.status}`),
          authorization: body.authorization || null
        };
      } else {
        state.reportExportPackagePreview = body;
        audit("Previewed enterprise report package.", "Reports");
      }
      saveState();
      renderReportExportPackagePreview();
    })
    .catch((error) => {
      if (!requestStillCurrent()) return;
      state.reportExportPackagePreview = { status: "error", error: error.message };
      saveState();
      renderReportExportPackagePreview();
    });
}

function renderReportExportPackagePreview() {
  if (!els.reportExportPackagePreview) return;
  const preview = state.reportExportPackagePreview;
  if (els.previewReportPackageBtn) {
    els.previewReportPackageBtn.disabled = !hasPermission(PERMISSIONS.REPORT_EXPORT) && !hasPermission(PERMISSIONS.EXPORT_WORKFLOW);
  }
  if (!preview) {
    els.reportExportPackagePreview.innerHTML = `
      <div class="export-package-empty">
        <strong>No package preview loaded</strong>
        <span>Preview creates aggregate-only export manifests for CSV, Excel, and PDF-ready reporting.</span>
      </div>
    `;
    return;
  }
  if (preview.status === "pending") {
    els.reportExportPackagePreview.innerHTML = empty("Building enterprise report package preview.");
    return;
  }
  if (preview.error) {
    els.reportExportPackagePreview.innerHTML = empty(`Report package preview unavailable: ${preview.error}`);
    return;
  }
  const job = preview.exportJob || {};
  const sheets = preview.workbook?.sheets || [];
  const pivots = preview.pivots || [];
  const pdfSections = preview.pdfReadyReport?.sections || [];
  els.reportExportPackagePreview.innerHTML = `
    <div class="report-export-summary">
      <span><strong>${escapeHtml(job.exportJobId || "local-report-package")}</strong></span>
      <span>${escapeHtml((job.formats || ["csv", "xlsx", "pdf-ready"]).join(" + "))}</span>
      <span>${formatNounCount(job.rowCount, "aggregate row")}</span>
      <span>${job.execution?.readyForDownloads ? "Downloads enabled" : "Preview only"}</span>
    </div>
    <div class="report-export-package-grid">
      ${renderPackageGroup("Workbook Sheets", sheets.map((sheet) => ({ label: sheet.name, meta: `${formatNounCount(sheet.rowCount, "row")} · ${formatNounCount(sheet.columns?.length || 0, "column")}` })))}
      ${renderPackageGroup("Pivot Summaries", pivots.map((pivot) => ({ label: pivot.name, meta: formatNounCount(pivot.rowCount, "row") })))}
      ${renderPackageGroup("PDF-Ready Sections", pdfSections.map((section) => ({ label: section.title, meta: section.type })))}
    </div>
  `;
}

function renderPackageGroup(title, rows) {
  return `
    <div class="report-export-group">
      <h4>${escapeHtml(title)}</h4>
      ${(rows || []).slice(0, 8).map((row) => `
        <div class="report-export-item">
          <span>${escapeHtml(row.label)}</span>
          <strong>${escapeHtml(row.meta)}</strong>
        </div>
      `).join("") || '<div class="report-export-item"><span>No rows</span><strong>0</strong></div>'}
    </div>
  `;
}

function buildLocalReportExportPackagePreview({ claims, filters }) {
  const includeFinancials = hasPermission(PERMISSIONS.VIEW_FINANCIALS);
  const analytics = buildExecutiveAnalytics(claims, includeFinancials);
  const followUpQueues = buildLocalReportFollowUpPackage(claims);
  const sheets = [
    packageSheet("Executive Summary", reportSummaryMetrics({
      claims,
      serviceRows: claims.flatMap((claim) => claim.services || []),
      financialBuckets: reportFinancialBuckets(claims),
      totalBilled: claims.flatMap((claim) => claim.services || []).reduce((sum, service) => sum + moneyNumber(service.charges || 0), 0),
      finalCollectionRate: "Local",
      apiStatus: reportApiStatusSummary()
    }).map(([metric, value]) => ({ metric, value }))),
    packageSheet("AR Aging", reportAgingMetrics(null, claims).map(([bucket, value]) => ({ bucket, value }))),
    packageSheet("Provider Performance", analytics.providerPerformance),
    packageSheet("Monthly Revenue Trend", analytics.monthlyTrends),
    packageSheet("Cash Flow", analytics.cashFlow),
    packageSheet("Payer Behavior", analytics.payerBehavior),
    packageSheet("Denials In Appeal", analytics.denialTrends),
    packageSheet("Underpayment Recovery", analytics.underpaymentExposure),
    packageSheet("Premium AR Focus", analytics.highRiskAr),
    packageSheet("Payer Worklists", followUpQueues.payerQueueRows),
    packageSheet("Status Queues", followUpQueues.statusQueueRows)
  ].filter((sheet) => sheet.rowCount > 0);
  const pivots = [
    packagePivot("Payer x Follow-Up Queue", followUpQueues.payerQueueRows, "payer", "queue"),
    packagePivot("Denial Reason x Month", analytics.denialTrends, "reason", "month"),
    packagePivot("Provider x Collection", analytics.providerPerformance, "provider", "collectionRate")
  ];
  return {
    schema: "tms-master-portal.report-export-package-preview.local.v1",
    exportJob: {
      exportJobId: `local-report-package-${Date.now()}`,
      exportKind: "enterprise_report_package",
      formats: ["csv", "xlsx", "pdf-ready"],
      filters,
      rowCount: sheets.reduce((sum, sheet) => sum + sheet.rowCount, 0) + pivots.reduce((sum, pivot) => sum + pivot.rowCount, 0),
      execution: { readyForDownloads: false, readyForWrite: false, reason: "local_preview_no_managed_exports" },
      authorization: { allowed: true, permission: "reports:export", reason: "" }
    },
    workbook: { formats: ["csv", "xlsx"], sheets },
    pivots,
    pdfReadyReport: {
      format: "pdf-ready",
      sections: [
        { title: "Executive Revenue Summary", type: "kpi-grid" },
        { title: "AR Aging And Risk", type: "table-pair" },
        { title: "Operational Analytics", type: "drilldown-index" },
        { title: "Pivot Appendix", type: "pivot-index" }
      ]
    }
  };
}

function buildLocalReportFollowUpPackage(claims) {
  return {
    payerQueueRows: payerWorklistRows(claims),
    statusQueueRows: reportStatusQueueRows({ statusQueueRows: [] }, claims)
  };
}

function packageSheet(name, rows) {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  return {
    name,
    rowCount: normalizedRows.length,
    columns: Array.from(normalizedRows.reduce((keys, row) => {
      Object.keys(row || {}).forEach((key) => keys.add(key));
      return keys;
    }, new Set())),
    rows: normalizedRows
  };
}

function packagePivot(name, rows, dimensionA, dimensionB) {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  return {
    name,
    rowCount: normalizedRows.length,
    rows: normalizedRows.map((row) => ({
      dimensionA: row[dimensionA] || row.label || "Unknown",
      dimensionB: row[dimensionB] || row.queue || "Total",
      claims: nonNegativeIntegerValue(row.claims || row.openClaims),
      outstanding: row.outstanding ?? row.owed ?? null
    }))
  };
}

// D1 (owner directive, 2026-08-07): "tell a biller what to work on today", ranked
// automatically. The per-claim ranked worklist below has existed since
// 2026-05-21 and was reachable by nobody: bindReportQueueDrilldownControls()
// had zero callers and its [data-report-drilldown] selector matched nothing in
// the codebase. These lanes are that missing entry point.
//
// EVERY lane carries a POSITIVE minimum balance. Measured 2026-08-07 against
// silver.vw_tms_tracker_claims: of the 83,021 rows with a non-zero balance,
// 39,948 (19.1% of all rows) are NEGATIVE - overpayments. A queue built on
// `<> 0`, or on absolute value, opens a biller's day on credit balances. Open
// work means money owed TO us.
//
// Ranking stays server-authoritative (work_priority DESC, follow_up_date ASC,
// final_insurance_balance DESC, claim_id ASC). Lanes re-scope; they never
// re-sort a page client-side, which would only reorder the 25 rows in hand and
// quietly contradict the rank the server applied to all of them.
//
// Facility is deliberately NOT a lane: it is the billing-entity/physician-record
// name, not a site (10 of 20 orgs have exactly one value), so ranking on it
// degenerates to "highest amount in the org". Owner question still open.
const WORK_QUEUE_MIN_BALANCE = "0.01";
// A client reads the same work queue from its own side of the table
// (2026-10-13): "Premium AR Focus" and "Biller work" are TMS desk language and
// "Owed to us" is the wrong pronoun on the client's screen.
const WORK_QUEUE_CLIENT_TEXT = Object.freeze({
  "Premium AR Focus": "Largest balances",
  "Ready For Outreach": "Longest without activity",
  "Biller work": "TMS working",
  "Hide rows that are waiting on the client": "Rows TMS is working on",
  "Client action": "Waiting on you",
  "Only rows waiting on the client or provider": "Rows waiting on your practice",
  "Owed to us only": "Balance owed only"
});
function workQueueText(text) {
  const value = String(text == null ? "" : text);
  return isExternalClientRole(state?.role) && WORK_QUEUE_CLIENT_TEXT[value] ? WORK_QUEUE_CLIENT_TEXT[value] : value;
}
const WORK_QUEUE_LANES = [
  { queueKey: "all", slaBucket: "Overdue", label: "Overdue", hint: "Follow-up date has passed" },
  { queueKey: "all", slaBucket: "Due Today", label: "Due Today", hint: "Follow-up date is today" },
  { queueKey: "payerFollowUp", slaBucket: "All", label: "Payer Follow-Up", hint: "Open with the payer" },
  { queueKey: "missingPayerResponse", slaBucket: "All", label: "Awaiting Payer Response", hint: "No payer response yet" },
  { queueKey: "highDollar", slaBucket: "All", label: "Premium AR Focus", hint: "Highest amount due first" },
  { queueKey: "stale", slaBucket: "All", label: "Ready For Outreach", hint: "Untouched the longest" },
  { queueKey: "all", slaBucket: "All", label: "All open work", hint: "Everything with a balance owed" }
];

// The lane the queue opens on. Named, never positional: "All open work" is the
// only lane that shows the full server rank, and that rank IS the answer to
// "what do I work on today". Overdue is a date refinement of it, so opening
// there hides most of the ranked day. Declared as its own const so changing
// the default never means reordering the chip row a biller reads left to right.
const WORK_QUEUE_DEFAULT_LANE = WORK_QUEUE_LANES.find((lane) => lane.queueKey === "all" && lane.slaBucket === "All");

// CT-TRACKER's two quick filters, ported for M1. Their own contract
// (WORKFLOW_COLLABORATION_QUEUES_DESIGN.md:188) is the design constraint:
// "Not Paid and Underpaid are quick filters, not separate queue engines." So
// these REFINE whichever lane is loaded rather than replacing it, and they draw
// as one compact row of two segmented groups - not as nine more lane cards,
// which would have pushed the first claim row back below the fold that cycles
// 20-23 spent three passes clearing.
//
// The vocabulary is the SERVER'S (backend/claimQueueQuickFilters.js). What
// travels is the KEY, never the label: the aging keys are hyphenated, and a
// display label (the reports tables use an en dash) matches no row and would
// empty the queue with no error at all. The labels here are deliberately
// hyphenated too, so the two can never drift apart on this side.
//
// Payment lane semantics, stated once so the chip and the SQL agree: Not paid
// means nothing has been received (paid <= 0, which includes a recoupment that
// took back more than was posted); Underpaid means something was received and a
// balance is still owed. They are exact complements, so the two chips always add
// up to the lane above them - a queue whose lanes do not reconcile is a queue
// nobody trusts with money.
const WORK_QUEUE_PAYMENT_LANES = [
  { key: "all", label: "All open", hint: "Everything with a balance owed" },
  { key: "notPaid", label: "Not paid", hint: "No insurance payment received yet" },
  { key: "underpaid", label: "Underpaid", hint: "Paid in part, balance still owed" }
];

// Bands are the server's AR aging authority (ctTrackerAgingBuckets, ported from
// the legacy build_lite_snapshot.py). Blank aging is reachable only through
// "All ages" - it is never fabricated into 0-30.
const WORK_QUEUE_AGING_LANES = [
  { key: "All", label: "All ages", hint: "Every aging band, including blank" },
  { key: "0-30", label: "0-30", hint: "0-30 days outstanding" },
  { key: "31-60", label: "31-60", hint: "31-60 days outstanding" },
  { key: "61-90", label: "61-90", hint: "61-90 days outstanding" },
  { key: "91-120", label: "91-120", hint: "91-120 days outstanding" },
  { key: "121+", label: "121+", hint: "121 or more days outstanding" }
];

// THE ACTION LANE, and why a work queue needs it.
//
// Measured on the TEST warehouse 2026-08-23: the Overdue lane holds 3,633 rows
// and 446 of them (12.3%) carry a Client Action Pending-* action - credentialing,
// authorization, coverage, COB, EVV. Every one is a drawer a biller opens to find
// nothing they can do, and until now there was no way to leave them out of a
// working session. (Run 3 filed 24.5%; the whole-lane figure is 12.3%. The higher
// number is what the TOP of the priority-sorted lane looks like - 43.4% over the
// first 800 rows - which is why it reads worse at the desk than the total says.)
//
// Keys, never labels, and the vocabulary is the SERVER'S
// (backend/claimQueueQuickFilters ACTION_LANES): an unrecognised value is a 400
// on both lanes rather than a silently wider population.
const WORK_QUEUE_ACTION_LANES = [
  { key: "all", label: "All actions", hint: "Every follow-up action in this lane" },
  { key: "billerAction", label: "Biller work", hint: "Hide rows that are waiting on the client" },
  { key: "clientAction", label: "Client action", hint: "Only rows waiting on the client or provider" }
];

const WORK_QUEUE_QUICK_FILTER_GROUPS = [
  { dimension: "paymentLane", legend: "Payment", fallback: "all", lanes: WORK_QUEUE_PAYMENT_LANES },
  { dimension: "agingBucket", legend: "Aging", fallback: "All", lanes: WORK_QUEUE_AGING_LANES },
  { dimension: "actionLane", legend: "Action", fallback: "all", lanes: WORK_QUEUE_ACTION_LANES }
];

// Read from the LOADED request, exactly like workQueueLaneIsActive - so the lit
// chip and the population on screen can never disagree.
function workQueueQuickFilterValue(dimension) {
  const queue = reportQueueDrilldownState.request?.queue || {};
  const group = WORK_QUEUE_QUICK_FILTER_GROUPS.find((entry) => entry.dimension === dimension);
  return queue[dimension] || group?.fallback || "All";
}

function workQueueLaneIsActive(lane) {
  const queue = reportQueueDrilldownState.request?.queue;
  if (!queue) return false;
  return (queue.queueKey || "all") === lane.queueKey && (queue.slaBucket || "All") === lane.slaBucket;
}

// Rendered from renderReports() rather than from a report branch: the panel is a
// sibling of #reportsBody (index.html), so it survives the innerHTML rewrite that
// would otherwise detach the once-at-boot els.reportQueueDrilldown binding.
function renderReportWorkQueuePanel() {
  const panel = els.reportWorkQueuePanel;
  if (!panel) return;
  // Same gate the Reports body already applies - this widens no permission.
  const visible = hasPermission(PERMISSIONS.VIEW_FINANCIALS)
    && reportArState.view === "workQueue";
  panel.hidden = !visible;
  if (!visible) {
    if (els.reportWorkQueueLanes) els.reportWorkQueueLanes.innerHTML = "";
    if (els.reportWorkQueueQuickFilters) els.reportWorkQueueQuickFilters.innerHTML = "";
    if (els.reportQueueDrilldown) els.reportQueueDrilldown.innerHTML = "";
    const sendAlert = document.getElementById("reportWorkQueueSendAlert");
    if (sendAlert) sendAlert.innerHTML = "";
    return;
  }
  // The authorization send lane is a different grain from the claim queue below,
  // so it is announced here rather than folded in as a lane.
  renderReportWorkQueueSendAlert();
  // Tell the biller what to work on instead of waiting to be asked: the default
  // lane loads itself the first time the queue is shown. Guarded on `idle` so the many
  // renderReports() call sites cannot re-fire it, and so a biller who moved to
  // another lane keeps the lane they chose. It runs BEFORE the lanes are drawn so
  // the request it registers is the one the lane row highlights.
  const loadedQueue = reportQueueDrilldownState.request?.queue || null;
  const loadedFilters = reportQueueDrilldownState.request?.filters || null;
  const currentFilters = reportFilterValues();
  const sharedFiltersChanged = loadedFilters && JSON.stringify(loadedFilters) !== JSON.stringify(currentFilters);
  if ((reportQueueDrilldownState.status === "idle" || sharedFiltersChanged) && reportApiEnabled()) {
    requestReportQueueDrilldown(loadedQueue || {
      queueKey: WORK_QUEUE_DEFAULT_LANE.queueKey,
      payer: "All",
      status: "All",
      slaBucket: WORK_QUEUE_DEFAULT_LANE.slaBucket,
      paymentLane: "all",
      agingBucket: "All",
      minBalance: WORK_QUEUE_MIN_BALANCE
    }, 1);
    return;
  }
  renderReportWorkQueueLanes();
  renderReportQueueDrilldown();
}

function renderReportWorkQueueLanes() {
  if (!els.reportWorkQueueLanes) return;
  els.reportWorkQueueLanes.innerHTML = WORK_QUEUE_LANES.map((lane) => {
    const active = workQueueLaneIsActive(lane);
    return `
      <button type="button" class="report-work-queue-lane${active ? " is-active" : ""}" data-report-drilldown data-queue-key="${escapeHtml(lane.queueKey)}" data-payer="All" data-status="All" data-sla-bucket="${escapeHtml(lane.slaBucket)}" data-min-balance="${WORK_QUEUE_MIN_BALANCE}" aria-pressed="${active ? "true" : "false"}" title="${escapeHtml(lane.hint)}">
        <strong>${escapeHtml(workQueueText(lane.label))}</strong>
        <span>${escapeHtml(workQueueText(lane.hint))}</span>
      </button>`;
  }).join("");
  renderReportWorkQueueQuickFilters();
  bindReportQueueDrilldownControls();
}

// One row, two segmented groups, drawn from the same state the lane chips read.
// Rendered here rather than from its own call site so the row can never be left
// showing a filter the loaded queue no longer carries.
function renderReportWorkQueueQuickFilters() {
  const host = els.reportWorkQueueQuickFilters;
  if (!host) return;
  const queue = reportQueueDrilldownState.request?.queue || {};
  // The openness floor rides on the TRIGGER and is never re-defaulted here. 19%
  // of rows are negative (overpayments), so a quick filter that silently reset
  // the floor to 0 would open a biller's day on credit balances. A drill-down
  // launched from outside the lane row therefore keeps ITS floor, not the lane
  // row's.
  const minBalance = queue.minBalance == null ? WORK_QUEUE_MIN_BALANCE : queue.minBalance;
  host.innerHTML = WORK_QUEUE_QUICK_FILTER_GROUPS.map((group) => {
    const active = workQueueQuickFilterValue(group.dimension);
    const chips = group.lanes.map((lane) => {
      const on = lane.key === active;
      return `<button type="button" class="report-work-queue-quickchip${on ? " is-active" : ""}" data-report-drilldown data-queue-quickfilter="${escapeHtml(group.dimension)}" data-quick-value="${escapeHtml(lane.key)}" data-min-balance="${escapeHtml(String(minBalance))}" aria-pressed="${on ? "true" : "false"}" title="${escapeHtml(workQueueText(lane.hint))}">${escapeHtml(workQueueText(lane.label))}</button>`;
    }).join("");
    return `
      <div class="report-work-queue-quickgroup" role="group" aria-label="${escapeHtml(group.legend)} filter">
        <span class="report-work-queue-quicklabel">${escapeHtml(group.legend)}</span>
        ${chips}
      </div>`;
  }).join("");
}

function bindReportQueueDrilldownControls() {
  Array.from(document.querySelectorAll("[data-report-drilldown]")).forEach((button) => {
    button.disabled = !reportApiEnabled();
    button.addEventListener("click", () => {
      // Two kinds of trigger share this binder. A LANE chip sets the lane and
      // must keep the quick filters the biller already chose; a QUICK FILTER
      // sets one dimension and must keep the loaded lane. Both read every
      // dimension they are not themselves changing from the LOADED request
      // rather than from their own dataset, so no control can silently reset
      // another one - the failure that would make a biller think the queue is
      // lying to them.
      const loaded = reportQueueDrilldownState.request?.queue || {};
      const dimension = button.dataset.queueQuickfilter || "";
      const chosen = button.dataset.quickValue || "";
      const quick = (key, fallback) => (dimension === key ? (chosen || fallback) : (loaded[key] || fallback));
      requestReportQueueDrilldown({
        paymentLane: quick("paymentLane", "all"),
        agingBucket: quick("agingBucket", "All"),
        actionLane: quick("actionLane", "all"),
        queueKey: dimension ? (loaded.queueKey || "all") : (button.dataset.queueKey || "all"),
        payer: dimension ? (loaded.payer || "All") : (button.dataset.payer || "All"),
        status: dimension ? (loaded.status || "All") : (button.dataset.status || "All"),
        slaBucket: dimension ? (loaded.slaBucket || "All") : (button.dataset.slaBucket || "All"),
        // Carried by the trigger, never defaulted here: a silent default in a
        // shared binder is how the overpayment trap gets un-fixed by accident.
        minBalance: button.dataset.minBalance || 0
      });
    });
  });
}

function requestReportQueueDrilldown(queue = {}, pageNumber = 1) {
  if (!reportApiEnabled()) {
    reportQueueDrilldownState = {
      key: "",
      status: "error",
      response: null,
      error: "Queue details are temporarily unavailable.",
      request: { queue }
    };
    renderReportQueueDrilldown();
    return;
  }
  const request = {
    filters: reportFilterValues(),
    queue: {
      queueKey: queue.queueKey || "all",
      payer: queue.payer || "All",
      status: queue.status || "All",
      slaBucket: queue.slaBucket || "All",
      owner: queue.owner || "All",
      // Keys, never labels - the server rejects anything outside its vocabulary
      // rather than widening the population, so a label here surfaces as a 400
      // instead of a silently wrong queue.
      paymentLane: queue.paymentLane || "all",
      agingBucket: queue.agingBucket || "All",
      // Same rule as the payment lane above: the KEY travels, never the label.
      actionLane: queue.actionLane || "all",
      minBalance: nonNegativeMoneyFilterValue(queue.minBalance)
    },
    page: { size: 25, number: Math.min(MAX_REPORT_QUEUE_DRILLDOWN_PAGE_NUMBER, Math.max(1, positiveNumber(pageNumber, 1))) }
  };
  const key = reportQueueDrilldownCacheKey(request);
  const base = reportApiBase();
  if (!base || typeof fetch !== "function") return;
  const requestStillCurrent = () => (
    reportQueueDrilldownState.key === key
    && reportQueueDrilldownCacheKey(request) === key
  );
  reportQueueDrilldownState = { key, status: "pending", response: null, error: "", request };
  renderReportQueueDrilldown();
  const token = reportApiToken();
  reportSessionFetch(`${base}/api/reports/claim-queues/drilldown`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(request)
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (!response.ok || body.error) {
        if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        reportQueueDrilldownState = { key, status: "error", response: null, error: response.status === 401 ? "Your session expired. Sign in again — this page will still be here." : friendlyQueueDrilldownError(body?.error?.message || body.message || `HTTP ${response.status}`), request };
        renderReportQueueDrilldown();
        return;
      }
      reportQueueDrilldownState = { key, status: "ok", response: body, error: "", request };
      renderReportQueueDrilldown();
    })
    .catch((error) => {
      if (!requestStillCurrent()) return;
      reportQueueDrilldownState = { key, status: "error", response: null, error: error.message, request };
      renderReportQueueDrilldown();
    });
}

function reportQueueDrilldownCacheKey(request) {
  return JSON.stringify({
    reportView: reportReadPublicationIdentity(),
    base: reportApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: reportAccessCacheEnvelope(),
    filters: request.filters || {},
    queue: request.queue || {},
    page: request.page || {}
  });
}

// AN EMPTY LANE MUST NOT BLAME THE READER (2026-08-10).
//
// Every zero-row page printed one sentence - "No claims in this queue page.
// Adjust filters or try another page." - over three different situations, and
// the reader's next action is different in all three. On a STRUCTURALLY empty
// lane (no claims of that kind exist at all) there is no filter to adjust and
// no other page to try, so the advice is not merely unhelpful: it sends a
// biller hunting for a filter that was never applied, and it makes "there is
// no work" indistinguishable from "you have hidden the work".
//
// The server's own `unavailable` message still wins when it is present - that
// is the lane-specific channel and it is owned elsewhere. This is the generic
// fallback underneath it.
function queueDrilldownEmptyMessage({ totalRows = 0, filtered = false, queueOutstanding = null } = {}) {
  if (totalRows > 0) return "This page is past the end of the queue. Go back a page to see the claims that are there.";
  if (filtered) return Number.isFinite(Number(queueOutstanding)) && Number(queueOutstanding) > 0
    ? `No claims match these queue filters. Filters are hiding ${formatReportMoney(queueOutstanding)} still on this queue. This is not $0.`
    : "No claims match these queue filters. Clear a filter to widen the queue. This is not $0 on the book.";
  return "This queue is empty. There are no claims of this kind right now - nothing to adjust, and no other page to try.";
}

// The server still logs the exact validation string. The panel talks to a
// biller, not a SQL builder: "warehouse table label must name a gold object"
// is how this screen went from "loading" to unreadable.
function friendlyQueueDrilldownError(raw = "") {
  const message = String(raw || "").trim();
  if (/gold object|t1a reader view|SQL plan failed validation/i.test(message)) {
    return "This claim list could not be loaded. Please retry or choose another queue.";
  }
  if (/timeout|timed out|ETIMEDOUT/i.test(message)) {
    return "This claim list is taking too long. Narrow the queue above (Overdue or Due Today is smaller than All open work) and retry.";
  }
  return message || "This claim list could not be loaded.";
}

function renderReportQueueDrilldown() {
  if (!els.reportQueueDrilldown) return;
  const container = els.reportQueueDrilldown;
  // A lane click goes straight to requestReportQueueDrilldown, so this is the
  // one place that always knows the loaded queue changed - redraw the lane row
  // here or the active lane never follows the biller.
  renderReportWorkQueueLanes();

  if (reportQueueDrilldownState.status === "idle") {
    container.innerHTML = `
      <div class="queue-drilldown-placeholder">
        <div class="queue-drilldown-placeholder-icon" aria-hidden="true">⌖</div>
        <p class="queue-drilldown-placeholder-title">Claims in the work pile you pick</p>
        <p class="queue-drilldown-placeholder-body">Click a queue above — Overdue, Due Today, Payer Follow-Up — to see the claims in it. Click a claim to open it.</p>
      </div>`;
    return;
  }

  if (reportQueueDrilldownState.status === "pending") {
    const queue = reportQueueDrilldownState.request?.queue || {};
    const requestedKey = queue.queueKey || "queue";
    const label = queueLabel(requestedKey) || requestedKey;
    container.innerHTML = `
      <div class="queue-drilldown-loading" role="status" aria-live="polite">
        <div class="queue-drilldown-spinner" aria-hidden="true"></div>
        <div class="queue-drilldown-loading-copy">
          <p class="queue-drilldown-loading-title">Loading ${escapeHtml(label)}</p>
          <p class="queue-drilldown-loading-body">This is the work list for that queue. A claim row opens the claim.</p>
        </div>
      </div>`;
    return;
  }

  if (reportQueueDrilldownState.status === "error") {
    const message = reportQueueDrilldownState.error || "Queue drill-down failed.";
    container.innerHTML = `
      <div class="queue-drilldown-error" role="alert">
        <div class="queue-drilldown-error-icon" aria-hidden="true">!</div>
        <div class="queue-drilldown-error-copy">
          <p class="queue-drilldown-error-title">Couldn’t load this work list</p>
          <p class="queue-drilldown-error-body">${escapeHtml(message)}</p>
        </div>
        <button class="small-button" type="button" data-report-drilldown-retry>Retry</button>
      </div>`;
    bindReportQueueDrilldownRetry();
    return;
  }

  const result = reportQueueDrilldownState.response || {};
  const rows = Array.isArray(result.rows) ? result.rows : [];
  const decision = result.decision || {};
  const queue = decision.queueApplied || reportQueueDrilldownState.request?.queue || {};
  const page = result.page || {};
  const pageNumber = Math.max(1, positiveNumber(page.number, reportQueueDrilldownState.request?.page?.number || 1));
  const totalPages = Math.max(1, positiveNumber(page.totalPages, 1));
  const totalRows = nonNegativeNumber(page.totalRows, rows.length);
  // AN EMPTY QUEUE MUST EXPLAIN ITSELF. The server attaches `unavailable` only
  // when a zero-row page is a property of the SOURCE — a queue this reporting
  // lane does not model, or an SLA lane that ranks by a follow-up date the lane
  // carries on no row. Without it a biller reads a clean empty table as "there
  // is no work", which is the failure mode this codebase treats as worse than
  // an error box.
  const queueUnavailableMessage = totalRows === 0 ? String(result.unavailable?.message || "") : "";
  // THE SCREEN MUST SAY HOW OLD ITS DATA IS. This lane reads a MIRROR that is
  // rebuilt by a copy job nobody scheduled, so "current" was never guaranteed —
  // and on 2026-08-10 it was serving a 2026-06-23 snapshot while saying nothing,
  // which is how seven-week-old numbers got read as today's and reported as
  // "the numbers look wrong". A stale screen that admits it is stale is merely
  // stale; one that does not is lying.
  //
  // Server-supplied (`dataAsOf.ingestedAtUtc` = MAX(ingested_at_utc) over the
  // counted rows), NOT a client clock: fetch time would say "now" no matter how
  // old the data was, which is the exact lie being fixed. When the server does
  // not supply it the stamp is omitted rather than guessed.
  const dataAsOfUtc = String(result.dataAsOf?.ingestedAtUtc || "");
  const dataAsOfAgeDays = dataAsOfUtc && Number.isFinite(Date.parse(dataAsOfUtc))
    ? (Date.now() - Date.parse(dataAsOfUtc)) / 86400000
    : null;
  // Two days, not one: the copy job has always been same-day-ish rather than
  // hourly, so a one-day threshold would cry stale every morning and get tuned
  // out — and being tuned out is how this went unnoticed for seven weeks.
  const dataAsOfStale = dataAsOfAgeDays !== null && dataAsOfAgeDays > 2;
  const dataAsOfLabel = dataAsOfUtc
    ? `${dataAsOfStale ? "Stale — data as of " : "Data as of "}${escapeHtml(dataAsOfUtc.slice(0, 16).replace("T", " "))} UTC`
    : "";
  const start = totalRows ? nonNegativeNumber(page.startIndex, 0) + 1 : 0;
  const end = totalRows ? Math.min(totalRows, nonNegativeNumber(page.endIndex, rows.length)) : 0;
  const queueKey = queue.queueKey || "all";
  // The chip a biller pressed and this header have to say the same words, so
  // both resolve against the SAME thing workQueueLaneIsActive() highlights -
  // the requested lane. Deriving from queueKey alone cannot tell Overdue
  // (queueKey "all", slaBucket "Overdue") from All open work, which is how the
  // default landing came to show one name on the chip and another in the h4.
  // Drill-downs launched from outside the lane row keep the old derivation.
  const activeLane = WORK_QUEUE_LANES.find(workQueueLaneIsActive);
  const queueLabelText = activeLane
    ? activeLane.label
    : (queueKey === "all" ? "All open work" : (queueLabel(queueKey) || queueKey));
  const financialLocked = booleanFlagValue(decision.redactedFinancials);
  const phiLocked = booleanFlagValue(decision.redactedPhi);
  const pageOutstanding = rows.reduce((sum, row) => sum + moneyNumber(row.outstanding || 0), 0);
  const queueTotalsRaw = result.totals || decision.totals || result.summary || {};
  // THE QUEUE TOTAL LIVES ON `aggregate`, AND NONE OF THESE FIVE KEYS EXIST.
  //
  // The response's top-level keys are schema, decision, page, counts, AGGREGATE,
  // rows, dataAsOf, queryPlanSummary, auditEvent, audit. Every candidate below
  // looked in totals / summary / decision, so the lookup never resolved, the
  // "Queue total" tile never rendered, and a biller working payer-follow-up saw
  // only "Outstanding on page" - the sum of 100 lines - while the queue was
  // actually worth $6,001,648.51. It also silently degraded the empty-state copy,
  // which reads queueOutstanding from the same dead lookup.
  //
  // aggregate.openOwed leads because it is the COLLECTABLE basis - the same one AR
  // Aging renders as Open Insurance AR - and a work queue's headline should be what
  // can actually be collected, not a signed net carrying credit balances.
  const queueAggregate = (result && typeof result.aggregate === "object" && result.aggregate) || {};
  const queueTotalOutstanding = [
    queueAggregate.openOwed,
    queueAggregate.outstanding,
    queueTotalsRaw.queueOutstanding,
    queueTotalsRaw.totalOutstanding,
    queueTotalsRaw.outstanding,
    result.queueOutstanding,
    decision.queueOutstanding
  ].find((value) => value !== null
    && value !== undefined
    && String(value).trim() !== ""
    && Number.isFinite(parseMoneyNumber(value)));
  const hasQueueTotal = !financialLocked && queueTotalOutstanding != null;

  const filterChips = [];
  if (queue.payer && queue.payer !== "All") filterChips.push({ label: "Payer", value: queue.payer });
  if (queue.status && queue.status !== "All") filterChips.push({ label: "Status", value: queue.status });
  if (queue.slaBucket && queue.slaBucket !== "All") filterChips.push({ label: "SLA", value: queue.slaBucket });
  if (queue.owner && queue.owner !== "All") filterChips.push({ label: "Owner", value: queue.owner });
  if (queue.actionLane && queue.actionLane !== "all") {
    filterChips.push({
      label: "Action",
      value: (WORK_QUEUE_ACTION_LANES.find((lane) => lane.key === queue.actionLane) || {}).label || queue.actionLane
    });
  }
  const queueMinBalance = nonNegativeMoneyFilterValue(queue.minBalance);
  if (queueMinBalance > 0) {
    filterChips.push(queueMinBalance < 1
      ? { label: "Balance", value: "Owed to us only" }
      : { label: "Min $", value: formatReportMoney(queueMinBalance) });
  }

  // Above the fold at 1280x720 (cycle 20 carry-forward): the panel sat at 369
  // and the first claim row at 829 - 460px of chrome and no actual work on
  // screen. Two of those bands earned their height by repeating something a
  // biller could already read:
  //   - the eyebrow and the <h4> resolve from the active lane, so they are
  //     verbatim the lit chip ~30px above them. They render only when NO lane
  //     is active - the external-drilldown path, where the derivation differs
  //     and the name is the only thing naming the queue.
  //   - of the four hero tiles, PAGE repeated the pager two rows below and PHI
  //     put a compliance readout in the same type size as the money. The money
  //     - outstanding on page, and the queue total when the backend sends one -
  //     now reads as an inline strip on the header row that already existed,
  //     instead of an 85px band of its own, and PHI moves beside Refresh as a
  //     badge. The counts are NOT repeated here: the pager one row below
  //     already reads "Rows 1-25 of 12,784" and "Page 137 of 512", and a strip
  //     carrying them measured 730px against a ~520px budget (header 982px less
  //     up to ~448px of chips/badge/Refresh), which wrapped the header to two
  //     lines and put the first row back below the fold at 747.
  // Both redaction states keep the amber is-locked treatment the tiles gave
  // them: losing the redaction signal would be a permissions regression, not a
  // layout win.
  container.innerHTML = `
    <header class="queue-drilldown-header">
      ${activeLane ? "" : `<div class="queue-drilldown-title">
        <span class="queue-drilldown-eyebrow">AR follow-up queue</span>
        <h4>${escapeHtml(queueLabelText)}</h4>
      </div>`}
      <div class="queue-drilldown-figures">
        <span class="queue-drilldown-figure queue-drilldown-figure--money${financialLocked ? " is-locked" : ""}">
          <em>${financialLocked ? "Financials" : "Outstanding on page"}</em>
          <strong>${financialLocked ? "Restricted" : formatReportMoney(pageOutstanding)}</strong>
        </span>
        ${hasQueueTotal ? `<span class="queue-drilldown-figure queue-drilldown-figure--queue-total">
          <em>Queue total</em>
          <strong>${escapeHtml(formatReportMoney(queueTotalOutstanding))}</strong>
        </span>` : ""}
      </div>
      <div class="queue-drilldown-actions">
        ${filterChips.length ? `
          <div class="queue-drilldown-filterchips" aria-label="Applied queue filters">
            ${filterChips.map((chip) => `<span class="queue-drilldown-filterchip"><em>${escapeHtml(chip.label)}</em>${escapeHtml(workQueueText(String(chip.value)))}</span>`).join("")}
          </div>` : ""}
        ${isExternalClientRole(state?.role) ? "" : `<span class="queue-drilldown-phi${phiLocked ? " is-locked" : ""}" title="${phiLocked ? "Patient identifiers are hidden from this role" : "Patient identifiers are visible to this role"}">PHI ${phiLocked ? "Restricted" : "Visible"}</span>`}
        <button class="small-button" type="button" data-report-drilldown-refresh title="Reload this queue">Refresh</button>
      </div>
    </header>
    <div class="queue-drilldown-pager">
      <!-- "Lines", not "Rows": this drill-down is SERVICE-LINE grained, so a
           multi-line claim legitimately repeats down the page and the total
           counts lines. Saying "Rows" is what made a repeated claim id look
           like duplicated money on 2026-08-10. No total on this screen is
           summed from the page, so the repeats cost nothing but the confusion
           this wording removes. -->
      <span class="queue-drilldown-rowcount">${totalRows ? `Lines ${start}-${end} of ${totalRows.toLocaleString()}` : "No lines"}</span>
      ${dataAsOfLabel ? `<span class="queue-drilldown-asof${dataAsOfStale ? " queue-drilldown-asof-stale" : ""}" title="When this information was last updated.">${dataAsOfLabel}</span>` : ""}
      <span class="queue-drilldown-pager-spacer"></span>
      <button class="small-button queue-drilldown-pagerbtn" type="button" data-report-drilldown-page="first" ${pageNumber <= 1 ? "disabled" : ""} title="First page">&laquo;</button>
      <button class="small-button queue-drilldown-pagerbtn" type="button" data-report-drilldown-page="prev" ${pageNumber <= 1 ? "disabled" : ""} title="Previous page">&larr; Prev</button>
      <span class="queue-drilldown-pageindicator">Page ${pageNumber} of ${totalPages}</span>
      <button class="small-button queue-drilldown-pagerbtn" type="button" data-report-drilldown-page="next" ${pageNumber >= totalPages ? "disabled" : ""} title="Next page">Next &rarr;</button>
      <button class="small-button queue-drilldown-pagerbtn" type="button" data-report-drilldown-page="last" ${pageNumber >= totalPages ? "disabled" : ""} title="Last page">&raquo;</button>
      ${totalPages > 5 ? `<label class="queue-drilldown-pagejump" title="Jump to page">
        <span>Go to</span>
        <input type="number" min="1" max="${totalPages}" value="${pageNumber}" data-report-drilldown-pagejump aria-label="Jump to page" />
      </label>` : ""}
    </div>
    <div class="table-wrap compact-table-wrap queue-drilldown-table-wrap">
      <table class="client-table queue-drilldown-table">
        <thead>
          <tr>
            <th>Claim</th>
            <!-- WHICH LINE. This drill-down is service-line grained, so Line / DOS / CPT
                 identify the exact row a biller is being handed. On the claim-grained
                 in-memory lane the server sends a DOS span and a CPT count instead of
                 picking one line, and both render honestly through the same cells. -->
            <th>Line</th>
            <th>DOS</th>
            <th>CPT</th>
            <th>Patient</th>
            <th>Tenant</th>
            <th>Payer</th>
            <th>Status</th>
            <!-- WHAT IS ALREADY BEING DONE ABOUT IT, AND BY WHOM. The queue ranked
                 and listed the work but never said what action was already on the
                 line, when it is next due, or who owns it - so 446 of the Overdue
                 lane's 3,633 rows (12.3%, measured 2026-08-23) were opened only to
                 discover they are waiting on the client. The server has always
                 returned all three on the row; only the table dropped them. -->
            <th>Action</th>
            <th>Follow-Up</th>
            <th>Owner</th>
            <th>SLA</th>
            <th class="num">Outstanding</th>
            <th class="num">Priority</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          ${rows.length ? rows.map((row) => {
            const reason = (row.queueReasons || []).join(", ") || "Queue match";
            const priority = nonNegativeNumber(row.priority, 0);
            const priorityTone = priority >= 80 ? "critical" : priority >= 50 ? "warn" : "info";
            const slaRaw = String(row.slaBucket || "");
            const slaTone = /overdue|breach|critical|past/i.test(slaRaw) ? "critical"
              : /risk|approach|warn|aging/i.test(slaRaw) ? "warn" : "info";
            // The SAME prefix test the server's action lane uses
            // (claimQueueQuickFilters.CLIENT_ACTION_PENDING_PREFIX), so the chip a
            // biller sees and the filter that removes it agree by construction.
            const actionText = String(row.action || "").trim() || "\u2014";
            const clientActionRow = actionText.toUpperCase().startsWith("CLIENT ACTION PENDING");
            // "NONE" IS A BELIEVABLE ANSWER AND THAT IS THE PROBLEM. formatDate returns
            // the same "None" for an absent date and for one it cannot parse, so a row
            // whose follow-up date arrives in a form the parser rejects reads as "no
            // follow-up is set" - on rows that all have one. Measured live 2026-08-23:
            // the queue row's follow_up_date came back as a driver Date rendered in the
            // host locale, so EVERY row in the Overdue lane would have said None. The
            // server-side normalizer is the real fix (claimQueueDrilldownSynapseApiAdapter
            // now uses dateOnlyString, as `dos` always did); this keeps the cell honest
            // if any lane ever hands over a shape the parser cannot read again.
            const followUpRaw = String(row.followUpDate || "").trim();
            const followUpFormatted = followUpRaw ? formatDate(followUpRaw) : "";
            const followUpText = !followUpRaw ? "\u2014" : (followUpFormatted === "None" ? "Unavailable" : followUpFormatted);
            return `
            <tr class="queue-drilldown-row" data-report-drilldown-row="${escapeHtml(row.claimId)}" data-report-drilldown-org="${escapeHtml(String(row.orgId || ""))}" data-report-drilldown-service="${escapeHtml(String(row.serviceId || ""))}" tabindex="0" title="Open claim ${escapeHtml(row.claimId)}${row.serviceId ? " line " + escapeHtml(String(row.serviceId)) : ""}">
              <td><span class="queue-drilldown-claimid">${escapeHtml(row.claimId)}</span></td>
              <td><span class="queue-drilldown-lineid">${escapeHtml(row.serviceId || "\u2014")}</span></td>
              <td>${escapeHtml(row.dos || "\u2014")}</td>
              <td>${escapeHtml(queueDrilldownCptLabel(row))}</td>
              <td>${phiLocked ? '<span class="queue-drilldown-locked" title="Patient name restricted by permissions">&mdash;</span>' : escapeHtml(row.patient || "\u2014")}</td>
              <td>${escapeHtml(row.tenant)}</td>
              <td>${escapeHtml(row.primaryPayer)}</td>
              <td><span class="queue-drilldown-statuschip">${escapeHtml(row.status)}</span></td>
              <td><span class="queue-drilldown-action${clientActionRow ? " is-client-action" : ""}" title="${clientActionRow ? "Waiting on the client or provider - nothing a biller can action" : escapeHtml(actionText)}">${escapeHtml(actionText)}</span></td>
              <td>${escapeHtml(followUpText)}</td>
              <td>${escapeHtml(row.owner || "Unassigned")}</td>
              <td><span class="queue-drilldown-slachip tone-${slaTone}">${escapeHtml(slaRaw || "—")}</span></td>
              <td class="num">${financialLocked ? '<span class="queue-drilldown-locked" title="Financials restricted by permissions">—</span>' : formatReportMoney(row.outstanding)}</td>
              <td class="num"><span class="queue-drilldown-prioritypill tone-${priorityTone}">${priority}</span></td>
              <td><span class="queue-drilldown-reason" title="${escapeHtml(reason)}">${escapeHtml(reason)}</span></td>
            </tr>`;
          }).join("") : `<tr><td colspan="15" class="queue-drilldown-emptyrow${queueUnavailableMessage ? " queue-drilldown-emptyrow--unavailable" : ""}">${escapeHtml(queueUnavailableMessage || queueDrilldownEmptyMessage({ totalRows, filtered: filterChips.length > 0, queueOutstanding: hasQueueTotal ? queueTotalOutstanding : null }))}</td></tr>`}
        </tbody>
      </table>
    </div>
  `;
  bindReportQueueDrilldownPager();
  bindReportQueueDrilldownDetailButtons();
  bindReportQueueDrilldownRefresh();
}

// WHAT CPT IS THIS ROW? One code when the row IS one line, an honest count when
// the server could only send a claim. `procedureCount` is the server's own count
// of DISTINCT codes on the claim, never re-derived here, so the cell can never
// claim more precision than the lane behind it has.
function queueDrilldownCptLabel(row) {
  const code = String(row.procedureCode || "").trim();
  if (code) return code;
  const count = nonNegativeNumber(row.procedureCount, 0);
  if (count > 1) return count + " CPTs";
  return "\u2014";
}

function bindReportQueueDrilldownRefresh() {
  if (!els.reportQueueDrilldown || typeof els.reportQueueDrilldown.querySelector !== "function") return;
  const button = els.reportQueueDrilldown.querySelector("[data-report-drilldown-refresh]");
  if (!button) return;
  button.addEventListener("click", () => {
    const queue = reportQueueDrilldownState.request?.queue || {};
    const pageNumber = Math.max(1, positiveNumber(reportQueueDrilldownState.request?.page?.number, 1));
    requestReportQueueDrilldown(queue, pageNumber);
  });
}

function bindReportQueueDrilldownRetry() {
  if (!els.reportQueueDrilldown || typeof els.reportQueueDrilldown.querySelector !== "function") return;
  const button = els.reportQueueDrilldown.querySelector("[data-report-drilldown-retry]");
  if (!button) return;
  button.addEventListener("click", () => {
    const queue = reportQueueDrilldownState.request?.queue || {};
    const pageNumber = Math.max(1, positiveNumber(reportQueueDrilldownState.request?.page?.number, 1));
    requestReportQueueDrilldown(queue, pageNumber);
  });
}

function bindReportQueueDrilldownPager() {
  if (!els.reportQueueDrilldown || typeof els.reportQueueDrilldown.querySelectorAll !== "function") return;
  const page = reportQueueDrilldownState.response?.page || {};
  const currentPage = Math.max(1, positiveNumber(page.number, reportQueueDrilldownState.request?.page?.number || 1));
  const totalPages = Math.max(1, positiveNumber(page.totalPages, 1));
  Array.from(els.reportQueueDrilldown.querySelectorAll("[data-report-drilldown-page]")).forEach((button) => {
    button.addEventListener("click", () => {
      const direction = button.dataset.reportDrilldownPage;
      let nextPage = currentPage;
      if (direction === "first") nextPage = 1;
      else if (direction === "prev") nextPage = currentPage - 1;
      else if (direction === "next") nextPage = currentPage + 1;
      else if (direction === "last") nextPage = totalPages;
      requestReportQueueDrilldown(reportQueueDrilldownState.request?.queue || {}, nextPage);
    });
  });
  const jumpInput = els.reportQueueDrilldown.querySelector("[data-report-drilldown-pagejump]");
  if (jumpInput) {
    const commit = () => {
      const requested = Math.max(1, Math.min(totalPages, positiveNumber(jumpInput.value, currentPage)));
      if (requested !== currentPage) {
        requestReportQueueDrilldown(reportQueueDrilldownState.request?.queue || {}, requested);
      }
    };
    jumpInput.addEventListener("change", commit);
    jumpInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commit();
      }
    });
  }
}

// A QUEUE ROW MUST CARRY ITS ORG, OR THE DRAWER GUESSES.
//
// This called openServerClaimDrawer with no orgId, so it fell through to
// getClaim(claimId)?.orgId - and getClaim matches on the claim id ALONE. Claim ids
// are org-scoped EZClaim sequences and collide hard: id 10 exists in 19 orgs, 487
// in 16, 311 in 15. Measured on 60 rows sampled from DEEP pages, 28 of 60 (46.7%)
// opened a DIFFERENT practice's claim - a $373.80 MyTeamABA line opening as
// AvenuesABA with $0.00 owed, so the biller closes it and the money never gets
// worked.
//
// The API-side detailPath already carries ?orgId=; this click path never read it,
// so the fix had landed at the wrong end. The row now carries the org and passes
// it explicitly - openServerClaimDrawer has always accepted options.orgId.
//
// The top of the queue hid this: both the queue and the detail resolver sort by
// outstanding descending, so the highest-dollar rows accidentally agree and a
// top-40 sample matched 40/40.
// AND ITS SERVICE LINE, FOR THE SAME REASON IT CARRIES ITS ORG.
//
// This drill-down is SERVICE-LINE grained - run 2 added Line / DOS / CPT to the
// row precisely so a biller could pick the line to work - and then the click
// threw the choice away. openServerClaimDrawer sent no serviceId, so
// loadTrackerAdapterClaimDetail fell through to `matched[0]` and the drawer
// focused the claim's FIRST line whatever row was pressed. Measured live
// 2026-08-23 on the Overdue lane: claim 32041 contributes three rows (86751,
// 86752, 86753) and all three opened 86751. The server has always honoured the
// parameter - GET /api/tracker/claims/32041?orgId=... answers focusServiceId
// 86751 without a serviceId and 86753 with one - so only the click path dropped it.
//
// THE BINDER BELOW BINDS THREE PATHS AND ALL THREE MUST CARRY IT. The org fix
// that landed earlier in this cycle missed the keydown handler and left the bug
// live for keyboard users; verify_report_queue_drilldown_line_target mutation-tests
// click, keydown and the detail button separately for exactly that reason.
function openReportQueueDrilldownClaim(claimId, orgId, serviceId) {
  if (!claimId) return;
  openServerClaimDrawer(claimId, {
    apiBase: reportApiBase(),
    apiToken: reportApiToken(),
    orgId: String(orgId || "").trim(),
    serviceId: String(serviceId || "").trim(),
    queueContext: {
      queueKey: reportQueueDrilldownState.request?.queue?.queueKey || "",
      queueSessionId: reportQueueDrilldownState.response?.audit?.queueWorkSessionId || "",
      source: reportQueueDrilldownState.response?.decision?.source || ""
    }
  });
}

function bindReportQueueDrilldownDetailButtons() {
  if (!els.reportQueueDrilldown || typeof els.reportQueueDrilldown.querySelectorAll !== "function") return;
  Array.from(els.reportQueueDrilldown.querySelectorAll("[data-report-drilldown-detail]")).forEach((button) => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      openReportQueueDrilldownClaim(button.dataset.reportDrilldownDetail, button.dataset.reportDrilldownOrg, button.dataset.reportDrilldownService);
    });
  });
  Array.from(els.reportQueueDrilldown.querySelectorAll("[data-report-drilldown-row]")).forEach((row) => {
    row.addEventListener("click", (event) => {
      if (event.target.closest("button, a, input, select, label")) return;
      openReportQueueDrilldownClaim(row.dataset.reportDrilldownRow, row.dataset.reportDrilldownOrg, row.dataset.reportDrilldownService);
    });
    row.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openReportQueueDrilldownClaim(row.dataset.reportDrilldownRow, row.dataset.reportDrilldownOrg, row.dataset.reportDrilldownService);
      }
    });
  });
}

function reportApiCacheKey(request) {
  return JSON.stringify({
    publicationEpoch: reportReadPublicationIdentity(),
    base: reportApiBase(),
    role: state.role,
    activeUserName: state.activeUserName || "",
    access: reportAccessCacheEnvelope(),
    filters: request.filters || {}
  });
}

function reportApiEnabled() {
  return reportApiMode() === "api" && Boolean(reportApiBase());
}

function reportApiMode() {
  const configured = String(window.TMS_REPORT_API_MODE || safeLocalStorageGet(REPORT_API_MODE_KEY) || "").toLowerCase();
  if (configured) return configured;
  // Live tracker-API deployments default the Collections report to the
  // server aggregation path — explicit overrides above still win.
  return trackerApiEnabled() ? "api" : "local";
}

function reportApiBase() {
  const configured = window.TMS_REPORT_API_BASE || safeLocalStorageGet(REPORT_API_BASE_KEY) || workflowApiBase();
  return String(configured || "").replace(/\/$/, "");
}

function reportApiToken() {
  return window.TMS_REPORT_API_TOKEN || safeLocalStorageGet(REPORT_API_TOKEN_KEY) || workflowApiToken();
}

function reportApiStatusSummary() {
  if (!reportApiEnabled()) return { tone: "fallback", label: "Report aggregation" };
  if (reportApiState.status === "ok") return { tone: "connected", label: "Reports ready" };
  if (reportApiState.status === "pending") return { tone: "pending", label: "Loading reports…" };
  if (reportApiState.status === "error") {
    const detail = "";
    return { tone: "error", label: `Reports unavailable${detail}` };
  }
  return { tone: "configured", label: "Reports ready" };
}

function trackerAccessEnvelope(mode = state.rowMode || "service", allPermittedColumns = false) {
  const profile = currentAccessProfile();
  const scope = profile.scope || {};
  const user = currentUser() || {};
  return {
    role: state.role,
    user: user.name || state.activeUserName || "",
    scope: scopedTenantBoundary(scope, state.role),
    permissionKeys: [...profile.permissions],
    allowedColumns: (allPermittedColumns ? permittedColumnsForMode(mode) : defaultOrderedColumns(mode)).map((column) => column.key),
    includeFinancials: hasPermission(PERMISSIONS.VIEW_FINANCIALS),
    includePhi: hasPermission(PERMISSIONS.VIEW_PHI),
    includeInternalNotes: hasPermission(PERMISSIONS.VIEW_INTERNAL_NOTES)
  };
}

function reportAccessCacheEnvelope() {
  const profile = currentAccessProfile();
  const scope = profile.scope || {};
  const user = currentUser() || {};
  return {
    user: user.name || state.activeUserName || "",
    scope: scopedTenantBoundary(scope, state.role),
    permissionKeys: [...profile.permissions]
  };
}

function matchesSlicers(claim, slicers, modes = ensureFilterModeState(state?.slicerModes, slicers)) {
  const visibleSlicersMatch = slicerDefinitions.every((definition) => {
    const selected = slicers[definition.key] || [];
    if (selected.length === 0) return true;
    const exclude = filterModeExcludes(modes, definition.key);
    if (definition.serviceField) {
      const values = serviceSlicerValues(claim, definition);
      const canonical = (values.length ? values : [""]).map((value) => canonicalSlicerValue(definition.key, value || "Blank"));
      // "Any line qualifies" is the claim-level reading in BOTH directions: an
      // exclusion keeps a claim that still has a line the reader did not exclude.
      return canonical.some((value) => selected.includes(value) !== exclude);
    }
    return selected.includes(claimSlicerValue(claim, definition)) !== exclude;
  });
  if (!visibleSlicersMatch) return false;
  const claimDueSlicer = Array.isArray(slicers.claimDue) ? slicers.claimDue : [];
  if (claimDueSlicer.includes("PAST DUE") && !isPastDue(claim)) return false;
  return true;
}

function matchesQueue(claim, key) {
  if (key === "all") return true;
  return buildClaimStatusProfile(claim).queueKeys.includes(key);
}

function matchesColumnFilters(claim, service = null, mode = state.rowMode || "service") {
  const filters = ensureColumnFilterState(state.columnFilters);
  const modes = ensureFilterModeState(state.columnFilterModes, filters);
  return Object.entries(filters).every(([key, selected]) => {
    if (!selected.length) return true;
    const exclude = filterModeExcludes(modes, key);
    // NOTES: a line matches when either of its notes is a ticked value - the
    // same rule, and the same key, the server compiles.
    if (key === "mergedNotes") {
      const wanted = new Set(selected.map((value) => mergedNoteKey(value)));
      const lineHit = (row) => mergedNoteLineKeys(mergedNoteInputsFromClaim(claim, row)).some((lineKey) => wanted.has(lineKey));
      if (mode === "service") return lineHit(service) !== exclude;
      return claim.services.some((item) => lineHit(item) !== exclude);
    }
    const hit = (row) => selected.some((value) => columnFilterValueMatches(key, value, getColumnValue(key, claim, row)));
    if (mode === "service") {
      return hit(service) !== exclude;
    }
    if (serviceColumnOnly(key)) {
      return claim.services.some((item) => hit(item) !== exclude);
    }
    return hit(null) !== exclude;
  });
}

// AN EXCEL-STYLE DROPDOWN ON A FREE-TEXT CLAIM-NOTE COLUMN (2026-09-17).
//
// Owner ask: open the Latest Claim Note header and READ what the notes are,
// because "a lot of times they are the same", then tick one or several to
// narrow the grid. Two things make a note column different from every other
// filterable column, and both are handled here rather than in the menu:
//
//   GROUPING. The same sentence typed twice differs by a trailing space, a
//   CRLF a biller pasted in, or a capital letter. Grouping on the raw string
//   splits one real value into four rows with count 1 and destroys exactly the
//   "these are all the same" signal the dropdown exists to show. So the group
//   key is the text with every whitespace run (newlines included) collapsed to
//   one space, trimmed, and lowercased, and the row DISPLAYS the most common
//   original spelling in that group.
//
//   THE FILTER USES THE IDENTICAL KEY. columnFilterValueMatches() below runs
//   both sides through noteFilterGroupKey(), so ticking a value returns exactly
//   the lines that were counted under it - the property
//   verify_note_column_dropdown.js mutation-proves. If the two ever diverge the
//   menu becomes a liar: it promises 37 lines and the grid shows 12.
//
// "Blank" is the existing repo-wide vocabulary for empty (addListPredicate and
// addSlicerPredicate both special-case it), so that is the STORED value; the
// menu renders it as Excel does, "(Blanks)". getColumnValue() returns the "N/A"
// sentinel for a line with no note, so the sentinel folds into the same bucket.
// A note whose whole text is literally "Blank" or "N/A" therefore lands in that
// bucket too - consistently on BOTH sides, so the count still equals the rows.
const NOTE_FILTER_COLUMN_KEYS = new Set(["serviceLineNotes", "notes", "sourceServiceLineNotes", "mergedNotes"]);
const NOTE_FILTER_BLANK_VALUE = "Blank";
// A bound, not a guess: the menu is a reading surface, and past a few hundred
// rows nobody reads it - they search. Truncation is SHOWN, never silent.
const NOTE_FILTER_MAX_OPTIONS = 500;
function isNoteFilterColumn(key) {
  return NOTE_FILTER_COLUMN_KEYS.has(String(key === null || key === undefined ? "" : key));
}
function normalizeNoteFilterText(value) {
  return String(value === null || value === undefined ? "" : value)
    .replace(/[\u2028\u2029]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
function noteFilterDisplayValue(value) {
  const text = normalizeNoteFilterText(value);
  return !text || /^n\/a$/i.test(text) ? NOTE_FILTER_BLANK_VALUE : text;
}
function noteFilterGroupKey(value) {
  return noteFilterDisplayValue(value).toLowerCase();
}
// Regroups an already-counted raw-value map into note groups. Takes counts
// rather than rows so it serves the local lane and any future server facet
// without a second traversal of the book.
function noteFilterOptionsFromCounts(counts) {
  const groups = new Map();
  counts.forEach((count, rawValue) => {
    const groupKey = noteFilterGroupKey(rawValue);
    const display = noteFilterDisplayValue(rawValue);
    const group = groups.get(groupKey) || { count: 0, spellings: new Map() };
    group.count += nonNegativeNumber(count, 0);
    group.spellings.set(display, (group.spellings.get(display) || 0) + nonNegativeNumber(count, 0));
    groups.set(groupKey, group);
  });
  const all = Array.from(groups.entries()).map(([groupKey, group]) => ({
    value: groupKey === NOTE_FILTER_BLANK_VALUE.toLowerCase()
      ? NOTE_FILTER_BLANK_VALUE
      : Array.from(group.spellings.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0],
    count: group.count
  }));
  // Most common first - that is the whole point of the reading surface - with
  // the empty bucket last, where Excel puts (Blanks).
  all.sort((a, b) => {
    const aBlank = a.value === NOTE_FILTER_BLANK_VALUE;
    const bBlank = b.value === NOTE_FILTER_BLANK_VALUE;
    if (aBlank !== bBlank) return aBlank ? 1 : -1;
    return b.count - a.count || a.value.localeCompare(b.value);
  });
  // The empty bucket is never truncated away. It sorts LAST (where Excel puts
  // it), so a plain slice deleted the option a biller reaches for most often
  // the moment a book had more distinct notes than the cap. The cap applies to
  // the NAMED notes; (Blanks) is then put back on the end.
  const blank = all.find((option) => option.value === NOTE_FILTER_BLANK_VALUE) || null;
  const named = all.filter((option) => option.value !== NOTE_FILTER_BLANK_VALUE);
  const shown = named.slice(0, NOTE_FILTER_MAX_OPTIONS - (blank ? 1 : 0));
  const options = blank ? shown.concat(blank) : shown;
  options.totalDistinct = all.length;
  options.truncated = named.length > shown.length;
  return options;
}
// One label resolver for every column-filter menu, so the two menus cannot
// disagree about what a stored value is called.
// A note is free text, so its chip shows a readable stub rather than the body.
// The full value stays on the remove attribute and in state - only the LABEL is
// clamped, so removing the chip still removes exactly the right selection.
const NOTE_FILTER_CHIP_MAX_CHARS = 60;
// HOW AN EXCLUSION NAMES ITSELF.
//
// "Follow-Up Action: 5 values" over an exclusion is the same lie as "3 selected"
// on the card: it names what is HIDDEN as though it were what is shown. Every
// chip, badge and formula term therefore reads the direction out loud, and when
// a whole family was excluded it says the family's name and how many spellings
// it covered rather than five near-identical strings the reader has to compare.
function filterSelectionSummaryParts(key, values) {
  const list = Array.isArray(values) ? values.slice() : [];
  if (key !== "action") return list.map((value) => String(value));
  const family = list.filter((value) => followUpActionInFamily(value));
  const parts = list.filter((value) => !followUpActionInFamily(value)).map((value) => slicerDisplayValue("action", value));
  if (family.length >= 2) parts.unshift(`${CLIENT_ACTION_PENDING_FAMILY} (${family.length})`);
  else family.forEach((value) => parts.unshift(slicerDisplayValue("action", value)));
  return parts;
}

function filterSelectionSummaryText(key, values, exclude) {
  const parts = filterSelectionSummaryParts(key, values);
  const body = parts.length > 3 ? `${parts.length} values` : parts.join(", ");
  return exclude ? `not ${body}` : body;
}

function columnFilterChipText(key, value) {
  if (key === "action") return slicerDisplayValue(key, value);
  if (!isNoteFilterColumn(key)) return value;
  const label = columnFilterOptionLabel(key, value);
  return label.length > NOTE_FILTER_CHIP_MAX_CHARS ? `${label.slice(0, NOTE_FILTER_CHIP_MAX_CHARS)}\u2026` : label;
}
// ---------------------------------------------------------------------------
// THE NUMBER / DATE FILTER SECTION, shared by BOTH header menus.
// ---------------------------------------------------------------------------
// One builder and one wiring function, used by the server-paginated menu and by
// the in-memory one, so the live lane and the local lane can never offer
// different words for the same decision - the same reason
// columnFilterModeControlsHtml is shared.
//
// IT STAGES INTO THE MENU'S OWN CONTEXT AND IS COMMITTED BY THE EXISTING APPLY
// BUTTON. Not its own Apply: a column can carry a checklist AND a comparison at
// once (Excel composes them with AND, and so does the server), so two Apply
// buttons would let a reader commit half of what they set up and leave the other
// half staged behind a closed popover.
//
// TICKING IS NOT RE-RENDERING. The operator select toggles the operand boxes in
// place rather than rebuilding the popover's innerHTML, for the reason recorded
// on the follow-up family button: rebuilding from inside a handler DETACHES the
// control that was just used, and the outside-click guard then reads a target
// that is no longer inside the menu and closes the dropdown.
function columnFilterRangeSectionHtml(key, range, expanded = false) {
  const family = columnFilterRangeFamily(key);
  if (!family) return "";
  const operators = columnFilterRangeOperators(key);
  const current = range && range.op && operators.includes(range.op) ? range.op : "";
  const arity = current ? (COLUMN_FILTER_RANGE_ARITY[current] || 0) : 0;
  const inputType = family === "date" ? "date" : "text";
  const title = family === "date" ? "Date filters" : "Number filters";
  const label = columnLabel(key);
  const operandValue = (value) => {
    if (value == null) return "";
    return String(value);
  };
  return '<details class="column-filter-compare" data-range-section' + (current || expanded ? " open" : "") + '>'
    + '<summary>' + escapeHtml(title) + (current ? ' <strong data-range-on>on</strong>' : '') + '</summary>'
    + '<div class="column-filter-compare-body">'
    + '<select class="column-filter-compare-op" data-range-op aria-label="' + escapeHtml(label) + ' comparison">'
    + '<option value=""' + (current ? "" : " selected") + '>No comparison</option>'
    + operators.map((op) => '<option value="' + escapeHtml(op) + '"' + (current === op ? " selected" : "") + '>'
      + escapeHtml(COLUMN_FILTER_RANGE_OPERATOR_LABELS[op] || op) + '</option>').join("")
    + '</select>'
    + '<input class="column-filter-compare-value" type="' + inputType + '" data-range-value'
    + ' aria-label="' + escapeHtml(label) + ' value"'
    + ' placeholder="' + escapeHtml(family === "date" ? "yyyy-mm-dd" : "Value") + '"'
    + ' value="' + escapeHtml(operandValue(range && range.value)) + '"' + (arity >= 1 ? "" : " hidden") + '>'
    + '<input class="column-filter-compare-value" type="' + inputType + '" data-range-value2'
    + ' aria-label="' + escapeHtml(label) + ' upper bound"'
    + ' placeholder="' + escapeHtml(family === "date" ? "yyyy-mm-dd" : "and") + '"'
    + ' value="' + escapeHtml(operandValue(range && range.value2)) + '"' + (arity >= 2 ? "" : " hidden") + '>'
    + '<p class="column-filter-compare-note" data-range-note aria-live="polite"></p>'
    + '</div></details>';
}

// The staged range, read off the controls. Returns null when the reader has
// chosen no comparison, and null - with a visible note - when they have chosen
// one but not finished it, so Apply never commits half a "between".
function columnFilterRangeFromMenu(menu, key) {
  const section = menu && menu.querySelector("[data-range-section]");
  if (!section) return { range: null, incomplete: false };
  const op = String(section.querySelector("[data-range-op]")?.value || "");
  if (!op) return { range: null, incomplete: false };
  const raw = {
    op,
    value: section.querySelector("[data-range-value]")?.value || "",
    value2: section.querySelector("[data-range-value2]")?.value || ""
  };
  const range = normalizeColumnFilterRangeValue(key, raw);
  return { range, incomplete: !range };
}

function wireColumnFilterRangeControls(menu, key, context) {
  const section = menu && menu.querySelector("[data-range-section]");
  if (!section) return;
  const operator = section.querySelector("[data-range-op]");
  const first = section.querySelector("[data-range-value]");
  const second = section.querySelector("[data-range-value2]");
  const note = section.querySelector("[data-range-note]");
  section.addEventListener("toggle", () => { context.rangeOpen = section.open; });
  const sync = () => {
    context.rangeDraft = { op: operator.value, value: first.value, value2: second.value };
    const arity = COLUMN_FILTER_RANGE_ARITY[operator.value] || 0;
    first.hidden = arity < 1;
    second.hidden = arity < 2;
    const staged = columnFilterRangeFromMenu(menu, key);
    context.range = staged.range;
    // NAMED, NOT SILENT. An operator with no operand is the one way this control
    // can be set up and do nothing; saying so beats an Apply that appears to
    // work and changes no rows.
    if (note) {
      note.textContent = staged.incomplete
        ? (columnFilterRangeFamily(key) === "date" ? "Enter a date to apply this comparison." : "Enter a number to apply this comparison.")
        : "";
    }
  };
  // TYPING IS NOT TICKING. "greater than 500" passes through "5" and "50", and
  // each of those is a complete-looking comparison that would fire a query and
  // repaint the grid under someone mid-number. So this half keeps its Apply and
  // commits on BLUR or ENTER - the two moments a person is actually finished -
  // while the checklist beside it commits by itself (owner, 2026-09-22:
  // "fewer buttons to click", and this is where an immediate apply would be
  // wrong).
  const commit = () => {
    if (typeof context.commitRange !== "function") return;
    const staged = columnFilterRangeFromMenu(menu, key);
    if (staged.incomplete) return;
    context.commitRange();
  };
  operator.addEventListener("change", () => { sync(); commit(); });
  [first, second].forEach((input) => {
    input.addEventListener("input", sync);
    input.addEventListener("blur", () => { if (!context.repainting && input.isConnected) commit(); });
    input.addEventListener("keydown", (event) => { if (event.key !== "Enter") return; event.preventDefault(); sync(); commit(); });
  });
  sync();
}

// A MONEY OPTION ARRIVES AS THE STRING THE GROUP BY PRODUCED - "230.9400",
// because the column is decimal(18,4) and the facet groups on the stored
// number rather than on its text. That string is the FILTER VALUE and must
// travel back unchanged; this is only what the reader sees, and they should
// see what the cell shows them: $230.94.
//
// TWO DECIMALS, NOT THE GRID CELL'S WHOLE DOLLARS. The cell rounds for
// density; a MENU may not, because two different charges that round to the
// same dollar would become two options reading the same words. The money
// presentation contract is about CELLS and is untouched - nothing here goes
// near formatApiCellValue or money().
function columnFilterNumberOptionLabel(key, value) {
  const text = String(value == null ? "" : value).trim();
  if (!text || /^blank$/i.test(text)) return "(Blanks)";
  const parsed = parseMoneyNumber(text);
  if (!Number.isFinite(parsed)) return text;
  return currencyColumns.has(key) ? Formatters.moneyExact(parsed) : String(Number(parsed));
}

// A NUMBER COLUMN READS IN VALUE ORDER. The bounded lane ranks its options by
// COUNT, which is what makes the list finite, and smartValueSort compares the
// leading digits of a string - so "1000.0000" sorted before "99.0000" and a
// money menu read as noise. Ordered numerically here, with the blanks last
// because "no charge on file" is not a small amount.
function columnFilterOptionComparator(key) {
  if (!COLUMN_FILTER_NUMBER_COLUMNS.has(key)) return (a, b) => smartValueSort(a.value, b.value);
  const rank = (option) => {
    const parsed = parseMoneyNumber(String(option?.value == null ? "" : option.value));
    return Number.isFinite(parsed) ? parsed : null;
  };
  return (a, b) => {
    const left = rank(a);
    const right = rank(b);
    if (left == null && right == null) return smartValueSort(a.value, b.value);
    if (left == null) return 1;
    if (right == null) return -1;
    return left - right;
  };
}

// A BILLER TYPES WHAT THE MENU SHOWS THEM, AND THE MENU SHOWS $1,230.94 while
// SQL grouped on 1230.9400.
//
// The SERVER searches the GROUPED string - that is what makes the search reach
// past the TOP-N the browser already has in hand - so a needle carrying a
// currency symbol or a thousands separator would match nothing at all, on a
// column where the reader has every reason to type both. Stripped to the
// characters the grouped form can actually contain. The minus survives, so
// "-$250.00" still finds -250.0000.
//
// A needle with no digit left in it (a bare "$") becomes NO SEARCH rather than
// a search for the empty string: the first shows the whole bounded list, which
// is what the reader was looking at a keystroke ago, and the second would show
// it too while claiming to have searched.
//
// The LOCAL narrowing in trackerLocalChoiceMatches still matches on the
// formatted label as well, so what is on screen keeps responding to "$1,2".
function columnFilterFacetSearchText(key, search) {
  const text = String(search == null ? "" : search);
  if (COLUMN_FILTER_DATE_COLUMNS.has(key)) {
    // The menu shows 05/05/2026; the server searches 2026-05-05 00:00:00.
    const us = /^\s*(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?\s*$/.exec(text);
    if (!us) return text;
    const md = `${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
    return us[3] ? `${us[3]}-${md}` : `-${md}`;
  }
  if (!COLUMN_FILTER_NUMBER_COLUMNS.has(key)) return text;
  const stripped = text.replace(/[$,\s]/g, "");
  return /[0-9]/.test(stripped) ? stripped : "";
}

// ONLY A NUMBER COLUMN IS RE-ORDERED HERE, and the restriction is load-bearing.
//
// The bounded lane hands its options back ranked by COUNT - most common first -
// and that order is a decision, not an accident: it is what makes a TOP-N list
// of 500 out of 4,113 the 500 worth showing. Every existing menu on that lane
// draws them in that order and verify_all_data_filter_controls_browser ticks
// options[0] expecting the most common one.
//
// A money column is the one place that order reads as noise, because the reader
// is looking for an AMOUNT, not a frequency. So it - and only it - is sorted by
// value. Follow-Up Actions use alphabetical reading order; other columns
// keep the order the server chose.
function columnFilterOptionsInReadingOrder(key, options) {
  if (key === "action" && Array.isArray(options)) return options.slice().sort(compareFollowUpActionChoices);
  if (!Array.isArray(options) || !COLUMN_FILTER_NUMBER_COLUMNS.has(key)) return options;
  return options.slice().sort(columnFilterOptionComparator(key));
}

function columnFilterOptionLabel(key, value) {
  if (key === "action") return slicerDisplayValue("action", value);
  if (isNoteFilterColumn(key) && value === NOTE_FILTER_BLANK_VALUE) return "(Blanks)";
  if (COLUMN_FILTER_NUMBER_COLUMNS.has(key)) return columnFilterNumberOptionLabel(key, value);
  if (COLUMN_FILTER_DATE_COLUMNS.has(key)) return columnFilterDateOptionLabel(value);
  return String(value === null || value === undefined ? "" : value);
}
// A date option arrives as the warehouse datetime2 text, "2026-05-05
// 00:00:00.0000000". That string stays the filter value; the reader sees the
// date the grid cell shows.
function columnFilterDateOptionLabel(value) {
  const text = String(value === null || value === undefined ? "" : value).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[ T]|$)/.exec(text);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : text;
}
function noteFilterOptionTitle(value) {
  return value === NOTE_FILTER_BLANK_VALUE ? "Lines with no claim note on file" : String(value);
}
// A note column needs a wider menu than the 300px categorical one or a wrapped
// sentence shows two words. Clamped to the viewport so 412px stays usable.
function columnFilterMenuWidth(key) {
  const viewport = Math.max(0, Number(window.innerWidth) || 0);
  if (!isNoteFilterColumn(key)) return 300;
  return viewport ? Math.max(260, Math.min(420, viewport - 24)) : 420;
}

function columnFilterValueMatches(key, selectedValue, rowValue) {
  // Same key on both sides - see the grouping note above.
  if (isNoteFilterColumn(key)) return noteFilterGroupKey(selectedValue) === noteFilterGroupKey(rowValue);
  if (key === "action") return followUpFilterValue(selectedValue) === followUpFilterValue(rowValue);
  if (currencyColumns.has(key) || numericColumns.has(key)) {
    const selectedNumber = currencyColumns.has(key) ? moneyNumber(selectedValue) : Number(selectedValue);
    const rowNumber = currencyColumns.has(key) ? moneyNumber(rowValue) : Number(rowValue);
    return Number.isFinite(selectedNumber) && Number.isFinite(rowNumber) && selectedNumber === rowNumber;
  }
  return String(selectedValue) === String(rowValue);
}

// Track the open column-filter menu so a facet response landing mid-open can
// re-render it in place (first open otherwise showed "No values" until the
// user closed and reopened the popup).
// A choice menu owns one bounded response. It does not replace the queue's
// complete facet totals or another menu's in-progress selection.
const trackerChoiceReads = new Map();
const trackerSlicerChoicePreferences = new Map();
function cancelTrackerChoiceRead(slot) {
  const previous = trackerChoiceReads.get(slot);
  previous?.preparedOwner?.observers?.delete(slot);
  for (const [subscriberSlot, subscriber] of trackerChoiceReads) {
    if (subscriber.preparedOwner === previous && previous) trackerChoiceReads.delete(subscriberSlot);
  }
  previous?.observers?.clear();
  if (previous?.timer) clearTimeout(previous.timer);
  previous?.controller?.abort();
  trackerChoiceReads.delete(slot);
  const preparation = typeof trackerAutomaticFacetPreparation === "undefined" ? null : trackerAutomaticFacetPreparation;
  if (previous && preparation?.records?.get(slot) === previous) {
    preparation.records.delete(slot);
    preparation.record = preparation.records.values().next().value || null;
    preparation.wake?.();
  }
}
function trackerChoiceRead(slot, query, onReady, debounce = false, refreshCurrent = false) {
  const key = trackerApiFacetCacheKey(query), previous = trackerChoiceReads.get(slot);
  if (!refreshCurrent && previous?.key === key && (previous.status !== "ok"
      || (!previous.fromCache && Number.isFinite(previous.at)
        && Date.now() - previous.at <= Math.min(...query.facets.map(facet => trackerFacetResponseTtlMs(facet, previous.response)))))) return previous;
  // FROM CACHE. The batch (or an earlier card at this same scope) already
  // answered every key this read asks for, so it is answered here, synchronously,
  // and the caller paints on the open instead of on a response.
  const cached = refreshCurrent ? null : trackerChoiceRecordFromFacetStore(key, query);
  if (cached) { cancelTrackerChoiceRead(slot); trackerChoiceReads.set(slot, cached); return cached; }
  // Opening a menu joins its automatic read instead of repeating the SQL.
  const prepared = trackerAutomaticChoiceRead(key, query);
  if (prepared && prepared.status === "pending" && slot !== prepared.slot) {
    cancelTrackerChoiceRead(slot);
    const subscription = { key, status: "pending", response: null, controller: null, timer: null, query, preparedOwner: prepared };
    trackerChoiceReads.set(slot, subscription);
    prepared.observers.set(slot, settled => {
      if (trackerChoiceReads.get(slot) !== subscription || trackerApiCurrentFacetCacheKey(query) !== key) return;
      subscription.status = settled.status;
      subscription.response = settled.response;
      subscription.at = settled.at;
      delete subscription.preparedOwner;
      onReady(subscription);
    });
    return subscription;
  }
  cancelTrackerChoiceRead(slot);
  if (trackerChoiceReads.size >= 24) cancelTrackerChoiceRead(trackerChoiceReads.keys().next().value);
  const controller = new AbortController(), record = { key, status: "pending", response: null, controller, timer: null, query };
  record.slot = slot;
  record.observers = new Map();
  trackerChoiceReads.set(slot, record);
  const current = () => trackerChoiceReads.get(slot) === record && trackerApiCurrentFacetCacheKey(query) === key;
  const run = async () => {
    record.timer = null;
    const timeout = setTimeout(() => controller.abort(), query.facets.some(facet => facet === "mergedNotes" || facet === "sourceServiceLineNotes") ? 45000 : query.facets.length > 1 ? 20000 : 10000);
    try {
      const token = trackerApiToken();
      const response = await fetch(refreshCurrent ? trackerApiBase() + "/api/tracker/facets?refresh=current" : trackerFacetReadUrl(trackerApiBase()), { method: "POST", cache: "no-store", headers: { "content-type": "application/json", ...(token ? { authorization: "Bearer " + token } : {}) }, body: JSON.stringify(query), signal: controller.signal });
      const body = await response.json().catch(() => ({}));
      if (!current()) return;
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      record.status = response.ok && !body.error && query.facets.every(facet => Object.hasOwn(body.facets || {}, facet)) ? "ok" : "error";
      record.response = record.status === "ok" ? body : null;
      if (record.status === "ok") record.at = Date.now();
      // The next card at this scope - and the next open of this one - is free.
      if (record.status === "ok") rememberTrackerFacetOptions(query, body);
    } catch (_) { if (!current()) return; record.status = "error"; }
    finally { clearTimeout(timeout); }
    if (current()) {
      const observers = Array.from(record.observers.values());
      record.observers.clear();
      observers.forEach(notify => notify(record));
      onReady(record);
    }
  };
  if (debounce) record.timer = setTimeout(run, 250); else run();
  return record;
}

// Prepare choices automatically, starting one read alongside the initial grid.
// Two reads after grid settlement avoid a burst of competing SQL work. The
// existing per-menu query builders retain self-filter removal and bounded pages.
let trackerAutomaticFacetPreparation = null;
function trackerAutomaticChoiceRead(key, query) {
  return Array.from(trackerAutomaticFacetPreparation?.records?.values() || []).find(record => record.key === key
    || (query?.facets?.every(facet => record.query?.facets?.includes(facet))
      && trackerApiFacetCacheKey({ ...record.query, facets: query.facets }) === key)) || null;
}
function groupTrackerAutomaticWorkflowChoices(choices) {
  const eligible = new Set(["action", "followUpDue", "followUpDate"]), groups = new Map(), result = [];
  for (const choice of choices) {
    if (choice.facetOptions || !choice.facets.every(facet => eligible.has(facet))) { result.push(choice); continue; }
    const key = trackerApiFacetCacheKey({ ...choice, facets: [] });
    const previous = groups.get(key);
    if (previous) previous.facets = Array.from(new Set([...previous.facets, ...choice.facets]));
    else { const group = { ...choice, facets: choice.facets.slice() }; groups.set(key, group); result.push(group); }
  }
  return result;
}
function groupTrackerAutomaticChoiceBatches(choices, eligible, maxFacets = 3) {
  const groups = new Map(), result = [];
  for (const choice of choices) {
    if (choice.facetOptions || choice.facets.length !== 1 || !eligible.has(choice.facets[0])) { result.push(choice); continue; }
    const key = trackerApiFacetCacheKey({ ...choice, facets: [] });
    const previous = groups.get(key);
    if (previous && previous.facets.length < maxFacets && !previous.facets.includes(choice.facets[0])) previous.facets.push(choice.facets[0]);
    else { const group = { ...choice, facets: choice.facets.slice() }; groups.set(key, group); result.push(group); }
  }
  return result;
}
function groupTrackerAutomaticNumericChoices(choices) {
  return groupTrackerAutomaticChoiceBatches(choices, COLUMN_FILTER_NUMBER_COLUMNS);
}
function groupTrackerAutomaticSlicerChoices(choices) {
  // These complete-list facets already share the server's ordinary batch lane.
  // Keep Notes, bounded text/date lists and mutable workflow choices separate.
  const eligible = new Set(["provider", "match", "aging", "primaryPayer", "secondaryPayer", "facility", "procedure", "claimStatus", "serviceMonth", "primaryPayerClass", "secondaryPayerClass", "patClassification", "secondaryCalc", "claimClassification", "place"]);
  return groupTrackerAutomaticChoiceBatches(choices, eligible, eligible.size);
}
function cancelObsoleteTrackerFilterPreparation(query) {
  if (trackerAutomaticFacetPreparation && trackerAutomaticFacetPreparation.scope !== trackerApiFacetScopeKey(query)) cancelTrackerFilterPreparation();
}
function cancelTrackerFilterPreparation() {
  const previous = trackerAutomaticFacetPreparation;
  trackerAutomaticFacetPreparation = null;
  if (previous?.timer) clearTimeout(previous.timer);
  for (const record of previous?.records?.values() || []) {
    if (trackerChoiceReads.get(record.slot) === record) cancelTrackerChoiceRead(record.slot);
  }
}
// AUTOMATIC RENEWAL IS PACED, NOT CONTINUOUS (2026-10-12).
//
// Renewing each prepared list shortly before its 15 s / 60 s lifetime, with a
// forced server-cache bypass, kept every open All Data tab reading facets
// back to back for as long as it stayed open: production recorded 8,459
// facet reads against 213 grid queries in 30 hours, one idle biller tab at
// ~200 reads a minute for 20 minutes, and every settle repainted the whole
// rail. That load slowed the grid and Claim ID lookups for everyone.
//
// Now a prepared list is renewed only when it is missing or two minutes old,
// only while someone is using the page, and through the server's cache. A
// biller's own saves still clear the store (invalidateTrackerApiFacetsAfterWorkflowWrite)
// and a new publication epoch still forces one current read. Opening a menu
// or card still demands a fresh list; closed cards show the last answer for
// this exact scope instead of "Open to load".
const TRACKER_AUTOMATIC_RENEWAL_AGE_MS = 120000;
const TRACKER_AUTOMATIC_IDLE_PAUSE_MS = 300000;
const TRACKER_AUTOMATIC_RENDER_COALESCE_MS = 150;
let trackerLastUserActivityAtMs = Date.now();
function noteTrackerUserActivity() { trackerLastUserActivityAtMs = Date.now(); }
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  ["pointerdown", "keydown", "wheel", "touchstart"].forEach(type => window.addEventListener(type, noteTrackerUserActivity, { capture: true, passive: true }));
}
function trackerAutomaticChoiceNeedsRenewal(choice) {
  return !trackerFacetOptionsFromStore(choice, Date.now(), 0, TRACKER_AUTOMATIC_RENEWAL_AGE_MS);
}
let trackerAutomaticChoiceRenderTimer = null;
function scheduleTrackerAutomaticChoiceRender() {
  if (trackerAutomaticChoiceRenderTimer !== null) return;
  trackerAutomaticChoiceRenderTimer = setTimeout(() => {
    trackerAutomaticChoiceRenderTimer = null;
    // A canceled preparation (scope change, view left) is repainted by
    // whatever changed the scope; a late repaint here would restart it.
    if (!trackerAutomaticFacetPreparation || state.view !== "workbench") return;
    renderSlicers();
  }, TRACKER_AUTOMATIC_RENDER_COALESCE_MS);
}
// COLUMN MENUS WAIT FOR A SETTLED FILTER (2026-10-13).
//
// Each filter change prepared every column-header menu (Claim ID, Service ID,
// Patient, DOS, M1, M2, money columns...): 17 facet reads per click. A biller
// stepping through Follow-up Actions made ~1,800 facet reads in 20 minutes
// against 5 grid queries. The slicer rail is still prepared at once; column
// menus are prepared only once the same filter has stood for 15 seconds, and a
// menu opened sooner loads on open as before.
const TRACKER_AUTOMATIC_COLUMN_DWELL_MS = 15000;
function trackerIdLookupQuery(query) {
  return Boolean(String(query?.filters?.claimIdSearch || "").trim() || String(query?.filters?.serviceIdSearch || "").trim());
}
function prepareTrackerFilterChoices(query) {
  const previous = trackerAutomaticFacetPreparation, scope = trackerApiFacetScopeKey(query);
  const active = () => trackerApiEnabled() && state.view === "workbench" && trackerApiFacetScopeKey(buildTrackerQueryRequest()) === scope;
  if (!active() || (previous && previous.scope !== scope)) cancelTrackerFilterPreparation();
  // A Claim ID / Service ID lookup is one record. Preparing every filter list
  // for it queued about 20 facet reads behind each lookup; menus load on open.
  if (trackerIdLookupQuery(query)) return;
  if (!active() || !["ok", "pending"].includes(trackerApiPageState.status) || trackerApiPageState.key !== trackerApiCacheKey(query)) return;
  if (trackerAutomaticFacetPreparation?.scope === scope) {
    if (trackerAutomaticFacetPreparation.pagePending && trackerApiPageState.status === "ok") {
      trackerAutomaticFacetPreparation.pagePending = false;
      trackerAutomaticFacetPreparation.wake?.();
    }
    return;
  }
  const choices = [], seen = new Set();
  const add = (choice, into = choices) => { const key = trackerApiFacetCacheKey(choice); if (!choice.facets?.length || seen.has(key)) return; seen.add(key); into.push(choice); };
  slicerDefinitions.forEach(definition => add(trackerFacetQueryRequest({ ...query, facets: [definition.key === "renderingProvider" ? "provider" : definition.key] })));
  const columnChoices = [];
  permittedColumnsForMode(query.mode).forEach(column => { if (columnFilterFacetKey(column.key)) add(trackerFacetQueryRequest(buildColumnFilterFacetQuery(column.key, query.mode)), columnChoices); });
  const group = list => groupTrackerAutomaticSlicerChoices(groupTrackerAutomaticNumericChoices(groupTrackerAutomaticWorkflowChoices(list)));
  choices.splice(0, choices.length, ...group(choices));
  const deferredColumnChoices = group(columnChoices);
  const priority = choice => choice.facets.includes("mergedNotes") ? 0 : choice.facets.some(key => TRACKER_WORKFLOW_BACKED_FACET_KEYS.has(key)) ? 2 : 1;
  // Short-lived workflow choices run after ordinary choices, avoiding expiry
  // while the initial queue is still being prepared. Open menus retain priority.
  choices.sort((a,b) => priority(a)-priority(b));
  const preparation = { scope, choices, pagePending: trackerApiPageState.status === "pending", initialComplete: false, allChoices: choices.slice(), failures: new Map(), records: new Map(), record: null, timer: null, maxPending: 0,
    deferredColumnChoices, columnsDueAt: Date.now() + TRACKER_AUTOMATIC_COLUMN_DWELL_MS };
  trackerAutomaticFacetPreparation = preparation;
  const current = () => trackerAutomaticFacetPreparation === preparation && active();
  const schedule = delay => {
    if (!current()) return;
    const dueAt = Date.now() + delay;
    if (preparation.timer !== null && preparation.timerDueAt <= dueAt) return;
    if (preparation.timer !== null) clearTimeout(preparation.timer);
    preparation.timerDueAt = dueAt;
    preparation.timer = setTimeout(advance, delay);
  };
  const advance = () => {
    preparation.timer = null;
    if (!current()) { if (trackerAutomaticFacetPreparation === preparation) cancelTrackerFilterPreparation(); return; }
    if (typeof document !== "undefined" && document.hidden) { schedule(1000); return; }
    if (preparation.initialComplete && Date.now() - trackerLastUserActivityAtMs > TRACKER_AUTOMATIC_IDLE_PAUSE_MS) { schedule(5000); return; }
    if (preparation.deferredColumnChoices.length && Date.now() >= preparation.columnsDueAt) {
      const settledColumns = preparation.deferredColumnChoices.sort((a,b) => priority(a)-priority(b));
      preparation.deferredColumnChoices = [];
      choices.push(...settledColumns);
      // Once the filter has settled the old order holds: Notes, the slowest read, first.
      choices.sort((a,b) => priority(a)-priority(b));
      preparation.allChoices.push(...settledColumns);
    }
    if (!choices.length && !preparation.records.size) preparation.initialComplete = true;
    if (preparation.initialComplete) {
      const scheduled = new Set([...choices.map(trackerApiFacetCacheKey), ...Array.from(preparation.records.values(), record => record.preparationKey || record.key)]);
      const now = Date.now();
      for (const choice of preparation.allChoices) {
        const failure = preparation.failures.get(trackerApiFacetCacheKey(choice));
        if (!scheduled.has(trackerApiFacetCacheKey(choice)) && trackerAutomaticChoiceNeedsRenewal(choice) && (!failure || (failure.count < 3 && now >= failure.retryAt))) choices.push(choice);
      }
      const renewalPriority = choice => priority(choice) === 2 ? 0 : priority(choice) === 0 ? 1 : 2;
      choices.sort((a,b) => renewalPriority(a) - renewalPriority(b));
      if (!choices.length) { schedule(preparation.deferredColumnChoices.length ? Math.max(0, Math.min(1000, preparation.columnsDueAt - Date.now())) : 1000); return; }
    }
    if ((preparation.initialComplete && trackerApiPageState.status === "pending") || Array.from(trackerChoiceReads.entries()).some(([slot, record]) => !slot.startsWith("automatic-choice-") && record.status === "pending")) { schedule(200); return; }
    // Start one initial choice beside the grid; release the other slot when it settles.
    for (let index=0;index<(trackerApiPageState.status === "pending" ? 1 : 2);index++) {
      const slot = "automatic-choice-" + index;
      if (preparation.records.has(slot)) continue;
      while (choices.length && !trackerAutomaticChoiceNeedsRenewal(choices[0])) choices.shift();
      if (!choices.length) break;
      const queuedChoice = choices.shift();
      // A menu may have answered part of this batch while it waited. Re-read
      // freshness per facet so automatic work does not repeat that same SQL.
      const facets = queuedChoice.facets.filter(facet => trackerAutomaticChoiceNeedsRenewal({ ...queuedChoice, facets: [facet] }));
      if (!facets.length) continue;
      const choice = { ...queuedChoice, facets };
      const preparationKey = trackerApiFacetCacheKey(queuedChoice);
      // The option store is authoritative: a retained slot cannot suppress renewal.
      cancelTrackerChoiceRead(slot);
      const record = trackerChoiceRead(slot, choice, settled => {
        if (!current()) return;
        const key = preparationKey;
        if (settled.status === "ok") preparation.failures.delete(key);
        else {
          const count = (preparation.failures.get(key)?.count || 0) + 1;
          preparation.failures.set(key, { count, retryAt: Date.now() + 15000 * count });
        }
        preparation.records.delete(slot);
        preparation.record = preparation.records.values().next().value || null;
        scheduleTrackerAutomaticChoiceRender(); schedule(0);
      }, false, false);
      record.preparationKey = preparationKey;
      if (record.status === "pending") preparation.records.set(slot,record);
      else schedule(0);
      preparation.record = preparation.records.values().next().value || null;
      preparation.maxPending = Math.max(preparation.maxPending, preparation.records.size);
    }
    if (preparation.initialComplete || (!choices.length && !preparation.records.size)) schedule(1000);
  };
  preparation.wake = () => schedule(0);
  schedule(0);
}

// WHICH FACETS THE SERVER ACTUALLY SEARCHES, AND WHICH ONES SEARCH THEMSELVES.
//
// /api/tracker/facets answers All Data in two shapes. The three SQL-bounded
// columns (Claim ID, Service ID, Service Line Notes) come back as a TOP-N WITH
// facetPages metadata, and their search has to run in SQL because the rest of
// the list was never sent. Every OTHER facet comes back WHOLE and carries no
// facetPages at all - and for those the search term this file posted was read by
// nobody: the live lane runs pagePreparedFacets only inside a report context
// (trackerSynapseApiAdapter), and the JSON lane ignores facetOptions outright.
//
// So typing into "Search Primary Payer", or into any slicer card's own search
// box, spent a round trip per keystroke and came back with the identical list.
// Worse, "Select this page" / "Select all" then ticked that WHOLE list: a biller
// who searched for one payer and pressed Select all selected every payer.
//
// A list that arrived whole is searched HERE, where it already is. The shape is
// remembered after the first answer so the needle stays OUT of the request body:
// the facet cache key does not move, trackerChoiceRead serves from memory, and
// searching an ordinary column costs zero requests instead of one per keystroke.
const trackerWholeFacetKeys = new Set();

function noteTrackerFacetShape(facetKey, record) {
  if (!facetKey || !record || record.status !== "ok") return;
  if (record.response?.facetPages?.[facetKey]) trackerWholeFacetKeys.delete(facetKey);
  else trackerWholeFacetKeys.add(facetKey);
}

function trackerFacetSearchesItself(facetKey) {
  return trackerWholeFacetKeys.has(facetKey);
}

// Action-name prefix matching narrows the server's substring result further.
// Other facets never filter a list the SERVER already narrowed: a bounded lane's rows are
// its answer to this needle, and filtering them again would drop options whose
// stored value matches while the thousands it could not send stay invisible.
function trackerLocalChoiceMatches(rows, facetKey, search, labelFor) {
  const needle = String(search == null ? "" : search).trim().toLocaleLowerCase();
  if (facetKey === "action") return rows.filter((row) => followUpActionChoiceMatches("action", typeof labelFor === "function" ? labelFor(row.value) : slicerDisplayValue("action", row.value), needle))
    .sort((left, right) => compareFollowUpActionChoices(slicerDisplayValue("action", left.value), slicerDisplayValue("action", right.value)));
  if (!needle || !trackerFacetSearchesItself(facetKey)) return rows;
  return rows.filter((row) => {
    const value = String(row?.value == null ? "" : row.value);
    if (value.toLocaleLowerCase().includes(needle)) return true;
    const label = typeof labelFor === "function" ? labelFor(value) : "";
    return String(label == null ? "" : label).toLocaleLowerCase().includes(needle);
  });
}

// The needle belongs in the request only when a lane exists to read it.
function applyTrackerChoiceRequestOptions(query, facetKey, { search = "", page = 1, pageSize = 50 } = {}) {
  if (trackerFacetSearchesItself(facetKey)) return query;
  if (!String(search || "") && Number(page) <= 1) return query;
  query.facetOptions = { [facetKey]: { search, page, pageSize } };
  return query;
}

// THE FOOT OF A CHOICE LIST MUST DESCRIBE THE LIST IT IS UNDER.
//
// Three different servers answer this control and only ONE of them pages:
//
//   OFFSET-PAGED   the prepared/report lane, which honours facetOptions.page and
//                  returns facetPages{page,pageSize,matchingOptions,hasMore}.
//                  Previous/Next are real there.
//   SEARCH-BOUNDED the EXPLICIT_ONLY lane (Service Line Notes, Claim ID, Service
//                  ID). Its SQL is a TOP-N with no OFFSET, so it answers page 2
//                  with the SAME options page 1 had. It marks itself `bounded`.
//   UNPAGED        the live Synapse lane for every ordinary bounded column: it
//                  returns the whole option list and NO facetPages at all.
//
// One caption and one pair of buttons used to be drawn for all three, keyed on a
// hardcoded page size of 50. So a Service Line Notes menu showing 500 options
// read "1–50 of 4,113 choices" with Next enabled, and pressing Next re-rendered
// the identical 500 under "501–1,000 of 4,113" - the control moved and the list
// did not. An ordinary column's full list read "No matching choices" underneath
// the choices it was sitting on, because no facetPages meant total === 0.
//
// `optionCount` is what is ON SCREEN; meta is what the server said about it.
function trackerChoicePagerHtml(meta = {}, status = "ok", optionCount = 0) {
  const shown = Math.max(0, Number(optionCount) || 0);
  const page = Number(meta.page) || 1, total = Number(meta.matchingOptions) || 0;
  const size = Math.max(1, Number(meta.pageSize) || 50);
  // A search-bounded lane has no second page to offer, so it offers none: the
  // caption names the bound and the control that reaches past it.
  const bounded = meta.bounded === true;
  const paged = !bounded && Number(meta.pageSize) > 0 && total > 0;
  const caption = status === "pending" ? 'Loading choices…'
    : status === "error" ? 'Choices could not be loaded.'
    : bounded ? (meta.hasMore
      ? ('Showing the ' + shown.toLocaleString() + ' most common of ' + total.toLocaleString() + ' choices. Search to reach the rest.')
      : (shown.toLocaleString() + (shown === 1 ? ' choice' : ' choices')))
    : paged ? (((page - 1) * size + 1) + '–' + Math.min(page * size, total) + ' of ' + total.toLocaleString() + ' choices')
    : shown ? (shown.toLocaleString() + (shown === 1 ? ' choice' : ' choices'))
    : 'No matching choices';
  return '<div class="tracker-choice-pager"><span role="status">' + caption + '</span>'
    + (bounded ? '' : '<button type="button" class="small-button" data-choice-prev' + (page <= 1 || !paged || status !== "ok" ? ' disabled' : '') + '>Previous choices</button><button type="button" class="small-button" data-choice-next' + (!meta.hasMore || !paged || status !== "ok" ? ' disabled' : '') + '>Next choices</button>')
    + (status === "error" ? '<button type="button" class="small-button" data-choice-retry>Retry choices</button>' : '') + '</div>';
}
// THE CARET THIS POPOVER HANGS OFF IS NOT A STABLE NODE.
//
// The popover captures the <button> it was opened from and keeps using it for
// two things: where to draw itself, and where to put focus back on Escape. But
// All Data repaints its whole header whenever a tracker page or facet response
// lands - renderClaimsTable() rebuilds every <th> - so on the live lane that
// captured node is routinely DETACHED while the popover is still open. A
// detached node reports a 0x0 rect at 0,0, so a re-render threw the menu into
// the top-left corner of the page, and focus() on it silently does nothing, so
// Escape dropped focus to <body> instead of returning it to the header.
//
// Re-resolve the live caret for the column instead of trusting the captured one.
// The column key is already the unique handle the header emits.
// A GRID REPAINT MUST NOT SWALLOW THE KEYBOARD (2026-09-22).
//
// closeColumnFilterMenu({ restoreFocus: true }) puts focus back on the caret the
// popover was opened from - and Apply and Clear then call renderClaimsTable(),
// which rebuilds every <th> and throws that node away. So the two buttons that
// CHANGE something left focus on <body>, while Cancel and Escape (which do not
// re-render) worked. Simply focusing after the synchronous render would not have
// held either: the server page lands a moment later and rebuilds the header a
// second time.
//
// The request therefore outlives the repaints. Every header rebuild re-seats it
// while nothing else holds focus, and it is dropped the moment the reader puts
// focus somewhere of their own choosing - so it can restore a focus a repaint
// took, and can never steal one the reader gave.
let pendingColumnFilterCaretFocus = "";

function requestColumnFilterCaretFocus(key) {
  pendingColumnFilterCaretFocus = String(key || "");
}

function restorePendingColumnFilterCaretFocus() {
  if (!pendingColumnFilterCaretFocus) return;
  const key = pendingColumnFilterCaretFocus;
  const active = document.activeElement;
  const onOwnCaret = active?.getAttribute?.("data-column-filter") === key;
  if (active && active !== document.body && !onOwnCaret) { pendingColumnFilterCaretFocus = ""; return; }
  if (onOwnCaret && active.isConnected !== false) return;
  document.querySelector('[data-column-filter="' + key.replace(/"/g, "") + '"]')?.focus?.();
}

function columnFilterTriggerNode(key, button) {
  if (button && button.isConnected !== false) return button;
  return document.querySelector('[data-column-filter="' + String(key).replace(/"/g, '') + '"]') || button || null;
}

function openPagedColumnFilterMenu(key, button) {
  const facet = columnFilterFacetKey(key); if (!facet) return;
  const query = trackerFacetQueryRequest(buildColumnFilterFacetQuery(key));
  const scopeKey = trackerApiFacetCacheKey(query);
  let context = openColumnFilterContext;
  // A late facet repaint can arrive after the header itself was replaced. The
  // menu's draft belongs to this open menu and exact filter scope, not to the
  // old button node. A changed company/filter scope starts a fresh draft.
  if (!context || context.key !== key || !context.draft || context.scopeKey !== scopeKey) {
    context = { key, button, scopeKey, search: "", page: 1, draft: new Set(state.columnFilters?.[key] || []), mode: filterModeFor(columnFilterModeState(), key), range: columnFilterRangeFor(key), rangeDraft: null, rangeOpen: columnFilterRangeFamily(key) === "date" };
    openColumnFilterContext = context;
  }
  applyTrackerChoiceRequestOptions(query, facet, { search: columnFilterFacetSearchText(key, context.search), page: context.page, pageSize: 50 });
  const rerender = () => { if (openColumnFilterContext === context) openPagedColumnFilterMenu(key, button); };
  const record = trackerChoiceRead("column", query, rerender, Boolean(context.debounce)); context.debounce = false;
  noteTrackerFacetShape(facet, record);
  const meta = record.response?.facetPages?.[facet] || {};
  // Matched on the stored value AND on the label the row is drawn with, so
  // searching "no follow-up" finds the Blank follow-up rows the same way the
  // local lane's own search box does.
  const options = columnFilterOptionsInReadingOrder(key, trackerLocalChoiceMatches(
    (record.response?.facets?.[facet] || []).map(row => ({ value: cleanFacetDisplayValue(row.value), count: nonNegativeNumber(row.count, 0) })).filter(row => row.value),
    facet, context.search, value => columnFilterOptionLabel(key, value)));
  const column = currentColumns().find(item => item.key === key) || { label: key };
  const focus = document.activeElement === els.columnFilterMenu.querySelector('[data-filter-search]'), cursor = els.columnFilterMenu.querySelector('[data-filter-search]')?.selectionStart;
  const rangeFocus = ['[data-range-op]', '[data-range-value]', '[data-range-value2]'].find(selector => document.activeElement === els.columnFilterMenu.querySelector(selector));
  // Preserve the selection as well as the caret: a late facet repaint between
  // Select All and replacement typing must not append the old operand.
  const rangeSelection = rangeFocus && typeof document.activeElement?.selectionStart === 'number'
    ? { start: document.activeElement.selectionStart, end: document.activeElement.selectionEnd, direction: document.activeElement.selectionDirection } : null;
  context.repainting = true;
  els.columnFilterMenu.innerHTML = '<div class="column-filter-title">Filter ' + escapeHtml(column.label) + '</div><input class="column-filter-search" type="search" value="' + escapeHtml(context.search) + '" placeholder="Search ' + escapeHtml(column.label) + '" aria-label="Search ' + escapeHtml(column.label) + ' values" data-filter-search>'
    + columnFilterModeControlsHtml(key, context.mode)
    + columnFilterRangeSectionHtml(key, context.rangeDraft || context.range, context.rangeOpen)
    + '<p class="muted-note">' + (context.draft.size ? context.draft.size + (context.mode === FILTER_MODE_EXCLUDE ? ' excluded across all choice pages' : ' selected across all choice pages') : 'All values. Choose values to narrow the results.') + '</p><label class="column-filter-select-all"><input type="checkbox" data-filter-select-all' + (options.length && options.every(row => context.draft.has(row.value)) ? ' checked' : '') + '>Select this page</label>'
    + '<div class="column-filter-options">' + options.map(row => '<label class="column-filter-choice' + (isNoteFilterColumn(key) ? ' column-filter-choice--multiline' : '') + '"' + (isNoteFilterColumn(key) ? ' title="' + escapeHtml(noteFilterOptionTitle(row.value)) + '"' : '') + '><input type="checkbox" data-filter-choice value="' + escapeHtml(row.value) + '"' + (context.draft.has(row.value) ? ' checked' : '') + '><span>' + escapeHtml(columnFilterOptionLabel(key, row.value)) + '</span><strong>' + row.count + '</strong></label>').join('') + '</div>'
    + trackerChoicePagerHtml(meta, record.status, options.length)
    + (key === "mergedNotes" ? mergedNotesMenuFootHtml(record) : isNoteFilterColumn(key) ? '<p class="column-filter-truncated">These are the notes in the last published snapshot, so a note typed in the EHR since then appears after the next refresh. Very long notes are grouped by their first 4,000 characters; ticking one returns every line that matches that far.</p>' : '')
    + '<p class="column-filter-applying" data-filter-applying role="status" aria-live="polite" hidden>Applying\u2026</p>'
    + '<div class="column-filter-actions"><button class="small-button" type="button" data-filter-clear>Clear</button><button class="small-button" type="button" data-filter-cancel>Cancel</button><button class="primary-button" type="button" data-filter-apply>Apply</button></div>';
  button = columnFilterTriggerNode(key, button) || button;
  context.button = button;
  const rect = button.getBoundingClientRect();
  const pagedMenuWidth = columnFilterMenuWidth(key);
  els.columnFilterMenu.style.width = pagedMenuWidth + 'px';
  els.columnFilterMenu.toggleAttribute('data-note-values', isNoteFilterColumn(key));
  els.columnFilterMenu.style.left = Math.max(window.scrollX + 8, Math.min(window.scrollX + rect.left, window.scrollX + window.innerWidth - pagedMenuWidth - 8)) + 'px';
  els.columnFilterMenu.style.top = Math.max(window.scrollY + 8, Math.min(window.scrollY + rect.bottom + 4, window.scrollY + window.innerHeight - 430)) + 'px';
  els.columnFilterMenu.style.setProperty('--filter-options-max-height', Math.max(72, Math.min(230, window.innerHeight - 270)) + 'px');
  els.columnFilterMenu.hidden = false;
  const search = els.columnFilterMenu.querySelector('[data-filter-search]');
  search.addEventListener('input', () => { context.search = search.value; context.page = 1; context.debounce = true; rerender(); });
  const inputs = Array.from(els.columnFilterMenu.querySelectorAll('[data-filter-choice]'));
  // What an auto-applied commit sends. Read at FIRE time, not at schedule time,
  // so a burst commits the state the reader finished with.
  const readPagedMenu = () => ({ checked: Array.from(context.draft), allValues: [], mode: context.mode, range: columnFilterRangeFromMenu(els.columnFilterMenu, key).range });
  context.commitRange = () => { cancelColumnFilterAutoApply(); const payload = readPagedMenu(); applyColumnFilter(key, payload.checked, payload.allValues, payload.mode, payload.range, { close: false }); };
  inputs.forEach(input => input.addEventListener('change', () => { if (input.checked) context.draft.add(input.value); else context.draft.delete(input.value); scheduleColumnFilterAutoApply(key, readPagedMenu); }));
  els.columnFilterMenu.querySelector('[data-filter-select-all]').addEventListener('change', event => { inputs.forEach(input => { input.checked = event.target.checked; if (input.checked) context.draft.add(input.value); else context.draft.delete(input.value); }); scheduleColumnFilterAutoApply(key, readPagedMenu); });
  els.columnFilterMenu.querySelector('[data-choice-prev]')?.addEventListener('click', () => { context.page = Math.max(1, (Number(meta.page) || 1) - 1); rerender(); });
  els.columnFilterMenu.querySelector('[data-choice-next]')?.addEventListener('click', () => { context.page = (Number(meta.page) || 1) + 1; rerender(); });
  els.columnFilterMenu.querySelector('[data-choice-retry]')?.addEventListener('click', () => { cancelTrackerChoiceRead('column'); rerender(); });
  els.columnFilterMenu.querySelector('[data-filter-clear]').addEventListener('click', () => clearColumnFilter(key));
  els.columnFilterMenu.querySelector('[data-filter-cancel]').addEventListener('click', () => closeColumnFilterMenu({ restoreFocus: true }));
  // TICKED IN PLACE, NOT RE-RENDERED. Rebuilding the popover's innerHTML from
  // inside a click handler DETACHES the button that was clicked, and the
  // outside-click guard then reads a target that is no longer inside the menu and
  // closes it - so the family button appeared to dismiss the dropdown instead of
  // ticking six rows. The staged set and the boxes are updated directly.
  wireColumnFilterModeControls(els.columnFilterMenu, key, context, () => ({
    options: inputs.map((input) => input.value),
    apply: (next) => {
      context.draft = next;
      inputs.forEach((input) => { input.checked = next.has(input.value); });
      const note = els.columnFilterMenu.querySelector('.muted-note');
      if (note) note.textContent = next.size
        ? next.size + (context.mode === FILTER_MODE_EXCLUDE ? ' excluded across all choice pages' : ' selected across all choice pages')
        : 'All values. Choose values to narrow the results.';
      scheduleColumnFilterAutoApply(key, readPagedMenu);
    },
    // The direction switch is a decision, not a keystroke, so it schedules too.
    changed: () => scheduleColumnFilterAutoApply(key, readPagedMenu)
  }));
  wireColumnFilterRangeControls(els.columnFilterMenu, key, context);
  // ONE APPLY COMMITS BOTH HALVES. A column may carry a checklist AND a
  // comparison, and the server composes them with AND, so committing one
  // without the other would leave the reader looking at a grid that answers
  // half of what they set up.
  // APPLY IS THE IMMEDIATE PATH, kept for exactly that: it commits now and
  // closes, without waiting out the debounce.
  els.columnFilterMenu.querySelector('[data-filter-apply]').addEventListener('click', () => { cancelColumnFilterAutoApply(); applyColumnFilter(key, Array.from(context.draft), [], context.mode, columnFilterRangeFromMenu(els.columnFilterMenu, key).range); });
  els.columnFilterMenu.onkeydown = event => { if (event.key === 'Escape') { event.preventDefault(); closeColumnFilterMenu({ restoreFocus: true }); return; } if (event.key !== 'Tab') return; const list = Array.from(els.columnFilterMenu.querySelectorAll('input,select,summary,button:not(:disabled)')); if (event.shiftKey && event.target === list[0]) { event.preventDefault(); list.at(-1)?.focus(); } else if (!event.shiftKey && event.target === list.at(-1)) { event.preventDefault(); list[0]?.focus(); } };
  if (rangeFocus) { const rangeInput = els.columnFilterMenu.querySelector(rangeFocus); rangeInput?.focus(); if (rangeSelection) try { rangeInput.setSelectionRange(rangeSelection.start, rangeSelection.end, rangeSelection.direction); } catch (_) {} }
  else if (focus || !document.activeElement || document.activeElement === button) { search.focus(); if (cursor != null) try { search.setSelectionRange(cursor, cursor); } catch (_) {} }
  context.repainting = false;
}
function requestPagedSlicerChoices(key, rawSearch = "", debounce = false) {
  const panel = document.querySelector('[data-slicer-panel="' + key + '"]');
  if (!panel?.open) return;
  const facet = key === "renderingProvider" ? "provider" : key;
  const query = trackerFacetQueryRequest({ ...buildTrackerQueryRequest(state.rowMode || "service"), facets: [facet] });
  const scope = trackerApiFacetCacheKey(query), previous = trackerSlicerChoicePreferences.get(key);
  const pref = previous && previous.scope === scope ? previous : { scope, search: "", page: 1 };
  if (pref.search !== String(rawSearch)) { pref.search = String(rawSearch); pref.page = 1; }
  trackerSlicerChoicePreferences.set(key, pref);
  applyTrackerChoiceRequestOptions(query, facet, { search: pref.search, page: pref.page, pageSize: 50 });
  const repaint = record => {
    if (!panel.open || trackerApiCurrentFacetCacheKey(query) !== record.key) return;
    const definition = slicerDefinitions.find(row => row.key === key), host = definition && els[definition.element]; if (!host) return;
    noteTrackerFacetShape(facet, record);
    const meta = record.response?.facetPages?.[facet] || {}, selected = new Set(slicerSelectionsForRender(key));
    // Narrowed before it is drawn, so "Select all" stages what the card SHOWS.
    const rows = trackerLocalChoiceMatches(record.response?.facets?.[facet] || [], facet, pref.search,
      value => slicerDisplayValue(key, canonicalSlicerValue(key, value)));
    host.innerHTML = rows.map(row => { const value = canonicalSlicerValue(key, row.value); return '<label class="slicer-option' + (selected.has(value) ? ' active' : '') + '"><input type="checkbox" data-slicer-key="' + escapeHtml(key) + '" data-slicer-value="' + escapeHtml(value) + '"' + (selected.has(value) ? ' checked' : '') + '><span>' + escapeHtml(slicerDisplayValue(key, value)) + '</span><strong>' + nonNegativeNumber(row.count, 0) + '</strong></label>'; }).join('');
    host.querySelectorAll('[data-slicer-key]').forEach(input => input.addEventListener('change', () => setSlicerDraftSelection(key, input.dataset.slicerValue, input.checked)));
    let pager = panel.querySelector('[data-slicer-choice-pager]'); if (!pager) { pager = document.createElement('div'); pager.dataset.slicerChoicePager = key; host.insertAdjacentElement('afterend', pager); }
    pager.innerHTML = trackerChoicePagerHtml(meta, record.status, rows.length);
    pager.querySelector('[data-choice-prev]')?.addEventListener('click', () => { pref.page = Math.max(1, (Number(meta.page) || 1) - 1); requestPagedSlicerChoices(key, pref.search); });
    pager.querySelector('[data-choice-next]')?.addEventListener('click', () => { pref.page = (Number(meta.page) || 1) + 1; requestPagedSlicerChoices(key, pref.search); });
    pager.querySelector('[data-choice-retry]')?.addEventListener('click', () => { cancelTrackerChoiceRead('slicer:' + key); requestPagedSlicerChoices(key, pref.search); });
    ensureSlicerPanelActions(key); refreshSlicerModeControls(key); updateSlicerPanelSummary(key, Number(meta.totalOptions) || rows.length, record.status === 'pending' ? 'Loading' : record.status === 'error' ? 'Unavailable' : '', Array.from(selected));
  };
  const record = trackerChoiceRead('slicer:' + key, query, repaint, debounce); repaint(record);
}

let openColumnFilterContext = null;

function openColumnFilterMenu(key, button) {
  if (trackerApiEnabled()) return openPagedColumnFilterMenu(key, button);
  openColumnFilterContext = { key, button };
  const columns = currentColumns();
  const column = columns.find((item) => item.key === key) || { key, label: key };
  const options = columnFilterOptions(key);
  const facetKeyForColumn = trackerApiEnabled()
    ? trackerApiFacetCacheKey(trackerFacetQueryRequest(buildColumnFilterFacetQuery(key)))
    : "";
  // Only call an empty list "No values" once the facet has actually resolved
  // (status ok); a pending/idle/error facet must not masquerade as confirmed-empty.
  const facetStatusForColumn = trackerApiEnabled()
    ? (trackerApiFacetState.key === facetKeyForColumn ? trackerApiFacetState.status : "idle")
    : "ok";
  const facetFetchPending = facetStatusForColumn === "pending";
  const facetFetchError = facetStatusForColumn === "error";
  const facetUnresolved = facetStatusForColumn !== "ok" && facetStatusForColumn !== "error";
  const selected = state.columnFilters?.[key] || [];
  const columnMode = filterModeFor(columnFilterModeState(), key);
  const columnRange = columnFilterRangeFor(key);
  const noteColumn = isNoteFilterColumn(key);
  const noteOptionsTruncated = noteColumn && options.truncated === true;
  // A TRUNCATED NOTE LIST STARTS UNTICKED. Pre-ticking every row encodes
  // "all values selected means no filter", which is true for a bounded column
  // and false the moment the list is a subset: pressing Apply without touching
  // anything would send the 500 shown notes as a filter and hide every line
  // whose note was not among them.
  // AN EXCLUSION NEVER STARTS ALL-TICKED. "Everything ticked means no filter" is
  // the Excel reading of an INCLUSION; under "exclude these" it would mean hide
  // the entire book, so the list starts from what is actually excluded.
  const activeSet = new Set(columnMode === FILTER_MODE_EXCLUDE || selected.length || noteOptionsTruncated
    ? selected
    : options.map((option) => option.value));
  els.columnFilterMenu.innerHTML = `
    <div class="column-filter-title">Filter ${escapeHtml(column.label)}</div>
    <input class="column-filter-search" type="search" placeholder="Search ${escapeHtml(column.label)}" aria-label="Search ${escapeHtml(column.label)} values" data-filter-search>
    ${columnFilterModeControlsHtml(key, columnMode)}
    ${columnFilterRangeSectionHtml(key, columnRange, columnFilterRangeFamily(key) === "date")}
    <label class="column-filter-select-all">
      <input type="checkbox" data-filter-select-all ${activeSet.size === options.length ? "checked" : ""}>
      Select all
    </label>
    <div class="column-filter-options">
      ${options.map((option) => `
        <label class="column-filter-choice${noteColumn ? " column-filter-choice--multiline" : ""}"${noteColumn ? ` title="${escapeHtml(noteFilterOptionTitle(option.value))}"` : ""}>
          <input type="checkbox" value="${escapeHtml(option.value)}" data-filter-choice ${activeSet.has(option.value) ? "checked" : ""}>
          <span>${escapeHtml(columnFilterOptionLabel(key, option.value))}</span>
          <strong>${option.count}</strong>
        </label>
      `).join("") || (facetFetchError ? '<div class="column-filter-empty">Couldn\'t load. Reopen to retry.</div>' : facetUnresolved ? '<div class="column-filter-empty">Loading values…</div>' : '<div class="column-filter-empty">No values</div>')}
    </div>
    ${noteOptionsTruncated ? `<p class="column-filter-truncated" data-filter-truncated>Showing the ${options.length} most common of ${options.totalDistinct} notes. Search to reach the rest.</p>` : ""}
    <p class="column-filter-applying" data-filter-applying role="status" aria-live="polite" hidden>Applying…</p>
    <div class="column-filter-actions">
      <button class="small-button" type="button" data-filter-clear>Clear</button>
      <button class="small-button" type="button" data-filter-cancel>Cancel</button>
      <button class="primary-button" type="button" data-filter-apply>Apply</button>
    </div>
  `;

  const rect = button.getBoundingClientRect();
  const menuWidth = columnFilterMenuWidth(key);
  const left = Math.max(window.scrollX + 8, Math.min(window.scrollX + rect.left, window.scrollX + window.innerWidth - menuWidth - 8));
  const chromeHeight = 178;
  const belowSpace = window.innerHeight - rect.bottom - 12;
  const aboveSpace = rect.top - 12;
  const usableSpace = Math.max(belowSpace, aboveSpace);
  const optionMaxHeight = Math.max(72, Math.min(230, usableSpace - chromeHeight));
  const estimatedHeight = chromeHeight + optionMaxHeight;
  const openAbove = belowSpace < estimatedHeight && aboveSpace > belowSpace;
  els.columnFilterMenu.style.left = `${left}px`;
  els.columnFilterMenu.style.top = `${window.scrollY + (openAbove ? Math.max(8, rect.top - estimatedHeight) : rect.bottom + 4)}px`;
  els.columnFilterMenu.style.setProperty("--filter-options-max-height", `${optionMaxHeight}px`);
  // The width is set on the element, not only used for placement, so the box
  // and the position it was computed from can never disagree.
  els.columnFilterMenu.style.width = `${menuWidth}px`;
  els.columnFilterMenu.toggleAttribute("data-note-values", noteColumn);
  els.columnFilterMenu.hidden = false;

  const search = els.columnFilterMenu.querySelector("[data-filter-search]");
  const selectAll = els.columnFilterMenu.querySelector("[data-filter-select-all]");
  const choices = () => Array.from(els.columnFilterMenu.querySelectorAll("[data-filter-choice]"));
  const localMenuContext = { key, mode: columnMode, range: columnRange };
  localMenuContext.commitRange = () => { cancelColumnFilterAutoApply(); const payload = readLocalMenu(); applyColumnFilter(key, payload.checked, payload.allValues, payload.mode, payload.range, { close: false }); };

  search.addEventListener("input", () => {
    const needle = search.value.trim().toLowerCase();
    choices().forEach((choice) => {
      const row = choice.closest(".column-filter-choice");
      row.hidden = key === "action" ? !followUpActionChoiceMatches("action", slicerDisplayValue("action", choice.value), needle) : needle && !choice.value.toLowerCase().includes(needle);
    });
  });

  // Same commit-by-itself rule as the paged menu above, reading the boxes at
  // fire time.
  const readLocalMenu = () => ({
    checked: choices().filter((choice) => choice.checked).map((choice) => choice.value),
    allValues: noteOptionsTruncated ? [] : options.map((option) => option.value),
    mode: localMenuContext.mode,
    range: columnFilterRangeFromMenu(els.columnFilterMenu, key).range
  });

  selectAll.addEventListener("change", () => {
    choices().forEach((choice) => {
      if (!choice.closest(".column-filter-choice").hidden) choice.checked = selectAll.checked;
    });
    scheduleColumnFilterAutoApply(key, readLocalMenu);
  });

  choices().forEach((choice) => {
    choice.addEventListener("change", () => {
      const all = choices();
      selectAll.checked = all.length > 0 && all.every((item) => item.checked);
      scheduleColumnFilterAutoApply(key, readLocalMenu);
    });
  });

  els.columnFilterMenu.querySelector("[data-filter-clear]").addEventListener("click", () => {
    clearColumnFilter(key);
  });
  els.columnFilterMenu.querySelector("[data-filter-cancel]").addEventListener("click", () => { cancelColumnFilterAutoApply(); closeColumnFilterMenu(); });
  wireColumnFilterRangeControls(els.columnFilterMenu, key, localMenuContext);
  wireColumnFilterModeControls(els.columnFilterMenu, key, localMenuContext, () => ({
    options: choices().map((choice) => choice.value),
    apply: (next) => { choices().forEach((choice) => { choice.checked = next.has(choice.value); }); scheduleColumnFilterAutoApply(key, readLocalMenu); },
    changed: () => scheduleColumnFilterAutoApply(key, readLocalMenu)
  }));
  els.columnFilterMenu.querySelector("[data-filter-apply]").addEventListener("click", () => {
    cancelColumnFilterAutoApply();
    const checked = choices().filter((choice) => choice.checked).map((choice) => choice.value);
    // On a TRUNCATED note list "all shown are ticked" is not "no filter": the
    // unshown values would come back. Pass an empty full-set so only an EMPTY
    // selection clears, which is the branch that genuinely means "no filter".
    applyColumnFilter(key, checked, noteOptionsTruncated ? [] : options.map((option) => option.value), localMenuContext.mode, columnFilterRangeFromMenu(els.columnFilterMenu, key).range);
  });

  const focusables = () => Array.from(els.columnFilterMenu.querySelectorAll("input:not([type='hidden']), button, select, textarea")).filter((node) => {
    const row = node.closest ? node.closest(".column-filter-choice") : null;
    return !(row && row.hidden);
  });
  // Keep this popup usable without a mouse. It covers the first rows of the
  // grid, so letting Tab escape into those covered controls is disorienting;
  // Escape should also close the transient UI and return to its trigger.
  els.columnFilterMenu.addEventListener?.("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeColumnFilterMenu({ restoreFocus: true });
      return;
    }
    if (event.key !== "Tab") return;
    const list = focusables();
    if (!list.length) return;
    const firstNode = list[0];
    const lastNode = list[list.length - 1];
    if (event.shiftKey && event.target === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && event.target === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  });
  search.focus();
}

function closeColumnFilterMenu({ restoreFocus = false } = {}) {
  cancelTrackerChoiceRead("column");
  // A pending auto-apply belongs to the menu that staged it; a closed menu must
  // not commit a selection the reader walked away from.
  cancelColumnFilterAutoApply();
  // The live caret, not the node this popover was opened from - the header may
  // have been rebuilt since. See columnFilterTriggerNode.
  const trigger = columnFilterTriggerNode(openColumnFilterContext?.key, openColumnFilterContext?.button);
  openColumnFilterContext = null;
  els.columnFilterMenu.hidden = true;
  els.columnFilterMenu.innerHTML = "";
  if (restoreFocus && trigger?.focus) trigger.focus();
}

// A ROWS/GROUPS SWITCH IS NOT A FILTER CLEAR (2026-09-22).
//
// Both handlers used to assign structuredClone(emptyColumnFilters) on the way
// through, so flipping to Assignment Groups and back silently discarded EVERY
// column filter - no chip, no badge, no sentence, and the pager jumped from the
// rows the reader had narrowed to back to the whole book. The reason a reset was
// there at all is real but much narrower: the two modes draw different column
// lists, so a filter on a column the target mode does not draw has nowhere to
// live and no caret to remove it from.
//
// So drop exactly those, and keep the rest. Claim ID, Follow-up Action,
// Follow-up Date, Claim Status, Provider, Client, Facility, both payers, Match
// and Aging are in BOTH lists and now survive the switch; Procedure, Srvc Date,
// M1/M2 and the other per-line columns do not exist in Groups mode and are the
// only ones that go. permittedColumnsForMode() is the same function the header
// row is built from, so "the mode can draw it" and "the mode can filter it"
// cannot drift apart.
function columnFiltersForRowMode(mode, filters = state.columnFilters) {
  const permitted = new Set(permittedColumnsForMode(mode).map((column) => column.key));
  const kept = structuredClone(emptyColumnFilters);
  Object.entries(ensureColumnFilterState(filters)).forEach(([key, values]) => {
    if (permitted.has(key) && Array.isArray(values) && values.length) kept[key] = values.slice();
  });
  return kept;
}

// `range` is THREE-STATE and the third state matters: undefined means "this
// caller is not speaking about the comparison, leave it alone" (the follow-up
// helpers and the blank-filter proofs call this with four arguments), null
// means "no comparison", and an object is one to store. Defaulting undefined
// to null would make every existing caller silently wipe a range the reader
// had set in the same popover.
// ---------------------------------------------------------------------------
// THE COLUMN MENU COMMITS BY ITSELF TOO (owner, 2026-09-22: "fewer buttons to
// click"), on the same one-timer-per-burst rule the slicer card uses.
// ---------------------------------------------------------------------------
// The popover is NOT re-rendered by an auto-apply and it is NOT closed: this
// menu is rebuilt from innerHTML on every paged read, and rebuilding it from
// inside a change handler detaches the control that was just used - the exact
// defect recorded on the family button, where the dropdown appeared to dismiss
// itself instead of ticking six rows. So the commit writes state and repaints
// the GRID, and the menu stays exactly as the reader left it.
//
// THE RANGE BOXES ARE NOT WIRED TO THIS. "greater than 500" is half-typed as
// "5" and then "50"; each of those is a legitimate-looking filter that would
// fire a query and repaint the grid under the reader. They keep Apply and
// commit on blur or Enter - see wireColumnFilterRangeControls.
let columnFilterAutoApplyTimer = null;
let columnFilterAutoApplyKey = "";

function cancelColumnFilterAutoApply() {
  if (columnFilterAutoApplyTimer && typeof window !== "undefined") window.clearTimeout(columnFilterAutoApplyTimer);
  columnFilterAutoApplyTimer = null;
  columnFilterAutoApplyKey = "";
  els.columnFilterMenu?.classList?.remove("is-applying");
}

function scheduleColumnFilterAutoApply(key, read) {
  if (typeof window === "undefined" || typeof read !== "function") return;
  cancelColumnFilterAutoApply();
  columnFilterAutoApplyKey = key;
  els.columnFilterMenu?.classList?.add("is-applying");
  const note = els.columnFilterMenu?.querySelector?.("[data-filter-applying]");
  if (note) note.hidden = false;
  columnFilterAutoApplyTimer = window.setTimeout(() => {
    columnFilterAutoApplyTimer = null;
    columnFilterAutoApplyKey = "";
    els.columnFilterMenu?.classList?.remove("is-applying");
    // STALE-SCOPE FENCED: the menu may have been closed, cancelled or reopened
    // on another column while this waited.
    if (!openColumnFilterContext || openColumnFilterContext.key !== key || els.columnFilterMenu?.hidden) return;
    const payload = read();
    if (!payload) return;
    applyColumnFilter(key, payload.checked, payload.allValues, payload.mode, payload.range, { close: false });
    const settled = els.columnFilterMenu?.querySelector?.("[data-filter-applying]");
    if (settled) settled.hidden = true;
  }, FILTER_AUTO_APPLY_DELAY_MS);
}

function applyColumnFilter(key, checked, allValues, mode = FILTER_MODE_INCLUDE, range = undefined, { close = true } = {}) {
  state.columnFilters = ensureColumnFilterState(state.columnFilters);
  // "Everything ticked is no filter" belongs to the INCLUDE reading only: under
  // an exclusion the same ticks mean hide the whole column, which is a real (and
  // deliberately empty) answer, not an absence of one.
  const clears = !checked.length
    || (mode !== FILTER_MODE_EXCLUDE && allValues.length > 0 && checked.length === allValues.length
      && !(key === "action" && checked.length === 1 && checked[0] === "Blank"));
  if (clears) delete state.columnFilters[key];
  else state.columnFilters[key] = checked;
  const nextModes = { ...(state.columnFilterModes || {}) };
  if (!clears && mode === FILTER_MODE_EXCLUDE) nextModes[key] = FILTER_MODE_EXCLUDE;
  else delete nextModes[key];
  state.columnFilterModes = ensureFilterModeState(nextModes, state.columnFilters);
  if (range !== undefined) {
    const nextRanges = { ...(state.columnFilterRanges || {}) };
    if (range) nextRanges[key] = range; else delete nextRanges[key];
    state.columnFilterRanges = ensureColumnFilterRangeState(nextRanges);
  }
  resetTrackerPage();
  saveState();
  if (close) {
    requestColumnFilterCaretFocus(key);
    closeColumnFilterMenu({ restoreFocus: true });
  }
  // Commit freshness before starting this filter read. The bubbling click
  // check would otherwise wait for it, discard it, and fetch it again.
  if (close) void checkTrackerPublication();
  renderClaimsTable();
}

// Shared by both header menus (the server-paginated one and the in-memory one),
// so the caret dropdown and the slicer card can never offer different words for
// the same decision. `context` carries the menu's own staged mode; `resolve`
// hands back the option values currently on screen plus the way to write a new
// selection into whichever menu is calling.
function columnFilterModeControlsHtml(key, mode) {
  if (!filterKeySupportsExclusion(key)) return "";
  const name = `column-filter-mode-${key}`;
  return '<div class="column-filter-mode" role="radiogroup" aria-label="' + escapeHtml(columnLabel(key)) + ' filter direction">'
    + '<label class="column-filter-mode-option"><input type="radio" name="' + escapeHtml(name) + '" value="' + FILTER_MODE_INCLUDE + '" data-filter-mode' + (mode === FILTER_MODE_EXCLUDE ? '' : ' checked') + '><span>Include only these</span></label>'
    + '<label class="column-filter-mode-option"><input type="radio" name="' + escapeHtml(name) + '" value="' + FILTER_MODE_EXCLUDE + '" data-filter-mode' + (mode === FILTER_MODE_EXCLUDE ? ' checked' : '') + '><span>Exclude these</span></label>'
    + '<button class="small-button" type="button" data-filter-family aria-pressed="false">' + escapeHtml(CLIENT_ACTION_PENDING_FAMILY) + ' (all)</button>'
    + '</div>';
}

function wireColumnFilterModeControls(menu, key, context, resolve) {
  if (!filterKeySupportsExclusion(key) || !menu) return;
  menu.querySelectorAll("[data-filter-mode]").forEach((input) => {
    input.addEventListener("change", () => {
      if (!input.checked) return;
      context.mode = input.value === FILTER_MODE_EXCLUDE ? FILTER_MODE_EXCLUDE : FILTER_MODE_INCLUDE;
      refreshColumnFilterFamilyButton(menu, key, context, resolve);
      // Flipping the direction is a whole different book, so it commits like a
      // tick does rather than waiting for a button (owner, 2026-09-22).
      resolve().changed?.();
    });
  });
  const family = menu.querySelector("[data-filter-family]");
  if (family) {
    family.addEventListener("click", () => {
      const { options, apply } = resolve();
      const values = followUpFamilyValues(options);
      if (!values.length) return;
      const current = new Set(columnFilterStagedValues(menu, context));
      const all = values.every((value) => current.has(value));
      values.forEach((value) => { if (all) current.delete(value); else current.add(value); });
      apply(current);
      refreshColumnFilterFamilyButton(menu, key, context, resolve);
    });
  }
  refreshColumnFilterFamilyButton(menu, key, context, resolve);
}

function columnFilterStagedValues(menu, context) {
  if (context && context.draft instanceof Set) return Array.from(context.draft);
  return Array.from(menu.querySelectorAll("[data-filter-choice]")).filter((input) => input.checked).map((input) => input.value);
}

function refreshColumnFilterFamilyButton(menu, key, context, resolve) {
  const family = menu.querySelector("[data-filter-family]");
  if (!family) return;
  const { options } = resolve();
  const values = followUpFamilyValues(options);
  family.hidden = values.length === 0;
  if (family.hidden) return;
  const staged = new Set(columnFilterStagedValues(menu, context));
  const all = values.every((value) => staged.has(value));
  family.setAttribute("aria-pressed", all ? "true" : "false");
  family.textContent = `${CLIENT_ACTION_PENDING_FAMILY} (all ${values.length})`;
}

// CLEAR MEANS THE WHOLE COLUMN. The Clear button sits under both controls and
// the caret glyph is lit by either of them, so leaving a comparison behind
// would leave a filtered column that claims to have been cleared.
function clearColumnFilter(key) {
  state.columnFilters = ensureColumnFilterState(state.columnFilters);
  delete state.columnFilters[key];
  const remainingRanges = { ...(state.columnFilterRanges || {}) };
  delete remainingRanges[key];
  state.columnFilterRanges = ensureColumnFilterRangeState(remainingRanges);
  state.columnFilterModes = ensureFilterModeState(state.columnFilterModes, state.columnFilters);
  resetTrackerPage();
  saveState();
  // restoreFocus, like Cancel and Escape already do, and a standing request so
  // the renderClaimsTable() below - which rebuilds the caret this just focused -
  // cannot drop the keyboard to <body>. From the chip rail there is no open
  // popover; the request is re-seated on the caret the column still has.
  if (openColumnFilterContext?.key === key) requestColumnFilterCaretFocus(key);
  closeColumnFilterMenu({ restoreFocus: true });
  void checkTrackerPublication();
  renderClaimsTable();
}

// Remove ONLY the comparison, leaving any checklist on the same column. The
// caret's own Clear button still takes both - see clearColumnFilter - because
// a reader who opens the menu and presses Clear means the column.
function clearColumnFilterRange(key) {
  const next = { ...(state.columnFilterRanges || {}) };
  if (!next[key]) return;
  delete next[key];
  state.columnFilterRanges = ensureColumnFilterRangeState(next);
  resetTrackerPage();
  saveState();
  void checkTrackerPublication();
  renderClaimsTable();
}

// UI-100 #33: remove a single value from a column filter (preserves other values).
function removeColumnFilterValue(key, value) {
  state.columnFilters = ensureColumnFilterState(state.columnFilters);
  const list = state.columnFilters[key];
  if (!Array.isArray(list)) return;
  state.columnFilters[key] = list.filter((v) => v !== value);
  if (!state.columnFilters[key].length) delete state.columnFilters[key];
  state.columnFilterModes = ensureFilterModeState(state.columnFilterModes, state.columnFilters);
  resetTrackerPage();
  saveState();
  void checkTrackerPublication();
  renderClaimsTable();
}

// ---------------------------------------------------------------------------
// THE FILTER PANEL: TURN A FILTER OFF WITHOUT LOSING IT (2026-10-22).
// ---------------------------------------------------------------------------
//   "add a slider here so that we can see what filters are we using and also we
//    can choose or remove filters according to our preference" - Abhishek.
//   On the chip x: "Yes but that will remove the filter."
//
// A filter that is OFF is moved out of the live request into
// state.disabledFilters, keyed by the same group id the chip rail already uses
// ("col:<key>", "range:<key>", "slicer:<key>", "quick:<key>"). The request, the
// pager and the totals keep reading only the live maps, so an OFF filter cannot
// narrow anything, and turning it back on restores the identical stored value.
// saveState() persists the whole state object, so OFF filters survive a reload
// and a trip into a claim and back. Sort is not a filter and stays chip-only.
let activeFilterPanelOpen = false;
let activeFilterRefreshTimer = null;
let activeFilterPanelListenersInstalled = false;
const ACTIVE_FILTER_GROUP_KINDS = new Set(["col", "range", "slicer", "quick"]);
const ACTIVE_FILTER_REFRESH_DELAY_MS = 180;

function splitActiveFilterGroupId(id) {
  const text = String(id || "");
  const at = text.indexOf(":");
  return at > 0 ? [text.slice(0, at), text.slice(at + 1)] : ["", ""];
}

function ensureDisabledFilterState(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const next = {};
  Object.entries(value).slice(0, 60).forEach(([id, entry]) => {
    const [kind, key] = splitActiveFilterGroupId(id);
    if (!ACTIVE_FILTER_GROUP_KINDS.has(kind) || !key || !entry || typeof entry !== "object") return;
    const stale = entry.stale === true;
    if (kind === "col" || kind === "slicer") {
      const values = Array.isArray(entry.values) ? entry.values.map(String) : [];
      if (values.length) next[id] = { values, exclude: entry.exclude === true, stale };
    } else if (kind === "range") {
      if (entry.range && typeof entry.range === "object") next[id] = { range: entry.range, stale };
    } else if (typeof entry.value === "string" ? entry.value.trim() : Array.isArray(entry.value) && entry.value.length) {
      next[id] = { value: Array.isArray(entry.value) ? entry.value.map(String) : entry.value, stale };
    }
  });
  return next;
}

function activeFilterColumnLabel(key) {
  const column = [].concat(typeof claimColumns !== "undefined" ? claimColumns : [], typeof serviceColumns !== "undefined" ? serviceColumns : [])
    .find((entry) => entry.key === key);
  return column ? column.label : "";
}

function activeFilterSlicerHeading(key) {
  const heading = document.querySelector(`[data-slicer-panel="${key}"] > summary > span`);
  const text = String(heading?.textContent || "").trim();
  return text || slicerLabel(key) || key;
}

// The live value of one filter group, or null when nothing is applied there.
function snapshotLiveFilterGroup(id) {
  const [kind, key] = splitActiveFilterGroupId(id);
  if (kind === "col") {
    const values = ensureColumnFilterState(state.columnFilters)[key];
    return values ? { values: values.slice(), exclude: filterModeExcludes(columnFilterModeState(), key) } : null;
  }
  if (kind === "slicer") {
    const values = ensureSlicerState(state.slicers)[key];
    return Array.isArray(values) && values.length ? { values: values.slice(), exclude: filterModeExcludes(slicerModeState(), key) } : null;
  }
  if (kind === "range") {
    const range = columnFilterRangeFor(key);
    return range ? { range: structuredClone(range) } : null;
  }
  if (kind === "quick") {
    const value = trackerFilterTermsExcludingScope(collectTrackerFilters())[key];
    // An unparseable date is not a value anyone can get back; it stays remove-only.
    if (value === "invalid" || !describeTrackerFilter(key, value) || !TRACKER_QUICK_FILTER_RESTORES[key]) return null;
    return { value: Array.isArray(value) ? value.slice() : value };
  }
  return null;
}

// Removes ONLY this group. A column's checklist and its comparison are separate
// groups, exactly as they are separate chips.
function clearLiveFilterGroup(id) {
  const [kind, key] = splitActiveFilterGroupId(id);
  if (kind === "col") {
    const next = { ...ensureColumnFilterState(state.columnFilters) };
    delete next[key];
    state.columnFilters = next;
    state.columnFilterModes = ensureFilterModeState(state.columnFilterModes, state.columnFilters);
  } else if (kind === "slicer") {
    state.slicers = ensureSlicerState(state.slicers);
    if (state.slicers[key]) state.slicers[key] = [];
    state.slicerModes = ensureFilterModeState(state.slicerModes, state.slicers);
  } else if (kind === "range") {
    const next = { ...columnFilterRangeState() };
    delete next[key];
    state.columnFilterRanges = ensureColumnFilterRangeState(next);
  } else if (kind === "quick") {
    TRACKER_QUICK_FILTER_RESETS[key]?.clear();
  }
}

// True only when the live request now carries the identical filter. False means
// the stored value can no longer be applied (a retired column, slicer, option or
// queue), and the caller flags it instead of applying something else.
function restoreLiveFilterGroup(id, entry) {
  const [kind, key] = splitActiveFilterGroupId(id);
  if (!entry || entry.stale) return false;
  if (kind === "col") {
    if (!activeFilterColumnLabel(key)) return false;
    state.columnFilters = ensureColumnFilterState({ ...ensureColumnFilterState(state.columnFilters), [key]: entry.values });
    const modes = { ...columnFilterModeState() };
    if (entry.exclude) modes[key] = FILTER_MODE_EXCLUDE;
    else delete modes[key];
    state.columnFilterModes = ensureFilterModeState(modes, state.columnFilters);
    return Boolean(state.columnFilters[key]);
  }
  if (kind === "slicer") {
    const slicers = ensureSlicerState(state.slicers);
    if (!slicers[key]) return false;
    slicers[key] = entry.values.slice();
    state.slicers = ensureSlicerState(slicers);
    const modes = { ...slicerModeState() };
    if (entry.exclude) modes[key] = FILTER_MODE_EXCLUDE;
    else delete modes[key];
    state.slicerModes = ensureFilterModeState(modes, state.slicers);
    return state.slicers[key].length > 0;
  }
  if (kind === "range") {
    const next = ensureColumnFilterRangeState({ ...columnFilterRangeState(), [key]: entry.range });
    if (!next[key]) return false;
    state.columnFilterRanges = next;
    return true;
  }
  if (kind === "quick") {
    const restore = TRACKER_QUICK_FILTER_RESTORES[key];
    return Boolean(restore && restore(entry.value) === true);
  }
  return false;
}

function activeFilterGroupText(id, entry) {
  const [kind, key] = splitActiveFilterGroupId(id);
  try {
    if (kind === "col" || kind === "slicer") {
      const label = kind === "col" ? (activeFilterColumnLabel(key) || key) : activeFilterSlicerHeading(key);
      const parts = filterSelectionSummaryParts(key, entry.values);
      return {
        text: `${label}: ${filterSelectionSummaryText(key, entry.values, entry.exclude)}`,
        title: `${label}: ${entry.exclude ? "not " : ""}${parts.join(", ")}`
      };
    }
    if (kind === "range") {
      const text = columnFilterRangeChipText(key, entry.range);
      if (text) return { text, title: text };
    }
    if (kind === "quick") {
      const text = describeTrackerFilter(key, entry.value);
      if (text) return { text, title: text };
    }
  } catch (_) { /* a stored value that no longer formats is still listed so it can be removed */ }
  const label = (kind === "col" ? activeFilterColumnLabel(key) : "") || key;
  return { text: label, title: label };
}

function disabledFilterEntries() {
  return Object.entries(ensureDisabledFilterState(state.disabledFilters)).map(([id, entry]) => ({
    id,
    stale: entry.stale,
    ...activeFilterGroupText(id, entry)
  }));
}

// The panel and the rail update at once; the grid request waits until the
// clicking stops, so five quick toggles send one request, not five racing ones.
function commitActiveFilterPanelChange() {
  resetTrackerPage();
  saveState();
  renderActiveFilterChips();
  if (activeFilterRefreshTimer) clearTimeout(activeFilterRefreshTimer);
  activeFilterRefreshTimer = setTimeout(() => {
    activeFilterRefreshTimer = null;
    // renderSlicers() reads the page too, so it waits with the grid.
    renderSlicers();
    void checkTrackerPublication();
    renderClaimsTable();
  }, ACTIVE_FILTER_REFRESH_DELAY_MS);
}

function turnOffActiveFilterGroup(id, { commit = true } = {}) {
  const snapshot = snapshotLiveFilterGroup(id);
  if (!snapshot) return false;
  clearLiveFilterGroup(id);
  state.disabledFilters = { ...ensureDisabledFilterState(state.disabledFilters), [id]: snapshot };
  if (commit) commitActiveFilterPanelChange();
  return true;
}

function turnOnDisabledFilterGroup(id, { commit = true } = {}) {
  const disabled = { ...ensureDisabledFilterState(state.disabledFilters) };
  const entry = disabled[id];
  if (!entry) return false;
  if (!restoreLiveFilterGroup(id, entry)) {
    disabled[id] = { ...entry, stale: true };
    state.disabledFilters = disabled;
    if (commit) {
      saveState();
      renderActiveFilterChips();
      showToast("That filter's value is no longer available. Remove it from the filter panel.");
    }
    return false;
  }
  delete disabled[id];
  state.disabledFilters = disabled;
  if (commit) commitActiveFilterPanelChange();
  return true;
}

function removeActiveFilterGroup(id, isOn) {
  if (isOn) {
    clearLiveFilterGroup(id);
    commitActiveFilterPanelChange();
    return;
  }
  // An OFF filter narrows nothing, so deleting it needs no new request.
  const disabled = { ...ensureDisabledFilterState(state.disabledFilters) };
  delete disabled[id];
  state.disabledFilters = disabled;
  saveState();
  renderActiveFilterChips();
}

function turnAllActiveFilterGroups(on, liveIds) {
  if (on) {
    const ids = Object.keys(ensureDisabledFilterState(state.disabledFilters));
    const missed = ids.filter((id) => !turnOnDisabledFilterGroup(id, { commit: false })).length;
    commitActiveFilterPanelChange();
    if (missed) showToast(`${formatNounCount(missed, "filter")} could not be turned back on because the value is no longer available.`);
    return;
  }
  liveIds.forEach((id) => turnOffActiveFilterGroup(id, { commit: false }));
  commitActiveFilterPanelChange();
}

function setActiveFilterPanelOpen(open, { focus = false } = {}) {
  activeFilterPanelOpen = Boolean(open);
  renderActiveFilterChips();
  if (focus && activeFilterPanelOpen) document.querySelector("#activeFilterPanel button:not([disabled])")?.focus();
  if (focus && !activeFilterPanelOpen) document.querySelector("#activeFilterChips [data-filter-panel-toggle]")?.focus();
}

function installActiveFilterPanelListeners() {
  if (activeFilterPanelListenersInstalled || typeof document === "undefined") return;
  activeFilterPanelListenersInstalled = true;
  // Capture phase: Escape on the page otherwise means Reset View, which would
  // throw away every filter the reader opened this panel to keep.
  document.addEventListener("keydown", (event) => {
    if (!activeFilterPanelOpen || event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    setActiveFilterPanelOpen(false, { focus: true });
  }, true);
  document.addEventListener("click", (event) => {
    if (!activeFilterPanelOpen) return;
    const target = event.target;
    if (!target?.isConnected) return; // a control the panel just re-rendered away
    if (target.closest?.("#activeFilterPanel, [data-filter-panel-toggle]")) return;
    setActiveFilterPanelOpen(false);
  });
}

function renderActiveFilterPanel(chips, disabledEntries) {
  const panel = document.getElementById("activeFilterPanel");
  if (!panel) return;
  if (!activeFilterPanelOpen) {
    panel.hidden = true;
    panel.innerHTML = "";
    return;
  }
  installActiveFilterPanelListeners();
  const focused = panel.contains(document.activeElement) ? document.activeElement : null;
  const focusSelector = focused?.dataset?.filterPanelSwitch
    ? `[data-filter-panel-switch="${escapeCss(focused.dataset.filterPanelSwitch)}"]`
    : focused?.dataset?.filterPanelAction ? `[data-filter-panel-action="${escapeCss(focused.dataset.filterPanelAction)}"]` : "";
  const liveIds = [...new Set(chips.filter((chip) => chip.kind !== "sort" && chip.key).map((chip) => chip.key))];
  const rows = [
    ...liveIds.map((id) => {
      const snapshot = snapshotLiveFilterGroup(id);
      const fallback = chips.find((chip) => chip.key === id)?.text || id;
      return { id, on: true, canToggle: Boolean(snapshot), stale: false, ...(snapshot ? activeFilterGroupText(id, snapshot) : { text: fallback, title: fallback }) };
    }),
    ...disabledEntries.map((entry) => ({ ...entry, on: false, canToggle: !entry.stale }))
  ];
  const onCount = liveIds.length;
  const offCount = disabledEntries.length;
  panel.hidden = false;
  panel.innerHTML = `
    <div class="active-filter-panel-head">
      <strong id="activeFilterPanelTitle">Filters</strong>
      <span>${onCount} on · ${offCount} off</span>
      <button type="button" class="active-filter-panel-close" data-filter-panel-action="close" aria-label="Close the filter panel">×</button>
    </div>
    ${rows.length ? `<ul class="active-filter-panel-list">
      ${rows.map((row) => `
        <li class="active-filter-panel-row ${row.on ? "is-on" : "is-off"}${row.stale ? " is-stale" : ""}">
          <button type="button" class="active-filter-switch" role="switch" aria-checked="${row.on ? "true" : "false"}" data-filter-panel-switch="${escapeHtml(row.id)}" data-filter-panel-on="${row.on ? "1" : "0"}" aria-label="${row.on ? "Turn off" : "Turn on"} ${escapeHtml(row.text)}" ${row.canToggle ? "" : "disabled"} title="${row.canToggle ? (row.on ? "On - click to turn off and keep it here" : "Off - click to turn back on") : (row.stale ? "This value is no longer available" : "This filter can only be removed")}"><span aria-hidden="true"></span></button>
          <span class="active-filter-panel-text" title="${escapeHtml(row.title)}">${escapeHtml(row.text)}</span>
          ${row.stale ? `<span class="active-filter-panel-flag">No longer available</span>` : ""}
          <button type="button" class="active-filter-panel-remove" data-filter-panel-remove="${escapeHtml(row.id)}" data-filter-panel-on="${row.on ? "1" : "0"}" aria-label="Remove ${escapeHtml(row.text)}" title="Remove this filter">×</button>
        </li>`).join("")}
    </ul>` : `<p class="active-filter-panel-empty">No filters applied. Add one from a column header or Edit.</p>`}
    <div class="active-filter-panel-foot">
      <button type="button" class="ghost-button" data-filter-panel-action="all-off" ${onCount ? "" : "disabled"}>Turn all off</button>
      <button type="button" class="ghost-button" data-filter-panel-action="all-on" ${offCount ? "" : "disabled"}>Turn all on</button>
      <button type="button" class="ghost-button active-filter-panel-clear" data-filter-panel-action="clear" ${rows.length ? "" : "disabled"}>Clear all</button>
    </div>
  `;
  panel.querySelectorAll("[data-filter-panel-switch]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.filterPanelOn === "1") turnOffActiveFilterGroup(btn.dataset.filterPanelSwitch);
      else turnOnDisabledFilterGroup(btn.dataset.filterPanelSwitch);
    });
  });
  panel.querySelectorAll("[data-filter-panel-remove]").forEach((btn) => {
    btn.addEventListener("click", () => {
      removeActiveFilterGroup(btn.dataset.filterPanelRemove, btn.dataset.filterPanelOn === "1");
      document.querySelector("#activeFilterPanel [data-filter-panel-remove], #activeFilterPanel [data-filter-panel-action='close']")?.focus();
    });
  });
  panel.querySelector("[data-filter-panel-action='close']")?.addEventListener("click", () => setActiveFilterPanelOpen(false, { focus: true }));
  panel.querySelector("[data-filter-panel-action='all-off']")?.addEventListener("click", () => turnAllActiveFilterGroups(false, liveIds));
  panel.querySelector("[data-filter-panel-action='all-on']")?.addEventListener("click", () => turnAllActiveFilterGroups(true, liveIds));
  panel.querySelector("[data-filter-panel-action='clear']")?.addEventListener("click", () => clearAllTrackerFilters());
  if (focusSelector) panel.querySelector(focusSelector)?.focus();
}

// UI-100 #33: render the active-filter chip strip above the claims table.
function renderActiveFilterChips() {
  const host = document.getElementById("activeFilterChips");
  if (!host) return;
  const chips = [];
  // Column filters
  const columnLabelByKey = Object.fromEntries(
    [].concat(typeof claimColumns !== "undefined" ? claimColumns : [],
              typeof serviceColumns !== "undefined" ? serviceColumns : [])
      .map((c) => [c.key, c.label])
  );
  // Per-column collapse: if a column has 1-3 selected values, render
  // individual chips (each with its own × to remove that value). If a
  // column has 4+ selected values, collapse to a single 'Column: N values'
  // chip whose × clears the whole column filter. Prevents the wall of
  // 100+ chips that 'Select All' used to produce.
  const chipColumnModes = columnFilterModeState();
  const chipSlicerModes = slicerModeState();
  Object.entries(state.columnFilters || {}).forEach(([key, vals]) => {
    if (!Array.isArray(vals) || !vals.length) return;
    const label = columnLabelByKey[key] || key;
    // An exclusion is ONE decision over a list, so it gets ONE chip: removing a
    // single value from "show everything except these" is not a thing a reader
    // can mean, and offering it per value is how the rail stopped describing the
    // filter it was standing over.
    if (filterModeExcludes(chipColumnModes, key)) {
      chips.push({
        kind: "col",
        key: `col:${key}`,
        text: `${label}: ${filterSelectionSummaryText(key, vals, true)}`,
        onRemoveAttr: `data-chip-remove-col-all="${escapeHtml(key)}"`
      });
      return;
    }
    if (vals.length >= 4) {
      chips.push({
        kind: "col",
        key: `col:${key}`,
        text: `${label}: ${vals.length} values`,
        onRemoveAttr: `data-chip-remove-col-all="${escapeHtml(key)}"`
      });
    } else {
      vals.forEach((v) => {
        chips.push({
          kind: "col",
          key: `col:${key}`,
          text: `${label}: ${columnFilterChipText(key, v)}`,
          onRemoveAttr: `data-chip-remove-col="${escapeHtml(key)}" data-chip-remove-val="${escapeHtml(String(v))}"`
        });
      });
    }
  });
  // A COMPARISON IS ITS OWN CHIP, AND IT READS AS ONE.
  //
  // "Charges > 500" and "Srvc Date between 2026-01-01 and 2026-03-31" are what
  // the reader asked for, in their own words. The collapse rule above does not
  // apply: a range is ONE decision with no member values to remove one at a
  // time, so it gets one chip whose x removes the comparison and leaves any
  // checklist on the same column alone - which is what "remove this filter"
  // can only mean when two of them sit on one column.
  Object.entries(columnFilterRangeState()).forEach(([key, range]) => {
    chips.push({
      kind: "col",
      key: `range:${key}`,
      text: columnFilterRangeChipText(key, range),
      onRemoveAttr: `data-chip-remove-col-range="${escapeHtml(key)}"`
    });
  });
  // Slicers — same collapse rule. Named the way the rail names them: the chip
  // used to print the internal key ("claimStatus: On Hold") beside a rail that
  // said "Claim Status: On Hold" for the same selection.
  const slicerLabel = (key) => {
    const heading = document.querySelector(`[data-slicer-panel="${key}"] > summary > span`);
    const text = String(heading?.textContent || "").trim();
    return text || key;
  };
  Object.entries(state.slicers || {}).forEach(([key, vals]) => {
    if (!Array.isArray(vals) || !vals.length) return;
    if (filterModeExcludes(chipSlicerModes, key)) {
      chips.push({
        kind: "slicer",
        key: `slicer:${key}`,
        text: `${slicerLabel(key)}: ${filterSelectionSummaryText(key, vals, true)}`,
        onRemoveAttr: `data-chip-remove-slicer-all="${escapeHtml(key)}"`
      });
      return;
    }
    if (vals.length >= 4) {
      chips.push({
        kind: "slicer",
        key: `slicer:${key}`,
        text: `${slicerLabel(key)}: ${vals.length} values`,
        onRemoveAttr: `data-chip-remove-slicer-all="${escapeHtml(key)}"`
      });
    } else {
      vals.forEach((v) => {
        chips.push({
          kind: "slicer",
          key: `slicer:${key}`,
          text: `${slicerLabel(key)}: ${key === "action" ? slicerDisplayValue(key, v) : v}`,
          onRemoveAttr: `data-chip-remove-slicer="${escapeHtml(key)}" data-chip-remove-val="${escapeHtml(String(v))}"`
        });
      });
    }
  });
  // Quick filters - the searches and dropdowns above the grid, and the queue
  // card - narrow the book exactly as a slicer does, and until 2026-09-17 not one
  // of them produced a chip: a claim-ID search on its own left this rail EMPTY
  // and hidden while the grid showed a single row. The keys are read from the
  // request the grid actually sends, so a filter cannot reach the server without
  // showing up here with an x beside it.
  // trackerFilterTermsExcludingScope() drops `tenant` only when the HEADER
  // switcher owns it; a company chosen in the filter strip's own "Company scope"
  // select is an ordinary filter and gets an ordinary chip whose x really clears it.
  const quickFilterValues = trackerFilterTermsExcludingScope(collectTrackerFilters());
  Object.keys(TRACKER_QUICK_FILTER_RESETS).forEach((key) => {
    const text = describeTrackerFilter(key, quickFilterValues[key]);
    if (!text) return;
    chips.push({ kind: "quick", key: `quick:${key}`, text, onRemoveAttr: `data-chip-remove-quick="${escapeHtml(key)}"` });
  });
  // User sort
  if (state.tableSort && state.tableSort.column) {
    const label = columnLabelByKey[state.tableSort.column] || state.tableSort.column;
    const dir = state.tableSort.dir === "desc" ? "↓" : "↑";
    chips.push({ kind: "sort", text: `Sort: ${label} ${dir}`, onRemoveAttr: `data-chip-remove-sort="1"` });
  }
  const disabledEntries = disabledFilterEntries();
  renderActiveFilterPanel(chips, disabledEntries);
  if (!chips.length && !disabledEntries.length) { host.innerHTML = ""; host.classList.add("hidden"); return; }
  host.classList.remove("hidden");
  // The count is ON filters only: an OFF filter narrows nothing.
  host.innerHTML = `
    <button class="active-filter-chips-label" type="button" data-filter-panel-toggle="1" aria-haspopup="dialog" aria-controls="activeFilterPanel" aria-expanded="${activeFilterPanelOpen ? "true" : "false"}" title="See every filter, turn one off without losing it, or remove it">Filters (${new Set(chips.filter((chip) => chip.kind !== "sort").map((chip) => chip.key)).size})</button>
    <button class="active-filter-chip chip-edit" type="button" data-chip-edit-filters="1">Edit</button>
    ${chips.map((c) => `
      <button class="active-filter-chip ${c.kind === "sort" ? "chip-sort" : c.kind === "slicer" ? "chip-slicer" : c.kind === "quick" ? "chip-quick" : "chip-col"}" type="button" ${c.onRemoveAttr} aria-label="Remove filter ${escapeHtml(c.text)}" title="${escapeHtml(c.text)}">
        <span>${escapeHtml(c.text)}</span>
        <span class="chip-x" aria-hidden="true">×</span>
      </button>
    `).join("")}
    ${disabledEntries.map((entry) => `
      <button class="active-filter-chip chip-off${entry.stale ? " chip-stale" : ""}" type="button" data-chip-turn-on="${escapeHtml(entry.id)}" aria-label="Turn on filter ${escapeHtml(entry.text)}" title="${entry.stale ? "No longer available - remove it in the filter panel" : "Off - click to turn back on"}: ${escapeHtml(entry.title)}">
        <span>${escapeHtml(entry.text)}</span>
        <span class="chip-off-tag" aria-hidden="true">off</span>
      </button>
    `).join("")}
    <button class="active-filter-chip chip-clear" type="button" data-chip-remove-all="1">Clear all</button>
  `;
  host.querySelector("[data-filter-panel-toggle]")?.addEventListener("click", () => setActiveFilterPanelOpen(!activeFilterPanelOpen, { focus: true }));
  host.querySelectorAll("[data-chip-turn-on]").forEach((btn) => {
    btn.addEventListener("click", () => turnOnDisabledFilterGroup(btn.dataset.chipTurnOn));
  });
  host.querySelector("[data-chip-edit-filters]")?.addEventListener("click", () => {
    const board = document.getElementById("slicerBoard");
    const toggle = document.getElementById("slicerToggleBtn");
    board?.removeAttribute?.("hidden");
    toggle?.setAttribute?.("aria-expanded", "true");
    if (toggle) toggle.innerHTML = 'Hide filters <span class="slicer-toggle-caret" aria-hidden="true">▴</span>';
    board?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  });
  host.querySelectorAll("[data-chip-remove-col]").forEach((btn) => {
    btn.addEventListener("click", () => removeColumnFilterValue(btn.dataset.chipRemoveCol, btn.dataset.chipRemoveVal));
  });
  host.querySelectorAll("[data-chip-remove-col-range]").forEach((btn) => {
    btn.addEventListener("click", () => clearColumnFilterRange(btn.dataset.chipRemoveColRange));
  });
  host.querySelectorAll("[data-chip-remove-col-all]").forEach((btn) => {
    btn.addEventListener("click", () => clearColumnFilter(btn.dataset.chipRemoveColAll));
  });
  host.querySelectorAll("[data-chip-remove-slicer]").forEach((btn) => {
    btn.addEventListener("click", () => toggleSlicer(btn.dataset.chipRemoveSlicer, btn.dataset.chipRemoveVal));
  });
  host.querySelectorAll("[data-chip-remove-slicer-all]").forEach((btn) => {
    btn.addEventListener("click", () => clearSlicer(btn.dataset.chipRemoveSlicerAll));
  });
  host.querySelectorAll("[data-chip-remove-quick]").forEach((btn) => {
    btn.addEventListener("click", () => clearTrackerQuickFilter(btn.dataset.chipRemoveQuick));
  });
  host.querySelector("[data-chip-remove-sort]")?.addEventListener("click", () => {
    state.tableSort = { column: null, dir: null };
    saveState();
    renderClaimsTable();
  });
  host.querySelector("[data-chip-remove-all]")?.addEventListener("click", () => {
    try { clearAllTrackerFilters(); } catch (_) {
      state.columnFilters = {};
      state.slicers = structuredClone(emptySlicers);
      state.tableSort = { column: null, dir: null };
      saveState();
      renderClaimsTable();
    }
  });
}

function columnFilterOptions(key) {
  // In API mode, a filter menu is an Excel-style facet over the complete
  // filtered population. Page rows are never a valid fallback: they omit values
  // that happen to live on another page and make the menu depend on pagination.
  if (trackerApiEnabled()) return columnFilterOptionsFromApiFacets(key);
  const mode = state.rowMode || "service";
  if (key === "mergedNotes") return mergedNoteLocalFilterOptions(getBaseFilteredClaims());
  const counts = new Map();
  if (mode === "service") {
    getBaseFilteredClaims().forEach((claim) => {
      claim.services.forEach((service) => {
        const value = getColumnValue(key, claim, service);
        counts.set(value, (counts.get(value) || 0) + 1);
      });
    });
  } else {
    getBaseFilteredClaims().forEach((claim) => {
      const values = serviceColumnOnly(key)
        ? Array.from(new Set(claim.services.map((service) => getColumnValue(key, claim, service))))
        : [getColumnValue(key, claim, null)];
      values.forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
    });
  }
  // Free-text note columns group on collapsed/lowercased text and sort by
  // frequency; every other column keeps the raw value + smart sort.
  if (isNoteFilterColumn(key)) return noteFilterOptionsFromCounts(counts);
  const options = Array.from(counts.entries())
    .map(([value, count]) => ({ value, count }))
    .sort(columnFilterOptionComparator(key));
  return key === "action" ? followUpFilterOptions(options) : options;
}

function columnFilterOptionsFromApiFacets(key) {
  if (!trackerApiEnabled()) return [];
  const facetKey = columnFilterFacetKey(key);
  if (!facetKey) return [];
  const query = buildColumnFilterFacetQuery(key, state.rowMode || "service");
  const serverFacets = trackerApiFacetsForQuery(query);
  const rows = serverFacets?.facets?.[facetKey];
  if (!Array.isArray(rows)) return [];
  if (!rows.length) return key === "action" ? followUpFilterOptions() : [];
  const options = rows
    .map((row) => ({
      value: cleanFacetDisplayValue(row.value),
      count: nonNegativeNumber(row.count, 0)
    }))
    .filter((option) => option.value)
    .sort(columnFilterOptionComparator(key));
  return key === "action" ? followUpFilterOptions(options) : options;
}

function buildColumnFilterFacetQuery(key, mode = state.rowMode || "service") {
  const query = buildTrackerQueryRequest(mode);
  const columnFilters = ensureColumnFilterState(query.columnFilters || {});
  delete columnFilters[key];
  // Its own direction goes with its own selection: a card that dropped its
  // filter but kept its mode would describe a population it is not counting.
  const columnFilterModes = ensureFilterModeState(query.columnFilterModes || {}, columnFilters);
  delete columnFilterModes[key];
  // ITS OWN COMPARISON GOES WITH ITS OWN SELECTION. A menu that kept the
  // column's own range would offer only the values that already satisfy it -
  // open the Charges menu under "Charges > 500" and every option under 500
  // disappears, so there is no way back. Excel's menu shows the choices the
  // OTHER filters leave; this is that rule, now over two controls instead of
  // one.
  const columnFilterRanges = ensureColumnFilterRangeState(query.columnFilterRanges || state.columnFilterRanges);
  delete columnFilterRanges[key];
  return {
    ...query,
    columnFilters,
    columnFilterModes,
    columnFilterRanges,
    facets: [columnFilterFacetKey(key)].filter(Boolean)
  };
}

// Live Excel-style menus request a DISTINCT facet over the whole filtered book.
// Keep that surface to useful bounded/moderate categorical and date dimensions;
// PHI/free text, timestamps, and exact numeric/money values remain
// filterable only in the local in-memory lane rather than becoming unbounded SQL.
const SERVER_COLUMN_FILTER_FACET_KEYS = new Set([
  "claimId", "serviceId",
  "tenant", "client", "provider", "renderingProvider",
  "primaryPayer", "primaryExternalId", "secondaryPayer", "secondaryExternalId",
  "action", "followUpDate", "match", "claimStatus", "lifecycle", "lifecycleStage",
  "claimClassification", "statusCategory", "recommendedNextAction", "calculationGuards",
  "aging", "owner", "assignedTo", "assignmentStatus", "facility", "queue",
  "queueReason", "claimDue", "orgId", "dos", "procedure", "m1", "m2", "place",
  "secondaryCalc", "paymentExternalPayerIds", "paymentStatus",
  // The FREE-TEXT / NAME members of this set. They are safe here only because
  // their facet SQL bounds itself: see EXPLICIT_ONLY_FACET_KEYS and the TOP-N +
  // HAVING search in trackerSynapseSqlBuilder. Adding any other free-text column
  // here without that bounding is how the 12.52 MB facet batch happened.
  //
  // `patient` is the Name column, added 2026-09-22 on the owner's request ("a
  // dropdown to show every single client possible based off the current
  // filters"). The server refuses it by name to any principal whose envelope
  // does not carry the Name column, and permittedColumnsForMode has already
  // dropped the whole column for those principals, so the caret cannot appear
  // for a role that cannot read the cell.
  "sourceServiceLineNotes", "patient",
  // Notes: the EZClaim note facet merged with the portal ledger's latest note,
  // bounded and searched in SQL like the column beside it.
  "mergedNotes",
  // ---------------------------------------------------------------------
  // EVERY REMAINING COLUMN, 2026-09-22 (owner: "There have to be drop-downs
  // for all of the columns").
  // ---------------------------------------------------------------------
  // These are the keys the server refused by name until today, and the reason
  // was sound: a distinct-value list over a continuous column is unbounded,
  // and an unbounded facet batch measured 12.52 MB / 38.7s. They are safe here
  // now for exactly the reason the three free-text members above are safe -
  // the server serves them on the BOUNDED lane (EXPLICIT_ONLY_FACET_KEYS:
  // named, alone, TOP-N by count, parameterised search, grouped in SQL), and
  // subtracts them from the unnamed default expansion so the widest request
  // there is never asks for one. Adding a column here WITHOUT that bounding is
  // how the 12.52 MB batch happened; adding one without a predicate is how the
  // Claim ID popover shipped saying "Choices could not be loaded."
  //
  // Money and rates group on the STORED decimal(18,4), not on its text, and
  // the menu formats it for display - see columnFilterOptionLabel. Each is
  // refused BY NAME to any principal whose envelope does not carry the column,
  // so a role that cannot read the Charges CELL cannot enumerate it either.
  "charges", "units", "adjs", "balance", "owed",
  "totalInsAmtPaid", "totalPatAmtPaid", "totalPatBal", "positiveTotalPatBal",
  "primaryRate", "primaryExpectedTotal", "secondaryRate", "secondaryAllowedTotal",
  "secondaryExpectedTotal", "staticTotalExpected", "finalExpectedTotalNumeric",
  "finalSecondaryExpectedNumeric", "secondaryExpectedChangeNumeric",
  "totalExpectedChangeNumeric", "writeOffAmount", "finalInsuranceExpected",
  "finalInsurancePaid", "finalInsuranceBalance", "prAdjustmentTotal",
  "primaryDisbursementTotal", "secondaryDisbursementTotal",
  "unmatchedDisbursementTotal", "totalExpected", "totalPaid",
  // Resp Party is a PHI name and is gated exactly like the Name column.
  "respPartyName",
  // Pat Class was absent with the note "the mirror cannot serve that facet
  // safely". It is an ordinary enumerable facet on the WAREHOUSE lane, which
  // is the lane production runs, and the header only draws a caret for the
  // mode that draws the column.
  "patClassification",
  // In/Out of Network, and the two identifier columns.
  "network", "ehrBillingEntryId",
  // Hold duration (backend/serviceLineHoldEpisodes.js).
  "holdReason", "onHoldSince", "daysOnHold", "holdReleasedOn"
]);

const SERVER_COLUMN_FILTER_FACET_ALIASES = {
  payer: "primaryPayer",
  followUpAction: "action",
  workQueue: "queue",
  location: "facility",
  renderingProvider: "provider",
  assignedTo: "owner",
  // RBT and Provider are the SAME warehouse column (rendering_provider), so
  // the RBT header reads Provider's option list rather than asking the server
  // to enumerate the identical values under a second key. The FILTER is still
  // stored under `rbt` and still says "RBT" on its chip, because that is the
  // column the reader clicked.
  rbt: "provider"
};

function columnFilterFacetKey(key) {
  if (!SERVER_FILTERABLE_TRACKER_COLUMNS.has(key)) return "";
  const facetKey = SERVER_COLUMN_FILTER_FACET_ALIASES[key] || key;
  // Claim Classification is mode-aware on the backend; Pat Classification is
  // intentionally absent because the mirror cannot serve that facet safely.
  return SERVER_COLUMN_FILTER_FACET_KEYS.has(facetKey) ? facetKey : "";
}

function trackerApiColumnFilterValue(row, key) {
  if (key === "claimId") return trackerApiClaimId(row);
  const raw = trackerApiCellRawValue(row, key);
  return cleanFacetDisplayValue(raw);
}

function apiRowAliasForColumn(key) {
  const aliases = {
    serviceId: "service_id",
    claimStatus: "claim_status",
    primaryPayer: "primary_payer",
    secondaryPayer: "secondary_payer",
    renderingProvider: "rendering_provider",
    followUpDate: "follow_up_date",
    action: "follow_up_action",
    assignedTo: "assigned_to",
    // The bill-date apply emits [bill_date_aging]; the local resolver emits
    // billDateAging. `time` resolves through this key, so both lanes agree.
    billDateAging: "bill_date_aging"
  };
  return aliases[key] || key;
}

function permittedColumnsForMode(mode = state.rowMode || "service") {
  const normalizedMode = normalizeColumnViewMode(mode);
  const roleColumns = isClientRole() ? (normalizedMode === "service" ? clientServiceColumns : clientClaimColumns) : (normalizedMode === "service" ? serviceColumns : claimColumns);
  const base = isExternalClientRole(state?.role)
    ? roleColumns.filter((column) => FULL_CLIENT_WORKBENCH_COLUMN_KEY_SET.has(column.key))
    : roleColumns;
  const gated = base.filter((column) => {
    if (isRedactedClientRole() && REDACTED_CLIENT_HIDDEN_COLUMN_KEYS.has(column.key)) return false;
    if (!isRedactedClientRole() && !hasPermission(PERMISSIONS.VIEW_FINANCIALS) && financialColumns.has(column.key)) return false;
    if (!hasPermission(PERMISSIONS.VIEW_PHI) && phiColumns.has(column.key)) return false;
    return true;
  });
  // NOTES DISCLOSES BOTH NOTE COLUMNS, so it exists only where both do. An
  // external client (no EZClaim source note) keeps Latest Claim Note exactly as
  // before, and with Notes absent nothing is hidden by default for them.
  const gatedKeys = new Set(gated.map((column) => column.key));
  const permitted = gatedKeys.has("serviceLineNotes") && gatedKeys.has("sourceServiceLineNotes")
    ? gated
    : gated.filter((column) => column.key !== "mergedNotes");
  if (isClientRole()) return permitted;
  if (offshoreBoardMode()) {
    const keys = normalizedMode === "service" ? ["claimId","serviceId","patient","dos","procedure","primaryPayer","claimStatus","owed","action","followUpDate","serviceLineNotes","notes"] : ["claimId","patient","primaryPayer","claimStatus","owed","action","followUpDate"];
    return keys.map((key) => permitted.find((column) => column.key === key)).filter(Boolean);
  }
  return permitted;
}

function selectedColumnKeysForMode(mode = state.rowMode || "service", permitted = permittedColumnsForMode(mode)) {
  const normalizedMode = normalizeColumnViewMode(mode);
  state.columnViewSelections = normalizeColumnViewSelections(state.columnViewSelections || {});
  const requested = normalizeColumnKeyList(state.columnViewSelections[normalizedMode]);
  if (!requested.length) return defaultVisibleColumnKeys(permitted);
  const permittedKeys = new Set(permitted.map((column) => column.key));
  const selected = requested.filter((key) => permittedKeys.has(key));
  return selected.length ? selected : defaultVisibleColumnKeys(permitted);
}

// THE DEFAULT LAYOUT HIDES WHAT THE MERGED NOTES COLUMN REPLACES (2026-09-23).
// These two are left out of the default only while the column that stands in
// for them (Notes) is permitted; the Columns dialog still lists them under their
// own names, and any saved selection or saved view that names one shows it,
// because a named selection never reaches this function.
const DEFAULT_HIDDEN_COLUMN_KEYS = new Set(["serviceLineNotes", "sourceServiceLineNotes"]);
function defaultVisibleColumnKeys(permitted = []) {
  const keys = permitted.map((column) => column.key);
  if (!keys.includes("mergedNotes")) return keys;
  // A column filter still active on a hidden original keeps that column on
  // screen, so a filter is never narrowing the grid from off-screen.
  const filtered = (key) => ((state.columnFilters || {})[key] || []).length > 0;
  return keys.filter((key) => !DEFAULT_HIDDEN_COLUMN_KEYS.has(key) || filtered(key));
}

// ===========================================================================
// USER COLUMN ORDER - THE ALL DATA GRID'S ONE ORDERING SOURCE OF TRUTH.
// ===========================================================================
//
// The offshore billers asked for their Excel tracker's one missing habit:
// "there's no option to rearrange the columns to match our workflow"
// (2026-09-22). The grid already had a visibility picker and a fixed order.
//
// The arrangement is threaded through currentColumns() and NOWHERE else. Every
// header, every cell, the frozen-column plan, every colspan and every
// column-filter label already read that one function, so a renderer cannot
// disagree with the arrangement by construction - the same reason the frozen
// columns are keyed by column key rather than by slot index.
//
// WHAT FOLLOWS THE ORDER, decided once and stated here so nobody has to guess:
//   * the on-screen grid, both lanes (live API rows and local/demo rows), and
//   * a browser print of that screen, because printing prints the DOM.
// WHAT DOES NOT:
//   * the tracker workbook export. The SERVER builds that book to a fixed tab
//     and column layout; the browser never lays it out.
//   * the report CSV exporters, which carry their own column lists.
// A personal arrangement is a reading preference. An exported book is a shared
// artifact, and two billers filtering the same rows must not hand out
// differently shaped files. No money value, rounding, format or datum changes
// either way - only the order cells are emitted in on screen.
//
// STORAGE is keyed by user AND role. The permitted column set differs by role
// (financial and PHI columns, RedactedClient, the offshore board), so an
// arrangement over one set means nothing over another. It is its own
// localStorage key rather than a field of `state`: state is one blob for the
// whole local preview and Reset demo wipes it, and a column arrangement should
// survive that the way the theme preference does.
const ALL_DATA_COLUMN_ORDER_KEY_PREFIX = "tms.portal.allData.columnOrder.v1";
const allDataColumnOrderCache = { key: "", value: null };

function allDataColumnOrderStorageKey() {
  const role = typeof canonicalRole === "function" ? canonicalRole(state?.role || "") : String(state?.role || "");
  let user = "";
  try { user = String(currentUser()?.name || state?.activeUserName || ""); }
  catch (_error) { user = String(state?.activeUserName || ""); }
  // Both halves are escaped, so a user whose name contains the separator can
  // never land on another identity's arrangement.
  return `${ALL_DATA_COLUMN_ORDER_KEY_PREFIX}::${encodeURIComponent(role)}::${encodeURIComponent(user)}`;
}

// MISSING, MALFORMED AND UNKNOWN ALL FALL BACK TO THE DEFAULT ORDER.
// Anything that is not an object of two key lists is discarded rather than
// repaired. normalizeColumnKeyList already drops non-identifier junk,
// duplicates and anything past 120 keys; a key that names no column is simply
// never found when the order is applied.
function readAllDataColumnOrders() {
  const key = allDataColumnOrderStorageKey();
  if (allDataColumnOrderCache.key === key && allDataColumnOrderCache.value) return allDataColumnOrderCache.value;
  let parsed = null;
  try { parsed = JSON.parse(safeLocalStorageGet(key) || "null"); } catch (_error) { parsed = null; }
  const value = normalizeColumnViewSelections(parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {});
  allDataColumnOrderCache.key = key;
  allDataColumnOrderCache.value = value;
  return value;
}

function writeAllDataColumnOrders(next) {
  const key = allDataColumnOrderStorageKey();
  const value = normalizeColumnViewSelections(next || {});
  allDataColumnOrderCache.key = key;
  allDataColumnOrderCache.value = value;
  safeLocalStorageSet(key, JSON.stringify(value));
  return value;
}

function userColumnOrderKeys(mode) {
  return readAllDataColumnOrders()[normalizeColumnViewMode(mode)] || [];
}

function hasUserColumnOrder(mode) {
  return userColumnOrderKeys(mode).length > 0;
}

// A COLUMN THE STORED ORDER NEVER NAMED KEEPS ITS DEFAULT POSITION.
// A column added to the grid after a biller last arranged theirs is not in
// their stored list. Dropping it would make every new column invisible to
// exactly the people who arrange their grid, forever; appending it would bury
// it at the far right of a 49-column sheet. So it re-enters immediately after
// the nearest default-order neighbour the stored order DID name - it lands
// where whoever added it put it.
function applyUserColumnOrder(mode, columns) {
  const list = (Array.isArray(columns) ? columns : []).filter(Boolean);
  const order = userColumnOrderKeys(mode);
  if (!order.length || list.length < 2) return list;
  const byKey = new Map(list.map((column) => [column.key, column]));
  const named = order.filter((key) => byKey.has(key));
  // An order that names nothing in this grid - a stale role, a hand-edited
  // value, a renamed column set - is no order at all. Default, not chaos.
  if (!named.length) return list;
  const ordered = named.map((key) => byKey.get(key));
  const placed = new Set(named);
  let headCursor = 0;
  list.forEach((column, index) => {
    if (placed.has(column.key)) return;
    let anchor = -1;
    for (let back = index - 1; back >= 0; back -= 1) {
      if (placed.has(list[back].key)) {
        anchor = ordered.findIndex((item) => item.key === list[back].key);
        break;
      }
    }
    if (anchor < 0) {
      ordered.splice(headCursor, 0, column);
      headCursor += 1;
    } else {
      ordered.splice(anchor + 1, 0, column);
    }
    placed.add(column.key);
  });
  return ordered;
}

// The arrangement is stored over the PERMITTED set, never the visible one, so
// hiding a column and showing it again returns it to where it was arranged
// rather than to the end.
function arrangedPermittedColumns(mode) {
  const normalizedMode = normalizeColumnViewMode(mode);
  return applyUserColumnOrder(normalizedMode, permittedColumnsForMode(normalizedMode));
}

function setUserColumnOrderKeys(mode, keys) {
  const normalizedMode = normalizeColumnViewMode(mode);
  const permitted = new Set(permittedColumnsForMode(normalizedMode).map((column) => column.key));
  const next = normalizeColumnKeyList(keys).filter((key) => permitted.has(key));
  writeAllDataColumnOrders({ ...readAllDataColumnOrders(), [normalizedMode]: next });
  return next;
}

function resetUserColumnOrder(mode) {
  const normalizedMode = normalizeColumnViewMode(mode);
  writeAllDataColumnOrders({ ...readAllDataColumnOrders(), [normalizedMode]: [] });
}

// The arrange control moves one column one step through the PERMITTED order.
function moveUserColumnByStep(mode, key, delta) {
  const normalizedMode = normalizeColumnViewMode(mode);
  const order = arrangedPermittedColumns(normalizedMode).map((column) => column.key);
  const from = order.indexOf(String(key || ""));
  const to = from + (Number(delta) || 0);
  if (from < 0 || to < 0 || to >= order.length) return null;
  order.splice(to, 0, order.splice(from, 1)[0]);
  setUserColumnOrderKeys(normalizedMode, order);
  return { from, to, total: order.length };
}

// A DRAG MOVES A VISIBLE COLUMN; THE STORE HOLDS EVERY PERMITTED ONE.
// Hidden columns keep their absolute slot and only the visible ones are
// permuted into the sequence the drop produced, so a drag can never reshuffle
// - or silently drop - a column the biller cannot see.
function applyVisibleColumnSequence(mode, nextVisibleKeys) {
  const normalizedMode = normalizeColumnViewMode(mode);
  const full = arrangedPermittedColumns(normalizedMode).map((column) => column.key);
  const sequence = normalizeColumnKeyList(nextVisibleKeys);
  const visible = new Set(sequence);
  let cursor = 0;
  const merged = full.map((key) => (visible.has(key) ? sequence[cursor++] : key));
  // Every visible slot must have been claimed exactly once. If the sequence and
  // the stored order have drifted, write nothing rather than a half-order.
  if (cursor !== sequence.length) return false;
  setUserColumnOrderKeys(normalizedMode, merged);
  return true;
}

// ---------------------------------------------------------------------------
// THE DRAG. Pointer-based, mouse-only, with a visible drop line.
// ---------------------------------------------------------------------------
// Two things this must not break, and both are one line each:
//   * a PLAIN CLICK MUST STILL SORT. A drag that ends over the header would
//     otherwise fire the header's own click handler and sort the column it just
//     moved. The capture-phase listener below eats exactly one click, and only
//     after a real drag; an ordinary pointerdown clears the flag first so a
//     stale suppression can never eat a genuine sort.
//   * the FILTER CARET OWNS ITS OWN POINTER. A pointerdown inside
//     [data-column-filter] never starts a drag, so the value dropdown opens
//     exactly as before.
// Touch and pen never start a drag (the arrange control in the Columns dialog
// is the everywhere path); a narrow layout therefore keeps its native
// horizontal scroll gesture.
const allDataColumnDragRuntime = { active: null, suppressClick: false };

function allDataColumnDropIndicator() {
  let node = document.getElementById("allDataColumnDropIndicator");
  if (!node) {
    node = document.createElement("div");
    node.id = "allDataColumnDropIndicator";
    node.className = "column-drop-indicator";
    node.setAttribute("aria-hidden", "true");
    node.hidden = true;
    document.body.appendChild(node);
  }
  return node;
}

function allDataColumnDropBoundary(cells, clientX) {
  for (let index = 0; index < cells.length; index += 1) {
    const rect = cells[index].getBoundingClientRect();
    if (clientX < rect.left + (rect.width / 2)) return index;
  }
  return cells.length;
}

function installAllDataColumnReorder() {
  const head = els.workbenchHeader || document.getElementById("workbenchHeader");
  if (!head || head.dataset.columnReorderInstalled === "1") return;
  head.dataset.columnReorderInstalled = "1";
  head.addEventListener("pointerdown", onAllDataColumnPointerDown);
  head.addEventListener("click", (event) => {
    if (!allDataColumnDragRuntime.suppressClick) return;
    allDataColumnDragRuntime.suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  }, true);
}

function onAllDataColumnPointerDown(event) {
  allDataColumnDragRuntime.suppressClick = false;
  if (allDataColumnDragRuntime.active) return;
  if (event.button !== 0 || event.pointerType !== "mouse") return;
  const target = event.target;
  if (!target || typeof target.closest !== "function") return;
  if (target.closest("[data-column-filter]")) return;
  const cell = target.closest("th[data-col]");
  if (!cell || !cell.parentElement) return;
  const cells = Array.from(cell.parentElement.querySelectorAll("th[data-col]"));
  const from = cells.indexOf(cell);
  if (cells.length < 2 || from < 0) return;
  const mode = normalizeColumnViewMode(state.rowMode || "service");
  const column = currentColumns(mode).find((item) => item.key === cell.dataset.col);
  allDataColumnDragRuntime.active = {
    mode,
    cells,
    from,
    boundary: from,
    startX: event.clientX,
    dragging: false,
    pointerId: event.pointerId,
    label: column?.label || cell.dataset.col
  };
  window.addEventListener("pointermove", onAllDataColumnPointerMove);
  window.addEventListener("pointerup", onAllDataColumnPointerUp);
  window.addEventListener("pointercancel", onAllDataColumnPointerUp);
}

function onAllDataColumnPointerMove(event) {
  const drag = allDataColumnDragRuntime.active;
  if (!drag || event.pointerId !== drag.pointerId) return;
  if (!drag.dragging) {
    // A few pixels of slop: a click with a shaky hand is still a click.
    if (Math.abs(event.clientX - drag.startX) < 6) return;
    drag.dragging = true;
    document.body.classList.add("all-data-column-dragging");
    drag.cells[drag.from]?.classList.add("is-column-dragging");
  }
  if (event.cancelable) event.preventDefault();
  drag.boundary = allDataColumnDropBoundary(drag.cells, event.clientX);
  const wrap = document.getElementById("trackerTableWrap");
  const rect = wrap ? wrap.getBoundingClientRect() : null;
  const last = drag.cells[drag.cells.length - 1];
  const edge = drag.boundary < drag.cells.length
    ? drag.cells[drag.boundary].getBoundingClientRect().left
    : last.getBoundingClientRect().right;
  const node = allDataColumnDropIndicator();
  node.hidden = false;
  node.style.left = `${Math.round(rect ? Math.min(Math.max(edge, rect.left), rect.right) : edge)}px`;
  node.style.top = `${Math.round(rect ? rect.top : 0)}px`;
  node.style.height = `${Math.round(rect ? rect.height : 0)}px`;
}

function onAllDataColumnPointerUp(event) {
  const drag = allDataColumnDragRuntime.active;
  if (!drag || (event && event.pointerId !== drag.pointerId)) return;
  finishAllDataColumnDrag(drag, Boolean(event && event.type === "pointerup"));
}

function finishAllDataColumnDrag(drag, commit) {
  window.removeEventListener("pointermove", onAllDataColumnPointerMove);
  window.removeEventListener("pointerup", onAllDataColumnPointerUp);
  window.removeEventListener("pointercancel", onAllDataColumnPointerUp);
  allDataColumnDragRuntime.active = null;
  document.body.classList.remove("all-data-column-dragging");
  drag.cells[drag.from]?.classList.remove("is-column-dragging");
  const node = document.getElementById("allDataColumnDropIndicator");
  if (node) node.hidden = true;
  if (!drag.dragging) return;
  // A drag that ENDED is not a click, whatever the browser reports next.
  allDataColumnDragRuntime.suppressClick = true;
  if (!commit) return;
  const keys = drag.cells.map((cell) => cell.dataset.col);
  const target = drag.boundary > drag.from ? drag.boundary - 1 : drag.boundary;
  if (!Number.isInteger(target) || target === drag.from || target < 0 || target >= keys.length) return;
  const next = keys.slice();
  next.splice(target, 0, next.splice(drag.from, 1)[0]);
  if (!applyVisibleColumnSequence(drag.mode, next)) return;
  renderClaimsTable();
  showToast(`Moved ${drag.label} to position ${target + 1} of ${next.length}.`);
}

function currentColumns(mode = state.rowMode || "service") {
  const normalizedMode = normalizeColumnViewMode(mode);
  return applyUserColumnOrder(normalizedMode, defaultOrderedColumns(normalizedMode));
}

// The DEFAULT order: role-permitted columns narrowed by the visibility picker
// and the workbench preset. Behaviour unchanged - it is simply no longer the
// last word on order.
function defaultOrderedColumns(mode = state.rowMode || "service") {
  const normalizedMode = normalizeColumnViewMode(mode);
  const permitted = permittedColumnsForMode(normalizedMode);
  const selectedKeys = selectedColumnKeysForMode(normalizedMode, permitted);
  if (selectedKeys.length && selectedKeys.length < permitted.length) {
    return selectedKeys
      .map((key) => permitted.find((column) => column.key === key))
      .filter(Boolean);
  }
  const presetKey = state.workbenchPreset || "arScan";
  const preset = workbenchPresets[presetKey] || workbenchPresets.full;
  const allow = preset[normalizedMode];
  if (!Array.isArray(allow) || !allow.length) return permitted;
  const allowSet = new Set(allow);
  const ordered = allow
    .map((key) => permitted.find((column) => column.key === key))
    .filter(Boolean);
  // Append any permitted columns not in preset that have active column filters — so user filter state stays visible.
  const filterKeys = Object.keys(state.columnFilters || {}).filter((key) => (state.columnFilters[key] || []).length);
  filterKeys.forEach((key) => {
    if (!allowSet.has(key)) {
      const col = permitted.find((column) => column.key === key);
      if (col) ordered.push(col);
    }
  });
  return ordered;
}

function renderColumnViewButton() {
  const button = els.columnViewButton || document.getElementById("columnViewButton");
  if (!button) return;
  const mode = normalizeColumnViewMode(state.rowMode || "service");
  const permitted = permittedColumnsForMode(mode);
  const visible = currentColumns(mode);
  button.textContent = `Columns (${visible.length})`;
  button.title = `${formatNounCount(visible.length, "column")} of ${formatNounCount(permitted.length, "column")} visible`;
  button.setAttribute("aria-expanded", columnViewUi.open ? "true" : "false");
}

function openColumnViewModal() {
  const returnFocus = document.activeElement;
  columnViewUi = { ...columnViewUi, open: true, search: "", saving: false, label: defaultColumnViewLabel(), selectedSavedViewId: "" };
  ensureColumnViewsLoaded();
  renderColumnViewModal();
  activateForegroundModal(document.getElementById("columnViewModal"), {
    initialFocus: "[data-column-view-search]",
    onEscape: closeColumnViewModal,
    returnFocus
  });
  renderColumnViewButton();
}

function closeColumnViewModal() {
  columnViewUi.open = false;
  const modal = document.getElementById("columnViewModal");
  deactivateForegroundModal(modal, { restoreFocus: true });
  modal?.remove();
  renderColumnViewButton();
}

function defaultColumnViewLabel() {
  const role = canonicalRole(state.role || "User").replace(/([a-z])([A-Z])/g, "$1 $2");
  const mode = normalizeColumnViewMode(state.rowMode || "service") === "service" ? "Rows" : "Groups";
  return `${role} ${mode} ${new Date().toISOString().slice(0, 10)}`;
}

// ---------------------------------------------------------------------------
// DRAGGING A COLUMN INSIDE THE COLUMNS DIALOG (owner, 2026-09-22).
// ---------------------------------------------------------------------------
//   "These arrows are crazy. I can only do one at a time. Can I drag it around?
//    Is there any way to make this easier?"
//
// Moving a column from position 44 to position 3 was 41 clicks, each one
// re-rendering the dialog under the reader's finger. The arrows are not deleted
// - they are the KEYBOARD path and the only one that works without a pointer -
// but they stop being the primary one, and they get quieter in the CSS to say so.
//
// ONE SOURCE OF TRUTH, SHARED WITH THE HEADER DRAG. The drop writes through
// setUserColumnOrderKeys over the PERMITTED set, exactly as moveUserColumnByStep
// and applyVisibleColumnSequence already do, so the grid, the header, the frozen
// plan, every colspan and this dialog cannot disagree about the arrangement.
// Nothing here knows how to store an order.
//
// IT WORKS WITH THE SEARCH NARROWED, and that is the part with a real decision
// in it. The arrows stay disabled under a search because "move left one" is
// meaningless when the neighbour you can see is not the neighbour you would
// swap with. A DROP has no such ambiguity: it names a visible card and lands
// immediately beside THAT column in the full order, so every column the search
// is hiding keeps its position relative to the one you dropped against. The
// reader gets the only reading of the gesture that is available to them.
//
// POINTER EVENTS, MOUSE AND TOUCH - unlike the grid header, which is
// deliberately mouse-only because a narrow layout needs its horizontal scroll
// gesture. Here the drag is vertical-ish inside a scrollable list, so touch gets
// a GRIP with touch-action:none rather than the whole card: a touch that starts
// on the card body must still scroll the list, and a touch that starts on the
// grip must not.
const columnViewDragRuntime = { active: null, suppressClick: false, frame: 0 };

// THE DROP POINT IN A WRAPPED GRID. The list is
// `repeat(auto-fill, minmax(220px, 1fr))`, so the cards read left-to-right and
// wrap - which means "before card i" is a READING-ORDER question, not an x or a
// y one. A pointer is PAST a card when it is below that card's row, or when it
// is level with the card and past the card's own midline; the first card it is
// not past is the one the moved column lands before.
function columnViewDropIndex(cards, clientX, clientY) {
  for (let index = 0; index < cards.length; index += 1) {
    const rect = cards[index].getBoundingClientRect();
    const pastRow = clientY > rect.bottom;
    const pastCard = clientY >= rect.top && clientX > rect.left + (rect.width / 2);
    if (!pastRow && !pastCard) return index;
  }
  return cards.length;
}

function columnViewDropIndicator() {
  let node = document.getElementById("columnViewDropIndicator");
  if (!node) {
    node = document.createElement("div");
    node.id = "columnViewDropIndicator";
    node.className = "column-view-drop-indicator";
    node.setAttribute("aria-hidden", "true");
    node.hidden = true;
    document.body.appendChild(node);
  }
  return node;
}

// A DROP MOVES ONE COLUMN BESIDE ANOTHER, IN THE FULL PERMITTED ORDER.
// The anchor is a card the reader can see; everything the search is hiding keeps
// its position relative to that anchor, so a narrowed list can never silently
// reshuffle - or lose - a column that is not on screen.
function applyColumnViewDrop(mode, movedKey, anchorKey, placement) {
  const normalizedMode = normalizeColumnViewMode(mode);
  const order = arrangedPermittedColumns(normalizedMode).map((column) => column.key);
  const from = order.indexOf(String(movedKey || ""));
  if (from < 0 || !order.includes(String(anchorKey || "")) || anchorKey === movedKey) return null;
  const without = order.filter((key) => key !== movedKey);
  const anchorAt = without.indexOf(anchorKey);
  const target = placement === "after" ? anchorAt + 1 : anchorAt;
  without.splice(target, 0, movedKey);
  const to = without.indexOf(movedKey);
  // A drop that lands a column where it already was is not a move. Writing it
  // anyway would replace the DEFAULT order with an explicit copy of itself and
  // light "Reset order" over an arrangement nobody made.
  if (to === from) return null;
  setUserColumnOrderKeys(normalizedMode, without);
  return { from, to, total: without.length };
}

function installColumnViewDrag(modal) {
  const list = modal.querySelector(".column-view-list");
  if (!list) return;
  list.addEventListener("pointerdown", onColumnViewPointerDown);
  // A drag that ENDED is not a click, whatever the browser reports next: the
  // card is a <label>, so an unsuppressed click would toggle the column's
  // visibility as a parting gift for having moved it.
  list.addEventListener("click", (event) => {
    if (!columnViewDragRuntime.suppressClick) return;
    columnViewDragRuntime.suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  }, true);
}

function onColumnViewPointerDown(event) {
  columnViewDragRuntime.suppressClick = false;
  if (columnViewDragRuntime.active) return;
  if (event.button !== 0) return;
  const target = event.target;
  if (!target || typeof target.closest !== "function") return;
  // The checkbox and the two arrows own their own pointer. Ticking a column and
  // stepping it one place are still one click each. The grip is a SPAN, not a
  // button, so it is not in this set and not in the tab order either - the
  // arrows are the keyboard path and a second focus stop that does nothing on
  // Enter would be a worse one.
  if (target.closest("input, button")) return;
  const grip = Boolean(target.closest("[data-column-view-grip]"));
  // TOUCH AND PEN START FROM THE GRIP ONLY. A touch on the card body has to keep
  // scrolling the list - the dialog holds 48 cards and the list is the only
  // thing that scrolls - and a list you cannot scroll is a worse regression than
  // the arrows the owner is complaining about.
  if (!grip && event.pointerType !== "mouse") return;
  const card = target.closest(".column-view-item");
  const cards = Array.from(event.currentTarget.querySelectorAll(".column-view-item"));
  const from = cards.indexOf(card);
  if (!card || cards.length < 2 || from < 0) return;
  columnViewDragRuntime.active = {
    mode: normalizeColumnViewMode(state.rowMode || "service"),
    list: event.currentTarget,
    cards,
    card,
    from,
    key: card.dataset.columnViewItem || "",
    label: card.dataset.columnViewLabel || card.dataset.columnViewItem || "",
    boundary: from,
    startX: event.clientX,
    startY: event.clientY,
    pointerX: event.clientX,
    pointerY: event.clientY,
    dragging: false,
    pointerId: event.pointerId
  };
  // Captured so the drag survives the pointer leaving the card - and, on touch,
  // so the gesture is not stolen by the scroller the moment it moves.
  try { event.currentTarget.setPointerCapture(event.pointerId); } catch (_error) { /* not capturable */ }
  window.addEventListener("pointermove", onColumnViewPointerMove);
  window.addEventListener("pointerup", onColumnViewPointerUp);
  window.addEventListener("pointercancel", onColumnViewPointerUp);
}

function onColumnViewPointerMove(event) {
  const drag = columnViewDragRuntime.active;
  if (!drag || event.pointerId !== drag.pointerId) return;
  if (!drag.dragging) {
    // A few pixels of slop, in BOTH axes: a tap on the label with a shaky hand
    // is still a tap, and this list wraps so a real drag can be horizontal.
    if (Math.abs(event.clientX - drag.startX) < 6 && Math.abs(event.clientY - drag.startY) < 6) return;
    drag.dragging = true;
    document.body.classList.add("column-view-dragging");
    drag.card.classList.add("is-column-view-dragging");
  }
  if (event.cancelable) event.preventDefault();
  drag.pointerX = event.clientX;
  drag.pointerY = event.clientY;
  drag.boundary = columnViewDropIndex(drag.cards, event.clientX, event.clientY);
  paintColumnViewDropIndicator(drag);
  scheduleColumnViewAutoScroll(drag);
}

// AUTO-SCROLL, because the list holds 48 cards in a box that shows about nine.
// Without it a column can only be dragged as far as the reader can already see,
// which on a 48-column grid is most of the reason the arrows were painful.
// Speed ramps with how deep into the edge band the pointer is, so a small
// correction near the edge does not fling the list.
const COLUMN_VIEW_AUTOSCROLL_BAND = 44;
const COLUMN_VIEW_AUTOSCROLL_MAX = 18;

function columnViewAutoScrollStep(drag) {
  const rect = drag.list.getBoundingClientRect();
  const above = rect.top + COLUMN_VIEW_AUTOSCROLL_BAND - drag.pointerY;
  const below = drag.pointerY - (rect.bottom - COLUMN_VIEW_AUTOSCROLL_BAND);
  if (above > 0) return -Math.min(COLUMN_VIEW_AUTOSCROLL_MAX, Math.ceil((above / COLUMN_VIEW_AUTOSCROLL_BAND) * COLUMN_VIEW_AUTOSCROLL_MAX));
  if (below > 0) return Math.min(COLUMN_VIEW_AUTOSCROLL_MAX, Math.ceil((below / COLUMN_VIEW_AUTOSCROLL_BAND) * COLUMN_VIEW_AUTOSCROLL_MAX));
  return 0;
}

function scheduleColumnViewAutoScroll(drag) {
  if (columnViewDragRuntime.frame) return;
  const tick = () => {
    columnViewDragRuntime.frame = 0;
    const current = columnViewDragRuntime.active;
    if (!current || !current.dragging) return;
    const step = columnViewAutoScrollStep(current);
    if (!step) return;
    const before = current.list.scrollTop;
    current.list.scrollTop = before + step;
    if (current.list.scrollTop === before) return;
    // The cards moved under a pointer that did not, so the drop point and the
    // line both have to be recomputed from the same pointer position.
    current.boundary = columnViewDropIndex(current.cards, current.pointerX, current.pointerY);
    paintColumnViewDropIndicator(current);
    columnViewDragRuntime.frame = requestAnimationFrame(tick);
  };
  columnViewDragRuntime.frame = requestAnimationFrame(tick);
}

function paintColumnViewDropIndicator(drag) {
  const node = columnViewDropIndicator();
  const listRect = drag.list.getBoundingClientRect();
  const edgeCard = drag.cards[Math.min(drag.boundary, drag.cards.length - 1)];
  const rect = edgeCard.getBoundingClientRect();
  const atEnd = drag.boundary >= drag.cards.length;
  node.hidden = false;
  node.style.left = `${Math.round(Math.min(Math.max(atEnd ? rect.right : rect.left, listRect.left), listRect.right))}px`;
  // Clamped to the list, so a card scrolled half out of view cannot draw the
  // line across the dialog's own toolbar.
  const top = Math.max(rect.top, listRect.top);
  const bottom = Math.min(rect.bottom, listRect.bottom);
  node.style.top = `${Math.round(top)}px`;
  node.style.height = `${Math.round(Math.max(0, bottom - top))}px`;
}

function onColumnViewPointerUp(event) {
  const drag = columnViewDragRuntime.active;
  if (!drag || (event && event.pointerId !== drag.pointerId)) return;
  finishColumnViewDrag(drag, Boolean(event && event.type === "pointerup"));
}

function finishColumnViewDrag(drag, commit) {
  window.removeEventListener("pointermove", onColumnViewPointerMove);
  window.removeEventListener("pointerup", onColumnViewPointerUp);
  window.removeEventListener("pointercancel", onColumnViewPointerUp);
  if (columnViewDragRuntime.frame) { cancelAnimationFrame(columnViewDragRuntime.frame); columnViewDragRuntime.frame = 0; }
  try { drag.list.releasePointerCapture(drag.pointerId); } catch (_error) { /* already released */ }
  columnViewDragRuntime.active = null;
  document.body.classList.remove("column-view-dragging");
  drag.card.classList.remove("is-column-view-dragging");
  const node = document.getElementById("columnViewDropIndicator");
  if (node) node.hidden = true;
  if (!drag.dragging) return;
  columnViewDragRuntime.suppressClick = true;
  if (!commit) return;
  // The anchor is a VISIBLE card, named by its key. Dropping past the last card
  // means "after that one" rather than "at the end of the permitted order":
  // under a search those are different positions, and the first is the one the
  // reader can see themselves asking for.
  const atEnd = drag.boundary >= drag.cards.length;
  const anchorCard = drag.cards[atEnd ? drag.cards.length - 1 : drag.boundary];
  const anchorKey = anchorCard?.dataset.columnViewItem || "";
  const moved = applyColumnViewDrop(drag.mode, drag.key, anchorKey, atEnd ? "after" : "before");
  if (!moved) return;
  renderClaimsTable();
  renderColumnViewModal();
  showToast(`Moved ${drag.label} to position ${moved.to + 1} of ${moved.total}.`);
}

function renderColumnViewModal() {
  if (!columnViewUi.open) return;
  let modal = document.getElementById("columnViewModal");
  if (!modal) {
    modal = document.createElement("div");
    modal.id = "columnViewModal";
    modal.className = "column-view-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-labelledby", "columnViewTitle");
    document.body.appendChild(modal);
  }
  const mode = normalizeColumnViewMode(state.rowMode || "service");
  // Arranged, not default: this list IS the arrange control, so it has to show
  // the order the grid is actually in.
  const permitted = arrangedPermittedColumns(mode);
  const visibleKeys = selectedColumnKeysForMode(mode, permitted);
  const visibleSet = new Set(visibleKeys);
  const search = String(columnViewUi.search || "").trim().toLowerCase();
  const arrangeLocked = Boolean(search);
  const filteredColumns = permitted.filter((column) => !search || column.label.toLowerCase().includes(search) || column.key.toLowerCase().includes(search));
  const savedViews = normalizeSavedColumnViews(state.savedColumnViews || {})[mode];
  const selectedSavedViewId = columnViewUi.selectedSavedViewId || savedViews[0]?.viewId || "";
  columnViewUi.selectedSavedViewId = selectedSavedViewId;
  const statusText = columnViewApiState.loading
    ? "Syncing account views"
    : (columnViewApiState.error ? "Account sync unavailable; local fallback active" : "");
  modal.innerHTML = `
    <div class="column-view-backdrop" data-column-view-close="1"></div>
    <section class="column-view-window${columnViewUi.saving ? " column-view-window--saving" : ""}">
      <div class="column-view-head">
        <div>
          <h3 id="columnViewTitle">Columns <span>${formatNounCount(visibleKeys.length, "column")} of ${formatNounCount(permitted.length, "column")} visible</span></h3>
          ${statusText ? `<p>${escapeHtml(statusText)}</p>` : ""}
        </div>
        <button type="button" class="ghost-button" data-column-view-close="1">Close</button>
      </div>
      <div class="column-view-toolbar">
        <button type="button" class="ghost-button" data-column-view-biller>Use Biller View</button>
        <button type="button" class="ghost-button" data-column-view-reset-order${hasUserColumnOrder(mode) ? "" : " disabled"}>Reset order</button>
        <button type="button" class="ghost-button" data-column-view-select-all>Select all</button>
        <button type="button" class="ghost-button" data-column-view-clear>Clear all</button>
        <button type="button" class="ghost-button" data-column-view-save-toggle>Save view</button>
        <select data-column-view-saved aria-label="Saved column views">
          ${savedViews.length ? savedViews.map((view) => `<option value="${escapeHtml(view.viewId)}"${view.viewId === selectedSavedViewId ? " selected" : ""}>${escapeHtml(view.label)}</option>`).join("") : `<option value="">No saved views</option>`}
        </select>
        <button type="button" class="ghost-button" data-column-view-use-saved ${selectedSavedViewId ? "" : "disabled"}>Use saved view</button>
        <button type="button" class="ghost-button" data-column-view-delete-saved ${selectedSavedViewId ? "" : "disabled"}>Delete</button>
      </div>
      ${columnViewUi.saving ? `
        <div class="column-view-save-row">
          <input type="text" data-column-view-label maxlength="80" value="${escapeHtml(columnViewUi.label || defaultColumnViewLabel())}" aria-label="Column view name">
          <button type="button" class="primary-button" data-column-view-save>Save</button>
          <button type="button" class="ghost-button" data-column-view-save-cancel>Cancel</button>
        </div>
      ` : ""}
      <input class="column-view-search" type="search" data-column-view-search placeholder="Search columns" value="${escapeHtml(columnViewUi.search || "")}" aria-label="Search columns">
      <p class="column-view-arrange-hint">Drag a column below to move it &mdash; anywhere on the card with a mouse, or by its &#10303; handle on a touch screen. The arrows are the keyboard path. You can also drag a column header in the grid (mouse only). Downloads and the Excel export keep the standard column order.${arrangeLocked ? " While the search is narrowed a card lands beside the card you drop it on; the arrows need the search cleared." : ""}</p>
      <div class="column-view-list">
        ${filteredColumns.length ? filteredColumns.map((column) => {
          const at = permitted.findIndex((item) => item.key === column.key);
          const position = `position ${at + 1} of ${permitted.length}`;
          return `
          <div class="column-view-item" data-column-view-item="${escapeHtml(column.key)}" data-column-view-label="${escapeHtml(column.label)}">
            <span class="column-view-grip" data-column-view-grip aria-hidden="true" title="Drag to move ${escapeHtml(column.label)}">&#10303;</span>
            <label class="column-view-choice">
              <input type="checkbox" data-column-view-key="${escapeHtml(column.key)}" ${visibleSet.has(column.key) ? "checked" : ""}>
              <span>${escapeHtml(column.label)}</span>
            </label>
            <span class="column-view-arrange" role="group" aria-label="Arrange ${escapeHtml(column.label)}">
              <button type="button" class="column-view-move" data-column-move="${escapeHtml(column.key)}" data-column-move-step="-1" data-column-move-label="${escapeHtml(column.label)}"${arrangeLocked || at <= 0 ? " disabled" : ""} aria-label="Move ${escapeHtml(column.label)} left (${position})" title="Move ${escapeHtml(column.label)} left">&#9664;</button>
              <button type="button" class="column-view-move" data-column-move="${escapeHtml(column.key)}" data-column-move-step="1" data-column-move-label="${escapeHtml(column.label)}"${arrangeLocked || at < 0 || at >= permitted.length - 1 ? " disabled" : ""} aria-label="Move ${escapeHtml(column.label)} right (${position})" title="Move ${escapeHtml(column.label)} right">&#9654;</button>
            </span>
          </div>
        `; }).join("") : `<div class="column-view-empty">No columns match that search.</div>`}
      </div>
    </section>
  `;
  wireColumnViewModal(modal);
  const searchInput = modal.querySelector("[data-column-view-search]");
  if (searchInput) {
    searchInput.selectionStart = searchInput.selectionEnd = searchInput.value.length;
  }
}

function wireColumnViewModal(modal) {
  // Re-installed on every render rather than once: renderColumnViewModal
  // rebuilds the dialog's innerHTML, so a listener bound to the old list is
  // bound to a detached node. The window-level move/up listeners are added
  // per drag and removed when it finishes, so nothing accumulates.
  installColumnViewDrag(modal);
  modal.querySelectorAll("[data-column-view-close]").forEach((node) => node.addEventListener("click", closeColumnViewModal));
  modal.querySelector("[data-column-view-search]")?.addEventListener("input", (event) => {
    columnViewUi.search = event.target.value;
    renderColumnViewModal();
    const nextSearch = modal.querySelector("[data-column-view-search]");
    nextSearch?.focus?.({ preventScroll: true });
    if (nextSearch) nextSearch.selectionStart = nextSearch.selectionEnd = nextSearch.value.length;
  });
  modal.querySelectorAll("[data-column-view-key]").forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      const mode = normalizeColumnViewMode(state.rowMode || "service");
      const permitted = permittedColumnsForMode(mode);
      const current = new Set(selectedColumnKeysForMode(mode, permitted));
      if (checkbox.checked) current.add(checkbox.dataset.columnViewKey);
      else current.delete(checkbox.dataset.columnViewKey);
      setColumnSelection(mode, Array.from(current), { clearActiveView: true });
    });
  });
  modal.querySelector("[data-column-view-select-all]")?.addEventListener("click", () => {
    const mode = normalizeColumnViewMode(state.rowMode || "service");
    setColumnSelection(mode, permittedColumnsForMode(mode).map((column) => column.key), { clearActiveView: true });
  });
  modal.querySelector("[data-column-view-clear]")?.addEventListener("click", () => {
    const mode = normalizeColumnViewMode(state.rowMode || "service");
    const fallback = permittedColumnsForMode(mode)[0]?.key;
    setColumnSelection(mode, fallback ? [fallback] : [], { clearActiveView: true });
    showToast("At least one column stays visible.");
  });
  modal.querySelectorAll("[data-column-move]").forEach((button) => {
    button.addEventListener("click", () => {
      const mode = normalizeColumnViewMode(state.rowMode || "service");
      const key = button.dataset.columnMove;
      const step = Number(button.dataset.columnMoveStep);
      const moved = moveUserColumnByStep(mode, key, step);
      if (!moved) return;
      renderClaimsTable();
      renderColumnViewModal();
      // Put the same control back under the caret so a column can be walked
      // several places without hunting for the button after every step. When
      // that control just hit the end of the row and went disabled, focus its
      // partner rather than dropping focus to the document.
      const next = document.getElementById("columnViewModal");
      const same = next?.querySelector(`[data-column-move="${key}"][data-column-move-step="${step}"]:not([disabled])`);
      (same || next?.querySelector(`[data-column-move="${key}"]:not([disabled])`))?.focus?.({ preventScroll: true });
      showToast(`Moved ${button.dataset.columnMoveLabel || key} to position ${moved.to + 1} of ${moved.total}.`);
    });
  });
  modal.querySelector("[data-column-view-reset-order]")?.addEventListener("click", () => {
    const mode = normalizeColumnViewMode(state.rowMode || "service");
    resetUserColumnOrder(mode);
    renderClaimsTable();
    renderColumnViewModal();
    document.getElementById("columnViewModal")?.querySelector("[data-column-view-reset-order]")?.focus?.({ preventScroll: true });
    showToast("Column order reset to the standard layout.");
  });
  modal.querySelector("[data-column-view-biller]")?.addEventListener("click", () => {
    const mode = normalizeColumnViewMode(state.rowMode || "service");
    setColumnSelection(mode, billerColumnViewKeys[mode], { clearActiveView: true });
  });
  modal.querySelector("[data-column-view-save-toggle]")?.addEventListener("click", () => {
    columnViewUi.saving = true;
    columnViewUi.label = columnViewUi.label || defaultColumnViewLabel();
    renderColumnViewModal();
  });
  modal.querySelector("[data-column-view-save-cancel]")?.addEventListener("click", () => {
    columnViewUi.saving = false;
    renderColumnViewModal();
  });
  modal.querySelector("[data-column-view-label]")?.addEventListener("input", (event) => {
    columnViewUi.label = event.target.value;
  });
  modal.querySelector("[data-column-view-save]")?.addEventListener("click", saveCurrentColumnView);
  modal.querySelector("[data-column-view-saved]")?.addEventListener("change", (event) => {
    columnViewUi.selectedSavedViewId = event.target.value;
    renderColumnViewModal();
  });
  modal.querySelector("[data-column-view-use-saved]")?.addEventListener("click", useSelectedSavedColumnView);
  modal.querySelector("[data-column-view-delete-saved]")?.addEventListener("click", deleteSelectedSavedColumnView);
}

function setColumnSelection(mode, keys, { clearActiveView = false } = {}) {
  const normalizedMode = normalizeColumnViewMode(mode);
  const permitted = permittedColumnsForMode(normalizedMode);
  const permittedKeys = new Set(permitted.map((column) => column.key));
  const selected = normalizeColumnKeyList(keys).filter((key) => permittedKeys.has(key));
  state.columnViewSelections = normalizeColumnViewSelections(state.columnViewSelections || {});
  state.columnViewSelections[normalizedMode] = selected.length ? selected : permitted.slice(0, 1).map((column) => column.key);
  state.activeColumnViewIds = normalizeActiveColumnViewIds(state.activeColumnViewIds || {});
  if (clearActiveView) state.activeColumnViewIds[normalizedMode] = "";
  resetTrackerPage();
  saveState();
  renderClaimsTable();
  if (columnViewUi.open) renderColumnViewModal();
}

function applySavedColumnViews(mode, views = []) {
  state.savedColumnViews = normalizeSavedColumnViews({
    ...(state.savedColumnViews || {}),
    [normalizeColumnViewMode(mode)]: views
  });
}

function upsertLocalSavedColumnView(view) {
  const normalized = normalizeSavedColumnView(view, view.rowMode || state.rowMode);
  if (!normalized) return null;
  state.savedColumnViews = normalizeSavedColumnViews(state.savedColumnViews || {});
  const rows = state.savedColumnViews[normalized.rowMode].filter((item) => item.viewId !== normalized.viewId);
  state.savedColumnViews[normalized.rowMode] = [normalized, ...rows].slice(0, 50);
  return normalized;
}

function removeLocalSavedColumnView(mode, viewId) {
  const normalizedMode = normalizeColumnViewMode(mode);
  state.savedColumnViews = normalizeSavedColumnViews(state.savedColumnViews || {});
  state.savedColumnViews[normalizedMode] = state.savedColumnViews[normalizedMode].filter((view) => view.viewId !== viewId);
  state.activeColumnViewIds = normalizeActiveColumnViewIds(state.activeColumnViewIds || {});
  if (state.activeColumnViewIds[normalizedMode] === viewId) state.activeColumnViewIds[normalizedMode] = "";
}

function columnViewApiBase() {
  return (workflowApiBase() || trackerApiBase() || "").replace(/\/$/, "");
}

function columnViewApiToken() {
  return workflowApiToken() || trackerApiToken();
}

function columnViewApiEnabled() {
  return Boolean(columnViewApiBase() && columnViewApiToken() && typeof fetch === "function");
}

// THE MODE ON SCREEN, NOT BOTH MODES.
//
// Every caller of this - the grid render and the Columns modal - works in ONE
// row mode, the one in state.rowMode, and the modal reads
// normalizeColumnViewMode(state.rowMode) for its whole list. Loading
// columnViewModes wholesale meant every boot spent two GET /api/user/column-views
// round trips on the single API worker for a screen that could only use one of
// them (perf/ALL_DATA_LATENCY_2026-09-22.md records the doubled read). Switching
// Rows/Groups re-renders the grid, which calls this again and loads the other
// mode then - at the moment it is first needed and never before.
function ensureColumnViewsLoaded(modes = [normalizeColumnViewMode((typeof state !== "undefined" && state && state.rowMode) || "service")]) {
  if (!columnViewApiEnabled()) return;
  columnViewApiState.loadedModes = columnViewApiState.loadedModes || {};
  columnViewApiState.loadingModes = columnViewApiState.loadingModes || {};
  const wanted = (Array.isArray(modes) ? modes : [modes])
    .map(normalizeColumnViewMode)
    .filter((mode, index, list) => list.indexOf(mode) === index)
    .filter((mode) => !columnViewApiState.loadedModes[mode] && !columnViewApiState.loadingModes[mode]);
  if (!wanted.length) return;
  wanted.forEach((mode) => { columnViewApiState.loadingModes[mode] = true; });
  columnViewApiState.loading = true;
  Promise.all(wanted.map(async (mode) => {
    await loadColumnViewsForMode(mode);
    columnViewApiState.loadedModes[mode] = true;
  }))
    .then(() => {
      columnViewApiState.loaded = columnViewModes.every((mode) => columnViewApiState.loadedModes[mode]);
      columnViewApiState.status = "ready";
      columnViewApiState.error = "";
      saveState();
      if (columnViewUi.open) renderColumnViewModal();
    })
    .catch((error) => {
      columnViewApiState.status = "fallback";
      columnViewApiState.error = error.message || "Account column views unavailable.";
      if (columnViewUi.open) renderColumnViewModal();
    })
    .finally(() => {
      wanted.forEach((mode) => { delete columnViewApiState.loadingModes[mode]; });
      columnViewApiState.loading = Object.keys(columnViewApiState.loadingModes).length > 0;
      if (columnViewUi.open) renderColumnViewModal();
    });
}

async function loadColumnViewsForMode(mode) {
  const response = await fetch(`${columnViewApiBase()}/api/user/column-views?rowMode=${encodeURIComponent(mode)}`, {
    method: "GET",
    cache: "no-store",
    headers: { authorization: `Bearer ${columnViewApiToken()}` }
  });
  if (response.status === 401) {
    if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    throw new Error("Your session expired. Sign in again — this page will still be here.");
  }
  if (!response.ok) throw new Error(`Column views failed to load (${response.status})`);
  const payload = await response.json();
  applySavedColumnViews(mode, Array.isArray(payload.views) ? payload.views : []);
}

async function saveCurrentColumnView() {
  const mode = normalizeColumnViewMode(state.rowMode || "service");
  const permitted = permittedColumnsForMode(mode);
  const label = String(columnViewUi.label || defaultColumnViewLabel()).trim().replace(/\s+/g, " ");
  if (!label) {
    showToast("Name the column view before saving.");
    return;
  }
  const columnKeys = selectedColumnKeysForMode(mode, permitted);
  const view = {
    viewId: `${mode}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 42) || "view"}-${Date.now()}`,
    label,
    rowMode: mode,
    columnKeys,
    createdAtUtc: new Date().toISOString(),
    updatedAtUtc: new Date().toISOString()
  };
  let saved = view;
  let sessionExpired = false;
  if (columnViewApiEnabled()) {
    try {
      const response = await fetch(`${columnViewApiBase()}/api/user/column-views`, {
        method: "POST",
        cache: "no-store",
        headers: {
          authorization: `Bearer ${columnViewApiToken()}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({ label, rowMode: mode, columnKeys })
      });
      const payload = await response.json().catch(() => ({}));
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        sessionExpired = true;
        throw new Error("Your session expired. Sign in again — this page will still be here.");
      }
      if (!response.ok) throw new Error(payload.message || `Column view save failed (${response.status})`);
      saved = payload.view || view;
      columnViewApiState.status = "ready";
      columnViewApiState.error = "";
    } catch (error) {
      columnViewApiState.status = "fallback";
      columnViewApiState.error = error.message || "Account save unavailable.";
    }
  }
  if (sessionExpired) {
    columnViewUi.saving = false;
    renderColumnViewModal();
    return;
  }
  const normalized = upsertLocalSavedColumnView(saved);
  if (normalized) {
    state.activeColumnViewIds = normalizeActiveColumnViewIds(state.activeColumnViewIds || {});
    state.activeColumnViewIds[mode] = normalized.viewId;
    columnViewUi.selectedSavedViewId = normalized.viewId;
  }
  columnViewUi.saving = false;
  saveState();
  renderColumnViewModal();
  showToast(columnViewApiState.error ? "Saved locally; account sync is unavailable." : "Column view saved.");
}

function useSelectedSavedColumnView() {
  const mode = normalizeColumnViewMode(state.rowMode || "service");
  const viewId = columnViewUi.selectedSavedViewId;
  const view = normalizeSavedColumnViews(state.savedColumnViews || {})[mode].find((item) => item.viewId === viewId);
  if (!view) return;
  state.activeColumnViewIds = normalizeActiveColumnViewIds(state.activeColumnViewIds || {});
  state.activeColumnViewIds[mode] = view.viewId;
  setColumnSelection(mode, view.columnKeys, { clearActiveView: false });
  showToast(`Column view: ${view.label}`);
}

async function deleteSelectedSavedColumnView() {
  const mode = normalizeColumnViewMode(state.rowMode || "service");
  const viewId = columnViewUi.selectedSavedViewId;
  if (!viewId) return;
  let sessionExpired = false;
  if (columnViewApiEnabled()) {
    try {
      const response = await fetch(`${columnViewApiBase()}/api/user/column-views/${encodeURIComponent(viewId)}`, {
        method: "DE" + "LETE",
        cache: "no-store",
        headers: { authorization: `Bearer ${columnViewApiToken()}` }
      });
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        sessionExpired = true;
        throw new Error("Your session expired. Sign in again — this page will still be here.");
      }
      if (!response.ok) throw new Error(`Column view delete failed (${response.status})`);
      columnViewApiState.error = "";
    } catch (error) {
      columnViewApiState.status = "fallback";
      columnViewApiState.error = error.message || "Account delete unavailable.";
    }
  }
  if (sessionExpired) {
    renderColumnViewModal();
    return;
  }
  removeLocalSavedColumnView(mode, viewId);
  columnViewUi.selectedSavedViewId = "";
  saveState();
  renderColumnViewModal();
  showToast(columnViewApiState.error ? "Removed locally; account sync is unavailable." : "Column view deleted.");
}

function serviceColumnOnly(key) {
  return new Set(serviceColumns.map((column) => column.key)).has(key) && !new Set(claimColumns.map((column) => column.key)).has(key);
}

function getColumnValue(key, claim, service = null) {
  const statusProfile = buildClaimStatusProfile(claim);
  const claimFinancial = claimFinancialView(claim);
  const serviceFinancial = service ? serviceFinancialView(service, claim) : null;
  const ownerValue = claim.assignedTo || "Unassigned";
  const firstPayments = Array.isArray(claim.payments) ? claim.payments.slice(0, 3) : [];
  const paymentAt = (index, field) => firstPayments[index]?.[field];
  const unitsConverted = service?.units ? service.units * 15 / 60 : 0;
  const revenuePerHour = unitsConverted && service?.expected ? service.expected / unitsConverted : 0;
  const writeOff = serviceFinancial?.writeOffAmount ?? claimFinancial.writeOffAmount ?? "";
  const rowFinancial = serviceFinancial || claimFinancial;
  const score = workPriorityScore(claim);
  const values = {
    claimId: claim.claimId,
    priority: score ? `${score} ${statusProfile.priorityBand}` : "0 Monitor",
    lifecycleStage: statusProfile.lifecycleLabel,
    statusCategory: statusProfile.statusCategory,
    queueReason: statusProfile.workReason,
    recommendedNextAction: statusProfile.recommendedNextAction,
    calculationGuards: calculationGuardSummary(evaluateClaimCalculationGuards(claim, service)),
    orgId: claim.orgId || "N/A",
    claimStatus: claim.claimStatus || "Open",
    claimClassification: claim.claimClassification || "N/A",
    serviceId: service?.serviceId || "None",
    patient: claim.patient || "N/A",
    renderingProvider: claim.renderingProvider || "Unknown",
    client: claim.client || "Unknown",
    facility: claim.facility || "Unknown",
    place: claim.place || "N/A",
    primaryPayer: claim.primaryPayer || "None",
    respPartyName: claim.respPartyName || "N/A",
    secondaryPayer: claim.secondaryPayer || "None",
    action: (function () {
      const overlay = service ? state.followUpByService?.[followUpKey(claim.claimId, service.serviceId)] : null;
      return overlay && Object.prototype.hasOwnProperty.call(overlay, "action")
        ? followUpFilterValue(overlay.action)
        : followUpFilterRowValue(claim, service);
    }()),
    followUpDate: (function () {
      const line = service
        ? firstPresent(
          (state.followUpByService?.[followUpKey(claim.claimId, service.serviceId)] || {}).followUpDate,
          service.followUpDate,
          claim.followUpDate
        )
        : firstPresent(claim.followUpDate);
      return line ? formatDate(line) : "None";
    }()),
    match: serviceFinancial?.finalizedMatch || claim.match || "Blank",
    aging: claim.aging || "Blank",
    totalExpected: money(serviceFinancial?.finalInsuranceExpected ?? rowFinancial.expectedReimbursement),
    totalPaid: money(serviceFinancial?.insurancePaid ?? claimFinancial.totalInsurancePaid),
    owed: money(serviceFinancial?.finalInsuranceBalance ?? rowFinancial.sourceOutstanding),
    primaryExpectedTotal: money(serviceFinancial?.primaryExpected || claimFinancial.primaryExpected || 0),
    secondaryExpectedTotal: money(serviceFinancial?.secondaryExpected || claimFinancial.secondaryExpected || 0),
    totalInsAmtPaid: money(serviceFinancial?.insurancePaid || 0),
    totalPatAmtPaid: money(service?.totalPatAmtPaid || service?.patientPaid || 0),
    totalPatBal: money(Math.max(0, moneyNumber(service?.patientBalance || 0))),
    prCodes: prCodes(service),
    truePatientResponsibility: money(serviceFinancial?.patientResponsibility || 0),
    payerResponsibility: money(serviceFinancial?.payerResponsibility ?? claimFinancial.payerResponsibility),
    patientResponsibility: money(claimFinancial.patientResponsibility),
    underpayment: money(serviceFinancial?.underpayment ?? claimFinancial.underpayment),
    overpayment: money(serviceFinancial?.overpayment ?? claimFinancial.overpayment),
    patientResponsibilityStatus: patientResponsibilityStatus(service, claim),
    statementStatus: statementStatus(service, claim),
    positiveTotalPatBal: money(serviceFinancial?.positiveTotalPatientBalance || 0),
    balance: money(serviceFinancial?.finalInsuranceBalance ?? rowFinancial.sourceOutstanding),
    charges: money(serviceFinancial?.charges || service?.expected || 0),
    adjs: money(service?.adjs || 0),
    primaryRate: serviceFinancial ? money(serviceFinancial.primaryRate || (service?.units ? (service.expected || 0) / service.units : 0)) : "N/A",
    secondaryRate: serviceFinancial ? money(serviceFinancial.secondaryRate || 0) : "N/A",
    secondaryCalc: serviceFinancial?.secondaryCalc || "N/A",
    originalBillDate: claim.originalBillDate ? formatDate(claim.originalBillDate) : (claim.billDateAging == null ? billDateBlankText(claim) : "N/A"),
    billDateAging: String(claim.billDateAging ?? (claim.originalBillDate ? "N/A" : billDateBlankText(claim))),
    addCode: [claim.primaryPayer, service?.procedure, service?.m1, service?.m2].filter(Boolean).join(" | ") || "N/A",
    // The LOCAL/demo render path. Same bucket, same three labels as the live
    // grid cell, the slicer, the export and Reports - and no "N/A" fallback,
    // because there is no missing case: a line with no matching contract is
    // Unknown, which is an answer rather than an absence.
    network: networkBucket(firstPresent(service?.contractNetwork, claim.contractNetwork, service?.network, claim.network)),
    writeOffAmount: writeOff === "" ? "" : money(writeOff),
    bcba: claim.bcba || "N/A",
    rbt: claim.rbt || claim.renderingProvider || "N/A",
    // No service-id fallback — see the trackerApiCellRawValue note above. "N/A" is
    // the honest answer for a line EZClaim has no billing entry for.
    ehrBillingEntryId: service?.ehrBillingEntryId || "N/A",
    patClassification: claim.patClassification || "N/A",
    time: String(claim.billDateAging ?? "N/A"),
    weekEnding: service?.dos ? formatDate(endOfWeek(service.dos)) : "N/A",
    payrollRate: claim.payrollRate ? money(claim.payrollRate) : "N/A",
    claimDue: isPastDue(claim) ? "PAST DUE" : "",
    unitsConverted: unitsConverted ? String(unitsConverted) : "N/A",
    revenuePerHour: revenuePerHour ? money(revenuePerHour) : "N/A",
    profitPerHour: "N/A",
    // The SOURCE note: the service line's own text, never the claim-note ledger.
    sourceServiceLineNotes: firstPresent(service?.sourceServiceLineNotes, service?.serviceLineNotes, claim.sourceServiceLineNotes, ""),
    serviceLineNotes: firstPresent(claim.latestClaimNoteText, claim.claimNoteSummary?.text, claim.claimNoteSummary?.latestText, service?.latestClaimNoteText, service?.latestClaimNote, claim.notes?.at(-1)?.text, "N/A"),
    // Notes: the newer of the two above, by the one merge rule both lanes use.
    mergedNotes: mergedNoteCellModel(mergedNoteInputsFromClaim(claim, service)).text || "N/A",
    notes: claim.notes?.at(-1)?.text || "",
    paymentStatus: claim.paymentAttribution?.status || "No attribution",
    assignedTo: ownerValue,
    assignmentStatus: claim.assignmentStatus || "Unassigned",
    dos: service?.dos ? formatDate(service.dos) : "None",
    procedure: service?.procedure || "None",
    m1: service?.m1 || "",
    m2: service?.m2 || "",
    mods: service ? [service.m1, service.m2].filter(Boolean).join(" / ") || "None" : "None",
    units: service ? String(service.units) : "None",
    pmtSource1: paymentAt(0, "source") || "",
    pmtDate1: paymentAt(0, "date") ? formatDate(paymentAt(0, "date")) : "",
    ref1: paymentAt(0, "ref") || "",
    applied1: paymentAt(0, "applied") ? money(paymentAt(0, "applied")) : "",
    pmtSource2: paymentAt(1, "source") || "",
    pmtDate2: paymentAt(1, "date") ? formatDate(paymentAt(1, "date")) : "",
    ref2: paymentAt(1, "ref") || "",
    applied2: paymentAt(1, "applied") ? money(paymentAt(1, "applied")) : "",
    pmtSource3: paymentAt(2, "source") || "",
    pmtDate3: paymentAt(2, "date") ? formatDate(paymentAt(2, "date")) : "",
    ref3: paymentAt(2, "ref") || "",
    applied3: paymentAt(2, "applied") ? money(paymentAt(2, "applied")) : "",
    refreshTime: "Local preview"
  };
  // NEVER SHOW A CONFIDENT WRONG NUMBER. A line with no rate on file has no
  // knowable expected amount, so every rate-derived money column reads as an
  // explicit unknown rather than $0 - in the grid, in the workbook export and
  // in the column-filter facets, which all read through here.
  if (unknownMoneyReason(key, rowFinancial || {})) return MONEY_UNKNOWN_TEXT;
  if (moneyIsEstimated(key, rowFinancial || {}) && values[key] != null) return String(values[key]) + " (Estimated)";
  return values[key] ?? "Blank";
}

function smartValueSort(a, b) {
  const moneyA = moneyToNumber(a);
  const moneyB = moneyToNumber(b);
  if (Number.isFinite(moneyA) && Number.isFinite(moneyB)) return moneyB - moneyA;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function moneyToNumber(value) {
  const text = String(value ?? "").trim();
  if (!/^-?\$|^\(\$/.test(text)) return NaN;
  const number = parseMoneyNumber(text);
  return Number.isFinite(number) ? number : NaN;
}

// UI-100 #19: comparable raw value per column key for click-to-sort.
function getClaimSortValue(claim, service, key) {
  const fin = claimFinancialView(claim);
  const sFin = service ? serviceFinancialView(service, claim) : null;
  switch (key) {
    case "totalExpected": return fin.expectedReimbursement || 0;
    case "totalPaid": return fin.totalInsurancePaid || 0;
    // "Total Ins Owed" displays finalInsuranceBalance/sourceOutstanding
    // (getColumnValue), so click-to-sort must rank by the same number — not
    // expected reimbursement.
    case "owed": return sFin?.finalInsuranceBalance ?? fin.sourceOutstanding ?? 0;
    case "balance": return sFin?.finalInsuranceBalance ?? fin.sourceOutstanding ?? 0;
    case "charges": return sFin?.charges || service?.expected || 0;
    case "adjs": return service?.adjs || 0;
    case "billDateAging":
    case "time": return Number(claim.billDateAging) || 0;
    case "aging": {
      return agingBucketOrdinal(claim.aging);
    }
    case "followUpDate": return dateOnlySortValue(claim.followUpDate);
    case "originalBillDate": return dateOnlySortValue(claim.originalBillDate);
    case "priority": return workPriorityScore(claim);
    default: {
      const display = String(getColumnValue(key, claim, service) ?? "").replace(/ \(Estimated\)$/, "");
      // If it looks like money, parse to number for numeric sort.
      if (typeof display === "string" && /^-?\$/.test(display)) {
        const n = moneyToNumber(display);
        if (Number.isFinite(n)) return n;
      }
      return String(display ?? "").toLowerCase();
    }
  }
}

function sortClaims(a, b) {
  // UI-100 #19: user-chosen column sort takes precedence over the default priority sort.
  const us = (typeof state !== "undefined" && state && state.tableSort) || null;
  if (us && us.column) {
    const av = getClaimSortValue(a, null, us.column);
    const bv = getClaimSortValue(b, null, us.column);
    let cmp;
    if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
    if (cmp !== 0) return us.dir === "desc" ? -cmp : cmp;
    // tie-break with priority so equal values stay deterministic
  }
  const priorityDelta = workPriorityScore(b) - workPriorityScore(a);
  if (priorityDelta) return priorityDelta;
  if (isPastDue(a) !== isPastDue(b)) return isPastDue(a) ? -1 : 1;
  const agingRank = (claim) => {
    const ordinal = agingBucketOrdinal(claim.aging);
    return ordinal < 0 ? 9 : CT_AR_AGING_BUCKET_KEYS.length - 1 - ordinal;
  };
  return agingRank(a) - agingRank(b)
    || claimFinancialView(b).sourceOutstanding - claimFinancialView(a).sourceOutstanding;
}

function toggleClaimSelection(claimId, checked, orgId = "", serviceId = "") {
  const selected = new Set(state.selectedClaims);
  const normalizedClaimId = normalizeClaimId(claimId);
  const normalizedServiceId = String(serviceId || "").trim();
  const normalizedOrgId = String(orgId || "").trim();
  state.selectedClaimTargets = { ...(state.selectedClaimTargets || {}) };
  state.selectedServiceTargets = { ...(state.selectedServiceTargets || {}) };
  // Follow-up bulk ops act per service line: track the (claim, service) pair
  // separately so a claim with two selected lines enqueues two per-line
  // follow-ups, not one claim-wide write. Claim-level bulk ops (assign) still
  // read selectedClaims/selectedClaimTargets.
  const serviceKey = serviceSelectionKey(normalizedOrgId, normalizedClaimId, normalizedServiceId);
  if (checked && normalizedClaimId) {
    selected.add(normalizedClaimId);
    state.selectedClaimTargets[normalizedClaimId] = { orgId: normalizedOrgId };
    state.selectedServiceTargets[serviceKey] = { orgId: normalizedOrgId, serviceId: normalizedServiceId, claimId: normalizedClaimId };
  } else {
    delete state.selectedServiceTargets[serviceKey];
    const remainingForClaim = Object.values(state.selectedServiceTargets)
      .some((target) => normalizeClaimId(target?.claimId) === normalizedClaimId);
    if (!remainingForClaim) {
      selected.delete(normalizedClaimId);
      delete state.selectedClaimTargets[normalizedClaimId];
    }
  }
  state.selectedClaims = Array.from(selected);
  saveState();
  renderClaimsTable({ selectionOnly: true });
}

function workflowTargetsForRenderedRows() {
  const targets = {};
  Array.from(document.querySelectorAll(".claim-select")).forEach((checkbox) => {
    const claimId = normalizeClaimId(checkbox.dataset.claimId || "");
    if (!claimId) return;
    targets[claimId] = { orgId: String(checkbox.dataset.claimOrgId || "").trim() };
  });
  return targets;
}

// Per-(claim, service-line) selection targets for the currently rendered rows,
// keyed by followUpKey. Used by "select all visible" so follow-up bulk ops fan
// onto exactly the visible service lines, not every line of each claim.
function serviceTargetsForRenderedRows() {
  const targets = {};
  Array.from(document.querySelectorAll(".claim-select")).forEach((checkbox) => {
    const claimId = normalizeClaimId(checkbox.dataset.claimId || "");
    if (!claimId) return;
    const serviceId = String(checkbox.dataset.serviceId || "").trim();
    const orgId = String(checkbox.dataset.claimOrgId || "").trim();
    targets[serviceSelectionKey(orgId, claimId, serviceId)] = { orgId, serviceId, claimId };
  });
  return targets;
}

function canDeleteAssignmentTasks() {
  return hasPermission(PERMISSIONS.ASSIGN_CLAIMS) && canonicalRole(state.role) !== "OffshoreBiller";
}

function deleteAssignmentTask(claimId, options = {}) {
  if (!canDeleteAssignmentTasks()) return denyAction(PERMISSIONS.ASSIGN_CLAIMS, "delete assignment task");
  const claim = requireClaimPermission(claimId, PERMISSIONS.ASSIGN_CLAIMS, "delete assignment task");
  if (!claim) return false;
  updateWorkflow(claimId, {
    assignedTo: "",
    assignedBy: "",
    assignmentDue: "",
    assignmentStatus: "Unassigned",
    assignmentInstruction: "",
    instructionBy: "",
    instructionAt: "",
    bounceReason: "",
    bounceBy: "",
    bounceAt: "",
    reviewNote: ""
  });
  enqueueWorkflowCommand("assign", {
    claimId,
    ...workflowTargetFieldsForClaim(claimId, options),
    assignedTo: "",
    assignmentDue: "",
    assignmentStatus: "Unassigned"
  });
  audit(`${claimId}: assignment task deleted`, activeInternalUser());
  saveState();
  render();
  showToast("Task deleted and returned to Unassigned.");
  return true;
}

// --- Bulk follow-up type-to-confirm guard ---------------------------------
// Owner call 2026-08-10: "We need to select how many rows we're adding
// follow-ups to if it's more than 3 ... you have to type in the number ...
// if it's not correct, then it shows 'not correct'."
//
// CT-TRACKER (workflow_app_prototype.py: BULK_CONFIRM_ROWS = 3 /
// CLEAR_CONFIRM_ROWS = 2) gates an apply at THREE OR MORE. The owner asked
// for MORE THAN three, and the newer instruction wins on the apply path -
// see CT_TRACKER_RULES_LEDGER.md section 1 for the superseded >=3 decision.
// The clear path keeps the stricter >=2 gate (a clear destroys work that is
// already there) and the >100 hard block is untouched.
const BULK_FOLLOW_UP_CONFIRM_MIN_ROWS = 4;
const BULK_FOLLOW_UP_CLEAR_CONFIRM_MIN_ROWS = 2;
const BULK_FOLLOW_UP_MAX_ROWS = 100;

function followUpDateOnLine(claimId, serviceId, orgId = "") {
  const prepared=preparedDrawerService(claimId,serviceId,orgId);
  if (prepared) return normalizeDateValue(prepared.followUpDate) || "";
  const normalizedClaimId = String(claimId || "").trim();
  const normalizedServiceId = String(serviceId || "").trim();
  const apiRow = (trackerApiPageState.response?.rows || []).find((row) => (
    trackerApiClaimId(row) === normalizedClaimId
    && String(row.serviceId || row.service_id || "").trim() === normalizedServiceId
    && (!orgId || String(row.orgId || row.org_id || "").trim() === String(orgId).trim())
  ));
  if (apiRow) return normalizeDateValue(trackerApiCellRawValue(apiRow, "followUpDate")) || "";
  if (orgId) return "";
  const claim = getClaim(normalizedClaimId);
  const service = (claim?.services || []).find((row) => String(row.serviceId || "").trim() === normalizedServiceId);
  const overlay = state.followUpByService?.[followUpKey(normalizedClaimId, normalizedServiceId)] || {};
  return normalizeDateValue(firstPresent(overlay.followUpDate, service?.followUpDate, claim?.followUpDate)) || "";
}

function serviceTargetSelected(orgId, claimId, serviceId) {
  const normalizedOrgId = String(orgId || "").trim();
  const normalizedClaimId = normalizeClaimId(claimId);
  const normalizedServiceId = String(serviceId || "").trim();
  const targetsForClaim = Object.values(state.selectedServiceTargets || {})
    .filter((target) => normalizeClaimId(target?.claimId) === normalizedClaimId);
  if (targetsForClaim.length) {
    return targetsForClaim.some((target) => String(target?.orgId || "").trim() === normalizedOrgId
      && String(target.serviceId || "").trim() === normalizedServiceId);
  }
  return state.selectedClaims.includes(normalizedClaimId) && String(state.selectedClaimTargets?.[normalizedClaimId]?.orgId || "").trim() === normalizedOrgId;
}

// THE CLAIM A FOLLOW-UP IS AUTHORIZED AGAINST, WHEN THE LINE IS NOT ON THE
// RENDERED ALL DATA PAGE.
//
// getClaim answers from three places: the demo store (which is EMPTY on the live
// tracker-API lane), the rows of the CURRENTLY RENDERED grid page, and the live
// workflow boards. A queue surface works lines that are by construction not on
// that page - Status checks loads the whole scoped queue while the grid shows one
// page - so getClaim returned null, requireClaimPermission refused EVERY save,
// the editor never opened, and each click wrote a false "Authorization denied"
// row into the local audit. The same held for a claim drawer opened from such a
// queue, and for any drawer reached from My Work, a notification or a link.
//
// This extends how the claim is RESOLVED. It does not weaken what is CHECKED:
// canActOnClaim still runs, against a real record wherever one exists.
//   1. the open drawer matching both the claim and explicitly requested company;
//   2. a cached claim belonging to the explicitly requested company;
//   3. the tenant facts the calling surface pinned from its own authorized read.
//
// (3) is FAIL-CLOSED. A field the surface cannot evidence stays empty, and an
// empty field can only make canAccessClaim REFUSE, never grant: the payer test
// needs a match, the provider test needs a match, assignedOnly needs the name.
// So a pinned record can lose an edit that a fully-hydrated one would allow, and
// can never win one it would not. No evidence at all is still a refusal.
function followUpScopeClaim(claimId, options = {}) {
  const normalizedId = normalizeClaimId(claimId);
  const orgId = String(options.orgId || "").trim();
  const drawer = activeClaimDrawerContext;
  if (orgId && drawer?.claim && normalizeClaimId(drawer.claimId) === normalizedId
    && normalizeClaimId(drawer.claim.claimId) === normalizedId
    && String(drawer.orgId || drawer.claim.orgId || "").trim() === orgId
    && String(drawer.claim.orgId || "").trim() === orgId) return drawer.claim;
  const cached = getClaim(claimId);
  if (cached && (!orgId || String(cached.orgId || "").trim() === orgId)) return cached;
  if (!normalizedId || !orgId) return null;
  const pinned = options.scopeClaim;
  if (!pinned || typeof pinned !== "object" || String(pinned.orgId || "").trim() !== orgId) return null;
  return { ...pinned, claimId: normalizedId, orgId };
}

// The permission gate for one service line's follow-up. Identical to
// requireClaimPermission whenever the claim is cached; for a line resolved by
// pin it adds the company-selection guard openDrawer already applies, so a
// biller working inside one selected company cannot act on another company's
// line from a queue. Existing callers never reach that branch.
function requireFollowUpLinePermission(claimId, options = {}) {
  const claim = followUpScopeClaim(claimId, options);
  if (!canActOnClaim(claim, PERMISSIONS.UPDATE_FOLLOW_UP)) return denyAction(PERMISSIONS.UPDATE_FOLLOW_UP, "edit follow-up");
  if (activeClientGroupTenant() && !activeClientGroupOrgIds().includes(String(claim.orgId || "").trim())) {
    showToast("Choose this claim\u2019s company at the top of the page before changing its follow-up.");
    return false;
  }
  return claim;
}

function openFollowUpEditor(claimId, options = {}) {
  if (!requireFollowUpLinePermission(claimId, options)) return;
  const serviceId = String(options.serviceId || "").trim();
  if (!serviceId) return showToast("Choose a service line before updating follow-up.");
  const orgId = String(options.orgId || workflowTargetFieldsForClaim(claimId, options).orgId || "").trim();
  const currentAction = options.currentAction === undefined ? followUpActionOnLine(claimId, serviceId, orgId) : String(options.currentAction || "");
  const sourceLine = preparedDrawerService(claimId, serviceId, orgId) || (trackerApiPageState.response?.rows || []).find(row =>
    trackerApiClaimId(row) === String(claimId) && String(row.serviceId || row.service_id || "") === serviceId && String(row.orgId || row.org_id || "") === orgId);
  const pendingDraft = sourceLine?.followUpDraft;
  const requestedAction = options.action === undefined ? (pendingDraft?.action ?? currentAction) : String(options.action || "");
  let followUpDate = options.followUpDate === undefined ? (pendingDraft?.followUpDate ?? followUpDateOnLine(claimId, serviceId, orgId)) : String(options.followUpDate || "");
  if (!pendingDraft && requestedAction && requestedAction !== "None" && requestedAction !== currentAction && options.followUpDate === undefined) {
    followUpDate = defaultFollowUpDateForAction(requestedAction, followUpDate);
  }
  if (!requestedAction || requestedAction === "None") followUpDate = "";
  followUpEditorState = {
    claimId: String(claimId || ""),
    orgId,
    serviceId,
    action: requestedAction || "None",
    followUpDate,
    sourceSurface: String(options.sourceSurface || "all-data-inline"),
    // The line editor visibly checks this radio on open. Keep the same default
    // in state so keyboard/test DOMs and the browser submit the identical scope.
    noteScope: "service_lines",
    returnFocus: options.returnFocus || document.activeElement,
    // PINNED LINE FACTS, FOR CALLERS WHOSE LINE IS NOT ON THE GRID PAGE.
    //
    // followUpActionOnLine and followUpVersionOnLine both resolve off the rendered
    // grid page, the open drawer, or the demo store. A queue surface (Status checks
    // loads the whole scoped queue, the grid shows one page) usually holds neither,
    // so both answer blank - and blank is not harmless: oversightDisposition RECURSES
    // on the current action, so a blank one silently UNDER-GATES a restricted or
    // approval-gated incumbent; and a blank expectedUpdatedAt against a line that
    // already carries an overlay is unconditionally stale, so the save is a
    // guaranteed 409. A caller holding the authoritative values from its own payload
    // pins them here. Every existing caller passes neither and is bit-for-bit
    // unchanged: `undefined` keeps the lookup, and the pinned version stays "".
    pinnedCurrentAction: options.currentAction === undefined ? null : String(options.currentAction || ""),
    pinnedVersion: String(options.expectedUpdatedAt === undefined ? followUpVersionOnLine(claimId, serviceId, orgId) : options.expectedUpdatedAt || "")
  };
  const restrictedCurrent = followUpActionIsRestrictedCurrent(currentAction);
  followUpEditorState.restrictedCurrent = restrictedCurrent;
  if (els.followUpEditorAction) {
    els.followUpEditorAction.innerHTML = followUpActionOptionsHtml(requestedAction === "None" ? "" : requestedAction, { clearValue: "None", clearLabel: "— Clear follow-up" });
  }
  setDisabled(els.followUpEditorAction, restrictedCurrent);
  setDisabled(els.followUpEditorDate, restrictedCurrent);
  setDisabled(els.followUpEditorSubmitBtn, restrictedCurrent);
  setValue(els.followUpEditorDate, followUpDate);
  const draftNote = String(followUpEditorDrafts[followUpKey(claimId, serviceId)] || "");
  setValue(els.followUpEditorNote, draftNote);
  setText(els.followUpEditorNoteCount, String(draftNote.length));
  Array.from(document.querySelectorAll("input[name='followUpEditorNoteScope']")).forEach((radio) => { radio.checked = radio.value === "service_lines"; });
  setText(els.followUpEditorStatus, restrictedCurrent ? "Restricted action — ask a supervisor to change or clear this action and its due date." : "");
  setText(els.followUpEditorContext, `Claim ${claimId} · service line ${serviceId}. The action stays on this line; choose whether the note stays here or applies to the entire claim.`);
  els.followUpEditorModal?.classList.remove("hidden");
  syncOversightFollowUpEditor();
  if (els.followUpEditorAction) els.followUpEditorAction.onchange = syncOversightFollowUpEditor;
  activateForegroundModal(els.followUpEditorModal, {
    initialFocus: els.followUpEditorNote,
    onEscape: closeFollowUpEditor,
    returnFocus: followUpEditorState.returnFocus
  });
}

// `{ discardDraft: true }` is passed ONLY by a save that actually landed. Every
// other exit - the close button, the overlay, a refusal the biller walks away
// from - keeps the note, because none of those mean the work is done.
//
// EVENT GUARD, same hazard as openFeedbackModal: this is bound directly with
// addEventListener("click", closeFollowUpEditor), so the first argument is
// usually a click Event. An Event has no `discardDraft`, so the read below is
// false for it - which is exactly right, a close button must keep the draft.
function closeFollowUpEditor(options) {
  const editor = followUpEditorState;
  const discardDraft = Boolean(options && typeof options === "object" && options.discardDraft === true);
  if (editor) {
    const key = followUpKey(editor.claimId, editor.serviceId);
    const typed = String(getValue(els.followUpEditorNote) || "");
    if (discardDraft || !typed.trim()) delete followUpEditorDrafts[key];
    else followUpEditorDrafts[key] = typed;
  }
  followUpEditorState = null;
  els.followUpEditorModal?.classList.add("hidden");
  const returnTarget = deactivateForegroundModal(els.followUpEditorModal, { restoreFocus: false });
  if (editor?.sourceSurface?.startsWith("claim-drawer") && activeClaimDrawerContext?.claim) {
    renderClaimDrawer(activeClaimDrawerContext.claim, {
      serverHydrated: true,
      deniedColumns: activeClaimDrawerContext.deniedColumns,
      sectionStatus: activeClaimDrawerContext.sectionStatus || null,
      focusServiceId: activeClaimDrawerContext.focusServiceId
    });
  }
  if (state.view === "workbench") renderClaimsTable();
  window.requestAnimationFrame(() => {
    if (returnTarget?.isConnected) {
      returnTarget.focus?.({ preventScroll: true });
      return;
    }
    if (!editor) return;
    const target = Array.from(document.querySelectorAll("[data-action-edit], [data-date-edit]"))
      .find((control) => String(control.dataset.actionEdit || control.dataset.dateEdit || "") === editor.claimId
        && String(control.dataset.serviceId || "") === editor.serviceId);
    (target?.closest(".followup-cell")?.querySelector("[data-followup-pill]") || target)?.focus?.({ preventScroll: true });
  });
}

// THE OUTCOME OF A FOLLOW-UP SAVE, FOR SURFACES THAT ARE NOT THE GRID.
//
// Status checks paints its own rows from its own payload, so it cannot read the
// result out of renderClaimsTable. One announcement carries what actually
// happened - a real change, a proposal still awaiting approval, an unconfirmed
// write, or the server's own refusal wording - so a listening surface updates the
// exact row it sent instead of re-fetching the queue. It never decides anything:
// every lane is still resolved above, by the one save path.
//
// Wrapped, because several release-gate verifiers execute this function inside a
// bare `vm` sandbox with no CustomEvent constructor. A missing constructor must
// never turn a landed save into a thrown error.
function announceFollowUpOutcome(detail = {}) {
  try { document.dispatchEvent(new CustomEvent("tms:follow-up-outcome", { detail })); }
  catch (_) { /* sandbox without DOM events: the save already happened */ }
}

async function submitFollowUpEditor(event) {
  event.preventDefault();
  const editor = followUpEditorState;
  if (!editor) return;
  if (editor.restrictedCurrent) return setText(els.followUpEditorStatus, "Restricted action — this update was not sent.");
  const action = String(getValue(els.followUpEditorAction) || "").trim();
  const clearing = !action || action === "None";
  const followUpDate = clearing ? "" : String(getValue(els.followUpEditorDate) || "").trim();
  const note = String(getValue(els.followUpEditorNote) || "").trim();
  const scopeMode = String(document.querySelector("input[name='followUpEditorNoteScope']:checked")?.value || editor.noteScope || "");
  if (!note) return setText(els.followUpEditorStatus, "Enter a claim note before saving the follow-up.");
  if (note.length > 20000) return setText(els.followUpEditorStatus, "Claim notes can be up to 20,000 characters.");
  if (!scopeMode) return setText(els.followUpEditorStatus, "Choose whether the note applies to this service line or the entire claim.");
  setDisabled(els.followUpEditorSubmitBtn, true);
  const commandId = claimNoteCommandId("follow-up");
  // LATCH. The tail of the success path (close, toast, drawer reload) can throw,
  // and that throw lands in the catch below - which would then announce "refused"
  // for a save the server accepted, leaving the queue row saying the opposite of
  // what happened. Announce once; never contradict it.
  let announced = false;
  try {
    const disposition = oversightDisposition(editor.orgId, action, editor.pinnedCurrentAction ?? followUpActionOnLine(editor.claimId, editor.serviceId, editor.orgId));
    const proposalKey = JSON.stringify({ action, followUpDate, note });
    if (editor.proposalKey !== proposalKey) { editor.proposalKey = proposalKey; editor.proposalCommandId = oversightId(); }
    if (disposition === "blocked") throw new Error("Your manager has restricted this action.");
    if (disposition === "approval") {
      await submitOversightProposal(editor, { orgId: editor.orgId, claimId: editor.claimId, serviceId: editor.serviceId, action: clearing ? "None" : action, followUpDate, note, sourceSurface: editor.sourceSurface, workflowCommandId: editor.proposalCommandId, expectedUpdatedAt: editor.pinnedVersion || followUpVersionOnLine(editor.claimId, editor.serviceId, editor.orgId) });
      // submitOversightProposal throws unless the server confirmed the proposal,
      // so reaching this line means the pending working action landed.
      announceFollowUpOutcome({ claimId: editor.claimId, serviceId: editor.serviceId, orgId: editor.orgId, action: clearing ? "None" : action, followUpDate, sourceSurface: editor.sourceSurface, outcome: "proposal" });
      announced = true;
      return;
    }
    const body = await postClaimNoteApi("/api/workflow/follow-up", {
      orgId: editor.orgId,
      claimId: editor.claimId,
      serviceId: editor.serviceId,
      action: clearing ? "None" : action,
      followUpDate,
      note,
      scopeMode,
      serviceLineIds: scopeMode === "service_lines" ? [editor.serviceId] : [],
      sourceSurface: editor.sourceSurface,
      workflowCommandId: commandId,
      commandId,
      expectedUpdatedAt: editor.pinnedVersion || followUpVersionOnLine(editor.claimId, editor.serviceId)
    });
    const effectiveMutation = confirmedDirectFollowUpMutation(body);
    const proposalMutation = confirmedFollowUpProposal(body);
    if (effectiveMutation) updateFollowUpOverlay(editor.claimId, editor.serviceId, effectiveMutation);
    rememberFollowUpVersionOnLine(editor.claimId, editor.serviceId, body.updatedAt || body.updatedAtUtc || body.followUpUpdatedAt || "", editor.orgId);
    invalidateTrackerApiFacetsAfterWorkflowWrite();
    invalidatePortalReadsAfterWorkflowWrite();
    recordClaimWorkAction(
      editor.claimId,
      effectiveMutation ? (clearing ? "followup_cleared" : "followup_set") : (proposalMutation ? "followup_proposed" : "followup_saved_unconfirmed"),
      { serviceId: editor.serviceId, actionType: clearing ? "None" : action }
    );
    // THE SERVER'S OWN VALUES whenever it confirmed something. The grid overlay is
    // updated from effectiveMutation, so announcing the REQUESTED action or date
    // would let the queue row and the grid disagree about one line the moment the
    // server canonicalises either of them.
    const confirmed = effectiveMutation
      || (proposalMutation ? { action: proposalMutation.proposedAction, followUpDate: proposalMutation.proposedFollowUpDate } : null);
    announceFollowUpOutcome({ claimId: editor.claimId, serviceId: editor.serviceId, orgId: editor.orgId,
      action: confirmed ? String(confirmed.action || "") : (clearing ? "None" : action),
      followUpDate: confirmed ? String(confirmed.followUpDate || "") : followUpDate,
      sourceSurface: editor.sourceSurface,
      outcome: effectiveMutation ? "effective" : (proposalMutation ? "proposal" : "unconfirmed"),
      // The new version travels with the outcome: rememberFollowUpVersionOnLine can
      // only stamp rows that already exist on the grid page, so for an off-page
      // queue line the listening row is the ONLY place the next expectedUpdatedAt
      // can live - and a date-only change leaves that line in the queue, where the
      // very next save would otherwise 409.
      updatedAt: body.updatedAt || body.updatedAtUtc || body.followUpUpdatedAt || "" });
    announced = true;
    closeFollowUpEditor({ discardDraft: true });
    const scopeText = scopeMode === "service_lines" ? "service-line note" : "entire-claim note";
    showToast(effectiveMutation
      ? (clearing ? `Follow-up cleared and ${scopeText} saved.` : `Follow-up and ${scopeText} saved.`)
      : (proposalMutation
        ? (body.followUpVersion?.supersedesProposalId
          ? `Pending follow-up replaced and ${scopeText} saved. Only the latest update needs approval; earlier updates remain in history.`
          : `Follow-up and ${scopeText} saved. The new action is pending approval.`)
        : `${scopeText} saved. Reloading the effective follow-up from the server.`));
    if (activeClaimDrawerContext?.claimId === editor.claimId) reopenActiveClaimDrawer();
  } catch (error) {
    // KEEP THE NOTE, AND SAY WHICH THING WAS REFUSED.
    //
    // The draft is stashed here as well as on close, so a biller who retries, or
    // reopens the line later, still has the sentence they wrote. And a 409 on this
    // route is the SERVICE LINE's version, never the note's - the generic "this
    // note changed after you opened it" pointed at the wrong object and sent people
    // looking at the notes panel. postClaimNoteApi now carries the server's own
    // wording and error code through, so the stale-version case can name itself and
    // the drawer can reload the line the biller has to compare against.
    followUpEditorDrafts[followUpKey(editor.claimId, editor.serviceId)] = note;
    // The modal already shows this verbatim; announce it too so a queue row that
    // sent the save can say what was refused instead of looking unchanged - unless
    // the save already landed and something in its tail threw.
    if (!announced) announceFollowUpOutcome({ claimId: editor.claimId, serviceId: editor.serviceId, orgId: editor.orgId, action: clearing ? "None" : action, followUpDate, sourceSurface: editor.sourceSurface, outcome: "refused", message: String(error?.message || ""), code: String(error?.code || "") });
    const staleLine = error && error.code === "follow_up_stale";
    setText(els.followUpEditorStatus, staleLine
      ? `${error.message || "This service line changed after you opened it."} Your note is still here - compare the reloaded line and save again.`
      : `${error.message || "The follow-up could not be saved."} Your note has been kept.`);
    if (staleLine && activeClaimDrawerContext?.claimId === editor.claimId) reopenActiveClaimDrawer();
  } finally {
    setDisabled(els.followUpEditorSubmitBtn, Boolean(editor.restrictedCurrent));
  }
}

// The service lines a bulk follow-up would actually write. Follow-up is
// per-service-line: apply to exactly the selected service lines
// (state.selectedServiceTargets), never fanning a claim onto all its lines.
// Back-compat: if no per-line selection was recorded (e.g. a persisted older
// selection or a code path that only set selectedClaims), fall back to the
// rendered service lines for each selected claim; failing that, one target
// per selected claim with an empty serviceId so the command still flows.
//
// ONE function so the number shown in the guard, the number the user must
// type and the number actually written can never drift apart.
function bulkFollowUpLineTargets() {
  let lineTargets = Object.values(state.selectedServiceTargets || {})
    .filter((target) => target && state.selectedClaims.includes(normalizeClaimId(target.claimId)));
  if (!lineTargets.length) {
    const rendered = serviceTargetsForRenderedRows();
    lineTargets = Object.values(rendered).filter((target) => target && state.selectedClaims.includes(normalizeClaimId(target.claimId)));
  }
  if (!lineTargets.length) {
    lineTargets = state.selectedClaims.map((claimId) => ({ claimId: normalizeClaimId(claimId), serviceId: "", orgId: "" }));
  }
  return lineTargets;
}

// Live guard state. Recomputed from the CURRENT selection every time it is
// asked for, so a box filled in before the selection changed is stale by
// construction and cannot pass.
function bulkFollowUpConfirmState() {
  const action = getValue(els.bulkAction);
  if (action && action !== "None" && currentFollowUpActionMode(action) === "denied") return showToast("That follow-up action is not available to your account.");
  if (action === "None") {
    const restrictedTargets = bulkFollowUpLineTargets().filter((target) => followUpActionIsRestrictedCurrent(followUpActionOnLine(target.claimId, target.serviceId, target.orgId)));
    if (restrictedTargets.length) return showToast(`Clear follow-up is blocked because ${formatNounCount(restrictedTargets.length, "selected service line")} carries an action your account cannot change.`);
  }
  const clearing = action === "None";
  const required = bulkFollowUpLineTargets().length;
  const threshold = clearing ? BULK_FOLLOW_UP_CLEAR_CONFIRM_MIN_ROWS : BULK_FOLLOW_UP_CONFIRM_MIN_ROWS;
  const needed = required > 0 && required >= threshold;
  // Exact trimmed string compare, never a numeric parse: "018", "18x", "1 8",
  // whitespace and a blank box all fail closed, and a prefix of the right
  // digits is not the right answer.
  const typed = String(getValue(els.bulkConfirmCount, "") || "");
  const matched = typed.trim() === String(required);
  return { action, clearing, required, threshold, needed, typed, matched };
}

// Paints the guard row and owns the commit button's disabled state (it runs
// immediately after the permission gate in renderClaimsTable, so it is the
// last writer). Never re-creates the input: the field is static markup in
// index.html, because renderClaimsTable({ selectionOnly: true }) fires on
// every checkbox tick and would otherwise wipe what the user is typing.
function updateBulkFollowUpGuard(options = {}) {
  const allowed = hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP);
  const guard = bulkFollowUpConfirmState();
  const field = els.bulkConfirmField;
  const wasHidden = field ? Boolean(field.hidden) : true;
  const show = allowed && guard.needed;
  if (field) field.hidden = !show;
  if (show) {
    setText(els.bulkConfirmLabel, `Type ${guard.required} to confirm`);
    if (els.bulkConfirmCount) {
      els.bulkConfirmCount.placeholder = String(guard.required);
      if (typeof els.bulkConfirmCount.setAttribute === "function") {
        els.bulkConfirmCount.setAttribute("aria-invalid", guard.typed.trim() && !guard.matched ? "true" : "false");
      }
    }
    const verb = guard.clearing ? "cleared" : "updated";
    setText(els.bulkConfirmMessage, !guard.typed.trim()
      ? `${formatNounCount(guard.required, "row")} selected. Type ${guard.required} to unlock this update.`
      : (guard.matched
        ? `Confirmed - ${formatNounCount(guard.required, "row")} will be ${verb}.`
        : `Not correct - ${formatNounCount(guard.required, "row")} selected. Type ${guard.required}.`));
    if (els.bulkConfirmMessage && els.bulkConfirmMessage.classList) {
      els.bulkConfirmMessage.classList.toggle("is-mismatch", Boolean(guard.typed.trim()) && !guard.matched);
      els.bulkConfirmMessage.classList.toggle("is-match", guard.matched);
    }
  } else {
    setValue(els.bulkConfirmCount, "");
    if (els.bulkConfirmCount && typeof els.bulkConfirmCount.setAttribute === "function") {
      els.bulkConfirmCount.setAttribute("aria-invalid", "false");
    }
    setText(els.bulkConfirmMessage, "");
    if (els.bulkConfirmMessage && els.bulkConfirmMessage.classList) {
      els.bulkConfirmMessage.classList.remove("is-mismatch");
      els.bulkConfirmMessage.classList.remove("is-match");
    }
  }
  setDisabled(els.bulkFollowUpBtn, !allowed || !String(getValue(els.bulkNote, "") || "").trim() || (guard.needed && !guard.matched));
  // Autofocus once, when the guard appears or a submit bounces off it - never
  // on every render, or ticking the sixth checkbox would yank focus each time.
  const active = (typeof document !== "undefined" && document.activeElement) || null;
  const activeIsRowSelect = Boolean(active && ((active.classList && active.classList.contains("claim-select")) || active.id === "selectAllVisible"));
  const appeared = show && wasHidden && !activeIsRowSelect;
  if ((options.focus || appeared) && show && els.bulkConfirmCount && typeof els.bulkConfirmCount.focus === "function") {
    els.bulkConfirmCount.focus();
    if (typeof els.bulkConfirmCount.select === "function") els.bulkConfirmCount.select();
  }
  return guard;
}

const BULK_FOLLOW_UP_RETRY_STORAGE_KEY = "tms.bulk-follow-up-retry.v1";

async function bulkFollowUpRetryKey(payload) {
  const raw = JSON.stringify({ ...payload, access: workflowCommandAccessSnapshot() });
  if (!globalThis.crypto?.subtle || typeof TextEncoder !== "function") return raw;
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
  return "sha256:" + Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function storedBulkFollowUpRetry(retryKey, intentKey) {
  try {
    const stored = JSON.parse(safeSessionStorageGet(BULK_FOLLOW_UP_RETRY_STORAGE_KEY) || "null");
    const sameRequest = stored?.retryKey === retryKey;
    const uncertainSameIntent = stored?.uncertain && stored?.intentKey === intentKey;
    return (sameRequest || uncertainSameIntent) && /^follow-up-bulk-[0-9a-f-]{36}$/i.test(stored.batchId || "") ? stored : null;
  } catch { return null; }
}

function rememberBulkFollowUpRetry(retryKey, batchId, intentKey, uncertain, versions) {
  bulkFollowUpRetryState = { retryKey, batchId, intentKey, uncertain, versions };
  // Store only digests, an opaque operation ID, and bounded optimistic version
  // timestamps across reloads. Notes and claim/service identifiers stay out.
  if (retryKey.startsWith("sha256:")) {
    safeSessionStorageSet(BULK_FOLLOW_UP_RETRY_STORAGE_KEY,
      JSON.stringify({ retryKey, batchId, intentKey, uncertain, versions }));
  }
}

function clearBulkFollowUpRetry() {
  bulkFollowUpRetryState = null;
  safeSessionStorageRemove(BULK_FOLLOW_UP_RETRY_STORAGE_KEY);
}

async function bulkFollowUpUpdate() {
  if (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) return denyAction(PERMISSIONS.UPDATE_FOLLOW_UP, "bulk follow-up");
  const action = getValue(els.bulkAction);
  const note = getValue(els.bulkNote).trim();
  const scopeMode = String(getValue(els.bulkNoteScope) || "service_lines");
  let followUpDate = getValue(els.bulkActionDate);
  if (!state.selectedClaims.length) return showToast("Select one or more visible claims first.");
  if (!action) return showToast("Choose a follow-up action.");
  if (!note) {
    els.bulkNote?.focus?.();
    return showToast("Enter the required claim note before updating follow-up.");
  }
  if (note.length > 20000) return showToast("Claim notes can be up to 20,000 characters.");
  // "None" is the explicit clear sentinel: the backend rejects empty actions,
  // so clearing sends the literal "None" with blanked local workflow values.
  const clearing = action === "None";
  if (clearing) followUpDate = "";

  const lineTargets = bulkFollowUpLineTargets();

  // CT-TRACKER parity (DEC 2026-05-28 / CT_TRACKER_RULES_LEDGER.md section 1),
  // as amended by the owner on 2026-08-10: bulk mass-mis-edit guardrails.
  // Hard-block over 100 rows; require the exact row count to be TYPED into the
  // guard box on the bulk strip for applies of more than 3 rows and clears of
  // 2 or more; small edits proceed with no gate at all.
  //
  // This is the authoritative gate, not the disabled button: it recomputes the
  // required count here, at click time, so a selection changed after the box
  // was filled can never be written against the stale number the user typed.
  const n = lineTargets.length;
  const isClear = clearing;
  if (n > BULK_FOLLOW_UP_MAX_ROWS) {
    return showToast(`Bulk follow-up over ${BULK_FOLLOW_UP_MAX_ROWS} rows is blocked (${formatNounCount(n, "service line")} selected). Narrow the selection and try again.`);
  }
  const confirmGuard = bulkFollowUpConfirmState();
  if (confirmGuard.needed && !confirmGuard.matched) {
    updateBulkFollowUpGuard({ focus: true });
    return showToast(confirmGuard.typed.trim()
      ? `Not correct. Type ${n} to confirm ${isClear ? "clearing" : "updating"} ${formatNounCount(n, "service line")}.`
      : `Type ${n} in the confirm box to ${isClear ? "clear" : "update"} ${formatNounCount(n, "service line")}.`);
  }

  const rule = clearing ? null : followUpRuleForAction(action);
  if (!followUpDate && rule && Number.isFinite(rule.offset)) {
    followUpDate = addDays(todayDateString(), rule.offset);
  }

  const requestTargets = lineTargets.map((target) => ({
    orgId: String(target.orgId || workflowTargetFieldsForClaim(target.claimId, target).orgId || "").trim(),
    claimId: normalizeClaimId(target.claimId),
    serviceId: String(target.serviceId || "").trim(),
    expectedUpdatedAt: followUpVersionOnLine(target.claimId, target.serviceId, target.orgId)
  })).sort((a, b) => a.orgId.localeCompare(b.orgId) || a.claimId.localeCompare(b.claimId) || a.serviceId.localeCompare(b.serviceId));
  const dispositions = requestTargets.map((target) => oversightDisposition(target.orgId, action, followUpActionOnLine(target.claimId, target.serviceId, target.orgId)));
  if (dispositions.includes("blocked")) return showToast("One or more selected lines do not allow this action. Narrow your selection.");
  if (dispositions.includes("approval")) {
    if (dispositions.some((value) => value !== "approval")) return showToast("Select companies with the same approval setting together. Some selected lines require approval and others save directly.");
    return submitOversightBulkRequests(requestTargets, clearing ? "None" : action, clearing ? "" : followUpDate, note);
  }
  const distinctClaims = Array.from(new Set(requestTargets.map((target) => `${target.orgId}\u0000${target.claimId}`)));
  setDisabled(els.bulkFollowUpBtn, true);
  const retryPayload = { action: clearing ? "None" : action, followUpDate: clearing ? "" : followUpDate, note, scopeMode, targets: requestTargets };
  let retryKey, intentKey;
  try {
    retryKey = await bulkFollowUpRetryKey(retryPayload);
    intentKey = await bulkFollowUpRetryKey({ ...retryPayload,
      targets: requestTargets.map(({ expectedUpdatedAt, ...target }) => target) });
  } catch (error) { setDisabled(els.bulkFollowUpBtn, false); return showToast("Could not prepare a safe retry. Nothing was sent."); }
  const currentRetry = bulkFollowUpRetryState;
  const retryAttempt = (currentRetry?.retryKey === retryKey
    || (currentRetry?.uncertain && currentRetry.intentKey === intentKey))
    ? currentRetry : storedBulkFollowUpRetry(retryKey, intentKey);
  if (retryAttempt?.uncertain && retryAttempt.retryKey !== retryKey) {
    const priorVersions = retryAttempt.versions;
    const validVersion = (version) => typeof version === "string" && (version === ""
      || (version.length <= 40 && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?(?:Z|[+-]\d{2}:\d{2})$/.test(version)));
    if (!Array.isArray(priorVersions) || priorVersions.length !== requestTargets.length || !priorVersions.every(validVersion)) {
      setDisabled(els.bulkFollowUpBtn, false);
      return showToast("The earlier bulk save cannot be checked safely from this tab. Keep these lines selected and review their saved history before starting another change.");
    }
    requestTargets.forEach((target, index) => { target.expectedUpdatedAt = priorVersions[index]; });
    try { retryKey = await bulkFollowUpRetryKey(retryPayload); }
    catch { setDisabled(els.bulkFollowUpBtn, false); return showToast("Could not prepare a safe retry. Nothing was sent."); }
    if (retryKey !== retryAttempt.retryKey) {
      setDisabled(els.bulkFollowUpBtn, false);
      return showToast("The earlier bulk save could not be matched safely. Review these lines before starting another change.");
    }
  }
  const batchId = retryAttempt?.batchId || claimNoteCommandId("follow-up-bulk");
  rememberBulkFollowUpRetry(retryKey, batchId, intentKey, true,
    requestTargets.map((target) => target.expectedUpdatedAt));
  let body;
  try {
    body = await postClaimNoteApi("/api/workflow/follow-up/bulk", {
      batchId,
      commandId: batchId,
      action: clearing ? "None" : action,
      followUpDate: clearing ? "" : followUpDate,
      note,
      scopeMode,
      sourceSurface: "all-data-bulk",
      targets: requestTargets.map((target) => ({ ...target }))
    });
  } catch (error) {
    setDisabled(els.bulkFollowUpBtn, false);
    return showToast("The server did not confirm this bulk save. Your selections and note are still here; retry the same change to check safely.");
  }
  const results = Array.isArray(body.results) ? body.results : [];
  const resultByClaim = new Map(results.map((result) => [`${String(result.orgId || "").trim()}\u0000${normalizeClaimId(result.claimId)}`, result]));
  const resultForTarget = (target) => resultByClaim.get(`${target.orgId}\u0000${normalizeClaimId(target.claimId)}`);
  const targetConfirmed = (target) => {
    const result = resultForTarget(target);
    return result?.status === "updated" && Array.isArray(result.serviceIds)
      && result.serviceIds.some((serviceId) => String(serviceId) === String(target.serviceId));
  };
  const successfulTargets = requestTargets.filter(targetConfirmed);
  const pendingTargets = requestTargets.filter((target) => !targetConfirmed(target));
  const pendingClaimKeys = new Set(pendingTargets.map((target) => `${target.orgId}\u0000${normalizeClaimId(target.claimId)}`));
  const pendingResults = Array.from(pendingClaimKeys, (key) => resultByClaim.get(key));
  const unknownClaims = pendingResults.filter((result) => !result || result.status === "unknown").length;
  const unattemptedClaims = pendingResults.filter((result) => result?.status === "unattempted").length;
  const failedClaims = pendingResults.length - unknownClaims - unattemptedClaims;
  let effectiveTargetCount = 0;
  let proposalTargetCount = 0;
  let unconfirmedTargetCount = 0;
  successfulTargets.forEach((target) => {
    const claimResult = resultForTarget(target);
    const lineMutation = Array.isArray(claimResult?.followUpMutations)
      ? claimResult.followUpMutations.find((mutation) => String(mutation?.serviceId || "").trim() === String(target.serviceId || "").trim())
      : null;
    const effectiveMutation = confirmedDirectFollowUpMutation({ followUpMutation: lineMutation });
    const proposalMutation = confirmedFollowUpProposal({ followUpMutation: lineMutation });
    if (effectiveMutation) {
      updateFollowUpOverlay(normalizeClaimId(target.claimId), String(target.serviceId || "").trim(), effectiveMutation);
      effectiveTargetCount += 1;
    } else if (proposalMutation) proposalTargetCount += 1;
    else unconfirmedTargetCount += 1;
    rememberFollowUpVersionOnLine(target.claimId, target.serviceId, claimResult?.followUpUpdatedAt || claimResult?.updatedAtUtc || "", target.orgId);
  });
  results.forEach((result) => {
    if (result.status !== "updated" && result.serviceId && result.currentUpdatedAt) {
      rememberFollowUpVersionOnLine(result.claimId, result.serviceId, result.currentUpdatedAt, result.orgId);
    }
  });
  audit(`Bulk follow-up response: ${effectiveTargetCount} applied, ${proposalTargetCount} proposed, ${unknownClaims} claims unconfirmed, ${unattemptedClaims} unattempted`, activeInternalUser());
  if (pendingTargets.length) {
    state.selectedClaims = Array.from(new Set(pendingTargets.map((target) => normalizeClaimId(target.claimId))));
    state.selectedClaimTargets = Object.fromEntries(pendingTargets.map((target) => [normalizeClaimId(target.claimId), { orgId: target.orgId }]));
    state.selectedServiceTargets = normalizeSelectedServiceTargets(pendingTargets);
    // The server derives an idempotent command per claim from batchId. Keep the
    // same batch for unresolved claims, including when an uncertain save
    // changes the expected version, so a retry replays or conflicts safely.
    try {
      const pendingKey = await bulkFollowUpRetryKey({ ...retryPayload, targets: pendingTargets });
      const pendingIntentKey = await bulkFollowUpRetryKey({ ...retryPayload,
        targets: pendingTargets.map(({ expectedUpdatedAt, ...target }) => target) });
      rememberBulkFollowUpRetry(pendingKey, batchId, pendingIntentKey,
        unknownClaims > 0 || Boolean(retryAttempt?.uncertain),
        pendingTargets.map((target) => target.expectedUpdatedAt));
    } catch { /* The in-memory original identity remains available this tab. */ }
  } else {
    state.selectedClaims = [];
    state.selectedClaimTargets = {};
    state.selectedServiceTargets = {};
    setValue(els.bulkAction, "");
    setValue(els.bulkActionDate, "");
    setValue(els.bulkNote, "");
    setValue(els.bulkNoteScope, "service_lines");
    clearBulkFollowUpRetry();
  }
  setValue(els.bulkConfirmCount, "");
  invalidateTrackerApiFacetsAfterWorkflowWrite();
  invalidatePortalReadsAfterWorkflowWrite();
  saveState();
  render();
  if (pendingTargets.length) {
    setValue(els.bulkAction, action);
    setValue(els.bulkActionDate, followUpDate);
    setValue(els.bulkNote, note);
    setValue(els.bulkNoteScope, scopeMode);
  }
  updateBulkFollowUpGuard();
  const outcomeParts = [];
  if (effectiveTargetCount) outcomeParts.push(`${formatNounCount(effectiveTargetCount, "service line")} applied`);
  if (proposalTargetCount) outcomeParts.push(`${formatNounCount(proposalTargetCount, "service line")} sent for approval`);
  if (unconfirmedTargetCount) outcomeParts.push(`${formatNounCount(unconfirmedTargetCount, "service line")} confirmed by the server and reloading`);
  const outcomeText = outcomeParts.join("; ") || "No service-line action was confirmed";
  if (pendingTargets.length) {
    // Say WHY a claim was rejected. The server returns a reason per claim; a bare
    // count left billers retrying a change that could never succeed.
    const rejectionReasons = Array.from(new Set(pendingResults
      .filter((result) => result && !["unknown", "unattempted"].includes(result.status))
      .map((result) => String(result.message || result.error || "").trim())
      .filter(Boolean)));
    const reasonText = rejectionReasons.length
      ? ` Reason: ${rejectionReasons.slice(0, 2).join(" / ")}${rejectionReasons.length > 2 ? ` (+${rejectionReasons.length - 2} more)` : ""}`
      : "";
    showToast(`${outcomeText}; ${formatNounCount(unknownClaims, "claim")} unconfirmed, ${formatNounCount(unattemptedClaims, "claim")} not attempted, ${formatNounCount(failedClaims, "claim")} rejected.${reasonText} Those lines remain selected for review and retry.`);
  } else {
    showToast(`${outcomeText} across ${formatNounCount(distinctClaims.length, "claim")}; one claim note saved per claim.`);
  }
}

function confirmedDirectFollowUpMutation(body = {}) {
  const mutation = body && typeof body === "object" && !Array.isArray(body) ? body.followUpMutation : null;
  if (!mutation || typeof mutation !== "object" || Array.isArray(mutation)) return null;
  if (mutation.effectiveApplied !== true || String(mutation.accessMode || "") !== "direct" || String(mutation.disposition || "") !== "effective") return null;
  if (typeof mutation.effectiveAction !== "string" || typeof mutation.effectiveFollowUpDate !== "string") return null;
  return { action: mutation.effectiveAction.trim().replace(/\s+/g, " "), followUpDate: mutation.effectiveFollowUpDate.trim() };
}

function confirmedFollowUpProposal(body = {}) {
  const mutation = body && typeof body === "object" && !Array.isArray(body) ? body.followUpMutation : null;
  if (!mutation || typeof mutation !== "object" || Array.isArray(mutation)) return null;
  if (mutation.effectiveApplied !== false || String(mutation.accessMode || "") !== "approval_required" || String(mutation.disposition || "") !== "proposal") return null;
  if (typeof mutation.proposalId !== "string" || !mutation.proposalId.trim()) return null;
  if (["effectiveAction", "effectiveFollowUpDate", "proposedAction", "proposedFollowUpDate"].some((key) => typeof mutation[key] !== "string")) return null;
  return {
    proposalId: mutation.proposalId.trim(),
    effectiveAction: mutation.effectiveAction.trim().replace(/\s+/g, " "),
    effectiveFollowUpDate: mutation.effectiveFollowUpDate.trim(),
    proposedAction: mutation.proposedAction.trim().replace(/\s+/g, " "),
    proposedFollowUpDate: mutation.proposedFollowUpDate.trim()
  };
}

async function submitForReview(claimId, completionNote = "", nextAction = "", nextDate = "", options = {}) {
  const claim = requireClaimPermission(claimId, PERMISSIONS.SUBMIT_REVIEW, "submit for review");
  if (!claim) return false;
  // Wave 3c belt-and-braces: enforce the ONE submit gate here too, not just
  // via disabled buttons — data-offshore-submit (and any scripted caller)
  // must obey the same blocker rule as the work card and planner.
  const gate = submitReviewGate(claim);
  if (gate.blocked) {
    showToast(gate.title);
    return false;
  }
  const note = stripNextAction(completionNote.trim());
  if (!note) {
    showToast("Enter the required claim note before submitting a follow-up action.");
    return false;
  }
  const action = String(nextAction || "").trim().replace(/\s+/g, " ");
  if (!action) {
    showToast("Choose the next follow-up action.");
    return false;
  }
  if (currentFollowUpActionMode(action) === "denied") {
    showToast("That follow-up action is not available to your account.");
    return false;
  }
  // Keeping the SAME follow-up action is a valid answer to a claim the
  // supervisor SENT BACK (owner decision, 2026-09-17): the biller may be moving
  // only the date, or simply replying. That is the "the system does not allow us
  // to request the same follow-up action" report, and its exact scope is the
  // owner’s own words -- "us sending it to offshore and then them trying to
  // respond back to us", i.e. a claim sitting in Bounced Back.
  //
  // Anywhere else the original refusal stands, because the must-differ rule was
  // also this lane’s only double-submit guard: on an approved claim a
  // nothing-changed resubmit clears the approval markers. The note above is
  // already required, so an unchanged action always carries a reply. The server
  // holds the same pair of conditions in handleSubmitReview.
  if (workflowActionKey(action) === workflowActionKey(claim.action) && claim.assignmentStatus !== "Bounced Back") {
    showToast("Choose what should happen next; it cannot stay on the same follow-up action.");
    return false;
  }
  const nextActionText = `Next action: ${action}.`;
  const noteText = note ? `${note} ${nextActionText}` : nextActionText;
  const patch = {
    assignmentStatus: "Submitted For Review",
    // Wave 3c: resubmission KEEPS bounceReason/bounceBy/bounceAt so the
    // re-reviewing manager still sees why it bounced. Approve clears them
    // (approveReview); the next bounce overwrites them. Offshore callouts are
    // status-gated on "Bounced Back", so the persisted reason stays hidden
    // from offshore once resubmitted.
    reviewNote: noteText
  };
  const rule = followUpRuleForAction(action);
  const proposedFollowUpDate = nextDate
    ? nextDate
    : (rule && Number.isFinite(rule.offset) ? addDays(todayDateString(), rule.offset) : "");
  try {
    const commandId = claimNoteCommandId("submit-review");
    const responseBody = await postClaimNoteApi("/api/workflow/submit-review", {
      claimId,
      ...workflowTargetFieldsForClaim(claimId, options),
      action,
      followUpDate: proposedFollowUpDate,
      note,
      workflowCommandId: commandId,
      commandId
    });
    // A 200 may mean direct application, a supervised proposal, or only a
    // claim-level review suggestion. Fail closed unless the nested server
    // contract explicitly proves a direct effective mutation.
    const effectiveMutation = confirmedDirectFollowUpMutation(responseBody);
    mutateClaim(claimId, { ...patch, ...(effectiveMutation || {}) });
    audit(`${claimId} submitted for review with next action ${action}`, claim.assignedTo || "Offshore");
    saveState();
    showToast(effectiveMutation
      ? "Submitted for review. The follow-up action was applied."
      : "Submitted for review. The current action is unchanged; the requested next action is pending line-level application.");
    return true;
  } catch (error) {
    if (error?.status === 409 && trackerApiPageState.status === "ok") trackerApiPageState.status = "idle";
    showToast(`${error?.message || "The review could not be submitted."} Your note, action, and date are still here.`);
    return false;
  }
}

function workflowActionKey(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
}

// Starting is the assigned biller's move: retain SUBMIT_REVIEW and per-claim scope.
const assignmentStartAttempts = new Map();
async function startAssignmentWork(claimId, options = {}) {
  const claim = requireClaimPermission(claimId, PERMISSIONS.SUBMIT_REVIEW, "start work");
  if (!claim) return false;
  const target = workflowTargetFieldsForClaim(claimId, options);
  if (!target.orgId) { showToast("Choose the exact company row before starting work."); return false; }
  const base = workflowApiBase() || trackerApiBase();
  const token = workflowApiToken() || trackerApiToken();
  const access = JSON.stringify(workflowCommandAccessSnapshot());
  const actor = activeInternalUser();
  const selectedOrg = assignmentScopeUiState.orgId || "";
  const isCurrent = () => selectedOrg === (assignmentScopeUiState.orgId || "")
    && base === (workflowApiBase() || trackerApiBase())
    && token === (workflowApiToken() || trackerApiToken())
    && access === JSON.stringify(workflowCommandAccessSnapshot());
  const key = JSON.stringify([base, JSON.parse(access), target.orgId, String(claimId)]);
  const retryKey = "tms.assignment-start:" + key;
  let attempt = assignmentStartAttempts.get(key);
  if (attempt?.busy && attempt.token === token) return false;
  if (attempt && attempt.token !== token) attempt = null;
  if (!attempt) {
    try {
      const savedId = window.sessionStorage.getItem(retryKey);
      const commandId = /^wf-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(savedId || "") ? savedId : claimNoteCommandId("wf");
      window.sessionStorage.setItem(retryKey, commandId);
      attempt = { commandId, busy: false, token };
      assignmentStartAttempts.set(key, attempt);
    } catch (_) { showToast("Could not prepare a safe retry. Work was not started."); return false; }
  }
  attempt.busy = true;
  showToast("Starting work…");
  try {
    const response = await postClaimNoteApi("/api/workflow/start-work", {
      claimId, ...target, assignmentStatus: "In Progress", note: "Work started",
      workflowCommandId: attempt.commandId
    }, { base, token, isCurrent });
    if (response?.decision?.allowed !== true || response?.patch?.assignmentStatus !== "In Progress"
      || String(response.claimId || "") !== String(claimId) || String(response.orgId || "") !== target.orgId) {
      throw new Error(response?.message || "The server did not confirm that work started.");
    }
    if (assignmentStartAttempts.get(key) === attempt) {
      assignmentStartAttempts.delete(key);
      try { if (window.sessionStorage.getItem(retryKey) === attempt.commandId) window.sessionStorage.removeItem(retryKey); } catch (_) {}
    }
    if (!isCurrent()) return true;
    if (claim._workflowReadAuthoritative || trackerApiEnabled()) resetWorkflowBoard();
    else if (sourceClaims.filter((row) => String(row.claimId) === String(claimId)).length === 1
      && sourceClaims.some((row) => String(row.claimId) === String(claimId)
        && String(row.orgId || row.org_id || "").trim() === target.orgId)) {
      mutateClaim(claimId, { assignmentStatus: "In Progress" });
    } else resetWorkflowBoard();
    audit(claimId + ": work started", actor);
    saveState();
    renderAssignments();
    showToast(claimId + " moved to In Progress.");
    return true;
  } catch (error) {
    // A transport failure may have committed. Retain the identity for an exact retry.
    if (!isCurrent()) return false;
    if (error?.status >= 400 && error.status < 500 && ![401, 408, 425, 429].includes(error.status)) {
      assignmentStartAttempts.delete(key);
      try { window.sessionStorage.removeItem(retryKey); } catch (_) {}
    }
    showToast((error?.message || "Work could not be started.") + " Start was not confirmed. Retry to check the same request.");
    return false;
  } finally { attempt.busy = false; }
}

function updateClaimStatus(claimId, status, auditText, options = {}) {
  if (!requireClaimPermission(claimId, PERMISSIONS.APPROVE_REVIEW, "review status update")) return;
  mutateClaim(claimId, { assignmentStatus: status });
  enqueueWorkflowCommand("review", {
    claimId,
    ...workflowTargetFieldsForClaim(claimId, options),
    assignmentStatus: status,
    note: auditText || ""
  });
  audit(`${claimId}: ${auditText}`, activeInternalUser());
  saveState();
  showToast(`${claimId} moved to ${status}.`);
}

function approveReview(claimId, approvalNote = "", options = {}) {
  const claim = requireClaimPermission(claimId, PERMISSIONS.APPROVE_REVIEW, "approve review");
  if (!claim) return;
  const note = approvalNote.trim();
  if (!note) return showToast("Enter an approval note first.");
  mutateClaim(claimId, {
    assignmentStatus: "Approved",
    reviewNote: note,
    // Approve closes the bounce loop (wave 3c): clear the manager bounce
    // reason + stamps that persisted through resubmission.
    bounceReason: "",
    bounceBy: "",
    bounceAt: "",
    approvedAt: new Date().toISOString(),
    ...(claim.assignedAt ? {} : { assignedAt: new Date().toISOString() })
  });
  enqueueWorkflowCommand("review", {
    claimId,
    ...workflowTargetFieldsForClaim(claimId, options),
    assignmentStatus: "Approved",
    note,
    reviewedBy: activeInternalUser(),
    reviewedAt: new Date().toISOString()
  });
  audit(`${claimId} approved after manager review`, activeInternalUser());
  saveState();
  showToast("Claim approved.");
}

function withdrawSubmittedReview(claimId, options = {}) {
  const claim = requireClaimPermission(claimId, PERMISSIONS.SUBMIT_REVIEW, "withdraw review");
  if (!claim) return;
  if (String(claim.assignmentStatus || "") !== "Submitted For Review") return showToast("Only a review awaiting a decision can be withdrawn.");
  enqueueWorkflowCommand("withdraw-review", {
    claimId,
    ...workflowTargetFieldsForClaim(claimId, options),
    expectedUpdatedAt: String(claim.workflowUpdatedAt || ""),
    note: "Review withdrawn before decision."
  });
  showToast("Withdrawal requested. The item will reopen after the server confirms it.");
}

function undoReviewApproval(claimId, options = {}) {
  const claim = requireClaimPermission(claimId, PERMISSIONS.APPROVE_REVIEW, "undo approval");
  if (!claim) return;
  if (String(claim.assignmentStatus || "") !== "Approved") return showToast("Only the current approval can be undone.");
  enqueueWorkflowCommand("undo-approval", {
    claimId,
    ...workflowTargetFieldsForClaim(claimId, options),
    expectedUpdatedAt: String(claim.workflowUpdatedAt || ""),
    expectedApprovalCommandId: String(claim.approvalCommandId || ""),
    note: "Approval undone before downstream use."
  });
  showToast("Undo requested. The item will reopen only if no downstream process has claimed it.");
}

function requestWriteOffSnapshot(claimId, reasonText = "") {
  if (!requireClaimPermission(claimId, PERMISSIONS.MANAGE_WRITE_OFFS, "write-off governance")) return false;
  // The legacy claim-level path optimistically wrote a Write-Off before any
  // server response and had no service-line identity. Refuse it: Write-Off is
  // now chosen on the exact service line and follows that employee's policy.
  showToast("Choose Write-Off on the exact service line in All Data. This claim-level shortcut no longer changes data.");
  return false;
}

// Assignment cards group work by claim, but follow-up persistence is always
// service-line scoped. A real serviceId is required before any follow-up write.
function saveAssignmentFollowUp(claimId, options = {}) {
  if (!requireClaimPermission(claimId, PERMISSIONS.UPDATE_FOLLOW_UP, "save follow-up")) return;
  const claim = getClaim(claimId);
  if (!claim) return;
  // Legacy bounced claims stored the manager reason in reviewNote; migrate it
  // to bounceReason BEFORE offshore's own note overwrites the shared field,
  // so the red callout never shows offshore's words as the manager's.
  if (claim.assignmentStatus === "Bounced Back" && !String(claim.bounceReason || "").trim() && String(claim.reviewNote || "").trim()) {
    claim.bounceReason = claim.reviewNote;
    if (!state.workflowByClaim) state.workflowByClaim = {};
    state.workflowByClaim[claimId] = { ...(state.workflowByClaim[claimId] || {}), bounceReason: claim.bounceReason };
  }
  const note = getTextareaValue("completion-note", claimId);
  const serviceId = String(getFieldValue("next-service", claimId) || "").trim();
  const requestedAction = getFieldValue("next-action", claimId);
  const requestedDate = getFieldValue("next-date", claimId);
  if (!requestedAction && !requestedDate) {
    if (!note) return showToast("Enter a claim note first.");
    postClaimNoteApi("/api/workflow/note", {
      orgId: String(options.orgId || claim.orgId || ""),
      claimId,
      text: note,
      commandId: claimNoteCommandId("assignment-note"),
      sourceSurface: "assignment-board"
    }).then(() => {
      invalidatePortalReadsAfterWorkflowWrite();
      showToast("Claim note saved — claim stays in your queue.");
    })
      .catch((error) => showToast(error.message || "Claim note could not be saved."));
    return;
  }
  if (!note) return showToast("Enter the required claim note before saving follow-up.");
  if (!serviceId) {
    showToast("Choose the service line this follow-up belongs to.");
    return;
  }
  const service = (claim.services || []).find((row) => String(row.serviceId || "").trim() === serviceId);
  if (!service) {
    showToast("That service line is no longer available. Refresh and try again.");
    return;
  }
  const prior = state.followUpByService?.[followUpKey(claimId, serviceId)] || {};
  const action = firstPresent(requestedAction, prior.action, service.action);
  if (!action) {
    showToast("Choose a follow-up action for this service line.");
    return;
  }
  openFollowUpEditor(claimId, {
    ...options,
    serviceId,
    action,
    followUpDate: action === "None" ? "" : requestedDate,
    sourceSurface: "assignment-board"
  });
  setValue(els.followUpEditorNote, note);
  setText(els.followUpEditorNoteCount, String(note.length));
}

// THE FOLLOW-UP ACTION ON ONE SERVICE LINE, resolved exactly the way the row the
// biller is looking at renders it. GRAIN IS THE WHOLE POINT: getClaim() answers at
// CLAIM grain — on the live API lane currentTrackerApiClaim() returns the FIRST
// page row carrying that claim id, regardless of which line is being edited — so on
// a multi-line claim it reports a DIFFERENT line's action. Reading it here would
// both refuse edits on lines that plainly show an action and write a sibling line's
// action onto a line that has none. Each branch below mirrors its own renderer, so
// the value written is always the value displayed.
function preparedDrawerService(claimId,serviceId,orgId="") {
  const context=activeClaimDrawerContext,claim=context?.claim;
  if (!claim?.servicePage?.complete || String(context.claimId)!==String(claimId) || (orgId && String(context.orgId || claim.orgId)!==String(orgId))) return null;
  return claimAvailableServiceChoices(claim).find(line=>String(line.serviceId)===String(serviceId)) || null;
}

function followUpActionOnLine(claimId, serviceId, orgId = "") {
  const prepared = preparedDrawerService(claimId,serviceId,orgId);
  if (prepared) return String(prepared.action || "");
  const normalizedClaimId = String(claimId || "").trim();
  const normalizedServiceId = String(serviceId || "").trim();
  const normalizedOrgId = String(orgId || "").trim();
  // LIVE API lane — trackerApiCellRawValue(row, "action") is what the grid paints
  // (trackerApiCellHtml), overlay included. The row is the authority for its own
  // line, INCLUDING when its action is blank; and if the line is not on the page we
  // have no evidence about it, which must read as "none" rather than as the claim's.
  if (trackerApiPageState.status === "ok" && normalizedServiceId) {
    const row = (trackerApiPageState.response?.rows || []).find((candidate) => (
      trackerApiClaimId(candidate) === normalizedClaimId
      && String(candidate.serviceId || candidate.service_id || "").trim() === normalizedServiceId
      && (!normalizedOrgId || String(candidate.orgId || candidate.org_id || "").trim() === normalizedOrgId)
    ));
    return row ? String(trackerApiCellRawValue(row, "action") || "").trim() : "";
  }
  if (normalizedOrgId) return "";
  // DEMO / local-claims lane — serviceCellHtml's own fallback chain, verbatim.
  const claim = getClaim(normalizedClaimId);
  const overlay = state.followUpByService?.[followUpKey(normalizedClaimId, normalizedServiceId)] || {};
  if (normalizedServiceId) {
    const service = (claim?.services || []).find((line) => String(line.serviceId || "").trim() === normalizedServiceId);
    if (service) return String(firstPresent(overlay.action, service.action, claim?.action) || "").trim();
    return String(overlay.action || "").trim();
  }
  // Claim-grouped row (no service id): the claim's own action IS the row's displayed
  // action. THE LIVE GRID CANNOT REACH THIS — trackerApiCellHtml only emits an
  // editable date cell when it has BOTH a claim id and a service id, so a
  // claim-grouped row there is read-only. It is reachable only from the demo lane
  // and the offshore planner, where the claim IS what the row shows; do not
  // "fix" it into a refusal.
  //
  // NOTE for whoever re-enables the offshore planner nav (window.TMS_ASSIGNMENTS_NAV)
  // while the API lane is live: the planner's lines are not in trackerApiPageState,
  // so the live branch above will not find them and every date edit there will
  // refuse. Refusing on no evidence is the right default — this is where to fix it.
  return String(firstPresent(overlay.action, claim?.action) || "").trim();
}

function followUpVersionOnLine(claimId, serviceId, orgId = "") {
  const prepared = preparedDrawerService(claimId,serviceId,orgId);
  if (prepared) return String(prepared.followUpUpdatedAt || "");
  const normalizedClaimId = String(claimId || "").trim();
  const normalizedServiceId = String(serviceId || "").trim();
  if (!normalizedClaimId || !normalizedServiceId) return "";
  const apiRow = (trackerApiPageState.response?.rows || []).find((candidate) => (
    trackerApiClaimId(candidate) === normalizedClaimId
    && String(candidate.serviceId || candidate.service_id || "").trim() === normalizedServiceId
    && (!String(orgId || "").trim() || String(candidate.orgId || candidate.org_id || "").trim() === String(orgId || "").trim())
  ));
  if (apiRow) return String(apiRow.followUpUpdatedAt || apiRow.follow_up_updated_at_utc || "").trim();
  if (String(orgId || "").trim()) return "";
  const activeClaim = activeClaimDrawerContext?.claimId === normalizedClaimId
    ? activeClaimDrawerContext.claim
    : null;
  const activeService = (activeClaim?.services || []).find((line) => String(line.serviceId || "").trim() === normalizedServiceId);
  const activeVersion = String(activeService?.followUpUpdatedAt || activeService?.follow_up_updated_at_utc || "").trim();
  if (activeVersion) return activeVersion;
  const claim = getClaim(normalizedClaimId);
  const service = (claim?.services || []).find((line) => String(line.serviceId || "").trim() === normalizedServiceId);
  return String(service?.followUpUpdatedAt || service?.follow_up_updated_at_utc || "").trim();
}

function rememberFollowUpVersionOnLine(claimId, serviceId, updatedAt, orgId = "") {
  const normalizedClaimId = String(claimId || "").trim();
  const normalizedServiceId = String(serviceId || "").trim();
  const version = String(updatedAt || "").trim();
  if (!normalizedClaimId || !normalizedServiceId || !version) return;
  (trackerApiPageState.response?.rows || []).forEach((row) => {
    if (trackerApiClaimId(row) === normalizedClaimId && String(row.serviceId || row.service_id || "").trim() === normalizedServiceId
      && (!String(orgId || "").trim() || String(row.orgId || row.org_id || "").trim() === String(orgId || "").trim())) {
      row.followUpUpdatedAt = version;
    }
  });
  const normalizedOrgId = String(orgId || "").trim();
  const claim = getClaim(normalizedClaimId);
  const claimServices = !normalizedOrgId || String(claim?.orgId || "").trim() === normalizedOrgId ? (claim?.services || []) : [];
  claimServices.forEach((service) => {
    if (String(service.serviceId || "").trim() === normalizedServiceId) service.followUpUpdatedAt = version;
  });
  // AND THE OPEN DRAWER'S OWN COPY. followUpVersionOnLine reads the drawer
  // context before it falls back to the demo store, and a drawer opened from the
  // report queue is not in trackerApiPageState at all - so without this the
  // context still holds the version from the last fetch and the NEXT save on the
  // same line 409s exactly as before. Two saves in a row without a refetch is the
  // ordinary case (set the action, then correct the date), not an edge.
  const drawerServices = activeClaimDrawerContext?.claimId === normalizedClaimId
    && (!normalizedOrgId || String(activeClaimDrawerContext.orgId || activeClaimDrawerContext.claim?.orgId || "").trim() === normalizedOrgId)
    ? (activeClaimDrawerContext.pinnedReportContext ? [] : claimAvailableServiceChoices(activeClaimDrawerContext.claim || {}))
    : [];
  drawerServices.forEach((service) => {
    if (String(service.serviceId || "").trim() === normalizedServiceId) service.followUpUpdatedAt = version;
  });
}

function bounceBack(claimId, bounceReason = "", options = {}) {
  if (!requireClaimPermission(claimId, PERMISSIONS.APPROVE_REVIEW, "bounce back")) return;
  const reason = bounceReason.trim();
  if (!reason) return showToast("Enter a bounce-back reason first.");
  // Field ownership (OFFSHORE_HANDOFF_SPEC §1): the manager's reason lives in
  // bounceReason — never in the offshore-owned reviewNote. The queued review
  // command below already carries the note; the backend patches bounce_reason.
  mutateClaim(claimId, {
    assignmentStatus: "Bounced Back",
    bounceReason: reason,
    bounceBy: activeInternalUser(),
    bounceAt: new Date().toISOString()
  });
  enqueueWorkflowCommand("review", {
    claimId,
    ...workflowTargetFieldsForClaim(claimId, options),
    assignmentStatus: "Bounced Back",
    note: reason
  });
  audit(`${claimId} bounced back to offshore`, activeInternalUser());
  saveState();
  recordClaimWorkAction(claimId, "bounce_back", {});
  showToast("Claim bounced back.");
}

// All five roles may open Claim Notes. Internal roles additionally receive the
// audit panel and authoring controls according to the canonical note grants.
// One funnel on purpose: every [data-open] button in the file reaches this
// function, so the same tenant/org/claim gate covers every row surface.
// One predicate for every drawer gate and every affordance that leads to one.
// Two spellings is how these drift: openDrawer refusing on the ROLE while
// openServerClaimDrawer refuses on view:workbench means that granting a client
// role view:workbench for some unrelated reason would open one door and not the
// other. Both now ask this.
function externalClientSession() {
  return isExternalClientRole(state?.role);
}

function openDrawer(claimId, options = {}) {
  // The detail request includes contracted rate columns. External client roles
  // use the dedicated Claim Notes surfaces and must never enter this drawer.
  // Full Clients open the claim drawer (Nathan, 2026-10-13: "We are a very transparent company"); the server scopes it to their org and they hold rate access. RedactedClient stays out: the drawer carries contracted rates in nested pricing detail and that role excludes insurer money by design.
  if (isRedactedClientRole(state?.role)) return denyAction(PERMISSIONS.VIEW_FINANCIALS, `open claim drawer ${claimId}`);
  if (!hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES)) return denyAction(PERMISSIONS.VIEW_CLAIM_NOTES, `open claim ${claimId}`);
  const requestedOrgId = String(options.orgId || activeClientGroupServerOrgId() || "").trim();
  if (activeClientGroupTenant() && !activeClientGroupOrgIds().includes(requestedOrgId)) { showToast("Choose this claim’s company at the top of the page before opening it."); return; }
  const claim = getClaim(claimId);
  const noteApiBase = workflowApiBase() || trackerApiBase();
  if (trackerApiEnabled() || noteApiBase) {
    return openServerClaimDrawer(claimId, {
      ...options,
      orgId: requestedOrgId || String(claim?.orgId || "").trim(),
      ...(noteApiBase ? { apiBase: noteApiBase, apiToken: workflowApiToken() || trackerApiToken() } : {})
    });
  }
  // Claim ids are not globally unique across EZClaim companies. A payment drill
  // carries org identity; never hydrate a same-id claim cached for another org.
  if (!claim || (requestedOrgId && String(claim.orgId || "").trim() !== requestedOrgId)) {
    if (trackerApiEnabled()) return openServerClaimDrawer(claimId, options);
    return denyAction(PERMISSIONS.VIEW_CLAIM_NOTES, `open claim ${claimId}`);
  }
  beginClaimWorkTelemetry(claimId, options);
  renderClaimDrawer(claim, { serverHydrated: false, focusServiceId: options.serviceId || "" });
}

// THE ROW THE USER CLICKED IS ALREADY IN THE BROWSER.
//
// Opening a claim used to show a spinner and nothing else until the whole
// server-backed detail read came back, so every drawer open cost a warehouse
// round trip before the biller could read the claim id, the client, the
// payer, the status or the follow-up they opened it to work. That row is
// already rendered in the grid, so the identity half of the drawer can paint
// immediately and the supporting sections can fill in behind it.
//
// WHAT THIS MAY AND MAY NOT CARRY. A tracker row is ONE SERVICE LINE, so it
// knows its own line and nothing about the claim's other lines. Presenting
// its figures as the claim's would be the exact defect recorded at
// mergeTrackerAdapterClaimRows - a nine-line claim rendered as $994 of
// $7,810 - so the caller paints this with detailPending set and EVERY money
// tile, the service-line table, patient responsibility, payment attribution
// and the reconciliation panel render a labelled loading state instead of a
// number. Identity, status, follow-up action/date and aging are claim/line
// facts the row genuinely carries, and those are what paints.
//
// Claim ids are an org-scoped EZClaim sequence, so the org must match or two
// different tenants' claims share a number. An explicit serviceId is a strict
// target: a line that is not on the loaded page is a miss, never row one.
function claimDrawerCoreClaimFromRow(claimId, orgId = "", serviceId = "") {
  try {
    if (!trackerApiEnabled() || trackerApiPageState.status !== "ok") return null;
    const rows = Array.isArray(trackerApiPageState.response?.rows) ? trackerApiPageState.response.rows : [];
    const normalizedId = normalizeClaimId(claimId);
    if (!normalizedId) return null;
    const org = String(orgId || "").trim();
    const service = String(serviceId || "").trim();
    const rowOrgId = (row) => String(firstPresent(row.orgId, row.org_id) || "").trim();
    const rowServiceId = (row) => String(firstPresent(row.serviceId, row.service_id) || "").trim();
    const candidates = rows.filter((row) => trackerApiClaimId(row) === normalizedId && (!org || rowOrgId(row) === org));
    if (!candidates.length) return null;
    const row = service ? candidates.find((candidate) => rowServiceId(candidate) === service) : candidates[0];
    if (!row) return null;
    const claim = trackerApiRowToClaim(row);
    if (!claim || claim.claimId !== normalizedId) return null;
    // Read by claimNotesPanelHtml and renderClaimDrawer: this claim is one row,
    // not the served claim record, and nothing may be written from it.
    claim.detailPending = true;
    claim.claimNotes = { items: [], status: "loading", hasMore: false };
    return claim;
  } catch (_) { return null; }
}

function openServerClaimDrawer(claimId, options = {}) {
  if (activeClientGroupTenant() && options.orgId && !activeClientGroupOrgIds().includes(String(options.orgId))) { showToast("Choose this claim’s company at the top of the page before opening it."); return; }
  // Full Clients open the claim drawer (Nathan, 2026-10-13: "We are a very transparent company"); the server scopes it to their org and they hold rate access. RedactedClient stays out: the drawer carries contracted rates in nested pricing detail and that role excludes insurer money by design.
  if (isRedactedClientRole(state?.role)) return denyAction(PERMISSIONS.VIEW_FINANCIALS, `open claim drawer ${claimId}`);
  // Reachable directly from the Authorizations board and the report drilldown,
  // both of which a client role CAN open — so it carries the same role gate as
  // openDrawer, not only the permission one.
  if (!hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES)) return denyAction(PERMISSIONS.VIEW_CLAIM_NOTES, `open claim ${claimId}`);
  const queueContext = normalizeQueueWorkContext(options.queueContext || {});
  beginClaimWorkTelemetry(claimId, { serviceId: options.serviceId, queueContext, source: queueContext.source });
  if (queueContext.queueKey || queueContext.queueSessionId || queueContext.source) {
    state.queueWorkContextByClaim = {
      ...(state.queueWorkContextByClaim || {}),
      [String(claimId)]: {
        ...queueContext,
        capturedAt: timestamp()
      }
    };
    saveState();
  }
  els.drawerKicker.textContent = "Server-backed tracker detail";
  els.drawerTitle.textContent = claimId;
  setDrawerPatientName("");
  els.drawerBody.innerHTML = `
    <section class="panel drawer-loading-panel" aria-live="polite">
      <div class="drawer-loading-row">
        <span class="inline-spinner" aria-hidden="true"></span>
        <div>
          <h3>Loading Claim Detail</h3>
          <p>This may take a few seconds while the portal pulls the full server-backed claim record.</p>
        </div>
      </div>
    </section>
  `;
  openDrawerShell();
  const base = options.apiBase || trackerApiBase();
  if (!base || typeof fetch !== "function") {
    els.drawerBody.innerHTML = `<section class="panel"><h3>Detail unavailable</h3><p>Claim details are temporarily unavailable. Please try again.</p></section>`;
    return;
  }
  const token = options.apiToken || trackerApiToken();
  const resolvedOrgId = String(options.orgId || getClaim(claimId)?.orgId || "").trim();
  const params = new URLSearchParams();
  ["queueKey", "queueSessionId", "source"].forEach((key) => {
    if (queueContext[key]) params.set(key, queueContext[key]);
  });
  if (resolvedOrgId) params.set("orgId", resolvedOrgId);
  if (options.serviceId) params.set("serviceId", options.serviceId);
  params.set("includeHistory", "false");
  const query = params.toString() ? `?${params.toString()}` : "";
  const requestParts = { base, claimId: String(claimId || ""), query };
  const requestKey = workflowAccessScopedRequestKey(requestParts);
  serverClaimDrawerController?.abort();
  const controller = new AbortController();
  serverClaimDrawerController = controller;
  activeClaimDrawerContext = null;
  const pinnedReportContext = typeof reportReadSurface === "function" && reportReadSurface() ? syncReportReadContext() : null;
  const requestStillCurrent = () => (
    serverClaimDrawerRequestKey === requestKey
    && serverClaimDrawerController === controller
    && (!pinnedReportContext || reportReadContextCurrent(pinnedReportContext))
    && workflowAccessScopedRequestKey(requestParts) === requestKey
  );
  serverClaimDrawerRequestKey = requestKey;
  const orgId = resolvedOrgId;
  const noteParams = new URLSearchParams({ orgId, limit: "50" });
  if (options.serviceId) noteParams.set("serviceId", options.serviceId);
  const requestOptions = { signal: controller.signal, isCurrent: requestStillCurrent };
  // CORE FIRST, IN THIS TURN. The generation fence above is already set, so a
  // previously opened claim can neither paint here nor survive here: this runs
  // synchronously under the request key that was just installed, and the
  // context it publishes carries that key so a late section from the previous
  // claim fails its own requestKey check and is discarded.
  const coreClaim = claimDrawerCoreClaimFromRow(claimId, orgId, options.serviceId);
  if (coreClaim) {
    activeClaimDrawerContext = {
      base,
      token,
      claim: coreClaim,
      claimId: String(claimId || ""),
      orgId: orgId || coreClaim.orgId,
      focusServiceId: options.serviceId || "",
      deniedColumns: [],
      requestKey, pinnedReportContext,
      detailPending: true
    };
    renderClaimDrawer(coreClaim, { serverHydrated: false, detailPending: true, focusServiceId: options.serviceId || "" });
  }
  const timeout = setTimeout(() => controller.abort(), 15000);
  const detailRequest = (async () => {
    let detailUrl = base + "/api/tracker/claims/" + encodeURIComponent(claimId) + query;
    if (pinnedReportContext) {
      const lease = await ensureReportReadSession(pinnedReportContext);
      if (!requestStillCurrent()) throw new DOMException("The claim view changed.", "AbortError");
      detailUrl = reportPinnedReadUrl(detailUrl, lease);
    }
    return claimDrawerFetchJson(detailUrl, token, requestOptions);
  })();
  let notesEnvelope = null;
  const settleNotes = (envelope) => {
    notesEnvelope = envelope;
    const context = activeClaimDrawerContext;
    if (!requestStillCurrent() || !context || context.requestKey !== requestKey) return;
    if (context.claim.claimNotes?.status !== "loading") return;
    context.claim.claimNotes = envelope.ok
      ? normalizeClaimNotesResponse(envelope.body)
      : claimNoteLoadFailure(envelope, "notes");
    repaintClaimLedgerPage(context, "notes");
  };
  const notesRequest = claimDrawerFetchJson(base + "/api/tracker/claims/" + encodeURIComponent(claimId) + "/notes?" + noteParams, token, requestOptions)
    .catch(() => ({ ok: false, status: 503, body: {} }))
    .then(settleNotes);
  void Promise.allSettled([detailRequest, notesRequest]).then(() => clearTimeout(timeout));
  detailRequest.then((detailEnvelope) => {
      const response = detailEnvelope;
      const body = detailEnvelope.body || {};
      if (!requestStillCurrent()) return;
      if (!response.ok || !body.claimDetail) {
        // A core paint published a context so the notes read could land on it.
        // The detail read has now failed, so drop it: nothing may act on a claim
        // record the server never served.
        activeClaimDrawerContext = null;
        if (response.status === 401 && typeof notePortalSessionExpired === "function") {
          notePortalSessionExpired();
          els.drawerBody.innerHTML = `<section class="panel"><h3>Sign in again</h3><p>Your session expired. Sign in again — this page will still be here.</p></section>`;
          return;
        }
        els.drawerBody.innerHTML = `<section class="panel"><h3>Detail unavailable</h3><p>${escapeHtml(serverClaimDrawerFailureText(body, response.status))}</p></section>`;
        return;
      }
      const claim = serverDetailToDrawerClaim({ ...body.claimDetail, lifeHistory: body.lifeHistory });
      claim.claimNotes = !notesEnvelope ? { items: [], status: "loading", hasMore: false }
        : notesEnvelope.ok ? normalizeClaimNotesResponse(notesEnvelope.body) : claimNoteLoadFailure(notesEnvelope, "notes");
      activeClaimDrawerContext = {
        base,
        token,
        claim,
        claimId: String(claimId || ""),
        orgId: orgId || claim.orgId,
        focusServiceId: options.serviceId || "",
        deniedColumns: Array.isArray(body.decision?.deniedColumns) ? body.decision.deniedColumns : [],
        requestKey, pinnedReportContext,
        // Which supporting sections answered and which could not. Kept on the
        // context so a later repaint of this same drawer keeps telling the
        // truth about them instead of silently healing them.
        sectionStatus: body.sectionStatus && typeof body.sectionStatus === "object" ? body.sectionStatus : null
      };
      renderClaimDrawer(claim, {
        serverHydrated: true,
        deniedColumns: activeClaimDrawerContext.deniedColumns,
        sectionStatus: activeClaimDrawerContext.sectionStatus,
        focusServiceId: options.serviceId || ""
      });
    })
    .catch((error) => {
      if (!requestStillCurrent()) return;
      activeClaimDrawerContext = null;
      els.drawerBody.innerHTML = `<section class="panel"><h3>Detail unavailable</h3><p>${escapeHtml(error.message)}</p></section>`;
    });
}

async function claimDrawerFetchJson(url, token, options = {}) {
  const response = await fetch(url, {
    ...(options.signal ? { signal: options.signal } : {}),
    method: "GET",
    cache: "no-store",
    headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) }
  });
  const body = await response.json().catch(() => ({}));
  if (response.status === 401 && (!options.isCurrent || options.isCurrent()) && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
  return { ok: response.ok, status: response.status, body };
}

function claimNoteLoadFailure(envelope = {}, kind = "notes") {
  return {
    items: [],
    hasMore: false,
    nextCursor: "",
    status: envelope.status === 403 ? "forbidden" : "error",
    error: envelope.status === 403
      ? "This history is not available for your role."
      : kind === "audit" ? "Audit history could not be loaded. Try again." : "Claim notes could not be loaded. Try again."
  };
}

function normalizeClaimNoteItem(item = {}) {
  const source = item && typeof item === "object" ? item : {};
  const permissions = source.permissions && typeof source.permissions === "object" ? source.permissions : {};
  const serviceLineIds = Array.isArray(source.serviceLineIds)
    ? Array.from(new Set(source.serviceLineIds.map((value) => String(value || "").trim()).filter(Boolean)))
    : [];
  const legacyServiceLineId = String(source.serviceLineId || source.service_line_id || "").trim();
  if (!serviceLineIds.length && legacyServiceLineId) serviceLineIds.push(legacyServiceLineId);
  return {
    noteId: String(source.noteId || source.id || source.eventId || ""),
    rootNoteId: String(source.rootNoteId || source.root_id || source.noteId || source.id || ""),
    revision: Math.max(1, Number(source.revision || source.version || 1) || 1),
    eventType: String(source.eventType || source.event_type || source.type || "create").toLowerCase(),
    scopeMode: String(source.scopeMode || source.scope_mode || (serviceLineIds.length ? "service_lines" : "claim")),
    serviceLineIds,
    serviceLineId: legacyServiceLineId,
    text: String(source.text || source.noteText || source.note_text || source.note || ""),
    authorFullName: String(source.authorFullName || source.authorDisplayName || source.author_full_name || source.author_display_name || source.fullName || source.by || source.author || "Author not recorded"),
    at: String(source.createdAtUtc || source.created_at_utc || source.eventAtUtc || source.event_at_utc || source.at || ""),
    sourceTimestampRaw: String(source.sourceTimestampRaw || source.source_timestamp_raw || ""),
    sourceTimestampPrecision: String(source.sourceTimestampPrecision || source.source_timestamp_precision || ""),
    sourceTimestampTimezone: String(source.sourceTimestampTimezone || source.source_timestamp_timezone || ""),
    sourceEventDate: String(source.sourceEventDate || source.source_event_date || ""),
    sourceEventTime: String(source.sourceEventTime || source.source_event_time || ""),
    source: String(source.sourceLabel || source.source || source.origin || "Portal"),
    corrected: booleanFlagValue(source.corrected) || Number(source.revision || source.version || 1) > 1,
    voided: booleanFlagValue(source.voided) || String(source.eventType || source.event_type || "").toLowerCase() === "void",
    canCorrect: permissions.correct === true || source.canCorrect === true,
    canVoid: permissions.void === true || source.canVoid === true
  };
}

function normalizeClaimNotesResponse(body = {}) {
  const rawItems = Array.isArray(body.items) ? body.items
    : Array.isArray(body.notes) ? body.notes
      : Array.isArray(body.entries) ? body.entries
        : Array.isArray(body.rows) ? body.rows : [];
  const normalizedItems = rawItems.map(normalizeClaimNoteItem);
  const visibleItems = isClientRole()
    ? normalizedItems.filter((item, index, all) => all.findIndex((candidate) => (candidate.rootNoteId || candidate.noteId) === (item.rootNoteId || item.noteId)) === index)
    : normalizedItems;
  const sourceCompleteness = body.sourceCompleteness || body.source_completeness || body.completeness || null;
  return {
    items: visibleItems,
    hasMore: booleanFlagValue(body.hasMore || body.has_more),
    nextCursor: String(body.nextCursor || body.next_cursor || body.cursor || ""),
    status: sourceCompleteness && sourceCompleteness.exposed === false ? "hidden" : "ok",
    sourceCompleteness
  };
}

function normalizeClaimNoteAuditResponse(body = {}) {
  const rawItems = Array.isArray(body.items) ? body.items
    : Array.isArray(body.events) ? body.events
      : Array.isArray(body.entries) ? body.entries
        : Array.isArray(body.rows) ? body.rows : [];
  const sourceCompleteness = body.sourceCompleteness || body.source_completeness || body.completeness || null;
  return {
    items: rawItems.map((item) => ({
      ...normalizeClaimNoteItem(item),
      kind: String(item.kind || item.eventType || item.event_type || item.action || "event"),
      action: String(item.action || item.title || item.kind || "Audit event"),
      serviceLineId: String(item.serviceLineId || item.service_line_id || ""),
      status: String(item.status || ""),
      actionLabel: String(item.actionLabel || item.action_label || ""),
      actorKind: String(item.actorKind || item.actor_kind || "").toLowerCase(),
      actorDisplayName: String(item.actorDisplayName || item.actor_display_name || ""),
      actorDisplayNameResolved: !(item.actorDisplayNameResolved === false || item.actor_display_name_resolved === false),
      ruleId: String(item.ruleId || item.rule_id || ""),
      ruleLabel: String(item.ruleLabel || item.rule_label || ""),
      sourceLabel: String(item.sourceLabel || item.source_label || ""),
      followUpVersion: item.followUpVersion || null,
      previousFollowUpAction: String(item.previousFollowUpAction || item.previous_follow_up_action || ""),
      nextFollowUpAction: String(item.nextFollowUpAction || item.next_follow_up_action || ""),
      previousFollowUpDate: String(item.previousFollowUpDate || item.previous_follow_up_date || ""),
      nextFollowUpDate: String(item.nextFollowUpDate || item.next_follow_up_date || ""),
      at: String(item.touchedAtUtc || item.touched_at_utc || item.createdAtUtc || item.created_at_utc || item.eventAtUtc || item.event_at_utc || item.at || ""),
      reason: String(item.reason || item.voidReason || item.void_reason || "")
    })),
    hasMore: booleanFlagValue(body.hasMore || body.has_more),
    nextCursor: String(body.nextCursor || body.next_cursor || body.cursor || ""),
    status: body.hidden || sourceCompleteness?.exposed === false ? "hidden" : "ok",
    sourceCompleteness
  };
}

// A refusal from /api/tracker/claims/:claimId carries decision.reason and no
// message, so the drawer used to render "HTTP 404" at a biller. Translate the
// reasons we own into a sentence, and keep "we do not have it" and "you may not
// see it" visibly DIFFERENT - a not-found and a denial must never read the same
// (CLAIM_LIFE_HISTORY_CONTRACT section 4). The text is deliberately generic: no
// claim contents and no patient identifier ever go into failure copy.
function serverClaimDrawerFailureText(body = {}, status = 0) {
  const reason = String((body && body.decision && body.decision.reason) || (body && body.reason) || "");
  if (reason === "claim_not_found") return "We could not find this claim. It may not have reached the tracker yet, or it may have been removed.";
  if (reason === "out_of_scope") return "This claim sits outside the clients your account can see.";
  if (body && body.message) return String(body.message);
  if (body && body.error) return String(body.error);
  if (status === 401) return "Your session expired. Sign in again — this page will still be here.";
  if (status === 404) return "We could not find this claim.";
  if (status === 403) return "You do not have access to this claim.";
  return `Something went wrong loading this claim (HTTP ${status}).`;
}

function normalizeQueueWorkContext(context = {}) {
  return {
    queueKey: safeContextToken(context.queueKey, 80),
    queueSessionId: safeContextToken(context.queueSessionId, 120),
    source: safeContextToken(context.source, 80)
  };
}

function safeContextToken(value, maxLength) {
  return String(value || "").replace(/[^\w:./@-]+/g, "-").slice(0, maxLength);
}

function serverDetailToDrawerClaim(detail = {}) {
  const source = detail && typeof detail === "object" ? detail : {};
  return {
    claimId: String(source.claimId || ""),
    orgId: String(source.orgId || source.org_id || ""),
    client: String(source.client || ""),
    facility: String(source.facility || ""),
    patient: String(source.patient || ""),
    renderingProvider: String(source.renderingProvider || ""),
    claimStatus: String(source.claimStatus || ""),
    claimClassification: String(source.claimClassification || ""),
    lifecycleStage: String(source.lifecycleStage || ""),
    statusCategory: String(source.statusCategory || ""),
    recommendedNextAction: String(source.recommendedNextAction || ""),
    claimDue: String(source.claimDue || ""),
    action: String(source.action || ""),
    followUpDate: String(source.followUpDate || ""),
    match: String(source.match || ""),
    // THE BLANK AGING PILL, 2026-09-17. This mapper is the drawer's whole view
    // of the hosted response: anything it does not copy does not exist for the
    // drawer. `aging` was served by the API and rendered by the hero card, and
    // was lost in between - the local/demo path passes a grid row straight
    // through, so the defect was invisible everywhere except the live site.
    aging: String(source.aging || ""),
    billDateAging: agingDaysNumber(source.billDateAging),
    totalExpected: source.totalExpected,
    totalPaid: source.totalPaid,
    owed: source.owed,
    primaryPayer: String(source.primaryPayer || ""),
    primaryPayerId: String(source.primaryPayerId || source.primaryPayorId || source.primaryExternalId || source.primaryPayerExternalId || ""),
    primaryExternalId: String(source.primaryExternalId || source.primaryPayerExternalId || source.primaryPayerId || source.primaryPayorId || ""),
    secondaryPayer: String(source.secondaryPayer || ""),
    secondaryPayerId: String(source.secondaryPayerId || source.secondaryPayorId || source.secondaryExternalId || source.secondaryPayerExternalId || ""),
    secondaryExternalId: String(source.secondaryExternalId || source.secondaryPayerExternalId || source.secondaryPayerId || source.secondaryPayorId || ""),
    externalPayerStatus: String(source.externalPayerStatus || ""),
    paymentAttribution: source.paymentAttribution && typeof source.paymentAttribution === "object" ? source.paymentAttribution : {},
    paymentEventsUnavailable: booleanFlagValue(source.paymentEventsUnavailable),
    paymentEventsTruncated: booleanFlagValue(source.paymentEventsTruncated),
    serviceLinesTruncated: booleanFlagValue(source.serviceLinesTruncated),
    serviceLineCount: Number(source.serviceLineCount) || 0,
    servicePage: source.servicePage?.complete === true ? {...source.servicePage} : null,
    preparedGeneration: String(source.preparedGeneration || ""),
    preparedFinancialSummary: source.preparedFinancialSummary || null,
    preparedPrSummary: source.preparedPrSummary || null,
    reconciliationServiceTotals:source.reconciliationServiceTotals || null,
    preparedGuardCounts: source.preparedGuardCounts || null,
    // The claim-grain payer adjustment total (2026-09-23), with the reason it is
    // absent when the lane did not serve it. Per-line figures ride on services.
    payerAdjustmentSummary: source.payerAdjustmentSummary && typeof source.payerAdjustmentSummary === "object" ? { ...source.payerAdjustmentSummary } : null,
    focusService: source.focusService ? normalizeServerDetailServices([source.focusService],source)[0] : null,
    payments: Array.isArray(source.payments) ? source.payments : [],
    services: normalizeServerDetailServices(source.services, source),
    assignedTo: String(source.assignment?.assignedTo || ""),
    assignedBy: String(source.assignment?.assignedBy || ""),
    assignmentDue: String(source.assignment?.assignmentDue || ""),
    assignmentStatus: String(source.assignment?.assignmentStatus || ""),
    notes: Array.isArray(source.notes) ? source.notes : [],
    workflowHistory: normalizeServerWorkflowHistory(source.workflowHistory),
    lifeHistory: source.lifeHistory && typeof source.lifeHistory === "object" ? source.lifeHistory : null
  };
}

function normalizeServerWorkflowHistory(history = {}) {
  const source = history && typeof history === "object" ? history : {};
  return {
    entries: Array.isArray(source.entries) ? source.entries : [],
    redacted: booleanFlagValue(source.redacted),
    totalEvents: nonNegativeIntegerValue(source.totalEvents),
    source: String(source.source || "")
  };
}

function renderOffshoreClaimActionPanel(claim) {
  const status = String(claim.assignmentStatus || "Routed to Offshore Review");
  const isTodo = /Routed|Assigned/i.test(status) && !/Progress|Submitted|Bounced/i.test(status);
  const isInProgress = /In Progress|Working/i.test(status);
  const isSubmitted = /Submitted/i.test(status);
  const isBounced = /Bounced/i.test(status);
  const tone = isSubmitted ? "teal" : isInProgress ? "amber" : isBounced ? "red" : "blue";
  const claimId = escapeHtml(claim.claimId);
  const orgId = escapeHtml(claim.orgId || "");
  return `
    <section class="panel offshore-action-panel offshore-action-panel--${tone}">
      <header class="offshore-action-head">
        <div>
          <span class="offshore-action-eyebrow">Your Task</span>
          <strong>Status: ${escapeHtml(status)}</strong>
        </div>
        <div class="offshore-action-buttons">
          ${!isInProgress && !isSubmitted ? `<button type="button" class="primary-button" data-offshore-start="${claimId}" data-offshore-org-id="${orgId}">Start Work</button>` : ""}
          ${!isSubmitted ? `<button type="button" class="primary-button" data-offshore-submit="${claimId}" data-offshore-org-id="${orgId}">Submit for Review</button>` : ""}
          ${isSubmitted ? `<span class="offshore-action-sentinel">Awaiting stateside sign-off</span>` : ""}
        </div>
      </header>
      ${isBounced && claimBounceReason(claim) ? `
        <div class="offshore-bounce-callout">
          <span class="offshore-bounce-callout-label">Stateside sent this back</span>
          <p>${escapeHtml(claimBounceReason(claim))}</p>
          ${claim.bounceBy || claim.bounceAt || claim.reviewedBy || claim.reviewedAt ? `<small>${escapeHtml([claim.bounceBy || claim.reviewedBy || "", (claim.bounceAt || claim.reviewedAt) ? formatDate(claim.bounceAt || claim.reviewedAt) : ""].filter(Boolean).join(" · "))}</small>` : ""}
        </div>
      ` : ""}
      ${claim.assignmentInstruction ? `
        <div class="offshore-action-note offshore-manager-instructions">
          <span class="offshore-action-note-label">Manager instructions</span>
          <p>${escapeHtml(claim.assignmentInstruction)}</p>
          ${claim.instructionBy || claim.instructionAt ? `<small>${escapeHtml([claim.instructionBy || "", claim.instructionAt ? formatDate(claim.instructionAt) : ""].filter(Boolean).join(" · "))}</small>` : ""}
        </div>
      ` : ""}
      <div class="offshore-action-note">
        <label for="offshoreNoteInput-${claimId}" class="offshore-action-note-label">Add note for stateside</label>
        <textarea id="offshoreNoteInput-${claimId}" class="offshore-action-note-input" rows="3" placeholder="What did you do? What does stateside need to know?"></textarea>
        ${!isSubmitted ? `
          <div class="offshore-next-action">
            <label for="offshoreNextAction-${claimId}">
              Next follow-up action
              <select id="offshoreNextAction-${claimId}" class="grid-select" data-offshore-next-action="${claimId}" data-offshore-org-id="${orgId}" data-current-action="${escapeHtml(claim.action || "")}">
                <option value="">Choose next action...</option>
                ${actionOptions("")}
              </select>
            </label>
            <label for="offshoreNextDate-${claimId}">
              Next due date
              <input id="offshoreNextDate-${claimId}" type="date" data-offshore-next-date="${claimId}" data-offshore-org-id="${orgId}" value="">
            </label>
          </div>
        ` : ""}
        <div class="offshore-action-note-actions">
          <button type="button" class="ghost-button" data-offshore-add-note="${claimId}" data-offshore-org-id="${orgId}">Add note</button>
        </div>
      </div>
    </section>
  `;
}

function defaultFollowUpDateForAction(action, fallback = "") {
  const rule = followUpRuleForAction(action);
  if (rule && Number.isFinite(rule.offset)) return addDays(todayDateString(), rule.offset);
  return fallback || "";
}

// Resolve the server-loaded drawer, never a same-numbered claim on another page.
function offshoreDrawerTarget(claimId, orgId, permission) {
  const context = activeClaimDrawerContext;
  const claim = context?.claim;
  if (!claim || context.pinnedReportContext || context.claimId !== String(claimId)
    || String(claim.claimId || "") !== String(claimId)
    || String(context.orgId || claim.orgId || "") !== String(orgId)
    || String(claim.orgId || "") !== String(orgId)
    || !canActOnClaim(claim, permission)) {
    denyAction(permission, "offshore drawer");
    return null;
  }
  if (activeClientGroupTenant() && !activeClientGroupOrgIds().includes(String(orgId))) {
    showToast("Choose this claim's company before saving.");
    return null;
  }
  const serviceId = String(context.focusServiceId || "");
  const service = claimAvailableServiceChoices(claim).find(line => String(line.serviceId) === serviceId);
  if (!serviceId || !service) {
    showToast("Open the service line you want to update before saving.");
    return null;
  }
  return { context, claim, service, serviceId, orgId: String(orgId), claimId: String(claimId) };
}

async function submitOffshoreDrawerFollowUp(claimId, orgId, note, action, followUpDate) {
  const target = offshoreDrawerTarget(claimId, orgId, PERMISSIONS.UPDATE_FOLLOW_UP);
  if (!target) return;
  if (!hasPermission(PERMISSIONS.ADD_CLAIM_NOTE)) return denyAction(PERMISSIONS.ADD_CLAIM_NOTE);
  if (!String(note || "").trim()) return showToast("Enter the required claim note before submitting a follow-up action.");
  if (!String(action || "").trim()) return showToast("Choose the next follow-up action.");
  if (followUpEditorState) return showToast("Finish the open follow-up edit before submitting another.");
  openFollowUpEditor(claimId, {
    orgId, serviceId: target.serviceId, action, followUpDate,
    currentAction: String(target.service.action || ""),
    expectedUpdatedAt: String(target.service.followUpUpdatedAt || ""),
    sourceSurface: "claim-drawer-offshore"
  });
  if (!followUpEditorState || followUpEditorState.claimId !== claimId
    || followUpEditorState.orgId !== orgId || followUpEditorState.serviceId !== target.serviceId) return;
  setValue(els.followUpEditorNote, note);
  setText(els.followUpEditorNoteCount, String(note.length));
  // Reuse the normal save, approval routing, version checks and retained-error draft.
  await submitFollowUpEditor({ preventDefault() {} });
}

function bindOffshoreActionPanel() {
  document.querySelectorAll("[data-offshore-next-action]").forEach((select) => {
    select.addEventListener("change", () => {
      const cid = select.dataset.offshoreNextAction;
      const dateInput = document.querySelector(`[data-offshore-next-date="${escapeCss(cid)}"]`);
      if (dateInput) dateInput.value = defaultFollowUpDateForAction(select.value);
    });
  });
  document.querySelectorAll("[data-offshore-start]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const cid = btn.dataset.offshoreStart;
      const orgId = btn.dataset.offshoreOrgId || "";
      // Reopen on the SAME line and company. A bare openDrawer(cid) dropped the
      // focused service line, so the biller's next "Add note" was refused with
      // "Open the service line you want to update before saving" (2026-10-08,
      // claim 6048) - and without the org a same-numbered claim could open.
      const context = activeClaimDrawerContext;
      const serviceId = context && String(context.claimId) === String(cid) ? String(context.focusServiceId || "") : "";
      mutateClaim(cid, { assignmentStatus: "In Progress" });
      enqueueWorkflowCommand("start-work", { claimId: cid, orgId });
      audit(`${cid}: offshore biller started work`, activeInternalUser());
      saveState();
      showToast("Marked In Progress.");
      openDrawer(cid, { orgId, serviceId });
    });
  });
  document.querySelectorAll("[data-offshore-submit]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const cid = btn.dataset.offshoreSubmit;
      const orgId = btn.dataset.offshoreOrgId || "";
      const noteInput = document.getElementById(`offshoreNoteInput-${cid}`);
      const noteText = (noteInput?.value || "").trim();
      const nextAction = document.querySelector(`[data-offshore-next-action="${escapeCss(cid)}"]`)?.value || "";
      const nextDate = document.querySelector(`[data-offshore-next-date="${escapeCss(cid)}"]`)?.value || "";
      btn.disabled = true;
      try { await submitOffshoreDrawerFollowUp(cid, orgId, noteText, nextAction, nextDate); }
      finally { if (btn.isConnected) btn.disabled = false; }
    });
  });
  document.querySelectorAll("[data-offshore-add-note]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const cid = btn.dataset.offshoreAddNote;
      const noteInput = document.getElementById(`offshoreNoteInput-${cid}`);
      const noteText = (noteInput?.value || "").trim();
      if (!noteText) {
        showToast("Type a note first.");
        noteInput?.focus();
        return;
      }
      const target = offshoreDrawerTarget(cid, btn.dataset.offshoreOrgId || "", PERMISSIONS.ADD_CLAIM_NOTE);
      if (!target || btn.disabled) return;
      btn.disabled = true;
      const context = target.context;
      postClaimNoteApi("/api/workflow/note", {
        orgId: target.orgId,
        claimId: cid,
        scopeMode: "service_lines",
        serviceLineIds: [target.serviceId],
        text: noteText,
        commandId: claimNoteCommandId("offshore-note"),
        sourceSurface: "offshore-claim-panel"
      }, context).then(() => {
        invalidatePortalReadsAfterWorkflowWrite();
        showToast("Claim note added.");
        if (activeClaimDrawerContext === context) {
          if (noteInput) noteInput.value = "";
          openDrawer(cid, { orgId: target.orgId, serviceId: target.serviceId });
        }
      }).catch((error) => showToast(error.message || "Claim note could not be saved."))
        .finally(() => { if (btn.isConnected) btn.disabled = false; });
    });
  });
}

function presentableDrawerValue(value) {
  const text = String(value ?? "")
    .replace(/,?\s*<\s*blank\s*>/gi, "")
    .trim()
    .replace(/^[,\s]+|[,\s]+$/g, "");
  if (!text || /^(undefined|null)$/i.test(text)) return "";
  return text;
}

function drawerExternalPayerIdText(value) {
  return presentableDrawerValue(value) || "";
}

function drawerFocusService(claim, focusServiceId) {
  const wanted = String(focusServiceId || "").trim();
  const services = claim && Array.isArray(claim.services) ? claim.services : [];
  const exactFocus = claim?.focusService;
  if (exactFocus && (!wanted || String(exactFocus.serviceId) === wanted)) return exactFocus;
  return services.find((service) => String(service.serviceId || "").trim() === wanted) || (wanted ? null : services[0]) || null;
}

function drawerExpectedExplain(service, claim) {
  if (!service) return "";
  const fin = serviceFinancialView(service, claim);
  if (fin.primaryRateEstimated) return "Estimated using a procedure-based default; no primary fee-schedule rate is on file. The missing-rate match remains open.";
  if (fin.primaryRateAbsent || fin.primaryRateMissing) return "No rate on file — expected is not a priced number.";
  const units = Number(service.units);
  const rate = Number(fin.primaryRate);
  const expected = Number(fin.expectedReimbursement);
  // A RATE OF $0 IS NOT A RATE. An absent primary rate reaches the drawer as 0
  // (numberValue(null) === 0 on the adapter, WHY_THIS_NUMBER_SPEC.md section 0), so this
  // printed "8 units × $0 = $456" under a real $456 on hosted TEST (org 40699714,
  // claim 33027, 2026-09-03) - an equation that is false on its face, showing the one
  // input that is unknown as a stated zero. Print the arithmetic only when it is true
  // to the cent; otherwise name the number and say the rate is not on file.
  if (Number.isFinite(units) && units > 0 && Number.isFinite(rate) && rate > 0 && Number.isFinite(expected) && Math.abs(units * rate - expected) < 0.005) {
    return units + " units × " + money(rate) + " = " + money(fin.expectedReimbursement) + ".";
  }
  if (Number.isFinite(rate) && rate <= 0) return "Expected " + money(fin.expectedReimbursement) + " from the stored expected amount — no per-unit rate is on file for this line.";
  return "Expected " + money(fin.expectedReimbursement) + ".";
}

function claimNoteSourceLabel(value) {
  const source = String(value || "Portal").trim();
  if (/service.?line/i.test(source)) return "Imported biller note";
  if (/ezclaim/i.test(source)) return "Legacy history";
  return "Portal note";
}

function claimNoteTimestampLabel(note = {}) {
  const timezone = String(note.sourceTimestampTimezone || "").trim().toLowerCase();
  if (timezone === "unknown") {
    const raw = String(note.sourceTimestampRaw || "").trim()
      || [String(note.sourceEventDate || "").trim(), String(note.sourceEventTime || "").trim()].filter(Boolean).join(" ");
    return raw ? `${raw} (source time; timezone not recorded)` : "Source timestamp timezone not recorded";
  }
  return note.at ? formatDateTime(note.at) : "Timestamp not recorded";
}

function claimNoteCanCorrect(note = {}) {
  if (!hasPermission(PERMISSIONS.CORRECT_OWN_CLAIM_NOTE) || note.voided) return false;
  if (note.canCorrect) return true;
  return String(note.authorFullName || "").trim().toLowerCase() === String(activeInternalUser() || "").trim().toLowerCase();
}

function claimNoteScopeLabel(note = {}) {
  const serviceLineIds = Array.isArray(note.serviceLineIds) ? note.serviceLineIds.filter(Boolean) : [];
  if (note.scopeMode !== "service_lines" || !serviceLineIds.length) return "Entire claim";
  if (serviceLineIds.length === 1) return `Service line ${serviceLineIds[0]}`;
  return `${serviceLineIds.length} selected service lines`;
}

function claimNoteCardHtml(note = {}) {
  const stateLabel = note.voided ? "Voided" : note.corrected ? `Corrected · revision ${note.revision}` : "Recorded";
  const sourceLabel = claimNoteSourceLabel(note.source);
  return `
    <article class="claim-note-card${note.voided ? " is-voided" : ""}" data-claim-note-id="${escapeHtml(note.rootNoteId || note.noteId)}">
      <div class="claim-note-card-head">
        <div>
          <strong>${escapeHtml(note.authorFullName || "Author not recorded")}</strong>
          <small>${escapeHtml(claimNoteTimestampLabel(note))} · ${escapeHtml(sourceLabel)}</small>
        </div>
        <div class="claim-note-card-state"><span class="claim-note-scope">${escapeHtml(claimNoteScopeLabel(note))}</span><span class="claim-note-state">${escapeHtml(stateLabel)}</span></div>
      </div>
      ${note.voided ? `<p class="claim-note-voided">This note was voided. Its immutable history remains available to authorized staff.</p>` : `<p>${escapeHtml(note.text || "")}</p>`}
      ${!note.voided && (claimNoteCanCorrect(note) || (hasPermission(PERMISSIONS.VOID_CLAIM_NOTE) && (note.canVoid || !isClientRole()))) ? `
        <details class="claim-note-actions-menu">
          <summary>Note actions</summary>
          <div class="claim-note-card-actions">
            ${claimNoteCanCorrect(note) ? `<button class="link-button" type="button" data-claim-note-correct="${escapeHtml(note.rootNoteId || note.noteId)}" data-note-revision="${note.revision}">Correct note</button>` : ""}
            ${hasPermission(PERMISSIONS.VOID_CLAIM_NOTE) && (note.canVoid || !isClientRole()) ? `<button class="link-button danger-link" type="button" data-claim-note-void="${escapeHtml(note.rootNoteId || note.noteId)}" data-note-revision="${note.revision}">Void note</button>` : ""}
          </div>
        </details>
      ` : ""}
    </article>`;
}

function claimNotesCompletenessText(value) {
  if (!value) return "Portal notes and imported biller notes";
  if (typeof value === "string") return value;
  if (value.exposed === false || value.status === "shadow-verification") {
    return "Imported biller notes are still being verified.";
  }
  const complete = value.complete === true || value.isComplete === true;
  return complete
    ? "Portal notes and imported biller notes"
    : "Some imported biller notes are temporarily unavailable.";
}

function preparedClaimServicePagerHtml(claim) {
  const page = claim.servicePage, pages = Math.max(1,Math.ceil(page.totalRows/page.pageSize));
  const start = (page.page-1)*page.pageSize, count = (claim.services || []).length;
  const label = count ? "Rows "+(start+1).toLocaleString()+"–"+(start+count).toLocaleString()+" of "+page.totalRows.toLocaleString() : "No rows on this page · "+page.totalRows.toLocaleString()+" service lines";
  const button=(target,text,disabled)=>'<button type="button" class="ghost-button" data-prepared-claim-page="'+target+'"'+(disabled||page.pending?' disabled':'')+'>'+text+'</button>';
  return '<div aria-busy="'+Boolean(page.pending)+'"><p role="status">'+escapeHtml(label)+' · Page '+page.page.toLocaleString()+' of '+pages.toLocaleString()+'</p>'+(page.error?'<p role="alert">'+escapeHtml(page.error)+'</p>':'')+'<nav class="report-pager" aria-label="Service line pages">'+button(1,'First',page.page===1)+button(page.page-1,'Previous',page.page<=1)+button(page.page+1,'Next',page.page>=pages)+button(pages,'Last',page.page>=pages)+'</nav></div>';
}

function bindPreparedClaimServicePager(host,claim,repaint) {
  if (!host) return;
  host.querySelectorAll('[data-prepared-claim-page]').forEach(button=>button.addEventListener('click',()=>loadPreparedClaimServicePage(claim,Number(button.dataset.preparedClaimPage),repaint)));
}

async function loadPreparedClaimServicePage(claim,page,repaint) {
  const context=activeClaimDrawerContext;
  if (!context || context.claim!==claim || !claim.servicePage?.complete || !Number.isSafeInteger(page) || page<1) return;
  context.servicePageController?.abort();
  const controller=new AbortController();context.servicePageController=controller;
  const generation=claim.preparedGeneration, access=workflowAccessScopedRequestKey({base:context.base,claimId:context.claimId});
  const current=()=>activeClaimDrawerContext===context && context.servicePageController===controller && workflowAccessScopedRequestKey({base:context.base,claimId:context.claimId})===access && (!context.pinnedReportContext || reportReadContextCurrent(context.pinnedReportContext));
  claim.servicePage.pending=true;claim.servicePage.error='';repaint();claim.repaintNoteChoices?.();
  const timer=setTimeout(()=>controller.abort(),10000);
  try {
    const params=new URLSearchParams({orgId:context.orgId,page:String(page),expectedGeneration:generation,detailShape:'services'});
    if (context.focusServiceId) params.set('serviceId',context.focusServiceId);
    let url=context.base+'/api/tracker/claims/'+encodeURIComponent(context.claimId)+'?'+params;
    if (context.pinnedReportContext) url=reportPinnedReadUrl(url,await ensureReportReadSession(context.pinnedReportContext));
    if (!current()) return;
    const envelope=await claimDrawerFetchJson(url,context.token,{signal:controller.signal,isCurrent:current});
    if (!current()) return;
    if (!envelope.ok) throw Error(envelope.status===409 ? 'This version is no longer available. Your current page is preserved; load the latest data before continuing.' : 'Service lines could not be loaded. Your current page is preserved.');
    const next=serverDetailToDrawerClaim(envelope.body.claimDetail);
    if (!next.servicePage?.complete || next.preparedGeneration!==generation || next.claimId!==claim.claimId || next.orgId!==claim.orgId || ["preparedFinancialSummary","preparedPrSummary","preparedGuardCounts","reconciliationServiceTotals"].some(key=>JSON.stringify(next[key])!==JSON.stringify(claim[key])) || next.servicePage.totalRows!==claim.servicePage.totalRows) throw Error('This claim has changed. Your current page is preserved; reopen it to load current data.');
    claim.services=next.services;claim.servicePage=next.servicePage;
  } catch(error) {
    if(current()) claim.servicePage.error=error.message || 'Service lines could not be loaded. Try again.';
  } finally {
    clearTimeout(timer);if(current()){claim.servicePage.pending=false;repaint();claim.repaintNoteChoices?.();claim.repaintPrPage?.();}
  }
}

function claimDrawerListScope(claim, focusServiceId = "") {
  return JSON.stringify([claim.orgId, claim.claimId, state.role, trackerAccessEnvelope("service"), focusServiceId]);
}

function claimServiceLimitWarningHtml(claim) {
  return claim.serviceLinesTruncated ? '<p class="subtext" role="status"><strong>Service detail is incomplete.</strong> The source limit was reached. These pages cover only the loaded service lines; service-based summaries and note line choices may omit additional lines. No further service pages are available from the source.</p>' : "";
}

function claimAvailableServiceChoices(claim) {
  const rows=[...(claim.services || [])];
  if (claim.focusService && !rows.some(line=>String(line.serviceId)===String(claim.focusService.serviceId))) rows.unshift(claim.focusService);
  return rows.filter(line=>String(line.serviceId || "").trim());
}

function claimNoteChoicesPageHtml(claim, focusServiceId = "", selectedIds = new Set([String(focusServiceId)])) {
  const services = claimAvailableServiceChoices(claim);
  if (claim.servicePage?.complete) return services.map(service=>claimNoteServiceLineChoiceHtml(service,selectedIds.has(String(service.serviceId))?service.serviceId:"")).join("") + preparedClaimServicePagerHtml(claim);
  const paging = managementListPageWindow("claim-note-choices", services, claimDrawerListScope(claim, focusServiceId));
  return claimServiceLimitWarningHtml(claim) + (services.length ? paging.rows.map((service) => claimNoteServiceLineChoiceHtml(service, selectedIds.has(String(service.serviceId)) ? service.serviceId : "")).join("") + managementListPagerHtml(paging, claim.serviceLinesTruncated ? "Loaded note service choices" : "Note service choices") : "<p>Service lines are unavailable. Reload the claim before saving a line-only note.</p>");
}

function claimNoteServiceLineChoiceHtml(service = {}, focusServiceId = "") {
  const serviceId = String(service.serviceId || "").trim();
  if (!serviceId) return "";
  const details = [service.dos ? formatDate(service.dos) : "", service.procedure || service.procedureCode || ""].filter(Boolean).join(" · ");
  return `<label class="claim-note-line-choice"><input type="checkbox" name="claimNoteServiceLine" value="${escapeHtml(serviceId)}" ${serviceId === String(focusServiceId || "") ? "checked" : ""}><span><strong>Line ${escapeHtml(serviceId)}</strong>${details ? `<small>${escapeHtml(details)}</small>` : ""}</span></label>`;
}

function claimNotesPanelHtml(claim = {}, focusServiceId = "") {
  const ledger = claim.claimNotes || { items: [], status: "loading", hasMore: false };
  const items = Array.isArray(ledger.items) ? ledger.items : [];
  // A note is scoped to service lines, and a core-paint claim knows only the
  // one line the row carried - so the scope picker would offer a choice that
  // is not the real one, and a draft typed here would be wiped by the
  // hydration repaint. The composer opens with the served claim.
  const canAdd = hasPermission(PERMISSIONS.ADD_CLAIM_NOTE) && ledger.sourceCompleteness?.exposed !== false && claim.detailPending !== true;
  const services = claimAvailableServiceChoices(claim);
  const focusedLineAvailable = services.some((service) => String(service.serviceId || "") === String(focusServiceId || ""));
  return `
    <section class="panel claim-notes-panel" data-panel="notes">
      <div class="panel-heading-row">
        <div>
          <h3>Claim Notes</h3>
          <p>Biller notes only, newest first. Includes notes entered here and imported Service Line Notes; routine billing-system events are excluded.</p>
        </div>
        <span data-claim-ledger-count="notes">${pill(`${items.length} on this page`, items.length ? "blue" : "muted")}</span>
      </div>
      <div class="claim-note-panel-body">
        <p class="claim-note-source-status">${escapeHtml(claimNotesCompletenessText(ledger.sourceCompleteness))}</p>
      ${canAdd ? `
        <form class="claim-note-composer" data-claim-note-create>
          <label for="newClaimNoteText">Add Claim Note</label>
          <textarea id="newClaimNoteText" rows="4" maxlength="20000" required placeholder="Write a plain-text note"></textarea>
          <details class="drawer-note-scope-details"><summary data-drawer-note-scope-label>Note scope · ${focusedLineAvailable ? "1 service line selected" : "Choose before saving"}</summary>
          <fieldset class="claim-note-scope-picker">
            <legend>Should this note apply to the entire claim or only selected service lines?</legend>
            <div class="claim-note-scope-options">
              <label><input type="radio" name="claimNoteScope" value="claim"><span><strong>Entire claim</strong><small>Everyone will understand this note applies to every line on the claim.</small></span></label>
              <label><input type="radio" name="claimNoteScope" value="service_lines" ${focusedLineAvailable ? "checked" : ""}><span><strong>One or selected service lines</strong><small>This note will not appear as a claim-wide latest note.</small></span></label>
            </div>
            <div class="claim-note-line-choices" data-claim-note-line-choices data-claim-note-focus-service="${escapeHtml(focusedLineAvailable ? String(focusServiceId) : "")}" ${focusedLineAvailable ? "" : "hidden"}>
              ${claimNoteChoicesPageHtml(claim, focusedLineAvailable ? focusServiceId : "")}
            </div>
            <p class="claim-note-scope-status" data-claim-note-scope-status aria-live="polite"></p>
          </fieldset></details>
          <div class="claim-note-composer-foot">
            <small><span data-claim-note-count>0</span> / 20,000 characters</small>
            <button class="primary-button" type="submit">Add Note</button>
          </div>
        </form>
      ` : ""}
${claimLedgerPageHtml(claim, "notes")}
      </div>
    </section>`;
}

function claimNoteAuditPanelHtml(claim = {}) {
  if (!hasPermission(PERMISSIONS.VIEW_CLAIM_NOTE_AUDIT)) return "";
  const ledger = claim.claimNoteAudit || { items: [], status: "loading", hasMore: false };
  const items = Array.isArray(ledger.items) ? ledger.items : [];
  return `
    <section class="panel claim-note-audit-panel" data-panel="claim-note-audit">
      <div class="panel-heading-row">
        <div><h3>Audit History</h3><p>Internal immutable note versions and structured workflow activity. Note text is not copied into workflow metadata.</p></div>
        <span data-claim-ledger-count="audit">${pill(`${items.length} on this page`, items.length ? "blue" : "muted")}</span>
      </div>
      <div class="claim-note-panel-body">
${claimLedgerPageHtml(claim, "audit")}
      </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// WHERE THE MONEY STANDS, IN PLAIN WORDS (owner decision, Nathan 2026-09-23).
// ---------------------------------------------------------------------------
// Display only. Every figure the waterfall shows is one the drawer is already
// served, at the money strip's one grain. The only arithmetic here is the
// FOOTING CHECK, and its result is never displayed as a number: it decides
// whether the honest note appears, nothing else.
//
// The owner's rows, in order: Charged -> Adjusted off by insurance -> Insurance
// paid -> Patient paid -> Still outstanding, split into Insurance still owes and
// Patient owes (the PR 1/2/3 total the drawer already shows as Deductible /
// Coinsurance / Copay). The footing rule is his, on exactly the rows shown:
//   Charged - Adjusted - Insurance paid - Patient paid
//     = Insurance still owes + Patient owes
// "Still outstanding" is a heading over its two parts, never a summed figure,
// because no lane serves that sum and the drawer does not invent one.
//
// UNKNOWN IS NOT ZERO (ENGINEERING_TRAPS #10). A term the drawer is not served
// renders "Not available here", the check does not run, and the note names the
// missing figure. The hosted drawer's `adjustments` list is PR rows only
// (trackerDetailResolver `prAdjustments`; the prepared lane's `adjustments`
// summary is built from those same rows), so a contractual total is never read
// off it. Since 2026-09-23 the claim-detail read serves the payer (non-PR)
// adjustment total itself - per line `payerAdjustmentTotal`, per claim
// `payerAdjustmentSummary` - with a reason whenever a lane did not serve it
// (the prepared lane does not capture it), and only a served figure is shown.
// ---------------------------------------------------------------------------
const OUTSTANDING_WATERFALL_UNFOOTED_NOTE = "Figures from the payer do not add up yet; the outstanding shown is the billing system's.";
const OUTSTANDING_WATERFALL_TERM_LABELS = Object.freeze({
  charged: "charges",
  adjusted: "insurance adjustments",
  insurancePaid: "insurance payments",
  patientPaid: "patient payments",
  insuranceOwes: "the insurance balance",
  patientOwes: "the patient share"
});

function outstandingWaterfallAmount(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function outstandingWaterfallModel(terms = {}) {
  const source = terms && typeof terms === "object" ? terms : {};
  const values = {};
  Object.keys(OUTSTANDING_WATERFALL_TERM_LABELS).forEach((key) => { values[key] = outstandingWaterfallAmount(source[key]); });
  const missing = Object.keys(values).filter((key) => values[key] === null);
  const known = missing.length === 0;
  // Whole cents, each term rounded on its own as it is displayed, so the check
  // is exact integer arithmetic on the rows a reader sees.
  const cents = (value) => Math.round(value * 100);
  const foots = known
    && cents(values.charged) - cents(values.adjusted) - cents(values.insurancePaid) - cents(values.patientPaid)
      === cents(values.insuranceOwes) + cents(values.patientOwes);
  let note = "";
  if (!known) {
    const names = missing.map((key) => OUTSTANDING_WATERFALL_TERM_LABELS[key]);
    const list = names.length === 1 ? names[0] : names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
    note = "Not available here: " + list + ". These figures are not checked against each other; the outstanding shown is the billing system's.";
  } else if (!foots) {
    note = OUTSTANDING_WATERFALL_UNFOOTED_NOTE;
  }
  return { values, missing, known, foots, state: !known ? "unchecked" : (foots ? "foots" : "unfooted"), note };
}

// Contractual and other payer adjustments (every group except PR) at the
// strip's grain. The local source file carries each line's full adjustment list.
// A server-hydrated drawer and the tracker API lane carry PR rows only in that
// list, so there the figure is the one the claim-detail read SERVES for it, or
// "not served" - never $0 and never a sum of the PR-only list.
function drawerServedPayerAdjustments(services = [], { serverHydrated = false, claim = null } = {}) {
  if (serverHydrated || trackerApiEnabled()) return drawerHostedPayerAdjustments(services, claim);
  const lines = Array.isArray(services) ? services : [];
  if (!lines.length || lines.some((service) => !Array.isArray(service?.adjustments))) return null;
  return moneyNumber(lines.reduce((sum, service) => {
    const summary = adjustmentSummary(service);
    return sum + summary.contractualAdjustments + summary.otherPayerAdjustments + summary.unclassifiedAdjustments;
  }, 0));
}

// The served payer adjustment figure. Claim grain (a claim is passed): the
// served claim total, and only when every line of the claim is loaded - a page
// or a truncated claim is never priced as the claim. Line grain: the one line's
// served figure. A blank reason is the server saying "served"; any other reason,
// or a response that predates the field, is not served.
function drawerHostedPayerAdjustments(services = [], claim = null) {
  if (claim) {
    const summary = claim.payerAdjustmentSummary;
    if (!summary || summary.reason !== "" || claim.serviceLinesTruncated || claim.servicePage?.complete) return null;
    const total = outstandingWaterfallAmount(summary.total);
    return total === null ? null : moneyNumber(total);
  }
  const lines = Array.isArray(services) ? services : [];
  if (lines.length !== 1 || !lines[0] || lines[0].payerAdjustmentReason !== "") return null;
  const value = outstandingWaterfallAmount(lines[0].payerAdjustmentTotal);
  return value === null ? null : moneyNumber(value);
}

// What the patient has paid on one line, as served (the detail whitelist's
// `patientPaid`, the prepared lane's patient financials, or the source row).
// Absent is unavailable, not $0.
function drawerServedPatientPaid(service = {}) {
  return outstandingWaterfallAmount(firstPresent(
    service?.patientPaid,
    service?.preparedPatientFinancials?.patientPaid,
    service?.totalPatAmtPaid,
    service?.["Total Pat Amt Paid"]
  ));
}

// The claim's patient payments: the prepared lane's served claim total when it
// is complete, otherwise the loaded lines only when every line is loaded and
// every line states it. A page of a larger claim is never priced as the claim.
function drawerClaimServedPatientPaid(claim = {}) {
  if (claim.servicePage?.complete) {
    const prepared = claim.preparedPrSummary;
    return prepared?.complete === true && Number(prepared.patientPaidUnknownLines) === 0
      ? outstandingWaterfallAmount(prepared.patientPaid)
      : null;
  }
  if (claim.serviceLinesTruncated) return null;
  const values = (Array.isArray(claim.services) ? claim.services : []).map(drawerServedPatientPaid);
  if (!values.length || values.some((value) => value === null)) return null;
  return moneyNumber(values.reduce((sum, value) => sum + value, 0));
}

const PATIENT_SHARE_CODE_WORDS = Object.freeze({ "1": "deductible", "2": "coinsurance", "3": "copay" });
function patientShareWords(codes = []) {
  const present = new Set((Array.isArray(codes) ? codes : []).map((code) => String(code).replace(/\D/g, "")));
  const words = ["1", "2", "3"].filter((code) => present.has(code)).map((code) => PATIENT_SHARE_CODE_WORDS[code]);
  if (!words.length) return "";
  return words.length === 1 ? words[0] : words.slice(0, -1).join(", ") + " and " + words[words.length - 1];
}

// ---------------------------------------------------------------------------
// ONE PLAIN REASON NEXT TO AN OUTSTANDING AMOUNT, PHRASED FOR CLIENTS.
// ---------------------------------------------------------------------------
// Reuses the existing status model - buildClaimStatusProfile's flags, the
// line's follow-up action, Client Action Pending - and only rewords it. No new
// classification: a claim the internal model calls denied reads as denied here
// too. Internal roles keep the internal wording (workReason) unchanged.
function clientActionTopicText(actionText = "", operationalText = "") {
  for (const text of [String(actionText || "").toLowerCase(), String(operationalText || "").toLowerCase()]) {
    if (/\bevv\b/.test(text)) return "EVV visit verification";
    if (/credential/.test(text)) return "provider credentialing";
    if (/\bauth/.test(text)) return "authorization missing";
    if (/\bcob\b|coordination of benefits|coverage/.test(text)) return "coverage or other-insurance details";
    if (/documentation|medical record/.test(text)) return "documentation";
  }
  return "see Client Action Pending";
}

function clientOutstandingReasonText({ profile = {}, action = "", operationalText = "", agingView = null, insuranceOwes = null, insurancePaid = null, patientOwes = null, patientShare = "", secondaryOnClaim = false } = {}) {
  const owes = outstandingWaterfallAmount(insuranceOwes);
  if (owes === null) return "";
  const flags = (profile && profile.flags) || {};
  const billed = agingView && agingView.known
    ? ", billed " + (agingView.basis === "days" ? (agingView.days === 1 ? "1 day" : Number(agingView.days).toLocaleString() + " days") : agingView.text) + " ago"
    : "";
  if (owes > 0 && !flags.paidClosed) {
    if (/client action pending/i.test(String(action || "")) || flags.clientAction) {
      return "Needs your action: " + clientActionTopicText(action, operationalText);
    }
    if (flags.denied) return "Denied by the payer; TMS is working on the appeal or correction";
    if (flags.waitingSecondary || flags.secondaryWork) return "Waiting on the secondary payer" + billed;
    // The status profile is claim-wide; on a line open the line's own paid
    // figure decides whether "paid part" is true of what the reader sees.
    const paid = outstandingWaterfallAmount(insurancePaid);
    if (flags.partialPaid && !(paid !== null && paid <= 0)) return "The payer paid part; TMS is following up on the rest" + billed;
    return "Waiting on the payer" + billed;
  }
  const share = outstandingWaterfallAmount(patientOwes);
  if (share !== null && share > 0) {
    if (secondaryOnClaim) return "Insurance has paid its part; the patient's share goes to the secondary payer";
    return "Insurance has paid its part; the patient owes " + (patientShare ? "their " + patientShare : "their share");
  }
  return "Nothing left for insurance to pay";
}

// Claim-drawer presentation only. Source balances and statement rules are never rewritten.
function drawerClarityPr(service = {}) {
  const source = Array.isArray(service.prAdjustments) && service.prAdjustments.length ? service.prAdjustments : adjustmentRows(service);
  const byCode = { PR1: 0, PR2: 0, PR3: 0 };
  let evidence = false;
  for (const row of source) {
    const group = String(row.group || row.groupCode || "PR").trim().toUpperCase();
    const code = String(row.reasonCode || row.code || "").replace(/\D/g, "");
    const amount = outstandingWaterfallAmount(row.amount);
    if (group !== "PR" || !["1", "2", "3"].includes(code) || amount === null) continue;
    evidence = true;
    byCode["PR" + code] = moneyNumber(byCode["PR" + code] + amount);
  }
  const scalar = outstandingWaterfallAmount(firstPresent(service.prAdjustmentTotal, service.pr_adjustment_total, service["PR Adjustment Total"]));
  // Net signed corrections before displaying the component. A total alone cannot identify its reason.
  return { byCode: evidence ? byCode : null, total: evidence ? moneyNumber(Object.values(byCode).reduce((sum, value) => sum + value, 0)) : scalar };
}

function drawerClarityModel(claim = {}, { focusServiceId = "", scope = "service", serverHydrated = false, pending = false } = {}) {
  const id = String(focusServiceId || "").trim();
  const selected = [claim.focusService, ...(claim.services || [])].find(service => service && String(service.serviceId) === id) || null;
  const isLine = scope === "service" && Boolean(id);
  const lines = isLine ? (selected ? [selected] : []) : (claim.services || []);
  const fullClaim = !claim.serviceLinesTruncated && (!claim.servicePage?.complete || Boolean(claim.preparedFinancialSummary || claim.preparedPrSummary?.complete));
  const knownScope = !pending && (isLine ? Boolean(selected) : fullClaim);
  const fin = isLine ? (selected ? serviceFinancialView(selected, claim) : {}) : claimFinancialView(claim);
  const amount = value => knownScope ? outstandingWaterfallAmount(value) : null;
  const completeLines = isLine ? Boolean(selected) : !claim.serviceLinesTruncated && (!claim.servicePage?.complete || Number(claim.servicePage.totalRows) === new Set(lines.map(service => String(service.serviceId))).size);
  const sumKnown = getter => {
    if (!knownScope || !completeLines || !lines.length) return null;
    const values = lines.map(getter);
    return values.some(value => value === null) ? null : moneyNumber(values.reduce((sum, value) => sum + value, 0));
  };
  const prRows = lines.map(drawerClarityPr);
  const preparedPr = !isLine && claim.servicePage?.complete && claim.preparedPrSummary?.complete ? claim.preparedPrSummary : null;
  const prTotal = preparedPr ? amount(preparedPr.total) : sumKnown(service => drawerClarityPr(service).total);
  const byCode = preparedPr?.codeBreakdownComplete ? preparedPr.byCode : (completeLines && prRows.length && prRows.every(row => row.byCode) ? Object.fromEntries(["PR1", "PR2", "PR3"].map(code => [code, moneyNumber(prRows.reduce((sum, row) => sum + row.byCode[code], 0))])) : (prTotal === 0 ? { PR1: 0, PR2: 0, PR3: 0 } : null));
  const patientPaid = knownScope ? (isLine ? drawerServedPatientPaid(selected || {}) : drawerClaimServedPatientPaid(claim)) : null;
  const positiveBalance = value => { const n = outstandingWaterfallAmount(value); return n === null ? null : Math.max(0, n); };
  const patientBalance = preparedPr ? (Number(preparedPr.patientBalanceUnknownLines) === 0 ? positiveBalance(amount(preparedPr.patientBalance)) : null) : sumKnown(service => positiveBalance(firstPresent(service.patientBalance, service.preparedPatientFinancials?.patientBalance, service.totalPatBal, service["Total Pat Bal"])));
  const paid = amount(isLine ? fin.insurancePaid : fin.totalInsurancePaid);
  let split = { primary: amount(fin.primaryPaid), secondary: amount(fin.secondaryPaid), unmatched: amount(fin.unmatchedPaid) };
  if (isLine && selected && !selected.preparedFinancials) {
    split = { primary: amount(firstPresent(selected.primaryPaid, selected.primary_paid)), secondary: amount(firstPresent(selected.secondaryPaid, selected.secondary_paid)), unmatched: amount(firstPresent(selected.unmatchedPaid, selected.unmatched_paid)) };
  }
  const events = (Array.isArray(claim.payments) ? claim.payments : []).filter(event => !isLine || String(event.serviceLineId || "") === id);
  if (knownScope && Object.values(split).some(value => value === null) && !claim.paymentEventsUnavailable && !claim.paymentEventsTruncated && events.length && events.every(event => outstandingWaterfallAmount(event.applied) !== null)) {
    const net = moneyNumber(events.reduce((sum, event) => sum + Number(event.applied), 0));
    // Only a complete, reconciled event set may stand in for a missing served split.
    if (paid !== null && net === paid) {
      split = { primary: 0, secondary: 0, unmatched: 0 };
      for (const event of events) {
        const role = paymentRoleFromEvidence(claim, event);
        const key = role === "Primary" ? "primary" : role === "Secondary" ? "secondary" : "unmatched";
        split[key] = moneyNumber(split[key] + Number(event.applied));
      }
    }
  }
  // A recorded $0 total is a real zero, so its missing parts are $0 too, not "Unavailable".
  if (paid === 0) split = { primary: split.primary ?? 0, secondary: split.secondary ?? 0, unmatched: split.unmatched ?? 0 };
  if (sameExternalPayerConflict(claim)) split = { primary: null, secondary: null, unmatched: paid };
  const secondary = patientResponsibilityClaimHasSecondary(claim) || lines.some(service => booleanFromSource(service.hasSecondaryPayer) === true || Boolean(presentableDrawerValue(service.secondaryExternalId)));
  const loadedAdjustment = isLine ? claim.drawerAdjustmentDetails?.[id] : null;
  const adjusted = knownScope ? (loadedAdjustment && !loadedAdjustment.loading && !loadedAdjustment.currentHistory && loadedAdjustment.reason === '' ? outstandingWaterfallAmount(loadedAdjustment.total) : drawerServedPayerAdjustments(lines, { serverHydrated, claim: isLine ? null : claim })) : null;
  const allLineAdjustments = !isLine && completeLines && knownScope && lines.length ? lines.map(service => {
    const detail = claim.drawerAdjustmentDetails?.[service.serviceId];
    return detail && !detail.loading && !detail.currentHistory && detail.reason === "" ? outstandingWaterfallAmount(detail.total) : (service.payerAdjustmentReason === "" ? outstandingWaterfallAmount(service.payerAdjustmentTotal) : null);
  }) : [];
  const adjustmentTotal = adjusted === null && allLineAdjustments.length && allLineAdjustments.every(value => value !== null) ? moneyNumber(allLineAdjustments.reduce((sum, value) => sum + value, 0)) : adjusted;
  const charged = amount(fin.charges);
  const outstanding = amount(fin.sourceOutstanding);
  const waterfall = outstandingWaterfallModel({ charged, adjusted: adjustmentTotal, insurancePaid: paid, patientPaid, insuranceOwes: outstanding, patientOwes: patientBalance });
  const serviceLabel = service => [formatDate(serviceDosValue(service)), service.procedure || service.procedureCode, hasPresent(service.units) ? service.units + " units" : ""].filter(Boolean).join(" · ");
  return { isLine, selected, id, lines, knownScope, completeLines, pending, fin, charged, paid, split, adjusted: adjustmentTotal, outstanding, prTotal, byCode, patientPaid, patientBalance, secondary, events, waterfall,
    scopeLabel: isLine ? (selected ? serviceLabel(selected) : "Selected service is unavailable") : "Whole claim · " + (claim.servicePage?.totalRows || claim.services?.length || 0) + " services",
    primaryName: presentableDrawerValue(claim.primaryPayer) || "Payer name unavailable",
    secondaryName: presentableDrawerValue(claim.secondaryPayer) || "No secondary payer listed",
    serviceLabel };
}

function drawerClarityHtml(claim, options = {}) {
  const model = drawerClarityModel(claim, options);
  const canMoney = hasPermission(PERMISSIONS.VIEW_FINANCIALS) && !isRedactedClientRole();
  const canPatient = canMoney || isRedactedClientRole();
  const exact = value => model.pending ? "Loading…" : value === null || value === undefined ? "Unavailable" : Formatters.moneyExact(value);
  const signed = (value, negativeLabel) => model.pending ? "Loading…" : value !== null && value < 0 ? `${Formatters.moneyExact(Math.abs(value))} ${negativeLabel}` : exact(value);
  const row = (key, label, value, hint = "", negativeLabel = "correction", emphasis = false) => `<div class="drawer-clear-row${emphasis ? " is-total" : ""}" data-waterfall-row="${escapeHtml(key)}"><dt>${escapeHtml(label)}${hint ? `<small>${escapeHtml(hint)}</small>` : ""}</dt><dd>${escapeHtml(signed(value, negativeLabel))}</dd></div>`;
  const scopeButton = (scope, label) => `<button type="button" class="ghost-button" data-drawer-money-scope="${scope}" aria-pressed="${model.isLine === (scope === "service")}"${scope === "service" && !options.focusServiceId ? " disabled" : ""}>${label}</button>`;
  const clientMode = isClientRole();
  const statusProfile = buildClaimStatusProfile(claim);
  const outstandingReason = clientMode ? clientOutstandingReasonText({
    profile: statusProfile, action: model.selected?.action || claim.action,
    operationalText: claimOperationalText(claim), agingView: agingDaysView(claim),
    insuranceOwes: model.outstanding, insurancePaid: model.paid,
    patientOwes: model.prTotal, patientShare: patientShareWords(model.byCode ? Object.keys(model.byCode).filter(key => model.byCode[key] > 0) : []), secondaryOnClaim: model.secondary
  }) : (statusProfile.workReason || statusProfile.lifecycleLabel || '');
  const patientState = model.pending ? "Loading patient details…" : model.secondary ? "Patient billing is on hold because this claim has secondary insurance." : model.patientBalance === 0 ? "No patient balance is recorded." : "Recorded balance is not a bill. Patient Statements verifies PR evidence, payments, the 30-day wait and follow-up holds before billing.";
  const card = (label, value, key) => `<div data-drawer-money-tile="${key}"><span>${label}</span><strong>${escapeHtml(value)}</strong></div>`;
  const prNames = [["PR1", "Deductible", "PR-1", "The deductible amount listed by insurance."], ["PR2", "Coinsurance", "PR-2", "The patient's percentage of the allowed amount."], ["PR3", "Copay", "PR-3", "The fixed copay listed by insurance."]];
  const paymentHistory = model.events.map(event => {
    const value = outstandingWaterfallAmount(event.applied);
    const role = event.attributionConflict ? "Payer needs review" : paymentRoleFromEvidence(claim, event);
    const service = [claim.focusService, ...(claim.services || [])].find(line => line && String(line.serviceId) === String(event.serviceLineId));
    return `<tr><td><strong>${escapeHtml(presentableDrawerValue(event.source) || "Payer name unavailable")}</strong><small>${escapeHtml(role === "Unmatched" ? "Payer needs review" : role + " insurance")}</small></td><td>${escapeHtml(formatDate(event.date))}<small>${escapeHtml(event.ref || "Reference unavailable")}</small></td><td>${escapeHtml(service ? model.serviceLabel(service) : event.serviceLineId ? "Service " + event.serviceLineId : "Service not identified")}</td><td>${escapeHtml(signed(value, "payment reduction"))}${value !== null && value < 0 ? "<small>Reduces recorded payments; reason not confirmed.</small>" : ""}</td></tr>`;
  }).join("");
  return `<div class="drawer-clear-context"><div class="drawer-clear-switch" role="group" aria-label="Financial totals scope">${scopeButton("service", "Selected service")}${scopeButton("claim", "Whole claim")}</div><p>${escapeHtml(model.scopeLabel)}</p><small>All amounts below use this scope. Note scope stays unchanged.</small></div>
    ${!model.knownScope && !model.pending ? '<p class="drawer-clear-notice" role="status">Complete figures for this selection are unavailable. Partial lines are not shown as whole-claim totals.</p>' : ""}
    <div class="drawer-clear-cards">${canMoney ? card("We charged", exact(model.charged), "charges") + card("Insurance paid", signed(model.paid, "net reduction"), "paid") + card(model.outstanding !== null && model.outstanding < 0 ? "Insurance credit" : "Insurance balance", exact(model.outstanding === null ? null : Math.abs(model.outstanding)), "outstanding") : ""}${canPatient ? card("Patient balance recorded", signed(model.patientBalance, "credit"), "patient") + card("Patient billing", model.pending ? "Loading…" : model.secondary ? "On hold" : model.patientBalance === 0 ? "No balance" : "Review statement", "billing") : ""}<div class="drawer-clear-provider" data-drawer-rendering-provider><span>Rendering provider</span><strong title="${escapeHtml(claim.renderingProvider || "Unavailable")}">${escapeHtml(claim.renderingProvider || "Unavailable")}</strong></div></div>
    ${canMoney && !model.pending ? `<section class="panel drawer-clear-money" data-drawer-waterfall="${model.isLine ? "line" : "claim"}" data-drawer-waterfall-state="${model.waterfall.state}"><h3>Where the money went</h3><dl>
      ${row("charged", "We charged", model.charged, "Original billed charge")}
      ${row("primaryPaid", "Primary insurance paid", model.split.primary, model.primaryName, "net payment reduction")}
      ${row("secondaryPaid", "Secondary insurance paid", model.split.secondary, model.secondaryName, "net payment reduction")}
      ${model.split.unmatched !== 0 ? row("unmatchedPaid", "Payments needing payer review", model.split.unmatched, "Not assigned to primary or secondary", "net payment reduction") : ""}
      ${row("insurancePaid", "Total insurance paid", model.paid, "Primary, secondary and any unassigned insurance payments", "net payment reduction", true)}
      ${row("insuranceOwes", model.outstanding !== null && model.outstanding < 0 ? "Insurance credit" : "Insurance balance remaining", model.outstanding === null ? null : Math.abs(model.outstanding), "Recorded insurance balance; separate from patient responsibility", "credit", true)}
    </dl>${Object.values(model.split).every(value => value !== null) && model.paid !== null && moneyNumber(model.split.primary + model.split.secondary + model.split.unmatched) !== model.paid ? '<p class="drawer-clear-notice">The payer breakdown does not yet match the recorded insurance total. Review the payment history before assigning the difference.</p>' : ""}${outstandingReason ? `<p class="drawer-waterfall-reason" data-drawer-outstanding-reason="${clientMode ? "client" : "internal"}">${escapeHtml(outstandingReason)}</p>` : ""}${model.waterfall.note ? `<p class="drawer-clear-notice" data-drawer-waterfall-note="${model.waterfall.state}">${escapeHtml(model.waterfall.note)}</p>` : ""}
    <details class="drawer-clear-details"><summary>Payment history${model.events.length ? " · " + model.events.length + " entries" : ""}</summary>
      ${claim.paymentEventsUnavailable ? '<p role="status">Payment history is temporarily unavailable. The recorded totals above remain separate.</p>' : ""}
      ${claim.paymentEventsTruncated ? '<p role="status">The payment history is incomplete; do not use it to total the claim.</p>' : ""}
      <div class="drawer-table-wrap"><table class="service-table"><thead><tr><th>Paid by</th><th>Date / reference</th><th>Applied to</th><th>Amount / effect</th></tr></thead><tbody>${paymentHistory || '<tr><td colspan="4">No payment entries available for this selection.</td></tr>'}</tbody></table></div>
    </details>${drawerClarityAdjustmentHtml(claim, model, exact)}
    </section>` : ""}
    ${canPatient ? `<section class="panel drawer-clear-patient"><h3>Patient responsibility</h3><p>Amounts assigned by insurance. These are not additional charges.</p><dl>
      ${prNames.map(([key, name, code, hint]) => row(key, `${name} (${code})`, model.byCode ? outstandingWaterfallAmount(model.byCode[key]) : null, hint, "net correction")).join("")}
      ${row("patientOwes", "Total assigned by insurance", model.prTotal, "Includes signed corrections to deductible, coinsurance and copay", "net correction", true)}
      ${row("patientPaid", "Patient payments recorded", model.patientPaid, "Already paid by the patient", "payment reduction")}
      ${row("patientBalance", "Patient balance recorded", model.patientBalance, "Source balance; billing eligibility is checked separately", "credit", true)}
    </dl><p class="drawer-clear-notice" data-drawer-patient-billing>${escapeHtml(patientState)}</p>${model.byCode ? "" : '<p class="subtext">The deductible, coinsurance and copay breakdown is unavailable. A total is not assigned to a reason by guesswork.</p>'}</section>` : ""}
    ${canMoney && !model.isLine && !model.pending ? `<section class="panel drawer-clear-money"><h3>Payments by service</h3><p>${claim.servicePage?.complete && claim.servicePage.totalRows > model.lines.length || claim.serviceLinesTruncated ? "Showing the loaded service page. Whole-claim totals above are separate." : "Each row shows where insurance payments were applied."}</p><div class="drawer-table-wrap"><table class="service-table"><thead><tr><th>Service</th><th>Charged</th><th>Primary paid</th><th>Secondary paid</th><th>Insurance balance</th></tr></thead><tbody>${model.lines.map(service => { const line = drawerClarityModel(claim, { ...options, focusServiceId: service.serviceId, scope: "service" }); return `<tr><td>${escapeHtml(model.serviceLabel(service))}</td><td>${escapeHtml(exact(line.charged))}</td><td>${escapeHtml(signed(line.split.primary, "reduction"))}</td><td>${escapeHtml(signed(line.split.secondary, "reduction"))}</td><td>${escapeHtml(signed(line.outstanding, "credit"))}</td></tr>`; }).join("")}</tbody></table></div><p class="subtext">More service details and page controls are under “Service lines and claim information”.</p></section>` : ""}
    ${canMoney && !model.pending ? `<details class="drawer-clear-details"><summary>Expected reimbursement and billing checks</summary><dl>${row("expected", "Expected from insurance", outstandingWaterfallAmount(model.fin.expectedReimbursement), "Contract-based expected reimbursement; not the original charge")}${row("writeOff", "Write-off already included", outstandingWaterfallAmount(model.fin.writeOffAmount), "Already reflected in expected reimbursement; do not subtract again")}</dl><p>Insurance balance is taken from the billing calculation. It is not replaced with charge minus payments.</p></details>` : ""}`;
}

function drawerClarityAdjustmentHtml(claim, model, exact) {
  const available = Array.from(new Map([claim.focusService, ...(claim.services || [])].filter(Boolean).map(service => [String(service.serviceId), service])).values());
  const selectedId = model.isLine ? model.id : String(claim.drawerAdjustmentServiceId || available[0]?.serviceId || '');
  const service = available.find(line => String(line.serviceId) === selectedId);
  const detail = claim.drawerAdjustmentDetails?.[selectedId];
  const rows = detail ? detail.events : service?.payerAdjustmentEvents;
  const groupNames = { CO: 'Contractual adjustment', OA: 'Other adjustment', PI: 'Payer-initiated adjustment' };
  return `<details class="drawer-clear-details" data-drawer-adjustment-disclosure><summary>View Adjustments</summary><dl><div class="drawer-clear-row" data-waterfall-row="adjusted"><dt>Net payer adjustments</dt><dd>${escapeHtml(exact(model.adjusted))}</dd></div></dl>
    ${model.isLine ? '' : `<label>Service to review<select data-drawer-adjustment-service>${available.map(line => `<option value="${escapeHtml(line.serviceId)}"${String(line.serviceId) === selectedId ? ' selected' : ''}>${escapeHtml(model.serviceLabel(line))} · ${escapeHtml(line.serviceId)}</option>`).join('')}</select></label><p>Review one service at a time. Use the service-line pages below to reach other services.</p>`}
    ${service ? `<p>${escapeHtml(model.serviceLabel(service))}</p>` : '<p>The selected service is unavailable.</p>'}
    ${detail?.currentHistory ? '<p class="drawer-clear-notice">Current adjustment history. The report-version totals above remain unchanged.</p>' : ''}
    ${detail?.loading ? '<p role="status">Loading adjustment reasons…</p>' : Array.isArray(rows) ? rows.length ? `<div class="drawer-table-wrap"><table class="service-table"><thead><tr><th>Date</th><th>Reason</th><th>Effect on charge</th></tr></thead><tbody>${rows.map(event => {
      const value = outstandingWaterfallAmount(event.amount);
      return `<tr><td>${escapeHtml(formatDate(event.adjustmentDate))}</td><td>${escapeHtml(event.reasonDescription || event.reason || groupNames[event.group] || 'Adjustment')}<small>${escapeHtml([event.group, event.reasonCode].filter(Boolean).join(' · '))}${event.reasonDescription || event.reason ? '' : ' · Specific reason description unavailable'}</small></td><td>${escapeHtml(value === null ? exact(null) : Formatters.moneyExact(Math.abs(value)) + (value < 0 ? ' increase' : ' reduction'))}</td></tr>`;
    }).join('')}</tbody></table></div>` : '<p>No payer adjustments were found for this service.</p>' : `<p>${detail ? 'Adjustment reasons are temporarily unavailable.' : 'Load the recorded adjustments for this service.'}</p>`}
    ${detail?.eventsTruncated || service?.payerAdjustmentEventsTruncated ? '<p class="drawer-clear-notice">Only the first 100 adjustment entries are shown. The recorded net total includes all entries.</p>' : ''}
    ${detail && !detail.loading && detail.total !== null && detail.total !== undefined ? `<p><strong>Net payer adjustments for this service: ${escapeHtml(exact(detail.total))}</strong></p>` : ''}
    ${service && !detail?.loading ? `<button type="button" class="ghost-button" data-drawer-load-adjustments="${escapeHtml(selectedId)}">${detail ? 'Reload adjustment reasons' : 'Load adjustment reasons'}</button>` : ''}
  </details>`;
}

// Reads the payer adjustments for one or more lines of the open claim. `reveal`
// is the biller's own "Load adjustment reasons" click: it opens the disclosure
// on that line. The automatic read on drawer open leaves the layout alone.
async function loadDrawerClarityAdjustments(claim, serviceIds, options, host, { reveal = true } = {}) {
  const context = activeClaimDrawerContext;
  claim.drawerAdjustmentDetails ||= {};
  const ids = [...new Set((Array.isArray(serviceIds) ? serviceIds : [serviceIds]).map(id => String(id || '')).filter(Boolean))]
    .filter(id => !claim.drawerAdjustmentDetails[id]?.loading);
  if (!context || context.claim !== claim || !ids.length || !host?.isConnected) return;
  const access = workflowAccessScopedRequestKey({ base: context.base, claimId: context.claimId });
  const current = () => activeClaimDrawerContext === context && context.claim === claim && host.isConnected && workflowAccessScopedRequestKey({ base: context.base, claimId: context.claimId }) === access;
  ids.forEach(id => { claim.drawerAdjustmentDetails[id] = { loading: true }; });
  const repaint = () => {
    if (!current()) return;
    const scroll = els.drawerBody.querySelector('.drawer-clear-main')?.scrollTop;
    const disclosure = host.querySelector('[data-drawer-adjustment-disclosure]');
    const wasOpen = Boolean(disclosure?.open);
    const selected = host.querySelector('[data-drawer-adjustment-service]')?.value;
    host.innerHTML = drawerClarityHtml(claim, { ...options, scope: claim.drawerMoneyScope || (options.focusServiceId ? 'service' : 'claim') });
    const next = host.querySelector('[data-drawer-adjustment-disclosure]');
    if (next) next.open = reveal || wasOpen;
    const selector = host.querySelector('[data-drawer-adjustment-service]');
    if (selector) selector.value = reveal ? ids[0] : (selected || selector.value);
    const main = els.drawerBody.querySelector('.drawer-clear-main');
    if (main && scroll !== undefined) main.scrollTop = scroll;
  };
  repaint();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 25000);
  try {
    const query = new URLSearchParams({ orgId: context.orgId, serviceId: ids[0], detailShape: 'adjustments' });
    if (ids.length > 1) query.set('serviceIds', ids.join(','));
    let url = context.base + '/api/tracker/claims/' + encodeURIComponent(context.claimId) + '?' + query;
    if (context.pinnedReportContext) url = reportPinnedReadUrl(url, await ensureReportReadSession(context.pinnedReportContext));
    if (!current()) return;
    const envelope = await claimDrawerFetchJson(url, context.token, { signal: controller.signal, isCurrent: current });
    if (!current()) return;
    const body = envelope.body || {};
    const details = body.adjustmentDetails || (body.adjustmentDetail ? { [String(body.serviceId)]: body.adjustmentDetail } : null);
    if (!envelope.ok || String(body.claimId) !== String(claim.claimId) || String(body.orgId) !== String(claim.orgId) || String(body.serviceId) !== ids[0] || !details || !ids.every(id => details[id])) throw Error('Adjustment details could not be loaded.');
    ids.forEach(id => { claim.drawerAdjustmentDetails[id] = { ...details[id], loading: false, currentHistory: Boolean(context.pinnedReportContext) }; });
  } catch (_) {
    if (current()) ids.forEach(id => { claim.drawerAdjustmentDetails[id] = { loading: false, reason: 'unavailable', events: null, total: null }; });
  } finally {
    clearTimeout(timeout);
    repaint();
  }
}

// THE PREPARED DETAIL DOES NOT CARRY PAYER ADJUSTMENTS (prepared_lane_not_captured),
// so "Where the money stands" could never foot on an ordinary open. Once the
// server-backed claim is painted, read them for the lines the drawer can total:
// the focus line, plus every line when the whole claim is loaded. Same bounded,
// org-scoped, financials-gated route as the manual button. A frozen report drawer
// is skipped: current adjustments never join its saved totals anyway.
function autoLoadDrawerClarityAdjustments(claim, options, host) {
  if (options.pending || !options.serverHydrated || !trackerApiEnabled()) return;
  if (!hasPermission(PERMISSIONS.VIEW_FINANCIALS) || isRedactedClientRole()) return;
  const context = activeClaimDrawerContext;
  if (!context || context.claim !== claim || context.pinnedReportContext) return;
  const unserved = service => service && service.serviceId && service.payerAdjustmentReason !== '' && !claim.drawerAdjustmentDetails?.[String(service.serviceId)];
  const focus = [claim.focusService, ...(claim.services || [])].find(service => service && String(service.serviceId) === String(options.focusServiceId || ''));
  const whole = drawerClarityModel(claim, { ...options, scope: 'claim' });
  const lines = whole.knownScope && whole.completeLines && whole.lines.length <= 50 ? whole.lines : [];
  const ids = [focus, ...lines].filter(unserved).map(service => String(service.serviceId));
  if (ids.length) loadDrawerClarityAdjustments(claim, ids, options, host, { reveal: false });
}

function bindDrawerClarity(claim, options) {
  const host = els.drawerBody.querySelector("[data-drawer-clear-finance]");
  if (!host) return;
  host.addEventListener('change', event => {
    if (!event.target.matches('[data-drawer-adjustment-service]')) return;
    claim.drawerAdjustmentServiceId = event.target.value;
    host.innerHTML = drawerClarityHtml(claim, { ...options, scope: claim.drawerMoneyScope || (options.focusServiceId ? 'service' : 'claim') });
    host.querySelector('[data-drawer-adjustment-disclosure]').open = true;
    host.querySelector('[data-drawer-adjustment-service]')?.focus({ preventScroll: true });
  });
  host.addEventListener("click", event => {
    const load = event.target.closest('[data-drawer-load-adjustments]');
    if (load) { loadDrawerClarityAdjustments(claim, load.dataset.drawerLoadAdjustments, options, host); return; }
    const button = event.target.closest("[data-drawer-money-scope]");
    if (!button || button.disabled) return;
    const scope = button.dataset.drawerMoneyScope;
    if (!["service", "claim"].includes(scope)) return;
    // Repaint this financial subtree only. The note composer, its scope and ledger are untouched.
    claim.drawerMoneyScope = scope;
    host.innerHTML = drawerClarityHtml(claim, { ...options, scope });
    host.querySelector(`[data-drawer-money-scope="${scope}"]`)?.focus({ preventScroll: true });
  });
  autoLoadDrawerClarityAdjustments(claim, options, host);
}


// Patient name for claim headers and task cards. "patient" is a PHI column, so
// it is shown only with VIEW_PHI; otherwise the claim number stands alone.
function claimPatientDisplayName(claim = {}) {
  if (!hasPermission(PERMISSIONS.VIEW_PHI)) return "";
  const name = String(claim.patient || claim.patientName || "").trim();
  return /^(restricted|redacted)$/i.test(name) ? "" : name;
}

function claimPatientLineHtml(claim, className = "claim-patient-line") {
  const name = claimPatientDisplayName(claim);
  return name ? `<span class="${className}">${escapeHtml(name)}</span>` : "";
}

function setDrawerPatientName(name = "") {
  if (!els.drawerPatient) return;
  els.drawerPatient.textContent = name;
  els.drawerPatient.hidden = !name;
}

function renderClaimDrawer(claim, { serverHydrated = false, deniedColumns = [], focusServiceId = "", detailPending = false, sectionStatus = null } = {}) {
  const safeDeniedColumns = Array.isArray(deniedColumns) ? deniedColumns.map(String).slice(0, 25) : [];
  const clientMode = isClientRole();
  const redactedClient = isRedactedClientRole();
  const canSeeFinancials = hasPermission(PERMISSIONS.VIEW_FINANCIALS) && !redactedClient;
  // PROGRESSIVE OPEN. `detailPending` means the drawer is painted from the GRID
  // ROW while the server-backed claim record is still in flight. One row is one
  // service line and knows no claim total, so no money renders as a figure in
  // that phase - every money formatter below returns a labelled loading state
  // and the totals/PR/service-line/attribution panels render a loading panel.
  // A stated wrong total is worse than a stated wait (ENGINEERING_TRAPS #10).
  //
  // `sectionStatus` is the server's per-section outcome for the HYDRATED paint.
  // A section that failed says so where it lives; it never blanks the drawer.
  const drawerPending = detailPending === true;
  const DRAWER_PENDING_MONEY = "Loading\u2026";
  const drawerSectionStatus = sectionStatus && typeof sectionStatus === "object" ? sectionStatus : {};
  const drawerSectionFailed = (key) => drawerSectionStatus[key] === "unavailable";
  const DRAWER_SECTION_LABELS = { noteSummary: "note summary", payments: "payment events", prAdjustments: "patient-responsibility detail", lifeHistory: "claim history", pricing: "pricing explanation" };
  const _failedSections = Object.keys(DRAWER_SECTION_LABELS).filter(drawerSectionFailed).map((key) => DRAWER_SECTION_LABELS[key]);
  const _failedSectionText = _failedSections.length ? `Could not load: ${_failedSections.join(", ")}. Everything else on this claim loaded.` : "";
  const drawerPendingPanel = (heading, label) => `
    <section class="panel drawer-loading-panel" data-drawer-section-pending="${escapeHtml(label)}" aria-live="polite">
      <div class="drawer-loading-row">
        <span class="inline-spinner" aria-hidden="true"></span>
        <div><h3>${escapeHtml(heading)}</h3><p>Loading ${escapeHtml(label)}\u2026</p></div>
      </div>
    </section>
`;
  const drawerSectionFailureHtml = (key, sentence) => (drawerSectionFailed(key)
    ? `<div class="claim-note-load-error" role="alert" data-drawer-section-failed="${escapeHtml(key)}">${escapeHtml(sentence)}</div>`
    : "");
  const moneyOrRestricted = (value) => (drawerPending ? DRAWER_PENDING_MONEY : (canSeeFinancials && value != null ? Formatters.moneyExact(value) : "Restricted"));
  // RESTRICTED AND UNAVAILABLE ARE DIFFERENT SENTENCES, AND NEITHER IS $0.
  // moneyOrRestricted collapses both into "Restricted", which reads as a
  // permissions problem when the truth is that this grain does not carry the
  // figure at all. ENGINEERING_TRAPS #10: a stated zero and an unknown must not
  // look alike, and here a stated "you may not see this" and "nobody can see this
  // at this grain" must not either.
  const moneyOrUnavailable = (value) => (drawerPending ? DRAWER_PENDING_MONEY : (!canSeeFinancials ? "Restricted" : (value == null ? "Unavailable" : Formatters.moneyExact(value))));
  const patientMoney = (value) => (drawerPending ? DRAWER_PENDING_MONEY : ((canSeeFinancials || redactedClient) && value != null ? Formatters.moneyExact(value) : "Restricted"));
  const financial = claimFinancialView(claim);
  const reconciliation = claimFinancialReconciliation(claim);
  const guards = evaluateClaimCalculationGuards(claim);
  const patientResponsibilitySummary = claimPatientResponsibilitySummary(claim);
  const prComponents = [
    { label: "Deductible", code: "PR 1", amount: patientResponsibilitySummary.byCode.PR1 },
    { label: "Coinsurance", code: "PR 2", amount: patientResponsibilitySummary.byCode.PR2 },
    { label: "Copay", code: "PR 3", amount: patientResponsibilitySummary.byCode.PR3 }
  ].filter((component) => component.amount > 0);
  const prComponentText = prComponents.map((component) => `${component.label} ${patientMoney(component.amount)} (${component.code})`).join(" · ");
  const prSummaryLine = `Total ${patientMoney(patientResponsibilitySummary.total)}${prComponentText ? ` — ${prComponentText}` : ""}`;
  const drawerListScope = claimDrawerListScope(claim, focusServiceId);
  const renderPrPage = () => {
    if (drawerPending) return drawerPendingPanel("Patient Responsibility", "patient responsibility across the claim");
    const currentPrSummary = claimPatientResponsibilitySummary(claim);
    const paging = claim.servicePage?.complete ? {rows:currentPrSummary.rows} : managementListPageWindow("claim-pr", currentPrSummary.rows, drawerListScope);
    return drawerSectionFailureHtml("prAdjustments", "Patient-responsibility reason detail could not be loaded. The totals shown come from the claim record; the per-code breakdown is incomplete.") + `    ${patientResponsibilitySummary.total > 0 ? `
    <section class="panel claim-pr-panel">
      <div class="panel-heading-row">
        <div>
          <h3>Patient Responsibility</h3>
        </div>
        ${patientResponsibilitySummary.verified ? pill("PR from remittance", "green") : pill("PR estimated", "muted")}
      </div>
      <div class="claim-pr-line">${prSummaryLine}</div>
      <div class="claim-pr-list">
        ${paging.rows.map((row) => `
          <div>
            <span>${escapeHtml(row.serviceId)} · ${formatDate(row.dos)} · ${escapeHtml(row.procedure || "No procedure")}</span>
            <strong>${patientMoney(row.amount)} ${escapeHtml(row.codes)}</strong>
            <small>${escapeHtml(row.reasons)} · ${escapeHtml(row.statementStatus)}</small>
          </div>
        `).join("")}
      </div>
      ${claim.servicePage?.complete ? `<p class="subtext">${patientResponsibilitySummary.positiveLineCount.toLocaleString()} lines with patient responsibility across the full claim. Details below follow the service page.</p>` : managementListPagerHtml(paging, claim.serviceLinesTruncated ? "Loaded patient responsibility lines" : "Patient responsibility lines")}
    </section>
    ` : `
    <section class="panel claim-pr-panel claim-pr-panel--empty">
      <div class="claim-pr-empty-line">${claim.serviceLinesTruncated ? "No patient responsibility in the loaded service lines; the complete claim is unavailable." : "Patient responsibility: none on this claim"}</div>
    </section>
    `}

`;
  };
  const renderServicePage = () => {
    if (drawerPending) return drawerPendingPanel("Service Lines", "every service line on this claim");
    const paging = claim.servicePage?.complete ? {...claim.servicePage,rows:claim.services} : managementListPageWindow("claim-services", claim.services, drawerListScope);
    return `    <section class="panel">
      <h3>Service Lines</h3>
      ${claimServiceLimitWarningHtml(claim)}
      <div class="drawer-table-wrap" role="region" aria-label="Service lines table" tabindex="0">
      <table class="service-table">
        <thead>
          <tr>
            <th>Service ID</th>
            <th>DOS</th>
            <th>Procedure</th>
            <th>Units</th>
            ${redactedClient ? "" : `<th>Charge</th><th>Expected</th><th>Paid</th><th>Owed</th>`}
            <th>Status</th>
            <th>PR Codes</th>
            <th>True Pat Resp</th>
            <th>Statement</th>
          </tr>
        </thead>
        <tbody>
          ${paging.rows.map((service) => {
            const serviceFinancial = serviceFinancialView(service, claim);
            return `
            <tr>
              <td>${escapeHtml(service.serviceId)}</td>
              <td>${formatDate(serviceDosValue(service))}</td>
              <td>${escapeHtml(service.procedure)}</td>
              <td>${service.units}</td>
              ${redactedClient ? "" : `<td data-drawer-line-charge="${escapeHtml(service.serviceId)}">${moneyOrRestricted(serviceFinancial.charges)}</td><td>${moneyOrRestricted(serviceFinancial.expectedReimbursement)}</td><td>${moneyOrRestricted(serviceFinancial.insurancePaid)}</td><td>${moneyOrRestricted(serviceFinancial.sourceOutstanding)}</td>`}
              <td>${escapeHtml(service.status)}</td>
              <td>${escapeHtml(service.prCodes || prCodes(service))}</td>
              <td>${patientMoney(servicePatientResponsibilityAmount(service, claim))}</td>
              <td>${escapeHtml(statementStatusForService(service, claim))}</td>
            </tr>
          `; }).join("")}
        </tbody>
      </table>
      </div>
      ${claim.servicePage?.complete ? preparedClaimServicePagerHtml(claim) : managementListPagerHtml(paging, claim.serviceLinesTruncated ? "Loaded service lines" : "Service lines")}
    </section>

`;
  };
  // Claim Notes and Audit History are independently permissioned below. The
  // service detail never supplies an alternate note source or denied sentinel.
  const _kickerParts = [presentableDrawerValue(claim.client), presentableDrawerValue(claim.facility)].filter(Boolean);
  els.drawerKicker.textContent = _kickerParts.join(" · ") || "Claim detail";
  const _primaryPayer = presentableDrawerValue(claim.primaryPayer);
  const _secondaryPayer = presentableDrawerValue(claim.secondaryPayer);
  els.drawerTitle.textContent = `Claim ${claim.claimId}`;
  setDrawerPatientName(claimPatientDisplayName(claim));
  // Tracker-parity / #56: the drawer is the primary action path — lead with
  // the action + due date + reason, then the data. Without this card users
  // had to scan 14 evenly-weighted detail-grid cells to find the next move.
  const _statusProfile = (() => { try { return buildClaimStatusProfile(claim); } catch (_) { return { lifecycleLabel: "", workReason: "" }; } })();
  const _pastDue = (() => { try { return isPastDue(claim); } catch (_) { return false; } })();
  // READ OFF THE CLAIM HEADER, deliberately - `_focusService` is not resolved
  // until the next line, and this does not need it. Two facts make the header
  // the right source rather than a convenient one:
  //   * mergeTrackerAdapterClaimRows builds the claim's header fields from the
  //     FOCUS ROW, so a line-targeted open already describes the line asked for;
  //   * bill-date aging is CLAIM-CONSTANT. It is days since ClaBillDate, a claim
  //     field, and a production measurement on 2026-09-17 found 0 of 89,591
  //     (org, claim) pairs whose lines carry different aging.
  // So this is NOT the money grain below, which genuinely differs per line and
  // therefore has to be chosen explicitly. If aging ever becomes line-varying,
  // this must move below _focusService and read it.
  const _agingView = agingDaysView(claim);
  const _focusService = drawerFocusService(claim, focusServiceId);
  const _focusServiceId = String(_focusService?.serviceId || "").trim();
  const _lineAction = _focusServiceId ? followUpActionOnLine(claim.claimId, _focusServiceId) : String(claim.action || "");
  const _lineDate = _focusServiceId
    ? String(firstPresent(
      (state.followUpByService?.[followUpKey(claim.claimId, _focusServiceId)] || {}).followUpDate,
      _focusService.followUpDate,
      claim.followUpDate
    ) || "")
    : String(claim.followUpDate || "");
  const _linePastDue = _focusService ? serviceLinePastDue(_lineDate, _focusService, claim) : _pastDue;
  const _followText = _lineDate ? formatDate(_lineDate) : "Not scheduled";
  const _focusFin = _focusService ? serviceFinancialView(_focusService, claim) : null;
  // Resolved with the rest of the strip below, NOT off `_focusFin` alone.
  // drawerFocusService falls back to services[0], so keying on _focusFin put
  // LINE ONE's write-off next to claim totals on a claim-grain open - the exact
  // mixed-grain defect the strip was rebuilt to remove, surviving on the one path
  // nobody looked at. Declared here because the write-off tile reads it, but the
  // value is chosen by _moneyGrainIsLine a few lines down.
  let _writeOffAmt = null;
  const _expectedWhy = drawerExpectedExplain(_focusService, claim);
  // ---------------------------------------------------------------------------
  // ONE GRAIN FOR THE WHOLE MONEY STRIP.
  // ---------------------------------------------------------------------------
  // The strip used to mix them. Total Expected / Total Paid / Write-off / Payer
  // Resp / Patient Resp switched to the FOCUSED LINE whenever there was one,
  // while Unapplied and the hero's Outstanding stayed at CLAIM grain always. So a
  // biller read `$6,933 - $0` beside an OUTSTANDING of `$34,381`, and `TOTAL PAID
  // $0` on a claim with $286.38 applied - every figure individually true, the
  // strip as a whole unreadable as arithmetic. Measured on the TEST warehouse
  // 2026-08-23, claim 33318 line 90290: the strip showed line expected $1,072.04
  // and line paid $95.98 next to the claim's outstanding $4,636.28.
  //
  // Now every money tile resolves from ONE grain, chosen once here, and the strip
  // states which grain it is and shows its own arithmetic. The claim total is
  // still reachable - as a labelled SENTENCE beside the strip, never as a tile
  // inside it, because a tile is read as a term of the sum.
  // A LINE GRAIN ONLY WHEN A LINE WAS ACTUALLY ASKED FOR.
  //
  // drawerFocusService falls back to services[0] when the caller named no line,
  // because the follow-up editor and the denial panel need SOME line to act on.
  // Money must not inherit that fallback: a drawer opened from the grid without a
  // line target would then price the claim at line one and call it the claim.
  // `focusServiceId` is the caller's explicit choice - the queue drill-down now
  // always supplies it, the grid supplies it per row, and a bare claim open does
  // not - so the money follows the CHOICE, never the fallback.
  const _moneyGrainIsLine = Boolean(_focusFin) && String(focusServiceId || "").trim() !== "";
  const _lineCount = claim.servicePage?.complete ? claim.servicePage.totalRows : Array.isArray(claim.services) ? claim.services.length : 0;
  const _grainExpected = _moneyGrainIsLine ? _focusFin.expectedReimbursement : financial.expectedReimbursement;
  const _grainPaid = _moneyGrainIsLine ? _focusFin.insurancePaid : financial.totalInsurancePaid;
  const _grainOutstanding = _moneyGrainIsLine ? _focusFin.sourceOutstanding : financial.sourceOutstanding;
  const _grainPayerResp = _moneyGrainIsLine ? _focusFin.payerResponsibility : financial.payerResponsibility;
  const _grainPatientResp = _moneyGrainIsLine ? _focusFin.patientResponsibility : financial.patientResponsibility;
  // CHARGES ARE INFORMATIONAL (Nathan, 2026-09-23: "The charge amounts should be
  // there when in the drawer."). Billed charges at the SAME grain as every other
  // tile - the focused line's charge on a line open, the claim's summed charges
  // otherwise - read off the same served figures (serviceFinancialView /
  // claimFinancialView, i.e. preparedFinancials.charges and
  // preparedFinancialSummary.charges on the prepared lane). Deliberately NOT a
  // term of the arithmetic line below: a charge is what was billed, not what the
  // payer owes, and Expected - Paid = Outstanding is unchanged by it.
  const _grainCharges = _moneyGrainIsLine ? _focusFin.charges : financial.charges;
  // UNAPPLIED IS CLAIM-ONLY AND SAYS SO. It is derived from the claim's payment
  // EVENTS (totalPaymentApplied less totalInsurancePaid) and no line-grain
  // equivalent is served on any lane. Rendering the claim figure among line
  // figures is exactly the defect above; rendering 0 would be worse, because a
  // stated zero unapplied is a real and reassuring statement. So at line grain it
  // renders "Unavailable".
  const _grainUnapplied = _moneyGrainIsLine ? null : financial.unappliedPayments;
  _writeOffAmt = _moneyGrainIsLine ? _focusFin.writeOffAmount : financial.writeOffAmount;
  // The strip's own arithmetic, computed from the FIGURES IT RENDERS so the
  // sentence can never disagree with the tiles above it. Write-off is deliberately
  // not a term: financialCalculationEngine already subtracts it inside
  // finalInsuranceExpected, so the identity at any single grain is
  // Expected - Paid = Outstanding, and the write-off tile is a disclosure of what
  // was already deducted.
  const _grainArithmeticKnown = !drawerPending && canSeeFinancials && _grainExpected != null && _grainPaid != null && _grainOutstanding != null;
  const _grainDelta = _grainArithmeticKnown
    ? moneyNumber(moneyNumber(_grainExpected) - moneyNumber(_grainPaid) - moneyNumber(_grainOutstanding))
    : null;
  const _grainScopeText = _moneyGrainIsLine
    ? "service line " + _focusServiceId
    // A core paint holds ONE row, so it must not state a line count it does
    // not know - "1 service line" on a nine-line claim is a false sentence.
    : (drawerPending ? "this claim (service lines still loading)" : (_lineCount === 1 ? "this claim (1 service line)" : "this claim (all " + _lineCount + " service lines)"));
  // TO THE CENT, unlike the tiles. money() is the house whole-dollar formatter, so
  // a sentence built from it would show "$101 \u2212 $0 = $100" over figures that
  // foot perfectly at $100.50 \u2212 $0.30 \u2212 $100.20 - an arithmetic line that
  // does not add up on its own face is worse than none. The tiles keep their
  // whole-dollar house style; the line that CLAIMS to be arithmetic shows cents.
  const _grainMoneyExact = (value) => Formatters.moneyExact(value);
  const _grainArithmeticText = !_grainArithmeticKnown
    ? ""
    : (_grainDelta === 0
      ? "Expected " + _grainMoneyExact(_grainExpected) + " \u2212 Paid " + _grainMoneyExact(_grainPaid) + " = Outstanding " + _grainMoneyExact(_grainOutstanding) + "."
      : "Expected " + _grainMoneyExact(_grainExpected) + " \u2212 Paid " + _grainMoneyExact(_grainPaid) + " does not equal Outstanding " + _grainMoneyExact(_grainOutstanding) + " (difference " + _grainMoneyExact(_grainDelta) + ") \u2014 see Financial Reconciliation below.");
  const _grainClaimTotalText = !drawerPending && _moneyGrainIsLine && canSeeFinancials && financial.sourceOutstanding != null
    ? "Whole claim: " + Formatters.moneyExact(financial.sourceOutstanding) + " outstanding across " + _lineCount + (_lineCount === 1 ? " line." : " lines.")
    : "";
  const _grainCaption = redactedClient ? "" : `
    <p class="drawer-money-grain${_grainArithmeticKnown && _grainDelta !== 0 ? " is-unfooted" : ""}">
      <span class="drawer-money-grain-scope">Figures below are for ${escapeHtml(_grainScopeText)}.</span>
      ${_grainArithmeticText ? `<span class="drawer-money-grain-math">${escapeHtml(_grainArithmeticText)}</span>` : ""}
      ${_grainClaimTotalText ? `<span class="drawer-money-grain-claim">${escapeHtml(_grainClaimTotalText)}</span>` : ""}
    </p>`;
  // WHERE THE MONEY STANDS (Nathan, 2026-09-23) - see outstandingWaterfallModel.
  // The SAME grain as the strip above: the focused line on a line open, the
  // whole claim otherwise. Client and internal roles; never RedactedClient, never
  // a principal without financials, never the core paint.
  const _waterfallVisible = !redactedClient && canSeeFinancials && !drawerPending;
  const _waterfallPatient = (() => {
    if (_moneyGrainIsLine) {
      const adjustments = patientResponsibilityAdjustmentsForService(_focusService);
      return {
        amount: moneyNumber(adjustments.length ? adjustments.reduce((sum, adjustment) => sum + adjustment.amount, 0) : servicePatientResponsibilityAmount(_focusService, claim)),
        codes: adjustments.map((adjustment) => adjustment.reasonCode)
      };
    }
    // A page of a larger claim, or a truncated claim, is not the claim's PR.
    if (claim.serviceLinesTruncated || (claim.servicePage?.complete && claim.preparedPrSummary?.complete !== true)) return { amount: null, codes: [] };
    return {
      amount: patientResponsibilitySummary.total,
      codes: ["1", "2", "3"].filter((code) => moneyNumber(patientResponsibilitySummary.byCode?.["PR" + code]) > 0)
    };
  })();
  const _waterfall = outstandingWaterfallModel({
    charged: _grainCharges,
    adjusted: drawerServedPayerAdjustments(_moneyGrainIsLine ? [_focusService] : (claim.servicePage?.complete || claim.serviceLinesTruncated ? [] : claim.services), { serverHydrated, claim: _moneyGrainIsLine ? null : claim }),
    insurancePaid: _grainPaid,
    patientPaid: _moneyGrainIsLine ? drawerServedPatientPaid(_focusService) : drawerClaimServedPatientPaid(claim),
    insuranceOwes: _grainOutstanding,
    patientOwes: _waterfallPatient.amount
  });
  const _waterfallShareWords = patientShareWords(_waterfallPatient.codes);
  const _waterfallSecondary = Boolean(_secondaryPayer) && !/^(none|n\/a)$/i.test(_secondaryPayer);
  // Client wording for Client; the internal wording, unchanged, for internal
  // roles. RedactedClient is served no outstanding amount to explain.
  const _clientOutstandingReason = clientMode && canSeeFinancials && !drawerPending
    ? clientOutstandingReasonText({
      profile: _statusProfile,
      action: _lineAction,
      operationalText: (() => { try { return claimOperationalText(claim); } catch (_) { return ""; } })(),
      agingView: _agingView,
      insuranceOwes: _grainOutstanding,
      insurancePaid: _grainPaid,
      patientOwes: _waterfallPatient.amount,
      patientShare: _waterfallShareWords,
      secondaryOnClaim: _waterfallSecondary
    })
    : "";
  const _outstandingReason = clientMode ? _clientOutstandingReason : (_statusProfile.workReason || _statusProfile.lifecycleLabel || "");
  const _heroCard = `
    <section class="drawer-next-action">
      <div class="drawer-next-action-head">
        <span class="drawer-next-action-eyebrow">Next action${_focusServiceId ? ` · line ${_focusServiceId}` : ""}</span>
        ${_linePastDue ? '<span class="drawer-next-action-flag is-pastdue">Past due</span>' : ""}
      </div>
      <div class="drawer-next-action-grid">
        <div class="drawer-next-action-primary">
          <strong>${escapeHtml(_lineAction || "No action assigned")}</strong>${trackerFollowUpReviewHtml(claimAvailableServiceChoices(claim).find(line => String(line.serviceId) === String(_focusServiceId)) || {}, { includeApproved: true })}${followUpActionIsRestrictedCurrent(_lineAction) ? '<span class="followup-restricted-label">Restricted action</span>' : ""}
          <small${_clientOutstandingReason ? ' data-drawer-outstanding-reason="client"' : ""}>${escapeHtml(_clientOutstandingReason || _statusProfile.workReason || _statusProfile.lifecycleLabel || "")}</small>
        </div>
        <div class="drawer-next-action-meta">
          <span>Follow-Up Due</span>
          <strong class="${_linePastDue ? "is-pastdue" : ""}">${escapeHtml(_followText)}</strong>
        </div>
        <div class="drawer-next-action-meta">
          <span>Aging</span>
          <strong class="drawer-aging-days" data-aging-basis="${escapeHtml(_agingView.basis || "none")}" data-status-pill data-tone="${_agingView.tone}" title="${escapeHtml(_agingView.detail)}">${escapeHtml(_agingView.text)}</strong>
        </div>
        ${redactedClient ? "" : `<div class="drawer-next-action-meta">
          <span>Outstanding \u00b7 ${_moneyGrainIsLine ? "this line" : "whole claim"}</span>
          <strong>${moneyOrRestricted(_grainOutstanding)}</strong>
        </div>`}
      </div>
      ${hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP) && _focusServiceId && !drawerPending ? `
        <div class="drawer-next-action-cta drawer-followup-edit">
          <label>Set follow-up
            <select class="grid-select" data-drawer-action-edit="${escapeHtml(claim.claimId)}" data-drawer-action-org="${escapeHtml(claim.orgId || "")}" data-drawer-service-id="${escapeHtml(_focusServiceId)}"${followUpActionIsRestrictedCurrent(_lineAction) ? " disabled aria-label=\"Restricted action; changes are not allowed\"" : ""}>
              ${followUpActionOptionsHtml(_lineAction)}
            </select>
          </label>
          <label>Due
            <input class="grid-date" type="date" value="${escapeHtml(_lineDate || "")}"${followUpActionIsRestrictedCurrent(_lineAction) ? " disabled title=\"Restricted action — the due date cannot be changed.\"" : ""} data-drawer-date-edit="${escapeHtml(claim.claimId)}" data-drawer-date-org="${escapeHtml(claim.orgId || "")}" data-drawer-service-id="${escapeHtml(_focusServiceId)}">
          </label>
          <button class="ghost-button" type="button" data-drawer-scroll="notes">Add note ↓</button>
        </div>
      ` : (drawerPending ? '<div class="drawer-next-action-cta"><small>Follow-up editing opens when the full claim finishes loading.</small></div>' : hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP) ? `
        <div class="drawer-next-action-cta">
          <button class="ghost-button" type="button" data-drawer-scroll="notes">Add note ↓</button>
        </div>
      ` : "")}
    </section>
  `;
  // Offshore biller action panel — status progression + note add.
  // Per user direction: 'he has nowhere to add notes and mark it as done.'
  const offshoreActionPanel = canonicalRole(state.role) === "OffshoreBiller" && !drawerPending
    ? renderOffshoreClaimActionPanel(claim)
    : "";
  // The drawer paints once at once and again when the full record arrives. A
  // section the user already opened on the first paint stays open on the
  // second, for the same claim, instead of snapping shut under their click.
  // Keyed on the role and claim id: the row, core and full paints of one open
  // drawer do not all carry the same company fields, and an opened section
  // must never carry over into a paint for a role that sees less.
  const drawerClaimKey = `${state.role || ""}|${claim.claimId || ""}`;
  const drawerSectionKey = (details) => String(details.querySelector(":scope > summary")?.textContent || "").split(/[:\u00b7]/)[0].trim();
  const openDrawerSections = els.drawerBody.dataset.drawerClaimKey === drawerClaimKey
    ? new Set(Array.from(els.drawerBody.querySelectorAll(".drawer-clear-main > details.drawer-clear-details[open]"), drawerSectionKey))
    : new Set();
  els.drawerBody.innerHTML = `
    <div class="drawer-clear-main" data-drawer-hydrated="${serverHydrated && !drawerPending}">
      ${drawerPending ? `<section class="notice-card" data-drawer-core-only="true" aria-live="polite"><strong>Loading claim details</strong><span>Amounts and service details will appear when the full record is ready.</span></section>` : ""}
      ${_failedSectionText ? `<p class="drawer-clear-notice" data-drawer-failed-sections="true" role="status">${escapeHtml(_failedSectionText)}</p>` : ""}
      ${drawerSectionFailureHtml("payments", "Payment history is unavailable. Recorded totals are shown separately.")}
      ${!clientMode ? `<details class="drawer-clear-details drawer-clear-workflow"><summary>Next action: ${escapeHtml(_lineAction || "No action assigned")}${claimAvailableServiceChoices(claim).find(line => String(line.serviceId) === String(_focusServiceId))?.followUpReview?.status === "pending" ? " · Pending approval" : ""} · ${escapeHtml(_followText)}</summary>${_heroCard}</details>` : ""}
      <div data-drawer-clear-finance>${drawerClarityHtml(claim, { focusServiceId, scope: claim.drawerMoneyScope || (focusServiceId ? "service" : "claim"), serverHydrated, pending: drawerPending })}</div>
      ${drawerPending ? "" : denialIntelligenceDrawerHtml(claim, _focusServiceId)}
      ${offshoreActionPanel}
      <details class="drawer-clear-details"><summary>Service lines and claim information</summary>
        <div class="detail-grid">
          <div><span>Claim status</span><strong>${escapeHtml(claim.claimStatus || "Unavailable")}</strong></div>
          <div><span>Payment status</span><strong>${escapeHtml(claim.match || "Unavailable")}</strong></div>
        </div>
        <div data-claim-services-page>${renderServicePage()}</div>
      </details>
      ${claimFollowUpHistoryPanelHtml(claim, focusServiceId)}
      ${clientMode ? "" : `<details class="drawer-clear-details"><summary>Assignment</summary><div class="rules-grid">
        <div><span>Assigned to</span><strong>${escapeHtml(claim.assignedTo || "Unassigned")}</strong></div>
        <div><span>Assigned by</span><strong>${escapeHtml(claim.assignedBy || "None")}</strong></div>
        <div><span>Due</span><strong>${formatDate(claim.assignmentDue)}</strong></div>
        <div><span>Status</span><strong>${escapeHtml(claim.assignmentStatus || "Unavailable")}</strong></div>
      </div></details>`}
      <details class="drawer-clear-details"><summary>Data availability and billing checks</summary>
        ${serverHydrated ? `<p>Server-backed detail · Details loaded</p>` : ""}
        ${safeDeniedColumns.length ? '<p>Some details are hidden by your access permissions.</p>' : ""}
        ${drawerSectionFailureHtml("pricing", "The expected-reimbursement explanation is unavailable.")}
        ${drawerSectionFailureHtml("prAdjustments", "Patient-responsibility reason detail is unavailable.")}
        ${claimServiceLimitWarningHtml(claim)}
        ${canSeeFinancials && guards.length ? `<p>${escapeHtml(calculationGuardSummary(guards))}</p>` : ""}
        <div data-claim-pr-page>${renderPrPage()}</div>
      </details>
    </div>
    <aside class="drawer-clear-notes" aria-label="Claim and service notes">${claimNotesPanelHtml(claim, focusServiceId)}</aside>
  `;
  els.drawerBody.dataset.drawerClaimKey = drawerClaimKey;
  if (openDrawerSections.size) els.drawerBody.querySelectorAll(".drawer-clear-main > details.drawer-clear-details").forEach((details) => {
    if (openDrawerSections.has(drawerSectionKey(details))) details.open = true;
  });
  bindDrawerClarity(claim, { focusServiceId, serverHydrated, pending: drawerPending });

  // A pending paint mounts loading panels, not lists: there is nothing to page.
  if (!drawerPending) [["claim-pr", "[data-claim-pr-page]", renderPrPage], ["claim-services", "[data-claim-services-page]", renderServicePage]].forEach(([key, selector, html]) => {
    const host = els.drawerBody.querySelector(selector);
    const repaint = () => {
      if (!host?.isConnected) return;
      host.innerHTML = html();
      if (key === "claim-services" && claim.servicePage?.complete) bindPreparedClaimServicePager(host,claim,repaint);
      else bindManagementListPager(host, key, repaint);
    };
    if (key === "claim-pr" && claim.servicePage?.complete) claim.repaintPrPage = repaint;
    if (key === "claim-services" && claim.servicePage?.complete) {
      claim.repaintServicePage = repaint;
      bindPreparedClaimServicePager(host,claim,repaint);
    } else bindManagementListPager(host, key, repaint);
  });
  bindClaimNotesPanel(claim);
  try { bindOffshoreActionPanel(); } catch (_) {}
  // Hero card CTAs (#56 drawer = primary action path). Guarded for the vm verifier.
  if (els.drawerBody && typeof els.drawerBody.querySelector === "function") {
    els.drawerBody.querySelector("[data-drawer-scroll='notes']")?.addEventListener("click", () => {
      const notesPanel = els.drawerBody.querySelector("[data-panel='notes']");
      if (notesPanel) notesPanel.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    const drawerAction = els.drawerBody.querySelector("[data-drawer-action-edit]");
    if (drawerAction) {
      drawerAction.addEventListener("change", () => {
        drawerAction.blur();
        openFollowUpEditor(drawerAction.getAttribute("data-drawer-action-edit"), {
          orgId: drawerAction.getAttribute("data-drawer-action-org") || "",
          serviceId: drawerAction.getAttribute("data-drawer-service-id") || "",
          action: drawerAction.value,
          sourceSurface: "claim-drawer-action"
        });
      });
    }
    const drawerDate = els.drawerBody.querySelector("[data-drawer-date-edit]");
    if (drawerDate) {
      drawerDate.addEventListener("change", () => {
        openFollowUpEditor(drawerDate.getAttribute("data-drawer-date-edit"), {
          orgId: drawerDate.getAttribute("data-drawer-date-org") || "",
          serviceId: drawerDate.getAttribute("data-drawer-service-id") || "",
          followUpDate: drawerDate.value,
          sourceSurface: "claim-drawer-date"
        });
      });
    }
  }

  openDrawerShell();
}

function claimNoteCommandId(prefix = "note") {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `${prefix}-${crypto.randomUUID()}`;
  const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (token) => {
    const value = Math.floor(Math.random() * 16);
    return (token === "x" ? value : ((value & 0x3) | 0x8)).toString(16);
  });
  return `${prefix}-${uuid}`;
}

async function postClaimNoteApi(route, payload, requestContext = null) {
  const context = requestContext || activeClaimDrawerContext;
  const base = requestContext ? requestContext.base : (context?.base || workflowApiBase() || trackerApiBase());
  const token = requestContext ? requestContext.token : (context?.token || workflowApiToken() || trackerApiToken());
  if (!base || typeof fetch !== "function") throw new Error("The portal note service is not configured.");
  const response = await fetch(`${base}${route}`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  const body = await response.json().catch(() => ({}));
  if (response.status === 401 && (!requestContext?.isCurrent || requestContext.isCurrent()) && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
  if (!response.ok) {
    // NAME THE OBJECT THE SERVER REFUSED. A 409 on /api/workflow/follow-up is the
    // SERVICE LINE's optimistic-concurrency version, not the note's, and the flat
    // "this note changed" text sent billers to the notes panel to look for a
    // conflict that was never there. The server already sends the right sentence
    // for each case; use it, and carry the machine-readable code so callers can
    // branch (follow_up_stale reloads the line and keeps the draft).
    const error = new Error(body.message
      || (response.status === 409 ? "This record changed after you opened it. Reload the claim and try again." : (body.error || "The claim note could not be saved.")));
    error.status = response.status;
    error.code = String(body.error || "");
    error.currentUpdatedAt = String(body.currentUpdatedAt || "");
    throw error;
  }
  return body;
}

function reopenActiveClaimDrawer() {
  const context = activeClaimDrawerContext;
  if (!context) return;
  openServerClaimDrawer(context.claimId, {
    orgId: context.orgId,
    serviceId: context.focusServiceId,
    apiBase: context.base,
    apiToken: context.token
  });
}

function bindClaimNotesPanel(claim) {
  if (!els.drawerBody || typeof els.drawerBody.querySelector !== "function") return;
  const composer = els.drawerBody.querySelector("[data-claim-note-create]");
  const textarea = composer?.querySelector("textarea");
  const count = composer?.querySelector("[data-claim-note-count]");
  const scopeStatus = composer?.querySelector("[data-claim-note-scope-status]");
  const lineChoices = composer?.querySelector("[data-claim-note-line-choices]");
  const scopeRadios = Array.from(composer?.querySelectorAll("input[name='claimNoteScope']") || []);
  const selectedScopeMode = () => String(scopeRadios.find((radio) => radio.checked)?.value || "");
  const focusServiceId = String(lineChoices?.dataset.claimNoteFocusService || "");
  const selectedLineIds = new Set(focusServiceId ? [focusServiceId] : []);
  const selectedServiceLineIds = () => Array.from(selectedLineIds);
  const syncScopePicker = () => {
    const lineMode = selectedScopeMode() === "service_lines";
    const scopeLabel = composer?.querySelector("[data-drawer-note-scope-label]");
    if (scopeLabel) scopeLabel.textContent = "Note scope · " + (!selectedScopeMode() ? "Choose before saving" : lineMode ? `${selectedServiceLineIds().length} service line(s) selected` : "Entire claim");
    if (lineChoices) lineChoices.hidden = !lineMode;
    if (scopeStatus) {
      const selected = selectedServiceLineIds().length;
      scopeStatus.textContent = !selectedScopeMode()
        ? "Choose a note scope before saving."
        : lineMode ? `${selected} service line${selected === 1 ? "" : "s"} selected.` : "This note will apply to the entire claim.";
    }
  };
  textarea?.addEventListener("input", () => { if (count) count.textContent = String(textarea.value.length); });
  scopeRadios.forEach((radio) => radio.addEventListener("change", syncScopePicker));
  const repaintChoices = () => {
    if (!lineChoices?.isConnected) return;
    lineChoices.innerHTML = claimNoteChoicesPageHtml(claim, focusServiceId, selectedLineIds);
    if (claim.servicePage?.complete) bindPreparedClaimServicePager(lineChoices,claim,claim.repaintServicePage || repaintChoices);
    else bindManagementListPager(lineChoices, "claim-note-choices", repaintChoices);
  };
  if (claim.servicePage?.complete) {
    claim.repaintNoteChoices = repaintChoices;
    bindPreparedClaimServicePager(lineChoices,claim,claim.repaintServicePage || repaintChoices);
  } else bindManagementListPager(lineChoices, "claim-note-choices", repaintChoices);
  lineChoices?.addEventListener("change", (event) => {
    const input = event.target;
    if (input?.name !== "claimNoteServiceLine") return;
    const id = String(input.value || "").trim();
    if (!id) return;
    if (input.checked && !selectedLineIds.has(id) && selectedLineIds.size >= 100) {
      input.checked = false;
      showToast("Select at most 100 service lines for one note.");
    } else if (input.checked) selectedLineIds.add(id);
    else selectedLineIds.delete(id);
    syncScopePicker();
  });
  syncScopePicker();
  composer?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!hasPermission(PERMISSIONS.ADD_CLAIM_NOTE)) return denyAction(PERMISSIONS.ADD_CLAIM_NOTE, "add claim note");
    const text = String(textarea?.value || "").trim();
    const scopeMode = selectedScopeMode();
    const serviceLineIds = selectedServiceLineIds();
    if (!text) return showToast("Enter a claim note first.");
    if (text.length > 20000) return showToast("Claim notes can be up to 20,000 characters.");
    if (!scopeMode) return showToast("Choose whether this note applies to the entire claim or selected service lines.");
    if (scopeMode === "service_lines" && !serviceLineIds.length) return showToast("Select at least one service line for this note.");
    const submit = composer.querySelector("button[type='submit']");
    if (submit) submit.disabled = true;
    try {
      await postClaimNoteApi("/api/workflow/note", {
        orgId: claim.orgId,
        claimId: claim.claimId,
        text,
        scopeMode,
        serviceLineIds: scopeMode === "service_lines" ? serviceLineIds : [],
        commandId: claimNoteCommandId("note-create"),
        sourceSurface: "claim-drawer"
      });
      invalidatePortalReadsAfterWorkflowWrite();
      showToast(scopeMode === "claim" ? "Entire-claim note added." : `Note added to ${serviceLineIds.length} service line${serviceLineIds.length === 1 ? "" : "s"}.`);
      reopenActiveClaimDrawer();
    } catch (error) {
      showToast(error.message || "Claim note could not be saved.");
      if (submit) submit.disabled = false;
    }
  });
  bindClaimLedgerControls(claim, els.drawerBody);
}

// A follow-up change reads as one sentence of history: WHEN, WHO, and what
// moved. WHO is an employee's display name, the rule behind a system change,
// or the import that carried it - never a raw user id, and never a metadata
// blob. Rows that are not follow-up changes keep the older audit rendering.
function claimAuditFollowUpEvent(item) {
  if (!item) return false;
  if (item.previousFollowUpAction || item.nextFollowUpAction || item.previousFollowUpDate || item.nextFollowUpDate) return true;
  return /follow.?up/i.test(String(item.action || item.actionLabel || ""));
}

function claimFollowUpActorLine(item = {}) {
  const kind = String(item.actorKind || "").toLowerCase();
  if (kind === "system") {
    const rule = String(item.ruleLabel || "").trim();
    return rule ? "System \u00b7 " + rule : "System";
  }
  if (kind === "import") return String(item.sourceLabel || "").trim() || "Imported history";
  // The backend tells us whether it resolved the actor id to a person. When it
  // did not, the id is not a name and must not be printed as one.
  if (kind === "human" && item.actorDisplayNameResolved === false) return "Unknown user";
  const name = String(item.actorDisplayName || item.authorFullName || "").trim();
  return name && name !== "Author not recorded" ? name : "Employee not recorded";
}

// A review DECISION carries no before and after -- the decision ledger does
// not store one, and the touch row written by the same decision already says
// what changed. Printed as a change it read "\u2014 \u2192 Escalate", an arrow out of
// nowhere, beside the true row for the same event.
function claimFollowUpChangeLine(item = {}) {
  if (item && item.decisionOnly === true) return "";
  const from = String(item.previousFollowUpAction || "").trim();
  const to = String(item.nextFollowUpAction || "").trim();
  // Neither side is known, so there is no transition to describe. The claim
  // AUDIT rows in the same history read project both sides blank by
  // construction -- they are access and decision events, not value changes --
  // and "\u2014 \u2192 cleared" for one of those says a follow-up was removed when
  // nothing of the kind happened.
  if (!from && !to) return "";
  return (from || "\u2014") + " \u2192 " + (to || "cleared");
}

function claimFollowUpDateChangeLine(item = {}) {
  if (item && item.decisionOnly === true) return "";
  const previous = String(item.previousFollowUpDate || "").trim();
  const next = String(item.nextFollowUpDate || "").trim();
  if (previous === next || (!previous && !next)) return "";
  return "Follow-up date: " + (previous ? formatDate(previous) : "\u2014") + " \u2192 " + (next ? formatDate(next) : "cleared");
}

function claimFollowUpHistoryRowHtml(item = {}) {
  const dateChange = claimFollowUpDateChangeLine(item);
  const ruleLabel = String(item.ruleLabel || "").trim();
  // System reasons arrive prefixed with the rule label that the actor line
  // ("System · <rule>") already carries. Print it once.
  let reason = String(item.reason || "").trim();
  if (String(item.actorKind || "").toLowerCase() === "system" && ruleLabel && reason.startsWith(ruleLabel + ": ")) reason = reason.slice(ruleLabel.length + 2).trim();
  return '<article class="followup-history-row">'
    + '<div class="followup-history-head"><strong>' + escapeHtml(claimFollowUpActorLine(item)) + '</strong>'
    + '<small>' + escapeHtml(claimNoteTimestampLabel(item)) + '</small></div>'
    + (claimFollowUpChangeLine(item) ? '<p class="followup-history-change">' + escapeHtml(claimFollowUpChangeLine(item)) + '</p>' : '')
    + (item.followUpVersion?.supersedesProposalId ? '<p class="followup-history-reason">Replaced the previous pending follow-up. The previous version is superseded; only the latest needs approval.</p>' : '')
    + (dateChange ? '<p class="followup-history-date">' + escapeHtml(dateChange) + '</p>' : '')
    + (reason ? '<p class="followup-history-reason">' + escapeHtml(reason) + '</p>' : '')
    + '</article>';
}

function claimFollowUpRangeText(count) {
  if (!count) return "No follow-up changes on this page";
  return count === 1 ? "1 follow-up change on this page" : count.toLocaleString() + " follow-up changes on this page";
}

// Nathan asked to open a line and see every follow-up change with who and why,
// so the drawer carries this section under the notes. It reads ONLY follow-up
// events; the rest of the structured audit stream stays out of a biller's way.
function claimFollowUpHistoryPanelHtml(claim = {}, focusServiceId = "") {
  if (!hasPermission(PERMISSIONS.VIEW_CLAIM_NOTE_AUDIT)) return "";
  const lineScoped = String(focusServiceId || "").trim() !== "";
  const body = claim.claimNoteAudit
    ? claimLedgerPageHtml(claim, "audit", { followUpOnly: true })
    : '<div class="followup-history-load"><p>'
      + escapeHtml("Every follow-up change on this " + (lineScoped ? "service line" : "claim") + ", loaded on demand so the drawer opens fast.")
      + '</p><button class="ghost-button" type="button" data-load-follow-up-history>Load follow-up history</button></div>';
  return '<section class="panel followup-history-panel" data-panel="follow-up-history">'
    + '<div class="panel-heading-row"><div><h3>Follow-up history</h3>'
    + '<p>Who changed the follow-up action, when, and why.</p></div></div>'
    + '<div class="claim-note-panel-body" data-follow-up-history-body>' + body + '</div>'
    + '</section>';
}

// The history stays LAZY on purpose: opening a drawer must never pay for it
// (verify_frontend_claim_notes_contract line 58). This is the only path that
// starts the first audit read, and it seeds the page state the pager expects.
function loadClaimFollowUpHistory(root) {
  const context = activeClaimDrawerContext;
  if (!context || !context.claim || !hasPermission(PERMISSIONS.VIEW_CLAIM_NOTE_AUDIT)) return;
  const scope = root || els.drawerBody;
  const host = scope && scope.querySelector("[data-follow-up-history-body]");
  if (!host) return;
  if (!context.claim.claimNoteAudit) {
    context.claim.claimNoteAudit = { items: [], status: "ok", hasMore: false, nextCursor: "", paging: { index: 0, cursors: [""], starts: [0], keys: [[]] } };
  }
  host.innerHTML = claimLedgerPageHtml(context.claim, "audit", { followUpOnly: true });
  bindClaimLedgerControls(context.claim, host);
  loadMoreClaimLedger("audit", "first");
}

function claimLedgerPageHtml(claim, kind, options = {}) {
  const audit = kind === "audit";
  const followUpOnly = audit && options.followUpOnly === true;
  if (!hasPermission(audit ? PERMISSIONS.VIEW_CLAIM_NOTE_AUDIT : PERMISSIONS.VIEW_CLAIM_NOTES)) return "";
  const ledger = (audit ? claim.claimNoteAudit : claim.claimNotes) || { items: [], status: "loading" };
  const allItems = Array.isArray(ledger.items) ? ledger.items : [];
  const items = followUpOnly ? allItems.filter(claimAuditFollowUpEvent) : allItems;
  const paging = ledger.paging || { index: 0, starts: [0] };
  const busy = paging.pending || ledger.status === "loading";
  const index = paging.index || 0;
  const start = Number(paging.starts?.[index] || 0);
  const label = followUpOnly ? "Follow-up history" : audit ? "Audit history" : "Claim notes";
  const range = followUpOnly ? claimFollowUpRangeText(items.length) : items.length ? "Rows " + (start + 1).toLocaleString() + "–" + (start + items.length).toLocaleString() : "No entries on this page";
  const status = busy ? (paging.slow ? "Still loading " + label.toLowerCase() + ". You can continue editing your note while this finishes." : "Loading " + label.toLowerCase() + "…") : range + " · Page " + (index + 1).toLocaleString() + (ledger.hasMore ? " · More available" : " · End of history");
  let content;
  if (ledger.status === "error" || ledger.status === "forbidden") content = '<div class="claim-note-load-error" role="alert">' + escapeHtml(ledger.error || label + " could not be loaded.") + "</div>";
  else if (ledger.status === "hidden") content = '<p role="status">' + label + " remains hidden while the legacy import is verified.</p>";
  else if (!items.length) content = busy ? "" : !audit ? `<p class="drawer-note-empty">${index ? "No notes on this page." : "No claim notes yet."}</p>` : empty(index ? (followUpOnly ? "No follow-up changes on this page." : "No entries on this page.") : "No follow-up changes recorded yet.");
  else if (!audit) content = items.map(claimNoteCardHtml).join("");
  else content = items.map(item => claimAuditFollowUpEvent(item) ? claimFollowUpHistoryRowHtml(item) : '<div class="claim-note-audit-row"><div><strong>' + escapeHtml(item.action || item.kind || "Audit event") + '</strong><small>' + escapeHtml(claimNoteTimestampLabel(item)) + " · " + escapeHtml(item.authorFullName || "System") + '</small></div><span>' + escapeHtml([item.status, claimNoteScopeLabel(item), item.eventType].filter(Boolean).join(" · ")) + '</span>' + (item.reason ? '<p>' + escapeHtml(item.reason) + '</p>' : "") + '</div>').join("");
  const enabled = ledger.status !== "forbidden" && ledger.status !== "hidden";
  const button = (action, text, disabled) => '<button class="ghost-button" type="button" data-claim-ledger-kind="' + kind + '" data-claim-ledger-page="' + action + '"' + (disabled || busy ? ' disabled' : '') + '>' + text + '</button>';
  const error = paging.error ? '<p role="alert">' + escapeHtml(paging.error) + '</p>' : '';
  const controls = enabled && (audit || items.length || index || ledger.hasMore || paging.error || ledger.status === "error") ? '<nav class="report-pager" aria-label="' + label + ' pages">' + button("first", "First", index === 0) + button("prev", "Previous", index === 0) + button("next", "Next", !ledger.hasMore || !ledger.nextCursor) + ((paging.error || ledger.status === "error") ? button("retry", "Retry", false) : '') + '</nav>' : '';
  return '<div data-claim-ledger-page-host="' + kind + '"' + (followUpOnly ? ' data-claim-ledger-followup-only="true"' : '') + ' aria-busy="' + Boolean(busy) + '"><p role="status" aria-live="polite">' + (busy ? '<span class="inline-spinner" aria-hidden="true"></span> ' : '') + escapeHtml(status) + '</p><div class="' + (audit ? 'claim-note-audit-list' : 'claim-note-list') + '">' + content + '</div>' + error + controls + '</div>';
}

function bindClaimLedgerControls(claim, root) {
  if (!root) return;
  for (const mode of ["correct", "void"]) {
    root.querySelectorAll('[data-claim-note-' + mode + ']').forEach(button => button.addEventListener("click", () => {
      const id = button.getAttribute('data-claim-note-' + mode) || "";
      const note = (claim.claimNotes?.items || []).find(item => (item.rootNoteId || item.noteId) === id);
      if (note) openClaimNoteMutationModal(mode, claim, note);
    }));
  }
  root.querySelectorAll("[data-load-follow-up-history]").forEach(button => button.addEventListener("click", () => loadClaimFollowUpHistory(root)));
  root.querySelectorAll("[data-claim-ledger-page]").forEach(button => button.addEventListener("click", () => loadMoreClaimLedger(button.getAttribute("data-claim-ledger-kind"), button.getAttribute("data-claim-ledger-page"))));
}

function repaintClaimLedgerPage(context, kind) {
  // Only replace this timeline. The composer, line selections, other panels and
  // their event handlers stay mounted while a history page changes.
  const host = els.drawerBody?.querySelector('[data-claim-ledger-page-host="' + kind + '"]');
  if (!host) return;
  const hadFocus = host.contains(document.activeElement);
  const focusedAction = hadFocus ? document.activeElement.getAttribute?.("data-claim-ledger-page") : "";
  const followUpOnly = host.getAttribute?.("data-claim-ledger-followup-only") === "true";
  host.outerHTML = claimLedgerPageHtml(context.claim, kind, { followUpOnly });
  const replacement = els.drawerBody.querySelector('[data-claim-ledger-page-host="' + kind + '"]');
  bindClaimLedgerControls(context.claim, replacement);
  const ledger = kind === "audit" ? context.claim.claimNoteAudit : context.claim.claimNotes;
  const count = els.drawerBody.querySelector('[data-claim-ledger-count="' + kind + '"]');
  if (count) count.innerHTML = pill((ledger.items || []).length + " on this page", ledger.items?.length ? "blue" : "muted");
  if (kind === "notes") {
    const composer = els.drawerBody.querySelector("[data-claim-note-create]");
    if (composer) composer.hidden = !hasPermission(PERMISSIONS.ADD_CLAIM_NOTE) || ledger.sourceCompleteness?.exposed === false;
    const source = els.drawerBody.querySelector(".claim-note-source-status");
    if (source) source.textContent = claimNotesCompletenessText(ledger.sourceCompleteness);
  }
  if (hadFocus) {
    const focus = (focusedAction && replacement?.querySelector('[data-claim-ledger-page="' + focusedAction + '"]:not(:disabled)')) || replacement?.querySelector('[role="status"]');
    focus?.setAttribute("tabindex", "-1");
    focus?.focus({ preventScroll: true });
  }
}

async function loadMoreClaimLedger(kind, action = "next") {
  if (!["notes", "audit"].includes(kind) || !["first", "prev", "next", "retry"].includes(action)) return;
  const permission = kind === "audit" ? PERMISSIONS.VIEW_CLAIM_NOTE_AUDIT : PERMISSIONS.VIEW_CLAIM_NOTES;
  if (!hasPermission(permission)) return;
  const context = activeClaimDrawerContext;
  if (!context || context.requestKey !== serverClaimDrawerRequestKey || context.token !== trackerApiToken()) return;
  try { if (JSON.stringify(JSON.parse(context.requestKey).access) !== JSON.stringify(workflowCommandAccessSnapshot())) return; } catch (_) { return; }
  const ledger = kind === "audit" ? context.claim.claimNoteAudit : context.claim.claimNotes;
  if (!ledger || ledger.status === "forbidden" || ledger.status === "hidden" || ledger.paging?.pending) return;
  const clientSafe = isClientRole();
  const itemKey = item => clientSafe ? String(item.rootNoteId || item.noteId || "") : String(item.noteId) + ":" + item.revision + ":" + item.eventType;
  const paging = ledger.paging || (ledger.paging = { index: 0, cursors: [""], starts: [0], keys: [(ledger.items || []).map(itemKey)] });
  const target = action === "first" ? 0 : action === "prev" ? paging.index - 1 : action === "retry" ? (paging.failedIndex ?? paging.index) : paging.index + 1;
  if (target < 0 || (action === "next" && (!ledger.hasMore || !ledger.nextCursor))) return;
  const cursor = target === paging.index + 1 ? ledger.nextCursor : paging.cursors[target];
  if (target > 0 && !cursor) return;
  const accessKey = workflowAccessScopedRequestKey({});
  const principalToken = trackerApiToken();
  const requestKey = serverClaimDrawerRequestKey;
  const stillCurrent = () => activeClaimDrawerContext === context && serverClaimDrawerRequestKey === requestKey && workflowAccessScopedRequestKey({}) === accessKey && trackerApiToken() === principalToken && hasPermission(permission) && (kind === "audit" ? context.claim.claimNoteAudit : context.claim.claimNotes) === ledger;
  paging.pending = true;
  paging.error = "";
  paging.slow = false;
  paging.failedIndex = target;
  repaintClaimLedgerPage(context, kind);
  const params = new URLSearchParams({ orgId: context.orgId, limit: "50" });
  if (cursor) params.set("cursor", cursor);
  if (context.focusServiceId) params.set("serviceId", context.focusServiceId);
  const controller = new AbortController();
  const slowTimer = setTimeout(() => { if (stillCurrent() && paging.pending) { paging.slow = true; repaintClaimLedgerPage(context, kind); } }, 4000);
  let timeout;
  try {
    const envelope = await Promise.race([
      claimDrawerFetchJson(context.base + "/api/tracker/claims/" + encodeURIComponent(context.claimId) + "/" + (kind === "audit" ? "audit" : "notes") + "?" + params, context.token, { signal: controller.signal, isCurrent: stillCurrent }),
      new Promise((resolve, reject) => { timeout = setTimeout(() => { controller.abort(); reject(new Error("history_read_timeout")); }, 30000); })
    ]);
    if (!stillCurrent()) return;
    if (!envelope.ok) throw new Error("history_read_unavailable");
    const next = kind === "audit" ? normalizeClaimNoteAuditResponse(envelope.body) : normalizeClaimNotesResponse(envelope.body);
    const seen = new Set(paging.keys.slice(0, target).flat());
    const items = next.items.filter(item => { const key = itemKey(item); if (seen.has(key)) return false; seen.add(key); return true; });
    const start = target === paging.index + 1 ? (paging.starts[paging.index] || 0) + (ledger.items || []).length : (paging.starts[target] || 0);
    paging.cursors = paging.cursors.slice(0, target);
    paging.starts = paging.starts.slice(0, target);
    paging.keys = paging.keys.slice(0, target);
    paging.cursors[target] = cursor || "";
    paging.starts[target] = start;
    paging.keys[target] = items.map(itemKey);
    paging.index = target;
    paging.failedIndex = null;
    Object.assign(ledger, next, { items, paging });
  } catch (_) {
    if (stillCurrent()) paging.error = (kind === "audit" ? "Audit history" : "Claim notes") + " could not be loaded. The current page is unchanged. Try again.";
  } finally {
    clearTimeout(slowTimer);
    clearTimeout(timeout);
    paging.pending = false;
    if (stillCurrent()) repaintClaimLedgerPage(context, kind);
  }
}

function openClaimNoteMutationModal(mode, claim, note) {
  const correct = mode === "correct";
  const permission = correct ? PERMISSIONS.CORRECT_OWN_CLAIM_NOTE : PERMISSIONS.VOID_CLAIM_NOTE;
  if (!hasPermission(permission)) return denyAction(permission, `${mode} claim note`);
  claimNoteMutationState = { mode, claim, note };
  if (els.claimNoteMutationTitle) els.claimNoteMutationTitle.textContent = correct ? "Correct claim note" : "Void claim note";
  if (els.claimNoteMutationContext) els.claimNoteMutationContext.textContent = correct
    ? "Save a corrected version. The original remains in the internal audit history."
    : "The note will be hidden from the effective timeline. Its immutable history remains internal.";
  if (els.claimNoteMutationTextLabel) els.claimNoteMutationTextLabel.hidden = !correct;
  if (els.claimNoteMutationText) els.claimNoteMutationText.value = correct ? note.text : "";
  if (els.claimNoteMutationReason) els.claimNoteMutationReason.value = "";
  if (els.claimNoteMutationStatus) els.claimNoteMutationStatus.textContent = "";
  if (els.claimNoteMutationSubmitBtn) els.claimNoteMutationSubmitBtn.textContent = correct ? "Save correction" : "Void note";
  els.claimNoteMutationModal?.classList.remove("hidden");
  activateForegroundModal(els.claimNoteMutationModal, {
    initialFocus: correct ? els.claimNoteMutationText : els.claimNoteMutationReason,
    onEscape: closeClaimNoteMutationModal,
    returnFocus: document.activeElement
  });
}

// ONE list of what is on, read by the sentence AND by the count badge beside
// the Show/Hide button. They were separate before the badge existed; keeping
// them separate is how a bar comes to say "1 active" over two live filters.
// The company is named by its DISPLAY LABEL where the inventory carries one
// (PORTAL_DIRECTIVES: company names, never org ids), falling back to the
// tenant name itself - never to an internal key.
function reportActiveFilterDescriptions() {
  const values = reportFilterValues();
  const active = [];
  // The company first and on its own: it is a single value, it is named by its
  // DISPLAY LABEL where the inventory carries one (PORTAL_DIRECTIVES: company
  // names, never org ids), and it never falls back to an internal key.
  if (values.tenant && values.tenant !== "All") active.push(`Client: ${values.tenantLabel || values.tenant}`);
  // EVERY OTHER FILTER NAMES ITS VALUES AND ITS DIRECTION, through the SAME
  // sentence builder the All Data chip rail uses - so "2 active · Payer: Aetna,
  // UHC · Follow-Up Action: not Client Action Pending (5)" is one function's
  // output on both surfaces and the two cannot drift. filterSelectionSummaryText
  // is what collapses a whole Client Action Pending family to its family name
  // and count instead of five near-identical strings, and what says "not" out
  // loud rather than naming what is hidden as though it were what is shown.
  REPORT_FILTER_DEFINITIONS.filter((row) => row.multi).forEach((row) => {
    const selected = Array.isArray(values.selections?.[row.key]) ? values.selections[row.key] : [];
    if (!selected.length) return;
    active.push(`${row.chip}: ${filterSelectionSummaryText(row.key, selected, filterModeExcludes(values.modes || {}, row.key))}`);
  });
  if (values.fromDate || values.toDate) active.push(`Dates: ${values.fromDate || "start"} to ${values.toDate || "today"}`);
  return active;
}

function reportFilterSummaryText(enabled = true) {
  if (!enabled) return "Use the report-specific controls below";
  const active = reportActiveFilterDescriptions();
  return active.length ? `${active.length} active · ${active.slice(0, 2).join(" · ")}${active.length > 2 ? " · …" : ""}` : "All data";
}

function closeClaimNoteMutationModal() {
  claimNoteMutationState = null;
  els.claimNoteMutationModal?.classList.add("hidden");
  deactivateForegroundModal(els.claimNoteMutationModal, { restoreFocus: true });
}

async function submitClaimNoteMutation(event) {
  event.preventDefault();
  const stateValue = claimNoteMutationState;
  if (!stateValue) return;
  const correct = stateValue.mode === "correct";
  const text = String(els.claimNoteMutationText?.value || "").trim();
  const reason = String(els.claimNoteMutationReason?.value || "").trim();
  if (correct && !text) return setText(els.claimNoteMutationStatus, "Enter the corrected note text.");
  if (text.length > 20000) return setText(els.claimNoteMutationStatus, "Claim notes can be up to 20,000 characters.");
  if (!reason) return setText(els.claimNoteMutationStatus, "Enter a reason. It becomes part of the audit history.");
  setDisabled(els.claimNoteMutationSubmitBtn, true);
  try {
    await postClaimNoteApi(`/api/workflow/note/${correct ? "correct" : "void"}`, {
      orgId: stateValue.claim.orgId,
      claimId: stateValue.claim.claimId,
      rootNoteId: stateValue.note.rootNoteId || stateValue.note.noteId,
      expectedRevision: stateValue.note.revision,
      ...(correct ? { text } : {}),
      reason,
      commandId: claimNoteCommandId(correct ? "note-correct" : "note-void")
    });
    invalidatePortalReadsAfterWorkflowWrite();
    closeClaimNoteMutationModal();
    showToast(correct ? "Claim note corrected." : "Claim note voided.");
    reopenActiveClaimDrawer();
  } catch (error) {
    setText(els.claimNoteMutationStatus, error.message || "The claim note could not be changed.");
  } finally {
    setDisabled(els.claimNoteMutationSubmitBtn, false);
  }
}

function renderAppealPacketPanel(claim, canSeeFinancials) {
  const packet = appealPacketReadiness(claim);
  return `
    <section class="panel appeal-packet-panel">
      <div class="panel-heading-row">
        <div>
          <h3>Appeal / Correction Packet</h3>
          <p>Evidence checklist before appeal, corrected claim, payer portal escalation, or client documentation request.</p>
        </div>
        ${pill(packet.statusLabel, packet.tone)}
      </div>
      <div class="appeal-packet-summary">
        <div>
          <span>Packet Readiness</span>
          <strong>${packet.readyCount}/${packet.items.length}</strong>
          <small>${packet.summary}</small>
        </div>
        <div>
          <span>Financial Exposure</span>
          <strong>${canSeeFinancials ? money(packet.exposure) : "Restricted"}</strong>
          <small>Outstanding or expected reimbursement under review</small>
        </div>
        <div>
          <span>Recommended Route</span>
          <strong>${escapeHtml(packet.route)}</strong>
          <small>${escapeHtml(packet.routeDetail)}</small>
        </div>
      </div>
      <div class="appeal-packet-checklist">
        ${packet.items.map((item) => `
          <div class="${item.ready ? "is-ready" : "is-missing"}">
            <span>${escapeHtml(item.label)}</span>
            <strong>${escapeHtml(item.ready ? "Ready" : "Missing")}</strong>
            <small>${escapeHtml(item.detail)}</small>
          </div>
        `).join("")}
      </div>
      <div class="appeal-packet-next">
        <strong>${escapeHtml(packet.nextAction)}</strong>
        <span>${escapeHtml(packet.nextDetail)}</span>
      </div>
    </section>
  `;
}

function appealPacketReadiness(claim = {}) {
  const financial = claimFinancialView(claim);
  const guards = evaluateClaimCalculationGuards(claim);
  const notes = Array.isArray(claim.notes) ? claim.notes : [];
  const denied = isDeniedClaim(claim);
  const authOrDocs = needsClientAction(claim);
  const services = claim.services || [];
  const hasClaimNarrative = Boolean(firstPresent(
    claim.latestClaimNoteText,
    claim.claimNoteSummary?.text,
    claim.claimNoteSummary?.latestText,
    notes.at(-1)?.text
  ));
  const hasAdjustmentEvidence = services.some((service) => adjustmentRows(service).length > 0);
  const attributionClear = !sameExternalPayerConflict(claim) && financial.unmatchedPaid <= 0;
  const mathClear = claimFinancialReconciliation(claim).mathOk && !guards.some((guard) => guard.severity === "blocker");
  const payerEvidence = /eob|era|payer|portal|response|denied|denial|reject|appeal|processed|manual review/i.test(claimOperationalText(claim));
  const followUpReady = Boolean(claim.followUpDate) && Boolean(claim.action);
  const items = [
    {
      key: "denialReason",
      label: "Denial / Issue Reason",
      ready: denied || payerEvidence || authOrDocs,
      detail: denied ? denialReason(claim) : authOrDocs ? "Client action issue identified" : "No denial or payer issue reason detected"
    },
    {
      key: "payerEvidence",
      label: "Payer Evidence",
      ready: payerEvidence,
      detail: payerEvidence ? "Payer/EOB/ERA/status language present" : "Add payer response, EOB, ERA, or portal status"
    },
    {
      key: "mathEvidence",
      label: "Expected / Paid Math",
      ready: mathClear,
      detail: mathClear ? "Claim and service totals match with no calculation issues" : calculationGuardSummary(guards)
    },
    {
      key: "attribution",
      label: "Payment Attribution",
      ready: attributionClear,
      detail: attributionClear ? paymentAttributionExplanation(claim) : "Resolve payer sequence or unmatched paid before appeal"
    },
    {
      key: "serviceEvidence",
      label: "Service Evidence",
      ready: hasClaimNarrative,
      detail: hasClaimNarrative ? "Claim notes available" : "Add claim-level rationale or supporting detail"
    },
    {
      key: "adjustments",
      label: "CARC / Adjustment Detail",
      ready: hasAdjustmentEvidence || financial.sourceOutstanding <= 0,
      detail: hasAdjustmentEvidence ? "Adjustment rows available" : "Missing adjustment detail for open balance"
    },
    {
      key: "workflow",
      label: "Workflow Evidence",
      ready: hasClaimNarrative && followUpReady,
      detail: hasClaimNarrative && followUpReady ? "Claim note and next follow-up are present" : "Add note, owner action, and follow-up date"
    }
  ];
  const readyCount = items.filter((item) => item.ready).length;
  const missing = items.filter((item) => !item.ready);
  const route = denied ? "Appeal / Corrected Claim" : authOrDocs ? "Client Evidence Request" : financial.underpayment > 0 ? "Underpayment Recovery" : "Payer Follow-Up";
  const statusLabel = missing.length === 0 ? "Packet Ready" : `${missing.length} Missing`;
  const tone = missing.length === 0 ? "green" : missing.length <= 2 ? "amber" : "red";
  return {
    items,
    readyCount,
    missingCount: missing.length,
    statusLabel,
    tone,
    route,
    routeDetail: denied ? denialReason(claim) : workReason(claim),
    exposure: moneyNumber(Math.max(financial.sourceOutstanding, financial.underpayment, financial.expectedReimbursement - financial.totalInsurancePaid, 0)),
    summary: missing.length ? `Missing ${missing.slice(0, 2).map((item) => item.label).join(", ")}${missing.length > 2 ? ` +${missing.length - 2}` : ""}` : "All appeal/correction evidence checks are ready",
    nextAction: missing.length ? `Complete ${missing[0].label}` : `Ready for ${route}`,
    nextDetail: missing.length ? missing[0].detail : "Manager can approve submission path or route to payer/client workflow."
  };
}

function renderWriteOffGovernancePanel(claim, canSeeFinancials) {
  const financial = claimFinancialView(claim);
  const snapshot = writeOffSnapshotForClaim(claim);
  const amount = snapshot?.amount ?? financial.sourceOutstanding;
  const canManage = hasPermission(PERMISSIONS.MANAGE_WRITE_OFFS);
  const eligible = canSeeFinancials && amount > 0;
  const guardSummary = snapshot?.guardSummary || calculationGuardSummary(evaluateClaimCalculationGuards(claim));
  const calculationReview = guardSummary && guardSummary !== "Clear" ? guardSummary : "No issues found";
  const statusLabel = snapshot ? "Review Record Saved" : eligible ? "Manager Review Required" : "No Open Write-Off Balance";
  const statusTone = snapshot ? "green" : eligible ? "amber" : "gray";
  return `
    <section class="panel writeoff-governance-panel">
      <div class="panel-heading-row">
        <div>
          <h3>Write-Off Review</h3>
          <p>Manager-only write-off review. Saving this record does not change the claim balance.</p>
        </div>
        ${pill(statusLabel, statusTone)}
      </div>
      <div class="writeoff-governance-grid">
        <div><span>Amount to review</span><strong>${canSeeFinancials ? money(amount) : "Restricted"}</strong><small>Open insurance balance at review time</small></div>
        <div><span>Who can save this</span><strong>${canManage ? "Manager or admin" : "Restricted"}</strong><small>${canManage ? "You can save a write-off review" : "Only a manager or admin can save it"}</small></div>
        <div><span>Calculation review</span><strong>${escapeHtml(calculationReview)}</strong><small>Saved with this review</small></div>
        <div><span>What saving does</span><strong>Records the review</strong><small>The write-off still needs approval</small></div>
      </div>
      ${snapshot ? `
        <div class="writeoff-snapshot-card">
          <span>Saved write-off review</span>
          <strong>${canSeeFinancials ? money(snapshot.amount) : "Restricted"} · ${escapeHtml(snapshot.status ? "Saved" : "Saved")}</strong>
          <p>${escapeHtml(snapshot.reason || "No reason stored.")}</p>
          <small>${escapeHtml(snapshot.by || "Unknown")} · ${escapeHtml(formatDateTime(snapshot.at || ""))} · ${escapeHtml(snapshot.guardSummary && snapshot.guardSummary !== "Clear" ? snapshot.guardSummary : "No calculation issues")}</small>
        </div>
      ` : ""}
      ${canManage && eligible ? `
        <div class="writeoff-actions">
          <label>
            Write-off reason
            <textarea id="writeOffReasonText" rows="3" placeholder="Document payer response, exhaustion of appeal path, client approval evidence, or contractual reason."></textarea>
          </label>
          <button id="captureWriteOffSnapshotBtn" class="primary-button" type="button">Save Review Record</button>
        </div>
      ` : `
        <div class="writeoff-restricted">
          <strong>${canManage ? "No eligible open insurance balance." : "Only a manager can save this."}</strong>
          <span>${canManage ? "The claim has no positive open insurance balance to review." : "Biller, offshore, and client roles can review status but cannot create write-off review records."}</span>
        </div>
      `}
    </section>
  `;
}

function writeOffSnapshotForClaim(claim = {}) {
  const stored = state.workflowByClaim?.[claim.claimId]?.writeOffSnapshot || claim.writeOffSnapshot || null;
  if (!stored || typeof stored !== "object") return null;
  return {
    schema: String(stored.schema || "tms-master-portal.write-off-snapshot.v1"),
    status: String(stored.status || "manager_review_snapshot"),
    amount: moneyNumber(stored.amount || stored.sourceOutstandingBefore || 0),
    sourceOutstandingBefore: moneyNumber(stored.sourceOutstandingBefore || stored.amount || 0),
    totalExpectedBefore: moneyNumber(stored.totalExpectedBefore || 0),
    totalInsurancePaidBefore: moneyNumber(stored.totalInsurancePaidBefore || 0),
    payerResponsibilityBefore: moneyNumber(stored.payerResponsibilityBefore || 0),
    reason: String(stored.reason || ""),
    by: String(stored.by || ""),
    role: String(stored.role || ""),
    at: String(stored.at || ""),
    guardSummary: String(stored.guardSummary || ""),
    noWriteBack: stored.noWriteBack !== false
  };
}

function claimFinancialReconciliation(claim) {
  const financial = claimFinancialView(claim);
  const services = claim.services || [];
  const prepared = claim.servicePage?.complete ? (claim.reconciliationServiceTotals || {}) : null;
  const serviceExpected = prepared ? prepared.serviceExpected : services.reduce((sum, service) => sum + serviceFinancialView(service, claim).expectedReimbursement, 0);
  const servicePaid = prepared ? prepared.servicePaid : services.reduce((sum, service) => sum + serviceFinancialView(service, claim).insurancePaid, 0);
  const serviceOwed = prepared ? prepared.serviceOwed : services.reduce((sum, service) => sum + serviceFinancialView(service, claim).sourceOutstanding, 0);
  const rows = [
    { label: "Expected", claimTotal: financial.expectedReimbursement, serviceTotal: serviceExpected },
    { label: "Paid", claimTotal: financial.totalInsurancePaid, serviceTotal: servicePaid },
    { label: "Outstanding", claimTotal: financial.sourceOutstanding, serviceTotal: serviceOwed }
  ].map((row) => ({ ...row, delta: row.claimTotal != null && row.serviceTotal != null && Number.isFinite(Number(row.claimTotal)) && Number.isFinite(Number(row.serviceTotal)) ? moneyNumber(row.claimTotal - row.serviceTotal) : null }));
  return {
    rows,
    mathOk: rows.every((row) => row.delta === 0)
  };
}

// THE TITLE, THE LABEL AND THE TABLE HAVE TO AGREE.
//
// A panel headed "Payment Attribution" printed "No attribution" directly above a
// table showing that claim's $1,208.96 attributed to Primary. The label was a
// bare `status || "No attribution"`, and the lane's status vocabulary is
// machine-cased (`complete`, `no_payment`, `mixed`) and sometimes simply absent -
// so a blank status, which means "nobody told us the status", rendered as the
// affirmative claim "there is no attribution". That is the absence-is-not-zero
// trap (ENGINEERING_TRAPS #10) wearing words instead of digits.
//
// Three outcomes, and never the fourth:
//   a known status  -> the human spelling of that status
//   no status, but money IS attributed or payment events exist
//                   -> "Attribution status unavailable", which contradicts nothing
//                      the table below is showing
//   no status and no money and no events -> "No payment posted"
// `financial` is passed by callers that already built the claim view, so the
// common path costs nothing; it falls back for the callers that have not.
function claimAttributionStatusLabel(claim = {}, financial = null) {
  const raw = String(claim.paymentAttribution?.status || "").trim();
  if (raw) {
    const spelled = {
      complete: "Fully attributed",
      partial: "Partly attributed",
      no_payment: "No payment posted",
      unmatched: "Unmatched payment",
      mixed: "Mixed across service lines",
      conflict: "Attribution conflict",
      primary_matched: "Attributed to primary",
      secondary_matched: "Attributed to secondary"
    }[raw.toLowerCase()];
    return spelled || raw;
  }
  const view = financial || claimFinancialView(claim);
  const attributed = moneyNumber(view.primaryPaid) + moneyNumber(view.secondaryPaid) + moneyNumber(view.unmatchedPaid);
  const events = Array.isArray(claim.payments) ? claim.payments.length : 0;
  if (attributed !== 0 || events > 0) return "Attribution status unavailable";
  return "No payment posted";
}

function paymentAttributionExplanation(claim) {
  const attribution = claim.paymentAttribution || {};
  if (attribution.status) return attribution.status;
  if (sameExternalPayerConflict(claim)) return "Same external payer ID requires sequence review.";
  if (claim.payments?.length) return "Payments available for attribution review.";
  return "No posted payment attribution yet.";
}

function servicePatientResponsibilityAmount(service = {}, claim = {}) {
  const authoritativePr = truePatientResponsibility(service);
  if (authoritativePr > 0) return authoritativePr;
  if (service.truePatientResponsibility != null) return moneyNumber(service.truePatientResponsibility);
  return serviceFinancialView(service, claim).patientResponsibility || truePatientResponsibility(service);
}

function statementStatusForService(service = {}, claim = {}) {
  if (truePatientResponsibility(service) <= 0) return "Not eligible";
  return statementStatus(service, claim);
}

function claimPatientResponsibilitySummary(claim = {}) {
  const byCode = { PR1: 0, PR2: 0, PR3: 0 };
  let evidenceBacked = false;
  const rows = (claim.services || []).map((service) => {
    const adjustments = patientResponsibilityAdjustmentsForService(service);
    if (adjustments.length || hasPresent(firstPresent(service?.prAdjustmentTotal, service?.pr_adjustment_total, service?.["PR Adjustment Total"]))) evidenceBacked = true;
    const fallbackAmount = adjustments.length ? 0 : servicePatientResponsibilityAmount(service, claim);
    const amount = moneyNumber(adjustments.reduce((sum, adjustment) => sum + adjustment.amount, fallbackAmount));
    adjustments.forEach((adjustment) => {
      const key = `PR${adjustment.reasonCode}`;
      if (Object.prototype.hasOwnProperty.call(byCode, key)) byCode[key] = moneyNumber(byCode[key] + adjustment.amount);
    });
    return {
      serviceId: service.serviceId || "",
      dos: service.dos || "",
      procedure: service.procedure || "",
      amount,
      codes: adjustments.length ? Array.from(new Set(adjustments.map((adjustment) => `PR ${adjustment.reasonCode}`))).join(", ") : (truePatientResponsibility(service) > 0 ? "PR 1/2/3 total" : prCodes(service)),
      reasons: adjustments.length ? Array.from(new Set(adjustments.map((adjustment) => adjustment.reason))).join(", ") : (truePatientResponsibility(service) > 0 ? "Reason detail unavailable" : prReasons(service)),
      statementStatus: statementStatusForService(service, claim)
    };
  }).filter((row) => row.amount > 0);
  const total = moneyNumber(rows.reduce((sum, row) => sum + row.amount, 0));
  Object.keys(byCode).forEach((key) => { byCode[key] = moneyNumber(byCode[key]); });
  if (claim.servicePage?.complete && claim.preparedPrSummary?.complete) return {total:claim.preparedPrSummary.total,byCode:claim.preparedPrSummary.byCode,rows,verified:claim.preparedPrSummary.evidenceBacked,positiveLineCount:claim.preparedPrSummary.positiveLineCount};
  return { total, byCode, rows, verified: evidenceBacked };
}

function patientResponsibilityAdjustmentsForService(service = {}) {
  const explicitRows = Array.isArray(service.prAdjustments) ? service.prAdjustments : [];
  const sourceRows = explicitRows.length ? explicitRows : adjustmentRows(service);
  return sourceRows
    .filter((adjustment) => {
      const group = String(adjustment.group || adjustment.groupCode || "PR").trim().toUpperCase();
      const reasonCode = String(adjustment.reasonCode || adjustment.code || "").replace(/\D/g, "");
      return group === "PR" && ["1", "2", "3"].includes(reasonCode);
    })
    .map((adjustment) => ({
      reasonCode: String(adjustment.reasonCode || adjustment.code || "").replace(/\D/g, ""),
      reason: adjustment.reason || adjustment.description || "Patient responsibility",
      amount: moneyNumber(adjustment.amount || 0)
    }))
    .filter((adjustment) => adjustment.amount !== 0);
}


// ---------------------------------------------------------------------------
// LEGACY NOTE HEADER PARSER — ported from CT-TRACKER, 2026-08-05
//
// WHY THIS EXISTS. The Claim Life-History panel used to print the literal string
// "EZClaim" as the author of every legacy note, and its only note source was the
// EZClaim note ledger fed from silver.ezclaim_claim_notes — which returns ZERO
// rows to us: it is one of the 22 lake-backed warehouse objects behind a bulk-load
// permission we do not hold and cannot grant ourselves (WAREHOUSE_DATA_GAP_2026-08-05.md).
// So the panel was showing a fabricated author on top of an empty table.
//
// Retained as a deterministic parser reference for old demo fixtures. Runtime
// Claim Notes are now read from the portal-owned API; the backend importer owns
// parsing and provenance for all historical source content.
//
// This is a port of their parse contract
// (CT-TRACKER scripts/legacy_workflow_note_migration.py @ 96c9eaf), verbatim where
// it matters:
//   * Only a CLEARLY STRUCTURED LEADING header (a date and/or delimited initials)
//     becomes timeline metadata. A date buried in ordinary prose stays text,
//     because it may describe a service, a payment, an appeal or a deadline rather
//     than when the note was written.
//   * Unconfirmed initials must be explicitly delimited ("RQ - called payer").
//   * A two-digit year above 39 is AMBIGUOUS. The entry keeps its raw text and
//     carries a parse warning; we never pick a century.
//   * One source value splits into several entries ONLY when its first non-blank
//     line is itself a dated header and at least two dated headers exist.
//     Otherwise the whole value is one entry.
//   * Entry identity is (Service ID, canonical entry text, occurrence) — never the
//     whole source field. CT-TRACKER's original whole-source uniqueness index
//     silently discarded the second distinct entry of a multi-entry note.
//   * We NEVER invent a date, a time, or an author.
//
// ONE DELIBERATE DIVERGENCE FROM CT-TRACKER: an UNDATED header never names a
// person. Their patterns 5 and 6 will treat a bare leading "SA:" or "IS " as an
// author, and in real billing prose those are far more likely to be "Service
// Authorization" and the word "is". We keep the initials (they are still what the
// source said) but refuse to attribute them to a human without a structured date
// beside them. A confident wrong attribution on a claim note is worse than an
// honest blank — the same doctrine NORTH_STAR.md §6 applies to money.
//
// AUTHORSHIP STATES — four, all honest, none a guess:
//     dated header + confirmed initials + directory loaded -> the person's name
//     dated header + confirmed initials, directory not loaded -> the initials
//     initials we cannot confirm, or an undated header      -> the initials
//     no parsable author                                    -> "Author not recorded"
// "EZClaim" is never presented as an author anywhere.
//
// WHERE THE NAMES LIVE, AND WHY NOT HERE. app.js is served ANONYMOUSLY and with a
// one-year immutable cache (staticwebapp.config.json has no allowedRoles), so
// anything in this file is world-readable at a stable URL. Twelve staff full names
// would therefore be a publicly enumerable roster — and since the mailboxes follow
// from the names, keeping the mailboxes server-side would not have bought much.
// So this file carries only the INITIALS (not identifying on their own) and the
// parse rules. Names arrive at runtime from an authenticated, internal-notes-gated
// endpoint (see backend/noteAuthorDirectory.js) via hydrateNoteAuthorDirectory().
// Until that route is registered the panel simply shows initials — which is the
// same honest-unknown behaviour it shows for an author nobody has confirmed.
//
// TIME. We deliberately do NOT synthesize an absolute instant from a parsed
// "7/21/26 2:30 PM" header. That stamp is a business-local scribble, not a
// timezone-qualified event; converting it here would be inventing precision the
// source never had. Known limitation: parsed stamps sort as local while a ledger
// row's captured timestamp sorts as UTC, so same-day ordering between the two can
// skew by the viewer's offset. Fixing that would require inventing a zone.
// ---------------------------------------------------------------------------

// Initials mirror of backend/noteAuthorDirectory.json. displayName is EMPTY here
// on purpose (see above) and is filled in at runtime by
// hydrateNoteAuthorDirectory(). backend/verify_service_line_note_authorship.js
// FAILS if the initials/status drift from the JSON, or if a name or a mailbox
// ever appears in this literal.
const NOTE_AUTHOR_DIRECTORY = [
  { initials: "YS", displayName: "", status: "confirmed" },
  { initials: "RG", displayName: "", status: "confirmed" },
  { initials: "NS", displayName: "", status: "confirmed" },
  { initials: "MG", displayName: "", status: "confirmed" },
  { initials: "SB", displayName: "", status: "confirmed" },
  { initials: "JS", displayName: "", status: "confirmed" },
  { initials: "RB", displayName: "", status: "confirmed" },
  { initials: "SA", displayName: "", status: "confirmed" },
  { initials: "DA", displayName: "", status: "confirmed" },
  { initials: "RS", displayName: "", status: "confirmed" },
  { initials: "IS", displayName: "", status: "confirmed" },
  { initials: "AA", displayName: "", status: "confirmed" }
];

const NOTE_AUTHOR_BY_INITIALS = NOTE_AUTHOR_DIRECTORY.reduce((map, author) => {
  map[String(author.initials).toUpperCase()] = author;
  return map;
}, Object.create(null));

let noteAuthorDirectoryState = { status: "idle", error: "" };

// Fill in names from the authenticated directory response. Only initials we
// already know are accepted — the endpoint cannot introduce a new identity into
// a running page, and a blank/whitespace name is ignored rather than rendered.
function hydrateNoteAuthorDirectory(rows) {
  let applied = 0;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = normalizeNoteAuthorInitials(row && row.initials);
    const author = key ? NOTE_AUTHOR_BY_INITIALS[key] : null;
    if (!author) return;
    const displayName = String((row && row.displayName) || "").trim();
    if (!displayName) return;
    author.displayName = displayName;
    if (row.status) author.status = String(row.status);
    applied += 1;
  });
  return applied;
}

// Fetch the names once, and only for a viewer who already holds the permission
// that governs this data. A 404 (route not yet registered) is not an error worth
// showing anyone: the panel degrades to initials, which is honest.
function requestNoteAuthorDirectory() {
  if (noteAuthorDirectoryState.status !== "idle") return;
  if (typeof fetch !== "function") return;
  if (typeof hasPermission === "function" && !hasPermission(PERMISSIONS.VIEW_INTERNAL_NOTES)) return;
  const base = workflowApiBase();
  if (!base) return;
  noteAuthorDirectoryState = { status: "pending", error: "" };
  const token = workflowApiToken() || trackerApiToken();
  fetch(`${base}/api/user/note-authors`, {
    method: "GET",
    cache: "no-store",
    headers: {
      "accept": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body.error) throw new Error(body?.error?.message || body.message || `HTTP ${response.status}`);
      hydrateNoteAuthorDirectory(body.authors);
      noteAuthorDirectoryState = { status: "ok", error: "" };
    })
    .catch((error) => {
      noteAuthorDirectoryState = { status: "error", error: error.message || "Note author directory unavailable." };
    });
}

// Exact, case-insensitive, dots stripped. Anything that is not a 2-4 letter
// initials token is rejected outright, so a free-text actor name ("Front Desk",
// "system") can never collide its way into a staff identity.
function normalizeNoteAuthorInitials(value) {
  const text = String(value == null ? "" : value).replace(/\./g, "").trim().toUpperCase();
  return /^[A-Z]{2,4}$/.test(text) ? text : "";
}

function noteAuthorFromInitials(value) {
  const key = normalizeNoteAuthorInitials(value);
  return key ? (NOTE_AUTHOR_BY_INITIALS[key] || null) : null;
}

// Attribution survives a departure — who wrote a note is a historical fact, and
// blanking it later would destroy true history. `status` only removes the "you
// can still go ask them" affordance.
function noteAuthorNameOrInitials(author) {
  if (!author) return "";
  if (!author.displayName) return author.initials;
  return author.status === "departed" ? `${author.displayName} (former staff)` : author.displayName;
}

// The ONE renderer for "who did this" on an explicit author FIELD (portal events,
// ledger rows). Unresolvable input comes back as the raw text it arrived as, or as
// the caller's honest fallback — never as a guess.
function noteAuthorDisplay(value, fallback = "Author not recorded") {
  const raw = String(value == null ? "" : value).trim();
  if (!raw) return fallback;
  const author = noteAuthorFromInitials(raw);
  return author ? noteAuthorNameOrInitials(author) : raw;
}

const NOTE_ENTRY_DATE_TOKEN = "(?:\\d{4}-\\d{1,2}-\\d{1,2}|\\d{1,2}[./-]\\d{1,2}[./-]\\d{2,4})";
const NOTE_ENTRY_TIME_TOKEN = "(?:\\d{1,2}:\\d{2}(?:\\s*[AaPp][Mm])?)";
const NOTE_ENTRY_INITIALS_TOKEN = "(?:[A-Z]{2,4})";

// Six header shapes, tried in order — a direct port of CT-TRACKER's HEADER_PATTERNS.
// Confirmed initials get the looser separators (we know them); unknown initials
// must carry an explicit "-" or ":" before they are treated as an author at all.
// Patterns 5 and 6 carry no date, so they set the initials for display but never
// attribute a person (see the divergence note above).
// (The "|" inside the [-|:] separator class is CT-TRACKER's; it makes a literal
// pipe a valid separator. Kept verbatim rather than silently corrected.)
const NOTE_ENTRY_HEADER_PATTERNS = (() => {
  const confirmed = NOTE_AUTHOR_DIRECTORY.map((author) => String(author.initials).toUpperCase());
  const plain = `(?:${confirmed.join("|")})`;
  const dotted = `(?:${confirmed.map((value) => value.split("").map((ch) => `${ch}\\.`).join("")).join("|")})`;
  const confirmedHeader = `(?:${plain}|${dotted})`;
  const sep = "(?:\\s*[-|:]\\s*|\\s+)";
  const d = NOTE_ENTRY_DATE_TOKEN;
  const t = NOTE_ENTRY_TIME_TOKEN;
  const i = NOTE_ENTRY_INITIALS_TOKEN;
  return [
    new RegExp(`^\\s*(?<date>${d})(?:\\s+(?<time>${t}))?${sep}(?<initials>${confirmedHeader})(?:\\s*[-:]\\s*|\\s+)`),
    new RegExp(`^\\s*(?<date>${d})(?:\\s+(?<time>${t}))?${sep}(?<initials>${i})\\s*[-:]\\s*`),
    new RegExp(`^\\s*(?<initials>(?:${dotted}|${i}))${sep}(?<date>${d})(?:\\s+(?<time>${t}))?(?:\\s*[-:]\\s*|\\s+)`),
    new RegExp(`^\\s*(?<date>${d})(?:\\s+(?<time>${t}))?(?:\\s*[-:]\\s*|\\s+)`),
    new RegExp(`^\\s*(?<initials>${confirmedHeader})\\s+`),
    new RegExp(`^\\s*(?<initials>[A-Za-z]{2,4})\\s*[-:]\\s*`)
  ];
})();

function matchNoteEntryHeader(text) {
  const value = String(text == null ? "" : text);
  for (let index = 0; index < NOTE_ENTRY_HEADER_PATTERNS.length; index += 1) {
    const match = NOTE_ENTRY_HEADER_PATTERNS[index].exec(value);
    if (match) return match;
  }
  return null;
}

function matchDatedNoteEntryHeader(text) {
  const match = matchNoteEntryHeader(text);
  return match && String((match.groups || {}).date || "").trim() ? match : null;
}

// Canonical entry text is the SHARED IDENTITY between this column-parsed source
// and the (currently unreadable) EZClaim note ledger: whitespace at the entry
// boundary is normalized, nothing inside is touched. The backend/table path keys
// on sha256 of exactly this string, so the two agree without either one having to
// hash in the browser.
function canonicalNoteEntryText(rawText) {
  const normalized = String(rawText == null ? "" : rawText).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalized.split("\n");
  while (lines.length && !lines[0].trim()) lines.shift();
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  if (lines.length) {
    lines[0] = lines[0].replace(/^[ \t]+/, "");
    lines[lines.length - 1] = lines[lines.length - 1].replace(/[ \t]+$/, "");
  }
  return lines.join("\n");
}

// Split a multi-entry note ONLY when we are confident: the first non-blank line
// must itself be a dated header, and there must be at least two dated headers.
// Anything less confident stays one entry with its text intact.
function splitNoteSourceEntries(rawText) {
  const source = String(rawText == null ? "" : rawText);
  const lines = [];
  let buffer = "";
  for (let index = 0; index < source.length; index += 1) {
    buffer += source[index];
    if (source[index] === "\n") { lines.push(buffer); buffer = ""; }
  }
  if (buffer) lines.push(buffer);
  if (lines.length < 2) return [source];
  const nonBlank = [];
  lines.forEach((line, index) => { if (line.trim()) nonBlank.push(index); });
  if (!nonBlank.length || !matchDatedNoteEntryHeader(lines[nonBlank[0]])) return [source];
  const headerIndexes = nonBlank.filter((index) => matchDatedNoteEntryHeader(lines[index]));
  if (headerIndexes.length < 2) return [source];
  return headerIndexes.map((headerStart, position) => {
    const start = position === 0 ? 0 : headerStart;
    const end = position + 1 < headerIndexes.length ? headerIndexes[position + 1] : lines.length;
    return lines.slice(start, end).join("");
  });
}

// Returns an ISO calendar date, or "" when the token cannot be read with
// certainty. A two-digit year above 39 is ambiguous (1998? 2098?) and is refused
// rather than guessed.
function parseNoteEntryDate(value) {
  const text = String(value == null ? "" : value).trim();
  if (!text) return "";
  const parts = text.split(/[./-]/);
  if (parts.length !== 3) return "";
  let year;
  let month;
  let day;
  if (/^\d{4}$/.test(parts[0])) {
    year = Number(parts[0]); month = Number(parts[1]); day = Number(parts[2]);
  } else {
    let yearText = parts[2];
    if (yearText.length === 2) {
      const shortYear = Number(yearText);
      if (!Number.isFinite(shortYear) || shortYear > 39) return "";
      yearText = String(2000 + shortYear);
    } else if (yearText.length !== 4) {
      return "";
    }
    month = Number(parts[0]); day = Number(parts[1]); year = Number(yearText);
  }
  if (![year, month, day].every((part) => Number.isInteger(part))) return "";
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return "";
  const pad = (number, width) => String(number).padStart(width, "0");
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

// Returns "HH:MM" (24h) or "" when the token cannot be read.
function parseNoteEntryTime(value) {
  const compact = String(value == null ? "" : value).replace(/\s+/g, "").toUpperCase();
  if (!compact) return "";
  const marker = compact.endsWith("AM") || compact.endsWith("PM") ? compact.slice(-2) : "";
  const body = marker ? compact.slice(0, -2) : compact;
  const parts = body.split(":");
  if (parts.length !== 2) return "";
  let hour = Number(parts[0]);
  const minute = Number(parts[1]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || minute < 0 || minute > 59) return "";
  if (marker) {
    if (hour < 1 || hour > 12) return "";
    hour = (hour % 12) + (marker === "PM" ? 12 : 0);
  } else if (hour < 0 || hour > 23) {
    return "";
  }
  const pad = (number) => String(number).padStart(2, "0");
  return `${pad(hour)}:${pad(minute)}`;
}

// Parse one legacy source value into zero or more timeline entries.
// Never throws, never invents. Every entry keeps its raw text.
function parseServiceLineNoteEntries(rawText) {
  const source = String(rawText == null ? "" : rawText);
  if (!source.trim()) return [];
  const occurrences = new Map();
  return splitNoteSourceEntries(source).map((entryText, index) => {
    const header = matchNoteEntryHeader(entryText);
    const groups = header ? (header.groups || {}) : {};
    const rawInitials = String(groups.initials || "").replace(/\./g, "").trim().toUpperCase();
    const dateText = String(groups.date || "").trim();
    const timeText = String(groups.time || "").trim();
    const warnings = [];
    let eventDate = "";
    let eventTime = "";
    let datePrecision = "unknown";
    if (dateText) {
      eventDate = parseNoteEntryDate(dateText);
      if (!eventDate) {
        warnings.push("invalid_date_or_time");
      } else {
        datePrecision = "date";
        if (timeText) {
          eventTime = parseNoteEntryTime(timeText);
          if (!eventTime) warnings.push("invalid_date_or_time");
          else datePrecision = "datetime";
        }
      }
    }
    // A person is named only from a DATED header. See the divergence note above:
    // a bare leading "SA:" is far more likely to be "Service Authorization" than
    // a signature, and we will not put a colleague's name on someone else's note.
    const headerIsDated = Boolean(dateText);
    const confirmed = noteAuthorFromInitials(rawInitials);
    const author = headerIsDated ? confirmed : null;
    if (rawInitials && !confirmed) warnings.push("unconfirmed_initials");
    if (rawInitials && confirmed && !headerIsDated) warnings.push("undated_header_not_attributed");
    if (!header) warnings.push("unstructured_source_text");
    const body = header ? entryText.slice(header[0].length) : entryText;
    const canonicalText = canonicalNoteEntryText(entryText);
    const occurrence = (occurrences.get(canonicalText) || 0) + 1;
    occurrences.set(canonicalText, occurrence);
    return {
      ordinal: index + 1,
      rawText: entryText,
      text: body.replace(/\s+/g, " ").trim(),
      initials: rawInitials,
      headerIsDated,
      author,
      eventDate,
      eventTime,
      datePrecision,
      warnings,
      canonicalText,
      occurrence
    };
  });
}

// The author line for one parsed entry. Initials we cannot turn into a confirmed,
// dated, named identity stay initials; nothing stands in for an author we do not
// know.
function serviceLineNoteAuthorLabel(entry) {
  // Dated + confirmed: the person (or their initials until the directory loads).
  if (entry && entry.author) return noteAuthorNameOrInitials(entry.author);
  // Confirmed initials on an UNDATED header: we recognize the token but refuse to
  // claim it is a signature, so it renders exactly as the source wrote it.
  if (entry && entry.initials && entry.warnings.includes("undated_header_not_attributed")) return entry.initials;
  if (entry && entry.initials) return `${entry.initials} — unidentified initials`;
  return "Author not recorded";
}

function serviceLineNoteEventRow(entry, serviceLineId) {
  const stamp = entry.eventDate
    ? `${formatDate(entry.eventDate)}${entry.eventTime ? ` ${entry.eventTime}` : ""}`
    : "";
  const metaParts = [
    stamp || "No date in source",
    entry.warnings.includes("invalid_date_or_time") ? "Source date could not be read — kept as written" : "",
    entry.warnings.includes("undated_header_not_attributed") ? "Initials only — no dated header, so no author is claimed" : ""
  ];
  return {
    sortAt: entry.eventDate ? `${entry.eventDate}T${entry.eventTime || "00:00"}` : "",
    source: "sourceNote",
    serviceLineId: String(serviceLineId || ""),
    title: serviceLineNoteAuthorLabel(entry),
    metaParts,
    meta: metaParts.filter(Boolean).join(" · "),
    diffs: [],
    text: entry.text || entry.rawText.trim(),
    canonicalText: entry.canonicalText,
    occurrence: entry.occurrence
  };
}

// Reconciliation seam for the day silver.ezclaim_claim_notes starts returning
// rows. Both sources describe the same note; the column-parsed entry wins because
// it is attached to a service line and carries parsed authorship. The one thing
// worth taking from the ledger row is its REAL capture timestamp, and only when
// the parse found no date — accepting a true timestamp is not inventing one.
function applyLedgerTimestampToSourceNoteRow(row, at) {
  if (!row || row.sortAt || !at) return false;
  row.sortAt = at;
  row.metaParts[0] = `${formatDateTime(at) || at} (EZClaim capture)`;
  row.meta = row.metaParts.filter(Boolean).join(" · ");
  return true;
}

function timestampSortValue(value) {
  const time = Date.parse(String(value || "").replace(" ", "T"));
  return Number.isFinite(time) ? time : 0;
}

// Shared renderer for one life-history row (per line or claim-level).
function claimTimelineRowHtml(row) {
  const src = claimTimelineSourceMeta(row.source);
  return `
          <div class="claim-timeline-row" data-source="${escapeHtml(row.source)}">
            <div class="claim-timeline-row-head">
              ${pill(src.label, src.tone)}
              <strong>${escapeHtml(row.title)}</strong>
            </div>
            <small>${escapeHtml(row.meta)}</small>
            ${Array.isArray(row.diffs) && row.diffs.length ? row.diffs.map((diff) => `<span class="claim-timeline-diff">${escapeHtml(diff)}</span>`).join("") : ""}
            ${row.text ? `<span>${escapeHtml(row.text)}</span>` : ""}
          </div>
        `;
}

// Normalize one server workflowHistory entry into a render row, carrying the
// serviceLineId so the caller can bucket it under the matching service line.
function workflowHistoryRowFromEntry(entry = {}) {
  const isEzclaim = entry.source === "ezclaim_note_ledger";
  const source = isEzclaim ? "ezclaim"
    : entry.kind === "command" ? "command"
    : (entry.previousStatus && entry.status && entry.previousStatus !== entry.status) ? "status"
    : entry.kind === "note" ? "note"
    : "event";
  const secondary = [
    entry.role,
    entry.status,
    entry.owner ? `owner ${entry.owner}` : "",
    entry.followUpDate ? `due ${formatDate(entry.followUpDate)}` : "",
    entry.slaBucket ? `SLA ${entry.slaBucket}` : ""
  ].filter(Boolean).join(" · ");
  const diffs = [];
  if ((entry.previousFollowUpDate || entry.followUpDate) && String(entry.previousFollowUpDate || "") !== String(entry.followUpDate || "")) {
    diffs.push(`Follow-up date: ${entry.previousFollowUpDate ? formatDate(entry.previousFollowUpDate) : "—"} → ${entry.followUpDate ? formatDate(entry.followUpDate) : "—"}`);
  }
  if ((entry.previousStatus || entry.status) && String(entry.previousStatus || "") !== String(entry.status || "")) {
    diffs.push(`Status: ${entry.previousStatus || "—"} → ${entry.status || "—"}`);
  }
  if ((entry.previousFollowUpAction || entry.followUpAction) && String(entry.previousFollowUpAction || "") !== String(entry.followUpAction || "")) {
    diffs.push(`Follow-up action: ${entry.previousFollowUpAction || "—"} → ${entry.followUpAction || "—"}`);
  }
  return {
    sortAt: entry.at || "",
    source,
    serviceLineId: String(entry.serviceLineId || ""),
    title: isEzclaim ? "EZClaim note" : (entry.action || entry.kind || "Workflow event"),
    meta: [entry.at || "No timestamp", noteAuthorDisplay(entry.by, isEzclaim ? "Author not recorded" : "system"), secondary].filter(Boolean).join(" · "),
    diffs,
    text: entry.notePreview || ""
  };
}

// Legacy compatibility renderer retained for old in-memory demo records. The
// active drawer reads the portal-owned claim-note and audit endpoints instead;
// it never hydrates note content from a service row.
function claimLineHistory(claim = {}, clientMode = false, { notesDenied = false } = {}) {
  const services = Array.isArray(claim.services) ? claim.services : [];
  const lineIndex = new Map();
  const lines = services.map((service) => {
    const serviceId = String(service.serviceId || "");
    const line = {
      serviceId,
      dos: service.dos || "",
      procedure: service.procedure || "",
      status: service.status || "",
      note: "",
      events: []
    };
    if (serviceId) lineIndex.set(serviceId, line);
    return line;
  });
  const claimEvents = [];
  const ezclaimNotes = [];
  // Canonical text -> the column-parsed row that already renders it. Both
  // sources describe the same EZClaim note, so when the ledger starts
  // returning rows this stops it producing a duplicate.
  const sourceNoteByCanonicalText = new Map();
  lines.forEach((line) => line.events.forEach((event) => {
    if (event.source !== "sourceNote" || !event.canonicalText) return;
    if (!sourceNoteByCanonicalText.has(event.canonicalText)) sourceNoteByCanonicalText.set(event.canonicalText, event);
  }));
  const history = claim.workflowHistory || {};
  if (!clientMode && !history.redacted) {
    (Array.isArray(history.entries) ? history.entries : []).forEach((entry) => {
      const row = workflowHistoryRowFromEntry(entry);
      if (entry.source === "ezclaim_note_ledger") {
        // Same note, two sources. The column-parsed entry wins: it is attached
        // to a service line and carries parsed authorship. All we take from the
        // ledger row is its real capture timestamp, and only where the parse
        // found no date — accepting a true timestamp is not inventing one.
        const twin = sourceNoteByCanonicalText.get(canonicalNoteEntryText(entry.notePreview || ""));
        if (twin) { applyLedgerTimestampToSourceNoteRow(twin, entry.at || ""); return; }
        ezclaimNotes.push(row);
        return;
      }
      const line = row.serviceLineId ? lineIndex.get(row.serviceLineId) : null;
      if (line) line.events.push(row); else claimEvents.push(row);
    });
  }
  // Claim-level internal notes + stateside bounce reason render whenever the
  // viewer is internal — independent of workflow-history redaction (they are the
  // claim's own notes, not server-audit events; the server withholds them in
  // client mode via the redacted/empty payload).
  if (!clientMode) {
    (Array.isArray(claim.notes) ? claim.notes : []).forEach((note) => {
      const text = String(note.text || "").trim();
      if (!text) return;
      claimEvents.push({
        sortAt: note.at || "",
        source: "note",
        serviceLineId: "",
        title: "Note",
        meta: [note.at || "No timestamp", note.by || "Unknown"].filter(Boolean).join(" · "),
        diffs: [],
        text
      });
    });
    const bounceReasonText = claimBounceReason(claim);
    if (bounceReasonText && /Bounced/i.test(String(claim.assignmentStatus || ""))) {
      claimEvents.push({
        sortAt: claim.bounceAt || claim.reviewedAt || "",
        source: "status",
        serviceLineId: "",
        title: "Bounced back — stateside review note",
        meta: [claim.bounceAt || claim.reviewedAt || "No timestamp", claim.bounceBy || claim.reviewedBy || "Stateside review"].filter(Boolean).join(" · "),
        diffs: [],
        text: bounceReasonText
      });
    }
  }
  const byNewest = (a, b) => timestampSortValue(b.sortAt) - timestampSortValue(a.sortAt);
  lines.forEach((line) => line.events.sort(byNewest));
  claimEvents.sort(byNewest);
  ezclaimNotes.sort(byNewest);
  const totalEvents = ezclaimNotes.length + claimEvents.length + lines.reduce((sum, line) => sum + line.events.length, 0);
  return { lines, claimEvents, ezclaimNotes, totalEvents };
}

function claimTimelineSourceMeta(source) {
  switch (source) {
    case "status": return { label: "Status change", tone: "amber" };
    case "command": return { label: "Portal action", tone: "blue" };
    case "service": return { label: "Service line", tone: "muted" };
    case "note": return { label: "Note", tone: "muted" };
    case "sourceNote": return { label: "Service line note", tone: "teal" };
    case "ezclaim": return { label: "EZClaim note", tone: "teal" };
    default: return { label: "Portal activity", tone: "blue" };
  }
}

// UI-100 #63: drawer focus management — trap focus while open, restore on close.
let __drawerPrevFocus = null;
function openDrawerShell() {
  // Only capture the prior focus on a TRUE fresh open. Re-renders that call
  // openDrawerShell again while the drawer is already open (e.g. addNote →
  // openDrawer at line ~20523) would otherwise overwrite __drawerPrevFocus
  // with a button INSIDE the drawer — and then closeDrawer would have no
  // outside opener to restore to. Also guard against capturing an activeElement
  // that's already inside the drawer (shouldn't happen on fresh open, but
  // defends against odd re-entry).
  if (typeof document !== "undefined") {
    const current = document.activeElement;
    const wasOpen = !!els.claimDrawer?.classList?.contains?.("open");
    const insideDrawer = !!(els.claimDrawer && typeof els.claimDrawer.contains === "function" && current && els.claimDrawer.contains(current));
    if (!wasOpen && !insideDrawer) {
      __drawerPrevFocus = (current && current !== document.body) ? current : null;
    }
  }
  if (els.drawerOverlay) els.drawerOverlay.hidden = false;
  if (!els.claimDrawer) return;
  els.claimDrawer.classList?.add("open");
  els.claimDrawer.setAttribute?.("aria-hidden", "false");
  if (typeof els.claimDrawer.getAttribute === "function") {
    els.claimDrawer.setAttribute("role", els.claimDrawer.getAttribute("role") || "dialog");
    els.claimDrawer.setAttribute("aria-modal", "true");
    if (!els.claimDrawer.hasAttribute?.("tabindex")) els.claimDrawer.setAttribute("tabindex", "-1");
  }
  // Focus the first focusable element inside the drawer (or the drawer itself).
  if (typeof requestAnimationFrame === "function" && typeof els.claimDrawer.querySelectorAll === "function") {
    requestAnimationFrame(() => {
      const focusables = els.claimDrawer.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])");
      const target = Array.from(focusables).find((n) => !n.disabled && n.offsetParent !== null);
      try { (target || els.claimDrawer).focus(); } catch (_) {}
    });
  }
  if (typeof document !== "undefined" && document.addEventListener) {
    document.addEventListener("keydown", drawerKeyTrap);
  }
}

function drawerKeyTrap(e) {
  if (!els.claimDrawer || !els.claimDrawer.classList.contains("open")) return;
  if (e.key === "Escape") {
    e.preventDefault();
    closeDrawer();
    return;
  }
  if (e.key !== "Tab") return;
  const focusables = Array.from(
    els.claimDrawer.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])")
  ).filter((n) => !n.disabled && n.offsetParent !== null);
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}


function closeDrawer() {
  try { endClaimWorkTelemetry("close"); claimWorkTelemetry.claimId = ""; } catch (_) {}
  serverClaimDrawerRequestKey = "";
  serverClaimDrawerController?.abort(); serverClaimDrawerController = null;
  document.querySelectorAll("[data-di-drawer-key] [data-di-analysis]").forEach(function (node) { denialIntelligenceHideAnalysis(node.dataset.diAnalysis); });
  activeClaimDrawerContext = null;
  const restoreTarget = __drawerPrevFocus;
  els.claimDrawer?.classList?.remove("open");
  els.claimDrawer?.setAttribute?.("aria-hidden", "true");
  if (els.drawerOverlay) els.drawerOverlay.hidden = true;
  if (typeof document !== "undefined" && document.removeEventListener) {
    document.removeEventListener("keydown", drawerKeyTrap); // UI-100 #63
  }
  __drawerPrevFocus = null;
  restoreFocusAfterDrawerClose(restoreTarget);
}

function restoreFocusAfterDrawerClose(restoreTarget) {
  const run = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (callback) => callback();
  run(() => {
    // The × close-button (or any Escape-from-inside path) is still
    // document.activeElement at this point, sitting inside a drawer that's been
    // visually hidden via transform/aria-hidden but is still in the DOM. Without
    // an explicit blur first, refocusing the opener can be silently bounced
    // (focus stays on closeDrawerBtn) — the symptom users reported. Force-blur
    // anything still focused inside the drawer before redirecting focus.
    try {
      const stuck = document.activeElement;
      const stuckInsideDrawer = !!(stuck && els.claimDrawer && typeof els.claimDrawer.contains === "function" && els.claimDrawer.contains(stuck));
      if (stuckInsideDrawer && typeof stuck.blur === "function") stuck.blur();
    } catch (_) {}
    const activeInsideClosedDrawer = Boolean(
      els.claimDrawer
      && typeof els.claimDrawer.contains === "function"
      && els.claimDrawer.contains(document.activeElement)
    );
    const restoreTargetInsideDrawer = Boolean(
      restoreTarget
      && els.claimDrawer
      && typeof els.claimDrawer.contains === "function"
      && els.claimDrawer.contains(restoreTarget)
    );
    if (restoreTarget && !restoreTargetInsideDrawer && typeof restoreTarget.focus === "function" && restoreTarget !== document.body) {
      try {
        restoreTarget.focus();
        // Verify focus stuck. If the browser snapped it back into the (now
        // hidden) drawer — async focusout/blur bounce from the close button --
        // retry once on the next frame.
        if (document.activeElement !== restoreTarget) {
          run(() => { try { restoreTarget.focus(); } catch (_) {} });
        }
        return;
      } catch (_) {}
    }
    if (activeInsideClosedDrawer) {
      const focusedRow = document.querySelector(".claims-table tbody tr.sheet-nav-focus, .claims-table tbody tr.focused-work-row");
      try { focusedRow?.focus?.(); if (document.activeElement === focusedRow) return; } catch (_) {}
      try { els.openFocusedClaimBtn?.focus?.(); return; } catch (_) {}
      if (document.body && !document.body.hasAttribute?.("tabindex")) document.body.setAttribute?.("tabindex", "-1");
      try { document.body?.focus?.(); } catch (_) {}
    }
  });
}

function resetDemo() {
  // Destructive: wipes all local workflow state. Confirm before proceeding (#87/#90).
  const ok = typeof window !== "undefined" && typeof window.confirm === "function"
    ? window.confirm("Reset all local workflow state (assignments, follow-ups, notes, saved views)? This cannot be undone.")
    : true;
  if (!ok) return;
  state = defaultState();
  saveState();
  render();
  showToast("Local workflow state reset.");
}

function exportState() {
  if (!hasPermission(PERMISSIONS.EXPORT_WORKFLOW)) return denyAction(PERMISSIONS.EXPORT_WORKFLOW, "export workflow");
  const exportText = JSON.stringify({
    exportedAt: new Date().toISOString(),
    source: `Local portal workflow state. Source claims are loaded from ${sourceDataInfo.url}.`,
    slicers: state.slicers,
    savedOperationsViews: state.savedOperationsViews,
    feeScheduleRows: state.feeScheduleRows,
    followUpRules: state.followUpRules,
    workflowByClaim: state.workflowByClaim,
    audit: state.audit
  }, null, 2);

  const blob = new Blob([exportText], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `tms-workflow-state-${localDateString()}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    navigator.clipboard.writeText(exportText).then(
      () => showToast("Workflow downloaded and copied."),
      () => showToast("Workflow downloaded.")
    );
  } else {
    showToast("Workflow downloaded.");
  }
}

function exportClientChartPack() {
  const selectedClient = els.clientSelect?.value || "All";
  const selectedState = els.clientStateFilter?.value || "All";
  const selectedInsurance = els.clientInsuranceFilter?.value || "All";
  const claims = getClaims().filter((claim) => {
    const clientMatch = selectedClient === "All" || claim.client === selectedClient;
    const stateMatch = selectedState === "All" || clientStateValue(claim) === selectedState;
    const insuranceMatch = selectedInsurance === "All"
      || claim.primaryPayer === selectedInsurance
      || claim.secondaryPayer === selectedInsurance;
    return clientMatch && stateMatch && insuranceMatch;
  });
  const serviceRows = claims.flatMap((claim) => claim.services.map((service) => ({ claim, service })));
  const claimFinancials = claims.map(claimFinancialView);
  const totals = {
    selectedClient,
    selectedState,
    selectedInsurance,
    totalExpected: claimFinancials.reduce((sum, claim) => sum + claim.expectedReimbursement, 0),
    totalPaid: claimFinancials.reduce((sum, claim) => sum + claim.totalInsurancePaid, 0),
    totalOwed: claimFinancials.reduce((sum, claim) => sum + claim.sourceOutstanding, 0),
    serviceExpected: serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).expectedReimbursement, 0),
    servicePaid: serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).insurancePaid, 0),
    serviceOwed: serviceRows.reduce((sum, row) => sum + serviceFinancialView(row.service, row.claim).sourceOutstanding, 0)
  };
  const payload = clientChartPackPayload(claims, serviceRows, totals);
  downloadJsonPayload(payload, `tms-client-chart-pack-${localDateString()}.json`);
  showToast("Client chart pack downloaded.");
}

function downloadJsonPayload(payload, filename) {
  const exportText = JSON.stringify(payload, null, 2);
  downloadTextPayload(exportText, filename, "application/json");
}

function downloadTextPayload(exportText, filename, contentType = "text/plain") {
  const blob = new Blob([exportText], { type: contentType });
  downloadBlobPayload(blob, filename);
}

function downloadBlobPayload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function importWorkflowState(event) {
  if (!hasPermission(PERMISSIONS.IMPORT_WORKFLOW)) {
    denyAction(PERMISSIONS.IMPORT_WORKFLOW, "import workflow");
    event.target.value = "";
    return;
  }
  const file = event.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = () => {
    try {
      const imported = JSON.parse(String(reader.result || "{}"));
      if (!imported.workflowByClaim || typeof imported.workflowByClaim !== "object") {
        throw new Error("Missing workflowByClaim");
      }
      state.workflowByClaim = workflowImportForCurrentScope(imported.workflowByClaim);
      bumpClaimsDataVersion();
      state.slicers = ensureSlicerState(imported.slicers);
      state.savedOperationsViews = normalizeSavedOperationsViews(imported.savedOperationsViews);
      if (hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES) && Array.isArray(imported.feeScheduleRows)) {
        state.feeScheduleRows = normalizeFeeScheduleRows(imported.feeScheduleRows);
        bumpFeeScheduleMatchEpoch();
        persistFeeScheduleRows(state.feeScheduleRows, { showToast: false });
      }
      const hostedFollowUpRulesIgnored = followUpRulesHostedMode() && Array.isArray(imported.followUpRules);
      if (!hostedFollowUpRulesIgnored && hasPermission(PERMISSIONS.MANAGE_FOLLOW_UP_RULES) && Array.isArray(imported.followUpRules)) {
        state.followUpRules = normalizeFollowUpRules(imported.followUpRules);
      }
      audit("Workflow state imported from local JSON file", activeInternalUser());
      saveState();
      render();
      showToast(hostedFollowUpRulesIgnored
        ? "Workflow imported. Hosted follow-up rules were ignored because the server rulebook is read-only."
        : "Workflow imported.");
    } catch {
      showToast("Import failed. Use an exported workflow JSON file.");
    } finally {
      event.target.value = "";
    }
  };
  reader.readAsText(file);
}

function workflowImportForCurrentScope(importedWorkflowByClaim) {
  const migrated = migrateWorkflowPatchByClaim(importedWorkflowByClaim || {});
  const sourceClaimIds = new Set(sourceClaims.map((claim) => claim.claimId));
  const profile = currentAccessProfile();
  if (profile.scope?.allOrgs) {
    const nextWorkflow = { ...defaultWorkflowByClaim() };
    Object.entries(migrated).forEach(([claimId, workflowPatch]) => {
      if (!sourceClaimIds.has(claimId)) return;
      nextWorkflow[claimId] = {
        ...(nextWorkflow[claimId] || {}),
        ...workflowPatch
      };
    });
    return nextWorkflow;
  }
  const nextWorkflow = {
    ...defaultWorkflowByClaim(),
    ...(state.workflowByClaim || {})
  };
  Object.entries(migrated).forEach(([claimId, workflowPatch]) => {
    if (!sourceClaimIds.has(claimId)) return;
    const sourceClaim = sourceClaims.find((claim) => claim.claimId === claimId);
    if (!sourceClaim) return;
    const currentWorkflow = state.workflowByClaim?.[claimId] || {};
    if (!canAccessClaim({ ...sourceClaim, ...currentWorkflow })) return;
    nextWorkflow[claimId] = {
      ...(nextWorkflow[claimId] || {}),
      ...workflowPatch
    };
  });
  return nextWorkflow;
}

function addFeeScheduleRow() {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "add fee schedule row");
  const filters = state.feeScheduleFilters || {};
  const selectedOrgId = filters.orgId && filters.orgId !== "All" ? String(filters.orgId || "").trim() : "";
  const selectedTenantId = filters.tenantId && filters.tenantId !== "All" ? String(filters.tenantId || "").trim() : "";
  const selectedTenant = selectedTenantId ? feeRowsForActiveUser(state.feeScheduleRows || []).find((row) => feeScheduleRowMatchesTenant(row, selectedTenantId)) : null;
  const selectedOrgIds = orgIdsForTenantName(selectedTenantId);
  const orgId = selectedOrgId || selectedTenant?.orgId || selectedOrgIds[0] || "";
  const tenantId = selectedTenantId || tenantNameForOrgId(orgId) || "";
  state.feeScheduleRows = [...state.feeScheduleRows, { tenantId, orgId, orgName: selectedTenant?.orgName || tenantNameForOrgId(orgId) || "", payorId: "", payer: "", state: "", service: "", procedure: "", m1: "", m2: "", place: "", rate: "", startDate: "", endDate: "", network: "", duplicateCheck: "", secondaryCalc: "" }];
  bumpFeeScheduleMatchEpoch();
  resetDerivedCaches();
  audit("Fee schedule row added", activeInternalUser());
  saveState();
  renderFeeSchedules();
  showToast(feeScheduleApiEnabled()
    ? "New rate row added. Fill payer, CPT, and rate, then save."
    : "New rate row added. Saving is temporarily unavailable.");
}

// ---- Guided "Add Rate" drawer ----------------------------------------------
// Front-loads picker-driven input + validation so a new rate matches the
// resolver (payer / CPT / modifier / POS) cleanly, instead of appending a
// blank free-text row to the grid. Builds the same row shape as
// addFeeScheduleRow() and reuses persistFeeScheduleRow() for the DB write.
const FEE_RATE_NETWORK_OPTIONS = ["", "In Network", "OON"];
let feeRatePrevFocus = null;

function feeScheduleDistinctRowValues(field) {
  const set = new Set();
  (state.feeScheduleRows || []).forEach((row) => {
    const value = String(row?.[field] == null ? "" : row[field]).trim();
    if (value && value.toLowerCase() !== "undefined") set.add(value);
  });
  return Array.from(set).sort((a, b) => compareText(a, b));
}

function feeScheduleKnownPayers() {
  const set = new Set();
  const add = (value) => { const text = String(value == null ? "" : value).trim(); if (text) set.add(text); };
  (state.feeScheduleRows || []).forEach((row) => add(row.payer));
  (getClaims() || []).forEach((claim) => { add(claim.primaryPayer); add(claim.secondaryPayer); });
  return Array.from(set).sort((a, b) => compareText(a, b));
}

function feeScheduleKnownProcedures() {
  const set = new Set();
  const add = (value) => { const text = String(value == null ? "" : value).trim(); if (text) set.add(text); };
  (state.feeScheduleRows || []).forEach((row) => add(row.procedure));
  (getClaims() || []).forEach((claim) => (claim.services || []).forEach((service) => add(service.procedure)));
  return Array.from(set).sort((a, b) => compareText(a, b));
}

function feeRateOrgIdsForTenant(tenantId = "") {
  const ids = new Set();
  orgIdsForTenantName(tenantId).forEach((id) => { const text = String(id || "").trim(); if (text) ids.add(text); });
  feeRowsForActiveUser(state.feeScheduleRows || [])
    .filter((row) => !tenantId || feeScheduleRowMatchesTenant(row, tenantId))
    .forEach((row) => {
      const text = String(row.orgId || "").trim();
      if (text && text.toLowerCase() !== "undefined") ids.add(text);
    });
  return Array.from(ids).sort((a, b) => compareText(a, b));
}

function feeRateOrgOptionsForTenant(tenantId = "") {
  const orgIds = feeRateOrgIdsForTenant(tenantId);
  const knownLabels = userMgmtOrgLabelMap();
  const labelsWithFallbacks = new Map(orgIds.map((orgId) => [orgId, knownLabels.get(orgId) || orgId]));
  const displayLabels = userMgmtDisplayOrgLabelMap(labelsWithFallbacks);
  const baseRows = orgIds.map((orgId) => ({
    value: orgId,
    label: String(displayLabels.get(orgId) || "Company name unavailable").trim() || "Company name unavailable"
  }));
  const labelCounts = baseRows.reduce((counts, row) => {
    const key = row.label.toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
    return counts;
  }, new Map());
  const labelOrdinals = new Map();
  return baseRows.map((row) => {
    const key = row.label.toLowerCase();
    if ((labelCounts.get(key) || 0) <= 1) return row;
    const ordinal = (labelOrdinals.get(key) || 0) + 1;
    labelOrdinals.set(key, ordinal);
    return { ...row, label: `${row.label} · Location ${ordinal}` };
  });
}

function openAddFeeRateModal(prefill = {}) {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "add fee schedule row");
  if (!els.feeRateModal || !els.feeRateForm) return addFeeScheduleRow();
  const filters = feeScheduleEffectiveFilters();
  const tenantId = filters.tenantId && filters.tenantId !== "All" ? String(filters.tenantId).trim() : "";
  const orgId = filters.orgId && filters.orgId !== "All" ? String(filters.orgId).trim() : "";
  const values = { tenantId, orgId, ...(prefill || {}) };
  if (activeClientGroupTenant()) {
    values.tenantId = activeClientGroupTenant();
    if (!feeScheduleRowInSelectedCompany(values)) values.orgId = activeClientGroupServerOrgId();
  } else if (values.orgId) values.tenantId = tenantNameForOrgId(values.orgId) || values.tenantId;
  renderFeeRateForm(values);
  feeRatePrevFocus = document.activeElement;
  if (els.feeRateModal.parentElement !== document.body) document.body.appendChild(els.feeRateModal);
  els.feeRateModal.classList.remove("hidden");
  els.feeRateModal.setAttribute("aria-hidden", "false");
  activateForegroundModal(els.feeRateModal, {
    initialFocus: values.payer && values.procedure && values.orgId ? "#feeRateAmount" : values.tenantId && values.orgId ? "#feeRatePayer" : "#feeRateTenant",
    onEscape: closeAddFeeRateModal,
    returnFocus: feeRatePrevFocus
  });
  if (values.payer && values.procedure) {
    els.feeRateForm.querySelector("#feeRateAmount")?.scrollIntoView?.({ block: "center", behavior: "instant" });
  }
}

function closeAddFeeRateModal() {
  if (!els.feeRateModal) return;
  els.feeRateModal.classList.add("hidden");
  els.feeRateModal.setAttribute("aria-hidden", "true");
  const restoredByModalSession = deactivateForegroundModal(els.feeRateModal);
  if (!restoredByModalSession) {
    try { feeRatePrevFocus?.focus?.({ preventScroll: true }); } catch (_) {}
  }
  feeRatePrevFocus = null;
}

function readFeeRateForm() {
  const form = els.feeRateForm;
  if (!form) return {};
  const get = (id) => String(form.querySelector(`#${id}`)?.value ?? "").trim();
  return {
    tenantId: get("feeRateTenant"),
    orgId: get("feeRateOrgId"),
    payer: get("feeRatePayer"),
    externalPayerId: get("feeRateExternalPayerId"),
    state: get("feeRateState"),
    service: get("feeRateService"),
    procedure: get("feeRateProcedure"),
    m1: get("feeRateM1"),
    m2: get("feeRateM2"),
    place: get("feeRatePlace"),
    network: get("feeRateNetwork"),
    networkKnown: get("feeRateNetwork") !== "__unselected__",
    rate: get("feeRateAmount"),
    startDate: get("feeRateStart"),
    endDate: get("feeRateEnd"),
    secondaryCalc: get("feeRateSecondaryCalc"),
    approvalStatus: get("feeRateApproval")
  };
}

function renderFeeRateForm(values = {}) {
  const form = els.feeRateForm;
  if (!form) return;
  const v = {
    tenantId: "", orgId: "", orgName: "", payer: "", state: "", service: "", procedure: "", m1: "", m2: "", place: "",
    externalPayerId: "", network: "", networkKnown: true, rate: "", startDate: "", endDate: "", secondaryCalc: "", approvalStatus: "draft", ...values
  };
  // Preserve a supplied source token instead of silently broadening it to Any.
  const networkOptions = v.network && !FEE_RATE_NETWORK_OPTIONS.includes(v.network) ? [...FEE_RATE_NETWORK_OPTIONS, v.network] : FEE_RATE_NETWORK_OPTIONS;
  const tenantOptions = feeScheduleTenantOptions(feeRowsForActiveUser(state.feeScheduleRows || []));
  const tenantOptionValues = tenantOptions.slice();
  if (v.tenantId && !tenantOptionValues.some((option) => String(option.value ?? option) === String(v.tenantId))) {
    tenantOptionValues.unshift({ value: v.tenantId, label: v.tenantId });
  }
  const orgOptions = feeRateOrgOptionsForTenant(v.tenantId);
  const orgOptionValues = orgOptions.slice();
  if (v.orgId && !orgOptionValues.some((option) => option.value === v.orgId)) {
    const selectedOrgLabel = [v.orgName, tenantNameForOrgId(v.orgId), v.tenantId]
      .map((value) => String(value || "").trim())
      .find((value) => value && value !== String(v.orgId).trim());
    orgOptionValues.unshift({ value: v.orgId, label: selectedOrgLabel || "Selected company or location" });
  }
  const selectOptions = (items, selected, placeholder) =>
    [`<option value="">${escapeHtml(placeholder)}</option>`]
      .concat(items.map((item) => {
        const value = String(item.value ?? item);
        const label = String(item.label ?? item);
        return `<option value="${escapeHtml(value)}"${value === String(selected) ? " selected" : ""}>${escapeHtml(label)}</option>`;
      })).join("");
  const dataList = (id, items) => `<datalist id="${id}">${items.map((item) => `<option value="${escapeHtml(item)}"></option>`).join("")}</datalist>`;
  form.innerHTML = `
    <div id="feeRateFormStatus" class="fee-rate-form-status" role="status" aria-live="polite" tabindex="-1"></div>
    ${v.payer && v.procedure ? `<p class="fee-rate-context"><strong>${escapeHtml(v.payer)} · ${escapeHtml(v.procedure)}</strong><span>${escapeHtml(v.orgName || tenantNameForOrgId(v.orgId) || v.tenantId)}${v.startDate ? " · Review coverage from " + escapeHtml(v.startDate) : ""}</span></p>` : ""}
    <p class="fee-rate-required-note"><span aria-hidden="true">*</span> Required to add a rate</p>
    <div class="fee-rate-form-grid">
      <h3 class="fee-rate-form-group">Company & payer</h3>
      <label><span>Client company <span class="fee-rate-required" aria-hidden="true">*</span></span>
        <select id="feeRateTenant" required ${activeClientGroupTenant() ? "disabled" : ""}>${selectOptions(tenantOptionValues, v.tenantId, "Select client…")}</select>
      </label>
      <label><span>Billing company or location <span class="fee-rate-required" aria-hidden="true">*</span></span>
        <select id="feeRateOrgId" required ${activeClientGroupTenant() && activeClientGroupOrgIds().length === 1 ? "disabled" : ""}>${selectOptions(orgOptionValues, v.orgId, orgOptions.length ? "Select company or location…" : "Pick a client first")}</select>
      </label>
      <label><span>Payer name <span class="fee-rate-required" aria-hidden="true">*</span></span>
        <input id="feeRatePayer" list="feeRatePayerList" value="${escapeHtml(v.payer)}" placeholder="e.g. Amerigroup" required>
      </label>
      <input id="feeRateExternalPayerId" type="hidden" value="${escapeHtml(v.externalPayerId || "")}">
      <h3 class="fee-rate-form-group">Service details</h3>
      <label>State
        <input id="feeRateState" list="feeRateStateList" value="${escapeHtml(v.state)}" placeholder="e.g. NJ">
      </label>
      <label>Service type (optional)
        <input id="feeRateService" list="feeRateServiceList" value="${escapeHtml(v.service)}" placeholder="optional">
      </label>
      <label><span>CPT code <span class="fee-rate-required" aria-hidden="true">*</span></span>
        <input id="feeRateProcedure" list="feeRateCptList" value="${escapeHtml(v.procedure)}" placeholder="e.g. 97153" required>
      </label>
      <label>Modifier 1
        <input id="feeRateM1" list="feeRateM1List" value="${escapeHtml(v.m1)}" placeholder="e.g. HN">
      </label>
      <label>Modifier 2
        <input id="feeRateM2" list="feeRateM2List" value="${escapeHtml(v.m2)}" placeholder="optional">
      </label>
      <label>Place of service (POS)
        <input id="feeRatePlace" list="feeRatePlaceList" value="${escapeHtml(v.place)}" placeholder="e.g. 11">
      </label>
      <label>Network coverage
        <select id="feeRateNetwork">${v.networkKnown === false ? '<option value="__unselected__" selected disabled>Choose network scope...</option>' : ""}${networkOptions.map((opt) => `<option value="${escapeHtml(opt)}"${v.networkKnown !== false && opt === v.network ? " selected" : ""}>${escapeHtml(opt || "Any (network-agnostic)")}</option>`).join("")}</select>
        ${v.networkKnown === false ? '<small>Network information is unavailable. Choose In Network, OON, or consciously choose Any before saving.</small>' : ""}
      </label>
      <h3 class="fee-rate-form-group">Rate & effective dates</h3>
      <label><span>Contracted rate (USD) <span class="fee-rate-required" aria-hidden="true">*</span></span>
        <input id="feeRateAmount" type="number" min="0" step="0.01" inputmode="decimal" value="${escapeHtml(v.rate)}" placeholder="Example: 85.00" required>
        <small>Use $0 only when the contract says this code is non-payable.</small>
      </label>
      <label>Secondary payment rule
        <select id="feeRateSecondaryCalc">
          <option value=""${v.secondaryCalc ? "" : " selected"}>Default</option>
          <option value="Allowed Amount"${v.secondaryCalc === "Allowed Amount" ? " selected" : ""}>Allowed Amount</option>
          <option value="PR Amount"${v.secondaryCalc === "PR Amount" ? " selected" : ""}>PR Amount</option>
        </select>
      </label>
      <label><span>Effective start date <span class="fee-rate-required" aria-hidden="true">*</span></span>
        <input id="feeRateStart" type="date" value="${escapeHtml(v.startDate)}" required>
      </label>
      <label>Effective end date
        <input id="feeRateEnd" type="date" value="${escapeHtml(v.endDate)}">
      </label>
      <label>Rate status
        <select id="feeRateApproval">
          <option value="draft"${v.approvalStatus === "approved" ? "" : " selected"}>Draft (not eligible for calculations)</option>
          <option value="approved"${v.approvalStatus === "approved" ? " selected" : ""}>Approved (eligible for calculations)</option>
        </select>
        <small>A rate is used only when it is active, approved, effective for the service date, and matches the claim details.</small>
      </label>
    </div>
    <div id="feeRateDuplicateWarning" class="fee-rate-duplicate" hidden></div>
    ${dataList("feeRatePayerList", feeScheduleKnownPayers())}
    ${dataList("feeRateStateList", feeScheduleDistinctRowValues("state"))}
    ${dataList("feeRateServiceList", feeScheduleDistinctRowValues("service"))}
    ${dataList("feeRateCptList", feeScheduleKnownProcedures())}
    ${dataList("feeRateM1List", feeScheduleDistinctRowValues("m1"))}
    ${dataList("feeRateM2List", feeScheduleDistinctRowValues("m2"))}
    ${dataList("feeRatePlaceList", feeScheduleDistinctRowValues("place"))}
    <div class="fee-rate-form-actions">
      <button class="ghost-button" type="button" data-fee-rate-close="1">Cancel</button>
      <button id="feeRateSubmitBtn" class="primary-button" type="submit">Add rate</button>
    </div>
  `;
  form.querySelectorAll("[data-fee-rate-close]").forEach((node) => node.addEventListener("click", closeAddFeeRateModal));
  evaluateFeeRateDuplicate();
}

function onAddFeeRateFormChange(event) {
  if (event.target?.id === "feeRateTenant") {
    const values = readFeeRateForm();
    values.orgId = "";
    renderFeeRateForm(values);
    window.requestAnimationFrame(() => { try { els.feeRateForm.querySelector("#feeRateOrgId")?.focus(); } catch (_) {} });
    return;
  }
  evaluateFeeRateDuplicate();
}

function onAddFeeRateFormInput(event) {
  if (event?.target?.id === "feeRatePayer") {
    const externalId = els.feeRateForm?.querySelector("#feeRateExternalPayerId");
    if (externalId) externalId.value = "";
  }
  evaluateFeeRateDuplicate();
}

function findDuplicateFeeRate(candidate = {}) {
  const norm = (value) => normalizedContractText(value);
  const orgId = String(candidate.orgId || "").trim();
  const payer = norm(candidate.payer);
  const procedure = norm(candidate.procedure);
  if (!orgId || !payer || !procedure) return null;
  const m1 = norm(candidate.m1);
  const m2 = norm(candidate.m2);
  const place = canonicalPlaceCode(candidate.place);
  const stateKey = norm(candidate.state);
  const serviceKey = norm(candidate.service);
  const networkKey = norm(candidate.network);
  return feeRowsForActiveUser(state.feeScheduleRows || []).find((row) =>
    String(row.orgId || "").trim() === orgId
    && norm(row.payer) === payer
    && norm(row.state) === stateKey
    && norm(row.service) === serviceKey
    && norm(row.procedure) === procedure
    && norm(row.m1) === m1
    && norm(row.m2) === m2
    && canonicalPlaceCode(row.place) === place
    && norm(row.network) === networkKey) || null;
}

function evaluateFeeRateDuplicate() {
  const warn = els.feeRateForm?.querySelector("#feeRateDuplicateWarning");
  if (!warn) return;
  const dup = findDuplicateFeeRate(readFeeRateForm());
  if (!dup) { warn.hidden = true; warn.textContent = ""; return; }
  const rate = moneyNumber(dup.rate);
  const rateText = rate > 0 ? ` (current rate ${Formatters.moneyExact(rate)})` : "";
  warn.hidden = false;
  warn.innerHTML = `<strong>Heads up:</strong> a rate for this org · payer · CPT · modifier · POS already exists${escapeHtml(rateText)}. Saving adds a second row — edit the existing one in the grid if you meant to change it.`;
}

async function submitAddFeeRate(event) {
  event?.preventDefault?.();
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "add fee schedule row");
  const v = readFeeRateForm();
  const errors = [];
  const fail = (fieldId, message) => errors.push({ fieldId, message });
  if (!v.tenantId) fail("feeRateTenant", "Choose a client company.");
  if (!v.orgId) fail("feeRateOrgId", "Choose a billing company or location.");
  if (!feeScheduleRowInSelectedCompany(v)) fail("feeRateOrgId", "Use a billing company within the company selected in the header.");
  if (!v.payer) fail("feeRatePayer", "Enter a payer name.");
  if (!v.procedure) fail("feeRateProcedure", "Enter a CPT code.");
  if (!v.networkKnown) fail("feeRateNetwork", "Choose network coverage before saving.");
  const normalizedRate = normalizeOptionalNumber(v.rate);
  const rate = moneyNumber(normalizedRate);
  if (normalizedRate === "" || rate < 0) fail("feeRateAmount", "Enter the contracted rate. Use $0 only for a contractually non-payable code.");
  if (!v.startDate) fail("feeRateStart", "Choose an effective start date.");
  if (v.startDate && v.endDate && v.endDate < v.startDate) fail("feeRateEnd", "End date can't be before the start date.");
  const status = els.feeRateForm?.querySelector("#feeRateFormStatus");
  els.feeRateForm?.querySelectorAll("[aria-invalid='true']").forEach((field) => field.removeAttribute("aria-invalid"));
  if (errors.length) {
    errors.forEach(({ fieldId }) => els.feeRateForm?.querySelector(`#${fieldId}`)?.setAttribute("aria-invalid", "true"));
    if (status) {
      status.className = "fee-rate-form-status fee-rate-form-status--error";
      status.setAttribute("role", "alert");
      status.textContent = errors.map(({ message }) => message).join(" ");
      status.innerHTML = `<p><strong>Please fix ${errors.length === 1 ? "this field" : `these ${errors.length} fields`}:</strong></p><ul>${errors.map(({ message }) => `<li>${escapeHtml(message)}</li>`).join("")}</ul>`;
    }
    try {
      const first = els.feeRateForm?.querySelector(`#${errors[0].fieldId}`);
      first?.focus();
      first?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    } catch (_) {}
    return;
  }
  const orgName = feeRowsForActiveUser(state.feeScheduleRows || [])
    .find((row) => String(row.orgId || "").trim() === v.orgId)?.orgName
    || tenantNameForOrgId(v.orgId) || "";
  const row = {
    tenantId: v.tenantId || tenantNameForOrgId(v.orgId) || "",
    orgId: v.orgId,
    orgName,
    payorId: "",
    externalPayerId: v.externalPayerId || "",
    payer: v.payer,
    state: v.state,
    service: v.service,
    procedure: v.procedure,
    m1: v.m1,
    m2: v.m2,
    place: v.place,
    rate: normalizedRate,
    startDate: v.startDate,
    endDate: v.endDate,
    network: v.network,
    secondaryCalc: v.secondaryCalc === "Allowed Amount" || v.secondaryCalc === "PR Amount" ? v.secondaryCalc : "",
    approvalStatus: v.approvalStatus === "approved" ? "approved" : "draft",
    duplicateCheck: ""
  };
  // Unsaved rates must stay outside the active calculation book.
  const submitButton = els.feeRateForm?.querySelector("#feeRateSubmitBtn");
  if (submitButton) submitButton.disabled = true;
  if (status) {
    status.className = "fee-rate-form-status";
    status.setAttribute("role", "status");
    status.textContent = feeScheduleApiEnabled() ? "Saving rate..." : "Adding rate locally...";
  }
  try {
    const hosted = feeScheduleApiEnabled();
    const result = await persistFeeScheduleRow(row, { showToast: false, rejectOnError: true });
    if (hosted && !result?.persisted) throw new Error("The save was not confirmed. Review the selected company and retry.");
    if (!hosted) { state.feeScheduleRows = [...(state.feeScheduleRows || []), row]; bumpFeeScheduleMatchEpoch(); saveState(); }
    resetDerivedCaches();
    audit("Fee schedule row added (guided)", activeInternalUser());
    renderFeeScheduleSurfaces();
    closeAddFeeRateModal();
    showToast(!hosted ? "Rate added locally for this session." : row.approvalStatus === "approved"
      ? "Approved rate saved. Missing Rates is refreshing to check coverage."
      : "Draft rate saved. Approve it when ready; missing coverage stays open until an approved rate matches.");
  } catch (error) {
    renderFeeScheduleSurfaces();
    if (status) {
      status.className = "fee-rate-form-status fee-rate-form-status--error";
      status.setAttribute("role", "alert");
      status.textContent = `Save failed: ${error.message}`;
    }
    try { els.feeRateForm?.querySelector("#feeRateAmount")?.focus(); } catch (_) {}
  } finally {
    if (submitButton) submitButton.disabled = false;
  }
}

const feeSchedulePendingRowSaves = new Set();

function updateFeeScheduleRow(index, field, value) {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "update fee schedule row");
  const allowedFields = new Set(["orgId", "orgName", "payorId", "payer", "state", "service", "procedure", "m1", "m2", "place", "rate", "startDate", "endDate", "network", "secondaryCalc", "approvalStatus", "duplicateCheck"]);
  if (!Number.isInteger(index) || index < 0 || index >= state.feeScheduleRows.length) return showToast("Fee schedule row was not found.");
  if (!allowedFields.has(field)) return showToast("Fee schedule field is invalid.");
  if (!state.feeScheduleRows[index]) return showToast("Fee schedule row was not found.");
  const previous = state.feeScheduleRows[index];
  const pendingKey = feeScheduleMergeKey(previous);
  if (feeSchedulePendingRowSaves.has(pendingKey)) return showToast("This rate is saving. Wait for confirmation before editing another field.");
  const next = { ...previous, [field]: field === "rate" ? normalizeOptionalNumber(value) : field === "approvalStatus" ? (String(value).toLowerCase() === "approved" ? "approved" : "draft") : value };
  if (!feeScheduleRowInSelectedCompany(next)) { showToast("Keep this rate within the company selected in the header."); renderFeeSchedules(); return; }
  if (field === "payer" && next.payer !== previous.payer) next.externalPayerId = "";
  if (!feeScheduleApiEnabled()) {
    state.feeScheduleRows[index] = next;
    bumpFeeScheduleMatchEpoch(); resetDerivedCaches();
    audit("Fee schedule " + field + " updated", activeInternalUser());
    saveState(); renderFeeSchedules(); return;
  }
  // Lock the whole row: simultaneous whole-row POSTs could overwrite each other.
  feeSchedulePendingRowSaves.add(pendingKey);
  document.querySelectorAll("#feeScheduleRows [data-fee-index='" + index + "'], #feeScheduleRows [data-delete-fee='" + index + "']").forEach((control) => { control.disabled = true; });
  // Keep the last confirmed value in the calculation book until POST succeeds.
  persistFeeScheduleRow(next, { rejectOnError: true, showDraftToast: true })
    .then((result) => {
      if (!result?.persisted) { showToast("The change was not saved. The previous value is retained."); return; }
      resetDerivedCaches(); audit("Fee schedule " + field + " updated", activeInternalUser());
    })
    .catch(() => {})
    .finally(() => { feeSchedulePendingRowSaves.delete(pendingKey); renderFeeScheduleSurfaces(); });
}

function deleteFeeScheduleRow(index) {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "delete fee schedule row");
  if (!Number.isInteger(index) || index < 0 || index >= state.feeScheduleRows.length) return showToast("Fee schedule row was not found.");
  const row = state.feeScheduleRows[index];
  if (row?.feeScheduleRateId && typeof window !== "undefined" && typeof window.confirm === "function") {
    const label = [row.payer, row.procedure, (row.rate != null && row.rate !== "") ? `${row.rate}` : ""].filter(Boolean).join(" / ") || "this rate";
    if (!window.confirm(`Archive ${label}? It will be hidden from active rates and expected-reimbursement matching. You can restore it later with the "Show archived" toggle.`)) return;
  }
  const removeVisibleRow = () => {
    state.feeScheduleRows = state.feeScheduleRows.filter((candidate) => row.feeScheduleRateId ? candidate.feeScheduleRateId !== row.feeScheduleRateId : candidate !== row);
    bumpFeeScheduleMatchEpoch();
    resetDerivedCaches();
    audit("Fee schedule row archived", activeInternalUser());
    saveState();
    renderFeeSchedules();
  };
  if (feeScheduleApiEnabled() && row?.feeScheduleRateId) {
    archiveFeeScheduleRowApi(row)
      .then(() => {
        removeVisibleRow();
        showToast("Fee schedule row archived.");
      })
      .catch(() => {});
    return;
  }
  removeVisibleRow();
  if (feeScheduleApiEnabled()) showToast("Unsaved rate row removed.");
}

function restoreFeeScheduleRow(index) {
  if (!hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return denyAction(PERMISSIONS.MANAGE_FEE_SCHEDULES, "restore fee schedule row");
  if (!Number.isInteger(index) || index < 0 || index >= state.feeScheduleRows.length) return showToast("Fee schedule row was not found.");
  const row = state.feeScheduleRows[index];
  if (!feeScheduleApiEnabled() || !row?.feeScheduleRateId) return showToast("Only saved rates can be restored.");
  restoreFeeScheduleRowApi(row)
    .then(() => {
      const target = state.feeScheduleRows.find((candidate) => candidate.feeScheduleRateId === row.feeScheduleRateId);
      if (target) target.status = "active";
      bumpFeeScheduleMatchEpoch();
      resetDerivedCaches();
      audit("Fee schedule row restored", activeInternalUser());
      saveState();
      renderFeeSchedules();
      showToast("Fee schedule row restored to active.");
    })
    .catch(() => {});
}

function restoreFeeScheduleRowApi(row = {}, options = {}) {
  if (!feeScheduleApiEnabled() || !hasPermission(PERMISSIONS.MANAGE_FEE_SCHEDULES)) return Promise.resolve({ skipped: true });
  const normalized = normalizeFeeScheduleRow(row);
  if (!normalized.feeScheduleRateId || !normalized.orgId) return Promise.resolve({ skipped: true });
  const token = workflowApiToken() || trackerApiToken();
  const currentKey = feeScheduleApiKey();
  feeScheduleApiState = { ...feeScheduleApiState, key: currentKey, status: "saving", error: "" };
  renderFeeSchedules();
  return fetch(`${workflowApiBase()}/api/setup/fee-schedule-rates/restore`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({
      tenantId: normalized.tenantId || normalized.orgName || normalized.orgId || "",
      orgId: normalized.orgId || "",
      feeScheduleRateId: normalized.feeScheduleRateId,
      reason: options.reason || "fee schedule screen restore"
    })
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        throw new Error("Your session expired. Sign in again — this page will still be here.");
      }
      if (!response.ok || body.error) throw new Error(body.message || body.error || `HTTP ${response.status}`);
      if (!feeScheduleApiRequestStillCurrent(currentKey)) {
        const staleError = new Error("Stale fee schedule restore response ignored.");
        staleError.stale = true;
        throw staleError;
      }
      const confirmed = normalizeFeeScheduleRow(body.row || {});
      if (body.restored !== true || confirmed.feeScheduleRateId !== normalized.feeScheduleRateId || confirmed.orgId !== normalized.orgId || confirmed.status !== "active") throw new Error("The server did not confirm the rate update. No local rate was changed.");
      feeScheduleApiState = { ...feeScheduleApiState, status: "ok", error: "", savedAt: timestamp() };
      refreshFeeScheduleMissingAfterPersistedMutation();
      return { restored: true, row: body.row || null };
    })
    .catch((error) => {
      if (error && error.stale) throw error;
      if (!feeScheduleApiRequestStillCurrent(currentKey)) throw error;
      feeScheduleApiState = { ...feeScheduleApiState, status: "error", error: error.message };
      showToast(`Fee schedule restore failed. ${String(error.message || "").slice(0, 120)}`);
      renderFeeSchedules();
      throw error;
    });
}

function addFollowUpRule() {
  if (!hasPermission(PERMISSIONS.MANAGE_FOLLOW_UP_RULES)) return denyAction(PERMISSIONS.MANAGE_FOLLOW_UP_RULES, "add follow-up rule");
  if (followUpRulesHostedMode()) return showToast("Hosted follow-up rules are server-owned and read-only. Open Query Map to review their authority and source status.");
  state.followUpRules = [...getActionRules(), { action: "New Action", offset: null, removeIfPaid: false }];
  audit("Follow-up action rule added", activeInternalUser());
  saveState();
  renderFollowUpRules();
  refreshActionControls();
}

function updateFollowUpRule(index, field, value) {
  if (!hasPermission(PERMISSIONS.MANAGE_FOLLOW_UP_RULES)) return denyAction(PERMISSIONS.MANAGE_FOLLOW_UP_RULES, "update follow-up rule");
  if (followUpRulesHostedMode()) return showToast("Hosted follow-up rules are server-owned and read-only. Browser edits are disabled.");
  const rules = getActionRules();
  const allowedFields = new Set(["action", "offset", "removeIfPaid"]);
  if (!Number.isInteger(index) || index < 0 || index >= rules.length) return showToast("Follow-up rule was not found.");
  if (!allowedFields.has(field)) return showToast("Follow-up rule field is invalid.");
  const patch = field === "offset"
    ? { offset: value === "" ? null : parseMoneyNumber(value) }
    : field === "removeIfPaid"
      ? { removeIfPaid: value === "true" }
      : { [field]: value };
  const normalized = normalizeFollowUpRule({ ...rules[index], ...patch });
  state.followUpRules[index] = normalized || rules[index];
  audit(`Follow-up rule ${field} updated`, activeInternalUser());
  saveState();
  refreshActionControls();
}

function deleteFollowUpRule(index) {
  if (followUpRulesHostedMode()) return showToast("Hosted follow-up rules are server-owned and read-only. Browser deletes are disabled.");
  if (!hasPermission(PERMISSIONS.MANAGE_FOLLOW_UP_RULES)) return denyAction(PERMISSIONS.MANAGE_FOLLOW_UP_RULES, "delete follow-up rule");
  const rules = getActionRules();
  if (!Number.isInteger(index) || index < 0 || index >= rules.length) return showToast("Follow-up rule was not found.");
  state.followUpRules = getActionRules().filter((_, ruleIndex) => ruleIndex !== index);
  audit("Follow-up action rule deleted", activeInternalUser());
  saveState();
  renderFollowUpRules();
  refreshActionControls();
}

function mutateClaim(claimId, patch) {
  updateWorkflow(claimId, patch);
  saveState();
  render();
}

function audit(text, by) {
  state.audit = [...state.audit, { at: timestamp(), by, text }];
}

// Bump whenever the inputs getClaims() depends on change: sourceClaims is
// reassigned, or any workflowByClaim record is written/mutated. A redundant
// bump only forces a rebuild (cheap + always correct); a MISSED bump would
// serve stale claims, so bump liberally.
function bumpClaimsDataVersion() {
  claimsDataVersion += 1;
}

// Signature of the role/user/scope that permittedByRole (canAccessClaim) keys
// off, plus the workflow overlay that getClaims spreads onto source claims.
// The overlay signature protects older direct mutation paths that predate the
// explicit claimsDataVersion bump.
function getClaimsScopeKey() {
  const role = state && state.role;
  const activeUser = state && state.activeUserName;
  const authScope = typeof currentAccessProfile === "function" ? currentAccessProfile().scope : null;
  // Sign the active user's identity + scope fields. In the non-auth (demo)
  // branch currentUser() resolves the active record from the users directory
  // and canAccessClaim keys off its role/status/orgIds/clientNames. If an admin
  // edits that record in place (e.g. Org A -> Org B), this signature changes
  // and forces a rebuild even if a mutation site forgot to bump.
  let activeUserScopeSignature = "";
  try {
    const u = (typeof currentUser === "function" ? currentUser() : null) || {};
    activeUserScopeSignature = [
      u.name || "",
      u.role || "",
      u.status || "",
      normalizeUserScopeList(u.orgIds).join(","),
      normalizeUserScopeList(u.clientNames).join(",")
    ].join("|");
  } catch (_) {}
  const workflow = (state && state.workflowByClaim) || {};
  const workflowSignature = Object.keys(workflow).sort().map((claimId) => {
    const row = workflow[claimId] || {};
    return [
      claimId,
      row.action || "",
      row.followUpDate || "",
      row.assignedTo || "",
      row.assignedBy || "",
      row.assignmentDue || "",
      row.assignmentStatus || "",
      row.assignmentInstruction || "",
      row.bounceReason || "",
      row.reviewNote || "",
      Array.isArray(row.notes) ? row.notes.length : 0,
      row.writeOffSnapshot ? JSON.stringify(row.writeOffSnapshot) : ""
    ].join("|");
  }).join("||");
  return JSON.stringify([role, activeUser, authScope || null, activeUserScopeSignature, workflowSignature]);
}

function getClaims() {
  const scopeKey = getClaimsScopeKey();
  if (getClaimsCache.claims
      && getClaimsCache.version === claimsDataVersion
      && getClaimsCache.scopeKey === scopeKey) {
    return getClaimsCache.claims;
  }
  const claims = sourceClaims.map((claim) => ({
    ...claim,
    ...(state.workflowByClaim[claim.claimId] || {})
  })).filter(permittedByRole);
  getClaimsCache = { version: claimsDataVersion, scopeKey, claims };
  return claims;
}

function permittedByRole(claim) {
  return canAccessClaim(claim);
}

// Demo seeder for offshore routing. Per user direction:
// 'Not every claim needs an owner. There's no such thing as closing out a
// claim. The only time there's an owner is if it was sent to review for
// offshore and then it's approved.'
//
// So we route only a small handful of claims (6) — enough to demonstrate the
// flow when the demo viewer switches to OffshoreBiller role, but NOT enough
// to imply that every claim has an owner. Live Synapse data is never touched.
// The demo Synapse export labels every claim with `network: "Synapse"` --
// a labeling artifact, not an actual INN/OON value. Patch a realistic
// distribution at boot so the INN/OON filter has signal: known
// commercial payers default to In Network, Medicaid/managed-care
// contractors flagged OON.
function normalizeDemoNetworkField() {
  if (!Array.isArray(sourceClaims) || !sourceClaims.length) return;
  const distinct = new Set(sourceClaims.map((c) => c.network).filter(Boolean));
  // If the source has at least 2 distinct values, leave it alone — it's
  // probably already real INN/OON data.
  if (distinct.size > 1) return;
  if (!distinct.has("Synapse") && !distinct.has("") && distinct.size > 0) return;
  const oonHints = /medicaid|amerigroup|wellcare|peachstate|nj familycare|caresource|magellan|community/i;
  sourceClaims.forEach((claim) => {
    const payer = String(claim.primaryPayer || "");
    claim.network = oonHints.test(payer) ? "OON" : "In Network";
  });
  bumpClaimsDataVersion();
}

function seedDemoOffshoreAssignments() {
  if (!Array.isArray(sourceClaims) || !sourceClaims.length) return;
  if (!localFallbackSeedingEnabled()) return;
  const info = sourceDataInfo || {};
  const isDemo = /demo|snapshot|json|fallback/i.test(`${info.label || ""} ${info.detail || ""}`);
  if (!isDemo) return;
  // 'Already seeded' check goes against the EFFECTIVE owner (workflowByClaim
  // overrides sourceClaims). Otherwise stale workflow state would falsely
  // signal 'already seeded' while the user sees no assignments.
  const effectiveOwner = (claim) => {
    const wf = state && state.workflowByClaim ? state.workflowByClaim[claim.claimId] : null;
    return String((wf && wf.assignedTo !== undefined ? wf.assignedTo : claim.assignedTo) || "");
  };
  // Route the demo to the first two real offshore billers on the roster
  // instead of hardcoded placeholder names, so the demo follows whoever the
  // admin has actually staffed (and stays correct after renames/turnover).
  const owners = assignableOffshoreBillers().map((u) => u.name).slice(0, 2);
  // ...and when nobody has been staffed yet, the DEMO still needs two owners to
  // demonstrate routing. These two strings are placeholders on demo CLAIM rows,
  // never a roster: they are not added to the user store, so they never appear
  // in the "Assign to..." picker and nobody can be assigned real work as them.
  // This branch only runs against demo/fallback data (checked above), and it
  // exists because the shipped default roster of real staff was removed — see
  // the note at the top of this file.
  if (!owners.length) owners.push("Offshore Biller A", "Offshore Biller B");
  const ownerSet = new Set(owners);
  const alreadySeeded = sourceClaims.some((c) => ownerSet.has(effectiveOwner(c)));
  if (alreadySeeded) return;
  // Pick the 6 most-past-due claims for the offshore routing demo.
  const candidates = sourceClaims
    .filter((c) => /^120|91-120/.test(String(c.aging || "")))
    .slice(0, 6);
  const target = candidates.length ? candidates : sourceClaims.slice(0, 6);
  target.forEach((claim, idx) => {
    const patch = {
      assignedTo: owners[idx % owners.length],
      assignedBy: "Stateside Biller A",
      assignmentDue: claim.followUpDate || todayDateString(),
      assignmentStatus: "Routed to Offshore Review"
    };
    // Write to BOTH the source claim (so non-workflow callers see it) AND
    // the persistent workflowByClaim (so reload + getClaims() spread won't
    // wipe it).
    Object.assign(claim, patch);
    if (state && state.workflowByClaim) {
      state.workflowByClaim[claim.claimId] = {
        ...(state.workflowByClaim[claim.claimId] || {}),
        ...patch
      };
    }
  });
  // Also seed the Review workflow: pretend offshore has already submitted
  // the first 2 routed claims back, and bounced 1 — so the Review Planner
  // demos all 4 lanes (Submitted, In Review, Approved, Bounced Back).
  const reviewSamples = [
    { idx: 0, status: "Submitted For Review", note: "Payer phone confirmed processing; waiting on 835." },
    { idx: 1, status: "Submitted For Review", note: "Appeal letter drafted; attached evidence." },
    { idx: 2, status: "In Review", note: "Spot-checked PR codes; need to verify rate match." },
    { idx: 3, status: "Bounced Back", note: "Missing ERA detail — re-pull from payer portal." }
  ];
  reviewSamples.forEach(({ idx, status, note }) => {
    const claim = target[idx];
    if (!claim) return;
    const patch = { assignmentStatus: status, reviewNote: note };
    Object.assign(claim, patch);
    if (state && state.workflowByClaim) {
      state.workflowByClaim[claim.claimId] = {
        ...(state.workflowByClaim[claim.claimId] || {}),
        ...patch
      };
    }
  });
  bumpClaimsDataVersion();
  try { saveState(); } catch (_) {}
}

function localFallbackSeedingEnabled() {
  return !trackerApiEnabled() && window.TMS_ENABLE_LOCAL_FALLBACK_SEEDING === true;
}

function getClaim(claimId) {
  const normalizedId = normalizeClaimId(claimId);
  return getClaims().find((claim) => claim.claimId === normalizedId)
    || currentTrackerApiClaim(normalizedId)
    // Live boards render from workflowBoardState (not the All Data page),
    // so board actions (assign / approve / bounce) resolve their claims
    // from the same cache. Board claims carry orgId, so canAccessClaim and
    // per-org workflow command targeting keep working.
    || currentWorkflowBoardClaim(normalizedId)
    || null;
}

function currentTrackerApiClaim(claimId) {
  const normalizedId = normalizeClaimId(claimId);
  if (!normalizedId || !trackerApiEnabled() || trackerApiPageState.status !== "ok") return null;
  const rows = Array.isArray(trackerApiPageState.response?.rows) ? trackerApiPageState.response.rows : [];
  const row = rows.find((candidate) => trackerApiClaimId(candidate) === normalizedId);
  return row ? trackerApiRowToClaim(row) : null;
}

function workflowTargetFieldsForClaim(claimId, overrides = {}) {
  const normalizedId = normalizeClaimId(claimId);
  const explicitOrgId = String(overrides.orgId || "").trim();
  if (explicitOrgId) return { orgId: explicitOrgId };
  const selectedOrgId = String(state.selectedClaimTargets?.[normalizedId]?.orgId || "").trim();
  if (selectedOrgId) return { orgId: selectedOrgId };
  const orgIds = new Set();
  const addClaim = (candidate) => {
    if (normalizeClaimId(candidate?.claimId || candidate?.claim_id) !== normalizedId) return;
    const orgId = String(candidate?.orgId || candidate?.org_id || "").trim();
    if (orgId) orgIds.add(orgId);
  };
  (getClaims() || []).forEach(addClaim);
  (trackerApiPageState.response?.rows || []).forEach(addClaim);
  [workflowBoardState.claims, workflowBoardState.personalClaims, workflowBoardState.intake].forEach((rows) => (rows || []).forEach(addClaim));
  return orgIds.size === 1 ? { orgId: [...orgIds][0] } : {};
}

// Per-line follow-up writer. Follow-up action/date live here (keyed per
// (claim, service)), NOT in workflowByClaim — that map stays claim-level for
// status/assignment. Bumps the claims data version like updateWorkflow.
function updateFollowUpOverlay(claimId, serviceId, patch) {
  const key = followUpKey(claimId, serviceId);
  const source = patch && typeof patch === "object" ? patch : {};
  const next = { ...(state.followUpByService?.[key] || {}) };
  if ("action" in source) next.action = String(source.action || "");
  if ("followUpDate" in source) next.followUpDate = String(source.followUpDate || "");
  if (!state.followUpByService || typeof state.followUpByService !== "object") state.followUpByService = {};
  next.savedAtMs = Date.now();
  state.followUpByService[key] = next;
  bumpClaimsDataVersion();
}

function reconcileFollowUpOverlayFromApiRows(rows = []) {
  if (!state.followUpByService || typeof state.followUpByService !== "object") return;
  let changed = false;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const claimId = trackerApiClaimId(row);
    const serviceId = String(row.serviceId || row.service_id || "").trim();
    if (!claimId || !serviceId) return;
    const key = followUpKey(claimId, serviceId);
    const local = state.followUpByService[key];
    if (!local) return;
    const command = latestFollowUpWorkflowCommand(claimId, serviceId, String(row.orgId || row.org_id || "").trim());
    if (command && (command.status === "pending" || command.status === "failed" || command.status === "local_fallback")) return;
    const liveAction = String(firstPresent(row.action, row.followUpAction, row.follow_up_action) || "").trim();
    const liveDate = String(firstPresent(row.followUpDate, row.follow_up_date) || "").trim();
    const localAction = String(local.action || "").trim();
    const localDate = String(local.followUpDate || "").trim();
    const liveAt = Date.parse(row.followUpUpdatedAt || "") || 0;
    const localAt = Number(local.savedAtMs || 0);
    const serverHasOurs = liveAction === localAction && liveDate === localDate;
    const serverIsNewer = liveAt > 0 && localAt > 0 && liveAt >= localAt && !serverHasOurs;
    if (serverHasOurs || serverIsNewer || row.followUpReview) {
      delete state.followUpByService[key];
      changed = true;
    }
  });
  if (changed) saveState();
}

function updateWorkflow(claimId, patch) {
  state.workflowByClaim[claimId] = {
    ...(state.workflowByClaim[claimId] || {}),
    ...sanitizeWorkflowPatch(patch)
  };
  bumpClaimsDataVersion();
}

function enqueueWorkflowCommand(command, body) {
  const commandId = claimNoteCommandId("wf");
  const targetFields = workflowTargetFieldsForClaim(body.claimId, body);
  if (!targetFields.orgId) {
    showToast("Choose the exact company row before changing workflow. Nothing was queued.");
    return null;
  }
  const queueContext = workflowQueueContextForClaim(body.claimId);
  const expectedUpdatedAt = command === "follow-up"
    ? followUpVersionOnLine(body.claimId, body.serviceId, targetFields.orgId)
    : (body.expectedUpdatedAt || undefined);
  const queuedCommand = {
    commandId,
    command,
    route: `/api/workflow/${command}`,
    body: sanitizeWorkflowBody({ ...body, ...targetFields, ...queueContext, expectedUpdatedAt, workflowCommandId: commandId }),
    access: workflowCommandAccessSnapshot(),
    status: workflowApiBase() ? "pending" : "local_fallback",
    queuedAt: timestamp(),
    queuedAtMs: Date.now()
  };
  state.workflowCommandQueue = [...(state.workflowCommandQueue || []), queuedCommand].slice(-500);
  trySendWorkflowCommand(queuedCommand);
  renderWorkflowSyncStatus();
  return queuedCommand;
}

function workflowQueueContextForClaim(claimId) {
  const context = state.queueWorkContextByClaim?.[String(claimId || "")] || {};
  const normalized = normalizeQueueWorkContext(context);
  if (!normalized.queueKey && !normalized.queueSessionId && !normalized.source) return {};
  return {
    queueKey: normalized.queueKey,
    queueSessionId: normalized.queueSessionId,
    queueSource: normalized.source
  };
}

function sanitizeWorkflowBody(body = {}) {
  return Object.fromEntries(Object.entries(body)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => [key, value == null ? "" : String(value)]));
}

function workflowCommandAccessSnapshot() {
  const profile = currentAccessProfile();
  return {
    role: state.role,
    user: activeInternalUser(),
    permissions: profile.permissions.slice(),
    scope: structuredClone(profile.scope || {})
  };
}

function normalizeServerDetailServices(services = [], claim = {}) {
  if (!Array.isArray(services)) return [];
  const primaryExternal = String(claim.primaryExternalId || claim.primaryPayerExternalId || claim.primaryPayerId || claim.primaryPayorId || "");
  const secondaryExternal = String(claim.secondaryExternalId || claim.secondaryPayerExternalId || claim.secondaryPayerId || claim.secondaryPayorId || "");
  return services.map((service) => ({
    ...service,
    primaryPayerId: String(service.primaryPayerId || service.primaryPayorId || service.primaryExternalId || service.primaryPayerExternalId || primaryExternal),
    primaryExternalId: String(service.primaryExternalId || service.primaryPayerExternalId || service.primaryPayerId || service.primaryPayorId || primaryExternal),
    secondaryPayerId: String(service.secondaryPayerId || service.secondaryPayorId || service.secondaryExternalId || service.secondaryPayerExternalId || secondaryExternal),
    secondaryExternalId: String(service.secondaryExternalId || service.secondaryPayerExternalId || service.secondaryPayerId || service.secondaryPayorId || secondaryExternal)
  }));
}

function workflowAccessScopedRequestKey(parts = {}) {
  return JSON.stringify({ ...parts, company: activeClientGroupTenant(), companyOrgIds: activeClientGroupOrgIds(), access: workflowCommandAccessSnapshot() });
}

function workflowApiBase() {
  const configured = window.TMS_WORKFLOW_API_BASE || safeLocalStorageGet(WORKFLOW_API_BASE_KEY) || "";
  return String(configured).replace(/\/$/, "");
}

function workflowApiToken() {
  return window.TMS_WORKFLOW_API_TOKEN || safeLocalStorageGet(WORKFLOW_API_TOKEN_KEY) || "";
}

function feedbackApiHeaders() {
  const token = workflowApiToken() || trackerApiToken();
  return {
    "content-type": "application/json",
    ...(token ? { authorization: `Bearer ${token}` } : {})
  };
}

// An optional prefilled draft. NOTE the Event guard: the existing binding is
// `addEventListener("click", openFeedbackModal)`, which passes the click Event
// as the first argument - without this, every ordinary ticket would open
// prefilled with an Event's properties.
function openFeedbackModal(draft = null) {
  if (!els.feedbackModal) return;
  const prefill = draft && typeof draft === "object" && !draft.target && !draft.type ? draft : null;
  const actionMenuPanel = document.getElementById("topbarActionMenuPanel");
  const returnFocus = actionMenuPanel?.contains(document.activeElement)
    ? document.getElementById("topbarMoreActionsBtn")
    : document.activeElement;
  closeTopbarActionMenu();
  if (els.feedbackModal.parentElement !== document.body) document.body.appendChild(els.feedbackModal);
  els.feedbackModal.classList.remove("hidden");
  els.feedbackTicketStatus.textContent = "";
  els.feedbackTicketStatus.className = "feedback-ticket-status";
  if (prefill) {
    if (els.feedbackTicketCategory && prefill.category) els.feedbackTicketCategory.value = String(prefill.category);
    if (els.feedbackTicketSubject && prefill.subject) els.feedbackTicketSubject.value = String(prefill.subject);
    if (els.feedbackTicketMessage && prefill.message) els.feedbackTicketMessage.value = String(prefill.message);
  }
  activateForegroundModal(els.feedbackModal, {
    initialFocus: "#feedbackTicketSubject",
    onEscape: closeFeedbackModal,
    returnFocus
  });
}

function closeFeedbackModal() {
  els.feedbackModal?.classList.add("hidden");
  deactivateForegroundModal(els.feedbackModal);
}

async function submitFeedbackTicket(event) {
  event?.preventDefault?.();
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") {
    setFeedbackTicketStatus("Ticket submission is temporarily unavailable.", "warn");
    return;
  }
  const body = {
    orgId: activeClientGroupServerOrgId(),
    category: els.feedbackTicketCategory?.value || "feedback",
    subject: els.feedbackTicketSubject?.value || "",
    message: els.feedbackTicketMessage?.value || ""
  };
  setFeedbackTicketBusy(true);
  setFeedbackTicketStatus("Sending ticket...", "loading");
  const requestSeq = ++feedbackTicketSubmitRequestSeq;
  try {
    const response = await fetch(`${base}/api/feedback`, {
      method: "POST",
      cache: "no-store",
      headers: feedbackApiHeaders(),
      body: JSON.stringify(body)
    });
    const payload = await response.json().catch(() => ({}));
    if (requestSeq !== feedbackTicketSubmitRequestSeq) return;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      throw new Error("Your session expired. Sign in again — this page will still be here.");
    }
    if (!response.ok) throw new Error(payload.message || payload.error || "Ticket could not be sent.");
    els.feedbackTicketForm?.reset?.();
    const deliveryMode = String(payload.delivery?.mode || "").trim().toLowerCase();
    const externallyNotified = deliveryMode === "provider-contract";
    setFeedbackTicketStatus(`Ticket saved in the portal: ${payload.ticket?.id || "created"}${externallyNotified ? " · Admin notification sent." : " · No external notification was sent."}`, "success");
    showToast(externallyNotified ? "Software ticket saved and admin notification sent." : "Software ticket saved in the portal.");
    if (state.view === "feedback") refreshFeedbackAdmin({ force: true });
  } catch (error) {
    if (requestSeq !== feedbackTicketSubmitRequestSeq) return;
    setFeedbackTicketStatus(error.message || "Ticket could not be sent.", "error");
  } finally {
    if (requestSeq !== feedbackTicketSubmitRequestSeq) return;
    setFeedbackTicketBusy(false);
  }
}

function setFeedbackTicketBusy(isBusy) {
  if (els.feedbackTicketSubmitBtn) {
    els.feedbackTicketSubmitBtn.disabled = Boolean(isBusy);
    els.feedbackTicketSubmitBtn.textContent = isBusy ? "Sending..." : "Send Ticket";
  }
}

function setFeedbackTicketStatus(message, tone = "") {
  if (!els.feedbackTicketStatus) return;
  els.feedbackTicketStatus.textContent = message || "";
  els.feedbackTicketStatus.className = `feedback-ticket-status${tone ? ` feedback-ticket-status--${tone}` : ""}`;
}

function renderFeedbackAdmin() {
  ensureSystemPortalDataScope();
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("feedback")) return;
  renderFeedbackStatusTabs();
  if (!els.feedbackAdminRows) return;
  const canAdmin = hasPermission(PERMISSIONS.SECURITY_AUDIT);
  if (!canAdmin) {
    els.feedbackAdminStatus.textContent = "Feedback queue requires security audit permission.";
    els.feedbackAdminRows.innerHTML = "";
    return;
  }
  if (!workflowApiBase()) {
    els.feedbackAdminStatus.textContent = "Feedback tickets are temporarily unavailable.";
    els.feedbackAdminRows.innerHTML = "";
    return;
  }
  if (!feedbackAdminState.loading && !feedbackAdminState.tickets.length && !feedbackAdminState.error) {
    refreshFeedbackAdmin();
  }
  renderFeedbackAdminRows();
}

function renderFeedbackStatusTabs() {
  Array.from(document.querySelectorAll("[data-feedback-status-filter]")).forEach((button) => {
    button.classList.toggle("active", (button.dataset.feedbackStatusFilter || "all") === (feedbackAdminState.status || "all"));
  });
}

async function refreshFeedbackAdmin({ force = false } = {}) {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function" || !hasPermission(PERMISSIONS.SECURITY_AUDIT)) {
    feedbackAdminRequestKey = "";
    renderFeedbackAdmin();
    return;
  }
  if (feedbackAdminState.loading && !force) return;
  ensureSystemPortalDataScope();
  const status = feedbackAdminStatusFilter();
  const orgId = activeClientGroupServerOrgId();
  const requestParts = { base, status, orgId };
  const requestKey = workflowAccessScopedRequestKey(requestParts);
  const read = beginSystemPortalDataRead("feedback-list");
  const requestStillCurrent = () => (
    read.current() &&
    feedbackAdminRequestKey === requestKey
    && workflowAccessScopedRequestKey(requestParts) === requestKey
  );
  feedbackAdminRequestKey = requestKey;
  feedbackAdminState.loading = true;
  feedbackAdminState.error = "";
  renderFeedbackAdminRows();
  try {
    const encodedStatus = encodeURIComponent(status);
    const response = await read.fetch(`${base}/api/admin/feedback?status=${encodedStatus}&orgId=${encodeURIComponent(orgId)}`, {
      method: "GET",
      cache: "no-store",
      headers: feedbackApiHeaders()
    });
    const payload = await response.json().catch(() => ({}));
    if (!requestStillCurrent()) return;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      throw new Error("Your session expired. Sign in again — this page will still be here.");
    }
    if (!response.ok) throw new Error(payload.message || payload.error || "Feedback queue unavailable.");
    feedbackAdminState.tickets = Array.isArray(payload.tickets) ? payload.tickets : [];
    feedbackAdminState.notificationEmail = payload.notificationEmail || "";
    if (els.feedbackNotificationEmail) els.feedbackNotificationEmail.value = feedbackAdminState.notificationEmail;
    if (els.feedbackNotificationEmailLabel) els.feedbackNotificationEmailLabel.textContent = feedbackAdminState.notificationEmail || "Not configured";
  } catch (error) {
    if (!requestStillCurrent()) return;
    feedbackAdminState.error = error.message || "Feedback queue unavailable.";
  } finally {
    read.finish();
    if (!requestStillCurrent()) return;
    feedbackAdminState.loading = false;
    renderFeedbackAdminRows();
  }
}

function renderFeedbackAdminRows() {
  if (!els.feedbackAdminRows || !els.feedbackAdminStatus) return;
  if (els.feedbackNotificationEmailLabel) els.feedbackNotificationEmailLabel.textContent = feedbackAdminState.notificationEmail || "Not loaded";
  if (feedbackAdminState.loading) {
    els.feedbackAdminStatus.textContent = "Loading feedback tickets...";
  } else if (feedbackAdminState.error) {
    els.feedbackAdminStatus.textContent = feedbackAdminState.error;
  } else {
    els.feedbackAdminStatus.textContent = `${feedbackAdminState.tickets.length} ticket${feedbackAdminState.tickets.length === 1 ? "" : "s"} in ${feedbackStatusLabel(feedbackAdminStatusFilter())}.`;
  }
  if (feedbackAdminState.loading && !feedbackAdminState.tickets.length) {
    els.feedbackAdminRows.innerHTML = `<div class="feedback-admin-empty">Loading tickets...</div>`;
    return;
  }
  if (!feedbackAdminState.tickets.length) {
    els.feedbackAdminRows.innerHTML = `<div class="feedback-admin-empty">${feedbackAdminState.error ? "No ticket rows loaded." : "No tickets match this status."}</div>`;
    return;
  }
  els.feedbackAdminRows.innerHTML = feedbackAdminState.tickets.map(feedbackTicketRowHtml).join("");
}

function feedbackTicketRowHtml(ticket = {}) {
  const status = feedbackTicketStatus(ticket.status);
  return `
    <article class="feedback-admin-row">
      <div class="feedback-admin-row-main">
        <div class="feedback-admin-row-kicker">
          <span>${escapeHtml(feedbackCategoryLabel(ticket.category))}</span>
          <span>${escapeHtml(formatDateTime(ticket.createdAtUtc || ""))}</span>
        </div>
        <h4>${escapeHtml(ticket.subject || "Untitled ticket")}</h4>
        <p>${escapeHtml(ticket.message || "")}</p>
        <small>Submitted by ${escapeHtml(ticket.submittedByEmail || "portal user")} · ${escapeHtml(ticket.submittedByRole || "Portal User")}</small>
      </div>
      <div class="feedback-admin-row-controls">
        <label>
          Status
          <select data-feedback-ticket-status="${escapeHtml(ticket.id || "")}">
            ${["new", "reviewed", "done", "closed"].map((option) => `<option value="${option}"${option === status ? " selected" : ""}>${escapeHtml(feedbackStatusLabel(option))}</option>`).join("")}
          </select>
        </label>
        <span class="feedback-admin-delivery">${escapeHtml(ticket.delivery?.mode || "saved")}</span>
      </div>
    </article>`;
}

async function updateFeedbackTicketStatus(ticketId, status) {
  const id = String(ticketId || "").trim();
  const base = workflowApiBase();
  if (!id || !base) return;
  const normalizedStatus = feedbackTicketStatus(status);
  const requestParts = { base, id, status: normalizedStatus };
  const requestKey = workflowAccessScopedRequestKey(requestParts);
  feedbackTicketStatusRequestKey = requestKey;
  const previous = feedbackAdminState.tickets.find((ticket) => ticket.id === id)?.status || "";
  feedbackAdminState.tickets = feedbackAdminState.tickets.map((ticket) => ticket.id === id ? { ...ticket, status: normalizedStatus } : ticket);
  renderFeedbackAdminRows();
  systemPortalFeedbackWrites += 1;
  try {
    const response = await fetch(`${base}/api/admin/feedback/status`, {
      method: "POST",
      cache: "no-store",
      headers: feedbackApiHeaders(),
      body: JSON.stringify({ id, status: normalizedStatus })
    });
    const payload = await response.json().catch(() => ({}));
    if (feedbackTicketStatusRequestKey !== requestKey || workflowAccessScopedRequestKey(requestParts) !== requestKey) return;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      throw new Error("Your session expired. Sign in again — this page will still be here.");
    }
    if (!response.ok) throw new Error(payload.message || payload.error || "Status update failed.");
    feedbackAdminState.tickets = feedbackAdminState.tickets.map((ticket) => ticket.id === id ? payload.ticket : ticket);
    renderFeedbackAdminRows();
    showToast("Feedback ticket updated.");
  } catch (error) {
    if (feedbackTicketStatusRequestKey !== requestKey || workflowAccessScopedRequestKey(requestParts) !== requestKey) return;
    feedbackAdminState.tickets = feedbackAdminState.tickets.map((ticket) => ticket.id === id ? { ...ticket, status: previous } : ticket);
    feedbackAdminState.error = error.message || "Status update failed.";
    renderFeedbackAdminRows();
  } finally { systemPortalFeedbackWrites -= 1; }
}

async function saveFeedbackNotificationEmail() {
  const email = String(els.feedbackNotificationEmail?.value || "").trim();
  const base = workflowApiBase();
  if (!email || !base) {
    feedbackAdminState.error = "Enter an email. Saving is temporarily unavailable.";
    renderFeedbackAdminRows();
    return;
  }
  const requestParts = { base, email };
  const requestKey = workflowAccessScopedRequestKey(requestParts);
  feedbackSettingsSaveRequestKey = requestKey;
  if (els.saveFeedbackEmailBtn) {
    els.saveFeedbackEmailBtn.disabled = true;
    els.saveFeedbackEmailBtn.textContent = "Saving...";
  }
  try {
    const response = await fetch(`${base}/api/admin/feedback/settings`, {
      method: "POST",
      cache: "no-store",
      headers: feedbackApiHeaders(),
      body: JSON.stringify({ email })
    });
    const payload = await response.json().catch(() => ({}));
    if (feedbackSettingsSaveRequestKey !== requestKey || workflowAccessScopedRequestKey(requestParts) !== requestKey) return;
    if (response.status === 401) {
      if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      throw new Error("Your session expired. Sign in again — this page will still be here.");
    }
    if (!response.ok) throw new Error(payload.message || payload.error || "Email could not be saved.");
    feedbackAdminState.notificationEmail = payload.notificationEmail || email;
    feedbackAdminState.error = "";
    if (els.feedbackNotificationEmailLabel) els.feedbackNotificationEmailLabel.textContent = feedbackAdminState.notificationEmail;
    showToast("Feedback notification email saved.");
  } catch (error) {
    if (feedbackSettingsSaveRequestKey !== requestKey || workflowAccessScopedRequestKey(requestParts) !== requestKey) return;
    feedbackAdminState.error = error.message || "Email could not be saved.";
    renderFeedbackAdminRows();
  } finally {
    if (feedbackSettingsSaveRequestKey === requestKey && els.saveFeedbackEmailBtn) {
      els.saveFeedbackEmailBtn.disabled = false;
      els.saveFeedbackEmailBtn.textContent = "Save Email";
    }
  }
}

function feedbackAdminStatusFilter() {
  const value = String(feedbackAdminState.status || "all");
  return ["all", "new", "reviewed", "done", "closed"].includes(value) ? value : "all";
}

function feedbackTicketStatus(status) {
  const value = String(status || "new");
  return ["new", "reviewed", "done", "closed"].includes(value) ? value : "new";
}

function reportGovernanceRequestKey(parts = {}) {
  return JSON.stringify({
    ...parts,
    access: workflowCommandAccessSnapshot()
  });
}

function feedbackStatusLabel(status) {
  return {
    all: "all statuses",
    new: "New",
    reviewed: "In progress",
    done: "Completed",
    closed: "Won't do"
  }[status] || "New";
}

function feedbackCategoryLabel(category) {
  return {
    bug: "Bug",
    feedback: "Feedback",
    feature_request: "Feature request",
    other: "Other"
  }[category] || "Feedback";
}

function refreshReportRunTrail() {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") return;
  const token = workflowApiToken();
  const query = reportRunFilterQuery();
  const requestParts = { base, query };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const read = beginSystemPortalDataRead("audit-runs");
  const requestStillCurrent = () => (
    read.current() &&
    reportRunTrailRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  reportRunTrailRequestKey = requestKey;
  read.fetch(`${base}/api/reports/runs?${query}`, {
    method: "GET",
    cache: "no-store",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return;
      }
      if (!response.ok || !Array.isArray(body.runs)) return;
      state.reportRunTrail = body.runs;
      saveState();
      renderReportRunTrail();
    })
    .catch(() => {
      // Report history is supplemental; workflow actions should not fail if this panel cannot load.
    }).finally(read.finish);
}

function refreshReportExportJobTrail() {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") return;
  const token = workflowApiToken();
  const query = reportRunFilterQuery();
  const requestParts = { base, query };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const read = beginSystemPortalDataRead("audit-jobs");
  const requestStillCurrent = () => (
    read.current() &&
    reportExportJobTrailRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  reportExportJobTrailRequestKey = requestKey;
  read.fetch(`${base}/api/reports/export-jobs?${query}`, {
    method: "GET",
    cache: "no-store",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return;
      }
      if (!response.ok || !Array.isArray(body.exportJobs)) return;
      state.reportExportJobTrail = body.exportJobs;
      saveState();
      renderReportExportJobTrail();
    })
    .catch(() => {
      // Export-job review is supplemental; the audit screen should still render without it.
    }).finally(read.finish);
}

function refreshExportGovernanceActivityTrail() {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") return;
  const token = workflowApiToken();
  const query = reportRunFilterQuery();
  const requestParts = { base, query };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const read = beginSystemPortalDataRead("audit-governance");
  const requestStillCurrent = () => (
    read.current() &&
    exportGovernanceActivityTrailRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  exportGovernanceActivityTrailRequestKey = requestKey;
  read.fetch(`${base}/api/reports/export-governance/activity?${query}`, {
    method: "GET",
    cache: "no-store",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return;
      }
      if (!response.ok || !Array.isArray(body.activity)) return;
      const localActivity = (state.exportGovernanceActivityTrail || []).filter((row) => row.source === "local-preview");
      state.exportGovernanceActivityTrail = [
        ...body.activity.map(sanitizeExportGovernanceActivity),
        ...localActivity
      ].slice(-150);
      saveState();
      renderExportGovernanceActivityTrail();
    })
    .catch(() => {
      // Export governance review is supplemental; existing local activity remains visible.
    }).finally(read.finish);
}

function refreshClaimQueueWorkAuditTrail() {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") return;
  const token = workflowApiToken();
  const query = reportRunFilterQuery();
  const requestParts = { base, query };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const read = beginSystemPortalDataRead("audit-queue");
  const requestStillCurrent = () => (
    read.current() &&
    claimQueueWorkAuditTrailRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  claimQueueWorkAuditTrailRequestKey = requestKey;
  read.fetch(`${base}/api/reports/claim-queue-work/audit?${query}`, {
    method: "GET",
    cache: "no-store",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return;
      }
      if (!response.ok || !Array.isArray(body.rows)) return;
      state.claimQueueWorkAuditTrail = body.rows;
      state.claimQueueWorkOutcomeSummary = body.outcomeSummary || null;
      saveState();
      renderClaimQueueWorkAuditTrail();
    })
    .catch(() => {
      // Queue-work review is supplemental; audit view still renders local history without it.
    }).finally(read.finish);
}

function previewExportGovernanceActivityExport() {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") {
    state.exportGovernanceActivityExportPreview = buildLocalExportGovernanceActivityExportPreview();
    appendExportGovernanceActivity({
      activityKind: "export_governance_activity_manifest",
      decision: "allow",
      exportJobId: state.exportGovernanceActivityExportPreview.exportJob.exportJobId,
      exportKind: "export_governance_activity_manifest",
      rowCount: state.exportGovernanceActivityExportPreview.exportJob.rowCount,
      readyForWrite: false,
      persisted: false,
      downloadEnabled: false,
      storageConfigured: false,
      reason: "local_preview_not_persisted"
    });
    saveState();
    renderExportGovernanceActivityExportPreview();
    renderExportGovernanceActivityTrail();
    showToast("Activity export preview created locally. Writes remain disabled.");
    return;
  }
  const token = workflowApiToken();
  const requestBody = {
    format: "csv",
    filters: state.reportRunFilters || {}
  };
  const requestParts = { base, requestBody };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const requestStillCurrent = () => (
    exportGovernanceActivityExportRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  exportGovernanceActivityExportRequestKey = requestKey;
  fetch(`${base}/api/reports/export-governance/activity/export-preview`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(requestBody)
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        showToast("Your session expired. Sign in again — this page will still be here.");
        return;
      }
      if (!response.ok || !body.exportJob) {
        appendExportGovernanceActivity({
          activityKind: "export_governance_activity_manifest",
          decision: "deny",
          exportKind: "export_governance_activity_manifest",
          reason: body.reason || body.error || "activity_export_preview_unavailable"
        });
        saveState();
        renderExportGovernanceActivityTrail();
        showToast("Activity export preview was denied or unavailable.");
        return;
      }
      state.exportGovernanceActivityExportPreview = body;
      appendLocalExportJobReview(body.exportJobReview);
      appendExportGovernanceActivity({
        activityKind: "export_governance_activity_manifest",
        decision: booleanFlagValue(body.exportJob.authorization?.allowed) ? "allow" : "deny",
        exportJobId: body.exportJob.exportJobId,
        exportKind: body.exportJob.exportKind,
        tenantId: body.exportJob.tenantId,
        orgId: body.exportJob.orgId,
        rowCount: body.exportJob.rowCount,
        readyForWrite: booleanFlagValue(body.exportJob.execution?.readyForWrite),
        persisted: booleanFlagValue(body.exportJob.persisted),
        downloadEnabled: false,
        storageConfigured: false,
        reason: body.exportJob.execution?.reason || ""
      });
      saveState();
      renderExportGovernanceActivityExportPreview();
      renderExportGovernanceActivityTrail();
      renderReportExportJobTrail();
    })
    .catch(() => {
      if (!requestStillCurrent()) return;
      appendExportGovernanceActivity({
        activityKind: "export_governance_activity_manifest",
        decision: "deny",
        exportKind: "export_governance_activity_manifest",
        reason: "activity_export_preview_failed"
      });
      saveState();
      renderExportGovernanceActivityTrail();
      showToast("Activity export preview failed.");
    });
}

function previewReportRunAuditExport() {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") {
    showToast("This export preview is temporarily unavailable.");
    return;
  }
  const token = workflowApiToken();
  const requestBody = {
    format: "csv",
    filters: state.reportRunFilters || {}
  };
  const requestParts = { base, requestBody };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const requestStillCurrent = () => (
    reportRunAuditExportRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  reportRunAuditExportRequestKey = requestKey;
  fetch(`${base}/api/reports/runs/export-preview`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(requestBody)
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return;
      }
      if (!response.ok || !body.exportJob) {
        appendExportGovernanceActivity({
          activityKind: "report_run_audit_export",
          decision: "deny",
          exportKind: "report_run_audit_extract",
          reason: body.reason || body.error || "export_preview_unavailable"
        });
        saveState();
        renderExportGovernanceActivityTrail();
        showToast("Report-run audit export preview was denied or unavailable.");
        return;
      }
      state.reportRunAuditExportPreview = body;
      appendExportGovernanceActivity({
        activityKind: "report_run_audit_export",
        decision: booleanFlagValue(body.exportJob.authorization?.allowed) ? "allow" : "deny",
        exportJobId: body.exportJob.exportJobId,
        exportKind: body.exportJob.exportKind,
        tenantId: body.exportJob.tenantId,
        orgId: body.exportJob.orgId,
        rowCount: body.exportJob.rowCount,
        readyForWrite: booleanFlagValue(body.exportJob.execution?.readyForWrite),
        persisted: booleanFlagValue(body.exportJob.persisted),
        reason: body.exportJob.execution?.reason || ""
      });
      saveState();
      renderReportRunTrail();
      renderExportGovernanceActivityTrail();
    })
    .catch(() => {
      if (!requestStillCurrent()) return;
      appendExportGovernanceActivity({
        activityKind: "report_run_audit_export",
        decision: "deny",
        exportKind: "report_run_audit_extract",
        reason: "export_preview_failed"
      });
      saveState();
      renderExportGovernanceActivityTrail();
      showToast("Report-run audit export preview failed.");
    });
}

function previewQueueWorkExport() {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") {
    showToast("This export preview is temporarily unavailable.");
    return;
  }
  const token = workflowApiToken();
  const requestBody = {
    format: "csv",
    filters: state.reportRunFilters || {}
  };
  const requestParts = { base, requestBody };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const requestStillCurrent = () => (
    queueWorkExportRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  queueWorkExportRequestKey = requestKey;
  fetch(`${base}/api/reports/claim-queue-work/export-preview`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(requestBody)
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        showToast("Your session expired. Sign in again — this page will still be here.");
        return;
      }
      if (!response.ok || !body.exportJob) {
        appendExportGovernanceActivity({
          activityKind: "queue_work_export",
          decision: "deny",
          exportKind: "queue_work_outcome_summary",
          reason: body.reason || body.error || "queue_export_preview_unavailable"
        });
        saveState();
        renderExportGovernanceActivityTrail();
        showToast("Queue-work export preview was denied or unavailable.");
        return;
      }
      state.queueWorkExportPreview = body;
      appendLocalExportJobReview(body.exportJobReview);
      appendExportGovernanceActivity({
        activityKind: "queue_work_export",
        decision: booleanFlagValue(body.exportJob.authorization?.allowed) ? "allow" : "deny",
        exportJobId: body.exportJob.exportJobId,
        exportKind: body.exportJob.exportKind,
        tenantId: body.exportJob.tenantId,
        orgId: body.exportJob.orgId,
        rowCount: body.exportJob.rowCount,
        readyForWrite: booleanFlagValue(body.exportJob.execution?.readyForWrite),
        persisted: booleanFlagValue(body.exportJob.persisted),
        reason: body.exportJob.execution?.reason || ""
      });
      saveState();
      renderQueueWorkExportPreview();
      renderReportExportJobTrail();
      renderExportGovernanceActivityTrail();
    })
    .catch(() => {
      if (!requestStillCurrent()) return;
      appendExportGovernanceActivity({
        activityKind: "queue_work_export",
        decision: "deny",
        exportKind: "queue_work_outcome_summary",
        reason: "queue_export_preview_failed"
      });
      saveState();
      renderExportGovernanceActivityTrail();
      showToast("Queue-work export preview failed.");
    });
}

function bindReportRunDetailButtons() {
  if (!els.reportRunTrail) return;
  Array.from(els.reportRunTrail.querySelectorAll("[data-report-run-detail]")).forEach((button) => {
    button.addEventListener("click", () => fetchReportRunDetail(button.dataset.reportRunDetail));
  });
}

function fetchReportRunDetail(reportRunId) {
  const base = workflowApiBase();
  if (!base || !reportRunId || typeof fetch !== "function") return;
  const token = workflowApiToken();
  const requestParts = { base, reportRunId: String(reportRunId || "") };
  const requestKey = reportGovernanceRequestKey(requestParts);
  const requestStillCurrent = () => (
    reportRunDetailRequestKey === requestKey
    && reportGovernanceRequestKey(requestParts) === requestKey
  );
  reportRunDetailRequestKey = requestKey;
  fetch(`${base}/api/reports/runs/${encodeURIComponent(reportRunId)}`, {
    method: "GET",
    cache: "no-store",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      if (response.status === 401) {
        if (typeof notePortalSessionExpired === "function") notePortalSessionExpired();
        return;
      }
      if (!response.ok || !body.reportRun) return;
      state.selectedReportRun = body.reportRun;
      saveState();
      renderReportRunTrail();
    })
    .catch(() => {
      // Detail lookup is supplemental to the audit screen.
    });
}

function renderSelectedReportRun() {
  const run = state.selectedReportRun;
  if (!run) return "";
  const manifest = run.manifest || {};
  return `
    <div class="report-run-detail">
      <div class="sync-item-header">
        <strong>${escapeHtml(run.reportRunId || "Report run detail")}</strong>
        ${pill(reportRunDecisionLabel(run), reportRunDecisionTone(run))}
      </div>
      <dl>
        <div><dt>Generated</dt><dd>${escapeHtml(run.createdAtUtc || "")}</dd></div>
        <div><dt>Requester</dt><dd>${escapeHtml(`${run.requestedByRole || ""} / ${run.requestedByUserId || ""}`)}</dd></div>
        <div><dt>Scope</dt><dd>${escapeHtml(portalCompanyLabel(run.orgId, clientActionCompanyName(run.tenantId, run.orgId) || "Company name unavailable"))}</dd></div>
        <div><dt>Counts</dt><dd>${escapeHtml(reportRunCountsSummary(run.counts || {}))}</dd></div>
        <div><dt>Export</dt><dd>${escapeHtml(reportRunExportSummary(run))}</dd></div>
      </dl>
      <div class="report-run-columns">
        <div>
          <strong>Allowed Fields</strong>
          <p>${escapeHtml((manifest.columns || run.allowedFields || []).join(", ") || "None")}</p>
        </div>
        <div>
          <strong>Redacted Fields</strong>
          <p>${escapeHtml((run.redactedFields || manifest.redactedFields || []).join(", ") || "None")}</p>
        </div>
      </div>
      <p>${escapeHtml(run.labels?.final || "")}</p>
      <p>${escapeHtml(run.labels?.provisional || "")}</p>
    </div>
  `;
}

function reportRunCountsSummary(counts = {}) {
  return `${counts.reportClaims || 0} report claims / ${counts.scopedClaims || 0} scoped / ${counts.sourceClaims || 0} source`;
}

function bindReportRunFilter(element, key, property = "value") {
  if (!element) return;
  element.addEventListener("change", () => {
    state.reportRunFilters = {
      ...(state.reportRunFilters || {}),
      [key]: property === "checked" ? Boolean(element.checked) : element.value
    };
    saveState();
    renderReportRunTrail();
    refreshReportRunTrail();
    refreshReportExportJobTrail();
    refreshClaimQueueWorkAuditTrail();
  });
}

function syncReportRunFilterControls() {
  const filters = state.reportRunFilters || {};
  if (els.reportRunTenantFilter) els.reportRunTenantFilter.value = filters.tenantId || "All";
  if (els.reportRunOrgFilter) els.reportRunOrgFilter.value = filters.orgId || "All";
  if (els.reportRunFromDate) els.reportRunFromDate.value = filters.fromDate || "";
  if (els.reportRunToDate) els.reportRunToDate.value = filters.toDate || "";
  if (els.reportRunExportOnly) els.reportRunExportOnly.checked = booleanFlagValue(filters.exportOnly);
  if (els.reportRunDeniedOnly) els.reportRunDeniedOnly.checked = booleanFlagValue(filters.deniedOnly);
  if (els.reportRunRedactedOnly) els.reportRunRedactedOnly.checked = booleanFlagValue(filters.redactedOnly);
  if (els.exportJobKindFilter) els.exportJobKindFilter.value = filters.exportKind || "All";
}

function reportRunFilterQuery() {
  const filters = { ...(state.reportRunFilters || {}), ...(activeClientGroupTenant() ? { tenantId: "All", orgId: activeClientGroupServerOrgId() } : {}) };
  const params = new URLSearchParams();
  params.set("limit", "25");
  ["tenantId", "orgId", "fromDate", "toDate", "exportKind"].forEach((key) => {
    if (filters[key] && filters[key] !== "All") params.set(key, filters[key]);
  });
  ["exportOnly", "deniedOnly", "redactedOnly"].forEach((key) => {
    if (booleanFlagValue(filters[key])) params.set(key, "true");
  });
  return params.toString();
}

function reportRunDecisionLabel(run) {
  if (run.authorizationDecision === "deny") return "Denied";
  if (booleanFlagValue(run.redactedFinancials)) return "Allowed · redacted";
  return "Allowed";
}

function reportRunDecisionTone(run) {
  if (run.authorizationDecision === "deny") return "red";
  if (booleanFlagValue(run.redactedFinancials)) return "amber";
  return "green";
}

function reportRunFilterSummary(filters = {}) {
  return [
    filters.tenant || "All tenants",
    filters.payer || "All payers",
    filters.provider || "All providers",
    filters.location || "All locations",
    filters.aging || "All aging",
    filters.status || "All statuses"
  ].filter(Boolean).join(" / ");
}

function reportRunExportSummary(run = {}) {
  if (!run.exportRequested) return "No export requested";
  if (run.exportAllowed) return `Export allowed${run.exportFormat ? ` · ${run.exportFormat}` : ""}`;
  return `Export denied${run.exportReason ? ` · ${run.exportReason}` : ""}`;
}

function workflowApiHealthSummary() {
  const base = workflowApiBase();
  if (!base) return { tone: "fallback", label: "Local fallback" };
  const health = state.workflowApiHealth || {};
  if (health.base !== base) return { tone: "unchecked", label: "Portal ready" };
  const formulaLabel = workflowApiFormulaParityLabel(health);
  if (health.status === "ok" && health.durableWorkflowStore) {
    return { tone: formulaLabel.tone || "connected", label: `Portal ready${formulaLabel.label ? ` · ${formulaLabel.label}` : ""}` };
  }
  if (health.status === "ok") return { tone: formulaLabel.tone || "connected", label: `Portal ready${formulaLabel.label ? ` · ${formulaLabel.label}` : ""}` };
  if (health.status === "error") return { tone: "error", label: "Portal unavailable" };
  return { tone: "unchecked", label: "Portal ready" };
}

function workflowApiFormulaParityLabel(health = {}) {
  const mirrorParity = health.dataAlignment?.formulaParity || null;
  if (mirrorParity?.available) {
    const failures = Array.isArray(mirrorParity.failures) ? mirrorParity.failures : [];
    const topBuckets = Array.isArray(mirrorParity.topBuckets) ? mirrorParity.topBuckets : [];
    const freshnessText = preparedCacheFreshnessText(mirrorParity.cacheFreshness);
    const bucketDetails = failures.slice(0, 5).map((failure) => topBuckets.find((bucket) => bucket.key === failure.key))
      .filter(Boolean)
      .map((bucket) => `${bucket.key || "formula"} · ${portalCompanyLabel(bucket.orgId)} · proc ${bucket.procedure || "unknown"} · ${formatNounCount(Number(bucket.rows || 0), "row")}${Number(bucket.mismatchSum || 0) ? ` · ${money(bucket.mismatchSum)}` : ""}`);
    if (mirrorParity.ready) {
      return {
        tone: "connected",
        label: "formula parity passed",
        detail: `Prepared app-owned formula parity cache passed across ${formatNounCount(Number(mirrorParity.sampledRows || 0), "mirrored service row")}.${freshnessText ? ` ${freshnessText}` : ""}`
      };
    }
    return {
      tone: "unchecked",
      label: "formula parity warnings",
      detail: `Prepared app-owned formula parity cache found ${failures.length} formula warning group${failures.length === 1 ? "" : "s"} across ${formatNounCount(Number(mirrorParity.sampledRows || 0), "mirrored service row")}: ${failures.slice(0, 5).map((failure) => `${failure.key} (${Number(failure.mismatchCount || 0).toLocaleString()})`).join(", ")}.${freshnessText ? ` ${freshnessText}` : ""}`,
      details: bucketDetails
    };
  }
  if (mirrorParity && mirrorParity.available === false) {
    return {
      tone: "unchecked",
      label: "formula parity not cached",
      detail: `Prepared app-owned formula parity cache is not available${mirrorParity.reason ? ` (${mirrorParity.reason})` : ""}.`
    };
  }
  const summary = health.adapterModePlanSummary || {};
  if (Array.isArray(summary.formulaParityBlockedSurfaces) && summary.formulaParityBlockedSurfaces.length) {
    return { tone: "error", label: "formula parity failed" };
  }
  if (Array.isArray(summary.formulaParityPassedSurfaces) && summary.formulaParityPassedSurfaces.includes("tracker")) {
    return { tone: "connected", label: "formula parity passed" };
  }
  if (Array.isArray(summary.formulaParityMissingSurfaces) && summary.formulaParityMissingSurfaces.includes("tracker")) {
    return { tone: "unchecked", label: "formula parity not checked" };
  }
  return { tone: "", label: "" };
}

function refreshWorkflowApiHealth(options = {}) {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") {
    workflowApiHealthRequestKey = "";
    state.workflowApiHealth = { status: "fallback", base: "", checkedAt: timestamp() };
    renderWorkflowSyncStatus();
    renderDeploymentReadiness();
    return;
  }
  const requestParts = { base };
  const requestKey = workflowAccessScopedRequestKey(requestParts);
  const requestStillCurrent = () => (
    workflowApiHealthRequestKey === requestKey
    && workflowAccessScopedRequestKey(requestParts) === requestKey
  );
  if (!options.force && workflowApiHealthRequestKey === requestKey && state.workflowApiHealth?.base === base && state.workflowApiHealth?.checkedAt && Date.now() - timestampValue(state.workflowApiHealth.checkedAt) < 15000) {
    renderWorkflowSyncStatus();
    renderDeploymentReadiness();
    return;
  }
  workflowApiHealthRequestKey = requestKey;
  fetch(`${base}/api/health`, { method: "GET", cache: "no-store" })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!requestStillCurrent()) return;
      state.workflowApiHealth = {
        status: response.ok && body.status === "ok" ? "ok" : "error",
        base,
        checkedAt: timestamp(),
        durableWorkflowStore: booleanFlagValue(body.durableWorkflowStore),
        workflowCommandRows: body.workflowCommandRows,
        trackerQueryMode: body.trackerQueryMode || "",
        trackerQueryAdapter: booleanFlagValue(body.trackerQueryAdapter),
        reportQueryMode: body.reportQueryMode || "",
        reportQueryAdapter: booleanFlagValue(body.reportQueryAdapter),
        claimQueueDrilldownMode: body.claimQueueDrilldownMode || "",
        claimQueueDrilldownAdapter: booleanFlagValue(body.claimQueueDrilldownAdapter),
        feeScheduleSourceMode: body.feeScheduleSourceMode || "",
        feeScheduleRepository: booleanFlagValue(body.feeScheduleRepository),
        rawPaymentMirror: body.rawPaymentMirror || null,
        rawPaymentCache: body.rawPaymentCache || null,
        clientPaymentsSource: body.clientPaymentsSource || "",
        clientPaymentsFallbackReason: body.clientPaymentsFallbackReason || "",
        dataAlignment: body.dataAlignment || null,
        preparedRuntimeCaches: body.preparedRuntimeCaches || null,
        preparedCacheRefreshPlan: body.preparedCacheRefreshPlan || null,
        preparedCacheRefreshScheduler: body.preparedCacheRefreshScheduler || null,
        calculationParity: body.calculationParity || null,
        financialReadinessActions: body.financialReadinessActions || null,
        financialProductionSignoff: body.financialProductionSignoff || null,
        identityMode: body.identityMode || "",
        identityProvider: body.identityProvider || "",
        adapterModePlanSummary: body.adapterModePlanSummary || null,
        adapterModePlanSurfaces: Array.isArray(body.adapterModePlanSurfaces) ? body.adapterModePlanSurfaces : []
      };
      saveState();
      renderWorkflowSyncStatus();
      renderDeploymentReadiness();
      if (state.view === "feeSchedules") renderFeeSchedules();
    })
    .catch((error) => {
      if (!requestStillCurrent()) return;
      state.workflowApiHealth = {
        status: "error",
        base,
        checkedAt: timestamp(),
        error: error.message
      };
      saveState();
      renderWorkflowSyncStatus();
      renderDeploymentReadiness();
      if (state.view === "feeSchedules") renderFeeSchedules();
    });
}

function safeLocalStorageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return "";
  }
}

function safeLocalStorageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Browser storage may be disabled; API calls can still use in-memory globals.
  }
}

function safeLocalStorageRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Browser storage may be disabled; ignore cleanup failures.
  }
}

function safeSessionStorageGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return "";
  }
}

function safeSessionStorageSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Fall back to in-memory globals if tab-scoped storage is unavailable.
  }
}

function safeSessionStorageRemove(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Browser storage may be disabled; ignore cleanup failures.
  }
}

function trySendWorkflowCommand(queuedCommand) {
  const base = workflowApiBase();
  if (!base || typeof fetch !== "function") return;
  const queue = state.workflowCommandQueue || [];
  const commandIndex = queue.findIndex((entry) => entry.commandId === queuedCommand.commandId);
  if (commandIndex < 0) return;
  const liveCommand = queue[commandIndex];
  if (liveCommand.command === "follow-up") {
    const lineKey = followUpKey(liveCommand.body?.claimId, liveCommand.body?.serviceId);
    const earlierActive = queue.slice(0, commandIndex).some((entry) => (
      entry.command === "follow-up"
      && followUpKey(entry.body?.claimId, entry.body?.serviceId) === lineKey
      && ["pending", "sending"].includes(entry.status)
    ));
    if (earlierActive) return;
  }
  liveCommand.status = "sending";
  saveState();
  queuedCommand = liveCommand;
  const token = workflowApiToken();
  const attemptKey = workflowCommandAttemptKey(queuedCommand);
  fetch(`${base}${queuedCommand.route}`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(queuedCommand.body)
  })
    .then(async (response) => {
      const responseBody = await response.json().catch(() => ({}));
      if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
      // 4xx means the server rejected the command body; auth/timing statuses stay retryable.
      const transientHttpStatus = [401, 408, 425, 429].includes(response.status);
      // A followup-review 200 can still have recorded NOTHING (defect 2): read the
      // per-item decisionRecorded, never the envelope. Dead-letter it rather than
      // retrying - a missing overlay row or a self-review refusal cannot be fixed
      // by sending the same command again.
      const unrecorded = queuedCommand.command === "followup-review" && response.ok
        ? followUpReviewUnrecordedItems(responseBody)
        : [];
      const status = unrecorded.length
        ? "rejected"
        : (response.ok ? "synced" : (response.status >= 400 && response.status < 500 && !transientHttpStatus ? "rejected" : "failed"));
      markWorkflowCommandDelivery(queuedCommand.commandId, status, {
        httpStatus: response.status,
        response: responseBody,
        ...(unrecorded.length ? { unrecorded } : {})
      }, attemptKey);
    })
    .catch((error) => {
      markWorkflowCommandDelivery(queuedCommand.commandId, "failed", {
        error: error.message
      }, attemptKey);
    });
}

function workflowCommandAttemptKey(command = {}) {
  return JSON.stringify({
    commandId: String(command.commandId || ""),
    retryCount: Number(command.retryCount || 0),
    retriedAtMs: Number(command.retriedAtMs || 0),
    queuedAtMs: Number(command.queuedAtMs || 0)
  });
}

function markWorkflowCommandDelivery(commandId, status, result = {}, expectedAttemptKey = "") {
  const deliveredAt = timestamp();
  const deliveredAtMs = Date.now();
  state.workflowCommandQueue = (state.workflowCommandQueue || []).map((queuedCommand) => {
    if (queuedCommand.commandId !== commandId) return queuedCommand;
    if (expectedAttemptKey && workflowCommandAttemptKey(queuedCommand) !== expectedAttemptKey) return queuedCommand;
    if (queuedCommand.access && JSON.stringify(queuedCommand.access) !== JSON.stringify(workflowCommandAccessSnapshot())) return queuedCommand;
    return { ...queuedCommand, status, result, deliveredAt, deliveredAtMs };
  });
  saveState();
  renderWorkflowSyncStatus();
  const deliveredCommand = (state.workflowCommandQueue || []).find((queuedCommand) => queuedCommand.commandId === commandId);
  // Debounced and shared, so a 30-line bulk decision is one badge refresh.
  if (status === "synced" && typeof noteMyWorkAttentionChanged === "function") noteMyWorkAttentionChanged();
  if (status === "synced") {
    const overwrite = result && result.response && result.response.overwrite;
    if (overwrite && overwrite.previousAction) {
      showToast(`Saved. Replaced ${overwrite.previousBy || "another biller"}'s ${overwrite.previousAction}.`);
    }
    if (state.view === "workbench") {
      window.setTimeout(() => {
        if (state.view === "workbench" && els.claimRows) renderClaimsTable();
      }, 12500);
    }
    if (deliveredCommand?.command === "withdraw-review" || deliveredCommand?.command === "undo-approval") {
      updateWorkflow(deliveredCommand.body?.claimId, {
        assignmentStatus: "In Progress",
        approvalEffectStatus: "withdrawn",
        workflowUpdatedAt: result.response?.touchEvent?.touchedAtUtc || ""
      });
      if (trackerApiPageState.status === "ok") trackerApiPageState.status = "idle";
      showToast(deliveredCommand.command === "withdraw-review" ? "Review withdrawn. You can edit and resubmit it." : "Approval undone. The item is open for revision.");
    }
  }
  if (state.view === "workbench" && els.claimRows) renderClaimsTable();
  if (state.view === "audit") renderWorkflowCommandTrail();
  if (status === "synced" && deliveredCommand?.command === "follow-up") {
    rememberFollowUpVersionOnLine(
      deliveredCommand.body?.claimId,
      deliveredCommand.body?.serviceId,
      result.response?.followUpUpdatedAt
    );
    const lineKey = followUpKey(deliveredCommand.body?.claimId, deliveredCommand.body?.serviceId);
    const nextCommand = (state.workflowCommandQueue || []).find((entry) => (
      entry.command === "follow-up"
      && entry.status === "pending"
      && followUpKey(entry.body?.claimId, entry.body?.serviceId) === lineKey
    ));
    if (nextCommand) {
      nextCommand.body.expectedUpdatedAt = followUpVersionOnLine(nextCommand.body?.claimId, nextCommand.body?.serviceId);
      saveState();
      trySendWorkflowCommand(nextCommand);
    }
  }
  const staleFollowUp = Boolean(deliveredCommand
    && deliveredCommand.command === "follow-up"
    && Number(result.httpStatus) === 409
    && result.response?.error === "follow_up_stale");
  if (staleFollowUp) {
    const key = followUpKey(deliveredCommand.body?.claimId, deliveredCommand.body?.serviceId);
    if (state.followUpByService && key) delete state.followUpByService[key];
    if (trackerApiPageState.status === "ok") trackerApiPageState.status = "idle";
    saveState();
    showToast("Someone else changed this service line first. Your edit was not saved; the latest value is reloading so you can compare and try again.");
  }
  const isApprovalDecision = Boolean(deliveredCommand && deliveredCommand.command === "followup-review");
  // Both notifiers aggregate a burst into ONE toast, so a 30-row bulk click that
  // is dead-lettered still shows a single generic notice pointing at Audit >
  // Workflow Sync (where the row reads "Rejected - not saved"). The precise
  // recorded-vs-asked count comes from closeApprovalDecisionBatch below.
  if (status === "failed") notifyWorkflowDeliveryFailure();
  else if (status === "rejected") notifyWorkflowRejection();
  // A delivered approve/reject flips the server projection; refetch the queue so
  // stale-version rows re-appear as pending ("changed since - re-review").
  if (isApprovalDecision) {
    recordApprovalDecisionDelivery(deliveredCommand, status, result);
    if (status === "synced") {
      invalidateApprovalQueue();
      if (state.view === "reviews" || (state.view === "assignments" && ["requests", "approvals"].includes(oversightState().tab))) renderReviews();
    }
  }
  refreshWorkflowApiHealth();
}

let workflowFailureToastTimer = 0;
let workflowFailureToastCount = 0;

// One aggregated toast per failure burst so bulk retries cannot spam the user.
function notifyWorkflowDeliveryFailure() {
  workflowFailureToastCount += 1;
  if (workflowFailureToastTimer) return;
  workflowFailureToastTimer = window.setTimeout(() => {
    const failures = workflowFailureToastCount;
    workflowFailureToastTimer = 0;
    workflowFailureToastCount = 0;
    showToast(failures === 1
      ? "A workflow change didn't reach the server — it will retry."
      : `${failures} workflow changes didn't reach the server — they will retry.`);
  }, 1200);
}

let workflowRejectionToastTimer = 0;
let workflowRejectionToastCount = 0;

// One aggregated toast per rejection burst; rejected commands are dead-lettered and never retried.
function notifyWorkflowRejection() {
  workflowRejectionToastCount += 1;
  if (workflowRejectionToastTimer) return;
  workflowRejectionToastTimer = window.setTimeout(() => {
    const rejections = workflowRejectionToastCount;
    workflowRejectionToastTimer = 0;
    workflowRejectionToastCount = 0;
    showToast(rejections === 1
      ? "A workflow change was rejected by the server and won't retry — see Audit > Workflow Sync."
      : `${rejections} workflow changes were rejected by the server and won't retry — see Audit > Workflow Sync.`);
  }, 1200);
}

function retryWorkflowCommand(commandId) {
  const command = (state.workflowCommandQueue || []).find((queuedCommand) => queuedCommand.commandId === commandId);
  if (!command) return showToast("Workflow command was not found.");
  if (!workflowApiBase()) return showToast("This action is temporarily unavailable. Please try again later.");
  const retryCommand = {
    ...command,
    status: "pending",
    retryCount: Number(command.retryCount || 0) + 1,
    retriedAt: timestamp()
  };
  state.workflowCommandQueue = (state.workflowCommandQueue || []).map((queuedCommand) => queuedCommand.commandId === commandId ? retryCommand : queuedCommand);
  saveState();
  renderWorkflowSyncStatus();
  if (state.view === "audit") renderWorkflowCommandTrail();
  trySendWorkflowCommand(retryCommand);
}

function retryAllWorkflowCommands() {
  const retryable = (state.workflowCommandQueue || []).filter(canRetryWorkflowCommand);
  if (!retryable.length) return showToast("No workflow commands need retry.");
  if (!workflowApiBase()) return showToast("This action is temporarily unavailable. Please try again later.");
  retryable.forEach((command) => retryWorkflowCommand(command.commandId));
}

function bindWorkflowRetryButtons(container = document) {
  Array.from(container.querySelectorAll("[data-retry-workflow]")).forEach((button) => {
    button.addEventListener("click", () => {
      const commandId = button.dataset.retryWorkflow;
      if (commandId === "all") retryAllWorkflowCommands();
      else retryWorkflowCommand(commandId);
    });
  });
}

const WORKFLOW_PENDING_STALE_MS = 2 * 60 * 1000;
const WORKFLOW_FLUSH_BATCH_SIZE = 10;

function canRetryWorkflowCommand(command) {
  if (["failed", "local_fallback"].includes(command?.status)) return true;
  return isStaleWorkflowPending(command);
}

function workflowCommandLastAttemptMs(command) {
  const attemptMs = Number(command?.retriedAtMs || command?.queuedAtMs || 0);
  if (attemptMs) return attemptMs;
  const parsed = Date.parse(command?.retriedAt || command?.queuedAt || "");
  return Number.isNaN(parsed) ? 0 : parsed;
}

// A pending/sending command is normally in flight, but a page closed mid-send
// leaves a persisted `sending` record with no callback able to finish it. Treat
// either state as retryable once the in-flight window has
// passed; replays are safe because the server dedupes on workflowCommandId.
function isStaleWorkflowPending(command) {
  if (!["pending", "sending"].includes(command?.status)) return false;
  const attemptedAtMs = workflowCommandLastAttemptMs(command);
  if (!attemptedAtMs) return true;
  return Date.now() - attemptedAtMs >= WORKFLOW_PENDING_STALE_MS;
}

// Re-send every undelivered command (failed, local fallback, stale pending/sending)
// with its original workflowCommandId so the server can dedupe replays.
// Runs once per boot after token hydration, including the post-login boot.
function flushWorkflowCommandQueue() {
  if (!state || !workflowApiBase() || typeof fetch !== "function") return 0;
  // This function runs only during boot. A persisted `sending` row has no
  // surviving callback in this new page, so it is orphaned immediately—not
  // merely after the ordinary two-minute in-flight window. Replay now with the
  // same workflowCommandId; server idempotency makes that safe.
  const resendable = (state.workflowCommandQueue || []).filter((command) => command?.status === "sending" || canRetryWorkflowCommand(command));
  if (!resendable.length) return 0;
  const retriedIds = new Set(resendable.map((command) => command.commandId));
  const retriedAt = timestamp();
  const retriedAtMs = Date.now();
  state.workflowCommandQueue = (state.workflowCommandQueue || []).map((queuedCommand) => retriedIds.has(queuedCommand.commandId)
    ? { ...queuedCommand, status: "pending", retryCount: Number(queuedCommand.retryCount || 0) + 1, retriedAt, retriedAtMs }
    : queuedCommand);
  saveState();
  renderWorkflowSyncStatus();
  if (state.view === "audit") renderWorkflowCommandTrail();
  const resends = state.workflowCommandQueue.filter((queuedCommand) => retriedIds.has(queuedCommand.commandId));
  const sendBatch = (startIndex) => {
    resends.slice(startIndex, startIndex + WORKFLOW_FLUSH_BATCH_SIZE).forEach((queuedCommand) => trySendWorkflowCommand(queuedCommand));
    if (startIndex + WORKFLOW_FLUSH_BATCH_SIZE < resends.length) window.setTimeout(() => sendBatch(startIndex + WORKFLOW_FLUSH_BATCH_SIZE), 250);
  };
  sendBatch(0);
  return resends.length;
}

function workflowCommandSummary() {
  const commands = state.workflowCommandQueue || [];
  return {
    total: commands.length,
    pending: commands.filter((command) => command.status === "pending").length,
    synced: commands.filter((command) => command.status === "synced").length,
    failed: commands.filter((command) => command.status === "failed").length,
    rejected: commands.filter((command) => command.status === "rejected").length,
    localFallback: commands.filter((command) => command.status === "local_fallback").length
  };
}

function workflowCommandStatusLabel(status) {
  if (status === "local_fallback") return "Local Fallback";
  if (status === "synced") return "Synced";
  if (status === "failed") return "Failed";
  if (status === "rejected") return "Rejected";
  if (status === "pending") return "Pending";
  return "Queued";
}

function workflowCommandStatusTone(status) {
  if (status === "synced") return "green";
  if (status === "failed") return "red";
  if (status === "rejected") return "red";
  if (status === "pending") return "amber";
  return "gray";
}

function pickWorkflowFields(claim) {
  return {
    action: claim.action || "",
    followUpDate: claim.followUpDate || "",
    assignedTo: claim.assignedTo || "",
    assignedBy: claim.assignedBy || "",
    assignmentDue: claim.assignmentDue || "",
    assignmentStatus: claim.assignmentStatus || "Unassigned",
    assignmentInstruction: claim.assignmentInstruction || "",
    instructionBy: claim.instructionBy || "",
    instructionAt: claim.instructionAt || "",
    bounceReason: claim.bounceReason || "",
    bounceBy: claim.bounceBy || "",
    bounceAt: claim.bounceAt || "",
    reviewNote: claim.reviewNote || "",
    writeOffSnapshot: claim.writeOffSnapshot || null,
    notes: Array.isArray(claim.notes) ? claim.notes : []
  };
}

function getActionRules() {
  // normalizeFollowUpRules structuredClone()s the whole matrix on every call.
  // During a grid row build the answer is a constant; see the memo note on
  // beginFollowUpPolicyBuildMemo. Outside a build this is unchanged.
  const memo = followUpPolicyBuildMemo;
  if (memo && memo.actionRules) return memo.actionRules;
  const rules = normalizeFollowUpRules(state?.followUpRules);
  if (memo) memo.actionRules = rules;
  return rules;
}

// Rules a HUMAN may pick. System-only actions (the auto-added "Claim Status Checkup Required")
// are excluded from every manual dropdown — CT hides them from the picker
// (workflow_app_prototype.py:329-330) — but they stay in getActionRules() so filters, slicers,
// due-date math and the Follow-Up Rules setup page still see the whole matrix.
function getSelectableActionRules() {
  const memo = followUpPolicyBuildMemo;
  if (memo && memo.selectable) return memo.selectable;
  const policy = currentFollowUpActionPolicy();
  const selectable = manualFollowUpActionRules()
    .filter((rule) => normalizeFollowUpActionPolicyMode(policy.actions[rule.action]) !== "denied")
    .map((rule) => ({ ...rule, accessMode: normalizeFollowUpActionPolicyMode(policy.actions[rule.action]) }))
    .sort((left, right) => compareFollowUpActionChoices(left.action, right.action));
  if (memo) memo.selectable = selectable;
  return selectable;
}

// ALIAS-AWARE matrix lookup. An exact, case-sensitive match used to be the only way to find a
// rule, so any legacy/imported action string that was not byte-identical ("Write-Off- Duplicate",
// "Client Action Pending - Autharazation", a stray double space) got NO due-date offset and no
// removeIfPaid semantics (CT_TRACKER_PARITY_AUDIT_2026-07-22 finding 3-B). Resolving through
// canonicalFollowUpAction() applies CT's normalize + alias rules first. The stored action string
// is NOT rewritten — this only decides which matrix row governs it.
function followUpRuleForAction(action) {
  const rules = getActionRules();
  const exact = rules.find((rule) => rule.action === action);
  if (exact) return exact;
  const canonical = canonicalFollowUpAction(action, rules);
  if (!canonical) return null;
  return rules.find((rule) => rule.action === canonical) || null;
}

// "THE SYSTEM DECIDED THIS, NOT A COLLEAGUE."
//
// Once the sweep starts writing, thousands of follow-up actions appear that
// nobody chose. A biller who cannot tell those apart from a colleague's
// judgement ends up trusting neither, and an untrusted work queue is the reason
// people go back to Excel.
//
// System-only actions identify themselves. Shared actions such as Insurance
// Follow-Up use the actor stamped by the authoritative service-line overlay.
function systemSetFollowUpMarkerHtml(action, updatedBy = "") {
  const rule = followUpRuleForAction(action);
  const automaticActor = String(updatedBy || "").startsWith("system:");
  if (!action || (!automaticActor && (!rule || !rule.systemOnly))) return "";
  return '<span class="subtext" title="Set automatically by the follow-up engine. Payment cleanup follows this action’s retention rule.">Auto</span>';
}

// The same fact in the dropdown, where a biller is deciding whether to change it.
function followUpOptionLabel(action) {
  const rule = followUpRuleForAction(action);
  return rule && rule.systemOnly ? `${action} (set by the system)` : action;
}

function refreshActionControls() {
  const currentFilter = getValue(els.actionFilter, "All");
  const currentBulk = getValue(els.bulkAction);
  if (els.actionFilter) els.actionFilter.innerHTML = '<option value="All">All</option>';
  if (els.bulkAction) els.bulkAction.innerHTML = '<option value="">Set action...</option>';
  // Filter lists the WHOLE matrix (including system-added actions, so a biller can filter to
  // that work); bulk-apply only offers what a human may set.
  replaceSelectOptions(els.actionFilter, getActionRules().map((rule) => rule.action));
  getSelectableActionRules().forEach((rule) => {
    if (els.bulkAction) {
      const bulkOption = document.createElement("option");
      bulkOption.value = rule.action;
      bulkOption.textContent = followUpActionChoiceLabel(rule.action);
      els.bulkAction.appendChild(bulkOption);
    }
  });
  if (els.bulkAction) {
    // The backend rejects empty actions, so clearing rides the explicit
    // "None" sentinel exposed here as a labelled Clear follow-up choice.
    const clearSeparator = document.createElement("option");
    clearSeparator.disabled = true;
    clearSeparator.textContent = "────────";
    els.bulkAction.appendChild(clearSeparator);
    const clearOption = document.createElement("option");
    clearOption.value = "None";
    clearOption.textContent = "— Clear follow-up";
    els.bulkAction.appendChild(clearOption);
  }
  if (els.actionFilter) {
    els.actionFilter.value = Array.from(els.actionFilter.options).some((option) => option.value === currentFilter) ? currentFilter : "All";
  }
  if (els.bulkAction) {
    els.bulkAction.value = Array.from(els.bulkAction.options).some((option) => option.value === currentBulk) ? currentBulk : "";
  }
  renderSlicers();
  renderClaimsTable();
}

function ensureSlicerState(value) {
  const next = structuredClone(emptySlicers);
  if (!value || typeof value !== "object") return next;
  Object.keys(next).forEach((key) => {
    const stored = Array.isArray(value[key]) ? value[key].filter(Boolean) : [];
    // Single funnel for persisted slicer state: the localStorage restore in loadState,
    // EVERY entry of state.savedOperationsViews (normalizeSavedOperationsViews ->
    // normalizeOperationsViewState), applyOperationsViewState and the state import — so
    // the retired "No Rate Found" selection and the legacy aging vocabulary are both
    // migrated on hydration, before the first render, even on code paths that never reach
    // normalizeSlicerSelections.
    next[key] = migrateSlicerSelectionValues(key, stored);
  });
  return next;
}

function ensureColumnFilterState(value) {
  if (!value || typeof value !== "object") return structuredClone(emptyColumnFilters);
  return Object.fromEntries(Object.entries(value)
    .filter(([, selected]) => Array.isArray(selected) && selected.length)
    .map(([key, selected]) => [key, selected.map(String)]));
}

// Also answers for a REPORT card id ("report:payer"), because every shared
// card control - the direction group's aria-label, the action bar's group name
// - names its filter through this one function. A card whose switch announced
// "report:payer filter direction" would be reading an internal key aloud.
function slicerLabel(key) {
  if (filterCardSurface(key) === "report") return reportFilterLabel(filterCardKey(key));
  const labels = {
    renderingProvider: "Rendering Provider",
    match: "Paid/Not Paid",
    claimDue: "Claim Due",
    followUpDue: "Follow-Up Due Status",
    primaryPayerClass: "Primary Payer Class",
    secondaryPayerClass: "Secondary Payer Class",
    aging: "Aging",
    action: "Follow up Action",
    primaryPayer: "Primary Payer",
    secondaryPayer: "Secondary Payer",
    facility: "Facility",
    procedure: "Procedure",
    claimStatus: "Claim Status",
    serviceMonth: "Service Month",
    patClassification: "Patient Classification",
    secondaryCalc: "Secondary Calc",
    claimClassification: "Claim Classification"
  };
  return labels[key] || key;
}

// Per-service-line past-due: same match gating as isPastDue(claim) but the
// due date comes from the service line's follow-up date, not the claim.
function serviceLinePastDue(followUpDate, service, claim) {
  if (!followUpDate) return false;
  const match = (service && (service.status || service.match)) || (claim && claim.match) || "";
  if (!["Under Paid", "Not Paid"].includes(match)) return false;
  const due = localDateFromDateOnly(followUpDate);
  return Boolean(due) && due <= todayStart();
}

function isPastDue(claim) {
  if (!claim.followUpDate) return false;
  if (!["Under Paid", "Not Paid"].includes(claim.match)) return false;
  const due = localDateFromDateOnly(claim.followUpDate);
  return Boolean(due) && due <= todayStart();
}

function todayDateString() {
  const today = todayStart();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// EASTERN business day, expressed as a browser-local midnight Date so it stays unit-compatible
// with localDateFromDateOnly(). Mirrors backend/tmsTime.js: America/New_York is the business
// clock, DST-aware via Intl (a fixed UTC-5 offset is WRONG half the year and is prohibited).
// Without this an offshore biller's browser (IST is already tomorrow all Eastern afternoon)
// defaulted follow-up dates and "past due" to the wrong business day.
// Built lazily (not as a module-level const) so no caller can hit a temporal-dead-zone error
// if it runs during script initialization.
var portalEasternDayFormatter = null; // eslint-disable-line no-var -- hoisted on purpose (no TDZ)

function easternDateOnlyString(instant = new Date()) {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (!Number.isFinite(date.getTime())) return "";
  if (!portalEasternDayFormatter) {
    portalEasternDayFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
  }
  // en-CA yields "YYYY-MM-DD".
  return portalEasternDayFormatter.format(date);
}

function todayStart(instant = new Date()) {
  const iso = easternDateOnlyString(instant);
  if (!iso) return new Date(NaN);
  return new Date(`${iso}T00:00:00`);
}

function workPriorityScore(claim) {
  return buildClaimStatusProfile(claim).priorityScore;
}

function priorityBand(score) {
  if (score >= 80) return "Critical";
  if (score >= 55) return "High";
  if (score >= 30) return "Routine";
  return "Monitor";
}

function workReason(claim) {
  return buildClaimStatusProfile(claim).workReason;
}

function buildClaimStatusProfile(claim) {
  const cache = claimCache(claim);
  if (cache.statusProfile) return cache.statusProfile;
  const guards = evaluateClaimCalculationGuards(claim);
  const financial = claimFinancialView(claim);
  const owed = financial.sourceOutstanding;
  // Lines the money engine closed as Paid with a residual (the $5 rule, or a learned
  // OON rate paid within 5%) do not keep the claim open.
  const toleranceResidual = moneyNumber((financial.serviceFinancials || []).reduce((sum, row) => sum + (row.finalizedMatch === "Paid" && row.sourceOutstanding > 0 ? row.sourceOutstanding : 0), 0));
  const paidWithinTolerance = owed > 0
    && (owed <= SMALL_BALANCE_PAID_TOLERANCE || (toleranceResidual > 0 && moneyNumber(owed - toleranceResidual) <= SMALL_BALANCE_PAID_TOLERANCE))
    && financial.totalInsurancePaid > 0;
  const paidClosed = (owed <= 0 || paidWithinTolerance)
    && (/paid|closed/i.test(String(claim?.claimStatus || "")) || /paid|over paid/i.test(String(claim?.match || "")) || paidWithinTolerance);
  // Paid-tolerance classification closes workflow only. sourceOutstanding keeps
  // the exact residual dollars so reporting remains auditable.
  const openBalance = owed > 0 && !paidClosed;
  const denied = !paidClosed && isDeniedClaim(claim);
  const clientAction = !paidClosed && needsClientAction(claim);
  const secondaryWork = !paidClosed && hasSecondaryWork(claim);
  const waitingPrimary = !paidClosed && isWaitingOnPrimary(claim);
  const waitingSecondary = !paidClosed && isWaitingOnSecondary(claim);
  const partialPaid = !paidClosed && isPartiallyPaidClaim(claim);
  const unpaid = !paidClosed && isUnpaidClaim(claim);
  const missingResponse = !paidClosed && missingPayerResponse(claim);
  const stale = !paidClosed && isStaleClaim(claim);
  const missingInfo = !paidClosed && hasMissingOperationalInfo(claim);
  const highDollar = openBalance && owed >= 1000;
  // D1 — A WRITTEN-OFF LINE IS BAD DEBT, NOT AR.
  //
  // Standing owner rule: we no longer expect this balance, so the line must
  // leave outstanding AR, aging, and every collectable work queue. Before this,
  // flagging "Write-Off" wrote the action and nothing else — the stale
  // follow-up date stayed, isPastDue never looked at the action, and
  // claimQueueKeys gated only on `owed > 0`. So a biller re-triaged the same
  // dead line every day, sometimes with a LOUDER past-due badge than before
  // they wrote it off.
  //
  // Deliberately reuses hasWriteOffWorkflow rather than inventing a second
  // recognizer — it already reads the workflow snapshot and both action fields,
  // and a second definition of "is this written off" is exactly the second
  // money engine this repo refuses.
  //
  // This changes CLASSIFICATION only. No dollar figure moves: sourceOutstanding
  // and writeOffAmount are untouched, so the money surfaces (and the Client
  // Home written-off bucket) still report the same numbers.
  // ⚠️ THE `owed <= 0` GATE IS THE SAFETY PROPERTY. Do not remove it.
  //
  // `hasWriteOffWorkflow` alone OVER-matches: its first arm is
  // `Boolean(workflow.writeOffSnapshot)`, and nothing in the app or the backend
  // write guard ever CLEARS that snapshot, while a later follow-up save is free
  // to move the action back to "Insurance Follow-Up". Both money engines key off
  // the action fields, not the snapshot — so `writtenOff && writeOffAmount === 0
  // && sourceOutstanding > 0` is reachable. Without this gate that claim would
  // be pulled out of every collectable queue, scored 0, and never past due,
  // while its dollars sat in NO bucket at all: not collectable, and not in the
  // Written Off figure either. That is silently deleting live AR.
  //
  // A genuine write-off drives the balance to `0 - paid`, so `owed <= 0` is the
  // money engine's own consequence of one. Gating on it makes classification
  // structurally unable to contradict the money, and it stays correct if a lane
  // later ships a PARTIAL stored write-off whose remainder must stay workable.
  //
  // `isWriteoffAction` is OR'd in to close the mirror gap: it strips hyphens and
  // matches WRITE + OFF, so documented imported spellings ("Write - Off
  // Approved", "WRITE - OFF - PR BALANCE") have their dollars written off but
  // were not being classified as bad debt, and kept resurfacing.
  const writtenOff = (hasWriteOffWorkflow(claim) || isWriteoffAction(claim.action)) && owed <= 0;
  const pastDue = !writtenOff && !paidClosed && isPastDue(claim);
  const lifecycle = claimLifecycleStage({
    paidClosed,
    denied,
    clientAction,
    waitingSecondary,
    secondaryWork,
    waitingPrimary,
    partialPaid,
    unpaid,
    missingResponse,
    openBalance,
    stale
  });
  const queueKeys = claimQueueKeys({
    denied,
    unpaid,
    partialPaid,
    clientAction,
    stale,
    highDollar,
    missingResponse,
    missingInfo,
    waitingPrimary,
    waitingSecondary,
    secondaryWork,
    writtenOff,
    openBalance
  });
  // A dead line does not compete for a biller's attention. Scoring it on `owed`
  // is what kept it near the top of a money-ranked queue.
  const priorityScore = (writtenOff || paidClosed) ? 0 : claimPriorityScore({
    owed,
    pastDue,
    denied,
    clientAction,
    waitingSecondary,
    waitingPrimary,
    partialPaid,
    unpaid,
    stale,
    highDollar,
    missingResponse,
    missingInfo,
    guards,
    aging: agingDays(claim)
  });
  cache.statusProfile = {
    lifecycleStage: lifecycle.key,
    lifecycleLabel: lifecycle.label,
    lifecycleRank: lifecycle.rank,
    statusCategory: lifecycle.category,
    priorityScore,
    priorityBand: priorityBand(priorityScore),
    workReason: claimWorkReasons({ pastDue, denied, partialPaid, unpaid, waitingPrimary, waitingSecondary, secondaryWork, clientAction, stale, highDollar, missingResponse, missingInfo, guards }),
    recommendedNextAction: recommendedNextAction({ paidClosed, denied, clientAction, waitingSecondary, waitingPrimary, partialPaid, unpaid, missingResponse, stale, highDollar, missingInfo }),
    queueKeys,
    flags: { openBalance, paidClosed, denied, clientAction, secondaryWork, waitingPrimary, waitingSecondary, partialPaid, unpaid, missingPayerResponse: missingResponse, stale, highDollar, missingInfo, pastDue, guarded: guards.length > 0 },
    slaBucket: paidClosed ? "No Date" : claimSlaBucket(claim),
    guardCodes: guards.map((guard) => guard.code)
  };
  return cache.statusProfile;
}

function claimLifecycleStage(flags) {
  if (flags.paidClosed) return { key: "paidClosed", label: "Paid / Closed", category: "closed", rank: 0 };
  if (flags.waitingSecondary || flags.secondaryWork) return { key: "secondaryFollowUp", label: "Secondary Follow-Up", category: "secondary", rank: 82 };
  if (flags.denied) return { key: "denialAppeal", label: "Denial / Appeal", category: "denied", rank: 95 };
  if (flags.clientAction) return { key: "clientAction", label: "Client / Provider Action", category: "external_action", rank: 90 };
  if (flags.waitingPrimary) return { key: "primaryFollowUp", label: "Primary Follow-Up", category: "payer_follow_up", rank: 78 };
  if (flags.unpaid || flags.missingResponse) return { key: "unresolvedNoResponse", label: "Unresolved / No Response", category: "unresolved", rank: 70 };
  if (flags.partialPaid) return { key: "partialPayment", label: "Partial / Underpaid", category: "partial", rank: 74 };
  if (flags.openBalance || flags.stale) return { key: "payerFollowUp", label: "Payer Follow-Up", category: "payer_follow_up", rank: 64 };
  return { key: "monitor", label: "Monitor", category: "monitor", rank: 20 };
}

function claimQueueKeys(flags) {
  const queues = new Set();
  // D1 — bad debt leaves every collectable queue, and it leaves them HERE, in
  // the one place queue membership is decided, rather than by adding an
  // `&& !writtenOff` to each of the fourteen lines below. It gets its own key so
  // the amount stays findable and auditable — written off is not deleted, it is
  // reclassified, which is what the four-bucket rule in
  // REPORTING_TRUTH_CONTRACT.md asks for.
  if (flags.writtenOff) {
    queues.add("writtenOff");
    return Array.from(queues);
  }
  if (flags.openBalance && (flags.denied || flags.partialPaid || flags.unpaid || flags.highDollar || flags.stale || flags.missingResponse || flags.clientAction || flags.waitingPrimary || flags.waitingSecondary)) queues.add("workFirst");
  if (flags.denied) queues.add("denied");
  if (flags.unpaid) queues.add("unpaid");
  if (flags.partialPaid) queues.add("partial");
  if (flags.openBalance && !flags.clientAction && (flags.missingResponse || flags.denied || flags.partialPaid || flags.unpaid || flags.waitingPrimary || flags.waitingSecondary)) queues.add("payerFollowUp");
  if (flags.waitingPrimary) queues.add("waitingPrimary");
  if (flags.secondaryWork) queues.add("secondary");
  if (flags.waitingSecondary) queues.add("waitingSecondary");
  if (flags.clientAction) queues.add("clientAction");
  if (flags.stale) queues.add("stale");
  if (flags.highDollar) queues.add("highDollar");
  if (flags.missingResponse) queues.add("missingPayerResponse");
  if (flags.missingInfo) queues.add("missingInfo");
  return Array.from(queues);
}

function claimPriorityScore({ owed, pastDue, denied, clientAction, waitingSecondary, waitingPrimary, partialPaid, unpaid, stale, highDollar, missingResponse, missingInfo, guards, aging }) {
  let score = 0;
  if (owed >= 5000) score += 35;
  else if (owed >= 1000) score += 24;
  else if (owed > 0) score += 10;
  if (pastDue) score += 30;
  if (denied) score += 28;
  if (clientAction) score += 22;
  if (partialPaid) score += 24;
  if (unpaid) score += 18;
  if (waitingSecondary) score += 18;
  if (waitingPrimary) score += 15;
  if (aging >= 120) score += 25;
  else if (aging >= 90) score += 18;
  else if (aging >= 60) score += 10;
  if (stale) score += 16;
  if (missingResponse) score += 12;
  if (missingInfo) score += 10;
  if (highDollar) score += 8;
  if (guards.length) score += 8;
  return Math.min(score, 100);
}

function claimWorkReasons(flags) {
  // Positive framing per PORTAL_DIRECTIVES — describe what TMS is doing
  // about the claim, not the situation alone.
  const reasons = [];
  if (flags.pastDue) reasons.push("Active follow-up");
  if (flags.denied) reasons.push("Denial/appeal");
  if (flags.partialPaid) reasons.push("Underpayment recovery");
  if (flags.unpaid) reasons.push("Unpaid");
  if (flags.waitingPrimary) reasons.push("Waiting primary");
  if (flags.waitingSecondary) reasons.push("Waiting secondary");
  else if (flags.secondaryWork) reasons.push("Secondary");
  if (flags.clientAction) reasons.push("Client/provider action");
  if (flags.stale) reasons.push("No recent touch");
  if (flags.highDollar) reasons.push("High dollar");
  if (flags.missingResponse) reasons.push("Missing payer response");
  if (flags.missingInfo) reasons.push("Missing info");
  if (flags.guards.length) reasons.push("Calculation issue");
  return reasons.slice(0, 4).join(" / ") || "Monitor";
}

function recommendedNextAction(flags) {
  if (flags.paidClosed) return "Monitor only";
  if (flags.waitingSecondary) return "Verify secondary submission and payer response";
  if (flags.denied) return "Review denial, appeal/correct, and set payer follow-up";
  if (flags.clientAction) return "Request authorization, COB, documentation, or provider action";
  if (flags.waitingPrimary) return "Follow primary payer for response or first payment";
  if (flags.unpaid || flags.missingResponse) return "Check payer portal/EOB and document response";
  if (flags.partialPaid) return "Validate expected reimbursement and pursue underpayment";
  if (flags.stale) return "Touch claim and reset next follow-up date";
  if (flags.highDollar) return "Escalate high-dollar AR review";
  if (flags.missingInfo) return "Complete missing status or payer-response fields";
  return "Continue monitoring";
}

function evaluateClaimCalculationGuards(claim, service = null) {
  if (!service) {
    const cache = claimCache(claim);
    if (cache.calculationGuards) return cache.calculationGuards;
    cache.calculationGuards = calculateClaimGuards(claim, service);
    return cache.calculationGuards;
  }
  const cache = serviceCache(service);
  if (cache.calculationGuards) return cache.calculationGuards;
  cache.calculationGuards = calculateClaimGuards(claim, service);
  return cache.calculationGuards;
}

function calculateClaimGuards(claim, service = null) {
  if (!service && claim.servicePage?.complete && claim.preparedGuardCounts) {
    const definitions = [["expectedIncompleteLines","EXPECTED_REIMBURSEMENT_INCOMPLETE","blocker","Expected reimbursement incomplete"],["adjustmentDetailMissingLines","ADJUSTMENT_DETAIL_MISSING","review","Adjustment detail missing"],["prReasonDetailMissingLines","PR_REASON_DETAIL_MISSING","blocker","PR reason detail missing"],["payerConflictLines","PAYER_SEQUENCE_REVIEW","review","Payer sequencing review"]];
    return definitions.filter(([key])=>Number(claim.preparedGuardCounts[key])>0).map(([key,code,severity,label])=>({code,severity,label,lineCount:Number(claim.preparedGuardCounts[key])}));
  }
  const guards = [];
  const services = service ? [service] : (claim.services || []);
  services.forEach((item) => {
    guards.push(...evaluateServiceCalculationGuards(claim, item));
  });
  if (sameExternalPayerConflict(claim)) {
    guards.push({
      code: "PAYER_SEQUENCE_REVIEW",
      severity: "review",
      label: "Payer sequencing review"
    });
  }
  return dedupeCalculationGuards(guards);
}

function evaluateServiceCalculationGuards(claim, service) {
  const guards = [];
  const financial = serviceFinancialView(service, claim);
  const expected = financial.expectedReimbursement;
  const owed = financial.sourceOutstanding;
  const patientBalance = moneyNumber(service?.patientBalance || 0);
  if (expected <= 0 || /no rate found/i.test(String(service?.status || claim?.match || ""))) {
    guards.push({
      code: "EXPECTED_REIMBURSEMENT_INCOMPLETE",
      severity: "blocker",
      label: "Expected reimbursement incomplete"
    });
  }
  if (owed > 0 && adjustmentRows(service).length === 0
      && !hasPresent(firstPresent(service?.prAdjustmentTotal, service?.pr_adjustment_total, service?.["PR Adjustment Total"]))) {
    guards.push({
      code: "ADJUSTMENT_DETAIL_MISSING",
      severity: "review",
      label: "Adjustment detail missing"
    });
  }
  if (patientBalance > 0 && truePatientResponsibility(service) <= 0) {
    guards.push({
      code: "PR_REASON_DETAIL_MISSING",
      severity: "blocker",
      label: "PR reason detail missing"
    });
  }
  return guards;
}

function sameExternalPayerConflict(claim) {
  const primary = String(claim?.primaryExternalId || "").trim();
  const secondary = String(claim?.secondaryExternalId || "").trim();
  return Boolean(primary && secondary && primary === secondary)
    || /manual review|same external/i.test(String(claim?.paymentAttribution?.status || ""));
}

function calculationGuardSummary(guards) {
  const values = dedupeCalculationGuards(guards);
  return values.length ? values.map((guard) => guard.label).join(" / ") : "Clear";
}

function dedupeCalculationGuards(guards) {
  const seen = new Set();
  return (guards || []).filter((guard) => {
    if (seen.has(guard.code)) return false;
    seen.add(guard.code);
    return true;
  });
}


// DENIALS CANNOT DETECT WHAT IT CANNOT READ (2026-08-10).
//
// isDeniedClaim() is a text match over claimStatus / claimClassification /
// action / reviewNote / the latest note / each service line's status+notes. On
// the live reporting lane every one of those fields arrives EMPTY, so the
// report returns 0 of ~21,932 claims every single time - and the screen said
// "No denial-flagged claims in the current filters", which a biller reads as
// "this book has no denials". Those are opposite facts with opposite next
// actions: one means nothing to work, the other means the detector is blind.
//
// So the copy is DERIVED FROM THE ROWS, never asserted. If not one claim in the
// filtered set carries any field the rule reads, the emptiness is missing data
// and the screen says exactly that. The day those fields start arriving this
// reverts to the plain wording by itself - there is no flag to remember to flip.
// claimStatus is DELIBERATELY NOT COUNTED as denial signal, and that exclusion
// is the difference between this helper working and not.
//
// Measured on the live reporting view (all 209,216 rows): claim_status is
// populated on 100% of them and matches the denial pattern on ZERO, because it
// only ever carries four workflow values - Submitted (207,295), On Hold (1,357),
// Ready to Submit (526), Other (38). None of them can ever mean "denied". So a
// populated status proves nothing about whether the detector can SEE a denial,
// and counting it would have flipped every live screen back to the plain
// "no denial-flagged claims" wording this whole helper exists to replace.
//
// The discriminating fields are the ones below: classification, follow-up
// action, review note, the latest claim note and the service-line statuses.
// Those are the ones measured empty on every row (follow_up_action: 0 of
// 209,216), and the view's own `denied` work queue is a measured 0 to match.
function denialDetectionSignalCount(claims) {
  return (claims || []).reduce((count, claim) => {
    const signal = [
      claim.claimClassification,
      claim.action,
      claim.reviewNote,
      claim.latestClaimNoteText,
      claim.claimNoteSummary?.text,
      claim.notes?.at(-1)?.text,
      ...(claim.services || []).map((service) => `${service.status || ""} ${service.latestClaimNoteText || service.latestClaimNote || ""}`)
    ].join("").trim();
    return signal ? count + 1 : count;
  }, 0);
}

function denialsEmptyMessage(claims) {
  const total = (claims || []).length;
  if (!total) return "No claims in the current filters, so there is nothing to check for denials. Widen the filters to load claims.";
  if (denialDetectionSignalCount(claims) > 0) {
    return "No denial-flagged claims in the current filters. Claims are flagged automatically from their status, follow-up actions, and notes.";
  }
  return `This does NOT mean there are no denials. A denial is detected from a claim's classification, follow-up action, review note or claim notes, and all `
    + `${formatCount(total)} claims in this filter arrived with every one of those fields empty - so nothing here can be flagged either way. `
    + "The detector is blind on this data, not clear. Work denials from the payer's own remits until this reporting lane carries those fields.";
}

function isDeniedClaim(claim) {
  const text = [
    claim.claimStatus,
    claim.claimClassification,
    claim.action,
    claim.reviewNote,
    claim.latestClaimNoteText,
    claim.claimNoteSummary?.text,
    claim.notes?.at(-1)?.text,
    ...(claim.services || []).map((service) => `${service.status || ""} ${service.latestClaimNoteText || service.latestClaimNote || ""}`)
  ].join(" ").toLowerCase();
  return /denied|denial|reject|appeal|correct/.test(text);
}

function isPartiallyPaidClaim(claim) {
  const financial = claimFinancialView(claim);
  return financial.sourceOutstanding > 0
    && (/under paid|underpaid|partial/i.test(claimOperationalText(claim))
      || (financial.totalInsurancePaid > 0 && financial.totalInsurancePaid < financial.expectedReimbursement));
}

function isUnpaidClaim(claim) {
  const financial = claimFinancialView(claim);
  return financial.sourceOutstanding > 0
    && (claim.match === "Not Paid" || financial.totalInsurancePaid <= 0);
}

function hasSecondaryWork(claim) {
  const secondary = String(claim.secondaryPayer || "").trim().toLowerCase();
  const hasSecondaryPayer = Boolean(secondary && secondary !== "none" && secondary !== "n/a");
  return /secondary/i.test(String(claim.action || ""))
    || /secondary/i.test(String(claim.reviewNote || ""))
    || (hasSecondaryPayer && /secondary/i.test(claimOperationalText(claim)));
}

function needsPayerFollowUp(claim) {
  if (claimFinancialView(claim).sourceOutstanding <= 0) return false;
  if (needsClientAction(claim)) return false;
  const text = claimOperationalText(claim);
  return /payer|portal|eob|era|response|follow.?up|no payment|not paid|under paid|underpaid|denied|reject|appeal|secondary/.test(text);
}

function isWaitingOnPrimary(claim) {
  const financial = claimFinancialView(claim);
  if (financial.sourceOutstanding <= 0) return false;
  const primaryPayer = String(claim.primaryPayer || "").trim().toLowerCase();
  if (!primaryPayer || primaryPayer === "none" || primaryPayer === "n/a") return false;
  return financial.primaryPaid <= 0 && financial.totalInsurancePaid <= 0 && !hasSecondaryWork(claim);
}

function isWaitingOnSecondary(claim) {
  const financial = claimFinancialView(claim);
  if (financial.sourceOutstanding <= 0) return false;
  if (!hasSecondaryWork(claim)) return false;
  return financial.secondaryPaid <= 0;
}

function missingPayerResponse(claim) {
  if (claimFinancialView(claim).sourceOutstanding <= 0) return false;
  const text = claimOperationalText(claim);
  return !/paid|denied|deny|reject|appeal|eob|era|response|processed|primary matched|manual review/.test(text)
    || /no payment yet|needs payer|payer\/status review|missing payer response/.test(text);
}

function claimSlaBucket(claim) {
  if (!claim.followUpDate) return "No Date";
  const due = localDateFromDateOnly(claim.followUpDate);
  if (!due) return "No Date";
  const days = Math.ceil((due - todayStart()) / 86400000);
  if (days < 0) return "Overdue";
  if (days === 0) return "Due Today";
  if (days <= 7) return "Due 1-7";
  if (days <= 14) return "Due 8-14";
  return "Due 15+";
}

function claimOperationalText(claim) {
  const cache = claimCache(claim);
  if (cache.operationalText) return cache.operationalText;
  cache.operationalText = [
    claim.claimStatus,
    claim.claimClassification,
    claim.action,
    claim.externalPayerStatus,
    claim.reviewNote,
    claim.paymentAttribution?.status,
    claim.latestClaimNoteText,
    claim.claimNoteSummary?.text,
    claim.notes?.at(-1)?.text,
    ...(claim.services || []).map((service) => `${service.status || ""} ${service.latestClaimNoteText || service.latestClaimNote || ""}`)
  ].join(" ").toLowerCase();
  return cache.operationalText;
}

function needsClientAction(claim) {
  const text = claimOperationalText(claim);
  return text.includes("client action")
    || /auth|authorization|coverage|cob|credential|provider|documentation|medical record/.test(text);
}

function isStaleClaim(claim) {
  if (claimFinancialView(claim).sourceOutstanding <= 0) return false;
  if (claim.assignmentStatus === "Submitted For Review") return false;
  const notes = Array.isArray(claim.notes) ? claim.notes : [];
  if (!notes.length) return true;
  const touchedAt = lastTouchedDate(claim);
  if (!touchedAt) return true;
  const ageDays = Math.floor((todayStart() - touchedAt) / 86400000);
  return ageDays >= 14;
}

function hasMissingOperationalInfo(claim) {
  const status = String(claim.claimStatus || "").trim().toLowerCase();
  const externalStatus = String(claim.externalPayerStatus || "").toLowerCase();
  return !status
    || status === "n/a"
    || status === "unknown"
    || !claim.followUpDate
    || externalStatus.includes("needs payer translator")
    || externalStatus.includes("manual review");
}

// Coarse day estimate when a row carries only a bucket label. Resolves through the
// canonical bands, so the label is read as its band FLOOR: "121+" (and its legacy
// spellings) now estimate 121, not 120 — 120 used to round a 121+ claim back down into
// the 91-120 band on any surface that re-bucketed this value.
function agingDays(claim) {
  const band = CT_AR_AGING_BUCKETS.find((bucket) => bucket.key === normalizeAgingBucketLabel(claim.aging));
  if (band) return band.min;
  const match = String(claim.aging || "").match(/(\d+)/);
  return match ? Number(match[1]) : Number(claim.billDateAging || 0);
}

function lastTouchedDate(claim) {
  const notes = Array.isArray(claim.notes) ? claim.notes : [];
  const latest = notes.at(-1)?.at;
  if (!latest) return null;
  const parsed = strictDateTimeMillis(latest);
  if (!Number.isFinite(parsed)) return null;
  const date = new Date(parsed);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Display names for the three LEGACY role aliases, used as the actor stamp on
// instructions, assignments, audit rows and note authorship. Every value here is
// a ROLE, never a person: the Manager arm used to return a real first name, and
// this file also publishes that person's initials in NOTE_AUTHOR_DIRECTORY, so an
// anonymous reader of /app.js could join the two halves back into the identity
// that f7cd8ce moved behind an authenticated endpoint. (Found by portal-guardian
// on the roster removal - the full name was never here, which is exactly why the
// name-substring check did not see it.)
function activeInternalUser() {
  if (isExternalClientRole(state.role)) return "Client User";
  if (state.role === "Biller") return "Biller CT";
  if (state.role === "Manager") return "Manager CT";
  if (state.role === "Offshore") return "Offshore Team A";
  return state.activeUserName || "Platform Admin";
}

// Client-mode UI gate. This read state.role RAW — no canonicalRole — so a
// legacy alias (Provider, ReadOnly) that canonicalRole maps to Client answered
// "not a client" here and got the internal view of these surfaces.
// isExternalClientRole fixes that pre-existing bug and covers ClientBiller in
// the same move.
function isClientRole() {
  return isExternalClientRole(state?.role);
}

function matchTone(match) {
  if (match === "Paid" || match === "Over Paid") return "green";
  if (match === "Under Paid") return "amber";
  // Both rate-cleanup buckets read blue: it is a fee-schedule task, not a payer denial.
  if (isNoRateMatch(match)) return "blue";
  return "red";
}

function statusTone(status) {
  if (status === "Approved" || status === "Closed") return "green";
  if (status === "Submitted For Review") return "amber";
  if (status === "Bounced Back") return "red";
  if (status === "Assigned" || status === "In Progress") return "blue";
  return "gray";
}

function paymentSummary(claim) {
  const attribution = claim.paymentAttribution || {};
  return `
    <span>${escapeHtml(claimAttributionStatusLabel(claim))}</span>
    <span class="subtext">P ${money(attribution.primaryPaid || 0)} / S ${money(attribution.secondaryPaid || 0)} / U ${money(attribution.unmatchedPaid || 0)}</span>
  `;
}

function lifeHistoryCoverageText(lifeHistory = {}) {
  const unavailable = Array.isArray(lifeHistory.unavailableSources) ? lifeHistory.unavailableSources : [];
  const truncated = Array.isArray(lifeHistory.truncatedSources) ? lifeHistory.truncatedSources : [];
  const incomplete = unavailable.length > 0 || truncated.length > 0 || lifeHistory.renderLimitReached === true;
  const boundary = lifeHistory.coverage?.fullyCoveredFromUtc || lifeHistory.coverage?.historyBeginsAtUtc || "";
  const parts = [];
  if (boundary && !incomplete) parts.push(`Captured history is complete only from ${formatDate(boundary)} onward`);
  else if (boundary) parts.push(`Captured history begins ${formatDate(boundary)}, but this view is incomplete`);
  else parts.push("The portal cannot prove a complete pre-portal history boundary");
  if (unavailable.length) parts.push(`${unavailable.length} audit source${unavailable.length === 1 ? " is" : "s are"} temporarily unavailable`);
  else parts.push("all configured durable audit sources answered this request");
  if (truncated.length) parts.push(`${truncated.length} source${truncated.length === 1 ? " reached" : "s reached"} its safety limit`);
  if (lifeHistory.renderLimitReached === true) parts.push("older events continue beyond the visible timeline");
  return `${parts.join("; ")}.`;
}

function paymentRows(claim) {
  if (!Array.isArray(claim.payments) || claim.payments.length === 0) {
    return `<tr><td colspan="7">${claim.paymentEventsUnavailable ? "Payment-event evidence is temporarily unavailable; aggregate attribution remains above." : "No payment events found for this claim."}</td></tr>`;
  }
  return claim.payments.map((payment) => `
    <tr>
      <td>${escapeHtml(payment.eventType === "signed-negative-payment-event" ? `${payment.source} · signed negative` : payment.source)}</td>
      <td>${escapeHtml(payment.externalPayerId || "N/A")}</td>
      <td>${formatDate(payment.date)}</td>
      <td>${escapeHtml(payment.ref || "")}</td>
      <td>${payment.applied == null ? "Unavailable" : money(payment.applied)}</td>
      <td>${payment.sequence || ""}</td>
      <td>${escapeHtml(payment.attributionConflict ? "Unmatched · same payer ID conflict" : (payment.attributedTo || "Unmatched"))}</td>
    </tr>
  `).join("");
}

function adjustmentRows(service) {
  return Array.isArray(service?.adjustments) ? service.adjustments : [];
}

function isTruePatientResponsibilityAdjustment(adjustment) {
  const group = String(adjustment?.group || "").toUpperCase().replace(/\s+/g, "");
  const code = String(adjustment?.reasonCode || adjustment?.code || "").replace(/\D/g, "");
  return group === "PR" && ["1", "2", "3"].includes(code);
}

function truePatientResponsibility(service) {
  const detailedTotal = adjustmentRows(service)
    .filter(isTruePatientResponsibilityAdjustment)
    .reduce((sum, adjustment) => sum + moneyNumber(adjustment.amount || 0), 0);
  if (detailedTotal > 0) return moneyNumber(detailedTotal);
  const scalar = firstPresent(
    service?.prAdjustmentTotal,
    service?.pr_adjustment_total,
    service?.["PR Adjustment Total"]
  );
  return scalar == null ? 0 : moneyNumber(scalar);
}

function adjustmentSummary(service) {
  return adjustmentRows(service).reduce((sum, adjustment) => {
    const amount = moneyNumber(adjustment.amount || 0);
    const group = String(adjustment.group || "").toUpperCase().replace(/\s+/g, "");
    if (group === "PR") {
      sum.patientAdjustments += amount;
      if (isTruePatientResponsibilityAdjustment(adjustment)) sum.truePatientResponsibility += amount;
    } else if (group === "CO") {
      sum.contractualAdjustments += amount;
    } else if (group === "OA" || group === "PI") {
      sum.otherPayerAdjustments += amount;
    } else {
      sum.unclassifiedAdjustments += amount;
    }
    sum.totalAdjustments += amount;
    return sum;
  }, {
    totalAdjustments: 0,
    contractualAdjustments: 0,
    patientAdjustments: 0,
    truePatientResponsibility: 0,
    otherPayerAdjustments: 0,
    unclassifiedAdjustments: 0
  });
}

const trackerSecondaryWorkflowClassifications = new Set([
  "SUBMITTED TO SECONDARY",
  "READY TO BILL TO SECONDARY",
  "SECONDARY PLACEHOLDER"
]);
const trackerSecondarySubmittedClassificationPatterns = [
  /\bSUBMITTED TO SEC(?:ONDARY)?\b/,
  /\bREADY TO BILL (?:TO )?SEC(?:ONDARY)?\b/,
  /\bREADY TO SUBMIT (?:TO )?SEC(?:ONDARY)?\b/,
  /\bSEC(?:ONDARY)? PLACE ?HOLDER\b/,
  /^SECONDARY AUTH\b/
];

// Browser mirror of financialCalculationEngine.js classificationIndicatesSecondarySubmitted.
// EZClaim emits both full and abbreviated workflow labels; NOT/NEVER variants
// are pre-submission evidence and must stay outside the secondary calculation.
function trackerClassificationIndicatesSecondarySubmitted(rawClassification) {
  if (!hasPresent(rawClassification)) return false;
  const normalized = normalizeTrackerText(rawClassification).replace(/\.$/, "");
  if (!normalized || /\b(?:NOT|NEVER|DO NOT)\b/.test(normalized)) return false;
  return trackerSecondarySubmittedClassificationPatterns.some((pattern) => pattern.test(normalized));
}
const trackerMedicaidMarkers = [
  "MEDICAID",
  "TENNCARE",
  "PEACHSTATE",
  "CARE SOURCE",
  "CARESOURCE",
  "AMERIGROUP",
  "WELLPOINT",
  "HEALTHKEEPERS",
  "BETTER HEALTH"
];

function serviceFinancialView(service = {}, claim = {}) {
  if (service.followUpReview?.status === "pending") {
    const action = service.followUpReview.approvedAction || "";
    service = { ...service, action, followUpAction: action };
    claim = { ...claim, action };
  }
  if (service.preparedFinancials && claim.servicePage?.complete) return service.preparedFinancials;
  if (service.preparedPatientFinancials && claim.servicePage?.complete) return {...service.preparedPatientFinancials};
  const cache = serviceCache(service);
  if (cache.financialView) return cache.financialView;
  const sourceExpected = moneyNumber(firstPresent(
    service.finalExpectedTotalNumeric,
    service["Final Expected Total Numeric"],
    service.totalExpected,
    service.expected,
    0
  ));
  const insurancePaid = moneyNumber(firstPresent(
    service.finalInsurancePaid,
    service.totalInsAmtPaid,
    service["Total Ins Amt Paid"],
    service.paid,
    0
  ));
  const patientBalance = moneyNumber(firstPresent(service.patientBalance, service.totalPatBal, service["Total Pat Bal"], 0));
  const totalPatientPaid = moneyNumber(firstPresent(service.totalPatAmtPaid, service["Total Pat Amt Paid"], service.patientPaid, 0));
  const adjustments = adjustmentSummary(service);
  const tracker = trackerServiceFinancials(service, claim, {
    sourceExpected,
    insurancePaid,
    patientBalance,
    totalPatientPaid,
    truePatientResponsibility: adjustments.truePatientResponsibility
  });
  const patientResponsibility = tracker.patientResponsibilityTotal;
  const expectedPayerResponsibility = tracker.finalInsuranceExpected;
  const payerResponsibility = moneyNumber(Math.max(expectedPayerResponsibility - insurancePaid, 0));
  cache.financialView = {
    charges: moneyNumber(firstPresent(service.charges, service.chargeAmount, service["Charge Amount"], 0)),
    units: quantityNumber(firstPresent(service.units, service.Units, 0)),
    expectedReimbursement: tracker.finalInsuranceExpected,
    insurancePaid,
    primaryExpected: tracker.primaryExpectedTotal,
    secondaryExpected: tracker.secondaryExpectedTotal,
    secondaryAllowedTotal: tracker.secondaryAllowedTotal,
    secondaryCalc: tracker.secondaryCalc,
    staticTotalExpected: tracker.staticTotalExpected,
    finalExpectedTotalNumeric: tracker.finalExpectedTotalNumeric,
    finalSecondaryExpectedNumeric: tracker.finalSecondaryExpectedNumeric,
    secondaryExpectedChangeNumeric: tracker.secondaryExpectedChangeNumeric,
    totalExpectedChangeNumeric: tracker.totalExpectedChangeNumeric,
    sourceOutstanding: tracker.finalInsuranceBalance,
    expectedOutstanding: tracker.finalInsuranceBalance,
    expectedPayerResponsibility,
    payerResponsibility,
    patientResponsibility,
    patientBalance,
    positiveTotalPatientBalance: tracker.positivePatientBalance,
    writeOffAmount: tracker.writeOffAmount,
    finalInsuranceExpected: tracker.finalInsuranceExpected,
    finalInsurancePaid: insurancePaid,
    finalInsuranceBalance: tracker.finalInsuranceBalance,
    finalizedMatch: tracker.finalizedMatch,
    primaryRate: tracker.primaryRate,
    secondaryRate: tracker.secondaryRate,
    primaryRateEstimated: tracker.primaryRateEstimated,
    secondaryRateEstimated: tracker.secondaryRateEstimated,
    primaryRateMissing: tracker.primaryRateMissing,
    secondaryRateMissing: tracker.secondaryRateMissing,
    // The asserted-absence pair, which is what the CLAIM grain rolls up.
    primaryRateAbsent: tracker.primaryRateAbsent,
    secondaryRateAbsent: tracker.secondaryRateAbsent,
    hasMedicaidPayer: tracker.hasMedicaidPayer,
    underpayment: payerResponsibility,
    overpayment: moneyNumber(Math.max(insurancePaid - expectedPayerResponsibility, 0)),
    adjustments,
    claimId: claim.claimId || ""
  };
  return cache.financialView;
}

function claimFinancialView(claim = {}) {
  if (claim.preparedFinancialSummary && claim.servicePage?.complete) return {...claim.preparedFinancialSummary, serviceFinancials:(claim.services || []).map(service => serviceFinancialView(service,claim))};
  if (claim.servicePage?.complete && claim.preparedPrSummary?.complete) return {patientResponsibility:claim.preparedPrSummary.financialPatientResponsibility};
  const cache = claimCache(claim);
  if (cache.financialView) return cache.financialView;
  const serviceRows = (claim.services || []).map((service) => serviceFinancialView(service, claim));
  const serviceTotals = serviceRows.reduce((sum, row) => {
    sum.charges += row.charges;
    sum.expectedReimbursement += row.expectedReimbursement;
    sum.insurancePaid += row.insurancePaid;
    sum.primaryExpected += row.primaryExpected;
    sum.secondaryExpected += row.secondaryExpected;
    sum.finalInsuranceExpected += row.finalInsuranceExpected;
    sum.finalInsuranceBalance += row.finalInsuranceBalance;
    sum.writeOffAmount += row.writeOffAmount;
    sum.sourceOutstanding += row.sourceOutstanding;
    sum.payerResponsibility += row.payerResponsibility;
    sum.patientResponsibility += row.patientResponsibility;
    sum.underpayment += row.underpayment;
    sum.overpayment += row.overpayment;
    return sum;
  }, {
    charges: 0,
    expectedReimbursement: 0,
    insurancePaid: 0,
    primaryExpected: 0,
    secondaryExpected: 0,
    finalInsuranceExpected: 0,
    finalInsuranceBalance: 0,
    writeOffAmount: 0,
    sourceOutstanding: 0,
    payerResponsibility: 0,
    patientResponsibility: 0,
    underpayment: 0,
    overpayment: 0
  });
  Object.keys(serviceTotals).forEach((key) => { serviceTotals[key] = moneyNumber(serviceTotals[key]); });
  const attribution = claim.paymentAttribution || {};
  const derivedAttribution = trackerPaymentAttributionFinancials(claim);
  const primaryPaid = attribution.primaryPaid == null ? derivedAttribution.primaryPaid : moneyNumber(attribution.primaryPaid);
  const secondaryPaid = attribution.secondaryPaid == null ? derivedAttribution.secondaryPaid : moneyNumber(attribution.secondaryPaid);
  const unmatchedPaid = attribution.unmatchedPaid == null ? derivedAttribution.unmatchedPaid : moneyNumber(attribution.unmatchedPaid);
  const totalPaymentApplied = (claim.payments || []).length
    ? moneyNumber((claim.payments || []).reduce((sum, payment) => sum + moneyNumber(firstPresent(payment.applied, payment.amount, payment["Payment Amount"], 0)), 0))
    : moneyNumber(primaryPaid + secondaryPaid + unmatchedPaid);
  const expectedReimbursement = serviceTotals.expectedReimbursement;
  const totalInsurancePaid = moneyNumber(claim.totalPaid ?? serviceTotals.insurancePaid);
  // ADDITIVE ONLY — see the block comment above this function's callers.
  // Nothing here recomputes money, it only counts.
  //
  // MERGE DECISION (2026-08-04): this counted `primaryRateMissing` when it was
  // written, and the sibling claim-grain roll-up below now deliberately does
  // NOT. `primaryRateMissing` is a PROXY that also fires on
  // `sourceExpected <= 0`, which a SETTLED line trips while being perfectly
  // well known — rolling it up to the CLAIM grain hid $1,000 of real owed
  // behind one settled line (portal-guardian, 2026-08-04). `expectedKnown` is
  // a claim-grain judgement built on this count, so counting the proxy would
  // reintroduce that bug on a new path: a claim with one settled line would
  // read expected-UNKNOWN and its expected would render as a dash, hiding
  // money that is not missing at all. Counts asserted absence instead, so both
  // claim-grain signals answer the same question the same way.
  const unratedLineCount = serviceRows.reduce((count, row) => count + (row.primaryRateAbsent ? 1 : 0), 0);
  cache.financialView = {
    charges: serviceTotals.charges,
    expectedReimbursement,
    serviceExpectedReimbursement: serviceTotals.expectedReimbursement,
    primaryExpected: serviceTotals.primaryExpected,
    secondaryExpected: serviceTotals.secondaryExpected,
    finalInsuranceExpected: serviceTotals.finalInsuranceExpected,
    finalInsuranceBalance: serviceTotals.finalInsuranceBalance,
    writeOffAmount: serviceTotals.writeOffAmount,
    primaryPaid,
    secondaryPaid,
    unmatchedPaid,
    totalPaymentApplied,
    totalInsurancePaid,
    unappliedPayments: moneyNumber(Math.max(totalPaymentApplied - totalInsurancePaid, 0)),
    sourceOutstanding: serviceTotals.sourceOutstanding,
    expectedOutstanding: moneyNumber(Math.max(expectedReimbursement - totalInsurancePaid, 0)),
    payerResponsibility: serviceTotals.payerResponsibility,
    patientResponsibility: serviceTotals.patientResponsibility,
    underpayment: serviceTotals.payerResponsibility,
    overpayment: serviceTotals.overpayment,
    // --- expected-basis provenance (additive; nothing above changed) --------
    unratedLineCount,
    // A claim counts as expected-KNOWN only when NOT ONE of its lines is
    // unrated. Conservative on purpose: a partly-rated claim's expected (and
    // therefore its outstanding) is a floor pretending to be a total, and the
    // one thing this whole change exists to prevent is a floor being read as
    // a total.
    expectedKnown: serviceRows.length > 0 && unratedLineCount === 0,
    serviceFinancials: serviceRows,
    // MONEY CERTAINTY, CARRIED TO THE CLAIM GRAIN.
    //
    // 35cdf57 taught the SERVICE lane to say "no rate" instead of "$0", and it
    // read the truth off serviceFinancialView().primaryRateMissing. This reducer
    // summed thirteen numbers and forwarded none of the flags, so the identical
    // gate in claimCellHtml() - unknownMoneyReason(key, claimFinancialView(claim))
    // - could never fire: a claim-mode row has NEVER rendered money-unknown. The
    // claim lane printed a confident total with an unknowable line folded into it
    // while the service lane, one toggle away, printed a dash for the same money.
    // (Scope, stated honestly: renderTrackerApiRows -> trackerApiCellHtml serves
    // the LIVE grid, so claimCellHtml is the local/sample path today. The gate is
    // now correct in both rather than dead in one.)
    //
    // ANY line whose rate is ASSERTED absent makes the claim total unknowable,
    // because every total above is a SUM over the lines - one unpriced line does
    // not make the sum approximately right, it makes it a different number.
    // some(), not every(). It rolls up primaryRateABSENT rather than
    // primaryRateMissing: the latter also fires on `sourceExpected <= 0`, which a
    // SETTLED line trips while being perfectly well known - rolling that proxy up
    // hid $1,000 of real owed behind one such line. A claim with no service lines
    // has nothing missing and stays known.
    primaryRateEstimated: serviceRows.some((row) => row.primaryRateEstimated) && serviceRows.every((row) => !row.primaryRateAbsent || row.primaryRateEstimated),
    secondaryRateEstimated: serviceRows.some((row) => row.secondaryRateEstimated) && serviceRows.every((row) => !row.secondaryRateAbsent || row.secondaryRateEstimated),
    primaryRateMissing: serviceRows.some((row) => row.primaryRateAbsent),
    secondaryRateMissing: serviceRows.some((row) => row.secondaryRateAbsent),
    // Counts, so the dash can name the grain rather than repeat the service-line
    // sentence at a claim that has six lines and one gap (rateMissingGrainNote).
    primaryRateMissingLines: serviceRows.filter((row) => row.primaryRateAbsent).length,
    secondaryRateMissingLines: serviceRows.filter((row) => row.secondaryRateAbsent).length,
    lineCount: serviceRows.length
  };
  return cache.financialView;
}

// Mirror of backend/financialCalculationEngine.js authoritativePrAdjustmentTotal.
// Returns a NUMBER when the payer adjudicated this line and NULL when no
// adjudication detail reached us. Must not coerce absent to 0 — that is the
// difference between "the patient owes nothing" and "we do not know yet", and
// collapsing them moves every AR and collection-rate figure in the product.
function trackerAuthoritativePrTotal(service = {}) {
  const raw = firstPresent(
    service.prAdjustmentTotal,
    service.pr_adjustment_total,
    service["PR Adjustment Total"],
    null
  );
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  return Math.max(0, moneyNumber(value));
}

// Local-only mirror of the server's classification guard. Prepared/API money
// remains authoritative and never depends on the browser's loaded fee rows.
function trackerLocalClassificationRejected(service, claim, role) {
  const payer = firstPresent(service[role + "Payer"], claim[role + "Payer"]);
  const payerId = firstPresent(service[role + "PayerId"], service[role + "PayorId"], service[role + "ExternalId"], service[role + "PayerExternalId"], claim[role + "PayerId"], claim[role + "PayorId"], claim[role + "ExternalId"], claim[role + "PayerExternalId"]);
  if (!hasRealPayer(payer) && !hasPresent(payerId)) return false;
  const classification = firstPresent(service.state, claim.state);
  if (!normalizedContractText(classification)) return true;
  const orgId = feeScheduleMatchOrgId({ ...claim, ...service });
  const candidates = (state.feeScheduleRows || []).filter(row =>
    String(row.status || "active") !== "archived"
    && String(firstPresent(row.orgId, row.org_id, row.OrgId) || "").trim() === orgId
    && feePayerMatches(payer, row.payer, payerId, row.payorId || row.payerId)
    && feeProcedureMatches(service.procedure, row.procedure));
  // feeStateCompatible, not feeStateMatches: since 2026-09-17 a BLANK contract
  // state is a wildcard over classified lines, so a blank-state row is no longer
  // evidence that the schedule disagrees with this line's classification. The
  // blank-ACTUAL rejection above is untouched.
  // Mirrors backend/feeScheduleRates.js#classificationRejected.
  return candidates.length > 0 && !candidates.some(row => feeStateCompatible(classification, row.state || row.State));
}

// Mirrors backend/missingRateAccrual.js; parity is verified for every CPT.
function trackerClassificationAccrualRate(procedure) {
  const rates = { "97151": 21, "97152": 15, "97153": 15, "97154": 15, "97155": 21, "97156": 21, "97158": 21, H0032: 21 };
  return rates[String(procedure || "").trim().toUpperCase().replace(/\.0$/, "")] || null;
}

function trackerServiceFinancials(service, claim, context) {
  const localClassificationGuard = !trackerApiEnabled();
  const primaryClassificationRejected = localClassificationGuard && trackerLocalClassificationRejected(service, claim, "primary");
  const secondaryClassificationRejected = localClassificationGuard && trackerLocalClassificationRejected(service, claim, "secondary");
  if (primaryClassificationRejected || secondaryClassificationRejected) {
    service = { ...service };
    context = { ...context, sourceExpected: 0 };
    for (const role of ["primary", "secondary"]) {
      if (!(role === "primary" ? primaryClassificationRejected : secondaryClassificationRejected)) continue;
      service[role + "Rate"] = 0;
      service[role + "RateState"] = "missing";
      service[role + "RateAbsent"] = true;
    }
    // These totals were derived using rejected rates; recompute them below.
    for (const key of ["secondaryExpectedTotal", "staticTotalExpected", "finalExpectedTotalNumeric", "finalSecondaryExpectedNumeric", "secondaryExpectedChangeNumeric", "totalExpectedChangeNumeric", "Secondary Expected Total", "Static Total Expected", "Final Expected Total Numeric", "Final Secondary Expected Numeric", "Secondary Expected Change Numeric", "Total Expected Change Numeric"]) delete service[key];
  }
  const successorAuthority = String(firstPresent(service.calculationAuthority, service.calculation_authority, claim.calculationAuthority, claim.calculation_authority) ?? "").trim().toLowerCase() === "app-v1";
  const units = quantityNumber(firstPresent(service.units, service.Units, 0));
  const primaryPayer = firstPresent(service.primaryPayer, claim.primaryPayer);
  const secondaryPayer = firstPresent(service.secondaryPayer, claim.secondaryPayer);
  const claimClassification = String(firstPresent(service.claimClassification, claim.claimClassification, claim.claClassification) ?? "").trim().toUpperCase();
  const explicitSecondarySubmitted = booleanFromSource(firstPresent(
    service.isSecondarySubmitted,
    service.IsSecondarySubmitted,
    service.secondarySubmitted,
    service.submittedToSecondary,
    claim.isSecondarySubmitted,
    claim.IsSecondarySubmitted,
    claim.secondarySubmitted,
    claim.submittedToSecondary,
    null
  ));
  const classificationSecondarySubmitted = trackerClassificationIndicatesSecondarySubmitted(firstPresent(service.claimClassification, claim.claimClassification, claim.claClassification));
  const actionSecondarySignal = /secondary/i.test(String(firstPresent(claim.action, service.action) ?? ""));
  const hasSecondary = booleanFromSource(firstPresent(service.hasSecondaryPayer, claim.hasSecondaryPayer, null)) === true
    || hasRealPayer(secondaryPayer)
    || hasPresent(firstPresent(service.secondaryExternalId, service.secondaryPayerExternalId, claim.secondaryExternalId, claim.secondaryPayerExternalId, null));
  // Owner rule 2026-08-18: a real secondary payer activates secondary math.
  // Classification remains display/workflow evidence, not a calculation gate.
  const secondaryWorkflowActive = successorAuthority
    ? hasSecondary
    : (explicitSecondarySubmitted === true || classificationSecondarySubmitted || actionSecondarySignal);
  // App-owned insurer math uses signed PR1/2/3 adjustments. Payer-name-based
  // Medicaid suppression must not alter either the money or source diagnostics.
  const hasMedicaidPayer = !successorAuthority && (isMedicaidPayer(primaryPayer) || isMedicaidPayer(secondaryPayer) || String(firstPresent(service.payerClassFilter, service["Payer Class Filter"]) ?? "").toLowerCase() === "medicaid");
  const positivePatientBalance = hasMedicaidPayer ? 0 : moneyNumber(Math.max(0, context.patientBalance));
  const primaryFeeMatch = bestFeeScheduleMatch(primaryPayer, {
    ...service,
    place: firstPresent(service.place, claim.place),
    state: firstPresent(service.state, claim.state),
    orgId: firstPresent(service.orgId, service.org_id, claim.orgId, claim.org_id),
    payerId: firstPresent(service.primaryPayerId, service.primaryPayorId, service.primaryExternalId, service.primaryPayerExternalId, claim.primaryPayerId, claim.primaryPayorId, claim.primaryExternalId, claim.primaryPayerExternalId)
  }, state.feeScheduleRows || []);
  const secondaryFeeMatch = bestFeeScheduleMatch(secondaryPayer, {
    ...service,
    place: firstPresent(service.place, claim.place),
    state: firstPresent(service.state, claim.state),
    orgId: firstPresent(service.orgId, service.org_id, claim.orgId, claim.org_id),
    payerId: firstPresent(service.secondaryPayerId, service.secondaryPayorId, service.secondaryExternalId, service.secondaryPayerExternalId, claim.secondaryPayerId, claim.secondaryPayorId, claim.secondaryExternalId, claim.secondaryPayerExternalId)
  }, state.feeScheduleRows || []);
  const primaryRateInput = firstPresent(service.primaryRate, service.primary_rate, service.Primary_Rate, service["Primary_Rate"], primaryFeeMatch?.rate, null);
  const secondaryRateInput = firstPresent(service.secondaryRate, service.secondary_rate, service.Secondary_Rate, service["Secondary_Rate"], secondaryFeeMatch?.rate, null);
  const explicitPrimaryRate = hasPresent(primaryRateInput);
  const explicitSecondaryRate = hasPresent(secondaryRateInput);
  const primaryRate = explicitPrimaryRate ? moneyNumber(primaryRateInput) : 0;
  const secondaryRate = explicitSecondaryRate ? moneyNumber(secondaryRateInput) : 0;
  const estimateCertainty = trackerEstimateCertainty(service.calculationGuardFlags || service.calculationGuards || service.calculation_guard_flags);
  const primaryClassificationAccrual = primaryClassificationRejected && !successorAuthority && hasRealPayer(primaryPayer) ? trackerClassificationAccrualRate(service.procedure) : null;
  const secondaryClassificationAccrual = secondaryClassificationRejected && !successorAuthority && hasSecondary && secondaryWorkflowActive ? trackerClassificationAccrualRate(service.procedure) : null;
  if (primaryClassificationRejected) estimateCertainty.primaryRateEstimated = primaryClassificationAccrual !== null;
  if (secondaryClassificationRejected) estimateCertainty.secondaryRateEstimated = secondaryClassificationAccrual !== null;
  const primaryExpectedTotal = primaryClassificationRejected ? moneyNumber(units * (primaryClassificationAccrual || 0)) : explicitPrimaryRate && units > 0 && !estimateCertainty.primaryRateEstimated
    ? moneyNumber(units * primaryRate)
    : moneyNumber(firstPresent(service.primaryExpectedTotal, service["Primary Expected Total"], context.sourceExpected));
  const secondaryAllowedTotal = secondaryClassificationRejected ? moneyNumber(units * (secondaryClassificationAccrual || 0)) : explicitSecondaryRate && units > 0 && !estimateCertainty.secondaryRateEstimated
    ? moneyNumber(units * secondaryRate)
    : moneyNumber(firstPresent(service.secondaryAllowedTotal, service["Secondary Allowed Total"], 0));
  // Per-payer secondary expected rule (old tracker SecondaryPaymentMapping):
  // the service row's own tracker-era Secondary_Calc wins; otherwise the
  // matched SECONDARY fee row supplies it. Mirrors
  // backend/feeScheduleRates.js#applyFeeScheduleRatesToClaims.
  const feeRowSecondaryCalc = secondaryFeeMatch ? String(secondaryFeeMatch.secondaryCalc || secondaryFeeMatch.secondary_calc || secondaryFeeMatch.Secondary_Calc || "") : "";
  const secondaryCalc = String(firstPresent(service.secondaryCalc, service.Secondary_Calc, service["Secondary_Calc"], feeRowSecondaryCalc, hasSecondary ? "Allowed Amount" : "PR Amount")) || "Allowed Amount";
  const secondaryExpectedTotal = moneyNumber(firstPresent(
    service.secondaryExpectedTotal,
    service["Secondary Expected Total"],
    hasSecondary
      ? (secondaryCalc === "PR Amount" ? (successorAuthority ? moneyNumber(context.truePatientResponsibility) : positivePatientBalance) : Math.max(0, secondaryAllowedTotal - primaryExpectedTotal))
      : 0
  ));
  const staticTotalExpected = moneyNumber(firstPresent(service.staticTotalExpected, service["Static Total Expected"], primaryExpectedTotal + secondaryExpectedTotal));
  const hasRateDrivenExpected = explicitPrimaryRate || explicitSecondaryRate || hasPresent(service.primaryRateSource) || hasPresent(service.secondaryRateSource);
  // Secondary revision per build_lite_snapshot.py / spec lines 156-162.
  // Mirrors backend/financialCalculationEngine.js#secondaryRevisedExpected.
  // Without the gate, the portal was leaving all secondary-workflow rows at
  // their static expected — never revising to the allowed amount and never
  // capping PR-amount rows when allowed < static.
  const revisedTotalExpected = trackerSecondaryRevisedExpected({
    secondaryWorkflowActive,
    hasSecondary,
    secondaryCalc,
    staticTotalExpected,
    secondaryAllowedTotal,
    insurancePaid: context.insurancePaid,
    baselineInsurancePaid: firstPresent(
      service.baselineTotalInsPaid,
      service["Baseline Total Ins Paid"],
      service.baselineInsurancePaid,
      service.secondarySubmissionBaselineInsPaid,
      null
    ),
    allowCurrentPaidFallback: !successorAuthority
  });
  const finalExpectedTotalNumeric = moneyNumber(firstPresent(
    service.finalExpectedTotalNumeric,
    service["Final Expected Total Numeric"],
    hasRateDrivenExpected
      ? revisedTotalExpected.finalExpectedTotal
      : (context.sourceExpected || revisedTotalExpected.finalExpectedTotal)
  ));
  const finalSecondaryExpectedNumeric = moneyNumber(firstPresent(
    service.finalSecondaryExpectedNumeric,
    service["Final Secondary Expected Numeric"],
    revisedTotalExpected.revisionApplies
      ? Math.max(0, finalExpectedTotalNumeric - context.insurancePaid)
      : secondaryExpectedTotal
  ));
  const secondaryExpectedChangeNumeric = moneyNumber(firstPresent(service.secondaryExpectedChangeNumeric, service["Secondary Expected Change Numeric"], finalSecondaryExpectedNumeric - secondaryExpectedTotal));
  const totalExpectedChangeNumeric = moneyNumber(firstPresent(service.totalExpectedChangeNumeric, service["Total Expected Change Numeric"], finalExpectedTotalNumeric - staticTotalExpected));
  // TRUE PATIENT RESPONSIBILITY — TWO CARRIERS. Mirror of
  // backend/financialCalculationEngine.js; the two engines must state one rule,
  // and verify_money_engines_agree.js is what holds them to it.
  //
  // Owner rule: the patient's share is PR 1/2/3 (deductible/coinsurance/copay),
  // NOT the balance the source line states, because that balance is sometimes
  // wrong. The per-code array stays first; the scalar `prAdjustmentTotal` is the
  // warehouse lane's carrier, summed in SQL because gold has no adjustment rows.
  //
  // NULL IS NOT ZERO — for EVIDENCE, not for money. Absent detail and an
  // adjudicated $0 both yield $0 of patient responsibility since the owner's
  // Option B ruling (2026-09-17); they stay distinguishable because the server
  // reports them as separate pr_evidence_state values and gates
  // submit-for-review on the difference. The stated balance is never the
  // patient's share on either.
  const scalarPrTotal = trackerAuthoritativePrTotal(service);
  const hasPerCodeDetail = Array.isArray(service.adjustments) && service.adjustments.length > 0;
  // Retained as the line's EVIDENCE fact and to mirror the backend engine's
  // parameter of the same name. Since Option B it selects no money branch.
  const hasAdjustmentDetail = hasPerCodeDetail || scalarPrTotal !== null;
  const resolvedTruePatientResponsibility = hasPerCodeDetail
    ? moneyNumber(Math.max(0, context.truePatientResponsibility || 0))
    : (scalarPrTotal === null ? moneyNumber(Math.max(0, context.truePatientResponsibility || 0)) : scalarPrTotal);
  // PATIENT RESPONSIBILITY IS PR 1/2/3, ON EVERY ARM (owner ruling, Option B,
  // 2026-09-17). Mirror of backend/financialCalculationEngine.js — see the long
  // note there for the production measurement that retired the stated-balance
  // fallback ($124,797.45 on 132 lines, all of it unposted contractual
  // write-offs EZClaim had parked in the patient bucket).
  //
  // positivePatientBalance stays computed: it still feeds the secondary payer's
  // "PR Amount" expectation and the positiveTotalPatientBalance output. It can no
  // longer reach this figure.
  //
  // A secondary makes the patient's collectible share zero. Keep PR 1/2/3 as
  // evidence for the secondary calculation above, but never subtract it from
  // insurance expected or present it as patient responsibility at the same time.
  // resolvedTruePatientResponsibility is already floored at zero on BOTH of its
  // branches, so no second Math.max belongs here: adding one would absorb a
  // negative net and quietly disarm the guard in verify_money_engines_agree.js
  // that proves the per-code net clamps.
  const patientResponsibilityBeforeSecondary = resolvedTruePatientResponsibility;
  const patientResponsibilityTotal = hasSecondary ? 0 : patientResponsibilityBeforeSecondary;
  const preWriteoffInsuranceExpected = moneyNumber(Math.max(0, finalExpectedTotalNumeric - patientResponsibilityTotal));
  // A WRITE-OFF IS THE REMAINING BALANCE, NEVER THE WHOLE EXPECTED.
  // Mirror of backend/financialCalculationEngine.js - see the long note there for the
  // owner rule, the CT expression it matches, and the legacy workbook column formula
  // it was measured against. Short version: the ceiling on any write-off is
  // `preWriteoff - paid`. The derived arm IS that subtraction; the else arm passes a
  // STORED amount through and nothing upstream bounds it, so it is clamped to the same
  // ceiling. An overstated write-off silently deletes collectable AR.
  const writeOffCeiling = moneyNumber(Math.max(0, preWriteoffInsuranceExpected - context.insurancePaid));
  const writeOffAmount = isWriteoffAction(firstPresent(service.action, service.followUpAction, claim.action))
    ? writeOffCeiling
    : moneyNumber(Math.min(
      writeOffCeiling,
      Math.max(0, moneyNumber(firstPresent(service.writeOffAmount, service["Write-off Amount"], 0)))
    ));
  const finalInsuranceExpected = moneyNumber(Math.max(0, preWriteoffInsuranceExpected - writeOffAmount));
  const finalInsuranceBalance = moneyNumber(finalInsuranceExpected - context.insurancePaid);
  // "NO RATE ON FILE" AS AN ASSERTION, KEPT SEPARATE FROM THE INFERENCE.
  //
  // primaryRateMissing (below) also fires on `sourceExpected <= 0`. That clause
  // is a PROXY, written when an absent rate was priced at units * 0 - back then a
  // zero expected really did mean "we could not price this line". Now that an
  // absent rate falls through to the source's own expected total, a zero expected
  // means the line IS zero: settled, bundled, fully adjusted. Rolling that proxy
  // up to the CLAIM grain hid $1,000 of real owed behind one settled line
  // (portal-guardian, 2026-08-04), so the claim roll-up uses primaryRateAbsent:
  // absent because something ASSERTED it - the adapter suppressed the rate, or the
  // finalized Match says so - never because the money happened to look small.
  //
  // The assertion also has to be CARRIED, not re-derived. backend/trackerApi.js
  // rateValueOrAbsent() nulls an absent rate so the engine stops pricing it at $0,
  // and nulling it flips explicitPrimaryRate false - which on the mirror lane sent
  // this flag down a branch that finds nothing (a 60,000-row probe: ZERO "no rate"
  // match strings against 11,939 lines with no primary rate). The mapper therefore
  // sets service.primaryRateAbsent, and both engines read it. Money got better and
  // the warning stayed.
  const assertedPrimaryRateAbsent = booleanFromSource(firstPresent(service.primaryRateAbsent, service.primary_rate_absent, null)) === true;
  const assertedSecondaryRateAbsent = booleanFromSource(firstPresent(service.secondaryRateAbsent, service.secondary_rate_absent, null)) === true;
  const primaryRateState = String(firstPresent(service.primaryRateState, service.primary_rate_state) ?? "").trim().toLowerCase();
  const secondaryRateState = String(firstPresent(service.secondaryRateState, service.secondary_rate_state) ?? "").trim().toLowerCase();
  const primaryRateStateMissing = ["missing", "ambiguous", "unreadable"].includes(primaryRateState);
  const secondaryRateStateMissing = ["missing", "ambiguous", "unreadable"].includes(secondaryRateState);
  const primaryRateConfiguredZero = primaryRateState === "matched_zero" || Number(primaryFeeMatch?.rate) === 0;
  const secondaryRateConfiguredZero = secondaryRateState === "matched_zero" || Number(secondaryFeeMatch?.rate) === 0;
  // THE NO-RATE MATCH IS A PER-LINE FACT, so read the LINE's own value first.
  // claim.match is only a statement about one line when the claim HAS one line:
  // on the drawer/adapter lane it is (one row in, one line out) and it is the
  // only place the signal lives, because service.status there carries the CLAIM
  // STATUS ("Open"). On a folded multi-line claim it is the FIRST row's match
  // applied to all of them, which marked every sibling of an unpriced line
  // unpriced too. Hence the line-count gate.
  const claimLineCount = Array.isArray(claim.services) ? claim.services.length : 0;
  const assertedNoRateMatch = isNoRateMatch(service.match) || isNoRateMatch(service.status)
    || (claimLineCount <= 1 && isNoRateMatch(claim.match));
  // ASSERTED MEANS ASSERTED. This deliberately does NOT include
  // `explicitPrimaryRate ? primaryRate <= 0`, and that omission is the whole
  // point. On the report lane every rate arrives as 0 whether it is absent or
  // not (reportDatasetSource and trackerApiRowToClaim both default it), so a
  // rate-value test cannot tell an UNPRICED line from a SETTLED one - bundled,
  // non-covered, fully adjusted, expected 0, owed 0. Including that arm rolled
  // a settled line up as an unknown and dashed a claim that owed a measured
  // $1,000 (portal-guardian). A rate is absent when SOMETHING SAYS SO: the
  // adapter that suppressed it, or the finalized Match value - which is exactly
  // the signal the live grid already reads through trackerRowMoneyCertainty, so
  // the claim grain and the live lane now derive certainty from one source.
  const primaryRateAbsent = assertedPrimaryRateAbsent || primaryRateStateMissing || assertedNoRateMatch;
  const primaryRateMissing = successorAuthority
    ? primaryRateAbsent
    : (assertedPrimaryRateAbsent || (!primaryRateConfiguredZero && (explicitPrimaryRate ? primaryRate <= 0 : context.sourceExpected <= 0 || /no rate/i.test(String(firstPresent(service.status, claim.match) ?? "")))));
  const secondaryRateMissing = hasSecondary && secondaryWorkflowActive && (successorAuthority
    ? (assertedSecondaryRateAbsent || secondaryRateStateMissing || assertedNoRateMatch)
    : (!secondaryRateConfiguredZero && (explicitSecondaryRate ? secondaryRate <= 0 : assertedSecondaryRateAbsent)));
  const secondaryRateAbsent = hasSecondary && secondaryWorkflowActive && (assertedSecondaryRateAbsent || secondaryRateStateMissing || assertedNoRateMatch);
  return {
    ...estimateCertainty,
    hasMedicaidPayer,
    positivePatientBalance,
    patientResponsibilityTotal,
    primaryRate,
    secondaryRate,
    primaryRateMissing,
    primaryRateAbsent,
    secondaryRateMissing,
    secondaryRateAbsent,
    primaryExpectedTotal,
    secondaryAllowedTotal,
    secondaryCalc,
    secondaryExpectedTotal,
    staticTotalExpected,
    finalExpectedTotalNumeric,
    finalSecondaryExpectedNumeric,
    secondaryExpectedChangeNumeric,
    totalExpectedChangeNumeric,
    writeOffAmount,
    finalInsuranceExpected,
    finalInsuranceBalance,
    finalizedMatch: trackerFinalizedMatch(finalInsuranceExpected, context.insurancePaid, primaryRateMissing, secondaryRateMissing, primaryRateState === "learned_oon", primaryExpectedTotal)
  };
}

function trackerPaymentAttributionFinancials(claim) {
  return (claim.payments || []).reduce((sum, payment) => {
    const amount = moneyNumber(firstPresent(payment.applied, payment.amount, payment["Payment Amount"], 0));
    const role = paymentRoleFromEvidence(claim, payment);
    if (role === "Primary") sum.primaryPaid += amount;
    else if (role === "Secondary") sum.secondaryPaid += amount;
    else sum.unmatchedPaid += amount;
    return sum;
  }, { primaryPaid: 0, secondaryPaid: 0, unmatchedPaid: 0 });
}

function paymentRoleFromEvidence(claim, payment) {
  const paymentId = externalPayerId(firstPresent(payment.externalPayerId, payment.externalPayerID, payment["External Payer ID"], payment.paymentExternalPayerId));
  const primaryId = externalPayerId(firstPresent(claim.primaryExternalId, claim.primaryPayerExternalId));
  const secondaryId = externalPayerId(firstPresent(claim.secondaryExternalId, claim.secondaryPayerExternalId));
  if (primaryId && secondaryId && primaryId === secondaryId) return "Unmatched";
  const secondaryEvidence = secondarySubmissionEvidence(claim);
  const secondaryEligible = hasSecondaryRole(claim)
    || secondaryEvidence.weak
    || Boolean(paymentId && secondaryId && paymentId === secondaryId && paymentId !== primaryId);
  const matches = [];
  if (paymentId && primaryId && paymentId === primaryId) matches.push("Primary");
  if (secondaryEligible && paymentId && secondaryId && paymentId === secondaryId) matches.push("Secondary");
  if (matches.length === 1) return matches[0];
  const explicit = String(payment.attributedTo || payment.matchedTo || "").trim().toLowerCase();
  if (explicit.includes("primary")) return "Primary";
  if (explicit.includes("secondary")) return "Secondary";
  if (explicit.includes("unmatched")) return "Unmatched";
  return "Unmatched";
}

// Owner rule 2026-08-18: a positive residual insurance balance <= $5 with a payment present reads
// "Paid" (billers do not chase trivial residuals). Mirrors build_lite_snapshot.py
// SMALL_BALANCE_PAID_TOLERANCE = 5.0. Money-DISPLAY classification only; no dollar math changes.
const SMALL_BALANCE_PAID_TOLERANCE = 5;
// Owner rule 2026-10-19: a line priced from a learned out-of-network rate and paid within
// 5% of it reads "Paid", over or under (2026-10-22). Mirrors financialCalculationEngine LEARNED_OON_PAID_TOLERANCE_RATIO.
const LEARNED_OON_PAID_TOLERANCE_RATIO = 0.05;
function trackerFinalizedMatch(finalInsuranceExpected, insurancePaid, primaryRateMissing, secondaryRateMissing, learnedRatePricing = false, learnedRateExpected = null) {
  // 5% of the learned rate x units, not of the insurance share after patient responsibility.
  const learnedTolerance = moneyNumber((Number(learnedRateExpected) > 0 ? Number(learnedRateExpected) : finalInsuranceExpected) * LEARNED_OON_PAID_TOLERANCE_RATIO);
  if (primaryRateMissing) return "No Primary Rate";
  if (secondaryRateMissing) return "No Secondary Rate";
  const balance = moneyNumber(finalInsuranceExpected - insurancePaid);
  if (balance === 0) return "Paid";
  if (balance > 0) {
    // Small-balance-paid tolerance: payment present + residual within $5 reads Paid.
    if (insurancePaid > 0 && balance <= SMALL_BALANCE_PAID_TOLERANCE) return "Paid";
    if (learnedRatePricing && insurancePaid > 0 && balance <= learnedTolerance) return "Paid";
    return insurancePaid <= 0 ? "Not Paid" : "Under Paid";
  }
  if (learnedRatePricing && insurancePaid > 0 && -balance <= learnedTolerance) return "Paid";
  return "Over Paid";
}

// Mirror of backend/financialCalculationEngine.js#secondaryRevisedExpected.
// Returns the revised total expected per spec lines 156-162.
function trackerSecondaryRevisedExpected({
  secondaryWorkflowActive,
  hasSecondary,
  secondaryCalc,
  staticTotalExpected,
  secondaryAllowedTotal,
  insurancePaid = 0,
  baselineInsurancePaid = null,
  allowCurrentPaidFallback = true
}) {
  if (!secondaryWorkflowActive || !hasSecondary || secondaryAllowedTotal <= 0) {
    return { finalExpectedTotal: staticTotalExpected, revisionApplies: false };
  }
  const calc = String(secondaryCalc || "Allowed Amount");
  const allowedRevision = !/PR\s*Amount/i.test(calc);
  const prAllowedCapRevision = /PR\s*Amount/i.test(calc) && secondaryAllowedTotal < staticTotalExpected;
  if (!allowedRevision && !prAllowedCapRevision) {
    return { finalExpectedTotal: staticTotalExpected, revisionApplies: false };
  }
  const baselinePaid = hasPresent(baselineInsurancePaid)
    ? moneyNumber(baselineInsurancePaid)
    : (allowCurrentPaidFallback ? moneyNumber(insurancePaid) : 0);
  const baselinePaidFloor = Math.min(baselinePaid, staticTotalExpected);
  return {
    finalExpectedTotal: moneyNumber(Math.max(secondaryAllowedTotal, baselinePaidFloor)),
    revisionApplies: true
  };
}

function hasSecondaryRole(claim) {
  const classification = String(firstPresent(claim.claimClassification, claim.claClassification) ?? "").trim().toUpperCase();
  return trackerSecondaryWorkflowClassifications.has(classification) || /secondary/i.test(String(firstPresent(claim.action, claim.claimStatus, claim.reviewNote) ?? ""));
}

function secondarySubmissionEvidence(claim = {}) {
  const explicit = booleanFromSource(firstPresent(
    claim.isSecondarySubmitted,
    claim.IsSecondarySubmitted,
    claim.secondarySubmitted,
    claim.submittedToSecondary,
    null
  ));
  if (explicit === true) return { strong: true, weak: true, submittedAt: null, source: "explicit secondary-submitted flag" };
  if (explicit === false) return { strong: false, weak: false, submittedAt: null, source: "explicit not-secondary flag" };

  const noteEvidence = claimTextEvidence(claim, {
    strongPattern: /\b(marked complete:\s*)?(secondary\s+claim\s+)?submitted(\s+to\s+secondary)?\b|\bsubmitted\s+(the\s+)?secondary\b|\bsecondary\s+(claim\s+)?sent\b|\bsecondary\s+submission\s+(complete|confirmed|saved)\b/i,
    weakPattern: /\bsecondary\b/i
  });
  if (noteEvidence.strong) return noteEvidence;

  const classification = normalizeTrackerText(firstPresent(claim.claimClassification, claim.claClassification));
  if (trackerSecondaryWorkflowClassifications.has(classification)) {
    return { strong: true, weak: true, submittedAt: null, source: "secondary workflow classification" };
  }
  if (noteEvidence.weak || /secondary/i.test(String(firstPresent(claim.action, claim.claimStatus, claim.reviewNote) ?? ""))) {
    return { strong: false, weak: true, submittedAt: noteEvidence.submittedAt || null, source: noteEvidence.source || "secondary workflow text" };
  }
  return { strong: false, weak: false, submittedAt: null, source: "" };
}

function claimTextEvidence(claim = {}, { strongPattern, weakPattern }) {
  const textItems = [];
  [claim.action, claim.claimStatus, claim.reviewNote, claim.notesText, claim.latestClaimNoteText, claim.claimNoteSummary?.text].forEach((value) => {
    if (hasPresent(value)) textItems.push({ text: String(value), at: null });
  });
  (Array.isArray(claim.notes) ? claim.notes : []).forEach((note) => {
    textItems.push({ text: String(firstPresent(note.text, note.note, note.body) ?? ""), at: timestampValue(firstPresent(note.at, note.date, note.createdAt)) || null });
  });
  (Array.isArray(claim.services) ? claim.services : []).forEach((service) => {
    [service.latestClaimNoteText, service.latestClaimNote].forEach((value) => {
      if (hasPresent(value)) textItems.push({ text: String(value), at: null });
    });
  });
  const strong = textItems.find((item) => strongPattern.test(item.text));
  if (strong) return { strong: true, weak: true, submittedAt: strong.at || null, source: "claim/service notes" };
  const weak = textItems.find((item) => weakPattern.test(item.text));
  return { strong: false, weak: Boolean(weak), submittedAt: weak?.at || null, source: weak ? "claim/service notes" : "" };
}

function claimHasTruePatientResponsibility(claim = {}) {
  return (Array.isArray(claim.services) ? claim.services : []).some((service) => truePatientResponsibility(service) > 0);
}

function isWriteoffAction(action) {
  const normalized = normalizeTrackerText(action).replace(/-/g, " ");
  return normalized.includes("WRITE") && normalized.includes("OFF");
}

function isMedicaidPayer(value) {
  const text = normalizeTrackerText(value);
  return trackerMedicaidMarkers.some((marker) => text.includes(marker));
}

// CT-TRACKER LITE "Payer Classification" parity (build_lite_snapshot.py
// MEDICAID_MARKERS). Derived purely from the public payer NAME, so it is
// non-financial. Kept SEPARATE from the financial trackerMedicaidMarkers list
// on purpose: this mirrors the CT-TRACKER snapshot markers exactly (it adds
// PEACH STATE / ALLIANCE - NC) without widening the Medicaid-driven
// patient-balance logic that isMedicaidPayer() feeds.
const payerClassMedicaidMarkers = [
  "MEDICAID",
  "TENNCARE",
  "PEACHSTATE",
  "PEACH STATE",
  "CARESOURCE",
  "CARE SOURCE",
  "AMERIGROUP",
  "WELLPOINT",
  "ALLIANCE - NC",
  "HEALTHKEEPERS",
  "BETTER HEALTH"
];

// "Medicaid" when the uppercased payer name contains any Medicaid marker;
// "" (no class) for a blank/missing payer; otherwise "Commercial".
function payerClassFor(payerName) {
  const text = normalizeTrackerText(payerName);
  if (!text) return "";
  return payerClassMedicaidMarkers.some((marker) => text.includes(marker)) ? "Medicaid" : "Commercial";
}

function hasRealPayer(value) {
  const text = normalizeTrackerText(value);
  return Boolean(text && text !== "NONE" && text !== "N/A" && text !== "NA");
}

function externalPayerId(value) {
  return String(value == null ? "" : value).trim().replace(/\.0+$/, "").toUpperCase();
}

function normalizeTrackerText(value) {
  return String(value == null ? "" : value).trim().toUpperCase().replace(/\s+/g, " ");
}

function firstPresent(...values) {
  return values.find(hasPresent);
}

function hasPresent(value) {
  return value !== undefined && value !== null && value !== "";
}

function booleanFromSource(value) {
  if (!hasPresent(value)) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const text = normalizeTrackerText(value);
  if (["1", "TRUE", "YES", "Y", "SUBMITTED", "SUBMITTED TO SECONDARY"].includes(text)) return true;
  if (["0", "FALSE", "NO", "N", "NOT SUBMITTED", "PRIMARY"].includes(text)) return false;
  return null;
}

function prCodes(service) {
  const values = adjustmentRows(service)
    .filter(isTruePatientResponsibilityAdjustment)
    .map((adjustment) => `PR ${String(adjustment.reasonCode || adjustment.code).replace(/\D/g, "")}`);
  return values.length ? Array.from(new Set(values)).join(", ")
    : (truePatientResponsibility(service) > 0 ? "PR 1/2/3 total" : "None");
}

function prReasons(service) {
  const reasons = adjustmentRows(service)
    .filter(isTruePatientResponsibilityAdjustment)
    .map((adjustment) => adjustment.reason || "Patient responsibility")
    .filter(Boolean);
  return reasons.length ? Array.from(new Set(reasons)).join(", ")
    : (truePatientResponsibility(service) > 0 ? "Reason detail unavailable" : "No PR 1/2/3");
}

function patientResponsibilityStatus(service, claim = {}) {
  // Secondary is a claim-level fact, so the claim is optional but wanted: callers that
  // have it get the same answer the panel, the report and the statement lane give.
  if (truePatientResponsibility(service) > 0) return patientResponsibilityClaimHasSecondary(claim) ? "PR 1/2/3 - secondary on claim" : "True PR 1/2/3";
  if (String(service?.responsibleParty || "").toLowerCase().includes("patient")) return "EZClaim flip - keep insurance";
  return "Insurance/other";
}

function statementEligibilityBlockReason(service = {}, claim = {}) {
  if (truePatientResponsibility(service) <= 0) return "no-qualifying-pr";
  const patientBalance = moneyNumber(firstPresent(
    service.patientBalance,
    service.totalPatBal,
    service["Total Pat Bal"],
    0
  ));
  if (patientBalance <= 0) return "no-patient-balance";
  if (Math.round(patientBalance * 100) < Math.round(truePatientResponsibility(service) * 100)) return "pr-balance-mismatch";
  const secondaryPayer = String(firstPresent(service.secondaryPayer, claim.secondaryPayer) ?? "").trim();
  const secondaryExternalId = String(firstPresent(service.secondaryExternalId, service.secondaryPayerExternalId, claim.secondaryExternalId, claim.secondaryPayerExternalId) ?? "").trim();
  const hasSecondary = booleanFromSource(firstPresent(service.hasSecondaryPayer, claim.hasSecondaryPayer, null)) === true
    || Boolean(secondaryPayer && !["none", "n/a"].includes(secondaryPayer.toLowerCase()))
    || Boolean(secondaryExternalId);
  if (hasSecondary) return "secondary-on-claim";
  const followUp = String(firstPresent(service.followUpAction, service.follow_up_action, service.action, claim.action) ?? "").trim();
  if (followUp && followUp.toUpperCase() !== "NONE") return "open-follow-up";
  return "";
}

// Claim rows carry no PR adjustment dates and no server print ledger, so this
// mirror cannot apply the grace hold or see a Statements-tab print. It only
// rules lines out; the Statements tab decides "ready" and "printed".
function statementStatus(service, claim = {}) {
  if (statementEligibilityBlockReason(service, claim)) return "Not eligible";
  return state.statementPrints?.[service.serviceId] ? "Printed" : "Check Statements tab";
}

function getStatementRows(claims) {
  return claims.flatMap((claim) => claim.services
    .filter((service) => !statementEligibilityBlockReason(service, claim))
    .map((service) => ({ claim, service })));
}

// A ONE-WAY DOOR, CLOSED (2026-08-10). These options are derived from the rows
// the server returned \u2014 correct while the server IGNORED the filters, and a
// trap the moment it honours them: pick a payer, the refetch returns only that
// payer's rows, the payer list rebuilds from those rows, and the control now
// offers exactly the value already selected. The user could not get back.
//
// So the facet lists ACCUMULATE for the life of the session instead of being
// replaced. resetReportDataset() clears them, which is also the login-switch
// boundary \u2014 a new principal must not inherit the previous one's facets.
let reportFilterFacets = {};
function reportFilterFacetValues(key, values) {
  const seen = reportFilterFacets[key] || (reportFilterFacets[key] = []);
  (values || []).forEach((value) => {
    const text = String(value == null ? "" : value);
    if (text && !seen.includes(text)) seen.push(text);
  });
  return [...seen].sort((a, b) => a.localeCompare(b));
}

const REPORT_FACET_CONTROLS = Object.freeze({ reportTenantFilter: "tenant", reportPayerFilter: "primaryPayer", reportProviderFilter: "provider", reportLocationFilter: "facility", reportAgingFilter: "aging", reportStatusFilter: "claimStatus", reportMatchFilter: "match", reportFollowUpDueFilter: "followUpDue", reportActionFilter: "action", reportServiceMonthFilter: "serviceMonth", reportProcedureFilter: "procedure" });
let reportFilterFacetReadState = { key: "", status: "idle", response: null, error: "" };
let reportFilterFacetRequestSequence = 0;
let reportFacetPreferenceScope = "", reportFacetPreferences = {}, reportFacetReadyOptions = {}, reportFacetSearchTimer = null;
function reportFacetVisibleSelects() {
  return Object.entries(REPORT_FACET_CONTROLS).flatMap(([control, facet]) => {
    const source = els[control], nodes = source ? [source] : [];
    const mirror = HOSTED_REPORT_SLICERS.find(row => row.control === control);
    if (mirror) nodes.push(...document.querySelectorAll('[data-hosted-report-slicer="' + mirror.key + '"]'));
    // THE REPORT SOURCES ARE INSIDE CARDS AND ARE NO LONGER PAINTED (2026-09-22).
    //
    // They still hold the option list, so "visible" for them means "the filter
    // panel is open", which #reportFilterGrid's own [hidden] already decides -
    // the same boundary this read has always used. Testing their client rects
    // or their card's open state instead would mean the panel opened onto
    // twelve empty checklists and only filled them one card at a time.
    return nodes.filter(node => node.dataset?.reportFilterSource
      ? !node.closest('[hidden]')
      : (node.getClientRects().length && !node.closest('[hidden]') && !node.closest('details:not([open])'))).map(select => ({ select, facet, control }));
  });
}
function reportFacetScopeKey() {
  return JSON.stringify({ reportView: reportReadPublicationIdentity(), base: trackerApiBase(), token: trackerApiToken(), access: trackerAccessEnvelope("service"), tenant: activeClientGroupTenant() || "All" });
}
function reportFilterFacetReadKey() {
  return JSON.stringify({ scope: reportFacetScopeKey(), visible: [...new Set(reportFacetVisibleSelects().map(row => row.facet))].sort(), options: reportFacetPreferences });
}
function requestReportFilterFacets() {
  if (!trackerApiEnabled() || typeof fetch !== "function") return;
  // The panel is a button over a [hidden] grid since 2026-09-22, not a
  // <details>, so there is no "toggle" event to hang the facet read on:
  // bindReportFilterDisclosure() asks for the facets when it opens the grid.
  const scope = reportFacetScopeKey();
  if (scope !== reportFacetPreferenceScope) { reportFacetPreferenceScope = scope; reportFacetPreferences = {}; reportFacetReadyOptions = {}; reportFilterFacets = {}; reportFilterFacetReadState = { key: "", status: "idle", response: null, error: "" }; }
  const visible = [...new Set(reportFacetVisibleSelects().map(row => row.facet))];
  const optionKey = facet => JSON.stringify({ search: "", page: 1, pageSize: 50, ...(reportFacetPreferences[facet] || {}) });
  const facets = visible.filter(facet => reportFacetReadyOptions[facet] !== optionKey(facet));
  if (!facets.length) return;
  const key = reportFilterFacetReadKey();
  if (reportFilterFacetReadState.key === key && reportFilterFacetReadState.status !== "idle") return;
  reportFilterFacetReadState = { key, status: "pending", response: reportFilterFacetReadState.response, error: "" };
  const requestId = ++reportFilterFacetRequestSequence;
  const current = () => requestId === reportFilterFacetRequestSequence && reportFilterFacetReadState.key === key && reportFacetScopeKey() === scope;
  const token = trackerApiToken();
  const query = { mode: "service", access: trackerAccessEnvelope("service"), page: { number: 1, size: 1 },
    filters: { tenant: activeClientGroupTenant() || "All", queue: "all" }, slicers: {}, columnFilters: {}, facets,
    facetOptions: Object.fromEntries(facets.map(facet => [facet, { search: "", page: 1, pageSize: 50, ...(reportFacetPreferences[facet] || {}) }])),
    responseShape: { columns: ["claimId"], includeFinancials: false, includePhi: false, includeInternalNotes: false } };
  reportReadJson("report-facets", trackerApiBase() + "/api/tracker/facets", { method: "POST", headers: { "content-type": "application/json", ...(token ? { authorization: "Bearer " + token } : {}) }, body: JSON.stringify(query) }).then(({ response, body }) => {
    if (!current()) return;
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (response.ok && body.facets && !body.error) facets.forEach(facet => { reportFacetReadyOptions[facet] = optionKey(facet); });
    const previous = reportFilterFacetReadState.response || {};
    reportFilterFacetReadState = response.ok && body.facets && !body.error
      ? { key, status: "ok", response: { ...body, facets: { ...previous.facets, ...body.facets }, facetPages: { ...previous.facetPages, ...body.facetPages }, facetTotals: { ...previous.facetTotals, ...body.facetTotals } }, error: "" }
      : { key, status: "error", response: null, error: response.status === 401 ? "Your session expired. Sign in again." : "Filter choices could not be loaded. Try again." };
    if (state.view === "reports") { renderReportFilterOptions([]); renderReportFacetChoiceControls(); }
  }).catch(() => {
    if (!current()) return;
    reportFilterFacetReadState = { key, status: "error", response: null, error: "Filter choices could not be loaded. Try again." };
    if (state.view === "reports") renderReportFacetChoiceControls();
  });
}
function renderReportFilterLoadStatus() { renderReportFacetChoiceControls(); }
function reportServerFacetValues(...keys) {
  if (!trackerApiEnabled()) return [];
  const facets = reportFilterFacetReadState.response?.facets || {};
  return keys.flatMap(key => facets[key] || []).map(row => cleanFacetDisplayValue(row?.value)).filter(Boolean);
}
function reportFacetSetPreference(facet, patch, search = false) {
  reportFacetPreferences[facet] = { search: "", page: 1, pageSize: 50, ...(reportFacetPreferences[facet] || {}), ...patch };
  if (reportFacetSearchTimer) clearTimeout(reportFacetSearchTimer);
  if (search) reportFacetSearchTimer = setTimeout(() => { reportFacetSearchTimer = null; requestReportFilterFacets(); renderReportFacetChoiceControls(); }, 250);
  else { requestReportFilterFacets(); renderReportFacetChoiceControls(); }
}
function renderReportFacetChoiceControls() {
  if (!trackerApiEnabled()) return;
  reportFacetVisibleSelects().forEach(({ select, facet, control }) => {
    const page = reportFilterFacetReadState.response?.facetPages?.[facet] || {}, pref = reportFacetPreferences[facet] || {};
    const source = els[control];
    if (select !== source && source) { const selected = source.value || "All"; replaceSelectOptions(select, Array.from(source.options).map(option => option.value).filter(value => value !== "All"), { retainMissingSelection: true, missingSelectionLabel: "selected" }); select.value = selected; }
    let host = select.nextElementSibling;
    if (!host?.matches('[data-report-facet-choices]')) { host = document.createElement("div"); host.dataset.reportFacetChoices = facet; host.className = "report-facet-choices"; select.insertAdjacentElement("afterend", host); }
    const cardOwnsSearch = Boolean(select.dataset.reportFilterSource);
    const hasPageMetadata = Number.isFinite(Number(page.matchingOptions));
    const needsSearch = Number(page.totalOptions) > 50 || pref.search || Number(pref.page) > 1;
    let input = host.querySelector("input");
    // Card search already drives reportFacetSetPreference; retain only its pager.
    if (cardOwnsSearch && input) { input.remove(); input = null; }
    if (needsSearch && !cardOwnsSearch && !input) { input = document.createElement("input"); input.type = "search"; input.placeholder = "Search choices"; input.setAttribute("aria-label", "Search " + (select.getAttribute("aria-label") || control.replace(/^report|Filter$/g, "")) + " choices"); input.value = pref.search || ""; input.addEventListener("input", () => reportFacetSetPreference(facet, { search: input.value, page: 1 }, true)); host.append(input); }
    if (input && document.activeElement !== input) input.value = pref.search || "";
    let status = host.querySelector('[role="status"]'); if (!status) { status = document.createElement("span"); status.setAttribute("role", "status"); host.append(status); }
    const total = Number(page.matchingOptions) || 0, at = Number(page.page) || 1;
    status.textContent = reportFilterFacetReadState.status === "pending" ? "Loading choices…" : reportFilterFacetReadState.status === "error" ? reportFilterFacetReadState.error : hasPageMetadata && (total > 50 || needsSearch) ? (total ? ((at - 1) * 50 + 1) + "–" + Math.min(at * 50, total) + " of " + total + " choices" : "No matching choices") : "";
    host.querySelectorAll("button").forEach(node => node.remove());
    const button = (label, disabled, action) => { const node = document.createElement("button"); node.type = "button"; node.className = "small-button"; node.textContent = label; node.disabled = disabled; node.addEventListener("click", action); host.append(node); };
    if (hasPageMetadata && (needsSearch || total > 50)) { button("Previous choices", at <= 1 || reportFilterFacetReadState.status === "pending", () => reportFacetSetPreference(facet, { page: at - 1 })); button("Next choices", !page.hasMore || reportFilterFacetReadState.status === "pending", () => reportFacetSetPreference(facet, { page: at + 1 })); }
    if (reportFilterFacetReadState.status === "error") button("Retry choices", false, () => { reportFilterFacetReadState.status = "idle"; requestReportFilterFacets(); });
  });
}
function renderReportFilterOptions(claims) {
  requestReportFilterFacets();
  const collectionsOptions = collectionsReportFilterOptions();
  const values = (key, local, facets) => trackerApiEnabled() ? reportServerFacetValues(...facets) : reportFilterFacetValues(key, local);
  const set = (control, key, local, facets, preserveOrder = false) => replaceSelectOptions(els[control], values(key, local, facets), { preserveOrder, retainMissingSelection: true, missingSelectionLabel: "selected" });
  set("reportTenantFilter", "tenant", [...uniqueValues(claims, "client"), ...collectionsOptions.clients], ["tenant", "client"]);
  set("reportPayerFilter", "payer", [...uniqueReportPayers(claims), ...collectionsOptions.payers], ["primaryPayer"]);
  set("reportProviderFilter", "provider", uniqueValues(claims, "renderingProvider"), ["provider"]);
  set("reportLocationFilter", "location", uniqueValues(claims, "facility"), ["facility"]);
  set("reportAgingFilter", "aging", uniqueValues(claims, "aging"), ["aging"]);
  set("reportStatusFilter", "status", uniqueValues(claims, "claimStatus"), ["claimStatus"]);
  set("reportMatchFilter", "match", [...uniqueValues(claims, "match"), "Paid", "Not Paid", "Under Paid"], ["match"], true);
  set("reportFollowUpDueFilter", "followUpDue", ["Due Now", "Future", "No Follow-Up Date"], ["followUpDue"], true);
  set("reportActionFilter", "action", [...uniqueValues(claims, "action"), ...getActionRules().map(rule => rule.action)], ["action"]);
  // BOTH OF THESE PASSED AN EMPTY LOCAL LIST, so off the API lane the two
  // line-grain filters offered nothing but "All" and could not be used at all -
  // while getReportClaims() has always implemented both predicates. The values
  // are read at SERVICE grain, which is the grain the predicate tests.
  set("reportServiceMonthFilter", "serviceMonth", reportServiceLineValues(claims, (service) => serviceMonth(service.dos)), ["serviceMonth"], true);
  set("reportProcedureFilter", "procedure", reportServiceLineValues(claims, (service) => service.procedure), ["procedure"]);
  replaceSelectOptions(els.reportNetworkFilter, NETWORK_BUCKET_OPTIONS, { preserveOrder: true });
  restoreReportFilterSelections();
  // Off the API lane the counts beside each choice are folded from the same
  // claims these option lists came from; on it they come from the facet read.
  reportFilterLocalCounts = trackerApiEnabled() ? new Map() : buildReportFilterLocalCounts(claims);
  // replaceSelectOptions rewrote every <select>.value, so the mirrors are put
  // back in step with the SELECTION before anything reads the filter bag.
  syncReportFilterSelectMirrors();
  bindReportFilterCards();
  renderReportFilterCards();
  renderReportFacetChoiceControls();
}

// Service-line values for the two line-grain report filters. Local/demo only:
// on the API lane these come from the server facets, like every other choice.
function reportServiceLineValues(claims, read) {
  const seen = new Set();
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    (Array.isArray(claim.services) ? claim.services : []).forEach((service) => {
      const value = read(service);
      const text = String(value == null ? "" : value).trim();
      if (text) seen.add(text);
    });
  });
  return Array.from(seen);
}

function collectionsReportFilterOptions() {
  if (reportArState.view !== "collections" || reportCollectionsState.status !== "ok") return { clients: [], payers: [] };
  const rows = Array.isArray(reportCollectionsState.response?.rows) ? reportCollectionsState.response.rows : [];
  return rows.reduce((options, row) => {
    const client = String(row.client || "").trim();
    const payer = String(row.payer || "").trim();
    if (client && !options.clients.includes(client)) options.clients.push(client);
    if (payer && !options.payers.includes(payer)) options.payers.push(payer);
    return options;
  }, { clients: [], payers: [] });
}

// Single-tenant scopes (e.g. a Client login) get a fixed tenant label
// instead of a pointless All/own toggle — clientReportClientOptions
// precedent: scope.clientNames first, then the scoped claims themselves.
let reportTenantFilterScopeLocked = false;
function lockReportTenantFilterForScope(claims = []) {
  const select = els.reportTenantFilter;
  if (!select) return;
  const scope = currentAccessProfile()?.scope || {};
  const scopedClients = normalizeUserScopeList(scope.clientNames);
  const candidates = scope.allOrgs ? [] : (scopedClients.length ? scopedClients : uniqueValues(claims, "client"));
  const lockValue = !scope.allOrgs && candidates.length === 1 ? candidates[0] : "";
  if (!lockValue) {
    if (reportTenantFilterScopeLocked) {
      select.disabled = false;
      select.title = "";
      reportTenantFilterScopeLocked = false;
    }
    return;
  }
  if ("innerHTML" in select) {
    select.innerHTML = `<option value="${escapeHtml(lockValue)}">${escapeHtml(lockValue)}</option>`;
  }
  select.value = lockValue;
  select.disabled = true;
  select.title = "Scoped to your organization";
  reportTenantFilterScopeLocked = true;
}

function replaceSelectOptions(select, values, options = {}) {
  if (!select) return;
  const actionFilter = select === els.actionFilter || select === els.reportActionFilter;
  const current = actionFilter && select.value && select.value !== "All" ? followUpFilterValue(select.value) : (select.value || "All");
  const cleanValues = Array.from(new Set(values.filter(Boolean)));
  if (!options.preserveOrder) cleanValues.sort((a, b) => String(a).localeCompare(String(b)));
  if (actionFilter) cleanValues.splice(0, cleanValues.length, ...followUpFilterValues(cleanValues));
  // A background facet refresh is not permission to widen the user's working
  // scope. This matters immediately after a workflow save: the edited row can
  // legitimately leave the active Provider / Action / Status / Due Now result,
  // and the refreshed facet may therefore report zero rows for the selected
  // value. Keep that value visible and selected so the empty result is honest;
  // only an explicit user filter change may return the control to All.
  const retainCurrent = options.retainMissingSelection === true && current !== "All" && !cleanValues.includes(current);
  if ("innerHTML" in select) {
    select.innerHTML = [
      '<option value="All">All</option>',
      retainCurrent ? `<option value="${escapeHtml(current)}">${escapeHtml(actionFilter ? slicerDisplayValue("action", current) : current)} (${escapeHtml(options.missingSelectionLabel || "selected - 0 current matches")})</option>` : "",
      ...cleanValues.map((value) => `<option value="${escapeHtml(value)}">${escapeHtml(actionFilter ? slicerDisplayValue("action", value) : value)}</option>`)
    ].filter(Boolean).join("");
  }
  select.value = retainCurrent || current === "All" || cleanValues.includes(current) ? current : "All";
}

function uniqueReportPayers(claims) {
  return Array.from(new Set(claims.flatMap((claim) => [claim.primaryPayer, claim.secondaryPayer]).filter(Boolean)));
}

// ---------------------------------------------------------------------------
// THE REPORT FILTER MODEL: A LIST PER KEY, PLUS A DIRECTION (owner, 2026-09-22).
// ---------------------------------------------------------------------------
//   "Every report filter ... a checklist with multi-select, search within the
//    list, Select all / Clear, an Include / Exclude direction (so he can pick
//    some values and hide others, for example exclude every Client Action
//    Pending variant), option counts, an honest chip/summary line, Clear all,
//    persistence across reload and report switches."
//
// This is the SAME model All Data already carries - a selection list beside a
// per-key mode, normalized by the SAME ensureFilterModeState - in the same key
// space the report predicates already travel on. Nothing here is a second
// filter model; the twelve <select>s are demoted to what they are actually good
// at, which is holding the option list the facet lane fills, while the answer
// to "what did the reader pick" moves to state.reportFilters.selections.
//
// TENANT STAYS SINGLE-SELECT, AND THAT IS A CONTRACT, NOT A SHORTCUT.
// reportFilterValues() has always read `activeClientGroupTenant() ||
// els.reportTenantFilter?.value`: the header client switcher is authoritative
// and it holds ONE group (PORTAL_FAMILY_NORTH_STAR.md:153 - "A header
// client-group switcher sets state.activeClientGroup = { value, label, orgIds }
// ... pushed into every surface's filters inside render()"), and
// lockSelectToClientScope/lockReportTenantFilterForScope then LOCK this control
// to that one company (UI_AUDIT_REDO_2026-08-24.md:71 records what happened the
// day those two disagreed). The prepared-report, Collections and Turnaround
// lanes all key their caches on one tenant name for the same reason. A
// multi-company report is a scope change, not a filter change, so it belongs to
// that switcher and not to this card.
const REPORT_FILTER_DEFINITIONS = Object.freeze([
  { key: "tenant", label: "Tenant", chip: "Client", control: "reportTenantFilter", facet: "tenant", multi: false, placeholder: "Search companies" },
  { key: "payer", label: "Payer", chip: "Payer", control: "reportPayerFilter", facet: "primaryPayer", multi: true, placeholder: "Search payers" },
  { key: "provider", label: "Provider", chip: "Provider", control: "reportProviderFilter", facet: "provider", multi: true, placeholder: "Search providers" },
  { key: "location", label: "Location", chip: "Location", control: "reportLocationFilter", facet: "facility", multi: true, placeholder: "Search locations" },
  { key: "aging", label: "Aging", chip: "Aging", control: "reportAgingFilter", facet: "aging", multi: true, placeholder: "Search aging bands" },
  { key: "status", label: "Claim Status", chip: "Status", control: "reportStatusFilter", facet: "claimStatus", multi: true, placeholder: "Search statuses" },
  { key: "match", label: "Finalized Match", chip: "Match", control: "reportMatchFilter", facet: "match", multi: true, placeholder: "Search match status" },
  { key: "followUpDue", label: "Follow-Up Due", chip: "Follow-up due", control: "reportFollowUpDueFilter", facet: "followUpDue", multi: true, placeholder: "Search due status" },
  { key: "action", label: "Follow-Up Action", chip: "Follow-Up Action", control: "reportActionFilter", facet: "action", multi: true, placeholder: "Search actions" },
  { key: "serviceMonth", label: "Service Month", chip: "Service month", control: "reportServiceMonthFilter", facet: "serviceMonth", multi: true, placeholder: "Search months" },
  { key: "procedure", label: "Procedure", chip: "CPT", control: "reportProcedureFilter", facet: "procedure", multi: true, placeholder: "Search CPT codes" },
  { key: "network", label: "In / Out of Network", chip: "Network", control: "reportNetworkFilter", facet: "", multi: true, placeholder: "" }
]);
const REPORT_FILTER_MULTI_KEYS = Object.freeze(REPORT_FILTER_DEFINITIONS.filter((row) => row.multi).map((row) => row.key));
const REPORT_FILTER_FACET_KEYS = Object.freeze(Object.fromEntries(REPORT_FILTER_DEFINITIONS.filter((row) => row.facet).map((row) => [row.key, row.facet])));

function reportFilterDefinition(key) {
  return REPORT_FILTER_DEFINITIONS.find((row) => row.key === key) || null;
}

function reportFilterKeyIsMultiSelect(key) {
  return REPORT_FILTER_MULTI_KEYS.includes(String(key));
}

function reportFilterLabel(key) {
  return reportFilterDefinition(key)?.label || String(key);
}

// ONE MIGRATION, AND IT IS THE IDENTITY FOR EVERY STORED BLOB.
//
// Every saved narrowing written before today is `{ payer: "Aetna", fromDate }`:
// a scalar per key. A scalar IS a one-value list, so it is read as one and the
// report comes back narrowed exactly as it was left - nobody's saved filter bar
// is dropped, and nothing had to be versioned. Include is still the default and
// is still never stored (see emptyFilterModes).
function readReportFilterStore() {
  const stored = (typeof state === "object" && state && state.reportFilters && typeof state.reportFilters === "object") ? state.reportFilters : {};
  const selections = {};
  const rawSelections = stored.selections && typeof stored.selections === "object" ? stored.selections : {};
  REPORT_FILTER_MULTI_KEYS.forEach((key) => {
    const fromList = Array.isArray(rawSelections[key]) ? rawSelections[key] : null;
    const legacy = typeof stored[key] === "string" ? String(stored[key]).trim() : "";
    const list = fromList || (legacy && legacy !== "All" ? [legacy] : []);
    const cleaned = [];
    list.forEach((value) => {
      const text = String(value == null ? "" : value).trim();
      if (!text || text === "All" || cleaned.includes(text)) return;
      cleaned.push(text);
    });
    if (cleaned.length) selections[key] = cleaned;
  });
  const tenant = String(stored.tenant || "").trim();
  return {
    tenant: tenant && tenant !== "All" ? tenant : "",
    selections,
    modes: ensureFilterModeState(stored.modes, selections),
    fromDate: String(stored.fromDate || ""),
    toDate: String(stored.toDate || "")
  };
}

function reportFilterSelectionState() {
  return readReportFilterStore().selections;
}

function reportFilterSelections(key) {
  const selections = reportFilterSelectionState();
  return Array.isArray(selections[key]) ? selections[key] : [];
}

function reportFilterModeState() {
  const store = readReportFilterStore();
  return ensureFilterModeState(store.modes, store.selections);
}

function reportFilterExcludes(key) {
  return filterModeExcludes(reportFilterModeState(), key);
}

// THE COMMIT. Values and direction move together or the card and the report
// disagree; the mirrors are re-synced before anything reads the bag, and the
// page index goes back to 1 because a different filter is a different book.
function applyReportFilterSelection(key, values, exclude, { revalidate = false } = {}) {
  if (!reportFilterKeyIsMultiSelect(key)) return;
  const store = readReportFilterStore();
  const cleaned = [];
  (Array.isArray(values) ? values : []).forEach((value) => {
    const text = String(value == null ? "" : value).trim();
    if (!text || text === "All" || cleaned.includes(text)) return;
    cleaned.push(text);
  });
  if (cleaned.length) store.selections[key] = cleaned;
  else delete store.selections[key];
  const modes = { ...store.modes };
  if (exclude) modes[key] = FILTER_MODE_EXCLUDE;
  else delete modes[key];
  store.modes = ensureFilterModeState(modes, store.selections);
  writeReportFilterStore(store);
  if (typeof reportPageState === "object" && reportPageState) reportPageState.page = 1;
  // Commit freshness before starting this filter read. The bubbling click
  // check would otherwise wait for it, discard it, and fetch it again.
  if (revalidate) void checkTrackerPublication();
  renderReports();
}

function writeReportFilterStore(store) {
  if (typeof state !== "object" || !state) return;
  const next = {};
  if (store.tenant && store.tenant !== "All") next.tenant = store.tenant;
  if (Object.keys(store.selections).length) next.selections = store.selections;
  const modes = ensureFilterModeState(store.modes, store.selections);
  if (Object.keys(modes).length) next.modes = modes;
  if (store.fromDate) next.fromDate = store.fromDate;
  if (store.toDate) next.toDate = store.toDate;
  state.reportFilters = next;
  syncReportFilterSelectMirrors();
  saveState();
}

// THE <select>s ARE MIRRORS NOW, NOT THE ANSWER.
//
// They still hold the OPTION LIST - the facet lane, lockReportTenantFilterForScope,
// replaceSelectOptions and the hosted-report slicer mirror all fill and read
// them, and none of that changed. What changed is that their .value is written
// FROM the selection rather than being it: the single included value, or "All"
// for none, for several, or for an exclusion. That keeps every legacy read
// honest (one value still reads as that value) without letting a lane that only
// looked at .value quietly report a wider population under a narrowed label -
// reportDatasetServerFilters, getReportClaims and the chip line all read the
// SELECTION, which is the only thing that decides a row.
function syncReportFilterSelectMirrors() {
  const selections = reportFilterSelectionState();
  const modes = reportFilterModeState();
  REPORT_FILTER_MULTI_KEYS.forEach((key) => {
    const select = els[reportFilterDefinition(key).control];
    if (!select) return;
    const values = Array.isArray(selections[key]) ? selections[key] : [];
    const single = values.length === 1 && !filterModeExcludes(modes, key) ? values[0] : "";
    if (single && typeof select.add === "function" && !Array.from(select.options || []).some((option) => option.value === single)) {
      select.add(new Option(single, single));
    }
    select.value = single || "All";
  });
}

// ---------------------------------------------------------------------------
// THE CARDS. One per filter, and they are THE All Data slicer card: same markup
// in index.html, same CSS, same draft machine, same Apply / Cancel / Select all
// / Clear / Include / Exclude / family controls (see filterCardHost).
// ---------------------------------------------------------------------------
let reportFilterLocalCounts = new Map();

function reportFilterServerCounts(key) {
  const facetKey = REPORT_FILTER_FACET_KEYS[key];
  if (!facetKey || !trackerApiEnabled()) return null;
  const rows = reportFilterFacetReadState.response?.facets?.[facetKey];
  if (!Array.isArray(rows)) return null;
  const counts = new Map();
  rows.forEach((row) => {
    const value = key === "action" ? followUpFilterValue(row?.value) : cleanFacetDisplayValue(row?.value);
    if (!value) return;
    counts.set(value, (counts.get(value) || 0) + Math.max(0, Number(row?.count) || 0));
  });
  return counts;
}

// Local/demo lane counts, folded at the grain each predicate actually tests:
// claim grain for the claim-level filters, SERVICE grain for the line-grain
// ones, because a count beside "97153" that counted claims would disagree with
// the number of lines the report then sums.
function buildReportFilterLocalCounts(claims = []) {
  const counts = new Map(REPORT_FILTER_MULTI_KEYS.map((key) => [key, new Map()]));
  const bump = (key, value) => {
    const text = String(value == null ? "" : value).trim();
    if (!text) return;
    const map = counts.get(key);
    if (map) map.set(text, (map.get(text) || 0) + 1);
  };
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    bump("payer", claim.primaryPayer);
    if (claim.secondaryPayer && claim.secondaryPayer !== claim.primaryPayer) bump("payer", claim.secondaryPayer);
    bump("provider", claim.renderingProvider);
    bump("location", claim.facility);
    bump("aging", claim.aging);
    bump("status", claim.claimStatus);
    bump("match", claim.match);
    const services = Array.isArray(claim.services) ? claim.services : [];
    services.forEach((service) => {
      bump("serviceMonth", serviceMonth(service.dos));
      bump("procedure", service.procedure);
      bump("network", networkBucket(firstPresent(service.contractNetwork, claim.contractNetwork, service.network, claim.network)));
      bump("action", followUpFilterRowValue(claim, service));
      const dueDate = firstPresent(service.followUpDate, service.follow_up_date, claim.followUpDate, claim.follow_up_date);
      bump("followUpDue", followUpDueStatusFromDate(dueDate));
    });
    if (!services.length) {
      bump("action", followUpFilterRowValue(claim));
      bump("followUpDue", followUpDueStatusFromDate(firstPresent(claim.followUpDate, claim.follow_up_date)));
    }
  });
  return counts;
}

function renderReportFilterCards() {
  REPORT_FILTER_MULTI_KEYS.forEach((key) => {
    const definition = reportFilterDefinition(key);
    const host = document.querySelector(`[data-report-filter-options="${key}"]`);
    if (!host) return;
    const cardId = filterCardId("report", key);
    const select = els[definition.control];
    const offered = Array.from(select?.options || [])
      .map((option) => String(option.value == null ? "" : option.value))
      .filter((value) => value && value !== "All");
    const selected = slicerSelectionsForRender(cardId);
    // AN ACTIVE SELECTION STAYS ON THE LIST even when the current facet read no
    // longer offers it: the same rule replaceSelectOptions applies to a value
    // whose facet has gone quiet. A value you cannot see is a value you cannot
    // untick, and the report would stay narrowed with no way back.
    const values = Array.from(new Set([...offered, ...selected]));
    if (key === "action") values.sort(compareFollowUpActionChoices);
    const serverCounts = reportFilterServerCounts(key);
    const counts = serverCounts || reportFilterLocalCounts.get(key) || new Map();
    const knowsCounts = Boolean(serverCounts) || !trackerApiEnabled();
    // TICKING IS NOT RE-RENDERING, and this card is repainted by every single
    // renderReports() - which a commit triggers. Rebuilding the checklist from
    // innerHTML while a reader is standing in it DETACHES the box they are
    // reaching for: the click lands on a node that is no longer in the
    // document, the scroll position jumps back to the top, and under auto-apply
    // (where a commit lands mid-burst) that happens between one tick and the
    // next. It is the same failure ensureSlicerPanelActions and the column
    // filter family button are both written to avoid.
    //
    // So the rows are rebuilt only when the OPTIONS themselves changed. While
    // the same values are on offer - the overwhelmingly common case, because
    // the counts are folded over the unfiltered scoped book - the existing
    // boxes are updated in place and the reader's nodes, scroll position and
    // focus all survive.
    const signature = JSON.stringify(values);
    if (host.dataset.reportFilterOptionSignature === signature) {
      host.querySelectorAll("input[data-slicer-value]").forEach((input) => {
        const active = selected.includes(input.dataset.slicerValue);
        input.checked = active;
        const row = input.closest(".slicer-option");
        row?.classList?.toggle("active", active);
        // Counts can arrive while someone is using this checkbox. Update the
        // count only; replacing the row loses focus and interrupts interaction.
        const count = counts.get(input.dataset.slicerValue);
        const showCount = knowsCounts && Number.isFinite(Number(count));
        let countCell = row?.querySelector("strong");
        if (showCount && row) {
          if (!countCell) {
            countCell = document.createElement("strong");
            row.append(countCell);
          }
          countCell.textContent = String(Number(count));
        } else {
          countCell?.remove();
        }
      });
      ensureSlicerPanelActions(cardId);
      updateSlicerPanelSummary(cardId, values.length, "", selected);
      return;
    }
    host.dataset.reportFilterOptionSignature = signature;
    host.innerHTML = values.map((value) => {
      const active = selected.includes(value);
      const display = key === "action" ? slicerDisplayValue("action", value) : value;
      const count = counts.get(value);
      // A count we do not have is left BLANK, never printed as 0: an asserted
      // zero is worse than a blank (the rule slicerFacetLane states for the
      // All Data rail, applied to the same card on this surface).
      const countCell = knowsCounts && Number.isFinite(Number(count)) ? `<strong>${Number(count)}</strong>` : "";
      return `<label class="slicer-option ${active ? "active" : ""}"><input type="checkbox" ${active ? "checked" : ""} data-report-filter-option data-slicer-key="${escapeHtml(cardId)}" data-slicer-value="${escapeHtml(value)}"><span>${escapeHtml(display)}</span>${countCell}</label>`;
    }).join("") || '<p class="slicer-empty muted-note">No choices for this filter yet.</p>';
    host.querySelectorAll("input[data-slicer-key]").forEach((input) => {
      input.addEventListener("change", () => setSlicerDraftSelection(input.dataset.slicerKey, input.dataset.slicerValue, input.checked));
    });
    ensureSlicerPanelActions(cardId);
    updateSlicerPanelSummary(cardId, values.length, "", selected);
    // A REPAINT MUST NOT UNDO A SEARCH. The option rows were just rebuilt, so
    // the narrowing has to be re-applied - including while the box still has
    // focus, which is exactly when a facet answer is most likely to land and
    // put the whole list back under a reader mid-word. The box's own VALUE is
    // never rewritten here; only the rows below it are hidden.
    const query = String(document.querySelector(`[data-slicer-search="${cardId}"]`)?.value || "").trim().toLocaleLowerCase();
    if (query) {
      Array.from(host.querySelectorAll(".slicer-option")).forEach((option) => {
        const input = option.querySelector("[data-slicer-value]");
        const label = key === "action" ? slicerDisplayValue("action", input?.dataset.slicerValue) : option.textContent || "";
        option.hidden = key === "action" ? !followUpActionChoiceMatches("action", label, query) : !String(label).toLocaleLowerCase().includes(query);
      });
    }
  });
  renderReportFilterChrome(reportFilterChromeEnabled);
}

// Opening one card closes the others, exactly as the All Data rail does, and a
// card that closes with a staged-but-unapplied draft discards it rather than
// leaving a count on a card whose boxes will be repainted from the applied set.
function positionReportFilterMenus() {
  const width = document.documentElement.clientWidth || window.innerWidth;
  document.querySelectorAll("#reportFilterGrid details[open] > .slicer-filter-body").forEach((body) => {
    body.style.left = "0px";
    const rect = body.getBoundingClientRect();
    body.style.left = `${Math.max(16 - rect.left, Math.min(0, width - 16 - rect.right))}px`;
  });
}
let reportFilterResizeBound = false;
function bindReportFilterCards() {
  if (!reportFilterResizeBound) {
    window.addEventListener("resize", positionReportFilterMenus);
    reportFilterResizeBound = true;
  }
  Array.from(document.querySelectorAll('#reportFilterGrid details[data-slicer-panel]')).forEach((panel) => {
    if (panel.dataset.reportFilterCardBound) return;
    panel.dataset.reportFilterCardBound = "1";
    panel.addEventListener("toggle", () => {
      const key = panel.dataset.slicerPanel || "";
      if (!panel.open) {
        if (slicerDraftKey === key) discardSlicerDraft({ returnFocus: false });
        return;
      }
      Array.from(document.querySelectorAll('#reportFilterGrid details[data-slicer-panel][open]')).forEach((other) => {
        if (other !== panel) other.open = false;
      });
      beginSlicerDraft(key, panel.querySelector("summary"));
      if (typeof requestReportFilterFacets === "function") requestReportFilterFacets();
      renderReportFilterCards();
      positionReportFilterMenus();
      panel.querySelector("[data-slicer-search]")?.focus?.({ preventScroll: true });
    });
  });
}

// MULTI-VALUE SINCE 2026-09-22. The twelve scalars in the bag below are
// MIRRORS - the single included value, or "All" for none, for several, or for
// an exclusion (see syncReportFilterSelectMirrors). `selections` and `modes`
// are the answer: every predicate lane, the request body, the local matcher and
// the chip line read THOSE, so a list can never be reported as "All". They are
// last in the object for the same reason tenantLabel is - the filter-bag
// contract check reads the predicate block with nothing in the way.
//
// The COMPANY is the one filter that is still a single value, and that is a
// scope contract rather than a shortcut; see REPORT_FILTER_DEFINITIONS.
function reportFilterValues() {
  return {
    // GLOBAL CLIENT SWITCHER is authoritative (see collectTrackerFilters).
    tenant: activeClientGroupTenant() || els.reportTenantFilter?.value || "All",
    payer: els.reportPayerFilter?.value || "All",
    provider: els.reportProviderFilter?.value || "All",
    location: els.reportLocationFilter?.value || "All",
    aging: els.reportAgingFilter?.value || "All",
    status: els.reportStatusFilter?.value || "All",
    match: els.reportMatchFilter?.value || "All",
    followUpDue: els.reportFollowUpDueFilter?.value || "All",
    action: els.reportActionFilter?.value || "All",
    serviceMonth: els.reportServiceMonthFilter?.value || "All",
    procedure: els.reportProcedureFilter?.value || "All",
    network: els.reportNetworkFilter?.value || "All",
    fromDate: els.reportFromDate?.value || "",
    toDate: els.reportToDate?.value || "",
    // WHAT ACTUALLY DECIDES A ROW (2026-09-22): the selection list per key and
    // the direction riding beside it.
    selections: reportFilterSelectionState(),
    modes: reportFilterModeState(),
    // DISPLAY ONLY, and last on purpose: the served tenant NAME above is the
    // key every predicate travels on, while the inventory label is what a
    // person calls the company. Kept out of the predicate block so the
    // filter-bag contract check reads the predicates without comment in the way.
    tenantLabel: (typeof activeClientGroupLabel === "function" ? activeClientGroupLabel() : "") || activeClientGroupTenant() || els.reportTenantFilter?.value || "All"
  };
}

// THE LOCAL MATCHER READS THE SAME LIST-AND-DIRECTION THE SERVER DOES.
//
// One helper decides every key, so the two passes cannot disagree about what a
// selection means. Its two rules are the ones listMatchSql states in SQL:
//   include - the row's value is one of the selected values
//   exclude - the row's value is NOT one of them, and a BLANK value survives
//             unless Blank itself was selected.
// The second half is the whole reason the server builds the complement instead
// of negating the inclusion, and it has to be true here too: an exclusion that
// silently dropped every blank-valued row would report a smaller book than the
// same filter does on the live lane.
function getReportClaims(claims = getClaims()) {
  const filters = reportFilterValues();
  const on = (key) => Boolean(filters.selections?.[key]?.length);
  // DECLARED HERE, not beside this function, because several shipped contract
  // verifiers lift getReportClaims out of app.js by its own source range and
  // run it in a vm: a helper one line above it would be a ReferenceError there,
  // and the proof would be red for a reason that has nothing to do with the
  // report.
  const matches = (key, value) => {
    const selected = Array.isArray(filters.selections?.[key]) ? filters.selections[key] : [];
    if (!selected.length) return true;
    const text = String(value == null ? "" : value).trim();
    const blankSelected = selected.some((candidate) => /^(blank|no payer)$/i.test(String(candidate)));
    const hit = text ? selected.includes(text) : blankSelected;
    return filterModeExcludes(filters.modes || {}, key) ? !hit : hit;
  };
  // THE READ IS STILL SHORT-CIRCUITED BY "IS THIS FILTER ON", and that is not
  // only about work avoided. `filters.x !== "All" && read(row)` never CALLED
  // read() for an unset filter; handing the read's result to matches() would
  // evaluate every derived value - the follow-up due bucket, the network
  // bucket, the service month - on every claim of every report whether or not
  // anybody filtered on them.
  const keeps = (key, read) => !on(key) || matches(key, read());
  return claims.filter((claim) => {
    if (filters.tenant !== "All" && claim.client !== filters.tenant) return false;
    // PAYER IS ONE FILTER OVER TWO SEATS, in both directions: include keeps a
    // claim either of whose payers matches, exclude requires BOTH to miss -
    // mirroring reportFilterPayerMatchSql on the server.
    if (on("payer")) {
      const primary = matches("payer", claim.primaryPayer);
      const secondary = matches("payer", claim.secondaryPayer);
      const excluding = filterModeExcludes(filters.modes || {}, "payer");
      if (excluding ? !(primary && secondary) : !(primary || secondary)) return false;
    }
    if (!keeps("provider", () => claim.renderingProvider)) return false;
    if (!keeps("location", () => claim.facility)) return false;
    if (!keeps("aging", () => claim.aging)) return false;
    if (!keeps("status", () => claim.claimStatus)) return false;
    if (!keeps("match", () => claim.match)) return false;
    const services = Array.isArray(claim.services) ? claim.services : [];
    const ownsAny = (service, keys) => keys.some((key) => Object.prototype.hasOwnProperty.call(service || {}, key));
    const actionKeys = ["action", "followUpAction", "follow_up_action"];
    const dueKeys = ["followUpDate", "follow_up_date"];
    const hasServiceAction = services.some((service) => ownsAny(service, actionKeys));
    const hasServiceDue = services.some((service) => ownsAny(service, dueKeys));
    const hasLineFilter = on("serviceMonth") || on("procedure") || on("network")
      || (on("action") && hasServiceAction)
      || (on("followUpDue") && hasServiceDue);
    if (hasLineFilter && !services.some((service) => {
      if (!keeps("serviceMonth", () => serviceMonth(service.dos))) return false;
      if (!keeps("procedure", () => String(service.procedure || ""))) return false;
      // A CLAIM CAN MIX NETWORKS, so this is a LINE test like procedure above and
      // not a claim test: the fee-schedule contract is matched per service line, so
      // a claim whose first line prices off an in-network contract and whose second
      // prices off an out-of-network one is genuinely both. services.some() keeps it,
      // which is also what the SQL predicate does - it filters at line grain and the
      // rows then fold into claims. Claim-level would have disagreed with the server.
      if (!keeps("network", () => networkBucket(firstPresent(service.contractNetwork, claim.contractNetwork, service.network, claim.network)))) return false;
      if (!keeps("action", () => followUpFilterRowValue(claim, service))) return false;
      if (!keeps("followUpDue", () => followUpDueStatusFromDate(hasServiceDue
        ? firstPresent(service.followUpDate, service.follow_up_date)
        : firstPresent(claim.followUpDate, claim.follow_up_date)))) return false;
      return true;
    })) return false;
    if (!hasLineFilter && !keeps("action", () => followUpFilterRowValue(claim))) return false;
    if (!hasLineFilter && !keeps("followUpDue", () => followUpDueStatusFromDate(firstPresent(claim.followUpDate, claim.follow_up_date)))) return false;
    if ((filters.fromDate || filters.toDate) && !claimHasServiceDateInRange(claim, filters.fromDate, filters.toDate)) return false;
    return true;
  });
}

function reportLocalHiddenFilterBook(scopedClaims = [], filteredClaims = []) {
  const book = Array.isArray(scopedClaims) ? scopedClaims : [];
  const filtered = Array.isArray(filteredClaims) ? filteredClaims : [];
  if (filtered.length || !book.length) return null;
  const outstanding = book.reduce((sum, claim) => {
    const financial = typeof claimFinancialView === "function" ? claimFinancialView(claim) : {};
    return sum + moneyNumber(financial.sourceOutstanding);
  }, 0);
  return { claims: book.length, outstanding };
}

function reportLocalEmptyFilterNote(hidden = null) {
  if (hidden && hidden.outstanding > 0) {
    return `No claims in the active filter. Filters are hiding ${money(hidden.outstanding)} across ${formatNounCount(hidden.claims, "claim")} still on this book. This is not $0.`;
  }
  if (hidden && hidden.claims) {
    return `No claims in the active filter. ${formatNounCount(hidden.claims, "claim")} remain on this book. That is not the same as $0.`;
  }
  return "No claims in the active filter.";
}

function claimHasServiceDateInRange(claim, fromDate, toDate) {
  const services = claim.services || [];
  if (!services.length) return false;
  return services.some((service) => {
    if (!service.dos) return false;
    if (fromDate && service.dos < fromDate) return false;
    if (toDate && service.dos > toDate) return false;
    return true;
  });
}

// THE FILTER BAR SURVIVES A RELOAD.
//
// Every other filter surface in this portal does (All Data rides state.slicers
// / state.columnFilters through loadState, the header company rides
// state.activeClientGroup), and Reports did not: a narrowed report came back
// as the whole book after a refresh, under a bar that then truthfully read
// "All data" - so the numbers moved and nothing said why. Only NON-DEFAULT
// values are stored, and they are replayed once per page from inside
// renderReportFilterOptions(), AFTER the option lists for this principal have
// been built: a stored COMPANY is therefore applied only if it is one this
// session may see, and lockReportTenantFilterForScope() still runs after and
// still wins. The header company scope is NOT stored here - it has its own.
const REPORT_FILTER_PERSISTED_CONTROLS = Object.freeze([
  ["reportTenantFilter", "tenant"], ["reportPayerFilter", "payer"], ["reportProviderFilter", "provider"],
  ["reportLocationFilter", "location"], ["reportAgingFilter", "aging"], ["reportStatusFilter", "status"],
  ["reportMatchFilter", "match"], ["reportFollowUpDueFilter", "followUpDue"], ["reportActionFilter", "action"],
  ["reportServiceMonthFilter", "serviceMonth"], ["reportProcedureFilter", "procedure"], ["reportNetworkFilter", "network"]
]);
let reportFilterSelectionsRestored = false;

// One key per principal: role, signed-in name and the access scope that decides
// which companies exist for this session. On a change every principal-derived
// choice list is emptied back to "All" rather than being left to whichever
// branch happens to run, and the stored narrowing is dropped with it - one
// person's payer is not another person's payer. Network is not in the list: its
// buckets are a fixed vocabulary, not a read of anybody's book.
let reportFilterOptionPrincipalKey = null;
function reportFilterOptionPrincipalIdentity() {
  const scope = (typeof currentAccessProfile === "function" ? currentAccessProfile()?.scope : null) || {};
  return JSON.stringify({ role: (typeof state === "object" && state && state.role) || "", user: (typeof state === "object" && state && state.activeUserName) || "", scope });
}
function resetReportFilterOptionsOnPrincipalChange() {
  const key = reportFilterOptionPrincipalIdentity();
  if (reportFilterOptionPrincipalKey === null) { reportFilterOptionPrincipalKey = key; return; }
  if (reportFilterOptionPrincipalKey === key) return;
  reportFilterOptionPrincipalKey = key;
  REPORT_FILTER_PERSISTED_CONTROLS.forEach(([control]) => {
    if (control === "reportNetworkFilter") return;
    const select = els[control];
    if (!select || !("innerHTML" in select)) return;
    select.innerHTML = '<option value="All">All</option>';
    select.value = "All";
  });
  if (els.reportFromDate) els.reportFromDate.value = "";
  if (els.reportToDate) els.reportToDate.value = "";
  reportTenantFilterScopeLocked = false;
  // AND THE ACCUMULATING LOCAL FACET CACHE, which is where the leak actually
  // lives. reportFilterFacetValues() keeps a per-facet "seen" array that is only
  // ever APPENDED to, so every value this browser has met stays on offer for the
  // life of the page. requestReportFilterFacets() empties it on a scope change -
  // but that function returns immediately when the tracker API is off, so off
  // that lane a session which had read the whole book went on offering the whole
  // book: other practices' payers, clinicians, sites and company names.
  reportFilterFacets = {};
  if (typeof state === "object" && state) { state.reportFilters = {}; saveState(); }
  // The stored narrowing is gone, so there is nothing left to replay; keeping
  // the one-shot latch closed stops a later render re-applying a cleared book.
  reportFilterSelectionsRestored = true;
  renderReportFilterChrome(reportFilterChromeEnabled);
}

// WHAT THIS STILL OWNS, AND WHAT MOVED.
//
// The multi-value checklists are committed by the card itself
// (applyReportFilterSelection), and they live in state.reportFilters.selections
// already - reading them back off a <select> that is now only a mirror would
// flatten every list to one value or to "All" the moment a date box changed.
// This keeps the two controls that are genuinely still single DOM values: the
// company, and the two date boxes.
function saveReportFilterSelections() {
  if (typeof state !== "object" || !state) return;
  const store = readReportFilterStore();
  const tenant = String(els.reportTenantFilter?.value || "All");
  store.tenant = tenant && tenant !== "All" ? tenant : "";
  store.fromDate = String(els.reportFromDate?.value || "");
  store.toDate = String(els.reportToDate?.value || "");
  writeReportFilterStore(store);
}

function restoreReportFilterSelections() {
  if (reportFilterSelectionsRestored) return;
  reportFilterSelectionsRestored = true;
  const stored = readReportFilterStore();
  let restored = false;
  // A COMPANY IS SCOPE, so it is replayed only when this principal is already
  // offered it. The checklists need no replay at all any more: they ARE state
  // (state.reportFilters.selections), they are read straight out of it by
  // reportFilterValues, and renderReportFilterCards keeps a selected value on
  // the list even when the current facet read no longer offers it - which is
  // what the per-control retention below used to be for.
  if (stored.tenant && els.reportTenantFilter) {
    const offered = Array.from(els.reportTenantFilter.options || []).some((option) => option.value === stored.tenant);
    if (offered) { els.reportTenantFilter.value = stored.tenant; restored = true; }
  }
  if (Object.keys(stored.selections).length) restored = true;
  if (els.reportFromDate && stored.fromDate) { els.reportFromDate.value = stored.fromDate; restored = true; }
  if (els.reportToDate && stored.toDate) { els.reportToDate.value = stored.toDate; restored = true; }
  // setReportsAudienceChrome() wrote the bar BEFORE this ran, so without this
  // the first paint after a reload says "All data / 0 active" over a narrowed
  // report - the exact untruth the bar exists to prevent.
  if (restored) renderReportFilterChrome(reportFilterChromeEnabled);
}

// THE BAR SAYS WHAT IS ON, IN BOTH STATES. A collapsed panel hides twelve
// controls; the count beside the button is what tells a reader the report they
// are looking at is narrowed, without opening anything.
let reportFilterChromeEnabled = true;
function renderReportFilterChrome(enabled = true) {
  reportFilterChromeEnabled = enabled;
  const summary = els.reportFilterSummaryText || document.getElementById("reportFilterSummaryText");
  if (summary) summary.textContent = reportFilterSummaryText(enabled);
  const badge = els.reportFilterActiveCount || document.getElementById("reportFilterActiveCount");
  if (!badge) return;
  const active = enabled ? reportActiveFilterDescriptions() : [];
  badge.textContent = `${active.length} active`;
  badge.classList.toggle("is-filtered", active.length > 0);
  badge.title = active.length ? active.join(" · ") : "";
}

const REPORT_FILTERS_OPEN_STORAGE_KEY = "tms.portal.reportFiltersOpen";
function reportFilterPanelIsOpen() {
  const grid = els.reportFilterGrid || document.getElementById("reportFilterGrid");
  return Boolean(grid) && !grid.hasAttribute("hidden");
}
function setReportFilterPanelOpen(open, { persist = true } = {}) {
  const grid = els.reportFilterGrid || document.getElementById("reportFilterGrid");
  const button = els.reportFilterToggleBtn || document.getElementById("reportFilterToggleBtn");
  const host = els.reportFilterDisclosure || document.getElementById("reportFilterDisclosure");
  if (!grid || !button) return;
  if (open) grid.removeAttribute("hidden");
  else grid.setAttribute("hidden", "");
  button.setAttribute("aria-expanded", open ? "true" : "false");
  button.innerHTML = open
    ? 'Hide filters <span class="slicer-toggle-caret" aria-hidden="true">▴</span>'
    : 'Show filters <span class="slicer-toggle-caret" aria-hidden="true">▾</span>';
  host?.classList?.toggle?.("is-open", open);
  if (persist) { try { localStorage.setItem(REPORT_FILTERS_OPEN_STORAGE_KEY, open ? "1" : "0"); } catch (_) {} }
}
function bindReportFilterDisclosure() {
  const button = els.reportFilterToggleBtn || document.getElementById("reportFilterToggleBtn");
  if (!button || button.dataset.reportFilterToggleBound) return;
  button.dataset.reportFilterToggleBound = "1";
  button.addEventListener("click", () => {
    setReportFilterPanelOpen(!reportFilterPanelIsOpen());
    // Facet choices are read for the controls that are actually on screen, so
    // opening the panel is what makes them visible and has to ask for them.
    if (typeof requestReportFilterFacets === "function") requestReportFilterFacets();
    if (typeof renderReportFacetChoiceControls === "function") renderReportFacetChoiceControls();
    if (typeof bindReportFilterCards === "function") bindReportFilterCards();
    if (typeof renderReportFilterCards === "function") renderReportFilterCards();
  });
  let stored = null;
  try { stored = localStorage.getItem(REPORT_FILTERS_OPEN_STORAGE_KEY); } catch (_) {}
  // Closed is still the default here (Reports is a reading surface), but a
  // restored narrowing opens the panel so the reader can see what is on.
  const anyActive = reportActiveFilterDescriptions().length > 0;
  setReportFilterPanelOpen(stored === "1" || (stored !== "0" && anyActive), { persist: false });
  bindReportFilterCards();
}

function clearReportFilters() {
  // Clear all means EVERY checklist, every direction and both dates - a mode
  // left behind on an emptied list is how a cleared bar comes back reading
  // "1 active" (the rule ensureFilterModeState states, applied to the whole bar).
  [els.reportTenantFilter, els.reportPayerFilter, els.reportProviderFilter, els.reportLocationFilter, els.reportAgingFilter, els.reportStatusFilter, els.reportMatchFilter, els.reportFollowUpDueFilter, els.reportActionFilter, els.reportServiceMonthFilter, els.reportProcedureFilter, els.reportNetworkFilter].forEach((select) => {
    if (select) select.value = "All";
  });
  if (els.reportFromDate) els.reportFromDate.value = "";
  if (els.reportToDate) els.reportToDate.value = "";
  // A staged draft on an open card must go with them, or Cancel/Apply would
  // put the cleared filter straight back.
  if (slicerDraftKey && filterCardSurface(slicerDraftKey) === "report") discardSlicerDraft({ returnFocus: false });
  // A clear that is not persisted comes back on the next reload.
  writeReportFilterStore({ tenant: "", selections: {}, modes: {}, fromDate: "", toDate: "" });
  renderReports();
}

function reportFinancialBuckets(claims) {
  return claims.reduce((buckets, claim) => {
    const financial = claimFinancialView(claim);
    const bucket = hasCalculationGuards(claim) ? buckets.provisional : buckets.final;
    bucket.claims += 1;
    bucket.expected += financial.expectedReimbursement;
    bucket.paid += financial.totalInsurancePaid;
    bucket.owed += financial.sourceOutstanding;
    bucket.billed += financial.charges;
    return buckets;
  }, {
    final: { claims: 0, billed: 0, expected: 0, paid: 0, owed: 0 },
    provisional: { claims: 0, billed: 0, expected: 0, paid: 0, owed: 0 }
  });
}

function buildExecutiveAnalytics(claims, includeFinancials = true) {
  const monthlyTrends = monthlyTrendRows(claims, includeFinancials);
  const payerBehavior = payerBehaviorRows(claims, includeFinancials);
  const cashFlow = cashFlowRows(claims, includeFinancials);
  const highRiskAr = highRiskArRows(claims, includeFinancials);
  const providerPerformance = providerPerformanceRows(claims, includeFinancials);
  const denialTrends = denialTrendRows(claims, includeFinancials);
  const underpaymentExposure = underpaymentExposureRows(claims, includeFinancials);
  // legacy workbook Monthly Data Summary By Payer parallel
  const monthlyByPayer = monthlyWorkbookByPayerRows(claims, includeFinancials);
  return {
    monthlyTrends,
    payerBehavior,
    cashFlow,
    highRiskAr,
    providerPerformance,
    denialTrends,
    underpaymentExposure,
    monthlyByPayer,
    chartPayloads: buildExecutiveChartPayloads({
      monthlyTrends,
      payerBehavior,
      cashFlow,
      highRiskAr,
      providerPerformance,
      denialTrends,
      underpaymentExposure,
      monthlyByPayer,
      includeFinancials
    })
  };
}

function buildExecutiveChartPayloads({
  monthlyTrends = [],
  payerBehavior = [],
  cashFlow = [],
  highRiskAr = [],
  providerPerformance = [],
  denialTrends = [],
  underpaymentExposure = [],
  monthlyByPayer = [],
  includeFinancials = true
} = {}) {
  const redactedFinancials = !includeFinancials;
  return {
    schema: "tms-master-portal.executive-chart-payloads.v1",
    redactedFinancials,
    charts: [
      chartPayload("monthly_revenue_ar", "Monthly Revenue And AR", "combo", "month", monthlyTrends, [
        ["billed", "Billed", "money", redactedFinancials],
        ["collected", "Collected by TMS", "money", redactedFinancials],
        ["outstanding", "AR In Motion", "money", redactedFinancials],
        ["collectionRate", "Resolution Rate", "percent", false],
        ["denialRate", "Appeals Pipeline", "percent", false]
      ]),
      chartPayload("cash_flow", "Cash Flow", "line", "month", cashFlow, [
        ["collected", "Collected", "money", redactedFinancials],
        ["netCash", "Net Cash", "money", redactedFinancials],
        ["cumulativeOutstanding", "Cumulative AR", "money", redactedFinancials]
      ]),
      chartPayload("payer_behavior", "Payer Behavior", "bar", "payer", payerBehavior, [
        ["claims", "Claims", "count", false],
        ["collectionRate", "Resolution Rate", "percent", false],
        ["denialRate", "Appeals Pipeline", "percent", false],
        ["outstanding", "AR In Motion", "money", redactedFinancials]
      ]),
      chartPayload("provider_performance", "Provider Performance", "bar", "provider", providerPerformance, [
        ["claims", "Claims", "count", false],
        ["collectionRate", "Resolution Rate", "percent", false],
        ["denialRate", "Appeals Pipeline", "percent", false],
        ["outstanding", "AR In Motion", "money", redactedFinancials]
      ]),
      // Positive framing: chart titles describe what TMS is actively
      // doing about the issue, not the issue alone.
      chartPayload("denial_trends", "Denials In Appeal", "stacked", "month", denialTrends, [
        ["claims", "Claims In Appeal", "count", false],
        ["outstanding", "Recovery Opportunity", "money", redactedFinancials],
        ["guarded", "Awaiting Adjudication", "count", false]
      ]),
      chartPayload("underpayment_exposure", "Underpayment Recovery", "bar", "payer", underpaymentExposure, [
        ["underpaidClaims", "Recovery Targets", "count", false],
        ["underpaidAmount", "Recovery Opportunity", "money", redactedFinancials],
        ["expected", "Expected", "money", redactedFinancials],
        ["collected", "Collected by TMS", "money", redactedFinancials]
      ]),
      chartPayload("high_risk_ar", "Premium AR Focus", "bar", "claimId", highRiskAr, [
        ["priority", "Priority", "score", false],
        ["outstanding", "AR In Motion", "money", redactedFinancials]
      ]),
      // legacy workbook 'MONTHLY DATA SUMMARY BY PAYER' — stacked bar of monthly
      // collections, one stack segment per top-N payer + 'Other Payers'.
      // The chart is critical for clients (per user direction): it's the
      // single chart the client view should expose.
      monthlyByPayerChartPayload(monthlyByPayer, redactedFinancials)
    ],
    exportDatasets: [
      chartDataset("monthly_trends", "Monthly Revenue And AR Trend", monthlyTrends),
      chartDataset("cash_flow", "Cash Flow", cashFlow),
      chartDataset("payer_behavior", "Payer Behavior", payerBehavior),
      chartDataset("provider_performance", "Provider Performance", providerPerformance),
      chartDataset("denial_trends", "Denials In Appeal", denialTrends),
      chartDataset("underpayment_exposure", "Underpayment Recovery", underpaymentExposure),
      chartDataset("high_risk_ar", "Premium AR Focus", highRiskAr),
      chartDataset("monthly_by_payer", "Monthly Data Summary By Payer", monthlyByPayer)
    ]
  };
}

// Hand-rolled chart payload for the legacy workbook 'Monthly Data Summary By Payer'
// stacked bar — bypasses chartPayload() because the source rows need to
// pivot into one column per payer first.
function monthlyByPayerChartPayload(monthlyByPayer, redactedFinancials) {
  const pivot = monthlyPayerStackedRows(monthlyByPayer, 8);
  return {
    id: "monthly_by_payer",
    title: "Monthly Data Summary By Payer",
    type: "stacked",
    xField: "month",
    rows: monthlyByPayer,
    labels: pivot.labels,
    series: pivot.series.map((s) => ({
      key: s.label,
      label: s.label,
      valueType: "money",
      redacted: redactedFinancials,
      values: redactedFinancials ? s.values.map(() => null) : s.values
    }))
  };
}

function chartPayload(id, title, type, xField, rows, seriesSpecs) {
  return {
    id,
    title,
    type,
    xField,
    labels: rows.map((row) => row[xField]),
    series: seriesSpecs.map(([key, label, valueType, redacted]) => ({
      key,
      label,
      valueType,
      redacted,
      values: rows.map((row) => redacted ? null : reportChartValue(row[key]))
    }))
  };
}

function chartDataset(id, label, rows) {
  return {
    id,
    label,
    rowCount: rows.length,
    columns: inferChartColumns(rows),
    rows
  };
}

function inferChartColumns(rows) {
  const keys = Array.from(rows.reduce((set, row) => {
    Object.keys(row || {}).forEach((key) => set.add(key));
    return set;
  }, new Set()));
  return keys.map((key) => ({
    key,
    label: key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/\b\w/g, (letter) => letter.toUpperCase()),
    valueType: reportChartValueType(key),
    redacted: rows.some((row) => row && row[key] === null)
  }));
}

function reportChartValue(value) {
  return typeof value === "number" ? Number(value.toFixed(2)) : value ?? null;
}

function reportChartValueType(key) {
  if (/rate/i.test(key)) return "percent";
  if (/billed|expected|collected|outstanding|cash|paid|owed/i.test(key)) return "money";
  if (/claims|lines|count|guarded|risk|unpaid|underpaid|priority/i.test(key)) return "count";
  return "text";
}

function monthlyTrendRows(claims, includeFinancials = true) {
  const grouped = new Map();
  claims.forEach((claim) => {
    const financial = claimFinancialView(claim);
    const month = claimMonth(claim);
    const row = grouped.get(month) || {
      month,
      claims: 0,
      serviceLines: 0,
      billed: 0,
      expected: 0,
      collected: 0,
      outstanding: 0,
      denied: 0,
      highRisk: 0,
      guarded: 0
    };
    row.claims += 1;
    row.serviceLines += (claim.services || []).length;
    row.billed += financial.charges;
    row.expected += financial.expectedReimbursement;
    row.collected += financial.totalInsurancePaid;
    row.outstanding += financial.sourceOutstanding;
    if (isDeniedClaim(claim)) row.denied += 1;
    if (workPriorityScore(claim) >= 80) row.highRisk += 1;
    if (hasCalculationGuards(claim)) row.guarded += 1;
    grouped.set(month, row);
  });
  return Array.from(grouped.values())
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((row) => ({
      month: row.month,
      claims: row.claims,
      serviceLines: row.serviceLines,
      billed: reportMoneyValue(row.billed, includeFinancials),
      expected: reportMoneyValue(row.expected, includeFinancials),
      collected: reportMoneyValue(row.collected, includeFinancials),
      outstanding: reportMoneyValue(row.outstanding, includeFinancials),
      // TWO DECIMALS, NOT AN INTEGER. Rounding here reported 96.16% as a flat 96%,
      // and on a book this size a sixth of a percent is real money - it is also the
      // difference between a rate that moved and one that looks static week to week.
      // The backend already serves the unrounded figure; this was the last place
      // throwing it away.
      collectionRate: row.expected > 0 ? Math.round((row.collected / row.expected) * 10000) / 100 : null,
      denialRate: row.claims > 0 ? Math.round((row.denied / row.claims) * 100) : 0,
      highRisk: row.highRisk,
      guarded: row.guarded
    }));
}

// legacy workbook 'MONTHLY DATA SUMMARY BY PAYER' parallel — long-form rows of
// (payer, month, collected). Returned as flat rows so the chart layer
// can pivot into stacked or grouped bars.
// (Named *Workbook* to avoid collision with the existing
// monthlyPayerSummaryRows() that builds a different watchlist shape.)
function monthlyWorkbookByPayerRows(claims, includeFinancials = true) {
  const grouped = new Map();
  claims.forEach((claim) => {
    const financial = claimFinancialView(claim);
    const payer = String(claim.primaryPayer || "Unknown").trim() || "Unknown";
    const month = claimMonth(claim);
    const key = `${payer}::${month}`;
    const row = grouped.get(key) || {
      payer, month, claims: 0, collected: 0, expected: 0, outstanding: 0
    };
    row.claims += 1;
    row.collected += financial.totalInsurancePaid;
    row.expected += financial.expectedReimbursement;
    row.outstanding += financial.sourceOutstanding;
    grouped.set(key, row);
  });
  return Array.from(grouped.values())
    .sort((a, b) => a.month.localeCompare(b.month) || a.payer.localeCompare(b.payer))
    .map((row) => ({
      payer: row.payer,
      month: row.month,
      claims: row.claims,
      collected: reportMoneyValue(row.collected, includeFinancials),
      expected: reportMoneyValue(row.expected, includeFinancials),
      outstanding: reportMoneyValue(row.outstanding, includeFinancials)
    }));
}

// Pivot the long-form rows above into one-row-per-month with a column
// per payer. This is the shape Plotly's stacked bar wants for the legacy workbook
// 'MONTHLY DATA SUMMARY BY PAYER' chart.
function monthlyPayerStackedRows(monthlyPayer, limit = 8) {
  if (!Array.isArray(monthlyPayer) || !monthlyPayer.length) return { labels: [], series: [] };
  // Top N payers by total outstanding so the legend isn't 50 entries.
  const payerTotals = new Map();
  monthlyPayer.forEach((r) => {
    payerTotals.set(r.payer, (payerTotals.get(r.payer) || 0) + moneyNumber(r.outstanding || 0) + moneyNumber(r.collected || 0));
  });
  const topPayers = [...payerTotals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([p]) => p);
  const topSet = new Set(topPayers);
  const labels = [...new Set(monthlyPayer.map((r) => r.month))].sort();
  const series = topPayers.map((payer) => ({
    label: payer,
    valueType: "money",
    values: labels.map((month) => {
      const hit = monthlyPayer.find((r) => r.payer === payer && r.month === month);
      return hit ? moneyNumber(hit.collected || 0) : 0;
    })
  }));
  // Bucket all other payers into "Other" so totals don't lie
  const otherValues = labels.map((month) => monthlyPayer
    .filter((r) => r.month === month && !topSet.has(r.payer))
    .reduce((sum, r) => sum + moneyNumber(r.collected || 0), 0));
  if (otherValues.some((v) => v > 0)) {
    series.push({ label: "Other Payers", valueType: "money", values: otherValues });
  }
  return { labels, series };
}

function payerBehaviorRows(claims, includeFinancials = true) {
  const grouped = new Map();
  claims.forEach((claim) => {
    const financial = claimFinancialView(claim);
    const payer = claim.primaryPayer || claim.secondaryPayer || "Unknown";
    const row = grouped.get(payer) || { payer, claims: 0, billed: 0, expected: 0, collected: 0, outstanding: 0, denied: 0, highRisk: 0, guarded: 0, unpaid: 0, underpaid: 0 };
    row.claims += 1;
    row.billed += financial.charges;
    row.expected += financial.expectedReimbursement;
    row.collected += financial.totalInsurancePaid;
    row.outstanding += financial.sourceOutstanding;
    if (isDeniedClaim(claim)) row.denied += 1;
    if (workPriorityScore(claim) >= 80) row.highRisk += 1;
    if (hasCalculationGuards(claim)) row.guarded += 1;
    if (claim.match === "Not Paid" && financial.sourceOutstanding > 0) row.unpaid += 1;
    if (financial.underpayment > 0) row.underpaid += 1;
    grouped.set(payer, row);
  });
  return Array.from(grouped.values())
    .map((row) => ({
      payer: row.payer,
      claims: row.claims,
      billed: reportMoneyValue(row.billed, includeFinancials),
      expected: reportMoneyValue(row.expected, includeFinancials),
      collected: reportMoneyValue(row.collected, includeFinancials),
      outstanding: reportMoneyValue(row.outstanding, includeFinancials),
      collectionRate: row.expected > 0 ? Math.round((row.collected / row.expected) * 10000) / 100 : null,
      denialRate: row.claims > 0 ? Math.round((row.denied / row.claims) * 100) : 0,
      highRisk: row.highRisk,
      guarded: row.guarded,
      unpaid: row.unpaid,
      underpaid: row.underpaid
    }))
    .sort((a, b) => moneyNumber(b.outstanding || 0) - moneyNumber(a.outstanding || 0) || b.highRisk - a.highRisk);
}

function cashFlowRows(claims, includeFinancials = true) {
  let cumulativeOutstanding = 0;
  return monthlyTrendRows(claims, true).map((row) => {
    cumulativeOutstanding += moneyNumber(row.outstanding || 0);
    return {
      month: row.month,
      billed: reportMoneyValue(row.billed, includeFinancials),
      collected: reportMoneyValue(row.collected, includeFinancials),
      netCash: reportMoneyValue(moneyNumber(row.collected || 0) - moneyNumber(row.billed || 0), includeFinancials),
      outstandingAdded: reportMoneyValue(row.outstanding, includeFinancials),
      cumulativeOutstanding: reportMoneyValue(cumulativeOutstanding, includeFinancials),
      collectionRate: row.collectionRate
    };
  });
}

function highRiskArRows(claims, includeFinancials = true) {
  return claims
    .filter((claim) => claimFinancialView(claim).sourceOutstanding > 0)
    .map((claim) => ({
      claimId: claim.claimId,
      tenant: claim.client,
      payer: claim.primaryPayer || claim.secondaryPayer || "Unknown",
      outstanding: reportMoneyValue(claimFinancialView(claim).sourceOutstanding, includeFinancials),
      priority: workPriorityScore(claim),
      guarded: hasCalculationGuards(claim),
      reason: workReason(claim)
    }))
    .sort((a, b) => b.priority - a.priority || moneyNumber(b.outstanding || 0) - moneyNumber(a.outstanding || 0))
    .slice(0, 25);
}

function providerPerformanceRows(claims, includeFinancials = true) {
  const grouped = new Map();
  claims.forEach((claim) => {
    const financial = claimFinancialView(claim);
    const provider = claim.renderingProvider || "Unknown";
    const row = grouped.get(provider) || {
      provider,
      claims: 0,
      serviceLines: 0,
      billed: 0,
      expected: 0,
      collected: 0,
      outstanding: 0,
      denied: 0,
      highRisk: 0,
      guarded: 0,
      underpaidClaims: 0,
      noRecentTouch: 0
    };
    row.claims += 1;
    row.serviceLines += (claim.services || []).length;
    row.billed += financial.charges;
    row.expected += financial.expectedReimbursement;
    row.collected += financial.totalInsurancePaid;
    row.outstanding += financial.sourceOutstanding;
    if (isDeniedClaim(claim)) row.denied += 1;
    if (workPriorityScore(claim) >= 80) row.highRisk += 1;
    if (hasCalculationGuards(claim)) row.guarded += 1;
    if (financial.underpayment > 0) row.underpaidClaims += 1;
    if (isStaleClaim(claim)) row.noRecentTouch += 1;
    grouped.set(provider, row);
  });
  return Array.from(grouped.values())
    .map((row) => ({
      provider: row.provider,
      claims: row.claims,
      serviceLines: row.serviceLines,
      billed: reportMoneyValue(row.billed, includeFinancials),
      expected: reportMoneyValue(row.expected, includeFinancials),
      collected: reportMoneyValue(row.collected, includeFinancials),
      outstanding: reportMoneyValue(row.outstanding, includeFinancials),
      collectionRate: row.expected > 0 ? Math.round((row.collected / row.expected) * 10000) / 100 : null,
      denialRate: row.claims > 0 ? Math.round((row.denied / row.claims) * 100) : 0,
      highRisk: row.highRisk,
      guarded: row.guarded,
      underpaidClaims: row.underpaidClaims,
      noRecentTouch: row.noRecentTouch
    }))
    .sort((a, b) => moneyNumber(b.outstanding || 0) - moneyNumber(a.outstanding || 0) || b.highRisk - a.highRisk);
}

function denialTrendRows(claims, includeFinancials = true) {
  const grouped = new Map();
  claims.filter(isDeniedClaim).forEach((claim) => {
    const financial = claimFinancialView(claim);
    const month = claimMonth(claim);
    const reason = denialReason(claim);
    const key = `${month}::${reason}`;
    const row = grouped.get(key) || { month, reason, claims: 0, outstanding: 0, guarded: 0 };
    row.claims += 1;
    row.outstanding += financial.sourceOutstanding;
    if (hasCalculationGuards(claim)) row.guarded += 1;
    grouped.set(key, row);
  });
  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      outstanding: reportMoneyValue(row.outstanding, includeFinancials)
    }))
    .sort((a, b) => a.month.localeCompare(b.month) || moneyNumber(b.outstanding || 0) - moneyNumber(a.outstanding || 0));
}

function underpaymentExposureRows(claims, includeFinancials = true) {
  const grouped = new Map();
  claims.filter((claim) => claimFinancialView(claim).underpayment > 0).forEach((claim) => {
    const financial = claimFinancialView(claim);
    const payer = claim.primaryPayer || claim.secondaryPayer || "Unknown";
    const row = grouped.get(payer) || {
      payer,
      underpaidClaims: 0,
      expected: 0,
      collected: 0,
      underpaidAmount: 0,
      highRisk: 0,
      guarded: 0,
      oldestAging: ""
    };
    row.underpaidClaims += 1;
    row.expected += financial.expectedReimbursement;
    row.collected += financial.totalInsurancePaid;
    row.underpaidAmount += financial.underpayment;
    row.oldestAging = olderAgingBucket(row.oldestAging, claim.aging);
    if (workPriorityScore(claim) >= 80) row.highRisk += 1;
    if (hasCalculationGuards(claim)) row.guarded += 1;
    grouped.set(payer, row);
  });
  return Array.from(grouped.values())
    .map((row) => ({
      payer: row.payer,
      underpaidClaims: row.underpaidClaims,
      expected: reportMoneyValue(row.expected, includeFinancials),
      collected: reportMoneyValue(row.collected, includeFinancials),
      underpaidAmount: reportMoneyValue(row.underpaidAmount, includeFinancials),
      recoveryRate: row.expected > 0 ? Math.round((row.collected / row.expected) * 100) : null,
      oldestAging: row.oldestAging || "Unknown",
      highRisk: row.highRisk,
      guarded: row.guarded
    }))
    .sort((a, b) => moneyNumber(b.underpaidAmount || 0) - moneyNumber(a.underpaidAmount || 0) || b.highRisk - a.highRisk);
}

function claimMonth(claim) {
  const dos = (claim.services || []).map((service) => service.dos || "").filter(Boolean).sort()[0] || claim.dos || claim.billDate || "";
  return /^\d{4}-\d{2}-\d{2}/.test(dos) ? dos.slice(0, 7) : "Unknown";
}

function claimBilled(claim) {
  return (claim.services || []).reduce((sum, service) => sum + moneyNumber(service.charges || 0), 0);
}

function reportMoneyValue(value, includeFinancials) {
  return includeFinancials ? moneyNumber(value) : null;
}

function parseMoneyNumber(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return value;
  const raw = String(value).trim();
  if (!raw) return 0;
  const negative = /^\(.*\)$/.test(raw);
  const normalized = raw
    .replace(/[,$]/g, "")
    .replace(/^\((.*)\)$/, "$1")
    .trim();
  const number = Number(normalized);
  return negative && Number.isFinite(number) ? -number : number;
}

// Mirrors backend/moneyParsing.js. Exact half cents round away from zero so
// credits and charges use the same accounting rule without binary-float drift.
function roundMoneyToCents(value) {
  const number = parseMoneyNumber(value);
  if (!Number.isFinite(number) || number === 0) return 0;

  const negative = number < 0;
  const [coefficient, exponentText = "0"] = Math.abs(number).toString().toLowerCase().split("e");
  const [whole, fraction = ""] = coefficient.split(".");
  const digits = `${whole}${fraction}`.replace(/^0+(?=\d)/, "") || "0";
  const decimalPlaces = fraction.length - Number(exponentText);
  let cents;

  if (decimalPlaces <= 2) {
    cents = BigInt(digits) * (10n ** BigInt(2 - decimalPlaces));
  } else {
    const divisor = 10n ** BigInt(decimalPlaces - 2);
    const magnitude = BigInt(digits);
    cents = magnitude / divisor;
    if ((magnitude % divisor) * 2n >= divisor) cents += 1n;
  }

  const signedCents = negative ? -cents : cents;
  const centsNumber = Number(signedCents);
  const rounded = Number.isFinite(centsNumber)
    ? centsNumber / 100
    : Number(signedCents / 100n) + (Number(signedCents % 100n) / 100);
  return Object.is(rounded, -0) ? 0 : rounded;
}

function moneyNumber(value) {
  return roundMoneyToCents(value);
}

function moneyDelta(...values) {
  if (!values.length) return 0;
  return moneyNumber(values.slice(1).reduce((sum, value) => sum - moneyNumber(value), moneyNumber(values[0])));
}

function moneyRatio(numerator, denominator) {
  const denominatorNumber = moneyNumber(denominator);
  if (denominatorNumber <= 0) return null;
  return moneyNumber(numerator) / denominatorNumber;
}

function percentNumber(value, options = {}) {
  const { percentStringAsRatio = false } = options;
  if (typeof value === "string") {
    const raw = value.trim();
    if (raw.endsWith("%")) {
      const number = parseMoneyNumber(raw.slice(0, -1));
      if (!Number.isFinite(number)) return NaN;
      return percentStringAsRatio ? number / 100 : number;
    }
  }
  return parseMoneyNumber(value);
}

function quantityNumber(value) {
  const number = parseMoneyNumber(value);
  return Number.isFinite(number) && number > 0 ? Number(number.toFixed(2)) : 0;
}

function nonNegativeMoneyFilterValue(value) {
  const number = parseMoneyNumber(value);
  return Number.isFinite(number) && number > 0 ? Number(number.toFixed(2)) : 0;
}

function nonNegativeIntegerValue(value) {
  if (value == null || value === "") return 0;
  const number = parseMoneyNumber(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
}

function booleanFlagValue(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value === null || value === undefined || value === "") return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "n", "off"].includes(normalized)) return false;
  }
  return Boolean(value);
}

function formatCount(value) {
  return nonNegativeIntegerValue(value).toLocaleString("en-US");
}

// "5 other companys." shipped in the Manage Users rail because the default
// plural was singular + "s". Consonant + y takes -ies; a vowel before the y does
// not, so "day" still pluralises to "days".
function defaultPluralNoun(singular) {
  const word = String(singular == null ? "" : singular);
  return /[^aeiou]y$/i.test(word) ? `${word.slice(0, -1)}ies` : `${word}s`;
}

function formatNounCount(value, singular, plural = defaultPluralNoun(singular)) {
  const count = nonNegativeIntegerValue(value);
  return `${formatCount(count)} ${count === 1 ? singular : plural}`;
}

function claimOutstanding(claim) {
  return claimFinancialView(claim).sourceOutstanding;
}

function claimUnderpayment(claim) {
  return claimFinancialView(claim).underpayment;
}

function claimOverpayment(claim) {
  return claimFinancialView(claim).overpayment;
}

function sumClaimMoney(claims = [], valueForClaim = claimOutstanding) {
  return (Array.isArray(claims) ? claims : []).reduce((sum, claim) => sum + moneyNumber(valueForClaim(claim)), 0);
}

function sumClaimOutstanding(claims = []) {
  return sumClaimMoney(claims, claimOutstanding);
}

function openArClaims(claims = []) {
  return (Array.isArray(claims) ? claims : []).filter((claim) => claimOutstanding(claim) > 0);
}

function claimsAtPriority(claims = [], minimumScore = 80) {
  return (Array.isArray(claims) ? claims : []).filter((claim) => workPriorityScore(claim) >= minimumScore);
}

function claimsFromTrackerRows(rows = [], mode = "service") {
  const safeRows = Array.isArray(rows) ? rows : [];
  if (safeRows.some(isFlatTrackerApiRow)) {
    return uniqueClaimsFromRows(safeRows.map(trackerApiRowToClaim));
  }
  return mode === "service" ? uniqueClaimsFromRows(safeRows) : safeRows;
}

// The client report feed and the /api/reports/dataset payload are
// service-grain: one flat row per service line.
// claimsFromTrackerRows dedupes first-row-wins per claim, which would drop
// every service line after the first, so the report path merges instead:
// group rows by claimId and append each row's single service. Claim-level
// totalPaid is removed because it is a per-service figure in service mode
// and claimFinancialView would let it override the sum of merged services.
function claimsFromServiceGrainRows(rows = []) {
  const byId = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const mapped = trackerApiRowToClaim(row);
    if (!mapped.claimId) return;
    if (Array.isArray(mapped.services) && mapped.services[0] && row && row.adjs !== undefined) {
      mapped.services[0].adjs = row.adjs;
    }
    // KEYED ON (org, claim), NOT claim alone. `claim_id` is an EZClaim sequence
    // and is org-scoped, so for a principal holding two orgs, two tenants' claim
    // 12345 folded into ONE claim carrying the first org's orgId and client name,
    // both orgs' service lines, and their summed balance - money added across a
    // tenant boundary and rendered under the wrong client's name. Reachable by any
    // principal scoped to >= 2 orgs, on both consumers of this mapper (Reports
    // and Client Home via reportDatasetState.claims, and clientReportSourceClaims()).
    // String(), not firstPresent(): hasPresent("") is false, so firstPresent would
    // return undefined for an org-less row and fold every one of them under
    // "undefined::<claimId>" - the same cross-tenant merge, one sentinel over.
    const foldKey = `${String(mapped.orgId || "")}::${mapped.claimId}`;
    const existing = byId.get(foldKey);
    if (!existing) {
      delete mapped.totalPaid;
      byId.set(foldKey, mapped);
      return;
    }
    existing.services.push(...(mapped.services || []));
    // balance arrives per service line in these feeds; accumulate it so
    // claim.balance stays claim-level (AR Aging reads it directly). Redacted
    // money fields reach the mapper as null and coerce to 0 via firstPresent.
    existing.balance = moneyNumber(existing.balance) + moneyNumber(mapped.balance);
  });
  return Array.from(byId.values());
}

function visibleClaimsFromPage(pageWindow, mode = "service") {
  if (pageWindow?.serverBacked) return claimsFromTrackerRows(pageWindow?.rows || [], mode);
  return claimsFromTrackerRows(pageWindow?.rows || [], mode);
}

function isFlatTrackerApiRow(row) {
  return Boolean(row && typeof row === "object" && !row.claim && (row.claimId || row.claim_id || row["Claim ID"] || row.serviceId || row.service_id));
}

function trackerApiRowToClaim(row = {}) {
  const claimId = trackerApiClaimId(row);
  const followUpDate = normalizeDateValue(firstPresent(row.followUpDate, row.follow_up_date, row.actionDate));
  const claim = {
    claimId,
    orgId: firstPresent(row.orgId, row.org_id),
    client: firstPresent(row.client, row.clientName),
    patient: firstPresent(row.patient, row.patientName, row.name),
    renderingProvider: firstPresent(row.renderingProvider, row.rendering_provider),
    facility: firstPresent(row.facility),
    primaryPayer: firstPresent(row.primaryPayer, row.primary_payer),
    primaryExternalId: firstPresent(row.primaryExternalId, row.primary_payer_external_id),
    secondaryPayer: firstPresent(row.secondaryPayer, row.secondary_payer),
    secondaryExternalId: firstPresent(row.secondaryExternalId, row.secondary_payer_external_id),
    claimStatus: firstPresent(row.claimStatus, row.claim_status),
    claimClassification: firstPresent(row.claimClassification, row.classification, row.claClassification),
    lifecycleStage: firstPresent(row.lifecycleStage, row.lifecycle_stage),
    statusCategory: firstPresent(row.statusCategory, row.status_category),
    // The report dataset returns work_queue on every row; without this the Review Queue view sees
    // no queue at all and reports zero open work against a book with ~18.6k open lines.
    queueReason: firstPresent(row.queueReason, row.work_queue),
    match: firstPresent(row.match, row.matchStatus, row.paymentStatus),
    aging: firstPresent(row.aging, row.agingBucket),
    // Reports dataset extras (2026-06-11): tolerate camelCase, snake_case,
    // or absence. billDateAging stays undefined when missing so the
    // arSubmissionAgingDays fallback chain still runs (Number("") is 0).
    originalBillDate: normalizeDateValue(firstPresent(row.originalBillDate, row.original_bill_date)),
    billDateAging: firstPresent(row.billDateAging, row.bill_date_aging),
    // The derived In/Out of Network, at CLAIM grain and under its own name - see
    // the note on the service-level field below for why it is not `network`.
    // Rows fold per claim, so this holds the FIRST line's value and is a fallback
    // for a claim whose lines did not each carry one; the slicer reads the LINE.
    // `network` itself is NOT set here any more. It is left to
    // normalizeDemoNetworkField, the only writer that means "this claim's
    // network" rather than "the contract that priced this line".
    contractNetwork: firstPresent(row.network),
    balance: firstPresent(row.balance, row.finalInsuranceBalance, row.owed, 0),
    action: row.followUpReview?.status === "pending" ? (row.followUpReview.approvedAction || "") : firstPresent(row.action, row.followUpAction, row.recommendedNextAction),
    followUpDate,
    assignedTo: firstPresent(row.assignedTo),
    assignedBy: firstPresent(row.assignedBy, row.assigned_by),
    assignmentStatus: firstPresent(row.assignmentStatus),
    // Offshore-handoff overlay columns (portal.claim_workflow_state): hydrate
    // due/instruction/bounce/note after reload in live mode. Pre-migration
    // rows read back NULL and render as absent.
    assignmentDue: normalizeDateValue(firstPresent(row.assignmentDue, row.assignment_due) || ""),
    assignmentInstruction: String(firstPresent(row.assignmentInstruction, row.assignment_instruction) || ""),
    bounceReason: String(firstPresent(row.bounceReason, row.bounce_reason) || ""),
    reviewNote: String(firstPresent(row.reviewNote, row.review_note) || ""),
    reviewSubmittedBy: String(firstPresent(row.reviewSubmittedBy, row.review_submitted_by_user_id) || ""),
    reviewSubmittedAt: String(firstPresent(row.reviewSubmittedAt, row.review_submitted_at_utc) || ""),
    reviewApprovedBy: String(firstPresent(row.reviewApprovedBy, row.review_approved_by_user_id) || ""),
    reviewApprovedAt: String(firstPresent(row.reviewApprovedAt, row.review_approved_at_utc) || ""),
    approvalCommandId: String(firstPresent(row.approvalCommandId, row.approval_command_id) || ""),
    approvalEffectStatus: String(firstPresent(row.approvalEffectStatus, row.approval_effect_status) || ""),
    approvalEffectUpdatedAt: String(firstPresent(row.approvalEffectUpdatedAt, row.approval_effect_updated_at_utc) || ""),
    workflowUpdatedBy: String(firstPresent(row.workflowUpdatedBy, row.workflow_updated_by_user_id) || ""),
    workflowUpdatedAt: String(firstPresent(row.workflowUpdatedAt, row.workflow_updated_at_utc, row.updatedAtUtc) || ""),
    latestClaimNoteText: firstPresent(row.latestClaimNoteText, row.latest_claim_note_text, row.latestClaimNote, row.latest_claim_note, row.claimNoteSummary?.text),
    claimNoteSummary: row.claimNoteSummary && typeof row.claimNoteSummary === "object" ? row.claimNoteSummary : null,
    lastTouchedAt: firstPresent(row.lastTouchedAt, row.lastTouchedAtUtc),
    totalPaid: firstPresent(row.totalPaid, row.finalInsurancePaid, row.totalInsAmtPaid, 0),
    services: []
  };
  claim.services = [{
    serviceId: firstPresent(row.serviceId, row.service_id, row["Service ID"]),
    ...(row.followUpReview ? { action: row.action || "", followUpDate: row.followUpDate || "", followUpReview: row.followUpReview } : {}),
    ...(row.followUpDraft ? { followUpDraft: row.followUpDraft } : {}),
    dos: serviceDosValue(row),
    procedure: firstPresent(row.procedure, row.procedureCode),
    place: firstPresent(row.place, row.placeOfService),
    units: firstPresent(row.units, 0),
    charges: firstPresent(row.charges, 0),
    // THE LINE KEEPS ITS OWN MATCH. Rows arrive at service grain and are folded
    // into claims, so claim.match ends up being the FIRST row's value; without
    // this, every per-line reader (serviceFinancialView's no-rate fallback, the
    // Rate Issues report, the claim-grain certainty roll-up) fell back to that
    // one value and could not tell which LINE the source called No Primary Rate.
    status: firstPresent(row.match, row.matchStatus),
    calculationGuardFlags: row.calculationGuards || row.calculationGuardFlags || row.calculation_guard_flags,
    primaryPayer: claim.primaryPayer,
    primaryExternalId: claim.primaryExternalId,
    secondaryPayer: claim.secondaryPayer,
    secondaryExternalId: claim.secondaryExternalId,
    claimClassification: claim.claimClassification,
    totalInsAmtPaid: firstPresent(row.totalInsAmtPaid, row.finalInsurancePaid, row.totalPaid, 0),
    totalPatAmtPaid: firstPresent(row.totalPatAmtPaid, 0),
    // `row.patientResponsibility` USED TO BE A FALLBACK HERE and no longer is.
    // It is the ENGINE's patient responsibility (max(positive pat bal + pat
    // paid, true PR), always >= 0) — a different quantity from the SIGNED
    // patient balance this field carries, and since 2026-08-10 the reports
    // dataset actually ships it, so the alias would have quietly loaded a
    // responsibility into a balance on any row whose balance was NULL.
    totalPatBal: firstPresent(row.totalPatBal, 0),
    // TRUE PATIENT RESPONSIBILITY (PR 1/2/3), summed at the source. NO 0
    // DEFAULT, by the same rule as the figures below: absence must stay ABSENT.
    // A 0 here would assert "the payer adjudicated this line and the patient
    // owes nothing" on every line the payer has not answered yet, which deletes
    // the patient's share and inflates insurance expected by the same amount.
    prAdjustmentTotal: firstPresent(row.prAdjustmentTotal, row.pr_adjustment_total),
    // THE ENGINE'S OWN FIGURES, PASSED THROUGH WITHOUT A 0 DEFAULT.
    // patientResponsibility is CT-TRACKER's `positive_pat_bal + total_pat_paid`
    // and finalInsuranceExpected is the NET expected (gross - patient
    // responsibility - write-off, floored at 0). Absence must stay ABSENT so the
    // report readers fall through their own ladders instead of asserting "$0"
    // on a lane that does not ship them — the same rule the rate defaults note
    // above states, for the same reason.
    patientResponsibility: row.patientResponsibility,
    finalInsuranceExpected: row.finalInsuranceExpected,
    // THE 0 DEFAULTS STAY HERE, deliberately — do not "fix" this to match
    // backend/reportDatasetSource.js's recomputeWarehouseRowFinancials.
    //
    // That twin is WAREHOUSE-ONLY by construction (loadRawDataset calls it
    // behind `useWarehouse ? ... : row`), so dropping its 0 defaults only ever
    // touches gold rows. THIS normalizer is SOURCE-AGNOSTIC: every serverBacked
    // page, mirror or warehouse, comes through it. And the mirror ships
    // `CAST(NULL AS float) AS primary_rate`
    // (deploy/create_curated_tracker_views.sql) with a match_status that only
    // ever says Paid/Underpaid/Not Paid — never 'No Primary Rate'.
    //
    // So removing the 0 here flips primaryRateMissing from TRUE to FALSE on the
    // live mirror lane: with no explicit rate the engine falls back to
    // `sourceExpected <= 0 || /no rate/i.test(match)`, and sourceExpected is
    // COALESCE(SrvExpectedPriPmt, SrvAllowedAmt, SrvCharges, 0) — positive on
    // virtually every line. Those two flags are the ONLY input to
    // unknownMoneyReason(), so the grid, the column filters and the workbook
    // export would stop rendering the honest "-- No primary rate on file" and
    // start printing a CHARGE-DERIVED number instead. That is NORTH_STAR §6
    // pointed backwards, on the currently-live source.
    //
    // Making this source-aware needs a primaryRateMissing rule that means "no
    // explicit rate AND no fee-schedule match" rather than inferring from
    // sourceExpected — tracked, not done here.
    primaryRate: firstPresent(row.primaryRate, 0),
    primaryExpectedTotal: firstPresent(row.primaryExpectedTotal, 0),
    secondaryRate: firstPresent(row.secondaryRate, 0),
    secondaryCalc: firstPresent(row.secondaryCalc),
    // IN/OUT OF NETWORK, AT LINE GRAIN AND UNDER ITS OWN NAME.
    //
    // Rows arrive at service grain and fold into claims, so claim.network above ends
    // up holding only the FIRST line's value - useless for a claim that mixes them.
    // The line keeps its own, exactly as `status` does a few fields up.
    //
    // NOT written as `service.network`: bestFeeScheduleMatch reads that key as a
    // MATCHING INPUT (feeNetworkRowMatches), and this value is an OUTPUT of that very
    // match on the server. Filling it would let the browser's re-match narrow on its
    // own answer and silently move expected reimbursement, which is not what a report
    // filter is allowed to do.
    contractNetwork: firstPresent(row.network),
    secondaryAllowedTotal: firstPresent(row.secondaryAllowedTotal, 0),
    secondaryExpectedTotal: firstPresent(row.secondaryExpectedTotal, 0),
    staticTotalExpected: firstPresent(row.staticTotalExpected, row.totalExpected, 0),
    finalExpectedTotalNumeric: firstPresent(row.finalExpectedTotalNumeric, row.totalExpected, row.finalInsuranceExpected, 0),
    finalSecondaryExpectedNumeric: firstPresent(row.finalSecondaryExpectedNumeric, 0),
    finalInsurancePaid: firstPresent(row.finalInsurancePaid, row.totalPaid, 0),
    finalInsuranceBalance: firstPresent(row.finalInsuranceBalance, row.owed, row.balance, 0),
    writeOffAmount: firstPresent(row.writeOffAmount, 0),
    latestClaimNoteText: firstPresent(row.latestClaimNoteText, row.latest_claim_note_text, row.latestClaimNote, row.latest_claim_note, row.claimNoteSummary?.text),
    // Historical saved-view key; never source it from a legacy service-note field.
    serviceLineNotes: firstPresent(row.latestClaimNoteText, row.latest_claim_note_text, row.latestClaimNote, row.latest_claim_note, row.claimNoteSummary?.text, row.serviceLineNotes)
  }];
  return {
    ...claim,
    ...(state.workflowByClaim[claimId] || {})
  };
}

function topByNumericField(rows = [], field, fallbackField = "") {
  if (!Array.isArray(rows) || !rows.length) return null;
  return rows
    .filter((row) => row && moneyNumber(row[field] ?? (fallbackField ? row[fallbackField] : 0) ?? 0) > 0)
    .sort((a, b) => moneyNumber(b[field] ?? (fallbackField ? b[fallbackField] : 0) ?? 0) - moneyNumber(a[field] ?? (fallbackField ? a[fallbackField] : 0) ?? 0))[0] || null;
}

// Mirror of backend/executiveAnalytics.js#olderAgingBucket: rank through the canonical
// bands so a legacy-vocabulary label is compared on its BAND, not on its position in a
// hand-written list that never contained it.
function olderAgingBucket(current, candidate) {
  if (!current) return candidate || "";
  if (!candidate) return current;
  return agingBucketOrdinal(candidate) > agingBucketOrdinal(current) ? candidate : current;
}

// Reports carry a THIRD state beyond known/unknown: "you are not allowed to
// see this". reportMoneyValue() nulls the figure when VIEW_FINANCIALS is
// absent, so null here means RESTRICTED - a permission fact - while
// UNKNOWN_MONEY means the number is genuinely not computable. Both are
// non-zero renderings; neither may collapse to $0.
function formatReportMoney(value) {
  if (value == null) return "Restricted";
  return money(value);
}

function formatPercent(value) {
  return value == null ? "N/A" : `${value}%`;
}

function formatPercentValue(value) {
  if (value == null || value === "") return "N/A";
  return String(value).endsWith("%") ? String(value) : `${value}%`;
}

function reportMetric([label, value]) {
  return `<div><span>${escapeHtml(label)}</span><strong>${escapeHtml(String(value))}</strong></div>`;
}

// Chart series colors route through the --chart-1..8 CSS tokens so dark
// mode and future palette tweaks affect every chart consistently.
const CHART_SERIES_COLORS = [
  "var(--chart-1, #0a63c9)",
  "var(--chart-2, #0e7c86)",
  "var(--chart-3, #b45309)",
  "var(--chart-4, #b91c1c)",
  "var(--chart-5, #6d28d9)",
  "var(--chart-6, #0891b2)",
  "var(--chart-7, #be123c)",
  "var(--chart-8, #65a30d)"
];

function chartSeriesColor(index) {
  return CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length];
}

function renderExecutiveChartsMeta(payload, reportPayload) {
  const charts = Array.isArray(payload?.charts) ? payload.charts : [];
  if (!charts.length) return "";
  const seriesCount = charts.reduce((sum, chart) => sum + (Array.isArray(chart.series) ? chart.series.length : 0), 0);
  const redactedSeries = charts.reduce((sum, chart) => sum + (Array.isArray(chart.series) ? chart.series.filter((s) => s.redacted).length : 0), 0);
  const sourceLabel = "Report results";
  return `
    <span class="report-charts-meta-chip"><strong>${charts.length}</strong> chart${charts.length === 1 ? "" : "s"}</span>
    <span class="report-charts-meta-chip"><strong>${seriesCount}</strong> series</span>
    <span class="report-charts-meta-chip report-charts-meta-source">${escapeHtml(sourceLabel)}</span>
    ${redactedSeries > 0 ? `<span class="report-charts-meta-chip is-redacted"><strong>${redactedSeries}</strong> restricted</span>` : ""}
  `;
}

function chartSeriesDelta(values, valueType) {
  if (!Array.isArray(values) || values.length < 2) return null;
  const numericIndices = [];
  values.forEach((value, index) => {
    if (Number.isFinite(Number(value))) numericIndices.push(index);
  });
  if (numericIndices.length < 2) return null;
  const latest = Number(values[numericIndices[numericIndices.length - 1]]);
  const previous = Number(values[numericIndices[numericIndices.length - 2]]);
  if (!Number.isFinite(latest) || !Number.isFinite(previous)) return null;
  const diff = latest - previous;
  const pct = previous !== 0 ? (diff / Math.abs(previous)) * 100 : null;
  let display;
  if (valueType === "percent" || valueType === "score") {
    const rounded = Math.round(diff * 10) / 10;
    display = `${rounded >= 0 ? "+" : ""}${rounded}${valueType === "percent" ? "pp" : ""}`;
  } else if (pct != null && Math.abs(pct) < 1000) {
    display = `${pct >= 0 ? "+" : ""}${pct.toFixed(Math.abs(pct) < 10 ? 1 : 0)}%`;
  } else {
    display = `${diff >= 0 ? "+" : ""}${formatChartValue(diff, valueType)}`;
  }
  return { diff, pct, display, direction: diff === 0 ? "flat" : diff > 0 ? "up" : "down" };
}

const EXECUTIVE_CHART_GROUPS = [
  { key: "revenue", label: "Revenue & Cash", description: "Money in, AR build, monthly trends.", chartIds: ["monthly_revenue_ar", "cash_flow"] },
  { key: "risk", label: "AR Risk", description: "Denials, underpayments, and highest-risk accounts.", chartIds: ["denial_trends", "underpayment_exposure", "high_risk_ar"] },
  { key: "operations", label: "Operations", description: "Payer behavior and provider performance.", chartIds: ["payer_behavior", "provider_performance"] }
];

function groupExecutiveCharts(charts) {
  const remaining = new Map(charts.map((chart) => [chart.id, chart]));
  const grouped = EXECUTIVE_CHART_GROUPS.map((group) => {
    const items = group.chartIds.map((id) => remaining.get(id)).filter(Boolean);
    items.forEach((chart) => remaining.delete(chart.id));
    return { ...group, charts: items };
  });
  const leftovers = Array.from(remaining.values());
  if (leftovers.length) {
    grouped.push({ key: "other", label: "Other", description: "Additional analytics from this payload.", charts: leftovers });
  }
  return grouped.filter((group) => group.charts.length);
}

function renderExecutiveChartCard(payload, chart) {
  const labels = Array.isArray(chart.labels) ? chart.labels.map((label) => label == null ? "" : String(label)) : [];
  const series = Array.isArray(chart.series) ? chart.series : [];
  const type = chart.type || "bar";
  const pointCount = labels.length;
  const hasTimeAxis = type === "line" || type === "combo" || type === "stacked" || chart.xField === "month";
  const kpi = series.map((item, index) => {
    const values = Array.isArray(item.values) ? item.values : [];
    const latest = [...values].reverse().find((value) => value != null);
    const delta = !item.redacted && hasTimeAxis ? chartSeriesDelta(values, item.valueType) : null;
    const goodWhenDown = /(denial|risk|underpa|guarded|aging|outstanding|exposure|owed)/i.test(item.label || item.key || "");
    const deltaTone = delta && delta.direction !== "flat"
      ? (goodWhenDown ? (delta.direction === "down" ? "good" : "bad") : (delta.direction === "up" ? "good" : "bad"))
      : "flat";
    const arrow = delta ? (delta.direction === "up" ? "▲" : delta.direction === "down" ? "▼" : "▬") : "";
    return `
      <div class="chart-kpi${item.redacted ? " is-redacted" : ""}">
        <span class="chart-kpi-dot" style="background:${chartSeriesColor(index)}"></span>
        <div class="chart-kpi-body">
          <span class="chart-kpi-label">${escapeHtml(item.label || item.key || "Series")}</span>
          <strong class="chart-kpi-value">${item.redacted ? "Restricted" : escapeHtml(formatChartValue(latest, item.valueType))}</strong>
          ${delta ? `<span class="chart-kpi-delta chart-kpi-delta--${deltaTone}" title="vs prior period">${arrow} ${escapeHtml(delta.display)}</span>` : ""}
        </div>
      </div>
    `;
  }).join("");
  const canvas = renderChartCanvas(type, labels, series);
  const tableMarkup = renderChartDataTable(labels, series);
  const legend = series.map((item, index) => `
    <span class="chart-legend-item${item.redacted ? " is-redacted" : ""}">
      <span class="chart-legend-swatch" style="background:${chartSeriesColor(index)}"></span>
      ${escapeHtml(item.label || item.key || "Series")}
    </span>
  `).join("");
  const chartId = chart.id || "";
  return `
    <section class="report-chart report-chart--${escapeHtml(type)}" data-chart-id="${escapeHtml(chartId)}">
      <header class="report-chart-heading">
        <div>
          <h4>${escapeHtml(chart.title || "Report Chart")}</h4>
          <p class="report-chart-meta">
            <span class="chart-type-pill chart-type-pill--${escapeHtml(type)}">${escapeHtml(type)}</span>
            <span>${pointCount} ${pointCount === 1 ? "point" : "points"}</span>
            <span>${series.length} ${series.length === 1 ? "series" : "series"}</span>
          </p>
        </div>
        <div class="report-chart-heading-actions">
          ${payload.redactedFinancials ? pill("Financials restricted", "amber") : ""}
          <div class="report-chart-viewtoggle" role="group" aria-label="Chart view">
            <button type="button" class="report-chart-viewbtn is-active" data-chart-view="chart" data-chart-target="${escapeHtml(chartId)}" aria-pressed="true">Chart</button>
            <button type="button" class="report-chart-viewbtn" data-chart-view="table" data-chart-target="${escapeHtml(chartId)}" aria-pressed="false">Table</button>
          </div>
        </div>
      </header>
      ${kpi ? `<div class="chart-kpi-strip">${kpi}</div>` : ""}
      <div class="chart-canvas" data-chart-view-target="chart">${canvas}</div>
      <div class="chart-data-table-wrap" data-chart-view-target="table" hidden>${tableMarkup}</div>
      ${legend ? `<div class="chart-legend">${legend}</div>` : ""}
    </section>
  `;
}

function renderChartDataTable(labels, series) {
  if (!labels.length || !series.length) {
    return '<p class="muted-note chart-empty">No tabular data for this filter set.</p>';
  }
  const head = `<tr><th scope="col">${escapeHtml(series[0]?.xLabel || "Label")}</th>${series.map((item) => `<th scope="col" class="num">${escapeHtml(item.label || item.key || "Series")}${item.redacted ? ' <span class="chart-data-restricted" title="Restricted">·R</span>' : ""}</th>`).join("")}</tr>`;
  const body = labels.map((label, rowIndex) => {
    const cells = series.map((item) => {
      if (item.redacted) return '<td class="num">Restricted</td>';
      const value = Array.isArray(item.values) ? item.values[rowIndex] : null;
      const formatted = value == null ? "—" : formatChartValue(value, item.valueType);
      return `<td class="num">${escapeHtml(formatted)}</td>`;
    }).join("");
    return `<tr><th scope="row">${escapeHtml(label)}</th>${cells}</tr>`;
  }).join("");
  return `
    <div class="chart-data-table-toolbar">
      <button type="button" class="small-button" data-chart-table-copy title="Copy table as TSV (paste into Excel/Sheets)">Copy</button>
    </div>
    <table class="chart-data-table"><thead>${head}</thead><tbody>${body}</tbody></table>
  `;
}

const EXECUTIVE_CHART_VIEW_KEY = "tms.masterPortal.executiveChartView";

function loadExecutiveChartViewPreferences() {
  try {
    if (typeof localStorage === "undefined") return new Map();
    const raw = localStorage.getItem(EXECUTIVE_CHART_VIEW_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return new Map();
    return new Map(Object.entries(parsed).filter(([, value]) => value === "chart" || value === "table"));
  } catch (_) {
    return new Map();
  }
}

function saveExecutiveChartViewPreferences(map) {
  try {
    if (typeof localStorage === "undefined") return;
    const obj = Object.fromEntries(Array.from(map.entries()).filter(([, value]) => value === "chart" || value === "table"));
    localStorage.setItem(EXECUTIVE_CHART_VIEW_KEY, JSON.stringify(obj));
  } catch (_) {
    // localStorage unavailable or quota — ignore, in-memory map still works
  }
}

const executiveChartViewPreference = loadExecutiveChartViewPreferences();

function chartTableToTsv(table) {
  if (!table) return "";
  return Array.from(table.querySelectorAll("tr")).map((tr) => Array.from(tr.children).map((cell) => cell.textContent.trim().replace(/\s+/g, " ")).join("\t")).join("\n");
}

function setExecutiveChartCardView(card, target) {
  if (!card) return;
  card.querySelectorAll("[data-chart-view]").forEach((btn) => {
    const isActive = btn.dataset.chartView === target;
    btn.classList.toggle("is-active", isActive);
    btn.setAttribute("aria-pressed", String(isActive));
  });
  card.querySelectorAll("[data-chart-view-target]").forEach((panel) => {
    panel.hidden = panel.dataset.chartViewTarget !== target;
  });
}

function bindExecutiveChartViewToggles() {
  if (!els.reportCharts || typeof els.reportCharts.querySelectorAll !== "function") return;
  Array.from(els.reportCharts.querySelectorAll(".report-chart")).forEach((card) => {
    const chartId = card.dataset.chartId || "";
    const remembered = executiveChartViewPreference.get(chartId);
    if (remembered && remembered !== "chart") {
      setExecutiveChartCardView(card, remembered);
    }
  });
  Array.from(els.reportCharts.querySelectorAll("[data-chart-view]")).forEach((button) => {
    button.addEventListener("click", () => {
      const card = button.closest(".report-chart");
      if (!card) return;
      const target = button.dataset.chartView;
      executiveChartViewPreference.set(card.dataset.chartId || "", target);
      saveExecutiveChartViewPreferences(executiveChartViewPreference);
      setExecutiveChartCardView(card, target);
    });
  });
  Array.from(els.reportCharts.querySelectorAll("[data-chart-table-copy]")).forEach((button) => {
    button.addEventListener("click", () => {
      const card = button.closest(".report-chart");
      const table = card?.querySelector(".chart-data-table");
      const tsv = chartTableToTsv(table);
      if (!tsv) {
        showToast("Nothing to copy.");
        return;
      }
      const writer = navigator?.clipboard?.writeText;
      if (writer) {
        writer.call(navigator.clipboard, tsv).then(() => showToast("Copied chart table to clipboard."), () => showToast("Copy failed — select the table manually."));
      } else {
        showToast("Clipboard not available in this browser.");
      }
    });
  });
}

function renderExecutiveChartPayloads(payload) {
  const charts = Array.isArray(payload?.charts) ? payload.charts : [];
  if (!charts.length) return empty("No chart payloads available for the current report filters.");
  const groups = groupExecutiveCharts(charts);
  if (groups.length === 1) {
    return `<div class="report-chart-grid">${groups[0].charts.map((chart) => renderExecutiveChartCard(payload, chart)).join("")}</div>`;
  }
  return groups.map((group) => `
    <section class="report-chart-group report-chart-group--${escapeHtml(group.key)}">
      <header class="report-chart-group-heading">
        <div>
          <span class="report-chart-group-eyebrow">${escapeHtml(group.label)}</span>
          <p class="report-chart-group-desc">${escapeHtml(group.description)}</p>
        </div>
        <span class="report-chart-group-count">${group.charts.length} ${group.charts.length === 1 ? "chart" : "charts"}</span>
      </header>
      <div class="report-chart-grid report-chart-grid--grouped">
        ${group.charts.map((chart) => renderExecutiveChartCard(payload, chart)).join("")}
      </div>
    </section>
  `).join("");
}

function renderChartCanvas(type, labels, series) {
  if (!labels.length || !series.length) {
    return '<p class="muted-note chart-empty">No chart data for this filter set.</p>';
  }
  // Per user feedback: charts must look "professional" / client-ready. When
  // Plotly is loaded we drop the hand-rolled SVG and route to Plotly so users
  // get hover tooltips, axis $-formatting, legend, responsive behavior, etc.
  if (typeof window !== "undefined" && typeof window.Plotly === "object") {
    const chartId = `plotly-chart-${(++__plotlyChartCounter)}`;
    // Schedule the actual draw after this innerHTML lands.
    requestAnimationFrame(() => {
      try { renderPlotlyChartInto(chartId, type, labels, series); } catch (e) {
        console.error("[tms.portal] plotly render failed:", e);
        const host = document.getElementById(chartId);
        if (host) host.innerHTML = renderChartCanvasSvgFallback(type, labels, series);
      }
    });
    return `<div id="${chartId}" class="plotly-host" style="width:100%;min-height:260px"></div>`;
  }
  schedulePlotlyLoad(() => render());
  return renderChartCanvasSvgFallback(type, labels, series);
}

function renderChartCanvasSvgFallback(type, labels, series) {
  if (type === "stacked") return renderStackedChart(labels, series);
  if (type === "line") return renderLineChart(labels, series, { mode: "line" });
  if (type === "combo") return renderComboChart(labels, series);
  return renderBarChart(labels, series);
}

let __plotlyChartCounter = 0;
let __plotlyLoadPromise = null;
let __plotlyLoadFailed = false;

function schedulePlotlyLoad(afterLoad) {
  if (typeof window === "undefined" || window.Plotly || __plotlyLoadFailed) return;
  const promise = loadPlotlyOnDemand();
  if (typeof afterLoad === "function") {
    promise.then(() => {
      const schedule = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (callback) => callback();
      schedule(() => {
        try { afterLoad(); } catch (error) { console.error("[tms.portal] plotly lazy redraw failed:", error); }
      });
    }).catch(() => {});
  }
}

function loadPlotlyOnDemand() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.Plotly) return Promise.resolve(window.Plotly);
  if (__plotlyLoadPromise) return __plotlyLoadPromise;
  __plotlyLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "./assets/vendor/plotly.min.js";
    script.async = true;
    script.onload = () => resolve(window.Plotly || null);
    script.onerror = () => {
      __plotlyLoadFailed = true;
      reject(new Error("Plotly failed to load"));
    };
    document.head.appendChild(script);
  });
  return __plotlyLoadPromise;
}

// Plotly base config — matches the old Tracker (Developer/Tracker) so users
// who've seen the client charts there get the same visual signature here.
function plotlyBaseLayout() {
  const isDark = document.documentElement.dataset.theme === "dark";
  return {
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    font: { family: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", size: 11, color: isDark ? "#cbd5e1" : "#475569" },
    margin: { t: 10, r: 16, b: 48, l: 64 },
    xaxis: {
      tickfont: { size: 11 },
      tickangle: 0,
      gridcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      zerolinecolor: isDark ? "#4b5b75" : "#cbd5e1",
      automargin: true
    },
    yaxis: {
      tickfont: { size: 11 },
      gridcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      zerolinecolor: isDark ? "#4b5b75" : "#cbd5e1",
      automargin: true
    },
    legend: { orientation: "h", y: -0.22, font: { size: 11 } },
    hoverlabel: { bgcolor: isDark ? "#0e1d33" : "#ffffff", bordercolor: isDark ? "#4b5b75" : "#cbd5e1", font: { family: "inherit", size: 12 } }
  };
}

function plotlySeriesColor(idx) {
  const styles = getComputedStyle(document.documentElement);
  return (styles.getPropertyValue(`--chart-${(idx % 8) + 1}`).trim() || ["#0a63c9", "#0e7c86", "#b45309", "#b91c1c", "#6d28d9", "#0891b2", "#be123c", "#65a30d"][idx % 8]);
}

// Meaning-keyed chart colors (2026-06-11): a series may name an explicit CSS
// variable (colorVar: "--aging-3"), or a single-series bar may carry one var
// per point (pointColorVars) — both resolved exactly the way plotlySeriesColor
// resolves --chart-N, so theme switches recolor them too. The hand-rolled SVG
// fallback stays positional and ignores both options.
function plotlyCssVarColor(varName, fallbackIdx) {
  const styles = getComputedStyle(document.documentElement);
  return (styles.getPropertyValue(varName).trim() || plotlySeriesColor(fallbackIdx));
}

function plotlySeriesMarkerColor(s, i) {
  if (Array.isArray(s.pointColorVars) && s.pointColorVars.length) {
    return s.pointColorVars.map((varName, idx) => plotlyCssVarColor(varName, idx));
  }
  if (s.colorVar) return plotlyCssVarColor(s.colorVar, s.colorIndex ?? i);
  return plotlySeriesColor(s.colorIndex ?? i);
}

function plotlyValueFormatForType(valueType) {
  if (valueType === "money") return "$,.0f";
  if (valueType === "percent") return ".1%";
  return ",.0f";
}

function renderPlotlyChartInto(divId, type, labels, series) {
  const host = document.getElementById(divId);
  if (!host || typeof window.Plotly === "undefined") return;
  const numeric = series.filter((s) => (s.values || []).some((v) => v != null && Number.isFinite(Number(v))));
  // Zero-state healing (2026-06-11): an all-null/all-zero dataset draws a
  // blank axis frame that reads as a broken chart — show the empty note.
  const hasSignal = numeric.some((s) => (s.values || []).some((v) => v != null && Number.isFinite(Number(v)) && Number(v) !== 0));
  if (!numeric.length || !hasSignal) { host.innerHTML = '<p class="muted-note chart-empty">No chart data for this filter set.</p>'; return; }
  const valueType = numeric[0]?.valueType || "number";
  const layout = plotlyBaseLayout();
  layout.yaxis.tickformat = plotlyValueFormatForType(valueType);
  // Money bars grow from $0 — never let Plotly pick a non-zero floor that
  // exaggerates the differences between bars.
  if ((type === "stacked" || type === "bar") && valueType === "money") layout.yaxis.rangemode = "tozero";
  // Stacked legends list series in stack order (first trace at the bottom).
  if (type === "stacked") layout.legend = { ...(layout.legend || {}), traceorder: "normal" };
  // Bar-family labels (service months, payers, states) are categories, not
  // timestamps — without this Plotly date-parses "2025-11"-style labels and
  // re-spaces the bars along a time axis. Line charts keep auto detection.
  if (type === "stacked" || type === "bar" || type === "combo") layout.xaxis.type = "category";
  let traces;
  if (type === "stacked") {
    layout.barmode = "stack";
    traces = numeric.map((s, i) => ({
      name: s.label || `Series ${i+1}`,
      type: "bar",
      x: labels,
      y: s.values.map((v) => v == null ? null : Number(v)),
      marker: { color: plotlySeriesMarkerColor(s, i) }
    }));
  } else if (type === "line") {
    traces = numeric.map((s, i) => ({
      name: s.label || `Series ${i+1}`,
      type: "scatter",
      mode: "lines+markers",
      x: labels,
      y: s.values.map((v) => v == null ? null : Number(v)),
      line: { color: plotlySeriesColor(s.colorIndex ?? i), width: 2.5 },
      marker: { color: plotlySeriesColor(s.colorIndex ?? i), size: 6 }
    }));
  } else if (type === "combo") {
    traces = numeric.map((s, i) => {
      const isLine = i === numeric.length - 1;
      return {
        name: s.label || `Series ${i+1}`,
        type: isLine ? "scatter" : "bar",
        mode: isLine ? "lines+markers" : undefined,
        x: labels,
        y: s.values.map((v) => v == null ? null : Number(v)),
        marker: { color: plotlySeriesColor(s.colorIndex ?? i) },
        line: isLine ? { color: plotlySeriesColor(s.colorIndex ?? i), width: 2.5, dash: "dot" } : undefined
      };
    });
  } else {
    // default: grouped bar
    layout.barmode = "group";
    // Single-series rankings read as slabs without breathing room.
    layout.bargap = 0.45;
    traces = numeric.map((s, i) => ({
      name: s.label || `Series ${i+1}`,
      type: "bar",
      x: labels,
      y: s.values.map((v) => v == null ? null : Number(v)),
      marker: { color: plotlySeriesMarkerColor(s, i) }
    }));
  }
  window.Plotly.newPlot(host, traces, layout, { responsive: true, displayModeBar: false });
}


// `seriesIndex` is the series' position in the ORIGINAL, unfiltered list — the
// same index the legend swatches colour themselves by. It has to be carried on
// the mapped copy: callers used to recover it with `series.indexOf(item)` after
// mapping, which can never match (the map returns new objects), so every colour
// lookup resolved to index -1 → `undefined` → `fill:undefined` → black bars.
function chartNumericSeries(series, seriesIndex) {
  return {
    ...series,
    chartSeriesIndex: Number.isInteger(seriesIndex) && seriesIndex >= 0 ? seriesIndex : 0,
    values: (series.values || []).map((value) => {
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    })
  };
}

function chartLabelTicks(labels, maxTicks = 8) {
  if (labels.length <= maxTicks) return labels.map((label, index) => ({ index, label }));
  const step = Math.ceil(labels.length / maxTicks);
  const ticks = [];
  for (let i = 0; i < labels.length; i += step) ticks.push({ index: i, label: labels[i] });
  if (ticks[ticks.length - 1]?.index !== labels.length - 1) {
    ticks.push({ index: labels.length - 1, label: labels[labels.length - 1] });
  }
  return ticks;
}

function chartNiceMax(value) {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const exponent = Math.floor(Math.log10(value));
  const base = Math.pow(10, exponent);
  const ratio = value / base;
  let nice;
  if (ratio <= 1) nice = 1;
  else if (ratio <= 2) nice = 2;
  else if (ratio <= 2.5) nice = 2.5;
  else if (ratio <= 5) nice = 5;
  else nice = 10;
  return nice * base;
}

function chartAxisRange(values) {
  const finite = values.filter((value) => Number.isFinite(value));
  if (!finite.length) return { min: 0, max: 1 };
  const min = Math.min(0, ...finite);
  const max = Math.max(...finite, 0);
  if (min === 0 && max === 0) return { min: 0, max: 1 };
  return {
    min: min < 0 ? -chartNiceMax(Math.abs(min)) : 0,
    max: chartNiceMax(Math.max(max, Math.abs(min) || 1))
  };
}

function chartFormatAxis(value, valueType) {
  if (!Number.isFinite(value)) return "";
  if (valueType === "money") {
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
  }
  if (valueType === "percent") return `${Math.round(value)}%`;
  if (valueType === "score") return `${Math.round(value)}`;
  if (Math.abs(value) >= 1000) return value.toLocaleString();
  return String(Math.round(value * 100) / 100);
}

function chartAxisTicks(min, max, count = 4) {
  const ticks = [];
  for (let i = 0; i <= count; i += 1) {
    ticks.push(min + ((max - min) * i) / count);
  }
  return ticks;
}

function renderBarChart(labels, series) {
  const numeric = series.filter((item) => !item.redacted).map((item) => chartNumericSeries(item, series.indexOf(item)));
  if (!numeric.length) return renderRedactedCanvas(series);
  const width = 520;
  const height = 220;
  const padding = { top: 14, right: 14, bottom: 38, left: 72 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const allValues = numeric.flatMap((item) => item.values);
  const { min, max } = chartAxisRange(allValues);
  const valueType = numeric[0]?.valueType;
  const groupCount = labels.length;
  const groupWidth = innerW / Math.max(1, groupCount);
  const barWidth = Math.max(2, (groupWidth - 6) / numeric.length);
  const y = (value) => padding.top + innerH - ((value - min) / (max - min || 1)) * innerH;
  const yTicks = chartAxisTicks(min, max, 4);
  const labelTicks = chartLabelTicks(labels, Math.min(10, labels.length));
  const grid = yTicks.map((tick) => `
    <line x1="${padding.left}" x2="${padding.left + innerW}" y1="${y(tick)}" y2="${y(tick)}" class="chart-grid-line"/>
    <text x="${padding.left - 8}" y="${y(tick) + 4}" class="chart-axis-text chart-axis-text--right">${escapeHtml(chartFormatAxis(tick, valueType))}</text>
  `).join("");
  const xLabels = labelTicks.map((tick) => {
    const cx = padding.left + groupWidth * (tick.index + 0.5);
    return `<text x="${cx}" y="${padding.top + innerH + 18}" class="chart-axis-text chart-axis-text--center">${escapeHtml(truncateChartLabel(tick.label, 10))}</text>`;
  }).join("");
  const bars = labels.map((label, groupIndex) => numeric.map((item, seriesIndex) => {
    const value = item.values[groupIndex];
    if (value == null) return "";
    const color = chartSeriesColor(item.chartSeriesIndex);
    const cx = padding.left + groupWidth * groupIndex + 3 + barWidth * seriesIndex;
    const baseline = y(0);
    const top = y(value);
    const barH = Math.abs(top - baseline);
    const barY = value >= 0 ? top : baseline;
    return `<rect x="${cx.toFixed(2)}" y="${barY.toFixed(2)}" width="${barWidth.toFixed(2)}" height="${Math.max(1, barH).toFixed(2)}" style="fill:${color}" rx="2" class="report-chart-bar"><title>${escapeHtml(`${label} · ${item.label}: ${formatChartValue(value, item.valueType)}`)}</title></rect>`;
  }).join("")).join("");
  return `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" class="chart-svg" role="img" aria-label="Bar chart">
    ${grid}
    <line x1="${padding.left}" x2="${padding.left + innerW}" y1="${y(0)}" y2="${y(0)}" class="chart-axis-baseline"/>
    ${bars}
    ${xLabels}
  </svg>`;
}

function renderStackedChart(labels, series) {
  const numeric = series.filter((item) => !item.redacted).map((item) => chartNumericSeries(item, series.indexOf(item)));
  if (!numeric.length) return renderRedactedCanvas(series);
  const width = 520;
  const height = 220;
  const padding = { top: 14, right: 14, bottom: 38, left: 72 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const totals = labels.map((_, index) => numeric.reduce((sum, item) => sum + Math.max(0, item.values[index] || 0), 0));
  const max = chartNiceMax(Math.max(1, ...totals));
  const valueType = numeric[0]?.valueType;
  const barWidth = Math.max(4, (innerW / labels.length) * 0.7);
  const groupWidth = innerW / labels.length;
  const y = (value) => padding.top + innerH - (value / max) * innerH;
  const yTicks = chartAxisTicks(0, max, 4);
  const grid = yTicks.map((tick) => `
    <line x1="${padding.left}" x2="${padding.left + innerW}" y1="${y(tick)}" y2="${y(tick)}" class="chart-grid-line"/>
    <text x="${padding.left - 8}" y="${y(tick) + 4}" class="chart-axis-text chart-axis-text--right">${escapeHtml(chartFormatAxis(tick, valueType))}</text>
  `).join("");
  const labelTicks = chartLabelTicks(labels, Math.min(10, labels.length));
  const xLabels = labelTicks.map((tick) => {
    const cx = padding.left + groupWidth * (tick.index + 0.5);
    return `<text x="${cx}" y="${padding.top + innerH + 18}" class="chart-axis-text chart-axis-text--center">${escapeHtml(truncateChartLabel(tick.label, 10))}</text>`;
  }).join("");
  const bars = labels.map((label, groupIndex) => {
    let cursor = 0;
    return numeric.map((item) => {
      const raw = item.values[groupIndex];
      const value = Math.max(0, raw || 0);
      if (value <= 0) return "";
      const color = chartSeriesColor(item.chartSeriesIndex);
      const cx = padding.left + groupWidth * groupIndex + (groupWidth - barWidth) / 2;
      const yTop = y(cursor + value);
      const segH = y(cursor) - y(cursor + value);
      cursor += value;
      return `<rect x="${cx.toFixed(2)}" y="${yTop.toFixed(2)}" width="${barWidth.toFixed(2)}" height="${Math.max(1, segH).toFixed(2)}" style="fill:${color}" rx="2" class="report-chart-bar"><title>${escapeHtml(`${label} · ${item.label}: ${formatChartValue(raw, item.valueType)}`)}</title></rect>`;
    }).join("");
  }).join("");
  return `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" class="chart-svg" role="img" aria-label="Stacked bar chart">
    ${grid}
    <line x1="${padding.left}" x2="${padding.left + innerW}" y1="${y(0)}" y2="${y(0)}" class="chart-axis-baseline"/>
    ${bars}
    ${xLabels}
  </svg>`;
}

function renderLineChart(labels, series) {
  const numeric = series.filter((item) => !item.redacted).map((item) => chartNumericSeries(item, series.indexOf(item)));
  if (!numeric.length) return renderRedactedCanvas(series);
  const width = 520;
  const height = 220;
  const padding = { top: 14, right: 14, bottom: 38, left: 72 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const allValues = numeric.flatMap((item) => item.values);
  const { min, max } = chartAxisRange(allValues);
  const valueType = numeric[0]?.valueType;
  const x = (index) => padding.left + (labels.length === 1 ? innerW / 2 : (innerW * index) / (labels.length - 1));
  const y = (value) => padding.top + innerH - ((value - min) / (max - min || 1)) * innerH;
  const yTicks = chartAxisTicks(min, max, 4);
  const grid = yTicks.map((tick) => `
    <line x1="${padding.left}" x2="${padding.left + innerW}" y1="${y(tick)}" y2="${y(tick)}" class="chart-grid-line"/>
    <text x="${padding.left - 8}" y="${y(tick) + 4}" class="chart-axis-text chart-axis-text--right">${escapeHtml(chartFormatAxis(tick, valueType))}</text>
  `).join("");
  const labelTicks = chartLabelTicks(labels, Math.min(8, labels.length));
  const xLabels = labelTicks.map((tick) => `<text x="${x(tick.index)}" y="${padding.top + innerH + 18}" class="chart-axis-text chart-axis-text--center">${escapeHtml(truncateChartLabel(tick.label, 10))}</text>`).join("");
  const paths = numeric.map((item) => {
    const color = chartSeriesColor(item.chartSeriesIndex);
    const points = item.values.map((value, index) => value == null ? null : `${x(index).toFixed(2)},${y(value).toFixed(2)}`);
    const segments = [];
    let current = [];
    points.forEach((point) => {
      if (point) current.push(point);
      else if (current.length) { segments.push(current); current = []; }
    });
    if (current.length) segments.push(current);
    const linePath = segments.map((segment) => `M${segment.join(" L")}`).join(" ");
    const areaPath = segments.length === 1
      ? `M${segments[0][0].split(",")[0]},${y(Math.max(0, min)).toFixed(2)} L${segments[0].join(" L")} L${segments[0][segments[0].length - 1].split(",")[0]},${y(Math.max(0, min)).toFixed(2)} Z`
      : "";
    const dots = item.values.map((value, index) => value == null ? "" : `<circle cx="${x(index).toFixed(2)}" cy="${y(value).toFixed(2)}" r="3" style="fill:${color}"><title>${escapeHtml(`${labels[index]} · ${item.label}: ${formatChartValue(value, item.valueType)}`)}</title></circle>`).join("");
    return `
      ${areaPath ? `<path d="${areaPath}" style="fill:${color}" fill-opacity="0.08"/>` : ""}
      <path d="${linePath}" fill="none" style="stroke:${color}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>
      ${dots}
    `;
  }).join("");
  return `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" class="chart-svg" role="img" aria-label="Line chart">
    ${grid}
    <line x1="${padding.left}" x2="${padding.left + innerW}" y1="${y(0)}" y2="${y(0)}" class="chart-axis-baseline"/>
    ${paths}
    ${xLabels}
  </svg>`;
}

function renderComboChart(labels, series) {
  const numeric = series.filter((item) => !item.redacted).map((item) => chartNumericSeries(item, series.indexOf(item)));
  if (!numeric.length) return renderRedactedCanvas(series);
  const isLineSeries = (item) => item.valueType === "percent" || item.valueType === "score";
  const lineSeries = numeric.filter(isLineSeries);
  const barSeries = numeric.filter((item) => !isLineSeries(item));
  if (!barSeries.length) return renderLineChart(labels, series);
  const width = 520;
  const height = 220;
  const padding = { top: 14, right: 56, bottom: 38, left: 72 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const barValues = barSeries.flatMap((item) => item.values);
  const { min: barMin, max: barMax } = chartAxisRange(barValues);
  const lineValues = lineSeries.flatMap((item) => item.values);
  const { min: lineMin, max: lineMax } = lineValues.length ? chartAxisRange(lineValues) : { min: 0, max: 1 };
  const barType = barSeries[0]?.valueType;
  const lineType = lineSeries[0]?.valueType;
  const groupWidth = innerW / Math.max(1, labels.length);
  const barWidth = Math.max(2, (groupWidth - 6) / barSeries.length);
  const yBar = (value) => padding.top + innerH - ((value - barMin) / (barMax - barMin || 1)) * innerH;
  const yLine = (value) => padding.top + innerH - ((value - lineMin) / (lineMax - lineMin || 1)) * innerH;
  const xCenter = (index) => padding.left + groupWidth * (index + 0.5);
  const yTicks = chartAxisTicks(barMin, barMax, 4);
  const grid = yTicks.map((tick) => `
    <line x1="${padding.left}" x2="${padding.left + innerW}" y1="${yBar(tick)}" y2="${yBar(tick)}" class="chart-grid-line"/>
    <text x="${padding.left - 8}" y="${yBar(tick) + 4}" class="chart-axis-text chart-axis-text--right">${escapeHtml(chartFormatAxis(tick, barType))}</text>
  `).join("");
  const rightTicks = lineSeries.length ? chartAxisTicks(lineMin, lineMax, 4).map((tick) => `
    <text x="${padding.left + innerW + 8}" y="${yLine(tick) + 4}" class="chart-axis-text chart-axis-text--left">${escapeHtml(chartFormatAxis(tick, lineType))}</text>
  `).join("") : "";
  const labelTicks = chartLabelTicks(labels, Math.min(10, labels.length));
  const xLabels = labelTicks.map((tick) => `<text x="${xCenter(tick.index)}" y="${padding.top + innerH + 18}" class="chart-axis-text chart-axis-text--center">${escapeHtml(truncateChartLabel(tick.label, 10))}</text>`).join("");
  const bars = labels.map((label, groupIndex) => barSeries.map((item, seriesIndex) => {
    const value = item.values[groupIndex];
    if (value == null) return "";
    const color = chartSeriesColor(item.chartSeriesIndex);
    const cx = padding.left + groupWidth * groupIndex + 3 + barWidth * seriesIndex;
    const baseline = yBar(0);
    const top = yBar(value);
    const barH = Math.abs(top - baseline);
    const barY = value >= 0 ? top : baseline;
    return `<rect x="${cx.toFixed(2)}" y="${barY.toFixed(2)}" width="${barWidth.toFixed(2)}" height="${Math.max(1, barH).toFixed(2)}" style="fill:${color}" rx="2" opacity="0.92" class="report-chart-bar"><title>${escapeHtml(`${label} · ${item.label}: ${formatChartValue(value, item.valueType)}`)}</title></rect>`;
  }).join("")).join("");
  const lines = lineSeries.map((item) => {
    const color = chartSeriesColor(item.chartSeriesIndex);
    const points = item.values.map((value, index) => value == null ? null : `${xCenter(index).toFixed(2)},${yLine(value).toFixed(2)}`);
    const segments = [];
    let current = [];
    points.forEach((point) => {
      if (point) current.push(point);
      else if (current.length) { segments.push(current); current = []; }
    });
    if (current.length) segments.push(current);
    const linePath = segments.map((segment) => `M${segment.join(" L")}`).join(" ");
    const dots = item.values.map((value, index) => value == null ? "" : `<circle cx="${xCenter(index).toFixed(2)}" cy="${yLine(value).toFixed(2)}" r="3" style="fill:${color}"><title>${escapeHtml(`${labels[index]} · ${item.label}: ${formatChartValue(value, item.valueType)}`)}</title></circle>`).join("");
    return `<path d="${linePath}" fill="none" style="stroke:${color}" stroke-width="2.2" stroke-linejoin="round" stroke-linecap="round"/>${dots}`;
  }).join("");
  return `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" class="chart-svg" role="img" aria-label="Combination chart">
    ${grid}
    <line x1="${padding.left}" x2="${padding.left + innerW}" y1="${yBar(0)}" y2="${yBar(0)}" class="chart-axis-baseline"/>
    ${bars}
    ${lines}
    ${xLabels}
    ${rightTicks}
  </svg>`;
}

function renderRedactedCanvas(series) {
  const labels = series.filter((item) => item.redacted).map((item) => item.label || item.key || "Series");
  const summary = labels.length ? labels.slice(0, 3).join(", ") : "this view";
  return `<div class="chart-empty chart-empty--redacted">Financial series restricted · ${escapeHtml(summary)}${labels.length > 3 ? "…" : ""}</div>`;
}

function truncateChartLabel(label, max) {
  const text = String(label ?? "");
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 1))}…`;
}

function formatChartValue(value, valueType) {
  if (value == null) return "N/A";
  if (valueType === "money") return formatReportMoney(value);
  if (valueType === "percent") return formatPercent(value);
  if (valueType === "score") return `${value} pts`;
  return String(value);
}

function groupedReportRows(claims, field) {
  const grouped = new Map();
  claims.forEach((claim) => {
    const financial = claimFinancialView(claim);
    const key = claim[field] || "Unknown";
    const existing = grouped.get(key) || { label: key, claims: 0, expected: 0, paid: 0, owed: 0, highRisk: 0 };
    existing.claims += 1;
    existing.expected += financial.expectedReimbursement;
    existing.paid += financial.totalInsurancePaid;
    existing.owed += financial.sourceOutstanding;
    if (workPriorityScore(claim) >= 80) existing.highRisk += 1;
    grouped.set(key, existing);
  });
  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      collectionRate: row.expected > 0 ? formatPercentValue(Math.round((row.paid / row.expected) * 10000) / 100) : "N/A"
    }))
    .sort((a, b) => b.owed - a.owed);
}

function denialReasonRows(claims) {
  return groupedOperationalRows(claims.filter(isDeniedClaim), (claim) => denialReason(claim), "reason");
}

function payerWorklistRows(claims) {
  const rows = [];
  ["payerFollowUp", "waitingPrimary", "waitingSecondary", "missingPayerResponse", "denied"].forEach((queueKey) => {
    groupedOperationalRows(claims.filter((claim) => matchesQueue(claim, queueKey)), (claim) => claim.primaryPayer || "Unknown", "payer")
      .forEach((row) => rows.push({ ...row, queueKey, queue: queueLabel(queueKey), outstanding: row.owed }));
  });
  return rows.sort((a, b) => b.owed - a.owed || b.guarded - a.guarded);
}

function ownerWorkloadRows(claims) {
  const openClaims = openArClaims(claims);
  return groupedOperationalRows(openClaims, (claim) => claim.assignedTo || "Unassigned", "owner")
    .map((row) => {
      const ownerClaims = openClaims.filter((claim) => (claim.assignedTo || "Unassigned") === row.owner);
      return { ...row, openClaims: ownerClaims.length, pastDue: ownerClaims.filter(isPastDue).length };
    })
    .sort((a, b) => b.pastDue - a.pastDue || b.owed - a.owed);
}

function slaAgingRows(claims) {
  const openClaims = openArClaims(claims);
  return ["Overdue", "Due Today", "Due 1-7", "Due 8-14", "Due 15+", "No Date"].map((bucket) => {
    const bucketClaims = openClaims.filter((claim) => slaBucket(claim) === bucket);
    return {
      bucket,
      claims: bucketClaims.length,
      owed: sumClaimOutstanding(bucketClaims),
      guarded: bucketClaims.filter(hasCalculationGuards).length
    };
  }).filter((row) => row.claims > 0);
}

function groupedOperationalRows(claims, labelForClaim, labelKey) {
  const grouped = new Map();
  claims.forEach((claim) => {
    const financial = claimFinancialView(claim);
    const label = labelForClaim(claim) || "Unknown";
    const existing = grouped.get(label) || { [labelKey]: label, claims: 0, owed: 0, guarded: 0 };
    existing.claims += 1;
    existing.owed += financial.sourceOutstanding;
    if (hasCalculationGuards(claim)) existing.guarded += 1;
    grouped.set(label, existing);
  });
  return Array.from(grouped.values()).sort((a, b) => b.owed - a.owed || b.guarded - a.guarded);
}

function denialReason(claim) {
  const text = claimOperationalText(claim);
  if (/auth|authorization/.test(text)) return "Authorization";
  if (/credential|provider/.test(text)) return "Provider/Credentialing";
  if (/cob|coverage/.test(text)) return "Coverage/COB";
  if (/modifier|procedure|correct/.test(text)) return "Coding/Correction";
  if (/medical record|documentation/.test(text)) return "Documentation";
  if (/same external|manual review|payer/.test(text)) return "Payer Review";
  return "Unclassified";
}

function queueLabel(key) {
  return queueDefinitions.find((queue) => queue.key === key)?.label || key;
}

function queueKeyForLabel(label) {
  const normalized = String(label || "").toLowerCase();
  return queueDefinitions.find((queue) => queue.label.toLowerCase() === normalized)?.key || normalized.replace(/[^a-z0-9]+(.)/g, (_match, letter) => letter.toUpperCase()) || "all";
}

function hasCalculationGuards(claim) {
  return evaluateClaimCalculationGuards(claim).length > 0;
}

// CT-TRACKER "Follow-Up Due Status" parity. Date-only (no match/financial
// gating): folds the six slaBucket() states into the three canonical ones.
//   Overdue + Due Today            -> "Due Now"   (follow-up date today or earlier)
//   Due 1-7 / Due 8-14 / Due 15+   -> "Future"    (follow-up date after today)
//   No Date                        -> "No Follow-Up Date"
function followUpDueStatus(claim) {
  const bucket = slaBucket(claim);
  if (bucket === "No Date") return "No Follow-Up Date";
  if (bucket === "Overdue" || bucket === "Due Today") return "Due Now";
  return "Future";
}

// Row-level twin for the API lane, where the grid renders serialized rows
// rather than claim objects. Reads the same follow-up date the cell displays
// (trackerApiCellRawValue applies the client overlay), so the post-filter
// agrees with what the biller sees.
function followUpDueStatusFromDate(followUpDate) {
  if (!followUpDate) return "No Follow-Up Date";
  const due = localDateFromDateOnly(followUpDate);
  if (!due) return "No Follow-Up Date";
  return due <= todayStart() ? "Due Now" : "Future";
}

function slaBucket(claim) {
  if (!claim.followUpDate) return "No Date";
  const due = localDateFromDateOnly(claim.followUpDate);
  if (!due) return "No Date";
  const days = Math.ceil((due - todayStart()) / 86400000);
  if (days < 0) return "Overdue";
  if (days === 0) return "Due Today";
  if (days <= 7) return "Due 1-7";
  if (days <= 14) return "Due 8-14";
  return "Due 15+";
}

function markStatementPrinted(serviceId) {
  if (!hasPermission(PERMISSIONS.MARK_STATEMENTS)) return denyAction(PERMISSIONS.MARK_STATEMENTS, "mark patient statement");
  const row = getClaims()
    .flatMap((claim) => claim.services.map((service) => ({ claim, service })))
    .find((item) => item.service.serviceId === serviceId);
  if (!row) return showToast("That statement line is unavailable.");
  const blockReason = statementEligibilityBlockReason(row.service, row.claim);
  if (blockReason === "secondary-on-claim") return showToast("Patient responsibility is $0 while a secondary exists on the claim.");
  if (blockReason === "open-follow-up") return showToast("Clear the active follow-up action before printing, then recheck every statement rule.");
  if (blockReason === "no-patient-balance") return showToast("This service line has no patient balance to bill.");
  if (blockReason) return showToast("Only eligible PR 1 / PR 2 / PR 3 lines can be printed.");
  state.statementPrints = {
    ...(state.statementPrints || {}),
    [serviceId]: {
      printedAt: timestamp(),
      by: activeInternalUser(),
      amount: truePatientResponsibility(row.service)
    }
  };
  audit(`${serviceId}: patient statement printed`, activeInternalUser());
  saveState();
  renderClientView();
  showToast("Statement marked printed.");
}

function monthlySummaryRows(serviceRows) {
  const buckets = new Map();
  serviceRows.forEach(({ claim, service }) => {
    const month = service.dos ? service.dos.slice(0, 7) : "No DOS";
    const current = buckets.get(month) || { month, count: 0, outstanding: 0 };
    current.count += 1;
    current.outstanding += serviceFinancialView(service, claim).sourceOutstanding;
    buckets.set(month, current);
  });
  return Array.from(buckets.values()).sort((a, b) => b.month.localeCompare(a.month));
}

function clientStatus(claim) {
  if (claim.match === "Paid" || claim.match === "Over Paid") return "Paid";
  if (claim.claimStatus === "Authorization Needed") return "Client Action Needed";
  if (claim.assignmentStatus === "Submitted For Review") return "In Review";
  if (claimFinancialView(claim).sourceOutstanding > 0) return "In Follow-Up";
  return claim.claimStatus || "Open";
}

function clientOpenNeed(claim) {
  if (claim.claimStatus === "Authorization Needed") return "Authorization needed";
  if (isNoRateMatch(claim.match)) return "Rate review in progress";
  if (claim.paymentAttribution?.status?.includes("Manual review")) return "Payment review in progress";
  if (isPastDue(claim)) return "Insurance follow-up";
  if (claimFinancialView(claim).sourceOutstanding > 0) return "Open payer balance";
  return "None";
}

function actionOptions(currentAction = "") {
  return followUpActionOptionsHtml(currentAction, { includeClear: false });
}

function pill(text, tone) {
  return `<span class="pill ${tone}">${escapeHtml(text)}</span>`;
}

function uniqueValues(rows, field) {
  return Array.from(new Set(rows.map((row) => row[field]).filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b)));
}

function uniqueClaimIds(claims = []) {
  return Array.from(new Set(claims
    .map((claim) => (typeof claim === "string" ? claim : claim?.claimId))
    .filter(Boolean)
    .map(normalizeClaimId)));
}

function uniqueClaimsFromRows(rows = []) {
  const byId = new Map();
  rows.forEach((row) => {
    const claim = row?.claim || row;
    if (claim?.claimId && !byId.has(claim.claimId)) byId.set(claim.claimId, claim);
  });
  return Array.from(byId.values());
}

function topPayerWorkload(claims = []) {
  const grouped = new Map();
  claims.forEach((claim) => {
    const payer = claim.primaryPayer || claim.secondaryPayer || "Unknown";
    const current = grouped.get(payer) || { payer, claims: 0, owed: 0 };
    current.claims += 1;
    current.owed += claimFinancialView(claim).sourceOutstanding;
    grouped.set(payer, current);
  });
  return Array.from(grouped.values()).sort((a, b) => b.owed - a.owed || b.claims - a.claims)[0] || null;
}

// The hero empty state. The body line used to be HARDCODED, so a FAILURE
// rendered through here ("Could not load the approval queue: ...") was followed
// by "Use AR filters or assignment controls to build the next worklist" -
// body copy belonging to a different sentence - with its Retry orphaned outside
// the box. Both are now caller-supplied; the default is unchanged, so all ~65
// existing call sites render byte-identically.
function empty(text, options = {}) {
  const body = String(options.body || "Use AR filters or assignment controls to build the next worklist.");
  const actionsHtml = String(options.actionsHtml || "");
  return `
    <div class="empty-state">
      <strong>${escapeHtml(text)}</strong>
      <p>${escapeHtml(body)}</p>
      ${actionsHtml ? `<div class="vob-empty__actions">${actionsHtml}</div>` : ""}
    </div>
  `;
}

// ONE empty-state renderer on the VOB recipe (styles.css "VOB PARITY - EMPTY
// STATES"). tone: "" quiet | "hero" | "blocked". Everything is escaped;
// actionsHtml is caller-built markup and must already be safe.
function emptyStateHtml({ tone = "", glyph = "", title = "", body = "", meta = "", actionsHtml = "", role = "status" } = {}) {
  const cls = ["vob-empty", tone === "hero" ? "vob-empty--hero" : "", tone === "blocked" ? "vob-empty--hero vob-empty--blocked" : ""].filter(Boolean).join(" ");
  return `
    <div class="${cls}" role="${escapeHtml(role)}">
      ${glyph ? `<div class="${tone ? "vob-empty__puck" : "vob-empty__glyph"}" aria-hidden="true">${escapeHtml(glyph)}</div>` : ""}
      ${title ? `<p class="vob-empty__title">${escapeHtml(title)}</p>` : ""}
      ${body ? `<p class="vob-empty__body">${escapeHtml(body)}</p>` : ""}
      ${meta ? `<p class="vob-empty__meta">${escapeHtml(meta)}</p>` : ""}
      ${actionsHtml ? `<div class="vob-empty__actions">${actionsHtml}</div>` : ""}
    </div>
  `;
}

// When the session started, for the account line an administrator has to act on.
const PORTAL_SESSION_STARTED_AT = new Date();
function portalSessionStartedClock(at = PORTAL_SESSION_STARTED_AT) {
  const when = at instanceof Date && !Number.isNaN(at.getTime()) ? at : new Date();
  return `${String(when.getHours()).padStart(2, "0")}:${String(when.getMinutes()).padStart(2, "0")}`;
}

// The facts a supervisor needs quoted back off a screenshot. Read from the
// session ONLY - never from the denial, which must never become a second source
// of truth about who the reader is.
function scopeDenialAccountLine() {
  const scope = (typeof state !== "undefined" && state && state.authUserScope) || null;
  const username = String((typeof state !== "undefined" && state && state.activeUserName) || "").trim() || "(not signed in)";
  const roleKey = String((scope && scope.role) || (typeof state !== "undefined" && state && state.role) || "").trim();
  const label = (roleProfiles[roleKey] && roleProfiles[roleKey].label) || roleKey || "(no role)";
  return `Account ${username} · ${label} · signed in ${portalSessionStartedClock()}`;
}

// A prefilled ticket, so "open a ticket" is not itself a piece of homework.
function scopeDenialTicketDraft() {
  const denial = portalScopeDenial() || normalizeScopeDenial({ code: "no_scope" }, "session");
  const scope = (typeof state !== "undefined" && state && state.authUserScope) || null;
  const username = String((typeof state !== "undefined" && state && state.activeUserName) || "").trim() || "(unknown)";
  const roleKey = String((scope && scope.role) || (typeof state !== "undefined" && state && state.role) || "").trim();
  const label = (roleProfiles[roleKey] && roleProfiles[roleKey].label) || roleKey || "(unknown)";
  return {
    category: "bug",
    subject: `Portal access not set up — ${username}`,
    message: [
      "I signed in and the portal has nothing to show me.",
      `Reason reported by the portal: ${denial.code} — ${denial.message}`,
      `Account: ${username}`,
      `Role: ${label}`
    ].join("\n")
  };
}

// THE NO-SCOPE PANEL. Amber "blocked" variant, because the design system
// reserves it for an emptiness that BLOCKS a workflow, and this one does.
//
// It must not be a dead end: title (what is wrong), message (the server's own
// sentence, including who to ask), remedy (what to ask for), the account facts,
// and two actions. "Check again" is NOT a Retry - there is nothing to retry, a
// deterministic 403 will 403 again; it is what a biller presses AFTER their
// manager says "done". The ticket button is suppressed when there is no portal
// API, because that lane answers "Connect to the portal API before sending a
// ticket" - a second dead end inside the first.
function scopeDeniedPanelHtml(denial, { compact = false } = {}) {
  const resolved = denial || normalizeScopeDenial({ code: "no_scope" }, "session");
  const canTicket = Boolean(typeof workflowApiBase === "function" && workflowApiBase()) && typeof fetch === "function";
  const actionsHtml = [
    canTicket ? `<button type="button" class="ghost-button" data-scope-denial-ticket="1">Open a ticket</button>` : "",
    `<button type="button" class="ghost-button" data-scope-denial-recheck="1">Check again</button>`
  ].filter(Boolean).join("");
  const body = [
    compact ? resolved.message : `${resolved.message} ${resolved.remedy}`,
    canTicket ? "" : "Ticket submission is temporarily unavailable. Contact your TMS supervisor directly."
  ].filter(Boolean).join(" ");
  // COMPACT is the summary strip, which sits directly above the grid body that
  // carries the full panel. Repeating the puck, the remedy and the account line
  // 200px apart is noise, not emphasis: the strip states the problem and offers
  // the two actions, and the panel below carries the evidence.
  return emptyStateHtml({
    tone: "blocked",
    // role="status", not "alert": a denial is a standing condition present at
    // page load, not an event, and alert would interrupt a screen-reader user
    // mid-sentence on every render.
    role: "status",
    glyph: compact ? "" : "⚠",
    title: resolved.title,
    body,
    meta: compact ? "" : scopeDenialAccountLine(),
    actionsHtml
  });
}

// THE ONE MONEY FORMATTER. Everything else in this file is a thin wrapper on
// it: money(), Formatters.moneyExact(), statementsMoney(), formatDecimal(),
// compactMoney() and formatReportMoney(). There used to be six independent
// implementations and they disagreed on two things that matter:
//
//   negatives  Intl emitted -$1,234.56, statementsMoney hand-built $-1,234.56
//              (sign after the symbol), and compactMoney discarded the sign
//              entirely via Math.abs - a refund rendered as a receipt.
//   locale     formatDecimal called toLocaleString(undefined, ...), so the
//              grouping and decimal separators followed the machine's locale
//              while every other formatter was pinned to en-US.
//
// One implementation means one negative rendering and one locale, forever.
// Decimal COUNT stays per-surface on purpose - 0 for the whole-dollar lane,
// 2 where a figure is reconciled - and moving the grid to cents is a separate,
// screenshotted decision (DESIGN_DIRECTION_2026.md checklist M2).
//
// An UNKNOWN_MONEY value renders as an explicit unknown, never as $0.
// A real zero still renders $0. That distinction is the whole point.
function formatMoneyValue(value, { decimals = 0, currency = true, unknown = MONEY_UNKNOWN_TEXT } = {}) {
  if (isUnknownMoney(value)) return unknown;
  const options = currency
    ? { style: "currency", currency: "USD", minimumFractionDigits: decimals, maximumFractionDigits: decimals }
    : { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
  // ONE Intl.NumberFormat per (currency, decimals) pair, not one per CALL.
  // Constructing the formatter - not formatting with it - was 343ms of a
  // 500-row All Data render, which formats ~18 money cells per row. Same
  // locale, same options, byte-identical output. The cache hangs off the
  // function so the shipped source stays the single self-contained unit the
  // money verifiers extract and execute on their own.
  const formatters = formatMoneyValue.formatters || (formatMoneyValue.formatters = new Map());
  const formatterKey = `${currency ? "c" : "n"}:${decimals}`;
  let formatter = formatters.get(formatterKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", options);
    formatters.set(formatterKey, formatter);
  }
  return formatter.format(moneyNumber(value));
}

function money(value) {
  return formatMoneyValue(value, { decimals: 0 });
}

// UI-100 #95: canonical formatter surface so call sites stop hand-rolling Intl options.
const Formatters = {
  money,
  moneyExact(value) {
    return formatMoneyValue(value, { decimals: 2 });
  },
  unknown: MONEY_UNKNOWN_TEXT,
  isUnknown: isUnknownMoney,
  UNKNOWN: UNKNOWN_MONEY,
  int(value) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(moneyNumber(value));
  },
  count(value) {
    return new Intl.NumberFormat("en-US").format(moneyNumber(value));
  },
  pct(value, digits = 1) {
    const n = percentNumber(value, { percentStringAsRatio: true });
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
  },
  pctRaw(value, digits = 1) {
    const n = percentNumber(value);
    if (!Number.isFinite(n)) return "—";
    return `${n.toFixed(digits)}%`;
  }
};
if (typeof window !== "undefined") window.Formatters = Formatters;

// UI-100 #91: glossary for billing/healthcare jargon. Used by .help-term <abbr> decoration.
const TMS_GLOSSARY = {
  "AR": "Accounts Receivable — outstanding insurance and patient balances.",
  "COB": "Coordination of Benefits — when a patient has more than one payer, COB determines which pays first.",
  "PR codes": "Patient Responsibility adjustment codes from the 835 ERA (e.g. PR-1 deductible, PR-2 coinsurance, PR-3 copay).",
  "PR code": "Patient Responsibility adjustment code from the 835 ERA (e.g. PR-1 deductible, PR-2 coinsurance, PR-3 copay).",
  "ERA": "Electronic Remittance Advice (835) — the payer's electronic explanation of payment.",
  "EOB": "Explanation of Benefits — payer-generated payment/denial detail per claim.",
  "EVV": "Electronic Visit Verification — required visit-capture evidence (start/end time, location) for many home/community services.",
  "timely filing": "The payer-specific window between date of service and when a claim must be received. Past this, claims are denied as untimely.",
  "BCBA": "Board Certified Behavior Analyst — supervises ABA programs and bills 97155 / assessment codes.",
  "RBT": "Registered Behavior Technician — delivers direct ABA services (typically 97153) under BCBA supervision.",
  "DOS": "Date of Service.",
  "CPT": "Current Procedural Terminology — the AMA-maintained code set used to bill professional services.",
  "ABA": "Applied Behavior Analysis — therapy modality with its own CPT family (97151-97158).",
  "97153": "Adaptive Behavior Treatment by Protocol — direct 1:1 therapy time, typically billed by an RBT.",
  "97155": "Adaptive Behavior Treatment with Protocol Modification — BCBA-led, often supervisory of 97153.",
  "97156": "Family Adaptive Behavior Treatment Guidance — caregiver/parent training.",
  "underpayment": "Paid amount less than the contracted expected amount, after adjustments.",
  "overpayment": "Paid amount greater than the contracted expected amount.",
  "Synapse": "Internal data platform (Azure Synapse) backing the live tracker queries.",
  "denial": "Payer refusal to pay — distinct from a zero-paid line that simply applies patient responsibility."
};

// UI-100 #37: apply a date-range preset to a pair of <input type="date"> fields.
// UI-100 #81: demo vs live data callout — visible whenever the data source is a JSON snapshot.
//
// ROLE GATE (2026-08-03). This strip is an INTERNAL OPERATIONAL WARNING: it
// names our Azure mode and instructs the reader to restore it "before
// production AR work". It gated on the DATA SOURCE alone and never on the
// principal, so on a local-fallback boot it rendered full-width above a
// practice owner's own money on Client Home — TMS plumbing, on a client's
// landing page, in our internal vocabulary.
//
// The identical sentence already ships in the sidebar Data Source card, and
// that card asks showsInternalDiagnostics(). Asking the same question here
// rather than minting a second answer is the point: two answers to "may this
// principal see our plumbing?" is how one of them silently drifts. It is also
// STRICTER than an external-client check — an OffshoreBiller or a Manager
// cannot restore Azure mode either, so the instruction is noise to them.
function updateDemoDataCallout() {
  const wrap = document.getElementById("demoDataCallout");
  if (!wrap) return;
  const internal = showsInternalDiagnostics();
  const onSampleData = !trackerApiEnabled() && sourceDataInfo && !sourceDataInfo.isLocalRealData && /demo|snapshot|json|fallback/i.test(`${sourceDataInfo.label || ""} ${sourceDataInfo.detail || ""}`);
  const isDemo = internal && onSampleData;
  // SAMPLE-DATA BANNER (2026-08-11). The Reports error state offers a one-click
  // switch to ./data/sample_claims.json, and it flips the WHOLE portal (All Data
  // too), not Reports alone. Until now the only two surfaces that disclosed the
  // switch - this callout and the sidebar Data Source card - both asked
  // showsInternalDiagnostics(), so a Manager, OffshoreBiller or ClientBiller who
  // clicked it saw fabricated claims, with payment dates shifted to look current,
  // and NOTHING on screen said so. That is the one thing a money screen may never
  // do quietly. So whenever a live API is configured and we are on sample data
  // anyway, the banner shows to EVERY role and cannot be dismissed.
  //
  // This does not reopen the leak the role gate above was added to close. That
  // gate exists because the internal copy names our Azure mode and tells the
  // reader to restore it. The sample-data copy names no plumbing at all - it says
  // the numbers are not real, which is exactly what every role needs to know.
  const sampleDataTrap = Boolean(onSampleData) && Boolean(workflowApiBase());
  let dismissed = false;
  try { dismissed = localStorage.getItem("tms.portal.demoCalloutDismissed") === "1"; } catch (_) {}
  const show = sampleDataTrap || (Boolean(isDemo) && !dismissed);
  wrap.classList.toggle("demo-callout--sample", sampleDataTrap);
  const calloutPill = wrap.querySelector(".demo-callout-pill");
  const calloutText = wrap.querySelector(".demo-callout-text");
  const calloutDismiss = document.getElementById("demoCalloutDismiss");
  const calloutUseLive = document.getElementById("demoCalloutUseLive");
  if (calloutPill) calloutPill.textContent = sampleDataTrap ? "Sample data" : "Local fallback";
  if (calloutText) {
    calloutText.textContent = sampleDataTrap
      ? "These are sample numbers for demonstration, not your live claims. Nothing here reflects real billing."
      : "Current information is temporarily unavailable. Please try again shortly.";
  }
  if (calloutDismiss) calloutDismiss.style.display = sampleDataTrap ? "none" : "";
  if (calloutUseLive) {
    calloutUseLive.style.display = sampleDataTrap ? "" : "none";
    if (calloutUseLive.dataset.useLiveBound !== "1") {
      calloutUseLive.dataset.useLiveBound = "1";
      calloutUseLive.addEventListener("click", () => {
        // app-config.js re-asserts window.TMS_*_API_MODE = "api" on every boot,
        // so clearing the two stored keys and reloading is a complete return.
        try { localStorage.removeItem(TRACKER_API_MODE_KEY); } catch (_) {}
        try { localStorage.removeItem(REPORT_API_MODE_KEY); } catch (_) {}
        window.location.reload();
      });
    }
  }
  wrap.classList.toggle("hidden", !show);
  // Belt as well as braces, for the same reason enforceExternalClientChrome()
  // above writes an inline display: a role gate that lives only in a stylesheet
  // is a role gate that silently lapses. Without this line the whole gate rests
  // on one .demo-callout.hidden { display: none } rule in a file this lane does
  // not own.
  if (show) wrap.style.removeProperty("display");
  else wrap.style.display = "none";
  // Bound once, not once-per-call. This function now re-runs on every render
  // (the role switcher changes the answer without a reload), and a { once:true }
  // listener added per render is a listener leak, not an idempotent binding.
  const dismiss = document.getElementById("demoCalloutDismiss");
  if (dismiss && dismiss.dataset.demoCalloutBound !== "1") {
    dismiss.dataset.demoCalloutBound = "1";
    dismiss.addEventListener("click", () => {
      try { localStorage.setItem("tms.portal.demoCalloutDismissed", "1"); } catch (_) {}
      wrap.classList.add("hidden");
    });
  }
}

// UI-100 #94: first-run product tour. Runs once per machine; resets with local state reset.
const FIRST_TOUR_KEY = "tms.portal.firstRunSeen";
const TOUR_STEPS = [
  { title: "Welcome to the AR Command Center", body: "This is your Excel-parity claims worklist. Filters and slicers are on the left rail; the spreadsheet fills the rest of the workspace." },
  { title: "AR is your home base", body: "The default view mirrors the Excel tracker: every claim, every column, scrollable. Headers and the first three columns stay sticky as you scroll." },
  { title: "Click any column to sort", body: "Sort cycles ascending → descending → off, with arrow indicators in the header. Filter from the chevron next to the column name." },
  { title: "⌘K jumps anywhere", body: "Press ⌘K (or Ctrl+K, or '/') to fuzzy-find a claim by ID, patient, payer, client, or provider. Enter opens the drawer." },
  { title: "Roles change scope", body: "Switch roles from the left rail to act as Biller, Manager, Offshore, etc. A banner reminds you of the active scope." }
];
function maybeRunFirstTour() {
  // Keep the app usable immediately on first load. The original auto-open
  // tour blocked navigation behind a backdrop and caused first-run clicks to
  // hit the overlay instead of the workbench controls.
  try {
    if (localStorage.getItem(FIRST_TOUR_KEY) !== "1") {
      localStorage.setItem(FIRST_TOUR_KEY, "1");
    }
  } catch (_) {}
}
let __tourIdx = 0;
function startFirstTour() {
  const wrap = document.getElementById("firstRunTour");
  if (!wrap) return;
  __tourIdx = 0;
  wrap.classList.remove("hidden");
  renderTourStep();
  document.getElementById("tourNext")?.addEventListener("click", tourNext);
  document.getElementById("tourPrev")?.addEventListener("click", tourPrev);
  wrap.querySelectorAll("[data-tour-skip]").forEach((n) => n.addEventListener("click", endTour));
  document.addEventListener("keydown", tourKey);
}
function renderTourStep() {
  const step = TOUR_STEPS[__tourIdx];
  if (!step) return endTour();
  document.getElementById("tourStepNum").textContent = String(__tourIdx + 1);
  document.getElementById("tourStepTotal").textContent = String(TOUR_STEPS.length);
  document.getElementById("tourTitle").textContent = step.title;
  document.getElementById("tourBody").textContent = step.body;
  const next = document.getElementById("tourNext");
  const prev = document.getElementById("tourPrev");
  if (prev) prev.disabled = __tourIdx === 0;
  if (next) next.textContent = __tourIdx === TOUR_STEPS.length - 1 ? "Done" : "Next";
}
function tourNext() {
  if (__tourIdx >= TOUR_STEPS.length - 1) return endTour();
  __tourIdx += 1;
  renderTourStep();
}
function tourPrev() {
  if (__tourIdx === 0) return;
  __tourIdx -= 1;
  renderTourStep();
}
function tourKey(e) {
  if (document.getElementById("firstRunTour")?.classList.contains("hidden")) return;
  if (e.key === "Escape") { e.preventDefault(); endTour(); }
  else if (e.key === "ArrowRight" || e.key === "Enter") { e.preventDefault(); tourNext(); }
  else if (e.key === "ArrowLeft") { e.preventDefault(); tourPrev(); }
}
function endTour() {
  const wrap = document.getElementById("firstRunTour");
  if (wrap) wrap.classList.add("hidden");
  try { localStorage.setItem(FIRST_TOUR_KEY, "1"); } catch (_) {}
  document.removeEventListener("keydown", tourKey);
}

// UI-100 #44: theme handling. Persists in localStorage and honors prefers-color-scheme.
const THEME_STORAGE_KEY = "tms.portal.theme";
function getStoredTheme() {
  try { return localStorage.getItem(THEME_STORAGE_KEY) || "auto"; } catch (_) { return "auto"; }
}
function setStoredTheme(value) {
  try { localStorage.setItem(THEME_STORAGE_KEY, value); } catch (_) {}
}
function resolveTheme(mode) {
  if (mode === "light" || mode === "dark") return mode;
  if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}
// UI-100 #85: unsaved-changes indicator driven by the workflow command queue.
function updateUnsavedIndicator() {
  const wrap = document.getElementById("unsavedIndicator");
  const count = document.getElementById("unsavedCount");
  if (!wrap || !count) return;
  const queue = Array.isArray(state.workflowCommandQueue) ? state.workflowCommandQueue : [];
  const undelivered = queue.filter((command) => ["pending", "failed", "local_fallback"].includes(command?.status)).length;
  count.textContent = String(undelivered);
  wrap.classList.toggle("hidden", undelivered === 0);
  wrap.classList.toggle("is-many", undelivered >= 5);
}
// Hook the existing saveState so any state mutation refreshes the chip.
(function hookSaveStateForUnsaved() {
  if (typeof saveState !== "function" || saveState.__unsavedHooked) return;
  const orig = saveState;
  saveState = function patchedSaveState() {
    const r = orig.apply(this, arguments);
    try { updateUnsavedIndicator(); } catch (_) {}
    return r;
  };
  saveState.__unsavedHooked = true;
})();

function applyThemeFromState() {
  const mode = getStoredTheme();
  const resolved = resolveTheme(mode);
  document.documentElement.dataset.theme = resolved;
  const btn = document.getElementById("themeToggleBtn");
  if (btn) {
    btn.setAttribute("aria-pressed", resolved === "dark" ? "true" : "false");
    btn.title = `Switch to ${resolved === "dark" ? "light" : "dark"} mode (currently ${mode === "auto" ? `auto · ${resolved}` : resolved})`;
  }
  // React to system changes when in auto mode.
  if (mode === "auto" && window.matchMedia) {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    if (!mq.__tmsBound) {
      mq.__tmsBound = true;
      mq.addEventListener?.("change", () => { if (getStoredTheme() === "auto") applyThemeFromState(); });
    }
  }
  document.getElementById("themeToggleBtn")?.addEventListener("click", toggleThemeOnce, { once: true });
}
function toggleThemeOnce() {
  const current = resolveTheme(getStoredTheme());
  setStoredTheme(current === "dark" ? "light" : "dark");
  applyThemeFromState();
}

// Excel-like keyboard navigation for the claims sheet:
//   ↓ / ↑   move focus one row
//   PageDown / PageUp   move 10 rows
//   Home / End   first / last visible row
//   Enter / Space   open drawer for focused row
//   /, ⌘K   open the command palette (already wired separately)
function installSheetKeyboardNav() {
  if (typeof document === "undefined" || document.__tmsSheetNav) return;
  document.__tmsSheetNav = true;
  document.addEventListener("keydown", (e) => {
    // Bail if user is typing in an input/select/textarea, or a modal is open.
    const t = e.target;
    if (e.defaultPrevented) return;
    if (t && /INPUT|TEXTAREA|SELECT/.test(t.tagName)) return;
    // Steer the sheet only while focus is on the page itself or inside the
    // grid. A focused sidebar item, topbar control, button, link or dialog
    // keeps its native key behaviour: Enter on "My Work" must navigate,
    // not open the first claim's drawer (2026-09-02 shell census: Enter and
    // Space on every nav item opened the drawer while All Data was active).
    if (t && t !== document.body && t !== document.documentElement && typeof t.closest === "function") {
      if (t.closest("button, a, [role='button'], [role='tab'], [role='menuitem'], [contenteditable='true'], #appSidebar, .topbar, [role='dialog']")) return;
      if (!t.closest("#workbenchView")) return;
    }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (!document.querySelector("#workbenchView.active")) return;
    if (document.getElementById("commandPalette")?.classList.contains("hidden") === false) return;
    if (document.getElementById("firstRunTour")?.classList.contains("hidden") === false) return;
    if (document.getElementById("claimDrawer")?.classList.contains("open")) return;

    const rows = Array.from(document.querySelectorAll(".claims-table tbody tr[data-row-claim-id]"));
    if (!rows.length) return;

    let idx = rows.findIndex((r) => r.classList.contains("sheet-nav-focus"));
    if (idx < 0) {
      // If a focused-work-row is set, start there; otherwise start at 0.
      idx = rows.findIndex((r) => r.classList.contains("focused-work-row"));
      if (idx < 0) idx = 0;
    }

    const move = (delta) => {
      const newIdx = Math.max(0, Math.min(rows.length - 1, idx + delta));
      if (newIdx === idx) return;
      rows[idx]?.classList.remove("sheet-nav-focus");
      rows[newIdx].classList.add("sheet-nav-focus");
      rows[newIdx].scrollIntoView({ block: "nearest", behavior: "smooth" });
      idx = newIdx;
    };

    switch (e.key) {
      case "ArrowDown": e.preventDefault(); move(1); break;
      case "ArrowUp":   e.preventDefault(); move(-1); break;
      case "PageDown":  e.preventDefault(); move(10); break;
      case "PageUp":    e.preventDefault(); move(-10); break;
      case "Home":      e.preventDefault(); move(-rows.length); break;
      case "End":       e.preventDefault(); move(rows.length); break;
      case "Enter":
      case " ": {
        // A focused control owns its own Enter/Space. This branch used to fire
        // for a focused Claim ID link too: it preventDefault()ed the link's own
        // click (which carries org + line) and opened rows[idx] - row ZERO when
        // nothing had been arrowed to - with a bare claim id. On TEST, Enter on
        // claim 10 in one practice opened claim 10 in a different practice
        // (2026-09-03). Claim ids repeat across practices; the org is the key.
        const control = t && typeof t.closest === "function" ? t.closest("button, a, [role='button'], summary") : null;
        if (control) return;
        const focusedRow = t && typeof t.closest === "function" ? t.closest("tr[data-row-claim-id]") : null;
        const row = focusedRow || rows[idx];
        const claimId = row?.dataset.rowClaimId;
        if (claimId) {
          e.preventDefault();
          try { openDrawer(claimId, { orgId: row.dataset.rowOrgId || "", serviceId: row.dataset.rowServiceId || "" }); } catch (_) {}
        }
        break;
      }
    }
  });
}

// UI-100 #26: jump-to-claim command palette (⌘K / Ctrl+K).
const CmdPalette = (() => {
  let active = -1;
  let results = [];
  let prevFocus = null;
  function el(id) { return document.getElementById(id); }
  function open() {
    const wrap = el("commandPalette");
    const input = el("cmdPaletteInput");
    if (!wrap || !input) return;
    prevFocus = document.activeElement;
    if (wrap.parentElement !== document.body) document.body.appendChild(wrap);
    wrap.classList.remove("hidden");
    input.value = "";
    active = -1;
    results = [];
    render();
    activateForegroundModal(wrap, {
      initialFocus: input,
      onEscape: close,
      returnFocus: prevFocus
    });
  }
  function close() {
    const wrap = el("commandPalette");
    if (!wrap) return;
    wrap.classList.add("hidden");
    const restoredByModalSession = deactivateForegroundModal(wrap);
    if (!restoredByModalSession && prevFocus && typeof prevFocus.focus === "function") {
      prevFocus.focus({ preventScroll: true });
    }
    prevFocus = null;
  }
  function isOpen() {
    return !el("commandPalette")?.classList.contains("hidden");
  }
  function buildIndex() {
    // Source claims may be large; trim each to display fields only.
    // CROSS-TENANT: this walked `sourceClaims` - the raw, unscoped, merged
    // multi-tenant export - so every scoped role (Client, ClientBiller,
    // OffshoreBiller, StatesideBiller) got other tenants' PATIENT NAME, payer
    // and company name painted into #cmdPaletteResults. There is no role gate
    // anywhere in this lane, and search("") returns idx.slice(0, 20) - so
    // twenty foreign rows appear the instant the palette opens, with no typing.
    //
    // Not exposed on the deployed build today, but only by accident of
    // configuration: tracker-API mode hard-returns claims: [] and the static
    // build ships no data/ directory, so sourceClaims is empty there. Local dev
    // fetches the real multi-tenant export and IS exposed - which means any
    // warehouse-cutover commit that hydrates sourceClaims would ship this green.
    //
    // getClaims() is the merged AND scoped view. The merge matters as much as
    // the filter: canAccessClaim's assignedOnly branch keys off the live owner
    // in the workflowByClaim overlay, which the raw array does not carry. It is
    // memoized, so rebuilding on open is a cache hit. Identical in API mode and
    // for SuperAdmin. workflowImportForCurrentScope already walked the same
    // rows through per-row canAccessClaim - the palette was the lane that missed it.
    const list = typeof getClaims === "function" ? getClaims() : [];
    return list.map((c) => ({
      claimId: c.claimId,
      patient: c.patient || "",
      payer: c.primaryPayer || "",
      client: c.client || "",
      facility: c.facility || "",
      provider: c.renderingProvider || c.bcba || "",
      haystack: [c.claimId, c.patient, c.primaryPayer, c.client, c.facility, c.renderingProvider, c.bcba, c.rbt]
        .filter(Boolean).join(" ").toLowerCase()
    }));
  }
  let idx = null;
  function ensureIndex() { if (!idx) idx = buildIndex(); }
  function search(q) {
    ensureIndex();
    const needle = String(q || "").trim().toLowerCase();
    if (!needle) return idx.slice(0, 20);
    return idx.filter((row) => row.haystack.includes(needle)).slice(0, 30);
  }
  function render() {
    const host = el("cmdPaletteResults");
    if (!host) return;
    if (!results.length) {
      host.innerHTML = `<div class="cmd-palette-empty">${idx && idx.length ? "No matches" : "No claims loaded yet"}</div>`;
      return;
    }
    host.innerHTML = results.map((r, i) => `
      <button class="cmd-palette-row ${i === active ? "is-active" : ""}" type="button" role="option" aria-selected="${i === active}" data-cmd-claim="${escapeHtml(r.claimId)}">
        <span class="cmd-palette-row-id">${escapeHtml(r.claimId)}</span>
        <span class="cmd-palette-row-name">${escapeHtml(r.patient || "—")}</span>
        <span class="cmd-palette-row-meta">${escapeHtml([r.payer, r.client].filter(Boolean).join(" · "))}</span>
      </button>
    `).join("");
    host.querySelectorAll("[data-cmd-claim]").forEach((btn) => {
      btn.addEventListener("click", () => pick(btn.dataset.cmdClaim));
    });
    const activeEl = host.querySelector(".cmd-palette-row.is-active");
    if (activeEl) activeEl.scrollIntoView({ block: "nearest" });
  }
  function pick(claimId) {
    close();
    try { focusWorkClaim(claimId, { open: true }); }
    catch (_) { try { openDrawer(claimId); } catch (_) {} }
  }
  function onInput(e) {
    results = search(e.target.value);
    active = results.length ? 0 : -1;
    render();
  }
  function onKey(e) {
    if (e.key === "Escape") { e.preventDefault(); close(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); if (results.length) { active = (active + 1) % results.length; render(); } return; }
    if (e.key === "ArrowUp")   { e.preventDefault(); if (results.length) { active = (active - 1 + results.length) % results.length; render(); } return; }
    if (e.key === "Enter")     { e.preventDefault(); if (active >= 0 && results[active]) pick(results[active].claimId); return; }
  }
  function bind() {
    el("cmdPaletteInput")?.addEventListener("input", onInput);
    el("cmdPaletteInput")?.addEventListener("keydown", onKey);
    document.querySelectorAll("[data-cmd-close]").forEach((n) => n.addEventListener("click", close));
    // Global hotkey
    if (typeof document !== "undefined") {
      document.addEventListener("keydown", (e) => {
        const mod = e.metaKey || e.ctrlKey;
        if (mod && (e.key === "k" || e.key === "K")) {
          e.preventDefault();
          if (state.view === "queryMap") { document.querySelector("[data-rules-help-search]")?.focus(); return; }
          idx = null; // refresh index on each open in case data changed
          open();
        } else if (e.key === "/" && !isOpen()
                   && !/INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || "")) {
          e.preventDefault();
          idx = null;
          open();
        }
      });
    }
  }
  return { open, close, bind, isOpen };
})();

// UI-100 #39: keep titlebar grid-mode mirror in sync with rowModeSelect.
function updateGridModeMirror() {
  const mode = state.rowMode === "claim" ? "claim" : "service";
  document.querySelectorAll(".grid-mode-mirror-btn[data-grid-mode]").forEach((btn) => {
    btn.setAttribute("aria-pressed", btn.dataset.gridMode === mode ? "true" : "false");
    btn.classList.toggle("is-active", btn.dataset.gridMode === mode);
  });
}

function applyDatePreset(preset, fromId, toId) {
  const fromEl = document.getElementById(fromId);
  const toEl = document.getElementById(toId);
  if (!fromEl || !toEl) return;
  // Date-input defaults are BUSINESS dates — anchor them on the Eastern day (see todayStart).
  const today = todayStart();
  let from = null;
  let to = today;
  if (preset === "mtd") from = new Date(today.getFullYear(), today.getMonth(), 1);
  else if (preset === "ytd") from = new Date(today.getFullYear(), 0, 1);
  else {
    const days = parseInt(preset, 10);
    if (!Number.isFinite(days) || days <= 0) return;
    from = new Date(today);
    from.setDate(from.getDate() - days + 1);
  }
  fromEl.value = localDateString(from);
  toEl.value = localDateString(to);
  fromEl.dispatchEvent(new Event("change", { bubbles: true }));
  toEl.dispatchEvent(new Event("change", { bubbles: true }));
}

function getJargonTitle(term) {
  if (!term) return "";
  const key = String(term).trim();
  return TMS_GLOSSARY[key] || TMS_GLOSSARY[key.toUpperCase()] || TMS_GLOSSARY[key.toLowerCase()] || "";
}

function decorateJargon(root) {
  if (!root) root = document;
  // Decorate any <abbr data-glossary> elements that don't yet have a title.
  root.querySelectorAll("abbr[data-glossary]").forEach((abbr) => {
    if (abbr.title) return;
    const t = getJargonTitle(abbr.dataset.glossary || abbr.textContent);
    if (t) abbr.title = t;
  });
}

function formatDate(value) {
  if (!value) return "None";
  const date = localDateFromDateOnly(value);
  const millis = strictDateTimeMillis(value);
  const parsed = date || (Number.isFinite(millis) ? new Date(millis) : null);
  if (!parsed || Number.isNaN(parsed.getTime())) return "None";
  // Hoisted for the same reason as formatMoneyValue's: the grid formats a
  // date cell per row and the constructor dominated the call.
  const formatter = formatDate.formatter
    || (formatDate.formatter = new Intl.DateTimeFormat("en-US", { month: "numeric", day: "numeric", year: "numeric" }));
  return formatter.format(parsed);
}

function formatWorkflowNoteDate(value) {
  const date = localDateFromDateOnly(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function dateOnlyFilterString(value) {
  if (value == null || value === "") return "";
  const text = String(value).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (!Number.isFinite(parsed.getTime())) return "";
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day
    ? text
    : "";
}

function strictDateTimeMillis(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  const text = String(value).trim();
  const dateOnly = dateOnlyFilterString(text);
  if (dateOnly) return Date.parse(`${dateOnly}T00:00:00.000Z`);
  const isoDatePart = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/.exec(text);
  if (isoDatePart && dateOnlyFilterString(isoDatePart[1])) {
    const hour = Number(isoDatePart[2]);
    const minute = Number(isoDatePart[3]);
    const second = isoDatePart[4] === undefined ? 0 : Number(isoDatePart[4]);
    if (hour <= 23 && minute <= 59 && second <= 59) {
      const parsed = Date.parse(text);
      return Number.isFinite(parsed) ? parsed : null;
    }
  }
  const localDateTime = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(text);
  if (localDateTime && dateOnlyFilterString(localDateTime[1])) {
    const [year, month, day] = localDateTime[1].split("-").map(Number);
    const hour = Number(localDateTime[2]);
    const minute = Number(localDateTime[3]);
    const second = localDateTime[4] === undefined ? 0 : Number(localDateTime[4]);
    const parsed = new Date(year, month - 1, day, hour, minute, second);
    return parsed.getFullYear() === year
      && parsed.getMonth() === month - 1
      && parsed.getDate() === day
      && parsed.getHours() === hour
      && parsed.getMinutes() === minute
      && parsed.getSeconds() === second
      ? parsed.getTime()
      : null;
  }
  const legacyDateOnly = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (legacyDateOnly) {
    const month = Number(legacyDateOnly[1]);
    const day = Number(legacyDateOnly[2]);
    const year = Number(legacyDateOnly[3]);
    const parsed = new Date(year, month - 1, day);
    return parsed.getFullYear() === year
      && parsed.getMonth() === month - 1
      && parsed.getDate() === day
      ? parsed.getTime()
      : null;
  }
  const legacyLocal = /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i.exec(text);
  if (!legacyLocal) return null;
  let hour = Number(legacyLocal[4]);
  const minute = Number(legacyLocal[5]);
  const second = legacyLocal[6] === undefined ? 0 : Number(legacyLocal[6]);
  const meridiem = String(legacyLocal[7] || "").toUpperCase();
  if (meridiem === "PM" && hour < 12) hour += 12;
  if (meridiem === "AM" && hour === 12) hour = 0;
  const month = Number(legacyLocal[1]);
  const day = Number(legacyLocal[2]);
  const year = Number(legacyLocal[3]);
  const parsed = new Date(year, month - 1, day, hour, minute, second);
  return parsed.getFullYear() === year
    && parsed.getMonth() === month - 1
    && parsed.getDate() === day
    && parsed.getHours() === hour
    && parsed.getMinutes() === minute
    && parsed.getSeconds() === second
    ? parsed.getTime()
    : null;
}

function formatDateTime(value) {
  const parsed = strictDateTimeMillis(value);
  const date = Number.isFinite(parsed) ? new Date(parsed) : null;
  if (!date || Number.isNaN(date.getTime())) return String(value || "").trim() ? "None" : "";
  return new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

function endOfWeek(value) {
  const date = localDateFromDateOnly(value);
  if (!date) return "";
  const daysUntilSunday = (7 - date.getDay()) % 7;
  date.setDate(date.getDate() + daysUntilSunday);
  return localDateString(date);
}

function timestamp() {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date());
}

function timestampValue(value) {
  const parsed = strictDateTimeMillis(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function addDays(dateValue, days) {
  const date = localDateFromDateOnly(dateValue);
  if (!date) return "";
  date.setDate(date.getDate() + days);
  return localDateString(date);
}

function localDateFromDateOnly(value) {
  const normalized = dateOnlyFilterString(value);
  if (!normalized) return null;
  const date = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateOnlySortValue(value) {
  const date = localDateFromDateOnly(value);
  return date ? date.getTime() : 0;
}

function localDateString(date = new Date()) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function stripNextAction(note) {
  return note.replace(/\s*Next action:\s*[^.]+\.?\s*$/i, "").trim();
}

function showToast(message) {
  els.toast.textContent = message;
  els.toast.classList.add("show");
  window.clearTimeout(showToast.timeout);
  showToast.timeout = window.setTimeout(() => els.toast.classList.remove("show"), 2400);
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function escapeCss(value) {
  return String(value)
    .replaceAll("\\", "\\\\")
    .replaceAll('"', '\\"')
    .replaceAll("\n", "\\A ")
    .replaceAll("\r", "\\D ")
    .replaceAll("\f", "\\C ");
}

// Team oversight: one personal queue and explicit company-level supervision.
let oversightWorkReadController = null;
// Owns the in-flight My Work tab-count reads so they can be cancelled when
// superseded or when the user leaves the screen.
let oversightTabCountController = null;
// The badge values currently ON SCREEN, with the summary key they belong to.
// A refresh replaces the cached summary with an empty one and fills it read
// by read; without this the strip painted straight from that half-filled
// entry and all five pills vanished for as long as the slowest read took
// (measured at 8000ms on a slow home-summary read). Absence is not zero, and
// it is not "still loading" either - a badge keeps its last known value for
// THIS key until its own replacement arrives.
let oversightTabCountsShown = null;
function oversightState() {
  if (!window.__tmsOversight) window.__tmsOversight = { tab: "overview", status: "idle", items: [], proposals: [], teams: [], request: 0 };
  const value = window.__tmsOversight;
  const session = additionalPortalDataReadScope();
  if (value.session !== session) { oversightWorkReadController?.abort(); Object.assign(value, { session, status: "idle", items: [], proposals: [], teams: [], pagination: null, request: value.request + 1 }); }
  const company = activeClientGroupServerOrgId();
  if (company) value.orgId = company;
  return value;
}

function oversightPolicies(value) {
  return (Array.isArray(value) ? value : []).map((p) => ({ orgId: String(p.orgId || ""), mode: p.mode === "approval" ? "approval" : "independent", reviewerUserId: String(p.reviewerUserId || ""), blockedActions: normalizeUserScopeList(p.blockedActions), reviewActions: normalizeUserScopeList(p.reviewActions) }));
}

function oversightDisposition(orgId, action, currentAction = "") {
  const source = state.authUserScope || currentUser() || {};
  const policy = oversightPolicies(source.followUpPolicies).find((p) => p.orgId === String(orgId));
  const canonical = canonicalFollowUpAction(action) || action || "None";
  const actionMode = currentFollowUpActionMode(canonical);
  if (actionMode === "denied" || policy?.blockedActions.includes(canonical)) return "blocked";
  const current = currentAction && currentAction !== "None" && currentAction !== canonical
    ? oversightDisposition(orgId, currentAction) : "independent";
  if (current === "blocked") return "blocked";
  if (current === "approval" || (policy && (policy.mode === "approval" || policy.reviewActions.includes(canonical)))) return "approval";
  return current === "live_approval" || actionMode === "approval_required" ? "live_approval" : "independent";
}

async function oversightRequest(path, body, method = "POST", options = {}) {
  const readRequest = body === undefined;
  const session = workflowAccessScopedRequestKey({ kind: "team-oversight", base: workflowApiBase() || trackerApiBase(), token: workflowApiToken() || trackerApiToken() });
  const readScope = readRequest ? additionalPortalDataReadScope() : "";
  const base = workflowApiBase() || trackerApiBase();
  const token = workflowApiToken() || trackerApiToken();
  if (!base || !token) throw new Error("Sign in to load your team's work.");
  const response = await fetch(body === undefined ? additionalPortalDataReadUrl(`${base}${path}`) : `${base}${path}`,  { method: body === undefined ? "GET" : method, cache: "no-store", headers: { authorization: `Bearer ${token}`, ...(body === undefined ? {} : { "content-type": "application/json" }) }, ...(options.signal ? { signal: options.signal } : {}), ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  const data = await response.json().catch(() => ({}));
  if (session !== workflowAccessScopedRequestKey({ kind: "team-oversight", base: workflowApiBase() || trackerApiBase(), token: workflowApiToken() || trackerApiToken() }) || (readRequest && readScope !== additionalPortalDataReadScope())) throw new Error("Your access changed. Refresh this page.");
  if (!response.ok) {
    const error = new Error(data.message || data.error?.message || "This change could not be completed. Refresh and try again.");
    error.code = typeof data.error === "string" ? data.error : data.error?.code;
    throw error;
  }
  if (!readRequest && /^\/api\/workflow\/follow-up-proposals(?:\/decision)?$/.test(path)) {
    invalidateTrackerApiFacetsAfterWorkflowWrite();
    invalidatePortalReadsAfterWorkflowWrite({ preserveMyWork: true });
  }
  return data;
}

function oversightPerson(key) {
  const person = governanceUserRows().find((u) => String(u.userId || u.username || u.email).toLowerCase() === String(key).toLowerCase());
  return person?.name || key || "Not assigned";
}

function oversightCompany(key, labels = clientActionOrgLabels()) { return clientActionCompanyName(labels.get(String(key)), key) || "Company name unavailable"; }
function workClaimLinkHtml(row = {}, label = "") {
  if (!row.claimId || !row.orgId) return escapeHtml(label || "No linked claim");
  return `<button type="button" class="ghost-button" data-work-open data-claim="${escapeHtml(row.claimId)}" data-org="${escapeHtml(row.orgId)}" data-service="${escapeHtml(row.serviceId || "")}" aria-label="Open claim ${escapeHtml(row.claimId)}">${escapeHtml(label || `Claim ${row.claimId}`)}</button>`;
}

function bindWorkClaimLinks(host) {
  if (!host) return;
  host.querySelectorAll("[data-work-open]").forEach((button) => {
    // BIND ONCE PER BUTTON. My Work's approval tab now binds one root through
    // two paths - the work results binder and the approval queue binder, which
    // draws inside it - and a second listener on the same element opened the
    // claim drawer twice for one click. Fresh markup has no flag, so a repaint
    // still binds normally.
    if (button.dataset.workOpenBound === "1") return;
    button.dataset.workOpenBound = "1";
    button.addEventListener("click", () => openDrawer(button.dataset.claim, { orgId: button.dataset.org, serviceId: button.dataset.service }));
  });
}

function oversightTime(value) { if (!value) return ""; const date = new Date(value); return Number.isFinite(date.getTime()) ? date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : value; }
function oversightId() { return crypto.randomUUID(); }

// The outstanding-count pill on a My Work tab. A count of zero and a count we
// do not have both render nothing: no loud zeros, and "not loaded yet" must
// never look like "none" (ENGINEERING_TRAPS #10 - absence is not zero).
// The number is decorative; the visually-hidden text is what a screen reader
// reads, so the badge is never colour- or glyph-only.
function oversightTabCountHtml(count) {
  const total = count && Number.isFinite(Number(count.total)) ? Math.max(0, Math.floor(Number(count.total))) : null;
  if (total === null || total < 1) return "";
  const shown = String(total) + (count.partial ? "+" : "");
  // One pill, but the spoken text names the STATES behind it. "3 sent back,
  // 7 waiting for review" is the difference between a biller knowing three
  // items are theirs to fix and reading a bare number.
  const spoken = (count.partial ? "at least " : "") + (count.spoken || (String(total) + " outstanding"));
  return '<span class="oversight-tab-count" aria-hidden="true">' + escapeHtml(shown) + '</span><span class="oversight-tab-count-text visually-hidden">, ' + escapeHtml(spoken) + '</span>';
}

function oversightTabs(entries, selected, attr) {
  return '<div class="oversight-tabs" role="tablist">' + entries.map((entry) => '<button type="button" role="tab" aria-selected="' + (selected === entry[0]) + '" ' + attr + '="' + entry[0] + '">' + escapeHtml(entry[1]) + oversightTabCountHtml(entry[2]) + '</button>').join("") + '</div>';
}

// Outstanding, per tab, defined as whatever that tab itself treats as still
// open. Every number below comes from the SAME scoped read the tab uses, so a
// badge can never count a row the signed-in user could not open:
//   Assigned to me / Sent by me - the server's whole-queue counts for the
//     principal (open + in-progress), which is exactly the row set the tab
//     shows while "Show finished tasks" is off.
//   My requests      - BOTH states that still need someone: sent back (mine to
//                      fix) and waiting for review. Approved and withdrawn are
//                      finished and never counted. Owner call 2026-09-17:
//                      sent-back work must be unmistakable to an offshore
//                      biller, and pending-only gave someone with three
//                      sent-back requests and nothing pending NO badge at all,
//                      while the tab defaults to "All decisions" and sorts
//                      sent-back first.
//   Needs my approval- claims awaiting my decision. The tab pages and renders
//                      CLAIMS (groupBy=claim), so the badge counts claims too;
//                      a line count there disagreed with every number on the
//                      screen the supervisor then opened.
//   Internal review  - service lines in my company scope whose action is
//                      "Internal Review Required" (an org-scoped queue, not a
//                      per-user one - the same rows the tab lists).
// Overview is a landing page and Claim assignments is a board whose scope is
// chosen inside the tab, so neither carries a count.
function oversightWorkTabCounts(model = oversightState()) {
  const counts = {};
  const entry = model.homeSummary && model.homeSummary.key === JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""]) ? model.homeSummary : null;
  if (!entry) return counts;
  const whole = (value) => { const numeric = Number(value); return Number.isFinite(numeric) ? Math.max(0, Math.floor(numeric)) : null; };
  const openTasks = (source) => source && source.counts ? whole(source.counts.open || 0) + whole(source.counts["in-progress"] || 0) : null;
  const unit = (value, one, many) => String(value) + " " + (value === 1 ? one : many);
  const set = (tab, total, partial, spoken) => { if (Number.isFinite(total)) counts[tab] = { total, partial: partial === true, spoken: spoken || "" }; };
  set("tasks", openTasks(entry.tasks), entry.tasks && entry.tasks.truncated === true);
  set("sent", openTasks(entry.sentTasks), entry.sentTasks && entry.sentTasks.truncated === true);
  // counts.rejected and counts.pending come from the SERVER, deliberately not
  // recomputed from the rows in hand: the supervisor lane narrows rejected to
  // non-superseded sent-backs (a resubmit on the same org + service line stops
  // counting), and reading the server field makes this badge follow that for
  // free instead of disagreeing with it.
  // SENT BACK, ACROSS ALL THREE LANES. My requests lists follow-up requests,
  // the Team-approvals rework queue and bounced-back whole claims together, so
  // entry.proposals.counts.rejected - which knows only the first - would print a
  // smaller number than the list underneath it. unifiedReturnedToMe is that
  // list's own total, and it is null until every lane has answered; a null must
  // NOT fall back to the proposals-only figure, because that is precisely the
  // quiet undercount. It is left unset instead, and oversightTabCountResolved
  // keeps the badge on its last known value until the real number arrives.
  // `pending` stays on the proposals read because only that lane has work
  // waiting for review; the other two contribute sent-back work only.
  const returnedToMe = unifiedReturnedToMe(model);
  if (entry.proposals && entry.proposals.counts && returnedToMe !== null) {
    const sentBack = returnedToMe, waiting = whole(entry.proposals.counts.pending || 0);
    const said = [sentBack ? sentBack + " sent back" : "", waiting ? waiting + " waiting for review" : ""].filter(Boolean).join(", ");
    set("requests", sentBack + waiting, entry.proposals.truncated === true, said);
  }
  // NEEDS MY APPROVAL, ACROSS BOTH ROUTES, IN SERVICE LINES.
  //
  // This badge used to count CLAIMS from the supervisor-request read alone, on
  // the ground that the tab paged and rendered claims. Both halves of that
  // stopped being true on 2026-09-18: the tab now shows ONE list fed by two
  // routes, and the number printed above that list is SERVICE LINES, because
  // lines is the unit both routes already report. A badge in a unit that
  // appears nowhere on the screen it opens is the same disagreement, one step
  // removed.
  //
  // unifiedApprovalsBadgeCount is that list's own total, and it is null until
  // EVERY route has answered. A null must not fall back to the request-only
  // figure - that is precisely the quiet undercount - so the badge is left
  // unset and oversightTabCountResolved holds its last known value.
  const approvals = unifiedApprovalsBadgeCount(model);
  if (approvals !== null) {
    set("approvals", approvals, entry.reviewProposals && entry.reviewProposals.truncated === true,
      unit(approvals, "line", "lines") + " awaiting your approval");
  }
  set("internalReview", Number.isFinite(entry.internalReview) ? entry.internalReview : null, false);
  return counts;
}

// Which summary field answers which tab. A badge may only change once ITS OWN
// read has come back - present as an own property, even when the value is null
// (refused or unreadable, which correctly REMOVES the badge rather than
// freezing a stale one). Everything else keeps what it is showing.
const OVERSIGHT_TAB_COUNT_FIELD = { tasks: "tasks", sent: "sentTasks", requests: "proposals", approvals: "reviewProposals", internalReview: "internalReview" };
// The unified Sent-back total for the badge, or null.
//
// Asked of billerReturnedCount - the SAME reader the My requests list uses -
// rather than of model.counts.returnedToMe, which only exists once
// refreshMyWorkCounts has run, and that has a single caller (a bulk decision).
// Reading the field would have left this badge blank in ordinary use. Going to
// the reader also means the badge cannot drift from the list it sits above.
//
// null whenever any lane is still loading or failed - never a partial sum -
// and a DENIED lane counts as answered, so a viewer who simply has no access to
// the Team-approvals queue still gets an exact total. A number and only a
// number: Number(null) is 0 and would be a silent zero badge.
function unifiedReturnedToMe(model = oversightState()) {
  const value = typeof billerReturnedCount === "function" ? billerReturnedCount(model) : null;
  return typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : null;
}

// The unified Needs-my-approval total for the badge, or null.
//
// Taken from the HOME SUMMARY on the supervisor-request side (the whole-queue
// line count, not the page in hand) and from the service-line queue read on
// the other, so the badge can resolve on Overview without the supervisor
// having opened the tab. A DENIED service-line queue counts as answered and
// contributes nothing - a reviewer with no access to that route still gets an
// exact total. A number and only a number: Number(null) is 0 and would be a
// silent zero badge.
function unifiedApprovalsBadgeCount(model = oversightState()) {
  const entry = model.homeSummary && model.homeSummary.key === JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""]) ? model.homeSummary : null;
  const lines = entry && entry.reviewProposals ? Number(entry.reviewProposals.count) : NaN;
  if (!Number.isFinite(lines)) return null;
  // The SAME reader the list uses, so the badge and the list it opens cannot
  // drift - including through a re-read, where both hold rather than blank.
  const sources = unifiedApprovalSources(model);
  if (!sources.team) return null;
  const team = sources.teamBody ? Number(sources.teamBody.total || 0) : 0;
  if (!Number.isFinite(team)) return null;
  return Math.max(0, Math.floor(lines)) + Math.max(0, Math.floor(team));
}

function oversightTabCountResolved(model = oversightState()) {
  const resolved = {};
  const entry = model.homeSummary && model.homeSummary.key === JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""]) ? model.homeSummary : null;
  if (!entry) return resolved;
  for (const tab of Object.keys(OVERSIGHT_TAB_COUNT_FIELD)) {
    if (Object.prototype.hasOwnProperty.call(entry, OVERSIGHT_TAB_COUNT_FIELD[tab])) resolved[tab] = true;
  }
  // My requests needs THREE lanes, not just the proposals read. Until the
  // cross-lane total exists this badge has no new value to show - which is not
  // the same as having none - so it is left unresolved and holds what it was
  // showing. On a cold start that means no badge until the lanes answer, which
  // is the honest state: absence is not zero, and it is not an undercount.
  if (resolved.requests && unifiedReturnedToMe(model) === null) delete resolved.requests;
  // Needs my approval needs BOTH routes for the same reason. Until the
  // cross-route total exists this badge has no new value to show - which is not
  // the same as having none - so it holds what it was showing rather than
  // printing a figure from one route. A company switch is a different cache key
  // and drops every remembered number on the spot, which is the one case where
  // blanking is correct.
  if (resolved.approvals && unifiedApprovalsBadgeCount(model) === null) delete resolved.approvals;
  return resolved;
}

// What the strip should DISPLAY right now, and the one place the carry-over
// lives, so the full re-render path and the in-place paint can never disagree.
//
// The key is the SAME identity the reads are already fenced on - it carries the
// tokens, the active company and the access snapshot - so a company, role or
// user switch is a different key and drops every remembered number on the spot.
// That is the one case where blanking is correct: another tenant's or another
// person's figure must not be on screen even for a single frame.
function oversightTabCountsForDisplay(model = oversightState()) {
  const key = JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""]);
  if (!oversightTabCountsShown || oversightTabCountsShown.key !== key) oversightTabCountsShown = { key, counts: {} };
  const fresh = oversightWorkTabCounts(model);
  const resolved = oversightTabCountResolved(model);
  const shown = oversightTabCountsShown.counts;
  for (const tab of Object.keys(OVERSIGHT_TAB_COUNT_FIELD)) if (resolved[tab]) shown[tab] = fresh[tab] || null;
  return shown;
}

function oversightWorkTabsWithCounts(model = oversightState()) {
  const counts = oversightTabCountsForDisplay(model);
  return oversightWorkTabs().map((tab) => [tab[0], tab[1], counts[tab[0]] || null]);
}

// The counts ride the home-summary cache the Overview cards already use, so a
// tab strip painted from any tab reads one source and issues no second set of
// reads. Mirrors renderOversightWork's own role gate - a client role never
// reaches My Work and never asks for these counts.
function ensureOversightTabCounts() {
  if (isExternalClientRole(state.role) || !hasPermission(PERMISSIONS.VIEW_ASSIGNMENTS)) return;
  const model = oversightState();
  const key = JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""]);
  if (!model.homeSummary || model.homeSummary.key !== key) loadBillerHomeSummary(key);
}

// ONE place that says "the My Work counts are no longer true": it drops the
// cached summary and cancels any count read still in flight for it. Every
// mutation that can change one of these five queues calls it - task
// transition and creation, single and BULK approve/send-back, withdraw,
// single and bulk request submission - and so does Refresh. Kept deliberately
// small and separate so the supervisor-reject lane can wire its
// refreshMyWorkCounts() to it at merge without either side restructuring.
function invalidateMyWorkCounts(model = oversightState()) {
  abortOversightTabCountReads();
  model.homeSummary = null;
  // Refresh straight away when the user is looking at My Work. Not every
  // caller re-renders - "Send task" closes its dialog and returns - and a
  // badge that waits for the next paint to stop being wrong is a badge that
  // lies. Callers that DO re-render cost nothing extra: the reload is keyed,
  // and loadBillerHomeSummary claims the key synchronously.
  // The sidebar badge is on EVERY screen, so a write made from All Data (a
  // follow-up request, a bulk submit) refreshes it too - one shared read,
  // debounced. Stamped BEFORE the reload below, so that reload already
  // counts as fresh and the badge does not read the same queues twice.
  if (typeof noteMyWorkAttentionChanged === "function") noteMyWorkAttentionChanged();
  if (state.view === "assignments") ensureOversightTabCounts();
}

// Cancels the count reads for a summary that is being replaced or dropped.
// (Until 2026-09-23 this also ran on leaving My Work; those reads now feed
// the sidebar badge on every screen, so leaving no longer cancels them. A
// read cancelled HERE is superseded, not failed: it never settles a null.)
function abortOversightTabCountReads() {
  oversightTabCountController?.abort();
  oversightTabCountController = null;
}

// Counts arrive after the strip is on screen. Patch the pills in place rather
// than re-rendering: renderOversightWork sets status idle and would re-fire the
// open tab's list read on every count that lands.
function paintOversightTabCounts() {
  // The sidebar badge is the sum of these same numbers, so it is repainted
  // from every place that repaints them - including off My Work.
  if (typeof updateMyWorkNavBadge === "function") updateMyWorkNavBadge();
  if (state.view !== "assignments") return;
  const counts = oversightTabCountsForDisplay();
  document.querySelectorAll("[data-work-tab]").forEach((button) => {
    const html = oversightTabCountHtml(counts[button.dataset.workTab]);
    const pills = button.querySelectorAll(".oversight-tab-count, .oversight-tab-count-text");
    // Swap a badge ONCE, and only when it really changes. Removing and
    // re-inserting an identical pill on every read that lands is DOM churn a
    // screen reader and a MutationObserver both see.
    if (Array.from(pills).map((node) => node.outerHTML).join("") === html) return;
    pills.forEach((node) => node.remove());
    if (html) button.insertAdjacentHTML("beforeend", html);
  });
}

// ============================================================================
// MY WORK SIDEBAR BADGE (owner request, Nathan 2026-09-23).
// ============================================================================
// "Can My Work load in the background so that when something needs my
// attention there's a little red count there, and when I click My Work it
// opens right away? ... For all the offshore ones it should work like this
// too." The My Work sidebar item carries the same red pill as Authorizations
// and Client Action Pending, for the three internal roles that have My Work:
// SuperAdmin, StatesideBiller and OffshoreBiller. Client and RedactedClient
// have no My Work, so they get no badge and no reads.
//
// THE COUNT, DEFINED ONCE (myWorkAttentionCount) - items waiting on THIS
// person, each taken from the number My Work itself prints:
//
//   Needs my approval  approvers only (the tab exists for them): service lines
//                      awaiting my decision across both approval routes - the
//                      number on the "Needs my approval" tab.
//   Assigned to me     my tasks that are open or in progress - the number on
//                      the "Assigned to me" tab.
//   Sent back to me    my follow-up requests sent back + my Team-approval lines
//                      sent back + my whole claims bounced back - the number on
//                      the Overview "Sent back to me" card. The whole-claim part
//                      is ONE count read (GET /api/workflow/sent-back-claims/
//                      count, the Claim assignments list's own read narrowed to
//                      a number), so neither the badge nor the card waits for
//                      that board, and neither carries a "+" (owner, 2026-09-23:
//                      exact numbers). Only a session with no tracker API to ask
//                      still falls back to the board rows or a "+" floor.
//
//   total = Needs my approval + Assigned to me + Sent back to me
//
// Overdue and due today are tasks ALREADY inside Assigned to me: the tooltip
// names them in brackets and the total never adds them twice, so the badge is
// always the plain sum of numbers the user can see in My Work. Not counted,
// because they wait on someone else or are not personal: Waiting for review,
// Sent by me, Internal review required (a company queue) and Claim assignments
// (a board with no count by design). My requests has no count of unanswered
// replies to add.
//
// UNKNOWN IS NOT ZERO (ENGINEERING_TRAPS #10). A part whose read FAILED makes
// the whole badge unknown and it shows no number. A part still loading holds
// the last number shown for this same person and company (stale-while-
// revalidate); a company, role or session switch is a different key and drops
// it on the spot. Zero hides the badge.
//
// ONE SET OF READS. Every part is the scoped read its tab already uses, landing
// in the cache that tab renders from (the home summary, its count reads and the
// approval-queue slices). The background load IS the prefetch: opening My Work
// paints from it at once and then revalidates quietly. Nothing is asked for
// before the first All Data rows paint, a refresh is one shared read that never
// rejects (the notification-count pattern), polling runs every 60 s while the
// tab is visible, and a data age floor of 30 s stops alt-tabbing or a burst of
// writes from re-asking.
const MY_WORK_ATTENTION_ROLES = Object.freeze(["SuperAdmin", "StatesideBiller", "OffshoreBiller"]);
const MY_WORK_ATTENTION_POLL_MS = 60000;
const MY_WORK_ATTENTION_MIN_GAP_MS = 30000;
const MY_WORK_ATTENTION_CHANGE_DEBOUNCE_MS = 300;
const myWorkAttentionState = { inFlight: null, forced: null, timer: null, changeTimer: null, shown: null, generation: 0, changedAtMs: 0, internalReviewReads: new WeakSet() };

function myWorkAttentionEligible() {
  if (typeof state === "undefined" || !state) return false;
  if (isExternalClientRole(state.role) || !MY_WORK_ATTENTION_ROLES.includes(canonicalRole(state.role))) return false;
  if (!hasPermission(PERMISSIONS.VIEW_ASSIGNMENTS)) return false;
  return typeof viewIsAvailable !== "function" || viewIsAvailable("assignments");
}

// The same identity the My Work tab counts are fenced on: the access-scoped
// session plus the company the tabs are showing (the header company when one is
// chosen, which oversightState() copies in).
function myWorkAttentionKey(model = oversightState()) {
  return JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""]);
}

// { status: "ready" | "pending" | "unknown" | "none", key, total, partial, parts }
function myWorkAttentionCount(model = oversightState()) {
  const key = myWorkAttentionKey(model);
  if (!myWorkAttentionEligible()) return { status: "none", key };
  const entry = model.homeSummary && model.homeSummary.key === key ? model.homeSummary : null;
  if (!entry || entry.status !== "ready") return { status: "pending", key };
  if (!entry.tasks || !entry.tasks.counts || !entry.proposals || !entry.proposals.counts) return { status: "unknown", key };
  const fresh = oversightWorkTabCounts(model);
  // Assigned to me: the tab's own number (open + in progress, whole queue).
  const tasks = fresh.tasks ? fresh.tasks.total : 0;
  const tasksPartial = Boolean(fresh.tasks && fresh.tasks.partial);
  // Needs my approval: the tab's own number, which exists only once BOTH
  // approval routes have answered. A route that failed is unknown, not zero.
  const approver = oversightWorkTabs().some((tab) => tab[0] === "approvals");
  let approvals = null, approvalsPartial = false;
  if (approver) {
    if (!Object.prototype.hasOwnProperty.call(entry, "reviewProposals")) return { status: "pending", key };
    const slice = typeof unifiedApprovalSlice === "function" ? unifiedApprovalSlice() : null;
    if (entry.reviewProposals === null || (slice && slice.status === "error")) return { status: "unknown", key };
    if (!fresh.approvals) return { status: "pending", key };
    approvals = fresh.approvals.total;
    approvalsPartial = fresh.approvals.partial === true;
  }
  // Sent back to me: the Overview card's lanes, read the way the card reads them.
  const sources = billerReturnedSources(model);
  if (sources.teamLinesFailed) return { status: "unknown", key };
  // Only a lane this session can actually ask for is worth waiting on; one it
  // cannot read is a floor, exactly as the Overview card treats it.
  const teamReadable = hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP) && typeof workflowApiBase === "function" && Boolean(workflowApiBase());
  if (!sources.teamLines && teamReadable) return { status: "pending", key };
  const summary = billerReturnedSummaryCounts(model);
  if (!summary) return { status: "unknown", key };
  // Whole claims bounced back: the exact count read wins. A count that FAILED is
  // unknown, and so is the badge - never the board's page of rows standing in
  // for it. Refused (denied) is an answer: that list is not this person's, so it
  // carries nothing. Only with no count lane at all does the old board-or-floor
  // rule apply.
  const exact = sentBackClaimCountAnswer(model);
  let bounced = 0, bouncedFloor = false;
  if (exact.status === "ok") bounced = exact.count;
  else if (exact.status === "denied") bounced = 0;
  else if (exact.status === "error") return { status: "unknown", key };
  else if (exact.status === "pending") return { status: "pending", key };
  else if (sources.claims) bounced = billerBouncedClaimRows(model).length;
  else bouncedFloor = true;
  const sentBack = summary.rejected
    + (sources.teamLines ? billerTeamReturnedRows(model).length : 0)
    + bounced;
  const sentBackFloor = !sources.teamLines || bouncedFloor;
  // Overdue / due today: the same rows and the same rule as the Overview cards.
  const today = todayDateString();
  const taskRows = (Array.isArray(entry.tasks.items) ? entry.tasks.items : []).filter((row) => !["completed", "canceled"].includes(row.status));
  const taskTotal = Number(entry.tasks.page?.totalRows ?? (Number(entry.tasks.counts?.open || 0) + Number(entry.tasks.counts?.["in-progress"] || 0))) || 0;
  const overdue = taskRows.filter((row) => row.dueDate && row.dueDate < today).length;
  const dueToday = taskRows.filter((row) => row.dueDate === today).length;
  return {
    status: "ready", key,
    total: (approvals || 0) + tasks + sentBack,
    partial: tasksPartial || approvalsPartial || sentBackFloor,
    parts: { approvals, approvalsPartial, tasks, tasksPartial, sentBack, sentBackFloor, overdue, dueToday, deadlinesPartial: taskTotal > taskRows.length }
  };
}

// "41 approvals · 5 assigned tasks (3 overdue, 1 due today) · 2 sent back".
// A part that is zero is left unsaid; a floor says "+".
function myWorkAttentionTooltip(count) {
  const parts = count && count.parts;
  if (!parts) return "";
  const figure = (value, floor) => String(value) + (floor ? "+" : "");
  const noun = (value, one, many) => (value === 1 ? one : many);
  const said = [];
  if (parts.approvals) said.push(figure(parts.approvals, parts.approvalsPartial) + " " + noun(parts.approvals, "approval", "approvals"));
  if (parts.tasks) {
    const due = [parts.overdue ? figure(parts.overdue, parts.deadlinesPartial) + " overdue" : "", parts.dueToday ? figure(parts.dueToday, parts.deadlinesPartial) + " due today" : ""].filter(Boolean).join(", ");
    said.push(figure(parts.tasks, parts.tasksPartial) + " " + noun(parts.tasks, "assigned task", "assigned tasks") + (due ? " (" + due + ")" : ""));
  }
  if (parts.sentBack) said.push(figure(parts.sentBack, parts.sentBackFloor) + " sent back");
  if (parts.sentBackFloor) said.push("whole claims sent back are checked in Claim assignments");
  return said.join(" · ");
}

// What the badge SHOWS: the last ready count for this key, held while a part
// re-reads, dropped when a part fails or the key changes.
function myWorkAttentionForDisplay(model = oversightState()) {
  const count = myWorkAttentionCount(model);
  if (!myWorkAttentionState.shown || myWorkAttentionState.shown.key !== count.key) myWorkAttentionState.shown = { key: count.key, count: null };
  if (count.status === "ready") myWorkAttentionState.shown.count = count;
  else if (count.status !== "pending") myWorkAttentionState.shown.count = null;
  return myWorkAttentionState.shown.count;
}

function updateMyWorkNavBadge() {
  const badge = typeof document !== "undefined" ? document.getElementById("myWorkNavBadge") : null;
  if (!badge) return;
  const navItem = typeof badge.closest === "function" ? badge.closest(".nav-item") : null;
  const eligible = myWorkAttentionEligible();
  const shown = eligible ? myWorkAttentionForDisplay() : null;
  const total = shown ? shown.total : 0;
  const text = total > 99 ? "99+" : String(total) + (shown && shown.partial ? "+" : "");
  const tip = shown && total > 0 ? myWorkAttentionTooltip(shown) : "";
  if (badge.textContent !== text) badge.textContent = text;
  badge.hidden = !(shown && total > 0);
  badge.setAttribute("aria-hidden", "true");
  if (tip) badge.title = tip; else badge.removeAttribute("title");
  if (!navItem) return;
  if (!eligible) navItem.removeAttribute("aria-label");
  else navItem.setAttribute("aria-label", tip ? "My Work, " + text + " waiting on you: " + tip : "My Work");
}

function myWorkAttentionApiReady() {
  const base = (typeof workflowApiBase === "function" && workflowApiBase()) || (typeof trackerApiBase === "function" && trackerApiBase());
  const token = (typeof workflowApiToken === "function" && workflowApiToken()) || (typeof trackerApiToken === "function" && trackerApiToken());
  return Boolean(base && token && typeof fetch === "function");
}

// A list the user is LOOKING AT is not re-read underneath them by a poll (a
// decision there re-reads it anyway). It is still ASKED FOR when nothing holds
// it - a decision empties both slices, and a lane nobody re-asks for would
// leave the badge holding its old number for good.
function myWorkAttentionListOnScreen(model) {
  return state.view === "reviews" || (state.view === "assignments" && ["requests", "approvals"].includes(model.tab));
}

// ---- Whole claims sent back to me: one count read ----------------------------
// Keyed like every other My Work read (access-scoped session + the company the
// tabs show) plus the lane the Claim assignments list reads on this page, so the
// count is taken from the same source as the list it summarises.
let sentBackClaimCountState = { key: "", status: "idle", count: null, held: null };

function sentBackClaimCountKey(model = oversightState()) {
  const lane = typeof window !== "undefined" && window.TMS_PORTAL_PREPARED_READS === true ? "prepared" : "tracker";
  return JSON.stringify([additionalPortalDataReadScope(), model.orgId || "", lane]);
}

// { status: "ok" | "denied" | "error" | "pending" | "none", count }. A re-read
// in flight answers with the number it is re-checking (stale-while-revalidate),
// so neither the badge nor the card flashes while it runs.
function sentBackClaimCountAnswer(model = oversightState()) {
  const base = typeof trackerApiBase === "function" ? trackerApiBase() : "";
  const slice = sentBackClaimCountState;
  if (slice.key !== sentBackClaimCountKey(model)) return { status: base ? "pending" : "none", count: null };
  if (slice.status === "ok") return { status: "ok", count: slice.count };
  if (slice.status === "pending" && Number.isFinite(slice.held)) return { status: "ok", count: slice.held };
  if (["denied", "error", "pending"].includes(slice.status)) return { status: slice.status, count: null };
  return { status: base ? "pending" : "none", count: null };
}

// One GET, shared: a read already on the wire for this key is never repeated.
// reread=false is a cached no-op once answered; reread=true asks again.
function requestSentBackClaimCount(model = oversightState(), reread = false) {
  const base = typeof trackerApiBase === "function" ? trackerApiBase() : "";
  const token = typeof trackerApiToken === "function" ? trackerApiToken() : "";
  if (!base || !token || typeof fetch !== "function") return sentBackClaimCountState;
  const key = sentBackClaimCountKey(model);
  const slice = sentBackClaimCountState;
  if (slice.key === key && slice.status === "pending") return slice;
  if (slice.key === key && ["ok", "denied", "error"].includes(slice.status) && !reread) return slice;
  const held = slice.key === key && slice.status === "ok" ? slice.count : null;
  const pending = { key, status: "pending", count: null, held };
  sentBackClaimCountState = pending;
  const params = new URLSearchParams();
  if (model.orgId) params.set("orgId", String(model.orgId));
  if (JSON.parse(key)[2] === "prepared") params.set("lane", "prepared");
  const query = params.toString() ? "?" + params.toString() : "";
  const previous = sentBackClaimCountAnswer(model);
  const settle = (next) => {
    if (sentBackClaimCountState !== pending || sentBackClaimCountKey(oversightState()) !== key) return;
    sentBackClaimCountState = { key, held: null, count: null, ...next };
    updateMyWorkNavBadge();
    // The Overview card reads this number too. Rebuild it only when the number
    // it shows actually moved.
    const now = sentBackClaimCountAnswer(oversightState());
    if (state.view === "assignments" && oversightState().tab === "overview" && (now.status !== previous.status || now.count !== previous.count)) renderAssignments();
  };
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timeout = setTimeout(() => controller?.abort(), 12000);
  fetch(additionalPortalDataReadUrl(base + "/api/workflow/sent-back-claims/count" + query), {
    method: "GET", cache: "no-store", headers: { authorization: "Bearer " + token }, ...(controller ? { signal: controller.signal } : {})
  }).then(async (response) => {
    const body = await response.json().catch(() => ({}));
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    const count = Number(body && body.count);
    if (response.ok && Number.isFinite(count) && count >= 0) settle({ status: "ok", count: Math.floor(count) });
    else settle({ status: response.status === 403 && body && body.error === "forbidden" ? "denied" : "error" });
  }).catch(() => settle({ status: "error" })).finally(() => clearTimeout(timeout));
  return sentBackClaimCountState;
}

// The approval-queue slices behind "Needs my approval" (approvers) and the
// Team-approval half of "Sent back to me". reread=false only makes sure each
// has been asked for (a cached no-op); reread=true asks again, sharing a read
// that is already on the wire rather than racing it, and never while that list
// is on screen.
function myWorkAttentionQueues(model, reread) {
  // Not a list anyone is looking at, so a refresh always re-asks it.
  requestSentBackClaimCount(model, reread);
  if (typeof requestApprovalQueue !== "function" || typeof workflowApiBase !== "function" || !workflowApiBase()) return;
  const onScreen = myWorkAttentionListOnScreen(model);
  const modes = [];
  if (hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) modes.push("rework");
  if (oversightWorkTabs().some((tab) => tab[0] === "approvals")) modes.push("pending");
  for (const mode of modes) {
    const slice = approvalQueueState[mode];
    const onTheWire = slice && slice.status === "pending" && slice.key === approvalQueueRequestKey(mode);
    if (reread && !onTheWire && !onScreen) approvalQueueState[mode] = { key: "", status: "idle", body: null, error: "" };
    requestApprovalQueue(mode);
  }
}

// Older than the floor, or older than the last change this session noted.
function myWorkAttentionStale(entry) {
  if (!entry || entry.status !== "ready") return false;
  const loadedAtMs = Number(entry.loadedAtMs) || 0;
  return loadedAtMs < myWorkAttentionState.changedAtMs || Date.now() - loadedAtMs >= MY_WORK_ATTENTION_MIN_GAP_MS;
}

// One refresh. Loads the summary when there is none for this key, re-reads it
// IN PLACE when it is older than the floor or older than the last change this
// session noted, and leaves a load that is already in flight alone - so a
// write that has ALREADY reloaded My Work (invalidateMyWorkCounts) costs no
// second read, and a change nothing reloaded yet costs exactly one.
async function myWorkAttentionRead() {
  const model = oversightState();
  const key = myWorkAttentionKey(model);
  const entry = model.homeSummary && model.homeSummary.key === key ? model.homeSummary : null;
  const stale = myWorkAttentionStale(entry);
  myWorkAttentionQueues(model, stale);
  const background = state.view !== "assignments";
  if (!entry) await loadBillerHomeSummary(key, { background });
  else if (stale) await loadBillerHomeSummary(key, { quiet: true, background: true });
  return true;
}

// THE SHARED REFRESH. Every trigger - render, poll, visibility, a write, a
// notification - calls this without awaiting it, so the handle NEVER rejects.
// A forced refresh that finds one already on the wire waits for it and then
// runs once more; any number of forced callers in that window share that one.
function refreshMyWorkAttention({ force = false } = {}) {
  const own = myWorkAttentionState;
  if (!myWorkAttentionEligible() || !myWorkAttentionApiReady()) { updateMyWorkNavBadge(); return Promise.resolve(false); }
  if (own.inFlight) {
    if (!force) return own.inFlight;
    if (!own.forced) own.forced = own.inFlight.then(() => { own.forced = null; return refreshMyWorkAttention({ force: true }); });
    return own.forced;
  }
  const generation = own.generation;
  const run = myWorkAttentionRead()
    .catch((error) => { console.warn("[my-work-badge] refresh failed", error && error.message ? error.message : error); return false; })
    .then((result) => {
      if (own.inFlight === run) own.inFlight = null;
      if (generation === own.generation) { try { updateMyWorkNavBadge(); } catch (_) {} }
      return result;
    });
  own.inFlight = run;
  return run;
}

// Something this session did or heard about may have changed what is waiting.
// Stamped NOW, so anything loaded before this moment counts as stale, and
// anything a caller reloads after it (invalidateMyWorkCounts calls this BEFORE
// it reloads) does not have to be read twice.
function noteMyWorkAttentionChanged() {
  const own = myWorkAttentionState;
  if (!myWorkAttentionEligible()) return;
  own.changedAtMs = Date.now();
  if (own.changeTimer || typeof setTimeout !== "function") return;
  own.changeTimer = setTimeout(() => { own.changeTimer = null; refreshMyWorkAttention({ force: true }); }, MY_WORK_ATTENTION_CHANGE_DEBOUNCE_MS);
}

// Sign-in, sign-out and a header company switch. The read already on the wire
// is fenced by its own key; this only stops later callers from sharing it.
function resetMyWorkAttention() {
  const own = myWorkAttentionState;
  own.generation += 1;
  own.inFlight = null;
  own.forced = null;
  own.shown = null;
  if (own.changeTimer) { clearTimeout(own.changeTimer); own.changeTimer = null; }
  updateMyWorkNavBadge();
}

function installMyWorkAttentionPolling() {
  const own = myWorkAttentionState;
  if (own.timer || typeof window === "undefined" || typeof document === "undefined") return;
  own.timer = window.setInterval(() => {
    if (!document.hidden && myWorkAttentionEligible()) refreshMyWorkAttention();
  }, MY_WORK_ATTENTION_POLL_MS);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && myWorkAttentionEligible()) refreshMyWorkAttention();
  });
}

// Called on every render (applyPermissionChrome). Paints from cache; asks the
// server only when there is nothing for this key yet, and never before the
// first All Data rows are on screen.
function requestMyWorkAttention() {
  updateMyWorkNavBadge();
  if (!myWorkAttentionEligible() || !myWorkAttentionApiReady()) return;
  if (deferBootRequestUntilFirstGridPaint("myWorkAttention", requestMyWorkAttention)) return;
  installMyWorkAttentionPolling();
  const model = oversightState();
  const key = myWorkAttentionKey(model);
  if (!model.homeSummary || model.homeSummary.key !== key) { refreshMyWorkAttention(); return; }
  myWorkAttentionQueues(model, false);
}

// My Work was just OPENED and has already painted from the cache. Fill in the
// one count a background load skips, quietly re-check the open list, and
// revalidate the summary if it is older than the floor. Nothing here blocks the
// paint that already happened.
function noteMyWorkOpened() {
  if (!myWorkAttentionEligible()) return;
  const model = oversightState();
  const key = myWorkAttentionKey(model);
  const entry = model.homeSummary && model.homeSummary.key === key ? model.homeSummary : null;
  // The count a background load skipped. Read on its own only when the
  // revalidation below will not run (it reads a missing one itself).
  if (entry && entry.status === "ready" && !myWorkAttentionStale(entry) && !Object.prototype.hasOwnProperty.call(entry, "internalReview")
    && oversightWorkTabs().some((tab) => tab[0] === "internalReview") && !myWorkAttentionState.internalReviewReads.has(entry)) {
    myWorkAttentionState.internalReviewReads.add(entry);
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const timeout = setTimeout(() => controller?.abort(), 12000);
    oversightInternalReviewCount(model, controller ? controller.signal : undefined).then((total) => total, () => null).then((total) => {
      clearTimeout(timeout);
      if (model.homeSummary !== entry || key !== myWorkAttentionKey(model)) return;
      entry.internalReview = total;
      paintOversightTabCounts();
    });
  }
  if (model.status === "ready" && !["overview", "existing", "internalReview"].includes(model.tab) && typeof loadOversightWork === "function") {
    loadOversightWork({ quiet: true }).catch(() => {});
  }
  refreshMyWorkAttention();
}

function assignmentBoardDisplayName(fallback = "") {
  const auth = state.authUserScope || storedPortalAuthUserSnapshot() || {};
  return String(auth.displayName || auth.username || auth.email || state.activeUserName || fallback || "Your work").trim();
}

function internalReviewWorkRequest(model) {
  const access = trackerAccessEnvelope("service");
  return {
    resource: "serviceLines", mode: "service",
    page: { number: Math.max(1, Math.floor(Number(model.internalReviewPage) || 1)), size: 25 },
    filters: {},
    columnFilters: { action: ["Internal Review Required"], ...(model.orgId ? { orgId: [String(model.orgId)] } : {}) },
    sort: [{ field: "primaryPayer", direction: "asc" }, { field: "claimId", direction: "asc" }, { field: "serviceId", direction: "asc" }],
    access,
    // aging + billDateAging: how old the line is, for the person deciding what
    // to do with it. Neither is money and neither is PHI, and both are already
    // in this principal's serviceColumns envelope - no role sees anything new.
    // patient: PHI, requested only with VIEW_PHI; the server narrows it away for
    // anyone else, and claimPatientLineHtml re-checks before showing it.
    responseShape: { columns: ["claimId", "serviceId", "orgId", "client", "primaryPayer", "secondaryPayer", "action", "followUpDate", "aging", "billDateAging", ...(access.includePhi ? ["patient"] : [])], includeFinancials: false, includePhi: access.includePhi, includeInternalNotes: false }
  };
}

async function loadInternalReviewWork() {
  const model = oversightState();
  const request = ++model.request;
  const readScope = additionalPortalDataReadScope();
  const body = internalReviewWorkRequest(model);
  model.internalReviewRows = [];
  const current = () => readScope === additionalPortalDataReadScope() && oversightState() === model && model.tab === "internalReview" && request === model.request && JSON.stringify(body) === JSON.stringify(internalReviewWorkRequest(model));
  model.status = "loading";
  try {
    const base = trackerApiBase(), token = trackerApiToken();
    if (!base || !token) throw new Error("Sign in to load internal review work.");
    const response = await fetch(additionalPortalDataReadUrl(base + "/api/tracker/query"), { method: "POST", cache: "no-store", headers: { "content-type": "application/json", authorization: "Bearer " + token }, body: JSON.stringify(body) });
    const data = await response.json().catch(() => ({}));
    if (!current()) return;
    if (!response.ok || data.error) throw new Error(response.status === 401 ? "Your session expired. Sign in again." : "Internal review work could not be loaded. Refresh to try again.");
    if (!Array.isArray(data.rows) || !data.page || !Number.isFinite(Number(data.page.totalRows)) || !Number.isFinite(Number(data.page.totalPages))) throw new Error("Internal review results were incomplete. Refresh to try again.");
    model.internalReviewRows = data.rows.map((row, index) => ({ ...row, latestReviewNote: { status: hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) && index < 25 ? "loading" : "unavailable", sourceComplete: false } }));
    model.internalReviewPagination = data.page;
    model.status = "ready";
    if (state.view === "assignments") renderOversightWork();
    await hydrateInternalReviewNotes(model, current);
    return;
  } catch (error) { if (!current()) return; model.status = "error"; model.error = error.message; }
  if (state.view === "assignments") renderOversightWork();
}

function internalReviewFollowUpDate(value) {
  const normalized = normalizeDateValue(value);
  return normalized ? formatWorkflowNoteDate(normalized) : "Not set";
}

async function hydrateInternalReviewNotes(model, current) {
  const rows = model.internalReviewRows;
  const base = trackerApiBase(), token = trackerApiToken();
  const permitted = () => current() && state.view === "assignments" && model.internalReviewRows === rows && hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) && base === trackerApiBase() && token === trackerApiToken();
  if (!permitted() || !base || !token) return;
  const pending = rows.slice(0, 25);
  let next = 0;
  const worker = async () => {
    while (next < pending.length && permitted()) {
      const row = pending[next++];
      let preview = { status: "unavailable", sourceComplete: false };
      if (row.orgId && row.claimId && row.serviceId) {
        try {
          const query = new URLSearchParams({ orgId: String(row.orgId), serviceId: String(row.serviceId), limit: "1" });
          const envelope = await claimDrawerFetchJson(additionalPortalDataReadUrl(`${base}/api/tracker/claims/${encodeURIComponent(row.claimId)}/notes?${query}`), token, { isCurrent: permitted });
          if (!permitted()) return;
          if (envelope.ok && !envelope.body?.error && !envelope.body?.hidden) {
            const notes = normalizeClaimNotesResponse(envelope.body);
            if (notes.status === "ok") {
              const sourceComplete = notes.sourceCompleteness?.complete === true;
              const item = notes.items[0];
              const inScope = item && (item.scopeMode === "claim" || (item.scopeMode === "service_lines" && item.serviceLineIds.includes(String(row.serviceId))));
              if (inScope && !item.voided && String(item.text || "").trim()) preview = { status: "ready", item, sourceComplete };
              else if (!notes.items.length && !notes.hasMore && sourceComplete) preview = { status: "empty", sourceComplete: true };
            }
          }
        } catch (_error) { /* Keep a failed or hidden preview distinct from a verified empty history. */ }
      }
      if (!permitted()) return;
      row.latestReviewNote = preview;
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, pending.length) }, worker));
  if (permitted()) updateInternalReviewNotePreviews(model);
}

function updateInternalReviewNotePreviews(model) {
  if (state.view !== "assignments" || oversightState() !== model || model.tab !== "internalReview" || !hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES)) return;
  const host = document.getElementById("offshoreQueue");
  if (!host) return;
  const key = (orgId, claimId, serviceId) => JSON.stringify([String(orgId), String(claimId), String(serviceId)]);
  const rows = new Map(model.internalReviewRows.map((row) => [key(row.orgId, row.claimId, row.serviceId), row]));
  host.querySelectorAll("[data-review-note-org]").forEach((cell) => {
    const row = rows.get(key(cell.dataset.reviewNoteOrg, cell.dataset.reviewNoteClaim, cell.dataset.reviewNoteService));
    if (row) cell.innerHTML = internalReviewNoteHtml(row);
  });
  const hint = host.querySelector("[data-internal-review-note-hint]");
  if (hint) hint.hidden = !model.internalReviewRows.some((row) => row.latestReviewNote?.status === "ready" && !row.latestReviewNote.sourceComplete);
}

function internalReviewNoteHtml(row) {
  if (!hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES)) return '<span class="internal-review-muted">Claim notes require access.</span>';
  const previewState = row.latestReviewNote || {};
  if (previewState.status === "loading") return '<span class="internal-review-muted">Loading note preview…</span>';
  if (previewState.status === "empty" && previewState.sourceComplete === true) return '<span class="internal-review-muted">No note recorded.</span>';
  const item = previewState.item;
  const inScope = item && (item.scopeMode === "claim" || (item.scopeMode === "service_lines" && Array.isArray(item.serviceLineIds) && item.serviceLineIds.includes(String(row.serviceId))));
  if (previewState.status !== "ready" || !inScope || item.voided || !String(item.text || "").trim()) return '<span class="internal-review-muted">Note preview unavailable. Open the service line to review.</span>';
  const note = String(item.text).trim();
  const compact = note.replace(/\s+/g, " ");
  const preview = compact.length > 180 ? compact.slice(0, 177).trimEnd() + "…" : compact;
  const metadata = [item.authorFullName, item.at ? oversightTime(item.at) : ""].filter(Boolean).join(" · ");
  return '<small class="internal-review-muted">Latest available note · ' + (item.scopeMode === "service_lines" ? 'This service line' : 'Whole claim') + '</small><p class="internal-review-note-preview">' + escapeHtml(preview) + '</p>'
    + (metadata ? '<small class="internal-review-muted">' + escapeHtml(metadata) + '</small>' : '')
    + (compact.length > 180 || note.includes("\n") ? '<details class="internal-review-note-details"><summary>Read full note</summary><p class="internal-review-note-full">' + escapeHtml(note) + '</p></details>' : '');
}

function renderInternalReviewWork(model) {
  const rows = model.internalReviewRows || [], page = model.internalReviewPagination || {};
  const number = Number(page.number) || 1, totalPages = Number(page.totalPages) || 1;
  const incompleteNotes = hasPermission(PERMISSIONS.VIEW_CLAIM_NOTES) && rows.some((row) => row.latestReviewNote?.status === "ready" && !row.latestReviewNote.sourceComplete);
  const intro = '<p class="internal-review-intro">Review notes by insurance and client. Open a service line to review what is needed.</p><p class="internal-review-muted" data-internal-review-note-hint' + (incompleteNotes ? '' : ' hidden') + '>Previews show the latest available note; full note history may be incomplete.</p>';
  if (!Number(page.totalRows)) return intro + '<div class="oversight-empty"><h3>No internal review work</h3><p>No service lines have this follow-up action in the selected companies.</p></div>';
  const company = (row) => String(row.client || oversightCompany(row.orgId) || "Client not listed");
  const payer = (row) => String(row.primaryPayer || "Insurance not listed");
  const orderedRows = [...rows].sort((a, b) => payer(a).localeCompare(payer(b), undefined, { sensitivity: "base" })
    || company(a).localeCompare(company(b), undefined, { sensitivity: "base" })
    || String(a.orgId || "").localeCompare(String(b.orgId || ""))
    || String(a.claimId || "").localeCompare(String(b.claimId || ""), undefined, { numeric: true })
    || String(a.serviceId || "").localeCompare(String(b.serviceId || ""), undefined, { numeric: true }));
  const pager = totalPages > 1 ? '<div class="oversight-actions internal-review-pager"><button type="button" class="ghost-button" data-internal-review-page="' + (number - 1) + '"' + (number <= 1 ? ' disabled' : '') + '>Previous</button><button type="button" class="ghost-button" data-internal-review-page="' + (number + 1) + '"' + (number >= totalPages ? ' disabled' : '') + '>Next</button></div>' : '';
  return intro + '<div class="internal-review-toolbar"><p class="oversight-count">' + escapeHtml(page.totalRows) + ' service lines'
    + (totalPages > 1 ? ' · Page ' + escapeHtml(number) + ' of ' + escapeHtml(totalPages) + ' · Grouped by insurance and client on this page' : ' · Grouped by insurance and client')
    + '</p>' + pager + '</div><div class="internal-review-table-scroll" role="region" aria-label="Internal review service lines by insurance and client" tabindex="0"><table class="internal-review-table"><caption class="visually-hidden">Internal review required. Each row is one service line. Each note identifies whether it applies to this line or the whole claim.</caption><thead><tr><th scope="col">Insurance</th><th scope="col">Client / company</th><th scope="col">Service line</th><th scope="col">Aging</th><th scope="col">Latest note / information needed</th><th scope="col">Follow-up date</th><th scope="col">Open</th></tr></thead><tbody>' + orderedRows.map((row, index) => {
      const previous = orderedRows[index - 1];
      const payerStart = previous && payer(previous) !== payer(row);
      const clientStart = previous && !payerStart && (company(previous) !== company(row) || String(previous.orgId) !== String(row.orgId));
      return '<tr data-internal-review-row class="' + (payerStart ? 'internal-review-payer-start' : clientStart ? 'internal-review-client-start' : '') + '"><td><strong>' + escapeHtml(payer(row)) + '</strong>'
        + (row.secondaryPayer ? '<small class="internal-review-muted">Secondary: ' + escapeHtml(row.secondaryPayer) + '</small>' : '')
        + '</td><td>' + escapeHtml(company(row)) + '</td><td>' + workClaimLinkHtml(row) + claimPatientLineHtml(row) + '<small class="internal-review-muted">Line ' + escapeHtml(row.serviceId) + '</small></td>'
        + '<td>' + agingDaysChipHtml(agingDaysView(row)) + '</td>'
        + '<td class="internal-review-note-cell" data-review-note-org="' + escapeHtml(row.orgId) + '" data-review-note-claim="' + escapeHtml(row.claimId) + '" data-review-note-service="' + escapeHtml(row.serviceId) + '">' + internalReviewNoteHtml(row) + '</td><td>' + escapeHtml(internalReviewFollowUpDate(row.followUpDate)) + '</td><td><button type="button" class="ghost-button" data-work-open data-claim="' + escapeHtml(row.claimId) + '" data-org="' + escapeHtml(row.orgId) + '" data-service="' + escapeHtml(row.serviceId) + '" aria-label="Open claim ' + escapeHtml(row.claimId) + ', service line ' + escapeHtml(row.serviceId) + '">Open</button></td></tr>';
    }).join('') + '</tbody></table></div>';
}

// options.quiet: STALE-WHILE-REVALIDATE. The ready summary for this key stays
// on screen and each read overwrites its own field in place when it lands,
// so nothing blanks and nothing flashes "..." while it is checked. A read
// that fails still clears its field: a failure is unknown, not old news.
// options.background: the load was not asked for by the My Work screen
// (the sidebar badge's prefetch), so it skips the internal-review count,
// which is a tracker query and must never queue behind or beside the All
// Data grid's own. noteMyWorkOpened fills it in when My Work is opened.
async function loadBillerHomeSummary(key, options = {}) {
  const model = oversightState();
  const held = options.quiet === true && model.homeSummary && model.homeSummary.key === key && model.homeSummary.status === "ready" ? model.homeSummary : null;
  const entry = held || { key, status: "loading" }; model.homeSummary = entry;
  entry.loadedAtMs = Date.now();
  const countReads = oversightTabCountReads(model, entry, key, { background: options.background === true });
  const params = new URLSearchParams({ page: "1", pageSize: "1" });
  if (model.orgId) params.set("orgId", model.orgId);
  // The home page counts overdue and due-today work from real rows, so the
  // task read asks for the first five instead of a count-only page.
  // order=deadline, not the default priority-first order: five priority-first
  // rows are not the five nearest deadlines, and the home page counts
  // overdue/due-today work from exactly these rows.
  const taskParams = new URLSearchParams({ page: "1", pageSize: "5", order: "deadline" });
  if (model.orgId) taskParams.set("orgId", model.orgId);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  const read = path => oversightRequest(path, undefined, "GET", { signal: controller.signal });
  const results = await Promise.allSettled([
    read('/api/directed-work?view=mine&' + taskParams),
    read('/api/workflow/follow-up-proposals?mode=mine&' + params)
  ]);
  clearTimeout(timeout);
  if (model.homeSummary !== entry || key !== JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""])) return;
  const before = held ? JSON.stringify([entry.tasks, entry.proposals]) : "";
  ['tasks', 'proposals'].forEach((name, index) => { entry[name] = results[index].status === 'fulfilled' ? results[index].value : null; });
  if (entry.tasks && !entry.tasks.counts) entry.tasks = null;
  if (entry.proposals && !entry.proposals.counts) entry.proposals = null;
  entry.status = "ready";
  // A quiet re-read that found nothing new repaints only the pills; the
  // Overview is not rebuilt under the user's hands to show the same thing.
  const changed = !held || JSON.stringify([entry.tasks, entry.proposals]) !== before;
  if (state.view === "assignments" && model.tab === "overview" && changed) renderAssignments();
  else paintOversightTabCounts();
  if (typeof updateMyWorkNavBadge === "function") updateMyWorkNavBadge();
  // Resolve only once every count read has settled too, so a caller that
  // awaits this load (the badge's shared refresh) owns the whole set.
  await countReads;
}

// Counts the tab strip needs that the four Overview cards do not. They run on
// their own controller and settle one by one, so a slow tracker query can
// never hold up the home cards, and a read that fails or is refused simply
// leaves that tab without a badge.
function oversightTabCountReads(model, entry, key, options = {}) {
  const tabs = oversightWorkTabs().map((tab) => tab[0]);
  abortOversightTabCountReads();
  const controller = new AbortController();
  oversightTabCountController = controller;
  const timeout = setTimeout(() => controller.abort(), 12000);
  const params = new URLSearchParams({ page: "1", pageSize: "1" });
  if (model.orgId) params.set("orgId", model.orgId);
  const current = () => model.homeSummary === entry && key === JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""]);
  const settle = (name, value) => { if (!current()) return; entry[name] = value; paintOversightTabCounts(); };
  // A read cancelled because a newer set replaced it is SUPERSEDED, not
  // failed. With a quiet re-read both sets write into the same summary, so
  // settling a null here would blank a badge the newer read is about to fill.
  // A read that hit its own deadline is still this set's, and still a failure.
  const failed = (name) => () => { if (controller.signal.aborted && oversightTabCountController !== controller) return; settle(name, null); };
  const read = (path) => oversightRequest(path, undefined, "GET", { signal: controller.signal });
  const counted = (data) => data && data.counts ? data : null;
  const grouped = (data) => data && data.page && Number.isFinite(Number(data.page.totalRows)) ? data : null;
  const reads = [];
  // Never ask for a queue this principal has no tab for.
  if (tabs.includes("sent")) reads.push(read("/api/directed-work?view=sent&" + params).then((data) => settle("sentTasks", counted(data)), failed("sentTasks")));
  // groupBy=claim is the request shape the approvals tab itself sends. Without
  // it the server pages lines and the badge would count a unit that appears
  // nowhere on the screen the supervisor opens.
  if (tabs.includes("approvals")) reads.push(read("/api/workflow/follow-up-proposals?mode=review&groupBy=claim&" + params).then((data) => settle("reviewProposals", grouped(data)), failed("reviewProposals")));
  // The OTHER approval route, started here so the "Needs my approval" badge can
  // resolve without the supervisor first opening the tab. It is cached per
  // company and access snapshot, so opening the tab later costs nothing, and it
  // repaints the strip from its own landing callback rather than being awaited
  // here - a slow queue read must not hold up the other four badges.
  if (tabs.includes("approvals") && typeof requestApprovalQueue === "function" && typeof workflowApiBase === "function" && workflowApiBase()) requestApprovalQueue("pending");
  // Not in a background load: this one is a tracker query (see loadBillerHomeSummary).
  // On My Work itself a background re-read still fills it when it is MISSING -
  // a superseded read never settles, and nothing else would ask again.
  const skipInternalReview = options.background === true && (state.view !== "assignments" || Object.prototype.hasOwnProperty.call(entry, "internalReview"));
  if (tabs.includes("internalReview") && !skipInternalReview) reads.push(oversightInternalReviewCount(model, controller.signal).then((total) => settle("internalReview", total), failed("internalReview")));
  return Promise.allSettled(reads).then(() => { clearTimeout(timeout); if (oversightTabCountController === controller) oversightTabCountController = null; });
}

// The same request the Internal review tab issues - same filters, same access
// envelope, same company scope - asking for one row instead of twenty-five.
// The badge is therefore that tab's own totalRows and nothing wider.
async function oversightInternalReviewCount(model, signal) {
  const base = trackerApiBase(), token = trackerApiToken();
  if (!base || !token) return null;
  const body = { ...internalReviewWorkRequest(model), page: { number: 1, size: 1 } };
  const response = await fetch(additionalPortalDataReadUrl(base + "/api/tracker/query"), { method: "POST", cache: "no-store", headers: { "content-type": "application/json", authorization: "Bearer " + token }, body: JSON.stringify(body), signal });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.error || !data.page || !Number.isFinite(Number(data.page.totalRows))) return null;
  return Number(data.page.totalRows);
}

function billerRequestStatusFilter(model) {
  return '<label class="oversight-company">Request status <select data-work-request-status>' + [["", "All decisions"], ["pending", "Waiting for review"], ["rejected", "Sent back"], ["approved", "Approved"]].map(([key, label]) => '<option value="' + key + '"' + ((model.requestStatus || "") === key ? ' selected' : '') + '>' + label + '</option>').join('') + '</select></label>';
}

function oversightTaskStatusLabel(status) {
  return { open: "Open", "in-progress": "In progress", completed: "Complete", canceled: "Canceled" }[String(status || "")] || "Status unavailable";
}

function oversightRequestStatusLabel(status) {
  // One vocabulary for follow-up requests everywhere in My Work: the raw
  // backend status never reaches the screen.
  return { pending: "Waiting for review", submitted: "Waiting for review", rejected: "Sent back", approved: "Approved", canceled: "Withdrawn" }[String(status || "").toLowerCase()] || "Review status unavailable";
}

function oversightDayGap(fromDate, toDate) {
  const start = Date.parse(String(fromDate) + "T00:00:00Z"), end = Date.parse(String(toDate) + "T00:00:00Z");
  return Number.isFinite(start) && Number.isFinite(end) ? Math.max(0, Math.round((end - start) / 86400000)) : 0;
}

function oversightShortDate(value) {
  const date = new Date(String(value) + "T00:00:00");
  return Number.isFinite(date.getTime()) ? date.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : String(value || "");
}

function oversightTaskDue(row = {}) {
  // Deadline ranking drives both the label and the sort: overdue, today, this
  // week, later, undated. Finished work sinks below everything still open.
  const due = String(row.dueDate || ""), today = todayDateString();
  if (["completed", "canceled"].includes(row.status)) return { label: due ? oversightShortDate(due) : "No due date", overdue: false, rank: 5 };
  if (!due) return { label: "No due date", overdue: false, rank: 4 };
  if (due < today) return { label: "Overdue · " + oversightDayGap(due, today) + "d", overdue: true, rank: 0 };
  if (due === today) return { label: "Today", overdue: false, rank: 1 };
  return { label: oversightShortDate(due), overdue: false, rank: oversightDayGap(today, due) <= 7 ? 2 : 3 };
}

function oversightTaskSort(rows) {
  const priority = { urgent: 0, high: 1, normal: 2 };
  return rows.slice().sort((a, b) => oversightTaskDue(a).rank - oversightTaskDue(b).rank
    || (priority[a.priority] ?? 3) - (priority[b.priority] ?? 3)
    || String(a.dueDate || a.createdAtUtc || "").localeCompare(String(b.dueDate || b.createdAtUtc || "")));
}

function oversightRequestSort(rows) {
  const rank = { rejected: 0, pending: 1, submitted: 1, approved: 2 };
  return rows.slice().sort((a, b) => (rank[String(a.status || "").toLowerCase()] ?? 3) - (rank[String(b.status || "").toLowerCase()] ?? 3)
    || String(b.createdAtUtc || b.createdAt || "").localeCompare(String(a.createdAtUtc || a.createdAt || "")));
}

function oversightEmptyStateHtml() {
  return `<div class="oversight-empty"><h3>You're clear</h3><p>No tasks assigned to you right now. New work arrives from a service line's follow-up window.</p></div>`;
}

function oversightTaskRowHtml(row, sent) {
  const due = oversightTaskDue(row);
  const closed = ["completed", "canceled"].includes(row.status);
  const rail = row.priority === "urgent" ? "var(--red)" : row.priority === "high" ? "var(--amber)" : "";
  // The rail is colour; screen readers and colour-blind users get the word.
  const priorityText = row.priority === "urgent" ? "Urgent priority" : row.priority === "high" ? "High priority" : "";
  const instruction = String(row.instruction || "");
  return `<tr class="oversight-task-row"${rail ? ` style="box-shadow: inset 3px 0 0 ${rail}"` : ""}><td><span class="oversight-badge">${escapeHtml(oversightTaskStatusLabel(row.status))}</span>${priorityText ? `<span class="visually-hidden">${priorityText}</span>` : ""}</td><td class="oversight-task-text" title="${escapeHtml(instruction)}">${escapeHtml(instruction)}</td><td>${workClaimLinkHtml(row)}${claimPatientLineHtml(row)}<span class="internal-review-muted">${escapeHtml(oversightCompany(row.orgId))}</span>${row.serviceId ? `<span class="internal-review-muted">Line ${escapeHtml(row.serviceId)}</span>` : ""}</td><td class="oversight-due-cell${due.overdue ? " is-overdue" : ""}">${escapeHtml(due.label)}</td><td>${escapeHtml(oversightPerson(sent ? row.assigneeUserId : row.assignedByUserId))}</td><td>${closed ? "" : sent ? `<button class="ghost-button" type="button" data-work-status="canceled" data-id="${escapeHtml(row.id)}">Cancel task</button>` : `<button class="primary-button" type="button" data-work-status="${row.status === "open" ? "in-progress" : "completed"}" data-id="${escapeHtml(row.id)}">${row.status === "open" ? "Start work" : "Mark complete"}</button>`}</td></tr>`;
}

function oversightTaskTableHtml(rows, sent) {
  return `<div class="internal-review-table-scroll"><table class="internal-review-table oversight-task-table"><thead><tr><th scope="col">Status</th><th scope="col">Task</th><th scope="col">Claim / line</th><th scope="col" class="oversight-due-cell">Due</th><th scope="col">${sent ? "Assigned to" : "From"}</th><th scope="col">Action</th></tr></thead><tbody>${rows.map((row) => oversightTaskRowHtml(row, sent)).join("")}</tbody></table></div>`;
}

function bindBillerShortcuts(host) {
  host.querySelectorAll("[data-biller-shortcut]").forEach((control) => {
    const open = () => {
      const model = oversightState(); oversightWorkReadController?.abort();
      Object.assign(model, { tab: control.dataset.billerShortcut, requestStatus: control.dataset.requestStatus || "", status: "idle", page: 1, pagination: null, request: model.request + 1 });
      invalidateApprovalQueue(); renderAssignments();
    };
    control.addEventListener("click", open);
    // Whole-card targets are role="button"; keep the keyboard contract a real button has.
    if (control.getAttribute("role") === "button") control.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); } });
  });
}

// OON RATE-CUT REVIEW (ticket "Rate cuts", owner 2026-10-27). Each confirmed cut
// is a task on Fee Schedules: fee-schedule editors Accept it (the new rate is
// real, so it stops coming up) or Dispute it (being pursued with the payer) and
// Resolve the dispute later, each with a note. A later cut on the same rate
// comes back as needing review. Review only: pricing, Expected and the client
// card are unchanged. GET /api/oon-rate-cut-reviews returns the cuts in the
// reader's scope with their review; POST .../decision records one decision.
const OON_CUT_REVIEW_TABS = Object.freeze([
  ["needs-review", "Needs review"],
  ["disputing", "Disputing"],
  ["accepted", "Accepted"],
  ["resolved", "Resolved"]
]);
const OON_CUT_REVIEW_PAGE_SIZE = 50;
const OON_CUT_ACTION_COPY = Object.freeze({
  accept: { title: "Accept this rate cut", hint: "The new rate is real. It stops showing as needing review.", button: "Accept" },
  dispute: { title: "Dispute this rate cut", hint: "You are taking it up with the payer. It stays under Disputing until you resolve it.", button: "Dispute" },
  resolve: { title: "Resolve the dispute", hint: "Say how it ended, for example reprocessed at the old rate, or the payer upheld the cut.", button: "Resolve" }
});
let oonCutReviewState = { status: "idle", body: null, error: "", requestedAt: 0, token: "", scopeKey: "", requestId: 0 };
let oonCutReviewTab = "needs-review";
let oonCutReviewSearch = "";
let oonCutReviewPage = 0;
// The one open decision form: { key, action, note, busy, error }.
let oonCutReviewForm = null;
// Why the last decision was refused, kept above the list after it reloads.
let oonCutReviewNotice = "";

function oonCutReviewAllowed() {
  return ["SuperAdmin", "StatesideBiller", "OffshoreBiller"].includes(canonicalRole(state.role))
    && hasPermission(PERMISSIONS.VIEW_FEE_SCHEDULES) && hasPermission(PERMISSIONS.VIEW_FINANCIALS);
}

function oonCutReviewRowKey(row = {}) {
  return `${row.id || ""}|${row.cutStart || ""}`;
}

// A read belongs to one sign-in and one company scope; anything else re-reads.
function oonCutReviewReadDue(scope, currentToken) {
  const current = oonCutReviewState;
  if (current.status === "idle") return true;
  if (String(current.token || "") !== String(currentToken || "") || String(current.scopeKey || "") !== scope.key) return true;
  if (current.status === "pending") return false;
  return Date.now() - Number(current.requestedAt || 0) >= OON_LEARNED_RATES_TTL_MS;
}

function requestOonCutReviews(onDone, scope = OON_ALL_COMPANIES_SCOPE, { force = false } = {}) {
  const token = workflowApiToken() || trackerApiToken();
  const requestId = (Number(oonCutReviewState.requestId) || 0) + 1;
  if (!token) {
    oonCutReviewState = { status: "unavailable", body: null, error: "", requestedAt: Date.now(), token: "", scopeKey: scope.key, requestId };
    return;
  }
  if (!force && oonCutReviewState.status === "pending" && oonCutReviewState.scopeKey === scope.key && oonCutReviewState.token === token) return;
  // While re-reading the same list it stays on screen; another sign-in or company never sees it.
  const kept = oonCutReviewState.token === token && oonCutReviewState.scopeKey === scope.key ? oonCutReviewState.body : null;
  oonCutReviewState = { status: "pending", body: kept, error: "", requestedAt: Date.now(), token, scopeKey: scope.key, requestId };
  const query = scope.orgIds.length ? `?orgIds=${encodeURIComponent(scope.orgIds.join(","))}` : "";
  const current = () => oonCutReviewState.requestId === requestId;
  const settle = (status, body, error) => { oonCutReviewState = { status, body, error, requestedAt: Date.now(), token, scopeKey: scope.key, requestId }; };
  fetch(additionalPortalDataReadUrl(`${workflowApiBase()}/api/oon-rate-cut-reviews${query}`), {
    method: "GET",
    cache: "no-store",
    headers: { authorization: `Bearer ${token}` }
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!current()) return;
      if (!response.ok) settle("error", null, String(body.message || "Rate cuts could not be loaded. Try again later."));
      else if (body.available === false) settle("unavailable", null, String(body.message || "Rate-cut review is not available yet."));
      else settle("ok", body, "");
    })
    .catch(() => { if (current()) settle("error", null, "Rate cuts could not be loaded. Try again later."); })
    .finally(() => { if (typeof onDone === "function") onDone(); });
}

function oonCutReviewCounts(rows = []) {
  const counts = Object.fromEntries(OON_CUT_REVIEW_TABS.map(([key]) => [key, 0]));
  rows.forEach((row) => { if (Object.hasOwn(counts, row.status)) counts[row.status] += 1; });
  return counts;
}

function oonCutReviewActionsHtml(row, canDecide) {
  if (!canDecide) return "";
  const actions = [];
  if (row.status !== "accepted") actions.push("accept");
  if (row.status !== "disputing") actions.push("dispute");
  if (row.status === "disputing") actions.push("resolve");
  return actions.map((action) => `<button type="button" class="small-button" data-oon-cut-action="${action}" data-oon-cut-key="${escapeHtml(oonCutReviewRowKey(row))}">${escapeHtml(OON_CUT_ACTION_COPY[action].button)}</button>`).join("");
}

function oonCutReviewStatusHtml(row = {}) {
  const tone = { "needs-review": "amber", disputing: "red", accepted: "green", resolved: "gray" }[row.status] || "gray";
  const label = (OON_CUT_REVIEW_TABS.find(([key]) => key === row.status) || [row.status, row.status])[1];
  const review = row.review;
  const who = review ? [review.decidedBy, review.decidedAtUtc ? formatDate(review.decidedAtUtc) : ""].filter(Boolean).join(" · ") : "";
  return `${pill(label, tone)}${who ? `<small class="oon-cut-who">${escapeHtml(who)}</small>` : ""}${review && review.note ? `<p class="oon-cut-note">${escapeHtml(review.note)}</p>` : ""}`;
}

function oonCutReviewFormHtml(form, colspan) {
  const copy = OON_CUT_ACTION_COPY[form.action] || OON_CUT_ACTION_COPY.accept;
  return `
          <tr class="oon-cut-form-row">
            <td colspan="${colspan}">
              <form class="oon-cut-form" data-oon-cut-form>
                <div><strong>${escapeHtml(copy.title)}</strong><span>${escapeHtml(copy.hint)}</span></div>
                <label>Note <small>required</small>
                  <textarea data-oon-cut-note rows="2" maxlength="1000" placeholder="Why? For example: the payer confirmed the new contract rate.">${escapeHtml(form.note || "")}</textarea>
                </label>
                ${form.error ? `<p class="oon-cut-error" role="alert">${escapeHtml(form.error)}</p>` : ""}
                <div class="oon-cut-form-buttons">
                  <button type="submit" class="primary-button"${form.busy ? " disabled" : ""}>${form.busy ? "Saving…" : escapeHtml(copy.button)}</button>
                  <button type="button" class="ghost-button" data-oon-cut-cancel${form.busy ? " disabled" : ""}>Cancel</button>
                </div>
              </form>
            </td>
          </tr>`;
}

// passive: a background refresh or a Fee Schedules repaint leaves the panel alone
// while someone is typing in it, so a half-written note never loses focus.
function renderOonCutReviewPanel(host, { passive = false } = {}) {
  if (!host) return;
  const section = host.closest("details");
  const counter = document.getElementById("oonRateCutReviewCount");
  const hideAll = () => {
    host.innerHTML = "";
    if (section) section.hidden = true;
  };
  if (!oonCutReviewAllowed()) return hideAll();
  const scope = oonLearnedRatesCompanyScope();
  if (oonCutReviewReadDue(scope, workflowApiToken() || trackerApiToken())) {
    requestOonCutReviews(() => { if (host.isConnected) renderOonCutReviewPanel(host, { passive: true }); }, scope);
  }
  if (passive && host.contains(document.activeElement) && document.activeElement.matches("textarea, input")) return;
  const { status } = oonCutReviewState;
  const body = oonCutReviewState.body;
  const rows = body && Array.isArray(body.rows) ? body.rows : [];
  // No learned-rate lane here, or no cut in this selection: the section stays out of the way.
  if (status === "unavailable" || (status === "ok" && !rows.length)) return hideAll();
  if (section) section.hidden = false;
  if (!body) {
    if (counter) counter.textContent = "";
    host.innerHTML = `<p class="oon-rates-note">${escapeHtml(status === "error" ? oonCutReviewState.error : "Loading rate cuts…")}</p>`;
    return;
  }
  const counts = oonCutReviewCounts(rows);
  if (counter) counter.textContent = counts["needs-review"] ? `${counts["needs-review"].toLocaleString()} need review` : "";
  const canDecide = body.canDecide === true;
  const pct = (row) => (Number.isFinite(Number(row.changePct)) ? Number(row.changePct) : 0);
  const visible = rows
    .filter((row) => row.status === oonCutReviewTab && oonLearnedRatesMatches(row, oonCutReviewSearch))
    .sort((a, b) => pct(a) - pct(b) || String(a.clientName || "").localeCompare(String(b.clientName || "")) || String(a.patientName || "").localeCompare(String(b.patientName || "")));
  const pageCount = Math.max(1, Math.ceil(visible.length / OON_CUT_REVIEW_PAGE_SIZE));
  oonCutReviewPage = Math.min(Math.max(0, oonCutReviewPage), pageCount - 1);
  const shown = visible.slice(oonCutReviewPage * OON_CUT_REVIEW_PAGE_SIZE, (oonCutReviewPage + 1) * OON_CUT_REVIEW_PAGE_SIZE);
  if (oonCutReviewForm && !rows.some((row) => oonCutReviewRowKey(row) === oonCutReviewForm.key)) oonCutReviewForm = null;
  const columns = canDecide ? 10 : 9;
  const emptyText = oonCutReviewSearch.trim() ? "No rate cuts match this search."
    : { "needs-review": "Every rate cut here has been reviewed.", disputing: "No disputes are open.", accepted: "No rate cuts have been accepted yet.", resolved: "No disputes have been resolved yet." }[oonCutReviewTab];
  host.innerHTML = `
    <p class="oon-cut-intro">A plan has paid less per unit for 5 payments in a row. ${canDecide
      ? "<strong>Accept</strong> a cut that is real so it stops coming up, or <strong>Dispute</strong> it while you take it up with the payer. A later cut on the same rate comes back here."
      : "Billers with fee-schedule edit access accept or dispute each cut."} Nothing here changes Expected.</p>
    ${oonCutReviewNotice ? `<p class="oon-cut-error" role="alert">${escapeHtml(oonCutReviewNotice)}</p>` : ""}
    <div class="oon-cut-toolbar">
      <div class="oon-cut-tabs" role="group" aria-label="Rate cut review status">
        ${OON_CUT_REVIEW_TABS.map(([key, label]) => `<button type="button" class="oon-cut-tab${key === oonCutReviewTab ? " active" : ""}${key === "needs-review" && counts[key] ? " alert" : ""}" data-oon-cut-tab="${key}" aria-pressed="${key === oonCutReviewTab}">${escapeHtml(label)} <b>${counts[key].toLocaleString()}</b></button>`).join("")}
      </div>
      <input type="search" class="oon-rates-search" data-oon-cut-search placeholder="Search a client, plan, code or patient" aria-label="Search rate cuts" value="${escapeHtml(oonCutReviewSearch)}">
    </div>
    <div class="oon-rates-table-wrap">
      <table class="oon-rates-table oon-cut-table">
        <thead><tr><th>Client</th><th>Patient</th><th>Plan</th><th>Code</th><th class="num">Was</th><th class="num">Now</th><th class="num">Change</th><th>Since</th><th>Status</th>${canDecide ? "<th>Action</th>" : ""}</tr></thead>
        <tbody data-oon-cut-rows>
          ${shown.length ? shown.map((row) => {
            const key = oonCutReviewRowKey(row);
            const change = Number.isFinite(Number(row.changePct)) ? `−${Math.round(-Number(row.changePct) * 100)}%` : "—";
            return `
          <tr data-oon-cut-row="${escapeHtml(key)}">
            <td>${escapeHtml(row.clientName || "—")}</td>
            <td>${escapeHtml(row.patientName || "—")}</td>
            <td>${escapeHtml(row.payerName || "—")}</td>
            <td>${escapeHtml([row.procedureCode, row.modifier].filter(Boolean).join(" ") || "—")}</td>
            <td class="num">${oonRateMoney(row.priorRate)}</td>
            <td class="num">${oonRateMoney(row.rate)}</td>
            <td class="num oon-cut-change">${escapeHtml(change)}</td>
            <td>${row.cutStart ? escapeHtml(formatDate(row.cutStart)) : "—"}</td>
            <td class="oon-cut-status">${oonCutReviewStatusHtml(row)}</td>
            ${canDecide ? `<td class="oon-cut-actions">${oonCutReviewActionsHtml(row, canDecide)}</td>` : ""}
          </tr>${oonCutReviewForm && oonCutReviewForm.key === key ? oonCutReviewFormHtml(oonCutReviewForm, columns) : ""}`;
          }).join("") : `
          <tr><td colspan="${columns}"><strong>${escapeHtml(emptyText)}</strong></td></tr>`}
        </tbody>
      </table>
    </div>
    ${pageCount > 1 ? `
    <div class="oon-rates-pager">
      <button type="button" class="ghost-button" data-oon-cut-page="-1"${oonCutReviewPage > 0 ? "" : " disabled"}>Previous</button>
      <span>Page ${(oonCutReviewPage + 1).toLocaleString()} of ${pageCount.toLocaleString()}</span>
      <button type="button" class="ghost-button" data-oon-cut-page="1"${oonCutReviewPage < pageCount - 1 ? "" : " disabled"}>Next</button>
    </div>` : ""}
    ${status === "error" ? `<p class="oon-rates-note">${escapeHtml(oonCutReviewState.error)}</p>` : ""}`;
  host.onclick = (event) => {
    const tab = event.target.closest("[data-oon-cut-tab]");
    if (tab) {
      oonCutReviewTab = tab.getAttribute("data-oon-cut-tab") || "needs-review";
      oonCutReviewNotice = "";
      oonCutReviewPage = 0;
      oonCutReviewForm = null;
      renderOonCutReviewPanel(host);
      host.querySelector(`[data-oon-cut-tab="${oonCutReviewTab}"]`)?.focus();
      return;
    }
    const page = event.target.closest("[data-oon-cut-page]");
    if (page) {
      oonCutReviewPage += Number(page.getAttribute("data-oon-cut-page")) || 0;
      renderOonCutReviewPanel(host);
      return;
    }
    if (event.target.closest("[data-oon-cut-cancel]")) {
      oonCutReviewForm = null;
      renderOonCutReviewPanel(host);
      return;
    }
    const action = event.target.closest("[data-oon-cut-action]");
    if (action) {
      oonCutReviewNotice = "";
      oonCutReviewForm = { key: action.getAttribute("data-oon-cut-key") || "", action: action.getAttribute("data-oon-cut-action") || "accept", note: "", busy: false, error: "" };
      renderOonCutReviewPanel(host);
      host.querySelector("[data-oon-cut-note]")?.focus();
    }
  };
  host.oninput = (event) => {
    if (event.target.matches("[data-oon-cut-note]") && oonCutReviewForm) {
      oonCutReviewForm.note = event.target.value;
      return;
    }
    if (event.target.matches("[data-oon-cut-search]")) {
      oonCutReviewSearch = event.target.value;
      oonCutReviewPage = 0;
      const caret = event.target.selectionStart;
      renderOonCutReviewPanel(host);
      const input = host.querySelector("[data-oon-cut-search]");
      if (input) { input.focus(); try { input.setSelectionRange(caret, caret); } catch (_) {} }
    }
  };
  host.onsubmit = (event) => {
    if (!event.target.closest("[data-oon-cut-form]")) return;
    event.preventDefault();
    submitOonCutReview(host);
  };
}

async function submitOonCutReview(host) {
  const form = oonCutReviewForm;
  if (!form || form.busy) return;
  const row = (oonCutReviewState.body?.rows || []).find((candidate) => oonCutReviewRowKey(candidate) === form.key);
  if (!row) {
    oonCutReviewForm = null;
    renderOonCutReviewPanel(host);
    return;
  }
  const note = String(form.note || "").trim();
  if (note.length < 3) {
    form.error = "Add a short note saying why.";
    renderOonCutReviewPanel(host);
    host.querySelector("[data-oon-cut-note]")?.focus();
    return;
  }
  form.busy = true;
  form.error = "";
  renderOonCutReviewPanel(host);
  const token = workflowApiToken() || trackerApiToken();
  try {
    const response = await fetch(`${workflowApiBase()}/api/oon-rate-cut-reviews/decision`, {
      method: "POST",
      cache: "no-store",
      headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ id: row.id, cutStart: row.cutStart, action: form.action, note, expectedVersion: row.review ? Number(row.review.version) || 0 : 0 })
    });
    const payload = await response.json().catch(() => ({}));
    if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
    if (!response.ok) {
      const error = new Error(payload.message || "The decision could not be saved. Try again later.");
      error.reload = response.status === 404 || response.status === 409;
      throw error;
    }
    row.status = payload.status;
    row.review = payload.review;
    if (oonCutReviewForm === form) oonCutReviewForm = null;
    showToast(`Rate cut ${payload.status === "accepted" ? "accepted" : payload.status === "disputing" ? "marked as disputed" : "dispute resolved"}.`);
    renderOonCutReviewPanel(host);
  } catch (error) {
    if (oonCutReviewForm !== form) return;
    form.busy = false;
    form.error = error.message || "The decision could not be saved. Try again later.";
    // Someone else decided first, or the cut moved: say so above the list and show the current list.
    if (error.reload) {
      oonCutReviewNotice = form.error;
      oonCutReviewForm = null;
    }
    if (error.reload) requestOonCutReviews(() => { if (host.isConnected) renderOonCutReviewPanel(host); }, oonLearnedRatesCompanyScope(), { force: true });
    renderOonCutReviewPanel(host);
  }
}

// MY WORK RATE-CUT CARD. Counts the cuts still waiting for a decision (and open
// disputes) from the same review read, and opens the review on Fee Schedules.
// Staff with the fee-schedule and financial grants only; hidden when nothing
// needs attention or the read is not available, so My Work never shows an empty
// or broken card.
function myWorkOonRateCutsAllowed() {
  return oonCutReviewAllowed();
}

function myWorkOonRateCutsModel(body = {}) {
  const rows = Array.isArray(body.rows) ? body.rows : [];
  const counts = oonCutReviewCounts(rows);
  const needs = counts["needs-review"];
  const disputing = counts.disputing;
  if (!needs && !disputing) return null;
  const clients = new Set(rows.filter((row) => row.status === "needs-review").map((row) => String(row.clientName || "").trim()).filter(Boolean));
  return { needs, disputing, clients: clients.size };
}

function renderMyWorkOonRateCuts(slot) {
  if (!slot) return;
  const hide = () => { slot.hidden = true; slot.innerHTML = ""; };
  if (!myWorkOonRateCutsAllowed()) return hide();
  // The card follows the header company (every company the reader may see when none
  // is selected), so a read for a different company is never reused.
  const scope = oonLearnedRatesHeaderScope();
  if (oonCutReviewReadDue(scope, workflowApiToken() || trackerApiToken())) {
    requestOonCutReviews(() => { if (slot.isConnected) renderMyWorkOonRateCuts(slot); }, scope);
  }
  const model = oonCutReviewState.status === "ok" && oonCutReviewState.scopeKey === scope.key ? myWorkOonRateCutsModel(oonCutReviewState.body || {}) : null;
  if (!model) return hide();
  slot.hidden = false;
  const headline = model.needs
    ? `${model.needs.toLocaleString()} rate ${model.needs === 1 ? "cut needs" : "cuts need"} review${model.clients ? ` across ${formatNounCount(model.clients, "company", "companies")}` : ""}`
    : `${model.disputing.toLocaleString()} rate-cut ${model.disputing === 1 ? "dispute is" : "disputes are"} open`;
  const detail = model.needs
    ? `A plan paid less per unit for 5 payments in a row. Accept it or dispute it with the payer.${model.disputing ? ` ${model.disputing.toLocaleString()} ${model.disputing === 1 ? "dispute is" : "disputes are"} still open.` : ""}`
    : "Resolve each one once the payer answers.";
  slot.innerHTML = `
    <div>
      <span>Out-of-network rates · ${escapeHtml(scope.label || "all your companies")}</span>
      <strong>${escapeHtml(headline)}</strong>
      <p>${escapeHtml(detail)}</p>
    </div>
    <button type="button" class="ghost-button" data-oon-open-cuts>Review rate cuts</button>`;
  slot.querySelector("[data-oon-open-cuts]")?.addEventListener("click", () => {
    oonCutReviewTab = model.needs ? "needs-review" : "disputing";
    oonCutReviewPage = 0;
    navigateToView("feeSchedules");
    const section = document.getElementById("oonRateCutReviewDetails");
    if (section) section.open = true;
    section?.scrollIntoView({ block: "start" });
  });
}

function renderBillerWorkOverview(host) {
  const model = oversightState();
  const summaryKey = JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""]);
  const summary = model.homeSummary?.key === summaryKey ? model.homeSummary : null;
  const ready = summary?.status === "ready";
  const today = todayDateString();
  const taskRows = (ready && summary.tasks && Array.isArray(summary.tasks.items) ? summary.tasks.items : []).filter((row) => !["completed", "canceled"].includes(row.status));
  const taskTotal = ready && summary.tasks ? Number(summary.tasks.page?.totalRows ?? (Number(summary.tasks.counts?.open || 0) + Number(summary.tasks.counts?.["in-progress"] || 0))) || 0 : 0;
  // The home read asks for one small page, so deadline counts derived from it
  // are a floor. Say so with "+" instead of printing a number we cannot prove.
  const partial = taskTotal > taskRows.length;
  const overdue = taskRows.filter((row) => row.dueDate && row.dueDate < today);
  const dueToday = taskRows.filter((row) => row.dueDate === today);
  const proposalCounts = ready && summary.proposals ? summary.proposals.counts || {} : null;
  const metric = (available, value, more) => !ready ? "…" : !available ? "—" : String(value) + (more ? "+" : "");
  const returned = billerReturnedOverviewCount(model, proposalCounts);
  const cards = [
    ["metric-card metric-card-alert", "Overdue", metric(ready && summary.tasks, overdue.length, partial), partial ? "" : "Work these first", "tasks", ""],
    ["metric-card metric-card-warning", "Due today", metric(ready && summary.tasks, dueToday.length, partial), "", "tasks", ""],
    // A FLOOR OF NOTHING IS NOT A COUNT. "0+" reads as "at least none", which
    // is no information at all, so the card shows the same dash it shows for
    // any other number it cannot state -- and the list it opens says which
    // lane could not be checked.
    ["metric-card", "Sent back to me", metric(proposalCounts && !(returned.floor && !returned.value), returned.value, returned.floor), "", "requests", "rejected"],
    ["metric-card", "Waiting for review", metric(proposalCounts, Number(proposalCounts?.pending || 0), false), "", "requests", "pending"]
  ];
  const focus = oversightTaskSort(overdue.concat(dueToday)).slice(0, 5);
  const seeAll = `<div class="oversight-actions"><button type="button" class="ghost-button" data-biller-shortcut="tasks" data-request-status="">See all ${taskTotal} ${taskTotal === 1 ? "task" : "tasks"}</button></div>`;
  const work = !ready ? "<p>Loading your work…</p>"
    : focus.length ? `<h3 class="oversight-home-subhead">Overdue and due today</h3>${oversightTaskTableHtml(focus, false)}${seeAll}`
    : taskTotal ? `<p class="oversight-count">${partial ? "No overdue or due-today work among your nearest deadlines — open all tasks to check." : "Nothing is overdue or due today."}</p>${seeAll}`
    : oversightEmptyStateHtml();
  host.innerHTML = `<section class="panel oversight-work"><div class="oversight-heading"><div><h2>My Work</h2><p>Your daily starting point. Work your tasks, check returned items, then track reviews and follow-up dates.</p><p>${escapeHtml(model.orgId ? oversightCompany(model.orgId) : "All my companies")}</p></div><button type="button" class="ghost-button" data-home-refresh>Refresh</button></div>${oversightTabs(oversightWorkTabsWithCounts(), "overview", "data-work-tab")}<section class="metric-strip" aria-label="My Work summary">${cards.map(([className, label, value, hint, tab, status]) => `<article class="${className}" role="button" tabindex="0" data-biller-shortcut="${tab}" data-request-status="${status}" aria-label="${escapeHtml(label + ": " + value)}"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong>${hint ? `<small>${escapeHtml(hint)}</small>` : ""}</article>`).join("")}</section>${work}</section>`;
  host.querySelector(".oversight-work")?.insertAdjacentHTML("beforeend", '<aside id="myWorkOonRateCuts" class="my-work-oon" hidden></aside>');
  renderMyWorkOonRateCuts(host.querySelector("#myWorkOonRateCuts"));
  bindOversightWorkTabs(host);
  host.querySelector('[data-home-refresh]')?.addEventListener('click', () => { invalidateMyWorkCounts(model); renderAssignments(); });
  if (!summary) loadBillerHomeSummary(summaryKey);
  bindWorkClaimLinks(host);
  host.querySelectorAll("[data-work-status]").forEach((button) => button.addEventListener("click", () => updateOversightTask(button)));
  bindBillerShortcuts(host);
}


// The offshore biller's side of the review loop. Returned work is the whole
// point of this screen, so it reads in one fixed order every time -- what to
// fix, who sent it back and when, which claim and line, what they had asked
// for -- grouped by company and then by claim, oldest return first, with one
// action per block. Many of these readers work in a second language and at
// volume: plain words beat density, and a state is never colour alone.
// ONE VIEW OF WORK SENT BACK, ACROSS EVERY REVIEW LANE.
//
// A biller's work can be returned from three different places, and until now
// this view showed ONE of them. A follow-up change can be sent back as a
// per-line REQUEST (portal.follow_up_proposals, when the company routes
// changes to a named supervisor) or through TEAM APPROVALS (the overlay's own
// proposal, which is what the standard offshore account gets), and a whole
// CLAIM can be bounced back for review. An offshore biller whose account uses
// the second of those saw an empty "nothing is waiting for you" here while
// work was waiting for them on another screen.
//
// The lanes are an implementation detail and are never named on screen. What
// the biller is told is what KIND of thing came back, in plain words, because
// fixing one line and reworking a whole claim are different jobs.
//
// EVERY SOURCE IS THE SAME SCOPED, PERMISSION-CHECKED READ ITS OWN LIST USES:
// the follow-up request read this view already performs, the Team-approvals
// rework read (requestApprovalQueue caches it, so this adds no second call),
// and the assignment board this session has ALREADY loaded. Nothing here
// starts a heavy query, and nothing here widens what a row may show.
function billerRequestKindLabel(row) {
  return row && row.requestKind === "claim" ? "Whole claim sent back for review" : "Follow-up change on one line";
}

// The Team-approvals rework rail, normalised into the shape this view already
// reads. Only rows that read returned true for THIS person.
function billerTeamReturnedRows(model) {
  if (typeof requestApprovalQueue !== "function") return [];
  const slice = requestApprovalQueue("rework");
  if (!slice || slice.status !== "ok" || !slice.body) return [];
  const orgId = String(model.orgId || "");
  return (Array.isArray(slice.body.rows) ? slice.body.rows : [])
    .filter((row) => !orgId || String(row.orgId || "") === orgId)
    .map((row) => ({
      requestKind: "teamLine",
      proposalId: `team:${row.orgId}:${row.claimId}:${row.serviceId}`,
      orgId: String(row.orgId || ""), claimId: String(row.claimId || ""), serviceId: String(row.serviceId || ""),
      status: "rejected", superseded: false,
      decisionReason: String(row.rejectReason || ""),
      decidedByUserId: String(row.rejectedBy || ""), reviewerUserId: String(row.rejectedBy || ""),
      decidedAtUtc: String(row.reviewedAt || ""), createdAtUtc: String(row.setAt || ""),
      action: String(row.followUpAction || ""), followUpDate: String(row.followUpDate || ""),
      note: String(row.note || ""), suggestedAction: String(row.suggestedAction || "")
    }));
}

// Team-approval lines this biller sent that a reviewer has not decided yet.
// Without these, re-editing a sent-back line made it simply vanish from the
// rework rail: the biller saw the claim's other, still-returned lines under
// "Sent back" and nothing saying the fixed ones were now waiting (Abhishek,
// 2026-10-15, AvenuesABA claim 6048). The biller's own action-review read
// ("mine") already carries them; only its pending rows are used here, and only
// on the My requests list, where a status filter other than waiting drops them.
function billerTeamPendingRows(model) {
  if (typeof requestApprovalQueue !== "function" || model.tab !== "requests") return [];
  const status = String(model.requestStatus || "");
  if (status && status !== "pending") return [];
  const slice = requestApprovalQueue("mine");
  if (!slice || slice.status !== "ok" || !slice.body) return [];
  const orgId = String(model.orgId || "");
  return (Array.isArray(slice.body.rows) ? slice.body.rows : [])
    .filter((row) => String(row.reviewStatus || "") === "pending")
    .filter((row) => !orgId || String(row.orgId || "") === orgId)
    .map((row) => ({
      requestKind: "teamLine",
      proposalId: `team:${row.orgId}:${row.claimId}:${row.serviceId}`,
      orgId: String(row.orgId || ""), claimId: String(row.claimId || ""), serviceId: String(row.serviceId || ""),
      status: "pending", superseded: false, decisionReason: "",
      decidedByUserId: "", reviewerUserId: "", decidedAtUtc: "", createdAtUtc: String(row.setAt || ""),
      action: String(row.followUpAction || ""), followUpDate: String(row.followUpDate || ""),
      note: String(row.note || "")
    }));
}

// Claims a reviewer bounced back. Read from the assignment board this session
// already holds -- never fetched from here, so opening My Work cannot start the
// board's query -- and only when that read is ready. claimBounceReason is the
// one bounce-reason helper (OFFSHORE_HANDOFF_SPEC), used rather than reading
// reviewNote directly, because reviewNote is offshore's OWN words in every
// other status.
function billerBouncedClaimRows(model) {
  const board = typeof workflowBoardState === "object" && workflowBoardState ? workflowBoardState : null;
  if (!board || board.status !== "ready" || !Array.isArray(board.personalClaims)) return [];
  const orgId = String(model.orgId || "");
  return board.personalClaims
    .filter((claim) => claim && claim.assignmentStatus === "Bounced Back")
    .filter((claim) => !orgId || String(claim.orgId || "") === orgId)
    .map((claim) => ({
      requestKind: "claim",
      proposalId: `claim:${claim.orgId}:${claim.claimId}`,
      orgId: String(claim.orgId || ""), claimId: String(claim.claimId || ""), serviceId: "",
      status: "rejected", superseded: false,
      decisionReason: claimBounceReason(claim),
      decidedByUserId: "", reviewerUserId: "", decidedAtUtc: "", createdAtUtc: "",
      action: String(claim.action || ""), followUpDate: String(claim.followUpDate || ""), note: ""
    }));
}

// Whether each source could be read at all. A number is only ever printed when
// every source behind it ANSWERED; a source that is still loading makes the
// TOTAL unknown rather than smaller, because a badge that quietly undercounts
// is worse than one that admits it does not know yet.
//
// Three different answers, not two. "ok" is rows. "denied" is also an ANSWER --
// the server says this viewer has no team-approvals rail, so that lane carries
// nothing and the total is still knowable. An ERROR is unknown and will stay
// unknown until someone refreshes, so it is reported separately: telling a
// biller we are "still checking" something nobody will check again is the
// lie-that-never-resolves this view was written to avoid.
//
// The freshness check is why an "ok" slice for a DIFFERENT scope cannot be
// counted as an answer for this one: the rows helper re-requests on a key
// change and returns nothing while that runs, so without this the total would
// silently lose a lane at the moment the biller changed company.
function billerReturnedSources(model) {
  const slice = typeof approvalQueueState === "object" && approvalQueueState ? approvalQueueState.rework : null;
  const board = typeof workflowBoardState === "object" && workflowBoardState ? workflowBoardState : null;
  const key = typeof approvalQueueRequestKey === "function" ? approvalQueueRequestKey("rework") : "";
  const fresh = Boolean(slice) && (!key || !slice.key || slice.key === key);
  const reworkStatus = fresh ? String(slice.status || "") : "";
  return {
    requests: model.status === "ready" && (!model.pagination || Number(model.pagination.totalPages || 1) <= 1),
    teamLines: reworkStatus === "ok" || reworkStatus === "denied",
    teamLinesFailed: reworkStatus === "error",
    claims: Boolean(board) && board.status === "ready"
  };
}

// THE OVERVIEW'S "Sent back to me". The card and the list it opens must not
// disagree, and that list is the union of three lanes -- but the Overview
// deliberately starts NEITHER of the other two reads (opening My Work must not
// kick off the assignment board's query, and this must not fetch a queue the
// biller has not asked for). So it adds the lanes that have ALREADY answered to
// the follow-up requests the home read counted, and marks the number a FLOOR
// whenever a lane cannot be checked from here. "N+" is honest -- it is the same
// vocabulary the deadline cards use for a partial page; a total that quietly
// omits a lane is not.
function billerReturnedOverviewCount(model, proposalCounts) {
  const sources = billerReturnedSources(model);
  let value = Number((proposalCounts && proposalCounts.rejected) || 0);
  let floor = false;
  if (sources.teamLines) value += billerTeamReturnedRows(model).length; else floor = true;
  // Whole claims: the exact count read (the badge's own), when it has answered.
  // Refused is an answer that carries nothing. Otherwise the board's rows, or a
  // floor - the rule this card had before that read existed.
  const exact = typeof sentBackClaimCountAnswer === "function" ? sentBackClaimCountAnswer(model) : null;
  if (exact && exact.status === "ok") value += exact.count;
  else if (exact && exact.status === "denied") value += 0;
  else if (sources.claims) value += billerBouncedClaimRows(model).length; else floor = true;
  return { value, floor };
}

// The follow-up constituent of "sent back to me", taken from the home
// summary's UNFILTERED whole-queue counts.
//
// NOT from model.proposals, which is whatever the LAST loaded tab put there:
// narrowed by the server-side ?status= filter, cut to one page, and simply
// empty on a tab whose read carries no proposals at all. Counting the rows in
// hand made this number - and the badge above it - shrink or disappear the
// moment the biller opened Assigned to me or filtered to Approved, which is
// the one thing about returned work the owner said must stay unmistakable.
//
// counts.rejected is already narrowed to non-superseded by the server
// (followUpProposals.list), so this is the same rule, just whole-queue - and it
// is the same field billerReturnedOverviewCount reads, so the Overview card
// and the tab badge now agree by construction rather than by coincidence.
function billerReturnedSummaryCounts(model) {
  const key = JSON.stringify([additionalPortalDataReadScope(), model.orgId || ""]);
  const entry = model.homeSummary && model.homeSummary.key === key ? model.homeSummary : null;
  const counts = entry && entry.proposals && entry.proposals.counts ? entry.proposals.counts : null;
  if (!counts) return null;
  const whole = (value) => { const numeric = Number(value || 0); return Number.isFinite(numeric) ? Math.max(0, Math.floor(numeric)) : null; };
  const rejected = whole(counts.rejected), pending = whole(counts.pending);
  return rejected === null || pending === null ? null : { rejected, pending };
}

function billerReturnedCount(model) {
  const sources = billerReturnedSources(model);
  const summary = billerReturnedSummaryCounts(model);
  // No summary yet is "not answered", exactly like a lane that has not come
  // back: the badge holds its last known value rather than showing a partial.
  if (!summary || !sources.teamLines || !sources.claims) return null;
  return summary.rejected + billerTeamReturnedRows(model).length + billerBouncedClaimRows(model).length;
}

// The union, in one list. The status filter is a filter on REQUESTS, so when
// the biller narrows to anything other than sent-back work the other lanes --
// which only ever contribute sent-back work -- drop out rather than ignoring it.
function billerRequestRows(model) {
  const requests = Array.isArray(model.proposals) ? model.proposals : [];
  const status = String(model.requestStatus || "");
  if (status === "pending") return [...requests, ...billerTeamPendingRows(model)];
  if (status && status !== "rejected") return requests.slice();
  if (status) return [...requests, ...billerTeamReturnedRows(model), ...billerBouncedClaimRows(model)];
  return [...requests, ...billerTeamReturnedRows(model), ...billerTeamPendingRows(model), ...billerBouncedClaimRows(model)];
}

function billerRequestState(row, resubmitted) {
  const status = String(row.status || "").toLowerCase();
  if (status === "rejected") return row.superseded ? "Replaced by your newer request" : "Sent back — needs your fix";
  if (status === "approved") return "Approved";
  if (status === "canceled") return "Withdrawn";
  if (["pending", "submitted"].includes(status)) return resubmitted ? "Resubmitted — waiting for review" : "Waiting for review";
  return oversightRequestStatusLabel(row.status);
}

// Work that still needs the biller's hands leads, oldest first so nothing ages
// quietly at the bottom; everything else follows, newest first.
function billerRequestRank(row) {
  const status = String(row.status || "").toLowerCase();
  if (status === "rejected") return row.superseded ? 3 : 0;
  if (["pending", "submitted"].includes(status)) return 1;
  return status === "approved" ? 2 : 3;
}

function billerRequestSort(rows) {
  const at = (row) => String(row.decidedAtUtc || row.createdAtUtc || "");
  return rows.slice().sort((a, b) => billerRequestRank(a) - billerRequestRank(b)
    || (billerRequestRank(a) === 0 ? at(a).localeCompare(at(b)) : at(b).localeCompare(at(a)))
    || String(a.claimId || "").localeCompare(String(b.claimId || ""))
    || String(a.serviceId || "").localeCompare(String(b.serviceId || "")));
}

function billerRequestOpenHtml(row, label) {
  return `<button type="button" class="primary-button" data-work-open data-claim="${escapeHtml(row.claimId)}" data-org="${escapeHtml(row.orgId)}" data-service="${escapeHtml(row.serviceId || "")}">${escapeHtml(label)}</button>`;
}

// The Sent-back block's aging chip. A WHOLE-CLAIM request carries no service
// line, so a feed that resolves aging per line may legitimately have nothing to
// say about it, and "Aging unavailable" printed on every claim card would be
// noise rather than the honest signal it is on a line somebody is deciding.
// Chip when the rows actually carry a readable aging; nothing when they do not.
// The reviewer's own card (renderSupervisorProposals) keeps the unconditional
// chip - that is where a decision is made and where an unknown aging must say
// so out loud.
function billerRequestAgingHtml(rows) {
  return oldestAgingDaysView(rows).known ? `<p class="work-aging-line">${agingDaysGroupChipHtml(rows)}</p>` : "";
}

// ONE CARD PER CLAIM (owner, 2026-10-15: "claim by claim not note by note" --
// "it needs to be clear and organized for them"). A claim sent back three times
// with three different notes used to read as three unrelated cards. Now the
// claim is the card; each different instruction is a numbered section inside
// it, carrying its note once and listing the lines it covers.
function billerRequestSectionKey(row) {
  return JSON.stringify([String(row.decisionReason || ""), row.action || "", row.followUpDate || "", row.decidedAtUtc || row.createdAtUtc || "", row.decidedByUserId || row.reviewerUserId || "", row.note || ""]);
}

function billerRequestSectionHtml(rows, index, count) {
  const row = rows[0];
  const status = String(row.status || "").toLowerCase();
  const returned = status === "rejected" && !row.superseded;
  const note = String(row.decisionReason || "").trim();
  const when = row.decidedAtUtc || row.createdAtUtc;
  // A claim bounce records no reviewer identity, so say nothing rather than
  // resolving "" into somebody's name.
  const who = row.decidedByUserId || row.reviewerUserId ? oversightPerson(row.decidedByUserId || row.reviewerUserId) : "";
  const decided = ["rejected", "approved"].includes(status);
  const lines = rows.length === 1 ? "1 line" : `${rows.length} lines`;
  const heading = count > 1 ? `<h4 class="oversight-claim-section-head">${returned ? "Fix" : "Request"} ${index + 1} of ${count} · ${escapeHtml(lines)}</h4>` : "";
  return `<section class="oversight-claim-section">
    ${heading}
    ${returned ? `<h3>What to fix</h3><p class="oversight-note" data-request-note>${note ? escapeHtml(note) : "The reviewer did not leave a note. Ask them what to change before you send it again."}</p>` : ""}
    ${decided ? `<p class="oversight-decision-meta">${status === "rejected" ? "Sent back" : "Approved"}${who ? ` by ${escapeHtml(who)}` : ""}${when ? ` · ${escapeHtml(formatRelativeOrDate(when))} (${escapeHtml(oversightTime(when))})` : ""}</p>` : `<p class="oversight-decision-meta">Waiting for ${who ? escapeHtml(who) : "review"}${when ? ` · sent ${escapeHtml(formatRelativeOrDate(when))} (${escapeHtml(oversightTime(when))})` : ""}</p>`}
    ${!returned && note ? `<p class="oversight-note"><b>Reviewer note:</b> ${escapeHtml(note)}</p>` : ""}
    <dl>${row.requestKind === "claim" ? `<dt>Follow-up on the claim</dt><dd>${escapeHtml(row.action || "None")}</dd>` : `<dt>You asked for</dt><dd>${escapeHtml(supervisorRequestIsRemoval(row) ? "Remove the follow-up action" : row.action)}</dd><dt>Follow-up date</dt><dd>${escapeHtml(row.followUpDate || "Cleared")}</dd><dt>Your note</dt><dd class="oversight-note">${escapeHtml(row.note || "No note.")}</dd>`}</dl>
    <ul class="oversight-returned-lines">${rows.map((entry) => `<li class="oversight-returned-line"><span>${entry.serviceId ? `Line ${escapeHtml(entry.serviceId)}` : `Claim ${escapeHtml(entry.claimId)}`}</span>${billerRequestOpenHtml(entry, entry.serviceId ? (returned ? "Open line to fix" : "Open line") : (returned ? "Open claim to fix" : "Open claim"))}</li>`).join("")}</ul>
    ${["pending", "submitted"].includes(status) && row.requestKind !== "teamLine" ? `<div class="oversight-actions">${rows.map((entry) => `<button class="ghost-button" type="button" data-proposal-withdraw="${escapeHtml(entry.proposalId)}">Withdraw${rows.length > 1 ? ` line ${escapeHtml(entry.serviceId)}` : " request"}</button>`).join("")}</div>` : ""}
  </section>`;
}

function billerRequestBlockHtml(rows, resubmittedLines) {
  const row = rows[0];
  const resubmitted = resubmittedLines.has(JSON.stringify([row.orgId, row.serviceId]));
  const sections = new Map();
  for (const entry of rows) {
    const key = billerRequestSectionKey(entry);
    if (!sections.has(key)) sections.set(key, []);
    sections.get(key).push(entry);
  }
  const groups = Array.from(sections.values());
  const lineCount = rows.filter((entry) => entry.serviceId).length;
  return `<article class="oversight-card oversight-returned-card" data-request-block="${escapeHtml(row.proposalId)}">
    <div class="oversight-card-top"><span class="oversight-badge">${escapeHtml(billerRequestState(row, resubmitted))}</span><span>${escapeHtml(billerRequestKindLabel(row))}${lineCount ? ` · ${lineCount === 1 ? "1 service line" : lineCount + " service lines"}` : ""}</span></div>
    <div class="oversight-claim-title-row"><h3 class="oversight-claim-title">Claim ${escapeHtml(row.claimId)}</h3>${typeof canAccessView === "function" && canAccessView("workbench") ? `<button type="button" class="ghost-button" data-request-all-data="${escapeHtml(row.claimId)}">Open claim in All Data</button>` : ""}</div>
    ${billerRequestAgingHtml(rows)}
    ${groups.map((group, index) => billerRequestSectionHtml(group, index, groups.length)).join("")}
  </article>`;
}

function renderBillerRequests(model) {
  const all = billerRequestRows(model);
  const sorted = billerRequestSort(all);
  // The server page describes the follow-up REQUEST read alone. The moment work
  // from another review lane is on screen those page numbers would be counting a
  // different list than the one being shown, so the view windows the union
  // itself and says plainly when the request read had more to give.
  const extra = all.length - (Array.isArray(model.proposals) ? model.proposals.length : 0);
  const serverPage = extra ? null : model.pagination;
  const page = serverPage
    ? { key: "oversight", page: Number(serverPage.number), pages: Number(serverPage.totalPages), total: Number(serverPage.totalRows), first: Number(serverPage.from), last: Number(serverPage.to), rows: sorted }
    : managementListPageWindow("oversight", sorted, JSON.stringify([model.tab, model.orgId, model.requestStatus || ""]));
  const notice = model.reconcileError ? `<p role="status">${escapeHtml(model.reconcileError)}</p>` : "";
  if (!page.rows.length) {
    return `${notice}<div class="oversight-empty"><h3 tabindex="-1" data-requests-empty>Nothing is waiting for you</h3><p>${model.requestStatus === "rejected"
      ? "No follow-up action has been sent back to you. When a supervisor returns one, it appears here with what to fix."
      : "Follow-ups you send for approval appear here with the reviewer’s answer."}</p></div>`;
  }
  const labels = clientActionOrgLabels();
  // A pending request on a line that also carries an answered send-back is a
  // reply, and says so. Derived from the rows on screen, never guessed.
  const resubmittedLines = new Set(all
    .filter((row) => row.status === "rejected" && row.superseded)
    // JSON.stringify rather than joining on a separator byte: it is the same
    // composite-key form supervisorProposalKey and workflowTargetKey already
    // use, and it cannot be defeated by a value that happens to contain the
    // separator. (The previous U+001F join was not ambiguous either.)
    .map((row) => JSON.stringify([row.orgId, row.serviceId])));
  const companies = new Map();
  for (const row of page.rows) {
    const company = String(row.orgId || "");
    if (!companies.has(company)) companies.set(company, new Map());
    const blocks = companies.get(company);
    const key = JSON.stringify([row.claimId, row.requestKind === "claim", row.status, Boolean(row.superseded), resubmittedLines.has(JSON.stringify([row.orgId, row.serviceId]))]);
    if (!blocks.has(key)) blocks.set(key, []);
    blocks.get(key).push(row);
  }
  const needFix = page.rows.filter((row) => row.status === "rejected" && !row.superseded).length;
  const sources = billerReturnedSources(model);
  const morePages = model.pagination && Number(model.pagination.totalPages || 1) > 1;
  // Two different situations, two different sentences. The rework read is one
  // this view asks for, so not having it yet is a WAIT. The claim list is one
  // this view deliberately never fetches -- opening My Work must not start the
  // assignment board's query -- so not having it is a PLACE TO GO, and saying
  // "loading" about something nobody is loading would be a lie that never
  // resolves.
  const waiting = sources.teamLines ? ""
    : sources.teamLinesFailed
      ? `<p role="status">Work sent back to you through team approvals could not be checked. Refresh to try again.</p>`
      : `<p role="status">Still checking work sent back to you through team approvals.</p>`;
  const elsewhere = !sources.claims ? `<p role="status">Whole claims sent back for review are listed once Claim assignments has loaded them.</p>` : "";
  const incomplete = waiting || elsewhere
    ? `${waiting}${elsewhere}`
    : (morePages ? `<p role="status">Showing the newest requests. Filter by company to see the rest.</p>` : "");
  return `${notice}${incomplete}<p class="oversight-count">${page.total} ${page.total === 1 ? "item" : "items"}${needFix ? ` \u00b7 ${needFix} sent back for you to fix` : ""}</p>${managementListPagerHtml(page, "My Work")}
    ${Array.from(companies.entries()).map(([company, blocks]) => {
      const lines = Array.from(blocks.values()).reduce((total, rows) => total + rows.length, 0);
      return `<section class="oversight-returned-company" aria-label="${escapeHtml(oversightCompany(company, labels))}">
        <h3 class="oversight-returned-head"><span>${escapeHtml(oversightCompany(company, labels))}</span><span class="oversight-returned-tally">${lines === 1 ? "1 service line" : escapeHtml(lines + " service lines")}</span></h3>
        <div class="oversight-cards">${Array.from(blocks.values()).map((rows) => billerRequestBlockHtml(rows, resubmittedLines)).join("")}</div></section>`;
    }).join("")}`;
}

function renderPersonalFollowUpReviews(body) {
  const rows = Array.isArray(body.rows) ? body.rows : [];
  const labels = { pending: "Waiting for review", rejected: "Sent back", approved: "Approved" };
  const page = managementListPageWindow("personalFollowups", rows.slice().sort((a, b) => String(b.setAt).localeCompare(String(a.setAt))), approvalQueueRequestKey("mine"));
  const partial = body.complete !== true || body.truncated === true;
  return '<section aria-label="My action reviews"><h3>Action reviews</h3><p>Follow-up actions submitted through the action-review workflow. Supervisor requests are listed above.</p>' + (partial ? '<p role="status">This list is incomplete. Filter by company or refresh before relying on the totals.</p>' : '') + managementListPagerHtml(page, "Action reviews") + '<div class="oversight-cards">' + (page.rows.map(row => '<article class="oversight-card"><div class="oversight-card-top"><span class="oversight-badge">' + escapeHtml(labels[row.reviewStatus] || "Review status unavailable") + '</span><span>' + escapeHtml(oversightCompany(row.orgId)) + '</span></div><h3>' + escapeHtml(approvalQueueActionLabel(row)) + '</h3><p>' + workClaimLinkHtml(row) + ' · Line ' + escapeHtml(row.serviceId) + '</p><p>Follow-up: ' + escapeHtml(row.followUpDate || "Not set") + '</p><p>Submitted ' + escapeHtml(oversightTime(row.setAt)) + '</p>' + (row.reviewedAt ? '<p>Reviewed ' + escapeHtml(oversightTime(row.reviewedAt)) + '</p>' : '') + (row.rejectReason ? '<p class="oversight-note"><b>Reviewer feedback:</b> ' + escapeHtml(row.rejectReason) + '</p>' : '') + (row.suggestedAction ? '<p>Suggested action: ' + escapeHtml(row.suggestedAction) + '</p>' : '') + '<div class="oversight-actions">' + workClaimLinkHtml(row, "Open claim") + '</div></article>').join('') || '<p>No action reviews match this view.</p>') + '</div></section>';
}

function oversightWorkTabs() {
  return [["overview", "Overview"], ["existing", "Claim assignments"], ["tasks", "Assigned to me"], ["internalReview", "Internal review required"], ["requests", "My requests"], ...(["SuperAdmin", "StatesideBiller"].includes(canonicalRole(state.role)) && hasPermission(PERMISSIONS.APPROVE_REVIEW) ? [["approvals", "Needs my approval"]] : []), ...(hasPermission(PERMISSIONS.ASSIGN_CLAIMS) ? [["sent", "Sent by me"]] : [])];
}

function bindOversightWorkTabs(host) {
  host.querySelectorAll("[data-work-tab]").forEach((button) => button.addEventListener("click", () => {
    const model = oversightState();
    oversightWorkReadController?.abort();
    model.tab = button.dataset.workTab;
    // Deliberately NOT discarding model.homeSummary here: the tab strip's
    // outstanding counts read that cache, so dropping it on every return to
    // Overview blanked five badges and re-issued five reads for the same
    // numbers. Refresh invalidates it, and so does every write below that
    // changes one of these queues.
    model.page = 1; model.pagination = null;
    model.internalReviewPage = 1;
    model.request += 1;
    model.status = "idle";
    renderAssignments();
  }));
}

function renderOversightWork() {
  if (typeof deferTrackerPublicationRender === "function" && deferTrackerPublicationRender("assignments")) return;
  const host = document.getElementById("offshoreQueue");
  if (!host) return;
  document.querySelector(".assignment-rules-card")?.setAttribute("hidden", "");
  if (isExternalClientRole(state.role) || !hasPermission(PERMISSIONS.VIEW_ASSIGNMENTS)) { host.innerHTML = ""; return; }
  const model = oversightState();
  if (model.tab === "existing") return renderAssignments();
  if (model.tab === "overview") return renderBillerWorkOverview(host);
  ensureOversightTabCounts();
  const scope = currentAccessProfile().scope || {};
  const companyLabels = clientActionOrgLabels();
  const orgChoices = (scope.allOrgs ? [...companyLabels.keys()] : normalizeUserScopeList(scope.orgIds))
    .sort((a, b) => compareText(oversightCompany(a, companyLabels), oversightCompany(b, companyLabels)));
  const companyPicker = activeClientGroupTenant() ? `<label class="oversight-company">Company <select data-work-company disabled><option value="${escapeHtml(model.orgId || "")}">${escapeHtml(activeClientGroupLabel())}</option></select></label>` : `<label class="oversight-company">Company <select data-work-company><option value="">All my companies</option>${orgChoices.map((id) => `<option value="${escapeHtml(id)}"${model.orgId === id ? " selected" : ""}>${escapeHtml(oversightCompany(id, companyLabels))}</option>`).join("")}</select></label>`;
  const tabs = oversightWorkTabsWithCounts();
  if (!tabs.some(([key]) => key === model.tab)) model.tab = "tasks";
  host.innerHTML = `<section class="panel oversight-work"><div class="oversight-heading"><div><h2>My Work</h2><p>Know what needs your attention and who is waiting for you.</p></div><button class="ghost-button" type="button" data-work-refresh>Refresh</button></div>${oversightTabs(tabs, model.tab, "data-work-tab")}${companyPicker}${model.tab === "requests" ? billerRequestStatusFilter(model) + '<p class="oversight-hint">Review approval confirms a follow-up change. It does not mean the follow-up work is finished. Open the claim to check its latest action and date.</p>' : ""}<div class="oversight-results" aria-live="polite">${oversightResultsHtml(model)}</div></section>`;
  bindOversightWorkTabs(host);
  host.querySelector("[data-work-request-status]")?.addEventListener("change", (event) => { model.requestStatus = event.target.value; model.page = 1; model.pagination = null; model.status = "idle"; invalidateApprovalQueue(); renderOversightWork(); });
  host.querySelector("[data-work-company]")?.addEventListener("change", (event) => { model.orgId = event.target.value; model.page = 1; model.pagination = null; model.internalReviewPage = 1; model.status = "idle"; renderOversightWork(); });
  host.querySelector("[data-work-refresh]")?.addEventListener("click", () => { model.status = "idle"; model.archiveProposals = null; invalidateApprovalQueue(); invalidateMyWorkCounts(model); renderOversightWork(); });
  bindOversightWorkResults(host, model);
  if (model.status === "idle") loadOversightWork();
}

// ONE gate for the queue body, shared by the full paint and the in-place swap.
//
// Needs my approval is the case the tab-wide gate could not express: it draws
// on TWO reads, so "the supervisor-request read has not answered yet" must not
// blank items the other read already has in hand -- that is how an empty state
// ended up above a full list. The unified renderer is handed both states and
// says out loud which part is still coming.
function oversightResultsHtml(model) {
  if (model.tab === "approvals") return renderOversightRows(model);
  return model.status === "error" ? `<p role="alert">${escapeHtml(model.error)}</p>`
    : model.status !== "ready" ? `<p>Loading your work…</p>` : renderOversightRows(model);
}

// Everything bound INSIDE .oversight-results, so the queue body can be rebuilt
// without touching the tab strip, the company picker or Refresh.
function bindOversightWorkResults(root, model) {
  if (model.pagination) root.querySelectorAll('[data-management-list="oversight"]').forEach(button => button.addEventListener("click", () => {
    model.page = Number(button.dataset.managementPage); model.status = "idle"; renderOversightWork();
  }));
  else bindManagementListPager(root, "oversight", renderOversightWork);
  root.querySelector("[data-work-finished]")?.addEventListener("change", (event) => { model.finished = event.target.checked; model.page = 1; model.pagination = null; model.status = "idle"; renderOversightWork(); });
  // Needs my approval: one card per identical change, both approval routes in
  // one list. Each line is still decided through its own route, endpoint,
  // permission check and refusal handling - see decideApprovalChangeCard.
  if (model.tab === "approvals") bindUnifiedApprovals(root, model);
  if (model.tab === "approvals") bindApprovalArchive(root, model);
  bindWorkClaimLinks(root);
  // The quickest place to fix every line of a sent-back claim is All Data,
  // narrowed to that claim (owner, 2026-10-15).
  root.querySelectorAll("[data-request-all-data]").forEach((button) => button.addEventListener("click", () => reportDrilldownGoToClaim(button.dataset.requestAllData)));
  root.querySelectorAll("[data-work-status]").forEach((button) => button.addEventListener("click", () => updateOversightTask(button)));
  root.querySelectorAll("[data-proposal-decision]").forEach((button) => button.addEventListener("click", () => decideOversightProposal(button.dataset.id, button.dataset.proposalDecision)));
  root.querySelectorAll("[data-proposal-withdraw]").forEach((button) => button.addEventListener("click", () => withdrawOversightProposal(button.dataset.proposalWithdraw)));
  root.querySelectorAll("[data-internal-review-page]").forEach((button) => button.addEventListener("click", () => { model.internalReviewPage = Number(button.dataset.internalReviewPage); model.status = "idle"; renderOversightWork(); }));
}

// Swap ONLY the queue body. A decision used to re-run the whole panel through
// innerHTML, which flashed "Loading your work..." over the list, threw away the
// tab strip and picker DOM, and dropped the reviewer back to the top of the page.
function renderOversightWorkResults() {
  const model = oversightState();
  const host = document.getElementById("offshoreQueue");
  const results = host ? host.querySelector(".oversight-results") : null;
  if (!results || state.view !== "assignments") return;
  const scrollY = window.scrollY;
  // A quiet server reconcile replaces these nodes too. Preserve keyboard
  // ownership only when focus was inside this approval list; a company picker
  // or dialog outside it keeps its focus. If the card retired, keep its slot.
  const focusedCard = results.contains?.(document.activeElement)
    ? document.activeElement?.closest?.("[data-change-card]") : null;
  const focusedIndex = focusedCard ? Array.from(results.querySelectorAll("[data-change-card]")).indexOf(focusedCard) : -1;
  const focusedKey = focusedCard?.dataset?.changeCard || "";
  results.innerHTML = oversightResultsHtml(model);
  bindOversightWorkResults(results, model);
  if (focusedKey) {
    const replacement = Array.from(results.querySelectorAll("[data-change-card]")).find(card => card.dataset.changeCard === focusedKey);
    if (replacement) approvalChangeActivate(replacement, { scroll: false });
    else focusApprovalChangeCard(focusedIndex);
  }
  if (window.scrollY !== scrollY) window.scrollTo(0, scrollY);
}

async function loadOversightWork(options = {}) {
  const model = oversightState();
  if (model.tab === "internalReview") return loadInternalReviewWork();
  const request = ++model.request;
  const readScope = additionalPortalDataReadScope();
  const selection = JSON.stringify([model.tab, model.orgId || "", Boolean(model.finished), model.page || 1, model.requestStatus || ""]);
  const current = () => readScope === additionalPortalDataReadScope() && oversightState() === model && request === model.request
    && selection === JSON.stringify([model.tab, model.orgId || "", Boolean(model.finished), model.page || 1, model.requestStatus || ""]);
  oversightWorkReadController?.abort();
  const controller = new AbortController(); oversightWorkReadController = controller;
  const timeout = setTimeout(() => controller.abort(), 10000);
  // A quiet reload keeps the rows on screen while it checks them against the
  // server, so reconciling a decision never blanks the queue.
  const quiet = options.quiet === true && model.status === "ready";
  if (!quiet) model.status = "loading";
  try {
    const proposals = ["requests", "approvals"].includes(model.tab);
    const params = new URLSearchParams({ page: String(model.page || 1), pageSize: "50" });
    if (model.orgId) params.set("orgId", model.orgId);
    if (proposals) { if (model.tab === "requests" && model.requestStatus) params.set("status", model.requestStatus); params.set("mode", model.tab === "approvals" ? "review" : "mine"); if (model.tab === "approvals") params.set("groupBy", "claim"); }
    else { params.set("view", model.tab === "sent" ? "sent" : "mine"); params.set("includeFinished", String(Boolean(model.finished))); }
    const data = await oversightRequest((proposals ? "/api/workflow/follow-up-proposals?" : "/api/directed-work?") + params, undefined, "GET", { signal: controller.signal });
    if (!current()) return;
    if (controller.signal.aborted) throw Object.assign(new Error("Your work could not be loaded in time. Refresh to try again."), { name: "AbortError" });
    model.truncated = data.truncated === true;
    model.items = Array.isArray(data.items) ? data.items : [];
    model.proposals = Array.isArray(data.proposals) ? data.proposals : [];
    model.pagination = data.page && Number.isFinite(Number(data.page.totalRows)) ? data.page : null;
    if (model.pagination) model.page = Number(model.pagination.number) || 1;
    model.counts = data.counts || {};
    model.reconcileError = "";
    model.status = "ready";
  } catch (error) { if (!current()) return; const message = error.name === "AbortError" ? "Your work could not be loaded in time. Refresh to try again." : error.message;
    // A background check that fails must not delete work the reviewer can see.
    if (quiet) model.reconcileError = message; else { model.status = "error"; model.error = message; } }
  finally { clearTimeout(timeout); if (oversightWorkReadController === controller) oversightWorkReadController = null; }
  if (state.view !== "assignments") return;
  if (quiet) renderOversightWorkResults(); else renderOversightWork();
}

function renderOversightRows(model) {
  if (model.tab === "internalReview") return renderInternalReviewWork(model);
  if (model.tab === "approvals") return renderSupervisorProposals(model);
  if (model.tab === "requests") return renderBillerRequests(model);
  const proposals = ["requests", "approvals"].includes(model.tab);
  const allRows = (proposals ? model.proposals : model.items).slice();
  const visible = proposals ? allRows : allRows.filter((row) => Boolean(model.finished) || !["completed", "canceled"].includes(row.status));
  const taskFilter = proposals ? "" : `<label class="oversight-finished"><input type="checkbox" data-work-finished${model.finished ? " checked" : ""}> Show finished tasks</label>`;
  // Tasks sort by deadline (overdue first) with priority as the tiebreak;
  // requests sort by what needs the biller's hands: sent back, then waiting.
  const rows = proposals ? oversightRequestSort(visible) : oversightTaskSort(visible);
  const serverPage = model.pagination;
  const page = serverPage ? { key: "oversight", page: Number(serverPage.number), pages: Number(serverPage.totalPages), total: Number(serverPage.totalRows), first: Number(serverPage.from), last: Number(serverPage.to), rows }
    : managementListPageWindow("oversight", rows, JSON.stringify([model.tab, model.orgId, Boolean(model.finished)]));
  if (!rows.length) return `${taskFilter}${proposals ? `<div class="oversight-empty"><h3>${model.tab === "approvals" ? "No pending action-change requests" : "No work here yet"}</h3><p>${model.tab === "approvals" ? "Proposed action changes will appear here when someone needs your approval." : "Follow-ups you submit for approval will appear here with their latest decision."}</p></div>` : oversightEmptyStateHtml()}`;
  return `${taskFilter}${model.truncated ? `<p role="status">Showing the newest available items. Use a company filter to narrow your queue.</p>` : ""}<p class="oversight-count">${page.total} ${proposals ? (page.total === 1 ? "supervisor request" : "supervisor requests") : (page.total === 1 ? "task" : "tasks")}</p>${managementListPagerHtml(page, "My Work")}${proposals ? `<div class="oversight-cards">${page.rows.map((row) => {
    const pending = ["pending", "submitted"].includes(row.status);
    return `<article class="oversight-card"><div class="oversight-card-top"><span class="oversight-badge">${escapeHtml(row.superseded ? "Superseded by a newer request" : oversightRequestStatusLabel(row.status))}</span><span>${escapeHtml(oversightCompany(row.orgId))}</span></div><h3>${escapeHtml(supervisorRequestHeadline(row))}</h3><p>${workClaimLinkHtml(row)}${row.serviceId ? ` · Line ${escapeHtml(row.serviceId)}` : ""}</p>${claimPatientLineHtml(row)}<p>Requested by ${escapeHtml(oversightPerson(row.requesterUserId))} · Reviewer: ${escapeHtml(oversightPerson(row.reviewerUserId))}</p><p class="work-aging-line">${agingDaysChipHtml(agingDaysView(row), "Aging")}</p><dl><dt>Follow-up date</dt><dd>${escapeHtml(row.followUpDate || "Cleared")}</dd><dt>Proposed note</dt><dd class="oversight-note">${escapeHtml(row.note || "")}</dd></dl>${pending ? `<p class="oversight-hint">Waiting for approval. The current follow-up has not changed.</p>` : ""}${row.decisionReason ? `<p class="oversight-note"><b>${row.superseded ? "Replacement:" : "Reviewer feedback:"}</b> ${escapeHtml(row.decisionReason)}</p><p class="oversight-decision-meta">${row.superseded ? "Replaced by" : "Reviewed by"} ${escapeHtml(oversightPerson(row.decidedByUserId || row.reviewerUserId))}${row.decidedAtUtc ? ` \u00b7 ${escapeHtml(oversightTime(row.decidedAtUtc))}` : ""}</p>` : ""}<div class="oversight-actions">${workClaimLinkHtml(row, "Open claim")}${model.tab === "approvals" && pending ? `<button class="primary-button" type="button" data-proposal-decision="approved" data-id="${escapeHtml(row.proposalId)}">Approve & apply</button><button class="ghost-button" type="button" data-proposal-decision="rejected" data-id="${escapeHtml(row.proposalId)}">Send back</button>` : ""}${model.tab === "requests" && pending ? `<button class="ghost-button" type="button" data-proposal-withdraw="${escapeHtml(row.proposalId)}">Withdraw request</button>` : ""}</div></article>`;
  }).join("")}</div>` : oversightTaskTableHtml(page.rows, model.tab === "sent")}`;
}


function oversightDialog(title, content, onReady) {
  document.getElementById("oversightDialog")?.remove();
  const dialog = document.createElement("dialog");
  dialog.id = "oversightDialog";
  dialog.className = "oversight-dialog";
  dialog.innerHTML = `<div class="oversight-heading"><h2 id="oversightDialogTitle">${escapeHtml(title)}</h2><button type="button" class="icon-button" aria-label="Close" data-oversight-close>×</button></div>${content}<p data-oversight-error role="alert"></p>`;
  dialog.setAttribute("aria-labelledby", "oversightDialogTitle");
  document.body.appendChild(dialog);
  dialog.querySelector("[data-oversight-close]").onclick = () => dialog.close();
  dialog.addEventListener("close", () => dialog.remove());
  dialog.showModal();
  if (onReady) onReady(dialog);
  return dialog;
}

async function updateOversightTask(button) {
  const model = oversightState();
  const row = (model.items || []).find((item) => item.id === button.dataset.id)
    || (model.homeSummary?.tasks?.items || []).find((item) => item.id === button.dataset.id);
  if (!row) return;
  button.disabled = true;
  try {
    await oversightRequest(`/api/directed-work/${encodeURIComponent(row.id)}`, { orgId: row.orgId, status: button.dataset.workStatus, version: row.version, operationId: oversightId() }, "PATCH");
    invalidateMyWorkCounts(); oversightState().status = "idle"; renderOversightWork();
  } catch (error) { button.disabled = false; showToast(error.message); }
}

function supervisorProposalSelection(model) {
  if (!(model.approvalSelection instanceof Set)) model.approvalSelection = new Set();
  const live = new Set(model.proposals.filter((row) => row.status === "pending").map((row) => row.proposalId));
  for (const id of model.approvalSelection) if (!live.has(id)) model.approvalSelection.delete(id);
  return model.approvalSelection;
}

// Decisions in flight, keyed by proposalId. A Set, not one page-wide boolean:
// a single slow decision used to disable every control in the queue, so a
// reviewer waiting on one approval could not send a different claim back at
// all -- which is what "it is not letting me reject" actually was.
function supervisorDecisionBusy(model) {
  if (!(model.approvalBusyIds instanceof Set)) model.approvalBusyIds = new Set();
  return model.approvalBusyIds;
}

// Per-line refusals, kept beside the card they belong to. A failed decision
// leaves its line in the queue and says why; it is never quietly dropped.
function supervisorDecisionErrors(model) {
  if (!model.approvalErrors || typeof model.approvalErrors !== "object") model.approvalErrors = {};
  return model.approvalErrors;
}

// The one place My Work's counters are recomputed. Exposed on purpose: the tab
// badges call this rather than keeping arithmetic of their own.
//
// counts.pending is the number of service lines waiting on this person for the
// tab they are on -- the review queue on Needs my approval, their own requests
// on My requests -- and renderSupervisorProposals prints it, so it is a number
// someone can see rather than a field nobody reads. It is only recounted from
// the rows in hand when those rows ARE the whole queue; across pages the
// server's total is kept and decremented per decided line.
function refreshMyWorkCounts(model = oversightState()) {
  const counts = { ...(model.counts || {}) };
  if (!model.pagination || Number(model.pagination.totalPages || 1) <= 1) {
    for (const key of ["pending", "approved", "rejected", "canceled"]) counts[key] = 0;
    for (const row of model.proposals || []) {
      if (!Object.prototype.hasOwnProperty.call(counts, row.status)) continue;
      if (row.status === "rejected" && row.superseded) continue;
      counts[row.status] += 1;
    }
  }
  // THE NUMBER BEHIND "Sent back to me". It counts every lane a biller can be
  // returned work from, and it is null -- not a smaller number -- whenever any
  // of those reads has not answered. Renderers print nothing for null.
  counts.returnedToMe = billerReturnedCount(model);
  model.counts = counts;
  // The cached home summary is NOT dropped here. invalidateMyWorkCounts owns
  // that, and every caller of this function calls it too - see
  // decideApprovalChangeCard. This function's one job is the printed
  // per-tab total above.
  return model.counts;
}

// Why this decision cannot be sent right now, in the reviewer's words, or ""
// when it can. Checked BEFORE the send-back dialog closes, so typed guidance
// is never thrown away by a silent early return.
// A request's identity, tenant-safe: the proposal id, qualified by the company
// and service line it belongs to. Claim ids are not unique across companies.
function supervisorProposalKey(row) {
  return JSON.stringify([String((row && row.orgId) || ""), String((row && row.serviceId) || ""), String((row && row.proposalId) || "")]);
}

// Resolve the rows a control was built from against the CURRENT queue.
// Holding object references across an await is what turned the send-back
// dialog into a dead end: the background reconcile replaces model.proposals
// wholesale, so every held reference stops being found by identity while
// still naming a live request, and the dialog refused forever.
function supervisorLiveRows(model, rows) {
  const wanted = new Set((rows || []).filter(Boolean).map(supervisorProposalKey));
  return (model.proposals || []).filter((row) => wanted.has(supervisorProposalKey(row)));
}

// How a refused decision is counted and described. Separated out so it can be
// proved on its own, because the one thing this must never do is tell a
// reviewer a line was approved when the server did not apply it.
//
// `phrase` is the same answer as the tail of "claim 3432 ...", for the one
// sentence a change-card click reports across all of its lines.
function supervisorDecisionOutcome(error) {
  const code = error && error.code;
  // Genuinely no longer pending -- another reviewer decided it, or the biller
  // withdrew it. It leaves the queue, but it was not THIS reviewer's decision
  // and must never be counted as one.
  if (code === "proposal_already_decided") return { outcome: "retired", message: "This request had already been reviewed or withdrawn.", phrase: "had already been reviewed or withdrawn" };
  // A 404 does NOT mean decided. The server answers it for a still-pending row
  // that is no longer ours to decide -- the reviewer was reassigned, or the
  // company left our scope. Keep the line and say so; the reconcile read
  // decides whether it really leaves.
  if (code === "proposal_not_found") return { outcome: "missed", message: "This request is no longer assigned to you, or is no longer available. Refresh to see the current queue.", phrase: "is no longer assigned to you - refresh to see the current queue" };
  // The line moved on after the request was made. A refusal, never an approval.
  if (code === "follow_up_stale") return { outcome: "missed", message: "This line changed since it was sent. Refresh and look at it again before deciding.", phrase: "changed since it was sent" };
  if (code === "self_review_forbidden") return { outcome: "missed", message: "You asked for this change yourself, so another supervisor must review it.", phrase: "was requested by you, so another supervisor must review it" };
  if (error && error.name === "AbortError") return { outcome: "missed", message: "This decision took too long to answer. It may still have been saved — refresh to check.", phrase: "took too long to answer - it may still have been saved, refresh to check" };
  const message = String((error && error.message) || "").trim();
  if (!message || /failed to fetch|networkerror|network request failed|load failed/i.test(message)) return { outcome: "missed", message: "This decision could not be sent. Check your connection, then try again.", phrase: "could not be sent - check your connection, then try again" };
  return { outcome: "missed", message, phrase: `could not be saved: ${message.replace(/[.\s]+$/, "")}` };
}

function supervisorDecisionBlockedReason(model, rows, decision, reason = "") {
  if (!hasPermission(PERMISSIONS.APPROVE_REVIEW)) return "Your access does not include approving follow-up requests.";
  if (model.tab !== "approvals") return "Open Needs my approval before deciding.";
  if (model.truncated === true) return "This queue is incomplete. Filter by company before deciding whole claims.";
  const list = supervisorLiveRows(model, rows).filter((row) => row.status === "pending");
  if (!list.length) return "These lines are no longer waiting for your review. Refresh to see the current queue.";
  if (list.some((row) => supervisorDecisionBusy(model).has(row.proposalId))) return "This decision is already on its way.";
  if (decision === "rejected" && !String(reason || "").trim()) return "Add the guidance the biller needs before sending this back.";
  return "";
}

// ONE test for "is this a request to REMOVE the action", used by both review
// lanes. There were three: an exact untrimmed `row.action === "None"` on the
// submitter's card, a falsy check on the Approval Queue's, and a trimmed
// case-insensitive one here -- so the same request could read as a removal on
// one screen and as "(no action)" on another.
function followUpActionIsRemoval(action) {
  const value = String(action == null ? "" : action).trim();
  return !value || value.toLowerCase() === "none";
}

// What the request would take away, for the person deciding it. Empty when
// the server has not resolved the line's current state, and never invented.
function followUpRemovalTarget(currentAction, currentDate) {
  const action = String(currentAction == null ? "" : currentAction).trim();
  const date = String(currentDate == null ? "" : currentDate).trim();
  if (!action) return "";
  return date ? `Removes ${action}, due ${date}` : `Removes ${action}`;
}

// A request to REMOVE a follow-up action travels as the clear sentinel "None",
// which on a card reads as "no action was requested" rather than "take the
// action that is there away". The reviewer is deciding on a removal, so the
// card says removal, in words -- the same way the submitter's own card does.
function supervisorRequestIsRemoval(row) {
  return followUpActionIsRemoval(row && row.action);
}

function supervisorRequestHeadline(row) {
  return supervisorRequestIsRemoval(row) ? "Remove the follow-up action" : String((row && row.action) || "");
}

// Approving a change with no date blanks the date as well, so "Due Cleared"
// was saying the same thing about two different outcomes.
function supervisorRequestEffect(row) {
  if (supervisorRequestIsRemoval(row)) {
    return followUpRemovalTarget(row && row.currentAction, row && row.currentFollowUpDate)
      || "Removes the follow-up action and its date";
  }
  const due = String((row && row.followUpDate) || "").trim();
  return due ? `Due ${due}` : "No follow-up date";
}

// ---------------------------------------------------------------------------
// NEEDS MY APPROVAL -- ONE QUEUE.
//
// A reviewer is asked to decide the SAME kind of change through two different
// routes. A company that routes follow-up changes to a named supervisor raises
// a per-line REQUEST; the granular follow-up policy raises its own proposal on
// the service line itself, which is what the standard offshore account gets.
// Both land on this tab and both are this person's to decide.
//
// They used to render as two independent sections, each with its own total and
// its own empty state, neither aware of the other. A company with nothing on
// the first route and seven items on the second therefore printed "No pending
// action-change requests" directly above seven cards awaiting approval. That is
// the defect the owner reported on 2026-09-18, and it was not a wording problem:
// two lists that cannot see each other cannot agree about anything.
//
// SO THE LIST IS UNIFIED AND NOTHING ELSE IS. Every item keeps its own decision
// path, endpoint, permission check, tenant scoping, idempotency key and refusal
// handling. What changes is that they are counted once, grouped once, read in
// one vocabulary and decided from one set of controls. The routes are an
// implementation detail and are NEVER named on screen -- a reviewer should not
// have to learn how an item reached them in order to decide it.
//
// A number is printed only when BOTH routes have answered. A route that is
// still loading makes the total unknown rather than smaller (ENGINEERING_TRAPS
// #10 -- absence is not zero), and the empty state appears only when both have
// answered and both are empty.
// ---------------------------------------------------------------------------

// The service-line route's cached read, but only when it describes the company
// and access the reviewer is looking at NOW. A slice fetched for a DIFFERENT
// company is not an answer for this one, and counting it would put another
// tenant's total on this screen for a frame.
function unifiedApprovalSlice() {
  const store = typeof approvalQueueState === "object" && approvalQueueState ? approvalQueueState : null;
  const slice = store ? store.pending : null;
  if (!slice) return null;
  const key = typeof approvalQueueRequestKey === "function" ? approvalQueueRequestKey("pending") : "";
  return !key || !slice.key || slice.key === key ? slice : null;
}

// Whether each route could be read at all. Three answers, not two: "denied" is
// an ANSWER -- the server says this reviewer has no service-line queue, so that
// route carries nothing and the total is still knowable. An ERROR is unknown
// and stays unknown until someone retries, so it is reported separately rather
// than quietly counted as none.
function unifiedApprovalSources(model) {
  const slice = unifiedApprovalSlice();
  const store = typeof approvalQueueState === "object" && approvalQueueState ? approvalQueueState : null;
  const status = slice ? String(slice.status || "") : "";
  const key = typeof approvalQueueRequestKey === "function" ? approvalQueueRequestKey("pending") : "";
  // NOTHING MAY FLASH (owner ruling 2026-09-17, the same rule the tab badges
  // follow). Every decision re-reads this queue, and the re-read starts by
  // emptying the cache - so without this the reviewer's list blanked to
  // "still checking" after every single approval, which is exactly the freeze
  // the per-line queue was fixed for. The last ANSWER is kept and kept showing
  // while the replacement is in flight.
  //
  // It is keyed, so a company switch discards it rather than showing another
  // tenant's rows for a frame, and a route that FAILED or was REFUSED drops it:
  // those are answers, and holding rows against them would be a lie that never
  // resolves.
  if (store) {
    const held = store.lastPendingAnswer;
    if (status === "ok" && slice.body) store.lastPendingAnswer = { key, body: slice.body };
    else if (status === "denied" || status === "error" || (held && held.key !== key)) store.lastPendingAnswer = null;
  }
  const revalidating = (status === "idle" || status === "pending") && store && store.lastPendingAnswer && store.lastPendingAnswer.key === key
    ? store.lastPendingAnswer.body : null;
  const body = status === "ok" && slice.body ? slice.body : revalidating;
  return {
    requests: model.status === "ready",
    requestsFailed: model.status === "error",
    team: status === "ok" || status === "denied" || Boolean(revalidating),
    teamFailed: status === "error",
    teamDenied: status === "denied",
    teamBody: body,
    teamRows: body && Array.isArray(body.rows) ? body.rows : []
  };
}

// ---------------------------------------------------------------------------
// ONE CARD PER IDENTICAL CHANGE (owner, 2026-09-23).
//
// "It looks very clunky and it's so hard to approve things. What am I
// approving? It's not clear. If a biller selected 10 claim IDs and sent them to
// me with the exact same issue, why do I have to approve them separately? I
// want to approve it all at the same time."
//
// The list used to be Company -> Payer -> one card PER CLAIM, so ten claims a
// biller sent in one go arrived as ten cards to read and ten clicks to make.
// The unit a reviewer decides is not a claim. It is ONE CHANGE: this person
// wants this company's lines moved from this action to that one, due this day,
// for this reason. So that is the card.
//
// THE KEY (approvalChangeKey) is exactly that sentence: company, requester,
// from-action, to-action, follow-up date, the request's own reason, and the
// review status. Two lines share a card only when every one of those agrees;
// anything else is a different decision and gets its own card. Both approval
// routes are read into the SAME item shape and grouped by the SAME key, so a
// supervisor request and a service-line review for the same change are one
// card, and each line is still decided through its own route.
//
// The service-line route's read ships `actionGroups` (Action x counterparty,
// with a spot-check line and a money-out flag). The card is deliberately NOT
// that group: an action-by-payer group spans requesters and splits one
// request across payers, which is the fragmentation the owner is describing.
// The groups are consumed for what they add to a change card - the rows, the
// server's spot-check pick, money-out, and the group id each decision reports
// back as telemetry - and the card keeps the owner's key.
//
// WHAT IS NOT CHANGED: every guard the two routes had. Self-review is decided
// by the server (canReview on the read, refusal on the write); a row the
// reviewer may not decide stays on the card, disabled, with the reason. A
// capped or uncertified read, or an incomplete supervisor-request page, keeps
// the whole card's decision off with the reason printed. A stale version is a
// refusal and is reported as one, by claim. Company scope is enforced by both
// routes on the server exactly as before.
// ---------------------------------------------------------------------------

// The counterparty a follow-up addresses, in the label the queue read already
// prints. A supervisor request carries no payer, so it files under "(No payer)".
function unifiedApprovalPayerLabel(row) {
  const primary = String((row && row.primaryPayer) || "").trim();
  const secondary = String((row && row.secondaryPayer) || "").trim();
  if (primary && secondary) return primary + " → " + secondary;
  return primary || secondary || "(No payer)";
}

// Text as the grouping key compares it: trimmed, inner whitespace collapsed.
function approvalChangeText(value) {
  return String(value == null ? "" : value).replace(/\s+/g, " ").trim();
}

// An action as the key compares it. A removal travels as "" on one route and
// "None" on the other; both are the same change, so both key as "".
function approvalChangeActionKey(action) {
  return followUpActionIsRemoval(action) ? "" : approvalChangeText(action).toLowerCase();
}

// THE GROUPING KEY. Every field is part of the change the reviewer is judging;
// none of them may be dropped without two different decisions sharing a click.
// The reason is compared through reasonKey: the request's own text on the
// supervisor-request route, and on the service-line route the server's digest
// of the note written with the change (noteClusterKey, computed before any PHI
// redaction), so a reviewer who cannot read notes still never gets two
// different notes merged into one click.
function approvalChangeKey(item) {
  const reasonKey = item && item.reasonKey !== undefined ? String(item.reasonKey) : approvalChangeText(item && item.reason);
  return JSON.stringify([
    String((item && item.orgId) || ""),
    approvalChangeText(item && item.requester).toLowerCase(),
    approvalChangeActionKey(item && item.fromAction),
    approvalChangeActionKey(item && item.toAction),
    approvalChangeText(item && item.date),
    reasonKey,
    approvalChangeText((item && item.status) || "pending").toLowerCase()
  ]);
}

// A short stable id for a card, for markup and keyboard focus. Never trusted as
// identity: a click resolves the id against the cards built from the CURRENT
// rows, so a card that changed between paint and click is simply not found.
function approvalChangeCardId(key) {
  let hash = 0x811c9dc5;
  const text = String(key || "");
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return "chg-" + hash.toString(36);
}

// One supervisor request (lane 1) in the shared item shape.
function approvalChangeRequestItem(row) {
  return {
    lane: "request",
    key: "r:" + supervisorProposalKey(row),
    row,
    orgId: String(row.orgId || ""),
    claimId: String(row.claimId || ""),
    serviceId: String(row.serviceId || ""),
    requester: String(row.requesterUserId || ""),
    fromAction: String(row.currentAction || ""),
    toAction: String(row.action || ""),
    fromDate: String(row.currentFollowUpDate || ""),
    date: String(row.followUpDate || ""),
    reason: String(row.note || ""),
    reasonKey: approvalChangeText(row.note),
    reasonHidden: false,
    status: String(row.status || "pending"),
    sentAt: String(row.createdAtUtc || ""),
    payer: unifiedApprovalPayerLabel(row),
    openBalance: null,
    groupId: "",
    moneyOut: false,
    spotCheck: ""
  };
}

// One service-line review (lane 2) in the shared item shape. That route has no
// reason field on the request itself: the biller writes the reason as the claim
// note that goes with the change, so the claim's latest note is the reason, and
// it is keyed on through the server's PHI-free digest. Claims whose notes differ
// are different requests and get different cards.
function approvalChangeTeamItem(row, meta = {}) {
  const balance = row.openBalance === null || row.openBalance === undefined || row.openBalance === "" ? null : Number(row.openBalance);
  const cluster = row.noteClusterKey === undefined || row.noteClusterKey === null ? null : String(row.noteClusterKey);
  return {
    lane: "team",
    key: "t:" + approvalQueueRowKey(row.orgId, row.claimId, row.serviceId),
    row,
    orgId: String(row.orgId || ""),
    claimId: String(row.claimId || ""),
    serviceId: String(row.serviceId || ""),
    requester: String(row.setBy || ""),
    fromAction: String(row.currentAction || ""),
    toAction: String(row.followUpAction || ""),
    fromDate: String(row.currentFollowUpDate || ""),
    date: String(row.followUpDate || ""),
    reason: row.noteRestricted ? "" : String(row.note || ""),
    reasonKey: cluster === null ? approvalChangeText(row.note) : (cluster ? "digest:" + cluster : ""),
    reasonHidden: row.noteRestricted === true,
    status: String(row.reviewStatus || "pending"),
    sentAt: String(row.setAt || ""),
    payer: unifiedApprovalPayerLabel(row),
    openBalance: Number.isFinite(balance) ? balance : null,
    groupId: String(meta.groupId || ""),
    moneyOut: meta.moneyOut === true,
    spotCheck: String(meta.spotCheck || "")
  };
}

// The service-line rows, read through the server's actionGroups (with their
// spot-check pick and money-out flag) and then through `rows`, so a row the
// groups do not carry is never silently dropped from the list.
function approvalChangeTeamRows(body) {
  const out = [];
  const seen = new Set();
  const add = (row, meta) => {
    if (!row || typeof row !== "object") return;
    const key = approvalQueueRowKey(row.orgId, row.claimId, row.serviceId);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ row, meta });
  };
  const groups = body && Array.isArray(body.actionGroups) ? body.actionGroups : [];
  for (const group of groups) {
    const spot = group && group.spotCheck ? group.spotCheck : null;
    for (const row of (group && Array.isArray(group.rows) ? group.rows : [])) {
      const picked = Boolean(spot && String(spot.claimId) === String(row.claimId) && String(spot.serviceId) === String(row.serviceId));
      add(row, { groupId: String(group.groupId || ""), moneyOut: group.moneyOut === true, spotCheck: picked ? String(spot.reason || "Spot check: chosen at random.") : "" });
    }
  }
  for (const row of (body && Array.isArray(body.rows) ? body.rows : [])) add(row, {});
  return out;
}

// Per-reviewer view state: which cards are open, which claims were unticked,
// which lines are on their way to the server, and what each refused line said.
// Scoped to the session and company, so a switch starts clean.
function approvalChangeView(model) {
  const scope = JSON.stringify([model.session || "", model.orgId || ""]);
  if (!model.approvalChangeView || model.approvalChangeView.scope !== scope) {
    model.approvalChangeView = { scope, expanded: new Set(), excluded: new Map(), inflight: new Set(), errors: {}, firstSeen: new Map(), active: "" };
  }
  return model.approvalChangeView;
}

// Every waiting line from both routes, minus the lines already on their way.
function approvalChangeItems(model) {
  const sources = unifiedApprovalSources(model);
  const view = approvalChangeView(model);
  const items = [];
  // A route that is RE-READING is not showing rows from the read in flight,
  // and the supervisor-request read has no cached answer to fall back on, so
  // its lines are drawn only when its read has answered.
  if (sources.requests) (model.proposals || []).forEach((row) => items.push(approvalChangeRequestItem(row)));
  approvalChangeTeamRows(sources.teamBody).forEach(({ row, meta }) => items.push(approvalChangeTeamItem(row, meta)));
  return items.filter((item) => !view.inflight.has(item.key));
}

// Items -> cards, by the key. Pure, so the key can be proved on its own.
function approvalChangeGroup(items) {
  const cards = new Map();
  for (const item of items || []) {
    const key = approvalChangeKey(item);
    if (!cards.has(key)) cards.set(key, { key, items: [] });
    cards.get(key).items.push(item);
  }
  const ids = new Set();
  return Array.from(cards.values()).map((card) => {
    let id = approvalChangeCardId(card.key);
    for (let suffix = 2; ids.has(id); suffix += 1) id = approvalChangeCardId(card.key) + "-" + suffix;
    ids.add(id);
    const claims = new Map();
    card.items.forEach((item) => {
      // Claim ids repeat across companies, so the org is part of the claim key.
      const claimKey = JSON.stringify([item.orgId, item.claimId]);
      if (!claims.has(claimKey)) claims.set(claimKey, { key: claimKey, orgId: item.orgId, claimId: item.claimId, items: [] });
      claims.get(claimKey).items.push(item);
    });
    // Claims in id order, so the reviewer can find one in a long card.
    const ordered = Array.from(claims.values()).sort((a, b) => String(a.claimId).localeCompare(String(b.claimId), undefined, { numeric: true }) || String(a.orgId).localeCompare(String(b.orgId)));
    return { id, key: card.key, items: card.items, claims: ordered, lead: card.items[0] };
  });
}

// The cards on screen, in reading order: company, then the biggest change
// first, then the order the routes answered in (the sort is stable), so a card
// keeps its place between repaints and the keyboard's slot means something.
function approvalChangeCards(model) {
  const labels = typeof clientActionOrgLabels === "function" ? clientActionOrgLabels() : null;
  const cards = approvalChangeGroup(approvalChangeItems(model));
  cards.forEach((card) => { card.company = oversightCompany(card.lead.orgId, labels); });
  return cards.sort((a, b) => compareText(a.company, b.company) || (b.claims.length - a.claims.length));
}

// Why ONE line cannot be decided from here, or "" when it can. Server-computed
// refusals (self-review, unmatched warehouse row) are shown in the server's
// own words; nothing about them is guessed in the browser.
function approvalChangeItemBlock(item, context) {
  if (item.lane === "request") {
    if (!context.canApprove) return "Your access does not include approving follow-up requests.";
    if (String(item.status || "") !== "pending") return "This request is no longer waiting for review.";
    if (context.busy && context.busy.has(item.row.proposalId)) return "This decision is already on its way.";
    return "";
  }
  if (!context.canApprove) return "Your access does not include approving follow-up requests.";
  if (item.row.canReview === false) return String(item.row.cannotReviewMessage || "You cannot review this line.");
  if (context.queued && context.queued(item)) return "This decision is being delivered. Use Workflow Sync to retry a failed delivery.";
  return "";
}

// What one click on this card would decide, and whether it may. Pure: every
// guard is an input, so each can be proved (and mutated) on its own.
//
//   * A card holding service-line rows from an UNCERTIFIED read (capped or
//     incomplete) refuses the card-level decision outright, with the reason.
//     Approving "all of these" out of a list that is not all there decides
//     rows nobody looked at - the per-line route has always refused it.
//   * A card holding supervisor requests from a TRUNCATED page refuses the same
//     way.
//   * A blocked line stays on the card, disabled, with its reason; it is never
//     part of the click, and it is never hidden.
//   * An unticked claim is left out of the click.
function approvalChangeVerdict(card, context = {}) {
  const excluded = context.excluded instanceof Set ? context.excluded : new Set();
  const hasTeam = card.items.some((item) => item.lane === "team");
  const hasRequest = card.items.some((item) => item.lane === "request");
  let reason = "";
  if (hasTeam && context.certified !== true) reason = "This queue is capped or incomplete, so these lines cannot be decided together. Filter by company to load all of them.";
  else if (hasRequest && context.requestsTruncated === true) reason = "This queue is incomplete. Filter by company before deciding a whole group.";
  const locked = Boolean(reason);
  const claims = card.claims.map((claim) => {
    const blocks = claim.items.map((item) => ({ item, why: approvalChangeItemBlock(item, context) }));
    const decidable = blocks.filter((entry) => !entry.why).map((entry) => entry.item);
    const blocked = blocks.filter((entry) => entry.why);
    return { claim, decidable, blocked, ticked: !reason && decidable.length > 0 && !excluded.has(claim.key) };
  });
  const selected = claims.filter((entry) => entry.ticked).flatMap((entry) => entry.decidable);
  const decidableClaims = claims.filter((entry) => entry.decidable.length > 0).length;
  if (!reason && !decidableClaims) {
    const first = claims.flatMap((entry) => entry.blocked)[0];
    reason = first ? first.why : "None of these lines can be decided by you.";
  }
  if (!reason && !selected.length) reason = "Tick at least one claim to decide.";
  return {
    claims,
    selected,
    selectedClaims: claims.filter((entry) => entry.ticked).length,
    decidableClaims,
    totalClaims: card.claims.length,
    reason,
    locked,
    enabled: !reason
  };
}

// The inputs approvalChangeVerdict needs, read from the live page once.
function approvalChangeContext(model, cardId = "") {
  const view = approvalChangeView(model);
  return {
    certified: unifiedApprovalCertified(),
    requestsTruncated: model.truncated === true,
    canApprove: hasPermission(PERMISSIONS.APPROVE_REVIEW),
    busy: supervisorDecisionBusy(model),
    queued: (item) => (typeof approvalDecisionIsPending === "function" ? approvalDecisionIsPending(approvalQueueRowKey(item.orgId, item.claimId, item.serviceId)) : false),
    excluded: view.excluded.get(cardId) || new Set()
  };
}

function unifiedApprovalCertified() {
  return typeof approvalQueuePopulationCertified === "function" ? approvalQueuePopulationCertified() : false;
}

// Submitter identity appears once above the change and full submitted note.
function approvalChangeHeadline(card) {
  const requester = approvalChangeText(card.lead?.requester);
  return requester ? oversightPerson(requester) : "Submitter not recorded";
}

function approvalChangeCountLabel(verdict) {
  if (verdict.totalClaims <= 1) return "";
  // A refused card names the whole change ("Approve all 10", greyed, with the
  // reason beside it) rather than a meaningless "Approve 0 of 10".
  if (!verdict.enabled || verdict.selectedClaims === verdict.totalClaims) return `all ${verdict.totalClaims}`;
  return `${verdict.selectedClaims} of ${verdict.totalClaims}`;
}

// Before -> after, as two named values and the date.
function approvalChangeShiftHtml(card) {
  const lead = card.lead || {};
  const from = approvalChangeText(lead.fromAction);
  const to = approvalChangeText(lead.toAction);
  const before = approvalChangeActionKey(from) ? from : (from ? "No action" : "Not shown");
  const after = approvalChangeActionKey(to) ? to : "No action (removed)";
  const fromDate = approvalChangeText(lead.fromDate);
  const date = approvalChangeText(lead.date);
  const due = fromDate && fromDate !== date ? `due ${fromDate} → ${date || "no date"}` : (date ? `due ${date}` : "no follow-up date");
  return `<p class="approval-change-shift"><span class="approval-change-before">${escapeHtml(before)}</span><span class="approval-change-arrow" aria-label="changes to">→</span><span class="approval-change-after">${escapeHtml(after)}</span><span class="approval-change-due">${escapeHtml(due)}</span></p>`;
}

// Total outstanding the card's lines carry. Unknown is not zero: lines with no
// balance are counted and said, never added in as $0.
function approvalChangeOutstanding(items) {
  let total = 0;
  let known = 0;
  (items || []).forEach((item) => { if (item.openBalance !== null && Number.isFinite(item.openBalance)) { total += item.openBalance; known += 1; } });
  const unknown = (items || []).length - known;
  if (!known) return "Outstanding balance not available for these lines";
  return `${Formatters.moneyExact(total)}${unknown ? ` · balance not available for ${unknown} ${unknown === 1 ? "line" : "lines"}` : ""}`;
}

function approvalChangeSentText(items) {
  const times = (items || []).map((item) => item.sentAt).filter(Boolean).sort();
  if (!times.length) return { text: "Time not recorded", title: "" };
  const first = formatRelativeOrDate(times[0]);
  const last = formatRelativeOrDate(times[times.length - 1]);
  return { text: first === last ? first : `${first} – ${last}`, title: times.length > 1 ? `${oversightTime(times[0])} – ${oversightTime(times[times.length - 1])}` : oversightTime(times[0]) };
}

function approvalChangePayersText(items) {
  const payers = Array.from(new Set((items || []).map((item) => item.payer).filter((payer) => payer && payer !== "(No payer)"))).sort(compareText);
  const missing = (items || []).filter((item) => !item.payer || item.payer === "(No payer)").length;
  if (!payers.length) return "Payer not shown on these requests";
  return payers.join(", ") + (missing ? ` · not shown for ${missing} ${missing === 1 ? "line" : "lines"}` : "");
}

// One claim inside an open card: its checkbox, its lines, and anything that
// stops a line being decided or went wrong last time.
function approvalChangeClaimHtml(card, entry, view, locked) {
  const claim = entry.claim;
  const lead = claim.items[0];
  const disabled = !entry.decidable.length || locked === true;
  const errors = claim.items.map((item) => view.errors[item.key]).filter(Boolean);
  const lines = claim.items.map((item) => {
    const row = item.row || {};
    const facts = [row.serviceId ? `svc ${row.serviceId}` : "", row.dos ? `DOS ${row.dos}` : "", row.cpt ? `CPT ${row.cpt}` : "", item.payer && item.payer !== "(No payer)" ? item.payer : "", item.openBalance !== null ? Formatters.moneyExact(item.openBalance) : ""].filter(Boolean).join(" · ");
    const block = entry.blocked.find((blocked) => blocked.item === item);
    // The reason is the same on every line of a card (it is part of the key),
    // so it is printed once on the card, not repeated here - and a note that
    // arrived after the change is listed only when it is NOT that reason.
    const reason = approvalChangeText(item.reason);
    const later = item.lane !== "team" ? []
      : (Array.isArray(row.newNotes) ? row.newNotes : []).filter((note) => approvalChangeText(note && note.text) !== reason);
    const notesRow = item.lane === "team" ? (row.newNotesRestricted ? row : { ...row, newNotes: later, newNotesCount: later.length }) : null;
    return `<li class="approval-change-line">
        <span>${escapeHtml(facts || "Service line")}</span>
        ${item.spotCheck ? `<span class="oversight-badge" title="${escapeHtml(item.spotCheck)}">Spot check</span>` : ""}
        ${notesRow ? approvalQueueNewNotesHtml(notesRow) : ""}
        ${block ? `<p class="approval-change-blocked" data-change-blocked>${escapeHtml(block.why)}</p>` : ""}
      </li>`;
  }).join("");
  return `<li class="approval-change-claim${disabled ? " is-disabled" : ""}${entry.ticked ? " is-ticked" : ""}">
      <label class="approval-change-tick"><input type="checkbox" data-change-tick="${escapeHtml(card.id)}" value="${escapeHtml(claim.key)}" ${entry.ticked ? "checked" : ""} ${disabled ? "disabled" : ""} aria-label="Include claim ${escapeHtml(claim.claimId)}"></label>
      <div class="approval-change-claim-body">
        <div class="approval-change-claim-top">${workClaimLinkHtml(lead.row)}${agingBucketBadgeHtml(approvalClaimAgeView(claim))}<span class="muted">${claim.items.length === 1 ? "1 service line" : escapeHtml(claim.items.length + " service lines")}</span></div>
        ${claimPatientLineHtml(lead.row)}
        ${errors.length ? `<p class="oversight-decision-error" role="alert" data-change-error>${escapeHtml(Array.from(new Set(errors)).join(" "))}</p>` : ""}
        <ul class="approval-change-lines">${lines}</ul>
      </div>
    </li>`;
}

function approvalChangeCardHtml(card, model, view, active) {
  const verdict = approvalChangeVerdict(card, approvalChangeContext(model, card.id));
  const expanded = view.expanded.has(card.id);
  const lines = card.items.length;
  const count = approvalChangeCountLabel(verdict);
  const sent = approvalChangeSentText(card.items);
  const lead = card.lead;
  const hasRequest = card.items.some((item) => item.lane === "request");
  const changeBlock = !verdict.enabled ? verdict.reason
    : (hasRequest ? "Change action works on service-line reviews only. Send supervisor requests back with the action you want."
      : (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP) ? "Changing an action also writes the follow-up, which your access does not include." : ""));
  const failures = card.items.filter((item) => view.errors[item.key]);
  const failedClaims = Array.from(new Set(failures.map((item) => item.claimId)));
  const failureNotes = Array.from(new Set(failures.map((item) => view.errors[item.key])));
  // Notes that landed after the change was set, OTHER than the reason note the
  // card already prints. The biller's own note is written with the change and
  // would otherwise flag every card.
  const newNotes = card.items.filter((item) => {
    if (item.lane !== "team" || !(Number(item.row.newNotesCount || 0) > 0)) return false;
    if (item.row.newNotesRestricted) return true;
    const reason = approvalChangeText(item.reason);
    return (Array.isArray(item.row.newNotes) ? item.row.newNotes : []).some((note) => approvalChangeText(note && note.text) !== reason);
  }).length;
  const moneyOut = card.items.some((item) => item.moneyOut);
  const spot = card.items.find((item) => item.spotCheck);
  const cautions = [
    newNotes ? `${newNotes} ${newNotes === 1 ? "line has" : "lines have"} a claim note added after this change was set. Open the card to read ${newNotes === 1 ? "it" : "them"} first.` : "",
    moneyOut ? "Money leaves on this action. Check the lines before approving them together." : ""
  ].filter(Boolean);
  const reason = String(lead.reason || "").trim();
  // Claim ID and patient on the card face, so the reviewer sees whose claims
  // these are before opening it. The name is PHI-gated by
  // claimPatientDisplayName; without access the claim ID stands alone.
  // Oldest first, so a 121+ claim is never hidden behind "+N more".
  const whoClaims = card.claims.slice().sort((a, b) => (approvalClaimAgeView(b).days ?? -1) - (approvalClaimAgeView(a).days ?? -1)).slice(0, 3).map((claim) => {
    const patient = claim.items.map((item) => claimPatientDisplayName(item.row)).find(Boolean) || "";
    return `<li><strong>Claim ${escapeHtml(claim.claimId)}</strong>${patient ? `<span>${escapeHtml(patient)}</span>` : ""}${agingBucketBadgeHtml(approvalClaimAgeView(claim))}</li>`;
  });
  const moreClaims = card.claims.length - whoClaims.length;
  const patientsHtml = `<ul class="approval-change-patients" aria-label="Claims and patients">${whoClaims.join("")}${moreClaims > 0 ? `<li class="muted">+${moreClaims} more ${moreClaims === 1 ? "claim" : "claims"}</li>` : ""}</ul>`;
  return `<article class="oversight-card approval-change-card${active ? " is-active" : ""}" data-approval-card data-change-card="${escapeHtml(card.id)}" tabindex="0" aria-labelledby="${escapeHtml(card.id)}-title"${active ? ' aria-current="true"' : ""}>
    <div class="approval-change-author"><h3 id="${escapeHtml(card.id)}-title" data-change-headline>${escapeHtml(approvalChangeHeadline(card))}</h3><span${sent.title ? ` title="${escapeHtml(sent.title)}"` : ""}>Submitted ${escapeHtml(sent.text)}</span></div>
    <div class="oversight-card-top"><span class="oversight-badge">${escapeHtml(card.company)}</span><span>${escapeHtml(`${card.claims.length} ${card.claims.length === 1 ? "claim" : "claims"} · ${lines} ${lines === 1 ? "service line" : "service lines"}`)}</span></div>
    ${patientsHtml}
    ${approvalChangeShiftHtml(card)}
    <section class="approval-change-submitted-note" aria-label="Submitted note"><h4>Submitted note</h4><p>${escapeHtml(reason || (lead.reasonHidden ? "Hidden (no PHI access)" : "No submitted note recorded."))}</p></section>
    <dl class="approval-change-facts">
      <dt>Payers</dt><dd>${escapeHtml(approvalChangePayersText(card.items))}</dd>
      <dt>Outstanding</dt><dd data-change-outstanding>${escapeHtml(approvalChangeOutstanding(card.items))}</dd>
    </dl>
    ${cautions.map((text) => `<p class="approval-change-caution">${escapeHtml(text)}</p>`).join("")}
    ${spot && !expanded ? `<p class="approval-change-spot"><strong>Spot check:</strong> claim ${escapeHtml(spot.claimId)}${spot.row.serviceId ? ` · svc ${escapeHtml(spot.row.serviceId)}` : ""}${spot.row.dos ? ` · DOS ${escapeHtml(spot.row.dos)}` : ""}${spot.openBalance !== null ? ` · ${escapeHtml(Formatters.moneyExact(spot.openBalance))}` : ""}. ${escapeHtml(spot.spotCheck)}</p>` : ""}
    ${failedClaims.length ? `<p class="oversight-decision-error" role="alert" data-change-error>${escapeHtml(`Not decided last time: ${failureNotes.slice(0, 3).join(" ")}${failureNotes.length > 3 ? ` And ${failureNotes.length - 3} more - open the card to see them.` : ""}`)}</p>` : ""}
    ${!verdict.enabled ? `<p class="approval-change-caution" role="note" data-change-refusal><strong>Cannot decide this card.</strong> ${escapeHtml(verdict.reason)}</p>` : ""}
    <div class="oversight-actions approval-change-actions">
      <button class="primary-button" type="button" data-change-approve="${escapeHtml(card.id)}" ${verdict.enabled ? "" : "disabled"}>${escapeHtml(("Approve " + count).trim())}</button>
      <button class="ghost-button" type="button" data-change-send-back="${escapeHtml(card.id)}" ${verdict.enabled ? "" : "disabled"}>${escapeHtml(("Send back " + count).trim())}</button>
      <button class="ghost-button" type="button" data-change-alter="${escapeHtml(card.id)}" ${changeBlock ? `disabled title="${escapeHtml(changeBlock)}"` : ""}>Change action</button>
      <button class="ghost-button" type="button" data-change-remove="${escapeHtml(card.id)}" ${changeBlock ? `disabled title="${escapeHtml(changeBlock.replace(/^Change action/, "Remove"))}"` : ""}>Remove follow-up</button>
      <button class="ghost-button" type="button" data-change-expand="${escapeHtml(card.id)}" aria-expanded="${expanded ? "true" : "false"}">${expanded ? "Hide claims" : `Show ${card.claims.length === 1 ? "the claim" : `${card.claims.length} claims`}`}</button>
    </div>
    ${expanded ? `<ul class="approval-change-claims" aria-label="Claims in this change">${verdict.claims.map((entry) => approvalChangeClaimHtml(card, entry, view, verdict.locked)).join("")}</ul>` : ""}
  </article>`;
}

// THE ONE TOTAL, in service lines, or null while either route is unknown.
// Lines already on their way to the server are not waiting any more.
function unifiedApprovalsLineTotal(model = oversightState()) {
  const sources = unifiedApprovalSources(model);
  if (!sources.requests || !sources.team) return null;
  const pending = Number(model.counts && model.counts.pending);
  const requests = Number.isFinite(pending) ? pending : (model.proposals || []).length;
  const team = sources.teamBody ? Number(sources.teamBody.total || 0) : 0;
  if (!Number.isFinite(team)) return null;
  // Lines on their way are not waiting any more - but only while they are still
  // in the read. A line the server already retired has left the total once.
  const view = model.approvalChangeView;
  let inflight = 0;
  if (view && view.inflight && view.inflight.size) {
    (model.proposals || []).forEach((row) => { if (view.inflight.has("r:" + supervisorProposalKey(row))) inflight += 1; });
    (sources.teamRows || []).forEach((row) => { if (view.inflight.has("t:" + approvalQueueRowKey(row.orgId, row.claimId, row.serviceId))) inflight += 1; });
  }
  return Math.max(0, Math.max(0, Math.floor(requests)) + Math.max(0, Math.floor(team)) - inflight);
}

// --- the panel -------------------------------------------------------------

// ---------------------------------------------------------------------------
// APPROVAL ARCHIVE - "Show archive" on Needs my approval (owner, 2026-10-27).
//
// "If I approved it by mistake, I want to go back to something and change it."
// Needs my approval lists only what is WAITING; a decided line left it with no
// way back. The archive reads what this reviewer already decided on BOTH
// approval routes, newest decision first:
//   service-line reviews  GET followup-review/pending?mode=archive  - can be
//                         decided again here (Send back / Approve / Change
//                         action). Each is a NEW row in the append-only decision
//                         ledger; the earlier decision is never erased.
//   supervisor requests   GET follow-up-proposals?mode=reviewed - final on their
//                         route once decided, so the card says how to change the
//                         line (open the claim) instead of offering a click that
//                         the server would refuse.
// ---------------------------------------------------------------------------
const APPROVAL_ARCHIVE_DAY_CHOICES = [7, 30, 90, 365];

function approvalArchiveDays(model = {}) {
  return APPROVAL_ARCHIVE_DAY_CHOICES.includes(Number(model.archiveDays)) ? Number(model.archiveDays) : 30;
}

function approvalArchiveToggleHtml(model) {
  const open = Boolean(model.approvalArchive);
  const controls = open
    ? `<select data-approval-archive-days aria-label="Decision date range">${APPROVAL_ARCHIVE_DAY_CHOICES.map((days) => `<option value="${days}"${approvalArchiveDays(model) === days ? " selected" : ""}>Decided in the last ${days} days</option>`).join("")}</select><input type="search" data-approval-archive-search value="${escapeHtml(model.archiveSearch || "")}" placeholder="Search claim, patient, action or person" aria-label="Search the archive">`
    : "";
  return `<div class="approval-archive-toolbar"><button class="ghost-button" type="button" data-approval-archive-toggle aria-pressed="${open ? "true" : "false"}">${open ? "\u2190 Back to waiting approvals" : "Show archive"}</button>${controls}</div>`;
}

function approvalArchiveProposalKey(model) {
  return JSON.stringify([additionalPortalDataReadScope(), approvalQueueSelectedOrg(), approvalArchiveDays(model)]);
}

async function loadApprovalArchiveProposals(model) {
  const key = approvalArchiveProposalKey(model);
  if (model.archiveProposals && model.archiveProposals.key === key) return;
  model.archiveProposals = { key, status: "pending", rows: [], error: "" };
  const params = new URLSearchParams({ mode: "reviewed", days: String(approvalArchiveDays(model)) });
  const orgId = approvalQueueSelectedOrg();
  if (orgId) params.set("orgId", orgId);
  try {
    const data = await oversightRequest(`/api/workflow/follow-up-proposals?${params.toString()}`, undefined, "GET");
    if (!model.archiveProposals || model.archiveProposals.key !== key) return;
    model.archiveProposals = { key, status: "ok", rows: Array.isArray(data.proposals) ? data.proposals : [], truncated: data.truncated === true, error: "" };
  } catch (error) {
    if (!model.archiveProposals || model.archiveProposals.key !== key) return;
    model.archiveProposals = { key, status: "error", rows: [], error: error && error.message ? error.message : "" };
  }
  if (state.view === "assignments" && oversightState() === model && model.tab === "approvals" && model.approvalArchive) renderOversightWorkResults();
}

function approvalArchiveLineSlice() {
  const slice = typeof approvalQueueState === "object" && approvalQueueState ? approvalQueueState.archive : null;
  return slice && slice.key === approvalQueueRequestKey("archive") ? slice : { status: "idle", body: null, error: "" };
}

// Both routes in ONE item shape, newest decision first.
function approvalArchiveItems(model) {
  const slice = approvalArchiveLineSlice();
  const lineRows = slice.status === "ok" && slice.body && Array.isArray(slice.body.rows) ? slice.body.rows : [];
  const lines = lineRows.map((row) => ({
    kind: "line",
    key: `line:${approvalQueueRowKey(row.orgId, row.claimId, row.serviceId)}`,
    row,
    status: row.reviewStatus === "rejected" ? "rejected" : "approved",
    decidedAt: String(row.reviewedAt || ""),
    decidedBy: String(row.reviewedBy || ""),
    requestedBy: String(row.setBy || ""),
    action: approvalQueueActionLabel(row),
    reason: String(row.decisionReason || "")
  }));
  const requestRows = model.archiveProposals && model.archiveProposals.status === "ok" ? model.archiveProposals.rows : [];
  const requests = requestRows.map((row) => ({
    kind: "request",
    key: `request:${row.orgId}:${row.proposalId}`,
    row,
    status: row.status === "rejected" ? "rejected" : "approved",
    decidedAt: String(row.decidedAtUtc || ""),
    decidedBy: String(row.decidedByUserId || row.reviewerUserId || ""),
    requestedBy: String(row.requesterUserId || ""),
    action: supervisorRequestHeadline(row),
    reason: String(row.decisionReason || "")
  }));
  return [...lines, ...requests].sort((a, b) => b.decidedAt.localeCompare(a.decidedAt) || a.key.localeCompare(b.key));
}

function approvalArchiveSearchText(item) {
  const row = item.row || {};
  return [row.claimId, row.serviceId, row.patient, item.action, row.overroteFrom, oversightPerson(item.decidedBy), oversightPerson(item.requestedBy), oversightCompany(row.orgId)]
    .map((value) => String(value || "").toLowerCase()).join(" ");
}

function approvalArchiveCardHtml(item, model) {
  const row = item.row || {};
  const needle = String(model.archiveSearch || "").trim().toLowerCase();
  const hidden = needle && !approvalArchiveSearchText(item).includes(needle);
  const busy = model.archiveBusy === item.key;
  const edit = model.archiveEdit && model.archiveEdit.key === item.key ? model.archiveEdit.kind : "";
  const error = (model.archiveErrors || {})[item.key] || "";
  const badge = item.status === "rejected" ? "Sent back" : (row.overroteFrom ? `Approved \u00b7 changed from ${row.overroteFrom}` : "Approved");
  let actions = workClaimLinkHtml(row, "Open claim");
  let hint = "";
  if (item.kind === "line") {
    if (row.canReview === false) {
      hint = row.cannotReviewMessage || "This line cannot be changed here. Open the claim to change its follow-up.";
    } else {
      const disabled = busy ? " disabled" : "";
      actions += item.status === "approved"
        ? `<button class="ghost-button" type="button" data-approval-archive-sendback="${escapeHtml(item.key)}"${disabled}>Send back</button>`
        : `<button class="primary-button" type="button" data-approval-archive-approve="${escapeHtml(item.key)}"${disabled}>Approve</button>`;
      if (hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) actions += `<button class="ghost-button" type="button" data-approval-archive-change="${escapeHtml(item.key)}"${disabled}>Change action</button>`;
    }
  } else {
    hint = item.status === "approved"
      ? "This request was applied to the claim. To change it, open the claim and set the follow-up."
      : "This request was sent back to the biller. They can resubmit it from My requests, or open the claim to set the follow-up yourself.";
  }
  let editHtml = "";
  if (edit === "sendback") {
    editHtml = `<div class="approval-archive-edit"><textarea data-approval-archive-reason rows="2" maxlength="2000" placeholder="Why is this going back to the biller? (required)" aria-label="Send-back reason"></textarea><div class="oversight-actions"><button class="primary-button" type="button" data-approval-archive-confirm="${escapeHtml(item.key)}"${busy ? " disabled" : ""}>Send back</button><button class="ghost-button" type="button" data-approval-archive-cancel>Cancel</button></div></div>`;
  } else if (edit === "change") {
    const current = String(row.followUpAction || "");
    const options = getSelectableActionRules()
      .filter((rule) => rule.action !== current)
      .map((rule) => `<option value="${escapeHtml(rule.action)}">${escapeHtml(rule.action)}</option>`).join("");
    editHtml = `<div class="approval-archive-edit"><select data-approval-archive-action aria-label="Correct follow-up action"><option value="">Choose the correct action\u2026</option>${options}</select><div class="oversight-actions"><button class="primary-button" type="button" data-approval-archive-confirm="${escapeHtml(item.key)}"${busy ? " disabled" : ""}>Change and approve</button><button class="ghost-button" type="button" data-approval-archive-cancel>Cancel</button></div></div>`;
  }
  return `<article class="oversight-card approval-archive-card" data-approval-archive-item="${escapeHtml(item.key)}" data-approval-archive-text="${escapeHtml(approvalArchiveSearchText(item))}"${hidden ? " hidden" : ""}><div class="oversight-card-top"><span class="oversight-badge">${escapeHtml(badge)}</span><span>${escapeHtml(oversightCompany(row.orgId))}</span></div><h3>${escapeHtml(item.action)}</h3><p>${workClaimLinkHtml(row)}${row.serviceId ? ` \u00b7 Line ${escapeHtml(row.serviceId)}` : ""}</p>${claimPatientLineHtml(row)}<p>${item.kind === "line" ? "Set" : "Requested"} by ${escapeHtml(oversightPerson(item.requestedBy))}</p><p class="oversight-decision-meta">${item.status === "rejected" ? "Sent back" : "Approved"} by ${escapeHtml(oversightPerson(item.decidedBy))}${item.decidedAt ? ` \u00b7 ${escapeHtml(oversightTime(item.decidedAt))}` : ""}</p>${item.reason ? `<p class="oversight-note"><b>Reason:</b> ${escapeHtml(item.reason)}</p>` : ""}${hint ? `<p class="oversight-hint">${escapeHtml(hint)}</p>` : ""}${error ? `<p role="alert">${escapeHtml(error)}</p>` : ""}${editHtml}<div class="oversight-actions">${actions}</div></article>`;
}

function renderApprovalArchive(model) {
  if (typeof workflowApiBase === "function" && workflowApiBase()) requestApprovalQueue("archive");
  loadApprovalArchiveProposals(model);
  const lineSlice = approvalArchiveLineSlice();
  const requestSlice = model.archiveProposals || { status: "idle" };
  const notices = [approvalArchiveToggleHtml(model)];
  const retry = `<button class="small-button" type="button" data-approval-archive-retry>Retry</button>`;
  if (lineSlice.status === "error" || requestSlice.status === "error") notices.push(`<p role="alert">Some decisions could not be loaded. This is a connection problem, not an empty archive. ${retry}</p>`);
  if ((lineSlice.body && lineSlice.body.truncated === true) || requestSlice.truncated === true) notices.push(`<p role="status">Showing the most recent decisions only. Pick a shorter range or one company to see everything in it.</p>`);
  const loading = ["idle", "pending"].includes(String(lineSlice.status || "idle")) || ["idle", "pending"].includes(String(requestSlice.status || "idle"));
  const items = approvalArchiveItems(model);
  const days = approvalArchiveDays(model);
  const failed = lineSlice.status === "error" || requestSlice.status === "error";
  if (!items.length) {
    // A failed read is not an empty archive: the alert above is the whole answer.
    if (failed && !loading) return notices.join("");
    return notices.join("") + (loading
      ? `<p role="status">Loading your decisions\u2026</p>`
      : `<div class="oversight-empty"><h3>No decisions in the last ${days} days</h3><p>Follow-up changes you approve or send back appear here, so a decision can be found and changed later.</p></div>`);
  }
  return `${notices.join("")}${loading ? `<p role="status">Still loading some of your decisions\u2026</p>` : ""}<p class="oversight-count">${items.length} ${items.length === 1 ? "decision" : "decisions"} in the last ${days} days <span class="muted">\u00b7 newest first</span></p><p class="oversight-hint">Every decision stays on record. Changing one adds a new decision; the earlier one is kept.</p><div class="oversight-cards approval-archive-list">${items.map((item) => approvalArchiveCardHtml(item, model)).join("")}</div>`;
}

function resetApprovalArchiveReads(model) {
  model.archiveProposals = null;
  if (typeof approvalQueueState === "object" && approvalQueueState) approvalQueueState.archive = { key: "", status: "idle", body: null, error: "" };
}

function bindApprovalArchive(root, model) {
  root.querySelectorAll("[data-approval-archive-toggle]").forEach((button) => button.addEventListener("click", () => {
    model.approvalArchive = !model.approvalArchive;
    model.archiveEdit = null;
    model.archiveErrors = {};
    // Always re-read: a decision made a moment ago must show on the other side.
    resetApprovalArchiveReads(model);
    renderOversightWorkResults();
  }));
  if (!model.approvalArchive) return;
  root.querySelector("[data-approval-archive-days]")?.addEventListener("change", (event) => {
    model.archiveDays = Number(event.target.value) || 30;
    model.archiveEdit = null;
    renderOversightWorkResults();
  });
  const search = root.querySelector("[data-approval-archive-search]");
  if (search) search.addEventListener("input", () => {
    // Filter in place so the search box keeps focus while typing.
    model.archiveSearch = search.value;
    const needle = String(search.value || "").trim().toLowerCase();
    root.querySelectorAll("[data-approval-archive-item]").forEach((card) => {
      card.hidden = Boolean(needle) && !String(card.dataset.approvalArchiveText || "").includes(needle);
    });
  });
  root.querySelectorAll("[data-approval-archive-retry]").forEach((button) => button.addEventListener("click", () => { resetApprovalArchiveReads(model); renderOversightWorkResults(); }));
  root.querySelectorAll("[data-approval-archive-sendback]").forEach((button) => button.addEventListener("click", () => { model.archiveEdit = { key: button.dataset.approvalArchiveSendback, kind: "sendback" }; renderOversightWorkResults(); }));
  root.querySelectorAll("[data-approval-archive-change]").forEach((button) => button.addEventListener("click", () => { model.archiveEdit = { key: button.dataset.approvalArchiveChange, kind: "change" }; renderOversightWorkResults(); }));
  root.querySelectorAll("[data-approval-archive-approve]").forEach((button) => button.addEventListener("click", () => decideApprovalArchiveItem(model, button.dataset.approvalArchiveApprove, "approved")));
  root.querySelector("[data-approval-archive-cancel]")?.addEventListener("click", () => { model.archiveEdit = null; renderOversightWorkResults(); });
  root.querySelector("[data-approval-archive-confirm]")?.addEventListener("click", (event) => {
    const key = event.currentTarget.dataset.approvalArchiveConfirm;
    if (!model.archiveEdit || model.archiveEdit.key !== key) return;
    if (model.archiveEdit.kind === "sendback") {
      const reason = String(root.querySelector("[data-approval-archive-reason]")?.value || "").trim();
      if (approvalChangeReasonIsBlank(reason)) { showToast("Enter a reason to send this back."); return; }
      decideApprovalArchiveItem(model, key, "rejected", { reason });
    } else {
      const newAction = String(root.querySelector("[data-approval-archive-action]")?.value || "").trim();
      if (!newAction) { showToast("Choose the correct action."); return; }
      decideApprovalArchiveItem(model, key, "changed", { newAction });
    }
  });
}

// One re-decision from the archive, through the SAME service-line request the
// queue uses (version-keyed, honest per-line outcome). A refusal stays on the
// card in the reviewer's words; nothing is reported as done unless it applied.
async function decideApprovalArchiveItem(model, key, verb, extras = {}) {
  const item = approvalArchiveItems(model).find((entry) => entry.key === key);
  if (!item || item.kind !== "line" || item.row.canReview === false || model.archiveBusy) return;
  if (!hasPermission(PERMISSIONS.APPROVE_REVIEW)) { denyAction(PERMISSIONS.APPROVE_REVIEW, "approve review"); return; }
  model.archiveBusy = key;
  model.archiveErrors = { ...(model.archiveErrors || {}) };
  delete model.archiveErrors[key];
  renderOversightWorkResults();
  const row = item.row;
  const outcome = await sendFollowUpReviewBatch([{ claimId: row.claimId, serviceId: row.serviceId, orgId: row.orgId, row, groupId: "approval-archive" }], verb, extras);
  model.archiveBusy = "";
  if (outcome.applied.length) {
    model.archiveEdit = null;
    showToast(verb === "approved" ? `Claim ${row.claimId} approved.` : verb === "changed" ? `Claim ${row.claimId} changed to ${extras.newAction} and approved.` : `Claim ${row.claimId} sent back to the biller.`);
    resetApprovalArchiveReads(model);
    if (typeof invalidateMyWorkCounts === "function") invalidateMyWorkCounts(model);
  } else {
    const failed = outcome.failed[0];
    model.archiveErrors[key] = `Claim ${row.claimId} ${failed && failed.message ? failed.message : "could not be saved"}.`;
  }
  renderOversightWorkResults();
}

function renderSupervisorProposals(model) {
  if (model.approvalArchive) return renderApprovalArchive(model);
  // Both routes are READ here, so no caller can forget one. The service-line
  // read is cached per company and access snapshot, so a repaint costs nothing.
  if (typeof requestApprovalQueue === "function" && typeof workflowApiBase === "function" && workflowApiBase()) requestApprovalQueue("pending");
  const sources = unifiedApprovalSources(model);
  const pendingChanges = approvalChangeCards(model);
  const view = approvalChangeView(model);
  const shownLines = pendingChanges.reduce((total, card) => total + card.items.length, 0);
  const known = unifiedApprovalsLineTotal(model);
  const total = known === null ? shownLines : known;
  const notices = [approvalArchiveToggleHtml(model)];
  if (model.reconcileError) notices.push(`<p role="status">${escapeHtml(model.reconcileError)}</p>`);
  if (model.truncated === true) notices.push('<p role="alert">This queue is incomplete. Filter by company before approving whole claims.</p>');
  if (sources.teamBody && sources.teamBody.truncated === true) notices.push(`<p role="alert">This queue is capped. Showing ${escapeHtml(String(sources.teamBody.total || 0))} of more than ${escapeHtml(String(sources.teamBody.rowLimit || sources.teamBody.total || 0))} matching service-line actions.</p>`);
  // WHY A NUMBER MIGHT BE MISSING, said out loud. A read still running is a
  // WAIT, a read that failed is something a person can retry. Neither is ever
  // reported as "none".
  let stillReading = false;
  if (!sources.requests) {
    if (sources.requestsFailed) notices.push(`<p role="alert">Some approvals could not be loaded${model.error ? ` — ${escapeHtml(model.error)}` : ""}. <button class="small-button" type="button" data-unified-retry>Retry</button></p>`);
    else stillReading = true;
  }
  if (!sources.team) {
    if (sources.teamFailed) notices.push(`<p role="alert">Some approvals could not be loaded. This is a connection or configuration problem, not an empty queue. <button class="small-button" type="button" data-unified-retry>Retry</button></p>`);
    else stillReading = true;
  }
  if (stillReading) notices.push(`<p role="status">Still checking the rest of the work waiting for your approval.</p>`);
  // ONE EMPTY STATE, AND ONLY WHEN BOTH ROUTES HAVE ANSWERED AND BOTH ARE
  // EMPTY - and nothing is on its way, because a list emptied by the reviewer's
  // own click is waiting on the server, not empty.
  if (!pendingChanges.length) {
    if (view.inflight.size) return notices.join("") + `<p role="status" data-change-sending>Sending your decision…</p>`;
    return notices.join("") + (sources.requests && sources.team
      ? `<div class="oversight-empty"><h3 tabindex="-1" data-approval-empty>No pending action-change requests</h3><p>Proposed action changes will appear here when someone needs your approval.</p></div>`
      : "");
  }
  const now = Date.now();
  pendingChanges.forEach((card) => { if (!view.firstSeen.has(card.id)) view.firstSeen.set(card.id, now); });
  if (view.active && !pendingChanges.some((card) => card.id === view.active)) view.active = "";
  const claims = pendingChanges.reduce((sum, card) => sum + card.claims.length, 0);
  const page = model.pagination;
  return `${notices.join("")}<p class="oversight-count" data-approval-waiting>${escapeHtml(String(total))}${known === null ? "+" : ""} ${total === 1 && known !== null ? "service line is" : "service lines are"} waiting for your decision <span class="muted">· ${escapeHtml(`${claims} ${claims === 1 ? "claim" : "claims"} in ${pendingChanges.length} ${pendingChanges.length === 1 ? "change" : "changes"}`)}</span></p>
    <p class="oversight-hint">Identical changes are one card: approve the whole card at once, or open it and untick the claims to leave out.${page && Number(page.totalPages) > 1 ? " Only the claims loaded on this page are included." : ""} <span class="approval-change-keys">Keys: <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>space</kbd> open · <kbd>a</kbd> approve · <kbd>s</kbd> send back · <kbd>c</kbd> change action</span></p>
    ${page && Number(page.totalPages) > 1 ? managementListPagerHtml({ key: "oversight", page: Number(page.number), pages: Number(page.totalPages), total: Number(page.totalRows), first: Number(page.from), last: Number(page.to) }, "My Work") : ""}
    <div class="approval-change-list">${pendingChanges.map((card) => approvalChangeCardHtml(card, model, view, card.id === view.active)).join("")}</div>`;
}

// --- deciding a card -------------------------------------------------------
//
// ONE CLICK, ONE REQUEST PER ROUTE. The service-line route has always taken a
// true batch ({ batchId, items }) and now gets the whole card in one POST. The
// supervisor-request route decides one request per call, so its lines are sent
// together, concurrently, and answered as one. Either way the reviewer reads ONE
// sentence about the click, and it says what actually happened.

// One service-line result -> applied, or refused with a phrase. A 200 is an
// envelope, not an outcome: only decisionRecorded AND projectionUpdated is an
// applied decision. Recorded-but-not-applied means the line moved on after the
// queue loaded - the decision is in the ledger against the old version and the
// line is still pending - so it is a refusal, reported as "changed since it was
// sent", never counted as approved.
//
// A refusal's reason is said in the reviewer's words, as a phrase that follows
// "claim 3432 ...". An unmapped reason falls back to the server's own sentence.
function followUpReviewItemOutcome(result) {
  const refusals = {
    self_review_forbidden: "was set by you, so someone else has to review it",
    claim_out_of_scope: "is outside your access",
    claim_org_mismatch: "belongs to another company",
    follow_up_not_found: "is no longer waiting for review",
    no_pending_proposal: "is no longer waiting for review",
    stale_version: "changed since it was sent",
    stale_version_override_refused: "changed since it was sent",
    override_no_change: "already has that action",
    reviewer_out_of_scope: "was submitted by someone outside your reporting tree",
    reviewer_scope_stale: "was not saved because your reporting tree changed - reload and try again",
    team_scope_unavailable: "could not be checked against your reporting tree right now - try again",
    missing_permission: "needs the follow-up permission to change an action",
    review_policy_changed: "cannot be approved because the requester's access or supervisor changed",
    database_storage_full: "was not saved - the portal database is out of space, tell an admin",
    database_busy: "was not sent - the portal database is busy, wait a minute and try again"
  };
  if (!result || typeof result !== "object") return { applied: false, message: "gave no answer the portal could read - refresh before retrying" };
  if (result.decisionRecorded === true && result.projectionUpdated === true) return { applied: true, message: "" };
  if (result.decisionRecorded === true) return { applied: false, message: "changed since it was sent" };
  const reason = String(result.reason || "");
  if (Object.prototype.hasOwnProperty.call(refusals, reason)) return { applied: false, message: refusals[reason] };
  const said = approvalChangeText(result.message);
  return { applied: false, message: said ? `could not be saved: ${said}` : "could not be saved" };
}

function approvalChangeReasonIsBlank(reason) {
  return !Array.from(String(reason == null ? "" : reason)).some((ch) => ch.trim() !== "" && ![0x200B, 0x200C, 0x200D, 0x2060, 0xFEFF].includes(ch.codePointAt(0)));
}

// ONE DECISION REQUEST AT A TIME, IN SMALL GROUPS. A reviewer clicking through
// the queue used to put every click on the wire at once, and they all fought
// for the same small SQL pools until they outran the ceiling below. Each click
// now waits its turn, and its clock only starts when it is actually sent. The
// server still decides line by line, so a large selection goes in groups that
// each have the whole ceiling to answer.
const FOLLOW_UP_REVIEW_GROUP_LINES = 20;
let followUpReviewSendTurn = Promise.resolve();
function sendFollowUpReviewBatch(items, verb, extras = {}) {
  const turn = followUpReviewSendTurn.then(() => sendFollowUpReviewGroups(items, verb, extras));
  followUpReviewSendTurn = turn.catch(() => {});
  return turn;
}

async function sendFollowUpReviewGroups(items, verb, extras) {
  const list = (items || []).filter(Boolean);
  const answer = { applied: [], failed: [], batchId: "" };
  let held = false;
  for (let offset = 0; offset < list.length; offset += FOLLOW_UP_REVIEW_GROUP_LINES) {
    const group = list.slice(offset, offset + FOLLOW_UP_REVIEW_GROUP_LINES);
    // Pressing on after a timeout or a busy answer would only add to the load.
    if (held) {
      group.forEach((item) => answer.failed.push({ item, message: "was not sent - the portal is busy, wait a minute and try again" }));
      continue;
    }
    const outcome = await postFollowUpReviewGroup(group, verb, extras);
    answer.applied.push(...outcome.applied);
    answer.failed.push(...outcome.failed);
    answer.batchId = answer.batchId || outcome.batchId;
    held = outcome.held === true;
  }
  return answer;
}

// THE ONE SERVICE-LINE REQUEST. Every item carries the version it was judged
// against (followupUpdatedAtUtc), so a line that changed after the queue loaded
// is refused by the server rather than decided blind.
async function postFollowUpReviewGroup(items, verb, extras = {}) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return { applied: [], failed: [], batchId: "" };
  const refuseAll = (message) => ({ applied: [], failed: list.map((item) => ({ item, message })), batchId: "" });
  const base = workflowApiBase();
  const token = workflowApiToken();
  if (!base || typeof fetch !== "function") return refuseAll("could not be sent - the approval service is not configured here");
  const batchId = claimNoteCommandId("review-batch");
  const telemetry = extras.telemetry || {};
  const flag = (value) => (value === true || value === false ? value : null);
  const body = {
    batchId,
    items: list.map((item) => ({
      claimId: item.claimId,
      serviceId: item.serviceId,
      orgId: item.orgId,
      decision: verb === "rejected" ? "sent_back" : verb,
      ...(verb === "rejected" ? { reason: String(extras.reason || "") } : {}),
      ...(verb === "rejected" && extras.suggestedAction ? { suggestedAction: String(extras.suggestedAction) } : {}),
      ...(verb === "changed" ? { newAction: String(extras.newAction || "") } : {}),
      ...(verb === "changed" && extras.reason ? { reason: String(extras.reason) } : {}),
      ...(verb === "changed" && extras.newFollowUpDate ? { newFollowUpDate: String(extras.newFollowUpDate) } : {}),
      followupUpdatedAtUtc: String((item.row && item.row.expectedUpdatedAt) || ""),
      expanded: flag(telemetry.expanded),
      viaOneClick: flag(telemetry.viaOneClick),
      dwellMs: Number.isFinite(telemetry.dwellMs) ? Math.max(0, Math.round(telemetry.dwellMs)) : null,
      groupId: item.groupId || ""
    }))
  };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), supervisorDecisionTimeoutMs);
  let response;
  let payload = {};
  try {
    response = await fetch(`${base}/api/workflow/followup-review`, {
      method: "POST",
      cache: "no-store",
      headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
      signal: controller.signal
    });
    payload = await response.json().catch(() => ({}));
  } catch (error) {
    const timedOut = Boolean(error && error.name === "AbortError");
    return { ...refuseAll(timedOut
      ? "took too long to answer - it may still have been saved, refresh to check"
      : "could not be sent - check your connection, then try again"), held: timedOut };
  } finally {
    clearTimeout(timer);
  }
  if (response.status === 401 && typeof notePortalSessionExpired === "function") notePortalSessionExpired();
  // The server answers one result per item, in order - and a 400 still carries
  // them when nothing at all was recorded, so the per-item reasons are read
  // from that answer too rather than collapsed into one failure.
  const results = Array.isArray(payload && payload.results) ? payload.results : [];
  if (!results.length) {
    const said = approvalChangeText(payload && (payload.message || (payload.error && payload.error.message)));
    return refuseAll(response.status === 401 ? "was not saved - your session expired, sign in again"
      : response.status === 403 ? "was refused - your access does not include this decision"
      : (said ? `could not be saved: ${said}` : "could not be saved - refresh before retrying"));
  }
  const applied = [];
  const failed = [];
  list.forEach((item, index) => {
    const result = results[index];
    if (!result || String(result.claimId) !== item.claimId || String(result.serviceId) !== item.serviceId) {
      failed.push({ item, message: "gave no answer the portal could read - refresh before retrying" });
      return;
    }
    const outcome = followUpReviewItemOutcome(result);
    if (outcome.applied) applied.push(item);
    else failed.push({ item, message: outcome.message });
  });
  return { applied, failed, batchId, held: results.some((result) => result && result.reason === "database_busy") };
}

// THE SUPERVISOR-REQUEST LINES, fired together. The route decides one request
// per call; the calls go out at once and are answered as one outcome, each with
// its own idempotency key and its own timeout.
async function sendSupervisorDecisions(model, items, decision, reason = "") {
  const busyIds = supervisorDecisionBusy(model);
  const errors = supervisorDecisionErrors(model);
  const list = (items || []).filter((item) => item && item.row && item.row.status === "pending");
  list.forEach((item) => { busyIds.add(item.row.proposalId); delete errors[item.row.proposalId]; });
  const answers = await Promise.all(list.map(async (item) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), supervisorDecisionTimeoutMs);
    try {
      const result = await oversightRequest("/api/workflow/follow-up-proposals/decision", { proposalId: item.row.proposalId, orgId: item.row.orgId, decision, reason, workflowCommandId: oversightId() }, "POST", { signal: controller.signal });
      if (result.proposal?.status === decision && (decision !== "approved" || result.applied === true)) return { item, outcome: "applied" };
      return { item, outcome: "missed", message: "This line still needs another review. Refresh to check its current status.", phrase: "still needs another review - refresh to check" };
    } catch (error) {
      return { item, ...supervisorDecisionOutcome(error) };
    } finally {
      clearTimeout(timer);
      busyIds.delete(item.row.proposalId);
    }
  }));
  const applied = [];
  const failed = [];
  const retired = [];
  answers.forEach((answer) => {
    if (answer.outcome === "applied") { applied.push(answer.item); supervisorDecisionResolved(model, answer.item.row); }
    else if (answer.outcome === "retired") { retired.push(answer.item); supervisorDecisionResolved(model, answer.item.row); }
    else {
      errors[answer.item.row.proposalId] = answer.message;
      failed.push({ item: answer.item, message: answer.phrase || answer.message });
    }
  });
  return { applied, failed, retired };
}

// ONE SENTENCE ABOUT ONE CLICK, counted in claims: "10 approved", or
// "8 approved, 2 could not be approved: claim 3432 changed since it was sent".
// A claim is approved only when every line of it that was sent was applied; a
// claim with any refused line is reported as refused, with the reason.
function approvalChangeOutcomeMessage(verb, applied = [], failed = [], retired = [], newAction = "") {
  // "Change action" to None is Remove, and is reported in those words.
  const removing = verb === "changed" && String(newAction || "").trim().toLowerCase() === "none";
  const past = verb === "approved" ? "approved" : verb === "rejected" ? "sent back" : removing ? "removed" : `changed${newAction ? ` to ${newAction}` : ""}`;
  const could = verb === "approved" ? "could not be approved" : verb === "rejected" ? "could not be sent back" : removing ? "could not be removed" : "could not be changed";
  const claimOf = (item) => JSON.stringify([item.orgId, item.claimId]);
  const failedClaims = new Map();
  failed.forEach(({ item, message }) => {
    const key = claimOf(item);
    if (!failedClaims.has(key)) failedClaims.set(key, { claimId: item.claimId, messages: [], failedLines: 0, appliedLines: 0 });
    const entry = failedClaims.get(key);
    entry.failedLines += 1;
    if (message && !entry.messages.includes(message)) entry.messages.push(message);
  });
  // A refused claim whose OTHER lines landed says so: under-reporting applied
  // work is as untrue as over-reporting it.
  applied.forEach((item) => { const entry = failedClaims.get(claimOf(item)); if (entry) entry.appliedLines += 1; });
  const pastLine = verb === "approved" ? "approved" : verb === "rejected" ? "sent back" : removing ? "removed" : "changed";
  const appliedByClaim = new Map();
  applied.forEach((item) => { const key = claimOf(item); if (!failedClaims.has(key)) appliedByClaim.set(key, (appliedByClaim.get(key) || 0) + 1); });
  const appliedLines = Array.from(appliedByClaim.values()).reduce((sum, value) => sum + value, 0);
  const retiredClaims = new Set(retired.map(claimOf).filter((key) => !failedClaims.has(key) && !appliedByClaim.has(key)));
  let text = `${appliedByClaim.size} ${past}`;
  if (appliedByClaim.size && appliedLines !== appliedByClaim.size) text += ` (${appliedLines} service lines)`;
  if (failedClaims.size) {
    const list = Array.from(failedClaims.values());
    const named = list.slice(0, 3).map((entry) => `claim ${entry.claimId} ${entry.messages.join("; ") || "could not be saved"}${entry.appliedLines ? ` (${entry.appliedLines} of its ${entry.appliedLines + entry.failedLines} service lines ${entry.appliedLines === 1 ? "was" : "were"} ${pastLine})` : ""}`);
    text += `, ${failedClaims.size} ${could}: ${named.join("; ")}${list.length > 3 ? `; and ${list.length - 3} more` : ""}`;
  }
  if (retiredClaims.size) text += `. ${retiredClaims.size} had already been reviewed or withdrawn`;
  return text;
}

// A decided service line leaves the cached read in place, so the card does not
// come back while the re-read is in flight; the re-read then confirms it.
function approvalChangeRetireTeamRows(items) {
  const slice = typeof approvalQueueState === "object" && approvalQueueState ? approvalQueueState.pending : null;
  const bodies = [slice && slice.body, approvalQueueState && approvalQueueState.lastPendingAnswer && approvalQueueState.lastPendingAnswer.body].filter(Boolean);
  const gone = new Set((items || []).map((item) => approvalQueueRowKey(item.orgId, item.claimId, item.serviceId)));
  if (!gone.size) return;
  const keep = (row) => !gone.has(approvalQueueRowKey(row.orgId, row.claimId, row.serviceId));
  Array.from(new Set(bodies)).forEach((body) => {
    if (Array.isArray(body.rows)) {
      const before = body.rows.length;
      body.rows = body.rows.filter(keep);
      const removed = before - body.rows.length;
      if (Number.isFinite(Number(body.total))) body.total = Math.max(0, Number(body.total) - removed);
      if (Number.isFinite(Number(body.totalLines))) body.totalLines = Math.max(0, Number(body.totalLines) - removed);
    }
    if (Array.isArray(body.actionGroups)) body.actionGroups.forEach((group) => { if (group && Array.isArray(group.rows)) group.rows = group.rows.filter(keep); });
  });
}

// A QUEUE RE-READ AFTER THE LAST CLICK, NOT AFTER EVERY CLICK. The read is
// hundreds of statements and the same pools serve the decisions, so re-reading
// per click starved the very saves it was checking. It waits until no decision
// is outstanding, the reviewer has paused, and any earlier read has answered.
const APPROVAL_CHANGE_RECONCILE_WAIT_MS = 1200;
const APPROVAL_CHANGE_RECONCILE_READ_WAITS = 15;
const approvalChangeReconcile = { outstanding: 0, timer: null, team: false, requests: false, counts: null, readWaits: 0 };

function scheduleApprovalChangeReconcile() {
  const wanted = approvalChangeReconcile;
  clearTimeout(wanted.timer);
  wanted.timer = null;
  if (wanted.outstanding > 0) return;
  wanted.timer = setTimeout(runApprovalChangeReconcile, APPROVAL_CHANGE_RECONCILE_WAIT_MS);
}

function runApprovalChangeReconcile() {
  const wanted = approvalChangeReconcile;
  wanted.timer = null;
  if (wanted.outstanding > 0) return;
  const model = oversightState();
  const onQueue = state.view === "assignments" && model.tab === "approvals";
  if (onQueue && wanted.team && approvalQueueState.pending?.status === "pending" && wanted.readWaits < APPROVAL_CHANGE_RECONCILE_READ_WAITS) {
    wanted.readWaits += 1;
    scheduleApprovalChangeReconcile();
    return;
  }
  const { team, requests, counts } = wanted;
  Object.assign(wanted, { team: false, requests: false, counts: null, readWaits: 0 });
  if (counts) invalidateMyWorkCounts(counts);
  if (!onQueue) return;
  if (team) refreshApprovalQueueAfterDecision();
  if (requests) loadOversightWork({ quiet: true });
}

// A click still waiting its turn exists only in this tab, so closing the tab
// would drop it. The row stays pending on the server, but say so first.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (event) => {
    if (approvalChangeReconcile.outstanding > 0) event.preventDefault();
  });
}

// Decide one card. The lines leave the list the moment the click lands
// (optimistic, through the same in-place results swap every decision uses);
// the ones the server refused come back with the reason beside them.
async function decideApprovalChangeCard(cardId, verb, options = {}) {
  const model = oversightState();
  const view = approvalChangeView(model);
  const cards = approvalChangeCards(model);
  const position = cards.findIndex((entry) => entry.id === cardId);
  const card = cards[position];
  if (!card) { showToast("This change is no longer waiting for your review. Refresh to see the current queue."); return null; }
  const verdict = approvalChangeVerdict(card, approvalChangeContext(model, cardId));
  if (!verdict.enabled) { showToast(verdict.reason); return null; }
  const reason = String(options.reason || "").trim();
  if (verb === "rejected" && approvalChangeReasonIsBlank(reason)) { showToast("Add the guidance the biller needs before sending this back."); return null; }
  const requests = verdict.selected.filter((item) => item.lane === "request");
  const team = verdict.selected.filter((item) => item.lane === "team");
  if (verb === "changed") {
    if (requests.length) { showToast("Change action works on service-line reviews only. Send supervisor requests back with the action you want."); return null; }
    if (!approvalChangeText(options.newAction)) { showToast("Choose the action to set instead."); return null; }
    if (approvalChangeIsRemoval(options.newAction) && approvalChangeReasonIsBlank(reason)) { showToast("Add the reason for removing this follow-up."); return null; }
  }
  if (requests.length) {
    const why = supervisorDecisionBlockedReason(model, requests.map((item) => item.row), verb === "rejected" ? "rejected" : "approved", reason);
    if (why) { showToast(why); return null; }
  }
  const selected = verdict.selected;
  const telemetry = { expanded: view.expanded.has(cardId), viaOneClick: !view.expanded.has(cardId), dwellMs: view.firstSeen.has(cardId) ? Date.now() - view.firstSeen.get(cardId) : null };
  selected.forEach((item) => { view.inflight.add(item.key); delete view.errors[item.key]; });
  view.excluded.delete(cardId);
  view.expanded.delete(cardId);
  renderOversightWorkResults();
  focusApprovalChangeCard(position);
  showToast(`${verb === "approved" ? "Approving" : verb === "rejected" ? "Sending back" : approvalChangeIsRemoval(options.newAction) ? "Removing follow-up on" : "Changing"} ${verdict.selectedClaims} ${verdict.selectedClaims === 1 ? "claim" : "claims"}…`);
  approvalChangeReconcile.outstanding += 1;
  const [teamOutcome, requestOutcome] = await Promise.all([
    team.length ? sendFollowUpReviewBatch(team, verb, { reason, suggestedAction: options.suggestedAction, newAction: options.newAction, newFollowUpDate: options.newFollowUpDate, telemetry }) : Promise.resolve({ applied: [], failed: [] }),
    requests.length ? sendSupervisorDecisions(model, requests, verb === "rejected" ? "rejected" : "approved", reason) : Promise.resolve({ applied: [], failed: [], retired: [] })
  ]).finally(() => { approvalChangeReconcile.outstanding -= 1; });
  const applied = teamOutcome.applied.concat(requestOutcome.applied);
  const failed = teamOutcome.failed.concat(requestOutcome.failed);
  const retired = requestOutcome.retired || [];
  approvalChangeRetireTeamRows(teamOutcome.applied);
  failed.forEach(({ item, message }) => { view.errors[item.key] = `Claim ${item.claimId} ${message}.`; });
  selected.forEach((item) => view.inflight.delete(item.key));
  const message = approvalChangeOutcomeMessage(verb, applied, failed, retired, options.newAction || "");
  showToast(message);
  // Two numbers move and each has one owner: refreshMyWorkCounts recounts the
  // printed per-tab total from the rows that are left; invalidateMyWorkCounts is
  // the ONLY thing that drops the cached home summary the tab badges read. Only
  // when something actually moved - a click refused for every line changed
  // nothing on the server. The drop waits for the re-read below: on My Work it
  // reloads the home summary at once.
  refreshMyWorkCounts(model);
  if (applied.length || retired.length) approvalChangeReconcile.counts = model;
  if (typeof paintOversightTabCounts === "function") paintOversightTabCounts();
  if (state.view === "assignments" && oversightState() === model && model.tab === "approvals") {
    renderOversightWorkResults();
    focusApprovalChangeCard(position);
    // Reconcile in the background, keeping the rows on screen while it runs.
    if (team.length) approvalChangeReconcile.team = true;
    if (requests.length) approvalChangeReconcile.requests = true;
  }
  scheduleApprovalChangeReconcile();
  return { applied, failed, retired, message };
}

// Keyboard users lose their focus when the card they acted on leaves. Hand it
// to whatever now holds that slot - the next change, or the last one - rather
// than throwing them back to the top of the queue.
function focusApprovalChangeCard(index) {
  if (typeof document === "undefined") return;
  // Focus that is still somewhere real belongs to the reviewer - a dialog, the
  // company picker, another card they moved to while this one was saving.
  const holder = document.activeElement;
  if (holder && holder !== document.body && document.contains(holder)) return;
  const cards = Array.from(document.querySelectorAll("#offshoreQueue [data-change-card]"));
  const view = approvalChangeView(oversightState());
  const next = cards.find((card) => card.dataset.changeCard === view.active)
    || (Number.isInteger(index) && index >= 0 ? (cards[index] || cards[cards.length - 1]) : cards[0])
    || document.querySelector("#offshoreQueue [data-approval-empty]");
  if (!next) return;
  if (next.dataset && next.dataset.changeCard) approvalChangeActivate(next, { scroll: false });
  else if (typeof next.focus === "function") next.focus({ preventScroll: true });
}

// Make one card the keyboard's card, without repainting the list.
function approvalChangeActivate(element, options = {}) {
  if (!element || !element.dataset) return;
  const view = approvalChangeView(oversightState());
  view.active = element.dataset.changeCard || "";
  document.querySelectorAll("#offshoreQueue [data-change-card]").forEach((card) => {
    const on = card === element;
    card.classList.toggle("is-active", on);
    if (on) card.setAttribute("aria-current", "true"); else card.removeAttribute("aria-current");
  });
  if (options.focus !== false && typeof element.focus === "function") element.focus({ preventScroll: true });
  if (options.scroll !== false && typeof element.scrollIntoView === "function") element.scrollIntoView({ block: "nearest" });
}

function approvalChangeRefocus(cardId) {
  const element = Array.from(document.querySelectorAll("#offshoreQueue [data-change-card]")).find((card) => card.dataset.changeCard === cardId);
  if (element) approvalChangeActivate(element, { scroll: false });
}

function toggleApprovalChangeCard(cardId) {
  const view = approvalChangeView(oversightState());
  if (view.expanded.has(cardId)) view.expanded.delete(cardId); else view.expanded.add(cardId);
  renderOversightWorkResults();
  approvalChangeRefocus(cardId);
}

// Resolve a card for a dialog, refusing out loud when it cannot be decided.
function approvalChangeCardFor(cardId) {
  const model = oversightState();
  const card = approvalChangeCards(model).find((entry) => entry.id === cardId);
  if (!card) { showToast("This change is no longer waiting for your review. Refresh to see the current queue."); return null; }
  const verdict = approvalChangeVerdict(card, approvalChangeContext(model, cardId));
  if (!verdict.enabled) { showToast(verdict.reason); return null; }
  return { model, card, verdict };
}

function approvalChangeActionOptionsHtml(excludeAction = "") {
  const skip = approvalChangeActionKey(excludeAction);
  const rules = typeof manualFollowUpActionRules === "function" ? manualFollowUpActionRules() : [];
  return rules.filter((rule) => approvalChangeActionKey(rule.action) !== skip)
    .sort((left, right) => compareFollowUpActionChoices(left.action, right.action))
    .map((rule) => `<option value="${escapeHtml(rule.action)}">${escapeHtml(rule.action)}</option>`).join("");
}

// ONE reason for the whole card. The dialog stays open - and keeps the typed
// guidance - whenever the decision cannot be sent.
function openApprovalChangeSendBack(cardId) {
  const found = approvalChangeCardFor(cardId);
  if (!found) return;
  const { card, verdict } = found;
  const who = approvalChangeText(card.lead.requester) ? oversightPerson(card.lead.requester) : "the biller";
  const hasTeam = verdict.selected.some((item) => item.lane === "team");
  const count = approvalChangeCountLabel(verdict);
  oversightDialog(("Send back " + count).trim(), `<p>${escapeHtml(`${verdict.selectedClaims} ${verdict.selectedClaims === 1 ? "claim" : "claims"} · ${verdict.selected.length} ${verdict.selected.length === 1 ? "service line" : "service lines"}`)} · the current follow-up stays unchanged. Tell ${escapeHtml(who)} what to correct.</p><form><label>Guidance (required)<textarea name="reason" required maxlength="2000"></textarea></label>${hasTeam ? `<label>Suggest an action (optional)<select name="suggested"><option value="">No suggestion</option>${approvalChangeActionOptionsHtml()}</select></label>` : ""}<button class="primary-button" type="submit">${escapeHtml(("Send back " + count).trim())}</button></form>`, (dialog) => {
    dialog.querySelector("form").onsubmit = (event) => {
      event.preventDefault();
      const reason = String(event.target.elements.reason.value || "").trim();
      const error = dialog.querySelector("[data-oversight-error]");
      if (approvalChangeReasonIsBlank(reason)) { error.textContent = "Add the guidance the biller needs before sending this back."; return; }
      const model = oversightState();
      const live = approvalChangeCards(model).find((entry) => entry.id === cardId);
      const check = live ? approvalChangeVerdict(live, approvalChangeContext(model, cardId)) : null;
      if (!check || !check.enabled) { error.textContent = check ? check.reason : "This change is no longer waiting for your review. Refresh to see the current queue."; return; }
      const suggested = event.target.elements.suggested ? String(event.target.elements.suggested.value || "") : "";
      event.target.querySelector("button[type=submit]").disabled = true;
      dialog.close();
      decideApprovalChangeCard(cardId, "rejected", { reason, suggestedAction: suggested });
    };
  });
}

// THE THIRD VERB: set a different action instead, as the reviewer, approved in
// the same write (decision:"changed" + newAction). Service-line reviews only -
// the supervisor-request route has no such decision.
function openApprovalChangeAction(cardId) {
  const found = approvalChangeCardFor(cardId);
  if (!found) return;
  const { card, verdict } = found;
  if (verdict.selected.some((item) => item.lane === "request")) { showToast("Change action works on service-line reviews only. Send supervisor requests back with the action you want."); return; }
  if (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) { showToast("Changing an action also writes the follow-up, which your access does not include."); return; }
  const to = approvalChangeText(card.lead.toAction) || "the requested action";
  oversightDialog("Change the action", `<p>${escapeHtml(`${verdict.selectedClaims} ${verdict.selectedClaims === 1 ? "claim" : "claims"} · ${verdict.selected.length} ${verdict.selected.length === 1 ? "service line" : "service lines"}`)}. Instead of ${escapeHtml(to)}, set this action. It is saved as your decision and approved in the same step.</p><form><label>New follow-up action<select name="action" required><option value="">Choose an action</option>${approvalChangeActionOptionsHtml(card.lead.toAction)}</select></label><label>Follow-up date (optional)<input type="date" name="date"></label><p class="muted">Leave the date empty to use the action's standard due date.</p><button class="primary-button" type="submit">Change action on ${escapeHtml(String(verdict.selectedClaims))} ${verdict.selectedClaims === 1 ? "claim" : "claims"}</button></form>`, (dialog) => {
    dialog.querySelector("form").onsubmit = (event) => {
      event.preventDefault();
      const newAction = String(event.target.elements.action.value || "").trim();
      const newFollowUpDate = String(event.target.elements.date.value || "").trim();
      if (!newAction) { dialog.querySelector("[data-oversight-error]").textContent = "Choose the action to set instead."; return; }
      event.target.querySelector("button[type=submit]").disabled = true;
      dialog.close();
      decideApprovalChangeCard(cardId, "changed", { newAction, newFollowUpDate });
    };
  });
}

function approvalChangeIsRemoval(newAction) {
  return String(newAction || "").trim().toLowerCase() === "none";
}

// REMOVE: clear the follow-up action instead of approving it. It is the
// "Change action" decision with no action (newAction "None"), so the server
// clears action and date and records the reviewer's decision in one write; the
// lines leave the queue rather than coming back blank for someone to approve.
// Service-line reviews only, like Change action, and a reason is required.
function openApprovalChangeRemove(cardId) {
  const found = approvalChangeCardFor(cardId);
  if (!found) return;
  const { card, verdict } = found;
  if (verdict.selected.some((item) => item.lane === "request")) { showToast("Remove works on service-line reviews only. Send supervisor requests back instead."); return; }
  if (!hasPermission(PERMISSIONS.UPDATE_FOLLOW_UP)) { showToast("Removing an action also writes the follow-up, which your access does not include."); return; }
  const action = approvalChangeText(card.lead.toAction) || "the follow-up action";
  const label = `Remove follow-up on ${verdict.selectedClaims} ${verdict.selectedClaims === 1 ? "claim" : "claims"}`;
  oversightDialog("Remove the follow-up", `<p>${escapeHtml(`${verdict.selectedClaims} ${verdict.selectedClaims === 1 ? "claim" : "claims"} · ${verdict.selected.length} ${verdict.selected.length === 1 ? "service line" : "service lines"}`)}. ${escapeHtml(action)} is cleared from ${verdict.selected.length === 1 ? "this line" : "these lines"}, with no follow-up date. It is saved as your decision and leaves the queue.</p><form><label>Reason (required)<textarea name="reason" required maxlength="2000"></textarea></label><button class="primary-button" type="submit">${escapeHtml(label)}</button></form>`, (dialog) => {
    dialog.querySelector("form").onsubmit = (event) => {
      event.preventDefault();
      const reason = String(event.target.elements.reason.value || "").trim();
      const error = dialog.querySelector("[data-oversight-error]");
      if (approvalChangeReasonIsBlank(reason)) { error.textContent = "Add the reason for removing this follow-up."; return; }
      event.target.querySelector("button[type=submit]").disabled = true;
      dialog.close();
      decideApprovalChangeCard(cardId, "changed", { newAction: "None", reason });
    };
  });
}

// j / k move · space open · a approve · s send back · c change action. Only on
// Needs my approval, never while typing, and never under an open dialog.
function handleApprovalChangeKey(event) {
  if (!event || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
  if (state.view !== "assignments" || oversightState().tab !== "approvals") return;
  if (document.querySelector("dialog[open]")) return;
  const target = event.target;
  if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(String(target.tagName || "")))) return;
  const key = event.key;
  if (!["j", "k", "a", "s", "c", " ", "Spacebar"].includes(key)) return;
  const cards = Array.from(document.querySelectorAll("#offshoreQueue [data-change-card]"));
  if (!cards.length) return;
  const view = approvalChangeView(oversightState());
  const current = (target && typeof target.closest === "function" ? target.closest("[data-change-card]") : null)
    || cards.find((card) => card.dataset.changeCard === view.active) || null;
  const index = current ? cards.indexOf(current) : -1;
  if (key === "j" || key === "k") {
    event.preventDefault();
    const next = index < 0 ? cards[0] : cards[Math.max(0, Math.min(cards.length - 1, index + (key === "j" ? 1 : -1)))];
    approvalChangeActivate(next);
    return;
  }
  if (!current) return;
  const id = current.dataset.changeCard;
  if (key === " " || key === "Spacebar") {
    // Space on a button or a claim link belongs to that control.
    if (target && target !== current && /^(BUTTON|A)$/.test(String(target.tagName || ""))) return;
    event.preventDefault();
    toggleApprovalChangeCard(id);
    return;
  }
  event.preventDefault();
  if (key === "a") decideApprovalChangeCard(id, "approved");
  else if (key === "s") openApprovalChangeSendBack(id);
  else if (key === "c") openApprovalChangeAction(id);
}

function bindUnifiedApprovals(root, model) {
  // The keyboard listener is document-wide and installed once; it acts only
  // while Needs my approval is on screen.
  if (!handleApprovalChangeKey.installed && typeof document !== "undefined") {
    handleApprovalChangeKey.installed = true;
    document.addEventListener("keydown", handleApprovalChangeKey);
  }
  root.querySelectorAll("[data-change-approve]").forEach((button) => button.addEventListener("click", () => {
    // Single flight at the control: the click disables the button, and the
    // optimistic repaint then removes the card, so a double click posts once.
    if (button.disabled) return;
    button.disabled = true;
    decideApprovalChangeCard(button.dataset.changeApprove, "approved").then((outcome) => { if (!outcome) renderOversightWorkResults(); });
  }));
  root.querySelectorAll("[data-change-send-back]").forEach((button) => button.addEventListener("click", () => openApprovalChangeSendBack(button.dataset.changeSendBack)));
  root.querySelectorAll("[data-change-alter]").forEach((button) => button.addEventListener("click", () => openApprovalChangeAction(button.dataset.changeAlter)));
  root.querySelectorAll("[data-change-remove]").forEach((button) => button.addEventListener("click", () => openApprovalChangeRemove(button.dataset.changeRemove)));
  root.querySelectorAll("[data-change-expand]").forEach((button) => button.addEventListener("click", () => toggleApprovalChangeCard(button.dataset.changeExpand)));
  root.querySelectorAll("[data-change-tick]").forEach((box) => box.addEventListener("change", () => {
    const view = approvalChangeView(model);
    const cardId = box.dataset.changeTick;
    const excluded = view.excluded.get(cardId) || new Set();
    if (box.checked) excluded.delete(box.value); else excluded.add(box.value);
    if (excluded.size) view.excluded.set(cardId, excluded); else view.excluded.delete(cardId);
    renderOversightWorkResults();
    approvalChangeRefocus(cardId);
  }));
  root.querySelectorAll("[data-change-card]").forEach((card) => card.addEventListener("focusin", () => {
    const view = approvalChangeView(model);
    if (view.active === card.dataset.changeCard) return;
    // Mark it as the keyboard's card; never pull focus off the control inside it.
    approvalChangeActivate(card, { scroll: false, focus: false });
  }));
  root.querySelectorAll("[data-unified-retry]").forEach((button) => button.addEventListener("click", () => {
    if (typeof invalidateApprovalQueue === "function") invalidateApprovalQueue();
    model.status = "idle";
    renderOversightWork();
  }));
}

// Retire one decided line from the queue, in place.
function supervisorDecisionResolved(model, row) {
  const key = supervisorProposalKey(row);
  const index = (model.proposals || []).findIndex((entry) => supervisorProposalKey(entry) === key);
  if (index > -1) model.proposals.splice(index, 1);
  supervisorProposalSelection(model).delete(row.proposalId);
  delete supervisorDecisionErrors(model)[row.proposalId];
  // Across pages the queue total comes from the server and cannot be recounted
  // from one page of rows, so it is decremented as each line leaves.
  const pending = Number(model.counts && model.counts.pending);
  if (Number.isFinite(pending)) model.counts = { ...model.counts, pending: Math.max(0, pending - 1) };
}

// A decision POST has no server-side queue behind it, so give it a ceiling of
// its own. Without one a slow hosted write hung the whole queue indefinitely.
const SUPERVISOR_DECISION_TIMEOUT_MS = 45000;
// A var, so a proof can shorten the ceiling and exercise the abort path in
// seconds rather than three quarters of a minute. Nothing in the app writes it.
var supervisorDecisionTimeoutMs = SUPERVISOR_DECISION_TIMEOUT_MS;

function decideOversightProposal(id, decision) {
  const proposal = oversightState().proposals.find((item) => item.proposalId === id);
  if (!proposal) return;
  oversightDialog(decision === "approved" ? "Approve this follow-up?" : "Send back with guidance", `<p>Claim ${escapeHtml(proposal.claimId)} · Line ${escapeHtml(proposal.serviceId)} · ${escapeHtml(proposal.action)}</p><p class="work-aging-line">Aging: ${agingDaysChipHtml(agingDaysView(proposal))}</p><p>${decision === "approved" ? "The proposed action, date and note will be applied together after checking that this line and the person's access have not changed." : "The current follow-up stays unchanged. Tell the biller what to correct."}</p><form data-decision-form><label>Reviewer feedback ${decision === "rejected" ? "(required)" : "(optional)"}<textarea name="reason" maxlength="2000" ${decision === "rejected" ? "required" : ""}></textarea></label><button class="primary-button" type="submit">${decision === "approved" ? "Approve & apply" : "Send back"}</button></form>`, (dialog) => {
    dialog.querySelector("form").onsubmit = async (event) => {
      event.preventDefault(); const button = event.target.querySelector("button"); button.disabled = true;
      try { await oversightRequest("/api/workflow/follow-up-proposals/decision", { proposalId: id, orgId: proposal.orgId, decision, reason: event.target.elements.reason.value, workflowCommandId: oversightId() }); dialog.close(); invalidateMyWorkCounts(); oversightState().status = "idle"; renderOversightWork(); }
      catch (error) { dialog.querySelector("[data-oversight-error]").textContent = error.message; button.disabled = false; }
    };
  });
}

function userMgmtSupervisionIsManaged(user = {}) {
  if (!isTrustedUserManagerSession() || !["StatesideBiller", "OffshoreBiller"].includes(canonicalRole(user.role))) return false;
  const target = String(user.userId || user.username || user.email || "").trim().toLowerCase();
  const actor = storedPortalAuthUserSnapshot() || currentUser() || {};
  const actorKeys = [actor.userId, actor.username, actor.userKey, actor.email].map((value) => String(value || "").trim().toLowerCase()).filter(Boolean);
  if (!target || actorKeys.includes(target) || portalUserAuthDisagreement(user)) return false;
  if (userMgmtProfileIsPortalManaged(user)) return true;
  const sources = Array.isArray(user.sources) ? user.sources : [];
  return user.directoryAuthoritative === true && user.supervisionManaged === true && user.supervisionUnavailable !== true
    && sources.includes("static-json")
    && normalizeUserStatus(user.status) === "active" && user.canSignIn === true
    && Number.isInteger(user.supervisionVersion) && user.supervisionVersion >= 0
    && /^[a-f0-9]{64}$/.test(String(user.supervisionBaselineDigest || ""))
    && userManagerCanAssignScope(user);
}

function oversightPolicySummary(policy) {
  if (!policy) return "Uses the person's action access.";
  return [policy.mode === "approval" ? "Every change needs approval" : "Uses the person's action access",
    policy.reviewerUserId ? "Supervisor: " + oversightPerson(policy.reviewerUserId) : "",
    policy.reviewActions.length ? "Approval for: " + policy.reviewActions.map((action) => action === "None" ? "Clear follow-up" : action).join(", ") : "",
    policy.blockedActions.length ? "Blocked: " + policy.blockedActions.map((action) => action === "None" ? "Clear follow-up" : action).join(", ") : ""].filter(Boolean).join(" · ");
}

function renderOversightProfile(user) {
  if (!["SuperAdmin", "StatesideBiller", "OffshoreBiller"].includes(canonicalRole(user.role))) return "";
  const policies = oversightPolicies(user.followUpPolicies);
  const summary = user.supervisionUnavailable === true ? "Company supervision is unavailable. Refresh before changing access."
    : user.supervisionNeedsReview === true ? "Review needed: the managed login baseline changed. Review company supervision before further follow-up changes."
    : policies.length ? policies.map((p) => oversightCompany(p.orgId) + ": " + oversightPolicySummary(p)).join("\n") : "Uses this person’s action access at their assigned companies.";
  return `<div class="user-mgmt-rail-section"><b>Supervision by company</b><span>${escapeHtml(summary).replaceAll("\n", "<br>")}</span>${userMgmtSupervisionIsManaged(user) ? `<button class="ghost-button" type="button" data-user-oversight>Edit company supervision</button>` : ""}<button class="ghost-button" type="button" data-user-access-history>Access history</button></div>`;
}

function openOversightPolicyEditor(user) {
  if (!userMgmtSupervisionIsManaged(user)) return;
  const staticManaged = !userMgmtProfileIsPortalManaged(user);
  const policies = oversightPolicies(staticManaged ? user.supervisionPolicies : user.followUpPolicies);
  const baselines = staticManaged ? oversightPolicies(user.supervisionBaselinePolicies) : [];
  const orgs = normalizeUserScopeList(user.orgIds).filter(id => !activeClientGroupTenant() || activeClientGroupOrgIds().includes(id));
  const actions = ["None", ...manualFollowUpActionRules().map((r) => r.action).filter((a) => a !== "None")];
  const userKey = String(user.userId || user.username || user.email).toLowerCase();
  const globalPolicy = followUpActionPolicyForRole(user.followUpActionAccess || user.followUpActionPolicy || {}, canonicalRole(user.role));
  const people = governanceUserRows().filter((u) => u.canSignIn !== false && normalizeUserStatus(u.status) === "active" && ["SuperAdmin", "StatesideBiller"].includes(canonicalRole(u.role)) && String(u.userId || u.username || u.email).toLowerCase() !== userKey);
  const needsReview = staticManaged && user.supervisionNeedsReview === true ? "<p role=\"status\"><b>Review needed:</b> The managed login baseline changed. Review the settings below and save to confirm the current restrictions.</p>" : "";
  const introduction = staticManaged
    ? "This managed login keeps its current role, companies and action access. Its baseline settings are read-only here. Add company restrictions below; removing an extra restriction returns to that baseline."
    : "Add company restrictions to this person’s action access. These settings can require additional approval or block actions; they cannot grant access. Company access and role stay under Edit profile.";
  oversightDialog(`${user.name}: company supervision`, `<p>${introduction}</p>${needsReview}<form data-policy-form>${orgs.map((orgId, index) => {
    const p = policies.find((entry) => entry.orgId === orgId) || { mode: "independent", reviewerUserId: "", blockedActions: [], reviewActions: [] };
    const baseline = baselines.find((entry) => entry.orgId === orgId);
    const baselineReviewer = baseline && (baseline.mode === "approval" || baseline.reviewActions.length) ? baseline.reviewerUserId : "";
    const selectedReviewer = baselineReviewer || p.reviewerUserId;
    const reviewers = people.filter((person) => person.allOrgs || person.orgIds?.includes(orgId));
    const reviewerMatches = (person) => String(person.userId || person.username || person.email).toLowerCase() === selectedReviewer.toLowerCase();
    const baselineHtml = staticManaged ? `<p data-policy-baseline><b>Managed login baseline:</b> ${escapeHtml(oversightPolicySummary(baseline))}.${baselineReviewer ? " This supervisor is fixed by the managed login baseline." : ""}</p>` : "";
    return `<fieldset data-policy-org="${escapeHtml(orgId)}"><legend>${escapeHtml(oversightCompany(orgId))}</legend>${baselineHtml}<label>${staticManaged ? "Extra company supervision" : "Company supervision"}<select name="mode${index}"><option value="independent"${p.mode === "independent" ? " selected" : ""}>${staticManaged ? "Use managed login baseline" : "Use this person’s action access"}</option><option value="approval"${p.mode === "approval" ? " selected" : ""}>Every change needs supervisor approval</option></select></label><label>Supervisor<select name="reviewer${index}"${baselineReviewer ? " disabled" : ""}><option value="">Choose when approval is needed</option>${reviewers.map((person) => { const key = person.userId || person.username || person.email; return `<option value="${escapeHtml(key)}"${reviewerMatches(person) ? " selected" : ""}>${escapeHtml(person.name)}</option>`; }).join("")}${selectedReviewer && !reviewers.some(reviewerMatches) ? `<option selected value="${escapeHtml(selectedReviewer)}">${escapeHtml(selectedReviewer)}${baselineReviewer ? " — managed baseline" : " — choose an active supervisor"}</option>` : ""}</select></label><details><summary>Extra restrictions at this company</summary><p>Blocked actions cannot be submitted. Approval keeps the current follow-up unchanged until accepted. Existing action access always applies.</p><div class="oversight-action-access">${actions.map((action, actionIndex) => {
      const blocked = staticManaged && (globalPolicy.actions[action] === "denied" || baseline?.blockedActions.includes(action));
      const inheritedApproval = staticManaged && (globalPolicy.actions[action] === "approval_required" || baseline?.mode === "approval" || baseline?.reviewActions.includes(action));
      const settingLabel = blocked ? "Blocked by baseline" : inheritedApproval ? "Baseline approval" : "Use company setting";
      return `<label><span>${escapeHtml(action === "None" ? "Clear follow-up" : action)}</span><select name="action${index}_${actionIndex}" data-policy-action="${escapeHtml(action)}"${blocked ? " disabled" : ""}><option value="default">${settingLabel}</option><option value="approval"${p.reviewActions.includes(action) ? " selected" : ""}>Needs approval</option><option value="blocked"${p.blockedActions.includes(action) ? " selected" : ""}>Blocked</option></select></label>`;
    }).join("")}</div></details></fieldset>`;
  }).join("") || `<p>No company is assigned to this account.</p>`}<label>Reason for this change<select name="changeReason"><option value="access_review">Access review</option><option value="performance_review">Performance review</option><option value="training_completed">Training completed</option><option value="coverage_change">Company coverage changed</option><option value="correction">Correct an access setting</option></select></label><p>${staticManaged ? "Saved restrictions apply to the next workflow action. The managed login itself stays unchanged." : "Saving updates access immediately and signs this person out so their next session uses the new settings."}</p><button type="submit" class="primary-button"${orgs.length ? "" : " disabled"}>Save company supervision</button></form>`, (dialog) => {
    dialog.querySelector("form").onsubmit = async (event) => {
      event.preventDefault(); const button = event.target.querySelector('button[type="submit"]'); button.disabled = true;
      try {
        const scopedNext = [...dialog.querySelectorAll("[data-policy-org]")].map((field, index) => ({ orgId: field.dataset.policyOrg, mode: field.querySelector(`[name="mode${index}"]`).value, reviewerUserId: field.querySelector(`[name="reviewer${index}"]`).value, blockedActions: [...field.querySelectorAll("[data-policy-action]")].filter((s) => s.value === "blocked").map((s) => s.dataset.policyAction), reviewActions: [...field.querySelectorAll("[data-policy-action]")].filter((s) => s.value === "approval").map((s) => s.dataset.policyAction) }));
        const next = [...policies.filter(p => !orgs.includes(p.orgId)), ...scopedNext];
        if (next.some((p) => (p.mode === "approval" || p.reviewActions.length) && !p.reviewerUserId)) throw new Error("Choose a supervisor for each company that needs approval.");
        if (staticManaged) {
          const result = await oversightRequest("/api/users/supervision", { userKey, expectedSupervisionVersion: user.supervisionVersion, expectedSupervisionBaselineDigest: user.supervisionBaselineDigest, followUpPolicies: next, changeReason: event.target.elements.changeReason.value });
          if (result.updated !== true || String(result.account?.userKey || "").toLowerCase() !== userKey || !Number.isInteger(result.account?.supervisionVersion) || result.account.supervisionVersion < user.supervisionVersion) throw new Error("The saved supervision could not be confirmed. Refresh before trying again.");
        } else {
          const result = await sendUserProfileChangeRequest(user, user.role, { orgIds: user.orgIds, followUpPolicies: next, changeReason: event.target.elements.changeReason.value }, user.payerNames || []);
          if (!result.ok) throw new Error(result.body?.message || "Access could not be saved. Refresh and try again.");
        }
        await refreshAuthoritativeUserGovernance({ force: true }); dialog.close(); renderUserMgmtList(); showToast(staticManaged ? "Company supervision saved. It applies to the next workflow action." : "Company supervision saved.");
      } catch (error) { dialog.querySelector("[data-oversight-error]").textContent = error.message; button.disabled = false; }
    };
  });
}

function syncOversightFollowUpEditor() {
  const editor = followUpEditorState;
  if (!editor) return;
  const disposition = oversightDisposition(editor.orgId, els.followUpEditorAction?.value || "None", editor.pinnedCurrentAction ?? followUpActionOnLine(editor.claimId, editor.serviceId));
  setText(els.followUpEditorSubmitBtn, ["approval", "live_approval"].includes(disposition) ? "Submit for approval" : "Save follow-up");
  setDisabled(els.followUpEditorSubmitBtn, disposition === "blocked");
  setText(els.followUpEditorContext, `Claim ${editor.claimId} · Service line ${editor.serviceId}. ${disposition === "approval" ? "Your supervisor must approve this request before the follow-up or note changes." : disposition === "live_approval" ? "The follow-up changes require approval. Your note is recorded with the request." : disposition === "blocked" ? "Your manager has restricted this action. Choose another action." : "The action stays on this line; the required note is saved on the claim."}`);
  let send = document.getElementById("oversightSendTask");
  if (!send && els.followUpEditorSubmitBtn?.parentElement) { send = document.createElement("button"); send.id = "oversightSendTask"; send.type = "button"; send.className = "ghost-button"; send.textContent = "Send to colleague"; send.onclick = () => openOversightAssignment({ ...followUpEditorState }); els.followUpEditorSubmitBtn.parentElement.prepend(send); }
  if (send) send.hidden = !hasPermission(PERMISSIONS.ASSIGN_CLAIMS);
}

async function submitOversightProposal(editor, payload) {
  const body = await oversightRequest("/api/workflow/follow-up-proposals", payload);
  if (body.applied !== false || !body.proposal) throw new Error("The approval request could not be confirmed. Refresh before trying again.");
  const model = oversightState();
  invalidateMyWorkCounts(model);
  closeFollowUpEditor();
  showToast("Saved. The new follow-up action is pending approval.");
  if (activeClaimDrawerContext?.claimId === editor.claimId) reopenActiveClaimDrawer();
  // The biller is usually looking at the item they just answered. Bring that
  // list up to date where they are standing, rather than rebuilding the page
  // around them and losing their place.
  if (state.view === "assignments" && ["requests", "approvals"].includes(model.tab) && model.status === "ready") loadOversightWork({ quiet: true });
  else model.status = "idle";
}

function openOversightAssignment(target) {
  if (!target?.orgId || !target?.serviceId) return showToast("Choose a service line first.");
  oversightDialog("Send to a colleague", `<p>Claim ${escapeHtml(target.claimId)} · Line ${escapeHtml(target.serviceId)} · ${escapeHtml(oversightCompany(target.orgId))}</p><form data-assignment-form><label>Assign to<select name="assignee" required><option value="">Loading colleagues…</option></select></label><label>Priority<select name="priority"><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent — act now</option></select></label><label>Complete by<input name="dueDate" type="date" required></label><label>What needs to be done?<textarea name="instruction" maxlength="2000" required placeholder="Give a clear next step and the result you need."></textarea></label><p>This deadline is for the task. It does not change the service line's follow-up date.</p><button class="primary-button" type="submit">Send task</button></form>`, async (dialog) => {
    const select = dialog.querySelector('[name="assignee"]');
    try {
      const data = await oversightRequest(`/api/directed-work/people?orgId=${encodeURIComponent(target.orgId)}`);
      select.innerHTML = '<option value="">Choose a colleague</option>' + (data.people || []).map((p) => `<option value="${escapeHtml(p.userId || p.userKey)}">${escapeHtml(p.displayName || p.name || p.userId || p.userKey)}</option>`).join("");
    } catch (error) { dialog.querySelector("[data-oversight-error]").textContent = error.message; }
    dialog.querySelector("form").onsubmit = async (event) => {
      event.preventDefault(); const form = event.target; const button = form.querySelector("button"); button.disabled = true;
      try { await oversightRequest("/api/directed-work", { orgId: target.orgId, claimId: target.claimId, serviceId: target.serviceId, assigneeUserId: form.elements.assignee.value, priority: form.elements.priority.value, dueDate: form.elements.dueDate.value, instruction: form.elements.instruction.value, operationId: oversightId() }); dialog.close(); invalidateMyWorkCounts(); oversightState().status = "idle"; showToast("Task sent to their My Work queue."); }
      catch (error) { dialog.querySelector("[data-oversight-error]").textContent = error.message; button.disabled = false; }
    };
  });
}

function ensureOversightTeamTabs() {
  const modal = document.getElementById("userMgmtModal");
  if (!modal || modal.querySelector("[data-oversight-teams-pane]")) return;
  const layout = modal.querySelector(".user-mgmt-layout");
  if (!layout) return;
  const tabs = document.createElement("div"); tabs.innerHTML = oversightTabs([["people", "People"], ["teams", "Teams"]], "people", "data-oversight-manage-tab");
  layout.before(tabs);
  const pane = document.createElement("section"); pane.dataset.oversightTeamsPane = ""; pane.className = "oversight-teams-pane"; pane.hidden = true; layout.after(pane);
  tabs.querySelectorAll("button").forEach((button) => button.onclick = () => { tabs.querySelectorAll("button").forEach((b) => b.setAttribute("aria-selected", String(b === button))); layout.hidden = button.dataset.oversightManageTab !== "people"; pane.hidden = !layout.hidden; if (!pane.hidden) loadOversightTeams(pane); });
}

async function loadOversightTeams(pane) {
  pane.innerHTML = `<p>Loading teams…</p>`;
  try { const data = await oversightRequest("/api/users/teams" + (activeClientGroupTenant() ? "?companyOrgId=" + encodeURIComponent(activeClientGroupServerOrgId()) : "")); oversightState().teams = data.teams || []; renderOversightTeams(pane); }
  catch (error) { pane.innerHTML = `<p role="alert">${escapeHtml(error.message)}</p><button type="button" class="ghost-button" data-team-retry>Retry</button>`; pane.querySelector("button").onclick = () => loadOversightTeams(pane); }
}

function renderOversightTeams(pane) {
  const teams = oversightState().teams;
  pane.innerHTML = `<div class="oversight-heading"><div><h2>Teams</h2><p>Group staff around their companies and a team lead. Joining a team never adds company access.</p></div><button class="primary-button" type="button" data-team-new>Create team</button></div><div class="oversight-cards">${teams.map((team) => `<article class="oversight-card"><h3>${escapeHtml(team.name)}</h3><p>Team lead: ${escapeHtml(oversightPerson(team.leadUserKey))}</p><p>${team.orgIds.map((id) => escapeHtml(oversightCompany(id))).join(", ")}</p><p>${team.memberUserKeys.map((key) => escapeHtml(oversightPerson(key))).join(", ") || "No members yet"}</p><button type="button" class="ghost-button" data-team-edit="${escapeHtml(team.teamId)}">Edit team</button></article>`).join("") || `<p>No teams yet. Create a team, choose its companies, then add staff who already have access.</p>`}</div>`;
  pane.querySelector("[data-team-new]").onclick = () => editOversightTeam(null, pane);
  pane.querySelectorAll("[data-team-edit]").forEach((button) => button.onclick = () => editOversightTeam(teams.find((t) => t.teamId === button.dataset.teamEdit), pane));
}

async function editOversightTeam(team, pane) {
  await requestPortalOrgDirectory({ force: portalOrgDirectoryState.status === "error" });
  if (portalOrgDirectoryState.status !== "ok") {
    showToast("Company choices are unavailable. Retry after the company directory loads.");
    return;
  }
  const people = governanceUserRows().filter((u) => ["SuperAdmin", "StatesideBiller", "OffshoreBiller"].includes(canonicalRole(u.role)) && normalizeUserStatus(u.status) === "active");
  const selectedOrgs = team?.orgIds || activeClientGroupOrgIds();
  const labels = userMgmtOrgLabelMap();
  const organizations = portalOrgDirectoryState.orgs
    .map(org => [String(org.orgId), labels.get(String(org.orgId)) || org.orgName])
    .filter(([id]) => !activeClientGroupTenant() || selectedOrgs.includes(id));
  if (organizations.some(([id, name]) => !clientActionCompanyName(name, id))) {
    showToast("Some company names are unavailable. Resolve the company directory before editing this team.");
    return;
  }
  oversightDialog(team ? "Edit team" : "Create team", `<form data-team-form><label>Team name<input name="name" required maxlength="120" value="${escapeHtml(team?.name || "")}"></label><fieldset><legend>Companies</legend><div class="oversight-checklist">${organizations.map(([id, name]) => `<label><input type="checkbox" name="org" value="${escapeHtml(id)}"${selectedOrgs.includes(id) ? " checked" : ""}${activeClientGroupTenant() ? " disabled" : ""}>${escapeHtml(name)}</label>`).join("")}</div></fieldset><fieldset><legend>Members</legend><p>Each person's own company access still applies. Select companies to see eligible staff.</p><div class="oversight-checklist">${people.map((p, index) => { const key = p.userId || p.username || p.email; return `<label data-team-person="${index}"><input type="checkbox" name="member" value="${escapeHtml(key)}"${team?.memberUserKeys.includes(key) ? " checked" : ""}>${escapeHtml(p.name)}</label>`; }).join("")}</div></fieldset><label>Team lead<select name="lead"><option value="">Choose a team member</option>${people.map((p) => { const key = p.userId || p.username || p.email; return `<option value="${escapeHtml(key)}"${team?.leadUserKey === key ? " selected" : ""}>${escapeHtml(p.name)}</option>`; }).join("")}</select></label><p>A team organizes people. Set approval requirements in each person's Follow-up access.</p><button class="primary-button" type="submit">Save team</button></form>`, (dialog) => {
    const filter = () => { const orgs = [...dialog.querySelectorAll('[name="org"]:checked')].map((i) => i.value); dialog.querySelectorAll("[data-team-person]").forEach((label) => { const p = people[Number(label.dataset.teamPerson)]; const eligible = orgs.length && (p.allOrgs || orgs.every((org) => p.orgIds?.includes(org))); label.hidden = !eligible; const input = label.querySelector("input"); input.disabled = !eligible; if (!eligible) input.checked = false; }); const selected = [...dialog.querySelectorAll('[name="member"]:checked')].map((i) => i.value); const lead = dialog.querySelector('[name="lead"]'); [...lead.options].forEach((option) => { if (option.value) option.disabled = !selected.includes(option.value); }); if (lead.value && !selected.includes(lead.value)) lead.value = ""; };
    dialog.querySelectorAll('input[type="checkbox"]').forEach((input) => input.onchange = filter); filter();
    dialog.querySelector("form").onsubmit = async (event) => {
      event.preventDefault(); const form = event.target; const button = form.querySelector('button[type="submit"]'); button.disabled = true;
      try { await oversightRequest("/api/users/teams", { teamId: team?.teamId, expectedVersion: team?.version, name: form.elements.name.value, orgIds: [...form.querySelectorAll('[name="org"]:checked')].map((i) => i.value), memberUserKeys: [...form.querySelectorAll('[name="member"]:checked')].map((i) => i.value), leadUserKey: form.elements.lead.value, followUpActions: team?.followUpActions || [] }); dialog.close(); await loadOversightTeams(pane); }
      catch (error) { dialog.querySelector("[data-oversight-error]").textContent = error.message; button.disabled = false; }
    };
  });
}



async function openOversightAccessHistory(user) {
  const key = user.userId || user.username || user.email;
  const dialog = oversightDialog(`${user.name}: access history`, `<p data-access-history>Loading access history…</p>`);
  try {
    const data = await oversightRequest(`/api/users/access-history?userKey=${encodeURIComponent(key)}`);
    const summary = (value = {}) => `${value.role || ""}${value.status ? ` · ${value.status}` : ""} · ${value.allOrgs ? "All companies" : (value.orgIds || []).map((orgId) => oversightCompany(orgId)).join(", ") || "No assigned companies"}${value.followUpActionPolicy?.actions ? " · Action access: " + Object.entries(value.followUpActionPolicy.actions).map(([action, mode]) => `${action}: ${mode === "direct" ? "direct" : mode === "approval_required" ? "approval required" : "blocked"}`).join(", ") : ""}${value.followUpPolicies?.length ? " · " + value.followUpPolicies.map((p) => `${oversightCompany(p.orgId)}: ${p.mode === "approval" ? "approval required" : "uses person’s action access"}${p.reviewerUserId ? `, reviewer ${oversightPerson(p.reviewerUserId)}` : ""}${p.blockedActions?.length ? `, blocked: ${p.blockedActions.join(", ")}` : ""}${p.reviewActions?.length ? `, approval for: ${p.reviewActions.join(", ")}` : ""}`).join("; ") : ""}`;
    dialog.querySelector("[data-access-history]").innerHTML = (data.events || []).map((event) => `<span class="oversight-history-row"><b>${escapeHtml(String(event.changeReason || event.type || "Access updated").replaceAll("_", " "))}</b><span>${escapeHtml(oversightPerson(event.actorUserId))} · ${escapeHtml(oversightTime(event.occurredAtUtc))}</span><span><b>Before:</b> ${escapeHtml(summary(event.before))}</span><span><b>After:</b> ${escapeHtml(summary(event.after))}</span></span>`).join("") || "Access changes recorded from September 3, 2026 will appear here. Earlier login and activity records remain in View full activity.";
    if (data.recentHistoryOnly === true || data.hasMore === true) dialog.querySelector("[data-access-history]").insertAdjacentHTML("beforeend", '<span class="oversight-hint" role="status">Recent access changes only; older records are not shown here.</span>');
  } catch (error) { dialog.querySelector("[data-access-history]").textContent = ""; dialog.querySelector("[data-oversight-error]").textContent = error.message; }
}

async function submitOversightBulkRequests(targets, action, followUpDate, note) {
  const key = JSON.stringify({ targets, action, followUpDate, note });
  const model = oversightState();
  if (model.bulkRetry?.key !== key) model.bulkRetry = { key, commands: targets.map(() => oversightId()), completed: [] };
  const retry = model.bulkRetry;
  setDisabled(els.bulkFollowUpBtn, true);
  try {
    for (let i = 0; i < targets.length; i++) {
      if (retry.completed.includes(i)) continue;
      const result = await oversightRequest("/api/workflow/follow-up-proposals", { ...targets[i], action, followUpDate, note, workflowCommandId: retry.commands[i], sourceSurface: "all-data-bulk" });
      if (result.applied !== false || !result.proposal) throw new Error("A request could not be confirmed.");
      retry.completed.push(i);
    }
    model.bulkRetry = null; invalidateMyWorkCounts(model); model.status = "idle";
    state.selectedClaims = []; state.selectedClaimTargets = {}; state.selectedServiceTargets = {};
    setValue(els.bulkNote, ""); setValue(els.bulkConfirmCount, "");
    renderClaimsTable(); updateBulkFollowUpGuard();
    showToast(`${targets.length} service-line requests submitted for approval. Current follow-ups are unchanged.`);
  } catch (error) { showToast(`${retry.completed.length} of ${targets.length} requests submitted. ${error.message} Retry keeps the same requests and avoids duplicates.`); }
  finally { setDisabled(els.bulkFollowUpBtn, false); }
}

function withdrawOversightProposal(id) {
  const proposal = oversightState().proposals.find((row) => row.proposalId === id);
  if (!proposal) return;
  oversightDialog("Withdraw this request?", '<p>The current follow-up will stay unchanged. You can submit a new request using your latest supervisor and access settings.</p><button class="primary-button" type="button" data-withdraw-confirm>Withdraw request</button>', (dialog) => {
    const commandId = oversightId();
    dialog.querySelector('[data-withdraw-confirm]').onclick = async (event) => {
      event.target.disabled = true;
      try { await oversightRequest('/api/workflow/follow-up-proposals/decision', { proposalId: id, orgId: proposal.orgId, decision: 'canceled', reason: '', workflowCommandId: commandId }); dialog.close(); invalidateMyWorkCounts(); oversightState().status = 'idle'; renderOversightWork(); }
      catch(error) { dialog.querySelector('[data-oversight-error]').textContent = error.message; event.target.disabled = false; }
    };
  });
}

// CLIENT_API_ACCESS_UI_START
// API access (clientApiAccess.js). The sidebar entry appears only after the
// server answers the status read for this session, so the server's mode and
// role rules decide visibility: off = never, test = SuperAdmin, live = SuperAdmin
// and client roles. Keys are shown once, at collection, and never stored here.
const clientApiAccessUi = { token: "", available: false, pending: false };

function clientApiAccessUiAvailable() {
  return clientApiAccessUi.available && clientApiAccessUi.token === portalAuthToken();
}

function refreshClientApiAccessAvailability() {
  const token = portalAuthToken();
  if (!token || clientApiAccessUi.pending || clientApiAccessUi.token === token) return;
  // Ask only from sessions that could ever use the screen. The release's
  // read-only verification token (tms-release-v1.) may read only its listed
  // routes, and internal billers never get API Access in any mode.
  const role = canonicalRole(state?.authUserScope?.role || state?.role);
  if (token.startsWith("tms-release-v1.") || !["SuperAdmin", "Client", "RedactedClient"].includes(role)) {
    clientApiAccessUi.token = token;
    clientApiAccessUi.available = false;
    return;
  }
  clientApiAccessUi.pending = true;
  fetch(`${workflowApiBase()}/api/client-api-access/status`, { cache: "no-store", headers: { authorization: `Bearer ${token}` } })
    .then((response) => (response.ok ? response.json() : null))
    .then((body) => Boolean(body && body.available === true))
    .catch(() => false)
    .then((available) => {
      clientApiAccessUi.pending = false;
      if (portalAuthToken() !== token) return;
      clientApiAccessUi.token = token;
      clientApiAccessUi.available = available;
      applyNavVisibility(els.navItems, els.navSections);
    });
}

function clientApiAccessAbsoluteBase() {
  const base = String(workflowApiBase() || "").replace(/\/+$/, "");
  return /^https?:\/\//i.test(base) ? base : `${window.location.origin}${base}`;
}

function openClientApiAccessModal() {
  if (!clientApiAccessUiAvailable()) return;
  document.getElementById("clientApiAccessDialog")?.close();
  document.getElementById("clientApiAccessDialog")?.remove();
  const origin = document.activeElement;
  const token = portalAuthToken();
  const controller = new AbortController();
  const dialog = document.createElement("dialog");
  dialog.id = "clientApiAccessDialog";
  dialog.setAttribute("aria-labelledby", "clientApiAccessTitle");
  dialog.innerHTML = `<style>
    #clientApiAccessDialog{font-family:system-ui,-apple-system,Segoe UI,sans-serif;width:min(1080px,95vw);max-height:90dvh;padding:0;border:1px solid #cbd5e1;border-radius:16px;color:#172b42;background:#fff;box-shadow:0 24px 80px #10253a44}
    #clientApiAccessDialog::backdrop{background:#13263b88}
    #clientApiAccessDialog .api-head{position:sticky;top:0;z-index:1;background:#fff;display:flex;justify-content:space-between;align-items:center;gap:16px;padding:20px 24px;border-bottom:1px solid #e2e8f0}
    #clientApiAccessDialog h2{margin:0;font-size:23px}#clientApiAccessDialog h3{margin:22px 0 10px;font-size:17px}
    #clientApiAccessDialog .api-body{padding:0 24px 24px;overflow-wrap:anywhere}
    #clientApiAccessDialog .api-note{font-size:13px;color:#526579;line-height:1.5}
    #clientApiAccessDialog .api-badge{display:inline-block;margin-left:10px;padding:3px 9px;border-radius:999px;background:#fff4d6;color:#7a4b00;font-size:12px;font-weight:600;vertical-align:middle}
    #clientApiAccessDialog label{display:grid;gap:5px;font-size:13px;font-weight:600}
    #clientApiAccessDialog .api-form{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;align-items:end}
    #clientApiAccessDialog button,#clientApiAccessDialog select,#clientApiAccessDialog input{font:inherit;min-height:38px;border:1px solid #b7c5d5;border-radius:7px;padding:7px 10px;max-width:100%;box-sizing:border-box}
    #clientApiAccessDialog button{cursor:pointer;background:#f1f5f9;color:#183b5a}#clientApiAccessDialog button:disabled{opacity:.5;cursor:default}
    #clientApiAccessDialog .api-primary{background:#126ba4;color:#fff;border-color:#126ba4}
    #clientApiAccessDialog .api-danger{color:#9b1c1c;border-color:#e3b5b5;background:#fff5f5}
    #clientApiAccessDialog table{width:100%;border-collapse:collapse;font-size:13px}
    #clientApiAccessDialog th,#clientApiAccessDialog td{text-align:left;padding:9px 8px;border-bottom:1px solid #e2e8f0;vertical-align:top}
    #clientApiAccessDialog th{font-size:12px;color:#526579;font-weight:600}
    #clientApiAccessDialog .api-actions{display:flex;flex-wrap:wrap;gap:6px}
    #clientApiAccessDialog .api-status{font-weight:600}
    #clientApiAccessDialog .api-key-panel{margin:16px 0;padding:16px;border:1px solid #9cc7e4;border-radius:10px;background:#f2f8fc}
    #clientApiAccessDialog .api-key-row{display:flex;gap:8px}#clientApiAccessDialog .api-key-row input{flex:1;font-family:ui-monospace,Consolas,monospace}
    #clientApiAccessDialog .api-warning{margin:12px 0 0;padding:10px 12px;border-left:4px solid #c27c0e;background:#fff8eb;font-size:13px;line-height:1.5}
    #clientApiAccessDialog code{font-family:ui-monospace,Consolas,monospace;font-size:12px;background:#eef2f6;padding:1px 5px;border-radius:4px}
    #clientApiAccessDialog [role=status]{min-height:22px;padding:8px 0;font-size:14px}
  </style><header class="api-head"><h2 id="clientApiAccessTitle">API access<span class="api-badge" data-api-mode hidden></span></h2><button type="button" data-api-close aria-label="Close API access">Close</button></header>
  <div class="api-body"><p class="api-note">Read-only keys that let a company read its own Vista data from its own tools. Each key covers one company, needs super admin approval, and can be turned off at any time.</p>
  <div role="status" aria-live="polite" data-api-message>Loading API access…</div>
  <div data-api-key-panel hidden></div>
  <div data-api-content hidden>
    <h3>Request a key</h3>
    <div class="api-form">
      <label>Company<select data-api-org></select></label>
      <label>Access<select data-api-level></select></label>
      <label>Key name<input data-api-label maxlength="80" placeholder="For example: Monthly reporting"></label>
      <label>Note for the approver (optional)<input data-api-note maxlength="250"></label>
    </div>
    <div class="api-actions" style="margin-top:12px"><button type="button" class="api-primary" data-api-request>Request key</button></div>
    <h3 data-api-list-title>Keys and requests</h3>
    <div data-api-list></div>
  </div></div>`;
  document.body.appendChild(dialog);
  const $ = (selector) => dialog.querySelector(selector);
  const valid = () => dialog.open && portalAuthToken() === token;
  const message = (text) => { if (valid()) $("[data-api-message]").textContent = text; };
  let status = null, keys = [], busy = false;
  const statusLabels = { pending: "Waiting for approval", approved: "Approved, ready to collect", active: "Active", denied: "Denied", cancelled: "Cancelled", revoked: "Turned off", expired: "Expired" };
  const when = (value) => value ? new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—";

  async function request(path, body) {
    if (!valid()) throw new Error("Your session changed. Reopen API access.");
    const response = await fetch(`${workflowApiBase()}/api/client-api-access/${path}`, {
      method: body === undefined ? "GET" : "POST", cache: "no-store", signal: controller.signal,
      headers: { authorization: `Bearer ${token}`, ...(body === undefined ? {} : { "content-type": "application/json" }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
    const payload = await response.json().catch(() => ({}));
    if (!valid()) throw new Error("Your session changed. Reopen API access.");
    if (!response.ok) throw new Error(payload.message || "API access is unavailable right now. Try again shortly.");
    return payload;
  }

  function renderForm() {
    const orgs = status.organizations || [];
    $("[data-api-org]").innerHTML = orgs.length
      ? orgs.map((org) => `<option value="${escapeHtml(org.orgId)}">${escapeHtml(org.name)}</option>`).join("")
      : `<option value="">${escapeHtml(status.organizationsError || "No companies available")}</option>`;
    $("[data-api-level]").innerHTML = (status.accessLevels || []).map((level) => `<option value="${escapeHtml(level.value)}">${escapeHtml(level.label)}</option>`).join("");
    $("[data-api-request]").disabled = busy || !orgs.length;
    const badge = $("[data-api-mode]");
    badge.hidden = status.mode !== "test";
    badge.textContent = status.mode === "test" ? "Test mode: only super admins can see this" : "";
    $("[data-api-list-title]").textContent = status.canApprove ? "Keys and requests" : "Your keys and requests";
  }

  function rowActions(key) {
    const parts = [];
    if (key.canDecide) {
      parts.push(`<select data-api-expiry="${escapeHtml(key.keyId)}" aria-label="Key lasts">${(status.expiryDays || []).map((days) => `<option value="${days}"${days === status.defaultExpiryDays ? " selected" : ""}>${days} days</option>`).join("")}</select>`);
      parts.push(`<button type="button" class="api-primary" data-api-approve="${escapeHtml(key.keyId)}">Approve</button>`);
      parts.push(`<button type="button" data-api-deny="${escapeHtml(key.keyId)}">Deny</button>`);
    }
    if (key.canCollect) parts.push(`<button type="button" class="api-primary" data-api-collect="${escapeHtml(key.keyId)}">Collect key</button>`);
    if (key.canCancel) parts.push(`<button type="button" data-api-cancel="${escapeHtml(key.keyId)}">Cancel request</button>`);
    if (key.canRevoke) parts.push(`<button type="button" class="api-danger" data-api-revoke="${escapeHtml(key.keyId)}">Turn off</button>`);
    return parts.length ? `<div class="api-actions">${parts.join("")}</div>` : "";
  }

  function renderList() {
    if (!keys.length) { $("[data-api-list]").innerHTML = `<p class="api-note">No keys or requests yet.</p>`; return; }
    $("[data-api-list]").innerHTML = `<table><thead><tr><th>Company</th><th>Key name</th><th>Access</th><th>Status</th><th>Requested</th><th>Expires</th><th>Last used</th><th>Actions</th></tr></thead><tbody>${keys.map((key) => `<tr>
      <td>${escapeHtml(key.companyName)}</td>
      <td>${escapeHtml(key.label)}${key.requestNote ? `<div class="api-note">${escapeHtml(key.requestNote)}</div>` : ""}${key.keyHint ? `<div class="api-note"><code>${escapeHtml(key.keyHint)}</code></div>` : ""}</td>
      <td>${escapeHtml(key.accessLevel === "redacted" ? "Redacted" : "Full detail")}</td>
      <td><span class="api-status">${escapeHtml(statusLabels[key.status] || key.status)}</span>${key.decidedBy ? `<div class="api-note">${escapeHtml(key.status === "denied" ? "Denied" : "Approved")} by ${escapeHtml(key.decidedBy)}</div>` : ""}</td>
      <td>${escapeHtml(key.requestedBy)}<div class="api-note">${escapeHtml(when(key.requestedAtUtc))}</div></td>
      <td>${escapeHtml(key.expiresAtUtc ? when(key.expiresAtUtc) : key.status === "pending" || key.status === "approved" ? `${key.expiryDays} days after collection` : "—")}</td>
      <td>${escapeHtml(when(key.lastUsedAtUtc))}</td>
      <td>${rowActions(key)}</td></tr>`).join("")}</tbody></table>`;
  }

  function showKey(payload) {
    const base = clientApiAccessAbsoluteBase();
    const panel = $("[data-api-key-panel]");
    panel.hidden = false;
    panel.innerHTML = `<div class="api-key-panel"><h3 style="margin-top:0">Key for ${escapeHtml(payload.key.companyName)}</h3>
      <p class="api-note"><strong>This is the only time the key is shown.</strong> Copy it now. If it is lost, turn it off and request a new one.</p>
      <div class="api-key-row"><input readonly data-api-key-value aria-label="API key" value="${escapeHtml(payload.apiKey)}"><button type="button" data-api-copy>Copy</button></div>
      <p class="api-note" style="margin-top:12px">Send it as a header: <code>Authorization: Bearer &lt;key&gt;</code><br>
      Read API: <code>${escapeHtml(base)}/api/v1/client</code> (start with <code>/api/v1/client/guide</code>)<br>
      MCP server (for Claude, Codex and other AI tools): <code>${escapeHtml(base)}/mcp</code></p>
      <p class="api-warning">${escapeHtml(payload.warning)}</p></div>`;
    panel.querySelector("[data-api-copy]").onclick = async () => {
      const input = panel.querySelector("[data-api-key-value]");
      try { await navigator.clipboard.writeText(input.value); message("Key copied."); }
      catch (_error) { input.select(); message("Select the key and copy it."); }
    };
  }

  async function refresh() {
    status = await request("status");
    const listed = await request("keys");
    if (!valid()) return;
    keys = listed.keys || [];
    renderForm();
    renderList();
    $("[data-api-content]").hidden = false;
  }

  async function perform(fn, done) {
    if (busy || !valid()) return;
    busy = true;
    dialog.querySelectorAll(".api-body button, .api-body select, .api-body input:not([readonly])").forEach((el) => { el.disabled = true; });
    try { await fn(); if (done) message(done); }
    catch (error) { if (error.name !== "AbortError") message(error.message || "API access is unavailable right now."); }
    finally {
      busy = false;
      if (valid()) { dialog.querySelectorAll(".api-body button, .api-body select, .api-body input").forEach((el) => { el.disabled = false; }); if (status) renderForm(); }
    }
  }

  $("[data-api-close]").onclick = () => dialog.close();
  dialog.addEventListener("close", () => { controller.abort(); dialog.remove(); origin?.focus?.(); }, { once: true });
  $("[data-api-request]").onclick = () => perform(async () => {
    const label = $("[data-api-label]").value.trim();
    if (!label) throw new Error("Give the key a name, for example what it will be used for.");
    await request("keys", { orgId: $("[data-api-org]").value, accessLevel: $("[data-api-level]").value, label, note: $("[data-api-note]").value });
    $("[data-api-label]").value = ""; $("[data-api-note]").value = "";
    await refresh();
  }, status?.canApprove ? "Request added. Approve it below to continue." : "Request sent. A super admin needs to approve it.");
  $("[data-api-list]").onclick = (event) => {
    const button = event.target.closest("button");
    if (!button || busy) return;
    const { apiApprove, apiDeny, apiCollect, apiCancel, apiRevoke } = button.dataset;
    if (apiApprove) perform(async () => {
      const expiry = Number(dialog.querySelector(`[data-api-expiry="${CSS.escape(apiApprove)}"]`)?.value || status.defaultExpiryDays);
      await request(`keys/${apiApprove}/decision`, { decision: "approve", expiryDays: expiry });
      await refresh();
    }, "Approved. The person who asked can now collect the key.");
    if (apiDeny) perform(async () => { await request(`keys/${apiDeny}/decision`, { decision: "deny" }); await refresh(); }, "Request denied.");
    if (apiCancel) perform(async () => { await request(`keys/${apiCancel}/cancel`, {}); await refresh(); }, "Request cancelled.");
    if (apiCollect) perform(async () => { const payload = await request(`keys/${apiCollect}/collect`, {}); showKey(payload); await refresh(); }, "Key created. Copy it now; it will not be shown again.");
    if (apiRevoke) {
      if (button.dataset.confirming !== "1") { button.dataset.confirming = "1"; button.textContent = "Confirm turn off"; return; }
      perform(async () => { await request(`keys/${apiRevoke}/revoke`, {}); await refresh(); }, "Key turned off. It stops working immediately.");
    }
  };
  dialog.showModal();
  perform(async () => { await refresh(); message(""); });
}
// CLIENT_API_ACCESS_UI_END

// EMAIL_NOTIFICATIONS_ADMIN_START
function openEmailNotificationsModal() {
  if (!isTrustedSuperAdminSession()) return;
  document.getElementById("emailNotificationsDialog")?.close();
  document.getElementById("emailNotificationsDialog")?.remove();
  const origin = document.activeElement;
  const token = portalAuthToken();
  const controller = new AbortController();
  const dialog = document.createElement("dialog");
  dialog.id = "emailNotificationsDialog";
  dialog.setAttribute("aria-labelledby", "emailNotificationsTitle");
  dialog.innerHTML = `<style>
    #emailNotificationsDialog{font-family:system-ui,-apple-system,Segoe UI,sans-serif;width:min(940px,94vw);max-height:90dvh;padding:0;border:1px solid #cbd5e1;border-radius:16px;color:#172b42;background:#fff;box-shadow:0 24px 80px #10253a44}
    #emailNotificationsDialog::backdrop{background:#13263b88}
    #emailNotificationsDialog .email-admin-head{position:sticky;top:0;z-index:1;background:white;display:flex;justify-content:space-between;align-items:center;padding:20px 24px;border-bottom:1px solid #e2e8f0;gap:16px}
    #emailNotificationsDialog h2{margin:0;font-size:23px}#emailNotificationsDialog h3{margin:22px 0 10px;font-size:17px}
    #emailNotificationsDialog .email-admin-body{padding:0 24px 24px;overflow-wrap:anywhere}
    #emailNotificationsDialog .email-admin-counts{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
    #emailNotificationsDialog .email-admin-count{background:#f3f6fa;padding:13px;border-radius:9px}#emailNotificationsDialog .email-admin-count strong{display:block;font-size:25px}
    #emailNotificationsDialog button,#emailNotificationsDialog select,#emailNotificationsDialog input{font:inherit;min-height:40px;border:1px solid #b7c5d5;border-radius:7px;padding:8px 11px;max-width:100%}
    #emailNotificationsDialog button{cursor:pointer;background:#f1f5f9;color:#183b5a}#emailNotificationsDialog button:disabled{opacity:.5;cursor:default}
    #emailNotificationsDialog .email-admin-actions{display:flex;flex-wrap:wrap;gap:8px;margin:14px 0}
    #emailNotificationsDialog .email-admin-primary{background:#126ba4;color:white;border-color:#126ba4}
    #emailNotificationsDialog label{display:grid;gap:5px;font-size:13px;font-weight:600}
    #emailNotificationsDialog .email-admin-form{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
    #emailNotificationsDialog .email-admin-check{display:flex;align-items:center;gap:8px}#emailNotificationsDialog .email-admin-check input{min-height:auto}
    #emailNotificationsDialog .email-admin-contact{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 0;border-bottom:1px solid #e2e8f0}
    #emailNotificationsDialog .email-admin-note{font-size:13px;color:#526579;line-height:1.5}#emailNotificationsDialog [role=status]{min-height:24px;padding:10px 0;font-size:14px}
    @media(max-width:520px){#emailNotificationsDialog .email-admin-head{padding:16px}#emailNotificationsDialog .email-admin-body{padding:0 16px 16px}#emailNotificationsDialog .email-admin-form{grid-template-columns:1fr}#emailNotificationsDialog .email-admin-counts{grid-template-columns:repeat(2,minmax(0,1fr))}}
    </style><header class="email-admin-head"><h2 id="emailNotificationsTitle">Email &amp; notifications</h2><button type="button" class="icon-button" data-email-close aria-label="Close email settings">&times;</button></header>
    <div class="email-admin-body"><p class="email-admin-note">Manage delivery, company contacts and notification tests. Details stay in the secure portal.</p><div role="status" aria-live="polite" data-email-message>Loading email settings…</div><div data-email-content hidden>
    <div data-email-health class="email-admin-note"></div><p data-email-inventory class="email-admin-note"></p><h3>Workflow delivery</h3><div class="email-admin-counts" data-email-counts></div><h3>Invitations, sign-in codes &amp; account recovery</h3><div class="email-admin-counts" data-email-account-counts></div><p class="email-admin-note">Accepted means the email provider accepted the message. Delivered requires a delivery report.</p>
    <div class="email-admin-actions"><button type="button" data-email-refresh>Refresh status</button><button type="button" data-email-run>Process due notifications</button><button type="button" data-email-test>Send all workflow tests to billing2@therapyms.com</button></div>
    <h3>Notification rollout</h3><label>Sending mode<select data-email-mode><option value="disabled">Disabled — no workflow notifications</option><option value="pilot">Pilot — approved test mailbox only</option><option value="internal">Internal — mapped staff contacts</option></select></label>
    <p class="email-admin-note">Updates are grouped into a weekday morning summary. Requests and replies are immediate. Authorization reminders use recorded end dates; client-action escalation follows five overdue weekdays. Credentialing alerts follow new review work and status changes.</p>
    <h3>Company contacts</h3><div data-email-contacts></div><p class="email-admin-note">Choose an existing person and their company. Missing profile emails must be set in Manage Users before adding a contact.</p>
    <div class="email-admin-form"><label>Person<select data-email-person></select></label><label>Company<select data-email-org></select></label><label>Workflow<select data-email-workflow><option value="authorization">Authorizations</option><option value="credentialing">Credentialing</option><option value="client_action">Client action</option><option value="feedback">Software feedback</option></select></label><label>Registered email<input type="email" data-email-address readonly></label><label class="email-admin-check"><input type="checkbox" data-email-supervisor>Supervisor for overdue escalations</label></div>
    <div class="email-admin-actions"><button type="button" data-email-add>Add contact</button><button type="button" data-email-save class="email-admin-primary">Save notification settings</button></div></div></div>`;
  document.body.appendChild(dialog);
  let users = [], contacts = [], orgs = [], busy = false;
  const $ = selector => dialog.querySelector(selector);
  const valid = () => dialog.open && isTrustedSuperAdminSession() && portalAuthToken() === token;
  const message = text => { if (valid()) $("[data-email-message]").textContent = text; };
  const name = id => { const user = users.find(u => u.userKey === id || u.accountId === id); return user?.displayName || id; };
  const company = id => id === "global" ? "Portal-wide administration" : orgs.find(o => o.orgId === id)?.orgName || id;
  const labels = { authorization: "Authorizations", credentialing: "Credentialing", client_action: "Client action", feedback: "Software feedback" };
  function renderContacts() {
    $("[data-email-contacts]").innerHTML = contacts.length ? contacts.map((c, i) => `<div class="email-admin-contact"><div><strong>${escapeHtml(name(c.userId))}</strong><br><span class="email-admin-note">${escapeHtml(company(c.orgId))} · ${escapeHtml(labels[c.workflow] || c.workflow)}${c.supervisor ? " · Supervisor" : ""}<br>${escapeHtml(c.email)}</span></div><button type="button" data-email-remove="${i}" aria-label="Remove contact for ${escapeHtml(name(c.userId))}">Remove</button></div>`).join("") : '<p class="email-admin-note">No contacts are mapped. Workflow notifications need a company contact.</p>';
  }
  function personChanged() {
    const user = users.find(u => u.userKey === $("[data-email-person]").value);
    const portalWide = $("[data-email-workflow]").value === "feedback";
    const available = portalWide ? (["SuperAdmin", "Super Admin"].includes(user?.role) ? ["global"] : []) : user?.allOrgs ? orgs.map(o => o.orgId) : user?.orgIds || [];
    const allowed = activeClientGroupTenant() ? available.filter(id => activeClientGroupOrgIds().includes(String(id))) : available;
    $("[data-email-org]").innerHTML = allowed.map(id => `<option value="${escapeHtml(id)}">${escapeHtml(company(id))}</option>`).join("");
    $("[data-email-org]").disabled = Boolean(activeClientGroupTenant());
    $("[data-email-address]").value = user?.email || "";
    $("[data-email-add]").disabled = busy || !user?.email || !allowed.length;
  }
  async function request(action, body) {
    if (!valid()) throw new Error("Your administrator session changed. Reopen email settings.");
    const response = await fetch(`${workflowApiBase()}/api/notifications/${action}`, { method: body === undefined ? "GET" : "POST", headers: { "content-type": "application/json", authorization: `Bearer ${token}` }, ...(body === undefined ? {} : { body: JSON.stringify(body) }), signal: controller.signal });
    if (!valid()) throw new Error("Your administrator session changed. Reopen email settings.");
    const payload = await response.json();
    if (!valid()) throw new Error("Your administrator session changed. Reopen email settings.");
    if (!response.ok) throw new Error(payload.message || "Email settings could not be updated. Check your access and try again.");
    return payload;
  }
  async function refresh(reloadContacts = false) {
    const status = await request("status");
    if (!valid()) return;
    $("[data-email-health]").textContent = `${status.senderLive ? "Email sender connected" : "Email sender unavailable"} · ${status.deliveryEventsConfigured ? "Delivery tracking configured" : "Delivery tracking unavailable"}${status.lastError ? " · Processing needs administrator attention" : ""}`;
    $("[data-email-counts]").innerHTML = ["queued", "accepted", "delivered", "failed", "uncertain", "suppressed"].map(key => `<div class="email-admin-count"><strong>${Number(status.delivery?.counts?.[key] || 0)}</strong>${key.charAt(0).toUpperCase() + key.slice(1)}</div>`).join("");
    const scan = status.credentialScanStatus;
    $("[data-email-inventory]").textContent = scan ? `New credentialing review checks: ${Number(scan.scanned || 0)} companies checked${Number(scan.incomplete || 0) ? `, ${Number(scan.incomplete)} incomplete — new-review alerts may be delayed` : ""}.` : "New credentialing review checks: verification pending.";
    $("[data-email-account-counts]").innerHTML = status.accounts ? ["queued", "accepted", "delivered", "failed", "uncertain", "expired"].map(key => `<div class="email-admin-count"><strong>${Number(status.accounts.counts?.[key] || 0)}</strong>${key.charAt(0).toUpperCase() + key.slice(1)}</div>`).join("") : '<p class="email-admin-note">Account delivery status is unavailable.</p>';
    if (reloadContacts) { contacts = (status.config?.contacts || []).map(c => ({ ...c })); $("[data-email-mode]").value = status.config?.mode || "disabled"; renderContacts(); }
  }
  async function perform(fn) {
    if (busy || !valid()) return;
    busy = true; dialog.querySelectorAll("button:not([data-email-close]),select").forEach(el => { el.disabled = true; });
    try { await fn(); } catch (error) { if (error.name !== "AbortError") message(error.message || "Email settings are temporarily unavailable."); }
    finally { busy = false; if (valid()) { dialog.querySelectorAll("button:not([data-email-close]),select").forEach(el => { el.disabled = false; }); personChanged(); } }
  }
  $("[data-email-close]").onclick = () => dialog.close();
  dialog.addEventListener("close", () => { controller.abort(); dialog.remove(); origin?.focus?.(); }, { once: true });
  $("[data-email-person]").onchange = personChanged;
  $("[data-email-workflow]").onchange = personChanged;
  $("[data-email-contacts]").onclick = event => { if (!valid() || busy) return; const button = event.target.closest("[data-email-remove]"); if (button) { contacts.splice(Number(button.dataset.emailRemove), 1); renderContacts(); message("Contact removed from draft. Save to apply."); } };
  $("[data-email-add]").onclick = () => {
    if (!valid() || busy) return;
    const user = users.find(u => u.userKey === $("[data-email-person]").value);
    const orgId = $("[data-email-org]").value, workflow = $("[data-email-workflow]").value;
    if (!user?.email || !orgId) return;
    if (contacts.some(c => c.userId === user.userKey && c.orgId === orgId && c.workflow === workflow)) { message("This person already has that company workflow. Remove the existing contact to replace it."); return; }
    contacts.push({ userId: user.userKey, email: user.email, orgId, workflow, supervisor: $("[data-email-supervisor]").checked }); renderContacts(); message("Contact added to draft. Save to apply.");
  };
  $("[data-email-refresh]").onclick = () => perform(async () => { await refresh(); message("Delivery status refreshed."); });
  $("[data-email-run]").onclick = () => perform(async () => { const result = await request("run", {}); await refresh(); message(result.error ? "Processing needs administrator attention." : "Due notifications checked. Review delivery status above."); });
  $("[data-email-test]").onclick = () => perform(async () => { await request("test", {}); await refresh(); message("Workflow tests submitted to billing2@therapyms.com. Refresh status for delivery reports."); });
  $("[data-email-save]").onclick = () => perform(async () => { await request("settings", { mode: $("[data-email-mode]").value, contacts }); await refresh(true); message("Notification settings saved."); });
  dialog.showModal(); $("[data-email-close]").focus();
  perform(async () => {
    const result = await request("users"); users = (result.users || []).filter(u => u.status === "active" && u.canSignIn && !u.scopeConflict);
    try { await requestPortalOrgDirectory(); } catch (_) {}
    if (!valid()) return;
    orgs = typeof portalOrgDirectoryState !== "undefined" ? portalOrgDirectoryState.orgs || [] : [];
    $("[data-email-person]").innerHTML = users.map(u => `<option value="${escapeHtml(u.userKey)}">${escapeHtml(u.displayName || u.userKey)}${u.email ? "" : " — profile email missing"}</option>`).join("");
    personChanged(); await refresh(true); $("[data-email-content]").hidden = false; message("Ready. Changes take effect only when you save.");
  });
}
// EMAIL_NOTIFICATIONS_ADMIN_END
